/**
 * @example
 * const a1 = new EventEmitter<'a' | 'b', [number]>();
 * a1.on('a', (num) => {});
 * const a2 = new EventEmitter<{ a: (age: string) => void; b: (name: number) => number }>();
 * a2.on('a', (age) => {});
 */
class EventEmitter {
    listeners = {};
    once = (event, fn) => {
        const wrapFn = (...args) => {
            this.off(event, wrapFn);
            fn(...args);
        };
        this.on(event, wrapFn);
        return this;
    };
    on = (event, fn) => {
        (this.listeners[event] || (this.listeners[event] = [])).push(fn);
        return this;
    };
    addListener = this.on;
    off = (event, fn) => {
        const arr = this.listeners[event];
        if (!arr)
            return;
        for (let i = 0; i < arr.length; i++) {
            if (fn === arr[i]) {
                arr.splice(i, 1);
            }
        }
        return this;
    };
    removeListener = this.off;
    emit = (event, ...args) => {
        this.listeners[event]?.forEach((fn) => {
            fn(...args);
        });
    };
}

var CameraProjectionMode;
(function (CameraProjectionMode) {
    CameraProjectionMode["ORTHOGRAPHIC"] = "ORTHOGRAPHIC";
    CameraProjectionMode["PERSPECTIVE"] = "PERSPECTIVE";
})(CameraProjectionMode || (CameraProjectionMode = {}));
var CameraType;
(function (CameraType) {
    /**
     * - Is described by world coordinates
     * - Camera at center in HCS.
     * - Change focal point with every rotation
     * - Not effected by rotate world
     * - Elevation > 90 is not allowed
     * - Elevation = 90 towards above
     */
    CameraType[CameraType["TRACKING"] = 0] = "TRACKING";
    /**
     * - Is described by focus point, distance, own azimuth and elevation
     * - FocalPoint at center in HCS.
     * - Change position with every rotation
     * - Elevation > 90 is not allowed
     * - Elevation = 90 is over the focalPoint
     */
    CameraType[CameraType["ORBITING"] = 1] = "ORBITING";
    /**
     * - Is described by focus point, distance, own azimuth and elevation
     * - FocalPoint at center in HCS.
     * - Change position with every rotation
     * - Rotate world by default
     * - Elevation > 90 is allowed, allows the camera to rotate upside-down
     * - Elevation = 90 is under the focalPoint
     */
    CameraType[CameraType["EXPLORING"] = 2] = "EXPLORING";
})(CameraType || (CameraType = {}));
var CameraTrackingMode;
(function (CameraTrackingMode) {
    /**
     * Not be changed.
     */
    CameraTrackingMode[CameraTrackingMode["DEFAULT"] = 0] = "DEFAULT";
    /**
     * Change the focusPoint.
     */
    CameraTrackingMode[CameraTrackingMode["ROTATIONAL"] = 1] = "ROTATIONAL";
    /**
     * Change the focusPoint and rotation
     */
    CameraTrackingMode[CameraTrackingMode["TRANSLATIONAL"] = 2] = "TRANSLATIONAL";
    /**
     * Change the focusPoint. And change up axes when setFocalPoint()
     */
    CameraTrackingMode[CameraTrackingMode["CINEMATIC"] = 3] = "CINEMATIC";
})(CameraTrackingMode || (CameraTrackingMode = {}));
var CameraEvent;
(function (CameraEvent) {
    CameraEvent["UPDATED"] = "updated";
    CameraEvent["ZOOMED"] = "zoomed";
})(CameraEvent || (CameraEvent = {}));
var ClipSpaceNearZ;
(function (ClipSpaceNearZ) {
    ClipSpaceNearZ[ClipSpaceNearZ["ZERO"] = 0] = "ZERO";
    ClipSpaceNearZ[ClipSpaceNearZ["NEGATIVE_ONE"] = 1] = "NEGATIVE_ONE";
})(ClipSpaceNearZ || (ClipSpaceNearZ = {}));

/**
 * Common utilities
 * @module glMatrix
 */
// Configuration Constants
var EPSILON$1 = 0.000001;
var ARRAY_TYPE = typeof Float32Array !== 'undefined' ? Float32Array : Array;
if (!Math.hypot) Math.hypot = function () {
  var y = 0,
      i = arguments.length;

  while (i--) {
    y += arguments[i] * arguments[i];
  }

  return Math.sqrt(y);
};

/**
 * 3x3 Matrix
 * @module mat3
 */

/**
 * Creates a new identity mat3
 *
 * @returns {mat3} a new 3x3 matrix
 */

function create$4() {
  var out = new ARRAY_TYPE(9);

  if (ARRAY_TYPE != Float32Array) {
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    out[5] = 0;
    out[6] = 0;
    out[7] = 0;
  }

  out[0] = 1;
  out[4] = 1;
  out[8] = 1;
  return out;
}
/**
 * Copies the upper-left 3x3 values into the given mat3.
 *
 * @param {mat3} out the receiving 3x3 matrix
 * @param {ReadonlyMat4} a   the source 4x4 matrix
 * @returns {mat3} out
 */

function fromMat4(out, a) {
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  out[3] = a[4];
  out[4] = a[5];
  out[5] = a[6];
  out[6] = a[8];
  out[7] = a[9];
  out[8] = a[10];
  return out;
}

/**
 * 4x4 Matrix<br>Format: column-major, when typed out it looks like row-major<br>The matrices are being post multiplied.
 * @module mat4
 */

/**
 * Creates a new identity mat4
 *
 * @returns {mat4} a new 4x4 matrix
 */

function create$3() {
  var out = new ARRAY_TYPE(16);

  if (ARRAY_TYPE != Float32Array) {
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    out[4] = 0;
    out[6] = 0;
    out[7] = 0;
    out[8] = 0;
    out[9] = 0;
    out[11] = 0;
    out[12] = 0;
    out[13] = 0;
    out[14] = 0;
  }

  out[0] = 1;
  out[5] = 1;
  out[10] = 1;
  out[15] = 1;
  return out;
}
/**
 * Creates a new mat4 initialized with values from an existing matrix
 *
 * @param {ReadonlyMat4} a matrix to clone
 * @returns {mat4} a new 4x4 matrix
 */

function clone$1(a) {
  var out = new ARRAY_TYPE(16);
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  out[3] = a[3];
  out[4] = a[4];
  out[5] = a[5];
  out[6] = a[6];
  out[7] = a[7];
  out[8] = a[8];
  out[9] = a[9];
  out[10] = a[10];
  out[11] = a[11];
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
  return out;
}
/**
 * Copy the values from one mat4 to another
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the source matrix
 * @returns {mat4} out
 */

function copy$3(out, a) {
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  out[3] = a[3];
  out[4] = a[4];
  out[5] = a[5];
  out[6] = a[6];
  out[7] = a[7];
  out[8] = a[8];
  out[9] = a[9];
  out[10] = a[10];
  out[11] = a[11];
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
  return out;
}
/**
 * Set a mat4 to the identity matrix
 *
 * @param {mat4} out the receiving matrix
 * @returns {mat4} out
 */

function identity(out) {
  out[0] = 1;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = 1;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 1;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
}
/**
 * Inverts a mat4
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the source matrix
 * @returns {mat4} out
 */

function invert$1(out, a) {
  var a00 = a[0],
      a01 = a[1],
      a02 = a[2],
      a03 = a[3];
  var a10 = a[4],
      a11 = a[5],
      a12 = a[6],
      a13 = a[7];
  var a20 = a[8],
      a21 = a[9],
      a22 = a[10],
      a23 = a[11];
  var a30 = a[12],
      a31 = a[13],
      a32 = a[14],
      a33 = a[15];
  var b00 = a00 * a11 - a01 * a10;
  var b01 = a00 * a12 - a02 * a10;
  var b02 = a00 * a13 - a03 * a10;
  var b03 = a01 * a12 - a02 * a11;
  var b04 = a01 * a13 - a03 * a11;
  var b05 = a02 * a13 - a03 * a12;
  var b06 = a20 * a31 - a21 * a30;
  var b07 = a20 * a32 - a22 * a30;
  var b08 = a20 * a33 - a23 * a30;
  var b09 = a21 * a32 - a22 * a31;
  var b10 = a21 * a33 - a23 * a31;
  var b11 = a22 * a33 - a23 * a32; // Calculate the determinant

  var det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;

  if (!det) {
    return null;
  }

  det = 1.0 / det;
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return out;
}
/**
 * Multiplies two mat4s
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the first operand
 * @param {ReadonlyMat4} b the second operand
 * @returns {mat4} out
 */

function multiply$1(out, a, b) {
  var a00 = a[0],
      a01 = a[1],
      a02 = a[2],
      a03 = a[3];
  var a10 = a[4],
      a11 = a[5],
      a12 = a[6],
      a13 = a[7];
  var a20 = a[8],
      a21 = a[9],
      a22 = a[10],
      a23 = a[11];
  var a30 = a[12],
      a31 = a[13],
      a32 = a[14],
      a33 = a[15]; // Cache only the current line of the second matrix

  var b0 = b[0],
      b1 = b[1],
      b2 = b[2],
      b3 = b[3];
  out[0] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
  out[1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
  out[2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
  out[3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  b0 = b[4];
  b1 = b[5];
  b2 = b[6];
  b3 = b[7];
  out[4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
  out[5] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
  out[6] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
  out[7] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  b0 = b[8];
  b1 = b[9];
  b2 = b[10];
  b3 = b[11];
  out[8] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
  out[9] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
  out[10] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
  out[11] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  b0 = b[12];
  b1 = b[13];
  b2 = b[14];
  b3 = b[15];
  out[12] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
  out[13] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
  out[14] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
  out[15] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  return out;
}
/**
 * Translate a mat4 by the given vector
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the matrix to translate
 * @param {ReadonlyVec3} v vector to translate by
 * @returns {mat4} out
 */

function translate(out, a, v) {
  var x = v[0],
      y = v[1],
      z = v[2];
  var a00, a01, a02, a03;
  var a10, a11, a12, a13;
  var a20, a21, a22, a23;

  if (a === out) {
    out[12] = a[0] * x + a[4] * y + a[8] * z + a[12];
    out[13] = a[1] * x + a[5] * y + a[9] * z + a[13];
    out[14] = a[2] * x + a[6] * y + a[10] * z + a[14];
    out[15] = a[3] * x + a[7] * y + a[11] * z + a[15];
  } else {
    a00 = a[0];
    a01 = a[1];
    a02 = a[2];
    a03 = a[3];
    a10 = a[4];
    a11 = a[5];
    a12 = a[6];
    a13 = a[7];
    a20 = a[8];
    a21 = a[9];
    a22 = a[10];
    a23 = a[11];
    out[0] = a00;
    out[1] = a01;
    out[2] = a02;
    out[3] = a03;
    out[4] = a10;
    out[5] = a11;
    out[6] = a12;
    out[7] = a13;
    out[8] = a20;
    out[9] = a21;
    out[10] = a22;
    out[11] = a23;
    out[12] = a00 * x + a10 * y + a20 * z + a[12];
    out[13] = a01 * x + a11 * y + a21 * z + a[13];
    out[14] = a02 * x + a12 * y + a22 * z + a[14];
    out[15] = a03 * x + a13 * y + a23 * z + a[15];
  }

  return out;
}
/**
 * Scales the mat4 by the dimensions in the given vec3 not using vectorization
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the matrix to scale
 * @param {ReadonlyVec3} v the vec3 to scale the matrix by
 * @returns {mat4} out
 **/

function scale$1(out, a, v) {
  var x = v[0],
      y = v[1],
      z = v[2];
  out[0] = a[0] * x;
  out[1] = a[1] * x;
  out[2] = a[2] * x;
  out[3] = a[3] * x;
  out[4] = a[4] * y;
  out[5] = a[5] * y;
  out[6] = a[6] * y;
  out[7] = a[7] * y;
  out[8] = a[8] * z;
  out[9] = a[9] * z;
  out[10] = a[10] * z;
  out[11] = a[11] * z;
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
  return out;
}
/**
 * Rotates a matrix by the given angle around the X axis
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the matrix to rotate
 * @param {Number} rad the angle to rotate the matrix by
 * @returns {mat4} out
 */

function rotateX(out, a, rad) {
  var s = Math.sin(rad);
  var c = Math.cos(rad);
  var a10 = a[4];
  var a11 = a[5];
  var a12 = a[6];
  var a13 = a[7];
  var a20 = a[8];
  var a21 = a[9];
  var a22 = a[10];
  var a23 = a[11];

  if (a !== out) {
    // If the source and destination differ, copy the unchanged rows
    out[0] = a[0];
    out[1] = a[1];
    out[2] = a[2];
    out[3] = a[3];
    out[12] = a[12];
    out[13] = a[13];
    out[14] = a[14];
    out[15] = a[15];
  } // Perform axis-specific matrix multiplication


  out[4] = a10 * c + a20 * s;
  out[5] = a11 * c + a21 * s;
  out[6] = a12 * c + a22 * s;
  out[7] = a13 * c + a23 * s;
  out[8] = a20 * c - a10 * s;
  out[9] = a21 * c - a11 * s;
  out[10] = a22 * c - a12 * s;
  out[11] = a23 * c - a13 * s;
  return out;
}
/**
 * Rotates a matrix by the given angle around the Y axis
 *
 * @param {mat4} out the receiving matrix
 * @param {ReadonlyMat4} a the matrix to rotate
 * @param {Number} rad the angle to rotate the matrix by
 * @returns {mat4} out
 */

function rotateY(out, a, rad) {
  var s = Math.sin(rad);
  var c = Math.cos(rad);
  var a00 = a[0];
  var a01 = a[1];
  var a02 = a[2];
  var a03 = a[3];
  var a20 = a[8];
  var a21 = a[9];
  var a22 = a[10];
  var a23 = a[11];

  if (a !== out) {
    // If the source and destination differ, copy the unchanged rows
    out[4] = a[4];
    out[5] = a[5];
    out[6] = a[6];
    out[7] = a[7];
    out[12] = a[12];
    out[13] = a[13];
    out[14] = a[14];
    out[15] = a[15];
  } // Perform axis-specific matrix multiplication


  out[0] = a00 * c - a20 * s;
  out[1] = a01 * c - a21 * s;
  out[2] = a02 * c - a22 * s;
  out[3] = a03 * c - a23 * s;
  out[8] = a00 * s + a20 * c;
  out[9] = a01 * s + a21 * c;
  out[10] = a02 * s + a22 * c;
  out[11] = a03 * s + a23 * c;
  return out;
}
/**
 * Returns the translation vector component of a transformation
 *  matrix. If a matrix is built with fromRotationTranslation,
 *  the returned vector will be the same as the translation vector
 *  originally supplied.
 * @param  {vec3} out Vector to receive translation component
 * @param  {ReadonlyMat4} mat Matrix to be decomposed (input)
 * @return {vec3} out
 */

function getTranslation(out, mat) {
  out[0] = mat[12];
  out[1] = mat[13];
  out[2] = mat[14];
  return out;
}
/**
 * Returns the scaling factor component of a transformation
 *  matrix. If a matrix is built with fromRotationTranslationScale
 *  with a normalized Quaternion paramter, the returned vector will be
 *  the same as the scaling vector
 *  originally supplied.
 * @param  {vec3} out Vector to receive scaling factor component
 * @param  {ReadonlyMat4} mat Matrix to be decomposed (input)
 * @return {vec3} out
 */

function getScaling(out, mat) {
  var m11 = mat[0];
  var m12 = mat[1];
  var m13 = mat[2];
  var m21 = mat[4];
  var m22 = mat[5];
  var m23 = mat[6];
  var m31 = mat[8];
  var m32 = mat[9];
  var m33 = mat[10];
  out[0] = Math.hypot(m11, m12, m13);
  out[1] = Math.hypot(m21, m22, m23);
  out[2] = Math.hypot(m31, m32, m33);
  return out;
}
/**
 * Returns a quaternion representing the rotational component
 *  of a transformation matrix. If a matrix is built with
 *  fromRotationTranslation, the returned quaternion will be the
 *  same as the quaternion originally supplied.
 * @param {quat} out Quaternion to receive the rotation component
 * @param {ReadonlyMat4} mat Matrix to be decomposed (input)
 * @return {quat} out
 */

function getRotation(out, mat) {
  var scaling = new ARRAY_TYPE(3);
  getScaling(scaling, mat);
  var is1 = 1 / scaling[0];
  var is2 = 1 / scaling[1];
  var is3 = 1 / scaling[2];
  var sm11 = mat[0] * is1;
  var sm12 = mat[1] * is2;
  var sm13 = mat[2] * is3;
  var sm21 = mat[4] * is1;
  var sm22 = mat[5] * is2;
  var sm23 = mat[6] * is3;
  var sm31 = mat[8] * is1;
  var sm32 = mat[9] * is2;
  var sm33 = mat[10] * is3;
  var trace = sm11 + sm22 + sm33;
  var S = 0;

  if (trace > 0) {
    S = Math.sqrt(trace + 1.0) * 2;
    out[3] = 0.25 * S;
    out[0] = (sm23 - sm32) / S;
    out[1] = (sm31 - sm13) / S;
    out[2] = (sm12 - sm21) / S;
  } else if (sm11 > sm22 && sm11 > sm33) {
    S = Math.sqrt(1.0 + sm11 - sm22 - sm33) * 2;
    out[3] = (sm23 - sm32) / S;
    out[0] = 0.25 * S;
    out[1] = (sm12 + sm21) / S;
    out[2] = (sm31 + sm13) / S;
  } else if (sm22 > sm33) {
    S = Math.sqrt(1.0 + sm22 - sm11 - sm33) * 2;
    out[3] = (sm31 - sm13) / S;
    out[0] = (sm12 + sm21) / S;
    out[1] = 0.25 * S;
    out[2] = (sm23 + sm32) / S;
  } else {
    S = Math.sqrt(1.0 + sm33 - sm11 - sm22) * 2;
    out[3] = (sm12 - sm21) / S;
    out[0] = (sm31 + sm13) / S;
    out[1] = (sm23 + sm32) / S;
    out[2] = 0.25 * S;
  }

  return out;
}
/**
 * Creates a matrix from a quaternion rotation, vector translation and vector scale
 * This is equivalent to (but much faster than):
 *
 *     mat4.identity(dest);
 *     mat4.translate(dest, vec);
 *     let quatMat = mat4.create();
 *     quat4.toMat4(quat, quatMat);
 *     mat4.multiply(dest, quatMat);
 *     mat4.scale(dest, scale)
 *
 * @param {mat4} out mat4 receiving operation result
 * @param {quat4} q Rotation quaternion
 * @param {ReadonlyVec3} v Translation vector
 * @param {ReadonlyVec3} s Scaling vector
 * @returns {mat4} out
 */

function fromRotationTranslationScale(out, q, v, s) {
  // Quaternion math
  var x = q[0],
      y = q[1],
      z = q[2],
      w = q[3];
  var x2 = x + x;
  var y2 = y + y;
  var z2 = z + z;
  var xx = x * x2;
  var xy = x * y2;
  var xz = x * z2;
  var yy = y * y2;
  var yz = y * z2;
  var zz = z * z2;
  var wx = w * x2;
  var wy = w * y2;
  var wz = w * z2;
  var sx = s[0];
  var sy = s[1];
  var sz = s[2];
  out[0] = (1 - (yy + zz)) * sx;
  out[1] = (xy + wz) * sx;
  out[2] = (xz - wy) * sx;
  out[3] = 0;
  out[4] = (xy - wz) * sy;
  out[5] = (1 - (xx + zz)) * sy;
  out[6] = (yz + wx) * sy;
  out[7] = 0;
  out[8] = (xz + wy) * sz;
  out[9] = (yz - wx) * sz;
  out[10] = (1 - (xx + yy)) * sz;
  out[11] = 0;
  out[12] = v[0];
  out[13] = v[1];
  out[14] = v[2];
  out[15] = 1;
  return out;
}
/**
 * Calculates a 4x4 matrix from the given quaternion
 *
 * @param {mat4} out mat4 receiving operation result
 * @param {ReadonlyQuat} q Quaternion to create matrix from
 *
 * @returns {mat4} out
 */

function fromQuat(out, q) {
  var x = q[0],
      y = q[1],
      z = q[2],
      w = q[3];
  var x2 = x + x;
  var y2 = y + y;
  var z2 = z + z;
  var xx = x * x2;
  var yx = y * x2;
  var yy = y * y2;
  var zx = z * x2;
  var zy = z * y2;
  var zz = z * z2;
  var wx = w * x2;
  var wy = w * y2;
  var wz = w * z2;
  out[0] = 1 - yy - zz;
  out[1] = yx + wz;
  out[2] = zx - wy;
  out[3] = 0;
  out[4] = yx - wz;
  out[5] = 1 - xx - zz;
  out[6] = zy + wx;
  out[7] = 0;
  out[8] = zx + wy;
  out[9] = zy - wx;
  out[10] = 1 - xx - yy;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
}
/**
 * Generates a orthogonal projection matrix with the given bounds.
 * The near/far clip planes correspond to a normalized device coordinate Z range of [-1, 1],
 * which matches WebGL/OpenGL's clip volume.
 *
 * @param {mat4} out mat4 frustum matrix will be written into
 * @param {number} left Left bound of the frustum
 * @param {number} right Right bound of the frustum
 * @param {number} bottom Bottom bound of the frustum
 * @param {number} top Top bound of the frustum
 * @param {number} near Near bound of the frustum
 * @param {number} far Far bound of the frustum
 * @returns {mat4} out
 */

function orthoNO(out, left, right, bottom, top, near, far) {
  var lr = 1 / (left - right);
  var bt = 1 / (bottom - top);
  var nf = 1 / (near - far);
  out[0] = -2 * lr;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = -2 * bt;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 2 * nf;
  out[11] = 0;
  out[12] = (left + right) * lr;
  out[13] = (top + bottom) * bt;
  out[14] = (far + near) * nf;
  out[15] = 1;
  return out;
}
/**
 * Alias for {@link mat4.orthoNO}
 * @function
 */

var ortho = orthoNO;
/**
 * Generates a orthogonal projection matrix with the given bounds.
 * The near/far clip planes correspond to a normalized device coordinate Z range of [0, 1],
 * which matches WebGPU/Vulkan/DirectX/Metal's clip volume.
 *
 * @param {mat4} out mat4 frustum matrix will be written into
 * @param {number} left Left bound of the frustum
 * @param {number} right Right bound of the frustum
 * @param {number} bottom Bottom bound of the frustum
 * @param {number} top Top bound of the frustum
 * @param {number} near Near bound of the frustum
 * @param {number} far Far bound of the frustum
 * @returns {mat4} out
 */

function orthoZO(out, left, right, bottom, top, near, far) {
  var lr = 1 / (left - right);
  var bt = 1 / (bottom - top);
  var nf = 1 / (near - far);
  out[0] = -2 * lr;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = -2 * bt;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = nf;
  out[11] = 0;
  out[12] = (left + right) * lr;
  out[13] = (top + bottom) * bt;
  out[14] = near * nf;
  out[15] = 1;
  return out;
}
/**
 * Generates a look-at matrix with the given eye position, focal point, and up axis.
 * If you want a matrix that actually makes an object look at another object, you should use targetTo instead.
 *
 * @param {mat4} out mat4 frustum matrix will be written into
 * @param {ReadonlyVec3} eye Position of the viewer
 * @param {ReadonlyVec3} center Point the viewer is looking at
 * @param {ReadonlyVec3} up vec3 pointing up
 * @returns {mat4} out
 */

function lookAt(out, eye, center, up) {
  var x0, x1, x2, y0, y1, y2, z0, z1, z2, len;
  var eyex = eye[0];
  var eyey = eye[1];
  var eyez = eye[2];
  var upx = up[0];
  var upy = up[1];
  var upz = up[2];
  var centerx = center[0];
  var centery = center[1];
  var centerz = center[2];

  if (Math.abs(eyex - centerx) < EPSILON$1 && Math.abs(eyey - centery) < EPSILON$1 && Math.abs(eyez - centerz) < EPSILON$1) {
    return identity(out);
  }

  z0 = eyex - centerx;
  z1 = eyey - centery;
  z2 = eyez - centerz;
  len = 1 / Math.hypot(z0, z1, z2);
  z0 *= len;
  z1 *= len;
  z2 *= len;
  x0 = upy * z2 - upz * z1;
  x1 = upz * z0 - upx * z2;
  x2 = upx * z1 - upy * z0;
  len = Math.hypot(x0, x1, x2);

  if (!len) {
    x0 = 0;
    x1 = 0;
    x2 = 0;
  } else {
    len = 1 / len;
    x0 *= len;
    x1 *= len;
    x2 *= len;
  }

  y0 = z1 * x2 - z2 * x1;
  y1 = z2 * x0 - z0 * x2;
  y2 = z0 * x1 - z1 * x0;
  len = Math.hypot(y0, y1, y2);

  if (!len) {
    y0 = 0;
    y1 = 0;
    y2 = 0;
  } else {
    len = 1 / len;
    y0 *= len;
    y1 *= len;
    y2 *= len;
  }

  out[0] = x0;
  out[1] = y0;
  out[2] = z0;
  out[3] = 0;
  out[4] = x1;
  out[5] = y1;
  out[6] = z1;
  out[7] = 0;
  out[8] = x2;
  out[9] = y2;
  out[10] = z2;
  out[11] = 0;
  out[12] = -(x0 * eyex + x1 * eyey + x2 * eyez);
  out[13] = -(y0 * eyex + y1 * eyey + y2 * eyez);
  out[14] = -(z0 * eyex + z1 * eyey + z2 * eyez);
  out[15] = 1;
  return out;
}

/**
 * 3 Dimensional Vector
 * @module vec3
 */

/**
 * Creates a new, empty vec3
 *
 * @returns {vec3} a new 3D vector
 */

function create$2() {
  var out = new ARRAY_TYPE(3);

  if (ARRAY_TYPE != Float32Array) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
  }

  return out;
}
/**
 * Creates a new vec3 initialized with values from an existing vector
 *
 * @param {ReadonlyVec3} a vector to clone
 * @returns {vec3} a new 3D vector
 */

function clone(a) {
  var out = new ARRAY_TYPE(3);
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  return out;
}
/**
 * Calculates the length of a vec3
 *
 * @param {ReadonlyVec3} a vector to calculate length of
 * @returns {Number} length of a
 */

function length$1(a) {
  var x = a[0];
  var y = a[1];
  var z = a[2];
  return Math.hypot(x, y, z);
}
/**
 * Creates a new vec3 initialized with the given values
 *
 * @param {Number} x X component
 * @param {Number} y Y component
 * @param {Number} z Z component
 * @returns {vec3} a new 3D vector
 */

function fromValues(x, y, z) {
  var out = new ARRAY_TYPE(3);
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}
/**
 * Copy the values from one vec3 to another
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the source vector
 * @returns {vec3} out
 */

function copy$2(out, a) {
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  return out;
}
/**
 * Set the components of a vec3 to the given values
 *
 * @param {vec3} out the receiving vector
 * @param {Number} x X component
 * @param {Number} y Y component
 * @param {Number} z Z component
 * @returns {vec3} out
 */

function set(out, x, y, z) {
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}
/**
 * Adds two vec3's
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the first operand
 * @param {ReadonlyVec3} b the second operand
 * @returns {vec3} out
 */

function add(out, a, b) {
  out[0] = a[0] + b[0];
  out[1] = a[1] + b[1];
  out[2] = a[2] + b[2];
  return out;
}
/**
 * Subtracts vector b from vector a
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the first operand
 * @param {ReadonlyVec3} b the second operand
 * @returns {vec3} out
 */

function subtract(out, a, b) {
  out[0] = a[0] - b[0];
  out[1] = a[1] - b[1];
  out[2] = a[2] - b[2];
  return out;
}
/**
 * Scales a vec3 by a scalar number
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the vector to scale
 * @param {Number} b amount to scale the vector by
 * @returns {vec3} out
 */

function scale(out, a, b) {
  out[0] = a[0] * b;
  out[1] = a[1] * b;
  out[2] = a[2] * b;
  return out;
}
/**
 * Normalize a vec3
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a vector to normalize
 * @returns {vec3} out
 */

function normalize$2(out, a) {
  var x = a[0];
  var y = a[1];
  var z = a[2];
  var len = x * x + y * y + z * z;

  if (len > 0) {
    //TODO: evaluate use of glm_invsqrt here?
    len = 1 / Math.sqrt(len);
  }

  out[0] = a[0] * len;
  out[1] = a[1] * len;
  out[2] = a[2] * len;
  return out;
}
/**
 * Calculates the dot product of two vec3's
 *
 * @param {ReadonlyVec3} a the first operand
 * @param {ReadonlyVec3} b the second operand
 * @returns {Number} dot product of a and b
 */

function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
/**
 * Computes the cross product of two vec3's
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the first operand
 * @param {ReadonlyVec3} b the second operand
 * @returns {vec3} out
 */

function cross(out, a, b) {
  var ax = a[0],
      ay = a[1],
      az = a[2];
  var bx = b[0],
      by = b[1],
      bz = b[2];
  out[0] = ay * bz - az * by;
  out[1] = az * bx - ax * bz;
  out[2] = ax * by - ay * bx;
  return out;
}
/**
 * Transforms the vec3 with a mat4.
 * 4th vector component is implicitly '1'
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the vector to transform
 * @param {ReadonlyMat4} m matrix to transform with
 * @returns {vec3} out
 */

function transformMat4$1(out, a, m) {
  var x = a[0],
      y = a[1],
      z = a[2];
  var w = m[3] * x + m[7] * y + m[11] * z + m[15];
  w = w || 1.0;
  out[0] = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
  out[1] = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
  out[2] = (m[2] * x + m[6] * y + m[10] * z + m[14]) / w;
  return out;
}
/**
 * Transforms the vec3 with a mat3.
 *
 * @param {vec3} out the receiving vector
 * @param {ReadonlyVec3} a the vector to transform
 * @param {ReadonlyMat3} m the 3x3 matrix to transform with
 * @returns {vec3} out
 */

function transformMat3(out, a, m) {
  var x = a[0],
      y = a[1],
      z = a[2];
  out[0] = x * m[0] + y * m[3] + z * m[6];
  out[1] = x * m[1] + y * m[4] + z * m[7];
  out[2] = x * m[2] + y * m[5] + z * m[8];
  return out;
}
/**
 * Returns whether or not the vectors have approximately the same elements in the same position.
 *
 * @param {ReadonlyVec3} a The first vector.
 * @param {ReadonlyVec3} b The second vector.
 * @returns {Boolean} True if the vectors are equal, false otherwise.
 */

function equals(a, b) {
  var a0 = a[0],
      a1 = a[1],
      a2 = a[2];
  var b0 = b[0],
      b1 = b[1],
      b2 = b[2];
  return Math.abs(a0 - b0) <= EPSILON$1 * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON$1 * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON$1 * Math.max(1.0, Math.abs(a2), Math.abs(b2));
}
/**
 * Alias for {@link vec3.length}
 * @function
 */

var len = length$1;
/**
 * Perform some operation over an array of vec3s.
 *
 * @param {Array} a the array of vectors to iterate over
 * @param {Number} stride Number of elements between the start of each vec3. If 0 assumes tightly packed
 * @param {Number} offset Number of elements to skip at the beginning of the array
 * @param {Number} count Number of vec3s to iterate over. If 0 iterates over entire array
 * @param {Function} fn Function to call for each vector in the array
 * @param {Object} [arg] additional argument to pass to fn
 * @returns {Array} a
 * @function
 */

(function () {
  var vec = create$2();
  return function (a, stride, offset, count, fn, arg) {
    var i, l;

    if (!stride) {
      stride = 3;
    }

    if (!offset) {
      offset = 0;
    }

    if (count) {
      l = Math.min(count * stride + offset, a.length);
    } else {
      l = a.length;
    }

    for (i = offset; i < l; i += stride) {
      vec[0] = a[i];
      vec[1] = a[i + 1];
      vec[2] = a[i + 2];
      fn(vec, vec, arg);
      a[i] = vec[0];
      a[i + 1] = vec[1];
      a[i + 2] = vec[2];
    }

    return a;
  };
})();

/**
 * 4 Dimensional Vector
 * @module vec4
 */

/**
 * Creates a new, empty vec4
 *
 * @returns {vec4} a new 4D vector
 */

function create$1() {
  var out = new ARRAY_TYPE(4);

  if (ARRAY_TYPE != Float32Array) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
  }

  return out;
}
/**
 * Copy the values from one vec4 to another
 *
 * @param {vec4} out the receiving vector
 * @param {ReadonlyVec4} a the source vector
 * @returns {vec4} out
 */

function copy$1(out, a) {
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  out[3] = a[3];
  return out;
}
/**
 * Normalize a vec4
 *
 * @param {vec4} out the receiving vector
 * @param {ReadonlyVec4} a vector to normalize
 * @returns {vec4} out
 */

function normalize$1(out, a) {
  var x = a[0];
  var y = a[1];
  var z = a[2];
  var w = a[3];
  var len = x * x + y * y + z * z + w * w;

  if (len > 0) {
    len = 1 / Math.sqrt(len);
  }

  out[0] = x * len;
  out[1] = y * len;
  out[2] = z * len;
  out[3] = w * len;
  return out;
}
/**
 * Transforms the vec4 with a mat4.
 *
 * @param {vec4} out the receiving vector
 * @param {ReadonlyVec4} a the vector to transform
 * @param {ReadonlyMat4} m matrix to transform with
 * @returns {vec4} out
 */

function transformMat4(out, a, m) {
  var x = a[0],
      y = a[1],
      z = a[2],
      w = a[3];
  out[0] = m[0] * x + m[4] * y + m[8] * z + m[12] * w;
  out[1] = m[1] * x + m[5] * y + m[9] * z + m[13] * w;
  out[2] = m[2] * x + m[6] * y + m[10] * z + m[14] * w;
  out[3] = m[3] * x + m[7] * y + m[11] * z + m[15] * w;
  return out;
}
/**
 * Perform some operation over an array of vec4s.
 *
 * @param {Array} a the array of vectors to iterate over
 * @param {Number} stride Number of elements between the start of each vec4. If 0 assumes tightly packed
 * @param {Number} offset Number of elements to skip at the beginning of the array
 * @param {Number} count Number of vec4s to iterate over. If 0 iterates over entire array
 * @param {Function} fn Function to call for each vector in the array
 * @param {Object} [arg] additional argument to pass to fn
 * @returns {Array} a
 * @function
 */

(function () {
  var vec = create$1();
  return function (a, stride, offset, count, fn, arg) {
    var i, l;

    if (!stride) {
      stride = 4;
    }

    if (!offset) {
      offset = 0;
    }

    if (count) {
      l = Math.min(count * stride + offset, a.length);
    } else {
      l = a.length;
    }

    for (i = offset; i < l; i += stride) {
      vec[0] = a[i];
      vec[1] = a[i + 1];
      vec[2] = a[i + 2];
      vec[3] = a[i + 3];
      fn(vec, vec, arg);
      a[i] = vec[0];
      a[i + 1] = vec[1];
      a[i + 2] = vec[2];
      a[i + 3] = vec[3];
    }

    return a;
  };
})();

/**
 * Quaternion
 * @module quat
 */

/**
 * Creates a new identity quat
 *
 * @returns {quat} a new quaternion
 */

function create() {
  var out = new ARRAY_TYPE(4);

  if (ARRAY_TYPE != Float32Array) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
  }

  out[3] = 1;
  return out;
}
/**
 * Sets a quat from the given angle and rotation axis,
 * then returns it.
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyVec3} axis the axis around which to rotate
 * @param {Number} rad the angle in radians
 * @returns {quat} out
 **/

function setAxisAngle(out, axis, rad) {
  rad = rad * 0.5;
  var s = Math.sin(rad);
  out[0] = s * axis[0];
  out[1] = s * axis[1];
  out[2] = s * axis[2];
  out[3] = Math.cos(rad);
  return out;
}
/**
 * Multiplies two quat's
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a the first operand
 * @param {ReadonlyQuat} b the second operand
 * @returns {quat} out
 */

function multiply(out, a, b) {
  var ax = a[0],
      ay = a[1],
      az = a[2],
      aw = a[3];
  var bx = b[0],
      by = b[1],
      bz = b[2],
      bw = b[3];
  out[0] = ax * bw + aw * bx + ay * bz - az * by;
  out[1] = ay * bw + aw * by + az * bx - ax * bz;
  out[2] = az * bw + aw * bz + ax * by - ay * bx;
  out[3] = aw * bw - ax * bx - ay * by - az * bz;
  return out;
}
/**
 * Performs a spherical linear interpolation between two quat
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a the first operand
 * @param {ReadonlyQuat} b the second operand
 * @param {Number} t interpolation amount, in the range [0-1], between the two inputs
 * @returns {quat} out
 */

function slerp(out, a, b, t) {
  // benchmarks:
  //    http://jsperf.com/quaternion-slerp-implementations
  var ax = a[0],
      ay = a[1],
      az = a[2],
      aw = a[3];
  var bx = b[0],
      by = b[1],
      bz = b[2],
      bw = b[3];
  var omega, cosom, sinom, scale0, scale1; // calc cosine

  cosom = ax * bx + ay * by + az * bz + aw * bw; // adjust signs (if necessary)

  if (cosom < 0.0) {
    cosom = -cosom;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  } // calculate coefficients


  if (1.0 - cosom > EPSILON$1) {
    // standard case (slerp)
    omega = Math.acos(cosom);
    sinom = Math.sin(omega);
    scale0 = Math.sin((1.0 - t) * omega) / sinom;
    scale1 = Math.sin(t * omega) / sinom;
  } else {
    // "from" and "to" quaternions are very close
    //  ... so we can do a linear interpolation
    scale0 = 1.0 - t;
    scale1 = t;
  } // calculate final values


  out[0] = scale0 * ax + scale1 * bx;
  out[1] = scale0 * ay + scale1 * by;
  out[2] = scale0 * az + scale1 * bz;
  out[3] = scale0 * aw + scale1 * bw;
  return out;
}
/**
 * Calculates the inverse of a quat
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a quat to calculate inverse of
 * @returns {quat} out
 */

function invert(out, a) {
  var a0 = a[0],
      a1 = a[1],
      a2 = a[2],
      a3 = a[3];
  var dot = a0 * a0 + a1 * a1 + a2 * a2 + a3 * a3;
  var invDot = dot ? 1.0 / dot : 0; // TODO: Would be faster to return [0,0,0,0] immediately if dot == 0

  out[0] = -a0 * invDot;
  out[1] = -a1 * invDot;
  out[2] = -a2 * invDot;
  out[3] = a3 * invDot;
  return out;
}
/**
 * Creates a quaternion from the given 3x3 rotation matrix.
 *
 * NOTE: The resultant quaternion is not normalized, so you should be sure
 * to renormalize the quaternion yourself where necessary.
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyMat3} m rotation matrix
 * @returns {quat} out
 * @function
 */

function fromMat3(out, m) {
  // Algorithm in Ken Shoemake's article in 1987 SIGGRAPH course notes
  // article "Quaternion Calculus and Fast Animation".
  var fTrace = m[0] + m[4] + m[8];
  var fRoot;

  if (fTrace > 0.0) {
    // |w| > 1/2, may as well choose w > 1/2
    fRoot = Math.sqrt(fTrace + 1.0); // 2w

    out[3] = 0.5 * fRoot;
    fRoot = 0.5 / fRoot; // 1/(4w)

    out[0] = (m[5] - m[7]) * fRoot;
    out[1] = (m[6] - m[2]) * fRoot;
    out[2] = (m[1] - m[3]) * fRoot;
  } else {
    // |w| <= 1/2
    var i = 0;
    if (m[4] > m[0]) i = 1;
    if (m[8] > m[i * 3 + i]) i = 2;
    var j = (i + 1) % 3;
    var k = (i + 2) % 3;
    fRoot = Math.sqrt(m[i * 3 + i] - m[j * 3 + j] - m[k * 3 + k] + 1.0);
    out[i] = 0.5 * fRoot;
    fRoot = 0.5 / fRoot;
    out[3] = (m[j * 3 + k] - m[k * 3 + j]) * fRoot;
    out[j] = (m[j * 3 + i] + m[i * 3 + j]) * fRoot;
    out[k] = (m[k * 3 + i] + m[i * 3 + k]) * fRoot;
  }

  return out;
}
/**
 * Copy the values from one quat to another
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a the source quaternion
 * @returns {quat} out
 * @function
 */

var copy = copy$1;
/**
 * Normalize a quat
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a quaternion to normalize
 * @returns {quat} out
 * @function
 */

var normalize = normalize$1;
/**
 * Sets a quaternion to represent the shortest rotation from one
 * vector to another.
 *
 * Both vectors are assumed to be unit length.
 *
 * @param {quat} out the receiving quaternion.
 * @param {ReadonlyVec3} a the initial vector
 * @param {ReadonlyVec3} b the destination vector
 * @returns {quat} out
 */

(function () {
  var tmpvec3 = create$2();
  var xUnitVec3 = fromValues(1, 0, 0);
  var yUnitVec3 = fromValues(0, 1, 0);
  return function (out, a, b) {
    var dot$1 = dot(a, b);

    if (dot$1 < -0.999999) {
      cross(tmpvec3, xUnitVec3, a);
      if (len(tmpvec3) < 0.000001) cross(tmpvec3, yUnitVec3, a);
      normalize$2(tmpvec3, tmpvec3);
      setAxisAngle(out, tmpvec3, Math.PI);
      return out;
    } else if (dot$1 > 0.999999) {
      out[0] = 0;
      out[1] = 0;
      out[2] = 0;
      out[3] = 1;
      return out;
    } else {
      cross(tmpvec3, a, b);
      out[0] = tmpvec3[0];
      out[1] = tmpvec3[1];
      out[2] = tmpvec3[2];
      out[3] = 1 + dot$1;
      return normalize(out, out);
    }
  };
})();
/**
 * Performs a spherical linear interpolation with two control points
 *
 * @param {quat} out the receiving quaternion
 * @param {ReadonlyQuat} a the first operand
 * @param {ReadonlyQuat} b the second operand
 * @param {ReadonlyQuat} c the third operand
 * @param {ReadonlyQuat} d the fourth operand
 * @param {Number} t interpolation amount, in the range [0-1], between the two inputs
 * @returns {quat} out
 */

(function () {
  var temp1 = create();
  var temp2 = create();
  return function (out, a, b, c, d, t) {
    slerp(temp1, a, d, t);
    slerp(temp2, b, c, t);
    slerp(out, temp1, temp2, 2 * t * (1 - t));
    return out;
  };
})();
/**
 * Sets the specified quaternion with values corresponding to the given
 * axes. Each axis is a vec3 and is expected to be unit length and
 * perpendicular to all other specified axes.
 *
 * @param {ReadonlyVec3} view  the vector representing the viewing direction
 * @param {ReadonlyVec3} right the vector representing the local "right" direction
 * @param {ReadonlyVec3} up    the vector representing the local "up" direction
 * @returns {quat} out
 */

(function () {
  var matr = create$4();
  return function (out, view, right, up) {
    matr[0] = right[0];
    matr[3] = right[1];
    matr[6] = right[2];
    matr[1] = up[0];
    matr[4] = up[1];
    matr[7] = up[2];
    matr[2] = -view[0];
    matr[5] = -view[1];
    matr[8] = -view[2];
    return normalize(out, fromMat3(out, matr));
  };
})();

class Point {
    x = 0;
    y = 0;
    constructor(x = 0, y = 0) {
        this.x = x;
        this.y = y;
    }
    /**
     * Creates a clone of this point
     * @returns A clone of this point
     */
    clone() {
        return new Point(this.x, this.y);
    }
    /**
     * Copies `x` and `y` from the given point into this point
     * @param p - The point to copy from
     * @returns The point instance itself
     */
    copyFrom(p) {
        this.set(p.x, p.y);
        return this;
    }
    copyTo(p) {
        p.set(this.x, this.y);
        return p;
    }
    equals(p) {
        return p.x === this.x && p.y === this.y;
    }
    set(x = 0, y = x) {
        this.x = x;
        this.y = y;
        return this;
    }
}

function isPointInShape(shape, point) {
    const x = point.x;
    const y = point.y;
    const n = shape.length;
    let res = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const p1 = shape[j];
        const p2 = shape[i];
        // assume line's direction is up to +y
        const x_ok = p1.x > x !== p2.x > x;
        // (y - y1) / (y2 - y1) = (pos_x - x1) / (x2 - x1)
        // y = (pos_x - x1) * (y2 - y1) / (x2 - x1) + y1
        const k = (p2.y - p1.y) / (p2.x - p1.x);
        const tempY = (x - p1.x) * k + p1.y;
        const y_ok = tempY > y;
        if (x_ok && y_ok) {
            res = !res;
        }
    }
    return res;
}
function length(x, y) {
    return Math.sqrt(x * x + y * y);
}
function distance(v1, v2) {
    const dx = v2.x - v1.x;
    const dy = v2.y - v1.y;
    return Math.sqrt(dx * dx + dy * dy);
}

function isUndefined(value) {
    return typeof value === 'undefined';
}
function isNil(v) {
    return v === null || v === undefined;
}
function isNumber(value) {
    if (typeof value === 'number') {
        return !isNaN(value);
    }
    return false;
}
function isString(value) {
    return typeof value === 'string';
}
function isObject(data) {
    return Object.prototype.toString.call(data) === '[object Object]';
}
function isArray(arr) {
    return Array.isArray(arr);
}

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
function deg2rad(deg) {
    return deg * DEG2RAD;
}
function rad2deg(rad) {
    return rad * RAD2DEG;
}
function getAngle(angle) {
    if (angle === undefined) {
        return 0;
    }
    else if (angle > 360 || angle < -360) {
        return angle % 360;
    }
    return angle;
}
function createVec3(x, y = 0, z = 0) {
    if (Array.isArray(x) && x.length === 3) {
        return fromValues(x[0], x[1], x[2]);
    }
    if (isNumber(x)) {
        return fromValues(x, y, z);
    }
    return fromValues(x[0] || 0, x[1] || y, x[2] || z);
}
/**
 * assume z from -1 to 1 (NDC), OpenGl (Y up, right hands,camera in +z look to -z)
  [
  (2n) / (r - l),      0,             (r + l) / (r - l),          0,
  0,               (2n) / (t - b),    (t + b) / (t - b),          0,
  0,                   0,            -(f + n) / (f - n),   -(2f * n) / (f - n),
  0,                   0,                   -1,                   0
  ]
 */
function makePerspective(out, left, right, top, bottom, near, far, zero) {
    const lf = right - left;
    const tb = top - bottom;
    const nf = far - near;
    let c;
    let d;
    if (zero) {
        c = -far / nf;
        d = (-far * near) / nf;
    }
    else {
        c = -(far + near) / nf;
        d = (-2 * far * near) / nf;
    }
    out[0] = (2 * near) / lf;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    out[4] = 0;
    out[5] = (2 * near) / tb;
    out[6] = 0;
    out[7] = 0;
    out[8] = (right + left) / lf;
    out[9] = (top + bottom) / tb;
    out[10] = c;
    out[11] = -1;
    out[12] = 0;
    out[13] = 0;
    out[14] = d;
    out[15] = 0;
    return out;
}
/**
 * assume z from -1 to 1 (NDC)
  [
  2 / (r - l),         0,             0           -(l + r) / (r - l),
  0,               2 / (t - b),       0,          -(t + b) / (t - b),
  0,                   0,         -2 / (f - n),   -(n + f) / (f - n),
  0,                   0,             0,                  1
  ]
 */
function makeOrthoGraphic(out, left, right, top, bottom, near, far, zero) {
    if (zero) {
        orthoZO(out, left, right, bottom, top, near, far);
    }
    else {
        ortho(out, left, right, bottom, top, near, far);
    }
    return out;
}
function clamp(num, min, max) {
    if (min > max) {
        const a = min;
        min = max;
        max = a;
    }
    let res = num;
    res = Math.min(res, max);
    res = Math.max(res, min);
    return res;
}

/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
const getGlobalThis = () => {
    if (typeof self !== 'undefined')
        return self;
    if (typeof window !== 'undefined')
        return window;
    // @ts-ignore
    if (typeof global !== 'undefined')
        return global;
    return {};
};
const runtimeGlobalThis = getGlobalThis();
const userAgent = runtimeGlobalThis.navigator
    ? runtimeGlobalThis.navigator.userAgent.toLowerCase()
    : '';
const isMac = userAgent.includes('mac');
userAgent.includes('win');
const isCtrlKey = (e) => isMac ? e.metaKey : e.ctrlKey;
/**
 * https://gist.github.com/1866474
 * https://github.com/Financial-Times/polyfill-library/blob/master/polyfills/requestAnimationFrame/polyfill.js
 **/
let uId = 1;
const uniqueId = () => uId++;
const nowOffset = Date.now();
// use performance api if exist, otherwise use Date.now.
// Date.now polyfill required.
const pnow = () => {
    if (runtimeGlobalThis.performance && typeof runtimeGlobalThis.performance.now === 'function') {
        return runtimeGlobalThis.performance.now();
    }
    // fallback
    return Date.now() - nowOffset;
};
let reservedCBs = {};
let lastTime = Date.now();
const polyfillRaf = (callback) => {
    if (typeof callback !== 'function') {
        throw new TypeError(callback + ' is not a function');
    }
    const currentTime = Date.now();
    const gap = currentTime - lastTime;
    const delay = gap > 16 ? 0 : 16 - gap;
    const id = uniqueId();
    reservedCBs[id] = callback;
    // has callback existing
    if (Object.keys(reservedCBs).length > 1)
        return id;
    setTimeout(() => {
        lastTime = currentTime;
        const copied = reservedCBs;
        reservedCBs = {};
        Object.keys(copied).forEach((key) => copied[key](pnow()));
    }, delay);
    return id;
};
const polyfillCaf = (id) => {
    delete reservedCBs[id];
};
const vendorPrefixes = ['', 'webkit', 'moz', 'ms', 'o'];
const getRequestAnimationFrame = (vp) => {
    if (typeof vp !== 'string')
        return polyfillRaf;
    if (vp === '')
        return runtimeGlobalThis['requestAnimationFrame'];
    return runtimeGlobalThis[vp + 'RequestAnimationFrame'];
};
const getCancelAnimationFrame = (vp) => {
    if (typeof vp !== 'string')
        return polyfillCaf;
    if (vp === '')
        return runtimeGlobalThis['cancelAnimationFrame'];
    return (runtimeGlobalThis[vp + 'CancelAnimationFrame'] ||
        runtimeGlobalThis[vp + 'CancelRequestAnimationFrame']);
};
const find = (arr, predicate) => {
    let i = 0;
    while (arr[i] !== void 0) {
        if (predicate(arr[i]))
            return arr[i];
        i = i + 1;
    }
};
const vp$1 = find(vendorPrefixes, (vp) => !!getRequestAnimationFrame(vp));
const raf = getRequestAnimationFrame(vp$1);
const caf = getCancelAnimationFrame(vp$1);
runtimeGlobalThis.requestAnimationFrame = raf.bind(runtimeGlobalThis);
runtimeGlobalThis.cancelAnimationFrame = caf.bind(runtimeGlobalThis);

function debounce(func, wait, options) {
    let result;
    let timerId;
    let lastCallTime;
    let lastInvokeTime = 0;
    let leading = false;
    let trailing = true;
    let lastArgs;
    wait = +wait || 0;
    leading = !!options?.leading;
    const maxWait = Math.max(+options.maxWait, wait) ;
    trailing = !!options?.trailing;
    const invokeFunc = (time) => {
        const args = lastArgs;
        lastArgs = undefined;
        lastInvokeTime = time;
        result = func(...args);
        return result;
    };
    const startTimer = (pendingFunc, milliseconds) => {
        return window.setTimeout(pendingFunc, milliseconds);
    };
    const cancelTimer = (id) => {
        window.clearTimeout(id);
    };
    const leadingEdge = (time) => {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = startTimer(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
    };
    const remainingWait = (time) => {
        if (lastCallTime === undefined)
            return 0;
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;
        return maxWait !== undefined
            ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
            : timeWaiting;
    };
    const shouldInvoke = (time) => {
        if (lastCallTime === undefined)
            return true;
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxWait !== undefined && timeSinceLastInvoke >= maxWait));
    };
    const timerExpired = () => {
        const time = Date.now();
        if (shouldInvoke(time)) {
            return trailingEdge(time);
        }
        // Restart the timer.
        timerId = startTimer(timerExpired, remainingWait(time));
        return undefined;
    };
    const trailingEdge = (time) => {
        timerId = undefined;
        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = undefined;
        return result;
    };
    const cancel = () => {
        if (timerId !== undefined) {
            cancelTimer(timerId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = timerId = undefined;
    };
    const flush = () => {
        return timerId === undefined ? result : trailingEdge(Date.now());
    };
    const pending = () => {
        return timerId !== undefined;
    };
    const debounced = (...args) => {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);
        lastArgs = args;
        lastCallTime = time;
        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (maxWait !== undefined) {
                // Handle invocations in a tight loop.
                timerId = startTimer(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timerId === undefined) {
            timerId = startTimer(timerExpired, wait);
        }
        return result;
    };
    debounced.cancel = cancel;
    debounced.flush = flush;
    debounced.pending = pending;
    return debounced;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function merge(target, ...args) {
    return args.reduce((acc, cur) => {
        if (isNil(cur))
            return acc;
        return Object.keys(cur).reduce((subAcc, key) => {
            const srcVal = cur[key];
            if (isObject(srcVal)) {
                const tarAccVal = subAcc[key];
                const isObj = tarAccVal && isObject(tarAccVal);
                // merge only if both target and src is object
                subAcc[key] = merge(isObj ? tarAccVal : {}, srcVal);
            }
            else if (isArray(srcVal)) {
                subAcc[key] = srcVal.map((item, idx) => {
                    // merge object into ?
                    if (isObject(item)) {
                        const tarAccVal = subAcc[key];
                        // into array
                        const isArr = isArray(tarAccVal) && tarAccVal[idx] !== undefined;
                        // into {} if tarAccVal[idx] is not object
                        return merge(isArr && isObject(tarAccVal[idx]) ? tarAccVal[idx] : {}, item);
                    }
                    else {
                        return item;
                    }
                });
            }
            else {
                srcVal !== undefined && (subAcc[key] = srcVal);
            }
            return subAcc;
        }, acc);
    }, target);
}

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4$1(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  return unsafeStringify(rnds);
}

const genUUID = (prefix = '') => {
    return prefix + v4$1();
};

function throttle(func, wait, options) {
    let leading = true;
    let trailing = true;
    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }
    leading = !!options?.leading;
    trailing = !!options?.trailing;
    return debounce(func, wait, {
        leading,
        trailing,
        maxWait: wait,
    });
}

const MIN_DISTANCE = 0.0002;
class Camera {
    eventEmitter = new EventEmitter();
    canvas;
    clipSpaceNearZ = ClipSpaceNearZ.NEGATIVE_ONE;
    /** true - webgl, false - Canvas2D */
    flipY = false;
    type = CameraType.EXPLORING;
    /**
     * works only when camera type is Tracking.
     */
    trackingMode = CameraTrackingMode.DEFAULT;
    projectionMode = CameraProjectionMode.ORTHOGRAPHIC;
    /**
     * matrix of camera.
     */
    matrix = create$3();
    near = 0.1;
    far = 10000;
    /**
     * vertical field of view angle
     */
    fov = 30;
    /**
     * height : width
     */
    aspect = 1;
    /**
     * Orthographic
     */
    boxLeft = 0;
    boxRight = 0;
    boxTop = 0;
    boxBottom = 0;
    /** vec2 for orthographic, number for perspective (vec2[0]). */
    zoom = [1, 1];
    // Axes (left hands)
    /**
     * u axis / +x / right
     */
    right = fromValues(1, 0, 0);
    /**
     * v axis / +y / up
     */
    up = fromValues(0, 1, 0);
    /**
     * n axis / +z / inside
     */
    forward = fromValues(0, 0, 1);
    /**
     * position of camera
     */
    position = fromValues(0, 0, 1);
    /**
     * position of focal point
     */
    focalPoint = fromValues(0, 0, 0);
    /**
     * vector from camera's position to focalPoint
     */
    distanceVector = fromValues(0, 0, -1);
    /**
     * length(distanceVector)
     */
    distance = 1;
    // Angles
    /**
     * - Camera posZ > focalPoint posZ is 0
     * - CameraType.TRACKING - Increasing anti-clockwise in HCS
     * - CameraType.EXPLORING and ORBITING - Increasing clockwise in HCS (when rotate world = false)
     * - CameraType.EXPLORING's rotate world flag is true by default, so it will increasing anti-clockwise like CameraType.TRACKING
     */
    azimuth = 0;
    elevation = 0;
    roll = 0;
    dollyingStep = 0;
    maxDistance = Infinity;
    minDistance = -Infinity;
    /**
     * invert the horizontal coordinate system (HCS)
     */
    rotateWorld = false;
    landmarks = [];
    landmarkAnimationID = undefined;
    projectionMatrix = create$3();
    projectionMatrixInverse = create$3();
    orthographicMatrix = create$3();
    /**
     * @see https://threejs.org/docs/#api/en/cameras/OrthographicCamera
     */
    view = {
        enabled: true,
        fullWidth: 1,
        fullHeight: 1,
        offsetX: 0,
        offsetY: 0,
        width: 1,
        height: 1,
    };
    constructor(canvas) {
        this.canvas = canvas;
    }
    isOrthographic = () => this.projectionMode === CameraProjectionMode.ORTHOGRAPHIC;
    isPerspective = () => this.projectionMode === CameraProjectionMode.PERSPECTIVE;
    get BoxLeft() {
        return this.boxLeft;
    }
    get BoxRight() {
        return this.boxRight;
    }
    get BoxTop() {
        return this.boxTop;
    }
    get BoxBottom() {
        return this.boxBottom;
    }
    get Type() {
        return this.type;
    }
    get FlipY() {
        return this.flipY;
    }
    get ProjectionMode() {
        return this.projectionMode;
    }
    get ProjectionMatrix() {
        return this.projectionMatrix;
    }
    get ProjectionInverse() {
        return this.projectionMatrixInverse;
    }
    get OrthographicMatrix() {
        return this.orthographicMatrix;
    }
    get ViewTransform() {
        return invert$1(create$3(), this.matrix);
    }
    get WorldTransform() {
        return this.matrix;
    }
    /**
     * not support for modified, used for unit test.
     */
    get Up() {
        return this.up;
    }
    /**
     * not support for modified, used for unit test.
     */
    get Right() {
        return this.right;
    }
    /**
     * not support for modified, used for unit test.
     */
    get Forward() {
        return this.forward;
    }
    get Position() {
        return this.position;
    }
    get FocalPoint() {
        return this.focalPoint;
    }
    get Distance() {
        return this.distance;
    }
    get DistanceVector() {
        return this.distanceVector;
    }
    get DollyingStep() {
        return this.dollyingStep;
    }
    get Near() {
        return this.near;
    }
    get Far() {
        return this.far;
    }
    get Zoom() {
        return this.zoom;
    }
    get Azimuth() {
        return this.azimuth;
    }
    get Elevation() {
        return this.elevation;
    }
    get Roll() {
        return this.roll;
    }
    get View() {
        return this.view;
    }
    setFlipY = (bool) => {
        this.flipY = bool;
        this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, this.near, this.far);
    };
    setType = (type, trackingMode) => {
        this.type = type;
        if (this.type === CameraType.EXPLORING) {
            this.setWorldRotation(true);
        }
        else {
            this.setWorldRotation(false);
        }
        this._getAngles();
        if (this.type === CameraType.TRACKING && trackingMode !== undefined) {
            this.setTrackingMode(trackingMode);
        }
        return this;
    };
    setTrackingMode = (trackingMode) => {
        if (this.type !== CameraType.TRACKING) {
            throw new Error('Impossible to set a tracking mode if the camera is not of tracking type');
        }
        this.trackingMode = trackingMode;
        return this;
    };
    setProjectionMode = (projectionMode) => {
        this.projectionMode = projectionMode;
        return this;
    };
    /**
     * If flag is true, it reverses the azimuth and elevation angles.
     * Subsequent calls to rotate, setAzimuth, setElevation,
     * changeAzimuth or changeElevation will cause the inverted effect.
     * setRoll or changeRoll is not affected by this method.
     *
     * This inversion is useful when one wants to simulate that the world
     * is moving, instead of the camera.
     *
     * By default the camera angles are not reversed.
     * @param{Boolean} flag the boolean flag to reverse the angles.
     */
    setWorldRotation = (flag) => {
        this.rotateWorld = flag;
        this._getAngles();
        return this;
    };
    /**
     * Set camera's matrix
     */
    setMatrix = (matrix) => {
        this.matrix = matrix;
        this._update();
        this._getOrthoMatrix();
        return this;
    };
    setFov = (fov) => {
        this.setPerspective(this.near, this.far, fov, this.aspect);
        return this;
    };
    setAspect = (aspect) => {
        this.setPerspective(this.near, this.far, this.fov, aspect);
        return this;
    };
    setNear = (near) => {
        if (this.projectionMode === CameraProjectionMode.PERSPECTIVE) {
            this.setPerspective(near, this.far, this.fov, this.aspect);
        }
        else {
            this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, near, this.far);
        }
        return this;
    };
    setFar = (far) => {
        if (this.projectionMode === CameraProjectionMode.PERSPECTIVE) {
            this.setPerspective(this.near, far, this.fov, this.aspect);
        }
        else {
            this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, this.near, far);
        }
        return this;
    };
    /**
     * Sets an offset in a larger frustum. This is useful for multi-window or
     * multi-monitor/multi-machine setups.
     *
     * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
     * the monitors are in grid like this
     *
     *   +---+---+---+
     *   | A | B | C |
     *   +---+---+---+
     *   | D | E | F |
     *   +---+---+---+
     *
     * then for each monitor you would call it like this
     *
     *   const w = 1920;
     *   const h = 1080;
     *   const fullWidth = w * 3;
     *   const fullHeight = h * 2;
     *
     *   --A--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
     *   --B--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
     *   --C--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
     *   --D--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
     *   --E--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
     *   --F--
     *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
     *
     *   Note there is no reason monitors have to be the same size or in a grid.
     */
    setViewOffset(fullWidth, fullHeight, x, y, width, height) {
        this.aspect = fullWidth / fullHeight;
        this.view.enabled = true;
        this.view.fullWidth = fullWidth;
        this.view.fullHeight = fullHeight;
        this.view.offsetX = x;
        this.view.offsetY = y;
        this.view.width = width;
        this.view.height = height;
        if (this.projectionMode === CameraProjectionMode.PERSPECTIVE) {
            this.setPerspective(this.near, this.far, this.fov, this.aspect);
        }
        else {
            this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, this.near, this.far);
        }
        return this;
    }
    clearViewOffset = () => {
        if (this.view) {
            this.view.enabled = false;
        }
        if (this.projectionMode === CameraProjectionMode.PERSPECTIVE) {
            this.setPerspective(this.near, this.far, this.fov, this.aspect);
        }
        else {
            this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, this.near, this.far);
        }
        return this;
    };
    setZoom = (zoom) => {
        this.zoom = zoom;
        if (this.projectionMode === CameraProjectionMode.PERSPECTIVE) {
            this.setPerspective(this.near, this.far, this.fov, this.aspect);
        }
        else {
            this.setOrthographic(this.boxLeft, this.boxRight, this.boxTop, this.boxBottom, this.near, this.far);
        }
        this.eventEmitter.emit(CameraEvent.ZOOMED);
        return this;
    };
    /** not make change of camera's viewbox */
    setZoomByViewportPoint = (zoom, viewportPoint) => {
        const { x, y } = this.canvas.viewport2Canvas({ x: viewportPoint[0], y: viewportPoint[1] });
        const roll = this.roll;
        this.rotate(0, 0, -roll);
        this._setPosition(x, y, this.position[2]);
        this.setFocalPoint(x, y, this.focalPoint[2]);
        this.setZoom(zoom);
        this.rotate(0, 0, roll);
        return this;
    };
    /** change the viewbox to be focus to viewportPoint */
    setZoomByScroll = (zoom, viewportPoint) => {
        const { x, y } = this.canvas.viewport2Canvas({ x: viewportPoint[0], y: viewportPoint[1] });
        const absl = this.position[0] + this.boxLeft / zoom[0];
        const absr = this.position[0] + this.boxRight / zoom[0];
        const abst = this.position[1] + this.boxTop / zoom[1];
        const absb = this.position[1] + this.boxBottom / zoom[1];
        const roll = this.roll;
        this.rotate(0, 0, -roll);
        this._setPosition(x, y, this.position[2]);
        this.setFocalPoint(x, y, this.focalPoint[2]);
        this.setZoom(zoom);
        this.rotate(0, 0, roll);
        const l = (absl - this.position[0]) * zoom[0];
        const r = (absr - this.position[0]) * zoom[0];
        const t = (abst - this.position[1]) * zoom[1];
        const b = (absb - this.position[1]) * zoom[1];
        this.setOrthographic(l, r, t, b, this.near, this.far);
        return this;
    };
    setPerspective = (near, far, fov, aspect) => {
        this.projectionMode = CameraProjectionMode.PERSPECTIVE;
        this.fov = fov;
        this.near = near;
        this.far = far;
        this.aspect = aspect;
        let top = (this.near * Math.tan(deg2rad(0.5 * this.fov))) / this.zoom[0];
        let height = 2 * top;
        let width = this.aspect * height;
        let left = -0.5 * width;
        if (this.view?.enabled) {
            const fullWidth = this.view.fullWidth;
            const fullHeight = this.view.fullHeight;
            left += (this.view.offsetX * width) / fullWidth;
            top -= (this.view.offsetY * height) / fullHeight;
            width *= this.view.width / fullWidth;
            height *= this.view.height / fullHeight;
        }
        makePerspective(this.projectionMatrix, left, left + width, top, top - height, near, far, this.clipSpaceNearZ === ClipSpaceNearZ.ZERO);
        invert$1(this.projectionMatrixInverse, this.projectionMatrix);
        this.triggerUpdate();
        return this;
    };
    setOrthographic = (l, r, t, b, near = this.near, far = this.far) => {
        this.projectionMode = CameraProjectionMode.ORTHOGRAPHIC;
        this.boxRight = r;
        this.boxLeft = l;
        this.boxTop = t;
        this.boxBottom = b;
        this.near = near;
        this.far = far;
        let left = this.boxLeft / this.zoom[0];
        let right = this.boxRight / this.zoom[0];
        let top = this.boxTop / this.zoom[1];
        let bottom = this.boxBottom / this.zoom[1];
        // console.log(
        //   'before setOrthographic, left=%s, right=%s, top=%s bottom=%s',
        //   l + this.position[0],
        //   r + this.position[0],
        //   t + this.position[1],
        //   b + this.position[1],
        // );
        // TODO: support view
        if (this.view?.enabled) {
            const scaleW = (this.boxRight - this.boxLeft) / this.view.fullWidth / this.zoom[0];
            const scaleH = (this.boxTop - this.boxBottom) / this.view.fullHeight / this.zoom[1];
            left += scaleW * this.view.offsetX;
            right = left + scaleW * this.view.width;
            top -= scaleH * this.view.offsetY;
            bottom = top - scaleH * this.view.height;
        }
        makeOrthoGraphic(this.projectionMatrix, left, right, top, bottom, near, far, this.clipSpaceNearZ === ClipSpaceNearZ.ZERO);
        invert$1(this.projectionMatrixInverse, this.projectionMatrix);
        this._getOrthoMatrix();
        this.triggerUpdate();
        return this;
    };
    setPosition = (x, y, z = this.position[2]) => {
        this._setPosition(x, y, z);
        this.setFocalPoint(this.focalPoint);
        return this;
    };
    /**
     * Looks at a given point in space (sets the focal point of this camera).
     *
     * Note: If the camera is doing cinematic tracking, the up vector will be affected.
     */
    setFocalPoint = (x, y, z = this.focalPoint[2]) => {
        let up = fromValues(0, 1, 0);
        this.focalPoint = createVec3(x, y, z);
        if (this.trackingMode === CameraTrackingMode.CINEMATIC) {
            const d = subtract(create$2(), this.focalPoint, this.position);
            x = d[0];
            y = d[1];
            z = d[2];
            const r = length$1(d);
            const el = rad2deg(Math.asin(y / r));
            const az = 90 + rad2deg(Math.atan2(z, x));
            const m = create$3();
            rotateY(m, m, deg2rad(az));
            rotateX(m, m, deg2rad(el));
            up = transformMat4$1(create$2(), [0, 1, 0], m);
        }
        invert$1(this.matrix, lookAt(create$3(), this.position, this.focalPoint, up));
        this._getAxes();
        this._getDistance();
        this._getAngles();
        this.triggerUpdate();
        return this;
    };
    setDistance = (d) => {
        if (this.distance === d || d < 0)
            return this;
        this.distance = d;
        if (this.distance < MIN_DISTANCE)
            this.distance = MIN_DISTANCE;
        this.dollyingStep = this.distance / 100;
        const pos = create$2();
        d = this.distance;
        const n = this.forward;
        const f = this.focalPoint;
        pos[0] = d * n[0] + f[0];
        pos[1] = d * n[1] + f[1];
        pos[2] = d * n[2] + f[2];
        this._setPosition(pos);
        this.triggerUpdate();
        return this;
    };
    setMaxDistance = (d) => {
        this.maxDistance = d;
        return this;
    };
    setMinDistance = (d) => {
        this.minDistance = d;
        return this;
    };
    setAzimuth = (az) => {
        this.azimuth = getAngle(az);
        this._computeMatrix();
        this._getAxes();
        if (this.type === CameraType.ORBITING || this.type === CameraType.EXPLORING) {
            this._getPosition();
        }
        else if (this.type === CameraType.TRACKING) {
            this._getFocalPoint();
        }
        this.triggerUpdate();
        return this;
    };
    setElevation = (el) => {
        this.elevation = getAngle(el);
        this._computeMatrix();
        this._getAxes();
        if (this.type === CameraType.ORBITING || this.type === CameraType.EXPLORING) {
            this._getPosition();
        }
        else if (this.type === CameraType.TRACKING) {
            this._getFocalPoint();
        }
        this.triggerUpdate();
        return this;
    };
    setRoll = (angle) => {
        this.roll = getAngle(angle);
        this._computeMatrix();
        this._getAxes();
        if (this.type === CameraType.ORBITING || this.type === CameraType.EXPLORING) {
            this._getPosition();
        }
        else if (this.type === CameraType.TRACKING) {
            this._getFocalPoint();
        }
        this.triggerUpdate();
        return this;
    };
    /**
     * Changes the azimuth and elevation relatively with respect to the current camera axes
     */
    rotate = (azimuth, elevation, roll) => {
        if (this.type === CameraType.EXPLORING) {
            // FIXME refactor. From nucleo.js, but it's definitely wrong when you rotate roll and azimuth.
            const relAzimuth = getAngle(azimuth);
            const relElevation = getAngle(elevation);
            const relRoll = getAngle(roll);
            this.roll += relRoll;
            const rotX = setAxisAngle(create(), [1, 0, 0], deg2rad(this.rotateWorld ? 1 : -1) * relElevation);
            const rotY = setAxisAngle(create(), [0, 1, 0], deg2rad(this.rotateWorld ? 1 : -1) * relAzimuth);
            const rotZ = setAxisAngle(create(), [0, 0, 1], deg2rad(relRoll));
            let rotQ = multiply(create(), rotY, rotX);
            rotQ = multiply(create(), rotQ, rotZ);
            const rotMatrix = fromQuat(create$3(), rotQ);
            translate(this.matrix, this.matrix, [0, 0, -this.distance]);
            multiply$1(this.matrix, this.matrix, rotMatrix);
            translate(this.matrix, this.matrix, [0, 0, this.distance]);
        }
        else if (this.type === CameraType.ORBITING || this.type === CameraType.TRACKING) {
            if (Math.abs(this.elevation + elevation) > 90)
                return this;
            const relAzimuth = getAngle(azimuth);
            const relElevation = getAngle(elevation);
            const relRoll = getAngle(roll);
            this.elevation += relElevation;
            this.azimuth += relAzimuth;
            this.roll += relRoll;
            this._computeMatrix();
        }
        this._getAxes();
        if (this.type === CameraType.ORBITING || this.type === CameraType.EXPLORING) {
            this._getPosition();
        }
        else if (this.type === CameraType.TRACKING) {
            this._getFocalPoint();
        }
        this._update();
        return this;
    };
    /**
     * Translate camera
     */
    translate = (x, y = 0, z = 0) => {
        const coords = createVec3(x, y, z);
        const pos = clone(this.position);
        add(pos, pos, coords);
        this._setPosition(pos);
        this.triggerUpdate();
        return this;
    };
    /**
     * Translate camera left to right and up to down.
     */
    pan = (tx, ty) => {
        const coords = createVec3(tx / this.zoom[0], ty / this.zoom[1]);
        const pos = clone(this.position);
        add(pos, pos, scale(create$2(), this.right, coords[0]));
        add(pos, pos, scale(create$2(), this.up, coords[1]));
        this._setPosition(pos);
        this.triggerUpdate();
        return this;
    };
    /**
     * Move along the camera n axis.
     */
    dolly = (value) => {
        const n = this.forward;
        const pos = clone(this.position);
        const step = value * this.dollyingStep;
        const clampedStep = clamp(this.distance + step, this.minDistance, this.maxDistance) - this.distance;
        pos[0] += clampedStep * n[0];
        pos[1] += clampedStep * n[1];
        pos[2] += clampedStep * n[2];
        this._setPosition(pos);
        if (this.type === CameraType.ORBITING || this.type === CameraType.EXPLORING) {
            this._getDistance();
        }
        else if (this.type === CameraType.TRACKING) {
            add(this.focalPoint, pos, this.distanceVector);
        }
        this.triggerUpdate();
        return this;
    };
    // updateWithActor = (actor: DisplayObject) => {
    //   if (this.following != actor) return; //fail safe
    //   switch (this.trackingMode) {
    //     case CameraTrackingMode.DEFAULT:
    //       break;
    //     case CameraTrackingMode.ROTATIONAL:
    //     case CameraTrackingMode.CINEMATIC:
    //       this.setFocalPoint(actor.position);
    //       break;
    //     case CameraTrackingMode.TRANSLATIONAL:
    //       this.translate(actor.rotation);
    //       this.setFocalPoint(actor.position);
    //       break;
    //   }
    // };
    createLandmark = (name, params = {}) => {
        const { position = this.position, focalPoint = this.focalPoint, zoom = this.zoom, roll = this.roll, } = params;
        const camera = new Camera(this.canvas);
        camera.setType(this.type);
        camera.setPosition(position[0], position[1], position[2] || this.position[2]);
        camera.setFocalPoint(focalPoint[0], focalPoint[1], focalPoint[2] || this.focalPoint[2]);
        camera.setRoll(roll);
        camera.setZoom(zoom);
        const landmark = {
            name,
            matrix: clone$1(camera.WorldTransform),
            right: clone(camera.right),
            up: clone(camera.up),
            forward: clone(camera.forward),
            position: clone(camera.position),
            focalPoint: clone(camera.focalPoint),
            distanceVector: clone(camera.distanceVector),
            distance: camera.distance,
            dollyingStep: camera.dollyingStep,
            azimuth: camera.azimuth,
            elevation: camera.elevation,
            roll: camera.roll,
            zoom: camera.zoom,
        };
        this.landmarks.push(landmark);
        return landmark;
    };
    gotoLandmark = (name, options = {}) => {
        // TODO
    };
    /**
     * Stop camera animation immediately.
     */
    cancelLandmarkAnimation = () => {
        if (this.landmarkAnimationID !== undefined) {
            cancelAnimationFrame(this.landmarkAnimationID);
        }
    };
    /**
     * Recalculate properties from current matrix.
     */
    _update = () => {
        this._getAxes();
        this._getPosition();
        this._getDistance();
        this._getAngles();
        this.triggerUpdate();
    };
    /**
     * Recalculates euler angles based on the current distanceVector
     */
    _getAngles = () => {
        // recalculates angles
        const x = this.distanceVector[0];
        const y = this.distanceVector[1];
        const z = this.distanceVector[2];
        const r = length$1(this.distanceVector);
        //FAST FAIL: If there is no distance we cannot compute angles
        if (r === 0) {
            this.elevation = 0;
            this.azimuth = 0;
        }
        if (this.type === CameraType.TRACKING) {
            this.elevation = rad2deg(Math.asin(y / r));
            this.azimuth = rad2deg(Math.atan2(-x, -z));
        }
        else {
            if (this.rotateWorld) {
                this.elevation = rad2deg(Math.asin(y / r));
                this.azimuth = rad2deg(Math.atan2(-x, -z));
            }
            else {
                this.elevation = -rad2deg(Math.asin(y / r));
                this.azimuth = -rad2deg(Math.atan2(-x, -z));
            }
        }
    };
    /**
     * Recalculates axes based on the current matrix
     */
    _getAxes = () => {
        const m = this.matrix;
        copy$2(this.right, createVec3(transformMat4(create$1(), [1, 0, 0, 0], m)));
        copy$2(this.up, createVec3(transformMat4(create$1(), [0, 1, 0, 0], m)));
        copy$2(this.forward, createVec3(transformMat4(create$1(), [0, 0, 1, 0], m)));
        normalize$2(this.right, this.right);
        normalize$2(this.up, this.up);
        normalize$2(this.forward, this.forward);
    };
    /**
     * Recalculates the position based on the current matrix
     */
    _getPosition = () => {
        copy$2(this.position, createVec3(transformMat4(create$1(), [0, 0, 0, 1], this.matrix)));
        this._getDistance();
    };
    /**
     * Recalculate distanceVector, distance and dollyingStep from focalPoint and position.
     */
    _getDistance = () => {
        this.distanceVector = subtract(create$2(), this.focalPoint, this.position);
        this.distance = length$1(this.distanceVector);
        this.dollyingStep = this.distance / 100;
    };
    /**
     * Recalculate distanceVector, focalPoint from matrix, distance and position.
     * Called only when rotate in TRACKING mode.
     */
    _getFocalPoint = () => {
        transformMat3(this.distanceVector, [0, 0, -this.distance], fromMat4(create$4(), this.matrix));
        add(this.focalPoint, this.position, this.distanceVector);
        this._getDistance();
    };
    /**
     * Recalculate matrix with input position.
     */
    _setPosition = (x, y, z) => {
        this.position = createVec3(x, y, z);
        const m = this.matrix;
        m[12] = this.position[0];
        m[13] = this.position[1];
        m[14] = this.position[2];
        m[15] = 1;
        this._getOrthoMatrix();
    };
    /**
     * Rebuild matrix from roll, elevation, azimuth and position / focalPoint
     */
    _computeMatrix = () => {
        let rotX = create();
        let rotY = create();
        const rotZ = setAxisAngle(create(), [0, 0, 1], deg2rad(this.roll));
        identity(this.matrix);
        if (this.type === CameraType.TRACKING) {
            rotX = setAxisAngle(rotX, [1, 0, 0], deg2rad(this.elevation));
            rotY = setAxisAngle(rotY, [0, 1, 0], deg2rad(this.azimuth));
        }
        else if (this.type === CameraType.EXPLORING || this.type === CameraType.ORBITING) {
            // only consider HCS for EXPLORING and ORBITING cameras
            rotX = setAxisAngle(rotX, [1, 0, 0], (this.rotateWorld ? 1 : -1) * deg2rad(this.elevation));
            rotY = setAxisAngle(rotY, [0, 1, 0], (this.rotateWorld ? 1 : -1) * deg2rad(this.azimuth));
        }
        let rotQ = multiply(create(), rotY, rotX);
        rotQ = multiply(create(), rotQ, rotZ);
        const rotMatrix = fromQuat(create$3(), rotQ);
        if (this.type === CameraType.TRACKING) {
            translate(this.matrix, this.matrix, this.position);
            multiply$1(this.matrix, this.matrix, rotMatrix);
        }
        else if (this.type === CameraType.EXPLORING || this.type === CameraType.ORBITING) {
            translate(this.matrix, this.matrix, this.focalPoint);
            multiply$1(this.matrix, this.matrix, rotMatrix);
            translate(this.matrix, this.matrix, [0, 0, this.distance]);
        }
    };
    /** world pos to screen pos */
    _getOrthoMatrix() {
        if (this.projectionMode !== CameraProjectionMode.ORTHOGRAPHIC) {
            return;
        }
        const { width, height } = this.canvas.getConfig();
        identity(this.orthographicMatrix);
        if (!this.flipY)
            scale$1(this.orthographicMatrix, this.orthographicMatrix, [width / 2, height / 2, 1 / 2]);
        else {
            scale$1(this.orthographicMatrix, this.orthographicMatrix, [width, height, 1]);
            translate(this.orthographicMatrix, this.orthographicMatrix, [0, 1, 0]);
            scale$1(this.orthographicMatrix, this.orthographicMatrix, [1, -1, 1]);
            scale$1(this.orthographicMatrix, this.orthographicMatrix, [1 / 2, 1 / 2, 1 / 2]);
        }
        translate(this.orthographicMatrix, this.orthographicMatrix, [1, 1, 1]);
        multiply$1(this.orthographicMatrix, this.orthographicMatrix, this.projectionMatrix);
        const matrixInverse = invert$1(create$3(), this.matrix);
        multiply$1(this.orthographicMatrix, this.orthographicMatrix, matrixInverse);
        //  mat4.invert(this.orthographicMatrix, this.orthographicMatrix);
    }
    triggerUpdate() {
        this.eventEmitter.emit(CameraEvent.UPDATED);
    }
}

var mz = Object.defineProperty;
var gz = (s, e, t) => e in s ? mz(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t;
var S = (s, e, t) => (gz(s, typeof e != "symbol" ? e + "" : e, t), t);
var Sc = typeof globalThis < "u" ? globalThis : typeof window < "u" ? window : typeof global < "u" ? global : typeof self < "u" ? self : {};
function fM(s) {
  return s && s.__esModule && Object.prototype.hasOwnProperty.call(s, "default") ? s.default : s;
}
function vz(s) {
  if (s.__esModule)
    return s;
  var e = s.default;
  if (typeof e == "function") {
    var t = function i() {
      return this instanceof i ? Reflect.construct(e, arguments, this.constructor) : e.apply(this, arguments);
    };
    t.prototype = e.prototype;
  } else
    t = {};
  return Object.defineProperty(t, "__esModule", { value: !0 }), Object.keys(s).forEach(function(i) {
    var n = Object.getOwnPropertyDescriptor(s, i);
    Object.defineProperty(t, i, n.get ? n : {
      enumerable: !0,
      get: function() {
        return s[i];
      }
    });
  }), t;
}
/*! *****************************************************************************
Copyright (C) Microsoft. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var Hw;
(function(s) {
  (function(e) {
    var t = typeof Sc == "object" ? Sc : typeof self == "object" ? self : typeof this == "object" ? this : Function("return this;")(), i = n(s);
    typeof t.Reflect > "u" ? t.Reflect = s : i = n(t.Reflect, i), e(i);
    function n(r, a) {
      return function(o, l) {
        typeof r[o] != "function" && Object.defineProperty(r, o, { configurable: !0, writable: !0, value: l }), a && a(o, l);
      };
    }
  })(function(e) {
    var t = Object.prototype.hasOwnProperty, i = typeof Symbol == "function", n = i && typeof Symbol.toPrimitive < "u" ? Symbol.toPrimitive : "@@toPrimitive", r = i && typeof Symbol.iterator < "u" ? Symbol.iterator : "@@iterator", a = typeof Object.create == "function", o = { __proto__: [] } instanceof Array, l = !a && !o, c = {
      // create an object in dictionary mode (a.k.a. "slow" mode in v8)
      create: a ? function() {
        return ve(/* @__PURE__ */ Object.create(null));
      } : o ? function() {
        return ve({ __proto__: null });
      } : function() {
        return ve({});
      },
      has: l ? function(Y, ee) {
        return t.call(Y, ee);
      } : function(Y, ee) {
        return ee in Y;
      },
      get: l ? function(Y, ee) {
        return t.call(Y, ee) ? Y[ee] : void 0;
      } : function(Y, ee) {
        return Y[ee];
      }
    }, h = Object.getPrototypeOf(Function), u = typeof process == "object" && process["env"] && process["env"].REFLECT_METADATA_USE_MAP_POLYFILL === "true", d = !u && typeof Map == "function" && typeof Map.prototype.entries == "function" ? Map : G(), f = !u && typeof Set == "function" && typeof Set.prototype.entries == "function" ? Set : D(), p = !u && typeof WeakMap == "function" ? WeakMap : ne(), v = new p();
    function g(Y, ee, we, de) {
      if (re(we)) {
        if (!rt(Y))
          throw new TypeError();
        if (!k(ee))
          throw new TypeError();
        return E(Y, ee);
      } else {
        if (!rt(Y))
          throw new TypeError();
        if (!J(ee))
          throw new TypeError();
        if (!J(de) && !re(de) && !be(de))
          throw new TypeError();
        return be(de) && (de = void 0), we = Je(we), P(Y, ee, we, de);
      }
    }
    e("decorate", g);
    function m(Y, ee) {
      function we(de, xe) {
        if (!J(de))
          throw new TypeError();
        if (!re(xe) && !Fe(xe))
          throw new TypeError();
        W(Y, ee, de, xe);
      }
      return we;
    }
    e("metadata", m);
    function x(Y, ee, we, de) {
      if (!J(we))
        throw new TypeError();
      return re(de) || (de = Je(de)), W(Y, ee, we, de);
    }
    e("defineMetadata", x);
    function y(Y, ee, we) {
      if (!J(ee))
        throw new TypeError();
      return re(we) || (we = Je(we)), I(Y, ee, we);
    }
    e("hasMetadata", y);
    function A(Y, ee, we) {
      if (!J(ee))
        throw new TypeError();
      return re(we) || (we = Je(we)), N(Y, ee, we);
    }
    e("hasOwnMetadata", A);
    function b(Y, ee, we) {
      if (!J(ee))
        throw new TypeError();
      return re(we) || (we = Je(we)), H(Y, ee, we);
    }
    e("getMetadata", b);
    function M(Y, ee, we) {
      if (!J(ee))
        throw new TypeError();
      return re(we) || (we = Je(we)), U(Y, ee, we);
    }
    e("getOwnMetadata", M);
    function w(Y, ee) {
      if (!J(Y))
        throw new TypeError();
      return re(ee) || (ee = Je(ee)), X(Y, ee);
    }
    e("getMetadataKeys", w);
    function C(Y, ee) {
      if (!J(Y))
        throw new TypeError();
      return re(ee) || (ee = Je(ee)), K(Y, ee);
    }
    e("getOwnMetadataKeys", C);
    function T(Y, ee, we) {
      if (!J(ee))
        throw new TypeError();
      re(we) || (we = Je(we));
      var de = L(
        ee,
        we,
        /*Create*/
        !1
      );
      if (re(de) || !de.delete(Y))
        return !1;
      if (de.size > 0)
        return !0;
      var xe = v.get(ee);
      return xe.delete(we), xe.size > 0 || v.delete(ee), !0;
    }
    e("deleteMetadata", T);
    function E(Y, ee) {
      for (var we = Y.length - 1; we >= 0; --we) {
        var de = Y[we], xe = de(ee);
        if (!re(xe) && !be(xe)) {
          if (!k(xe))
            throw new TypeError();
          ee = xe;
        }
      }
      return ee;
    }
    function P(Y, ee, we, de) {
      for (var xe = Y.length - 1; xe >= 0; --xe) {
        var it = Y[xe], q = it(ee, we, de);
        if (!re(q) && !be(q)) {
          if (!J(q))
            throw new TypeError();
          de = q;
        }
      }
      return de;
    }
    function L(Y, ee, we) {
      var de = v.get(Y);
      if (re(de)) {
        if (!we)
          return;
        de = new d(), v.set(Y, de);
      }
      var xe = de.get(ee);
      if (re(xe)) {
        if (!we)
          return;
        xe = new d(), de.set(ee, xe);
      }
      return xe;
    }
    function I(Y, ee, we) {
      var de = N(Y, ee, we);
      if (de)
        return !0;
      var xe = Ze(ee);
      return be(xe) ? !1 : I(Y, xe, we);
    }
    function N(Y, ee, we) {
      var de = L(
        ee,
        we,
        /*Create*/
        !1
      );
      return re(de) ? !1 : ye(de.has(Y));
    }
    function H(Y, ee, we) {
      var de = N(Y, ee, we);
      if (de)
        return U(Y, ee, we);
      var xe = Ze(ee);
      if (!be(xe))
        return H(Y, xe, we);
    }
    function U(Y, ee, we) {
      var de = L(
        ee,
        we,
        /*Create*/
        !1
      );
      if (!re(de))
        return de.get(Y);
    }
    function W(Y, ee, we, de) {
      var xe = L(
        we,
        de,
        /*Create*/
        !0
      );
      xe.set(Y, ee);
    }
    function X(Y, ee) {
      var we = K(Y, ee), de = Ze(Y);
      if (de === null)
        return we;
      var xe = X(de, ee);
      if (xe.length <= 0)
        return we;
      if (we.length <= 0)
        return xe;
      for (var it = new f(), q = [], ae = 0, Ee = we; ae < Ee.length; ae++) {
        var Le = Ee[ae], Ae = it.has(Le);
        Ae || (it.add(Le), q.push(Le));
      }
      for (var We = 0, ot = xe; We < ot.length; We++) {
        var Le = ot[We], Ae = it.has(Le);
        Ae || (it.add(Le), q.push(Le));
      }
      return q;
    }
    function K(Y, ee) {
      var we = [], de = L(
        Y,
        ee,
        /*Create*/
        !1
      );
      if (re(de))
        return we;
      for (var xe = de.keys(), it = qe(xe), q = 0; ; ) {
        var ae = vt(it);
        if (!ae)
          return we.length = q, we;
        var Ee = Ie(ae);
        try {
          we[q] = Ee;
        } catch (Le) {
          try {
            Ve(it);
          } finally {
            throw Le;
          }
        }
        q++;
      }
    }
    function te(Y) {
      if (Y === null)
        return 1;
      switch (typeof Y) {
        case "undefined":
          return 0;
        case "boolean":
          return 2;
        case "string":
          return 3;
        case "symbol":
          return 4;
        case "number":
          return 5;
        case "object":
          return Y === null ? 1 : 6;
        default:
          return 6;
      }
    }
    function re(Y) {
      return Y === void 0;
    }
    function be(Y) {
      return Y === null;
    }
    function Pe(Y) {
      return typeof Y == "symbol";
    }
    function J(Y) {
      return typeof Y == "object" ? Y !== null : typeof Y == "function";
    }
    function fe(Y, ee) {
      switch (te(Y)) {
        case 0:
          return Y;
        case 1:
          return Y;
        case 2:
          return Y;
        case 3:
          return Y;
        case 4:
          return Y;
        case 5:
          return Y;
      }
      var we = "string" , de = Ge(Y, n);
      if (de !== void 0) {
        var xe = de.call(Y, we);
        if (J(xe))
          throw new TypeError();
        return xe;
      }
      return Me(Y);
    }
    function Me(Y, ee) {
      var xe, de; {
        var we = Y.toString;
        if (wt(we)) {
          var de = we.call(Y);
          if (!J(de))
            return de;
        }
        var xe = Y.valueOf;
        if (wt(xe)) {
          var de = xe.call(Y);
          if (!J(de))
            return de;
        }
      }
      throw new TypeError();
    }
    function ye(Y) {
      return !!Y;
    }
    function Ye(Y) {
      return "" + Y;
    }
    function Je(Y) {
      var ee = fe(
        Y);
      return Pe(ee) ? ee : Ye(ee);
    }
    function rt(Y) {
      return Array.isArray ? Array.isArray(Y) : Y instanceof Object ? Y instanceof Array : Object.prototype.toString.call(Y) === "[object Array]";
    }
    function wt(Y) {
      return typeof Y == "function";
    }
    function k(Y) {
      return typeof Y == "function";
    }
    function Fe(Y) {
      switch (te(Y)) {
        case 3:
          return !0;
        case 4:
          return !0;
        default:
          return !1;
      }
    }
    function Ge(Y, ee) {
      var we = Y[ee];
      if (we != null) {
        if (!wt(we))
          throw new TypeError();
        return we;
      }
    }
    function qe(Y) {
      var ee = Ge(Y, r);
      if (!wt(ee))
        throw new TypeError();
      var we = ee.call(Y);
      if (!J(we))
        throw new TypeError();
      return we;
    }
    function Ie(Y) {
      return Y.value;
    }
    function vt(Y) {
      var ee = Y.next();
      return ee.done ? !1 : ee;
    }
    function Ve(Y) {
      var ee = Y.return;
      ee && ee.call(Y);
    }
    function Ze(Y) {
      var ee = Object.getPrototypeOf(Y);
      if (typeof Y != "function" || Y === h || ee !== h)
        return ee;
      var we = Y.prototype, de = we && Object.getPrototypeOf(we);
      if (de == null || de === Object.prototype)
        return ee;
      var xe = de.constructor;
      return typeof xe != "function" || xe === Y ? ee : xe;
    }
    function G() {
      var Y = {}, ee = [], we = (
        /** @class */
        function() {
          function q(ae, Ee, Le) {
            this._index = 0, this._keys = ae, this._values = Ee, this._selector = Le;
          }
          return q.prototype["@@iterator"] = function() {
            return this;
          }, q.prototype[r] = function() {
            return this;
          }, q.prototype.next = function() {
            var ae = this._index;
            if (ae >= 0 && ae < this._keys.length) {
              var Ee = this._selector(this._keys[ae], this._values[ae]);
              return ae + 1 >= this._keys.length ? (this._index = -1, this._keys = ee, this._values = ee) : this._index++, { value: Ee, done: !1 };
            }
            return { value: void 0, done: !0 };
          }, q.prototype.throw = function(ae) {
            throw this._index >= 0 && (this._index = -1, this._keys = ee, this._values = ee), ae;
          }, q.prototype.return = function(ae) {
            return this._index >= 0 && (this._index = -1, this._keys = ee, this._values = ee), { value: ae, done: !0 };
          }, q;
        }()
      );
      return (
        /** @class */
        function() {
          function q() {
            this._keys = [], this._values = [], this._cacheKey = Y, this._cacheIndex = -2;
          }
          return Object.defineProperty(q.prototype, "size", {
            get: function() {
              return this._keys.length;
            },
            enumerable: !0,
            configurable: !0
          }), q.prototype.has = function(ae) {
            return this._find(
              ae,
              /*insert*/
              !1
            ) >= 0;
          }, q.prototype.get = function(ae) {
            var Ee = this._find(
              ae,
              /*insert*/
              !1
            );
            return Ee >= 0 ? this._values[Ee] : void 0;
          }, q.prototype.set = function(ae, Ee) {
            var Le = this._find(
              ae,
              /*insert*/
              !0
            );
            return this._values[Le] = Ee, this;
          }, q.prototype.delete = function(ae) {
            var Ee = this._find(
              ae,
              /*insert*/
              !1
            );
            if (Ee >= 0) {
              for (var Le = this._keys.length, Ae = Ee + 1; Ae < Le; Ae++)
                this._keys[Ae - 1] = this._keys[Ae], this._values[Ae - 1] = this._values[Ae];
              return this._keys.length--, this._values.length--, ae === this._cacheKey && (this._cacheKey = Y, this._cacheIndex = -2), !0;
            }
            return !1;
          }, q.prototype.clear = function() {
            this._keys.length = 0, this._values.length = 0, this._cacheKey = Y, this._cacheIndex = -2;
          }, q.prototype.keys = function() {
            return new we(this._keys, this._values, de);
          }, q.prototype.values = function() {
            return new we(this._keys, this._values, xe);
          }, q.prototype.entries = function() {
            return new we(this._keys, this._values, it);
          }, q.prototype["@@iterator"] = function() {
            return this.entries();
          }, q.prototype[r] = function() {
            return this.entries();
          }, q.prototype._find = function(ae, Ee) {
            return this._cacheKey !== ae && (this._cacheIndex = this._keys.indexOf(this._cacheKey = ae)), this._cacheIndex < 0 && Ee && (this._cacheIndex = this._keys.length, this._keys.push(ae), this._values.push(void 0)), this._cacheIndex;
          }, q;
        }()
      );
      function de(q, ae) {
        return q;
      }
      function xe(q, ae) {
        return ae;
      }
      function it(q, ae) {
        return [q, ae];
      }
    }
    function D() {
      return (
        /** @class */
        function() {
          function Y() {
            this._map = new d();
          }
          return Object.defineProperty(Y.prototype, "size", {
            get: function() {
              return this._map.size;
            },
            enumerable: !0,
            configurable: !0
          }), Y.prototype.has = function(ee) {
            return this._map.has(ee);
          }, Y.prototype.add = function(ee) {
            return this._map.set(ee, ee), this;
          }, Y.prototype.delete = function(ee) {
            return this._map.delete(ee);
          }, Y.prototype.clear = function() {
            this._map.clear();
          }, Y.prototype.keys = function() {
            return this._map.keys();
          }, Y.prototype.values = function() {
            return this._map.values();
          }, Y.prototype.entries = function() {
            return this._map.entries();
          }, Y.prototype["@@iterator"] = function() {
            return this.keys();
          }, Y.prototype[r] = function() {
            return this.keys();
          }, Y;
        }()
      );
    }
    function ne() {
      var Y = 16, ee = c.create(), we = de();
      return (
        /** @class */
        function() {
          function Ee() {
            this._key = de();
          }
          return Ee.prototype.has = function(Le) {
            var Ae = xe(
              Le,
              /*create*/
              !1
            );
            return Ae !== void 0 ? c.has(Ae, this._key) : !1;
          }, Ee.prototype.get = function(Le) {
            var Ae = xe(
              Le,
              /*create*/
              !1
            );
            return Ae !== void 0 ? c.get(Ae, this._key) : void 0;
          }, Ee.prototype.set = function(Le, Ae) {
            var We = xe(
              Le,
              /*create*/
              !0
            );
            return We[this._key] = Ae, this;
          }, Ee.prototype.delete = function(Le) {
            var Ae = xe(
              Le,
              /*create*/
              !1
            );
            return Ae !== void 0 ? delete Ae[this._key] : !1;
          }, Ee.prototype.clear = function() {
            this._key = de();
          }, Ee;
        }()
      );
      function de() {
        var Ee;
        do
          Ee = "@@WeakMap@@" + ae();
        while (c.has(ee, Ee));
        return ee[Ee] = !0, Ee;
      }
      function xe(Ee, Le) {
        if (!t.call(Ee, we)) {
          if (!Le)
            return;
          Object.defineProperty(Ee, we, { value: c.create() });
        }
        return Ee[we];
      }
      function it(Ee, Le) {
        for (var Ae = 0; Ae < Le; ++Ae)
          Ee[Ae] = Math.random() * 255 | 0;
        return Ee;
      }
      function q(Ee) {
        return typeof Uint8Array == "function" ? typeof crypto < "u" ? crypto.getRandomValues(new Uint8Array(Ee)) : typeof msCrypto < "u" ? msCrypto.getRandomValues(new Uint8Array(Ee)) : it(new Uint8Array(Ee), Ee) : it(new Array(Ee), Ee);
      }
      function ae() {
        var Ee = q(Y);
        Ee[6] = Ee[6] & 79 | 64, Ee[8] = Ee[8] & 191 | 128;
        for (var Le = "", Ae = 0; Ae < Y; ++Ae) {
          var We = Ee[Ae];
          (Ae === 4 || Ae === 6 || Ae === 8) && (Le += "-"), We < 16 && (Le += "0"), Le += We.toString(16).toLowerCase();
        }
        return Le;
      }
    }
    function ve(Y) {
      return Y.__ = void 0, delete Y.__, Y;
    }
  });
})(Hw || (Hw = {}));
/**
 * @license
 * Copyright 2010-2024 Three.js Authors
 * SPDX-License-Identifier: MIT
 */
const Qo = "166", Lr = { LEFT: 0, MIDDLE: 1, RIGHT: 2, ROTATE: 0, DOLLY: 1, PAN: 2 }, Xh = { ROTATE: 0, PAN: 1, DOLLY_PAN: 2, DOLLY_ROTATE: 3 }, $D = 0, G6 = 1, eL = 2, yz = 3, xz = 0, pM = 1, $g = 2, Do = 3, sa = 0, Ps = 1, ui = 2, vn = 0, so = 1, oo = 2, m9 = 3, g9 = 4, u4 = 5, Ma = 100, tL = 101, iL = 102, nL = 103, sL = 104, ep = 200, rL = 201, aL = 202, oL = 203, v9 = 204, y9 = 205, d4 = 206, lL = 207, f4 = 208, cL = 209, hL = 210, uL = 211, dL = 212, fL = 213, pL = 214, mL = 0, gL = 1, vL = 2, j1 = 3, yL = 4, xL = 5, AL = 6, bL = 7, e2 = 0, ML = 1, SL = 2, ol = 0, mM = 1, gM = 2, vM = 3, Hp = 4, wL = 5, yM = 6, xM = 7, H6 = "attached", EL = "detached", Ho = 300, lo = 301, a0 = 302, ml = 303, Fc = 304, Qc = 306, un = 1e3, yn = 1001, co = 1002, Ni = 1003, $c = 1004, Az = 1004, $a = 1005, bz = 1005, ii = 1006, $o = 1007, Mz = 1007, ds = 1008, Sz = 1008, o0 = 1009, AM = 1010, bM = 1011, tp = 1012, p4 = 1013, gl = 1014, xn = 1015, on = 1016, m4 = 1017, g4 = 1018, vl = 1020, MM = 35902, SM = 1021, wM = 1022, dn = 1023, EM = 1024, TM = 1025, Au = 1026, yl = 1027, Vu = 1028, t2 = 1029, CM = 1030, v4 = 1031, wz = 1032, y4 = 1033, x1 = 33776, A1 = 33777, b1 = 33778, M1 = 33779, x9 = 35840, A9 = 35841, b9 = 35842, M9 = 35843, S9 = 36196, w9 = 37492, E9 = 37496, T9 = 37808, C9 = 37809, _9 = 37810, P9 = 37811, R9 = 37812, D9 = 37813, L9 = 37814, I9 = 37815, O9 = 37816, N9 = 37817, B9 = 37818, F9 = 37819, U9 = 37820, z9 = 37821, S1 = 36492, k9 = 36494, G9 = 36495, _M = 36283, H9 = 36284, V9 = 36285, W9 = 36286, PM = 2200, RM = 2201, TL = 2202, Uc = 2300, l0 = 2301, Sy = 2302, hu = 2400, uu = 2401, q1 = 2402, x4 = 2500, DM = 2501, CL = 0, i2 = 1, ip = 2, _L = 3200, LM = 3201, eh = 0, PL = 1, Uo = "", qi = "srgb", Si = "srgb-linear", A4 = "display-p3", n2 = "display-p3-linear", Y1 = "linear", pn = "srgb", Z1 = "rec709", K1 = "p3", Ez = 0, jh = 7680, Tz = 7681, Cz = 7682, _z = 7683, Pz = 34055, Rz = 34056, Dz = 5386, Lz = 512, Iz = 513, Oz = 514, Nz = 515, Bz = 516, Fz = 517, Uz = 518, V6 = 519, RL = 512, DL = 513, LL = 514, IM = 515, IL = 516, OL = 517, NL = 518, BL = 519, J1 = 35044, Js = 35048, zz = 35040, kz = 35045, Gz = 35049, Hz = 35041, Vz = 35046, Wz = 35050, Xz = 35042, jz = "100", W6 = "300 es", Vo = 2e3, Q1 = 2001;
let ra = class {
  addEventListener(e, t) {
    this._listeners === void 0 && (this._listeners = {});
    const i = this._listeners;
    i[e] === void 0 && (i[e] = []), i[e].indexOf(t) === -1 && i[e].push(t);
  }
  hasEventListener(e, t) {
    if (this._listeners === void 0)
      return !1;
    const i = this._listeners;
    return i[e] !== void 0 && i[e].indexOf(t) !== -1;
  }
  removeEventListener(e, t) {
    if (this._listeners === void 0)
      return;
    const n = this._listeners[e];
    if (n !== void 0) {
      const r = n.indexOf(t);
      r !== -1 && n.splice(r, 1);
    }
  }
  dispatchEvent(e) {
    if (this._listeners === void 0)
      return;
    const i = this._listeners[e.type];
    if (i !== void 0) {
      e.target = this;
      const n = i.slice(0);
      for (let r = 0, a = n.length; r < a; r++)
        n[r].call(this, e);
      e.target = null;
    }
  }
};
const Vs = ["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "0a", "0b", "0c", "0d", "0e", "0f", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "1a", "1b", "1c", "1d", "1e", "1f", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "2a", "2b", "2c", "2d", "2e", "2f", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "3a", "3b", "3c", "3d", "3e", "3f", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "4a", "4b", "4c", "4d", "4e", "4f", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "5a", "5b", "5c", "5d", "5e", "5f", "60", "61", "62", "63", "64", "65", "66", "67", "68", "69", "6a", "6b", "6c", "6d", "6e", "6f", "70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "7a", "7b", "7c", "7d", "7e", "7f", "80", "81", "82", "83", "84", "85", "86", "87", "88", "89", "8a", "8b", "8c", "8d", "8e", "8f", "90", "91", "92", "93", "94", "95", "96", "97", "98", "99", "9a", "9b", "9c", "9d", "9e", "9f", "a0", "a1", "a2", "a3", "a4", "a5", "a6", "a7", "a8", "a9", "aa", "ab", "ac", "ad", "ae", "af", "b0", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "b8", "b9", "ba", "bb", "bc", "bd", "be", "bf", "c0", "c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8", "c9", "ca", "cb", "cc", "cd", "ce", "cf", "d0", "d1", "d2", "d3", "d4", "d5", "d6", "d7", "d8", "d9", "da", "db", "dc", "dd", "de", "df", "e0", "e1", "e2", "e3", "e4", "e5", "e6", "e7", "e8", "e9", "ea", "eb", "ec", "ed", "ee", "ef", "f0", "f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8", "f9", "fa", "fb", "fc", "fd", "fe", "ff"];
let Vw = 1234567;
const bu = Math.PI / 180, np = 180 / Math.PI;
function ea() {
  const s = Math.random() * 4294967295 | 0, e = Math.random() * 4294967295 | 0, t = Math.random() * 4294967295 | 0, i = Math.random() * 4294967295 | 0;
  return (Vs[s & 255] + Vs[s >> 8 & 255] + Vs[s >> 16 & 255] + Vs[s >> 24 & 255] + "-" + Vs[e & 255] + Vs[e >> 8 & 255] + "-" + Vs[e >> 16 & 15 | 64] + Vs[e >> 24 & 255] + "-" + Vs[t & 63 | 128] + Vs[t >> 8 & 255] + "-" + Vs[t >> 16 & 255] + Vs[t >> 24 & 255] + Vs[i & 255] + Vs[i >> 8 & 255] + Vs[i >> 16 & 255] + Vs[i >> 24 & 255]).toLowerCase();
}
function Wn(s, e, t) {
  return Math.max(e, Math.min(t, s));
}
function OM(s, e) {
  return (s % e + e) % e;
}
function qz(s, e, t, i, n) {
  return i + (s - e) * (n - i) / (t - e);
}
function Yz(s, e, t) {
  return s !== e ? (t - s) / (e - s) : 0;
}
function w1(s, e, t) {
  return (1 - t) * s + t * e;
}
function Zz(s, e, t, i) {
  return w1(s, e, 1 - Math.exp(-t * i));
}
function Kz(s, e = 1) {
  return e - Math.abs(OM(s, e * 2) - e);
}
function Jz(s, e, t) {
  return s <= e ? 0 : s >= t ? 1 : (s = (s - e) / (t - e), s * s * (3 - 2 * s));
}
function Qz(s, e, t) {
  return s <= e ? 0 : s >= t ? 1 : (s = (s - e) / (t - e), s * s * s * (s * (s * 6 - 15) + 10));
}
function $z(s, e) {
  return s + Math.floor(Math.random() * (e - s + 1));
}
function ek(s, e) {
  return s + Math.random() * (e - s);
}
function tk(s) {
  return s * (0.5 - Math.random());
}
function ik(s) {
  s !== void 0 && (Vw = s);
  let e = Vw += 1831565813;
  return e = Math.imul(e ^ e >>> 15, e | 1), e ^= e + Math.imul(e ^ e >>> 7, e | 61), ((e ^ e >>> 14) >>> 0) / 4294967296;
}
function nk(s) {
  return s * bu;
}
function sk(s) {
  return s * np;
}
function rk(s) {
  return (s & s - 1) === 0 && s !== 0;
}
function ak(s) {
  return Math.pow(2, Math.ceil(Math.log(s) / Math.LN2));
}
function ok(s) {
  return Math.pow(2, Math.floor(Math.log(s) / Math.LN2));
}
function lk(s, e, t, i, n) {
  const r = Math.cos, a = Math.sin, o = r(t / 2), l = a(t / 2), c = r((e + i) / 2), h = a((e + i) / 2), u = r((e - i) / 2), d = a((e - i) / 2), f = r((i - e) / 2), p = a((i - e) / 2);
  switch (n) {
    case "XYX":
      s.set(o * h, l * u, l * d, o * c);
      break;
    case "YZY":
      s.set(l * d, o * h, l * u, o * c);
      break;
    case "ZXZ":
      s.set(l * u, l * d, o * h, o * c);
      break;
    case "XZX":
      s.set(o * h, l * p, l * f, o * c);
      break;
    case "YXY":
      s.set(l * f, o * h, l * p, o * c);
      break;
    case "ZYZ":
      s.set(l * p, l * f, o * h, o * c);
      break;
    default:
      console.warn("THREE.MathUtils: .setQuaternionFromProperEuler() encountered an unknown order: " + n);
  }
}
function vr(s, e) {
  switch (e.constructor) {
    case Float32Array:
      return s;
    case Uint32Array:
      return s / 4294967295;
    case Uint16Array:
      return s / 65535;
    case Uint8Array:
      return s / 255;
    case Int32Array:
      return Math.max(s / 2147483647, -1);
    case Int16Array:
      return Math.max(s / 32767, -1);
    case Int8Array:
      return Math.max(s / 127, -1);
    default:
      throw new Error("Invalid component type.");
  }
}
function hi(s, e) {
  switch (e.constructor) {
    case Float32Array:
      return s;
    case Uint32Array:
      return Math.round(s * 4294967295);
    case Uint16Array:
      return Math.round(s * 65535);
    case Uint8Array:
      return Math.round(s * 255);
    case Int32Array:
      return Math.round(s * 2147483647);
    case Int16Array:
      return Math.round(s * 32767);
    case Int8Array:
      return Math.round(s * 127);
    default:
      throw new Error("Invalid component type.");
  }
}
const nt = {
  DEG2RAD: bu,
  RAD2DEG: np,
  generateUUID: ea,
  clamp: Wn,
  euclideanModulo: OM,
  mapLinear: qz,
  inverseLerp: Yz,
  lerp: w1,
  damp: Zz,
  pingpong: Kz,
  smoothstep: Jz,
  smootherstep: Qz,
  randInt: $z,
  randFloat: ek,
  randFloatSpread: tk,
  seededRandom: ik,
  degToRad: nk,
  radToDeg: sk,
  isPowerOfTwo: rk,
  ceilPowerOfTwo: ak,
  floorPowerOfTwo: ok,
  setQuaternionFromProperEuler: lk,
  normalize: hi,
  denormalize: vr
};
let Se = class FL {
  constructor(e = 0, t = 0) {
    FL.prototype.isVector2 = !0, this.x = e, this.y = t;
  }
  get width() {
    return this.x;
  }
  set width(e) {
    this.x = e;
  }
  get height() {
    return this.y;
  }
  set height(e) {
    this.y = e;
  }
  set(e, t) {
    return this.x = e, this.y = t, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this;
  }
  divide(e) {
    return this.x /= e.x, this.y /= e.y, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  applyMatrix3(e) {
    const t = this.x, i = this.y, n = e.elements;
    return this.x = n[0] * t + n[3] * i + n[6], this.y = n[1] * t + n[4] * i + n[7], this;
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y;
  }
  cross(e) {
    return this.x * e.y - this.y * e.x;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  angle() {
    return Math.atan2(-this.y, -this.x) + Math.PI;
  }
  angleTo(e) {
    const t = Math.sqrt(this.lengthSq() * e.lengthSq());
    if (t === 0)
      return Math.PI / 2;
    const i = this.dot(e) / t;
    return Math.acos(Wn(i, -1, 1));
  }
  distanceTo(e) {
    return Math.sqrt(this.distanceToSquared(e));
  }
  distanceToSquared(e) {
    const t = this.x - e.x, i = this.y - e.y;
    return t * t + i * i;
  }
  manhattanDistanceTo(e) {
    return Math.abs(this.x - e.x) + Math.abs(this.y - e.y);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this;
  }
  rotateAround(e, t) {
    const i = Math.cos(t), n = Math.sin(t), r = this.x - e.x, a = this.y - e.y;
    return this.x = r * i - a * n + e.x, this.y = r * n + a * i + e.y, this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y;
  }
}, Gt = class UL {
  constructor(e, t, i, n, r, a, o, l, c) {
    UL.prototype.isMatrix3 = !0, this.elements = [
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1
    ], e !== void 0 && this.set(e, t, i, n, r, a, o, l, c);
  }
  set(e, t, i, n, r, a, o, l, c) {
    const h = this.elements;
    return h[0] = e, h[1] = n, h[2] = o, h[3] = t, h[4] = r, h[5] = l, h[6] = i, h[7] = a, h[8] = c, this;
  }
  identity() {
    return this.set(
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1
    ), this;
  }
  copy(e) {
    const t = this.elements, i = e.elements;
    return t[0] = i[0], t[1] = i[1], t[2] = i[2], t[3] = i[3], t[4] = i[4], t[5] = i[5], t[6] = i[6], t[7] = i[7], t[8] = i[8], this;
  }
  extractBasis(e, t, i) {
    return e.setFromMatrix3Column(this, 0), t.setFromMatrix3Column(this, 1), i.setFromMatrix3Column(this, 2), this;
  }
  setFromMatrix4(e) {
    const t = e.elements;
    return this.set(
      t[0],
      t[4],
      t[8],
      t[1],
      t[5],
      t[9],
      t[2],
      t[6],
      t[10]
    ), this;
  }
  multiply(e) {
    return this.multiplyMatrices(this, e);
  }
  premultiply(e) {
    return this.multiplyMatrices(e, this);
  }
  multiplyMatrices(e, t) {
    const i = e.elements, n = t.elements, r = this.elements, a = i[0], o = i[3], l = i[6], c = i[1], h = i[4], u = i[7], d = i[2], f = i[5], p = i[8], v = n[0], g = n[3], m = n[6], x = n[1], y = n[4], A = n[7], b = n[2], M = n[5], w = n[8];
    return r[0] = a * v + o * x + l * b, r[3] = a * g + o * y + l * M, r[6] = a * m + o * A + l * w, r[1] = c * v + h * x + u * b, r[4] = c * g + h * y + u * M, r[7] = c * m + h * A + u * w, r[2] = d * v + f * x + p * b, r[5] = d * g + f * y + p * M, r[8] = d * m + f * A + p * w, this;
  }
  multiplyScalar(e) {
    const t = this.elements;
    return t[0] *= e, t[3] *= e, t[6] *= e, t[1] *= e, t[4] *= e, t[7] *= e, t[2] *= e, t[5] *= e, t[8] *= e, this;
  }
  determinant() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8];
    return t * a * h - t * o * c - i * r * h + i * o * l + n * r * c - n * a * l;
  }
  invert() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8], u = h * a - o * c, d = o * l - h * r, f = c * r - a * l, p = t * u + i * d + n * f;
    if (p === 0)
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0);
    const v = 1 / p;
    return e[0] = u * v, e[1] = (n * c - h * i) * v, e[2] = (o * i - n * a) * v, e[3] = d * v, e[4] = (h * t - n * l) * v, e[5] = (n * r - o * t) * v, e[6] = f * v, e[7] = (i * l - c * t) * v, e[8] = (a * t - i * r) * v, this;
  }
  transpose() {
    let e;
    const t = this.elements;
    return e = t[1], t[1] = t[3], t[3] = e, e = t[2], t[2] = t[6], t[6] = e, e = t[5], t[5] = t[7], t[7] = e, this;
  }
  getNormalMatrix(e) {
    return this.setFromMatrix4(e).invert().transpose();
  }
  transposeIntoArray(e) {
    const t = this.elements;
    return e[0] = t[0], e[1] = t[3], e[2] = t[6], e[3] = t[1], e[4] = t[4], e[5] = t[7], e[6] = t[2], e[7] = t[5], e[8] = t[8], this;
  }
  setUvTransform(e, t, i, n, r, a, o) {
    const l = Math.cos(r), c = Math.sin(r);
    return this.set(
      i * l,
      i * c,
      -i * (l * a + c * o) + a + e,
      -n * c,
      n * l,
      -n * (-c * a + l * o) + o + t,
      0,
      0,
      1
    ), this;
  }
  //
  scale(e, t) {
    return this.premultiply(b8.makeScale(e, t)), this;
  }
  rotate(e) {
    return this.premultiply(b8.makeRotation(-e)), this;
  }
  translate(e, t) {
    return this.premultiply(b8.makeTranslation(e, t)), this;
  }
  // for 2D Transforms
  makeTranslation(e, t) {
    return e.isVector2 ? this.set(
      1,
      0,
      e.x,
      0,
      1,
      e.y,
      0,
      0,
      1
    ) : this.set(
      1,
      0,
      e,
      0,
      1,
      t,
      0,
      0,
      1
    ), this;
  }
  makeRotation(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      -i,
      0,
      i,
      t,
      0,
      0,
      0,
      1
    ), this;
  }
  makeScale(e, t) {
    return this.set(
      e,
      0,
      0,
      0,
      t,
      0,
      0,
      0,
      1
    ), this;
  }
  //
  equals(e) {
    const t = this.elements, i = e.elements;
    for (let n = 0; n < 9; n++)
      if (t[n] !== i[n])
        return !1;
    return !0;
  }
  fromArray(e, t = 0) {
    for (let i = 0; i < 9; i++)
      this.elements[i] = e[i + t];
    return this;
  }
  toArray(e = [], t = 0) {
    const i = this.elements;
    return e[t] = i[0], e[t + 1] = i[1], e[t + 2] = i[2], e[t + 3] = i[3], e[t + 4] = i[4], e[t + 5] = i[5], e[t + 6] = i[6], e[t + 7] = i[7], e[t + 8] = i[8], e;
  }
  clone() {
    return new this.constructor().fromArray(this.elements);
  }
};
const b8 = /* @__PURE__ */ new Gt();
function zL(s) {
  for (let e = s.length - 1; e >= 0; --e)
    if (s[e] >= 65535)
      return !0;
  return !1;
}
const ck = {
  Int8Array,
  Uint8Array,
  Uint8ClampedArray,
  Int16Array,
  Uint16Array,
  Int32Array,
  Uint32Array,
  Float32Array,
  Float64Array
};
function yf(s, e) {
  return new ck[s](e);
}
function $1(s) {
  return document.createElementNS("http://www.w3.org/1999/xhtml", s);
}
function kL() {
  const s = $1("canvas");
  return s.style.display = "block", s;
}
const Ww = {};
function NM(s) {
  s in Ww || (Ww[s] = !0, console.warn(s));
}
function hk(s, e, t) {
  return new Promise(function(i, n) {
    function r() {
      switch (s.clientWaitSync(e, s.SYNC_FLUSH_COMMANDS_BIT, 0)) {
        case s.WAIT_FAILED:
          n();
          break;
        case s.TIMEOUT_EXPIRED:
          setTimeout(r, t);
          break;
        default:
          i();
      }
    }
    setTimeout(r, t);
  });
}
const Xw = /* @__PURE__ */ new Gt().set(
  0.8224621,
  0.177538,
  0,
  0.0331941,
  0.9668058,
  0,
  0.0170827,
  0.0723974,
  0.9105199
), jw = /* @__PURE__ */ new Gt().set(
  1.2249401,
  -0.2249404,
  0,
  -0.0420569,
  1.0420571,
  0,
  -0.0196376,
  -0.0786361,
  1.0982735
), Y2 = {
  [Si]: {
    transfer: Y1,
    primaries: Z1,
    toReference: (s) => s,
    fromReference: (s) => s
  },
  [qi]: {
    transfer: pn,
    primaries: Z1,
    toReference: (s) => s.convertSRGBToLinear(),
    fromReference: (s) => s.convertLinearToSRGB()
  },
  [n2]: {
    transfer: Y1,
    primaries: K1,
    toReference: (s) => s.applyMatrix3(jw),
    fromReference: (s) => s.applyMatrix3(Xw)
  },
  [A4]: {
    transfer: pn,
    primaries: K1,
    toReference: (s) => s.convertSRGBToLinear().applyMatrix3(jw),
    fromReference: (s) => s.applyMatrix3(Xw).convertLinearToSRGB()
  }
}, uk = /* @__PURE__ */ new Set([Si, n2]), bi = {
  enabled: !0,
  _workingColorSpace: Si,
  get workingColorSpace() {
    return this._workingColorSpace;
  },
  set workingColorSpace(s) {
    if (!uk.has(s))
      throw new Error(`Unsupported working color space, "${s}".`);
    this._workingColorSpace = s;
  },
  convert: function(s, e, t) {
    if (this.enabled === !1 || e === t || !e || !t)
      return s;
    const i = Y2[e].toReference, n = Y2[t].fromReference;
    return n(i(s));
  },
  fromWorkingColorSpace: function(s, e) {
    return this.convert(s, this._workingColorSpace, e);
  },
  toWorkingColorSpace: function(s, e) {
    return this.convert(s, e, this._workingColorSpace);
  },
  getPrimaries: function(s) {
    return Y2[s].primaries;
  },
  getTransfer: function(s) {
    return s === Uo ? Y1 : Y2[s].transfer;
  }
};
function Nf(s) {
  return s < 0.04045 ? s * 0.0773993808 : Math.pow(s * 0.9478672986 + 0.0521327014, 2.4);
}
function M8(s) {
  return s < 31308e-7 ? s * 12.92 : 1.055 * Math.pow(s, 0.41666) - 0.055;
}
let sd, GL = class {
  static getDataURL(e) {
    if (/^data:/i.test(e.src) || typeof HTMLCanvasElement > "u")
      return e.src;
    let t;
    if (e instanceof HTMLCanvasElement)
      t = e;
    else {
      sd === void 0 && (sd = $1("canvas")), sd.width = e.width, sd.height = e.height;
      const i = sd.getContext("2d");
      e instanceof ImageData ? i.putImageData(e, 0, 0) : i.drawImage(e, 0, 0, e.width, e.height), t = sd;
    }
    return t.width > 2048 || t.height > 2048 ? (console.warn("THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons", e), t.toDataURL("image/jpeg", 0.6)) : t.toDataURL("image/png");
  }
  static sRGBToLinear(e) {
    if (typeof HTMLImageElement < "u" && e instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && e instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && e instanceof ImageBitmap) {
      const t = $1("canvas");
      t.width = e.width, t.height = e.height;
      const i = t.getContext("2d");
      i.drawImage(e, 0, 0, e.width, e.height);
      const n = i.getImageData(0, 0, e.width, e.height), r = n.data;
      for (let a = 0; a < r.length; a++)
        r[a] = Nf(r[a] / 255) * 255;
      return i.putImageData(n, 0, 0), t;
    } else if (e.data) {
      const t = e.data.slice(0);
      for (let i = 0; i < t.length; i++)
        t instanceof Uint8Array || t instanceof Uint8ClampedArray ? t[i] = Math.floor(Nf(t[i] / 255) * 255) : t[i] = Nf(t[i]);
      return {
        data: t,
        width: e.width,
        height: e.height
      };
    } else
      return console.warn("THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied."), e;
  }
}, dk = 0, wa = class {
  constructor(e = null) {
    this.isSource = !0, Object.defineProperty(this, "id", { value: dk++ }), this.uuid = ea(), this.data = e, this.dataReady = !0, this.version = 0;
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    if (!t && e.images[this.uuid] !== void 0)
      return e.images[this.uuid];
    const i = {
      uuid: this.uuid,
      url: ""
    }, n = this.data;
    if (n !== null) {
      let r;
      if (Array.isArray(n)) {
        r = [];
        for (let a = 0, o = n.length; a < o; a++)
          n[a].isDataTexture ? r.push(S8(n[a].image)) : r.push(S8(n[a]));
      } else
        r = S8(n);
      i.url = r;
    }
    return t || (e.images[this.uuid] = i), i;
  }
};
function S8(s) {
  return typeof HTMLImageElement < "u" && s instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && s instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && s instanceof ImageBitmap ? GL.getDataURL(s) : s.data ? {
    data: Array.from(s.data),
    width: s.width,
    height: s.height,
    type: s.data.constructor.name
  } : (console.warn("THREE.Texture: Unable to serialize Texture."), {});
}
let fk = 0, Ut = class wy extends ra {
  constructor(e = wy.DEFAULT_IMAGE, t = wy.DEFAULT_MAPPING, i = yn, n = yn, r = ii, a = ds, o = dn, l = o0, c = wy.DEFAULT_ANISOTROPY, h = Uo) {
    super(), this.isTexture = !0, Object.defineProperty(this, "id", { value: fk++ }), this.uuid = ea(), this.name = "", this.source = new wa(e), this.mipmaps = [], this.mapping = t, this.channel = 0, this.wrapS = i, this.wrapT = n, this.magFilter = r, this.minFilter = a, this.anisotropy = c, this.format = o, this.internalFormat = null, this.type = l, this.offset = new Se(0, 0), this.repeat = new Se(1, 1), this.center = new Se(0, 0), this.rotation = 0, this.matrixAutoUpdate = !0, this.matrix = new Gt(), this.generateMipmaps = !0, this.premultiplyAlpha = !1, this.flipY = !0, this.unpackAlignment = 4, this.colorSpace = h, this.userData = {}, this.version = 0, this.onUpdate = null, this.isRenderTargetTexture = !1, this.pmremVersion = 0;
  }
  get image() {
    return this.source.data;
  }
  set image(e = null) {
    this.source.data = e;
  }
  updateMatrix() {
    this.matrix.setUvTransform(this.offset.x, this.offset.y, this.repeat.x, this.repeat.y, this.rotation, this.center.x, this.center.y);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.name = e.name, this.source = e.source, this.mipmaps = e.mipmaps.slice(0), this.mapping = e.mapping, this.channel = e.channel, this.wrapS = e.wrapS, this.wrapT = e.wrapT, this.magFilter = e.magFilter, this.minFilter = e.minFilter, this.anisotropy = e.anisotropy, this.format = e.format, this.internalFormat = e.internalFormat, this.type = e.type, this.offset.copy(e.offset), this.repeat.copy(e.repeat), this.center.copy(e.center), this.rotation = e.rotation, this.matrixAutoUpdate = e.matrixAutoUpdate, this.matrix.copy(e.matrix), this.generateMipmaps = e.generateMipmaps, this.premultiplyAlpha = e.premultiplyAlpha, this.flipY = e.flipY, this.unpackAlignment = e.unpackAlignment, this.colorSpace = e.colorSpace, this.userData = JSON.parse(JSON.stringify(e.userData)), this.needsUpdate = !0, this;
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    if (!t && e.textures[this.uuid] !== void 0)
      return e.textures[this.uuid];
    const i = {
      metadata: {
        version: 4.6,
        type: "Texture",
        generator: "Texture.toJSON"
      },
      uuid: this.uuid,
      name: this.name,
      image: this.source.toJSON(e).uuid,
      mapping: this.mapping,
      channel: this.channel,
      repeat: [this.repeat.x, this.repeat.y],
      offset: [this.offset.x, this.offset.y],
      center: [this.center.x, this.center.y],
      rotation: this.rotation,
      wrap: [this.wrapS, this.wrapT],
      format: this.format,
      internalFormat: this.internalFormat,
      type: this.type,
      colorSpace: this.colorSpace,
      minFilter: this.minFilter,
      magFilter: this.magFilter,
      anisotropy: this.anisotropy,
      flipY: this.flipY,
      generateMipmaps: this.generateMipmaps,
      premultiplyAlpha: this.premultiplyAlpha,
      unpackAlignment: this.unpackAlignment
    };
    return Object.keys(this.userData).length > 0 && (i.userData = this.userData), t || (e.textures[this.uuid] = i), i;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
  transformUv(e) {
    if (this.mapping !== Ho)
      return e;
    if (e.applyMatrix3(this.matrix), e.x < 0 || e.x > 1)
      switch (this.wrapS) {
        case un:
          e.x = e.x - Math.floor(e.x);
          break;
        case yn:
          e.x = e.x < 0 ? 0 : 1;
          break;
        case co:
          Math.abs(Math.floor(e.x) % 2) === 1 ? e.x = Math.ceil(e.x) - e.x : e.x = e.x - Math.floor(e.x);
          break;
      }
    if (e.y < 0 || e.y > 1)
      switch (this.wrapT) {
        case un:
          e.y = e.y - Math.floor(e.y);
          break;
        case yn:
          e.y = e.y < 0 ? 0 : 1;
          break;
        case co:
          Math.abs(Math.floor(e.y) % 2) === 1 ? e.y = Math.ceil(e.y) - e.y : e.y = e.y - Math.floor(e.y);
          break;
      }
    return this.flipY && (e.y = 1 - e.y), e;
  }
  set needsUpdate(e) {
    e === !0 && (this.version++, this.source.needsUpdate = !0);
  }
  set needsPMREMUpdate(e) {
    e === !0 && this.pmremVersion++;
  }
};
Ut.DEFAULT_IMAGE = null;
Ut.DEFAULT_MAPPING = Ho;
Ut.DEFAULT_ANISOTROPY = 1;
let Yt = class HL {
  constructor(e = 0, t = 0, i = 0, n = 1) {
    HL.prototype.isVector4 = !0, this.x = e, this.y = t, this.z = i, this.w = n;
  }
  get width() {
    return this.z;
  }
  set width(e) {
    this.z = e;
  }
  get height() {
    return this.w;
  }
  set height(e) {
    this.w = e;
  }
  set(e, t, i, n) {
    return this.x = e, this.y = t, this.z = i, this.w = n, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this.z = e, this.w = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setZ(e) {
    return this.z = e, this;
  }
  setW(e) {
    return this.w = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      case 2:
        this.z = t;
        break;
      case 3:
        this.w = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      case 3:
        return this.w;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z, this.w);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this.z = e.z, this.w = e.w !== void 0 ? e.w : 1, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this.z += e.z, this.w += e.w, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this.z += e, this.w += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this.z = e.z + t.z, this.w = e.w + t.w, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this.z += e.z * t, this.w += e.w * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this.z -= e.z, this.w -= e.w, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this.z -= e, this.w -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this.z = e.z - t.z, this.w = e.w - t.w, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this.z *= e.z, this.w *= e.w, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this.z *= e, this.w *= e, this;
  }
  applyMatrix4(e) {
    const t = this.x, i = this.y, n = this.z, r = this.w, a = e.elements;
    return this.x = a[0] * t + a[4] * i + a[8] * n + a[12] * r, this.y = a[1] * t + a[5] * i + a[9] * n + a[13] * r, this.z = a[2] * t + a[6] * i + a[10] * n + a[14] * r, this.w = a[3] * t + a[7] * i + a[11] * n + a[15] * r, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  setAxisAngleFromQuaternion(e) {
    this.w = 2 * Math.acos(e.w);
    const t = Math.sqrt(1 - e.w * e.w);
    return t < 1e-4 ? (this.x = 1, this.y = 0, this.z = 0) : (this.x = e.x / t, this.y = e.y / t, this.z = e.z / t), this;
  }
  setAxisAngleFromRotationMatrix(e) {
    let t, i, n, r;
    const l = e.elements, c = l[0], h = l[4], u = l[8], d = l[1], f = l[5], p = l[9], v = l[2], g = l[6], m = l[10];
    if (Math.abs(h - d) < 0.01 && Math.abs(u - v) < 0.01 && Math.abs(p - g) < 0.01) {
      if (Math.abs(h + d) < 0.1 && Math.abs(u + v) < 0.1 && Math.abs(p + g) < 0.1 && Math.abs(c + f + m - 3) < 0.1)
        return this.set(1, 0, 0, 0), this;
      t = Math.PI;
      const y = (c + 1) / 2, A = (f + 1) / 2, b = (m + 1) / 2, M = (h + d) / 4, w = (u + v) / 4, C = (p + g) / 4;
      return y > A && y > b ? y < 0.01 ? (i = 0, n = 0.707106781, r = 0.707106781) : (i = Math.sqrt(y), n = M / i, r = w / i) : A > b ? A < 0.01 ? (i = 0.707106781, n = 0, r = 0.707106781) : (n = Math.sqrt(A), i = M / n, r = C / n) : b < 0.01 ? (i = 0.707106781, n = 0.707106781, r = 0) : (r = Math.sqrt(b), i = w / r, n = C / r), this.set(i, n, r, t), this;
    }
    let x = Math.sqrt((g - p) * (g - p) + (u - v) * (u - v) + (d - h) * (d - h));
    return Math.abs(x) < 1e-3 && (x = 1), this.x = (g - p) / x, this.y = (u - v) / x, this.z = (d - h) / x, this.w = Math.acos((c + f + m - 1) / 2), this;
  }
  setFromMatrixPosition(e) {
    const t = e.elements;
    return this.x = t[12], this.y = t[13], this.z = t[14], this.w = t[15], this;
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this.z = Math.min(this.z, e.z), this.w = Math.min(this.w, e.w), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this.z = Math.max(this.z, e.z), this.w = Math.max(this.w, e.w), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this.z = Math.max(e.z, Math.min(t.z, this.z)), this.w = Math.max(e.w, Math.min(t.w, this.w)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this.z = Math.max(e, Math.min(t, this.z)), this.w = Math.max(e, Math.min(t, this.w)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this.z = Math.floor(this.z), this.w = Math.floor(this.w), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this.z = Math.ceil(this.z), this.w = Math.ceil(this.w), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this.z = Math.round(this.z), this.w = Math.round(this.w), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this.z = Math.trunc(this.z), this.w = Math.trunc(this.w), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this.z = -this.z, this.w = -this.w, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y + this.z * e.z + this.w * e.w;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z) + Math.abs(this.w);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this.z += (e.z - this.z) * t, this.w += (e.w - this.w) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this.z = e.z + (t.z - e.z) * i, this.w = e.w + (t.w - e.w) * i, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y && e.z === this.z && e.w === this.w;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this.z = e[t + 2], this.w = e[t + 3], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e[t + 2] = this.z, e[t + 3] = this.w, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this.z = e.getZ(t), this.w = e.getW(t), this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this.z = Math.random(), this.w = Math.random(), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y, yield this.z, yield this.w;
  }
}, VL = class extends ra {
  constructor(e = 1, t = 1, i = {}) {
    super(), this.isRenderTarget = !0, this.width = e, this.height = t, this.depth = 1, this.scissor = new Yt(0, 0, e, t), this.scissorTest = !1, this.viewport = new Yt(0, 0, e, t);
    const n = { width: e, height: t, depth: 1 };
    i = Object.assign({
      generateMipmaps: !1,
      internalFormat: null,
      minFilter: ii,
      depthBuffer: !0,
      stencilBuffer: !1,
      resolveDepthBuffer: !0,
      resolveStencilBuffer: !0,
      depthTexture: null,
      samples: 0,
      count: 1
    }, i);
    const r = new Ut(n, i.mapping, i.wrapS, i.wrapT, i.magFilter, i.minFilter, i.format, i.type, i.anisotropy, i.colorSpace);
    r.flipY = !1, r.generateMipmaps = i.generateMipmaps, r.internalFormat = i.internalFormat, this.textures = [];
    const a = i.count;
    for (let o = 0; o < a; o++)
      this.textures[o] = r.clone(), this.textures[o].isRenderTargetTexture = !0;
    this.depthBuffer = i.depthBuffer, this.stencilBuffer = i.stencilBuffer, this.resolveDepthBuffer = i.resolveDepthBuffer, this.resolveStencilBuffer = i.resolveStencilBuffer, this.depthTexture = i.depthTexture, this.samples = i.samples;
  }
  get texture() {
    return this.textures[0];
  }
  set texture(e) {
    this.textures[0] = e;
  }
  setSize(e, t, i = 1) {
    if (this.width !== e || this.height !== t || this.depth !== i) {
      this.width = e, this.height = t, this.depth = i;
      for (let n = 0, r = this.textures.length; n < r; n++)
        this.textures[n].image.width = e, this.textures[n].image.height = t, this.textures[n].image.depth = i;
      this.dispose();
    }
    this.viewport.set(0, 0, e, t), this.scissor.set(0, 0, e, t);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.width = e.width, this.height = e.height, this.depth = e.depth, this.scissor.copy(e.scissor), this.scissorTest = e.scissorTest, this.viewport.copy(e.viewport), this.textures.length = 0;
    for (let i = 0, n = e.textures.length; i < n; i++)
      this.textures[i] = e.textures[i].clone(), this.textures[i].isRenderTargetTexture = !0;
    const t = Object.assign({}, e.texture.image);
    return this.texture.source = new wa(t), this.depthBuffer = e.depthBuffer, this.stencilBuffer = e.stencilBuffer, this.resolveDepthBuffer = e.resolveDepthBuffer, this.resolveStencilBuffer = e.resolveStencilBuffer, e.depthTexture !== null && (this.depthTexture = e.depthTexture.clone()), this.samples = e.samples, this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
}, Xi = class extends VL {
  constructor(e = 1, t = 1, i = {}) {
    super(e, t, i), this.isWebGLRenderTarget = !0;
  }
}, b4 = class extends Ut {
  constructor(e = null, t = 1, i = 1, n = 1) {
    super(null), this.isDataArrayTexture = !0, this.image = { data: e, width: t, height: i, depth: n }, this.magFilter = Ni, this.minFilter = Ni, this.wrapR = yn, this.generateMipmaps = !1, this.flipY = !1, this.unpackAlignment = 1, this.layerUpdates = /* @__PURE__ */ new Set();
  }
  addLayerUpdate(e) {
    this.layerUpdates.add(e);
  }
  clearLayerUpdates() {
    this.layerUpdates.clear();
  }
};
class pk extends Xi {
  constructor(e = 1, t = 1, i = 1, n = {}) {
    super(e, t, n), this.isWebGLArrayRenderTarget = !0, this.depth = i, this.texture = new b4(null, e, t, i), this.texture.isRenderTargetTexture = !0;
  }
}
let BM = class extends Ut {
  constructor(e = null, t = 1, i = 1, n = 1) {
    super(null), this.isData3DTexture = !0, this.image = { data: e, width: t, height: i, depth: n }, this.magFilter = Ni, this.minFilter = Ni, this.wrapR = yn, this.generateMipmaps = !1, this.flipY = !1, this.unpackAlignment = 1;
  }
};
class mk extends Xi {
  constructor(e = 1, t = 1, i = 1, n = {}) {
    super(e, t, n), this.isWebGL3DRenderTarget = !0, this.depth = i, this.texture = new BM(null, e, t, i), this.texture.isRenderTargetTexture = !0;
  }
}
let St = class {
  constructor(e = 0, t = 0, i = 0, n = 1) {
    this.isQuaternion = !0, this._x = e, this._y = t, this._z = i, this._w = n;
  }
  static slerpFlat(e, t, i, n, r, a, o) {
    let l = i[n + 0], c = i[n + 1], h = i[n + 2], u = i[n + 3];
    const d = r[a + 0], f = r[a + 1], p = r[a + 2], v = r[a + 3];
    if (o === 0) {
      e[t + 0] = l, e[t + 1] = c, e[t + 2] = h, e[t + 3] = u;
      return;
    }
    if (o === 1) {
      e[t + 0] = d, e[t + 1] = f, e[t + 2] = p, e[t + 3] = v;
      return;
    }
    if (u !== v || l !== d || c !== f || h !== p) {
      let g = 1 - o;
      const m = l * d + c * f + h * p + u * v, x = m >= 0 ? 1 : -1, y = 1 - m * m;
      if (y > Number.EPSILON) {
        const b = Math.sqrt(y), M = Math.atan2(b, m * x);
        g = Math.sin(g * M) / b, o = Math.sin(o * M) / b;
      }
      const A = o * x;
      if (l = l * g + d * A, c = c * g + f * A, h = h * g + p * A, u = u * g + v * A, g === 1 - o) {
        const b = 1 / Math.sqrt(l * l + c * c + h * h + u * u);
        l *= b, c *= b, h *= b, u *= b;
      }
    }
    e[t] = l, e[t + 1] = c, e[t + 2] = h, e[t + 3] = u;
  }
  static multiplyQuaternionsFlat(e, t, i, n, r, a) {
    const o = i[n], l = i[n + 1], c = i[n + 2], h = i[n + 3], u = r[a], d = r[a + 1], f = r[a + 2], p = r[a + 3];
    return e[t] = o * p + h * u + l * f - c * d, e[t + 1] = l * p + h * d + c * u - o * f, e[t + 2] = c * p + h * f + o * d - l * u, e[t + 3] = h * p - o * u - l * d - c * f, e;
  }
  get x() {
    return this._x;
  }
  set x(e) {
    this._x = e, this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(e) {
    this._y = e, this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(e) {
    this._z = e, this._onChangeCallback();
  }
  get w() {
    return this._w;
  }
  set w(e) {
    this._w = e, this._onChangeCallback();
  }
  set(e, t, i, n) {
    return this._x = e, this._y = t, this._z = i, this._w = n, this._onChangeCallback(), this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._w);
  }
  copy(e) {
    return this._x = e.x, this._y = e.y, this._z = e.z, this._w = e.w, this._onChangeCallback(), this;
  }
  setFromEuler(e, t = !0) {
    const i = e._x, n = e._y, r = e._z, a = e._order, o = Math.cos, l = Math.sin, c = o(i / 2), h = o(n / 2), u = o(r / 2), d = l(i / 2), f = l(n / 2), p = l(r / 2);
    switch (a) {
      case "XYZ":
        this._x = d * h * u + c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "YXZ":
        this._x = d * h * u + c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u + d * f * p;
        break;
      case "ZXY":
        this._x = d * h * u - c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "ZYX":
        this._x = d * h * u - c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u + d * f * p;
        break;
      case "YZX":
        this._x = d * h * u + c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "XZY":
        this._x = d * h * u - c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u + d * f * p;
        break;
      default:
        console.warn("THREE.Quaternion: .setFromEuler() encountered an unknown order: " + a);
    }
    return t === !0 && this._onChangeCallback(), this;
  }
  setFromAxisAngle(e, t) {
    const i = t / 2, n = Math.sin(i);
    return this._x = e.x * n, this._y = e.y * n, this._z = e.z * n, this._w = Math.cos(i), this._onChangeCallback(), this;
  }
  setFromRotationMatrix(e) {
    const t = e.elements, i = t[0], n = t[4], r = t[8], a = t[1], o = t[5], l = t[9], c = t[2], h = t[6], u = t[10], d = i + o + u;
    if (d > 0) {
      const f = 0.5 / Math.sqrt(d + 1);
      this._w = 0.25 / f, this._x = (h - l) * f, this._y = (r - c) * f, this._z = (a - n) * f;
    } else if (i > o && i > u) {
      const f = 2 * Math.sqrt(1 + i - o - u);
      this._w = (h - l) / f, this._x = 0.25 * f, this._y = (n + a) / f, this._z = (r + c) / f;
    } else if (o > u) {
      const f = 2 * Math.sqrt(1 + o - i - u);
      this._w = (r - c) / f, this._x = (n + a) / f, this._y = 0.25 * f, this._z = (l + h) / f;
    } else {
      const f = 2 * Math.sqrt(1 + u - i - o);
      this._w = (a - n) / f, this._x = (r + c) / f, this._y = (l + h) / f, this._z = 0.25 * f;
    }
    return this._onChangeCallback(), this;
  }
  setFromUnitVectors(e, t) {
    let i = e.dot(t) + 1;
    return i < Number.EPSILON ? (i = 0, Math.abs(e.x) > Math.abs(e.z) ? (this._x = -e.y, this._y = e.x, this._z = 0, this._w = i) : (this._x = 0, this._y = -e.z, this._z = e.y, this._w = i)) : (this._x = e.y * t.z - e.z * t.y, this._y = e.z * t.x - e.x * t.z, this._z = e.x * t.y - e.y * t.x, this._w = i), this.normalize();
  }
  angleTo(e) {
    return 2 * Math.acos(Math.abs(Wn(this.dot(e), -1, 1)));
  }
  rotateTowards(e, t) {
    const i = this.angleTo(e);
    if (i === 0)
      return this;
    const n = Math.min(1, t / i);
    return this.slerp(e, n), this;
  }
  identity() {
    return this.set(0, 0, 0, 1);
  }
  invert() {
    return this.conjugate();
  }
  conjugate() {
    return this._x *= -1, this._y *= -1, this._z *= -1, this._onChangeCallback(), this;
  }
  dot(e) {
    return this._x * e._x + this._y * e._y + this._z * e._z + this._w * e._w;
  }
  lengthSq() {
    return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;
  }
  length() {
    return Math.sqrt(this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w);
  }
  normalize() {
    let e = this.length();
    return e === 0 ? (this._x = 0, this._y = 0, this._z = 0, this._w = 1) : (e = 1 / e, this._x = this._x * e, this._y = this._y * e, this._z = this._z * e, this._w = this._w * e), this._onChangeCallback(), this;
  }
  multiply(e) {
    return this.multiplyQuaternions(this, e);
  }
  premultiply(e) {
    return this.multiplyQuaternions(e, this);
  }
  multiplyQuaternions(e, t) {
    const i = e._x, n = e._y, r = e._z, a = e._w, o = t._x, l = t._y, c = t._z, h = t._w;
    return this._x = i * h + a * o + n * c - r * l, this._y = n * h + a * l + r * o - i * c, this._z = r * h + a * c + i * l - n * o, this._w = a * h - i * o - n * l - r * c, this._onChangeCallback(), this;
  }
  slerp(e, t) {
    if (t === 0)
      return this;
    if (t === 1)
      return this.copy(e);
    const i = this._x, n = this._y, r = this._z, a = this._w;
    let o = a * e._w + i * e._x + n * e._y + r * e._z;
    if (o < 0 ? (this._w = -e._w, this._x = -e._x, this._y = -e._y, this._z = -e._z, o = -o) : this.copy(e), o >= 1)
      return this._w = a, this._x = i, this._y = n, this._z = r, this;
    const l = 1 - o * o;
    if (l <= Number.EPSILON) {
      const f = 1 - t;
      return this._w = f * a + t * this._w, this._x = f * i + t * this._x, this._y = f * n + t * this._y, this._z = f * r + t * this._z, this.normalize(), this;
    }
    const c = Math.sqrt(l), h = Math.atan2(c, o), u = Math.sin((1 - t) * h) / c, d = Math.sin(t * h) / c;
    return this._w = a * u + this._w * d, this._x = i * u + this._x * d, this._y = n * u + this._y * d, this._z = r * u + this._z * d, this._onChangeCallback(), this;
  }
  slerpQuaternions(e, t, i) {
    return this.copy(e).slerp(t, i);
  }
  random() {
    const e = 2 * Math.PI * Math.random(), t = 2 * Math.PI * Math.random(), i = Math.random(), n = Math.sqrt(1 - i), r = Math.sqrt(i);
    return this.set(
      n * Math.sin(e),
      n * Math.cos(e),
      r * Math.sin(t),
      r * Math.cos(t)
    );
  }
  equals(e) {
    return e._x === this._x && e._y === this._y && e._z === this._z && e._w === this._w;
  }
  fromArray(e, t = 0) {
    return this._x = e[t], this._y = e[t + 1], this._z = e[t + 2], this._w = e[t + 3], this._onChangeCallback(), this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this._x, e[t + 1] = this._y, e[t + 2] = this._z, e[t + 3] = this._w, e;
  }
  fromBufferAttribute(e, t) {
    return this._x = e.getX(t), this._y = e.getY(t), this._z = e.getZ(t), this._w = e.getW(t), this._onChangeCallback(), this;
  }
  toJSON() {
    return this.toArray();
  }
  _onChange(e) {
    return this._onChangeCallback = e, this;
  }
  _onChangeCallback() {
  }
  *[Symbol.iterator]() {
    yield this._x, yield this._y, yield this._z, yield this._w;
  }
}, _ = class WL {
  constructor(e = 0, t = 0, i = 0) {
    WL.prototype.isVector3 = !0, this.x = e, this.y = t, this.z = i;
  }
  set(e, t, i) {
    return i === void 0 && (i = this.z), this.x = e, this.y = t, this.z = i, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this.z = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setZ(e) {
    return this.z = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      case 2:
        this.z = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this.z = e.z, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this.z += e.z, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this.z += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this.z = e.z + t.z, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this.z += e.z * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this.z -= e.z, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this.z -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this.z = e.z - t.z, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this.z *= e.z, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this.z *= e, this;
  }
  multiplyVectors(e, t) {
    return this.x = e.x * t.x, this.y = e.y * t.y, this.z = e.z * t.z, this;
  }
  applyEuler(e) {
    return this.applyQuaternion(qw.setFromEuler(e));
  }
  applyAxisAngle(e, t) {
    return this.applyQuaternion(qw.setFromAxisAngle(e, t));
  }
  applyMatrix3(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements;
    return this.x = r[0] * t + r[3] * i + r[6] * n, this.y = r[1] * t + r[4] * i + r[7] * n, this.z = r[2] * t + r[5] * i + r[8] * n, this;
  }
  applyNormalMatrix(e) {
    return this.applyMatrix3(e).normalize();
  }
  applyMatrix4(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements, a = 1 / (r[3] * t + r[7] * i + r[11] * n + r[15]);
    return this.x = (r[0] * t + r[4] * i + r[8] * n + r[12]) * a, this.y = (r[1] * t + r[5] * i + r[9] * n + r[13]) * a, this.z = (r[2] * t + r[6] * i + r[10] * n + r[14]) * a, this;
  }
  applyQuaternion(e) {
    const t = this.x, i = this.y, n = this.z, r = e.x, a = e.y, o = e.z, l = e.w, c = 2 * (a * n - o * i), h = 2 * (o * t - r * n), u = 2 * (r * i - a * t);
    return this.x = t + l * c + a * u - o * h, this.y = i + l * h + o * c - r * u, this.z = n + l * u + r * h - a * c, this;
  }
  project(e) {
    return this.applyMatrix4(e.matrixWorldInverse).applyMatrix4(e.projectionMatrix);
  }
  unproject(e) {
    return this.applyMatrix4(e.projectionMatrixInverse).applyMatrix4(e.matrixWorld);
  }
  transformDirection(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements;
    return this.x = r[0] * t + r[4] * i + r[8] * n, this.y = r[1] * t + r[5] * i + r[9] * n, this.z = r[2] * t + r[6] * i + r[10] * n, this.normalize();
  }
  divide(e) {
    return this.x /= e.x, this.y /= e.y, this.z /= e.z, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this.z = Math.min(this.z, e.z), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this.z = Math.max(this.z, e.z), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this.z = Math.max(e.z, Math.min(t.z, this.z)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this.z = Math.max(e, Math.min(t, this.z)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this.z = Math.floor(this.z), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this.z = Math.ceil(this.z), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this.z = Math.round(this.z), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this.z = Math.trunc(this.z), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this.z = -this.z, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y + this.z * e.z;
  }
  // TODO lengthSquared?
  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this.z += (e.z - this.z) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this.z = e.z + (t.z - e.z) * i, this;
  }
  cross(e) {
    return this.crossVectors(this, e);
  }
  crossVectors(e, t) {
    const i = e.x, n = e.y, r = e.z, a = t.x, o = t.y, l = t.z;
    return this.x = n * l - r * o, this.y = r * a - i * l, this.z = i * o - n * a, this;
  }
  projectOnVector(e) {
    const t = e.lengthSq();
    if (t === 0)
      return this.set(0, 0, 0);
    const i = e.dot(this) / t;
    return this.copy(e).multiplyScalar(i);
  }
  projectOnPlane(e) {
    return w8.copy(this).projectOnVector(e), this.sub(w8);
  }
  reflect(e) {
    return this.sub(w8.copy(e).multiplyScalar(2 * this.dot(e)));
  }
  angleTo(e) {
    const t = Math.sqrt(this.lengthSq() * e.lengthSq());
    if (t === 0)
      return Math.PI / 2;
    const i = this.dot(e) / t;
    return Math.acos(Wn(i, -1, 1));
  }
  distanceTo(e) {
    return Math.sqrt(this.distanceToSquared(e));
  }
  distanceToSquared(e) {
    const t = this.x - e.x, i = this.y - e.y, n = this.z - e.z;
    return t * t + i * i + n * n;
  }
  manhattanDistanceTo(e) {
    return Math.abs(this.x - e.x) + Math.abs(this.y - e.y) + Math.abs(this.z - e.z);
  }
  setFromSpherical(e) {
    return this.setFromSphericalCoords(e.radius, e.phi, e.theta);
  }
  setFromSphericalCoords(e, t, i) {
    const n = Math.sin(t) * e;
    return this.x = n * Math.sin(i), this.y = Math.cos(t) * e, this.z = n * Math.cos(i), this;
  }
  setFromCylindrical(e) {
    return this.setFromCylindricalCoords(e.radius, e.theta, e.y);
  }
  setFromCylindricalCoords(e, t, i) {
    return this.x = e * Math.sin(t), this.y = i, this.z = e * Math.cos(t), this;
  }
  setFromMatrixPosition(e) {
    const t = e.elements;
    return this.x = t[12], this.y = t[13], this.z = t[14], this;
  }
  setFromMatrixScale(e) {
    const t = this.setFromMatrixColumn(e, 0).length(), i = this.setFromMatrixColumn(e, 1).length(), n = this.setFromMatrixColumn(e, 2).length();
    return this.x = t, this.y = i, this.z = n, this;
  }
  setFromMatrixColumn(e, t) {
    return this.fromArray(e.elements, t * 4);
  }
  setFromMatrix3Column(e, t) {
    return this.fromArray(e.elements, t * 3);
  }
  setFromEuler(e) {
    return this.x = e._x, this.y = e._y, this.z = e._z, this;
  }
  setFromColor(e) {
    return this.x = e.r, this.y = e.g, this.z = e.b, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y && e.z === this.z;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this.z = e[t + 2], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e[t + 2] = this.z, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this.z = e.getZ(t), this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this.z = Math.random(), this;
  }
  randomDirection() {
    const e = Math.random() * Math.PI * 2, t = Math.random() * 2 - 1, i = Math.sqrt(1 - t * t);
    return this.x = i * Math.cos(e), this.y = t, this.z = i * Math.sin(e), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y, yield this.z;
  }
};
const w8 = /* @__PURE__ */ new _(), qw = /* @__PURE__ */ new St();
let zt = class {
  constructor(e = new _(1 / 0, 1 / 0, 1 / 0), t = new _(-1 / 0, -1 / 0, -1 / 0)) {
    this.isBox3 = !0, this.min = e, this.max = t;
  }
  set(e, t) {
    return this.min.copy(e), this.max.copy(t), this;
  }
  setFromArray(e) {
    this.makeEmpty();
    for (let t = 0, i = e.length; t < i; t += 3)
      this.expandByPoint(Oa.fromArray(e, t));
    return this;
  }
  setFromBufferAttribute(e) {
    this.makeEmpty();
    for (let t = 0, i = e.count; t < i; t++)
      this.expandByPoint(Oa.fromBufferAttribute(e, t));
    return this;
  }
  setFromPoints(e) {
    this.makeEmpty();
    for (let t = 0, i = e.length; t < i; t++)
      this.expandByPoint(e[t]);
    return this;
  }
  setFromCenterAndSize(e, t) {
    const i = Oa.copy(t).multiplyScalar(0.5);
    return this.min.copy(e).sub(i), this.max.copy(e).add(i), this;
  }
  setFromObject(e, t = !1) {
    return this.makeEmpty(), this.expandByObject(e, t);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.min.copy(e.min), this.max.copy(e.max), this;
  }
  makeEmpty() {
    return this.min.x = this.min.y = this.min.z = 1 / 0, this.max.x = this.max.y = this.max.z = -1 / 0, this;
  }
  isEmpty() {
    return this.max.x < this.min.x || this.max.y < this.min.y || this.max.z < this.min.z;
  }
  getCenter(e) {
    return this.isEmpty() ? e.set(0, 0, 0) : e.addVectors(this.min, this.max).multiplyScalar(0.5);
  }
  getSize(e) {
    return this.isEmpty() ? e.set(0, 0, 0) : e.subVectors(this.max, this.min);
  }
  expandByPoint(e) {
    return this.min.min(e), this.max.max(e), this;
  }
  expandByVector(e) {
    return this.min.sub(e), this.max.add(e), this;
  }
  expandByScalar(e) {
    return this.min.addScalar(-e), this.max.addScalar(e), this;
  }
  expandByObject(e, t = !1) {
    e.updateWorldMatrix(!1, !1);
    const i = e.geometry;
    if (i !== void 0) {
      const r = i.getAttribute("position");
      if (t === !0 && r !== void 0 && e.isInstancedMesh !== !0)
        for (let a = 0, o = r.count; a < o; a++)
          e.isMesh === !0 ? e.getVertexPosition(a, Oa) : Oa.fromBufferAttribute(r, a), Oa.applyMatrix4(e.matrixWorld), this.expandByPoint(Oa);
      else
        e.boundingBox !== void 0 ? (e.boundingBox === null && e.computeBoundingBox(), Z2.copy(e.boundingBox)) : (i.boundingBox === null && i.computeBoundingBox(), Z2.copy(i.boundingBox)), Z2.applyMatrix4(e.matrixWorld), this.union(Z2);
    }
    const n = e.children;
    for (let r = 0, a = n.length; r < a; r++)
      this.expandByObject(n[r], t);
    return this;
  }
  containsPoint(e) {
    return !(e.x < this.min.x || e.x > this.max.x || e.y < this.min.y || e.y > this.max.y || e.z < this.min.z || e.z > this.max.z);
  }
  containsBox(e) {
    return this.min.x <= e.min.x && e.max.x <= this.max.x && this.min.y <= e.min.y && e.max.y <= this.max.y && this.min.z <= e.min.z && e.max.z <= this.max.z;
  }
  getParameter(e, t) {
    return t.set(
      (e.x - this.min.x) / (this.max.x - this.min.x),
      (e.y - this.min.y) / (this.max.y - this.min.y),
      (e.z - this.min.z) / (this.max.z - this.min.z)
    );
  }
  intersectsBox(e) {
    return !(e.max.x < this.min.x || e.min.x > this.max.x || e.max.y < this.min.y || e.min.y > this.max.y || e.max.z < this.min.z || e.min.z > this.max.z);
  }
  intersectsSphere(e) {
    return this.clampPoint(e.center, Oa), Oa.distanceToSquared(e.center) <= e.radius * e.radius;
  }
  intersectsPlane(e) {
    let t, i;
    return e.normal.x > 0 ? (t = e.normal.x * this.min.x, i = e.normal.x * this.max.x) : (t = e.normal.x * this.max.x, i = e.normal.x * this.min.x), e.normal.y > 0 ? (t += e.normal.y * this.min.y, i += e.normal.y * this.max.y) : (t += e.normal.y * this.max.y, i += e.normal.y * this.min.y), e.normal.z > 0 ? (t += e.normal.z * this.min.z, i += e.normal.z * this.max.z) : (t += e.normal.z * this.max.z, i += e.normal.z * this.min.z), t <= -e.constant && i >= -e.constant;
  }
  intersectsTriangle(e) {
    if (this.isEmpty())
      return !1;
    this.getCenter(om), K2.subVectors(this.max, om), rd.subVectors(e.a, om), ad.subVectors(e.b, om), od.subVectors(e.c, om), Ol.subVectors(ad, rd), Nl.subVectors(od, ad), fh.subVectors(rd, od);
    let t = [
      0,
      -Ol.z,
      Ol.y,
      0,
      -Nl.z,
      Nl.y,
      0,
      -fh.z,
      fh.y,
      Ol.z,
      0,
      -Ol.x,
      Nl.z,
      0,
      -Nl.x,
      fh.z,
      0,
      -fh.x,
      -Ol.y,
      Ol.x,
      0,
      -Nl.y,
      Nl.x,
      0,
      -fh.y,
      fh.x,
      0
    ];
    return !E8(t, rd, ad, od, K2) || (t = [1, 0, 0, 0, 1, 0, 0, 0, 1], !E8(t, rd, ad, od, K2)) ? !1 : (J2.crossVectors(Ol, Nl), t = [J2.x, J2.y, J2.z], E8(t, rd, ad, od, K2));
  }
  clampPoint(e, t) {
    return t.copy(e).clamp(this.min, this.max);
  }
  distanceToPoint(e) {
    return this.clampPoint(e, Oa).distanceTo(e);
  }
  getBoundingSphere(e) {
    return this.isEmpty() ? e.makeEmpty() : (this.getCenter(e.center), e.radius = this.getSize(Oa).length() * 0.5), e;
  }
  intersect(e) {
    return this.min.max(e.min), this.max.min(e.max), this.isEmpty() && this.makeEmpty(), this;
  }
  union(e) {
    return this.min.min(e.min), this.max.max(e.max), this;
  }
  applyMatrix4(e) {
    return this.isEmpty() ? this : (S0[0].set(this.min.x, this.min.y, this.min.z).applyMatrix4(e), S0[1].set(this.min.x, this.min.y, this.max.z).applyMatrix4(e), S0[2].set(this.min.x, this.max.y, this.min.z).applyMatrix4(e), S0[3].set(this.min.x, this.max.y, this.max.z).applyMatrix4(e), S0[4].set(this.max.x, this.min.y, this.min.z).applyMatrix4(e), S0[5].set(this.max.x, this.min.y, this.max.z).applyMatrix4(e), S0[6].set(this.max.x, this.max.y, this.min.z).applyMatrix4(e), S0[7].set(this.max.x, this.max.y, this.max.z).applyMatrix4(e), this.setFromPoints(S0), this);
  }
  translate(e) {
    return this.min.add(e), this.max.add(e), this;
  }
  equals(e) {
    return e.min.equals(this.min) && e.max.equals(this.max);
  }
};
const S0 = [
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _(),
  /* @__PURE__ */ new _()
], Oa = /* @__PURE__ */ new _(), Z2 = /* @__PURE__ */ new zt(), rd = /* @__PURE__ */ new _(), ad = /* @__PURE__ */ new _(), od = /* @__PURE__ */ new _(), Ol = /* @__PURE__ */ new _(), Nl = /* @__PURE__ */ new _(), fh = /* @__PURE__ */ new _(), om = /* @__PURE__ */ new _(), K2 = /* @__PURE__ */ new _(), J2 = /* @__PURE__ */ new _(), ph = /* @__PURE__ */ new _();
function E8(s, e, t, i, n) {
  for (let r = 0, a = s.length - 3; r <= a; r += 3) {
    ph.fromArray(s, r);
    const o = n.x * Math.abs(ph.x) + n.y * Math.abs(ph.y) + n.z * Math.abs(ph.z), l = e.dot(ph), c = t.dot(ph), h = i.dot(ph);
    if (Math.max(-Math.max(l, c, h), Math.min(l, c, h)) > o)
      return !1;
  }
  return !0;
}
const gk = /* @__PURE__ */ new zt(), lm = /* @__PURE__ */ new _(), T8 = /* @__PURE__ */ new _();
let Tn = class {
  constructor(e = new _(), t = -1) {
    this.isSphere = !0, this.center = e, this.radius = t;
  }
  set(e, t) {
    return this.center.copy(e), this.radius = t, this;
  }
  setFromPoints(e, t) {
    const i = this.center;
    t !== void 0 ? i.copy(t) : gk.setFromPoints(e).getCenter(i);
    let n = 0;
    for (let r = 0, a = e.length; r < a; r++)
      n = Math.max(n, i.distanceToSquared(e[r]));
    return this.radius = Math.sqrt(n), this;
  }
  copy(e) {
    return this.center.copy(e.center), this.radius = e.radius, this;
  }
  isEmpty() {
    return this.radius < 0;
  }
  makeEmpty() {
    return this.center.set(0, 0, 0), this.radius = -1, this;
  }
  containsPoint(e) {
    return e.distanceToSquared(this.center) <= this.radius * this.radius;
  }
  distanceToPoint(e) {
    return e.distanceTo(this.center) - this.radius;
  }
  intersectsSphere(e) {
    const t = this.radius + e.radius;
    return e.center.distanceToSquared(this.center) <= t * t;
  }
  intersectsBox(e) {
    return e.intersectsSphere(this);
  }
  intersectsPlane(e) {
    return Math.abs(e.distanceToPoint(this.center)) <= this.radius;
  }
  clampPoint(e, t) {
    const i = this.center.distanceToSquared(e);
    return t.copy(e), i > this.radius * this.radius && (t.sub(this.center).normalize(), t.multiplyScalar(this.radius).add(this.center)), t;
  }
  getBoundingBox(e) {
    return this.isEmpty() ? (e.makeEmpty(), e) : (e.set(this.center, this.center), e.expandByScalar(this.radius), e);
  }
  applyMatrix4(e) {
    return this.center.applyMatrix4(e), this.radius = this.radius * e.getMaxScaleOnAxis(), this;
  }
  translate(e) {
    return this.center.add(e), this;
  }
  expandByPoint(e) {
    if (this.isEmpty())
      return this.center.copy(e), this.radius = 0, this;
    lm.subVectors(e, this.center);
    const t = lm.lengthSq();
    if (t > this.radius * this.radius) {
      const i = Math.sqrt(t), n = (i - this.radius) * 0.5;
      this.center.addScaledVector(lm, n / i), this.radius += n;
    }
    return this;
  }
  union(e) {
    return e.isEmpty() ? this : this.isEmpty() ? (this.copy(e), this) : (this.center.equals(e.center) === !0 ? this.radius = Math.max(this.radius, e.radius) : (T8.subVectors(e.center, this.center).setLength(e.radius), this.expandByPoint(lm.copy(e.center).add(T8)), this.expandByPoint(lm.copy(e.center).sub(T8))), this);
  }
  equals(e) {
    return e.center.equals(this.center) && e.radius === this.radius;
  }
  clone() {
    return new this.constructor().copy(this);
  }
};
const w0 = /* @__PURE__ */ new _(), C8 = /* @__PURE__ */ new _(), Q2 = /* @__PURE__ */ new _(), Bl = /* @__PURE__ */ new _(), _8 = /* @__PURE__ */ new _(), $2 = /* @__PURE__ */ new _(), P8 = /* @__PURE__ */ new _();
let g0 = class {
  constructor(e = new _(), t = new _(0, 0, -1)) {
    this.origin = e, this.direction = t;
  }
  set(e, t) {
    return this.origin.copy(e), this.direction.copy(t), this;
  }
  copy(e) {
    return this.origin.copy(e.origin), this.direction.copy(e.direction), this;
  }
  at(e, t) {
    return t.copy(this.origin).addScaledVector(this.direction, e);
  }
  lookAt(e) {
    return this.direction.copy(e).sub(this.origin).normalize(), this;
  }
  recast(e) {
    return this.origin.copy(this.at(e, w0)), this;
  }
  closestPointToPoint(e, t) {
    t.subVectors(e, this.origin);
    const i = t.dot(this.direction);
    return i < 0 ? t.copy(this.origin) : t.copy(this.origin).addScaledVector(this.direction, i);
  }
  distanceToPoint(e) {
    return Math.sqrt(this.distanceSqToPoint(e));
  }
  distanceSqToPoint(e) {
    const t = w0.subVectors(e, this.origin).dot(this.direction);
    return t < 0 ? this.origin.distanceToSquared(e) : (w0.copy(this.origin).addScaledVector(this.direction, t), w0.distanceToSquared(e));
  }
  distanceSqToSegment(e, t, i, n) {
    C8.copy(e).add(t).multiplyScalar(0.5), Q2.copy(t).sub(e).normalize(), Bl.copy(this.origin).sub(C8);
    const r = e.distanceTo(t) * 0.5, a = -this.direction.dot(Q2), o = Bl.dot(this.direction), l = -Bl.dot(Q2), c = Bl.lengthSq(), h = Math.abs(1 - a * a);
    let u, d, f, p;
    if (h > 0)
      if (u = a * l - o, d = a * o - l, p = r * h, u >= 0)
        if (d >= -p)
          if (d <= p) {
            const v = 1 / h;
            u *= v, d *= v, f = u * (u + a * d + 2 * o) + d * (a * u + d + 2 * l) + c;
          } else
            d = r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
        else
          d = -r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
      else
        d <= -p ? (u = Math.max(0, -(-a * r + o)), d = u > 0 ? -r : Math.min(Math.max(-r, -l), r), f = -u * u + d * (d + 2 * l) + c) : d <= p ? (u = 0, d = Math.min(Math.max(-r, -l), r), f = d * (d + 2 * l) + c) : (u = Math.max(0, -(a * r + o)), d = u > 0 ? r : Math.min(Math.max(-r, -l), r), f = -u * u + d * (d + 2 * l) + c);
    else
      d = a > 0 ? -r : r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
    return i && i.copy(this.origin).addScaledVector(this.direction, u), n && n.copy(C8).addScaledVector(Q2, d), f;
  }
  intersectSphere(e, t) {
    w0.subVectors(e.center, this.origin);
    const i = w0.dot(this.direction), n = w0.dot(w0) - i * i, r = e.radius * e.radius;
    if (n > r)
      return null;
    const a = Math.sqrt(r - n), o = i - a, l = i + a;
    return l < 0 ? null : o < 0 ? this.at(l, t) : this.at(o, t);
  }
  intersectsSphere(e) {
    return this.distanceSqToPoint(e.center) <= e.radius * e.radius;
  }
  distanceToPlane(e) {
    const t = e.normal.dot(this.direction);
    if (t === 0)
      return e.distanceToPoint(this.origin) === 0 ? 0 : null;
    const i = -(this.origin.dot(e.normal) + e.constant) / t;
    return i >= 0 ? i : null;
  }
  intersectPlane(e, t) {
    const i = this.distanceToPlane(e);
    return i === null ? null : this.at(i, t);
  }
  intersectsPlane(e) {
    const t = e.distanceToPoint(this.origin);
    return t === 0 || e.normal.dot(this.direction) * t < 0;
  }
  intersectBox(e, t) {
    let i, n, r, a, o, l;
    const c = 1 / this.direction.x, h = 1 / this.direction.y, u = 1 / this.direction.z, d = this.origin;
    return c >= 0 ? (i = (e.min.x - d.x) * c, n = (e.max.x - d.x) * c) : (i = (e.max.x - d.x) * c, n = (e.min.x - d.x) * c), h >= 0 ? (r = (e.min.y - d.y) * h, a = (e.max.y - d.y) * h) : (r = (e.max.y - d.y) * h, a = (e.min.y - d.y) * h), i > a || r > n || ((r > i || isNaN(i)) && (i = r), (a < n || isNaN(n)) && (n = a), u >= 0 ? (o = (e.min.z - d.z) * u, l = (e.max.z - d.z) * u) : (o = (e.max.z - d.z) * u, l = (e.min.z - d.z) * u), i > l || o > n) || ((o > i || i !== i) && (i = o), (l < n || n !== n) && (n = l), n < 0) ? null : this.at(i >= 0 ? i : n, t);
  }
  intersectsBox(e) {
    return this.intersectBox(e, w0) !== null;
  }
  intersectTriangle(e, t, i, n, r) {
    _8.subVectors(t, e), $2.subVectors(i, e), P8.crossVectors(_8, $2);
    let a = this.direction.dot(P8), o;
    if (a > 0) {
      if (n)
        return null;
      o = 1;
    } else if (a < 0)
      o = -1, a = -a;
    else
      return null;
    Bl.subVectors(this.origin, e);
    const l = o * this.direction.dot($2.crossVectors(Bl, $2));
    if (l < 0)
      return null;
    const c = o * this.direction.dot(_8.cross(Bl));
    if (c < 0 || l + c > a)
      return null;
    const h = -o * Bl.dot(P8);
    return h < 0 ? null : this.at(h / a, r);
  }
  applyMatrix4(e) {
    return this.origin.applyMatrix4(e), this.direction.transformDirection(e), this;
  }
  equals(e) {
    return e.origin.equals(this.origin) && e.direction.equals(this.direction);
  }
  clone() {
    return new this.constructor().copy(this);
  }
}, et = class X6 {
  constructor(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g) {
    X6.prototype.isMatrix4 = !0, this.elements = [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ], e !== void 0 && this.set(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g);
  }
  set(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g) {
    const m = this.elements;
    return m[0] = e, m[4] = t, m[8] = i, m[12] = n, m[1] = r, m[5] = a, m[9] = o, m[13] = l, m[2] = c, m[6] = h, m[10] = u, m[14] = d, m[3] = f, m[7] = p, m[11] = v, m[15] = g, this;
  }
  identity() {
    return this.set(
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  clone() {
    return new X6().fromArray(this.elements);
  }
  copy(e) {
    const t = this.elements, i = e.elements;
    return t[0] = i[0], t[1] = i[1], t[2] = i[2], t[3] = i[3], t[4] = i[4], t[5] = i[5], t[6] = i[6], t[7] = i[7], t[8] = i[8], t[9] = i[9], t[10] = i[10], t[11] = i[11], t[12] = i[12], t[13] = i[13], t[14] = i[14], t[15] = i[15], this;
  }
  copyPosition(e) {
    const t = this.elements, i = e.elements;
    return t[12] = i[12], t[13] = i[13], t[14] = i[14], this;
  }
  setFromMatrix3(e) {
    const t = e.elements;
    return this.set(
      t[0],
      t[3],
      t[6],
      0,
      t[1],
      t[4],
      t[7],
      0,
      t[2],
      t[5],
      t[8],
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  extractBasis(e, t, i) {
    return e.setFromMatrixColumn(this, 0), t.setFromMatrixColumn(this, 1), i.setFromMatrixColumn(this, 2), this;
  }
  makeBasis(e, t, i) {
    return this.set(
      e.x,
      t.x,
      i.x,
      0,
      e.y,
      t.y,
      i.y,
      0,
      e.z,
      t.z,
      i.z,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  extractRotation(e) {
    const t = this.elements, i = e.elements, n = 1 / ld.setFromMatrixColumn(e, 0).length(), r = 1 / ld.setFromMatrixColumn(e, 1).length(), a = 1 / ld.setFromMatrixColumn(e, 2).length();
    return t[0] = i[0] * n, t[1] = i[1] * n, t[2] = i[2] * n, t[3] = 0, t[4] = i[4] * r, t[5] = i[5] * r, t[6] = i[6] * r, t[7] = 0, t[8] = i[8] * a, t[9] = i[9] * a, t[10] = i[10] * a, t[11] = 0, t[12] = 0, t[13] = 0, t[14] = 0, t[15] = 1, this;
  }
  makeRotationFromEuler(e) {
    const t = this.elements, i = e.x, n = e.y, r = e.z, a = Math.cos(i), o = Math.sin(i), l = Math.cos(n), c = Math.sin(n), h = Math.cos(r), u = Math.sin(r);
    if (e.order === "XYZ") {
      const d = a * h, f = a * u, p = o * h, v = o * u;
      t[0] = l * h, t[4] = -l * u, t[8] = c, t[1] = f + p * c, t[5] = d - v * c, t[9] = -o * l, t[2] = v - d * c, t[6] = p + f * c, t[10] = a * l;
    } else if (e.order === "YXZ") {
      const d = l * h, f = l * u, p = c * h, v = c * u;
      t[0] = d + v * o, t[4] = p * o - f, t[8] = a * c, t[1] = a * u, t[5] = a * h, t[9] = -o, t[2] = f * o - p, t[6] = v + d * o, t[10] = a * l;
    } else if (e.order === "ZXY") {
      const d = l * h, f = l * u, p = c * h, v = c * u;
      t[0] = d - v * o, t[4] = -a * u, t[8] = p + f * o, t[1] = f + p * o, t[5] = a * h, t[9] = v - d * o, t[2] = -a * c, t[6] = o, t[10] = a * l;
    } else if (e.order === "ZYX") {
      const d = a * h, f = a * u, p = o * h, v = o * u;
      t[0] = l * h, t[4] = p * c - f, t[8] = d * c + v, t[1] = l * u, t[5] = v * c + d, t[9] = f * c - p, t[2] = -c, t[6] = o * l, t[10] = a * l;
    } else if (e.order === "YZX") {
      const d = a * l, f = a * c, p = o * l, v = o * c;
      t[0] = l * h, t[4] = v - d * u, t[8] = p * u + f, t[1] = u, t[5] = a * h, t[9] = -o * h, t[2] = -c * h, t[6] = f * u + p, t[10] = d - v * u;
    } else if (e.order === "XZY") {
      const d = a * l, f = a * c, p = o * l, v = o * c;
      t[0] = l * h, t[4] = -u, t[8] = c * h, t[1] = d * u + v, t[5] = a * h, t[9] = f * u - p, t[2] = p * u - f, t[6] = o * h, t[10] = v * u + d;
    }
    return t[3] = 0, t[7] = 0, t[11] = 0, t[12] = 0, t[13] = 0, t[14] = 0, t[15] = 1, this;
  }
  makeRotationFromQuaternion(e) {
    return this.compose(vk, e, yk);
  }
  lookAt(e, t, i) {
    const n = this.elements;
    return Hr.subVectors(e, t), Hr.lengthSq() === 0 && (Hr.z = 1), Hr.normalize(), Fl.crossVectors(i, Hr), Fl.lengthSq() === 0 && (Math.abs(i.z) === 1 ? Hr.x += 1e-4 : Hr.z += 1e-4, Hr.normalize(), Fl.crossVectors(i, Hr)), Fl.normalize(), ev.crossVectors(Hr, Fl), n[0] = Fl.x, n[4] = ev.x, n[8] = Hr.x, n[1] = Fl.y, n[5] = ev.y, n[9] = Hr.y, n[2] = Fl.z, n[6] = ev.z, n[10] = Hr.z, this;
  }
  multiply(e) {
    return this.multiplyMatrices(this, e);
  }
  premultiply(e) {
    return this.multiplyMatrices(e, this);
  }
  multiplyMatrices(e, t) {
    const i = e.elements, n = t.elements, r = this.elements, a = i[0], o = i[4], l = i[8], c = i[12], h = i[1], u = i[5], d = i[9], f = i[13], p = i[2], v = i[6], g = i[10], m = i[14], x = i[3], y = i[7], A = i[11], b = i[15], M = n[0], w = n[4], C = n[8], T = n[12], E = n[1], P = n[5], L = n[9], I = n[13], N = n[2], H = n[6], U = n[10], W = n[14], X = n[3], K = n[7], te = n[11], re = n[15];
    return r[0] = a * M + o * E + l * N + c * X, r[4] = a * w + o * P + l * H + c * K, r[8] = a * C + o * L + l * U + c * te, r[12] = a * T + o * I + l * W + c * re, r[1] = h * M + u * E + d * N + f * X, r[5] = h * w + u * P + d * H + f * K, r[9] = h * C + u * L + d * U + f * te, r[13] = h * T + u * I + d * W + f * re, r[2] = p * M + v * E + g * N + m * X, r[6] = p * w + v * P + g * H + m * K, r[10] = p * C + v * L + g * U + m * te, r[14] = p * T + v * I + g * W + m * re, r[3] = x * M + y * E + A * N + b * X, r[7] = x * w + y * P + A * H + b * K, r[11] = x * C + y * L + A * U + b * te, r[15] = x * T + y * I + A * W + b * re, this;
  }
  multiplyScalar(e) {
    const t = this.elements;
    return t[0] *= e, t[4] *= e, t[8] *= e, t[12] *= e, t[1] *= e, t[5] *= e, t[9] *= e, t[13] *= e, t[2] *= e, t[6] *= e, t[10] *= e, t[14] *= e, t[3] *= e, t[7] *= e, t[11] *= e, t[15] *= e, this;
  }
  determinant() {
    const e = this.elements, t = e[0], i = e[4], n = e[8], r = e[12], a = e[1], o = e[5], l = e[9], c = e[13], h = e[2], u = e[6], d = e[10], f = e[14], p = e[3], v = e[7], g = e[11], m = e[15];
    return p * (+r * l * u - n * c * u - r * o * d + i * c * d + n * o * f - i * l * f) + v * (+t * l * f - t * c * d + r * a * d - n * a * f + n * c * h - r * l * h) + g * (+t * c * u - t * o * f - r * a * u + i * a * f + r * o * h - i * c * h) + m * (-n * o * h - t * l * u + t * o * d + n * a * u - i * a * d + i * l * h);
  }
  transpose() {
    const e = this.elements;
    let t;
    return t = e[1], e[1] = e[4], e[4] = t, t = e[2], e[2] = e[8], e[8] = t, t = e[6], e[6] = e[9], e[9] = t, t = e[3], e[3] = e[12], e[12] = t, t = e[7], e[7] = e[13], e[13] = t, t = e[11], e[11] = e[14], e[14] = t, this;
  }
  setPosition(e, t, i) {
    const n = this.elements;
    return e.isVector3 ? (n[12] = e.x, n[13] = e.y, n[14] = e.z) : (n[12] = e, n[13] = t, n[14] = i), this;
  }
  invert() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8], u = e[9], d = e[10], f = e[11], p = e[12], v = e[13], g = e[14], m = e[15], x = u * g * c - v * d * c + v * l * f - o * g * f - u * l * m + o * d * m, y = p * d * c - h * g * c - p * l * f + a * g * f + h * l * m - a * d * m, A = h * v * c - p * u * c + p * o * f - a * v * f - h * o * m + a * u * m, b = p * u * l - h * v * l - p * o * d + a * v * d + h * o * g - a * u * g, M = t * x + i * y + n * A + r * b;
    if (M === 0)
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    const w = 1 / M;
    return e[0] = x * w, e[1] = (v * d * r - u * g * r - v * n * f + i * g * f + u * n * m - i * d * m) * w, e[2] = (o * g * r - v * l * r + v * n * c - i * g * c - o * n * m + i * l * m) * w, e[3] = (u * l * r - o * d * r - u * n * c + i * d * c + o * n * f - i * l * f) * w, e[4] = y * w, e[5] = (h * g * r - p * d * r + p * n * f - t * g * f - h * n * m + t * d * m) * w, e[6] = (p * l * r - a * g * r - p * n * c + t * g * c + a * n * m - t * l * m) * w, e[7] = (a * d * r - h * l * r + h * n * c - t * d * c - a * n * f + t * l * f) * w, e[8] = A * w, e[9] = (p * u * r - h * v * r - p * i * f + t * v * f + h * i * m - t * u * m) * w, e[10] = (a * v * r - p * o * r + p * i * c - t * v * c - a * i * m + t * o * m) * w, e[11] = (h * o * r - a * u * r - h * i * c + t * u * c + a * i * f - t * o * f) * w, e[12] = b * w, e[13] = (h * v * n - p * u * n + p * i * d - t * v * d - h * i * g + t * u * g) * w, e[14] = (p * o * n - a * v * n - p * i * l + t * v * l + a * i * g - t * o * g) * w, e[15] = (a * u * n - h * o * n + h * i * l - t * u * l - a * i * d + t * o * d) * w, this;
  }
  scale(e) {
    const t = this.elements, i = e.x, n = e.y, r = e.z;
    return t[0] *= i, t[4] *= n, t[8] *= r, t[1] *= i, t[5] *= n, t[9] *= r, t[2] *= i, t[6] *= n, t[10] *= r, t[3] *= i, t[7] *= n, t[11] *= r, this;
  }
  getMaxScaleOnAxis() {
    const e = this.elements, t = e[0] * e[0] + e[1] * e[1] + e[2] * e[2], i = e[4] * e[4] + e[5] * e[5] + e[6] * e[6], n = e[8] * e[8] + e[9] * e[9] + e[10] * e[10];
    return Math.sqrt(Math.max(t, i, n));
  }
  makeTranslation(e, t, i) {
    return e.isVector3 ? this.set(
      1,
      0,
      0,
      e.x,
      0,
      1,
      0,
      e.y,
      0,
      0,
      1,
      e.z,
      0,
      0,
      0,
      1
    ) : this.set(
      1,
      0,
      0,
      e,
      0,
      1,
      0,
      t,
      0,
      0,
      1,
      i,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationX(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      1,
      0,
      0,
      0,
      0,
      t,
      -i,
      0,
      0,
      i,
      t,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationY(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      0,
      i,
      0,
      0,
      1,
      0,
      0,
      -i,
      0,
      t,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationZ(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      -i,
      0,
      0,
      i,
      t,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationAxis(e, t) {
    const i = Math.cos(t), n = Math.sin(t), r = 1 - i, a = e.x, o = e.y, l = e.z, c = r * a, h = r * o;
    return this.set(
      c * a + i,
      c * o - n * l,
      c * l + n * o,
      0,
      c * o + n * l,
      h * o + i,
      h * l - n * a,
      0,
      c * l - n * o,
      h * l + n * a,
      r * l * l + i,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeScale(e, t, i) {
    return this.set(
      e,
      0,
      0,
      0,
      0,
      t,
      0,
      0,
      0,
      0,
      i,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeShear(e, t, i, n, r, a) {
    return this.set(
      1,
      i,
      r,
      0,
      e,
      1,
      a,
      0,
      t,
      n,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  compose(e, t, i) {
    const n = this.elements, r = t._x, a = t._y, o = t._z, l = t._w, c = r + r, h = a + a, u = o + o, d = r * c, f = r * h, p = r * u, v = a * h, g = a * u, m = o * u, x = l * c, y = l * h, A = l * u, b = i.x, M = i.y, w = i.z;
    return n[0] = (1 - (v + m)) * b, n[1] = (f + A) * b, n[2] = (p - y) * b, n[3] = 0, n[4] = (f - A) * M, n[5] = (1 - (d + m)) * M, n[6] = (g + x) * M, n[7] = 0, n[8] = (p + y) * w, n[9] = (g - x) * w, n[10] = (1 - (d + v)) * w, n[11] = 0, n[12] = e.x, n[13] = e.y, n[14] = e.z, n[15] = 1, this;
  }
  decompose(e, t, i) {
    const n = this.elements;
    let r = ld.set(n[0], n[1], n[2]).length();
    const a = ld.set(n[4], n[5], n[6]).length(), o = ld.set(n[8], n[9], n[10]).length();
    this.determinant() < 0 && (r = -r), e.x = n[12], e.y = n[13], e.z = n[14], Na.copy(this);
    const c = 1 / r, h = 1 / a, u = 1 / o;
    return Na.elements[0] *= c, Na.elements[1] *= c, Na.elements[2] *= c, Na.elements[4] *= h, Na.elements[5] *= h, Na.elements[6] *= h, Na.elements[8] *= u, Na.elements[9] *= u, Na.elements[10] *= u, t.setFromRotationMatrix(Na), i.x = r, i.y = a, i.z = o, this;
  }
  makePerspective(e, t, i, n, r, a, o = Vo) {
    const l = this.elements, c = 2 * r / (t - e), h = 2 * r / (i - n), u = (t + e) / (t - e), d = (i + n) / (i - n);
    let f, p;
    if (o === Vo)
      f = -(a + r) / (a - r), p = -2 * a * r / (a - r);
    else if (o === Q1)
      f = -a / (a - r), p = -a * r / (a - r);
    else
      throw new Error("THREE.Matrix4.makePerspective(): Invalid coordinate system: " + o);
    return l[0] = c, l[4] = 0, l[8] = u, l[12] = 0, l[1] = 0, l[5] = h, l[9] = d, l[13] = 0, l[2] = 0, l[6] = 0, l[10] = f, l[14] = p, l[3] = 0, l[7] = 0, l[11] = -1, l[15] = 0, this;
  }
  makeOrthographic(e, t, i, n, r, a, o = Vo) {
    const l = this.elements, c = 1 / (t - e), h = 1 / (i - n), u = 1 / (a - r), d = (t + e) * c, f = (i + n) * h;
    let p, v;
    if (o === Vo)
      p = (a + r) * u, v = -2 * u;
    else if (o === Q1)
      p = r * u, v = -1 * u;
    else
      throw new Error("THREE.Matrix4.makeOrthographic(): Invalid coordinate system: " + o);
    return l[0] = 2 * c, l[4] = 0, l[8] = 0, l[12] = -d, l[1] = 0, l[5] = 2 * h, l[9] = 0, l[13] = -f, l[2] = 0, l[6] = 0, l[10] = v, l[14] = -p, l[3] = 0, l[7] = 0, l[11] = 0, l[15] = 1, this;
  }
  equals(e) {
    const t = this.elements, i = e.elements;
    for (let n = 0; n < 16; n++)
      if (t[n] !== i[n])
        return !1;
    return !0;
  }
  fromArray(e, t = 0) {
    for (let i = 0; i < 16; i++)
      this.elements[i] = e[i + t];
    return this;
  }
  toArray(e = [], t = 0) {
    const i = this.elements;
    return e[t] = i[0], e[t + 1] = i[1], e[t + 2] = i[2], e[t + 3] = i[3], e[t + 4] = i[4], e[t + 5] = i[5], e[t + 6] = i[6], e[t + 7] = i[7], e[t + 8] = i[8], e[t + 9] = i[9], e[t + 10] = i[10], e[t + 11] = i[11], e[t + 12] = i[12], e[t + 13] = i[13], e[t + 14] = i[14], e[t + 15] = i[15], e;
  }
};
const ld = /* @__PURE__ */ new _(), Na = /* @__PURE__ */ new et(), vk = /* @__PURE__ */ new _(0, 0, 0), yk = /* @__PURE__ */ new _(1, 1, 1), Fl = /* @__PURE__ */ new _(), ev = /* @__PURE__ */ new _(), Hr = /* @__PURE__ */ new _(), Yw = /* @__PURE__ */ new et(), Zw = /* @__PURE__ */ new St();
let Mi = class XL {
  constructor(e = 0, t = 0, i = 0, n = XL.DEFAULT_ORDER) {
    this.isEuler = !0, this._x = e, this._y = t, this._z = i, this._order = n;
  }
  get x() {
    return this._x;
  }
  set x(e) {
    this._x = e, this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(e) {
    this._y = e, this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(e) {
    this._z = e, this._onChangeCallback();
  }
  get order() {
    return this._order;
  }
  set order(e) {
    this._order = e, this._onChangeCallback();
  }
  set(e, t, i, n = this._order) {
    return this._x = e, this._y = t, this._z = i, this._order = n, this._onChangeCallback(), this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._order);
  }
  copy(e) {
    return this._x = e._x, this._y = e._y, this._z = e._z, this._order = e._order, this._onChangeCallback(), this;
  }
  setFromRotationMatrix(e, t = this._order, i = !0) {
    const n = e.elements, r = n[0], a = n[4], o = n[8], l = n[1], c = n[5], h = n[9], u = n[2], d = n[6], f = n[10];
    switch (t) {
      case "XYZ":
        this._y = Math.asin(Wn(o, -1, 1)), Math.abs(o) < 0.9999999 ? (this._x = Math.atan2(-h, f), this._z = Math.atan2(-a, r)) : (this._x = Math.atan2(d, c), this._z = 0);
        break;
      case "YXZ":
        this._x = Math.asin(-Wn(h, -1, 1)), Math.abs(h) < 0.9999999 ? (this._y = Math.atan2(o, f), this._z = Math.atan2(l, c)) : (this._y = Math.atan2(-u, r), this._z = 0);
        break;
      case "ZXY":
        this._x = Math.asin(Wn(d, -1, 1)), Math.abs(d) < 0.9999999 ? (this._y = Math.atan2(-u, f), this._z = Math.atan2(-a, c)) : (this._y = 0, this._z = Math.atan2(l, r));
        break;
      case "ZYX":
        this._y = Math.asin(-Wn(u, -1, 1)), Math.abs(u) < 0.9999999 ? (this._x = Math.atan2(d, f), this._z = Math.atan2(l, r)) : (this._x = 0, this._z = Math.atan2(-a, c));
        break;
      case "YZX":
        this._z = Math.asin(Wn(l, -1, 1)), Math.abs(l) < 0.9999999 ? (this._x = Math.atan2(-h, c), this._y = Math.atan2(-u, r)) : (this._x = 0, this._y = Math.atan2(o, f));
        break;
      case "XZY":
        this._z = Math.asin(-Wn(a, -1, 1)), Math.abs(a) < 0.9999999 ? (this._x = Math.atan2(d, c), this._y = Math.atan2(o, r)) : (this._x = Math.atan2(-h, f), this._y = 0);
        break;
      default:
        console.warn("THREE.Euler: .setFromRotationMatrix() encountered an unknown order: " + t);
    }
    return this._order = t, i === !0 && this._onChangeCallback(), this;
  }
  setFromQuaternion(e, t, i) {
    return Yw.makeRotationFromQuaternion(e), this.setFromRotationMatrix(Yw, t, i);
  }
  setFromVector3(e, t = this._order) {
    return this.set(e.x, e.y, e.z, t);
  }
  reorder(e) {
    return Zw.setFromEuler(this), this.setFromQuaternion(Zw, e);
  }
  equals(e) {
    return e._x === this._x && e._y === this._y && e._z === this._z && e._order === this._order;
  }
  fromArray(e) {
    return this._x = e[0], this._y = e[1], this._z = e[2], e[3] !== void 0 && (this._order = e[3]), this._onChangeCallback(), this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this._x, e[t + 1] = this._y, e[t + 2] = this._z, e[t + 3] = this._order, e;
  }
  _onChange(e) {
    return this._onChangeCallback = e, this;
  }
  _onChangeCallback() {
  }
  *[Symbol.iterator]() {
    yield this._x, yield this._y, yield this._z, yield this._order;
  }
};
Mi.DEFAULT_ORDER = "XYZ";
let e0 = class {
  constructor() {
    this.mask = 1;
  }
  set(e) {
    this.mask = (1 << e | 0) >>> 0;
  }
  enable(e) {
    this.mask |= 1 << e | 0;
  }
  enableAll() {
    this.mask = -1;
  }
  toggle(e) {
    this.mask ^= 1 << e | 0;
  }
  disable(e) {
    this.mask &= ~(1 << e | 0);
  }
  disableAll() {
    this.mask = 0;
  }
  test(e) {
    return (this.mask & e.mask) !== 0;
  }
  isEnabled(e) {
    return (this.mask & (1 << e | 0)) !== 0;
  }
}, xk = 0;
const Kw = /* @__PURE__ */ new _(), cd = /* @__PURE__ */ new St(), E0 = /* @__PURE__ */ new et(), tv = /* @__PURE__ */ new _(), cm = /* @__PURE__ */ new _(), Ak = /* @__PURE__ */ new _(), bk = /* @__PURE__ */ new St(), Jw = /* @__PURE__ */ new _(1, 0, 0), Qw = /* @__PURE__ */ new _(0, 1, 0), $w = /* @__PURE__ */ new _(0, 0, 1), eE = { type: "added" }, Mk = { type: "removed" }, hd = { type: "childadded", child: null }, R8 = { type: "childremoved", child: null };
let mt = class Ey extends ra {
  constructor() {
    super(), this.isObject3D = !0, Object.defineProperty(this, "id", { value: xk++ }), this.uuid = ea(), this.name = "", this.type = "Object3D", this.parent = null, this.children = [], this.up = Ey.DEFAULT_UP.clone();
    const e = new _(), t = new Mi(), i = new St(), n = new _(1, 1, 1);
    function r() {
      i.setFromEuler(t, !1);
    }
    function a() {
      t.setFromQuaternion(i, void 0, !1);
    }
    t._onChange(r), i._onChange(a), Object.defineProperties(this, {
      position: {
        configurable: !0,
        enumerable: !0,
        value: e
      },
      rotation: {
        configurable: !0,
        enumerable: !0,
        value: t
      },
      quaternion: {
        configurable: !0,
        enumerable: !0,
        value: i
      },
      scale: {
        configurable: !0,
        enumerable: !0,
        value: n
      },
      modelViewMatrix: {
        value: new et()
      },
      normalMatrix: {
        value: new Gt()
      }
    }), this.matrix = new et(), this.matrixWorld = new et(), this.matrixAutoUpdate = Ey.DEFAULT_MATRIX_AUTO_UPDATE, this.matrixWorldAutoUpdate = Ey.DEFAULT_MATRIX_WORLD_AUTO_UPDATE, this.matrixWorldNeedsUpdate = !1, this.layers = new e0(), this.visible = !0, this.castShadow = !1, this.receiveShadow = !1, this.frustumCulled = !0, this.renderOrder = 0, this.animations = [], this.userData = {};
  }
  onBeforeShadow() {
  }
  onAfterShadow() {
  }
  onBeforeRender() {
  }
  onAfterRender() {
  }
  applyMatrix4(e) {
    this.matrixAutoUpdate && this.updateMatrix(), this.matrix.premultiply(e), this.matrix.decompose(this.position, this.quaternion, this.scale);
  }
  applyQuaternion(e) {
    return this.quaternion.premultiply(e), this;
  }
  setRotationFromAxisAngle(e, t) {
    this.quaternion.setFromAxisAngle(e, t);
  }
  setRotationFromEuler(e) {
    this.quaternion.setFromEuler(e, !0);
  }
  setRotationFromMatrix(e) {
    this.quaternion.setFromRotationMatrix(e);
  }
  setRotationFromQuaternion(e) {
    this.quaternion.copy(e);
  }
  rotateOnAxis(e, t) {
    return cd.setFromAxisAngle(e, t), this.quaternion.multiply(cd), this;
  }
  rotateOnWorldAxis(e, t) {
    return cd.setFromAxisAngle(e, t), this.quaternion.premultiply(cd), this;
  }
  rotateX(e) {
    return this.rotateOnAxis(Jw, e);
  }
  rotateY(e) {
    return this.rotateOnAxis(Qw, e);
  }
  rotateZ(e) {
    return this.rotateOnAxis($w, e);
  }
  translateOnAxis(e, t) {
    return Kw.copy(e).applyQuaternion(this.quaternion), this.position.add(Kw.multiplyScalar(t)), this;
  }
  translateX(e) {
    return this.translateOnAxis(Jw, e);
  }
  translateY(e) {
    return this.translateOnAxis(Qw, e);
  }
  translateZ(e) {
    return this.translateOnAxis($w, e);
  }
  localToWorld(e) {
    return this.updateWorldMatrix(!0, !1), e.applyMatrix4(this.matrixWorld);
  }
  worldToLocal(e) {
    return this.updateWorldMatrix(!0, !1), e.applyMatrix4(E0.copy(this.matrixWorld).invert());
  }
  lookAt(e, t, i) {
    e.isVector3 ? tv.copy(e) : tv.set(e, t, i);
    const n = this.parent;
    this.updateWorldMatrix(!0, !1), cm.setFromMatrixPosition(this.matrixWorld), this.isCamera || this.isLight ? E0.lookAt(cm, tv, this.up) : E0.lookAt(tv, cm, this.up), this.quaternion.setFromRotationMatrix(E0), n && (E0.extractRotation(n.matrixWorld), cd.setFromRotationMatrix(E0), this.quaternion.premultiply(cd.invert()));
  }
  add(e) {
    if (arguments.length > 1) {
      for (let t = 0; t < arguments.length; t++)
        this.add(arguments[t]);
      return this;
    }
    return e === this ? (console.error("THREE.Object3D.add: object can't be added as a child of itself.", e), this) : (e && e.isObject3D ? (e.removeFromParent(), e.parent = this, this.children.push(e), e.dispatchEvent(eE), hd.child = e, this.dispatchEvent(hd), hd.child = null) : console.error("THREE.Object3D.add: object not an instance of THREE.Object3D.", e), this);
  }
  remove(e) {
    if (arguments.length > 1) {
      for (let i = 0; i < arguments.length; i++)
        this.remove(arguments[i]);
      return this;
    }
    const t = this.children.indexOf(e);
    return t !== -1 && (e.parent = null, this.children.splice(t, 1), e.dispatchEvent(Mk), R8.child = e, this.dispatchEvent(R8), R8.child = null), this;
  }
  removeFromParent() {
    const e = this.parent;
    return e !== null && e.remove(this), this;
  }
  clear() {
    return this.remove(...this.children);
  }
  attach(e) {
    return this.updateWorldMatrix(!0, !1), E0.copy(this.matrixWorld).invert(), e.parent !== null && (e.parent.updateWorldMatrix(!0, !1), E0.multiply(e.parent.matrixWorld)), e.applyMatrix4(E0), e.removeFromParent(), e.parent = this, this.children.push(e), e.updateWorldMatrix(!1, !0), e.dispatchEvent(eE), hd.child = e, this.dispatchEvent(hd), hd.child = null, this;
  }
  getObjectById(e) {
    return this.getObjectByProperty("id", e);
  }
  getObjectByName(e) {
    return this.getObjectByProperty("name", e);
  }
  getObjectByProperty(e, t) {
    if (this[e] === t)
      return this;
    for (let i = 0, n = this.children.length; i < n; i++) {
      const a = this.children[i].getObjectByProperty(e, t);
      if (a !== void 0)
        return a;
    }
  }
  getObjectsByProperty(e, t, i = []) {
    this[e] === t && i.push(this);
    const n = this.children;
    for (let r = 0, a = n.length; r < a; r++)
      n[r].getObjectsByProperty(e, t, i);
    return i;
  }
  getWorldPosition(e) {
    return this.updateWorldMatrix(!0, !1), e.setFromMatrixPosition(this.matrixWorld);
  }
  getWorldQuaternion(e) {
    return this.updateWorldMatrix(!0, !1), this.matrixWorld.decompose(cm, e, Ak), e;
  }
  getWorldScale(e) {
    return this.updateWorldMatrix(!0, !1), this.matrixWorld.decompose(cm, bk, e), e;
  }
  getWorldDirection(e) {
    this.updateWorldMatrix(!0, !1);
    const t = this.matrixWorld.elements;
    return e.set(t[8], t[9], t[10]).normalize();
  }
  raycast() {
  }
  traverse(e) {
    e(this);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].traverse(e);
  }
  traverseVisible(e) {
    if (this.visible === !1)
      return;
    e(this);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].traverseVisible(e);
  }
  traverseAncestors(e) {
    const t = this.parent;
    t !== null && (e(t), t.traverseAncestors(e));
  }
  updateMatrix() {
    this.matrix.compose(this.position, this.quaternion, this.scale), this.matrixWorldNeedsUpdate = !0;
  }
  updateMatrixWorld(e) {
    this.matrixAutoUpdate && this.updateMatrix(), (this.matrixWorldNeedsUpdate || e) && (this.matrixWorldAutoUpdate === !0 && (this.parent === null ? this.matrixWorld.copy(this.matrix) : this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix)), this.matrixWorldNeedsUpdate = !1, e = !0);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].updateMatrixWorld(e);
  }
  updateWorldMatrix(e, t) {
    const i = this.parent;
    if (e === !0 && i !== null && i.updateWorldMatrix(!0, !1), this.matrixAutoUpdate && this.updateMatrix(), this.matrixWorldAutoUpdate === !0 && (this.parent === null ? this.matrixWorld.copy(this.matrix) : this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix)), t === !0) {
      const n = this.children;
      for (let r = 0, a = n.length; r < a; r++)
        n[r].updateWorldMatrix(!1, !0);
    }
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string", i = {};
    t && (e = {
      geometries: {},
      materials: {},
      textures: {},
      images: {},
      shapes: {},
      skeletons: {},
      animations: {},
      nodes: {}
    }, i.metadata = {
      version: 4.6,
      type: "Object",
      generator: "Object3D.toJSON"
    });
    const n = {};
    n.uuid = this.uuid, n.type = this.type, this.name !== "" && (n.name = this.name), this.castShadow === !0 && (n.castShadow = !0), this.receiveShadow === !0 && (n.receiveShadow = !0), this.visible === !1 && (n.visible = !1), this.frustumCulled === !1 && (n.frustumCulled = !1), this.renderOrder !== 0 && (n.renderOrder = this.renderOrder), Object.keys(this.userData).length > 0 && (n.userData = this.userData), n.layers = this.layers.mask, n.matrix = this.matrix.toArray(), n.up = this.up.toArray(), this.matrixAutoUpdate === !1 && (n.matrixAutoUpdate = !1), this.isInstancedMesh && (n.type = "InstancedMesh", n.count = this.count, n.instanceMatrix = this.instanceMatrix.toJSON(), this.instanceColor !== null && (n.instanceColor = this.instanceColor.toJSON())), this.isBatchedMesh && (n.type = "BatchedMesh", n.perObjectFrustumCulled = this.perObjectFrustumCulled, n.sortObjects = this.sortObjects, n.drawRanges = this._drawRanges, n.reservedRanges = this._reservedRanges, n.visibility = this._visibility, n.active = this._active, n.bounds = this._bounds.map((o) => ({
      boxInitialized: o.boxInitialized,
      boxMin: o.box.min.toArray(),
      boxMax: o.box.max.toArray(),
      sphereInitialized: o.sphereInitialized,
      sphereRadius: o.sphere.radius,
      sphereCenter: o.sphere.center.toArray()
    })), n.maxInstanceCount = this._maxInstanceCount, n.maxVertexCount = this._maxVertexCount, n.maxIndexCount = this._maxIndexCount, n.geometryInitialized = this._geometryInitialized, n.geometryCount = this._geometryCount, n.matricesTexture = this._matricesTexture.toJSON(e), this._colorsTexture !== null && (n.colorsTexture = this._colorsTexture.toJSON(e)), this.boundingSphere !== null && (n.boundingSphere = {
      center: n.boundingSphere.center.toArray(),
      radius: n.boundingSphere.radius
    }), this.boundingBox !== null && (n.boundingBox = {
      min: n.boundingBox.min.toArray(),
      max: n.boundingBox.max.toArray()
    }));
    function r(o, l) {
      return o[l.uuid] === void 0 && (o[l.uuid] = l.toJSON(e)), l.uuid;
    }
    if (this.isScene)
      this.background && (this.background.isColor ? n.background = this.background.toJSON() : this.background.isTexture && (n.background = this.background.toJSON(e).uuid)), this.environment && this.environment.isTexture && this.environment.isRenderTargetTexture !== !0 && (n.environment = this.environment.toJSON(e).uuid);
    else if (this.isMesh || this.isLine || this.isPoints) {
      n.geometry = r(e.geometries, this.geometry);
      const o = this.geometry.parameters;
      if (o !== void 0 && o.shapes !== void 0) {
        const l = o.shapes;
        if (Array.isArray(l))
          for (let c = 0, h = l.length; c < h; c++) {
            const u = l[c];
            r(e.shapes, u);
          }
        else
          r(e.shapes, l);
      }
    }
    if (this.isSkinnedMesh && (n.bindMode = this.bindMode, n.bindMatrix = this.bindMatrix.toArray(), this.skeleton !== void 0 && (r(e.skeletons, this.skeleton), n.skeleton = this.skeleton.uuid)), this.material !== void 0)
      if (Array.isArray(this.material)) {
        const o = [];
        for (let l = 0, c = this.material.length; l < c; l++)
          o.push(r(e.materials, this.material[l]));
        n.material = o;
      } else
        n.material = r(e.materials, this.material);
    if (this.children.length > 0) {
      n.children = [];
      for (let o = 0; o < this.children.length; o++)
        n.children.push(this.children[o].toJSON(e).object);
    }
    if (this.animations.length > 0) {
      n.animations = [];
      for (let o = 0; o < this.animations.length; o++) {
        const l = this.animations[o];
        n.animations.push(r(e.animations, l));
      }
    }
    if (t) {
      const o = a(e.geometries), l = a(e.materials), c = a(e.textures), h = a(e.images), u = a(e.shapes), d = a(e.skeletons), f = a(e.animations), p = a(e.nodes);
      o.length > 0 && (i.geometries = o), l.length > 0 && (i.materials = l), c.length > 0 && (i.textures = c), h.length > 0 && (i.images = h), u.length > 0 && (i.shapes = u), d.length > 0 && (i.skeletons = d), f.length > 0 && (i.animations = f), p.length > 0 && (i.nodes = p);
    }
    return i.object = n, i;
    function a(o) {
      const l = [];
      for (const c in o) {
        const h = o[c];
        delete h.metadata, l.push(h);
      }
      return l;
    }
  }
  clone(e) {
    return new this.constructor().copy(this, e);
  }
  copy(e, t = !0) {
    if (this.name = e.name, this.up.copy(e.up), this.position.copy(e.position), this.rotation.order = e.rotation.order, this.quaternion.copy(e.quaternion), this.scale.copy(e.scale), this.matrix.copy(e.matrix), this.matrixWorld.copy(e.matrixWorld), this.matrixAutoUpdate = e.matrixAutoUpdate, this.matrixWorldAutoUpdate = e.matrixWorldAutoUpdate, this.matrixWorldNeedsUpdate = e.matrixWorldNeedsUpdate, this.layers.mask = e.layers.mask, this.visible = e.visible, this.castShadow = e.castShadow, this.receiveShadow = e.receiveShadow, this.frustumCulled = e.frustumCulled, this.renderOrder = e.renderOrder, this.animations = e.animations.slice(), this.userData = JSON.parse(JSON.stringify(e.userData)), t === !0)
      for (let i = 0; i < e.children.length; i++) {
        const n = e.children[i];
        this.add(n.clone());
      }
    return this;
  }
};
mt.DEFAULT_UP = /* @__PURE__ */ new _(0, 1, 0);
mt.DEFAULT_MATRIX_AUTO_UPDATE = !0;
mt.DEFAULT_MATRIX_WORLD_AUTO_UPDATE = !0;
const Ba = /* @__PURE__ */ new _(), T0 = /* @__PURE__ */ new _(), D8 = /* @__PURE__ */ new _(), C0 = /* @__PURE__ */ new _(), ud = /* @__PURE__ */ new _(), dd = /* @__PURE__ */ new _(), tE = /* @__PURE__ */ new _(), L8 = /* @__PURE__ */ new _(), I8 = /* @__PURE__ */ new _(), O8 = /* @__PURE__ */ new _();
let Wo = class ff {
  constructor(e = new _(), t = new _(), i = new _()) {
    this.a = e, this.b = t, this.c = i;
  }
  static getNormal(e, t, i, n) {
    n.subVectors(i, t), Ba.subVectors(e, t), n.cross(Ba);
    const r = n.lengthSq();
    return r > 0 ? n.multiplyScalar(1 / Math.sqrt(r)) : n.set(0, 0, 0);
  }
  // static/instance method to calculate barycentric coordinates
  // based on: http://www.blackpawn.com/texts/pointinpoly/default.html
  static getBarycoord(e, t, i, n, r) {
    Ba.subVectors(n, t), T0.subVectors(i, t), D8.subVectors(e, t);
    const a = Ba.dot(Ba), o = Ba.dot(T0), l = Ba.dot(D8), c = T0.dot(T0), h = T0.dot(D8), u = a * c - o * o;
    if (u === 0)
      return r.set(0, 0, 0), null;
    const d = 1 / u, f = (c * l - o * h) * d, p = (a * h - o * l) * d;
    return r.set(1 - f - p, p, f);
  }
  static containsPoint(e, t, i, n) {
    return this.getBarycoord(e, t, i, n, C0) === null ? !1 : C0.x >= 0 && C0.y >= 0 && C0.x + C0.y <= 1;
  }
  static getInterpolation(e, t, i, n, r, a, o, l) {
    return this.getBarycoord(e, t, i, n, C0) === null ? (l.x = 0, l.y = 0, "z" in l && (l.z = 0), "w" in l && (l.w = 0), null) : (l.setScalar(0), l.addScaledVector(r, C0.x), l.addScaledVector(a, C0.y), l.addScaledVector(o, C0.z), l);
  }
  static isFrontFacing(e, t, i, n) {
    return Ba.subVectors(i, t), T0.subVectors(e, t), Ba.cross(T0).dot(n) < 0;
  }
  set(e, t, i) {
    return this.a.copy(e), this.b.copy(t), this.c.copy(i), this;
  }
  setFromPointsAndIndices(e, t, i, n) {
    return this.a.copy(e[t]), this.b.copy(e[i]), this.c.copy(e[n]), this;
  }
  setFromAttributeAndIndices(e, t, i, n) {
    return this.a.fromBufferAttribute(e, t), this.b.fromBufferAttribute(e, i), this.c.fromBufferAttribute(e, n), this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.a.copy(e.a), this.b.copy(e.b), this.c.copy(e.c), this;
  }
  getArea() {
    return Ba.subVectors(this.c, this.b), T0.subVectors(this.a, this.b), Ba.cross(T0).length() * 0.5;
  }
  getMidpoint(e) {
    return e.addVectors(this.a, this.b).add(this.c).multiplyScalar(1 / 3);
  }
  getNormal(e) {
    return ff.getNormal(this.a, this.b, this.c, e);
  }
  getPlane(e) {
    return e.setFromCoplanarPoints(this.a, this.b, this.c);
  }
  getBarycoord(e, t) {
    return ff.getBarycoord(e, this.a, this.b, this.c, t);
  }
  getInterpolation(e, t, i, n, r) {
    return ff.getInterpolation(e, this.a, this.b, this.c, t, i, n, r);
  }
  containsPoint(e) {
    return ff.containsPoint(e, this.a, this.b, this.c);
  }
  isFrontFacing(e) {
    return ff.isFrontFacing(this.a, this.b, this.c, e);
  }
  intersectsBox(e) {
    return e.intersectsTriangle(this);
  }
  closestPointToPoint(e, t) {
    const i = this.a, n = this.b, r = this.c;
    let a, o;
    ud.subVectors(n, i), dd.subVectors(r, i), L8.subVectors(e, i);
    const l = ud.dot(L8), c = dd.dot(L8);
    if (l <= 0 && c <= 0)
      return t.copy(i);
    I8.subVectors(e, n);
    const h = ud.dot(I8), u = dd.dot(I8);
    if (h >= 0 && u <= h)
      return t.copy(n);
    const d = l * u - h * c;
    if (d <= 0 && l >= 0 && h <= 0)
      return a = l / (l - h), t.copy(i).addScaledVector(ud, a);
    O8.subVectors(e, r);
    const f = ud.dot(O8), p = dd.dot(O8);
    if (p >= 0 && f <= p)
      return t.copy(r);
    const v = f * c - l * p;
    if (v <= 0 && c >= 0 && p <= 0)
      return o = c / (c - p), t.copy(i).addScaledVector(dd, o);
    const g = h * p - f * u;
    if (g <= 0 && u - h >= 0 && f - p >= 0)
      return tE.subVectors(r, n), o = (u - h) / (u - h + (f - p)), t.copy(n).addScaledVector(tE, o);
    const m = 1 / (g + v + d);
    return a = v * m, o = d * m, t.copy(i).addScaledVector(ud, a).addScaledVector(dd, o);
  }
  equals(e) {
    return e.a.equals(this.a) && e.b.equals(this.b) && e.c.equals(this.c);
  }
};
const jL = {
  aliceblue: 15792383,
  antiquewhite: 16444375,
  aqua: 65535,
  aquamarine: 8388564,
  azure: 15794175,
  beige: 16119260,
  bisque: 16770244,
  black: 0,
  blanchedalmond: 16772045,
  blue: 255,
  blueviolet: 9055202,
  brown: 10824234,
  burlywood: 14596231,
  cadetblue: 6266528,
  chartreuse: 8388352,
  chocolate: 13789470,
  coral: 16744272,
  cornflowerblue: 6591981,
  cornsilk: 16775388,
  crimson: 14423100,
  cyan: 65535,
  darkblue: 139,
  darkcyan: 35723,
  darkgoldenrod: 12092939,
  darkgray: 11119017,
  darkgreen: 25600,
  darkgrey: 11119017,
  darkkhaki: 12433259,
  darkmagenta: 9109643,
  darkolivegreen: 5597999,
  darkorange: 16747520,
  darkorchid: 10040012,
  darkred: 9109504,
  darksalmon: 15308410,
  darkseagreen: 9419919,
  darkslateblue: 4734347,
  darkslategray: 3100495,
  darkslategrey: 3100495,
  darkturquoise: 52945,
  darkviolet: 9699539,
  deeppink: 16716947,
  deepskyblue: 49151,
  dimgray: 6908265,
  dimgrey: 6908265,
  dodgerblue: 2003199,
  firebrick: 11674146,
  floralwhite: 16775920,
  forestgreen: 2263842,
  fuchsia: 16711935,
  gainsboro: 14474460,
  ghostwhite: 16316671,
  gold: 16766720,
  goldenrod: 14329120,
  gray: 8421504,
  green: 32768,
  greenyellow: 11403055,
  grey: 8421504,
  honeydew: 15794160,
  hotpink: 16738740,
  indianred: 13458524,
  indigo: 4915330,
  ivory: 16777200,
  khaki: 15787660,
  lavender: 15132410,
  lavenderblush: 16773365,
  lawngreen: 8190976,
  lemonchiffon: 16775885,
  lightblue: 11393254,
  lightcoral: 15761536,
  lightcyan: 14745599,
  lightgoldenrodyellow: 16448210,
  lightgray: 13882323,
  lightgreen: 9498256,
  lightgrey: 13882323,
  lightpink: 16758465,
  lightsalmon: 16752762,
  lightseagreen: 2142890,
  lightskyblue: 8900346,
  lightslategray: 7833753,
  lightslategrey: 7833753,
  lightsteelblue: 11584734,
  lightyellow: 16777184,
  lime: 65280,
  limegreen: 3329330,
  linen: 16445670,
  magenta: 16711935,
  maroon: 8388608,
  mediumaquamarine: 6737322,
  mediumblue: 205,
  mediumorchid: 12211667,
  mediumpurple: 9662683,
  mediumseagreen: 3978097,
  mediumslateblue: 8087790,
  mediumspringgreen: 64154,
  mediumturquoise: 4772300,
  mediumvioletred: 13047173,
  midnightblue: 1644912,
  mintcream: 16121850,
  mistyrose: 16770273,
  moccasin: 16770229,
  navajowhite: 16768685,
  navy: 128,
  oldlace: 16643558,
  olive: 8421376,
  olivedrab: 7048739,
  orange: 16753920,
  orangered: 16729344,
  orchid: 14315734,
  palegoldenrod: 15657130,
  palegreen: 10025880,
  paleturquoise: 11529966,
  palevioletred: 14381203,
  papayawhip: 16773077,
  peachpuff: 16767673,
  peru: 13468991,
  pink: 16761035,
  plum: 14524637,
  powderblue: 11591910,
  purple: 8388736,
  rebeccapurple: 6697881,
  red: 16711680,
  rosybrown: 12357519,
  royalblue: 4286945,
  saddlebrown: 9127187,
  salmon: 16416882,
  sandybrown: 16032864,
  seagreen: 3050327,
  seashell: 16774638,
  sienna: 10506797,
  silver: 12632256,
  skyblue: 8900331,
  slateblue: 6970061,
  slategray: 7372944,
  slategrey: 7372944,
  snow: 16775930,
  springgreen: 65407,
  steelblue: 4620980,
  tan: 13808780,
  teal: 32896,
  thistle: 14204888,
  tomato: 16737095,
  turquoise: 4251856,
  violet: 15631086,
  wheat: 16113331,
  white: 16777215,
  whitesmoke: 16119285,
  yellow: 16776960,
  yellowgreen: 10145074
}, Ul = { h: 0, s: 0, l: 0 }, iv = { h: 0, s: 0, l: 0 };
function N8(s, e, t) {
  return t < 0 && (t += 1), t > 1 && (t -= 1), t < 1 / 6 ? s + (e - s) * 6 * t : t < 1 / 2 ? e : t < 2 / 3 ? s + (e - s) * 6 * (2 / 3 - t) : s;
}
let _e = class {
  constructor(e, t, i) {
    return this.isColor = !0, this.r = 1, this.g = 1, this.b = 1, this.set(e, t, i);
  }
  set(e, t, i) {
    if (t === void 0 && i === void 0) {
      const n = e;
      n && n.isColor ? this.copy(n) : typeof n == "number" ? this.setHex(n) : typeof n == "string" && this.setStyle(n);
    } else
      this.setRGB(e, t, i);
    return this;
  }
  setScalar(e) {
    return this.r = e, this.g = e, this.b = e, this;
  }
  setHex(e, t = qi) {
    return e = Math.floor(e), this.r = (e >> 16 & 255) / 255, this.g = (e >> 8 & 255) / 255, this.b = (e & 255) / 255, bi.toWorkingColorSpace(this, t), this;
  }
  setRGB(e, t, i, n = bi.workingColorSpace) {
    return this.r = e, this.g = t, this.b = i, bi.toWorkingColorSpace(this, n), this;
  }
  setHSL(e, t, i, n = bi.workingColorSpace) {
    if (e = OM(e, 1), t = Wn(t, 0, 1), i = Wn(i, 0, 1), t === 0)
      this.r = this.g = this.b = i;
    else {
      const r = i <= 0.5 ? i * (1 + t) : i + t - i * t, a = 2 * i - r;
      this.r = N8(a, r, e + 1 / 3), this.g = N8(a, r, e), this.b = N8(a, r, e - 1 / 3);
    }
    return bi.toWorkingColorSpace(this, n), this;
  }
  setStyle(e, t = qi) {
    function i(r) {
      r !== void 0 && parseFloat(r) < 1 && console.warn("THREE.Color: Alpha component of " + e + " will be ignored.");
    }
    let n;
    if (n = /^(\w+)\(([^\)]*)\)/.exec(e)) {
      let r;
      const a = n[1], o = n[2];
      switch (a) {
        case "rgb":
        case "rgba":
          if (r = /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setRGB(
              Math.min(255, parseInt(r[1], 10)) / 255,
              Math.min(255, parseInt(r[2], 10)) / 255,
              Math.min(255, parseInt(r[3], 10)) / 255,
              t
            );
          if (r = /^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setRGB(
              Math.min(100, parseInt(r[1], 10)) / 100,
              Math.min(100, parseInt(r[2], 10)) / 100,
              Math.min(100, parseInt(r[3], 10)) / 100,
              t
            );
          break;
        case "hsl":
        case "hsla":
          if (r = /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setHSL(
              parseFloat(r[1]) / 360,
              parseFloat(r[2]) / 100,
              parseFloat(r[3]) / 100,
              t
            );
          break;
        default:
          console.warn("THREE.Color: Unknown color model " + e);
      }
    } else if (n = /^\#([A-Fa-f\d]+)$/.exec(e)) {
      const r = n[1], a = r.length;
      if (a === 3)
        return this.setRGB(
          parseInt(r.charAt(0), 16) / 15,
          parseInt(r.charAt(1), 16) / 15,
          parseInt(r.charAt(2), 16) / 15,
          t
        );
      if (a === 6)
        return this.setHex(parseInt(r, 16), t);
      console.warn("THREE.Color: Invalid hex color " + e);
    } else if (e && e.length > 0)
      return this.setColorName(e, t);
    return this;
  }
  setColorName(e, t = qi) {
    const i = jL[e.toLowerCase()];
    return i !== void 0 ? this.setHex(i, t) : console.warn("THREE.Color: Unknown color " + e), this;
  }
  clone() {
    return new this.constructor(this.r, this.g, this.b);
  }
  copy(e) {
    return this.r = e.r, this.g = e.g, this.b = e.b, this;
  }
  copySRGBToLinear(e) {
    return this.r = Nf(e.r), this.g = Nf(e.g), this.b = Nf(e.b), this;
  }
  copyLinearToSRGB(e) {
    return this.r = M8(e.r), this.g = M8(e.g), this.b = M8(e.b), this;
  }
  convertSRGBToLinear() {
    return this.copySRGBToLinear(this), this;
  }
  convertLinearToSRGB() {
    return this.copyLinearToSRGB(this), this;
  }
  getHex(e = qi) {
    return bi.fromWorkingColorSpace(Ws.copy(this), e), Math.round(Wn(Ws.r * 255, 0, 255)) * 65536 + Math.round(Wn(Ws.g * 255, 0, 255)) * 256 + Math.round(Wn(Ws.b * 255, 0, 255));
  }
  getHexString(e = qi) {
    return ("000000" + this.getHex(e).toString(16)).slice(-6);
  }
  getHSL(e, t = bi.workingColorSpace) {
    bi.fromWorkingColorSpace(Ws.copy(this), t);
    const i = Ws.r, n = Ws.g, r = Ws.b, a = Math.max(i, n, r), o = Math.min(i, n, r);
    let l, c;
    const h = (o + a) / 2;
    if (o === a)
      l = 0, c = 0;
    else {
      const u = a - o;
      switch (c = h <= 0.5 ? u / (a + o) : u / (2 - a - o), a) {
        case i:
          l = (n - r) / u + (n < r ? 6 : 0);
          break;
        case n:
          l = (r - i) / u + 2;
          break;
        case r:
          l = (i - n) / u + 4;
          break;
      }
      l /= 6;
    }
    return e.h = l, e.s = c, e.l = h, e;
  }
  getRGB(e, t = bi.workingColorSpace) {
    return bi.fromWorkingColorSpace(Ws.copy(this), t), e.r = Ws.r, e.g = Ws.g, e.b = Ws.b, e;
  }
  getStyle(e = qi) {
    bi.fromWorkingColorSpace(Ws.copy(this), e);
    const t = Ws.r, i = Ws.g, n = Ws.b;
    return e !== qi ? `color(${e} ${t.toFixed(3)} ${i.toFixed(3)} ${n.toFixed(3)})` : `rgb(${Math.round(t * 255)},${Math.round(i * 255)},${Math.round(n * 255)})`;
  }
  offsetHSL(e, t, i) {
    return this.getHSL(Ul), this.setHSL(Ul.h + e, Ul.s + t, Ul.l + i);
  }
  add(e) {
    return this.r += e.r, this.g += e.g, this.b += e.b, this;
  }
  addColors(e, t) {
    return this.r = e.r + t.r, this.g = e.g + t.g, this.b = e.b + t.b, this;
  }
  addScalar(e) {
    return this.r += e, this.g += e, this.b += e, this;
  }
  sub(e) {
    return this.r = Math.max(0, this.r - e.r), this.g = Math.max(0, this.g - e.g), this.b = Math.max(0, this.b - e.b), this;
  }
  multiply(e) {
    return this.r *= e.r, this.g *= e.g, this.b *= e.b, this;
  }
  multiplyScalar(e) {
    return this.r *= e, this.g *= e, this.b *= e, this;
  }
  lerp(e, t) {
    return this.r += (e.r - this.r) * t, this.g += (e.g - this.g) * t, this.b += (e.b - this.b) * t, this;
  }
  lerpColors(e, t, i) {
    return this.r = e.r + (t.r - e.r) * i, this.g = e.g + (t.g - e.g) * i, this.b = e.b + (t.b - e.b) * i, this;
  }
  lerpHSL(e, t) {
    this.getHSL(Ul), e.getHSL(iv);
    const i = w1(Ul.h, iv.h, t), n = w1(Ul.s, iv.s, t), r = w1(Ul.l, iv.l, t);
    return this.setHSL(i, n, r), this;
  }
  setFromVector3(e) {
    return this.r = e.x, this.g = e.y, this.b = e.z, this;
  }
  applyMatrix3(e) {
    const t = this.r, i = this.g, n = this.b, r = e.elements;
    return this.r = r[0] * t + r[3] * i + r[6] * n, this.g = r[1] * t + r[4] * i + r[7] * n, this.b = r[2] * t + r[5] * i + r[8] * n, this;
  }
  equals(e) {
    return e.r === this.r && e.g === this.g && e.b === this.b;
  }
  fromArray(e, t = 0) {
    return this.r = e[t], this.g = e[t + 1], this.b = e[t + 2], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.r, e[t + 1] = this.g, e[t + 2] = this.b, e;
  }
  fromBufferAttribute(e, t) {
    return this.r = e.getX(t), this.g = e.getY(t), this.b = e.getZ(t), this;
  }
  toJSON() {
    return this.getHex();
  }
  *[Symbol.iterator]() {
    yield this.r, yield this.g, yield this.b;
  }
};
const Ws = /* @__PURE__ */ new _e();
_e.NAMES = jL;
let Sk = 0, Cn = class extends ra {
  constructor() {
    super(), this.isMaterial = !0, Object.defineProperty(this, "id", { value: Sk++ }), this.uuid = ea(), this.name = "", this.type = "Material", this.blending = so, this.side = sa, this.vertexColors = !1, this.opacity = 1, this.transparent = !1, this.alphaHash = !1, this.blendSrc = v9, this.blendDst = y9, this.blendEquation = Ma, this.blendSrcAlpha = null, this.blendDstAlpha = null, this.blendEquationAlpha = null, this.blendColor = new _e(0, 0, 0), this.blendAlpha = 0, this.depthFunc = j1, this.depthTest = !0, this.depthWrite = !0, this.stencilWriteMask = 255, this.stencilFunc = V6, this.stencilRef = 0, this.stencilFuncMask = 255, this.stencilFail = jh, this.stencilZFail = jh, this.stencilZPass = jh, this.stencilWrite = !1, this.clippingPlanes = null, this.clipIntersection = !1, this.clipShadows = !1, this.shadowSide = null, this.colorWrite = !0, this.precision = null, this.polygonOffset = !1, this.polygonOffsetFactor = 0, this.polygonOffsetUnits = 0, this.dithering = !1, this.alphaToCoverage = !1, this.premultipliedAlpha = !1, this.forceSinglePass = !1, this.visible = !0, this.toneMapped = !0, this.userData = {}, this.version = 0, this._alphaTest = 0;
  }
  get alphaTest() {
    return this._alphaTest;
  }
  set alphaTest(e) {
    this._alphaTest > 0 != e > 0 && this.version++, this._alphaTest = e;
  }
  onBeforeCompile() {
  }
  customProgramCacheKey() {
    return this.onBeforeCompile.toString();
  }
  setValues(e) {
    if (e !== void 0)
      for (const t in e) {
        const i = e[t];
        if (i === void 0) {
          console.warn(`THREE.Material: parameter '${t}' has value of undefined.`);
          continue;
        }
        const n = this[t];
        if (n === void 0) {
          console.warn(`THREE.Material: '${t}' is not a property of THREE.${this.type}.`);
          continue;
        }
        n && n.isColor ? n.set(i) : n && n.isVector3 && i && i.isVector3 ? n.copy(i) : this[t] = i;
      }
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    t && (e = {
      textures: {},
      images: {}
    });
    const i = {
      metadata: {
        version: 4.6,
        type: "Material",
        generator: "Material.toJSON"
      }
    };
    i.uuid = this.uuid, i.type = this.type, this.name !== "" && (i.name = this.name), this.color && this.color.isColor && (i.color = this.color.getHex()), this.roughness !== void 0 && (i.roughness = this.roughness), this.metalness !== void 0 && (i.metalness = this.metalness), this.sheen !== void 0 && (i.sheen = this.sheen), this.sheenColor && this.sheenColor.isColor && (i.sheenColor = this.sheenColor.getHex()), this.sheenRoughness !== void 0 && (i.sheenRoughness = this.sheenRoughness), this.emissive && this.emissive.isColor && (i.emissive = this.emissive.getHex()), this.emissiveIntensity !== void 0 && this.emissiveIntensity !== 1 && (i.emissiveIntensity = this.emissiveIntensity), this.specular && this.specular.isColor && (i.specular = this.specular.getHex()), this.specularIntensity !== void 0 && (i.specularIntensity = this.specularIntensity), this.specularColor && this.specularColor.isColor && (i.specularColor = this.specularColor.getHex()), this.shininess !== void 0 && (i.shininess = this.shininess), this.clearcoat !== void 0 && (i.clearcoat = this.clearcoat), this.clearcoatRoughness !== void 0 && (i.clearcoatRoughness = this.clearcoatRoughness), this.clearcoatMap && this.clearcoatMap.isTexture && (i.clearcoatMap = this.clearcoatMap.toJSON(e).uuid), this.clearcoatRoughnessMap && this.clearcoatRoughnessMap.isTexture && (i.clearcoatRoughnessMap = this.clearcoatRoughnessMap.toJSON(e).uuid), this.clearcoatNormalMap && this.clearcoatNormalMap.isTexture && (i.clearcoatNormalMap = this.clearcoatNormalMap.toJSON(e).uuid, i.clearcoatNormalScale = this.clearcoatNormalScale.toArray()), this.dispersion !== void 0 && (i.dispersion = this.dispersion), this.iridescence !== void 0 && (i.iridescence = this.iridescence), this.iridescenceIOR !== void 0 && (i.iridescenceIOR = this.iridescenceIOR), this.iridescenceThicknessRange !== void 0 && (i.iridescenceThicknessRange = this.iridescenceThicknessRange), this.iridescenceMap && this.iridescenceMap.isTexture && (i.iridescenceMap = this.iridescenceMap.toJSON(e).uuid), this.iridescenceThicknessMap && this.iridescenceThicknessMap.isTexture && (i.iridescenceThicknessMap = this.iridescenceThicknessMap.toJSON(e).uuid), this.anisotropy !== void 0 && (i.anisotropy = this.anisotropy), this.anisotropyRotation !== void 0 && (i.anisotropyRotation = this.anisotropyRotation), this.anisotropyMap && this.anisotropyMap.isTexture && (i.anisotropyMap = this.anisotropyMap.toJSON(e).uuid), this.map && this.map.isTexture && (i.map = this.map.toJSON(e).uuid), this.matcap && this.matcap.isTexture && (i.matcap = this.matcap.toJSON(e).uuid), this.alphaMap && this.alphaMap.isTexture && (i.alphaMap = this.alphaMap.toJSON(e).uuid), this.lightMap && this.lightMap.isTexture && (i.lightMap = this.lightMap.toJSON(e).uuid, i.lightMapIntensity = this.lightMapIntensity), this.aoMap && this.aoMap.isTexture && (i.aoMap = this.aoMap.toJSON(e).uuid, i.aoMapIntensity = this.aoMapIntensity), this.bumpMap && this.bumpMap.isTexture && (i.bumpMap = this.bumpMap.toJSON(e).uuid, i.bumpScale = this.bumpScale), this.normalMap && this.normalMap.isTexture && (i.normalMap = this.normalMap.toJSON(e).uuid, i.normalMapType = this.normalMapType, i.normalScale = this.normalScale.toArray()), this.displacementMap && this.displacementMap.isTexture && (i.displacementMap = this.displacementMap.toJSON(e).uuid, i.displacementScale = this.displacementScale, i.displacementBias = this.displacementBias), this.roughnessMap && this.roughnessMap.isTexture && (i.roughnessMap = this.roughnessMap.toJSON(e).uuid), this.metalnessMap && this.metalnessMap.isTexture && (i.metalnessMap = this.metalnessMap.toJSON(e).uuid), this.emissiveMap && this.emissiveMap.isTexture && (i.emissiveMap = this.emissiveMap.toJSON(e).uuid), this.specularMap && this.specularMap.isTexture && (i.specularMap = this.specularMap.toJSON(e).uuid), this.specularIntensityMap && this.specularIntensityMap.isTexture && (i.specularIntensityMap = this.specularIntensityMap.toJSON(e).uuid), this.specularColorMap && this.specularColorMap.isTexture && (i.specularColorMap = this.specularColorMap.toJSON(e).uuid), this.envMap && this.envMap.isTexture && (i.envMap = this.envMap.toJSON(e).uuid, this.combine !== void 0 && (i.combine = this.combine)), this.envMapRotation !== void 0 && (i.envMapRotation = this.envMapRotation.toArray()), this.envMapIntensity !== void 0 && (i.envMapIntensity = this.envMapIntensity), this.reflectivity !== void 0 && (i.reflectivity = this.reflectivity), this.refractionRatio !== void 0 && (i.refractionRatio = this.refractionRatio), this.gradientMap && this.gradientMap.isTexture && (i.gradientMap = this.gradientMap.toJSON(e).uuid), this.transmission !== void 0 && (i.transmission = this.transmission), this.transmissionMap && this.transmissionMap.isTexture && (i.transmissionMap = this.transmissionMap.toJSON(e).uuid), this.thickness !== void 0 && (i.thickness = this.thickness), this.thicknessMap && this.thicknessMap.isTexture && (i.thicknessMap = this.thicknessMap.toJSON(e).uuid), this.attenuationDistance !== void 0 && this.attenuationDistance !== 1 / 0 && (i.attenuationDistance = this.attenuationDistance), this.attenuationColor !== void 0 && (i.attenuationColor = this.attenuationColor.getHex()), this.size !== void 0 && (i.size = this.size), this.shadowSide !== null && (i.shadowSide = this.shadowSide), this.sizeAttenuation !== void 0 && (i.sizeAttenuation = this.sizeAttenuation), this.blending !== so && (i.blending = this.blending), this.side !== sa && (i.side = this.side), this.vertexColors === !0 && (i.vertexColors = !0), this.opacity < 1 && (i.opacity = this.opacity), this.transparent === !0 && (i.transparent = !0), this.blendSrc !== v9 && (i.blendSrc = this.blendSrc), this.blendDst !== y9 && (i.blendDst = this.blendDst), this.blendEquation !== Ma && (i.blendEquation = this.blendEquation), this.blendSrcAlpha !== null && (i.blendSrcAlpha = this.blendSrcAlpha), this.blendDstAlpha !== null && (i.blendDstAlpha = this.blendDstAlpha), this.blendEquationAlpha !== null && (i.blendEquationAlpha = this.blendEquationAlpha), this.blendColor && this.blendColor.isColor && (i.blendColor = this.blendColor.getHex()), this.blendAlpha !== 0 && (i.blendAlpha = this.blendAlpha), this.depthFunc !== j1 && (i.depthFunc = this.depthFunc), this.depthTest === !1 && (i.depthTest = this.depthTest), this.depthWrite === !1 && (i.depthWrite = this.depthWrite), this.colorWrite === !1 && (i.colorWrite = this.colorWrite), this.stencilWriteMask !== 255 && (i.stencilWriteMask = this.stencilWriteMask), this.stencilFunc !== V6 && (i.stencilFunc = this.stencilFunc), this.stencilRef !== 0 && (i.stencilRef = this.stencilRef), this.stencilFuncMask !== 255 && (i.stencilFuncMask = this.stencilFuncMask), this.stencilFail !== jh && (i.stencilFail = this.stencilFail), this.stencilZFail !== jh && (i.stencilZFail = this.stencilZFail), this.stencilZPass !== jh && (i.stencilZPass = this.stencilZPass), this.stencilWrite === !0 && (i.stencilWrite = this.stencilWrite), this.rotation !== void 0 && this.rotation !== 0 && (i.rotation = this.rotation), this.polygonOffset === !0 && (i.polygonOffset = !0), this.polygonOffsetFactor !== 0 && (i.polygonOffsetFactor = this.polygonOffsetFactor), this.polygonOffsetUnits !== 0 && (i.polygonOffsetUnits = this.polygonOffsetUnits), this.linewidth !== void 0 && this.linewidth !== 1 && (i.linewidth = this.linewidth), this.dashSize !== void 0 && (i.dashSize = this.dashSize), this.gapSize !== void 0 && (i.gapSize = this.gapSize), this.scale !== void 0 && (i.scale = this.scale), this.dithering === !0 && (i.dithering = !0), this.alphaTest > 0 && (i.alphaTest = this.alphaTest), this.alphaHash === !0 && (i.alphaHash = !0), this.alphaToCoverage === !0 && (i.alphaToCoverage = !0), this.premultipliedAlpha === !0 && (i.premultipliedAlpha = !0), this.forceSinglePass === !0 && (i.forceSinglePass = !0), this.wireframe === !0 && (i.wireframe = !0), this.wireframeLinewidth > 1 && (i.wireframeLinewidth = this.wireframeLinewidth), this.wireframeLinecap !== "round" && (i.wireframeLinecap = this.wireframeLinecap), this.wireframeLinejoin !== "round" && (i.wireframeLinejoin = this.wireframeLinejoin), this.flatShading === !0 && (i.flatShading = !0), this.visible === !1 && (i.visible = !1), this.toneMapped === !1 && (i.toneMapped = !1), this.fog === !1 && (i.fog = !1), Object.keys(this.userData).length > 0 && (i.userData = this.userData);
    function n(r) {
      const a = [];
      for (const o in r) {
        const l = r[o];
        delete l.metadata, a.push(l);
      }
      return a;
    }
    if (t) {
      const r = n(e.textures), a = n(e.images);
      r.length > 0 && (i.textures = r), a.length > 0 && (i.images = a);
    }
    return i;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.name = e.name, this.blending = e.blending, this.side = e.side, this.vertexColors = e.vertexColors, this.opacity = e.opacity, this.transparent = e.transparent, this.blendSrc = e.blendSrc, this.blendDst = e.blendDst, this.blendEquation = e.blendEquation, this.blendSrcAlpha = e.blendSrcAlpha, this.blendDstAlpha = e.blendDstAlpha, this.blendEquationAlpha = e.blendEquationAlpha, this.blendColor.copy(e.blendColor), this.blendAlpha = e.blendAlpha, this.depthFunc = e.depthFunc, this.depthTest = e.depthTest, this.depthWrite = e.depthWrite, this.stencilWriteMask = e.stencilWriteMask, this.stencilFunc = e.stencilFunc, this.stencilRef = e.stencilRef, this.stencilFuncMask = e.stencilFuncMask, this.stencilFail = e.stencilFail, this.stencilZFail = e.stencilZFail, this.stencilZPass = e.stencilZPass, this.stencilWrite = e.stencilWrite;
    const t = e.clippingPlanes;
    let i = null;
    if (t !== null) {
      const n = t.length;
      i = new Array(n);
      for (let r = 0; r !== n; ++r)
        i[r] = t[r].clone();
    }
    return this.clippingPlanes = i, this.clipIntersection = e.clipIntersection, this.clipShadows = e.clipShadows, this.shadowSide = e.shadowSide, this.colorWrite = e.colorWrite, this.precision = e.precision, this.polygonOffset = e.polygonOffset, this.polygonOffsetFactor = e.polygonOffsetFactor, this.polygonOffsetUnits = e.polygonOffsetUnits, this.dithering = e.dithering, this.alphaTest = e.alphaTest, this.alphaHash = e.alphaHash, this.alphaToCoverage = e.alphaToCoverage, this.premultipliedAlpha = e.premultipliedAlpha, this.forceSinglePass = e.forceSinglePass, this.visible = e.visible, this.toneMapped = e.toneMapped, this.userData = JSON.parse(JSON.stringify(e.userData)), this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  onBuild() {
    console.warn("Material: onBuild() has been removed.");
  }
  onBeforeRender() {
    console.warn("Material: onBeforeRender() has been removed.");
  }
}, kt = class extends Cn {
  constructor(e) {
    super(), this.isMeshBasicMaterial = !0, this.type = "MeshBasicMaterial", this.color = new _e(16777215), this.map = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.specularMap = null, this.alphaMap = null, this.envMap = null, this.envMapRotation = new Mi(), this.combine = e2, this.reflectivity = 1, this.refractionRatio = 0.98, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.specularMap = e.specularMap, this.alphaMap = e.alphaMap, this.envMap = e.envMap, this.envMapRotation.copy(e.envMapRotation), this.combine = e.combine, this.reflectivity = e.reflectivity, this.refractionRatio = e.refractionRatio, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.fog = e.fog, this;
  }
};
const J0 = /* @__PURE__ */ wk();
function wk() {
  const s = new ArrayBuffer(4), e = new Float32Array(s), t = new Uint32Array(s), i = new Uint32Array(512), n = new Uint32Array(512);
  for (let l = 0; l < 256; ++l) {
    const c = l - 127;
    c < -27 ? (i[l] = 0, i[l | 256] = 32768, n[l] = 24, n[l | 256] = 24) : c < -14 ? (i[l] = 1024 >> -c - 14, i[l | 256] = 1024 >> -c - 14 | 32768, n[l] = -c - 1, n[l | 256] = -c - 1) : c <= 15 ? (i[l] = c + 15 << 10, i[l | 256] = c + 15 << 10 | 32768, n[l] = 13, n[l | 256] = 13) : c < 128 ? (i[l] = 31744, i[l | 256] = 64512, n[l] = 24, n[l | 256] = 24) : (i[l] = 31744, i[l | 256] = 64512, n[l] = 13, n[l | 256] = 13);
  }
  const r = new Uint32Array(2048), a = new Uint32Array(64), o = new Uint32Array(64);
  for (let l = 1; l < 1024; ++l) {
    let c = l << 13, h = 0;
    for (; !(c & 8388608); )
      c <<= 1, h -= 8388608;
    c &= -8388609, h += 947912704, r[l] = c | h;
  }
  for (let l = 1024; l < 2048; ++l)
    r[l] = 939524096 + (l - 1024 << 13);
  for (let l = 1; l < 31; ++l)
    a[l] = l << 23;
  a[31] = 1199570944, a[32] = 2147483648;
  for (let l = 33; l < 63; ++l)
    a[l] = 2147483648 + (l - 32 << 23);
  a[63] = 3347054592;
  for (let l = 1; l < 64; ++l)
    l !== 32 && (o[l] = 1024);
  return {
    floatView: e,
    uint32View: t,
    baseTable: i,
    shiftTable: n,
    mantissaTable: r,
    exponentTable: a,
    offsetTable: o
  };
}
function Er(s) {
  Math.abs(s) > 65504 && console.warn("THREE.DataUtils.toHalfFloat(): Value out of range."), s = Wn(s, -65504, 65504), J0.floatView[0] = s;
  const e = J0.uint32View[0], t = e >> 23 & 511;
  return J0.baseTable[t] + ((e & 8388607) >> J0.shiftTable[t]);
}
function s1(s) {
  const e = s >> 10;
  return J0.uint32View[0] = J0.mantissaTable[J0.offsetTable[e] + (s & 1023)] + J0.exponentTable[e], J0.floatView[0];
}
const el = {
  toHalfFloat: Er,
  fromHalfFloat: s1
}, is = /* @__PURE__ */ new _(), nv = /* @__PURE__ */ new Se();
let Ht = class {
  constructor(e, t, i = !1) {
    if (Array.isArray(e))
      throw new TypeError("THREE.BufferAttribute: array should be a Typed Array.");
    this.isBufferAttribute = !0, this.name = "", this.array = e, this.itemSize = t, this.count = e !== void 0 ? e.length / t : 0, this.normalized = i, this.usage = J1, this._updateRange = { offset: 0, count: -1 }, this.updateRanges = [], this.gpuType = xn, this.version = 0;
  }
  onUploadCallback() {
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  get updateRange() {
    return NM("THREE.BufferAttribute: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead."), this._updateRange;
  }
  setUsage(e) {
    return this.usage = e, this;
  }
  addUpdateRange(e, t) {
    this.updateRanges.push({ start: e, count: t });
  }
  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }
  copy(e) {
    return this.name = e.name, this.array = new e.array.constructor(e.array), this.itemSize = e.itemSize, this.count = e.count, this.normalized = e.normalized, this.usage = e.usage, this.gpuType = e.gpuType, this;
  }
  copyAt(e, t, i) {
    e *= this.itemSize, i *= t.itemSize;
    for (let n = 0, r = this.itemSize; n < r; n++)
      this.array[e + n] = t.array[i + n];
    return this;
  }
  copyArray(e) {
    return this.array.set(e), this;
  }
  applyMatrix3(e) {
    if (this.itemSize === 2)
      for (let t = 0, i = this.count; t < i; t++)
        nv.fromBufferAttribute(this, t), nv.applyMatrix3(e), this.setXY(t, nv.x, nv.y);
    else if (this.itemSize === 3)
      for (let t = 0, i = this.count; t < i; t++)
        is.fromBufferAttribute(this, t), is.applyMatrix3(e), this.setXYZ(t, is.x, is.y, is.z);
    return this;
  }
  applyMatrix4(e) {
    for (let t = 0, i = this.count; t < i; t++)
      is.fromBufferAttribute(this, t), is.applyMatrix4(e), this.setXYZ(t, is.x, is.y, is.z);
    return this;
  }
  applyNormalMatrix(e) {
    for (let t = 0, i = this.count; t < i; t++)
      is.fromBufferAttribute(this, t), is.applyNormalMatrix(e), this.setXYZ(t, is.x, is.y, is.z);
    return this;
  }
  transformDirection(e) {
    for (let t = 0, i = this.count; t < i; t++)
      is.fromBufferAttribute(this, t), is.transformDirection(e), this.setXYZ(t, is.x, is.y, is.z);
    return this;
  }
  set(e, t = 0) {
    return this.array.set(e, t), this;
  }
  getComponent(e, t) {
    let i = this.array[e * this.itemSize + t];
    return this.normalized && (i = vr(i, this.array)), i;
  }
  setComponent(e, t, i) {
    return this.normalized && (i = hi(i, this.array)), this.array[e * this.itemSize + t] = i, this;
  }
  getX(e) {
    let t = this.array[e * this.itemSize];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setX(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize] = t, this;
  }
  getY(e) {
    let t = this.array[e * this.itemSize + 1];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setY(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 1] = t, this;
  }
  getZ(e) {
    let t = this.array[e * this.itemSize + 2];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setZ(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 2] = t, this;
  }
  getW(e) {
    let t = this.array[e * this.itemSize + 3];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setW(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 3] = t, this;
  }
  setXY(e, t, i) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this;
  }
  setXYZ(e, t, i, n) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this.array[e + 2] = n, this;
  }
  setXYZW(e, t, i, n, r) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array), r = hi(r, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this.array[e + 2] = n, this.array[e + 3] = r, this;
  }
  onUpload(e) {
    return this.onUploadCallback = e, this;
  }
  clone() {
    return new this.constructor(this.array, this.itemSize).copy(this);
  }
  toJSON() {
    const e = {
      itemSize: this.itemSize,
      type: this.array.constructor.name,
      array: Array.from(this.array),
      normalized: this.normalized
    };
    return this.name !== "" && (e.name = this.name), this.usage !== J1 && (e.usage = this.usage), e;
  }
};
class Ek extends Ht {
  constructor(e, t, i) {
    super(new Int8Array(e), t, i);
  }
}
class Tk extends Ht {
  constructor(e, t, i) {
    super(new Uint8Array(e), t, i);
  }
}
class Ck extends Ht {
  constructor(e, t, i) {
    super(new Uint8ClampedArray(e), t, i);
  }
}
class _k extends Ht {
  constructor(e, t, i) {
    super(new Int16Array(e), t, i);
  }
}
let FM = class extends Ht {
  constructor(e, t, i) {
    super(new Uint16Array(e), t, i);
  }
};
class Pk extends Ht {
  constructor(e, t, i) {
    super(new Int32Array(e), t, i);
  }
}
let M4 = class extends Ht {
  constructor(e, t, i) {
    super(new Uint32Array(e), t, i);
  }
};
class Rk extends Ht {
  constructor(e, t, i) {
    super(new Uint16Array(e), t, i), this.isFloat16BufferAttribute = !0;
  }
  getX(e) {
    let t = s1(this.array[e * this.itemSize]);
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setX(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize] = Er(t), this;
  }
  getY(e) {
    let t = s1(this.array[e * this.itemSize + 1]);
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setY(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 1] = Er(t), this;
  }
  getZ(e) {
    let t = s1(this.array[e * this.itemSize + 2]);
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setZ(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 2] = Er(t), this;
  }
  getW(e) {
    let t = s1(this.array[e * this.itemSize + 3]);
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setW(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.array[e * this.itemSize + 3] = Er(t), this;
  }
  setXY(e, t, i) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array)), this.array[e + 0] = Er(t), this.array[e + 1] = Er(i), this;
  }
  setXYZ(e, t, i, n) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array)), this.array[e + 0] = Er(t), this.array[e + 1] = Er(i), this.array[e + 2] = Er(n), this;
  }
  setXYZW(e, t, i, n, r) {
    return e *= this.itemSize, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array), r = hi(r, this.array)), this.array[e + 0] = Er(t), this.array[e + 1] = Er(i), this.array[e + 2] = Er(n), this.array[e + 3] = Er(r), this;
  }
}
let gt = class extends Ht {
  constructor(e, t, i) {
    super(new Float32Array(e), t, i);
  }
}, Dk = 0;
const la = /* @__PURE__ */ new et(), B8 = /* @__PURE__ */ new mt(), fd = /* @__PURE__ */ new _(), Vr = /* @__PURE__ */ new zt(), hm = /* @__PURE__ */ new zt(), bs = /* @__PURE__ */ new _();
let Pt = class qL extends ra {
  constructor() {
    super(), this.isBufferGeometry = !0, Object.defineProperty(this, "id", { value: Dk++ }), this.uuid = ea(), this.name = "", this.type = "BufferGeometry", this.index = null, this.attributes = {}, this.morphAttributes = {}, this.morphTargetsRelative = !1, this.groups = [], this.boundingBox = null, this.boundingSphere = null, this.drawRange = { start: 0, count: 1 / 0 }, this.userData = {};
  }
  getIndex() {
    return this.index;
  }
  setIndex(e) {
    return Array.isArray(e) ? this.index = new (zL(e) ? M4 : FM)(e, 1) : this.index = e, this;
  }
  getAttribute(e) {
    return this.attributes[e];
  }
  setAttribute(e, t) {
    return this.attributes[e] = t, this;
  }
  deleteAttribute(e) {
    return delete this.attributes[e], this;
  }
  hasAttribute(e) {
    return this.attributes[e] !== void 0;
  }
  addGroup(e, t, i = 0) {
    this.groups.push({
      start: e,
      count: t,
      materialIndex: i
    });
  }
  clearGroups() {
    this.groups = [];
  }
  setDrawRange(e, t) {
    this.drawRange.start = e, this.drawRange.count = t;
  }
  applyMatrix4(e) {
    const t = this.attributes.position;
    t !== void 0 && (t.applyMatrix4(e), t.needsUpdate = !0);
    const i = this.attributes.normal;
    if (i !== void 0) {
      const r = new Gt().getNormalMatrix(e);
      i.applyNormalMatrix(r), i.needsUpdate = !0;
    }
    const n = this.attributes.tangent;
    return n !== void 0 && (n.transformDirection(e), n.needsUpdate = !0), this.boundingBox !== null && this.computeBoundingBox(), this.boundingSphere !== null && this.computeBoundingSphere(), this;
  }
  applyQuaternion(e) {
    return la.makeRotationFromQuaternion(e), this.applyMatrix4(la), this;
  }
  rotateX(e) {
    return la.makeRotationX(e), this.applyMatrix4(la), this;
  }
  rotateY(e) {
    return la.makeRotationY(e), this.applyMatrix4(la), this;
  }
  rotateZ(e) {
    return la.makeRotationZ(e), this.applyMatrix4(la), this;
  }
  translate(e, t, i) {
    return la.makeTranslation(e, t, i), this.applyMatrix4(la), this;
  }
  scale(e, t, i) {
    return la.makeScale(e, t, i), this.applyMatrix4(la), this;
  }
  lookAt(e) {
    return B8.lookAt(e), B8.updateMatrix(), this.applyMatrix4(B8.matrix), this;
  }
  center() {
    return this.computeBoundingBox(), this.boundingBox.getCenter(fd).negate(), this.translate(fd.x, fd.y, fd.z), this;
  }
  setFromPoints(e) {
    const t = [];
    for (let i = 0, n = e.length; i < n; i++) {
      const r = e[i];
      t.push(r.x, r.y, r.z || 0);
    }
    return this.setAttribute("position", new gt(t, 3)), this;
  }
  computeBoundingBox() {
    this.boundingBox === null && (this.boundingBox = new zt());
    const e = this.attributes.position, t = this.morphAttributes.position;
    if (e && e.isGLBufferAttribute) {
      console.error("THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.", this), this.boundingBox.set(
        new _(-1 / 0, -1 / 0, -1 / 0),
        new _(1 / 0, 1 / 0, 1 / 0)
      );
      return;
    }
    if (e !== void 0) {
      if (this.boundingBox.setFromBufferAttribute(e), t)
        for (let i = 0, n = t.length; i < n; i++) {
          const r = t[i];
          Vr.setFromBufferAttribute(r), this.morphTargetsRelative ? (bs.addVectors(this.boundingBox.min, Vr.min), this.boundingBox.expandByPoint(bs), bs.addVectors(this.boundingBox.max, Vr.max), this.boundingBox.expandByPoint(bs)) : (this.boundingBox.expandByPoint(Vr.min), this.boundingBox.expandByPoint(Vr.max));
        }
    } else
      this.boundingBox.makeEmpty();
    (isNaN(this.boundingBox.min.x) || isNaN(this.boundingBox.min.y) || isNaN(this.boundingBox.min.z)) && console.error('THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.', this);
  }
  computeBoundingSphere() {
    this.boundingSphere === null && (this.boundingSphere = new Tn());
    const e = this.attributes.position, t = this.morphAttributes.position;
    if (e && e.isGLBufferAttribute) {
      console.error("THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.", this), this.boundingSphere.set(new _(), 1 / 0);
      return;
    }
    if (e) {
      const i = this.boundingSphere.center;
      if (Vr.setFromBufferAttribute(e), t)
        for (let r = 0, a = t.length; r < a; r++) {
          const o = t[r];
          hm.setFromBufferAttribute(o), this.morphTargetsRelative ? (bs.addVectors(Vr.min, hm.min), Vr.expandByPoint(bs), bs.addVectors(Vr.max, hm.max), Vr.expandByPoint(bs)) : (Vr.expandByPoint(hm.min), Vr.expandByPoint(hm.max));
        }
      Vr.getCenter(i);
      let n = 0;
      for (let r = 0, a = e.count; r < a; r++)
        bs.fromBufferAttribute(e, r), n = Math.max(n, i.distanceToSquared(bs));
      if (t)
        for (let r = 0, a = t.length; r < a; r++) {
          const o = t[r], l = this.morphTargetsRelative;
          for (let c = 0, h = o.count; c < h; c++)
            bs.fromBufferAttribute(o, c), l && (fd.fromBufferAttribute(e, c), bs.add(fd)), n = Math.max(n, i.distanceToSquared(bs));
        }
      this.boundingSphere.radius = Math.sqrt(n), isNaN(this.boundingSphere.radius) && console.error('THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.', this);
    }
  }
  computeTangents() {
    const e = this.index, t = this.attributes;
    if (e === null || t.position === void 0 || t.normal === void 0 || t.uv === void 0) {
      console.error("THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)");
      return;
    }
    const i = t.position, n = t.normal, r = t.uv;
    this.hasAttribute("tangent") === !1 && this.setAttribute("tangent", new Ht(new Float32Array(4 * i.count), 4));
    const a = this.getAttribute("tangent"), o = [], l = [];
    for (let C = 0; C < i.count; C++)
      o[C] = new _(), l[C] = new _();
    const c = new _(), h = new _(), u = new _(), d = new Se(), f = new Se(), p = new Se(), v = new _(), g = new _();
    function m(C, T, E) {
      c.fromBufferAttribute(i, C), h.fromBufferAttribute(i, T), u.fromBufferAttribute(i, E), d.fromBufferAttribute(r, C), f.fromBufferAttribute(r, T), p.fromBufferAttribute(r, E), h.sub(c), u.sub(c), f.sub(d), p.sub(d);
      const P = 1 / (f.x * p.y - p.x * f.y);
      isFinite(P) && (v.copy(h).multiplyScalar(p.y).addScaledVector(u, -f.y).multiplyScalar(P), g.copy(u).multiplyScalar(f.x).addScaledVector(h, -p.x).multiplyScalar(P), o[C].add(v), o[T].add(v), o[E].add(v), l[C].add(g), l[T].add(g), l[E].add(g));
    }
    let x = this.groups;
    x.length === 0 && (x = [{
      start: 0,
      count: e.count
    }]);
    for (let C = 0, T = x.length; C < T; ++C) {
      const E = x[C], P = E.start, L = E.count;
      for (let I = P, N = P + L; I < N; I += 3)
        m(
          e.getX(I + 0),
          e.getX(I + 1),
          e.getX(I + 2)
        );
    }
    const y = new _(), A = new _(), b = new _(), M = new _();
    function w(C) {
      b.fromBufferAttribute(n, C), M.copy(b);
      const T = o[C];
      y.copy(T), y.sub(b.multiplyScalar(b.dot(T))).normalize(), A.crossVectors(M, T);
      const P = A.dot(l[C]) < 0 ? -1 : 1;
      a.setXYZW(C, y.x, y.y, y.z, P);
    }
    for (let C = 0, T = x.length; C < T; ++C) {
      const E = x[C], P = E.start, L = E.count;
      for (let I = P, N = P + L; I < N; I += 3)
        w(e.getX(I + 0)), w(e.getX(I + 1)), w(e.getX(I + 2));
    }
  }
  computeVertexNormals() {
    const e = this.index, t = this.getAttribute("position");
    if (t !== void 0) {
      let i = this.getAttribute("normal");
      if (i === void 0)
        i = new Ht(new Float32Array(t.count * 3), 3), this.setAttribute("normal", i);
      else
        for (let d = 0, f = i.count; d < f; d++)
          i.setXYZ(d, 0, 0, 0);
      const n = new _(), r = new _(), a = new _(), o = new _(), l = new _(), c = new _(), h = new _(), u = new _();
      if (e)
        for (let d = 0, f = e.count; d < f; d += 3) {
          const p = e.getX(d + 0), v = e.getX(d + 1), g = e.getX(d + 2);
          n.fromBufferAttribute(t, p), r.fromBufferAttribute(t, v), a.fromBufferAttribute(t, g), h.subVectors(a, r), u.subVectors(n, r), h.cross(u), o.fromBufferAttribute(i, p), l.fromBufferAttribute(i, v), c.fromBufferAttribute(i, g), o.add(h), l.add(h), c.add(h), i.setXYZ(p, o.x, o.y, o.z), i.setXYZ(v, l.x, l.y, l.z), i.setXYZ(g, c.x, c.y, c.z);
        }
      else
        for (let d = 0, f = t.count; d < f; d += 3)
          n.fromBufferAttribute(t, d + 0), r.fromBufferAttribute(t, d + 1), a.fromBufferAttribute(t, d + 2), h.subVectors(a, r), u.subVectors(n, r), h.cross(u), i.setXYZ(d + 0, h.x, h.y, h.z), i.setXYZ(d + 1, h.x, h.y, h.z), i.setXYZ(d + 2, h.x, h.y, h.z);
      this.normalizeNormals(), i.needsUpdate = !0;
    }
  }
  normalizeNormals() {
    const e = this.attributes.normal;
    for (let t = 0, i = e.count; t < i; t++)
      bs.fromBufferAttribute(e, t), bs.normalize(), e.setXYZ(t, bs.x, bs.y, bs.z);
  }
  toNonIndexed() {
    function e(o, l) {
      const c = o.array, h = o.itemSize, u = o.normalized, d = new c.constructor(l.length * h);
      let f = 0, p = 0;
      for (let v = 0, g = l.length; v < g; v++) {
        o.isInterleavedBufferAttribute ? f = l[v] * o.data.stride + o.offset : f = l[v] * h;
        for (let m = 0; m < h; m++)
          d[p++] = c[f++];
      }
      return new Ht(d, h, u);
    }
    if (this.index === null)
      return console.warn("THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed."), this;
    const t = new qL(), i = this.index.array, n = this.attributes;
    for (const o in n) {
      const l = n[o], c = e(l, i);
      t.setAttribute(o, c);
    }
    const r = this.morphAttributes;
    for (const o in r) {
      const l = [], c = r[o];
      for (let h = 0, u = c.length; h < u; h++) {
        const d = c[h], f = e(d, i);
        l.push(f);
      }
      t.morphAttributes[o] = l;
    }
    t.morphTargetsRelative = this.morphTargetsRelative;
    const a = this.groups;
    for (let o = 0, l = a.length; o < l; o++) {
      const c = a[o];
      t.addGroup(c.start, c.count, c.materialIndex);
    }
    return t;
  }
  toJSON() {
    const e = {
      metadata: {
        version: 4.6,
        type: "BufferGeometry",
        generator: "BufferGeometry.toJSON"
      }
    };
    if (e.uuid = this.uuid, e.type = this.type, this.name !== "" && (e.name = this.name), Object.keys(this.userData).length > 0 && (e.userData = this.userData), this.parameters !== void 0) {
      const l = this.parameters;
      for (const c in l)
        l[c] !== void 0 && (e[c] = l[c]);
      return e;
    }
    e.data = { attributes: {} };
    const t = this.index;
    t !== null && (e.data.index = {
      type: t.array.constructor.name,
      array: Array.prototype.slice.call(t.array)
    });
    const i = this.attributes;
    for (const l in i) {
      const c = i[l];
      e.data.attributes[l] = c.toJSON(e.data);
    }
    const n = {};
    let r = !1;
    for (const l in this.morphAttributes) {
      const c = this.morphAttributes[l], h = [];
      for (let u = 0, d = c.length; u < d; u++) {
        const f = c[u];
        h.push(f.toJSON(e.data));
      }
      h.length > 0 && (n[l] = h, r = !0);
    }
    r && (e.data.morphAttributes = n, e.data.morphTargetsRelative = this.morphTargetsRelative);
    const a = this.groups;
    a.length > 0 && (e.data.groups = JSON.parse(JSON.stringify(a)));
    const o = this.boundingSphere;
    return o !== null && (e.data.boundingSphere = {
      center: o.center.toArray(),
      radius: o.radius
    }), e;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.index = null, this.attributes = {}, this.morphAttributes = {}, this.groups = [], this.boundingBox = null, this.boundingSphere = null;
    const t = {};
    this.name = e.name;
    const i = e.index;
    i !== null && this.setIndex(i.clone(t));
    const n = e.attributes;
    for (const c in n) {
      const h = n[c];
      this.setAttribute(c, h.clone(t));
    }
    const r = e.morphAttributes;
    for (const c in r) {
      const h = [], u = r[c];
      for (let d = 0, f = u.length; d < f; d++)
        h.push(u[d].clone(t));
      this.morphAttributes[c] = h;
    }
    this.morphTargetsRelative = e.morphTargetsRelative;
    const a = e.groups;
    for (let c = 0, h = a.length; c < h; c++) {
      const u = a[c];
      this.addGroup(u.start, u.count, u.materialIndex);
    }
    const o = e.boundingBox;
    o !== null && (this.boundingBox = o.clone());
    const l = e.boundingSphere;
    return l !== null && (this.boundingSphere = l.clone()), this.drawRange.start = e.drawRange.start, this.drawRange.count = e.drawRange.count, this.userData = e.userData, this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
};
const iE = /* @__PURE__ */ new et(), mh = /* @__PURE__ */ new g0(), sv = /* @__PURE__ */ new Tn(), nE = /* @__PURE__ */ new _(), pd = /* @__PURE__ */ new _(), md = /* @__PURE__ */ new _(), gd = /* @__PURE__ */ new _(), F8 = /* @__PURE__ */ new _(), rv = /* @__PURE__ */ new _(), av = /* @__PURE__ */ new Se(), ov = /* @__PURE__ */ new Se(), lv = /* @__PURE__ */ new Se(), sE = /* @__PURE__ */ new _(), rE = /* @__PURE__ */ new _(), aE = /* @__PURE__ */ new _(), cv = /* @__PURE__ */ new _(), hv = /* @__PURE__ */ new _();
let Re = class extends mt {
  constructor(e = new Pt(), t = new kt()) {
    super(), this.isMesh = !0, this.type = "Mesh", this.geometry = e, this.material = t, this.updateMorphTargets();
  }
  copy(e, t) {
    return super.copy(e, t), e.morphTargetInfluences !== void 0 && (this.morphTargetInfluences = e.morphTargetInfluences.slice()), e.morphTargetDictionary !== void 0 && (this.morphTargetDictionary = Object.assign({}, e.morphTargetDictionary)), this.material = Array.isArray(e.material) ? e.material.slice() : e.material, this.geometry = e.geometry, this;
  }
  updateMorphTargets() {
    const t = this.geometry.morphAttributes, i = Object.keys(t);
    if (i.length > 0) {
      const n = t[i[0]];
      if (n !== void 0) {
        this.morphTargetInfluences = [], this.morphTargetDictionary = {};
        for (let r = 0, a = n.length; r < a; r++) {
          const o = n[r].name || String(r);
          this.morphTargetInfluences.push(0), this.morphTargetDictionary[o] = r;
        }
      }
    }
  }
  getVertexPosition(e, t) {
    const i = this.geometry, n = i.attributes.position, r = i.morphAttributes.position, a = i.morphTargetsRelative;
    t.fromBufferAttribute(n, e);
    const o = this.morphTargetInfluences;
    if (r && o) {
      rv.set(0, 0, 0);
      for (let l = 0, c = r.length; l < c; l++) {
        const h = o[l], u = r[l];
        h !== 0 && (F8.fromBufferAttribute(u, e), a ? rv.addScaledVector(F8, h) : rv.addScaledVector(F8.sub(t), h));
      }
      t.add(rv);
    }
    return t;
  }
  raycast(e, t) {
    const i = this.geometry, n = this.material, r = this.matrixWorld;
    n !== void 0 && (i.boundingSphere === null && i.computeBoundingSphere(), sv.copy(i.boundingSphere), sv.applyMatrix4(r), mh.copy(e.ray).recast(e.near), !(sv.containsPoint(mh.origin) === !1 && (mh.intersectSphere(sv, nE) === null || mh.origin.distanceToSquared(nE) > (e.far - e.near) ** 2)) && (iE.copy(r).invert(), mh.copy(e.ray).applyMatrix4(iE), !(i.boundingBox !== null && mh.intersectsBox(i.boundingBox) === !1) && this._computeIntersections(e, t, mh)));
  }
  _computeIntersections(e, t, i) {
    let n;
    const r = this.geometry, a = this.material, o = r.index, l = r.attributes.position, c = r.attributes.uv, h = r.attributes.uv1, u = r.attributes.normal, d = r.groups, f = r.drawRange;
    if (o !== null)
      if (Array.isArray(a))
        for (let p = 0, v = d.length; p < v; p++) {
          const g = d[p], m = a[g.materialIndex], x = Math.max(g.start, f.start), y = Math.min(o.count, Math.min(g.start + g.count, f.start + f.count));
          for (let A = x, b = y; A < b; A += 3) {
            const M = o.getX(A), w = o.getX(A + 1), C = o.getX(A + 2);
            n = uv(this, m, e, i, c, h, u, M, w, C), n && (n.faceIndex = Math.floor(A / 3), n.face.materialIndex = g.materialIndex, t.push(n));
          }
        }
      else {
        const p = Math.max(0, f.start), v = Math.min(o.count, f.start + f.count);
        for (let g = p, m = v; g < m; g += 3) {
          const x = o.getX(g), y = o.getX(g + 1), A = o.getX(g + 2);
          n = uv(this, a, e, i, c, h, u, x, y, A), n && (n.faceIndex = Math.floor(g / 3), t.push(n));
        }
      }
    else if (l !== void 0)
      if (Array.isArray(a))
        for (let p = 0, v = d.length; p < v; p++) {
          const g = d[p], m = a[g.materialIndex], x = Math.max(g.start, f.start), y = Math.min(l.count, Math.min(g.start + g.count, f.start + f.count));
          for (let A = x, b = y; A < b; A += 3) {
            const M = A, w = A + 1, C = A + 2;
            n = uv(this, m, e, i, c, h, u, M, w, C), n && (n.faceIndex = Math.floor(A / 3), n.face.materialIndex = g.materialIndex, t.push(n));
          }
        }
      else {
        const p = Math.max(0, f.start), v = Math.min(l.count, f.start + f.count);
        for (let g = p, m = v; g < m; g += 3) {
          const x = g, y = g + 1, A = g + 2;
          n = uv(this, a, e, i, c, h, u, x, y, A), n && (n.faceIndex = Math.floor(g / 3), t.push(n));
        }
      }
  }
};
function Lk(s, e, t, i, n, r, a, o) {
  let l;
  if (e.side === Ps ? l = i.intersectTriangle(a, r, n, !0, o) : l = i.intersectTriangle(n, r, a, e.side === sa, o), l === null)
    return null;
  hv.copy(o), hv.applyMatrix4(s.matrixWorld);
  const c = t.ray.origin.distanceTo(hv);
  return c < t.near || c > t.far ? null : {
    distance: c,
    point: hv.clone(),
    object: s
  };
}
function uv(s, e, t, i, n, r, a, o, l, c) {
  s.getVertexPosition(o, pd), s.getVertexPosition(l, md), s.getVertexPosition(c, gd);
  const h = Lk(s, e, t, i, pd, md, gd, cv);
  if (h) {
    n && (av.fromBufferAttribute(n, o), ov.fromBufferAttribute(n, l), lv.fromBufferAttribute(n, c), h.uv = Wo.getInterpolation(cv, pd, md, gd, av, ov, lv, new Se())), r && (av.fromBufferAttribute(r, o), ov.fromBufferAttribute(r, l), lv.fromBufferAttribute(r, c), h.uv1 = Wo.getInterpolation(cv, pd, md, gd, av, ov, lv, new Se())), a && (sE.fromBufferAttribute(a, o), rE.fromBufferAttribute(a, l), aE.fromBufferAttribute(a, c), h.normal = Wo.getInterpolation(cv, pd, md, gd, sE, rE, aE, new _()), h.normal.dot(i.direction) > 0 && h.normal.multiplyScalar(-1));
    const u = {
      a: o,
      b: l,
      c,
      normal: new _(),
      materialIndex: 0
    };
    Wo.getNormal(pd, md, gd, u.normal), h.face = u;
  }
  return h;
}
let oi = class YL extends Pt {
  constructor(e = 1, t = 1, i = 1, n = 1, r = 1, a = 1) {
    super(), this.type = "BoxGeometry", this.parameters = {
      width: e,
      height: t,
      depth: i,
      widthSegments: n,
      heightSegments: r,
      depthSegments: a
    };
    const o = this;
    n = Math.floor(n), r = Math.floor(r), a = Math.floor(a);
    const l = [], c = [], h = [], u = [];
    let d = 0, f = 0;
    p("z", "y", "x", -1, -1, i, t, e, a, r, 0), p("z", "y", "x", 1, -1, i, t, -e, a, r, 1), p("x", "z", "y", 1, 1, e, i, t, n, a, 2), p("x", "z", "y", 1, -1, e, i, -t, n, a, 3), p("x", "y", "z", 1, -1, e, t, i, n, r, 4), p("x", "y", "z", -1, -1, e, t, -i, n, r, 5), this.setIndex(l), this.setAttribute("position", new gt(c, 3)), this.setAttribute("normal", new gt(h, 3)), this.setAttribute("uv", new gt(u, 2));
    function p(v, g, m, x, y, A, b, M, w, C, T) {
      const E = A / w, P = b / C, L = A / 2, I = b / 2, N = M / 2, H = w + 1, U = C + 1;
      let W = 0, X = 0;
      const K = new _();
      for (let te = 0; te < U; te++) {
        const re = te * P - I;
        for (let be = 0; be < H; be++) {
          const Pe = be * E - L;
          K[v] = Pe * x, K[g] = re * y, K[m] = N, c.push(K.x, K.y, K.z), K[v] = 0, K[g] = 0, K[m] = M > 0 ? 1 : -1, h.push(K.x, K.y, K.z), u.push(be / w), u.push(1 - te / C), W += 1;
        }
      }
      for (let te = 0; te < C; te++)
        for (let re = 0; re < w; re++) {
          const be = d + re + H * te, Pe = d + re + H * (te + 1), J = d + (re + 1) + H * (te + 1), fe = d + (re + 1) + H * te;
          l.push(be, Pe, fe), l.push(Pe, J, fe), X += 6;
        }
      o.addGroup(f, X, T), f += X, d += W;
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new YL(e.width, e.height, e.depth, e.widthSegments, e.heightSegments, e.depthSegments);
  }
};
function sp(s) {
  const e = {};
  for (const t in s) {
    e[t] = {};
    for (const i in s[t]) {
      const n = s[t][i];
      n && (n.isColor || n.isMatrix3 || n.isMatrix4 || n.isVector2 || n.isVector3 || n.isVector4 || n.isTexture || n.isQuaternion) ? n.isRenderTargetTexture ? (console.warn("UniformsUtils: Textures of render targets cannot be cloned via cloneUniforms() or mergeUniforms()."), e[t][i] = null) : e[t][i] = n.clone() : Array.isArray(n) ? e[t][i] = n.slice() : e[t][i] = n;
    }
  }
  return e;
}
function dr(s) {
  const e = {};
  for (let t = 0; t < s.length; t++) {
    const i = sp(s[t]);
    for (const n in i)
      e[n] = i[n];
  }
  return e;
}
function Ik(s) {
  const e = [];
  for (let t = 0; t < s.length; t++)
    e.push(s[t].clone());
  return e;
}
function ZL(s) {
  const e = s.getRenderTarget();
  return e === null ? s.outputColorSpace : e.isXRRenderTarget === !0 ? e.texture.colorSpace : bi.workingColorSpace;
}
const rs = { clone: sp, merge: dr };
var Ok = `void main() {
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}`, Nk = `void main() {
	gl_FragColor = vec4( 1.0, 0.0, 0.0, 1.0 );
}`;
let Jt = class extends Cn {
  constructor(e) {
    super(), this.isShaderMaterial = !0, this.type = "ShaderMaterial", this.defines = {}, this.uniforms = {}, this.uniformsGroups = [], this.vertexShader = Ok, this.fragmentShader = Nk, this.linewidth = 1, this.wireframe = !1, this.wireframeLinewidth = 1, this.fog = !1, this.lights = !1, this.clipping = !1, this.forceSinglePass = !0, this.extensions = {
      clipCullDistance: !1,
      // set to use vertex shader clipping
      multiDraw: !1
      // set to use vertex shader multi_draw / enable gl_DrawID
    }, this.defaultAttributeValues = {
      color: [1, 1, 1],
      uv: [0, 0],
      uv1: [0, 0]
    }, this.index0AttributeName = void 0, this.uniformsNeedUpdate = !1, this.glslVersion = null, e !== void 0 && this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.fragmentShader = e.fragmentShader, this.vertexShader = e.vertexShader, this.uniforms = sp(e.uniforms), this.uniformsGroups = Ik(e.uniformsGroups), this.defines = Object.assign({}, e.defines), this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.fog = e.fog, this.lights = e.lights, this.clipping = e.clipping, this.extensions = Object.assign({}, e.extensions), this.glslVersion = e.glslVersion, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    t.glslVersion = this.glslVersion, t.uniforms = {};
    for (const n in this.uniforms) {
      const a = this.uniforms[n].value;
      a && a.isTexture ? t.uniforms[n] = {
        type: "t",
        value: a.toJSON(e).uuid
      } : a && a.isColor ? t.uniforms[n] = {
        type: "c",
        value: a.getHex()
      } : a && a.isVector2 ? t.uniforms[n] = {
        type: "v2",
        value: a.toArray()
      } : a && a.isVector3 ? t.uniforms[n] = {
        type: "v3",
        value: a.toArray()
      } : a && a.isVector4 ? t.uniforms[n] = {
        type: "v4",
        value: a.toArray()
      } : a && a.isMatrix3 ? t.uniforms[n] = {
        type: "m3",
        value: a.toArray()
      } : a && a.isMatrix4 ? t.uniforms[n] = {
        type: "m4",
        value: a.toArray()
      } : t.uniforms[n] = {
        value: a
      };
    }
    Object.keys(this.defines).length > 0 && (t.defines = this.defines), t.vertexShader = this.vertexShader, t.fragmentShader = this.fragmentShader, t.lights = this.lights, t.clipping = this.clipping;
    const i = {};
    for (const n in this.extensions)
      this.extensions[n] === !0 && (i[n] = !0);
    return Object.keys(i).length > 0 && (t.extensions = i), t;
  }
}, s2 = class extends mt {
  constructor() {
    super(), this.isCamera = !0, this.type = "Camera", this.matrixWorldInverse = new et(), this.projectionMatrix = new et(), this.projectionMatrixInverse = new et(), this.coordinateSystem = Vo;
  }
  copy(e, t) {
    return super.copy(e, t), this.matrixWorldInverse.copy(e.matrixWorldInverse), this.projectionMatrix.copy(e.projectionMatrix), this.projectionMatrixInverse.copy(e.projectionMatrixInverse), this.coordinateSystem = e.coordinateSystem, this;
  }
  getWorldDirection(e) {
    return super.getWorldDirection(e).negate();
  }
  updateMatrixWorld(e) {
    super.updateMatrixWorld(e), this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  updateWorldMatrix(e, t) {
    super.updateWorldMatrix(e, t), this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  clone() {
    return new this.constructor().copy(this);
  }
};
const zl = /* @__PURE__ */ new _(), oE = /* @__PURE__ */ new Se(), lE = /* @__PURE__ */ new Se();
let Zt = class extends s2 {
  constructor(e = 50, t = 1, i = 0.1, n = 2e3) {
    super(), this.isPerspectiveCamera = !0, this.type = "PerspectiveCamera", this.fov = e, this.zoom = 1, this.near = i, this.far = n, this.focus = 10, this.aspect = t, this.view = null, this.filmGauge = 35, this.filmOffset = 0, this.updateProjectionMatrix();
  }
  copy(e, t) {
    return super.copy(e, t), this.fov = e.fov, this.zoom = e.zoom, this.near = e.near, this.far = e.far, this.focus = e.focus, this.aspect = e.aspect, this.view = e.view === null ? null : Object.assign({}, e.view), this.filmGauge = e.filmGauge, this.filmOffset = e.filmOffset, this;
  }
  /**
   * Sets the FOV by focal length in respect to the current .filmGauge.
   *
   * The default film gauge is 35, so that the focal length can be specified for
   * a 35mm (full frame) camera.
   *
   * Values for focal length and film gauge must have the same unit.
   */
  setFocalLength(e) {
    const t = 0.5 * this.getFilmHeight() / e;
    this.fov = np * 2 * Math.atan(t), this.updateProjectionMatrix();
  }
  /**
   * Calculates the focal length from the current .fov and .filmGauge.
   */
  getFocalLength() {
    const e = Math.tan(bu * 0.5 * this.fov);
    return 0.5 * this.getFilmHeight() / e;
  }
  getEffectiveFOV() {
    return np * 2 * Math.atan(
      Math.tan(bu * 0.5 * this.fov) / this.zoom
    );
  }
  getFilmWidth() {
    return this.filmGauge * Math.min(this.aspect, 1);
  }
  getFilmHeight() {
    return this.filmGauge / Math.max(this.aspect, 1);
  }
  /**
   * Computes the 2D bounds of the camera's viewable rectangle at a given distance along the viewing direction.
   * Sets minTarget and maxTarget to the coordinates of the lower-left and upper-right corners of the view rectangle.
   */
  getViewBounds(e, t, i) {
    zl.set(-1, -1, 0.5).applyMatrix4(this.projectionMatrixInverse), t.set(zl.x, zl.y).multiplyScalar(-e / zl.z), zl.set(1, 1, 0.5).applyMatrix4(this.projectionMatrixInverse), i.set(zl.x, zl.y).multiplyScalar(-e / zl.z);
  }
  /**
   * Computes the width and height of the camera's viewable rectangle at a given distance along the viewing direction.
   * Copies the result into the target Vector2, where x is width and y is height.
   */
  getViewSize(e, t) {
    return this.getViewBounds(e, oE, lE), t.subVectors(lE, oE);
  }
  /**
   * Sets an offset in a larger frustum. This is useful for multi-window or
   * multi-monitor/multi-machine setups.
   *
   * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
   * the monitors are in grid like this
   *
   *   +---+---+---+
   *   | A | B | C |
   *   +---+---+---+
   *   | D | E | F |
   *   +---+---+---+
   *
   * then for each monitor you would call it like this
   *
   *   const w = 1920;
   *   const h = 1080;
   *   const fullWidth = w * 3;
   *   const fullHeight = h * 2;
   *
   *   --A--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
   *   --B--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
   *   --C--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
   *   --D--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
   *   --E--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
   *   --F--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
   *
   *   Note there is no reason monitors have to be the same size or in a grid.
   */
  setViewOffset(e, t, i, n, r, a) {
    this.aspect = e / t, this.view === null && (this.view = {
      enabled: !0,
      fullWidth: 1,
      fullHeight: 1,
      offsetX: 0,
      offsetY: 0,
      width: 1,
      height: 1
    }), this.view.enabled = !0, this.view.fullWidth = e, this.view.fullHeight = t, this.view.offsetX = i, this.view.offsetY = n, this.view.width = r, this.view.height = a, this.updateProjectionMatrix();
  }
  clearViewOffset() {
    this.view !== null && (this.view.enabled = !1), this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const e = this.near;
    let t = e * Math.tan(bu * 0.5 * this.fov) / this.zoom, i = 2 * t, n = this.aspect * i, r = -0.5 * n;
    const a = this.view;
    if (this.view !== null && this.view.enabled) {
      const l = a.fullWidth, c = a.fullHeight;
      r += a.offsetX * n / l, t -= a.offsetY * i / c, n *= a.width / l, i *= a.height / c;
    }
    const o = this.filmOffset;
    o !== 0 && (r += e * o / this.getFilmWidth()), this.projectionMatrix.makePerspective(r, r + n, t, t - i, e, this.far, this.coordinateSystem), this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.fov = this.fov, t.object.zoom = this.zoom, t.object.near = this.near, t.object.far = this.far, t.object.focus = this.focus, t.object.aspect = this.aspect, this.view !== null && (t.object.view = Object.assign({}, this.view)), t.object.filmGauge = this.filmGauge, t.object.filmOffset = this.filmOffset, t;
  }
};
const vd = -90, yd = 1;
let UM = class extends mt {
  constructor(e, t, i) {
    super(), this.type = "CubeCamera", this.renderTarget = i, this.coordinateSystem = null, this.activeMipmapLevel = 0;
    const n = new Zt(vd, yd, e, t);
    n.layers = this.layers, this.add(n);
    const r = new Zt(vd, yd, e, t);
    r.layers = this.layers, this.add(r);
    const a = new Zt(vd, yd, e, t);
    a.layers = this.layers, this.add(a);
    const o = new Zt(vd, yd, e, t);
    o.layers = this.layers, this.add(o);
    const l = new Zt(vd, yd, e, t);
    l.layers = this.layers, this.add(l);
    const c = new Zt(vd, yd, e, t);
    c.layers = this.layers, this.add(c);
  }
  updateCoordinateSystem() {
    const e = this.coordinateSystem, t = this.children.concat(), [i, n, r, a, o, l] = t;
    for (const c of t)
      this.remove(c);
    if (e === Vo)
      i.up.set(0, 1, 0), i.lookAt(1, 0, 0), n.up.set(0, 1, 0), n.lookAt(-1, 0, 0), r.up.set(0, 0, -1), r.lookAt(0, 1, 0), a.up.set(0, 0, 1), a.lookAt(0, -1, 0), o.up.set(0, 1, 0), o.lookAt(0, 0, 1), l.up.set(0, 1, 0), l.lookAt(0, 0, -1);
    else if (e === Q1)
      i.up.set(0, -1, 0), i.lookAt(-1, 0, 0), n.up.set(0, -1, 0), n.lookAt(1, 0, 0), r.up.set(0, 0, 1), r.lookAt(0, 1, 0), a.up.set(0, 0, -1), a.lookAt(0, -1, 0), o.up.set(0, -1, 0), o.lookAt(0, 0, 1), l.up.set(0, -1, 0), l.lookAt(0, 0, -1);
    else
      throw new Error("THREE.CubeCamera.updateCoordinateSystem(): Invalid coordinate system: " + e);
    for (const c of t)
      this.add(c), c.updateMatrixWorld();
  }
  update(e, t) {
    this.parent === null && this.updateMatrixWorld();
    const { renderTarget: i, activeMipmapLevel: n } = this;
    this.coordinateSystem !== e.coordinateSystem && (this.coordinateSystem = e.coordinateSystem, this.updateCoordinateSystem());
    const [r, a, o, l, c, h] = this.children, u = e.getRenderTarget(), d = e.getActiveCubeFace(), f = e.getActiveMipmapLevel(), p = e.xr.enabled;
    e.xr.enabled = !1;
    const v = i.texture.generateMipmaps;
    i.texture.generateMipmaps = !1, e.setRenderTarget(i, 0, n), e.render(t, r), e.setRenderTarget(i, 1, n), e.render(t, a), e.setRenderTarget(i, 2, n), e.render(t, o), e.setRenderTarget(i, 3, n), e.render(t, l), e.setRenderTarget(i, 4, n), e.render(t, c), i.texture.generateMipmaps = v, e.setRenderTarget(i, 5, n), e.render(t, h), e.setRenderTarget(u, d, f), e.xr.enabled = p, i.texture.needsPMREMUpdate = !0;
  }
}, Vp = class extends Ut {
  constructor(e, t, i, n, r, a, o, l, c, h) {
    e = e !== void 0 ? e : [], t = t !== void 0 ? t : lo, super(e, t, i, n, r, a, o, l, c, h), this.isCubeTexture = !0, this.flipY = !1;
  }
  get images() {
    return this.image;
  }
  set images(e) {
    this.image = e;
  }
}, zM = class extends Xi {
  constructor(e = 1, t = {}) {
    super(e, e, t), this.isWebGLCubeRenderTarget = !0;
    const i = { width: e, height: e, depth: 1 }, n = [i, i, i, i, i, i];
    this.texture = new Vp(n, t.mapping, t.wrapS, t.wrapT, t.magFilter, t.minFilter, t.format, t.type, t.anisotropy, t.colorSpace), this.texture.isRenderTargetTexture = !0, this.texture.generateMipmaps = t.generateMipmaps !== void 0 ? t.generateMipmaps : !1, this.texture.minFilter = t.minFilter !== void 0 ? t.minFilter : ii;
  }
  fromEquirectangularTexture(e, t) {
    this.texture.type = t.type, this.texture.colorSpace = t.colorSpace, this.texture.generateMipmaps = t.generateMipmaps, this.texture.minFilter = t.minFilter, this.texture.magFilter = t.magFilter;
    const i = {
      uniforms: {
        tEquirect: { value: null }
      },
      vertexShader: (
        /* glsl */
        `

				varying vec3 vWorldDirection;

				vec3 transformDirection( in vec3 dir, in mat4 matrix ) {

					return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );

				}

				void main() {

					vWorldDirection = transformDirection( position, modelMatrix );

					#include <begin_vertex>
					#include <project_vertex>

				}
			`
      ),
      fragmentShader: (
        /* glsl */
        `

				uniform sampler2D tEquirect;

				varying vec3 vWorldDirection;

				#include <common>

				void main() {

					vec3 direction = normalize( vWorldDirection );

					vec2 sampleUV = equirectUv( direction );

					gl_FragColor = texture2D( tEquirect, sampleUV );

				}
			`
      )
    }, n = new oi(5, 5, 5), r = new Jt({
      name: "CubemapFromEquirect",
      uniforms: sp(i.uniforms),
      vertexShader: i.vertexShader,
      fragmentShader: i.fragmentShader,
      side: Ps,
      blending: vn
    });
    r.uniforms.tEquirect.value = t;
    const a = new Re(n, r), o = t.minFilter;
    return t.minFilter === ds && (t.minFilter = ii), new UM(1, 10, this).update(e, a), t.minFilter = o, a.geometry.dispose(), a.material.dispose(), this;
  }
  clear(e, t, i, n) {
    const r = e.getRenderTarget();
    for (let a = 0; a < 6; a++)
      e.setRenderTarget(this, a), e.clear(t, i, n);
    e.setRenderTarget(r);
  }
};
const U8 = /* @__PURE__ */ new _(), Bk = /* @__PURE__ */ new _(), Fk = /* @__PURE__ */ new Gt();
let ln = class {
  constructor(e = new _(1, 0, 0), t = 0) {
    this.isPlane = !0, this.normal = e, this.constant = t;
  }
  set(e, t) {
    return this.normal.copy(e), this.constant = t, this;
  }
  setComponents(e, t, i, n) {
    return this.normal.set(e, t, i), this.constant = n, this;
  }
  setFromNormalAndCoplanarPoint(e, t) {
    return this.normal.copy(e), this.constant = -t.dot(this.normal), this;
  }
  setFromCoplanarPoints(e, t, i) {
    const n = U8.subVectors(i, t).cross(Bk.subVectors(e, t)).normalize();
    return this.setFromNormalAndCoplanarPoint(n, e), this;
  }
  copy(e) {
    return this.normal.copy(e.normal), this.constant = e.constant, this;
  }
  normalize() {
    const e = 1 / this.normal.length();
    return this.normal.multiplyScalar(e), this.constant *= e, this;
  }
  negate() {
    return this.constant *= -1, this.normal.negate(), this;
  }
  distanceToPoint(e) {
    return this.normal.dot(e) + this.constant;
  }
  distanceToSphere(e) {
    return this.distanceToPoint(e.center) - e.radius;
  }
  projectPoint(e, t) {
    return t.copy(e).addScaledVector(this.normal, -this.distanceToPoint(e));
  }
  intersectLine(e, t) {
    const i = e.delta(U8), n = this.normal.dot(i);
    if (n === 0)
      return this.distanceToPoint(e.start) === 0 ? t.copy(e.start) : null;
    const r = -(e.start.dot(this.normal) + this.constant) / n;
    return r < 0 || r > 1 ? null : t.copy(e.start).addScaledVector(i, r);
  }
  intersectsLine(e) {
    const t = this.distanceToPoint(e.start), i = this.distanceToPoint(e.end);
    return t < 0 && i > 0 || i < 0 && t > 0;
  }
  intersectsBox(e) {
    return e.intersectsPlane(this);
  }
  intersectsSphere(e) {
    return e.intersectsPlane(this);
  }
  coplanarPoint(e) {
    return e.copy(this.normal).multiplyScalar(-this.constant);
  }
  applyMatrix4(e, t) {
    const i = t || Fk.getNormalMatrix(e), n = this.coplanarPoint(U8).applyMatrix4(e), r = this.normal.applyMatrix3(i).normalize();
    return this.constant = -n.dot(r), this;
  }
  translate(e) {
    return this.constant -= e.dot(this.normal), this;
  }
  equals(e) {
    return e.normal.equals(this.normal) && e.constant === this.constant;
  }
  clone() {
    return new this.constructor().copy(this);
  }
};
const gh = /* @__PURE__ */ new Tn(), dv = /* @__PURE__ */ new _();
let th = class {
  constructor(e = new ln(), t = new ln(), i = new ln(), n = new ln(), r = new ln(), a = new ln()) {
    this.planes = [e, t, i, n, r, a];
  }
  set(e, t, i, n, r, a) {
    const o = this.planes;
    return o[0].copy(e), o[1].copy(t), o[2].copy(i), o[3].copy(n), o[4].copy(r), o[5].copy(a), this;
  }
  copy(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++)
      t[i].copy(e.planes[i]);
    return this;
  }
  setFromProjectionMatrix(e, t = Vo) {
    const i = this.planes, n = e.elements, r = n[0], a = n[1], o = n[2], l = n[3], c = n[4], h = n[5], u = n[6], d = n[7], f = n[8], p = n[9], v = n[10], g = n[11], m = n[12], x = n[13], y = n[14], A = n[15];
    if (i[0].setComponents(l - r, d - c, g - f, A - m).normalize(), i[1].setComponents(l + r, d + c, g + f, A + m).normalize(), i[2].setComponents(l + a, d + h, g + p, A + x).normalize(), i[3].setComponents(l - a, d - h, g - p, A - x).normalize(), i[4].setComponents(l - o, d - u, g - v, A - y).normalize(), t === Vo)
      i[5].setComponents(l + o, d + u, g + v, A + y).normalize();
    else if (t === Q1)
      i[5].setComponents(o, u, v, y).normalize();
    else
      throw new Error("THREE.Frustum.setFromProjectionMatrix(): Invalid coordinate system: " + t);
    return this;
  }
  intersectsObject(e) {
    if (e.boundingSphere !== void 0)
      e.boundingSphere === null && e.computeBoundingSphere(), gh.copy(e.boundingSphere).applyMatrix4(e.matrixWorld);
    else {
      const t = e.geometry;
      t.boundingSphere === null && t.computeBoundingSphere(), gh.copy(t.boundingSphere).applyMatrix4(e.matrixWorld);
    }
    return this.intersectsSphere(gh);
  }
  intersectsSprite(e) {
    return gh.center.set(0, 0, 0), gh.radius = 0.7071067811865476, gh.applyMatrix4(e.matrixWorld), this.intersectsSphere(gh);
  }
  intersectsSphere(e) {
    const t = this.planes, i = e.center, n = -e.radius;
    for (let r = 0; r < 6; r++)
      if (t[r].distanceToPoint(i) < n)
        return !1;
    return !0;
  }
  intersectsBox(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++) {
      const n = t[i];
      if (dv.x = n.normal.x > 0 ? e.max.x : e.min.x, dv.y = n.normal.y > 0 ? e.max.y : e.min.y, dv.z = n.normal.z > 0 ? e.max.z : e.min.z, n.distanceToPoint(dv) < 0)
        return !1;
    }
    return !0;
  }
  containsPoint(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++)
      if (t[i].distanceToPoint(e) < 0)
        return !1;
    return !0;
  }
  clone() {
    return new this.constructor().copy(this);
  }
};
function KL() {
  let s = null, e = !1, t = null, i = null;
  function n(r, a) {
    t(r, a), i = s.requestAnimationFrame(n);
  }
  return {
    start: function() {
      e !== !0 && t !== null && (i = s.requestAnimationFrame(n), e = !0);
    },
    stop: function() {
      s.cancelAnimationFrame(i), e = !1;
    },
    setAnimationLoop: function(r) {
      t = r;
    },
    setContext: function(r) {
      s = r;
    }
  };
}
function Uk(s) {
  const e = /* @__PURE__ */ new WeakMap();
  function t(o, l) {
    const c = o.array, h = o.usage, u = c.byteLength, d = s.createBuffer();
    s.bindBuffer(l, d), s.bufferData(l, c, h), o.onUploadCallback();
    let f;
    if (c instanceof Float32Array)
      f = s.FLOAT;
    else if (c instanceof Uint16Array)
      o.isFloat16BufferAttribute ? f = s.HALF_FLOAT : f = s.UNSIGNED_SHORT;
    else if (c instanceof Int16Array)
      f = s.SHORT;
    else if (c instanceof Uint32Array)
      f = s.UNSIGNED_INT;
    else if (c instanceof Int32Array)
      f = s.INT;
    else if (c instanceof Int8Array)
      f = s.BYTE;
    else if (c instanceof Uint8Array)
      f = s.UNSIGNED_BYTE;
    else if (c instanceof Uint8ClampedArray)
      f = s.UNSIGNED_BYTE;
    else
      throw new Error("THREE.WebGLAttributes: Unsupported buffer data format: " + c);
    return {
      buffer: d,
      type: f,
      bytesPerElement: c.BYTES_PER_ELEMENT,
      version: o.version,
      size: u
    };
  }
  function i(o, l, c) {
    const h = l.array, u = l._updateRange, d = l.updateRanges;
    if (s.bindBuffer(c, o), u.count === -1 && d.length === 0 && s.bufferSubData(c, 0, h), d.length !== 0) {
      for (let f = 0, p = d.length; f < p; f++) {
        const v = d[f];
        s.bufferSubData(
          c,
          v.start * h.BYTES_PER_ELEMENT,
          h,
          v.start,
          v.count
        );
      }
      l.clearUpdateRanges();
    }
    u.count !== -1 && (s.bufferSubData(
      c,
      u.offset * h.BYTES_PER_ELEMENT,
      h,
      u.offset,
      u.count
    ), u.count = -1), l.onUploadCallback();
  }
  function n(o) {
    return o.isInterleavedBufferAttribute && (o = o.data), e.get(o);
  }
  function r(o) {
    o.isInterleavedBufferAttribute && (o = o.data);
    const l = e.get(o);
    l && (s.deleteBuffer(l.buffer), e.delete(o));
  }
  function a(o, l) {
    if (o.isGLBufferAttribute) {
      const h = e.get(o);
      (!h || h.version < o.version) && e.set(o, {
        buffer: o.buffer,
        type: o.type,
        bytesPerElement: o.elementSize,
        version: o.version
      });
      return;
    }
    o.isInterleavedBufferAttribute && (o = o.data);
    const c = e.get(o);
    if (c === void 0)
      e.set(o, t(o, l));
    else if (c.version < o.version) {
      if (c.size !== o.array.byteLength)
        throw new Error("THREE.WebGLAttributes: The size of the buffer attribute's array buffer does not match the original size. Resizing buffer attributes is not supported.");
      i(c.buffer, o, l), c.version = o.version;
    }
  }
  return {
    get: n,
    remove: r,
    update: a
  };
}
let $n = class JL extends Pt {
  constructor(e = 1, t = 1, i = 1, n = 1) {
    super(), this.type = "PlaneGeometry", this.parameters = {
      width: e,
      height: t,
      widthSegments: i,
      heightSegments: n
    };
    const r = e / 2, a = t / 2, o = Math.floor(i), l = Math.floor(n), c = o + 1, h = l + 1, u = e / o, d = t / l, f = [], p = [], v = [], g = [];
    for (let m = 0; m < h; m++) {
      const x = m * d - a;
      for (let y = 0; y < c; y++) {
        const A = y * u - r;
        p.push(A, -x, 0), v.push(0, 0, 1), g.push(y / o), g.push(1 - m / l);
      }
    }
    for (let m = 0; m < l; m++)
      for (let x = 0; x < o; x++) {
        const y = x + c * m, A = x + c * (m + 1), b = x + 1 + c * (m + 1), M = x + 1 + c * m;
        f.push(y, A, M), f.push(A, b, M);
      }
    this.setIndex(f), this.setAttribute("position", new gt(p, 3)), this.setAttribute("normal", new gt(v, 3)), this.setAttribute("uv", new gt(g, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new JL(e.width, e.height, e.widthSegments, e.heightSegments);
  }
};
var zk = `#ifdef USE_ALPHAHASH
	if ( diffuseColor.a < getAlphaHashThreshold( vPosition ) ) discard;
#endif`, kk = `#ifdef USE_ALPHAHASH
	const float ALPHA_HASH_SCALE = 0.05;
	float hash2D( vec2 value ) {
		return fract( 1.0e4 * sin( 17.0 * value.x + 0.1 * value.y ) * ( 0.1 + abs( sin( 13.0 * value.y + value.x ) ) ) );
	}
	float hash3D( vec3 value ) {
		return hash2D( vec2( hash2D( value.xy ), value.z ) );
	}
	float getAlphaHashThreshold( vec3 position ) {
		float maxDeriv = max(
			length( dFdx( position.xyz ) ),
			length( dFdy( position.xyz ) )
		);
		float pixScale = 1.0 / ( ALPHA_HASH_SCALE * maxDeriv );
		vec2 pixScales = vec2(
			exp2( floor( log2( pixScale ) ) ),
			exp2( ceil( log2( pixScale ) ) )
		);
		vec2 alpha = vec2(
			hash3D( floor( pixScales.x * position.xyz ) ),
			hash3D( floor( pixScales.y * position.xyz ) )
		);
		float lerpFactor = fract( log2( pixScale ) );
		float x = ( 1.0 - lerpFactor ) * alpha.x + lerpFactor * alpha.y;
		float a = min( lerpFactor, 1.0 - lerpFactor );
		vec3 cases = vec3(
			x * x / ( 2.0 * a * ( 1.0 - a ) ),
			( x - 0.5 * a ) / ( 1.0 - a ),
			1.0 - ( ( 1.0 - x ) * ( 1.0 - x ) / ( 2.0 * a * ( 1.0 - a ) ) )
		);
		float threshold = ( x < ( 1.0 - a ) )
			? ( ( x < a ) ? cases.x : cases.y )
			: cases.z;
		return clamp( threshold , 1.0e-6, 1.0 );
	}
#endif`, Gk = `#ifdef USE_ALPHAMAP
	diffuseColor.a *= texture2D( alphaMap, vAlphaMapUv ).g;
#endif`, Hk = `#ifdef USE_ALPHAMAP
	uniform sampler2D alphaMap;
#endif`, Vk = `#ifdef USE_ALPHATEST
	#ifdef ALPHA_TO_COVERAGE
	diffuseColor.a = smoothstep( alphaTest, alphaTest + fwidth( diffuseColor.a ), diffuseColor.a );
	if ( diffuseColor.a == 0.0 ) discard;
	#else
	if ( diffuseColor.a < alphaTest ) discard;
	#endif
#endif`, Wk = `#ifdef USE_ALPHATEST
	uniform float alphaTest;
#endif`, Xk = `#ifdef USE_AOMAP
	float ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;
	reflectedLight.indirectDiffuse *= ambientOcclusion;
	#if defined( USE_CLEARCOAT ) 
		clearcoatSpecularIndirect *= ambientOcclusion;
	#endif
	#if defined( USE_SHEEN ) 
		sheenSpecularIndirect *= ambientOcclusion;
	#endif
	#if defined( USE_ENVMAP ) && defined( STANDARD )
		float dotNV = saturate( dot( geometryNormal, geometryViewDir ) );
		reflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV, ambientOcclusion, material.roughness );
	#endif
#endif`, jk = `#ifdef USE_AOMAP
	uniform sampler2D aoMap;
	uniform float aoMapIntensity;
#endif`, qk = `#ifdef USE_BATCHING
	#if ! defined( GL_ANGLE_multi_draw )
	#define gl_DrawID _gl_DrawID
	uniform int _gl_DrawID;
	#endif
	uniform highp sampler2D batchingTexture;
	uniform highp usampler2D batchingIdTexture;
	mat4 getBatchingMatrix( const in float i ) {
		int size = textureSize( batchingTexture, 0 ).x;
		int j = int( i ) * 4;
		int x = j % size;
		int y = j / size;
		vec4 v1 = texelFetch( batchingTexture, ivec2( x, y ), 0 );
		vec4 v2 = texelFetch( batchingTexture, ivec2( x + 1, y ), 0 );
		vec4 v3 = texelFetch( batchingTexture, ivec2( x + 2, y ), 0 );
		vec4 v4 = texelFetch( batchingTexture, ivec2( x + 3, y ), 0 );
		return mat4( v1, v2, v3, v4 );
	}
	float getIndirectIndex( const in int i ) {
		int size = textureSize( batchingIdTexture, 0 ).x;
		int x = i % size;
		int y = i / size;
		return float( texelFetch( batchingIdTexture, ivec2( x, y ), 0 ).r );
	}
#endif
#ifdef USE_BATCHING_COLOR
	uniform sampler2D batchingColorTexture;
	vec3 getBatchingColor( const in float i ) {
		int size = textureSize( batchingColorTexture, 0 ).x;
		int j = int( i );
		int x = j % size;
		int y = j / size;
		return texelFetch( batchingColorTexture, ivec2( x, y ), 0 ).rgb;
	}
#endif`, Yk = `#ifdef USE_BATCHING
	mat4 batchingMatrix = getBatchingMatrix( getIndirectIndex( gl_DrawID ) );
#endif`, Zk = `vec3 transformed = vec3( position );
#ifdef USE_ALPHAHASH
	vPosition = vec3( position );
#endif`, Kk = `vec3 objectNormal = vec3( normal );
#ifdef USE_TANGENT
	vec3 objectTangent = vec3( tangent.xyz );
#endif`, Jk = `float G_BlinnPhong_Implicit( ) {
	return 0.25;
}
float D_BlinnPhong( const in float shininess, const in float dotNH ) {
	return RECIPROCAL_PI * ( shininess * 0.5 + 1.0 ) * pow( dotNH, shininess );
}
vec3 BRDF_BlinnPhong( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 specularColor, const in float shininess ) {
	vec3 halfDir = normalize( lightDir + viewDir );
	float dotNH = saturate( dot( normal, halfDir ) );
	float dotVH = saturate( dot( viewDir, halfDir ) );
	vec3 F = F_Schlick( specularColor, 1.0, dotVH );
	float G = G_BlinnPhong_Implicit( );
	float D = D_BlinnPhong( shininess, dotNH );
	return F * ( G * D );
} // validated`, Qk = `#ifdef USE_IRIDESCENCE
	const mat3 XYZ_TO_REC709 = mat3(
		 3.2404542, -0.9692660,  0.0556434,
		-1.5371385,  1.8760108, -0.2040259,
		-0.4985314,  0.0415560,  1.0572252
	);
	vec3 Fresnel0ToIor( vec3 fresnel0 ) {
		vec3 sqrtF0 = sqrt( fresnel0 );
		return ( vec3( 1.0 ) + sqrtF0 ) / ( vec3( 1.0 ) - sqrtF0 );
	}
	vec3 IorToFresnel0( vec3 transmittedIor, float incidentIor ) {
		return pow2( ( transmittedIor - vec3( incidentIor ) ) / ( transmittedIor + vec3( incidentIor ) ) );
	}
	float IorToFresnel0( float transmittedIor, float incidentIor ) {
		return pow2( ( transmittedIor - incidentIor ) / ( transmittedIor + incidentIor ));
	}
	vec3 evalSensitivity( float OPD, vec3 shift ) {
		float phase = 2.0 * PI * OPD * 1.0e-9;
		vec3 val = vec3( 5.4856e-13, 4.4201e-13, 5.2481e-13 );
		vec3 pos = vec3( 1.6810e+06, 1.7953e+06, 2.2084e+06 );
		vec3 var = vec3( 4.3278e+09, 9.3046e+09, 6.6121e+09 );
		vec3 xyz = val * sqrt( 2.0 * PI * var ) * cos( pos * phase + shift ) * exp( - pow2( phase ) * var );
		xyz.x += 9.7470e-14 * sqrt( 2.0 * PI * 4.5282e+09 ) * cos( 2.2399e+06 * phase + shift[ 0 ] ) * exp( - 4.5282e+09 * pow2( phase ) );
		xyz /= 1.0685e-7;
		vec3 rgb = XYZ_TO_REC709 * xyz;
		return rgb;
	}
	vec3 evalIridescence( float outsideIOR, float eta2, float cosTheta1, float thinFilmThickness, vec3 baseF0 ) {
		vec3 I;
		float iridescenceIOR = mix( outsideIOR, eta2, smoothstep( 0.0, 0.03, thinFilmThickness ) );
		float sinTheta2Sq = pow2( outsideIOR / iridescenceIOR ) * ( 1.0 - pow2( cosTheta1 ) );
		float cosTheta2Sq = 1.0 - sinTheta2Sq;
		if ( cosTheta2Sq < 0.0 ) {
			return vec3( 1.0 );
		}
		float cosTheta2 = sqrt( cosTheta2Sq );
		float R0 = IorToFresnel0( iridescenceIOR, outsideIOR );
		float R12 = F_Schlick( R0, 1.0, cosTheta1 );
		float T121 = 1.0 - R12;
		float phi12 = 0.0;
		if ( iridescenceIOR < outsideIOR ) phi12 = PI;
		float phi21 = PI - phi12;
		vec3 baseIOR = Fresnel0ToIor( clamp( baseF0, 0.0, 0.9999 ) );		vec3 R1 = IorToFresnel0( baseIOR, iridescenceIOR );
		vec3 R23 = F_Schlick( R1, 1.0, cosTheta2 );
		vec3 phi23 = vec3( 0.0 );
		if ( baseIOR[ 0 ] < iridescenceIOR ) phi23[ 0 ] = PI;
		if ( baseIOR[ 1 ] < iridescenceIOR ) phi23[ 1 ] = PI;
		if ( baseIOR[ 2 ] < iridescenceIOR ) phi23[ 2 ] = PI;
		float OPD = 2.0 * iridescenceIOR * thinFilmThickness * cosTheta2;
		vec3 phi = vec3( phi21 ) + phi23;
		vec3 R123 = clamp( R12 * R23, 1e-5, 0.9999 );
		vec3 r123 = sqrt( R123 );
		vec3 Rs = pow2( T121 ) * R23 / ( vec3( 1.0 ) - R123 );
		vec3 C0 = R12 + Rs;
		I = C0;
		vec3 Cm = Rs - T121;
		for ( int m = 1; m <= 2; ++ m ) {
			Cm *= r123;
			vec3 Sm = 2.0 * evalSensitivity( float( m ) * OPD, float( m ) * phi );
			I += Cm * Sm;
		}
		return max( I, vec3( 0.0 ) );
	}
#endif`, $k = `#ifdef USE_BUMPMAP
	uniform sampler2D bumpMap;
	uniform float bumpScale;
	vec2 dHdxy_fwd() {
		vec2 dSTdx = dFdx( vBumpMapUv );
		vec2 dSTdy = dFdy( vBumpMapUv );
		float Hll = bumpScale * texture2D( bumpMap, vBumpMapUv ).x;
		float dBx = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdx ).x - Hll;
		float dBy = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdy ).x - Hll;
		return vec2( dBx, dBy );
	}
	vec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {
		vec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );
		vec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );
		vec3 vN = surf_norm;
		vec3 R1 = cross( vSigmaY, vN );
		vec3 R2 = cross( vN, vSigmaX );
		float fDet = dot( vSigmaX, R1 ) * faceDirection;
		vec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );
		return normalize( abs( fDet ) * surf_norm - vGrad );
	}
#endif`, eG = `#if NUM_CLIPPING_PLANES > 0
	vec4 plane;
	#ifdef ALPHA_TO_COVERAGE
		float distanceToPlane, distanceGradient;
		float clipOpacity = 1.0;
		#pragma unroll_loop_start
		for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {
			plane = clippingPlanes[ i ];
			distanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;
			distanceGradient = fwidth( distanceToPlane ) / 2.0;
			clipOpacity *= smoothstep( - distanceGradient, distanceGradient, distanceToPlane );
			if ( clipOpacity == 0.0 ) discard;
		}
		#pragma unroll_loop_end
		#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES
			float unionClipOpacity = 1.0;
			#pragma unroll_loop_start
			for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {
				plane = clippingPlanes[ i ];
				distanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;
				distanceGradient = fwidth( distanceToPlane ) / 2.0;
				unionClipOpacity *= 1.0 - smoothstep( - distanceGradient, distanceGradient, distanceToPlane );
			}
			#pragma unroll_loop_end
			clipOpacity *= 1.0 - unionClipOpacity;
		#endif
		diffuseColor.a *= clipOpacity;
		if ( diffuseColor.a == 0.0 ) discard;
	#else
		#pragma unroll_loop_start
		for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {
			plane = clippingPlanes[ i ];
			if ( dot( vClipPosition, plane.xyz ) > plane.w ) discard;
		}
		#pragma unroll_loop_end
		#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES
			bool clipped = true;
			#pragma unroll_loop_start
			for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {
				plane = clippingPlanes[ i ];
				clipped = ( dot( vClipPosition, plane.xyz ) > plane.w ) && clipped;
			}
			#pragma unroll_loop_end
			if ( clipped ) discard;
		#endif
	#endif
#endif`, tG = `#if NUM_CLIPPING_PLANES > 0
	varying vec3 vClipPosition;
	uniform vec4 clippingPlanes[ NUM_CLIPPING_PLANES ];
#endif`, iG = `#if NUM_CLIPPING_PLANES > 0
	varying vec3 vClipPosition;
#endif`, nG = `#if NUM_CLIPPING_PLANES > 0
	vClipPosition = - mvPosition.xyz;
#endif`, sG = `#if defined( USE_COLOR_ALPHA )
	diffuseColor *= vColor;
#elif defined( USE_COLOR )
	diffuseColor.rgb *= vColor;
#endif`, rG = `#if defined( USE_COLOR_ALPHA )
	varying vec4 vColor;
#elif defined( USE_COLOR )
	varying vec3 vColor;
#endif`, aG = `#if defined( USE_COLOR_ALPHA )
	varying vec4 vColor;
#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )
	varying vec3 vColor;
#endif`, oG = `#if defined( USE_COLOR_ALPHA )
	vColor = vec4( 1.0 );
#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )
	vColor = vec3( 1.0 );
#endif
#ifdef USE_COLOR
	vColor *= color;
#endif
#ifdef USE_INSTANCING_COLOR
	vColor.xyz *= instanceColor.xyz;
#endif
#ifdef USE_BATCHING_COLOR
	vec3 batchingColor = getBatchingColor( getIndirectIndex( gl_DrawID ) );
	vColor.xyz *= batchingColor.xyz;
#endif`, lG = `#define PI 3.141592653589793
#define PI2 6.283185307179586
#define PI_HALF 1.5707963267948966
#define RECIPROCAL_PI 0.3183098861837907
#define RECIPROCAL_PI2 0.15915494309189535
#define EPSILON 1e-6
#ifndef saturate
#define saturate( a ) clamp( a, 0.0, 1.0 )
#endif
#define whiteComplement( a ) ( 1.0 - saturate( a ) )
float pow2( const in float x ) { return x*x; }
vec3 pow2( const in vec3 x ) { return x*x; }
float pow3( const in float x ) { return x*x*x; }
float pow4( const in float x ) { float x2 = x*x; return x2*x2; }
float max3( const in vec3 v ) { return max( max( v.x, v.y ), v.z ); }
float average( const in vec3 v ) { return dot( v, vec3( 0.3333333 ) ); }
highp float rand( const in vec2 uv ) {
	const highp float a = 12.9898, b = 78.233, c = 43758.5453;
	highp float dt = dot( uv.xy, vec2( a,b ) ), sn = mod( dt, PI );
	return fract( sin( sn ) * c );
}
#ifdef HIGH_PRECISION
	float precisionSafeLength( vec3 v ) { return length( v ); }
#else
	float precisionSafeLength( vec3 v ) {
		float maxComponent = max3( abs( v ) );
		return length( v / maxComponent ) * maxComponent;
	}
#endif
struct IncidentLight {
	vec3 color;
	vec3 direction;
	bool visible;
};
struct ReflectedLight {
	vec3 directDiffuse;
	vec3 directSpecular;
	vec3 indirectDiffuse;
	vec3 indirectSpecular;
};
#ifdef USE_ALPHAHASH
	varying vec3 vPosition;
#endif
vec3 transformDirection( in vec3 dir, in mat4 matrix ) {
	return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );
}
vec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {
	return normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );
}
mat3 transposeMat3( const in mat3 m ) {
	mat3 tmp;
	tmp[ 0 ] = vec3( m[ 0 ].x, m[ 1 ].x, m[ 2 ].x );
	tmp[ 1 ] = vec3( m[ 0 ].y, m[ 1 ].y, m[ 2 ].y );
	tmp[ 2 ] = vec3( m[ 0 ].z, m[ 1 ].z, m[ 2 ].z );
	return tmp;
}
float luminance( const in vec3 rgb ) {
	const vec3 weights = vec3( 0.2126729, 0.7151522, 0.0721750 );
	return dot( weights, rgb );
}
bool isPerspectiveMatrix( mat4 m ) {
	return m[ 2 ][ 3 ] == - 1.0;
}
vec2 equirectUv( in vec3 dir ) {
	float u = atan( dir.z, dir.x ) * RECIPROCAL_PI2 + 0.5;
	float v = asin( clamp( dir.y, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;
	return vec2( u, v );
}
vec3 BRDF_Lambert( const in vec3 diffuseColor ) {
	return RECIPROCAL_PI * diffuseColor;
}
vec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {
	float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );
	return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );
}
float F_Schlick( const in float f0, const in float f90, const in float dotVH ) {
	float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );
	return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );
} // validated`, cG = `#ifdef ENVMAP_TYPE_CUBE_UV
	#define cubeUV_minMipLevel 4.0
	#define cubeUV_minTileSize 16.0
	float getFace( vec3 direction ) {
		vec3 absDirection = abs( direction );
		float face = - 1.0;
		if ( absDirection.x > absDirection.z ) {
			if ( absDirection.x > absDirection.y )
				face = direction.x > 0.0 ? 0.0 : 3.0;
			else
				face = direction.y > 0.0 ? 1.0 : 4.0;
		} else {
			if ( absDirection.z > absDirection.y )
				face = direction.z > 0.0 ? 2.0 : 5.0;
			else
				face = direction.y > 0.0 ? 1.0 : 4.0;
		}
		return face;
	}
	vec2 getUV( vec3 direction, float face ) {
		vec2 uv;
		if ( face == 0.0 ) {
			uv = vec2( direction.z, direction.y ) / abs( direction.x );
		} else if ( face == 1.0 ) {
			uv = vec2( - direction.x, - direction.z ) / abs( direction.y );
		} else if ( face == 2.0 ) {
			uv = vec2( - direction.x, direction.y ) / abs( direction.z );
		} else if ( face == 3.0 ) {
			uv = vec2( - direction.z, direction.y ) / abs( direction.x );
		} else if ( face == 4.0 ) {
			uv = vec2( - direction.x, direction.z ) / abs( direction.y );
		} else {
			uv = vec2( direction.x, direction.y ) / abs( direction.z );
		}
		return 0.5 * ( uv + 1.0 );
	}
	vec3 bilinearCubeUV( sampler2D envMap, vec3 direction, float mipInt ) {
		float face = getFace( direction );
		float filterInt = max( cubeUV_minMipLevel - mipInt, 0.0 );
		mipInt = max( mipInt, cubeUV_minMipLevel );
		float faceSize = exp2( mipInt );
		highp vec2 uv = getUV( direction, face ) * ( faceSize - 2.0 ) + 1.0;
		if ( face > 2.0 ) {
			uv.y += faceSize;
			face -= 3.0;
		}
		uv.x += face * faceSize;
		uv.x += filterInt * 3.0 * cubeUV_minTileSize;
		uv.y += 4.0 * ( exp2( CUBEUV_MAX_MIP ) - faceSize );
		uv.x *= CUBEUV_TEXEL_WIDTH;
		uv.y *= CUBEUV_TEXEL_HEIGHT;
		#ifdef texture2DGradEXT
			return texture2DGradEXT( envMap, uv, vec2( 0.0 ), vec2( 0.0 ) ).rgb;
		#else
			return texture2D( envMap, uv ).rgb;
		#endif
	}
	#define cubeUV_r0 1.0
	#define cubeUV_m0 - 2.0
	#define cubeUV_r1 0.8
	#define cubeUV_m1 - 1.0
	#define cubeUV_r4 0.4
	#define cubeUV_m4 2.0
	#define cubeUV_r5 0.305
	#define cubeUV_m5 3.0
	#define cubeUV_r6 0.21
	#define cubeUV_m6 4.0
	float roughnessToMip( float roughness ) {
		float mip = 0.0;
		if ( roughness >= cubeUV_r1 ) {
			mip = ( cubeUV_r0 - roughness ) * ( cubeUV_m1 - cubeUV_m0 ) / ( cubeUV_r0 - cubeUV_r1 ) + cubeUV_m0;
		} else if ( roughness >= cubeUV_r4 ) {
			mip = ( cubeUV_r1 - roughness ) * ( cubeUV_m4 - cubeUV_m1 ) / ( cubeUV_r1 - cubeUV_r4 ) + cubeUV_m1;
		} else if ( roughness >= cubeUV_r5 ) {
			mip = ( cubeUV_r4 - roughness ) * ( cubeUV_m5 - cubeUV_m4 ) / ( cubeUV_r4 - cubeUV_r5 ) + cubeUV_m4;
		} else if ( roughness >= cubeUV_r6 ) {
			mip = ( cubeUV_r5 - roughness ) * ( cubeUV_m6 - cubeUV_m5 ) / ( cubeUV_r5 - cubeUV_r6 ) + cubeUV_m5;
		} else {
			mip = - 2.0 * log2( 1.16 * roughness );		}
		return mip;
	}
	vec4 textureCubeUV( sampler2D envMap, vec3 sampleDir, float roughness ) {
		float mip = clamp( roughnessToMip( roughness ), cubeUV_m0, CUBEUV_MAX_MIP );
		float mipF = fract( mip );
		float mipInt = floor( mip );
		vec3 color0 = bilinearCubeUV( envMap, sampleDir, mipInt );
		if ( mipF == 0.0 ) {
			return vec4( color0, 1.0 );
		} else {
			vec3 color1 = bilinearCubeUV( envMap, sampleDir, mipInt + 1.0 );
			return vec4( mix( color0, color1, mipF ), 1.0 );
		}
	}
#endif`, hG = `vec3 transformedNormal = objectNormal;
#ifdef USE_TANGENT
	vec3 transformedTangent = objectTangent;
#endif
#ifdef USE_BATCHING
	mat3 bm = mat3( batchingMatrix );
	transformedNormal /= vec3( dot( bm[ 0 ], bm[ 0 ] ), dot( bm[ 1 ], bm[ 1 ] ), dot( bm[ 2 ], bm[ 2 ] ) );
	transformedNormal = bm * transformedNormal;
	#ifdef USE_TANGENT
		transformedTangent = bm * transformedTangent;
	#endif
#endif
#ifdef USE_INSTANCING
	mat3 im = mat3( instanceMatrix );
	transformedNormal /= vec3( dot( im[ 0 ], im[ 0 ] ), dot( im[ 1 ], im[ 1 ] ), dot( im[ 2 ], im[ 2 ] ) );
	transformedNormal = im * transformedNormal;
	#ifdef USE_TANGENT
		transformedTangent = im * transformedTangent;
	#endif
#endif
transformedNormal = normalMatrix * transformedNormal;
#ifdef FLIP_SIDED
	transformedNormal = - transformedNormal;
#endif
#ifdef USE_TANGENT
	transformedTangent = ( modelViewMatrix * vec4( transformedTangent, 0.0 ) ).xyz;
	#ifdef FLIP_SIDED
		transformedTangent = - transformedTangent;
	#endif
#endif`, uG = `#ifdef USE_DISPLACEMENTMAP
	uniform sampler2D displacementMap;
	uniform float displacementScale;
	uniform float displacementBias;
#endif`, dG = `#ifdef USE_DISPLACEMENTMAP
	transformed += normalize( objectNormal ) * ( texture2D( displacementMap, vDisplacementMapUv ).x * displacementScale + displacementBias );
#endif`, fG = `#ifdef USE_EMISSIVEMAP
	vec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );
	totalEmissiveRadiance *= emissiveColor.rgb;
#endif`, pG = `#ifdef USE_EMISSIVEMAP
	uniform sampler2D emissiveMap;
#endif`, mG = "gl_FragColor = linearToOutputTexel( gl_FragColor );", gG = `
const mat3 LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = mat3(
	vec3( 0.8224621, 0.177538, 0.0 ),
	vec3( 0.0331941, 0.9668058, 0.0 ),
	vec3( 0.0170827, 0.0723974, 0.9105199 )
);
const mat3 LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = mat3(
	vec3( 1.2249401, - 0.2249404, 0.0 ),
	vec3( - 0.0420569, 1.0420571, 0.0 ),
	vec3( - 0.0196376, - 0.0786361, 1.0982735 )
);
vec4 LinearSRGBToLinearDisplayP3( in vec4 value ) {
	return vec4( value.rgb * LINEAR_SRGB_TO_LINEAR_DISPLAY_P3, value.a );
}
vec4 LinearDisplayP3ToLinearSRGB( in vec4 value ) {
	return vec4( value.rgb * LINEAR_DISPLAY_P3_TO_LINEAR_SRGB, value.a );
}
vec4 LinearTransferOETF( in vec4 value ) {
	return value;
}
vec4 sRGBTransferOETF( in vec4 value ) {
	return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
}
vec4 LinearToLinear( in vec4 value ) {
	return value;
}
vec4 LinearTosRGB( in vec4 value ) {
	return sRGBTransferOETF( value );
}`, vG = `#ifdef USE_ENVMAP
	#ifdef ENV_WORLDPOS
		vec3 cameraToFrag;
		if ( isOrthographic ) {
			cameraToFrag = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );
		} else {
			cameraToFrag = normalize( vWorldPosition - cameraPosition );
		}
		vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
		#ifdef ENVMAP_MODE_REFLECTION
			vec3 reflectVec = reflect( cameraToFrag, worldNormal );
		#else
			vec3 reflectVec = refract( cameraToFrag, worldNormal, refractionRatio );
		#endif
	#else
		vec3 reflectVec = vReflect;
	#endif
	#ifdef ENVMAP_TYPE_CUBE
		vec4 envColor = textureCube( envMap, envMapRotation * vec3( flipEnvMap * reflectVec.x, reflectVec.yz ) );
	#else
		vec4 envColor = vec4( 0.0 );
	#endif
	#ifdef ENVMAP_BLENDING_MULTIPLY
		outgoingLight = mix( outgoingLight, outgoingLight * envColor.xyz, specularStrength * reflectivity );
	#elif defined( ENVMAP_BLENDING_MIX )
		outgoingLight = mix( outgoingLight, envColor.xyz, specularStrength * reflectivity );
	#elif defined( ENVMAP_BLENDING_ADD )
		outgoingLight += envColor.xyz * specularStrength * reflectivity;
	#endif
#endif`, yG = `#ifdef USE_ENVMAP
	uniform float envMapIntensity;
	uniform float flipEnvMap;
	uniform mat3 envMapRotation;
	#ifdef ENVMAP_TYPE_CUBE
		uniform samplerCube envMap;
	#else
		uniform sampler2D envMap;
	#endif
	
#endif`, xG = `#ifdef USE_ENVMAP
	uniform float reflectivity;
	#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )
		#define ENV_WORLDPOS
	#endif
	#ifdef ENV_WORLDPOS
		varying vec3 vWorldPosition;
		uniform float refractionRatio;
	#else
		varying vec3 vReflect;
	#endif
#endif`, AG = `#ifdef USE_ENVMAP
	#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )
		#define ENV_WORLDPOS
	#endif
	#ifdef ENV_WORLDPOS
		
		varying vec3 vWorldPosition;
	#else
		varying vec3 vReflect;
		uniform float refractionRatio;
	#endif
#endif`, bG = `#ifdef USE_ENVMAP
	#ifdef ENV_WORLDPOS
		vWorldPosition = worldPosition.xyz;
	#else
		vec3 cameraToVertex;
		if ( isOrthographic ) {
			cameraToVertex = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );
		} else {
			cameraToVertex = normalize( worldPosition.xyz - cameraPosition );
		}
		vec3 worldNormal = inverseTransformDirection( transformedNormal, viewMatrix );
		#ifdef ENVMAP_MODE_REFLECTION
			vReflect = reflect( cameraToVertex, worldNormal );
		#else
			vReflect = refract( cameraToVertex, worldNormal, refractionRatio );
		#endif
	#endif
#endif`, MG = `#ifdef USE_FOG
	vFogDepth = - mvPosition.z;
#endif`, SG = `#ifdef USE_FOG
	varying float vFogDepth;
#endif`, wG = `#ifdef USE_FOG
	#ifdef FOG_EXP2
		float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
	#else
		float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
	#endif
	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
#endif`, EG = `#ifdef USE_FOG
	uniform vec3 fogColor;
	varying float vFogDepth;
	#ifdef FOG_EXP2
		uniform float fogDensity;
	#else
		uniform float fogNear;
		uniform float fogFar;
	#endif
#endif`, TG = `#ifdef USE_GRADIENTMAP
	uniform sampler2D gradientMap;
#endif
vec3 getGradientIrradiance( vec3 normal, vec3 lightDirection ) {
	float dotNL = dot( normal, lightDirection );
	vec2 coord = vec2( dotNL * 0.5 + 0.5, 0.0 );
	#ifdef USE_GRADIENTMAP
		return vec3( texture2D( gradientMap, coord ).r );
	#else
		vec2 fw = fwidth( coord ) * 0.5;
		return mix( vec3( 0.7 ), vec3( 1.0 ), smoothstep( 0.7 - fw.x, 0.7 + fw.x, coord.x ) );
	#endif
}`, CG = `#ifdef USE_LIGHTMAP
	uniform sampler2D lightMap;
	uniform float lightMapIntensity;
#endif`, _G = `LambertMaterial material;
material.diffuseColor = diffuseColor.rgb;
material.specularStrength = specularStrength;`, PG = `varying vec3 vViewPosition;
struct LambertMaterial {
	vec3 diffuseColor;
	float specularStrength;
};
void RE_Direct_Lambert( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {
	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
	vec3 irradiance = dotNL * directLight.color;
	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
void RE_IndirectDiffuse_Lambert( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {
	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
#define RE_Direct				RE_Direct_Lambert
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Lambert`, RG = `uniform bool receiveShadow;
uniform vec3 ambientLightColor;
#if defined( USE_LIGHT_PROBES )
	uniform vec3 lightProbe[ 9 ];
#endif
vec3 shGetIrradianceAt( in vec3 normal, in vec3 shCoefficients[ 9 ] ) {
	float x = normal.x, y = normal.y, z = normal.z;
	vec3 result = shCoefficients[ 0 ] * 0.886227;
	result += shCoefficients[ 1 ] * 2.0 * 0.511664 * y;
	result += shCoefficients[ 2 ] * 2.0 * 0.511664 * z;
	result += shCoefficients[ 3 ] * 2.0 * 0.511664 * x;
	result += shCoefficients[ 4 ] * 2.0 * 0.429043 * x * y;
	result += shCoefficients[ 5 ] * 2.0 * 0.429043 * y * z;
	result += shCoefficients[ 6 ] * ( 0.743125 * z * z - 0.247708 );
	result += shCoefficients[ 7 ] * 2.0 * 0.429043 * x * z;
	result += shCoefficients[ 8 ] * 0.429043 * ( x * x - y * y );
	return result;
}
vec3 getLightProbeIrradiance( const in vec3 lightProbe[ 9 ], const in vec3 normal ) {
	vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
	vec3 irradiance = shGetIrradianceAt( worldNormal, lightProbe );
	return irradiance;
}
vec3 getAmbientLightIrradiance( const in vec3 ambientLightColor ) {
	vec3 irradiance = ambientLightColor;
	return irradiance;
}
float getDistanceAttenuation( const in float lightDistance, const in float cutoffDistance, const in float decayExponent ) {
	float distanceFalloff = 1.0 / max( pow( lightDistance, decayExponent ), 0.01 );
	if ( cutoffDistance > 0.0 ) {
		distanceFalloff *= pow2( saturate( 1.0 - pow4( lightDistance / cutoffDistance ) ) );
	}
	return distanceFalloff;
}
float getSpotAttenuation( const in float coneCosine, const in float penumbraCosine, const in float angleCosine ) {
	return smoothstep( coneCosine, penumbraCosine, angleCosine );
}
#if NUM_DIR_LIGHTS > 0
	struct DirectionalLight {
		vec3 direction;
		vec3 color;
	};
	uniform DirectionalLight directionalLights[ NUM_DIR_LIGHTS ];
	void getDirectionalLightInfo( const in DirectionalLight directionalLight, out IncidentLight light ) {
		light.color = directionalLight.color;
		light.direction = directionalLight.direction;
		light.visible = true;
	}
#endif
#if NUM_POINT_LIGHTS > 0
	struct PointLight {
		vec3 position;
		vec3 color;
		float distance;
		float decay;
	};
	uniform PointLight pointLights[ NUM_POINT_LIGHTS ];
	void getPointLightInfo( const in PointLight pointLight, const in vec3 geometryPosition, out IncidentLight light ) {
		vec3 lVector = pointLight.position - geometryPosition;
		light.direction = normalize( lVector );
		float lightDistance = length( lVector );
		light.color = pointLight.color;
		light.color *= getDistanceAttenuation( lightDistance, pointLight.distance, pointLight.decay );
		light.visible = ( light.color != vec3( 0.0 ) );
	}
#endif
#if NUM_SPOT_LIGHTS > 0
	struct SpotLight {
		vec3 position;
		vec3 direction;
		vec3 color;
		float distance;
		float decay;
		float coneCos;
		float penumbraCos;
	};
	uniform SpotLight spotLights[ NUM_SPOT_LIGHTS ];
	void getSpotLightInfo( const in SpotLight spotLight, const in vec3 geometryPosition, out IncidentLight light ) {
		vec3 lVector = spotLight.position - geometryPosition;
		light.direction = normalize( lVector );
		float angleCos = dot( light.direction, spotLight.direction );
		float spotAttenuation = getSpotAttenuation( spotLight.coneCos, spotLight.penumbraCos, angleCos );
		if ( spotAttenuation > 0.0 ) {
			float lightDistance = length( lVector );
			light.color = spotLight.color * spotAttenuation;
			light.color *= getDistanceAttenuation( lightDistance, spotLight.distance, spotLight.decay );
			light.visible = ( light.color != vec3( 0.0 ) );
		} else {
			light.color = vec3( 0.0 );
			light.visible = false;
		}
	}
#endif
#if NUM_RECT_AREA_LIGHTS > 0
	struct RectAreaLight {
		vec3 color;
		vec3 position;
		vec3 halfWidth;
		vec3 halfHeight;
	};
	uniform sampler2D ltc_1;	uniform sampler2D ltc_2;
	uniform RectAreaLight rectAreaLights[ NUM_RECT_AREA_LIGHTS ];
#endif
#if NUM_HEMI_LIGHTS > 0
	struct HemisphereLight {
		vec3 direction;
		vec3 skyColor;
		vec3 groundColor;
	};
	uniform HemisphereLight hemisphereLights[ NUM_HEMI_LIGHTS ];
	vec3 getHemisphereLightIrradiance( const in HemisphereLight hemiLight, const in vec3 normal ) {
		float dotNL = dot( normal, hemiLight.direction );
		float hemiDiffuseWeight = 0.5 * dotNL + 0.5;
		vec3 irradiance = mix( hemiLight.groundColor, hemiLight.skyColor, hemiDiffuseWeight );
		return irradiance;
	}
#endif`, DG = `#ifdef USE_ENVMAP
	vec3 getIBLIrradiance( const in vec3 normal ) {
		#ifdef ENVMAP_TYPE_CUBE_UV
			vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
			vec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, 1.0 );
			return PI * envMapColor.rgb * envMapIntensity;
		#else
			return vec3( 0.0 );
		#endif
	}
	vec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {
		#ifdef ENVMAP_TYPE_CUBE_UV
			vec3 reflectVec = reflect( - viewDir, normal );
			reflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );
			reflectVec = inverseTransformDirection( reflectVec, viewMatrix );
			vec4 envMapColor = textureCubeUV( envMap, envMapRotation * reflectVec, roughness );
			return envMapColor.rgb * envMapIntensity;
		#else
			return vec3( 0.0 );
		#endif
	}
	#ifdef USE_ANISOTROPY
		vec3 getIBLAnisotropyRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness, const in vec3 bitangent, const in float anisotropy ) {
			#ifdef ENVMAP_TYPE_CUBE_UV
				vec3 bentNormal = cross( bitangent, viewDir );
				bentNormal = normalize( cross( bentNormal, bitangent ) );
				bentNormal = normalize( mix( bentNormal, normal, pow2( pow2( 1.0 - anisotropy * ( 1.0 - roughness ) ) ) ) );
				return getIBLRadiance( viewDir, bentNormal, roughness );
			#else
				return vec3( 0.0 );
			#endif
		}
	#endif
#endif`, LG = `ToonMaterial material;
material.diffuseColor = diffuseColor.rgb;`, IG = `varying vec3 vViewPosition;
struct ToonMaterial {
	vec3 diffuseColor;
};
void RE_Direct_Toon( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {
	vec3 irradiance = getGradientIrradiance( geometryNormal, directLight.direction ) * directLight.color;
	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
void RE_IndirectDiffuse_Toon( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {
	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
#define RE_Direct				RE_Direct_Toon
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Toon`, OG = `BlinnPhongMaterial material;
material.diffuseColor = diffuseColor.rgb;
material.specularColor = specular;
material.specularShininess = shininess;
material.specularStrength = specularStrength;`, NG = `varying vec3 vViewPosition;
struct BlinnPhongMaterial {
	vec3 diffuseColor;
	vec3 specularColor;
	float specularShininess;
	float specularStrength;
};
void RE_Direct_BlinnPhong( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {
	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
	vec3 irradiance = dotNL * directLight.color;
	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
	reflectedLight.directSpecular += irradiance * BRDF_BlinnPhong( directLight.direction, geometryViewDir, geometryNormal, material.specularColor, material.specularShininess ) * material.specularStrength;
}
void RE_IndirectDiffuse_BlinnPhong( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {
	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
#define RE_Direct				RE_Direct_BlinnPhong
#define RE_IndirectDiffuse		RE_IndirectDiffuse_BlinnPhong`, BG = `PhysicalMaterial material;
material.diffuseColor = diffuseColor.rgb * ( 1.0 - metalnessFactor );
vec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );
float geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );
material.roughness = max( roughnessFactor, 0.0525 );material.roughness += geometryRoughness;
material.roughness = min( material.roughness, 1.0 );
#ifdef IOR
	material.ior = ior;
	#ifdef USE_SPECULAR
		float specularIntensityFactor = specularIntensity;
		vec3 specularColorFactor = specularColor;
		#ifdef USE_SPECULAR_COLORMAP
			specularColorFactor *= texture2D( specularColorMap, vSpecularColorMapUv ).rgb;
		#endif
		#ifdef USE_SPECULAR_INTENSITYMAP
			specularIntensityFactor *= texture2D( specularIntensityMap, vSpecularIntensityMapUv ).a;
		#endif
		material.specularF90 = mix( specularIntensityFactor, 1.0, metalnessFactor );
	#else
		float specularIntensityFactor = 1.0;
		vec3 specularColorFactor = vec3( 1.0 );
		material.specularF90 = 1.0;
	#endif
	material.specularColor = mix( min( pow2( ( material.ior - 1.0 ) / ( material.ior + 1.0 ) ) * specularColorFactor, vec3( 1.0 ) ) * specularIntensityFactor, diffuseColor.rgb, metalnessFactor );
#else
	material.specularColor = mix( vec3( 0.04 ), diffuseColor.rgb, metalnessFactor );
	material.specularF90 = 1.0;
#endif
#ifdef USE_CLEARCOAT
	material.clearcoat = clearcoat;
	material.clearcoatRoughness = clearcoatRoughness;
	material.clearcoatF0 = vec3( 0.04 );
	material.clearcoatF90 = 1.0;
	#ifdef USE_CLEARCOATMAP
		material.clearcoat *= texture2D( clearcoatMap, vClearcoatMapUv ).x;
	#endif
	#ifdef USE_CLEARCOAT_ROUGHNESSMAP
		material.clearcoatRoughness *= texture2D( clearcoatRoughnessMap, vClearcoatRoughnessMapUv ).y;
	#endif
	material.clearcoat = saturate( material.clearcoat );	material.clearcoatRoughness = max( material.clearcoatRoughness, 0.0525 );
	material.clearcoatRoughness += geometryRoughness;
	material.clearcoatRoughness = min( material.clearcoatRoughness, 1.0 );
#endif
#ifdef USE_DISPERSION
	material.dispersion = dispersion;
#endif
#ifdef USE_IRIDESCENCE
	material.iridescence = iridescence;
	material.iridescenceIOR = iridescenceIOR;
	#ifdef USE_IRIDESCENCEMAP
		material.iridescence *= texture2D( iridescenceMap, vIridescenceMapUv ).r;
	#endif
	#ifdef USE_IRIDESCENCE_THICKNESSMAP
		material.iridescenceThickness = (iridescenceThicknessMaximum - iridescenceThicknessMinimum) * texture2D( iridescenceThicknessMap, vIridescenceThicknessMapUv ).g + iridescenceThicknessMinimum;
	#else
		material.iridescenceThickness = iridescenceThicknessMaximum;
	#endif
#endif
#ifdef USE_SHEEN
	material.sheenColor = sheenColor;
	#ifdef USE_SHEEN_COLORMAP
		material.sheenColor *= texture2D( sheenColorMap, vSheenColorMapUv ).rgb;
	#endif
	material.sheenRoughness = clamp( sheenRoughness, 0.07, 1.0 );
	#ifdef USE_SHEEN_ROUGHNESSMAP
		material.sheenRoughness *= texture2D( sheenRoughnessMap, vSheenRoughnessMapUv ).a;
	#endif
#endif
#ifdef USE_ANISOTROPY
	#ifdef USE_ANISOTROPYMAP
		mat2 anisotropyMat = mat2( anisotropyVector.x, anisotropyVector.y, - anisotropyVector.y, anisotropyVector.x );
		vec3 anisotropyPolar = texture2D( anisotropyMap, vAnisotropyMapUv ).rgb;
		vec2 anisotropyV = anisotropyMat * normalize( 2.0 * anisotropyPolar.rg - vec2( 1.0 ) ) * anisotropyPolar.b;
	#else
		vec2 anisotropyV = anisotropyVector;
	#endif
	material.anisotropy = length( anisotropyV );
	if( material.anisotropy == 0.0 ) {
		anisotropyV = vec2( 1.0, 0.0 );
	} else {
		anisotropyV /= material.anisotropy;
		material.anisotropy = saturate( material.anisotropy );
	}
	material.alphaT = mix( pow2( material.roughness ), 1.0, pow2( material.anisotropy ) );
	material.anisotropyT = tbn[ 0 ] * anisotropyV.x + tbn[ 1 ] * anisotropyV.y;
	material.anisotropyB = tbn[ 1 ] * anisotropyV.x - tbn[ 0 ] * anisotropyV.y;
#endif`, FG = `struct PhysicalMaterial {
	vec3 diffuseColor;
	float roughness;
	vec3 specularColor;
	float specularF90;
	float dispersion;
	#ifdef USE_CLEARCOAT
		float clearcoat;
		float clearcoatRoughness;
		vec3 clearcoatF0;
		float clearcoatF90;
	#endif
	#ifdef USE_IRIDESCENCE
		float iridescence;
		float iridescenceIOR;
		float iridescenceThickness;
		vec3 iridescenceFresnel;
		vec3 iridescenceF0;
	#endif
	#ifdef USE_SHEEN
		vec3 sheenColor;
		float sheenRoughness;
	#endif
	#ifdef IOR
		float ior;
	#endif
	#ifdef USE_TRANSMISSION
		float transmission;
		float transmissionAlpha;
		float thickness;
		float attenuationDistance;
		vec3 attenuationColor;
	#endif
	#ifdef USE_ANISOTROPY
		float anisotropy;
		float alphaT;
		vec3 anisotropyT;
		vec3 anisotropyB;
	#endif
};
vec3 clearcoatSpecularDirect = vec3( 0.0 );
vec3 clearcoatSpecularIndirect = vec3( 0.0 );
vec3 sheenSpecularDirect = vec3( 0.0 );
vec3 sheenSpecularIndirect = vec3(0.0 );
vec3 Schlick_to_F0( const in vec3 f, const in float f90, const in float dotVH ) {
    float x = clamp( 1.0 - dotVH, 0.0, 1.0 );
    float x2 = x * x;
    float x5 = clamp( x * x2 * x2, 0.0, 0.9999 );
    return ( f - vec3( f90 ) * x5 ) / ( 1.0 - x5 );
}
float V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {
	float a2 = pow2( alpha );
	float gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );
	float gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );
	return 0.5 / max( gv + gl, EPSILON );
}
float D_GGX( const in float alpha, const in float dotNH ) {
	float a2 = pow2( alpha );
	float denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0;
	return RECIPROCAL_PI * a2 / pow2( denom );
}
#ifdef USE_ANISOTROPY
	float V_GGX_SmithCorrelated_Anisotropic( const in float alphaT, const in float alphaB, const in float dotTV, const in float dotBV, const in float dotTL, const in float dotBL, const in float dotNV, const in float dotNL ) {
		float gv = dotNL * length( vec3( alphaT * dotTV, alphaB * dotBV, dotNV ) );
		float gl = dotNV * length( vec3( alphaT * dotTL, alphaB * dotBL, dotNL ) );
		float v = 0.5 / ( gv + gl );
		return saturate(v);
	}
	float D_GGX_Anisotropic( const in float alphaT, const in float alphaB, const in float dotNH, const in float dotTH, const in float dotBH ) {
		float a2 = alphaT * alphaB;
		highp vec3 v = vec3( alphaB * dotTH, alphaT * dotBH, a2 * dotNH );
		highp float v2 = dot( v, v );
		float w2 = a2 / v2;
		return RECIPROCAL_PI * a2 * pow2 ( w2 );
	}
#endif
#ifdef USE_CLEARCOAT
	vec3 BRDF_GGX_Clearcoat( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material) {
		vec3 f0 = material.clearcoatF0;
		float f90 = material.clearcoatF90;
		float roughness = material.clearcoatRoughness;
		float alpha = pow2( roughness );
		vec3 halfDir = normalize( lightDir + viewDir );
		float dotNL = saturate( dot( normal, lightDir ) );
		float dotNV = saturate( dot( normal, viewDir ) );
		float dotNH = saturate( dot( normal, halfDir ) );
		float dotVH = saturate( dot( viewDir, halfDir ) );
		vec3 F = F_Schlick( f0, f90, dotVH );
		float V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );
		float D = D_GGX( alpha, dotNH );
		return F * ( V * D );
	}
#endif
vec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material ) {
	vec3 f0 = material.specularColor;
	float f90 = material.specularF90;
	float roughness = material.roughness;
	float alpha = pow2( roughness );
	vec3 halfDir = normalize( lightDir + viewDir );
	float dotNL = saturate( dot( normal, lightDir ) );
	float dotNV = saturate( dot( normal, viewDir ) );
	float dotNH = saturate( dot( normal, halfDir ) );
	float dotVH = saturate( dot( viewDir, halfDir ) );
	vec3 F = F_Schlick( f0, f90, dotVH );
	#ifdef USE_IRIDESCENCE
		F = mix( F, material.iridescenceFresnel, material.iridescence );
	#endif
	#ifdef USE_ANISOTROPY
		float dotTL = dot( material.anisotropyT, lightDir );
		float dotTV = dot( material.anisotropyT, viewDir );
		float dotTH = dot( material.anisotropyT, halfDir );
		float dotBL = dot( material.anisotropyB, lightDir );
		float dotBV = dot( material.anisotropyB, viewDir );
		float dotBH = dot( material.anisotropyB, halfDir );
		float V = V_GGX_SmithCorrelated_Anisotropic( material.alphaT, alpha, dotTV, dotBV, dotTL, dotBL, dotNV, dotNL );
		float D = D_GGX_Anisotropic( material.alphaT, alpha, dotNH, dotTH, dotBH );
	#else
		float V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );
		float D = D_GGX( alpha, dotNH );
	#endif
	return F * ( V * D );
}
vec2 LTC_Uv( const in vec3 N, const in vec3 V, const in float roughness ) {
	const float LUT_SIZE = 64.0;
	const float LUT_SCALE = ( LUT_SIZE - 1.0 ) / LUT_SIZE;
	const float LUT_BIAS = 0.5 / LUT_SIZE;
	float dotNV = saturate( dot( N, V ) );
	vec2 uv = vec2( roughness, sqrt( 1.0 - dotNV ) );
	uv = uv * LUT_SCALE + LUT_BIAS;
	return uv;
}
float LTC_ClippedSphereFormFactor( const in vec3 f ) {
	float l = length( f );
	return max( ( l * l + f.z ) / ( l + 1.0 ), 0.0 );
}
vec3 LTC_EdgeVectorFormFactor( const in vec3 v1, const in vec3 v2 ) {
	float x = dot( v1, v2 );
	float y = abs( x );
	float a = 0.8543985 + ( 0.4965155 + 0.0145206 * y ) * y;
	float b = 3.4175940 + ( 4.1616724 + y ) * y;
	float v = a / b;
	float theta_sintheta = ( x > 0.0 ) ? v : 0.5 * inversesqrt( max( 1.0 - x * x, 1e-7 ) ) - v;
	return cross( v1, v2 ) * theta_sintheta;
}
vec3 LTC_Evaluate( const in vec3 N, const in vec3 V, const in vec3 P, const in mat3 mInv, const in vec3 rectCoords[ 4 ] ) {
	vec3 v1 = rectCoords[ 1 ] - rectCoords[ 0 ];
	vec3 v2 = rectCoords[ 3 ] - rectCoords[ 0 ];
	vec3 lightNormal = cross( v1, v2 );
	if( dot( lightNormal, P - rectCoords[ 0 ] ) < 0.0 ) return vec3( 0.0 );
	vec3 T1, T2;
	T1 = normalize( V - N * dot( V, N ) );
	T2 = - cross( N, T1 );
	mat3 mat = mInv * transposeMat3( mat3( T1, T2, N ) );
	vec3 coords[ 4 ];
	coords[ 0 ] = mat * ( rectCoords[ 0 ] - P );
	coords[ 1 ] = mat * ( rectCoords[ 1 ] - P );
	coords[ 2 ] = mat * ( rectCoords[ 2 ] - P );
	coords[ 3 ] = mat * ( rectCoords[ 3 ] - P );
	coords[ 0 ] = normalize( coords[ 0 ] );
	coords[ 1 ] = normalize( coords[ 1 ] );
	coords[ 2 ] = normalize( coords[ 2 ] );
	coords[ 3 ] = normalize( coords[ 3 ] );
	vec3 vectorFormFactor = vec3( 0.0 );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 0 ], coords[ 1 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 1 ], coords[ 2 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 2 ], coords[ 3 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 3 ], coords[ 0 ] );
	float result = LTC_ClippedSphereFormFactor( vectorFormFactor );
	return vec3( result );
}
#if defined( USE_SHEEN )
float D_Charlie( float roughness, float dotNH ) {
	float alpha = pow2( roughness );
	float invAlpha = 1.0 / alpha;
	float cos2h = dotNH * dotNH;
	float sin2h = max( 1.0 - cos2h, 0.0078125 );
	return ( 2.0 + invAlpha ) * pow( sin2h, invAlpha * 0.5 ) / ( 2.0 * PI );
}
float V_Neubelt( float dotNV, float dotNL ) {
	return saturate( 1.0 / ( 4.0 * ( dotNL + dotNV - dotNL * dotNV ) ) );
}
vec3 BRDF_Sheen( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, vec3 sheenColor, const in float sheenRoughness ) {
	vec3 halfDir = normalize( lightDir + viewDir );
	float dotNL = saturate( dot( normal, lightDir ) );
	float dotNV = saturate( dot( normal, viewDir ) );
	float dotNH = saturate( dot( normal, halfDir ) );
	float D = D_Charlie( sheenRoughness, dotNH );
	float V = V_Neubelt( dotNV, dotNL );
	return sheenColor * ( D * V );
}
#endif
float IBLSheenBRDF( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {
	float dotNV = saturate( dot( normal, viewDir ) );
	float r2 = roughness * roughness;
	float a = roughness < 0.25 ? -339.2 * r2 + 161.4 * roughness - 25.9 : -8.48 * r2 + 14.3 * roughness - 9.95;
	float b = roughness < 0.25 ? 44.0 * r2 - 23.7 * roughness + 3.26 : 1.97 * r2 - 3.27 * roughness + 0.72;
	float DG = exp( a * dotNV + b ) + ( roughness < 0.25 ? 0.0 : 0.1 * ( roughness - 0.25 ) );
	return saturate( DG * RECIPROCAL_PI );
}
vec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {
	float dotNV = saturate( dot( normal, viewDir ) );
	const vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );
	const vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );
	vec4 r = roughness * c0 + c1;
	float a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;
	vec2 fab = vec2( - 1.04, 1.04 ) * a004 + r.zw;
	return fab;
}
vec3 EnvironmentBRDF( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness ) {
	vec2 fab = DFGApprox( normal, viewDir, roughness );
	return specularColor * fab.x + specularF90 * fab.y;
}
#ifdef USE_IRIDESCENCE
void computeMultiscatteringIridescence( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float iridescence, const in vec3 iridescenceF0, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {
#else
void computeMultiscattering( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {
#endif
	vec2 fab = DFGApprox( normal, viewDir, roughness );
	#ifdef USE_IRIDESCENCE
		vec3 Fr = mix( specularColor, iridescenceF0, iridescence );
	#else
		vec3 Fr = specularColor;
	#endif
	vec3 FssEss = Fr * fab.x + specularF90 * fab.y;
	float Ess = fab.x + fab.y;
	float Ems = 1.0 - Ess;
	vec3 Favg = Fr + ( 1.0 - Fr ) * 0.047619;	vec3 Fms = FssEss * Favg / ( 1.0 - Ems * Favg );
	singleScatter += FssEss;
	multiScatter += Fms * Ems;
}
#if NUM_RECT_AREA_LIGHTS > 0
	void RE_Direct_RectArea_Physical( const in RectAreaLight rectAreaLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {
		vec3 normal = geometryNormal;
		vec3 viewDir = geometryViewDir;
		vec3 position = geometryPosition;
		vec3 lightPos = rectAreaLight.position;
		vec3 halfWidth = rectAreaLight.halfWidth;
		vec3 halfHeight = rectAreaLight.halfHeight;
		vec3 lightColor = rectAreaLight.color;
		float roughness = material.roughness;
		vec3 rectCoords[ 4 ];
		rectCoords[ 0 ] = lightPos + halfWidth - halfHeight;		rectCoords[ 1 ] = lightPos - halfWidth - halfHeight;
		rectCoords[ 2 ] = lightPos - halfWidth + halfHeight;
		rectCoords[ 3 ] = lightPos + halfWidth + halfHeight;
		vec2 uv = LTC_Uv( normal, viewDir, roughness );
		vec4 t1 = texture2D( ltc_1, uv );
		vec4 t2 = texture2D( ltc_2, uv );
		mat3 mInv = mat3(
			vec3( t1.x, 0, t1.y ),
			vec3(    0, 1,    0 ),
			vec3( t1.z, 0, t1.w )
		);
		vec3 fresnel = ( material.specularColor * t2.x + ( vec3( 1.0 ) - material.specularColor ) * t2.y );
		reflectedLight.directSpecular += lightColor * fresnel * LTC_Evaluate( normal, viewDir, position, mInv, rectCoords );
		reflectedLight.directDiffuse += lightColor * material.diffuseColor * LTC_Evaluate( normal, viewDir, position, mat3( 1.0 ), rectCoords );
	}
#endif
void RE_Direct_Physical( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {
	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
	vec3 irradiance = dotNL * directLight.color;
	#ifdef USE_CLEARCOAT
		float dotNLcc = saturate( dot( geometryClearcoatNormal, directLight.direction ) );
		vec3 ccIrradiance = dotNLcc * directLight.color;
		clearcoatSpecularDirect += ccIrradiance * BRDF_GGX_Clearcoat( directLight.direction, geometryViewDir, geometryClearcoatNormal, material );
	#endif
	#ifdef USE_SHEEN
		sheenSpecularDirect += irradiance * BRDF_Sheen( directLight.direction, geometryViewDir, geometryNormal, material.sheenColor, material.sheenRoughness );
	#endif
	reflectedLight.directSpecular += irradiance * BRDF_GGX( directLight.direction, geometryViewDir, geometryNormal, material );
	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
void RE_IndirectDiffuse_Physical( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {
	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}
void RE_IndirectSpecular_Physical( const in vec3 radiance, const in vec3 irradiance, const in vec3 clearcoatRadiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {
	#ifdef USE_CLEARCOAT
		clearcoatSpecularIndirect += clearcoatRadiance * EnvironmentBRDF( geometryClearcoatNormal, geometryViewDir, material.clearcoatF0, material.clearcoatF90, material.clearcoatRoughness );
	#endif
	#ifdef USE_SHEEN
		sheenSpecularIndirect += irradiance * material.sheenColor * IBLSheenBRDF( geometryNormal, geometryViewDir, material.sheenRoughness );
	#endif
	vec3 singleScattering = vec3( 0.0 );
	vec3 multiScattering = vec3( 0.0 );
	vec3 cosineWeightedIrradiance = irradiance * RECIPROCAL_PI;
	#ifdef USE_IRIDESCENCE
		computeMultiscatteringIridescence( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.iridescence, material.iridescenceFresnel, material.roughness, singleScattering, multiScattering );
	#else
		computeMultiscattering( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.roughness, singleScattering, multiScattering );
	#endif
	vec3 totalScattering = singleScattering + multiScattering;
	vec3 diffuse = material.diffuseColor * ( 1.0 - max( max( totalScattering.r, totalScattering.g ), totalScattering.b ) );
	reflectedLight.indirectSpecular += radiance * singleScattering;
	reflectedLight.indirectSpecular += multiScattering * cosineWeightedIrradiance;
	reflectedLight.indirectDiffuse += diffuse * cosineWeightedIrradiance;
}
#define RE_Direct				RE_Direct_Physical
#define RE_Direct_RectArea		RE_Direct_RectArea_Physical
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Physical
#define RE_IndirectSpecular		RE_IndirectSpecular_Physical
float computeSpecularOcclusion( const in float dotNV, const in float ambientOcclusion, const in float roughness ) {
	return saturate( pow( dotNV + ambientOcclusion, exp2( - 16.0 * roughness - 1.0 ) ) - 1.0 + ambientOcclusion );
}`, UG = `
vec3 geometryPosition = - vViewPosition;
vec3 geometryNormal = normal;
vec3 geometryViewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );
vec3 geometryClearcoatNormal = vec3( 0.0 );
#ifdef USE_CLEARCOAT
	geometryClearcoatNormal = clearcoatNormal;
#endif
#ifdef USE_IRIDESCENCE
	float dotNVi = saturate( dot( normal, geometryViewDir ) );
	if ( material.iridescenceThickness == 0.0 ) {
		material.iridescence = 0.0;
	} else {
		material.iridescence = saturate( material.iridescence );
	}
	if ( material.iridescence > 0.0 ) {
		material.iridescenceFresnel = evalIridescence( 1.0, material.iridescenceIOR, dotNVi, material.iridescenceThickness, material.specularColor );
		material.iridescenceF0 = Schlick_to_F0( material.iridescenceFresnel, 1.0, dotNVi );
	}
#endif
IncidentLight directLight;
#if ( NUM_POINT_LIGHTS > 0 ) && defined( RE_Direct )
	PointLight pointLight;
	#if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0
	PointLightShadow pointLightShadow;
	#endif
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {
		pointLight = pointLights[ i ];
		getPointLightInfo( pointLight, geometryPosition, directLight );
		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )
		pointLightShadow = pointLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getPointShadow( pointShadowMap[ i ], pointLightShadow.shadowMapSize, pointLightShadow.shadowIntensity, pointLightShadow.shadowBias, pointLightShadow.shadowRadius, vPointShadowCoord[ i ], pointLightShadow.shadowCameraNear, pointLightShadow.shadowCameraFar ) : 1.0;
		#endif
		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
	}
	#pragma unroll_loop_end
#endif
#if ( NUM_SPOT_LIGHTS > 0 ) && defined( RE_Direct )
	SpotLight spotLight;
	vec4 spotColor;
	vec3 spotLightCoord;
	bool inSpotLightMap;
	#if defined( USE_SHADOWMAP ) && NUM_SPOT_LIGHT_SHADOWS > 0
	SpotLightShadow spotLightShadow;
	#endif
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {
		spotLight = spotLights[ i ];
		getSpotLightInfo( spotLight, geometryPosition, directLight );
		#if ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#define SPOT_LIGHT_MAP_INDEX UNROLLED_LOOP_INDEX
		#elif ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		#define SPOT_LIGHT_MAP_INDEX NUM_SPOT_LIGHT_MAPS
		#else
		#define SPOT_LIGHT_MAP_INDEX ( UNROLLED_LOOP_INDEX - NUM_SPOT_LIGHT_SHADOWS + NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#endif
		#if ( SPOT_LIGHT_MAP_INDEX < NUM_SPOT_LIGHT_MAPS )
			spotLightCoord = vSpotLightCoord[ i ].xyz / vSpotLightCoord[ i ].w;
			inSpotLightMap = all( lessThan( abs( spotLightCoord * 2. - 1. ), vec3( 1.0 ) ) );
			spotColor = texture2D( spotLightMap[ SPOT_LIGHT_MAP_INDEX ], spotLightCoord.xy );
			directLight.color = inSpotLightMap ? directLight.color * spotColor.rgb : directLight.color;
		#endif
		#undef SPOT_LIGHT_MAP_INDEX
		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		spotLightShadow = spotLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( spotShadowMap[ i ], spotLightShadow.shadowMapSize, spotLightShadow.shadowIntensity, spotLightShadow.shadowBias, spotLightShadow.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;
		#endif
		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
	}
	#pragma unroll_loop_end
#endif
#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct )
	DirectionalLight directionalLight;
	#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
	DirectionalLightShadow directionalLightShadow;
	#endif
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {
		directionalLight = directionalLights[ i ];
		getDirectionalLightInfo( directionalLight, directLight );
		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )
		directionalLightShadow = directionalLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;
		#endif
		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
	}
	#pragma unroll_loop_end
#endif
#if ( NUM_RECT_AREA_LIGHTS > 0 ) && defined( RE_Direct_RectArea )
	RectAreaLight rectAreaLight;
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_RECT_AREA_LIGHTS; i ++ ) {
		rectAreaLight = rectAreaLights[ i ];
		RE_Direct_RectArea( rectAreaLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
	}
	#pragma unroll_loop_end
#endif
#if defined( RE_IndirectDiffuse )
	vec3 iblIrradiance = vec3( 0.0 );
	vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );
	#if defined( USE_LIGHT_PROBES )
		irradiance += getLightProbeIrradiance( lightProbe, geometryNormal );
	#endif
	#if ( NUM_HEMI_LIGHTS > 0 )
		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {
			irradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal );
		}
		#pragma unroll_loop_end
	#endif
#endif
#if defined( RE_IndirectSpecular )
	vec3 radiance = vec3( 0.0 );
	vec3 clearcoatRadiance = vec3( 0.0 );
#endif`, zG = `#if defined( RE_IndirectDiffuse )
	#ifdef USE_LIGHTMAP
		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		vec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;
		irradiance += lightMapIrradiance;
	#endif
	#if defined( USE_ENVMAP ) && defined( STANDARD ) && defined( ENVMAP_TYPE_CUBE_UV )
		iblIrradiance += getIBLIrradiance( geometryNormal );
	#endif
#endif
#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )
	#ifdef USE_ANISOTROPY
		radiance += getIBLAnisotropyRadiance( geometryViewDir, geometryNormal, material.roughness, material.anisotropyB, material.anisotropy );
	#else
		radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );
	#endif
	#ifdef USE_CLEARCOAT
		clearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );
	#endif
#endif`, kG = `#if defined( RE_IndirectDiffuse )
	RE_IndirectDiffuse( irradiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
#endif
#if defined( RE_IndirectSpecular )
	RE_IndirectSpecular( radiance, iblIrradiance, clearcoatRadiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
#endif`, GG = `#if defined( USE_LOGDEPTHBUF )
	gl_FragDepth = vIsPerspective == 0.0 ? gl_FragCoord.z : log2( vFragDepth ) * logDepthBufFC * 0.5;
#endif`, HG = `#if defined( USE_LOGDEPTHBUF )
	uniform float logDepthBufFC;
	varying float vFragDepth;
	varying float vIsPerspective;
#endif`, VG = `#ifdef USE_LOGDEPTHBUF
	varying float vFragDepth;
	varying float vIsPerspective;
#endif`, WG = `#ifdef USE_LOGDEPTHBUF
	vFragDepth = 1.0 + gl_Position.w;
	vIsPerspective = float( isPerspectiveMatrix( projectionMatrix ) );
#endif`, XG = `#ifdef USE_MAP
	vec4 sampledDiffuseColor = texture2D( map, vMapUv );
	#ifdef DECODE_VIDEO_TEXTURE
		sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );
	
	#endif
	diffuseColor *= sampledDiffuseColor;
#endif`, jG = `#ifdef USE_MAP
	uniform sampler2D map;
#endif`, qG = `#if defined( USE_MAP ) || defined( USE_ALPHAMAP )
	#if defined( USE_POINTS_UV )
		vec2 uv = vUv;
	#else
		vec2 uv = ( uvTransform * vec3( gl_PointCoord.x, 1.0 - gl_PointCoord.y, 1 ) ).xy;
	#endif
#endif
#ifdef USE_MAP
	diffuseColor *= texture2D( map, uv );
#endif
#ifdef USE_ALPHAMAP
	diffuseColor.a *= texture2D( alphaMap, uv ).g;
#endif`, YG = `#if defined( USE_POINTS_UV )
	varying vec2 vUv;
#else
	#if defined( USE_MAP ) || defined( USE_ALPHAMAP )
		uniform mat3 uvTransform;
	#endif
#endif
#ifdef USE_MAP
	uniform sampler2D map;
#endif
#ifdef USE_ALPHAMAP
	uniform sampler2D alphaMap;
#endif`, ZG = `float metalnessFactor = metalness;
#ifdef USE_METALNESSMAP
	vec4 texelMetalness = texture2D( metalnessMap, vMetalnessMapUv );
	metalnessFactor *= texelMetalness.b;
#endif`, KG = `#ifdef USE_METALNESSMAP
	uniform sampler2D metalnessMap;
#endif`, JG = `#ifdef USE_INSTANCING_MORPH
	float morphTargetInfluences[ MORPHTARGETS_COUNT ];
	float morphTargetBaseInfluence = texelFetch( morphTexture, ivec2( 0, gl_InstanceID ), 0 ).r;
	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {
		morphTargetInfluences[i] =  texelFetch( morphTexture, ivec2( i + 1, gl_InstanceID ), 0 ).r;
	}
#endif`, QG = `#if defined( USE_MORPHCOLORS )
	vColor *= morphTargetBaseInfluence;
	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {
		#if defined( USE_COLOR_ALPHA )
			if ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ) * morphTargetInfluences[ i ];
		#elif defined( USE_COLOR )
			if ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ).rgb * morphTargetInfluences[ i ];
		#endif
	}
#endif`, $G = `#ifdef USE_MORPHNORMALS
	objectNormal *= morphTargetBaseInfluence;
	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {
		if ( morphTargetInfluences[ i ] != 0.0 ) objectNormal += getMorph( gl_VertexID, i, 1 ).xyz * morphTargetInfluences[ i ];
	}
#endif`, eH = `#ifdef USE_MORPHTARGETS
	#ifndef USE_INSTANCING_MORPH
		uniform float morphTargetBaseInfluence;
		uniform float morphTargetInfluences[ MORPHTARGETS_COUNT ];
	#endif
	uniform sampler2DArray morphTargetsTexture;
	uniform ivec2 morphTargetsTextureSize;
	vec4 getMorph( const in int vertexIndex, const in int morphTargetIndex, const in int offset ) {
		int texelIndex = vertexIndex * MORPHTARGETS_TEXTURE_STRIDE + offset;
		int y = texelIndex / morphTargetsTextureSize.x;
		int x = texelIndex - y * morphTargetsTextureSize.x;
		ivec3 morphUV = ivec3( x, y, morphTargetIndex );
		return texelFetch( morphTargetsTexture, morphUV, 0 );
	}
#endif`, tH = `#ifdef USE_MORPHTARGETS
	transformed *= morphTargetBaseInfluence;
	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {
		if ( morphTargetInfluences[ i ] != 0.0 ) transformed += getMorph( gl_VertexID, i, 0 ).xyz * morphTargetInfluences[ i ];
	}
#endif`, iH = `float faceDirection = gl_FrontFacing ? 1.0 : - 1.0;
#ifdef FLAT_SHADED
	vec3 fdx = dFdx( vViewPosition );
	vec3 fdy = dFdy( vViewPosition );
	vec3 normal = normalize( cross( fdx, fdy ) );
#else
	vec3 normal = normalize( vNormal );
	#ifdef DOUBLE_SIDED
		normal *= faceDirection;
	#endif
#endif
#if defined( USE_NORMALMAP_TANGENTSPACE ) || defined( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY )
	#ifdef USE_TANGENT
		mat3 tbn = mat3( normalize( vTangent ), normalize( vBitangent ), normal );
	#else
		mat3 tbn = getTangentFrame( - vViewPosition, normal,
		#if defined( USE_NORMALMAP )
			vNormalMapUv
		#elif defined( USE_CLEARCOAT_NORMALMAP )
			vClearcoatNormalMapUv
		#else
			vUv
		#endif
		);
	#endif
	#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )
		tbn[0] *= faceDirection;
		tbn[1] *= faceDirection;
	#endif
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
	#ifdef USE_TANGENT
		mat3 tbn2 = mat3( normalize( vTangent ), normalize( vBitangent ), normal );
	#else
		mat3 tbn2 = getTangentFrame( - vViewPosition, normal, vClearcoatNormalMapUv );
	#endif
	#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )
		tbn2[0] *= faceDirection;
		tbn2[1] *= faceDirection;
	#endif
#endif
vec3 nonPerturbedNormal = normal;`, nH = `#ifdef USE_NORMALMAP_OBJECTSPACE
	normal = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
	#ifdef FLIP_SIDED
		normal = - normal;
	#endif
	#ifdef DOUBLE_SIDED
		normal = normal * faceDirection;
	#endif
	normal = normalize( normalMatrix * normal );
#elif defined( USE_NORMALMAP_TANGENTSPACE )
	vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
	mapN.xy *= normalScale;
	normal = normalize( tbn * mapN );
#elif defined( USE_BUMPMAP )
	normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );
#endif`, sH = `#ifndef FLAT_SHADED
	varying vec3 vNormal;
	#ifdef USE_TANGENT
		varying vec3 vTangent;
		varying vec3 vBitangent;
	#endif
#endif`, rH = `#ifndef FLAT_SHADED
	varying vec3 vNormal;
	#ifdef USE_TANGENT
		varying vec3 vTangent;
		varying vec3 vBitangent;
	#endif
#endif`, aH = `#ifndef FLAT_SHADED
	vNormal = normalize( transformedNormal );
	#ifdef USE_TANGENT
		vTangent = normalize( transformedTangent );
		vBitangent = normalize( cross( vNormal, vTangent ) * tangent.w );
	#endif
#endif`, oH = `#ifdef USE_NORMALMAP
	uniform sampler2D normalMap;
	uniform vec2 normalScale;
#endif
#ifdef USE_NORMALMAP_OBJECTSPACE
	uniform mat3 normalMatrix;
#endif
#if ! defined ( USE_TANGENT ) && ( defined ( USE_NORMALMAP_TANGENTSPACE ) || defined ( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY ) )
	mat3 getTangentFrame( vec3 eye_pos, vec3 surf_norm, vec2 uv ) {
		vec3 q0 = dFdx( eye_pos.xyz );
		vec3 q1 = dFdy( eye_pos.xyz );
		vec2 st0 = dFdx( uv.st );
		vec2 st1 = dFdy( uv.st );
		vec3 N = surf_norm;
		vec3 q1perp = cross( q1, N );
		vec3 q0perp = cross( N, q0 );
		vec3 T = q1perp * st0.x + q0perp * st1.x;
		vec3 B = q1perp * st0.y + q0perp * st1.y;
		float det = max( dot( T, T ), dot( B, B ) );
		float scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );
		return mat3( T * scale, B * scale, N );
	}
#endif`, lH = `#ifdef USE_CLEARCOAT
	vec3 clearcoatNormal = nonPerturbedNormal;
#endif`, cH = `#ifdef USE_CLEARCOAT_NORMALMAP
	vec3 clearcoatMapN = texture2D( clearcoatNormalMap, vClearcoatNormalMapUv ).xyz * 2.0 - 1.0;
	clearcoatMapN.xy *= clearcoatNormalScale;
	clearcoatNormal = normalize( tbn2 * clearcoatMapN );
#endif`, hH = `#ifdef USE_CLEARCOATMAP
	uniform sampler2D clearcoatMap;
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
	uniform sampler2D clearcoatNormalMap;
	uniform vec2 clearcoatNormalScale;
#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP
	uniform sampler2D clearcoatRoughnessMap;
#endif`, uH = `#ifdef USE_IRIDESCENCEMAP
	uniform sampler2D iridescenceMap;
#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP
	uniform sampler2D iridescenceThicknessMap;
#endif`, dH = `#ifdef OPAQUE
diffuseColor.a = 1.0;
#endif
#ifdef USE_TRANSMISSION
diffuseColor.a *= material.transmissionAlpha;
#endif
gl_FragColor = vec4( outgoingLight, diffuseColor.a );`, fH = `vec3 packNormalToRGB( const in vec3 normal ) {
	return normalize( normal ) * 0.5 + 0.5;
}
vec3 unpackRGBToNormal( const in vec3 rgb ) {
	return 2.0 * rgb.xyz - 1.0;
}
const float PackUpscale = 256. / 255.;const float UnpackDownscale = 255. / 256.;
const vec3 PackFactors = vec3( 256. * 256. * 256., 256. * 256., 256. );
const vec4 UnpackFactors = UnpackDownscale / vec4( PackFactors, 1. );
const float ShiftRight8 = 1. / 256.;
vec4 packDepthToRGBA( const in float v ) {
	vec4 r = vec4( fract( v * PackFactors ), v );
	r.yzw -= r.xyz * ShiftRight8;	return r * PackUpscale;
}
float unpackRGBAToDepth( const in vec4 v ) {
	return dot( v, UnpackFactors );
}
vec2 packDepthToRG( in highp float v ) {
	return packDepthToRGBA( v ).yx;
}
float unpackRGToDepth( const in highp vec2 v ) {
	return unpackRGBAToDepth( vec4( v.xy, 0.0, 0.0 ) );
}
vec4 pack2HalfToRGBA( vec2 v ) {
	vec4 r = vec4( v.x, fract( v.x * 255.0 ), v.y, fract( v.y * 255.0 ) );
	return vec4( r.x - r.y / 255.0, r.y, r.z - r.w / 255.0, r.w );
}
vec2 unpackRGBATo2Half( vec4 v ) {
	return vec2( v.x + ( v.y / 255.0 ), v.z + ( v.w / 255.0 ) );
}
float viewZToOrthographicDepth( const in float viewZ, const in float near, const in float far ) {
	return ( viewZ + near ) / ( near - far );
}
float orthographicDepthToViewZ( const in float depth, const in float near, const in float far ) {
	return depth * ( near - far ) - near;
}
float viewZToPerspectiveDepth( const in float viewZ, const in float near, const in float far ) {
	return ( ( near + viewZ ) * far ) / ( ( far - near ) * viewZ );
}
float perspectiveDepthToViewZ( const in float depth, const in float near, const in float far ) {
	return ( near * far ) / ( ( far - near ) * depth - far );
}`, pH = `#ifdef PREMULTIPLIED_ALPHA
	gl_FragColor.rgb *= gl_FragColor.a;
#endif`, mH = `vec4 mvPosition = vec4( transformed, 1.0 );
#ifdef USE_BATCHING
	mvPosition = batchingMatrix * mvPosition;
#endif
#ifdef USE_INSTANCING
	mvPosition = instanceMatrix * mvPosition;
#endif
mvPosition = modelViewMatrix * mvPosition;
gl_Position = projectionMatrix * mvPosition;`, gH = `#ifdef DITHERING
	gl_FragColor.rgb = dithering( gl_FragColor.rgb );
#endif`, vH = `#ifdef DITHERING
	vec3 dithering( vec3 color ) {
		float grid_position = rand( gl_FragCoord.xy );
		vec3 dither_shift_RGB = vec3( 0.25 / 255.0, -0.25 / 255.0, 0.25 / 255.0 );
		dither_shift_RGB = mix( 2.0 * dither_shift_RGB, -2.0 * dither_shift_RGB, grid_position );
		return color + dither_shift_RGB;
	}
#endif`, yH = `float roughnessFactor = roughness;
#ifdef USE_ROUGHNESSMAP
	vec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );
	roughnessFactor *= texelRoughness.g;
#endif`, xH = `#ifdef USE_ROUGHNESSMAP
	uniform sampler2D roughnessMap;
#endif`, AH = `#if NUM_SPOT_LIGHT_COORDS > 0
	varying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];
#endif
#if NUM_SPOT_LIGHT_MAPS > 0
	uniform sampler2D spotLightMap[ NUM_SPOT_LIGHT_MAPS ];
#endif
#ifdef USE_SHADOWMAP
	#if NUM_DIR_LIGHT_SHADOWS > 0
		uniform sampler2D directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];
		varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];
		struct DirectionalLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};
		uniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];
	#endif
	#if NUM_SPOT_LIGHT_SHADOWS > 0
		uniform sampler2D spotShadowMap[ NUM_SPOT_LIGHT_SHADOWS ];
		struct SpotLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};
		uniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];
	#endif
	#if NUM_POINT_LIGHT_SHADOWS > 0
		uniform sampler2D pointShadowMap[ NUM_POINT_LIGHT_SHADOWS ];
		varying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];
		struct PointLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
			float shadowCameraNear;
			float shadowCameraFar;
		};
		uniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];
	#endif
	float texture2DCompare( sampler2D depths, vec2 uv, float compare ) {
		return step( compare, unpackRGBAToDepth( texture2D( depths, uv ) ) );
	}
	vec2 texture2DDistribution( sampler2D shadow, vec2 uv ) {
		return unpackRGBATo2Half( texture2D( shadow, uv ) );
	}
	float VSMShadow (sampler2D shadow, vec2 uv, float compare ){
		float occlusion = 1.0;
		vec2 distribution = texture2DDistribution( shadow, uv );
		float hard_shadow = step( compare , distribution.x );
		if (hard_shadow != 1.0 ) {
			float distance = compare - distribution.x ;
			float variance = max( 0.00000, distribution.y * distribution.y );
			float softness_probability = variance / (variance + distance * distance );			softness_probability = clamp( ( softness_probability - 0.3 ) / ( 0.95 - 0.3 ), 0.0, 1.0 );			occlusion = clamp( max( hard_shadow, softness_probability ), 0.0, 1.0 );
		}
		return occlusion;
	}
	float getShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord ) {
		float shadow = 1.0;
		shadowCoord.xyz /= shadowCoord.w;
		shadowCoord.z += shadowBias;
		bool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;
		bool frustumTest = inFrustum && shadowCoord.z <= 1.0;
		if ( frustumTest ) {
		#if defined( SHADOWMAP_TYPE_PCF )
			vec2 texelSize = vec2( 1.0 ) / shadowMapSize;
			float dx0 = - texelSize.x * shadowRadius;
			float dy0 = - texelSize.y * shadowRadius;
			float dx1 = + texelSize.x * shadowRadius;
			float dy1 = + texelSize.y * shadowRadius;
			float dx2 = dx0 / 2.0;
			float dy2 = dy0 / 2.0;
			float dx3 = dx1 / 2.0;
			float dy3 = dy1 / 2.0;
			shadow = (
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy1 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy1 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy1 ), shadowCoord.z )
			) * ( 1.0 / 17.0 );
		#elif defined( SHADOWMAP_TYPE_PCF_SOFT )
			vec2 texelSize = vec2( 1.0 ) / shadowMapSize;
			float dx = texelSize.x;
			float dy = texelSize.y;
			vec2 uv = shadowCoord.xy;
			vec2 f = fract( uv * shadowMapSize + 0.5 );
			uv -= f * texelSize;
			shadow = (
				texture2DCompare( shadowMap, uv, shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + vec2( dx, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + vec2( 0.0, dy ), shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + texelSize, shadowCoord.z ) +
				mix( texture2DCompare( shadowMap, uv + vec2( -dx, 0.0 ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 0.0 ), shadowCoord.z ),
					 f.x ) +
				mix( texture2DCompare( shadowMap, uv + vec2( -dx, dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, dy ), shadowCoord.z ),
					 f.x ) +
				mix( texture2DCompare( shadowMap, uv + vec2( 0.0, -dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 0.0, 2.0 * dy ), shadowCoord.z ),
					 f.y ) +
				mix( texture2DCompare( shadowMap, uv + vec2( dx, -dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( dx, 2.0 * dy ), shadowCoord.z ),
					 f.y ) +
				mix( mix( texture2DCompare( shadowMap, uv + vec2( -dx, -dy ), shadowCoord.z ),
						  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, -dy ), shadowCoord.z ),
						  f.x ),
					 mix( texture2DCompare( shadowMap, uv + vec2( -dx, 2.0 * dy ), shadowCoord.z ),
						  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 2.0 * dy ), shadowCoord.z ),
						  f.x ),
					 f.y )
			) * ( 1.0 / 9.0 );
		#elif defined( SHADOWMAP_TYPE_VSM )
			shadow = VSMShadow( shadowMap, shadowCoord.xy, shadowCoord.z );
		#else
			shadow = texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z );
		#endif
		}
		return mix( 1.0, shadow, shadowIntensity );
	}
	vec2 cubeToUV( vec3 v, float texelSizeY ) {
		vec3 absV = abs( v );
		float scaleToCube = 1.0 / max( absV.x, max( absV.y, absV.z ) );
		absV *= scaleToCube;
		v *= scaleToCube * ( 1.0 - 2.0 * texelSizeY );
		vec2 planar = v.xy;
		float almostATexel = 1.5 * texelSizeY;
		float almostOne = 1.0 - almostATexel;
		if ( absV.z >= almostOne ) {
			if ( v.z > 0.0 )
				planar.x = 4.0 - v.x;
		} else if ( absV.x >= almostOne ) {
			float signX = sign( v.x );
			planar.x = v.z * signX + 2.0 * signX;
		} else if ( absV.y >= almostOne ) {
			float signY = sign( v.y );
			planar.x = v.x + 2.0 * signY + 2.0;
			planar.y = v.z * signY - 2.0;
		}
		return vec2( 0.125, 0.25 ) * planar + vec2( 0.375, 0.75 );
	}
	float getPointShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord, float shadowCameraNear, float shadowCameraFar ) {
		float shadow = 1.0;
		vec3 lightToPosition = shadowCoord.xyz;
		
		float lightToPositionLength = length( lightToPosition );
		if ( lightToPositionLength - shadowCameraFar <= 0.0 && lightToPositionLength - shadowCameraNear >= 0.0 ) {
			float dp = ( lightToPositionLength - shadowCameraNear ) / ( shadowCameraFar - shadowCameraNear );			dp += shadowBias;
			vec3 bd3D = normalize( lightToPosition );
			vec2 texelSize = vec2( 1.0 ) / ( shadowMapSize * vec2( 4.0, 2.0 ) );
			#if defined( SHADOWMAP_TYPE_PCF ) || defined( SHADOWMAP_TYPE_PCF_SOFT ) || defined( SHADOWMAP_TYPE_VSM )
				vec2 offset = vec2( - 1, 1 ) * shadowRadius * texelSize.y;
				shadow = (
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxx, texelSize.y ), dp )
				) * ( 1.0 / 9.0 );
			#else
				shadow = texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp );
			#endif
		}
		return mix( 1.0, shadow, shadowIntensity );
	}
#endif`, bH = `#if NUM_SPOT_LIGHT_COORDS > 0
	uniform mat4 spotLightMatrix[ NUM_SPOT_LIGHT_COORDS ];
	varying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];
#endif
#ifdef USE_SHADOWMAP
	#if NUM_DIR_LIGHT_SHADOWS > 0
		uniform mat4 directionalShadowMatrix[ NUM_DIR_LIGHT_SHADOWS ];
		varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];
		struct DirectionalLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};
		uniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];
	#endif
	#if NUM_SPOT_LIGHT_SHADOWS > 0
		struct SpotLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};
		uniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];
	#endif
	#if NUM_POINT_LIGHT_SHADOWS > 0
		uniform mat4 pointShadowMatrix[ NUM_POINT_LIGHT_SHADOWS ];
		varying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];
		struct PointLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
			float shadowCameraNear;
			float shadowCameraFar;
		};
		uniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];
	#endif
#endif`, MH = `#if ( defined( USE_SHADOWMAP ) && ( NUM_DIR_LIGHT_SHADOWS > 0 || NUM_POINT_LIGHT_SHADOWS > 0 ) ) || ( NUM_SPOT_LIGHT_COORDS > 0 )
	vec3 shadowWorldNormal = inverseTransformDirection( transformedNormal, viewMatrix );
	vec4 shadowWorldPosition;
#endif
#if defined( USE_SHADOWMAP )
	#if NUM_DIR_LIGHT_SHADOWS > 0
		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {
			shadowWorldPosition = worldPosition + vec4( shadowWorldNormal * directionalLightShadows[ i ].shadowNormalBias, 0 );
			vDirectionalShadowCoord[ i ] = directionalShadowMatrix[ i ] * shadowWorldPosition;
		}
		#pragma unroll_loop_end
	#endif
	#if NUM_POINT_LIGHT_SHADOWS > 0
		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {
			shadowWorldPosition = worldPosition + vec4( shadowWorldNormal * pointLightShadows[ i ].shadowNormalBias, 0 );
			vPointShadowCoord[ i ] = pointShadowMatrix[ i ] * shadowWorldPosition;
		}
		#pragma unroll_loop_end
	#endif
#endif
#if NUM_SPOT_LIGHT_COORDS > 0
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHT_COORDS; i ++ ) {
		shadowWorldPosition = worldPosition;
		#if ( defined( USE_SHADOWMAP ) && UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
			shadowWorldPosition.xyz += shadowWorldNormal * spotLightShadows[ i ].shadowNormalBias;
		#endif
		vSpotLightCoord[ i ] = spotLightMatrix[ i ] * shadowWorldPosition;
	}
	#pragma unroll_loop_end
#endif`, SH = `float getShadowMask() {
	float shadow = 1.0;
	#ifdef USE_SHADOWMAP
	#if NUM_DIR_LIGHT_SHADOWS > 0
	DirectionalLightShadow directionalLight;
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {
		directionalLight = directionalLightShadows[ i ];
		shadow *= receiveShadow ? getShadow( directionalShadowMap[ i ], directionalLight.shadowMapSize, directionalLight.shadowIntensity, directionalLight.shadowBias, directionalLight.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;
	}
	#pragma unroll_loop_end
	#endif
	#if NUM_SPOT_LIGHT_SHADOWS > 0
	SpotLightShadow spotLight;
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHT_SHADOWS; i ++ ) {
		spotLight = spotLightShadows[ i ];
		shadow *= receiveShadow ? getShadow( spotShadowMap[ i ], spotLight.shadowMapSize, spotLight.shadowIntensity, spotLight.shadowBias, spotLight.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;
	}
	#pragma unroll_loop_end
	#endif
	#if NUM_POINT_LIGHT_SHADOWS > 0
	PointLightShadow pointLight;
	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {
		pointLight = pointLightShadows[ i ];
		shadow *= receiveShadow ? getPointShadow( pointShadowMap[ i ], pointLight.shadowMapSize, pointLight.shadowIntensity, pointLight.shadowBias, pointLight.shadowRadius, vPointShadowCoord[ i ], pointLight.shadowCameraNear, pointLight.shadowCameraFar ) : 1.0;
	}
	#pragma unroll_loop_end
	#endif
	#endif
	return shadow;
}`, wH = `#ifdef USE_SKINNING
	mat4 boneMatX = getBoneMatrix( skinIndex.x );
	mat4 boneMatY = getBoneMatrix( skinIndex.y );
	mat4 boneMatZ = getBoneMatrix( skinIndex.z );
	mat4 boneMatW = getBoneMatrix( skinIndex.w );
#endif`, EH = `#ifdef USE_SKINNING
	uniform mat4 bindMatrix;
	uniform mat4 bindMatrixInverse;
	uniform highp sampler2D boneTexture;
	mat4 getBoneMatrix( const in float i ) {
		int size = textureSize( boneTexture, 0 ).x;
		int j = int( i ) * 4;
		int x = j % size;
		int y = j / size;
		vec4 v1 = texelFetch( boneTexture, ivec2( x, y ), 0 );
		vec4 v2 = texelFetch( boneTexture, ivec2( x + 1, y ), 0 );
		vec4 v3 = texelFetch( boneTexture, ivec2( x + 2, y ), 0 );
		vec4 v4 = texelFetch( boneTexture, ivec2( x + 3, y ), 0 );
		return mat4( v1, v2, v3, v4 );
	}
#endif`, TH = `#ifdef USE_SKINNING
	vec4 skinVertex = bindMatrix * vec4( transformed, 1.0 );
	vec4 skinned = vec4( 0.0 );
	skinned += boneMatX * skinVertex * skinWeight.x;
	skinned += boneMatY * skinVertex * skinWeight.y;
	skinned += boneMatZ * skinVertex * skinWeight.z;
	skinned += boneMatW * skinVertex * skinWeight.w;
	transformed = ( bindMatrixInverse * skinned ).xyz;
#endif`, CH = `#ifdef USE_SKINNING
	mat4 skinMatrix = mat4( 0.0 );
	skinMatrix += skinWeight.x * boneMatX;
	skinMatrix += skinWeight.y * boneMatY;
	skinMatrix += skinWeight.z * boneMatZ;
	skinMatrix += skinWeight.w * boneMatW;
	skinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;
	objectNormal = vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz;
	#ifdef USE_TANGENT
		objectTangent = vec4( skinMatrix * vec4( objectTangent, 0.0 ) ).xyz;
	#endif
#endif`, _H = `float specularStrength;
#ifdef USE_SPECULARMAP
	vec4 texelSpecular = texture2D( specularMap, vSpecularMapUv );
	specularStrength = texelSpecular.r;
#else
	specularStrength = 1.0;
#endif`, PH = `#ifdef USE_SPECULARMAP
	uniform sampler2D specularMap;
#endif`, RH = `#if defined( TONE_MAPPING )
	gl_FragColor.rgb = toneMapping( gl_FragColor.rgb );
#endif`, DH = `#ifndef saturate
#define saturate( a ) clamp( a, 0.0, 1.0 )
#endif
uniform float toneMappingExposure;
vec3 LinearToneMapping( vec3 color ) {
	return saturate( toneMappingExposure * color );
}
vec3 ReinhardToneMapping( vec3 color ) {
	color *= toneMappingExposure;
	return saturate( color / ( vec3( 1.0 ) + color ) );
}
vec3 OptimizedCineonToneMapping( vec3 color ) {
	color *= toneMappingExposure;
	color = max( vec3( 0.0 ), color - 0.004 );
	return pow( ( color * ( 6.2 * color + 0.5 ) ) / ( color * ( 6.2 * color + 1.7 ) + 0.06 ), vec3( 2.2 ) );
}
vec3 RRTAndODTFit( vec3 v ) {
	vec3 a = v * ( v + 0.0245786 ) - 0.000090537;
	vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;
	return a / b;
}
vec3 ACESFilmicToneMapping( vec3 color ) {
	const mat3 ACESInputMat = mat3(
		vec3( 0.59719, 0.07600, 0.02840 ),		vec3( 0.35458, 0.90834, 0.13383 ),
		vec3( 0.04823, 0.01566, 0.83777 )
	);
	const mat3 ACESOutputMat = mat3(
		vec3(  1.60475, -0.10208, -0.00327 ),		vec3( -0.53108,  1.10813, -0.07276 ),
		vec3( -0.07367, -0.00605,  1.07602 )
	);
	color *= toneMappingExposure / 0.6;
	color = ACESInputMat * color;
	color = RRTAndODTFit( color );
	color = ACESOutputMat * color;
	return saturate( color );
}
const mat3 LINEAR_REC2020_TO_LINEAR_SRGB = mat3(
	vec3( 1.6605, - 0.1246, - 0.0182 ),
	vec3( - 0.5876, 1.1329, - 0.1006 ),
	vec3( - 0.0728, - 0.0083, 1.1187 )
);
const mat3 LINEAR_SRGB_TO_LINEAR_REC2020 = mat3(
	vec3( 0.6274, 0.0691, 0.0164 ),
	vec3( 0.3293, 0.9195, 0.0880 ),
	vec3( 0.0433, 0.0113, 0.8956 )
);
vec3 agxDefaultContrastApprox( vec3 x ) {
	vec3 x2 = x * x;
	vec3 x4 = x2 * x2;
	return + 15.5 * x4 * x2
		- 40.14 * x4 * x
		+ 31.96 * x4
		- 6.868 * x2 * x
		+ 0.4298 * x2
		+ 0.1191 * x
		- 0.00232;
}
vec3 AgXToneMapping( vec3 color ) {
	const mat3 AgXInsetMatrix = mat3(
		vec3( 0.856627153315983, 0.137318972929847, 0.11189821299995 ),
		vec3( 0.0951212405381588, 0.761241990602591, 0.0767994186031903 ),
		vec3( 0.0482516061458583, 0.101439036467562, 0.811302368396859 )
	);
	const mat3 AgXOutsetMatrix = mat3(
		vec3( 1.1271005818144368, - 0.1413297634984383, - 0.14132976349843826 ),
		vec3( - 0.11060664309660323, 1.157823702216272, - 0.11060664309660294 ),
		vec3( - 0.016493938717834573, - 0.016493938717834257, 1.2519364065950405 )
	);
	const float AgxMinEv = - 12.47393;	const float AgxMaxEv = 4.026069;
	color *= toneMappingExposure;
	color = LINEAR_SRGB_TO_LINEAR_REC2020 * color;
	color = AgXInsetMatrix * color;
	color = max( color, 1e-10 );	color = log2( color );
	color = ( color - AgxMinEv ) / ( AgxMaxEv - AgxMinEv );
	color = clamp( color, 0.0, 1.0 );
	color = agxDefaultContrastApprox( color );
	color = AgXOutsetMatrix * color;
	color = pow( max( vec3( 0.0 ), color ), vec3( 2.2 ) );
	color = LINEAR_REC2020_TO_LINEAR_SRGB * color;
	color = clamp( color, 0.0, 1.0 );
	return color;
}
vec3 NeutralToneMapping( vec3 color ) {
	const float StartCompression = 0.8 - 0.04;
	const float Desaturation = 0.15;
	color *= toneMappingExposure;
	float x = min( color.r, min( color.g, color.b ) );
	float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
	color -= offset;
	float peak = max( color.r, max( color.g, color.b ) );
	if ( peak < StartCompression ) return color;
	float d = 1. - StartCompression;
	float newPeak = 1. - d * d / ( peak + d - StartCompression );
	color *= newPeak / peak;
	float g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. );
	return mix( color, vec3( newPeak ), g );
}
vec3 CustomToneMapping( vec3 color ) { return color; }`, LH = `#ifdef USE_TRANSMISSION
	material.transmission = transmission;
	material.transmissionAlpha = 1.0;
	material.thickness = thickness;
	material.attenuationDistance = attenuationDistance;
	material.attenuationColor = attenuationColor;
	#ifdef USE_TRANSMISSIONMAP
		material.transmission *= texture2D( transmissionMap, vTransmissionMapUv ).r;
	#endif
	#ifdef USE_THICKNESSMAP
		material.thickness *= texture2D( thicknessMap, vThicknessMapUv ).g;
	#endif
	vec3 pos = vWorldPosition;
	vec3 v = normalize( cameraPosition - pos );
	vec3 n = inverseTransformDirection( normal, viewMatrix );
	vec4 transmitted = getIBLVolumeRefraction(
		n, v, material.roughness, material.diffuseColor, material.specularColor, material.specularF90,
		pos, modelMatrix, viewMatrix, projectionMatrix, material.dispersion, material.ior, material.thickness,
		material.attenuationColor, material.attenuationDistance );
	material.transmissionAlpha = mix( material.transmissionAlpha, transmitted.a, material.transmission );
	totalDiffuse = mix( totalDiffuse, transmitted.rgb, material.transmission );
#endif`, IH = `#ifdef USE_TRANSMISSION
	uniform float transmission;
	uniform float thickness;
	uniform float attenuationDistance;
	uniform vec3 attenuationColor;
	#ifdef USE_TRANSMISSIONMAP
		uniform sampler2D transmissionMap;
	#endif
	#ifdef USE_THICKNESSMAP
		uniform sampler2D thicknessMap;
	#endif
	uniform vec2 transmissionSamplerSize;
	uniform sampler2D transmissionSamplerMap;
	uniform mat4 modelMatrix;
	uniform mat4 projectionMatrix;
	varying vec3 vWorldPosition;
	float w0( float a ) {
		return ( 1.0 / 6.0 ) * ( a * ( a * ( - a + 3.0 ) - 3.0 ) + 1.0 );
	}
	float w1( float a ) {
		return ( 1.0 / 6.0 ) * ( a *  a * ( 3.0 * a - 6.0 ) + 4.0 );
	}
	float w2( float a ){
		return ( 1.0 / 6.0 ) * ( a * ( a * ( - 3.0 * a + 3.0 ) + 3.0 ) + 1.0 );
	}
	float w3( float a ) {
		return ( 1.0 / 6.0 ) * ( a * a * a );
	}
	float g0( float a ) {
		return w0( a ) + w1( a );
	}
	float g1( float a ) {
		return w2( a ) + w3( a );
	}
	float h0( float a ) {
		return - 1.0 + w1( a ) / ( w0( a ) + w1( a ) );
	}
	float h1( float a ) {
		return 1.0 + w3( a ) / ( w2( a ) + w3( a ) );
	}
	vec4 bicubic( sampler2D tex, vec2 uv, vec4 texelSize, float lod ) {
		uv = uv * texelSize.zw + 0.5;
		vec2 iuv = floor( uv );
		vec2 fuv = fract( uv );
		float g0x = g0( fuv.x );
		float g1x = g1( fuv.x );
		float h0x = h0( fuv.x );
		float h1x = h1( fuv.x );
		float h0y = h0( fuv.y );
		float h1y = h1( fuv.y );
		vec2 p0 = ( vec2( iuv.x + h0x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;
		vec2 p1 = ( vec2( iuv.x + h1x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;
		vec2 p2 = ( vec2( iuv.x + h0x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;
		vec2 p3 = ( vec2( iuv.x + h1x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;
		return g0( fuv.y ) * ( g0x * textureLod( tex, p0, lod ) + g1x * textureLod( tex, p1, lod ) ) +
			g1( fuv.y ) * ( g0x * textureLod( tex, p2, lod ) + g1x * textureLod( tex, p3, lod ) );
	}
	vec4 textureBicubic( sampler2D sampler, vec2 uv, float lod ) {
		vec2 fLodSize = vec2( textureSize( sampler, int( lod ) ) );
		vec2 cLodSize = vec2( textureSize( sampler, int( lod + 1.0 ) ) );
		vec2 fLodSizeInv = 1.0 / fLodSize;
		vec2 cLodSizeInv = 1.0 / cLodSize;
		vec4 fSample = bicubic( sampler, uv, vec4( fLodSizeInv, fLodSize ), floor( lod ) );
		vec4 cSample = bicubic( sampler, uv, vec4( cLodSizeInv, cLodSize ), ceil( lod ) );
		return mix( fSample, cSample, fract( lod ) );
	}
	vec3 getVolumeTransmissionRay( const in vec3 n, const in vec3 v, const in float thickness, const in float ior, const in mat4 modelMatrix ) {
		vec3 refractionVector = refract( - v, normalize( n ), 1.0 / ior );
		vec3 modelScale;
		modelScale.x = length( vec3( modelMatrix[ 0 ].xyz ) );
		modelScale.y = length( vec3( modelMatrix[ 1 ].xyz ) );
		modelScale.z = length( vec3( modelMatrix[ 2 ].xyz ) );
		return normalize( refractionVector ) * thickness * modelScale;
	}
	float applyIorToRoughness( const in float roughness, const in float ior ) {
		return roughness * clamp( ior * 2.0 - 2.0, 0.0, 1.0 );
	}
	vec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {
		float lod = log2( transmissionSamplerSize.x ) * applyIorToRoughness( roughness, ior );
		return textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );
	}
	vec3 volumeAttenuation( const in float transmissionDistance, const in vec3 attenuationColor, const in float attenuationDistance ) {
		if ( isinf( attenuationDistance ) ) {
			return vec3( 1.0 );
		} else {
			vec3 attenuationCoefficient = -log( attenuationColor ) / attenuationDistance;
			vec3 transmittance = exp( - attenuationCoefficient * transmissionDistance );			return transmittance;
		}
	}
	vec4 getIBLVolumeRefraction( const in vec3 n, const in vec3 v, const in float roughness, const in vec3 diffuseColor,
		const in vec3 specularColor, const in float specularF90, const in vec3 position, const in mat4 modelMatrix,
		const in mat4 viewMatrix, const in mat4 projMatrix, const in float dispersion, const in float ior, const in float thickness,
		const in vec3 attenuationColor, const in float attenuationDistance ) {
		vec4 transmittedLight;
		vec3 transmittance;
		#ifdef USE_DISPERSION
			float halfSpread = ( ior - 1.0 ) * 0.025 * dispersion;
			vec3 iors = vec3( ior - halfSpread, ior, ior + halfSpread );
			for ( int i = 0; i < 3; i ++ ) {
				vec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, iors[ i ], modelMatrix );
				vec3 refractedRayExit = position + transmissionRay;
		
				vec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );
				vec2 refractionCoords = ndcPos.xy / ndcPos.w;
				refractionCoords += 1.0;
				refractionCoords /= 2.0;
		
				vec4 transmissionSample = getTransmissionSample( refractionCoords, roughness, iors[ i ] );
				transmittedLight[ i ] = transmissionSample[ i ];
				transmittedLight.a += transmissionSample.a;
				transmittance[ i ] = diffuseColor[ i ] * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance )[ i ];
			}
			transmittedLight.a /= 3.0;
		
		#else
		
			vec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, ior, modelMatrix );
			vec3 refractedRayExit = position + transmissionRay;
			vec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );
			vec2 refractionCoords = ndcPos.xy / ndcPos.w;
			refractionCoords += 1.0;
			refractionCoords /= 2.0;
			transmittedLight = getTransmissionSample( refractionCoords, roughness, ior );
			transmittance = diffuseColor * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance );
		
		#endif
		vec3 attenuatedColor = transmittance * transmittedLight.rgb;
		vec3 F = EnvironmentBRDF( n, v, specularColor, specularF90, roughness );
		float transmittanceFactor = ( transmittance.r + transmittance.g + transmittance.b ) / 3.0;
		return vec4( ( 1.0 - F ) * attenuatedColor, 1.0 - ( 1.0 - transmittedLight.a ) * transmittanceFactor );
	}
#endif`, OH = `#if defined( USE_UV ) || defined( USE_ANISOTROPY )
	varying vec2 vUv;
#endif
#ifdef USE_MAP
	varying vec2 vMapUv;
#endif
#ifdef USE_ALPHAMAP
	varying vec2 vAlphaMapUv;
#endif
#ifdef USE_LIGHTMAP
	varying vec2 vLightMapUv;
#endif
#ifdef USE_AOMAP
	varying vec2 vAoMapUv;
#endif
#ifdef USE_BUMPMAP
	varying vec2 vBumpMapUv;
#endif
#ifdef USE_NORMALMAP
	varying vec2 vNormalMapUv;
#endif
#ifdef USE_EMISSIVEMAP
	varying vec2 vEmissiveMapUv;
#endif
#ifdef USE_METALNESSMAP
	varying vec2 vMetalnessMapUv;
#endif
#ifdef USE_ROUGHNESSMAP
	varying vec2 vRoughnessMapUv;
#endif
#ifdef USE_ANISOTROPYMAP
	varying vec2 vAnisotropyMapUv;
#endif
#ifdef USE_CLEARCOATMAP
	varying vec2 vClearcoatMapUv;
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
	varying vec2 vClearcoatNormalMapUv;
#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP
	varying vec2 vClearcoatRoughnessMapUv;
#endif
#ifdef USE_IRIDESCENCEMAP
	varying vec2 vIridescenceMapUv;
#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP
	varying vec2 vIridescenceThicknessMapUv;
#endif
#ifdef USE_SHEEN_COLORMAP
	varying vec2 vSheenColorMapUv;
#endif
#ifdef USE_SHEEN_ROUGHNESSMAP
	varying vec2 vSheenRoughnessMapUv;
#endif
#ifdef USE_SPECULARMAP
	varying vec2 vSpecularMapUv;
#endif
#ifdef USE_SPECULAR_COLORMAP
	varying vec2 vSpecularColorMapUv;
#endif
#ifdef USE_SPECULAR_INTENSITYMAP
	varying vec2 vSpecularIntensityMapUv;
#endif
#ifdef USE_TRANSMISSIONMAP
	uniform mat3 transmissionMapTransform;
	varying vec2 vTransmissionMapUv;
#endif
#ifdef USE_THICKNESSMAP
	uniform mat3 thicknessMapTransform;
	varying vec2 vThicknessMapUv;
#endif`, NH = `#if defined( USE_UV ) || defined( USE_ANISOTROPY )
	varying vec2 vUv;
#endif
#ifdef USE_MAP
	uniform mat3 mapTransform;
	varying vec2 vMapUv;
#endif
#ifdef USE_ALPHAMAP
	uniform mat3 alphaMapTransform;
	varying vec2 vAlphaMapUv;
#endif
#ifdef USE_LIGHTMAP
	uniform mat3 lightMapTransform;
	varying vec2 vLightMapUv;
#endif
#ifdef USE_AOMAP
	uniform mat3 aoMapTransform;
	varying vec2 vAoMapUv;
#endif
#ifdef USE_BUMPMAP
	uniform mat3 bumpMapTransform;
	varying vec2 vBumpMapUv;
#endif
#ifdef USE_NORMALMAP
	uniform mat3 normalMapTransform;
	varying vec2 vNormalMapUv;
#endif
#ifdef USE_DISPLACEMENTMAP
	uniform mat3 displacementMapTransform;
	varying vec2 vDisplacementMapUv;
#endif
#ifdef USE_EMISSIVEMAP
	uniform mat3 emissiveMapTransform;
	varying vec2 vEmissiveMapUv;
#endif
#ifdef USE_METALNESSMAP
	uniform mat3 metalnessMapTransform;
	varying vec2 vMetalnessMapUv;
#endif
#ifdef USE_ROUGHNESSMAP
	uniform mat3 roughnessMapTransform;
	varying vec2 vRoughnessMapUv;
#endif
#ifdef USE_ANISOTROPYMAP
	uniform mat3 anisotropyMapTransform;
	varying vec2 vAnisotropyMapUv;
#endif
#ifdef USE_CLEARCOATMAP
	uniform mat3 clearcoatMapTransform;
	varying vec2 vClearcoatMapUv;
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
	uniform mat3 clearcoatNormalMapTransform;
	varying vec2 vClearcoatNormalMapUv;
#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP
	uniform mat3 clearcoatRoughnessMapTransform;
	varying vec2 vClearcoatRoughnessMapUv;
#endif
#ifdef USE_SHEEN_COLORMAP
	uniform mat3 sheenColorMapTransform;
	varying vec2 vSheenColorMapUv;
#endif
#ifdef USE_SHEEN_ROUGHNESSMAP
	uniform mat3 sheenRoughnessMapTransform;
	varying vec2 vSheenRoughnessMapUv;
#endif
#ifdef USE_IRIDESCENCEMAP
	uniform mat3 iridescenceMapTransform;
	varying vec2 vIridescenceMapUv;
#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP
	uniform mat3 iridescenceThicknessMapTransform;
	varying vec2 vIridescenceThicknessMapUv;
#endif
#ifdef USE_SPECULARMAP
	uniform mat3 specularMapTransform;
	varying vec2 vSpecularMapUv;
#endif
#ifdef USE_SPECULAR_COLORMAP
	uniform mat3 specularColorMapTransform;
	varying vec2 vSpecularColorMapUv;
#endif
#ifdef USE_SPECULAR_INTENSITYMAP
	uniform mat3 specularIntensityMapTransform;
	varying vec2 vSpecularIntensityMapUv;
#endif
#ifdef USE_TRANSMISSIONMAP
	uniform mat3 transmissionMapTransform;
	varying vec2 vTransmissionMapUv;
#endif
#ifdef USE_THICKNESSMAP
	uniform mat3 thicknessMapTransform;
	varying vec2 vThicknessMapUv;
#endif`, BH = `#if defined( USE_UV ) || defined( USE_ANISOTROPY )
	vUv = vec3( uv, 1 ).xy;
#endif
#ifdef USE_MAP
	vMapUv = ( mapTransform * vec3( MAP_UV, 1 ) ).xy;
#endif
#ifdef USE_ALPHAMAP
	vAlphaMapUv = ( alphaMapTransform * vec3( ALPHAMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_LIGHTMAP
	vLightMapUv = ( lightMapTransform * vec3( LIGHTMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_AOMAP
	vAoMapUv = ( aoMapTransform * vec3( AOMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_BUMPMAP
	vBumpMapUv = ( bumpMapTransform * vec3( BUMPMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_NORMALMAP
	vNormalMapUv = ( normalMapTransform * vec3( NORMALMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_DISPLACEMENTMAP
	vDisplacementMapUv = ( displacementMapTransform * vec3( DISPLACEMENTMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_EMISSIVEMAP
	vEmissiveMapUv = ( emissiveMapTransform * vec3( EMISSIVEMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_METALNESSMAP
	vMetalnessMapUv = ( metalnessMapTransform * vec3( METALNESSMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_ROUGHNESSMAP
	vRoughnessMapUv = ( roughnessMapTransform * vec3( ROUGHNESSMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_ANISOTROPYMAP
	vAnisotropyMapUv = ( anisotropyMapTransform * vec3( ANISOTROPYMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOATMAP
	vClearcoatMapUv = ( clearcoatMapTransform * vec3( CLEARCOATMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
	vClearcoatNormalMapUv = ( clearcoatNormalMapTransform * vec3( CLEARCOAT_NORMALMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP
	vClearcoatRoughnessMapUv = ( clearcoatRoughnessMapTransform * vec3( CLEARCOAT_ROUGHNESSMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_IRIDESCENCEMAP
	vIridescenceMapUv = ( iridescenceMapTransform * vec3( IRIDESCENCEMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP
	vIridescenceThicknessMapUv = ( iridescenceThicknessMapTransform * vec3( IRIDESCENCE_THICKNESSMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_SHEEN_COLORMAP
	vSheenColorMapUv = ( sheenColorMapTransform * vec3( SHEEN_COLORMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_SHEEN_ROUGHNESSMAP
	vSheenRoughnessMapUv = ( sheenRoughnessMapTransform * vec3( SHEEN_ROUGHNESSMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_SPECULARMAP
	vSpecularMapUv = ( specularMapTransform * vec3( SPECULARMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_SPECULAR_COLORMAP
	vSpecularColorMapUv = ( specularColorMapTransform * vec3( SPECULAR_COLORMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_SPECULAR_INTENSITYMAP
	vSpecularIntensityMapUv = ( specularIntensityMapTransform * vec3( SPECULAR_INTENSITYMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_TRANSMISSIONMAP
	vTransmissionMapUv = ( transmissionMapTransform * vec3( TRANSMISSIONMAP_UV, 1 ) ).xy;
#endif
#ifdef USE_THICKNESSMAP
	vThicknessMapUv = ( thicknessMapTransform * vec3( THICKNESSMAP_UV, 1 ) ).xy;
#endif`, FH = `#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0
	vec4 worldPosition = vec4( transformed, 1.0 );
	#ifdef USE_BATCHING
		worldPosition = batchingMatrix * worldPosition;
	#endif
	#ifdef USE_INSTANCING
		worldPosition = instanceMatrix * worldPosition;
	#endif
	worldPosition = modelMatrix * worldPosition;
#endif`;
const UH = `varying vec2 vUv;
uniform mat3 uvTransform;
void main() {
	vUv = ( uvTransform * vec3( uv, 1 ) ).xy;
	gl_Position = vec4( position.xy, 1.0, 1.0 );
}`, zH = `uniform sampler2D t2D;
uniform float backgroundIntensity;
varying vec2 vUv;
void main() {
	vec4 texColor = texture2D( t2D, vUv );
	#ifdef DECODE_VIDEO_TEXTURE
		texColor = vec4( mix( pow( texColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), texColor.rgb * 0.0773993808, vec3( lessThanEqual( texColor.rgb, vec3( 0.04045 ) ) ) ), texColor.w );
	#endif
	texColor.rgb *= backgroundIntensity;
	gl_FragColor = texColor;
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
}`, kH = `varying vec3 vWorldDirection;
#include <common>
void main() {
	vWorldDirection = transformDirection( position, modelMatrix );
	#include <begin_vertex>
	#include <project_vertex>
	gl_Position.z = gl_Position.w;
}`, GH = `#ifdef ENVMAP_TYPE_CUBE
	uniform samplerCube envMap;
#elif defined( ENVMAP_TYPE_CUBE_UV )
	uniform sampler2D envMap;
#endif
uniform float flipEnvMap;
uniform float backgroundBlurriness;
uniform float backgroundIntensity;
uniform mat3 backgroundRotation;
varying vec3 vWorldDirection;
#include <cube_uv_reflection_fragment>
void main() {
	#ifdef ENVMAP_TYPE_CUBE
		vec4 texColor = textureCube( envMap, backgroundRotation * vec3( flipEnvMap * vWorldDirection.x, vWorldDirection.yz ) );
	#elif defined( ENVMAP_TYPE_CUBE_UV )
		vec4 texColor = textureCubeUV( envMap, backgroundRotation * vWorldDirection, backgroundBlurriness );
	#else
		vec4 texColor = vec4( 0.0, 0.0, 0.0, 1.0 );
	#endif
	texColor.rgb *= backgroundIntensity;
	gl_FragColor = texColor;
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
}`, HH = `varying vec3 vWorldDirection;
#include <common>
void main() {
	vWorldDirection = transformDirection( position, modelMatrix );
	#include <begin_vertex>
	#include <project_vertex>
	gl_Position.z = gl_Position.w;
}`, VH = `uniform samplerCube tCube;
uniform float tFlip;
uniform float opacity;
varying vec3 vWorldDirection;
void main() {
	vec4 texColor = textureCube( tCube, vec3( tFlip * vWorldDirection.x, vWorldDirection.yz ) );
	gl_FragColor = texColor;
	gl_FragColor.a *= opacity;
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
}`, WH = `#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
varying vec2 vHighPrecisionZW;
void main() {
	#include <uv_vertex>
	#include <batching_vertex>
	#include <skinbase_vertex>
	#include <morphinstance_vertex>
	#ifdef USE_DISPLACEMENTMAP
		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinnormal_vertex>
	#endif
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vHighPrecisionZW = gl_Position.zw;
}`, XH = `#if DEPTH_PACKING == 3200
	uniform float opacity;
#endif
#include <common>
#include <packing>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
varying vec2 vHighPrecisionZW;
void main() {
	vec4 diffuseColor = vec4( 1.0 );
	#include <clipping_planes_fragment>
	#if DEPTH_PACKING == 3200
		diffuseColor.a = opacity;
	#endif
	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <logdepthbuf_fragment>
	float fragCoordZ = 0.5 * vHighPrecisionZW[0] / vHighPrecisionZW[1] + 0.5;
	#if DEPTH_PACKING == 3200
		gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );
	#elif DEPTH_PACKING == 3201
		gl_FragColor = packDepthToRGBA( fragCoordZ );
	#endif
}`, jH = `#define DISTANCE
varying vec3 vWorldPosition;
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <batching_vertex>
	#include <skinbase_vertex>
	#include <morphinstance_vertex>
	#ifdef USE_DISPLACEMENTMAP
		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinnormal_vertex>
	#endif
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <worldpos_vertex>
	#include <clipping_planes_vertex>
	vWorldPosition = worldPosition.xyz;
}`, qH = `#define DISTANCE
uniform vec3 referencePosition;
uniform float nearDistance;
uniform float farDistance;
varying vec3 vWorldPosition;
#include <common>
#include <packing>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <clipping_planes_pars_fragment>
void main () {
	vec4 diffuseColor = vec4( 1.0 );
	#include <clipping_planes_fragment>
	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	float dist = length( vWorldPosition - referencePosition );
	dist = ( dist - nearDistance ) / ( farDistance - nearDistance );
	dist = saturate( dist );
	gl_FragColor = packDepthToRGBA( dist );
}`, YH = `varying vec3 vWorldDirection;
#include <common>
void main() {
	vWorldDirection = transformDirection( position, modelMatrix );
	#include <begin_vertex>
	#include <project_vertex>
}`, ZH = `uniform sampler2D tEquirect;
varying vec3 vWorldDirection;
#include <common>
void main() {
	vec3 direction = normalize( vWorldDirection );
	vec2 sampleUV = equirectUv( direction );
	gl_FragColor = texture2D( tEquirect, sampleUV );
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
}`, KH = `uniform float scale;
attribute float lineDistance;
varying float vLineDistance;
#include <common>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	vLineDistance = scale * lineDistance;
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>
}`, JH = `uniform vec3 diffuse;
uniform float opacity;
uniform float dashSize;
uniform float totalSize;
varying float vLineDistance;
#include <common>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	if ( mod( vLineDistance, totalSize ) > dashSize ) {
		discard;
	}
	vec3 outgoingLight = vec3( 0.0 );
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	outgoingLight = diffuseColor.rgb;
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
}`, QH = `#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#if defined ( USE_ENVMAP ) || defined ( USE_SKINNING )
		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinbase_vertex>
		#include <skinnormal_vertex>
		#include <defaultnormal_vertex>
	#endif
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <fog_vertex>
}`, $H = `uniform vec3 diffuse;
uniform float opacity;
#ifndef FLAT_SHADED
	varying vec3 vNormal;
#endif
#include <common>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	#ifdef USE_LIGHTMAP
		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		reflectedLight.indirectDiffuse += lightMapTexel.rgb * lightMapIntensity * RECIPROCAL_PI;
	#else
		reflectedLight.indirectDiffuse += vec3( 1.0 );
	#endif
	#include <aomap_fragment>
	reflectedLight.indirectDiffuse *= diffuseColor.rgb;
	vec3 outgoingLight = reflectedLight.indirectDiffuse;
	#include <envmap_fragment>
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, eV = `#define LAMBERT
varying vec3 vViewPosition;
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vViewPosition = - mvPosition.xyz;
	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
}`, tV = `#define LAMBERT
uniform vec3 diffuse;
uniform vec3 emissive;
uniform float opacity;
#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_lambert_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>
	#include <lights_lambert_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>
	#include <aomap_fragment>
	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;
	#include <envmap_fragment>
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, iV = `#define MATCAP
varying vec3 vViewPosition;
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <displacementmap_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>
	vViewPosition = - mvPosition.xyz;
}`, nV = `#define MATCAP
uniform vec3 diffuse;
uniform float opacity;
uniform sampler2D matcap;
varying vec3 vViewPosition;
#include <common>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <normal_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	vec3 viewDir = normalize( vViewPosition );
	vec3 x = normalize( vec3( viewDir.z, 0.0, - viewDir.x ) );
	vec3 y = cross( viewDir, x );
	vec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5;
	#ifdef USE_MATCAP
		vec4 matcapColor = texture2D( matcap, uv );
	#else
		vec4 matcapColor = vec4( vec3( mix( 0.2, 0.8, uv.y ) ), 1.0 );
	#endif
	vec3 outgoingLight = diffuseColor.rgb * matcapColor.rgb;
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, sV = `#define NORMAL
#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )
	varying vec3 vViewPosition;
#endif
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )
	vViewPosition = - mvPosition.xyz;
#endif
}`, rV = `#define NORMAL
uniform float opacity;
#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )
	varying vec3 vViewPosition;
#endif
#include <packing>
#include <uv_pars_fragment>
#include <normal_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( 0.0, 0.0, 0.0, opacity );
	#include <clipping_planes_fragment>
	#include <logdepthbuf_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	gl_FragColor = vec4( packNormalToRGB( normal ), diffuseColor.a );
	#ifdef OPAQUE
		gl_FragColor.a = 1.0;
	#endif
}`, aV = `#define PHONG
varying vec3 vViewPosition;
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vViewPosition = - mvPosition.xyz;
	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
}`, oV = `#define PHONG
uniform vec3 diffuse;
uniform vec3 emissive;
uniform vec3 specular;
uniform float shininess;
uniform float opacity;
#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_phong_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>
	#include <lights_phong_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>
	#include <aomap_fragment>
	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;
	#include <envmap_fragment>
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, lV = `#define STANDARD
varying vec3 vViewPosition;
#ifdef USE_TRANSMISSION
	varying vec3 vWorldPosition;
#endif
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vViewPosition = - mvPosition.xyz;
	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
#ifdef USE_TRANSMISSION
	vWorldPosition = worldPosition.xyz;
#endif
}`, cV = `#define STANDARD
#ifdef PHYSICAL
	#define IOR
	#define USE_SPECULAR
#endif
uniform vec3 diffuse;
uniform vec3 emissive;
uniform float roughness;
uniform float metalness;
uniform float opacity;
#ifdef IOR
	uniform float ior;
#endif
#ifdef USE_SPECULAR
	uniform float specularIntensity;
	uniform vec3 specularColor;
	#ifdef USE_SPECULAR_COLORMAP
		uniform sampler2D specularColorMap;
	#endif
	#ifdef USE_SPECULAR_INTENSITYMAP
		uniform sampler2D specularIntensityMap;
	#endif
#endif
#ifdef USE_CLEARCOAT
	uniform float clearcoat;
	uniform float clearcoatRoughness;
#endif
#ifdef USE_DISPERSION
	uniform float dispersion;
#endif
#ifdef USE_IRIDESCENCE
	uniform float iridescence;
	uniform float iridescenceIOR;
	uniform float iridescenceThicknessMinimum;
	uniform float iridescenceThicknessMaximum;
#endif
#ifdef USE_SHEEN
	uniform vec3 sheenColor;
	uniform float sheenRoughness;
	#ifdef USE_SHEEN_COLORMAP
		uniform sampler2D sheenColorMap;
	#endif
	#ifdef USE_SHEEN_ROUGHNESSMAP
		uniform sampler2D sheenRoughnessMap;
	#endif
#endif
#ifdef USE_ANISOTROPY
	uniform vec2 anisotropyVector;
	#ifdef USE_ANISOTROPYMAP
		uniform sampler2D anisotropyMap;
	#endif
#endif
varying vec3 vViewPosition;
#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <iridescence_fragment>
#include <cube_uv_reflection_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_physical_pars_fragment>
#include <fog_pars_fragment>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_physical_pars_fragment>
#include <transmission_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <clearcoat_pars_fragment>
#include <iridescence_pars_fragment>
#include <roughnessmap_pars_fragment>
#include <metalnessmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <roughnessmap_fragment>
	#include <metalnessmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <clearcoat_normal_fragment_begin>
	#include <clearcoat_normal_fragment_maps>
	#include <emissivemap_fragment>
	#include <lights_physical_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>
	#include <aomap_fragment>
	vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
	vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;
	#include <transmission_fragment>
	vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;
	#ifdef USE_SHEEN
		float sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );
		outgoingLight = outgoingLight * sheenEnergyComp + sheenSpecularDirect + sheenSpecularIndirect;
	#endif
	#ifdef USE_CLEARCOAT
		float dotNVcc = saturate( dot( geometryClearcoatNormal, geometryViewDir ) );
		vec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );
		outgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * material.clearcoat;
	#endif
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, hV = `#define TOON
varying vec3 vViewPosition;
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vViewPosition = - mvPosition.xyz;
	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
}`, uV = `#define TOON
uniform vec3 diffuse;
uniform vec3 emissive;
uniform float opacity;
#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <gradientmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_toon_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>
	#include <lights_toon_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>
	#include <aomap_fragment>
	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>
}`, dV = `uniform float size;
uniform float scale;
#include <common>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
#ifdef USE_POINTS_UV
	varying vec2 vUv;
	uniform mat3 uvTransform;
#endif
void main() {
	#ifdef USE_POINTS_UV
		vUv = ( uvTransform * vec3( uv, 1 ) ).xy;
	#endif
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <project_vertex>
	gl_PointSize = size;
	#ifdef USE_SIZEATTENUATION
		bool isPerspective = isPerspectiveMatrix( projectionMatrix );
		if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );
	#endif
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <worldpos_vertex>
	#include <fog_vertex>
}`, fV = `uniform vec3 diffuse;
uniform float opacity;
#include <common>
#include <color_pars_fragment>
#include <map_particle_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	vec3 outgoingLight = vec3( 0.0 );
	#include <logdepthbuf_fragment>
	#include <map_particle_fragment>
	#include <color_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	outgoingLight = diffuseColor.rgb;
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
}`, pV = `#include <common>
#include <batching_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <shadowmap_pars_vertex>
void main() {
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
}`, mV = `uniform vec3 color;
uniform float opacity;
#include <common>
#include <packing>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <logdepthbuf_pars_fragment>
#include <shadowmap_pars_fragment>
#include <shadowmask_pars_fragment>
void main() {
	#include <logdepthbuf_fragment>
	gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
}`, gV = `uniform float rotation;
uniform vec2 center;
#include <common>
#include <uv_pars_vertex>
#include <fog_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>
void main() {
	#include <uv_vertex>
	vec4 mvPosition = modelViewMatrix * vec4( 0.0, 0.0, 0.0, 1.0 );
	vec2 scale;
	scale.x = length( vec3( modelMatrix[ 0 ].x, modelMatrix[ 0 ].y, modelMatrix[ 0 ].z ) );
	scale.y = length( vec3( modelMatrix[ 1 ].x, modelMatrix[ 1 ].y, modelMatrix[ 1 ].z ) );
	#ifndef USE_SIZEATTENUATION
		bool isPerspective = isPerspectiveMatrix( projectionMatrix );
		if ( isPerspective ) scale *= - mvPosition.z;
	#endif
	vec2 alignedPosition = ( position.xy - ( center - vec2( 0.5 ) ) ) * scale;
	vec2 rotatedPosition;
	rotatedPosition.x = cos( rotation ) * alignedPosition.x - sin( rotation ) * alignedPosition.y;
	rotatedPosition.y = sin( rotation ) * alignedPosition.x + cos( rotation ) * alignedPosition.y;
	mvPosition.xy += rotatedPosition;
	gl_Position = projectionMatrix * mvPosition;
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>
}`, vV = `uniform vec3 diffuse;
uniform float opacity;
#include <common>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>
	vec3 outgoingLight = vec3( 0.0 );
	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	outgoingLight = diffuseColor.rgb;
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
}`, ai = {
  alphahash_fragment: zk,
  alphahash_pars_fragment: kk,
  alphamap_fragment: Gk,
  alphamap_pars_fragment: Hk,
  alphatest_fragment: Vk,
  alphatest_pars_fragment: Wk,
  aomap_fragment: Xk,
  aomap_pars_fragment: jk,
  batching_pars_vertex: qk,
  batching_vertex: Yk,
  begin_vertex: Zk,
  beginnormal_vertex: Kk,
  bsdfs: Jk,
  iridescence_fragment: Qk,
  bumpmap_pars_fragment: $k,
  clipping_planes_fragment: eG,
  clipping_planes_pars_fragment: tG,
  clipping_planes_pars_vertex: iG,
  clipping_planes_vertex: nG,
  color_fragment: sG,
  color_pars_fragment: rG,
  color_pars_vertex: aG,
  color_vertex: oG,
  common: lG,
  cube_uv_reflection_fragment: cG,
  defaultnormal_vertex: hG,
  displacementmap_pars_vertex: uG,
  displacementmap_vertex: dG,
  emissivemap_fragment: fG,
  emissivemap_pars_fragment: pG,
  colorspace_fragment: mG,
  colorspace_pars_fragment: gG,
  envmap_fragment: vG,
  envmap_common_pars_fragment: yG,
  envmap_pars_fragment: xG,
  envmap_pars_vertex: AG,
  envmap_physical_pars_fragment: DG,
  envmap_vertex: bG,
  fog_vertex: MG,
  fog_pars_vertex: SG,
  fog_fragment: wG,
  fog_pars_fragment: EG,
  gradientmap_pars_fragment: TG,
  lightmap_pars_fragment: CG,
  lights_lambert_fragment: _G,
  lights_lambert_pars_fragment: PG,
  lights_pars_begin: RG,
  lights_toon_fragment: LG,
  lights_toon_pars_fragment: IG,
  lights_phong_fragment: OG,
  lights_phong_pars_fragment: NG,
  lights_physical_fragment: BG,
  lights_physical_pars_fragment: FG,
  lights_fragment_begin: UG,
  lights_fragment_maps: zG,
  lights_fragment_end: kG,
  logdepthbuf_fragment: GG,
  logdepthbuf_pars_fragment: HG,
  logdepthbuf_pars_vertex: VG,
  logdepthbuf_vertex: WG,
  map_fragment: XG,
  map_pars_fragment: jG,
  map_particle_fragment: qG,
  map_particle_pars_fragment: YG,
  metalnessmap_fragment: ZG,
  metalnessmap_pars_fragment: KG,
  morphinstance_vertex: JG,
  morphcolor_vertex: QG,
  morphnormal_vertex: $G,
  morphtarget_pars_vertex: eH,
  morphtarget_vertex: tH,
  normal_fragment_begin: iH,
  normal_fragment_maps: nH,
  normal_pars_fragment: sH,
  normal_pars_vertex: rH,
  normal_vertex: aH,
  normalmap_pars_fragment: oH,
  clearcoat_normal_fragment_begin: lH,
  clearcoat_normal_fragment_maps: cH,
  clearcoat_pars_fragment: hH,
  iridescence_pars_fragment: uH,
  opaque_fragment: dH,
  packing: fH,
  premultiplied_alpha_fragment: pH,
  project_vertex: mH,
  dithering_fragment: gH,
  dithering_pars_fragment: vH,
  roughnessmap_fragment: yH,
  roughnessmap_pars_fragment: xH,
  shadowmap_pars_fragment: AH,
  shadowmap_pars_vertex: bH,
  shadowmap_vertex: MH,
  shadowmask_pars_fragment: SH,
  skinbase_vertex: wH,
  skinning_pars_vertex: EH,
  skinning_vertex: TH,
  skinnormal_vertex: CH,
  specularmap_fragment: _H,
  specularmap_pars_fragment: PH,
  tonemapping_fragment: RH,
  tonemapping_pars_fragment: DH,
  transmission_fragment: LH,
  transmission_pars_fragment: IH,
  uv_pars_fragment: OH,
  uv_pars_vertex: NH,
  uv_vertex: BH,
  worldpos_vertex: FH,
  background_vert: UH,
  background_frag: zH,
  backgroundCube_vert: kH,
  backgroundCube_frag: GH,
  cube_vert: HH,
  cube_frag: VH,
  depth_vert: WH,
  depth_frag: XH,
  distanceRGBA_vert: jH,
  distanceRGBA_frag: qH,
  equirect_vert: YH,
  equirect_frag: ZH,
  linedashed_vert: KH,
  linedashed_frag: JH,
  meshbasic_vert: QH,
  meshbasic_frag: $H,
  meshlambert_vert: eV,
  meshlambert_frag: tV,
  meshmatcap_vert: iV,
  meshmatcap_frag: nV,
  meshnormal_vert: sV,
  meshnormal_frag: rV,
  meshphong_vert: aV,
  meshphong_frag: oV,
  meshphysical_vert: lV,
  meshphysical_frag: cV,
  meshtoon_vert: hV,
  meshtoon_frag: uV,
  points_vert: dV,
  points_frag: fV,
  shadow_vert: pV,
  shadow_frag: mV,
  sprite_vert: gV,
  sprite_frag: vV
}, bt = {
  common: {
    diffuse: { value: /* @__PURE__ */ new _e(16777215) },
    opacity: { value: 1 },
    map: { value: null },
    mapTransform: { value: /* @__PURE__ */ new Gt() },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new Gt() },
    alphaTest: { value: 0 }
  },
  specularmap: {
    specularMap: { value: null },
    specularMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  envmap: {
    envMap: { value: null },
    envMapRotation: { value: /* @__PURE__ */ new Gt() },
    flipEnvMap: { value: -1 },
    reflectivity: { value: 1 },
    // basic, lambert, phong
    ior: { value: 1.5 },
    // physical
    refractionRatio: { value: 0.98 }
    // basic, lambert, phong
  },
  aomap: {
    aoMap: { value: null },
    aoMapIntensity: { value: 1 },
    aoMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  lightmap: {
    lightMap: { value: null },
    lightMapIntensity: { value: 1 },
    lightMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  bumpmap: {
    bumpMap: { value: null },
    bumpMapTransform: { value: /* @__PURE__ */ new Gt() },
    bumpScale: { value: 1 }
  },
  normalmap: {
    normalMap: { value: null },
    normalMapTransform: { value: /* @__PURE__ */ new Gt() },
    normalScale: { value: /* @__PURE__ */ new Se(1, 1) }
  },
  displacementmap: {
    displacementMap: { value: null },
    displacementMapTransform: { value: /* @__PURE__ */ new Gt() },
    displacementScale: { value: 1 },
    displacementBias: { value: 0 }
  },
  emissivemap: {
    emissiveMap: { value: null },
    emissiveMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  metalnessmap: {
    metalnessMap: { value: null },
    metalnessMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  roughnessmap: {
    roughnessMap: { value: null },
    roughnessMapTransform: { value: /* @__PURE__ */ new Gt() }
  },
  gradientmap: {
    gradientMap: { value: null }
  },
  fog: {
    fogDensity: { value: 25e-5 },
    fogNear: { value: 1 },
    fogFar: { value: 2e3 },
    fogColor: { value: /* @__PURE__ */ new _e(16777215) }
  },
  lights: {
    ambientLightColor: { value: [] },
    lightProbe: { value: [] },
    directionalLights: { value: [], properties: {
      direction: {},
      color: {}
    } },
    directionalLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {}
    } },
    directionalShadowMap: { value: [] },
    directionalShadowMatrix: { value: [] },
    spotLights: { value: [], properties: {
      color: {},
      position: {},
      direction: {},
      distance: {},
      coneCos: {},
      penumbraCos: {},
      decay: {}
    } },
    spotLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {}
    } },
    spotLightMap: { value: [] },
    spotShadowMap: { value: [] },
    spotLightMatrix: { value: [] },
    pointLights: { value: [], properties: {
      color: {},
      position: {},
      decay: {},
      distance: {}
    } },
    pointLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {},
      shadowCameraNear: {},
      shadowCameraFar: {}
    } },
    pointShadowMap: { value: [] },
    pointShadowMatrix: { value: [] },
    hemisphereLights: { value: [], properties: {
      direction: {},
      skyColor: {},
      groundColor: {}
    } },
    // TODO (abelnation): RectAreaLight BRDF data needs to be moved from example to main src
    rectAreaLights: { value: [], properties: {
      color: {},
      position: {},
      width: {},
      height: {}
    } },
    ltc_1: { value: null },
    ltc_2: { value: null }
  },
  points: {
    diffuse: { value: /* @__PURE__ */ new _e(16777215) },
    opacity: { value: 1 },
    size: { value: 1 },
    scale: { value: 1 },
    map: { value: null },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new Gt() },
    alphaTest: { value: 0 },
    uvTransform: { value: /* @__PURE__ */ new Gt() }
  },
  sprite: {
    diffuse: { value: /* @__PURE__ */ new _e(16777215) },
    opacity: { value: 1 },
    center: { value: /* @__PURE__ */ new Se(0.5, 0.5) },
    rotation: { value: 0 },
    map: { value: null },
    mapTransform: { value: /* @__PURE__ */ new Gt() },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new Gt() },
    alphaTest: { value: 0 }
  }
}, xa = {
  basic: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.specularmap,
      bt.envmap,
      bt.aomap,
      bt.lightmap,
      bt.fog
    ]),
    vertexShader: ai.meshbasic_vert,
    fragmentShader: ai.meshbasic_frag
  },
  lambert: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.specularmap,
      bt.envmap,
      bt.aomap,
      bt.lightmap,
      bt.emissivemap,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      bt.fog,
      bt.lights,
      {
        emissive: { value: /* @__PURE__ */ new _e(0) }
      }
    ]),
    vertexShader: ai.meshlambert_vert,
    fragmentShader: ai.meshlambert_frag
  },
  phong: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.specularmap,
      bt.envmap,
      bt.aomap,
      bt.lightmap,
      bt.emissivemap,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      bt.fog,
      bt.lights,
      {
        emissive: { value: /* @__PURE__ */ new _e(0) },
        specular: { value: /* @__PURE__ */ new _e(1118481) },
        shininess: { value: 30 }
      }
    ]),
    vertexShader: ai.meshphong_vert,
    fragmentShader: ai.meshphong_frag
  },
  standard: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.envmap,
      bt.aomap,
      bt.lightmap,
      bt.emissivemap,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      bt.roughnessmap,
      bt.metalnessmap,
      bt.fog,
      bt.lights,
      {
        emissive: { value: /* @__PURE__ */ new _e(0) },
        roughness: { value: 1 },
        metalness: { value: 0 },
        envMapIntensity: { value: 1 }
      }
    ]),
    vertexShader: ai.meshphysical_vert,
    fragmentShader: ai.meshphysical_frag
  },
  toon: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.aomap,
      bt.lightmap,
      bt.emissivemap,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      bt.gradientmap,
      bt.fog,
      bt.lights,
      {
        emissive: { value: /* @__PURE__ */ new _e(0) }
      }
    ]),
    vertexShader: ai.meshtoon_vert,
    fragmentShader: ai.meshtoon_frag
  },
  matcap: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      bt.fog,
      {
        matcap: { value: null }
      }
    ]),
    vertexShader: ai.meshmatcap_vert,
    fragmentShader: ai.meshmatcap_frag
  },
  points: {
    uniforms: /* @__PURE__ */ dr([
      bt.points,
      bt.fog
    ]),
    vertexShader: ai.points_vert,
    fragmentShader: ai.points_frag
  },
  dashed: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.fog,
      {
        scale: { value: 1 },
        dashSize: { value: 1 },
        totalSize: { value: 2 }
      }
    ]),
    vertexShader: ai.linedashed_vert,
    fragmentShader: ai.linedashed_frag
  },
  depth: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.displacementmap
    ]),
    vertexShader: ai.depth_vert,
    fragmentShader: ai.depth_frag
  },
  normal: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.bumpmap,
      bt.normalmap,
      bt.displacementmap,
      {
        opacity: { value: 1 }
      }
    ]),
    vertexShader: ai.meshnormal_vert,
    fragmentShader: ai.meshnormal_frag
  },
  sprite: {
    uniforms: /* @__PURE__ */ dr([
      bt.sprite,
      bt.fog
    ]),
    vertexShader: ai.sprite_vert,
    fragmentShader: ai.sprite_frag
  },
  background: {
    uniforms: {
      uvTransform: { value: /* @__PURE__ */ new Gt() },
      t2D: { value: null },
      backgroundIntensity: { value: 1 }
    },
    vertexShader: ai.background_vert,
    fragmentShader: ai.background_frag
  },
  backgroundCube: {
    uniforms: {
      envMap: { value: null },
      flipEnvMap: { value: -1 },
      backgroundBlurriness: { value: 0 },
      backgroundIntensity: { value: 1 },
      backgroundRotation: { value: /* @__PURE__ */ new Gt() }
    },
    vertexShader: ai.backgroundCube_vert,
    fragmentShader: ai.backgroundCube_frag
  },
  cube: {
    uniforms: {
      tCube: { value: null },
      tFlip: { value: -1 },
      opacity: { value: 1 }
    },
    vertexShader: ai.cube_vert,
    fragmentShader: ai.cube_frag
  },
  equirect: {
    uniforms: {
      tEquirect: { value: null }
    },
    vertexShader: ai.equirect_vert,
    fragmentShader: ai.equirect_frag
  },
  distanceRGBA: {
    uniforms: /* @__PURE__ */ dr([
      bt.common,
      bt.displacementmap,
      {
        referencePosition: { value: /* @__PURE__ */ new _() },
        nearDistance: { value: 1 },
        farDistance: { value: 1e3 }
      }
    ]),
    vertexShader: ai.distanceRGBA_vert,
    fragmentShader: ai.distanceRGBA_frag
  },
  shadow: {
    uniforms: /* @__PURE__ */ dr([
      bt.lights,
      bt.fog,
      {
        color: { value: /* @__PURE__ */ new _e(0) },
        opacity: { value: 1 }
      }
    ]),
    vertexShader: ai.shadow_vert,
    fragmentShader: ai.shadow_frag
  }
};
xa.physical = {
  uniforms: /* @__PURE__ */ dr([
    xa.standard.uniforms,
    {
      clearcoat: { value: 0 },
      clearcoatMap: { value: null },
      clearcoatMapTransform: { value: /* @__PURE__ */ new Gt() },
      clearcoatNormalMap: { value: null },
      clearcoatNormalMapTransform: { value: /* @__PURE__ */ new Gt() },
      clearcoatNormalScale: { value: /* @__PURE__ */ new Se(1, 1) },
      clearcoatRoughness: { value: 0 },
      clearcoatRoughnessMap: { value: null },
      clearcoatRoughnessMapTransform: { value: /* @__PURE__ */ new Gt() },
      dispersion: { value: 0 },
      iridescence: { value: 0 },
      iridescenceMap: { value: null },
      iridescenceMapTransform: { value: /* @__PURE__ */ new Gt() },
      iridescenceIOR: { value: 1.3 },
      iridescenceThicknessMinimum: { value: 100 },
      iridescenceThicknessMaximum: { value: 400 },
      iridescenceThicknessMap: { value: null },
      iridescenceThicknessMapTransform: { value: /* @__PURE__ */ new Gt() },
      sheen: { value: 0 },
      sheenColor: { value: /* @__PURE__ */ new _e(0) },
      sheenColorMap: { value: null },
      sheenColorMapTransform: { value: /* @__PURE__ */ new Gt() },
      sheenRoughness: { value: 1 },
      sheenRoughnessMap: { value: null },
      sheenRoughnessMapTransform: { value: /* @__PURE__ */ new Gt() },
      transmission: { value: 0 },
      transmissionMap: { value: null },
      transmissionMapTransform: { value: /* @__PURE__ */ new Gt() },
      transmissionSamplerSize: { value: /* @__PURE__ */ new Se() },
      transmissionSamplerMap: { value: null },
      thickness: { value: 0 },
      thicknessMap: { value: null },
      thicknessMapTransform: { value: /* @__PURE__ */ new Gt() },
      attenuationDistance: { value: 0 },
      attenuationColor: { value: /* @__PURE__ */ new _e(0) },
      specularColor: { value: /* @__PURE__ */ new _e(1, 1, 1) },
      specularColorMap: { value: null },
      specularColorMapTransform: { value: /* @__PURE__ */ new Gt() },
      specularIntensity: { value: 1 },
      specularIntensityMap: { value: null },
      specularIntensityMapTransform: { value: /* @__PURE__ */ new Gt() },
      anisotropyVector: { value: /* @__PURE__ */ new Se() },
      anisotropyMap: { value: null },
      anisotropyMapTransform: { value: /* @__PURE__ */ new Gt() }
    }
  ]),
  vertexShader: ai.meshphysical_vert,
  fragmentShader: ai.meshphysical_frag
};
const fv = { r: 0, b: 0, g: 0 }, vh = /* @__PURE__ */ new Mi(), yV = /* @__PURE__ */ new et();
function xV(s, e, t, i, n, r, a) {
  const o = new _e(0);
  let l = r === !0 ? 0 : 1, c, h, u = null, d = 0, f = null;
  function p(x) {
    let y = x.isScene === !0 ? x.background : null;
    return y && y.isTexture && (y = (x.backgroundBlurriness > 0 ? t : e).get(y)), y;
  }
  function v(x) {
    let y = !1;
    const A = p(x);
    A === null ? m(o, l) : A && A.isColor && (m(A, 1), y = !0);
    const b = s.xr.getEnvironmentBlendMode();
    b === "additive" ? i.buffers.color.setClear(0, 0, 0, 1, a) : b === "alpha-blend" && i.buffers.color.setClear(0, 0, 0, 0, a), (s.autoClear || y) && (i.buffers.depth.setTest(!0), i.buffers.depth.setMask(!0), i.buffers.color.setMask(!0), s.clear(s.autoClearColor, s.autoClearDepth, s.autoClearStencil));
  }
  function g(x, y) {
    const A = p(y);
    A && (A.isCubeTexture || A.mapping === Qc) ? (h === void 0 && (h = new Re(
      new oi(1, 1, 1),
      new Jt({
        name: "BackgroundCubeMaterial",
        uniforms: sp(xa.backgroundCube.uniforms),
        vertexShader: xa.backgroundCube.vertexShader,
        fragmentShader: xa.backgroundCube.fragmentShader,
        side: Ps,
        depthTest: !1,
        depthWrite: !1,
        fog: !1
      })
    ), h.geometry.deleteAttribute("normal"), h.geometry.deleteAttribute("uv"), h.onBeforeRender = function(b, M, w) {
      this.matrixWorld.copyPosition(w.matrixWorld);
    }, Object.defineProperty(h.material, "envMap", {
      get: function() {
        return this.uniforms.envMap.value;
      }
    }), n.update(h)), vh.copy(y.backgroundRotation), vh.x *= -1, vh.y *= -1, vh.z *= -1, A.isCubeTexture && A.isRenderTargetTexture === !1 && (vh.y *= -1, vh.z *= -1), h.material.uniforms.envMap.value = A, h.material.uniforms.flipEnvMap.value = A.isCubeTexture && A.isRenderTargetTexture === !1 ? -1 : 1, h.material.uniforms.backgroundBlurriness.value = y.backgroundBlurriness, h.material.uniforms.backgroundIntensity.value = y.backgroundIntensity, h.material.uniforms.backgroundRotation.value.setFromMatrix4(yV.makeRotationFromEuler(vh)), h.material.toneMapped = bi.getTransfer(A.colorSpace) !== pn, (u !== A || d !== A.version || f !== s.toneMapping) && (h.material.needsUpdate = !0, u = A, d = A.version, f = s.toneMapping), h.layers.enableAll(), x.unshift(h, h.geometry, h.material, 0, 0, null)) : A && A.isTexture && (c === void 0 && (c = new Re(
      new $n(2, 2),
      new Jt({
        name: "BackgroundMaterial",
        uniforms: sp(xa.background.uniforms),
        vertexShader: xa.background.vertexShader,
        fragmentShader: xa.background.fragmentShader,
        side: sa,
        depthTest: !1,
        depthWrite: !1,
        fog: !1
      })
    ), c.geometry.deleteAttribute("normal"), Object.defineProperty(c.material, "map", {
      get: function() {
        return this.uniforms.t2D.value;
      }
    }), n.update(c)), c.material.uniforms.t2D.value = A, c.material.uniforms.backgroundIntensity.value = y.backgroundIntensity, c.material.toneMapped = bi.getTransfer(A.colorSpace) !== pn, A.matrixAutoUpdate === !0 && A.updateMatrix(), c.material.uniforms.uvTransform.value.copy(A.matrix), (u !== A || d !== A.version || f !== s.toneMapping) && (c.material.needsUpdate = !0, u = A, d = A.version, f = s.toneMapping), c.layers.enableAll(), x.unshift(c, c.geometry, c.material, 0, 0, null));
  }
  function m(x, y) {
    x.getRGB(fv, ZL(s)), i.buffers.color.setClear(fv.r, fv.g, fv.b, y, a);
  }
  return {
    getClearColor: function() {
      return o;
    },
    setClearColor: function(x, y = 1) {
      o.set(x), l = y, m(o, l);
    },
    getClearAlpha: function() {
      return l;
    },
    setClearAlpha: function(x) {
      l = x, m(o, l);
    },
    render: v,
    addToRenderList: g
  };
}
function AV(s, e) {
  const t = s.getParameter(s.MAX_VERTEX_ATTRIBS), i = {}, n = d(null);
  let r = n, a = !1;
  function o(E, P, L, I, N) {
    let H = !1;
    const U = u(I, L, P);
    r !== U && (r = U, c(r.object)), H = f(E, I, L, N), H && p(E, I, L, N), N !== null && e.update(N, s.ELEMENT_ARRAY_BUFFER), (H || a) && (a = !1, A(E, P, L, I), N !== null && s.bindBuffer(s.ELEMENT_ARRAY_BUFFER, e.get(N).buffer));
  }
  function l() {
    return s.createVertexArray();
  }
  function c(E) {
    return s.bindVertexArray(E);
  }
  function h(E) {
    return s.deleteVertexArray(E);
  }
  function u(E, P, L) {
    const I = L.wireframe === !0;
    let N = i[E.id];
    N === void 0 && (N = {}, i[E.id] = N);
    let H = N[P.id];
    H === void 0 && (H = {}, N[P.id] = H);
    let U = H[I];
    return U === void 0 && (U = d(l()), H[I] = U), U;
  }
  function d(E) {
    const P = [], L = [], I = [];
    for (let N = 0; N < t; N++)
      P[N] = 0, L[N] = 0, I[N] = 0;
    return {
      // for backward compatibility on non-VAO support browser
      geometry: null,
      program: null,
      wireframe: !1,
      newAttributes: P,
      enabledAttributes: L,
      attributeDivisors: I,
      object: E,
      attributes: {},
      index: null
    };
  }
  function f(E, P, L, I) {
    const N = r.attributes, H = P.attributes;
    let U = 0;
    const W = L.getAttributes();
    for (const X in W)
      if (W[X].location >= 0) {
        const te = N[X];
        let re = H[X];
        if (re === void 0 && (X === "instanceMatrix" && E.instanceMatrix && (re = E.instanceMatrix), X === "instanceColor" && E.instanceColor && (re = E.instanceColor)), te === void 0 || te.attribute !== re || re && te.data !== re.data)
          return !0;
        U++;
      }
    return r.attributesNum !== U || r.index !== I;
  }
  function p(E, P, L, I) {
    const N = {}, H = P.attributes;
    let U = 0;
    const W = L.getAttributes();
    for (const X in W)
      if (W[X].location >= 0) {
        let te = H[X];
        te === void 0 && (X === "instanceMatrix" && E.instanceMatrix && (te = E.instanceMatrix), X === "instanceColor" && E.instanceColor && (te = E.instanceColor));
        const re = {};
        re.attribute = te, te && te.data && (re.data = te.data), N[X] = re, U++;
      }
    r.attributes = N, r.attributesNum = U, r.index = I;
  }
  function v() {
    const E = r.newAttributes;
    for (let P = 0, L = E.length; P < L; P++)
      E[P] = 0;
  }
  function g(E) {
    m(E, 0);
  }
  function m(E, P) {
    const L = r.newAttributes, I = r.enabledAttributes, N = r.attributeDivisors;
    L[E] = 1, I[E] === 0 && (s.enableVertexAttribArray(E), I[E] = 1), N[E] !== P && (s.vertexAttribDivisor(E, P), N[E] = P);
  }
  function x() {
    const E = r.newAttributes, P = r.enabledAttributes;
    for (let L = 0, I = P.length; L < I; L++)
      P[L] !== E[L] && (s.disableVertexAttribArray(L), P[L] = 0);
  }
  function y(E, P, L, I, N, H, U) {
    U === !0 ? s.vertexAttribIPointer(E, P, L, N, H) : s.vertexAttribPointer(E, P, L, I, N, H);
  }
  function A(E, P, L, I) {
    v();
    const N = I.attributes, H = L.getAttributes(), U = P.defaultAttributeValues;
    for (const W in H) {
      const X = H[W];
      if (X.location >= 0) {
        let K = N[W];
        if (K === void 0 && (W === "instanceMatrix" && E.instanceMatrix && (K = E.instanceMatrix), W === "instanceColor" && E.instanceColor && (K = E.instanceColor)), K !== void 0) {
          const te = K.normalized, re = K.itemSize, be = e.get(K);
          if (be === void 0)
            continue;
          const Pe = be.buffer, J = be.type, fe = be.bytesPerElement, Me = J === s.INT || J === s.UNSIGNED_INT || K.gpuType === p4;
          if (K.isInterleavedBufferAttribute) {
            const ye = K.data, Ye = ye.stride, Je = K.offset;
            if (ye.isInstancedInterleavedBuffer) {
              for (let rt = 0; rt < X.locationSize; rt++)
                m(X.location + rt, ye.meshPerAttribute);
              E.isInstancedMesh !== !0 && I._maxInstanceCount === void 0 && (I._maxInstanceCount = ye.meshPerAttribute * ye.count);
            } else
              for (let rt = 0; rt < X.locationSize; rt++)
                g(X.location + rt);
            s.bindBuffer(s.ARRAY_BUFFER, Pe);
            for (let rt = 0; rt < X.locationSize; rt++)
              y(
                X.location + rt,
                re / X.locationSize,
                J,
                te,
                Ye * fe,
                (Je + re / X.locationSize * rt) * fe,
                Me
              );
          } else {
            if (K.isInstancedBufferAttribute) {
              for (let ye = 0; ye < X.locationSize; ye++)
                m(X.location + ye, K.meshPerAttribute);
              E.isInstancedMesh !== !0 && I._maxInstanceCount === void 0 && (I._maxInstanceCount = K.meshPerAttribute * K.count);
            } else
              for (let ye = 0; ye < X.locationSize; ye++)
                g(X.location + ye);
            s.bindBuffer(s.ARRAY_BUFFER, Pe);
            for (let ye = 0; ye < X.locationSize; ye++)
              y(
                X.location + ye,
                re / X.locationSize,
                J,
                te,
                re * fe,
                re / X.locationSize * ye * fe,
                Me
              );
          }
        } else if (U !== void 0) {
          const te = U[W];
          if (te !== void 0)
            switch (te.length) {
              case 2:
                s.vertexAttrib2fv(X.location, te);
                break;
              case 3:
                s.vertexAttrib3fv(X.location, te);
                break;
              case 4:
                s.vertexAttrib4fv(X.location, te);
                break;
              default:
                s.vertexAttrib1fv(X.location, te);
            }
        }
      }
    }
    x();
  }
  function b() {
    C();
    for (const E in i) {
      const P = i[E];
      for (const L in P) {
        const I = P[L];
        for (const N in I)
          h(I[N].object), delete I[N];
        delete P[L];
      }
      delete i[E];
    }
  }
  function M(E) {
    if (i[E.id] === void 0)
      return;
    const P = i[E.id];
    for (const L in P) {
      const I = P[L];
      for (const N in I)
        h(I[N].object), delete I[N];
      delete P[L];
    }
    delete i[E.id];
  }
  function w(E) {
    for (const P in i) {
      const L = i[P];
      if (L[E.id] === void 0)
        continue;
      const I = L[E.id];
      for (const N in I)
        h(I[N].object), delete I[N];
      delete L[E.id];
    }
  }
  function C() {
    T(), a = !0, r !== n && (r = n, c(r.object));
  }
  function T() {
    n.geometry = null, n.program = null, n.wireframe = !1;
  }
  return {
    setup: o,
    reset: C,
    resetDefaultState: T,
    dispose: b,
    releaseStatesOfGeometry: M,
    releaseStatesOfProgram: w,
    initAttributes: v,
    enableAttribute: g,
    disableUnusedAttributes: x
  };
}
function bV(s, e, t) {
  let i;
  function n(c) {
    i = c;
  }
  function r(c, h) {
    s.drawArrays(i, c, h), t.update(h, i, 1);
  }
  function a(c, h, u) {
    u !== 0 && (s.drawArraysInstanced(i, c, h, u), t.update(h, i, u));
  }
  function o(c, h, u) {
    if (u === 0)
      return;
    e.get("WEBGL_multi_draw").multiDrawArraysWEBGL(i, c, 0, h, 0, u);
    let f = 0;
    for (let p = 0; p < u; p++)
      f += h[p];
    t.update(f, i, 1);
  }
  function l(c, h, u, d) {
    if (u === 0)
      return;
    const f = e.get("WEBGL_multi_draw");
    if (f === null)
      for (let p = 0; p < c.length; p++)
        a(c[p], h[p], d[p]);
    else {
      f.multiDrawArraysInstancedWEBGL(i, c, 0, h, 0, d, 0, u);
      let p = 0;
      for (let v = 0; v < u; v++)
        p += h[v];
      for (let v = 0; v < d.length; v++)
        t.update(p, i, d[v]);
    }
  }
  this.setMode = n, this.render = r, this.renderInstances = a, this.renderMultiDraw = o, this.renderMultiDrawInstances = l;
}
function MV(s, e, t, i) {
  let n;
  function r() {
    if (n !== void 0)
      return n;
    if (e.has("EXT_texture_filter_anisotropic") === !0) {
      const M = e.get("EXT_texture_filter_anisotropic");
      n = s.getParameter(M.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
    } else
      n = 0;
    return n;
  }
  function a(M) {
    return !(M !== dn && i.convert(M) !== s.getParameter(s.IMPLEMENTATION_COLOR_READ_FORMAT));
  }
  function o(M) {
    const w = M === on && (e.has("EXT_color_buffer_half_float") || e.has("EXT_color_buffer_float"));
    return !(M !== o0 && i.convert(M) !== s.getParameter(s.IMPLEMENTATION_COLOR_READ_TYPE) && // Edge and Chrome Mac < 52 (#9513)
    M !== xn && !w);
  }
  function l(M) {
    if (M === "highp") {
      if (s.getShaderPrecisionFormat(s.VERTEX_SHADER, s.HIGH_FLOAT).precision > 0 && s.getShaderPrecisionFormat(s.FRAGMENT_SHADER, s.HIGH_FLOAT).precision > 0)
        return "highp";
      M = "mediump";
    }
    return M === "mediump" && s.getShaderPrecisionFormat(s.VERTEX_SHADER, s.MEDIUM_FLOAT).precision > 0 && s.getShaderPrecisionFormat(s.FRAGMENT_SHADER, s.MEDIUM_FLOAT).precision > 0 ? "mediump" : "lowp";
  }
  let c = t.precision !== void 0 ? t.precision : "highp";
  const h = l(c);
  h !== c && (console.warn("THREE.WebGLRenderer:", c, "not supported, using", h, "instead."), c = h);
  const u = t.logarithmicDepthBuffer === !0, d = s.getParameter(s.MAX_TEXTURE_IMAGE_UNITS), f = s.getParameter(s.MAX_VERTEX_TEXTURE_IMAGE_UNITS), p = s.getParameter(s.MAX_TEXTURE_SIZE), v = s.getParameter(s.MAX_CUBE_MAP_TEXTURE_SIZE), g = s.getParameter(s.MAX_VERTEX_ATTRIBS), m = s.getParameter(s.MAX_VERTEX_UNIFORM_VECTORS), x = s.getParameter(s.MAX_VARYING_VECTORS), y = s.getParameter(s.MAX_FRAGMENT_UNIFORM_VECTORS), A = f > 0, b = s.getParameter(s.MAX_SAMPLES);
  return {
    isWebGL2: !0,
    // keeping this for backwards compatibility
    getMaxAnisotropy: r,
    getMaxPrecision: l,
    textureFormatReadable: a,
    textureTypeReadable: o,
    precision: c,
    logarithmicDepthBuffer: u,
    maxTextures: d,
    maxVertexTextures: f,
    maxTextureSize: p,
    maxCubemapSize: v,
    maxAttributes: g,
    maxVertexUniforms: m,
    maxVaryings: x,
    maxFragmentUniforms: y,
    vertexTextures: A,
    maxSamples: b
  };
}
function SV(s) {
  const e = this;
  let t = null, i = 0, n = !1, r = !1;
  const a = new ln(), o = new Gt(), l = { value: null, needsUpdate: !1 };
  this.uniform = l, this.numPlanes = 0, this.numIntersection = 0, this.init = function(u, d) {
    const f = u.length !== 0 || d || // enable state of previous frame - the clipping code has to
    // run another frame in order to reset the state:
    i !== 0 || n;
    return n = d, i = u.length, f;
  }, this.beginShadows = function() {
    r = !0, h(null);
  }, this.endShadows = function() {
    r = !1;
  }, this.setGlobalState = function(u, d) {
    t = h(u, d, 0);
  }, this.setState = function(u, d, f) {
    const p = u.clippingPlanes, v = u.clipIntersection, g = u.clipShadows, m = s.get(u);
    if (!n || p === null || p.length === 0 || r && !g)
      r ? h(null) : c();
    else {
      const x = r ? 0 : i, y = x * 4;
      let A = m.clippingState || null;
      l.value = A, A = h(p, d, y, f);
      for (let b = 0; b !== y; ++b)
        A[b] = t[b];
      m.clippingState = A, this.numIntersection = v ? this.numPlanes : 0, this.numPlanes += x;
    }
  };
  function c() {
    l.value !== t && (l.value = t, l.needsUpdate = i > 0), e.numPlanes = i, e.numIntersection = 0;
  }
  function h(u, d, f, p) {
    const v = u !== null ? u.length : 0;
    let g = null;
    if (v !== 0) {
      if (g = l.value, p !== !0 || g === null) {
        const m = f + v * 4, x = d.matrixWorldInverse;
        o.getNormalMatrix(x), (g === null || g.length < m) && (g = new Float32Array(m));
        for (let y = 0, A = f; y !== v; ++y, A += 4)
          a.copy(u[y]).applyMatrix4(x, o), a.normal.toArray(g, A), g[A + 3] = a.constant;
      }
      l.value = g, l.needsUpdate = !0;
    }
    return e.numPlanes = v, e.numIntersection = 0, g;
  }
}
function wV(s) {
  let e = /* @__PURE__ */ new WeakMap();
  function t(a, o) {
    return o === ml ? a.mapping = lo : o === Fc && (a.mapping = a0), a;
  }
  function i(a) {
    if (a && a.isTexture) {
      const o = a.mapping;
      if (o === ml || o === Fc)
        if (e.has(a)) {
          const l = e.get(a).texture;
          return t(l, a.mapping);
        } else {
          const l = a.image;
          if (l && l.height > 0) {
            const c = new zM(l.height);
            return c.fromEquirectangularTexture(s, a), e.set(a, c), a.addEventListener("dispose", n), t(c.texture, a.mapping);
          } else
            return null;
        }
    }
    return a;
  }
  function n(a) {
    const o = a.target;
    o.removeEventListener("dispose", n);
    const l = e.get(o);
    l !== void 0 && (e.delete(o), l.dispose());
  }
  function r() {
    e = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: i,
    dispose: r
  };
}
let Xn = class extends s2 {
  constructor(e = -1, t = 1, i = 1, n = -1, r = 0.1, a = 2e3) {
    super(), this.isOrthographicCamera = !0, this.type = "OrthographicCamera", this.zoom = 1, this.view = null, this.left = e, this.right = t, this.top = i, this.bottom = n, this.near = r, this.far = a, this.updateProjectionMatrix();
  }
  copy(e, t) {
    return super.copy(e, t), this.left = e.left, this.right = e.right, this.top = e.top, this.bottom = e.bottom, this.near = e.near, this.far = e.far, this.zoom = e.zoom, this.view = e.view === null ? null : Object.assign({}, e.view), this;
  }
  setViewOffset(e, t, i, n, r, a) {
    this.view === null && (this.view = {
      enabled: !0,
      fullWidth: 1,
      fullHeight: 1,
      offsetX: 0,
      offsetY: 0,
      width: 1,
      height: 1
    }), this.view.enabled = !0, this.view.fullWidth = e, this.view.fullHeight = t, this.view.offsetX = i, this.view.offsetY = n, this.view.width = r, this.view.height = a, this.updateProjectionMatrix();
  }
  clearViewOffset() {
    this.view !== null && (this.view.enabled = !1), this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const e = (this.right - this.left) / (2 * this.zoom), t = (this.top - this.bottom) / (2 * this.zoom), i = (this.right + this.left) / 2, n = (this.top + this.bottom) / 2;
    let r = i - e, a = i + e, o = n + t, l = n - t;
    if (this.view !== null && this.view.enabled) {
      const c = (this.right - this.left) / this.view.fullWidth / this.zoom, h = (this.top - this.bottom) / this.view.fullHeight / this.zoom;
      r += c * this.view.offsetX, a = r + c * this.view.width, o -= h * this.view.offsetY, l = o - h * this.view.height;
    }
    this.projectionMatrix.makeOrthographic(r, a, o, l, this.near, this.far, this.coordinateSystem), this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.zoom = this.zoom, t.object.left = this.left, t.object.right = this.right, t.object.top = this.top, t.object.bottom = this.bottom, t.object.near = this.near, t.object.far = this.far, this.view !== null && (t.object.view = Object.assign({}, this.view)), t;
  }
};
const xf = 4, cE = [0.125, 0.215, 0.35, 0.446, 0.526, 0.582], nu = 20, z8 = /* @__PURE__ */ new Xn(), hE = /* @__PURE__ */ new _e();
let k8 = null, G8 = 0, H8 = 0, V8 = !1;
const qh = (1 + Math.sqrt(5)) / 2, xd = 1 / qh, uE = [
  /* @__PURE__ */ new _(-qh, xd, 0),
  /* @__PURE__ */ new _(qh, xd, 0),
  /* @__PURE__ */ new _(-xd, 0, qh),
  /* @__PURE__ */ new _(xd, 0, qh),
  /* @__PURE__ */ new _(0, qh, -xd),
  /* @__PURE__ */ new _(0, qh, xd),
  /* @__PURE__ */ new _(-1, 1, -1),
  /* @__PURE__ */ new _(1, 1, -1),
  /* @__PURE__ */ new _(-1, 1, 1),
  /* @__PURE__ */ new _(1, 1, 1)
];
let rp = class {
  constructor(e) {
    this._renderer = e, this._pingPongRenderTarget = null, this._lodMax = 0, this._cubeSize = 0, this._lodPlanes = [], this._sizeLods = [], this._sigmas = [], this._blurMaterial = null, this._cubemapMaterial = null, this._equirectMaterial = null, this._compileMaterial(this._blurMaterial);
  }
  /**
   * Generates a PMREM from a supplied Scene, which can be faster than using an
   * image if networking bandwidth is low. Optional sigma specifies a blur radius
   * in radians to be applied to the scene before PMREM generation. Optional near
   * and far planes ensure the scene is rendered in its entirety (the cubeCamera
   * is placed at the origin).
   */
  fromScene(e, t = 0, i = 0.1, n = 100) {
    k8 = this._renderer.getRenderTarget(), G8 = this._renderer.getActiveCubeFace(), H8 = this._renderer.getActiveMipmapLevel(), V8 = this._renderer.xr.enabled, this._renderer.xr.enabled = !1, this._setSize(256);
    const r = this._allocateTargets();
    return r.depthBuffer = !0, this._sceneToCubeUV(e, i, n, r), t > 0 && this._blur(r, 0, 0, t), this._applyPMREM(r), this._cleanup(r), r;
  }
  /**
   * Generates a PMREM from an equirectangular texture, which can be either LDR
   * or HDR. The ideal input image size is 1k (1024 x 512),
   * as this matches best with the 256 x 256 cubemap output.
   * The smallest supported equirectangular image size is 64 x 32.
   */
  fromEquirectangular(e, t = null) {
    return this._fromTexture(e, t);
  }
  /**
   * Generates a PMREM from an cubemap texture, which can be either LDR
   * or HDR. The ideal input cube size is 256 x 256,
   * as this matches best with the 256 x 256 cubemap output.
   * The smallest supported cube size is 16 x 16.
   */
  fromCubemap(e, t = null) {
    return this._fromTexture(e, t);
  }
  /**
   * Pre-compiles the cubemap shader. You can get faster start-up by invoking this method during
   * your texture's network fetch for increased concurrency.
   */
  compileCubemapShader() {
    this._cubemapMaterial === null && (this._cubemapMaterial = pE(), this._compileMaterial(this._cubemapMaterial));
  }
  /**
   * Pre-compiles the equirectangular shader. You can get faster start-up by invoking this method during
   * your texture's network fetch for increased concurrency.
   */
  compileEquirectangularShader() {
    this._equirectMaterial === null && (this._equirectMaterial = fE(), this._compileMaterial(this._equirectMaterial));
  }
  /**
   * Disposes of the PMREMGenerator's internal memory. Note that PMREMGenerator is a static class,
   * so you should not need more than one PMREMGenerator object. If you do, calling dispose() on
   * one of them will cause any others to also become unusable.
   */
  dispose() {
    this._dispose(), this._cubemapMaterial !== null && this._cubemapMaterial.dispose(), this._equirectMaterial !== null && this._equirectMaterial.dispose();
  }
  // private interface
  _setSize(e) {
    this._lodMax = Math.floor(Math.log2(e)), this._cubeSize = Math.pow(2, this._lodMax);
  }
  _dispose() {
    this._blurMaterial !== null && this._blurMaterial.dispose(), this._pingPongRenderTarget !== null && this._pingPongRenderTarget.dispose();
    for (let e = 0; e < this._lodPlanes.length; e++)
      this._lodPlanes[e].dispose();
  }
  _cleanup(e) {
    this._renderer.setRenderTarget(k8, G8, H8), this._renderer.xr.enabled = V8, e.scissorTest = !1, pv(e, 0, 0, e.width, e.height);
  }
  _fromTexture(e, t) {
    e.mapping === lo || e.mapping === a0 ? this._setSize(e.image.length === 0 ? 16 : e.image[0].width || e.image[0].image.width) : this._setSize(e.image.width / 4), k8 = this._renderer.getRenderTarget(), G8 = this._renderer.getActiveCubeFace(), H8 = this._renderer.getActiveMipmapLevel(), V8 = this._renderer.xr.enabled, this._renderer.xr.enabled = !1;
    const i = t || this._allocateTargets();
    return this._textureToCubeUV(e, i), this._applyPMREM(i), this._cleanup(i), i;
  }
  _allocateTargets() {
    const e = 3 * Math.max(this._cubeSize, 112), t = 4 * this._cubeSize, i = {
      magFilter: ii,
      minFilter: ii,
      generateMipmaps: !1,
      type: on,
      format: dn,
      colorSpace: Si,
      depthBuffer: !1
    }, n = dE(e, t, i);
    if (this._pingPongRenderTarget === null || this._pingPongRenderTarget.width !== e || this._pingPongRenderTarget.height !== t) {
      this._pingPongRenderTarget !== null && this._dispose(), this._pingPongRenderTarget = dE(e, t, i);
      const { _lodMax: r } = this;
      (({ sizeLods: this._sizeLods, lodPlanes: this._lodPlanes, sigmas: this._sigmas } = EV(r))), this._blurMaterial = TV(r, e, t);
    }
    return n;
  }
  _compileMaterial(e) {
    const t = new Re(this._lodPlanes[0], e);
    this._renderer.compile(t, z8);
  }
  _sceneToCubeUV(e, t, i, n) {
    const o = new Zt(90, 1, t, i), l = [1, -1, 1, 1, 1, 1], c = [1, 1, 1, -1, -1, -1], h = this._renderer, u = h.autoClear, d = h.toneMapping;
    h.getClearColor(hE), h.toneMapping = ol, h.autoClear = !1;
    const f = new kt({
      name: "PMREM.Background",
      side: Ps,
      depthWrite: !1,
      depthTest: !1
    }), p = new Re(new oi(), f);
    let v = !1;
    const g = e.background;
    g ? g.isColor && (f.color.copy(g), e.background = null, v = !0) : (f.color.copy(hE), v = !0);
    for (let m = 0; m < 6; m++) {
      const x = m % 3;
      x === 0 ? (o.up.set(0, l[m], 0), o.lookAt(c[m], 0, 0)) : x === 1 ? (o.up.set(0, 0, l[m]), o.lookAt(0, c[m], 0)) : (o.up.set(0, l[m], 0), o.lookAt(0, 0, c[m]));
      const y = this._cubeSize;
      pv(n, x * y, m > 2 ? y : 0, y, y), h.setRenderTarget(n), v && h.render(p, o), h.render(e, o);
    }
    p.geometry.dispose(), p.material.dispose(), h.toneMapping = d, h.autoClear = u, e.background = g;
  }
  _textureToCubeUV(e, t) {
    const i = this._renderer, n = e.mapping === lo || e.mapping === a0;
    n ? (this._cubemapMaterial === null && (this._cubemapMaterial = pE()), this._cubemapMaterial.uniforms.flipEnvMap.value = e.isRenderTargetTexture === !1 ? -1 : 1) : this._equirectMaterial === null && (this._equirectMaterial = fE());
    const r = n ? this._cubemapMaterial : this._equirectMaterial, a = new Re(this._lodPlanes[0], r), o = r.uniforms;
    o.envMap.value = e;
    const l = this._cubeSize;
    pv(t, 0, 0, 3 * l, 2 * l), i.setRenderTarget(t), i.render(a, z8);
  }
  _applyPMREM(e) {
    const t = this._renderer, i = t.autoClear;
    t.autoClear = !1;
    const n = this._lodPlanes.length;
    for (let r = 1; r < n; r++) {
      const a = Math.sqrt(this._sigmas[r] * this._sigmas[r] - this._sigmas[r - 1] * this._sigmas[r - 1]), o = uE[(n - r - 1) % uE.length];
      this._blur(e, r - 1, r, a, o);
    }
    t.autoClear = i;
  }
  /**
   * This is a two-pass Gaussian blur for a cubemap. Normally this is done
   * vertically and horizontally, but this breaks down on a cube. Here we apply
   * the blur latitudinally (around the poles), and then longitudinally (towards
   * the poles) to approximate the orthogonally-separable blur. It is least
   * accurate at the poles, but still does a decent job.
   */
  _blur(e, t, i, n, r) {
    const a = this._pingPongRenderTarget;
    this._halfBlur(
      e,
      a,
      t,
      i,
      n,
      "latitudinal",
      r
    ), this._halfBlur(
      a,
      e,
      i,
      i,
      n,
      "longitudinal",
      r
    );
  }
  _halfBlur(e, t, i, n, r, a, o) {
    const l = this._renderer, c = this._blurMaterial;
    a !== "latitudinal" && a !== "longitudinal" && console.error(
      "blur direction must be either latitudinal or longitudinal!"
    );
    const h = 3, u = new Re(this._lodPlanes[n], c), d = c.uniforms, f = this._sizeLods[i] - 1, p = isFinite(r) ? Math.PI / (2 * f) : 2 * Math.PI / (2 * nu - 1), v = r / p, g = isFinite(r) ? 1 + Math.floor(h * v) : nu;
    g > nu && console.warn(`sigmaRadians, ${r}, is too large and will clip, as it requested ${g} samples when the maximum is set to ${nu}`);
    const m = [];
    let x = 0;
    for (let w = 0; w < nu; ++w) {
      const C = w / v, T = Math.exp(-C * C / 2);
      m.push(T), w === 0 ? x += T : w < g && (x += 2 * T);
    }
    for (let w = 0; w < m.length; w++)
      m[w] = m[w] / x;
    d.envMap.value = e.texture, d.samples.value = g, d.weights.value = m, d.latitudinal.value = a === "latitudinal", o && (d.poleAxis.value = o);
    const { _lodMax: y } = this;
    d.dTheta.value = p, d.mipInt.value = y - i;
    const A = this._sizeLods[n], b = 3 * A * (n > y - xf ? n - y + xf : 0), M = 4 * (this._cubeSize - A);
    pv(t, b, M, 3 * A, 2 * A), l.setRenderTarget(t), l.render(u, z8);
  }
};
function EV(s) {
  const e = [], t = [], i = [];
  let n = s;
  const r = s - xf + 1 + cE.length;
  for (let a = 0; a < r; a++) {
    const o = Math.pow(2, n);
    t.push(o);
    let l = 1 / o;
    a > s - xf ? l = cE[a - s + xf - 1] : a === 0 && (l = 0), i.push(l);
    const c = 1 / (o - 2), h = -c, u = 1 + c, d = [h, h, u, h, u, u, h, h, u, u, h, u], f = 6, p = 6, v = 3, g = 2, m = 1, x = new Float32Array(v * p * f), y = new Float32Array(g * p * f), A = new Float32Array(m * p * f);
    for (let M = 0; M < f; M++) {
      const w = M % 3 * 2 / 3 - 1, C = M > 2 ? 0 : -1, T = [
        w,
        C,
        0,
        w + 2 / 3,
        C,
        0,
        w + 2 / 3,
        C + 1,
        0,
        w,
        C,
        0,
        w + 2 / 3,
        C + 1,
        0,
        w,
        C + 1,
        0
      ];
      x.set(T, v * p * M), y.set(d, g * p * M);
      const E = [M, M, M, M, M, M];
      A.set(E, m * p * M);
    }
    const b = new Pt();
    b.setAttribute("position", new Ht(x, v)), b.setAttribute("uv", new Ht(y, g)), b.setAttribute("faceIndex", new Ht(A, m)), e.push(b), n > xf && n--;
  }
  return { lodPlanes: e, sizeLods: t, sigmas: i };
}
function dE(s, e, t) {
  const i = new Xi(s, e, t);
  return i.texture.mapping = Qc, i.texture.name = "PMREM.cubeUv", i.scissorTest = !0, i;
}
function pv(s, e, t, i, n) {
  s.viewport.set(e, t, i, n), s.scissor.set(e, t, i, n);
}
function TV(s, e, t) {
  const i = new Float32Array(nu), n = new _(0, 1, 0);
  return new Jt({
    name: "SphericalGaussianBlur",
    defines: {
      n: nu,
      CUBEUV_TEXEL_WIDTH: 1 / e,
      CUBEUV_TEXEL_HEIGHT: 1 / t,
      CUBEUV_MAX_MIP: `${s}.0`
    },
    uniforms: {
      envMap: { value: null },
      samples: { value: 1 },
      weights: { value: i },
      latitudinal: { value: !1 },
      dTheta: { value: 0 },
      mipInt: { value: 0 },
      poleAxis: { value: n }
    },
    vertexShader: kM(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;
			uniform int samples;
			uniform float weights[ n ];
			uniform bool latitudinal;
			uniform float dTheta;
			uniform float mipInt;
			uniform vec3 poleAxis;

			#define ENVMAP_TYPE_CUBE_UV
			#include <cube_uv_reflection_fragment>

			vec3 getSample( float theta, vec3 axis ) {

				float cosTheta = cos( theta );
				// Rodrigues' axis-angle rotation
				vec3 sampleDirection = vOutputDirection * cosTheta
					+ cross( axis, vOutputDirection ) * sin( theta )
					+ axis * dot( axis, vOutputDirection ) * ( 1.0 - cosTheta );

				return bilinearCubeUV( envMap, sampleDirection, mipInt );

			}

			void main() {

				vec3 axis = latitudinal ? poleAxis : cross( poleAxis, vOutputDirection );

				if ( all( equal( axis, vec3( 0.0 ) ) ) ) {

					axis = vec3( vOutputDirection.z, 0.0, - vOutputDirection.x );

				}

				axis = normalize( axis );

				gl_FragColor = vec4( 0.0, 0.0, 0.0, 1.0 );
				gl_FragColor.rgb += weights[ 0 ] * getSample( 0.0, axis );

				for ( int i = 1; i < n; i++ ) {

					if ( i >= samples ) {

						break;

					}

					float theta = dTheta * float( i );
					gl_FragColor.rgb += weights[ i ] * getSample( -1.0 * theta, axis );
					gl_FragColor.rgb += weights[ i ] * getSample( theta, axis );

				}

			}
		`
    ),
    blending: vn,
    depthTest: !1,
    depthWrite: !1
  });
}
function fE() {
  return new Jt({
    name: "EquirectangularToCubeUV",
    uniforms: {
      envMap: { value: null }
    },
    vertexShader: kM(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;

			#include <common>

			void main() {

				vec3 outputDirection = normalize( vOutputDirection );
				vec2 uv = equirectUv( outputDirection );

				gl_FragColor = vec4( texture2D ( envMap, uv ).rgb, 1.0 );

			}
		`
    ),
    blending: vn,
    depthTest: !1,
    depthWrite: !1
  });
}
function pE() {
  return new Jt({
    name: "CubemapToCubeUV",
    uniforms: {
      envMap: { value: null },
      flipEnvMap: { value: -1 }
    },
    vertexShader: kM(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			uniform float flipEnvMap;

			varying vec3 vOutputDirection;

			uniform samplerCube envMap;

			void main() {

				gl_FragColor = textureCube( envMap, vec3( flipEnvMap * vOutputDirection.x, vOutputDirection.yz ) );

			}
		`
    ),
    blending: vn,
    depthTest: !1,
    depthWrite: !1
  });
}
function kM() {
  return (
    /* glsl */
    `

		precision mediump float;
		precision mediump int;

		attribute float faceIndex;

		varying vec3 vOutputDirection;

		// RH coordinate system; PMREM face-indexing convention
		vec3 getDirection( vec2 uv, float face ) {

			uv = 2.0 * uv - 1.0;

			vec3 direction = vec3( uv, 1.0 );

			if ( face == 0.0 ) {

				direction = direction.zyx; // ( 1, v, u ) pos x

			} else if ( face == 1.0 ) {

				direction = direction.xzy;
				direction.xz *= -1.0; // ( -u, 1, -v ) pos y

			} else if ( face == 2.0 ) {

				direction.x *= -1.0; // ( -u, v, 1 ) pos z

			} else if ( face == 3.0 ) {

				direction = direction.zyx;
				direction.xz *= -1.0; // ( -1, v, -u ) neg x

			} else if ( face == 4.0 ) {

				direction = direction.xzy;
				direction.xy *= -1.0; // ( -u, -1, v ) neg y

			} else if ( face == 5.0 ) {

				direction.z *= -1.0; // ( u, v, -1 ) neg z

			}

			return direction;

		}

		void main() {

			vOutputDirection = getDirection( uv, faceIndex );
			gl_Position = vec4( position, 1.0 );

		}
	`
  );
}
function CV(s) {
  let e = /* @__PURE__ */ new WeakMap(), t = null;
  function i(o) {
    if (o && o.isTexture) {
      const l = o.mapping, c = l === ml || l === Fc, h = l === lo || l === a0;
      if (c || h) {
        let u = e.get(o);
        const d = u !== void 0 ? u.texture.pmremVersion : 0;
        if (o.isRenderTargetTexture && o.pmremVersion !== d)
          return t === null && (t = new rp(s)), u = c ? t.fromEquirectangular(o, u) : t.fromCubemap(o, u), u.texture.pmremVersion = o.pmremVersion, e.set(o, u), u.texture;
        if (u !== void 0)
          return u.texture;
        {
          const f = o.image;
          return c && f && f.height > 0 || h && f && n(f) ? (t === null && (t = new rp(s)), u = c ? t.fromEquirectangular(o) : t.fromCubemap(o), u.texture.pmremVersion = o.pmremVersion, e.set(o, u), o.addEventListener("dispose", r), u.texture) : null;
        }
      }
    }
    return o;
  }
  function n(o) {
    let l = 0;
    const c = 6;
    for (let h = 0; h < c; h++)
      o[h] !== void 0 && l++;
    return l === c;
  }
  function r(o) {
    const l = o.target;
    l.removeEventListener("dispose", r);
    const c = e.get(l);
    c !== void 0 && (e.delete(l), c.dispose());
  }
  function a() {
    e = /* @__PURE__ */ new WeakMap(), t !== null && (t.dispose(), t = null);
  }
  return {
    get: i,
    dispose: a
  };
}
function _V(s) {
  const e = {};
  function t(i) {
    if (e[i] !== void 0)
      return e[i];
    let n;
    switch (i) {
      case "WEBGL_depth_texture":
        n = s.getExtension("WEBGL_depth_texture") || s.getExtension("MOZ_WEBGL_depth_texture") || s.getExtension("WEBKIT_WEBGL_depth_texture");
        break;
      case "EXT_texture_filter_anisotropic":
        n = s.getExtension("EXT_texture_filter_anisotropic") || s.getExtension("MOZ_EXT_texture_filter_anisotropic") || s.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        break;
      case "WEBGL_compressed_texture_s3tc":
        n = s.getExtension("WEBGL_compressed_texture_s3tc") || s.getExtension("MOZ_WEBGL_compressed_texture_s3tc") || s.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc");
        break;
      case "WEBGL_compressed_texture_pvrtc":
        n = s.getExtension("WEBGL_compressed_texture_pvrtc") || s.getExtension("WEBKIT_WEBGL_compressed_texture_pvrtc");
        break;
      default:
        n = s.getExtension(i);
    }
    return e[i] = n, n;
  }
  return {
    has: function(i) {
      return t(i) !== null;
    },
    init: function() {
      t("EXT_color_buffer_float"), t("WEBGL_clip_cull_distance"), t("OES_texture_float_linear"), t("EXT_color_buffer_half_float"), t("WEBGL_multisampled_render_to_texture"), t("WEBGL_render_shared_exponent");
    },
    get: function(i) {
      const n = t(i);
      return n === null && NM("THREE.WebGLRenderer: " + i + " extension not supported."), n;
    }
  };
}
function PV(s, e, t, i) {
  const n = {}, r = /* @__PURE__ */ new WeakMap();
  function a(u) {
    const d = u.target;
    d.index !== null && e.remove(d.index);
    for (const p in d.attributes)
      e.remove(d.attributes[p]);
    for (const p in d.morphAttributes) {
      const v = d.morphAttributes[p];
      for (let g = 0, m = v.length; g < m; g++)
        e.remove(v[g]);
    }
    d.removeEventListener("dispose", a), delete n[d.id];
    const f = r.get(d);
    f && (e.remove(f), r.delete(d)), i.releaseStatesOfGeometry(d), d.isInstancedBufferGeometry === !0 && delete d._maxInstanceCount, t.memory.geometries--;
  }
  function o(u, d) {
    return n[d.id] === !0 || (d.addEventListener("dispose", a), n[d.id] = !0, t.memory.geometries++), d;
  }
  function l(u) {
    const d = u.attributes;
    for (const p in d)
      e.update(d[p], s.ARRAY_BUFFER);
    const f = u.morphAttributes;
    for (const p in f) {
      const v = f[p];
      for (let g = 0, m = v.length; g < m; g++)
        e.update(v[g], s.ARRAY_BUFFER);
    }
  }
  function c(u) {
    const d = [], f = u.index, p = u.attributes.position;
    let v = 0;
    if (f !== null) {
      const x = f.array;
      v = f.version;
      for (let y = 0, A = x.length; y < A; y += 3) {
        const b = x[y + 0], M = x[y + 1], w = x[y + 2];
        d.push(b, M, M, w, w, b);
      }
    } else if (p !== void 0) {
      const x = p.array;
      v = p.version;
      for (let y = 0, A = x.length / 3 - 1; y < A; y += 3) {
        const b = y + 0, M = y + 1, w = y + 2;
        d.push(b, M, M, w, w, b);
      }
    } else
      return;
    const g = new (zL(d) ? M4 : FM)(d, 1);
    g.version = v;
    const m = r.get(u);
    m && e.remove(m), r.set(u, g);
  }
  function h(u) {
    const d = r.get(u);
    if (d) {
      const f = u.index;
      f !== null && d.version < f.version && c(u);
    } else
      c(u);
    return r.get(u);
  }
  return {
    get: o,
    update: l,
    getWireframeAttribute: h
  };
}
function RV(s, e, t) {
  let i;
  function n(d) {
    i = d;
  }
  let r, a;
  function o(d) {
    r = d.type, a = d.bytesPerElement;
  }
  function l(d, f) {
    s.drawElements(i, f, r, d * a), t.update(f, i, 1);
  }
  function c(d, f, p) {
    p !== 0 && (s.drawElementsInstanced(i, f, r, d * a, p), t.update(f, i, p));
  }
  function h(d, f, p) {
    if (p === 0)
      return;
    e.get("WEBGL_multi_draw").multiDrawElementsWEBGL(i, f, 0, r, d, 0, p);
    let g = 0;
    for (let m = 0; m < p; m++)
      g += f[m];
    t.update(g, i, 1);
  }
  function u(d, f, p, v) {
    if (p === 0)
      return;
    const g = e.get("WEBGL_multi_draw");
    if (g === null)
      for (let m = 0; m < d.length; m++)
        c(d[m] / a, f[m], v[m]);
    else {
      g.multiDrawElementsInstancedWEBGL(i, f, 0, r, d, 0, v, 0, p);
      let m = 0;
      for (let x = 0; x < p; x++)
        m += f[x];
      for (let x = 0; x < v.length; x++)
        t.update(m, i, v[x]);
    }
  }
  this.setMode = n, this.setIndex = o, this.render = l, this.renderInstances = c, this.renderMultiDraw = h, this.renderMultiDrawInstances = u;
}
function DV(s) {
  const e = {
    geometries: 0,
    textures: 0
  }, t = {
    frame: 0,
    calls: 0,
    triangles: 0,
    points: 0,
    lines: 0
  };
  function i(r, a, o) {
    switch (t.calls++, a) {
      case s.TRIANGLES:
        t.triangles += o * (r / 3);
        break;
      case s.LINES:
        t.lines += o * (r / 2);
        break;
      case s.LINE_STRIP:
        t.lines += o * (r - 1);
        break;
      case s.LINE_LOOP:
        t.lines += o * r;
        break;
      case s.POINTS:
        t.points += o * r;
        break;
      default:
        console.error("THREE.WebGLInfo: Unknown draw mode:", a);
        break;
    }
  }
  function n() {
    t.calls = 0, t.triangles = 0, t.points = 0, t.lines = 0;
  }
  return {
    memory: e,
    render: t,
    programs: null,
    autoReset: !0,
    reset: n,
    update: i
  };
}
function LV(s, e, t) {
  const i = /* @__PURE__ */ new WeakMap(), n = new Yt();
  function r(a, o, l) {
    const c = a.morphTargetInfluences, h = o.morphAttributes.position || o.morphAttributes.normal || o.morphAttributes.color, u = h !== void 0 ? h.length : 0;
    let d = i.get(o);
    if (d === void 0 || d.count !== u) {
      let T = function() {
        w.dispose(), i.delete(o), o.removeEventListener("dispose", T);
      };
      d !== void 0 && d.texture.dispose();
      const f = o.morphAttributes.position !== void 0, p = o.morphAttributes.normal !== void 0, v = o.morphAttributes.color !== void 0, g = o.morphAttributes.position || [], m = o.morphAttributes.normal || [], x = o.morphAttributes.color || [];
      let y = 0;
      f === !0 && (y = 1), p === !0 && (y = 2), v === !0 && (y = 3);
      let A = o.attributes.position.count * y, b = 1;
      A > e.maxTextureSize && (b = Math.ceil(A / e.maxTextureSize), A = e.maxTextureSize);
      const M = new Float32Array(A * b * 4 * u), w = new b4(M, A, b, u);
      w.type = xn, w.needsUpdate = !0;
      const C = y * 4;
      for (let E = 0; E < u; E++) {
        const P = g[E], L = m[E], I = x[E], N = A * b * 4 * E;
        for (let H = 0; H < P.count; H++) {
          const U = H * C;
          f === !0 && (n.fromBufferAttribute(P, H), M[N + U + 0] = n.x, M[N + U + 1] = n.y, M[N + U + 2] = n.z, M[N + U + 3] = 0), p === !0 && (n.fromBufferAttribute(L, H), M[N + U + 4] = n.x, M[N + U + 5] = n.y, M[N + U + 6] = n.z, M[N + U + 7] = 0), v === !0 && (n.fromBufferAttribute(I, H), M[N + U + 8] = n.x, M[N + U + 9] = n.y, M[N + U + 10] = n.z, M[N + U + 11] = I.itemSize === 4 ? n.w : 1);
        }
      }
      d = {
        count: u,
        texture: w,
        size: new Se(A, b)
      }, i.set(o, d), o.addEventListener("dispose", T);
    }
    if (a.isInstancedMesh === !0 && a.morphTexture !== null)
      l.getUniforms().setValue(s, "morphTexture", a.morphTexture, t);
    else {
      let f = 0;
      for (let v = 0; v < c.length; v++)
        f += c[v];
      const p = o.morphTargetsRelative ? 1 : 1 - f;
      l.getUniforms().setValue(s, "morphTargetBaseInfluence", p), l.getUniforms().setValue(s, "morphTargetInfluences", c);
    }
    l.getUniforms().setValue(s, "morphTargetsTexture", d.texture, t), l.getUniforms().setValue(s, "morphTargetsTextureSize", d.size);
  }
  return {
    update: r
  };
}
function IV(s, e, t, i) {
  let n = /* @__PURE__ */ new WeakMap();
  function r(l) {
    const c = i.render.frame, h = l.geometry, u = e.get(l, h);
    if (n.get(u) !== c && (e.update(u), n.set(u, c)), l.isInstancedMesh && (l.hasEventListener("dispose", o) === !1 && l.addEventListener("dispose", o), n.get(l) !== c && (t.update(l.instanceMatrix, s.ARRAY_BUFFER), l.instanceColor !== null && t.update(l.instanceColor, s.ARRAY_BUFFER), n.set(l, c))), l.isSkinnedMesh) {
      const d = l.skeleton;
      n.get(d) !== c && (d.update(), n.set(d, c));
    }
    return u;
  }
  function a() {
    n = /* @__PURE__ */ new WeakMap();
  }
  function o(l) {
    const c = l.target;
    c.removeEventListener("dispose", o), t.remove(c.instanceMatrix), c.instanceColor !== null && t.remove(c.instanceColor);
  }
  return {
    update: r,
    dispose: a
  };
}
let r2 = class extends Ut {
  constructor(e, t, i, n, r, a, o, l, c, h = Au) {
    if (h !== Au && h !== yl)
      throw new Error("DepthTexture format must be either THREE.DepthFormat or THREE.DepthStencilFormat");
    i === void 0 && h === Au && (i = gl), i === void 0 && h === yl && (i = vl), super(null, n, r, a, o, l, h, i, c), this.isDepthTexture = !0, this.image = { width: e, height: t }, this.magFilter = o !== void 0 ? o : Ni, this.minFilter = l !== void 0 ? l : Ni, this.flipY = !1, this.generateMipmaps = !1, this.compareFunction = null;
  }
  copy(e) {
    return super.copy(e), this.compareFunction = e.compareFunction, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return this.compareFunction !== null && (t.compareFunction = this.compareFunction), t;
  }
};
const QL = /* @__PURE__ */ new Ut(), mE = /* @__PURE__ */ new r2(1, 1), $L = /* @__PURE__ */ new b4(), eI = /* @__PURE__ */ new BM(), tI = /* @__PURE__ */ new Vp(), gE = [], vE = [], yE = new Float32Array(16), xE = new Float32Array(9), AE = new Float32Array(4);
function Wp(s, e, t) {
  const i = s[0];
  if (i <= 0 || i > 0)
    return s;
  const n = e * t;
  let r = gE[n];
  if (r === void 0 && (r = new Float32Array(n), gE[n] = r), e !== 0) {
    i.toArray(r, 0);
    for (let a = 1, o = 0; a !== e; ++a)
      o += t, s[a].toArray(r, o);
  }
  return r;
}
function gs(s, e) {
  if (s.length !== e.length)
    return !1;
  for (let t = 0, i = s.length; t < i; t++)
    if (s[t] !== e[t])
      return !1;
  return !0;
}
function vs(s, e) {
  for (let t = 0, i = e.length; t < i; t++)
    s[t] = e[t];
}
function S4(s, e) {
  let t = vE[e];
  t === void 0 && (t = new Int32Array(e), vE[e] = t);
  for (let i = 0; i !== e; ++i)
    t[i] = s.allocateTextureUnit();
  return t;
}
function OV(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1f(this.addr, e), t[0] = e);
}
function NV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2f(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (gs(t, e))
      return;
    s.uniform2fv(this.addr, e), vs(t, e);
  }
}
function BV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3f(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else if (e.r !== void 0)
    (t[0] !== e.r || t[1] !== e.g || t[2] !== e.b) && (s.uniform3f(this.addr, e.r, e.g, e.b), t[0] = e.r, t[1] = e.g, t[2] = e.b);
  else {
    if (gs(t, e))
      return;
    s.uniform3fv(this.addr, e), vs(t, e);
  }
}
function FV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4f(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (gs(t, e))
      return;
    s.uniform4fv(this.addr, e), vs(t, e);
  }
}
function UV(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (gs(t, e))
      return;
    s.uniformMatrix2fv(this.addr, !1, e), vs(t, e);
  } else {
    if (gs(t, i))
      return;
    AE.set(i), s.uniformMatrix2fv(this.addr, !1, AE), vs(t, i);
  }
}
function zV(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (gs(t, e))
      return;
    s.uniformMatrix3fv(this.addr, !1, e), vs(t, e);
  } else {
    if (gs(t, i))
      return;
    xE.set(i), s.uniformMatrix3fv(this.addr, !1, xE), vs(t, i);
  }
}
function kV(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (gs(t, e))
      return;
    s.uniformMatrix4fv(this.addr, !1, e), vs(t, e);
  } else {
    if (gs(t, i))
      return;
    yE.set(i), s.uniformMatrix4fv(this.addr, !1, yE), vs(t, i);
  }
}
function GV(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1i(this.addr, e), t[0] = e);
}
function HV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2i(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (gs(t, e))
      return;
    s.uniform2iv(this.addr, e), vs(t, e);
  }
}
function VV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3i(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else {
    if (gs(t, e))
      return;
    s.uniform3iv(this.addr, e), vs(t, e);
  }
}
function WV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4i(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (gs(t, e))
      return;
    s.uniform4iv(this.addr, e), vs(t, e);
  }
}
function XV(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1ui(this.addr, e), t[0] = e);
}
function jV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2ui(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (gs(t, e))
      return;
    s.uniform2uiv(this.addr, e), vs(t, e);
  }
}
function qV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3ui(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else {
    if (gs(t, e))
      return;
    s.uniform3uiv(this.addr, e), vs(t, e);
  }
}
function YV(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4ui(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (gs(t, e))
      return;
    s.uniform4uiv(this.addr, e), vs(t, e);
  }
}
function ZV(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n);
  let r;
  this.type === s.SAMPLER_2D_SHADOW ? (mE.compareFunction = IM, r = mE) : r = QL, t.setTexture2D(e || r, n);
}
function KV(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTexture3D(e || eI, n);
}
function JV(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTextureCube(e || tI, n);
}
function QV(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTexture2DArray(e || $L, n);
}
function $V(s) {
  switch (s) {
    case 5126:
      return OV;
    case 35664:
      return NV;
    case 35665:
      return BV;
    case 35666:
      return FV;
    case 35674:
      return UV;
    case 35675:
      return zV;
    case 35676:
      return kV;
    case 5124:
    case 35670:
      return GV;
    case 35667:
    case 35671:
      return HV;
    case 35668:
    case 35672:
      return VV;
    case 35669:
    case 35673:
      return WV;
    case 5125:
      return XV;
    case 36294:
      return jV;
    case 36295:
      return qV;
    case 36296:
      return YV;
    case 35678:
    case 36198:
    case 36298:
    case 36306:
    case 35682:
      return ZV;
    case 35679:
    case 36299:
    case 36307:
      return KV;
    case 35680:
    case 36300:
    case 36308:
    case 36293:
      return JV;
    case 36289:
    case 36303:
    case 36311:
    case 36292:
      return QV;
  }
}
function eW(s, e) {
  s.uniform1fv(this.addr, e);
}
function tW(s, e) {
  const t = Wp(e, this.size, 2);
  s.uniform2fv(this.addr, t);
}
function iW(s, e) {
  const t = Wp(e, this.size, 3);
  s.uniform3fv(this.addr, t);
}
function nW(s, e) {
  const t = Wp(e, this.size, 4);
  s.uniform4fv(this.addr, t);
}
function sW(s, e) {
  const t = Wp(e, this.size, 4);
  s.uniformMatrix2fv(this.addr, !1, t);
}
function rW(s, e) {
  const t = Wp(e, this.size, 9);
  s.uniformMatrix3fv(this.addr, !1, t);
}
function aW(s, e) {
  const t = Wp(e, this.size, 16);
  s.uniformMatrix4fv(this.addr, !1, t);
}
function oW(s, e) {
  s.uniform1iv(this.addr, e);
}
function lW(s, e) {
  s.uniform2iv(this.addr, e);
}
function cW(s, e) {
  s.uniform3iv(this.addr, e);
}
function hW(s, e) {
  s.uniform4iv(this.addr, e);
}
function uW(s, e) {
  s.uniform1uiv(this.addr, e);
}
function dW(s, e) {
  s.uniform2uiv(this.addr, e);
}
function fW(s, e) {
  s.uniform3uiv(this.addr, e);
}
function pW(s, e) {
  s.uniform4uiv(this.addr, e);
}
function mW(s, e, t) {
  const i = this.cache, n = e.length, r = S4(t, n);
  gs(i, r) || (s.uniform1iv(this.addr, r), vs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture2D(e[a] || QL, r[a]);
}
function gW(s, e, t) {
  const i = this.cache, n = e.length, r = S4(t, n);
  gs(i, r) || (s.uniform1iv(this.addr, r), vs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture3D(e[a] || eI, r[a]);
}
function vW(s, e, t) {
  const i = this.cache, n = e.length, r = S4(t, n);
  gs(i, r) || (s.uniform1iv(this.addr, r), vs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTextureCube(e[a] || tI, r[a]);
}
function yW(s, e, t) {
  const i = this.cache, n = e.length, r = S4(t, n);
  gs(i, r) || (s.uniform1iv(this.addr, r), vs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture2DArray(e[a] || $L, r[a]);
}
function xW(s) {
  switch (s) {
    case 5126:
      return eW;
    case 35664:
      return tW;
    case 35665:
      return iW;
    case 35666:
      return nW;
    case 35674:
      return sW;
    case 35675:
      return rW;
    case 35676:
      return aW;
    case 5124:
    case 35670:
      return oW;
    case 35667:
    case 35671:
      return lW;
    case 35668:
    case 35672:
      return cW;
    case 35669:
    case 35673:
      return hW;
    case 5125:
      return uW;
    case 36294:
      return dW;
    case 36295:
      return fW;
    case 36296:
      return pW;
    case 35678:
    case 36198:
    case 36298:
    case 36306:
    case 35682:
      return mW;
    case 35679:
    case 36299:
    case 36307:
      return gW;
    case 35680:
    case 36300:
    case 36308:
    case 36293:
      return vW;
    case 36289:
    case 36303:
    case 36311:
    case 36292:
      return yW;
  }
}
let AW = class {
  constructor(e, t, i) {
    this.id = e, this.addr = i, this.cache = [], this.type = t.type, this.setValue = $V(t.type);
  }
}, bW = class {
  constructor(e, t, i) {
    this.id = e, this.addr = i, this.cache = [], this.type = t.type, this.size = t.size, this.setValue = xW(t.type);
  }
}, MW = class {
  constructor(e) {
    this.id = e, this.seq = [], this.map = {};
  }
  setValue(e, t, i) {
    const n = this.seq;
    for (let r = 0, a = n.length; r !== a; ++r) {
      const o = n[r];
      o.setValue(e, t[o.id], i);
    }
  }
};
const W8 = /(\w+)(\])?(\[|\.)?/g;
function bE(s, e) {
  s.seq.push(e), s.map[e.id] = e;
}
function SW(s, e, t) {
  const i = s.name, n = i.length;
  for (W8.lastIndex = 0; ; ) {
    const r = W8.exec(i), a = W8.lastIndex;
    let o = r[1];
    const l = r[2] === "]", c = r[3];
    if (l && (o = o | 0), c === void 0 || c === "[" && a + 2 === n) {
      bE(t, c === void 0 ? new AW(o, s, e) : new bW(o, s, e));
      break;
    } else {
      let u = t.map[o];
      u === void 0 && (u = new MW(o), bE(t, u)), t = u;
    }
  }
}
let Ty = class {
  constructor(e, t) {
    this.seq = [], this.map = {};
    const i = e.getProgramParameter(t, e.ACTIVE_UNIFORMS);
    for (let n = 0; n < i; ++n) {
      const r = e.getActiveUniform(t, n), a = e.getUniformLocation(t, r.name);
      SW(r, a, this);
    }
  }
  setValue(e, t, i, n) {
    const r = this.map[t];
    r !== void 0 && r.setValue(e, i, n);
  }
  setOptional(e, t, i) {
    const n = t[i];
    n !== void 0 && this.setValue(e, i, n);
  }
  static upload(e, t, i, n) {
    for (let r = 0, a = t.length; r !== a; ++r) {
      const o = t[r], l = i[o.id];
      l.needsUpdate !== !1 && o.setValue(e, l.value, n);
    }
  }
  static seqWithValue(e, t) {
    const i = [];
    for (let n = 0, r = e.length; n !== r; ++n) {
      const a = e[n];
      a.id in t && i.push(a);
    }
    return i;
  }
};
function ME(s, e, t) {
  const i = s.createShader(e);
  return s.shaderSource(i, t), s.compileShader(i), i;
}
const wW = 37297;
let EW = 0;
function TW(s, e) {
  const t = s.split(`
`), i = [], n = Math.max(e - 6, 0), r = Math.min(e + 6, t.length);
  for (let a = n; a < r; a++) {
    const o = a + 1;
    i.push(`${o === e ? ">" : " "} ${o}: ${t[a]}`);
  }
  return i.join(`
`);
}
function CW(s) {
  const e = bi.getPrimaries(bi.workingColorSpace), t = bi.getPrimaries(s);
  let i;
  switch (e === t ? i = "" : e === K1 && t === Z1 ? i = "LinearDisplayP3ToLinearSRGB" : e === Z1 && t === K1 && (i = "LinearSRGBToLinearDisplayP3"), s) {
    case Si:
    case n2:
      return [i, "LinearTransferOETF"];
    case qi:
    case A4:
      return [i, "sRGBTransferOETF"];
    default:
      return console.warn("THREE.WebGLProgram: Unsupported color space:", s), [i, "LinearTransferOETF"];
  }
}
function SE(s, e, t) {
  const i = s.getShaderParameter(e, s.COMPILE_STATUS), n = s.getShaderInfoLog(e).trim();
  if (i && n === "")
    return "";
  const r = /ERROR: 0:(\d+)/.exec(n);
  if (r) {
    const a = parseInt(r[1]);
    return t.toUpperCase() + `

` + n + `

` + TW(s.getShaderSource(e), a);
  } else
    return n;
}
function _W(s, e) {
  const t = CW(e);
  return `vec4 ${s}( vec4 value ) { return ${t[0]}( ${t[1]}( value ) ); }`;
}
function PW(s, e) {
  let t;
  switch (e) {
    case mM:
      t = "Linear";
      break;
    case gM:
      t = "Reinhard";
      break;
    case vM:
      t = "OptimizedCineon";
      break;
    case Hp:
      t = "ACESFilmic";
      break;
    case yM:
      t = "AgX";
      break;
    case xM:
      t = "Neutral";
      break;
    case wL:
      t = "Custom";
      break;
    default:
      console.warn("THREE.WebGLProgram: Unsupported toneMapping:", e), t = "Linear";
  }
  return "vec3 " + s + "( vec3 color ) { return " + t + "ToneMapping( color ); }";
}
function RW(s) {
  return [
    s.extensionClipCullDistance ? "#extension GL_ANGLE_clip_cull_distance : require" : "",
    s.extensionMultiDraw ? "#extension GL_ANGLE_multi_draw : require" : ""
  ].filter(r1).join(`
`);
}
function DW(s) {
  const e = [];
  for (const t in s) {
    const i = s[t];
    i !== !1 && e.push("#define " + t + " " + i);
  }
  return e.join(`
`);
}
function LW(s, e) {
  const t = {}, i = s.getProgramParameter(e, s.ACTIVE_ATTRIBUTES);
  for (let n = 0; n < i; n++) {
    const r = s.getActiveAttrib(e, n), a = r.name;
    let o = 1;
    r.type === s.FLOAT_MAT2 && (o = 2), r.type === s.FLOAT_MAT3 && (o = 3), r.type === s.FLOAT_MAT4 && (o = 4), t[a] = {
      type: r.type,
      location: s.getAttribLocation(e, a),
      locationSize: o
    };
  }
  return t;
}
function r1(s) {
  return s !== "";
}
function wE(s, e) {
  const t = e.numSpotLightShadows + e.numSpotLightMaps - e.numSpotLightShadowsWithMaps;
  return s.replace(/NUM_DIR_LIGHTS/g, e.numDirLights).replace(/NUM_SPOT_LIGHTS/g, e.numSpotLights).replace(/NUM_SPOT_LIGHT_MAPS/g, e.numSpotLightMaps).replace(/NUM_SPOT_LIGHT_COORDS/g, t).replace(/NUM_RECT_AREA_LIGHTS/g, e.numRectAreaLights).replace(/NUM_POINT_LIGHTS/g, e.numPointLights).replace(/NUM_HEMI_LIGHTS/g, e.numHemiLights).replace(/NUM_DIR_LIGHT_SHADOWS/g, e.numDirLightShadows).replace(/NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS/g, e.numSpotLightShadowsWithMaps).replace(/NUM_SPOT_LIGHT_SHADOWS/g, e.numSpotLightShadows).replace(/NUM_POINT_LIGHT_SHADOWS/g, e.numPointLightShadows);
}
function EE(s, e) {
  return s.replace(/NUM_CLIPPING_PLANES/g, e.numClippingPlanes).replace(/UNION_CLIPPING_PLANES/g, e.numClippingPlanes - e.numClipIntersection);
}
const IW = /^[ \t]*#include +<([\w\d./]+)>/gm;
function j6(s) {
  return s.replace(IW, NW);
}
const OW = /* @__PURE__ */ new Map();
function NW(s, e) {
  let t = ai[e];
  if (t === void 0) {
    const i = OW.get(e);
    if (i !== void 0)
      t = ai[i], console.warn('THREE.WebGLRenderer: Shader chunk "%s" has been deprecated. Use "%s" instead.', e, i);
    else
      throw new Error("Can not resolve #include <" + e + ">");
  }
  return j6(t);
}
const BW = /#pragma unroll_loop_start\s+for\s*\(\s*int\s+i\s*=\s*(\d+)\s*;\s*i\s*<\s*(\d+)\s*;\s*i\s*\+\+\s*\)\s*{([\s\S]+?)}\s+#pragma unroll_loop_end/g;
function TE(s) {
  return s.replace(BW, FW);
}
function FW(s, e, t, i) {
  let n = "";
  for (let r = parseInt(e); r < parseInt(t); r++)
    n += i.replace(/\[\s*i\s*\]/g, "[ " + r + " ]").replace(/UNROLLED_LOOP_INDEX/g, r);
  return n;
}
function CE(s) {
  let e = `precision ${s.precision} float;
	precision ${s.precision} int;
	precision ${s.precision} sampler2D;
	precision ${s.precision} samplerCube;
	precision ${s.precision} sampler3D;
	precision ${s.precision} sampler2DArray;
	precision ${s.precision} sampler2DShadow;
	precision ${s.precision} samplerCubeShadow;
	precision ${s.precision} sampler2DArrayShadow;
	precision ${s.precision} isampler2D;
	precision ${s.precision} isampler3D;
	precision ${s.precision} isamplerCube;
	precision ${s.precision} isampler2DArray;
	precision ${s.precision} usampler2D;
	precision ${s.precision} usampler3D;
	precision ${s.precision} usamplerCube;
	precision ${s.precision} usampler2DArray;
	`;
  return s.precision === "highp" ? e += `
#define HIGH_PRECISION` : s.precision === "mediump" ? e += `
#define MEDIUM_PRECISION` : s.precision === "lowp" && (e += `
#define LOW_PRECISION`), e;
}
function UW(s) {
  let e = "SHADOWMAP_TYPE_BASIC";
  return s.shadowMapType === pM ? e = "SHADOWMAP_TYPE_PCF" : s.shadowMapType === $g ? e = "SHADOWMAP_TYPE_PCF_SOFT" : s.shadowMapType === Do && (e = "SHADOWMAP_TYPE_VSM"), e;
}
function zW(s) {
  let e = "ENVMAP_TYPE_CUBE";
  if (s.envMap)
    switch (s.envMapMode) {
      case lo:
      case a0:
        e = "ENVMAP_TYPE_CUBE";
        break;
      case Qc:
        e = "ENVMAP_TYPE_CUBE_UV";
        break;
    }
  return e;
}
function kW(s) {
  let e = "ENVMAP_MODE_REFLECTION";
  if (s.envMap)
    switch (s.envMapMode) {
      case a0:
        e = "ENVMAP_MODE_REFRACTION";
        break;
    }
  return e;
}
function GW(s) {
  let e = "ENVMAP_BLENDING_NONE";
  if (s.envMap)
    switch (s.combine) {
      case e2:
        e = "ENVMAP_BLENDING_MULTIPLY";
        break;
      case ML:
        e = "ENVMAP_BLENDING_MIX";
        break;
      case SL:
        e = "ENVMAP_BLENDING_ADD";
        break;
    }
  return e;
}
function HW(s) {
  const e = s.envMapCubeUVHeight;
  if (e === null)
    return null;
  const t = Math.log2(e) - 2, i = 1 / e;
  return { texelWidth: 1 / (3 * Math.max(Math.pow(2, t), 7 * 16)), texelHeight: i, maxMip: t };
}
function VW(s, e, t, i) {
  const n = s.getContext(), r = t.defines;
  let a = t.vertexShader, o = t.fragmentShader;
  const l = UW(t), c = zW(t), h = kW(t), u = GW(t), d = HW(t), f = RW(t), p = DW(r), v = n.createProgram();
  let g, m, x = t.glslVersion ? "#version " + t.glslVersion + `
` : "";
  t.isRawShaderMaterial ? (g = [
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p
  ].filter(r1).join(`
`), g.length > 0 && (g += `
`), m = [
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p
  ].filter(r1).join(`
`), m.length > 0 && (m += `
`)) : (g = [
    CE(t),
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p,
    t.extensionClipCullDistance ? "#define USE_CLIP_DISTANCE" : "",
    t.batching ? "#define USE_BATCHING" : "",
    t.batchingColor ? "#define USE_BATCHING_COLOR" : "",
    t.instancing ? "#define USE_INSTANCING" : "",
    t.instancingColor ? "#define USE_INSTANCING_COLOR" : "",
    t.instancingMorph ? "#define USE_INSTANCING_MORPH" : "",
    t.useFog && t.fog ? "#define USE_FOG" : "",
    t.useFog && t.fogExp2 ? "#define FOG_EXP2" : "",
    t.map ? "#define USE_MAP" : "",
    t.envMap ? "#define USE_ENVMAP" : "",
    t.envMap ? "#define " + h : "",
    t.lightMap ? "#define USE_LIGHTMAP" : "",
    t.aoMap ? "#define USE_AOMAP" : "",
    t.bumpMap ? "#define USE_BUMPMAP" : "",
    t.normalMap ? "#define USE_NORMALMAP" : "",
    t.normalMapObjectSpace ? "#define USE_NORMALMAP_OBJECTSPACE" : "",
    t.normalMapTangentSpace ? "#define USE_NORMALMAP_TANGENTSPACE" : "",
    t.displacementMap ? "#define USE_DISPLACEMENTMAP" : "",
    t.emissiveMap ? "#define USE_EMISSIVEMAP" : "",
    t.anisotropy ? "#define USE_ANISOTROPY" : "",
    t.anisotropyMap ? "#define USE_ANISOTROPYMAP" : "",
    t.clearcoatMap ? "#define USE_CLEARCOATMAP" : "",
    t.clearcoatRoughnessMap ? "#define USE_CLEARCOAT_ROUGHNESSMAP" : "",
    t.clearcoatNormalMap ? "#define USE_CLEARCOAT_NORMALMAP" : "",
    t.iridescenceMap ? "#define USE_IRIDESCENCEMAP" : "",
    t.iridescenceThicknessMap ? "#define USE_IRIDESCENCE_THICKNESSMAP" : "",
    t.specularMap ? "#define USE_SPECULARMAP" : "",
    t.specularColorMap ? "#define USE_SPECULAR_COLORMAP" : "",
    t.specularIntensityMap ? "#define USE_SPECULAR_INTENSITYMAP" : "",
    t.roughnessMap ? "#define USE_ROUGHNESSMAP" : "",
    t.metalnessMap ? "#define USE_METALNESSMAP" : "",
    t.alphaMap ? "#define USE_ALPHAMAP" : "",
    t.alphaHash ? "#define USE_ALPHAHASH" : "",
    t.transmission ? "#define USE_TRANSMISSION" : "",
    t.transmissionMap ? "#define USE_TRANSMISSIONMAP" : "",
    t.thicknessMap ? "#define USE_THICKNESSMAP" : "",
    t.sheenColorMap ? "#define USE_SHEEN_COLORMAP" : "",
    t.sheenRoughnessMap ? "#define USE_SHEEN_ROUGHNESSMAP" : "",
    //
    t.mapUv ? "#define MAP_UV " + t.mapUv : "",
    t.alphaMapUv ? "#define ALPHAMAP_UV " + t.alphaMapUv : "",
    t.lightMapUv ? "#define LIGHTMAP_UV " + t.lightMapUv : "",
    t.aoMapUv ? "#define AOMAP_UV " + t.aoMapUv : "",
    t.emissiveMapUv ? "#define EMISSIVEMAP_UV " + t.emissiveMapUv : "",
    t.bumpMapUv ? "#define BUMPMAP_UV " + t.bumpMapUv : "",
    t.normalMapUv ? "#define NORMALMAP_UV " + t.normalMapUv : "",
    t.displacementMapUv ? "#define DISPLACEMENTMAP_UV " + t.displacementMapUv : "",
    t.metalnessMapUv ? "#define METALNESSMAP_UV " + t.metalnessMapUv : "",
    t.roughnessMapUv ? "#define ROUGHNESSMAP_UV " + t.roughnessMapUv : "",
    t.anisotropyMapUv ? "#define ANISOTROPYMAP_UV " + t.anisotropyMapUv : "",
    t.clearcoatMapUv ? "#define CLEARCOATMAP_UV " + t.clearcoatMapUv : "",
    t.clearcoatNormalMapUv ? "#define CLEARCOAT_NORMALMAP_UV " + t.clearcoatNormalMapUv : "",
    t.clearcoatRoughnessMapUv ? "#define CLEARCOAT_ROUGHNESSMAP_UV " + t.clearcoatRoughnessMapUv : "",
    t.iridescenceMapUv ? "#define IRIDESCENCEMAP_UV " + t.iridescenceMapUv : "",
    t.iridescenceThicknessMapUv ? "#define IRIDESCENCE_THICKNESSMAP_UV " + t.iridescenceThicknessMapUv : "",
    t.sheenColorMapUv ? "#define SHEEN_COLORMAP_UV " + t.sheenColorMapUv : "",
    t.sheenRoughnessMapUv ? "#define SHEEN_ROUGHNESSMAP_UV " + t.sheenRoughnessMapUv : "",
    t.specularMapUv ? "#define SPECULARMAP_UV " + t.specularMapUv : "",
    t.specularColorMapUv ? "#define SPECULAR_COLORMAP_UV " + t.specularColorMapUv : "",
    t.specularIntensityMapUv ? "#define SPECULAR_INTENSITYMAP_UV " + t.specularIntensityMapUv : "",
    t.transmissionMapUv ? "#define TRANSMISSIONMAP_UV " + t.transmissionMapUv : "",
    t.thicknessMapUv ? "#define THICKNESSMAP_UV " + t.thicknessMapUv : "",
    //
    t.vertexTangents && t.flatShading === !1 ? "#define USE_TANGENT" : "",
    t.vertexColors ? "#define USE_COLOR" : "",
    t.vertexAlphas ? "#define USE_COLOR_ALPHA" : "",
    t.vertexUv1s ? "#define USE_UV1" : "",
    t.vertexUv2s ? "#define USE_UV2" : "",
    t.vertexUv3s ? "#define USE_UV3" : "",
    t.pointsUvs ? "#define USE_POINTS_UV" : "",
    t.flatShading ? "#define FLAT_SHADED" : "",
    t.skinning ? "#define USE_SKINNING" : "",
    t.morphTargets ? "#define USE_MORPHTARGETS" : "",
    t.morphNormals && t.flatShading === !1 ? "#define USE_MORPHNORMALS" : "",
    t.morphColors ? "#define USE_MORPHCOLORS" : "",
    t.morphTargetsCount > 0 ? "#define MORPHTARGETS_TEXTURE_STRIDE " + t.morphTextureStride : "",
    t.morphTargetsCount > 0 ? "#define MORPHTARGETS_COUNT " + t.morphTargetsCount : "",
    t.doubleSided ? "#define DOUBLE_SIDED" : "",
    t.flipSided ? "#define FLIP_SIDED" : "",
    t.shadowMapEnabled ? "#define USE_SHADOWMAP" : "",
    t.shadowMapEnabled ? "#define " + l : "",
    t.sizeAttenuation ? "#define USE_SIZEATTENUATION" : "",
    t.numLightProbes > 0 ? "#define USE_LIGHT_PROBES" : "",
    t.logarithmicDepthBuffer ? "#define USE_LOGDEPTHBUF" : "",
    "uniform mat4 modelMatrix;",
    "uniform mat4 modelViewMatrix;",
    "uniform mat4 projectionMatrix;",
    "uniform mat4 viewMatrix;",
    "uniform mat3 normalMatrix;",
    "uniform vec3 cameraPosition;",
    "uniform bool isOrthographic;",
    "#ifdef USE_INSTANCING",
    "	attribute mat4 instanceMatrix;",
    "#endif",
    "#ifdef USE_INSTANCING_COLOR",
    "	attribute vec3 instanceColor;",
    "#endif",
    "#ifdef USE_INSTANCING_MORPH",
    "	uniform sampler2D morphTexture;",
    "#endif",
    "attribute vec3 position;",
    "attribute vec3 normal;",
    "attribute vec2 uv;",
    "#ifdef USE_UV1",
    "	attribute vec2 uv1;",
    "#endif",
    "#ifdef USE_UV2",
    "	attribute vec2 uv2;",
    "#endif",
    "#ifdef USE_UV3",
    "	attribute vec2 uv3;",
    "#endif",
    "#ifdef USE_TANGENT",
    "	attribute vec4 tangent;",
    "#endif",
    "#if defined( USE_COLOR_ALPHA )",
    "	attribute vec4 color;",
    "#elif defined( USE_COLOR )",
    "	attribute vec3 color;",
    "#endif",
    "#ifdef USE_SKINNING",
    "	attribute vec4 skinIndex;",
    "	attribute vec4 skinWeight;",
    "#endif",
    `
`
  ].filter(r1).join(`
`), m = [
    CE(t),
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p,
    t.useFog && t.fog ? "#define USE_FOG" : "",
    t.useFog && t.fogExp2 ? "#define FOG_EXP2" : "",
    t.alphaToCoverage ? "#define ALPHA_TO_COVERAGE" : "",
    t.map ? "#define USE_MAP" : "",
    t.matcap ? "#define USE_MATCAP" : "",
    t.envMap ? "#define USE_ENVMAP" : "",
    t.envMap ? "#define " + c : "",
    t.envMap ? "#define " + h : "",
    t.envMap ? "#define " + u : "",
    d ? "#define CUBEUV_TEXEL_WIDTH " + d.texelWidth : "",
    d ? "#define CUBEUV_TEXEL_HEIGHT " + d.texelHeight : "",
    d ? "#define CUBEUV_MAX_MIP " + d.maxMip + ".0" : "",
    t.lightMap ? "#define USE_LIGHTMAP" : "",
    t.aoMap ? "#define USE_AOMAP" : "",
    t.bumpMap ? "#define USE_BUMPMAP" : "",
    t.normalMap ? "#define USE_NORMALMAP" : "",
    t.normalMapObjectSpace ? "#define USE_NORMALMAP_OBJECTSPACE" : "",
    t.normalMapTangentSpace ? "#define USE_NORMALMAP_TANGENTSPACE" : "",
    t.emissiveMap ? "#define USE_EMISSIVEMAP" : "",
    t.anisotropy ? "#define USE_ANISOTROPY" : "",
    t.anisotropyMap ? "#define USE_ANISOTROPYMAP" : "",
    t.clearcoat ? "#define USE_CLEARCOAT" : "",
    t.clearcoatMap ? "#define USE_CLEARCOATMAP" : "",
    t.clearcoatRoughnessMap ? "#define USE_CLEARCOAT_ROUGHNESSMAP" : "",
    t.clearcoatNormalMap ? "#define USE_CLEARCOAT_NORMALMAP" : "",
    t.dispersion ? "#define USE_DISPERSION" : "",
    t.iridescence ? "#define USE_IRIDESCENCE" : "",
    t.iridescenceMap ? "#define USE_IRIDESCENCEMAP" : "",
    t.iridescenceThicknessMap ? "#define USE_IRIDESCENCE_THICKNESSMAP" : "",
    t.specularMap ? "#define USE_SPECULARMAP" : "",
    t.specularColorMap ? "#define USE_SPECULAR_COLORMAP" : "",
    t.specularIntensityMap ? "#define USE_SPECULAR_INTENSITYMAP" : "",
    t.roughnessMap ? "#define USE_ROUGHNESSMAP" : "",
    t.metalnessMap ? "#define USE_METALNESSMAP" : "",
    t.alphaMap ? "#define USE_ALPHAMAP" : "",
    t.alphaTest ? "#define USE_ALPHATEST" : "",
    t.alphaHash ? "#define USE_ALPHAHASH" : "",
    t.sheen ? "#define USE_SHEEN" : "",
    t.sheenColorMap ? "#define USE_SHEEN_COLORMAP" : "",
    t.sheenRoughnessMap ? "#define USE_SHEEN_ROUGHNESSMAP" : "",
    t.transmission ? "#define USE_TRANSMISSION" : "",
    t.transmissionMap ? "#define USE_TRANSMISSIONMAP" : "",
    t.thicknessMap ? "#define USE_THICKNESSMAP" : "",
    t.vertexTangents && t.flatShading === !1 ? "#define USE_TANGENT" : "",
    t.vertexColors || t.instancingColor || t.batchingColor ? "#define USE_COLOR" : "",
    t.vertexAlphas ? "#define USE_COLOR_ALPHA" : "",
    t.vertexUv1s ? "#define USE_UV1" : "",
    t.vertexUv2s ? "#define USE_UV2" : "",
    t.vertexUv3s ? "#define USE_UV3" : "",
    t.pointsUvs ? "#define USE_POINTS_UV" : "",
    t.gradientMap ? "#define USE_GRADIENTMAP" : "",
    t.flatShading ? "#define FLAT_SHADED" : "",
    t.doubleSided ? "#define DOUBLE_SIDED" : "",
    t.flipSided ? "#define FLIP_SIDED" : "",
    t.shadowMapEnabled ? "#define USE_SHADOWMAP" : "",
    t.shadowMapEnabled ? "#define " + l : "",
    t.premultipliedAlpha ? "#define PREMULTIPLIED_ALPHA" : "",
    t.numLightProbes > 0 ? "#define USE_LIGHT_PROBES" : "",
    t.decodeVideoTexture ? "#define DECODE_VIDEO_TEXTURE" : "",
    t.logarithmicDepthBuffer ? "#define USE_LOGDEPTHBUF" : "",
    "uniform mat4 viewMatrix;",
    "uniform vec3 cameraPosition;",
    "uniform bool isOrthographic;",
    t.toneMapping !== ol ? "#define TONE_MAPPING" : "",
    t.toneMapping !== ol ? ai.tonemapping_pars_fragment : "",
    // this code is required here because it is used by the toneMapping() function defined below
    t.toneMapping !== ol ? PW("toneMapping", t.toneMapping) : "",
    t.dithering ? "#define DITHERING" : "",
    t.opaque ? "#define OPAQUE" : "",
    ai.colorspace_pars_fragment,
    // this code is required here because it is used by the various encoding/decoding function defined below
    _W("linearToOutputTexel", t.outputColorSpace),
    t.useDepthPacking ? "#define DEPTH_PACKING " + t.depthPacking : "",
    `
`
  ].filter(r1).join(`
`)), a = j6(a), a = wE(a, t), a = EE(a, t), o = j6(o), o = wE(o, t), o = EE(o, t), a = TE(a), o = TE(o), t.isRawShaderMaterial !== !0 && (x = `#version 300 es
`, g = [
    f,
    "#define attribute in",
    "#define varying out",
    "#define texture2D texture"
  ].join(`
`) + `
` + g, m = [
    "#define varying in",
    t.glslVersion === W6 ? "" : "layout(location = 0) out highp vec4 pc_fragColor;",
    t.glslVersion === W6 ? "" : "#define gl_FragColor pc_fragColor",
    "#define gl_FragDepthEXT gl_FragDepth",
    "#define texture2D texture",
    "#define textureCube texture",
    "#define texture2DProj textureProj",
    "#define texture2DLodEXT textureLod",
    "#define texture2DProjLodEXT textureProjLod",
    "#define textureCubeLodEXT textureLod",
    "#define texture2DGradEXT textureGrad",
    "#define texture2DProjGradEXT textureProjGrad",
    "#define textureCubeGradEXT textureGrad"
  ].join(`
`) + `
` + m);
  const y = x + g + a, A = x + m + o, b = ME(n, n.VERTEX_SHADER, y), M = ME(n, n.FRAGMENT_SHADER, A);
  n.attachShader(v, b), n.attachShader(v, M), t.index0AttributeName !== void 0 ? n.bindAttribLocation(v, 0, t.index0AttributeName) : t.morphTargets === !0 && n.bindAttribLocation(v, 0, "position"), n.linkProgram(v);
  function w(P) {
    if (s.debug.checkShaderErrors) {
      const L = n.getProgramInfoLog(v).trim(), I = n.getShaderInfoLog(b).trim(), N = n.getShaderInfoLog(M).trim();
      let H = !0, U = !0;
      if (n.getProgramParameter(v, n.LINK_STATUS) === !1)
        if (H = !1, typeof s.debug.onShaderError == "function")
          s.debug.onShaderError(n, v, b, M);
        else {
          const W = SE(n, b, "vertex"), X = SE(n, M, "fragment");
          console.error(
            "THREE.WebGLProgram: Shader Error " + n.getError() + " - VALIDATE_STATUS " + n.getProgramParameter(v, n.VALIDATE_STATUS) + `

Material Name: ` + P.name + `
Material Type: ` + P.type + `

Program Info Log: ` + L + `
` + W + `
` + X
          );
        }
      else
        L !== "" ? console.warn("THREE.WebGLProgram: Program Info Log:", L) : (I === "" || N === "") && (U = !1);
      U && (P.diagnostics = {
        runnable: H,
        programLog: L,
        vertexShader: {
          log: I,
          prefix: g
        },
        fragmentShader: {
          log: N,
          prefix: m
        }
      });
    }
    n.deleteShader(b), n.deleteShader(M), C = new Ty(n, v), T = LW(n, v);
  }
  let C;
  this.getUniforms = function() {
    return C === void 0 && w(this), C;
  };
  let T;
  this.getAttributes = function() {
    return T === void 0 && w(this), T;
  };
  let E = t.rendererExtensionParallelShaderCompile === !1;
  return this.isReady = function() {
    return E === !1 && (E = n.getProgramParameter(v, wW)), E;
  }, this.destroy = function() {
    i.releaseStatesOfProgram(this), n.deleteProgram(v), this.program = void 0;
  }, this.type = t.shaderType, this.name = t.shaderName, this.id = EW++, this.cacheKey = e, this.usedTimes = 1, this.program = v, this.vertexShader = b, this.fragmentShader = M, this;
}
let WW = 0, XW = class {
  constructor() {
    this.shaderCache = /* @__PURE__ */ new Map(), this.materialCache = /* @__PURE__ */ new Map();
  }
  update(e) {
    const t = e.vertexShader, i = e.fragmentShader, n = this._getShaderStage(t), r = this._getShaderStage(i), a = this._getShaderCacheForMaterial(e);
    return a.has(n) === !1 && (a.add(n), n.usedTimes++), a.has(r) === !1 && (a.add(r), r.usedTimes++), this;
  }
  remove(e) {
    const t = this.materialCache.get(e);
    for (const i of t)
      i.usedTimes--, i.usedTimes === 0 && this.shaderCache.delete(i.code);
    return this.materialCache.delete(e), this;
  }
  getVertexShaderID(e) {
    return this._getShaderStage(e.vertexShader).id;
  }
  getFragmentShaderID(e) {
    return this._getShaderStage(e.fragmentShader).id;
  }
  dispose() {
    this.shaderCache.clear(), this.materialCache.clear();
  }
  _getShaderCacheForMaterial(e) {
    const t = this.materialCache;
    let i = t.get(e);
    return i === void 0 && (i = /* @__PURE__ */ new Set(), t.set(e, i)), i;
  }
  _getShaderStage(e) {
    const t = this.shaderCache;
    let i = t.get(e);
    return i === void 0 && (i = new jW(e), t.set(e, i)), i;
  }
}, jW = class {
  constructor(e) {
    this.id = WW++, this.code = e, this.usedTimes = 0;
  }
};
function qW(s, e, t, i, n, r, a) {
  const o = new e0(), l = new XW(), c = /* @__PURE__ */ new Set(), h = [], u = n.logarithmicDepthBuffer, d = n.vertexTextures;
  let f = n.precision;
  const p = {
    MeshDepthMaterial: "depth",
    MeshDistanceMaterial: "distanceRGBA",
    MeshNormalMaterial: "normal",
    MeshBasicMaterial: "basic",
    MeshLambertMaterial: "lambert",
    MeshPhongMaterial: "phong",
    MeshToonMaterial: "toon",
    MeshStandardMaterial: "physical",
    MeshPhysicalMaterial: "physical",
    MeshMatcapMaterial: "matcap",
    LineBasicMaterial: "basic",
    LineDashedMaterial: "dashed",
    PointsMaterial: "points",
    ShadowMaterial: "shadow",
    SpriteMaterial: "sprite"
  };
  function v(T) {
    return c.add(T), T === 0 ? "uv" : `uv${T}`;
  }
  function g(T, E, P, L, I) {
    const N = L.fog, H = I.geometry, U = T.isMeshStandardMaterial ? L.environment : null, W = (T.isMeshStandardMaterial ? t : e).get(T.envMap || U), X = W && W.mapping === Qc ? W.image.height : null, K = p[T.type];
    T.precision !== null && (f = n.getMaxPrecision(T.precision), f !== T.precision && console.warn("THREE.WebGLProgram.getParameters:", T.precision, "not supported, using", f, "instead."));
    const te = H.morphAttributes.position || H.morphAttributes.normal || H.morphAttributes.color, re = te !== void 0 ? te.length : 0;
    let be = 0;
    H.morphAttributes.position !== void 0 && (be = 1), H.morphAttributes.normal !== void 0 && (be = 2), H.morphAttributes.color !== void 0 && (be = 3);
    let Pe, J, fe, Me;
    if (K) {
      const Ot = xa[K];
      Pe = Ot.vertexShader, J = Ot.fragmentShader;
    } else
      Pe = T.vertexShader, J = T.fragmentShader, l.update(T), fe = l.getVertexShaderID(T), Me = l.getFragmentShaderID(T);
    const ye = s.getRenderTarget(), Ye = I.isInstancedMesh === !0, Je = I.isBatchedMesh === !0, rt = !!T.map, wt = !!T.matcap, k = !!W, Fe = !!T.aoMap, Ge = !!T.lightMap, qe = !!T.bumpMap, Ie = !!T.normalMap, vt = !!T.displacementMap, Ve = !!T.emissiveMap, Ze = !!T.metalnessMap, G = !!T.roughnessMap, D = T.anisotropy > 0, ne = T.clearcoat > 0, ve = T.dispersion > 0, Y = T.iridescence > 0, ee = T.sheen > 0, we = T.transmission > 0, de = D && !!T.anisotropyMap, xe = ne && !!T.clearcoatMap, it = ne && !!T.clearcoatNormalMap, q = ne && !!T.clearcoatRoughnessMap, ae = Y && !!T.iridescenceMap, Ee = Y && !!T.iridescenceThicknessMap, Le = ee && !!T.sheenColorMap, Ae = ee && !!T.sheenRoughnessMap, We = !!T.specularMap, ot = !!T.specularColorMap, Ct = !!T.specularIntensityMap, F = we && !!T.transmissionMap, ue = we && !!T.thicknessMap, Q = !!T.gradientMap, he = !!T.alphaMap, Be = T.alphaTest > 0, ft = !!T.alphaHash, Et = !!T.extensions;
    let ei = ol;
    T.toneMapped && (ye === null || ye.isXRRenderTarget === !0) && (ei = s.toneMapping);
    const di = {
      shaderID: K,
      shaderType: T.type,
      shaderName: T.name,
      vertexShader: Pe,
      fragmentShader: J,
      defines: T.defines,
      customVertexShaderID: fe,
      customFragmentShaderID: Me,
      isRawShaderMaterial: T.isRawShaderMaterial === !0,
      glslVersion: T.glslVersion,
      precision: f,
      batching: Je,
      batchingColor: Je && I._colorsTexture !== null,
      instancing: Ye,
      instancingColor: Ye && I.instanceColor !== null,
      instancingMorph: Ye && I.morphTexture !== null,
      supportsVertexTextures: d,
      outputColorSpace: ye === null ? s.outputColorSpace : ye.isXRRenderTarget === !0 ? ye.texture.colorSpace : Si,
      alphaToCoverage: !!T.alphaToCoverage,
      map: rt,
      matcap: wt,
      envMap: k,
      envMapMode: k && W.mapping,
      envMapCubeUVHeight: X,
      aoMap: Fe,
      lightMap: Ge,
      bumpMap: qe,
      normalMap: Ie,
      displacementMap: d && vt,
      emissiveMap: Ve,
      normalMapObjectSpace: Ie && T.normalMapType === PL,
      normalMapTangentSpace: Ie && T.normalMapType === eh,
      metalnessMap: Ze,
      roughnessMap: G,
      anisotropy: D,
      anisotropyMap: de,
      clearcoat: ne,
      clearcoatMap: xe,
      clearcoatNormalMap: it,
      clearcoatRoughnessMap: q,
      dispersion: ve,
      iridescence: Y,
      iridescenceMap: ae,
      iridescenceThicknessMap: Ee,
      sheen: ee,
      sheenColorMap: Le,
      sheenRoughnessMap: Ae,
      specularMap: We,
      specularColorMap: ot,
      specularIntensityMap: Ct,
      transmission: we,
      transmissionMap: F,
      thicknessMap: ue,
      gradientMap: Q,
      opaque: T.transparent === !1 && T.blending === so && T.alphaToCoverage === !1,
      alphaMap: he,
      alphaTest: Be,
      alphaHash: ft,
      combine: T.combine,
      //
      mapUv: rt && v(T.map.channel),
      aoMapUv: Fe && v(T.aoMap.channel),
      lightMapUv: Ge && v(T.lightMap.channel),
      bumpMapUv: qe && v(T.bumpMap.channel),
      normalMapUv: Ie && v(T.normalMap.channel),
      displacementMapUv: vt && v(T.displacementMap.channel),
      emissiveMapUv: Ve && v(T.emissiveMap.channel),
      metalnessMapUv: Ze && v(T.metalnessMap.channel),
      roughnessMapUv: G && v(T.roughnessMap.channel),
      anisotropyMapUv: de && v(T.anisotropyMap.channel),
      clearcoatMapUv: xe && v(T.clearcoatMap.channel),
      clearcoatNormalMapUv: it && v(T.clearcoatNormalMap.channel),
      clearcoatRoughnessMapUv: q && v(T.clearcoatRoughnessMap.channel),
      iridescenceMapUv: ae && v(T.iridescenceMap.channel),
      iridescenceThicknessMapUv: Ee && v(T.iridescenceThicknessMap.channel),
      sheenColorMapUv: Le && v(T.sheenColorMap.channel),
      sheenRoughnessMapUv: Ae && v(T.sheenRoughnessMap.channel),
      specularMapUv: We && v(T.specularMap.channel),
      specularColorMapUv: ot && v(T.specularColorMap.channel),
      specularIntensityMapUv: Ct && v(T.specularIntensityMap.channel),
      transmissionMapUv: F && v(T.transmissionMap.channel),
      thicknessMapUv: ue && v(T.thicknessMap.channel),
      alphaMapUv: he && v(T.alphaMap.channel),
      //
      vertexTangents: !!H.attributes.tangent && (Ie || D),
      vertexColors: T.vertexColors,
      vertexAlphas: T.vertexColors === !0 && !!H.attributes.color && H.attributes.color.itemSize === 4,
      pointsUvs: I.isPoints === !0 && !!H.attributes.uv && (rt || he),
      fog: !!N,
      useFog: T.fog === !0,
      fogExp2: !!N && N.isFogExp2,
      flatShading: T.flatShading === !0,
      sizeAttenuation: T.sizeAttenuation === !0,
      logarithmicDepthBuffer: u,
      skinning: I.isSkinnedMesh === !0,
      morphTargets: H.morphAttributes.position !== void 0,
      morphNormals: H.morphAttributes.normal !== void 0,
      morphColors: H.morphAttributes.color !== void 0,
      morphTargetsCount: re,
      morphTextureStride: be,
      numDirLights: E.directional.length,
      numPointLights: E.point.length,
      numSpotLights: E.spot.length,
      numSpotLightMaps: E.spotLightMap.length,
      numRectAreaLights: E.rectArea.length,
      numHemiLights: E.hemi.length,
      numDirLightShadows: E.directionalShadowMap.length,
      numPointLightShadows: E.pointShadowMap.length,
      numSpotLightShadows: E.spotShadowMap.length,
      numSpotLightShadowsWithMaps: E.numSpotLightShadowsWithMaps,
      numLightProbes: E.numLightProbes,
      numClippingPlanes: a.numPlanes,
      numClipIntersection: a.numIntersection,
      dithering: T.dithering,
      shadowMapEnabled: s.shadowMap.enabled && P.length > 0,
      shadowMapType: s.shadowMap.type,
      toneMapping: ei,
      decodeVideoTexture: rt && T.map.isVideoTexture === !0 && bi.getTransfer(T.map.colorSpace) === pn,
      premultipliedAlpha: T.premultipliedAlpha,
      doubleSided: T.side === ui,
      flipSided: T.side === Ps,
      useDepthPacking: T.depthPacking >= 0,
      depthPacking: T.depthPacking || 0,
      index0AttributeName: T.index0AttributeName,
      extensionClipCullDistance: Et && T.extensions.clipCullDistance === !0 && i.has("WEBGL_clip_cull_distance"),
      extensionMultiDraw: (Et && T.extensions.multiDraw === !0 || Je) && i.has("WEBGL_multi_draw"),
      rendererExtensionParallelShaderCompile: i.has("KHR_parallel_shader_compile"),
      customProgramCacheKey: T.customProgramCacheKey()
    };
    return di.vertexUv1s = c.has(1), di.vertexUv2s = c.has(2), di.vertexUv3s = c.has(3), c.clear(), di;
  }
  function m(T) {
    const E = [];
    if (T.shaderID ? E.push(T.shaderID) : (E.push(T.customVertexShaderID), E.push(T.customFragmentShaderID)), T.defines !== void 0)
      for (const P in T.defines)
        E.push(P), E.push(T.defines[P]);
    return T.isRawShaderMaterial === !1 && (x(E, T), y(E, T), E.push(s.outputColorSpace)), E.push(T.customProgramCacheKey), E.join();
  }
  function x(T, E) {
    T.push(E.precision), T.push(E.outputColorSpace), T.push(E.envMapMode), T.push(E.envMapCubeUVHeight), T.push(E.mapUv), T.push(E.alphaMapUv), T.push(E.lightMapUv), T.push(E.aoMapUv), T.push(E.bumpMapUv), T.push(E.normalMapUv), T.push(E.displacementMapUv), T.push(E.emissiveMapUv), T.push(E.metalnessMapUv), T.push(E.roughnessMapUv), T.push(E.anisotropyMapUv), T.push(E.clearcoatMapUv), T.push(E.clearcoatNormalMapUv), T.push(E.clearcoatRoughnessMapUv), T.push(E.iridescenceMapUv), T.push(E.iridescenceThicknessMapUv), T.push(E.sheenColorMapUv), T.push(E.sheenRoughnessMapUv), T.push(E.specularMapUv), T.push(E.specularColorMapUv), T.push(E.specularIntensityMapUv), T.push(E.transmissionMapUv), T.push(E.thicknessMapUv), T.push(E.combine), T.push(E.fogExp2), T.push(E.sizeAttenuation), T.push(E.morphTargetsCount), T.push(E.morphAttributeCount), T.push(E.numDirLights), T.push(E.numPointLights), T.push(E.numSpotLights), T.push(E.numSpotLightMaps), T.push(E.numHemiLights), T.push(E.numRectAreaLights), T.push(E.numDirLightShadows), T.push(E.numPointLightShadows), T.push(E.numSpotLightShadows), T.push(E.numSpotLightShadowsWithMaps), T.push(E.numLightProbes), T.push(E.shadowMapType), T.push(E.toneMapping), T.push(E.numClippingPlanes), T.push(E.numClipIntersection), T.push(E.depthPacking);
  }
  function y(T, E) {
    o.disableAll(), E.supportsVertexTextures && o.enable(0), E.instancing && o.enable(1), E.instancingColor && o.enable(2), E.instancingMorph && o.enable(3), E.matcap && o.enable(4), E.envMap && o.enable(5), E.normalMapObjectSpace && o.enable(6), E.normalMapTangentSpace && o.enable(7), E.clearcoat && o.enable(8), E.iridescence && o.enable(9), E.alphaTest && o.enable(10), E.vertexColors && o.enable(11), E.vertexAlphas && o.enable(12), E.vertexUv1s && o.enable(13), E.vertexUv2s && o.enable(14), E.vertexUv3s && o.enable(15), E.vertexTangents && o.enable(16), E.anisotropy && o.enable(17), E.alphaHash && o.enable(18), E.batching && o.enable(19), E.dispersion && o.enable(20), E.batchingColor && o.enable(21), T.push(o.mask), o.disableAll(), E.fog && o.enable(0), E.useFog && o.enable(1), E.flatShading && o.enable(2), E.logarithmicDepthBuffer && o.enable(3), E.skinning && o.enable(4), E.morphTargets && o.enable(5), E.morphNormals && o.enable(6), E.morphColors && o.enable(7), E.premultipliedAlpha && o.enable(8), E.shadowMapEnabled && o.enable(9), E.doubleSided && o.enable(10), E.flipSided && o.enable(11), E.useDepthPacking && o.enable(12), E.dithering && o.enable(13), E.transmission && o.enable(14), E.sheen && o.enable(15), E.opaque && o.enable(16), E.pointsUvs && o.enable(17), E.decodeVideoTexture && o.enable(18), E.alphaToCoverage && o.enable(19), T.push(o.mask);
  }
  function A(T) {
    const E = p[T.type];
    let P;
    if (E) {
      const L = xa[E];
      P = rs.clone(L.uniforms);
    } else
      P = T.uniforms;
    return P;
  }
  function b(T, E) {
    let P;
    for (let L = 0, I = h.length; L < I; L++) {
      const N = h[L];
      if (N.cacheKey === E) {
        P = N, ++P.usedTimes;
        break;
      }
    }
    return P === void 0 && (P = new VW(s, E, T, r), h.push(P)), P;
  }
  function M(T) {
    if (--T.usedTimes === 0) {
      const E = h.indexOf(T);
      h[E] = h[h.length - 1], h.pop(), T.destroy();
    }
  }
  function w(T) {
    l.remove(T);
  }
  function C() {
    l.dispose();
  }
  return {
    getParameters: g,
    getProgramCacheKey: m,
    getUniforms: A,
    acquireProgram: b,
    releaseProgram: M,
    releaseShaderCache: w,
    // Exposed for resource monitoring & error feedback via renderer.info:
    programs: h,
    dispose: C
  };
}
function YW() {
  let s = /* @__PURE__ */ new WeakMap();
  function e(r) {
    let a = s.get(r);
    return a === void 0 && (a = {}, s.set(r, a)), a;
  }
  function t(r) {
    s.delete(r);
  }
  function i(r, a, o) {
    s.get(r)[a] = o;
  }
  function n() {
    s = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: e,
    remove: t,
    update: i,
    dispose: n
  };
}
function ZW(s, e) {
  return s.groupOrder !== e.groupOrder ? s.groupOrder - e.groupOrder : s.renderOrder !== e.renderOrder ? s.renderOrder - e.renderOrder : s.material.id !== e.material.id ? s.material.id - e.material.id : s.z !== e.z ? s.z - e.z : s.id - e.id;
}
function _E(s, e) {
  return s.groupOrder !== e.groupOrder ? s.groupOrder - e.groupOrder : s.renderOrder !== e.renderOrder ? s.renderOrder - e.renderOrder : s.z !== e.z ? e.z - s.z : s.id - e.id;
}
function PE() {
  const s = [];
  let e = 0;
  const t = [], i = [], n = [];
  function r() {
    e = 0, t.length = 0, i.length = 0, n.length = 0;
  }
  function a(u, d, f, p, v, g) {
    let m = s[e];
    return m === void 0 ? (m = {
      id: u.id,
      object: u,
      geometry: d,
      material: f,
      groupOrder: p,
      renderOrder: u.renderOrder,
      z: v,
      group: g
    }, s[e] = m) : (m.id = u.id, m.object = u, m.geometry = d, m.material = f, m.groupOrder = p, m.renderOrder = u.renderOrder, m.z = v, m.group = g), e++, m;
  }
  function o(u, d, f, p, v, g) {
    const m = a(u, d, f, p, v, g);
    f.transmission > 0 ? i.push(m) : f.transparent === !0 ? n.push(m) : t.push(m);
  }
  function l(u, d, f, p, v, g) {
    const m = a(u, d, f, p, v, g);
    f.transmission > 0 ? i.unshift(m) : f.transparent === !0 ? n.unshift(m) : t.unshift(m);
  }
  function c(u, d) {
    t.length > 1 && t.sort(u || ZW), i.length > 1 && i.sort(d || _E), n.length > 1 && n.sort(d || _E);
  }
  function h() {
    for (let u = e, d = s.length; u < d; u++) {
      const f = s[u];
      if (f.id === null)
        break;
      f.id = null, f.object = null, f.geometry = null, f.material = null, f.group = null;
    }
  }
  return {
    opaque: t,
    transmissive: i,
    transparent: n,
    init: r,
    push: o,
    unshift: l,
    finish: h,
    sort: c
  };
}
function KW() {
  let s = /* @__PURE__ */ new WeakMap();
  function e(i, n) {
    const r = s.get(i);
    let a;
    return r === void 0 ? (a = new PE(), s.set(i, [a])) : n >= r.length ? (a = new PE(), r.push(a)) : a = r[n], a;
  }
  function t() {
    s = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: e,
    dispose: t
  };
}
function JW() {
  const s = {};
  return {
    get: function(e) {
      if (s[e.id] !== void 0)
        return s[e.id];
      let t;
      switch (e.type) {
        case "DirectionalLight":
          t = {
            direction: new _(),
            color: new _e()
          };
          break;
        case "SpotLight":
          t = {
            position: new _(),
            direction: new _(),
            color: new _e(),
            distance: 0,
            coneCos: 0,
            penumbraCos: 0,
            decay: 0
          };
          break;
        case "PointLight":
          t = {
            position: new _(),
            color: new _e(),
            distance: 0,
            decay: 0
          };
          break;
        case "HemisphereLight":
          t = {
            direction: new _(),
            skyColor: new _e(),
            groundColor: new _e()
          };
          break;
        case "RectAreaLight":
          t = {
            color: new _e(),
            position: new _(),
            halfWidth: new _(),
            halfHeight: new _()
          };
          break;
      }
      return s[e.id] = t, t;
    }
  };
}
function QW() {
  const s = {};
  return {
    get: function(e) {
      if (s[e.id] !== void 0)
        return s[e.id];
      let t;
      switch (e.type) {
        case "DirectionalLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new Se()
          };
          break;
        case "SpotLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new Se()
          };
          break;
        case "PointLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new Se(),
            shadowCameraNear: 1,
            shadowCameraFar: 1e3
          };
          break;
      }
      return s[e.id] = t, t;
    }
  };
}
let $W = 0;
function eX(s, e) {
  return (e.castShadow ? 2 : 0) - (s.castShadow ? 2 : 0) + (e.map ? 1 : 0) - (s.map ? 1 : 0);
}
function tX(s) {
  const e = new JW(), t = QW(), i = {
    version: 0,
    hash: {
      directionalLength: -1,
      pointLength: -1,
      spotLength: -1,
      rectAreaLength: -1,
      hemiLength: -1,
      numDirectionalShadows: -1,
      numPointShadows: -1,
      numSpotShadows: -1,
      numSpotMaps: -1,
      numLightProbes: -1
    },
    ambient: [0, 0, 0],
    probe: [],
    directional: [],
    directionalShadow: [],
    directionalShadowMap: [],
    directionalShadowMatrix: [],
    spot: [],
    spotLightMap: [],
    spotShadow: [],
    spotShadowMap: [],
    spotLightMatrix: [],
    rectArea: [],
    rectAreaLTC1: null,
    rectAreaLTC2: null,
    point: [],
    pointShadow: [],
    pointShadowMap: [],
    pointShadowMatrix: [],
    hemi: [],
    numSpotLightShadowsWithMaps: 0,
    numLightProbes: 0
  };
  for (let c = 0; c < 9; c++)
    i.probe.push(new _());
  const n = new _(), r = new et(), a = new et();
  function o(c) {
    let h = 0, u = 0, d = 0;
    for (let T = 0; T < 9; T++)
      i.probe[T].set(0, 0, 0);
    let f = 0, p = 0, v = 0, g = 0, m = 0, x = 0, y = 0, A = 0, b = 0, M = 0, w = 0;
    c.sort(eX);
    for (let T = 0, E = c.length; T < E; T++) {
      const P = c[T], L = P.color, I = P.intensity, N = P.distance, H = P.shadow && P.shadow.map ? P.shadow.map.texture : null;
      if (P.isAmbientLight)
        h += L.r * I, u += L.g * I, d += L.b * I;
      else if (P.isLightProbe) {
        for (let U = 0; U < 9; U++)
          i.probe[U].addScaledVector(P.sh.coefficients[U], I);
        w++;
      } else if (P.isDirectionalLight) {
        const U = e.get(P);
        if (U.color.copy(P.color).multiplyScalar(P.intensity), P.castShadow) {
          const W = P.shadow, X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, i.directionalShadow[f] = X, i.directionalShadowMap[f] = H, i.directionalShadowMatrix[f] = P.shadow.matrix, x++;
        }
        i.directional[f] = U, f++;
      } else if (P.isSpotLight) {
        const U = e.get(P);
        U.position.setFromMatrixPosition(P.matrixWorld), U.color.copy(L).multiplyScalar(I), U.distance = N, U.coneCos = Math.cos(P.angle), U.penumbraCos = Math.cos(P.angle * (1 - P.penumbra)), U.decay = P.decay, i.spot[v] = U;
        const W = P.shadow;
        if (P.map && (i.spotLightMap[b] = P.map, b++, W.updateMatrices(P), P.castShadow && M++), i.spotLightMatrix[v] = W.matrix, P.castShadow) {
          const X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, i.spotShadow[v] = X, i.spotShadowMap[v] = H, A++;
        }
        v++;
      } else if (P.isRectAreaLight) {
        const U = e.get(P);
        U.color.copy(L).multiplyScalar(I), U.halfWidth.set(P.width * 0.5, 0, 0), U.halfHeight.set(0, P.height * 0.5, 0), i.rectArea[g] = U, g++;
      } else if (P.isPointLight) {
        const U = e.get(P);
        if (U.color.copy(P.color).multiplyScalar(P.intensity), U.distance = P.distance, U.decay = P.decay, P.castShadow) {
          const W = P.shadow, X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, X.shadowCameraNear = W.camera.near, X.shadowCameraFar = W.camera.far, i.pointShadow[p] = X, i.pointShadowMap[p] = H, i.pointShadowMatrix[p] = P.shadow.matrix, y++;
        }
        i.point[p] = U, p++;
      } else if (P.isHemisphereLight) {
        const U = e.get(P);
        U.skyColor.copy(P.color).multiplyScalar(I), U.groundColor.copy(P.groundColor).multiplyScalar(I), i.hemi[m] = U, m++;
      }
    }
    g > 0 && (s.has("OES_texture_float_linear") === !0 ? (i.rectAreaLTC1 = bt.LTC_FLOAT_1, i.rectAreaLTC2 = bt.LTC_FLOAT_2) : (i.rectAreaLTC1 = bt.LTC_HALF_1, i.rectAreaLTC2 = bt.LTC_HALF_2)), i.ambient[0] = h, i.ambient[1] = u, i.ambient[2] = d;
    const C = i.hash;
    (C.directionalLength !== f || C.pointLength !== p || C.spotLength !== v || C.rectAreaLength !== g || C.hemiLength !== m || C.numDirectionalShadows !== x || C.numPointShadows !== y || C.numSpotShadows !== A || C.numSpotMaps !== b || C.numLightProbes !== w) && (i.directional.length = f, i.spot.length = v, i.rectArea.length = g, i.point.length = p, i.hemi.length = m, i.directionalShadow.length = x, i.directionalShadowMap.length = x, i.pointShadow.length = y, i.pointShadowMap.length = y, i.spotShadow.length = A, i.spotShadowMap.length = A, i.directionalShadowMatrix.length = x, i.pointShadowMatrix.length = y, i.spotLightMatrix.length = A + b - M, i.spotLightMap.length = b, i.numSpotLightShadowsWithMaps = M, i.numLightProbes = w, C.directionalLength = f, C.pointLength = p, C.spotLength = v, C.rectAreaLength = g, C.hemiLength = m, C.numDirectionalShadows = x, C.numPointShadows = y, C.numSpotShadows = A, C.numSpotMaps = b, C.numLightProbes = w, i.version = $W++);
  }
  function l(c, h) {
    let u = 0, d = 0, f = 0, p = 0, v = 0;
    const g = h.matrixWorldInverse;
    for (let m = 0, x = c.length; m < x; m++) {
      const y = c[m];
      if (y.isDirectionalLight) {
        const A = i.directional[u];
        A.direction.setFromMatrixPosition(y.matrixWorld), n.setFromMatrixPosition(y.target.matrixWorld), A.direction.sub(n), A.direction.transformDirection(g), u++;
      } else if (y.isSpotLight) {
        const A = i.spot[f];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), A.direction.setFromMatrixPosition(y.matrixWorld), n.setFromMatrixPosition(y.target.matrixWorld), A.direction.sub(n), A.direction.transformDirection(g), f++;
      } else if (y.isRectAreaLight) {
        const A = i.rectArea[p];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), a.identity(), r.copy(y.matrixWorld), r.premultiply(g), a.extractRotation(r), A.halfWidth.set(y.width * 0.5, 0, 0), A.halfHeight.set(0, y.height * 0.5, 0), A.halfWidth.applyMatrix4(a), A.halfHeight.applyMatrix4(a), p++;
      } else if (y.isPointLight) {
        const A = i.point[d];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), d++;
      } else if (y.isHemisphereLight) {
        const A = i.hemi[v];
        A.direction.setFromMatrixPosition(y.matrixWorld), A.direction.transformDirection(g), v++;
      }
    }
  }
  return {
    setup: o,
    setupView: l,
    state: i
  };
}
function RE(s) {
  const e = new tX(s), t = [], i = [];
  function n(h) {
    c.camera = h, t.length = 0, i.length = 0;
  }
  function r(h) {
    t.push(h);
  }
  function a(h) {
    i.push(h);
  }
  function o() {
    e.setup(t);
  }
  function l(h) {
    e.setupView(t, h);
  }
  const c = {
    lightsArray: t,
    shadowsArray: i,
    camera: null,
    lights: e,
    transmissionRenderTarget: {}
  };
  return {
    init: n,
    state: c,
    setupLights: o,
    setupLightsView: l,
    pushLight: r,
    pushShadow: a
  };
}
function iX(s) {
  let e = /* @__PURE__ */ new WeakMap();
  function t(n, r = 0) {
    const a = e.get(n);
    let o;
    return a === void 0 ? (o = new RE(s), e.set(n, [o])) : r >= a.length ? (o = new RE(s), a.push(o)) : o = a[r], o;
  }
  function i() {
    e = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: t,
    dispose: i
  };
}
let w4 = class extends Cn {
  constructor(e) {
    super(), this.isMeshDepthMaterial = !0, this.type = "MeshDepthMaterial", this.depthPacking = _L, this.map = null, this.alphaMap = null, this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.wireframe = !1, this.wireframeLinewidth = 1, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.depthPacking = e.depthPacking, this.map = e.map, this.alphaMap = e.alphaMap, this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this;
  }
}, GM = class extends Cn {
  constructor(e) {
    super(), this.isMeshDistanceMaterial = !0, this.type = "MeshDistanceMaterial", this.map = null, this.alphaMap = null, this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.map = e.map, this.alphaMap = e.alphaMap, this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this;
  }
};
const nX = `void main() {
	gl_Position = vec4( position, 1.0 );
}`, sX = `uniform sampler2D shadow_pass;
uniform vec2 resolution;
uniform float radius;
#include <packing>
void main() {
	const float samples = float( VSM_SAMPLES );
	float mean = 0.0;
	float squared_mean = 0.0;
	float uvStride = samples <= 1.0 ? 0.0 : 2.0 / ( samples - 1.0 );
	float uvStart = samples <= 1.0 ? 0.0 : - 1.0;
	for ( float i = 0.0; i < samples; i ++ ) {
		float uvOffset = uvStart + i * uvStride;
		#ifdef HORIZONTAL_PASS
			vec2 distribution = unpackRGBATo2Half( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( uvOffset, 0.0 ) * radius ) / resolution ) );
			mean += distribution.x;
			squared_mean += distribution.y * distribution.y + distribution.x * distribution.x;
		#else
			float depth = unpackRGBAToDepth( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( 0.0, uvOffset ) * radius ) / resolution ) );
			mean += depth;
			squared_mean += depth * depth;
		#endif
	}
	mean = mean / samples;
	squared_mean = squared_mean / samples;
	float std_dev = sqrt( squared_mean - mean * mean );
	gl_FragColor = pack2HalfToRGBA( vec2( mean, std_dev ) );
}`;
function rX(s, e, t) {
  let i = new th();
  const n = new Se(), r = new Se(), a = new Yt(), o = new w4({ depthPacking: LM }), l = new GM(), c = {}, h = t.maxTextureSize, u = { [sa]: Ps, [Ps]: sa, [ui]: ui }, d = new Jt({
    defines: {
      VSM_SAMPLES: 8
    },
    uniforms: {
      shadow_pass: { value: null },
      resolution: { value: new Se() },
      radius: { value: 4 }
    },
    vertexShader: nX,
    fragmentShader: sX
  }), f = d.clone();
  f.defines.HORIZONTAL_PASS = 1;
  const p = new Pt();
  p.setAttribute(
    "position",
    new Ht(
      new Float32Array([-1, -1, 0.5, 3, -1, 0.5, -1, 3, 0.5]),
      3
    )
  );
  const v = new Re(p, d), g = this;
  this.enabled = !1, this.autoUpdate = !0, this.needsUpdate = !1, this.type = pM;
  let m = this.type;
  this.render = function(M, w, C) {
    if (g.enabled === !1 || g.autoUpdate === !1 && g.needsUpdate === !1 || M.length === 0)
      return;
    const T = s.getRenderTarget(), E = s.getActiveCubeFace(), P = s.getActiveMipmapLevel(), L = s.state;
    L.setBlending(vn), L.buffers.color.setClear(1, 1, 1, 1), L.buffers.depth.setTest(!0), L.setScissorTest(!1);
    const I = m !== Do && this.type === Do, N = m === Do && this.type !== Do;
    for (let H = 0, U = M.length; H < U; H++) {
      const W = M[H], X = W.shadow;
      if (X === void 0) {
        console.warn("THREE.WebGLShadowMap:", W, "has no shadow.");
        continue;
      }
      if (X.autoUpdate === !1 && X.needsUpdate === !1)
        continue;
      n.copy(X.mapSize);
      const K = X.getFrameExtents();
      if (n.multiply(K), r.copy(X.mapSize), (n.x > h || n.y > h) && (n.x > h && (r.x = Math.floor(h / K.x), n.x = r.x * K.x, X.mapSize.x = r.x), n.y > h && (r.y = Math.floor(h / K.y), n.y = r.y * K.y, X.mapSize.y = r.y)), X.map === null || I === !0 || N === !0) {
        const re = this.type !== Do ? { minFilter: Ni, magFilter: Ni } : {};
        X.map !== null && X.map.dispose(), X.map = new Xi(n.x, n.y, re), X.map.texture.name = W.name + ".shadowMap", X.camera.updateProjectionMatrix();
      }
      s.setRenderTarget(X.map), s.clear();
      const te = X.getViewportCount();
      for (let re = 0; re < te; re++) {
        const be = X.getViewport(re);
        a.set(
          r.x * be.x,
          r.y * be.y,
          r.x * be.z,
          r.y * be.w
        ), L.viewport(a), X.updateMatrices(W, re), i = X.getFrustum(), A(w, C, X.camera, W, this.type);
      }
      X.isPointLightShadow !== !0 && this.type === Do && x(X, C), X.needsUpdate = !1;
    }
    m = this.type, g.needsUpdate = !1, s.setRenderTarget(T, E, P);
  };
  function x(M, w) {
    const C = e.update(v);
    d.defines.VSM_SAMPLES !== M.blurSamples && (d.defines.VSM_SAMPLES = M.blurSamples, f.defines.VSM_SAMPLES = M.blurSamples, d.needsUpdate = !0, f.needsUpdate = !0), M.mapPass === null && (M.mapPass = new Xi(n.x, n.y)), d.uniforms.shadow_pass.value = M.map.texture, d.uniforms.resolution.value = M.mapSize, d.uniforms.radius.value = M.radius, s.setRenderTarget(M.mapPass), s.clear(), s.renderBufferDirect(w, null, C, d, v, null), f.uniforms.shadow_pass.value = M.mapPass.texture, f.uniforms.resolution.value = M.mapSize, f.uniforms.radius.value = M.radius, s.setRenderTarget(M.map), s.clear(), s.renderBufferDirect(w, null, C, f, v, null);
  }
  function y(M, w, C, T) {
    let E = null;
    const P = C.isPointLight === !0 ? M.customDistanceMaterial : M.customDepthMaterial;
    if (P !== void 0)
      E = P;
    else if (E = C.isPointLight === !0 ? l : o, s.localClippingEnabled && w.clipShadows === !0 && Array.isArray(w.clippingPlanes) && w.clippingPlanes.length !== 0 || w.displacementMap && w.displacementScale !== 0 || w.alphaMap && w.alphaTest > 0 || w.map && w.alphaTest > 0) {
      const L = E.uuid, I = w.uuid;
      let N = c[L];
      N === void 0 && (N = {}, c[L] = N);
      let H = N[I];
      H === void 0 && (H = E.clone(), N[I] = H, w.addEventListener("dispose", b)), E = H;
    }
    if (E.visible = w.visible, E.wireframe = w.wireframe, T === Do ? E.side = w.shadowSide !== null ? w.shadowSide : w.side : E.side = w.shadowSide !== null ? w.shadowSide : u[w.side], E.alphaMap = w.alphaMap, E.alphaTest = w.alphaTest, E.map = w.map, E.clipShadows = w.clipShadows, E.clippingPlanes = w.clippingPlanes, E.clipIntersection = w.clipIntersection, E.displacementMap = w.displacementMap, E.displacementScale = w.displacementScale, E.displacementBias = w.displacementBias, E.wireframeLinewidth = w.wireframeLinewidth, E.linewidth = w.linewidth, C.isPointLight === !0 && E.isMeshDistanceMaterial === !0) {
      const L = s.properties.get(E);
      L.light = C;
    }
    return E;
  }
  function A(M, w, C, T, E) {
    if (M.visible === !1)
      return;
    if (M.layers.test(w.layers) && (M.isMesh || M.isLine || M.isPoints) && (M.castShadow || M.receiveShadow && E === Do) && (!M.frustumCulled || i.intersectsObject(M))) {
      M.modelViewMatrix.multiplyMatrices(C.matrixWorldInverse, M.matrixWorld);
      const I = e.update(M), N = M.material;
      if (Array.isArray(N)) {
        const H = I.groups;
        for (let U = 0, W = H.length; U < W; U++) {
          const X = H[U], K = N[X.materialIndex];
          if (K && K.visible) {
            const te = y(M, K, T, E);
            M.onBeforeShadow(s, M, w, C, I, te, X), s.renderBufferDirect(C, null, I, te, M, X), M.onAfterShadow(s, M, w, C, I, te, X);
          }
        }
      } else if (N.visible) {
        const H = y(M, N, T, E);
        M.onBeforeShadow(s, M, w, C, I, H, null), s.renderBufferDirect(C, null, I, H, M, null), M.onAfterShadow(s, M, w, C, I, H, null);
      }
    }
    const L = M.children;
    for (let I = 0, N = L.length; I < N; I++)
      A(L[I], w, C, T, E);
  }
  function b(M) {
    M.target.removeEventListener("dispose", b);
    for (const C in c) {
      const T = c[C], E = M.target.uuid;
      E in T && (T[E].dispose(), delete T[E]);
    }
  }
}
function aX(s) {
  function e() {
    let F = !1;
    const ue = new Yt();
    let Q = null;
    const he = new Yt(0, 0, 0, 0);
    return {
      setMask: function(Be) {
        Q !== Be && !F && (s.colorMask(Be, Be, Be, Be), Q = Be);
      },
      setLocked: function(Be) {
        F = Be;
      },
      setClear: function(Be, ft, Et, ei, di) {
        di === !0 && (Be *= ei, ft *= ei, Et *= ei), ue.set(Be, ft, Et, ei), he.equals(ue) === !1 && (s.clearColor(Be, ft, Et, ei), he.copy(ue));
      },
      reset: function() {
        F = !1, Q = null, he.set(-1, 0, 0, 0);
      }
    };
  }
  function t() {
    let F = !1, ue = null, Q = null, he = null;
    return {
      setTest: function(Be) {
        Be ? Me(s.DEPTH_TEST) : ye(s.DEPTH_TEST);
      },
      setMask: function(Be) {
        ue !== Be && !F && (s.depthMask(Be), ue = Be);
      },
      setFunc: function(Be) {
        if (Q !== Be) {
          switch (Be) {
            case mL:
              s.depthFunc(s.NEVER);
              break;
            case gL:
              s.depthFunc(s.ALWAYS);
              break;
            case vL:
              s.depthFunc(s.LESS);
              break;
            case j1:
              s.depthFunc(s.LEQUAL);
              break;
            case yL:
              s.depthFunc(s.EQUAL);
              break;
            case xL:
              s.depthFunc(s.GEQUAL);
              break;
            case AL:
              s.depthFunc(s.GREATER);
              break;
            case bL:
              s.depthFunc(s.NOTEQUAL);
              break;
            default:
              s.depthFunc(s.LEQUAL);
          }
          Q = Be;
        }
      },
      setLocked: function(Be) {
        F = Be;
      },
      setClear: function(Be) {
        he !== Be && (s.clearDepth(Be), he = Be);
      },
      reset: function() {
        F = !1, ue = null, Q = null, he = null;
      }
    };
  }
  function i() {
    let F = !1, ue = null, Q = null, he = null, Be = null, ft = null, Et = null, ei = null, di = null;
    return {
      setTest: function(Ot) {
        F || (Ot ? Me(s.STENCIL_TEST) : ye(s.STENCIL_TEST));
      },
      setMask: function(Ot) {
        ue !== Ot && !F && (s.stencilMask(Ot), ue = Ot);
      },
      setFunc: function(Ot, Wi, Li) {
        (Q !== Ot || he !== Wi || Be !== Li) && (s.stencilFunc(Ot, Wi, Li), Q = Ot, he = Wi, Be = Li);
      },
      setOp: function(Ot, Wi, Li) {
        (ft !== Ot || Et !== Wi || ei !== Li) && (s.stencilOp(Ot, Wi, Li), ft = Ot, Et = Wi, ei = Li);
      },
      setLocked: function(Ot) {
        F = Ot;
      },
      setClear: function(Ot) {
        di !== Ot && (s.clearStencil(Ot), di = Ot);
      },
      reset: function() {
        F = !1, ue = null, Q = null, he = null, Be = null, ft = null, Et = null, ei = null, di = null;
      }
    };
  }
  const n = new e(), r = new t(), a = new i(), o = /* @__PURE__ */ new WeakMap(), l = /* @__PURE__ */ new WeakMap();
  let c = {}, h = {}, u = /* @__PURE__ */ new WeakMap(), d = [], f = null, p = !1, v = null, g = null, m = null, x = null, y = null, A = null, b = null, M = new _e(0, 0, 0), w = 0, C = !1, T = null, E = null, P = null, L = null, I = null;
  const N = s.getParameter(s.MAX_COMBINED_TEXTURE_IMAGE_UNITS);
  let H = !1, U = 0;
  const W = s.getParameter(s.VERSION);
  W.indexOf("WebGL") !== -1 ? (U = parseFloat(/^WebGL (\d)/.exec(W)[1]), H = U >= 1) : W.indexOf("OpenGL ES") !== -1 && (U = parseFloat(/^OpenGL ES (\d)/.exec(W)[1]), H = U >= 2);
  let X = null, K = {};
  const te = s.getParameter(s.SCISSOR_BOX), re = s.getParameter(s.VIEWPORT), be = new Yt().fromArray(te), Pe = new Yt().fromArray(re);
  function J(F, ue, Q, he) {
    const Be = new Uint8Array(4), ft = s.createTexture();
    s.bindTexture(F, ft), s.texParameteri(F, s.TEXTURE_MIN_FILTER, s.NEAREST), s.texParameteri(F, s.TEXTURE_MAG_FILTER, s.NEAREST);
    for (let Et = 0; Et < Q; Et++)
      F === s.TEXTURE_3D || F === s.TEXTURE_2D_ARRAY ? s.texImage3D(ue, 0, s.RGBA, 1, 1, he, 0, s.RGBA, s.UNSIGNED_BYTE, Be) : s.texImage2D(ue + Et, 0, s.RGBA, 1, 1, 0, s.RGBA, s.UNSIGNED_BYTE, Be);
    return ft;
  }
  const fe = {};
  fe[s.TEXTURE_2D] = J(s.TEXTURE_2D, s.TEXTURE_2D, 1), fe[s.TEXTURE_CUBE_MAP] = J(s.TEXTURE_CUBE_MAP, s.TEXTURE_CUBE_MAP_POSITIVE_X, 6), fe[s.TEXTURE_2D_ARRAY] = J(s.TEXTURE_2D_ARRAY, s.TEXTURE_2D_ARRAY, 1, 1), fe[s.TEXTURE_3D] = J(s.TEXTURE_3D, s.TEXTURE_3D, 1, 1), n.setClear(0, 0, 0, 1), r.setClear(1), a.setClear(0), Me(s.DEPTH_TEST), r.setFunc(j1), qe(!1), Ie(G6), Me(s.CULL_FACE), Fe(vn);
  function Me(F) {
    c[F] !== !0 && (s.enable(F), c[F] = !0);
  }
  function ye(F) {
    c[F] !== !1 && (s.disable(F), c[F] = !1);
  }
  function Ye(F, ue) {
    return h[F] !== ue ? (s.bindFramebuffer(F, ue), h[F] = ue, F === s.DRAW_FRAMEBUFFER && (h[s.FRAMEBUFFER] = ue), F === s.FRAMEBUFFER && (h[s.DRAW_FRAMEBUFFER] = ue), !0) : !1;
  }
  function Je(F, ue) {
    let Q = d, he = !1;
    if (F) {
      Q = u.get(ue), Q === void 0 && (Q = [], u.set(ue, Q));
      const Be = F.textures;
      if (Q.length !== Be.length || Q[0] !== s.COLOR_ATTACHMENT0) {
        for (let ft = 0, Et = Be.length; ft < Et; ft++)
          Q[ft] = s.COLOR_ATTACHMENT0 + ft;
        Q.length = Be.length, he = !0;
      }
    } else
      Q[0] !== s.BACK && (Q[0] = s.BACK, he = !0);
    he && s.drawBuffers(Q);
  }
  function rt(F) {
    return f !== F ? (s.useProgram(F), f = F, !0) : !1;
  }
  const wt = {
    [Ma]: s.FUNC_ADD,
    [tL]: s.FUNC_SUBTRACT,
    [iL]: s.FUNC_REVERSE_SUBTRACT
  };
  wt[nL] = s.MIN, wt[sL] = s.MAX;
  const k = {
    [ep]: s.ZERO,
    [rL]: s.ONE,
    [aL]: s.SRC_COLOR,
    [v9]: s.SRC_ALPHA,
    [hL]: s.SRC_ALPHA_SATURATE,
    [f4]: s.DST_COLOR,
    [d4]: s.DST_ALPHA,
    [oL]: s.ONE_MINUS_SRC_COLOR,
    [y9]: s.ONE_MINUS_SRC_ALPHA,
    [cL]: s.ONE_MINUS_DST_COLOR,
    [lL]: s.ONE_MINUS_DST_ALPHA,
    [uL]: s.CONSTANT_COLOR,
    [dL]: s.ONE_MINUS_CONSTANT_COLOR,
    [fL]: s.CONSTANT_ALPHA,
    [pL]: s.ONE_MINUS_CONSTANT_ALPHA
  };
  function Fe(F, ue, Q, he, Be, ft, Et, ei, di, Ot) {
    if (F === vn) {
      p === !0 && (ye(s.BLEND), p = !1);
      return;
    }
    if (p === !1 && (Me(s.BLEND), p = !0), F !== u4) {
      if (F !== v || Ot !== C) {
        if ((g !== Ma || y !== Ma) && (s.blendEquation(s.FUNC_ADD), g = Ma, y = Ma), Ot)
          switch (F) {
            case so:
              s.blendFuncSeparate(s.ONE, s.ONE_MINUS_SRC_ALPHA, s.ONE, s.ONE_MINUS_SRC_ALPHA);
              break;
            case oo:
              s.blendFunc(s.ONE, s.ONE);
              break;
            case m9:
              s.blendFuncSeparate(s.ZERO, s.ONE_MINUS_SRC_COLOR, s.ZERO, s.ONE);
              break;
            case g9:
              s.blendFuncSeparate(s.ZERO, s.SRC_COLOR, s.ZERO, s.SRC_ALPHA);
              break;
            default:
              console.error("THREE.WebGLState: Invalid blending: ", F);
              break;
          }
        else
          switch (F) {
            case so:
              s.blendFuncSeparate(s.SRC_ALPHA, s.ONE_MINUS_SRC_ALPHA, s.ONE, s.ONE_MINUS_SRC_ALPHA);
              break;
            case oo:
              s.blendFunc(s.SRC_ALPHA, s.ONE);
              break;
            case m9:
              s.blendFuncSeparate(s.ZERO, s.ONE_MINUS_SRC_COLOR, s.ZERO, s.ONE);
              break;
            case g9:
              s.blendFunc(s.ZERO, s.SRC_COLOR);
              break;
            default:
              console.error("THREE.WebGLState: Invalid blending: ", F);
              break;
          }
        m = null, x = null, A = null, b = null, M.set(0, 0, 0), w = 0, v = F, C = Ot;
      }
      return;
    }
    Be = Be || ue, ft = ft || Q, Et = Et || he, (ue !== g || Be !== y) && (s.blendEquationSeparate(wt[ue], wt[Be]), g = ue, y = Be), (Q !== m || he !== x || ft !== A || Et !== b) && (s.blendFuncSeparate(k[Q], k[he], k[ft], k[Et]), m = Q, x = he, A = ft, b = Et), (ei.equals(M) === !1 || di !== w) && (s.blendColor(ei.r, ei.g, ei.b, di), M.copy(ei), w = di), v = F, C = !1;
  }
  function Ge(F, ue) {
    F.side === ui ? ye(s.CULL_FACE) : Me(s.CULL_FACE);
    let Q = F.side === Ps;
    ue && (Q = !Q), qe(Q), F.blending === so && F.transparent === !1 ? Fe(vn) : Fe(F.blending, F.blendEquation, F.blendSrc, F.blendDst, F.blendEquationAlpha, F.blendSrcAlpha, F.blendDstAlpha, F.blendColor, F.blendAlpha, F.premultipliedAlpha), r.setFunc(F.depthFunc), r.setTest(F.depthTest), r.setMask(F.depthWrite), n.setMask(F.colorWrite);
    const he = F.stencilWrite;
    a.setTest(he), he && (a.setMask(F.stencilWriteMask), a.setFunc(F.stencilFunc, F.stencilRef, F.stencilFuncMask), a.setOp(F.stencilFail, F.stencilZFail, F.stencilZPass)), Ve(F.polygonOffset, F.polygonOffsetFactor, F.polygonOffsetUnits), F.alphaToCoverage === !0 ? Me(s.SAMPLE_ALPHA_TO_COVERAGE) : ye(s.SAMPLE_ALPHA_TO_COVERAGE);
  }
  function qe(F) {
    T !== F && (F ? s.frontFace(s.CW) : s.frontFace(s.CCW), T = F);
  }
  function Ie(F) {
    F !== $D ? (Me(s.CULL_FACE), F !== E && (F === G6 ? s.cullFace(s.BACK) : F === eL ? s.cullFace(s.FRONT) : s.cullFace(s.FRONT_AND_BACK))) : ye(s.CULL_FACE), E = F;
  }
  function vt(F) {
    F !== P && (H && s.lineWidth(F), P = F);
  }
  function Ve(F, ue, Q) {
    F ? (Me(s.POLYGON_OFFSET_FILL), (L !== ue || I !== Q) && (s.polygonOffset(ue, Q), L = ue, I = Q)) : ye(s.POLYGON_OFFSET_FILL);
  }
  function Ze(F) {
    F ? Me(s.SCISSOR_TEST) : ye(s.SCISSOR_TEST);
  }
  function G(F) {
    F === void 0 && (F = s.TEXTURE0 + N - 1), X !== F && (s.activeTexture(F), X = F);
  }
  function D(F, ue, Q) {
    Q === void 0 && (X === null ? Q = s.TEXTURE0 + N - 1 : Q = X);
    let he = K[Q];
    he === void 0 && (he = { type: void 0, texture: void 0 }, K[Q] = he), (he.type !== F || he.texture !== ue) && (X !== Q && (s.activeTexture(Q), X = Q), s.bindTexture(F, ue || fe[F]), he.type = F, he.texture = ue);
  }
  function ne() {
    const F = K[X];
    F !== void 0 && F.type !== void 0 && (s.bindTexture(F.type, null), F.type = void 0, F.texture = void 0);
  }
  function ve() {
    try {
      s.compressedTexImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Y() {
    try {
      s.compressedTexImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function ee() {
    try {
      s.texSubImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function we() {
    try {
      s.texSubImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function de() {
    try {
      s.compressedTexSubImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function xe() {
    try {
      s.compressedTexSubImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function it() {
    try {
      s.texStorage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function q() {
    try {
      s.texStorage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function ae() {
    try {
      s.texImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Ee() {
    try {
      s.texImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Le(F) {
    be.equals(F) === !1 && (s.scissor(F.x, F.y, F.z, F.w), be.copy(F));
  }
  function Ae(F) {
    Pe.equals(F) === !1 && (s.viewport(F.x, F.y, F.z, F.w), Pe.copy(F));
  }
  function We(F, ue) {
    let Q = l.get(ue);
    Q === void 0 && (Q = /* @__PURE__ */ new WeakMap(), l.set(ue, Q));
    let he = Q.get(F);
    he === void 0 && (he = s.getUniformBlockIndex(ue, F.name), Q.set(F, he));
  }
  function ot(F, ue) {
    const he = l.get(ue).get(F);
    o.get(ue) !== he && (s.uniformBlockBinding(ue, he, F.__bindingPointIndex), o.set(ue, he));
  }
  function Ct() {
    s.disable(s.BLEND), s.disable(s.CULL_FACE), s.disable(s.DEPTH_TEST), s.disable(s.POLYGON_OFFSET_FILL), s.disable(s.SCISSOR_TEST), s.disable(s.STENCIL_TEST), s.disable(s.SAMPLE_ALPHA_TO_COVERAGE), s.blendEquation(s.FUNC_ADD), s.blendFunc(s.ONE, s.ZERO), s.blendFuncSeparate(s.ONE, s.ZERO, s.ONE, s.ZERO), s.blendColor(0, 0, 0, 0), s.colorMask(!0, !0, !0, !0), s.clearColor(0, 0, 0, 0), s.depthMask(!0), s.depthFunc(s.LESS), s.clearDepth(1), s.stencilMask(4294967295), s.stencilFunc(s.ALWAYS, 0, 4294967295), s.stencilOp(s.KEEP, s.KEEP, s.KEEP), s.clearStencil(0), s.cullFace(s.BACK), s.frontFace(s.CCW), s.polygonOffset(0, 0), s.activeTexture(s.TEXTURE0), s.bindFramebuffer(s.FRAMEBUFFER, null), s.bindFramebuffer(s.DRAW_FRAMEBUFFER, null), s.bindFramebuffer(s.READ_FRAMEBUFFER, null), s.useProgram(null), s.lineWidth(1), s.scissor(0, 0, s.canvas.width, s.canvas.height), s.viewport(0, 0, s.canvas.width, s.canvas.height), c = {}, X = null, K = {}, h = {}, u = /* @__PURE__ */ new WeakMap(), d = [], f = null, p = !1, v = null, g = null, m = null, x = null, y = null, A = null, b = null, M = new _e(0, 0, 0), w = 0, C = !1, T = null, E = null, P = null, L = null, I = null, be.set(0, 0, s.canvas.width, s.canvas.height), Pe.set(0, 0, s.canvas.width, s.canvas.height), n.reset(), r.reset(), a.reset();
  }
  return {
    buffers: {
      color: n,
      depth: r,
      stencil: a
    },
    enable: Me,
    disable: ye,
    bindFramebuffer: Ye,
    drawBuffers: Je,
    useProgram: rt,
    setBlending: Fe,
    setMaterial: Ge,
    setFlipSided: qe,
    setCullFace: Ie,
    setLineWidth: vt,
    setPolygonOffset: Ve,
    setScissorTest: Ze,
    activeTexture: G,
    bindTexture: D,
    unbindTexture: ne,
    compressedTexImage2D: ve,
    compressedTexImage3D: Y,
    texImage2D: ae,
    texImage3D: Ee,
    updateUBOMapping: We,
    uniformBlockBinding: ot,
    texStorage2D: it,
    texStorage3D: q,
    texSubImage2D: ee,
    texSubImage3D: we,
    compressedTexSubImage2D: de,
    compressedTexSubImage3D: xe,
    scissor: Le,
    viewport: Ae,
    reset: Ct
  };
}
function oX(s, e) {
  const t = s.image && s.image.width ? s.image.width / s.image.height : 1;
  return t > e ? (s.repeat.x = 1, s.repeat.y = t / e, s.offset.x = 0, s.offset.y = (1 - s.repeat.y) / 2) : (s.repeat.x = e / t, s.repeat.y = 1, s.offset.x = (1 - s.repeat.x) / 2, s.offset.y = 0), s;
}
function lX(s, e) {
  const t = s.image && s.image.width ? s.image.width / s.image.height : 1;
  return t > e ? (s.repeat.x = e / t, s.repeat.y = 1, s.offset.x = (1 - s.repeat.x) / 2, s.offset.y = 0) : (s.repeat.x = 1, s.repeat.y = t / e, s.offset.x = 0, s.offset.y = (1 - s.repeat.y) / 2), s;
}
function cX(s) {
  return s.repeat.x = 1, s.repeat.y = 1, s.offset.x = 0, s.offset.y = 0, s;
}
function q6(s, e, t, i) {
  const n = hX(i);
  switch (t) {
    case SM:
      return s * e;
    case EM:
      return s * e;
    case TM:
      return s * e * 2;
    case Vu:
      return s * e / n.components * n.byteLength;
    case t2:
      return s * e / n.components * n.byteLength;
    case CM:
      return s * e * 2 / n.components * n.byteLength;
    case v4:
      return s * e * 2 / n.components * n.byteLength;
    case wM:
      return s * e * 3 / n.components * n.byteLength;
    case dn:
      return s * e * 4 / n.components * n.byteLength;
    case y4:
      return s * e * 4 / n.components * n.byteLength;
    case x1:
    case A1:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 8;
    case b1:
    case M1:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case A9:
    case M9:
      return Math.max(s, 16) * Math.max(e, 8) / 4;
    case x9:
    case b9:
      return Math.max(s, 8) * Math.max(e, 8) / 2;
    case S9:
    case w9:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 8;
    case E9:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case T9:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case C9:
      return Math.floor((s + 4) / 5) * Math.floor((e + 3) / 4) * 16;
    case _9:
      return Math.floor((s + 4) / 5) * Math.floor((e + 4) / 5) * 16;
    case P9:
      return Math.floor((s + 5) / 6) * Math.floor((e + 4) / 5) * 16;
    case R9:
      return Math.floor((s + 5) / 6) * Math.floor((e + 5) / 6) * 16;
    case D9:
      return Math.floor((s + 7) / 8) * Math.floor((e + 4) / 5) * 16;
    case L9:
      return Math.floor((s + 7) / 8) * Math.floor((e + 5) / 6) * 16;
    case I9:
      return Math.floor((s + 7) / 8) * Math.floor((e + 7) / 8) * 16;
    case O9:
      return Math.floor((s + 9) / 10) * Math.floor((e + 4) / 5) * 16;
    case N9:
      return Math.floor((s + 9) / 10) * Math.floor((e + 5) / 6) * 16;
    case B9:
      return Math.floor((s + 9) / 10) * Math.floor((e + 7) / 8) * 16;
    case F9:
      return Math.floor((s + 9) / 10) * Math.floor((e + 9) / 10) * 16;
    case U9:
      return Math.floor((s + 11) / 12) * Math.floor((e + 9) / 10) * 16;
    case z9:
      return Math.floor((s + 11) / 12) * Math.floor((e + 11) / 12) * 16;
    case S1:
    case k9:
    case G9:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 16;
    case _M:
    case H9:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 8;
    case V9:
    case W9:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 16;
  }
  throw new Error(
    `Unable to determine texture byte length for ${t} format.`
  );
}
function hX(s) {
  switch (s) {
    case o0:
    case AM:
      return { byteLength: 1, components: 1 };
    case tp:
    case bM:
    case on:
      return { byteLength: 2, components: 1 };
    case m4:
    case g4:
      return { byteLength: 2, components: 4 };
    case gl:
    case p4:
    case xn:
      return { byteLength: 4, components: 1 };
    case MM:
      return { byteLength: 4, components: 3 };
  }
  throw new Error(`Unknown texture type ${s}.`);
}
const uX = {
  contain: oX,
  cover: lX,
  fill: cX,
  getByteLength: q6
};
function dX(s, e, t, i, n, r, a) {
  const o = e.has("WEBGL_multisampled_render_to_texture") ? e.get("WEBGL_multisampled_render_to_texture") : null, l = typeof navigator > "u" ? !1 : /OculusBrowser/g.test(navigator.userAgent), c = new Se(), h = /* @__PURE__ */ new WeakMap();
  let u;
  const d = /* @__PURE__ */ new WeakMap();
  let f = !1;
  try {
    f = typeof OffscreenCanvas < "u" && new OffscreenCanvas(1, 1).getContext("2d") !== null;
  } catch {
  }
  function p(G, D) {
    return f ? (
      // eslint-disable-next-line compat/compat
      new OffscreenCanvas(G, D)
    ) : $1("canvas");
  }
  function v(G, D, ne) {
    let ve = 1;
    const Y = Ze(G);
    if ((Y.width > ne || Y.height > ne) && (ve = ne / Math.max(Y.width, Y.height)), ve < 1)
      if (typeof HTMLImageElement < "u" && G instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && G instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && G instanceof ImageBitmap || typeof VideoFrame < "u" && G instanceof VideoFrame) {
        const ee = Math.floor(ve * Y.width), we = Math.floor(ve * Y.height);
        u === void 0 && (u = p(ee, we));
        const de = D ? p(ee, we) : u;
        return de.width = ee, de.height = we, de.getContext("2d").drawImage(G, 0, 0, ee, we), console.warn("THREE.WebGLRenderer: Texture has been resized from (" + Y.width + "x" + Y.height + ") to (" + ee + "x" + we + ")."), de;
      } else
        return "data" in G && console.warn("THREE.WebGLRenderer: Image in DataTexture is too big (" + Y.width + "x" + Y.height + ")."), G;
    return G;
  }
  function g(G) {
    return G.generateMipmaps && G.minFilter !== Ni && G.minFilter !== ii;
  }
  function m(G) {
    s.generateMipmap(G);
  }
  function x(G, D, ne, ve, Y = !1) {
    if (G !== null) {
      if (s[G] !== void 0)
        return s[G];
      console.warn("THREE.WebGLRenderer: Attempt to use non-existing WebGL internal format '" + G + "'");
    }
    let ee = D;
    if (D === s.RED && (ne === s.FLOAT && (ee = s.R32F), ne === s.HALF_FLOAT && (ee = s.R16F), ne === s.UNSIGNED_BYTE && (ee = s.R8)), D === s.RED_INTEGER && (ne === s.UNSIGNED_BYTE && (ee = s.R8UI), ne === s.UNSIGNED_SHORT && (ee = s.R16UI), ne === s.UNSIGNED_INT && (ee = s.R32UI), ne === s.BYTE && (ee = s.R8I), ne === s.SHORT && (ee = s.R16I), ne === s.INT && (ee = s.R32I)), D === s.RG && (ne === s.FLOAT && (ee = s.RG32F), ne === s.HALF_FLOAT && (ee = s.RG16F), ne === s.UNSIGNED_BYTE && (ee = s.RG8)), D === s.RG_INTEGER && (ne === s.UNSIGNED_BYTE && (ee = s.RG8UI), ne === s.UNSIGNED_SHORT && (ee = s.RG16UI), ne === s.UNSIGNED_INT && (ee = s.RG32UI), ne === s.BYTE && (ee = s.RG8I), ne === s.SHORT && (ee = s.RG16I), ne === s.INT && (ee = s.RG32I)), D === s.RGB && ne === s.UNSIGNED_INT_5_9_9_9_REV && (ee = s.RGB9_E5), D === s.RGBA) {
      const we = Y ? Y1 : bi.getTransfer(ve);
      ne === s.FLOAT && (ee = s.RGBA32F), ne === s.HALF_FLOAT && (ee = s.RGBA16F), ne === s.UNSIGNED_BYTE && (ee = we === pn ? s.SRGB8_ALPHA8 : s.RGBA8), ne === s.UNSIGNED_SHORT_4_4_4_4 && (ee = s.RGBA4), ne === s.UNSIGNED_SHORT_5_5_5_1 && (ee = s.RGB5_A1);
    }
    return (ee === s.R16F || ee === s.R32F || ee === s.RG16F || ee === s.RG32F || ee === s.RGBA16F || ee === s.RGBA32F) && e.get("EXT_color_buffer_float"), ee;
  }
  function y(G, D) {
    let ne;
    return G ? D === null || D === gl || D === vl ? ne = s.DEPTH24_STENCIL8 : D === xn ? ne = s.DEPTH32F_STENCIL8 : D === tp && (ne = s.DEPTH24_STENCIL8, console.warn("DepthTexture: 16 bit depth attachment is not supported with stencil. Using 24-bit attachment.")) : D === null || D === gl || D === vl ? ne = s.DEPTH_COMPONENT24 : D === xn ? ne = s.DEPTH_COMPONENT32F : D === tp && (ne = s.DEPTH_COMPONENT16), ne;
  }
  function A(G, D) {
    return g(G) === !0 || G.isFramebufferTexture && G.minFilter !== Ni && G.minFilter !== ii ? Math.log2(Math.max(D.width, D.height)) + 1 : G.mipmaps !== void 0 && G.mipmaps.length > 0 ? G.mipmaps.length : G.isCompressedTexture && Array.isArray(G.image) ? D.mipmaps.length : 1;
  }
  function b(G) {
    const D = G.target;
    D.removeEventListener("dispose", b), w(D), D.isVideoTexture && h.delete(D);
  }
  function M(G) {
    const D = G.target;
    D.removeEventListener("dispose", M), T(D);
  }
  function w(G) {
    const D = i.get(G);
    if (D.__webglInit === void 0)
      return;
    const ne = G.source, ve = d.get(ne);
    if (ve) {
      const Y = ve[D.__cacheKey];
      Y.usedTimes--, Y.usedTimes === 0 && C(G), Object.keys(ve).length === 0 && d.delete(ne);
    }
    i.remove(G);
  }
  function C(G) {
    const D = i.get(G);
    s.deleteTexture(D.__webglTexture);
    const ne = G.source, ve = d.get(ne);
    delete ve[D.__cacheKey], a.memory.textures--;
  }
  function T(G) {
    const D = i.get(G);
    if (G.depthTexture && G.depthTexture.dispose(), G.isWebGLCubeRenderTarget)
      for (let ve = 0; ve < 6; ve++) {
        if (Array.isArray(D.__webglFramebuffer[ve]))
          for (let Y = 0; Y < D.__webglFramebuffer[ve].length; Y++)
            s.deleteFramebuffer(D.__webglFramebuffer[ve][Y]);
        else
          s.deleteFramebuffer(D.__webglFramebuffer[ve]);
        D.__webglDepthbuffer && s.deleteRenderbuffer(D.__webglDepthbuffer[ve]);
      }
    else {
      if (Array.isArray(D.__webglFramebuffer))
        for (let ve = 0; ve < D.__webglFramebuffer.length; ve++)
          s.deleteFramebuffer(D.__webglFramebuffer[ve]);
      else
        s.deleteFramebuffer(D.__webglFramebuffer);
      if (D.__webglDepthbuffer && s.deleteRenderbuffer(D.__webglDepthbuffer), D.__webglMultisampledFramebuffer && s.deleteFramebuffer(D.__webglMultisampledFramebuffer), D.__webglColorRenderbuffer)
        for (let ve = 0; ve < D.__webglColorRenderbuffer.length; ve++)
          D.__webglColorRenderbuffer[ve] && s.deleteRenderbuffer(D.__webglColorRenderbuffer[ve]);
      D.__webglDepthRenderbuffer && s.deleteRenderbuffer(D.__webglDepthRenderbuffer);
    }
    const ne = G.textures;
    for (let ve = 0, Y = ne.length; ve < Y; ve++) {
      const ee = i.get(ne[ve]);
      ee.__webglTexture && (s.deleteTexture(ee.__webglTexture), a.memory.textures--), i.remove(ne[ve]);
    }
    i.remove(G);
  }
  let E = 0;
  function P() {
    E = 0;
  }
  function L() {
    const G = E;
    return G >= n.maxTextures && console.warn("THREE.WebGLTextures: Trying to use " + G + " texture units while this GPU supports only " + n.maxTextures), E += 1, G;
  }
  function I(G) {
    const D = [];
    return D.push(G.wrapS), D.push(G.wrapT), D.push(G.wrapR || 0), D.push(G.magFilter), D.push(G.minFilter), D.push(G.anisotropy), D.push(G.internalFormat), D.push(G.format), D.push(G.type), D.push(G.generateMipmaps), D.push(G.premultiplyAlpha), D.push(G.flipY), D.push(G.unpackAlignment), D.push(G.colorSpace), D.join();
  }
  function N(G, D) {
    const ne = i.get(G);
    if (G.isVideoTexture && vt(G), G.isRenderTargetTexture === !1 && G.version > 0 && ne.__version !== G.version) {
      const ve = G.image;
      if (ve === null)
        console.warn("THREE.WebGLRenderer: Texture marked for update but no image data found.");
      else if (ve.complete === !1)
        console.warn("THREE.WebGLRenderer: Texture marked for update but image is incomplete");
      else {
        Pe(ne, G, D);
        return;
      }
    }
    t.bindTexture(s.TEXTURE_2D, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function H(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      Pe(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_2D_ARRAY, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function U(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      Pe(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_3D, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function W(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      J(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_CUBE_MAP, ne.__webglTexture, s.TEXTURE0 + D);
  }
  const X = {
    [un]: s.REPEAT,
    [yn]: s.CLAMP_TO_EDGE,
    [co]: s.MIRRORED_REPEAT
  }, K = {
    [Ni]: s.NEAREST,
    [$c]: s.NEAREST_MIPMAP_NEAREST,
    [$a]: s.NEAREST_MIPMAP_LINEAR,
    [ii]: s.LINEAR,
    [$o]: s.LINEAR_MIPMAP_NEAREST,
    [ds]: s.LINEAR_MIPMAP_LINEAR
  }, te = {
    [RL]: s.NEVER,
    [BL]: s.ALWAYS,
    [DL]: s.LESS,
    [IM]: s.LEQUAL,
    [LL]: s.EQUAL,
    [NL]: s.GEQUAL,
    [IL]: s.GREATER,
    [OL]: s.NOTEQUAL
  };
  function re(G, D) {
    if (D.type === xn && e.has("OES_texture_float_linear") === !1 && (D.magFilter === ii || D.magFilter === $o || D.magFilter === $a || D.magFilter === ds || D.minFilter === ii || D.minFilter === $o || D.minFilter === $a || D.minFilter === ds) && console.warn("THREE.WebGLRenderer: Unable to use linear filtering with floating point textures. OES_texture_float_linear not supported on this device."), s.texParameteri(G, s.TEXTURE_WRAP_S, X[D.wrapS]), s.texParameteri(G, s.TEXTURE_WRAP_T, X[D.wrapT]), (G === s.TEXTURE_3D || G === s.TEXTURE_2D_ARRAY) && s.texParameteri(G, s.TEXTURE_WRAP_R, X[D.wrapR]), s.texParameteri(G, s.TEXTURE_MAG_FILTER, K[D.magFilter]), s.texParameteri(G, s.TEXTURE_MIN_FILTER, K[D.minFilter]), D.compareFunction && (s.texParameteri(G, s.TEXTURE_COMPARE_MODE, s.COMPARE_REF_TO_TEXTURE), s.texParameteri(G, s.TEXTURE_COMPARE_FUNC, te[D.compareFunction])), e.has("EXT_texture_filter_anisotropic") === !0) {
      if (D.magFilter === Ni || D.minFilter !== $a && D.minFilter !== ds || D.type === xn && e.has("OES_texture_float_linear") === !1)
        return;
      if (D.anisotropy > 1 || i.get(D).__currentAnisotropy) {
        const ne = e.get("EXT_texture_filter_anisotropic");
        s.texParameterf(G, ne.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(D.anisotropy, n.getMaxAnisotropy())), i.get(D).__currentAnisotropy = D.anisotropy;
      }
    }
  }
  function be(G, D) {
    let ne = !1;
    G.__webglInit === void 0 && (G.__webglInit = !0, D.addEventListener("dispose", b));
    const ve = D.source;
    let Y = d.get(ve);
    Y === void 0 && (Y = {}, d.set(ve, Y));
    const ee = I(D);
    if (ee !== G.__cacheKey) {
      Y[ee] === void 0 && (Y[ee] = {
        texture: s.createTexture(),
        usedTimes: 0
      }, a.memory.textures++, ne = !0), Y[ee].usedTimes++;
      const we = Y[G.__cacheKey];
      we !== void 0 && (Y[G.__cacheKey].usedTimes--, we.usedTimes === 0 && C(D)), G.__cacheKey = ee, G.__webglTexture = Y[ee].texture;
    }
    return ne;
  }
  function Pe(G, D, ne) {
    let ve = s.TEXTURE_2D;
    (D.isDataArrayTexture || D.isCompressedArrayTexture) && (ve = s.TEXTURE_2D_ARRAY), D.isData3DTexture && (ve = s.TEXTURE_3D);
    const Y = be(G, D), ee = D.source;
    t.bindTexture(ve, G.__webglTexture, s.TEXTURE0 + ne);
    const we = i.get(ee);
    if (ee.version !== we.__version || Y === !0) {
      t.activeTexture(s.TEXTURE0 + ne);
      const de = bi.getPrimaries(bi.workingColorSpace), xe = D.colorSpace === Uo ? null : bi.getPrimaries(D.colorSpace), it = D.colorSpace === Uo || de === xe ? s.NONE : s.BROWSER_DEFAULT_WEBGL;
      s.pixelStorei(s.UNPACK_FLIP_Y_WEBGL, D.flipY), s.pixelStorei(s.UNPACK_PREMULTIPLY_ALPHA_WEBGL, D.premultiplyAlpha), s.pixelStorei(s.UNPACK_ALIGNMENT, D.unpackAlignment), s.pixelStorei(s.UNPACK_COLORSPACE_CONVERSION_WEBGL, it);
      let q = v(D.image, !1, n.maxTextureSize);
      q = Ve(D, q);
      const ae = r.convert(D.format, D.colorSpace), Ee = r.convert(D.type);
      let Le = x(D.internalFormat, ae, Ee, D.colorSpace, D.isVideoTexture);
      re(ve, D);
      let Ae;
      const We = D.mipmaps, ot = D.isVideoTexture !== !0, Ct = we.__version === void 0 || Y === !0, F = ee.dataReady, ue = A(D, q);
      if (D.isDepthTexture)
        Le = y(D.format === yl, D.type), Ct && (ot ? t.texStorage2D(s.TEXTURE_2D, 1, Le, q.width, q.height) : t.texImage2D(s.TEXTURE_2D, 0, Le, q.width, q.height, 0, ae, Ee, null));
      else if (D.isDataTexture)
        if (We.length > 0) {
          ot && Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, We[0].width, We[0].height);
          for (let Q = 0, he = We.length; Q < he; Q++)
            Ae = We[Q], ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ee, Ae.data) : t.texImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, ae, Ee, Ae.data);
          D.generateMipmaps = !1;
        } else
          ot ? (Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, q.width, q.height), F && t.texSubImage2D(s.TEXTURE_2D, 0, 0, 0, q.width, q.height, ae, Ee, q.data)) : t.texImage2D(s.TEXTURE_2D, 0, Le, q.width, q.height, 0, ae, Ee, q.data);
      else if (D.isCompressedTexture)
        if (D.isCompressedArrayTexture) {
          ot && Ct && t.texStorage3D(s.TEXTURE_2D_ARRAY, ue, Le, We[0].width, We[0].height, q.depth);
          for (let Q = 0, he = We.length; Q < he; Q++)
            if (Ae = We[Q], D.format !== dn)
              if (ae !== null)
                if (ot) {
                  if (F)
                    if (D.layerUpdates.size > 0) {
                      const Be = q6(Ae.width, Ae.height, D.format, D.type);
                      for (const ft of D.layerUpdates) {
                        const Et = Ae.data.subarray(
                          ft * Be / Ae.data.BYTES_PER_ELEMENT,
                          (ft + 1) * Be / Ae.data.BYTES_PER_ELEMENT
                        );
                        t.compressedTexSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, ft, Ae.width, Ae.height, 1, ae, Et, 0, 0);
                      }
                      D.clearLayerUpdates();
                    } else
                      t.compressedTexSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, 0, Ae.width, Ae.height, q.depth, ae, Ae.data, 0, 0);
                } else
                  t.compressedTexImage3D(s.TEXTURE_2D_ARRAY, Q, Le, Ae.width, Ae.height, q.depth, 0, Ae.data, 0, 0);
              else
                console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()");
            else
              ot ? F && t.texSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, 0, Ae.width, Ae.height, q.depth, ae, Ee, Ae.data) : t.texImage3D(s.TEXTURE_2D_ARRAY, Q, Le, Ae.width, Ae.height, q.depth, 0, ae, Ee, Ae.data);
        } else {
          ot && Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, We[0].width, We[0].height);
          for (let Q = 0, he = We.length; Q < he; Q++)
            Ae = We[Q], D.format !== dn ? ae !== null ? ot ? F && t.compressedTexSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ae.data) : t.compressedTexImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, Ae.data) : console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()") : ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ee, Ae.data) : t.texImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, ae, Ee, Ae.data);
        }
      else if (D.isDataArrayTexture)
        if (ot) {
          if (Ct && t.texStorage3D(s.TEXTURE_2D_ARRAY, ue, Le, q.width, q.height, q.depth), F)
            if (D.layerUpdates.size > 0) {
              const Q = q6(q.width, q.height, D.format, D.type);
              for (const he of D.layerUpdates) {
                const Be = q.data.subarray(
                  he * Q / q.data.BYTES_PER_ELEMENT,
                  (he + 1) * Q / q.data.BYTES_PER_ELEMENT
                );
                t.texSubImage3D(s.TEXTURE_2D_ARRAY, 0, 0, 0, he, q.width, q.height, 1, ae, Ee, Be);
              }
              D.clearLayerUpdates();
            } else
              t.texSubImage3D(s.TEXTURE_2D_ARRAY, 0, 0, 0, 0, q.width, q.height, q.depth, ae, Ee, q.data);
        } else
          t.texImage3D(s.TEXTURE_2D_ARRAY, 0, Le, q.width, q.height, q.depth, 0, ae, Ee, q.data);
      else if (D.isData3DTexture)
        ot ? (Ct && t.texStorage3D(s.TEXTURE_3D, ue, Le, q.width, q.height, q.depth), F && t.texSubImage3D(s.TEXTURE_3D, 0, 0, 0, 0, q.width, q.height, q.depth, ae, Ee, q.data)) : t.texImage3D(s.TEXTURE_3D, 0, Le, q.width, q.height, q.depth, 0, ae, Ee, q.data);
      else if (D.isFramebufferTexture) {
        if (Ct)
          if (ot)
            t.texStorage2D(s.TEXTURE_2D, ue, Le, q.width, q.height);
          else {
            let Q = q.width, he = q.height;
            for (let Be = 0; Be < ue; Be++)
              t.texImage2D(s.TEXTURE_2D, Be, Le, Q, he, 0, ae, Ee, null), Q >>= 1, he >>= 1;
          }
      } else if (We.length > 0) {
        if (ot && Ct) {
          const Q = Ze(We[0]);
          t.texStorage2D(s.TEXTURE_2D, ue, Le, Q.width, Q.height);
        }
        for (let Q = 0, he = We.length; Q < he; Q++)
          Ae = We[Q], ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, ae, Ee, Ae) : t.texImage2D(s.TEXTURE_2D, Q, Le, ae, Ee, Ae);
        D.generateMipmaps = !1;
      } else if (ot) {
        if (Ct) {
          const Q = Ze(q);
          t.texStorage2D(s.TEXTURE_2D, ue, Le, Q.width, Q.height);
        }
        F && t.texSubImage2D(s.TEXTURE_2D, 0, 0, 0, ae, Ee, q);
      } else
        t.texImage2D(s.TEXTURE_2D, 0, Le, ae, Ee, q);
      g(D) && m(ve), we.__version = ee.version, D.onUpdate && D.onUpdate(D);
    }
    G.__version = D.version;
  }
  function J(G, D, ne) {
    if (D.image.length !== 6)
      return;
    const ve = be(G, D), Y = D.source;
    t.bindTexture(s.TEXTURE_CUBE_MAP, G.__webglTexture, s.TEXTURE0 + ne);
    const ee = i.get(Y);
    if (Y.version !== ee.__version || ve === !0) {
      t.activeTexture(s.TEXTURE0 + ne);
      const we = bi.getPrimaries(bi.workingColorSpace), de = D.colorSpace === Uo ? null : bi.getPrimaries(D.colorSpace), xe = D.colorSpace === Uo || we === de ? s.NONE : s.BROWSER_DEFAULT_WEBGL;
      s.pixelStorei(s.UNPACK_FLIP_Y_WEBGL, D.flipY), s.pixelStorei(s.UNPACK_PREMULTIPLY_ALPHA_WEBGL, D.premultiplyAlpha), s.pixelStorei(s.UNPACK_ALIGNMENT, D.unpackAlignment), s.pixelStorei(s.UNPACK_COLORSPACE_CONVERSION_WEBGL, xe);
      const it = D.isCompressedTexture || D.image[0].isCompressedTexture, q = D.image[0] && D.image[0].isDataTexture, ae = [];
      for (let he = 0; he < 6; he++)
        !it && !q ? ae[he] = v(D.image[he], !0, n.maxCubemapSize) : ae[he] = q ? D.image[he].image : D.image[he], ae[he] = Ve(D, ae[he]);
      const Ee = ae[0], Le = r.convert(D.format, D.colorSpace), Ae = r.convert(D.type), We = x(D.internalFormat, Le, Ae, D.colorSpace), ot = D.isVideoTexture !== !0, Ct = ee.__version === void 0 || ve === !0, F = Y.dataReady;
      let ue = A(D, Ee);
      re(s.TEXTURE_CUBE_MAP, D);
      let Q;
      if (it) {
        ot && Ct && t.texStorage2D(s.TEXTURE_CUBE_MAP, ue, We, Ee.width, Ee.height);
        for (let he = 0; he < 6; he++) {
          Q = ae[he].mipmaps;
          for (let Be = 0; Be < Q.length; Be++) {
            const ft = Q[Be];
            D.format !== dn ? Le !== null ? ot ? F && t.compressedTexSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, 0, 0, ft.width, ft.height, Le, ft.data) : t.compressedTexImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, We, ft.width, ft.height, 0, ft.data) : console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .setTextureCube()") : ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, 0, 0, ft.width, ft.height, Le, Ae, ft.data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, We, ft.width, ft.height, 0, Le, Ae, ft.data);
          }
        }
      } else {
        if (Q = D.mipmaps, ot && Ct) {
          Q.length > 0 && ue++;
          const he = Ze(ae[0]);
          t.texStorage2D(s.TEXTURE_CUBE_MAP, ue, We, he.width, he.height);
        }
        for (let he = 0; he < 6; he++)
          if (q) {
            ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, 0, 0, ae[he].width, ae[he].height, Le, Ae, ae[he].data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, We, ae[he].width, ae[he].height, 0, Le, Ae, ae[he].data);
            for (let Be = 0; Be < Q.length; Be++) {
              const Et = Q[Be].image[he].image;
              ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, 0, 0, Et.width, Et.height, Le, Ae, Et.data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, We, Et.width, Et.height, 0, Le, Ae, Et.data);
            }
          } else {
            ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, 0, 0, Le, Ae, ae[he]) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, We, Le, Ae, ae[he]);
            for (let Be = 0; Be < Q.length; Be++) {
              const ft = Q[Be];
              ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, 0, 0, Le, Ae, ft.image[he]) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, We, Le, Ae, ft.image[he]);
            }
          }
      }
      g(D) && m(s.TEXTURE_CUBE_MAP), ee.__version = Y.version, D.onUpdate && D.onUpdate(D);
    }
    G.__version = D.version;
  }
  function fe(G, D, ne, ve, Y, ee) {
    const we = r.convert(ne.format, ne.colorSpace), de = r.convert(ne.type), xe = x(ne.internalFormat, we, de, ne.colorSpace);
    if (!i.get(D).__hasExternalTextures) {
      const q = Math.max(1, D.width >> ee), ae = Math.max(1, D.height >> ee);
      Y === s.TEXTURE_3D || Y === s.TEXTURE_2D_ARRAY ? t.texImage3D(Y, ee, xe, q, ae, D.depth, 0, we, de, null) : t.texImage2D(Y, ee, xe, q, ae, 0, we, de, null);
    }
    t.bindFramebuffer(s.FRAMEBUFFER, G), Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, ve, Y, i.get(ne).__webglTexture, 0, qe(D)) : (Y === s.TEXTURE_2D || Y >= s.TEXTURE_CUBE_MAP_POSITIVE_X && Y <= s.TEXTURE_CUBE_MAP_NEGATIVE_Z) && s.framebufferTexture2D(s.FRAMEBUFFER, ve, Y, i.get(ne).__webglTexture, ee), t.bindFramebuffer(s.FRAMEBUFFER, null);
  }
  function Me(G, D, ne) {
    if (s.bindRenderbuffer(s.RENDERBUFFER, G), D.depthBuffer) {
      const ve = D.depthTexture, Y = ve && ve.isDepthTexture ? ve.type : null, ee = y(D.stencilBuffer, Y), we = D.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT, de = qe(D);
      Ie(D) ? o.renderbufferStorageMultisampleEXT(s.RENDERBUFFER, de, ee, D.width, D.height) : ne ? s.renderbufferStorageMultisample(s.RENDERBUFFER, de, ee, D.width, D.height) : s.renderbufferStorage(s.RENDERBUFFER, ee, D.width, D.height), s.framebufferRenderbuffer(s.FRAMEBUFFER, we, s.RENDERBUFFER, G);
    } else {
      const ve = D.textures;
      for (let Y = 0; Y < ve.length; Y++) {
        const ee = ve[Y], we = r.convert(ee.format, ee.colorSpace), de = r.convert(ee.type), xe = x(ee.internalFormat, we, de, ee.colorSpace), it = qe(D);
        ne && Ie(D) === !1 ? s.renderbufferStorageMultisample(s.RENDERBUFFER, it, xe, D.width, D.height) : Ie(D) ? o.renderbufferStorageMultisampleEXT(s.RENDERBUFFER, it, xe, D.width, D.height) : s.renderbufferStorage(s.RENDERBUFFER, xe, D.width, D.height);
      }
    }
    s.bindRenderbuffer(s.RENDERBUFFER, null);
  }
  function ye(G, D) {
    if (D && D.isWebGLCubeRenderTarget)
      throw new Error("Depth Texture with cube render targets is not supported");
    if (t.bindFramebuffer(s.FRAMEBUFFER, G), !(D.depthTexture && D.depthTexture.isDepthTexture))
      throw new Error("renderTarget.depthTexture must be an instance of THREE.DepthTexture");
    (!i.get(D.depthTexture).__webglTexture || D.depthTexture.image.width !== D.width || D.depthTexture.image.height !== D.height) && (D.depthTexture.image.width = D.width, D.depthTexture.image.height = D.height, D.depthTexture.needsUpdate = !0), N(D.depthTexture, 0);
    const ve = i.get(D.depthTexture).__webglTexture, Y = qe(D);
    if (D.depthTexture.format === Au)
      Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, s.DEPTH_ATTACHMENT, s.TEXTURE_2D, ve, 0, Y) : s.framebufferTexture2D(s.FRAMEBUFFER, s.DEPTH_ATTACHMENT, s.TEXTURE_2D, ve, 0);
    else if (D.depthTexture.format === yl)
      Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, s.DEPTH_STENCIL_ATTACHMENT, s.TEXTURE_2D, ve, 0, Y) : s.framebufferTexture2D(s.FRAMEBUFFER, s.DEPTH_STENCIL_ATTACHMENT, s.TEXTURE_2D, ve, 0);
    else
      throw new Error("Unknown depthTexture format");
  }
  function Ye(G) {
    const D = i.get(G), ne = G.isWebGLCubeRenderTarget === !0;
    if (G.depthTexture && !D.__autoAllocateDepthBuffer) {
      if (ne)
        throw new Error("target.depthTexture not supported in Cube render targets");
      ye(D.__webglFramebuffer, G);
    } else if (ne) {
      D.__webglDepthbuffer = [];
      for (let ve = 0; ve < 6; ve++)
        t.bindFramebuffer(s.FRAMEBUFFER, D.__webglFramebuffer[ve]), D.__webglDepthbuffer[ve] = s.createRenderbuffer(), Me(D.__webglDepthbuffer[ve], G, !1);
    } else
      t.bindFramebuffer(s.FRAMEBUFFER, D.__webglFramebuffer), D.__webglDepthbuffer = s.createRenderbuffer(), Me(D.__webglDepthbuffer, G, !1);
    t.bindFramebuffer(s.FRAMEBUFFER, null);
  }
  function Je(G, D, ne) {
    const ve = i.get(G);
    D !== void 0 && fe(ve.__webglFramebuffer, G, G.texture, s.COLOR_ATTACHMENT0, s.TEXTURE_2D, 0), ne !== void 0 && Ye(G);
  }
  function rt(G) {
    const D = G.texture, ne = i.get(G), ve = i.get(D);
    G.addEventListener("dispose", M);
    const Y = G.textures, ee = G.isWebGLCubeRenderTarget === !0, we = Y.length > 1;
    if (we || (ve.__webglTexture === void 0 && (ve.__webglTexture = s.createTexture()), ve.__version = D.version, a.memory.textures++), ee) {
      ne.__webglFramebuffer = [];
      for (let de = 0; de < 6; de++)
        if (D.mipmaps && D.mipmaps.length > 0) {
          ne.__webglFramebuffer[de] = [];
          for (let xe = 0; xe < D.mipmaps.length; xe++)
            ne.__webglFramebuffer[de][xe] = s.createFramebuffer();
        } else
          ne.__webglFramebuffer[de] = s.createFramebuffer();
    } else {
      if (D.mipmaps && D.mipmaps.length > 0) {
        ne.__webglFramebuffer = [];
        for (let de = 0; de < D.mipmaps.length; de++)
          ne.__webglFramebuffer[de] = s.createFramebuffer();
      } else
        ne.__webglFramebuffer = s.createFramebuffer();
      if (we)
        for (let de = 0, xe = Y.length; de < xe; de++) {
          const it = i.get(Y[de]);
          it.__webglTexture === void 0 && (it.__webglTexture = s.createTexture(), a.memory.textures++);
        }
      if (G.samples > 0 && Ie(G) === !1) {
        ne.__webglMultisampledFramebuffer = s.createFramebuffer(), ne.__webglColorRenderbuffer = [], t.bindFramebuffer(s.FRAMEBUFFER, ne.__webglMultisampledFramebuffer);
        for (let de = 0; de < Y.length; de++) {
          const xe = Y[de];
          ne.__webglColorRenderbuffer[de] = s.createRenderbuffer(), s.bindRenderbuffer(s.RENDERBUFFER, ne.__webglColorRenderbuffer[de]);
          const it = r.convert(xe.format, xe.colorSpace), q = r.convert(xe.type), ae = x(xe.internalFormat, it, q, xe.colorSpace, G.isXRRenderTarget === !0), Ee = qe(G);
          s.renderbufferStorageMultisample(s.RENDERBUFFER, Ee, ae, G.width, G.height), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + de, s.RENDERBUFFER, ne.__webglColorRenderbuffer[de]);
        }
        s.bindRenderbuffer(s.RENDERBUFFER, null), G.depthBuffer && (ne.__webglDepthRenderbuffer = s.createRenderbuffer(), Me(ne.__webglDepthRenderbuffer, G, !0)), t.bindFramebuffer(s.FRAMEBUFFER, null);
      }
    }
    if (ee) {
      t.bindTexture(s.TEXTURE_CUBE_MAP, ve.__webglTexture), re(s.TEXTURE_CUBE_MAP, D);
      for (let de = 0; de < 6; de++)
        if (D.mipmaps && D.mipmaps.length > 0)
          for (let xe = 0; xe < D.mipmaps.length; xe++)
            fe(ne.__webglFramebuffer[de][xe], G, D, s.COLOR_ATTACHMENT0, s.TEXTURE_CUBE_MAP_POSITIVE_X + de, xe);
        else
          fe(ne.__webglFramebuffer[de], G, D, s.COLOR_ATTACHMENT0, s.TEXTURE_CUBE_MAP_POSITIVE_X + de, 0);
      g(D) && m(s.TEXTURE_CUBE_MAP), t.unbindTexture();
    } else if (we) {
      for (let de = 0, xe = Y.length; de < xe; de++) {
        const it = Y[de], q = i.get(it);
        t.bindTexture(s.TEXTURE_2D, q.__webglTexture), re(s.TEXTURE_2D, it), fe(ne.__webglFramebuffer, G, it, s.COLOR_ATTACHMENT0 + de, s.TEXTURE_2D, 0), g(it) && m(s.TEXTURE_2D);
      }
      t.unbindTexture();
    } else {
      let de = s.TEXTURE_2D;
      if ((G.isWebGL3DRenderTarget || G.isWebGLArrayRenderTarget) && (de = G.isWebGL3DRenderTarget ? s.TEXTURE_3D : s.TEXTURE_2D_ARRAY), t.bindTexture(de, ve.__webglTexture), re(de, D), D.mipmaps && D.mipmaps.length > 0)
        for (let xe = 0; xe < D.mipmaps.length; xe++)
          fe(ne.__webglFramebuffer[xe], G, D, s.COLOR_ATTACHMENT0, de, xe);
      else
        fe(ne.__webglFramebuffer, G, D, s.COLOR_ATTACHMENT0, de, 0);
      g(D) && m(de), t.unbindTexture();
    }
    G.depthBuffer && Ye(G);
  }
  function wt(G) {
    const D = G.textures;
    for (let ne = 0, ve = D.length; ne < ve; ne++) {
      const Y = D[ne];
      if (g(Y)) {
        const ee = G.isWebGLCubeRenderTarget ? s.TEXTURE_CUBE_MAP : s.TEXTURE_2D, we = i.get(Y).__webglTexture;
        t.bindTexture(ee, we), m(ee), t.unbindTexture();
      }
    }
  }
  const k = [], Fe = [];
  function Ge(G) {
    if (G.samples > 0) {
      if (Ie(G) === !1) {
        const D = G.textures, ne = G.width, ve = G.height;
        let Y = s.COLOR_BUFFER_BIT;
        const ee = G.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT, we = i.get(G), de = D.length > 1;
        if (de)
          for (let xe = 0; xe < D.length; xe++)
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglMultisampledFramebuffer), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.RENDERBUFFER, null), t.bindFramebuffer(s.FRAMEBUFFER, we.__webglFramebuffer), s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.TEXTURE_2D, null, 0);
        t.bindFramebuffer(s.READ_FRAMEBUFFER, we.__webglMultisampledFramebuffer), t.bindFramebuffer(s.DRAW_FRAMEBUFFER, we.__webglFramebuffer);
        for (let xe = 0; xe < D.length; xe++) {
          if (G.resolveDepthBuffer && (G.depthBuffer && (Y |= s.DEPTH_BUFFER_BIT), G.stencilBuffer && G.resolveStencilBuffer && (Y |= s.STENCIL_BUFFER_BIT)), de) {
            s.framebufferRenderbuffer(s.READ_FRAMEBUFFER, s.COLOR_ATTACHMENT0, s.RENDERBUFFER, we.__webglColorRenderbuffer[xe]);
            const it = i.get(D[xe]).__webglTexture;
            s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0, s.TEXTURE_2D, it, 0);
          }
          s.blitFramebuffer(0, 0, ne, ve, 0, 0, ne, ve, Y, s.NEAREST), l === !0 && (k.length = 0, Fe.length = 0, k.push(s.COLOR_ATTACHMENT0 + xe), G.depthBuffer && G.resolveDepthBuffer === !1 && (k.push(ee), Fe.push(ee), s.invalidateFramebuffer(s.DRAW_FRAMEBUFFER, Fe)), s.invalidateFramebuffer(s.READ_FRAMEBUFFER, k));
        }
        if (t.bindFramebuffer(s.READ_FRAMEBUFFER, null), t.bindFramebuffer(s.DRAW_FRAMEBUFFER, null), de)
          for (let xe = 0; xe < D.length; xe++) {
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglMultisampledFramebuffer), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.RENDERBUFFER, we.__webglColorRenderbuffer[xe]);
            const it = i.get(D[xe]).__webglTexture;
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglFramebuffer), s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.TEXTURE_2D, it, 0);
          }
        t.bindFramebuffer(s.DRAW_FRAMEBUFFER, we.__webglMultisampledFramebuffer);
      } else if (G.depthBuffer && G.resolveDepthBuffer === !1 && l) {
        const D = G.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT;
        s.invalidateFramebuffer(s.DRAW_FRAMEBUFFER, [D]);
      }
    }
  }
  function qe(G) {
    return Math.min(n.maxSamples, G.samples);
  }
  function Ie(G) {
    const D = i.get(G);
    return G.samples > 0 && e.has("WEBGL_multisampled_render_to_texture") === !0 && D.__useRenderToTexture !== !1;
  }
  function vt(G) {
    const D = a.render.frame;
    h.get(G) !== D && (h.set(G, D), G.update());
  }
  function Ve(G, D) {
    const ne = G.colorSpace, ve = G.format, Y = G.type;
    return G.isCompressedTexture === !0 || G.isVideoTexture === !0 || ne !== Si && ne !== Uo && (bi.getTransfer(ne) === pn ? (ve !== dn || Y !== o0) && console.warn("THREE.WebGLTextures: sRGB encoded textures have to use RGBAFormat and UnsignedByteType.") : console.error("THREE.WebGLTextures: Unsupported texture color space:", ne)), D;
  }
  function Ze(G) {
    return typeof HTMLImageElement < "u" && G instanceof HTMLImageElement ? (c.width = G.naturalWidth || G.width, c.height = G.naturalHeight || G.height) : typeof VideoFrame < "u" && G instanceof VideoFrame ? (c.width = G.displayWidth, c.height = G.displayHeight) : (c.width = G.width, c.height = G.height), c;
  }
  this.allocateTextureUnit = L, this.resetTextureUnits = P, this.setTexture2D = N, this.setTexture2DArray = H, this.setTexture3D = U, this.setTextureCube = W, this.rebindTextures = Je, this.setupRenderTarget = rt, this.updateRenderTargetMipmap = wt, this.updateMultisampleRenderTarget = Ge, this.setupDepthRenderbuffer = Ye, this.setupFrameBufferTexture = fe, this.useMultisampledRTT = Ie;
}
function iI(s, e) {
  function t(i, n = Uo) {
    let r;
    const a = bi.getTransfer(n);
    if (i === o0)
      return s.UNSIGNED_BYTE;
    if (i === m4)
      return s.UNSIGNED_SHORT_4_4_4_4;
    if (i === g4)
      return s.UNSIGNED_SHORT_5_5_5_1;
    if (i === MM)
      return s.UNSIGNED_INT_5_9_9_9_REV;
    if (i === AM)
      return s.BYTE;
    if (i === bM)
      return s.SHORT;
    if (i === tp)
      return s.UNSIGNED_SHORT;
    if (i === p4)
      return s.INT;
    if (i === gl)
      return s.UNSIGNED_INT;
    if (i === xn)
      return s.FLOAT;
    if (i === on)
      return s.HALF_FLOAT;
    if (i === SM)
      return s.ALPHA;
    if (i === wM)
      return s.RGB;
    if (i === dn)
      return s.RGBA;
    if (i === EM)
      return s.LUMINANCE;
    if (i === TM)
      return s.LUMINANCE_ALPHA;
    if (i === Au)
      return s.DEPTH_COMPONENT;
    if (i === yl)
      return s.DEPTH_STENCIL;
    if (i === Vu)
      return s.RED;
    if (i === t2)
      return s.RED_INTEGER;
    if (i === CM)
      return s.RG;
    if (i === v4)
      return s.RG_INTEGER;
    if (i === y4)
      return s.RGBA_INTEGER;
    if (i === x1 || i === A1 || i === b1 || i === M1)
      if (a === pn)
        if (r = e.get("WEBGL_compressed_texture_s3tc_srgb"), r !== null) {
          if (i === x1)
            return r.COMPRESSED_SRGB_S3TC_DXT1_EXT;
          if (i === A1)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT1_EXT;
          if (i === b1)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT3_EXT;
          if (i === M1)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT5_EXT;
        } else
          return null;
      else if (r = e.get("WEBGL_compressed_texture_s3tc"), r !== null) {
        if (i === x1)
          return r.COMPRESSED_RGB_S3TC_DXT1_EXT;
        if (i === A1)
          return r.COMPRESSED_RGBA_S3TC_DXT1_EXT;
        if (i === b1)
          return r.COMPRESSED_RGBA_S3TC_DXT3_EXT;
        if (i === M1)
          return r.COMPRESSED_RGBA_S3TC_DXT5_EXT;
      } else
        return null;
    if (i === x9 || i === A9 || i === b9 || i === M9)
      if (r = e.get("WEBGL_compressed_texture_pvrtc"), r !== null) {
        if (i === x9)
          return r.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;
        if (i === A9)
          return r.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;
        if (i === b9)
          return r.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG;
        if (i === M9)
          return r.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG;
      } else
        return null;
    if (i === S9 || i === w9 || i === E9)
      if (r = e.get("WEBGL_compressed_texture_etc"), r !== null) {
        if (i === S9 || i === w9)
          return a === pn ? r.COMPRESSED_SRGB8_ETC2 : r.COMPRESSED_RGB8_ETC2;
        if (i === E9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ETC2_EAC : r.COMPRESSED_RGBA8_ETC2_EAC;
      } else
        return null;
    if (i === T9 || i === C9 || i === _9 || i === P9 || i === R9 || i === D9 || i === L9 || i === I9 || i === O9 || i === N9 || i === B9 || i === F9 || i === U9 || i === z9)
      if (r = e.get("WEBGL_compressed_texture_astc"), r !== null) {
        if (i === T9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_4x4_KHR : r.COMPRESSED_RGBA_ASTC_4x4_KHR;
        if (i === C9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_5x4_KHR : r.COMPRESSED_RGBA_ASTC_5x4_KHR;
        if (i === _9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_5x5_KHR : r.COMPRESSED_RGBA_ASTC_5x5_KHR;
        if (i === P9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_6x5_KHR : r.COMPRESSED_RGBA_ASTC_6x5_KHR;
        if (i === R9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_6x6_KHR : r.COMPRESSED_RGBA_ASTC_6x6_KHR;
        if (i === D9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x5_KHR : r.COMPRESSED_RGBA_ASTC_8x5_KHR;
        if (i === L9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x6_KHR : r.COMPRESSED_RGBA_ASTC_8x6_KHR;
        if (i === I9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x8_KHR : r.COMPRESSED_RGBA_ASTC_8x8_KHR;
        if (i === O9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x5_KHR : r.COMPRESSED_RGBA_ASTC_10x5_KHR;
        if (i === N9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x6_KHR : r.COMPRESSED_RGBA_ASTC_10x6_KHR;
        if (i === B9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x8_KHR : r.COMPRESSED_RGBA_ASTC_10x8_KHR;
        if (i === F9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x10_KHR : r.COMPRESSED_RGBA_ASTC_10x10_KHR;
        if (i === U9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_12x10_KHR : r.COMPRESSED_RGBA_ASTC_12x10_KHR;
        if (i === z9)
          return a === pn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_12x12_KHR : r.COMPRESSED_RGBA_ASTC_12x12_KHR;
      } else
        return null;
    if (i === S1 || i === k9 || i === G9)
      if (r = e.get("EXT_texture_compression_bptc"), r !== null) {
        if (i === S1)
          return a === pn ? r.COMPRESSED_SRGB_ALPHA_BPTC_UNORM_EXT : r.COMPRESSED_RGBA_BPTC_UNORM_EXT;
        if (i === k9)
          return r.COMPRESSED_RGB_BPTC_SIGNED_FLOAT_EXT;
        if (i === G9)
          return r.COMPRESSED_RGB_BPTC_UNSIGNED_FLOAT_EXT;
      } else
        return null;
    if (i === _M || i === H9 || i === V9 || i === W9)
      if (r = e.get("EXT_texture_compression_rgtc"), r !== null) {
        if (i === S1)
          return r.COMPRESSED_RED_RGTC1_EXT;
        if (i === H9)
          return r.COMPRESSED_SIGNED_RED_RGTC1_EXT;
        if (i === V9)
          return r.COMPRESSED_RED_GREEN_RGTC2_EXT;
        if (i === W9)
          return r.COMPRESSED_SIGNED_RED_GREEN_RGTC2_EXT;
      } else
        return null;
    return i === vl ? s.UNSIGNED_INT_24_8 : s[i] !== void 0 ? s[i] : null;
  }
  return { convert: t };
}
let nI = class extends Zt {
  constructor(e = []) {
    super(), this.isArrayCamera = !0, this.cameras = e;
  }
}, ci = class extends mt {
  constructor() {
    super(), this.isGroup = !0, this.type = "Group";
  }
};
const fX = { type: "move" };
let X8 = class {
  constructor() {
    this._targetRay = null, this._grip = null, this._hand = null;
  }
  getHandSpace() {
    return this._hand === null && (this._hand = new ci(), this._hand.matrixAutoUpdate = !1, this._hand.visible = !1, this._hand.joints = {}, this._hand.inputState = { pinching: !1 }), this._hand;
  }
  getTargetRaySpace() {
    return this._targetRay === null && (this._targetRay = new ci(), this._targetRay.matrixAutoUpdate = !1, this._targetRay.visible = !1, this._targetRay.hasLinearVelocity = !1, this._targetRay.linearVelocity = new _(), this._targetRay.hasAngularVelocity = !1, this._targetRay.angularVelocity = new _()), this._targetRay;
  }
  getGripSpace() {
    return this._grip === null && (this._grip = new ci(), this._grip.matrixAutoUpdate = !1, this._grip.visible = !1, this._grip.hasLinearVelocity = !1, this._grip.linearVelocity = new _(), this._grip.hasAngularVelocity = !1, this._grip.angularVelocity = new _()), this._grip;
  }
  dispatchEvent(e) {
    return this._targetRay !== null && this._targetRay.dispatchEvent(e), this._grip !== null && this._grip.dispatchEvent(e), this._hand !== null && this._hand.dispatchEvent(e), this;
  }
  connect(e) {
    if (e && e.hand) {
      const t = this._hand;
      if (t)
        for (const i of e.hand.values())
          this._getHandJoint(t, i);
    }
    return this.dispatchEvent({ type: "connected", data: e }), this;
  }
  disconnect(e) {
    return this.dispatchEvent({ type: "disconnected", data: e }), this._targetRay !== null && (this._targetRay.visible = !1), this._grip !== null && (this._grip.visible = !1), this._hand !== null && (this._hand.visible = !1), this;
  }
  update(e, t, i) {
    let n = null, r = null, a = null;
    const o = this._targetRay, l = this._grip, c = this._hand;
    if (e && t.session.visibilityState !== "visible-blurred") {
      if (c && e.hand) {
        a = !0;
        for (const v of e.hand.values()) {
          const g = t.getJointPose(v, i), m = this._getHandJoint(c, v);
          g !== null && (m.matrix.fromArray(g.transform.matrix), m.matrix.decompose(m.position, m.rotation, m.scale), m.matrixWorldNeedsUpdate = !0, m.jointRadius = g.radius), m.visible = g !== null;
        }
        const h = c.joints["index-finger-tip"], u = c.joints["thumb-tip"], d = h.position.distanceTo(u.position), f = 0.02, p = 5e-3;
        c.inputState.pinching && d > f + p ? (c.inputState.pinching = !1, this.dispatchEvent({
          type: "pinchend",
          handedness: e.handedness,
          target: this
        })) : !c.inputState.pinching && d <= f - p && (c.inputState.pinching = !0, this.dispatchEvent({
          type: "pinchstart",
          handedness: e.handedness,
          target: this
        }));
      } else
        l !== null && e.gripSpace && (r = t.getPose(e.gripSpace, i), r !== null && (l.matrix.fromArray(r.transform.matrix), l.matrix.decompose(l.position, l.rotation, l.scale), l.matrixWorldNeedsUpdate = !0, r.linearVelocity ? (l.hasLinearVelocity = !0, l.linearVelocity.copy(r.linearVelocity)) : l.hasLinearVelocity = !1, r.angularVelocity ? (l.hasAngularVelocity = !0, l.angularVelocity.copy(r.angularVelocity)) : l.hasAngularVelocity = !1));
      o !== null && (n = t.getPose(e.targetRaySpace, i), n === null && r !== null && (n = r), n !== null && (o.matrix.fromArray(n.transform.matrix), o.matrix.decompose(o.position, o.rotation, o.scale), o.matrixWorldNeedsUpdate = !0, n.linearVelocity ? (o.hasLinearVelocity = !0, o.linearVelocity.copy(n.linearVelocity)) : o.hasLinearVelocity = !1, n.angularVelocity ? (o.hasAngularVelocity = !0, o.angularVelocity.copy(n.angularVelocity)) : o.hasAngularVelocity = !1, this.dispatchEvent(fX)));
    }
    return o !== null && (o.visible = n !== null), l !== null && (l.visible = r !== null), c !== null && (c.visible = a !== null), this;
  }
  // private method
  _getHandJoint(e, t) {
    if (e.joints[t.jointName] === void 0) {
      const i = new ci();
      i.matrixAutoUpdate = !1, i.visible = !1, e.joints[t.jointName] = i, e.add(i);
    }
    return e.joints[t.jointName];
  }
};
const pX = `
void main() {

	gl_Position = vec4( position, 1.0 );

}`, mX = `
uniform sampler2DArray depthColor;
uniform float depthWidth;
uniform float depthHeight;

void main() {

	vec2 coord = vec2( gl_FragCoord.x / depthWidth, gl_FragCoord.y / depthHeight );

	if ( coord.x >= 1.0 ) {

		gl_FragDepth = texture( depthColor, vec3( coord.x - 1.0, coord.y, 1 ) ).r;

	} else {

		gl_FragDepth = texture( depthColor, vec3( coord.x, coord.y, 0 ) ).r;

	}

}`;
let gX = class {
  constructor() {
    this.texture = null, this.mesh = null, this.depthNear = 0, this.depthFar = 0;
  }
  init(e, t, i) {
    if (this.texture === null) {
      const n = new Ut(), r = e.properties.get(n);
      r.__webglTexture = t.texture, (t.depthNear != i.depthNear || t.depthFar != i.depthFar) && (this.depthNear = t.depthNear, this.depthFar = t.depthFar), this.texture = n;
    }
  }
  getMesh(e) {
    if (this.texture !== null && this.mesh === null) {
      const t = e.cameras[0].viewport, i = new Jt({
        vertexShader: pX,
        fragmentShader: mX,
        uniforms: {
          depthColor: { value: this.texture },
          depthWidth: { value: t.z },
          depthHeight: { value: t.w }
        }
      });
      this.mesh = new Re(new $n(20, 20), i);
    }
    return this.mesh;
  }
  reset() {
    this.texture = null, this.mesh = null;
  }
  getDepthTexture() {
    return this.texture;
  }
}, vX = class extends ra {
  constructor(e, t) {
    super();
    const i = this;
    let n = null, r = 1, a = null, o = "local-floor", l = 1, c = null, h = null, u = null, d = null, f = null, p = null;
    const v = new gX(), g = t.getContextAttributes();
    let m = null, x = null;
    const y = [], A = [], b = new Se();
    let M = null;
    const w = new Zt();
    w.layers.enable(1), w.viewport = new Yt();
    const C = new Zt();
    C.layers.enable(2), C.viewport = new Yt();
    const T = [w, C], E = new nI();
    E.layers.enable(1), E.layers.enable(2);
    let P = null, L = null;
    this.cameraAutoUpdate = !0, this.enabled = !1, this.isPresenting = !1, this.getController = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new X8(), y[J] = fe), fe.getTargetRaySpace();
    }, this.getControllerGrip = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new X8(), y[J] = fe), fe.getGripSpace();
    }, this.getHand = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new X8(), y[J] = fe), fe.getHandSpace();
    };
    function I(J) {
      const fe = A.indexOf(J.inputSource);
      if (fe === -1)
        return;
      const Me = y[fe];
      Me !== void 0 && (Me.update(J.inputSource, J.frame, c || a), Me.dispatchEvent({ type: J.type, data: J.inputSource }));
    }
    function N() {
      n.removeEventListener("select", I), n.removeEventListener("selectstart", I), n.removeEventListener("selectend", I), n.removeEventListener("squeeze", I), n.removeEventListener("squeezestart", I), n.removeEventListener("squeezeend", I), n.removeEventListener("end", N), n.removeEventListener("inputsourceschange", H);
      for (let J = 0; J < y.length; J++) {
        const fe = A[J];
        fe !== null && (A[J] = null, y[J].disconnect(fe));
      }
      P = null, L = null, v.reset(), e.setRenderTarget(m), f = null, d = null, u = null, n = null, x = null, Pe.stop(), i.isPresenting = !1, e.setPixelRatio(M), e.setSize(b.width, b.height, !1), i.dispatchEvent({ type: "sessionend" });
    }
    this.setFramebufferScaleFactor = function(J) {
      r = J, i.isPresenting === !0 && console.warn("THREE.WebXRManager: Cannot change framebuffer scale while presenting.");
    }, this.setReferenceSpaceType = function(J) {
      o = J, i.isPresenting === !0 && console.warn("THREE.WebXRManager: Cannot change reference space type while presenting.");
    }, this.getReferenceSpace = function() {
      return c || a;
    }, this.setReferenceSpace = function(J) {
      c = J;
    }, this.getBaseLayer = function() {
      return d !== null ? d : f;
    }, this.getBinding = function() {
      return u;
    }, this.getFrame = function() {
      return p;
    }, this.getSession = function() {
      return n;
    }, this.setSession = async function(J) {
      if (n = J, n !== null) {
        if (m = e.getRenderTarget(), n.addEventListener("select", I), n.addEventListener("selectstart", I), n.addEventListener("selectend", I), n.addEventListener("squeeze", I), n.addEventListener("squeezestart", I), n.addEventListener("squeezeend", I), n.addEventListener("end", N), n.addEventListener("inputsourceschange", H), g.xrCompatible !== !0 && await t.makeXRCompatible(), M = e.getPixelRatio(), e.getSize(b), n.renderState.layers === void 0) {
          const fe = {
            antialias: g.antialias,
            alpha: !0,
            depth: g.depth,
            stencil: g.stencil,
            framebufferScaleFactor: r
          };
          f = new XRWebGLLayer(n, t, fe), n.updateRenderState({ baseLayer: f }), e.setPixelRatio(1), e.setSize(f.framebufferWidth, f.framebufferHeight, !1), x = new Xi(
            f.framebufferWidth,
            f.framebufferHeight,
            {
              format: dn,
              type: o0,
              colorSpace: e.outputColorSpace,
              stencilBuffer: g.stencil
            }
          );
        } else {
          let fe = null, Me = null, ye = null;
          g.depth && (ye = g.stencil ? t.DEPTH24_STENCIL8 : t.DEPTH_COMPONENT24, fe = g.stencil ? yl : Au, Me = g.stencil ? vl : gl);
          const Ye = {
            colorFormat: t.RGBA8,
            depthFormat: ye,
            scaleFactor: r
          };
          u = new XRWebGLBinding(n, t), d = u.createProjectionLayer(Ye), n.updateRenderState({ layers: [d] }), e.setPixelRatio(1), e.setSize(d.textureWidth, d.textureHeight, !1), x = new Xi(
            d.textureWidth,
            d.textureHeight,
            {
              format: dn,
              type: o0,
              depthTexture: new r2(d.textureWidth, d.textureHeight, Me, void 0, void 0, void 0, void 0, void 0, void 0, fe),
              stencilBuffer: g.stencil,
              colorSpace: e.outputColorSpace,
              samples: g.antialias ? 4 : 0,
              resolveDepthBuffer: d.ignoreDepthValues === !1
            }
          );
        }
        x.isXRRenderTarget = !0, this.setFoveation(l), c = null, a = await n.requestReferenceSpace(o), Pe.setContext(n), Pe.start(), i.isPresenting = !0, i.dispatchEvent({ type: "sessionstart" });
      }
    }, this.getEnvironmentBlendMode = function() {
      if (n !== null)
        return n.environmentBlendMode;
    }, this.getDepthTexture = function() {
      return v.getDepthTexture();
    };
    function H(J) {
      for (let fe = 0; fe < J.removed.length; fe++) {
        const Me = J.removed[fe], ye = A.indexOf(Me);
        ye >= 0 && (A[ye] = null, y[ye].disconnect(Me));
      }
      for (let fe = 0; fe < J.added.length; fe++) {
        const Me = J.added[fe];
        let ye = A.indexOf(Me);
        if (ye === -1) {
          for (let Je = 0; Je < y.length; Je++)
            if (Je >= A.length) {
              A.push(Me), ye = Je;
              break;
            } else if (A[Je] === null) {
              A[Je] = Me, ye = Je;
              break;
            }
          if (ye === -1)
            break;
        }
        const Ye = y[ye];
        Ye && Ye.connect(Me);
      }
    }
    const U = new _(), W = new _();
    function X(J, fe, Me) {
      U.setFromMatrixPosition(fe.matrixWorld), W.setFromMatrixPosition(Me.matrixWorld);
      const ye = U.distanceTo(W), Ye = fe.projectionMatrix.elements, Je = Me.projectionMatrix.elements, rt = Ye[14] / (Ye[10] - 1), wt = Ye[14] / (Ye[10] + 1), k = (Ye[9] + 1) / Ye[5], Fe = (Ye[9] - 1) / Ye[5], Ge = (Ye[8] - 1) / Ye[0], qe = (Je[8] + 1) / Je[0], Ie = rt * Ge, vt = rt * qe, Ve = ye / (-Ge + qe), Ze = Ve * -Ge;
      fe.matrixWorld.decompose(J.position, J.quaternion, J.scale), J.translateX(Ze), J.translateZ(Ve), J.matrixWorld.compose(J.position, J.quaternion, J.scale), J.matrixWorldInverse.copy(J.matrixWorld).invert();
      const G = rt + Ve, D = wt + Ve, ne = Ie - Ze, ve = vt + (ye - Ze), Y = k * wt / D * G, ee = Fe * wt / D * G;
      J.projectionMatrix.makePerspective(ne, ve, Y, ee, G, D), J.projectionMatrixInverse.copy(J.projectionMatrix).invert();
    }
    function K(J, fe) {
      fe === null ? J.matrixWorld.copy(J.matrix) : J.matrixWorld.multiplyMatrices(fe.matrixWorld, J.matrix), J.matrixWorldInverse.copy(J.matrixWorld).invert();
    }
    this.updateCamera = function(J) {
      if (n === null)
        return;
      v.texture !== null && (J.near = v.depthNear, J.far = v.depthFar), E.near = C.near = w.near = J.near, E.far = C.far = w.far = J.far, (P !== E.near || L !== E.far) && (n.updateRenderState({
        depthNear: E.near,
        depthFar: E.far
      }), P = E.near, L = E.far, w.near = P, w.far = L, C.near = P, C.far = L, w.updateProjectionMatrix(), C.updateProjectionMatrix(), J.updateProjectionMatrix());
      const fe = J.parent, Me = E.cameras;
      K(E, fe);
      for (let ye = 0; ye < Me.length; ye++)
        K(Me[ye], fe);
      Me.length === 2 ? X(E, w, C) : E.projectionMatrix.copy(w.projectionMatrix), te(J, E, fe);
    };
    function te(J, fe, Me) {
      Me === null ? J.matrix.copy(fe.matrixWorld) : (J.matrix.copy(Me.matrixWorld), J.matrix.invert(), J.matrix.multiply(fe.matrixWorld)), J.matrix.decompose(J.position, J.quaternion, J.scale), J.updateMatrixWorld(!0), J.projectionMatrix.copy(fe.projectionMatrix), J.projectionMatrixInverse.copy(fe.projectionMatrixInverse), J.isPerspectiveCamera && (J.fov = np * 2 * Math.atan(1 / J.projectionMatrix.elements[5]), J.zoom = 1);
    }
    this.getCamera = function() {
      return E;
    }, this.getFoveation = function() {
      if (!(d === null && f === null))
        return l;
    }, this.setFoveation = function(J) {
      l = J, d !== null && (d.fixedFoveation = J), f !== null && f.fixedFoveation !== void 0 && (f.fixedFoveation = J);
    }, this.hasDepthSensing = function() {
      return v.texture !== null;
    }, this.getDepthSensingMesh = function() {
      return v.getMesh(E);
    };
    let re = null;
    function be(J, fe) {
      if (h = fe.getViewerPose(c || a), p = fe, h !== null) {
        const Me = h.views;
        f !== null && (e.setRenderTargetFramebuffer(x, f.framebuffer), e.setRenderTarget(x));
        let ye = !1;
        Me.length !== E.cameras.length && (E.cameras.length = 0, ye = !0);
        for (let Je = 0; Je < Me.length; Je++) {
          const rt = Me[Je];
          let wt = null;
          if (f !== null)
            wt = f.getViewport(rt);
          else {
            const Fe = u.getViewSubImage(d, rt);
            wt = Fe.viewport, Je === 0 && (e.setRenderTargetTextures(
              x,
              Fe.colorTexture,
              d.ignoreDepthValues ? void 0 : Fe.depthStencilTexture
            ), e.setRenderTarget(x));
          }
          let k = T[Je];
          k === void 0 && (k = new Zt(), k.layers.enable(Je), k.viewport = new Yt(), T[Je] = k), k.matrix.fromArray(rt.transform.matrix), k.matrix.decompose(k.position, k.quaternion, k.scale), k.projectionMatrix.fromArray(rt.projectionMatrix), k.projectionMatrixInverse.copy(k.projectionMatrix).invert(), k.viewport.set(wt.x, wt.y, wt.width, wt.height), Je === 0 && (E.matrix.copy(k.matrix), E.matrix.decompose(E.position, E.quaternion, E.scale)), ye === !0 && E.cameras.push(k);
        }
        const Ye = n.enabledFeatures;
        if (Ye && Ye.includes("depth-sensing")) {
          const Je = u.getDepthInformation(Me[0]);
          Je && Je.isValid && Je.texture && v.init(e, Je, n.renderState);
        }
      }
      for (let Me = 0; Me < y.length; Me++) {
        const ye = A[Me], Ye = y[Me];
        ye !== null && Ye !== void 0 && Ye.update(ye, fe, c || a);
      }
      re && re(J, fe), fe.detectedPlanes && i.dispatchEvent({ type: "planesdetected", data: fe }), p = null;
    }
    const Pe = new KL();
    Pe.setAnimationLoop(be), this.setAnimationLoop = function(J) {
      re = J;
    }, this.dispose = function() {
    };
  }
};
const yh = /* @__PURE__ */ new Mi(), yX = /* @__PURE__ */ new et();
function xX(s, e) {
  function t(g, m) {
    g.matrixAutoUpdate === !0 && g.updateMatrix(), m.value.copy(g.matrix);
  }
  function i(g, m) {
    m.color.getRGB(g.fogColor.value, ZL(s)), m.isFog ? (g.fogNear.value = m.near, g.fogFar.value = m.far) : m.isFogExp2 && (g.fogDensity.value = m.density);
  }
  function n(g, m, x, y, A) {
    m.isMeshBasicMaterial || m.isMeshLambertMaterial ? r(g, m) : m.isMeshToonMaterial ? (r(g, m), u(g, m)) : m.isMeshPhongMaterial ? (r(g, m), h(g, m)) : m.isMeshStandardMaterial ? (r(g, m), d(g, m), m.isMeshPhysicalMaterial && f(g, m, A)) : m.isMeshMatcapMaterial ? (r(g, m), p(g, m)) : m.isMeshDepthMaterial ? r(g, m) : m.isMeshDistanceMaterial ? (r(g, m), v(g, m)) : m.isMeshNormalMaterial ? r(g, m) : m.isLineBasicMaterial ? (a(g, m), m.isLineDashedMaterial && o(g, m)) : m.isPointsMaterial ? l(g, m, x, y) : m.isSpriteMaterial ? c(g, m) : m.isShadowMaterial ? (g.color.value.copy(m.color), g.opacity.value = m.opacity) : m.isShaderMaterial && (m.uniformsNeedUpdate = !1);
  }
  function r(g, m) {
    g.opacity.value = m.opacity, m.color && g.diffuse.value.copy(m.color), m.emissive && g.emissive.value.copy(m.emissive).multiplyScalar(m.emissiveIntensity), m.map && (g.map.value = m.map, t(m.map, g.mapTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.bumpMap && (g.bumpMap.value = m.bumpMap, t(m.bumpMap, g.bumpMapTransform), g.bumpScale.value = m.bumpScale, m.side === Ps && (g.bumpScale.value *= -1)), m.normalMap && (g.normalMap.value = m.normalMap, t(m.normalMap, g.normalMapTransform), g.normalScale.value.copy(m.normalScale), m.side === Ps && g.normalScale.value.negate()), m.displacementMap && (g.displacementMap.value = m.displacementMap, t(m.displacementMap, g.displacementMapTransform), g.displacementScale.value = m.displacementScale, g.displacementBias.value = m.displacementBias), m.emissiveMap && (g.emissiveMap.value = m.emissiveMap, t(m.emissiveMap, g.emissiveMapTransform)), m.specularMap && (g.specularMap.value = m.specularMap, t(m.specularMap, g.specularMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
    const x = e.get(m), y = x.envMap, A = x.envMapRotation;
    y && (g.envMap.value = y, yh.copy(A), yh.x *= -1, yh.y *= -1, yh.z *= -1, y.isCubeTexture && y.isRenderTargetTexture === !1 && (yh.y *= -1, yh.z *= -1), g.envMapRotation.value.setFromMatrix4(yX.makeRotationFromEuler(yh)), g.flipEnvMap.value = y.isCubeTexture && y.isRenderTargetTexture === !1 ? -1 : 1, g.reflectivity.value = m.reflectivity, g.ior.value = m.ior, g.refractionRatio.value = m.refractionRatio), m.lightMap && (g.lightMap.value = m.lightMap, g.lightMapIntensity.value = m.lightMapIntensity, t(m.lightMap, g.lightMapTransform)), m.aoMap && (g.aoMap.value = m.aoMap, g.aoMapIntensity.value = m.aoMapIntensity, t(m.aoMap, g.aoMapTransform));
  }
  function a(g, m) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, m.map && (g.map.value = m.map, t(m.map, g.mapTransform));
  }
  function o(g, m) {
    g.dashSize.value = m.dashSize, g.totalSize.value = m.dashSize + m.gapSize, g.scale.value = m.scale;
  }
  function l(g, m, x, y) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, g.size.value = m.size * x, g.scale.value = y * 0.5, m.map && (g.map.value = m.map, t(m.map, g.uvTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
  }
  function c(g, m) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, g.rotation.value = m.rotation, m.map && (g.map.value = m.map, t(m.map, g.mapTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
  }
  function h(g, m) {
    g.specular.value.copy(m.specular), g.shininess.value = Math.max(m.shininess, 1e-4);
  }
  function u(g, m) {
    m.gradientMap && (g.gradientMap.value = m.gradientMap);
  }
  function d(g, m) {
    g.metalness.value = m.metalness, m.metalnessMap && (g.metalnessMap.value = m.metalnessMap, t(m.metalnessMap, g.metalnessMapTransform)), g.roughness.value = m.roughness, m.roughnessMap && (g.roughnessMap.value = m.roughnessMap, t(m.roughnessMap, g.roughnessMapTransform)), m.envMap && (g.envMapIntensity.value = m.envMapIntensity);
  }
  function f(g, m, x) {
    g.ior.value = m.ior, m.sheen > 0 && (g.sheenColor.value.copy(m.sheenColor).multiplyScalar(m.sheen), g.sheenRoughness.value = m.sheenRoughness, m.sheenColorMap && (g.sheenColorMap.value = m.sheenColorMap, t(m.sheenColorMap, g.sheenColorMapTransform)), m.sheenRoughnessMap && (g.sheenRoughnessMap.value = m.sheenRoughnessMap, t(m.sheenRoughnessMap, g.sheenRoughnessMapTransform))), m.clearcoat > 0 && (g.clearcoat.value = m.clearcoat, g.clearcoatRoughness.value = m.clearcoatRoughness, m.clearcoatMap && (g.clearcoatMap.value = m.clearcoatMap, t(m.clearcoatMap, g.clearcoatMapTransform)), m.clearcoatRoughnessMap && (g.clearcoatRoughnessMap.value = m.clearcoatRoughnessMap, t(m.clearcoatRoughnessMap, g.clearcoatRoughnessMapTransform)), m.clearcoatNormalMap && (g.clearcoatNormalMap.value = m.clearcoatNormalMap, t(m.clearcoatNormalMap, g.clearcoatNormalMapTransform), g.clearcoatNormalScale.value.copy(m.clearcoatNormalScale), m.side === Ps && g.clearcoatNormalScale.value.negate())), m.dispersion > 0 && (g.dispersion.value = m.dispersion), m.iridescence > 0 && (g.iridescence.value = m.iridescence, g.iridescenceIOR.value = m.iridescenceIOR, g.iridescenceThicknessMinimum.value = m.iridescenceThicknessRange[0], g.iridescenceThicknessMaximum.value = m.iridescenceThicknessRange[1], m.iridescenceMap && (g.iridescenceMap.value = m.iridescenceMap, t(m.iridescenceMap, g.iridescenceMapTransform)), m.iridescenceThicknessMap && (g.iridescenceThicknessMap.value = m.iridescenceThicknessMap, t(m.iridescenceThicknessMap, g.iridescenceThicknessMapTransform))), m.transmission > 0 && (g.transmission.value = m.transmission, g.transmissionSamplerMap.value = x.texture, g.transmissionSamplerSize.value.set(x.width, x.height), m.transmissionMap && (g.transmissionMap.value = m.transmissionMap, t(m.transmissionMap, g.transmissionMapTransform)), g.thickness.value = m.thickness, m.thicknessMap && (g.thicknessMap.value = m.thicknessMap, t(m.thicknessMap, g.thicknessMapTransform)), g.attenuationDistance.value = m.attenuationDistance, g.attenuationColor.value.copy(m.attenuationColor)), m.anisotropy > 0 && (g.anisotropyVector.value.set(m.anisotropy * Math.cos(m.anisotropyRotation), m.anisotropy * Math.sin(m.anisotropyRotation)), m.anisotropyMap && (g.anisotropyMap.value = m.anisotropyMap, t(m.anisotropyMap, g.anisotropyMapTransform))), g.specularIntensity.value = m.specularIntensity, g.specularColor.value.copy(m.specularColor), m.specularColorMap && (g.specularColorMap.value = m.specularColorMap, t(m.specularColorMap, g.specularColorMapTransform)), m.specularIntensityMap && (g.specularIntensityMap.value = m.specularIntensityMap, t(m.specularIntensityMap, g.specularIntensityMapTransform));
  }
  function p(g, m) {
    m.matcap && (g.matcap.value = m.matcap);
  }
  function v(g, m) {
    const x = e.get(m).light;
    g.referencePosition.value.setFromMatrixPosition(x.matrixWorld), g.nearDistance.value = x.shadow.camera.near, g.farDistance.value = x.shadow.camera.far;
  }
  return {
    refreshFogUniforms: i,
    refreshMaterialUniforms: n
  };
}
function AX(s, e, t, i) {
  let n = {}, r = {}, a = [];
  const o = s.getParameter(s.MAX_UNIFORM_BUFFER_BINDINGS);
  function l(x, y) {
    const A = y.program;
    i.uniformBlockBinding(x, A);
  }
  function c(x, y) {
    let A = n[x.id];
    A === void 0 && (p(x), A = h(x), n[x.id] = A, x.addEventListener("dispose", g));
    const b = y.program;
    i.updateUBOMapping(x, b);
    const M = e.render.frame;
    r[x.id] !== M && (d(x), r[x.id] = M);
  }
  function h(x) {
    const y = u();
    x.__bindingPointIndex = y;
    const A = s.createBuffer(), b = x.__size, M = x.usage;
    return s.bindBuffer(s.UNIFORM_BUFFER, A), s.bufferData(s.UNIFORM_BUFFER, b, M), s.bindBuffer(s.UNIFORM_BUFFER, null), s.bindBufferBase(s.UNIFORM_BUFFER, y, A), A;
  }
  function u() {
    for (let x = 0; x < o; x++)
      if (a.indexOf(x) === -1)
        return a.push(x), x;
    return console.error("THREE.WebGLRenderer: Maximum number of simultaneously usable uniforms groups reached."), 0;
  }
  function d(x) {
    const y = n[x.id], A = x.uniforms, b = x.__cache;
    s.bindBuffer(s.UNIFORM_BUFFER, y);
    for (let M = 0, w = A.length; M < w; M++) {
      const C = Array.isArray(A[M]) ? A[M] : [A[M]];
      for (let T = 0, E = C.length; T < E; T++) {
        const P = C[T];
        if (f(P, M, T, b) === !0) {
          const L = P.__offset, I = Array.isArray(P.value) ? P.value : [P.value];
          let N = 0;
          for (let H = 0; H < I.length; H++) {
            const U = I[H], W = v(U);
            typeof U == "number" || typeof U == "boolean" ? (P.__data[0] = U, s.bufferSubData(s.UNIFORM_BUFFER, L + N, P.__data)) : U.isMatrix3 ? (P.__data[0] = U.elements[0], P.__data[1] = U.elements[1], P.__data[2] = U.elements[2], P.__data[3] = 0, P.__data[4] = U.elements[3], P.__data[5] = U.elements[4], P.__data[6] = U.elements[5], P.__data[7] = 0, P.__data[8] = U.elements[6], P.__data[9] = U.elements[7], P.__data[10] = U.elements[8], P.__data[11] = 0) : (U.toArray(P.__data, N), N += W.storage / Float32Array.BYTES_PER_ELEMENT);
          }
          s.bufferSubData(s.UNIFORM_BUFFER, L, P.__data);
        }
      }
    }
    s.bindBuffer(s.UNIFORM_BUFFER, null);
  }
  function f(x, y, A, b) {
    const M = x.value, w = y + "_" + A;
    if (b[w] === void 0)
      return typeof M == "number" || typeof M == "boolean" ? b[w] = M : b[w] = M.clone(), !0;
    {
      const C = b[w];
      if (typeof M == "number" || typeof M == "boolean") {
        if (C !== M)
          return b[w] = M, !0;
      } else if (C.equals(M) === !1)
        return C.copy(M), !0;
    }
    return !1;
  }
  function p(x) {
    const y = x.uniforms;
    let A = 0;
    const b = 16;
    for (let w = 0, C = y.length; w < C; w++) {
      const T = Array.isArray(y[w]) ? y[w] : [y[w]];
      for (let E = 0, P = T.length; E < P; E++) {
        const L = T[E], I = Array.isArray(L.value) ? L.value : [L.value];
        for (let N = 0, H = I.length; N < H; N++) {
          const U = I[N], W = v(U), X = A % b;
          X !== 0 && b - X < W.boundary && (A += b - X), L.__data = new Float32Array(W.storage / Float32Array.BYTES_PER_ELEMENT), L.__offset = A, A += W.storage;
        }
      }
    }
    const M = A % b;
    return M > 0 && (A += b - M), x.__size = A, x.__cache = {}, this;
  }
  function v(x) {
    const y = {
      boundary: 0,
      // bytes
      storage: 0
      // bytes
    };
    return typeof x == "number" || typeof x == "boolean" ? (y.boundary = 4, y.storage = 4) : x.isVector2 ? (y.boundary = 8, y.storage = 8) : x.isVector3 || x.isColor ? (y.boundary = 16, y.storage = 12) : x.isVector4 ? (y.boundary = 16, y.storage = 16) : x.isMatrix3 ? (y.boundary = 48, y.storage = 48) : x.isMatrix4 ? (y.boundary = 64, y.storage = 64) : x.isTexture ? console.warn("THREE.WebGLRenderer: Texture samplers can not be part of an uniforms group.") : console.warn("THREE.WebGLRenderer: Unsupported uniform value type.", x), y;
  }
  function g(x) {
    const y = x.target;
    y.removeEventListener("dispose", g);
    const A = a.indexOf(y.__bindingPointIndex);
    a.splice(A, 1), s.deleteBuffer(n[y.id]), delete n[y.id], delete r[y.id];
  }
  function m() {
    for (const x in n)
      s.deleteBuffer(n[x]);
    a = [], n = {}, r = {};
  }
  return {
    bind: l,
    update: c,
    dispose: m
  };
}
class HM {
  constructor(e = {}) {
    const {
      canvas: t = kL(),
      context: i = null,
      depth: n = !0,
      stencil: r = !1,
      alpha: a = !1,
      antialias: o = !1,
      premultipliedAlpha: l = !0,
      preserveDrawingBuffer: c = !1,
      powerPreference: h = "default",
      failIfMajorPerformanceCaveat: u = !1
    } = e;
    this.isWebGLRenderer = !0;
    let d;
    if (i !== null) {
      if (typeof WebGLRenderingContext < "u" && i instanceof WebGLRenderingContext)
        throw new Error("THREE.WebGLRenderer: WebGL 1 is not supported since r163.");
      d = i.getContextAttributes().alpha;
    } else
      d = a;
    const f = new Uint32Array(4), p = new Int32Array(4);
    let v = null, g = null;
    const m = [], x = [];
    this.domElement = t, this.debug = {
      /**
       * Enables error checking and reporting when shader programs are being compiled
       * @type {boolean}
       */
      checkShaderErrors: !0,
      /**
       * Callback for custom error reporting.
       * @type {?Function}
       */
      onShaderError: null
    }, this.autoClear = !0, this.autoClearColor = !0, this.autoClearDepth = !0, this.autoClearStencil = !0, this.sortObjects = !0, this.clippingPlanes = [], this.localClippingEnabled = !1, this._outputColorSpace = qi, this.toneMapping = ol, this.toneMappingExposure = 1;
    const y = this;
    let A = !1, b = 0, M = 0, w = null, C = -1, T = null;
    const E = new Yt(), P = new Yt();
    let L = null;
    const I = new _e(0);
    let N = 0, H = t.width, U = t.height, W = 1, X = null, K = null;
    const te = new Yt(0, 0, H, U), re = new Yt(0, 0, H, U);
    let be = !1;
    const Pe = new th();
    let J = !1, fe = !1;
    const Me = new et(), ye = new _(), Ye = new Yt(), Je = { background: null, fog: null, environment: null, overrideMaterial: null, isScene: !0 };
    let rt = !1;
    function wt() {
      return w === null ? W : 1;
    }
    let k = i;
    function Fe(O, Z) {
      return t.getContext(O, Z);
    }
    try {
      const O = {
        alpha: !0,
        depth: n,
        stencil: r,
        antialias: o,
        premultipliedAlpha: l,
        preserveDrawingBuffer: c,
        powerPreference: h,
        failIfMajorPerformanceCaveat: u
      };
      if ("setAttribute" in t && t.setAttribute("data-engine", `three.js r${Qo}`), t.addEventListener("webglcontextlost", Q, !1), t.addEventListener("webglcontextrestored", he, !1), t.addEventListener("webglcontextcreationerror", Be, !1), k === null) {
        const Z = "webgl2";
        if (k = Fe(Z, O), k === null)
          throw Fe(Z) ? new Error("Error creating WebGL context with your selected attributes.") : new Error("Error creating WebGL context.");
      }
    } catch (O) {
      throw console.error("THREE.WebGLRenderer: " + O.message), O;
    }
    let Ge, qe, Ie, vt, Ve, Ze, G, D, ne, ve, Y, ee, we, de, xe, it, q, ae, Ee, Le, Ae, We, ot, Ct;
    function F() {
      Ge = new _V(k), Ge.init(), We = new iI(k, Ge), qe = new MV(k, Ge, e, We), Ie = new aX(k), vt = new DV(k), Ve = new YW(), Ze = new dX(k, Ge, Ie, Ve, qe, We, vt), G = new wV(y), D = new CV(y), ne = new Uk(k), ot = new AV(k, ne), ve = new PV(k, ne, vt, ot), Y = new IV(k, ve, ne, vt), Ee = new LV(k, qe, Ze), it = new SV(Ve), ee = new qW(y, G, D, Ge, qe, ot, it), we = new xX(y, Ve), de = new KW(), xe = new iX(Ge), ae = new xV(y, G, D, Ie, Y, d, l), q = new rX(y, Y, qe), Ct = new AX(k, vt, qe, Ie), Le = new bV(k, Ge, vt), Ae = new RV(k, Ge, vt), vt.programs = ee.programs, y.capabilities = qe, y.extensions = Ge, y.properties = Ve, y.renderLists = de, y.shadowMap = q, y.state = Ie, y.info = vt;
    }
    F();
    const ue = new vX(y, k);
    this.xr = ue, this.getContext = function() {
      return k;
    }, this.getContextAttributes = function() {
      return k.getContextAttributes();
    }, this.forceContextLoss = function() {
      const O = Ge.get("WEBGL_lose_context");
      O && O.loseContext();
    }, this.forceContextRestore = function() {
      const O = Ge.get("WEBGL_lose_context");
      O && O.restoreContext();
    }, this.getPixelRatio = function() {
      return W;
    }, this.setPixelRatio = function(O) {
      O !== void 0 && (W = O, this.setSize(H, U, !1));
    }, this.getSize = function(O) {
      return O.set(H, U);
    }, this.setSize = function(O, Z, R = !0) {
      if (ue.isPresenting) {
        console.warn("THREE.WebGLRenderer: Can't change size while VR device is presenting.");
        return;
      }
      H = O, U = Z, t.width = Math.floor(O * W), t.height = Math.floor(Z * W), R === !0 && (t.style.width = O + "px", t.style.height = Z + "px"), this.setViewport(0, 0, O, Z);
    }, this.getDrawingBufferSize = function(O) {
      return O.set(H * W, U * W).floor();
    }, this.setDrawingBufferSize = function(O, Z, R) {
      H = O, U = Z, W = R, t.width = Math.floor(O * R), t.height = Math.floor(Z * R), this.setViewport(0, 0, O, Z);
    }, this.getCurrentViewport = function(O) {
      return O.copy(E);
    }, this.getViewport = function(O) {
      return O.copy(te);
    }, this.setViewport = function(O, Z, R, B) {
      O.isVector4 ? te.set(O.x, O.y, O.z, O.w) : te.set(O, Z, R, B), Ie.viewport(E.copy(te).multiplyScalar(W).round());
    }, this.getScissor = function(O) {
      return O.copy(re);
    }, this.setScissor = function(O, Z, R, B) {
      O.isVector4 ? re.set(O.x, O.y, O.z, O.w) : re.set(O, Z, R, B), Ie.scissor(P.copy(re).multiplyScalar(W).round());
    }, this.getScissorTest = function() {
      return be;
    }, this.setScissorTest = function(O) {
      Ie.setScissorTest(be = O);
    }, this.setOpaqueSort = function(O) {
      X = O;
    }, this.setTransparentSort = function(O) {
      K = O;
    }, this.getClearColor = function(O) {
      return O.copy(ae.getClearColor());
    }, this.setClearColor = function() {
      ae.setClearColor.apply(ae, arguments);
    }, this.getClearAlpha = function() {
      return ae.getClearAlpha();
    }, this.setClearAlpha = function() {
      ae.setClearAlpha.apply(ae, arguments);
    }, this.clear = function(O = !0, Z = !0, R = !0) {
      let B = 0;
      if (O) {
        let z = !1;
        if (w !== null) {
          const $ = w.texture.format;
          z = $ === y4 || $ === v4 || $ === t2;
        }
        if (z) {
          const $ = w.texture.type, ce = $ === o0 || $ === gl || $ === tp || $ === vl || $ === m4 || $ === g4, se = ae.getClearColor(), pe = ae.getClearAlpha(), Ne = se.r, De = se.g, Te = se.b;
          ce ? (f[0] = Ne, f[1] = De, f[2] = Te, f[3] = pe, k.clearBufferuiv(k.COLOR, 0, f)) : (p[0] = Ne, p[1] = De, p[2] = Te, p[3] = pe, k.clearBufferiv(k.COLOR, 0, p));
        } else
          B |= k.COLOR_BUFFER_BIT;
      }
      Z && (B |= k.DEPTH_BUFFER_BIT), R && (B |= k.STENCIL_BUFFER_BIT, this.state.buffers.stencil.setMask(4294967295)), k.clear(B);
    }, this.clearColor = function() {
      this.clear(!0, !1, !1);
    }, this.clearDepth = function() {
      this.clear(!1, !0, !1);
    }, this.clearStencil = function() {
      this.clear(!1, !1, !0);
    }, this.dispose = function() {
      t.removeEventListener("webglcontextlost", Q, !1), t.removeEventListener("webglcontextrestored", he, !1), t.removeEventListener("webglcontextcreationerror", Be, !1), de.dispose(), xe.dispose(), Ve.dispose(), G.dispose(), D.dispose(), Y.dispose(), ot.dispose(), Ct.dispose(), ee.dispose(), ue.dispose(), ue.removeEventListener("sessionstart", Li), ue.removeEventListener("sessionend", cr), sn.stop();
    };
    function Q(O) {
      O.preventDefault(), console.log("THREE.WebGLRenderer: Context Lost."), A = !0;
    }
    function he() {
      console.log("THREE.WebGLRenderer: Context Restored."), A = !1;
      const O = vt.autoReset, Z = q.enabled, R = q.autoUpdate, B = q.needsUpdate, z = q.type;
      F(), vt.autoReset = O, q.enabled = Z, q.autoUpdate = R, q.needsUpdate = B, q.type = z;
    }
    function Be(O) {
      console.error("THREE.WebGLRenderer: A WebGL context could not be created. Reason: ", O.statusMessage);
    }
    function ft(O) {
      const Z = O.target;
      Z.removeEventListener("dispose", ft), Et(Z);
    }
    function Et(O) {
      ei(O), Ve.remove(O);
    }
    function ei(O) {
      const Z = Ve.get(O).programs;
      Z !== void 0 && (Z.forEach(function(R) {
        ee.releaseProgram(R);
      }), O.isShaderMaterial && ee.releaseShaderCache(O));
    }
    this.renderBufferDirect = function(O, Z, R, B, z, $) {
      Z === null && (Z = Je);
      const ce = z.isMesh && z.matrixWorld.determinant() < 0, se = Ll(O, Z, R, B, z);
      Ie.setMaterial(B, ce);
      let pe = R.index, Ne = 1;
      if (B.wireframe === !0) {
        if (pe = ve.getWireframeAttribute(R), pe === void 0)
          return;
        Ne = 2;
      }
      const De = R.drawRange, Te = R.attributes.position;
      let Ue = De.start * Ne, at = (De.start + De.count) * Ne;
      $ !== null && (Ue = Math.max(Ue, $.start * Ne), at = Math.min(at, ($.start + $.count) * Ne)), pe !== null ? (Ue = Math.max(Ue, 0), at = Math.min(at, pe.count)) : Te != null && (Ue = Math.max(Ue, 0), at = Math.min(at, Te.count));
      const ht = at - Ue;
      if (ht < 0 || ht === 1 / 0)
        return;
      ot.setup(z, B, se, R, pe);
      let At, lt = Le;
      if (pe !== null && (At = ne.get(pe), lt = Ae, lt.setIndex(At)), z.isMesh)
        B.wireframe === !0 ? (Ie.setLineWidth(B.wireframeLinewidth * wt()), lt.setMode(k.LINES)) : lt.setMode(k.TRIANGLES);
      else if (z.isLine) {
        let Oe = B.linewidth;
        Oe === void 0 && (Oe = 1), Ie.setLineWidth(Oe * wt()), z.isLineSegments ? lt.setMode(k.LINES) : z.isLineLoop ? lt.setMode(k.LINE_LOOP) : lt.setMode(k.LINE_STRIP);
      } else
        z.isPoints ? lt.setMode(k.POINTS) : z.isSprite && lt.setMode(k.TRIANGLES);
      if (z.isBatchedMesh)
        if (z._multiDrawInstances !== null)
          lt.renderMultiDrawInstances(z._multiDrawStarts, z._multiDrawCounts, z._multiDrawCount, z._multiDrawInstances);
        else if (Ge.get("WEBGL_multi_draw"))
          lt.renderMultiDraw(z._multiDrawStarts, z._multiDrawCounts, z._multiDrawCount);
        else {
          const Oe = z._multiDrawStarts, yt = z._multiDrawCounts, V = z._multiDrawCount, le = pe ? ne.get(pe).bytesPerElement : 1, ie = Ve.get(B).currentProgram.getUniforms();
          for (let ge = 0; ge < V; ge++)
            ie.setValue(k, "_gl_DrawID", ge), lt.render(Oe[ge] / le, yt[ge]);
        }
      else if (z.isInstancedMesh)
        lt.renderInstances(Ue, ht, z.count);
      else if (R.isInstancedBufferGeometry) {
        const Oe = R._maxInstanceCount !== void 0 ? R._maxInstanceCount : 1 / 0, yt = Math.min(R.instanceCount, Oe);
        lt.renderInstances(Ue, ht, yt);
      } else
        lt.render(Ue, ht);
    };
    function di(O, Z, R) {
      O.transparent === !0 && O.side === ui && O.forceSinglePass === !1 ? (O.side = Ps, O.needsUpdate = !0, Gr(O, Z, R), O.side = sa, O.needsUpdate = !0, Gr(O, Z, R), O.side = ui) : Gr(O, Z, R);
    }
    this.compile = function(O, Z, R = null) {
      R === null && (R = O), g = xe.get(R), g.init(Z), x.push(g), R.traverseVisible(function(z) {
        z.isLight && z.layers.test(Z.layers) && (g.pushLight(z), z.castShadow && g.pushShadow(z));
      }), O !== R && O.traverseVisible(function(z) {
        z.isLight && z.layers.test(Z.layers) && (g.pushLight(z), z.castShadow && g.pushShadow(z));
      }), g.setupLights();
      const B = /* @__PURE__ */ new Set();
      return O.traverse(function(z) {
        const $ = z.material;
        if ($)
          if (Array.isArray($))
            for (let ce = 0; ce < $.length; ce++) {
              const se = $[ce];
              di(se, R, z), B.add(se);
            }
          else
            di($, R, z), B.add($);
      }), x.pop(), g = null, B;
    }, this.compileAsync = function(O, Z, R = null) {
      const B = this.compile(O, Z, R);
      return new Promise((z) => {
        function $() {
          if (B.forEach(function(ce) {
            Ve.get(ce).currentProgram.isReady() && B.delete(ce);
          }), B.size === 0) {
            z(O);
            return;
          }
          setTimeout($, 10);
        }
        Ge.get("KHR_parallel_shader_compile") !== null ? $() : setTimeout($, 10);
      });
    };
    let Ot = null;
    function Wi(O) {
      Ot && Ot(O);
    }
    function Li() {
      sn.stop();
    }
    function cr() {
      sn.start();
    }
    const sn = new KL();
    sn.setAnimationLoop(Wi), typeof self < "u" && sn.setContext(self), this.setAnimationLoop = function(O) {
      Ot = O, ue.setAnimationLoop(O), O === null ? sn.stop() : sn.start();
    }, ue.addEventListener("sessionstart", Li), ue.addEventListener("sessionend", cr), this.render = function(O, Z) {
      if (Z !== void 0 && Z.isCamera !== !0) {
        console.error("THREE.WebGLRenderer.render: camera is not an instance of THREE.Camera.");
        return;
      }
      if (A === !0)
        return;
      if (O.matrixWorldAutoUpdate === !0 && O.updateMatrixWorld(), Z.parent === null && Z.matrixWorldAutoUpdate === !0 && Z.updateMatrixWorld(), ue.enabled === !0 && ue.isPresenting === !0 && (ue.cameraAutoUpdate === !0 && ue.updateCamera(Z), Z = ue.getCamera()), O.isScene === !0 && O.onBeforeRender(y, O, Z, w), g = xe.get(O, x.length), g.init(Z), x.push(g), Me.multiplyMatrices(Z.projectionMatrix, Z.matrixWorldInverse), Pe.setFromProjectionMatrix(Me), fe = this.localClippingEnabled, J = it.init(this.clippingPlanes, fe), v = de.get(O, m.length), v.init(), m.push(v), ue.enabled === !0 && ue.isPresenting === !0) {
        const $ = y.xr.getDepthSensingMesh();
        $ !== null && Os($, Z, -1 / 0, y.sortObjects);
      }
      Os(O, Z, 0, y.sortObjects), v.finish(), y.sortObjects === !0 && v.sort(X, K), rt = ue.enabled === !1 || ue.isPresenting === !1 || ue.hasDepthSensing() === !1, rt && ae.addToRenderList(v, O), this.info.render.frame++, J === !0 && it.beginShadows();
      const R = g.state.shadowsArray;
      q.render(R, O, Z), J === !0 && it.endShadows(), this.info.autoReset === !0 && this.info.reset();
      const B = v.opaque, z = v.transmissive;
      if (g.setupLights(), Z.isArrayCamera) {
        const $ = Z.cameras;
        if (z.length > 0)
          for (let ce = 0, se = $.length; ce < se; ce++) {
            const pe = $[ce];
            kr(B, z, O, pe);
          }
        rt && ae.render(O);
        for (let ce = 0, se = $.length; ce < se; ce++) {
          const pe = $[ce];
          br(v, O, pe, pe.viewport);
        }
      } else
        z.length > 0 && kr(B, z, O, Z), rt && ae.render(O), br(v, O, Z);
      w !== null && (Ze.updateMultisampleRenderTarget(w), Ze.updateRenderTargetMipmap(w)), O.isScene === !0 && O.onAfterRender(y, O, Z), ot.resetDefaultState(), C = -1, T = null, x.pop(), x.length > 0 ? (g = x[x.length - 1], J === !0 && it.setGlobalState(y.clippingPlanes, g.state.camera)) : g = null, m.pop(), m.length > 0 ? v = m[m.length - 1] : v = null;
    };
    function Os(O, Z, R, B) {
      if (O.visible === !1)
        return;
      if (O.layers.test(Z.layers)) {
        if (O.isGroup)
          R = O.renderOrder;
        else if (O.isLOD)
          O.autoUpdate === !0 && O.update(Z);
        else if (O.isLight)
          g.pushLight(O), O.castShadow && g.pushShadow(O);
        else if (O.isSprite) {
          if (!O.frustumCulled || Pe.intersectsSprite(O)) {
            B && Ye.setFromMatrixPosition(O.matrixWorld).applyMatrix4(Me);
            const ce = Y.update(O), se = O.material;
            se.visible && v.push(O, ce, se, R, Ye.z, null);
          }
        } else if ((O.isMesh || O.isLine || O.isPoints) && (!O.frustumCulled || Pe.intersectsObject(O))) {
          const ce = Y.update(O), se = O.material;
          if (B && (O.boundingSphere !== void 0 ? (O.boundingSphere === null && O.computeBoundingSphere(), Ye.copy(O.boundingSphere.center)) : (ce.boundingSphere === null && ce.computeBoundingSphere(), Ye.copy(ce.boundingSphere.center)), Ye.applyMatrix4(O.matrixWorld).applyMatrix4(Me)), Array.isArray(se)) {
            const pe = ce.groups;
            for (let Ne = 0, De = pe.length; Ne < De; Ne++) {
              const Te = pe[Ne], Ue = se[Te.materialIndex];
              Ue && Ue.visible && v.push(O, ce, Ue, R, Ye.z, Te);
            }
          } else
            se.visible && v.push(O, ce, se, R, Ye.z, null);
        }
      }
      const $ = O.children;
      for (let ce = 0, se = $.length; ce < se; ce++)
        Os($[ce], Z, R, B);
    }
    function br(O, Z, R, B) {
      const z = O.opaque, $ = O.transmissive, ce = O.transparent;
      g.setupLightsView(R), J === !0 && it.setGlobalState(y.clippingPlanes, R), B && Ie.viewport(E.copy(B)), z.length > 0 && Mr(z, Z, R), $.length > 0 && Mr($, Z, R), ce.length > 0 && Mr(ce, Z, R), Ie.buffers.depth.setTest(!0), Ie.buffers.depth.setMask(!0), Ie.buffers.color.setMask(!0), Ie.setPolygonOffset(!1);
    }
    function kr(O, Z, R, B) {
      if ((R.isScene === !0 ? R.overrideMaterial : null) !== null)
        return;
      g.state.transmissionRenderTarget[B.id] === void 0 && (g.state.transmissionRenderTarget[B.id] = new Xi(1, 1, {
        generateMipmaps: !0,
        type: Ge.has("EXT_color_buffer_half_float") || Ge.has("EXT_color_buffer_float") ? on : o0,
        minFilter: ds,
        samples: 4,
        stencilBuffer: r,
        resolveDepthBuffer: !1,
        resolveStencilBuffer: !1,
        colorSpace: bi.workingColorSpace
      }));
      const $ = g.state.transmissionRenderTarget[B.id], ce = B.viewport || E;
      $.setSize(ce.z, ce.w);
      const se = y.getRenderTarget();
      y.setRenderTarget($), y.getClearColor(I), N = y.getClearAlpha(), N < 1 && y.setClearColor(16777215, 0.5), rt ? ae.render(R) : y.clear();
      const pe = y.toneMapping;
      y.toneMapping = ol;
      const Ne = B.viewport;
      if (B.viewport !== void 0 && (B.viewport = void 0), g.setupLightsView(B), J === !0 && it.setGlobalState(y.clippingPlanes, B), Mr(O, R, B), Ze.updateMultisampleRenderTarget($), Ze.updateRenderTargetMipmap($), Ge.has("WEBGL_multisampled_render_to_texture") === !1) {
        let De = !1;
        for (let Te = 0, Ue = Z.length; Te < Ue; Te++) {
          const at = Z[Te], ht = at.object, At = at.geometry, lt = at.material, Oe = at.group;
          if (lt.side === ui && ht.layers.test(B.layers)) {
            const yt = lt.side;
            lt.side = Ps, lt.needsUpdate = !0, A0(ht, R, B, At, lt, Oe), lt.side = yt, lt.needsUpdate = !0, De = !0;
          }
        }
        De === !0 && (Ze.updateMultisampleRenderTarget($), Ze.updateRenderTargetMipmap($));
      }
      y.setRenderTarget(se), y.setClearColor(I, N), Ne !== void 0 && (B.viewport = Ne), y.toneMapping = pe;
    }
    function Mr(O, Z, R) {
      const B = Z.isScene === !0 ? Z.overrideMaterial : null;
      for (let z = 0, $ = O.length; z < $; z++) {
        const ce = O[z], se = ce.object, pe = ce.geometry, Ne = B === null ? ce.material : B, De = ce.group;
        se.layers.test(R.layers) && A0(se, Z, R, pe, Ne, De);
      }
    }
    function A0(O, Z, R, B, z, $) {
      O.onBeforeRender(y, Z, R, B, z, $), O.modelViewMatrix.multiplyMatrices(R.matrixWorldInverse, O.matrixWorld), O.normalMatrix.getNormalMatrix(O.modelViewMatrix), z.transparent === !0 && z.side === ui && z.forceSinglePass === !1 ? (z.side = Ps, z.needsUpdate = !0, y.renderBufferDirect(R, Z, B, z, O, $), z.side = sa, z.needsUpdate = !0, y.renderBufferDirect(R, Z, B, z, O, $), z.side = ui) : y.renderBufferDirect(R, Z, B, z, O, $), O.onAfterRender(y, Z, R, B, z, $);
    }
    function Gr(O, Z, R) {
      Z.isScene !== !0 && (Z = Je);
      const B = Ve.get(O), z = g.state.lights, $ = g.state.shadowsArray, ce = z.state.version, se = ee.getParameters(O, z.state, $, Z, R), pe = ee.getProgramCacheKey(se);
      let Ne = B.programs;
      B.environment = O.isMeshStandardMaterial ? Z.environment : null, B.fog = Z.fog, B.envMap = (O.isMeshStandardMaterial ? D : G).get(O.envMap || B.environment), B.envMapRotation = B.environment !== null && O.envMap === null ? Z.environmentRotation : O.envMapRotation, Ne === void 0 && (O.addEventListener("dispose", ft), Ne = /* @__PURE__ */ new Map(), B.programs = Ne);
      let De = Ne.get(pe);
      if (De !== void 0) {
        if (B.currentProgram === De && B.lightsStateVersion === ce)
          return M0(O, se), De;
      } else
        se.uniforms = ee.getUniforms(O), O.onBeforeCompile(se, y), De = ee.acquireProgram(se, pe), Ne.set(pe, De), B.uniforms = se.uniforms;
      const Te = B.uniforms;
      return (!O.isShaderMaterial && !O.isRawShaderMaterial || O.clipping === !0) && (Te.clippingPlanes = it.uniform), M0(O, se), B.needsLights = dh(O), B.lightsStateVersion = ce, B.needsLights && (Te.ambientLightColor.value = z.state.ambient, Te.lightProbe.value = z.state.probe, Te.directionalLights.value = z.state.directional, Te.directionalLightShadows.value = z.state.directionalShadow, Te.spotLights.value = z.state.spot, Te.spotLightShadows.value = z.state.spotShadow, Te.rectAreaLights.value = z.state.rectArea, Te.ltc_1.value = z.state.rectAreaLTC1, Te.ltc_2.value = z.state.rectAreaLTC2, Te.pointLights.value = z.state.point, Te.pointLightShadows.value = z.state.pointShadow, Te.hemisphereLights.value = z.state.hemi, Te.directionalShadowMap.value = z.state.directionalShadowMap, Te.directionalShadowMatrix.value = z.state.directionalShadowMatrix, Te.spotShadowMap.value = z.state.spotShadowMap, Te.spotLightMatrix.value = z.state.spotLightMatrix, Te.spotLightMap.value = z.state.spotLightMap, Te.pointShadowMap.value = z.state.pointShadowMap, Te.pointShadowMatrix.value = z.state.pointShadowMatrix), B.currentProgram = De, B.uniformsList = null, De;
    }
    function b0(O) {
      if (O.uniformsList === null) {
        const Z = O.currentProgram.getUniforms();
        O.uniformsList = Ty.seqWithValue(Z.seq, O.uniforms);
      }
      return O.uniformsList;
    }
    function M0(O, Z) {
      const R = Ve.get(O);
      R.outputColorSpace = Z.outputColorSpace, R.batching = Z.batching, R.batchingColor = Z.batchingColor, R.instancing = Z.instancing, R.instancingColor = Z.instancingColor, R.instancingMorph = Z.instancingMorph, R.skinning = Z.skinning, R.morphTargets = Z.morphTargets, R.morphNormals = Z.morphNormals, R.morphColors = Z.morphColors, R.morphTargetsCount = Z.morphTargetsCount, R.numClippingPlanes = Z.numClippingPlanes, R.numIntersection = Z.numClipIntersection, R.vertexAlphas = Z.vertexAlphas, R.vertexTangents = Z.vertexTangents, R.toneMapping = Z.toneMapping;
    }
    function Ll(O, Z, R, B, z) {
      Z.isScene !== !0 && (Z = Je), Ze.resetTextureUnits();
      const $ = Z.fog, ce = B.isMeshStandardMaterial ? Z.environment : null, se = w === null ? y.outputColorSpace : w.isXRRenderTarget === !0 ? w.texture.colorSpace : Si, pe = (B.isMeshStandardMaterial ? D : G).get(B.envMap || ce), Ne = B.vertexColors === !0 && !!R.attributes.color && R.attributes.color.itemSize === 4, De = !!R.attributes.tangent && (!!B.normalMap || B.anisotropy > 0), Te = !!R.morphAttributes.position, Ue = !!R.morphAttributes.normal, at = !!R.morphAttributes.color;
      let ht = ol;
      B.toneMapped && (w === null || w.isXRRenderTarget === !0) && (ht = y.toneMapping);
      const At = R.morphAttributes.position || R.morphAttributes.normal || R.morphAttributes.color, lt = At !== void 0 ? At.length : 0, Oe = Ve.get(B), yt = g.state.lights;
      if (J === !0 && (fe === !0 || O !== T)) {
        const Ke = O === T && B.id === C;
        it.setState(B, O, Ke);
      }
      let V = !1;
      B.version === Oe.__version ? (Oe.needsLights && Oe.lightsStateVersion !== yt.state.version || Oe.outputColorSpace !== se || z.isBatchedMesh && Oe.batching === !1 || !z.isBatchedMesh && Oe.batching === !0 || z.isBatchedMesh && Oe.batchingColor === !0 && z.colorTexture === null || z.isBatchedMesh && Oe.batchingColor === !1 && z.colorTexture !== null || z.isInstancedMesh && Oe.instancing === !1 || !z.isInstancedMesh && Oe.instancing === !0 || z.isSkinnedMesh && Oe.skinning === !1 || !z.isSkinnedMesh && Oe.skinning === !0 || z.isInstancedMesh && Oe.instancingColor === !0 && z.instanceColor === null || z.isInstancedMesh && Oe.instancingColor === !1 && z.instanceColor !== null || z.isInstancedMesh && Oe.instancingMorph === !0 && z.morphTexture === null || z.isInstancedMesh && Oe.instancingMorph === !1 && z.morphTexture !== null || Oe.envMap !== pe || B.fog === !0 && Oe.fog !== $ || Oe.numClippingPlanes !== void 0 && (Oe.numClippingPlanes !== it.numPlanes || Oe.numIntersection !== it.numIntersection) || Oe.vertexAlphas !== Ne || Oe.vertexTangents !== De || Oe.morphTargets !== Te || Oe.morphNormals !== Ue || Oe.morphColors !== at || Oe.toneMapping !== ht || Oe.morphTargetsCount !== lt) && (V = !0) : (V = !0, Oe.__version = B.version);
      let le = Oe.currentProgram;
      V === !0 && (le = Gr(B, Z, z));
      let ie = !1, ge = !1, Ce = !1;
      const oe = le.getUniforms(), He = Oe.uniforms;
      if (Ie.useProgram(le.program) && (ie = !0, ge = !0, Ce = !0), B.id !== C && (C = B.id, ge = !0), ie || T !== O) {
        oe.setValue(k, "projectionMatrix", O.projectionMatrix), oe.setValue(k, "viewMatrix", O.matrixWorldInverse);
        const Ke = oe.map.cameraPosition;
        Ke !== void 0 && Ke.setValue(k, ye.setFromMatrixPosition(O.matrixWorld)), qe.logarithmicDepthBuffer && oe.setValue(
          k,
          "logDepthBufFC",
          2 / (Math.log(O.far + 1) / Math.LN2)
        ), (B.isMeshPhongMaterial || B.isMeshToonMaterial || B.isMeshLambertMaterial || B.isMeshBasicMaterial || B.isMeshStandardMaterial || B.isShaderMaterial) && oe.setValue(k, "isOrthographic", O.isOrthographicCamera === !0), T !== O && (T = O, ge = !0, Ce = !0);
      }
      if (z.isSkinnedMesh) {
        oe.setOptional(k, z, "bindMatrix"), oe.setOptional(k, z, "bindMatrixInverse");
        const Ke = z.skeleton;
        Ke && (Ke.boneTexture === null && Ke.computeBoneTexture(), oe.setValue(k, "boneTexture", Ke.boneTexture, Ze));
      }
      z.isBatchedMesh && (oe.setOptional(k, z, "batchingTexture"), oe.setValue(k, "batchingTexture", z._matricesTexture, Ze), oe.setOptional(k, z, "batchingIdTexture"), oe.setValue(k, "batchingIdTexture", z._indirectTexture, Ze), oe.setOptional(k, z, "batchingColorTexture"), z._colorsTexture !== null && oe.setValue(k, "batchingColorTexture", z._colorsTexture, Ze));
      const ct = R.morphAttributes;
      if ((ct.position !== void 0 || ct.normal !== void 0 || ct.color !== void 0) && Ee.update(z, R, le), (ge || Oe.receiveShadow !== z.receiveShadow) && (Oe.receiveShadow = z.receiveShadow, oe.setValue(k, "receiveShadow", z.receiveShadow)), B.isMeshGouraudMaterial && B.envMap !== null && (He.envMap.value = pe, He.flipEnvMap.value = pe.isCubeTexture && pe.isRenderTargetTexture === !1 ? -1 : 1), B.isMeshStandardMaterial && B.envMap === null && Z.environment !== null && (He.envMapIntensity.value = Z.environmentIntensity), ge && (oe.setValue(k, "toneMappingExposure", y.toneMappingExposure), Oe.needsLights && Il(He, Ce), $ && B.fog === !0 && we.refreshFogUniforms(He, $), we.refreshMaterialUniforms(He, B, W, U, g.state.transmissionRenderTarget[O.id]), Ty.upload(k, b0(Oe), He, Ze)), B.isShaderMaterial && B.uniformsNeedUpdate === !0 && (Ty.upload(k, b0(Oe), He, Ze), B.uniformsNeedUpdate = !1), B.isSpriteMaterial && oe.setValue(k, "center", z.center), oe.setValue(k, "modelViewMatrix", z.modelViewMatrix), oe.setValue(k, "normalMatrix", z.normalMatrix), oe.setValue(k, "modelMatrix", z.matrixWorld), B.isShaderMaterial || B.isRawShaderMaterial) {
        const Ke = B.uniformsGroups;
        for (let tt = 0, ut = Ke.length; tt < ut; tt++) {
          const _t = Ke[tt];
          Ct.update(_t, le), Ct.bind(_t, le);
        }
      }
      return le;
    }
    function Il(O, Z) {
      O.ambientLightColor.needsUpdate = Z, O.lightProbe.needsUpdate = Z, O.directionalLights.needsUpdate = Z, O.directionalLightShadows.needsUpdate = Z, O.pointLights.needsUpdate = Z, O.pointLightShadows.needsUpdate = Z, O.spotLights.needsUpdate = Z, O.spotLightShadows.needsUpdate = Z, O.rectAreaLights.needsUpdate = Z, O.hemisphereLights.needsUpdate = Z;
    }
    function dh(O) {
      return O.isMeshLambertMaterial || O.isMeshToonMaterial || O.isMeshPhongMaterial || O.isMeshStandardMaterial || O.isShadowMaterial || O.isShaderMaterial && O.lights === !0;
    }
    this.getActiveCubeFace = function() {
      return b;
    }, this.getActiveMipmapLevel = function() {
      return M;
    }, this.getRenderTarget = function() {
      return w;
    }, this.setRenderTargetTextures = function(O, Z, R) {
      Ve.get(O.texture).__webglTexture = Z, Ve.get(O.depthTexture).__webglTexture = R;
      const B = Ve.get(O);
      B.__hasExternalTextures = !0, B.__autoAllocateDepthBuffer = R === void 0, B.__autoAllocateDepthBuffer || Ge.has("WEBGL_multisampled_render_to_texture") === !0 && (console.warn("THREE.WebGLRenderer: Render-to-texture extension was disabled because an external texture was provided"), B.__useRenderToTexture = !1);
    }, this.setRenderTargetFramebuffer = function(O, Z) {
      const R = Ve.get(O);
      R.__webglFramebuffer = Z, R.__useDefaultFramebuffer = Z === void 0;
    }, this.setRenderTarget = function(O, Z = 0, R = 0) {
      w = O, b = Z, M = R;
      let B = !0, z = null, $ = !1, ce = !1;
      if (O) {
        const pe = Ve.get(O);
        pe.__useDefaultFramebuffer !== void 0 ? (Ie.bindFramebuffer(k.FRAMEBUFFER, null), B = !1) : pe.__webglFramebuffer === void 0 ? Ze.setupRenderTarget(O) : pe.__hasExternalTextures && Ze.rebindTextures(O, Ve.get(O.texture).__webglTexture, Ve.get(O.depthTexture).__webglTexture);
        const Ne = O.texture;
        (Ne.isData3DTexture || Ne.isDataArrayTexture || Ne.isCompressedArrayTexture) && (ce = !0);
        const De = Ve.get(O).__webglFramebuffer;
        O.isWebGLCubeRenderTarget ? (Array.isArray(De[Z]) ? z = De[Z][R] : z = De[Z], $ = !0) : O.samples > 0 && Ze.useMultisampledRTT(O) === !1 ? z = Ve.get(O).__webglMultisampledFramebuffer : Array.isArray(De) ? z = De[R] : z = De, E.copy(O.viewport), P.copy(O.scissor), L = O.scissorTest;
      } else
        E.copy(te).multiplyScalar(W).floor(), P.copy(re).multiplyScalar(W).floor(), L = be;
      if (Ie.bindFramebuffer(k.FRAMEBUFFER, z) && B && Ie.drawBuffers(O, z), Ie.viewport(E), Ie.scissor(P), Ie.setScissorTest(L), $) {
        const pe = Ve.get(O.texture);
        k.framebufferTexture2D(k.FRAMEBUFFER, k.COLOR_ATTACHMENT0, k.TEXTURE_CUBE_MAP_POSITIVE_X + Z, pe.__webglTexture, R);
      } else if (ce) {
        const pe = Ve.get(O.texture), Ne = Z || 0;
        k.framebufferTextureLayer(k.FRAMEBUFFER, k.COLOR_ATTACHMENT0, pe.__webglTexture, R || 0, Ne);
      }
      C = -1;
    }, this.readRenderTargetPixels = function(O, Z, R, B, z, $, ce) {
      if (!(O && O.isWebGLRenderTarget)) {
        console.error("THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.");
        return;
      }
      let se = Ve.get(O).__webglFramebuffer;
      if (O.isWebGLCubeRenderTarget && ce !== void 0 && (se = se[ce]), se) {
        Ie.bindFramebuffer(k.FRAMEBUFFER, se);
        try {
          const pe = O.texture, Ne = pe.format, De = pe.type;
          if (!qe.textureFormatReadable(Ne)) {
            console.error("THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in RGBA or implementation defined format.");
            return;
          }
          if (!qe.textureTypeReadable(De)) {
            console.error("THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in UnsignedByteType or implementation defined type.");
            return;
          }
          Z >= 0 && Z <= O.width - B && R >= 0 && R <= O.height - z && k.readPixels(Z, R, B, z, We.convert(Ne), We.convert(De), $);
        } finally {
          const pe = w !== null ? Ve.get(w).__webglFramebuffer : null;
          Ie.bindFramebuffer(k.FRAMEBUFFER, pe);
        }
      }
    }, this.readRenderTargetPixelsAsync = async function(O, Z, R, B, z, $, ce) {
      if (!(O && O.isWebGLRenderTarget))
        throw new Error("THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.");
      let se = Ve.get(O).__webglFramebuffer;
      if (O.isWebGLCubeRenderTarget && ce !== void 0 && (se = se[ce]), se) {
        Ie.bindFramebuffer(k.FRAMEBUFFER, se);
        try {
          const pe = O.texture, Ne = pe.format, De = pe.type;
          if (!qe.textureFormatReadable(Ne))
            throw new Error("THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in RGBA or implementation defined format.");
          if (!qe.textureTypeReadable(De))
            throw new Error("THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in UnsignedByteType or implementation defined type.");
          if (Z >= 0 && Z <= O.width - B && R >= 0 && R <= O.height - z) {
            const Te = k.createBuffer();
            k.bindBuffer(k.PIXEL_PACK_BUFFER, Te), k.bufferData(k.PIXEL_PACK_BUFFER, $.byteLength, k.STREAM_READ), k.readPixels(Z, R, B, z, We.convert(Ne), We.convert(De), 0), k.flush();
            const Ue = k.fenceSync(k.SYNC_GPU_COMMANDS_COMPLETE, 0);
            await hk(k, Ue, 4);
            try {
              k.bindBuffer(k.PIXEL_PACK_BUFFER, Te), k.getBufferSubData(k.PIXEL_PACK_BUFFER, 0, $);
            } finally {
              k.deleteBuffer(Te), k.deleteSync(Ue);
            }
            return $;
          }
        } finally {
          const pe = w !== null ? Ve.get(w).__webglFramebuffer : null;
          Ie.bindFramebuffer(k.FRAMEBUFFER, pe);
        }
      }
    }, this.copyFramebufferToTexture = function(O, Z = null, R = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyFramebufferToTexture function signature has changed."), Z = arguments[0] || null, O = arguments[1]);
      const B = Math.pow(2, -R), z = Math.floor(O.image.width * B), $ = Math.floor(O.image.height * B), ce = Z !== null ? Z.x : 0, se = Z !== null ? Z.y : 0;
      Ze.setTexture2D(O, 0), k.copyTexSubImage2D(k.TEXTURE_2D, R, 0, 0, ce, se, z, $), Ie.unbindTexture();
    }, this.copyTextureToTexture = function(O, Z, R = null, B = null, z = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyTextureToTexture function signature has changed."), B = arguments[0] || null, O = arguments[1], Z = arguments[2], z = arguments[3] || 0, R = null);
      let $, ce, se, pe, Ne, De;
      R !== null ? ($ = R.max.x - R.min.x, ce = R.max.y - R.min.y, se = R.min.x, pe = R.min.y) : ($ = O.image.width, ce = O.image.height, se = 0, pe = 0), B !== null ? (Ne = B.x, De = B.y) : (Ne = 0, De = 0);
      const Te = We.convert(Z.format), Ue = We.convert(Z.type);
      Ze.setTexture2D(Z, 0), k.pixelStorei(k.UNPACK_FLIP_Y_WEBGL, Z.flipY), k.pixelStorei(k.UNPACK_PREMULTIPLY_ALPHA_WEBGL, Z.premultiplyAlpha), k.pixelStorei(k.UNPACK_ALIGNMENT, Z.unpackAlignment);
      const at = k.getParameter(k.UNPACK_ROW_LENGTH), ht = k.getParameter(k.UNPACK_IMAGE_HEIGHT), At = k.getParameter(k.UNPACK_SKIP_PIXELS), lt = k.getParameter(k.UNPACK_SKIP_ROWS), Oe = k.getParameter(k.UNPACK_SKIP_IMAGES), yt = O.isCompressedTexture ? O.mipmaps[z] : O.image;
      k.pixelStorei(k.UNPACK_ROW_LENGTH, yt.width), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, yt.height), k.pixelStorei(k.UNPACK_SKIP_PIXELS, se), k.pixelStorei(k.UNPACK_SKIP_ROWS, pe), O.isDataTexture ? k.texSubImage2D(k.TEXTURE_2D, z, Ne, De, $, ce, Te, Ue, yt.data) : O.isCompressedTexture ? k.compressedTexSubImage2D(k.TEXTURE_2D, z, Ne, De, yt.width, yt.height, Te, yt.data) : k.texSubImage2D(k.TEXTURE_2D, z, Ne, De, $, ce, Te, Ue, yt), k.pixelStorei(k.UNPACK_ROW_LENGTH, at), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, ht), k.pixelStorei(k.UNPACK_SKIP_PIXELS, At), k.pixelStorei(k.UNPACK_SKIP_ROWS, lt), k.pixelStorei(k.UNPACK_SKIP_IMAGES, Oe), z === 0 && Z.generateMipmaps && k.generateMipmap(k.TEXTURE_2D), Ie.unbindTexture();
    }, this.copyTextureToTexture3D = function(O, Z, R = null, B = null, z = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyTextureToTexture3D function signature has changed."), R = arguments[0] || null, B = arguments[1] || null, O = arguments[2], Z = arguments[3], z = arguments[4] || 0);
      let $, ce, se, pe, Ne, De, Te, Ue, at;
      const ht = O.isCompressedTexture ? O.mipmaps[z] : O.image;
      R !== null ? ($ = R.max.x - R.min.x, ce = R.max.y - R.min.y, se = R.max.z - R.min.z, pe = R.min.x, Ne = R.min.y, De = R.min.z) : ($ = ht.width, ce = ht.height, se = ht.depth, pe = 0, Ne = 0, De = 0), B !== null ? (Te = B.x, Ue = B.y, at = B.z) : (Te = 0, Ue = 0, at = 0);
      const At = We.convert(Z.format), lt = We.convert(Z.type);
      let Oe;
      if (Z.isData3DTexture)
        Ze.setTexture3D(Z, 0), Oe = k.TEXTURE_3D;
      else if (Z.isDataArrayTexture || Z.isCompressedArrayTexture)
        Ze.setTexture2DArray(Z, 0), Oe = k.TEXTURE_2D_ARRAY;
      else {
        console.warn("THREE.WebGLRenderer.copyTextureToTexture3D: only supports THREE.DataTexture3D and THREE.DataTexture2DArray.");
        return;
      }
      k.pixelStorei(k.UNPACK_FLIP_Y_WEBGL, Z.flipY), k.pixelStorei(k.UNPACK_PREMULTIPLY_ALPHA_WEBGL, Z.premultiplyAlpha), k.pixelStorei(k.UNPACK_ALIGNMENT, Z.unpackAlignment);
      const yt = k.getParameter(k.UNPACK_ROW_LENGTH), V = k.getParameter(k.UNPACK_IMAGE_HEIGHT), le = k.getParameter(k.UNPACK_SKIP_PIXELS), ie = k.getParameter(k.UNPACK_SKIP_ROWS), ge = k.getParameter(k.UNPACK_SKIP_IMAGES);
      k.pixelStorei(k.UNPACK_ROW_LENGTH, ht.width), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, ht.height), k.pixelStorei(k.UNPACK_SKIP_PIXELS, pe), k.pixelStorei(k.UNPACK_SKIP_ROWS, Ne), k.pixelStorei(k.UNPACK_SKIP_IMAGES, De), O.isDataTexture || O.isData3DTexture ? k.texSubImage3D(Oe, z, Te, Ue, at, $, ce, se, At, lt, ht.data) : Z.isCompressedArrayTexture ? k.compressedTexSubImage3D(Oe, z, Te, Ue, at, $, ce, se, At, ht.data) : k.texSubImage3D(Oe, z, Te, Ue, at, $, ce, se, At, lt, ht), k.pixelStorei(k.UNPACK_ROW_LENGTH, yt), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, V), k.pixelStorei(k.UNPACK_SKIP_PIXELS, le), k.pixelStorei(k.UNPACK_SKIP_ROWS, ie), k.pixelStorei(k.UNPACK_SKIP_IMAGES, ge), z === 0 && Z.generateMipmaps && k.generateMipmap(Oe), Ie.unbindTexture();
    }, this.initRenderTarget = function(O) {
      Ve.get(O).__webglFramebuffer === void 0 && Ze.setupRenderTarget(O);
    }, this.initTexture = function(O) {
      O.isCubeTexture ? Ze.setTextureCube(O, 0) : O.isData3DTexture ? Ze.setTexture3D(O, 0) : O.isDataArrayTexture || O.isCompressedArrayTexture ? Ze.setTexture2DArray(O, 0) : Ze.setTexture2D(O, 0), Ie.unbindTexture();
    }, this.resetState = function() {
      b = 0, M = 0, w = null, Ie.reset(), ot.reset();
    }, typeof __THREE_DEVTOOLS__ < "u" && __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent("observe", { detail: this }));
  }
  get coordinateSystem() {
    return Vo;
  }
  get outputColorSpace() {
    return this._outputColorSpace;
  }
  set outputColorSpace(e) {
    this._outputColorSpace = e;
    const t = this.getContext();
    t.drawingBufferColorSpace = e === A4 ? "display-p3" : "srgb", t.unpackColorSpace = bi.workingColorSpace === n2 ? "display-p3" : "srgb";
  }
}
class Xp {
  constructor(e, t = 25e-5) {
    this.isFogExp2 = !0, this.name = "", this.color = new _e(e), this.density = t;
  }
  clone() {
    return new Xp(this.color, this.density);
  }
  toJSON() {
    return {
      type: "FogExp2",
      name: this.name,
      color: this.color.getHex(),
      density: this.density
    };
  }
}
class Wu {
  constructor(e, t = 1, i = 1e3) {
    this.isFog = !0, this.name = "", this.color = new _e(e), this.near = t, this.far = i;
  }
  clone() {
    return new Wu(this.color, this.near, this.far);
  }
  toJSON() {
    return {
      type: "Fog",
      name: this.name,
      color: this.color.getHex(),
      near: this.near,
      far: this.far
    };
  }
}
class Da extends mt {
  constructor() {
    super(), this.isScene = !0, this.type = "Scene", this.background = null, this.environment = null, this.fog = null, this.backgroundBlurriness = 0, this.backgroundIntensity = 1, this.backgroundRotation = new Mi(), this.environmentIntensity = 1, this.environmentRotation = new Mi(), this.overrideMaterial = null, typeof __THREE_DEVTOOLS__ < "u" && __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent("observe", { detail: this }));
  }
  copy(e, t) {
    return super.copy(e, t), e.background !== null && (this.background = e.background.clone()), e.environment !== null && (this.environment = e.environment.clone()), e.fog !== null && (this.fog = e.fog.clone()), this.backgroundBlurriness = e.backgroundBlurriness, this.backgroundIntensity = e.backgroundIntensity, this.backgroundRotation.copy(e.backgroundRotation), this.environmentIntensity = e.environmentIntensity, this.environmentRotation.copy(e.environmentRotation), e.overrideMaterial !== null && (this.overrideMaterial = e.overrideMaterial.clone()), this.matrixAutoUpdate = e.matrixAutoUpdate, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return this.fog !== null && (t.object.fog = this.fog.toJSON()), this.backgroundBlurriness > 0 && (t.object.backgroundBlurriness = this.backgroundBlurriness), this.backgroundIntensity !== 1 && (t.object.backgroundIntensity = this.backgroundIntensity), t.object.backgroundRotation = this.backgroundRotation.toArray(), this.environmentIntensity !== 1 && (t.object.environmentIntensity = this.environmentIntensity), t.object.environmentRotation = this.environmentRotation.toArray(), t;
  }
}
class Xu {
  constructor(e, t) {
    this.isInterleavedBuffer = !0, this.array = e, this.stride = t, this.count = e !== void 0 ? e.length / t : 0, this.usage = J1, this._updateRange = { offset: 0, count: -1 }, this.updateRanges = [], this.version = 0, this.uuid = ea();
  }
  onUploadCallback() {
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  get updateRange() {
    return NM("THREE.InterleavedBuffer: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead."), this._updateRange;
  }
  setUsage(e) {
    return this.usage = e, this;
  }
  addUpdateRange(e, t) {
    this.updateRanges.push({ start: e, count: t });
  }
  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }
  copy(e) {
    return this.array = new e.array.constructor(e.array), this.count = e.count, this.stride = e.stride, this.usage = e.usage, this;
  }
  copyAt(e, t, i) {
    e *= this.stride, i *= t.stride;
    for (let n = 0, r = this.stride; n < r; n++)
      this.array[e + n] = t.array[i + n];
    return this;
  }
  set(e, t = 0) {
    return this.array.set(e, t), this;
  }
  clone(e) {
    e.arrayBuffers === void 0 && (e.arrayBuffers = {}), this.array.buffer._uuid === void 0 && (this.array.buffer._uuid = ea()), e.arrayBuffers[this.array.buffer._uuid] === void 0 && (e.arrayBuffers[this.array.buffer._uuid] = this.array.slice(0).buffer);
    const t = new this.array.constructor(e.arrayBuffers[this.array.buffer._uuid]), i = new this.constructor(t, this.stride);
    return i.setUsage(this.usage), i;
  }
  onUpload(e) {
    return this.onUploadCallback = e, this;
  }
  toJSON(e) {
    return e.arrayBuffers === void 0 && (e.arrayBuffers = {}), this.array.buffer._uuid === void 0 && (this.array.buffer._uuid = ea()), e.arrayBuffers[this.array.buffer._uuid] === void 0 && (e.arrayBuffers[this.array.buffer._uuid] = Array.from(new Uint32Array(this.array.buffer))), {
      uuid: this.uuid,
      buffer: this.array.buffer._uuid,
      type: this.array.constructor.name,
      stride: this.stride
    };
  }
}
const hr = /* @__PURE__ */ new _();
class c0 {
  constructor(e, t, i, n = !1) {
    this.isInterleavedBufferAttribute = !0, this.name = "", this.data = e, this.itemSize = t, this.offset = i, this.normalized = n;
  }
  get count() {
    return this.data.count;
  }
  get array() {
    return this.data.array;
  }
  set needsUpdate(e) {
    this.data.needsUpdate = e;
  }
  applyMatrix4(e) {
    for (let t = 0, i = this.data.count; t < i; t++)
      hr.fromBufferAttribute(this, t), hr.applyMatrix4(e), this.setXYZ(t, hr.x, hr.y, hr.z);
    return this;
  }
  applyNormalMatrix(e) {
    for (let t = 0, i = this.count; t < i; t++)
      hr.fromBufferAttribute(this, t), hr.applyNormalMatrix(e), this.setXYZ(t, hr.x, hr.y, hr.z);
    return this;
  }
  transformDirection(e) {
    for (let t = 0, i = this.count; t < i; t++)
      hr.fromBufferAttribute(this, t), hr.transformDirection(e), this.setXYZ(t, hr.x, hr.y, hr.z);
    return this;
  }
  getComponent(e, t) {
    let i = this.array[e * this.data.stride + this.offset + t];
    return this.normalized && (i = vr(i, this.array)), i;
  }
  setComponent(e, t, i) {
    return this.normalized && (i = hi(i, this.array)), this.data.array[e * this.data.stride + this.offset + t] = i, this;
  }
  setX(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.data.array[e * this.data.stride + this.offset] = t, this;
  }
  setY(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.data.array[e * this.data.stride + this.offset + 1] = t, this;
  }
  setZ(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.data.array[e * this.data.stride + this.offset + 2] = t, this;
  }
  setW(e, t) {
    return this.normalized && (t = hi(t, this.array)), this.data.array[e * this.data.stride + this.offset + 3] = t, this;
  }
  getX(e) {
    let t = this.data.array[e * this.data.stride + this.offset];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  getY(e) {
    let t = this.data.array[e * this.data.stride + this.offset + 1];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  getZ(e) {
    let t = this.data.array[e * this.data.stride + this.offset + 2];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  getW(e) {
    let t = this.data.array[e * this.data.stride + this.offset + 3];
    return this.normalized && (t = vr(t, this.array)), t;
  }
  setXY(e, t, i) {
    return e = e * this.data.stride + this.offset, this.normalized && (t = hi(t, this.array), i = hi(i, this.array)), this.data.array[e + 0] = t, this.data.array[e + 1] = i, this;
  }
  setXYZ(e, t, i, n) {
    return e = e * this.data.stride + this.offset, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array)), this.data.array[e + 0] = t, this.data.array[e + 1] = i, this.data.array[e + 2] = n, this;
  }
  setXYZW(e, t, i, n, r) {
    return e = e * this.data.stride + this.offset, this.normalized && (t = hi(t, this.array), i = hi(i, this.array), n = hi(n, this.array), r = hi(r, this.array)), this.data.array[e + 0] = t, this.data.array[e + 1] = i, this.data.array[e + 2] = n, this.data.array[e + 3] = r, this;
  }
  clone(e) {
    if (e === void 0) {
      console.log("THREE.InterleavedBufferAttribute.clone(): Cloning an interleaved buffer attribute will de-interleave buffer data.");
      const t = [];
      for (let i = 0; i < this.count; i++) {
        const n = i * this.data.stride + this.offset;
        for (let r = 0; r < this.itemSize; r++)
          t.push(this.data.array[n + r]);
      }
      return new Ht(new this.array.constructor(t), this.itemSize, this.normalized);
    } else
      return e.interleavedBuffers === void 0 && (e.interleavedBuffers = {}), e.interleavedBuffers[this.data.uuid] === void 0 && (e.interleavedBuffers[this.data.uuid] = this.data.clone(e)), new c0(e.interleavedBuffers[this.data.uuid], this.itemSize, this.offset, this.normalized);
  }
  toJSON(e) {
    if (e === void 0) {
      console.log("THREE.InterleavedBufferAttribute.toJSON(): Serializing an interleaved buffer attribute will de-interleave buffer data.");
      const t = [];
      for (let i = 0; i < this.count; i++) {
        const n = i * this.data.stride + this.offset;
        for (let r = 0; r < this.itemSize; r++)
          t.push(this.data.array[n + r]);
      }
      return {
        itemSize: this.itemSize,
        type: this.array.constructor.name,
        array: t,
        normalized: this.normalized
      };
    } else
      return e.interleavedBuffers === void 0 && (e.interleavedBuffers = {}), e.interleavedBuffers[this.data.uuid] === void 0 && (e.interleavedBuffers[this.data.uuid] = this.data.toJSON(e)), {
        isInterleavedBufferAttribute: !0,
        itemSize: this.itemSize,
        data: this.data.uuid,
        offset: this.offset,
        normalized: this.normalized
      };
  }
}
class ar extends Cn {
  constructor(e) {
    super(), this.isSpriteMaterial = !0, this.type = "SpriteMaterial", this.color = new _e(16777215), this.map = null, this.alphaMap = null, this.rotation = 0, this.sizeAttenuation = !0, this.transparent = !0, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.alphaMap = e.alphaMap, this.rotation = e.rotation, this.sizeAttenuation = e.sizeAttenuation, this.fog = e.fog, this;
  }
}
let Ad;
const um = /* @__PURE__ */ new _(), bd = /* @__PURE__ */ new _(), Md = /* @__PURE__ */ new _(), Sd = /* @__PURE__ */ new Se(), dm = /* @__PURE__ */ new Se(), sI = /* @__PURE__ */ new et(), mv = /* @__PURE__ */ new _(), fm = /* @__PURE__ */ new _(), gv = /* @__PURE__ */ new _(), DE = /* @__PURE__ */ new Se(), j8 = /* @__PURE__ */ new Se(), LE = /* @__PURE__ */ new Se();
class wn extends mt {
  constructor(e = new ar()) {
    if (super(), this.isSprite = !0, this.type = "Sprite", Ad === void 0) {
      Ad = new Pt();
      const t = new Float32Array([
        -0.5,
        -0.5,
        0,
        0,
        0,
        0.5,
        -0.5,
        0,
        1,
        0,
        0.5,
        0.5,
        0,
        1,
        1,
        -0.5,
        0.5,
        0,
        0,
        1
      ]), i = new Xu(t, 5);
      Ad.setIndex([0, 1, 2, 0, 2, 3]), Ad.setAttribute("position", new c0(i, 3, 0, !1)), Ad.setAttribute("uv", new c0(i, 2, 3, !1));
    }
    this.geometry = Ad, this.material = e, this.center = new Se(0.5, 0.5);
  }
  raycast(e, t) {
    e.camera === null && console.error('THREE.Sprite: "Raycaster.camera" needs to be set in order to raycast against sprites.'), bd.setFromMatrixScale(this.matrixWorld), sI.copy(e.camera.matrixWorld), this.modelViewMatrix.multiplyMatrices(e.camera.matrixWorldInverse, this.matrixWorld), Md.setFromMatrixPosition(this.modelViewMatrix), e.camera.isPerspectiveCamera && this.material.sizeAttenuation === !1 && bd.multiplyScalar(-Md.z);
    const i = this.material.rotation;
    let n, r;
    i !== 0 && (r = Math.cos(i), n = Math.sin(i));
    const a = this.center;
    vv(mv.set(-0.5, -0.5, 0), Md, a, bd, n, r), vv(fm.set(0.5, -0.5, 0), Md, a, bd, n, r), vv(gv.set(0.5, 0.5, 0), Md, a, bd, n, r), DE.set(0, 0), j8.set(1, 0), LE.set(1, 1);
    let o = e.ray.intersectTriangle(mv, fm, gv, !1, um);
    if (o === null && (vv(fm.set(-0.5, 0.5, 0), Md, a, bd, n, r), j8.set(0, 1), o = e.ray.intersectTriangle(mv, gv, fm, !1, um), o === null))
      return;
    const l = e.ray.origin.distanceTo(um);
    l < e.near || l > e.far || t.push({
      distance: l,
      point: um.clone(),
      uv: Wo.getInterpolation(um, mv, fm, gv, DE, j8, LE, new Se()),
      face: null,
      object: this
    });
  }
  copy(e, t) {
    return super.copy(e, t), e.center !== void 0 && this.center.copy(e.center), this.material = e.material, this;
  }
}
function vv(s, e, t, i, n, r) {
  Sd.subVectors(s, t).addScalar(0.5).multiply(i), n !== void 0 ? (dm.x = r * Sd.x - n * Sd.y, dm.y = n * Sd.x + r * Sd.y) : dm.copy(Sd), s.copy(e), s.x += dm.x, s.y += dm.y, s.applyMatrix4(sI);
}
const yv = /* @__PURE__ */ new _(), IE = /* @__PURE__ */ new _();
class E4 extends mt {
  constructor() {
    super(), this._currentLevel = 0, this.type = "LOD", Object.defineProperties(this, {
      levels: {
        enumerable: !0,
        value: []
      },
      isLOD: {
        value: !0
      }
    }), this.autoUpdate = !0;
  }
  copy(e) {
    super.copy(e, !1);
    const t = e.levels;
    for (let i = 0, n = t.length; i < n; i++) {
      const r = t[i];
      this.addLevel(r.object.clone(), r.distance, r.hysteresis);
    }
    return this.autoUpdate = e.autoUpdate, this;
  }
  addLevel(e, t = 0, i = 0) {
    t = Math.abs(t);
    const n = this.levels;
    let r;
    for (r = 0; r < n.length && !(t < n[r].distance); r++)
      ;
    return n.splice(r, 0, { distance: t, hysteresis: i, object: e }), this.add(e), this;
  }
  getCurrentLevel() {
    return this._currentLevel;
  }
  getObjectForDistance(e) {
    const t = this.levels;
    if (t.length > 0) {
      let i, n;
      for (i = 1, n = t.length; i < n; i++) {
        let r = t[i].distance;
        if (t[i].object.visible && (r -= r * t[i].hysteresis), e < r)
          break;
      }
      return t[i - 1].object;
    }
    return null;
  }
  raycast(e, t) {
    if (this.levels.length > 0) {
      yv.setFromMatrixPosition(this.matrixWorld);
      const n = e.ray.origin.distanceTo(yv);
      this.getObjectForDistance(n).raycast(e, t);
    }
  }
  update(e) {
    const t = this.levels;
    if (t.length > 1) {
      yv.setFromMatrixPosition(e.matrixWorld), IE.setFromMatrixPosition(this.matrixWorld);
      const i = yv.distanceTo(IE) / e.zoom;
      t[0].object.visible = !0;
      let n, r;
      for (n = 1, r = t.length; n < r; n++) {
        let a = t[n].distance;
        if (t[n].object.visible && (a -= a * t[n].hysteresis), i >= a)
          t[n - 1].object.visible = !1, t[n].object.visible = !0;
        else
          break;
      }
      for (this._currentLevel = n - 1; n < r; n++)
        t[n].object.visible = !1;
    }
  }
  toJSON(e) {
    const t = super.toJSON(e);
    this.autoUpdate === !1 && (t.object.autoUpdate = !1), t.object.levels = [];
    const i = this.levels;
    for (let n = 0, r = i.length; n < r; n++) {
      const a = i[n];
      t.object.levels.push({
        object: a.object.uuid,
        distance: a.distance,
        hysteresis: a.hysteresis
      });
    }
    return t;
  }
}
const OE = /* @__PURE__ */ new _(), NE = /* @__PURE__ */ new Yt(), BE = /* @__PURE__ */ new Yt(), bX = /* @__PURE__ */ new _(), FE = /* @__PURE__ */ new et(), xv = /* @__PURE__ */ new _(), q8 = /* @__PURE__ */ new Tn(), UE = /* @__PURE__ */ new et(), Y8 = /* @__PURE__ */ new g0();
class a2 extends Re {
  constructor(e, t) {
    super(e, t), this.isSkinnedMesh = !0, this.type = "SkinnedMesh", this.bindMode = H6, this.bindMatrix = new et(), this.bindMatrixInverse = new et(), this.boundingBox = null, this.boundingSphere = null;
  }
  computeBoundingBox() {
    const e = this.geometry;
    this.boundingBox === null && (this.boundingBox = new zt()), this.boundingBox.makeEmpty();
    const t = e.getAttribute("position");
    for (let i = 0; i < t.count; i++)
      this.getVertexPosition(i, xv), this.boundingBox.expandByPoint(xv);
  }
  computeBoundingSphere() {
    const e = this.geometry;
    this.boundingSphere === null && (this.boundingSphere = new Tn()), this.boundingSphere.makeEmpty();
    const t = e.getAttribute("position");
    for (let i = 0; i < t.count; i++)
      this.getVertexPosition(i, xv), this.boundingSphere.expandByPoint(xv);
  }
  copy(e, t) {
    return super.copy(e, t), this.bindMode = e.bindMode, this.bindMatrix.copy(e.bindMatrix), this.bindMatrixInverse.copy(e.bindMatrixInverse), this.skeleton = e.skeleton, e.boundingBox !== null && (this.boundingBox = e.boundingBox.clone()), e.boundingSphere !== null && (this.boundingSphere = e.boundingSphere.clone()), this;
  }
  raycast(e, t) {
    const i = this.material, n = this.matrixWorld;
    i !== void 0 && (this.boundingSphere === null && this.computeBoundingSphere(), q8.copy(this.boundingSphere), q8.applyMatrix4(n), e.ray.intersectsSphere(q8) !== !1 && (UE.copy(n).invert(), Y8.copy(e.ray).applyMatrix4(UE), !(this.boundingBox !== null && Y8.intersectsBox(this.boundingBox) === !1) && this._computeIntersections(e, t, Y8)));
  }
  getVertexPosition(e, t) {
    return super.getVertexPosition(e, t), this.applyBoneTransform(e, t), t;
  }
  bind(e, t) {
    this.skeleton = e, t === void 0 && (this.updateMatrixWorld(!0), this.skeleton.calculateInverses(), t = this.matrixWorld), this.bindMatrix.copy(t), this.bindMatrixInverse.copy(t).invert();
  }
  pose() {
    this.skeleton.pose();
  }
  normalizeSkinWeights() {
    const e = new Yt(), t = this.geometry.attributes.skinWeight;
    for (let i = 0, n = t.count; i < n; i++) {
      e.fromBufferAttribute(t, i);
      const r = 1 / e.manhattanLength();
      r !== 1 / 0 ? e.multiplyScalar(r) : e.set(1, 0, 0, 0), t.setXYZW(i, e.x, e.y, e.z, e.w);
    }
  }
  updateMatrixWorld(e) {
    super.updateMatrixWorld(e), this.bindMode === H6 ? this.bindMatrixInverse.copy(this.matrixWorld).invert() : this.bindMode === EL ? this.bindMatrixInverse.copy(this.bindMatrix).invert() : console.warn("THREE.SkinnedMesh: Unrecognized bindMode: " + this.bindMode);
  }
  applyBoneTransform(e, t) {
    const i = this.skeleton, n = this.geometry;
    NE.fromBufferAttribute(n.attributes.skinIndex, e), BE.fromBufferAttribute(n.attributes.skinWeight, e), OE.copy(t).applyMatrix4(this.bindMatrix), t.set(0, 0, 0);
    for (let r = 0; r < 4; r++) {
      const a = BE.getComponent(r);
      if (a !== 0) {
        const o = NE.getComponent(r);
        FE.multiplyMatrices(i.bones[o].matrixWorld, i.boneInverses[o]), t.addScaledVector(bX.copy(OE).applyMatrix4(FE), a);
      }
    }
    return t.applyMatrix4(this.bindMatrixInverse);
  }
}
class ih extends mt {
  constructor() {
    super(), this.isBone = !0, this.type = "Bone";
  }
}
class Qn extends Ut {
  constructor(e = null, t = 1, i = 1, n, r, a, o, l, c = Ni, h = Ni, u, d) {
    super(null, a, o, l, c, h, n, r, u, d), this.isDataTexture = !0, this.image = { data: e, width: t, height: i }, this.generateMipmaps = !1, this.flipY = !1, this.unpackAlignment = 1;
  }
}
const zE = /* @__PURE__ */ new et(), MX = /* @__PURE__ */ new et();
class nh {
  constructor(e = [], t = []) {
    this.uuid = ea(), this.bones = e.slice(0), this.boneInverses = t, this.boneMatrices = null, this.boneTexture = null, this.init();
  }
  init() {
    const e = this.bones, t = this.boneInverses;
    if (this.boneMatrices = new Float32Array(e.length * 16), t.length === 0)
      this.calculateInverses();
    else if (e.length !== t.length) {
      console.warn("THREE.Skeleton: Number of inverse bone matrices does not match amount of bones."), this.boneInverses = [];
      for (let i = 0, n = this.bones.length; i < n; i++)
        this.boneInverses.push(new et());
    }
  }
  calculateInverses() {
    this.boneInverses.length = 0;
    for (let e = 0, t = this.bones.length; e < t; e++) {
      const i = new et();
      this.bones[e] && i.copy(this.bones[e].matrixWorld).invert(), this.boneInverses.push(i);
    }
  }
  pose() {
    for (let e = 0, t = this.bones.length; e < t; e++) {
      const i = this.bones[e];
      i && i.matrixWorld.copy(this.boneInverses[e]).invert();
    }
    for (let e = 0, t = this.bones.length; e < t; e++) {
      const i = this.bones[e];
      i && (i.parent && i.parent.isBone ? (i.matrix.copy(i.parent.matrixWorld).invert(), i.matrix.multiply(i.matrixWorld)) : i.matrix.copy(i.matrixWorld), i.matrix.decompose(i.position, i.quaternion, i.scale));
    }
  }
  update() {
    const e = this.bones, t = this.boneInverses, i = this.boneMatrices, n = this.boneTexture;
    for (let r = 0, a = e.length; r < a; r++) {
      const o = e[r] ? e[r].matrixWorld : MX;
      zE.multiplyMatrices(o, t[r]), zE.toArray(i, r * 16);
    }
    n !== null && (n.needsUpdate = !0);
  }
  clone() {
    return new nh(this.bones, this.boneInverses);
  }
  computeBoneTexture() {
    let e = Math.sqrt(this.bones.length * 4);
    e = Math.ceil(e / 4) * 4, e = Math.max(e, 4);
    const t = new Float32Array(e * e * 4);
    t.set(this.boneMatrices);
    const i = new Qn(t, e, e, dn, xn);
    return i.needsUpdate = !0, this.boneMatrices = t, this.boneTexture = i, this;
  }
  getBoneByName(e) {
    for (let t = 0, i = this.bones.length; t < i; t++) {
      const n = this.bones[t];
      if (n.name === e)
        return n;
    }
  }
  dispose() {
    this.boneTexture !== null && (this.boneTexture.dispose(), this.boneTexture = null);
  }
  fromJSON(e, t) {
    this.uuid = e.uuid;
    for (let i = 0, n = e.bones.length; i < n; i++) {
      const r = e.bones[i];
      let a = t[r];
      a === void 0 && (console.warn("THREE.Skeleton: No bone found with UUID:", r), a = new ih()), this.bones.push(a), this.boneInverses.push(new et().fromArray(e.boneInverses[i]));
    }
    return this.init(), this;
  }
  toJSON() {
    const e = {
      metadata: {
        version: 4.6,
        type: "Skeleton",
        generator: "Skeleton.toJSON"
      },
      bones: [],
      boneInverses: []
    };
    e.uuid = this.uuid;
    const t = this.bones, i = this.boneInverses;
    for (let n = 0, r = t.length; n < r; n++) {
      const a = t[n];
      e.bones.push(a.uuid);
      const o = i[n];
      e.boneInverses.push(o.toArray());
    }
    return e;
  }
}
class hs extends Ht {
  constructor(e, t, i, n = 1) {
    super(e, t, i), this.isInstancedBufferAttribute = !0, this.meshPerAttribute = n;
  }
  copy(e) {
    return super.copy(e), this.meshPerAttribute = e.meshPerAttribute, this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.meshPerAttribute = this.meshPerAttribute, e.isInstancedBufferAttribute = !0, e;
  }
}
const wd = /* @__PURE__ */ new et(), kE = /* @__PURE__ */ new et(), Av = [], GE = /* @__PURE__ */ new zt(), SX = /* @__PURE__ */ new et(), pm = /* @__PURE__ */ new Re(), mm = /* @__PURE__ */ new Tn();
class ho extends Re {
  constructor(e, t, i) {
    super(e, t), this.isInstancedMesh = !0, this.instanceMatrix = new hs(new Float32Array(i * 16), 16), this.instanceColor = null, this.morphTexture = null, this.count = i, this.boundingBox = null, this.boundingSphere = null;
    for (let n = 0; n < i; n++)
      this.setMatrixAt(n, SX);
  }
  computeBoundingBox() {
    const e = this.geometry, t = this.count;
    this.boundingBox === null && (this.boundingBox = new zt()), e.boundingBox === null && e.computeBoundingBox(), this.boundingBox.makeEmpty();
    for (let i = 0; i < t; i++)
      this.getMatrixAt(i, wd), GE.copy(e.boundingBox).applyMatrix4(wd), this.boundingBox.union(GE);
  }
  computeBoundingSphere() {
    const e = this.geometry, t = this.count;
    this.boundingSphere === null && (this.boundingSphere = new Tn()), e.boundingSphere === null && e.computeBoundingSphere(), this.boundingSphere.makeEmpty();
    for (let i = 0; i < t; i++)
      this.getMatrixAt(i, wd), mm.copy(e.boundingSphere).applyMatrix4(wd), this.boundingSphere.union(mm);
  }
  copy(e, t) {
    return super.copy(e, t), this.instanceMatrix.copy(e.instanceMatrix), e.morphTexture !== null && (this.morphTexture = e.morphTexture.clone()), e.instanceColor !== null && (this.instanceColor = e.instanceColor.clone()), this.count = e.count, e.boundingBox !== null && (this.boundingBox = e.boundingBox.clone()), e.boundingSphere !== null && (this.boundingSphere = e.boundingSphere.clone()), this;
  }
  getColorAt(e, t) {
    t.fromArray(this.instanceColor.array, e * 3);
  }
  getMatrixAt(e, t) {
    t.fromArray(this.instanceMatrix.array, e * 16);
  }
  getMorphAt(e, t) {
    const i = t.morphTargetInfluences, n = this.morphTexture.source.data.data, r = i.length + 1, a = e * r + 1;
    for (let o = 0; o < i.length; o++)
      i[o] = n[a + o];
  }
  raycast(e, t) {
    const i = this.matrixWorld, n = this.count;
    if (pm.geometry = this.geometry, pm.material = this.material, pm.material !== void 0 && (this.boundingSphere === null && this.computeBoundingSphere(), mm.copy(this.boundingSphere), mm.applyMatrix4(i), e.ray.intersectsSphere(mm) !== !1))
      for (let r = 0; r < n; r++) {
        this.getMatrixAt(r, wd), kE.multiplyMatrices(i, wd), pm.matrixWorld = kE, pm.raycast(e, Av);
        for (let a = 0, o = Av.length; a < o; a++) {
          const l = Av[a];
          l.instanceId = r, l.object = this, t.push(l);
        }
        Av.length = 0;
      }
  }
  setColorAt(e, t) {
    this.instanceColor === null && (this.instanceColor = new hs(new Float32Array(this.instanceMatrix.count * 3), 3)), t.toArray(this.instanceColor.array, e * 3);
  }
  setMatrixAt(e, t) {
    t.toArray(this.instanceMatrix.array, e * 16);
  }
  setMorphAt(e, t) {
    const i = t.morphTargetInfluences, n = i.length + 1;
    this.morphTexture === null && (this.morphTexture = new Qn(new Float32Array(n * this.count), n, this.count, Vu, xn));
    const r = this.morphTexture.source.data.data;
    let a = 0;
    for (let c = 0; c < i.length; c++)
      a += i[c];
    const o = this.geometry.morphTargetsRelative ? 1 : 1 - a, l = n * e;
    r[l] = o, r.set(i, l + 1);
  }
  updateMorphTargets() {
  }
  dispose() {
    return this.dispatchEvent({ type: "dispose" }), this.morphTexture !== null && (this.morphTexture.dispose(), this.morphTexture = null), this;
  }
}
function wX(s, e) {
  return s.z - e.z;
}
function EX(s, e) {
  return e.z - s.z;
}
class TX {
  constructor() {
    this.index = 0, this.pool = [], this.list = [];
  }
  push(e, t, i) {
    const n = this.pool, r = this.list;
    this.index >= n.length && n.push({
      start: -1,
      count: -1,
      z: -1,
      index: -1
    });
    const a = n[this.index];
    r.push(a), this.index++, a.start = e.start, a.count = e.count, a.z = t, a.index = i;
  }
  reset() {
    this.list.length = 0, this.index = 0;
  }
}
const kl = /* @__PURE__ */ new et(), Z8 = /* @__PURE__ */ new et(), CX = /* @__PURE__ */ new et(), _X = /* @__PURE__ */ new _e(1, 1, 1), HE = /* @__PURE__ */ new et(), K8 = /* @__PURE__ */ new th(), bv = /* @__PURE__ */ new zt(), xh = /* @__PURE__ */ new Tn(), gm = /* @__PURE__ */ new _(), VE = /* @__PURE__ */ new _(), PX = /* @__PURE__ */ new _(), J8 = /* @__PURE__ */ new TX(), Xs = /* @__PURE__ */ new Re(), Mv = [];
function RX(s, e, t = 0) {
  const i = e.itemSize;
  if (s.isInterleavedBufferAttribute || s.array.constructor !== e.array.constructor) {
    const n = s.count;
    for (let r = 0; r < n; r++)
      for (let a = 0; a < i; a++)
        e.setComponent(r + t, a, s.getComponent(r, a));
  } else
    e.array.set(s.array, t * i);
  e.needsUpdate = !0;
}
class rI extends Re {
  get maxInstanceCount() {
    return this._maxInstanceCount;
  }
  constructor(e, t, i = t * 2, n) {
    super(new Pt(), n), this.isBatchedMesh = !0, this.perObjectFrustumCulled = !0, this.sortObjects = !0, this.boundingBox = null, this.boundingSphere = null, this.customSort = null, this._drawInfo = [], this._drawRanges = [], this._reservedRanges = [], this._bounds = [], this._maxInstanceCount = e, this._maxVertexCount = t, this._maxIndexCount = i, this._geometryInitialized = !1, this._geometryCount = 0, this._multiDrawCounts = new Int32Array(e), this._multiDrawStarts = new Int32Array(e), this._multiDrawCount = 0, this._multiDrawInstances = null, this._visibilityChanged = !0, this._matricesTexture = null, this._indirectTexture = null, this._colorsTexture = null, this._initMatricesTexture(), this._initIndirectTexture();
  }
  _initMatricesTexture() {
    let e = Math.sqrt(this._maxInstanceCount * 4);
    e = Math.ceil(e / 4) * 4, e = Math.max(e, 4);
    const t = new Float32Array(e * e * 4), i = new Qn(t, e, e, dn, xn);
    this._matricesTexture = i;
  }
  _initIndirectTexture() {
    let e = Math.sqrt(this._maxInstanceCount);
    e = Math.ceil(e);
    const t = new Uint32Array(e * e), i = new Qn(t, e, e, t2, gl);
    this._indirectTexture = i;
  }
  _initColorsTexture() {
    let e = Math.sqrt(this._maxIndexCount);
    e = Math.ceil(e);
    const t = new Float32Array(e * e * 4).fill(1), i = new Qn(t, e, e, dn, xn);
    i.colorSpace = bi.workingColorSpace, this._colorsTexture = i;
  }
  _initializeGeometry(e) {
    const t = this.geometry, i = this._maxVertexCount, n = this._maxIndexCount;
    if (this._geometryInitialized === !1) {
      for (const r in e.attributes) {
        const a = e.getAttribute(r), { array: o, itemSize: l, normalized: c } = a, h = new o.constructor(i * l), u = new Ht(h, l, c);
        t.setAttribute(r, u);
      }
      if (e.getIndex() !== null) {
        const r = i > 65535 ? new Uint32Array(n) : new Uint16Array(n);
        t.setIndex(new Ht(r, 1));
      }
      this._geometryInitialized = !0;
    }
  }
  // Make sure the geometry is compatible with the existing combined geometry attributes
  _validateGeometry(e) {
    const t = this.geometry;
    if (!!e.getIndex() != !!t.getIndex())
      throw new Error('BatchedMesh: All geometries must consistently have "index".');
    for (const i in t.attributes) {
      if (!e.hasAttribute(i))
        throw new Error(`BatchedMesh: Added geometry missing "${i}". All geometries must have consistent attributes.`);
      const n = e.getAttribute(i), r = t.getAttribute(i);
      if (n.itemSize !== r.itemSize || n.normalized !== r.normalized)
        throw new Error("BatchedMesh: All attributes must have a consistent itemSize and normalized value.");
    }
  }
  setCustomSort(e) {
    return this.customSort = e, this;
  }
  computeBoundingBox() {
    this.boundingBox === null && (this.boundingBox = new zt());
    const e = this._geometryCount, t = this.boundingBox, i = this._drawInfo;
    t.makeEmpty();
    for (let n = 0; n < e; n++) {
      if (i[n].active === !1)
        continue;
      const r = i[n].geometryIndex;
      this.getMatrixAt(n, kl), this.getBoundingBoxAt(r, bv).applyMatrix4(kl), t.union(bv);
    }
  }
  computeBoundingSphere() {
    this.boundingSphere === null && (this.boundingSphere = new Tn());
    const e = this.boundingSphere, t = this._drawInfo;
    e.makeEmpty();
    for (let i = 0, n = t.length; i < n; i++) {
      if (t[i].active === !1)
        continue;
      const r = t[i].geometryIndex;
      this.getMatrixAt(i, kl), this.getBoundingSphereAt(r, xh).applyMatrix4(kl), e.union(xh);
    }
  }
  addInstance(e) {
    if (this._drawInfo.length >= this._maxInstanceCount)
      throw new Error("BatchedMesh: Maximum item count reached.");
    this._drawInfo.push({
      visible: !0,
      active: !0,
      geometryIndex: e
    });
    const t = this._drawInfo.length - 1, i = this._matricesTexture, n = i.image.data;
    CX.toArray(n, t * 16), i.needsUpdate = !0;
    const r = this._colorsTexture;
    return r && (_X.toArray(r.image.data, t * 4), r.needsUpdate = !0), t;
  }
  addGeometry(e, t = -1, i = -1) {
    if (this._initializeGeometry(e), this._validateGeometry(e), this._drawInfo.length >= this._maxInstanceCount)
      throw new Error("BatchedMesh: Maximum item count reached.");
    const n = {
      vertexStart: -1,
      vertexCount: -1,
      indexStart: -1,
      indexCount: -1
    };
    let r = null;
    const a = this._reservedRanges, o = this._drawRanges, l = this._bounds;
    this._geometryCount !== 0 && (r = a[a.length - 1]), t === -1 ? n.vertexCount = e.getAttribute("position").count : n.vertexCount = t, r === null ? n.vertexStart = 0 : n.vertexStart = r.vertexStart + r.vertexCount;
    const c = e.getIndex(), h = c !== null;
    if (h && (i === -1 ? n.indexCount = c.count : n.indexCount = i, r === null ? n.indexStart = 0 : n.indexStart = r.indexStart + r.indexCount), n.indexStart !== -1 && n.indexStart + n.indexCount > this._maxIndexCount || n.vertexStart + n.vertexCount > this._maxVertexCount)
      throw new Error("BatchedMesh: Reserved space request exceeds the maximum buffer size.");
    const u = this._geometryCount;
    return this._geometryCount++, a.push(n), o.push({
      start: h ? n.indexStart : n.vertexStart,
      count: -1
    }), l.push({
      boxInitialized: !1,
      box: new zt(),
      sphereInitialized: !1,
      sphere: new Tn()
    }), this.setGeometryAt(u, e), u;
  }
  setGeometryAt(e, t) {
    if (e >= this._geometryCount)
      throw new Error("BatchedMesh: Maximum geometry count reached.");
    this._validateGeometry(t);
    const i = this.geometry, n = i.getIndex() !== null, r = i.getIndex(), a = t.getIndex(), o = this._reservedRanges[e];
    if (n && a.count > o.indexCount || t.attributes.position.count > o.vertexCount)
      throw new Error("BatchedMesh: Reserved space not large enough for provided geometry.");
    const l = o.vertexStart, c = o.vertexCount;
    for (const f in i.attributes) {
      const p = t.getAttribute(f), v = i.getAttribute(f);
      RX(p, v, l);
      const g = p.itemSize;
      for (let m = p.count, x = c; m < x; m++) {
        const y = l + m;
        for (let A = 0; A < g; A++)
          v.setComponent(y, A, 0);
      }
      v.needsUpdate = !0, v.addUpdateRange(l * g, c * g);
    }
    if (n) {
      const f = o.indexStart;
      for (let p = 0; p < a.count; p++)
        r.setX(f + p, l + a.getX(p));
      for (let p = a.count, v = o.indexCount; p < v; p++)
        r.setX(f + p, l);
      r.needsUpdate = !0, r.addUpdateRange(f, o.indexCount);
    }
    const h = this._bounds[e];
    t.boundingBox !== null ? (h.box.copy(t.boundingBox), h.boxInitialized = !0) : h.boxInitialized = !1, t.boundingSphere !== null ? (h.sphere.copy(t.boundingSphere), h.sphereInitialized = !0) : h.sphereInitialized = !1;
    const u = this._drawRanges[e], d = t.getAttribute("position");
    return u.count = n ? a.count : d.count, this._visibilityChanged = !0, e;
  }
  /*
  	deleteGeometry( geometryId ) {
  
  		// TODO: delete geometry and associated instances
  
  	}
  	*/
  /*
  	deleteInstance( instanceId ) {
  
  		// Note: User needs to call optimize() afterward to pack the data.
  
  		const drawInfo = this._drawInfo;
  		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {
  
  			return this;
  
  		}
  
  		drawInfo[ instanceId ].active = false;
  		this._visibilityChanged = true;
  
  		return this;
  
  	}
  	*/
  // get bounding box and compute it if it doesn't exist
  getBoundingBoxAt(e, t) {
    if (e >= this._geometryCount)
      return null;
    const i = this._bounds[e], n = i.box, r = this.geometry;
    if (i.boxInitialized === !1) {
      n.makeEmpty();
      const a = r.index, o = r.attributes.position, l = this._drawRanges[e];
      for (let c = l.start, h = l.start + l.count; c < h; c++) {
        let u = c;
        a && (u = a.getX(u)), n.expandByPoint(gm.fromBufferAttribute(o, u));
      }
      i.boxInitialized = !0;
    }
    return t.copy(n), t;
  }
  // get bounding sphere and compute it if it doesn't exist
  getBoundingSphereAt(e, t) {
    if (e >= this._geometryCount)
      return null;
    const i = this._bounds[e], n = i.sphere, r = this.geometry;
    if (i.sphereInitialized === !1) {
      n.makeEmpty(), this.getBoundingBoxAt(e, bv), bv.getCenter(n.center);
      const a = r.index, o = r.attributes.position, l = this._drawRanges[e];
      let c = 0;
      for (let h = l.start, u = l.start + l.count; h < u; h++) {
        let d = h;
        a && (d = a.getX(d)), gm.fromBufferAttribute(o, d), c = Math.max(c, n.center.distanceToSquared(gm));
      }
      n.radius = Math.sqrt(c), i.sphereInitialized = !0;
    }
    return t.copy(n), t;
  }
  setMatrixAt(e, t) {
    const i = this._drawInfo, n = this._matricesTexture, r = this._matricesTexture.image.data;
    return e >= i.length || i[e].active === !1 ? this : (t.toArray(r, e * 16), n.needsUpdate = !0, this);
  }
  getMatrixAt(e, t) {
    const i = this._drawInfo, n = this._matricesTexture.image.data;
    return e >= i.length || i[e].active === !1 ? null : t.fromArray(n, e * 16);
  }
  setColorAt(e, t) {
    this._colorsTexture === null && this._initColorsTexture();
    const i = this._colorsTexture, n = this._colorsTexture.image.data, r = this._drawInfo;
    return e >= r.length || r[e].active === !1 ? this : (t.toArray(n, e * 4), i.needsUpdate = !0, this);
  }
  getColorAt(e, t) {
    const i = this._colorsTexture.image.data, n = this._drawInfo;
    return e >= n.length || n[e].active === !1 ? null : t.fromArray(i, e * 4);
  }
  setVisibleAt(e, t) {
    const i = this._drawInfo;
    return e >= i.length || i[e].active === !1 || i[e].visible === t ? this : (i[e].visible = t, this._visibilityChanged = !0, this);
  }
  getVisibleAt(e) {
    const t = this._drawInfo;
    return e >= t.length || t[e].active === !1 ? !1 : t[e].visible;
  }
  raycast(e, t) {
    const i = this._drawInfo, n = this._drawRanges, r = this.matrixWorld, a = this.geometry;
    Xs.material = this.material, Xs.geometry.index = a.index, Xs.geometry.attributes = a.attributes, Xs.geometry.boundingBox === null && (Xs.geometry.boundingBox = new zt()), Xs.geometry.boundingSphere === null && (Xs.geometry.boundingSphere = new Tn());
    for (let o = 0, l = i.length; o < l; o++) {
      if (!i[o].visible || !i[o].active)
        continue;
      const c = i[o].geometryIndex, h = n[c];
      Xs.geometry.setDrawRange(h.start, h.count), this.getMatrixAt(o, Xs.matrixWorld).premultiply(r), this.getBoundingBoxAt(c, Xs.geometry.boundingBox), this.getBoundingSphereAt(c, Xs.geometry.boundingSphere), Xs.raycast(e, Mv);
      for (let u = 0, d = Mv.length; u < d; u++) {
        const f = Mv[u];
        f.object = this, f.batchId = o, t.push(f);
      }
      Mv.length = 0;
    }
    Xs.material = null, Xs.geometry.index = null, Xs.geometry.attributes = {}, Xs.geometry.setDrawRange(0, 1 / 0);
  }
  copy(e) {
    return super.copy(e), this.geometry = e.geometry.clone(), this.perObjectFrustumCulled = e.perObjectFrustumCulled, this.sortObjects = e.sortObjects, this.boundingBox = e.boundingBox !== null ? e.boundingBox.clone() : null, this.boundingSphere = e.boundingSphere !== null ? e.boundingSphere.clone() : null, this._drawRanges = e._drawRanges.map((t) => ({ ...t })), this._reservedRanges = e._reservedRanges.map((t) => ({ ...t })), this._drawInfo = e._drawInfo.map((t) => ({ ...t })), this._bounds = e._bounds.map((t) => ({
      boxInitialized: t.boxInitialized,
      box: t.box.clone(),
      sphereInitialized: t.sphereInitialized,
      sphere: t.sphere.clone()
    })), this._maxInstanceCount = e._maxInstanceCount, this._maxVertexCount = e._maxVertexCount, this._maxIndexCount = e._maxIndexCount, this._geometryInitialized = e._geometryInitialized, this._geometryCount = e._geometryCount, this._multiDrawCounts = e._multiDrawCounts.slice(), this._multiDrawStarts = e._multiDrawStarts.slice(), this._matricesTexture = e._matricesTexture.clone(), this._matricesTexture.image.data = this._matricesTexture.image.slice(), this._colorsTexture !== null && (this._colorsTexture = e._colorsTexture.clone(), this._colorsTexture.image.data = this._colorsTexture.image.slice()), this;
  }
  dispose() {
    return this.geometry.dispose(), this._matricesTexture.dispose(), this._matricesTexture = null, this._indirectTexture.dispose(), this._indirectTexture = null, this._colorsTexture !== null && (this._colorsTexture.dispose(), this._colorsTexture = null), this;
  }
  onBeforeRender(e, t, i, n, r) {
    if (!this._visibilityChanged && !this.perObjectFrustumCulled && !this.sortObjects)
      return;
    const a = n.getIndex(), o = a === null ? 1 : a.array.BYTES_PER_ELEMENT, l = this._drawInfo, c = this._multiDrawStarts, h = this._multiDrawCounts, u = this._drawRanges, d = this.perObjectFrustumCulled, f = this._indirectTexture, p = f.image.data;
    d && (HE.multiplyMatrices(i.projectionMatrix, i.matrixWorldInverse).multiply(this.matrixWorld), K8.setFromProjectionMatrix(
      HE,
      e.coordinateSystem
    ));
    let v = 0;
    if (this.sortObjects) {
      Z8.copy(this.matrixWorld).invert(), gm.setFromMatrixPosition(i.matrixWorld).applyMatrix4(Z8), VE.set(0, 0, -1).transformDirection(i.matrixWorld).transformDirection(Z8);
      for (let x = 0, y = l.length; x < y; x++)
        if (l[x].visible && l[x].active) {
          const A = l[x].geometryIndex;
          this.getMatrixAt(x, kl), this.getBoundingSphereAt(A, xh).applyMatrix4(kl);
          let b = !1;
          if (d && (b = !K8.intersectsSphere(xh)), !b) {
            const M = PX.subVectors(xh.center, gm).dot(VE);
            J8.push(u[A], M, x);
          }
        }
      const g = J8.list, m = this.customSort;
      m === null ? g.sort(r.transparent ? EX : wX) : m.call(this, g, i);
      for (let x = 0, y = g.length; x < y; x++) {
        const A = g[x];
        c[v] = A.start * o, h[v] = A.count, p[v] = A.index, v++;
      }
      J8.reset();
    } else
      for (let g = 0, m = l.length; g < m; g++)
        if (l[g].visible && l[g].active) {
          const x = l[g].geometryIndex;
          let y = !1;
          if (d && (this.getMatrixAt(g, kl), this.getBoundingSphereAt(x, xh).applyMatrix4(kl), y = !K8.intersectsSphere(xh)), !y) {
            const A = u[x];
            c[v] = A.start * o, h[v] = A.count, p[v] = g, v++;
          }
        }
    f.needsUpdate = !0, this._multiDrawCount = v, this._visibilityChanged = !1;
  }
  onBeforeShadow(e, t, i, n, r, a) {
    this.onBeforeRender(e, null, n, r, a);
  }
}
class Bi extends Cn {
  constructor(e) {
    super(), this.isLineBasicMaterial = !0, this.type = "LineBasicMaterial", this.color = new _e(16777215), this.map = null, this.linewidth = 1, this.linecap = "round", this.linejoin = "round", this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.linewidth = e.linewidth, this.linecap = e.linecap, this.linejoin = e.linejoin, this.fog = e.fog, this;
  }
}
const X9 = /* @__PURE__ */ new _(), j9 = /* @__PURE__ */ new _(), WE = /* @__PURE__ */ new et(), vm = /* @__PURE__ */ new g0(), Sv = /* @__PURE__ */ new Tn(), Q8 = /* @__PURE__ */ new _(), XE = /* @__PURE__ */ new _();
class ti extends mt {
  constructor(e = new Pt(), t = new Bi()) {
    super(), this.isLine = !0, this.type = "Line", this.geometry = e, this.material = t, this.updateMorphTargets();
  }
  copy(e, t) {
    return super.copy(e, t), this.material = Array.isArray(e.material) ? e.material.slice() : e.material, this.geometry = e.geometry, this;
  }
  computeLineDistances() {
    const e = this.geometry;
    if (e.index === null) {
      const t = e.attributes.position, i = [0];
      for (let n = 1, r = t.count; n < r; n++)
        X9.fromBufferAttribute(t, n - 1), j9.fromBufferAttribute(t, n), i[n] = i[n - 1], i[n] += X9.distanceTo(j9);
      e.setAttribute("lineDistance", new gt(i, 1));
    } else
      console.warn("THREE.Line.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.");
    return this;
  }
  raycast(e, t) {
    const i = this.geometry, n = this.matrixWorld, r = e.params.Line.threshold, a = i.drawRange;
    if (i.boundingSphere === null && i.computeBoundingSphere(), Sv.copy(i.boundingSphere), Sv.applyMatrix4(n), Sv.radius += r, e.ray.intersectsSphere(Sv) === !1)
      return;
    WE.copy(n).invert(), vm.copy(e.ray).applyMatrix4(WE);
    const o = r / ((this.scale.x + this.scale.y + this.scale.z) / 3), l = o * o, c = this.isLineSegments ? 2 : 1, h = i.index, d = i.attributes.position;
    if (h !== null) {
      const f = Math.max(0, a.start), p = Math.min(h.count, a.start + a.count);
      for (let v = f, g = p - 1; v < g; v += c) {
        const m = h.getX(v), x = h.getX(v + 1), y = wv(this, e, vm, l, m, x);
        y && t.push(y);
      }
      if (this.isLineLoop) {
        const v = h.getX(p - 1), g = h.getX(f), m = wv(this, e, vm, l, v, g);
        m && t.push(m);
      }
    } else {
      const f = Math.max(0, a.start), p = Math.min(d.count, a.start + a.count);
      for (let v = f, g = p - 1; v < g; v += c) {
        const m = wv(this, e, vm, l, v, v + 1);
        m && t.push(m);
      }
      if (this.isLineLoop) {
        const v = wv(this, e, vm, l, p - 1, f);
        v && t.push(v);
      }
    }
  }
  updateMorphTargets() {
    const t = this.geometry.morphAttributes, i = Object.keys(t);
    if (i.length > 0) {
      const n = t[i[0]];
      if (n !== void 0) {
        this.morphTargetInfluences = [], this.morphTargetDictionary = {};
        for (let r = 0, a = n.length; r < a; r++) {
          const o = n[r].name || String(r);
          this.morphTargetInfluences.push(0), this.morphTargetDictionary[o] = r;
        }
      }
    }
  }
}
function wv(s, e, t, i, n, r) {
  const a = s.geometry.attributes.position;
  if (X9.fromBufferAttribute(a, n), j9.fromBufferAttribute(a, r), t.distanceSqToSegment(X9, j9, Q8, XE) > i)
    return;
  Q8.applyMatrix4(s.matrixWorld);
  const l = e.ray.origin.distanceTo(Q8);
  if (!(l < e.near || l > e.far))
    return {
      distance: l,
      // What do we want? intersection point on the ray or on the segment??
      // point: raycaster.ray.at( distance ),
      point: XE.clone().applyMatrix4(s.matrixWorld),
      index: n,
      face: null,
      faceIndex: null,
      object: s
    };
}
const jE = /* @__PURE__ */ new _(), qE = /* @__PURE__ */ new _();
class _n extends ti {
  constructor(e, t) {
    super(e, t), this.isLineSegments = !0, this.type = "LineSegments";
  }
  computeLineDistances() {
    const e = this.geometry;
    if (e.index === null) {
      const t = e.attributes.position, i = [];
      for (let n = 0, r = t.count; n < r; n += 2)
        jE.fromBufferAttribute(t, n), qE.fromBufferAttribute(t, n + 1), i[n] = n === 0 ? 0 : i[n - 1], i[n + 1] = i[n] + jE.distanceTo(qE);
      e.setAttribute("lineDistance", new gt(i, 1));
    } else
      console.warn("THREE.LineSegments.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.");
    return this;
  }
}
class ju extends ti {
  constructor(e, t) {
    super(e, t), this.isLineLoop = !0, this.type = "LineLoop";
  }
}
class qu extends Cn {
  constructor(e) {
    super(), this.isPointsMaterial = !0, this.type = "PointsMaterial", this.color = new _e(16777215), this.map = null, this.alphaMap = null, this.size = 1, this.sizeAttenuation = !0, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.alphaMap = e.alphaMap, this.size = e.size, this.sizeAttenuation = e.sizeAttenuation, this.fog = e.fog, this;
  }
}
const YE = /* @__PURE__ */ new et(), Y6 = /* @__PURE__ */ new g0(), Ev = /* @__PURE__ */ new Tn(), Tv = /* @__PURE__ */ new _();
class sh extends mt {
  constructor(e = new Pt(), t = new qu()) {
    super(), this.isPoints = !0, this.type = "Points", this.geometry = e, this.material = t, this.updateMorphTargets();
  }
  copy(e, t) {
    return super.copy(e, t), this.material = Array.isArray(e.material) ? e.material.slice() : e.material, this.geometry = e.geometry, this;
  }
  raycast(e, t) {
    const i = this.geometry, n = this.matrixWorld, r = e.params.Points.threshold, a = i.drawRange;
    if (i.boundingSphere === null && i.computeBoundingSphere(), Ev.copy(i.boundingSphere), Ev.applyMatrix4(n), Ev.radius += r, e.ray.intersectsSphere(Ev) === !1)
      return;
    YE.copy(n).invert(), Y6.copy(e.ray).applyMatrix4(YE);
    const o = r / ((this.scale.x + this.scale.y + this.scale.z) / 3), l = o * o, c = i.index, u = i.attributes.position;
    if (c !== null) {
      const d = Math.max(0, a.start), f = Math.min(c.count, a.start + a.count);
      for (let p = d, v = f; p < v; p++) {
        const g = c.getX(p);
        Tv.fromBufferAttribute(u, g), ZE(Tv, g, l, n, e, t, this);
      }
    } else {
      const d = Math.max(0, a.start), f = Math.min(u.count, a.start + a.count);
      for (let p = d, v = f; p < v; p++)
        Tv.fromBufferAttribute(u, p), ZE(Tv, p, l, n, e, t, this);
    }
  }
  updateMorphTargets() {
    const t = this.geometry.morphAttributes, i = Object.keys(t);
    if (i.length > 0) {
      const n = t[i[0]];
      if (n !== void 0) {
        this.morphTargetInfluences = [], this.morphTargetDictionary = {};
        for (let r = 0, a = n.length; r < a; r++) {
          const o = n[r].name || String(r);
          this.morphTargetInfluences.push(0), this.morphTargetDictionary[o] = r;
        }
      }
    }
  }
}
function ZE(s, e, t, i, n, r, a) {
  const o = Y6.distanceSqToPoint(s);
  if (o < t) {
    const l = new _();
    Y6.closestPointToPoint(s, l), l.applyMatrix4(i);
    const c = n.ray.origin.distanceTo(l);
    if (c < n.near || c > n.far)
      return;
    r.push({
      distance: c,
      distanceToRay: Math.sqrt(o),
      point: l,
      index: e,
      face: null,
      object: a
    });
  }
}
class q9 extends Ut {
  constructor(e, t, i, n, r, a, o, l, c) {
    super(e, t, i, n, r, a, o, l, c), this.isVideoTexture = !0, this.minFilter = a !== void 0 ? a : ii, this.magFilter = r !== void 0 ? r : ii, this.generateMipmaps = !1;
    const h = this;
    function u() {
      h.needsUpdate = !0, e.requestVideoFrameCallback(u);
    }
    "requestVideoFrameCallback" in e && e.requestVideoFrameCallback(u);
  }
  clone() {
    return new this.constructor(this.image).copy(this);
  }
  update() {
    const e = this.image;
    "requestVideoFrameCallback" in e === !1 && e.readyState >= e.HAVE_CURRENT_DATA && (this.needsUpdate = !0);
  }
}
class DX extends Ut {
  constructor(e, t) {
    super({ width: e, height: t }), this.isFramebufferTexture = !0, this.magFilter = Ni, this.minFilter = Ni, this.generateMipmaps = !1, this.needsUpdate = !0;
  }
}
class T4 extends Ut {
  constructor(e, t, i, n, r, a, o, l, c, h, u, d) {
    super(null, a, o, l, c, h, n, r, u, d), this.isCompressedTexture = !0, this.image = { width: t, height: i }, this.mipmaps = e, this.flipY = !1, this.generateMipmaps = !1;
  }
}
class LX extends T4 {
  constructor(e, t, i, n, r, a) {
    super(e, t, i, r, a), this.isCompressedArrayTexture = !0, this.image.depth = n, this.wrapR = yn, this.layerUpdates = /* @__PURE__ */ new Set();
  }
  addLayerUpdate(e) {
    this.layerUpdates.add(e);
  }
  clearLayerUpdates() {
    this.layerUpdates.clear();
  }
}
class IX extends T4 {
  constructor(e, t, i) {
    super(void 0, e[0].width, e[0].height, t, i, lo), this.isCompressedCubeTexture = !0, this.isCubeTexture = !0, this.image = e;
  }
}
class zc extends Ut {
  constructor(e, t, i, n, r, a, o, l, c) {
    super(e, t, i, n, r, a, o, l, c), this.isCanvasTexture = !0, this.needsUpdate = !0;
  }
}
class vo {
  constructor() {
    this.type = "Curve", this.arcLengthDivisions = 200;
  }
  // Virtual base class method to overwrite and implement in subclasses
  //	- t [0 .. 1]
  getPoint() {
    return console.warn("THREE.Curve: .getPoint() not implemented."), null;
  }
  // Get point at relative position in curve according to arc length
  // - u [0 .. 1]
  getPointAt(e, t) {
    const i = this.getUtoTmapping(e);
    return this.getPoint(i, t);
  }
  // Get sequence of points using getPoint( t )
  getPoints(e = 5) {
    const t = [];
    for (let i = 0; i <= e; i++)
      t.push(this.getPoint(i / e));
    return t;
  }
  // Get sequence of points using getPointAt( u )
  getSpacedPoints(e = 5) {
    const t = [];
    for (let i = 0; i <= e; i++)
      t.push(this.getPointAt(i / e));
    return t;
  }
  // Get total curve arc length
  getLength() {
    const e = this.getLengths();
    return e[e.length - 1];
  }
  // Get list of cumulative segment lengths
  getLengths(e = this.arcLengthDivisions) {
    if (this.cacheArcLengths && this.cacheArcLengths.length === e + 1 && !this.needsUpdate)
      return this.cacheArcLengths;
    this.needsUpdate = !1;
    const t = [];
    let i, n = this.getPoint(0), r = 0;
    t.push(0);
    for (let a = 1; a <= e; a++)
      i = this.getPoint(a / e), r += i.distanceTo(n), t.push(r), n = i;
    return this.cacheArcLengths = t, t;
  }
  updateArcLengths() {
    this.needsUpdate = !0, this.getLengths();
  }
  // Given u ( 0 .. 1 ), get a t to find p. This gives you points which are equidistant
  getUtoTmapping(e, t) {
    const i = this.getLengths();
    let n = 0;
    const r = i.length;
    let a;
    t ? a = t : a = e * i[r - 1];
    let o = 0, l = r - 1, c;
    for (; o <= l; )
      if (n = Math.floor(o + (l - o) / 2), c = i[n] - a, c < 0)
        o = n + 1;
      else if (c > 0)
        l = n - 1;
      else {
        l = n;
        break;
      }
    if (n = l, i[n] === a)
      return n / (r - 1);
    const h = i[n], d = i[n + 1] - h, f = (a - h) / d;
    return (n + f) / (r - 1);
  }
  // Returns a unit vector tangent at t
  // In case any sub curve does not implement its tangent derivation,
  // 2 points a small delta apart will be used to find its gradient
  // which seems to give a reasonable approximation
  getTangent(e, t) {
    let n = e - 1e-4, r = e + 1e-4;
    n < 0 && (n = 0), r > 1 && (r = 1);
    const a = this.getPoint(n), o = this.getPoint(r), l = t || (a.isVector2 ? new Se() : new _());
    return l.copy(o).sub(a).normalize(), l;
  }
  getTangentAt(e, t) {
    const i = this.getUtoTmapping(e);
    return this.getTangent(i, t);
  }
  computeFrenetFrames(e, t) {
    const i = new _(), n = [], r = [], a = [], o = new _(), l = new et();
    for (let f = 0; f <= e; f++) {
      const p = f / e;
      n[f] = this.getTangentAt(p, new _());
    }
    r[0] = new _(), a[0] = new _();
    let c = Number.MAX_VALUE;
    const h = Math.abs(n[0].x), u = Math.abs(n[0].y), d = Math.abs(n[0].z);
    h <= c && (c = h, i.set(1, 0, 0)), u <= c && (c = u, i.set(0, 1, 0)), d <= c && i.set(0, 0, 1), o.crossVectors(n[0], i).normalize(), r[0].crossVectors(n[0], o), a[0].crossVectors(n[0], r[0]);
    for (let f = 1; f <= e; f++) {
      if (r[f] = r[f - 1].clone(), a[f] = a[f - 1].clone(), o.crossVectors(n[f - 1], n[f]), o.length() > Number.EPSILON) {
        o.normalize();
        const p = Math.acos(Wn(n[f - 1].dot(n[f]), -1, 1));
        r[f].applyMatrix4(l.makeRotationAxis(o, p));
      }
      a[f].crossVectors(n[f], r[f]);
    }
    if (t === !0) {
      let f = Math.acos(Wn(r[0].dot(r[e]), -1, 1));
      f /= e, n[0].dot(o.crossVectors(r[0], r[e])) > 0 && (f = -f);
      for (let p = 1; p <= e; p++)
        r[p].applyMatrix4(l.makeRotationAxis(n[p], f * p)), a[p].crossVectors(n[p], r[p]);
    }
    return {
      tangents: n,
      normals: r,
      binormals: a
    };
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.arcLengthDivisions = e.arcLengthDivisions, this;
  }
  toJSON() {
    const e = {
      metadata: {
        version: 4.6,
        type: "Curve",
        generator: "Curve.toJSON"
      }
    };
    return e.arcLengthDivisions = this.arcLengthDivisions, e.type = this.type, e;
  }
  fromJSON(e) {
    return this.arcLengthDivisions = e.arcLengthDivisions, this;
  }
}
class C4 extends vo {
  constructor(e = 0, t = 0, i = 1, n = 1, r = 0, a = Math.PI * 2, o = !1, l = 0) {
    super(), this.isEllipseCurve = !0, this.type = "EllipseCurve", this.aX = e, this.aY = t, this.xRadius = i, this.yRadius = n, this.aStartAngle = r, this.aEndAngle = a, this.aClockwise = o, this.aRotation = l;
  }
  getPoint(e, t = new Se()) {
    const i = t, n = Math.PI * 2;
    let r = this.aEndAngle - this.aStartAngle;
    const a = Math.abs(r) < Number.EPSILON;
    for (; r < 0; )
      r += n;
    for (; r > n; )
      r -= n;
    r < Number.EPSILON && (a ? r = 0 : r = n), this.aClockwise === !0 && !a && (r === n ? r = -n : r = r - n);
    const o = this.aStartAngle + e * r;
    let l = this.aX + this.xRadius * Math.cos(o), c = this.aY + this.yRadius * Math.sin(o);
    if (this.aRotation !== 0) {
      const h = Math.cos(this.aRotation), u = Math.sin(this.aRotation), d = l - this.aX, f = c - this.aY;
      l = d * h - f * u + this.aX, c = d * u + f * h + this.aY;
    }
    return i.set(l, c);
  }
  copy(e) {
    return super.copy(e), this.aX = e.aX, this.aY = e.aY, this.xRadius = e.xRadius, this.yRadius = e.yRadius, this.aStartAngle = e.aStartAngle, this.aEndAngle = e.aEndAngle, this.aClockwise = e.aClockwise, this.aRotation = e.aRotation, this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.aX = this.aX, e.aY = this.aY, e.xRadius = this.xRadius, e.yRadius = this.yRadius, e.aStartAngle = this.aStartAngle, e.aEndAngle = this.aEndAngle, e.aClockwise = this.aClockwise, e.aRotation = this.aRotation, e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.aX = e.aX, this.aY = e.aY, this.xRadius = e.xRadius, this.yRadius = e.yRadius, this.aStartAngle = e.aStartAngle, this.aEndAngle = e.aEndAngle, this.aClockwise = e.aClockwise, this.aRotation = e.aRotation, this;
  }
}
class aI extends C4 {
  constructor(e, t, i, n, r, a) {
    super(e, t, i, i, n, r, a), this.isArcCurve = !0, this.type = "ArcCurve";
  }
}
function VM() {
  let s = 0, e = 0, t = 0, i = 0;
  function n(r, a, o, l) {
    s = r, e = o, t = -3 * r + 3 * a - 2 * o - l, i = 2 * r - 2 * a + o + l;
  }
  return {
    initCatmullRom: function(r, a, o, l, c) {
      n(a, o, c * (o - r), c * (l - a));
    },
    initNonuniformCatmullRom: function(r, a, o, l, c, h, u) {
      let d = (a - r) / c - (o - r) / (c + h) + (o - a) / h, f = (o - a) / h - (l - a) / (h + u) + (l - o) / u;
      d *= h, f *= h, n(a, o, d, f);
    },
    calc: function(r) {
      const a = r * r, o = a * r;
      return s + e * r + t * a + i * o;
    }
  };
}
const Cv = /* @__PURE__ */ new _(), $8 = /* @__PURE__ */ new VM(), e5 = /* @__PURE__ */ new VM(), t5 = /* @__PURE__ */ new VM();
class Mu extends vo {
  constructor(e = [], t = !1, i = "centripetal", n = 0.5) {
    super(), this.isCatmullRomCurve3 = !0, this.type = "CatmullRomCurve3", this.points = e, this.closed = t, this.curveType = i, this.tension = n;
  }
  getPoint(e, t = new _()) {
    const i = t, n = this.points, r = n.length, a = (r - (this.closed ? 0 : 1)) * e;
    let o = Math.floor(a), l = a - o;
    this.closed ? o += o > 0 ? 0 : (Math.floor(Math.abs(o) / r) + 1) * r : l === 0 && o === r - 1 && (o = r - 2, l = 1);
    let c, h;
    this.closed || o > 0 ? c = n[(o - 1) % r] : (Cv.subVectors(n[0], n[1]).add(n[0]), c = Cv);
    const u = n[o % r], d = n[(o + 1) % r];
    if (this.closed || o + 2 < r ? h = n[(o + 2) % r] : (Cv.subVectors(n[r - 1], n[r - 2]).add(n[r - 1]), h = Cv), this.curveType === "centripetal" || this.curveType === "chordal") {
      const f = this.curveType === "chordal" ? 0.5 : 0.25;
      let p = Math.pow(c.distanceToSquared(u), f), v = Math.pow(u.distanceToSquared(d), f), g = Math.pow(d.distanceToSquared(h), f);
      v < 1e-4 && (v = 1), p < 1e-4 && (p = v), g < 1e-4 && (g = v), $8.initNonuniformCatmullRom(c.x, u.x, d.x, h.x, p, v, g), e5.initNonuniformCatmullRom(c.y, u.y, d.y, h.y, p, v, g), t5.initNonuniformCatmullRom(c.z, u.z, d.z, h.z, p, v, g);
    } else
      this.curveType === "catmullrom" && ($8.initCatmullRom(c.x, u.x, d.x, h.x, this.tension), e5.initCatmullRom(c.y, u.y, d.y, h.y, this.tension), t5.initCatmullRom(c.z, u.z, d.z, h.z, this.tension));
    return i.set(
      $8.calc(l),
      e5.calc(l),
      t5.calc(l)
    ), i;
  }
  copy(e) {
    super.copy(e), this.points = [];
    for (let t = 0, i = e.points.length; t < i; t++) {
      const n = e.points[t];
      this.points.push(n.clone());
    }
    return this.closed = e.closed, this.curveType = e.curveType, this.tension = e.tension, this;
  }
  toJSON() {
    const e = super.toJSON();
    e.points = [];
    for (let t = 0, i = this.points.length; t < i; t++) {
      const n = this.points[t];
      e.points.push(n.toArray());
    }
    return e.closed = this.closed, e.curveType = this.curveType, e.tension = this.tension, e;
  }
  fromJSON(e) {
    super.fromJSON(e), this.points = [];
    for (let t = 0, i = e.points.length; t < i; t++) {
      const n = e.points[t];
      this.points.push(new _().fromArray(n));
    }
    return this.closed = e.closed, this.curveType = e.curveType, this.tension = e.tension, this;
  }
}
function KE(s, e, t, i, n) {
  const r = (i - e) * 0.5, a = (n - t) * 0.5, o = s * s, l = s * o;
  return (2 * t - 2 * i + r + a) * l + (-3 * t + 3 * i - 2 * r - a) * o + r * s + t;
}
function OX(s, e) {
  const t = 1 - s;
  return t * t * e;
}
function NX(s, e) {
  return 2 * (1 - s) * s * e;
}
function BX(s, e) {
  return s * s * e;
}
function E1(s, e, t, i) {
  return OX(s, e) + NX(s, t) + BX(s, i);
}
function FX(s, e) {
  const t = 1 - s;
  return t * t * t * e;
}
function UX(s, e) {
  const t = 1 - s;
  return 3 * t * t * s * e;
}
function zX(s, e) {
  return 3 * (1 - s) * s * s * e;
}
function kX(s, e) {
  return s * s * s * e;
}
function T1(s, e, t, i, n) {
  return FX(s, e) + UX(s, t) + zX(s, i) + kX(s, n);
}
class WM extends vo {
  constructor(e = new Se(), t = new Se(), i = new Se(), n = new Se()) {
    super(), this.isCubicBezierCurve = !0, this.type = "CubicBezierCurve", this.v0 = e, this.v1 = t, this.v2 = i, this.v3 = n;
  }
  getPoint(e, t = new Se()) {
    const i = t, n = this.v0, r = this.v1, a = this.v2, o = this.v3;
    return i.set(
      T1(e, n.x, r.x, a.x, o.x),
      T1(e, n.y, r.y, a.y, o.y)
    ), i;
  }
  copy(e) {
    return super.copy(e), this.v0.copy(e.v0), this.v1.copy(e.v1), this.v2.copy(e.v2), this.v3.copy(e.v3), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v0 = this.v0.toArray(), e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e.v3 = this.v3.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v0.fromArray(e.v0), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this.v3.fromArray(e.v3), this;
  }
}
class oI extends vo {
  constructor(e = new _(), t = new _(), i = new _(), n = new _()) {
    super(), this.isCubicBezierCurve3 = !0, this.type = "CubicBezierCurve3", this.v0 = e, this.v1 = t, this.v2 = i, this.v3 = n;
  }
  getPoint(e, t = new _()) {
    const i = t, n = this.v0, r = this.v1, a = this.v2, o = this.v3;
    return i.set(
      T1(e, n.x, r.x, a.x, o.x),
      T1(e, n.y, r.y, a.y, o.y),
      T1(e, n.z, r.z, a.z, o.z)
    ), i;
  }
  copy(e) {
    return super.copy(e), this.v0.copy(e.v0), this.v1.copy(e.v1), this.v2.copy(e.v2), this.v3.copy(e.v3), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v0 = this.v0.toArray(), e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e.v3 = this.v3.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v0.fromArray(e.v0), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this.v3.fromArray(e.v3), this;
  }
}
class XM extends vo {
  constructor(e = new Se(), t = new Se()) {
    super(), this.isLineCurve = !0, this.type = "LineCurve", this.v1 = e, this.v2 = t;
  }
  getPoint(e, t = new Se()) {
    const i = t;
    return e === 1 ? i.copy(this.v2) : (i.copy(this.v2).sub(this.v1), i.multiplyScalar(e).add(this.v1)), i;
  }
  // Line curve is linear, so we can overwrite default getPointAt
  getPointAt(e, t) {
    return this.getPoint(e, t);
  }
  getTangent(e, t = new Se()) {
    return t.subVectors(this.v2, this.v1).normalize();
  }
  getTangentAt(e, t) {
    return this.getTangent(e, t);
  }
  copy(e) {
    return super.copy(e), this.v1.copy(e.v1), this.v2.copy(e.v2), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this;
  }
}
class Y9 extends vo {
  constructor(e = new _(), t = new _()) {
    super(), this.isLineCurve3 = !0, this.type = "LineCurve3", this.v1 = e, this.v2 = t;
  }
  getPoint(e, t = new _()) {
    const i = t;
    return e === 1 ? i.copy(this.v2) : (i.copy(this.v2).sub(this.v1), i.multiplyScalar(e).add(this.v1)), i;
  }
  // Line curve is linear, so we can overwrite default getPointAt
  getPointAt(e, t) {
    return this.getPoint(e, t);
  }
  getTangent(e, t = new _()) {
    return t.subVectors(this.v2, this.v1).normalize();
  }
  getTangentAt(e, t) {
    return this.getTangent(e, t);
  }
  copy(e) {
    return super.copy(e), this.v1.copy(e.v1), this.v2.copy(e.v2), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this;
  }
}
class jM extends vo {
  constructor(e = new Se(), t = new Se(), i = new Se()) {
    super(), this.isQuadraticBezierCurve = !0, this.type = "QuadraticBezierCurve", this.v0 = e, this.v1 = t, this.v2 = i;
  }
  getPoint(e, t = new Se()) {
    const i = t, n = this.v0, r = this.v1, a = this.v2;
    return i.set(
      E1(e, n.x, r.x, a.x),
      E1(e, n.y, r.y, a.y)
    ), i;
  }
  copy(e) {
    return super.copy(e), this.v0.copy(e.v0), this.v1.copy(e.v1), this.v2.copy(e.v2), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v0 = this.v0.toArray(), e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v0.fromArray(e.v0), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this;
  }
}
class qM extends vo {
  constructor(e = new _(), t = new _(), i = new _()) {
    super(), this.isQuadraticBezierCurve3 = !0, this.type = "QuadraticBezierCurve3", this.v0 = e, this.v1 = t, this.v2 = i;
  }
  getPoint(e, t = new _()) {
    const i = t, n = this.v0, r = this.v1, a = this.v2;
    return i.set(
      E1(e, n.x, r.x, a.x),
      E1(e, n.y, r.y, a.y),
      E1(e, n.z, r.z, a.z)
    ), i;
  }
  copy(e) {
    return super.copy(e), this.v0.copy(e.v0), this.v1.copy(e.v1), this.v2.copy(e.v2), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.v0 = this.v0.toArray(), e.v1 = this.v1.toArray(), e.v2 = this.v2.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.v0.fromArray(e.v0), this.v1.fromArray(e.v1), this.v2.fromArray(e.v2), this;
  }
}
class YM extends vo {
  constructor(e = []) {
    super(), this.isSplineCurve = !0, this.type = "SplineCurve", this.points = e;
  }
  getPoint(e, t = new Se()) {
    const i = t, n = this.points, r = (n.length - 1) * e, a = Math.floor(r), o = r - a, l = n[a === 0 ? a : a - 1], c = n[a], h = n[a > n.length - 2 ? n.length - 1 : a + 1], u = n[a > n.length - 3 ? n.length - 1 : a + 2];
    return i.set(
      KE(o, l.x, c.x, h.x, u.x),
      KE(o, l.y, c.y, h.y, u.y)
    ), i;
  }
  copy(e) {
    super.copy(e), this.points = [];
    for (let t = 0, i = e.points.length; t < i; t++) {
      const n = e.points[t];
      this.points.push(n.clone());
    }
    return this;
  }
  toJSON() {
    const e = super.toJSON();
    e.points = [];
    for (let t = 0, i = this.points.length; t < i; t++) {
      const n = this.points[t];
      e.points.push(n.toArray());
    }
    return e;
  }
  fromJSON(e) {
    super.fromJSON(e), this.points = [];
    for (let t = 0, i = e.points.length; t < i; t++) {
      const n = e.points[t];
      this.points.push(new Se().fromArray(n));
    }
    return this;
  }
}
var Z9 = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  ArcCurve: aI,
  CatmullRomCurve3: Mu,
  CubicBezierCurve: WM,
  CubicBezierCurve3: oI,
  EllipseCurve: C4,
  LineCurve: XM,
  LineCurve3: Y9,
  QuadraticBezierCurve: jM,
  QuadraticBezierCurve3: qM,
  SplineCurve: YM
});
class lI extends vo {
  constructor() {
    super(), this.type = "CurvePath", this.curves = [], this.autoClose = !1;
  }
  add(e) {
    this.curves.push(e);
  }
  closePath() {
    const e = this.curves[0].getPoint(0), t = this.curves[this.curves.length - 1].getPoint(1);
    if (!e.equals(t)) {
      const i = e.isVector2 === !0 ? "LineCurve" : "LineCurve3";
      this.curves.push(new Z9[i](t, e));
    }
    return this;
  }
  // To get accurate point with reference to
  // entire path distance at time t,
  // following has to be done:
  // 1. Length of each sub path have to be known
  // 2. Locate and identify type of curve
  // 3. Get t for the curve
  // 4. Return curve.getPointAt(t')
  getPoint(e, t) {
    const i = e * this.getLength(), n = this.getCurveLengths();
    let r = 0;
    for (; r < n.length; ) {
      if (n[r] >= i) {
        const a = n[r] - i, o = this.curves[r], l = o.getLength(), c = l === 0 ? 0 : 1 - a / l;
        return o.getPointAt(c, t);
      }
      r++;
    }
    return null;
  }
  // We cannot use the default THREE.Curve getPoint() with getLength() because in
  // THREE.Curve, getLength() depends on getPoint() but in THREE.CurvePath
  // getPoint() depends on getLength
  getLength() {
    const e = this.getCurveLengths();
    return e[e.length - 1];
  }
  // cacheLengths must be recalculated.
  updateArcLengths() {
    this.needsUpdate = !0, this.cacheLengths = null, this.getCurveLengths();
  }
  // Compute lengths and cache them
  // We cannot overwrite getLengths() because UtoT mapping uses it.
  getCurveLengths() {
    if (this.cacheLengths && this.cacheLengths.length === this.curves.length)
      return this.cacheLengths;
    const e = [];
    let t = 0;
    for (let i = 0, n = this.curves.length; i < n; i++)
      t += this.curves[i].getLength(), e.push(t);
    return this.cacheLengths = e, e;
  }
  getSpacedPoints(e = 40) {
    const t = [];
    for (let i = 0; i <= e; i++)
      t.push(this.getPoint(i / e));
    return this.autoClose && t.push(t[0]), t;
  }
  getPoints(e = 12) {
    const t = [];
    let i;
    for (let n = 0, r = this.curves; n < r.length; n++) {
      const a = r[n], o = a.isEllipseCurve ? e * 2 : a.isLineCurve || a.isLineCurve3 ? 1 : a.isSplineCurve ? e * a.points.length : e, l = a.getPoints(o);
      for (let c = 0; c < l.length; c++) {
        const h = l[c];
        i && i.equals(h) || (t.push(h), i = h);
      }
    }
    return this.autoClose && t.length > 1 && !t[t.length - 1].equals(t[0]) && t.push(t[0]), t;
  }
  copy(e) {
    super.copy(e), this.curves = [];
    for (let t = 0, i = e.curves.length; t < i; t++) {
      const n = e.curves[t];
      this.curves.push(n.clone());
    }
    return this.autoClose = e.autoClose, this;
  }
  toJSON() {
    const e = super.toJSON();
    e.autoClose = this.autoClose, e.curves = [];
    for (let t = 0, i = this.curves.length; t < i; t++) {
      const n = this.curves[t];
      e.curves.push(n.toJSON());
    }
    return e;
  }
  fromJSON(e) {
    super.fromJSON(e), this.autoClose = e.autoClose, this.curves = [];
    for (let t = 0, i = e.curves.length; t < i; t++) {
      const n = e.curves[t];
      this.curves.push(new Z9[n.type]().fromJSON(n));
    }
    return this;
  }
}
class eg extends lI {
  constructor(e) {
    super(), this.type = "Path", this.currentPoint = new Se(), e && this.setFromPoints(e);
  }
  setFromPoints(e) {
    this.moveTo(e[0].x, e[0].y);
    for (let t = 1, i = e.length; t < i; t++)
      this.lineTo(e[t].x, e[t].y);
    return this;
  }
  moveTo(e, t) {
    return this.currentPoint.set(e, t), this;
  }
  lineTo(e, t) {
    const i = new XM(this.currentPoint.clone(), new Se(e, t));
    return this.curves.push(i), this.currentPoint.set(e, t), this;
  }
  quadraticCurveTo(e, t, i, n) {
    const r = new jM(
      this.currentPoint.clone(),
      new Se(e, t),
      new Se(i, n)
    );
    return this.curves.push(r), this.currentPoint.set(i, n), this;
  }
  bezierCurveTo(e, t, i, n, r, a) {
    const o = new WM(
      this.currentPoint.clone(),
      new Se(e, t),
      new Se(i, n),
      new Se(r, a)
    );
    return this.curves.push(o), this.currentPoint.set(r, a), this;
  }
  splineThru(e) {
    const t = [this.currentPoint.clone()].concat(e), i = new YM(t);
    return this.curves.push(i), this.currentPoint.copy(e[e.length - 1]), this;
  }
  arc(e, t, i, n, r, a) {
    const o = this.currentPoint.x, l = this.currentPoint.y;
    return this.absarc(
      e + o,
      t + l,
      i,
      n,
      r,
      a
    ), this;
  }
  absarc(e, t, i, n, r, a) {
    return this.absellipse(e, t, i, i, n, r, a), this;
  }
  ellipse(e, t, i, n, r, a, o, l) {
    const c = this.currentPoint.x, h = this.currentPoint.y;
    return this.absellipse(e + c, t + h, i, n, r, a, o, l), this;
  }
  absellipse(e, t, i, n, r, a, o, l) {
    const c = new C4(e, t, i, n, r, a, o, l);
    if (this.curves.length > 0) {
      const u = c.getPoint(0);
      u.equals(this.currentPoint) || this.lineTo(u.x, u.y);
    }
    this.curves.push(c);
    const h = c.getPoint(1);
    return this.currentPoint.copy(h), this;
  }
  copy(e) {
    return super.copy(e), this.currentPoint.copy(e.currentPoint), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.currentPoint = this.currentPoint.toArray(), e;
  }
  fromJSON(e) {
    return super.fromJSON(e), this.currentPoint.fromArray(e.currentPoint), this;
  }
}
class jp extends Pt {
  constructor(e = [new Se(0, -0.5), new Se(0.5, 0), new Se(0, 0.5)], t = 12, i = 0, n = Math.PI * 2) {
    super(), this.type = "LatheGeometry", this.parameters = {
      points: e,
      segments: t,
      phiStart: i,
      phiLength: n
    }, t = Math.floor(t), n = Wn(n, 0, Math.PI * 2);
    const r = [], a = [], o = [], l = [], c = [], h = 1 / t, u = new _(), d = new Se(), f = new _(), p = new _(), v = new _();
    let g = 0, m = 0;
    for (let x = 0; x <= e.length - 1; x++)
      switch (x) {
        case 0:
          g = e[x + 1].x - e[x].x, m = e[x + 1].y - e[x].y, f.x = m * 1, f.y = -g, f.z = m * 0, v.copy(f), f.normalize(), l.push(f.x, f.y, f.z);
          break;
        case e.length - 1:
          l.push(v.x, v.y, v.z);
          break;
        default:
          g = e[x + 1].x - e[x].x, m = e[x + 1].y - e[x].y, f.x = m * 1, f.y = -g, f.z = m * 0, p.copy(f), f.x += v.x, f.y += v.y, f.z += v.z, f.normalize(), l.push(f.x, f.y, f.z), v.copy(p);
      }
    for (let x = 0; x <= t; x++) {
      const y = i + x * h * n, A = Math.sin(y), b = Math.cos(y);
      for (let M = 0; M <= e.length - 1; M++) {
        u.x = e[M].x * A, u.y = e[M].y, u.z = e[M].x * b, a.push(u.x, u.y, u.z), d.x = x / t, d.y = M / (e.length - 1), o.push(d.x, d.y);
        const w = l[3 * M + 0] * A, C = l[3 * M + 1], T = l[3 * M + 0] * b;
        c.push(w, C, T);
      }
    }
    for (let x = 0; x < t; x++)
      for (let y = 0; y < e.length - 1; y++) {
        const A = y + x * e.length, b = A, M = A + e.length, w = A + e.length + 1, C = A + 1;
        r.push(b, M, C), r.push(w, C, M);
      }
    this.setIndex(r), this.setAttribute("position", new gt(a, 3)), this.setAttribute("uv", new gt(o, 2)), this.setAttribute("normal", new gt(c, 3));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new jp(e.points, e.segments, e.phiStart, e.phiLength);
  }
}
class _u extends jp {
  constructor(e = 1, t = 1, i = 4, n = 8) {
    const r = new eg();
    r.absarc(0, -t / 2, e, Math.PI * 1.5, 0), r.absarc(0, t / 2, e, 0, Math.PI * 0.5), super(r.getPoints(i), n), this.type = "CapsuleGeometry", this.parameters = {
      radius: e,
      length: t,
      capSegments: i,
      radialSegments: n
    };
  }
  static fromJSON(e) {
    return new _u(e.radius, e.length, e.capSegments, e.radialSegments);
  }
}
class o2 extends Pt {
  constructor(e = 1, t = 32, i = 0, n = Math.PI * 2) {
    super(), this.type = "CircleGeometry", this.parameters = {
      radius: e,
      segments: t,
      thetaStart: i,
      thetaLength: n
    }, t = Math.max(3, t);
    const r = [], a = [], o = [], l = [], c = new _(), h = new Se();
    a.push(0, 0, 0), o.push(0, 0, 1), l.push(0.5, 0.5);
    for (let u = 0, d = 3; u <= t; u++, d += 3) {
      const f = i + u / t * n;
      c.x = e * Math.cos(f), c.y = e * Math.sin(f), a.push(c.x, c.y, c.z), o.push(0, 0, 1), h.x = (a[d] / e + 1) / 2, h.y = (a[d + 1] / e + 1) / 2, l.push(h.x, h.y);
    }
    for (let u = 1; u <= t; u++)
      r.push(u, u + 1, 0);
    this.setIndex(r), this.setAttribute("position", new gt(a, 3)), this.setAttribute("normal", new gt(o, 3)), this.setAttribute("uv", new gt(l, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new o2(e.radius, e.segments, e.thetaStart, e.thetaLength);
  }
}
class Ei extends Pt {
  constructor(e = 1, t = 1, i = 1, n = 32, r = 1, a = !1, o = 0, l = Math.PI * 2) {
    super(), this.type = "CylinderGeometry", this.parameters = {
      radiusTop: e,
      radiusBottom: t,
      height: i,
      radialSegments: n,
      heightSegments: r,
      openEnded: a,
      thetaStart: o,
      thetaLength: l
    };
    const c = this;
    n = Math.floor(n), r = Math.floor(r);
    const h = [], u = [], d = [], f = [];
    let p = 0;
    const v = [], g = i / 2;
    let m = 0;
    x(), a === !1 && (e > 0 && y(!0), t > 0 && y(!1)), this.setIndex(h), this.setAttribute("position", new gt(u, 3)), this.setAttribute("normal", new gt(d, 3)), this.setAttribute("uv", new gt(f, 2));
    function x() {
      const A = new _(), b = new _();
      let M = 0;
      const w = (t - e) / i;
      for (let C = 0; C <= r; C++) {
        const T = [], E = C / r, P = E * (t - e) + e;
        for (let L = 0; L <= n; L++) {
          const I = L / n, N = I * l + o, H = Math.sin(N), U = Math.cos(N);
          b.x = P * H, b.y = -E * i + g, b.z = P * U, u.push(b.x, b.y, b.z), A.set(H, w, U).normalize(), d.push(A.x, A.y, A.z), f.push(I, 1 - E), T.push(p++);
        }
        v.push(T);
      }
      for (let C = 0; C < n; C++)
        for (let T = 0; T < r; T++) {
          const E = v[T][C], P = v[T + 1][C], L = v[T + 1][C + 1], I = v[T][C + 1];
          h.push(E, P, I), h.push(P, L, I), M += 6;
        }
      c.addGroup(m, M, 0), m += M;
    }
    function y(A) {
      const b = p, M = new Se(), w = new _();
      let C = 0;
      const T = A === !0 ? e : t, E = A === !0 ? 1 : -1;
      for (let L = 1; L <= n; L++)
        u.push(0, g * E, 0), d.push(0, E, 0), f.push(0.5, 0.5), p++;
      const P = p;
      for (let L = 0; L <= n; L++) {
        const N = L / n * l + o, H = Math.cos(N), U = Math.sin(N);
        w.x = T * U, w.y = g * E, w.z = T * H, u.push(w.x, w.y, w.z), d.push(0, E, 0), M.x = H * 0.5 + 0.5, M.y = U * 0.5 * E + 0.5, f.push(M.x, M.y), p++;
      }
      for (let L = 0; L < n; L++) {
        const I = b + L, N = P + L;
        A === !0 ? h.push(N, N + 1, I) : h.push(N + 1, N, I), C += 3;
      }
      c.addGroup(m, C, A === !0 ? 1 : 2), m += C;
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new Ei(e.radiusTop, e.radiusBottom, e.height, e.radialSegments, e.heightSegments, e.openEnded, e.thetaStart, e.thetaLength);
  }
}
class qp extends Ei {
  constructor(e = 1, t = 1, i = 32, n = 1, r = !1, a = 0, o = Math.PI * 2) {
    super(0, e, t, i, n, r, a, o), this.type = "ConeGeometry", this.parameters = {
      radius: e,
      height: t,
      radialSegments: i,
      heightSegments: n,
      openEnded: r,
      thetaStart: a,
      thetaLength: o
    };
  }
  static fromJSON(e) {
    return new qp(e.radius, e.height, e.radialSegments, e.heightSegments, e.openEnded, e.thetaStart, e.thetaLength);
  }
}
class El extends Pt {
  constructor(e = [], t = [], i = 1, n = 0) {
    super(), this.type = "PolyhedronGeometry", this.parameters = {
      vertices: e,
      indices: t,
      radius: i,
      detail: n
    };
    const r = [], a = [];
    o(n), c(i), h(), this.setAttribute("position", new gt(r, 3)), this.setAttribute("normal", new gt(r.slice(), 3)), this.setAttribute("uv", new gt(a, 2)), n === 0 ? this.computeVertexNormals() : this.normalizeNormals();
    function o(x) {
      const y = new _(), A = new _(), b = new _();
      for (let M = 0; M < t.length; M += 3)
        f(t[M + 0], y), f(t[M + 1], A), f(t[M + 2], b), l(y, A, b, x);
    }
    function l(x, y, A, b) {
      const M = b + 1, w = [];
      for (let C = 0; C <= M; C++) {
        w[C] = [];
        const T = x.clone().lerp(A, C / M), E = y.clone().lerp(A, C / M), P = M - C;
        for (let L = 0; L <= P; L++)
          L === 0 && C === M ? w[C][L] = T : w[C][L] = T.clone().lerp(E, L / P);
      }
      for (let C = 0; C < M; C++)
        for (let T = 0; T < 2 * (M - C) - 1; T++) {
          const E = Math.floor(T / 2);
          T % 2 === 0 ? (d(w[C][E + 1]), d(w[C + 1][E]), d(w[C][E])) : (d(w[C][E + 1]), d(w[C + 1][E + 1]), d(w[C + 1][E]));
        }
    }
    function c(x) {
      const y = new _();
      for (let A = 0; A < r.length; A += 3)
        y.x = r[A + 0], y.y = r[A + 1], y.z = r[A + 2], y.normalize().multiplyScalar(x), r[A + 0] = y.x, r[A + 1] = y.y, r[A + 2] = y.z;
    }
    function h() {
      const x = new _();
      for (let y = 0; y < r.length; y += 3) {
        x.x = r[y + 0], x.y = r[y + 1], x.z = r[y + 2];
        const A = g(x) / 2 / Math.PI + 0.5, b = m(x) / Math.PI + 0.5;
        a.push(A, 1 - b);
      }
      p(), u();
    }
    function u() {
      for (let x = 0; x < a.length; x += 6) {
        const y = a[x + 0], A = a[x + 2], b = a[x + 4], M = Math.max(y, A, b), w = Math.min(y, A, b);
        M > 0.9 && w < 0.1 && (y < 0.2 && (a[x + 0] += 1), A < 0.2 && (a[x + 2] += 1), b < 0.2 && (a[x + 4] += 1));
      }
    }
    function d(x) {
      r.push(x.x, x.y, x.z);
    }
    function f(x, y) {
      const A = x * 3;
      y.x = e[A + 0], y.y = e[A + 1], y.z = e[A + 2];
    }
    function p() {
      const x = new _(), y = new _(), A = new _(), b = new _(), M = new Se(), w = new Se(), C = new Se();
      for (let T = 0, E = 0; T < r.length; T += 9, E += 6) {
        x.set(r[T + 0], r[T + 1], r[T + 2]), y.set(r[T + 3], r[T + 4], r[T + 5]), A.set(r[T + 6], r[T + 7], r[T + 8]), M.set(a[E + 0], a[E + 1]), w.set(a[E + 2], a[E + 3]), C.set(a[E + 4], a[E + 5]), b.copy(x).add(y).add(A).divideScalar(3);
        const P = g(b);
        v(M, E + 0, x, P), v(w, E + 2, y, P), v(C, E + 4, A, P);
      }
    }
    function v(x, y, A, b) {
      b < 0 && x.x === 1 && (a[y] = x.x - 1), A.x === 0 && A.z === 0 && (a[y] = b / 2 / Math.PI + 0.5);
    }
    function g(x) {
      return Math.atan2(x.z, -x.x);
    }
    function m(x) {
      return Math.atan2(-x.y, Math.sqrt(x.x * x.x + x.z * x.z));
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new El(e.vertices, e.indices, e.radius, e.details);
  }
}
class l2 extends El {
  constructor(e = 1, t = 0) {
    const i = (1 + Math.sqrt(5)) / 2, n = 1 / i, r = [
      // (±1, ±1, ±1)
      -1,
      -1,
      -1,
      -1,
      -1,
      1,
      -1,
      1,
      -1,
      -1,
      1,
      1,
      1,
      -1,
      -1,
      1,
      -1,
      1,
      1,
      1,
      -1,
      1,
      1,
      1,
      // (0, ±1/φ, ±φ)
      0,
      -n,
      -i,
      0,
      -n,
      i,
      0,
      n,
      -i,
      0,
      n,
      i,
      // (±1/φ, ±φ, 0)
      -n,
      -i,
      0,
      -n,
      i,
      0,
      n,
      -i,
      0,
      n,
      i,
      0,
      // (±φ, 0, ±1/φ)
      -i,
      0,
      -n,
      i,
      0,
      -n,
      -i,
      0,
      n,
      i,
      0,
      n
    ], a = [
      3,
      11,
      7,
      3,
      7,
      15,
      3,
      15,
      13,
      7,
      19,
      17,
      7,
      17,
      6,
      7,
      6,
      15,
      17,
      4,
      8,
      17,
      8,
      10,
      17,
      10,
      6,
      8,
      0,
      16,
      8,
      16,
      2,
      8,
      2,
      10,
      0,
      12,
      1,
      0,
      1,
      18,
      0,
      18,
      16,
      6,
      10,
      2,
      6,
      2,
      13,
      6,
      13,
      15,
      2,
      16,
      18,
      2,
      18,
      3,
      2,
      3,
      13,
      18,
      1,
      9,
      18,
      9,
      11,
      18,
      11,
      3,
      4,
      14,
      12,
      4,
      12,
      0,
      4,
      0,
      8,
      11,
      9,
      5,
      11,
      5,
      19,
      11,
      19,
      7,
      19,
      5,
      14,
      19,
      14,
      4,
      19,
      4,
      17,
      1,
      12,
      14,
      1,
      14,
      5,
      1,
      5,
      9
    ];
    super(r, a, e, t), this.type = "DodecahedronGeometry", this.parameters = {
      radius: e,
      detail: t
    };
  }
  static fromJSON(e) {
    return new l2(e.radius, e.detail);
  }
}
const _v = /* @__PURE__ */ new _(), Pv = /* @__PURE__ */ new _(), i5 = /* @__PURE__ */ new _(), Rv = /* @__PURE__ */ new Wo();
class ap extends Pt {
  constructor(e = null, t = 1) {
    if (super(), this.type = "EdgesGeometry", this.parameters = {
      geometry: e,
      thresholdAngle: t
    }, e !== null) {
      const n = Math.pow(10, 4), r = Math.cos(bu * t), a = e.getIndex(), o = e.getAttribute("position"), l = a ? a.count : o.count, c = [0, 0, 0], h = ["a", "b", "c"], u = new Array(3), d = {}, f = [];
      for (let p = 0; p < l; p += 3) {
        a ? (c[0] = a.getX(p), c[1] = a.getX(p + 1), c[2] = a.getX(p + 2)) : (c[0] = p, c[1] = p + 1, c[2] = p + 2);
        const { a: v, b: g, c: m } = Rv;
        if (v.fromBufferAttribute(o, c[0]), g.fromBufferAttribute(o, c[1]), m.fromBufferAttribute(o, c[2]), Rv.getNormal(i5), u[0] = `${Math.round(v.x * n)},${Math.round(v.y * n)},${Math.round(v.z * n)}`, u[1] = `${Math.round(g.x * n)},${Math.round(g.y * n)},${Math.round(g.z * n)}`, u[2] = `${Math.round(m.x * n)},${Math.round(m.y * n)},${Math.round(m.z * n)}`, !(u[0] === u[1] || u[1] === u[2] || u[2] === u[0]))
          for (let x = 0; x < 3; x++) {
            const y = (x + 1) % 3, A = u[x], b = u[y], M = Rv[h[x]], w = Rv[h[y]], C = `${A}_${b}`, T = `${b}_${A}`;
            T in d && d[T] ? (i5.dot(d[T].normal) <= r && (f.push(M.x, M.y, M.z), f.push(w.x, w.y, w.z)), d[T] = null) : C in d || (d[C] = {
              index0: c[x],
              index1: c[y],
              normal: i5.clone()
            });
          }
      }
      for (const p in d)
        if (d[p]) {
          const { index0: v, index1: g } = d[p];
          _v.fromBufferAttribute(o, v), Pv.fromBufferAttribute(o, g), f.push(_v.x, _v.y, _v.z), f.push(Pv.x, Pv.y, Pv.z);
        }
      this.setAttribute("position", new gt(f, 3));
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
}
class ll extends eg {
  constructor(e) {
    super(e), this.uuid = ea(), this.type = "Shape", this.holes = [];
  }
  getPointsHoles(e) {
    const t = [];
    for (let i = 0, n = this.holes.length; i < n; i++)
      t[i] = this.holes[i].getPoints(e);
    return t;
  }
  // get points of shape and holes (keypoints based on segments parameter)
  extractPoints(e) {
    return {
      shape: this.getPoints(e),
      holes: this.getPointsHoles(e)
    };
  }
  copy(e) {
    super.copy(e), this.holes = [];
    for (let t = 0, i = e.holes.length; t < i; t++) {
      const n = e.holes[t];
      this.holes.push(n.clone());
    }
    return this;
  }
  toJSON() {
    const e = super.toJSON();
    e.uuid = this.uuid, e.holes = [];
    for (let t = 0, i = this.holes.length; t < i; t++) {
      const n = this.holes[t];
      e.holes.push(n.toJSON());
    }
    return e;
  }
  fromJSON(e) {
    super.fromJSON(e), this.uuid = e.uuid, this.holes = [];
    for (let t = 0, i = e.holes.length; t < i; t++) {
      const n = e.holes[t];
      this.holes.push(new eg().fromJSON(n));
    }
    return this;
  }
}
const GX = {
  triangulate: function(s, e, t = 2) {
    const i = e && e.length, n = i ? e[0] * t : s.length;
    let r = cI(s, 0, n, t, !0);
    const a = [];
    if (!r || r.next === r.prev)
      return a;
    let o, l, c, h, u, d, f;
    if (i && (r = jX(s, e, r, t)), s.length > 80 * t) {
      o = c = s[0], l = h = s[1];
      for (let p = t; p < n; p += t)
        u = s[p], d = s[p + 1], u < o && (o = u), d < l && (l = d), u > c && (c = u), d > h && (h = d);
      f = Math.max(c - o, h - l), f = f !== 0 ? 32767 / f : 0;
    }
    return tg(r, a, t, o, l, f, 0), a;
  }
};
function cI(s, e, t, i, n) {
  let r, a;
  if (n === nj(s, e, t, i) > 0)
    for (r = e; r < t; r += i)
      a = JE(r, s[r], s[r + 1], a);
  else
    for (r = t - i; r >= e; r -= i)
      a = JE(r, s[r], s[r + 1], a);
  return a && _4(a, a.next) && (ng(a), a = a.next), a;
}
function Pu(s, e) {
  if (!s)
    return s;
  e || (e = s);
  let t = s, i;
  do
    if (i = !1, !t.steiner && (_4(t, t.next) || Un(t.prev, t, t.next) === 0)) {
      if (ng(t), t = e = t.prev, t === t.next)
        break;
      i = !0;
    } else
      t = t.next;
  while (i || t !== e);
  return e;
}
function tg(s, e, t, i, n, r, a) {
  if (!s)
    return;
  !a && r && JX(s, i, n, r);
  let o = s, l, c;
  for (; s.prev !== s.next; ) {
    if (l = s.prev, c = s.next, r ? VX(s, i, n, r) : HX(s)) {
      e.push(l.i / t | 0), e.push(s.i / t | 0), e.push(c.i / t | 0), ng(s), s = c.next, o = c.next;
      continue;
    }
    if (s = c, s === o) {
      a ? a === 1 ? (s = WX(Pu(s), e, t), tg(s, e, t, i, n, r, 2)) : a === 2 && XX(s, e, t, i, n, r) : tg(Pu(s), e, t, i, n, r, 1);
      break;
    }
  }
}
function HX(s) {
  const e = s.prev, t = s, i = s.next;
  if (Un(e, t, i) >= 0)
    return !1;
  const n = e.x, r = t.x, a = i.x, o = e.y, l = t.y, c = i.y, h = n < r ? n < a ? n : a : r < a ? r : a, u = o < l ? o < c ? o : c : l < c ? l : c, d = n > r ? n > a ? n : a : r > a ? r : a, f = o > l ? o > c ? o : c : l > c ? l : c;
  let p = i.next;
  for (; p !== e; ) {
    if (p.x >= h && p.x <= d && p.y >= u && p.y <= f && Af(n, o, r, l, a, c, p.x, p.y) && Un(p.prev, p, p.next) >= 0)
      return !1;
    p = p.next;
  }
  return !0;
}
function VX(s, e, t, i) {
  const n = s.prev, r = s, a = s.next;
  if (Un(n, r, a) >= 0)
    return !1;
  const o = n.x, l = r.x, c = a.x, h = n.y, u = r.y, d = a.y, f = o < l ? o < c ? o : c : l < c ? l : c, p = h < u ? h < d ? h : d : u < d ? u : d, v = o > l ? o > c ? o : c : l > c ? l : c, g = h > u ? h > d ? h : d : u > d ? u : d, m = Z6(f, p, e, t, i), x = Z6(v, g, e, t, i);
  let y = s.prevZ, A = s.nextZ;
  for (; y && y.z >= m && A && A.z <= x; ) {
    if (y.x >= f && y.x <= v && y.y >= p && y.y <= g && y !== n && y !== a && Af(o, h, l, u, c, d, y.x, y.y) && Un(y.prev, y, y.next) >= 0 || (y = y.prevZ, A.x >= f && A.x <= v && A.y >= p && A.y <= g && A !== n && A !== a && Af(o, h, l, u, c, d, A.x, A.y) && Un(A.prev, A, A.next) >= 0))
      return !1;
    A = A.nextZ;
  }
  for (; y && y.z >= m; ) {
    if (y.x >= f && y.x <= v && y.y >= p && y.y <= g && y !== n && y !== a && Af(o, h, l, u, c, d, y.x, y.y) && Un(y.prev, y, y.next) >= 0)
      return !1;
    y = y.prevZ;
  }
  for (; A && A.z <= x; ) {
    if (A.x >= f && A.x <= v && A.y >= p && A.y <= g && A !== n && A !== a && Af(o, h, l, u, c, d, A.x, A.y) && Un(A.prev, A, A.next) >= 0)
      return !1;
    A = A.nextZ;
  }
  return !0;
}
function WX(s, e, t) {
  let i = s;
  do {
    const n = i.prev, r = i.next.next;
    !_4(n, r) && hI(n, i, i.next, r) && ig(n, r) && ig(r, n) && (e.push(n.i / t | 0), e.push(i.i / t | 0), e.push(r.i / t | 0), ng(i), ng(i.next), i = s = r), i = i.next;
  } while (i !== s);
  return Pu(i);
}
function XX(s, e, t, i, n, r) {
  let a = s;
  do {
    let o = a.next.next;
    for (; o !== a.prev; ) {
      if (a.i !== o.i && ej(a, o)) {
        let l = uI(a, o);
        a = Pu(a, a.next), l = Pu(l, l.next), tg(a, e, t, i, n, r, 0), tg(l, e, t, i, n, r, 0);
        return;
      }
      o = o.next;
    }
    a = a.next;
  } while (a !== s);
}
function jX(s, e, t, i) {
  const n = [];
  let r, a, o, l, c;
  for (r = 0, a = e.length; r < a; r++)
    o = e[r] * i, l = r < a - 1 ? e[r + 1] * i : s.length, c = cI(s, o, l, i, !1), c === c.next && (c.steiner = !0), n.push($X(c));
  for (n.sort(qX), r = 0; r < n.length; r++)
    t = YX(n[r], t);
  return t;
}
function qX(s, e) {
  return s.x - e.x;
}
function YX(s, e) {
  const t = ZX(s, e);
  if (!t)
    return e;
  const i = uI(t, s);
  return Pu(i, i.next), Pu(t, t.next);
}
function ZX(s, e) {
  let t = e, i = -1 / 0, n;
  const r = s.x, a = s.y;
  do {
    if (a <= t.y && a >= t.next.y && t.next.y !== t.y) {
      const d = t.x + (a - t.y) * (t.next.x - t.x) / (t.next.y - t.y);
      if (d <= r && d > i && (i = d, n = t.x < t.next.x ? t : t.next, d === r))
        return n;
    }
    t = t.next;
  } while (t !== e);
  if (!n)
    return null;
  const o = n, l = n.x, c = n.y;
  let h = 1 / 0, u;
  t = n;
  do
    r >= t.x && t.x >= l && r !== t.x && Af(a < c ? r : i, a, l, c, a < c ? i : r, a, t.x, t.y) && (u = Math.abs(a - t.y) / (r - t.x), ig(t, s) && (u < h || u === h && (t.x > n.x || t.x === n.x && KX(n, t))) && (n = t, h = u)), t = t.next;
  while (t !== o);
  return n;
}
function KX(s, e) {
  return Un(s.prev, s, e.prev) < 0 && Un(e.next, s, s.next) < 0;
}
function JX(s, e, t, i) {
  let n = s;
  do
    n.z === 0 && (n.z = Z6(n.x, n.y, e, t, i)), n.prevZ = n.prev, n.nextZ = n.next, n = n.next;
  while (n !== s);
  n.prevZ.nextZ = null, n.prevZ = null, QX(n);
}
function QX(s) {
  let e, t, i, n, r, a, o, l, c = 1;
  do {
    for (t = s, s = null, r = null, a = 0; t; ) {
      for (a++, i = t, o = 0, e = 0; e < c && (o++, i = i.nextZ, !!i); e++)
        ;
      for (l = c; o > 0 || l > 0 && i; )
        o !== 0 && (l === 0 || !i || t.z <= i.z) ? (n = t, t = t.nextZ, o--) : (n = i, i = i.nextZ, l--), r ? r.nextZ = n : s = n, n.prevZ = r, r = n;
      t = i;
    }
    r.nextZ = null, c *= 2;
  } while (a > 1);
  return s;
}
function Z6(s, e, t, i, n) {
  return s = (s - t) * n | 0, e = (e - i) * n | 0, s = (s | s << 8) & 16711935, s = (s | s << 4) & 252645135, s = (s | s << 2) & 858993459, s = (s | s << 1) & 1431655765, e = (e | e << 8) & 16711935, e = (e | e << 4) & 252645135, e = (e | e << 2) & 858993459, e = (e | e << 1) & 1431655765, s | e << 1;
}
function $X(s) {
  let e = s, t = s;
  do
    (e.x < t.x || e.x === t.x && e.y < t.y) && (t = e), e = e.next;
  while (e !== s);
  return t;
}
function Af(s, e, t, i, n, r, a, o) {
  return (n - a) * (e - o) >= (s - a) * (r - o) && (s - a) * (i - o) >= (t - a) * (e - o) && (t - a) * (r - o) >= (n - a) * (i - o);
}
function ej(s, e) {
  return s.next.i !== e.i && s.prev.i !== e.i && !tj(s, e) && // dones't intersect other edges
  (ig(s, e) && ig(e, s) && ij(s, e) && // locally visible
  (Un(s.prev, s, e.prev) || Un(s, e.prev, e)) || // does not create opposite-facing sectors
  _4(s, e) && Un(s.prev, s, s.next) > 0 && Un(e.prev, e, e.next) > 0);
}
function Un(s, e, t) {
  return (e.y - s.y) * (t.x - e.x) - (e.x - s.x) * (t.y - e.y);
}
function _4(s, e) {
  return s.x === e.x && s.y === e.y;
}
function hI(s, e, t, i) {
  const n = Lv(Un(s, e, t)), r = Lv(Un(s, e, i)), a = Lv(Un(t, i, s)), o = Lv(Un(t, i, e));
  return !!(n !== r && a !== o || n === 0 && Dv(s, t, e) || r === 0 && Dv(s, i, e) || a === 0 && Dv(t, s, i) || o === 0 && Dv(t, e, i));
}
function Dv(s, e, t) {
  return e.x <= Math.max(s.x, t.x) && e.x >= Math.min(s.x, t.x) && e.y <= Math.max(s.y, t.y) && e.y >= Math.min(s.y, t.y);
}
function Lv(s) {
  return s > 0 ? 1 : s < 0 ? -1 : 0;
}
function tj(s, e) {
  let t = s;
  do {
    if (t.i !== s.i && t.next.i !== s.i && t.i !== e.i && t.next.i !== e.i && hI(t, t.next, s, e))
      return !0;
    t = t.next;
  } while (t !== s);
  return !1;
}
function ig(s, e) {
  return Un(s.prev, s, s.next) < 0 ? Un(s, e, s.next) >= 0 && Un(s, s.prev, e) >= 0 : Un(s, e, s.prev) < 0 || Un(s, s.next, e) < 0;
}
function ij(s, e) {
  let t = s, i = !1;
  const n = (s.x + e.x) / 2, r = (s.y + e.y) / 2;
  do
    t.y > r != t.next.y > r && t.next.y !== t.y && n < (t.next.x - t.x) * (r - t.y) / (t.next.y - t.y) + t.x && (i = !i), t = t.next;
  while (t !== s);
  return i;
}
function uI(s, e) {
  const t = new K6(s.i, s.x, s.y), i = new K6(e.i, e.x, e.y), n = s.next, r = e.prev;
  return s.next = e, e.prev = s, t.next = n, n.prev = t, i.next = t, t.prev = i, r.next = i, i.prev = r, i;
}
function JE(s, e, t, i) {
  const n = new K6(s, e, t);
  return i ? (n.next = i.next, n.prev = i, i.next.prev = n, i.next = n) : (n.prev = n, n.next = n), n;
}
function ng(s) {
  s.next.prev = s.prev, s.prev.next = s.next, s.prevZ && (s.prevZ.nextZ = s.nextZ), s.nextZ && (s.nextZ.prevZ = s.prevZ);
}
function K6(s, e, t) {
  this.i = s, this.x = e, this.y = t, this.prev = null, this.next = null, this.z = 0, this.prevZ = null, this.nextZ = null, this.steiner = !1;
}
function nj(s, e, t, i) {
  let n = 0;
  for (let r = e, a = t - i; r < t; r += i)
    n += (s[a] - s[r]) * (s[r + 1] + s[a + 1]), a = r;
  return n;
}
class ta {
  // calculate area of the contour polygon
  static area(e) {
    const t = e.length;
    let i = 0;
    for (let n = t - 1, r = 0; r < t; n = r++)
      i += e[n].x * e[r].y - e[r].x * e[n].y;
    return i * 0.5;
  }
  static isClockWise(e) {
    return ta.area(e) < 0;
  }
  static triangulateShape(e, t) {
    const i = [], n = [], r = [];
    QE(e), $E(i, e);
    let a = e.length;
    t.forEach(QE);
    for (let l = 0; l < t.length; l++)
      n.push(a), a += t[l].length, $E(i, t[l]);
    const o = GX.triangulate(i, n);
    for (let l = 0; l < o.length; l += 3)
      r.push(o.slice(l, l + 3));
    return r;
  }
}
function QE(s) {
  const e = s.length;
  e > 2 && s[e - 1].equals(s[0]) && s.pop();
}
function $E(s, e) {
  for (let t = 0; t < e.length; t++)
    s.push(e[t].x), s.push(e[t].y);
}
class c2 extends Pt {
  constructor(e = new ll([new Se(0.5, 0.5), new Se(-0.5, 0.5), new Se(-0.5, -0.5), new Se(0.5, -0.5)]), t = {}) {
    super(), this.type = "ExtrudeGeometry", this.parameters = {
      shapes: e,
      options: t
    }, e = Array.isArray(e) ? e : [e];
    const i = this, n = [], r = [];
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o];
      a(c);
    }
    this.setAttribute("position", new gt(n, 3)), this.setAttribute("uv", new gt(r, 2)), this.computeVertexNormals();
    function a(o) {
      const l = [], c = t.curveSegments !== void 0 ? t.curveSegments : 12, h = t.steps !== void 0 ? t.steps : 1, u = t.depth !== void 0 ? t.depth : 1;
      let d = t.bevelEnabled !== void 0 ? t.bevelEnabled : !0, f = t.bevelThickness !== void 0 ? t.bevelThickness : 0.2, p = t.bevelSize !== void 0 ? t.bevelSize : f - 0.1, v = t.bevelOffset !== void 0 ? t.bevelOffset : 0, g = t.bevelSegments !== void 0 ? t.bevelSegments : 3;
      const m = t.extrudePath, x = t.UVGenerator !== void 0 ? t.UVGenerator : sj;
      let y, A = !1, b, M, w, C;
      m && (y = m.getSpacedPoints(h), A = !0, d = !1, b = m.computeFrenetFrames(h, !1), M = new _(), w = new _(), C = new _()), d || (g = 0, f = 0, p = 0, v = 0);
      const T = o.extractPoints(c);
      let E = T.shape;
      const P = T.holes;
      if (!ta.isClockWise(E)) {
        E = E.reverse();
        for (let k = 0, Fe = P.length; k < Fe; k++) {
          const Ge = P[k];
          ta.isClockWise(Ge) && (P[k] = Ge.reverse());
        }
      }
      const I = ta.triangulateShape(E, P), N = E;
      for (let k = 0, Fe = P.length; k < Fe; k++) {
        const Ge = P[k];
        E = E.concat(Ge);
      }
      function H(k, Fe, Ge) {
        return Fe || console.error("THREE.ExtrudeGeometry: vec does not exist"), k.clone().addScaledVector(Fe, Ge);
      }
      const U = E.length, W = I.length;
      function X(k, Fe, Ge) {
        let qe, Ie, vt;
        const Ve = k.x - Fe.x, Ze = k.y - Fe.y, G = Ge.x - k.x, D = Ge.y - k.y, ne = Ve * Ve + Ze * Ze, ve = Ve * D - Ze * G;
        if (Math.abs(ve) > Number.EPSILON) {
          const Y = Math.sqrt(ne), ee = Math.sqrt(G * G + D * D), we = Fe.x - Ze / Y, de = Fe.y + Ve / Y, xe = Ge.x - D / ee, it = Ge.y + G / ee, q = ((xe - we) * D - (it - de) * G) / (Ve * D - Ze * G);
          qe = we + Ve * q - k.x, Ie = de + Ze * q - k.y;
          const ae = qe * qe + Ie * Ie;
          if (ae <= 2)
            return new Se(qe, Ie);
          vt = Math.sqrt(ae / 2);
        } else {
          let Y = !1;
          Ve > Number.EPSILON ? G > Number.EPSILON && (Y = !0) : Ve < -Number.EPSILON ? G < -Number.EPSILON && (Y = !0) : Math.sign(Ze) === Math.sign(D) && (Y = !0), Y ? (qe = -Ze, Ie = Ve, vt = Math.sqrt(ne)) : (qe = Ve, Ie = Ze, vt = Math.sqrt(ne / 2));
        }
        return new Se(qe / vt, Ie / vt);
      }
      const K = [];
      for (let k = 0, Fe = N.length, Ge = Fe - 1, qe = k + 1; k < Fe; k++, Ge++, qe++)
        Ge === Fe && (Ge = 0), qe === Fe && (qe = 0), K[k] = X(N[k], N[Ge], N[qe]);
      const te = [];
      let re, be = K.concat();
      for (let k = 0, Fe = P.length; k < Fe; k++) {
        const Ge = P[k];
        re = [];
        for (let qe = 0, Ie = Ge.length, vt = Ie - 1, Ve = qe + 1; qe < Ie; qe++, vt++, Ve++)
          vt === Ie && (vt = 0), Ve === Ie && (Ve = 0), re[qe] = X(Ge[qe], Ge[vt], Ge[Ve]);
        te.push(re), be = be.concat(re);
      }
      for (let k = 0; k < g; k++) {
        const Fe = k / g, Ge = f * Math.cos(Fe * Math.PI / 2), qe = p * Math.sin(Fe * Math.PI / 2) + v;
        for (let Ie = 0, vt = N.length; Ie < vt; Ie++) {
          const Ve = H(N[Ie], K[Ie], qe);
          ye(Ve.x, Ve.y, -Ge);
        }
        for (let Ie = 0, vt = P.length; Ie < vt; Ie++) {
          const Ve = P[Ie];
          re = te[Ie];
          for (let Ze = 0, G = Ve.length; Ze < G; Ze++) {
            const D = H(Ve[Ze], re[Ze], qe);
            ye(D.x, D.y, -Ge);
          }
        }
      }
      const Pe = p + v;
      for (let k = 0; k < U; k++) {
        const Fe = d ? H(E[k], be[k], Pe) : E[k];
        A ? (w.copy(b.normals[0]).multiplyScalar(Fe.x), M.copy(b.binormals[0]).multiplyScalar(Fe.y), C.copy(y[0]).add(w).add(M), ye(C.x, C.y, C.z)) : ye(Fe.x, Fe.y, 0);
      }
      for (let k = 1; k <= h; k++)
        for (let Fe = 0; Fe < U; Fe++) {
          const Ge = d ? H(E[Fe], be[Fe], Pe) : E[Fe];
          A ? (w.copy(b.normals[k]).multiplyScalar(Ge.x), M.copy(b.binormals[k]).multiplyScalar(Ge.y), C.copy(y[k]).add(w).add(M), ye(C.x, C.y, C.z)) : ye(Ge.x, Ge.y, u / h * k);
        }
      for (let k = g - 1; k >= 0; k--) {
        const Fe = k / g, Ge = f * Math.cos(Fe * Math.PI / 2), qe = p * Math.sin(Fe * Math.PI / 2) + v;
        for (let Ie = 0, vt = N.length; Ie < vt; Ie++) {
          const Ve = H(N[Ie], K[Ie], qe);
          ye(Ve.x, Ve.y, u + Ge);
        }
        for (let Ie = 0, vt = P.length; Ie < vt; Ie++) {
          const Ve = P[Ie];
          re = te[Ie];
          for (let Ze = 0, G = Ve.length; Ze < G; Ze++) {
            const D = H(Ve[Ze], re[Ze], qe);
            A ? ye(D.x, D.y + y[h - 1].y, y[h - 1].x + Ge) : ye(D.x, D.y, u + Ge);
          }
        }
      }
      J(), fe();
      function J() {
        const k = n.length / 3;
        if (d) {
          let Fe = 0, Ge = U * Fe;
          for (let qe = 0; qe < W; qe++) {
            const Ie = I[qe];
            Ye(Ie[2] + Ge, Ie[1] + Ge, Ie[0] + Ge);
          }
          Fe = h + g * 2, Ge = U * Fe;
          for (let qe = 0; qe < W; qe++) {
            const Ie = I[qe];
            Ye(Ie[0] + Ge, Ie[1] + Ge, Ie[2] + Ge);
          }
        } else {
          for (let Fe = 0; Fe < W; Fe++) {
            const Ge = I[Fe];
            Ye(Ge[2], Ge[1], Ge[0]);
          }
          for (let Fe = 0; Fe < W; Fe++) {
            const Ge = I[Fe];
            Ye(Ge[0] + U * h, Ge[1] + U * h, Ge[2] + U * h);
          }
        }
        i.addGroup(k, n.length / 3 - k, 0);
      }
      function fe() {
        const k = n.length / 3;
        let Fe = 0;
        Me(N, Fe), Fe += N.length;
        for (let Ge = 0, qe = P.length; Ge < qe; Ge++) {
          const Ie = P[Ge];
          Me(Ie, Fe), Fe += Ie.length;
        }
        i.addGroup(k, n.length / 3 - k, 1);
      }
      function Me(k, Fe) {
        let Ge = k.length;
        for (; --Ge >= 0; ) {
          const qe = Ge;
          let Ie = Ge - 1;
          Ie < 0 && (Ie = k.length - 1);
          for (let vt = 0, Ve = h + g * 2; vt < Ve; vt++) {
            const Ze = U * vt, G = U * (vt + 1), D = Fe + qe + Ze, ne = Fe + Ie + Ze, ve = Fe + Ie + G, Y = Fe + qe + G;
            Je(D, ne, ve, Y);
          }
        }
      }
      function ye(k, Fe, Ge) {
        l.push(k), l.push(Fe), l.push(Ge);
      }
      function Ye(k, Fe, Ge) {
        rt(k), rt(Fe), rt(Ge);
        const qe = n.length / 3, Ie = x.generateTopUV(i, n, qe - 3, qe - 2, qe - 1);
        wt(Ie[0]), wt(Ie[1]), wt(Ie[2]);
      }
      function Je(k, Fe, Ge, qe) {
        rt(k), rt(Fe), rt(qe), rt(Fe), rt(Ge), rt(qe);
        const Ie = n.length / 3, vt = x.generateSideWallUV(i, n, Ie - 6, Ie - 3, Ie - 2, Ie - 1);
        wt(vt[0]), wt(vt[1]), wt(vt[3]), wt(vt[1]), wt(vt[2]), wt(vt[3]);
      }
      function rt(k) {
        n.push(l[k * 3 + 0]), n.push(l[k * 3 + 1]), n.push(l[k * 3 + 2]);
      }
      function wt(k) {
        r.push(k.x), r.push(k.y);
      }
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  toJSON() {
    const e = super.toJSON(), t = this.parameters.shapes, i = this.parameters.options;
    return rj(t, i, e);
  }
  static fromJSON(e, t) {
    const i = [];
    for (let r = 0, a = e.shapes.length; r < a; r++) {
      const o = t[e.shapes[r]];
      i.push(o);
    }
    const n = e.options.extrudePath;
    return n !== void 0 && (e.options.extrudePath = new Z9[n.type]().fromJSON(n)), new c2(i, e.options);
  }
}
const sj = {
  generateTopUV: function(s, e, t, i, n) {
    const r = e[t * 3], a = e[t * 3 + 1], o = e[i * 3], l = e[i * 3 + 1], c = e[n * 3], h = e[n * 3 + 1];
    return [
      new Se(r, a),
      new Se(o, l),
      new Se(c, h)
    ];
  },
  generateSideWallUV: function(s, e, t, i, n, r) {
    const a = e[t * 3], o = e[t * 3 + 1], l = e[t * 3 + 2], c = e[i * 3], h = e[i * 3 + 1], u = e[i * 3 + 2], d = e[n * 3], f = e[n * 3 + 1], p = e[n * 3 + 2], v = e[r * 3], g = e[r * 3 + 1], m = e[r * 3 + 2];
    return Math.abs(o - h) < Math.abs(a - c) ? [
      new Se(a, 1 - l),
      new Se(c, 1 - u),
      new Se(d, 1 - p),
      new Se(v, 1 - m)
    ] : [
      new Se(o, 1 - l),
      new Se(h, 1 - u),
      new Se(f, 1 - p),
      new Se(g, 1 - m)
    ];
  }
};
function rj(s, e, t) {
  if (t.shapes = [], Array.isArray(s))
    for (let i = 0, n = s.length; i < n; i++) {
      const r = s[i];
      t.shapes.push(r.uuid);
    }
  else
    t.shapes.push(s.uuid);
  return t.options = Object.assign({}, e), e.extrudePath !== void 0 && (t.options.extrudePath = e.extrudePath.toJSON()), t;
}
class h2 extends El {
  constructor(e = 1, t = 0) {
    const i = (1 + Math.sqrt(5)) / 2, n = [
      -1,
      i,
      0,
      1,
      i,
      0,
      -1,
      -i,
      0,
      1,
      -i,
      0,
      0,
      -1,
      i,
      0,
      1,
      i,
      0,
      -1,
      -i,
      0,
      1,
      -i,
      i,
      0,
      -1,
      i,
      0,
      1,
      -i,
      0,
      -1,
      -i,
      0,
      1
    ], r = [
      0,
      11,
      5,
      0,
      5,
      1,
      0,
      1,
      7,
      0,
      7,
      10,
      0,
      10,
      11,
      1,
      5,
      9,
      5,
      11,
      4,
      11,
      10,
      2,
      10,
      7,
      6,
      7,
      1,
      8,
      3,
      9,
      4,
      3,
      4,
      2,
      3,
      2,
      6,
      3,
      6,
      8,
      3,
      8,
      9,
      4,
      9,
      5,
      2,
      4,
      11,
      6,
      2,
      10,
      8,
      6,
      7,
      9,
      8,
      1
    ];
    super(n, r, e, t), this.type = "IcosahedronGeometry", this.parameters = {
      radius: e,
      detail: t
    };
  }
  static fromJSON(e) {
    return new h2(e.radius, e.detail);
  }
}
class Nr extends El {
  constructor(e = 1, t = 0) {
    const i = [
      1,
      0,
      0,
      -1,
      0,
      0,
      0,
      1,
      0,
      0,
      -1,
      0,
      0,
      0,
      1,
      0,
      0,
      -1
    ], n = [
      0,
      2,
      4,
      0,
      4,
      3,
      0,
      3,
      5,
      0,
      5,
      2,
      1,
      2,
      5,
      1,
      5,
      3,
      1,
      3,
      4,
      1,
      4,
      2
    ];
    super(i, n, e, t), this.type = "OctahedronGeometry", this.parameters = {
      radius: e,
      detail: t
    };
  }
  static fromJSON(e) {
    return new Nr(e.radius, e.detail);
  }
}
class u2 extends Pt {
  constructor(e = 0.5, t = 1, i = 32, n = 1, r = 0, a = Math.PI * 2) {
    super(), this.type = "RingGeometry", this.parameters = {
      innerRadius: e,
      outerRadius: t,
      thetaSegments: i,
      phiSegments: n,
      thetaStart: r,
      thetaLength: a
    }, i = Math.max(3, i), n = Math.max(1, n);
    const o = [], l = [], c = [], h = [];
    let u = e;
    const d = (t - e) / n, f = new _(), p = new Se();
    for (let v = 0; v <= n; v++) {
      for (let g = 0; g <= i; g++) {
        const m = r + g / i * a;
        f.x = u * Math.cos(m), f.y = u * Math.sin(m), l.push(f.x, f.y, f.z), c.push(0, 0, 1), p.x = (f.x / t + 1) / 2, p.y = (f.y / t + 1) / 2, h.push(p.x, p.y);
      }
      u += d;
    }
    for (let v = 0; v < n; v++) {
      const g = v * (i + 1);
      for (let m = 0; m < i; m++) {
        const x = m + g, y = x, A = x + i + 1, b = x + i + 2, M = x + 1;
        o.push(y, A, M), o.push(A, b, M);
      }
    }
    this.setIndex(o), this.setAttribute("position", new gt(l, 3)), this.setAttribute("normal", new gt(c, 3)), this.setAttribute("uv", new gt(h, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new u2(e.innerRadius, e.outerRadius, e.thetaSegments, e.phiSegments, e.thetaStart, e.thetaLength);
  }
}
class d2 extends Pt {
  constructor(e = new ll([new Se(0, 0.5), new Se(-0.5, -0.5), new Se(0.5, -0.5)]), t = 12) {
    super(), this.type = "ShapeGeometry", this.parameters = {
      shapes: e,
      curveSegments: t
    };
    const i = [], n = [], r = [], a = [];
    let o = 0, l = 0;
    if (Array.isArray(e) === !1)
      c(e);
    else
      for (let h = 0; h < e.length; h++)
        c(e[h]), this.addGroup(o, l, h), o += l, l = 0;
    this.setIndex(i), this.setAttribute("position", new gt(n, 3)), this.setAttribute("normal", new gt(r, 3)), this.setAttribute("uv", new gt(a, 2));
    function c(h) {
      const u = n.length / 3, d = h.extractPoints(t);
      let f = d.shape;
      const p = d.holes;
      ta.isClockWise(f) === !1 && (f = f.reverse());
      for (let g = 0, m = p.length; g < m; g++) {
        const x = p[g];
        ta.isClockWise(x) === !0 && (p[g] = x.reverse());
      }
      const v = ta.triangulateShape(f, p);
      for (let g = 0, m = p.length; g < m; g++) {
        const x = p[g];
        f = f.concat(x);
      }
      for (let g = 0, m = f.length; g < m; g++) {
        const x = f[g];
        n.push(x.x, x.y, 0), r.push(0, 0, 1), a.push(x.x, x.y);
      }
      for (let g = 0, m = v.length; g < m; g++) {
        const x = v[g], y = x[0] + u, A = x[1] + u, b = x[2] + u;
        i.push(y, A, b), l += 3;
      }
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  toJSON() {
    const e = super.toJSON(), t = this.parameters.shapes;
    return aj(t, e);
  }
  static fromJSON(e, t) {
    const i = [];
    for (let n = 0, r = e.shapes.length; n < r; n++) {
      const a = t[e.shapes[n]];
      i.push(a);
    }
    return new d2(i, e.curveSegments);
  }
}
function aj(s, e) {
  if (e.shapes = [], Array.isArray(s))
    for (let t = 0, i = s.length; t < i; t++) {
      const n = s[t];
      e.shapes.push(n.uuid);
    }
  else
    e.shapes.push(s.uuid);
  return e;
}
class Hs extends Pt {
  constructor(e = 1, t = 32, i = 16, n = 0, r = Math.PI * 2, a = 0, o = Math.PI) {
    super(), this.type = "SphereGeometry", this.parameters = {
      radius: e,
      widthSegments: t,
      heightSegments: i,
      phiStart: n,
      phiLength: r,
      thetaStart: a,
      thetaLength: o
    }, t = Math.max(3, Math.floor(t)), i = Math.max(2, Math.floor(i));
    const l = Math.min(a + o, Math.PI);
    let c = 0;
    const h = [], u = new _(), d = new _(), f = [], p = [], v = [], g = [];
    for (let m = 0; m <= i; m++) {
      const x = [], y = m / i;
      let A = 0;
      m === 0 && a === 0 ? A = 0.5 / t : m === i && l === Math.PI && (A = -0.5 / t);
      for (let b = 0; b <= t; b++) {
        const M = b / t;
        u.x = -e * Math.cos(n + M * r) * Math.sin(a + y * o), u.y = e * Math.cos(a + y * o), u.z = e * Math.sin(n + M * r) * Math.sin(a + y * o), p.push(u.x, u.y, u.z), d.copy(u).normalize(), v.push(d.x, d.y, d.z), g.push(M + A, 1 - y), x.push(c++);
      }
      h.push(x);
    }
    for (let m = 0; m < i; m++)
      for (let x = 0; x < t; x++) {
        const y = h[m][x + 1], A = h[m][x], b = h[m + 1][x], M = h[m + 1][x + 1];
        (m !== 0 || a > 0) && f.push(y, A, M), (m !== i - 1 || l < Math.PI) && f.push(A, b, M);
      }
    this.setIndex(f), this.setAttribute("position", new gt(p, 3)), this.setAttribute("normal", new gt(v, 3)), this.setAttribute("uv", new gt(g, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new Hs(e.radius, e.widthSegments, e.heightSegments, e.phiStart, e.phiLength, e.thetaStart, e.thetaLength);
  }
}
class f2 extends El {
  constructor(e = 1, t = 0) {
    const i = [
      1,
      1,
      1,
      -1,
      -1,
      1,
      -1,
      1,
      -1,
      1,
      -1,
      -1
    ], n = [
      2,
      1,
      0,
      0,
      3,
      2,
      1,
      3,
      0,
      2,
      3,
      1
    ];
    super(i, n, e, t), this.type = "TetrahedronGeometry", this.parameters = {
      radius: e,
      detail: t
    };
  }
  static fromJSON(e) {
    return new f2(e.radius, e.detail);
  }
}
class nr extends Pt {
  constructor(e = 1, t = 0.4, i = 12, n = 48, r = Math.PI * 2) {
    super(), this.type = "TorusGeometry", this.parameters = {
      radius: e,
      tube: t,
      radialSegments: i,
      tubularSegments: n,
      arc: r
    }, i = Math.floor(i), n = Math.floor(n);
    const a = [], o = [], l = [], c = [], h = new _(), u = new _(), d = new _();
    for (let f = 0; f <= i; f++)
      for (let p = 0; p <= n; p++) {
        const v = p / n * r, g = f / i * Math.PI * 2;
        u.x = (e + t * Math.cos(g)) * Math.cos(v), u.y = (e + t * Math.cos(g)) * Math.sin(v), u.z = t * Math.sin(g), o.push(u.x, u.y, u.z), h.x = e * Math.cos(v), h.y = e * Math.sin(v), d.subVectors(u, h).normalize(), l.push(d.x, d.y, d.z), c.push(p / n), c.push(f / i);
      }
    for (let f = 1; f <= i; f++)
      for (let p = 1; p <= n; p++) {
        const v = (n + 1) * f + p - 1, g = (n + 1) * (f - 1) + p - 1, m = (n + 1) * (f - 1) + p, x = (n + 1) * f + p;
        a.push(v, g, x), a.push(g, m, x);
      }
    this.setIndex(a), this.setAttribute("position", new gt(o, 3)), this.setAttribute("normal", new gt(l, 3)), this.setAttribute("uv", new gt(c, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new nr(e.radius, e.tube, e.radialSegments, e.tubularSegments, e.arc);
  }
}
class p2 extends Pt {
  constructor(e = 1, t = 0.4, i = 64, n = 8, r = 2, a = 3) {
    super(), this.type = "TorusKnotGeometry", this.parameters = {
      radius: e,
      tube: t,
      tubularSegments: i,
      radialSegments: n,
      p: r,
      q: a
    }, i = Math.floor(i), n = Math.floor(n);
    const o = [], l = [], c = [], h = [], u = new _(), d = new _(), f = new _(), p = new _(), v = new _(), g = new _(), m = new _();
    for (let y = 0; y <= i; ++y) {
      const A = y / i * r * Math.PI * 2;
      x(A, r, a, e, f), x(A + 0.01, r, a, e, p), g.subVectors(p, f), m.addVectors(p, f), v.crossVectors(g, m), m.crossVectors(v, g), v.normalize(), m.normalize();
      for (let b = 0; b <= n; ++b) {
        const M = b / n * Math.PI * 2, w = -t * Math.cos(M), C = t * Math.sin(M);
        u.x = f.x + (w * m.x + C * v.x), u.y = f.y + (w * m.y + C * v.y), u.z = f.z + (w * m.z + C * v.z), l.push(u.x, u.y, u.z), d.subVectors(u, f).normalize(), c.push(d.x, d.y, d.z), h.push(y / i), h.push(b / n);
      }
    }
    for (let y = 1; y <= i; y++)
      for (let A = 1; A <= n; A++) {
        const b = (n + 1) * (y - 1) + (A - 1), M = (n + 1) * y + (A - 1), w = (n + 1) * y + A, C = (n + 1) * (y - 1) + A;
        o.push(b, M, C), o.push(M, w, C);
      }
    this.setIndex(o), this.setAttribute("position", new gt(l, 3)), this.setAttribute("normal", new gt(c, 3)), this.setAttribute("uv", new gt(h, 2));
    function x(y, A, b, M, w) {
      const C = Math.cos(y), T = Math.sin(y), E = b / A * y, P = Math.cos(E);
      w.x = M * (2 + P) * 0.5 * C, w.y = M * (2 + P) * T * 0.5, w.z = M * Math.sin(E) * 0.5;
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new p2(e.radius, e.tube, e.tubularSegments, e.radialSegments, e.p, e.q);
  }
}
class kc extends Pt {
  constructor(e = new qM(new _(-1, -1, 0), new _(-1, 1, 0), new _(1, 1, 0)), t = 64, i = 1, n = 8, r = !1) {
    super(), this.type = "TubeGeometry", this.parameters = {
      path: e,
      tubularSegments: t,
      radius: i,
      radialSegments: n,
      closed: r
    };
    const a = e.computeFrenetFrames(t, r);
    this.tangents = a.tangents, this.normals = a.normals, this.binormals = a.binormals;
    const o = new _(), l = new _(), c = new Se();
    let h = new _();
    const u = [], d = [], f = [], p = [];
    v(), this.setIndex(p), this.setAttribute("position", new gt(u, 3)), this.setAttribute("normal", new gt(d, 3)), this.setAttribute("uv", new gt(f, 2));
    function v() {
      for (let y = 0; y < t; y++)
        g(y);
      g(r === !1 ? t : 0), x(), m();
    }
    function g(y) {
      h = e.getPointAt(y / t, h);
      const A = a.normals[y], b = a.binormals[y];
      for (let M = 0; M <= n; M++) {
        const w = M / n * Math.PI * 2, C = Math.sin(w), T = -Math.cos(w);
        l.x = T * A.x + C * b.x, l.y = T * A.y + C * b.y, l.z = T * A.z + C * b.z, l.normalize(), d.push(l.x, l.y, l.z), o.x = h.x + i * l.x, o.y = h.y + i * l.y, o.z = h.z + i * l.z, u.push(o.x, o.y, o.z);
      }
    }
    function m() {
      for (let y = 1; y <= t; y++)
        for (let A = 1; A <= n; A++) {
          const b = (n + 1) * (y - 1) + (A - 1), M = (n + 1) * y + (A - 1), w = (n + 1) * y + A, C = (n + 1) * (y - 1) + A;
          p.push(b, M, C), p.push(M, w, C);
        }
    }
    function x() {
      for (let y = 0; y <= t; y++)
        for (let A = 0; A <= n; A++)
          c.x = y / t, c.y = A / n, f.push(c.x, c.y);
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.path = this.parameters.path.toJSON(), e;
  }
  static fromJSON(e) {
    return new kc(
      new Z9[e.path.type]().fromJSON(e.path),
      e.tubularSegments,
      e.radius,
      e.radialSegments,
      e.closed
    );
  }
}
class ZM extends Pt {
  constructor(e = null) {
    if (super(), this.type = "WireframeGeometry", this.parameters = {
      geometry: e
    }, e !== null) {
      const t = [], i = /* @__PURE__ */ new Set(), n = new _(), r = new _();
      if (e.index !== null) {
        const a = e.attributes.position, o = e.index;
        let l = e.groups;
        l.length === 0 && (l = [{ start: 0, count: o.count, materialIndex: 0 }]);
        for (let c = 0, h = l.length; c < h; ++c) {
          const u = l[c], d = u.start, f = u.count;
          for (let p = d, v = d + f; p < v; p += 3)
            for (let g = 0; g < 3; g++) {
              const m = o.getX(p + g), x = o.getX(p + (g + 1) % 3);
              n.fromBufferAttribute(a, m), r.fromBufferAttribute(a, x), eT(n, r, i) === !0 && (t.push(n.x, n.y, n.z), t.push(r.x, r.y, r.z));
            }
        }
      } else {
        const a = e.attributes.position;
        for (let o = 0, l = a.count / 3; o < l; o++)
          for (let c = 0; c < 3; c++) {
            const h = 3 * o + c, u = 3 * o + (c + 1) % 3;
            n.fromBufferAttribute(a, h), r.fromBufferAttribute(a, u), eT(n, r, i) === !0 && (t.push(n.x, n.y, n.z), t.push(r.x, r.y, r.z));
          }
      }
      this.setAttribute("position", new gt(t, 3));
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
}
function eT(s, e, t) {
  const i = `${s.x},${s.y},${s.z}-${e.x},${e.y},${e.z}`, n = `${e.x},${e.y},${e.z}-${s.x},${s.y},${s.z}`;
  return t.has(i) === !0 || t.has(n) === !0 ? !1 : (t.add(i), t.add(n), !0);
}
var tT = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  BoxGeometry: oi,
  CapsuleGeometry: _u,
  CircleGeometry: o2,
  ConeGeometry: qp,
  CylinderGeometry: Ei,
  DodecahedronGeometry: l2,
  EdgesGeometry: ap,
  ExtrudeGeometry: c2,
  IcosahedronGeometry: h2,
  LatheGeometry: jp,
  OctahedronGeometry: Nr,
  PlaneGeometry: $n,
  PolyhedronGeometry: El,
  RingGeometry: u2,
  ShapeGeometry: d2,
  SphereGeometry: Hs,
  TetrahedronGeometry: f2,
  TorusGeometry: nr,
  TorusKnotGeometry: p2,
  TubeGeometry: kc,
  WireframeGeometry: ZM
});
class dI extends Cn {
  constructor(e) {
    super(), this.isShadowMaterial = !0, this.type = "ShadowMaterial", this.color = new _e(0), this.transparent = !0, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.fog = e.fog, this;
  }
}
class KM extends Jt {
  constructor(e) {
    super(e), this.isRawShaderMaterial = !0, this.type = "RawShaderMaterial";
  }
}
class or extends Cn {
  constructor(e) {
    super(), this.isMeshStandardMaterial = !0, this.defines = { STANDARD: "" }, this.type = "MeshStandardMaterial", this.color = new _e(16777215), this.roughness = 1, this.metalness = 0, this.map = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.emissive = new _e(0), this.emissiveIntensity = 1, this.emissiveMap = null, this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.roughnessMap = null, this.metalnessMap = null, this.alphaMap = null, this.envMap = null, this.envMapRotation = new Mi(), this.envMapIntensity = 1, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.flatShading = !1, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.defines = { STANDARD: "" }, this.color.copy(e.color), this.roughness = e.roughness, this.metalness = e.metalness, this.map = e.map, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.emissive.copy(e.emissive), this.emissiveMap = e.emissiveMap, this.emissiveIntensity = e.emissiveIntensity, this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.roughnessMap = e.roughnessMap, this.metalnessMap = e.metalnessMap, this.alphaMap = e.alphaMap, this.envMap = e.envMap, this.envMapRotation.copy(e.envMapRotation), this.envMapIntensity = e.envMapIntensity, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.flatShading = e.flatShading, this.fog = e.fog, this;
  }
}
class nn extends or {
  constructor(e) {
    super(), this.isMeshPhysicalMaterial = !0, this.defines = {
      STANDARD: "",
      PHYSICAL: ""
    }, this.type = "MeshPhysicalMaterial", this.anisotropyRotation = 0, this.anisotropyMap = null, this.clearcoatMap = null, this.clearcoatRoughness = 0, this.clearcoatRoughnessMap = null, this.clearcoatNormalScale = new Se(1, 1), this.clearcoatNormalMap = null, this.ior = 1.5, Object.defineProperty(this, "reflectivity", {
      get: function() {
        return Wn(2.5 * (this.ior - 1) / (this.ior + 1), 0, 1);
      },
      set: function(t) {
        this.ior = (1 + 0.4 * t) / (1 - 0.4 * t);
      }
    }), this.iridescenceMap = null, this.iridescenceIOR = 1.3, this.iridescenceThicknessRange = [100, 400], this.iridescenceThicknessMap = null, this.sheenColor = new _e(0), this.sheenColorMap = null, this.sheenRoughness = 1, this.sheenRoughnessMap = null, this.transmissionMap = null, this.thickness = 0, this.thicknessMap = null, this.attenuationDistance = 1 / 0, this.attenuationColor = new _e(1, 1, 1), this.specularIntensity = 1, this.specularIntensityMap = null, this.specularColor = new _e(1, 1, 1), this.specularColorMap = null, this._anisotropy = 0, this._clearcoat = 0, this._dispersion = 0, this._iridescence = 0, this._sheen = 0, this._transmission = 0, this.setValues(e);
  }
  get anisotropy() {
    return this._anisotropy;
  }
  set anisotropy(e) {
    this._anisotropy > 0 != e > 0 && this.version++, this._anisotropy = e;
  }
  get clearcoat() {
    return this._clearcoat;
  }
  set clearcoat(e) {
    this._clearcoat > 0 != e > 0 && this.version++, this._clearcoat = e;
  }
  get iridescence() {
    return this._iridescence;
  }
  set iridescence(e) {
    this._iridescence > 0 != e > 0 && this.version++, this._iridescence = e;
  }
  get dispersion() {
    return this._dispersion;
  }
  set dispersion(e) {
    this._dispersion > 0 != e > 0 && this.version++, this._dispersion = e;
  }
  get sheen() {
    return this._sheen;
  }
  set sheen(e) {
    this._sheen > 0 != e > 0 && this.version++, this._sheen = e;
  }
  get transmission() {
    return this._transmission;
  }
  set transmission(e) {
    this._transmission > 0 != e > 0 && this.version++, this._transmission = e;
  }
  copy(e) {
    return super.copy(e), this.defines = {
      STANDARD: "",
      PHYSICAL: ""
    }, this.anisotropy = e.anisotropy, this.anisotropyRotation = e.anisotropyRotation, this.anisotropyMap = e.anisotropyMap, this.clearcoat = e.clearcoat, this.clearcoatMap = e.clearcoatMap, this.clearcoatRoughness = e.clearcoatRoughness, this.clearcoatRoughnessMap = e.clearcoatRoughnessMap, this.clearcoatNormalMap = e.clearcoatNormalMap, this.clearcoatNormalScale.copy(e.clearcoatNormalScale), this.dispersion = e.dispersion, this.ior = e.ior, this.iridescence = e.iridescence, this.iridescenceMap = e.iridescenceMap, this.iridescenceIOR = e.iridescenceIOR, this.iridescenceThicknessRange = [...e.iridescenceThicknessRange], this.iridescenceThicknessMap = e.iridescenceThicknessMap, this.sheen = e.sheen, this.sheenColor.copy(e.sheenColor), this.sheenColorMap = e.sheenColorMap, this.sheenRoughness = e.sheenRoughness, this.sheenRoughnessMap = e.sheenRoughnessMap, this.transmission = e.transmission, this.transmissionMap = e.transmissionMap, this.thickness = e.thickness, this.thicknessMap = e.thicknessMap, this.attenuationDistance = e.attenuationDistance, this.attenuationColor.copy(e.attenuationColor), this.specularIntensity = e.specularIntensity, this.specularIntensityMap = e.specularIntensityMap, this.specularColor.copy(e.specularColor), this.specularColorMap = e.specularColorMap, this;
  }
}
class m2 extends Cn {
  constructor(e) {
    super(), this.isMeshPhongMaterial = !0, this.type = "MeshPhongMaterial", this.color = new _e(16777215), this.specular = new _e(1118481), this.shininess = 30, this.map = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.emissive = new _e(0), this.emissiveIntensity = 1, this.emissiveMap = null, this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.specularMap = null, this.alphaMap = null, this.envMap = null, this.envMapRotation = new Mi(), this.combine = e2, this.reflectivity = 1, this.refractionRatio = 0.98, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.flatShading = !1, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.specular.copy(e.specular), this.shininess = e.shininess, this.map = e.map, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.emissive.copy(e.emissive), this.emissiveMap = e.emissiveMap, this.emissiveIntensity = e.emissiveIntensity, this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.specularMap = e.specularMap, this.alphaMap = e.alphaMap, this.envMap = e.envMap, this.envMapRotation.copy(e.envMapRotation), this.combine = e.combine, this.reflectivity = e.reflectivity, this.refractionRatio = e.refractionRatio, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.flatShading = e.flatShading, this.fog = e.fog, this;
  }
}
class fI extends Cn {
  constructor(e) {
    super(), this.isMeshToonMaterial = !0, this.defines = { TOON: "" }, this.type = "MeshToonMaterial", this.color = new _e(16777215), this.map = null, this.gradientMap = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.emissive = new _e(0), this.emissiveIntensity = 1, this.emissiveMap = null, this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.alphaMap = null, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.gradientMap = e.gradientMap, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.emissive.copy(e.emissive), this.emissiveMap = e.emissiveMap, this.emissiveIntensity = e.emissiveIntensity, this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.alphaMap = e.alphaMap, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.fog = e.fog, this;
  }
}
class P4 extends Cn {
  constructor(e) {
    super(), this.isMeshNormalMaterial = !0, this.type = "MeshNormalMaterial", this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.wireframe = !1, this.wireframeLinewidth = 1, this.flatShading = !1, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.flatShading = e.flatShading, this;
  }
}
class pI extends Cn {
  constructor(e) {
    super(), this.isMeshLambertMaterial = !0, this.type = "MeshLambertMaterial", this.color = new _e(16777215), this.map = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.emissive = new _e(0), this.emissiveIntensity = 1, this.emissiveMap = null, this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.specularMap = null, this.alphaMap = null, this.envMap = null, this.envMapRotation = new Mi(), this.combine = e2, this.reflectivity = 1, this.refractionRatio = 0.98, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.flatShading = !1, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.emissive.copy(e.emissive), this.emissiveMap = e.emissiveMap, this.emissiveIntensity = e.emissiveIntensity, this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.specularMap = e.specularMap, this.alphaMap = e.alphaMap, this.envMap = e.envMap, this.envMapRotation.copy(e.envMapRotation), this.combine = e.combine, this.reflectivity = e.reflectivity, this.refractionRatio = e.refractionRatio, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.flatShading = e.flatShading, this.fog = e.fog, this;
  }
}
class mI extends Cn {
  constructor(e) {
    super(), this.isMeshMatcapMaterial = !0, this.defines = { MATCAP: "" }, this.type = "MeshMatcapMaterial", this.color = new _e(16777215), this.matcap = null, this.map = null, this.bumpMap = null, this.bumpScale = 1, this.normalMap = null, this.normalMapType = eh, this.normalScale = new Se(1, 1), this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.alphaMap = null, this.flatShading = !1, this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.defines = { MATCAP: "" }, this.color.copy(e.color), this.matcap = e.matcap, this.map = e.map, this.bumpMap = e.bumpMap, this.bumpScale = e.bumpScale, this.normalMap = e.normalMap, this.normalMapType = e.normalMapType, this.normalScale.copy(e.normalScale), this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.alphaMap = e.alphaMap, this.flatShading = e.flatShading, this.fog = e.fog, this;
  }
}
class gI extends Bi {
  constructor(e) {
    super(), this.isLineDashedMaterial = !0, this.type = "LineDashedMaterial", this.scale = 1, this.dashSize = 3, this.gapSize = 1, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.scale = e.scale, this.dashSize = e.dashSize, this.gapSize = e.gapSize, this;
  }
}
function du(s, e, t) {
  return !s || // let 'undefined' and 'null' pass
  !t && s.constructor === e ? s : typeof e.BYTES_PER_ELEMENT == "number" ? new e(s) : Array.prototype.slice.call(s);
}
function vI(s) {
  return ArrayBuffer.isView(s) && !(s instanceof DataView);
}
function yI(s) {
  function e(n, r) {
    return s[n] - s[r];
  }
  const t = s.length, i = new Array(t);
  for (let n = 0; n !== t; ++n)
    i[n] = n;
  return i.sort(e), i;
}
function J6(s, e, t) {
  const i = s.length, n = new s.constructor(i);
  for (let r = 0, a = 0; a !== i; ++r) {
    const o = t[r] * e;
    for (let l = 0; l !== e; ++l)
      n[a++] = s[o + l];
  }
  return n;
}
function JM(s, e, t, i) {
  let n = 1, r = s[0];
  for (; r !== void 0 && r[i] === void 0; )
    r = s[n++];
  if (r === void 0)
    return;
  let a = r[i];
  if (a !== void 0)
    if (Array.isArray(a))
      do
        a = r[i], a !== void 0 && (e.push(r.time), t.push.apply(t, a)), r = s[n++];
      while (r !== void 0);
    else if (a.toArray !== void 0)
      do
        a = r[i], a !== void 0 && (e.push(r.time), a.toArray(t, t.length)), r = s[n++];
      while (r !== void 0);
    else
      do
        a = r[i], a !== void 0 && (e.push(r.time), t.push(a)), r = s[n++];
      while (r !== void 0);
}
function oj(s, e, t, i, n = 30) {
  const r = s.clone();
  r.name = e;
  const a = [];
  for (let l = 0; l < r.tracks.length; ++l) {
    const c = r.tracks[l], h = c.getValueSize(), u = [], d = [];
    for (let f = 0; f < c.times.length; ++f) {
      const p = c.times[f] * n;
      if (!(p < t || p >= i)) {
        u.push(c.times[f]);
        for (let v = 0; v < h; ++v)
          d.push(c.values[f * h + v]);
      }
    }
    u.length !== 0 && (c.times = du(u, c.times.constructor), c.values = du(d, c.values.constructor), a.push(c));
  }
  r.tracks = a;
  let o = 1 / 0;
  for (let l = 0; l < r.tracks.length; ++l)
    o > r.tracks[l].times[0] && (o = r.tracks[l].times[0]);
  for (let l = 0; l < r.tracks.length; ++l)
    r.tracks[l].shift(-1 * o);
  return r.resetDuration(), r;
}
function lj(s, e = 0, t = s, i = 30) {
  i <= 0 && (i = 30);
  const n = t.tracks.length, r = e / i;
  for (let a = 0; a < n; ++a) {
    const o = t.tracks[a], l = o.ValueTypeName;
    if (l === "bool" || l === "string")
      continue;
    const c = s.tracks.find(function(m) {
      return m.name === o.name && m.ValueTypeName === l;
    });
    if (c === void 0)
      continue;
    let h = 0;
    const u = o.getValueSize();
    o.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline && (h = u / 3);
    let d = 0;
    const f = c.getValueSize();
    c.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline && (d = f / 3);
    const p = o.times.length - 1;
    let v;
    if (r <= o.times[0]) {
      const m = h, x = u - h;
      v = o.values.slice(m, x);
    } else if (r >= o.times[p]) {
      const m = p * u + h, x = m + u - h;
      v = o.values.slice(m, x);
    } else {
      const m = o.createInterpolant(), x = h, y = u - h;
      m.evaluate(r), v = m.resultBuffer.slice(x, y);
    }
    l === "quaternion" && new St().fromArray(v).normalize().conjugate().toArray(v);
    const g = c.times.length;
    for (let m = 0; m < g; ++m) {
      const x = m * f + d;
      if (l === "quaternion")
        St.multiplyQuaternionsFlat(
          c.values,
          x,
          v,
          0,
          c.values,
          x
        );
      else {
        const y = f - d * 2;
        for (let A = 0; A < y; ++A)
          c.values[x + A] -= v[A];
      }
    }
  }
  return s.blendMode = DM, s;
}
const Q6 = {
  convertArray: du,
  isTypedArray: vI,
  getKeyframeOrder: yI,
  sortedArray: J6,
  flattenJSON: JM,
  subclip: oj,
  makeClipAdditive: lj
};
class Is {
  constructor(e, t, i, n) {
    this.parameterPositions = e, this._cachedIndex = 0, this.resultBuffer = n !== void 0 ? n : new t.constructor(i), this.sampleValues = t, this.valueSize = i, this.settings = null, this.DefaultSettings_ = {};
  }
  evaluate(e) {
    const t = this.parameterPositions;
    let i = this._cachedIndex, n = t[i], r = t[i - 1];
    e: {
      t: {
        let a;
        i: {
          n:
            if (!(e < n)) {
              for (let o = i + 2; ; ) {
                if (n === void 0) {
                  if (e < r)
                    break n;
                  return i = t.length, this._cachedIndex = i, this.copySampleValue_(i - 1);
                }
                if (i === o)
                  break;
                if (r = n, n = t[++i], e < n)
                  break t;
              }
              a = t.length;
              break i;
            }
          if (!(e >= r)) {
            const o = t[1];
            e < o && (i = 2, r = o);
            for (let l = i - 2; ; ) {
              if (r === void 0)
                return this._cachedIndex = 0, this.copySampleValue_(0);
              if (i === l)
                break;
              if (n = r, r = t[--i - 1], e >= r)
                break t;
            }
            a = i, i = 0;
            break i;
          }
          break e;
        }
        for (; i < a; ) {
          const o = i + a >>> 1;
          e < t[o] ? a = o : i = o + 1;
        }
        if (n = t[i], r = t[i - 1], r === void 0)
          return this._cachedIndex = 0, this.copySampleValue_(0);
        if (n === void 0)
          return i = t.length, this._cachedIndex = i, this.copySampleValue_(i - 1);
      }
      this._cachedIndex = i, this.intervalChanged_(i, r, n);
    }
    return this.interpolate_(i, r, e, n);
  }
  getSettings_() {
    return this.settings || this.DefaultSettings_;
  }
  copySampleValue_(e) {
    const t = this.resultBuffer, i = this.sampleValues, n = this.valueSize, r = e * n;
    for (let a = 0; a !== n; ++a)
      t[a] = i[r + a];
    return t;
  }
  // Template methods for derived classes:
  interpolate_() {
    throw new Error("call to abstract method");
  }
  intervalChanged_() {
  }
}
let xI = class extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n), this._weightPrev = -0, this._offsetPrev = -0, this._weightNext = -0, this._offsetNext = -0, this.DefaultSettings_ = {
      endingStart: hu,
      endingEnd: hu
    };
  }
  intervalChanged_(e, t, i) {
    const n = this.parameterPositions;
    let r = e - 2, a = e + 1, o = n[r], l = n[a];
    if (o === void 0)
      switch (this.getSettings_().endingStart) {
        case uu:
          r = e, o = 2 * t - i;
          break;
        case q1:
          r = n.length - 2, o = t + n[r] - n[r + 1];
          break;
        default:
          r = e, o = i;
      }
    if (l === void 0)
      switch (this.getSettings_().endingEnd) {
        case uu:
          a = e, l = 2 * i - t;
          break;
        case q1:
          a = 1, l = i + n[1] - n[0];
          break;
        default:
          a = e - 1, l = t;
      }
    const c = (i - t) * 0.5, h = this.valueSize;
    this._weightPrev = c / (t - o), this._weightNext = c / (l - i), this._offsetPrev = r * h, this._offsetNext = a * h;
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = e * o, c = l - o, h = this._offsetPrev, u = this._offsetNext, d = this._weightPrev, f = this._weightNext, p = (i - t) / (n - t), v = p * p, g = v * p, m = -d * g + 2 * d * v - d * p, x = (1 + d) * g + (-1.5 - 2 * d) * v + (-0.5 + d) * p + 1, y = (-1 - f) * g + (1.5 + f) * v + 0.5 * p, A = f * g - f * v;
    for (let b = 0; b !== o; ++b)
      r[b] = m * a[h + b] + x * a[c + b] + y * a[l + b] + A * a[u + b];
    return r;
  }
};
class sg extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = e * o, c = l - o, h = (i - t) / (n - t), u = 1 - h;
    for (let d = 0; d !== o; ++d)
      r[d] = a[c + d] * u + a[l + d] * h;
    return r;
  }
}
class AI extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  interpolate_(e) {
    return this.copySampleValue_(e - 1);
  }
}
class yo {
  constructor(e, t, i, n) {
    if (e === void 0)
      throw new Error("THREE.KeyframeTrack: track name is undefined");
    if (t === void 0 || t.length === 0)
      throw new Error("THREE.KeyframeTrack: no keyframes in track named " + e);
    this.name = e, this.times = du(t, this.TimeBufferType), this.values = du(i, this.ValueBufferType), this.setInterpolation(n || this.DefaultInterpolation);
  }
  // Serialization (in static context, because of constructor invocation
  // and automatic invocation of .toJSON):
  static toJSON(e) {
    const t = e.constructor;
    let i;
    if (t.toJSON !== this.toJSON)
      i = t.toJSON(e);
    else {
      i = {
        name: e.name,
        times: du(e.times, Array),
        values: du(e.values, Array)
      };
      const n = e.getInterpolation();
      n !== e.DefaultInterpolation && (i.interpolation = n);
    }
    return i.type = e.ValueTypeName, i;
  }
  InterpolantFactoryMethodDiscrete(e) {
    return new AI(this.times, this.values, this.getValueSize(), e);
  }
  InterpolantFactoryMethodLinear(e) {
    return new sg(this.times, this.values, this.getValueSize(), e);
  }
  InterpolantFactoryMethodSmooth(e) {
    return new xI(this.times, this.values, this.getValueSize(), e);
  }
  setInterpolation(e) {
    let t;
    switch (e) {
      case Uc:
        t = this.InterpolantFactoryMethodDiscrete;
        break;
      case l0:
        t = this.InterpolantFactoryMethodLinear;
        break;
      case Sy:
        t = this.InterpolantFactoryMethodSmooth;
        break;
    }
    if (t === void 0) {
      const i = "unsupported interpolation for " + this.ValueTypeName + " keyframe track named " + this.name;
      if (this.createInterpolant === void 0)
        if (e !== this.DefaultInterpolation)
          this.setInterpolation(this.DefaultInterpolation);
        else
          throw new Error(i);
      return console.warn("THREE.KeyframeTrack:", i), this;
    }
    return this.createInterpolant = t, this;
  }
  getInterpolation() {
    switch (this.createInterpolant) {
      case this.InterpolantFactoryMethodDiscrete:
        return Uc;
      case this.InterpolantFactoryMethodLinear:
        return l0;
      case this.InterpolantFactoryMethodSmooth:
        return Sy;
    }
  }
  getValueSize() {
    return this.values.length / this.times.length;
  }
  // move all keyframes either forwards or backwards in time
  shift(e) {
    if (e !== 0) {
      const t = this.times;
      for (let i = 0, n = t.length; i !== n; ++i)
        t[i] += e;
    }
    return this;
  }
  // scale all keyframe times by a factor (useful for frame <-> seconds conversions)
  scale(e) {
    if (e !== 1) {
      const t = this.times;
      for (let i = 0, n = t.length; i !== n; ++i)
        t[i] *= e;
    }
    return this;
  }
  // removes keyframes before and after animation without changing any values within the range [startTime, endTime].
  // IMPORTANT: We do not shift around keys to the start of the track time, because for interpolated keys this will change their values
  trim(e, t) {
    const i = this.times, n = i.length;
    let r = 0, a = n - 1;
    for (; r !== n && i[r] < e; )
      ++r;
    for (; a !== -1 && i[a] > t; )
      --a;
    if (++a, r !== 0 || a !== n) {
      r >= a && (a = Math.max(a, 1), r = a - 1);
      const o = this.getValueSize();
      this.times = i.slice(r, a), this.values = this.values.slice(r * o, a * o);
    }
    return this;
  }
  // ensure we do not get a GarbageInGarbageOut situation, make sure tracks are at least minimally viable
  validate() {
    let e = !0;
    const t = this.getValueSize();
    t - Math.floor(t) !== 0 && (console.error("THREE.KeyframeTrack: Invalid value size in track.", this), e = !1);
    const i = this.times, n = this.values, r = i.length;
    r === 0 && (console.error("THREE.KeyframeTrack: Track is empty.", this), e = !1);
    let a = null;
    for (let o = 0; o !== r; o++) {
      const l = i[o];
      if (typeof l == "number" && isNaN(l)) {
        console.error("THREE.KeyframeTrack: Time is not a valid number.", this, o, l), e = !1;
        break;
      }
      if (a !== null && a > l) {
        console.error("THREE.KeyframeTrack: Out of order keys.", this, o, l, a), e = !1;
        break;
      }
      a = l;
    }
    if (n !== void 0 && vI(n))
      for (let o = 0, l = n.length; o !== l; ++o) {
        const c = n[o];
        if (isNaN(c)) {
          console.error("THREE.KeyframeTrack: Value is not a valid number.", this, o, c), e = !1;
          break;
        }
      }
    return e;
  }
  // removes equivalent sequential keys as common in morph target sequences
  // (0,0,0,0,1,1,1,0,0,0,0,0,0,0) --> (0,0,1,1,0,0)
  optimize() {
    const e = this.times.slice(), t = this.values.slice(), i = this.getValueSize(), n = this.getInterpolation() === Sy, r = e.length - 1;
    let a = 1;
    for (let o = 1; o < r; ++o) {
      let l = !1;
      const c = e[o], h = e[o + 1];
      if (c !== h && (o !== 1 || c !== e[0]))
        if (n)
          l = !0;
        else {
          const u = o * i, d = u - i, f = u + i;
          for (let p = 0; p !== i; ++p) {
            const v = t[u + p];
            if (v !== t[d + p] || v !== t[f + p]) {
              l = !0;
              break;
            }
          }
        }
      if (l) {
        if (o !== a) {
          e[a] = e[o];
          const u = o * i, d = a * i;
          for (let f = 0; f !== i; ++f)
            t[d + f] = t[u + f];
        }
        ++a;
      }
    }
    if (r > 0) {
      e[a] = e[r];
      for (let o = r * i, l = a * i, c = 0; c !== i; ++c)
        t[l + c] = t[o + c];
      ++a;
    }
    return a !== e.length ? (this.times = e.slice(0, a), this.values = t.slice(0, a * i)) : (this.times = e, this.values = t), this;
  }
  clone() {
    const e = this.times.slice(), t = this.values.slice(), i = this.constructor, n = new i(this.name, e, t);
    return n.createInterpolant = this.createInterpolant, n;
  }
}
yo.prototype.TimeBufferType = Float32Array;
yo.prototype.ValueBufferType = Float32Array;
yo.prototype.DefaultInterpolation = l0;
class Yu extends yo {
  // No interpolation parameter because only InterpolateDiscrete is valid.
  constructor(e, t, i) {
    super(e, t, i);
  }
}
Yu.prototype.ValueTypeName = "bool";
Yu.prototype.ValueBufferType = Array;
Yu.prototype.DefaultInterpolation = Uc;
Yu.prototype.InterpolantFactoryMethodLinear = void 0;
Yu.prototype.InterpolantFactoryMethodSmooth = void 0;
class QM extends yo {
}
QM.prototype.ValueTypeName = "color";
class uo extends yo {
}
uo.prototype.ValueTypeName = "number";
class bI extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = (i - t) / (n - t);
    let c = e * o;
    for (let h = c + o; c !== h; c += 4)
      St.slerpFlat(r, 0, a, c - o, a, c, l);
    return r;
  }
}
class fo extends yo {
  InterpolantFactoryMethodLinear(e) {
    return new bI(this.times, this.values, this.getValueSize(), e);
  }
}
fo.prototype.ValueTypeName = "quaternion";
fo.prototype.InterpolantFactoryMethodSmooth = void 0;
class Zu extends yo {
  // No interpolation parameter because only InterpolateDiscrete is valid.
  constructor(e, t, i) {
    super(e, t, i);
  }
}
Zu.prototype.ValueTypeName = "string";
Zu.prototype.ValueBufferType = Array;
Zu.prototype.DefaultInterpolation = Uc;
Zu.prototype.InterpolantFactoryMethodLinear = void 0;
Zu.prototype.InterpolantFactoryMethodSmooth = void 0;
class po extends yo {
}
po.prototype.ValueTypeName = "vector";
class xl {
  constructor(e = "", t = -1, i = [], n = x4) {
    this.name = e, this.tracks = i, this.duration = t, this.blendMode = n, this.uuid = ea(), this.duration < 0 && this.resetDuration();
  }
  static parse(e) {
    const t = [], i = e.tracks, n = 1 / (e.fps || 1);
    for (let a = 0, o = i.length; a !== o; ++a)
      t.push(hj(i[a]).scale(n));
    const r = new this(e.name, e.duration, t, e.blendMode);
    return r.uuid = e.uuid, r;
  }
  static toJSON(e) {
    const t = [], i = e.tracks, n = {
      name: e.name,
      duration: e.duration,
      tracks: t,
      uuid: e.uuid,
      blendMode: e.blendMode
    };
    for (let r = 0, a = i.length; r !== a; ++r)
      t.push(yo.toJSON(i[r]));
    return n;
  }
  static CreateFromMorphTargetSequence(e, t, i, n) {
    const r = t.length, a = [];
    for (let o = 0; o < r; o++) {
      let l = [], c = [];
      l.push(
        (o + r - 1) % r,
        o,
        (o + 1) % r
      ), c.push(0, 1, 0);
      const h = yI(l);
      l = J6(l, 1, h), c = J6(c, 1, h), !n && l[0] === 0 && (l.push(r), c.push(c[0])), a.push(
        new uo(
          ".morphTargetInfluences[" + t[o].name + "]",
          l,
          c
        ).scale(1 / i)
      );
    }
    return new this(e, -1, a);
  }
  static findByName(e, t) {
    let i = e;
    if (!Array.isArray(e)) {
      const n = e;
      i = n.geometry && n.geometry.animations || n.animations;
    }
    for (let n = 0; n < i.length; n++)
      if (i[n].name === t)
        return i[n];
    return null;
  }
  static CreateClipsFromMorphTargetSequences(e, t, i) {
    const n = {}, r = /^([\w-]*?)([\d]+)$/;
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o], h = c.name.match(r);
      if (h && h.length > 1) {
        const u = h[1];
        let d = n[u];
        d || (n[u] = d = []), d.push(c);
      }
    }
    const a = [];
    for (const o in n)
      a.push(this.CreateFromMorphTargetSequence(o, n[o], t, i));
    return a;
  }
  // parse the animation.hierarchy format
  static parseAnimation(e, t) {
    if (!e)
      return console.error("THREE.AnimationClip: No animation in JSONLoader data."), null;
    const i = function(u, d, f, p, v) {
      if (f.length !== 0) {
        const g = [], m = [];
        JM(f, g, m, p), g.length !== 0 && v.push(new u(d, g, m));
      }
    }, n = [], r = e.name || "default", a = e.fps || 30, o = e.blendMode;
    let l = e.length || -1;
    const c = e.hierarchy || [];
    for (let u = 0; u < c.length; u++) {
      const d = c[u].keys;
      if (!(!d || d.length === 0))
        if (d[0].morphTargets) {
          const f = {};
          let p;
          for (p = 0; p < d.length; p++)
            if (d[p].morphTargets)
              for (let v = 0; v < d[p].morphTargets.length; v++)
                f[d[p].morphTargets[v]] = -1;
          for (const v in f) {
            const g = [], m = [];
            for (let x = 0; x !== d[p].morphTargets.length; ++x) {
              const y = d[p];
              g.push(y.time), m.push(y.morphTarget === v ? 1 : 0);
            }
            n.push(new uo(".morphTargetInfluence[" + v + "]", g, m));
          }
          l = f.length * a;
        } else {
          const f = ".bones[" + t[u].name + "]";
          i(
            po,
            f + ".position",
            d,
            "pos",
            n
          ), i(
            fo,
            f + ".quaternion",
            d,
            "rot",
            n
          ), i(
            po,
            f + ".scale",
            d,
            "scl",
            n
          );
        }
    }
    return n.length === 0 ? null : new this(r, l, n, o);
  }
  resetDuration() {
    const e = this.tracks;
    let t = 0;
    for (let i = 0, n = e.length; i !== n; ++i) {
      const r = this.tracks[i];
      t = Math.max(t, r.times[r.times.length - 1]);
    }
    return this.duration = t, this;
  }
  trim() {
    for (let e = 0; e < this.tracks.length; e++)
      this.tracks[e].trim(0, this.duration);
    return this;
  }
  validate() {
    let e = !0;
    for (let t = 0; t < this.tracks.length; t++)
      e = e && this.tracks[t].validate();
    return e;
  }
  optimize() {
    for (let e = 0; e < this.tracks.length; e++)
      this.tracks[e].optimize();
    return this;
  }
  clone() {
    const e = [];
    for (let t = 0; t < this.tracks.length; t++)
      e.push(this.tracks[t].clone());
    return new this.constructor(this.name, this.duration, e, this.blendMode);
  }
  toJSON() {
    return this.constructor.toJSON(this);
  }
}
function cj(s) {
  switch (s.toLowerCase()) {
    case "scalar":
    case "double":
    case "float":
    case "number":
    case "integer":
      return uo;
    case "vector":
    case "vector2":
    case "vector3":
    case "vector4":
      return po;
    case "color":
      return QM;
    case "quaternion":
      return fo;
    case "bool":
    case "boolean":
      return Yu;
    case "string":
      return Zu;
  }
  throw new Error("THREE.KeyframeTrack: Unsupported typeName: " + s);
}
function hj(s) {
  if (s.type === void 0)
    throw new Error("THREE.KeyframeTrack: track type undefined, can not parse");
  const e = cj(s.type);
  if (s.times === void 0) {
    const t = [], i = [];
    JM(s.keys, t, i, "value"), s.times = t, s.values = i;
  }
  return e.parse !== void 0 ? e.parse(s) : new e(s.name, s.times, s.values, s.interpolation);
}
const tl = {
  enabled: !1,
  files: {},
  add: function(s, e) {
    this.enabled !== !1 && (this.files[s] = e);
  },
  get: function(s) {
    if (this.enabled !== !1)
      return this.files[s];
  },
  remove: function(s) {
    delete this.files[s];
  },
  clear: function() {
    this.files = {};
  }
};
class g2 {
  constructor(e, t, i) {
    const n = this;
    let r = !1, a = 0, o = 0, l;
    const c = [];
    this.onStart = void 0, this.onLoad = e, this.onProgress = t, this.onError = i, this.itemStart = function(h) {
      o++, r === !1 && n.onStart !== void 0 && n.onStart(h, a, o), r = !0;
    }, this.itemEnd = function(h) {
      a++, n.onProgress !== void 0 && n.onProgress(h, a, o), a === o && (r = !1, n.onLoad !== void 0 && n.onLoad());
    }, this.itemError = function(h) {
      n.onError !== void 0 && n.onError(h);
    }, this.resolveURL = function(h) {
      return l ? l(h) : h;
    }, this.setURLModifier = function(h) {
      return l = h, this;
    }, this.addHandler = function(h, u) {
      return c.push(h, u), this;
    }, this.removeHandler = function(h) {
      const u = c.indexOf(h);
      return u !== -1 && c.splice(u, 2), this;
    }, this.getHandler = function(h) {
      for (let u = 0, d = c.length; u < d; u += 2) {
        const f = c[u], p = c[u + 1];
        if (f.global && (f.lastIndex = 0), f.test(h))
          return p;
      }
      return null;
    };
  }
}
const Ku = /* @__PURE__ */ new g2();
let As = class {
  constructor(e) {
    this.manager = e !== void 0 ? e : Ku, this.crossOrigin = "anonymous", this.withCredentials = !1, this.path = "", this.resourcePath = "", this.requestHeader = {};
  }
  load() {
  }
  loadAsync(e, t) {
    const i = this;
    return new Promise(function(n, r) {
      i.load(e, n, t, r);
    });
  }
  parse() {
  }
  setCrossOrigin(e) {
    return this.crossOrigin = e, this;
  }
  setWithCredentials(e) {
    return this.withCredentials = e, this;
  }
  setPath(e) {
    return this.path = e, this;
  }
  setResourcePath(e) {
    return this.resourcePath = e, this;
  }
  setRequestHeader(e) {
    return this.requestHeader = e, this;
  }
};
As.DEFAULT_MATERIAL_NAME = "__DEFAULT";
const _0 = {};
class uj extends Error {
  constructor(e, t) {
    super(e), this.response = t;
  }
}
class Nn extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    e === void 0 && (e = ""), this.path !== void 0 && (e = this.path + e), e = this.manager.resolveURL(e);
    const r = tl.get(e);
    if (r !== void 0)
      return this.manager.itemStart(e), setTimeout(() => {
        t && t(r), this.manager.itemEnd(e);
      }, 0), r;
    if (_0[e] !== void 0) {
      _0[e].push({
        onLoad: t,
        onProgress: i,
        onError: n
      });
      return;
    }
    _0[e] = [], _0[e].push({
      onLoad: t,
      onProgress: i,
      onError: n
    });
    const a = new Request(e, {
      headers: new Headers(this.requestHeader),
      credentials: this.withCredentials ? "include" : "same-origin"
      // An abort controller could be added within a future PR
    }), o = this.mimeType, l = this.responseType;
    fetch(a).then((c) => {
      if (c.status === 200 || c.status === 0) {
        if (c.status === 0 && console.warn("THREE.FileLoader: HTTP Status 0 received."), typeof ReadableStream > "u" || c.body === void 0 || c.body.getReader === void 0)
          return c;
        const h = _0[e], u = c.body.getReader(), d = c.headers.get("X-File-Size") || c.headers.get("Content-Length"), f = d ? parseInt(d) : 0, p = f !== 0;
        let v = 0;
        const g = new ReadableStream({
          start(m) {
            x();
            function x() {
              u.read().then(({ done: y, value: A }) => {
                if (y)
                  m.close();
                else {
                  v += A.byteLength;
                  const b = new ProgressEvent("progress", { lengthComputable: p, loaded: v, total: f });
                  for (let M = 0, w = h.length; M < w; M++) {
                    const C = h[M];
                    C.onProgress && C.onProgress(b);
                  }
                  m.enqueue(A), x();
                }
              }, (y) => {
                m.error(y);
              });
            }
          }
        });
        return new Response(g);
      } else
        throw new uj(`fetch for "${c.url}" responded with ${c.status}: ${c.statusText}`, c);
    }).then((c) => {
      switch (l) {
        case "arraybuffer":
          return c.arrayBuffer();
        case "blob":
          return c.blob();
        case "document":
          return c.text().then((h) => new DOMParser().parseFromString(h, o));
        case "json":
          return c.json();
        default:
          if (o === void 0)
            return c.text();
          {
            const u = /charset="?([^;"\s]*)"?/i.exec(o), d = u && u[1] ? u[1].toLowerCase() : void 0, f = new TextDecoder(d);
            return c.arrayBuffer().then((p) => f.decode(p));
          }
      }
    }).then((c) => {
      tl.add(e, c);
      const h = _0[e];
      delete _0[e];
      for (let u = 0, d = h.length; u < d; u++) {
        const f = h[u];
        f.onLoad && f.onLoad(c);
      }
    }).catch((c) => {
      const h = _0[e];
      if (h === void 0)
        throw this.manager.itemError(e), c;
      delete _0[e];
      for (let u = 0, d = h.length; u < d; u++) {
        const f = h[u];
        f.onError && f.onError(c);
      }
      this.manager.itemError(e);
    }).finally(() => {
      this.manager.itemEnd(e);
    }), this.manager.itemStart(e);
  }
  setResponseType(e) {
    return this.responseType = e, this;
  }
  setMimeType(e) {
    return this.mimeType = e, this;
  }
}
class dj extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = new Nn(this.manager);
    a.setPath(this.path), a.setRequestHeader(this.requestHeader), a.setWithCredentials(this.withCredentials), a.load(e, function(o) {
      try {
        t(r.parse(JSON.parse(o)));
      } catch (l) {
        n ? n(l) : console.error(l), r.manager.itemError(e);
      }
    }, i, n);
  }
  parse(e) {
    const t = [];
    for (let i = 0; i < e.length; i++) {
      const n = xl.parse(e[i]);
      t.push(n);
    }
    return t;
  }
}
class fj extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = [], o = new T4(), l = new Nn(this.manager);
    l.setPath(this.path), l.setResponseType("arraybuffer"), l.setRequestHeader(this.requestHeader), l.setWithCredentials(r.withCredentials);
    let c = 0;
    function h(u) {
      l.load(e[u], function(d) {
        const f = r.parse(d, !0);
        a[u] = {
          width: f.width,
          height: f.height,
          format: f.format,
          mipmaps: f.mipmaps
        }, c += 1, c === 6 && (f.mipmapCount === 1 && (o.minFilter = ii), o.image = a, o.format = f.format, o.needsUpdate = !0, t && t(o));
      }, i, n);
    }
    if (Array.isArray(e))
      for (let u = 0, d = e.length; u < d; ++u)
        h(u);
    else
      l.load(e, function(u) {
        const d = r.parse(u, !0);
        if (d.isCubemap) {
          const f = d.mipmaps.length / d.mipmapCount;
          for (let p = 0; p < f; p++) {
            a[p] = { mipmaps: [] };
            for (let v = 0; v < d.mipmapCount; v++)
              a[p].mipmaps.push(d.mipmaps[p * d.mipmapCount + v]), a[p].format = d.format, a[p].width = d.width, a[p].height = d.height;
          }
          o.image = a;
        } else
          o.image.width = d.width, o.image.height = d.height, o.mipmaps = d.mipmaps;
        d.mipmapCount === 1 && (o.minFilter = ii), o.format = d.format, o.needsUpdate = !0, t && t(o);
      }, i, n);
    return o;
  }
}
class Ru extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    this.path !== void 0 && (e = this.path + e), e = this.manager.resolveURL(e);
    const r = this, a = tl.get(e);
    if (a !== void 0)
      return r.manager.itemStart(e), setTimeout(function() {
        t && t(a), r.manager.itemEnd(e);
      }, 0), a;
    const o = $1("img");
    function l() {
      h(), tl.add(e, this), t && t(this), r.manager.itemEnd(e);
    }
    function c(u) {
      h(), n && n(u), r.manager.itemError(e), r.manager.itemEnd(e);
    }
    function h() {
      o.removeEventListener("load", l, !1), o.removeEventListener("error", c, !1);
    }
    return o.addEventListener("load", l, !1), o.addEventListener("error", c, !1), e.slice(0, 5) !== "data:" && this.crossOrigin !== void 0 && (o.crossOrigin = this.crossOrigin), r.manager.itemStart(e), o.src = e, o;
  }
}
class pj extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = new Vp();
    r.colorSpace = qi;
    const a = new Ru(this.manager);
    a.setCrossOrigin(this.crossOrigin), a.setPath(this.path);
    let o = 0;
    function l(c) {
      a.load(e[c], function(h) {
        r.images[c] = h, o++, o === 6 && (r.needsUpdate = !0, t && t(r));
      }, void 0, n);
    }
    for (let c = 0; c < e.length; ++c)
      l(c);
    return r;
  }
}
class R4 extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = new Qn(), o = new Nn(this.manager);
    return o.setResponseType("arraybuffer"), o.setRequestHeader(this.requestHeader), o.setPath(this.path), o.setWithCredentials(r.withCredentials), o.load(e, function(l) {
      let c;
      try {
        c = r.parse(l);
      } catch (h) {
        if (n !== void 0)
          n(h);
        else {
          console.error(h);
          return;
        }
      }
      c.image !== void 0 ? a.image = c.image : c.data !== void 0 && (a.image.width = c.width, a.image.height = c.height, a.image.data = c.data), a.wrapS = c.wrapS !== void 0 ? c.wrapS : yn, a.wrapT = c.wrapT !== void 0 ? c.wrapT : yn, a.magFilter = c.magFilter !== void 0 ? c.magFilter : ii, a.minFilter = c.minFilter !== void 0 ? c.minFilter : ii, a.anisotropy = c.anisotropy !== void 0 ? c.anisotropy : 1, c.colorSpace !== void 0 && (a.colorSpace = c.colorSpace), c.flipY !== void 0 && (a.flipY = c.flipY), c.format !== void 0 && (a.format = c.format), c.type !== void 0 && (a.type = c.type), c.mipmaps !== void 0 && (a.mipmaps = c.mipmaps, a.minFilter = ds), c.mipmapCount === 1 && (a.minFilter = ii), c.generateMipmaps !== void 0 && (a.generateMipmaps = c.generateMipmaps), a.needsUpdate = !0, t && t(a, c);
    }, i, n), a;
  }
}
class rh extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = new Ut(), a = new Ru(this.manager);
    return a.setCrossOrigin(this.crossOrigin), a.setPath(this.path), a.load(e, function(o) {
      r.image = o, r.needsUpdate = !0, t !== void 0 && t(r);
    }, i, n), r;
  }
}
class ah extends mt {
  constructor(e, t = 1) {
    super(), this.isLight = !0, this.type = "Light", this.color = new _e(e), this.intensity = t;
  }
  dispose() {
  }
  copy(e, t) {
    return super.copy(e, t), this.color.copy(e.color), this.intensity = e.intensity, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.color = this.color.getHex(), t.object.intensity = this.intensity, this.groundColor !== void 0 && (t.object.groundColor = this.groundColor.getHex()), this.distance !== void 0 && (t.object.distance = this.distance), this.angle !== void 0 && (t.object.angle = this.angle), this.decay !== void 0 && (t.object.decay = this.decay), this.penumbra !== void 0 && (t.object.penumbra = this.penumbra), this.shadow !== void 0 && (t.object.shadow = this.shadow.toJSON()), this.target !== void 0 && (t.object.target = this.target.uuid), t;
  }
}
class v2 extends ah {
  constructor(e, t, i) {
    super(e, i), this.isHemisphereLight = !0, this.type = "HemisphereLight", this.position.copy(mt.DEFAULT_UP), this.updateMatrix(), this.groundColor = new _e(t);
  }
  copy(e, t) {
    return super.copy(e, t), this.groundColor.copy(e.groundColor), this;
  }
}
const n5 = /* @__PURE__ */ new et(), iT = /* @__PURE__ */ new _(), nT = /* @__PURE__ */ new _();
class $M {
  constructor(e) {
    this.camera = e, this.intensity = 1, this.bias = 0, this.normalBias = 0, this.radius = 1, this.blurSamples = 8, this.mapSize = new Se(512, 512), this.map = null, this.mapPass = null, this.matrix = new et(), this.autoUpdate = !0, this.needsUpdate = !1, this._frustum = new th(), this._frameExtents = new Se(1, 1), this._viewportCount = 1, this._viewports = [
      new Yt(0, 0, 1, 1)
    ];
  }
  getViewportCount() {
    return this._viewportCount;
  }
  getFrustum() {
    return this._frustum;
  }
  updateMatrices(e) {
    const t = this.camera, i = this.matrix;
    iT.setFromMatrixPosition(e.matrixWorld), t.position.copy(iT), nT.setFromMatrixPosition(e.target.matrixWorld), t.lookAt(nT), t.updateMatrixWorld(), n5.multiplyMatrices(t.projectionMatrix, t.matrixWorldInverse), this._frustum.setFromProjectionMatrix(n5), i.set(
      0.5,
      0,
      0,
      0.5,
      0,
      0.5,
      0,
      0.5,
      0,
      0,
      0.5,
      0.5,
      0,
      0,
      0,
      1
    ), i.multiply(n5);
  }
  getViewport(e) {
    return this._viewports[e];
  }
  getFrameExtents() {
    return this._frameExtents;
  }
  dispose() {
    this.map && this.map.dispose(), this.mapPass && this.mapPass.dispose();
  }
  copy(e) {
    return this.camera = e.camera.clone(), this.intensity = e.intensity, this.bias = e.bias, this.radius = e.radius, this.mapSize.copy(e.mapSize), this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  toJSON() {
    const e = {};
    return this.intensity !== 1 && (e.intensity = this.intensity), this.bias !== 0 && (e.bias = this.bias), this.normalBias !== 0 && (e.normalBias = this.normalBias), this.radius !== 1 && (e.radius = this.radius), (this.mapSize.x !== 512 || this.mapSize.y !== 512) && (e.mapSize = this.mapSize.toArray()), e.camera = this.camera.toJSON(!1).object, delete e.camera.matrix, e;
  }
}
class mj extends $M {
  constructor() {
    super(new Zt(50, 1, 0.5, 500)), this.isSpotLightShadow = !0, this.focus = 1;
  }
  updateMatrices(e) {
    const t = this.camera, i = np * 2 * e.angle * this.focus, n = this.mapSize.width / this.mapSize.height, r = e.distance || t.far;
    (i !== t.fov || n !== t.aspect || r !== t.far) && (t.fov = i, t.aspect = n, t.far = r, t.updateProjectionMatrix()), super.updateMatrices(e);
  }
  copy(e) {
    return super.copy(e), this.focus = e.focus, this;
  }
}
class oh extends ah {
  constructor(e, t, i = 0, n = Math.PI / 3, r = 0, a = 2) {
    super(e, t), this.isSpotLight = !0, this.type = "SpotLight", this.position.copy(mt.DEFAULT_UP), this.updateMatrix(), this.target = new mt(), this.distance = i, this.angle = n, this.penumbra = r, this.decay = a, this.map = null, this.shadow = new mj();
  }
  get power() {
    return this.intensity * Math.PI;
  }
  set power(e) {
    this.intensity = e / Math.PI;
  }
  dispose() {
    this.shadow.dispose();
  }
  copy(e, t) {
    return super.copy(e, t), this.distance = e.distance, this.angle = e.angle, this.penumbra = e.penumbra, this.decay = e.decay, this.target = e.target.clone(), this.shadow = e.shadow.clone(), this;
  }
}
const sT = /* @__PURE__ */ new et(), ym = /* @__PURE__ */ new _(), s5 = /* @__PURE__ */ new _();
class gj extends $M {
  constructor() {
    super(new Zt(90, 1, 0.5, 500)), this.isPointLightShadow = !0, this._frameExtents = new Se(4, 2), this._viewportCount = 6, this._viewports = [
      // These viewports map a cube-map onto a 2D texture with the
      // following orientation:
      //
      //  xzXZ
      //   y Y
      //
      // X - Positive x direction
      // x - Negative x direction
      // Y - Positive y direction
      // y - Negative y direction
      // Z - Positive z direction
      // z - Negative z direction
      // positive X
      new Yt(2, 1, 1, 1),
      // negative X
      new Yt(0, 1, 1, 1),
      // positive Z
      new Yt(3, 1, 1, 1),
      // negative Z
      new Yt(1, 1, 1, 1),
      // positive Y
      new Yt(3, 0, 1, 1),
      // negative Y
      new Yt(1, 0, 1, 1)
    ], this._cubeDirections = [
      new _(1, 0, 0),
      new _(-1, 0, 0),
      new _(0, 0, 1),
      new _(0, 0, -1),
      new _(0, 1, 0),
      new _(0, -1, 0)
    ], this._cubeUps = [
      new _(0, 1, 0),
      new _(0, 1, 0),
      new _(0, 1, 0),
      new _(0, 1, 0),
      new _(0, 0, 1),
      new _(0, 0, -1)
    ];
  }
  updateMatrices(e, t = 0) {
    const i = this.camera, n = this.matrix, r = e.distance || i.far;
    r !== i.far && (i.far = r, i.updateProjectionMatrix()), ym.setFromMatrixPosition(e.matrixWorld), i.position.copy(ym), s5.copy(i.position), s5.add(this._cubeDirections[t]), i.up.copy(this._cubeUps[t]), i.lookAt(s5), i.updateMatrixWorld(), n.makeTranslation(-ym.x, -ym.y, -ym.z), sT.multiplyMatrices(i.projectionMatrix, i.matrixWorldInverse), this._frustum.setFromProjectionMatrix(sT);
  }
}
class Tl extends ah {
  constructor(e, t, i = 0, n = 2) {
    super(e, t), this.isPointLight = !0, this.type = "PointLight", this.distance = i, this.decay = n, this.shadow = new gj();
  }
  get power() {
    return this.intensity * 4 * Math.PI;
  }
  set power(e) {
    this.intensity = e / (4 * Math.PI);
  }
  dispose() {
    this.shadow.dispose();
  }
  copy(e, t) {
    return super.copy(e, t), this.distance = e.distance, this.decay = e.decay, this.shadow = e.shadow.clone(), this;
  }
}
class vj extends $M {
  constructor() {
    super(new Xn(-5, 5, 5, -5, 0.5, 500)), this.isDirectionalLightShadow = !0;
  }
}
class _a extends ah {
  constructor(e, t) {
    super(e, t), this.isDirectionalLight = !0, this.type = "DirectionalLight", this.position.copy(mt.DEFAULT_UP), this.updateMatrix(), this.target = new mt(), this.shadow = new vj();
  }
  dispose() {
    this.shadow.dispose();
  }
  copy(e) {
    return super.copy(e), this.target = e.target.clone(), this.shadow = e.shadow.clone(), this;
  }
}
class Gc extends ah {
  constructor(e, t) {
    super(e, t), this.isAmbientLight = !0, this.type = "AmbientLight";
  }
}
class y2 extends ah {
  constructor(e, t, i = 10, n = 10) {
    super(e, t), this.isRectAreaLight = !0, this.type = "RectAreaLight", this.width = i, this.height = n;
  }
  get power() {
    return this.intensity * this.width * this.height * Math.PI;
  }
  set power(e) {
    this.intensity = e / (this.width * this.height * Math.PI);
  }
  copy(e) {
    return super.copy(e), this.width = e.width, this.height = e.height, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.width = this.width, t.object.height = this.height, t;
  }
}
class MI {
  constructor() {
    this.isSphericalHarmonics3 = !0, this.coefficients = [];
    for (let e = 0; e < 9; e++)
      this.coefficients.push(new _());
  }
  set(e) {
    for (let t = 0; t < 9; t++)
      this.coefficients[t].copy(e[t]);
    return this;
  }
  zero() {
    for (let e = 0; e < 9; e++)
      this.coefficients[e].set(0, 0, 0);
    return this;
  }
  // get the radiance in the direction of the normal
  // target is a Vector3
  getAt(e, t) {
    const i = e.x, n = e.y, r = e.z, a = this.coefficients;
    return t.copy(a[0]).multiplyScalar(0.282095), t.addScaledVector(a[1], 0.488603 * n), t.addScaledVector(a[2], 0.488603 * r), t.addScaledVector(a[3], 0.488603 * i), t.addScaledVector(a[4], 1.092548 * (i * n)), t.addScaledVector(a[5], 1.092548 * (n * r)), t.addScaledVector(a[6], 0.315392 * (3 * r * r - 1)), t.addScaledVector(a[7], 1.092548 * (i * r)), t.addScaledVector(a[8], 0.546274 * (i * i - n * n)), t;
  }
  // get the irradiance (radiance convolved with cosine lobe) in the direction of the normal
  // target is a Vector3
  // https://graphics.stanford.edu/papers/envmap/envmap.pdf
  getIrradianceAt(e, t) {
    const i = e.x, n = e.y, r = e.z, a = this.coefficients;
    return t.copy(a[0]).multiplyScalar(0.886227), t.addScaledVector(a[1], 2 * 0.511664 * n), t.addScaledVector(a[2], 2 * 0.511664 * r), t.addScaledVector(a[3], 2 * 0.511664 * i), t.addScaledVector(a[4], 2 * 0.429043 * i * n), t.addScaledVector(a[5], 2 * 0.429043 * n * r), t.addScaledVector(a[6], 0.743125 * r * r - 0.247708), t.addScaledVector(a[7], 2 * 0.429043 * i * r), t.addScaledVector(a[8], 0.429043 * (i * i - n * n)), t;
  }
  add(e) {
    for (let t = 0; t < 9; t++)
      this.coefficients[t].add(e.coefficients[t]);
    return this;
  }
  addScaledSH(e, t) {
    for (let i = 0; i < 9; i++)
      this.coefficients[i].addScaledVector(e.coefficients[i], t);
    return this;
  }
  scale(e) {
    for (let t = 0; t < 9; t++)
      this.coefficients[t].multiplyScalar(e);
    return this;
  }
  lerp(e, t) {
    for (let i = 0; i < 9; i++)
      this.coefficients[i].lerp(e.coefficients[i], t);
    return this;
  }
  equals(e) {
    for (let t = 0; t < 9; t++)
      if (!this.coefficients[t].equals(e.coefficients[t]))
        return !1;
    return !0;
  }
  copy(e) {
    return this.set(e.coefficients);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  fromArray(e, t = 0) {
    const i = this.coefficients;
    for (let n = 0; n < 9; n++)
      i[n].fromArray(e, t + n * 3);
    return this;
  }
  toArray(e = [], t = 0) {
    const i = this.coefficients;
    for (let n = 0; n < 9; n++)
      i[n].toArray(e, t + n * 3);
    return e;
  }
  // evaluate the basis functions
  // shBasis is an Array[ 9 ]
  static getBasisAt(e, t) {
    const i = e.x, n = e.y, r = e.z;
    t[0] = 0.282095, t[1] = 0.488603 * n, t[2] = 0.488603 * r, t[3] = 0.488603 * i, t[4] = 1.092548 * i * n, t[5] = 1.092548 * n * r, t[6] = 0.315392 * (3 * r * r - 1), t[7] = 1.092548 * i * r, t[8] = 0.546274 * (i * i - n * n);
  }
}
class D4 extends ah {
  constructor(e = new MI(), t = 1) {
    super(void 0, t), this.isLightProbe = !0, this.sh = e;
  }
  copy(e) {
    return super.copy(e), this.sh.copy(e.sh), this;
  }
  fromJSON(e) {
    return this.intensity = e.intensity, this.sh.fromArray(e.sh), this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.sh = this.sh.toArray(), t;
  }
}
class x2 extends As {
  constructor(e) {
    super(e), this.textures = {};
  }
  load(e, t, i, n) {
    const r = this, a = new Nn(r.manager);
    a.setPath(r.path), a.setRequestHeader(r.requestHeader), a.setWithCredentials(r.withCredentials), a.load(e, function(o) {
      try {
        t(r.parse(JSON.parse(o)));
      } catch (l) {
        n ? n(l) : console.error(l), r.manager.itemError(e);
      }
    }, i, n);
  }
  parse(e) {
    const t = this.textures;
    function i(r) {
      return t[r] === void 0 && console.warn("THREE.MaterialLoader: Undefined texture", r), t[r];
    }
    const n = x2.createMaterialFromType(e.type);
    if (e.uuid !== void 0 && (n.uuid = e.uuid), e.name !== void 0 && (n.name = e.name), e.color !== void 0 && n.color !== void 0 && n.color.setHex(e.color), e.roughness !== void 0 && (n.roughness = e.roughness), e.metalness !== void 0 && (n.metalness = e.metalness), e.sheen !== void 0 && (n.sheen = e.sheen), e.sheenColor !== void 0 && (n.sheenColor = new _e().setHex(e.sheenColor)), e.sheenRoughness !== void 0 && (n.sheenRoughness = e.sheenRoughness), e.emissive !== void 0 && n.emissive !== void 0 && n.emissive.setHex(e.emissive), e.specular !== void 0 && n.specular !== void 0 && n.specular.setHex(e.specular), e.specularIntensity !== void 0 && (n.specularIntensity = e.specularIntensity), e.specularColor !== void 0 && n.specularColor !== void 0 && n.specularColor.setHex(e.specularColor), e.shininess !== void 0 && (n.shininess = e.shininess), e.clearcoat !== void 0 && (n.clearcoat = e.clearcoat), e.clearcoatRoughness !== void 0 && (n.clearcoatRoughness = e.clearcoatRoughness), e.dispersion !== void 0 && (n.dispersion = e.dispersion), e.iridescence !== void 0 && (n.iridescence = e.iridescence), e.iridescenceIOR !== void 0 && (n.iridescenceIOR = e.iridescenceIOR), e.iridescenceThicknessRange !== void 0 && (n.iridescenceThicknessRange = e.iridescenceThicknessRange), e.transmission !== void 0 && (n.transmission = e.transmission), e.thickness !== void 0 && (n.thickness = e.thickness), e.attenuationDistance !== void 0 && (n.attenuationDistance = e.attenuationDistance), e.attenuationColor !== void 0 && n.attenuationColor !== void 0 && n.attenuationColor.setHex(e.attenuationColor), e.anisotropy !== void 0 && (n.anisotropy = e.anisotropy), e.anisotropyRotation !== void 0 && (n.anisotropyRotation = e.anisotropyRotation), e.fog !== void 0 && (n.fog = e.fog), e.flatShading !== void 0 && (n.flatShading = e.flatShading), e.blending !== void 0 && (n.blending = e.blending), e.combine !== void 0 && (n.combine = e.combine), e.side !== void 0 && (n.side = e.side), e.shadowSide !== void 0 && (n.shadowSide = e.shadowSide), e.opacity !== void 0 && (n.opacity = e.opacity), e.transparent !== void 0 && (n.transparent = e.transparent), e.alphaTest !== void 0 && (n.alphaTest = e.alphaTest), e.alphaHash !== void 0 && (n.alphaHash = e.alphaHash), e.depthFunc !== void 0 && (n.depthFunc = e.depthFunc), e.depthTest !== void 0 && (n.depthTest = e.depthTest), e.depthWrite !== void 0 && (n.depthWrite = e.depthWrite), e.colorWrite !== void 0 && (n.colorWrite = e.colorWrite), e.blendSrc !== void 0 && (n.blendSrc = e.blendSrc), e.blendDst !== void 0 && (n.blendDst = e.blendDst), e.blendEquation !== void 0 && (n.blendEquation = e.blendEquation), e.blendSrcAlpha !== void 0 && (n.blendSrcAlpha = e.blendSrcAlpha), e.blendDstAlpha !== void 0 && (n.blendDstAlpha = e.blendDstAlpha), e.blendEquationAlpha !== void 0 && (n.blendEquationAlpha = e.blendEquationAlpha), e.blendColor !== void 0 && n.blendColor !== void 0 && n.blendColor.setHex(e.blendColor), e.blendAlpha !== void 0 && (n.blendAlpha = e.blendAlpha), e.stencilWriteMask !== void 0 && (n.stencilWriteMask = e.stencilWriteMask), e.stencilFunc !== void 0 && (n.stencilFunc = e.stencilFunc), e.stencilRef !== void 0 && (n.stencilRef = e.stencilRef), e.stencilFuncMask !== void 0 && (n.stencilFuncMask = e.stencilFuncMask), e.stencilFail !== void 0 && (n.stencilFail = e.stencilFail), e.stencilZFail !== void 0 && (n.stencilZFail = e.stencilZFail), e.stencilZPass !== void 0 && (n.stencilZPass = e.stencilZPass), e.stencilWrite !== void 0 && (n.stencilWrite = e.stencilWrite), e.wireframe !== void 0 && (n.wireframe = e.wireframe), e.wireframeLinewidth !== void 0 && (n.wireframeLinewidth = e.wireframeLinewidth), e.wireframeLinecap !== void 0 && (n.wireframeLinecap = e.wireframeLinecap), e.wireframeLinejoin !== void 0 && (n.wireframeLinejoin = e.wireframeLinejoin), e.rotation !== void 0 && (n.rotation = e.rotation), e.linewidth !== void 0 && (n.linewidth = e.linewidth), e.dashSize !== void 0 && (n.dashSize = e.dashSize), e.gapSize !== void 0 && (n.gapSize = e.gapSize), e.scale !== void 0 && (n.scale = e.scale), e.polygonOffset !== void 0 && (n.polygonOffset = e.polygonOffset), e.polygonOffsetFactor !== void 0 && (n.polygonOffsetFactor = e.polygonOffsetFactor), e.polygonOffsetUnits !== void 0 && (n.polygonOffsetUnits = e.polygonOffsetUnits), e.dithering !== void 0 && (n.dithering = e.dithering), e.alphaToCoverage !== void 0 && (n.alphaToCoverage = e.alphaToCoverage), e.premultipliedAlpha !== void 0 && (n.premultipliedAlpha = e.premultipliedAlpha), e.forceSinglePass !== void 0 && (n.forceSinglePass = e.forceSinglePass), e.visible !== void 0 && (n.visible = e.visible), e.toneMapped !== void 0 && (n.toneMapped = e.toneMapped), e.userData !== void 0 && (n.userData = e.userData), e.vertexColors !== void 0 && (typeof e.vertexColors == "number" ? n.vertexColors = e.vertexColors > 0 : n.vertexColors = e.vertexColors), e.uniforms !== void 0)
      for (const r in e.uniforms) {
        const a = e.uniforms[r];
        switch (n.uniforms[r] = {}, a.type) {
          case "t":
            n.uniforms[r].value = i(a.value);
            break;
          case "c":
            n.uniforms[r].value = new _e().setHex(a.value);
            break;
          case "v2":
            n.uniforms[r].value = new Se().fromArray(a.value);
            break;
          case "v3":
            n.uniforms[r].value = new _().fromArray(a.value);
            break;
          case "v4":
            n.uniforms[r].value = new Yt().fromArray(a.value);
            break;
          case "m3":
            n.uniforms[r].value = new Gt().fromArray(a.value);
            break;
          case "m4":
            n.uniforms[r].value = new et().fromArray(a.value);
            break;
          default:
            n.uniforms[r].value = a.value;
        }
      }
    if (e.defines !== void 0 && (n.defines = e.defines), e.vertexShader !== void 0 && (n.vertexShader = e.vertexShader), e.fragmentShader !== void 0 && (n.fragmentShader = e.fragmentShader), e.glslVersion !== void 0 && (n.glslVersion = e.glslVersion), e.extensions !== void 0)
      for (const r in e.extensions)
        n.extensions[r] = e.extensions[r];
    if (e.lights !== void 0 && (n.lights = e.lights), e.clipping !== void 0 && (n.clipping = e.clipping), e.size !== void 0 && (n.size = e.size), e.sizeAttenuation !== void 0 && (n.sizeAttenuation = e.sizeAttenuation), e.map !== void 0 && (n.map = i(e.map)), e.matcap !== void 0 && (n.matcap = i(e.matcap)), e.alphaMap !== void 0 && (n.alphaMap = i(e.alphaMap)), e.bumpMap !== void 0 && (n.bumpMap = i(e.bumpMap)), e.bumpScale !== void 0 && (n.bumpScale = e.bumpScale), e.normalMap !== void 0 && (n.normalMap = i(e.normalMap)), e.normalMapType !== void 0 && (n.normalMapType = e.normalMapType), e.normalScale !== void 0) {
      let r = e.normalScale;
      Array.isArray(r) === !1 && (r = [r, r]), n.normalScale = new Se().fromArray(r);
    }
    return e.displacementMap !== void 0 && (n.displacementMap = i(e.displacementMap)), e.displacementScale !== void 0 && (n.displacementScale = e.displacementScale), e.displacementBias !== void 0 && (n.displacementBias = e.displacementBias), e.roughnessMap !== void 0 && (n.roughnessMap = i(e.roughnessMap)), e.metalnessMap !== void 0 && (n.metalnessMap = i(e.metalnessMap)), e.emissiveMap !== void 0 && (n.emissiveMap = i(e.emissiveMap)), e.emissiveIntensity !== void 0 && (n.emissiveIntensity = e.emissiveIntensity), e.specularMap !== void 0 && (n.specularMap = i(e.specularMap)), e.specularIntensityMap !== void 0 && (n.specularIntensityMap = i(e.specularIntensityMap)), e.specularColorMap !== void 0 && (n.specularColorMap = i(e.specularColorMap)), e.envMap !== void 0 && (n.envMap = i(e.envMap)), e.envMapRotation !== void 0 && n.envMapRotation.fromArray(e.envMapRotation), e.envMapIntensity !== void 0 && (n.envMapIntensity = e.envMapIntensity), e.reflectivity !== void 0 && (n.reflectivity = e.reflectivity), e.refractionRatio !== void 0 && (n.refractionRatio = e.refractionRatio), e.lightMap !== void 0 && (n.lightMap = i(e.lightMap)), e.lightMapIntensity !== void 0 && (n.lightMapIntensity = e.lightMapIntensity), e.aoMap !== void 0 && (n.aoMap = i(e.aoMap)), e.aoMapIntensity !== void 0 && (n.aoMapIntensity = e.aoMapIntensity), e.gradientMap !== void 0 && (n.gradientMap = i(e.gradientMap)), e.clearcoatMap !== void 0 && (n.clearcoatMap = i(e.clearcoatMap)), e.clearcoatRoughnessMap !== void 0 && (n.clearcoatRoughnessMap = i(e.clearcoatRoughnessMap)), e.clearcoatNormalMap !== void 0 && (n.clearcoatNormalMap = i(e.clearcoatNormalMap)), e.clearcoatNormalScale !== void 0 && (n.clearcoatNormalScale = new Se().fromArray(e.clearcoatNormalScale)), e.iridescenceMap !== void 0 && (n.iridescenceMap = i(e.iridescenceMap)), e.iridescenceThicknessMap !== void 0 && (n.iridescenceThicknessMap = i(e.iridescenceThicknessMap)), e.transmissionMap !== void 0 && (n.transmissionMap = i(e.transmissionMap)), e.thicknessMap !== void 0 && (n.thicknessMap = i(e.thicknessMap)), e.anisotropyMap !== void 0 && (n.anisotropyMap = i(e.anisotropyMap)), e.sheenColorMap !== void 0 && (n.sheenColorMap = i(e.sheenColorMap)), e.sheenRoughnessMap !== void 0 && (n.sheenRoughnessMap = i(e.sheenRoughnessMap)), n;
  }
  setTextures(e) {
    return this.textures = e, this;
  }
  static createMaterialFromType(e) {
    const t = {
      ShadowMaterial: dI,
      SpriteMaterial: ar,
      RawShaderMaterial: KM,
      ShaderMaterial: Jt,
      PointsMaterial: qu,
      MeshPhysicalMaterial: nn,
      MeshStandardMaterial: or,
      MeshPhongMaterial: m2,
      MeshToonMaterial: fI,
      MeshNormalMaterial: P4,
      MeshLambertMaterial: pI,
      MeshDepthMaterial: w4,
      MeshDistanceMaterial: GM,
      MeshBasicMaterial: kt,
      MeshMatcapMaterial: mI,
      LineDashedMaterial: gI,
      LineBasicMaterial: Bi,
      Material: Cn
    };
    return new t[e]();
  }
}
class Rs {
  static decodeText(e) {
    if (console.warn("THREE.LoaderUtils: decodeText() has been deprecated with r165 and will be removed with r175. Use TextDecoder instead."), typeof TextDecoder < "u")
      return new TextDecoder().decode(e);
    let t = "";
    for (let i = 0, n = e.length; i < n; i++)
      t += String.fromCharCode(e[i]);
    try {
      return decodeURIComponent(escape(t));
    } catch {
      return t;
    }
  }
  static extractUrlBase(e) {
    const t = e.lastIndexOf("/");
    return t === -1 ? "./" : e.slice(0, t + 1);
  }
  static resolveURL(e, t) {
    return typeof e != "string" || e === "" ? "" : (/^https?:\/\//i.test(t) && /^\//.test(e) && (t = t.replace(/(^https?:\/\/[^\/]+).*/i, "$1")), /^(https?:)?\/\//i.test(e) || /^data:.*,.*$/i.test(e) || /^blob:.*$/i.test(e) ? e : t + e);
  }
}
class eS extends Pt {
  constructor() {
    super(), this.isInstancedBufferGeometry = !0, this.type = "InstancedBufferGeometry", this.instanceCount = 1 / 0;
  }
  copy(e) {
    return super.copy(e), this.instanceCount = e.instanceCount, this;
  }
  toJSON() {
    const e = super.toJSON();
    return e.instanceCount = this.instanceCount, e.isInstancedBufferGeometry = !0, e;
  }
}
class tS extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = new Nn(r.manager);
    a.setPath(r.path), a.setRequestHeader(r.requestHeader), a.setWithCredentials(r.withCredentials), a.load(e, function(o) {
      try {
        t(r.parse(JSON.parse(o)));
      } catch (l) {
        n ? n(l) : console.error(l), r.manager.itemError(e);
      }
    }, i, n);
  }
  parse(e) {
    const t = {}, i = {};
    function n(f, p) {
      if (t[p] !== void 0)
        return t[p];
      const g = f.interleavedBuffers[p], m = r(f, g.buffer), x = yf(g.type, m), y = new Xu(x, g.stride);
      return y.uuid = g.uuid, t[p] = y, y;
    }
    function r(f, p) {
      if (i[p] !== void 0)
        return i[p];
      const g = f.arrayBuffers[p], m = new Uint32Array(g).buffer;
      return i[p] = m, m;
    }
    const a = e.isInstancedBufferGeometry ? new eS() : new Pt(), o = e.data.index;
    if (o !== void 0) {
      const f = yf(o.type, o.array);
      a.setIndex(new Ht(f, 1));
    }
    const l = e.data.attributes;
    for (const f in l) {
      const p = l[f];
      let v;
      if (p.isInterleavedBufferAttribute) {
        const g = n(e.data, p.data);
        v = new c0(g, p.itemSize, p.offset, p.normalized);
      } else {
        const g = yf(p.type, p.array), m = p.isInstancedBufferAttribute ? hs : Ht;
        v = new m(g, p.itemSize, p.normalized);
      }
      p.name !== void 0 && (v.name = p.name), p.usage !== void 0 && v.setUsage(p.usage), a.setAttribute(f, v);
    }
    const c = e.data.morphAttributes;
    if (c)
      for (const f in c) {
        const p = c[f], v = [];
        for (let g = 0, m = p.length; g < m; g++) {
          const x = p[g];
          let y;
          if (x.isInterleavedBufferAttribute) {
            const A = n(e.data, x.data);
            y = new c0(A, x.itemSize, x.offset, x.normalized);
          } else {
            const A = yf(x.type, x.array);
            y = new Ht(A, x.itemSize, x.normalized);
          }
          x.name !== void 0 && (y.name = x.name), v.push(y);
        }
        a.morphAttributes[f] = v;
      }
    e.data.morphTargetsRelative && (a.morphTargetsRelative = !0);
    const u = e.data.groups || e.data.drawcalls || e.data.offsets;
    if (u !== void 0)
      for (let f = 0, p = u.length; f !== p; ++f) {
        const v = u[f];
        a.addGroup(v.start, v.count, v.materialIndex);
      }
    const d = e.data.boundingSphere;
    if (d !== void 0) {
      const f = new _();
      d.center !== void 0 && f.fromArray(d.center), a.boundingSphere = new Tn(f, d.radius);
    }
    return e.name && (a.name = e.name), e.userData && (a.userData = e.userData), a;
  }
}
let SI = class extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = this.path === "" ? Rs.extractUrlBase(e) : this.path;
    this.resourcePath = this.resourcePath || a;
    const o = new Nn(this.manager);
    o.setPath(this.path), o.setRequestHeader(this.requestHeader), o.setWithCredentials(this.withCredentials), o.load(e, function(l) {
      let c = null;
      try {
        c = JSON.parse(l);
      } catch (u) {
        n !== void 0 && n(u), console.error("THREE:ObjectLoader: Can't parse " + e + ".", u.message);
        return;
      }
      const h = c.metadata;
      if (h === void 0 || h.type === void 0 || h.type.toLowerCase() === "geometry") {
        n !== void 0 && n(new Error("THREE.ObjectLoader: Can't load " + e)), console.error("THREE.ObjectLoader: Can't load " + e);
        return;
      }
      r.parse(c, t);
    }, i, n);
  }
  async loadAsync(e, t) {
    const i = this, n = this.path === "" ? Rs.extractUrlBase(e) : this.path;
    this.resourcePath = this.resourcePath || n;
    const r = new Nn(this.manager);
    r.setPath(this.path), r.setRequestHeader(this.requestHeader), r.setWithCredentials(this.withCredentials);
    const a = await r.loadAsync(e, t), o = JSON.parse(a), l = o.metadata;
    if (l === void 0 || l.type === void 0 || l.type.toLowerCase() === "geometry")
      throw new Error("THREE.ObjectLoader: Can't load " + e);
    return await i.parseAsync(o);
  }
  parse(e, t) {
    const i = this.parseAnimations(e.animations), n = this.parseShapes(e.shapes), r = this.parseGeometries(e.geometries, n), a = this.parseImages(e.images, function() {
      t !== void 0 && t(c);
    }), o = this.parseTextures(e.textures, a), l = this.parseMaterials(e.materials, o), c = this.parseObject(e.object, r, l, o, i), h = this.parseSkeletons(e.skeletons, c);
    if (this.bindSkeletons(c, h), this.bindLightTargets(c), t !== void 0) {
      let u = !1;
      for (const d in a)
        if (a[d].data instanceof HTMLImageElement) {
          u = !0;
          break;
        }
      u === !1 && t(c);
    }
    return c;
  }
  async parseAsync(e) {
    const t = this.parseAnimations(e.animations), i = this.parseShapes(e.shapes), n = this.parseGeometries(e.geometries, i), r = await this.parseImagesAsync(e.images), a = this.parseTextures(e.textures, r), o = this.parseMaterials(e.materials, a), l = this.parseObject(e.object, n, o, a, t), c = this.parseSkeletons(e.skeletons, l);
    return this.bindSkeletons(l, c), l;
  }
  parseShapes(e) {
    const t = {};
    if (e !== void 0)
      for (let i = 0, n = e.length; i < n; i++) {
        const r = new ll().fromJSON(e[i]);
        t[r.uuid] = r;
      }
    return t;
  }
  parseSkeletons(e, t) {
    const i = {}, n = {};
    if (t.traverse(function(r) {
      r.isBone && (n[r.uuid] = r);
    }), e !== void 0)
      for (let r = 0, a = e.length; r < a; r++) {
        const o = new nh().fromJSON(e[r], n);
        i[o.uuid] = o;
      }
    return i;
  }
  parseGeometries(e, t) {
    const i = {};
    if (e !== void 0) {
      const n = new tS();
      for (let r = 0, a = e.length; r < a; r++) {
        let o;
        const l = e[r];
        switch (l.type) {
          case "BufferGeometry":
          case "InstancedBufferGeometry":
            o = n.parse(l);
            break;
          default:
            l.type in tT ? o = tT[l.type].fromJSON(l, t) : console.warn(`THREE.ObjectLoader: Unsupported geometry type "${l.type}"`);
        }
        o.uuid = l.uuid, l.name !== void 0 && (o.name = l.name), l.userData !== void 0 && (o.userData = l.userData), i[l.uuid] = o;
      }
    }
    return i;
  }
  parseMaterials(e, t) {
    const i = {}, n = {};
    if (e !== void 0) {
      const r = new x2();
      r.setTextures(t);
      for (let a = 0, o = e.length; a < o; a++) {
        const l = e[a];
        i[l.uuid] === void 0 && (i[l.uuid] = r.parse(l)), n[l.uuid] = i[l.uuid];
      }
    }
    return n;
  }
  parseAnimations(e) {
    const t = {};
    if (e !== void 0)
      for (let i = 0; i < e.length; i++) {
        const n = e[i], r = xl.parse(n);
        t[r.uuid] = r;
      }
    return t;
  }
  parseImages(e, t) {
    const i = this, n = {};
    let r;
    function a(l) {
      return i.manager.itemStart(l), r.load(l, function() {
        i.manager.itemEnd(l);
      }, void 0, function() {
        i.manager.itemError(l), i.manager.itemEnd(l);
      });
    }
    function o(l) {
      if (typeof l == "string") {
        const c = l, h = /^(\/\/)|([a-z]+:(\/\/)?)/i.test(c) ? c : i.resourcePath + c;
        return a(h);
      } else
        return l.data ? {
          data: yf(l.type, l.data),
          width: l.width,
          height: l.height
        } : null;
    }
    if (e !== void 0 && e.length > 0) {
      const l = new g2(t);
      r = new Ru(l), r.setCrossOrigin(this.crossOrigin);
      for (let c = 0, h = e.length; c < h; c++) {
        const u = e[c], d = u.url;
        if (Array.isArray(d)) {
          const f = [];
          for (let p = 0, v = d.length; p < v; p++) {
            const g = d[p], m = o(g);
            m !== null && (m instanceof HTMLImageElement ? f.push(m) : f.push(new Qn(m.data, m.width, m.height)));
          }
          n[u.uuid] = new wa(f);
        } else {
          const f = o(u.url);
          n[u.uuid] = new wa(f);
        }
      }
    }
    return n;
  }
  async parseImagesAsync(e) {
    const t = this, i = {};
    let n;
    async function r(a) {
      if (typeof a == "string") {
        const o = a, l = /^(\/\/)|([a-z]+:(\/\/)?)/i.test(o) ? o : t.resourcePath + o;
        return await n.loadAsync(l);
      } else
        return a.data ? {
          data: yf(a.type, a.data),
          width: a.width,
          height: a.height
        } : null;
    }
    if (e !== void 0 && e.length > 0) {
      n = new Ru(this.manager), n.setCrossOrigin(this.crossOrigin);
      for (let a = 0, o = e.length; a < o; a++) {
        const l = e[a], c = l.url;
        if (Array.isArray(c)) {
          const h = [];
          for (let u = 0, d = c.length; u < d; u++) {
            const f = c[u], p = await r(f);
            p !== null && (p instanceof HTMLImageElement ? h.push(p) : h.push(new Qn(p.data, p.width, p.height)));
          }
          i[l.uuid] = new wa(h);
        } else {
          const h = await r(l.url);
          i[l.uuid] = new wa(h);
        }
      }
    }
    return i;
  }
  parseTextures(e, t) {
    function i(r, a) {
      return typeof r == "number" ? r : (console.warn("THREE.ObjectLoader.parseTexture: Constant should be in numeric form.", r), a[r]);
    }
    const n = {};
    if (e !== void 0)
      for (let r = 0, a = e.length; r < a; r++) {
        const o = e[r];
        o.image === void 0 && console.warn('THREE.ObjectLoader: No "image" specified for', o.uuid), t[o.image] === void 0 && console.warn("THREE.ObjectLoader: Undefined image", o.image);
        const l = t[o.image], c = l.data;
        let h;
        Array.isArray(c) ? (h = new Vp(), c.length === 6 && (h.needsUpdate = !0)) : (c && c.data ? h = new Qn() : h = new Ut(), c && (h.needsUpdate = !0)), h.source = l, h.uuid = o.uuid, o.name !== void 0 && (h.name = o.name), o.mapping !== void 0 && (h.mapping = i(o.mapping, yj)), o.channel !== void 0 && (h.channel = o.channel), o.offset !== void 0 && h.offset.fromArray(o.offset), o.repeat !== void 0 && h.repeat.fromArray(o.repeat), o.center !== void 0 && h.center.fromArray(o.center), o.rotation !== void 0 && (h.rotation = o.rotation), o.wrap !== void 0 && (h.wrapS = i(o.wrap[0], rT), h.wrapT = i(o.wrap[1], rT)), o.format !== void 0 && (h.format = o.format), o.internalFormat !== void 0 && (h.internalFormat = o.internalFormat), o.type !== void 0 && (h.type = o.type), o.colorSpace !== void 0 && (h.colorSpace = o.colorSpace), o.minFilter !== void 0 && (h.minFilter = i(o.minFilter, aT)), o.magFilter !== void 0 && (h.magFilter = i(o.magFilter, aT)), o.anisotropy !== void 0 && (h.anisotropy = o.anisotropy), o.flipY !== void 0 && (h.flipY = o.flipY), o.generateMipmaps !== void 0 && (h.generateMipmaps = o.generateMipmaps), o.premultiplyAlpha !== void 0 && (h.premultiplyAlpha = o.premultiplyAlpha), o.unpackAlignment !== void 0 && (h.unpackAlignment = o.unpackAlignment), o.compareFunction !== void 0 && (h.compareFunction = o.compareFunction), o.userData !== void 0 && (h.userData = o.userData), n[o.uuid] = h;
      }
    return n;
  }
  parseObject(e, t, i, n, r) {
    let a;
    function o(d) {
      return t[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined geometry", d), t[d];
    }
    function l(d) {
      if (d !== void 0) {
        if (Array.isArray(d)) {
          const f = [];
          for (let p = 0, v = d.length; p < v; p++) {
            const g = d[p];
            i[g] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", g), f.push(i[g]);
          }
          return f;
        }
        return i[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", d), i[d];
      }
    }
    function c(d) {
      return n[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined texture", d), n[d];
    }
    let h, u;
    switch (e.type) {
      case "Scene":
        a = new Da(), e.background !== void 0 && (Number.isInteger(e.background) ? a.background = new _e(e.background) : a.background = c(e.background)), e.environment !== void 0 && (a.environment = c(e.environment)), e.fog !== void 0 && (e.fog.type === "Fog" ? a.fog = new Wu(e.fog.color, e.fog.near, e.fog.far) : e.fog.type === "FogExp2" && (a.fog = new Xp(e.fog.color, e.fog.density)), e.fog.name !== "" && (a.fog.name = e.fog.name)), e.backgroundBlurriness !== void 0 && (a.backgroundBlurriness = e.backgroundBlurriness), e.backgroundIntensity !== void 0 && (a.backgroundIntensity = e.backgroundIntensity), e.backgroundRotation !== void 0 && a.backgroundRotation.fromArray(e.backgroundRotation), e.environmentIntensity !== void 0 && (a.environmentIntensity = e.environmentIntensity), e.environmentRotation !== void 0 && a.environmentRotation.fromArray(e.environmentRotation);
        break;
      case "PerspectiveCamera":
        a = new Zt(e.fov, e.aspect, e.near, e.far), e.focus !== void 0 && (a.focus = e.focus), e.zoom !== void 0 && (a.zoom = e.zoom), e.filmGauge !== void 0 && (a.filmGauge = e.filmGauge), e.filmOffset !== void 0 && (a.filmOffset = e.filmOffset), e.view !== void 0 && (a.view = Object.assign({}, e.view));
        break;
      case "OrthographicCamera":
        a = new Xn(e.left, e.right, e.top, e.bottom, e.near, e.far), e.zoom !== void 0 && (a.zoom = e.zoom), e.view !== void 0 && (a.view = Object.assign({}, e.view));
        break;
      case "AmbientLight":
        a = new Gc(e.color, e.intensity);
        break;
      case "DirectionalLight":
        a = new _a(e.color, e.intensity), a.target = e.target || "";
        break;
      case "PointLight":
        a = new Tl(e.color, e.intensity, e.distance, e.decay);
        break;
      case "RectAreaLight":
        a = new y2(e.color, e.intensity, e.width, e.height);
        break;
      case "SpotLight":
        a = new oh(e.color, e.intensity, e.distance, e.angle, e.penumbra, e.decay), a.target = e.target || "";
        break;
      case "HemisphereLight":
        a = new v2(e.color, e.groundColor, e.intensity);
        break;
      case "LightProbe":
        a = new D4().fromJSON(e);
        break;
      case "SkinnedMesh":
        h = o(e.geometry), u = l(e.material), a = new a2(h, u), e.bindMode !== void 0 && (a.bindMode = e.bindMode), e.bindMatrix !== void 0 && a.bindMatrix.fromArray(e.bindMatrix), e.skeleton !== void 0 && (a.skeleton = e.skeleton);
        break;
      case "Mesh":
        h = o(e.geometry), u = l(e.material), a = new Re(h, u);
        break;
      case "InstancedMesh":
        h = o(e.geometry), u = l(e.material);
        const d = e.count, f = e.instanceMatrix, p = e.instanceColor;
        a = new ho(h, u, d), a.instanceMatrix = new hs(new Float32Array(f.array), 16), p !== void 0 && (a.instanceColor = new hs(new Float32Array(p.array), p.itemSize));
        break;
      case "BatchedMesh":
        h = o(e.geometry), u = l(e.material), a = new rI(e.maxInstanceCount, e.maxVertexCount, e.maxIndexCount, u), a.geometry = h, a.perObjectFrustumCulled = e.perObjectFrustumCulled, a.sortObjects = e.sortObjects, a._drawRanges = e.drawRanges, a._reservedRanges = e.reservedRanges, a._visibility = e.visibility, a._active = e.active, a._bounds = e.bounds.map((v) => {
          const g = new zt();
          g.min.fromArray(v.boxMin), g.max.fromArray(v.boxMax);
          const m = new Tn();
          return m.radius = v.sphereRadius, m.center.fromArray(v.sphereCenter), {
            boxInitialized: v.boxInitialized,
            box: g,
            sphereInitialized: v.sphereInitialized,
            sphere: m
          };
        }), a._maxInstanceCount = e.maxInstanceCount, a._maxVertexCount = e.maxVertexCount, a._maxIndexCount = e.maxIndexCount, a._geometryInitialized = e.geometryInitialized, a._geometryCount = e.geometryCount, a._matricesTexture = c(e.matricesTexture.uuid), e.colorsTexture !== void 0 && (a._colorsTexture = c(e.colorsTexture.uuid));
        break;
      case "LOD":
        a = new E4();
        break;
      case "Line":
        a = new ti(o(e.geometry), l(e.material));
        break;
      case "LineLoop":
        a = new ju(o(e.geometry), l(e.material));
        break;
      case "LineSegments":
        a = new _n(o(e.geometry), l(e.material));
        break;
      case "PointCloud":
      case "Points":
        a = new sh(o(e.geometry), l(e.material));
        break;
      case "Sprite":
        a = new wn(l(e.material));
        break;
      case "Group":
        a = new ci();
        break;
      case "Bone":
        a = new ih();
        break;
      default:
        a = new mt();
    }
    if (a.uuid = e.uuid, e.name !== void 0 && (a.name = e.name), e.matrix !== void 0 ? (a.matrix.fromArray(e.matrix), e.matrixAutoUpdate !== void 0 && (a.matrixAutoUpdate = e.matrixAutoUpdate), a.matrixAutoUpdate && a.matrix.decompose(a.position, a.quaternion, a.scale)) : (e.position !== void 0 && a.position.fromArray(e.position), e.rotation !== void 0 && a.rotation.fromArray(e.rotation), e.quaternion !== void 0 && a.quaternion.fromArray(e.quaternion), e.scale !== void 0 && a.scale.fromArray(e.scale)), e.up !== void 0 && a.up.fromArray(e.up), e.castShadow !== void 0 && (a.castShadow = e.castShadow), e.receiveShadow !== void 0 && (a.receiveShadow = e.receiveShadow), e.shadow && (e.shadow.intensity !== void 0 && (a.shadow.intensity = e.shadow.intensity), e.shadow.bias !== void 0 && (a.shadow.bias = e.shadow.bias), e.shadow.normalBias !== void 0 && (a.shadow.normalBias = e.shadow.normalBias), e.shadow.radius !== void 0 && (a.shadow.radius = e.shadow.radius), e.shadow.mapSize !== void 0 && a.shadow.mapSize.fromArray(e.shadow.mapSize), e.shadow.camera !== void 0 && (a.shadow.camera = this.parseObject(e.shadow.camera))), e.visible !== void 0 && (a.visible = e.visible), e.frustumCulled !== void 0 && (a.frustumCulled = e.frustumCulled), e.renderOrder !== void 0 && (a.renderOrder = e.renderOrder), e.userData !== void 0 && (a.userData = e.userData), e.layers !== void 0 && (a.layers.mask = e.layers), e.children !== void 0) {
      const d = e.children;
      for (let f = 0; f < d.length; f++)
        a.add(this.parseObject(d[f], t, i, n, r));
    }
    if (e.animations !== void 0) {
      const d = e.animations;
      for (let f = 0; f < d.length; f++) {
        const p = d[f];
        a.animations.push(r[p]);
      }
    }
    if (e.type === "LOD") {
      e.autoUpdate !== void 0 && (a.autoUpdate = e.autoUpdate);
      const d = e.levels;
      for (let f = 0; f < d.length; f++) {
        const p = d[f], v = a.getObjectByProperty("uuid", p.object);
        v !== void 0 && a.addLevel(v, p.distance, p.hysteresis);
      }
    }
    return a;
  }
  bindSkeletons(e, t) {
    Object.keys(t).length !== 0 && e.traverse(function(i) {
      if (i.isSkinnedMesh === !0 && i.skeleton !== void 0) {
        const n = t[i.skeleton];
        n === void 0 ? console.warn("THREE.ObjectLoader: No skeleton found with UUID:", i.skeleton) : i.bind(n, i.bindMatrix);
      }
    });
  }
  bindLightTargets(e) {
    e.traverse(function(t) {
      if (t.isDirectionalLight || t.isSpotLight) {
        const i = t.target, n = e.getObjectByProperty("uuid", i);
        n !== void 0 ? t.target = n : t.target = new mt();
      }
    });
  }
};
const yj = {
  UVMapping: Ho,
  CubeReflectionMapping: lo,
  CubeRefractionMapping: a0,
  EquirectangularReflectionMapping: ml,
  EquirectangularRefractionMapping: Fc,
  CubeUVReflectionMapping: Qc
}, rT = {
  RepeatWrapping: un,
  ClampToEdgeWrapping: yn,
  MirroredRepeatWrapping: co
}, aT = {
  NearestFilter: Ni,
  NearestMipmapNearestFilter: $c,
  NearestMipmapLinearFilter: $a,
  LinearFilter: ii,
  LinearMipmapNearestFilter: $o,
  LinearMipmapLinearFilter: ds
};
class A2 extends As {
  constructor(e) {
    super(e), this.isImageBitmapLoader = !0, typeof createImageBitmap > "u" && console.warn("THREE.ImageBitmapLoader: createImageBitmap() not supported."), typeof fetch > "u" && console.warn("THREE.ImageBitmapLoader: fetch() not supported."), this.options = { premultiplyAlpha: "none" };
  }
  setOptions(e) {
    return this.options = e, this;
  }
  load(e, t, i, n) {
    e === void 0 && (e = ""), this.path !== void 0 && (e = this.path + e), e = this.manager.resolveURL(e);
    const r = this, a = tl.get(e);
    if (a !== void 0) {
      if (r.manager.itemStart(e), a.then) {
        a.then((c) => {
          t && t(c), r.manager.itemEnd(e);
        }).catch((c) => {
          n && n(c);
        });
        return;
      }
      return setTimeout(function() {
        t && t(a), r.manager.itemEnd(e);
      }, 0), a;
    }
    const o = {};
    o.credentials = this.crossOrigin === "anonymous" ? "same-origin" : "include", o.headers = this.requestHeader;
    const l = fetch(e, o).then(function(c) {
      return c.blob();
    }).then(function(c) {
      return createImageBitmap(c, Object.assign(r.options, { colorSpaceConversion: "none" }));
    }).then(function(c) {
      return tl.add(e, c), t && t(c), r.manager.itemEnd(e), c;
    }).catch(function(c) {
      n && n(c), tl.remove(e), r.manager.itemError(e), r.manager.itemEnd(e);
    });
    tl.add(e, l), r.manager.itemStart(e);
  }
}
let Iv;
class iS {
  static getContext() {
    return Iv === void 0 && (Iv = new (window.AudioContext || window.webkitAudioContext)()), Iv;
  }
  static setContext(e) {
    Iv = e;
  }
}
class xj extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = new Nn(this.manager);
    a.setResponseType("arraybuffer"), a.setPath(this.path), a.setRequestHeader(this.requestHeader), a.setWithCredentials(this.withCredentials), a.load(e, function(l) {
      try {
        const c = l.slice(0);
        iS.getContext().decodeAudioData(c, function(u) {
          t(u);
        }).catch(o);
      } catch (c) {
        o(c);
      }
    }, i, n);
    function o(l) {
      n ? n(l) : console.error(l), r.manager.itemError(e);
    }
  }
}
const oT = /* @__PURE__ */ new et(), lT = /* @__PURE__ */ new et(), Ah = /* @__PURE__ */ new et();
class Aj {
  constructor() {
    this.type = "StereoCamera", this.aspect = 1, this.eyeSep = 0.064, this.cameraL = new Zt(), this.cameraL.layers.enable(1), this.cameraL.matrixAutoUpdate = !1, this.cameraR = new Zt(), this.cameraR.layers.enable(2), this.cameraR.matrixAutoUpdate = !1, this._cache = {
      focus: null,
      fov: null,
      aspect: null,
      near: null,
      far: null,
      zoom: null,
      eyeSep: null
    };
  }
  update(e) {
    const t = this._cache;
    if (t.focus !== e.focus || t.fov !== e.fov || t.aspect !== e.aspect * this.aspect || t.near !== e.near || t.far !== e.far || t.zoom !== e.zoom || t.eyeSep !== this.eyeSep) {
      t.focus = e.focus, t.fov = e.fov, t.aspect = e.aspect * this.aspect, t.near = e.near, t.far = e.far, t.zoom = e.zoom, t.eyeSep = this.eyeSep, Ah.copy(e.projectionMatrix);
      const n = t.eyeSep / 2, r = n * t.near / t.focus, a = t.near * Math.tan(bu * t.fov * 0.5) / t.zoom;
      let o, l;
      lT.elements[12] = -n, oT.elements[12] = n, o = -a * t.aspect + r, l = a * t.aspect + r, Ah.elements[0] = 2 * t.near / (l - o), Ah.elements[8] = (l + o) / (l - o), this.cameraL.projectionMatrix.copy(Ah), o = -a * t.aspect - r, l = a * t.aspect - r, Ah.elements[0] = 2 * t.near / (l - o), Ah.elements[8] = (l + o) / (l - o), this.cameraR.projectionMatrix.copy(Ah);
    }
    this.cameraL.matrixWorld.copy(e.matrixWorld).multiply(lT), this.cameraR.matrixWorld.copy(e.matrixWorld).multiply(oT);
  }
}
let Ju = class {
  constructor(e = !0) {
    this.autoStart = e, this.startTime = 0, this.oldTime = 0, this.elapsedTime = 0, this.running = !1;
  }
  start() {
    this.startTime = cT(), this.oldTime = this.startTime, this.elapsedTime = 0, this.running = !0;
  }
  stop() {
    this.getElapsedTime(), this.running = !1, this.autoStart = !1;
  }
  getElapsedTime() {
    return this.getDelta(), this.elapsedTime;
  }
  getDelta() {
    let e = 0;
    if (this.autoStart && !this.running)
      return this.start(), 0;
    if (this.running) {
      const t = cT();
      e = (t - this.oldTime) / 1e3, this.oldTime = t, this.elapsedTime += e;
    }
    return e;
  }
};
function cT() {
  return (typeof performance > "u" ? Date : performance).now();
}
const bh = /* @__PURE__ */ new _(), hT = /* @__PURE__ */ new St(), bj = /* @__PURE__ */ new _(), Mh = /* @__PURE__ */ new _();
class Mj extends mt {
  constructor() {
    super(), this.type = "AudioListener", this.context = iS.getContext(), this.gain = this.context.createGain(), this.gain.connect(this.context.destination), this.filter = null, this.timeDelta = 0, this._clock = new Ju();
  }
  getInput() {
    return this.gain;
  }
  removeFilter() {
    return this.filter !== null && (this.gain.disconnect(this.filter), this.filter.disconnect(this.context.destination), this.gain.connect(this.context.destination), this.filter = null), this;
  }
  getFilter() {
    return this.filter;
  }
  setFilter(e) {
    return this.filter !== null ? (this.gain.disconnect(this.filter), this.filter.disconnect(this.context.destination)) : this.gain.disconnect(this.context.destination), this.filter = e, this.gain.connect(this.filter), this.filter.connect(this.context.destination), this;
  }
  getMasterVolume() {
    return this.gain.gain.value;
  }
  setMasterVolume(e) {
    return this.gain.gain.setTargetAtTime(e, this.context.currentTime, 0.01), this;
  }
  updateMatrixWorld(e) {
    super.updateMatrixWorld(e);
    const t = this.context.listener, i = this.up;
    if (this.timeDelta = this._clock.getDelta(), this.matrixWorld.decompose(bh, hT, bj), Mh.set(0, 0, -1).applyQuaternion(hT), t.positionX) {
      const n = this.context.currentTime + this.timeDelta;
      t.positionX.linearRampToValueAtTime(bh.x, n), t.positionY.linearRampToValueAtTime(bh.y, n), t.positionZ.linearRampToValueAtTime(bh.z, n), t.forwardX.linearRampToValueAtTime(Mh.x, n), t.forwardY.linearRampToValueAtTime(Mh.y, n), t.forwardZ.linearRampToValueAtTime(Mh.z, n), t.upX.linearRampToValueAtTime(i.x, n), t.upY.linearRampToValueAtTime(i.y, n), t.upZ.linearRampToValueAtTime(i.z, n);
    } else
      t.setPosition(bh.x, bh.y, bh.z), t.setOrientation(Mh.x, Mh.y, Mh.z, i.x, i.y, i.z);
  }
}
class wI extends mt {
  constructor(e) {
    super(), this.type = "Audio", this.listener = e, this.context = e.context, this.gain = this.context.createGain(), this.gain.connect(e.getInput()), this.autoplay = !1, this.buffer = null, this.detune = 0, this.loop = !1, this.loopStart = 0, this.loopEnd = 0, this.offset = 0, this.duration = void 0, this.playbackRate = 1, this.isPlaying = !1, this.hasPlaybackControl = !0, this.source = null, this.sourceType = "empty", this._startedAt = 0, this._progress = 0, this._connected = !1, this.filters = [];
  }
  getOutput() {
    return this.gain;
  }
  setNodeSource(e) {
    return this.hasPlaybackControl = !1, this.sourceType = "audioNode", this.source = e, this.connect(), this;
  }
  setMediaElementSource(e) {
    return this.hasPlaybackControl = !1, this.sourceType = "mediaNode", this.source = this.context.createMediaElementSource(e), this.connect(), this;
  }
  setMediaStreamSource(e) {
    return this.hasPlaybackControl = !1, this.sourceType = "mediaStreamNode", this.source = this.context.createMediaStreamSource(e), this.connect(), this;
  }
  setBuffer(e) {
    return this.buffer = e, this.sourceType = "buffer", this.autoplay && this.play(), this;
  }
  play(e = 0) {
    if (this.isPlaying === !0) {
      console.warn("THREE.Audio: Audio is already playing.");
      return;
    }
    if (this.hasPlaybackControl === !1) {
      console.warn("THREE.Audio: this Audio has no playback control.");
      return;
    }
    this._startedAt = this.context.currentTime + e;
    const t = this.context.createBufferSource();
    return t.buffer = this.buffer, t.loop = this.loop, t.loopStart = this.loopStart, t.loopEnd = this.loopEnd, t.onended = this.onEnded.bind(this), t.start(this._startedAt, this._progress + this.offset, this.duration), this.isPlaying = !0, this.source = t, this.setDetune(this.detune), this.setPlaybackRate(this.playbackRate), this.connect();
  }
  pause() {
    if (this.hasPlaybackControl === !1) {
      console.warn("THREE.Audio: this Audio has no playback control.");
      return;
    }
    return this.isPlaying === !0 && (this._progress += Math.max(this.context.currentTime - this._startedAt, 0) * this.playbackRate, this.loop === !0 && (this._progress = this._progress % (this.duration || this.buffer.duration)), this.source.stop(), this.source.onended = null, this.isPlaying = !1), this;
  }
  stop() {
    if (this.hasPlaybackControl === !1) {
      console.warn("THREE.Audio: this Audio has no playback control.");
      return;
    }
    return this._progress = 0, this.source !== null && (this.source.stop(), this.source.onended = null), this.isPlaying = !1, this;
  }
  connect() {
    if (this.filters.length > 0) {
      this.source.connect(this.filters[0]);
      for (let e = 1, t = this.filters.length; e < t; e++)
        this.filters[e - 1].connect(this.filters[e]);
      this.filters[this.filters.length - 1].connect(this.getOutput());
    } else
      this.source.connect(this.getOutput());
    return this._connected = !0, this;
  }
  disconnect() {
    if (this._connected !== !1) {
      if (this.filters.length > 0) {
        this.source.disconnect(this.filters[0]);
        for (let e = 1, t = this.filters.length; e < t; e++)
          this.filters[e - 1].disconnect(this.filters[e]);
        this.filters[this.filters.length - 1].disconnect(this.getOutput());
      } else
        this.source.disconnect(this.getOutput());
      return this._connected = !1, this;
    }
  }
  getFilters() {
    return this.filters;
  }
  setFilters(e) {
    return e || (e = []), this._connected === !0 ? (this.disconnect(), this.filters = e.slice(), this.connect()) : this.filters = e.slice(), this;
  }
  setDetune(e) {
    return this.detune = e, this.isPlaying === !0 && this.source.detune !== void 0 && this.source.detune.setTargetAtTime(this.detune, this.context.currentTime, 0.01), this;
  }
  getDetune() {
    return this.detune;
  }
  getFilter() {
    return this.getFilters()[0];
  }
  setFilter(e) {
    return this.setFilters(e ? [e] : []);
  }
  setPlaybackRate(e) {
    if (this.hasPlaybackControl === !1) {
      console.warn("THREE.Audio: this Audio has no playback control.");
      return;
    }
    return this.playbackRate = e, this.isPlaying === !0 && this.source.playbackRate.setTargetAtTime(this.playbackRate, this.context.currentTime, 0.01), this;
  }
  getPlaybackRate() {
    return this.playbackRate;
  }
  onEnded() {
    this.isPlaying = !1;
  }
  getLoop() {
    return this.hasPlaybackControl === !1 ? (console.warn("THREE.Audio: this Audio has no playback control."), !1) : this.loop;
  }
  setLoop(e) {
    if (this.hasPlaybackControl === !1) {
      console.warn("THREE.Audio: this Audio has no playback control.");
      return;
    }
    return this.loop = e, this.isPlaying === !0 && (this.source.loop = this.loop), this;
  }
  setLoopStart(e) {
    return this.loopStart = e, this;
  }
  setLoopEnd(e) {
    return this.loopEnd = e, this;
  }
  getVolume() {
    return this.gain.gain.value;
  }
  setVolume(e) {
    return this.gain.gain.setTargetAtTime(e, this.context.currentTime, 0.01), this;
  }
}
const Sh = /* @__PURE__ */ new _(), uT = /* @__PURE__ */ new St(), Sj = /* @__PURE__ */ new _(), wh = /* @__PURE__ */ new _();
class wj extends wI {
  constructor(e) {
    super(e), this.panner = this.context.createPanner(), this.panner.panningModel = "HRTF", this.panner.connect(this.gain);
  }
  connect() {
    super.connect(), this.panner.connect(this.gain);
  }
  disconnect() {
    super.disconnect(), this.panner.disconnect(this.gain);
  }
  getOutput() {
    return this.panner;
  }
  getRefDistance() {
    return this.panner.refDistance;
  }
  setRefDistance(e) {
    return this.panner.refDistance = e, this;
  }
  getRolloffFactor() {
    return this.panner.rolloffFactor;
  }
  setRolloffFactor(e) {
    return this.panner.rolloffFactor = e, this;
  }
  getDistanceModel() {
    return this.panner.distanceModel;
  }
  setDistanceModel(e) {
    return this.panner.distanceModel = e, this;
  }
  getMaxDistance() {
    return this.panner.maxDistance;
  }
  setMaxDistance(e) {
    return this.panner.maxDistance = e, this;
  }
  setDirectionalCone(e, t, i) {
    return this.panner.coneInnerAngle = e, this.panner.coneOuterAngle = t, this.panner.coneOuterGain = i, this;
  }
  updateMatrixWorld(e) {
    if (super.updateMatrixWorld(e), this.hasPlaybackControl === !0 && this.isPlaying === !1)
      return;
    this.matrixWorld.decompose(Sh, uT, Sj), wh.set(0, 0, 1).applyQuaternion(uT);
    const t = this.panner;
    if (t.positionX) {
      const i = this.context.currentTime + this.listener.timeDelta;
      t.positionX.linearRampToValueAtTime(Sh.x, i), t.positionY.linearRampToValueAtTime(Sh.y, i), t.positionZ.linearRampToValueAtTime(Sh.z, i), t.orientationX.linearRampToValueAtTime(wh.x, i), t.orientationY.linearRampToValueAtTime(wh.y, i), t.orientationZ.linearRampToValueAtTime(wh.z, i);
    } else
      t.setPosition(Sh.x, Sh.y, Sh.z), t.setOrientation(wh.x, wh.y, wh.z);
  }
}
class Ej {
  constructor(e, t = 2048) {
    this.analyser = e.context.createAnalyser(), this.analyser.fftSize = t, this.data = new Uint8Array(this.analyser.frequencyBinCount), e.getOutput().connect(this.analyser);
  }
  getFrequencyData() {
    return this.analyser.getByteFrequencyData(this.data), this.data;
  }
  getAverageFrequency() {
    let e = 0;
    const t = this.getFrequencyData();
    for (let i = 0; i < t.length; i++)
      e += t[i];
    return e / t.length;
  }
}
class EI {
  constructor(e, t, i) {
    this.binding = e, this.valueSize = i;
    let n, r, a;
    switch (t) {
      case "quaternion":
        n = this._slerp, r = this._slerpAdditive, a = this._setAdditiveIdentityQuaternion, this.buffer = new Float64Array(i * 6), this._workIndex = 5;
        break;
      case "string":
      case "bool":
        n = this._select, r = this._select, a = this._setAdditiveIdentityOther, this.buffer = new Array(i * 5);
        break;
      default:
        n = this._lerp, r = this._lerpAdditive, a = this._setAdditiveIdentityNumeric, this.buffer = new Float64Array(i * 5);
    }
    this._mixBufferRegion = n, this._mixBufferRegionAdditive = r, this._setIdentity = a, this._origIndex = 3, this._addIndex = 4, this.cumulativeWeight = 0, this.cumulativeWeightAdditive = 0, this.useCount = 0, this.referenceCount = 0;
  }
  // accumulate data in the 'incoming' region into 'accu<i>'
  accumulate(e, t) {
    const i = this.buffer, n = this.valueSize, r = e * n + n;
    let a = this.cumulativeWeight;
    if (a === 0) {
      for (let o = 0; o !== n; ++o)
        i[r + o] = i[o];
      a = t;
    } else {
      a += t;
      const o = t / a;
      this._mixBufferRegion(i, r, 0, o, n);
    }
    this.cumulativeWeight = a;
  }
  // accumulate data in the 'incoming' region into 'add'
  accumulateAdditive(e) {
    const t = this.buffer, i = this.valueSize, n = i * this._addIndex;
    this.cumulativeWeightAdditive === 0 && this._setIdentity(), this._mixBufferRegionAdditive(t, n, 0, e, i), this.cumulativeWeightAdditive += e;
  }
  // apply the state of 'accu<i>' to the binding when accus differ
  apply(e) {
    const t = this.valueSize, i = this.buffer, n = e * t + t, r = this.cumulativeWeight, a = this.cumulativeWeightAdditive, o = this.binding;
    if (this.cumulativeWeight = 0, this.cumulativeWeightAdditive = 0, r < 1) {
      const l = t * this._origIndex;
      this._mixBufferRegion(
        i,
        n,
        l,
        1 - r,
        t
      );
    }
    a > 0 && this._mixBufferRegionAdditive(i, n, this._addIndex * t, 1, t);
    for (let l = t, c = t + t; l !== c; ++l)
      if (i[l] !== i[l + t]) {
        o.setValue(i, n);
        break;
      }
  }
  // remember the state of the bound property and copy it to both accus
  saveOriginalState() {
    const e = this.binding, t = this.buffer, i = this.valueSize, n = i * this._origIndex;
    e.getValue(t, n);
    for (let r = i, a = n; r !== a; ++r)
      t[r] = t[n + r % i];
    this._setIdentity(), this.cumulativeWeight = 0, this.cumulativeWeightAdditive = 0;
  }
  // apply the state previously taken via 'saveOriginalState' to the binding
  restoreOriginalState() {
    const e = this.valueSize * 3;
    this.binding.setValue(this.buffer, e);
  }
  _setAdditiveIdentityNumeric() {
    const e = this._addIndex * this.valueSize, t = e + this.valueSize;
    for (let i = e; i < t; i++)
      this.buffer[i] = 0;
  }
  _setAdditiveIdentityQuaternion() {
    this._setAdditiveIdentityNumeric(), this.buffer[this._addIndex * this.valueSize + 3] = 1;
  }
  _setAdditiveIdentityOther() {
    const e = this._origIndex * this.valueSize, t = this._addIndex * this.valueSize;
    for (let i = 0; i < this.valueSize; i++)
      this.buffer[t + i] = this.buffer[e + i];
  }
  // mix functions
  _select(e, t, i, n, r) {
    if (n >= 0.5)
      for (let a = 0; a !== r; ++a)
        e[t + a] = e[i + a];
  }
  _slerp(e, t, i, n) {
    St.slerpFlat(e, t, e, t, e, i, n);
  }
  _slerpAdditive(e, t, i, n, r) {
    const a = this._workIndex * r;
    St.multiplyQuaternionsFlat(e, a, e, t, e, i), St.slerpFlat(e, t, e, t, e, a, n);
  }
  _lerp(e, t, i, n, r) {
    const a = 1 - n;
    for (let o = 0; o !== r; ++o) {
      const l = t + o;
      e[l] = e[l] * a + e[i + o] * n;
    }
  }
  _lerpAdditive(e, t, i, n, r) {
    for (let a = 0; a !== r; ++a) {
      const o = t + a;
      e[o] = e[o] + e[i + a] * n;
    }
  }
}
const nS = "\\[\\]\\.:\\/", Tj = new RegExp("[" + nS + "]", "g"), sS = "[^" + nS + "]", Cj = "[^" + nS.replace("\\.", "") + "]", _j = /* @__PURE__ */ /((?:WC+[\/:])*)/.source.replace("WC", sS), Pj = /* @__PURE__ */ /(WCOD+)?/.source.replace("WCOD", Cj), Rj = /* @__PURE__ */ /(?:\.(WC+)(?:\[(.+)\])?)?/.source.replace("WC", sS), Dj = /* @__PURE__ */ /\.(WC+)(?:\[(.+)\])?/.source.replace("WC", sS), Lj = new RegExp(
  "^" + _j + Pj + Rj + Dj + "$"
), Ij = ["material", "materials", "bones", "map"];
class Oj {
  constructor(e, t, i) {
    const n = i || Pi.parseTrackName(t);
    this._targetGroup = e, this._bindings = e.subscribe_(t, n);
  }
  getValue(e, t) {
    this.bind();
    const i = this._targetGroup.nCachedObjects_, n = this._bindings[i];
    n !== void 0 && n.getValue(e, t);
  }
  setValue(e, t) {
    const i = this._bindings;
    for (let n = this._targetGroup.nCachedObjects_, r = i.length; n !== r; ++n)
      i[n].setValue(e, t);
  }
  bind() {
    const e = this._bindings;
    for (let t = this._targetGroup.nCachedObjects_, i = e.length; t !== i; ++t)
      e[t].bind();
  }
  unbind() {
    const e = this._bindings;
    for (let t = this._targetGroup.nCachedObjects_, i = e.length; t !== i; ++t)
      e[t].unbind();
  }
}
class Pi {
  constructor(e, t, i) {
    this.path = t, this.parsedPath = i || Pi.parseTrackName(t), this.node = Pi.findNode(e, this.parsedPath.nodeName), this.rootNode = e, this.getValue = this._getValue_unbound, this.setValue = this._setValue_unbound;
  }
  static create(e, t, i) {
    return e && e.isAnimationObjectGroup ? new Pi.Composite(e, t, i) : new Pi(e, t, i);
  }
  /**
   * Replaces spaces with underscores and removes unsupported characters from
   * node names, to ensure compatibility with parseTrackName().
   *
   * @param {string} name Node name to be sanitized.
   * @return {string}
   */
  static sanitizeNodeName(e) {
    return e.replace(/\s/g, "_").replace(Tj, "");
  }
  static parseTrackName(e) {
    const t = Lj.exec(e);
    if (t === null)
      throw new Error("PropertyBinding: Cannot parse trackName: " + e);
    const i = {
      // directoryName: matches[ 1 ], // (tschw) currently unused
      nodeName: t[2],
      objectName: t[3],
      objectIndex: t[4],
      propertyName: t[5],
      // required
      propertyIndex: t[6]
    }, n = i.nodeName && i.nodeName.lastIndexOf(".");
    if (n !== void 0 && n !== -1) {
      const r = i.nodeName.substring(n + 1);
      Ij.indexOf(r) !== -1 && (i.nodeName = i.nodeName.substring(0, n), i.objectName = r);
    }
    if (i.propertyName === null || i.propertyName.length === 0)
      throw new Error("PropertyBinding: can not parse propertyName from trackName: " + e);
    return i;
  }
  static findNode(e, t) {
    if (t === void 0 || t === "" || t === "." || t === -1 || t === e.name || t === e.uuid)
      return e;
    if (e.skeleton) {
      const i = e.skeleton.getBoneByName(t);
      if (i !== void 0)
        return i;
    }
    if (e.children) {
      const i = function(r) {
        for (let a = 0; a < r.length; a++) {
          const o = r[a];
          if (o.name === t || o.uuid === t)
            return o;
          const l = i(o.children);
          if (l)
            return l;
        }
        return null;
      }, n = i(e.children);
      if (n)
        return n;
    }
    return null;
  }
  // these are used to "bind" a nonexistent property
  _getValue_unavailable() {
  }
  _setValue_unavailable() {
  }
  // Getters
  _getValue_direct(e, t) {
    e[t] = this.targetObject[this.propertyName];
  }
  _getValue_array(e, t) {
    const i = this.resolvedProperty;
    for (let n = 0, r = i.length; n !== r; ++n)
      e[t++] = i[n];
  }
  _getValue_arrayElement(e, t) {
    e[t] = this.resolvedProperty[this.propertyIndex];
  }
  _getValue_toArray(e, t) {
    this.resolvedProperty.toArray(e, t);
  }
  // Direct
  _setValue_direct(e, t) {
    this.targetObject[this.propertyName] = e[t];
  }
  _setValue_direct_setNeedsUpdate(e, t) {
    this.targetObject[this.propertyName] = e[t], this.targetObject.needsUpdate = !0;
  }
  _setValue_direct_setMatrixWorldNeedsUpdate(e, t) {
    this.targetObject[this.propertyName] = e[t], this.targetObject.matrixWorldNeedsUpdate = !0;
  }
  // EntireArray
  _setValue_array(e, t) {
    const i = this.resolvedProperty;
    for (let n = 0, r = i.length; n !== r; ++n)
      i[n] = e[t++];
  }
  _setValue_array_setNeedsUpdate(e, t) {
    const i = this.resolvedProperty;
    for (let n = 0, r = i.length; n !== r; ++n)
      i[n] = e[t++];
    this.targetObject.needsUpdate = !0;
  }
  _setValue_array_setMatrixWorldNeedsUpdate(e, t) {
    const i = this.resolvedProperty;
    for (let n = 0, r = i.length; n !== r; ++n)
      i[n] = e[t++];
    this.targetObject.matrixWorldNeedsUpdate = !0;
  }
  // ArrayElement
  _setValue_arrayElement(e, t) {
    this.resolvedProperty[this.propertyIndex] = e[t];
  }
  _setValue_arrayElement_setNeedsUpdate(e, t) {
    this.resolvedProperty[this.propertyIndex] = e[t], this.targetObject.needsUpdate = !0;
  }
  _setValue_arrayElement_setMatrixWorldNeedsUpdate(e, t) {
    this.resolvedProperty[this.propertyIndex] = e[t], this.targetObject.matrixWorldNeedsUpdate = !0;
  }
  // HasToFromArray
  _setValue_fromArray(e, t) {
    this.resolvedProperty.fromArray(e, t);
  }
  _setValue_fromArray_setNeedsUpdate(e, t) {
    this.resolvedProperty.fromArray(e, t), this.targetObject.needsUpdate = !0;
  }
  _setValue_fromArray_setMatrixWorldNeedsUpdate(e, t) {
    this.resolvedProperty.fromArray(e, t), this.targetObject.matrixWorldNeedsUpdate = !0;
  }
  _getValue_unbound(e, t) {
    this.bind(), this.getValue(e, t);
  }
  _setValue_unbound(e, t) {
    this.bind(), this.setValue(e, t);
  }
  // create getter / setter pair for a property in the scene graph
  bind() {
    let e = this.node;
    const t = this.parsedPath, i = t.objectName, n = t.propertyName;
    let r = t.propertyIndex;
    if (e || (e = Pi.findNode(this.rootNode, t.nodeName), this.node = e), this.getValue = this._getValue_unavailable, this.setValue = this._setValue_unavailable, !e) {
      console.warn("THREE.PropertyBinding: No target node found for track: " + this.path + ".");
      return;
    }
    if (i) {
      let c = t.objectIndex;
      switch (i) {
        case "materials":
          if (!e.material) {
            console.error("THREE.PropertyBinding: Can not bind to material as node does not have a material.", this);
            return;
          }
          if (!e.material.materials) {
            console.error("THREE.PropertyBinding: Can not bind to material.materials as node.material does not have a materials array.", this);
            return;
          }
          e = e.material.materials;
          break;
        case "bones":
          if (!e.skeleton) {
            console.error("THREE.PropertyBinding: Can not bind to bones as node does not have a skeleton.", this);
            return;
          }
          e = e.skeleton.bones;
          for (let h = 0; h < e.length; h++)
            if (e[h].name === c) {
              c = h;
              break;
            }
          break;
        case "map":
          if ("map" in e) {
            e = e.map;
            break;
          }
          if (!e.material) {
            console.error("THREE.PropertyBinding: Can not bind to material as node does not have a material.", this);
            return;
          }
          if (!e.material.map) {
            console.error("THREE.PropertyBinding: Can not bind to material.map as node.material does not have a map.", this);
            return;
          }
          e = e.material.map;
          break;
        default:
          if (e[i] === void 0) {
            console.error("THREE.PropertyBinding: Can not bind to objectName of node undefined.", this);
            return;
          }
          e = e[i];
      }
      if (c !== void 0) {
        if (e[c] === void 0) {
          console.error("THREE.PropertyBinding: Trying to bind to objectIndex of objectName, but is undefined.", this, e);
          return;
        }
        e = e[c];
      }
    }
    const a = e[n];
    if (a === void 0) {
      const c = t.nodeName;
      console.error("THREE.PropertyBinding: Trying to update property for track: " + c + "." + n + " but it wasn't found.", e);
      return;
    }
    let o = this.Versioning.None;
    this.targetObject = e, e.needsUpdate !== void 0 ? o = this.Versioning.NeedsUpdate : e.matrixWorldNeedsUpdate !== void 0 && (o = this.Versioning.MatrixWorldNeedsUpdate);
    let l = this.BindingType.Direct;
    if (r !== void 0) {
      if (n === "morphTargetInfluences") {
        if (!e.geometry) {
          console.error("THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.", this);
          return;
        }
        if (!e.geometry.morphAttributes) {
          console.error("THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.morphAttributes.", this);
          return;
        }
        e.morphTargetDictionary[r] !== void 0 && (r = e.morphTargetDictionary[r]);
      }
      l = this.BindingType.ArrayElement, this.resolvedProperty = a, this.propertyIndex = r;
    } else
      a.fromArray !== void 0 && a.toArray !== void 0 ? (l = this.BindingType.HasFromToArray, this.resolvedProperty = a) : Array.isArray(a) ? (l = this.BindingType.EntireArray, this.resolvedProperty = a) : this.propertyName = n;
    this.getValue = this.GetterByBindingType[l], this.setValue = this.SetterByBindingTypeAndVersioning[l][o];
  }
  unbind() {
    this.node = null, this.getValue = this._getValue_unbound, this.setValue = this._setValue_unbound;
  }
}
Pi.Composite = Oj;
Pi.prototype.BindingType = {
  Direct: 0,
  EntireArray: 1,
  ArrayElement: 2,
  HasFromToArray: 3
};
Pi.prototype.Versioning = {
  None: 0,
  NeedsUpdate: 1,
  MatrixWorldNeedsUpdate: 2
};
Pi.prototype.GetterByBindingType = [
  Pi.prototype._getValue_direct,
  Pi.prototype._getValue_array,
  Pi.prototype._getValue_arrayElement,
  Pi.prototype._getValue_toArray
];
Pi.prototype.SetterByBindingTypeAndVersioning = [
  [
    // Direct
    Pi.prototype._setValue_direct,
    Pi.prototype._setValue_direct_setNeedsUpdate,
    Pi.prototype._setValue_direct_setMatrixWorldNeedsUpdate
  ],
  [
    // EntireArray
    Pi.prototype._setValue_array,
    Pi.prototype._setValue_array_setNeedsUpdate,
    Pi.prototype._setValue_array_setMatrixWorldNeedsUpdate
  ],
  [
    // ArrayElement
    Pi.prototype._setValue_arrayElement,
    Pi.prototype._setValue_arrayElement_setNeedsUpdate,
    Pi.prototype._setValue_arrayElement_setMatrixWorldNeedsUpdate
  ],
  [
    // HasToFromArray
    Pi.prototype._setValue_fromArray,
    Pi.prototype._setValue_fromArray_setNeedsUpdate,
    Pi.prototype._setValue_fromArray_setMatrixWorldNeedsUpdate
  ]
];
class Nj {
  constructor() {
    this.isAnimationObjectGroup = !0, this.uuid = ea(), this._objects = Array.prototype.slice.call(arguments), this.nCachedObjects_ = 0;
    const e = {};
    this._indicesByUUID = e;
    for (let i = 0, n = arguments.length; i !== n; ++i)
      e[arguments[i].uuid] = i;
    this._paths = [], this._parsedPaths = [], this._bindings = [], this._bindingsIndicesByPath = {};
    const t = this;
    this.stats = {
      objects: {
        get total() {
          return t._objects.length;
        },
        get inUse() {
          return this.total - t.nCachedObjects_;
        }
      },
      get bindingsPerObject() {
        return t._bindings.length;
      }
    };
  }
  add() {
    const e = this._objects, t = this._indicesByUUID, i = this._paths, n = this._parsedPaths, r = this._bindings, a = r.length;
    let o, l = e.length, c = this.nCachedObjects_;
    for (let h = 0, u = arguments.length; h !== u; ++h) {
      const d = arguments[h], f = d.uuid;
      let p = t[f];
      if (p === void 0) {
        p = l++, t[f] = p, e.push(d);
        for (let v = 0, g = a; v !== g; ++v)
          r[v].push(new Pi(d, i[v], n[v]));
      } else if (p < c) {
        o = e[p];
        const v = --c, g = e[v];
        t[g.uuid] = p, e[p] = g, t[f] = v, e[v] = d;
        for (let m = 0, x = a; m !== x; ++m) {
          const y = r[m], A = y[v];
          let b = y[p];
          y[p] = A, b === void 0 && (b = new Pi(d, i[m], n[m])), y[v] = b;
        }
      } else
        e[p] !== o && console.error("THREE.AnimationObjectGroup: Different objects with the same UUID detected. Clean the caches or recreate your infrastructure when reloading scenes.");
    }
    this.nCachedObjects_ = c;
  }
  remove() {
    const e = this._objects, t = this._indicesByUUID, i = this._bindings, n = i.length;
    let r = this.nCachedObjects_;
    for (let a = 0, o = arguments.length; a !== o; ++a) {
      const l = arguments[a], c = l.uuid, h = t[c];
      if (h !== void 0 && h >= r) {
        const u = r++, d = e[u];
        t[d.uuid] = h, e[h] = d, t[c] = u, e[u] = l;
        for (let f = 0, p = n; f !== p; ++f) {
          const v = i[f], g = v[u], m = v[h];
          v[h] = g, v[u] = m;
        }
      }
    }
    this.nCachedObjects_ = r;
  }
  // remove & forget
  uncache() {
    const e = this._objects, t = this._indicesByUUID, i = this._bindings, n = i.length;
    let r = this.nCachedObjects_, a = e.length;
    for (let o = 0, l = arguments.length; o !== l; ++o) {
      const c = arguments[o], h = c.uuid, u = t[h];
      if (u !== void 0)
        if (delete t[h], u < r) {
          const d = --r, f = e[d], p = --a, v = e[p];
          t[f.uuid] = u, e[u] = f, t[v.uuid] = d, e[d] = v, e.pop();
          for (let g = 0, m = n; g !== m; ++g) {
            const x = i[g], y = x[d], A = x[p];
            x[u] = y, x[d] = A, x.pop();
          }
        } else {
          const d = --a, f = e[d];
          d > 0 && (t[f.uuid] = u), e[u] = f, e.pop();
          for (let p = 0, v = n; p !== v; ++p) {
            const g = i[p];
            g[u] = g[d], g.pop();
          }
        }
    }
    this.nCachedObjects_ = r;
  }
  // Internal interface used by befriended PropertyBinding.Composite:
  subscribe_(e, t) {
    const i = this._bindingsIndicesByPath;
    let n = i[e];
    const r = this._bindings;
    if (n !== void 0)
      return r[n];
    const a = this._paths, o = this._parsedPaths, l = this._objects, c = l.length, h = this.nCachedObjects_, u = new Array(c);
    n = r.length, i[e] = n, a.push(e), o.push(t), r.push(u);
    for (let d = h, f = l.length; d !== f; ++d) {
      const p = l[d];
      u[d] = new Pi(p, e, t);
    }
    return u;
  }
  unsubscribe_(e) {
    const t = this._bindingsIndicesByPath, i = t[e];
    if (i !== void 0) {
      const n = this._paths, r = this._parsedPaths, a = this._bindings, o = a.length - 1, l = a[o], c = e[o];
      t[c] = i, a[i] = l, a.pop(), r[i] = r[o], r.pop(), n[i] = n[o], n.pop();
    }
  }
}
class TI {
  constructor(e, t, i = null, n = t.blendMode) {
    this._mixer = e, this._clip = t, this._localRoot = i, this.blendMode = n;
    const r = t.tracks, a = r.length, o = new Array(a), l = {
      endingStart: hu,
      endingEnd: hu
    };
    for (let c = 0; c !== a; ++c) {
      const h = r[c].createInterpolant(null);
      o[c] = h, h.settings = l;
    }
    this._interpolantSettings = l, this._interpolants = o, this._propertyBindings = new Array(a), this._cacheIndex = null, this._byClipCacheIndex = null, this._timeScaleInterpolant = null, this._weightInterpolant = null, this.loop = RM, this._loopCount = -1, this._startTime = null, this.time = 0, this.timeScale = 1, this._effectiveTimeScale = 1, this.weight = 1, this._effectiveWeight = 1, this.repetitions = 1 / 0, this.paused = !1, this.enabled = !0, this.clampWhenFinished = !1, this.zeroSlopeAtStart = !0, this.zeroSlopeAtEnd = !0;
  }
  // State & Scheduling
  play() {
    return this._mixer._activateAction(this), this;
  }
  stop() {
    return this._mixer._deactivateAction(this), this.reset();
  }
  reset() {
    return this.paused = !1, this.enabled = !0, this.time = 0, this._loopCount = -1, this._startTime = null, this.stopFading().stopWarping();
  }
  isRunning() {
    return this.enabled && !this.paused && this.timeScale !== 0 && this._startTime === null && this._mixer._isActiveAction(this);
  }
  // return true when play has been called
  isScheduled() {
    return this._mixer._isActiveAction(this);
  }
  startAt(e) {
    return this._startTime = e, this;
  }
  setLoop(e, t) {
    return this.loop = e, this.repetitions = t, this;
  }
  // Weight
  // set the weight stopping any scheduled fading
  // although .enabled = false yields an effective weight of zero, this
  // method does *not* change .enabled, because it would be confusing
  setEffectiveWeight(e) {
    return this.weight = e, this._effectiveWeight = this.enabled ? e : 0, this.stopFading();
  }
  // return the weight considering fading and .enabled
  getEffectiveWeight() {
    return this._effectiveWeight;
  }
  fadeIn(e) {
    return this._scheduleFading(e, 0, 1);
  }
  fadeOut(e) {
    return this._scheduleFading(e, 1, 0);
  }
  crossFadeFrom(e, t, i) {
    if (e.fadeOut(t), this.fadeIn(t), i) {
      const n = this._clip.duration, r = e._clip.duration, a = r / n, o = n / r;
      e.warp(1, a, t), this.warp(o, 1, t);
    }
    return this;
  }
  crossFadeTo(e, t, i) {
    return e.crossFadeFrom(this, t, i);
  }
  stopFading() {
    const e = this._weightInterpolant;
    return e !== null && (this._weightInterpolant = null, this._mixer._takeBackControlInterpolant(e)), this;
  }
  // Time Scale Control
  // set the time scale stopping any scheduled warping
  // although .paused = true yields an effective time scale of zero, this
  // method does *not* change .paused, because it would be confusing
  setEffectiveTimeScale(e) {
    return this.timeScale = e, this._effectiveTimeScale = this.paused ? 0 : e, this.stopWarping();
  }
  // return the time scale considering warping and .paused
  getEffectiveTimeScale() {
    return this._effectiveTimeScale;
  }
  setDuration(e) {
    return this.timeScale = this._clip.duration / e, this.stopWarping();
  }
  syncWith(e) {
    return this.time = e.time, this.timeScale = e.timeScale, this.stopWarping();
  }
  halt(e) {
    return this.warp(this._effectiveTimeScale, 0, e);
  }
  warp(e, t, i) {
    const n = this._mixer, r = n.time, a = this.timeScale;
    let o = this._timeScaleInterpolant;
    o === null && (o = n._lendControlInterpolant(), this._timeScaleInterpolant = o);
    const l = o.parameterPositions, c = o.sampleValues;
    return l[0] = r, l[1] = r + i, c[0] = e / a, c[1] = t / a, this;
  }
  stopWarping() {
    const e = this._timeScaleInterpolant;
    return e !== null && (this._timeScaleInterpolant = null, this._mixer._takeBackControlInterpolant(e)), this;
  }
  // Object Accessors
  getMixer() {
    return this._mixer;
  }
  getClip() {
    return this._clip;
  }
  getRoot() {
    return this._localRoot || this._mixer._root;
  }
  // Interna
  _update(e, t, i, n) {
    if (!this.enabled) {
      this._updateWeight(e);
      return;
    }
    const r = this._startTime;
    if (r !== null) {
      const l = (e - r) * i;
      l < 0 || i === 0 ? t = 0 : (this._startTime = null, t = i * l);
    }
    t *= this._updateTimeScale(e);
    const a = this._updateTime(t), o = this._updateWeight(e);
    if (o > 0) {
      const l = this._interpolants, c = this._propertyBindings;
      switch (this.blendMode) {
        case DM:
          for (let h = 0, u = l.length; h !== u; ++h)
            l[h].evaluate(a), c[h].accumulateAdditive(o);
          break;
        case x4:
        default:
          for (let h = 0, u = l.length; h !== u; ++h)
            l[h].evaluate(a), c[h].accumulate(n, o);
      }
    }
  }
  _updateWeight(e) {
    let t = 0;
    if (this.enabled) {
      t = this.weight;
      const i = this._weightInterpolant;
      if (i !== null) {
        const n = i.evaluate(e)[0];
        t *= n, e > i.parameterPositions[1] && (this.stopFading(), n === 0 && (this.enabled = !1));
      }
    }
    return this._effectiveWeight = t, t;
  }
  _updateTimeScale(e) {
    let t = 0;
    if (!this.paused) {
      t = this.timeScale;
      const i = this._timeScaleInterpolant;
      if (i !== null) {
        const n = i.evaluate(e)[0];
        t *= n, e > i.parameterPositions[1] && (this.stopWarping(), t === 0 ? this.paused = !0 : this.timeScale = t);
      }
    }
    return this._effectiveTimeScale = t, t;
  }
  _updateTime(e) {
    const t = this._clip.duration, i = this.loop;
    let n = this.time + e, r = this._loopCount;
    const a = i === TL;
    if (e === 0)
      return r === -1 ? n : a && (r & 1) === 1 ? t - n : n;
    if (i === PM) {
      r === -1 && (this._loopCount = 0, this._setEndings(!0, !0, !1));
      e: {
        if (n >= t)
          n = t;
        else if (n < 0)
          n = 0;
        else {
          this.time = n;
          break e;
        }
        this.clampWhenFinished ? this.paused = !0 : this.enabled = !1, this.time = n, this._mixer.dispatchEvent({
          type: "finished",
          action: this,
          direction: e < 0 ? -1 : 1
        });
      }
    } else {
      if (r === -1 && (e >= 0 ? (r = 0, this._setEndings(!0, this.repetitions === 0, a)) : this._setEndings(this.repetitions === 0, !0, a)), n >= t || n < 0) {
        const o = Math.floor(n / t);
        n -= t * o, r += Math.abs(o);
        const l = this.repetitions - r;
        if (l <= 0)
          this.clampWhenFinished ? this.paused = !0 : this.enabled = !1, n = e > 0 ? t : 0, this.time = n, this._mixer.dispatchEvent({
            type: "finished",
            action: this,
            direction: e > 0 ? 1 : -1
          });
        else {
          if (l === 1) {
            const c = e < 0;
            this._setEndings(c, !c, a);
          } else
            this._setEndings(!1, !1, a);
          this._loopCount = r, this.time = n, this._mixer.dispatchEvent({
            type: "loop",
            action: this,
            loopDelta: o
          });
        }
      } else
        this.time = n;
      if (a && (r & 1) === 1)
        return t - n;
    }
    return n;
  }
  _setEndings(e, t, i) {
    const n = this._interpolantSettings;
    i ? (n.endingStart = uu, n.endingEnd = uu) : (e ? n.endingStart = this.zeroSlopeAtStart ? uu : hu : n.endingStart = q1, t ? n.endingEnd = this.zeroSlopeAtEnd ? uu : hu : n.endingEnd = q1);
  }
  _scheduleFading(e, t, i) {
    const n = this._mixer, r = n.time;
    let a = this._weightInterpolant;
    a === null && (a = n._lendControlInterpolant(), this._weightInterpolant = a);
    const o = a.parameterPositions, l = a.sampleValues;
    return o[0] = r, l[0] = t, o[1] = r + e, l[1] = i, this;
  }
}
const Bj = new Float32Array(1);
class rS extends ra {
  constructor(e) {
    super(), this._root = e, this._initMemoryManager(), this._accuIndex = 0, this.time = 0, this.timeScale = 1;
  }
  _bindAction(e, t) {
    const i = e._localRoot || this._root, n = e._clip.tracks, r = n.length, a = e._propertyBindings, o = e._interpolants, l = i.uuid, c = this._bindingsByRootAndName;
    let h = c[l];
    h === void 0 && (h = {}, c[l] = h);
    for (let u = 0; u !== r; ++u) {
      const d = n[u], f = d.name;
      let p = h[f];
      if (p !== void 0)
        ++p.referenceCount, a[u] = p;
      else {
        if (p = a[u], p !== void 0) {
          p._cacheIndex === null && (++p.referenceCount, this._addInactiveBinding(p, l, f));
          continue;
        }
        const v = t && t._propertyBindings[u].binding.parsedPath;
        p = new EI(
          Pi.create(i, f, v),
          d.ValueTypeName,
          d.getValueSize()
        ), ++p.referenceCount, this._addInactiveBinding(p, l, f), a[u] = p;
      }
      o[u].resultBuffer = p.buffer;
    }
  }
  _activateAction(e) {
    if (!this._isActiveAction(e)) {
      if (e._cacheIndex === null) {
        const i = (e._localRoot || this._root).uuid, n = e._clip.uuid, r = this._actionsByClip[n];
        this._bindAction(
          e,
          r && r.knownActions[0]
        ), this._addInactiveAction(e, n, i);
      }
      const t = e._propertyBindings;
      for (let i = 0, n = t.length; i !== n; ++i) {
        const r = t[i];
        r.useCount++ === 0 && (this._lendBinding(r), r.saveOriginalState());
      }
      this._lendAction(e);
    }
  }
  _deactivateAction(e) {
    if (this._isActiveAction(e)) {
      const t = e._propertyBindings;
      for (let i = 0, n = t.length; i !== n; ++i) {
        const r = t[i];
        --r.useCount === 0 && (r.restoreOriginalState(), this._takeBackBinding(r));
      }
      this._takeBackAction(e);
    }
  }
  // Memory manager
  _initMemoryManager() {
    this._actions = [], this._nActiveActions = 0, this._actionsByClip = {}, this._bindings = [], this._nActiveBindings = 0, this._bindingsByRootAndName = {}, this._controlInterpolants = [], this._nActiveControlInterpolants = 0;
    const e = this;
    this.stats = {
      actions: {
        get total() {
          return e._actions.length;
        },
        get inUse() {
          return e._nActiveActions;
        }
      },
      bindings: {
        get total() {
          return e._bindings.length;
        },
        get inUse() {
          return e._nActiveBindings;
        }
      },
      controlInterpolants: {
        get total() {
          return e._controlInterpolants.length;
        },
        get inUse() {
          return e._nActiveControlInterpolants;
        }
      }
    };
  }
  // Memory management for AnimationAction objects
  _isActiveAction(e) {
    const t = e._cacheIndex;
    return t !== null && t < this._nActiveActions;
  }
  _addInactiveAction(e, t, i) {
    const n = this._actions, r = this._actionsByClip;
    let a = r[t];
    if (a === void 0)
      a = {
        knownActions: [e],
        actionByRoot: {}
      }, e._byClipCacheIndex = 0, r[t] = a;
    else {
      const o = a.knownActions;
      e._byClipCacheIndex = o.length, o.push(e);
    }
    e._cacheIndex = n.length, n.push(e), a.actionByRoot[i] = e;
  }
  _removeInactiveAction(e) {
    const t = this._actions, i = t[t.length - 1], n = e._cacheIndex;
    i._cacheIndex = n, t[n] = i, t.pop(), e._cacheIndex = null;
    const r = e._clip.uuid, a = this._actionsByClip, o = a[r], l = o.knownActions, c = l[l.length - 1], h = e._byClipCacheIndex;
    c._byClipCacheIndex = h, l[h] = c, l.pop(), e._byClipCacheIndex = null;
    const u = o.actionByRoot, d = (e._localRoot || this._root).uuid;
    delete u[d], l.length === 0 && delete a[r], this._removeInactiveBindingsForAction(e);
  }
  _removeInactiveBindingsForAction(e) {
    const t = e._propertyBindings;
    for (let i = 0, n = t.length; i !== n; ++i) {
      const r = t[i];
      --r.referenceCount === 0 && this._removeInactiveBinding(r);
    }
  }
  _lendAction(e) {
    const t = this._actions, i = e._cacheIndex, n = this._nActiveActions++, r = t[n];
    e._cacheIndex = n, t[n] = e, r._cacheIndex = i, t[i] = r;
  }
  _takeBackAction(e) {
    const t = this._actions, i = e._cacheIndex, n = --this._nActiveActions, r = t[n];
    e._cacheIndex = n, t[n] = e, r._cacheIndex = i, t[i] = r;
  }
  // Memory management for PropertyMixer objects
  _addInactiveBinding(e, t, i) {
    const n = this._bindingsByRootAndName, r = this._bindings;
    let a = n[t];
    a === void 0 && (a = {}, n[t] = a), a[i] = e, e._cacheIndex = r.length, r.push(e);
  }
  _removeInactiveBinding(e) {
    const t = this._bindings, i = e.binding, n = i.rootNode.uuid, r = i.path, a = this._bindingsByRootAndName, o = a[n], l = t[t.length - 1], c = e._cacheIndex;
    l._cacheIndex = c, t[c] = l, t.pop(), delete o[r], Object.keys(o).length === 0 && delete a[n];
  }
  _lendBinding(e) {
    const t = this._bindings, i = e._cacheIndex, n = this._nActiveBindings++, r = t[n];
    e._cacheIndex = n, t[n] = e, r._cacheIndex = i, t[i] = r;
  }
  _takeBackBinding(e) {
    const t = this._bindings, i = e._cacheIndex, n = --this._nActiveBindings, r = t[n];
    e._cacheIndex = n, t[n] = e, r._cacheIndex = i, t[i] = r;
  }
  // Memory management of Interpolants for weight and time scale
  _lendControlInterpolant() {
    const e = this._controlInterpolants, t = this._nActiveControlInterpolants++;
    let i = e[t];
    return i === void 0 && (i = new sg(
      new Float32Array(2),
      new Float32Array(2),
      1,
      Bj
    ), i.__cacheIndex = t, e[t] = i), i;
  }
  _takeBackControlInterpolant(e) {
    const t = this._controlInterpolants, i = e.__cacheIndex, n = --this._nActiveControlInterpolants, r = t[n];
    e.__cacheIndex = n, t[n] = e, r.__cacheIndex = i, t[i] = r;
  }
  // return an action for a clip optionally using a custom root target
  // object (this method allocates a lot of dynamic memory in case a
  // previously unknown clip/root combination is specified)
  clipAction(e, t, i) {
    const n = t || this._root, r = n.uuid;
    let a = typeof e == "string" ? xl.findByName(n, e) : e;
    const o = a !== null ? a.uuid : e, l = this._actionsByClip[o];
    let c = null;
    if (i === void 0 && (a !== null ? i = a.blendMode : i = x4), l !== void 0) {
      const u = l.actionByRoot[r];
      if (u !== void 0 && u.blendMode === i)
        return u;
      c = l.knownActions[0], a === null && (a = c._clip);
    }
    if (a === null)
      return null;
    const h = new TI(this, a, t, i);
    return this._bindAction(h, c), this._addInactiveAction(h, o, r), h;
  }
  // get an existing action
  existingAction(e, t) {
    const i = t || this._root, n = i.uuid, r = typeof e == "string" ? xl.findByName(i, e) : e, a = r ? r.uuid : e, o = this._actionsByClip[a];
    return o !== void 0 && o.actionByRoot[n] || null;
  }
  // deactivates all previously scheduled actions
  stopAllAction() {
    const e = this._actions, t = this._nActiveActions;
    for (let i = t - 1; i >= 0; --i)
      e[i].stop();
    return this;
  }
  // advance the time and update apply the animation
  update(e) {
    e *= this.timeScale;
    const t = this._actions, i = this._nActiveActions, n = this.time += e, r = Math.sign(e), a = this._accuIndex ^= 1;
    for (let c = 0; c !== i; ++c)
      t[c]._update(n, e, r, a);
    const o = this._bindings, l = this._nActiveBindings;
    for (let c = 0; c !== l; ++c)
      o[c].apply(a);
    return this;
  }
  // Allows you to seek to a specific time in an animation.
  setTime(e) {
    this.time = 0;
    for (let t = 0; t < this._actions.length; t++)
      this._actions[t].time = 0;
    return this.update(e);
  }
  // return this mixer's root target object
  getRoot() {
    return this._root;
  }
  // free all resources specific to a particular clip
  uncacheClip(e) {
    const t = this._actions, i = e.uuid, n = this._actionsByClip, r = n[i];
    if (r !== void 0) {
      const a = r.knownActions;
      for (let o = 0, l = a.length; o !== l; ++o) {
        const c = a[o];
        this._deactivateAction(c);
        const h = c._cacheIndex, u = t[t.length - 1];
        c._cacheIndex = null, c._byClipCacheIndex = null, u._cacheIndex = h, t[h] = u, t.pop(), this._removeInactiveBindingsForAction(c);
      }
      delete n[i];
    }
  }
  // free all resources specific to a particular root target object
  uncacheRoot(e) {
    const t = e.uuid, i = this._actionsByClip;
    for (const a in i) {
      const o = i[a].actionByRoot, l = o[t];
      l !== void 0 && (this._deactivateAction(l), this._removeInactiveAction(l));
    }
    const n = this._bindingsByRootAndName, r = n[t];
    if (r !== void 0)
      for (const a in r) {
        const o = r[a];
        o.restoreOriginalState(), this._removeInactiveBinding(o);
      }
  }
  // remove a targeted clip from the cache
  uncacheAction(e, t) {
    const i = this.existingAction(e, t);
    i !== null && (this._deactivateAction(i), this._removeInactiveAction(i));
  }
}
class ya {
  constructor(e) {
    this.value = e;
  }
  clone() {
    return new ya(this.value.clone === void 0 ? this.value : this.value.clone());
  }
}
let Fj = 0;
class Uj extends ra {
  constructor() {
    super(), this.isUniformsGroup = !0, Object.defineProperty(this, "id", { value: Fj++ }), this.name = "", this.usage = J1, this.uniforms = [];
  }
  add(e) {
    return this.uniforms.push(e), this;
  }
  remove(e) {
    const t = this.uniforms.indexOf(e);
    return t !== -1 && this.uniforms.splice(t, 1), this;
  }
  setName(e) {
    return this.name = e, this;
  }
  setUsage(e) {
    return this.usage = e, this;
  }
  dispose() {
    return this.dispatchEvent({ type: "dispose" }), this;
  }
  copy(e) {
    this.name = e.name, this.usage = e.usage;
    const t = e.uniforms;
    this.uniforms.length = 0;
    for (let i = 0, n = t.length; i < n; i++) {
      const r = Array.isArray(t[i]) ? t[i] : [t[i]];
      for (let a = 0; a < r.length; a++)
        this.uniforms.push(r[a].clone());
    }
    return this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
class zj extends Xu {
  constructor(e, t, i = 1) {
    super(e, t), this.isInstancedInterleavedBuffer = !0, this.meshPerAttribute = i;
  }
  copy(e) {
    return super.copy(e), this.meshPerAttribute = e.meshPerAttribute, this;
  }
  clone(e) {
    const t = super.clone(e);
    return t.meshPerAttribute = this.meshPerAttribute, t;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.isInstancedInterleavedBuffer = !0, t.meshPerAttribute = this.meshPerAttribute, t;
  }
}
class kj {
  constructor(e, t, i, n, r) {
    this.isGLBufferAttribute = !0, this.name = "", this.buffer = e, this.type = t, this.itemSize = i, this.elementSize = n, this.count = r, this.version = 0;
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  setBuffer(e) {
    return this.buffer = e, this;
  }
  setType(e, t) {
    return this.type = e, this.elementSize = t, this;
  }
  setItemSize(e) {
    return this.itemSize = e, this;
  }
  setCount(e) {
    return this.count = e, this;
  }
}
const dT = /* @__PURE__ */ new et();
class lr {
  constructor(e, t, i = 0, n = 1 / 0) {
    this.ray = new g0(e, t), this.near = i, this.far = n, this.camera = null, this.layers = new e0(), this.params = {
      Mesh: {},
      Line: { threshold: 1 },
      LOD: {},
      Points: { threshold: 1 },
      Sprite: {}
    };
  }
  set(e, t) {
    this.ray.set(e, t);
  }
  setFromCamera(e, t) {
    t.isPerspectiveCamera ? (this.ray.origin.setFromMatrixPosition(t.matrixWorld), this.ray.direction.set(e.x, e.y, 0.5).unproject(t).sub(this.ray.origin).normalize(), this.camera = t) : t.isOrthographicCamera ? (this.ray.origin.set(e.x, e.y, (t.near + t.far) / (t.near - t.far)).unproject(t), this.ray.direction.set(0, 0, -1).transformDirection(t.matrixWorld), this.camera = t) : console.error("THREE.Raycaster: Unsupported camera type: " + t.type);
  }
  setFromXRController(e) {
    return dT.identity().extractRotation(e.matrixWorld), this.ray.origin.setFromMatrixPosition(e.matrixWorld), this.ray.direction.set(0, 0, -1).applyMatrix4(dT), this;
  }
  intersectObject(e, t = !0, i = []) {
    return $6(e, this, i, t), i.sort(fT), i;
  }
  intersectObjects(e, t = !0, i = []) {
    for (let n = 0, r = e.length; n < r; n++)
      $6(e[n], this, i, t);
    return i.sort(fT), i;
  }
}
function fT(s, e) {
  return s.distance - e.distance;
}
function $6(s, e, t, i) {
  let n = !0;
  if (s.layers.test(e.layers) && s.raycast(e, t) === !1 && (n = !1), n === !0 && i === !0) {
    const r = s.children;
    for (let a = 0, o = r.length; a < o; a++)
      $6(r[a], e, t, !0);
  }
}
class Ds {
  constructor(e = 1, t = 0, i = 0) {
    return this.radius = e, this.phi = t, this.theta = i, this;
  }
  set(e, t, i) {
    return this.radius = e, this.phi = t, this.theta = i, this;
  }
  copy(e) {
    return this.radius = e.radius, this.phi = e.phi, this.theta = e.theta, this;
  }
  // restrict phi to be between EPS and PI-EPS
  makeSafe() {
    return this.phi = Math.max(1e-6, Math.min(Math.PI - 1e-6, this.phi)), this;
  }
  setFromVector3(e) {
    return this.setFromCartesianCoords(e.x, e.y, e.z);
  }
  setFromCartesianCoords(e, t, i) {
    return this.radius = Math.sqrt(e * e + t * t + i * i), this.radius === 0 ? (this.theta = 0, this.phi = 0) : (this.theta = Math.atan2(e, i), this.phi = Math.acos(Wn(t / this.radius, -1, 1))), this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
class Gj {
  constructor(e = 1, t = 0, i = 0) {
    return this.radius = e, this.theta = t, this.y = i, this;
  }
  set(e, t, i) {
    return this.radius = e, this.theta = t, this.y = i, this;
  }
  copy(e) {
    return this.radius = e.radius, this.theta = e.theta, this.y = e.y, this;
  }
  setFromVector3(e) {
    return this.setFromCartesianCoords(e.x, e.y, e.z);
  }
  setFromCartesianCoords(e, t, i) {
    return this.radius = Math.sqrt(e * e + i * i), this.theta = Math.atan2(e, i), this.y = t, this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const pT = /* @__PURE__ */ new Se();
class Hj {
  constructor(e = new Se(1 / 0, 1 / 0), t = new Se(-1 / 0, -1 / 0)) {
    this.isBox2 = !0, this.min = e, this.max = t;
  }
  set(e, t) {
    return this.min.copy(e), this.max.copy(t), this;
  }
  setFromPoints(e) {
    this.makeEmpty();
    for (let t = 0, i = e.length; t < i; t++)
      this.expandByPoint(e[t]);
    return this;
  }
  setFromCenterAndSize(e, t) {
    const i = pT.copy(t).multiplyScalar(0.5);
    return this.min.copy(e).sub(i), this.max.copy(e).add(i), this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.min.copy(e.min), this.max.copy(e.max), this;
  }
  makeEmpty() {
    return this.min.x = this.min.y = 1 / 0, this.max.x = this.max.y = -1 / 0, this;
  }
  isEmpty() {
    return this.max.x < this.min.x || this.max.y < this.min.y;
  }
  getCenter(e) {
    return this.isEmpty() ? e.set(0, 0) : e.addVectors(this.min, this.max).multiplyScalar(0.5);
  }
  getSize(e) {
    return this.isEmpty() ? e.set(0, 0) : e.subVectors(this.max, this.min);
  }
  expandByPoint(e) {
    return this.min.min(e), this.max.max(e), this;
  }
  expandByVector(e) {
    return this.min.sub(e), this.max.add(e), this;
  }
  expandByScalar(e) {
    return this.min.addScalar(-e), this.max.addScalar(e), this;
  }
  containsPoint(e) {
    return !(e.x < this.min.x || e.x > this.max.x || e.y < this.min.y || e.y > this.max.y);
  }
  containsBox(e) {
    return this.min.x <= e.min.x && e.max.x <= this.max.x && this.min.y <= e.min.y && e.max.y <= this.max.y;
  }
  getParameter(e, t) {
    return t.set(
      (e.x - this.min.x) / (this.max.x - this.min.x),
      (e.y - this.min.y) / (this.max.y - this.min.y)
    );
  }
  intersectsBox(e) {
    return !(e.max.x < this.min.x || e.min.x > this.max.x || e.max.y < this.min.y || e.min.y > this.max.y);
  }
  clampPoint(e, t) {
    return t.copy(e).clamp(this.min, this.max);
  }
  distanceToPoint(e) {
    return this.clampPoint(e, pT).distanceTo(e);
  }
  intersect(e) {
    return this.min.max(e.min), this.max.min(e.max), this.isEmpty() && this.makeEmpty(), this;
  }
  union(e) {
    return this.min.min(e.min), this.max.max(e.max), this;
  }
  translate(e) {
    return this.min.add(e), this.max.add(e), this;
  }
  equals(e) {
    return e.min.equals(this.min) && e.max.equals(this.max);
  }
}
const mT = /* @__PURE__ */ new _(), Ov = /* @__PURE__ */ new _();
class Qs {
  constructor(e = new _(), t = new _()) {
    this.start = e, this.end = t;
  }
  set(e, t) {
    return this.start.copy(e), this.end.copy(t), this;
  }
  copy(e) {
    return this.start.copy(e.start), this.end.copy(e.end), this;
  }
  getCenter(e) {
    return e.addVectors(this.start, this.end).multiplyScalar(0.5);
  }
  delta(e) {
    return e.subVectors(this.end, this.start);
  }
  distanceSq() {
    return this.start.distanceToSquared(this.end);
  }
  distance() {
    return this.start.distanceTo(this.end);
  }
  at(e, t) {
    return this.delta(t).multiplyScalar(e).add(this.start);
  }
  closestPointToPointParameter(e, t) {
    mT.subVectors(e, this.start), Ov.subVectors(this.end, this.start);
    const i = Ov.dot(Ov);
    let r = Ov.dot(mT) / i;
    return t && (r = Wn(r, 0, 1)), r;
  }
  closestPointToPoint(e, t, i) {
    const n = this.closestPointToPointParameter(e, t);
    return this.delta(i).multiplyScalar(n).add(this.start);
  }
  applyMatrix4(e) {
    return this.start.applyMatrix4(e), this.end.applyMatrix4(e), this;
  }
  equals(e) {
    return e.start.equals(this.start) && e.end.equals(this.end);
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const gT = /* @__PURE__ */ new _();
class Vj extends mt {
  constructor(e, t) {
    super(), this.light = e, this.matrixAutoUpdate = !1, this.color = t, this.type = "SpotLightHelper";
    const i = new Pt(), n = [
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      1,
      0,
      0,
      0,
      -1,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      1,
      0,
      0,
      0,
      0,
      -1,
      1
    ];
    for (let a = 0, o = 1, l = 32; a < l; a++, o++) {
      const c = a / l * Math.PI * 2, h = o / l * Math.PI * 2;
      n.push(
        Math.cos(c),
        Math.sin(c),
        1,
        Math.cos(h),
        Math.sin(h),
        1
      );
    }
    i.setAttribute("position", new gt(n, 3));
    const r = new Bi({ fog: !1, toneMapped: !1 });
    this.cone = new _n(i, r), this.add(this.cone), this.update();
  }
  dispose() {
    this.cone.geometry.dispose(), this.cone.material.dispose();
  }
  update() {
    this.light.updateWorldMatrix(!0, !1), this.light.target.updateWorldMatrix(!0, !1), this.parent ? (this.parent.updateWorldMatrix(!0), this.matrix.copy(this.parent.matrixWorld).invert().multiply(this.light.matrixWorld)) : this.matrix.copy(this.light.matrixWorld), this.matrixWorld.copy(this.light.matrixWorld);
    const e = this.light.distance ? this.light.distance : 1e3, t = e * Math.tan(this.light.angle);
    this.cone.scale.set(t, t, e), gT.setFromMatrixPosition(this.light.target.matrixWorld), this.cone.lookAt(gT), this.color !== void 0 ? this.cone.material.color.set(this.color) : this.cone.material.color.copy(this.light.color);
  }
}
const Gl = /* @__PURE__ */ new _(), Nv = /* @__PURE__ */ new et(), r5 = /* @__PURE__ */ new et();
class Wj extends _n {
  constructor(e) {
    const t = CI(e), i = new Pt(), n = [], r = [], a = new _e(0, 0, 1), o = new _e(0, 1, 0);
    for (let c = 0; c < t.length; c++) {
      const h = t[c];
      h.parent && h.parent.isBone && (n.push(0, 0, 0), n.push(0, 0, 0), r.push(a.r, a.g, a.b), r.push(o.r, o.g, o.b));
    }
    i.setAttribute("position", new gt(n, 3)), i.setAttribute("color", new gt(r, 3));
    const l = new Bi({ vertexColors: !0, depthTest: !1, depthWrite: !1, toneMapped: !1, transparent: !0 });
    super(i, l), this.isSkeletonHelper = !0, this.type = "SkeletonHelper", this.root = e, this.bones = t, this.matrix = e.matrixWorld, this.matrixAutoUpdate = !1;
  }
  updateMatrixWorld(e) {
    const t = this.bones, i = this.geometry, n = i.getAttribute("position");
    r5.copy(this.root.matrixWorld).invert();
    for (let r = 0, a = 0; r < t.length; r++) {
      const o = t[r];
      o.parent && o.parent.isBone && (Nv.multiplyMatrices(r5, o.matrixWorld), Gl.setFromMatrixPosition(Nv), n.setXYZ(a, Gl.x, Gl.y, Gl.z), Nv.multiplyMatrices(r5, o.parent.matrixWorld), Gl.setFromMatrixPosition(Nv), n.setXYZ(a + 1, Gl.x, Gl.y, Gl.z), a += 2);
    }
    i.getAttribute("position").needsUpdate = !0, super.updateMatrixWorld(e);
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
function CI(s) {
  const e = [];
  s.isBone === !0 && e.push(s);
  for (let t = 0; t < s.children.length; t++)
    e.push.apply(e, CI(s.children[t]));
  return e;
}
class Xj extends Re {
  constructor(e, t, i) {
    const n = new Hs(t, 4, 2), r = new kt({ wireframe: !0, fog: !1, toneMapped: !1 });
    super(n, r), this.light = e, this.color = i, this.type = "PointLightHelper", this.matrix = this.light.matrixWorld, this.matrixAutoUpdate = !1, this.update();
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
  update() {
    this.light.updateWorldMatrix(!0, !1), this.color !== void 0 ? this.material.color.set(this.color) : this.material.color.copy(this.light.color);
  }
}
const jj = /* @__PURE__ */ new _(), vT = /* @__PURE__ */ new _e(), yT = /* @__PURE__ */ new _e();
class qj extends mt {
  constructor(e, t, i) {
    super(), this.light = e, this.matrix = e.matrixWorld, this.matrixAutoUpdate = !1, this.color = i, this.type = "HemisphereLightHelper";
    const n = new Nr(t);
    n.rotateY(Math.PI * 0.5), this.material = new kt({ wireframe: !0, fog: !1, toneMapped: !1 }), this.color === void 0 && (this.material.vertexColors = !0);
    const r = n.getAttribute("position"), a = new Float32Array(r.count * 3);
    n.setAttribute("color", new Ht(a, 3)), this.add(new Re(n, this.material)), this.update();
  }
  dispose() {
    this.children[0].geometry.dispose(), this.children[0].material.dispose();
  }
  update() {
    const e = this.children[0];
    if (this.color !== void 0)
      this.material.color.set(this.color);
    else {
      const t = e.geometry.getAttribute("color");
      vT.copy(this.light.color), yT.copy(this.light.groundColor);
      for (let i = 0, n = t.count; i < n; i++) {
        const r = i < n / 2 ? vT : yT;
        t.setXYZ(i, r.r, r.g, r.b);
      }
      t.needsUpdate = !0;
    }
    this.light.updateWorldMatrix(!0, !1), e.lookAt(jj.setFromMatrixPosition(this.light.matrixWorld).negate());
  }
}
class Yj extends _n {
  constructor(e = 10, t = 10, i = 4473924, n = 8947848) {
    i = new _e(i), n = new _e(n);
    const r = t / 2, a = e / t, o = e / 2, l = [], c = [];
    for (let d = 0, f = 0, p = -o; d <= t; d++, p += a) {
      l.push(-o, 0, p, o, 0, p), l.push(p, 0, -o, p, 0, o);
      const v = d === r ? i : n;
      v.toArray(c, f), f += 3, v.toArray(c, f), f += 3, v.toArray(c, f), f += 3, v.toArray(c, f), f += 3;
    }
    const h = new Pt();
    h.setAttribute("position", new gt(l, 3)), h.setAttribute("color", new gt(c, 3));
    const u = new Bi({ vertexColors: !0, toneMapped: !1 });
    super(h, u), this.type = "GridHelper";
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
class Zj extends _n {
  constructor(e = 10, t = 16, i = 8, n = 64, r = 4473924, a = 8947848) {
    r = new _e(r), a = new _e(a);
    const o = [], l = [];
    if (t > 1)
      for (let u = 0; u < t; u++) {
        const d = u / t * (Math.PI * 2), f = Math.sin(d) * e, p = Math.cos(d) * e;
        o.push(0, 0, 0), o.push(f, 0, p);
        const v = u & 1 ? r : a;
        l.push(v.r, v.g, v.b), l.push(v.r, v.g, v.b);
      }
    for (let u = 0; u < i; u++) {
      const d = u & 1 ? r : a, f = e - e / i * u;
      for (let p = 0; p < n; p++) {
        let v = p / n * (Math.PI * 2), g = Math.sin(v) * f, m = Math.cos(v) * f;
        o.push(g, 0, m), l.push(d.r, d.g, d.b), v = (p + 1) / n * (Math.PI * 2), g = Math.sin(v) * f, m = Math.cos(v) * f, o.push(g, 0, m), l.push(d.r, d.g, d.b);
      }
    }
    const c = new Pt();
    c.setAttribute("position", new gt(o, 3)), c.setAttribute("color", new gt(l, 3));
    const h = new Bi({ vertexColors: !0, toneMapped: !1 });
    super(c, h), this.type = "PolarGridHelper";
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
const xT = /* @__PURE__ */ new _(), Bv = /* @__PURE__ */ new _(), AT = /* @__PURE__ */ new _();
class aS extends mt {
  constructor(e, t, i) {
    super(), this.light = e, this.matrix = e.matrixWorld, this.matrixAutoUpdate = !1, this.color = i, this.type = "DirectionalLightHelper", t === void 0 && (t = 1);
    let n = new Pt();
    n.setAttribute("position", new gt([
      -t,
      t,
      0,
      t,
      t,
      0,
      t,
      -t,
      0,
      -t,
      -t,
      0,
      -t,
      t,
      0
    ], 3));
    const r = new Bi({ fog: !1, toneMapped: !1 });
    this.lightPlane = new ti(n, r), this.add(this.lightPlane), n = new Pt(), n.setAttribute("position", new gt([0, 0, 0, 0, 0, 1], 3)), this.targetLine = new ti(n, r), this.add(this.targetLine), this.update();
  }
  dispose() {
    this.lightPlane.geometry.dispose(), this.lightPlane.material.dispose(), this.targetLine.geometry.dispose(), this.targetLine.material.dispose();
  }
  update() {
    this.light.updateWorldMatrix(!0, !1), this.light.target.updateWorldMatrix(!0, !1), xT.setFromMatrixPosition(this.light.matrixWorld), Bv.setFromMatrixPosition(this.light.target.matrixWorld), AT.subVectors(Bv, xT), this.lightPlane.lookAt(Bv), this.color !== void 0 ? (this.lightPlane.material.color.set(this.color), this.targetLine.material.color.set(this.color)) : (this.lightPlane.material.color.copy(this.light.color), this.targetLine.material.color.copy(this.light.color)), this.targetLine.lookAt(Bv), this.targetLine.scale.z = AT.length();
  }
}
const Fv = /* @__PURE__ */ new _(), kn = /* @__PURE__ */ new s2();
class b2 extends _n {
  constructor(e) {
    const t = new Pt(), i = new Bi({ color: 16777215, vertexColors: !0, toneMapped: !1 }), n = [], r = [], a = {};
    o("n1", "n2"), o("n2", "n4"), o("n4", "n3"), o("n3", "n1"), o("f1", "f2"), o("f2", "f4"), o("f4", "f3"), o("f3", "f1"), o("n1", "f1"), o("n2", "f2"), o("n3", "f3"), o("n4", "f4"), o("p", "n1"), o("p", "n2"), o("p", "n3"), o("p", "n4"), o("u1", "u2"), o("u2", "u3"), o("u3", "u1"), o("c", "t"), o("p", "c"), o("cn1", "cn2"), o("cn3", "cn4"), o("cf1", "cf2"), o("cf3", "cf4");
    function o(p, v) {
      l(p), l(v);
    }
    function l(p) {
      n.push(0, 0, 0), r.push(0, 0, 0), a[p] === void 0 && (a[p] = []), a[p].push(n.length / 3 - 1);
    }
    t.setAttribute("position", new gt(n, 3)), t.setAttribute("color", new gt(r, 3)), super(t, i), this.type = "CameraHelper", this.camera = e, this.camera.updateProjectionMatrix && this.camera.updateProjectionMatrix(), this.matrix = e.matrixWorld, this.matrixAutoUpdate = !1, this.pointMap = a, this.update();
    const c = new _e(16755200), h = new _e(16711680), u = new _e(43775), d = new _e(16777215), f = new _e(3355443);
    this.setColors(c, h, u, d, f);
  }
  setColors(e, t, i, n, r) {
    const o = this.geometry.getAttribute("color");
    o.setXYZ(0, e.r, e.g, e.b), o.setXYZ(1, e.r, e.g, e.b), o.setXYZ(2, e.r, e.g, e.b), o.setXYZ(3, e.r, e.g, e.b), o.setXYZ(4, e.r, e.g, e.b), o.setXYZ(5, e.r, e.g, e.b), o.setXYZ(6, e.r, e.g, e.b), o.setXYZ(7, e.r, e.g, e.b), o.setXYZ(8, e.r, e.g, e.b), o.setXYZ(9, e.r, e.g, e.b), o.setXYZ(10, e.r, e.g, e.b), o.setXYZ(11, e.r, e.g, e.b), o.setXYZ(12, e.r, e.g, e.b), o.setXYZ(13, e.r, e.g, e.b), o.setXYZ(14, e.r, e.g, e.b), o.setXYZ(15, e.r, e.g, e.b), o.setXYZ(16, e.r, e.g, e.b), o.setXYZ(17, e.r, e.g, e.b), o.setXYZ(18, e.r, e.g, e.b), o.setXYZ(19, e.r, e.g, e.b), o.setXYZ(20, e.r, e.g, e.b), o.setXYZ(21, e.r, e.g, e.b), o.setXYZ(22, e.r, e.g, e.b), o.setXYZ(23, e.r, e.g, e.b), o.setXYZ(24, t.r, t.g, t.b), o.setXYZ(25, t.r, t.g, t.b), o.setXYZ(26, t.r, t.g, t.b), o.setXYZ(27, t.r, t.g, t.b), o.setXYZ(28, t.r, t.g, t.b), o.setXYZ(29, t.r, t.g, t.b), o.setXYZ(30, t.r, t.g, t.b), o.setXYZ(31, t.r, t.g, t.b), o.setXYZ(32, i.r, i.g, i.b), o.setXYZ(33, i.r, i.g, i.b), o.setXYZ(34, i.r, i.g, i.b), o.setXYZ(35, i.r, i.g, i.b), o.setXYZ(36, i.r, i.g, i.b), o.setXYZ(37, i.r, i.g, i.b), o.setXYZ(38, n.r, n.g, n.b), o.setXYZ(39, n.r, n.g, n.b), o.setXYZ(40, r.r, r.g, r.b), o.setXYZ(41, r.r, r.g, r.b), o.setXYZ(42, r.r, r.g, r.b), o.setXYZ(43, r.r, r.g, r.b), o.setXYZ(44, r.r, r.g, r.b), o.setXYZ(45, r.r, r.g, r.b), o.setXYZ(46, r.r, r.g, r.b), o.setXYZ(47, r.r, r.g, r.b), o.setXYZ(48, r.r, r.g, r.b), o.setXYZ(49, r.r, r.g, r.b), o.needsUpdate = !0;
  }
  update() {
    const e = this.geometry, t = this.pointMap, i = 1, n = 1;
    kn.projectionMatrixInverse.copy(this.camera.projectionMatrixInverse), Yn("c", t, e, kn, 0, 0, -1), Yn("t", t, e, kn, 0, 0, 1), Yn("n1", t, e, kn, -i, -n, -1), Yn("n2", t, e, kn, i, -n, -1), Yn("n3", t, e, kn, -i, n, -1), Yn("n4", t, e, kn, i, n, -1), Yn("f1", t, e, kn, -i, -n, 1), Yn("f2", t, e, kn, i, -n, 1), Yn("f3", t, e, kn, -i, n, 1), Yn("f4", t, e, kn, i, n, 1), Yn("u1", t, e, kn, i * 0.7, n * 1.1, -1), Yn("u2", t, e, kn, -i * 0.7, n * 1.1, -1), Yn("u3", t, e, kn, 0, n * 2, -1), Yn("cf1", t, e, kn, -i, 0, 1), Yn("cf2", t, e, kn, i, 0, 1), Yn("cf3", t, e, kn, 0, -n, 1), Yn("cf4", t, e, kn, 0, n, 1), Yn("cn1", t, e, kn, -i, 0, -1), Yn("cn2", t, e, kn, i, 0, -1), Yn("cn3", t, e, kn, 0, -n, -1), Yn("cn4", t, e, kn, 0, n, -1), e.getAttribute("position").needsUpdate = !0;
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
function Yn(s, e, t, i, n, r, a) {
  Fv.set(n, r, a).unproject(i);
  const o = e[s];
  if (o !== void 0) {
    const l = t.getAttribute("position");
    for (let c = 0, h = o.length; c < h; c++)
      l.setXYZ(o[c], Fv.x, Fv.y, Fv.z);
  }
}
const Uv = /* @__PURE__ */ new zt();
class eb extends _n {
  constructor(e, t = 16776960) {
    const i = new Uint16Array([0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7]), n = new Float32Array(8 * 3), r = new Pt();
    r.setIndex(new Ht(i, 1)), r.setAttribute("position", new Ht(n, 3)), super(r, new Bi({ color: t, toneMapped: !1 })), this.object = e, this.type = "BoxHelper", this.matrixAutoUpdate = !1, this.update();
  }
  update(e) {
    if (e !== void 0 && console.warn("THREE.BoxHelper: .update() has no longer arguments."), this.object !== void 0 && Uv.setFromObject(this.object), Uv.isEmpty())
      return;
    const t = Uv.min, i = Uv.max, n = this.geometry.attributes.position, r = n.array;
    r[0] = i.x, r[1] = i.y, r[2] = i.z, r[3] = t.x, r[4] = i.y, r[5] = i.z, r[6] = t.x, r[7] = t.y, r[8] = i.z, r[9] = i.x, r[10] = t.y, r[11] = i.z, r[12] = i.x, r[13] = i.y, r[14] = t.z, r[15] = t.x, r[16] = i.y, r[17] = t.z, r[18] = t.x, r[19] = t.y, r[20] = t.z, r[21] = i.x, r[22] = t.y, r[23] = t.z, n.needsUpdate = !0, this.geometry.computeBoundingSphere();
  }
  setFromObject(e) {
    return this.object = e, this.update(), this;
  }
  copy(e, t) {
    return super.copy(e, t), this.object = e.object, this;
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
class L4 extends _n {
  constructor(e, t = 16776960) {
    const i = new Uint16Array([0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7]), n = [1, 1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1, 1, 1, -1, -1, 1, -1, -1, -1, -1, 1, -1, -1], r = new Pt();
    r.setIndex(new Ht(i, 1)), r.setAttribute("position", new gt(n, 3)), super(r, new Bi({ color: t, toneMapped: !1 })), this.box = e, this.type = "Box3Helper", this.geometry.computeBoundingSphere();
  }
  updateMatrixWorld(e) {
    const t = this.box;
    t.isEmpty() || (t.getCenter(this.position), t.getSize(this.scale), this.scale.multiplyScalar(0.5), super.updateMatrixWorld(e));
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
class Kj extends ti {
  constructor(e, t = 1, i = 16776960) {
    const n = i, r = [1, -1, 0, -1, 1, 0, -1, -1, 0, 1, 1, 0, -1, 1, 0, -1, -1, 0, 1, -1, 0, 1, 1, 0], a = new Pt();
    a.setAttribute("position", new gt(r, 3)), a.computeBoundingSphere(), super(a, new Bi({ color: n, toneMapped: !1 })), this.type = "PlaneHelper", this.plane = e, this.size = t;
    const o = [1, 1, 0, -1, 1, 0, -1, -1, 0, 1, 1, 0, -1, -1, 0, 1, -1, 0], l = new Pt();
    l.setAttribute("position", new gt(o, 3)), l.computeBoundingSphere(), this.add(new Re(l, new kt({ color: n, opacity: 0.2, transparent: !0, depthWrite: !1, toneMapped: !1 })));
  }
  updateMatrixWorld(e) {
    this.position.set(0, 0, 0), this.scale.set(0.5 * this.size, 0.5 * this.size, 1), this.lookAt(this.plane.normal), this.translateZ(-this.plane.constant), super.updateMatrixWorld(e);
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose(), this.children[0].geometry.dispose(), this.children[0].material.dispose();
  }
}
const bT = /* @__PURE__ */ new _();
let zv, a5;
class Jj extends mt {
  // dir is assumed to be normalized
  constructor(e = new _(0, 0, 1), t = new _(0, 0, 0), i = 1, n = 16776960, r = i * 0.2, a = r * 0.2) {
    super(), this.type = "ArrowHelper", zv === void 0 && (zv = new Pt(), zv.setAttribute("position", new gt([0, 0, 0, 0, 1, 0], 3)), a5 = new Ei(0, 0.5, 1, 5, 1), a5.translate(0, -0.5, 0)), this.position.copy(t), this.line = new ti(zv, new Bi({ color: n, toneMapped: !1 })), this.line.matrixAutoUpdate = !1, this.add(this.line), this.cone = new Re(a5, new kt({ color: n, toneMapped: !1 })), this.cone.matrixAutoUpdate = !1, this.add(this.cone), this.setDirection(e), this.setLength(i, r, a);
  }
  setDirection(e) {
    if (e.y > 0.99999)
      this.quaternion.set(0, 0, 0, 1);
    else if (e.y < -0.99999)
      this.quaternion.set(1, 0, 0, 0);
    else {
      bT.set(e.z, 0, -e.x).normalize();
      const t = Math.acos(e.y);
      this.quaternion.setFromAxisAngle(bT, t);
    }
  }
  setLength(e, t = e * 0.2, i = t * 0.2) {
    this.line.scale.set(1, Math.max(1e-4, e - t), 1), this.line.updateMatrix(), this.cone.scale.set(i, t, i), this.cone.position.y = e, this.cone.updateMatrix();
  }
  setColor(e) {
    this.line.material.color.set(e), this.cone.material.color.set(e);
  }
  copy(e) {
    return super.copy(e, !1), this.line.copy(e.line), this.cone.copy(e.cone), this;
  }
  dispose() {
    this.line.geometry.dispose(), this.line.material.dispose(), this.cone.geometry.dispose(), this.cone.material.dispose();
  }
}
class Qj extends _n {
  constructor(e = 1) {
    const t = [
      0,
      0,
      0,
      e,
      0,
      0,
      0,
      0,
      0,
      0,
      e,
      0,
      0,
      0,
      0,
      0,
      0,
      e
    ], i = [
      1,
      0,
      0,
      1,
      0.6,
      0,
      0,
      1,
      0,
      0.6,
      1,
      0,
      0,
      0,
      1,
      0,
      0.6,
      1
    ], n = new Pt();
    n.setAttribute("position", new gt(t, 3)), n.setAttribute("color", new gt(i, 3));
    const r = new Bi({ vertexColors: !0, toneMapped: !1 });
    super(n, r), this.type = "AxesHelper";
  }
  setColors(e, t, i) {
    const n = new _e(), r = this.geometry.attributes.color.array;
    return n.set(e), n.toArray(r, 0), n.toArray(r, 3), n.set(t), n.toArray(r, 6), n.toArray(r, 9), n.set(i), n.toArray(r, 12), n.toArray(r, 15), this.geometry.attributes.color.needsUpdate = !0, this;
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
class $j {
  constructor() {
    this.type = "ShapePath", this.color = new _e(), this.subPaths = [], this.currentPath = null;
  }
  moveTo(e, t) {
    return this.currentPath = new eg(), this.subPaths.push(this.currentPath), this.currentPath.moveTo(e, t), this;
  }
  lineTo(e, t) {
    return this.currentPath.lineTo(e, t), this;
  }
  quadraticCurveTo(e, t, i, n) {
    return this.currentPath.quadraticCurveTo(e, t, i, n), this;
  }
  bezierCurveTo(e, t, i, n, r, a) {
    return this.currentPath.bezierCurveTo(e, t, i, n, r, a), this;
  }
  splineThru(e) {
    return this.currentPath.splineThru(e), this;
  }
  toShapes(e) {
    function t(m) {
      const x = [];
      for (let y = 0, A = m.length; y < A; y++) {
        const b = m[y], M = new ll();
        M.curves = b.curves, x.push(M);
      }
      return x;
    }
    function i(m, x) {
      const y = x.length;
      let A = !1;
      for (let b = y - 1, M = 0; M < y; b = M++) {
        let w = x[b], C = x[M], T = C.x - w.x, E = C.y - w.y;
        if (Math.abs(E) > Number.EPSILON) {
          if (E < 0 && (w = x[M], T = -T, C = x[b], E = -E), m.y < w.y || m.y > C.y)
            continue;
          if (m.y === w.y) {
            if (m.x === w.x)
              return !0;
          } else {
            const P = E * (m.x - w.x) - T * (m.y - w.y);
            if (P === 0)
              return !0;
            if (P < 0)
              continue;
            A = !A;
          }
        } else {
          if (m.y !== w.y)
            continue;
          if (C.x <= m.x && m.x <= w.x || w.x <= m.x && m.x <= C.x)
            return !0;
        }
      }
      return A;
    }
    const n = ta.isClockWise, r = this.subPaths;
    if (r.length === 0)
      return [];
    let a, o, l;
    const c = [];
    if (r.length === 1)
      return o = r[0], l = new ll(), l.curves = o.curves, c.push(l), c;
    let h = !n(r[0].getPoints());
    h = e ? !h : h;
    const u = [], d = [];
    let f = [], p = 0, v;
    d[p] = void 0, f[p] = [];
    for (let m = 0, x = r.length; m < x; m++)
      o = r[m], v = o.getPoints(), a = n(v), a = e ? !a : a, a ? (!h && d[p] && p++, d[p] = { s: new ll(), p: v }, d[p].s.curves = o.curves, h && p++, f[p] = []) : f[p].push({ h: o, p: v[0] });
    if (!d[0])
      return t(r);
    if (d.length > 1) {
      let m = !1, x = 0;
      for (let y = 0, A = d.length; y < A; y++)
        u[y] = [];
      for (let y = 0, A = d.length; y < A; y++) {
        const b = f[y];
        for (let M = 0; M < b.length; M++) {
          const w = b[M];
          let C = !0;
          for (let T = 0; T < d.length; T++)
            i(w.p, d[T].p) && (y !== T && x++, C ? (C = !1, u[T].push(w)) : m = !0);
          C && u[y].push(w);
        }
      }
      x > 0 && m === !1 && (f = u);
    }
    let g;
    for (let m = 0, x = d.length; m < x; m++) {
      l = d[m].s, c.push(l), g = f[m];
      for (let y = 0, A = g.length; y < A; y++)
        l.holes.push(g[y].h);
    }
    return c;
  }
}
class eq extends Xi {
  // @deprecated, r162
  constructor(e = 1, t = 1, i = 1, n = {}) {
    console.warn('THREE.WebGLMultipleRenderTargets has been deprecated and will be removed in r172. Use THREE.WebGLRenderTarget and set the "count" parameter to enable MRT.'), super(e, t, { ...n, count: i }), this.isWebGLMultipleRenderTargets = !0;
  }
  get texture() {
    return this.textures;
  }
}
typeof __THREE_DEVTOOLS__ < "u" && __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent("register", { detail: {
  revision: Qo
} }));
typeof window < "u" && (window.__THREE__ ? console.warn("WARNING: Multiple instances of Three.js being imported.") : window.__THREE__ = Qo);
const _I = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ACESFilmicToneMapping: Hp,
  AddEquation: Ma,
  AddOperation: SL,
  AdditiveAnimationBlendMode: DM,
  AdditiveBlending: oo,
  AgXToneMapping: yM,
  AlphaFormat: SM,
  AlwaysCompare: BL,
  AlwaysDepth: gL,
  AlwaysStencilFunc: V6,
  AmbientLight: Gc,
  AnimationAction: TI,
  AnimationClip: xl,
  AnimationLoader: dj,
  AnimationMixer: rS,
  AnimationObjectGroup: Nj,
  AnimationUtils: Q6,
  ArcCurve: aI,
  ArrayCamera: nI,
  ArrowHelper: Jj,
  AttachedBindMode: H6,
  Audio: wI,
  AudioAnalyser: Ej,
  AudioContext: iS,
  AudioListener: Mj,
  AudioLoader: xj,
  AxesHelper: Qj,
  BackSide: Ps,
  BasicDepthPacking: _L,
  BasicShadowMap: xz,
  BatchedMesh: rI,
  Bone: ih,
  BooleanKeyframeTrack: Yu,
  Box2: Hj,
  Box3: zt,
  Box3Helper: L4,
  BoxGeometry: oi,
  BoxHelper: eb,
  BufferAttribute: Ht,
  BufferGeometry: Pt,
  BufferGeometryLoader: tS,
  ByteType: AM,
  Cache: tl,
  Camera: s2,
  CameraHelper: b2,
  CanvasTexture: zc,
  CapsuleGeometry: _u,
  CatmullRomCurve3: Mu,
  CineonToneMapping: vM,
  CircleGeometry: o2,
  ClampToEdgeWrapping: yn,
  Clock: Ju,
  Color: _e,
  ColorKeyframeTrack: QM,
  ColorManagement: bi,
  CompressedArrayTexture: LX,
  CompressedCubeTexture: IX,
  CompressedTexture: T4,
  CompressedTextureLoader: fj,
  ConeGeometry: qp,
  ConstantAlphaFactor: fL,
  ConstantColorFactor: uL,
  CubeCamera: UM,
  CubeReflectionMapping: lo,
  CubeRefractionMapping: a0,
  CubeTexture: Vp,
  CubeTextureLoader: pj,
  CubeUVReflectionMapping: Qc,
  CubicBezierCurve: WM,
  CubicBezierCurve3: oI,
  CubicInterpolant: xI,
  CullFaceBack: G6,
  CullFaceFront: eL,
  CullFaceFrontBack: yz,
  CullFaceNone: $D,
  Curve: vo,
  CurvePath: lI,
  CustomBlending: u4,
  CustomToneMapping: wL,
  CylinderGeometry: Ei,
  Cylindrical: Gj,
  Data3DTexture: BM,
  DataArrayTexture: b4,
  DataTexture: Qn,
  DataTextureLoader: R4,
  DataUtils: el,
  DecrementStencilOp: _z,
  DecrementWrapStencilOp: Rz,
  DefaultLoadingManager: Ku,
  DepthFormat: Au,
  DepthStencilFormat: yl,
  DepthTexture: r2,
  DetachedBindMode: EL,
  DirectionalLight: _a,
  DirectionalLightHelper: aS,
  DiscreteInterpolant: AI,
  DisplayP3ColorSpace: A4,
  DodecahedronGeometry: l2,
  DoubleSide: ui,
  DstAlphaFactor: d4,
  DstColorFactor: f4,
  DynamicCopyUsage: Wz,
  DynamicDrawUsage: Js,
  DynamicReadUsage: Gz,
  EdgesGeometry: ap,
  EllipseCurve: C4,
  EqualCompare: LL,
  EqualDepth: yL,
  EqualStencilFunc: Oz,
  EquirectangularReflectionMapping: ml,
  EquirectangularRefractionMapping: Fc,
  Euler: Mi,
  EventDispatcher: ra,
  ExtrudeGeometry: c2,
  FileLoader: Nn,
  Float16BufferAttribute: Rk,
  Float32BufferAttribute: gt,
  FloatType: xn,
  Fog: Wu,
  FogExp2: Xp,
  FramebufferTexture: DX,
  FrontSide: sa,
  Frustum: th,
  GLBufferAttribute: kj,
  GLSL1: jz,
  GLSL3: W6,
  GreaterCompare: IL,
  GreaterDepth: AL,
  GreaterEqualCompare: NL,
  GreaterEqualDepth: xL,
  GreaterEqualStencilFunc: Uz,
  GreaterStencilFunc: Bz,
  GridHelper: Yj,
  Group: ci,
  HalfFloatType: on,
  HemisphereLight: v2,
  HemisphereLightHelper: qj,
  IcosahedronGeometry: h2,
  ImageBitmapLoader: A2,
  ImageLoader: Ru,
  ImageUtils: GL,
  IncrementStencilOp: Cz,
  IncrementWrapStencilOp: Pz,
  InstancedBufferAttribute: hs,
  InstancedBufferGeometry: eS,
  InstancedInterleavedBuffer: zj,
  InstancedMesh: ho,
  Int16BufferAttribute: _k,
  Int32BufferAttribute: Pk,
  Int8BufferAttribute: Ek,
  IntType: p4,
  InterleavedBuffer: Xu,
  InterleavedBufferAttribute: c0,
  Interpolant: Is,
  InterpolateDiscrete: Uc,
  InterpolateLinear: l0,
  InterpolateSmooth: Sy,
  InvertStencilOp: Dz,
  KeepStencilOp: jh,
  KeyframeTrack: yo,
  LOD: E4,
  LatheGeometry: jp,
  Layers: e0,
  LessCompare: DL,
  LessDepth: vL,
  LessEqualCompare: IM,
  LessEqualDepth: j1,
  LessEqualStencilFunc: Nz,
  LessStencilFunc: Iz,
  Light: ah,
  LightProbe: D4,
  Line: ti,
  Line3: Qs,
  LineBasicMaterial: Bi,
  LineCurve: XM,
  LineCurve3: Y9,
  LineDashedMaterial: gI,
  LineLoop: ju,
  LineSegments: _n,
  LinearDisplayP3ColorSpace: n2,
  LinearFilter: ii,
  LinearInterpolant: sg,
  LinearMipMapLinearFilter: Sz,
  LinearMipMapNearestFilter: Mz,
  LinearMipmapLinearFilter: ds,
  LinearMipmapNearestFilter: $o,
  LinearSRGBColorSpace: Si,
  LinearToneMapping: mM,
  LinearTransfer: Y1,
  Loader: As,
  LoaderUtils: Rs,
  LoadingManager: g2,
  LoopOnce: PM,
  LoopPingPong: TL,
  LoopRepeat: RM,
  LuminanceAlphaFormat: TM,
  LuminanceFormat: EM,
  MOUSE: Lr,
  Material: Cn,
  MaterialLoader: x2,
  MathUtils: nt,
  Matrix3: Gt,
  Matrix4: et,
  MaxEquation: sL,
  Mesh: Re,
  MeshBasicMaterial: kt,
  MeshDepthMaterial: w4,
  MeshDistanceMaterial: GM,
  MeshLambertMaterial: pI,
  MeshMatcapMaterial: mI,
  MeshNormalMaterial: P4,
  MeshPhongMaterial: m2,
  MeshPhysicalMaterial: nn,
  MeshStandardMaterial: or,
  MeshToonMaterial: fI,
  MinEquation: nL,
  MirroredRepeatWrapping: co,
  MixOperation: ML,
  MultiplyBlending: g9,
  MultiplyOperation: e2,
  NearestFilter: Ni,
  NearestMipMapLinearFilter: bz,
  NearestMipMapNearestFilter: Az,
  NearestMipmapLinearFilter: $a,
  NearestMipmapNearestFilter: $c,
  NeutralToneMapping: xM,
  NeverCompare: RL,
  NeverDepth: mL,
  NeverStencilFunc: Lz,
  NoBlending: vn,
  NoColorSpace: Uo,
  NoToneMapping: ol,
  NormalAnimationBlendMode: x4,
  NormalBlending: so,
  NotEqualCompare: OL,
  NotEqualDepth: bL,
  NotEqualStencilFunc: Fz,
  NumberKeyframeTrack: uo,
  Object3D: mt,
  ObjectLoader: SI,
  ObjectSpaceNormalMap: PL,
  OctahedronGeometry: Nr,
  OneFactor: rL,
  OneMinusConstantAlphaFactor: pL,
  OneMinusConstantColorFactor: dL,
  OneMinusDstAlphaFactor: lL,
  OneMinusDstColorFactor: cL,
  OneMinusSrcAlphaFactor: y9,
  OneMinusSrcColorFactor: oL,
  OrthographicCamera: Xn,
  P3Primaries: K1,
  PCFShadowMap: pM,
  PCFSoftShadowMap: $g,
  PMREMGenerator: rp,
  Path: eg,
  PerspectiveCamera: Zt,
  Plane: ln,
  PlaneGeometry: $n,
  PlaneHelper: Kj,
  PointLight: Tl,
  PointLightHelper: Xj,
  Points: sh,
  PointsMaterial: qu,
  PolarGridHelper: Zj,
  PolyhedronGeometry: El,
  PositionalAudio: wj,
  PropertyBinding: Pi,
  PropertyMixer: EI,
  QuadraticBezierCurve: jM,
  QuadraticBezierCurve3: qM,
  Quaternion: St,
  QuaternionKeyframeTrack: fo,
  QuaternionLinearInterpolant: bI,
  RED_GREEN_RGTC2_Format: V9,
  RED_RGTC1_Format: _M,
  REVISION: Qo,
  RGBADepthPacking: LM,
  RGBAFormat: dn,
  RGBAIntegerFormat: y4,
  RGBA_ASTC_10x10_Format: F9,
  RGBA_ASTC_10x5_Format: O9,
  RGBA_ASTC_10x6_Format: N9,
  RGBA_ASTC_10x8_Format: B9,
  RGBA_ASTC_12x10_Format: U9,
  RGBA_ASTC_12x12_Format: z9,
  RGBA_ASTC_4x4_Format: T9,
  RGBA_ASTC_5x4_Format: C9,
  RGBA_ASTC_5x5_Format: _9,
  RGBA_ASTC_6x5_Format: P9,
  RGBA_ASTC_6x6_Format: R9,
  RGBA_ASTC_8x5_Format: D9,
  RGBA_ASTC_8x6_Format: L9,
  RGBA_ASTC_8x8_Format: I9,
  RGBA_BPTC_Format: S1,
  RGBA_ETC2_EAC_Format: E9,
  RGBA_PVRTC_2BPPV1_Format: M9,
  RGBA_PVRTC_4BPPV1_Format: b9,
  RGBA_S3TC_DXT1_Format: A1,
  RGBA_S3TC_DXT3_Format: b1,
  RGBA_S3TC_DXT5_Format: M1,
  RGBFormat: wM,
  RGBIntegerFormat: wz,
  RGB_BPTC_SIGNED_Format: k9,
  RGB_BPTC_UNSIGNED_Format: G9,
  RGB_ETC1_Format: S9,
  RGB_ETC2_Format: w9,
  RGB_PVRTC_2BPPV1_Format: A9,
  RGB_PVRTC_4BPPV1_Format: x9,
  RGB_S3TC_DXT1_Format: x1,
  RGFormat: CM,
  RGIntegerFormat: v4,
  RawShaderMaterial: KM,
  Ray: g0,
  Raycaster: lr,
  Rec709Primaries: Z1,
  RectAreaLight: y2,
  RedFormat: Vu,
  RedIntegerFormat: t2,
  ReinhardToneMapping: gM,
  RenderTarget: VL,
  RepeatWrapping: un,
  ReplaceStencilOp: Tz,
  ReverseSubtractEquation: iL,
  RingGeometry: u2,
  SIGNED_RED_GREEN_RGTC2_Format: W9,
  SIGNED_RED_RGTC1_Format: H9,
  SRGBColorSpace: qi,
  SRGBTransfer: pn,
  Scene: Da,
  ShaderChunk: ai,
  ShaderLib: xa,
  ShaderMaterial: Jt,
  ShadowMaterial: dI,
  Shape: ll,
  ShapeGeometry: d2,
  ShapePath: $j,
  ShapeUtils: ta,
  ShortType: bM,
  Skeleton: nh,
  SkeletonHelper: Wj,
  SkinnedMesh: a2,
  Source: wa,
  Sphere: Tn,
  SphereGeometry: Hs,
  Spherical: Ds,
  SphericalHarmonics3: MI,
  SplineCurve: YM,
  SpotLight: oh,
  SpotLightHelper: Vj,
  Sprite: wn,
  SpriteMaterial: ar,
  SrcAlphaFactor: v9,
  SrcAlphaSaturateFactor: hL,
  SrcColorFactor: aL,
  StaticCopyUsage: Vz,
  StaticDrawUsage: J1,
  StaticReadUsage: kz,
  StereoCamera: Aj,
  StreamCopyUsage: Xz,
  StreamDrawUsage: zz,
  StreamReadUsage: Hz,
  StringKeyframeTrack: Zu,
  SubtractEquation: tL,
  SubtractiveBlending: m9,
  TOUCH: Xh,
  TangentSpaceNormalMap: eh,
  TetrahedronGeometry: f2,
  Texture: Ut,
  TextureLoader: rh,
  TextureUtils: uX,
  TorusGeometry: nr,
  TorusKnotGeometry: p2,
  Triangle: Wo,
  TriangleFanDrawMode: ip,
  TriangleStripDrawMode: i2,
  TrianglesDrawMode: CL,
  TubeGeometry: kc,
  UVMapping: Ho,
  Uint16BufferAttribute: FM,
  Uint32BufferAttribute: M4,
  Uint8BufferAttribute: Tk,
  Uint8ClampedBufferAttribute: Ck,
  Uniform: ya,
  UniformsGroup: Uj,
  UniformsLib: bt,
  UniformsUtils: rs,
  UnsignedByteType: o0,
  UnsignedInt248Type: vl,
  UnsignedInt5999Type: MM,
  UnsignedIntType: gl,
  UnsignedShort4444Type: m4,
  UnsignedShort5551Type: g4,
  UnsignedShortType: tp,
  VSMShadowMap: Do,
  Vector2: Se,
  Vector3: _,
  Vector4: Yt,
  VectorKeyframeTrack: po,
  VideoTexture: q9,
  WebGL3DRenderTarget: mk,
  WebGLArrayRenderTarget: pk,
  WebGLCoordinateSystem: Vo,
  WebGLCubeRenderTarget: zM,
  WebGLMultipleRenderTargets: eq,
  WebGLRenderTarget: Xi,
  WebGLRenderer: HM,
  WebGLUtils: iI,
  WebGPUCoordinateSystem: Q1,
  WireframeGeometry: ZM,
  WrapAroundEnding: q1,
  ZeroCurvatureEnding: hu,
  ZeroFactor: ep,
  ZeroSlopeEnding: uu,
  ZeroStencilOp: Ez,
  createCanvasElement: kL
}, Symbol.toStringTag, { value: "Module" }));
var Ci = Object.freeze({
  Linear: Object.freeze({
    None: function(s) {
      return s;
    },
    In: function(s) {
      return this.None(s);
    },
    Out: function(s) {
      return this.None(s);
    },
    InOut: function(s) {
      return this.None(s);
    }
  }),
  Quadratic: Object.freeze({
    In: function(s) {
      return s * s;
    },
    Out: function(s) {
      return s * (2 - s);
    },
    InOut: function(s) {
      return (s *= 2) < 1 ? 0.5 * s * s : -0.5 * (--s * (s - 2) - 1);
    }
  }),
  Cubic: Object.freeze({
    In: function(s) {
      return s * s * s;
    },
    Out: function(s) {
      return --s * s * s + 1;
    },
    InOut: function(s) {
      return (s *= 2) < 1 ? 0.5 * s * s * s : 0.5 * ((s -= 2) * s * s + 2);
    }
  }),
  Quartic: Object.freeze({
    In: function(s) {
      return s * s * s * s;
    },
    Out: function(s) {
      return 1 - --s * s * s * s;
    },
    InOut: function(s) {
      return (s *= 2) < 1 ? 0.5 * s * s * s * s : -0.5 * ((s -= 2) * s * s * s - 2);
    }
  }),
  Quintic: Object.freeze({
    In: function(s) {
      return s * s * s * s * s;
    },
    Out: function(s) {
      return --s * s * s * s * s + 1;
    },
    InOut: function(s) {
      return (s *= 2) < 1 ? 0.5 * s * s * s * s * s : 0.5 * ((s -= 2) * s * s * s * s + 2);
    }
  }),
  Sinusoidal: Object.freeze({
    In: function(s) {
      return 1 - Math.sin((1 - s) * Math.PI / 2);
    },
    Out: function(s) {
      return Math.sin(s * Math.PI / 2);
    },
    InOut: function(s) {
      return 0.5 * (1 - Math.sin(Math.PI * (0.5 - s)));
    }
  }),
  Exponential: Object.freeze({
    In: function(s) {
      return s === 0 ? 0 : Math.pow(1024, s - 1);
    },
    Out: function(s) {
      return s === 1 ? 1 : 1 - Math.pow(2, -10 * s);
    },
    InOut: function(s) {
      return s === 0 ? 0 : s === 1 ? 1 : (s *= 2) < 1 ? 0.5 * Math.pow(1024, s - 1) : 0.5 * (-Math.pow(2, -10 * (s - 1)) + 2);
    }
  }),
  Circular: Object.freeze({
    In: function(s) {
      return 1 - Math.sqrt(1 - s * s);
    },
    Out: function(s) {
      return Math.sqrt(1 - --s * s);
    },
    InOut: function(s) {
      return (s *= 2) < 1 ? -0.5 * (Math.sqrt(1 - s * s) - 1) : 0.5 * (Math.sqrt(1 - (s -= 2) * s) + 1);
    }
  }),
  Elastic: Object.freeze({
    In: function(s) {
      return s === 0 ? 0 : s === 1 ? 1 : -Math.pow(2, 10 * (s - 1)) * Math.sin((s - 1.1) * 5 * Math.PI);
    },
    Out: function(s) {
      return s === 0 ? 0 : s === 1 ? 1 : Math.pow(2, -10 * s) * Math.sin((s - 0.1) * 5 * Math.PI) + 1;
    },
    InOut: function(s) {
      return s === 0 ? 0 : s === 1 ? 1 : (s *= 2, s < 1 ? -0.5 * Math.pow(2, 10 * (s - 1)) * Math.sin((s - 1.1) * 5 * Math.PI) : 0.5 * Math.pow(2, -10 * (s - 1)) * Math.sin((s - 1.1) * 5 * Math.PI) + 1);
    }
  }),
  Back: Object.freeze({
    In: function(s) {
      var e = 1.70158;
      return s === 1 ? 1 : s * s * ((e + 1) * s - e);
    },
    Out: function(s) {
      var e = 1.70158;
      return s === 0 ? 0 : --s * s * ((e + 1) * s + e) + 1;
    },
    InOut: function(s) {
      var e = 2.5949095;
      return (s *= 2) < 1 ? 0.5 * (s * s * ((e + 1) * s - e)) : 0.5 * ((s -= 2) * s * ((e + 1) * s + e) + 2);
    }
  }),
  Bounce: Object.freeze({
    In: function(s) {
      return 1 - Ci.Bounce.Out(1 - s);
    },
    Out: function(s) {
      return s < 1 / 2.75 ? 7.5625 * s * s : s < 2 / 2.75 ? 7.5625 * (s -= 1.5 / 2.75) * s + 0.75 : s < 2.5 / 2.75 ? 7.5625 * (s -= 2.25 / 2.75) * s + 0.9375 : 7.5625 * (s -= 2.625 / 2.75) * s + 0.984375;
    },
    InOut: function(s) {
      return s < 0.5 ? Ci.Bounce.In(s * 2) * 0.5 : Ci.Bounce.Out(s * 2 - 1) * 0.5 + 0.5;
    }
  }),
  generatePow: function(s) {
    return s === void 0 && (s = 4), s = s < Number.EPSILON ? Number.EPSILON : s, s = s > 1e4 ? 1e4 : s, {
      In: function(e) {
        return Math.pow(e, s);
      },
      Out: function(e) {
        return 1 - Math.pow(1 - e, s);
      },
      InOut: function(e) {
        return e < 0.5 ? Math.pow(e * 2, s) / 2 : (1 - Math.pow(2 - e * 2, s)) / 2 + 0.5;
      }
    };
  }
}), a1 = function() {
  return performance.now();
}, tq = (
  /** @class */
  function() {
    function s() {
      this._tweens = {}, this._tweensAddedDuringUpdate = {};
    }
    return s.prototype.getAll = function() {
      var e = this;
      return Object.keys(this._tweens).map(function(t) {
        return e._tweens[t];
      });
    }, s.prototype.removeAll = function() {
      this._tweens = {};
    }, s.prototype.add = function(e) {
      this._tweens[e.getId()] = e, this._tweensAddedDuringUpdate[e.getId()] = e;
    }, s.prototype.remove = function(e) {
      delete this._tweens[e.getId()], delete this._tweensAddedDuringUpdate[e.getId()];
    }, s.prototype.update = function(e, t) {
      e === void 0 && (e = a1()), t === void 0 && (t = !1);
      var i = Object.keys(this._tweens);
      if (i.length === 0)
        return !1;
      for (; i.length > 0; ) {
        this._tweensAddedDuringUpdate = {};
        for (var n = 0; n < i.length; n++) {
          var r = this._tweens[i[n]], a = !t;
          r && r.update(e, a) === !1 && !t && delete this._tweens[i[n]];
        }
        i = Object.keys(this._tweensAddedDuringUpdate);
      }
      return !0;
    }, s;
  }()
), bf = {
  Linear: function(s, e) {
    var t = s.length - 1, i = t * e, n = Math.floor(i), r = bf.Utils.Linear;
    return e < 0 ? r(s[0], s[1], i) : e > 1 ? r(s[t], s[t - 1], t - i) : r(s[n], s[n + 1 > t ? t : n + 1], i - n);
  },
  Bezier: function(s, e) {
    for (var t = 0, i = s.length - 1, n = Math.pow, r = bf.Utils.Bernstein, a = 0; a <= i; a++)
      t += n(1 - e, i - a) * n(e, a) * s[a] * r(i, a);
    return t;
  },
  CatmullRom: function(s, e) {
    var t = s.length - 1, i = t * e, n = Math.floor(i), r = bf.Utils.CatmullRom;
    return s[0] === s[t] ? (e < 0 && (n = Math.floor(i = t * (1 + e))), r(s[(n - 1 + t) % t], s[n], s[(n + 1) % t], s[(n + 2) % t], i - n)) : e < 0 ? s[0] - (r(s[0], s[0], s[1], s[1], -i) - s[0]) : e > 1 ? s[t] - (r(s[t], s[t], s[t - 1], s[t - 1], i - t) - s[t]) : r(s[n ? n - 1 : 0], s[n], s[t < n + 1 ? t : n + 1], s[t < n + 2 ? t : n + 2], i - n);
  },
  Utils: {
    Linear: function(s, e, t) {
      return (e - s) * t + s;
    },
    Bernstein: function(s, e) {
      var t = bf.Utils.Factorial;
      return t(s) / t(e) / t(s - e);
    },
    Factorial: function() {
      var s = [1];
      return function(e) {
        var t = 1;
        if (s[e])
          return s[e];
        for (var i = e; i > 1; i--)
          t *= i;
        return s[e] = t, t;
      };
    }(),
    CatmullRom: function(s, e, t, i, n) {
      var r = (t - s) * 0.5, a = (i - e) * 0.5, o = n * n, l = n * o;
      return (2 * e - 2 * t + r + a) * l + (-3 * e + 3 * t - 2 * r - a) * o + r * n + e;
    }
  }
}, iq = (
  /** @class */
  function() {
    function s() {
    }
    return s.nextId = function() {
      return s._nextId++;
    }, s._nextId = 0, s;
  }()
), tb = new tq(), nq = (
  /** @class */
  function() {
    function s(e, t) {
      t === void 0 && (t = tb), this._object = e, this._group = t, this._isPaused = !1, this._pauseStart = 0, this._valuesStart = {}, this._valuesEnd = {}, this._valuesStartRepeat = {}, this._duration = 1e3, this._isDynamic = !1, this._initialRepeat = 0, this._repeat = 0, this._yoyo = !1, this._isPlaying = !1, this._reversed = !1, this._delayTime = 0, this._startTime = 0, this._easingFunction = Ci.Linear.None, this._interpolationFunction = bf.Linear, this._chainedTweens = [], this._onStartCallbackFired = !1, this._onEveryStartCallbackFired = !1, this._id = iq.nextId(), this._isChainStopped = !1, this._propertiesAreSetUp = !1, this._goToEnd = !1;
    }
    return s.prototype.getId = function() {
      return this._id;
    }, s.prototype.isPlaying = function() {
      return this._isPlaying;
    }, s.prototype.isPaused = function() {
      return this._isPaused;
    }, s.prototype.to = function(e, t) {
      if (t === void 0 && (t = 1e3), this._isPlaying)
        throw new Error("Can not call Tween.to() while Tween is already started or paused. Stop the Tween first.");
      return this._valuesEnd = e, this._propertiesAreSetUp = !1, this._duration = t, this;
    }, s.prototype.duration = function(e) {
      return e === void 0 && (e = 1e3), this._duration = e, this;
    }, s.prototype.dynamic = function(e) {
      return e === void 0 && (e = !1), this._isDynamic = e, this;
    }, s.prototype.start = function(e, t) {
      if (e === void 0 && (e = a1()), t === void 0 && (t = !1), this._isPlaying)
        return this;
      if (this._group && this._group.add(this), this._repeat = this._initialRepeat, this._reversed) {
        this._reversed = !1;
        for (var i in this._valuesStartRepeat)
          this._swapEndStartRepeatValues(i), this._valuesStart[i] = this._valuesStartRepeat[i];
      }
      if (this._isPlaying = !0, this._isPaused = !1, this._onStartCallbackFired = !1, this._onEveryStartCallbackFired = !1, this._isChainStopped = !1, this._startTime = e, this._startTime += this._delayTime, !this._propertiesAreSetUp || t) {
        if (this._propertiesAreSetUp = !0, !this._isDynamic) {
          var n = {};
          for (var r in this._valuesEnd)
            n[r] = this._valuesEnd[r];
          this._valuesEnd = n;
        }
        this._setupProperties(this._object, this._valuesStart, this._valuesEnd, this._valuesStartRepeat, t);
      }
      return this;
    }, s.prototype.startFromCurrentValues = function(e) {
      return this.start(e, !0);
    }, s.prototype._setupProperties = function(e, t, i, n, r) {
      for (var a in i) {
        var o = e[a], l = Array.isArray(o), c = l ? "array" : typeof o, h = !l && Array.isArray(i[a]);
        if (!(c === "undefined" || c === "function")) {
          if (h) {
            var u = i[a];
            if (u.length === 0)
              continue;
            for (var d = [o], f = 0, p = u.length; f < p; f += 1) {
              var v = this._handleRelativeValue(o, u[f]);
              if (isNaN(v)) {
                h = !1, console.warn("Found invalid interpolation list. Skipping.");
                break;
              }
              d.push(v);
            }
            h && (i[a] = d);
          }
          if ((c === "object" || l) && o && !h) {
            t[a] = l ? [] : {};
            var g = o;
            for (var m in g)
              t[a][m] = g[m];
            n[a] = l ? [] : {};
            var u = i[a];
            if (!this._isDynamic) {
              var x = {};
              for (var m in u)
                x[m] = u[m];
              i[a] = u = x;
            }
            this._setupProperties(g, t[a], u, n[a], r);
          } else
            (typeof t[a] > "u" || r) && (t[a] = o), l || (t[a] *= 1), h ? n[a] = i[a].slice().reverse() : n[a] = t[a] || 0;
        }
      }
    }, s.prototype.stop = function() {
      return this._isChainStopped || (this._isChainStopped = !0, this.stopChainedTweens()), this._isPlaying ? (this._group && this._group.remove(this), this._isPlaying = !1, this._isPaused = !1, this._onStopCallback && this._onStopCallback(this._object), this) : this;
    }, s.prototype.end = function() {
      return this._goToEnd = !0, this.update(1 / 0), this;
    }, s.prototype.pause = function(e) {
      return e === void 0 && (e = a1()), this._isPaused || !this._isPlaying ? this : (this._isPaused = !0, this._pauseStart = e, this._group && this._group.remove(this), this);
    }, s.prototype.resume = function(e) {
      return e === void 0 && (e = a1()), !this._isPaused || !this._isPlaying ? this : (this._isPaused = !1, this._startTime += e - this._pauseStart, this._pauseStart = 0, this._group && this._group.add(this), this);
    }, s.prototype.stopChainedTweens = function() {
      for (var e = 0, t = this._chainedTweens.length; e < t; e++)
        this._chainedTweens[e].stop();
      return this;
    }, s.prototype.group = function(e) {
      return e === void 0 && (e = tb), this._group = e, this;
    }, s.prototype.delay = function(e) {
      return e === void 0 && (e = 0), this._delayTime = e, this;
    }, s.prototype.repeat = function(e) {
      return e === void 0 && (e = 0), this._initialRepeat = e, this._repeat = e, this;
    }, s.prototype.repeatDelay = function(e) {
      return this._repeatDelayTime = e, this;
    }, s.prototype.yoyo = function(e) {
      return e === void 0 && (e = !1), this._yoyo = e, this;
    }, s.prototype.easing = function(e) {
      return e === void 0 && (e = Ci.Linear.None), this._easingFunction = e, this;
    }, s.prototype.interpolation = function(e) {
      return e === void 0 && (e = bf.Linear), this._interpolationFunction = e, this;
    }, s.prototype.chain = function() {
      for (var e = [], t = 0; t < arguments.length; t++)
        e[t] = arguments[t];
      return this._chainedTweens = e, this;
    }, s.prototype.onStart = function(e) {
      return this._onStartCallback = e, this;
    }, s.prototype.onEveryStart = function(e) {
      return this._onEveryStartCallback = e, this;
    }, s.prototype.onUpdate = function(e) {
      return this._onUpdateCallback = e, this;
    }, s.prototype.onRepeat = function(e) {
      return this._onRepeatCallback = e, this;
    }, s.prototype.onComplete = function(e) {
      return this._onCompleteCallback = e, this;
    }, s.prototype.onStop = function(e) {
      return this._onStopCallback = e, this;
    }, s.prototype.update = function(e, t) {
      if (e === void 0 && (e = a1()), t === void 0 && (t = !0), this._isPaused)
        return !0;
      var i, n, r = this._startTime + this._duration;
      if (!this._goToEnd && !this._isPlaying) {
        if (e > r)
          return !1;
        t && this.start(e, !0);
      }
      if (this._goToEnd = !1, e < this._startTime)
        return !0;
      this._onStartCallbackFired === !1 && (this._onStartCallback && this._onStartCallback(this._object), this._onStartCallbackFired = !0), this._onEveryStartCallbackFired === !1 && (this._onEveryStartCallback && this._onEveryStartCallback(this._object), this._onEveryStartCallbackFired = !0), n = (e - this._startTime) / this._duration, n = this._duration === 0 || n > 1 ? 1 : n;
      var a = this._easingFunction(n);
      if (this._updateProperties(this._object, this._valuesStart, this._valuesEnd, a), this._onUpdateCallback && this._onUpdateCallback(this._object, n), n === 1)
        if (this._repeat > 0) {
          isFinite(this._repeat) && this._repeat--;
          for (i in this._valuesStartRepeat)
            !this._yoyo && typeof this._valuesEnd[i] == "string" && (this._valuesStartRepeat[i] = // eslint-disable-next-line
            // @ts-ignore FIXME?
            this._valuesStartRepeat[i] + parseFloat(this._valuesEnd[i])), this._yoyo && this._swapEndStartRepeatValues(i), this._valuesStart[i] = this._valuesStartRepeat[i];
          return this._yoyo && (this._reversed = !this._reversed), this._repeatDelayTime !== void 0 ? this._startTime = e + this._repeatDelayTime : this._startTime = e + this._delayTime, this._onRepeatCallback && this._onRepeatCallback(this._object), this._onEveryStartCallbackFired = !1, !0;
        } else {
          this._onCompleteCallback && this._onCompleteCallback(this._object);
          for (var o = 0, l = this._chainedTweens.length; o < l; o++)
            this._chainedTweens[o].start(this._startTime + this._duration, !1);
          return this._isPlaying = !1, !1;
        }
      return !0;
    }, s.prototype._updateProperties = function(e, t, i, n) {
      for (var r in i)
        if (t[r] !== void 0) {
          var a = t[r] || 0, o = i[r], l = Array.isArray(e[r]), c = Array.isArray(o), h = !l && c;
          h ? e[r] = this._interpolationFunction(o, n) : typeof o == "object" && o ? this._updateProperties(e[r], a, o, n) : (o = this._handleRelativeValue(a, o), typeof o == "number" && (e[r] = a + (o - a) * n));
        }
    }, s.prototype._handleRelativeValue = function(e, t) {
      return typeof t != "string" ? t : t.charAt(0) === "+" || t.charAt(0) === "-" ? e + parseFloat(t) : parseFloat(t);
    }, s.prototype._swapEndStartRepeatValues = function(e) {
      var t = this._valuesStartRepeat[e], i = this._valuesEnd[e];
      typeof i == "string" ? this._valuesStartRepeat[e] = this._valuesStartRepeat[e] + parseFloat(i) : this._valuesStartRepeat[e] = this._valuesEnd[e], this._valuesEnd[e] = t;
    }, s;
  }()
), h0 = tb;
h0.getAll.bind(h0);
h0.removeAll.bind(h0);
h0.add.bind(h0);
var Yi = h0.remove.bind(h0), sq = h0.update.bind(h0);
//! moment.js
//! version : 2.30.1
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com
var PI;
function It() {
  return PI.apply(null, arguments);
}
function rq(s) {
  PI = s;
}
function mo(s) {
  return s instanceof Array || Object.prototype.toString.call(s) === "[object Array]";
}
function Su(s) {
  return s != null && Object.prototype.toString.call(s) === "[object Object]";
}
function Zi(s, e) {
  return Object.prototype.hasOwnProperty.call(s, e);
}
function oS(s) {
  if (Object.getOwnPropertyNames)
    return Object.getOwnPropertyNames(s).length === 0;
  var e;
  for (e in s)
    if (Zi(s, e))
      return !1;
  return !0;
}
function Rr(s) {
  return s === void 0;
}
function Al(s) {
  return typeof s == "number" || Object.prototype.toString.call(s) === "[object Number]";
}
function M2(s) {
  return s instanceof Date || Object.prototype.toString.call(s) === "[object Date]";
}
function RI(s, e) {
  var t = [], i, n = s.length;
  for (i = 0; i < n; ++i)
    t.push(e(s[i], i));
  return t;
}
function wc(s, e) {
  for (var t in e)
    Zi(e, t) && (s[t] = e[t]);
  return Zi(e, "toString") && (s.toString = e.toString), Zi(e, "valueOf") && (s.valueOf = e.valueOf), s;
}
function v0(s, e, t, i) {
  return $I(s, e, t, i, !0).utc();
}
function aq() {
  return {
    empty: !1,
    unusedTokens: [],
    unusedInput: [],
    overflow: -2,
    charsLeftOver: 0,
    nullInput: !1,
    invalidEra: null,
    invalidMonth: null,
    invalidFormat: !1,
    userInvalidated: !1,
    iso: !1,
    parsedDateParts: [],
    era: null,
    meridiem: null,
    rfc2822: !1,
    weekdayMismatch: !1
  };
}
function gi(s) {
  return s._pf == null && (s._pf = aq()), s._pf;
}
var ib;
Array.prototype.some ? ib = Array.prototype.some : ib = function(s) {
  var e = Object(this), t = e.length >>> 0, i;
  for (i = 0; i < t; i++)
    if (i in e && s.call(this, e[i], i, e))
      return !0;
  return !1;
};
function lS(s) {
  var e = null, t = !1, i = s._d && !isNaN(s._d.getTime());
  if (i && (e = gi(s), t = ib.call(e.parsedDateParts, function(n) {
    return n != null;
  }), i = e.overflow < 0 && !e.empty && !e.invalidEra && !e.invalidMonth && !e.invalidWeekday && !e.weekdayMismatch && !e.nullInput && !e.invalidFormat && !e.userInvalidated && (!e.meridiem || e.meridiem && t), s._strict && (i = i && e.charsLeftOver === 0 && e.unusedTokens.length === 0 && e.bigHour === void 0)), Object.isFrozen == null || !Object.isFrozen(s))
    s._isValid = i;
  else
    return i;
  return s._isValid;
}
function I4(s) {
  var e = v0(NaN);
  return s != null ? wc(gi(e), s) : gi(e).userInvalidated = !0, e;
}
var MT = It.momentProperties = [], o5 = !1;
function cS(s, e) {
  var t, i, n, r = MT.length;
  if (Rr(e._isAMomentObject) || (s._isAMomentObject = e._isAMomentObject), Rr(e._i) || (s._i = e._i), Rr(e._f) || (s._f = e._f), Rr(e._l) || (s._l = e._l), Rr(e._strict) || (s._strict = e._strict), Rr(e._tzm) || (s._tzm = e._tzm), Rr(e._isUTC) || (s._isUTC = e._isUTC), Rr(e._offset) || (s._offset = e._offset), Rr(e._pf) || (s._pf = gi(e)), Rr(e._locale) || (s._locale = e._locale), r > 0)
    for (t = 0; t < r; t++)
      i = MT[t], n = e[i], Rr(n) || (s[i] = n);
  return s;
}
function S2(s) {
  cS(this, s), this._d = new Date(s._d != null ? s._d.getTime() : NaN), this.isValid() || (this._d = /* @__PURE__ */ new Date(NaN)), o5 === !1 && (o5 = !0, It.updateOffset(this), o5 = !1);
}
function go(s) {
  return s instanceof S2 || s != null && s._isAMomentObject != null;
}
function DI(s) {
  It.suppressDeprecationWarnings === !1 && typeof console < "u" && console.warn && console.warn("Deprecation warning: " + s);
}
function La(s, e) {
  var t = !0;
  return wc(function() {
    if (It.deprecationHandler != null && It.deprecationHandler(null, s), t) {
      var i = [], n, r, a, o = arguments.length;
      for (r = 0; r < o; r++) {
        if (n = "", typeof arguments[r] == "object") {
          n += `
[` + r + "] ";
          for (a in arguments[0])
            Zi(arguments[0], a) && (n += a + ": " + arguments[0][a] + ", ");
          n = n.slice(0, -2);
        } else
          n = arguments[r];
        i.push(n);
      }
      DI(
        s + `
Arguments: ` + Array.prototype.slice.call(i).join("") + `
` + new Error().stack
      ), t = !1;
    }
    return e.apply(this, arguments);
  }, e);
}
var ST = {};
function LI(s, e) {
  It.deprecationHandler != null && It.deprecationHandler(s, e), ST[s] || (DI(e), ST[s] = !0);
}
It.suppressDeprecationWarnings = !1;
It.deprecationHandler = null;
function y0(s) {
  return typeof Function < "u" && s instanceof Function || Object.prototype.toString.call(s) === "[object Function]";
}
function oq(s) {
  var e, t;
  for (t in s)
    Zi(s, t) && (e = s[t], y0(e) ? this[t] = e : this["_" + t] = e);
  this._config = s, this._dayOfMonthOrdinalParseLenient = new RegExp(
    (this._dayOfMonthOrdinalParse.source || this._ordinalParse.source) + "|" + /\d{1,2}/.source
  );
}
function nb(s, e) {
  var t = wc({}, s), i;
  for (i in e)
    Zi(e, i) && (Su(s[i]) && Su(e[i]) ? (t[i] = {}, wc(t[i], s[i]), wc(t[i], e[i])) : e[i] != null ? t[i] = e[i] : delete t[i]);
  for (i in s)
    Zi(s, i) && !Zi(e, i) && Su(s[i]) && (t[i] = wc({}, t[i]));
  return t;
}
function hS(s) {
  s != null && this.set(s);
}
var sb;
Object.keys ? sb = Object.keys : sb = function(s) {
  var e, t = [];
  for (e in s)
    Zi(s, e) && t.push(e);
  return t;
};
var lq = {
  sameDay: "[Today at] LT",
  nextDay: "[Tomorrow at] LT",
  nextWeek: "dddd [at] LT",
  lastDay: "[Yesterday at] LT",
  lastWeek: "[Last] dddd [at] LT",
  sameElse: "L"
};
function cq(s, e, t) {
  var i = this._calendar[s] || this._calendar.sameElse;
  return y0(i) ? i.call(e, t) : i;
}
function u0(s, e, t) {
  var i = "" + Math.abs(s), n = e - i.length, r = s >= 0;
  return (r ? t ? "+" : "" : "-") + Math.pow(10, Math.max(0, n)).toString().substr(1) + i;
}
var uS = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|N{1,5}|YYYYYY|YYYYY|YYYY|YY|y{2,4}|yo?|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g, kv = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g, l5 = {}, Bf = {};
function Vt(s, e, t, i) {
  var n = i;
  typeof i == "string" && (n = function() {
    return this[i]();
  }), s && (Bf[s] = n), e && (Bf[e[0]] = function() {
    return u0(n.apply(this, arguments), e[1], e[2]);
  }), t && (Bf[t] = function() {
    return this.localeData().ordinal(
      n.apply(this, arguments),
      s
    );
  });
}
function hq(s) {
  return s.match(/\[[\s\S]/) ? s.replace(/^\[|\]$/g, "") : s.replace(/\\/g, "");
}
function uq(s) {
  var e = s.match(uS), t, i;
  for (t = 0, i = e.length; t < i; t++)
    Bf[e[t]] ? e[t] = Bf[e[t]] : e[t] = hq(e[t]);
  return function(n) {
    var r = "", a;
    for (a = 0; a < i; a++)
      r += y0(e[a]) ? e[a].call(n, s) : e[a];
    return r;
  };
}
function Cy(s, e) {
  return s.isValid() ? (e = II(e, s.localeData()), l5[e] = l5[e] || uq(e), l5[e](s)) : s.localeData().invalidDate();
}
function II(s, e) {
  var t = 5;
  function i(n) {
    return e.longDateFormat(n) || n;
  }
  for (kv.lastIndex = 0; t >= 0 && kv.test(s); )
    s = s.replace(
      kv,
      i
    ), kv.lastIndex = 0, t -= 1;
  return s;
}
var dq = {
  LTS: "h:mm:ss A",
  LT: "h:mm A",
  L: "MM/DD/YYYY",
  LL: "MMMM D, YYYY",
  LLL: "MMMM D, YYYY h:mm A",
  LLLL: "dddd, MMMM D, YYYY h:mm A"
};
function fq(s) {
  var e = this._longDateFormat[s], t = this._longDateFormat[s.toUpperCase()];
  return e || !t ? e : (this._longDateFormat[s] = t.match(uS).map(function(i) {
    return i === "MMMM" || i === "MM" || i === "DD" || i === "dddd" ? i.slice(1) : i;
  }).join(""), this._longDateFormat[s]);
}
var pq = "Invalid date";
function mq() {
  return this._invalidDate;
}
var gq = "%d", vq = /\d{1,2}/;
function yq(s) {
  return this._ordinal.replace("%d", s);
}
var xq = {
  future: "in %s",
  past: "%s ago",
  s: "a few seconds",
  ss: "%d seconds",
  m: "a minute",
  mm: "%d minutes",
  h: "an hour",
  hh: "%d hours",
  d: "a day",
  dd: "%d days",
  w: "a week",
  ww: "%d weeks",
  M: "a month",
  MM: "%d months",
  y: "a year",
  yy: "%d years"
};
function Aq(s, e, t, i) {
  var n = this._relativeTime[t];
  return y0(n) ? n(s, e, t, i) : n.replace(/%d/i, s);
}
function bq(s, e) {
  var t = this._relativeTime[s > 0 ? "future" : "past"];
  return y0(t) ? t(e) : t.replace(/%s/i, e);
}
var wT = {
  D: "date",
  dates: "date",
  date: "date",
  d: "day",
  days: "day",
  day: "day",
  e: "weekday",
  weekdays: "weekday",
  weekday: "weekday",
  E: "isoWeekday",
  isoweekdays: "isoWeekday",
  isoweekday: "isoWeekday",
  DDD: "dayOfYear",
  dayofyears: "dayOfYear",
  dayofyear: "dayOfYear",
  h: "hour",
  hours: "hour",
  hour: "hour",
  ms: "millisecond",
  milliseconds: "millisecond",
  millisecond: "millisecond",
  m: "minute",
  minutes: "minute",
  minute: "minute",
  M: "month",
  months: "month",
  month: "month",
  Q: "quarter",
  quarters: "quarter",
  quarter: "quarter",
  s: "second",
  seconds: "second",
  second: "second",
  gg: "weekYear",
  weekyears: "weekYear",
  weekyear: "weekYear",
  GG: "isoWeekYear",
  isoweekyears: "isoWeekYear",
  isoweekyear: "isoWeekYear",
  w: "week",
  weeks: "week",
  week: "week",
  W: "isoWeek",
  isoweeks: "isoWeek",
  isoweek: "isoWeek",
  y: "year",
  years: "year",
  year: "year"
};
function Ia(s) {
  return typeof s == "string" ? wT[s] || wT[s.toLowerCase()] : void 0;
}
function dS(s) {
  var e = {}, t, i;
  for (i in s)
    Zi(s, i) && (t = Ia(i), t && (e[t] = s[i]));
  return e;
}
var Mq = {
  date: 9,
  day: 11,
  weekday: 11,
  isoWeekday: 11,
  dayOfYear: 4,
  hour: 13,
  millisecond: 16,
  minute: 14,
  month: 8,
  quarter: 7,
  second: 15,
  weekYear: 1,
  isoWeekYear: 1,
  week: 5,
  isoWeek: 5,
  year: 1
};
function Sq(s) {
  var e = [], t;
  for (t in s)
    Zi(s, t) && e.push({ unit: t, priority: Mq[t] });
  return e.sort(function(i, n) {
    return i.priority - n.priority;
  }), e;
}
var OI = /\d/, aa = /\d\d/, NI = /\d{3}/, fS = /\d{4}/, O4 = /[+-]?\d{6}/, Rn = /\d\d?/, BI = /\d\d\d\d?/, FI = /\d\d\d\d\d\d?/, N4 = /\d{1,3}/, pS = /\d{1,4}/, B4 = /[+-]?\d{1,6}/, Yp = /\d+/, F4 = /[+-]?\d+/, wq = /Z|[+-]\d\d:?\d\d/gi, U4 = /Z|[+-]\d\d(?::?\d\d)?/gi, Eq = /[+-]?\d+(\.\d{1,3})?/, w2 = /[0-9]{0,256}['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFF07\uFF10-\uFFEF]{1,256}|[\u0600-\u06FF\/]{1,256}(\s*?[\u0600-\u06FF]{1,256}){1,2}/i, Zp = /^[1-9]\d?/, mS = /^([1-9]\d|\d)/, K9;
K9 = {};
function Nt(s, e, t) {
  K9[s] = y0(e) ? e : function(i, n) {
    return i && t ? t : e;
  };
}
function Tq(s, e) {
  return Zi(K9, s) ? K9[s](e._strict, e._locale) : new RegExp(Cq(s));
}
function Cq(s) {
  return cl(
    s.replace("\\", "").replace(
      /\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g,
      function(e, t, i, n, r) {
        return t || i || n || r;
      }
    )
  );
}
function cl(s) {
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
}
function Aa(s) {
  return s < 0 ? Math.ceil(s) || 0 : Math.floor(s);
}
function Di(s) {
  var e = +s, t = 0;
  return e !== 0 && isFinite(e) && (t = Aa(e)), t;
}
var rb = {};
function fn(s, e) {
  var t, i = e, n;
  for (typeof s == "string" && (s = [s]), Al(e) && (i = function(r, a) {
    a[e] = Di(r);
  }), n = s.length, t = 0; t < n; t++)
    rb[s[t]] = i;
}
function E2(s, e) {
  fn(s, function(t, i, n, r) {
    n._w = n._w || {}, e(t, n._w, n, r);
  });
}
function _q(s, e, t) {
  e != null && Zi(rb, s) && rb[s](e, t._a, t, s);
}
function z4(s) {
  return s % 4 === 0 && s % 100 !== 0 || s % 400 === 0;
}
var rr = 0, il = 1, zo = 2, fs = 3, eo = 4, nl = 5, fu = 6, Pq = 7, Rq = 8;
Vt("Y", 0, 0, function() {
  var s = this.year();
  return s <= 9999 ? u0(s, 4) : "+" + s;
});
Vt(0, ["YY", 2], 0, function() {
  return this.year() % 100;
});
Vt(0, ["YYYY", 4], 0, "year");
Vt(0, ["YYYYY", 5], 0, "year");
Vt(0, ["YYYYYY", 6, !0], 0, "year");
Nt("Y", F4);
Nt("YY", Rn, aa);
Nt("YYYY", pS, fS);
Nt("YYYYY", B4, O4);
Nt("YYYYYY", B4, O4);
fn(["YYYYY", "YYYYYY"], rr);
fn("YYYY", function(s, e) {
  e[rr] = s.length === 2 ? It.parseTwoDigitYear(s) : Di(s);
});
fn("YY", function(s, e) {
  e[rr] = It.parseTwoDigitYear(s);
});
fn("Y", function(s, e) {
  e[rr] = parseInt(s, 10);
});
function C1(s) {
  return z4(s) ? 366 : 365;
}
It.parseTwoDigitYear = function(s) {
  return Di(s) + (Di(s) > 68 ? 1900 : 2e3);
};
var UI = Kp("FullYear", !0);
function Dq() {
  return z4(this.year());
}
function Kp(s, e) {
  return function(t) {
    return t != null ? (zI(this, s, t), It.updateOffset(this, e), this) : rg(this, s);
  };
}
function rg(s, e) {
  if (!s.isValid())
    return NaN;
  var t = s._d, i = s._isUTC;
  switch (e) {
    case "Milliseconds":
      return i ? t.getUTCMilliseconds() : t.getMilliseconds();
    case "Seconds":
      return i ? t.getUTCSeconds() : t.getSeconds();
    case "Minutes":
      return i ? t.getUTCMinutes() : t.getMinutes();
    case "Hours":
      return i ? t.getUTCHours() : t.getHours();
    case "Date":
      return i ? t.getUTCDate() : t.getDate();
    case "Day":
      return i ? t.getUTCDay() : t.getDay();
    case "Month":
      return i ? t.getUTCMonth() : t.getMonth();
    case "FullYear":
      return i ? t.getUTCFullYear() : t.getFullYear();
    default:
      return NaN;
  }
}
function zI(s, e, t) {
  var i, n, r, a, o;
  if (!(!s.isValid() || isNaN(t))) {
    switch (i = s._d, n = s._isUTC, e) {
      case "Milliseconds":
        return void (n ? i.setUTCMilliseconds(t) : i.setMilliseconds(t));
      case "Seconds":
        return void (n ? i.setUTCSeconds(t) : i.setSeconds(t));
      case "Minutes":
        return void (n ? i.setUTCMinutes(t) : i.setMinutes(t));
      case "Hours":
        return void (n ? i.setUTCHours(t) : i.setHours(t));
      case "Date":
        return void (n ? i.setUTCDate(t) : i.setDate(t));
      case "FullYear":
        break;
      default:
        return;
    }
    r = t, a = s.month(), o = s.date(), o = o === 29 && a === 1 && !z4(r) ? 28 : o, n ? i.setUTCFullYear(r, a, o) : i.setFullYear(r, a, o);
  }
}
function Lq(s) {
  return s = Ia(s), y0(this[s]) ? this[s]() : this;
}
function Iq(s, e) {
  if (typeof s == "object") {
    s = dS(s);
    var t = Sq(s), i, n = t.length;
    for (i = 0; i < n; i++)
      this[t[i].unit](s[t[i].unit]);
  } else if (s = Ia(s), y0(this[s]))
    return this[s](e);
  return this;
}
function Oq(s, e) {
  return (s % e + e) % e;
}
var Kn;
Array.prototype.indexOf ? Kn = Array.prototype.indexOf : Kn = function(s) {
  var e;
  for (e = 0; e < this.length; ++e)
    if (this[e] === s)
      return e;
  return -1;
};
function gS(s, e) {
  if (isNaN(s) || isNaN(e))
    return NaN;
  var t = Oq(e, 12);
  return s += (e - t) / 12, t === 1 ? z4(s) ? 29 : 28 : 31 - t % 7 % 2;
}
Vt("M", ["MM", 2], "Mo", function() {
  return this.month() + 1;
});
Vt("MMM", 0, 0, function(s) {
  return this.localeData().monthsShort(this, s);
});
Vt("MMMM", 0, 0, function(s) {
  return this.localeData().months(this, s);
});
Nt("M", Rn, Zp);
Nt("MM", Rn, aa);
Nt("MMM", function(s, e) {
  return e.monthsShortRegex(s);
});
Nt("MMMM", function(s, e) {
  return e.monthsRegex(s);
});
fn(["M", "MM"], function(s, e) {
  e[il] = Di(s) - 1;
});
fn(["MMM", "MMMM"], function(s, e, t, i) {
  var n = t._locale.monthsParse(s, i, t._strict);
  n != null ? e[il] = n : gi(t).invalidMonth = s;
});
var Nq = "January_February_March_April_May_June_July_August_September_October_November_December".split(
  "_"
), kI = "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), GI = /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/, Bq = w2, Fq = w2;
function Uq(s, e) {
  return s ? mo(this._months) ? this._months[s.month()] : this._months[(this._months.isFormat || GI).test(e) ? "format" : "standalone"][s.month()] : mo(this._months) ? this._months : this._months.standalone;
}
function zq(s, e) {
  return s ? mo(this._monthsShort) ? this._monthsShort[s.month()] : this._monthsShort[GI.test(e) ? "format" : "standalone"][s.month()] : mo(this._monthsShort) ? this._monthsShort : this._monthsShort.standalone;
}
function kq(s, e, t) {
  var i, n, r, a = s.toLocaleLowerCase();
  if (!this._monthsParse)
    for (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = [], i = 0; i < 12; ++i)
      r = v0([2e3, i]), this._shortMonthsParse[i] = this.monthsShort(
        r,
        ""
      ).toLocaleLowerCase(), this._longMonthsParse[i] = this.months(r, "").toLocaleLowerCase();
  return t ? e === "MMM" ? (n = Kn.call(this._shortMonthsParse, a), n !== -1 ? n : null) : (n = Kn.call(this._longMonthsParse, a), n !== -1 ? n : null) : e === "MMM" ? (n = Kn.call(this._shortMonthsParse, a), n !== -1 ? n : (n = Kn.call(this._longMonthsParse, a), n !== -1 ? n : null)) : (n = Kn.call(this._longMonthsParse, a), n !== -1 ? n : (n = Kn.call(this._shortMonthsParse, a), n !== -1 ? n : null));
}
function Gq(s, e, t) {
  var i, n, r;
  if (this._monthsParseExact)
    return kq.call(this, s, e, t);
  for (this._monthsParse || (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = []), i = 0; i < 12; i++) {
    if (n = v0([2e3, i]), t && !this._longMonthsParse[i] && (this._longMonthsParse[i] = new RegExp(
      "^" + this.months(n, "").replace(".", "") + "$",
      "i"
    ), this._shortMonthsParse[i] = new RegExp(
      "^" + this.monthsShort(n, "").replace(".", "") + "$",
      "i"
    )), !t && !this._monthsParse[i] && (r = "^" + this.months(n, "") + "|^" + this.monthsShort(n, ""), this._monthsParse[i] = new RegExp(r.replace(".", ""), "i")), t && e === "MMMM" && this._longMonthsParse[i].test(s))
      return i;
    if (t && e === "MMM" && this._shortMonthsParse[i].test(s))
      return i;
    if (!t && this._monthsParse[i].test(s))
      return i;
  }
}
function HI(s, e) {
  if (!s.isValid())
    return s;
  if (typeof e == "string") {
    if (/^\d+$/.test(e))
      e = Di(e);
    else if (e = s.localeData().monthsParse(e), !Al(e))
      return s;
  }
  var t = e, i = s.date();
  return i = i < 29 ? i : Math.min(i, gS(s.year(), t)), s._isUTC ? s._d.setUTCMonth(t, i) : s._d.setMonth(t, i), s;
}
function VI(s) {
  return s != null ? (HI(this, s), It.updateOffset(this, !0), this) : rg(this, "Month");
}
function Hq() {
  return gS(this.year(), this.month());
}
function Vq(s) {
  return this._monthsParseExact ? (Zi(this, "_monthsRegex") || WI.call(this), s ? this._monthsShortStrictRegex : this._monthsShortRegex) : (Zi(this, "_monthsShortRegex") || (this._monthsShortRegex = Bq), this._monthsShortStrictRegex && s ? this._monthsShortStrictRegex : this._monthsShortRegex);
}
function Wq(s) {
  return this._monthsParseExact ? (Zi(this, "_monthsRegex") || WI.call(this), s ? this._monthsStrictRegex : this._monthsRegex) : (Zi(this, "_monthsRegex") || (this._monthsRegex = Fq), this._monthsStrictRegex && s ? this._monthsStrictRegex : this._monthsRegex);
}
function WI() {
  function s(l, c) {
    return c.length - l.length;
  }
  var e = [], t = [], i = [], n, r, a, o;
  for (n = 0; n < 12; n++)
    r = v0([2e3, n]), a = cl(this.monthsShort(r, "")), o = cl(this.months(r, "")), e.push(a), t.push(o), i.push(o), i.push(a);
  e.sort(s), t.sort(s), i.sort(s), this._monthsRegex = new RegExp("^(" + i.join("|") + ")", "i"), this._monthsShortRegex = this._monthsRegex, this._monthsStrictRegex = new RegExp(
    "^(" + t.join("|") + ")",
    "i"
  ), this._monthsShortStrictRegex = new RegExp(
    "^(" + e.join("|") + ")",
    "i"
  );
}
function Xq(s, e, t, i, n, r, a) {
  var o;
  return s < 100 && s >= 0 ? (o = new Date(s + 400, e, t, i, n, r, a), isFinite(o.getFullYear()) && o.setFullYear(s)) : o = new Date(s, e, t, i, n, r, a), o;
}
function ag(s) {
  var e, t;
  return s < 100 && s >= 0 ? (t = Array.prototype.slice.call(arguments), t[0] = s + 400, e = new Date(Date.UTC.apply(null, t)), isFinite(e.getUTCFullYear()) && e.setUTCFullYear(s)) : e = new Date(Date.UTC.apply(null, arguments)), e;
}
function J9(s, e, t) {
  var i = 7 + e - t, n = (7 + ag(s, 0, i).getUTCDay() - e) % 7;
  return -n + i - 1;
}
function XI(s, e, t, i, n) {
  var r = (7 + t - i) % 7, a = J9(s, i, n), o = 1 + 7 * (e - 1) + r + a, l, c;
  return o <= 0 ? (l = s - 1, c = C1(l) + o) : o > C1(s) ? (l = s + 1, c = o - C1(s)) : (l = s, c = o), {
    year: l,
    dayOfYear: c
  };
}
function og(s, e, t) {
  var i = J9(s.year(), e, t), n = Math.floor((s.dayOfYear() - i - 1) / 7) + 1, r, a;
  return n < 1 ? (a = s.year() - 1, r = n + hl(a, e, t)) : n > hl(s.year(), e, t) ? (r = n - hl(s.year(), e, t), a = s.year() + 1) : (a = s.year(), r = n), {
    week: r,
    year: a
  };
}
function hl(s, e, t) {
  var i = J9(s, e, t), n = J9(s + 1, e, t);
  return (C1(s) - i + n) / 7;
}
Vt("w", ["ww", 2], "wo", "week");
Vt("W", ["WW", 2], "Wo", "isoWeek");
Nt("w", Rn, Zp);
Nt("ww", Rn, aa);
Nt("W", Rn, Zp);
Nt("WW", Rn, aa);
E2(
  ["w", "ww", "W", "WW"],
  function(s, e, t, i) {
    e[i.substr(0, 1)] = Di(s);
  }
);
function jq(s) {
  return og(s, this._week.dow, this._week.doy).week;
}
var qq = {
  dow: 0,
  // Sunday is the first day of the week.
  doy: 6
  // The week that contains Jan 6th is the first week of the year.
};
function Yq() {
  return this._week.dow;
}
function Zq() {
  return this._week.doy;
}
function Kq(s) {
  var e = this.localeData().week(this);
  return s == null ? e : this.add((s - e) * 7, "d");
}
function Jq(s) {
  var e = og(this, 1, 4).week;
  return s == null ? e : this.add((s - e) * 7, "d");
}
Vt("d", 0, "do", "day");
Vt("dd", 0, 0, function(s) {
  return this.localeData().weekdaysMin(this, s);
});
Vt("ddd", 0, 0, function(s) {
  return this.localeData().weekdaysShort(this, s);
});
Vt("dddd", 0, 0, function(s) {
  return this.localeData().weekdays(this, s);
});
Vt("e", 0, 0, "weekday");
Vt("E", 0, 0, "isoWeekday");
Nt("d", Rn);
Nt("e", Rn);
Nt("E", Rn);
Nt("dd", function(s, e) {
  return e.weekdaysMinRegex(s);
});
Nt("ddd", function(s, e) {
  return e.weekdaysShortRegex(s);
});
Nt("dddd", function(s, e) {
  return e.weekdaysRegex(s);
});
E2(["dd", "ddd", "dddd"], function(s, e, t, i) {
  var n = t._locale.weekdaysParse(s, i, t._strict);
  n != null ? e.d = n : gi(t).invalidWeekday = s;
});
E2(["d", "e", "E"], function(s, e, t, i) {
  e[i] = Di(s);
});
function Qq(s, e) {
  return typeof s != "string" ? s : isNaN(s) ? (s = e.weekdaysParse(s), typeof s == "number" ? s : null) : parseInt(s, 10);
}
function $q(s, e) {
  return typeof s == "string" ? e.weekdaysParse(s) % 7 || 7 : isNaN(s) ? null : s;
}
function vS(s, e) {
  return s.slice(e, 7).concat(s.slice(0, e));
}
var eY = "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), jI = "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), tY = "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), iY = w2, nY = w2, sY = w2;
function rY(s, e) {
  var t = mo(this._weekdays) ? this._weekdays : this._weekdays[s && s !== !0 && this._weekdays.isFormat.test(e) ? "format" : "standalone"];
  return s === !0 ? vS(t, this._week.dow) : s ? t[s.day()] : t;
}
function aY(s) {
  return s === !0 ? vS(this._weekdaysShort, this._week.dow) : s ? this._weekdaysShort[s.day()] : this._weekdaysShort;
}
function oY(s) {
  return s === !0 ? vS(this._weekdaysMin, this._week.dow) : s ? this._weekdaysMin[s.day()] : this._weekdaysMin;
}
function lY(s, e, t) {
  var i, n, r, a = s.toLocaleLowerCase();
  if (!this._weekdaysParse)
    for (this._weekdaysParse = [], this._shortWeekdaysParse = [], this._minWeekdaysParse = [], i = 0; i < 7; ++i)
      r = v0([2e3, 1]).day(i), this._minWeekdaysParse[i] = this.weekdaysMin(
        r,
        ""
      ).toLocaleLowerCase(), this._shortWeekdaysParse[i] = this.weekdaysShort(
        r,
        ""
      ).toLocaleLowerCase(), this._weekdaysParse[i] = this.weekdays(r, "").toLocaleLowerCase();
  return t ? e === "dddd" ? (n = Kn.call(this._weekdaysParse, a), n !== -1 ? n : null) : e === "ddd" ? (n = Kn.call(this._shortWeekdaysParse, a), n !== -1 ? n : null) : (n = Kn.call(this._minWeekdaysParse, a), n !== -1 ? n : null) : e === "dddd" ? (n = Kn.call(this._weekdaysParse, a), n !== -1 || (n = Kn.call(this._shortWeekdaysParse, a), n !== -1) ? n : (n = Kn.call(this._minWeekdaysParse, a), n !== -1 ? n : null)) : e === "ddd" ? (n = Kn.call(this._shortWeekdaysParse, a), n !== -1 || (n = Kn.call(this._weekdaysParse, a), n !== -1) ? n : (n = Kn.call(this._minWeekdaysParse, a), n !== -1 ? n : null)) : (n = Kn.call(this._minWeekdaysParse, a), n !== -1 || (n = Kn.call(this._weekdaysParse, a), n !== -1) ? n : (n = Kn.call(this._shortWeekdaysParse, a), n !== -1 ? n : null));
}
function cY(s, e, t) {
  var i, n, r;
  if (this._weekdaysParseExact)
    return lY.call(this, s, e, t);
  for (this._weekdaysParse || (this._weekdaysParse = [], this._minWeekdaysParse = [], this._shortWeekdaysParse = [], this._fullWeekdaysParse = []), i = 0; i < 7; i++) {
    if (n = v0([2e3, 1]).day(i), t && !this._fullWeekdaysParse[i] && (this._fullWeekdaysParse[i] = new RegExp(
      "^" + this.weekdays(n, "").replace(".", "\\.?") + "$",
      "i"
    ), this._shortWeekdaysParse[i] = new RegExp(
      "^" + this.weekdaysShort(n, "").replace(".", "\\.?") + "$",
      "i"
    ), this._minWeekdaysParse[i] = new RegExp(
      "^" + this.weekdaysMin(n, "").replace(".", "\\.?") + "$",
      "i"
    )), this._weekdaysParse[i] || (r = "^" + this.weekdays(n, "") + "|^" + this.weekdaysShort(n, "") + "|^" + this.weekdaysMin(n, ""), this._weekdaysParse[i] = new RegExp(r.replace(".", ""), "i")), t && e === "dddd" && this._fullWeekdaysParse[i].test(s))
      return i;
    if (t && e === "ddd" && this._shortWeekdaysParse[i].test(s))
      return i;
    if (t && e === "dd" && this._minWeekdaysParse[i].test(s))
      return i;
    if (!t && this._weekdaysParse[i].test(s))
      return i;
  }
}
function hY(s) {
  if (!this.isValid())
    return s != null ? this : NaN;
  var e = rg(this, "Day");
  return s != null ? (s = Qq(s, this.localeData()), this.add(s - e, "d")) : e;
}
function uY(s) {
  if (!this.isValid())
    return s != null ? this : NaN;
  var e = (this.day() + 7 - this.localeData()._week.dow) % 7;
  return s == null ? e : this.add(s - e, "d");
}
function dY(s) {
  if (!this.isValid())
    return s != null ? this : NaN;
  if (s != null) {
    var e = $q(s, this.localeData());
    return this.day(this.day() % 7 ? e : e - 7);
  } else
    return this.day() || 7;
}
function fY(s) {
  return this._weekdaysParseExact ? (Zi(this, "_weekdaysRegex") || yS.call(this), s ? this._weekdaysStrictRegex : this._weekdaysRegex) : (Zi(this, "_weekdaysRegex") || (this._weekdaysRegex = iY), this._weekdaysStrictRegex && s ? this._weekdaysStrictRegex : this._weekdaysRegex);
}
function pY(s) {
  return this._weekdaysParseExact ? (Zi(this, "_weekdaysRegex") || yS.call(this), s ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex) : (Zi(this, "_weekdaysShortRegex") || (this._weekdaysShortRegex = nY), this._weekdaysShortStrictRegex && s ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex);
}
function mY(s) {
  return this._weekdaysParseExact ? (Zi(this, "_weekdaysRegex") || yS.call(this), s ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex) : (Zi(this, "_weekdaysMinRegex") || (this._weekdaysMinRegex = sY), this._weekdaysMinStrictRegex && s ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex);
}
function yS() {
  function s(h, u) {
    return u.length - h.length;
  }
  var e = [], t = [], i = [], n = [], r, a, o, l, c;
  for (r = 0; r < 7; r++)
    a = v0([2e3, 1]).day(r), o = cl(this.weekdaysMin(a, "")), l = cl(this.weekdaysShort(a, "")), c = cl(this.weekdays(a, "")), e.push(o), t.push(l), i.push(c), n.push(o), n.push(l), n.push(c);
  e.sort(s), t.sort(s), i.sort(s), n.sort(s), this._weekdaysRegex = new RegExp("^(" + n.join("|") + ")", "i"), this._weekdaysShortRegex = this._weekdaysRegex, this._weekdaysMinRegex = this._weekdaysRegex, this._weekdaysStrictRegex = new RegExp(
    "^(" + i.join("|") + ")",
    "i"
  ), this._weekdaysShortStrictRegex = new RegExp(
    "^(" + t.join("|") + ")",
    "i"
  ), this._weekdaysMinStrictRegex = new RegExp(
    "^(" + e.join("|") + ")",
    "i"
  );
}
function xS() {
  return this.hours() % 12 || 12;
}
function gY() {
  return this.hours() || 24;
}
Vt("H", ["HH", 2], 0, "hour");
Vt("h", ["hh", 2], 0, xS);
Vt("k", ["kk", 2], 0, gY);
Vt("hmm", 0, 0, function() {
  return "" + xS.apply(this) + u0(this.minutes(), 2);
});
Vt("hmmss", 0, 0, function() {
  return "" + xS.apply(this) + u0(this.minutes(), 2) + u0(this.seconds(), 2);
});
Vt("Hmm", 0, 0, function() {
  return "" + this.hours() + u0(this.minutes(), 2);
});
Vt("Hmmss", 0, 0, function() {
  return "" + this.hours() + u0(this.minutes(), 2) + u0(this.seconds(), 2);
});
function qI(s, e) {
  Vt(s, 0, 0, function() {
    return this.localeData().meridiem(
      this.hours(),
      this.minutes(),
      e
    );
  });
}
qI("a", !0);
qI("A", !1);
function YI(s, e) {
  return e._meridiemParse;
}
Nt("a", YI);
Nt("A", YI);
Nt("H", Rn, mS);
Nt("h", Rn, Zp);
Nt("k", Rn, Zp);
Nt("HH", Rn, aa);
Nt("hh", Rn, aa);
Nt("kk", Rn, aa);
Nt("hmm", BI);
Nt("hmmss", FI);
Nt("Hmm", BI);
Nt("Hmmss", FI);
fn(["H", "HH"], fs);
fn(["k", "kk"], function(s, e, t) {
  var i = Di(s);
  e[fs] = i === 24 ? 0 : i;
});
fn(["a", "A"], function(s, e, t) {
  t._isPm = t._locale.isPM(s), t._meridiem = s;
});
fn(["h", "hh"], function(s, e, t) {
  e[fs] = Di(s), gi(t).bigHour = !0;
});
fn("hmm", function(s, e, t) {
  var i = s.length - 2;
  e[fs] = Di(s.substr(0, i)), e[eo] = Di(s.substr(i)), gi(t).bigHour = !0;
});
fn("hmmss", function(s, e, t) {
  var i = s.length - 4, n = s.length - 2;
  e[fs] = Di(s.substr(0, i)), e[eo] = Di(s.substr(i, 2)), e[nl] = Di(s.substr(n)), gi(t).bigHour = !0;
});
fn("Hmm", function(s, e, t) {
  var i = s.length - 2;
  e[fs] = Di(s.substr(0, i)), e[eo] = Di(s.substr(i));
});
fn("Hmmss", function(s, e, t) {
  var i = s.length - 4, n = s.length - 2;
  e[fs] = Di(s.substr(0, i)), e[eo] = Di(s.substr(i, 2)), e[nl] = Di(s.substr(n));
});
function vY(s) {
  return (s + "").toLowerCase().charAt(0) === "p";
}
var yY = /[ap]\.?m?\.?/i, xY = Kp("Hours", !0);
function AY(s, e, t) {
  return s > 11 ? t ? "pm" : "PM" : t ? "am" : "AM";
}
var ZI = {
  calendar: lq,
  longDateFormat: dq,
  invalidDate: pq,
  ordinal: gq,
  dayOfMonthOrdinalParse: vq,
  relativeTime: xq,
  months: Nq,
  monthsShort: kI,
  week: qq,
  weekdays: eY,
  weekdaysMin: tY,
  weekdaysShort: jI,
  meridiemParse: yY
}, In = {}, xm = {}, lg;
function bY(s, e) {
  var t, i = Math.min(s.length, e.length);
  for (t = 0; t < i; t += 1)
    if (s[t] !== e[t])
      return t;
  return i;
}
function ET(s) {
  return s && s.toLowerCase().replace("_", "-");
}
function MY(s) {
  for (var e = 0, t, i, n, r; e < s.length; ) {
    for (r = ET(s[e]).split("-"), t = r.length, i = ET(s[e + 1]), i = i ? i.split("-") : null; t > 0; ) {
      if (n = k4(r.slice(0, t).join("-")), n)
        return n;
      if (i && i.length >= t && bY(r, i) >= t - 1)
        break;
      t--;
    }
    e++;
  }
  return lg;
}
function SY(s) {
  return !!(s && s.match("^[^/\\\\]*$"));
}
function k4(s) {
  var e = null, t;
  if (In[s] === void 0 && typeof module < "u" && module && module.exports && SY(s))
    try {
      e = lg._abbr, t = require, t("./locale/" + s), Dc(e);
    } catch {
      In[s] = null;
    }
  return In[s];
}
function Dc(s, e) {
  var t;
  return s && (Rr(e) ? t = Cl(s) : t = AS(s, e), t ? lg = t : typeof console < "u" && console.warn && console.warn(
    "Locale " + s + " not found. Did you forget to load it?"
  )), lg._abbr;
}
function AS(s, e) {
  if (e !== null) {
    var t, i = ZI;
    if (e.abbr = s, In[s] != null)
      LI(
        "defineLocaleOverride",
        "use moment.updateLocale(localeName, config) to change an existing locale. moment.defineLocale(localeName, config) should only be used for creating a new locale See http://momentjs.com/guides/#/warnings/define-locale/ for more info."
      ), i = In[s]._config;
    else if (e.parentLocale != null)
      if (In[e.parentLocale] != null)
        i = In[e.parentLocale]._config;
      else if (t = k4(e.parentLocale), t != null)
        i = t._config;
      else
        return xm[e.parentLocale] || (xm[e.parentLocale] = []), xm[e.parentLocale].push({
          name: s,
          config: e
        }), null;
    return In[s] = new hS(nb(i, e)), xm[s] && xm[s].forEach(function(n) {
      AS(n.name, n.config);
    }), Dc(s), In[s];
  } else
    return delete In[s], null;
}
function wY(s, e) {
  if (e != null) {
    var t, i, n = ZI;
    In[s] != null && In[s].parentLocale != null ? In[s].set(nb(In[s]._config, e)) : (i = k4(s), i != null && (n = i._config), e = nb(n, e), i == null && (e.abbr = s), t = new hS(e), t.parentLocale = In[s], In[s] = t), Dc(s);
  } else
    In[s] != null && (In[s].parentLocale != null ? (In[s] = In[s].parentLocale, s === Dc() && Dc(s)) : In[s] != null && delete In[s]);
  return In[s];
}
function Cl(s) {
  var e;
  if (s && s._locale && s._locale._abbr && (s = s._locale._abbr), !s)
    return lg;
  if (!mo(s)) {
    if (e = k4(s), e)
      return e;
    s = [s];
  }
  return MY(s);
}
function EY() {
  return sb(In);
}
function bS(s) {
  var e, t = s._a;
  return t && gi(s).overflow === -2 && (e = t[il] < 0 || t[il] > 11 ? il : t[zo] < 1 || t[zo] > gS(t[rr], t[il]) ? zo : t[fs] < 0 || t[fs] > 24 || t[fs] === 24 && (t[eo] !== 0 || t[nl] !== 0 || t[fu] !== 0) ? fs : t[eo] < 0 || t[eo] > 59 ? eo : t[nl] < 0 || t[nl] > 59 ? nl : t[fu] < 0 || t[fu] > 999 ? fu : -1, gi(s)._overflowDayOfYear && (e < rr || e > zo) && (e = zo), gi(s)._overflowWeeks && e === -1 && (e = Pq), gi(s)._overflowWeekday && e === -1 && (e = Rq), gi(s).overflow = e), s;
}
var TY = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/, CY = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d|))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/, _Y = /Z|[+-]\d\d(?::?\d\d)?/, Gv = [
  ["YYYYYY-MM-DD", /[+-]\d{6}-\d\d-\d\d/],
  ["YYYY-MM-DD", /\d{4}-\d\d-\d\d/],
  ["GGGG-[W]WW-E", /\d{4}-W\d\d-\d/],
  ["GGGG-[W]WW", /\d{4}-W\d\d/, !1],
  ["YYYY-DDD", /\d{4}-\d{3}/],
  ["YYYY-MM", /\d{4}-\d\d/, !1],
  ["YYYYYYMMDD", /[+-]\d{10}/],
  ["YYYYMMDD", /\d{8}/],
  ["GGGG[W]WWE", /\d{4}W\d{3}/],
  ["GGGG[W]WW", /\d{4}W\d{2}/, !1],
  ["YYYYDDD", /\d{7}/],
  ["YYYYMM", /\d{6}/, !1],
  ["YYYY", /\d{4}/, !1]
], c5 = [
  ["HH:mm:ss.SSSS", /\d\d:\d\d:\d\d\.\d+/],
  ["HH:mm:ss,SSSS", /\d\d:\d\d:\d\d,\d+/],
  ["HH:mm:ss", /\d\d:\d\d:\d\d/],
  ["HH:mm", /\d\d:\d\d/],
  ["HHmmss.SSSS", /\d\d\d\d\d\d\.\d+/],
  ["HHmmss,SSSS", /\d\d\d\d\d\d,\d+/],
  ["HHmmss", /\d\d\d\d\d\d/],
  ["HHmm", /\d\d\d\d/],
  ["HH", /\d\d/]
], PY = /^\/?Date\((-?\d+)/i, RY = /^(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{2,4})\s(\d\d):(\d\d)(?::(\d\d))?\s(?:(UT|GMT|[ECMP][SD]T)|([Zz])|([+-]\d{4}))$/, DY = {
  UT: 0,
  GMT: 0,
  EDT: -4 * 60,
  EST: -5 * 60,
  CDT: -5 * 60,
  CST: -6 * 60,
  MDT: -6 * 60,
  MST: -7 * 60,
  PDT: -7 * 60,
  PST: -8 * 60
};
function KI(s) {
  var e, t, i = s._i, n = TY.exec(i) || CY.exec(i), r, a, o, l, c = Gv.length, h = c5.length;
  if (n) {
    for (gi(s).iso = !0, e = 0, t = c; e < t; e++)
      if (Gv[e][1].exec(n[1])) {
        a = Gv[e][0], r = Gv[e][2] !== !1;
        break;
      }
    if (a == null) {
      s._isValid = !1;
      return;
    }
    if (n[3]) {
      for (e = 0, t = h; e < t; e++)
        if (c5[e][1].exec(n[3])) {
          o = (n[2] || " ") + c5[e][0];
          break;
        }
      if (o == null) {
        s._isValid = !1;
        return;
      }
    }
    if (!r && o != null) {
      s._isValid = !1;
      return;
    }
    if (n[4])
      if (_Y.exec(n[4]))
        l = "Z";
      else {
        s._isValid = !1;
        return;
      }
    s._f = a + (o || "") + (l || ""), SS(s);
  } else
    s._isValid = !1;
}
function LY(s, e, t, i, n, r) {
  var a = [
    IY(s),
    kI.indexOf(e),
    parseInt(t, 10),
    parseInt(i, 10),
    parseInt(n, 10)
  ];
  return r && a.push(parseInt(r, 10)), a;
}
function IY(s) {
  var e = parseInt(s, 10);
  return e <= 49 ? 2e3 + e : e <= 999 ? 1900 + e : e;
}
function OY(s) {
  return s.replace(/\([^()]*\)|[\n\t]/g, " ").replace(/(\s\s+)/g, " ").replace(/^\s\s*/, "").replace(/\s\s*$/, "");
}
function NY(s, e, t) {
  if (s) {
    var i = jI.indexOf(s), n = new Date(
      e[0],
      e[1],
      e[2]
    ).getDay();
    if (i !== n)
      return gi(t).weekdayMismatch = !0, t._isValid = !1, !1;
  }
  return !0;
}
function BY(s, e, t) {
  if (s)
    return DY[s];
  if (e)
    return 0;
  var i = parseInt(t, 10), n = i % 100, r = (i - n) / 100;
  return r * 60 + n;
}
function JI(s) {
  var e = RY.exec(OY(s._i)), t;
  if (e) {
    if (t = LY(
      e[4],
      e[3],
      e[2],
      e[5],
      e[6],
      e[7]
    ), !NY(e[1], t, s))
      return;
    s._a = t, s._tzm = BY(e[8], e[9], e[10]), s._d = ag.apply(null, s._a), s._d.setUTCMinutes(s._d.getUTCMinutes() - s._tzm), gi(s).rfc2822 = !0;
  } else
    s._isValid = !1;
}
function FY(s) {
  var e = PY.exec(s._i);
  if (e !== null) {
    s._d = /* @__PURE__ */ new Date(+e[1]);
    return;
  }
  if (KI(s), s._isValid === !1)
    delete s._isValid;
  else
    return;
  if (JI(s), s._isValid === !1)
    delete s._isValid;
  else
    return;
  s._strict ? s._isValid = !1 : It.createFromInputFallback(s);
}
It.createFromInputFallback = La(
  "value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are discouraged. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.",
  function(s) {
    s._d = /* @__PURE__ */ new Date(s._i + (s._useUTC ? " UTC" : ""));
  }
);
function pf(s, e, t) {
  return s ?? e ?? t;
}
function UY(s) {
  var e = new Date(It.now());
  return s._useUTC ? [
    e.getUTCFullYear(),
    e.getUTCMonth(),
    e.getUTCDate()
  ] : [e.getFullYear(), e.getMonth(), e.getDate()];
}
function MS(s) {
  var e, t, i = [], n, r, a;
  if (!s._d) {
    for (n = UY(s), s._w && s._a[zo] == null && s._a[il] == null && zY(s), s._dayOfYear != null && (a = pf(s._a[rr], n[rr]), (s._dayOfYear > C1(a) || s._dayOfYear === 0) && (gi(s)._overflowDayOfYear = !0), t = ag(a, 0, s._dayOfYear), s._a[il] = t.getUTCMonth(), s._a[zo] = t.getUTCDate()), e = 0; e < 3 && s._a[e] == null; ++e)
      s._a[e] = i[e] = n[e];
    for (; e < 7; e++)
      s._a[e] = i[e] = s._a[e] == null ? e === 2 ? 1 : 0 : s._a[e];
    s._a[fs] === 24 && s._a[eo] === 0 && s._a[nl] === 0 && s._a[fu] === 0 && (s._nextDay = !0, s._a[fs] = 0), s._d = (s._useUTC ? ag : Xq).apply(
      null,
      i
    ), r = s._useUTC ? s._d.getUTCDay() : s._d.getDay(), s._tzm != null && s._d.setUTCMinutes(s._d.getUTCMinutes() - s._tzm), s._nextDay && (s._a[fs] = 24), s._w && typeof s._w.d < "u" && s._w.d !== r && (gi(s).weekdayMismatch = !0);
  }
}
function zY(s) {
  var e, t, i, n, r, a, o, l, c;
  e = s._w, e.GG != null || e.W != null || e.E != null ? (r = 1, a = 4, t = pf(
    e.GG,
    s._a[rr],
    og(Pn(), 1, 4).year
  ), i = pf(e.W, 1), n = pf(e.E, 1), (n < 1 || n > 7) && (l = !0)) : (r = s._locale._week.dow, a = s._locale._week.doy, c = og(Pn(), r, a), t = pf(e.gg, s._a[rr], c.year), i = pf(e.w, c.week), e.d != null ? (n = e.d, (n < 0 || n > 6) && (l = !0)) : e.e != null ? (n = e.e + r, (e.e < 0 || e.e > 6) && (l = !0)) : n = r), i < 1 || i > hl(t, r, a) ? gi(s)._overflowWeeks = !0 : l != null ? gi(s)._overflowWeekday = !0 : (o = XI(t, i, n, r, a), s._a[rr] = o.year, s._dayOfYear = o.dayOfYear);
}
It.ISO_8601 = function() {
};
It.RFC_2822 = function() {
};
function SS(s) {
  if (s._f === It.ISO_8601) {
    KI(s);
    return;
  }
  if (s._f === It.RFC_2822) {
    JI(s);
    return;
  }
  s._a = [], gi(s).empty = !0;
  var e = "" + s._i, t, i, n, r, a, o = e.length, l = 0, c, h;
  for (n = II(s._f, s._locale).match(uS) || [], h = n.length, t = 0; t < h; t++)
    r = n[t], i = (e.match(Tq(r, s)) || [])[0], i && (a = e.substr(0, e.indexOf(i)), a.length > 0 && gi(s).unusedInput.push(a), e = e.slice(
      e.indexOf(i) + i.length
    ), l += i.length), Bf[r] ? (i ? gi(s).empty = !1 : gi(s).unusedTokens.push(r), _q(r, i, s)) : s._strict && !i && gi(s).unusedTokens.push(r);
  gi(s).charsLeftOver = o - l, e.length > 0 && gi(s).unusedInput.push(e), s._a[fs] <= 12 && gi(s).bigHour === !0 && s._a[fs] > 0 && (gi(s).bigHour = void 0), gi(s).parsedDateParts = s._a.slice(0), gi(s).meridiem = s._meridiem, s._a[fs] = kY(
    s._locale,
    s._a[fs],
    s._meridiem
  ), c = gi(s).era, c !== null && (s._a[rr] = s._locale.erasConvertYear(c, s._a[rr])), MS(s), bS(s);
}
function kY(s, e, t) {
  var i;
  return t == null ? e : s.meridiemHour != null ? s.meridiemHour(e, t) : (s.isPM != null && (i = s.isPM(t), i && e < 12 && (e += 12), !i && e === 12 && (e = 0)), e);
}
function GY(s) {
  var e, t, i, n, r, a, o = !1, l = s._f.length;
  if (l === 0) {
    gi(s).invalidFormat = !0, s._d = /* @__PURE__ */ new Date(NaN);
    return;
  }
  for (n = 0; n < l; n++)
    r = 0, a = !1, e = cS({}, s), s._useUTC != null && (e._useUTC = s._useUTC), e._f = s._f[n], SS(e), lS(e) && (a = !0), r += gi(e).charsLeftOver, r += gi(e).unusedTokens.length * 10, gi(e).score = r, o ? r < i && (i = r, t = e) : (i == null || r < i || a) && (i = r, t = e, a && (o = !0));
  wc(s, t || e);
}
function HY(s) {
  if (!s._d) {
    var e = dS(s._i), t = e.day === void 0 ? e.date : e.day;
    s._a = RI(
      [e.year, e.month, t, e.hour, e.minute, e.second, e.millisecond],
      function(i) {
        return i && parseInt(i, 10);
      }
    ), MS(s);
  }
}
function VY(s) {
  var e = new S2(bS(QI(s)));
  return e._nextDay && (e.add(1, "d"), e._nextDay = void 0), e;
}
function QI(s) {
  var e = s._i, t = s._f;
  return s._locale = s._locale || Cl(s._l), e === null || t === void 0 && e === "" ? I4({ nullInput: !0 }) : (typeof e == "string" && (s._i = e = s._locale.preparse(e)), go(e) ? new S2(bS(e)) : (M2(e) ? s._d = e : mo(t) ? GY(s) : t ? SS(s) : WY(s), lS(s) || (s._d = null), s));
}
function WY(s) {
  var e = s._i;
  Rr(e) ? s._d = new Date(It.now()) : M2(e) ? s._d = new Date(e.valueOf()) : typeof e == "string" ? FY(s) : mo(e) ? (s._a = RI(e.slice(0), function(t) {
    return parseInt(t, 10);
  }), MS(s)) : Su(e) ? HY(s) : Al(e) ? s._d = new Date(e) : It.createFromInputFallback(s);
}
function $I(s, e, t, i, n) {
  var r = {};
  return (e === !0 || e === !1) && (i = e, e = void 0), (t === !0 || t === !1) && (i = t, t = void 0), (Su(s) && oS(s) || mo(s) && s.length === 0) && (s = void 0), r._isAMomentObject = !0, r._useUTC = r._isUTC = n, r._l = t, r._i = s, r._f = e, r._strict = i, VY(r);
}
function Pn(s, e, t, i) {
  return $I(s, e, t, i, !1);
}
var XY = La(
  "moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/",
  function() {
    var s = Pn.apply(null, arguments);
    return this.isValid() && s.isValid() ? s < this ? this : s : I4();
  }
), jY = La(
  "moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/",
  function() {
    var s = Pn.apply(null, arguments);
    return this.isValid() && s.isValid() ? s > this ? this : s : I4();
  }
);
function eO(s, e) {
  var t, i;
  if (e.length === 1 && mo(e[0]) && (e = e[0]), !e.length)
    return Pn();
  for (t = e[0], i = 1; i < e.length; ++i)
    (!e[i].isValid() || e[i][s](t)) && (t = e[i]);
  return t;
}
function qY() {
  var s = [].slice.call(arguments, 0);
  return eO("isBefore", s);
}
function YY() {
  var s = [].slice.call(arguments, 0);
  return eO("isAfter", s);
}
var ZY = function() {
  return Date.now ? Date.now() : +/* @__PURE__ */ new Date();
}, Am = [
  "year",
  "quarter",
  "month",
  "week",
  "day",
  "hour",
  "minute",
  "second",
  "millisecond"
];
function KY(s) {
  var e, t = !1, i, n = Am.length;
  for (e in s)
    if (Zi(s, e) && !(Kn.call(Am, e) !== -1 && (s[e] == null || !isNaN(s[e]))))
      return !1;
  for (i = 0; i < n; ++i)
    if (s[Am[i]]) {
      if (t)
        return !1;
      parseFloat(s[Am[i]]) !== Di(s[Am[i]]) && (t = !0);
    }
  return !0;
}
function JY() {
  return this._isValid;
}
function QY() {
  return xo(NaN);
}
function G4(s) {
  var e = dS(s), t = e.year || 0, i = e.quarter || 0, n = e.month || 0, r = e.week || e.isoWeek || 0, a = e.day || 0, o = e.hour || 0, l = e.minute || 0, c = e.second || 0, h = e.millisecond || 0;
  this._isValid = KY(e), this._milliseconds = +h + c * 1e3 + // 1000
  l * 6e4 + // 1000 * 60
  o * 1e3 * 60 * 60, this._days = +a + r * 7, this._months = +n + i * 3 + t * 12, this._data = {}, this._locale = Cl(), this._bubble();
}
function _y(s) {
  return s instanceof G4;
}
function ab(s) {
  return s < 0 ? Math.round(-1 * s) * -1 : Math.round(s);
}
function $Y(s, e, t) {
  var i = Math.min(s.length, e.length), n = Math.abs(s.length - e.length), r = 0, a;
  for (a = 0; a < i; a++)
    (Di(s[a]) !== Di(e[a])) && r++;
  return r + n;
}
function tO(s, e) {
  Vt(s, 0, 0, function() {
    var t = this.utcOffset(), i = "+";
    return t < 0 && (t = -t, i = "-"), i + u0(~~(t / 60), 2) + e + u0(~~t % 60, 2);
  });
}
tO("Z", ":");
tO("ZZ", "");
Nt("Z", U4);
Nt("ZZ", U4);
fn(["Z", "ZZ"], function(s, e, t) {
  t._useUTC = !0, t._tzm = wS(U4, s);
});
var eZ = /([\+\-]|\d\d)/gi;
function wS(s, e) {
  var t = (e || "").match(s), i, n, r;
  return t === null ? null : (i = t[t.length - 1] || [], n = (i + "").match(eZ) || ["-", 0, 0], r = +(n[1] * 60) + Di(n[2]), r === 0 ? 0 : n[0] === "+" ? r : -r);
}
function ES(s, e) {
  var t, i;
  return e._isUTC ? (t = e.clone(), i = (go(s) || M2(s) ? s.valueOf() : Pn(s).valueOf()) - t.valueOf(), t._d.setTime(t._d.valueOf() + i), It.updateOffset(t, !1), t) : Pn(s).local();
}
function ob(s) {
  return -Math.round(s._d.getTimezoneOffset());
}
It.updateOffset = function() {
};
function tZ(s, e, t) {
  var i = this._offset || 0, n;
  if (!this.isValid())
    return s != null ? this : NaN;
  if (s != null) {
    if (typeof s == "string") {
      if (s = wS(U4, s), s === null)
        return this;
    } else
      Math.abs(s) < 16 && !t && (s = s * 60);
    return !this._isUTC && e && (n = ob(this)), this._offset = s, this._isUTC = !0, n != null && this.add(n, "m"), i !== s && (!e || this._changeInProgress ? sO(
      this,
      xo(s - i, "m"),
      1,
      !1
    ) : this._changeInProgress || (this._changeInProgress = !0, It.updateOffset(this, !0), this._changeInProgress = null)), this;
  } else
    return this._isUTC ? i : ob(this);
}
function iZ(s, e) {
  return s != null ? (typeof s != "string" && (s = -s), this.utcOffset(s, e), this) : -this.utcOffset();
}
function nZ(s) {
  return this.utcOffset(0, s);
}
function sZ(s) {
  return this._isUTC && (this.utcOffset(0, s), this._isUTC = !1, s && this.subtract(ob(this), "m")), this;
}
function rZ() {
  if (this._tzm != null)
    this.utcOffset(this._tzm, !1, !0);
  else if (typeof this._i == "string") {
    var s = wS(wq, this._i);
    s != null ? this.utcOffset(s) : this.utcOffset(0, !0);
  }
  return this;
}
function aZ(s) {
  return this.isValid() ? (s = s ? Pn(s).utcOffset() : 0, (this.utcOffset() - s) % 60 === 0) : !1;
}
function oZ() {
  return this.utcOffset() > this.clone().month(0).utcOffset() || this.utcOffset() > this.clone().month(5).utcOffset();
}
function lZ() {
  if (!Rr(this._isDSTShifted))
    return this._isDSTShifted;
  var s = {}, e;
  return cS(s, this), s = QI(s), s._a ? (e = s._isUTC ? v0(s._a) : Pn(s._a), this._isDSTShifted = this.isValid() && $Y(s._a, e.toArray()) > 0) : this._isDSTShifted = !1, this._isDSTShifted;
}
function cZ() {
  return this.isValid() ? !this._isUTC : !1;
}
function hZ() {
  return this.isValid() ? this._isUTC : !1;
}
function iO() {
  return this.isValid() ? this._isUTC && this._offset === 0 : !1;
}
var uZ = /^(-|\+)?(?:(\d*)[. ])?(\d+):(\d+)(?::(\d+)(\.\d*)?)?$/, dZ = /^(-|\+)?P(?:([-+]?[0-9,.]*)Y)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)W)?(?:([-+]?[0-9,.]*)D)?(?:T(?:([-+]?[0-9,.]*)H)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)S)?)?$/;
function xo(s, e) {
  var t = s, i = null, n, r, a;
  return _y(s) ? t = {
    ms: s._milliseconds,
    d: s._days,
    M: s._months
  } : Al(s) || !isNaN(+s) ? (t = {}, e ? t[e] = +s : t.milliseconds = +s) : (i = uZ.exec(s)) ? (n = i[1] === "-" ? -1 : 1, t = {
    y: 0,
    d: Di(i[zo]) * n,
    h: Di(i[fs]) * n,
    m: Di(i[eo]) * n,
    s: Di(i[nl]) * n,
    ms: Di(ab(i[fu] * 1e3)) * n
    // the millisecond decimal point is included in the match
  }) : (i = dZ.exec(s)) ? (n = i[1] === "-" ? -1 : 1, t = {
    y: Eh(i[2], n),
    M: Eh(i[3], n),
    w: Eh(i[4], n),
    d: Eh(i[5], n),
    h: Eh(i[6], n),
    m: Eh(i[7], n),
    s: Eh(i[8], n)
  }) : t == null ? t = {} : typeof t == "object" && ("from" in t || "to" in t) && (a = fZ(
    Pn(t.from),
    Pn(t.to)
  ), t = {}, t.ms = a.milliseconds, t.M = a.months), r = new G4(t), _y(s) && Zi(s, "_locale") && (r._locale = s._locale), _y(s) && Zi(s, "_isValid") && (r._isValid = s._isValid), r;
}
xo.fn = G4.prototype;
xo.invalid = QY;
function Eh(s, e) {
  var t = s && parseFloat(s.replace(",", "."));
  return (isNaN(t) ? 0 : t) * e;
}
function TT(s, e) {
  var t = {};
  return t.months = e.month() - s.month() + (e.year() - s.year()) * 12, s.clone().add(t.months, "M").isAfter(e) && --t.months, t.milliseconds = +e - +s.clone().add(t.months, "M"), t;
}
function fZ(s, e) {
  var t;
  return s.isValid() && e.isValid() ? (e = ES(e, s), s.isBefore(e) ? t = TT(s, e) : (t = TT(e, s), t.milliseconds = -t.milliseconds, t.months = -t.months), t) : { milliseconds: 0, months: 0 };
}
function nO(s, e) {
  return function(t, i) {
    var n, r;
    return i !== null && !isNaN(+i) && (LI(
      e,
      "moment()." + e + "(period, number) is deprecated. Please use moment()." + e + "(number, period). See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info."
    ), r = t, t = i, i = r), n = xo(t, i), sO(this, n, s), this;
  };
}
function sO(s, e, t, i) {
  var n = e._milliseconds, r = ab(e._days), a = ab(e._months);
  s.isValid() && (i = i ?? !0, a && HI(s, rg(s, "Month") + a * t), r && zI(s, "Date", rg(s, "Date") + r * t), n && s._d.setTime(s._d.valueOf() + n * t), i && It.updateOffset(s, r || a));
}
var pZ = nO(1, "add"), mZ = nO(-1, "subtract");
function rO(s) {
  return typeof s == "string" || s instanceof String;
}
function gZ(s) {
  return go(s) || M2(s) || rO(s) || Al(s) || yZ(s) || vZ(s) || s === null || s === void 0;
}
function vZ(s) {
  var e = Su(s) && !oS(s), t = !1, i = [
    "years",
    "year",
    "y",
    "months",
    "month",
    "M",
    "days",
    "day",
    "d",
    "dates",
    "date",
    "D",
    "hours",
    "hour",
    "h",
    "minutes",
    "minute",
    "m",
    "seconds",
    "second",
    "s",
    "milliseconds",
    "millisecond",
    "ms"
  ], n, r, a = i.length;
  for (n = 0; n < a; n += 1)
    r = i[n], t = t || Zi(s, r);
  return e && t;
}
function yZ(s) {
  var e = mo(s), t = !1;
  return e && (t = s.filter(function(i) {
    return !Al(i) && rO(s);
  }).length === 0), e && t;
}
function xZ(s) {
  var e = Su(s) && !oS(s), t = !1, i = [
    "sameDay",
    "nextDay",
    "lastDay",
    "nextWeek",
    "lastWeek",
    "sameElse"
  ], n, r;
  for (n = 0; n < i.length; n += 1)
    r = i[n], t = t || Zi(s, r);
  return e && t;
}
function AZ(s, e) {
  var t = s.diff(e, "days", !0);
  return t < -6 ? "sameElse" : t < -1 ? "lastWeek" : t < 0 ? "lastDay" : t < 1 ? "sameDay" : t < 2 ? "nextDay" : t < 7 ? "nextWeek" : "sameElse";
}
function bZ(s, e) {
  arguments.length === 1 && (arguments[0] ? gZ(arguments[0]) ? (s = arguments[0], e = void 0) : xZ(arguments[0]) && (e = arguments[0], s = void 0) : (s = void 0, e = void 0));
  var t = s || Pn(), i = ES(t, this).startOf("day"), n = It.calendarFormat(this, i) || "sameElse", r = e && (y0(e[n]) ? e[n].call(this, t) : e[n]);
  return this.format(
    r || this.localeData().calendar(n, this, Pn(t))
  );
}
function MZ() {
  return new S2(this);
}
function SZ(s, e) {
  var t = go(s) ? s : Pn(s);
  return this.isValid() && t.isValid() ? (e = Ia(e) || "millisecond", e === "millisecond" ? this.valueOf() > t.valueOf() : t.valueOf() < this.clone().startOf(e).valueOf()) : !1;
}
function wZ(s, e) {
  var t = go(s) ? s : Pn(s);
  return this.isValid() && t.isValid() ? (e = Ia(e) || "millisecond", e === "millisecond" ? this.valueOf() < t.valueOf() : this.clone().endOf(e).valueOf() < t.valueOf()) : !1;
}
function EZ(s, e, t, i) {
  var n = go(s) ? s : Pn(s), r = go(e) ? e : Pn(e);
  return this.isValid() && n.isValid() && r.isValid() ? (i = i || "()", (i[0] === "(" ? this.isAfter(n, t) : !this.isBefore(n, t)) && (i[1] === ")" ? this.isBefore(r, t) : !this.isAfter(r, t))) : !1;
}
function TZ(s, e) {
  var t = go(s) ? s : Pn(s), i;
  return this.isValid() && t.isValid() ? (e = Ia(e) || "millisecond", e === "millisecond" ? this.valueOf() === t.valueOf() : (i = t.valueOf(), this.clone().startOf(e).valueOf() <= i && i <= this.clone().endOf(e).valueOf())) : !1;
}
function CZ(s, e) {
  return this.isSame(s, e) || this.isAfter(s, e);
}
function _Z(s, e) {
  return this.isSame(s, e) || this.isBefore(s, e);
}
function PZ(s, e, t) {
  var i, n, r;
  if (!this.isValid())
    return NaN;
  if (i = ES(s, this), !i.isValid())
    return NaN;
  switch (n = (i.utcOffset() - this.utcOffset()) * 6e4, e = Ia(e), e) {
    case "year":
      r = Py(this, i) / 12;
      break;
    case "month":
      r = Py(this, i);
      break;
    case "quarter":
      r = Py(this, i) / 3;
      break;
    case "second":
      r = (this - i) / 1e3;
      break;
    case "minute":
      r = (this - i) / 6e4;
      break;
    case "hour":
      r = (this - i) / 36e5;
      break;
    case "day":
      r = (this - i - n) / 864e5;
      break;
    case "week":
      r = (this - i - n) / 6048e5;
      break;
    default:
      r = this - i;
  }
  return t ? r : Aa(r);
}
function Py(s, e) {
  if (s.date() < e.date())
    return -Py(e, s);
  var t = (e.year() - s.year()) * 12 + (e.month() - s.month()), i = s.clone().add(t, "months"), n, r;
  return e - i < 0 ? (n = s.clone().add(t - 1, "months"), r = (e - i) / (i - n)) : (n = s.clone().add(t + 1, "months"), r = (e - i) / (n - i)), -(t + r) || 0;
}
It.defaultFormat = "YYYY-MM-DDTHH:mm:ssZ";
It.defaultFormatUtc = "YYYY-MM-DDTHH:mm:ss[Z]";
function RZ() {
  return this.clone().locale("en").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ");
}
function DZ(s) {
  if (!this.isValid())
    return null;
  var e = s !== !0, t = e ? this.clone().utc() : this;
  return t.year() < 0 || t.year() > 9999 ? Cy(
    t,
    e ? "YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]" : "YYYYYY-MM-DD[T]HH:mm:ss.SSSZ"
  ) : y0(Date.prototype.toISOString) ? e ? this.toDate().toISOString() : new Date(this.valueOf() + this.utcOffset() * 60 * 1e3).toISOString().replace("Z", Cy(t, "Z")) : Cy(
    t,
    e ? "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]" : "YYYY-MM-DD[T]HH:mm:ss.SSSZ"
  );
}
function LZ() {
  if (!this.isValid())
    return "moment.invalid(/* " + this._i + " */)";
  var s = "moment", e = "", t, i, n, r;
  return this.isLocal() || (s = this.utcOffset() === 0 ? "moment.utc" : "moment.parseZone", e = "Z"), t = "[" + s + '("]', i = 0 <= this.year() && this.year() <= 9999 ? "YYYY" : "YYYYYY", n = "-MM-DD[T]HH:mm:ss.SSS", r = e + '[")]', this.format(t + i + n + r);
}
function IZ(s) {
  s || (s = this.isUtc() ? It.defaultFormatUtc : It.defaultFormat);
  var e = Cy(this, s);
  return this.localeData().postformat(e);
}
function OZ(s, e) {
  return this.isValid() && (go(s) && s.isValid() || Pn(s).isValid()) ? xo({ to: this, from: s }).locale(this.locale()).humanize(!e) : this.localeData().invalidDate();
}
function NZ(s) {
  return this.from(Pn(), s);
}
function BZ(s, e) {
  return this.isValid() && (go(s) && s.isValid() || Pn(s).isValid()) ? xo({ from: this, to: s }).locale(this.locale()).humanize(!e) : this.localeData().invalidDate();
}
function FZ(s) {
  return this.to(Pn(), s);
}
function aO(s) {
  var e;
  return s === void 0 ? this._locale._abbr : (e = Cl(s), e != null && (this._locale = e), this);
}
var oO = La(
  "moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.",
  function(s) {
    return s === void 0 ? this.localeData() : this.locale(s);
  }
);
function lO() {
  return this._locale;
}
var Q9 = 1e3, Ff = 60 * Q9, $9 = 60 * Ff, cO = (365 * 400 + 97) * 24 * $9;
function Uf(s, e) {
  return (s % e + e) % e;
}
function hO(s, e, t) {
  return s < 100 && s >= 0 ? new Date(s + 400, e, t) - cO : new Date(s, e, t).valueOf();
}
function uO(s, e, t) {
  return s < 100 && s >= 0 ? Date.UTC(s + 400, e, t) - cO : Date.UTC(s, e, t);
}
function UZ(s) {
  var e, t;
  if (s = Ia(s), s === void 0 || s === "millisecond" || !this.isValid())
    return this;
  switch (t = this._isUTC ? uO : hO, s) {
    case "year":
      e = t(this.year(), 0, 1);
      break;
    case "quarter":
      e = t(
        this.year(),
        this.month() - this.month() % 3,
        1
      );
      break;
    case "month":
      e = t(this.year(), this.month(), 1);
      break;
    case "week":
      e = t(
        this.year(),
        this.month(),
        this.date() - this.weekday()
      );
      break;
    case "isoWeek":
      e = t(
        this.year(),
        this.month(),
        this.date() - (this.isoWeekday() - 1)
      );
      break;
    case "day":
    case "date":
      e = t(this.year(), this.month(), this.date());
      break;
    case "hour":
      e = this._d.valueOf(), e -= Uf(
        e + (this._isUTC ? 0 : this.utcOffset() * Ff),
        $9
      );
      break;
    case "minute":
      e = this._d.valueOf(), e -= Uf(e, Ff);
      break;
    case "second":
      e = this._d.valueOf(), e -= Uf(e, Q9);
      break;
  }
  return this._d.setTime(e), It.updateOffset(this, !0), this;
}
function zZ(s) {
  var e, t;
  if (s = Ia(s), s === void 0 || s === "millisecond" || !this.isValid())
    return this;
  switch (t = this._isUTC ? uO : hO, s) {
    case "year":
      e = t(this.year() + 1, 0, 1) - 1;
      break;
    case "quarter":
      e = t(
        this.year(),
        this.month() - this.month() % 3 + 3,
        1
      ) - 1;
      break;
    case "month":
      e = t(this.year(), this.month() + 1, 1) - 1;
      break;
    case "week":
      e = t(
        this.year(),
        this.month(),
        this.date() - this.weekday() + 7
      ) - 1;
      break;
    case "isoWeek":
      e = t(
        this.year(),
        this.month(),
        this.date() - (this.isoWeekday() - 1) + 7
      ) - 1;
      break;
    case "day":
    case "date":
      e = t(this.year(), this.month(), this.date() + 1) - 1;
      break;
    case "hour":
      e = this._d.valueOf(), e += $9 - Uf(
        e + (this._isUTC ? 0 : this.utcOffset() * Ff),
        $9
      ) - 1;
      break;
    case "minute":
      e = this._d.valueOf(), e += Ff - Uf(e, Ff) - 1;
      break;
    case "second":
      e = this._d.valueOf(), e += Q9 - Uf(e, Q9) - 1;
      break;
  }
  return this._d.setTime(e), It.updateOffset(this, !0), this;
}
function kZ() {
  return this._d.valueOf() - (this._offset || 0) * 6e4;
}
function GZ() {
  return Math.floor(this.valueOf() / 1e3);
}
function HZ() {
  return new Date(this.valueOf());
}
function VZ() {
  var s = this;
  return [
    s.year(),
    s.month(),
    s.date(),
    s.hour(),
    s.minute(),
    s.second(),
    s.millisecond()
  ];
}
function WZ() {
  var s = this;
  return {
    years: s.year(),
    months: s.month(),
    date: s.date(),
    hours: s.hours(),
    minutes: s.minutes(),
    seconds: s.seconds(),
    milliseconds: s.milliseconds()
  };
}
function XZ() {
  return this.isValid() ? this.toISOString() : null;
}
function jZ() {
  return lS(this);
}
function qZ() {
  return wc({}, gi(this));
}
function YZ() {
  return gi(this).overflow;
}
function ZZ() {
  return {
    input: this._i,
    format: this._f,
    locale: this._locale,
    isUTC: this._isUTC,
    strict: this._strict
  };
}
Vt("N", 0, 0, "eraAbbr");
Vt("NN", 0, 0, "eraAbbr");
Vt("NNN", 0, 0, "eraAbbr");
Vt("NNNN", 0, 0, "eraName");
Vt("NNNNN", 0, 0, "eraNarrow");
Vt("y", ["y", 1], "yo", "eraYear");
Vt("y", ["yy", 2], 0, "eraYear");
Vt("y", ["yyy", 3], 0, "eraYear");
Vt("y", ["yyyy", 4], 0, "eraYear");
Nt("N", TS);
Nt("NN", TS);
Nt("NNN", TS);
Nt("NNNN", aK);
Nt("NNNNN", oK);
fn(
  ["N", "NN", "NNN", "NNNN", "NNNNN"],
  function(s, e, t, i) {
    var n = t._locale.erasParse(s, i, t._strict);
    n ? gi(t).era = n : gi(t).invalidEra = s;
  }
);
Nt("y", Yp);
Nt("yy", Yp);
Nt("yyy", Yp);
Nt("yyyy", Yp);
Nt("yo", lK);
fn(["y", "yy", "yyy", "yyyy"], rr);
fn(["yo"], function(s, e, t, i) {
  var n;
  t._locale._eraYearOrdinalRegex && (n = s.match(t._locale._eraYearOrdinalRegex)), t._locale.eraYearOrdinalParse ? e[rr] = t._locale.eraYearOrdinalParse(s, n) : e[rr] = parseInt(s, 10);
});
function KZ(s, e) {
  var t, i, n, r = this._eras || Cl("en")._eras;
  for (t = 0, i = r.length; t < i; ++t) {
    switch (typeof r[t].since) {
      case "string":
        n = It(r[t].since).startOf("day"), r[t].since = n.valueOf();
        break;
    }
    switch (typeof r[t].until) {
      case "undefined":
        r[t].until = 1 / 0;
        break;
      case "string":
        n = It(r[t].until).startOf("day").valueOf(), r[t].until = n.valueOf();
        break;
    }
  }
  return r;
}
function JZ(s, e, t) {
  var i, n, r = this.eras(), a, o, l;
  for (s = s.toUpperCase(), i = 0, n = r.length; i < n; ++i)
    if (a = r[i].name.toUpperCase(), o = r[i].abbr.toUpperCase(), l = r[i].narrow.toUpperCase(), t)
      switch (e) {
        case "N":
        case "NN":
        case "NNN":
          if (o === s)
            return r[i];
          break;
        case "NNNN":
          if (a === s)
            return r[i];
          break;
        case "NNNNN":
          if (l === s)
            return r[i];
          break;
      }
    else if ([a, o, l].indexOf(s) >= 0)
      return r[i];
}
function QZ(s, e) {
  var t = s.since <= s.until ? 1 : -1;
  return e === void 0 ? It(s.since).year() : It(s.since).year() + (e - s.offset) * t;
}
function $Z() {
  var s, e, t, i = this.localeData().eras();
  for (s = 0, e = i.length; s < e; ++s)
    if (t = this.clone().startOf("day").valueOf(), i[s].since <= t && t <= i[s].until || i[s].until <= t && t <= i[s].since)
      return i[s].name;
  return "";
}
function eK() {
  var s, e, t, i = this.localeData().eras();
  for (s = 0, e = i.length; s < e; ++s)
    if (t = this.clone().startOf("day").valueOf(), i[s].since <= t && t <= i[s].until || i[s].until <= t && t <= i[s].since)
      return i[s].narrow;
  return "";
}
function tK() {
  var s, e, t, i = this.localeData().eras();
  for (s = 0, e = i.length; s < e; ++s)
    if (t = this.clone().startOf("day").valueOf(), i[s].since <= t && t <= i[s].until || i[s].until <= t && t <= i[s].since)
      return i[s].abbr;
  return "";
}
function iK() {
  var s, e, t, i, n = this.localeData().eras();
  for (s = 0, e = n.length; s < e; ++s)
    if (t = n[s].since <= n[s].until ? 1 : -1, i = this.clone().startOf("day").valueOf(), n[s].since <= i && i <= n[s].until || n[s].until <= i && i <= n[s].since)
      return (this.year() - It(n[s].since).year()) * t + n[s].offset;
  return this.year();
}
function nK(s) {
  return Zi(this, "_erasNameRegex") || CS.call(this), s ? this._erasNameRegex : this._erasRegex;
}
function sK(s) {
  return Zi(this, "_erasAbbrRegex") || CS.call(this), s ? this._erasAbbrRegex : this._erasRegex;
}
function rK(s) {
  return Zi(this, "_erasNarrowRegex") || CS.call(this), s ? this._erasNarrowRegex : this._erasRegex;
}
function TS(s, e) {
  return e.erasAbbrRegex(s);
}
function aK(s, e) {
  return e.erasNameRegex(s);
}
function oK(s, e) {
  return e.erasNarrowRegex(s);
}
function lK(s, e) {
  return e._eraYearOrdinalRegex || Yp;
}
function CS() {
  var s = [], e = [], t = [], i = [], n, r, a, o, l, c = this.eras();
  for (n = 0, r = c.length; n < r; ++n)
    a = cl(c[n].name), o = cl(c[n].abbr), l = cl(c[n].narrow), e.push(a), s.push(o), t.push(l), i.push(a), i.push(o), i.push(l);
  this._erasRegex = new RegExp("^(" + i.join("|") + ")", "i"), this._erasNameRegex = new RegExp("^(" + e.join("|") + ")", "i"), this._erasAbbrRegex = new RegExp("^(" + s.join("|") + ")", "i"), this._erasNarrowRegex = new RegExp(
    "^(" + t.join("|") + ")",
    "i"
  );
}
Vt(0, ["gg", 2], 0, function() {
  return this.weekYear() % 100;
});
Vt(0, ["GG", 2], 0, function() {
  return this.isoWeekYear() % 100;
});
function H4(s, e) {
  Vt(0, [s, s.length], 0, e);
}
H4("gggg", "weekYear");
H4("ggggg", "weekYear");
H4("GGGG", "isoWeekYear");
H4("GGGGG", "isoWeekYear");
Nt("G", F4);
Nt("g", F4);
Nt("GG", Rn, aa);
Nt("gg", Rn, aa);
Nt("GGGG", pS, fS);
Nt("gggg", pS, fS);
Nt("GGGGG", B4, O4);
Nt("ggggg", B4, O4);
E2(
  ["gggg", "ggggg", "GGGG", "GGGGG"],
  function(s, e, t, i) {
    e[i.substr(0, 2)] = Di(s);
  }
);
E2(["gg", "GG"], function(s, e, t, i) {
  e[i] = It.parseTwoDigitYear(s);
});
function cK(s) {
  return dO.call(
    this,
    s,
    this.week(),
    this.weekday() + this.localeData()._week.dow,
    this.localeData()._week.dow,
    this.localeData()._week.doy
  );
}
function hK(s) {
  return dO.call(
    this,
    s,
    this.isoWeek(),
    this.isoWeekday(),
    1,
    4
  );
}
function uK() {
  return hl(this.year(), 1, 4);
}
function dK() {
  return hl(this.isoWeekYear(), 1, 4);
}
function fK() {
  var s = this.localeData()._week;
  return hl(this.year(), s.dow, s.doy);
}
function pK() {
  var s = this.localeData()._week;
  return hl(this.weekYear(), s.dow, s.doy);
}
function dO(s, e, t, i, n) {
  var r;
  return s == null ? og(this, i, n).year : (r = hl(s, i, n), e > r && (e = r), mK.call(this, s, e, t, i, n));
}
function mK(s, e, t, i, n) {
  var r = XI(s, e, t, i, n), a = ag(r.year, 0, r.dayOfYear);
  return this.year(a.getUTCFullYear()), this.month(a.getUTCMonth()), this.date(a.getUTCDate()), this;
}
Vt("Q", 0, "Qo", "quarter");
Nt("Q", OI);
fn("Q", function(s, e) {
  e[il] = (Di(s) - 1) * 3;
});
function gK(s) {
  return s == null ? Math.ceil((this.month() + 1) / 3) : this.month((s - 1) * 3 + this.month() % 3);
}
Vt("D", ["DD", 2], "Do", "date");
Nt("D", Rn, Zp);
Nt("DD", Rn, aa);
Nt("Do", function(s, e) {
  return s ? e._dayOfMonthOrdinalParse || e._ordinalParse : e._dayOfMonthOrdinalParseLenient;
});
fn(["D", "DD"], zo);
fn("Do", function(s, e) {
  e[zo] = Di(s.match(Rn)[0]);
});
var fO = Kp("Date", !0);
Vt("DDD", ["DDDD", 3], "DDDo", "dayOfYear");
Nt("DDD", N4);
Nt("DDDD", NI);
fn(["DDD", "DDDD"], function(s, e, t) {
  t._dayOfYear = Di(s);
});
function vK(s) {
  var e = Math.round(
    (this.clone().startOf("day") - this.clone().startOf("year")) / 864e5
  ) + 1;
  return s == null ? e : this.add(s - e, "d");
}
Vt("m", ["mm", 2], 0, "minute");
Nt("m", Rn, mS);
Nt("mm", Rn, aa);
fn(["m", "mm"], eo);
var yK = Kp("Minutes", !1);
Vt("s", ["ss", 2], 0, "second");
Nt("s", Rn, mS);
Nt("ss", Rn, aa);
fn(["s", "ss"], nl);
var xK = Kp("Seconds", !1);
Vt("S", 0, 0, function() {
  return ~~(this.millisecond() / 100);
});
Vt(0, ["SS", 2], 0, function() {
  return ~~(this.millisecond() / 10);
});
Vt(0, ["SSS", 3], 0, "millisecond");
Vt(0, ["SSSS", 4], 0, function() {
  return this.millisecond() * 10;
});
Vt(0, ["SSSSS", 5], 0, function() {
  return this.millisecond() * 100;
});
Vt(0, ["SSSSSS", 6], 0, function() {
  return this.millisecond() * 1e3;
});
Vt(0, ["SSSSSSS", 7], 0, function() {
  return this.millisecond() * 1e4;
});
Vt(0, ["SSSSSSSS", 8], 0, function() {
  return this.millisecond() * 1e5;
});
Vt(0, ["SSSSSSSSS", 9], 0, function() {
  return this.millisecond() * 1e6;
});
Nt("S", N4, OI);
Nt("SS", N4, aa);
Nt("SSS", N4, NI);
var Ec, pO;
for (Ec = "SSSS"; Ec.length <= 9; Ec += "S")
  Nt(Ec, Yp);
function AK(s, e) {
  e[fu] = Di(("0." + s) * 1e3);
}
for (Ec = "S"; Ec.length <= 9; Ec += "S")
  fn(Ec, AK);
pO = Kp("Milliseconds", !1);
Vt("z", 0, 0, "zoneAbbr");
Vt("zz", 0, 0, "zoneName");
function bK() {
  return this._isUTC ? "UTC" : "";
}
function MK() {
  return this._isUTC ? "Coordinated Universal Time" : "";
}
var Mt = S2.prototype;
Mt.add = pZ;
Mt.calendar = bZ;
Mt.clone = MZ;
Mt.diff = PZ;
Mt.endOf = zZ;
Mt.format = IZ;
Mt.from = OZ;
Mt.fromNow = NZ;
Mt.to = BZ;
Mt.toNow = FZ;
Mt.get = Lq;
Mt.invalidAt = YZ;
Mt.isAfter = SZ;
Mt.isBefore = wZ;
Mt.isBetween = EZ;
Mt.isSame = TZ;
Mt.isSameOrAfter = CZ;
Mt.isSameOrBefore = _Z;
Mt.isValid = jZ;
Mt.lang = oO;
Mt.locale = aO;
Mt.localeData = lO;
Mt.max = jY;
Mt.min = XY;
Mt.parsingFlags = qZ;
Mt.set = Iq;
Mt.startOf = UZ;
Mt.subtract = mZ;
Mt.toArray = VZ;
Mt.toObject = WZ;
Mt.toDate = HZ;
Mt.toISOString = DZ;
Mt.inspect = LZ;
typeof Symbol < "u" && Symbol.for != null && (Mt[Symbol.for("nodejs.util.inspect.custom")] = function() {
  return "Moment<" + this.format() + ">";
});
Mt.toJSON = XZ;
Mt.toString = RZ;
Mt.unix = GZ;
Mt.valueOf = kZ;
Mt.creationData = ZZ;
Mt.eraName = $Z;
Mt.eraNarrow = eK;
Mt.eraAbbr = tK;
Mt.eraYear = iK;
Mt.year = UI;
Mt.isLeapYear = Dq;
Mt.weekYear = cK;
Mt.isoWeekYear = hK;
Mt.quarter = Mt.quarters = gK;
Mt.month = VI;
Mt.daysInMonth = Hq;
Mt.week = Mt.weeks = Kq;
Mt.isoWeek = Mt.isoWeeks = Jq;
Mt.weeksInYear = fK;
Mt.weeksInWeekYear = pK;
Mt.isoWeeksInYear = uK;
Mt.isoWeeksInISOWeekYear = dK;
Mt.date = fO;
Mt.day = Mt.days = hY;
Mt.weekday = uY;
Mt.isoWeekday = dY;
Mt.dayOfYear = vK;
Mt.hour = Mt.hours = xY;
Mt.minute = Mt.minutes = yK;
Mt.second = Mt.seconds = xK;
Mt.millisecond = Mt.milliseconds = pO;
Mt.utcOffset = tZ;
Mt.utc = nZ;
Mt.local = sZ;
Mt.parseZone = rZ;
Mt.hasAlignedHourOffset = aZ;
Mt.isDST = oZ;
Mt.isLocal = cZ;
Mt.isUtcOffset = hZ;
Mt.isUtc = iO;
Mt.isUTC = iO;
Mt.zoneAbbr = bK;
Mt.zoneName = MK;
Mt.dates = La(
  "dates accessor is deprecated. Use date instead.",
  fO
);
Mt.months = La(
  "months accessor is deprecated. Use month instead",
  VI
);
Mt.years = La(
  "years accessor is deprecated. Use year instead",
  UI
);
Mt.zone = La(
  "moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/",
  iZ
);
Mt.isDSTShifted = La(
  "isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information",
  lZ
);
function SK(s) {
  return Pn(s * 1e3);
}
function wK() {
  return Pn.apply(null, arguments).parseZone();
}
function mO(s) {
  return s;
}
var Ki = hS.prototype;
Ki.calendar = cq;
Ki.longDateFormat = fq;
Ki.invalidDate = mq;
Ki.ordinal = yq;
Ki.preparse = mO;
Ki.postformat = mO;
Ki.relativeTime = Aq;
Ki.pastFuture = bq;
Ki.set = oq;
Ki.eras = KZ;
Ki.erasParse = JZ;
Ki.erasConvertYear = QZ;
Ki.erasAbbrRegex = sK;
Ki.erasNameRegex = nK;
Ki.erasNarrowRegex = rK;
Ki.months = Uq;
Ki.monthsShort = zq;
Ki.monthsParse = Gq;
Ki.monthsRegex = Wq;
Ki.monthsShortRegex = Vq;
Ki.week = jq;
Ki.firstDayOfYear = Zq;
Ki.firstDayOfWeek = Yq;
Ki.weekdays = rY;
Ki.weekdaysMin = oY;
Ki.weekdaysShort = aY;
Ki.weekdaysParse = cY;
Ki.weekdaysRegex = fY;
Ki.weekdaysShortRegex = pY;
Ki.weekdaysMinRegex = mY;
Ki.isPM = vY;
Ki.meridiem = AY;
function ex(s, e, t, i) {
  var n = Cl(), r = v0().set(i, e);
  return n[t](r, s);
}
function gO(s, e, t) {
  if (Al(s) && (e = s, s = void 0), s = s || "", e != null)
    return ex(s, e, t, "month");
  var i, n = [];
  for (i = 0; i < 12; i++)
    n[i] = ex(s, i, t, "month");
  return n;
}
function _S(s, e, t, i) {
  typeof s == "boolean" ? (Al(e) && (t = e, e = void 0), e = e || "") : (e = s, t = e, s = !1, Al(e) && (t = e, e = void 0), e = e || "");
  var n = Cl(), r = s ? n._week.dow : 0, a, o = [];
  if (t != null)
    return ex(e, (t + r) % 7, i, "day");
  for (a = 0; a < 7; a++)
    o[a] = ex(e, (a + r) % 7, i, "day");
  return o;
}
function EK(s, e) {
  return gO(s, e, "months");
}
function TK(s, e) {
  return gO(s, e, "monthsShort");
}
function CK(s, e, t) {
  return _S(s, e, t, "weekdays");
}
function _K(s, e, t) {
  return _S(s, e, t, "weekdaysShort");
}
function PK(s, e, t) {
  return _S(s, e, t, "weekdaysMin");
}
Dc("en", {
  eras: [
    {
      since: "0001-01-01",
      until: 1 / 0,
      offset: 1,
      name: "Anno Domini",
      narrow: "AD",
      abbr: "AD"
    },
    {
      since: "0000-12-31",
      until: -1 / 0,
      offset: 1,
      name: "Before Christ",
      narrow: "BC",
      abbr: "BC"
    }
  ],
  dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/,
  ordinal: function(s) {
    var e = s % 10, t = Di(s % 100 / 10) === 1 ? "th" : e === 1 ? "st" : e === 2 ? "nd" : e === 3 ? "rd" : "th";
    return s + t;
  }
});
It.lang = La(
  "moment.lang is deprecated. Use moment.locale instead.",
  Dc
);
It.langData = La(
  "moment.langData is deprecated. Use moment.localeData instead.",
  Cl
);
var P0 = Math.abs;
function RK() {
  var s = this._data;
  return this._milliseconds = P0(this._milliseconds), this._days = P0(this._days), this._months = P0(this._months), s.milliseconds = P0(s.milliseconds), s.seconds = P0(s.seconds), s.minutes = P0(s.minutes), s.hours = P0(s.hours), s.months = P0(s.months), s.years = P0(s.years), this;
}
function vO(s, e, t, i) {
  var n = xo(e, t);
  return s._milliseconds += i * n._milliseconds, s._days += i * n._days, s._months += i * n._months, s._bubble();
}
function DK(s, e) {
  return vO(this, s, e, 1);
}
function LK(s, e) {
  return vO(this, s, e, -1);
}
function CT(s) {
  return s < 0 ? Math.floor(s) : Math.ceil(s);
}
function IK() {
  var s = this._milliseconds, e = this._days, t = this._months, i = this._data, n, r, a, o, l;
  return s >= 0 && e >= 0 && t >= 0 || s <= 0 && e <= 0 && t <= 0 || (s += CT(lb(t) + e) * 864e5, e = 0, t = 0), i.milliseconds = s % 1e3, n = Aa(s / 1e3), i.seconds = n % 60, r = Aa(n / 60), i.minutes = r % 60, a = Aa(r / 60), i.hours = a % 24, e += Aa(a / 24), l = Aa(yO(e)), t += l, e -= CT(lb(l)), o = Aa(t / 12), t %= 12, i.days = e, i.months = t, i.years = o, this;
}
function yO(s) {
  return s * 4800 / 146097;
}
function lb(s) {
  return s * 146097 / 4800;
}
function OK(s) {
  if (!this.isValid())
    return NaN;
  var e, t, i = this._milliseconds;
  if (s = Ia(s), s === "month" || s === "quarter" || s === "year")
    switch (e = this._days + i / 864e5, t = this._months + yO(e), s) {
      case "month":
        return t;
      case "quarter":
        return t / 3;
      case "year":
        return t / 12;
    }
  else
    switch (e = this._days + Math.round(lb(this._months)), s) {
      case "week":
        return e / 7 + i / 6048e5;
      case "day":
        return e + i / 864e5;
      case "hour":
        return e * 24 + i / 36e5;
      case "minute":
        return e * 1440 + i / 6e4;
      case "second":
        return e * 86400 + i / 1e3;
      case "millisecond":
        return Math.floor(e * 864e5) + i;
      default:
        throw new Error("Unknown unit " + s);
    }
}
function _l(s) {
  return function() {
    return this.as(s);
  };
}
var xO = _l("ms"), NK = _l("s"), BK = _l("m"), FK = _l("h"), UK = _l("d"), zK = _l("w"), kK = _l("M"), GK = _l("Q"), HK = _l("y"), VK = xO;
function WK() {
  return xo(this);
}
function XK(s) {
  return s = Ia(s), this.isValid() ? this[s + "s"]() : NaN;
}
function Qu(s) {
  return function() {
    return this.isValid() ? this._data[s] : NaN;
  };
}
var jK = Qu("milliseconds"), qK = Qu("seconds"), YK = Qu("minutes"), ZK = Qu("hours"), KK = Qu("days"), JK = Qu("months"), QK = Qu("years");
function $K() {
  return Aa(this.days() / 7);
}
var W0 = Math.round, Mf = {
  ss: 44,
  // a few seconds to seconds
  s: 45,
  // seconds to minute
  m: 45,
  // minutes to hour
  h: 22,
  // hours to day
  d: 26,
  // days to month/week
  w: null,
  // weeks to month
  M: 11
  // months to year
};
function eJ(s, e, t, i, n) {
  return n.relativeTime(e || 1, !!t, s, i);
}
function tJ(s, e, t, i) {
  var n = xo(s).abs(), r = W0(n.as("s")), a = W0(n.as("m")), o = W0(n.as("h")), l = W0(n.as("d")), c = W0(n.as("M")), h = W0(n.as("w")), u = W0(n.as("y")), d = r <= t.ss && ["s", r] || r < t.s && ["ss", r] || a <= 1 && ["m"] || a < t.m && ["mm", a] || o <= 1 && ["h"] || o < t.h && ["hh", o] || l <= 1 && ["d"] || l < t.d && ["dd", l];
  return t.w != null && (d = d || h <= 1 && ["w"] || h < t.w && ["ww", h]), d = d || c <= 1 && ["M"] || c < t.M && ["MM", c] || u <= 1 && ["y"] || ["yy", u], d[2] = e, d[3] = +s > 0, d[4] = i, eJ.apply(null, d);
}
function iJ(s) {
  return s === void 0 ? W0 : typeof s == "function" ? (W0 = s, !0) : !1;
}
function nJ(s, e) {
  return Mf[s] === void 0 ? !1 : e === void 0 ? Mf[s] : (Mf[s] = e, s === "s" && (Mf.ss = e - 1), !0);
}
function sJ(s, e) {
  if (!this.isValid())
    return this.localeData().invalidDate();
  var t = !1, i = Mf, n, r;
  return typeof s == "object" && (e = s, s = !1), typeof s == "boolean" && (t = s), typeof e == "object" && (i = Object.assign({}, Mf, e), e.s != null && e.ss == null && (i.ss = e.s - 1)), n = this.localeData(), r = tJ(this, !t, i, n), t && (r = n.pastFuture(+this, r)), n.postformat(r);
}
var h5 = Math.abs;
function Ed(s) {
  return (s > 0) - (s < 0) || +s;
}
function V4() {
  if (!this.isValid())
    return this.localeData().invalidDate();
  var s = h5(this._milliseconds) / 1e3, e = h5(this._days), t = h5(this._months), i, n, r, a, o = this.asSeconds(), l, c, h, u;
  return o ? (i = Aa(s / 60), n = Aa(i / 60), s %= 60, i %= 60, r = Aa(t / 12), t %= 12, a = s ? s.toFixed(3).replace(/\.?0+$/, "") : "", l = o < 0 ? "-" : "", c = Ed(this._months) !== Ed(o) ? "-" : "", h = Ed(this._days) !== Ed(o) ? "-" : "", u = Ed(this._milliseconds) !== Ed(o) ? "-" : "", l + "P" + (r ? c + r + "Y" : "") + (t ? c + t + "M" : "") + (e ? h + e + "D" : "") + (n || i || s ? "T" : "") + (n ? u + n + "H" : "") + (i ? u + i + "M" : "") + (s ? u + a + "S" : "")) : "P0D";
}
var Gi = G4.prototype;
Gi.isValid = JY;
Gi.abs = RK;
Gi.add = DK;
Gi.subtract = LK;
Gi.as = OK;
Gi.asMilliseconds = xO;
Gi.asSeconds = NK;
Gi.asMinutes = BK;
Gi.asHours = FK;
Gi.asDays = UK;
Gi.asWeeks = zK;
Gi.asMonths = kK;
Gi.asQuarters = GK;
Gi.asYears = HK;
Gi.valueOf = VK;
Gi._bubble = IK;
Gi.clone = WK;
Gi.get = XK;
Gi.milliseconds = jK;
Gi.seconds = qK;
Gi.minutes = YK;
Gi.hours = ZK;
Gi.days = KK;
Gi.weeks = $K;
Gi.months = JK;
Gi.years = QK;
Gi.humanize = sJ;
Gi.toISOString = V4;
Gi.toString = V4;
Gi.toJSON = V4;
Gi.locale = aO;
Gi.localeData = lO;
Gi.toIsoString = La(
  "toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)",
  V4
);
Gi.lang = oO;
Vt("X", 0, 0, "unix");
Vt("x", 0, 0, "valueOf");
Nt("x", F4);
Nt("X", Eq);
fn("X", function(s, e, t) {
  t._d = new Date(parseFloat(s) * 1e3);
});
fn("x", function(s, e, t) {
  t._d = new Date(Di(s));
});
//! moment.js
It.version = "2.30.1";
rq(Pn);
It.fn = Mt;
It.min = qY;
It.max = YY;
It.now = ZY;
It.utc = v0;
It.unix = SK;
It.months = EK;
It.isDate = M2;
It.locale = Dc;
It.invalid = I4;
It.duration = xo;
It.isMoment = go;
It.weekdays = CK;
It.parseZone = wK;
It.localeData = Cl;
It.isDuration = _y;
It.monthsShort = TK;
It.weekdaysMin = PK;
It.defineLocale = AS;
It.updateLocale = wY;
It.locales = EY;
It.weekdaysShort = _K;
It.normalizeUnits = Ia;
It.relativeTimeRounding = iJ;
It.relativeTimeThreshold = nJ;
It.calendarFormat = AZ;
It.prototype = Mt;
It.HTML5_FMT = {
  DATETIME_LOCAL: "YYYY-MM-DDTHH:mm",
  // <input type="datetime-local" />
  DATETIME_LOCAL_SECONDS: "YYYY-MM-DDTHH:mm:ss",
  // <input type="datetime-local" step="1" />
  DATETIME_LOCAL_MS: "YYYY-MM-DDTHH:mm:ss.SSS",
  // <input type="datetime-local" step="0.001" />
  DATE: "YYYY-MM-DD",
  // <input type="date" />
  TIME: "HH:mm",
  // <input type="time" />
  TIME_SECONDS: "HH:mm:ss",
  // <input type="time" step="1" />
  TIME_MS: "HH:mm:ss.SSS",
  // <input type="time" step="0.001" />
  WEEK: "GGGG-[W]WW",
  // <input type="week" />
  MONTH: "YYYY-MM"
  // <input type="month" />
};
function Hi(s, e, t) {
  const i = t.value;
  return {
    configurable: !0,
    enumerable: !1,
    // getter function
    get() {
      return (...a) => {
        if (Tt.enabled)
          return i.apply(this, a);
      };
    }
  };
}
var Jp = TypeError;
const rJ = {}, aJ = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  default: rJ
}, Symbol.toStringTag, { value: "Module" })), oJ = /* @__PURE__ */ vz(aJ);
var PS = typeof Map == "function" && Map.prototype, u5 = Object.getOwnPropertyDescriptor && PS ? Object.getOwnPropertyDescriptor(Map.prototype, "size") : null, tx = PS && u5 && typeof u5.get == "function" ? u5.get : null, _T = PS && Map.prototype.forEach, RS = typeof Set == "function" && Set.prototype, d5 = Object.getOwnPropertyDescriptor && RS ? Object.getOwnPropertyDescriptor(Set.prototype, "size") : null, ix = RS && d5 && typeof d5.get == "function" ? d5.get : null, PT = RS && Set.prototype.forEach, lJ = typeof WeakMap == "function" && WeakMap.prototype, _1 = lJ ? WeakMap.prototype.has : null, cJ = typeof WeakSet == "function" && WeakSet.prototype, P1 = cJ ? WeakSet.prototype.has : null, hJ = typeof WeakRef == "function" && WeakRef.prototype, RT = hJ ? WeakRef.prototype.deref : null, uJ = Boolean.prototype.valueOf, dJ = Object.prototype.toString, fJ = Function.prototype.toString, pJ = String.prototype.match, DS = String.prototype.slice, Tc = String.prototype.replace, mJ = String.prototype.toUpperCase, DT = String.prototype.toLowerCase, AO = RegExp.prototype.test, LT = Array.prototype.concat, No = Array.prototype.join, gJ = Array.prototype.slice, IT = Math.floor, cb = typeof BigInt == "function" ? BigInt.prototype.valueOf : null, f5 = Object.getOwnPropertySymbols, hb = typeof Symbol == "function" && typeof Symbol.iterator == "symbol" ? Symbol.prototype.toString : null, op = typeof Symbol == "function" && typeof Symbol.iterator == "object", R1 = typeof Symbol == "function" && Symbol.toStringTag && (typeof Symbol.toStringTag === op || "symbol") ? Symbol.toStringTag : null, bO = Object.prototype.propertyIsEnumerable, OT = (typeof Reflect == "function" ? Reflect.getPrototypeOf : Object.getPrototypeOf) || ([].__proto__ === Array.prototype ? function(s) {
  return s.__proto__;
} : null);
function NT(s, e) {
  if (s === 1 / 0 || s === -1 / 0 || s !== s || s && s > -1e3 && s < 1e3 || AO.call(/e/, e))
    return e;
  var t = /[0-9](?=(?:[0-9]{3})+(?![0-9]))/g;
  if (typeof s == "number") {
    var i = s < 0 ? -IT(-s) : IT(s);
    if (i !== s) {
      var n = String(i), r = DS.call(e, n.length + 1);
      return Tc.call(n, t, "$&_") + "." + Tc.call(Tc.call(r, /([0-9]{3})/g, "$&_"), /_$/, "");
    }
  }
  return Tc.call(e, t, "$&_");
}
var ub = oJ, BT = ub.custom, FT = wO(BT) ? BT : null, MO = {
  __proto__: null,
  double: '"',
  single: "'"
}, vJ = {
  __proto__: null,
  double: /(["\\])/g,
  single: /(['\\])/g
}, W4 = function s(e, t, i, n) {
  var r = t || {};
  if (X0(r, "quoteStyle") && !X0(MO, r.quoteStyle))
    throw new TypeError('option "quoteStyle" must be "single" or "double"');
  if (X0(r, "maxStringLength") && (typeof r.maxStringLength == "number" ? r.maxStringLength < 0 && r.maxStringLength !== 1 / 0 : r.maxStringLength !== null))
    throw new TypeError('option "maxStringLength", if provided, must be a positive integer, Infinity, or `null`');
  var a = X0(r, "customInspect") ? r.customInspect : !0;
  if (typeof a != "boolean" && a !== "symbol")
    throw new TypeError("option \"customInspect\", if provided, must be `true`, `false`, or `'symbol'`");
  if (X0(r, "indent") && r.indent !== null && r.indent !== "	" && !(parseInt(r.indent, 10) === r.indent && r.indent > 0))
    throw new TypeError('option "indent" must be "\\t", an integer > 0, or `null`');
  if (X0(r, "numericSeparator") && typeof r.numericSeparator != "boolean")
    throw new TypeError('option "numericSeparator", if provided, must be `true` or `false`');
  var o = r.numericSeparator;
  if (typeof e > "u")
    return "undefined";
  if (e === null)
    return "null";
  if (typeof e == "boolean")
    return e ? "true" : "false";
  if (typeof e == "string")
    return TO(e, r);
  if (typeof e == "number") {
    if (e === 0)
      return 1 / 0 / e > 0 ? "0" : "-0";
    var l = String(e);
    return o ? NT(e, l) : l;
  }
  if (typeof e == "bigint") {
    var c = String(e) + "n";
    return o ? NT(e, c) : c;
  }
  var h = typeof r.depth > "u" ? 5 : r.depth;
  if (typeof i > "u" && (i = 0), i >= h && h > 0 && typeof e == "object")
    return db(e) ? "[Array]" : "[Object]";
  var u = NJ(r, i);
  if (typeof n > "u")
    n = [];
  else if (EO(n, e) >= 0)
    return "[Circular]";
  function d(I, N, H) {
    if (N && (n = gJ.call(n), n.push(N)), H) {
      var U = {
        depth: r.depth
      };
      return X0(r, "quoteStyle") && (U.quoteStyle = r.quoteStyle), s(I, U, i + 1, n);
    }
    return s(I, r, i + 1, n);
  }
  if (typeof e == "function" && !UT(e)) {
    var f = TJ(e), p = Hv(e, d);
    return "[Function" + (f ? ": " + f : " (anonymous)") + "]" + (p.length > 0 ? " { " + No.call(p, ", ") + " }" : "");
  }
  if (wO(e)) {
    var v = op ? Tc.call(String(e), /^(Symbol\(.*\))_[^)]*$/, "$1") : hb.call(e);
    return typeof e == "object" && !op ? bm(v) : v;
  }
  if (LJ(e)) {
    for (var g = "<" + DT.call(String(e.nodeName)), m = e.attributes || [], x = 0; x < m.length; x++)
      g += " " + m[x].name + "=" + SO(yJ(m[x].value), "double", r);
    return g += ">", e.childNodes && e.childNodes.length && (g += "..."), g += "</" + DT.call(String(e.nodeName)) + ">", g;
  }
  if (db(e)) {
    if (e.length === 0)
      return "[]";
    var y = Hv(e, d);
    return u && !OJ(y) ? "[" + fb(y, u) + "]" : "[ " + No.call(y, ", ") + " ]";
  }
  if (AJ(e)) {
    var A = Hv(e, d);
    return !("cause" in Error.prototype) && "cause" in e && !bO.call(e, "cause") ? "{ [" + String(e) + "] " + No.call(LT.call("[cause]: " + d(e.cause), A), ", ") + " }" : A.length === 0 ? "[" + String(e) + "]" : "{ [" + String(e) + "] " + No.call(A, ", ") + " }";
  }
  if (typeof e == "object" && a) {
    if (FT && typeof e[FT] == "function" && ub)
      return ub(e, { depth: h - i });
    if (a !== "symbol" && typeof e.inspect == "function")
      return e.inspect();
  }
  if (CJ(e)) {
    var b = [];
    return _T && _T.call(e, function(I, N) {
      b.push(d(N, e, !0) + " => " + d(I, e));
    }), zT("Map", tx.call(e), b, u);
  }
  if (RJ(e)) {
    var M = [];
    return PT && PT.call(e, function(I) {
      M.push(d(I, e));
    }), zT("Set", ix.call(e), M, u);
  }
  if (_J(e))
    return p5("WeakMap");
  if (DJ(e))
    return p5("WeakSet");
  if (PJ(e))
    return p5("WeakRef");
  if (MJ(e))
    return bm(d(Number(e)));
  if (wJ(e))
    return bm(d(cb.call(e)));
  if (SJ(e))
    return bm(uJ.call(e));
  if (bJ(e))
    return bm(d(String(e)));
  if (typeof window < "u" && e === window)
    return "{ [object Window] }";
  if (typeof globalThis < "u" && e === globalThis || typeof Sc < "u" && e === Sc)
    return "{ [object globalThis] }";
  if (!xJ(e) && !UT(e)) {
    var w = Hv(e, d), C = OT ? OT(e) === Object.prototype : e instanceof Object || e.constructor === Object, T = e instanceof Object ? "" : "null prototype", E = !C && R1 && Object(e) === e && R1 in e ? DS.call(lh(e), 8, -1) : T ? "Object" : "", P = C || typeof e.constructor != "function" ? "" : e.constructor.name ? e.constructor.name + " " : "", L = P + (E || T ? "[" + No.call(LT.call([], E || [], T || []), ": ") + "] " : "");
    return w.length === 0 ? L + "{}" : u ? L + "{" + fb(w, u) + "}" : L + "{ " + No.call(w, ", ") + " }";
  }
  return String(e);
};
function SO(s, e, t) {
  var i = t.quoteStyle || e, n = MO[i];
  return n + s + n;
}
function yJ(s) {
  return Tc.call(String(s), /"/g, "&quot;");
}
function $u(s) {
  return !R1 || !(typeof s == "object" && (R1 in s || typeof s[R1] < "u"));
}
function db(s) {
  return lh(s) === "[object Array]" && $u(s);
}
function xJ(s) {
  return lh(s) === "[object Date]" && $u(s);
}
function UT(s) {
  return lh(s) === "[object RegExp]" && $u(s);
}
function AJ(s) {
  return lh(s) === "[object Error]" && $u(s);
}
function bJ(s) {
  return lh(s) === "[object String]" && $u(s);
}
function MJ(s) {
  return lh(s) === "[object Number]" && $u(s);
}
function SJ(s) {
  return lh(s) === "[object Boolean]" && $u(s);
}
function wO(s) {
  if (op)
    return s && typeof s == "object" && s instanceof Symbol;
  if (typeof s == "symbol")
    return !0;
  if (!s || typeof s != "object" || !hb)
    return !1;
  try {
    return hb.call(s), !0;
  } catch {
  }
  return !1;
}
function wJ(s) {
  if (!s || typeof s != "object" || !cb)
    return !1;
  try {
    return cb.call(s), !0;
  } catch {
  }
  return !1;
}
var EJ = Object.prototype.hasOwnProperty || function(s) {
  return s in this;
};
function X0(s, e) {
  return EJ.call(s, e);
}
function lh(s) {
  return dJ.call(s);
}
function TJ(s) {
  if (s.name)
    return s.name;
  var e = pJ.call(fJ.call(s), /^function\s*([\w$]+)/);
  return e ? e[1] : null;
}
function EO(s, e) {
  if (s.indexOf)
    return s.indexOf(e);
  for (var t = 0, i = s.length; t < i; t++)
    if (s[t] === e)
      return t;
  return -1;
}
function CJ(s) {
  if (!tx || !s || typeof s != "object")
    return !1;
  try {
    tx.call(s);
    try {
      ix.call(s);
    } catch {
      return !0;
    }
    return s instanceof Map;
  } catch {
  }
  return !1;
}
function _J(s) {
  if (!_1 || !s || typeof s != "object")
    return !1;
  try {
    _1.call(s, _1);
    try {
      P1.call(s, P1);
    } catch {
      return !0;
    }
    return s instanceof WeakMap;
  } catch {
  }
  return !1;
}
function PJ(s) {
  if (!RT || !s || typeof s != "object")
    return !1;
  try {
    return RT.call(s), !0;
  } catch {
  }
  return !1;
}
function RJ(s) {
  if (!ix || !s || typeof s != "object")
    return !1;
  try {
    ix.call(s);
    try {
      tx.call(s);
    } catch {
      return !0;
    }
    return s instanceof Set;
  } catch {
  }
  return !1;
}
function DJ(s) {
  if (!P1 || !s || typeof s != "object")
    return !1;
  try {
    P1.call(s, P1);
    try {
      _1.call(s, _1);
    } catch {
      return !0;
    }
    return s instanceof WeakSet;
  } catch {
  }
  return !1;
}
function LJ(s) {
  return !s || typeof s != "object" ? !1 : typeof HTMLElement < "u" && s instanceof HTMLElement ? !0 : typeof s.nodeName == "string" && typeof s.getAttribute == "function";
}
function TO(s, e) {
  if (s.length > e.maxStringLength) {
    var t = s.length - e.maxStringLength, i = "... " + t + " more character" + (t > 1 ? "s" : "");
    return TO(DS.call(s, 0, e.maxStringLength), e) + i;
  }
  var n = vJ[e.quoteStyle || "single"];
  n.lastIndex = 0;
  var r = Tc.call(Tc.call(s, n, "\\$1"), /[\x00-\x1f]/g, IJ);
  return SO(r, "single", e);
}
function IJ(s) {
  var e = s.charCodeAt(0), t = {
    8: "b",
    9: "t",
    10: "n",
    12: "f",
    13: "r"
  }[e];
  return t ? "\\" + t : "\\x" + (e < 16 ? "0" : "") + mJ.call(e.toString(16));
}
function bm(s) {
  return "Object(" + s + ")";
}
function p5(s) {
  return s + " { ? }";
}
function zT(s, e, t, i) {
  var n = i ? fb(t, i) : No.call(t, ", ");
  return s + " (" + e + ") {" + n + "}";
}
function OJ(s) {
  for (var e = 0; e < s.length; e++)
    if (EO(s[e], `
`) >= 0)
      return !1;
  return !0;
}
function NJ(s, e) {
  var t;
  if (s.indent === "	")
    t = "	";
  else if (typeof s.indent == "number" && s.indent > 0)
    t = No.call(Array(s.indent + 1), " ");
  else
    return null;
  return {
    base: t,
    prev: No.call(Array(e + 1), t)
  };
}
function fb(s, e) {
  if (s.length === 0)
    return "";
  var t = `
` + e.prev + e.base;
  return t + No.call(s, "," + t) + `
` + e.prev;
}
function Hv(s, e) {
  var t = db(s), i = [];
  if (t) {
    i.length = s.length;
    for (var n = 0; n < s.length; n++)
      i[n] = X0(s, n) ? e(s[n], s) : "";
  }
  var r = typeof f5 == "function" ? f5(s) : [], a;
  if (op) {
    a = {};
    for (var o = 0; o < r.length; o++)
      a["$" + r[o]] = r[o];
  }
  for (var l in s)
    X0(s, l) && (t && String(Number(l)) === l && l < s.length || op && a["$" + l] instanceof Symbol || (AO.call(/[^\w$]/, l) ? i.push(e(l, s) + ": " + e(s[l], s)) : i.push(l + ": " + e(s[l], s))));
  if (typeof f5 == "function")
    for (var c = 0; c < r.length; c++)
      bO.call(s, r[c]) && i.push("[" + e(r[c]) + "]: " + e(s[r[c]], s));
  return i;
}
var BJ = W4, FJ = Jp, X4 = function(s, e, t) {
  for (var i = s, n; (n = i.next) != null; i = n)
    if (n.key === e)
      return i.next = n.next, t || (n.next = /** @type {NonNullable<typeof list.next>} */
      s.next, s.next = n), n;
}, UJ = function(s, e) {
  if (s) {
    var t = X4(s, e);
    return t && t.value;
  }
}, zJ = function(s, e, t) {
  var i = X4(s, e);
  i ? i.value = t : s.next = /** @type {import('./list.d.ts').ListNode<typeof value, typeof key>} */
  {
    // eslint-disable-line no-param-reassign, no-extra-parens
    key: e,
    next: s.next,
    value: t
  };
}, kJ = function(s, e) {
  return s ? !!X4(s, e) : !1;
}, GJ = function(s, e) {
  if (s)
    return X4(s, e, !0);
}, HJ = function() {
  var e, t = {
    assert: function(i) {
      if (!t.has(i))
        throw new FJ("Side channel does not contain " + BJ(i));
    },
    delete: function(i) {
      var n = e && e.next, r = GJ(e, i);
      return r && n && n === r && (e = void 0), !!r;
    },
    get: function(i) {
      return UJ(e, i);
    },
    has: function(i) {
      return kJ(e, i);
    },
    set: function(i, n) {
      e || (e = {
        next: void 0
      }), zJ(
        /** @type {NonNullable<typeof $o>} */
        e,
        i,
        n
      );
    }
  };
  return t;
}, CO = Object, VJ = Error, WJ = EvalError, XJ = RangeError, jJ = ReferenceError, qJ = SyntaxError, YJ = URIError, ZJ = Math.abs, KJ = Math.floor, JJ = Math.max, QJ = Math.min, $J = Math.pow, eQ = Math.round, tQ = Number.isNaN || function(e) {
  return e !== e;
}, iQ = tQ, nQ = function(e) {
  return iQ(e) || e === 0 ? e : e < 0 ? -1 : 1;
}, sQ = Object.getOwnPropertyDescriptor, Ry = sQ;
if (Ry)
  try {
    Ry([], "length");
  } catch {
    Ry = null;
  }
var _O = Ry, Dy = Object.defineProperty || !1;
if (Dy)
  try {
    Dy({}, "a", { value: 1 });
  } catch {
    Dy = !1;
  }
var rQ = Dy, m5, kT;
function aQ() {
  return kT || (kT = 1, m5 = function() {
    if (typeof Symbol != "function" || typeof Object.getOwnPropertySymbols != "function")
      return !1;
    if (typeof Symbol.iterator == "symbol")
      return !0;
    var e = {}, t = Symbol("test"), i = Object(t);
    if (typeof t == "string" || Object.prototype.toString.call(t) !== "[object Symbol]" || Object.prototype.toString.call(i) !== "[object Symbol]")
      return !1;
    var n = 42;
    e[t] = n;
    for (var r in e)
      return !1;
    if (typeof Object.keys == "function" && Object.keys(e).length !== 0 || typeof Object.getOwnPropertyNames == "function" && Object.getOwnPropertyNames(e).length !== 0)
      return !1;
    var a = Object.getOwnPropertySymbols(e);
    if (a.length !== 1 || a[0] !== t || !Object.prototype.propertyIsEnumerable.call(e, t))
      return !1;
    if (typeof Object.getOwnPropertyDescriptor == "function") {
      var o = (
        /** @type {PropertyDescriptor} */
        Object.getOwnPropertyDescriptor(e, t)
      );
      if (o.value !== n || o.enumerable !== !0)
        return !1;
    }
    return !0;
  }), m5;
}
var g5, GT;
function oQ() {
  if (GT)
    return g5;
  GT = 1;
  var s = typeof Symbol < "u" && Symbol, e = aQ();
  return g5 = function() {
    return typeof s != "function" || typeof Symbol != "function" || typeof s("foo") != "symbol" || typeof Symbol("bar") != "symbol" ? !1 : e();
  }, g5;
}
var v5, HT;
function PO() {
  return HT || (HT = 1, v5 = typeof Reflect < "u" && Reflect.getPrototypeOf || null), v5;
}
var y5, VT;
function RO() {
  if (VT)
    return y5;
  VT = 1;
  var s = CO;
  return y5 = s.getPrototypeOf || null, y5;
}
var x5, WT;
function lQ() {
  if (WT)
    return x5;
  WT = 1;
  var s = "Function.prototype.bind called on incompatible ", e = Object.prototype.toString, t = Math.max, i = "[object Function]", n = function(l, c) {
    for (var h = [], u = 0; u < l.length; u += 1)
      h[u] = l[u];
    for (var d = 0; d < c.length; d += 1)
      h[d + l.length] = c[d];
    return h;
  }, r = function(l, c) {
    for (var h = [], u = c, d = 0; u < l.length; u += 1, d += 1)
      h[d] = l[u];
    return h;
  }, a = function(o, l) {
    for (var c = "", h = 0; h < o.length; h += 1)
      c += o[h], h + 1 < o.length && (c += l);
    return c;
  };
  return x5 = function(l) {
    var c = this;
    if (typeof c != "function" || e.apply(c) !== i)
      throw new TypeError(s + c);
    for (var h = r(arguments, 1), u, d = function() {
      if (this instanceof u) {
        var m = c.apply(
          this,
          n(h, arguments)
        );
        return Object(m) === m ? m : this;
      }
      return c.apply(
        l,
        n(h, arguments)
      );
    }, f = t(0, c.length - h.length), p = [], v = 0; v < f; v++)
      p[v] = "$" + v;
    if (u = Function("binder", "return function (" + a(p, ",") + "){ return binder.apply(this,arguments); }")(d), c.prototype) {
      var g = function() {
      };
      g.prototype = c.prototype, u.prototype = new g(), g.prototype = null;
    }
    return u;
  }, x5;
}
var A5, XT;
function j4() {
  if (XT)
    return A5;
  XT = 1;
  var s = lQ();
  return A5 = Function.prototype.bind || s, A5;
}
var b5, jT;
function LS() {
  return jT || (jT = 1, b5 = Function.prototype.call), b5;
}
var M5, qT;
function DO() {
  return qT || (qT = 1, M5 = Function.prototype.apply), M5;
}
var cQ = typeof Reflect < "u" && Reflect && Reflect.apply, hQ = j4(), uQ = DO(), dQ = LS(), fQ = cQ, pQ = fQ || hQ.call(dQ, uQ), mQ = j4(), gQ = Jp, vQ = LS(), yQ = pQ, LO = function(e) {
  if (e.length < 1 || typeof e[0] != "function")
    throw new gQ("a function is required");
  return yQ(mQ, vQ, e);
}, S5, YT;
function xQ() {
  if (YT)
    return S5;
  YT = 1;
  var s = LO, e = _O, t;
  try {
    t = /** @type {{ __proto__?: typeof Array.prototype }} */
    [].__proto__ === Array.prototype;
  } catch (a) {
    if (!a || typeof a != "object" || !("code" in a) || a.code !== "ERR_PROTO_ACCESS")
      throw a;
  }
  var i = !!t && e && e(
    Object.prototype,
    /** @type {keyof typeof Object.prototype} */
    "__proto__"
  ), n = Object, r = n.getPrototypeOf;
  return S5 = i && typeof i.get == "function" ? s([i.get]) : typeof r == "function" ? (
    /** @type {import('./get')} */
    function(o) {
      return r(o == null ? o : n(o));
    }
  ) : !1, S5;
}
var w5, ZT;
function AQ() {
  if (ZT)
    return w5;
  ZT = 1;
  var s = PO(), e = RO(), t = xQ();
  return w5 = s ? function(n) {
    return s(n);
  } : e ? function(n) {
    if (!n || typeof n != "object" && typeof n != "function")
      throw new TypeError("getProto: not an object");
    return e(n);
  } : t ? function(n) {
    return t(n);
  } : null, w5;
}
var E5, KT;
function bQ() {
  if (KT)
    return E5;
  KT = 1;
  var s = Function.prototype.call, e = Object.prototype.hasOwnProperty, t = j4();
  return E5 = t.call(s, e), E5;
}
var wi, MQ = CO, SQ = VJ, wQ = WJ, EQ = XJ, TQ = jJ, lp = qJ, zf = Jp, CQ = YJ, _Q = ZJ, PQ = KJ, RQ = JJ, DQ = QJ, LQ = $J, IQ = eQ, OQ = nQ, IO = Function, T5 = function(s) {
  try {
    return IO('"use strict"; return (' + s + ").constructor;")();
  } catch {
  }
}, cg = _O, NQ = rQ, C5 = function() {
  throw new zf();
}, BQ = cg ? function() {
  try {
    return arguments.callee, C5;
  } catch {
    try {
      return cg(arguments, "callee").get;
    } catch {
      return C5;
    }
  }
}() : C5, Td = oQ()(), Es = AQ(), FQ = RO(), UQ = PO(), OO = DO(), T2 = LS(), mf = {}, zQ = typeof Uint8Array > "u" || !Es ? wi : Es(Uint8Array), wu = {
  __proto__: null,
  "%AggregateError%": typeof AggregateError > "u" ? wi : AggregateError,
  "%Array%": Array,
  "%ArrayBuffer%": typeof ArrayBuffer > "u" ? wi : ArrayBuffer,
  "%ArrayIteratorPrototype%": Td && Es ? Es([][Symbol.iterator]()) : wi,
  "%AsyncFromSyncIteratorPrototype%": wi,
  "%AsyncFunction%": mf,
  "%AsyncGenerator%": mf,
  "%AsyncGeneratorFunction%": mf,
  "%AsyncIteratorPrototype%": mf,
  "%Atomics%": typeof Atomics > "u" ? wi : Atomics,
  "%BigInt%": typeof BigInt > "u" ? wi : BigInt,
  "%BigInt64Array%": typeof BigInt64Array > "u" ? wi : BigInt64Array,
  "%BigUint64Array%": typeof BigUint64Array > "u" ? wi : BigUint64Array,
  "%Boolean%": Boolean,
  "%DataView%": typeof DataView > "u" ? wi : DataView,
  "%Date%": Date,
  "%decodeURI%": decodeURI,
  "%decodeURIComponent%": decodeURIComponent,
  "%encodeURI%": encodeURI,
  "%encodeURIComponent%": encodeURIComponent,
  "%Error%": SQ,
  "%eval%": eval,
  // eslint-disable-line no-eval
  "%EvalError%": wQ,
  "%Float16Array%": typeof Float16Array > "u" ? wi : Float16Array,
  "%Float32Array%": typeof Float32Array > "u" ? wi : Float32Array,
  "%Float64Array%": typeof Float64Array > "u" ? wi : Float64Array,
  "%FinalizationRegistry%": typeof FinalizationRegistry > "u" ? wi : FinalizationRegistry,
  "%Function%": IO,
  "%GeneratorFunction%": mf,
  "%Int8Array%": typeof Int8Array > "u" ? wi : Int8Array,
  "%Int16Array%": typeof Int16Array > "u" ? wi : Int16Array,
  "%Int32Array%": typeof Int32Array > "u" ? wi : Int32Array,
  "%isFinite%": isFinite,
  "%isNaN%": isNaN,
  "%IteratorPrototype%": Td && Es ? Es(Es([][Symbol.iterator]())) : wi,
  "%JSON%": typeof JSON == "object" ? JSON : wi,
  "%Map%": typeof Map > "u" ? wi : Map,
  "%MapIteratorPrototype%": typeof Map > "u" || !Td || !Es ? wi : Es((/* @__PURE__ */ new Map())[Symbol.iterator]()),
  "%Math%": Math,
  "%Number%": Number,
  "%Object%": MQ,
  "%Object.getOwnPropertyDescriptor%": cg,
  "%parseFloat%": parseFloat,
  "%parseInt%": parseInt,
  "%Promise%": typeof Promise > "u" ? wi : Promise,
  "%Proxy%": typeof Proxy > "u" ? wi : Proxy,
  "%RangeError%": EQ,
  "%ReferenceError%": TQ,
  "%Reflect%": typeof Reflect > "u" ? wi : Reflect,
  "%RegExp%": RegExp,
  "%Set%": typeof Set > "u" ? wi : Set,
  "%SetIteratorPrototype%": typeof Set > "u" || !Td || !Es ? wi : Es((/* @__PURE__ */ new Set())[Symbol.iterator]()),
  "%SharedArrayBuffer%": typeof SharedArrayBuffer > "u" ? wi : SharedArrayBuffer,
  "%String%": String,
  "%StringIteratorPrototype%": Td && Es ? Es(""[Symbol.iterator]()) : wi,
  "%Symbol%": Td ? Symbol : wi,
  "%SyntaxError%": lp,
  "%ThrowTypeError%": BQ,
  "%TypedArray%": zQ,
  "%TypeError%": zf,
  "%Uint8Array%": typeof Uint8Array > "u" ? wi : Uint8Array,
  "%Uint8ClampedArray%": typeof Uint8ClampedArray > "u" ? wi : Uint8ClampedArray,
  "%Uint16Array%": typeof Uint16Array > "u" ? wi : Uint16Array,
  "%Uint32Array%": typeof Uint32Array > "u" ? wi : Uint32Array,
  "%URIError%": CQ,
  "%WeakMap%": typeof WeakMap > "u" ? wi : WeakMap,
  "%WeakRef%": typeof WeakRef > "u" ? wi : WeakRef,
  "%WeakSet%": typeof WeakSet > "u" ? wi : WeakSet,
  "%Function.prototype.call%": T2,
  "%Function.prototype.apply%": OO,
  "%Object.defineProperty%": NQ,
  "%Object.getPrototypeOf%": FQ,
  "%Math.abs%": _Q,
  "%Math.floor%": PQ,
  "%Math.max%": RQ,
  "%Math.min%": DQ,
  "%Math.pow%": LQ,
  "%Math.round%": IQ,
  "%Math.sign%": OQ,
  "%Reflect.getPrototypeOf%": UQ
};
if (Es)
  try {
    null.error;
  } catch (s) {
    var kQ = Es(Es(s));
    wu["%Error.prototype%"] = kQ;
  }
var GQ = function s(e) {
  var t;
  if (e === "%AsyncFunction%")
    t = T5("async function () {}");
  else if (e === "%GeneratorFunction%")
    t = T5("function* () {}");
  else if (e === "%AsyncGeneratorFunction%")
    t = T5("async function* () {}");
  else if (e === "%AsyncGenerator%") {
    var i = s("%AsyncGeneratorFunction%");
    i && (t = i.prototype);
  } else if (e === "%AsyncIteratorPrototype%") {
    var n = s("%AsyncGenerator%");
    n && Es && (t = Es(n.prototype));
  }
  return wu[e] = t, t;
}, JT = {
  __proto__: null,
  "%ArrayBufferPrototype%": ["ArrayBuffer", "prototype"],
  "%ArrayPrototype%": ["Array", "prototype"],
  "%ArrayProto_entries%": ["Array", "prototype", "entries"],
  "%ArrayProto_forEach%": ["Array", "prototype", "forEach"],
  "%ArrayProto_keys%": ["Array", "prototype", "keys"],
  "%ArrayProto_values%": ["Array", "prototype", "values"],
  "%AsyncFunctionPrototype%": ["AsyncFunction", "prototype"],
  "%AsyncGenerator%": ["AsyncGeneratorFunction", "prototype"],
  "%AsyncGeneratorPrototype%": ["AsyncGeneratorFunction", "prototype", "prototype"],
  "%BooleanPrototype%": ["Boolean", "prototype"],
  "%DataViewPrototype%": ["DataView", "prototype"],
  "%DatePrototype%": ["Date", "prototype"],
  "%ErrorPrototype%": ["Error", "prototype"],
  "%EvalErrorPrototype%": ["EvalError", "prototype"],
  "%Float32ArrayPrototype%": ["Float32Array", "prototype"],
  "%Float64ArrayPrototype%": ["Float64Array", "prototype"],
  "%FunctionPrototype%": ["Function", "prototype"],
  "%Generator%": ["GeneratorFunction", "prototype"],
  "%GeneratorPrototype%": ["GeneratorFunction", "prototype", "prototype"],
  "%Int8ArrayPrototype%": ["Int8Array", "prototype"],
  "%Int16ArrayPrototype%": ["Int16Array", "prototype"],
  "%Int32ArrayPrototype%": ["Int32Array", "prototype"],
  "%JSONParse%": ["JSON", "parse"],
  "%JSONStringify%": ["JSON", "stringify"],
  "%MapPrototype%": ["Map", "prototype"],
  "%NumberPrototype%": ["Number", "prototype"],
  "%ObjectPrototype%": ["Object", "prototype"],
  "%ObjProto_toString%": ["Object", "prototype", "toString"],
  "%ObjProto_valueOf%": ["Object", "prototype", "valueOf"],
  "%PromisePrototype%": ["Promise", "prototype"],
  "%PromiseProto_then%": ["Promise", "prototype", "then"],
  "%Promise_all%": ["Promise", "all"],
  "%Promise_reject%": ["Promise", "reject"],
  "%Promise_resolve%": ["Promise", "resolve"],
  "%RangeErrorPrototype%": ["RangeError", "prototype"],
  "%ReferenceErrorPrototype%": ["ReferenceError", "prototype"],
  "%RegExpPrototype%": ["RegExp", "prototype"],
  "%SetPrototype%": ["Set", "prototype"],
  "%SharedArrayBufferPrototype%": ["SharedArrayBuffer", "prototype"],
  "%StringPrototype%": ["String", "prototype"],
  "%SymbolPrototype%": ["Symbol", "prototype"],
  "%SyntaxErrorPrototype%": ["SyntaxError", "prototype"],
  "%TypedArrayPrototype%": ["TypedArray", "prototype"],
  "%TypeErrorPrototype%": ["TypeError", "prototype"],
  "%Uint8ArrayPrototype%": ["Uint8Array", "prototype"],
  "%Uint8ClampedArrayPrototype%": ["Uint8ClampedArray", "prototype"],
  "%Uint16ArrayPrototype%": ["Uint16Array", "prototype"],
  "%Uint32ArrayPrototype%": ["Uint32Array", "prototype"],
  "%URIErrorPrototype%": ["URIError", "prototype"],
  "%WeakMapPrototype%": ["WeakMap", "prototype"],
  "%WeakSetPrototype%": ["WeakSet", "prototype"]
}, C2 = j4(), nx = bQ(), HQ = C2.call(T2, Array.prototype.concat), VQ = C2.call(OO, Array.prototype.splice), QT = C2.call(T2, String.prototype.replace), sx = C2.call(T2, String.prototype.slice), WQ = C2.call(T2, RegExp.prototype.exec), XQ = /[^%.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|%$))/g, jQ = /\\(\\)?/g, qQ = function(e) {
  var t = sx(e, 0, 1), i = sx(e, -1);
  if (t === "%" && i !== "%")
    throw new lp("invalid intrinsic syntax, expected closing `%`");
  if (i === "%" && t !== "%")
    throw new lp("invalid intrinsic syntax, expected opening `%`");
  var n = [];
  return QT(e, XQ, function(r, a, o, l) {
    n[n.length] = o ? QT(l, jQ, "$1") : a || r;
  }), n;
}, YQ = function(e, t) {
  var i = e, n;
  if (nx(JT, i) && (n = JT[i], i = "%" + n[0] + "%"), nx(wu, i)) {
    var r = wu[i];
    if (r === mf && (r = GQ(i)), typeof r > "u" && !t)
      throw new zf("intrinsic " + e + " exists, but is not available. Please file an issue!");
    return {
      alias: n,
      name: i,
      value: r
    };
  }
  throw new lp("intrinsic " + e + " does not exist!");
}, IS = function(e, t) {
  if (typeof e != "string" || e.length === 0)
    throw new zf("intrinsic name must be a non-empty string");
  if (arguments.length > 1 && typeof t != "boolean")
    throw new zf('"allowMissing" argument must be a boolean');
  if (WQ(/^%?[^%]*%?$/, e) === null)
    throw new lp("`%` may not be present anywhere but at the beginning and end of the intrinsic name");
  var i = qQ(e), n = i.length > 0 ? i[0] : "", r = YQ("%" + n + "%", t), a = r.name, o = r.value, l = !1, c = r.alias;
  c && (n = c[0], VQ(i, HQ([0, 1], c)));
  for (var h = 1, u = !0; h < i.length; h += 1) {
    var d = i[h], f = sx(d, 0, 1), p = sx(d, -1);
    if ((f === '"' || f === "'" || f === "`" || p === '"' || p === "'" || p === "`") && f !== p)
      throw new lp("property names with quotes must have matching quotes");
    if ((d === "constructor" || !u) && (l = !0), n += "." + d, a = "%" + n + "%", nx(wu, a))
      o = wu[a];
    else if (o != null) {
      if (!(d in o)) {
        if (!t)
          throw new zf("base intrinsic for " + e + " exists, but the property is not available.");
        return;
      }
      if (cg && h + 1 >= i.length) {
        var v = cg(o, d);
        u = !!v, u && "get" in v && !("originalValue" in v.get) ? o = v.get : o = o[d];
      } else
        u = nx(o, d), o = o[d];
      u && !l && (wu[a] = o);
    }
  }
  return o;
}, NO = IS, BO = LO, ZQ = BO([NO("%String.prototype.indexOf%")]), FO = function(e, t) {
  var i = (
    /** @type {(this: unknown, ...args: unknown[]) => unknown} */
    NO(e, !!t)
  );
  return typeof i == "function" && ZQ(e, ".prototype.") > -1 ? BO(
    /** @type {const} */
    [i]
  ) : i;
}, KQ = IS, _2 = FO, JQ = W4, QQ = Jp, $T = KQ("%Map%", !0), $Q = _2("Map.prototype.get", !0), e$ = _2("Map.prototype.set", !0), t$ = _2("Map.prototype.has", !0), i$ = _2("Map.prototype.delete", !0), n$ = _2("Map.prototype.size", !0), UO = !!$T && /** @type {Exclude<import('.'), false>} */
function() {
  var e, t = {
    assert: function(i) {
      if (!t.has(i))
        throw new QQ("Side channel does not contain " + JQ(i));
    },
    delete: function(i) {
      if (e) {
        var n = i$(e, i);
        return n$(e) === 0 && (e = void 0), n;
      }
      return !1;
    },
    get: function(i) {
      if (e)
        return $Q(e, i);
    },
    has: function(i) {
      return e ? t$(e, i) : !1;
    },
    set: function(i, n) {
      e || (e = new $T()), e$(e, i, n);
    }
  };
  return t;
}, s$ = IS, q4 = FO, r$ = W4, Vv = UO, a$ = Jp, Cd = s$("%WeakMap%", !0), o$ = q4("WeakMap.prototype.get", !0), l$ = q4("WeakMap.prototype.set", !0), c$ = q4("WeakMap.prototype.has", !0), h$ = q4("WeakMap.prototype.delete", !0), u$ = Cd ? (
  /** @type {Exclude<import('.'), false>} */
  function() {
    var e, t, i = {
      assert: function(n) {
        if (!i.has(n))
          throw new a$("Side channel does not contain " + r$(n));
      },
      delete: function(n) {
        if (Cd && n && (typeof n == "object" || typeof n == "function")) {
          if (e)
            return h$(e, n);
        } else if (Vv && t)
          return t.delete(n);
        return !1;
      },
      get: function(n) {
        return Cd && n && (typeof n == "object" || typeof n == "function") && e ? o$(e, n) : t && t.get(n);
      },
      has: function(n) {
        return Cd && n && (typeof n == "object" || typeof n == "function") && e ? c$(e, n) : !!t && t.has(n);
      },
      set: function(n, r) {
        Cd && n && (typeof n == "object" || typeof n == "function") ? (e || (e = new Cd()), l$(e, n, r)) : Vv && (t || (t = Vv()), t.set(n, r));
      }
    };
    return i;
  }
) : Vv, d$ = Jp, f$ = W4, p$ = HJ, m$ = UO, g$ = u$, v$ = g$ || m$ || p$, y$ = function() {
  var e, t = {
    assert: function(i) {
      if (!t.has(i))
        throw new d$("Side channel does not contain " + f$(i));
    },
    delete: function(i) {
      return !!e && e.delete(i);
    },
    get: function(i) {
      return e && e.get(i);
    },
    has: function(i) {
      return !!e && e.has(i);
    },
    set: function(i, n) {
      e || (e = v$()), e.set(i, n);
    }
  };
  return t;
}, x$ = String.prototype.replace, A$ = /%20/g, _5 = {
  RFC1738: "RFC1738",
  RFC3986: "RFC3986"
}, OS = {
  default: _5.RFC3986,
  formatters: {
    RFC1738: function(s) {
      return x$.call(s, A$, "+");
    },
    RFC3986: function(s) {
      return String(s);
    }
  },
  RFC1738: _5.RFC1738,
  RFC3986: _5.RFC3986
}, b$ = OS, P5 = Object.prototype.hasOwnProperty, su = Array.isArray, bo = function() {
  for (var s = [], e = 0; e < 256; ++e)
    s.push("%" + ((e < 16 ? "0" : "") + e.toString(16)).toUpperCase());
  return s;
}(), M$ = function(e) {
  for (; e.length > 1; ) {
    var t = e.pop(), i = t.obj[t.prop];
    if (su(i)) {
      for (var n = [], r = 0; r < i.length; ++r)
        typeof i[r] < "u" && n.push(i[r]);
      t.obj[t.prop] = n;
    }
  }
}, zO = function(e, t) {
  for (var i = t && t.plainObjects ? { __proto__: null } : {}, n = 0; n < e.length; ++n)
    typeof e[n] < "u" && (i[n] = e[n]);
  return i;
}, S$ = function s(e, t, i) {
  if (!t)
    return e;
  if (typeof t != "object" && typeof t != "function") {
    if (su(e))
      e.push(t);
    else if (e && typeof e == "object")
      (i && (i.plainObjects || i.allowPrototypes) || !P5.call(Object.prototype, t)) && (e[t] = !0);
    else
      return [e, t];
    return e;
  }
  if (!e || typeof e != "object")
    return [e].concat(t);
  var n = e;
  return su(e) && !su(t) && (n = zO(e, i)), su(e) && su(t) ? (t.forEach(function(r, a) {
    if (P5.call(e, a)) {
      var o = e[a];
      o && typeof o == "object" && r && typeof r == "object" ? e[a] = s(o, r, i) : e.push(r);
    } else
      e[a] = r;
  }), e) : Object.keys(t).reduce(function(r, a) {
    var o = t[a];
    return P5.call(r, a) ? r[a] = s(r[a], o, i) : r[a] = o, r;
  }, n);
}, w$ = function(e, t) {
  return Object.keys(t).reduce(function(i, n) {
    return i[n] = t[n], i;
  }, e);
}, E$ = function(s, e, t) {
  var i = s.replace(/\+/g, " ");
  if (t === "iso-8859-1")
    return i.replace(/%[0-9a-f]{2}/gi, unescape);
  try {
    return decodeURIComponent(i);
  } catch {
    return i;
  }
}, R5 = 1024, T$ = function(e, t, i, n, r) {
  if (e.length === 0)
    return e;
  var a = e;
  if (typeof e == "symbol" ? a = Symbol.prototype.toString.call(e) : typeof e != "string" && (a = String(e)), i === "iso-8859-1")
    return escape(a).replace(/%u[0-9a-f]{4}/gi, function(f) {
      return "%26%23" + parseInt(f.slice(2), 16) + "%3B";
    });
  for (var o = "", l = 0; l < a.length; l += R5) {
    for (var c = a.length >= R5 ? a.slice(l, l + R5) : a, h = [], u = 0; u < c.length; ++u) {
      var d = c.charCodeAt(u);
      if (d === 45 || d === 46 || d === 95 || d === 126 || d >= 48 && d <= 57 || d >= 65 && d <= 90 || d >= 97 && d <= 122 || r === b$.RFC1738 && (d === 40 || d === 41)) {
        h[h.length] = c.charAt(u);
        continue;
      }
      if (d < 128) {
        h[h.length] = bo[d];
        continue;
      }
      if (d < 2048) {
        h[h.length] = bo[192 | d >> 6] + bo[128 | d & 63];
        continue;
      }
      if (d < 55296 || d >= 57344) {
        h[h.length] = bo[224 | d >> 12] + bo[128 | d >> 6 & 63] + bo[128 | d & 63];
        continue;
      }
      u += 1, d = 65536 + ((d & 1023) << 10 | c.charCodeAt(u) & 1023), h[h.length] = bo[240 | d >> 18] + bo[128 | d >> 12 & 63] + bo[128 | d >> 6 & 63] + bo[128 | d & 63];
    }
    o += h.join("");
  }
  return o;
}, C$ = function(e) {
  for (var t = [{ obj: { o: e }, prop: "o" }], i = [], n = 0; n < t.length; ++n)
    for (var r = t[n], a = r.obj[r.prop], o = Object.keys(a), l = 0; l < o.length; ++l) {
      var c = o[l], h = a[c];
      typeof h == "object" && h !== null && i.indexOf(h) === -1 && (t.push({ obj: a, prop: c }), i.push(h));
    }
  return M$(t), e;
}, _$ = function(e) {
  return Object.prototype.toString.call(e) === "[object RegExp]";
}, P$ = function(e) {
  return !e || typeof e != "object" ? !1 : !!(e.constructor && e.constructor.isBuffer && e.constructor.isBuffer(e));
}, R$ = function(e, t) {
  return [].concat(e, t);
}, D$ = function(e, t) {
  if (su(e)) {
    for (var i = [], n = 0; n < e.length; n += 1)
      i.push(t(e[n]));
    return i;
  }
  return t(e);
}, kO = {
  arrayToObject: zO,
  assign: w$,
  combine: R$,
  compact: C$,
  decode: E$,
  encode: T$,
  isBuffer: P$,
  isRegExp: _$,
  maybeMap: D$,
  merge: S$
}, GO = y$, Ly = kO, D1 = OS, L$ = Object.prototype.hasOwnProperty, HO = {
  brackets: function(e) {
    return e + "[]";
  },
  comma: "comma",
  indices: function(e, t) {
    return e + "[" + t + "]";
  },
  repeat: function(e) {
    return e;
  }
}, Lo = Array.isArray, I$ = Array.prototype.push, VO = function(s, e) {
  I$.apply(s, Lo(e) ? e : [e]);
}, O$ = Date.prototype.toISOString, eC = D1.default, os = {
  addQueryPrefix: !1,
  allowDots: !1,
  allowEmptyArrays: !1,
  arrayFormat: "indices",
  charset: "utf-8",
  charsetSentinel: !1,
  commaRoundTrip: !1,
  delimiter: "&",
  encode: !0,
  encodeDotInKeys: !1,
  encoder: Ly.encode,
  encodeValuesOnly: !1,
  filter: void 0,
  format: eC,
  formatter: D1.formatters[eC],
  // deprecated
  indices: !1,
  serializeDate: function(e) {
    return O$.call(e);
  },
  skipNulls: !1,
  strictNullHandling: !1
}, N$ = function(e) {
  return typeof e == "string" || typeof e == "number" || typeof e == "boolean" || typeof e == "symbol" || typeof e == "bigint";
}, D5 = {}, B$ = function s(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g, m, x) {
  for (var y = e, A = x, b = 0, M = !1; (A = A.get(D5)) !== void 0 && !M; ) {
    var w = A.get(e);
    if (b += 1, typeof w < "u") {
      if (w === b)
        throw new RangeError("Cyclic object value");
      M = !0;
    }
    typeof A.get(D5) > "u" && (b = 0);
  }
  if (typeof h == "function" ? y = h(t, y) : y instanceof Date ? y = f(y) : i === "comma" && Lo(y) && (y = Ly.maybeMap(y, function(te) {
    return te instanceof Date ? f(te) : te;
  })), y === null) {
    if (a)
      return c && !g ? c(t, os.encoder, m, "key", p) : t;
    y = "";
  }
  if (N$(y) || Ly.isBuffer(y)) {
    if (c) {
      var C = g ? t : c(t, os.encoder, m, "key", p);
      return [v(C) + "=" + v(c(y, os.encoder, m, "value", p))];
    }
    return [v(t) + "=" + v(String(y))];
  }
  var T = [];
  if (typeof y > "u")
    return T;
  var E;
  if (i === "comma" && Lo(y))
    g && c && (y = Ly.maybeMap(y, c)), E = [{ value: y.length > 0 ? y.join(",") || null : void 0 }];
  else if (Lo(h))
    E = h;
  else {
    var P = Object.keys(y);
    E = u ? P.sort(u) : P;
  }
  var L = l ? String(t).replace(/\./g, "%2E") : String(t), I = n && Lo(y) && y.length === 1 ? L + "[]" : L;
  if (r && Lo(y) && y.length === 0)
    return I + "[]";
  for (var N = 0; N < E.length; ++N) {
    var H = E[N], U = typeof H == "object" && H && typeof H.value < "u" ? H.value : y[H];
    if (!(o && U === null)) {
      var W = d && l ? String(H).replace(/\./g, "%2E") : String(H), X = Lo(y) ? typeof i == "function" ? i(I, W) : I : I + (d ? "." + W : "[" + W + "]");
      x.set(e, b);
      var K = GO();
      K.set(D5, x), VO(T, s(
        U,
        X,
        i,
        n,
        r,
        a,
        o,
        l,
        i === "comma" && g && Lo(y) ? null : c,
        h,
        u,
        d,
        f,
        p,
        v,
        g,
        m,
        K
      ));
    }
  }
  return T;
}, F$ = function(e) {
  if (!e)
    return os;
  if (typeof e.allowEmptyArrays < "u" && typeof e.allowEmptyArrays != "boolean")
    throw new TypeError("`allowEmptyArrays` option can only be `true` or `false`, when provided");
  if (typeof e.encodeDotInKeys < "u" && typeof e.encodeDotInKeys != "boolean")
    throw new TypeError("`encodeDotInKeys` option can only be `true` or `false`, when provided");
  if (e.encoder !== null && typeof e.encoder < "u" && typeof e.encoder != "function")
    throw new TypeError("Encoder has to be a function.");
  var t = e.charset || os.charset;
  if (typeof e.charset < "u" && e.charset !== "utf-8" && e.charset !== "iso-8859-1")
    throw new TypeError("The charset option must be either utf-8, iso-8859-1, or undefined");
  var i = D1.default;
  if (typeof e.format < "u") {
    if (!L$.call(D1.formatters, e.format))
      throw new TypeError("Unknown format option provided.");
    i = e.format;
  }
  var n = D1.formatters[i], r = os.filter;
  (typeof e.filter == "function" || Lo(e.filter)) && (r = e.filter);
  var a;
  if (e.arrayFormat in HO ? a = e.arrayFormat : "indices" in e ? a = e.indices ? "indices" : "repeat" : a = os.arrayFormat, "commaRoundTrip" in e && typeof e.commaRoundTrip != "boolean")
    throw new TypeError("`commaRoundTrip` must be a boolean, or absent");
  var o = typeof e.allowDots > "u" ? e.encodeDotInKeys === !0 ? !0 : os.allowDots : !!e.allowDots;
  return {
    addQueryPrefix: typeof e.addQueryPrefix == "boolean" ? e.addQueryPrefix : os.addQueryPrefix,
    allowDots: o,
    allowEmptyArrays: typeof e.allowEmptyArrays == "boolean" ? !!e.allowEmptyArrays : os.allowEmptyArrays,
    arrayFormat: a,
    charset: t,
    charsetSentinel: typeof e.charsetSentinel == "boolean" ? e.charsetSentinel : os.charsetSentinel,
    commaRoundTrip: !!e.commaRoundTrip,
    delimiter: typeof e.delimiter > "u" ? os.delimiter : e.delimiter,
    encode: typeof e.encode == "boolean" ? e.encode : os.encode,
    encodeDotInKeys: typeof e.encodeDotInKeys == "boolean" ? e.encodeDotInKeys : os.encodeDotInKeys,
    encoder: typeof e.encoder == "function" ? e.encoder : os.encoder,
    encodeValuesOnly: typeof e.encodeValuesOnly == "boolean" ? e.encodeValuesOnly : os.encodeValuesOnly,
    filter: r,
    format: i,
    formatter: n,
    serializeDate: typeof e.serializeDate == "function" ? e.serializeDate : os.serializeDate,
    skipNulls: typeof e.skipNulls == "boolean" ? e.skipNulls : os.skipNulls,
    sort: typeof e.sort == "function" ? e.sort : null,
    strictNullHandling: typeof e.strictNullHandling == "boolean" ? e.strictNullHandling : os.strictNullHandling
  };
}, U$ = function(s, e) {
  var t = s, i = F$(e), n, r;
  typeof i.filter == "function" ? (r = i.filter, t = r("", t)) : Lo(i.filter) && (r = i.filter, n = r);
  var a = [];
  if (typeof t != "object" || t === null)
    return "";
  var o = HO[i.arrayFormat], l = o === "comma" && i.commaRoundTrip;
  n || (n = Object.keys(t)), i.sort && n.sort(i.sort);
  for (var c = GO(), h = 0; h < n.length; ++h) {
    var u = n[h], d = t[u];
    i.skipNulls && d === null || VO(a, B$(
      d,
      u,
      o,
      l,
      i.allowEmptyArrays,
      i.strictNullHandling,
      i.skipNulls,
      i.encodeDotInKeys,
      i.encode ? i.encoder : null,
      i.filter,
      i.sort,
      i.allowDots,
      i.serializeDate,
      i.format,
      i.formatter,
      i.encodeValuesOnly,
      i.charset,
      c
    ));
  }
  var f = a.join(i.delimiter), p = i.addQueryPrefix === !0 ? "?" : "";
  return i.charsetSentinel && (i.charset === "iso-8859-1" ? p += "utf8=%26%2310003%3B&" : p += "utf8=%E2%9C%93&"), f.length > 0 ? p + f : "";
}, Du = kO, pb = Object.prototype.hasOwnProperty, tC = Array.isArray, Hn = {
  allowDots: !1,
  allowEmptyArrays: !1,
  allowPrototypes: !1,
  allowSparse: !1,
  arrayLimit: 20,
  charset: "utf-8",
  charsetSentinel: !1,
  comma: !1,
  decodeDotInKeys: !1,
  decoder: Du.decode,
  delimiter: "&",
  depth: 5,
  duplicates: "combine",
  ignoreQueryPrefix: !1,
  interpretNumericEntities: !1,
  parameterLimit: 1e3,
  parseArrays: !0,
  plainObjects: !1,
  strictDepth: !1,
  strictNullHandling: !1,
  throwOnLimitExceeded: !1
}, z$ = function(s) {
  return s.replace(/&#(\d+);/g, function(e, t) {
    return String.fromCharCode(parseInt(t, 10));
  });
}, WO = function(s, e, t) {
  if (s && typeof s == "string" && e.comma && s.indexOf(",") > -1)
    return s.split(",");
  if (e.throwOnLimitExceeded && t >= e.arrayLimit)
    throw new RangeError("Array limit exceeded. Only " + e.arrayLimit + " element" + (e.arrayLimit === 1 ? "" : "s") + " allowed in an array.");
  return s;
}, k$ = "utf8=%26%2310003%3B", G$ = "utf8=%E2%9C%93", H$ = function(e, t) {
  var i = { __proto__: null }, n = t.ignoreQueryPrefix ? e.replace(/^\?/, "") : e;
  n = n.replace(/%5B/gi, "[").replace(/%5D/gi, "]");
  var r = t.parameterLimit === 1 / 0 ? void 0 : t.parameterLimit, a = n.split(
    t.delimiter,
    t.throwOnLimitExceeded ? r + 1 : r
  );
  if (t.throwOnLimitExceeded && a.length > r)
    throw new RangeError("Parameter limit exceeded. Only " + r + " parameter" + (r === 1 ? "" : "s") + " allowed.");
  var o = -1, l, c = t.charset;
  if (t.charsetSentinel)
    for (l = 0; l < a.length; ++l)
      a[l].indexOf("utf8=") === 0 && (a[l] === G$ ? c = "utf-8" : a[l] === k$ && (c = "iso-8859-1"), o = l, l = a.length);
  for (l = 0; l < a.length; ++l)
    if (l !== o) {
      var h = a[l], u = h.indexOf("]="), d = u === -1 ? h.indexOf("=") : u + 1, f, p;
      d === -1 ? (f = t.decoder(h, Hn.decoder, c, "key"), p = t.strictNullHandling ? null : "") : (f = t.decoder(h.slice(0, d), Hn.decoder, c, "key"), p = Du.maybeMap(
        WO(
          h.slice(d + 1),
          t,
          tC(i[f]) ? i[f].length : 0
        ),
        function(g) {
          return t.decoder(g, Hn.decoder, c, "value");
        }
      )), p && t.interpretNumericEntities && c === "iso-8859-1" && (p = z$(String(p))), h.indexOf("[]=") > -1 && (p = tC(p) ? [p] : p);
      var v = pb.call(i, f);
      v && t.duplicates === "combine" ? i[f] = Du.combine(i[f], p) : (!v || t.duplicates === "last") && (i[f] = p);
    }
  return i;
}, V$ = function(s, e, t, i) {
  var n = 0;
  if (s.length > 0 && s[s.length - 1] === "[]") {
    var r = s.slice(0, -1).join("");
    n = Array.isArray(e) && e[r] ? e[r].length : 0;
  }
  for (var a = i ? e : WO(e, t, n), o = s.length - 1; o >= 0; --o) {
    var l, c = s[o];
    if (c === "[]" && t.parseArrays)
      l = t.allowEmptyArrays && (a === "" || t.strictNullHandling && a === null) ? [] : Du.combine([], a);
    else {
      l = t.plainObjects ? { __proto__: null } : {};
      var h = c.charAt(0) === "[" && c.charAt(c.length - 1) === "]" ? c.slice(1, -1) : c, u = t.decodeDotInKeys ? h.replace(/%2E/g, ".") : h, d = parseInt(u, 10);
      !t.parseArrays && u === "" ? l = { 0: a } : !isNaN(d) && c !== u && String(d) === u && d >= 0 && t.parseArrays && d <= t.arrayLimit ? (l = [], l[d] = a) : u !== "__proto__" && (l[u] = a);
    }
    a = l;
  }
  return a;
}, W$ = function(e, t, i, n) {
  if (e) {
    var r = i.allowDots ? e.replace(/\.([^.[]+)/g, "[$1]") : e, a = /(\[[^[\]]*])/, o = /(\[[^[\]]*])/g, l = i.depth > 0 && a.exec(r), c = l ? r.slice(0, l.index) : r, h = [];
    if (c) {
      if (!i.plainObjects && pb.call(Object.prototype, c) && !i.allowPrototypes)
        return;
      h.push(c);
    }
    for (var u = 0; i.depth > 0 && (l = o.exec(r)) !== null && u < i.depth; ) {
      if (u += 1, !i.plainObjects && pb.call(Object.prototype, l[1].slice(1, -1)) && !i.allowPrototypes)
        return;
      h.push(l[1]);
    }
    if (l) {
      if (i.strictDepth === !0)
        throw new RangeError("Input depth exceeded depth option of " + i.depth + " and strictDepth is true");
      h.push("[" + r.slice(l.index) + "]");
    }
    return V$(h, t, i, n);
  }
}, X$ = function(e) {
  if (!e)
    return Hn;
  if (typeof e.allowEmptyArrays < "u" && typeof e.allowEmptyArrays != "boolean")
    throw new TypeError("`allowEmptyArrays` option can only be `true` or `false`, when provided");
  if (typeof e.decodeDotInKeys < "u" && typeof e.decodeDotInKeys != "boolean")
    throw new TypeError("`decodeDotInKeys` option can only be `true` or `false`, when provided");
  if (e.decoder !== null && typeof e.decoder < "u" && typeof e.decoder != "function")
    throw new TypeError("Decoder has to be a function.");
  if (typeof e.charset < "u" && e.charset !== "utf-8" && e.charset !== "iso-8859-1")
    throw new TypeError("The charset option must be either utf-8, iso-8859-1, or undefined");
  if (typeof e.throwOnLimitExceeded < "u" && typeof e.throwOnLimitExceeded != "boolean")
    throw new TypeError("`throwOnLimitExceeded` option must be a boolean");
  var t = typeof e.charset > "u" ? Hn.charset : e.charset, i = typeof e.duplicates > "u" ? Hn.duplicates : e.duplicates;
  if (i !== "combine" && i !== "first" && i !== "last")
    throw new TypeError("The duplicates option must be either combine, first, or last");
  var n = typeof e.allowDots > "u" ? e.decodeDotInKeys === !0 ? !0 : Hn.allowDots : !!e.allowDots;
  return {
    allowDots: n,
    allowEmptyArrays: typeof e.allowEmptyArrays == "boolean" ? !!e.allowEmptyArrays : Hn.allowEmptyArrays,
    allowPrototypes: typeof e.allowPrototypes == "boolean" ? e.allowPrototypes : Hn.allowPrototypes,
    allowSparse: typeof e.allowSparse == "boolean" ? e.allowSparse : Hn.allowSparse,
    arrayLimit: typeof e.arrayLimit == "number" ? e.arrayLimit : Hn.arrayLimit,
    charset: t,
    charsetSentinel: typeof e.charsetSentinel == "boolean" ? e.charsetSentinel : Hn.charsetSentinel,
    comma: typeof e.comma == "boolean" ? e.comma : Hn.comma,
    decodeDotInKeys: typeof e.decodeDotInKeys == "boolean" ? e.decodeDotInKeys : Hn.decodeDotInKeys,
    decoder: typeof e.decoder == "function" ? e.decoder : Hn.decoder,
    delimiter: typeof e.delimiter == "string" || Du.isRegExp(e.delimiter) ? e.delimiter : Hn.delimiter,
    // eslint-disable-next-line no-implicit-coercion, no-extra-parens
    depth: typeof e.depth == "number" || e.depth === !1 ? +e.depth : Hn.depth,
    duplicates: i,
    ignoreQueryPrefix: e.ignoreQueryPrefix === !0,
    interpretNumericEntities: typeof e.interpretNumericEntities == "boolean" ? e.interpretNumericEntities : Hn.interpretNumericEntities,
    parameterLimit: typeof e.parameterLimit == "number" ? e.parameterLimit : Hn.parameterLimit,
    parseArrays: e.parseArrays !== !1,
    plainObjects: typeof e.plainObjects == "boolean" ? e.plainObjects : Hn.plainObjects,
    strictDepth: typeof e.strictDepth == "boolean" ? !!e.strictDepth : Hn.strictDepth,
    strictNullHandling: typeof e.strictNullHandling == "boolean" ? e.strictNullHandling : Hn.strictNullHandling,
    throwOnLimitExceeded: typeof e.throwOnLimitExceeded == "boolean" ? e.throwOnLimitExceeded : !1
  };
}, j$ = function(s, e) {
  var t = X$(e);
  if (s === "" || s === null || typeof s > "u")
    return t.plainObjects ? { __proto__: null } : {};
  for (var i = typeof s == "string" ? H$(s, t) : s, n = t.plainObjects ? { __proto__: null } : {}, r = Object.keys(i), a = 0; a < r.length; ++a) {
    var o = r[a], l = W$(o, i[o], t, typeof s == "string");
    n = Du.merge(n, l, t);
  }
  return t.allowSparse === !0 ? n : Du.compact(n);
}, q$ = U$, Y$ = j$, Z$ = OS, K$ = {
  formats: Z$,
  parse: Y$,
  stringify: q$
};
const L5 = /* @__PURE__ */ fM(K$);
function XO() {
  return Math.random().toString(36).slice(3, 20);
}
function J$(s) {
  switch (s.status) {
    case 500:
      return Promise.reject(new Error("服务器端错误"));
    case 403:
      return Promise.reject(new Error("暂无产品权限"));
    case 418:
    case 401:
      return window.location.href = `/login?return_to=${window.encodeURIComponent(window.location.href)}`, Promise.reject(new Error("用户未登录"));
    case 200:
    default:
      return s.json();
  }
}
function Q$(s) {
  return s != null && s.success ? s.data ? (s.data.success = s.success, s.data.message = s.message, s.data) : s.success : s.code ? Promise.reject(s) : Promise.reject(new Error(s.message));
}
async function zi(s, e, t) {
  let [i, n] = s.split(" ");
  i || (n = i, i = "GET");
  const {
    query: r = {},
    params: a = {},
    headers: o = {},
    hideError: l,
    noSpaceId: c
  } = t || {};
  let { body: h } = t || {};
  Object.keys(a).forEach((p) => {
    n = n.replace(`{${p}}`, a[p]);
  }), L5.stringify(r) && (n += `${n.includes("?") ? "&" : "?"}${L5.stringify(r)}`), !(h instanceof FormData) && typeof h != "string" && (h = JSON.stringify(h));
  const u = {
    "x-request-id": XO()
  }, d = L5.parse(window.location.search.slice(1));
  !c && d.sid && (u["space-id"] = d.sid);
  function f(p) {
    return (p == null ? void 0 : p.name) !== "AbortError" ? Promise.reject(p) : Promise.resolve("");
  }
  return fetch(n, {
    ...t,
    method: i,
    body: h,
    headers: {
      ...u,
      ...h instanceof FormData ? {} : { "content-type": "application/json" },
      ...o
    }
  }).then(J$).then(Q$).catch(f);
}
const Ji = {
  fileGet: "GET /api/twin/v1/file",
  fileUploadPost: "POST /api/twin/v1/file/upload",
  fileMultipartInitPost: "POST /api/twin/v1/file/multipart/init",
  fileMultipartUploadPost: "POST /api/twin/v1/file/multipart/upload",
  fileMultipartCompletePost: "POST /api/twin/v1/file/multipart/complete",
  fileMultipartAbortPost: "POST /api/twin/v1/file/multipart/abort",
  fileModelPacketInitPost: "POST /api/twin/v1/file/model-packet/init",
  fileModelPacketUploadPost: "POST /api/twin/v1/file/model-packet/upload",
  fileModelPacketCompletePost: "POST /api/twin/v1/file/model-packet/complete",
  fileModelPacketAbortPost: "POST /api/twin/v1/file/model-packet/abort",
  fileHandlesGet: "GET /api/twin/v1/file/handles",
  fileHandlesPost: "POST /api/twin/v1/file/handles",
  fileHandlesDelete: "DELETE /api/twin/v1/file/handles",
  assetsGet: "GET /api/twin/v1/assets",
  assetsPost: "POST /api/twin/v1/assets",
  assetsPatch: "PATCH /api/twin/v1/assets",
  assetsFontsGet: "GET /api/twin/v1/assets/fonts",
  assetsAllGet: "GET /api/twin/v1/assets/all",
  assetGet: "GET /api/twin/v1/assets/{id}",
  assetPatch: "PATCH /api/twin/v1/assets/{id}",
  assetDelete: "DELETE /api/twin/v1/assets/{id}",
  assetCategoriesGet: "GET /api/twin/v1/asset-categories",
  assetCategoriesPost: "POST /api/twin/v1/asset-categories",
  assetCategoriePatch: "PATCH /api/twin/v1/asset-categories/{id}",
  assetCategorieDelete: "DELETE /api/twin/v1/asset-categories/{id}",
  projectsGet: "GET /api/twin/v1/projects",
  projectsDelete: "DELETE /api/twin/v1/projects",
  projectsPost: "POST /api/twin/v1/projects",
  projectsCountGet: "GET /api/twin/v1/projects/count",
  projectsVideoProjectsGet: "GET /api/twin/v1/projects/video-projects",
  projectGet: "GET /api/twin/v1/projects/{id}",
  projectPatch: "PATCH /api/twin/v1/projects/{id}",
  projectsRecoverPatch: "PATCH /api/twin/v1/projects/{id}/recover",
  projectsCopyPost: "POST /api/twin/v1/projects/{id}/copy",
  projectsImportPost: "POST /api/twin/v1/projects/import",
  projectsExportGet: "GET /api/twin/v1/projects/{id}/export",
  projectsTemporarySynchronizationCodeGet: "GET /api/twin/v1/projects/{id}/temporary/synchronization-code",
  projectsSynchronizePost: "POST /api/twin/v1/projects/synchronize",
  projectsSynchronizationCodePost: "POST /api/twin/v1/projects/{id}/synchronization-code",
  projectGroupsGet: "GET /api/twin/v1/project-groups",
  projectGroupsPost: "POST /api/twin/v1/project-groups",
  projectGroupsPatch: "PATCH /api/twin/v1/project-groups",
  projectGroupPatch: "PATCH /api/twin/v1/project-groups/{id}",
  projectGroupDelete: "DELETE /api/twin/v1/project-groups/{id}",
  projectsScenesGet: "GET /api/twin/v1/projects/{projectId}/scenes",
  projectsScenePatch: "PATCH /api/twin/v1/projects/scenes/{id}",
  scenesObjsHierarchyPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/hierarchy",
  scenesObjsHierarchyConfigPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/hierarchy/config",
  scenesObjsGet: "GET /api/twin/v1/scenes/{sceneId}/objs",
  scenesObjsPost: "POST /api/twin/v1/scenes/{sceneId}/objs",
  scenesObjsPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs",
  scenesObjsDeletedGet: "GET /api/twin/v1/scenes/{sceneId}/objs/deleted",
  scenesObjGet: "GET /api/twin/v1/scenes/{sceneId}/objs/{id}",
  scenesObjPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{id}",
  scenesObjsGatherPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/gather",
  scenesObjsRecoverPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/recover",
  scenesObjsRemovePost: "POST /api/twin/v1/scenes/{sceneId}/objs/remove",
  scenesObjsCopyPost: "POST /api/twin/v1/scenes/{sceneId}/objs/copy",
  scenesObjsSplitPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/split",
  scenesObjsComponentsStatesPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{componentId}/states",
  scenesObjsComponentsVersionsPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/versions",
  scenesObjsVideoProjectPost: "POST /api/twin/v1/scenes/{sceneId}/objs/video-project",
  scenesObjsComponentsRelsGet: "GET /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}/rels",
  spaceGet: "GET /api/twin/v1/space",
  usersMeGet: "GET /api/twin/v1/users/me",
  resourceResourceCategoryGet: "GET /api/twin/v1/resource/resource-category",
  resourceStaticMaterialGet: "GET /api/twin/v1/resource/static-material",
  resourceGet: "GET /api/twin/v1/resource",
  resourcePost: "POST /api/twin/v1/resource",
  resourceDelete: "DELETE /api/twin/v1/resource",
  resourcePatch: "PATCH /api/twin/v1/resource/{id}",
  officialResourcesGet: "GET /api/twin/v1/official-resources",
  officialResourcesPost: "POST /api/twin/v1/official-resources",
  officialResourcesDelete: "DELETE /api/twin/v1/official-resources",
  officialResourcesCategoryGet: "GET /api/twin/v1/official-resources/category",
  officialResourcesCategoryPost: "POST /api/twin/v1/official-resources/category",
  officialResourcesCategoryDelete: "DELETE /api/twin/v1/official-resources/category",
  officialResourcesAllGet: "GET /api/twin/v1/official-resources/all",
  officialResourcePatch: "PATCH /api/twin/v1/official-resources/{id}",
  officialResourcesCategoryPatch: "PATCH /api/twin/v1/official-resources/category/{id}",
  scenesResourcesGet: "GET /api/twin/v1/scenes/{sceneId}/resources",
  scenesResourcesPost: "POST /api/twin/v1/scenes/{sceneId}/resources",
  scenesResourcesPatch: "PATCH /api/twin/v1/scenes/{sceneId}/resources",
  scenesResourcePost: "POST /api/twin/v1/scenes/{sceneId}/resource",
  scenesResourcePatch: "PATCH /api/twin/v1/scenes/{sceneId}/resources/{id}",
  scenesRemoveResourcesPost: "POST /api/twin/v1/scenes/{sceneId}/remove/resources",
  shareProjectGet: "GET /api/twin/v1/share/projects/{projectId}",
  shareProjectsSceneGet: "GET /api/twin/v1/share/projects/{projectId}/scenes/{sceneId}",
  scenesObjsComponentsGet: "GET /api/twin/v1/scenes/{sceneId}/objs/{objId}/components",
  scenesObjsComponentsPost: "POST /api/twin/v1/scenes/{sceneId}/objs/{objId}/components",
  scenesObjsComponentsPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components",
  scenesObjsComponentsDelete: "DELETE /api/twin/v1/scenes/{sceneId}/objs/{objId}/components",
  scenesObjsComponentsRelsPost: "POST /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}/rels",
  scenesObjsComponentsRelsPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}/rels",
  scenesObjsComponentsRelsDelete: "DELETE /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}/rels",
  scenesObjsReplaceComponentsPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/replace/components",
  scenesObjsComponentPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}",
  scenesObjsComponentsVersionPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{id}/version",
  scenesObjsComponentsSortPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/sort",
  scenesObjsComponentsStatesSortPatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{componentId}/states/sort",
  scenesObjsComponentsStatePatch: "PATCH /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{componentId}/states/{id}",
  scenesObjsComponentsStatesPost: "POST /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{componentId}/states",
  scenesObjsComponentsStatesDelete: "DELETE /api/twin/v1/scenes/{sceneId}/objs/{objId}/components/{componentId}/states"
};
var $$ = Object.defineProperty, eee = Object.getOwnPropertyDescriptor, Vi = (s, e, t, i) => {
  for (var n = eee(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && $$(e, t, n), n;
};
const iee = "PATCH /api/twin/v1/scenes/{sceneId}/objs/hierarchy/config", nee = "GET /api/twin/v1/scenes/{sceneId}/objs/components/rels", see = "PATCH /api/twin/v1/scenes/{sceneId}/objs/components/versions";
class Tt {
  constructor() {
    S(this, "event", new es());
    S(this, "sceneId", "");
    S(this, "proxy", {
      Api: "http://twin.easyv.test"
    });
  }
  setApiProxy(e) {
    this.proxy.api = e;
  }
  async getObjects(e) {
    try {
      return await zi(Ji.scenesObjsGet, this.proxy.Api, { params: { sceneId: e } });
    } catch (t) {
      console.error(t);
    }
  }
  async updateObjectName(e, t, i) {
    try {
      return await zi(Ji.scenesObjPatch, this.proxy.Api, {
        params: {
          sceneId: e,
          id: t
        },
        body: {
          name: i
        }
      });
    } catch (n) {
      console.error(n);
    }
  }
  async updateObjectSort(e, t) {
    const { objIds: i, targetObjId: n, sortLocation: r } = t;
    try {
      return await zi(Ji.scenesObjsHierarchyPatch, this.proxy.Api, {
        params: {
          sceneId: e
        },
        body: {
          objIds: i,
          targetObjId: n,
          sortLocation: r
        }
      });
    } catch (a) {
      console.error(a);
    }
  }
  async updateObjectHierarchy(e, t, i, n) {
    try {
      const r = {};
      r[i] = n;
      const a = t.map((o) => ({
        id: o,
        config: r
      }));
      return await zi(iee, this.proxy.Api, {
        params: {
          sceneId: e
        },
        body: {
          objs: a
        }
      });
    } catch (r) {
      console.error(r);
    }
  }
  async addObject(e, t, i = []) {
    try {
      return await zi(Ji.scenesObjsPost, this.proxy.Api, {
        params: { sceneId: e },
        body: {
          name: t.name ?? "",
          type: t.type,
          parentObjId: t.parentId,
          hierarchyConfig: {
            inActive: t.inActive,
            visible: t.visible,
            isCollapsed: !1
          },
          commonConfig: t.commonConfig ?? {},
          stateConfig: [{}],
          viewpointConfig: [{}],
          animationConfig: [{}],
          components: i
        }
      });
    } catch (n) {
      return console.error(n), this.event.dispatchEvent("entity-add-error", {}), {
        objId: ""
      };
    }
  }
  async copyObjects(e, t, i, n) {
    return await zi(Ji.scenesObjsCopyPost, this.proxy.Api, {
      params: { sceneId: e },
      body: {
        ids: t,
        indexObjId: i,
        location: n
      }
    });
  }
  async deleteObjects(e, t, i = [], n = !1) {
    try {
      return await zi(Ji.scenesObjsRemovePost, this.proxy.Api, {
        params: { sceneId: e },
        body: {
          ids: t,
          privateMaterialResources: i,
          isDeleted: n
        }
      });
    } catch (r) {
      console.error(r);
    }
  }
  async getDeletedObjects(e) {
    try {
      return await zi(Ji.scenesObjsDeletedGet, this.proxy.Api, {
        params: { sceneId: e },
        query: { date: It().subtract(1, "days").format() }
      });
    } catch (t) {
      console.error(t);
    }
  }
  async recoverObject(e, t) {
    try {
      return await zi(Ji.scenesObjsRecoverPatch, this.proxy.Api, {
        params: { sceneId: e },
        body: { ids: t }
      });
    } catch (i) {
      console.error(i);
    }
  }
  async addComponent(e, t, i = []) {
    try {
      return await zi(Ji.scenesObjsComponentsPost, this.proxy.Api, {
        params: { sceneId: this.sceneId, objId: e },
        body: {
          name: t.name ?? "",
          data: t.data,
          type: t.type,
          version: t.version,
          states: i
        }
      });
    } catch (n) {
      this.event.dispatchEvent("component-add-error", {}), console.error(n);
    }
  }
  async updateComponent(e, t, i) {
    try {
      await zi(Ji.scenesObjsComponentPatch, this.proxy.Api, {
        params: { sceneId: this.sceneId, objId: e, id: t.id },
        body: {
          name: t.name ?? "",
          data: t.data,
          // attribute: customAttributes,
          initialData: i,
          type: t.type
        }
      });
    } catch {
      this.event.dispatchEvent("component-update-error", {});
    }
  }
  async deleteComponent(e, t) {
    try {
      return await zi(Ji.scenesObjsComponentsDelete, this.proxy.Api, {
        params: { sceneId: this.sceneId, objId: e },
        query: { ids: t },
        body: {}
      });
    } catch (i) {
      console.error(i);
    }
  }
  async addState(e, t, i) {
    try {
      return await zi(Ji.scenesObjsComponentsStatesPost, this.proxy.Api, {
        params: { sceneId: this.sceneId, objId: e, componentId: t },
        body: {
          name: i.name,
          config: i.config
        }
      });
    } catch (n) {
      console.error(n);
    }
  }
  async updateState(e, t, i) {
    try {
      return await zi(Ji.scenesObjsComponentsStatePatch, this.proxy.Api, {
        params: { sceneId: this.sceneId, objId: e, componentId: t, id: i.id },
        body: {
          name: i.name,
          config: i.config
        }
      });
    } catch (n) {
      console.log(n);
    }
  }
  async updateStates(e, t) {
    try {
      await zi("PATCH /api/twin/v1/scenes/{sceneId}/objs/components/states", this.proxy.Api, {
        params: { sceneId: t ?? this.sceneId },
        body: {
          componentStates: e
        }
      });
    } catch (i) {
      console.error(i);
    }
  }
  async deleteState(e, t, i) {
    try {
      return await zi(Ji.scenesObjsComponentsStatesDelete, this.proxy.Api, {
        query: {
          ids: i
        },
        params: {
          sceneId: this.sceneId,
          objId: e,
          componentId: t
        }
        // body: {},
      });
    } catch (n) {
      console.error(n);
    }
  }
  async updateComponentStateSort(e, t, { upperId: i, lowerId: n }) {
    return await zi(Ji.scenesObjsComponentsStatesSortPatch, this.proxy.Api, {
      params: {
        componentId: e,
        objId: t,
        sceneId: this.sceneId
      },
      body: {
        upperId: i,
        lowerId: n
      }
    });
  }
  async updateVersions(e) {
    return await zi(see, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: { objs: e }
    });
  }
  async makeGroup(e, t, i, n, r) {
    const a = {
      type: "EngineGroup",
      /** 需要成组的元件ids */
      objIds: t,
      /** 组名称 */
      name: i,
      /** 父级元件id */
      parentObjId: n
    };
    return r && Object.assign(a, {
      commonConfig: {
        version: r
      }
    }), await zi(Ji.scenesObjsGatherPatch, this.proxy.Api, {
      params: {
        sceneId: e
      },
      body: a
    });
  }
  async splitGroup(e, t) {
    return zi(Ji.scenesObjsSplitPatch, this.proxy.Api, {
      params: {
        objId: e,
        sceneId: t
      }
    });
  }
  async createMaterials(e) {
    return zi(Ji.scenesResourcesPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        sceneResources: e
      }
    });
  }
  async updateMaterial(e, t) {
    return zi(Ji.scenesResourcePatch, this.proxy.Api, {
      params: {
        id: e,
        sceneId: this.sceneId
      },
      body: {
        ...t
      }
    });
  }
  async batchUpdateMaterials(e) {
    return zi(Ji.scenesResourcesPatch, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        sceneResources: e
      }
    });
  }
  async createTextures(e) {
    return zi(Ji.scenesResourcesPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        sceneResources: e
      }
    });
  }
  async updateTexture(e, t) {
    return zi(Ji.scenesResourcePatch, this.proxy.Api, {
      params: {
        id: e,
        sceneId: this.sceneId
      },
      body: {
        ...t
      }
    });
  }
  async deleteResources(e) {
    return zi(Ji.scenesRemoveResourcesPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        ...e
      }
    });
  }
  async getSceneResources() {
    return zi(Ji.scenesResourcesGet, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      }
    });
  }
  async replaceComponent(e, t, i) {
    return zi(Ji.scenesObjsReplaceComponentsPatch, this.proxy.Api, {
      params: {
        sceneId: this.sceneId,
        objId: e
      },
      body: {
        replaceIds: t,
        components: i.map((n) => ({
          name: n.name,
          data: n.data,
          type: n.type,
          version: n.version,
          states: n.states
        }))
      }
    });
  }
  async copyEntitiesFromOtherScene(e, t, i, n) {
    return zi(Ji.scenesObjsCopyPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        ids: e,
        sourceSceneId: t,
        indexObjId: i,
        location: n
      }
    });
  }
  async createVideoProjectEntity(e) {
    return zi(Ji.scenesObjsVideoProjectPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId
      },
      body: {
        name: e.name,
        commonConfig: e.commonConfig,
        component: e.component
      }
    });
  }
  async batchGetComponentRel(e) {
    return zi(nee, this.proxy.Api, {
      params: {
        sceneId: this.sceneId,
        componentIds: e
      }
    });
  }
  async addComponentRel(e, t, i, n, r) {
    return zi(Ji.scenesObjsComponentsRelsPost, this.proxy.Api, {
      params: {
        sceneId: this.sceneId,
        id: t,
        objId: e
      },
      body: {
        instanceId: n,
        stateId: i,
        relType: r
      }
    });
  }
  async updateComponentRel(e, t, i, n, r) {
    return zi(Ji.scenesObjsComponentsRelsPatch, this.proxy.Api, {
      params: {
        sceneId: this.sceneId,
        objId: e,
        id: t
      },
      body: {
        oldInstanceId: n,
        stateId: i,
        newInstanceId: r
      }
    });
  }
  async deleteComponentRel(e, t, i, n, r) {
    return zi(Ji.scenesObjsComponentsRelsDelete, this.proxy.Api, {
      params: {
        sceneId: this.sceneId,
        objId: e,
        id: t
      },
      query: {
        instanceId: n,
        stateId: i
      }
    });
  }
}
S(Tt, "enabled", !1);
Vi([
  Hi
], Tt.prototype, "getObjects");
Vi([
  Hi
], Tt.prototype, "updateObjectName");
Vi([
  Hi
], Tt.prototype, "updateObjectSort");
Vi([
  Hi
], Tt.prototype, "updateObjectHierarchy");
Vi([
  Hi
], Tt.prototype, "addObject");
Vi([
  Hi
], Tt.prototype, "copyObjects");
Vi([
  Hi
], Tt.prototype, "deleteObjects");
Vi([
  Hi
], Tt.prototype, "getDeletedObjects");
Vi([
  Hi
], Tt.prototype, "recoverObject");
Vi([
  Hi
], Tt.prototype, "addComponent");
Vi([
  Hi
], Tt.prototype, "updateComponent");
Vi([
  Hi
], Tt.prototype, "deleteComponent");
Vi([
  Hi
], Tt.prototype, "addState");
Vi([
  Hi
], Tt.prototype, "updateState");
Vi([
  Hi
], Tt.prototype, "updateStates");
Vi([
  Hi
], Tt.prototype, "deleteState");
Vi([
  Hi
], Tt.prototype, "updateComponentStateSort");
Vi([
  Hi
], Tt.prototype, "updateVersions");
Vi([
  Hi
], Tt.prototype, "makeGroup");
Vi([
  Hi
], Tt.prototype, "splitGroup");
Vi([
  Hi
], Tt.prototype, "createMaterials");
Vi([
  Hi
], Tt.prototype, "updateMaterial");
Vi([
  Hi
], Tt.prototype, "batchUpdateMaterials");
Vi([
  Hi
], Tt.prototype, "createTextures");
Vi([
  Hi
], Tt.prototype, "updateTexture");
Vi([
  Hi
], Tt.prototype, "deleteResources");
Vi([
  Hi
], Tt.prototype, "getSceneResources");
Vi([
  Hi
], Tt.prototype, "replaceComponent");
Vi([
  Hi
], Tt.prototype, "copyEntitiesFromOtherScene");
Vi([
  Hi
], Tt.prototype, "createVideoProjectEntity");
Vi([
  Hi
], Tt.prototype, "batchGetComponentRel");
Vi([
  Hi
], Tt.prototype, "addComponentRel");
Vi([
  Hi
], Tt.prototype, "updateComponentRel");
Vi([
  Hi
], Tt.prototype, "deleteComponentRel");
var _i = /* @__PURE__ */ ((s) => (s.Add = "add-component", s.AddState = "add-state-component", s.UpdateData = "update-data-component", s.RemoveChild = "remove-child-component", s.UpdateState = "update-state-component", s))(_i || {});
const iC = {
  Scene: 15,
  // 数字要素 末尾为 1
  Point: 17,
  // 00010001
  PoiPanel: 33,
  // 00100001
  Iframe: 49,
  // 00110001
  IframeV2: 49,
  FlowFence: 65,
  // 01000001
  FlowRoute: 81,
  PoiPanelV2: 97,
  Floor: 113,
  // 模型相关 末尾为2
  Model: 18,
  // 00010010
  InstanceModel: 34,
  // 00100010
  BatchModel: 34,
  // 00100010,
  // 组 末尾为3
  EngineGroup: 19,
  // 00010011
  // 光源末尾为4
  Light: 20,
  // 00010100
  PointLight: 36,
  // 00100100
  DirectLight: 52,
  // 00110100
  SpotLight: 68,
  RectAreaLight: 84,
  SunLight: 100,
  // 热力末尾为5
  ClusteringHeatmap: 21,
  AreaHeatmap: 37,
  // 水面
  Water: 49,
  // 镜头末尾为14
  Camera: 30,
  CameraCruise: 46,
  CameraSurround: 62,
  CameraFirstPerson: 62,
  PathCruise: 62,
  // 其他末尾为15
  GammaCorrection: 15,
  BatchCreateModelTool: 15,
  Bloom: 15,
  Select: 15,
  Transform: 15,
  FinalComposer: 15,
  ImageQuality: 15,
  Draw: 15,
  Engine: 15,
  KeyboardRoma: 15,
  InfiniteGrid: 15,
  Taa: 15,
  SSAO: 15,
  Outline: 15,
  Animation: 15,
  CustomAnimation: 15,
  BuildingsAnimation: 15,
  Template: 15,
  ParticleSystem: 18,
  VideoProject: 68,
  ClipModel: 68,
  //
  MiniMap: 68
};
var Iy = /* @__PURE__ */ ((s) => (s.Add = "entity-add", s.ComponentAdd = "component-add", s))(Iy || {});
const ree = {
  Water: "2.0.0",
  Floor: "2.0.0",
  Model: "2.3.1",
  InstanceModel: "2.6.0",
  Camera: void 0,
  CameraCruise: void 0,
  CameraSurround: void 0,
  CameraFirstPerson: "2.4.0",
  PathCruise: "2.0.0",
  PoiPanel: void 0,
  PoiPanelV2: "2.0.0",
  Point: "2.0.0",
  Iframe: void 0,
  IframeV2: "2.0.0",
  FlowRoute: "2.0.0",
  PointLight: void 0,
  DirectLight: void 0,
  SpotLight: void 0,
  RectAreaLight: void 0,
  SunLight: void 0,
  FlowFence: "2.0.0",
  EngineGroup: "2.6.0",
  BatchModel: void 0,
  ClusteringHeatmap: void 0,
  AreaHeatmap: "2.0.0",
  VideoProject: "1.10.0",
  BuildingsAnimation: "2.0.0",
  ParticleSystem: "2.1.0",
  ClipModel: "2.2.0",
  MiniMap: "2.7.0"
};
class ed extends mt {
  constructor(t) {
    super();
    // declare userData: EngineEntityUserData;
    // 每个实体下，同种类型组件只会有一个
    S(this, "componentMap", /* @__PURE__ */ new Map());
    S(this, "isAdded", !1);
    // 一次性标识，初次load 完成时设置为true
    S(this, "isInitComplete", !1);
    S(this, "tag", "");
    // 用于赋予实体类型，引擎不做处理
    S(this, "eType", "EngineGroup");
    S(this, "parentId");
    S(this, "inActive", !1);
    // 记录实体的层级
    S(this, "mPath", "");
    S(this, "world");
    S(this, "deletedAt");
    S(this, "commonConfig", {});
    // 是否能在场景中选中
    S(this, "canSelectedInWorld", !0);
    const {
      id: i,
      parentId: n,
      name: r = "",
      type: a = "EngineGroup",
      mPath: o = "",
      visible: l = !0,
      inActive: c = !1
    } = t;
    this.uuid = i, this.name = r, this.eType = a, this.mPath = o, this.visible = l, this.inActive = c, this.userData.config = {
      isCollapsed: !1
    }, Object.assign(this.commonConfig, t.commonConfig), n && (this.parentId = n);
  }
  dispose() {
    var t;
    this.componentMap.forEach((i) => {
      i.dispose();
    }), this.componentMap.clear(), (t = this.parent) == null || t.remove(this);
  }
  getComponentInit() {
    let t = -1;
    return t = Array.from(this.componentMap.values()).findIndex((i) => !i.isInit), t === -1;
  }
  hasComponent(t) {
    let i = !1;
    return this.componentMap.forEach((n) => {
      n.type === t && (i = !0);
    }), i;
  }
  /**
   *
   * @param componentId
   * @param componentClass
   * @param componentOptions
   */
  addComponent(t, i, n, r = !Tt.enabled) {
    let a = this.componentMap.get(t);
    if (a)
      return a;
    a = new n(t, i, this), this.componentMap.set(t, a), this.add(a.group), a.isAdded = r;
    const o = () => {
      !r && Tt.enabled && a && Qe.ApiServer.addComponent(this.uuid, a.toJson()).then((l) => {
        a && (this.setComponentId(a.id, l.id), this.dispatchComponentAdded(a), dt.Entity.setState({
          id: this.uuid,
          name: this.name,
          components: this.getComponentArray().map((c) => c.toJson())
        }));
      }), dt.Select.refreshState(), r && this.dispatchEvent({
        type: "component-add"
        /* ComponentAdd */
      });
    };
    return !this.isAdded && Tt.enabled ? this.addEventListener("entity-add", o) : o(), a;
  }
  async addComponentAsync(t, i, n, r = !Tt.enabled) {
    let a = this.componentMap.get(t);
    if (a)
      return a;
    a = new n(t, i, this), this.componentMap.set(t, a), this.add(a.group), a.isAdded = r;
    const l = await (() => new Promise((c) => {
      this.isAdded ? c(!0) : this.addEventListener("entity-add", () => {
        c(!0);
      });
    }))();
    if (!r && l) {
      const c = await Qe.ApiServer.addComponent(this.uuid, a.toJson());
      a && (this.setComponentId(a.id, c.id), this.dispatchComponentAdded(a), dt.Entity.setState({
        id: this.uuid,
        name: this.name,
        components: this.getComponentArray().map((h) => h.toJson())
      }), this.dispatchEvent({
        type: "component-add"
        /* ComponentAdd */
      }));
    }
    return a;
  }
  addComponentInstance(t) {
    this.componentMap.has(t.id) || (this.componentMap.set(t.id, t), this.add(t.group), t.isAdded = !0);
  }
  removeComponent(t, i = !0) {
    const n = [];
    if (t)
      if (t instanceof Array)
        t.forEach((r) => {
          const a = this.componentMap.get(r);
          a == null || a.dispose(), this.componentMap.delete(r), n.push(r);
        });
      else {
        const r = this.componentMap.get(t);
        r == null || r.dispose(), this.componentMap.delete(t), n.push(t);
      }
    else
      n.push(...Array.from(this.componentMap.keys()));
    i && Qe.ApiServer.deleteComponent(this.uuid, n), dt.Entity.setState({
      id: this.uuid,
      name: this.name,
      components: this.getComponentArray().map((r) => r.toJson())
    });
  }
  getComponentIds() {
    return Array.from(this.componentMap.keys());
  }
  setId(t) {
    this.uuid = t;
  }
  getComponentArray() {
    return Array.from(this.componentMap.values());
  }
  getComponentById(t) {
    return this.componentMap.get(t);
  }
  getComponentByType(t) {
    let i;
    return this.componentMap.forEach((n) => {
      n.type === t && (i = n);
    }), i;
  }
  // 拿到该实体下可以被拖动的 组件
  getEntityComponentByCommandName(t) {
    return Array.from(this.componentMap.values()).sort(
      (n, r) => (iC[n.type] & 15) - (iC[r.type] & 15)
    )[0];
  }
  getComponentLabels() {
    const t = [];
    return this.componentMap.forEach((i) => {
      t.push(...i.getDataLabels());
    }), t;
  }
  setComponentId(t, i) {
    const n = this.componentMap.get(t);
    n && (n.setId(i), this.componentMap.delete(t), this.componentMap.set(i, n));
  }
  dispatchComponentAdded(t) {
    t.dispatchEvent(_i.Add, {}), t.isAdded = !0;
  }
  getCommonConfigLastVersion() {
    return ree[this.eType];
  }
}
class Pl extends ed {
  constructor(t) {
    super(t);
    S(this, "inActive");
    S(this, "onClick");
    S(this, "onLoad");
    S(this, "componentMap", /* @__PURE__ */ new Map());
    S(this, "tag", "");
    const {
      position: i = new _(),
      rotation: n = new Mi(),
      scale: r = new _(1, 1, 1),
      visible: a,
      inActive: o,
      onClick: l,
      onLoad: c,
      id: h,
      // userData,
      animations: u
    } = t;
    this.uuid = h, this.inActive = o ?? !1, this.onClick = l, this.onLoad = c, this.position.set(i.x, i.y, i.z), this.rotation.set(n.x, n.y, n.z), this.scale.set(r.x, r.y, r.z), a !== void 0 && (this.visible = a);
  }
  attachTransformControls() {
  }
  detachTransformControls() {
  }
}
class cp extends Pl {
  constructor(t) {
    const i = {
      ...t,
      components: []
    };
    super(i);
    S(this, "autoResize", !0);
    S(this, "faceToCamera", !0);
    S(this, "element", document.createElement("div"));
    this.element.style.position = "absolute";
  }
  attachTransformControls() {
  }
  detachTransformControls() {
  }
}
class es {
  constructor() {
    S(this, "m_listeners", /* @__PURE__ */ new Map());
    S(this, "disableTypeSet", /* @__PURE__ */ new Set());
  }
  /**
   * 销毁所有的事件监听函数
   * 函数调用必须在不使用EventDispatcher之后
   */
  dispose() {
    this.removeAllEventListeners();
  }
  /**
   * 判断是否有同一类型的事件被监听
   *
   * @param type - 事件类型.
   * @param listener -事件触发后被执行的函数.
   */
  hasEventListener(e, t) {
    const i = this.m_listeners.get(e);
    return i === void 0 ? !1 : t !== void 0 ? i.has(t) : !1;
  }
  enableType(e) {
    return this.disableTypeSet.delete(e), this;
  }
  disableType(e) {
    return this.disableTypeSet.add(e), this;
  }
  /**
   * 绑定一个类型的事件
   *
   * @param type - 事件类型.
   * @param listener -事件触发后被执行的函数.
   */
  addEventListener(e, t) {
    let i = this.m_listeners.get(e);
    i === void 0 && (i = /* @__PURE__ */ new Set(), i.add(t), this.m_listeners.set(e, i)), i.add(t);
  }
  once(e, t) {
    this.m_listeners.get(e);
    const i = (n) => (this.removeEventListener(e, i), t.call(this, n));
    return this.addEventListener(e, i);
  }
  /**
   * 移除对应类型的事件.
   *
   * @param type - 事件类型.
   * @param listener -事件触发后被执行的函数. 如果值是undefined，那么所有事件都会被移除
   */
  removeEventListener(e, t) {
    if (this.m_listeners.get(e) !== void 0)
      if (t === void 0)
        this.m_listeners.delete(e);
      else {
        const n = this.m_listeners.get(e);
        n == null || n.delete(t), t || this.m_listeners.delete(e);
      }
  }
  /**
   * 移除已存的所有事件监听.
   */
  removeAllEventListeners() {
    const e = Array.from(this.m_listeners.keys());
    for (const t of e)
      this.removeEventListener(t);
  }
  /**
   * 获取已存的所有事件类型.
   *
   * @returns Array of event types.
   */
  get eventTypes() {
    return Array.from(this.m_listeners.keys());
  }
  /**
   * 获取所有已存对应类型的事件执行函数
   *
   * @param type - 事件类型.
   * @returns Array of event listeners.
   */
  listeners(e) {
    return this.m_listeners.get(e);
  }
  /**
   * 触发事件
   *
   * @param event.
   */
  dispatchEvent(e, t) {
    const i = this.m_listeners.get(e);
    i !== void 0 && (this.disableTypeSet.has(e) || i.forEach((n) => n.call(this, t)));
  }
}
var x0 = /* @__PURE__ */ ((s) => (s.Select = "select", s.DrawUtils = "draw-util", s.StateUpdate = "state-update", s.AddEntity = "add-entity", s.RestoreEntity = "restore-entity", s.RemoveEntity = "remove-entity", s))(x0 || {});
class jO extends es {
  constructor() {
    super(...arguments);
    S(this, "undoArray", []);
    S(this, "redoArray", []);
    S(this, "lastCmdTime", Date.now());
    S(this, "commandMap", /* @__PURE__ */ new Map());
    S(this, "enabled", !1);
    S(this, "hasBind", !1);
    S(this, "_handleKeyDownBindThis", this._handleKeyDown.bind(this));
  }
  async execute(t, i) {
    this.undoArray.push(t);
    const n = this.commandMap.get(t.type);
    return n ? n.push(t) : this.commandMap.set(t.type, [t]), this.redoArray.length = 0, await t.execute();
  }
  undo() {
    let t;
    return this.undoArray.length > 0 && (t = this.undoArray.pop()), t !== void 0 && (t.undo(), this.dispatchEvent("undo", {
      type: t.type,
      orderId: t.orderId,
      name: t.name
    }), this.redoArray.push(t)), t;
  }
  redo() {
    let t;
    return this.redoArray.length > 0 && (t = this.redoArray.pop()), t !== void 0 && (t.redo(), this.dispatchEvent("redo", {
      type: t.type,
      orderId: t.orderId,
      name: t.name
    }), this.undoArray.push(t)), t;
  }
  active() {
    document.onkeydown = this._handleKeyDownBindThis, this.hasBind = !0;
  }
  clear() {
    this.undoArray.forEach((t) => t.dispose()), this.redoArray.forEach((t) => t.dispose()), Array.from(this.commandMap.values()).forEach(
      (t) => t.forEach((i) => i.dispose())
    ), this.undoArray.length = 0, this.redoArray.length = 0, this.commandMap.clear(), document.removeEventListener("keydown", this._handleKeyDownBindThis), this.hasBind = !1;
  }
  _handleKeyDown(t) {
    const { key: i, ctrlKey: n, shiftKey: r, metaKey: a } = t, { userAgent: o } = window.navigator, c = o.includes("Mac") ? a : n;
    c && i === "z" && (t.preventDefault(), this.undo()), c && i === "Z" && r && (t.preventDefault(), this.redo());
  }
}
const oee = (s, e, t) => {
  let i = 1;
  const n = s.splice(e, i)[0];
  i = 0, s.splice(t, i, n);
};
class nC extends ci {
  unshift(e) {
    this.add(e), oee(this.children, this.children.length - 1, 0);
  }
}
class hg {
  constructor() {
    S(this, "_map", /* @__PURE__ */ new Map());
    S(this, "_size", 0);
    S(this, "_array", []);
    S(this, "_keyArray", []);
  }
  get size() {
    return this._map.size;
  }
  forEach(e, t) {
    return this._array.forEach(e, t);
  }
  set(e, t) {
    this._array.push(t), this._keyArray.push(e), this._map.set(e, t);
  }
  insert(e, t, i) {
    this._array.splice(e, 0, i), this._keyArray.splice(e, 0, t), this._map.set(t, i);
  }
  get(e) {
    return this._map.get(e);
  }
  getByIndex(e) {
    return this._array[e];
  }
  values() {
    return [...this._array];
  }
  delete(e) {
    const t = this._map.get(e);
    this._keyArray.splice(this._keyArray.indexOf(e), 1), t && this._array.splice(this._array.indexOf(t), 1), this._map.delete(e);
  }
  has(e) {
    return this._map.has(e);
  }
  clear() {
    this._array.length = 0, this._map.clear();
  }
  pop() {
    this._array.pop();
    const e = this._keyArray.pop();
    e && this._map.delete(e);
  }
}
let lee = 0;
class Rl {
  constructor(e, t = "") {
    S(this, "orderId", lee++);
    this.type = e, this.name = t;
  }
  async execute() {
  }
  undo() {
  }
  redo() {
  }
  toJSON() {
  }
  dispose() {
  }
}
class cee extends Rl {
  constructor(e, t, i, n, r, a) {
    super(x0.StateUpdate, "状态配置更新"), this.id = e, this.config = t, this.newConfig = i, this.updateCallback = n, this.stateName = r, this.newStateName = a;
  }
  undo() {
    this.updateCallback(this.id, this.config, this.stateName);
  }
  redo() {
    this.updateCallback(this.id, this.newConfig, this.newStateName);
  }
}
class hee {
  constructor(e) {
    S(this, "curStateId");
    S(this, "stateList", new hg());
    S(this, "stateUpdatedCallbackMap", /* @__PURE__ */ new Map());
    S(this, "_commandUpdateCallbackBindThis", this._commandUpdateCallback.bind(this));
    S(this, "needsUpdateStateSet", /* @__PURE__ */ new Set());
    this.component = e;
  }
  clearStates(e) {
    e.forEach((t) => this.clear(t.id));
  }
  clear(e) {
    if (this.stateList.delete(e), e === this.curStateId) {
      const [t] = this.stateList.values();
      this.curStateId = t == null ? void 0 : t.id;
    }
  }
  hasState(e) {
    return this.stateList.has(e);
  }
  add(e, t) {
    this.stateList.set(e.id, e), t && this.stateUpdatedCallbackMap.set(e.id, t), !this.curStateId && (this.curStateId = e.id);
  }
  addStates(e, t) {
    e.forEach((i) => this.add(i, t));
  }
  // 获得深拷贝后的config
  deepGet(e) {
    let t = this.stateList.get(e);
    return t && (t = JSON.parse(JSON.stringify(t))), t;
  }
  addStateUpdatedCallback(e, t) {
    this.stateUpdatedCallbackMap.set(e, t);
  }
  updateStateConfig(e, t, i) {
    const n = this.stateList.get(e);
    return n && (Qe.history.execute(
      new cee(
        e,
        n.config,
        t,
        this._commandUpdateCallbackBindThis,
        n.name,
        i
      )
    ), n.config = t, i && (n.name = i), dt.Entity.setState({
      id: this.component.entity.uuid,
      name: this.component.entity.name,
      components: this.component.entity.getComponentArray().map((r) => r.toJson())
    }), this.needsUpdateStateSet.add(e)), n;
  }
  _commandUpdateCallback(e, t, i) {
    const n = this.stateList.get(e);
    n && (e === this.curStateId && (this.component.needsUpdate = !0), n.config = t, i && (n.name = i), dt.Entity.setState({
      id: this.component.entity.uuid,
      name: this.component.entity.name,
      components: this.component.entity.getComponentArray().map((r) => r.toJson())
    }), this.needsUpdateStateSet.add(e));
  }
  getCurrentStateConfig() {
    const [e] = this.stateList.values();
    let { config: t } = e ?? {};
    if (this.curStateId) {
      const i = this.stateList.get(this.curStateId);
      i && (t = i.config);
    }
    return t ?? {};
  }
  getStateConfigById(e) {
    const t = this.stateList.get(e);
    let i;
    return t && (i = t.config), i;
  }
  initStates(e) {
    this.stateList.clear(), this.sort(e).forEach((t) => {
      this.stateList.set(t.id, t);
    }), e[0] && (this.curStateId = e[0].id);
  }
  sort(e) {
    let t = !0;
    return e.forEach((i) => {
      i.rank !== 0 && (t = !1);
    }), t ? e.sort((i, n) => new Date(i.createdAt).getTime() - new Date(n.createdAt).getTime()) : e.sort((i, n) => i.rank - n.rank), e;
  }
  // 设置更新至某一状态
  setState(e) {
    const t = this.stateList.get(e);
    return t && (this.curStateId = e), t;
  }
  updateStateId(e, t) {
    const i = this.stateList.get(e);
    i && (i.id = t, this.stateList.set(t, i), this.stateList.delete(e), this.curStateId === e && (this.curStateId = t));
  }
  // public getSortedStateArray(): EasyTwinStateOptions<T>[] {
  //   const result: EasyTwinStateOptions<T>[] = [];
  //   this.stateList.forEach((state) => {
  //     result.push(state);
  //   });
  //   return result;
  // }
  toJson() {
    const e = [];
    return this.stateList.forEach((t) => {
      e.push({
        id: t.id,
        config: t.config,
        name: t.name,
        rank: t.rank
      });
    }), e;
  }
}
const Th = new zt(), fee = new _(), qO = Math.PI * 4, pee = new _(1, 0, 0), YO = new _(0, -1, 0), mee = new _(0, 0, 1), Mm = new ln(YO, 0), rC = new lr();
new _();
new _();
function Y4(s, e, t, i, n = 0) {
  const r = new _(), a = $r(e, t, i);
  switch (rC.setFromCamera(a, s), Mm.constant = n, Qe.Info.gridAxisPlane) {
    case "xy":
      Mm.normal = mee;
      break;
    case "xz":
      Mm.normal = YO;
      break;
    case "yz":
      Mm.normal = pee;
      break;
  }
  return rC.ray.intersectPlane(Mm, r), r;
}
function $r(s, e, t) {
  const i = vee(s, e, t), n = t.getBoundingClientRect(), r = new Se();
  return i && n && (r.x = i.x / n.width * 2 - 1, r.y = i.y / n.height * -2 + 1), document.pointerLockElement !== null && r.set(0, 0), r;
}
function gee(s, e, t, i) {
  const n = s.getComponentByType("Scene");
  return n ? n.geographicProjectionUtils.projectPositionToGeographic(e, t, i) : { lon: 0, lat: 0, alt: 0 };
}
function Lu(s, e, t, i) {
  const n = s.getComponentByType("Scene");
  return n ? n.geographicProjectionUtils.projectGeographicToPosition(e, t, i) : { x: 0, y: 0, z: 0 };
}
function vee(s, e, t) {
  const i = t.getBoundingClientRect();
  return i ? {
    x: s - i.left,
    y: e - i.top
  } : {
    x: 0,
    y: 0
  };
}
function yee(s, e, t) {
  if (s.updateWorldMatrix(!1, !1), s.isMesh) {
    const { geometry: i } = s;
    if (i.boundingBox === null && i.computeBoundingBox(), s.isInstancedMesh) {
      Th.makeEmpty();
      const n = Array.from(s.instanceMatrix.array).slice(
        e * 16,
        (e + 1) * 16
      ), r = new et().fromArray(n);
      Th.setFromCenterAndSize(fee.clone(), new _()), i.boundingBox && (Th.copy(i.boundingBox), Th.applyMatrix4(r));
    }
    Th.applyMatrix4(s.matrixWorld), t.expandByPoint(Th.min), t.expandByPoint(Th.max);
  }
  return t;
}
function Lc(s) {
  return s / qO;
}
function rx(s) {
  return s * 50 / qO;
}
function d0(s, e) {
  const t = s / 100;
  let i = t <= 66 ? 255 : Math.round(329.698727446 * (t - 60) ** -0.1332047592), n = t <= 66 ? Math.round(99.4708025861 * Math.log(t) - 161.1195681661) : Math.round(288.1221695283 * (t - 60) ** -0.0755148492), r = t >= 66 ? 255 : t <= 19 ? 0 : Math.round(138.5177312231 * Math.log(t - 10) - 305.0447927307);
  return i = Math.max(0, Math.min(255, i)), n = Math.max(0, Math.min(255, n)), r = Math.max(0, Math.min(255, r)), e.r = i / 255, e.g = n / 255, e.b = r / 255, e;
}
function aC(s) {
  return s.split(".").map((e) => parseInt(e, 10));
}
function ZO(s, e) {
  const t = aC(s), i = aC(e);
  for (let n = 0; n < 3; n++) {
    if (t[n] > i[n])
      return 1;
    if (t[n] < i[n])
      return -1;
  }
  return 0;
}
function L1(s) {
  if (s)
    return s * Math.PI / 180;
}
const lu = class lu {
  constructor() {
    S(this, "dependencies", /* @__PURE__ */ new Map());
  }
  static getInstance() {
    return lu.instance || (lu.instance = new lu()), lu.instance;
  }
  register(e, t) {
    this.dependencies.set(e, t);
  }
  resolve(e) {
    const t = this.dependencies.get(e);
    return t || (console.warn(`Dependency ${e} is not registered.`), {
      ctor: e4,
      originCtor: e4,
      type: "Template",
      version: "1.7.0"
    });
  }
  filterComponentsWithClass(e) {
    return Array.from(this.dependencies.keys()).filter(
      (t) => e === t.split("@")[0]
    );
  }
  getLastVersionComponent(e) {
    const t = Array.from(this.dependencies.keys()).filter(
      (i) => i.includes(e)
    );
    return t.sort(ZO), this.dependencies.get(t.reverse()[0]);
  }
};
S(lu, "instance");
let Hc = lu;
const cu = class cu {
  constructor() {
    S(this, "dependencies", /* @__PURE__ */ new Map());
    S(this, "dependenciesInstance", /* @__PURE__ */ new Map());
  }
  static getInstance() {
    return cu.instance || (cu.instance = new cu()), cu.instance;
  }
  dispose() {
    this.dependenciesInstance.clear();
  }
  getDependencyKey(e, t) {
    return `${e}`;
  }
  register(e, t) {
    const i = this.getDependencyKey(e, t);
    this.dependencies.get(i) || this.dependencies.set(i, t);
  }
  resolve(e, t) {
    const i = this.dependencies.get(`${e}`);
    if (i) {
      let n = this.dependenciesInstance.get(t);
      return n || (n = new i(), this.dependenciesInstance.set(t, n)), n;
    }
  }
};
S(cu, "instance");
let hp = cu;
const mb = "injection-service-metadata";
function KO(s, e = "default") {
  return (t, i) => {
    const n = Reflect.getOwnMetadata(mb, t.constructor) ?? [];
    n.push({
      name: s,
      identifier: e,
      property: i
    }), Reflect.defineMetadata(mb, n, t.constructor);
  };
}
function xee(s) {
  return (e) => (hp.getInstance().register(s, e), e);
}
const ni = (s, e = { version: "1.7.0" }) => function(t) {
  const i = class extends t {
    constructor(...r) {
      super(...r);
      S(this, "type", s);
      S(this, "version", e.version ?? "1.7.0");
      const a = Reflect.getOwnMetadata(mb, t);
      if (a) {
        const o = this;
        a.forEach(({ identifier: l, property: c, name: h }) => {
          o[c] = hp.getInstance().resolve(h, l);
        });
      }
    }
  };
  return Hc.getInstance().register(`${s}@${e.version ?? "1.7.0"}`, {
    ctor: i,
    originCtor: t,
    type: s,
    version: e.version ?? "1.7.0"
  }), i;
};
function Qp(s = 0.3) {
  return function(e, t, i) {
    const n = i.value, r = i;
    let a = !1;
    return r.value = function(...o) {
      if (a)
        return;
      a = !0;
      const l = setTimeout(() => {
        a = !1, clearTimeout(l);
      }, s * 1e3);
      n.apply(this, o);
    }, i;
  };
}
const oC = {
  uniforms: {
    tDiffuse: { value: null },
    luminosityThreshold: { value: 1 },
    smoothWidth: { value: 1 },
    defaultColor: { value: new _e(0) },
    defaultOpacity: { value: 0 }
  },
  vertexShader: (
    /* glsl */
    `
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`
  ),
  fragmentShader: (
    /* glsl */
    `
    uniform sampler2D tDiffuse;
    uniform vec3 defaultColor;
    uniform float defaultOpacity;
    uniform float luminosityThreshold;
    uniform float smoothWidth;
    varying vec2 vUv;
    void main() {
        vec4 texel = texture2D( tDiffuse, vUv );
        vec3 luma = vec3( 0.299, 0.587, 0.114 );
        float v = dot( texel.xyz, luma );
        vec4 outputColor = vec4( defaultColor.rgb, defaultOpacity );
        float alpha = smoothstep( luminosityThreshold, luminosityThreshold + smoothWidth, v );
        gl_FragColor = mix( outputColor, texel, alpha );
    }`
  )
}, lC = {
  uniforms: {},
  vertexShader: (
    /* glsl */
    ` 
    uniform mat4 lastFrameModel;
    uniform mat4 lastFrameView;
    uniform mat4 lastFrameProjection;
    varying vec4 clipPos;
    varying vec4 lastclipPos;

    void main() {
        clipPos = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        lastclipPos = lastFrameProjection * lastFrameView * lastFrameModel * vec4(position, 1.0);

        gl_Position = clipPos;
    }
  `
  ),
  fragmentShader: (
    /* glsl */
    `
    precision mediump float;

    uniform float colorScale;
    varying vec4 clipPos;
    varying vec4 lastclipPos;

    void main() {
        // 视角划分
        vec3 motion = ((clipPos / clipPos.w) - (lastclipPos / lastclipPos.w)).xyz;

        // 镜头移动后的颜色偏移
        vec3 motionColor = ((colorScale * motion) + 2.0) / 4.0;

        gl_FragColor = vec4(motionColor, 1.0);
    }
  `
  )
}, cC = {
  uniforms: {
    numFrames: { value: 5 },
    opacity: { value: 0.1 },
    width: { value: 100 },
    height: { value: 100 },
    tLastFrame: { value: null },
    tMotion: { value: null }
    // u_LookAt: { value: new Matrix4() },
    // u_lastLookAt: { value: new Matrix4() },
  },
  vertexShader: (
    /* glsl */
    `
    varying vec2 Uv;

    void main() {

      Uv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

    }
  `
  ),
  fragmentShader: (
    /* glsl */
    `
    precision mediump float;

    uniform float opacity;
    uniform float numFrames;
    uniform float height;
    uniform float width;
    uniform vec2 speed;
    uniform float weight;
    uniform sampler2D tDiffuse;
    uniform sampler2D tMotion;
    uniform sampler2D tLastFrame;
    varying vec2 Uv;

    vec3 yCgCoToRgb(vec4 yCgCo) {
      float temp = yCgCo.x - yCgCo.y;
      return vec3(temp + yCgCo.z, yCgCo.x + yCgCo.y, temp - yCgCo.z);
    }
    

    void main() {
        vec4 texel = texture2D(tDiffuse, Uv);
        vec3 pixelMovement = texture2D(tMotion, Uv).rgb;
        vec2 oldPixelUv = Uv - ((pixelMovement.xy * 2.0) - 1.0);
        vec4 oldTexel = texture2D(tLastFrame, oldPixelUv);
        // 混合相邻像素颜色
        vec4 maxNeighbor = vec4(0.0);
        vec4 minNeighbor = vec4(1.0);
        vec4 average = vec4(0.0);
        for (int x = -1; x <= 1; x++) {
            for (int y = -1; y <= 1; y++) {
                vec2 neighborUv = Uv + vec2(float(x) / width, float(y) / height);
                vec4 neighborTexel = texture2D(tDiffuse, neighborUv);
                vec4 yCgCoNeighborTexel = vec4(mat3(0.25, -0.25, 0.5, 0.5, 0.5, 0.0, 0.25, -0.25, -0.5) * neighborTexel.rgb, neighborTexel.a);
                maxNeighbor = max(maxNeighbor, yCgCoNeighborTexel);
                minNeighbor = min(minNeighbor, yCgCoNeighborTexel);
                average += neighborTexel / 9.0;
            }
        }
        // AABB 截断颜色
        vec3 rgbMaxNeighbor = yCgCoToRgb(maxNeighbor);
        vec3 rgbMinNeighbor = yCgCoToRgb(minNeighbor);

        vec3 p_clip = 0.5 * (rgbMaxNeighbor + rgbMinNeighbor);
        vec3 e_clip = 0.5 * (rgbMaxNeighbor - rgbMinNeighbor);
        vec3 v_clip = oldTexel.xyz - p_clip;
        vec3 v_unit = v_clip.xyz / e_clip;
        vec3 a_unit = abs(v_unit);
        float ma_unit = max(a_unit.x, max(a_unit.y, a_unit.z));
        if (ma_unit > 1.0) oldTexel.rgb = p_clip + v_clip / ma_unit;

        vec4 compositeColor = mix(oldTexel, texel, min(weight + length(speed.xy), 1.0));
        // vec4 compositeColor = mix(oldTexel, texel, min(weight + length(speed.xy), 1.0) * distance(average, texel));
        gl_FragColor = compositeColor;
    }
  `
  )
}, Aee = {
  uniforms: {
    tDiffuse: { value: null },
    exposure: { value: 0 },
    hue: { value: 0 },
    opacity: { value: 0 },
    colorFilter: { value: new _e("#000") }
  },
  vertexShader: (
    /* glsl */
    `

		varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `

		uniform sampler2D tDiffuse;
		uniform float exposure;
		uniform float hue;
		uniform float opacity;
		uniform vec3 colorFilter;

		varying vec2 vUv;

		vec3 hueAdjust(vec3 color, float hue) {
			// (sqrt(3) / 3)
			const vec3 k = vec3(0.57735, 0.57735, 0.57735);
			float cosAngle = cos(hue);
			return vec3(color * cosAngle + cross(k, color) * sin(hue) + k * dot(k, color) * (1.0 - cosAngle));
		}

		void main() {

			vec4 tex = texture2D( tDiffuse, vUv );
			vec3 result_color = tex.rgb;
			result_color *= (1.0 + exposure);
			result_color -= colorFilter;

			result_color = hueAdjust(result_color, hue);
			gl_FragColor = vec4(result_color, tex.a);
		}`
  )
}, Sm = {
  defines: {
    PERSPECTIVE_CAMERA: 1,
    KERNEL_SIZE: 32
  },
  uniforms: {
    tDiffuse: { value: null },
    tNormal: { value: null },
    tDepth: { value: null },
    tNoise: { value: null },
    kernel: { value: null },
    cameraNear: { value: null },
    cameraFar: { value: null },
    resolution: { value: new Se() },
    cameraProjectionMatrix: { value: new et() },
    cameraInverseProjectionMatrix: { value: new et() },
    kernelRadius: { value: 8 },
    minDistance: { value: 5e-3 },
    maxDistance: { value: 0.05 },
    intensity: { value: 1 }
  },
  vertexShader: (
    /* glsl */
    `
    varying vec2 vUv;

    void main() {

      vUv = uv;

      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

    }
  `
  ),
  fragmentShader: (
    /* glsl */
    `
    uniform sampler2D tDiffuse;
      uniform sampler2D tNormal;
      uniform sampler2D tDepth;
      uniform sampler2D tNoise;
      uniform float intensity;

      uniform vec3 kernel[ KERNEL_SIZE ];

      uniform vec2 resolution;

      uniform float cameraNear;
      uniform float cameraFar;
      uniform mat4 cameraProjectionMatrix;
      uniform mat4 cameraInverseProjectionMatrix;

      uniform float kernelRadius;
      uniform float minDistance; // avoid artifacts caused by neighbour fragments with minimal depth difference
      uniform float maxDistance; // avoid the influence of fragments which are too far away

      varying vec2 vUv;

      #include <packing>

      float getDepth( const in vec2 screenPosition ) {

        return texture2D( tDepth, screenPosition ).x;

      }

      float getLinearDepth( const in vec2 screenPosition ) {

        #if PERSPECTIVE_CAMERA == 1

          float fragCoordZ = texture2D( tDepth, screenPosition ).x;
          float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
          return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );

        #else

          return texture2D( tDepth, screenPosition ).x;

        #endif

      }

      float getViewZ( const in float depth ) {

        #if PERSPECTIVE_CAMERA == 1

          return perspectiveDepthToViewZ( depth, cameraNear, cameraFar );

        #else

          return orthographicDepthToViewZ( depth, cameraNear, cameraFar );

        #endif

      }

      vec3 getViewPosition( const in vec2 screenPosition, const in float depth, const in float viewZ ) {

        float clipW = cameraProjectionMatrix[2][3] * viewZ + cameraProjectionMatrix[3][3];

        vec4 clipPosition = vec4( ( vec3( screenPosition, depth ) - 0.5 ) * 2.0, 1.0 );

        clipPosition *= clipW; // unprojection.

        return ( cameraInverseProjectionMatrix * clipPosition ).xyz;

      }

      vec3 getViewNormal( const in vec2 screenPosition ) {

        return unpackRGBToNormal( texture2D( tNormal, screenPosition ).xyz );

      }

      void main() {

        float depth = getDepth( vUv );
        float viewZ = getViewZ( depth );

        vec3 viewPosition = getViewPosition( vUv, depth, viewZ );
        vec3 viewNormal = getViewNormal( vUv );

        vec2 noiseScale = vec2( resolution.x / 4.0, resolution.y / 4.0 );
        vec3 random = vec3( texture2D( tNoise, vUv * noiseScale ).r );

        // compute matrix used to reorient a kernel vector

        vec3 tangent = normalize( random - viewNormal * dot( random, viewNormal ) );
        vec3 bitangent = cross( viewNormal, tangent );
        mat3 kernelMatrix = mat3( tangent, bitangent, viewNormal );

      float occlusion = 0.0;

      for ( int i = 0; i < KERNEL_SIZE; i ++ ) {

          vec3 sampleVector = kernelMatrix * kernel[ i ]; // reorient sample vector in view space
          vec3 samplePoint = viewPosition + ( sampleVector * kernelRadius ); // calculate sample point

          vec4 samplePointNDC = cameraProjectionMatrix * vec4( samplePoint, 1.0 ); // project point and calculate NDC
          samplePointNDC /= samplePointNDC.w;

          vec2 samplePointUv = samplePointNDC.xy * 0.5 + 0.5; // compute uv coordinates

          float realDepth = getLinearDepth( samplePointUv ); // get linear depth from depth texture
          float sampleDepth = viewZToOrthographicDepth( samplePoint.z, cameraNear, cameraFar ); // compute linear depth of the sample view Z value
          float delta = sampleDepth - realDepth;

          if ( delta > minDistance && delta < maxDistance ) { // if fragment is before sample point, increase occlusion

            occlusion += 1.0;

          }

        }

        occlusion /= intensity;
        occlusion = clamp( occlusion / float( KERNEL_SIZE ), 0.0, 1.0 );

        gl_FragColor = vec4( vec3( 1.0 - occlusion ), 1.0 );

      }
  `
  )
}, Wv = {
  defines: {
    PERSPECTIVE_CAMERA: 1
  },
  uniforms: {
    tDepth: { value: null },
    depthOffset: { value: 0 },
    cameraNear: { value: null },
    cameraFar: { value: null }
  },
  vertexShader: `
    varying vec2 vUv;

    void main() {

      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

    }
  `,
  fragmentShader: `
  uniform sampler2D tDepth;

		uniform float cameraNear;
		uniform float cameraFar;
		uniform float depthOffset;

		varying vec2 vUv;

		#include <packing>

		float getLinearDepth( const in vec2 screenPosition ) {

			#if PERSPECTIVE_CAMERA == 1

				float fragCoordZ = texture2D( tDepth, screenPosition ).x;
				float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
				return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );

			#else

				return texture2D( tDepth, screenPosition ).x;

			#endif

		}

		void main() {

			float depth = getLinearDepth( vUv );
			gl_FragColor = vec4( vec3( depthOffset + 1.0 - depth ), 1.0 );

		}
  `
}, I5 = {
  uniforms: {
    tDiffuse: { value: null },
    resolution: { value: new Se() },
    blur: { value: 2 }
  },
  vertexShader: `
    varying vec2 vUv;

    void main() {

      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;

    uniform vec2 resolution;
    uniform int blur;

    varying vec2 vUv;

    void main() {

      vec2 texelSize = ( 1.0 / resolution );
      float result = 0.0;

      for ( int i = - blur; i <= blur; i ++ ) {

        for ( int j = - blur; j <= blur; j ++ ) {

          vec2 offset = ( vec2( float( i ), float( j ) ) ) * texelSize;
          result += texture2D( tDiffuse, vUv + offset ).r;

        }

      }
      float r = float(blur * 2 + 1);
      gl_FragColor = vec4( vec3( result / ( r * r ) ), 1.0 );

    }
  `
}, JO = (
  /* glsl */
  `
  varying float fogDepth;
  uniform vec3 red;
  uniform vec3 green;
  uniform vec3 blue;
  uniform float mode;
  uniform vec2 offset;
  varying vec3 color;
  void main() {
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

    fogDepth = -(modelViewMatrix * vec4(position, 1.0)).z;

    if (position.x == offset.x ) {
      color = mode == 10.0 ? green : blue;
    } else if (position.z == offset.y ) {
      color = mode == 8.0 ? green : red;
    } else {
      color = vec3(0.5);
    }
  }
`
), QO = (
  /* glsl */
  `
  varying float fogDepth;
  uniform float angleFadeFactor;
  uniform float opacityFactor;
  uniform float gridScale;
  varying vec3 color;
  void main() {

    float fogAmount = smoothstep(500.0*gridScale, 4500.0*gridScale, fogDepth);
    vec4 solidColor = vec4(color, angleFadeFactor * opacityFactor) *vec4(0.5);
    vec4 fogColor = vec4(color, 0.0);
    gl_FragColor = mix(solidColor, fogColor, fogAmount);  

  }
`
), bee = {
  uniforms: {
    angleFadeFactor: { value: 1 },
    opacityFactor: { value: 0.6 },
    gridScale: { value: 1 },
    red: { value: new _e(0.902, 0.392, 0.373) },
    blue: { value: new _e(0.227, 0.537, 0.996) },
    green: { value: new _e(0.153, 0.761, 0.275) },
    mode: { value: 9 },
    offset: { value: new Se() }
  },
  vertexShader: JO,
  fragmentShader: QO
}, Mee = {
  uniforms: {
    angleFadeFactor: { value: 1 },
    opacityFactor: { value: 0.4 },
    gridScale: { value: 1 },
    mode: { value: 9 }
  },
  vertexShader: JO,
  fragmentShader: QO
}, hC = (
  /* glsl */
  `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  }
`
), See = (
  /* glsl */
  `
  uniform float time;
  // uniform float offset;
  uniform vec3 color;
  uniform vec3 routeColor;
  uniform vec3 flowStartColor;
  uniform vec3 flowEndColor;
  uniform float lineLength;
  uniform float flowStartOpacity;
  uniform float flowEndOpacity;
  uniform float routeOpacity;
  varying vec2 vUv;
  
  void main() {
    vec2 uv = vUv;
    float start = time * 0.0005;
    start = fract(start) * (1.0 + lineLength) - lineLength;
    float end = start + lineLength;
    float offset = end - vUv.x;
    float intensity = 1.0;
    intensity = 1.0 - abs(offset / lineLength);
    if(offset < 0.0 || offset > lineLength) {
      gl_FragColor = vec4(routeColor, routeOpacity);
    }
    else {
      float opacity = mix(flowEndOpacity, flowStartOpacity, intensity);
      vec3 color = mix(flowEndColor, flowStartColor, intensity);
      gl_FragColor = vec4(mix(routeColor, color, opacity), opacity);
    }
  }
`
), wee = (
  /* glsl */
  `
  varying vec2 vUv;
  uniform float time;
  uniform float offsetX;
  uniform float count;
  uniform sampler2D routeTexture;
  uniform float rotationAngle;

  void main(){
    vec2 texCoord = vec2(vUv.x, 1.0 - vUv.y); // Flip y-coordinate if needed
    texCoord.x = fract(texCoord.x*count - offsetX * time);
    // Calculate the center of rotation
    vec2 center = vec2(0.5, 0.5);
  
    // Perform rotation
    float cosTheta = cos(rotationAngle);
    float sinTheta = sin(rotationAngle);
    mat2 rotationMatrix = mat2(cosTheta, -sinTheta, sinTheta, cosTheta);
    vec2 rotatedTexCoord = rotationMatrix * (texCoord - center) + center;

    gl_FragColor = texture2D( routeTexture, rotatedTexCoord);
  }
`
);
let Eee = class {
  constructor(e) {
    S(this, "_mesh");
    S(this, "_camera");
    this._camera = new Xn(-1, 1, 1, -1, 0, 1);
    const t = new $n(2, 2);
    this._mesh = new Re(t, e);
  }
  get material() {
    return this._mesh.material;
  }
  set material(e) {
    this._mesh.material = e;
  }
  render(e) {
    e.render(this._mesh, this._camera);
  }
  dispose() {
  }
};
class Tee {
  constructor(e) {
    S(this, "_lastFrameProjection");
    S(this, "_lastFrameView");
    S(this, "_renderCallback");
    S(this, "_modelMatrices", /* @__PURE__ */ new Map());
    S(this, "_baseMaterial");
    S(this, "_cachedMotionMaterials", /* @__PURE__ */ new Map());
    this.renderer = e, this._baseMaterial = new Jt({
      side: ui,
      fragmentShader: lC.fragmentShader,
      vertexShader: lC.vertexShader
    }), this._lastFrameView = e.mainCamera.matrixWorldInverse.clone(), this._lastFrameProjection = e.mainCamera.projectionMatrix.clone(), this._renderCallback = e.onPostRender(() => {
      this._lastFrameView = e.mainCamera.matrixWorldInverse.clone(), this._lastFrameProjection = e.mainCamera.projectionMatrix.clone(), e.rootScene.updateMatrixWorld(!1), e.rootScene.traverse((t) => {
        this._modelMatrices.set(t.id, t.matrixWorld.clone());
      });
    });
  }
  dispose() {
    this._renderCallback.cancel && this._renderCallback.cancel(), this._cachedMotionMaterials.forEach((e, t) => {
      e.dispose();
    });
  }
  renderMotionMap(e, t, i) {
    const { renderer: n } = this, r = n.mainCamera;
    i.traverse((a) => {
      const o = a;
      if (!o.material || o.isTransformControls)
        return;
      o.oldMaterial = o.material;
      const l = this._cachedMotionMaterials.get(a.id);
      l ? o.material = l : (o.material = this._baseMaterial.clone(), this._cachedMotionMaterials.set(a.id, a.material));
      const { uniforms: c } = a.material;
      c.colorScale = { value: e }, c.lastFrameModel = { value: this._modelMatrices.get(a.id) || a.matrixWorld }, c.lastFrameView = { value: this._lastFrameView }, c.lastFrameProjection = { value: this._lastFrameProjection };
    }), n.threeRenderer.setRenderTarget(t), n.threeRenderer.render(i, r), i.traverse((a) => {
      const o = a;
      !o.material || o.isTransformControls || o.material && (o.material = o.oldMaterial, delete o.oldMaterial);
    });
  }
}
const ia = {
  name: "CopyShader",
  uniforms: {
    tDiffuse: { value: null },
    opacity: { value: 1 }
  },
  vertexShader: (
    /* glsl */
    `

		varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `

		uniform float opacity;

		uniform sampler2D tDiffuse;

		varying vec2 vUv;

		void main() {

			vec4 texel = texture2D( tDiffuse, vUv );
			gl_FragColor = opacity * texel;


		}`
  )
};
class Ao {
  constructor() {
    this.isPass = !0, this.enabled = !0, this.needsSwap = !0, this.clear = !1, this.renderToScreen = !1;
  }
  setSize() {
  }
  render() {
    console.error("THREE.Pass: .render() must be implemented in derived pass.");
  }
  dispose() {
  }
}
const Cee = new Xn(-1, 1, 1, -1, 0, 1);
class _ee extends Pt {
  constructor() {
    super(), this.setAttribute("position", new gt([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3)), this.setAttribute("uv", new gt([0, 2, 0, 0, 2, 0], 2));
  }
}
const Pee = new _ee();
let P2 = class {
  constructor(e) {
    this._mesh = new Re(Pee, e);
  }
  dispose() {
    this._mesh.geometry.dispose();
  }
  render(e) {
    e.render(this._mesh, Cee);
  }
  get material() {
    return this._mesh.material;
  }
  set material(e) {
    this._mesh.material = e;
  }
};
class Z4 extends Ao {
  constructor(e, t) {
    super(), this.textureID = t !== void 0 ? t : "tDiffuse", e instanceof Jt ? (this.uniforms = e.uniforms, this.material = e) : e && (this.uniforms = rs.clone(e.uniforms), this.material = new Jt({
      name: e.name !== void 0 ? e.name : "unspecified",
      defines: Object.assign({}, e.defines),
      uniforms: this.uniforms,
      vertexShader: e.vertexShader,
      fragmentShader: e.fragmentShader
    })), this.fsQuad = new P2(this.material);
  }
  render(e, t, i) {
    this.uniforms[this.textureID] && (this.uniforms[this.textureID].value = i.texture), this.fsQuad.material = this.material, this.renderToScreen ? (e.setRenderTarget(null), this.fsQuad.render(e)) : (e.setRenderTarget(t), this.clear && e.clear(e.autoClearColor, e.autoClearDepth, e.autoClearStencil), this.fsQuad.render(e));
  }
  dispose() {
    this.material.dispose(), this.fsQuad.dispose();
  }
}
class uC extends Ao {
  constructor(e, t) {
    super(), this.scene = e, this.camera = t, this.clear = !0, this.needsSwap = !1, this.inverse = !1;
  }
  render(e, t, i) {
    const n = e.getContext(), r = e.state;
    r.buffers.color.setMask(!1), r.buffers.depth.setMask(!1), r.buffers.color.setLocked(!0), r.buffers.depth.setLocked(!0);
    let a, o;
    this.inverse ? (a = 0, o = 1) : (a = 1, o = 0), r.buffers.stencil.setTest(!0), r.buffers.stencil.setOp(n.REPLACE, n.REPLACE, n.REPLACE), r.buffers.stencil.setFunc(n.ALWAYS, a, 4294967295), r.buffers.stencil.setClear(o), r.buffers.stencil.setLocked(!0), e.setRenderTarget(i), this.clear && e.clear(), e.render(this.scene, this.camera), e.setRenderTarget(t), this.clear && e.clear(), e.render(this.scene, this.camera), r.buffers.color.setLocked(!1), r.buffers.depth.setLocked(!1), r.buffers.color.setMask(!0), r.buffers.depth.setMask(!0), r.buffers.stencil.setLocked(!1), r.buffers.stencil.setFunc(n.EQUAL, 1, 4294967295), r.buffers.stencil.setOp(n.KEEP, n.KEEP, n.KEEP), r.buffers.stencil.setLocked(!0);
  }
}
class Ree extends Ao {
  constructor() {
    super(), this.needsSwap = !1;
  }
  render(e) {
    e.state.buffers.stencil.setLocked(!1), e.state.buffers.stencil.setTest(!1);
  }
}
class dC {
  constructor(e, t) {
    if (this.renderer = e, this._pixelRatio = e.getPixelRatio(), t === void 0) {
      const i = e.getSize(new Se());
      this._width = i.width, this._height = i.height, t = new Xi(this._width * this._pixelRatio, this._height * this._pixelRatio, { type: on }), t.texture.name = "EffectComposer.rt1";
    } else
      this._width = t.width, this._height = t.height;
    this.renderTarget1 = t, this.renderTarget2 = t.clone(), this.renderTarget2.texture.name = "EffectComposer.rt2", this.writeBuffer = this.renderTarget1, this.readBuffer = this.renderTarget2, this.renderToScreen = !0, this.passes = [], this.copyPass = new Z4(ia), this.copyPass.material.blending = vn, this.clock = new Ju();
  }
  swapBuffers() {
    const e = this.readBuffer;
    this.readBuffer = this.writeBuffer, this.writeBuffer = e;
  }
  addPass(e) {
    this.passes.push(e), e.setSize(this._width * this._pixelRatio, this._height * this._pixelRatio);
  }
  insertPass(e, t) {
    this.passes.splice(t, 0, e), e.setSize(this._width * this._pixelRatio, this._height * this._pixelRatio);
  }
  removePass(e) {
    const t = this.passes.indexOf(e);
    t !== -1 && this.passes.splice(t, 1);
  }
  isLastEnabledPass(e) {
    for (let t = e + 1; t < this.passes.length; t++)
      if (this.passes[t].enabled)
        return !1;
    return !0;
  }
  render(e) {
    e === void 0 && (e = this.clock.getDelta());
    const t = this.renderer.getRenderTarget();
    let i = !1;
    for (let n = 0, r = this.passes.length; n < r; n++) {
      const a = this.passes[n];
      if (a.enabled !== !1) {
        if (a.renderToScreen = this.renderToScreen && this.isLastEnabledPass(n), a.render(this.renderer, this.writeBuffer, this.readBuffer, e, i), a.needsSwap) {
          if (i) {
            const o = this.renderer.getContext(), l = this.renderer.state.buffers.stencil;
            l.setFunc(o.NOTEQUAL, 1, 4294967295), this.copyPass.render(this.renderer, this.writeBuffer, this.readBuffer, e), l.setFunc(o.EQUAL, 1, 4294967295);
          }
          this.swapBuffers();
        }
        uC !== void 0 && (a instanceof uC ? i = !0 : a instanceof Ree && (i = !1));
      }
    }
    this.renderer.setRenderTarget(t);
  }
  reset(e) {
    if (e === void 0) {
      const t = this.renderer.getSize(new Se());
      this._pixelRatio = this.renderer.getPixelRatio(), this._width = t.width, this._height = t.height, e = this.renderTarget1.clone(), e.setSize(this._width * this._pixelRatio, this._height * this._pixelRatio);
    }
    this.renderTarget1.dispose(), this.renderTarget2.dispose(), this.renderTarget1 = e, this.renderTarget2 = e.clone(), this.writeBuffer = this.renderTarget1, this.readBuffer = this.renderTarget2;
  }
  setSize(e, t) {
    this._width = e, this._height = t;
    const i = this._width * this._pixelRatio, n = this._height * this._pixelRatio;
    this.renderTarget1.setSize(i, n), this.renderTarget2.setSize(i, n);
    for (let r = 0; r < this.passes.length; r++)
      this.passes[r].setSize(i, n);
  }
  setPixelRatio(e) {
    this._pixelRatio = e, this.setSize(this._width, this._height);
  }
  dispose() {
    this.renderTarget1.dispose(), this.renderTarget2.dispose(), this.copyPass.dispose();
  }
}
class Dee extends Ao {
  constructor(e, t, i = null, n = null, r = null) {
    super(), this.scene = e, this.camera = t, this.overrideMaterial = i, this.clearColor = n, this.clearAlpha = r, this.clear = !0, this.clearDepth = !1, this.needsSwap = !1, this._oldClearColor = new _e();
  }
  render(e, t, i) {
    const n = e.autoClear;
    e.autoClear = !1;
    let r, a;
    this.overrideMaterial !== null && (a = this.scene.overrideMaterial, this.scene.overrideMaterial = this.overrideMaterial), this.clearColor !== null && (e.getClearColor(this._oldClearColor), e.setClearColor(this.clearColor, e.getClearAlpha())), this.clearAlpha !== null && (r = e.getClearAlpha(), e.setClearAlpha(this.clearAlpha)), this.clearDepth == !0 && e.clearDepth(), e.setRenderTarget(this.renderToScreen ? null : i), this.clear === !0 && e.clear(e.autoClearColor, e.autoClearDepth, e.autoClearStencil), e.render(this.scene, this.camera), this.clearColor !== null && e.setClearColor(this._oldClearColor), this.clearAlpha !== null && e.setClearAlpha(r), this.overrideMaterial !== null && (this.scene.overrideMaterial = a), e.autoClear = n;
  }
}
function up(s, e) {
  if (e === CL)
    return console.warn("THREE.BufferGeometryUtils.toTrianglesDrawMode(): Geometry already defined as triangles."), s;
  if (e === ip || e === i2) {
    let t = s.getIndex();
    if (t === null) {
      const a = [], o = s.getAttribute("position");
      if (o !== void 0) {
        for (let l = 0; l < o.count; l++)
          a.push(l);
        s.setIndex(a), t = s.getIndex();
      } else
        return console.error("THREE.BufferGeometryUtils.toTrianglesDrawMode(): Undefined position attribute. Processing not possible."), s;
    }
    const i = t.count - 2, n = [];
    if (e === ip)
      for (let a = 1; a <= i; a++)
        n.push(t.getX(0)), n.push(t.getX(a)), n.push(t.getX(a + 1));
    else
      for (let a = 0; a < i; a++)
        a % 2 === 0 ? (n.push(t.getX(a)), n.push(t.getX(a + 1)), n.push(t.getX(a + 2))) : (n.push(t.getX(a + 2)), n.push(t.getX(a + 1)), n.push(t.getX(a)));
    n.length / 3 !== i && console.error("THREE.BufferGeometryUtils.toTrianglesDrawMode(): Unable to generate correct amount of triangles.");
    const r = s.clone();
    return r.setIndex(n), r.clearGroups(), r;
  } else
    return console.error("THREE.BufferGeometryUtils.toTrianglesDrawMode(): Unknown draw mode:", e), s;
}
let K4 = class extends As {
  constructor(e) {
    super(e), this.dracoLoader = null, this.ktx2Loader = null, this.meshoptDecoder = null, this.pluginCallbacks = [], this.register(function(t) {
      return new Bee(t);
    }), this.register(function(t) {
      return new Fee(t);
    }), this.register(function(t) {
      return new jee(t);
    }), this.register(function(t) {
      return new qee(t);
    }), this.register(function(t) {
      return new Yee(t);
    }), this.register(function(t) {
      return new zee(t);
    }), this.register(function(t) {
      return new kee(t);
    }), this.register(function(t) {
      return new Gee(t);
    }), this.register(function(t) {
      return new Hee(t);
    }), this.register(function(t) {
      return new Nee(t);
    }), this.register(function(t) {
      return new Vee(t);
    }), this.register(function(t) {
      return new Uee(t);
    }), this.register(function(t) {
      return new Xee(t);
    }), this.register(function(t) {
      return new Wee(t);
    }), this.register(function(t) {
      return new Iee(t);
    }), this.register(function(t) {
      return new Zee(t);
    }), this.register(function(t) {
      return new Kee(t);
    });
  }
  load(e, t, i, n) {
    const r = this;
    let a;
    if (this.resourcePath !== "")
      a = this.resourcePath;
    else if (this.path !== "") {
      const c = Rs.extractUrlBase(e);
      a = Rs.resolveURL(c, this.path);
    } else
      a = Rs.extractUrlBase(e);
    this.manager.itemStart(e);
    const o = function(c) {
      n ? n(c) : console.error(c), r.manager.itemError(e), r.manager.itemEnd(e);
    }, l = new Nn(this.manager);
    l.setPath(this.path), l.setResponseType("arraybuffer"), l.setRequestHeader(this.requestHeader), l.setWithCredentials(this.withCredentials), l.load(e, function(c) {
      try {
        r.parse(c, a, function(h) {
          t(h), r.manager.itemEnd(e);
        }, o);
      } catch (h) {
        o(h);
      }
    }, i, o);
  }
  setDRACOLoader(e) {
    return this.dracoLoader = e, this;
  }
  setDDSLoader() {
    throw new Error(
      'THREE.GLTFLoader: "MSFT_texture_dds" no longer supported. Please update to "KHR_texture_basisu".'
    );
  }
  setKTX2Loader(e) {
    return this.ktx2Loader = e, this;
  }
  setMeshoptDecoder(e) {
    return this.meshoptDecoder = e, this;
  }
  register(e) {
    return this.pluginCallbacks.indexOf(e) === -1 && this.pluginCallbacks.push(e), this;
  }
  unregister(e) {
    return this.pluginCallbacks.indexOf(e) !== -1 && this.pluginCallbacks.splice(this.pluginCallbacks.indexOf(e), 1), this;
  }
  parse(e, t, i, n) {
    let r;
    const a = {}, o = {}, l = new TextDecoder();
    if (typeof e == "string")
      r = JSON.parse(e);
    else if (e instanceof ArrayBuffer)
      if (l.decode(new Uint8Array(e, 0, 4)) === $O) {
        try {
          a[Ti.KHR_BINARY_GLTF] = new Jee(e);
        } catch (u) {
          n && n(u);
          return;
        }
        r = JSON.parse(a[Ti.KHR_BINARY_GLTF].content);
      } else
        r = JSON.parse(l.decode(e));
    else
      r = e;
    if (r.asset === void 0 || r.asset.version[0] < 2) {
      n && n(new Error("THREE.GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported."));
      return;
    }
    const c = new hte(r, {
      path: t || this.resourcePath || "",
      crossOrigin: this.crossOrigin,
      requestHeader: this.requestHeader,
      manager: this.manager,
      ktx2Loader: this.ktx2Loader,
      meshoptDecoder: this.meshoptDecoder
    });
    c.fileLoader.setRequestHeader(this.requestHeader);
    for (let h = 0; h < this.pluginCallbacks.length; h++) {
      const u = this.pluginCallbacks[h](c);
      u.name || console.error("THREE.GLTFLoader: Invalid plugin found: missing name"), o[u.name] = u, a[u.name] = !0;
    }
    if (r.extensionsUsed)
      for (let h = 0; h < r.extensionsUsed.length; ++h) {
        const u = r.extensionsUsed[h], d = r.extensionsRequired || [];
        switch (u) {
          case Ti.KHR_MATERIALS_UNLIT:
            a[u] = new Oee();
            break;
          case Ti.KHR_DRACO_MESH_COMPRESSION:
            a[u] = new Qee(r, this.dracoLoader);
            break;
          case Ti.KHR_TEXTURE_TRANSFORM:
            a[u] = new $ee();
            break;
          case Ti.KHR_MESH_QUANTIZATION:
            a[u] = new ete();
            break;
          default:
            d.indexOf(u) >= 0 && o[u] === void 0 && console.warn('THREE.GLTFLoader: Unknown extension "' + u + '".');
        }
      }
    c.setExtensions(a), c.setPlugins(o), c.parse(i, n);
  }
  parseAsync(e, t) {
    const i = this;
    return new Promise(function(n, r) {
      i.parse(e, t, n, r);
    });
  }
};
function Lee() {
  let s = {};
  return {
    get: function(e) {
      return s[e];
    },
    add: function(e, t) {
      s[e] = t;
    },
    remove: function(e) {
      delete s[e];
    },
    removeAll: function() {
      s = {};
    }
  };
}
const Ti = {
  KHR_BINARY_GLTF: "KHR_binary_glTF",
  KHR_DRACO_MESH_COMPRESSION: "KHR_draco_mesh_compression",
  KHR_LIGHTS_PUNCTUAL: "KHR_lights_punctual",
  KHR_MATERIALS_CLEARCOAT: "KHR_materials_clearcoat",
  KHR_MATERIALS_DISPERSION: "KHR_materials_dispersion",
  KHR_MATERIALS_IOR: "KHR_materials_ior",
  KHR_MATERIALS_SHEEN: "KHR_materials_sheen",
  KHR_MATERIALS_SPECULAR: "KHR_materials_specular",
  KHR_MATERIALS_TRANSMISSION: "KHR_materials_transmission",
  KHR_MATERIALS_IRIDESCENCE: "KHR_materials_iridescence",
  KHR_MATERIALS_ANISOTROPY: "KHR_materials_anisotropy",
  KHR_MATERIALS_UNLIT: "KHR_materials_unlit",
  KHR_MATERIALS_VOLUME: "KHR_materials_volume",
  KHR_TEXTURE_BASISU: "KHR_texture_basisu",
  KHR_TEXTURE_TRANSFORM: "KHR_texture_transform",
  KHR_MESH_QUANTIZATION: "KHR_mesh_quantization",
  KHR_MATERIALS_EMISSIVE_STRENGTH: "KHR_materials_emissive_strength",
  EXT_MATERIALS_BUMP: "EXT_materials_bump",
  EXT_TEXTURE_WEBP: "EXT_texture_webp",
  EXT_TEXTURE_AVIF: "EXT_texture_avif",
  EXT_MESHOPT_COMPRESSION: "EXT_meshopt_compression",
  EXT_MESH_GPU_INSTANCING: "EXT_mesh_gpu_instancing"
};
let Iee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_LIGHTS_PUNCTUAL, this.cache = { refs: {}, uses: {} };
  }
  _markDefs() {
    const e = this.parser, t = this.parser.json.nodes || [];
    for (let i = 0, n = t.length; i < n; i++) {
      const r = t[i];
      r.extensions && r.extensions[this.name] && r.extensions[this.name].light !== void 0 && e._addNodeRef(this.cache, r.extensions[this.name].light);
    }
  }
  _loadLight(e) {
    const t = this.parser, i = "light:" + e;
    let n = t.cache.get(i);
    if (n)
      return n;
    const r = t.json, l = ((r.extensions && r.extensions[this.name] || {}).lights || [])[e];
    let c;
    const h = new _e(16777215);
    l.color !== void 0 && h.setRGB(l.color[0], l.color[1], l.color[2], Si);
    const u = l.range !== void 0 ? l.range : 0;
    switch (l.type) {
      case "directional":
        c = new _a(h), c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      case "point":
        c = new Tl(h), c.distance = u;
        break;
      case "spot":
        c = new oh(h), c.distance = u, l.spot = l.spot || {}, l.spot.innerConeAngle = l.spot.innerConeAngle !== void 0 ? l.spot.innerConeAngle : 0, l.spot.outerConeAngle = l.spot.outerConeAngle !== void 0 ? l.spot.outerConeAngle : Math.PI / 4, c.angle = l.spot.outerConeAngle, c.penumbra = 1 - l.spot.innerConeAngle / l.spot.outerConeAngle, c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      default:
        throw new Error("THREE.GLTFLoader: Unexpected light type: " + l.type);
    }
    return c.position.set(0, 0, 0), c.decay = 2, j0(c, l), l.intensity !== void 0 && (c.intensity = l.intensity), c.name = t.createUniqueName(l.name || "light_" + e), n = Promise.resolve(c), t.cache.add(i, n), n;
  }
  getDependency(e, t) {
    if (e === "light")
      return this._loadLight(t);
  }
  createNodeAttachment(e) {
    const t = this, i = this.parser, r = i.json.nodes[e], o = (r.extensions && r.extensions[this.name] || {}).light;
    return o === void 0 ? null : this._loadLight(o).then(function(l) {
      return i._getNodeRef(t.cache, o, l);
    });
  }
}, Oee = class {
  constructor() {
    this.name = Ti.KHR_MATERIALS_UNLIT;
  }
  getMaterialType() {
    return kt;
  }
  extendParams(e, t, i) {
    const n = [];
    e.color = new _e(1, 1, 1), e.opacity = 1;
    const r = t.pbrMetallicRoughness;
    if (r) {
      if (Array.isArray(r.baseColorFactor)) {
        const a = r.baseColorFactor;
        e.color.setRGB(a[0], a[1], a[2], Si), e.opacity = a[3];
      }
      r.baseColorTexture !== void 0 && n.push(i.assignTexture(e, "map", r.baseColorTexture, qi));
    }
    return Promise.all(n);
  }
}, Nee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_EMISSIVE_STRENGTH;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name].emissiveStrength;
    return r !== void 0 && (t.emissiveIntensity = r), Promise.resolve();
  }
}, Bee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_CLEARCOAT;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    if (a.clearcoatFactor !== void 0 && (t.clearcoat = a.clearcoatFactor), a.clearcoatTexture !== void 0 && r.push(i.assignTexture(t, "clearcoatMap", a.clearcoatTexture)), a.clearcoatRoughnessFactor !== void 0 && (t.clearcoatRoughness = a.clearcoatRoughnessFactor), a.clearcoatRoughnessTexture !== void 0 && r.push(i.assignTexture(t, "clearcoatRoughnessMap", a.clearcoatRoughnessTexture)), a.clearcoatNormalTexture !== void 0 && (r.push(i.assignTexture(t, "clearcoatNormalMap", a.clearcoatNormalTexture)), a.clearcoatNormalTexture.scale !== void 0)) {
      const o = a.clearcoatNormalTexture.scale;
      t.clearcoatNormalScale = new Se(o, o);
    }
    return Promise.all(r);
  }
};
class Fee {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_DISPERSION;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name];
    return t.dispersion = r.dispersion !== void 0 ? r.dispersion : 0, Promise.resolve();
  }
}
let Uee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_IRIDESCENCE;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.iridescenceFactor !== void 0 && (t.iridescence = a.iridescenceFactor), a.iridescenceTexture !== void 0 && r.push(i.assignTexture(t, "iridescenceMap", a.iridescenceTexture)), a.iridescenceIor !== void 0 && (t.iridescenceIOR = a.iridescenceIor), t.iridescenceThicknessRange === void 0 && (t.iridescenceThicknessRange = [100, 400]), a.iridescenceThicknessMinimum !== void 0 && (t.iridescenceThicknessRange[0] = a.iridescenceThicknessMinimum), a.iridescenceThicknessMaximum !== void 0 && (t.iridescenceThicknessRange[1] = a.iridescenceThicknessMaximum), a.iridescenceThicknessTexture !== void 0 && r.push(i.assignTexture(t, "iridescenceThicknessMap", a.iridescenceThicknessTexture)), Promise.all(r);
  }
}, zee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_SHEEN;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [];
    t.sheenColor = new _e(0, 0, 0), t.sheenRoughness = 0, t.sheen = 1;
    const a = n.extensions[this.name];
    if (a.sheenColorFactor !== void 0) {
      const o = a.sheenColorFactor;
      t.sheenColor.setRGB(o[0], o[1], o[2], Si);
    }
    return a.sheenRoughnessFactor !== void 0 && (t.sheenRoughness = a.sheenRoughnessFactor), a.sheenColorTexture !== void 0 && r.push(i.assignTexture(t, "sheenColorMap", a.sheenColorTexture, qi)), a.sheenRoughnessTexture !== void 0 && r.push(i.assignTexture(t, "sheenRoughnessMap", a.sheenRoughnessTexture)), Promise.all(r);
  }
}, kee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_TRANSMISSION;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.transmissionFactor !== void 0 && (t.transmission = a.transmissionFactor), a.transmissionTexture !== void 0 && r.push(i.assignTexture(t, "transmissionMap", a.transmissionTexture)), Promise.all(r);
  }
}, Gee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_VOLUME;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.thickness = a.thicknessFactor !== void 0 ? a.thicknessFactor : 0, a.thicknessTexture !== void 0 && r.push(i.assignTexture(t, "thicknessMap", a.thicknessTexture)), t.attenuationDistance = a.attenuationDistance || 1 / 0;
    const o = a.attenuationColor || [1, 1, 1];
    return t.attenuationColor = new _e().setRGB(o[0], o[1], o[2], Si), Promise.all(r);
  }
}, Hee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_IOR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name];
    return t.ior = r.ior !== void 0 ? r.ior : 1.5, Promise.resolve();
  }
}, Vee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_SPECULAR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.specularIntensity = a.specularFactor !== void 0 ? a.specularFactor : 1, a.specularTexture !== void 0 && r.push(i.assignTexture(t, "specularIntensityMap", a.specularTexture));
    const o = a.specularColorFactor || [1, 1, 1];
    return t.specularColor = new _e().setRGB(o[0], o[1], o[2], Si), a.specularColorTexture !== void 0 && r.push(i.assignTexture(t, "specularColorMap", a.specularColorTexture, qi)), Promise.all(r);
  }
};
class Wee {
  constructor(e) {
    this.parser = e, this.name = Ti.EXT_MATERIALS_BUMP;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return t.bumpScale = a.bumpFactor !== void 0 ? a.bumpFactor : 1, a.bumpTexture !== void 0 && r.push(i.assignTexture(t, "bumpMap", a.bumpTexture)), Promise.all(r);
  }
}
let Xee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_MATERIALS_ANISOTROPY;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.anisotropyStrength !== void 0 && (t.anisotropy = a.anisotropyStrength), a.anisotropyRotation !== void 0 && (t.anisotropyRotation = a.anisotropyRotation), a.anisotropyTexture !== void 0 && r.push(i.assignTexture(t, "anisotropyMap", a.anisotropyTexture)), Promise.all(r);
  }
}, jee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.KHR_TEXTURE_BASISU;
  }
  loadTexture(e) {
    const t = this.parser, i = t.json, n = i.textures[e];
    if (!n.extensions || !n.extensions[this.name])
      return null;
    const r = n.extensions[this.name], a = t.options.ktx2Loader;
    if (!a) {
      if (i.extensionsRequired && i.extensionsRequired.indexOf(this.name) >= 0)
        throw new Error("THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures");
      return null;
    }
    return t.loadTextureImage(e, r.source, a);
  }
}, qee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.EXT_TEXTURE_WEBP, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: WebP required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}, Yee = class {
  constructor(e) {
    this.parser = e, this.name = Ti.EXT_TEXTURE_AVIF, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: AVIF required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}, Zee = class {
  constructor(e) {
    this.name = Ti.EXT_MESHOPT_COMPRESSION, this.parser = e;
  }
  loadBufferView(e) {
    const t = this.parser.json, i = t.bufferViews[e];
    if (i.extensions && i.extensions[this.name]) {
      const n = i.extensions[this.name], r = this.parser.getDependency("buffer", n.buffer), a = this.parser.options.meshoptDecoder;
      if (!a || !a.supported) {
        if (t.extensionsRequired && t.extensionsRequired.indexOf(this.name) >= 0)
          throw new Error("THREE.GLTFLoader: setMeshoptDecoder must be called before loading compressed files");
        return null;
      }
      return r.then(function(o) {
        const l = n.byteOffset || 0, c = n.byteLength || 0, h = n.count, u = n.byteStride, d = new Uint8Array(o, l, c);
        return a.decodeGltfBufferAsync ? a.decodeGltfBufferAsync(h, u, d, n.mode, n.filter).then(function(f) {
          return f.buffer;
        }) : a.ready.then(function() {
          const f = new ArrayBuffer(h * u);
          return a.decodeGltfBuffer(new Uint8Array(f), h, u, d, n.mode, n.filter), f;
        });
      });
    } else
      return null;
  }
}, Kee = class {
  constructor(e) {
    this.name = Ti.EXT_MESH_GPU_INSTANCING, this.parser = e;
  }
  createNodeMesh(e) {
    const t = this.parser.json, i = t.nodes[e];
    if (!i.extensions || !i.extensions[this.name] || i.mesh === void 0)
      return null;
    const n = t.meshes[i.mesh];
    for (const c of n.primitives)
      if (c.mode !== pa.TRIANGLES && c.mode !== pa.TRIANGLE_STRIP && c.mode !== pa.TRIANGLE_FAN && c.mode !== void 0)
        return null;
    const a = i.extensions[this.name].attributes, o = [], l = {};
    for (const c in a)
      o.push(this.parser.getDependency("accessor", a[c]).then((h) => (l[c] = h, l[c])));
    return o.length < 1 ? null : (o.push(this.parser.createNodeMesh(e)), Promise.all(o).then((c) => {
      const h = c.pop(), u = h.isGroup ? h.children : [h], d = c[0].count, f = [];
      for (const p of u) {
        const v = new et(), g = new _(), m = new St(), x = new _(1, 1, 1), y = new ho(p.geometry, p.material, d);
        for (let A = 0; A < d; A++)
          l.TRANSLATION && g.fromBufferAttribute(l.TRANSLATION, A), l.ROTATION && m.fromBufferAttribute(l.ROTATION, A), l.SCALE && x.fromBufferAttribute(l.SCALE, A), y.setMatrixAt(A, v.compose(g, m, x));
        for (const A in l)
          if (A === "_COLOR_0") {
            const b = l[A];
            y.instanceColor = new hs(b.array, b.itemSize, b.normalized);
          } else
            A !== "TRANSLATION" && A !== "ROTATION" && A !== "SCALE" && p.geometry.setAttribute(A, l[A]);
        mt.prototype.copy.call(y, p), this.parser.assignFinalMaterial(y), f.push(y);
      }
      return h.isGroup ? (h.clear(), h.add(...f), h) : f[0];
    }));
  }
};
const $O = "glTF", wm = 12, fC = { JSON: 1313821514, BIN: 5130562 };
let Jee = class {
  constructor(e) {
    this.name = Ti.KHR_BINARY_GLTF, this.content = null, this.body = null;
    const t = new DataView(e, 0, wm), i = new TextDecoder();
    if (this.header = {
      magic: i.decode(new Uint8Array(e.slice(0, 4))),
      version: t.getUint32(4, !0),
      length: t.getUint32(8, !0)
    }, this.header.magic !== $O)
      throw new Error("THREE.GLTFLoader: Unsupported glTF-Binary header.");
    if (this.header.version < 2)
      throw new Error("THREE.GLTFLoader: Legacy binary file detected.");
    const n = this.header.length - wm, r = new DataView(e, wm);
    let a = 0;
    for (; a < n; ) {
      const o = r.getUint32(a, !0);
      a += 4;
      const l = r.getUint32(a, !0);
      if (a += 4, l === fC.JSON) {
        const c = new Uint8Array(e, wm + a, o);
        this.content = i.decode(c);
      } else if (l === fC.BIN) {
        const c = wm + a;
        this.body = e.slice(c, c + o);
      }
      a += o;
    }
    if (this.content === null)
      throw new Error("THREE.GLTFLoader: JSON content not found.");
  }
}, Qee = class {
  constructor(e, t) {
    if (!t)
      throw new Error("THREE.GLTFLoader: No DRACOLoader instance provided.");
    this.name = Ti.KHR_DRACO_MESH_COMPRESSION, this.json = e, this.dracoLoader = t, this.dracoLoader.preload();
  }
  decodePrimitive(e, t) {
    const i = this.json, n = this.dracoLoader, r = e.extensions[this.name].bufferView, a = e.extensions[this.name].attributes, o = {}, l = {}, c = {};
    for (const h in a) {
      const u = gb[h] || h.toLowerCase();
      o[u] = a[h];
    }
    for (const h in e.attributes) {
      const u = gb[h] || h.toLowerCase();
      if (a[h] !== void 0) {
        const d = i.accessors[e.attributes[h]], f = kf[d.componentType];
        c[u] = f.name, l[u] = d.normalized === !0;
      }
    }
    return t.getDependency("bufferView", r).then(function(h) {
      return new Promise(function(u, d) {
        n.decodeDracoFile(h, function(f) {
          for (const p in f.attributes) {
            const v = f.attributes[p], g = l[p];
            g !== void 0 && (v.normalized = g);
          }
          u(f);
        }, o, c, Si, d);
      });
    });
  }
}, $ee = class {
  constructor() {
    this.name = Ti.KHR_TEXTURE_TRANSFORM;
  }
  extendTexture(e, t) {
    return (t.texCoord === void 0 || t.texCoord === e.channel) && t.offset === void 0 && t.rotation === void 0 && t.scale === void 0 || (e = e.clone(), t.texCoord !== void 0 && (e.channel = t.texCoord), t.offset !== void 0 && e.offset.fromArray(t.offset), t.rotation !== void 0 && (e.rotation = t.rotation), t.scale !== void 0 && e.repeat.fromArray(t.scale), e.needsUpdate = !0), e;
  }
}, ete = class {
  constructor() {
    this.name = Ti.KHR_MESH_QUANTIZATION;
  }
}, eN = class extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  copySampleValue_(e) {
    const t = this.resultBuffer, i = this.sampleValues, n = this.valueSize, r = e * n * 3 + n;
    for (let a = 0; a !== n; a++)
      t[a] = i[r + a];
    return t;
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = o * 2, c = o * 3, h = n - t, u = (i - t) / h, d = u * u, f = d * u, p = e * c, v = p - c, g = -2 * f + 3 * d, m = f - d, x = 1 - g, y = m - d + u;
    for (let A = 0; A !== o; A++) {
      const b = a[v + A + o], M = a[v + A + l] * h, w = a[p + A + o], C = a[p + A] * h;
      r[A] = x * b + y * M + g * w + m * C;
    }
    return r;
  }
};
const tte = new St();
let ite = class extends eN {
  interpolate_(e, t, i, n) {
    const r = super.interpolate_(e, t, i, n);
    return tte.fromArray(r).normalize().toArray(r), r;
  }
};
const pa = {
  FLOAT: 5126,
  //FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123
}, kf = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array
}, pC = {
  9728: Ni,
  9729: ii,
  9984: $c,
  9985: $o,
  9986: $a,
  9987: ds
}, mC = {
  33071: yn,
  33648: co,
  10497: un
}, O5 = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16
}, gb = {
  POSITION: "position",
  NORMAL: "normal",
  TANGENT: "tangent",
  TEXCOORD_0: "uv",
  TEXCOORD_1: "uv1",
  TEXCOORD_2: "uv2",
  TEXCOORD_3: "uv3",
  COLOR_0: "color",
  WEIGHTS_0: "skinWeight",
  JOINTS_0: "skinIndex"
}, Hl = {
  scale: "scale",
  translation: "position",
  rotation: "quaternion",
  weights: "morphTargetInfluences"
}, nte = {
  CUBICSPLINE: void 0,
  // We use a custom interpolant (GLTFCubicSplineInterpolation) for CUBICSPLINE tracks. Each
  // keyframe track will be initialized with a default interpolation type, then modified.
  LINEAR: l0,
  STEP: Uc
}, N5 = {
  OPAQUE: "OPAQUE",
  MASK: "MASK",
  BLEND: "BLEND"
};
function ste(s) {
  return s.DefaultMaterial === void 0 && (s.DefaultMaterial = new or({
    color: 16777215,
    emissive: 0,
    metalness: 1,
    roughness: 1,
    transparent: !1,
    depthTest: !0,
    side: sa
  })), s.DefaultMaterial;
}
function Ch(s, e, t) {
  for (const i in t.extensions)
    s[i] === void 0 && (e.userData.gltfExtensions = e.userData.gltfExtensions || {}, e.userData.gltfExtensions[i] = t.extensions[i]);
}
function j0(s, e) {
  e.extras !== void 0 && (typeof e.extras == "object" ? Object.assign(s.userData, e.extras) : console.warn("THREE.GLTFLoader: Ignoring primitive type .extras, " + e.extras));
}
function rte(s, e, t) {
  let i = !1, n = !1, r = !1;
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (u.POSITION !== void 0 && (i = !0), u.NORMAL !== void 0 && (n = !0), u.COLOR_0 !== void 0 && (r = !0), i && n && r)
      break;
  }
  if (!i && !n && !r)
    return Promise.resolve(s);
  const a = [], o = [], l = [];
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (i) {
      const d = u.POSITION !== void 0 ? t.getDependency("accessor", u.POSITION) : s.attributes.position;
      a.push(d);
    }
    if (n) {
      const d = u.NORMAL !== void 0 ? t.getDependency("accessor", u.NORMAL) : s.attributes.normal;
      o.push(d);
    }
    if (r) {
      const d = u.COLOR_0 !== void 0 ? t.getDependency("accessor", u.COLOR_0) : s.attributes.color;
      l.push(d);
    }
  }
  return Promise.all([
    Promise.all(a),
    Promise.all(o),
    Promise.all(l)
  ]).then(function(c) {
    const h = c[0], u = c[1], d = c[2];
    return i && (s.morphAttributes.position = h), n && (s.morphAttributes.normal = u), r && (s.morphAttributes.color = d), s.morphTargetsRelative = !0, s;
  });
}
function ate(s, e) {
  if (s.updateMorphTargets(), e.weights !== void 0)
    for (let t = 0, i = e.weights.length; t < i; t++)
      s.morphTargetInfluences[t] = e.weights[t];
  if (e.extras && Array.isArray(e.extras.targetNames)) {
    const t = e.extras.targetNames;
    if (s.morphTargetInfluences.length === t.length) {
      s.morphTargetDictionary = {};
      for (let i = 0, n = t.length; i < n; i++)
        s.morphTargetDictionary[t[i]] = i;
    } else
      console.warn("THREE.GLTFLoader: Invalid extras.targetNames length. Ignoring names.");
  }
}
function ote(s) {
  let e;
  const t = s.extensions && s.extensions[Ti.KHR_DRACO_MESH_COMPRESSION];
  if (t ? e = "draco:" + t.bufferView + ":" + t.indices + ":" + B5(t.attributes) : e = s.indices + ":" + B5(s.attributes) + ":" + s.mode, s.targets !== void 0)
    for (let i = 0, n = s.targets.length; i < n; i++)
      e += ":" + B5(s.targets[i]);
  return e;
}
function B5(s) {
  let e = "";
  const t = Object.keys(s).sort();
  for (let i = 0, n = t.length; i < n; i++)
    e += t[i] + ":" + s[t[i]] + ";";
  return e;
}
function vb(s) {
  switch (s) {
    case Int8Array:
      return 1 / 127;
    case Uint8Array:
      return 1 / 255;
    case Int16Array:
      return 1 / 32767;
    case Uint16Array:
      return 1 / 65535;
    default:
      throw new Error("THREE.GLTFLoader: Unsupported normalized accessor component type.");
  }
}
function lte(s) {
  return s.search(/\.jpe?g($|\?)/i) > 0 || s.search(/^data\:image\/jpeg/) === 0 ? "image/jpeg" : s.search(/\.webp($|\?)/i) > 0 || s.search(/^data\:image\/webp/) === 0 ? "image/webp" : "image/png";
}
const cte = new et();
let hte = class {
  constructor(e = {}, t = {}) {
    this.json = e, this.extensions = {}, this.plugins = {}, this.options = t, this.cache = new Lee(), this.associations = /* @__PURE__ */ new Map(), this.primitiveCache = {}, this.nodeCache = {}, this.meshCache = { refs: {}, uses: {} }, this.cameraCache = { refs: {}, uses: {} }, this.lightCache = { refs: {}, uses: {} }, this.sourceCache = {}, this.textureCache = {}, this.nodeNamesUsed = {};
    let i = !1, n = -1, r = !1, a = -1;
    if (typeof navigator < "u") {
      const o = navigator.userAgent;
      i = /^((?!chrome|android).)*safari/i.test(o) === !0;
      const l = o.match(/Version\/(\d+)/);
      n = i && l ? parseInt(l[1], 10) : -1, r = o.indexOf("Firefox") > -1, a = r ? o.match(/Firefox\/([0-9]+)\./)[1] : -1;
    }
    typeof createImageBitmap > "u" || i && n < 17 || r && a < 98 ? this.textureLoader = new rh(this.options.manager) : this.textureLoader = new A2(this.options.manager), this.textureLoader.setCrossOrigin(this.options.crossOrigin), this.textureLoader.setRequestHeader(this.options.requestHeader), this.fileLoader = new Nn(this.options.manager), this.fileLoader.setResponseType("arraybuffer"), this.options.crossOrigin === "use-credentials" && this.fileLoader.setWithCredentials(!0);
  }
  setExtensions(e) {
    this.extensions = e;
  }
  setPlugins(e) {
    this.plugins = e;
  }
  parse(e, t) {
    const i = this, n = this.json, r = this.extensions;
    this.cache.removeAll(), this.nodeCache = {}, this._invokeAll(function(a) {
      return a._markDefs && a._markDefs();
    }), Promise.all(this._invokeAll(function(a) {
      return a.beforeRoot && a.beforeRoot();
    })).then(function() {
      return Promise.all([
        i.getDependencies("scene"),
        i.getDependencies("animation"),
        i.getDependencies("camera")
      ]);
    }).then(function(a) {
      const o = {
        scene: a[0][n.scene || 0],
        scenes: a[0],
        animations: a[1],
        cameras: a[2],
        asset: n.asset,
        parser: i,
        userData: {}
      };
      return Ch(r, o, n), j0(o, n), Promise.all(i._invokeAll(function(l) {
        return l.afterRoot && l.afterRoot(o);
      })).then(function() {
        for (const l of o.scenes)
          l.updateMatrixWorld();
        e(o);
      });
    }).catch(t);
  }
  /**
   * Marks the special nodes/meshes in json for efficient parse.
   */
  _markDefs() {
    const e = this.json.nodes || [], t = this.json.skins || [], i = this.json.meshes || [];
    for (let n = 0, r = t.length; n < r; n++) {
      const a = t[n].joints;
      for (let o = 0, l = a.length; o < l; o++)
        e[a[o]].isBone = !0;
    }
    for (let n = 0, r = e.length; n < r; n++) {
      const a = e[n];
      a.mesh !== void 0 && (this._addNodeRef(this.meshCache, a.mesh), a.skin !== void 0 && (i[a.mesh].isSkinnedMesh = !0)), a.camera !== void 0 && this._addNodeRef(this.cameraCache, a.camera);
    }
  }
  /**
   * Counts references to shared node / Object3D resources. These resources
   * can be reused, or "instantiated", at multiple nodes in the scene
   * hierarchy. Mesh, Camera, and Light instances are instantiated and must
   * be marked. Non-scenegraph resources (like Materials, Geometries, and
   * Textures) can be reused directly and are not marked here.
   *
   * Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
   */
  _addNodeRef(e, t) {
    t !== void 0 && (e.refs[t] === void 0 && (e.refs[t] = e.uses[t] = 0), e.refs[t]++);
  }
  /** Returns a reference to a shared resource, cloning it if necessary. */
  _getNodeRef(e, t, i) {
    if (e.refs[t] <= 1)
      return i;
    const n = i.clone(), r = (a, o) => {
      const l = this.associations.get(a);
      l != null && this.associations.set(o, l);
      for (const [c, h] of a.children.entries())
        r(h, o.children[c]);
    };
    return r(i, n), n.name += "_instance_" + e.uses[t]++, n;
  }
  _invokeOne(e) {
    const t = Object.values(this.plugins);
    t.push(this);
    for (let i = 0; i < t.length; i++) {
      const n = e(t[i]);
      if (n)
        return n;
    }
    return null;
  }
  _invokeAll(e) {
    const t = Object.values(this.plugins);
    t.unshift(this);
    const i = [];
    for (let n = 0; n < t.length; n++) {
      const r = e(t[n]);
      r && i.push(r);
    }
    return i;
  }
  /**
   * Requests the specified dependency asynchronously, with caching.
   * @param {string} type
   * @param {number} index
   * @return {Promise<Object3D|Material|THREE.Texture|AnimationClip|ArrayBuffer|Object>}
   */
  getDependency(e, t) {
    const i = e + ":" + t;
    let n = this.cache.get(i);
    if (!n) {
      switch (e) {
        case "scene":
          n = this.loadScene(t);
          break;
        case "node":
          n = this._invokeOne(function(r) {
            return r.loadNode && r.loadNode(t);
          });
          break;
        case "mesh":
          n = this._invokeOne(function(r) {
            return r.loadMesh && r.loadMesh(t);
          });
          break;
        case "accessor":
          n = this.loadAccessor(t);
          break;
        case "bufferView":
          n = this._invokeOne(function(r) {
            return r.loadBufferView && r.loadBufferView(t);
          });
          break;
        case "buffer":
          n = this.loadBuffer(t);
          break;
        case "material":
          n = this._invokeOne(function(r) {
            return r.loadMaterial && r.loadMaterial(t);
          });
          break;
        case "texture":
          n = this._invokeOne(function(r) {
            return r.loadTexture && r.loadTexture(t);
          });
          break;
        case "skin":
          n = this.loadSkin(t);
          break;
        case "animation":
          n = this._invokeOne(function(r) {
            return r.loadAnimation && r.loadAnimation(t);
          });
          break;
        case "camera":
          n = this.loadCamera(t);
          break;
        default:
          if (n = this._invokeOne(function(r) {
            return r != this && r.getDependency && r.getDependency(e, t);
          }), !n)
            throw new Error("Unknown type: " + e);
          break;
      }
      this.cache.add(i, n);
    }
    return n;
  }
  /**
   * Requests all dependencies of the specified type asynchronously, with caching.
   * @param {string} type
   * @return {Promise<Array<Object>>}
   */
  getDependencies(e) {
    let t = this.cache.get(e);
    if (!t) {
      const i = this, n = this.json[e + (e === "mesh" ? "es" : "s")] || [];
      t = Promise.all(n.map(function(r, a) {
        return i.getDependency(e, a);
      })), this.cache.add(e, t);
    }
    return t;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBuffer(e) {
    const t = this.json.buffers[e], i = this.fileLoader;
    if (t.type && t.type !== "arraybuffer")
      throw new Error("THREE.GLTFLoader: " + t.type + " buffer type is not supported.");
    if (t.uri === void 0 && e === 0)
      return Promise.resolve(this.extensions[Ti.KHR_BINARY_GLTF].body);
    const n = this.options;
    return new Promise(function(r, a) {
      i.load(Rs.resolveURL(t.uri, n.path), r, void 0, function() {
        a(new Error('THREE.GLTFLoader: Failed to load buffer "' + t.uri + '".'));
      });
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferViewIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBufferView(e) {
    const t = this.json.bufferViews[e];
    return this.getDependency("buffer", t.buffer).then(function(i) {
      const n = t.byteLength || 0, r = t.byteOffset || 0;
      return i.slice(r, r + n);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
   * @param {number} accessorIndex
   * @return {Promise<BufferAttribute|InterleavedBufferAttribute>}
   */
  loadAccessor(e) {
    const t = this, i = this.json, n = this.json.accessors[e];
    if (n.bufferView === void 0 && n.sparse === void 0) {
      const a = O5[n.type], o = kf[n.componentType], l = n.normalized === !0, c = new o(n.count * a);
      return Promise.resolve(new Ht(c, a, l));
    }
    const r = [];
    return n.bufferView !== void 0 ? r.push(this.getDependency("bufferView", n.bufferView)) : r.push(null), n.sparse !== void 0 && (r.push(this.getDependency("bufferView", n.sparse.indices.bufferView)), r.push(this.getDependency("bufferView", n.sparse.values.bufferView))), Promise.all(r).then(function(a) {
      const o = a[0], l = O5[n.type], c = kf[n.componentType], h = c.BYTES_PER_ELEMENT, u = h * l, d = n.byteOffset || 0, f = n.bufferView !== void 0 ? i.bufferViews[n.bufferView].byteStride : void 0, p = n.normalized === !0;
      let v, g;
      if (f && f !== u) {
        const m = Math.floor(d / f), x = "InterleavedBuffer:" + n.bufferView + ":" + n.componentType + ":" + m + ":" + n.count;
        let y = t.cache.get(x);
        y || (v = new c(o, m * f, n.count * f / h), y = new Xu(v, f / h), t.cache.add(x, y)), g = new c0(y, l, d % f / h, p);
      } else
        o === null ? v = new c(n.count * l) : v = new c(o, d, n.count * l), g = new Ht(v, l, p);
      if (n.sparse !== void 0) {
        const m = O5.SCALAR, x = kf[n.sparse.indices.componentType], y = n.sparse.indices.byteOffset || 0, A = n.sparse.values.byteOffset || 0, b = new x(a[1], y, n.sparse.count * m), M = new c(a[2], A, n.sparse.count * l);
        o !== null && (g = new Ht(g.array.slice(), g.itemSize, g.normalized));
        for (let w = 0, C = b.length; w < C; w++) {
          const T = b[w];
          if (g.setX(T, M[w * l]), l >= 2 && g.setY(T, M[w * l + 1]), l >= 3 && g.setZ(T, M[w * l + 2]), l >= 4 && g.setW(T, M[w * l + 3]), l >= 5)
            throw new Error("THREE.GLTFLoader: Unsupported itemSize in sparse BufferAttribute.");
        }
      }
      return g;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
   * @param {number} textureIndex
   * @return {Promise<THREE.Texture|null>}
   */
  loadTexture(e) {
    const t = this.json, i = this.options, r = t.textures[e].source, a = t.images[r];
    let o = this.textureLoader;
    if (a.uri) {
      const l = i.manager.getHandler(a.uri);
      l !== null && (o = l);
    }
    return this.loadTextureImage(e, r, o);
  }
  loadTextureImage(e, t, i) {
    const n = this, r = this.json, a = r.textures[e], o = r.images[t], l = (o.uri || o.bufferView) + ":" + a.sampler;
    if (this.textureCache[l])
      return this.textureCache[l];
    const c = this.loadImageSource(t, i).then(function(h) {
      h.flipY = !1, h.name = a.name || o.name || "", h.name === "" && typeof o.uri == "string" && o.uri.startsWith("data:image/") === !1 && (h.name = o.uri);
      const d = (r.samplers || {})[a.sampler] || {};
      return h.magFilter = pC[d.magFilter] || ii, h.minFilter = pC[d.minFilter] || ds, h.wrapS = mC[d.wrapS] || un, h.wrapT = mC[d.wrapT] || un, n.associations.set(h, { textures: e }), h;
    }).catch(function() {
      return null;
    });
    return this.textureCache[l] = c, c;
  }
  loadImageSource(e, t) {
    const i = this, n = this.json, r = this.options;
    if (this.sourceCache[e] !== void 0)
      return this.sourceCache[e].then((u) => u.clone());
    const a = n.images[e], o = self.URL || self.webkitURL;
    let l = a.uri || "", c = !1;
    if (a.bufferView !== void 0)
      l = i.getDependency("bufferView", a.bufferView).then(function(u) {
        c = !0;
        const d = new Blob([u], { type: a.mimeType });
        return l = o.createObjectURL(d), l;
      });
    else if (a.uri === void 0)
      throw new Error("THREE.GLTFLoader: Image " + e + " is missing URI and bufferView");
    const h = Promise.resolve(l).then(function(u) {
      return new Promise(function(d, f) {
        let p = d;
        t.isImageBitmapLoader === !0 && (p = function(v) {
          const g = new Ut(v);
          g.needsUpdate = !0, d(g);
        }), t.load(Rs.resolveURL(u, r.path), p, void 0, f);
      });
    }).then(function(u) {
      return c === !0 && o.revokeObjectURL(l), j0(u, a), u.userData.mimeType = a.mimeType || lte(a.uri), u;
    }).catch(function(u) {
      throw console.error("THREE.GLTFLoader: Couldn't load texture", l), u;
    });
    return this.sourceCache[e] = h, h;
  }
  /**
   * Asynchronously assigns a texture to the given material parameters.
   * @param {Object} materialParams
   * @param {string} mapName
   * @param {Object} mapDef
   * @return {Promise<Texture>}
   */
  assignTexture(e, t, i, n) {
    const r = this;
    return this.getDependency("texture", i.index).then(function(a) {
      if (!a)
        return null;
      if (i.texCoord !== void 0 && i.texCoord > 0 && (a = a.clone(), a.channel = i.texCoord), r.extensions[Ti.KHR_TEXTURE_TRANSFORM]) {
        const o = i.extensions !== void 0 ? i.extensions[Ti.KHR_TEXTURE_TRANSFORM] : void 0;
        if (o) {
          const l = r.associations.get(a);
          a = r.extensions[Ti.KHR_TEXTURE_TRANSFORM].extendTexture(a, o), r.associations.set(a, l);
        }
      }
      return n !== void 0 && (a.colorSpace = n), e[t] = a, a;
    });
  }
  /**
   * Assigns final material to a Mesh, Line, or Points instance. The instance
   * already has a material (generated from the glTF material options alone)
   * but reuse of the same glTF material may require multiple threejs materials
   * to accommodate different primitive types, defines, etc. New materials will
   * be created if necessary, and reused from a cache.
   * @param  {Object3D} mesh Mesh, Line, or Points instance.
   */
  assignFinalMaterial(e) {
    const t = e.geometry;
    let i = e.material;
    const n = t.attributes.tangent === void 0, r = t.attributes.color !== void 0, a = t.attributes.normal === void 0;
    if (e.isPoints) {
      const o = "PointsMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new qu(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, l.sizeAttenuation = !1, this.cache.add(o, l)), i = l;
    } else if (e.isLine) {
      const o = "LineBasicMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new Bi(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, this.cache.add(o, l)), i = l;
    }
    if (n || r || a) {
      let o = "ClonedMaterial:" + i.uuid + ":";
      n && (o += "derivative-tangents:"), r && (o += "vertex-colors:"), a && (o += "flat-shading:");
      let l = this.cache.get(o);
      l || (l = i.clone(), r && (l.vertexColors = !0), a && (l.flatShading = !0), n && (l.normalScale && (l.normalScale.y *= -1), l.clearcoatNormalScale && (l.clearcoatNormalScale.y *= -1)), this.cache.add(o, l), this.associations.set(l, this.associations.get(i))), i = l;
    }
    e.material = i;
  }
  getMaterialType() {
    return or;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
   * @param {number} materialIndex
   * @return {Promise<Material>}
   */
  loadMaterial(e) {
    const t = this, i = this.json, n = this.extensions, r = i.materials[e];
    let a;
    const o = {}, l = r.extensions || {}, c = [];
    if (l[Ti.KHR_MATERIALS_UNLIT]) {
      const u = n[Ti.KHR_MATERIALS_UNLIT];
      a = u.getMaterialType(), c.push(u.extendParams(o, r, t));
    } else {
      const u = r.pbrMetallicRoughness || {};
      if (o.color = new _e(1, 1, 1), o.opacity = 1, Array.isArray(u.baseColorFactor)) {
        const d = u.baseColorFactor;
        o.color.setRGB(d[0], d[1], d[2], Si), o.opacity = d[3];
      }
      u.baseColorTexture !== void 0 && c.push(t.assignTexture(o, "map", u.baseColorTexture, qi)), o.metalness = u.metallicFactor !== void 0 ? u.metallicFactor : 1, o.roughness = u.roughnessFactor !== void 0 ? u.roughnessFactor : 1, u.metallicRoughnessTexture !== void 0 && (c.push(t.assignTexture(o, "metalnessMap", u.metallicRoughnessTexture)), c.push(t.assignTexture(o, "roughnessMap", u.metallicRoughnessTexture))), a = this._invokeOne(function(d) {
        return d.getMaterialType && d.getMaterialType(e);
      }), c.push(Promise.all(this._invokeAll(function(d) {
        return d.extendMaterialParams && d.extendMaterialParams(e, o);
      })));
    }
    r.doubleSided === !0 && (o.side = ui);
    const h = r.alphaMode || N5.OPAQUE;
    if (h === N5.BLEND ? (o.transparent = !0, o.depthWrite = !1) : (o.transparent = !1, h === N5.MASK && (o.alphaTest = r.alphaCutoff !== void 0 ? r.alphaCutoff : 0.5)), r.normalTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "normalMap", r.normalTexture)), o.normalScale = new Se(1, 1), r.normalTexture.scale !== void 0)) {
      const u = r.normalTexture.scale;
      o.normalScale.set(u, u);
    }
    if (r.occlusionTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "aoMap", r.occlusionTexture)), r.occlusionTexture.strength !== void 0 && (o.aoMapIntensity = r.occlusionTexture.strength)), r.emissiveFactor !== void 0 && a !== kt) {
      const u = r.emissiveFactor;
      o.emissive = new _e().setRGB(u[0], u[1], u[2], Si);
    }
    return r.emissiveTexture !== void 0 && a !== kt && c.push(t.assignTexture(o, "emissiveMap", r.emissiveTexture, qi)), Promise.all(c).then(function() {
      const u = new a(o);
      return r.name && (u.name = r.name), j0(u, r), t.associations.set(u, { materials: e }), r.extensions && Ch(n, u, r), u;
    });
  }
  /** When Object3D instances are targeted by animation, they need unique names. */
  createUniqueName(e) {
    const t = Pi.sanitizeNodeName(e || "");
    return t in this.nodeNamesUsed ? t + "_" + ++this.nodeNamesUsed[t] : (this.nodeNamesUsed[t] = 0, t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
   *
   * Creates BufferGeometries from primitives.
   *
   * @param {Array<GLTF.Primitive>} primitives
   * @return {Promise<Array<BufferGeometry>>}
   */
  loadGeometries(e) {
    const t = this, i = this.extensions, n = this.primitiveCache;
    function r(o) {
      return i[Ti.KHR_DRACO_MESH_COMPRESSION].decodePrimitive(o, t).then(function(l) {
        return gC(l, o, t);
      });
    }
    const a = [];
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o], h = ote(c), u = n[h];
      if (u)
        a.push(u.promise);
      else {
        let d;
        c.extensions && c.extensions[Ti.KHR_DRACO_MESH_COMPRESSION] ? d = r(c) : d = gC(new Pt(), c, t), n[h] = { primitive: c, promise: d }, a.push(d);
      }
    }
    return Promise.all(a);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
   * @param {number} meshIndex
   * @return {Promise<Group|Mesh|SkinnedMesh>}
   */
  loadMesh(e) {
    const t = this, i = this.json, n = this.extensions, r = i.meshes[e], a = r.primitives, o = [];
    for (let l = 0, c = a.length; l < c; l++) {
      const h = a[l].material === void 0 ? ste(this.cache) : this.getDependency("material", a[l].material);
      o.push(h);
    }
    return o.push(t.loadGeometries(a)), Promise.all(o).then(function(l) {
      const c = l.slice(0, l.length - 1), h = l[l.length - 1], u = [];
      for (let f = 0, p = h.length; f < p; f++) {
        const v = h[f], g = a[f];
        let m;
        const x = c[f];
        if (g.mode === pa.TRIANGLES || g.mode === pa.TRIANGLE_STRIP || g.mode === pa.TRIANGLE_FAN || g.mode === void 0)
          m = r.isSkinnedMesh === !0 ? new a2(v, x) : new Re(v, x), m.isSkinnedMesh === !0 && m.normalizeSkinWeights(), g.mode === pa.TRIANGLE_STRIP ? m.geometry = up(m.geometry, i2) : g.mode === pa.TRIANGLE_FAN && (m.geometry = up(m.geometry, ip));
        else if (g.mode === pa.LINES)
          m = new _n(v, x);
        else if (g.mode === pa.LINE_STRIP)
          m = new ti(v, x);
        else if (g.mode === pa.LINE_LOOP)
          m = new ju(v, x);
        else if (g.mode === pa.POINTS)
          m = new sh(v, x);
        else
          throw new Error("THREE.GLTFLoader: Primitive mode unsupported: " + g.mode);
        Object.keys(m.geometry.morphAttributes).length > 0 && ate(m, r), m.name = t.createUniqueName(r.name || "mesh_" + e), j0(m, r), g.extensions && Ch(n, m, g), t.assignFinalMaterial(m), u.push(m);
      }
      for (let f = 0, p = u.length; f < p; f++)
        t.associations.set(u[f], {
          meshes: e,
          primitives: f
        });
      if (u.length === 1)
        return r.extensions && Ch(n, u[0], r), u[0];
      const d = new ci();
      r.extensions && Ch(n, d, r), t.associations.set(d, { meshes: e });
      for (let f = 0, p = u.length; f < p; f++)
        d.add(u[f]);
      return d;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#cameras
   * @param {number} cameraIndex
   * @return {Promise<THREE.Camera>}
   */
  loadCamera(e) {
    let t;
    const i = this.json.cameras[e], n = i[i.type];
    if (!n) {
      console.warn("THREE.GLTFLoader: Missing camera parameters.");
      return;
    }
    return i.type === "perspective" ? t = new Zt(nt.radToDeg(n.yfov), n.aspectRatio || 1, n.znear || 1, n.zfar || 2e6) : i.type === "orthographic" && (t = new Xn(-n.xmag, n.xmag, n.ymag, -n.ymag, n.znear, n.zfar)), i.name && (t.name = this.createUniqueName(i.name)), j0(t, i), Promise.resolve(t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#skins
   * @param {number} skinIndex
   * @return {Promise<Skeleton>}
   */
  loadSkin(e) {
    const t = this.json.skins[e], i = [];
    for (let n = 0, r = t.joints.length; n < r; n++)
      i.push(this._loadNodeShallow(t.joints[n]));
    return t.inverseBindMatrices !== void 0 ? i.push(this.getDependency("accessor", t.inverseBindMatrices)) : i.push(null), Promise.all(i).then(function(n) {
      const r = n.pop(), a = n, o = [], l = [];
      for (let c = 0, h = a.length; c < h; c++) {
        const u = a[c];
        if (u) {
          o.push(u);
          const d = new et();
          r !== null && d.fromArray(r.array, c * 16), l.push(d);
        } else
          console.warn('THREE.GLTFLoader: Joint "%s" could not be found.', t.joints[c]);
      }
      return new nh(o, l);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#animations
   * @param {number} animationIndex
   * @return {Promise<AnimationClip>}
   */
  loadAnimation(e) {
    const t = this.json, i = this, n = t.animations[e], r = n.name ? n.name : "animation_" + e, a = [], o = [], l = [], c = [], h = [];
    for (let u = 0, d = n.channels.length; u < d; u++) {
      const f = n.channels[u], p = n.samplers[f.sampler], v = f.target, g = v.node, m = n.parameters !== void 0 ? n.parameters[p.input] : p.input, x = n.parameters !== void 0 ? n.parameters[p.output] : p.output;
      v.node !== void 0 && (a.push(this.getDependency("node", g)), o.push(this.getDependency("accessor", m)), l.push(this.getDependency("accessor", x)), c.push(p), h.push(v));
    }
    return Promise.all([
      Promise.all(a),
      Promise.all(o),
      Promise.all(l),
      Promise.all(c),
      Promise.all(h)
    ]).then(function(u) {
      const d = u[0], f = u[1], p = u[2], v = u[3], g = u[4], m = [];
      for (let x = 0, y = d.length; x < y; x++) {
        const A = d[x], b = f[x], M = p[x], w = v[x], C = g[x];
        if (A === void 0)
          continue;
        A.updateMatrix && A.updateMatrix();
        const T = i._createAnimationTracks(A, b, M, w, C);
        if (T)
          for (let E = 0; E < T.length; E++)
            m.push(T[E]);
      }
      return new xl(r, void 0, m);
    });
  }
  createNodeMesh(e) {
    const t = this.json, i = this, n = t.nodes[e];
    return n.mesh === void 0 ? null : i.getDependency("mesh", n.mesh).then(function(r) {
      const a = i._getNodeRef(i.meshCache, n.mesh, r);
      return n.weights !== void 0 && a.traverse(function(o) {
        if (o.isMesh)
          for (let l = 0, c = n.weights.length; l < c; l++)
            o.morphTargetInfluences[l] = n.weights[l];
      }), a;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
   * @param {number} nodeIndex
   * @return {Promise<Object3D>}
   */
  loadNode(e) {
    const t = this.json, i = this, n = t.nodes[e], r = i._loadNodeShallow(e), a = [], o = n.children || [];
    for (let c = 0, h = o.length; c < h; c++)
      a.push(i.getDependency("node", o[c]));
    const l = n.skin === void 0 ? Promise.resolve(null) : i.getDependency("skin", n.skin);
    return Promise.all([
      r,
      Promise.all(a),
      l
    ]).then(function(c) {
      const h = c[0], u = c[1], d = c[2];
      d !== null && h.traverse(function(f) {
        f.isSkinnedMesh && f.bind(d, cte);
      });
      for (let f = 0, p = u.length; f < p; f++)
        h.add(u[f]);
      return h;
    });
  }
  // ._loadNodeShallow() parses a single node.
  // skin and child nodes are created and added in .loadNode() (no '_' prefix).
  _loadNodeShallow(e) {
    const t = this.json, i = this.extensions, n = this;
    if (this.nodeCache[e] !== void 0)
      return this.nodeCache[e];
    const r = t.nodes[e], a = r.name ? n.createUniqueName(r.name) : "", o = [], l = n._invokeOne(function(c) {
      return c.createNodeMesh && c.createNodeMesh(e);
    });
    return l && o.push(l), r.camera !== void 0 && o.push(n.getDependency("camera", r.camera).then(function(c) {
      return n._getNodeRef(n.cameraCache, r.camera, c);
    })), n._invokeAll(function(c) {
      return c.createNodeAttachment && c.createNodeAttachment(e);
    }).forEach(function(c) {
      o.push(c);
    }), this.nodeCache[e] = Promise.all(o).then(function(c) {
      let h;
      if (r.isBone === !0 ? h = new ih() : c.length > 1 ? h = new ci() : c.length === 1 ? h = c[0] : h = new mt(), h !== c[0])
        for (let u = 0, d = c.length; u < d; u++)
          h.add(c[u]);
      if (r.name && (h.userData.name = r.name, h.name = a), j0(h, r), r.extensions && Ch(i, h, r), r.matrix !== void 0) {
        const u = new et();
        u.fromArray(r.matrix), h.applyMatrix4(u);
      } else
        r.translation !== void 0 && h.position.fromArray(r.translation), r.rotation !== void 0 && h.quaternion.fromArray(r.rotation), r.scale !== void 0 && h.scale.fromArray(r.scale);
      return n.associations.has(h) || n.associations.set(h, {}), n.associations.get(h).nodes = e, h;
    }), this.nodeCache[e];
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
   * @param {number} sceneIndex
   * @return {Promise<Group>}
   */
  loadScene(e) {
    const t = this.extensions, i = this.json.scenes[e], n = this, r = new ci();
    i.name && (r.name = n.createUniqueName(i.name)), j0(r, i), i.extensions && Ch(t, r, i);
    const a = i.nodes || [], o = [];
    for (let l = 0, c = a.length; l < c; l++)
      o.push(n.getDependency("node", a[l]));
    return Promise.all(o).then(function(l) {
      for (let h = 0, u = l.length; h < u; h++)
        r.add(l[h]);
      const c = (h) => {
        const u = /* @__PURE__ */ new Map();
        for (const [d, f] of n.associations)
          (d instanceof Cn || d instanceof Ut) && u.set(d, f);
        return h.traverse((d) => {
          const f = n.associations.get(d);
          f != null && u.set(d, f);
        }), u;
      };
      return n.associations = c(r), r;
    });
  }
  _createAnimationTracks(e, t, i, n, r) {
    const a = [], o = e.name ? e.name : e.uuid, l = [];
    Hl[r.path] === Hl.weights ? e.traverse(function(d) {
      d.morphTargetInfluences && l.push(d.name ? d.name : d.uuid);
    }) : l.push(o);
    let c;
    switch (Hl[r.path]) {
      case Hl.weights:
        c = uo;
        break;
      case Hl.rotation:
        c = fo;
        break;
      case Hl.position:
      case Hl.scale:
        c = po;
        break;
      default:
        switch (i.itemSize) {
          case 1:
            c = uo;
            break;
          case 2:
          case 3:
          default:
            c = po;
            break;
        }
        break;
    }
    const h = n.interpolation !== void 0 ? nte[n.interpolation] : l0, u = this._getArrayFromAccessor(i);
    for (let d = 0, f = l.length; d < f; d++) {
      const p = new c(
        l[d] + "." + Hl[r.path],
        t.array,
        u,
        h
      );
      n.interpolation === "CUBICSPLINE" && this._createCubicSplineTrackInterpolant(p), a.push(p);
    }
    return a;
  }
  _getArrayFromAccessor(e) {
    let t = e.array;
    if (e.normalized) {
      const i = vb(t.constructor), n = new Float32Array(t.length);
      for (let r = 0, a = t.length; r < a; r++)
        n[r] = t[r] * i;
      t = n;
    }
    return t;
  }
  _createCubicSplineTrackInterpolant(e) {
    e.createInterpolant = function(i) {
      const n = this instanceof fo ? ite : eN;
      return new n(this.times, this.values, this.getValueSize() / 3, i);
    }, e.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline = !0;
  }
};
function ute(s, e, t) {
  const i = e.attributes, n = new zt();
  if (i.POSITION !== void 0) {
    const o = t.json.accessors[i.POSITION], l = o.min, c = o.max;
    if (l !== void 0 && c !== void 0) {
      if (n.set(
        new _(l[0], l[1], l[2]),
        new _(c[0], c[1], c[2])
      ), o.normalized) {
        const h = vb(kf[o.componentType]);
        n.min.multiplyScalar(h), n.max.multiplyScalar(h);
      }
    } else {
      console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      return;
    }
  } else
    return;
  const r = e.targets;
  if (r !== void 0) {
    const o = new _(), l = new _();
    for (let c = 0, h = r.length; c < h; c++) {
      const u = r[c];
      if (u.POSITION !== void 0) {
        const d = t.json.accessors[u.POSITION], f = d.min, p = d.max;
        if (f !== void 0 && p !== void 0) {
          if (l.setX(Math.max(Math.abs(f[0]), Math.abs(p[0]))), l.setY(Math.max(Math.abs(f[1]), Math.abs(p[1]))), l.setZ(Math.max(Math.abs(f[2]), Math.abs(p[2]))), d.normalized) {
            const v = vb(kf[d.componentType]);
            l.multiplyScalar(v);
          }
          o.max(l);
        } else
          console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      }
    }
    n.expandByVector(o);
  }
  s.boundingBox = n;
  const a = new Tn();
  n.getCenter(a.center), a.radius = n.min.distanceTo(n.max) / 2, s.boundingSphere = a;
}
function gC(s, e, t) {
  const i = e.attributes, n = [];
  function r(a, o) {
    return t.getDependency("accessor", a).then(function(l) {
      s.setAttribute(o, l);
    });
  }
  for (const a in i) {
    const o = gb[a] || a.toLowerCase();
    o in s.attributes || n.push(r(i[a], o));
  }
  if (e.indices !== void 0 && !s.index) {
    const a = t.getDependency("accessor", e.indices).then(function(o) {
      s.setIndex(o);
    });
    n.push(a);
  }
  return bi.workingColorSpace !== Si && "COLOR_0" in i && console.warn(`THREE.GLTFLoader: Converting vertex colors from "srgb-linear" to "${bi.workingColorSpace}" not supported.`), j0(s, e), ute(s, e, t), Promise.all(n).then(function() {
    return e.targets !== void 0 ? rte(s, e.targets, t) : s;
  });
}
/*!
fflate - fast JavaScript compression/decompression
<https://101arrowz.github.io/fflate>
Licensed under MIT. https://github.com/101arrowz/fflate/blob/master/LICENSE
version 0.8.2
*/
var Sa = Uint8Array, Sf = Uint16Array, dte = Int32Array, tN = new Sa([
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  1,
  1,
  1,
  1,
  2,
  2,
  2,
  2,
  3,
  3,
  3,
  3,
  4,
  4,
  4,
  4,
  5,
  5,
  5,
  5,
  0,
  /* unused */
  0,
  0,
  /* impossible */
  0
]), iN = new Sa([
  0,
  0,
  0,
  0,
  1,
  1,
  2,
  2,
  3,
  3,
  4,
  4,
  5,
  5,
  6,
  6,
  7,
  7,
  8,
  8,
  9,
  9,
  10,
  10,
  11,
  11,
  12,
  12,
  13,
  13,
  /* unused */
  0,
  0
]), fte = new Sa([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]), nN = function(s, e) {
  for (var t = new Sf(31), i = 0; i < 31; ++i)
    t[i] = e += 1 << s[i - 1];
  for (var n = new dte(t[30]), i = 1; i < 30; ++i)
    for (var r = t[i]; r < t[i + 1]; ++r)
      n[r] = r - t[i] << 5 | i;
  return { b: t, r: n };
}, sN = nN(tN, 2), rN = sN.b, pte = sN.r;
rN[28] = 258, pte[258] = 28;
var mte = nN(iN, 0), gte = mte.b, yb = new Sf(32768);
for (var En = 0; En < 32768; ++En) {
  var Vl = (En & 43690) >> 1 | (En & 21845) << 1;
  Vl = (Vl & 52428) >> 2 | (Vl & 13107) << 2, Vl = (Vl & 61680) >> 4 | (Vl & 3855) << 4, yb[En] = ((Vl & 65280) >> 8 | (Vl & 255) << 8) >> 1;
}
var I1 = function(s, e, t) {
  for (var i = s.length, n = 0, r = new Sf(e); n < i; ++n)
    s[n] && ++r[s[n] - 1];
  var a = new Sf(e);
  for (n = 1; n < e; ++n)
    a[n] = a[n - 1] + r[n - 1] << 1;
  var o;
  {
    o = new Sf(1 << e);
    var l = 15 - e;
    for (n = 0; n < i; ++n)
      if (s[n])
        for (var c = n << 4 | s[n], h = e - s[n], u = a[s[n] - 1]++ << h, d = u | (1 << h) - 1; u <= d; ++u)
          o[yb[u] >> l] = c;
  }
  return o;
}, R2 = new Sa(288);
for (var En = 0; En < 144; ++En)
  R2[En] = 8;
for (var En = 144; En < 256; ++En)
  R2[En] = 9;
for (var En = 256; En < 280; ++En)
  R2[En] = 7;
for (var En = 280; En < 288; ++En)
  R2[En] = 8;
var aN = new Sa(32);
for (var En = 0; En < 32; ++En)
  aN[En] = 5;
var vte = /* @__PURE__ */ I1(R2, 9), yte = /* @__PURE__ */ I1(aN, 5), F5 = function(s) {
  for (var e = s[0], t = 1; t < s.length; ++t)
    s[t] > e && (e = s[t]);
  return e;
}, Fa = function(s, e, t) {
  var i = e / 8 | 0;
  return (s[i] | s[i + 1] << 8) >> (e & 7) & t;
}, U5 = function(s, e) {
  var t = e / 8 | 0;
  return (s[t] | s[t + 1] << 8 | s[t + 2] << 16) >> (e & 7);
}, xte = function(s) {
  return (s + 7) / 8 | 0;
}, Ate = function(s, e, t) {
  return (t == null || t > s.length) && (t = s.length), new Sa(s.subarray(e, t));
}, bte = [
  "unexpected EOF",
  "invalid block type",
  "invalid length/literal",
  "invalid distance",
  "stream finished",
  "no stream handler",
  ,
  "no callback",
  "invalid UTF-8 data",
  "extra field too long",
  "date not in range 1980-2099",
  "filename too long",
  "stream finishing",
  "invalid zip data"
  // determined by unknown compression method
], ja = function(s, e, t) {
  var i = new Error(e || bte[s]);
  if (i.code = s, Error.captureStackTrace && Error.captureStackTrace(i, ja), !t)
    throw i;
  return i;
}, Mte = function(s, e, t, i) {
  var n = s.length, r = 0;
  if (!n || e.f && !e.l)
    return t || new Sa(0);
  var a = !t, o = a || e.i != 2, l = e.i;
  a && (t = new Sa(n * 3));
  var c = function(wt) {
    var k = t.length;
    if (wt > k) {
      var Fe = new Sa(Math.max(k * 2, wt));
      Fe.set(t), t = Fe;
    }
  }, h = e.f || 0, u = e.p || 0, d = e.b || 0, f = e.l, p = e.d, v = e.m, g = e.n, m = n * 8;
  do {
    if (!f) {
      h = Fa(s, u, 1);
      var x = Fa(s, u + 1, 3);
      if (u += 3, x)
        if (x == 1)
          f = vte, p = yte, v = 9, g = 5;
        else if (x == 2) {
          var M = Fa(s, u, 31) + 257, w = Fa(s, u + 10, 15) + 4, C = M + Fa(s, u + 5, 31) + 1;
          u += 14;
          for (var T = new Sa(C), E = new Sa(19), P = 0; P < w; ++P)
            E[fte[P]] = Fa(s, u + P * 3, 7);
          u += w * 3;
          for (var L = F5(E), I = (1 << L) - 1, N = I1(E, L), P = 0; P < C; ) {
            var H = N[Fa(s, u, I)];
            u += H & 15;
            var y = H >> 4;
            if (y < 16)
              T[P++] = y;
            else {
              var U = 0, W = 0;
              for (y == 16 ? (W = 3 + Fa(s, u, 3), u += 2, U = T[P - 1]) : y == 17 ? (W = 3 + Fa(s, u, 7), u += 3) : y == 18 && (W = 11 + Fa(s, u, 127), u += 7); W--; )
                T[P++] = U;
            }
          }
          var X = T.subarray(0, M), K = T.subarray(M);
          v = F5(X), g = F5(K), f = I1(X, v), p = I1(K, g);
        } else
          ja(1);
      else {
        var y = xte(u) + 4, A = s[y - 4] | s[y - 3] << 8, b = y + A;
        if (b > n) {
          l && ja(0);
          break;
        }
        o && c(d + A), t.set(s.subarray(y, b), d), e.b = d += A, e.p = u = b * 8, e.f = h;
        continue;
      }
      if (u > m) {
        l && ja(0);
        break;
      }
    }
    o && c(d + 131072);
    for (var te = (1 << v) - 1, re = (1 << g) - 1, be = u; ; be = u) {
      var U = f[U5(s, u) & te], Pe = U >> 4;
      if (u += U & 15, u > m) {
        l && ja(0);
        break;
      }
      if (U || ja(2), Pe < 256)
        t[d++] = Pe;
      else if (Pe == 256) {
        be = u, f = null;
        break;
      } else {
        var J = Pe - 254;
        if (Pe > 264) {
          var P = Pe - 257, fe = tN[P];
          J = Fa(s, u, (1 << fe) - 1) + rN[P], u += fe;
        }
        var Me = p[U5(s, u) & re], ye = Me >> 4;
        Me || ja(3), u += Me & 15;
        var K = gte[ye];
        if (ye > 3) {
          var fe = iN[ye];
          K += U5(s, u) & (1 << fe) - 1, u += fe;
        }
        if (u > m) {
          l && ja(0);
          break;
        }
        o && c(d + 131072);
        var Ye = d + J;
        if (d < K) {
          var Je = r - K, rt = Math.min(K, Ye);
          for (Je + d < 0 && ja(3); d < rt; ++d)
            t[d] = i[Je + d];
        }
        for (; d < Ye; ++d)
          t[d] = t[d - K];
      }
    }
    e.l = f, e.p = be, e.b = d, e.f = h, f && (h = 1, e.m = v, e.d = p, e.n = g);
  } while (!h);
  return d != t.length && a ? Ate(t, 0, d) : t.subarray(0, d);
}, Ste = /* @__PURE__ */ new Sa(0), wte = function(s, e) {
  return ((s[0] & 15) != 8 || s[0] >> 4 > 7 || (s[0] << 8 | s[1]) % 31) && ja(6, "invalid zlib data"), (s[1] >> 5 & 1) == +!e && ja(6, "invalid zlib data: " + (s[1] & 32 ? "need" : "unexpected") + " dictionary"), (s[1] >> 3 & 4) + 2;
};
function Xv(s, e) {
  return Mte(s.subarray(wte(s, e), -4), { i: 2 }, e, e);
}
var Ete = typeof TextDecoder < "u" && /* @__PURE__ */ new TextDecoder(), Tte = 0;
try {
  Ete.decode(Ste, { stream: !0 }), Tte = 1;
} catch {
}
class Cte extends R4 {
  constructor(e) {
    super(e), this.type = on;
  }
  parse(e) {
    const T = Math.pow(2.7182818, 2.2);
    function E(R, B) {
      let z = 0;
      for (let ce = 0; ce < 65536; ++ce)
        (ce == 0 || R[ce >> 3] & 1 << (ce & 7)) && (B[z++] = ce);
      const $ = z - 1;
      for (; z < 65536; )
        B[z++] = 0;
      return $;
    }
    function P(R) {
      for (let B = 0; B < 16384; B++)
        R[B] = {}, R[B].len = 0, R[B].lit = 0, R[B].p = null;
    }
    const L = { l: 0, c: 0, lc: 0 };
    function I(R, B, z, $, ce) {
      for (; z < R; )
        B = B << 8 | We($, ce), z += 8;
      z -= R, L.l = B >> z & (1 << R) - 1, L.c = B, L.lc = z;
    }
    const N = new Array(59);
    function H(R) {
      for (let z = 0; z <= 58; ++z)
        N[z] = 0;
      for (let z = 0; z < 65537; ++z)
        N[R[z]] += 1;
      let B = 0;
      for (let z = 58; z > 0; --z) {
        const $ = B + N[z] >> 1;
        N[z] = B, B = $;
      }
      for (let z = 0; z < 65537; ++z) {
        const $ = R[z];
        $ > 0 && (R[z] = $ | N[$]++ << 6);
      }
    }
    function U(R, B, z, $, ce, se) {
      const pe = B;
      let Ne = 0, De = 0;
      for (; $ <= ce; $++) {
        if (pe.value - B.value > z)
          return !1;
        I(6, Ne, De, R, pe);
        const Te = L.l;
        if (Ne = L.c, De = L.lc, se[$] = Te, Te == 63) {
          if (pe.value - B.value > z)
            throw new Error("Something wrong with hufUnpackEncTable");
          I(8, Ne, De, R, pe);
          let Ue = L.l + 6;
          if (Ne = L.c, De = L.lc, $ + Ue > ce + 1)
            throw new Error("Something wrong with hufUnpackEncTable");
          for (; Ue--; )
            se[$++] = 0;
          $--;
        } else if (Te >= 59) {
          let Ue = Te - 59 + 2;
          if ($ + Ue > ce + 1)
            throw new Error("Something wrong with hufUnpackEncTable");
          for (; Ue--; )
            se[$++] = 0;
          $--;
        }
      }
      H(se);
    }
    function W(R) {
      return R & 63;
    }
    function X(R) {
      return R >> 6;
    }
    function K(R, B, z, $) {
      for (; B <= z; B++) {
        const ce = X(R[B]), se = W(R[B]);
        if (ce >> se)
          throw new Error("Invalid table entry");
        if (se > 14) {
          const pe = $[ce >> se - 14];
          if (pe.len)
            throw new Error("Invalid table entry");
          if (pe.lit++, pe.p) {
            const Ne = pe.p;
            pe.p = new Array(pe.lit);
            for (let De = 0; De < pe.lit - 1; ++De)
              pe.p[De] = Ne[De];
          } else
            pe.p = new Array(1);
          pe.p[pe.lit - 1] = B;
        } else if (se) {
          let pe = 0;
          for (let Ne = 1 << 14 - se; Ne > 0; Ne--) {
            const De = $[(ce << 14 - se) + pe];
            if (De.len || De.p)
              throw new Error("Invalid table entry");
            De.len = se, De.lit = B, pe++;
          }
        }
      }
      return !0;
    }
    const te = { c: 0, lc: 0 };
    function re(R, B, z, $) {
      R = R << 8 | We(z, $), B += 8, te.c = R, te.lc = B;
    }
    const be = { c: 0, lc: 0 };
    function Pe(R, B, z, $, ce, se, pe, Ne, De) {
      if (R == B) {
        $ < 8 && (re(z, $, ce, se), z = te.c, $ = te.lc), $ -= 8;
        let Te = z >> $;
        if (Te = new Uint8Array([Te])[0], Ne.value + Te > De)
          return !1;
        const Ue = pe[Ne.value - 1];
        for (; Te-- > 0; )
          pe[Ne.value++] = Ue;
      } else if (Ne.value < De)
        pe[Ne.value++] = R;
      else
        return !1;
      be.c = z, be.lc = $;
    }
    function J(R) {
      return R & 65535;
    }
    function fe(R) {
      const B = J(R);
      return B > 32767 ? B - 65536 : B;
    }
    const Me = { a: 0, b: 0 };
    function ye(R, B) {
      const z = fe(R), ce = fe(B), se = z + (ce & 1) + (ce >> 1), pe = se, Ne = se - ce;
      Me.a = pe, Me.b = Ne;
    }
    function Ye(R, B) {
      const z = J(R), $ = J(B), ce = z - ($ >> 1) & 65535, se = $ + ce - 32768 & 65535;
      Me.a = se, Me.b = ce;
    }
    function Je(R, B, z, $, ce, se, pe) {
      const Ne = pe < 16384, De = z > ce ? ce : z;
      let Te = 1, Ue, at;
      for (; Te <= De; )
        Te <<= 1;
      for (Te >>= 1, Ue = Te, Te >>= 1; Te >= 1; ) {
        at = 0;
        const ht = at + se * (ce - Ue), At = se * Te, lt = se * Ue, Oe = $ * Te, yt = $ * Ue;
        let V, le, ie, ge;
        for (; at <= ht; at += lt) {
          let Ce = at;
          const oe = at + $ * (z - Ue);
          for (; Ce <= oe; Ce += yt) {
            const He = Ce + Oe, ct = Ce + At, Ke = ct + Oe;
            Ne ? (ye(R[Ce + B], R[ct + B]), V = Me.a, ie = Me.b, ye(R[He + B], R[Ke + B]), le = Me.a, ge = Me.b, ye(V, le), R[Ce + B] = Me.a, R[He + B] = Me.b, ye(ie, ge), R[ct + B] = Me.a, R[Ke + B] = Me.b) : (Ye(R[Ce + B], R[ct + B]), V = Me.a, ie = Me.b, Ye(R[He + B], R[Ke + B]), le = Me.a, ge = Me.b, Ye(V, le), R[Ce + B] = Me.a, R[He + B] = Me.b, Ye(ie, ge), R[ct + B] = Me.a, R[Ke + B] = Me.b);
          }
          if (z & Te) {
            const He = Ce + At;
            Ne ? ye(R[Ce + B], R[He + B]) : Ye(R[Ce + B], R[He + B]), V = Me.a, R[He + B] = Me.b, R[Ce + B] = V;
          }
        }
        if (ce & Te) {
          let Ce = at;
          const oe = at + $ * (z - Ue);
          for (; Ce <= oe; Ce += yt) {
            const He = Ce + Oe;
            Ne ? ye(R[Ce + B], R[He + B]) : Ye(R[Ce + B], R[He + B]), V = Me.a, R[He + B] = Me.b, R[Ce + B] = V;
          }
        }
        Ue = Te, Te >>= 1;
      }
      return at;
    }
    function rt(R, B, z, $, ce, se, pe, Ne, De) {
      let Te = 0, Ue = 0;
      const at = pe, ht = Math.trunc($.value + (ce + 7) / 8);
      for (; $.value < ht; )
        for (re(Te, Ue, z, $), Te = te.c, Ue = te.lc; Ue >= 14; ) {
          const lt = Te >> Ue - 14 & 16383, Oe = B[lt];
          if (Oe.len)
            Ue -= Oe.len, Pe(Oe.lit, se, Te, Ue, z, $, Ne, De, at), Te = be.c, Ue = be.lc;
          else {
            if (!Oe.p)
              throw new Error("hufDecode issues");
            let yt;
            for (yt = 0; yt < Oe.lit; yt++) {
              const V = W(R[Oe.p[yt]]);
              for (; Ue < V && $.value < ht; )
                re(Te, Ue, z, $), Te = te.c, Ue = te.lc;
              if (Ue >= V && X(R[Oe.p[yt]]) == (Te >> Ue - V & (1 << V) - 1)) {
                Ue -= V, Pe(Oe.p[yt], se, Te, Ue, z, $, Ne, De, at), Te = be.c, Ue = be.lc;
                break;
              }
            }
            if (yt == Oe.lit)
              throw new Error("hufDecode issues");
          }
        }
      const At = 8 - ce & 7;
      for (Te >>= At, Ue -= At; Ue > 0; ) {
        const lt = B[Te << 14 - Ue & 16383];
        if (lt.len)
          Ue -= lt.len, Pe(lt.lit, se, Te, Ue, z, $, Ne, De, at), Te = be.c, Ue = be.lc;
        else
          throw new Error("hufDecode issues");
      }
      return !0;
    }
    function wt(R, B, z, $, ce, se) {
      const pe = { value: 0 }, Ne = z.value, De = Ae(B, z), Te = Ae(B, z);
      z.value += 4;
      const Ue = Ae(B, z);
      if (z.value += 4, De < 0 || De >= 65537 || Te < 0 || Te >= 65537)
        throw new Error("Something wrong with HUF_ENCSIZE");
      const at = new Array(65537), ht = new Array(16384);
      P(ht);
      const At = $ - (z.value - Ne);
      if (U(R, z, At, De, Te, at), Ue > 8 * ($ - (z.value - Ne)))
        throw new Error("Something wrong with hufUncompress");
      K(at, De, Te, ht), rt(at, ht, R, z, Ue, Te, se, ce, pe);
    }
    function k(R, B, z) {
      for (let $ = 0; $ < z; ++$)
        B[$] = R[B[$]];
    }
    function Fe(R) {
      for (let B = 1; B < R.length; B++) {
        const z = R[B - 1] + R[B] - 128;
        R[B] = z;
      }
    }
    function Ge(R, B) {
      let z = 0, $ = Math.floor((R.length + 1) / 2), ce = 0;
      const se = R.length - 1;
      for (; !(ce > se || (B[ce++] = R[z++], ce > se)); )
        B[ce++] = R[$++];
    }
    function qe(R) {
      let B = R.byteLength;
      const z = new Array();
      let $ = 0;
      const ce = new DataView(R);
      for (; B > 0; ) {
        const se = ce.getInt8($++);
        if (se < 0) {
          const pe = -se;
          B -= pe + 1;
          for (let Ne = 0; Ne < pe; Ne++)
            z.push(ce.getUint8($++));
        } else {
          const pe = se;
          B -= 2;
          const Ne = ce.getUint8($++);
          for (let De = 0; De < pe + 1; De++)
            z.push(Ne);
        }
      }
      return z;
    }
    function Ie(R, B, z, $, ce, se) {
      let pe = new DataView(se.buffer);
      const Ne = z[R.idx[0]].width, De = z[R.idx[0]].height, Te = 3, Ue = Math.floor(Ne / 8), at = Math.ceil(Ne / 8), ht = Math.ceil(De / 8), At = Ne - (at - 1) * 8, lt = De - (ht - 1) * 8, Oe = { value: 0 }, yt = new Array(Te), V = new Array(Te), le = new Array(Te), ie = new Array(Te), ge = new Array(Te);
      for (let oe = 0; oe < Te; ++oe)
        ge[oe] = B[R.idx[oe]], yt[oe] = oe < 1 ? 0 : yt[oe - 1] + at * ht, V[oe] = new Float32Array(64), le[oe] = new Uint16Array(64), ie[oe] = new Uint16Array(at * 64);
      for (let oe = 0; oe < ht; ++oe) {
        let He = 8;
        oe == ht - 1 && (He = lt);
        let ct = 8;
        for (let tt = 0; tt < at; ++tt) {
          tt == at - 1 && (ct = At);
          for (let ut = 0; ut < Te; ++ut)
            le[ut].fill(0), le[ut][0] = ce[yt[ut]++], vt(Oe, $, le[ut]), Ve(le[ut], V[ut]), Ze(V[ut]);
          G(V);
          for (let ut = 0; ut < Te; ++ut)
            D(V[ut], ie[ut], tt * 64);
        }
        let Ke = 0;
        for (let tt = 0; tt < Te; ++tt) {
          const ut = z[R.idx[tt]].type;
          for (let _t = 8 * oe; _t < 8 * oe + He; ++_t) {
            Ke = ge[tt][_t];
            for (let ri = 0; ri < Ue; ++ri) {
              const fi = ri * 64 + (_t & 7) * 8;
              pe.setUint16(Ke + 0 * 2 * ut, ie[tt][fi + 0], !0), pe.setUint16(Ke + 1 * 2 * ut, ie[tt][fi + 1], !0), pe.setUint16(Ke + 2 * 2 * ut, ie[tt][fi + 2], !0), pe.setUint16(Ke + 3 * 2 * ut, ie[tt][fi + 3], !0), pe.setUint16(Ke + 4 * 2 * ut, ie[tt][fi + 4], !0), pe.setUint16(Ke + 5 * 2 * ut, ie[tt][fi + 5], !0), pe.setUint16(Ke + 6 * 2 * ut, ie[tt][fi + 6], !0), pe.setUint16(Ke + 7 * 2 * ut, ie[tt][fi + 7], !0), Ke += 8 * 2 * ut;
            }
          }
          if (Ue != at)
            for (let _t = 8 * oe; _t < 8 * oe + He; ++_t) {
              const ri = ge[tt][_t] + 8 * Ue * 2 * ut, fi = Ue * 64 + (_t & 7) * 8;
              for (let Ui = 0; Ui < ct; ++Ui)
                pe.setUint16(ri + Ui * 2 * ut, ie[tt][fi + Ui], !0);
            }
        }
      }
      const Ce = new Uint16Array(Ne);
      pe = new DataView(se.buffer);
      for (let oe = 0; oe < Te; ++oe) {
        z[R.idx[oe]].decoded = !0;
        const He = z[R.idx[oe]].type;
        if (z[oe].type == 2)
          for (let ct = 0; ct < De; ++ct) {
            const Ke = ge[oe][ct];
            for (let tt = 0; tt < Ne; ++tt)
              Ce[tt] = pe.getUint16(Ke + tt * 2 * He, !0);
            for (let tt = 0; tt < Ne; ++tt)
              pe.setFloat32(Ke + tt * 2 * He, Q(Ce[tt]), !0);
          }
      }
    }
    function vt(R, B, z) {
      let $, ce = 1;
      for (; ce < 64; )
        $ = B[R.value], $ == 65280 ? ce = 64 : $ >> 8 == 255 ? ce += $ & 255 : (z[ce] = $, ce++), R.value++;
    }
    function Ve(R, B) {
      B[0] = Q(R[0]), B[1] = Q(R[1]), B[2] = Q(R[5]), B[3] = Q(R[6]), B[4] = Q(R[14]), B[5] = Q(R[15]), B[6] = Q(R[27]), B[7] = Q(R[28]), B[8] = Q(R[2]), B[9] = Q(R[4]), B[10] = Q(R[7]), B[11] = Q(R[13]), B[12] = Q(R[16]), B[13] = Q(R[26]), B[14] = Q(R[29]), B[15] = Q(R[42]), B[16] = Q(R[3]), B[17] = Q(R[8]), B[18] = Q(R[12]), B[19] = Q(R[17]), B[20] = Q(R[25]), B[21] = Q(R[30]), B[22] = Q(R[41]), B[23] = Q(R[43]), B[24] = Q(R[9]), B[25] = Q(R[11]), B[26] = Q(R[18]), B[27] = Q(R[24]), B[28] = Q(R[31]), B[29] = Q(R[40]), B[30] = Q(R[44]), B[31] = Q(R[53]), B[32] = Q(R[10]), B[33] = Q(R[19]), B[34] = Q(R[23]), B[35] = Q(R[32]), B[36] = Q(R[39]), B[37] = Q(R[45]), B[38] = Q(R[52]), B[39] = Q(R[54]), B[40] = Q(R[20]), B[41] = Q(R[22]), B[42] = Q(R[33]), B[43] = Q(R[38]), B[44] = Q(R[46]), B[45] = Q(R[51]), B[46] = Q(R[55]), B[47] = Q(R[60]), B[48] = Q(R[21]), B[49] = Q(R[34]), B[50] = Q(R[37]), B[51] = Q(R[47]), B[52] = Q(R[50]), B[53] = Q(R[56]), B[54] = Q(R[59]), B[55] = Q(R[61]), B[56] = Q(R[35]), B[57] = Q(R[36]), B[58] = Q(R[48]), B[59] = Q(R[49]), B[60] = Q(R[57]), B[61] = Q(R[58]), B[62] = Q(R[62]), B[63] = Q(R[63]);
    }
    function Ze(R) {
      const B = 0.5 * Math.cos(0.7853975), z = 0.5 * Math.cos(3.14159 / 16), $ = 0.5 * Math.cos(3.14159 / 8), ce = 0.5 * Math.cos(3 * 3.14159 / 16), se = 0.5 * Math.cos(5 * 3.14159 / 16), pe = 0.5 * Math.cos(3 * 3.14159 / 8), Ne = 0.5 * Math.cos(7 * 3.14159 / 16), De = new Array(4), Te = new Array(4), Ue = new Array(4), at = new Array(4);
      for (let ht = 0; ht < 8; ++ht) {
        const At = ht * 8;
        De[0] = $ * R[At + 2], De[1] = pe * R[At + 2], De[2] = $ * R[At + 6], De[3] = pe * R[At + 6], Te[0] = z * R[At + 1] + ce * R[At + 3] + se * R[At + 5] + Ne * R[At + 7], Te[1] = ce * R[At + 1] - Ne * R[At + 3] - z * R[At + 5] - se * R[At + 7], Te[2] = se * R[At + 1] - z * R[At + 3] + Ne * R[At + 5] + ce * R[At + 7], Te[3] = Ne * R[At + 1] - se * R[At + 3] + ce * R[At + 5] - z * R[At + 7], Ue[0] = B * (R[At + 0] + R[At + 4]), Ue[3] = B * (R[At + 0] - R[At + 4]), Ue[1] = De[0] + De[3], Ue[2] = De[1] - De[2], at[0] = Ue[0] + Ue[1], at[1] = Ue[3] + Ue[2], at[2] = Ue[3] - Ue[2], at[3] = Ue[0] - Ue[1], R[At + 0] = at[0] + Te[0], R[At + 1] = at[1] + Te[1], R[At + 2] = at[2] + Te[2], R[At + 3] = at[3] + Te[3], R[At + 4] = at[3] - Te[3], R[At + 5] = at[2] - Te[2], R[At + 6] = at[1] - Te[1], R[At + 7] = at[0] - Te[0];
      }
      for (let ht = 0; ht < 8; ++ht)
        De[0] = $ * R[16 + ht], De[1] = pe * R[16 + ht], De[2] = $ * R[48 + ht], De[3] = pe * R[48 + ht], Te[0] = z * R[8 + ht] + ce * R[24 + ht] + se * R[40 + ht] + Ne * R[56 + ht], Te[1] = ce * R[8 + ht] - Ne * R[24 + ht] - z * R[40 + ht] - se * R[56 + ht], Te[2] = se * R[8 + ht] - z * R[24 + ht] + Ne * R[40 + ht] + ce * R[56 + ht], Te[3] = Ne * R[8 + ht] - se * R[24 + ht] + ce * R[40 + ht] - z * R[56 + ht], Ue[0] = B * (R[ht] + R[32 + ht]), Ue[3] = B * (R[ht] - R[32 + ht]), Ue[1] = De[0] + De[3], Ue[2] = De[1] - De[2], at[0] = Ue[0] + Ue[1], at[1] = Ue[3] + Ue[2], at[2] = Ue[3] - Ue[2], at[3] = Ue[0] - Ue[1], R[0 + ht] = at[0] + Te[0], R[8 + ht] = at[1] + Te[1], R[16 + ht] = at[2] + Te[2], R[24 + ht] = at[3] + Te[3], R[32 + ht] = at[3] - Te[3], R[40 + ht] = at[2] - Te[2], R[48 + ht] = at[1] - Te[1], R[56 + ht] = at[0] - Te[0];
    }
    function G(R) {
      for (let B = 0; B < 64; ++B) {
        const z = R[0][B], $ = R[1][B], ce = R[2][B];
        R[0][B] = z + 1.5747 * ce, R[1][B] = z - 0.1873 * $ - 0.4682 * ce, R[2][B] = z + 1.8556 * $;
      }
    }
    function D(R, B, z) {
      for (let $ = 0; $ < 64; ++$)
        B[z + $] = el.toHalfFloat(ne(R[$]));
    }
    function ne(R) {
      return R <= 1 ? Math.sign(R) * Math.pow(Math.abs(R), 2.2) : Math.sign(R) * Math.pow(T, Math.abs(R) - 1);
    }
    function ve(R) {
      return new DataView(R.array.buffer, R.offset.value, R.size);
    }
    function Y(R) {
      const B = R.viewer.buffer.slice(R.offset.value, R.offset.value + R.size), z = new Uint8Array(qe(B)), $ = new Uint8Array(z.length);
      return Fe(z), Ge(z, $), new DataView($.buffer);
    }
    function ee(R) {
      const B = R.array.slice(R.offset.value, R.offset.value + R.size), z = Xv(B), $ = new Uint8Array(z.length);
      return Fe(z), Ge(z, $), new DataView($.buffer);
    }
    function we(R) {
      const B = R.viewer, z = { value: R.offset.value }, $ = new Uint16Array(R.columns * R.lines * (R.inputChannels.length * R.type)), ce = new Uint8Array(8192);
      let se = 0;
      const pe = new Array(R.inputChannels.length);
      for (let lt = 0, Oe = R.inputChannels.length; lt < Oe; lt++)
        pe[lt] = {}, pe[lt].start = se, pe[lt].end = pe[lt].start, pe[lt].nx = R.columns, pe[lt].ny = R.lines, pe[lt].size = R.type, se += pe[lt].nx * pe[lt].ny * pe[lt].size;
      const Ne = he(B, z), De = he(B, z);
      if (De >= 8192)
        throw new Error("Something is wrong with PIZ_COMPRESSION BITMAP_SIZE");
      if (Ne <= De)
        for (let lt = 0; lt < De - Ne + 1; lt++)
          ce[lt + Ne] = ot(B, z);
      const Te = new Uint16Array(65536), Ue = E(ce, Te), at = Ae(B, z);
      wt(R.array, B, z, at, $, se);
      for (let lt = 0; lt < R.inputChannels.length; ++lt) {
        const Oe = pe[lt];
        for (let yt = 0; yt < pe[lt].size; ++yt)
          Je(
            $,
            Oe.start + yt,
            Oe.nx,
            Oe.size,
            Oe.ny,
            Oe.nx * Oe.size,
            Ue
          );
      }
      k(Te, $, se);
      let ht = 0;
      const At = new Uint8Array($.buffer.byteLength);
      for (let lt = 0; lt < R.lines; lt++)
        for (let Oe = 0; Oe < R.inputChannels.length; Oe++) {
          const yt = pe[Oe], V = yt.nx * yt.size, le = new Uint8Array($.buffer, yt.end * 2, V * 2);
          At.set(le, ht), ht += V * 2, yt.end += V;
        }
      return new DataView(At.buffer);
    }
    function de(R) {
      const B = R.array.slice(R.offset.value, R.offset.value + R.size), z = Xv(B), $ = R.inputChannels.length * R.lines * R.columns * R.totalBytes, ce = new ArrayBuffer($), se = new DataView(ce);
      let pe = 0, Ne = 0;
      const De = new Array(4);
      for (let Te = 0; Te < R.lines; Te++)
        for (let Ue = 0; Ue < R.inputChannels.length; Ue++) {
          let at = 0;
          switch (R.inputChannels[Ue].pixelType) {
            case 1:
              De[0] = pe, De[1] = De[0] + R.columns, pe = De[1] + R.columns;
              for (let At = 0; At < R.columns; ++At) {
                const lt = z[De[0]++] << 8 | z[De[1]++];
                at += lt, se.setUint16(Ne, at, !0), Ne += 2;
              }
              break;
            case 2:
              De[0] = pe, De[1] = De[0] + R.columns, De[2] = De[1] + R.columns, pe = De[2] + R.columns;
              for (let At = 0; At < R.columns; ++At) {
                const lt = z[De[0]++] << 24 | z[De[1]++] << 16 | z[De[2]++] << 8;
                at += lt, se.setUint32(Ne, at, !0), Ne += 4;
              }
              break;
          }
        }
      return se;
    }
    function xe(R) {
      const B = R.viewer, z = { value: R.offset.value }, $ = new Uint8Array(R.columns * R.lines * (R.inputChannels.length * R.type * 2)), ce = {
        version: Ct(B, z),
        unknownUncompressedSize: Ct(B, z),
        unknownCompressedSize: Ct(B, z),
        acCompressedSize: Ct(B, z),
        dcCompressedSize: Ct(B, z),
        rleCompressedSize: Ct(B, z),
        rleUncompressedSize: Ct(B, z),
        rleRawSize: Ct(B, z),
        totalAcUncompressedCount: Ct(B, z),
        totalDcUncompressedCount: Ct(B, z),
        acCompression: Ct(B, z)
      };
      if (ce.version < 2)
        throw new Error("EXRLoader.parse: " + O.compression + " version " + ce.version + " is unsupported");
      const se = new Array();
      let pe = he(B, z) - 2;
      for (; pe > 0; ) {
        const Oe = it(B.buffer, z), yt = ot(B, z), V = yt >> 2 & 3, le = (yt >> 4) - 1, ie = new Int8Array([le])[0], ge = ot(B, z);
        se.push({
          name: Oe,
          index: ie,
          type: ge,
          compression: V
        }), pe -= Oe.length + 3;
      }
      const Ne = O.channels, De = new Array(R.inputChannels.length);
      for (let Oe = 0; Oe < R.inputChannels.length; ++Oe) {
        const yt = De[Oe] = {}, V = Ne[Oe];
        yt.name = V.name, yt.compression = 0, yt.decoded = !1, yt.type = V.pixelType, yt.pLinear = V.pLinear, yt.width = R.columns, yt.height = R.lines;
      }
      const Te = {
        idx: new Array(3)
      };
      for (let Oe = 0; Oe < R.inputChannels.length; ++Oe) {
        const yt = De[Oe];
        for (let V = 0; V < se.length; ++V) {
          const le = se[V];
          yt.name == le.name && (yt.compression = le.compression, le.index >= 0 && (Te.idx[le.index] = Oe), yt.offset = Oe);
        }
      }
      let Ue, at, ht;
      if (ce.acCompressedSize > 0)
        switch (ce.acCompression) {
          case 0:
            Ue = new Uint16Array(ce.totalAcUncompressedCount), wt(R.array, B, z, ce.acCompressedSize, Ue, ce.totalAcUncompressedCount);
            break;
          case 1:
            const Oe = R.array.slice(z.value, z.value + ce.totalAcUncompressedCount), yt = Xv(Oe);
            Ue = new Uint16Array(yt.buffer), z.value += ce.totalAcUncompressedCount;
            break;
        }
      if (ce.dcCompressedSize > 0) {
        const Oe = {
          array: R.array,
          offset: z,
          size: ce.dcCompressedSize
        };
        at = new Uint16Array(ee(Oe).buffer), z.value += ce.dcCompressedSize;
      }
      if (ce.rleRawSize > 0) {
        const Oe = R.array.slice(z.value, z.value + ce.rleCompressedSize), yt = Xv(Oe);
        ht = qe(yt.buffer), z.value += ce.rleCompressedSize;
      }
      let At = 0;
      const lt = new Array(De.length);
      for (let Oe = 0; Oe < lt.length; ++Oe)
        lt[Oe] = new Array();
      for (let Oe = 0; Oe < R.lines; ++Oe)
        for (let yt = 0; yt < De.length; ++yt)
          lt[yt].push(At), At += De[yt].width * R.type * 2;
      Ie(Te, lt, De, Ue, at, $);
      for (let Oe = 0; Oe < De.length; ++Oe) {
        const yt = De[Oe];
        if (!yt.decoded)
          switch (yt.compression) {
            case 2:
              let V = 0, le = 0;
              for (let ie = 0; ie < R.lines; ++ie) {
                let ge = lt[Oe][V];
                for (let Ce = 0; Ce < yt.width; ++Ce) {
                  for (let oe = 0; oe < 2 * yt.type; ++oe)
                    $[ge++] = ht[le + oe * yt.width * yt.height];
                  le++;
                }
                V++;
              }
              break;
            case 1:
            default:
              throw new Error("EXRLoader.parse: unsupported channel compression");
          }
      }
      return new DataView($.buffer);
    }
    function it(R, B) {
      const z = new Uint8Array(R);
      let $ = 0;
      for (; z[B.value + $] != 0; )
        $ += 1;
      const ce = new TextDecoder().decode(
        z.slice(B.value, B.value + $)
      );
      return B.value = B.value + $ + 1, ce;
    }
    function q(R, B, z) {
      const $ = new TextDecoder().decode(
        new Uint8Array(R).slice(B.value, B.value + z)
      );
      return B.value = B.value + z, $;
    }
    function ae(R, B) {
      const z = Le(R, B), $ = Ae(R, B);
      return [z, $];
    }
    function Ee(R, B) {
      const z = Ae(R, B), $ = Ae(R, B);
      return [z, $];
    }
    function Le(R, B) {
      const z = R.getInt32(B.value, !0);
      return B.value = B.value + 4, z;
    }
    function Ae(R, B) {
      const z = R.getUint32(B.value, !0);
      return B.value = B.value + 4, z;
    }
    function We(R, B) {
      const z = R[B.value];
      return B.value = B.value + 1, z;
    }
    function ot(R, B) {
      const z = R.getUint8(B.value);
      return B.value = B.value + 1, z;
    }
    const Ct = function(R, B) {
      let z;
      return "getBigInt64" in DataView.prototype ? z = Number(R.getBigInt64(B.value, !0)) : z = R.getUint32(B.value + 4, !0) + Number(R.getUint32(B.value, !0) << 32), B.value += 8, z;
    };
    function F(R, B) {
      const z = R.getFloat32(B.value, !0);
      return B.value += 4, z;
    }
    function ue(R, B) {
      return el.toHalfFloat(F(R, B));
    }
    function Q(R) {
      const B = (R & 31744) >> 10, z = R & 1023;
      return (R >> 15 ? -1 : 1) * (B ? B === 31 ? z ? NaN : 1 / 0 : Math.pow(2, B - 15) * (1 + z / 1024) : 6103515625e-14 * (z / 1024));
    }
    function he(R, B) {
      const z = R.getUint16(B.value, !0);
      return B.value += 2, z;
    }
    function Be(R, B) {
      return Q(he(R, B));
    }
    function ft(R, B, z, $) {
      const ce = z.value, se = [];
      for (; z.value < ce + $ - 1; ) {
        const pe = it(B, z), Ne = Le(R, z), De = ot(R, z);
        z.value += 3;
        const Te = Le(R, z), Ue = Le(R, z);
        se.push({
          name: pe,
          pixelType: Ne,
          pLinear: De,
          xSampling: Te,
          ySampling: Ue
        });
      }
      return z.value += 1, se;
    }
    function Et(R, B) {
      const z = F(R, B), $ = F(R, B), ce = F(R, B), se = F(R, B), pe = F(R, B), Ne = F(R, B), De = F(R, B), Te = F(R, B);
      return { redX: z, redY: $, greenX: ce, greenY: se, blueX: pe, blueY: Ne, whiteX: De, whiteY: Te };
    }
    function ei(R, B) {
      const z = [
        "NO_COMPRESSION",
        "RLE_COMPRESSION",
        "ZIPS_COMPRESSION",
        "ZIP_COMPRESSION",
        "PIZ_COMPRESSION",
        "PXR24_COMPRESSION",
        "B44_COMPRESSION",
        "B44A_COMPRESSION",
        "DWAA_COMPRESSION",
        "DWAB_COMPRESSION"
      ], $ = ot(R, B);
      return z[$];
    }
    function di(R, B) {
      const z = Le(R, B), $ = Le(R, B), ce = Le(R, B), se = Le(R, B);
      return { xMin: z, yMin: $, xMax: ce, yMax: se };
    }
    function Ot(R, B) {
      const z = [
        "INCREASING_Y",
        "DECREASING_Y",
        "RANDOM_Y"
      ], $ = ot(R, B);
      return z[$];
    }
    function Wi(R, B) {
      const z = [
        "ENVMAP_LATLONG",
        "ENVMAP_CUBE"
      ], $ = ot(R, B);
      return z[$];
    }
    function Li(R, B) {
      const z = [
        "ONE_LEVEL",
        "MIPMAP_LEVELS",
        "RIPMAP_LEVELS"
      ], $ = [
        "ROUND_DOWN",
        "ROUND_UP"
      ], ce = Ae(R, B), se = Ae(R, B), pe = ot(R, B);
      return {
        xSize: ce,
        ySize: se,
        levelMode: z[pe & 15],
        roundingMode: $[pe >> 4]
      };
    }
    function cr(R, B) {
      const z = F(R, B), $ = F(R, B);
      return [z, $];
    }
    function sn(R, B) {
      const z = F(R, B), $ = F(R, B), ce = F(R, B);
      return [z, $, ce];
    }
    function Os(R, B, z, $, ce) {
      if ($ === "string" || $ === "stringvector" || $ === "iccProfile")
        return q(B, z, ce);
      if ($ === "chlist")
        return ft(R, B, z, ce);
      if ($ === "chromaticities")
        return Et(R, z);
      if ($ === "compression")
        return ei(R, z);
      if ($ === "box2i")
        return di(R, z);
      if ($ === "envmap")
        return Wi(R, z);
      if ($ === "tiledesc")
        return Li(R, z);
      if ($ === "lineOrder")
        return Ot(R, z);
      if ($ === "float")
        return F(R, z);
      if ($ === "v2f")
        return cr(R, z);
      if ($ === "v3f")
        return sn(R, z);
      if ($ === "int")
        return Le(R, z);
      if ($ === "rational")
        return ae(R, z);
      if ($ === "timecode")
        return Ee(R, z);
      if ($ === "preview")
        return z.value += ce, "skipped";
      z.value += ce;
    }
    function br(R, B) {
      const z = Math.log2(R);
      return B == "ROUND_DOWN" ? Math.floor(z) : Math.ceil(z);
    }
    function kr(R, B, z) {
      let $ = 0;
      switch (R.levelMode) {
        case "ONE_LEVEL":
          $ = 1;
          break;
        case "MIPMAP_LEVELS":
          $ = br(Math.max(B, z), R.roundingMode) + 1;
          break;
        case "RIPMAP_LEVELS":
          throw new Error("THREE.EXRLoader: RIPMAP_LEVELS tiles currently unsupported.");
      }
      return $;
    }
    function Mr(R, B, z, $) {
      const ce = new Array(R);
      for (let se = 0; se < R; se++) {
        const pe = 1 << se;
        let Ne = B / pe | 0;
        $ == "ROUND_UP" && Ne * pe < B && (Ne += 1);
        const De = Math.max(Ne, 1);
        ce[se] = (De + z - 1) / z | 0;
      }
      return ce;
    }
    function A0() {
      const R = this, B = R.offset, z = { value: 0 };
      for (let $ = 0; $ < R.tileCount; $++) {
        const ce = Le(R.viewer, B), se = Le(R.viewer, B);
        B.value += 8, R.size = Ae(R.viewer, B);
        const pe = ce * R.blockWidth, Ne = se * R.blockHeight;
        R.columns = pe + R.blockWidth > R.width ? R.width - pe : R.blockWidth, R.lines = Ne + R.blockHeight > R.height ? R.height - Ne : R.blockHeight;
        const De = R.columns * R.totalBytes, Ue = R.size < R.lines * De ? R.uncompress(R) : ve(R);
        B.value += R.size;
        for (let at = 0; at < R.lines; at++) {
          const ht = at * R.columns * R.totalBytes;
          for (let At = 0; At < R.inputChannels.length; At++) {
            const lt = O.channels[At].name, Oe = R.channelByteOffsets[lt] * R.columns, yt = R.decodeChannels[lt];
            if (yt === void 0)
              continue;
            z.value = ht + Oe;
            const V = (R.height - (1 + Ne + at)) * R.outLineWidth;
            for (let le = 0; le < R.columns; le++) {
              const ie = V + (le + pe) * R.outputChannels + yt;
              R.byteArray[ie] = R.getter(Ue, z);
            }
          }
        }
      }
    }
    function Gr() {
      const R = this, B = R.offset, z = { value: 0 };
      for (let $ = 0; $ < R.height / R.blockHeight; $++) {
        const ce = Le(R.viewer, B) - O.dataWindow.yMin;
        R.size = Ae(R.viewer, B), R.lines = ce + R.blockHeight > R.height ? R.height - ce : R.blockHeight;
        const se = R.columns * R.totalBytes, Ne = R.size < R.lines * se ? R.uncompress(R) : ve(R);
        B.value += R.size;
        for (let De = 0; De < R.blockHeight; De++) {
          const Te = $ * R.blockHeight, Ue = De + R.scanOrder(Te);
          if (Ue >= R.height)
            continue;
          const at = De * se, ht = (R.height - 1 - Ue) * R.outLineWidth;
          for (let At = 0; At < R.inputChannels.length; At++) {
            const lt = O.channels[At].name, Oe = R.channelByteOffsets[lt] * R.columns, yt = R.decodeChannels[lt];
            if (yt !== void 0) {
              z.value = at + Oe;
              for (let V = 0; V < R.columns; V++) {
                const le = ht + V * R.outputChannels + yt;
                R.byteArray[le] = R.getter(Ne, z);
              }
            }
          }
        }
      }
    }
    function b0(R, B, z) {
      const $ = {};
      if (R.getUint32(0, !0) != 20000630)
        throw new Error("THREE.EXRLoader: Provided file doesn't appear to be in OpenEXR format.");
      $.version = R.getUint8(4);
      const ce = R.getUint8(5);
      $.spec = {
        singleTile: !!(ce & 2),
        longName: !!(ce & 4),
        deepFormat: !!(ce & 8),
        multiPart: !!(ce & 16)
      }, z.value = 8;
      let se = !0;
      for (; se; ) {
        const pe = it(B, z);
        if (pe == 0)
          se = !1;
        else {
          const Ne = it(B, z), De = Ae(R, z), Te = Os(R, B, z, Ne, De);
          Te === void 0 ? console.warn(`THREE.EXRLoader: Skipped unknown header attribute type '${Ne}'.`) : $[pe] = Te;
        }
      }
      if (ce & -7)
        throw console.error("THREE.EXRHeader:", $), new Error("THREE.EXRLoader: Provided file is currently unsupported.");
      return $;
    }
    function M0(R, B, z, $, ce) {
      const se = {
        size: 0,
        viewer: B,
        array: z,
        offset: $,
        width: R.dataWindow.xMax - R.dataWindow.xMin + 1,
        height: R.dataWindow.yMax - R.dataWindow.yMin + 1,
        inputChannels: R.channels,
        channelByteOffsets: {},
        scanOrder: null,
        totalBytes: null,
        columns: null,
        lines: null,
        type: null,
        uncompress: null,
        getter: null,
        format: null,
        colorSpace: Si
      };
      switch (R.compression) {
        case "NO_COMPRESSION":
          se.blockHeight = 1, se.uncompress = ve;
          break;
        case "RLE_COMPRESSION":
          se.blockHeight = 1, se.uncompress = Y;
          break;
        case "ZIPS_COMPRESSION":
          se.blockHeight = 1, se.uncompress = ee;
          break;
        case "ZIP_COMPRESSION":
          se.blockHeight = 16, se.uncompress = ee;
          break;
        case "PIZ_COMPRESSION":
          se.blockHeight = 32, se.uncompress = we;
          break;
        case "PXR24_COMPRESSION":
          se.blockHeight = 16, se.uncompress = de;
          break;
        case "DWAA_COMPRESSION":
          se.blockHeight = 32, se.uncompress = xe;
          break;
        case "DWAB_COMPRESSION":
          se.blockHeight = 256, se.uncompress = xe;
          break;
        default:
          throw new Error("EXRLoader.parse: " + R.compression + " is unsupported");
      }
      const pe = {};
      for (const Ue of R.channels)
        switch (Ue.name) {
          case "Y":
          case "R":
          case "G":
          case "B":
          case "A":
            pe[Ue.name] = !0, se.type = Ue.pixelType;
        }
      let Ne = !1;
      if (pe.R && pe.G && pe.B)
        Ne = !pe.A, se.outputChannels = 4, se.decodeChannels = { R: 0, G: 1, B: 2, A: 3 };
      else if (pe.Y)
        se.outputChannels = 1, se.decodeChannels = { Y: 0 };
      else
        throw new Error("EXRLoader.parse: file contains unsupported data channels.");
      if (se.type == 1)
        switch (ce) {
          case xn:
            se.getter = Be;
            break;
          case on:
            se.getter = he;
            break;
        }
      else if (se.type == 2)
        switch (ce) {
          case xn:
            se.getter = F;
            break;
          case on:
            se.getter = ue;
        }
      else
        throw new Error("EXRLoader.parse: unsupported pixelType " + se.type + " for " + R.compression + ".");
      se.columns = se.width;
      const De = se.width * se.height * se.outputChannels;
      switch (ce) {
        case xn:
          se.byteArray = new Float32Array(De), Ne && se.byteArray.fill(1, 0, De);
          break;
        case on:
          se.byteArray = new Uint16Array(De), Ne && se.byteArray.fill(15360, 0, De);
          break;
        default:
          console.error("THREE.EXRLoader: unsupported type: ", ce);
          break;
      }
      let Te = 0;
      for (const Ue of R.channels)
        se.decodeChannels[Ue.name] !== void 0 && (se.channelByteOffsets[Ue.name] = Te), Te += Ue.pixelType * 2;
      if (se.totalBytes = Te, se.outLineWidth = se.width * se.outputChannels, R.lineOrder === "INCREASING_Y" ? se.scanOrder = (Ue) => Ue : se.scanOrder = (Ue) => se.height - 1 - Ue, se.outputChannels == 4 ? (se.format = dn, se.colorSpace = Si) : (se.format = Vu, se.colorSpace = Uo), R.spec.singleTile) {
        se.blockHeight = R.tiles.ySize, se.blockWidth = R.tiles.xSize;
        const Ue = kr(R.tiles, se.width, se.height), at = Mr(Ue, se.width, R.tiles.xSize, R.tiles.roundingMode), ht = Mr(Ue, se.height, R.tiles.ySize, R.tiles.roundingMode);
        se.tileCount = at[0] * ht[0];
        for (let At = 0; At < Ue; At++)
          for (let lt = 0; lt < ht[At]; lt++)
            for (let Oe = 0; Oe < at[At]; Oe++)
              Ct(B, $);
        se.decode = A0.bind(se);
      } else {
        se.blockWidth = se.width;
        const Ue = Math.ceil(se.height / se.blockHeight);
        for (let at = 0; at < Ue; at++)
          Ct(B, $);
        se.decode = Gr.bind(se);
      }
      return se;
    }
    const Ll = { value: 0 }, Il = new DataView(e), dh = new Uint8Array(e), O = b0(Il, e, Ll), Z = M0(O, Il, dh, Ll, this.type);
    return Z.decode(), {
      header: O,
      width: Z.width,
      height: Z.height,
      data: Z.byteArray,
      format: Z.format,
      colorSpace: Z.colorSpace,
      type: this.type
    };
  }
  setDataType(e) {
    return this.type = e, this;
  }
  load(e, t, i, n) {
    function r(a, o) {
      a.colorSpace = o.colorSpace, a.minFilter = ii, a.magFilter = ii, a.generateMipmaps = !1, a.flipY = !1, t && t(a, o);
    }
    return super.load(e, r, i, n);
  }
}
class _te extends R4 {
  constructor(e) {
    super(e), this.type = on;
  }
  // adapted from http://www.graphics.cornell.edu/~bjw/rgbe.html
  parse(e) {
    const a = function(C, T) {
      switch (C) {
        case 1:
          throw new Error("THREE.RGBELoader: Read Error: " + (T || ""));
        case 2:
          throw new Error("THREE.RGBELoader: Write Error: " + (T || ""));
        case 3:
          throw new Error("THREE.RGBELoader: Bad File Format: " + (T || ""));
        default:
        case 4:
          throw new Error("THREE.RGBELoader: Memory Error: " + (T || ""));
      }
    }, h = `
`, u = function(C, T, E) {
      T = T || 1024;
      let L = C.pos, I = -1, N = 0, H = "", U = String.fromCharCode.apply(null, new Uint16Array(C.subarray(L, L + 128)));
      for (; 0 > (I = U.indexOf(h)) && N < T && L < C.byteLength; )
        H += U, N += U.length, L += 128, U += String.fromCharCode.apply(null, new Uint16Array(C.subarray(L, L + 128)));
      return -1 < I ? ((C.pos += N + I + 1), H + U.slice(0, I)) : !1;
    }, d = function(C) {
      const T = /^#\?(\S+)/, E = /^\s*GAMMA\s*=\s*(\d+(\.\d+)?)\s*$/, P = /^\s*EXPOSURE\s*=\s*(\d+(\.\d+)?)\s*$/, L = /^\s*FORMAT=(\S+)\s*$/, I = /^\s*\-Y\s+(\d+)\s+\+X\s+(\d+)\s*$/, N = {
        valid: 0,
        /* indicate which fields are valid */
        string: "",
        /* the actual header string */
        comments: "",
        /* comments found in header */
        programtype: "RGBE",
        /* listed at beginning of file to identify it after "#?". defaults to "RGBE" */
        format: "",
        /* RGBE format, default 32-bit_rle_rgbe */
        gamma: 1,
        /* image has already been gamma corrected with given gamma. defaults to 1.0 (no correction) */
        exposure: 1,
        /* a value of 1.0 in an image corresponds to <exposure> watts/steradian/m^2. defaults to 1.0 */
        width: 0,
        height: 0
        /* image dimensions, width/height */
      };
      let H, U;
      for ((C.pos >= C.byteLength || !(H = u(C))) && a(1, "no header found"), (U = H.match(T)) || a(3, "bad initial token"), N.valid |= 1, N.programtype = U[1], N.string += H + `
`; H = u(C), H !== !1; ) {
        if (N.string += H + `
`, H.charAt(0) === "#") {
          N.comments += H + `
`;
          continue;
        }
        if ((U = H.match(E)) && (N.gamma = parseFloat(U[1])), (U = H.match(P)) && (N.exposure = parseFloat(U[1])), (U = H.match(L)) && (N.valid |= 2, N.format = U[1]), (U = H.match(I)) && (N.valid |= 4, N.height = parseInt(U[1], 10), N.width = parseInt(U[2], 10)), N.valid & 2 && N.valid & 4)
          break;
      }
      return N.valid & 2 || a(3, "missing format specifier"), N.valid & 4 || a(3, "missing image size specifier"), N;
    }, f = function(C, T, E) {
      const P = T;
      if (
        // run length encoding is not allowed so read flat
        P < 8 || P > 32767 || // this file is not run length encoded
        C[0] !== 2 || C[1] !== 2 || C[2] & 128
      )
        return new Uint8Array(C);
      P !== (C[2] << 8 | C[3]) && a(3, "wrong scanline width");
      const L = new Uint8Array(4 * T * E);
      L.length || a(4, "unable to allocate buffer space");
      let I = 0, N = 0;
      const H = 4 * P, U = new Uint8Array(4), W = new Uint8Array(H);
      let X = E;
      for (; X > 0 && N < C.byteLength; ) {
        N + 4 > C.byteLength && a(1), U[0] = C[N++], U[1] = C[N++], U[2] = C[N++], U[3] = C[N++], (U[0] != 2 || U[1] != 2 || (U[2] << 8 | U[3]) != P) && a(3, "bad rgbe scanline format");
        let K = 0, te;
        for (; K < H && N < C.byteLength; ) {
          te = C[N++];
          const be = te > 128;
          if (be && (te -= 128), (te === 0 || K + te > H) && a(3, "bad scanline data"), be) {
            const Pe = C[N++];
            for (let J = 0; J < te; J++)
              W[K++] = Pe;
          } else
            W.set(C.subarray(N, N + te), K), K += te, N += te;
        }
        const re = P;
        for (let be = 0; be < re; be++) {
          let Pe = 0;
          L[I] = W[be + Pe], Pe += P, L[I + 1] = W[be + Pe], Pe += P, L[I + 2] = W[be + Pe], Pe += P, L[I + 3] = W[be + Pe], I += 4;
        }
        X--;
      }
      return L;
    }, p = function(C, T, E, P) {
      const L = C[T + 3], I = Math.pow(2, L - 128) / 255;
      E[P + 0] = C[T + 0] * I, E[P + 1] = C[T + 1] * I, E[P + 2] = C[T + 2] * I, E[P + 3] = 1;
    }, v = function(C, T, E, P) {
      const L = C[T + 3], I = Math.pow(2, L - 128) / 255;
      E[P + 0] = el.toHalfFloat(Math.min(C[T + 0] * I, 65504)), E[P + 1] = el.toHalfFloat(Math.min(C[T + 1] * I, 65504)), E[P + 2] = el.toHalfFloat(Math.min(C[T + 2] * I, 65504)), E[P + 3] = el.toHalfFloat(1);
    }, g = new Uint8Array(e);
    g.pos = 0;
    const m = d(g), x = m.width, y = m.height, A = f(g.subarray(g.pos), x, y);
    let b, M, w;
    switch (this.type) {
      case xn:
        w = A.length / 4;
        const C = new Float32Array(w * 4);
        for (let E = 0; E < w; E++)
          p(A, E * 4, C, E * 4);
        b = C, M = xn;
        break;
      case on:
        w = A.length / 4;
        const T = new Uint16Array(w * 4);
        for (let E = 0; E < w; E++)
          v(A, E * 4, T, E * 4);
        b = T, M = on;
        break;
      default:
        throw new Error("THREE.RGBELoader: Unsupported type: " + this.type);
    }
    return {
      width: x,
      height: y,
      data: b,
      header: m.string,
      gamma: m.gamma,
      exposure: m.exposure,
      type: M
    };
  }
  setDataType(e) {
    return this.type = e, this;
  }
  load(e, t, i, n) {
    function r(a, o) {
      switch (a.type) {
        case xn:
        case on:
          a.colorSpace = Si, a.minFilter = ii, a.magFilter = ii, a.generateMipmaps = !1, a.flipY = !0;
          break;
      }
      t && t(a, o);
    }
    return super.load(e, r, i, n);
  }
}
const z5 = /* @__PURE__ */ new WeakMap();
class J4 extends As {
  constructor(e) {
    super(e), this.decoderPath = "", this.decoderConfig = {}, this.decoderBinary = null, this.decoderPending = null, this.workerLimit = 4, this.workerPool = [], this.workerNextTaskID = 1, this.workerSourceURL = "", this.defaultAttributeIDs = {
      position: "POSITION",
      normal: "NORMAL",
      color: "COLOR",
      uv: "TEX_COORD"
    }, this.defaultAttributeTypes = {
      position: "Float32Array",
      normal: "Float32Array",
      color: "Float32Array",
      uv: "Float32Array"
    };
  }
  setDecoderPath(e) {
    return this.decoderPath = e, this;
  }
  setDecoderConfig(e) {
    return this.decoderConfig = e, this;
  }
  setWorkerLimit(e) {
    return this.workerLimit = e, this;
  }
  load(e, t, i, n) {
    const r = new Nn(this.manager);
    r.setPath(this.path), r.setResponseType("arraybuffer"), r.setRequestHeader(this.requestHeader), r.setWithCredentials(this.withCredentials), r.load(e, (a) => {
      this.parse(a, t, n);
    }, i, n);
  }
  parse(e, t, i = () => {
  }) {
    this.decodeDracoFile(e, t, null, null, qi, i).catch(i);
  }
  decodeDracoFile(e, t, i, n, r = Si, a = () => {
  }) {
    const o = {
      attributeIDs: i || this.defaultAttributeIDs,
      attributeTypes: n || this.defaultAttributeTypes,
      useUniqueIDs: !!i,
      vertexColorSpace: r
    };
    return this.decodeGeometry(e, o).then(t).catch(a);
  }
  decodeGeometry(e, t) {
    const i = JSON.stringify(t);
    if (z5.has(e)) {
      const l = z5.get(e);
      if (l.key === i)
        return l.promise;
      if (e.byteLength === 0)
        throw new Error(
          "THREE.DRACOLoader: Unable to re-decode a buffer with different settings. Buffer has already been transferred."
        );
    }
    let n;
    const r = this.workerNextTaskID++, a = e.byteLength, o = this._getWorker(r, a).then((l) => (n = l, new Promise((c, h) => {
      n._callbacks[r] = { resolve: c, reject: h }, n.postMessage({ type: "decode", id: r, taskConfig: t, buffer: e }, [e]);
    }))).then((l) => this._createGeometry(l.geometry));
    return o.catch(() => !0).then(() => {
      n && r && this._releaseTask(n, r);
    }), z5.set(e, {
      key: i,
      promise: o
    }), o;
  }
  _createGeometry(e) {
    const t = new Pt();
    e.index && t.setIndex(new Ht(e.index.array, 1));
    for (let i = 0; i < e.attributes.length; i++) {
      const n = e.attributes[i], r = n.name, a = n.array, o = n.itemSize, l = new Ht(a, o);
      r === "color" && (this._assignVertexColorSpace(l, n.vertexColorSpace), l.normalized = !(a instanceof Float32Array)), t.setAttribute(r, l);
    }
    return t;
  }
  _assignVertexColorSpace(e, t) {
    if (t !== qi)
      return;
    const i = new _e();
    for (let n = 0, r = e.count; n < r; n++)
      i.fromBufferAttribute(e, n).convertSRGBToLinear(), e.setXYZ(n, i.r, i.g, i.b);
  }
  _loadLibrary(e, t) {
    const i = new Nn(this.manager);
    return i.setPath(this.decoderPath), i.setResponseType(t), i.setWithCredentials(this.withCredentials), new Promise((n, r) => {
      i.load(e, n, void 0, r);
    });
  }
  preload() {
    return this._initDecoder(), this;
  }
  _initDecoder() {
    if (this.decoderPending)
      return this.decoderPending;
    const e = typeof WebAssembly != "object" || this.decoderConfig.type === "js", t = [];
    return e ? t.push(this._loadLibrary("draco_decoder.js", "text")) : (t.push(this._loadLibrary("draco_wasm_wrapper.js", "text")), t.push(this._loadLibrary("draco_decoder.wasm", "arraybuffer"))), this.decoderPending = Promise.all(t).then((i) => {
      const n = i[0];
      e || (this.decoderConfig.wasmBinary = i[1]);
      const r = Pte.toString(), a = [
        "/* draco decoder */",
        n,
        "",
        "/* worker */",
        r.substring(r.indexOf("{") + 1, r.lastIndexOf("}"))
      ].join(`
`);
      this.workerSourceURL = URL.createObjectURL(new Blob([a]));
    }), this.decoderPending;
  }
  _getWorker(e, t) {
    return this._initDecoder().then(() => {
      if (this.workerPool.length < this.workerLimit) {
        const n = new Worker(this.workerSourceURL);
        n._callbacks = {}, n._taskCosts = {}, n._taskLoad = 0, n.postMessage({ type: "init", decoderConfig: this.decoderConfig }), n.onmessage = function(r) {
          const a = r.data;
          switch (a.type) {
            case "decode":
              n._callbacks[a.id].resolve(a);
              break;
            case "error":
              n._callbacks[a.id].reject(a);
              break;
            default:
              console.error('THREE.DRACOLoader: Unexpected message, "' + a.type + '"');
          }
        }, this.workerPool.push(n);
      } else
        this.workerPool.sort(function(n, r) {
          return n._taskLoad > r._taskLoad ? -1 : 1;
        });
      const i = this.workerPool[this.workerPool.length - 1];
      return i._taskCosts[e] = t, i._taskLoad += t, i;
    });
  }
  _releaseTask(e, t) {
    e._taskLoad -= e._taskCosts[t], delete e._callbacks[t], delete e._taskCosts[t];
  }
  debug() {
    console.log("Task load: ", this.workerPool.map((e) => e._taskLoad));
  }
  dispose() {
    for (let e = 0; e < this.workerPool.length; ++e)
      this.workerPool[e].terminate();
    return this.workerPool.length = 0, this.workerSourceURL !== "" && URL.revokeObjectURL(this.workerSourceURL), this;
  }
}
function Pte() {
  let s, e;
  onmessage = function(a) {
    const o = a.data;
    switch (o.type) {
      case "init":
        s = o.decoderConfig, e = new Promise(function(h) {
          s.onModuleLoaded = function(u) {
            h({ draco: u });
          }, DracoDecoderModule(s);
        });
        break;
      case "decode":
        const l = o.buffer, c = o.taskConfig;
        e.then((h) => {
          const u = h.draco, d = new u.Decoder();
          try {
            const f = t(u, d, new Int8Array(l), c), p = f.attributes.map((v) => v.array.buffer);
            f.index && p.push(f.index.array.buffer), self.postMessage({ type: "decode", id: o.id, geometry: f }, p);
          } catch (f) {
            console.error(f), self.postMessage({ type: "error", id: o.id, error: f.message });
          } finally {
            u.destroy(d);
          }
        });
        break;
    }
  };
  function t(a, o, l, c) {
    const h = c.attributeIDs, u = c.attributeTypes;
    let d, f;
    const p = o.GetEncodedGeometryType(l);
    if (p === a.TRIANGULAR_MESH)
      d = new a.Mesh(), f = o.DecodeArrayToMesh(l, l.byteLength, d);
    else if (p === a.POINT_CLOUD)
      d = new a.PointCloud(), f = o.DecodeArrayToPointCloud(l, l.byteLength, d);
    else
      throw new Error("THREE.DRACOLoader: Unexpected geometry type.");
    if (!f.ok() || d.ptr === 0)
      throw new Error("THREE.DRACOLoader: Decoding failed: " + f.error_msg());
    const v = { index: null, attributes: [] };
    for (const g in h) {
      const m = self[u[g]];
      let x, y;
      if (c.useUniqueIDs)
        y = h[g], x = o.GetAttributeByUniqueId(d, y);
      else {
        if (y = o.GetAttributeId(d, a[h[g]]), y === -1)
          continue;
        x = o.GetAttribute(d, y);
      }
      const A = n(a, o, d, g, m, x);
      g === "color" && (A.vertexColorSpace = c.vertexColorSpace), v.attributes.push(A);
    }
    return p === a.TRIANGULAR_MESH && (v.index = i(a, o, d)), a.destroy(d), v;
  }
  function i(a, o, l) {
    const h = l.num_faces() * 3, u = h * 4, d = a._malloc(u);
    o.GetTrianglesUInt32Array(l, u, d);
    const f = new Uint32Array(a.HEAPF32.buffer, d, h).slice();
    return a._free(d), { array: f, itemSize: 1 };
  }
  function n(a, o, l, c, h, u) {
    const d = u.num_components(), p = l.num_points() * d, v = p * h.BYTES_PER_ELEMENT, g = r(a, h), m = a._malloc(v);
    o.GetAttributeDataArrayForAllPoints(l, u, g, v, m);
    const x = new h(a.HEAPF32.buffer, m, p).slice();
    return a._free(m), {
      name: c,
      array: x,
      itemSize: d
    };
  }
  function r(a, o) {
    switch (o) {
      case Float32Array:
        return a.DT_FLOAT32;
      case Int8Array:
        return a.DT_INT8;
      case Int16Array:
        return a.DT_INT16;
      case Int32Array:
        return a.DT_INT32;
      case Uint8Array:
        return a.DT_UINT8;
      case Uint16Array:
        return a.DT_UINT16;
      case Uint32Array:
        return a.DT_UINT32;
    }
  }
}
let oN = class {
  constructor(e = Math) {
    this.grad3 = [
      [1, 1, 0],
      [-1, 1, 0],
      [1, -1, 0],
      [-1, -1, 0],
      [1, 0, 1],
      [-1, 0, 1],
      [1, 0, -1],
      [-1, 0, -1],
      [0, 1, 1],
      [0, -1, 1],
      [0, 1, -1],
      [0, -1, -1]
    ], this.grad4 = [
      [0, 1, 1, 1],
      [0, 1, 1, -1],
      [0, 1, -1, 1],
      [0, 1, -1, -1],
      [0, -1, 1, 1],
      [0, -1, 1, -1],
      [0, -1, -1, 1],
      [0, -1, -1, -1],
      [1, 0, 1, 1],
      [1, 0, 1, -1],
      [1, 0, -1, 1],
      [1, 0, -1, -1],
      [-1, 0, 1, 1],
      [-1, 0, 1, -1],
      [-1, 0, -1, 1],
      [-1, 0, -1, -1],
      [1, 1, 0, 1],
      [1, 1, 0, -1],
      [1, -1, 0, 1],
      [1, -1, 0, -1],
      [-1, 1, 0, 1],
      [-1, 1, 0, -1],
      [-1, -1, 0, 1],
      [-1, -1, 0, -1],
      [1, 1, 1, 0],
      [1, 1, -1, 0],
      [1, -1, 1, 0],
      [1, -1, -1, 0],
      [-1, 1, 1, 0],
      [-1, 1, -1, 0],
      [-1, -1, 1, 0],
      [-1, -1, -1, 0]
    ], this.p = [];
    for (let t = 0; t < 256; t++)
      this.p[t] = Math.floor(e.random() * 256);
    this.perm = [];
    for (let t = 0; t < 512; t++)
      this.perm[t] = this.p[t & 255];
    this.simplex = [
      [0, 1, 2, 3],
      [0, 1, 3, 2],
      [0, 0, 0, 0],
      [0, 2, 3, 1],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [1, 2, 3, 0],
      [0, 2, 1, 3],
      [0, 0, 0, 0],
      [0, 3, 1, 2],
      [0, 3, 2, 1],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [1, 3, 2, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [1, 2, 0, 3],
      [0, 0, 0, 0],
      [1, 3, 0, 2],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [2, 3, 0, 1],
      [2, 3, 1, 0],
      [1, 0, 2, 3],
      [1, 0, 3, 2],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [2, 0, 3, 1],
      [0, 0, 0, 0],
      [2, 1, 3, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [2, 0, 1, 3],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [3, 0, 1, 2],
      [3, 0, 2, 1],
      [0, 0, 0, 0],
      [3, 1, 2, 0],
      [2, 1, 0, 3],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [3, 1, 0, 2],
      [0, 0, 0, 0],
      [3, 2, 0, 1],
      [3, 2, 1, 0]
    ];
  }
  dot(e, t, i) {
    return e[0] * t + e[1] * i;
  }
  dot3(e, t, i, n) {
    return e[0] * t + e[1] * i + e[2] * n;
  }
  dot4(e, t, i, n, r) {
    return e[0] * t + e[1] * i + e[2] * n + e[3] * r;
  }
  noise(e, t) {
    let i, n, r;
    const a = 0.5 * (Math.sqrt(3) - 1), o = (e + t) * a, l = Math.floor(e + o), c = Math.floor(t + o), h = (3 - Math.sqrt(3)) / 6, u = (l + c) * h, d = l - u, f = c - u, p = e - d, v = t - f;
    let g, m;
    p > v ? (g = 1, m = 0) : (g = 0, m = 1);
    const x = p - g + h, y = v - m + h, A = p - 1 + 2 * h, b = v - 1 + 2 * h, M = l & 255, w = c & 255, C = this.perm[M + this.perm[w]] % 12, T = this.perm[M + g + this.perm[w + m]] % 12, E = this.perm[M + 1 + this.perm[w + 1]] % 12;
    let P = 0.5 - p * p - v * v;
    P < 0 ? i = 0 : (P *= P, i = P * P * this.dot(this.grad3[C], p, v));
    let L = 0.5 - x * x - y * y;
    L < 0 ? n = 0 : (L *= L, n = L * L * this.dot(this.grad3[T], x, y));
    let I = 0.5 - A * A - b * b;
    return I < 0 ? r = 0 : (I *= I, r = I * I * this.dot(this.grad3[E], A, b)), 70 * (i + n + r);
  }
  // 3D simplex noise
  noise3d(e, t, i) {
    let n, r, a, o;
    const l = 0.3333333333333333, c = (e + t + i) * l, h = Math.floor(e + c), u = Math.floor(t + c), d = Math.floor(i + c), f = 1 / 6, p = (h + u + d) * f, v = h - p, g = u - p, m = d - p, x = e - v, y = t - g, A = i - m;
    let b, M, w, C, T, E;
    x >= y ? y >= A ? (b = 1, M = 0, w = 0, C = 1, T = 1, E = 0) : x >= A ? (b = 1, M = 0, w = 0, C = 1, T = 0, E = 1) : (b = 0, M = 0, w = 1, C = 1, T = 0, E = 1) : y < A ? (b = 0, M = 0, w = 1, C = 0, T = 1, E = 1) : x < A ? (b = 0, M = 1, w = 0, C = 0, T = 1, E = 1) : (b = 0, M = 1, w = 0, C = 1, T = 1, E = 0);
    const P = x - b + f, L = y - M + f, I = A - w + f, N = x - C + 2 * f, H = y - T + 2 * f, U = A - E + 2 * f, W = x - 1 + 3 * f, X = y - 1 + 3 * f, K = A - 1 + 3 * f, te = h & 255, re = u & 255, be = d & 255, Pe = this.perm[te + this.perm[re + this.perm[be]]] % 12, J = this.perm[te + b + this.perm[re + M + this.perm[be + w]]] % 12, fe = this.perm[te + C + this.perm[re + T + this.perm[be + E]]] % 12, Me = this.perm[te + 1 + this.perm[re + 1 + this.perm[be + 1]]] % 12;
    let ye = 0.6 - x * x - y * y - A * A;
    ye < 0 ? n = 0 : (ye *= ye, n = ye * ye * this.dot3(this.grad3[Pe], x, y, A));
    let Ye = 0.6 - P * P - L * L - I * I;
    Ye < 0 ? r = 0 : (Ye *= Ye, r = Ye * Ye * this.dot3(this.grad3[J], P, L, I));
    let Je = 0.6 - N * N - H * H - U * U;
    Je < 0 ? a = 0 : (Je *= Je, a = Je * Je * this.dot3(this.grad3[fe], N, H, U));
    let rt = 0.6 - W * W - X * X - K * K;
    return rt < 0 ? o = 0 : (rt *= rt, o = rt * rt * this.dot3(this.grad3[Me], W, X, K)), 32 * (n + r + a + o);
  }
  // 4D simplex noise
  noise4d(e, t, i, n) {
    const r = this.grad4, a = this.simplex, o = this.perm, l = (Math.sqrt(5) - 1) / 4, c = (5 - Math.sqrt(5)) / 20;
    let h, u, d, f, p;
    const v = (e + t + i + n) * l, g = Math.floor(e + v), m = Math.floor(t + v), x = Math.floor(i + v), y = Math.floor(n + v), A = (g + m + x + y) * c, b = g - A, M = m - A, w = x - A, C = y - A, T = e - b, E = t - M, P = i - w, L = n - C, I = T > E ? 32 : 0, N = T > P ? 16 : 0, H = E > P ? 8 : 0, U = T > L ? 4 : 0, W = E > L ? 2 : 0, X = P > L ? 1 : 0, K = I + N + H + U + W + X, te = a[K][0] >= 3 ? 1 : 0, re = a[K][1] >= 3 ? 1 : 0, be = a[K][2] >= 3 ? 1 : 0, Pe = a[K][3] >= 3 ? 1 : 0, J = a[K][0] >= 2 ? 1 : 0, fe = a[K][1] >= 2 ? 1 : 0, Me = a[K][2] >= 2 ? 1 : 0, ye = a[K][3] >= 2 ? 1 : 0, Ye = a[K][0] >= 1 ? 1 : 0, Je = a[K][1] >= 1 ? 1 : 0, rt = a[K][2] >= 1 ? 1 : 0, wt = a[K][3] >= 1 ? 1 : 0, k = T - te + c, Fe = E - re + c, Ge = P - be + c, qe = L - Pe + c, Ie = T - J + 2 * c, vt = E - fe + 2 * c, Ve = P - Me + 2 * c, Ze = L - ye + 2 * c, G = T - Ye + 3 * c, D = E - Je + 3 * c, ne = P - rt + 3 * c, ve = L - wt + 3 * c, Y = T - 1 + 4 * c, ee = E - 1 + 4 * c, we = P - 1 + 4 * c, de = L - 1 + 4 * c, xe = g & 255, it = m & 255, q = x & 255, ae = y & 255, Ee = o[xe + o[it + o[q + o[ae]]]] % 32, Le = o[xe + te + o[it + re + o[q + be + o[ae + Pe]]]] % 32, Ae = o[xe + J + o[it + fe + o[q + Me + o[ae + ye]]]] % 32, We = o[xe + Ye + o[it + Je + o[q + rt + o[ae + wt]]]] % 32, ot = o[xe + 1 + o[it + 1 + o[q + 1 + o[ae + 1]]]] % 32;
    let Ct = 0.6 - T * T - E * E - P * P - L * L;
    Ct < 0 ? h = 0 : (Ct *= Ct, h = Ct * Ct * this.dot4(r[Ee], T, E, P, L));
    let F = 0.6 - k * k - Fe * Fe - Ge * Ge - qe * qe;
    F < 0 ? u = 0 : (F *= F, u = F * F * this.dot4(r[Le], k, Fe, Ge, qe));
    let ue = 0.6 - Ie * Ie - vt * vt - Ve * Ve - Ze * Ze;
    ue < 0 ? d = 0 : (ue *= ue, d = ue * ue * this.dot4(r[Ae], Ie, vt, Ve, Ze));
    let Q = 0.6 - G * G - D * D - ne * ne - ve * ve;
    Q < 0 ? f = 0 : (Q *= Q, f = Q * Q * this.dot4(r[We], G, D, ne, ve));
    let he = 0.6 - Y * Y - ee * ee - we * we - de * de;
    return he < 0 ? p = 0 : (he *= he, p = he * he * this.dot4(r[ot], Y, ee, we, de)), 27 * (h + u + d + f + p);
  }
};
const jv = {
  name: "SSAOShader",
  defines: {
    PERSPECTIVE_CAMERA: 1,
    KERNEL_SIZE: 32
  },
  uniforms: {
    tNormal: { value: null },
    tDepth: { value: null },
    tNoise: { value: null },
    kernel: { value: null },
    cameraNear: { value: null },
    cameraFar: { value: null },
    resolution: { value: new Se() },
    cameraProjectionMatrix: { value: new et() },
    cameraInverseProjectionMatrix: { value: new et() },
    kernelRadius: { value: 8 },
    minDistance: { value: 5e-3 },
    maxDistance: { value: 0.05 }
  },
  vertexShader: (
    /* glsl */
    `

		varying vec2 vUv;

		void main() {

			vUv = uv;

			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `
		uniform highp sampler2D tNormal;
		uniform highp sampler2D tDepth;
		uniform sampler2D tNoise;

		uniform vec3 kernel[ KERNEL_SIZE ];

		uniform vec2 resolution;

		uniform float cameraNear;
		uniform float cameraFar;
		uniform mat4 cameraProjectionMatrix;
		uniform mat4 cameraInverseProjectionMatrix;

		uniform float kernelRadius;
		uniform float minDistance; // avoid artifacts caused by neighbour fragments with minimal depth difference
		uniform float maxDistance; // avoid the influence of fragments which are too far away

		varying vec2 vUv;

		#include <packing>

		float getDepth( const in vec2 screenPosition ) {

			return texture2D( tDepth, screenPosition ).x;

		}

		float getLinearDepth( const in vec2 screenPosition ) {

			#if PERSPECTIVE_CAMERA == 1

				float fragCoordZ = texture2D( tDepth, screenPosition ).x;
				float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
				return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );

			#else

				return texture2D( tDepth, screenPosition ).x;

			#endif

		}

		float getViewZ( const in float depth ) {

			#if PERSPECTIVE_CAMERA == 1

				return perspectiveDepthToViewZ( depth, cameraNear, cameraFar );

			#else

				return orthographicDepthToViewZ( depth, cameraNear, cameraFar );

			#endif

		}

		vec3 getViewPosition( const in vec2 screenPosition, const in float depth, const in float viewZ ) {

			float clipW = cameraProjectionMatrix[2][3] * viewZ + cameraProjectionMatrix[3][3];

			vec4 clipPosition = vec4( ( vec3( screenPosition, depth ) - 0.5 ) * 2.0, 1.0 );

			clipPosition *= clipW; // unprojection.

			return ( cameraInverseProjectionMatrix * clipPosition ).xyz;

		}

		vec3 getViewNormal( const in vec2 screenPosition ) {

			return unpackRGBToNormal( texture2D( tNormal, screenPosition ).xyz );

		}

		void main() {

			float depth = getDepth( vUv );

			if ( depth == 1.0 ) {

				gl_FragColor = vec4( 1.0 ); // don't influence background
				
			} else {

				float viewZ = getViewZ( depth );

				vec3 viewPosition = getViewPosition( vUv, depth, viewZ );
				vec3 viewNormal = getViewNormal( vUv );

				vec2 noiseScale = vec2( resolution.x / 4.0, resolution.y / 4.0 );
				vec3 random = vec3( texture2D( tNoise, vUv * noiseScale ).r );

				// compute matrix used to reorient a kernel vector

				vec3 tangent = normalize( random - viewNormal * dot( random, viewNormal ) );
				vec3 bitangent = cross( viewNormal, tangent );
				mat3 kernelMatrix = mat3( tangent, bitangent, viewNormal );

				float occlusion = 0.0;

				for ( int i = 0; i < KERNEL_SIZE; i ++ ) {

					vec3 sampleVector = kernelMatrix * kernel[ i ]; // reorient sample vector in view space
					vec3 samplePoint = viewPosition + ( sampleVector * kernelRadius ); // calculate sample point

					vec4 samplePointNDC = cameraProjectionMatrix * vec4( samplePoint, 1.0 ); // project point and calculate NDC
					samplePointNDC /= samplePointNDC.w;

					vec2 samplePointUv = samplePointNDC.xy * 0.5 + 0.5; // compute uv coordinates

					float realDepth = getLinearDepth( samplePointUv ); // get linear depth from depth texture
					float sampleDepth = viewZToOrthographicDepth( samplePoint.z, cameraNear, cameraFar ); // compute linear depth of the sample view Z value
					float delta = sampleDepth - realDepth;

					if ( delta > minDistance && delta < maxDistance ) { // if fragment is before sample point, increase occlusion

						occlusion += 1.0;

					}

				}

				occlusion = clamp( occlusion / float( KERNEL_SIZE ), 0.0, 1.0 );

				gl_FragColor = vec4( vec3( 1.0 - occlusion ), 1.0 );

			}

		}`
  )
}, qv = {
  name: "SSAODepthShader",
  defines: {
    PERSPECTIVE_CAMERA: 1
  },
  uniforms: {
    tDepth: { value: null },
    cameraNear: { value: null },
    cameraFar: { value: null }
  },
  vertexShader: `varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`,
  fragmentShader: `uniform sampler2D tDepth;

		uniform float cameraNear;
		uniform float cameraFar;

		varying vec2 vUv;

		#include <packing>

		float getLinearDepth( const in vec2 screenPosition ) {

			#if PERSPECTIVE_CAMERA == 1

				float fragCoordZ = texture2D( tDepth, screenPosition ).x;
				float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
				return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );

			#else

				return texture2D( tDepth, screenPosition ).x;

			#endif

		}

		void main() {

			float depth = getLinearDepth( vUv );
			gl_FragColor = vec4( vec3( 1.0 - depth ), 1.0 );

		}`
}, Yv = {
  name: "SSAOBlurShader",
  uniforms: {
    tDiffuse: { value: null },
    resolution: { value: new Se() }
  },
  vertexShader: `varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`,
  fragmentShader: `uniform sampler2D tDiffuse;

		uniform vec2 resolution;

		varying vec2 vUv;

		void main() {

			vec2 texelSize = ( 1.0 / resolution );
			float result = 0.0;

			for ( int i = - 2; i <= 2; i ++ ) {

				for ( int j = - 2; j <= 2; j ++ ) {

					vec2 offset = ( vec2( float( i ), float( j ) ) ) * texelSize;
					result += texture2D( tDiffuse, vUv + offset ).r;

				}

			}

			gl_FragColor = vec4( vec3( result / ( 5.0 * 5.0 ) ), 1.0 );

		}`
};
let lN = class gf extends Ao {
  constructor(e, t, i, n, r = 32) {
    super(), this.width = i !== void 0 ? i : 512, this.height = n !== void 0 ? n : 512, this.clear = !0, this.needsSwap = !1, this.camera = t, this.scene = e, this.kernelRadius = 8, this.kernel = [], this.noiseTexture = null, this.output = 0, this.minDistance = 5e-3, this.maxDistance = 0.1, this._visibilityCache = /* @__PURE__ */ new Map(), this.generateSampleKernel(r), this.generateRandomKernelRotations();
    const a = new r2();
    a.format = yl, a.type = vl, this.normalRenderTarget = new Xi(this.width, this.height, {
      minFilter: Ni,
      magFilter: Ni,
      type: on,
      depthTexture: a
    }), this.ssaoRenderTarget = new Xi(this.width, this.height, { type: on }), this.blurRenderTarget = this.ssaoRenderTarget.clone(), this.ssaoMaterial = new Jt({
      defines: Object.assign({}, jv.defines),
      uniforms: rs.clone(jv.uniforms),
      vertexShader: jv.vertexShader,
      fragmentShader: jv.fragmentShader,
      blending: vn
    }), this.ssaoMaterial.defines.KERNEL_SIZE = r, this.ssaoMaterial.uniforms.tNormal.value = this.normalRenderTarget.texture, this.ssaoMaterial.uniforms.tDepth.value = this.normalRenderTarget.depthTexture, this.ssaoMaterial.uniforms.tNoise.value = this.noiseTexture, this.ssaoMaterial.uniforms.kernel.value = this.kernel, this.ssaoMaterial.uniforms.cameraNear.value = this.camera.near, this.ssaoMaterial.uniforms.cameraFar.value = this.camera.far, this.ssaoMaterial.uniforms.resolution.value.set(this.width, this.height), this.ssaoMaterial.uniforms.cameraProjectionMatrix.value.copy(this.camera.projectionMatrix), this.ssaoMaterial.uniforms.cameraInverseProjectionMatrix.value.copy(this.camera.projectionMatrixInverse), this.normalMaterial = new P4(), this.normalMaterial.blending = vn, this.blurMaterial = new Jt({
      defines: Object.assign({}, Yv.defines),
      uniforms: rs.clone(Yv.uniforms),
      vertexShader: Yv.vertexShader,
      fragmentShader: Yv.fragmentShader
    }), this.blurMaterial.uniforms.tDiffuse.value = this.ssaoRenderTarget.texture, this.blurMaterial.uniforms.resolution.value.set(this.width, this.height), this.depthRenderMaterial = new Jt({
      defines: Object.assign({}, qv.defines),
      uniforms: rs.clone(qv.uniforms),
      vertexShader: qv.vertexShader,
      fragmentShader: qv.fragmentShader,
      blending: vn
    }), this.depthRenderMaterial.uniforms.tDepth.value = this.normalRenderTarget.depthTexture, this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near, this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far, this.copyMaterial = new Jt({
      uniforms: rs.clone(ia.uniforms),
      vertexShader: ia.vertexShader,
      fragmentShader: ia.fragmentShader,
      transparent: !0,
      depthTest: !1,
      depthWrite: !1,
      blendSrc: f4,
      blendDst: ep,
      blendEquation: Ma,
      blendSrcAlpha: d4,
      blendDstAlpha: ep,
      blendEquationAlpha: Ma
    }), this.fsQuad = new P2(null), this.originalClearColor = new _e();
  }
  dispose() {
    this.normalRenderTarget.dispose(), this.ssaoRenderTarget.dispose(), this.blurRenderTarget.dispose(), this.normalMaterial.dispose(), this.blurMaterial.dispose(), this.copyMaterial.dispose(), this.depthRenderMaterial.dispose(), this.fsQuad.dispose();
  }
  render(e, t, i) {
    switch (this.overrideVisibility(), this.renderOverride(e, this.normalMaterial, this.normalRenderTarget, 7829503, 1), this.restoreVisibility(), this.ssaoMaterial.uniforms.kernelRadius.value = this.kernelRadius, this.ssaoMaterial.uniforms.minDistance.value = this.minDistance, this.ssaoMaterial.uniforms.maxDistance.value = this.maxDistance, this.renderPass(e, this.ssaoMaterial, this.ssaoRenderTarget), this.renderPass(e, this.blurMaterial, this.blurRenderTarget), this.output) {
      case gf.OUTPUT.SSAO:
        this.copyMaterial.uniforms.tDiffuse.value = this.ssaoRenderTarget.texture, this.copyMaterial.blending = vn, this.renderPass(e, this.copyMaterial, this.renderToScreen ? null : t);
        break;
      case gf.OUTPUT.Blur:
        this.copyMaterial.uniforms.tDiffuse.value = this.blurRenderTarget.texture, this.copyMaterial.blending = vn, this.renderPass(e, this.copyMaterial, this.renderToScreen ? null : t);
        break;
      case gf.OUTPUT.Depth:
        this.renderPass(e, this.depthRenderMaterial, this.renderToScreen ? null : t);
        break;
      case gf.OUTPUT.Normal:
        this.copyMaterial.uniforms.tDiffuse.value = this.normalRenderTarget.texture, this.copyMaterial.blending = vn, this.renderPass(e, this.copyMaterial, this.renderToScreen ? null : t);
        break;
      case gf.OUTPUT.Default:
        this.copyMaterial.uniforms.tDiffuse.value = this.blurRenderTarget.texture, this.copyMaterial.blending = u4, this.renderPass(e, this.copyMaterial, this.renderToScreen ? null : i);
        break;
      default:
        console.warn("THREE.SSAOPass: Unknown output type.");
    }
  }
  renderPass(e, t, i, n, r) {
    e.getClearColor(this.originalClearColor);
    const a = e.getClearAlpha(), o = e.autoClear;
    e.setRenderTarget(i), e.autoClear = !1, n != null && (e.setClearColor(n), e.setClearAlpha(r || 0), e.clear()), this.fsQuad.material = t, this.fsQuad.render(e), e.autoClear = o, e.setClearColor(this.originalClearColor), e.setClearAlpha(a);
  }
  renderOverride(e, t, i, n, r) {
    e.getClearColor(this.originalClearColor);
    const a = e.getClearAlpha(), o = e.autoClear;
    e.setRenderTarget(i), e.autoClear = !1, n = t.clearColor || n, r = t.clearAlpha || r, n != null && (e.setClearColor(n), e.setClearAlpha(r || 0), e.clear()), this.scene.overrideMaterial = t, e.render(this.scene, this.camera), this.scene.overrideMaterial = null, e.autoClear = o, e.setClearColor(this.originalClearColor), e.setClearAlpha(a);
  }
  setSize(e, t) {
    this.width = e, this.height = t, this.ssaoRenderTarget.setSize(e, t), this.normalRenderTarget.setSize(e, t), this.blurRenderTarget.setSize(e, t), this.ssaoMaterial.uniforms.resolution.value.set(e, t), this.ssaoMaterial.uniforms.cameraProjectionMatrix.value.copy(this.camera.projectionMatrix), this.ssaoMaterial.uniforms.cameraInverseProjectionMatrix.value.copy(this.camera.projectionMatrixInverse), this.blurMaterial.uniforms.resolution.value.set(e, t);
  }
  generateSampleKernel(e) {
    const t = this.kernel;
    for (let i = 0; i < e; i++) {
      const n = new _();
      n.x = Math.random() * 2 - 1, n.y = Math.random() * 2 - 1, n.z = Math.random(), n.normalize();
      let r = i / e;
      r = nt.lerp(0.1, 1, r * r), n.multiplyScalar(r), t.push(n);
    }
  }
  generateRandomKernelRotations() {
    const i = new oN(), n = 4 * 4, r = new Float32Array(n);
    for (let a = 0; a < n; a++) {
      const o = Math.random() * 2 - 1, l = Math.random() * 2 - 1, c = 0;
      r[a] = i.noise3d(o, l, c);
    }
    this.noiseTexture = new Qn(r, 4, 4, Vu, xn), this.noiseTexture.wrapS = un, this.noiseTexture.wrapT = un, this.noiseTexture.needsUpdate = !0;
  }
  overrideVisibility() {
    const e = this.scene, t = this._visibilityCache;
    e.traverse(function(i) {
      t.set(i, i.visible), (i.isPoints || i.isLine) && (i.visible = !1);
    });
  }
  restoreVisibility() {
    const e = this.scene, t = this._visibilityCache;
    e.traverse(function(i) {
      const n = t.get(i);
      i.visible = n;
    }), t.clear();
  }
};
lN.OUTPUT = {
  Default: 0,
  SSAO: 1,
  Blur: 2,
  Depth: 3,
  Normal: 4
};
const Rte = {
  name: "LuminosityHighPassShader",
  shaderID: "luminosityHighPass",
  uniforms: {
    tDiffuse: { value: null },
    luminosityThreshold: { value: 1 },
    smoothWidth: { value: 1 },
    defaultColor: { value: new _e(0) },
    defaultOpacity: { value: 0 }
  },
  vertexShader: (
    /* glsl */
    `

		varying vec2 vUv;

		void main() {

			vUv = uv;

			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `

		uniform sampler2D tDiffuse;
		uniform vec3 defaultColor;
		uniform float defaultOpacity;
		uniform float luminosityThreshold;
		uniform float smoothWidth;

		varying vec2 vUv;

		void main() {

			vec4 texel = texture2D( tDiffuse, vUv );

			vec3 luma = vec3( 0.299, 0.587, 0.114 );

			float v = dot( texel.xyz, luma );

			vec4 outputColor = vec4( defaultColor.rgb, defaultOpacity );

			float alpha = smoothstep( luminosityThreshold, luminosityThreshold + smoothWidth, v );

			gl_FragColor = mix( outputColor, texel, alpha );

		}`
  )
};
class dp extends Ao {
  constructor(e, t, i, n) {
    super(), this.strength = t !== void 0 ? t : 1, this.radius = i, this.threshold = n, this.resolution = e !== void 0 ? new Se(e.x, e.y) : new Se(256, 256), this.clearColor = new _e(0, 0, 0), this.renderTargetsHorizontal = [], this.renderTargetsVertical = [], this.nMips = 5;
    let r = Math.round(this.resolution.x / 2), a = Math.round(this.resolution.y / 2);
    this.renderTargetBright = new Xi(r, a, { type: on }), this.renderTargetBright.texture.name = "UnrealBloomPass.bright", this.renderTargetBright.texture.generateMipmaps = !1;
    for (let u = 0; u < this.nMips; u++) {
      const d = new Xi(r, a, { type: on });
      d.texture.name = "UnrealBloomPass.h" + u, d.texture.generateMipmaps = !1, this.renderTargetsHorizontal.push(d);
      const f = new Xi(r, a, { type: on });
      f.texture.name = "UnrealBloomPass.v" + u, f.texture.generateMipmaps = !1, this.renderTargetsVertical.push(f), r = Math.round(r / 2), a = Math.round(a / 2);
    }
    const o = Rte;
    this.highPassUniforms = rs.clone(o.uniforms), this.highPassUniforms.luminosityThreshold.value = n, this.highPassUniforms.smoothWidth.value = 0.01, this.materialHighPassFilter = new Jt({
      uniforms: this.highPassUniforms,
      vertexShader: o.vertexShader,
      fragmentShader: o.fragmentShader
    }), this.separableBlurMaterials = [];
    const l = [3, 5, 7, 9, 11];
    r = Math.round(this.resolution.x / 2), a = Math.round(this.resolution.y / 2);
    for (let u = 0; u < this.nMips; u++)
      this.separableBlurMaterials.push(this.getSeperableBlurMaterial(l[u])), this.separableBlurMaterials[u].uniforms.invSize.value = new Se(1 / r, 1 / a), r = Math.round(r / 2), a = Math.round(a / 2);
    this.compositeMaterial = this.getCompositeMaterial(this.nMips), this.compositeMaterial.uniforms.blurTexture1.value = this.renderTargetsVertical[0].texture, this.compositeMaterial.uniforms.blurTexture2.value = this.renderTargetsVertical[1].texture, this.compositeMaterial.uniforms.blurTexture3.value = this.renderTargetsVertical[2].texture, this.compositeMaterial.uniforms.blurTexture4.value = this.renderTargetsVertical[3].texture, this.compositeMaterial.uniforms.blurTexture5.value = this.renderTargetsVertical[4].texture, this.compositeMaterial.uniforms.bloomStrength.value = t, this.compositeMaterial.uniforms.bloomRadius.value = 0.1;
    const c = [1, 0.8, 0.6, 0.4, 0.2];
    this.compositeMaterial.uniforms.bloomFactors.value = c, this.bloomTintColors = [new _(1, 1, 1), new _(1, 1, 1), new _(1, 1, 1), new _(1, 1, 1), new _(1, 1, 1)], this.compositeMaterial.uniforms.bloomTintColors.value = this.bloomTintColors;
    const h = ia;
    this.copyUniforms = rs.clone(h.uniforms), this.blendMaterial = new Jt({
      uniforms: this.copyUniforms,
      vertexShader: h.vertexShader,
      fragmentShader: h.fragmentShader,
      blending: oo,
      depthTest: !1,
      depthWrite: !1,
      transparent: !0
    }), this.enabled = !0, this.needsSwap = !1, this._oldClearColor = new _e(), this.oldClearAlpha = 1, this.basic = new kt(), this.fsQuad = new P2(null);
  }
  dispose() {
    for (let e = 0; e < this.renderTargetsHorizontal.length; e++)
      this.renderTargetsHorizontal[e].dispose();
    for (let e = 0; e < this.renderTargetsVertical.length; e++)
      this.renderTargetsVertical[e].dispose();
    this.renderTargetBright.dispose();
    for (let e = 0; e < this.separableBlurMaterials.length; e++)
      this.separableBlurMaterials[e].dispose();
    this.compositeMaterial.dispose(), this.blendMaterial.dispose(), this.basic.dispose(), this.fsQuad.dispose();
  }
  setSize(e, t) {
    let i = Math.round(e / 2), n = Math.round(t / 2);
    this.renderTargetBright.setSize(i, n);
    for (let r = 0; r < this.nMips; r++)
      this.renderTargetsHorizontal[r].setSize(i, n), this.renderTargetsVertical[r].setSize(i, n), this.separableBlurMaterials[r].uniforms.invSize.value = new Se(1 / i, 1 / n), i = Math.round(i / 2), n = Math.round(n / 2);
  }
  render(e, t, i, n, r) {
    e.getClearColor(this._oldClearColor), this.oldClearAlpha = e.getClearAlpha();
    const a = e.autoClear;
    e.autoClear = !1, e.setClearColor(this.clearColor, 0), r && e.state.buffers.stencil.setTest(!1), this.renderToScreen && (this.fsQuad.material = this.basic, this.basic.map = i.texture, e.setRenderTarget(null), e.clear(), this.fsQuad.render(e)), this.highPassUniforms.tDiffuse.value = i.texture, this.highPassUniforms.luminosityThreshold.value = this.threshold, this.fsQuad.material = this.materialHighPassFilter, e.setRenderTarget(this.renderTargetBright), e.clear(), this.fsQuad.render(e);
    let o = this.renderTargetBright;
    for (let l = 0; l < this.nMips; l++)
      this.fsQuad.material = this.separableBlurMaterials[l], this.separableBlurMaterials[l].uniforms.colorTexture.value = o.texture, this.separableBlurMaterials[l].uniforms.direction.value = dp.BlurDirectionX, e.setRenderTarget(this.renderTargetsHorizontal[l]), e.clear(), this.fsQuad.render(e), this.separableBlurMaterials[l].uniforms.colorTexture.value = this.renderTargetsHorizontal[l].texture, this.separableBlurMaterials[l].uniforms.direction.value = dp.BlurDirectionY, e.setRenderTarget(this.renderTargetsVertical[l]), e.clear(), this.fsQuad.render(e), o = this.renderTargetsVertical[l];
    this.fsQuad.material = this.compositeMaterial, this.compositeMaterial.uniforms.bloomStrength.value = this.strength, this.compositeMaterial.uniforms.bloomRadius.value = this.radius, this.compositeMaterial.uniforms.bloomTintColors.value = this.bloomTintColors, e.setRenderTarget(this.renderTargetsHorizontal[0]), e.clear(), this.fsQuad.render(e), this.fsQuad.material = this.blendMaterial, this.copyUniforms.tDiffuse.value = this.renderTargetsHorizontal[0].texture, r && e.state.buffers.stencil.setTest(!0), this.renderToScreen ? (e.setRenderTarget(null), this.fsQuad.render(e)) : (e.setRenderTarget(i), this.fsQuad.render(e)), e.setClearColor(this._oldClearColor, this.oldClearAlpha), e.autoClear = a;
  }
  getSeperableBlurMaterial(e) {
    const t = [];
    for (let i = 0; i < e; i++)
      t.push(0.39894 * Math.exp(-0.5 * i * i / (e * e)) / e);
    return new Jt({
      defines: {
        KERNEL_RADIUS: e
      },
      uniforms: {
        colorTexture: { value: null },
        invSize: { value: new Se(0.5, 0.5) },
        // inverse texture size
        direction: { value: new Se(0.5, 0.5) },
        gaussianCoefficients: { value: t }
        // precomputed Gaussian coefficients
      },
      vertexShader: `varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `#include <common>
				varying vec2 vUv;
				uniform sampler2D colorTexture;
				uniform vec2 invSize;
				uniform vec2 direction;
				uniform float gaussianCoefficients[KERNEL_RADIUS];

				void main() {
					float weightSum = gaussianCoefficients[0];
					vec3 diffuseSum = texture2D( colorTexture, vUv ).rgb * weightSum;
					for( int i = 1; i < KERNEL_RADIUS; i ++ ) {
						float x = float(i);
						float w = gaussianCoefficients[i];
						vec2 uvOffset = direction * invSize * x;
						vec3 sample1 = texture2D( colorTexture, vUv + uvOffset ).rgb;
						vec3 sample2 = texture2D( colorTexture, vUv - uvOffset ).rgb;
						diffuseSum += (sample1 + sample2) * w;
						weightSum += 2.0 * w;
					}
					gl_FragColor = vec4(diffuseSum/weightSum, 1.0);
				}`
    });
  }
  getCompositeMaterial(e) {
    return new Jt({
      defines: {
        NUM_MIPS: e
      },
      uniforms: {
        blurTexture1: { value: null },
        blurTexture2: { value: null },
        blurTexture3: { value: null },
        blurTexture4: { value: null },
        blurTexture5: { value: null },
        bloomStrength: { value: 1 },
        bloomFactors: { value: null },
        bloomTintColors: { value: null },
        bloomRadius: { value: 0 }
      },
      vertexShader: `varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `varying vec2 vUv;
				uniform sampler2D blurTexture1;
				uniform sampler2D blurTexture2;
				uniform sampler2D blurTexture3;
				uniform sampler2D blurTexture4;
				uniform sampler2D blurTexture5;
				uniform float bloomStrength;
				uniform float bloomRadius;
				uniform float bloomFactors[NUM_MIPS];
				uniform vec3 bloomTintColors[NUM_MIPS];

				float lerpBloomFactor(const in float factor) {
					float mirrorFactor = 1.2 - factor;
					return mix(factor, mirrorFactor, bloomRadius);
				}

				void main() {
					gl_FragColor = bloomStrength * ( lerpBloomFactor(bloomFactors[0]) * vec4(bloomTintColors[0], 1.0) * texture2D(blurTexture1, vUv) +
						lerpBloomFactor(bloomFactors[1]) * vec4(bloomTintColors[1], 1.0) * texture2D(blurTexture2, vUv) +
						lerpBloomFactor(bloomFactors[2]) * vec4(bloomTintColors[2], 1.0) * texture2D(blurTexture3, vUv) +
						lerpBloomFactor(bloomFactors[3]) * vec4(bloomTintColors[3], 1.0) * texture2D(blurTexture4, vUv) +
						lerpBloomFactor(bloomFactors[4]) * vec4(bloomTintColors[4], 1.0) * texture2D(blurTexture5, vUv) );
				}`
    });
  }
}
dp.BlurDirectionX = new Se(1, 0);
dp.BlurDirectionY = new Se(0, 1);
class Dte {
  static init() {
    const e = [1, 0, 0, 2e-5, 1, 0, 0, 503905e-9, 1, 0, 0, 201562e-8, 1, 0, 0, 453516e-8, 1, 0, 0, 806253e-8, 1, 0, 0, 0.0125978, 1, 0, 0, 0.018141, 1, 0, 0, 0.0246924, 1, 0, 0, 0.0322525, 1, 0, 0, 0.0408213, 1, 0, 0, 0.0503999, 1, 0, 0, 0.0609894, 1, 0, 0, 0.0725906, 1, 0, 0, 0.0852058, 1, 0, 0, 0.0988363, 1, 0, 0, 0.113484, 1, 0, 0, 0.129153, 1, 0, 0, 0.145839, 1, 0, 0, 0.163548, 1, 0, 0, 0.182266, 1, 0, 0, 0.201942, 1, 0, 0, 0.222314, 1, 0, 0, 0.241906, 1, 0, 0, 0.262314, 1, 0, 0, 0.285754, 1, 0, 0, 0.310159, 1, 0, 0, 0.335426, 1, 0, 0, 0.361341, 1, 0, 0, 0.387445, 1, 0, 0, 0.412784, 1, 0, 0, 0.438197, 1, 0, 0, 0.466966, 1, 0, 0, 0.49559, 1, 0, 0, 0.523448, 1, 0, 0, 0.549938, 1, 0, 0, 0.57979, 1, 0, 0, 0.608746, 1, 0, 0, 0.636185, 1, 0, 0, 0.664748, 1, 0, 0, 0.69313, 1, 0, 0, 0.71966, 1, 0, 0, 0.747662, 1, 0, 0, 0.774023, 1, 0, 0, 0.799775, 1, 0, 0, 0.825274, 1, 0, 0, 0.849156, 1, 0, 0, 0.873248, 1, 0, 0, 0.89532, 1, 0, 0, 0.917565, 1, 0, 0, 0.937863, 1, 0, 0, 0.958139, 1, 0, 0, 0.976563, 1, 0, 0, 0.994658, 1, 0, 0, 1.0112, 1, 0, 0, 1.02712, 1, 0, 0, 1.04189, 1, 0, 0, 1.05568, 1, 0, 0, 1.06877, 1, 0, 0, 1.08058, 1, 0, 0, 1.09194, 1, 0, 0, 1.10191, 1, 0, 0, 1.11161, 1, 0, 0, 1.1199, 1, 0, 0, 1.12813, 0.999547, -448815e-12, 0.0224417, 199902e-10, 0.999495, -113079e-10, 0.0224406, 503651e-9, 0.999496, -452317e-10, 0.0224406, 201461e-8, 0.999496, -101772e-9, 0.0224406, 453287e-8, 0.999495, -180928e-9, 0.0224406, 805845e-8, 0.999497, -282702e-9, 0.0224406, 0.0125914, 0.999496, -407096e-9, 0.0224406, 0.0181319, 0.999498, -554114e-9, 0.0224406, 0.02468, 0.999499, -723768e-9, 0.0224406, 0.0322363, 0.999495, -916058e-9, 0.0224405, 0.0408009, 0.999499, -113101e-8, 0.0224408, 0.050375, 0.999494, -136863e-8, 0.0224405, 0.0609586, 0.999489, -162896e-8, 0.0224401, 0.0725537, 0.999489, -191201e-8, 0.0224414, 0.0851619, 0.999498, -221787e-8, 0.0224413, 0.0987867, 0.999492, -254642e-8, 0.0224409, 0.113426, 0.999507, -289779e-8, 0.0224417, 0.129088, 0.999494, -32716e-7, 0.0224386, 0.145767, 0.999546, -36673e-7, 0.0224424, 0.163472, 0.999543, -408166e-8, 0.0224387, 0.182182, 0.999499, -450056e-8, 0.0224338, 0.201843, 0.999503, -483661e-8, 0.0224203, 0.222198, 0.999546, -452928e-8, 0.022315, 0.241714, 0.999508, -587403e-8, 0.0224329, 0.262184, 0.999509, -638806e-8, 0.0224271, 0.285609, 0.999501, -691028e-8, 0.0224166, 0.309998, 0.999539, -741979e-8, 0.0223989, 0.335262, 0.999454, -786282e-8, 0.0223675, 0.361154, 0.999529, -811928e-8, 0.0222828, 0.387224, 0.999503, -799941e-8, 0.0221063, 0.41252, 0.999561, -952753e-8, 0.0223057, 0.438006, 0.999557, -99134e-7, 0.0222065, 0.466735, 0.999541, -0.0100935, 0.0220402, 0.495332, 0.999562, -996821e-8, 0.0218067, 0.523197, 0.999556, -0.0105031, 0.0217096, 0.550223, 0.999561, -0.0114191, 0.0217215, 0.579498, 0.999588, -0.0111818, 0.0213357, 0.608416, 0.999633, -0.0107725, 0.0208689, 0.635965, 0.999527, -0.0121671, 0.0210149, 0.664476, 0.999508, -0.0116005, 0.020431, 0.692786, 0.999568, -0.0115604, 0.0199791, 0.719709, 0.999671, -0.0121117, 0.0197415, 0.74737, 0.999688, -0.0110769, 0.0188846, 0.773692, 0.99962, -0.0122368, 0.0188452, 0.799534, 0.999823, -0.0110325, 0.0178001, 0.825046, 0.999599, -0.0114923, 0.0174221, 0.849075, 0.999619, -0.0105923, 0.0164345, 0.872999, 0.999613, -0.0105988, 0.0158227, 0.895371, 0.99964, -979861e-8, 0.0148131, 0.917364, 0.99977, -967238e-8, 0.0140721, 0.938002, 0.999726, -869175e-8, 0.0129543, 0.957917, 0.99973, -866872e-8, 0.0122329, 0.976557, 0.999773, -731956e-8, 0.0108958, 0.994459, 0.999811, -756027e-8, 0.0102715, 1.01118, 0.999862, -583732e-8, 878781e-8, 1.02701, 0.999835, -631438e-8, 827529e-8, 1.04186, 0.999871, -450785e-8, 674583e-8, 1.05569, 0.999867, -486079e-8, 621041e-8, 1.06861, 0.999939, -322072e-8, 478301e-8, 1.08064, 0.999918, -318199e-8, 406395e-8, 1.09181, 1.00003, -193348e-8, 280682e-8, 1.10207, 0.999928, -153729e-8, 198741e-8, 1.11152, 0.999933, -623666e-9, 917714e-9, 1.12009, 1, -102387e-11, 907581e-12, 1.12813, 0.997866, -896716e-12, 0.0448334, 199584e-10, 0.997987, -225945e-10, 0.0448389, 502891e-9, 0.997987, -903781e-10, 0.0448388, 201156e-8, 0.997985, -203351e-9, 0.0448388, 452602e-8, 0.997986, -361514e-9, 0.0448388, 804629e-8, 0.997987, -56487e-8, 0.0448389, 0.0125724, 0.997988, -813423e-9, 0.0448389, 0.0181045, 0.997984, -110718e-8, 0.0448387, 0.0246427, 0.997985, -144616e-8, 0.0448388, 0.0321875, 0.997987, -183038e-8, 0.044839, 0.0407392, 0.997983, -225987e-8, 0.0448387, 0.0502986, 0.997991, -273467e-8, 0.0448389, 0.0608667, 0.997984, -325481e-8, 0.0448384, 0.0724444, 0.998002, -382043e-8, 0.044839, 0.0850348, 0.997997, -443145e-8, 0.0448396, 0.0986372, 0.998007, -508796e-8, 0.0448397, 0.113255, 0.998008, -578985e-8, 0.04484, 0.128891, 0.998003, -653683e-8, 0.0448384, 0.145548, 0.997983, -732713e-8, 0.0448358, 0.163221, 0.997985, -815454e-8, 0.0448358, 0.181899, 0.998005, -898985e-8, 0.0448286, 0.201533, 0.998026, -964404e-8, 0.0447934, 0.221821, 0.998055, -922677e-8, 0.044611, 0.241282, 0.99804, -0.0117361, 0.0448245, 0.261791, 0.998048, -0.0127628, 0.0448159, 0.285181, 0.998088, -0.0138055, 0.0447996, 0.30954, 0.998058, -0.0148206, 0.0447669, 0.334751, 0.998099, -0.0156998, 0.044697, 0.36061, 0.998116, -0.0161976, 0.0445122, 0.386603, 0.998195, -0.015945, 0.0441711, 0.411844, 0.998168, -0.0183947, 0.0444255, 0.43773, 0.998184, -0.0197913, 0.0443809, 0.466009, 0.998251, -0.0201426, 0.0440689, 0.494574, 0.998305, -0.0198847, 0.0435632, 0.522405, 0.998273, -0.0210577, 0.043414, 0.549967, 0.998254, -0.0227901, 0.0433943, 0.578655, 0.998349, -0.0223108, 0.0426529, 0.60758, 0.99843, -0.0223088, 0.042, 0.635524, 0.998373, -0.0241141, 0.0418987, 0.663621, 0.998425, -0.0231446, 0.0408118, 0.691906, 0.998504, -0.0233684, 0.0400565, 0.719339, 0.998443, -0.0241652, 0.0394634, 0.74643, 0.99848, -0.0228715, 0.0380002, 0.773086, 0.998569, -0.023519, 0.0372322, 0.798988, 0.998619, -0.0223108, 0.0356468, 0.824249, 0.998594, -0.0223105, 0.034523, 0.848808, 0.998622, -0.0213426, 0.0328887, 0.87227, 0.998669, -0.0207912, 0.0314374, 0.895157, 0.998705, -0.0198416, 0.0296925, 0.916769, 0.998786, -0.0189168, 0.0279634, 0.937773, 0.998888, -0.0178811, 0.0261597, 0.957431, 0.99906, -0.0166845, 0.0242159, 0.976495, 0.999038, -0.0155464, 0.0222638, 0.994169, 0.999237, -0.0141349, 0.0201967, 1.01112, 0.999378, -0.0129324, 0.0181744, 1.02692, 0.999433, -0.0113192, 0.0159898, 1.04174, 0.999439, -0.0101244, 0.0140385, 1.05559, 0.999614, -837456e-8, 0.0117826, 1.06852, 0.999722, -721769e-8, 983745e-8, 1.08069, 0.999817, -554067e-8, 769002e-8, 1.09176, 0.99983, -426961e-8, 5782e-6, 1.10211, 0.999964, -273904e-8, 374503e-8, 1.11152, 1.00001, -136739e-8, 187176e-8, 1.12031, 0.999946, 393227e-10, -28919e-9, 1.12804, 0.995847, -13435e-10, 0.0671785, 19916e-9, 0.995464, -338387e-10, 0.0671527, 501622e-9, 0.99547, -135355e-9, 0.0671531, 200649e-8, 0.995471, -30455e-8, 0.0671532, 451461e-8, 0.99547, -541423e-9, 0.0671531, 8026e-6, 0.995471, -84598e-8, 0.0671531, 0.0125407, 0.99547, -121823e-8, 0.0671531, 0.0180589, 0.99547, -165817e-8, 0.0671531, 0.0245806, 0.995463, -216583e-8, 0.0671526, 0.0321062, 0.995468, -274127e-8, 0.0671527, 0.0406366, 0.995474, -338447e-8, 0.0671534, 0.0501717, 0.995473, -409554e-8, 0.0671533, 0.0607131, 0.995478, -487451e-8, 0.0671531, 0.0722618, 0.995476, -572148e-8, 0.0671532, 0.0848191, 0.995477, -663658e-8, 0.0671539, 0.0983882, 0.995498, -761986e-8, 0.0671541, 0.112972, 0.995509, -867094e-8, 0.0671542, 0.128568, 0.995509, -978951e-8, 0.0671531, 0.145183, 0.995503, -0.0109725, 0.0671491, 0.162808, 0.995501, -0.012211, 0.0671465, 0.181441, 0.99553, -0.0134565, 0.0671371, 0.201015, 0.99555, -0.014391, 0.0670831, 0.221206, 0.99558, -0.014351, 0.0668883, 0.240813, 0.995577, -0.0173997, 0.0671055, 0.261257, 0.995602, -0.0191111, 0.0671178, 0.284467, 0.995623, -0.0206705, 0.0670946, 0.308765, 0.995658, -0.022184, 0.0670472, 0.333905, 0.995705, -0.0234832, 0.0669417, 0.359677, 0.995719, -0.0241933, 0.0666714, 0.385554, 0.995786, -0.0243539, 0.066266, 0.410951, 0.995887, -0.0271866, 0.0664367, 0.437163, 0.995944, -0.0296012, 0.0664931, 0.464842, 0.996004, -0.0301045, 0.0660105, 0.49332, 0.996128, -0.0298311, 0.0652694, 0.521131, 0.996253, -0.0316426, 0.0650739, 0.549167, 0.996244, -0.0339043, 0.0649433, 0.57737, 0.996309, -0.033329, 0.0638926, 0.606073, 0.996417, -0.0338935, 0.0630849, 0.634527, 0.996372, -0.0353104, 0.0625083, 0.66256, 0.996542, -0.0348942, 0.0611986, 0.690516, 0.996568, -0.0351614, 0.060069, 0.718317, 0.996711, -0.0354317, 0.0588522, 0.74528, 0.996671, -0.0349513, 0.0571902, 0.772061, 0.996865, -0.0345622, 0.0555321, 0.798089, 0.996802, -0.0342566, 0.0537816, 0.823178, 0.996992, -0.0330862, 0.0516095, 0.847949, 0.996944, -0.0324666, 0.0495537, 0.871431, 0.997146, -0.0309544, 0.0470302, 0.894357, 0.997189, -0.0299372, 0.0446043, 0.916142, 0.997471, -0.0281389, 0.0418812, 0.937193, 0.997515, -0.0268702, 0.0391823, 0.957, 0.997812, -0.0247166, 0.0361338, 0.975936, 0.998027, -0.0233525, 0.0333945, 0.99391, 0.998233, -0.0209839, 0.0301917, 1.01075, 0.998481, -0.0194309, 0.027271, 1.02669, 0.998859, -0.0169728, 0.0240162, 1.04173, 0.99894, -0.0152322, 0.0210517, 1.05551, 0.999132, -0.0127497, 0.0178632, 1.06856, 0.999369, -0.0108282, 0.014787, 1.08054, 0.999549, -845886e-8, 0.0116185, 1.09185, 0.999805, -63937e-7, 867209e-8, 1.10207, 0.99985, -414582e-8, 566823e-8, 1.1117, 0.999912, -207443e-8, 277562e-8, 1.12022, 1.00001, 870226e-10, -53766e-9, 1.12832, 0.991943, -178672e-11, 0.0893382, 198384e-10, 0.991952, -450183e-10, 0.089339, 499849e-9, 0.991956, -180074e-9, 0.0893394, 19994e-7, 0.991955, -405167e-9, 0.0893393, 449867e-8, 0.991953, -720298e-9, 0.0893391, 799764e-8, 0.991955, -112548e-8, 0.0893393, 0.0124964, 0.991957, -16207e-7, 0.0893395, 0.0179951, 0.991958, -220601e-8, 0.0893396, 0.0244939, 0.991947, -288137e-8, 0.0893385, 0.0319929, 0.991962, -364693e-8, 0.0893399, 0.0404933, 0.991965, -450264e-8, 0.0893399, 0.049995, 0.99198, -544862e-8, 0.0893411, 0.0604995, 0.99197, -648491e-8, 0.0893397, 0.0720074, 0.991976, -761164e-8, 0.089341, 0.0845207, 0.99198, -882891e-8, 0.0893405, 0.0980413, 0.991982, -0.0101367, 0.0893396, 0.112571, 0.992008, -0.011535, 0.0893415, 0.128115, 0.992026, -0.0130228, 0.0893414, 0.144672, 0.992064, -0.0145966, 0.0893418, 0.162241, 0.992041, -0.0162421, 0.0893359, 0.180801, 0.992086, -0.0178888, 0.0893214, 0.200302, 0.992157, -0.0190368, 0.0892401, 0.220332, 0.992181, -0.0195584, 0.0890525, 0.240144, 0.992175, -0.0227257, 0.0892153, 0.260728, 0.99221, -0.0254195, 0.089304, 0.283473, 0.99222, -0.0274883, 0.0892703, 0.307673, 0.992317, -0.0294905, 0.0892027, 0.332729, 0.992374, -0.0311861, 0.0890577, 0.358387, 0.992505, -0.0320656, 0.0886994, 0.384102, 0.992568, -0.0329715, 0.0883198, 0.409767, 0.992675, -0.036006, 0.0883602, 0.436145, 0.992746, -0.0392897, 0.0884591, 0.463217, 0.992873, -0.0399337, 0.0878287, 0.491557, 0.992934, -0.040231, 0.0870108, 0.519516, 0.993091, -0.0422013, 0.0865857, 0.547741, 0.993259, -0.0443503, 0.0861937, 0.575792, 0.993455, -0.0446368, 0.0851187, 0.604233, 0.993497, -0.0454299, 0.0840576, 0.632925, 0.993694, -0.0463296, 0.0829671, 0.660985, 0.993718, -0.0470619, 0.0817185, 0.688714, 0.993973, -0.0468838, 0.0800294, 0.716743, 0.994207, -0.046705, 0.0781286, 0.74377, 0.994168, -0.0469698, 0.0763337, 0.77042, 0.9945, -0.0456816, 0.0738184, 0.796659, 0.994356, -0.0455518, 0.0715545, 0.821868, 0.994747, -0.0439488, 0.0686085, 0.846572, 0.994937, -0.0430056, 0.065869, 0.870435, 0.995142, -0.0413414, 0.0626446, 0.893272, 0.995451, -0.0396521, 0.05929, 0.915376, 0.995445, -0.0378453, 0.0558503, 0.936196, 0.995967, -0.0355219, 0.0520949, 0.956376, 0.996094, -0.0335146, 0.048377, 0.975327, 0.996622, -0.030682, 0.0442575, 0.993471, 0.996938, -0.0285504, 0.0404693, 1.01052, 0.997383, -0.0253399, 0.0360903, 1.02637, 0.997714, -0.0231651, 0.0322176, 1.04139, 0.998249, -0.0198138, 0.0278433, 1.05542, 0.998596, -0.0174337, 0.0238759, 1.06846, 0.998946, -0.0141349, 0.0195944, 1.08056, 0.99928, -0.0115603, 0.0156279, 1.09181, 0.999507, -839065e-8, 0.0114607, 1.10213, 0.999697, -5666e-6, 763325e-8, 1.11169, 0.999869, -269902e-8, 364946e-8, 1.12042, 1.00001, 623836e-10, -319288e-10, 1.12832, 0.987221, -222675e-11, 0.111332, 197456e-10, 0.98739, -561116e-10, 0.111351, 497563e-9, 0.987448, -224453e-9, 0.111357, 199031e-8, 0.987441, -505019e-9, 0.111357, 44782e-7, 0.987442, -897816e-9, 0.111357, 796129e-8, 0.987442, -140284e-8, 0.111357, 0.0124396, 0.987444, -202012e-8, 0.111357, 0.0179132, 0.987442, -274964e-8, 0.111357, 0.0243824, 0.987446, -359147e-8, 0.111357, 0.0318474, 0.987435, -454562e-8, 0.111356, 0.0403086, 0.987461, -561225e-8, 0.111358, 0.0497678, 0.987458, -679125e-8, 0.111358, 0.0602239, 0.987443, -80828e-7, 0.111356, 0.0716792, 0.987476, -94872e-7, 0.111358, 0.0841364, 0.98749, -0.0110044, 0.111361, 0.097597, 0.987508, -0.0126344, 0.111362, 0.112062, 0.987494, -0.0143767, 0.111357, 0.127533, 0.987526, -0.0162307, 0.111359, 0.144015, 0.987558, -0.0181912, 0.111361, 0.161502, 0.987602, -0.0202393, 0.111355, 0.179979, 0.987692, -0.022273, 0.111346, 0.199386, 0.987702, -0.0235306, 0.111215, 0.219183, 0.987789, -0.0247628, 0.111061, 0.239202, 0.987776, -0.0280668, 0.111171, 0.259957, 0.987856, -0.0316751, 0.111327, 0.282198, 0.987912, -0.0342468, 0.111282, 0.306294, 0.988, -0.0367205, 0.111198, 0.331219, 0.988055, -0.0387766, 0.110994, 0.356708, 0.988241, -0.0397722, 0.110547, 0.382234, 0.988399, -0.0416076, 0.110198, 0.408227, 0.988539, -0.0448192, 0.110137, 0.434662, 0.988661, -0.0483793, 0.110143, 0.461442, 0.988967, -0.0495895, 0.109453, 0.489318, 0.989073, -0.0506797, 0.108628, 0.517516, 0.989274, -0.0526953, 0.108003, 0.545844, 0.989528, -0.054578, 0.107255, 0.573823, 0.989709, -0.0561503, 0.106294, 0.601944, 0.989991, -0.056866, 0.104896, 0.630855, 0.990392, -0.0572914, 0.103336, 0.658925, 0.990374, -0.0586224, 0.10189, 0.686661, 0.990747, -0.0584764, 0.099783, 0.714548, 0.991041, -0.0582662, 0.0974309, 0.74186, 0.991236, -0.0584118, 0.0951678, 0.768422, 0.991585, -0.0573055, 0.0921581, 0.794817, 0.991984, -0.0564241, 0.0891167, 0.820336, 0.9921, -0.0553608, 0.085805, 0.84493, 0.992749, -0.0533816, 0.0820354, 0.868961, 0.99288, -0.0518661, 0.0782181, 0.891931, 0.993511, -0.0492492, 0.0738935, 0.914186, 0.993617, -0.0471956, 0.0696402, 0.93532, 0.99411, -0.044216, 0.0649659, 0.95543, 0.994595, -0.0416654, 0.0603177, 0.974685, 0.994976, -0.0384314, 0.0553493, 0.992807, 0.995579, -0.0353491, 0.0503942, 1.00996, 0.996069, -0.0319787, 0.0452123, 1.02606, 0.996718, -0.028472, 0.0400112, 1.04114, 0.997173, -0.0250789, 0.0349456, 1.05517, 0.997818, -0.0213326, 0.029653, 1.0683, 0.998318, -0.0178509, 0.024549, 1.0805, 0.998853, -0.0141118, 0.0194197, 1.09177, 0.999218, -0.0105914, 0.0143869, 1.1022, 0.999594, -693474e-8, 943517e-8, 1.11175, 0.99975, -340478e-8, 464051e-8, 1.12056, 1.00001, 109172e-9, -112821e-9, 1.12853, 0.983383, -266524e-11, 0.133358, 196534e-10, 0.981942, -671009e-10, 0.133162, 494804e-9, 0.981946, -268405e-9, 0.133163, 197923e-8, 0.981944, -603912e-9, 0.133163, 445326e-8, 0.981941, -107362e-8, 0.133162, 791693e-8, 0.981946, -167755e-8, 0.133163, 0.0123703, 0.981944, -241569e-8, 0.133162, 0.0178135, 0.981945, -328807e-8, 0.133163, 0.0242466, 0.981945, -429472e-8, 0.133162, 0.03167, 0.981955, -543573e-8, 0.133164, 0.0400846, 0.981951, -671105e-8, 0.133163, 0.0494901, 0.981968, -812092e-8, 0.133165, 0.0598886, 0.981979, -966541e-8, 0.133166, 0.0712811, 0.981996, -0.0113446, 0.133168, 0.083669, 0.982014, -0.0131585, 0.133169, 0.0970533, 0.982011, -0.0151073, 0.133167, 0.111438, 0.982062, -0.0171906, 0.133172, 0.126826, 0.9821, -0.0194067, 0.133175, 0.143215, 0.982149, -0.0217502, 0.133176, 0.160609, 0.982163, -0.0241945, 0.133173, 0.178981, 0.982247, -0.0265907, 0.133148, 0.198249, 0.982291, -0.027916, 0.132974, 0.217795, 0.982396, -0.0299663, 0.132868, 0.238042, 0.982456, -0.0334544, 0.132934, 0.258901, 0.982499, -0.0378636, 0.133137, 0.280639, 0.982617, -0.0409274, 0.133085, 0.304604, 0.98274, -0.0438523, 0.132985, 0.329376, 0.982944, -0.0462288, 0.132728, 0.354697, 0.98308, -0.0475995, 0.132228, 0.380102, 0.983391, -0.0501901, 0.131924, 0.406256, 0.983514, -0.0535899, 0.131737, 0.432735, 0.98373, -0.0571858, 0.131567, 0.459359, 0.984056, -0.0592353, 0.130932, 0.486637, 0.984234, -0.0610488, 0.130092, 0.51509, 0.984748, -0.0630758, 0.12923, 0.543461, 0.985073, -0.0647398, 0.128174, 0.571376, 0.985195, -0.0671941, 0.127133, 0.599414, 0.985734, -0.0681345, 0.125576, 0.628134, 0.986241, -0.0686089, 0.123639, 0.656399, 0.986356, -0.0698511, 0.121834, 0.684258, 0.986894, -0.0700931, 0.119454, 0.711818, 0.987382, -0.0698321, 0.116718, 0.739511, 0.988109, -0.0693975, 0.113699, 0.766267, 0.988363, -0.0689584, 0.110454, 0.792456, 0.989112, -0.0672353, 0.106602, 0.81813, 0.989241, -0.0662034, 0.10267, 0.842889, 0.990333, -0.0638938, 0.0981381, 0.867204, 0.990591, -0.0618534, 0.0935388, 0.89038, 0.991106, -0.0593117, 0.088553, 0.912576, 0.991919, -0.0562676, 0.0832187, 0.934118, 0.992111, -0.0534085, 0.0778302, 0.954254, 0.992997, -0.0495459, 0.0720453, 0.973722, 0.993317, -0.0463707, 0.0663458, 0.991949, 0.994133, -0.0421245, 0.0601883, 1.00936, 0.994705, -0.0384977, 0.0542501, 1.02559, 0.995495, -0.0340956, 0.0479862, 1.04083, 0.996206, -0.030105, 0.041887, 1.05497, 0.996971, -0.0256095, 0.0355355, 1.06824, 0.997796, -0.0213932, 0.0293655, 1.08056, 0.998272, -0.0169612, 0.0232926, 1.09182, 0.998857, -0.0126756, 0.0172786, 1.10219, 0.99939, -832486e-8, 0.0113156, 1.11192, 0.999752, -410826e-8, 557892e-8, 1.12075, 1, 150957e-9, -119101e-9, 1.12885, 0.975169, -309397e-11, 0.154669, 195073e-10, 0.975439, -779608e-10, 0.154712, 491534e-9, 0.975464, -311847e-9, 0.154716, 196617e-8, 0.975464, -701656e-9, 0.154716, 442387e-8, 0.975462, -12474e-7, 0.154715, 78647e-7, 0.975461, -194906e-8, 0.154715, 0.0122886, 0.975464, -280667e-8, 0.154715, 0.0176959, 0.975468, -382025e-8, 0.154716, 0.0240867, 0.975471, -498985e-8, 0.154716, 0.0314612, 0.975472, -631541e-8, 0.154717, 0.0398199, 0.975486, -779719e-8, 0.154718, 0.0491639, 0.975489, -943505e-8, 0.154718, 0.0594932, 0.975509, -0.0112295, 0.154721, 0.0708113, 0.97554, -0.0131802, 0.154724, 0.0831176, 0.975557, -0.0152876, 0.154726, 0.096415, 0.975585, -0.0175512, 0.154728, 0.110705, 0.975605, -0.0199713, 0.154729, 0.125992, 0.975645, -0.0225447, 0.154729, 0.142272, 0.975711, -0.0252649, 0.154735, 0.159549, 0.975788, -0.0280986, 0.154736, 0.177805, 0.975872, -0.0308232, 0.154704, 0.196911, 0.975968, -0.0324841, 0.154525, 0.216324, 0.976063, -0.0351281, 0.154432, 0.236628, 0.976157, -0.0388618, 0.15446, 0.257539, 0.976204, -0.0437704, 0.154665, 0.278975, 0.976358, -0.047514, 0.154652, 0.302606, 0.976571, -0.0508638, 0.154535, 0.327204, 0.976725, -0.0534995, 0.154221, 0.352276, 0.977013, -0.0555547, 0.153737, 0.377696, 0.977294, -0.0586728, 0.153403, 0.403855, 0.977602, -0.0622715, 0.15312, 0.430333, 0.977932, -0.0658166, 0.152755, 0.456855, 0.978241, -0.0689877, 0.152233, 0.483668, 0.978602, -0.0712805, 0.15132, 0.512097, 0.979234, -0.0732775, 0.150235, 0.540455, 0.97977, -0.075163, 0.148978, 0.568486, 0.979995, -0.0778026, 0.147755, 0.596524, 0.98078, -0.0791854, 0.146019, 0.624825, 0.981628, -0.0799666, 0.143906, 0.653403, 0.982067, -0.0808532, 0.141561, 0.681445, 0.98271, -0.0816024, 0.139025, 0.708918, 0.983734, -0.0812511, 0.135764, 0.736594, 0.98431, -0.0806201, 0.132152, 0.763576, 0.985071, -0.0801605, 0.12846, 0.789797, 0.98618, -0.0784208, 0.124084, 0.815804, 0.986886, -0.0766643, 0.1193, 0.840869, 0.987485, -0.0747744, 0.114236, 0.864952, 0.988431, -0.0716701, 0.108654, 0.888431, 0.988886, -0.0691609, 0.102994, 0.910963, 0.990024, -0.0654048, 0.0967278, 0.932629, 0.990401, -0.0619765, 0.090384, 0.95313, 0.991093, -0.0579296, 0.0837885, 0.972587, 0.992018, -0.0536576, 0.0770171, 0.991184, 0.992536, -0.0493719, 0.0701486, 1.00863, 0.993421, -0.0444813, 0.062953, 1.02494, 0.993928, -0.040008, 0.0560455, 1.04017, 0.994994, -0.0347982, 0.04856, 1.05463, 0.995866, -0.0301017, 0.0416152, 1.06807, 0.996916, -0.0248225, 0.0342597, 1.08039, 0.997766, -0.0199229, 0.0271668, 1.09177, 0.998479, -0.0147422, 0.0201387, 1.10235, 0.99921, -980173e-8, 0.0131944, 1.11206, 0.999652, -47426e-7, 640712e-8, 1.12104, 0.999998, 891673e-10, -10379e-8, 1.12906, 0.967868, -351885e-11, 0.175947, 193569e-10, 0.968001, -886733e-10, 0.175972, 487782e-9, 0.96801, -354697e-9, 0.175973, 195115e-8, 0.968012, -798063e-9, 0.175974, 439006e-8, 0.968011, -141879e-8, 0.175973, 780461e-8, 0.968011, -221686e-8, 0.175973, 0.0121948, 0.968016, -319231e-8, 0.175974, 0.0175607, 0.968019, -434515e-8, 0.175974, 0.0239027, 0.968018, -567538e-8, 0.175974, 0.0312208, 0.968033, -718308e-8, 0.175977, 0.0395158, 0.968049, -886836e-8, 0.175979, 0.0487885, 0.968047, -0.0107312, 0.175978, 0.0590394, 0.968072, -0.0127719, 0.175981, 0.0702705, 0.968108, -0.0149905, 0.175986, 0.0824836, 0.968112, -0.0173866, 0.175985, 0.0956783, 0.968173, -0.0199611, 0.175993, 0.109862, 0.96827, -0.0227128, 0.176008, 0.125033, 0.968292, -0.025639, 0.17601, 0.141193, 0.968339, -0.0287299, 0.176007, 0.158336, 0.968389, -0.0319399, 0.176001, 0.176441, 0.968501, -0.034941, 0.175962, 0.195359, 0.968646, -0.0370812, 0.175793, 0.214686, 0.968789, -0.0402329, 0.175708, 0.234973, 0.96886, -0.0442601, 0.1757, 0.255871, 0.969013, -0.049398, 0.175876, 0.277238, 0.969242, -0.0539932, 0.17594, 0.300326, 0.969419, -0.0577299, 0.175781, 0.324702, 0.969763, -0.0605643, 0.175432, 0.349527, 0.970093, -0.0634488, 0.174992, 0.374976, 0.970361, -0.0670589, 0.174611, 0.401097, 0.970825, -0.0708246, 0.174226, 0.427496, 0.971214, -0.0742871, 0.173684, 0.453858, 0.971622, -0.0782608, 0.173186, 0.480637, 0.972175, -0.0813151, 0.172288, 0.508655, 0.972944, -0.0832678, 0.170979, 0.536973, 0.973595, -0.0855964, 0.169573, 0.565138, 0.974345, -0.0882163, 0.168152, 0.593222, 0.975233, -0.0901671, 0.166314, 0.621201, 0.976239, -0.0912111, 0.163931, 0.649919, 0.977289, -0.0916959, 0.161106, 0.678011, 0.978076, -0.0927061, 0.158272, 0.705717, 0.979533, -0.0925562, 0.15475, 0.733228, 0.980335, -0.0918159, 0.150638, 0.760454, 0.981808, -0.0908508, 0.146201, 0.786918, 0.983061, -0.0896172, 0.141386, 0.812953, 0.984148, -0.0871588, 0.135837, 0.838281, 0.985047, -0.0850624, 0.130135, 0.862594, 0.986219, -0.0818541, 0.123882, 0.88633, 0.987043, -0.0784523, 0.117126, 0.908952, 0.988107, -0.0749601, 0.110341, 0.930744, 0.988955, -0.0703548, 0.102885, 0.951728, 0.989426, -0.0662798, 0.0954167, 0.971166, 0.990421, -0.0610834, 0.0876331, 0.989984, 0.991032, -0.0562936, 0.0797785, 1.00765, 0.992041, -0.0508154, 0.0718166, 1.02434, 0.992794, -0.0454045, 0.0637125, 1.03976, 0.993691, -0.0398194, 0.0555338, 1.05418, 0.994778, -0.0341482, 0.0473388, 1.06772, 0.995915, -0.028428, 0.0391016, 1.08028, 0.997109, -0.022642, 0.0309953, 1.09185, 0.998095, -0.0168738, 0.0230288, 1.10247, 0.998985, -0.0111274, 0.0150722, 1.11229, 0.999581, -543881e-8, 740605e-8, 1.12131, 1.00003, 162239e-9, -105549e-9, 1.12946, 0.959505, -393734e-11, 0.196876, 191893e-10, 0.959599, -992157e-10, 0.196895, 483544e-9, 0.959641, -396868e-9, 0.196903, 19342e-7, 0.959599, -892948e-9, 0.196895, 435193e-8, 0.959603, -158747e-8, 0.196896, 77368e-7, 0.959604, -248042e-8, 0.196896, 0.0120888, 0.959605, -357184e-8, 0.196896, 0.0174082, 0.959605, -486169e-8, 0.196896, 0.0236949, 0.959613, -635008e-8, 0.196897, 0.0309497, 0.959619, -803696e-8, 0.196898, 0.0391725, 0.959636, -992255e-8, 0.196901, 0.0483649, 0.959634, -0.0120067, 0.1969, 0.0585266, 0.959675, -0.0142898, 0.196906, 0.0696609, 0.959712, -0.0167717, 0.196911, 0.0817678, 0.959752, -0.0194524, 0.196918, 0.0948494, 0.959807, -0.0223321, 0.196925, 0.10891, 0.959828, -0.0254091, 0.196924, 0.123947, 0.959906, -0.0286815, 0.196934, 0.139968, 0.960005, -0.0321371, 0.196944, 0.156968, 0.960071, -0.0357114, 0.196936, 0.17491, 0.960237, -0.0389064, 0.196882, 0.193597, 0.960367, -0.041623, 0.196731, 0.21285, 0.960562, -0.0452655, 0.196654, 0.233075, 0.960735, -0.0496207, 0.196643, 0.253941, 0.960913, -0.0549379, 0.196774, 0.275278, 0.961121, -0.0603414, 0.196893, 0.297733, 0.96139, -0.0644244, 0.196717, 0.321877, 0.961818, -0.067556, 0.196314, 0.346476, 0.962175, -0.0712709, 0.195917, 0.371907, 0.96255, -0.0752848, 0.1955, 0.397916, 0.963164, -0.0792073, 0.195026, 0.424229, 0.963782, -0.0828225, 0.194424, 0.450637, 0.964306, -0.0873119, 0.193831, 0.477288, 0.964923, -0.0911051, 0.192973, 0.504716, 0.966048, -0.093251, 0.19151, 0.533053, 0.967024, -0.0958983, 0.190013, 0.561366, 0.968038, -0.09835, 0.188253, 0.589464, 0.969152, -0.100754, 0.186257, 0.617433, 0.970557, -0.102239, 0.183775, 0.645801, 0.972104, -0.102767, 0.180645, 0.674278, 0.973203, -0.103492, 0.177242, 0.702004, 0.975123, -0.103793, 0.17345, 0.729529, 0.97641, -0.102839, 0.168886, 0.756712, 0.978313, -0.101687, 0.163892, 0.783801, 0.980036, -0.100314, 0.158439, 0.809671, 0.981339, -0.097836, 0.152211, 0.835402, 0.982794, -0.0950006, 0.145679, 0.860081, 0.984123, -0.0920994, 0.138949, 0.883757, 0.984918, -0.0878641, 0.131283, 0.90685, 0.985999, -0.083939, 0.123464, 0.928786, 0.987151, -0.0791234, 0.115324, 0.94983, 0.987827, -0.0739332, 0.106854, 0.96962, 0.988806, -0.0688088, 0.0982691, 0.98861, 0.989588, -0.0628962, 0.0893456, 1.00667, 0.990438, -0.0573146, 0.0805392, 1.02344, 0.991506, -0.0509433, 0.0713725, 1.03933, 0.992492, -0.0448724, 0.0623732, 1.05378, 0.993663, -0.0383497, 0.0530838, 1.06747, 0.994956, -0.0319593, 0.0439512, 1.08007, 0.99634, -0.025401, 0.0347803, 1.09182, 0.99761, -0.0189687, 0.0257954, 1.1025, 0.99863, -0.0124441, 0.0169893, 1.11247, 0.99947, -614003e-8, 829498e-8, 1.12151, 1.00008, 216624e-9, -146107e-9, 1.12993, 0.950129, -434955e-11, 0.217413, 190081e-10, 0.950264, -10957e-8, 0.217444, 47884e-8, 0.9503, -438299e-9, 0.217451, 191543e-8, 0.950246, -986124e-9, 0.21744, 430951e-8, 0.950246, -175311e-8, 0.21744, 766137e-8, 0.950245, -273923e-8, 0.21744, 0.011971, 0.950253, -394453e-8, 0.217441, 0.0172385, 0.950258, -536897e-8, 0.217442, 0.0234641, 0.950267, -701262e-8, 0.217444, 0.030648, 0.950277, -887551e-8, 0.217446, 0.038791, 0.950284, -0.0109576, 0.217446, 0.0478931, 0.950312, -0.0132591, 0.217451, 0.0579568, 0.950334, -0.01578, 0.217454, 0.0689821, 0.950378, -0.0185204, 0.217462, 0.0809714, 0.950417, -0.0214803, 0.217467, 0.0939265, 0.950488, -0.0246594, 0.217479, 0.10785, 0.950534, -0.0280565, 0.217483, 0.122743, 0.950633, -0.0316685, 0.217498, 0.138611, 0.950698, -0.0354787, 0.217499, 0.155442, 0.950844, -0.0394003, 0.217507, 0.173208, 0.950999, -0.0426812, 0.217419, 0.191605, 0.951221, -0.0461302, 0.217317, 0.21084, 0.951412, -0.0502131, 0.217238, 0.230945, 0.951623, -0.0549183, 0.21722, 0.251745, 0.951867, -0.0604493, 0.217306, 0.273001, 0.952069, -0.0665189, 0.217466, 0.294874, 0.952459, -0.0709179, 0.217266, 0.318732, 0.952996, -0.0746112, 0.216891, 0.34318, 0.953425, -0.0789252, 0.216503, 0.36849, 0.953885, -0.0833293, 0.216042, 0.394373, 0.954617, -0.087371, 0.215469, 0.420505, 0.955429, -0.0914054, 0.214802, 0.446907, 0.956068, -0.0961671, 0.214146, 0.473522, 0.957094, -0.10048, 0.213286, 0.50052, 0.958372, -0.103248, 0.211796, 0.528715, 0.959654, -0.106033, 0.21016, 0.557065, 0.961305, -0.108384, 0.208149, 0.585286, 0.962785, -0.111122, 0.206024, 0.613334, 0.964848, -0.112981, 0.203442, 0.641334, 0.966498, -0.113717, 0.19996, 0.669955, 0.968678, -0.114121, 0.196105, 0.698094, 0.970489, -0.114524, 0.191906, 0.725643, 0.972903, -0.113792, 0.186963, 0.752856, 0.974701, -0.112406, 0.181343, 0.780013, 0.976718, -0.110685, 0.175185, 0.806268, 0.978905, -0.108468, 0.168535, 0.832073, 0.980267, -0.105061, 0.161106, 0.857149, 0.981967, -0.101675, 0.153387, 0.881145, 0.983063, -0.0974492, 0.145199, 0.904255, 0.984432, -0.0925815, 0.136527, 0.926686, 0.985734, -0.0877983, 0.127584, 0.947901, 0.986228, -0.081884, 0.118125, 0.968111, 0.98719, -0.0761208, 0.108594, 0.98719, 0.988228, -0.0698196, 0.0989996, 1.00559, 0.989046, -0.0632739, 0.0890074, 1.02246, 0.990242, -0.056522, 0.0790832, 1.03841, 0.991252, -0.0495272, 0.0689182, 1.05347, 0.992542, -0.0425373, 0.0588592, 1.06724, 0.994096, -0.0353198, 0.0486833, 1.08009, 0.995593, -0.028235, 0.0385977, 1.09177, 0.99711, -0.0209511, 0.0286457, 1.10274, 0.998263, -0.0139289, 0.0188497, 1.11262, 0.999254, -67359e-7, 9208e-6, 1.12191, 0.999967, 141846e-9, -657764e-10, 1.13024, 0.935608, -474692e-11, 0.236466, 187817e-10, 0.93996, -11971e-8, 0.237568, 473646e-9, 0.939959, -478845e-9, 0.237567, 18946e-7, 0.939954, -10774e-7, 0.237566, 426284e-8, 0.939956, -191538e-8, 0.237566, 757842e-8, 0.939954, -299277e-8, 0.237566, 0.0118413, 0.93996, -430961e-8, 0.237567, 0.0170518, 0.939969, -586589e-8, 0.237569, 0.02321, 0.939982, -766166e-8, 0.237572, 0.0303164, 0.939987, -969686e-8, 0.237572, 0.0383711, 0.939997, -0.0119715, 0.237574, 0.0473751, 0.940031, -0.0144858, 0.237581, 0.0573298, 0.940073, -0.0172399, 0.237589, 0.0682366, 0.94012, -0.0202335, 0.237598, 0.080097, 0.940162, -0.0234663, 0.237604, 0.0929116, 0.940237, -0.0269387, 0.237615, 0.106686, 0.940328, -0.0306489, 0.237632, 0.121421, 0.940419, -0.0345917, 0.237645, 0.137115, 0.940522, -0.0387481, 0.237654, 0.153766, 0.940702, -0.0429906, 0.237661, 0.17133, 0.940871, -0.0465089, 0.237561, 0.189502, 0.941103, -0.050531, 0.23748, 0.208616, 0.941369, -0.0550657, 0.237423, 0.228595, 0.941641, -0.0601337, 0.237399, 0.249287, 0.941903, -0.0658804, 0.237443, 0.270467, 0.942224, -0.0722674, 0.237597, 0.292024, 0.942633, -0.0771788, 0.237419, 0.315272, 0.943172, -0.0815623, 0.237068, 0.339579, 0.943691, -0.0863973, 0.236682, 0.364717, 0.944382, -0.0911536, 0.236213, 0.390435, 0.945392, -0.0952967, 0.235562, 0.416425, 0.946185, -0.0998948, 0.234832, 0.442772, 0.947212, -0.104796, 0.234114, 0.469347, 0.948778, -0.10928, 0.233222, 0.496162, 0.950149, -0.113081, 0.231845, 0.523978, 0.951989, -0.115893, 0.230005, 0.552295, 0.953921, -0.11846, 0.227862, 0.580569, 0.955624, -0.12115, 0.225439, 0.608698, 0.958234, -0.123373, 0.222635, 0.636696, 0.960593, -0.124519, 0.219093, 0.665208, 0.963201, -0.124736, 0.214749, 0.693557, 0.965642, -0.125012, 0.210059, 0.721334, 0.968765, -0.124661, 0.204935, 0.748613, 0.971753, -0.122996, 0.198661, 0.776224, 0.973751, -0.120998, 0.191823, 0.802461, 0.976709, -0.118583, 0.184359, 0.828399, 0.977956, -0.115102, 0.176437, 0.853693, 0.979672, -0.111077, 0.167681, 0.877962, 0.981816, -0.10688, 0.158872, 0.901564, 0.98238, -0.101469, 0.149398, 0.924057, 0.983964, -0.0960013, 0.139436, 0.945751, 0.984933, -0.0899626, 0.12943, 0.966272, 0.985694, -0.0832973, 0.11894, 0.985741, 0.986822, -0.0767082, 0.108349, 1.00407, 0.987725, -0.0693614, 0.0976026, 1.02154, 0.98877, -0.06211, 0.086652, 1.03757, 0.990129, -0.0544143, 0.0756182, 1.05296, 0.991337, -0.046744, 0.0645753, 1.06683, 0.992978, -0.0387931, 0.0534683, 1.0798, 0.994676, -0.030973, 0.0424137, 1.09181, 0.99645, -0.0230311, 0.0314035, 1.10286, 0.997967, -0.0152065, 0.0206869, 1.11291, 0.99922, -744837e-8, 0.010155, 1.12237, 1.00002, 240209e-9, -752767e-10, 1.13089, 0.922948, -515351e-11, 0.255626, 186069e-10, 0.928785, -129623e-9, 0.257244, 468009e-9, 0.928761, -51849e-8, 0.257237, 187202e-8, 0.928751, -11666e-7, 0.257235, 421204e-8, 0.928751, -207395e-8, 0.257234, 74881e-7, 0.928754, -324055e-8, 0.257235, 0.0117002, 0.92876, -466639e-8, 0.257236, 0.0168486, 0.928763, -635149e-8, 0.257237, 0.0229334, 0.928774, -829584e-8, 0.257239, 0.029955, 0.928791, -0.0104995, 0.257243, 0.0379139, 0.928804, -0.0129623, 0.257245, 0.0468108, 0.928847, -0.0156846, 0.257255, 0.0566473, 0.92889, -0.0186661, 0.257263, 0.0674246, 0.928924, -0.0219067, 0.257268, 0.0791433, 0.928989, -0.0254066, 0.257282, 0.0918076, 0.92909, -0.0291651, 0.257301, 0.105419, 0.92918, -0.0331801, 0.257316, 0.119978, 0.92929, -0.0374469, 0.257332, 0.135491, 0.929453, -0.041939, 0.257357, 0.151948, 0.929586, -0.0464612, 0.257347, 0.169275, 0.929858, -0.0503426, 0.257269, 0.187257, 0.930125, -0.0548409, 0.257199, 0.206204, 0.930403, -0.0598063, 0.257149, 0.22601, 0.930726, -0.0652437, 0.257122, 0.246561, 0.931098, -0.0712376, 0.257153, 0.267618, 0.931396, -0.0777506, 0.257237, 0.288993, 0.931947, -0.0832374, 0.257124, 0.311527, 0.932579, -0.0883955, 0.25683, 0.335697, 0.933194, -0.0937037, 0.256444, 0.360634, 0.934013, -0.0987292, 0.255939, 0.386126, 0.935307, -0.103215, 0.255282, 0.412018, 0.936374, -0.108234, 0.254538, 0.438292, 0.93776, -0.113234, 0.253728, 0.464805, 0.939599, -0.118013, 0.25275, 0.491464, 0.941036, -0.122661, 0.251404, 0.518751, 0.94337, -0.125477, 0.249435, 0.547133, 0.945318, -0.128374, 0.247113, 0.575456, 0.947995, -0.130996, 0.244441, 0.60372, 0.950818, -0.133438, 0.241352, 0.63174, 0.954378, -0.135004, 0.237849, 0.659971, 0.957151, -0.135313, 0.233188, 0.688478, 0.960743, -0.13521, 0.228001, 0.716767, 0.964352, -0.135007, 0.222249, 0.744349, 0.967273, -0.133523, 0.21542, 0.771786, 0.969767, -0.131155, 0.208039, 0.798639, 0.973195, -0.128492, 0.200076, 0.824774, 0.975557, -0.125094, 0.191451, 0.850222, 0.977692, -0.120578, 0.18184, 0.874761, 0.98026, -0.115882, 0.172102, 0.898497, 0.981394, -0.110372, 0.161859, 0.921636, 0.982386, -0.10415, 0.15108, 0.943467, 0.983783, -0.0978128, 0.140407, 0.964045, 0.98422, -0.0906171, 0.129058, 0.98398, 0.985447, -0.0832921, 0.117614, 1.00276, 0.986682, -0.0754412, 0.10585, 1.02047, 0.987326, -0.0673885, 0.0940943, 1.03678, 0.988707, -0.0592565, 0.0822093, 1.05218, 0.990185, -0.050717, 0.070192, 1.06652, 0.991866, -0.0423486, 0.0582081, 1.07965, 0.993897, -0.0336118, 0.0460985, 1.09188, 0.995841, -0.0252178, 0.0342737, 1.10307, 0.997605, -0.0164893, 0.0224829, 1.11324, 0.999037, -817112e-8, 0.0110647, 1.12262, 1.00003, 291686e-9, -168673e-9, 1.13139, 0.915304, -552675e-11, 0.275999, 183285e-10, 0.91668, -139285e-9, 0.276414, 461914e-9, 0.916664, -55713e-8, 0.276409, 184763e-8, 0.916653, -125354e-8, 0.276406, 415715e-8, 0.916651, -222851e-8, 0.276405, 739053e-8, 0.916655, -348205e-8, 0.276406, 0.0115478, 0.916653, -501414e-8, 0.276405, 0.0166291, 0.916667, -682478e-8, 0.276409, 0.0226346, 0.91668, -891398e-8, 0.276412, 0.0295648, 0.91669, -0.0112817, 0.276413, 0.0374199, 0.916727, -0.013928, 0.276422, 0.0462016, 0.916759, -0.0168528, 0.276429, 0.0559101, 0.916793, -0.0200558, 0.276436, 0.0665466, 0.916849, -0.0235373, 0.276448, 0.0781139, 0.916964, -0.0272973, 0.276474, 0.0906156, 0.917047, -0.0313344, 0.276491, 0.104051, 0.917152, -0.0356465, 0.276511, 0.118424, 0.917286, -0.0402271, 0.276533, 0.133736, 0.917469, -0.0450408, 0.276564, 0.149978, 0.917686, -0.0497872, 0.276563, 0.167057, 0.917953, -0.0540937, 0.276493, 0.184846, 0.918228, -0.0590709, 0.276437, 0.203614, 0.918572, -0.0644277, 0.276398, 0.223212, 0.918918, -0.0702326, 0.276362, 0.243584, 0.919356, -0.076484, 0.276383, 0.264465, 0.919842, -0.0830808, 0.276434, 0.285701, 0.920451, -0.0892972, 0.276407, 0.307559, 0.921113, -0.095016, 0.276128, 0.331501, 0.921881, -0.100771, 0.275754, 0.356207, 0.923027, -0.106029, 0.275254, 0.381477, 0.924364, -0.111029, 0.274595, 0.40722, 0.925818, -0.116345, 0.273841, 0.433385, 0.92746, -0.121424, 0.272913, 0.459848, 0.929167, -0.12657, 0.271837, 0.486493, 0.931426, -0.131581, 0.270575, 0.513432, 0.934001, -0.135038, 0.268512, 0.541502, 0.936296, -0.138039, 0.266135, 0.569658, 0.939985, -0.140687, 0.263271, 0.598375, 0.943516, -0.143247, 0.260058, 0.626563, 0.94782, -0.145135, 0.256138, 0.654711, 0.951023, -0.145733, 0.251154, 0.683285, 0.955338, -0.145554, 0.245562, 0.711831, 0.959629, -0.145008, 0.239265, 0.739573, 0.963123, -0.144003, 0.232064, 0.767027, 0.966742, -0.141289, 0.224036, 0.794359, 0.969991, -0.138247, 0.215305, 0.820361, 0.973403, -0.134786, 0.206051, 0.846548, 0.975317, -0.129966, 0.195914, 0.871541, 0.977647, -0.12471, 0.185184, 0.895313, 0.980137, -0.119086, 0.174161, 0.918398, 0.981031, -0.112297, 0.162792, 0.940679, 0.982037, -0.105372, 0.150952, 0.961991, 0.983164, -0.097821, 0.138921, 0.981913, 0.983757, -0.0897245, 0.126611, 1.00109, 0.985036, -0.0815974, 0.114228, 1.01902, 0.986289, -0.0727725, 0.101389, 1.03604, 0.987329, -0.0639323, 0.0886476, 1.05149, 0.989193, -0.0548109, 0.0756837, 1.06619, 0.990716, -0.045687, 0.0627581, 1.07948, 0.992769, -0.0364315, 0.0498337, 1.09172, 0.99524, -0.0271761, 0.0370305, 1.1033, 0.997154, -0.0179609, 0.0243959, 1.11353, 0.998845, -878063e-8, 0.0119567, 1.12319, 1.00002, 259038e-9, -108146e-9, 1.13177, 0.903945, -591681e-11, 0.295126, 181226e-10, 0.903668, -148672e-9, 0.295037, 455367e-9, 0.903677, -594683e-9, 0.29504, 182145e-8, 0.903673, -133805e-8, 0.295039, 409831e-8, 0.903666, -237872e-8, 0.295036, 728584e-8, 0.903668, -371676e-8, 0.295037, 0.0113842, 0.903679, -535212e-8, 0.29504, 0.0163936, 0.903684, -728479e-8, 0.295041, 0.0223141, 0.903698, -951473e-8, 0.295044, 0.0291462, 0.903718, -0.0120419, 0.295049, 0.0368904, 0.903754, -0.0148664, 0.295058, 0.0455477, 0.903801, -0.017988, 0.29507, 0.0551194, 0.903851, -0.0214064, 0.295082, 0.0656058, 0.903921, -0.0251219, 0.295097, 0.0770109, 0.904002, -0.0291337, 0.295116, 0.0893354, 0.904111, -0.033441, 0.29514, 0.102583, 0.904246, -0.0380415, 0.295169, 0.116755, 0.904408, -0.0429258, 0.295202, 0.131853, 0.904637, -0.0480468, 0.295245, 0.147869, 0.904821, -0.0529208, 0.295214, 0.164658, 0.905163, -0.0577748, 0.295185, 0.182274, 0.905469, -0.0631763, 0.295143, 0.200828, 0.905851, -0.068917, 0.295112, 0.2202, 0.906322, -0.0750861, 0.295104, 0.240372, 0.906761, -0.0815855, 0.295086, 0.261082, 0.90735, -0.0882138, 0.295095, 0.282123, 0.908087, -0.095082, 0.295139, 0.303563, 0.908826, -0.101488, 0.29492, 0.327028, 0.909832, -0.107577, 0.294577, 0.351464, 0.911393, -0.113033, 0.294115, 0.376497, 0.912804, -0.118629, 0.293446, 0.402115, 0.914081, -0.124232, 0.292581, 0.428111, 0.91637, -0.129399, 0.29166, 0.454442, 0.91814, -0.134892, 0.290422, 0.481024, 0.921179, -0.140069, 0.289194, 0.507924, 0.924544, -0.144431, 0.287421, 0.535557, 0.927995, -0.147498, 0.284867, 0.563984, 0.931556, -0.150197, 0.281722, 0.5923, 0.935777, -0.152711, 0.278207, 0.620832, 0.940869, -0.154836, 0.274148, 0.649069, 0.945994, -0.155912, 0.269057, 0.677746, 0.949634, -0.155641, 0.262799, 0.706293, 0.955032, -0.154809, 0.256097, 0.734278, 0.95917, -0.153678, 0.248618, 0.761751, 0.962931, -0.151253, 0.239794, 0.789032, 0.966045, -0.147625, 0.230281, 0.815422, 0.96971, -0.143964, 0.220382, 0.841787, 0.972747, -0.139464, 0.209846, 0.867446, 0.975545, -0.133459, 0.198189, 0.892004, 0.978381, -0.127424, 0.186362, 0.915458, 0.979935, -0.120506, 0.173964, 0.937948, 0.980948, -0.11282, 0.161429, 0.959732, 0.982234, -0.104941, 0.148557, 0.980118, 0.982767, -0.0962905, 0.135508, 0.999463, 0.983544, -0.0873625, 0.122338, 1.01756, 0.984965, -0.0783447, 0.108669, 1.03492, 0.986233, -0.0684798, 0.0949911, 1.05087, 0.987796, -0.0590867, 0.0811386, 1.0656, 0.989885, -0.0489145, 0.0673099, 1.0794, 0.991821, -0.0391, 0.0535665, 1.09174, 0.99448, -0.029087, 0.0397529, 1.10341, 0.996769, -0.019114, 0.0261463, 1.11383, 0.998641, -947007e-8, 0.0128731, 1.1237, 0.999978, 446316e-9, -169093e-9, 1.13253, 0.888362, -627064e-11, 0.312578, 178215e-10, 0.889988, -157791e-9, 0.313148, 448451e-9, 0.889825, -631076e-9, 0.313092, 179356e-8, 0.88984, -141994e-8, 0.313097, 403554e-8, 0.889828, -25243e-7, 0.313092, 717429e-8, 0.889831, -394421e-8, 0.313093, 0.0112099, 0.889831, -567962e-8, 0.313093, 0.0161425, 0.889844, -773051e-8, 0.313096, 0.0219724, 0.889858, -0.0100968, 0.3131, 0.0286999, 0.889882, -0.0127786, 0.313106, 0.0363256, 0.889918, -0.0157757, 0.313116, 0.0448509, 0.889967, -0.0190878, 0.313129, 0.0542758, 0.89003, -0.022715, 0.313145, 0.0646032, 0.890108, -0.0266566, 0.313165, 0.0758339, 0.890218, -0.0309131, 0.313193, 0.0879729, 0.890351, -0.0354819, 0.313226, 0.101019, 0.89051, -0.0403613, 0.313263, 0.114979, 0.890672, -0.0455385, 0.313294, 0.129848, 0.890882, -0.0509444, 0.313333, 0.145616, 0.891189, -0.0559657, 0.313324, 0.162122, 0.891457, -0.0613123, 0.313281, 0.179524, 0.891856, -0.0671488, 0.313281, 0.197855, 0.892312, -0.0732732, 0.313268, 0.216991, 0.892819, -0.0797865, 0.313263, 0.236924, 0.893369, -0.0865269, 0.313247, 0.257433, 0.894045, -0.0931592, 0.313205, 0.278215, 0.894884, -0.100532, 0.313276, 0.299467, 0.895832, -0.107716, 0.313205, 0.322276, 0.897043, -0.114099, 0.312873, 0.34642, 0.898515, -0.119941, 0.312331, 0.371187, 0.900191, -0.126044, 0.311731, 0.396656, 0.90188, -0.131808, 0.310859, 0.422488, 0.904359, -0.137289, 0.309857, 0.448744, 0.906923, -0.142991, 0.308714, 0.475239, 0.910634, -0.148253, 0.307465, 0.501983, 0.914502, -0.153332, 0.305774, 0.529254, 0.919046, -0.156646, 0.303156, 0.557709, 0.923194, -0.159612, 0.299928, 0.586267, 0.928858, -0.162027, 0.296245, 0.614925, 0.934464, -0.164203, 0.291832, 0.643187, 0.939824, -0.165602, 0.286565, 0.671601, 0.944582, -0.165383, 0.280073, 0.700213, 0.949257, -0.164439, 0.272891, 0.728432, 0.954389, -0.162953, 0.264771, 0.756082, 0.958595, -0.161007, 0.255927, 0.78369, 0.962138, -0.157243, 0.245769, 0.810769, 0.966979, -0.152872, 0.235127, 0.836999, 0.969566, -0.148209, 0.22347, 0.862684, 0.972372, -0.142211, 0.211147, 0.887847, 0.975916, -0.135458, 0.198606, 0.911843, 0.978026, -0.128398, 0.185498, 0.934795, 0.979686, -0.120313, 0.17171, 0.956787, 0.980748, -0.11166, 0.158159, 0.978046, 0.981622, -0.103035, 0.144399, 0.997693, 0.982356, -0.0930328, 0.13001, 1.01642, 0.983308, -0.0834627, 0.115778, 1.03366, 0.985037, -0.0732249, 0.101327, 1.05014, 0.986493, -0.0628145, 0.086554, 1.06507, 0.988484, -0.0526556, 0.0720413, 1.07907, 0.991051, -0.0415744, 0.0571151, 1.09189, 0.993523, -0.0314275, 0.0426643, 1.10369, 0.99628, -0.0203603, 0.0279325, 1.11423, 0.998344, -0.0102446, 0.0138182, 1.12421, 0.999997, 42612e-8, -193628e-9, 1.1333, 0.871555, -660007e-11, 0.329176, 174749e-10, 0.875255, -166579e-9, 0.330571, 441051e-9, 0.875644, -666394e-9, 0.330718, 176441e-8, 0.875159, -149903e-8, 0.330536, 396899e-8, 0.87516, -266493e-8, 0.330536, 7056e-6, 0.875158, -416393e-8, 0.330535, 0.0110251, 0.87516, -599598e-8, 0.330535, 0.0158764, 0.875163, -816108e-8, 0.330536, 0.0216101, 0.875174, -0.0106591, 0.330538, 0.0282266, 0.875199, -0.0134899, 0.330545, 0.0357266, 0.875257, -0.0166538, 0.330563, 0.0441117, 0.875304, -0.0201501, 0.330575, 0.0533821, 0.875373, -0.0239785, 0.330595, 0.0635395, 0.875464, -0.0281389, 0.330619, 0.0745872, 0.875565, -0.0326301, 0.330645, 0.0865255, 0.875691, -0.0374516, 0.330676, 0.0993599, 0.875897, -0.0425993, 0.330733, 0.113093, 0.876091, -0.0480576, 0.330776, 0.127722, 0.876353, -0.0537216, 0.330826, 0.143227, 0.876649, -0.0589807, 0.330809, 0.159462, 0.877034, -0.0647865, 0.330819, 0.176642, 0.877443, -0.0709789, 0.330817, 0.194702, 0.877956, -0.0774782, 0.330832, 0.213577, 0.878499, -0.0843175, 0.330822, 0.233246, 0.879144, -0.0912714, 0.330804, 0.253512, 0.879982, -0.0980824, 0.330766, 0.274137, 0.88097, -0.105823, 0.330864, 0.295209, 0.882051, -0.113671, 0.330896, 0.317226, 0.883397, -0.120303, 0.330545, 0.341068, 0.884987, -0.12667, 0.330068, 0.365613, 0.886789, -0.133118, 0.329418, 0.390807, 0.889311, -0.139024, 0.328683, 0.416494, 0.891995, -0.144971, 0.327729, 0.442618, 0.895106, -0.150747, 0.326521, 0.469131, 0.899527, -0.156283, 0.325229, 0.495921, 0.90504, -0.161707, 0.32378, 0.523162, 0.909875, -0.165661, 0.32122, 0.55092, 0.91561, -0.168755, 0.317942, 0.579928, 0.921225, -0.171193, 0.313983, 0.608539, 0.927308, -0.17319, 0.309636, 0.636854, 0.933077, -0.174819, 0.304262, 0.66523, 0.938766, -0.175002, 0.297563, 0.693609, 0.943667, -0.173946, 0.289613, 0.722157, 0.949033, -0.172221, 0.281227, 0.750021, 0.953765, -0.169869, 0.271545, 0.777466, 0.95804, -0.166578, 0.261034, 0.804853, 0.962302, -0.161761, 0.249434, 0.831569, 0.966544, -0.156636, 0.237484, 0.857779, 0.969372, -0.150784, 0.224395, 0.883051, 0.972486, -0.143672, 0.210786, 0.907864, 0.975853, -0.135772, 0.196556, 0.931223, 0.977975, -0.127942, 0.182307, 0.954061, 0.979122, -0.118347, 0.167607, 0.97531, 0.980719, -0.109112, 0.152739, 0.995666, 0.981223, -0.0991789, 0.137932, 1.01475, 0.98216, -0.0883553, 0.122692, 1.03253, 0.983379, -0.0780825, 0.107493, 1.04917, 0.985434, -0.0665646, 0.0917791, 1.06464, 0.987332, -0.0557714, 0.0764949, 1.07896, 0.990004, -0.0442805, 0.060721, 1.09199, 0.992975, -0.0331676, 0.0452284, 1.10393, 0.995811, -0.0219547, 0.0297934, 1.11476, 0.9982, -0.0107613, 0.0146415, 1.12484, 1.00002, 248678e-9, -14555e-8, 1.13413, 0.859519, -693595e-11, 0.347264, 171673e-10, 0.859843, -17503e-8, 0.347394, 433219e-9, 0.859656, -700076e-9, 0.347319, 173277e-8, 0.859671, -157517e-8, 0.347325, 389875e-8, 0.859669, -280028e-8, 0.347324, 693112e-8, 0.85967, -43754e-7, 0.347324, 0.01083, 0.859665, -630049e-8, 0.347321, 0.0155954, 0.859685, -85755e-7, 0.347328, 0.0212278, 0.859694, -0.0112003, 0.347329, 0.0277273, 0.859718, -0.0141747, 0.347336, 0.0350946, 0.85976, -0.0174988, 0.347348, 0.0433314, 0.85982, -0.0211722, 0.347366, 0.0524384, 0.859892, -0.0251941, 0.347387, 0.0624168, 0.860006, -0.0295649, 0.347422, 0.0732708, 0.860122, -0.0342825, 0.347453, 0.0849999, 0.860282, -0.0393462, 0.347499, 0.0976102, 0.860482, -0.0447513, 0.347554, 0.111104, 0.860719, -0.0504775, 0.347614, 0.125479, 0.860998, -0.0563577, 0.347666, 0.140703, 0.861322, -0.0619473, 0.347662, 0.156681, 0.861724, -0.0681277, 0.347684, 0.173597, 0.862198, -0.0746567, 0.347709, 0.191371, 0.862733, -0.0815234, 0.347727, 0.209976, 0.863371, -0.0886643, 0.347744, 0.229351, 0.86414, -0.0957908, 0.347734, 0.24934, 0.865138, -0.102912, 0.34772, 0.269797, 0.866182, -0.110924, 0.3478, 0.290654, 0.867436, -0.119223, 0.347911, 0.312074, 0.869087, -0.126197, 0.347649, 0.335438, 0.870859, -0.133145, 0.347222, 0.359732, 0.872997, -0.139869, 0.346645, 0.38467, 0.875939, -0.146089, 0.345935, 0.41019, 0.879012, -0.152334, 0.345012, 0.436218, 0.883353, -0.15821, 0.343924, 0.462641, 0.888362, -0.164097, 0.342636, 0.489449, 0.895026, -0.169528, 0.341351, 0.516629, 0.900753, -0.174408, 0.339115, 0.544109, 0.906814, -0.17751, 0.335809, 0.572857, 0.912855, -0.180101, 0.331597, 0.601554, 0.919438, -0.182116, 0.32698, 0.630198, 0.925962, -0.183494, 0.321449, 0.658404, 0.931734, -0.184159, 0.314595, 0.686625, 0.93762, -0.18304, 0.306462, 0.71531, 0.943858, -0.181323, 0.297514, 0.744272, 0.948662, -0.178683, 0.287447, 0.771462, 0.953299, -0.175379, 0.276166, 0.798593, 0.957346, -0.170395, 0.263758, 0.8256, 0.962565, -0.165042, 0.251019, 0.852575, 0.966075, -0.158655, 0.237011, 0.878316, 0.969048, -0.151707, 0.222518, 0.90329, 0.972423, -0.143271, 0.207848, 0.927745, 0.975833, -0.134824, 0.192463, 0.950859, 0.977629, -0.125444, 0.1768, 0.972947, 0.978995, -0.114949, 0.161033, 0.993263, 0.980533, -0.104936, 0.145523, 1.01337, 0.980745, -0.0935577, 0.129799, 1.03128, 0.981814, -0.0822956, 0.113486, 1.04825, 0.983943, -0.0710082, 0.0972925, 1.06405, 0.986141, -0.0587931, 0.0808138, 1.0785, 0.988878, -0.0472755, 0.0644915, 1.09204, 0.992132, -0.0349128, 0.0478128, 1.10413, 0.9953, -0.0232407, 0.031621, 1.11527, 0.998117, -0.0112713, 0.0154935, 1.12551, 1.00003, 339743e-9, -195763e-9, 1.13504, 0.845441, -729126e-11, 0.364305, 169208e-10, 0.843588, -183164e-9, 0.363506, 425067e-9, 0.843412, -73253e-8, 0.36343, 169999e-8, 0.843401, -164818e-8, 0.363426, 382495e-8, 0.843399, -293008e-8, 0.363425, 679993e-8, 0.843401, -457822e-8, 0.363425, 0.010625, 0.843394, -659249e-8, 0.363421, 0.0153002, 0.843398, -897282e-8, 0.363421, 0.0208258, 0.843415, -0.0117191, 0.363426, 0.0272024, 0.843438, -0.0148312, 0.363432, 0.0344305, 0.843483, -0.018309, 0.363447, 0.0425116, 0.84356, -0.0221521, 0.363472, 0.0514471, 0.843646, -0.0263597, 0.363499, 0.061238, 0.843743, -0.0309315, 0.363527, 0.0718873, 0.84388, -0.0358658, 0.363569, 0.0833969, 0.844079, -0.0411624, 0.363631, 0.0957742, 0.844279, -0.0468128, 0.363688, 0.109015, 0.844549, -0.0527923, 0.363761, 0.123124, 0.844858, -0.0588204, 0.363817, 0.138044, 0.84522, -0.0647573, 0.36383, 0.153755, 0.845669, -0.0713181, 0.363879, 0.170394, 0.846155, -0.0781697, 0.363908, 0.187861, 0.846789, -0.0853913, 0.363969, 0.206176, 0.847502, -0.0928086, 0.363999, 0.225244, 0.8484, -0.10005, 0.363997, 0.244926, 0.849461, -0.107615, 0.364008, 0.265188, 0.850562, -0.115814, 0.364055, 0.28587, 0.851962, -0.124334, 0.364179, 0.306926, 0.854326, -0.131995, 0.364233, 0.329605, 0.856295, -0.139338, 0.363856, 0.35359, 0.858857, -0.146346, 0.363347, 0.37831, 0.862428, -0.152994, 0.362807, 0.403722, 0.866203, -0.159463, 0.361963, 0.429537, 0.871629, -0.165623, 0.36112, 0.456, 0.877365, -0.171649, 0.359917, 0.482773, 0.883744, -0.177151, 0.35848, 0.509705, 0.890693, -0.182381, 0.356523, 0.537215, 0.897278, -0.186076, 0.3533, 0.565493, 0.903958, -0.188602, 0.349095, 0.594293, 0.910908, -0.190755, 0.344215, 0.623165, 0.918117, -0.192063, 0.338606, 0.651573, 0.924644, -0.192758, 0.331544, 0.679869, 0.931054, -0.192238, 0.323163, 0.708668, 0.937303, -0.190035, 0.313529, 0.737201, 0.943387, -0.187162, 0.303152, 0.764977, 0.948494, -0.183876, 0.29146, 0.792683, 0.952546, -0.178901, 0.277917, 0.819228, 0.958077, -0.173173, 0.264753, 0.846559, 0.962462, -0.16645, 0.25002, 0.872962, 0.966569, -0.159452, 0.234873, 0.898729, 0.969108, -0.15074, 0.218752, 0.923126, 0.973072, -0.141523, 0.202673, 0.947278, 0.975452, -0.132075, 0.186326, 0.969938, 0.977784, -0.121257, 0.169396, 0.991325, 0.97899, -0.110182, 0.153044, 1.01123, 0.979777, -0.0989634, 0.136485, 1.0299, 0.980865, -0.0865894, 0.119343, 1.04727, 0.982432, -0.0746115, 0.102452, 1.06341, 0.984935, -0.0621822, 0.0852423, 1.07834, 0.987776, -0.0495694, 0.0678546, 1.092, 0.99103, -0.0372386, 0.0506917, 1.1043, 0.99474, -0.0244353, 0.0333316, 1.11576, 0.997768, -0.0121448, 0.0164348, 1.12617, 1.00003, 31774e-8, -169504e-9, 1.13598, 0.825551, -756799e-11, 0.378425, 165099e-10, 0.82664, -190922e-9, 0.378923, 416504e-9, 0.826323, -763495e-9, 0.378779, 16656e-7, 0.826359, -171789e-8, 0.378795, 374768e-8, 0.82636, -305402e-8, 0.378795, 666259e-8, 0.826368, -477185e-8, 0.378798, 0.0104104, 0.826364, -687131e-8, 0.378795, 0.0149912, 0.826368, -935232e-8, 0.378795, 0.0204054, 0.826376, -0.0122146, 0.378797, 0.0266532, 0.826399, -0.0154581, 0.378803, 0.0337355, 0.82646, -0.0190825, 0.378824, 0.0416537, 0.826525, -0.0230873, 0.378846, 0.0504091, 0.826614, -0.0274719, 0.378876, 0.0600032, 0.82674, -0.0322355, 0.378917, 0.0704393, 0.826888, -0.0373766, 0.378964, 0.0817195, 0.827078, -0.0428936, 0.379024, 0.0938492, 0.827318, -0.0487778, 0.379099, 0.106828, 0.82764, -0.0549935, 0.379199, 0.120659, 0.827926, -0.0611058, 0.379227, 0.13526, 0.828325, -0.0675054, 0.379275, 0.150713, 0.828801, -0.0743455, 0.379332, 0.167034, 0.8294, -0.0815523, 0.379415, 0.184209, 0.830094, -0.0890779, 0.379495, 0.202203, 0.8309, -0.096736, 0.379555, 0.220945, 0.831943, -0.104135, 0.379577, 0.240306, 0.833037, -0.112106, 0.379604, 0.260317, 0.834278, -0.120554, 0.379668, 0.2808, 0.836192, -0.129128, 0.3799, 0.301654, 0.838671, -0.137541, 0.380109, 0.323502, 0.840939, -0.14523, 0.379809, 0.347176, 0.844575, -0.15248, 0.379593, 0.371706, 0.848379, -0.159607, 0.37909, 0.39688, 0.853616, -0.166267, 0.378617, 0.422702, 0.858921, -0.172698, 0.377746, 0.448919, 0.865324, -0.178823, 0.376749, 0.475661, 0.872207, -0.184542, 0.375363, 0.502599, 0.880018, -0.189836, 0.373657, 0.529914, 0.88694, -0.194294, 0.370673, 0.557683, 0.894779, -0.197022, 0.36662, 0.586848, 0.902242, -0.199108, 0.36138, 0.615831, 0.909914, -0.200398, 0.355434, 0.644478, 0.917088, -0.20094, 0.348173, 0.672905, 0.923888, -0.200671, 0.339482, 0.701327, 0.930495, -0.198773, 0.32956, 0.730101, 0.937247, -0.195394, 0.318363, 0.758383, 0.943108, -0.191956, 0.306323, 0.786539, 0.948296, -0.187227, 0.292576, 0.813637, 0.953472, -0.181165, 0.278234, 0.840793, 0.958485, -0.174119, 0.263054, 0.867712, 0.962714, -0.166564, 0.246756, 0.893635, 0.966185, -0.158181, 0.229945, 0.919028, 0.970146, -0.148275, 0.212633, 0.943413, 0.973491, -0.138157, 0.195229, 0.966627, 0.975741, -0.127574, 0.178048, 0.988817, 0.977238, -0.11554, 0.160312, 1.00924, 0.978411, -0.10364, 0.142857, 1.02845, 0.979811, -0.0913122, 0.125317, 1.04648, 0.98116, -0.0782558, 0.107627, 1.06284, 0.983543, -0.0655957, 0.0895862, 1.07798, 0.986789, -0.0520411, 0.0713756, 1.092, 0.990292, -0.0389727, 0.053228, 1.10484, 0.994187, -0.025808, 0.0351945, 1.11642, 0.997499, -0.0126071, 0.0173198, 1.12703, 0.999999, 275604e-9, -148602e-9, 1.13674, 0.81075, -78735e-10, 0.394456, 161829e-10, 0.808692, -198293e-9, 0.393453, 407564e-9, 0.80846, -792877e-9, 0.39334, 162965e-8, 0.808595, -178416e-8, 0.393407, 366711e-8, 0.808597, -317182e-8, 0.393408, 651934e-8, 0.808598, -495589e-8, 0.393408, 0.0101866, 0.808591, -713627e-8, 0.393403, 0.0146689, 0.808592, -971285e-8, 0.393402, 0.0199667, 0.80861, -0.0126855, 0.393407, 0.0260803, 0.808633, -0.0160538, 0.393413, 0.0330107, 0.80868, -0.0198175, 0.393429, 0.0407589, 0.808748, -0.0239758, 0.393453, 0.0493264, 0.808854, -0.0285286, 0.39349, 0.0587161, 0.808992, -0.0334748, 0.39354, 0.0689304, 0.809141, -0.0388116, 0.393588, 0.0799707, 0.809352, -0.0445375, 0.39366, 0.0918432, 0.809608, -0.0506427, 0.393742, 0.104549, 0.809915, -0.0570708, 0.393834, 0.118085, 0.810253, -0.0633526, 0.393885, 0.132377, 0.810687, -0.0700966, 0.393953, 0.147537, 0.811233, -0.0772274, 0.394047, 0.163543, 0.811865, -0.0847629, 0.394148, 0.180394, 0.812648, -0.0925663, 0.394265, 0.198051, 0.813583, -0.100416, 0.394363, 0.216443, 0.814683, -0.108119, 0.394402, 0.235502, 0.815948, -0.11644, 0.394489, 0.255242, 0.817278, -0.125036, 0.394542, 0.275441, 0.819605, -0.133655, 0.39486, 0.296094, 0.822256, -0.142682, 0.395248, 0.317309, 0.825349, -0.150756, 0.395241, 0.340516, 0.829605, -0.158392, 0.395285, 0.364819, 0.83391, -0.165801, 0.394922, 0.389736, 0.839808, -0.172677, 0.394691, 0.415409, 0.845708, -0.179448, 0.394006, 0.441546, 0.853025, -0.185746, 0.393279, 0.46832, 0.859666, -0.191684, 0.391655, 0.495302, 0.86789, -0.197146, 0.390068, 0.52262, 0.875845, -0.201904, 0.38727, 0.550336, 0.882634, -0.205023, 0.382688, 0.578825, 0.891076, -0.207098, 0.377543, 0.608103, 0.900589, -0.208474, 0.371752, 0.63723, 0.90791, -0.209068, 0.364016, 0.665769, 0.915971, -0.208655, 0.355593, 0.694428, 0.923455, -0.20729, 0.345439, 0.723224, 0.931514, -0.203821, 0.334099, 0.751925, 0.937885, -0.19986, 0.321069, 0.780249, 0.943136, -0.194993, 0.306571, 0.8077, 0.948818, -0.189132, 0.291556, 0.83497, 0.954433, -0.181617, 0.275745, 0.86188, 0.959078, -0.173595, 0.258695, 0.888562, 0.962705, -0.164855, 0.240825, 0.914008, 0.966753, -0.155129, 0.22268, 0.939145, 0.970704, -0.144241, 0.204542, 0.963393, 0.973367, -0.133188, 0.185927, 0.985983, 0.975984, -0.121146, 0.167743, 1.00704, 0.976994, -0.108366, 0.149218, 1.02715, 0.978485, -0.0956746, 0.13131, 1.0455, 0.980074, -0.0820733, 0.112513, 1.06221, 0.98225, -0.0684061, 0.0938323, 1.07782, 0.98553, -0.0549503, 0.0749508, 1.09199, 0.989529, -0.0407857, 0.055848, 1.10508, 0.993536, -0.0271978, 0.0368581, 1.11684, 0.997247, -0.0132716, 0.0181845, 1.12789, 1, 431817e-9, -198809e-9, 1.13792, 0.785886, -812608e-11, 0.405036, 157669e-10, 0.790388, -205278e-9, 0.407355, 398297e-9, 0.790145, -820824e-9, 0.407231, 159263e-8, 0.790135, -184681e-8, 0.407226, 358336e-8, 0.790119, -328316e-8, 0.407218, 637039e-8, 0.790126, -512988e-8, 0.40722, 99539e-7, 0.79013, -738684e-8, 0.407221, 0.0143339, 0.790135, -0.0100538, 0.407221, 0.0195107, 0.790134, -0.0131306, 0.407217, 0.0254848, 0.79016, -0.0166169, 0.407224, 0.0322572, 0.790197, -0.020512, 0.407236, 0.0398284, 0.790273, -0.0248157, 0.407263, 0.0482014, 0.790381, -0.029527, 0.407304, 0.0573777, 0.790521, -0.0346446, 0.407355, 0.0673602, 0.790704, -0.0401665, 0.40742, 0.0781522, 0.790925, -0.0460896, 0.407499, 0.0897582, 0.791195, -0.0524017, 0.407589, 0.10218, 0.791522, -0.0590121, 0.407691, 0.11541, 0.791878, -0.0654876, 0.407748, 0.12939, 0.792361, -0.0725207, 0.407849, 0.144237, 0.792942, -0.0799844, 0.407963, 0.159924, 0.79362, -0.0877896, 0.408087, 0.176425, 0.794529, -0.0958451, 0.408259, 0.193733, 0.795521, -0.103827, 0.408362, 0.211756, 0.796778, -0.111937, 0.408482, 0.230524, 0.798027, -0.120521, 0.408547, 0.249967, 0.799813, -0.129242, 0.408721, 0.269926, 0.802387, -0.138048, 0.409148, 0.290338, 0.805279, -0.147301, 0.409641, 0.311193, 0.809251, -0.155895, 0.410154, 0.333611, 0.813733, -0.163942, 0.410297, 0.357615, 0.819081, -0.171666, 0.410373, 0.382339, 0.825427, -0.178905, 0.410348, 0.407828, 0.83172, -0.185812, 0.409486, 0.434034, 0.83877, -0.192318, 0.408776, 0.460493, 0.845817, -0.198249, 0.407176, 0.487346, 0.854664, -0.204034, 0.405719, 0.514832, 0.863495, -0.208908, 0.403282, 0.542401, 0.871883, -0.212765, 0.399293, 0.570683, 0.88065, -0.214911, 0.393803, 0.599947, 0.89004, -0.216214, 0.387536, 0.62932, 0.898476, -0.216745, 0.379846, 0.658319, 0.906738, -0.216387, 0.370625, 0.687138, 0.914844, -0.215053, 0.360139, 0.71601, 0.923877, -0.212007, 0.348849, 0.745124, 0.931925, -0.207481, 0.335639, 0.773366, 0.938054, -0.202418, 0.320798, 0.801636, 0.943895, -0.196507, 0.304772, 0.829055, 0.949468, -0.189009, 0.288033, 0.856097, 0.955152, -0.180539, 0.270532, 0.88301, 0.959403, -0.171437, 0.251639, 0.909296, 0.963309, -0.161661, 0.232563, 0.934868, 0.967399, -0.150425, 0.213231, 0.959662, 0.972009, -0.138659, 0.194247, 0.98302, 0.97433, -0.126595, 0.174718, 1.00517, 0.975823, -0.113205, 0.155518, 1.02566, 0.976371, -0.0996096, 0.136709, 1.04418, 0.978705, -0.0860754, 0.117571, 1.06146, 0.981477, -0.0714438, 0.0980046, 1.07777, 0.984263, -0.0572304, 0.0782181, 1.09214, 0.988423, -0.0428875, 0.0584052, 1.10553, 0.993, -0.0282442, 0.038522, 1.11758, 0.99704, -0.0140183, 0.0190148, 1.12864, 0.999913, 369494e-9, -145203e-9, 1.13901, 0.777662, -84153e-10, 0.423844, 154403e-10, 0.770458, -211714e-9, 0.419915, 38845e-8, 0.770716, -846888e-9, 0.420055, 155386e-8, 0.770982, -190567e-8, 0.420202, 349653e-8, 0.770981, -338782e-8, 0.420201, 621606e-8, 0.77098, -529338e-8, 0.4202, 971274e-8, 0.770983, -762223e-8, 0.4202, 0.0139867, 0.770985, -0.0103741, 0.420198, 0.0190381, 0.770996, -0.0135489, 0.4202, 0.0248677, 0.771029, -0.0171461, 0.420212, 0.0314764, 0.771052, -0.0211647, 0.420215, 0.0388648, 0.771131, -0.0256048, 0.420245, 0.047036, 0.771235, -0.0304647, 0.420284, 0.0559911, 0.771383, -0.0357436, 0.420341, 0.0657346, 0.771591, -0.0414392, 0.420423, 0.0762694, 0.771819, -0.0475462, 0.420506, 0.0875984, 0.772123, -0.0540506, 0.420617, 0.099727, 0.772464, -0.060797, 0.42072, 0.112637, 0.772855, -0.0675393, 0.420799, 0.126313, 0.773317, -0.0748323, 0.420893, 0.140824, 0.773981, -0.0825681, 0.421058, 0.15617, 0.774746, -0.0906307, 0.421226, 0.172322, 0.77566, -0.0988982, 0.421397, 0.189253, 0.776837, -0.106994, 0.421569, 0.206912, 0.778097, -0.115528, 0.421704, 0.225359, 0.779588, -0.124317, 0.421849, 0.24447, 0.781574, -0.133139, 0.422097, 0.264156, 0.784451, -0.142179, 0.422615, 0.284318, 0.787682, -0.15165, 0.423269, 0.304902, 0.792433, -0.160771, 0.424396, 0.3265, 0.797359, -0.169166, 0.424772, 0.35014, 0.803986, -0.177149, 0.425475, 0.374768, 0.809504, -0.184745, 0.424996, 0.399928, 0.815885, -0.19173, 0.424247, 0.425796, 0.823513, -0.198525, 0.423515, 0.452287, 0.832549, -0.204709, 0.422787, 0.479321, 0.841653, -0.210447, 0.421187, 0.506718, 0.850401, -0.215501, 0.418519, 0.53432, 0.859854, -0.219752, 0.414715, 0.56242, 0.869364, -0.222305, 0.409462, 0.591558, 0.878837, -0.223744, 0.402926, 0.621074, 0.888636, -0.224065, 0.395043, 0.650538, 0.898132, -0.223742, 0.38564, 0.679538, 0.907181, -0.222308, 0.375378, 0.708674, 0.915621, -0.219837, 0.363212, 0.737714, 0.9239, -0.215233, 0.349313, 0.767014, 0.931644, -0.209592, 0.334162, 0.795133, 0.938887, -0.203644, 0.317943, 0.823228, 0.945282, -0.196349, 0.300581, 0.850822, 0.950758, -0.18742, 0.282195, 0.877594, 0.956146, -0.177879, 0.262481, 0.904564, 0.960355, -0.167643, 0.242487, 0.930741, 0.965256, -0.156671, 0.222668, 0.955868, 0.968029, -0.144123, 0.201907, 0.979869, 0.97251, -0.131305, 0.18202, 1.00291, 0.974925, -0.118335, 0.161909, 1.02392, 0.975402, -0.103714, 0.142129, 1.0433, 0.976987, -0.089415, 0.122447, 1.06089, 0.979677, -0.0748858, 0.102248, 1.07713, 0.983184, -0.0596086, 0.0814851, 1.09218, 0.987466, -0.0447671, 0.0609484, 1.10585, 0.992348, -0.0295217, 0.0401835, 1.11829, 0.996674, -0.0143917, 0.0198163, 1.12966, 1.00003, 321364e-9, -149983e-9, 1.1402, 0.757901, -869074e-11, 0.436176, 151011e-10, 0.751195, -217848e-9, 0.432317, 378533e-9, 0.751178, -871373e-9, 0.432307, 15141e-7, 0.751195, -196061e-8, 0.432317, 34068e-7, 0.751198, -348552e-8, 0.432318, 605659e-8, 0.751195, -544599e-8, 0.432315, 946353e-8, 0.751207, -784203e-8, 0.43232, 0.013628, 0.751213, -0.0106732, 0.43232, 0.0185499, 0.751221, -0.0139393, 0.432319, 0.0242302, 0.751244, -0.0176398, 0.432325, 0.0306694, 0.7513, -0.0217743, 0.432348, 0.0378698, 0.751358, -0.0263412, 0.432367, 0.0458321, 0.751458, -0.0313396, 0.432404, 0.0545587, 0.751608, -0.0367682, 0.432464, 0.0640543, 0.7518, -0.0426246, 0.43254, 0.0743222, 0.752065, -0.0489031, 0.432645, 0.0853668, 0.752376, -0.0555828, 0.432762, 0.0971911, 0.752715, -0.0623861, 0.432859, 0.109768, 0.753137, -0.069415, 0.432958, 0.123126, 0.753676, -0.0770039, 0.433099, 0.137308, 0.754345, -0.084971, 0.433272, 0.15229, 0.755235, -0.0932681, 0.433504, 0.168075, 0.756186, -0.10171, 0.433693, 0.184625, 0.757363, -0.110019, 0.433857, 0.201897, 0.75884, -0.11887, 0.434102, 0.220014, 0.760467, -0.127881, 0.434306, 0.238778, 0.762969, -0.136766, 0.434751, 0.258172, 0.765823, -0.14612, 0.43529, 0.278062, 0.769676, -0.15566, 0.436236, 0.298437, 0.774909, -0.165177, 0.437754, 0.319532, 0.77994, -0.17402, 0.438343, 0.342505, 0.785757, -0.182201, 0.438609, 0.366693, 0.792487, -0.190104, 0.438762, 0.391668, 0.80038, -0.197438, 0.438795, 0.417494, 0.808494, -0.204365, 0.438226, 0.443933, 0.817695, -0.210714, 0.437283, 0.470929, 0.828111, -0.216651, 0.436087, 0.498569, 0.837901, -0.221804, 0.433717, 0.526165, 0.847813, -0.226318, 0.430133, 0.554155, 0.858314, -0.229297, 0.425213, 0.582822, 0.868891, -0.230999, 0.418576, 0.612847, 0.878941, -0.231155, 0.410405, 0.642445, 0.888809, -0.230935, 0.400544, 0.672024, 0.898089, -0.229343, 0.389613, 0.701366, 0.908081, -0.226886, 0.377197, 0.730763, 0.916819, -0.222676, 0.363397, 0.759642, 0.924968, -0.216835, 0.347437, 0.788775, 0.932906, -0.210245, 0.32995, 0.817135, 0.940025, -0.202992, 0.312262, 0.844912, 0.946101, -0.19436, 0.293313, 0.872164, 0.952835, -0.184125, 0.273638, 0.899443, 0.957347, -0.173657, 0.252385, 0.926389, 0.961434, -0.162204, 0.231038, 0.951947, 0.965522, -0.14979, 0.209834, 0.976751, 0.969412, -0.136307, 0.188821, 1.00022, 0.973902, -0.122527, 0.168013, 1.02229, 0.974045, -0.108213, 0.147634, 1.04199, 0.975775, -0.0927397, 0.12705, 1.06019, 0.978383, -0.0778212, 0.106309, 1.07711, 0.98211, -0.0621216, 0.0849279, 1.09245, 0.986517, -0.0463847, 0.0633519, 1.10651, 0.991696, -0.0309353, 0.0419698, 1.11903, 0.996349, -0.0150914, 0.0206272, 1.13073, 1.00003, 442449e-9, -231396e-9, 1.14146, 0.727498, -885074e-11, 0.441528, 145832e-10, 0.730897, -223525e-9, 0.443589, 368298e-9, 0.730796, -893996e-9, 0.443528, 147303e-8, 0.730805, -201149e-8, 0.443533, 331433e-8, 0.730814, -357596e-8, 0.443538, 589222e-8, 0.730815, -558734e-8, 0.443538, 920678e-8, 0.730822, -804544e-8, 0.44354, 0.0132582, 0.730836, -0.0109501, 0.443545, 0.0180468, 0.730848, -0.0143008, 0.443546, 0.0235732, 0.730871, -0.0180969, 0.443552, 0.0298382, 0.730915, -0.022338, 0.443567, 0.0368438, 0.730982, -0.0270225, 0.443591, 0.044591, 0.731076, -0.0321491, 0.443627, 0.0530831, 0.731245, -0.0377166, 0.443699, 0.0623243, 0.73144, -0.0437216, 0.443777, 0.0723181, 0.7317, -0.0501576, 0.443881, 0.0830691, 0.732034, -0.0569942, 0.444014, 0.0945809, 0.732388, -0.0638756, 0.444113, 0.106825, 0.732853, -0.071203, 0.444247, 0.119859, 0.733473, -0.0790076, 0.444442, 0.13369, 0.734195, -0.0871937, 0.444645, 0.148304, 0.735069, -0.095696, 0.444877, 0.163702, 0.736169, -0.10426, 0.445133, 0.179861, 0.73747, -0.112853, 0.44537, 0.196778, 0.738991, -0.12199, 0.445651, 0.214496, 0.740865, -0.131153, 0.445958, 0.232913, 0.743637, -0.140245, 0.446548, 0.251977, 0.746797, -0.149722, 0.447246, 0.271551, 0.751517, -0.159341, 0.448656, 0.291774, 0.756156, -0.169106, 0.449866, 0.312455, 0.761519, -0.178436, 0.450919, 0.334552, 0.768295, -0.186904, 0.451776, 0.358491, 0.776613, -0.195117, 0.452832, 0.383446, 0.783966, -0.202695, 0.45249, 0.408945, 0.793542, -0.20985, 0.452587, 0.435364, 0.803192, -0.216403, 0.451852, 0.462336, 0.813892, -0.22251, 0.450708, 0.48987, 0.824968, -0.227676, 0.4486, 0.517697, 0.835859, -0.232443, 0.445156, 0.545975, 0.846825, -0.235775, 0.440351, 0.574483, 0.858085, -0.237897, 0.433641, 0.604246, 0.868825, -0.238074, 0.425354, 0.634101, 0.879638, -0.237661, 0.415383, 0.664201, 0.889966, -0.236186, 0.404136, 0.693918, 0.899479, -0.233599, 0.390917, 0.723481, 0.908769, -0.229737, 0.376352, 0.75258, 0.917966, -0.223836, 0.360372, 0.781764, 0.926304, -0.217067, 0.342551, 0.811139, 0.934626, -0.209309, 0.324238, 0.839585, 0.941841, -0.20071, 0.304484, 0.867044, 0.94789, -0.190602, 0.283607, 0.894579, 0.954196, -0.179253, 0.262205, 0.921743, 0.958383, -0.167646, 0.239847, 0.948026, 0.963119, -0.155073, 0.218078, 0.973296, 0.966941, -0.141426, 0.195899, 0.998135, 0.970836, -0.126849, 0.174121, 1.02021, 0.973301, -0.112296, 0.153052, 1.04085, 0.97448, -0.0964965, 0.131733, 1.05946, 0.977045, -0.080489, 0.10997, 1.07693, 0.980751, -0.064844, 0.0881657, 1.09254, 0.985475, -0.0481938, 0.0657987, 1.10697, 0.991089, -0.0319185, 0.0435215, 1.12004, 0.996122, -0.0158088, 0.0214779, 1.13173, 1.00001, 372455e-9, -200295e-9, 1.14291, 0.708622, -907597e-11, 0.45304, 141962e-10, 0.711162, -228911e-9, 0.454662, 358052e-9, 0.709812, -914446e-9, 0.453797, 143034e-8, 0.709865, -205819e-8, 0.453834, 321935e-8, 0.709864, -365894e-8, 0.453833, 572331e-8, 0.709855, -571692e-8, 0.453826, 894278e-8, 0.709862, -823201e-8, 0.453828, 0.012878, 0.709875, -0.011204, 0.453832, 0.0175295, 0.709896, -0.0146323, 0.453839, 0.0228978, 0.709925, -0.0185163, 0.453847, 0.0289839, 0.709974, -0.0228551, 0.453866, 0.0357894, 0.710045, -0.0276473, 0.453892, 0.0433161, 0.710133, -0.032891, 0.453924, 0.0515665, 0.710292, -0.0385851, 0.453992, 0.0605458, 0.710485, -0.0447254, 0.45407, 0.0702574, 0.710769, -0.0513051, 0.454192, 0.0807077, 0.711106, -0.0582733, 0.454329, 0.091896, 0.711516, -0.0652866, 0.45446, 0.103814, 0.712071, -0.0728426, 0.454653, 0.116508, 0.712676, -0.0808307, 0.45484, 0.129968, 0.713476, -0.0892216, 0.455096, 0.144206, 0.714377, -0.0979047, 0.455346, 0.159212, 0.715579, -0.106531, 0.455647, 0.174973, 0.716977, -0.115492, 0.455961, 0.191504, 0.71862, -0.124821, 0.456315, 0.208835, 0.72084, -0.134079, 0.4568, 0.226869, 0.723786, -0.143427, 0.457521, 0.245582, 0.727464, -0.153061, 0.458475, 0.264957, 0.732771, -0.162768, 0.460239, 0.284948, 0.736515, -0.172627, 0.460899, 0.30522, 0.743519, -0.182487, 0.463225, 0.326717, 0.750041, -0.191295, 0.464027, 0.350113, 0.758589, -0.199746, 0.465227, 0.374782, 0.767703, -0.207584, 0.465877, 0.400226, 0.777484, -0.214973, 0.465996, 0.426442, 0.788792, -0.221796, 0.466019, 0.453688, 0.800194, -0.228038, 0.465083, 0.481246, 0.811234, -0.233346, 0.462506, 0.509086, 0.822859, -0.238073, 0.459257, 0.537338, 0.835082, -0.241764, 0.454863, 0.566108, 0.846332, -0.244241, 0.448163, 0.595126, 0.858355, -0.244736, 0.439709, 0.625574, 0.87034, -0.244278, 0.429837, 0.65617, 0.881027, -0.24255, 0.418002, 0.686029, 0.891007, -0.239912, 0.404325, 0.716039, 0.900874, -0.236133, 0.389222, 0.745518, 0.911072, -0.230672, 0.373269, 0.775026, 0.920359, -0.22356, 0.355083, 0.804521, 0.928604, -0.215591, 0.335533, 0.834045, 0.937175, -0.206503, 0.315278, 0.861612, 0.942825, -0.196684, 0.293653, 0.889131, 0.949805, -0.185116, 0.271503, 0.916853, 0.955535, -0.172703, 0.248821, 0.943541, 0.959843, -0.159978, 0.225591, 0.970132, 0.964393, -0.146375, 0.202719, 0.994709, 0.968008, -0.131269, 0.179928, 1.0186, 0.971013, -0.11569, 0.158007, 1.03928, 0.973334, -0.1003, 0.13624, 1.05887, 0.975775, -0.0833352, 0.1138, 1.07652, 0.979579, -0.0668981, 0.0913141, 1.09297, 0.984323, -0.0500902, 0.0683051, 1.10734, 0.990351, -0.0332377, 0.0451771, 1.12084, 0.995823, -0.0161491, 0.0221705, 1.13296, 1.0001, 234083e-9, -108712e-9, 1.14441, 0.683895, -924677e-11, 0.46015, 137429e-10, 0.68833, -233383e-9, 0.463134, 346865e-9, 0.688368, -933547e-9, 0.463159, 138748e-8, 0.688367, -210049e-8, 0.463159, 312187e-8, 0.688369, -373415e-8, 0.463159, 555004e-8, 0.688377, -583449e-8, 0.463163, 867216e-8, 0.688386, -840128e-8, 0.463166, 0.0124884, 0.688398, -0.0114343, 0.463169, 0.0169993, 0.688418, -0.0149329, 0.463175, 0.0222054, 0.688453, -0.0188964, 0.463188, 0.028108, 0.688515, -0.0233239, 0.463214, 0.0347085, 0.68857, -0.0282136, 0.463231, 0.0420091, 0.688679, -0.033564, 0.463276, 0.0500132, 0.688854, -0.0393733, 0.463356, 0.0587255, 0.689038, -0.0456354, 0.46343, 0.0681476, 0.689321, -0.0523433, 0.463553, 0.0782897, 0.689662, -0.059412, 0.463693, 0.0891501, 0.690188, -0.0665736, 0.4639, 0.100735, 0.690755, -0.0743106, 0.464107, 0.113074, 0.691405, -0.0824722, 0.464329, 0.126161, 0.692198, -0.0910484, 0.464585, 0.140007, 0.693196, -0.0998778, 0.464893, 0.154612, 0.69454, -0.108651, 0.465285, 0.169984, 0.695921, -0.117855, 0.465596, 0.186106, 0.697749, -0.12734, 0.466056, 0.203034, 0.700375, -0.136714, 0.466771, 0.220703, 0.703395, -0.146386, 0.467579, 0.239062, 0.707904, -0.156096, 0.469067, 0.258188, 0.711673, -0.165904, 0.469851, 0.277759, 0.717489, -0.175812, 0.471815, 0.297935, 0.724051, -0.185931, 0.47389, 0.318916, 0.731965, -0.195238, 0.47587, 0.341591, 0.741151, -0.204021, 0.477523, 0.366062, 0.751416, -0.212113, 0.478881, 0.391396, 0.761848, -0.21979, 0.479226, 0.417599, 0.771886, -0.2267, 0.478495, 0.444401, 0.783998, -0.232991, 0.477622, 0.472084, 0.796523, -0.238645, 0.475833, 0.500193, 0.808851, -0.243396, 0.472568, 0.52865, 0.821191, -0.247226, 0.467857, 0.557362, 0.834261, -0.250102, 0.461871, 0.586768, 0.846762, -0.251056, 0.453543, 0.617085, 0.859867, -0.250604, 0.443494, 0.647659, 0.871948, -0.248783, 0.431711, 0.678119, 0.882967, -0.245855, 0.417911, 0.708399, 0.892826, -0.242168, 0.401993, 0.738256, 0.90332, -0.237062, 0.385371, 0.767999, 0.913633, -0.22997, 0.366837, 0.798191, 0.922774, -0.221687, 0.346372, 0.827756, 0.931371, -0.212345, 0.325682, 0.856425, 0.938929, -0.20206, 0.303665, 0.884299, 0.944821, -0.190981, 0.280786, 0.912023, 0.951792, -0.178065, 0.2573, 0.939669, 0.957712, -0.164634, 0.233448, 0.96655, 0.961912, -0.150863, 0.209504, 0.992366, 0.966382, -0.13577, 0.18597, 1.01633, 0.969588, -0.119593, 0.162905, 1.03843, 0.971777, -0.103203, 0.14053, 1.05841, 0.97433, -0.0865888, 0.117909, 1.07632, 0.978686, -0.0690829, 0.0944101, 1.09326, 0.983281, -0.0516568, 0.0705671, 1.10796, 0.989562, -0.034558, 0.0468592, 1.12182, 0.995465, -0.0167808, 0.0229846, 1.1342, 0.999991, 373016e-9, -235606e-9, 1.1459, 0.662251, -939016e-11, 0.468575, 132714e-10, 0.666634, -237624e-9, 0.471675, 335842e-9, 0.666411, -950385e-9, 0.471516, 134321e-8, 0.666399, -213833e-8, 0.471509, 302221e-8, 0.666386, -38014e-7, 0.471499, 537283e-8, 0.666405, -593958e-8, 0.471511, 839533e-8, 0.666406, -855253e-8, 0.471508, 0.0120898, 0.666428, -0.0116401, 0.471519, 0.0164569, 0.666444, -0.0152015, 0.471522, 0.0214971, 0.66649, -0.0192362, 0.471543, 0.027212, 0.666537, -0.0237428, 0.471558, 0.033603, 0.666617, -0.0287198, 0.471591, 0.0406728, 0.666718, -0.0341647, 0.471631, 0.0484238, 0.666889, -0.0400759, 0.47171, 0.0568621, 0.667104, -0.0464479, 0.471805, 0.0659915, 0.667374, -0.0532677, 0.471923, 0.0758178, 0.667772, -0.0603805, 0.472098, 0.0863425, 0.668371, -0.0677392, 0.472363, 0.0975917, 0.668971, -0.0756028, 0.472596, 0.109567, 0.669696, -0.0839293, 0.472869, 0.122272, 0.670481, -0.0926683, 0.473126, 0.135718, 0.6715, -0.1016, 0.473442, 0.149914, 0.672911, -0.110566, 0.47389, 0.164882, 0.674512, -0.119984, 0.474354, 0.180602, 0.67651, -0.129574, 0.474922, 0.19711, 0.679292, -0.139106, 0.475764, 0.214371, 0.682798, -0.148993, 0.476886, 0.232405, 0.686955, -0.158737, 0.478179, 0.251153, 0.691406, -0.168754, 0.479432, 0.270436, 0.697438, -0.178703, 0.481481, 0.290374, 0.704761, -0.188955, 0.484143, 0.311044, 0.713599, -0.198814, 0.487007, 0.333003, 0.723194, -0.207869, 0.488962, 0.357144, 0.732601, -0.216189, 0.489815, 0.382169, 0.744193, -0.22398, 0.490888, 0.408227, 0.754907, -0.231156, 0.490355, 0.434928, 0.767403, -0.23747, 0.489548, 0.462599, 0.78107, -0.243503, 0.488274, 0.490908, 0.793893, -0.248114, 0.484843, 0.519421, 0.807296, -0.25222, 0.4803, 0.548561, 0.820529, -0.255265, 0.474097, 0.577772, 0.833716, -0.256741, 0.466041, 0.607782, 0.848403, -0.25637, 0.456547, 0.638807, 0.860755, -0.254804, 0.443946, 0.670058, 0.874012, -0.251834, 0.430852, 0.700749, 0.885619, -0.247867, 0.414903, 0.731446, 0.896069, -0.242634, 0.397276, 0.761191, 0.906266, -0.236093, 0.378535, 0.791053, 0.916759, -0.227543, 0.358038, 0.821298, 0.92523, -0.21783, 0.335705, 0.850747, 0.93436, -0.207534, 0.313797, 0.879258, 0.941631, -0.195983, 0.289671, 0.907734, 0.947564, -0.183567, 0.265319, 0.935206, 0.953681, -0.169345, 0.240815, 0.962739, 0.960008, -0.154909, 0.216119, 0.989227, 0.964145, -0.140161, 0.192096, 1.01465, 0.968171, -0.123411, 0.167855, 1.03737, 0.969859, -0.106525, 0.144817, 1.05767, 0.972666, -0.0891023, 0.12149, 1.0761, 0.977055, -0.0718094, 0.0975306, 1.09336, 0.982527, -0.0534213, 0.0730217, 1.10878, 0.989001, -0.0355579, 0.0483366, 1.12285, 0.99512, -0.0176383, 0.023938, 1.13548, 1.00007, 368831e-9, -211581e-9, 1.14744, 0.651047, -960845e-11, 0.484101, 12922e-9, 0.644145, -241347e-9, 0.478968, 324578e-9, 0.64396, -965142e-9, 0.478831, 129798e-8, 0.64396, -217154e-8, 0.47883, 292046e-8, 0.643968, -386049e-8, 0.478835, 519202e-8, 0.643974, -603186e-8, 0.478838, 81128e-7, 0.643977, -86854e-7, 0.478836, 0.011683, 0.643982, -0.0118207, 0.478834, 0.0159031, 0.644024, -0.0154374, 0.478856, 0.0207743, 0.644059, -0.0195343, 0.478868, 0.0262975, 0.644122, -0.0241103, 0.478896, 0.0324747, 0.644207, -0.0291638, 0.478933, 0.039309, 0.64432, -0.0346919, 0.478981, 0.0468029, 0.644481, -0.0406919, 0.479053, 0.0549614, 0.644722, -0.047159, 0.479169, 0.0637909, 0.645013, -0.0540748, 0.479302, 0.0732974, 0.645503, -0.0612001, 0.479541, 0.0834898, 0.646117, -0.0687303, 0.479829, 0.0943873, 0.646707, -0.0767846, 0.480061, 0.105991, 0.647431, -0.0852465, 0.480343, 0.11831, 0.64831, -0.0940719, 0.48066, 0.131348, 0.649486, -0.103056, 0.481083, 0.14514, 0.650864, -0.112261, 0.481528, 0.159676, 0.652604, -0.121852, 0.482102, 0.174979, 0.654825, -0.131505, 0.482813, 0.191079, 0.657876, -0.141189, 0.483876, 0.207927, 0.661339, -0.151239, 0.48499, 0.225586, 0.665463, -0.161091, 0.486279, 0.243947, 0.670542, -0.171235, 0.487968, 0.262957, 0.677361, -0.181347, 0.49053, 0.282781, 0.685672, -0.191679, 0.493862, 0.303311, 0.694551, -0.201781, 0.49699, 0.324607, 0.703753, -0.211164, 0.498884, 0.347916, 0.713703, -0.219675, 0.500086, 0.372628, 0.725911, -0.227836, 0.501554, 0.398694, 0.73862, -0.23533, 0.502193, 0.425529, 0.752118, -0.241786, 0.501811, 0.453209, 0.76579, -0.247865, 0.500185, 0.481381, 0.779568, -0.252696, 0.497159, 0.51011, 0.793991, -0.256802, 0.492765, 0.539322, 0.808182, -0.259942, 0.486827, 0.569078, 0.821698, -0.261703, 0.478386, 0.598818, 0.836009, -0.262006, 0.468772, 0.629762, 0.849824, -0.260333, 0.456352, 0.661366, 0.863888, -0.257398, 0.442533, 0.69295, 0.876585, -0.253264, 0.426573, 0.723608, 0.888665, -0.248026, 0.408964, 0.754378, 0.899537, -0.241487, 0.389677, 0.784761, 0.9094, -0.233463, 0.368516, 0.814688, 0.920166, -0.223397, 0.346624, 0.845009, 0.928899, -0.21255, 0.322717, 0.874431, 0.937156, -0.200869, 0.298698, 0.902922, 0.943861, -0.188387, 0.273491, 0.931356, 0.949557, -0.174341, 0.247866, 0.958854, 0.955862, -0.158994, 0.222496, 0.986098, 0.961721, -0.143664, 0.197522, 1.01229, 0.965976, -0.127412, 0.17302, 1.03571, 0.968652, -0.109798, 0.148954, 1.05699, 0.971084, -0.0916787, 0.125044, 1.07587, 0.975584, -0.0739634, 0.100577, 1.09372, 0.98122, -0.055322, 0.0753666, 1.10948, 0.988253, -0.0366825, 0.0498899, 1.12394, 0.99482, -0.0180389, 0.024611, 1.13694, 1.00001, 229839e-9, -188283e-9, 1.14919, 0.613867, -964198e-11, 0.479449, 123452e-10, 0.621485, -244534e-9, 0.485399, 313091e-9, 0.621429, -978202e-9, 0.485353, 125245e-8, 0.62112, -220004e-8, 0.485114, 281687e-8, 0.621119, -39111e-7, 0.485112, 500783e-8, 0.621122, -611091e-8, 0.485112, 782498e-8, 0.621133, -879922e-8, 0.485117, 0.0112687, 0.621152, -0.0119756, 0.485125, 0.0153394, 0.621183, -0.0156396, 0.485139, 0.0200382, 0.621227, -0.0197898, 0.485158, 0.0253663, 0.621298, -0.0244253, 0.485192, 0.0313261, 0.621388, -0.0295441, 0.485233, 0.0379204, 0.621507, -0.0351432, 0.485286, 0.0451523, 0.621693, -0.0412198, 0.485378, 0.0530277, 0.621933, -0.0477673, 0.485495, 0.0615522, 0.622232, -0.0547574, 0.485635, 0.0707316, 0.622809, -0.0619417, 0.485943, 0.0805883, 0.623407, -0.069625, 0.486232, 0.0911267, 0.62406, -0.077796, 0.486516, 0.102354, 0.624835, -0.0863731, 0.486838, 0.114279, 0.625758, -0.095251, 0.487188, 0.126902, 0.627043, -0.104299, 0.487695, 0.140285, 0.628438, -0.113724, 0.488163, 0.154397, 0.630325, -0.123417, 0.488858, 0.169267, 0.632801, -0.133137, 0.489754, 0.184941, 0.635784, -0.143052, 0.490815, 0.20136, 0.639406, -0.153132, 0.492048, 0.218643, 0.643872, -0.163143, 0.49363, 0.236615, 0.6499, -0.17333, 0.496009, 0.255449, 0.657201, -0.183622, 0.498994, 0.275006, 0.666221, -0.194019, 0.502888, 0.295354, 0.674419, -0.204192, 0.505459, 0.316244, 0.683729, -0.21406, 0.507771, 0.33849, 0.695584, -0.222854, 0.510245, 0.363166, 0.708583, -0.231315, 0.512293, 0.389071, 0.721233, -0.238911, 0.512747, 0.415737, 0.735134, -0.245657, 0.512482, 0.443331, 0.750179, -0.251879, 0.511526, 0.471891, 0.765073, -0.256911, 0.508935, 0.500892, 0.779794, -0.261144, 0.504341, 0.530294, 0.794801, -0.264316, 0.498515, 0.560144, 0.810339, -0.266276, 0.491015, 0.590213, 0.824818, -0.266981, 0.481126, 0.620865, 0.839375, -0.265778, 0.468685, 0.652687, 0.853043, -0.262748, 0.453925, 0.684759, 0.867335, -0.258474, 0.437912, 0.716209, 0.88037, -0.253187, 0.419648, 0.747508, 0.891711, -0.246476, 0.39982, 0.77797, 0.902896, -0.238735, 0.37879, 0.808586, 0.913601, -0.22885, 0.355891, 0.838843, 0.923019, -0.217656, 0.331773, 0.869014, 0.933432, -0.205539, 0.307356, 0.898512, 0.939691, -0.192595, 0.281321, 0.9269, 0.946938, -0.178945, 0.255441, 0.955297, 0.952372, -0.163587, 0.229013, 0.983231, 0.95909, -0.147214, 0.203179, 1.00971, 0.963675, -0.13064, 0.17792, 1.03438, 0.968247, -0.113121, 0.152898, 1.05625, 0.97001, -0.0945824, 0.128712, 1.07598, 0.974458, -0.0755648, 0.103349, 1.094, 0.980168, -0.0571998, 0.0776731, 1.1104, 0.987295, -0.0377994, 0.0514445, 1.12491, 0.994432, -0.0186417, 0.025429, 1.13851, 0.999975, 542714e-9, -282356e-9, 1.15108, 0.592656, -980249e-11, 0.486018, 119532e-10, 0.598467, -247275e-9, 0.490781, 301531e-9, 0.597934, -988317e-9, 0.490343, 120517e-8, 0.597903, -222366e-8, 0.490319, 27116e-7, 0.597913, -395315e-8, 0.490327, 482077e-8, 0.597919, -617653e-8, 0.490329, 753264e-8, 0.597936, -889375e-8, 0.490339, 0.0108478, 0.597956, -0.0121043, 0.490347, 0.0147668, 0.597992, -0.0158073, 0.490365, 0.0192905, 0.598032, -0.0200017, 0.490382, 0.0244204, 0.598109, -0.0246865, 0.49042, 0.0301593, 0.598215, -0.0298594, 0.490474, 0.03651, 0.59833, -0.0355167, 0.490524, 0.0434757, 0.598525, -0.0416559, 0.490624, 0.0510629, 0.598778, -0.0482692, 0.490753, 0.0592781, 0.599135, -0.0553114, 0.49094, 0.0681304, 0.599802, -0.062542, 0.491328, 0.0776467, 0.600361, -0.0703638, 0.491598, 0.0878184, 0.60101, -0.0786256, 0.491882, 0.0986573, 0.601811, -0.0872962, 0.492232, 0.11018, 0.602861, -0.0962284, 0.492684, 0.1224, 0.604167, -0.10538, 0.493213, 0.135354, 0.605693, -0.114896, 0.493799, 0.149034, 0.607682, -0.124654, 0.494576, 0.163469, 0.610672, -0.13456, 0.4959, 0.178747, 0.613313, -0.144581, 0.496713, 0.194723, 0.617603, -0.154703, 0.498499, 0.211617, 0.622174, -0.16489, 0.500188, 0.229183, 0.628855, -0.175164, 0.503072, 0.247786, 0.636963, -0.185565, 0.506798, 0.267116, 0.644866, -0.195911, 0.509719, 0.28702, 0.653741, -0.206104, 0.512776, 0.307763, 0.664942, -0.216447, 0.516812, 0.329631, 0.67633, -0.22552, 0.519181, 0.353515, 0.690012, -0.234316, 0.521681, 0.379226, 0.704243, -0.242032, 0.523129, 0.405901, 0.719396, -0.249172, 0.523768, 0.433585, 0.734471, -0.255543, 0.522541, 0.462085, 0.750539, -0.260697, 0.520217, 0.491233, 0.766365, -0.26501, 0.516293, 0.521094, 0.781677, -0.268409, 0.509708, 0.551014, 0.797132, -0.270399, 0.501944, 0.581463, 0.812655, -0.271247, 0.492025, 0.612402, 0.828592, -0.270708, 0.480424, 0.643798, 0.844044, -0.268085, 0.465955, 0.67682, 0.857305, -0.263459, 0.448425, 0.708496, 0.87114, -0.258151, 0.430243, 0.74046, 0.884936, -0.251171, 0.410578, 0.771583, 0.895772, -0.243305, 0.38862, 0.802234, 0.906961, -0.234037, 0.365214, 0.833179, 0.917775, -0.222714, 0.34116, 0.86353, 0.927883, -0.210175, 0.31572, 0.893557, 0.936617, -0.196925, 0.289159, 0.922976, 0.943384, -0.182788, 0.261996, 0.951606, 0.949713, -0.167965, 0.235324, 0.979958, 0.955818, -0.151109, 0.208408, 1.00765, 0.961344, -0.133834, 0.182591, 1.03329, 0.965469, -0.115987, 0.156958, 1.0557, 0.968693, -0.09746, 0.132239, 1.07583, 0.973165, -0.0778514, 0.106195, 1.09451, 0.979387, -0.0585067, 0.0797669, 1.11137, 0.98671, -0.0390409, 0.0530263, 1.12643, 0.994093, -0.019408, 0.0263163, 1.14016, 1.00002, 540029e-9, -194487e-9, 1.15299, 0.574483, -989066e-11, 0.494533, 114896e-10, 0.574478, -249127e-9, 0.494528, 289403e-9, 0.574607, -996811e-9, 0.494637, 115797e-8, 0.574396, -224241e-8, 0.494458, 260498e-8, 0.574377, -398632e-8, 0.49444, 463102e-8, 0.574386, -622836e-8, 0.494445, 723623e-8, 0.574401, -89683e-7, 0.494453, 0.010421, 0.574419, -0.0122056, 0.49446, 0.0141859, 0.574459, -0.0159396, 0.494481, 0.0185322, 0.574525, -0.0201692, 0.49452, 0.0234617, 0.574587, -0.0248924, 0.494547, 0.0289762, 0.574697, -0.0301074, 0.494604, 0.0350797, 0.574853, -0.0358114, 0.494688, 0.0417767, 0.575027, -0.041999, 0.494772, 0.0490718, 0.575294, -0.0486618, 0.494915, 0.0569728, 0.575733, -0.0557148, 0.495173, 0.0654955, 0.576356, -0.0630489, 0.495537, 0.0746612, 0.576944, -0.0709285, 0.495836, 0.0844615, 0.57765, -0.0792723, 0.496177, 0.0949142, 0.578491, -0.0880167, 0.496563, 0.10603, 0.579639, -0.0969462, 0.497096, 0.117841, 0.580989, -0.10622, 0.497684, 0.130367, 0.582587, -0.115861, 0.498337, 0.143609, 0.584951, -0.125605, 0.499414, 0.157625, 0.587602, -0.135608, 0.500518, 0.172413, 0.59076, -0.145742, 0.501767, 0.187999, 0.594992, -0.155934, 0.503542, 0.20445, 0.600656, -0.166303, 0.506135, 0.221764, 0.607816, -0.176681, 0.509542, 0.24002, 0.61522, -0.187071, 0.51263, 0.258992, 0.623702, -0.197465, 0.516021, 0.278773, 0.634192, -0.207816, 0.520422, 0.299377, 0.644936, -0.218183, 0.524073, 0.320802, 0.657888, -0.2278, 0.528049, 0.34384, 0.670666, -0.236747, 0.52986, 0.36916, 0.685626, -0.24484, 0.531892, 0.395867, 0.701304, -0.252071, 0.532727, 0.423488, 0.717727, -0.258714, 0.532146, 0.452201, 0.733914, -0.264211, 0.529883, 0.481579, 0.750529, -0.26859, 0.5259, 0.511558, 0.76747, -0.272046, 0.51999, 0.542042, 0.785189, -0.274225, 0.513083, 0.572799, 0.800954, -0.275189, 0.502936, 0.603816, 0.816962, -0.274946, 0.490921, 0.635461, 0.83336, -0.272695, 0.47684, 0.6676, 0.848143, -0.268223, 0.459405, 0.70051, 0.861818, -0.262768, 0.440319, 0.732902, 0.876828, -0.255872, 0.420123, 0.765084, 0.889312, -0.247703, 0.398379, 0.796391, 0.900412, -0.238381, 0.374496, 0.827333, 0.912251, -0.227783, 0.349874, 0.858385, 0.921792, -0.214832, 0.323181, 0.888652, 0.931273, -0.200949, 0.296624, 0.917763, 0.940295, -0.186537, 0.269211, 0.947878, 0.946812, -0.171538, 0.241447, 0.977016, 0.953588, -0.155254, 0.213829, 1.00501, 0.958841, -0.137156, 0.186807, 1.03179, 0.963746, -0.118699, 0.160706, 1.05502, 0.966468, -0.0998358, 0.135504, 1.07568, 0.971178, -0.0805186, 0.109131, 1.09479, 0.97831, -0.0599348, 0.0818293, 1.1123, 0.985886, -0.0399661, 0.0545872, 1.12771, 0.994021, -0.0198682, 0.0269405, 1.14186, 1.00009, 271022e-9, -12989e-8, 1.15514, 0.538716, -990918e-11, 0.486732, 109675e-10, 0.550656, -250642e-9, 0.497518, 277412e-9, 0.55057, -100265e-8, 0.497441, 110974e-8, 0.550903, -225672e-8, 0.497733, 249779e-8, 0.550568, -401046e-8, 0.497438, 443906e-8, 0.550574, -626613e-8, 0.49744, 693637e-8, 0.550591, -90226e-7, 0.497449, 998921e-8, 0.550623, -0.0122795, 0.497469, 0.0135984, 0.550667, -0.0160361, 0.497495, 0.0177654, 0.550724, -0.0202908, 0.497526, 0.0224915, 0.550792, -0.0250421, 0.497557, 0.0277795, 0.550918, -0.0302878, 0.49763, 0.0336334, 0.551058, -0.0360241, 0.497701, 0.0400573, 0.551276, -0.0422473, 0.497824, 0.0470585, 0.551551, -0.0489441, 0.497977, 0.0546433, 0.552074, -0.0559596, 0.498312, 0.0628367, 0.552681, -0.0633978, 0.498679, 0.071646, 0.553324, -0.0713176, 0.499031, 0.0810746, 0.554011, -0.0797268, 0.499365, 0.091129, 0.55488, -0.0885238, 0.499779, 0.101837, 0.556171, -0.0974417, 0.500444, 0.113239, 0.557498, -0.106841, 0.501025, 0.125316, 0.559299, -0.116533, 0.501864, 0.138128, 0.561647, -0.126298, 0.502967, 0.151695, 0.564347, -0.136388, 0.504129, 0.16604, 0.567863, -0.146576, 0.505713, 0.181207, 0.572569, -0.156832, 0.507953, 0.197259, 0.578919, -0.167323, 0.511186, 0.214258, 0.585387, -0.177712, 0.514042, 0.232038, 0.593134, -0.188184, 0.517484, 0.250733, 0.603295, -0.198717, 0.522345, 0.270454, 0.613854, -0.209177, 0.526751, 0.290807, 0.626092, -0.219644, 0.531595, 0.312202, 0.637868, -0.229494, 0.534721, 0.334435, 0.652458, -0.238718, 0.538304, 0.359184, 0.666985, -0.247061, 0.539875, 0.385637, 0.683301, -0.254652, 0.541042, 0.41328, 0.69998, -0.261376, 0.540735, 0.441903, 0.717824, -0.267085, 0.539139, 0.471609, 0.734617, -0.271465, 0.534958, 0.501446, 0.753663, -0.27528, 0.53032, 0.532571, 0.770512, -0.277617, 0.522134, 0.563641, 0.787356, -0.278525, 0.51206, 0.595067, 0.806252, -0.278512, 0.50119, 0.627226, 0.822061, -0.277023, 0.486791, 0.659402, 0.838959, -0.273175, 0.470467, 0.692874, 0.85379, -0.267238, 0.450688, 0.725702, 0.868268, -0.260327, 0.429741, 0.75832, 0.881994, -0.251946, 0.407223, 0.790189, 0.893885, -0.242432, 0.383214, 0.821625, 0.905118, -0.231904, 0.357297, 0.853011, 0.916045, -0.219545, 0.330733, 0.883773, 0.927614, -0.205378, 0.303916, 0.914435, 0.936005, -0.190388, 0.275941, 0.944502, 0.944533, -0.1749, 0.247493, 0.974439, 0.950758, -0.158588, 0.218996, 1.00286, 0.957078, -0.141027, 0.191559, 1.0304, 0.962448, -0.121507, 0.164457, 1.05466, 0.964993, -0.102068, 0.138636, 1.0761, 0.970017, -0.0822598, 0.111861, 1.09541, 0.97661, -0.062033, 0.0843438, 1.11317, 0.985073, -0.0409832, 0.0558496, 1.12911, 0.993515, -0.020146, 0.0275331, 1.1438, 1.00006, 27329e-8, -107883e-9, 1.15736, 0.525324, -999341e-11, 0.498153, 105385e-10, 0.526513, -251605e-9, 0.499277, 265329e-9, 0.526517, -100641e-8, 0.499282, 10613e-7, 0.526588, -226466e-8, 0.499337, 238823e-8, 0.526539, -40255e-7, 0.499302, 424535e-8, 0.526547, -628954e-8, 0.499306, 663364e-8, 0.526561, -905628e-8, 0.499313, 955337e-8, 0.526593, -0.0123253, 0.499334, 0.0130054, 0.526642, -0.0160957, 0.499365, 0.0169911, 0.5267, -0.0203661, 0.499396, 0.0215122, 0.526792, -0.0251347, 0.499451, 0.0265718, 0.526904, -0.0303985, 0.499511, 0.0321732, 0.527079, -0.0361554, 0.499617, 0.0383231, 0.527285, -0.0423982, 0.499731, 0.045026, 0.527602, -0.0491121, 0.499924, 0.0522936, 0.528166, -0.0561127, 0.500306, 0.0601528, 0.52879, -0.0635988, 0.5007, 0.0686059, 0.529421, -0.071581, 0.501048, 0.0776518, 0.530144, -0.0799854, 0.501421, 0.0873148, 0.531062, -0.0888032, 0.501884, 0.0976084, 0.532374, -0.0977643, 0.50259, 0.108588, 0.533828, -0.107197, 0.50329, 0.120234, 0.53581, -0.116887, 0.504312, 0.132602, 0.538063, -0.126755, 0.505365, 0.145721, 0.5409, -0.136819, 0.506668, 0.159617, 0.544882, -0.147117, 0.508731, 0.174369, 0.550238, -0.157446, 0.511601, 0.190028, 0.556038, -0.167988, 0.514431, 0.206587, 0.563031, -0.178364, 0.517808, 0.224046, 0.571543, -0.189007, 0.521937, 0.242503, 0.582255, -0.199546, 0.527415, 0.261977, 0.59272, -0.210084, 0.531682, 0.282162, 0.605648, -0.220448, 0.537123, 0.303426, 0.61785, -0.230593, 0.540664, 0.325323, 0.632223, -0.240238, 0.544467, 0.348993, 0.648819, -0.24887, 0.547594, 0.375462, 0.665825, -0.256657, 0.54912, 0.403024, 0.683389, -0.263711, 0.549294, 0.431773, 0.701495, -0.269666, 0.547649, 0.461494, 0.719197, -0.274169, 0.543786, 0.491623, 0.737906, -0.278124, 0.538644, 0.522994, 0.756652, -0.280632, 0.531057, 0.554775, 0.775279, -0.281741, 0.521972, 0.586441, 0.792688, -0.281652, 0.509613, 0.618596, 0.811894, -0.280345, 0.496497, 0.651462, 0.827938, -0.277128, 0.47968, 0.684023, 0.844837, -0.271646, 0.460688, 0.718024, 0.859239, -0.264397, 0.438872, 0.751207, 0.874088, -0.256144, 0.41577, 0.784232, 0.887693, -0.246311, 0.391369, 0.816191, 0.899402, -0.235497, 0.365872, 0.847828, 0.910973, -0.223631, 0.338618, 0.87934, 0.92204, -0.209874, 0.310803, 0.910325, 0.930987, -0.194265, 0.281802, 0.940695, 0.94, -0.178125, 0.252836, 0.970958, 0.948018, -0.161479, 0.224239, 1.00078, 0.955141, -0.144038, 0.195857, 1.0288, 0.960513, -0.124915, 0.168487, 1.05371, 0.963964, -0.104284, 0.141495, 1.07596, 0.968713, -0.0838732, 0.114437, 1.09628, 0.975524, -0.0635579, 0.0863105, 1.11448, 0.98431, -0.042291, 0.0574774, 1.13069, 0.992916, -0.0209131, 0.0284343, 1.14568, 0.999926, 743097e-9, -379265e-9, 1.15955, 0.501042, -998428e-11, 0.498726, 100306e-10, 0.502992, -252112e-9, 0.500665, 253283e-9, 0.502417, -100791e-8, 0.500092, 101259e-8, 0.502965, -226919e-8, 0.500621, 227978e-8, 0.502318, -403109e-8, 0.499994, 405011e-8, 0.502333, -629832e-8, 0.500005, 632868e-8, 0.502362, -906907e-8, 0.500027, 911446e-8, 0.502369, -0.0123423, 0.500023, 0.0124078, 0.50243, -0.0161178, 0.500066, 0.016211, 0.502493, -0.0203937, 0.500103, 0.0205256, 0.502592, -0.0251684, 0.500166, 0.0253548, 0.502707, -0.0304389, 0.50023, 0.0307029, 0.502881, -0.0362015, 0.500335, 0.0365753, 0.503124, -0.0424507, 0.500488, 0.0429798, 0.503443, -0.0491582, 0.500686, 0.0499268, 0.504083, -0.0561476, 0.501155, 0.0574541, 0.504668, -0.0636846, 0.501524, 0.0655408, 0.505319, -0.0716834, 0.501904, 0.0742072, 0.50609, -0.0800925, 0.502321, 0.0834699, 0.507122, -0.0888425, 0.502896, 0.0933603, 0.508414, -0.097855, 0.503603, 0.10391, 0.509955, -0.107304, 0.504416, 0.115113, 0.512061, -0.116921, 0.505565, 0.127054, 0.514419, -0.12689, 0.506732, 0.139709, 0.517529, -0.136934, 0.508338, 0.153173, 0.522085, -0.147327, 0.510987, 0.167528, 0.526986, -0.157612, 0.513527, 0.182708, 0.533122, -0.168213, 0.516717, 0.198881, 0.540807, -0.178688, 0.520832, 0.215986, 0.550687, -0.189511, 0.52632, 0.234335, 0.560567, -0.199998, 0.531009, 0.253375, 0.571698, -0.210652, 0.535839, 0.273499, 0.584364, -0.220917, 0.541091, 0.294355, 0.599066, -0.23137, 0.546875, 0.316525, 0.614148, -0.241206, 0.551306, 0.339671, 0.631157, -0.250379, 0.555187, 0.36531, 0.647919, -0.258397, 0.556595, 0.392767, 0.666112, -0.265528, 0.556949, 0.421397, 0.686158, -0.271827, 0.556617, 0.451433, 0.704838, -0.27674, 0.552975, 0.482131, 0.723957, -0.280733, 0.547814, 0.513458, 0.74262, -0.283359, 0.53997, 0.545446, 0.762009, -0.284541, 0.530422, 0.57775, 0.781314, -0.284507, 0.518546, 0.610434, 0.799116, -0.283309, 0.504178, 0.643178, 0.817604, -0.280378, 0.48843, 0.676248, 0.83459, -0.275619, 0.469457, 0.709698, 0.850974, -0.26856, 0.447698, 0.744245, 0.866747, -0.260094, 0.424791, 0.777695, 0.881412, -0.249929, 0.399913, 0.810392, 0.8936, -0.239137, 0.37308, 0.842872, 0.905943, -0.226818, 0.345705, 0.874677, 0.916408, -0.213699, 0.31706, 0.906257, 0.927215, -0.198428, 0.288444, 0.936881, 0.935625, -0.181643, 0.258329, 0.96795, 0.944076, -0.164386, 0.228488, 0.998216, 0.951229, -0.146339, 0.199763, 1.02689, 0.958793, -0.127709, 0.172153, 1.0535, 0.963219, -0.107244, 0.144989, 1.07646, 0.967562, -0.0857764, 0.11685, 1.09675, 0.974866, -0.0645377, 0.0880571, 1.11576, 0.983353, -0.0431732, 0.0587352, 1.13227, 0.992503, -0.0218356, 0.0294181, 1.1478, 1.00003, 605203e-9, -231013e-9, 1.16207, 0.482935, -101177e-10, 0.504695, 968142e-11, 0.477554, -251521e-9, 0.499071, 240676e-9, 0.477904, -100683e-8, 0.499436, 96342e-8, 0.478368, -226636e-8, 0.499899, 21687e-7, 0.477977, -402719e-8, 0.499513, 385384e-8, 0.477993, -629226e-8, 0.499525, 60221e-7, 0.478011, -906011e-8, 0.499536, 867289e-8, 0.478051, -0.0123305, 0.499566, 0.0118074, 0.478089, -0.016102, 0.499587, 0.0154269, 0.478171, -0.0203736, 0.499645, 0.0195341, 0.478254, -0.025143, 0.499692, 0.0241318, 0.47839, -0.0304071, 0.499779, 0.0292247, 0.478588, -0.0361631, 0.499911, 0.0348196, 0.478812, -0.0424023, 0.500046, 0.0409231, 0.479208, -0.0490724, 0.500326, 0.047552, 0.479841, -0.0560722, 0.500805, 0.0547377, 0.480392, -0.0636125, 0.501152, 0.0624607, 0.481068, -0.0716134, 0.501561, 0.0707473, 0.481898, -0.0800062, 0.502054, 0.0796118, 0.483022, -0.0886568, 0.502728, 0.0890974, 0.484332, -0.0977553, 0.503479, 0.0992099, 0.486126, -0.107173, 0.504546, 0.10999, 0.488066, -0.11677, 0.50557, 0.121476, 0.490521, -0.126725, 0.506849, 0.133672, 0.494232, -0.136793, 0.50911, 0.146731, 0.498302, -0.147116, 0.511345, 0.160577, 0.503565, -0.157446, 0.514344, 0.175335, 0.510902, -0.168121, 0.518824, 0.191207, 0.519263, -0.178799, 0.523666, 0.208058, 0.528204, -0.189407, 0.528296, 0.225875, 0.538854, -0.200145, 0.533724, 0.244782, 0.551278, -0.210701, 0.539833, 0.264753, 0.565222, -0.221303, 0.546131, 0.285745, 0.579403, -0.231688, 0.551496, 0.307592, 0.595469, -0.241718, 0.556809, 0.330582, 0.610929, -0.250992, 0.559641, 0.354995, 0.629433, -0.259602, 0.562379, 0.382471, 0.648504, -0.267038, 0.563676, 0.411126, 0.66756, -0.273388, 0.562092, 0.440924, 0.689143, -0.278788, 0.560807, 0.472118, 0.709056, -0.282783, 0.555701, 0.503774, 0.729855, -0.285836, 0.548698, 0.536364, 0.748954, -0.287078, 0.538544, 0.56895, 0.768373, -0.287133, 0.526711, 0.601991, 0.78827, -0.285839, 0.512511, 0.635403, 0.807465, -0.283238, 0.496323, 0.668797, 0.825194, -0.27906, 0.477638, 0.702584, 0.842203, -0.272286, 0.456253, 0.736393, 0.857749, -0.263854, 0.432412, 0.77096, 0.874799, -0.253943, 0.407806, 0.80489, 0.887497, -0.24237, 0.38033, 0.83771, 0.89966, -0.230278, 0.352446, 0.870376, 0.911753, -0.21646, 0.323268, 0.902256, 0.923011, -0.202071, 0.294314, 0.933306, 0.932375, -0.185519, 0.264104, 0.965177, 0.940537, -0.167604, 0.234035, 0.996303, 0.948904, -0.149068, 0.20412, 1.0261, 0.955263, -0.129539, 0.175431, 1.05304, 0.960303, -0.109932, 0.148116, 1.07617, 0.965512, -0.0880572, 0.119693, 1.09742, 0.973466, -0.0660548, 0.0901619, 1.11721, 0.98284, -0.0439228, 0.0599875, 1.13436, 0.992216, -0.0219588, 0.0298975, 1.15006, 0.999946, 119402e-9, -208547e-10, 1.16471, 0.447827, -100414e-10, 0.491543, 914833e-11, 0.454778, -251257e-9, 0.499172, 22891e-8, 0.453519, -100342e-8, 0.497787, 914184e-9, 0.45357, -225776e-8, 0.497847, 205701e-8, 0.453578, -401371e-8, 0.497855, 365705e-8, 0.45357, -627107e-8, 0.497841, 571453e-8, 0.453598, -902968e-8, 0.497864, 823019e-8, 0.453627, -0.0122888, 0.497882, 0.0112049, 0.453684, -0.0160475, 0.497923, 0.0146405, 0.453764, -0.0203044, 0.49798, 0.0185394, 0.453866, -0.0250576, 0.498049, 0.0229054, 0.453996, -0.0303028, 0.49813, 0.0277424, 0.454196, -0.0360379, 0.498267, 0.0330587, 0.454457, -0.0422521, 0.498445, 0.0388613, 0.454926, -0.0488393, 0.498812, 0.0451767, 0.455525, -0.0558653, 0.499272, 0.0520153, 0.456074, -0.0633772, 0.499625, 0.0593754, 0.456752, -0.0713606, 0.500049, 0.0672751, 0.457648, -0.07971, 0.500615, 0.0757447, 0.458849, -0.0883032, 0.501399, 0.0848231, 0.46029, -0.0974095, 0.502293, 0.0945135, 0.462, -0.106729, 0.503301, 0.104848, 0.464121, -0.116354, 0.504533, 0.115884, 0.466889, -0.126214, 0.506172, 0.127652, 0.470744, -0.136324, 0.508667, 0.14024, 0.47488, -0.146595, 0.510995, 0.153673, 0.480845, -0.157027, 0.514832, 0.168053, 0.488262, -0.167658, 0.519506, 0.183508, 0.496547, -0.178343, 0.524347, 0.199948, 0.506254, -0.188916, 0.52983, 0.217503, 0.517961, -0.199975, 0.536357, 0.236272, 0.531484, -0.210624, 0.543641, 0.256096, 0.545496, -0.221227, 0.550048, 0.277085, 0.559497, -0.231568, 0.555076, 0.298615, 0.575752, -0.241698, 0.560541, 0.321547, 0.591999, -0.251172, 0.564156, 0.345602, 0.610654, -0.260178, 0.567607, 0.371851, 0.630484, -0.268094, 0.56923, 0.40076, 0.651807, -0.274661, 0.569779, 0.430801, 0.67239, -0.280331, 0.566791, 0.461939, 0.693024, -0.284501, 0.562007, 0.493854, 0.715473, -0.287852, 0.555791, 0.526992, 0.736323, -0.28929, 0.546345, 0.560102, 0.755771, -0.289405, 0.534, 0.593543, 0.775424, -0.2881, 0.519114, 0.627256, 0.795447, -0.285562, 0.502543, 0.661464, 0.815319, -0.281416, 0.484773, 0.695206, 0.831769, -0.275523, 0.463445, 0.729044, 0.849464, -0.267516, 0.440269, 0.764069, 0.866775, -0.257584, 0.415049, 0.799089, 0.881252, -0.245817, 0.388049, 0.831948, 0.894209, -0.233127, 0.35889, 0.865526, 0.906922, -0.219579, 0.329915, 0.89818, 0.919686, -0.204491, 0.300441, 0.930013, 0.929044, -0.188962, 0.269445, 0.962061, 0.938393, -0.171079, 0.238402, 0.994214, 0.94661, -0.15199, 0.208204, 1.02533, 0.953095, -0.131953, 0.178653, 1.0529, 0.958644, -0.111233, 0.150684, 1.0771, 0.963925, -0.0903098, 0.122359, 1.09855, 0.971995, -0.0680505, 0.0923342, 1.11874, 0.981658, -0.0448512, 0.0614195, 1.13635, 0.991649, -0.0221931, 0.0303582, 1.15238, 0.999985, 393403e-9, -111086e-9, 1.16772, 0.396806, -971563e-11, 0.457671, 842355e-11, 0.429186, -249421e-9, 0.495017, 21625e-8, 0.429324, -998052e-9, 0.495173, 865322e-9, 0.429175, -224487e-8, 0.494999, 194637e-8, 0.429129, -399041e-8, 0.494952, 346004e-8, 0.429153, -623476e-8, 0.494974, 540684e-8, 0.429168, -89773e-7, 0.494983, 778714e-8, 0.429207, -0.0122175, 0.495012, 0.0106022, 0.429257, -0.0159542, 0.495047, 0.0138535, 0.429338, -0.0201864, 0.495106, 0.0175443, 0.429431, -0.0249104, 0.495165, 0.0216774, 0.429587, -0.0301252, 0.495279, 0.0262594, 0.429796, -0.0358249, 0.495432, 0.0312968, 0.430065, -0.0419972, 0.495621, 0.0367985, 0.430588, -0.0485144, 0.496061, 0.042798, 0.43113, -0.0555028, 0.496472, 0.0492914, 0.431743, -0.0629852, 0.496904, 0.0562907, 0.432448, -0.0709256, 0.497369, 0.0638056, 0.433414, -0.0791942, 0.498032, 0.071885, 0.434638, -0.0877346, 0.498854, 0.0805517, 0.43611, -0.0968056, 0.499812, 0.0898047, 0.437859, -0.106002, 0.500891, 0.0997142, 0.440017, -0.115648, 0.502198, 0.110289, 0.443236, -0.125427, 0.504389, 0.121644, 0.44697, -0.135492, 0.506809, 0.133769, 0.451689, -0.145746, 0.509858, 0.146787, 0.45811, -0.156219, 0.514247, 0.160793, 0.465305, -0.166834, 0.518816, 0.175791, 0.474085, -0.177546, 0.524331, 0.191906, 0.484808, -0.188262, 0.53104, 0.209199, 0.49732, -0.199346, 0.538511, 0.227825, 0.509693, -0.209951, 0.544554, 0.247269, 0.524367, -0.220533, 0.551616, 0.267978, 0.539228, -0.231082, 0.557368, 0.289672, 0.55644, -0.241342, 0.563782, 0.31268, 0.574204, -0.250964, 0.568851, 0.33651, 0.593388, -0.260306, 0.57312, 0.362219, 0.613358, -0.268667, 0.574916, 0.390322, 0.634512, -0.275591, 0.575053, 0.420478, 0.65563, -0.281328, 0.572404, 0.451614, 0.678265, -0.285948, 0.568893, 0.484112, 0.70011, -0.289408, 0.561878, 0.517348, 0.723005, -0.291328, 0.55359, 0.551355, 0.743744, -0.291418, 0.541099, 0.585109, 0.763949, -0.290252, 0.526489, 0.619487, 0.784186, -0.287648, 0.509496, 0.65404, 0.804304, -0.283782, 0.491484, 0.688649, 0.823629, -0.278067, 0.470517, 0.723133, 0.84094, -0.270588, 0.44705, 0.757163, 0.857852, -0.261188, 0.421252, 0.792816, 0.874934, -0.249313, 0.394191, 0.827248, 0.888709, -0.236492, 0.365359, 0.861074, 0.902589, -0.222185, 0.336016, 0.894417, 0.914201, -0.207314, 0.30527, 0.926825, 0.925978, -0.191146, 0.274532, 0.9595, 0.93512, -0.174135, 0.243393, 0.991583, 0.943656, -0.155231, 0.212414, 1.02356, 0.951719, -0.134403, 0.182005, 1.05239, 0.957164, -0.113023, 0.153043, 1.07754, 0.962656, -0.0914493, 0.124186, 1.09984, 0.970695, -0.0694179, 0.0941654, 1.12, 0.980749, -0.0466199, 0.0629671, 1.13849, 0.991205, -0.0227032, 0.0311146, 1.15494, 0.999884, 632388e-9, -254483e-9, 1.1706, 0.379821, -957289e-11, 0.460637, 789337e-11, 0.405188, -247483e-9, 0.491396, 204064e-9, 0.404796, -989434e-9, 0.490914, 815853e-9, 0.40483, -222607e-8, 0.490949, 183559e-8, 0.40473, -395723e-8, 0.49084, 326332e-8, 0.404731, -618287e-8, 0.490836, 509945e-8, 0.404768, -890258e-8, 0.490871, 734463e-8, 0.404791, -0.0121156, 0.490883, 999992e-8, 0.404857, -0.0158214, 0.490938, 0.0130676, 0.404943, -0.0200178, 0.491004, 0.0165503, 0.405059, -0.0247027, 0.491093, 0.0204521, 0.405213, -0.0298729, 0.491205, 0.0247788, 0.405399, -0.0355226, 0.491333, 0.0295373, 0.405731, -0.0416352, 0.491604, 0.034741, 0.406303, -0.0480807, 0.492116, 0.0404255, 0.406814, -0.0550458, 0.492506, 0.0465732, 0.407404, -0.0624652, 0.492926, 0.0532058, 0.408149, -0.0702958, 0.493442, 0.0603442, 0.409128, -0.0784623, 0.494136, 0.0680297, 0.410408, -0.087007, 0.495054, 0.0762786, 0.411813, -0.0959639, 0.495962, 0.0851046, 0.413735, -0.105075, 0.497257, 0.0945878, 0.416137, -0.114646, 0.498882, 0.104725, 0.41934, -0.124394, 0.501132, 0.11563, 0.423326, -0.134328, 0.503883, 0.127325, 0.428419, -0.14458, 0.50747, 0.139911, 0.43484, -0.154979, 0.511964, 0.153481, 0.442641, -0.165628, 0.517328, 0.168114, 0.452511, -0.176365, 0.524258, 0.183995, 0.463473, -0.187298, 0.531248, 0.200953, 0.475564, -0.198244, 0.538367, 0.219176, 0.488664, -0.208938, 0.545175, 0.238514, 0.504073, -0.219599, 0.553227, 0.259129, 0.520832, -0.230378, 0.560653, 0.280997, 0.538455, -0.240703, 0.567523, 0.303821, 0.55709, -0.250548, 0.573287, 0.327948, 0.576646, -0.259964, 0.577795, 0.353362, 0.596705, -0.268721, 0.580077, 0.380336, 0.618053, -0.276054, 0.58018, 0.4101, 0.640303, -0.282176, 0.578747, 0.44161, 0.662365, -0.286931, 0.574294, 0.474106, 0.684542, -0.290521, 0.567035, 0.507549, 0.707984, -0.292672, 0.558687, 0.541853, 0.730913, -0.293189, 0.547606, 0.576581, 0.752948, -0.292199, 0.533471, 0.61172, 0.773452, -0.289508, 0.516395, 0.646339, 0.794715, -0.285716, 0.497873, 0.682131, 0.814251, -0.280051, 0.476845, 0.716396, 0.833057, -0.272873, 0.453449, 0.751503, 0.84959, -0.263982, 0.427857, 0.786085, 0.867022, -0.252745, 0.400335, 0.821355, 0.882277, -0.239655, 0.371304, 0.85646, 0.895375, -0.225386, 0.340397, 0.890828, 0.909347, -0.209587, 0.310005, 0.923532, 0.921885, -0.193433, 0.2796, 0.956419, 0.932127, -0.176135, 0.247276, 0.989445, 0.941869, -0.157872, 0.216186, 1.02221, 0.949735, -0.137577, 0.185602, 1.05195, 0.956617, -0.115285, 0.155767, 1.07822, 0.961974, -0.0928418, 0.126103, 1.10149, 0.96972, -0.0700592, 0.0956758, 1.12207, 0.98012, -0.0474671, 0.0643269, 1.1408, 0.990825, -0.0238113, 0.0320863, 1.1577, 0.999876, 381574e-9, -812203e-10, 1.17403, 0.367636, -961342e-11, 0.469176, 753287e-11, 0.380377, -244772e-9, 0.485434, 191797e-9, 0.380416, -978857e-9, 0.485475, 767015e-9, 0.380376, -220165e-8, 0.485435, 172522e-8, 0.380419, -391408e-8, 0.485487, 306734e-8, 0.380438, -611549e-8, 0.485505, 479332e-8, 0.380462, -880558e-8, 0.485525, 690391e-8, 0.380496, -0.0119837, 0.485551, 940039e-8, 0.38056, -0.0156487, 0.485605, 0.0122848, 0.38064, -0.0197988, 0.485666, 0.0155601, 0.380767, -0.0244324, 0.48577, 0.0192313, 0.380909, -0.0295444, 0.485871, 0.0233032, 0.381142, -0.0351321, 0.48606, 0.0277861, 0.381472, -0.0411535, 0.486336, 0.0326939, 0.382015, -0.0475408, 0.486833, 0.0380565, 0.382523, -0.0544395, 0.487231, 0.0438615, 0.383129, -0.061784, 0.487683, 0.0501332, 0.383952, -0.0695085, 0.488313, 0.0568996, 0.38498, -0.0775819, 0.489077, 0.0641952, 0.386331, -0.0860443, 0.490113, 0.0720324, 0.387788, -0.0948406, 0.491099, 0.0804379, 0.389808, -0.103899, 0.492566, 0.0894899, 0.39252, -0.113313, 0.494601, 0.0992098, 0.395493, -0.123007, 0.496619, 0.109641, 0.399826, -0.132859, 0.499912, 0.120919, 0.405341, -0.143077, 0.504061, 0.133107, 0.411932, -0.153465, 0.508905, 0.146263, 0.420591, -0.164108, 0.515482, 0.160544, 0.43101, -0.174893, 0.523191, 0.176123, 0.441881, -0.185839, 0.53026, 0.192757, 0.453919, -0.196633, 0.537295, 0.210535, 0.468715, -0.207611, 0.546156, 0.229886, 0.485182, -0.218517, 0.555173, 0.250543, 0.501926, -0.229249, 0.562728, 0.27221, 0.51785, -0.239481, 0.567494, 0.294892, 0.536947, -0.249395, 0.573889, 0.318987, 0.557115, -0.259, 0.578831, 0.344348, 0.577966, -0.268075, 0.582055, 0.371223, 0.599489, -0.276115, 0.583307, 0.399834, 0.62479, -0.282523, 0.583902, 0.431415, 0.647504, -0.287663, 0.57953, 0.464301, 0.670601, -0.291538, 0.573103, 0.498123, 0.693539, -0.293842, 0.563731, 0.532662, 0.717385, -0.294681, 0.553169, 0.567925, 0.741533, -0.293717, 0.539908, 0.603502, 0.762142, -0.291156, 0.521902, 0.639074, 0.783014, -0.28719, 0.502815, 0.674439, 0.805158, -0.281773, 0.482598, 0.710497, 0.823646, -0.274682, 0.458949, 0.7456, 0.841879, -0.266184, 0.433129, 0.781085, 0.859515, -0.255682, 0.406064, 0.816, 0.875335, -0.242849, 0.376509, 0.851074, 0.890147, -0.228329, 0.345502, 0.886473, 0.903144, -0.212491, 0.31428, 0.920751, 0.916618, -0.195695, 0.282994, 0.954606, 0.927953, -0.178267, 0.251091, 0.988402, 0.937414, -0.159549, 0.219107, 1.02141, 0.946823, -0.140022, 0.18896, 1.05167, 0.954651, -0.118154, 0.158667, 1.07819, 0.959955, -0.0946636, 0.128808, 1.1025, 0.96858, -0.0711792, 0.0973787, 1.12391, 0.97938, -0.0475046, 0.0650965, 1.14322, 0.990498, -0.024059, 0.0326267, 1.16077, 0.999844, -512408e-10, 112444e-9, 1.17727, 0.316912, -934977e-11, 0.425996, 695559e-11, 0.356423, -241372e-9, 0.479108, 179562e-9, 0.356272, -965292e-9, 0.478897, 71811e-8, 0.356262, -217182e-8, 0.478894, 161574e-8, 0.356265, -386092e-8, 0.478895, 287261e-8, 0.356278, -60324e-7, 0.478905, 448907e-8, 0.356293, -868565e-8, 0.478914, 646572e-8, 0.356346, -0.0118207, 0.478965, 880438e-8, 0.356395, -0.0154355, 0.479001, 0.0115066, 0.356484, -0.019529, 0.479075, 0.0145762, 0.356609, -0.0240991, 0.47918, 0.018018, 0.356766, -0.0291413, 0.479305, 0.0218379, 0.357009, -0.0346498, 0.479512, 0.0260454, 0.357424, -0.0405462, 0.479909, 0.0306657, 0.357899, -0.0468825, 0.480337, 0.0357054, 0.358424, -0.0536887, 0.480771, 0.0411728, 0.359041, -0.0609416, 0.481242, 0.0470841, 0.359903, -0.0685239, 0.481943, 0.0534831, 0.360932, -0.0764883, 0.482741, 0.0603795, 0.362196, -0.0848364, 0.483688, 0.0678028, 0.363847, -0.0935002, 0.484947, 0.0758086, 0.365972, -0.102471, 0.486588, 0.0844173, 0.368741, -0.111751, 0.488787, 0.0937199, 0.372146, -0.121334, 0.491405, 0.103732, 0.377114, -0.131147, 0.495604, 0.114608, 0.38226, -0.141213, 0.499436, 0.126345, 0.389609, -0.151632, 0.505334, 0.139116, 0.397925, -0.162073, 0.51168, 0.152995, 0.407824, -0.172819, 0.518876, 0.168071, 0.420014, -0.183929, 0.527639, 0.184495, 0.434266, -0.195032, 0.537588, 0.20232, 0.447352, -0.205792, 0.544379, 0.221189, 0.463726, -0.216704, 0.553422, 0.241616, 0.481406, -0.227531, 0.562074, 0.263298, 0.498707, -0.238017, 0.568227, 0.286116, 0.518039, -0.247936, 0.574473, 0.3101, 0.538277, -0.257437, 0.579191, 0.335401, 0.561166, -0.266829, 0.584807, 0.362246, 0.583189, -0.275329, 0.586476, 0.390609, 0.606024, -0.28234, 0.585578, 0.420998, 0.632419, -0.287924, 0.584496, 0.454357, 0.656128, -0.291972, 0.577766, 0.488233, 0.679953, -0.29456, 0.56875, 0.523248, 0.704654, -0.295816, 0.558388, 0.559168, 0.729016, -0.295157, 0.544826, 0.595326, 0.752062, -0.292779, 0.528273, 0.631864, 0.773138, -0.288681, 0.508482, 0.667793, 0.794869, -0.283358, 0.487341, 0.704035, 0.815101, -0.27608, 0.46354, 0.739925, 0.834212, -0.26767, 0.438672, 0.775539, 0.852368, -0.257397, 0.411239, 0.810895, 0.870207, -0.245689, 0.3829, 0.846472, 0.884063, -0.231452, 0.351496, 0.881788, 0.898284, -0.215561, 0.31895, 0.917438, 0.912964, -0.198208, 0.287367, 0.952422, 0.924666, -0.180426, 0.254487, 0.987551, 0.934429, -0.161525, 0.222226, 1.02142, 0.943485, -0.141197, 0.191143, 1.05218, 0.9521, -0.120085, 0.161112, 1.07937, 0.957876, -0.0975881, 0.130982, 1.10403, 0.966943, -0.0726842, 0.0990553, 1.12616, 0.978313, -0.0483705, 0.0662818, 1.14619, 0.990048, -0.0239072, 0.0329243, 1.16413, 0.999984, 461885e-9, -772859e-10, 1.18099, 0.321287, -935049e-11, 0.455413, 659662e-11, 0.332595, -237513e-9, 0.471437, 167562e-9, 0.332729, -949964e-9, 0.471618, 670192e-9, 0.332305, -213618e-8, 0.471028, 150712e-8, 0.332326, -379765e-8, 0.471055, 267959e-8, 0.332344, -593353e-8, 0.471072, 418751e-8, 0.332356, -854349e-8, 0.471077, 603172e-8, 0.332403, -0.0116268, 0.471121, 821362e-8, 0.332461, -0.0151824, 0.47117, 0.0107357, 0.332552, -0.0192088, 0.471251, 0.0136014, 0.332657, -0.0237024, 0.47133, 0.0168152, 0.332835, -0.0286615, 0.471487, 0.0203853, 0.333083, -0.0340765, 0.471708, 0.0243212, 0.333547, -0.0398563, 0.47219, 0.0286518, 0.333989, -0.0460916, 0.472587, 0.0333763, 0.334532, -0.0527897, 0.473054, 0.0385084, 0.335167, -0.0599284, 0.473568, 0.0440638, 0.33608, -0.0673514, 0.474362, 0.0500962, 0.337146, -0.0752237, 0.475231, 0.0566022, 0.338462, -0.083418, 0.476282, 0.0636272, 0.34014, -0.0919382, 0.477615, 0.0712153, 0.342341, -0.100741, 0.479404, 0.079417, 0.345088, -0.109905, 0.481618, 0.0882631, 0.349049, -0.119369, 0.485081, 0.0978851, 0.353939, -0.129033, 0.489317, 0.108336, 0.359893, -0.139038, 0.494309, 0.119698, 0.366945, -0.149411, 0.499983, 0.132024, 0.375814, -0.159843, 0.507185, 0.145558, 0.387112, -0.170664, 0.516392, 0.160433, 0.40023, -0.181897, 0.526519, 0.176648, 0.412555, -0.192785, 0.53423, 0.193922, 0.427023, -0.203663, 0.542741, 0.212662, 0.443685, -0.214695, 0.552066, 0.232944, 0.461499, -0.225561, 0.560762, 0.254495, 0.480975, -0.236257, 0.569421, 0.277531, 0.501, -0.24639, 0.576101, 0.301724, 0.521691, -0.256101, 0.581493, 0.327112, 0.543478, -0.265289, 0.585221, 0.353917, 0.566094, -0.273938, 0.587614, 0.381941, 0.589578, -0.281679, 0.587991, 0.41172, 0.614583, -0.287655, 0.585928, 0.444148, 0.641813, -0.292228, 0.582092, 0.478617, 0.666189, -0.295172, 0.57398, 0.51397, 0.690475, -0.29648, 0.561676, 0.550118, 0.715543, -0.296203, 0.548758, 0.586933, 0.740405, -0.293999, 0.532792, 0.62384, 0.762183, -0.28998, 0.512735, 0.660723, 0.786069, -0.28478, 0.492402, 0.69807, 0.806812, -0.277568, 0.469058, 0.734422, 0.826987, -0.268951, 0.443017, 0.770946, 0.844588, -0.259049, 0.415501, 0.80699, 0.863725, -0.2471, 0.387328, 0.842107, 0.879137, -0.234157, 0.356108, 0.878078, 0.894634, -0.218719, 0.324315, 0.914058, 0.909162, -0.201293, 0.291813, 0.949922, 0.92072, -0.18267, 0.258474, 0.985337, 0.93158, -0.163212, 0.225593, 1.0205, 0.941238, -0.142771, 0.193986, 1.05273, 0.949293, -0.120956, 0.163392, 1.08075, 0.956226, -0.0985743, 0.132934, 1.10559, 0.96546, -0.075118, 0.101255, 1.12823, 0.977403, -0.0497921, 0.0675441, 1.149, 0.989648, -0.0241574, 0.0334681, 1.16765, 1.00001, 5762e-7, -184807e-9, 1.18519, 0.303474, -916603e-11, 0.4542, 61243e-10, 0.308894, -232869e-9, 0.462306, 155592e-9, 0.309426, -931661e-9, 0.463093, 622499e-9, 0.308643, -20949e-7, 0.461933, 139979e-8, 0.308651, -37242e-7, 0.461941, 248874e-8, 0.308662, -581873e-8, 0.46195, 388933e-8, 0.308687, -837818e-8, 0.461974, 560247e-8, 0.308728, -0.0114016, 0.462011, 762948e-8, 0.308789, -0.0148884, 0.462067, 997326e-8, 0.308882, -0.0188369, 0.462151, 0.0126375, 0.309007, -0.0232436, 0.462263, 0.0156271, 0.30918, -0.0281054, 0.462417, 0.0189498, 0.309442, -0.0334065, 0.462667, 0.0226167, 0.309901, -0.0390589, 0.463162, 0.0266614, 0.310331, -0.0452042, 0.463555, 0.0310715, 0.310858, -0.0517735, 0.464019, 0.0358698, 0.311576, -0.0587359, 0.464669, 0.0410848, 0.312436, -0.0660383, 0.465406, 0.0467453, 0.313526, -0.0737266, 0.466339, 0.0528718, 0.314903, -0.0817574, 0.467504, 0.0595039, 0.316814, -0.090167, 0.469226, 0.0666888, 0.318965, -0.0987555, 0.470981, 0.0744658, 0.322077, -0.107792, 0.473814, 0.082912, 0.325947, -0.117098, 0.477241, 0.0920846, 0.331008, -0.126602, 0.48184, 0.102137, 0.337893, -0.136619, 0.488334, 0.113135, 0.345106, -0.146838, 0.494415, 0.12511, 0.355111, -0.157357, 0.503275, 0.138356, 0.365095, -0.167955, 0.510966, 0.152686, 0.378344, -0.179157, 0.521508, 0.16856, 0.391599, -0.190143, 0.530455, 0.18561, 0.407786, -0.20123, 0.541275, 0.204308, 0.425294, -0.212456, 0.551784, 0.224623, 0.444021, -0.223568, 0.561493, 0.246172, 0.463418, -0.234154, 0.569886, 0.268979, 0.484077, -0.244546, 0.577116, 0.293411, 0.505513, -0.254301, 0.582914, 0.318936, 0.527672, -0.263564, 0.587208, 0.345856, 0.550565, -0.272332, 0.589277, 0.374054, 0.573656, -0.280011, 0.588426, 0.403276, 0.59827, -0.286924, 0.587504, 0.43474, 0.624731, -0.291994, 0.583401, 0.468767, 0.652396, -0.295159, 0.576997, 0.504411, 0.67732, -0.296954, 0.565863, 0.54114, 0.703147, -0.296877, 0.552316, 0.57816, 0.728715, -0.295147, 0.536773, 0.616124, 0.752448, -0.291275, 0.51771, 0.653885, 0.775169, -0.285905, 0.496087, 0.691537, 0.799307, -0.279064, 0.474232, 0.729251, 0.819482, -0.270294, 0.447676, 0.766267, 0.837659, -0.260032, 0.419656, 0.802616, 0.856903, -0.248497, 0.391328, 0.838583, 0.873325, -0.235252, 0.360285, 0.874711, 0.889788, -0.221126, 0.329215, 0.91077, 0.904486, -0.204304, 0.296392, 0.94653, 0.917711, -0.185562, 0.262159, 0.983828, 0.928969, -0.165635, 0.229142, 1.01955, 0.939707, -0.14442, 0.19673, 1.05317, 0.948167, -0.122147, 0.165095, 1.0823, 0.955222, -0.099098, 0.13451, 1.10791, 0.964401, -0.0755332, 0.102476, 1.1312, 0.976605, -0.0513817, 0.0689667, 1.15218, 0.989085, -0.0258499, 0.034506, 1.17129, 0.999908, 617773e-9, -271268e-9, 1.18961, 0.285803, -905752e-11, 0.452348, 572272e-11, 0.284689, -22732e-8, 0.450581, 143626e-9, 0.285263, -910214e-9, 0.451482, 575099e-9, 0.285302, -204784e-8, 0.451553, 129395e-8, 0.285318, -364057e-8, 0.451574, 23006e-7, 0.28533, -568813e-8, 0.451585, 359547e-8, 0.285361, -819001e-8, 0.451618, 517934e-8, 0.285397, -0.0111458, 0.45165, 7054e-6, 0.285447, -0.0145536, 0.451688, 922167e-8, 0.285527, -0.0184127, 0.451758, 0.0116869, 0.285688, -0.0227207, 0.451929, 0.0144555, 0.28584, -0.0274712, 0.452055, 0.0175341, 0.286136, -0.0326278, 0.452369, 0.0209406, 0.286574, -0.0381792, 0.452853, 0.0246965, 0.287012, -0.0441879, 0.453272, 0.0287996, 0.287542, -0.0506096, 0.453752, 0.033268, 0.288299, -0.0573634, 0.454488, 0.0381504, 0.289186, -0.0645458, 0.455294, 0.0434447, 0.290302, -0.0720405, 0.456301, 0.0491973, 0.291776, -0.0799046, 0.457648, 0.0554453, 0.29372, -0.088117, 0.459483, 0.0622311, 0.296052, -0.0965328, 0.461571, 0.0695992, 0.299563, -0.105409, 0.465085, 0.077658, 0.30335, -0.114553, 0.468506, 0.0864176, 0.309167, -0.123917, 0.474423, 0.0961078, 0.31529, -0.13381, 0.47995, 0.106643, 0.324163, -0.144021, 0.488592, 0.118322, 0.333272, -0.154382, 0.496461, 0.131133, 0.344224, -0.165015, 0.50562, 0.145208, 0.357733, -0.176168, 0.516719, 0.16073, 0.373046, -0.187468, 0.528513, 0.177807, 0.38788, -0.198488, 0.537713, 0.196072, 0.405133, -0.209545, 0.547999, 0.21605, 0.423845, -0.220724, 0.55759, 0.237484, 0.443777, -0.231518, 0.566246, 0.26039, 0.464824, -0.242035, 0.574326, 0.284835, 0.486635, -0.251898, 0.58037, 0.310518, 0.51012, -0.261304, 0.58568, 0.337678, 0.535301, -0.270384, 0.590197, 0.366242, 0.559193, -0.27841, 0.590569, 0.395873, 0.583544, -0.285325, 0.588161, 0.426857, 0.608834, -0.291113, 0.584249, 0.459477, 0.635753, -0.294882, 0.57763, 0.494734, 0.664367, -0.297088, 0.569479, 0.532023, 0.689688, -0.297364, 0.555064, 0.569629, 0.715732, -0.295949, 0.539522, 0.608124, 0.741307, -0.292259, 0.521613, 0.646231, 0.764949, -0.287063, 0.49969, 0.684938, 0.788599, -0.28012, 0.476747, 0.723548, 0.81048, -0.27153, 0.45116, 0.761135, 0.831372, -0.261289, 0.424101, 0.798916, 0.850092, -0.249559, 0.39443, 0.835952, 0.867777, -0.236348, 0.363849, 0.871606, 0.884632, -0.221569, 0.332477, 0.907843, 0.90047, -0.20618, 0.300667, 0.944187, 0.914524, -0.188771, 0.266552, 0.981371, 0.926892, -0.168362, 0.232349, 1.01841, 0.937951, -0.146761, 0.199359, 1.05308, 0.947236, -0.123813, 0.1675, 1.0839, 0.954367, -0.099984, 0.136166, 1.11047, 0.963907, -0.0759278, 0.103808, 1.13414, 0.976218, -0.0511367, 0.0697061, 1.15575, 0.988772, -0.0267415, 0.0352529, 1.17531, 0.999888, -520778e-9, 289926e-9, 1.19389, 0.263546, -883274e-11, 0.441896, 526783e-11, 0.262352, -221849e-9, 0.439889, 132311e-9, 0.262325, -886683e-9, 0.439848, 528824e-9, 0.26228, -199476e-8, 0.439765, 118975e-8, 0.262372, -354671e-8, 0.439922, 211568e-8, 0.26239, -554141e-8, 0.439941, 330652e-8, 0.262412, -797888e-8, 0.439961, 476346e-8, 0.262453, -0.0108584, 0.440002, 648818e-8, 0.262528, -0.0141788, 0.440085, 84835e-7, 0.262615, -0.017938, 0.440166, 0.0107533, 0.262744, -0.0221346, 0.440291, 0.0133044, 0.262939, -0.026762, 0.440493, 0.0161445, 0.263277, -0.0317573, 0.440889, 0.0192974, 0.26368, -0.0371832, 0.441338, 0.0227699, 0.264106, -0.0430371, 0.441753, 0.0265698, 0.264624, -0.0493035, 0.442227, 0.0307178, 0.265378, -0.0558669, 0.442985, 0.0352616, 0.266253, -0.0628718, 0.443795, 0.0401968, 0.267478, -0.0701569, 0.445008, 0.04559, 0.269062, -0.077845, 0.446599, 0.0514539, 0.270926, -0.0857941, 0.448349, 0.0578382, 0.273693, -0.0940773, 0.451221, 0.0648363, 0.276746, -0.102704, 0.454097, 0.0724389, 0.281693, -0.111735, 0.459517, 0.0808744, 0.287335, -0.121004, 0.46531, 0.0901551, 0.29448, -0.130734, 0.472605, 0.100371, 0.30257, -0.140777, 0.480251, 0.111644, 0.312465, -0.15111, 0.489444, 0.124111, 0.324856, -0.16189, 0.500919, 0.137979, 0.33774, -0.172946, 0.511317, 0.153163, 0.35255, -0.184152, 0.522684, 0.169817, 0.367786, -0.19522, 0.53248, 0.187886, 0.385474, -0.20632, 0.543326, 0.207634, 0.404976, -0.217744, 0.554109, 0.229165, 0.425203, -0.228691, 0.563395, 0.252068, 0.446704, -0.239299, 0.571565, 0.276471, 0.468951, -0.249348, 0.577935, 0.302323, 0.493487, -0.258933, 0.584309, 0.329882, 0.517861, -0.268009, 0.58773, 0.358525, 0.543309, -0.276238, 0.589612, 0.388585, 0.569704, -0.28356, 0.589294, 0.419787, 0.594871, -0.289497, 0.585137, 0.452114, 0.622555, -0.294452, 0.580356, 0.486466, 0.651167, -0.296918, 0.57185, 0.523079, 0.677332, -0.297647, 0.558428, 0.5611, 0.703718, -0.296321, 0.542232, 0.599592, 0.730262, -0.293339, 0.524541, 0.639138, 0.754304, -0.288036, 0.502691, 0.677978, 0.778051, -0.281018, 0.479212, 0.716537, 0.801557, -0.272414, 0.454071, 0.75586, 0.822559, -0.262419, 0.425952, 0.794477, 0.843051, -0.250702, 0.397313, 0.832664, 0.86232, -0.237264, 0.366534, 0.869876, 0.879044, -0.222716, 0.334816, 0.906973, 0.896362, -0.206827, 0.303143, 0.943558, 0.910342, -0.189659, 0.269699, 0.979759, 0.924119, -0.171108, 0.236411, 1.01718, 0.935374, -0.149579, 0.202224, 1.05289, 0.944295, -0.126295, 0.16989, 1.08496, 0.952227, -0.101511, 0.138089, 1.11256, 0.962041, -0.0766392, 0.105053, 1.1375, 0.97528, -0.0511967, 0.070329, 1.15983, 0.988476, -0.025463, 0.0351268, 1.17987, 0.999962, 286808e-10, 145564e-10, 1.19901, 0.227089, -841413e-11, 0.404216, 472707e-11, 0.239725, -215083e-9, 0.426708, 120833e-9, 0.239904, -860718e-9, 0.427028, 483555e-9, 0.239911, -193661e-8, 0.427039, 108806e-8, 0.239914, -344276e-8, 0.42704, 193457e-8, 0.239933, -537907e-8, 0.427064, 302363e-8, 0.239944, -774482e-8, 0.427065, 435604e-8, 0.239993, -0.01054, 0.427122, 593398e-8, 0.240052, -0.0137626, 0.427179, 775987e-8, 0.240148, -0.0174115, 0.427279, 983854e-8, 0.240278, -0.021484, 0.42741, 0.0121763, 0.240472, -0.0259729, 0.427618, 0.0147827, 0.240839, -0.0308131, 0.428086, 0.0176837, 0.241201, -0.0360893, 0.428482, 0.0208775, 0.241626, -0.0417723, 0.428907, 0.0243821, 0.242207, -0.0478337, 0.42952, 0.0282228, 0.24298, -0.0542199, 0.430332, 0.0324333, 0.243881, -0.0610015, 0.431222, 0.0370252, 0.245123, -0.0680874, 0.432512, 0.0420535, 0.24667, -0.0755482, 0.434088, 0.0475414, 0.248779, -0.0832873, 0.436323, 0.0535542, 0.251665, -0.0913546, 0.439509, 0.0601716, 0.255305, -0.0998489, 0.443478, 0.0674282, 0.260049, -0.108576, 0.448713, 0.0754673, 0.266192, -0.117754, 0.455524, 0.084339, 0.273158, -0.127294, 0.4627, 0.0941683, 0.282131, -0.137311, 0.472068, 0.10515, 0.293332, -0.147736, 0.483565, 0.117402, 0.304667, -0.158357, 0.493702, 0.130824, 0.317785, -0.169274, 0.504708, 0.145724, 0.333245, -0.180595, 0.517107, 0.16215, 0.349843, -0.191892, 0.528849, 0.180149, 0.367944, -0.203168, 0.540301, 0.199746, 0.387579, -0.214443, 0.551514, 0.221047, 0.408247, -0.225624, 0.560906, 0.243981, 0.43014, -0.236422, 0.56959, 0.268513, 0.452669, -0.24654, 0.576098, 0.294409, 0.476196, -0.256157, 0.580925, 0.322002, 0.501157, -0.265289, 0.584839, 0.351052, 0.527632, -0.273671, 0.587614, 0.3812, 0.555754, -0.281254, 0.589119, 0.412994, 0.581682, -0.287448, 0.585204, 0.445498, 0.608196, -0.292614, 0.579006, 0.479505, 0.635661, -0.296068, 0.571297, 0.514643, 0.664999, -0.297395, 0.560855, 0.552213, 0.691039, -0.296645, 0.544525, 0.591365, 0.7179, -0.293785, 0.526535, 0.630883, 0.744059, -0.289089, 0.50545, 0.670932, 0.76863, -0.282239, 0.482514, 0.710904, 0.793273, -0.273688, 0.457246, 0.750259, 0.814731, -0.26328, 0.428872, 0.78948, 0.835603, -0.251526, 0.399384, 0.828597, 0.85489, -0.238339, 0.368811, 0.866892, 0.872828, -0.223607, 0.336617, 0.90563, 0.889462, -0.207538, 0.303997, 0.943538, 0.904929, -0.190297, 0.270812, 0.980591, 0.919101, -0.172034, 0.237453, 1.01935, 0.930536, -0.152058, 0.204431, 1.05498, 0.941223, -0.129515, 0.172495, 1.08717, 0.94982, -0.104263, 0.140175, 1.11551, 0.960592, -0.0781944, 0.106465, 1.14098, 0.974629, -0.051688, 0.0711592, 1.16418, 0.98811, -0.0253929, 0.0354432, 1.18465, 1.00004, 804378e-9, -330876e-9, 1.20462, 0.214668, -821282e-11, 0.406619, 433582e-11, 0.218053, -208144e-9, 0.413025, 109887e-9, 0.217987, -832212e-9, 0.412901, 439362e-9, 0.217971, -187246e-8, 0.412876, 988623e-9, 0.217968, -332855e-8, 0.41286, 175772e-8, 0.217985, -520055e-8, 0.412882, 274729e-8, 0.218014, -748814e-8, 0.412916, 395842e-8, 0.218054, -0.0101901, 0.412957, 539274e-8, 0.218106, -0.0133057, 0.413005, 705348e-8, 0.218217, -0.0168342, 0.413139, 894581e-8, 0.218338, -0.0207707, 0.413258, 0.0110754, 0.21855, -0.0251001, 0.413509, 0.0134551, 0.218913, -0.0297861, 0.413992, 0.0161081, 0.219265, -0.0348956, 0.414383, 0.0190307, 0.219696, -0.0403909, 0.414839, 0.0222458, 0.220329, -0.0462003, 0.415567, 0.025792, 0.220989, -0.0524208, 0.41621, 0.0296637, 0.222027, -0.058948, 0.417385, 0.0339323, 0.223301, -0.0658208, 0.418779, 0.0386055, 0.224988, -0.0730347, 0.420665, 0.0437355, 0.227211, -0.0805274, 0.423198, 0.0493844, 0.230131, -0.088395, 0.426566, 0.0556135, 0.233908, -0.0966208, 0.43091, 0.0624829, 0.239092, -0.105223, 0.437148, 0.0701636, 0.245315, -0.11424, 0.444302, 0.0786949, 0.253166, -0.12368, 0.453262, 0.0882382, 0.262374, -0.133569, 0.463211, 0.0988682, 0.273145, -0.143836, 0.474271, 0.110727, 0.285512, -0.154577, 0.4863, 0.123945, 0.299512, -0.165501, 0.498817, 0.138581, 0.314287, -0.176698, 0.510341, 0.154676, 0.331083, -0.188066, 0.522583, 0.172459, 0.349615, -0.199597, 0.534879, 0.191979, 0.369318, -0.210843, 0.546083, 0.21309, 0.390377, -0.222068, 0.5562, 0.235998, 0.412411, -0.233059, 0.564704, 0.260518, 0.435715, -0.24357, 0.572314, 0.286795, 0.461196, -0.253356, 0.579395, 0.314559, 0.485587, -0.262362, 0.581985, 0.343581, 0.511908, -0.270895, 0.584347, 0.374367, 0.539798, -0.278452, 0.58505, 0.406015, 0.567974, -0.284877, 0.583344, 0.439168, 0.594303, -0.290124, 0.577348, 0.473005, 0.622951, -0.294183, 0.570751, 0.508534, 0.652404, -0.296389, 0.561541, 0.544764, 0.679291, -0.296605, 0.546426, 0.582927, 0.706437, -0.294095, 0.528599, 0.622681, 0.734485, -0.28978, 0.508676, 0.663567, 0.758841, -0.283363, 0.484768, 0.704092, 0.78537, -0.275015, 0.460434, 0.745101, 0.807315, -0.264689, 0.432166, 0.784712, 0.8271, -0.252597, 0.401807, 0.824241, 0.849191, -0.239154, 0.371458, 0.863803, 0.867046, -0.224451, 0.338873, 0.903063, 0.8852, -0.208342, 0.306175, 0.942763, 0.901771, -0.190684, 0.272759, 0.981559, 0.915958, -0.172105, 0.239306, 1.02048, 0.928046, -0.152214, 0.206071, 1.05765, 0.939961, -0.130247, 0.17367, 1.08999, 0.948711, -0.10672, 0.142201, 1.11829, 0.959305, -0.0808688, 0.108454, 1.14467, 0.973009, -0.0539145, 0.0728109, 1.16839, 0.987631, -0.0262947, 0.0360625, 1.19004, 0.999978, 132758e-8, -559424e-9, 1.21058, 0.193925, -793421e-11, 0.391974, 392537e-11, 0.196746, -200315e-9, 0.397675, 991033e-10, 0.19667, -801099e-9, 0.397521, 396342e-9, 0.196633, -180246e-8, 0.397445, 891829e-9, 0.196654, -320443e-8, 0.397482, 158582e-8, 0.196659, -500647e-8, 0.39748, 247867e-8, 0.196683, -72086e-7, 0.397506, 357167e-8, 0.196728, -981001e-8, 0.397562, 486675e-8, 0.196792, -0.0128096, 0.397633, 636707e-8, 0.19689, -0.0162055, 0.397746, 807752e-8, 0.197017, -0.0199943, 0.397884, 0.0100052, 0.19729, -0.024139, 0.39827, 0.0121691, 0.197583, -0.0286671, 0.398639, 0.0145755, 0.197927, -0.0335858, 0.399034, 0.0172355, 0.198383, -0.0388806, 0.399554, 0.0201718, 0.199002, -0.0444736, 0.400289, 0.0234194, 0.199739, -0.0504583, 0.401111, 0.026984, 0.200784, -0.056729, 0.402349, 0.0309217, 0.202075, -0.0633643, 0.403841, 0.0352496, 0.203898, -0.0703247, 0.406076, 0.0400313, 0.206199, -0.0775565, 0.408841, 0.0453282, 0.209252, -0.085184, 0.41259, 0.0511794, 0.213638, -0.0931994, 0.418288, 0.0577459, 0.21881, -0.101617, 0.424681, 0.0650508, 0.225642, -0.11052, 0.433429, 0.0732759, 0.233717, -0.119772, 0.442897, 0.0824683, 0.242823, -0.129505, 0.452888, 0.0927484, 0.254772, -0.139906, 0.466407, 0.104417, 0.266603, -0.150402, 0.477413, 0.117211, 0.28073, -0.161395, 0.490519, 0.131598, 0.295399, -0.172465, 0.50201, 0.147407, 0.312705, -0.183982, 0.515311, 0.165031, 0.331335, -0.195532, 0.52786, 0.184336, 0.351037, -0.206971, 0.5392, 0.205361, 0.372175, -0.218117, 0.54941, 0.228043, 0.394548, -0.229327, 0.558642, 0.25267, 0.419598, -0.240052, 0.567861, 0.279071, 0.443922, -0.249937, 0.573332, 0.306882, 0.471495, -0.259407, 0.58013, 0.33661, 0.496769, -0.267749, 0.580564, 0.367328, 0.524951, -0.275524, 0.581696, 0.399753, 0.55318, -0.282148, 0.579885, 0.433134, 0.581577, -0.287533, 0.575471, 0.467534, 0.609231, -0.291612, 0.567445, 0.502943, 0.637478, -0.293911, 0.557657, 0.53871, 0.667795, -0.295096, 0.546535, 0.576568, 0.694272, -0.294073, 0.529561, 0.614929, 0.722937, -0.290386, 0.510561, 0.655909, 0.749682, -0.284481, 0.487846, 0.697663, 0.774754, -0.276188, 0.462487, 0.738515, 0.799301, -0.266215, 0.43481, 0.779802, 0.820762, -0.254116, 0.404879, 0.820045, 0.843231, -0.240393, 0.374559, 0.860294, 0.861857, -0.225503, 0.341582, 0.900965, 0.880815, -0.209382, 0.308778, 0.941727, 0.89766, -0.19155, 0.275232, 0.980916, 0.912926, -0.172346, 0.240938, 1.02162, 0.926391, -0.151799, 0.207223, 1.0597, 0.938429, -0.129968, 0.17484, 1.09291, 0.947834, -0.10651, 0.142984, 1.12248, 0.958432, -0.0824098, 0.109902, 1.149, 0.972402, -0.0565242, 0.0744454, 1.1733, 0.987191, -0.028427, 0.0373794, 1.19538, 0.999975, 385685e-10, -4203e-8, 1.21676, 0.178114, -766075e-11, 0.385418, 354027e-11, 0.176074, -191966e-9, 0.381002, 887135e-10, 0.17601, -767549e-9, 0.380861, 354715e-9, 0.17598, -172696e-8, 0.380798, 798168e-9, 0.175994, -307012e-8, 0.380824, 141928e-8, 0.176017, -479684e-8, 0.380858, 221859e-8, 0.176019, -690648e-8, 0.380839, 319714e-8, 0.176072, -939888e-8, 0.380913, 43572e-7, 0.176131, -0.0122726, 0.380979, 5702e-6, 0.176239, -0.0155264, 0.38112, 723689e-8, 0.176371, -0.0191551, 0.381272, 896907e-8, 0.176638, -0.023117, 0.381669, 0.0109194, 0.176912, -0.0274633, 0.382015, 0.0130903, 0.177279, -0.032173, 0.382476, 0.0154949, 0.17774, -0.0372219, 0.383041, 0.0181669, 0.178344, -0.0426132, 0.38378, 0.0211209, 0.179153, -0.0483309, 0.384773, 0.0243899, 0.180197, -0.0543447, 0.386076, 0.0280062, 0.181581, -0.0607122, 0.387809, 0.032004, 0.18344, -0.0673855, 0.390205, 0.036453, 0.186139, -0.0743989, 0.393944, 0.0414162, 0.189432, -0.0817731, 0.39832, 0.0469394, 0.193795, -0.0895464, 0.404188, 0.0531442, 0.199641, -0.0978264, 0.4121, 0.0601374, 0.206679, -0.106499, 0.421425, 0.0680078, 0.214865, -0.115654, 0.431504, 0.076919, 0.224406, -0.125268, 0.442526, 0.0868835, 0.235876, -0.135475, 0.455465, 0.0981875, 0.248335, -0.146023, 0.4681, 0.110759, 0.262868, -0.157016, 0.482069, 0.124885, 0.278962, -0.168245, 0.496182, 0.140645, 0.295082, -0.17958, 0.507401, 0.157838, 0.313738, -0.191227, 0.520252, 0.17695, 0.333573, -0.202718, 0.531708, 0.197817, 0.356433, -0.214424, 0.544509, 0.220785, 0.378853, -0.225492, 0.55373, 0.245306, 0.402717, -0.236236, 0.561348, 0.271593, 0.428375, -0.246568, 0.568538, 0.299776, 0.454724, -0.255941, 0.573462, 0.329433, 0.482291, -0.264511, 0.576356, 0.360598, 0.509706, -0.272129, 0.576446, 0.393204, 0.538805, -0.278979, 0.575298, 0.427227, 0.568919, -0.284528, 0.572154, 0.462157, 0.596804, -0.288801, 0.564691, 0.497997, 0.625987, -0.291334, 0.555134, 0.534467, 0.656414, -0.292722, 0.545051, 0.571736, 0.683916, -0.292185, 0.528813, 0.610158, 0.711809, -0.290043, 0.51106, 0.649061, 0.739547, -0.285246, 0.490103, 0.690081, 0.766914, -0.277647, 0.465523, 0.732554, 0.791375, -0.267603, 0.437718, 0.773982, 0.814772, -0.256109, 0.40882, 0.81609, 0.836691, -0.242281, 0.377823, 0.856849, 0.856984, -0.227155, 0.34496, 0.898363, 0.876332, -0.210395, 0.311335, 0.939471, 0.894988, -0.192612, 0.277703, 0.980799, 0.911113, -0.173236, 0.243019, 1.02215, 0.924092, -0.152258, 0.209037, 1.06139, 0.936828, -0.129575, 0.175909, 1.09635, 0.946869, -0.10594, 0.143852, 1.12707, 0.958284, -0.081318, 0.110289, 1.15419, 0.972325, -0.0556133, 0.0747232, 1.17909, 0.986878, -0.0297899, 0.0383149, 1.20163, 0.999936, -197169e-8, 912402e-9, 1.22338, 0.151174, -720365e-11, 0.351531, 309789e-11, 0.155594, -18279e-8, 0.361806, 78608e-9, 0.156099, -731569e-9, 0.362982, 314615e-9, 0.156053, -164578e-8, 0.362869, 707845e-9, 0.156093, -29261e-7, 0.362961, 125884e-8, 0.156099, -457155e-8, 0.362959, 196783e-8, 0.15612, -658224e-8, 0.362982, 283622e-8, 0.156168, -895774e-8, 0.363048, 386625e-8, 0.156221, -0.0116962, 0.363101, 506109e-8, 0.156324, -0.0147973, 0.363241, 642675e-8, 0.156476, -0.0182503, 0.363448, 797175e-8, 0.156731, -0.0220266, 0.36384, 971484e-8, 0.156994, -0.026176, 0.364179, 0.0116575, 0.157341, -0.0306701, 0.36462, 0.0138207, 0.157867, -0.0354591, 0.365364, 0.0162356, 0.15846, -0.0406141, 0.366111, 0.0189092, 0.159308, -0.0460519, 0.367248, 0.021885, 0.160426, -0.0518096, 0.368767, 0.0252004, 0.161877, -0.0578906, 0.370745, 0.0288825, 0.163995, -0.0642812, 0.373831, 0.0330139, 0.16655, -0.0710067, 0.377366, 0.0376283, 0.170237, -0.0781522, 0.382799, 0.0428493, 0.175096, -0.0857172, 0.389915, 0.0487324, 0.181069, -0.0938025, 0.398487, 0.0554214, 0.188487, -0.102363, 0.408799, 0.0630189, 0.197029, -0.111343, 0.419991, 0.071634, 0.206684, -0.120812, 0.431455, 0.0812797, 0.218698, -0.131033, 0.445746, 0.0923651, 0.230726, -0.141373, 0.457471, 0.104545, 0.245516, -0.152387, 0.472388, 0.118449, 0.261551, -0.163628, 0.486671, 0.133923, 0.277437, -0.174814, 0.49762, 0.150849, 0.296662, -0.186713, 0.51162, 0.169924, 0.31795, -0.198513, 0.525435, 0.190848, 0.339422, -0.210119, 0.536267, 0.213504, 0.362143, -0.221354, 0.545982, 0.237947, 0.387198, -0.23224, 0.555364, 0.264427, 0.412349, -0.24257, 0.561489, 0.292519, 0.439274, -0.252284, 0.566903, 0.322561, 0.466779, -0.261023, 0.569614, 0.353952, 0.496011, -0.26899, 0.571589, 0.387278, 0.524964, -0.275498, 0.570325, 0.421356, 0.556518, -0.281449, 0.568792, 0.457314, 0.584363, -0.285526, 0.560268, 0.493199, 0.614214, -0.28844, 0.55205, 0.530276, 0.645684, -0.289777, 0.541906, 0.56855, 0.673446, -0.289722, 0.526464, 0.606927, 0.701924, -0.287792, 0.509872, 0.645945, 0.73037, -0.284315, 0.490649, 0.685564, 0.757405, -0.278804, 0.467964, 0.726511, 0.784025, -0.269543, 0.441468, 0.768601, 0.808255, -0.258117, 0.41216, 0.811321, 0.830739, -0.244728, 0.380606, 0.853496, 0.851914, -0.229428, 0.348111, 0.895374, 0.872586, -0.212508, 0.314732, 0.937674, 0.891581, -0.194025, 0.280338, 0.979869, 0.907641, -0.174711, 0.245203, 1.02253, 0.922233, -0.153509, 0.21077, 1.06371, 0.935878, -0.130418, 0.177399, 1.09972, 0.946338, -0.105558, 0.144507, 1.13124, 0.957265, -0.080059, 0.110508, 1.15973, 0.971668, -0.0539766, 0.0742311, 1.18515, 0.9866, -0.0277101, 0.0375224, 1.20858, 1.00021, -515531e-9, 135226e-9, 1.23135, 0.137468, -686011e-11, 0.345041, 273315e-11, 0.13703, -173378e-9, 0.343936, 690761e-10, 0.136986, -693048e-9, 0.34383, 276126e-9, 0.136964, -155931e-8, 0.343761, 621337e-9, 0.137003, -277211e-8, 0.343863, 110494e-8, 0.137012, -433103e-8, 0.343868, 172744e-8, 0.137043, -623606e-8, 0.343916, 249022e-8, 0.13709, -84868e-7, 0.343986, 339559e-8, 0.137145, -0.0110814, 0.344045, 444687e-8, 0.137242, -0.0140187, 0.344177, 565007e-8, 0.137431, -0.0172713, 0.344491, 701868e-8, 0.137644, -0.0208605, 0.344805, 856042e-8, 0.13791, -0.024792, 0.345172, 0.0102863, 0.138295, -0.0290461, 0.345734, 0.0122185, 0.138764, -0.0335957, 0.346371, 0.0143771, 0.139415, -0.038467, 0.347298, 0.0167894, 0.140272, -0.0436176, 0.348527, 0.0194895, 0.141457, -0.0491016, 0.350276, 0.0225043, 0.14303, -0.0548764, 0.352646, 0.0258962, 0.145289, -0.0610096, 0.356206, 0.0297168, 0.148502, -0.0674777, 0.361488, 0.0340562, 0.152188, -0.074345, 0.367103, 0.0389534, 0.157359, -0.0817442, 0.375247, 0.0445541, 0.16379, -0.0896334, 0.385064, 0.0509535, 0.171376, -0.098005, 0.396082, 0.0582611, 0.179901, -0.106817, 0.407418, 0.06654, 0.189892, -0.116239, 0.420031, 0.075994, 0.201838, -0.12627, 0.434321, 0.0867239, 0.214311, -0.136701, 0.447631, 0.0987517, 0.228902, -0.147616, 0.462046, 0.112353, 0.245107, -0.158871, 0.476942, 0.127605, 0.262292, -0.170261, 0.490285, 0.144469, 0.281215, -0.182017, 0.503783, 0.163282, 0.301058, -0.193729, 0.515505, 0.183873, 0.322752, -0.205512, 0.52682, 0.206466, 0.347547, -0.217214, 0.539473, 0.231194, 0.370969, -0.227966, 0.546625, 0.257288, 0.397533, -0.238555, 0.55472, 0.285789, 0.42398, -0.248278, 0.559468, 0.315746, 0.452928, -0.257422, 0.564095, 0.347724, 0.482121, -0.265306, 0.565426, 0.380922, 0.510438, -0.272043, 0.563205, 0.415639, 0.541188, -0.277614, 0.561087, 0.451702, 0.571667, -0.281927, 0.554922, 0.48845, 0.602432, -0.285015, 0.546838, 0.526442, 0.634126, -0.286512, 0.537415, 0.564896, 0.662816, -0.286388, 0.522906, 0.604037, 0.692411, -0.284734, 0.507003, 0.643795, 0.720946, -0.281297, 0.488398, 0.68298, 0.748293, -0.276262, 0.466353, 0.723466, 0.776931, -0.269978, 0.443573, 0.764565, 0.801065, -0.260305, 0.415279, 0.805838, 0.825843, -0.247426, 0.384773, 0.849985, 0.84807, -0.232437, 0.352555, 0.893174, 0.869122, -0.215806, 0.318642, 0.936564, 0.888963, -0.197307, 0.28381, 0.980253, 0.905547, -0.177203, 0.247888, 1.02463, 0.918554, -0.155542, 0.212904, 1.06714, 0.931395, -0.131948, 0.1787, 1.10451, 0.941749, -0.106723, 0.145902, 1.13694, 0.954551, -0.0804939, 0.111193, 1.1666, 0.970279, -0.0534239, 0.0744697, 1.19249, 0.986117, -0.0257452, 0.0368788, 1.21665, 0.999938, 190634e-8, -10291e-7, 1.23981, 0.118493, -647439e-11, 0.32272, 23772e-10, 0.118765, -163023e-9, 0.323456, 598573e-10, 0.118772, -65212e-8, 0.323477, 239447e-9, 0.118843, -146741e-8, 0.323657, 538881e-9, 0.118804, -260846e-8, 0.323553, 95826e-8, 0.118826, -407576e-8, 0.323595, 149845e-8, 0.118846, -586826e-8, 0.323617, 216047e-8, 0.118886, -798578e-8, 0.32367, 294679e-8, 0.118947, -0.0104273, 0.323753, 386124e-8, 0.119055, -0.0131909, 0.323922, 490999e-8, 0.119241, -0.0162444, 0.324251, 610804e-8, 0.11944, -0.0196339, 0.324544, 745805e-8, 0.119739, -0.0233378, 0.325026, 897805e-8, 0.12011, -0.0273179, 0.325586, 0.0106895, 0.120571, -0.0316143, 0.326231, 0.0126073, 0.12124, -0.0361939, 0.327264, 0.0147654, 0.122162, -0.0410511, 0.328733, 0.0172001, 0.123378, -0.0462233, 0.330659, 0.0199375, 0.125183, -0.0517109, 0.333754, 0.0230498, 0.127832, -0.0575652, 0.338507, 0.026597, 0.130909, -0.0637441, 0.343666, 0.0306345, 0.135221, -0.0704302, 0.351063, 0.035273, 0.14082, -0.0776364, 0.360604, 0.0406137, 0.146781, -0.0852293, 0.369638, 0.0466788, 0.155121, -0.0935351, 0.3827, 0.0537628, 0.16398, -0.102234, 0.39522, 0.0617985, 0.173926, -0.111465, 0.40793, 0.07097, 0.185137, -0.121296, 0.42105, 0.0813426, 0.19826, -0.13169, 0.435735, 0.0931596, 0.212938, -0.142614, 0.450932, 0.106547, 0.229046, -0.153884, 0.465726, 0.121575, 0.246246, -0.165382, 0.479461, 0.138286, 0.264637, -0.176806, 0.492106, 0.15666, 0.284959, -0.188793, 0.504774, 0.17728, 0.308157, -0.200763, 0.518805, 0.19988, 0.330951, -0.21239, 0.528231, 0.224293, 0.3549, -0.223521, 0.536376, 0.250541, 0.381502, -0.234169, 0.544846, 0.278902, 0.409529, -0.244077, 0.551717, 0.309227, 0.437523, -0.253363, 0.55517, 0.341426, 0.467624, -0.261659, 0.557772, 0.37518, 0.497268, -0.268498, 0.556442, 0.41007, 0.528294, -0.274018, 0.553915, 0.446445, 0.559053, -0.278169, 0.549153, 0.483779, 0.589329, -0.281229, 0.539878, 0.522249, 0.622503, -0.282902, 0.53162, 0.561754, 0.652382, -0.282815, 0.518119, 0.601544, 0.681847, -0.281247, 0.502187, 0.641574, 0.712285, -0.277986, 0.484824, 0.682633, 0.740094, -0.273017, 0.463483, 0.723426, 0.768478, -0.266692, 0.441299, 0.763747, 0.794556, -0.258358, 0.415238, 0.805565, 0.819408, -0.248807, 0.386912, 0.847254, 0.843411, -0.236214, 0.356165, 0.891091, 0.862397, -0.219794, 0.320562, 0.936174, 0.883113, -0.201768, 0.285322, 0.982562, 0.90023, -0.181672, 0.249713, 1.02862, 0.915192, -0.159279, 0.214546, 1.07163, 0.928458, -0.134725, 0.180285, 1.10995, 0.94069, -0.10913, 0.147119, 1.14354, 0.953409, -0.0821315, 0.112492, 1.17372, 0.969537, -0.0542677, 0.0752014, 1.20043, 0.985612, -0.0259096, 0.0370361, 1.22528, 0.999835, 298198e-8, -151801e-8, 1.24959, 0.10097, -602574e-11, 0.300277, 202619e-11, 0.101577, -152164e-9, 0.302077, 511662e-10, 0.101572, -608889e-9, 0.302066, 204751e-9, 0.101566, -136997e-8, 0.302047, 460753e-9, 0.101592, -243557e-8, 0.302114, 819497e-9, 0.101608, -38053e-7, 0.30214, 128154e-8, 0.101627, -547906e-8, 0.30216, 18483e-7, 0.101669, -745647e-8, 0.302224, 252223e-8, 0.101732, -973615e-8, 0.302318, 330716e-8, 0.101844, -0.0123097, 0.302513, 421061e-8, 0.102025, -0.0151681, 0.30285, 524481e-8, 0.102224, -0.0183334, 0.303166, 64154e-7, 0.102515, -0.0217819, 0.303654, 774063e-8, 0.102886, -0.0255067, 0.304243, 92398e-7, 0.103395, -0.029514, 0.305089, 0.0109339, 0.104109, -0.0337912, 0.306301, 0.0128561, 0.105074, -0.0383565, 0.30798, 0.0150338, 0.10654, -0.0432132, 0.310726, 0.0175228, 0.108478, -0.0484244, 0.314351, 0.0203648, 0.111015, -0.0539339, 0.319032, 0.0236325, 0.114682, -0.0598885, 0.32605, 0.0274188, 0.11911, -0.0663375, 0.334109, 0.0317905, 0.124736, -0.0733011, 0.344013, 0.0368502, 0.131479, -0.0807744, 0.355358, 0.0427104, 0.139283, -0.0888204, 0.367614, 0.0494788, 0.148054, -0.0973394, 0.380072, 0.0572367, 0.159037, -0.10665, 0.395678, 0.0662704, 0.169794, -0.116221, 0.40795, 0.0763192, 0.18314, -0.126632, 0.423546, 0.087956, 0.197515, -0.137383, 0.438213, 0.101042, 0.213514, -0.148641, 0.453248, 0.115827, 0.23065, -0.160117, 0.46688, 0.132283, 0.249148, -0.171807, 0.479962, 0.150644, 0.270219, -0.183695, 0.494618, 0.171073, 0.292338, -0.195574, 0.506937, 0.193378, 0.314999, -0.207205, 0.516463, 0.217585, 0.340991, -0.218955, 0.528123, 0.24428, 0.367982, -0.229917, 0.537025, 0.272784, 0.39432, -0.239737, 0.541627, 0.302742, 0.423364, -0.249048, 0.546466, 0.335112, 0.453751, -0.257329, 0.549466, 0.369032, 0.48416, -0.264623, 0.549503, 0.404577, 0.515262, -0.270411, 0.547008, 0.441337, 0.547036, -0.274581, 0.542249, 0.479162, 0.576614, -0.277266, 0.533015, 0.517904, 0.611143, -0.279144, 0.525512, 0.558508, 0.640989, -0.279001, 0.51154, 0.598995, 0.671182, -0.277324, 0.495641, 0.639935, 0.700848, -0.273908, 0.477526, 0.681017, 0.729862, -0.269063, 0.457955, 0.722764, 0.758273, -0.262282, 0.434846, 0.764349, 0.784121, -0.254281, 0.409203, 0.806206, 0.809798, -0.24505, 0.382694, 0.848617, 0.834953, -0.233861, 0.354034, 0.892445, 0.856817, -0.221308, 0.321764, 0.936263, 0.877609, -0.205996, 0.288118, 0.982401, 0.897489, -0.186702, 0.253277, 1.02975, 0.913792, -0.164618, 0.217963, 1.07488, 0.92785, -0.140023, 0.183221, 1.11487, 0.940378, -0.11328, 0.149385, 1.14947, 0.95273, -0.0853958, 0.114152, 1.1807, 0.969059, -0.0568698, 0.0769845, 1.20912, 0.985574, -0.0276502, 0.0381186, 1.23498, 0.999943, 239052e-8, -126861e-8, 1.25987, 0.0852715, -560067e-11, 0.279021, 171162e-11, 0.0854143, -140871e-9, 0.279483, 430516e-10, 0.0854191, -563385e-9, 0.2795, 172184e-9, 0.0854188, -126753e-8, 0.279493, 387464e-9, 0.0854229, -225337e-8, 0.279501, 68918e-8, 0.0854443, -352086e-8, 0.279549, 107803e-8, 0.0854697, -506962e-8, 0.279591, 155536e-8, 0.0855093, -689873e-8, 0.279652, 212354e-8, 0.0855724, -900821e-8, 0.279752, 278703e-8, 0.0856991, -0.0113799, 0.280011, 35551e-7, 0.085855, -0.0140314, 0.280297, 443449e-8, 0.0860682, -0.016963, 0.280682, 543636e-8, 0.086344, -0.0201438, 0.281159, 65788e-7, 0.0867426, -0.0235999, 0.281886, 787977e-8, 0.087239, -0.0273069, 0.282745, 93606e-7, 0.0879815, -0.031269, 0.284139, 0.011056, 0.0891258, -0.035531, 0.28647, 0.0130065, 0.0906909, -0.0400947, 0.289708, 0.0152495, 0.0927624, -0.0449638, 0.293904, 0.0178454, 0.0958376, -0.0502427, 0.300471, 0.0208915, 0.0995827, -0.0559514, 0.30806, 0.0244247, 0.104526, -0.0622152, 0.317874, 0.0285721, 0.110532, -0.0690046, 0.329332, 0.0334227, 0.117385, -0.0763068, 0.341217, 0.0390466, 0.12522, -0.084184, 0.353968, 0.0455786, 0.134037, -0.0925248, 0.366797, 0.0530773, 0.144014, -0.101487, 0.380209, 0.0617424, 0.156013, -0.111273, 0.395956, 0.071777, 0.168872, -0.121431, 0.41053, 0.0830905, 0.183089, -0.132105, 0.425073, 0.0959341, 0.198763, -0.143286, 0.439833, 0.110448, 0.216159, -0.154841, 0.454507, 0.126769, 0.234859, -0.166588, 0.468368, 0.14495, 0.255879, -0.178626, 0.482846, 0.165233, 0.27677, -0.190218, 0.493489, 0.187217, 0.301184, -0.202227, 0.506549, 0.211659, 0.325852, -0.213764, 0.5158, 0.237922, 0.352824, -0.22487, 0.525442, 0.26632, 0.380882, -0.235246, 0.532487, 0.296691, 0.410137, -0.244847, 0.537703, 0.329179, 0.439787, -0.253122, 0.540361, 0.363135, 0.472291, -0.260517, 0.542734, 0.399222, 0.501856, -0.266519, 0.538826, 0.436352, 0.534816, -0.270905, 0.535152, 0.474505, 0.565069, -0.273826, 0.525979, 0.513988, 0.597154, -0.275333, 0.516394, 0.554852, 0.630473, -0.275314, 0.506206, 0.596592, 0.660574, -0.273323, 0.489769, 0.638117, 0.692015, -0.270008, 0.472578, 0.680457, 0.720647, -0.265001, 0.452134, 0.723008, 0.750528, -0.258311, 0.430344, 0.765954, 0.777568, -0.250046, 0.405624, 0.809012, 0.80387, -0.240114, 0.378339, 0.852425, 0.828439, -0.228737, 0.349877, 0.895346, 0.851472, -0.216632, 0.318968, 0.940695, 0.873906, -0.202782, 0.287489, 0.987235, 0.89467, -0.187059, 0.254394, 1.03348, 0.912281, -0.168818, 0.221294, 1.07812, 0.927358, -0.146494, 0.18675, 1.11928, 0.940385, -0.120009, 0.152322, 1.15609, 0.952672, -0.0917183, 0.117514, 1.18875, 0.968496, -0.0620321, 0.0797405, 1.21821, 0.985236, -0.0314945, 0.0402383, 1.24523, 0.99998, -575153e-9, 110644e-9, 1.27133, 0.0702429, -512222e-11, 0.255273, 140947e-11, 0.0702981, -128826e-9, 0.255469, 354488e-10, 0.0703691, -515562e-9, 0.255727, 141874e-9, 0.0703805, -116e-5, 0.255754, 31929e-8, 0.0703961, -206224e-8, 0.255813, 567999e-9, 0.0704102, -322223e-8, 0.255839, 88871e-8, 0.0704298, -463928e-8, 0.255863, 128272e-8, 0.0704759, -631375e-8, 0.255953, 175283e-8, 0.0705434, -824317e-8, 0.256079, 230342e-8, 0.0706693, -0.010412, 0.25636, 29443e-7, 0.0708189, -0.0128439, 0.256647, 368031e-8, 0.0710364, -0.0155177, 0.257084, 452614e-8, 0.0713223, -0.0184374, 0.257637, 549706e-8, 0.0717182, -0.0216002, 0.258416, 661246e-8, 0.072321, -0.0249966, 0.259699, 790147e-8, 0.0731446, -0.0286566, 0.261475, 93884e-7, 0.0743352, -0.0325888, 0.264132, 0.0111186, 0.0760676, -0.036843, 0.26815, 0.013145, 0.078454, -0.0414292, 0.273636, 0.0155251, 0.0818618, -0.0464634, 0.281653, 0.0183525, 0.0857382, -0.0519478, 0.289992, 0.0216642, 0.0908131, -0.0579836, 0.30066, 0.0255956, 0.0967512, -0.0645124, 0.312204, 0.0301954, 0.103717, -0.0716505, 0.325001, 0.0356017, 0.111596, -0.0793232, 0.338129, 0.041896, 0.120933, -0.087645, 0.352853, 0.0492447, 0.130787, -0.096492, 0.366192, 0.0576749, 0.142311, -0.105973, 0.380864, 0.0673969, 0.155344, -0.116182, 0.396575, 0.0785899, 0.169535, -0.126815, 0.411443, 0.0912377, 0.185173, -0.138015, 0.426256, 0.105607, 0.201755, -0.149325, 0.439607, 0.121551, 0.221334, -0.161207, 0.455467, 0.139608, 0.241461, -0.173162, 0.469096, 0.159591, 0.26294, -0.18504, 0.481014, 0.18156, 0.286776, -0.196881, 0.493291, 0.205781, 0.311596, -0.208311, 0.503556, 0.231819, 0.338667, -0.219671, 0.513268, 0.260274, 0.366021, -0.230451, 0.519414, 0.290862, 0.395875, -0.240131, 0.526766, 0.323196, 0.425564, -0.248566, 0.52905, 0.357071, 0.457094, -0.256195, 0.530796, 0.393262, 0.488286, -0.262331, 0.528703, 0.430797, 0.522291, -0.267141, 0.52727, 0.470231, 0.554172, -0.270411, 0.519848, 0.510477, 0.586427, -0.271986, 0.510307, 0.551594, 0.619638, -0.27192, 0.499158, 0.593849, 0.650656, -0.269817, 0.483852, 0.636314, 0.68284, -0.266267, 0.467515, 0.679679, 0.714356, -0.26113, 0.44931, 0.723884, 0.742717, -0.254067, 0.425789, 0.767245, 0.770894, -0.245652, 0.401144, 0.811819, 0.797358, -0.235554, 0.374224, 0.856315, 0.823377, -0.223896, 0.346167, 0.901077, 0.847456, -0.210865, 0.316056, 0.946502, 0.870697, -0.196574, 0.284503, 0.993711, 0.891068, -0.180814, 0.251628, 1.04134, 0.909267, -0.163314, 0.219065, 1.08609, 0.925653, -0.143304, 0.186446, 1.12702, 0.940017, -0.121322, 0.153416, 1.16371, 0.952398, -0.0973872, 0.120334, 1.19712, 0.967568, -0.0698785, 0.08352, 1.22791, 0.984772, -0.0390031, 0.0439209, 1.25672, 1.00026, -70087e-7, 315668e-8, 1.28428, 0.0556653, -459654e-11, 0.227325, 112556e-11, 0.0565238, -116382e-9, 0.230826, 284985e-10, 0.0565717, -465666e-9, 0.231026, 114036e-9, 0.0565859, -104773e-8, 0.231079, 256656e-9, 0.0565761, -186255e-8, 0.231025, 45663e-8, 0.0565913, -291002e-8, 0.231058, 714664e-9, 0.0566108, -418998e-8, 0.231085, 103224e-8, 0.0566532, -570206e-8, 0.231169, 141202e-8, 0.0567473, -743666e-8, 0.231417, 186018e-8, 0.0568567, -940298e-8, 0.231661, 238264e-8, 0.0569859, -0.0115991, 0.231895, 298699e-8, 0.0572221, -0.0140096, 0.232456, 368957e-8, 0.057519, -0.0166508, 0.233096, 450303e-8, 0.0579534, -0.01951, 0.234094, 544945e-8, 0.0585922, -0.0225991, 0.235629, 655564e-8, 0.0595647, -0.0259416, 0.238106, 785724e-8, 0.0609109, -0.0295661, 0.241557, 939127e-8, 0.0628751, -0.0335126, 0.246652, 0.0112198, 0.0656908, -0.0378604, 0.254091, 0.0134168, 0.0691347, -0.0426543, 0.262666, 0.0160374, 0.0732165, -0.0478967, 0.272029, 0.0191514, 0.0782863, -0.0536716, 0.283007, 0.0228597, 0.0843973, -0.0600683, 0.295732, 0.0272829, 0.0913598, -0.0670095, 0.308779, 0.032484, 0.0994407, -0.0745516, 0.322886, 0.0385886, 0.108189, -0.082712, 0.336408, 0.0457133, 0.118574, -0.0914927, 0.351692, 0.0539832, 0.129989, -0.100854, 0.366502, 0.0635162, 0.142722, -0.110837, 0.381675, 0.0744386, 0.156654, -0.121353, 0.3963, 0.0868483, 0.172151, -0.132414, 0.411477, 0.100963, 0.188712, -0.143809, 0.42508, 0.116795, 0.208093, -0.155765, 0.441328, 0.134715, 0.227936, -0.167608, 0.454328, 0.154396, 0.249495, -0.179579, 0.467235, 0.176179, 0.27362, -0.191488, 0.480248, 0.200193, 0.296371, -0.202618, 0.487886, 0.225775, 0.324234, -0.214133, 0.499632, 0.25441, 0.353049, -0.225212, 0.509532, 0.285077, 0.381785, -0.234875, 0.514265, 0.317047, 0.414038, -0.244205, 0.521282, 0.351874, 0.445251, -0.252145, 0.522931, 0.388279, 0.476819, -0.258433, 0.520947, 0.425825, 0.509209, -0.263411, 0.517669, 0.465104, 0.542759, -0.266732, 0.512841, 0.505741, 0.574822, -0.268263, 0.503317, 0.547611, 0.609324, -0.268489, 0.493035, 0.590953, 0.641772, -0.266941, 0.478816, 0.63488, 0.674049, -0.263297, 0.462863, 0.679072, 0.705071, -0.257618, 0.442931, 0.723487, 0.734709, -0.250625, 0.421299, 0.768708, 0.763704, -0.24179, 0.397085, 0.814375, 0.791818, -0.231115, 0.370577, 0.859907, 0.817439, -0.21922, 0.34232, 0.906715, 0.843202, -0.205658, 0.312627, 0.953943, 0.866639, -0.190563, 0.280933, 1.00185, 0.888129, -0.173978, 0.248393, 1.05105, 0.907239, -0.155485, 0.216007, 1.09704, 0.923893, -0.134782, 0.183233, 1.13857, 0.938882, -0.11249, 0.150376, 1.17539, 0.952464, -0.0890706, 0.117177, 1.20924, 0.968529, -0.0646523, 0.0813095, 1.24055, 0.984763, -0.038606, 0.0439378, 1.27018, 1.00053, -0.01238, 598668e-8, 1.29873, 0.0437928, -409594e-11, 0.204012, 879224e-12, 0.0440166, -103395e-9, 0.205049, 221946e-10, 0.0440529, -413633e-9, 0.205225, 887981e-10, 0.0440493, -930594e-9, 0.2052, 199858e-9, 0.0439884, -165352e-8, 0.204901, 355495e-9, 0.0440716, -25849e-7, 0.205255, 556983e-9, 0.0440968, -372222e-8, 0.205311, 805326e-9, 0.0441359, -506478e-8, 0.205391, 110333e-8, 0.0442231, -660384e-8, 0.205638, 145768e-8, 0.0443254, -835246e-8, 0.205877, 187275e-8, 0.0444832, -0.0102992, 0.20627, 235938e-8, 0.0447001, -0.0124449, 0.206796, 29299e-7, 0.0450168, -0.0147935, 0.207593, 36005e-7, 0.0454816, -0.017336, 0.208819, 439246e-8, 0.0462446, -0.0201156, 0.211036, 533864e-8, 0.0473694, -0.0231568, 0.214388, 646984e-8, 0.0490191, -0.0264941, 0.219357, 783856e-8, 0.0512776, -0.030184, 0.226061, 950182e-8, 0.0541279, -0.0342661, 0.234094, 0.0115156, 0.0578989, -0.0388539, 0.244297, 0.0139687, 0.0620835, -0.0438735, 0.254457, 0.0169015, 0.0673497, -0.04951, 0.266706, 0.0204554, 0.0731759, -0.0556263, 0.278753, 0.0246606, 0.0803937, -0.0624585, 0.29309, 0.0297126, 0.0879287, -0.0697556, 0.305856, 0.0355868, 0.0970669, -0.0778795, 0.321059, 0.0425768, 0.106508, -0.0863541, 0.333873, 0.05056, 0.11776, -0.0955935, 0.349008, 0.0598972, 0.130081, -0.105438, 0.363776, 0.0706314, 0.144454, -0.115899, 0.380112, 0.0828822, 0.1596, -0.126827, 0.394843, 0.0967611, 0.176097, -0.138161, 0.409033, 0.112381, 0.194726, -0.149904, 0.424257, 0.129952, 0.213944, -0.161675, 0.436945, 0.149333, 0.235516, -0.173659, 0.450176, 0.170892, 0.260564, -0.185963, 0.466305, 0.194984, 0.285183, -0.197582, 0.477328, 0.220805, 0.311095, -0.208697, 0.486566, 0.248694, 0.338924, -0.219519, 0.494811, 0.279015, 0.369757, -0.229766, 0.504065, 0.311725, 0.3996, -0.238879, 0.507909, 0.345844, 0.430484, -0.246802, 0.509805, 0.381749, 0.46413, -0.253924, 0.511436, 0.420251, 0.497077, -0.259319, 0.508787, 0.459957, 0.530434, -0.263297, 0.50394, 0.501356, 0.565725, -0.265619, 0.49804, 0.544252, 0.599254, -0.265842, 0.487346, 0.587856, 0.631251, -0.263978, 0.472975, 0.631969, 0.663972, -0.26043, 0.457135, 0.677471, 0.697724, -0.255358, 0.439844, 0.723744, 0.727725, -0.248308, 0.417872, 0.770653, 0.756417, -0.239181, 0.39273, 0.817357, 0.785419, -0.22814, 0.367839, 0.864221, 0.81266, -0.215681, 0.339449, 0.912701, 0.839391, -0.201623, 0.309279, 0.962419, 0.86366, -0.185624, 0.278029, 1.0122, 0.885028, -0.16797, 0.245294, 1.06186, 0.904639, -0.148336, 0.212689, 1.10934, 0.922048, -0.12637, 0.179616, 1.15063, 0.936952, -0.102928, 0.146749, 1.18885, 0.951895, -0.0785268, 0.112733, 1.22352, 0.967198, -0.0530153, 0.0760056, 1.25681, 0.984405, -0.02649, 0.0383183, 1.28762, 1.00021, 70019e-8, -20039e-8, 1.31656, 0.0325964, -355447e-11, 0.176706, 655682e-12, 0.0329333, -899174e-10, 0.178527, 165869e-10, 0.0329181, -359637e-9, 0.178453, 663498e-10, 0.0329085, -808991e-9, 0.178383, 149332e-9, 0.0329181, -143826e-8, 0.178394, 265873e-9, 0.0329425, -224678e-8, 0.178517, 416597e-9, 0.0329511, -323575e-8, 0.17849, 603299e-9, 0.033011, -439875e-8, 0.178695, 829422e-9, 0.0330733, -574059e-8, 0.178843, 109908e-8, 0.0331857, -725896e-8, 0.179176, 141933e-8, 0.0333445, -895289e-8, 0.179618, 17999e-7, 0.0335674, -0.0108219, 0.180238, 225316e-8, 0.033939, -0.0128687, 0.181417, 279765e-8, 0.0345239, -0.015114, 0.183395, 34564e-7, 0.0354458, -0.017596, 0.186616, 425864e-8, 0.0368313, -0.0203524, 0.191547, 524936e-8, 0.0386115, -0.0234105, 0.197508, 647033e-8, 0.0410303, -0.0268509, 0.205395, 798121e-8, 0.0442245, -0.0307481, 0.215365, 98557e-7, 0.0478659, -0.0350863, 0.225595, 0.0121417, 0.0522416, -0.0399506, 0.236946, 0.0149385, 0.0574513, -0.045357, 0.249442, 0.0183189, 0.0631208, -0.0512863, 0.261222, 0.0223644, 0.0701124, -0.0579273, 0.275418, 0.0272418, 0.0777331, -0.0650652, 0.288989, 0.0329458, 0.0862709, -0.0728813, 0.302546, 0.0396819, 0.096103, -0.081363, 0.317164, 0.04757, 0.106976, -0.0904463, 0.331733, 0.0567012, 0.119175, -0.100105, 0.34661, 0.067202, 0.132919, -0.110375, 0.362249, 0.0792588, 0.147727, -0.121115, 0.376978, 0.0928672, 0.163618, -0.132299, 0.390681, 0.108228, 0.182234, -0.143887, 0.406571, 0.125502, 0.201809, -0.155827, 0.42042, 0.144836, 0.225041, -0.168357, 0.438411, 0.166706, 0.247621, -0.18004, 0.450368, 0.189909, 0.27097, -0.191536, 0.460083, 0.215251, 0.296658, -0.203024, 0.469765, 0.243164, 0.325892, -0.214056, 0.481837, 0.273388, 0.35406, -0.224104, 0.487474, 0.305344, 0.384372, -0.233489, 0.492773, 0.339741, 0.41749, -0.241874, 0.498451, 0.376287, 0.45013, -0.248834, 0.499632, 0.414195, 0.481285, -0.254658, 0.495233, 0.454077, 0.519183, -0.259367, 0.496401, 0.496352, 0.551544, -0.261818, 0.487686, 0.538798, 0.587349, -0.262964, 0.479453, 0.583626, 0.621679, -0.262128, 0.467709, 0.629451, 0.654991, -0.258998, 0.452123, 0.67566, 0.686873, -0.254119, 0.433495, 0.723248, 0.719801, -0.246946, 0.413657, 0.771156, 0.750355, -0.237709, 0.390366, 0.81989, 0.780033, -0.226549, 0.364947, 0.868601, 0.809254, -0.214186, 0.337256, 0.920034, 0.836576, -0.199639, 0.307395, 0.971706, 0.861774, -0.183169, 0.275431, 1.02479, 0.885707, -0.165111, 0.243431, 1.07837, 0.904742, -0.144363, 0.210921, 1.12783, 0.915604, -0.121305, 0.17647, 1.17254, 0.930959, -0.0962119, 0.143106, 1.21012, 0.948404, -0.069969, 0.108112, 1.24474, 0.967012, -0.0427586, 0.0708478, 1.27718, 0.984183, -0.0147043, 0.032335, 1.3083, 0.999577, 0.0142165, -726867e-8, 1.3382, 0.0229227, -299799e-11, 0.148623, 462391e-12, 0.0232194, -758796e-10, 0.15054, 117033e-10, 0.0232315, -303636e-9, 0.15063, 468397e-10, 0.0232354, -683189e-9, 0.150624, 105472e-9, 0.0232092, -12136e-7, 0.150445, 187744e-9, 0.0232523, -189765e-8, 0.150679, 294847e-9, 0.0232828, -273247e-8, 0.150789, 428013e-9, 0.0233371, -371287e-8, 0.150995, 591134e-9, 0.0234015, -484794e-8, 0.15118, 787642e-9, 0.023514, -612877e-8, 0.151562, 102547e-8, 0.023679, -756125e-8, 0.152116, 131351e-8, 0.0239559, -914651e-8, 0.153162, 166594e-8, 0.0244334, -0.010904, 0.155133, 210182e-8, 0.025139, -0.0128615, 0.158035, 264406e-8, 0.0262598, -0.0150628, 0.162751, 332923e-8, 0.0277875, -0.0175532, 0.168944, 419773e-8, 0.0298472, -0.0203981, 0.176835, 530034e-8, 0.0325444, -0.023655, 0.186686, 669777e-8, 0.0355581, -0.0272982, 0.196248, 842661e-8, 0.0392841, -0.0314457, 0.207352, 0.0105854, 0.0436815, -0.0361157, 0.219279, 0.0132458, 0.0485272, -0.0412932, 0.230728, 0.0164736, 0.0541574, -0.0470337, 0.242994, 0.0203715, 0.0609479, -0.0535002, 0.257042, 0.0250953, 0.0685228, -0.0605409, 0.27102, 0.0306856, 0.0768042, -0.0680553, 0.28406, 0.037193, 0.0864844, -0.0765011, 0.299186, 0.0449795, 0.0969415, -0.0852674, 0.3132, 0.0538316, 0.108478, -0.0947333, 0.327138, 0.0641149, 0.121705, -0.10481, 0.342345, 0.0759185, 0.136743, -0.115474, 0.358472, 0.0894116, 0.152986, -0.126536, 0.374067, 0.104562, 0.170397, -0.138061, 0.388267, 0.121632, 0.191392, -0.150203, 0.406467, 0.140996, 0.211566, -0.161751, 0.418641, 0.161696, 0.233567, -0.173407, 0.430418, 0.184557, 0.257769, -0.185397, 0.44277, 0.210092, 0.28531, -0.197048, 0.457191, 0.237827, 0.311726, -0.20784, 0.464712, 0.267253, 0.340537, -0.218345, 0.472539, 0.299332, 0.372921, -0.228306, 0.482331, 0.333988, 0.402924, -0.236665, 0.484378, 0.369722, 0.434475, -0.244097, 0.484717, 0.407836, 0.469736, -0.250547, 0.487093, 0.448465, 0.505045, -0.25511, 0.485575, 0.490263, 0.540262, -0.258444, 0.481225, 0.534495, 0.576347, -0.259903, 0.473481, 0.579451, 0.608656, -0.259572, 0.4603, 0.625604, 0.646679, -0.257908, 0.450341, 0.674511, 0.679902, -0.253663, 0.431561, 0.723269, 0.714159, -0.247419, 0.412684, 0.773263, 0.745345, -0.239122, 0.389388, 0.824182, 0.778248, -0.228837, 0.365361, 0.876634, 0.807208, -0.216197, 0.337667, 0.92945, 0.835019, -0.201772, 0.307197, 0.985261, 0.860261, -0.185291, 0.274205, 1.04299, 0.877601, -0.165809, 0.240178, 1.09816, 0.898211, -0.143897, 0.207571, 1.14694, 0.915789, -0.119513, 0.174904, 1.19008, 0.931831, -0.0932919, 0.141423, 1.2297, 0.949244, -0.0656528, 0.105603, 1.26553, 0.967527, -0.0370262, 0.0679551, 1.29986, 0.984139, -730117e-8, 0.0283133, 1.33252, 0.999713, 0.0234648, -0.0121785, 1.36397, 0.0152135, -245447e-11, 0.122795, 304092e-12, 0.0151652, -615778e-10, 0.122399, 76292e-10, 0.0151181, -245948e-9, 0.122023, 304802e-10, 0.0151203, -553394e-9, 0.12203, 686634e-10, 0.015125, -983841e-9, 0.122037, 122463e-9, 0.0151427, -153774e-8, 0.12214, 192706e-9, 0.0151708, -22103e-7, 0.122237, 281219e-9, 0.0152115, -300741e-8, 0.12238, 390804e-9, 0.0152877, -392494e-8, 0.1227, 526317e-9, 0.015412, -496597e-8, 0.123244, 69443e-8, 0.0156201, -613314e-8, 0.124228, 90547e-8, 0.0159658, -744113e-8, 0.125945, 11732e-7, 0.0165674, -892546e-8, 0.129098, 151888e-8, 0.017487, -0.010627, 0.133865, 197007e-8, 0.018839, -0.0126043, 0.140682, 25637e-7, 0.020554, -0.0148814, 0.148534, 333637e-8, 0.0226727, -0.0175123, 0.157381, 433738e-8, 0.0251879, -0.0205266, 0.166685, 561664e-8, 0.0283635, -0.0240319, 0.177796, 725563e-8, 0.0318694, -0.0279432, 0.188251, 928811e-8, 0.0361044, -0.0324313, 0.200038, 0.011835, 0.0406656, -0.0373527, 0.210685, 0.0149146, 0.0463846, -0.0430132, 0.224182, 0.0187254, 0.0525696, -0.0491013, 0.23634, 0.0232283, 0.0598083, -0.0559175, 0.250013, 0.0286521, 0.0679437, -0.0633657, 0.263981, 0.0350634, 0.0771181, -0.0714602, 0.278072, 0.0425882, 0.0881273, -0.0803502, 0.29511, 0.0514487, 0.0996628, -0.0896903, 0.309976, 0.0615766, 0.112702, -0.099644, 0.325611, 0.0732139, 0.126488, -0.109829, 0.339321, 0.0862324, 0.142625, -0.120859, 0.35574, 0.101275, 0.15953, -0.131956, 0.369845, 0.117892, 0.176991, -0.143145, 0.38146, 0.136205, 0.199715, -0.155292, 0.40052, 0.157252, 0.220787, -0.167066, 0.412055, 0.179966, 0.243697, -0.178396, 0.423133, 0.204418, 0.272106, -0.190433, 0.439524, 0.232141, 0.297637, -0.201265, 0.447041, 0.261109, 0.325273, -0.211834, 0.454488, 0.292627, 0.357219, -0.221889, 0.465004, 0.326669, 0.387362, -0.230729, 0.468527, 0.362426, 0.423131, -0.23924, 0.475836, 0.401533, 0.45543, -0.246067, 0.475017, 0.441902, 0.493393, -0.251557, 0.478017, 0.484239, 0.526253, -0.255571, 0.4709, 0.528586, 0.560554, -0.257752, 0.463167, 0.574346, 0.599306, -0.258076, 0.456452, 0.621655, 0.634541, -0.256471, 0.443725, 0.670492, 0.668907, -0.253283, 0.428719, 0.721943, 0.705619, -0.247562, 0.411348, 0.772477, 0.739034, -0.240626, 0.388939, 0.8264, 0.771408, -0.231493, 0.36425, 0.881702, 0.803312, -0.220125, 0.337321, 0.9385, 0.828457, -0.206645, 0.305364, 0.997437, 0.854819, -0.190664, 0.273715, 1.05693, 0.878666, -0.171429, 0.242218, 1.11251, 0.898404, -0.149235, 0.209556, 1.16398, 0.917416, -0.12435, 0.176863, 1.21014, 0.933133, -0.0972703, 0.142775, 1.25178, 0.95066, -0.0683607, 0.106735, 1.29028, 0.968589, -0.0378724, 0.0681609, 1.32703, 0.984776, -605712e-8, 0.0273966, 1.36158, 0.99994, 0.0263276, -0.0138124, 1.3943, 867437e-8, -186005e-11, 0.0928979, 173682e-12, 864003e-8, -466389e-10, 0.0925237, 435505e-11, 864593e-8, -186594e-9, 0.0925806, 174322e-10, 864095e-8, -419639e-9, 0.0924903, 392862e-10, 863851e-8, -746272e-9, 0.0924589, 702598e-10, 868531e-8, -116456e-8, 0.0929, 111188e-9, 869667e-8, -167711e-8, 0.0928529, 163867e-9, 874332e-8, -228051e-8, 0.0930914, 23104e-8, 882709e-8, -297864e-8, 0.0935679, 31741e-8, 898874e-8, -377557e-8, 0.0946165, 430186e-9, 929346e-8, -469247e-8, 0.0967406, 580383e-9, 978271e-8, -575491e-8, 0.100084, 783529e-9, 0.0105746, -701514e-8, 0.105447, 106304e-8, 0.0116949, -851797e-8, 0.112494, 144685e-8, 0.0130419, -0.0102757, 0.119876, 196439e-8, 0.0148375, -0.012381, 0.129034, 266433e-8, 0.0168725, -0.01482, 0.137812, 358364e-8, 0.0193689, -0.0176563, 0.147696, 478132e-8, 0.0222691, -0.0209211, 0.157795, 631721e-8, 0.0256891, -0.0246655, 0.168431, 826346e-8, 0.0294686, -0.0288597, 0.178587, 0.0106714, 0.0340412, -0.0336441, 0.190251, 0.0136629, 0.0393918, -0.039033, 0.202999, 0.0173272, 0.0453947, -0.0450087, 0.215655, 0.0217448, 0.0521936, -0.0515461, 0.228686, 0.0269941, 0.0600279, -0.058817, 0.242838, 0.033272, 0.0692398, -0.0667228, 0.258145, 0.0406457, 0.0793832, -0.0752401, 0.273565, 0.0492239, 0.0902297, -0.0841851, 0.287735, 0.0590105, 0.102014, -0.0936479, 0.301161, 0.0702021, 0.116054, -0.103967, 0.317438, 0.0832001, 0.13191, -0.114622, 0.334166, 0.0977951, 0.148239, -0.125452, 0.348192, 0.113985, 0.165809, -0.136453, 0.361094, 0.131928, 0.184616, -0.147648, 0.373534, 0.151811, 0.207491, -0.159607, 0.39101, 0.174476, 0.230106, -0.171119, 0.402504, 0.198798, 0.257036, -0.182906, 0.418032, 0.225796, 0.281172, -0.193605, 0.425468, 0.254027, 0.312034, -0.204771, 0.440379, 0.285713, 0.340402, -0.214988, 0.445406, 0.319196, 0.370231, -0.224711, 0.44968, 0.35537, 0.407105, -0.233516, 0.460747, 0.393838, 0.439037, -0.240801, 0.460624, 0.433747, 0.47781, -0.24762, 0.465957, 0.477234, 0.510655, -0.251823, 0.460054, 0.52044, 0.550584, -0.255552, 0.459172, 0.567853, 0.585872, -0.257036, 0.450311, 0.615943, 0.620466, -0.257535, 0.437763, 0.667693, 0.660496, -0.255248, 0.426639, 0.718988, 0.695578, -0.251141, 0.409185, 0.772503, 0.732176, -0.244718, 0.39015, 0.827023, 0.760782, -0.236782, 0.362594, 0.885651, 0.79422, -0.225923, 0.33711, 0.943756, 0.824521, -0.213855, 0.308272, 1.00874, 0.854964, -0.197723, 0.278529, 1.06764, 0.878065, -0.179209, 0.246208, 1.12836, 0.899834, -0.157569, 0.21329, 1.18318, 0.918815, -0.133206, 0.181038, 1.23161, 0.934934, -0.106545, 0.146993, 1.27644, 0.952115, -0.0780574, 0.111175, 1.31842, 0.96906, -0.0478279, 0.0728553, 1.35839, 0.985178, -0.0160014, 0.032579, 1.39697, 1.00039, 0.0173126, -95256e-7, 1.43312, 384146e-8, -124311e-11, 0.0613583, 778271e-13, 390023e-8, -314043e-10, 0.0622919, 196626e-11, 389971e-8, -125622e-9, 0.0622632, 787379e-11, 389491e-8, -282352e-9, 0.0620659, 1778e-8, 391618e-8, -502512e-9, 0.0624687, 320918e-10, 392662e-8, -784458e-9, 0.0625113, 515573e-10, 396053e-8, -112907e-8, 0.0628175, 778668e-10, 401911e-8, -153821e-8, 0.0633286, 113811e-9, 414994e-8, -20208e-7, 0.0646443, 16445e-8, 441223e-8, -260007e-8, 0.0673886, 237734e-9, 484427e-8, -33097e-7, 0.0716528, 345929e-9, 549109e-8, -418966e-8, 0.0774998, 505987e-9, 636293e-8, -527331e-8, 0.0844758, 739208e-9, 746566e-8, -660428e-8, 0.0921325, 107347e-8, 876625e-8, -818826e-8, 0.0997067, 153691e-8, 0.0103125, -0.0100811, 0.107433, 217153e-8, 0.0123309, -0.0123643, 0.117088, 303427e-8, 0.0146274, -0.0150007, 0.126438, 416018e-8, 0.0172295, -0.0180531, 0.135672, 561513e-8, 0.0204248, -0.0215962, 0.146244, 7478e-6, 0.0241597, -0.0256234, 0.157481, 981046e-8, 0.0284693, -0.0302209, 0.169125, 0.0127148, 0.033445, -0.0353333, 0.181659, 0.0162453, 0.0391251, -0.0410845, 0.1944, 0.0205417, 0.0454721, -0.0473451, 0.207082, 0.0256333, 0.0530983, -0.0542858, 0.221656, 0.0317036, 0.0615356, -0.0618384, 0.236036, 0.0388319, 0.0703363, -0.0697631, 0.248398, 0.046974, 0.0810391, -0.0784757, 0.263611, 0.0565246, 0.0920144, -0.0873488, 0.275857, 0.0671724, 0.105584, -0.0973652, 0.292555, 0.0798105, 0.119506, -0.107271, 0.306333, 0.0935945, 0.134434, -0.117608, 0.318888, 0.109106, 0.153399, -0.128938, 0.337552, 0.127074, 0.171258, -0.139944, 0.349955, 0.14643, 0.191059, -0.151288, 0.361545, 0.168, 0.215069, -0.163018, 0.378421, 0.192082, 0.237838, -0.174226, 0.38879, 0.217838, 0.266965, -0.186063, 0.405857, 0.246931, 0.292827, -0.196909, 0.414146, 0.277505, 0.324352, -0.207473, 0.426955, 0.310711, 0.354427, -0.217713, 0.433429, 0.346794, 0.389854, -0.227183, 0.443966, 0.385237, 0.420749, -0.235131, 0.44471, 0.424955, 0.459597, -0.242786, 0.451729, 0.468446, 0.495316, -0.248767, 0.45072, 0.513422, 0.534903, -0.253351, 0.450924, 0.560618, 0.572369, -0.256277, 0.445266, 0.609677, 0.612383, -0.2576, 0.438798, 0.660995, 0.644037, -0.256931, 0.421693, 0.713807, 0.686749, -0.254036, 0.4109, 0.767616, 0.719814, -0.249785, 0.390151, 0.82533, 0.754719, -0.244283, 0.367847, 0.888311, 0.792022, -0.235076, 0.345013, 0.948177, 0.822404, -0.225061, 0.316193, 1.01661, 0.853084, -0.211113, 0.287013, 1.08075, 0.879871, -0.19449, 0.255424, 1.14501, 0.901655, -0.174023, 0.222879, 1.20203, 0.919957, -0.1509, 0.18989, 1.25698, 0.938412, -0.124923, 0.15606, 1.30588, 0.953471, -0.0968139, 0.120512, 1.3529, 0.970451, -0.066734, 0.0828515, 1.3986, 0.985522, -0.034734, 0.0424458, 1.44148, 1.00099, -102222e-8, 678929e-9, 1.48398, 965494e-9, -627338e-12, 0.0306409, 197672e-13, 99168e-8, -158573e-10, 0.0314638, 499803e-12, 991068e-9, -634012e-10, 0.031363, 200682e-11, 974567e-9, -14144e-8, 0.03036, 457312e-11, 998079e-9, -252812e-9, 0.031496, 860131e-11, 102243e-8, -396506e-9, 0.0319955, 148288e-10, 107877e-8, -577593e-9, 0.0331376, 249141e-10, 121622e-8, -816816e-9, 0.0359396, 423011e-10, 14455e-7, -113761e-8, 0.0399652, 724613e-10, 178791e-8, -156959e-8, 0.0450556, 123929e-9, 225668e-8, -214064e-8, 0.0508025, 208531e-9, 285627e-8, -287655e-8, 0.0568443, 341969e-9, 35991e-7, -380271e-8, 0.0630892, 544158e-9, 455524e-8, -496264e-8, 0.0702204, 842423e-9, 569143e-8, -63793e-7, 0.0773426, 126704e-8, 716928e-8, -813531e-8, 0.0860839, 186642e-8, 885307e-8, -0.0101946, 0.0944079, 267014e-8, 0.0109316, -0.0126386, 0.103951, 374033e-8, 0.0133704, -0.0154876, 0.113786, 51304e-7, 0.0161525, -0.0187317, 0.123477, 688858e-8, 0.0194267, -0.0224652, 0.133986, 910557e-8, 0.0230967, -0.0265976, 0.143979, 0.0118074, 0.0273627, -0.0312848, 0.154645, 0.0151266, 0.0323898, -0.0365949, 0.166765, 0.0191791, 0.0379225, -0.0422914, 0.177932, 0.0239236, 0.0447501, -0.0487469, 0.19167, 0.0296568, 0.0519391, -0.0556398, 0.203224, 0.0362924, 0.0599464, -0.0631646, 0.215652, 0.0440585, 0.0702427, -0.0714308, 0.232089, 0.0531619, 0.0806902, -0.0800605, 0.245258, 0.0634564, 0.0923194, -0.0892815, 0.258609, 0.0752481, 0.106938, -0.09931, 0.276654, 0.0888914, 0.121238, -0.109575, 0.289847, 0.104055, 0.138817, -0.120461, 0.307566, 0.121266, 0.15595, -0.131209, 0.320117, 0.139944, 0.178418, -0.143049, 0.339677, 0.161591, 0.197875, -0.154074, 0.349886, 0.184303, 0.224368, -0.166307, 0.369352, 0.210669, 0.252213, -0.178051, 0.386242, 0.238895, 0.277321, -0.189335, 0.395294, 0.269182, 0.310332, -0.200683, 0.412148, 0.302508, 0.338809, -0.210856, 0.418266, 0.337264, 0.372678, -0.220655, 0.428723, 0.374881, 0.405632, -0.230053, 0.433887, 0.415656, 0.442293, -0.237993, 0.439911, 0.457982, 0.477256, -0.244897, 0.440175, 0.502831, 0.515592, -0.250657, 0.441079, 0.550277, 0.550969, -0.255459, 0.435219, 0.601102, 0.592883, -0.257696, 0.432882, 0.651785, 0.629092, -0.259894, 0.421054, 0.708961, 0.672033, -0.258592, 0.41177, 0.763806, 0.709147, -0.256525, 0.395267, 0.824249, 0.745367, -0.254677, 0.375013, 0.8951, 0.784715, -0.247892, 0.353906, 0.959317, 0.818107, -0.240162, 0.327801, 1.03153, 0.847895, -0.229741, 0.298821, 1.10601, 0.879603, -0.213084, 0.269115, 1.164, 0.902605, -0.195242, 0.236606, 1.22854, 0.922788, -0.174505, 0.203442, 1.29017, 0.944831, -0.150169, 0.169594, 1.34157, 0.959656, -0.124099, 0.135909, 1.3956, 0.972399, -0.0960626, 0.0990563, 1.45128, 0.986549, -0.0657097, 0.0602348, 1.50312, 1.00013, -0.0333558, 0.0186694, 1.55364, 619747e-11, -1e-7, 778326e-8, 796756e-16, 237499e-13, -999999e-13, 282592e-10, 114596e-15, 100292e-11, -166369e-11, 250354e-9, 677492e-14, 350752e-11, -637769e-11, 357289e-9, 631655e-13, 826445e-11, -174689e-10, 516179e-9, 31851e-11, 242481e-10, -450868e-10, 10223e-7, 130577e-11, 455631e-10, -89044e-9, 144302e-8, 374587e-11, 971222e-10, -178311e-9, 241912e-8, 102584e-10, 171403e-9, -313976e-9, 354938e-8, 236481e-10, 292747e-9, -520026e-9, 513765e-8, 496014e-10, 789827e-9, -118187e-8, 0.0238621, 139056e-9, 114093e-8, -171827e-8, 0.0286691, 244093e-9, 176119e-8, -249667e-8, 0.0368565, 420623e-9, 22233e-7, -333742e-8, 0.0400469, 65673e-8, 343382e-8, -481976e-8, 0.0535751, 109323e-8, 427602e-8, -600755e-8, 0.057099, 155268e-8, 461435e-8, -737637e-8, 0.0551084, 215031e-8, 695698e-8, -971401e-8, 0.0715767, 316529e-8, 867619e-8, -0.0120943, 0.0793314, 436995e-8, 0.0106694, -0.0148202, 0.0869391, 58959e-7, 0.0140351, -0.0183501, 0.101572, 798757e-8, 0.0168939, -0.022006, 0.11018, 0.0104233, 0.020197, -0.0261568, 0.119041, 0.0134167, 0.0254702, -0.0312778, 0.135404, 0.0173009, 0.0298384, -0.0362469, 0.1437, 0.0215428, 0.035159, -0.042237, 0.15512, 0.0268882, 0.0427685, -0.0488711, 0.17128, 0.033235, 0.0494848, -0.0557997, 0.181813, 0.0404443, 0.0592394, -0.0635578, 0.198745, 0.0490043, 0.0681463, -0.071838, 0.210497, 0.0588239, 0.0804753, -0.0809297, 0.228864, 0.0702835, 0.0942205, -0.0906488, 0.247008, 0.0834012, 0.106777, -0.100216, 0.258812, 0.0975952, 0.124471, -0.110827, 0.278617, 0.114162, 0.138389, -0.121193, 0.287049, 0.131983, 0.159543, -0.13253, 0.307151, 0.152541, 0.176432, -0.143611, 0.31564, 0.174673, 0.201723, -0.15548, 0.33538, 0.199842, 0.229721, -0.167166, 0.355256, 0.227097, 0.250206, -0.178238, 0.360047, 0.256014, 0.282118, -0.189905, 0.378761, 0.28855, 0.312821, -0.201033, 0.39181, 0.323348, 0.341482, -0.211584, 0.397716, 0.360564, 0.377368, -0.221314, 0.410141, 0.400004, 0.418229, -0.230474, 0.423485, 0.442371, 0.444881, -0.239443, 0.418874, 0.488796, 0.488899, -0.245987, 0.427545, 0.535012, 0.520317, -0.253948, 0.422147, 0.589678, 0.568566, -0.256616, 0.42719, 0.637683, 0.599607, -0.26376, 0.415114, 0.703363, 0.64222, -0.268687, 0.408715, 0.771363, 0.685698, -0.2694, 0.399722, 0.83574, 0.732327, -0.266642, 0.388651, 0.897764, 0.769873, -0.267712, 0.369198, 0.983312, 0.806733, -0.263479, 0.346802, 1.06222, 0.843466, -0.254575, 0.321368, 1.13477, 0.873008, -0.242749, 0.29211, 1.20712, 0.908438, -0.22725, 0.262143, 1.27465, 0.936321, -0.207621, 0.228876, 1.33203, 0.950353, -0.187932, 0.19484, 1.40439, 0.96442, -0.165154, 0.163178, 1.4732, 0.979856, -0.139302, 0.127531, 1.53574, 0.982561, -0.11134, 0.0903457, 1.59982, 0.996389, -0.0808124, 0.0489007, 1.6577], t = [1, 0, 0, 0, 1, 791421e-36, 0, 0, 1, 104392e-29, 0, 0, 1, 349405e-26, 0, 0, 1, 109923e-23, 0, 0, 1, 947414e-22, 0, 0, 1, 359627e-20, 0, 0, 1, 772053e-19, 0, 0, 1, 108799e-17, 0, 0, 1, 110655e-16, 0, 0, 1, 865818e-16, 0, 0, 0.999998, 545037e-15, 0, 0, 0.999994, 285095e-14, 0, 0, 0.999989, 126931e-13, 0, 0, 0.999973, 489938e-13, 0, 0, 0.999947, 166347e-12, 0, 0, 0.999894, 502694e-12, 0, 0, 0.999798, 136532e-11, 0, 0, 0.999617, 335898e-11, 0, 0, 0.999234, 752126e-11, 0, 0, 0.998258, 152586e-10, 0, 0, 0.99504, 266207e-10, 0, 0, 0.980816, 236802e-10, 0, 0, 0.967553, 207684e-11, 0, 0, 0.966877, 403733e-11, 0, 0, 0.965752, 741174e-11, 0, 0, 0.96382, 127746e-10, 0, 0, 0.960306, 202792e-10, 0, 0, 0.953619, 280232e-10, 0, 0, 0.941103, 278816e-10, 0, 0, 0.926619, 160221e-10, 0, 0, 0.920983, 235164e-10, 0, 0, 0.912293, 311924e-10, 0, 0.0158731, 0.899277, 348118e-10, 0, 0.0476191, 0.880884, 26041e-9, 0, 0.0793651, 0.870399, 338726e-10, 0, 0.111111, 0.856138, 392906e-10, 0, 0.142857, 0.837436, 372874e-10, 0, 0.174603, 0.820973, 392558e-10, 0, 0.206349, 0.803583, 434658e-10, 0, 0.238095, 0.782168, 40256e-9, 0, 0.269841, 0.764107, 448159e-10, 0, 0.301587, 0.743092, 457627e-10, 0, 0.333333, 0.721626, 455314e-10, 0, 0.365079, 0.700375, 477335e-10, 0, 0.396825, 0.677334, 461072e-10, 0, 0.428571, 0.655702, 484393e-10, 0, 0.460317, 0.632059, 464583e-10, 0, 0.492064, 0.610125, 483923e-10, 0, 0.52381, 0.58653, 464342e-10, 0, 0.555556, 0.564508, 477033e-10, 0, 0.587302, 0.541405, 459263e-10, 0, 0.619048, 0.519556, 46412e-9, 0, 0.650794, 0.497292, 448913e-10, 0, 0.68254, 0.475898, 445789e-10, 0, 0.714286, 0.454722, 433496e-10, 0, 0.746032, 0.434042, 423054e-10, 0, 0.777778, 0.414126, 413737e-10, 0, 0.809524, 0.394387, 397265e-10, 0, 0.84127, 0.375841, 390709e-10, 0, 0.873016, 0.357219, 369938e-10, 0, 0.904762, 0.340084, 365618e-10, 0, 0.936508, 0.322714, 342533e-10, 0, 0.968254, 0.306974, 339596e-10, 0, 1, 1, 101524e-23, 0, 0, 1, 10292e-22, 0, 0, 1, 130908e-23, 0, 0, 1, 473331e-23, 0, 0, 1, 625319e-22, 0, 0, 1, 107932e-20, 0, 0, 1, 163779e-19, 0, 0, 1, 203198e-18, 0, 0, 1, 204717e-17, 0, 0, 0.999999, 168995e-16, 0, 0, 0.999998, 115855e-15, 0, 0, 0.999996, 66947e-14, 0, 0, 0.999991, 330863e-14, 0, 0, 0.999983, 141737e-13, 0, 0, 0.999968, 532626e-13, 0, 0, 0.99994, 177431e-12, 0, 0, 0.999891, 528835e-12, 0, 0, 0.999797, 142169e-11, 0, 0, 0.999617, 347057e-11, 0, 0, 0.999227, 77231e-10, 0, 0, 0.998239, 155753e-10, 0, 0, 0.994937, 268495e-10, 0, 0, 0.980225, 213742e-10, 0, 0, 0.967549, 21631e-10, 0, 0, 0.966865, 417989e-11, 0, 0, 0.965739, 763341e-11, 0, 0, 0.963794, 130892e-10, 0, 0, 0.960244, 206456e-10, 0, 0, 0.953495, 282016e-10, 0, 148105e-9, 0.940876, 271581e-10, 0, 2454e-6, 0.926569, 164159e-10, 0, 867491e-8, 0.920905, 239521e-10, 0, 0.01956, 0.912169, 315127e-10, 0, 0.035433, 0.899095, 346626e-10, 0, 0.056294, 0.882209, 290223e-10, 0, 0.0818191, 0.870272, 342992e-10, 0, 0.111259, 0.855977, 394164e-10, 0, 0.142857, 0.837431, 372343e-10, 0, 0.174603, 0.820826, 396691e-10, 0, 0.206349, 0.803408, 435395e-10, 0, 0.238095, 0.782838, 419579e-10, 0, 0.269841, 0.763941, 450953e-10, 0, 0.301587, 0.742904, 455847e-10, 0, 0.333333, 0.721463, 458833e-10, 0, 0.365079, 0.700197, 477159e-10, 0, 0.396825, 0.677501, 470641e-10, 0, 0.428571, 0.655527, 484732e-10, 0, 0.460317, 0.6324, 476834e-10, 0, 0.492064, 0.609964, 484213e-10, 0, 0.52381, 0.586839, 475541e-10, 0, 0.555556, 0.564353, 476951e-10, 0, 0.587302, 0.541589, 467611e-10, 0, 0.619048, 0.519413, 463493e-10, 0, 0.650794, 0.497337, 453994e-10, 0, 0.68254, 0.475797, 445308e-10, 0, 0.714286, 0.454659, 435787e-10, 0, 0.746032, 0.434065, 424839e-10, 0, 0.777778, 0.414018, 41436e-9, 0, 0.809524, 0.39455, 401902e-10, 0, 0.84127, 0.375742, 390813e-10, 0, 0.873016, 0.357501, 377116e-10, 0, 0.904762, 0.339996, 36535e-9, 0, 0.936508, 0.323069, 351265e-10, 0, 0.968254, 0.306897, 339112e-10, 0, 1, 1, 10396e-19, 0, 0, 1, 104326e-20, 0, 0, 1, 110153e-20, 0, 0, 1, 144668e-20, 0, 0, 1, 34528e-19, 0, 0, 1, 175958e-19, 0, 0, 1, 12627e-17, 0, 0, 1, 936074e-18, 0, 0, 1, 645742e-17, 0, 0, 0.999998, 401228e-16, 0, 0, 0.999997, 222338e-15, 0, 0, 0.999995, 10967e-13, 0, 0, 0.999991, 482132e-14, 0, 0, 0.999981, 189434e-13, 0, 0, 0.999967, 667716e-13, 0, 0, 0.999938, 212066e-12, 0, 0, 0.999886, 60977e-11, 0, 0, 0.999792, 159504e-11, 0, 0, 0.999608, 381191e-11, 0, 0, 0.999209, 833727e-11, 0, 0, 0.998179, 165288e-10, 0, 0, 0.994605, 274387e-10, 0, 0, 0.979468, 167316e-10, 0, 0, 0.967529, 242877e-11, 0, 0, 0.966836, 461696e-11, 0, 0, 0.96569, 830977e-11, 0, 0, 0.963706, 140427e-10, 0, 244659e-11, 0.960063, 217353e-10, 0, 760774e-9, 0.953113, 286606e-10, 0, 367261e-8, 0.940192, 247691e-10, 0, 940263e-8, 0.927731, 195814e-10, 0, 0.018333, 0.920669, 252531e-10, 0, 0.0306825, 0.911799, 324277e-10, 0, 0.0465556, 0.89857, 340982e-10, 0, 0.0659521, 0.883283, 319622e-10, 0, 0.0887677, 0.86989, 35548e-9, 0, 0.114784, 0.855483, 397143e-10, 0, 0.143618, 0.837987, 391665e-10, 0, 0.174606, 0.820546, 411306e-10, 0, 0.206349, 0.802878, 436753e-10, 0, 0.238095, 0.783402, 444e-7, 0, 0.269841, 0.763439, 458726e-10, 0, 0.301587, 0.742925, 467097e-10, 0, 0.333333, 0.721633, 478887e-10, 0, 0.365079, 0.69985, 481251e-10, 0, 0.396825, 0.67783, 491811e-10, 0, 0.428571, 0.655126, 488199e-10, 0, 0.460318, 0.632697, 496025e-10, 0, 0.492064, 0.609613, 48829e-9, 0, 0.52381, 0.587098, 492754e-10, 0, 0.555556, 0.564119, 482625e-10, 0, 0.587302, 0.541813, 482807e-10, 0, 0.619048, 0.519342, 471552e-10, 0, 0.650794, 0.497514, 466765e-10, 0, 0.68254, 0.475879, 455582e-10, 0, 0.714286, 0.454789, 446007e-10, 0, 0.746032, 0.434217, 435382e-10, 0, 0.777778, 0.414086, 421753e-10, 0, 0.809524, 0.394744, 412093e-10, 0, 0.84127, 0.375782, 396634e-10, 0, 0.873016, 0.357707, 386419e-10, 0, 0.904762, 0.340038, 370345e-10, 0, 0.936508, 0.323284, 359725e-10, 0, 0.968254, 0.306954, 3436e-8, 0, 1, 1, 599567e-19, 0, 0, 1, 600497e-19, 0, 0, 1, 614839e-19, 0, 0, 1, 686641e-19, 0, 0, 1, 972658e-19, 0, 0, 1, 221271e-18, 0, 0, 1, 833195e-18, 0, 0, 1, 403601e-17, 0, 0, 0.999999, 206001e-16, 0, 0, 0.999998, 101739e-15, 0, 0, 0.999997, 470132e-15, 0, 0, 0.999993, 200436e-14, 0, 0, 0.999988, 783682e-14, 0, 0, 0.999979, 280338e-13, 0, 0, 0.999962, 917033e-13, 0, 0, 0.999933, 274514e-12, 0, 0, 0.999881, 753201e-12, 0, 0, 0.999783, 189826e-11, 0, 0, 0.999594, 440279e-11, 0, 0, 0.999178, 93898e-10, 0, 0, 0.998073, 181265e-10, 0, 0, 0.993993, 280487e-10, 0, 0, 0.979982, 149422e-10, 0, 0, 0.968145, 378481e-11, 0, 0, 0.966786, 53771e-10, 0, 0, 0.965611, 947508e-11, 0, 388934e-10, 0.963557, 156616e-10, 0, 9693e-7, 0.959752, 235144e-10, 0, 370329e-8, 0.952461, 291568e-10, 0, 868428e-8, 0.940193, 240102e-10, 0, 0.0161889, 0.929042, 231235e-10, 0, 0.0263948, 0.920266, 273968e-10, 0, 0.0394088, 0.911178, 337915e-10, 0, 0.0552818, 0.897873, 333629e-10, 0, 0.0740138, 0.884053, 351405e-10, 0, 0.0955539, 0.869455, 378034e-10, 0, 0.119795, 0.854655, 399378e-10, 0, 0.14656, 0.838347, 419108e-10, 0, 0.175573, 0.820693, 440831e-10, 0, 0.206388, 0.802277, 445599e-10, 0, 0.238095, 0.783634, 472691e-10, 0, 0.269841, 0.763159, 476984e-10, 0, 0.301587, 0.742914, 491487e-10, 0, 0.333333, 0.721662, 502312e-10, 0, 0.365079, 0.699668, 502817e-10, 0, 0.396825, 0.677839, 51406e-9, 0, 0.428571, 0.655091, 511095e-10, 0, 0.460317, 0.632665, 516067e-10, 0, 0.492064, 0.609734, 512255e-10, 0, 0.52381, 0.587043, 510263e-10, 0, 0.555556, 0.564298, 50565e-9, 0, 0.587302, 0.541769, 497951e-10, 0, 0.619048, 0.519529, 492698e-10, 0, 0.650794, 0.497574, 482066e-10, 0, 0.68254, 0.476028, 473689e-10, 0, 0.714286, 0.454961, 461941e-10, 0, 0.746032, 0.434341, 450618e-10, 0, 0.777778, 0.414364, 438355e-10, 0, 0.809524, 0.394832, 424196e-10, 0, 0.84127, 0.376109, 412563e-10, 0, 0.873016, 0.35779, 396226e-10, 0, 0.904762, 0.340379, 384886e-10, 0, 0.936508, 0.323385, 368214e-10, 0, 0.968254, 0.307295, 356636e-10, 0, 1, 1, 106465e-17, 0, 0, 1, 106555e-17, 0, 0, 1, 107966e-17, 0, 0, 1, 114601e-17, 0, 0, 1, 137123e-17, 0, 0, 1, 21243e-16, 0, 0, 0.999999, 489653e-17, 0, 0, 0.999999, 160283e-16, 0, 0, 0.999998, 62269e-15, 0, 0, 0.999997, 251859e-15, 0, 0, 0.999996, 996192e-15, 0, 0, 0.999992, 374531e-14, 0, 0, 0.999986, 132022e-13, 0, 0, 0.999975, 433315e-13, 0, 0, 0.999959, 131956e-12, 0, 0, 0.999927, 372249e-12, 0, 0, 0.999871, 972461e-12, 0, 0, 0.999771, 235343e-11, 0, 0, 0.999572, 52768e-10, 0, 0, 0.999133, 109237e-10, 0, 0, 0.997912, 203675e-10, 0, 0, 0.993008, 279396e-10, 0, 0, 0.980645, 139604e-10, 0, 0, 0.970057, 646596e-11, 0, 0, 0.966717, 65089e-10, 0, 474145e-10, 0.965497, 111863e-10, 0, 89544e-8, 0.96334, 179857e-10, 0, 32647e-7, 0.959294, 259045e-10, 0, 75144e-7, 0.951519, 292327e-10, 0, 0.0138734, 0.940517, 249769e-10, 0, 0.0224952, 0.93014, 26803e-9, 0, 0.0334828, 0.91972, 303656e-10, 0, 0.0468973, 0.910294, 353323e-10, 0, 0.0627703, 0.897701, 351002e-10, 0, 0.0811019, 0.884522, 388104e-10, 0, 0.10186, 0.869489, 412932e-10, 0, 0.124985, 0.853983, 415781e-10, 0, 0.150372, 0.838425, 454066e-10, 0, 0.177868, 0.820656, 471624e-10, 0, 0.207245, 0.801875, 475243e-10, 0, 0.238143, 0.783521, 505621e-10, 0, 0.269841, 0.763131, 50721e-9, 0, 0.301587, 0.74261, 523293e-10, 0, 0.333333, 0.72148, 528699e-10, 0, 0.365079, 0.699696, 538677e-10, 0, 0.396825, 0.677592, 539255e-10, 0, 0.428571, 0.65525, 546367e-10, 0, 0.460317, 0.632452, 541348e-10, 0, 0.492064, 0.609903, 544976e-10, 0, 0.52381, 0.586928, 536201e-10, 0, 0.555556, 0.564464, 535185e-10, 0, 0.587302, 0.541801, 524949e-10, 0, 0.619048, 0.519681, 51812e-9, 0, 0.650794, 0.497685, 507687e-10, 0, 0.68254, 0.47622, 496243e-10, 0, 0.714286, 0.455135, 485714e-10, 0, 0.746032, 0.4346, 471847e-10, 0, 0.777778, 0.414564, 459294e-10, 0, 0.809524, 0.395165, 444705e-10, 0, 0.84127, 0.376333, 430772e-10, 0, 0.873016, 0.358197, 416229e-10, 0, 0.904762, 0.34064, 401019e-10, 0, 0.936508, 0.323816, 386623e-10, 0, 0.968254, 0.307581, 370933e-10, 0, 1, 1, 991541e-17, 0, 0, 1, 992077e-17, 0, 0, 1, 100041e-16, 0, 0, 1, 10385e-15, 0, 0, 1, 115777e-16, 0, 0, 1, 150215e-16, 0, 0, 0.999999, 254738e-16, 0, 0, 0.999999, 598822e-16, 0, 0, 0.999998, 179597e-15, 0, 0, 0.999997, 602367e-15, 0, 0, 0.999994, 206835e-14, 0, 0, 0.99999, 694952e-14, 0, 0, 0.999984, 223363e-13, 0, 0, 0.999972, 678578e-13, 0, 0, 0.999952, 193571e-12, 0, 0, 0.999919, 516594e-12, 0, 0, 0.99986, 128739e-11, 0, 0, 0.999753, 299298e-11, 0, 0, 0.999546, 648258e-11, 0, 0, 0.999074, 129985e-10, 0, 0, 0.997671, 232176e-10, 0, 0, 0.991504, 256701e-10, 0, 0, 0.981148, 131141e-10, 0, 0, 0.971965, 869048e-11, 0, 280182e-10, 0.966624, 808301e-11, 0, 695475e-9, 0.965344, 135235e-10, 0, 265522e-8, 0.963048, 210592e-10, 0, 622975e-8, 0.958673, 287473e-10, 0, 0.0116234, 0.950262, 281379e-10, 0, 0.018976, 0.940836, 271089e-10, 0, 0.0283844, 0.930996, 30926e-9, 0, 0.0399151, 0.919848, 348359e-10, 0, 0.0536063, 0.909136, 366092e-10, 0, 0.0694793, 0.897554, 384162e-10, 0, 0.0875342, 0.884691, 430971e-10, 0, 0.107749, 0.869414, 447803e-10, 0, 0.130087, 0.853462, 452858e-10, 0, 0.154481, 0.838187, 495769e-10, 0, 0.180833, 0.820381, 502709e-10, 0, 0.209005, 0.801844, 522713e-10, 0, 0.238791, 0.783061, 541505e-10, 0, 0.269869, 0.763205, 553712e-10, 0, 0.301587, 0.742362, 564909e-10, 0, 0.333333, 0.721393, 572646e-10, 0, 0.365079, 0.699676, 581012e-10, 0, 0.396825, 0.677395, 58096e-9, 0, 0.428571, 0.655208, 585766e-10, 0, 0.460317, 0.632451, 583602e-10, 0, 0.492064, 0.609839, 580234e-10, 0, 0.52381, 0.587093, 577161e-10, 0, 0.555556, 0.564467, 568447e-10, 0, 0.587302, 0.542043, 563166e-10, 0, 0.619048, 0.519826, 55156e-9, 0, 0.650794, 0.497952, 541682e-10, 0, 0.68254, 0.476477, 528971e-10, 0, 0.714286, 0.455412, 514952e-10, 0, 0.746032, 0.434926, 502222e-10, 0, 0.777778, 0.4149, 485779e-10, 0, 0.809524, 0.395552, 472242e-10, 0, 0.84127, 0.376712, 454891e-10, 0, 0.873016, 0.358622, 440924e-10, 0, 0.904762, 0.341048, 422984e-10, 0, 0.936508, 0.324262, 408582e-10, 0, 0.968254, 0.308013, 390839e-10, 0, 1, 1, 613913e-16, 0, 0, 1, 614145e-16, 0, 0, 1, 617708e-16, 0, 0, 1, 633717e-16, 0, 0, 1, 681648e-16, 0, 0, 1, 808291e-16, 0, 0, 1, 114608e-15, 0, 0, 0.999998, 210507e-15, 0, 0, 0.999997, 499595e-15, 0, 0, 0.999995, 139897e-14, 0, 0, 0.999994, 419818e-14, 0, 0, 0.999988, 127042e-13, 0, 0, 0.999979, 375153e-13, 0, 0, 0.999965, 106206e-12, 0, 0, 0.999945, 285381e-12, 0, 0, 0.999908, 723611e-12, 0, 0, 0.999846, 17255e-10, 0, 0, 0.999733, 386104e-11, 0, 0, 0.999511, 808493e-11, 0, 0, 0.998993, 156884e-10, 0, 0, 0.997326, 265538e-10, 0, 0, 0.989706, 206466e-10, 0, 0, 0.981713, 130756e-10, 0, 70005e-10, 0.973636, 106473e-10, 0, 464797e-9, 0.966509, 10194e-9, 0, 201743e-8, 0.965149, 165881e-10, 0, 497549e-8, 0.962669, 249147e-10, 0, 953262e-8, 0.95786, 317449e-10, 0, 0.0158211, 0.949334, 281045e-10, 0, 0.0239343, 0.941041, 303263e-10, 0, 0.0339372, 0.931575, 356754e-10, 0, 0.0458738, 0.920102, 397075e-10, 0, 0.059772, 0.908002, 384886e-10, 0, 0.075645, 0.897269, 43027e-9, 0, 0.0934929, 0.884559, 479925e-10, 0, 0.113302, 0.869161, 48246e-9, 0, 0.135045, 0.853342, 509505e-10, 0, 0.158678, 0.837633, 542846e-10, 0, 0.184136, 0.820252, 554139e-10, 0, 0.211325, 0.801872, 581412e-10, 0, 0.240113, 0.782418, 585535e-10, 0, 0.270306, 0.7631, 610923e-10, 0, 0.301594, 0.742183, 613678e-10, 0, 0.333333, 0.721098, 627275e-10, 0, 0.365079, 0.699512, 629413e-10, 0, 0.396825, 0.677372, 636351e-10, 0, 0.428571, 0.655059, 633555e-10, 0, 0.460317, 0.632567, 636513e-10, 0, 0.492064, 0.609784, 628965e-10, 0, 0.52381, 0.587237, 625546e-10, 0, 0.555556, 0.564525, 615825e-10, 0, 0.587302, 0.542181, 605048e-10, 0, 0.619048, 0.520017, 596329e-10, 0, 0.650794, 0.498204, 581516e-10, 0, 0.68254, 0.476742, 569186e-10, 0, 0.714286, 0.455803, 553833e-10, 0, 0.746032, 0.435251, 537807e-10, 0, 0.777778, 0.415374, 522025e-10, 0, 0.809524, 0.395921, 503421e-10, 0, 0.84127, 0.377253, 488211e-10, 0, 0.873016, 0.359021, 468234e-10, 0, 0.904762, 0.341637, 453269e-10, 0, 0.936508, 0.3247, 433014e-10, 0, 0.968254, 0.308625, 418007e-10, 0, 1, 1, 286798e-15, 0, 0, 1, 286877e-15, 0, 0, 1, 288094e-15, 0, 0, 1, 293506e-15, 0, 0, 1, 309262e-15, 0, 0, 0.999999, 348593e-15, 0, 0, 0.999999, 444582e-15, 0, 0, 0.999998, 688591e-15, 0, 0, 0.999996, 134391e-14, 0, 0, 0.999993, 317438e-14, 0, 0, 0.999989, 835609e-14, 0, 0, 0.999983, 228677e-13, 0, 0, 0.999974, 623361e-13, 0, 0, 0.999959, 165225e-12, 0, 0, 0.999936, 419983e-12, 0, 0, 0.999896, 101546e-11, 0, 0, 0.99983, 232376e-11, 0, 0, 0.999709, 50156e-10, 0, 0, 0.999469, 10167e-9, 0, 0, 0.998886, 190775e-10, 0, 0, 0.996819, 300511e-10, 0, 0, 0.988837, 185092e-10, 0, 168222e-12, 0.982178, 134622e-10, 0, 259622e-9, 0.975017, 125961e-10, 0, 142595e-8, 0.967101, 13507e-9, 0, 382273e-8, 0.964905, 205003e-10, 0, 764164e-8, 0.96218, 29546e-9, 0, 0.0130121, 0.956821, 343738e-10, 0, 0.0200253, 0.948829, 305063e-10, 0, 0.0287452, 0.941092, 346487e-10, 0, 0.039218, 0.931883, 412061e-10, 0, 0.0514748, 0.920211, 444651e-10, 0, 0.0655351, 0.907307, 431252e-10, 0, 0.0814082, 0.89684, 490382e-10, 0, 0.0990939, 0.884119, 53334e-9, 0, 0.118583, 0.869148, 54114e-9, 0, 0.139856, 0.853377, 578536e-10, 0, 0.162882, 0.836753, 592285e-10, 0, 0.187615, 0.820063, 622787e-10, 0, 0.213991, 0.801694, 645492e-10, 0, 0.241918, 0.782116, 65353e-9, 0, 0.271267, 0.762673, 674344e-10, 0, 0.301847, 0.742133, 682788e-10, 0, 0.333333, 0.720779, 691959e-10, 0, 0.365079, 0.699386, 696817e-10, 0, 0.396826, 0.67732, 699583e-10, 0, 0.428572, 0.654888, 698447e-10, 0, 0.460318, 0.632499, 694063e-10, 0, 0.492064, 0.609825, 691612e-10, 0, 0.52381, 0.587287, 681576e-10, 0, 0.555556, 0.564743, 674138e-10, 0, 0.587302, 0.542409, 661617e-10, 0, 0.619048, 0.520282, 647785e-10, 0, 0.650794, 0.498506, 633836e-10, 0, 0.68254, 0.477102, 615905e-10, 0, 0.714286, 0.456167, 601013e-10, 0, 0.746032, 0.435728, 581457e-10, 0, 0.777778, 0.415809, 564215e-10, 0, 0.809524, 0.396517, 544997e-10, 0, 0.84127, 0.377737, 525061e-10, 0, 0.873016, 0.359698, 506831e-10, 0, 0.904762, 0.342164, 48568e-9, 0, 0.936508, 0.325417, 467826e-10, 0, 0.968254, 0.309186, 446736e-10, 0, 1, 1, 109018e-14, 0, 0, 1, 10904e-13, 0, 0, 1, 109393e-14, 0, 0, 1, 11095e-13, 0, 0, 1, 1154e-12, 0, 0, 1, 126089e-14, 0, 0, 0.999999, 15059e-13, 0, 0, 0.999997, 207899e-14, 0, 0, 0.999994, 348164e-14, 0, 0, 0.999993, 705728e-14, 0, 0, 0.999987, 163692e-13, 0, 0, 0.999981, 406033e-13, 0, 0, 0.999969, 10245e-11, 0, 0, 0.999953, 255023e-12, 0, 0, 0.999925, 61511e-11, 0, 0, 0.999881, 142218e-11, 0, 0, 0.99981, 313086e-11, 0, 0, 0.99968, 653119e-11, 0, 0, 0.999418, 12832e-9, 0, 0, 0.998748, 232497e-10, 0, 0, 0.996066, 329522e-10, 0, 0, 0.988379, 179613e-10, 0, 108799e-9, 0.982567, 143715e-10, 0, 921302e-9, 0.976097, 148096e-10, 0, 280738e-8, 0.968475, 178905e-10, 0, 596622e-8, 0.964606, 253921e-10, 0, 0.0105284, 0.961564, 348623e-10, 0, 0.0165848, 0.955517, 357612e-10, 0, 0.0242, 0.948381, 343493e-10, 0, 0.03342, 0.941095, 405849e-10, 0, 0.0442777, 0.931923, 475394e-10, 0, 0.0567958, 0.91996, 484328e-10, 0, 0.0709879, 0.907419, 502146e-10, 0, 0.086861, 0.89618, 561654e-10, 0, 0.104415, 0.88337, 587612e-10, 0, 0.123643, 0.869046, 618057e-10, 0, 0.144531, 0.853278, 657392e-10, 0, 0.167057, 0.836091, 66303e-9, 0, 0.191188, 0.819644, 704445e-10, 0, 0.216878, 0.801246, 714071e-10, 0, 0.244062, 0.782031, 740093e-10, 0, 0.272649, 0.762066, 74685e-9, 0, 0.302509, 0.741964, 766647e-10, 0, 0.333442, 0.720554, 766328e-10, 0, 0.365079, 0.699098, 777857e-10, 0, 0.396826, 0.677189, 774633e-10, 0, 0.428572, 0.65484, 776235e-10, 0, 0.460318, 0.632496, 770316e-10, 0, 0.492064, 0.609908, 762669e-10, 0, 0.52381, 0.587312, 753972e-10, 0, 0.555556, 0.564938, 739994e-10, 0, 0.587302, 0.542577, 728382e-10, 0, 0.619048, 0.52062, 71112e-9, 0, 0.650794, 0.498819, 694004e-10, 0, 0.68254, 0.477555, 675575e-10, 0, 0.714286, 0.456568, 653449e-10, 0, 0.746032, 0.436278, 636068e-10, 0, 0.777778, 0.41637, 613466e-10, 0, 0.809524, 0.397144, 594177e-10, 0, 0.84127, 0.378412, 570987e-10, 0, 0.873016, 0.360376, 550419e-10, 0, 0.904762, 0.342906, 527422e-10, 0, 0.936508, 0.326136, 506544e-10, 0, 0.968254, 0.30997, 484307e-10, 0, 1, 1, 354014e-14, 0, 0, 1, 354073e-14, 0, 0, 1, 354972e-14, 0, 0, 1, 358929e-14, 0, 0, 1, 370093e-14, 0, 0, 0.999999, 396194e-14, 0, 0, 0.999998, 453352e-14, 0, 0, 0.999997, 578828e-14, 0, 0, 0.999994, 863812e-14, 0, 0, 0.999991, 153622e-13, 0, 0, 0.999985, 316356e-13, 0, 0, 0.999977, 712781e-13, 0, 0, 0.999964, 166725e-12, 0, 0, 0.999945, 390501e-12, 0, 0, 0.999912, 895622e-12, 0, 0, 0.999866, 198428e-11, 0, 0, 0.999786, 421038e-11, 0, 0, 0.999647, 850239e-11, 0, 0, 0.999356, 162059e-10, 0, 0, 0.998563, 282652e-10, 0, 0, 0.994928, 336309e-10, 0, 244244e-10, 0.987999, 178458e-10, 0, 523891e-9, 0.982893, 159162e-10, 0, 194729e-8, 0.977044, 178056e-10, 0, 451099e-8, 0.969972, 230624e-10, 0, 835132e-8, 0.964237, 313922e-10, 0, 0.013561, 0.960791, 406145e-10, 0, 0.0202056, 0.954292, 372796e-10, 0, 0.0283321, 0.948052, 403199e-10, 0, 0.0379739, 0.940938, 479537e-10, 0, 0.0491551, 0.931689, 545292e-10, 0, 0.0618918, 0.91987, 54038e-9, 0, 0.0761941, 0.907665, 589909e-10, 0, 0.0920672, 0.895281, 642651e-10, 0, 0.109511, 0.882621, 659707e-10, 0, 0.12852, 0.86873, 709973e-10, 0, 0.149085, 0.853008, 742221e-10, 0, 0.171189, 0.835944, 761754e-10, 0, 0.194809, 0.818949, 797052e-10, 0, 0.21991, 0.800951, 812434e-10, 0, 0.246447, 0.781847, 838075e-10, 0, 0.274352, 0.761649, 84501e-9, 0, 0.303535, 0.74152, 860258e-10, 0, 0.333857, 0.720495, 866233e-10, 0, 0.365104, 0.698742, 868326e-10, 0, 0.396826, 0.677096, 87133e-9, 0, 0.428572, 0.654782, 863497e-10, 0, 0.460318, 0.632335, 860206e-10, 0, 0.492064, 0.610031, 849337e-10, 0, 0.52381, 0.587457, 838279e-10, 0, 0.555556, 0.56513, 82309e-9, 0, 0.587302, 0.542877, 803542e-10, 0, 0.619048, 0.5209, 786928e-10, 0, 0.650794, 0.499291, 765171e-10, 0, 0.68254, 0.477971, 744753e-10, 0, 0.714286, 0.457221, 72209e-9, 0, 0.746032, 0.436803, 697448e-10, 0, 0.777778, 0.417083, 675333e-10, 0, 0.809524, 0.397749, 648058e-10, 0, 0.84127, 0.379177, 625759e-10, 0, 0.873016, 0.361061, 598584e-10, 0, 0.904762, 0.343713, 575797e-10, 0, 0.936508, 0.326894, 549999e-10, 0, 0.968254, 0.310816, 527482e-10, 0, 1, 1, 10153e-12, 0, 0, 1, 101544e-13, 0, 0, 1, 101751e-13, 0, 0, 1, 102662e-13, 0, 0, 1, 10521e-12, 0, 0, 0.999999, 111049e-13, 0, 0, 0.999999, 123408e-13, 0, 0, 0.999996, 14924e-12, 0, 0, 0.999992, 204471e-13, 0, 0, 0.999989, 326539e-13, 0, 0, 0.99998, 603559e-13, 0, 0, 0.999971, 123936e-12, 0, 0, 0.999955, 269058e-12, 0, 0, 0.999933, 593604e-12, 0, 0, 0.999901, 129633e-11, 0, 0, 0.999847, 275621e-11, 0, 0, 0.999761, 564494e-11, 0, 0, 0.999607, 110485e-10, 0, 0, 0.999282, 204388e-10, 0, 0, 0.99831, 341084e-10, 0, 22038e-11, 0.993288, 294949e-10, 0, 242388e-9, 0.987855, 192736e-10, 0, 12503e-7, 0.983167, 182383e-10, 0, 32745e-7, 0.977908, 218633e-10, 0, 646321e-8, 0.971194, 290662e-10, 0, 0.0109133, 0.963867, 386401e-10, 0, 0.0166927, 0.95982, 462827e-10, 0, 0.0238494, 0.953497, 420705e-10, 0, 0.0324178, 0.947621, 477743e-10, 0, 0.0424225, 0.940611, 568258e-10, 0, 0.0538808, 0.931174, 618061e-10, 0, 0.0668047, 0.919919, 627098e-10, 0, 0.0812014, 0.907856, 694714e-10, 0, 0.0970745, 0.894509, 735008e-10, 0, 0.114424, 0.881954, 763369e-10, 0, 0.133246, 0.868309, 821896e-10, 0, 0.153534, 0.852511, 83769e-9, 0, 0.175275, 0.835821, 881615e-10, 0, 0.198453, 0.817981, 896368e-10, 0, 0.223042, 0.800504, 930906e-10, 0, 0.249009, 0.78141, 945056e-10, 0, 0.276304, 0.761427, 963605e-10, 0, 0.304862, 0.74094, 968088e-10, 0, 0.334584, 0.720233, 981481e-10, 0, 0.365322, 0.698592, 979122e-10, 0, 0.396826, 0.676763, 981057e-10, 0, 0.428571, 0.654808, 973956e-10, 0, 0.460318, 0.632326, 962619e-10, 0, 0.492064, 0.610049, 952996e-10, 0, 0.52381, 0.58763, 933334e-10, 0, 0.555556, 0.565261, 917573e-10, 0, 0.587302, 0.543244, 896636e-10, 0, 0.619048, 0.521273, 873304e-10, 0, 0.650794, 0.499818, 852648e-10, 0, 0.68254, 0.478536, 823961e-10, 0, 0.714286, 0.457826, 79939e-9, 0, 0.746032, 0.437549, 77126e-9, 0, 0.777778, 0.41776, 743043e-10, 0, 0.809524, 0.39863, 716426e-10, 0, 0.84127, 0.379954, 686456e-10, 0, 0.873016, 0.362025, 660514e-10, 0, 0.904762, 0.344581, 630755e-10, 0, 0.936508, 0.327909, 605439e-10, 0, 0.968254, 0.311736, 576345e-10, 0, 1, 1, 263344e-13, 0, 0, 1, 263373e-13, 0, 0, 1, 263815e-13, 0, 0, 1, 265753e-13, 0, 0, 1, 271132e-13, 0, 0, 0.999999, 283279e-13, 0, 0, 0.999997, 30833e-12, 0, 0, 0.999995, 358711e-13, 0, 0, 0.999992, 461266e-13, 0, 0, 0.999985, 67574e-12, 0, 0, 0.999977, 11358e-11, 0, 0, 0.999966, 213657e-12, 0, 0, 0.999948, 431151e-12, 0, 0, 0.999923, 896656e-12, 0, 0, 0.999884, 186603e-11, 0, 0, 0.999826, 381115e-11, 0, 0, 0.999732, 754184e-11, 0, 0, 0.999561, 143192e-10, 0, 0, 0.999191, 257061e-10, 0, 0, 0.997955, 405724e-10, 0, 744132e-10, 0.992228, 276537e-10, 0, 716477e-9, 0.987638, 208885e-10, 0, 22524e-7, 0.983395, 215226e-10, 0, 484816e-8, 0.978614, 270795e-10, 0, 860962e-8, 0.972389, 365282e-10, 0, 0.0136083, 0.964392, 474747e-10, 0, 0.0198941, 0.95861, 509141e-10, 0, 0.0275023, 0.952806, 48963e-9, 0, 0.0364584, 0.94712, 571119e-10, 0, 0.04678, 0.940104, 671704e-10, 0, 0.0584799, 0.930398, 687586e-10, 0, 0.0715665, 0.919866, 738161e-10, 0, 0.086045, 0.907853, 813235e-10, 0, 0.101918, 0.894078, 834582e-10, 0, 0.119186, 0.881177, 892093e-10, 0, 0.137845, 0.867575, 944548e-10, 0, 0.157891, 0.852107, 969607e-10, 0, 0.179316, 0.835502, 101456e-9, 0, 0.202106, 0.81756, 103256e-9, 0, 0.226243, 0.79984, 106954e-9, 0, 0.251704, 0.780998, 108066e-9, 0, 0.278451, 0.761132, 110111e-9, 0, 0.306436, 0.740429, 110459e-9, 0, 0.335586, 0.719836, 111219e-9, 0, 0.365796, 0.698467, 11145e-8, 0, 0.3969, 0.676446, 110393e-9, 0, 0.428571, 0.654635, 110035e-9, 0, 0.460318, 0.632411, 108548e-9, 0, 0.492064, 0.609986, 106963e-9, 0, 0.52381, 0.587872, 105238e-9, 0, 0.555556, 0.565528, 102665e-9, 0, 0.587302, 0.543563, 100543e-9, 0, 0.619048, 0.52176, 976182e-10, 0, 0.650794, 0.500188, 947099e-10, 0, 0.68254, 0.479204, 919929e-10, 0, 0.714286, 0.458413, 886139e-10, 0, 0.746032, 0.438314, 857839e-10, 0, 0.777778, 0.418573, 82411e-9, 0, 0.809524, 0.39947, 792211e-10, 0, 0.84127, 0.380892, 759546e-10, 0, 0.873016, 0.362953, 727571e-10, 0, 0.904762, 0.345601, 695738e-10, 0, 0.936508, 0.328895, 664907e-10, 0, 0.968254, 0.312808, 634277e-10, 0, 1, 1, 628647e-13, 0, 0, 1, 628705e-13, 0, 0, 1, 629587e-13, 0, 0, 1, 633441e-13, 0, 0, 0.999999, 644087e-13, 0, 0, 0.999998, 667856e-13, 0, 0, 0.999997, 715889e-13, 0, 0, 0.999995, 809577e-13, 0, 0, 0.999989, 992764e-13, 0, 0, 0.999983, 135834e-12, 0, 0, 0.999974, 210482e-12, 0, 0, 0.999959, 365215e-12, 0, 0, 0.999939, 686693e-12, 0, 0, 0.999911, 13472e-10, 0, 0, 0.999868, 26731e-10, 0, 0, 0.999804, 524756e-11, 0, 0, 0.9997, 100403e-10, 0, 0, 0.99951, 185019e-10, 0, 0, 0.999078, 322036e-10, 0, 620676e-11, 0.997428, 470002e-10, 0, 341552e-9, 0.99162, 287123e-10, 0, 143727e-8, 0.987479, 234706e-10, 0, 349201e-8, 0.983582, 260083e-10, 0, 66242e-7, 0.979186, 337927e-10, 0, 0.0109113, 0.97325, 454689e-10, 0, 0.0164064, 0.965221, 573759e-10, 0, 0.0231463, 0.957262, 544114e-10, 0, 0.0311571, 0.952211, 587006e-10, 0, 0.0404572, 0.946631, 692256e-10, 0, 0.0510592, 0.939391, 787819e-10, 0, 0.0629723, 0.929795, 792368e-10, 0, 0.0762025, 0.91965, 875075e-10, 0, 0.090753, 0.907737, 950903e-10, 0, 0.106626, 0.893899, 972963e-10, 0, 0.123822, 0.880239, 10459e-8, 0, 0.142337, 0.866562, 107689e-9, 0, 0.16217, 0.85164, 113081e-9, 0, 0.183314, 0.835021, 116636e-9, 0, 0.20576, 0.817311, 120074e-9, 0, 0.229496, 0.798845, 121921e-9, 0, 0.254502, 0.780479, 12475e-8, 0, 0.280753, 0.760694, 125255e-9, 0, 0.308212, 0.740142, 126719e-9, 0, 0.336825, 0.719248, 12636e-8, 0, 0.366517, 0.698209, 126712e-9, 0, 0.397167, 0.676398, 125769e-9, 0, 0.428578, 0.654378, 124432e-9, 0, 0.460318, 0.632484, 123272e-9, 0, 0.492064, 0.610113, 12085e-8, 0, 0.52381, 0.587931, 118411e-9, 0, 0.555556, 0.565872, 11569e-8, 0, 0.587302, 0.543814, 112521e-9, 0, 0.619048, 0.522265, 109737e-9, 0, 0.650794, 0.500835, 106228e-9, 0, 0.68254, 0.479818, 102591e-9, 0, 0.714286, 0.459258, 991288e-10, 0, 0.746032, 0.439061, 952325e-10, 0, 0.777778, 0.419552, 91895e-9, 0, 0.809524, 0.400399, 879051e-10, 0, 0.84127, 0.381976, 844775e-10, 0, 0.873016, 0.364009, 806316e-10, 0, 0.904762, 0.346761, 771848e-10, 0, 0.936508, 0.330049, 735429e-10, 0, 0.968254, 0.314018, 702103e-10, 0, 1, 1, 139968e-12, 0, 0, 1, 139979e-12, 0, 0, 1, 140145e-12, 0, 0, 1, 14087e-11, 0, 0, 0.999999, 142865e-12, 0, 0, 0.999998, 147279e-12, 0, 0, 0.999997, 156057e-12, 0, 0, 0.999992, 17276e-11, 0, 0, 0.999989, 204352e-12, 0, 0, 0.99998, 26494e-11, 0, 0, 0.999969, 383435e-12, 0, 0, 0.999953, 618641e-12, 0, 0, 0.999929, 108755e-11, 0, 0, 0.999898, 201497e-11, 0, 0, 0.999849, 381346e-11, 0, 0, 0.999778, 719815e-11, 0, 0, 0.999661, 133215e-10, 0, 0, 0.999451, 238313e-10, 0, 0, 0.998936, 401343e-10, 0, 113724e-9, 0.99662, 517346e-10, 0, 820171e-9, 0.991094, 304323e-10, 0, 238143e-8, 0.987487, 281757e-10, 0, 493527e-8, 0.983731, 320048e-10, 0, 856859e-8, 0.979647, 423905e-10, 0, 0.0133393, 0.973837, 562935e-10, 0, 0.0192863, 0.96584, 677442e-10, 0, 0.0264369, 0.956309, 623073e-10, 0, 0.03481, 0.951523, 704131e-10, 0, 0.0444184, 0.946003, 836594e-10, 0, 0.0552713, 0.938454, 911736e-10, 0, 0.0673749, 0.929279, 938264e-10, 0, 0.0807329, 0.919239, 103754e-9, 0, 0.0953479, 0.907293, 109928e-9, 0, 0.111221, 0.893936, 115257e-9, 0, 0.128352, 0.879674, 122265e-9, 0, 0.14674, 0.865668, 125733e-9, 0, 0.166382, 0.850998, 132305e-9, 0, 0.187276, 0.834498, 134844e-9, 0, 0.209413, 0.816903, 139276e-9, 0, 0.232786, 0.798235, 140984e-9, 0, 0.257382, 0.779724, 14378e-8, 0, 0.283181, 0.760251, 144623e-9, 0, 0.310156, 0.739808, 145228e-9, 0, 0.338269, 0.718762, 14539e-8, 0, 0.367461, 0.697815, 144432e-9, 0, 0.397646, 0.67631, 143893e-9, 0, 0.428685, 0.654278, 141846e-9, 0, 0.460318, 0.632347, 13935e-8, 0, 0.492064, 0.610296, 137138e-9, 0, 0.52381, 0.588039, 133806e-9, 0, 0.555556, 0.566218, 130755e-9, 0, 0.587302, 0.544346, 127128e-9, 0, 0.619048, 0.522701, 123002e-9, 0, 0.650794, 0.501542, 119443e-9, 0, 0.68254, 0.480508, 115055e-9, 0, 0.714286, 0.460092, 111032e-9, 0, 0.746032, 0.440021, 106635e-9, 0, 0.777778, 0.420446, 102162e-9, 0, 0.809524, 0.401512, 98184e-9, 0, 0.84127, 0.38299, 936497e-10, 0, 0.873016, 0.365232, 89813e-9, 0, 0.904762, 0.347865, 853073e-10, 0, 0.936508, 0.331342, 817068e-10, 0, 0.968254, 0.315202, 773818e-10, 0, 1, 1, 29368e-11, 0, 0, 1, 2937e-10, 0, 0, 1, 293998e-12, 0, 0, 1, 295298e-12, 0, 0, 0.999999, 298865e-12, 0, 0, 0.999998, 3067e-10, 0, 0, 0.999995, 322082e-12, 0, 0, 0.999992, 350767e-12, 0, 0, 0.999986, 403538e-12, 0, 0, 0.999976, 501372e-12, 0, 0, 0.999964, 68562e-11, 0, 0, 0.999945, 10374e-10, 0, 0, 0.999919, 171269e-11, 0, 0, 0.999882, 300175e-11, 0, 0, 0.999829, 542144e-11, 0, 0, 0.999749, 984182e-11, 0, 0, 0.99962, 176213e-10, 0, 0, 0.999382, 305995e-10, 0, 138418e-10, 0.998751, 496686e-10, 0, 389844e-9, 0.995344, 510733e-10, 0, 150343e-8, 0.990768, 345829e-10, 0, 352451e-8, 0.987464, 342841e-10, 0, 655379e-8, 0.983846, 399072e-10, 0, 0.0106554, 0.980007, 533219e-10, 0, 0.0158723, 0.974494, 696992e-10, 0, 0.0222333, 0.96622, 776754e-10, 0, 0.029758, 0.956273, 747718e-10, 0, 0.0384596, 0.950952, 864611e-10, 0, 0.0483473, 0.945215, 100464e-9, 0, 0.0594266, 0.937287, 103729e-9, 0, 0.0717019, 0.928649, 111665e-9, 0, 0.0851752, 0.918791, 12353e-8, 0, 0.0998479, 0.906685, 127115e-9, 0, 0.115721, 0.893706, 13628e-8, 0, 0.132794, 0.879248, 142427e-9, 0, 0.151067, 0.864685, 148091e-9, 0, 0.170538, 0.850032, 153517e-9, 0, 0.191204, 0.833853, 157322e-9, 0, 0.213063, 0.816353, 161086e-9, 0, 0.236107, 0.797834, 164111e-9, 0, 0.260329, 0.778831, 165446e-9, 0, 0.285714, 0.759756, 167492e-9, 0, 0.312243, 0.739419, 166928e-9, 0, 0.339887, 0.718491, 167e-6, 0, 0.368604, 0.697392, 165674e-9, 0, 0.398329, 0.676102, 163815e-9, 0, 0.428961, 0.654243, 162003e-9, 0, 0.460331, 0.632176, 158831e-9, 0, 0.492064, 0.610407, 155463e-9, 0, 0.52381, 0.588394, 152062e-9, 0, 0.555556, 0.56645, 147665e-9, 0, 0.587302, 0.5449, 14375e-8, 0, 0.619048, 0.523276, 138905e-9, 0, 0.650794, 0.502179, 134189e-9, 0, 0.68254, 0.481359, 129392e-9, 0, 0.714286, 0.46092, 124556e-9, 0, 0.746032, 0.441084, 11957e-8, 0, 0.777778, 0.421517, 114652e-9, 0, 0.809524, 0.402721, 109688e-9, 0, 0.84127, 0.384222, 104667e-9, 0, 0.873016, 0.366534, 999633e-10, 0, 0.904762, 0.349205, 950177e-10, 0, 0.936508, 0.332702, 907301e-10, 0, 0.968254, 0.316599, 859769e-10, 0, 1, 1, 585473e-12, 0, 0, 1, 585507e-12, 0, 0, 1, 58602e-11, 0, 0, 0.999999, 588259e-12, 0, 0, 0.999999, 594381e-12, 0, 0, 0.999998, 607754e-12, 0, 0, 0.999995, 633729e-12, 0, 0, 0.99999, 68137e-11, 0, 0, 0.999984, 767003e-12, 0, 0, 0.999973, 921212e-12, 0, 0, 0.999959, 120218e-11, 0, 0, 0.999936, 172024e-11, 0, 0, 0.999907, 268088e-11, 0, 0, 0.999866, 445512e-11, 0, 0, 0.999806, 768481e-11, 0, 0, 0.999716, 1342e-8, 0, 0, 0.999576, 232473e-10, 0, 0, 0.9993, 391694e-10, 0, 129917e-9, 0.998498, 608429e-10, 0, 845035e-9, 0.994132, 489743e-10, 0, 237616e-8, 0.99031, 384644e-10, 0, 484456e-8, 0.987409, 421768e-10, 0, 832472e-8, 0.983981, 504854e-10, 0, 0.0128643, 0.980268, 671028e-10, 0, 0.0184947, 0.974875, 852749e-10, 0, 0.025237, 0.966063, 85531e-9, 0, 0.0331046, 0.956779, 900588e-10, 0, 0.0421067, 0.950259, 10577e-8, 0, 0.0522487, 0.944239, 119458e-9, 0, 0.0635343, 0.936341, 122164e-9, 0, 0.0759654, 0.928047, 134929e-9, 0, 0.0895434, 0.918065, 145544e-9, 0, 0.104269, 0.906267, 150531e-9, 0, 0.120142, 0.893419, 161652e-9, 0, 0.137163, 0.878758, 16593e-8, 0, 0.15533, 0.863699, 174014e-9, 0, 0.174645, 0.848876, 177877e-9, 0, 0.195106, 0.833032, 184049e-9, 0, 0.21671, 0.815557, 186088e-9, 0, 0.239454, 0.797323, 19054e-8, 0, 0.263332, 0.778124, 191765e-9, 0, 0.288336, 0.758929, 192535e-9, 0, 0.314451, 0.738979, 192688e-9, 0, 0.341658, 0.718213, 191522e-9, 0, 0.369924, 0.696947, 190491e-9, 0, 0.399202, 0.675807, 187913e-9, 0, 0.429416, 0.654147, 184451e-9, 0, 0.460447, 0.63229, 181442e-9, 0, 0.492064, 0.610499, 177139e-9, 0, 0.523809, 0.588747, 172596e-9, 0, 0.555555, 0.566783, 167457e-9, 0, 0.587301, 0.545359, 162518e-9, 0, 0.619048, 0.523984, 156818e-9, 0, 0.650794, 0.502917, 151884e-9, 0, 0.68254, 0.482294, 145514e-9, 0, 0.714286, 0.461945, 140199e-9, 0, 0.746032, 0.442133, 134101e-9, 0, 0.777778, 0.422705, 128374e-9, 0, 0.809524, 0.403916, 122996e-9, 0, 0.84127, 0.38554, 116808e-9, 0, 0.873016, 0.367909, 111973e-9, 0, 0.904762, 0.350651, 105938e-9, 0, 0.936508, 0.334208, 101355e-9, 0, 0.968254, 0.318123, 957629e-10, 0, 1, 1, 111633e-11, 0, 0, 1, 111639e-11, 0, 0, 1, 111725e-11, 0, 0, 1, 112096e-11, 0, 0, 0.999999, 11311e-10, 0, 0, 0.999997, 115315e-11, 0, 0, 0.999995, 11956e-10, 0, 0, 0.999989, 127239e-11, 0, 0, 0.999981, 140772e-11, 0, 0, 0.999969, 164541e-11, 0, 0, 0.999952, 206607e-11, 0, 0, 0.999928, 281783e-11, 0, 0, 0.999895, 416835e-11, 0, 0, 0.999848, 658728e-11, 0, 0, 0.999781, 108648e-10, 0, 0, 0.999682, 182579e-10, 0, 0, 0.999523, 306003e-10, 0, 159122e-10, 0.999205, 499862e-10, 0, 391184e-9, 0.998131, 73306e-9, 0, 147534e-8, 0.993334, 513229e-10, 0, 34227e-7, 0.99016, 467783e-10, 0, 632232e-8, 0.987321, 523413e-10, 0, 0.0102295, 0.984099, 64267e-9, 0, 0.0151794, 0.980432, 843042e-10, 0, 0.0211947, 0.974976, 102819e-9, 0, 0.0282899, 0.966429, 996234e-10, 0, 0.0364739, 0.957633, 111074e-9, 0, 0.0457522, 0.949422, 128644e-9, 0, 0.0561278, 0.943045, 140076e-9, 0, 0.0676023, 0.935448, 146349e-9, 0, 0.0801762, 0.927225, 161854e-9, 0, 0.0938499, 0.917033, 169135e-9, 0, 0.108623, 0.905762, 179987e-9, 0, 0.124496, 0.892879, 189832e-9, 0, 0.141469, 0.878435, 195881e-9, 0, 0.159541, 0.863114, 20466e-8, 0, 0.178713, 0.84776, 209473e-9, 0, 0.198985, 0.832084, 214861e-9, 0, 0.220355, 0.814915, 217695e-9, 0, 0.242823, 0.796711, 220313e-9, 0, 0.266385, 0.777603, 22313e-8, 0, 0.291036, 0.757991, 222471e-9, 0, 0.316767, 0.738371, 222869e-9, 0, 0.343563, 0.717872, 221243e-9, 0, 0.371402, 0.696619, 218089e-9, 0, 0.400248, 0.675379, 21562e-8, 0, 0.430047, 0.65411, 21169e-8, 0, 0.460709, 0.63241, 206947e-9, 0, 0.492079, 0.61046, 201709e-9, 0, 0.52381, 0.58903, 196753e-9, 0, 0.555556, 0.567267, 189637e-9, 0, 0.587302, 0.545886, 184735e-9, 0, 0.619048, 0.524714, 177257e-9, 0, 0.650794, 0.503789, 171424e-9, 0, 0.68254, 0.483204, 164688e-9, 0, 0.714286, 0.462976, 157172e-9, 0, 0.746032, 0.443294, 151341e-9, 0, 0.777778, 0.423988, 143737e-9, 0, 0.809524, 0.405325, 138098e-9, 0, 0.84127, 0.386981, 130698e-9, 0, 0.873016, 0.369436, 125276e-9, 0, 0.904762, 0.35219, 118349e-9, 0, 0.936508, 0.335804, 11312e-8, 0, 0.968254, 0.319749, 106687e-9, 0, 1, 1, 204685e-11, 0, 0, 1, 204694e-11, 0, 0, 1, 204831e-11, 0, 0, 0.999999, 205428e-11, 0, 0, 0.999999, 207056e-11, 0, 0, 0.999997, 210581e-11, 0, 0, 0.999993, 21732e-10, 0, 0, 0.999987, 229365e-11, 0, 0, 0.999979, 250243e-11, 0, 0, 0.999965, 286127e-11, 0, 0, 0.999947, 348028e-11, 0, 0, 0.999918, 455588e-11, 0, 0, 0.999881, 643303e-11, 0, 0, 0.999828, 970064e-11, 0, 0, 0.999753, 153233e-10, 0, 0, 0.999642, 24793e-9, 0, 0, 0.999464, 402032e-10, 0, 122947e-9, 0.999089, 635852e-10, 0, 807414e-9, 0.997567, 857026e-10, 0, 227206e-8, 0.992903, 594912e-10, 0, 462812e-8, 0.990011, 578515e-10, 0, 794162e-8, 0.987192, 65399e-9, 0, 0.0122534, 0.98418, 819675e-10, 0, 0.0175888, 0.980491, 105514e-9, 0, 0.0239635, 0.974779, 121532e-9, 0, 0.031387, 0.96675, 119144e-9, 0, 0.0398644, 0.958248, 136125e-9, 0, 0.0493982, 0.948884, 155408e-9, 0, 0.0599896, 0.941673, 162281e-9, 0, 0.0716382, 0.934521, 176754e-9, 0, 0.0843437, 0.926205, 192873e-9, 0, 0.0981056, 0.916089, 200038e-9, 0, 0.112923, 0.904963, 213624e-9, 0, 0.128796, 0.892089, 221834e-9, 0, 0.145725, 0.878028, 232619e-9, 0, 0.163709, 0.86249, 238632e-9, 0, 0.182749, 0.846587, 247002e-9, 0, 0.202847, 0.830988, 250702e-9, 0, 0.224001, 0.814165, 255562e-9, 0, 0.246214, 0.796135, 257505e-9, 0, 0.269482, 0.777052, 258625e-9, 0, 0.293805, 0.757201, 258398e-9, 0, 0.319176, 0.737655, 256714e-9, 0, 0.345587, 0.717477, 255187e-9, 0, 0.373021, 0.696433, 251792e-9, 0, 0.401454, 0.675084, 247223e-9, 0, 0.430844, 0.653907, 242213e-9, 0, 0.461125, 0.632561, 237397e-9, 0, 0.492187, 0.610658, 229313e-9, 0, 0.52381, 0.589322, 224402e-9, 0, 0.555556, 0.567857, 216116e-9, 0, 0.587302, 0.54652, 209124e-9, 0, 0.619048, 0.525433, 201601e-9, 0, 0.650794, 0.504679, 192957e-9, 0, 0.68254, 0.484203, 186052e-9, 0, 0.714286, 0.464203, 177672e-9, 0, 0.746032, 0.444549, 170005e-9, 0, 0.777778, 0.425346, 162401e-9, 0, 0.809524, 0.406706, 1544e-7, 0, 0.84127, 0.388576, 147437e-9, 0, 0.873016, 0.37094, 139493e-9, 0, 0.904762, 0.353996, 133219e-9, 0, 0.936508, 0.337391, 125573e-9, 0, 0.968254, 0.321648, 119867e-9, 0, 1, 1, 362511e-11, 0, 0, 1, 362525e-11, 0, 0, 1, 362739e-11, 0, 0, 0.999999, 363673e-11, 0, 0, 0.999998, 366214e-11, 0, 0, 0.999996, 371698e-11, 0, 0, 0.999992, 382116e-11, 0, 0, 0.999986, 400554e-11, 0, 0, 0.999976, 432058e-11, 0, 0, 0.999961, 485194e-11, 0, 0, 0.999938, 574808e-11, 0, 0, 0.999908, 726643e-11, 0, 0, 0.999865, 984707e-11, 0, 0, 0.999807, 142217e-10, 0, 0, 0.999723, 215581e-10, 0, 0, 0.999602, 336114e-10, 0, 119113e-10, 0.999398, 527353e-10, 0, 355813e-9, 0.998946, 805809e-10, 0, 137768e-8, 0.996647, 942908e-10, 0, 322469e-8, 0.992298, 668733e-10, 0, 597897e-8, 0.989802, 716564e-10, 0, 968903e-8, 0.987019, 821355e-10, 0, 0.0143845, 0.984219, 104555e-9, 0, 0.0200831, 0.980425, 131245e-9, 0, 0.0267948, 0.974241, 139613e-9, 0, 0.034525, 0.967006, 145931e-9, 0, 0.0432757, 0.95893, 167153e-9, 0, 0.0530471, 0.949157, 188146e-9, 0, 0.0638386, 0.94062, 194625e-9, 0, 0.0756487, 0.933509, 213721e-9, 0, 0.0884762, 0.925088, 229616e-9, 0, 0.10232, 0.915178, 239638e-9, 0, 0.117178, 0.904093, 254814e-9, 0, 0.133051, 0.891337, 263685e-9, 0, 0.149939, 0.877326, 274789e-9, 0, 0.167841, 0.861794, 280534e-9, 0, 0.18676, 0.845758, 289534e-9, 0, 0.206696, 0.829792, 294446e-9, 0, 0.22765, 0.813037, 296877e-9, 0, 0.249625, 0.795285, 300217e-9, 0, 0.27262, 0.776323, 299826e-9, 0, 0.296636, 0.756673, 299787e-9, 0, 0.321671, 0.736856, 297867e-9, 0, 0.347718, 0.716883, 294052e-9, 0, 0.374768, 0.696089, 289462e-9, 0, 0.402804, 0.67505, 285212e-9, 0, 0.431796, 0.653509, 27653e-8, 0, 0.461695, 0.63258, 271759e-9, 0, 0.49242, 0.61104, 262811e-9, 0, 0.523822, 0.589567, 255151e-9, 0, 0.555556, 0.568322, 246434e-9, 0, 0.587302, 0.547235, 237061e-9, 0, 0.619048, 0.52616, 228343e-9, 0, 0.650794, 0.505716, 219236e-9, 0, 0.68254, 0.485274, 209595e-9, 0, 0.714286, 0.465411, 201011e-9, 0, 0.746032, 0.445854, 19109e-8, 0, 0.777778, 0.426911, 182897e-9, 0, 0.809524, 0.408222, 173569e-9, 0, 0.84127, 0.390307, 165496e-9, 0, 0.873016, 0.372624, 156799e-9, 0, 0.904762, 0.355804, 14917e-8, 0, 0.936508, 0.33924, 140907e-9, 0, 0.968254, 0.323534, 134062e-9, 0, 1, 1, 622487e-11, 0, 0, 1, 62251e-10, 0, 0, 1, 622837e-11, 0, 0, 0.999999, 624259e-11, 0, 0, 0.999998, 628127e-11, 0, 0, 0.999996, 636451e-11, 0, 0, 0.999991, 65218e-10, 0, 0, 0.999984, 679782e-11, 0, 0, 0.999973, 726361e-11, 0, 0, 0.999955, 803644e-11, 0, 0, 0.999931, 931397e-11, 0, 0, 0.999896, 114299e-10, 0, 0, 0.999847, 149402e-10, 0, 0, 0.999784, 207461e-10, 0, 0, 0.999692, 302493e-10, 0, 0, 0.999554, 454957e-10, 0, 997275e-10, 0.999326, 690762e-10, 0, 724813e-9, 0.998757, 101605e-9, 0, 20972e-7, 0.995367, 958745e-10, 0, 432324e-8, 0.99209, 832808e-10, 0, 746347e-8, 0.989517, 887601e-10, 0, 0.0115534, 0.987008, 10564e-8, 0, 0.0166134, 0.98421, 133179e-9, 0, 0.0226552, 0.98021, 161746e-9, 0, 0.0296838, 0.973676, 161821e-9, 0, 0.0377016, 0.967052, 178635e-9, 0, 0.0467079, 0.959385, 206765e-9, 0, 0.0567013, 0.949461, 22476e-8, 0, 0.0676796, 0.939578, 23574e-8, 0, 0.0796403, 0.932416, 25893e-8, 0, 0.0925812, 0.923759, 271228e-9, 0, 0.106501, 0.914223, 289165e-9, 0, 0.121397, 0.902942, 301156e-9, 0, 0.13727, 0.890419, 313852e-9, 0, 0.15412, 0.876639, 324408e-9, 0, 0.171946, 0.861316, 33249e-8, 0, 0.190751, 0.84496, 338497e-9, 0, 0.210537, 0.828427, 345861e-9, 0, 0.231305, 0.811871, 347863e-9, 0, 0.253057, 0.794397, 350225e-9, 0, 0.275797, 0.775726, 349915e-9, 0, 0.299525, 0.75617, 347297e-9, 0, 0.324242, 0.736091, 344232e-9, 0, 0.349947, 0.716213, 340835e-9, 0, 0.376633, 0.695736, 332369e-9, 0, 0.404289, 0.674961, 327943e-9, 0, 0.432895, 0.653518, 318533e-9, 0, 0.462415, 0.632574, 310391e-9, 0, 0.492788, 0.61134, 300755e-9, 0, 0.523909, 0.590017, 290506e-9, 0, 0.555556, 0.568752, 280446e-9, 0, 0.587302, 0.548061, 269902e-9, 0, 0.619048, 0.52711, 258815e-9, 0, 0.650794, 0.506682, 248481e-9, 0, 0.68254, 0.486524, 237141e-9, 0, 0.714286, 0.466812, 226872e-9, 0, 0.746032, 0.44732, 216037e-9, 0, 0.777778, 0.428473, 205629e-9, 0, 0.809524, 0.409921, 195691e-9, 0, 0.84127, 0.392028, 185457e-9, 0, 0.873016, 0.374606, 176436e-9, 0, 0.904762, 0.357601, 166508e-9, 0, 0.936508, 0.341348, 158385e-9, 0, 0.968254, 0.32542, 149203e-9, 0, 1, 1, 103967e-10, 0, 0, 1, 10397e-9, 0, 0, 1, 104019e-10, 0, 0, 0.999999, 104231e-10, 0, 0, 0.999998, 104806e-10, 0, 0, 0.999995, 106042e-10, 0, 0, 0.999991, 108366e-10, 0, 0, 0.999982, 112415e-10, 0, 0, 0.999968, 119174e-10, 0, 0, 0.99995, 130227e-10, 0, 0, 0.999922, 148176e-10, 0, 0, 0.999884, 177303e-10, 0, 0, 0.99983, 224564e-10, 0, 0, 0.999758, 300966e-10, 0, 0, 0.999654, 423193e-10, 0, 549083e-11, 0.999503, 614848e-10, 0, 296087e-9, 0.999237, 903576e-10, 0, 123144e-8, 0.998491, 1271e-7, 0, 295954e-8, 0.994594, 107754e-9, 0, 555829e-8, 0.99178, 103025e-9, 0, 907209e-8, 0.989265, 11154e-8, 0, 0.0135257, 0.986998, 136296e-9, 0, 0.0189327, 0.984137, 169154e-9, 0, 0.0252993, 0.979798, 196671e-9, 0, 0.0326272, 0.97337, 196678e-9, 0, 0.0409157, 0.967239, 223121e-9, 0, 0.0501623, 0.959543, 253809e-9, 0, 0.0603638, 0.949466, 265972e-9, 0, 0.0715171, 0.939074, 288372e-9, 0, 0.0836187, 0.931118, 310983e-9, 0, 0.0966657, 0.922525, 325561e-9, 0, 0.110656, 0.912983, 345725e-9, 0, 0.125588, 0.901617, 3556e-7, 0, 0.141461, 0.889487, 374012e-9, 0, 0.158275, 0.875787, 383445e-9, 0, 0.176031, 0.860654, 393972e-9, 0, 0.19473, 0.844417, 400311e-9, 0, 0.214374, 0.82741, 405004e-9, 0, 0.234967, 0.810545, 407378e-9, 0, 0.256512, 0.793312, 407351e-9, 0, 0.279011, 0.774847, 406563e-9, 0, 0.302468, 0.755621, 404903e-9, 0, 0.326887, 0.735511, 397486e-9, 0, 0.352266, 0.715435, 39357e-8, 0, 0.378605, 0.695403, 384739e-9, 0, 0.405897, 0.674681, 376108e-9, 0, 0.43413, 0.65359, 365997e-9, 0, 0.463277, 0.632471, 354957e-9, 0, 0.493295, 0.61151, 343593e-9, 0, 0.524106, 0.59064, 331841e-9, 0, 0.555561, 0.569386, 318891e-9, 0, 0.587302, 0.548785, 3072e-7, 0, 0.619048, 0.528146, 29361e-8, 0, 0.650794, 0.507872, 281709e-9, 0, 0.68254, 0.487805, 268627e-9, 0, 0.714286, 0.468196, 255887e-9, 0, 0.746032, 0.448922, 243997e-9, 0, 0.777778, 0.430093, 231662e-9, 0, 0.809524, 0.411845, 220339e-9, 0, 0.84127, 0.393808, 208694e-9, 0, 0.873016, 0.376615, 198045e-9, 0, 0.904762, 0.359655, 187375e-9, 0, 0.936508, 0.343452, 177371e-9, 0, 0.968254, 0.32765, 167525e-9, 0, 1, 1, 169351e-10, 0, 0, 1, 169356e-10, 0, 0, 1, 169427e-10, 0, 0, 0.999999, 169736e-10, 0, 0, 0.999998, 170575e-10, 0, 0, 0.999995, 172372e-10, 0, 0, 0.99999, 175739e-10, 0, 0, 0.999979, 181568e-10, 0, 0, 0.999966, 191206e-10, 0, 0, 0.999944, 20677e-9, 0, 0, 0.999912, 231644e-10, 0, 0, 0.999869, 271268e-10, 0, 0, 0.999811, 334272e-10, 0, 0, 0.99973, 433979e-10, 0, 0, 0.999617, 590083e-10, 0, 680315e-10, 0.999445, 829497e-10, 0, 612796e-9, 0.999138, 118019e-9, 0, 187408e-8, 0.998095, 156712e-9, 0, 395791e-8, 0.993919, 125054e-9, 0, 692144e-8, 0.991333, 126091e-9, 0, 0.0107962, 0.989226, 144912e-9, 0, 0.0155986, 0.986954, 175737e-9, 0, 0.0213364, 0.983982, 213883e-9, 0, 0.0280114, 0.979128, 234526e-9, 0, 0.0356226, 0.973327, 243725e-9, 0, 0.0441668, 0.967416, 2773e-7, 0, 0.0536399, 0.959729, 308799e-9, 0, 0.0640376, 0.949758, 322447e-9, 0, 0.0753554, 0.939173, 350021e-9, 0, 0.0875893, 0.9296, 370089e-9, 0, 0.100736, 0.921181, 391365e-9, 0, 0.114793, 0.91164, 413636e-9, 0, 0.129759, 0.900435, 427068e-9, 0, 0.145632, 0.888183, 441046e-9, 0, 0.162412, 0.874772, 454968e-9, 0, 0.180101, 0.859566, 461882e-9, 0, 0.1987, 0.843579, 471556e-9, 0, 0.218213, 0.826453, 474335e-9, 0, 0.238641, 0.809164, 477078e-9, 0, 0.259989, 0.792179, 47755e-8, 0, 0.282262, 0.773866, 472573e-9, 0, 0.305464, 0.754944, 469765e-9, 0, 0.329599, 0.735133, 462371e-9, 0, 0.35467, 0.714858, 453674e-9, 0, 0.380678, 0.694829, 443888e-9, 0, 0.407622, 0.674453, 432052e-9, 0, 0.435493, 0.653685, 420315e-9, 0, 0.464275, 0.632666, 406829e-9, 0, 0.493938, 0.611676, 392234e-9, 0, 0.524422, 0.591193, 379208e-9, 0, 0.555624, 0.570145, 36319e-8, 0, 0.587302, 0.549566, 349111e-9, 0, 0.619048, 0.529278, 334166e-9, 0, 0.650794, 0.509026, 318456e-9, 0, 0.68254, 0.489186, 30449e-8, 0, 0.714286, 0.469662, 289051e-9, 0, 0.746032, 0.450691, 275494e-9, 0, 0.777778, 0.431841, 261437e-9, 0, 0.809524, 0.413752, 247846e-9, 0, 0.84127, 0.395951, 235085e-9, 0, 0.873016, 0.378633, 222245e-9, 0, 0.904762, 0.36194, 210533e-9, 0, 0.936508, 0.345599, 198494e-9, 0, 0.968254, 0.329999, 188133e-9, 0, 1, 1, 269663e-10, 0, 0, 1, 26967e-9, 0, 0, 1, 269772e-10, 0, 0, 0.999999, 270214e-10, 0, 0, 0.999998, 271415e-10, 0, 0, 0.999994, 27398e-9, 0, 0, 0.999988, 278771e-10, 0, 0, 0.999977, 287019e-10, 0, 0, 0.999961, 300544e-10, 0, 0, 0.999937, 322138e-10, 0, 0, 0.999904, 356163e-10, 0, 0, 0.999854, 409465e-10, 0, 0, 0.99979, 492651e-10, 0, 0, 0.999699, 621722e-10, 0, 88288e-11, 0.999572, 819715e-10, 0, 223369e-9, 0.999381, 111689e-9, 0, 105414e-8, 0.999016, 153862e-9, 0, 26493e-7, 0.997437, 187667e-9, 0, 508608e-8, 0.993545, 155672e-9, 0, 840554e-8, 0.991135, 161455e-9, 0, 0.012629, 0.989157, 188241e-9, 0, 0.0177661, 0.986874, 226229e-9, 0, 0.0238198, 0.983714, 268668e-9, 0, 0.0307887, 0.978301, 277109e-9, 0, 0.0386688, 0.973227, 303446e-9, 0, 0.0474554, 0.967317, 341851e-9, 0, 0.0571428, 0.959477, 370885e-9, 0, 0.0677256, 0.950012, 392753e-9, 0, 0.0791988, 0.939484, 42781e-8, 0, 0.0915576, 0.928135, 443866e-9, 0, 0.104798, 0.919819, 472959e-9, 0, 0.118918, 0.910049, 491551e-9, 0, 0.133915, 0.899181, 512616e-9, 0, 0.149788, 0.886881, 523563e-9, 0, 0.166537, 0.87359, 540183e-9, 0, 0.184164, 0.858613, 547386e-9, 0, 0.202669, 0.842809, 554809e-9, 0, 0.222056, 0.825727, 558316e-9, 0, 0.242329, 0.808086, 557824e-9, 0, 0.263492, 0.790728, 556346e-9, 0, 0.285551, 0.772987, 552672e-9, 0, 0.30851, 0.7541, 543738e-9, 0, 0.332376, 0.734669, 536107e-9, 0, 0.357153, 0.714411, 523342e-9, 0, 0.382845, 0.694196, 512238e-9, 0, 0.409454, 0.674252, 497465e-9, 0, 0.436977, 0.65357, 481096e-9, 0, 0.465404, 0.632999, 467054e-9, 0, 0.494713, 0.611994, 448771e-9, 0, 0.524864, 0.591604, 431889e-9, 0, 0.555779, 0.571134, 415238e-9, 0, 0.587302, 0.550528, 396369e-9, 0, 0.619048, 0.530292, 379477e-9, 0, 0.650794, 0.510364, 361488e-9, 0, 0.68254, 0.490749, 343787e-9, 0, 0.714286, 0.471266, 327822e-9, 0, 0.746032, 0.452462, 310626e-9, 0, 0.777778, 0.433907, 295352e-9, 0, 0.809524, 0.415659, 279179e-9, 0, 0.84127, 0.398138, 264685e-9, 0, 0.873016, 0.380833, 249905e-9, 0, 0.904762, 0.364247, 236282e-9, 0, 0.936508, 0.348041, 222905e-9, 0, 0.968254, 0.332389, 210522e-9, 0, 1, 1, 420604e-10, 0, 0, 1, 420614e-10, 0, 0, 1, 420757e-10, 0, 0, 0.999999, 42138e-9, 0, 0, 0.999997, 423067e-10, 0, 0, 0.999993, 426668e-10, 0, 0, 0.999986, 433372e-10, 0, 0, 0.999974, 444857e-10, 0, 0, 0.999956, 463554e-10, 0, 0, 0.99993, 493105e-10, 0, 0, 0.999892, 539077e-10, 0, 0, 0.999838, 610005e-10, 0, 0, 0.999767, 718822e-10, 0, 0, 0.999666, 884581e-10, 0, 365471e-10, 0.999525, 113398e-9, 0, 485623e-9, 0.999311, 150043e-9, 0, 162096e-8, 0.998865, 200063e-9, 0, 355319e-8, 0.996278, 211014e-9, 0, 633818e-8, 0.992956, 189672e-9, 0, 0.0100043, 0.991017, 210262e-9, 0, 0.0145648, 0.989055, 244292e-9, 0, 0.0200237, 0.986741, 290481e-9, 0, 0.0263798, 0.983288, 334303e-9, 0, 0.033629, 0.977784, 340307e-9, 0, 0.0417652, 0.973037, 377864e-9, 0, 0.0507821, 0.967181, 4239e-7, 0, 0.060673, 0.958971, 443854e-9, 0, 0.0714314, 0.950093, 483039e-9, 0, 0.0830518, 0.939552, 517934e-9, 0, 0.0955288, 0.927678, 539449e-9, 0, 0.108859, 0.918278, 568604e-9, 0, 0.123038, 0.908449, 588505e-9, 0, 0.138065, 0.897713, 612473e-9, 0, 0.153938, 0.885533, 625575e-9, 0, 0.170657, 0.872131, 63854e-8, 0, 0.188224, 0.857517, 647034e-9, 0, 0.20664, 0.841796, 65209e-8, 0, 0.225909, 0.824726, 6544e-7, 0, 0.246035, 0.807297, 655744e-9, 0, 0.267022, 0.789058, 646716e-9, 0, 0.288878, 0.77189, 643898e-9, 0, 0.311607, 0.753082, 629973e-9, 0, 0.335216, 0.7341, 621564e-9, 0, 0.359713, 0.714094, 605171e-9, 0, 0.385103, 0.693839, 588752e-9, 0, 0.41139, 0.673891, 573294e-9, 0, 0.438576, 0.653565, 552682e-9, 0, 0.466656, 0.633326, 533446e-9, 0, 0.495617, 0.612582, 514635e-9, 0, 0.525431, 0.59205, 49303e-8, 0, 0.556041, 0.571918, 471842e-9, 0, 0.587338, 0.551572, 451713e-9, 0, 0.619048, 0.531553, 430049e-9, 0, 0.650794, 0.51175, 410445e-9, 0, 0.68254, 0.49238, 390098e-9, 0, 0.714286, 0.473143, 370033e-9, 0, 0.746032, 0.45423, 351205e-9, 0, 0.777778, 0.435963, 332049e-9, 0, 0.809524, 0.41787, 315021e-9, 0, 0.84127, 0.400387, 297315e-9, 0, 0.873016, 0.383332, 281385e-9, 0, 0.904762, 0.366665, 265397e-9, 0, 0.936508, 0.350633, 250601e-9, 0, 0.968254, 0.334964, 23589e-8, 0, 1, 1, 643736e-10, 0, 0, 1, 64375e-9, 0, 0, 1, 643947e-10, 0, 0, 0.999999, 64481e-9, 0, 0, 0.999997, 647143e-10, 0, 0, 0.999994, 652119e-10, 0, 0, 0.999985, 661359e-10, 0, 0, 0.999972, 677116e-10, 0, 0, 0.999952, 702599e-10, 0, 0, 0.999922, 742517e-10, 0, 0, 0.99988, 803906e-10, 0, 0, 0.99982, 897315e-10, 0, 0, 0.999741, 103838e-9, 0, 0, 0.999629, 12496e-8, 0, 149024e-9, 0.999474, 156161e-9, 0, 861027e-9, 0.999229, 201034e-9, 0, 231198e-8, 0.998662, 259069e-9, 0, 458147e-8, 0.995299, 245439e-9, 0, 770895e-8, 0.992732, 24498e-8, 0, 0.0117126, 0.990847, 273211e-9, 0, 0.0165989, 0.988911, 316492e-9, 0, 0.0223674, 0.98654, 37161e-8, 0, 0.0290135, 0.982636, 410352e-9, 0, 0.0365309, 0.977346, 421756e-9, 0, 0.0449117, 0.972909, 475578e-9, 0, 0.0541481, 0.966821, 522482e-9, 0, 0.0642326, 0.958686, 545008e-9, 0, 0.075158, 0.949754, 589286e-9, 0, 0.0869181, 0.939184, 619995e-9, 0, 0.0995074, 0.927505, 654266e-9, 0, 0.112922, 0.916606, 682362e-9, 0, 0.127157, 0.906707, 704286e-9, 0, 0.142212, 0.895937, 725909e-9, 0, 0.158085, 0.883913, 743939e-9, 0, 0.174776, 0.870642, 755157e-9, 0, 0.192287, 0.856241, 764387e-9, 0, 0.210619, 0.84069, 771032e-9, 0, 0.229775, 0.823728, 765906e-9, 0, 0.249761, 0.806481, 767604e-9, 0, 0.270582, 0.787924, 754385e-9, 0, 0.292243, 0.770588, 749668e-9, 0, 0.314753, 0.751991, 731613e-9, 0, 0.338118, 0.733407, 717655e-9, 0, 0.362347, 0.713688, 700604e-9, 0, 0.387447, 0.693595, 678765e-9, 0, 0.413424, 0.673426, 657042e-9, 0, 0.440284, 0.65359, 635892e-9, 0, 0.468027, 0.633576, 611569e-9, 0, 0.496645, 0.613144, 586011e-9, 0, 0.526122, 0.592711, 563111e-9, 0, 0.556417, 0.572722, 537699e-9, 0, 0.587451, 0.552762, 512556e-9, 0, 0.619048, 0.532985, 489757e-9, 0, 0.650794, 0.513219, 464139e-9, 0, 0.68254, 0.493992, 442193e-9, 0, 0.714286, 0.47509, 418629e-9, 0, 0.746032, 0.456287, 397045e-9, 0, 0.777778, 0.438152, 375504e-9, 0, 0.809524, 0.420294, 35492e-8, 0, 0.84127, 0.402749, 335327e-9, 0, 0.873016, 0.385879, 316422e-9, 0, 0.904762, 0.369352, 298333e-9, 0, 0.936508, 0.353301, 281417e-9, 0, 0.968254, 0.337781, 265203e-9, 0, 1, 1, 968267e-10, 0, 0, 1, 968284e-10, 0, 0, 1, 968556e-10, 0, 0, 0.999999, 969733e-10, 0, 0, 0.999997, 972913e-10, 0, 0, 0.999993, 979688e-10, 0, 0, 0.999984, 992239e-10, 0, 0, 0.999969, 101356e-9, 0, 0, 0.999946, 104784e-9, 0, 0, 0.999913, 110111e-9, 0, 0, 0.999868, 118217e-9, 0, 0, 0.999801, 130396e-9, 0, 0, 0.999712, 148523e-9, 0, 124907e-10, 0.999589, 175233e-9, 0, 355405e-9, 0.999416, 213999e-9, 0, 13528e-7, 0.999136, 268529e-9, 0, 312557e-8, 0.998367, 333088e-9, 0, 573045e-8, 0.994701, 304757e-9, 0, 919397e-8, 0.992497, 318031e-9, 0, 0.0135261, 0.990608, 353863e-9, 0, 0.0187278, 0.988715, 409044e-9, 0, 0.0247947, 0.986241, 472967e-9, 0, 0.0317196, 0.981696, 495104e-9, 0, 0.039494, 0.977097, 532873e-9, 0, 0.0481087, 0.972583, 594447e-9, 0, 0.0575549, 0.966142, 636867e-9, 0, 0.0678242, 0.95823, 669899e-9, 0, 0.0789089, 0.949677, 719499e-9, 0, 0.0908023, 0.939226, 750584e-9, 0, 0.103499, 0.927501, 793183e-9, 0, 0.116993, 0.915199, 81995e-8, 0, 0.131282, 0.90498, 847654e-9, 0, 0.146364, 0.894243, 868929e-9, 0, 0.162237, 0.882154, 884278e-9, 0, 0.178902, 0.869161, 898108e-9, 0, 0.196358, 0.854751, 901254e-9, 0, 0.21461, 0.839368, 90679e-8, 0, 0.23366, 0.822874, 901541e-9, 0, 0.253512, 0.805514, 897297e-9, 0, 0.274174, 0.78716, 881856e-9, 0, 0.29565, 0.769061, 870032e-9, 0, 0.31795, 0.751, 851719e-9, 0, 0.341081, 0.732614, 830671e-9, 0, 0.365053, 0.713171, 806569e-9, 0, 0.389874, 0.693472, 78338e-8, 0, 0.415553, 0.673528, 756404e-9, 0, 0.442098, 0.653397, 726872e-9, 0, 0.469512, 0.633781, 700494e-9, 0, 0.497794, 0.613877, 67105e-8, 0, 0.526935, 0.593506, 640361e-9, 0, 0.556908, 0.573667, 613502e-9, 0, 0.587657, 0.553932, 583177e-9, 0, 0.61906, 0.534345, 554375e-9, 0, 0.650794, 0.515042, 527811e-9, 0, 0.68254, 0.495674, 499367e-9, 0, 0.714286, 0.477132, 47429e-8, 0, 0.746032, 0.458609, 447726e-9, 0, 0.777778, 0.440354, 424205e-9, 0, 0.809524, 0.422765, 399549e-9, 0, 0.84127, 0.405472, 378315e-9, 0, 0.873016, 0.388482, 355327e-9, 0, 0.904762, 0.372191, 336122e-9, 0, 0.936508, 0.356099, 315247e-9, 0, 0.968254, 0.340737, 29794e-8, 0, 1, 1, 143327e-9, 0, 0, 1, 14333e-8, 0, 0, 1, 143366e-9, 0, 0, 0.999999, 143524e-9, 0, 0, 0.999996, 143952e-9, 0, 0, 0.999991, 144862e-9, 0, 0, 0.999981, 146544e-9, 0, 0, 0.999966, 149391e-9, 0, 0, 0.999941, 153946e-9, 0, 0, 0.999905, 160971e-9, 0, 0, 0.999852, 171562e-9, 0, 0, 0.99978, 18729e-8, 0, 0, 0.999681, 210386e-9, 0, 826239e-10, 0.999546, 243906e-9, 0, 664807e-9, 0.999352, 291739e-9, 0, 196192e-8, 0.999027, 357419e-9, 0, 405941e-8, 0.997886, 422349e-9, 0, 699664e-8, 0.99419, 385008e-9, 0, 0.0107896, 0.99214, 409775e-9, 0, 0.0154415, 0.990274, 456418e-9, 0, 0.0209488, 0.988455, 527008e-9, 0, 0.0273037, 0.985804, 597685e-9, 0, 0.0344969, 0.98103, 613124e-9, 0, 0.0425183, 0.976674, 668321e-9, 0, 0.0513575, 0.972021, 736985e-9, 0, 0.0610046, 0.965274, 773789e-9, 0, 0.0714508, 0.958046, 830852e-9, 0, 0.0826877, 0.949333, 875766e-9, 0, 0.0947085, 0.939135, 917088e-9, 0, 0.107507, 0.927119, 952244e-9, 0, 0.121078, 0.91469, 990626e-9, 0, 0.135419, 0.903006, 101304e-8, 0, 0.150526, 0.892368, 103834e-8, 0, 0.166399, 0.880231, 105002e-8, 0, 0.183038, 0.867432, 106331e-8, 0, 0.200443, 0.853208, 106783e-8, 0, 0.218618, 0.837956, 106458e-8, 0, 0.237566, 0.821772, 105945e-8, 0, 0.257291, 0.804328, 104685e-8, 0, 0.2778, 0.786465, 103178e-8, 0, 0.2991, 0.768004, 101077e-8, 0, 0.321199, 0.74972, 985504e-9, 0, 0.344106, 0.731682, 962893e-9, 0, 0.36783, 0.712813, 932146e-9, 0, 0.392383, 0.693139, 89871e-8, 0, 0.417774, 0.673566, 869678e-9, 0, 0.444013, 0.653483, 835525e-9, 0, 0.471107, 0.633891, 799853e-9, 0, 0.49906, 0.614433, 766838e-9, 0, 0.527869, 0.594586, 732227e-9, 0, 0.557517, 0.574769, 696442e-9, 0, 0.587966, 0.555149, 663935e-9, 0, 0.61913, 0.535898, 629826e-9, 0, 0.650794, 0.516753, 596486e-9, 0, 0.68254, 0.497816, 567078e-9, 0, 0.714286, 0.479034, 534399e-9, 0, 0.746032, 0.460975, 507013e-9, 0, 0.777778, 0.442935, 477421e-9, 0, 0.809524, 0.425263, 451101e-9, 0, 0.84127, 0.408248, 424964e-9, 0, 0.873016, 0.391339, 39993e-8, 0, 0.904762, 0.37513, 377619e-9, 0, 0.936508, 0.359172, 354418e-9, 0, 0.968254, 0.343876, 334823e-9, 0, 1, 1, 209042e-9, 0, 0, 1, 209045e-9, 0, 0, 1, 209093e-9, 0, 0, 0.999999, 209304e-9, 0, 0, 0.999996, 209871e-9, 0, 0, 0.999991, 211078e-9, 0, 0, 0.999979, 213304e-9, 0, 0, 0.999963, 217061e-9, 0, 0, 0.999933, 223042e-9, 0, 0, 0.999894, 232206e-9, 0, 0, 0.999837, 245901e-9, 0, 0, 0.999756, 266023e-9, 0, 102927e-11, 0.999648, 295204e-9, 0, 233468e-9, 0.999499, 336958e-9, 0, 108237e-8, 0.999283, 395563e-9, 0, 268832e-8, 0.998896, 473785e-9, 0, 511138e-8, 0.997006, 520008e-9, 0, 837705e-8, 0.993819, 497261e-9, 0, 0.0124928, 0.991632, 523722e-9, 0, 0.0174561, 0.989875, 587258e-9, 0, 0.0232596, 0.988109, 676329e-9, 0, 0.0298932, 0.985155, 747701e-9, 0, 0.0373453, 0.980479, 768803e-9, 0, 0.0456045, 0.976271, 841054e-9, 0, 0.0546593, 0.971347, 911469e-9, 0, 0.0644994, 0.964528, 953057e-9, 0, 0.0751152, 0.957632, 102221e-8, 0, 0.0864981, 0.948681, 106122e-8, 0, 0.0986407, 0.938716, 111857e-8, 0, 0.111537, 0.926629, 114762e-8, 0, 0.125182, 0.914025, 118995e-8, 0, 0.139571, 0.901026, 121228e-8, 0, 0.154703, 0.890358, 123946e-8, 0, 0.170576, 0.878283, 12527e-7, 0, 0.18719, 0.865459, 125536e-8, 0, 0.204547, 0.851407, 126134e-8, 0, 0.222648, 0.836276, 124759e-8, 0, 0.241498, 0.820436, 124443e-8, 0, 0.261101, 0.803253, 122071e-8, 0, 0.281465, 0.785562, 120107e-8, 0, 0.302595, 0.76718, 117762e-8, 0, 0.324501, 0.748551, 114289e-8, 0, 0.347192, 0.730564, 110872e-8, 0, 0.370679, 0.712253, 107636e-8, 0, 0.394973, 0.692867, 103646e-8, 0, 0.420085, 0.673695, 996793e-9, 0, 0.446027, 0.653912, 95675e-8, 0, 0.47281, 0.634129, 916739e-9, 0, 0.500441, 0.615004, 874401e-9, 0, 0.528921, 0.595587, 833411e-9, 0, 0.558244, 0.575965, 794556e-9, 0, 0.588384, 0.5566, 75196e-8, 0, 0.619281, 0.537428, 716381e-9, 0, 0.650795, 0.518623, 676558e-9, 0, 0.68254, 0.499964, 64074e-8, 0, 0.714286, 0.481356, 605984e-9, 0, 0.746032, 0.463279, 570256e-9, 0, 0.777778, 0.445673, 540138e-9, 0, 0.809524, 0.428032, 507299e-9, 0, 0.84127, 0.411112, 479553e-9, 0, 0.873016, 0.394444, 450737e-9, 0, 0.904762, 0.378247, 424269e-9, 0, 0.936508, 0.362415, 399111e-9, 0, 0.968254, 0.347103, 375274e-9, 0, 1, 1, 300729e-9, 0, 0, 1, 300733e-9, 0, 0, 1, 300797e-9, 0, 0, 0.999998, 301072e-9, 0, 0, 0.999996, 301817e-9, 0, 0, 0.999989, 303398e-9, 0, 0, 0.999977, 306309e-9, 0, 0, 0.999958, 311209e-9, 0, 0, 0.999927, 318975e-9, 0, 0, 0.999884, 330804e-9, 0, 0, 0.99982, 34834e-8, 0, 0, 0.999733, 373854e-9, 0, 326995e-10, 0.999613, 410424e-9, 0, 477174e-9, 0.999447, 462047e-9, 0, 161099e-8, 0.999204, 533322e-9, 0, 353153e-8, 0.998725, 624964e-9, 0, 627965e-8, 0.995871, 631786e-9, 0, 98693e-7, 0.993194, 632017e-9, 0, 0.0143011, 0.991541, 68923e-8, 0, 0.019568, 0.989773, 766892e-9, 0, 0.0256593, 0.987647, 863668e-9, 0, 0.0325625, 0.984193, 922089e-9, 0, 0.0402647, 0.980016, 970749e-9, 0, 0.0487532, 0.975859, 106027e-8, 0, 0.058016, 0.970514, 112239e-8, 0, 0.0680419, 0.963625, 117212e-8, 0, 0.0788208, 0.956959, 125211e-8, 0, 0.0903439, 0.947956, 129411e-8, 0, 0.102604, 0.93809, 135879e-8, 0, 0.115594, 0.92659, 139309e-8, 0, 0.129309, 0.913829, 143253e-8, 0, 0.143745, 0.90005, 145809e-8, 0, 0.158901, 0.888129, 14748e-7, 0, 0.174774, 0.87607, 148756e-8, 0, 0.191365, 0.863461, 148714e-8, 0, 0.208674, 0.849594, 148892e-8, 0, 0.226705, 0.834531, 146496e-8, 0, 0.245461, 0.81903, 14579e-7, 0, 0.264947, 0.802122, 143039e-8, 0, 0.28517, 0.78445, 139717e-8, 0, 0.306137, 0.766434, 136312e-8, 0, 0.327857, 0.747816, 132597e-8, 0, 0.350341, 0.729519, 128323e-8, 0, 0.373598, 0.711454, 123803e-8, 0, 0.397642, 0.692699, 119097e-8, 0, 0.422485, 0.673723, 114565e-8, 0, 0.448139, 0.654386, 109552e-8, 0, 0.474619, 0.634673, 104553e-8, 0, 0.501933, 0.615554, 99985e-8, 0, 0.530089, 0.596462, 948207e-9, 0, 0.559087, 0.577385, 902299e-9, 0, 0.588913, 0.558257, 856448e-9, 0, 0.619525, 0.5392, 810395e-9, 0, 0.650826, 0.520543, 768558e-9, 0, 0.68254, 0.502206, 7239e-7, 0, 0.714286, 0.48402, 685794e-9, 0, 0.746032, 0.465779, 64471e-8, 0, 0.777778, 0.448455, 609583e-9, 0, 0.809524, 0.431091, 57227e-8, 0, 0.84127, 0.414147, 54042e-8, 0, 0.873016, 0.39765, 506545e-9, 0, 0.904762, 0.381576, 477635e-9, 0, 0.936508, 0.365881, 448446e-9, 0, 0.968254, 0.350582, 421424e-9, 0, 1, 1, 427144e-9, 0, 0, 1, 427151e-9, 0, 0, 1, 427232e-9, 0, 0, 0.999998, 42759e-8, 0, 0, 0.999995, 428555e-9, 0, 0, 0.999988, 430603e-9, 0, 0, 0.999976, 434368e-9, 0, 0, 0.999952, 440688e-9, 0, 0, 0.999919, 450667e-9, 0, 0, 0.999871, 46578e-8, 0, 0, 0.999801, 488024e-9, 0, 0, 0.999704, 520092e-9, 0, 129791e-9, 0.999572, 565553e-9, 0, 821056e-9, 0.999389, 628906e-9, 0, 225241e-8, 0.999114, 714911e-9, 0, 449109e-8, 0.998488, 819218e-9, 0, 756249e-8, 0.995234, 80415e-8, 0, 0.0114716, 0.993021, 830181e-9, 0, 0.0162131, 0.991407, 902645e-9, 0, 0.021776, 0.989625, 996934e-9, 0, 0.0281471, 0.987064, 109707e-8, 0, 0.0353118, 0.983265, 114353e-8, 0, 0.0432562, 0.979535, 12272e-7, 0, 0.0519665, 0.975224, 132642e-8, 0, 0.0614298, 0.969574, 138092e-8, 0, 0.0716348, 0.963021, 145896e-8, 0, 0.0825709, 0.956046, 152834e-8, 0, 0.094229, 0.947136, 158217e-8, 0, 0.106602, 0.937313, 16347e-7, 0, 0.119682, 0.926073, 168383e-8, 0, 0.133465, 0.913121, 171627e-8, 0, 0.147947, 0.899165, 174229e-8, 0, 0.163125, 0.885891, 176137e-8, 0, 0.178998, 0.873783, 176406e-8, 0, 0.195566, 0.861331, 176156e-8, 0, 0.21283, 0.847569, 175346e-8, 0, 0.230793, 0.832785, 172753e-8, 0, 0.249459, 0.817442, 170204e-8, 0, 0.268832, 0.800613, 166576e-8, 0, 0.28892, 0.783597, 162909e-8, 0, 0.30973, 0.76571, 15826e-7, 0, 0.331271, 0.747021, 153106e-8, 0, 0.353554, 0.728593, 148036e-8, 0, 0.37659, 0.710661, 142808e-8, 0, 0.400391, 0.692426, 136906e-8, 0, 0.424973, 0.673623, 131066e-8, 0, 0.450347, 0.65494, 125569e-8, 0, 0.476531, 0.635448, 119517e-8, 0, 0.503535, 0.616221, 113828e-8, 0, 0.531372, 0.597531, 10816e-7, 0, 0.560047, 0.578795, 102673e-8, 0, 0.589554, 0.559892, 970985e-9, 0, 0.619869, 0.541307, 919773e-9, 0, 0.650923, 0.522608, 868479e-9, 0, 0.68254, 0.504484, 82137e-8, 0, 0.714286, 0.486603, 772916e-9, 0, 0.746032, 0.468802, 730353e-9, 0, 0.777778, 0.451172, 684955e-9, 0, 0.809524, 0.434348, 647565e-9, 0, 0.84127, 0.417445, 605863e-9, 0, 0.873016, 0.401077, 571885e-9, 0, 0.904762, 0.385039, 536034e-9, 0, 0.936508, 0.369483, 504227e-9, 0, 0.968254, 0.354272, 473165e-9, 0, 1, 1, 599525e-9, 0, 0, 1, 599533e-9, 0, 0, 1, 599639e-9, 0, 0, 0.999998, 600097e-9, 0, 0, 0.999994, 601336e-9, 0, 0, 0.999987, 603958e-9, 0, 0, 0.999972, 608775e-9, 0, 0, 0.999949, 616842e-9, 0, 0, 0.999912, 629534e-9, 0, 0, 0.999857, 648658e-9, 0, 0, 0.999781, 676615e-9, 0, 538873e-11, 0.999674, 716574e-9, 0, 308602e-9, 0.999528, 772641e-9, 0, 127003e-8, 0.999326, 849806e-9, 0, 300783e-8, 0.999009, 952682e-9, 0, 556637e-8, 0.998112, 106394e-8, 0, 895889e-8, 0.994496, 102228e-8, 0, 0.0131827, 0.992806, 108586e-8, 0, 0.0182277, 0.991211, 11759e-7, 0, 0.0240795, 0.989415, 128955e-8, 0, 0.030723, 0.986499, 139038e-8, 0, 0.0381418, 0.982679, 144539e-8, 0, 0.046321, 0.978839, 153954e-8, 0, 0.0552459, 0.974295, 164417e-8, 0, 0.0649034, 0.968784, 171517e-8, 0, 0.0752814, 0.962324, 180282e-8, 0, 0.0863693, 0.954956, 186387e-8, 0, 0.0981578, 0.94624, 193817e-8, 0, 0.110639, 0.936517, 198156e-8, 0, 0.123806, 0.925186, 203042e-8, 0, 0.137655, 0.91252, 20664e-7, 0, 0.15218, 0.898441, 207822e-8, 0, 0.16738, 0.884394, 20992e-7, 0, 0.183253, 0.871273, 208748e-8, 0, 0.199799, 0.859057, 208686e-8, 0, 0.21702, 0.845243, 205519e-8, 0, 0.234918, 0.830723, 202868e-8, 0, 0.253496, 0.815801, 199501e-8, 0, 0.272761, 0.79914, 194193e-8, 0, 0.292719, 0.782372, 188824e-8, 0, 0.313377, 0.76482, 183695e-8, 0, 0.334745, 0.746586, 177418e-8, 0, 0.356833, 0.7281, 170628e-8, 0, 0.379654, 0.709842, 164063e-8, 0, 0.403221, 0.692019, 157355e-8, 0, 0.427548, 0.67364, 150262e-8, 0, 0.452651, 0.655277, 143473e-8, 0, 0.478545, 0.636438, 136371e-8, 0, 0.505246, 0.617364, 129911e-8, 0, 0.532768, 0.598603, 123014e-8, 0, 0.561122, 0.580195, 116587e-8, 0, 0.590309, 0.561786, 110398e-8, 0, 0.620318, 0.543377, 104148e-8, 0, 0.651102, 0.525093, 983984e-9, 0, 0.682545, 0.506791, 92667e-8, 0, 0.714286, 0.489291, 874326e-9, 0, 0.746032, 0.471811, 821734e-9, 0, 0.777778, 0.454435, 774698e-9, 0, 0.809524, 0.437493, 727302e-9, 0, 0.84127, 0.420977, 684039e-9, 0, 0.873016, 0.404729, 64373e-8, 0, 0.904762, 0.388756, 60285e-8, 0, 0.936508, 0.373344, 56765e-8, 0, 0.968254, 0.358191, 531929e-9, 0, 1, 1, 832169e-9, 0, 0, 1, 832178e-9, 0, 0, 1, 83231e-8, 0, 0, 0.999998, 832893e-9, 0, 0, 0.999995, 834465e-9, 0, 0, 0.999985, 837791e-9, 0, 0, 0.999969, 843893e-9, 0, 0, 0.999944, 854086e-9, 0, 0, 0.999903, 870071e-9, 0, 0, 0.999843, 894042e-9, 0, 0, 0.999759, 928865e-9, 0, 531805e-10, 0.999643, 978242e-9, 0, 579365e-9, 0.99948, 104684e-8, 0, 182774e-8, 0.999255, 114012e-8, 0, 387804e-8, 0.998885, 126188e-8, 0, 675709e-8, 0.997405, 135888e-8, 0, 0.010468, 0.99424, 133626e-8, 0, 0.0150018, 0.992458, 140905e-8, 0, 0.0203443, 0.990929, 152305e-8, 0, 0.0264786, 0.989116, 165882e-8, 0, 0.0333875, 0.985624, 174128e-8, 0, 0.0410536, 0.982003, 182108e-8, 0, 0.0494609, 0.978336, 194498e-8, 0, 0.0585941, 0.973184, 202708e-8, 0, 0.0684396, 0.9678, 212166e-8, 0, 0.0789851, 0.961348, 221366e-8, 0, 0.0902199, 0.953841, 228219e-8, 0, 0.102134, 0.94534, 235662e-8, 0, 0.114721, 0.935552, 240572e-8, 0, 0.127972, 0.924064, 244405e-8, 0, 0.141884, 0.911827, 247557e-8, 0, 0.156451, 0.897731, 248374e-8, 0, 0.171672, 0.883409, 249863e-8, 0, 0.187545, 0.868625, 246688e-8, 0, 0.20407, 0.856529, 246523e-8, 0, 0.221249, 0.842999, 242368e-8, 0, 0.239083, 0.828505, 237354e-8, 0, 0.257578, 0.813825, 232588e-8, 0, 0.276738, 0.797813, 226731e-8, 0, 0.296569, 0.781097, 219704e-8, 0, 0.31708, 0.764038, 212394e-8, 0, 0.338281, 0.746067, 204786e-8, 0, 0.360181, 0.727687, 196728e-8, 0, 0.382794, 0.709571, 188779e-8, 0, 0.406133, 0.691503, 180532e-8, 0, 0.430213, 0.673673, 171849e-8, 0, 0.45505, 0.655732, 164147e-8, 0, 0.480662, 0.637399, 155858e-8, 0, 0.507065, 0.618616, 147641e-8, 0, 0.534278, 0.60005, 140125e-8, 0, 0.562313, 0.581713, 132441e-8, 0, 0.59118, 0.563546, 125014e-8, 0, 0.620875, 0.545605, 118249e-8, 0, 0.651373, 0.527559, 11116e-7, 0, 0.682593, 0.509764, 104979e-8, 0, 0.714286, 0.49193, 985977e-9, 0, 0.746032, 0.475011, 928592e-9, 0, 0.777778, 0.457878, 873466e-9, 0, 0.809524, 0.440979, 819585e-9, 0, 0.84127, 0.424613, 772365e-9, 0, 0.873016, 0.408549, 722195e-9, 0, 0.904762, 0.392771, 680014e-9, 0, 0.936508, 0.377317, 636797e-9, 0, 0.968254, 0.362352, 598318e-9, 0, 1, 1, 114313e-8, 0, 0, 1, 114314e-8, 0, 0, 0.999999, 114331e-8, 0, 0, 0.999998, 114404e-8, 0, 0, 0.999994, 114601e-8, 0, 0, 0.999984, 115019e-8, 0, 0, 0.999967, 115784e-8, 0, 0, 0.999937, 11706e-7, 0, 0, 0.999894, 119054e-8, 0, 0, 0.999828, 122031e-8, 0, 0, 0.999735, 126331e-8, 0, 169263e-9, 0.999606, 132382e-8, 0, 949167e-9, 0.999426, 14071e-7, 0, 249668e-8, 0.999173, 151895e-8, 0, 486392e-8, 0.99873, 166102e-8, 0, 806323e-8, 0.996243, 17023e-7, 0, 0.0120895, 0.993779, 172782e-8, 0, 0.0169288, 0.9919, 18108e-7, 0, 0.0225633, 0.990524, 196028e-8, 0, 0.028974, 0.98868, 212014e-8, 0, 0.036142, 0.984663, 217598e-8, 0, 0.044049, 0.981457, 230563e-8, 0, 0.0526781, 0.977608, 243966e-8, 0, 0.0620137, 0.972215, 251336e-8, 0, 0.0720418, 0.966798, 26285e-7, 0, 0.0827499, 0.960241, 271409e-8, 0, 0.0941271, 0.952489, 278381e-8, 0, 0.106164, 0.944127, 285399e-8, 0, 0.118852, 0.934282, 290994e-8, 0, 0.132185, 0.923271, 294558e-8, 0, 0.146157, 0.910803, 296269e-8, 0, 0.160766, 0.896705, 296803e-8, 0, 0.176007, 0.88238, 296637e-8, 0, 0.19188, 0.867116, 293163e-8, 0, 0.208385, 0.853636, 289418e-8, 0, 0.225523, 0.840469, 284663e-8, 0, 0.243296, 0.82639, 278594e-8, 0, 0.261709, 0.811759, 271618e-8, 0, 0.280767, 0.796113, 263187e-8, 0, 0.300476, 0.779518, 254589e-8, 0, 0.320845, 0.763142, 246003e-8, 0, 0.341883, 0.745464, 236529e-8, 0, 0.363601, 0.727491, 226536e-8, 0, 0.386011, 0.709414, 216375e-8, 0, 0.409128, 0.691396, 207127e-8, 0, 0.432967, 0.67368, 197106e-8, 0, 0.457545, 0.656049, 187022e-8, 0, 0.482881, 0.638188, 177605e-8, 0, 0.508992, 0.620177, 168482e-8, 0, 0.535899, 0.601506, 158909e-8, 0, 0.563619, 0.58362, 150583e-8, 0, 0.592165, 0.565496, 141791e-8, 0, 0.621544, 0.54789, 133693e-8, 0, 0.651743, 0.530323, 126038e-8, 0, 0.682709, 0.512795, 118556e-8, 0, 0.714286, 0.495199, 111527e-8, 0, 0.746032, 0.478101, 10489e-7, 0, 0.777778, 0.461511, 984264e-9, 0, 0.809524, 0.444879, 92591e-8, 0, 0.84127, 0.428424, 866582e-9, 0, 0.873016, 0.412495, 814463e-9, 0, 0.904762, 0.396975, 764498e-9, 0, 0.936508, 0.381614, 715967e-9, 0, 0.968254, 0.366732, 672483e-9, 0, 1, 1, 155501e-8, 0, 0, 1, 155503e-8, 0, 0, 1, 155524e-8, 0, 0, 0.999998, 155615e-8, 0, 0, 0.999994, 15586e-7, 0, 0, 0.999983, 156379e-8, 0, 0, 0.999963, 15733e-7, 0, 0, 0.999932, 158911e-8, 0, 0, 0.999882, 161376e-8, 0, 0, 0.99981, 165041e-8, 0, 100875e-10, 0.999708, 170304e-8, 0, 367658e-9, 0.999565, 177658e-8, 0, 14234e-7, 0.999368, 187688e-8, 0, 327939e-8, 0.999081, 200989e-8, 0, 596629e-8, 0.99852, 217177e-8, 0, 94852e-7, 0.99549, 21745e-7, 0, 0.013824, 0.993252, 222357e-8, 0, 0.0189642, 0.991727, 235022e-8, 0, 0.0248856, 0.989951, 250561e-8, 0, 0.0315669, 0.988029, 268829e-8, 0, 0.0389882, 0.984029, 27496e-7, 0, 0.0471302, 0.980683, 289793e-8, 0, 0.0559754, 0.976554, 303315e-8, 0, 0.0655081, 0.97139, 313257e-8, 0, 0.0757138, 0.965544, 323656e-8, 0, 0.08658, 0.95912, 333432e-8, 0, 0.0980954, 0.951183, 34039e-7, 0, 0.110251, 0.942974, 347515e-8, 0, 0.123038, 0.932642, 350381e-8, 0, 0.13645, 0.922158, 354519e-8, 0, 0.150482, 0.909404, 353851e-8, 0, 0.165129, 0.896071, 35435e-7, 0, 0.18039, 0.881206, 349936e-8, 0, 0.196263, 0.866077, 347256e-8, 0, 0.212748, 0.85093, 3415e-6, 0, 0.229847, 0.837703, 333367e-8, 0, 0.247561, 0.823878, 3249e-6, 0, 0.265895, 0.809449, 316347e-8, 0, 0.284854, 0.794379, 306351e-8, 0, 0.304445, 0.778138, 29499e-7, 0, 0.324675, 0.761997, 284099e-8, 0, 0.345555, 0.744938, 272104e-8, 0, 0.367095, 0.727212, 260715e-8, 0, 0.389309, 0.709549, 248855e-8, 0, 0.41221, 0.691704, 236783e-8, 0, 0.435814, 0.673689, 225178e-8, 0, 0.460138, 0.656453, 213765e-8, 0, 0.485203, 0.639128, 202178e-8, 0, 0.511028, 0.621512, 191443e-8, 0, 0.537634, 0.603598, 180977e-8, 0, 0.565041, 0.58559, 170456e-8, 0, 0.593268, 0.567852, 160927e-8, 0, 0.622327, 0.5503, 151395e-8, 0, 0.652217, 0.533033, 142499e-8, 0, 0.682907, 0.515942, 133955e-8, 0, 0.714296, 0.498814, 12602e-7, 0, 0.746032, 0.481595, 118188e-8, 0, 0.777778, 0.465117, 111171e-8, 0, 0.809524, 0.448865, 104091e-8, 0, 0.84127, 0.432711, 976618e-9, 0, 0.873016, 0.416822, 91859e-8, 0, 0.904762, 0.401272, 857704e-9, 0, 0.936508, 0.386226, 807172e-9, 0, 0.968254, 0.371321, 75464e-8, 0, 1, 1, 209596e-8, 0, 0, 1, 209598e-8, 0, 0, 1, 209624e-8, 0, 0, 0.999997, 209736e-8, 0, 0, 0.999991, 210039e-8, 0, 0, 0.999979, 210678e-8, 0, 0, 0.999959, 211847e-8, 0, 0, 0.999925, 21379e-7, 0, 0, 0.99987, 216809e-8, 0, 0, 0.999791, 221281e-8, 0, 681487e-10, 0.999677, 227669e-8, 0, 658161e-9, 0.999521, 236533e-8, 0, 200635e-8, 0.999301, 248514e-8, 0, 41779e-7, 0.998977, 264185e-8, 0, 718648e-8, 0.998191, 281695e-8, 0, 0.0110239, 0.994801, 278518e-8, 0, 0.015672, 0.993091, 288774e-8, 0, 0.0211091, 0.991571, 303931e-8, 0, 0.0273123, 0.9897, 321643e-8, 0, 0.034259, 0.987023, 337332e-8, 0, 0.0419282, 0.983289, 346146e-8, 0, 0.0502998, 0.979892, 363704e-8, 0, 0.0593562, 0.975111, 373601e-8, 0, 0.069081, 0.970351, 38842e-7, 0, 0.0794598, 0.964131, 397053e-8, 0, 0.0904798, 0.957747, 408078e-8, 0, 0.10213, 0.949536, 413533e-8, 0, 0.1144, 0.941372, 420305e-8, 0, 0.127284, 0.931049, 422815e-8, 0, 0.140772, 0.920647, 425048e-8, 0, 0.154862, 0.908033, 42281e-7, 0, 0.169548, 0.895028, 422026e-8, 0, 0.184828, 0.879968, 415042e-8, 0, 0.200701, 0.864875, 408821e-8, 0, 0.217167, 0.84918, 400909e-8, 0, 0.234227, 0.834934, 391178e-8, 0, 0.251884, 0.821397, 380066e-8, 0, 0.270141, 0.807135, 367974e-8, 0, 0.289004, 0.792363, 355172e-8, 0, 0.308479, 0.776661, 3411e-6, 0, 0.328575, 0.760705, 328123e-8, 0, 0.349301, 0.744408, 314003e-8, 0, 0.370668, 0.726994, 29906e-7, 0, 0.392689, 0.709598, 285034e-8, 0, 0.415379, 0.692112, 271179e-8, 0, 0.438754, 0.674435, 257185e-8, 0, 0.46283, 0.65676, 243425e-8, 0, 0.48763, 0.639982, 230351e-8, 0, 0.513173, 0.622983, 21777e-7, 0, 0.539482, 0.605471, 204991e-8, 0, 0.566579, 0.58796, 193759e-8, 0, 0.594488, 0.570463, 181976e-8, 0, 0.623226, 0.553058, 171497e-8, 0, 0.6528, 0.535894, 161109e-8, 0, 0.683198, 0.519089, 151394e-8, 0, 0.714354, 0.502454, 142122e-8, 0, 0.746032, 0.485681, 133488e-8, 0, 0.777778, 0.468935, 124975e-8, 0, 0.809524, 0.452951, 117309e-8, 0, 0.84127, 0.437139, 110155e-8, 0, 0.873016, 0.421446, 103124e-8, 0, 0.904762, 0.405951, 966387e-9, 0, 0.936508, 0.391003, 908119e-9, 0, 0.968254, 0.376198, 848057e-9, 0, 1, 1, 280076e-8, 0, 0, 1, 280078e-8, 0, 0, 0.999999, 280109e-8, 0, 0, 0.999997, 280246e-8, 0, 0, 0.999992, 280616e-8, 0, 0, 0.999979, 281396e-8, 0, 0, 0.999956, 282822e-8, 0, 0, 0.999916, 285186e-8, 0, 0, 0.999857, 28885e-7, 0, 0, 0.999768, 294259e-8, 0, 196026e-9, 0.999645, 301946e-8, 0, 104842e-8, 0.99947, 312541e-8, 0, 270199e-8, 0.999229, 326733e-8, 0, 519449e-8, 0.998852, 344992e-8, 0, 852602e-8, 0.997558, 361052e-8, 0, 0.0126804, 0.994417, 35898e-7, 0, 0.017635, 0.992824, 372393e-8, 0, 0.023365, 0.991344, 390695e-8, 0, 0.0298456, 0.989337, 410392e-8, 0, 0.0370529, 0.985811, 420987e-8, 0, 0.0449651, 0.982772, 437488e-8, 0, 0.0535615, 0.979001, 455069e-8, 0, 0.0628243, 0.974102, 464462e-8, 0, 0.0727368, 0.969197, 480577e-8, 0, 0.0832844, 0.962759, 487818e-8, 0, 0.0944545, 0.956207, 498176e-8, 0, 0.106236, 0.947909, 503392e-8, 0, 0.118619, 0.939596, 507474e-8, 0, 0.131595, 0.929642, 509798e-8, 0, 0.145159, 0.918807, 508476e-8, 0, 0.159305, 0.906921, 505634e-8, 0, 0.174028, 0.893312, 498845e-8, 0, 0.189327, 0.878933, 49133e-7, 0, 0.2052, 0.863986, 48259e-7, 0, 0.221647, 0.847936, 470848e-8, 0, 0.23867, 0.832253, 456889e-8, 0, 0.25627, 0.818619, 442726e-8, 0, 0.274453, 0.804788, 427677e-8, 0, 0.293222, 0.790241, 411906e-8, 0, 0.312585, 0.775162, 394833e-8, 0, 0.33255, 0.759463, 377366e-8, 0, 0.353126, 0.743598, 361026e-8, 0, 0.374324, 0.72697, 343627e-8, 0, 0.396158, 0.709646, 326422e-8, 0, 0.418641, 0.69277, 309717e-8, 0, 0.44179, 0.675371, 29356e-7, 0, 0.465624, 0.657863, 277712e-8, 0, 0.490163, 0.640772, 261738e-8, 0, 0.515429, 0.624441, 24737e-7, 0, 0.541445, 0.607497, 233125e-8, 0, 0.568236, 0.590438, 218994e-8, 0, 0.595828, 0.573224, 20664e-7, 0, 0.624242, 0.556168, 193526e-8, 0, 0.653496, 0.539232, 182463e-8, 0, 0.683588, 0.522352, 170735e-8, 0, 0.714482, 0.506172, 160555e-8, 0, 0.746032, 0.489842, 150451e-8, 0, 0.777778, 0.473463, 140938e-8, 0, 0.809524, 0.457266, 132568e-8, 0, 0.84127, 0.441609, 12376e-7, 0, 0.873016, 0.426348, 116265e-8, 0, 0.904762, 0.411002, 108935e-8, 0, 0.936508, 0.396045, 101946e-8, 0, 0.968254, 0.381448, 955665e-9, 0, 1, 1, 37121e-7, 0, 0, 1, 371213e-8, 0, 0, 1, 371251e-8, 0, 0, 0.999997, 371417e-8, 0, 0, 0.99999, 371863e-8, 0, 0, 0.999977, 372807e-8, 0, 0, 0.99995, 374529e-8, 0, 0, 0.999908, 37738e-7, 0, 0, 0.999843, 381789e-8, 0, 123596e-10, 0.999745, 388273e-8, 0, 407442e-9, 0.999608, 397443e-8, 0, 15447e-7, 0.999415, 409998e-8, 0, 351385e-8, 0.999143, 426662e-8, 0, 63316e-7, 0.9987, 447625e-8, 0, 998679e-8, 0.996363, 455323e-8, 0, 0.0144569, 0.994021, 461052e-8, 0, 0.0197151, 0.992372, 476359e-8, 0, 0.0257344, 0.991007, 499101e-8, 0, 0.0324882, 0.988767, 51972e-7, 0, 0.0399517, 0.984872, 528407e-8, 0, 0.0481022, 0.982004, 548926e-8, 0, 0.0569191, 0.977714, 564385e-8, 0, 0.0663839, 0.973076, 57693e-7, 0, 0.0764801, 0.967565, 58924e-7, 0, 0.0871928, 0.961384, 599629e-8, 0, 0.0985095, 0.954435, 605998e-8, 0, 0.110419, 0.946303, 61133e-7, 0, 0.122912, 0.937662, 612028e-8, 0, 0.13598, 0.927867, 612209e-8, 0, 0.149617, 0.916475, 604813e-8, 0, 0.163817, 0.90541, 603088e-8, 0, 0.178577, 0.891591, 592218e-8, 0, 0.193894, 0.877573, 578854e-8, 0, 0.209767, 0.862511, 566648e-8, 0, 0.226196, 0.846861, 551481e-8, 0, 0.243182, 0.83068, 533754e-8, 0, 0.260728, 0.815725, 515487e-8, 0, 0.278837, 0.802321, 49655e-7, 0, 0.297515, 0.787826, 475421e-8, 0, 0.316768, 0.773454, 456002e-8, 0, 0.336605, 0.758224, 434727e-8, 0, 0.357034, 0.74265, 414444e-8, 0, 0.378067, 0.726729, 393738e-8, 0, 0.399717, 0.710155, 373575e-8, 0, 0.421998, 0.693312, 353736e-8, 0, 0.444928, 0.67653, 334368e-8, 0, 0.468523, 0.659444, 315981e-8, 0, 0.492806, 0.642051, 297809e-8, 0, 0.517798, 0.625758, 280592e-8, 0, 0.543525, 0.609615, 264254e-8, 0, 0.570012, 0.592919, 248459e-8, 0, 0.597288, 0.576298, 233327e-8, 0, 0.625379, 0.559489, 219519e-8, 0, 0.654307, 0.542891, 205441e-8, 0, 0.684084, 0.526255, 193385e-8, 0, 0.714693, 0.509853, 180745e-8, 0, 0.746044, 0.494131, 169817e-8, 0, 0.777778, 0.478114, 15913e-7, 0, 0.809524, 0.462274, 148981e-8, 0, 0.84127, 0.446412, 139537e-8, 0, 0.873016, 0.431274, 130984e-8, 0, 0.904762, 0.41635, 122403e-8, 0, 0.936508, 0.401476, 114809e-8, 0, 0.968254, 0.386993, 107563e-8, 0, 1, 1, 488216e-8, 0, 0, 1, 48822e-7, 0, 0, 1, 488265e-8, 0, 0, 0.999997, 488463e-8, 0, 0, 0.999988, 488999e-8, 0, 0, 0.999974, 490129e-8, 0, 0, 0.999946, 492191e-8, 0, 0, 0.999897, 495598e-8, 0, 0, 0.999825, 500855e-8, 0, 744791e-10, 0.999718, 508559e-8, 0, 712744e-9, 0.999565, 5194e-6, 0, 215249e-8, 0.999352, 534147e-8, 0, 444576e-8, 0.999046, 553523e-8, 0, 759218e-8, 0.998492, 577016e-8, 0, 0.0115714, 0.995564, 578487e-8, 0, 0.0163557, 0.993339, 586414e-8, 0, 0.021915, 0.991834, 606002e-8, 0, 0.0282201, 0.990496, 633312e-8, 0, 0.0352433, 0.987826, 651941e-8, 0, 0.042959, 0.98383, 660842e-8, 0, 0.0513439, 0.98109, 685523e-8, 0, 0.0603772, 0.976131, 695778e-8, 0, 0.0700402, 0.971922, 714236e-8, 0, 0.0803163, 0.965901, 721437e-8, 0, 0.0911908, 0.959606, 732017e-8, 0, 0.102651, 0.952504, 735788e-8, 0, 0.114686, 0.944365, 738493e-8, 0, 0.127286, 0.935652, 737969e-8, 0, 0.140443, 0.925813, 733612e-8, 0, 0.154151, 0.914397, 723094e-8, 0, 0.168405, 0.903257, 714002e-8, 0, 0.183201, 0.890015, 700149e-8, 0, 0.198536, 0.876014, 682813e-8, 0, 0.214409, 0.861436, 665567e-8, 0, 0.23082, 0.845752, 644526e-8, 0, 0.24777, 0.829169, 621635e-8, 0, 0.265263, 0.813435, 597789e-8, 0, 0.283301, 0.799701, 575694e-8, 0, 0.301889, 0.785726, 549866e-8, 0, 0.321035, 0.77152, 52503e-7, 0, 0.340746, 0.75683, 499619e-8, 0, 0.361032, 0.741951, 47543e-7, 0, 0.381904, 0.726367, 45084e-7, 0, 0.403374, 0.710537, 426784e-8, 0, 0.425457, 0.693965, 403487e-8, 0, 0.448169, 0.677724, 38075e-7, 0, 0.47153, 0.66117, 359431e-8, 0, 0.495561, 0.644274, 338354e-8, 0, 0.520284, 0.627449, 318163e-8, 0, 0.545725, 0.611645, 299672e-8, 0, 0.571911, 0.595614, 281016e-8, 0, 0.598873, 0.579426, 264252e-8, 0, 0.62664, 0.563016, 247509e-8, 0, 0.655239, 0.546728, 232647e-8, 0, 0.684692, 0.530539, 217803e-8, 0, 0.714999, 0.514164, 204216e-8, 0, 0.746106, 0.498344, 191403e-8, 0, 0.777778, 0.482957, 179203e-8, 0, 0.809524, 0.467336, 167695e-8, 0, 0.84127, 0.451994, 157567e-8, 0, 0.873016, 0.436514, 147113e-8, 0, 0.904762, 0.42178, 138034e-8, 0, 0.936508, 0.407271, 129219e-8, 0, 0.968254, 0.392822, 12098e-7, 0, 1, 1, 637427e-8, 0, 0, 1, 637431e-8, 0, 0, 0.999999, 637485e-8, 0, 0, 0.999996, 637721e-8, 0, 0, 0.999987, 638357e-8, 0, 0, 0.999971, 6397e-6, 0, 0, 0.999939, 642142e-8, 0, 0, 0.999888, 646177e-8, 0, 0, 0.999807, 652387e-8, 0, 207916e-9, 0.999689, 661454e-8, 0, 112051e-8, 0.99952, 674155e-8, 0, 287719e-8, 0.999283, 691313e-8, 0, 550145e-8, 0.998936, 713598e-8, 0, 897928e-8, 0.998165, 738501e-8, 0, 0.0132829, 0.994847, 734388e-8, 0, 0.01838, 0.993182, 749991e-8, 0, 0.0242381, 0.991665, 77246e-7, 0, 0.030826, 0.989708, 797579e-8, 0, 0.0381152, 0.986663, 813011e-8, 0, 0.0460794, 0.983288, 830365e-8, 0, 0.0546951, 0.980104, 853496e-8, 0, 0.0639411, 0.974855, 861045e-8, 0, 0.0737988, 0.97045, 879133e-8, 0, 0.0842516, 0.964509, 886377e-8, 0, 0.0952848, 0.957594, 890346e-8, 0, 0.106886, 0.950546, 893289e-8, 0, 0.119044, 0.942225, 890074e-8, 0, 0.131749, 0.933365, 886826e-8, 0, 0.144994, 0.923202, 87316e-7, 0, 0.158772, 0.912605, 863082e-8, 0, 0.173078, 0.901099, 847403e-8, 0, 0.187908, 0.888177, 825838e-8, 0, 0.203261, 0.873955, 801834e-8, 0, 0.219134, 0.860091, 779026e-8, 0, 0.235527, 0.84434, 752478e-8, 0, 0.252443, 0.828517, 724074e-8, 0, 0.269883, 0.81239, 693769e-8, 0, 0.287851, 0.79721, 664817e-8, 0, 0.306352, 0.783489, 634763e-8, 0, 0.325393, 0.769514, 604221e-8, 0, 0.344981, 0.755419, 573568e-8, 0, 0.365126, 0.741083, 544359e-8, 0, 0.385839, 0.726059, 515515e-8, 0, 0.407132, 0.710809, 487139e-8, 0, 0.42902, 0.695052, 459846e-8, 0, 0.45152, 0.678886, 433412e-8, 0, 0.474651, 0.663042, 407981e-8, 0, 0.498433, 0.646634, 384264e-8, 0, 0.52289, 0.630117, 360897e-8, 0, 0.548048, 0.613804, 338863e-8, 0, 0.573936, 0.598338, 318486e-8, 0, 0.600584, 0.582687, 298377e-8, 0, 0.628027, 0.566809, 280082e-8, 0, 0.656295, 0.550817, 262255e-8, 0, 0.685417, 0.534937, 245835e-8, 0, 0.715406, 0.519151, 230574e-8, 0, 0.74624, 0.503118, 21549e-7, 0, 0.777778, 0.487723, 202008e-8, 0, 0.809524, 0.472725, 189355e-8, 0, 0.84127, 0.457599, 177108e-8, 0, 0.873016, 0.442558, 165843e-8, 0, 0.904762, 0.427624, 155494e-8, 0, 0.936508, 0.413171, 145273e-8, 0, 0.968254, 0.399122, 136454e-8, 0, 1, 1, 826496e-8, 0, 0, 1, 826499e-8, 0, 0, 1, 826564e-8, 0, 0, 0.999996, 826842e-8, 0, 0, 0.999987, 827589e-8, 0, 0, 0.999967, 829167e-8, 0, 0, 0.999933, 832037e-8, 0, 0, 0.999876, 836768e-8, 0, 109338e-10, 0.999786, 844031e-8, 0, 427145e-9, 0.999655, 854603e-8, 0, 16384e-7, 0.999468, 869337e-8, 0, 372392e-8, 0.999203, 8891e-6, 0, 668513e-8, 0.998803, 914387e-8, 0, 0.0104968, 0.99748, 935838e-8, 0, 0.015125, 0.994446, 933309e-8, 0, 0.0205338, 0.99292, 953084e-8, 0, 0.0266884, 0.991414, 97893e-7, 0, 0.0335565, 0.989049, 0.0100228, 0, 0.0411086, 0.98582, 0.0101664, 0, 0.0493181, 0.982441, 0.0103582, 0, 0.0581613, 0.978595, 0.0105292, 0, 0.0676169, 0.973495, 0.0106274, 0, 0.0776661, 0.968405, 0.0107261, 0, 0.0882926, 0.962717, 0.0108234, 0, 0.0994817, 0.955478, 0.0108102, 0, 0.111221, 0.948275, 0.0107914, 0, 0.123499, 0.940006, 0.0107161, 0, 0.136308, 0.930831, 0.0106309, 0, 0.149639, 0.920648, 0.0104083, 0, 0.163485, 0.910205, 0.0102312, 0, 0.177843, 0.898445, 0.0100051, 0, 0.192707, 0.885986, 971928e-8, 0, 0.208077, 0.872204, 940747e-8, 0, 0.22395, 0.858436, 91085e-7, 0, 0.240326, 0.843454, 876595e-8, 0, 0.257208, 0.827437, 839794e-8, 0, 0.274596, 0.811488, 803692e-8, 0, 0.292496, 0.796039, 767352e-8, 0, 0.310911, 0.781083, 73097e-7, 0, 0.329849, 0.767642, 694032e-8, 0, 0.349316, 0.753901, 657476e-8, 0, 0.369323, 0.740131, 622699e-8, 0, 0.38988, 0.725845, 58838e-7, 0, 0.410999, 0.710991, 555586e-8, 0, 0.432696, 0.696002, 523089e-8, 0, 0.454987, 0.680461, 492494e-8, 0, 0.47789, 0.664875, 463464e-8, 0, 0.501426, 0.649273, 435422e-8, 0, 0.52562, 0.63302, 40875e-7, 0, 0.550498, 0.61705, 384075e-8, 0, 0.576089, 0.601154, 359557e-8, 0, 0.602427, 0.586008, 337636e-8, 0, 0.629544, 0.570699, 316019e-8, 0, 0.657479, 0.555166, 296033e-8, 0, 0.686264, 0.539645, 277552e-8, 0, 0.715924, 0.524159, 259499e-8, 0, 0.746459, 0.508682, 243257e-8, 0, 0.777789, 0.493163, 227851e-8, 0, 0.809524, 0.478004, 213083e-8, 0, 0.84127, 0.46347, 199502e-8, 0, 0.873016, 0.448778, 186967e-8, 0, 0.904762, 0.434105, 174732e-8, 0, 0.936508, 0.419576, 163861e-8, 0, 0.968254, 0.405541, 153341e-8, 0, 1, 1, 0.0106462, 0, 0, 1, 0.0106462, 0, 0, 0.999999, 0.010647, 0, 0, 0.999995, 0.0106502, 0, 0, 0.999985, 0.0106589, 0, 0, 0.999964, 0.0106773, 0, 0, 0.999925, 0.0107106, 0, 0, 0.999861, 0.0107655, 0, 712986e-10, 0.999763, 0.0108497, 0, 743959e-9, 0.999616, 0.0109716, 0, 227361e-8, 0.999408, 0.0111408, 0, 46983e-7, 0.999112, 0.0113659, 0, 800158e-8, 0.998637, 0.0116475, 0, 0.0121493, 0.996223, 0.0117231, 0, 0.0171023, 0.994006, 0.0118064, 0, 0.0228218, 0.992444, 0.0120254, 0, 0.0292711, 0.991028, 0.0123314, 0, 0.036417, 0.98803, 0.0124954, 0, 0.0442295, 0.984816, 0.0126538, 0, 0.0526815, 0.981399, 0.0128537, 0, 0.0617492, 0.977085, 0.0129694, 0, 0.0714114, 0.972154, 0.013091, 0, 0.0816495, 0.966617, 0.0131166, 0, 0.0924472, 0.960628, 0.0131583, 0, 0.10379, 0.953295, 0.0131094, 0, 0.115665, 0.94575, 0.0129966, 0, 0.128062, 0.937654, 0.0128796, 0, 0.140972, 0.927716, 0.0126477, 0, 0.154387, 0.917932, 0.0123889, 0, 0.168301, 0.907719, 0.012131, 0, 0.182709, 0.89584, 0.0118013, 0, 0.197608, 0.883526, 0.0114145, 0, 0.212994, 0.870301, 0.0110075, 0, 0.228867, 0.856272, 0.0106019, 0, 0.245227, 0.842251, 0.0101938, 0, 0.262074, 0.826466, 973254e-8, 0, 0.279412, 0.810859, 92846e-7, 0, 0.297244, 0.795051, 883304e-8, 0, 0.315575, 0.780053, 840272e-8, 0, 0.334412, 0.76575, 796438e-8, 0, 0.35376, 0.752298, 752526e-8, 0, 0.373631, 0.739153, 711486e-8, 0, 0.394034, 0.725514, 670361e-8, 0, 0.414983, 0.711473, 632656e-8, 0, 0.436491, 0.696936, 595206e-8, 0, 0.458575, 0.682126, 559191e-8, 0, 0.481253, 0.667027, 525362e-8, 0, 0.504547, 0.651875, 493805e-8, 0, 0.528481, 0.636463, 462848e-8, 0, 0.553081, 0.620641, 433936e-8, 0, 0.578377, 0.604931, 407e-5, 0, 0.604404, 0.589549, 380864e-8, 0, 0.631197, 0.574712, 357049e-8, 0, 0.658795, 0.559775, 334466e-8, 0, 0.687238, 0.544514, 312505e-8, 0, 0.716559, 0.529555, 293199e-8, 0, 0.746776, 0.514402, 274204e-8, 0, 0.777849, 0.499302, 256647e-8, 0, 0.809524, 0.484114, 239901e-8, 0, 0.84127, 0.469308, 225148e-8, 0, 0.873016, 0.455133, 210178e-8, 0, 0.904762, 0.440939, 19727e-7, 0, 0.936508, 0.426627, 184382e-8, 0, 0.968254, 0.412509, 172548e-8, 0, 1, 1, 0.013628, 0, 0, 1, 0.0136281, 0, 0, 0.999999, 0.0136289, 0, 0, 0.999995, 0.0136327, 0, 0, 0.999983, 0.0136427, 0, 0, 0.99996, 0.0136638, 0, 0, 0.999917, 0.0137022, 0, 0, 0.999846, 0.0137652, 0, 204597e-9, 0.999736, 0.0138615, 0, 116837e-8, 0.999573, 0.0140007, 0, 303325e-8, 0.99934, 0.0141927, 0, 580613e-8, 0.999004, 0.0144457, 0, 945626e-8, 0.998407, 0.0147489, 0, 0.0139421, 0.995464, 0.014731, 0, 0.0192202, 0.993328, 0.0148283, 0, 0.0252495, 0.991799, 0.0150797, 0, 0.0319921, 0.990397, 0.0154316, 0, 0.0394138, 0.986835, 0.0155005, 0, 0.0474843, 0.983938, 0.0157308, 0, 0.0561763, 0.980154, 0.0158753, 0, 0.0654661, 0.975659, 0.0159581, 0, 0.0753326, 0.970171, 0.0159832, 0, 0.0857571, 0.964803, 0.0160084, 0, 0.0967236, 0.958366, 0.0159484, 0, 0.108218, 0.950613, 0.0158001, 0, 0.120227, 0.942874, 0.0155845, 0, 0.132741, 0.935005, 0.0154292, 0, 0.145751, 0.924991, 0.0150742, 0, 0.159249, 0.914814, 0.0146757, 0, 0.17323, 0.904743, 0.0143097, 0, 0.187687, 0.893216, 0.0138695, 0, 0.202619, 0.880769, 0.0133706, 0, 0.218021, 0.868136, 0.0128606, 0, 0.233894, 0.85469, 0.0123403, 0, 0.250238, 0.840593, 0.0118091, 0, 0.267052, 0.825808, 0.011253, 0, 0.284341, 0.81009, 0.0107099, 0, 0.302106, 0.79504, 0.0101636, 0, 0.320354, 0.779757, 964041e-8, 0, 0.33909, 0.764697, 911896e-8, 0, 0.358322, 0.750913, 859533e-8, 0, 0.378059, 0.738175, 811592e-8, 0, 0.398311, 0.725242, 764504e-8, 0, 0.41909, 0.711864, 718885e-8, 0, 0.440412, 0.698009, 675843e-8, 0, 0.462292, 0.683841, 634984e-8, 0, 0.484748, 0.669391, 595502e-8, 0, 0.507802, 0.654731, 558671e-8, 0, 0.531477, 0.639805, 523578e-8, 0, 0.555802, 0.624789, 490834e-8, 0, 0.580805, 0.609325, 459448e-8, 0, 0.606522, 0.593975, 430342e-8, 0, 0.63299, 0.578983, 403019e-8, 0, 0.66025, 0.564442, 37707e-7, 0, 0.688346, 0.549835, 35316e-7, 0, 0.717319, 0.535039, 330255e-8, 0, 0.7472, 0.520403, 308932e-8, 0, 0.777982, 0.505687, 289335e-8, 0, 0.809524, 0.490939, 270818e-8, 0, 0.84127, 0.476233, 25343e-7, 0, 0.873016, 0.461624, 237097e-8, 0, 0.904762, 0.447833, 222065e-8, 0, 0.936508, 0.433992, 207561e-8, 0, 0.968254, 0.420147, 194955e-8, 0, 1, 1, 0.0173415, 0, 0, 1, 0.0173416, 0, 0, 0.999999, 0.0173426, 0, 0, 0.999995, 0.0173468, 0, 0, 0.999983, 0.0173582, 0, 0, 0.999954, 0.0173822, 0, 0, 0.999908, 0.0174258, 0, 669501e-11, 0.999828, 0.0174973, 0, 427399e-9, 0.999705, 0.0176063, 0, 171019e-8, 0.999524, 0.0177631, 0, 39248e-7, 0.999263, 0.0179781, 0, 705382e-8, 0.998878, 0.018258, 0, 0.0110552, 0.998012, 0.0185551, 0, 0.0158812, 0.994614, 0.0184264, 0, 0.0214852, 0.993132, 0.0186385, 0, 0.0278239, 0.991563, 0.0189067, 0, 0.0348585, 0.989298, 0.0191577, 0, 0.0425544, 0.986036, 0.0192522, 0, 0.050881, 0.982558, 0.0194063, 0, 0.059811, 0.978531, 0.019486, 0, 0.0693209, 0.974198, 0.0195847, 0, 0.0793895, 0.968148, 0.0194749, 0, 0.0899984, 0.962565, 0.0194277, 0, 0.101132, 0.956041, 0.0192991, 0, 0.112775, 0.947749, 0.0189893, 0, 0.124917, 0.94018, 0.018704, 0, 0.137547, 0.93165, 0.0183458, 0, 0.150655, 0.921798, 0.0178775, 0, 0.164236, 0.911573, 0.0173618, 0, 0.178281, 0.901569, 0.0168482, 0, 0.192788, 0.890341, 0.016265, 0, 0.207752, 0.877835, 0.0156199, 0, 0.223171, 0.865472, 0.0149516, 0, 0.239044, 0.852905, 0.0143274, 0, 0.255371, 0.838906, 0.0136643, 0, 0.272153, 0.824888, 0.0129903, 0, 0.289393, 0.809977, 0.0123218, 0, 0.307093, 0.794697, 0.0116572, 0, 0.325259, 0.780028, 0.0110307, 0, 0.343896, 0.765124, 0.0104236, 0, 0.363012, 0.750411, 98219e-7, 0, 0.382617, 0.737264, 924397e-8, 0, 0.402719, 0.724799, 868719e-8, 0, 0.423332, 0.712253, 816476e-8, 0, 0.444469, 0.699267, 767262e-8, 0, 0.466146, 0.685618, 719746e-8, 0, 0.488383, 0.671736, 673916e-8, 0, 0.511199, 0.657777, 631937e-8, 0, 0.534618, 0.643497, 592411e-8, 0, 0.558668, 0.62889, 553928e-8, 0, 0.58338, 0.614299, 51934e-7, 0, 0.608787, 0.599197, 485985e-8, 0, 0.634929, 0.584175, 454357e-8, 0, 0.661849, 0.569541, 425787e-8, 0, 0.689594, 0.555193, 397905e-8, 0, 0.718211, 0.540947, 372364e-8, 0, 0.747742, 0.526593, 348599e-8, 0, 0.778205, 0.512335, 326103e-8, 0, 0.80953, 0.498017, 305137e-8, 0, 0.84127, 0.483609, 285485e-8, 0, 0.873016, 0.469368, 267472e-8, 0, 0.904762, 0.455037, 249945e-8, 0, 0.936508, 0.441493, 234792e-8, 0, 0.968254, 0.428147, 219936e-8, 0, 1, 1, 0.0219422, 0, 0, 1, 0.0219423, 0, 0, 0.999998, 0.0219434, 0, 0, 0.999993, 0.0219481, 0, 0, 0.999981, 0.021961, 0, 0, 0.999949, 0.0219879, 0, 0, 0.999896, 0.0220367, 0, 593194e-10, 0.999808, 0.0221167, 0, 75364e-8, 0.99967, 0.0222383, 0, 237884e-8, 0.999466, 0.0224125, 0, 495612e-8, 0.999174, 0.0226495, 0, 844887e-8, 0.998725, 0.0229525, 0, 0.0128058, 0.996979, 0.0231123, 0, 0.0179742, 0.994317, 0.0230742, 0, 0.0239047, 0.992781, 0.0232895, 0, 0.0305526, 0.991191, 0.0235734, 0, 0.0378786, 0.987787, 0.0236152, 0, 0.0458475, 0.985092, 0.0237994, 0, 0.0544287, 0.981121, 0.0238553, 0, 0.0635952, 0.976924, 0.0238706, 0, 0.0733233, 0.97218, 0.0238704, 0, 0.0835922, 0.965956, 0.0236598, 0, 0.0943839, 0.959998, 0.0234735, 0, 0.105682, 0.953245, 0.0232277, 0, 0.117474, 0.944445, 0.0226973, 0, 0.129747, 0.937087, 0.0223527, 0, 0.142491, 0.928341, 0.0218144, 0, 0.155697, 0.9184, 0.0211516, 0, 0.169358, 0.907959, 0.0204553, 0, 0.183469, 0.89808, 0.0197673, 0, 0.198024, 0.887047, 0.0189915, 0, 0.21302, 0.875221, 0.0182082, 0, 0.228455, 0.86269, 0.0173584, 0, 0.244329, 0.850735, 0.0165718, 0, 0.260639, 0.837545, 0.0157524, 0, 0.277389, 0.823639, 0.0149482, 0, 0.29458, 0.809699, 0.0141431, 0, 0.312216, 0.794797, 0.0133527, 0, 0.3303, 0.780578, 0.0126193, 0, 0.34884, 0.766019, 0.0118914, 0, 0.367842, 0.751447, 0.0111839, 0, 0.387315, 0.737275, 0.010514, 0, 0.40727, 0.724545, 987277e-8, 0, 0.427717, 0.712644, 926569e-8, 0, 0.448671, 0.700432, 869029e-8, 0, 0.470149, 0.687664, 814691e-8, 0, 0.492167, 0.674288, 763012e-8, 0, 0.514746, 0.660966, 714437e-8, 0, 0.537911, 0.647264, 668457e-8, 0, 0.561688, 0.633431, 626581e-8, 0, 0.586108, 0.619133, 585593e-8, 0, 0.611206, 0.604935, 548188e-8, 0, 0.637022, 0.590236, 513288e-8, 0, 0.663599, 0.575473, 47906e-7, 0, 0.690989, 0.561228, 448895e-8, 0, 0.719242, 0.547054, 420233e-8, 0, 0.748411, 0.533175, 392869e-8, 0, 0.778531, 0.519163, 367445e-8, 0, 0.809583, 0.505328, 344097e-8, 0, 0.84127, 0.491446, 322003e-8, 0, 0.873016, 0.477356, 301283e-8, 0, 0.904762, 0.46356, 282592e-8, 0, 0.936508, 0.449623, 264956e-8, 0, 0.968254, 0.436068, 246956e-8, 0, 1, 1, 0.0276135, 0, 0, 1, 0.0276136, 0, 0, 0.999998, 0.0276148, 0, 0, 0.999993, 0.0276201, 0, 0, 0.999976, 0.0276342, 0, 0, 0.999945, 0.027664, 0, 0, 0.999884, 0.0277179, 0, 18679e-8, 0.999784, 0.027806, 0, 119607e-8, 0.99963, 0.0279394, 0, 318407e-8, 0.999401, 0.0281295, 0, 613601e-8, 0.999066, 0.0283858, 0, 999963e-8, 0.998524, 0.0287027, 0, 0.0147164, 0.995702, 0.0286256, 0, 0.0202295, 0.993593, 0.0286733, 0, 0.0264876, 0.992067, 0.0288989, 0, 0.0334452, 0.990548, 0.0292135, 0, 0.0410621, 0.986775, 0.0291296, 0, 0.0493032, 0.984054, 0.0293099, 0, 0.0581381, 0.979481, 0.0291881, 0, 0.0675397, 0.975297, 0.0291598, 0, 0.0774848, 0.96981, 0.028954, 0, 0.0879528, 0.963524, 0.028628, 0, 0.0989258, 0.957398, 0.0283135, 0, 0.110388, 0.950088, 0.0278469, 0, 0.122327, 0.941538, 0.0271798, 0, 0.134729, 0.933332, 0.0265388, 0, 0.147587, 0.924392, 0.0257776, 0, 0.160889, 0.914581, 0.024916, 0, 0.174631, 0.904347, 0.0240242, 0, 0.188806, 0.894324, 0.0231229, 0, 0.203409, 0.883724, 0.022153, 0, 0.218437, 0.872207, 0.0211355, 0, 0.233888, 0.859927, 0.0201048, 0, 0.249761, 0.848373, 0.0191263, 0, 0.266056, 0.836023, 0.0181306, 0, 0.282774, 0.82289, 0.0171718, 0, 0.299917, 0.809324, 0.0162196, 0, 0.317488, 0.795361, 0.0152622, 0, 0.335493, 0.781253, 0.01439, 0, 0.353936, 0.767338, 0.013533, 0, 0.372825, 0.753156, 0.0127244, 0, 0.392168, 0.739122, 0.0119454, 0, 0.411976, 0.725358, 0.0112054, 0, 0.432259, 0.712949, 0.010487, 0, 0.453032, 0.701621, 984032e-8, 0, 0.47431, 0.689703, 921495e-8, 0, 0.496111, 0.677216, 862492e-8, 0, 0.518456, 0.664217, 806882e-8, 0, 0.541367, 0.65137, 755922e-8, 0, 0.564872, 0.638, 705705e-8, 0, 0.589001, 0.62453, 661266e-8, 0, 0.613789, 0.610601, 618432e-8, 0, 0.639277, 0.59676, 578033e-8, 0, 0.66551, 0.582433, 540927e-8, 0, 0.692539, 0.568026, 506104e-8, 0, 0.720422, 0.55414, 47353e-7, 0, 0.749216, 0.540178, 442889e-8, 0, 0.778974, 0.526513, 414363e-8, 0, 0.809711, 0.512954, 388237e-8, 0, 0.84127, 0.499403, 362875e-8, 0, 0.873016, 0.486026, 340827e-8, 0, 0.904762, 0.472345, 318598e-8, 0, 0.936508, 0.458828, 297635e-8, 0, 0.968254, 0.445379, 279447e-8, 0, 1, 1, 0.0345716, 0, 0, 1, 0.0345717, 0, 0, 0.999999, 0.034573, 0, 0, 0.999991, 0.0345787, 0, 0, 0.999974, 0.0345941, 0, 0, 0.999937, 0.0346263, 0, 188589e-11, 0.999869, 0.0346847, 0, 409238e-9, 0.999757, 0.0347798, 0, 17674e-7, 0.999582, 0.0349233, 0, 413658e-8, 0.999322, 0.0351265, 0, 747408e-8, 0.998939, 0.0353967, 0, 0.0117157, 0.998219, 0.0357018, 0, 0.0167966, 0.994974, 0.0354726, 0, 0.0226572, 0.993201, 0.0355621, 0, 0.0292445, 0.991573, 0.0357641, 0, 0.0365123, 0.989301, 0.0359252, 0, 0.0444203, 0.985712, 0.0358017, 0, 0.0529334, 0.982411, 0.0358353, 0, 0.0620214, 0.977827, 0.035617, 0, 0.0716574, 0.973278, 0.0354398, 0, 0.0818186, 0.967397, 0.0350483, 0, 0.0924846, 0.960696, 0.0344795, 0, 0.103638, 0.954349, 0.0339861, 0, 0.115263, 0.946066, 0.0331323, 0, 0.127348, 0.938012, 0.032359, 0, 0.13988, 0.929413, 0.0314413, 0, 0.152849, 0.920355, 0.0304103, 0, 0.166248, 0.910586, 0.0292785, 0, 0.18007, 0.900609, 0.0281391, 0, 0.194308, 0.890093, 0.0269103, 0, 0.208958, 0.880013, 0.0257269, 0, 0.224018, 0.869001, 0.0244671, 0, 0.239485, 0.85751, 0.0232252, 0, 0.255359, 0.84582, 0.0220117, 0, 0.271638, 0.834383, 0.0208274, 0, 0.288324, 0.822158, 0.0196628, 0, 0.305419, 0.809056, 0.0185306, 0, 0.322927, 0.795832, 0.0174174, 0, 0.340851, 0.782547, 0.0163758, 0, 0.359199, 0.7689, 0.015391, 0, 0.377975, 0.755526, 0.0144488, 0, 0.397189, 0.741681, 0.0135372, 0, 0.416851, 0.728178, 0.0126957, 0, 0.436971, 0.714642, 0.0118812, 0, 0.457564, 0.702756, 0.0111165, 0, 0.478644, 0.69175, 0.0104145, 0, 0.500229, 0.680159, 974439e-8, 0, 0.522339, 0.668073, 911926e-8, 0, 0.544997, 0.655405, 851393e-8, 0, 0.56823, 0.642921, 797637e-8, 0, 0.592068, 0.629993, 745119e-8, 0, 0.616546, 0.616828, 696972e-8, 0, 0.641705, 0.603305, 652425e-8, 0, 0.66759, 0.589833, 610188e-8, 0, 0.694255, 0.575945, 570834e-8, 0, 0.72176, 0.561745, 533384e-8, 0, 0.750168, 0.548277, 500001e-8, 0, 0.779545, 0.534467, 467582e-8, 0, 0.809933, 0.521032, 438092e-8, 0, 0.841272, 0.507877, 410348e-8, 0, 0.873016, 0.494654, 383618e-8, 0, 0.904762, 0.481592, 358699e-8, 0, 0.936508, 0.468509, 337281e-8, 0, 0.968254, 0.455293, 316196e-8, 0, 1, 1, 0.0430698, 0, 0, 1, 0.0430699, 0, 0, 0.999998, 0.0430713, 0, 0, 0.999991, 0.0430773, 0, 0, 0.99997, 0.0430936, 0, 0, 0.999928, 0.0431277, 0, 406396e-10, 0.999852, 0.0431893, 0, 744376e-9, 0.999724, 0.0432895, 0, 24806e-7, 0.999527, 0.0434397, 0, 524779e-8, 0.99923, 0.0436507, 0, 898164e-8, 0.998783, 0.0439255, 0, 0.0136083, 0.997507, 0.0441104, 0, 0.0190582, 0.994418, 0.0438225, 0, 0.0252694, 0.992864, 0.0439396, 0, 0.0321879, 0.991127, 0.0440962, 0, 0.039767, 0.987331, 0.0438408, 0, 0.0479667, 0.984819, 0.0438991, 0, 0.056752, 0.980384, 0.0435906, 0, 0.0660929, 0.975846, 0.0432543, 0, 0.075963, 0.970748, 0.0428293, 0, 0.0863398, 0.964303, 0.042153, 0, 0.0972035, 0.95772, 0.0414111, 0, 0.108537, 0.950747, 0.0405893, 0, 0.120325, 0.942533, 0.0394887, 0, 0.132554, 0.934045, 0.0383544, 0, 0.145215, 0.924942, 0.037057, 0, 0.158296, 0.915811, 0.0356993, 0, 0.17179, 0.90612, 0.0342401, 0, 0.185691, 0.896434, 0.0328078, 0, 0.199993, 0.886021, 0.031288, 0, 0.214691, 0.876081, 0.0297776, 0, 0.229782, 0.865608, 0.0282334, 0, 0.245265, 0.854924, 0.026749, 0, 0.261138, 0.843607, 0.02526, 0, 0.277401, 0.832456, 0.0238214, 0, 0.294056, 0.821342, 0.0224682, 0, 0.311104, 0.809303, 0.0211297, 0, 0.328548, 0.796468, 0.0198387, 0, 0.346394, 0.784046, 0.0186227, 0, 0.364645, 0.771262, 0.0174561, 0, 0.38331, 0.758118, 0.0163806, 0, 0.402396, 0.745075, 0.0153287, 0, 0.421912, 0.731926, 0.0143647, 0, 0.44187, 0.71863, 0.0134363, 0, 0.462283, 0.705414, 0.0125603, 0, 0.483165, 0.693792, 0.0117508, 0, 0.504535, 0.683108, 0.0110016, 0, 0.52641, 0.67183, 0.0102757, 0, 0.548816, 0.66015, 962044e-8, 0, 0.571776, 0.647907, 898031e-8, 0, 0.595323, 0.635734, 840811e-8, 0, 0.619489, 0.623208, 786211e-8, 0, 0.644317, 0.610438, 734953e-8, 0, 0.669852, 0.597345, 687688e-8, 0, 0.696148, 0.584138, 643469e-8, 0, 0.723267, 0.5707, 602236e-8, 0, 0.75128, 0.556966, 56324e-7, 0, 0.780258, 0.543607, 528277e-8, 0, 0.810268, 0.530213, 493999e-8, 0, 0.841311, 0.516912, 462265e-8, 0, 0.873016, 0.503916, 43307e-7, 0, 0.904762, 0.491146, 406858e-8, 0, 0.936508, 0.478439, 381436e-8, 0, 0.968254, 0.465834, 358003e-8, 0, 1, 1, 0.0534039, 0, 0, 1, 0.053404, 0, 0, 0.999998, 0.0534055, 0, 0, 0.999989, 0.0534116, 0, 0, 0.999968, 0.0534283, 0, 0, 0.999918, 0.0534633, 0, 155895e-9, 0.99983, 0.0535262, 0, 120914e-8, 0.999685, 0.0536281, 0, 334944e-8, 0.999461, 0.0537799, 0, 653077e-8, 0.999119, 0.0539902, 0, 0.0106718, 0.998582, 0.0542524, 0, 0.0156907, 0.995919, 0.0540318, 0, 0.0215147, 0.993735, 0.0538914, 0, 0.0280801, 0.992126, 0.0539557, 0, 0.0353323, 0.990266, 0.0540401, 0, 0.0432247, 0.986317, 0.0536064, 0, 0.0517172, 0.983213, 0.0534425, 0, 0.0607754, 0.978303, 0.0528622, 0, 0.0703698, 0.973665, 0.0523363, 0, 0.0804742, 0.968091, 0.0516165, 0, 0.0910667, 0.961026, 0.0505434, 0, 0.102128, 0.954333, 0.049523, 0, 0.113641, 0.946372, 0.0481698, 0, 0.125591, 0.938254, 0.0467674, 0, 0.137965, 0.929516, 0.0452341, 0, 0.150754, 0.920106, 0.0435083, 0, 0.163947, 0.910899, 0.0417399, 0, 0.177537, 0.901532, 0.0399389, 0, 0.191516, 0.891919, 0.0380901, 0, 0.205881, 0.882006, 0.0362341, 0, 0.220626, 0.871965, 0.0343444, 0, 0.235749, 0.862145, 0.0324832, 0, 0.251248, 0.852058, 0.0306681, 0, 0.267121, 0.84161, 0.0289097, 0, 0.283368, 0.830806, 0.0272079, 0, 0.299992, 0.820476, 0.0256089, 0, 0.316992, 0.809514, 0.0240394, 0, 0.334374, 0.797865, 0.0225379, 0, 0.35214, 0.785621, 0.0211235, 0, 0.370296, 0.773765, 0.0197908, 0, 0.388849, 0.761629, 0.0185235, 0, 0.407807, 0.748891, 0.0173358, 0, 0.427178, 0.736437, 0.0162305, 0, 0.446974, 0.723707, 0.0151778, 0, 0.467207, 0.710606, 0.0141791, 0, 0.487892, 0.698019, 0.0132592, 0, 0.509046, 0.686203, 0.0123887, 0, 0.530687, 0.675692, 0.0115976, 0, 0.552839, 0.664826, 0.0108325, 0, 0.575527, 0.65349, 0.0101348, 0, 0.59878, 0.641774, 947756e-8, 0, 0.622634, 0.629794, 886058e-8, 0, 0.647128, 0.617647, 828526e-8, 0, 0.672308, 0.60534, 775312e-8, 0, 0.698231, 0.592718, 726033e-8, 0, 0.724958, 0.579746, 679731e-8, 0, 0.752563, 0.566763, 636111e-8, 0, 0.781127, 0.553515, 595228e-8, 0, 0.810733, 0.540118, 556876e-8, 0, 0.841426, 0.527325, 523051e-8, 0, 0.873016, 0.514265, 490712e-8, 0, 0.904762, 0.501406, 460297e-8, 0, 0.936508, 0.488922, 431247e-8, 0, 0.968254, 0.476541, 40472e-7, 0, 1, 1, 0.0659184, 0, 0, 1, 0.0659185, 0, 0, 0.999998, 0.06592, 0, 0, 0.999988, 0.0659259, 0, 0, 0.999963, 0.0659423, 0, 0, 0.999907, 0.0659764, 0, 374198e-9, 0.999806, 0.0660376, 0, 182071e-8, 0.999639, 0.0661361, 0, 43894e-7, 0.999378, 0.0662814, 0, 800055e-8, 0.998985, 0.0664779, 0, 0.0125594, 0.998285, 0.0666914, 0, 0.0179786, 0.995071, 0.0661989, 0, 0.0241822, 0.993172, 0.0660454, 0, 0.031106, 0.991438, 0.0660105, 0, 0.0386952, 0.988428, 0.0656875, 0, 0.0469032, 0.985218, 0.0652913, 0, 0.0556905, 0.981128, 0.0647107, 0, 0.065023, 0.976015, 0.0638491, 0, 0.0748717, 0.97097, 0.062993, 0, 0.0852112, 0.964582, 0.0617927, 0, 0.0960199, 0.957383, 0.0603626, 0, 0.107279, 0.949969, 0.0588128, 0, 0.118971, 0.941843, 0.0570274, 0, 0.131084, 0.933624, 0.0551885, 0, 0.143604, 0.924543, 0.053122, 0, 0.156521, 0.914919, 0.0508897, 0, 0.169825, 0.905773, 0.0486418, 0, 0.18351, 0.896434, 0.0463364, 0, 0.197569, 0.887195, 0.0440623, 0, 0.211997, 0.877706, 0.0417799, 0, 0.226789, 0.867719, 0.03945, 0, 0.241944, 0.858587, 0.037243, 0, 0.257458, 0.849317, 0.0350956, 0, 0.273331, 0.839585, 0.0329852, 0, 0.289563, 0.829856, 0.0310028, 0, 0.306154, 0.819589, 0.0290953, 0, 0.323108, 0.809714, 0.0272738, 0, 0.340426, 0.79934, 0.0255631, 0, 0.358113, 0.788224, 0.0239175, 0, 0.376175, 0.776619, 0.0223831, 0, 0.394616, 0.76521, 0.0209298, 0, 0.413445, 0.753716, 0.0195786, 0, 0.432671, 0.741564, 0.0183001, 0, 0.452305, 0.729413, 0.0171259, 0, 0.472358, 0.717146, 0.0159933, 0, 0.492845, 0.70436, 0.0149495, 0, 0.513783, 0.69219, 0.0139681, 0, 0.535189, 0.680289, 0.0130577, 0, 0.557087, 0.669611, 0.0122198, 0, 0.5795, 0.659113, 0.0114174, 0, 0.602459, 0.648148, 0.0106729, 0, 0.625997, 0.636905, 998997e-8, 0, 0.650154, 0.625154, 934313e-8, 0, 0.674976, 0.613481, 874839e-8, 0, 0.700518, 0.60154, 818265e-8, 0, 0.726845, 0.58943, 766889e-8, 0, 0.754032, 0.576828, 717153e-8, 0, 0.782167, 0.564194, 672696e-8, 0, 0.811344, 0.551501, 630863e-8, 0, 0.841644, 0.538635, 592177e-8, 0, 0.873016, 0.525724, 554888e-8, 0, 0.904762, 0.513209, 520225e-8, 0, 0.936508, 0.500457, 488231e-8, 0, 0.968254, 0.48799, 457153e-8, 0, 1, 1, 0.0810131, 0, 0, 1, 0.0810133, 0, 0, 0.999997, 0.0810145, 0, 0, 0.999985, 0.08102, 0, 0, 0.999956, 0.0810347, 0, 195026e-10, 0.999893, 0.0810656, 0, 719316e-9, 0.999777, 0.0811205, 0, 259774e-8, 0.999583, 0.081208, 0, 561807e-8, 0.999281, 0.0813343, 0, 967472e-8, 0.998813, 0.0814969, 0, 0.0146627, 0.997597, 0.0815217, 0, 0.0204902, 0.994379, 0.0808502, 0, 0.0270802, 0.992744, 0.0806792, 0, 0.0343674, 0.990745, 0.0804589, 0, 0.0422974, 0.986646, 0.0796107, 0, 0.0508242, 0.983611, 0.0790913, 0, 0.0599087, 0.978869, 0.0780746, 0, 0.0695175, 0.973475, 0.0768218, 0, 0.0796223, 0.967845, 0.0754926, 0, 0.0901983, 0.960778, 0.0737063, 0, 0.101224, 0.953333, 0.0718052, 0, 0.112682, 0.945274, 0.0695946, 0, 0.124555, 0.936955, 0.0672492, 0, 0.136831, 0.928319, 0.0647732, 0, 0.149496, 0.919075, 0.0620947, 0, 0.162542, 0.909114, 0.0591816, 0, 0.175958, 0.900137, 0.0563917, 0, 0.189739, 0.891069, 0.0535392, 0, 0.203877, 0.882262, 0.0507642, 0, 0.218368, 0.873232, 0.0479793, 0, 0.233208, 0.864042, 0.045226, 0, 0.248393, 0.855002, 0.0425413, 0, 0.263923, 0.846569, 0.0400126, 0, 0.279796, 0.837714, 0.0375269, 0, 0.296012, 0.828918, 0.0352027, 0, 0.312573, 0.819783, 0.0330011, 0, 0.329479, 0.810129, 0.0308908, 0, 0.346734, 0.800866, 0.0289112, 0, 0.364342, 0.79093, 0.0270255, 0, 0.382307, 0.780593, 0.0252758, 0, 0.400637, 0.769511, 0.0236178, 0, 0.419337, 0.758558, 0.0220652, 0, 0.438418, 0.747632, 0.0206289, 0, 0.457889, 0.736146, 0.0192873, 0, 0.477761, 0.724093, 0.0180333, 0, 0.49805, 0.71234, 0.0168264, 0, 0.51877, 0.700201, 0.015746, 0, 0.53994, 0.687949, 0.0147027, 0, 0.561581, 0.676163, 0.0137512, 0, 0.583718, 0.665001, 0.0128655, 0, 0.60638, 0.65472, 0.0120366, 0, 0.629599, 0.644213, 0.0112604, 0, 0.653415, 0.633382, 0.0105413, 0, 0.677874, 0.62212, 986498e-8, 0, 0.70303, 0.610631, 923308e-8, 0, 0.728948, 0.599078, 864206e-8, 0, 0.755706, 0.587519, 811784e-8, 0, 0.783396, 0.575505, 761237e-8, 0, 0.812121, 0.563148, 713949e-8, 0, 0.841989, 0.550828, 668379e-8, 0, 0.873035, 0.538458, 627715e-8, 0, 0.904762, 0.525905, 588336e-8, 0, 0.936508, 0.513517, 552687e-8, 0, 0.968254, 0.501395, 519681e-8, 0, 1, 1, 0.0991506, 0, 0, 1, 0.0991504, 0, 0, 0.999996, 0.0991515, 0, 0, 0.999984, 0.0991558, 0, 0, 0.999947, 0.0991672, 0, 114389e-9, 0.999874, 0.0991912, 0, 121503e-8, 0.999739, 0.0992331, 0, 356108e-8, 0.999514, 0.0992983, 0, 705578e-8, 0.999159, 0.0993877, 0, 0.011574, 0.998586, 0.0994837, 0, 0.017003, 0.995731, 0.0988425, 0, 0.0232484, 0.993384, 0.098276, 0, 0.0302318, 0.991615, 0.0979269, 0, 0.0378884, 0.989029, 0.0973432, 0, 0.0461641, 0.985373, 0.0963539, 0, 0.0550136, 0.981278, 0.0952306, 0, 0.0643988, 0.975777, 0.0936233, 0, 0.0742868, 0.970526, 0.0920219, 0, 0.0846501, 0.963755, 0.0898912, 0, 0.0954644, 0.956676, 0.0876064, 0, 0.106709, 0.948099, 0.0847751, 0, 0.118367, 0.939718, 0.0818638, 0, 0.130423, 0.931305, 0.078857, 0, 0.142862, 0.922342, 0.0756127, 0, 0.155674, 0.912842, 0.0721473, 0, 0.168849, 0.903304, 0.0686195, 0, 0.182378, 0.89411, 0.0650589, 0, 0.196255, 0.885512, 0.0616022, 0, 0.210473, 0.877193, 0.0582434, 0, 0.225027, 0.86877, 0.0548979, 0, 0.239915, 0.860267, 0.0516095, 0, 0.255132, 0.851915, 0.048468, 0, 0.270678, 0.843912, 0.0454447, 0, 0.286551, 0.83604, 0.0425612, 0, 0.302751, 0.828245, 0.0398752, 0, 0.31928, 0.820159, 0.0373198, 0, 0.336138, 0.81167, 0.034916, 0, 0.35333, 0.802659, 0.0326402, 0, 0.370858, 0.793921, 0.0304901, 0, 0.388728, 0.784713, 0.0284857, 0, 0.406944, 0.774946, 0.0266186, 0, 0.425515, 0.76448, 0.0248593, 0, 0.444449, 0.753793, 0.0232114, 0, 0.463756, 0.743506, 0.0217039, 0, 0.483447, 0.732555, 0.0202841, 0, 0.503535, 0.720965, 0.0189648, 0, 0.524036, 0.709422, 0.0177189, 0, 0.544968, 0.697756, 0.0165626, 0, 0.56635, 0.685565, 0.015483, 0, 0.588208, 0.673987, 0.0144892, 0, 0.610569, 0.66244, 0.0135607, 0, 0.633466, 0.651675, 0.0126956, 0, 0.656936, 0.641598, 0.0118788, 0, 0.681025, 0.63121, 0.0111261, 0, 0.705788, 0.620514, 0.010437, 0, 0.731289, 0.609366, 978747e-8, 0, 0.757606, 0.598137, 917257e-8, 0, 0.784834, 0.586966, 859778e-8, 0, 0.813085, 0.575549, 806803e-8, 0, 0.842485, 0.563797, 757294e-8, 0, 0.87313, 0.551758, 710592e-8, 0, 0.904762, 0.539894, 66841e-7, 0, 0.936508, 0.527901, 627901e-8, 0, 0.968254, 0.515819, 590506e-8, 0, 1, 1, 0.120864, 0, 0, 1, 0.120864, 0, 0, 0.999996, 0.120864, 0, 0, 0.99998, 0.120867, 0, 0, 0.99994, 0.120872, 0, 323781e-9, 0.999852, 0.120884, 0, 188693e-8, 0.999693, 0.120903, 0, 473489e-8, 0.999426, 0.120929, 0, 872704e-8, 0.999002, 0.120955, 0, 0.0137237, 0.998235, 0.120918, 0, 0.0196068, 0.994608, 0.119764, 0, 0.0262803, 0.992997, 0.119265, 0, 0.0336657, 0.990968, 0.11863, 0, 0.0416987, 0.987002, 0.117261, 0, 0.0503261, 0.983524, 0.116009, 0, 0.0595035, 0.97875, 0.114252, 0, 0.0691935, 0.972652, 0.11193, 0, 0.0793645, 0.966613, 0.109555, 0, 0.0899894, 0.959275, 0.106612, 0, 0.101045, 0.951272, 0.103375, 0, 0.112512, 0.942323, 0.0996594, 0, 0.124372, 0.933679, 0.0958841, 0, 0.136611, 0.924822, 0.0919265, 0, 0.149216, 0.915742, 0.0878061, 0, 0.162176, 0.906348, 0.0834894, 0, 0.175482, 0.896883, 0.079085, 0, 0.189125, 0.88774, 0.0746745, 0, 0.203098, 0.87986, 0.0705773, 0, 0.217396, 0.871998, 0.0665005, 0, 0.232015, 0.864325, 0.0625413, 0, 0.24695, 0.856685, 0.0586781, 0, 0.2622, 0.84925, 0.0550063, 0, 0.277761, 0.841719, 0.0514727, 0, 0.293634, 0.834755, 0.0481398, 0, 0.309819, 0.827853, 0.0450172, 0, 0.326315, 0.820888, 0.0420969, 0, 0.343126, 0.813616, 0.0393702, 0, 0.360254, 0.805767, 0.0367771, 0, 0.377701, 0.797338, 0.0343274, 0, 0.395474, 0.789122, 0.0320529, 0, 0.413577, 0.780601, 0.0299485, 0, 0.432018, 0.771424, 0.0279812, 0, 0.450804, 0.761502, 0.0261054, 0, 0.469944, 0.751166, 0.0243942, 0, 0.489451, 0.741276, 0.0228087, 0, 0.509337, 0.730898, 0.0213265, 0, 0.529617, 0.719878, 0.0199307, 0, 0.550307, 0.708379, 0.0186574, 0, 0.571428, 0.697165, 0.0174446, 0, 0.593003, 0.685554, 0.0163144, 0, 0.615059, 0.673631, 0.015276, 0, 0.637628, 0.662385, 0.0143003, 0, 0.660746, 0.651059, 0.0134112, 0, 0.68446, 0.640451, 0.0125794, 0, 0.70882, 0.630536, 0.011793, 0, 0.733893, 0.620316, 0.0110547, 0, 0.759756, 0.609722, 0.0103668, 0, 0.786505, 0.598804, 973009e-8, 0, 0.814259, 0.587871, 912812e-8, 0, 0.843157, 0.577121, 858916e-8, 0, 0.87334, 0.566019, 807333e-8, 0, 0.904762, 0.554664, 759687e-8, 0, 0.936508, 0.543101, 714759e-8, 0, 0.968254, 0.531558, 673418e-8, 0, 1, 1, 0.146767, 0, 0, 1, 0.146767, 0, 0, 0.999997, 0.146767, 0, 0, 0.999977, 0.146765, 0, 320658e-11, 0.999929, 0.146762, 0, 682576e-9, 0.999823, 0.146753, 0, 276402e-8, 0.999633, 0.146735, 0, 614771e-8, 0.999314, 0.146699, 0, 0.0106613, 0.998796, 0.14662, 0, 0.0161546, 0.997124, 0.146107, 0, 0.0225063, 0.994062, 0.144857, 0, 0.0296198, 0.992154, 0.144011, 0, 0.037417, 0.989186, 0.142712, 0, 0.0458348, 0.985279, 0.140926, 0, 0.0548211, 0.980826, 0.13885, 0, 0.0643326, 0.975056, 0.136168, 0, 0.074333, 0.969005, 0.133217, 0, 0.0847917, 0.961554, 0.12959, 0, 0.0956828, 0.954206, 0.125886, 0, 0.106984, 0.945046, 0.121335, 0, 0.118675, 0.935678, 0.116492, 0, 0.130741, 0.926748, 0.111635, 0, 0.143166, 0.917764, 0.106625, 0, 0.155939, 0.908358, 0.101325, 0, 0.169049, 0.899219, 0.0960249, 0, 0.182487, 0.890089, 0.0906527, 0, 0.196245, 0.881488, 0.0853905, 0, 0.210317, 0.874031, 0.0804177, 0, 0.224697, 0.866932, 0.0756005, 0, 0.23938, 0.859976, 0.0709019, 0, 0.254364, 0.853375, 0.0664391, 0, 0.269646, 0.846971, 0.0622012, 0, 0.285223, 0.840483, 0.058129, 0, 0.301096, 0.833969, 0.0542762, 0, 0.317265, 0.82806, 0.0507042, 0, 0.333729, 0.822128, 0.047368, 0, 0.350491, 0.815989, 0.044272, 0, 0.367554, 0.809336, 0.0413444, 0, 0.38492, 0.802177, 0.038601, 0, 0.402594, 0.79441, 0.0360227, 0, 0.420582, 0.786573, 0.0336383, 0, 0.438891, 0.778619, 0.0314321, 0, 0.457527, 0.77, 0.029362, 0, 0.476499, 0.760698, 0.0274102, 0, 0.49582, 0.750932, 0.0256146, 0, 0.5155, 0.740993, 0.023974, 0, 0.535555, 0.731159, 0.0224182, 0, 0.556, 0.720836, 0.0209889, 0, 0.576855, 0.709913, 0.0196411, 0, 0.598143, 0.698415, 0.0183824, 0, 0.619888, 0.68745, 0.0172222, 0, 0.642123, 0.676154, 0.0161509, 0, 0.664883, 0.664383, 0.0151397, 0, 0.688211, 0.6533, 0.0141873, 0, 0.71216, 0.642072, 0.0133105, 0, 0.736792, 0.631412, 0.0124932, 0, 0.762186, 0.621622, 0.0117408, 0, 0.788439, 0.611681, 0.0110358, 0, 0.815672, 0.60142, 0.0103775, 0, 0.844034, 0.59083, 975623e-8, 0, 0.873699, 0.580254, 918084e-8, 0, 0.904765, 0.569841, 864721e-8, 0, 0.936508, 0.559224, 815731e-8, 0, 0.968254, 0.548315, 767924e-8, 0, 1, 1, 0.177563, 0, 0, 1, 0.177563, 0, 0, 0.999994, 0.177562, 0, 0, 0.999972, 0.177555, 0, 664171e-10, 0.999914, 0.177536, 0, 12276e-7, 0.999787, 0.177496, 0, 388025e-8, 0.999556, 0.17742, 0, 783463e-8, 0.999165, 0.177285, 0, 0.0128953, 0.9985, 0.177037, 0, 0.0189053, 0.995388, 0.175634, 0, 0.025742, 0.993102, 0.174375, 0, 0.033309, 0.990992, 0.173121, 0, 0.0415298, 0.986932, 0.170896, 0, 0.0503425, 0.982786, 0.16847, 0, 0.0596964, 0.977592, 0.165455, 0, 0.0695498, 0.971075, 0.161676, 0, 0.0798676, 0.963967, 0.157458, 0, 0.0906201, 0.956397, 0.152836, 0, 0.101783, 0.947489, 0.147467, 0, 0.113333, 0.937564, 0.14145, 0, 0.125254, 0.928182, 0.135383, 0, 0.137529, 0.919027, 0.129212, 0, 0.150144, 0.909618, 0.12276, 0, 0.163088, 0.900492, 0.116273, 0, 0.176351, 0.891671, 0.1098, 0, 0.189924, 0.883146, 0.103362, 0, 0.203799, 0.875151, 0.0970799, 0, 0.21797, 0.868338, 0.0911732, 0, 0.232433, 0.862033, 0.0854966, 0, 0.247182, 0.856107, 0.0800691, 0, 0.262216, 0.850644, 0.0749618, 0, 0.27753, 0.845261, 0.070079, 0, 0.293124, 0.839885, 0.0654321, 0, 0.308997, 0.834609, 0.0610975, 0, 0.325149, 0.829083, 0.0569741, 0, 0.341581, 0.82404, 0.0531736, 0, 0.358294, 0.818968, 0.049665, 0, 0.37529, 0.813496, 0.0463856, 0, 0.392573, 0.807533, 0.0433217, 0, 0.410148, 0.80099, 0.0404402, 0, 0.428019, 0.793891, 0.0377578, 0, 0.446192, 0.786281, 0.0352616, 0, 0.464676, 0.778773, 0.0329577, 0, 0.483478, 0.770737, 0.030808, 0, 0.502608, 0.762094, 0.0287964, 0, 0.522079, 0.752898, 0.0269254, 0, 0.541905, 0.743306, 0.0251926, 0, 0.5621, 0.733416, 0.023595, 0, 0.582684, 0.723742, 0.0221155, 0, 0.603677, 0.713542, 0.0207435, 0, 0.625106, 0.702755, 0.019434, 0, 0.646998, 0.691484, 0.0182046, 0, 0.66939, 0.680531, 0.0170771, 0, 0.692324, 0.66953, 0.0160339, 0, 0.715849, 0.658126, 0.0150677, 0, 0.740028, 0.646933, 0.0141551, 0, 0.764937, 0.636107, 0.0133179, 0, 0.790673, 0.625271, 0.0125284, 0, 0.817358, 0.615225, 0.0117937, 0, 0.84515, 0.605678, 0.0111181, 0, 0.874244, 0.59583, 0.0104759, 0, 0.904828, 0.585704, 986672e-8, 0, 0.936508, 0.575413, 929712e-8, 0, 0.968254, 0.565373, 876713e-8, 0, 1, 1, 0.214058, 0, 0, 0.999999, 0.214058, 0, 0, 0.999994, 0.214055, 0, 0, 0.999966, 0.214039, 0, 259642e-9, 0.999893, 0.213998, 0, 200075e-8, 0.999737, 0.21391, 0, 527775e-8, 0.999449, 0.213745, 0, 983959e-8, 0.99896, 0.213458, 0, 0.0154755, 0.9979, 0.212855, 0, 0.0220249, 0.994278, 0.210779, 0, 0.0293654, 0.992254, 0.20926, 0, 0.0374021, 0.98881, 0.206908, 0, 0.0460604, 0.984715, 0.204009, 0, 0.0552802, 0.979738, 0.200471, 0, 0.0650127, 0.972884, 0.195813, 0, 0.0752175, 0.965996, 0.190856, 0, 0.0858612, 0.957974, 0.185077, 0, 0.0969155, 0.949155, 0.17868, 0, 0.108356, 0.939288, 0.171513, 0, 0.120163, 0.928996, 0.163838, 0, 0.132319, 0.919563, 0.156246, 0, 0.144808, 0.910004, 0.148359, 0, 0.157618, 0.900791, 0.140417, 0, 0.170737, 0.892135, 0.132569, 0, 0.184155, 0.883803, 0.124741, 0, 0.197866, 0.876034, 0.117091, 0, 0.211861, 0.869219, 0.109835, 0, 0.226134, 0.863062, 0.102859, 0, 0.240682, 0.857795, 0.0962928, 0, 0.255499, 0.853009, 0.0900725, 0, 0.270583, 0.848603, 0.0842101, 0, 0.285931, 0.844335, 0.0786527, 0, 0.301542, 0.840208, 0.0734397, 0, 0.317415, 0.836035, 0.0685334, 0, 0.33355, 0.83172, 0.0639275, 0, 0.349948, 0.827135, 0.0595909, 0, 0.36661, 0.822797, 0.0556204, 0, 0.383539, 0.818387, 0.0519394, 0, 0.400738, 0.813565, 0.0485317, 0, 0.41821, 0.808142, 0.0453138, 0, 0.435961, 0.802212, 0.0423354, 0, 0.453997, 0.79573, 0.0395553, 0, 0.472324, 0.788741, 0.036988, 0, 0.490951, 0.781093, 0.0345688, 0, 0.509887, 0.773597, 0.0323297, 0, 0.529144, 0.765622, 0.0302719, 0, 0.548735, 0.757083, 0.0283477, 0, 0.568674, 0.747992, 0.0265562, 0, 0.588979, 0.738591, 0.0248844, 0, 0.609671, 0.728719, 0.0233342, 0, 0.630773, 0.719146, 0.0219081, 0, 0.652314, 0.709165, 0.0205711, 0, 0.674328, 0.69875, 0.0193248, 0, 0.696854, 0.687884, 0.0181582, 0, 0.719942, 0.676818, 0.0170746, 0, 0.743651, 0.666247, 0.0160718, 0, 0.768057, 0.655284, 0.0151262, 0, 0.793253, 0.64401, 0.0142561, 0, 0.819363, 0.633353, 0.0134327, 0, 0.846547, 0.622674, 0.012653, 0, 0.875017, 0.612265, 0.0119354, 0, 0.905021, 0.602455, 0.0112533, 0, 0.936508, 0.593147, 0.0106234, 0, 0.968254, 0.583592, 0.0100213, 0, 1, 1, 0.25717, 0, 0, 1, 0.25717, 0, 0, 0.999992, 0.257164, 0, 0, 0.999958, 0.257135, 0, 641715e-9, 0.999864, 0.25706, 0, 305314e-8, 0.999666, 0.256897, 0, 700975e-8, 0.999302, 0.256596, 0, 0.0122194, 0.998663, 0.25607, 0, 0.0184622, 0.995607, 0.254123, 0, 0.0255773, 0.993094, 0.252081, 0, 0.0334439, 0.9907, 0.249867, 0, 0.0419696, 0.98594, 0.246118, 0, 0.0510823, 0.981214, 0.242049, 0, 0.0607242, 0.974966, 0.236869, 0, 0.0708486, 0.967589, 0.230724, 0, 0.081417, 0.95915, 0.223635, 0, 0.0923974, 0.950257, 0.21596, 0, 0.103763, 0.940165, 0.207296, 0, 0.115491, 0.929396, 0.197901, 0, 0.127562, 0.919288, 0.188437, 0, 0.13996, 0.909428, 0.178762, 0, 0.15267, 0.900105, 0.169072, 0, 0.165679, 0.891418, 0.159478, 0, 0.178979, 0.883347, 0.15002, 0, 0.192558, 0.875992, 0.140813, 0, 0.20641, 0.869466, 0.13196, 0, 0.220529, 0.863699, 0.123501, 0, 0.234907, 0.858553, 0.115436, 0, 0.249542, 0.854379, 0.107901, 0, 0.264428, 0.850894, 0.10088, 0, 0.279564, 0.847632, 0.0942296, 0, 0.294947, 0.844571, 0.0879861, 0, 0.310575, 0.84163, 0.0821534, 0, 0.326448, 0.838542, 0.0766409, 0, 0.342566, 0.835412, 0.0715322, 0, 0.358929, 0.831899, 0.0666883, 0, 0.37554, 0.828177, 0.0622175, 0, 0.392399, 0.82416, 0.0580452, 0, 0.409511, 0.820393, 0.054267, 0, 0.426878, 0.816068, 0.0507172, 0, 0.444506, 0.811201, 0.0474041, 0, 0.4624, 0.805785, 0.0443174, 0, 0.480566, 0.799878, 0.0414562, 0, 0.499013, 0.793469, 0.0388147, 0, 0.517749, 0.786473, 0.0363453, 0, 0.536785, 0.778874, 0.0340225, 0, 0.556134, 0.771277, 0.0318599, 0, 0.575809, 0.763426, 0.0298859, 0, 0.595827, 0.755044, 0.0280357, 0, 0.616207, 0.746161, 0.0262979, 0, 0.636973, 0.737124, 0.0247295, 0, 0.65815, 0.72761, 0.0232514, 0, 0.679772, 0.717822, 0.0218755, 0, 0.701876, 0.708279, 0.0205942, 0, 0.724509, 0.698333, 0.0193947, 0, 0.74773, 0.68802, 0.0182717, 0, 0.771609, 0.677321, 0.0172044, 0, 0.79624, 0.666504, 0.0162122, 0, 0.821743, 0.656184, 0.0152924, 0, 0.84828, 0.64556, 0.0144326, 0, 0.876069, 0.634636, 0.0136157, 0, 0.905404, 0.624124, 0.0128612, 0, 0.936508, 0.613914, 0.0121435, 0, 0.968254, 0.603589, 0.0114887, 0, 1, 1, 0.307946, 0, 0, 0.999999, 0.307945, 0, 0, 0.999988, 0.307934, 0, 204479e-10, 0.999944, 0.307886, 0, 127833e-8, 0.999824, 0.307756, 0, 445047e-8, 0.999565, 0.30748, 0, 914673e-8, 0.999085, 0.306966, 0, 0.0150498, 0.998103, 0.306004, 0, 0.0219367, 0.994249, 0.303028, 0, 0.0296485, 0.991807, 0.300435, 0, 0.038068, 0.987773, 0.296554, 0, 0.0471062, 0.982673, 0.2916, 0, 0.0566942, 0.976623, 0.285641, 0, 0.0667768, 0.968757, 0.27815, 0, 0.0773099, 0.959849, 0.269529, 0, 0.088257, 0.950663, 0.260248, 0, 0.0995879, 0.940129, 0.249704, 0, 0.111277, 0.92895, 0.238291, 0, 0.123304, 0.917996, 0.226501, 0, 0.13565, 0.907813, 0.214669, 0, 0.148299, 0.898305, 0.202835, 0, 0.161237, 0.889626, 0.191158, 0, 0.174455, 0.88175, 0.179695, 0, 0.187941, 0.874715, 0.168548, 0, 0.201687, 0.868746, 0.15792, 0, 0.215687, 0.863703, 0.147807, 0, 0.229933, 0.859315, 0.138149, 0, 0.24442, 0.855538, 0.128993, 0, 0.259145, 0.852428, 0.120414, 0, 0.274103, 0.850168, 0.112498, 0, 0.289293, 0.848132, 0.105054, 0, 0.304711, 0.846291, 0.0981087, 0, 0.320357, 0.844431, 0.0915942, 0, 0.33623, 0.842493, 0.0855056, 0, 0.35233, 0.840368, 0.0798204, 0, 0.368658, 0.83798, 0.0745097, 0, 0.385214, 0.83523, 0.0695424, 0, 0.402002, 0.832091, 0.0649092, 0, 0.419023, 0.828667, 0.0606291, 0, 0.436282, 0.824805, 0.0566523, 0, 0.453782, 0.820988, 0.0530229, 0, 0.471529, 0.816635, 0.0496364, 0, 0.489528, 0.811725, 0.0464658, 0, 0.507788, 0.806316, 0.0435082, 0, 0.526317, 0.800469, 0.0407873, 0, 0.545124, 0.794107, 0.038255, 0, 0.564221, 0.787218, 0.0358825, 0, 0.583621, 0.779872, 0.0336785, 0, 0.603341, 0.772097, 0.0316379, 0, 0.623397, 0.764484, 0.0297379, 0, 0.643812, 0.756428, 0.0279581, 0, 0.664611, 0.748022, 0.0263153, 0, 0.685824, 0.739268, 0.0247799, 0, 0.707488, 0.73024, 0.0233385, 0, 0.729646, 0.720893, 0.0220035, 0, 0.752354, 0.71119, 0.0207555, 0, 0.77568, 0.701791, 0.0195843, 0, 0.799715, 0.692184, 0.0184891, 0, 0.824574, 0.682258, 0.0174541, 0, 0.850417, 0.67206, 0.0164873, 0, 0.877466, 0.661717, 0.0155959, 0, 0.90604, 0.651462, 0.0147519, 0, 0.936528, 0.641467, 0.0139727, 0, 0.968254, 0.631229, 0.0132363, 0, 1, 1, 0.367573, 0, 0, 0.999999, 0.367571, 0, 0, 0.999984, 0.367553, 0, 183382e-9, 0.999925, 0.367473, 0, 225254e-8, 0.999759, 0.367259, 0, 628165e-8, 0.99941, 0.366801, 0, 0.0117858, 0.998739, 0.365946, 0, 0.0184359, 0.995529, 0.363191, 0, 0.0260114, 0.992875, 0.360171, 0, 0.0343581, 0.989135, 0.355981, 0, 0.0433637, 0.984166, 0.350401, 0, 0.0529438, 0.977871, 0.343348, 0, 0.0630334, 0.96951, 0.334341, 0, 0.0735805, 0.959964, 0.323862, 0, 0.0845437, 0.950162, 0.312521, 0, 0.095889, 0.938882, 0.299577, 0, 0.107588, 0.926992, 0.285573, 0, 0.119617, 0.915589, 0.271212, 0, 0.131957, 0.904791, 0.256611, 0, 0.144591, 0.895177, 0.242224, 0, 0.157503, 0.886403, 0.227952, 0, 0.170682, 0.878957, 0.214192, 0, 0.184117, 0.872418, 0.200795, 0, 0.197799, 0.867029, 0.188015, 0, 0.21172, 0.862835, 0.175975, 0, 0.225873, 0.859411, 0.164526, 0, 0.240253, 0.856655, 0.153693, 0, 0.254854, 0.854519, 0.14352, 0, 0.269673, 0.852828, 0.13397, 0, 0.284707, 0.851412, 0.124984, 0, 0.299953, 0.850609, 0.116748, 0, 0.315408, 0.849855, 0.10905, 0, 0.331073, 0.849017, 0.101839, 0, 0.346946, 0.848079, 0.0951359, 0, 0.363028, 0.846911, 0.0888774, 0, 0.379318, 0.845445, 0.0830375, 0, 0.395818, 0.84362, 0.0775844, 0, 0.41253, 0.841411, 0.0725054, 0, 0.429457, 0.838768, 0.0677691, 0, 0.446602, 0.835801, 0.0634016, 0, 0.463968, 0.832341, 0.0593095, 0, 0.481561, 0.828424, 0.0555121, 0, 0.499386, 0.824312, 0.052024, 0, 0.51745, 0.819918, 0.0487865, 0, 0.535761, 0.815072, 0.0457801, 0, 0.554328, 0.809863, 0.0430184, 0, 0.573162, 0.804164, 0.0404245, 0, 0.592275, 0.798034, 0.0380146, 0, 0.611681, 0.791436, 0.0357436, 0, 0.631398, 0.784498, 0.0336475, 0, 0.651445, 0.777125, 0.0316666, 0, 0.671845, 0.769365, 0.0298122, 0, 0.692628, 0.761579, 0.0281001, 0, 0.713827, 0.753746, 0.0265049, 0, 0.735484, 0.745573, 0.0250067, 0, 0.75765, 0.737083, 0.0236026, 0, 0.78039, 0.728545, 0.0223302, 0, 0.803789, 0.719691, 0.0211243, 0, 0.82796, 0.710569, 0.0199983, 0, 0.853056, 0.701216, 0.0189569, 0, 0.879298, 0.692094, 0.0179702, 0, 0.907014, 0.682909, 0.0170418, 0, 0.936691, 0.673509, 0.0161732, 0, 0.968254, 0.663863, 0.0153406, 0, 1, 1, 0.437395, 0, 0, 0.999998, 0.437394, 0, 0, 0.99998, 0.437363, 0, 616704e-9, 0.999891, 0.437232, 0, 367925e-8, 0.999656, 0.436877, 0, 867446e-8, 0.999148, 0.436121, 0, 0.0150679, 0.997959, 0.434564, 0, 0.022531, 0.993464, 0.430134, 0, 0.0308507, 0.990606, 0.426077, 0, 0.0398805, 0.985027, 0.419397, 0, 0.0495148, 0.978491, 0.41118, 0, 0.0596749, 0.969643, 0.40048, 0, 0.0703001, 0.959189, 0.38769, 0, 0.0813427, 0.948223, 0.373575, 0, 0.0927641, 0.935955, 0.357622, 0, 0.104533, 0.923237, 0.34043, 0, 0.116624, 0.911074, 0.322735, 0, 0.129015, 0.899724, 0.30479, 0, 0.141687, 0.890189, 0.287392, 0, 0.154626, 0.881796, 0.270248, 0, 0.167818, 0.874781, 0.253659, 0, 0.181252, 0.869166, 0.237786, 0, 0.194918, 0.864725, 0.222618, 0, 0.208807, 0.861565, 0.208356, 0, 0.222913, 0.859284, 0.194867, 0, 0.237229, 0.857677, 0.18212, 0, 0.25175, 0.856714, 0.17018, 0, 0.266473, 0.856155, 0.158969, 0, 0.281392, 0.8558, 0.148413, 0, 0.296505, 0.855672, 0.138578, 0, 0.311811, 0.855538, 0.129345, 0, 0.327306, 0.855689, 0.120861, 0, 0.342991, 0.855767, 0.112969, 0, 0.358864, 0.855618, 0.105593, 0, 0.374925, 0.85525, 0.0987451, 0, 0.391176, 0.854583, 0.0923727, 0, 0.407616, 0.853534, 0.0864143, 0, 0.424249, 0.852061, 0.0808338, 0, 0.441076, 0.850253, 0.0756771, 0, 0.4581, 0.848004, 0.0708612, 0, 0.475324, 0.845333, 0.0663784, 0, 0.492754, 0.842376, 0.0622631, 0, 0.510394, 0.838956, 0.0584112, 0, 0.528251, 0.835121, 0.0548328, 0, 0.546331, 0.830842, 0.0514838, 0, 0.564644, 0.826212, 0.048355, 0, 0.583198, 0.821522, 0.0454714, 0, 0.602005, 0.816551, 0.0428263, 0, 0.621078, 0.811211, 0.0403612, 0, 0.640434, 0.805479, 0.038039, 0, 0.660089, 0.799409, 0.0358739, 0, 0.680066, 0.79306, 0.0338727, 0, 0.70039, 0.786395, 0.0319985, 0, 0.721094, 0.779416, 0.030241, 0, 0.742215, 0.77214, 0.0285951, 0, 0.7638, 0.764636, 0.0270747, 0, 0.785912, 0.756836, 0.0256354, 0, 0.808628, 0.749315, 0.0243027, 0, 0.832055, 0.741561, 0.0230497, 0, 0.856338, 0.733589, 0.0218801, 0, 0.88169, 0.725479, 0.020784, 0, 0.908441, 0.717255, 0.0197702, 0, 0.937125, 0.708829, 0.0188168, 0, 0.968254, 0.700191, 0.0179113, 0, 1, 1, 0.518937, 0, 0, 0.999998, 0.518933, 0, 0, 0.999967, 0.518883, 0, 147741e-8, 0.999832, 0.51866, 0, 573221e-8, 0.999466, 0.518057, 0, 0.011826, 0.998644, 0.516752, 0, 0.0192116, 0.994458, 0.512347, 0, 0.027573, 0.991223, 0.507675, 0, 0.0367099, 0.985515, 0.500188, 0, 0.046487, 0.978308, 0.490408, 0, 0.0568071, 0.968359, 0.477357, 0, 0.0675984, 0.95682, 0.461752, 0, 0.0788059, 0.943929, 0.443796, 0, 0.090386, 0.930224, 0.423893, 0, 0.102304, 0.916514, 0.402682, 0, 0.114532, 0.903653, 0.380914, 0, 0.127047, 0.892315, 0.359212, 0, 0.139828, 0.882942, 0.338102, 0, 0.152861, 0.875438, 0.31773, 0, 0.16613, 0.869642, 0.298186, 0, 0.179624, 0.865304, 0.279491, 0, 0.193332, 0.862382, 0.261804, 0, 0.207247, 0.860666, 0.245146, 0, 0.22136, 0.859788, 0.229406, 0, 0.235666, 0.859608, 0.214605, 0, 0.250158, 0.859912, 0.200691, 0, 0.264832, 0.86053, 0.187623, 0, 0.279684, 0.861368, 0.17539, 0, 0.294711, 0.862237, 0.163901, 0, 0.309911, 0.863127, 0.153175, 0, 0.32528, 0.863923, 0.143147, 0, 0.340819, 0.864567, 0.133781, 0, 0.356524, 0.865013, 0.125042, 0, 0.372397, 0.86539, 0.116952, 0, 0.388438, 0.865591, 0.109476, 0, 0.404645, 0.865517, 0.102542, 0, 0.421022, 0.865084, 0.0960688, 0, 0.437569, 0.864309, 0.0900499, 0, 0.454287, 0.863151, 0.0844328, 0, 0.471181, 0.861649, 0.0792218, 0, 0.488253, 0.859742, 0.0743482, 0, 0.505507, 0.857446, 0.0697963, 0, 0.522947, 0.854757, 0.0655364, 0, 0.54058, 0.851783, 0.061608, 0, 0.558412, 0.848516, 0.0579701, 0, 0.576449, 0.844897, 0.0545742, 0, 0.594701, 0.840956, 0.0514167, 0, 0.613178, 0.836676, 0.0484598, 0, 0.631892, 0.832075, 0.0456934, 0, 0.650856, 0.827191, 0.0431178, 0, 0.670088, 0.822295, 0.0407718, 0, 0.689606, 0.817294, 0.0386032, 0, 0.709434, 0.812013, 0.0365675, 0, 0.7296, 0.806465, 0.0346547, 0, 0.750138, 0.800691, 0.0328717, 0, 0.771093, 0.794709, 0.031211, 0, 0.792519, 0.788493, 0.0296504, 0, 0.814488, 0.782049, 0.0281782, 0, 0.837097, 0.775403, 0.0267965, 0, 0.860481, 0.76857, 0.0255002, 0, 0.884842, 0.761536, 0.0242759, 0, 0.910494, 0.754303, 0.0231142, 0, 0.937985, 0.74692, 0.0220305, 0, 0.968254, 0.739745, 0.0210192, 0, 1, 1, 0.613914, 0, 0, 0.999996, 0.613907, 0, 963597e-10, 0.999942, 0.613814, 0, 301247e-8, 0.999704, 0.613407, 0, 870385e-8, 0.999046, 0.612302, 0, 0.0160714, 0.995516, 0.608266, 0, 0.0245899, 0.991726, 0.602863, 0, 0.0339681, 0.985157, 0.593956, 0, 0.0440254, 0.97642, 0.581748, 0, 0.0546409, 0.964404, 0.565183, 0, 0.0657284, 0.950601, 0.545273, 0, 0.0772246, 0.935158, 0.522129, 0, 0.0890812, 0.919364, 0.496782, 0, 0.10126, 0.904754, 0.470571, 0, 0.113731, 0.89176, 0.444037, 0, 0.126469, 0.881492, 0.418322, 0, 0.139454, 0.873656, 0.393522, 0, 0.15267, 0.868053, 0.369795, 0, 0.166101, 0.864336, 0.347171, 0, 0.179736, 0.862259, 0.325737, 0, 0.193565, 0.861556, 0.305532, 0, 0.207578, 0.861776, 0.286416, 0, 0.221769, 0.862661, 0.268355, 0, 0.23613, 0.864015, 0.251334, 0, 0.250656, 0.865711, 0.235352, 0, 0.265343, 0.867519, 0.220302, 0, 0.280187, 0.869351, 0.206161, 0, 0.295183, 0.871144, 0.192908, 0, 0.31033, 0.872839, 0.180505, 0, 0.325624, 0.874307, 0.168848, 0, 0.341065, 0.875667, 0.158021, 0, 0.35665, 0.876758, 0.147877, 0, 0.37238, 0.87764, 0.138441, 0, 0.388253, 0.878237, 0.129627, 0, 0.404269, 0.878563, 0.121415, 0, 0.42043, 0.878572, 0.113741, 0, 0.436735, 0.87842, 0.106652, 0, 0.453187, 0.878057, 0.100097, 0, 0.469786, 0.877413, 0.0940128, 0, 0.486536, 0.87646, 0.0883462, 0, 0.503439, 0.875233, 0.0830924, 0, 0.520498, 0.8737, 0.0781975, 0, 0.537717, 0.871873, 0.07364, 0, 0.555102, 0.86978, 0.0694103, 0, 0.572657, 0.867405, 0.0654696, 0, 0.59039, 0.864751, 0.0617914, 0, 0.608307, 0.861818, 0.0583491, 0, 0.626419, 0.858645, 0.0551443, 0, 0.644733, 0.855307, 0.0521894, 0, 0.663264, 0.851736, 0.0494334, 0, 0.682025, 0.847927, 0.0468504, 0, 0.701032, 0.843888, 0.0444261, 0, 0.720308, 0.839629, 0.0421497, 0, 0.739875, 0.835158, 0.0400082, 0, 0.759764, 0.830509, 0.0380076, 0, 0.780014, 0.825714, 0.0361488, 0, 0.800673, 0.820729, 0.0343956, 0, 0.821803, 0.815751, 0.0327781, 0, 0.843492, 0.810752, 0.031275, 0, 0.86586, 0.805587, 0.0298542, 0, 0.889087, 0.800317, 0.0285397, 0, 0.913466, 0.79489, 0.0272948, 0, 0.93952, 0.789314, 0.0261139, 0, 0.96835, 0.783593, 0.0249938, 0, 1, 1, 0.724258, 0, 0, 0.999992, 0.724243, 0, 726889e-9, 0.99987, 0.724044, 0, 569574e-8, 0.999336, 0.72317, 0, 0.0131702, 0.996271, 0.719432, 0, 0.0220738, 0.991159, 0.712576, 0, 0.0319405, 0.982465, 0.700927, 0, 0.0425202, 0.97049, 0.684297, 0, 0.0536599, 0.953973, 0.661244, 0, 0.065258, 0.935546, 0.633804, 0, 0.0772427, 0.916596, 0.603071, 0, 0.0895616, 0.899353, 0.57105, 0, 0.102175, 0.885216, 0.539206, 0, 0.11505, 0.875076, 0.508714, 0, 0.128164, 0.868334, 0.479571, 0, 0.141495, 0.864414, 0.451796, 0, 0.155026, 0.862678, 0.425328, 0, 0.168745, 0.862835, 0.400352, 0, 0.182639, 0.864067, 0.376532, 0, 0.196699, 0.866086, 0.35391, 0, 0.210915, 0.868557, 0.332424, 0, 0.225282, 0.871271, 0.312053, 0, 0.239792, 0.874058, 0.292764, 0, 0.25444, 0.8768, 0.27453, 0, 0.269223, 0.87939, 0.257297, 0, 0.284135, 0.8819, 0.24114, 0, 0.299174, 0.884187, 0.225934, 0, 0.314337, 0.886262, 0.211669, 0, 0.329622, 0.888119, 0.198311, 0, 0.345026, 0.889709, 0.185783, 0, 0.360549, 0.891054, 0.174063, 0, 0.376189, 0.892196, 0.163143, 0, 0.391946, 0.893101, 0.152952, 0, 0.407819, 0.893803, 0.143475, 0, 0.423808, 0.894277, 0.134647, 0, 0.439914, 0.894532, 0.126434, 0, 0.456137, 0.894576, 0.1188, 0, 0.472479, 0.894393, 0.111694, 0, 0.48894, 0.893976, 0.105069, 0, 0.505523, 0.893346, 0.0989077, 0, 0.52223, 0.892502, 0.0931724, 0, 0.539064, 0.891441, 0.0878276, 0, 0.556028, 0.890276, 0.082903, 0, 0.573125, 0.888972, 0.0783505, 0, 0.590361, 0.887469, 0.0741083, 0, 0.607741, 0.885785, 0.0701633, 0, 0.62527, 0.883914, 0.0664835, 0, 0.642957, 0.881872, 0.0630567, 0, 0.660809, 0.879651, 0.0598527, 0, 0.678836, 0.877267, 0.0568615, 0, 0.69705, 0.874717, 0.05406, 0, 0.715465, 0.872012, 0.0514378, 0, 0.734098, 0.869157, 0.0489805, 0, 0.752968, 0.866155, 0.0466727, 0, 0.772101, 0.863014, 0.0445056, 0, 0.791529, 0.859748, 0.0424733, 0, 0.81129, 0.856416, 0.0405957, 0, 0.831438, 0.852958, 0.0388273, 0, 0.852044, 0.849382, 0.0371619, 0, 0.87321, 0.845694, 0.0355959, 0, 0.89509, 0.841893, 0.0341155, 0, 0.917932, 0.837981, 0.0327141, 0, 0.942204, 0.833963, 0.0313856, 0, 0.968981, 0.829847, 0.0301275, 0, 1, 1, 0.85214, 0, 0, 0.999969, 0.852095, 0, 279627e-8, 0.999483, 0.851408, 0, 0.0107635, 0.994545, 0.84579, 0, 0.0206454, 0.986188, 0.835231, 0, 0.0315756, 0.969847, 0.814687, 0, 0.0432021, 0.945951, 0.783735, 0, 0.0553396, 0.91917, 0.746074, 0, 0.0678766, 0.895488, 0.706938, 0, 0.0807395, 0.878232, 0.669534, 0, 0.0938767, 0.868252, 0.635168, 0, 0.10725, 0.863873, 0.603069, 0, 0.120832, 0.863369, 0.572514, 0, 0.134598, 0.86545, 0.543169, 0, 0.148533, 0.868803, 0.514578, 0, 0.16262, 0.872794, 0.486762, 0, 0.176849, 0.87702, 0.459811, 0, 0.19121, 0.881054, 0.433654, 0, 0.205694, 0.884974, 0.408574, 0, 0.220294, 0.888587, 0.384525, 0, 0.235005, 0.891877, 0.36156, 0, 0.24982, 0.894793, 0.339661, 0, 0.264737, 0.89743, 0.318913, 0, 0.279751, 0.899796, 0.299302, 0, 0.294859, 0.901943, 0.280843, 0, 0.310058, 0.903858, 0.263481, 0, 0.325346, 0.905574, 0.247197, 0, 0.340721, 0.907069, 0.231915, 0, 0.356181, 0.908379, 0.217614, 0, 0.371725, 0.90952, 0.20425, 0, 0.387353, 0.910483, 0.191758, 0, 0.403063, 0.91128, 0.180092, 0, 0.418854, 0.911936, 0.169222, 0, 0.434727, 0.912454, 0.159098, 0, 0.450682, 0.912835, 0.149668, 0, 0.466718, 0.913078, 0.140884, 0, 0.482837, 0.913192, 0.132709, 0, 0.499038, 0.913175, 0.125095, 0, 0.515324, 0.91304, 0.118012, 0, 0.531695, 0.912781, 0.111417, 0, 0.548153, 0.91241, 0.105281, 0, 0.5647, 0.911924, 0.0995691, 0, 0.581338, 0.911331, 0.0942531, 0, 0.59807, 0.910637, 0.0893076, 0, 0.6149, 0.90984, 0.0846998, 0, 0.63183, 0.908941, 0.0804044, 0, 0.648865, 0.907944, 0.0763984, 0, 0.666011, 0.906857, 0.0726638, 0, 0.683273, 0.90568, 0.0691783, 0, 0.700659, 0.904416, 0.0659222, 0, 0.718176, 0.903067, 0.0628782, 0, 0.735834, 0.901637, 0.0600307, 0, 0.753646, 0.900128, 0.0573647, 0, 0.771625, 0.898544, 0.0548668, 0, 0.78979, 0.89689, 0.052527, 0, 0.808162, 0.895165, 0.0503306, 0, 0.826771, 0.893371, 0.0482668, 0, 0.845654, 0.891572, 0.0463605, 0, 0.864863, 0.889763, 0.0445998, 0, 0.884472, 0.887894, 0.0429451, 0, 0.904592, 0.885967, 0.0413884, 0, 0.925407, 0.883984, 0.0399225, 0, 0.947271, 0.881945, 0.0385405, 0, 0.97105, 0.879854, 0.0372362, 0, 1, 0.999804, 0.995833, 0, 0, 0.938155, 0.933611, 0, 0.0158731, 0.864755, 0.854311, 0, 0.0317461, 0.888594, 0.865264, 0, 0.0476191, 0.905575, 0.863922, 0, 0.0634921, 0.915125, 0.850558, 0, 0.0793651, 0.920665, 0.829254, 0, 0.0952381, 0.924073, 0.802578, 0, 0.111111, 0.926304, 0.772211, 0, 0.126984, 0.927829, 0.739366, 0, 0.142857, 0.928924, 0.705033, 0, 0.15873, 0.92973, 0.670019, 0, 0.174603, 0.930339, 0.634993, 0, 0.190476, 0.930811, 0.600485, 0, 0.206349, 0.931191, 0.566897, 0, 0.222222, 0.93149, 0.534485, 0, 0.238095, 0.931737, 0.503429, 0, 0.253968, 0.931939, 0.473811, 0, 0.269841, 0.932108, 0.445668, 0, 0.285714, 0.93225, 0.418993, 0, 0.301587, 0.932371, 0.393762, 0, 0.31746, 0.932474, 0.369939, 0, 0.333333, 0.932562, 0.347479, 0, 0.349206, 0.932638, 0.326336, 0, 0.365079, 0.932703, 0.306462, 0, 0.380952, 0.93276, 0.287805, 0, 0.396825, 0.932809, 0.270313, 0, 0.412698, 0.932851, 0.253933, 0, 0.428571, 0.932887, 0.23861, 0, 0.444444, 0.932917, 0.224289, 0, 0.460317, 0.932943, 0.210917, 0, 0.47619, 0.932965, 0.19844, 0, 0.492063, 0.932982, 0.186807, 0, 0.507937, 0.932995, 0.175966, 0, 0.52381, 0.933005, 0.165869, 0, 0.539683, 0.933011, 0.156468, 0, 0.555556, 0.933013, 0.147719, 0, 0.571429, 0.933013, 0.139579, 0, 0.587302, 0.93301, 0.132007, 0, 0.603175, 0.933004, 0.124965, 0, 0.619048, 0.932994, 0.118416, 0, 0.634921, 0.932982, 0.112326, 0, 0.650794, 0.932968, 0.106663, 0, 0.666667, 0.93295, 0.101397, 0, 0.68254, 0.932931, 0.0964993, 0, 0.698413, 0.932908, 0.0919438, 0, 0.714286, 0.932883, 0.0877057, 0, 0.730159, 0.932856, 0.0837623, 0, 0.746032, 0.932827, 0.0800921, 0, 0.761905, 0.932796, 0.0766754, 0, 0.777778, 0.932762, 0.0734936, 0, 0.793651, 0.932727, 0.0705296, 0, 0.809524, 0.932689, 0.0677676, 0, 0.825397, 0.93265, 0.0651929, 0, 0.84127, 0.932609, 0.0627917, 0, 0.857143, 0.932565, 0.0605515, 0, 0.873016, 0.932521, 0.0584606, 0, 0.888889, 0.932474, 0.0565082, 0, 0.904762, 0.932427, 0.0546841, 0, 0.920635, 0.932377, 0.0529793, 0, 0.936508, 0.932326, 0.0513851, 0, 0.952381, 0.932274, 0.0498936, 0, 0.968254, 0.93222, 0.0484975, 0, 0.984127, 0.932164, 0.0471899, 0, 1], i = new Float32Array(e), n = new Float32Array(t);
    bt.LTC_FLOAT_1 = new Qn(i, 64, 64, dn, xn, Ho, yn, yn, ii, Ni, 1), bt.LTC_FLOAT_2 = new Qn(n, 64, 64, dn, xn, Ho, yn, yn, ii, Ni, 1), bt.LTC_FLOAT_1.needsUpdate = !0, bt.LTC_FLOAT_2.needsUpdate = !0;
    const r = new Uint16Array(e.length);
    e.forEach(function(o, l) {
      r[l] = el.toHalfFloat(o);
    });
    const a = new Uint16Array(t.length);
    t.forEach(function(o, l) {
      a[l] = el.toHalfFloat(o);
    }), bt.LTC_HALF_1 = new Qn(r, 64, 64, dn, on, Ho, yn, yn, ii, Ni, 1), bt.LTC_HALF_2 = new Qn(a, 64, 64, dn, on, Ho, yn, yn, ii, Ni, 1), bt.LTC_HALF_1.needsUpdate = !0, bt.LTC_HALF_2.needsUpdate = !0;
  }
}
const k5 = new et();
class Q4 {
  constructor(e) {
    e = e || {}, this.vertices = {
      near: [
        new _(),
        new _(),
        new _(),
        new _()
      ],
      far: [
        new _(),
        new _(),
        new _(),
        new _()
      ]
    }, e.projectionMatrix !== void 0 && this.setFromProjectionMatrix(e.projectionMatrix, e.maxFar || 1e4);
  }
  setFromProjectionMatrix(e, t) {
    const i = e.elements[11] === 0;
    return k5.copy(e).invert(), this.vertices.near[0].set(1, 1, -1), this.vertices.near[1].set(1, -1, -1), this.vertices.near[2].set(-1, -1, -1), this.vertices.near[3].set(-1, 1, -1), this.vertices.near.forEach(function(n) {
      n.applyMatrix4(k5);
    }), this.vertices.far[0].set(1, 1, 1), this.vertices.far[1].set(1, -1, 1), this.vertices.far[2].set(-1, -1, 1), this.vertices.far[3].set(-1, 1, 1), this.vertices.far.forEach(function(n) {
      n.applyMatrix4(k5);
      const r = Math.abs(n.z);
      i ? n.z *= Math.min(t / r, 1) : n.multiplyScalar(Math.min(t / r, 1));
    }), this.vertices;
  }
  split(e, t) {
    for (; e.length > t.length; )
      t.push(new Q4());
    t.length = e.length;
    for (let i = 0; i < e.length; i++) {
      const n = t[i];
      if (i === 0)
        for (let r = 0; r < 4; r++)
          n.vertices.near[r].copy(this.vertices.near[r]);
      else
        for (let r = 0; r < 4; r++)
          n.vertices.near[r].lerpVectors(this.vertices.near[r], this.vertices.far[r], e[i - 1]);
      if (i === e.length - 1)
        for (let r = 0; r < 4; r++)
          n.vertices.far[r].copy(this.vertices.far[r]);
      else
        for (let r = 0; r < 4; r++)
          n.vertices.far[r].lerpVectors(this.vertices.near[r], this.vertices.far[r], e[i]);
    }
  }
  toSpace(e, t) {
    for (let i = 0; i < 4; i++)
      t.vertices.near[i].copy(this.vertices.near[i]).applyMatrix4(e), t.vertices.far[i].copy(this.vertices.far[i]).applyMatrix4(e);
  }
}
const vC = {
  lights_fragment_begin: (
    /* glsl */
    `
vec3 geometryPosition = - vViewPosition;
vec3 geometryNormal = normal;
vec3 geometryViewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );

vec3 geometryClearcoatNormal = vec3( 0.0 );

#ifdef USE_CLEARCOAT

	geometryClearcoatNormal = clearcoatNormal;

#endif

#ifdef USE_IRIDESCENCE
	float dotNVi = saturate( dot( normal, geometryViewDir ) );
	if ( material.iridescenceThickness == 0.0 ) {
		material.iridescence = 0.0;
	} else {
		material.iridescence = saturate( material.iridescence );
	}
	if ( material.iridescence > 0.0 ) {
		material.iridescenceFresnel = evalIridescence( 1.0, material.iridescenceIOR, dotNVi, material.iridescenceThickness, material.specularColor );
		// Iridescence F0 approximation
		material.iridescenceF0 = Schlick_to_F0( material.iridescenceFresnel, 1.0, dotNVi );
	}
#endif

IncidentLight directLight;

#if ( NUM_POINT_LIGHTS > 0 ) && defined( RE_Direct )

	PointLight pointLight;
	#if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0
	PointLightShadow pointLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {

		pointLight = pointLights[ i ];

		getPointLightInfo( pointLight, geometryPosition, directLight );

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )
		pointLightShadow = pointLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getPointShadow( pointShadowMap[ i ], pointLightShadow.shadowMapSize, pointLightShadow.shadowBias, pointLightShadow.shadowRadius, vPointShadowCoord[ i ], pointLightShadow.shadowCameraNear, pointLightShadow.shadowCameraFar ) : 1.0;
		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_SPOT_LIGHTS > 0 ) && defined( RE_Direct )

	SpotLight spotLight;
 	vec4 spotColor;
	vec3 spotLightCoord;
	bool inSpotLightMap;

	#if defined( USE_SHADOWMAP ) && NUM_SPOT_LIGHT_SHADOWS > 0
	SpotLightShadow spotLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {

		spotLight = spotLights[ i ];

		getSpotLightInfo( spotLight, geometryPosition, directLight );

  		// spot lights are ordered [shadows with maps, shadows without maps, maps without shadows, none]
		#if ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#define SPOT_LIGHT_MAP_INDEX UNROLLED_LOOP_INDEX
		#elif ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		#define SPOT_LIGHT_MAP_INDEX NUM_SPOT_LIGHT_MAPS
		#else
		#define SPOT_LIGHT_MAP_INDEX ( UNROLLED_LOOP_INDEX - NUM_SPOT_LIGHT_SHADOWS + NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#endif
		#if ( SPOT_LIGHT_MAP_INDEX < NUM_SPOT_LIGHT_MAPS )
			spotLightCoord = vSpotLightCoord[ i ].xyz / vSpotLightCoord[ i ].w;
			inSpotLightMap = all( lessThan( abs( spotLightCoord * 2. - 1. ), vec3( 1.0 ) ) );
			spotColor = texture2D( spotLightMap[ SPOT_LIGHT_MAP_INDEX ], spotLightCoord.xy );
			directLight.color = inSpotLightMap ? directLight.color * spotColor.rgb : directLight.color;
		#endif
		#undef SPOT_LIGHT_MAP_INDEX

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		spotLightShadow = spotLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( spotShadowMap[ i ], spotLightShadow.shadowMapSize, spotLightShadow.shadowIntensity, spotLightShadow.shadowBias, spotLightShadow.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;

		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct ) && defined( USE_CSM ) && defined( CSM_CASCADES )

	DirectionalLight directionalLight;
	float linearDepth = (vViewPosition.z) / (shadowFar - cameraNear);
	#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
	DirectionalLightShadow directionalLightShadow;
	#endif

	#if defined( USE_SHADOWMAP ) && defined( CSM_FADE )
		vec2 cascade;
		float cascadeCenter;
		float closestEdge;
		float margin;
		float csmx;
		float csmy;

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {

			directionalLight = directionalLights[ i ];
			getDirectionalLightInfo( directionalLight, directLight );

			#if ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )
				// NOTE: Depth gets larger away from the camera.
				// cascade.x is closer, cascade.y is further
				cascade = CSM_cascades[ i ];
				cascadeCenter = ( cascade.x + cascade.y ) / 2.0;
				closestEdge = linearDepth < cascadeCenter ? cascade.x : cascade.y;
				margin = 0.25 * pow( closestEdge, 2.0 );
				csmx = cascade.x - margin / 2.0;
				csmy = cascade.y + margin / 2.0;
				if( linearDepth >= csmx && ( linearDepth < csmy || UNROLLED_LOOP_INDEX == CSM_CASCADES - 1 ) ) {

					float dist = min( linearDepth - csmx, csmy - linearDepth );
					float ratio = clamp( dist / margin, 0.0, 1.0 );

					vec3 prevColor = directLight.color;
					directionalLightShadow = directionalLightShadows[ i ];
					directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;

					bool shouldFadeLastCascade = UNROLLED_LOOP_INDEX == CSM_CASCADES - 1 && linearDepth > cascadeCenter;
					directLight.color = mix( prevColor, directLight.color, shouldFadeLastCascade ? ratio : 1.0 );

					ReflectedLight prevLight = reflectedLight;
					RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

					bool shouldBlend = UNROLLED_LOOP_INDEX != CSM_CASCADES - 1 || UNROLLED_LOOP_INDEX == CSM_CASCADES - 1 && linearDepth < cascadeCenter;
					float blendRatio = shouldBlend ? ratio : 1.0;

					reflectedLight.directDiffuse = mix( prevLight.directDiffuse, reflectedLight.directDiffuse, blendRatio );
					reflectedLight.directSpecular = mix( prevLight.directSpecular, reflectedLight.directSpecular, blendRatio );
					reflectedLight.indirectDiffuse = mix( prevLight.indirectDiffuse, reflectedLight.indirectDiffuse, blendRatio );
					reflectedLight.indirectSpecular = mix( prevLight.indirectSpecular, reflectedLight.indirectSpecular, blendRatio );

				}
			#endif

		}
		#pragma unroll_loop_end
	#elif defined (USE_SHADOWMAP)

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {

			directionalLight = directionalLights[ i ];
			getDirectionalLightInfo( directionalLight, directLight );

			#if ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )

				directionalLightShadow = directionalLightShadows[ i ];
				if(linearDepth >= CSM_cascades[UNROLLED_LOOP_INDEX].x && linearDepth < CSM_cascades[UNROLLED_LOOP_INDEX].y) directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;

				if(linearDepth >= CSM_cascades[UNROLLED_LOOP_INDEX].x && (linearDepth < CSM_cascades[UNROLLED_LOOP_INDEX].y || UNROLLED_LOOP_INDEX == CSM_CASCADES - 1)) RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

			#endif

		}
		#pragma unroll_loop_end

	#elif ( NUM_DIR_LIGHT_SHADOWS > 0 )
		// note: no loop here - all CSM lights are in fact one light only
		getDirectionalLightInfo( directionalLights[0], directLight );
		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	#endif

	#if ( NUM_DIR_LIGHTS > NUM_DIR_LIGHT_SHADOWS)
		// compute the lights not casting shadows (if any)

		#pragma unroll_loop_start
		for ( int i = NUM_DIR_LIGHT_SHADOWS; i < NUM_DIR_LIGHTS; i ++ ) {

			directionalLight = directionalLights[ i ];

			getDirectionalLightInfo( directionalLight, directLight );

			RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

		}
		#pragma unroll_loop_end

	#endif

#endif


#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct ) && !defined( USE_CSM ) && !defined( CSM_CASCADES )

	DirectionalLight directionalLight;
	#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
	DirectionalLightShadow directionalLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {

		directionalLight = directionalLights[ i ];

		getDirectionalLightInfo( directionalLight, directLight );

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )
		directionalLightShadow = directionalLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;
		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_RECT_AREA_LIGHTS > 0 ) && defined( RE_Direct_RectArea )

	RectAreaLight rectAreaLight;

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_RECT_AREA_LIGHTS; i ++ ) {

		rectAreaLight = rectAreaLights[ i ];
		RE_Direct_RectArea( rectAreaLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if defined( RE_IndirectDiffuse )

	vec3 iblIrradiance = vec3( 0.0 );

	vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );

	#if defined( USE_LIGHT_PROBES )

		irradiance += getLightProbeIrradiance( lightProbe, geometryNormal );

	#endif

	#if ( NUM_HEMI_LIGHTS > 0 )

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {

			irradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal );

		}
		#pragma unroll_loop_end

	#endif

#endif

#if defined( RE_IndirectSpecular )

	vec3 radiance = vec3( 0.0 );
	vec3 clearcoatRadiance = vec3( 0.0 );

#endif
`
  ),
  lights_pars_begin: (
    /* glsl */
    `
#if defined( USE_CSM ) && defined( CSM_CASCADES )
uniform vec2 CSM_cascades[CSM_CASCADES];
uniform float cameraNear;
uniform float shadowFar;
#endif
	` + ai.lights_pars_begin
  )
}, yC = new et(), G5 = new Q4(), R0 = new _(), Em = new zt(), H5 = [], V5 = [], W5 = new et(), xC = new et(), Lte = new _(0, 1, 0);
class Ite {
  constructor(e) {
    this.camera = e.camera, this.parent = e.parent, this.cascades = e.cascades || 3, this.maxFar = e.maxFar || 1e5, this.mode = e.mode || "practical", this.shadowMapSize = e.shadowMapSize || 2048, this.shadowBias = e.shadowBias || 1e-6, this.lightDirection = e.lightDirection || new _(1, -1, 1).normalize(), this.lightIntensity = e.lightIntensity || 3, this.lightNear = e.lightNear || 1, this.lightFar = e.lightFar || 2e3, this.lightMargin = e.lightMargin || 200, this.customSplitsCallback = e.customSplitsCallback, this.fade = !1, this.mainFrustum = new Q4(), this.frustums = [], this.breaks = [], this.lights = [], this.shaders = /* @__PURE__ */ new Map(), this.createLights(), this.updateFrustums(), this.injectInclude();
  }
  createLights() {
    for (let e = 0; e < this.cascades; e++) {
      const t = new _a(16777215, this.lightIntensity);
      t.castShadow = !0, t.shadow.mapSize.width = this.shadowMapSize, t.shadow.mapSize.height = this.shadowMapSize, t.shadow.camera.near = this.lightNear, t.shadow.camera.far = this.lightFar, t.shadow.bias = this.shadowBias, this.parent.add(t), this.parent.add(t.target), this.lights.push(t);
    }
  }
  initCascades() {
    const e = this.camera;
    e.updateProjectionMatrix(), this.mainFrustum.setFromProjectionMatrix(e.projectionMatrix, this.maxFar), this.mainFrustum.split(this.breaks, this.frustums);
  }
  updateShadowBounds() {
    const e = this.frustums;
    for (let t = 0; t < e.length; t++) {
      const n = this.lights[t].shadow.camera, r = this.frustums[t], a = r.vertices.near, o = r.vertices.far, l = o[0];
      let c;
      l.distanceTo(o[2]) > l.distanceTo(a[2]) ? c = o[2] : c = a[2];
      let h = l.distanceTo(c);
      if (this.fade) {
        const u = this.camera, d = Math.max(u.far, this.maxFar), f = r.vertices.far[0].z / (d - u.near), p = 0.25 * Math.pow(f, 2) * (d - u.near);
        h += p;
      }
      n.left = -h / 2, n.right = h / 2, n.top = h / 2, n.bottom = -h / 2, n.updateProjectionMatrix();
    }
  }
  getBreaks() {
    const e = this.camera, t = Math.min(e.far, this.maxFar);
    switch (this.breaks.length = 0, this.mode) {
      case "uniform":
        i(this.cascades, e.near, t, this.breaks);
        break;
      case "logarithmic":
        n(this.cascades, e.near, t, this.breaks);
        break;
      case "practical":
        r(this.cascades, e.near, t, 0.5, this.breaks);
        break;
      case "custom":
        this.customSplitsCallback === void 0 && console.error("CSM: Custom split scheme callback not defined."), this.customSplitsCallback(this.cascades, e.near, t, this.breaks);
        break;
    }
    function i(a, o, l, c) {
      for (let h = 1; h < a; h++)
        c.push((o + (l - o) * h / a) / l);
      c.push(1);
    }
    function n(a, o, l, c) {
      for (let h = 1; h < a; h++)
        c.push(o * (l / o) ** (h / a) / l);
      c.push(1);
    }
    function r(a, o, l, c, h) {
      H5.length = 0, V5.length = 0, n(a, o, l, V5), i(a, o, l, H5);
      for (let u = 1; u < a; u++)
        h.push(nt.lerp(H5[u - 1], V5[u - 1], c));
      h.push(1);
    }
  }
  update() {
    const e = this.camera, t = this.frustums;
    W5.lookAt(new _(), this.lightDirection, Lte), xC.copy(W5).invert();
    for (let i = 0; i < t.length; i++) {
      const n = this.lights[i], r = n.shadow.camera, a = (r.right - r.left) / this.shadowMapSize, o = (r.top - r.bottom) / this.shadowMapSize;
      yC.multiplyMatrices(xC, e.matrixWorld), t[i].toSpace(yC, G5);
      const l = G5.vertices.near, c = G5.vertices.far;
      Em.makeEmpty();
      for (let h = 0; h < 4; h++)
        Em.expandByPoint(l[h]), Em.expandByPoint(c[h]);
      Em.getCenter(R0), R0.z = Em.max.z + this.lightMargin, R0.x = Math.floor(R0.x / a) * a, R0.y = Math.floor(R0.y / o) * o, R0.applyMatrix4(W5), n.position.copy(R0), n.target.position.copy(R0), n.target.position.x += this.lightDirection.x, n.target.position.y += this.lightDirection.y, n.target.position.z += this.lightDirection.z;
    }
  }
  injectInclude() {
    ai.lights_fragment_begin = vC.lights_fragment_begin, ai.lights_pars_begin = vC.lights_pars_begin;
  }
  setupMaterial(e) {
    e.defines = e.defines || {}, e.defines.USE_CSM = 1, e.defines.CSM_CASCADES = this.cascades, this.fade && (e.defines.CSM_FADE = "");
    const t = [], i = this, n = this.shaders;
    e.onBeforeCompile = function(r) {
      const a = Math.min(i.camera.far, i.maxFar);
      i.getExtendedBreaks(t), r.uniforms.CSM_cascades = { value: t }, r.uniforms.cameraNear = { value: i.camera.near }, r.uniforms.shadowFar = { value: a }, n.set(e, r);
    }, n.set(e, null);
  }
  updateUniforms() {
    const e = Math.min(this.camera.far, this.maxFar);
    this.shaders.forEach(function(i, n) {
      if (i !== null) {
        const r = i.uniforms;
        this.getExtendedBreaks(r.CSM_cascades.value), r.cameraNear.value = this.camera.near, r.shadowFar.value = e;
      }
      !this.fade && "CSM_FADE" in n.defines ? (delete n.defines.CSM_FADE, n.needsUpdate = !0) : this.fade && !("CSM_FADE" in n.defines) && (n.defines.CSM_FADE = "", n.needsUpdate = !0);
    }, this);
  }
  getExtendedBreaks(e) {
    for (; e.length < this.breaks.length; )
      e.push(new Se());
    e.length = this.breaks.length;
    for (let t = 0; t < this.cascades; t++) {
      const i = this.breaks[t], n = this.breaks[t - 1] || 0;
      e[t].x = n, e[t].y = i;
    }
  }
  updateFrustums() {
    this.getBreaks(), this.initCascades(), this.updateShadowBounds(), this.updateUniforms();
  }
  remove() {
    for (let e = 0; e < this.lights.length; e++)
      this.parent.remove(this.lights[e].target), this.parent.remove(this.lights[e]);
  }
  dispose() {
    const e = this.shaders;
    e.forEach(function(t, i) {
      delete i.onBeforeCompile, delete i.defines.USE_CSM, delete i.defines.CSM_CASCADES, delete i.defines.CSM_FADE, t !== null && (delete t.uniforms.CSM_cascades, delete t.uniforms.cameraNear, delete t.uniforms.shadowFar), i.needsUpdate = !0;
    }), e.clear();
  }
}
const Ote = {
  name: "OutputShader",
  uniforms: {
    tDiffuse: { value: null },
    toneMappingExposure: { value: 1 }
  },
  vertexShader: (
    /* glsl */
    `
		precision highp float;

		uniform mat4 modelViewMatrix;
		uniform mat4 projectionMatrix;

		attribute vec3 position;
		attribute vec2 uv;

		varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `
	
		precision highp float;

		uniform sampler2D tDiffuse;

		#include <tonemapping_pars_fragment>
		#include <colorspace_pars_fragment>

		varying vec2 vUv;

		void main() {

			gl_FragColor = texture2D( tDiffuse, vUv );

			// tone mapping

			#ifdef LINEAR_TONE_MAPPING

				gl_FragColor.rgb = LinearToneMapping( gl_FragColor.rgb );

			#elif defined( REINHARD_TONE_MAPPING )

				gl_FragColor.rgb = ReinhardToneMapping( gl_FragColor.rgb );

			#elif defined( CINEON_TONE_MAPPING )

				gl_FragColor.rgb = OptimizedCineonToneMapping( gl_FragColor.rgb );

			#elif defined( ACES_FILMIC_TONE_MAPPING )

				gl_FragColor.rgb = ACESFilmicToneMapping( gl_FragColor.rgb );

			#elif defined( AGX_TONE_MAPPING )

				gl_FragColor.rgb = AgXToneMapping( gl_FragColor.rgb );

			#elif defined( NEUTRAL_TONE_MAPPING )

				gl_FragColor.rgb = NeutralToneMapping( gl_FragColor.rgb );

			#endif

			// color space

			#ifdef SRGB_TRANSFER

				gl_FragColor = sRGBTransferOETF( gl_FragColor );

			#endif

		}`
  )
};
class Nte extends Ao {
  constructor() {
    super();
    const e = Ote;
    this.uniforms = rs.clone(e.uniforms), this.material = new KM({
      name: e.name,
      uniforms: this.uniforms,
      vertexShader: e.vertexShader,
      fragmentShader: e.fragmentShader
    }), this.fsQuad = new P2(this.material), this._outputColorSpace = null, this._toneMapping = null;
  }
  render(e, t, i) {
    this.uniforms.tDiffuse.value = i.texture, this.uniforms.toneMappingExposure.value = e.toneMappingExposure, (this._outputColorSpace !== e.outputColorSpace || this._toneMapping !== e.toneMapping) && (this._outputColorSpace = e.outputColorSpace, this._toneMapping = e.toneMapping, this.material.defines = {}, bi.getTransfer(this._outputColorSpace) === pn && (this.material.defines.SRGB_TRANSFER = ""), this._toneMapping === mM ? this.material.defines.LINEAR_TONE_MAPPING = "" : this._toneMapping === gM ? this.material.defines.REINHARD_TONE_MAPPING = "" : this._toneMapping === vM ? this.material.defines.CINEON_TONE_MAPPING = "" : this._toneMapping === Hp ? this.material.defines.ACES_FILMIC_TONE_MAPPING = "" : this._toneMapping === yM ? this.material.defines.AGX_TONE_MAPPING = "" : this._toneMapping === xM && (this.material.defines.NEUTRAL_TONE_MAPPING = ""), this.material.needsUpdate = !0), this.renderToScreen === !0 ? (e.setRenderTarget(null), this.fsQuad.render(e)) : (e.setRenderTarget(t), this.clear && e.clear(e.autoClearColor, e.autoClearDepth, e.autoClearStencil), this.fsQuad.render(e));
  }
  dispose() {
    this.material.dispose(), this.fsQuad.dispose();
  }
}
class $4 {
  constructor(e = new _(0, 0, 0), t = new _(0, 1, 0), i = 1) {
    this.start = e, this.end = t, this.radius = i;
  }
  clone() {
    return new $4(this.start.clone(), this.end.clone(), this.radius);
  }
  set(e, t, i) {
    this.start.copy(e), this.end.copy(t), this.radius = i;
  }
  copy(e) {
    this.start.copy(e.start), this.end.copy(e.end), this.radius = e.radius;
  }
  getCenter(e) {
    return e.copy(this.end).add(this.start).multiplyScalar(0.5);
  }
  translate(e) {
    this.start.add(e), this.end.add(e);
  }
  checkAABBAxis(e, t, i, n, r, a, o, l, c) {
    return (r - e < c || r - i < c) && (e - a < c || i - a < c) && (o - t < c || o - n < c) && (t - l < c || n - l < c);
  }
  intersectsBox(e) {
    return this.checkAABBAxis(
      this.start.x,
      this.start.y,
      this.end.x,
      this.end.y,
      e.min.x,
      e.max.x,
      e.min.y,
      e.max.y,
      this.radius
    ) && this.checkAABBAxis(
      this.start.x,
      this.start.z,
      this.end.x,
      this.end.z,
      e.min.x,
      e.max.x,
      e.min.z,
      e.max.z,
      this.radius
    ) && this.checkAABBAxis(
      this.start.y,
      this.start.z,
      this.end.y,
      this.end.z,
      e.min.y,
      e.max.y,
      e.min.z,
      e.max.z,
      this.radius
    );
  }
}
const Tm = new _(), Cm = new _(), Zv = new _(), _m = new _(), Mo = new ln(), X5 = new Qs(), Bte = new Qs(), Kv = new Tn(), Pm = new $4(), Fte = new _(), Ute = new _(), zte = new _(), kte = 1e-10;
function Gte(s, e, t = null, i = null) {
  const n = Fte.copy(s.end).sub(s.start), r = Ute.copy(e.end).sub(e.start), a = zte.copy(e.start).sub(s.start), o = n.dot(r), l = n.dot(n), c = r.dot(r), h = r.dot(a), u = n.dot(a);
  let d, f;
  const p = l * c - o * o;
  if (Math.abs(p) < kte) {
    const v = -h / c, g = (o - h) / c;
    Math.abs(v - 0.5) < Math.abs(g - 0.5) ? (d = 0, f = v) : (d = 1, f = g);
  } else
    d = (h * o + u * c) / p, f = (d * o - h) / c;
  f = Math.max(0, Math.min(1, f)), d = Math.max(0, Math.min(1, d)), t && t.copy(n).multiplyScalar(d).add(s.start), i && i.copy(r).multiplyScalar(f).add(e.start);
}
class NS {
  constructor(e) {
    this.box = e, this.bounds = new zt(), this.subTrees = [], this.triangles = [], this.layers = new e0();
  }
  addTriangle(e) {
    return this.bounds.min.x = Math.min(this.bounds.min.x, e.a.x, e.b.x, e.c.x), this.bounds.min.y = Math.min(this.bounds.min.y, e.a.y, e.b.y, e.c.y), this.bounds.min.z = Math.min(this.bounds.min.z, e.a.z, e.b.z, e.c.z), this.bounds.max.x = Math.max(this.bounds.max.x, e.a.x, e.b.x, e.c.x), this.bounds.max.y = Math.max(this.bounds.max.y, e.a.y, e.b.y, e.c.y), this.bounds.max.z = Math.max(this.bounds.max.z, e.a.z, e.b.z, e.c.z), this.triangles.push(e), this;
  }
  calcBox() {
    return this.box = this.bounds.clone(), this.box.min.x -= 0.01, this.box.min.y -= 0.01, this.box.min.z -= 0.01, this;
  }
  split(e) {
    if (!this.box)
      return;
    const t = [], i = Cm.copy(this.box.max).sub(this.box.min).multiplyScalar(0.5);
    for (let r = 0; r < 2; r++)
      for (let a = 0; a < 2; a++)
        for (let o = 0; o < 2; o++) {
          const l = new zt(), c = Tm.set(r, a, o);
          l.min.copy(this.box.min).add(c.multiply(i)), l.max.copy(l.min).add(i), t.push(new NS(l));
        }
    let n;
    for (; n = this.triangles.pop(); )
      for (let r = 0; r < t.length; r++)
        t[r].box.intersectsTriangle(n) && t[r].triangles.push(n);
    for (let r = 0; r < t.length; r++) {
      const a = t[r].triangles.length;
      a > 8 && e < 16 && t[r].split(e + 1), a !== 0 && this.subTrees.push(t[r]);
    }
    return this;
  }
  build() {
    return this.calcBox(), this.split(0), this;
  }
  getRayTriangles(e, t) {
    for (let i = 0; i < this.subTrees.length; i++) {
      const n = this.subTrees[i];
      if (e.intersectsBox(n.box))
        if (n.triangles.length > 0)
          for (let r = 0; r < n.triangles.length; r++)
            t.indexOf(n.triangles[r]) === -1 && t.push(n.triangles[r]);
        else
          n.getRayTriangles(e, t);
    }
    return t;
  }
  triangleCapsuleIntersect(e, t) {
    t.getPlane(Mo);
    const i = Mo.distanceToPoint(e.start) - e.radius, n = Mo.distanceToPoint(e.end) - e.radius;
    if (i > 0 && n > 0 || i < -e.radius && n < -e.radius)
      return !1;
    const r = Math.abs(i / (Math.abs(i) + Math.abs(n))), a = Tm.copy(e.start).lerp(e.end, r);
    if (t.containsPoint(a))
      return { normal: Mo.normal.clone(), point: a.clone(), depth: Math.abs(Math.min(i, n)) };
    const o = e.radius * e.radius, l = X5.set(e.start, e.end), c = [
      [t.a, t.b],
      [t.b, t.c],
      [t.c, t.a]
    ];
    for (let h = 0; h < c.length; h++) {
      const u = Bte.set(c[h][0], c[h][1]);
      if (Gte(l, u, Zv, _m), Zv.distanceToSquared(_m) < o)
        return {
          normal: Zv.clone().sub(_m).normalize(),
          point: _m.clone(),
          depth: e.radius - Zv.distanceTo(_m)
        };
    }
    return !1;
  }
  triangleSphereIntersect(e, t) {
    if (t.getPlane(Mo), !e.intersectsPlane(Mo))
      return !1;
    const i = Math.abs(Mo.distanceToSphere(e)), n = e.radius * e.radius - i * i, r = Mo.projectPoint(e.center, Tm);
    if (t.containsPoint(e.center))
      return { normal: Mo.normal.clone(), point: r.clone(), depth: Math.abs(Mo.distanceToSphere(e)) };
    const a = [
      [t.a, t.b],
      [t.b, t.c],
      [t.c, t.a]
    ];
    for (let o = 0; o < a.length; o++) {
      X5.set(a[o][0], a[o][1]), X5.closestPointToPoint(r, !0, Cm);
      const l = Cm.distanceToSquared(e.center);
      if (l < n)
        return { normal: e.center.clone().sub(Cm).normalize(), point: Cm.clone(), depth: e.radius - Math.sqrt(l) };
    }
    return !1;
  }
  getSphereTriangles(e, t) {
    for (let i = 0; i < this.subTrees.length; i++) {
      const n = this.subTrees[i];
      if (e.intersectsBox(n.box))
        if (n.triangles.length > 0)
          for (let r = 0; r < n.triangles.length; r++)
            t.indexOf(n.triangles[r]) === -1 && t.push(n.triangles[r]);
        else
          n.getSphereTriangles(e, t);
    }
  }
  getCapsuleTriangles(e, t) {
    for (let i = 0; i < this.subTrees.length; i++) {
      const n = this.subTrees[i];
      if (e.intersectsBox(n.box))
        if (n.triangles.length > 0)
          for (let r = 0; r < n.triangles.length; r++)
            t.indexOf(n.triangles[r]) === -1 && t.push(n.triangles[r]);
        else
          n.getCapsuleTriangles(e, t);
    }
  }
  sphereIntersect(e) {
    Kv.copy(e);
    const t = [];
    let i, n = !1;
    this.getSphereTriangles(e, t);
    for (let r = 0; r < t.length; r++)
      (i = this.triangleSphereIntersect(Kv, t[r])) && (n = !0, Kv.center.add(i.normal.multiplyScalar(i.depth)));
    if (n) {
      const r = Kv.center.clone().sub(e.center), a = r.length();
      return { normal: r.normalize(), depth: a };
    }
    return !1;
  }
  capsuleIntersect(e) {
    Pm.copy(e);
    const t = [];
    let i, n = !1;
    this.getCapsuleTriangles(Pm, t);
    for (let r = 0; r < t.length; r++)
      (i = this.triangleCapsuleIntersect(Pm, t[r])) && (n = !0, Pm.translate(i.normal.multiplyScalar(i.depth)));
    if (n) {
      const r = Pm.getCenter(new _()).sub(e.getCenter(Tm)), a = r.length();
      return { normal: r.normalize(), depth: a };
    }
    return !1;
  }
  rayIntersect(e) {
    if (e.direction.length() === 0)
      return;
    const t = [];
    let i, n, r = 1e100;
    this.getRayTriangles(e, t);
    for (let a = 0; a < t.length; a++) {
      const o = e.intersectTriangle(t[a].a, t[a].b, t[a].c, !0, Tm);
      if (o) {
        const l = o.sub(e.origin).length();
        r > l && (n = o.clone().add(e.origin), r = l, i = t[a]);
      }
    }
    return r < 1e100 ? { distance: r, triangle: i, position: n } : !1;
  }
  fromGraphNode(e) {
    return e.updateWorldMatrix(!0, !0), e.traverse((t) => {
      if (t.isMesh === !0 && this.layers.test(t.layers)) {
        let i, n = !1;
        t.geometry.index !== null ? (n = !0, i = t.geometry.toNonIndexed()) : i = t.geometry;
        const r = i.getAttribute("position");
        for (let a = 0; a < r.count; a += 3) {
          const o = new _().fromBufferAttribute(r, a), l = new _().fromBufferAttribute(r, a + 1), c = new _().fromBufferAttribute(r, a + 2);
          o.applyMatrix4(t.matrixWorld), l.applyMatrix4(t.matrixWorld), c.applyMatrix4(t.matrixWorld), this.addTriangle(new Wo(o, l, c));
        }
        n && i.dispose();
      }
    }), this.build(), this;
  }
  clear() {
    return this.box = null, this.bounds.makeEmpty(), this.subTrees.length = 0, this.triangles.length = 0, this;
  }
}
const _h = new lr(), Dr = new _(), Wl = new _(), Dn = new St(), AC = {
  X: new _(1, 0, 0),
  Y: new _(0, 1, 0),
  Z: new _(0, 0, 1)
}, j5 = {
  type: "change"
}, bC = {
  type: "mouseDown"
}, MC = {
  type: "mouseUp",
  mode: null
}, SC = {
  type: "objectChange"
};
let BS = class extends mt {
  constructor(e, t) {
    super(), t === void 0 && (console.warn('THREE.TransformControls: The second parameter "domElement" is now mandatory.'), t = document), this.isTransformControls = !0, this.visible = !1, this.domElement = t, this.domElement.style.touchAction = "none";
    const i = new qte();
    this._gizmo = i, this.add(i);
    const n = new Yte();
    this._plane = n, this.add(n);
    const r = this;
    function a(x, y) {
      let A = y;
      Object.defineProperty(r, x, {
        get: function() {
          return A !== void 0 ? A : y;
        },
        set: function(b) {
          A !== b && (A = b, n[x] = b, i[x] = b, r.dispatchEvent({
            type: x + "-changed",
            value: b
          }), r.dispatchEvent(j5));
        }
      }), r[x] = y, n[x] = y, i[x] = y;
    }
    a("camera", e), a("object", void 0), a("enabled", !0), a("axis", null), a("mode", "translate"), a("translationSnap", null), a("rotationSnap", null), a("scaleSnap", null), a("space", "world"), a("size", 1), a("dragging", !1), a("showX", !0), a("showY", !0), a("showZ", !0);
    const o = new _(), l = new _(), c = new St(), h = new St(), u = new _(), d = new St(), f = new _(), p = new _(), v = new _(), g = 0, m = new _();
    a("worldPosition", o), a("worldPositionStart", l), a("worldQuaternion", c), a("worldQuaternionStart", h), a("cameraPosition", u), a("cameraQuaternion", d), a("pointStart", f), a("pointEnd", p), a("rotationAxis", v), a("rotationAngle", g), a("eye", m), this._offset = new _(), this._startNorm = new _(), this._endNorm = new _(), this._cameraScale = new _(), this._parentPosition = new _(), this._parentQuaternion = new St(), this._parentQuaternionInv = new St(), this._parentScale = new _(), this._worldScaleStart = new _(), this._worldQuaternionInv = new St(), this._worldScale = new _(), this._positionStart = new _(), this._quaternionStart = new St(), this._scaleStart = new _(), this._getPointer = Hte.bind(this), this._onPointerDown = Wte.bind(this), this._onPointerHover = Vte.bind(this), this._onPointerMove = Xte.bind(this), this._onPointerUp = jte.bind(this), this.domElement.addEventListener("pointerdown", this._onPointerDown), this.domElement.addEventListener("pointermove", this._onPointerHover), this.domElement.addEventListener("pointerup", this._onPointerUp);
  }
  // updateMatrixWorld  updates key transformation variables
  setDomElement(e) {
    this.domElement.removeEventListener("pointerdown", this._onPointerDown), this.domElement.removeEventListener("pointermove", this._onPointerHover), this.domElement.removeEventListener("pointerup", this._onPointerUp), e.addEventListener("pointerdown", this._onPointerDown), e.addEventListener("pointermove", this._onPointerHover), e.addEventListener("pointerup", this._onPointerUp), this.domElement = e;
  }
  updateMatrixWorld() {
    this.object !== void 0 && (this.object.updateMatrixWorld(), this.object.parent === null ? console.error(
      "TransformControls: The attached 3D object must be a part of the scene graph."
    ) : this.object.parent.matrixWorld.decompose(
      this._parentPosition,
      this._parentQuaternion,
      this._parentScale
    ), this.object.matrixWorld.decompose(this.worldPosition, this.worldQuaternion, this._worldScale), this._parentQuaternionInv.copy(this._parentQuaternion).invert(), this._worldQuaternionInv.copy(this.worldQuaternion).invert()), this.camera.updateMatrixWorld(), this.camera.matrixWorld.decompose(
      this.cameraPosition,
      this.cameraQuaternion,
      this._cameraScale
    ), this.camera.isOrthographicCamera ? this.camera.getWorldDirection(this.eye).negate() : this.eye.copy(this.cameraPosition).sub(this.worldPosition).normalize(), super.updateMatrixWorld(this);
  }
  pointerHover(e) {
    if (this.object === void 0 || this.dragging === !0)
      return;
    _h.setFromCamera(e, this.camera);
    const t = q5(this._gizmo.picker[this.mode], _h);
    t ? this.axis = t.object.name : this.axis = null;
  }
  pointerDown(e) {
    if (!(this.object === void 0 || this.dragging === !0 || e.button !== 0) && this.axis !== null) {
      _h.setFromCamera(e, this.camera);
      const t = q5(this._plane, _h, !0);
      t && (this.object.updateMatrixWorld(), this.object.parent.updateMatrixWorld(), this._positionStart.copy(this.object.position), this._quaternionStart.copy(this.object.quaternion), this._scaleStart.copy(this.object.scale), this.object.matrixWorld.decompose(
        this.worldPositionStart,
        this.worldQuaternionStart,
        this._worldScaleStart
      ), this.pointStart.copy(t.point).sub(this.worldPositionStart)), this.dragging = !0, bC.mode = this.mode, this.dispatchEvent(bC);
    }
  }
  pointerMove(e) {
    const t = this.axis, i = this.mode, n = this.object;
    let r = this.space;
    if (i === "scale" ? r = "local" : (t === "E" || t === "XYZE" || t === "XYZ") && (r = "world"), n === void 0 || t === null || this.dragging === !1 || e.button !== -1)
      return;
    _h.setFromCamera(e, this.camera);
    const a = q5(this._plane, _h, !0);
    if (a) {
      if (this.pointEnd.copy(a.point).sub(this.worldPositionStart), i === "translate")
        this._offset.copy(this.pointEnd).sub(this.pointStart), r === "local" && t !== "XYZ" && this._offset.applyQuaternion(this._worldQuaternionInv), t.indexOf("X") === -1 && (this._offset.x = 0), t.indexOf("Y") === -1 && (this._offset.y = 0), t.indexOf("Z") === -1 && (this._offset.z = 0), r === "local" && t !== "XYZ" ? this._offset.applyQuaternion(this._quaternionStart).divide(this._parentScale) : this._offset.applyQuaternion(this._parentQuaternionInv).divide(this._parentScale), n.position.copy(this._offset).add(this._positionStart), this.translationSnap && (r === "local" && (n.position.applyQuaternion(Dn.copy(this._quaternionStart).invert()), t.search("X") !== -1 && (n.position.x = Math.round(n.position.x / this.translationSnap) * this.translationSnap), t.search("Y") !== -1 && (n.position.y = Math.round(n.position.y / this.translationSnap) * this.translationSnap), t.search("Z") !== -1 && (n.position.z = Math.round(n.position.z / this.translationSnap) * this.translationSnap), n.position.applyQuaternion(this._quaternionStart)), r === "world" && (n.parent && n.position.add(Dr.setFromMatrixPosition(n.parent.matrixWorld)), t.search("X") !== -1 && (n.position.x = Math.round(n.position.x / this.translationSnap) * this.translationSnap), t.search("Y") !== -1 && (n.position.y = Math.round(n.position.y / this.translationSnap) * this.translationSnap), t.search("Z") !== -1 && (n.position.z = Math.round(n.position.z / this.translationSnap) * this.translationSnap), n.parent && n.position.sub(Dr.setFromMatrixPosition(n.parent.matrixWorld))));
      else if (i === "scale") {
        if (t.search("XYZ") !== -1) {
          let o = this.pointEnd.length() / this.pointStart.length();
          this.pointEnd.dot(this.pointStart) < 0 && (o *= -1), Wl.set(o, o, o);
        } else
          Dr.copy(this.pointStart), Wl.copy(this.pointEnd), Dr.applyQuaternion(this._worldQuaternionInv), Wl.applyQuaternion(this._worldQuaternionInv), Wl.divide(Dr), t.search("X") === -1 && (Wl.x = 1), t.search("Y") === -1 && (Wl.y = 1), t.search("Z") === -1 && (Wl.z = 1);
        n.scale.copy(this._scaleStart).multiply(Wl), this.scaleSnap && (t.search("X") !== -1 && (n.scale.x = Math.round(n.scale.x / this.scaleSnap) * this.scaleSnap || this.scaleSnap), t.search("Y") !== -1 && (n.scale.y = Math.round(n.scale.y / this.scaleSnap) * this.scaleSnap || this.scaleSnap), t.search("Z") !== -1 && (n.scale.z = Math.round(n.scale.z / this.scaleSnap) * this.scaleSnap || this.scaleSnap));
      } else if (i === "rotate") {
        this._offset.copy(this.pointEnd).sub(this.pointStart);
        const o = 20 / this.worldPosition.distanceTo(Dr.setFromMatrixPosition(this.camera.matrixWorld));
        t === "E" ? (this.rotationAxis.copy(this.eye), this.rotationAngle = this.pointEnd.angleTo(this.pointStart), this._startNorm.copy(this.pointStart).normalize(), this._endNorm.copy(this.pointEnd).normalize(), this.rotationAngle *= this._endNorm.cross(this._startNorm).dot(this.eye) < 0 ? 1 : -1) : t === "XYZE" ? (this.rotationAxis.copy(this._offset).cross(this.eye).normalize(), this.rotationAngle = this._offset.dot(Dr.copy(this.rotationAxis).cross(this.eye)) * o) : (t === "X" || t === "Y" || t === "Z") && (this.rotationAxis.copy(AC[t]), Dr.copy(AC[t]), r === "local" && Dr.applyQuaternion(this.worldQuaternion), this.rotationAngle = this._offset.dot(Dr.cross(this.eye).normalize()) * o), this.rotationSnap && (this.rotationAngle = Math.round(this.rotationAngle / this.rotationSnap) * this.rotationSnap), r === "local" && t !== "E" && t !== "XYZE" ? (n.quaternion.copy(this._quaternionStart), n.quaternion.multiply(Dn.setFromAxisAngle(this.rotationAxis, this.rotationAngle)).normalize()) : (this.rotationAxis.applyQuaternion(this._parentQuaternionInv), n.quaternion.copy(
          Dn.setFromAxisAngle(this.rotationAxis, this.rotationAngle)
        ), n.quaternion.multiply(this._quaternionStart).normalize());
      }
      this.dispatchEvent(j5), this.dispatchEvent(SC);
    }
  }
  pointerUp(e) {
    e.button === 0 && (this.dragging && this.axis !== null && (MC.mode = this.mode, this.dispatchEvent(MC)), this.dragging = !1, this.axis = null);
  }
  dispose() {
    this.domElement.removeEventListener("pointerdown", this._onPointerDown), this.domElement.removeEventListener("pointermove", this._onPointerHover), this.domElement.removeEventListener("pointermove", this._onPointerMove), this.domElement.removeEventListener("pointerup", this._onPointerUp), this.traverse(function(e) {
      e.geometry && e.geometry.dispose(), e.material && e.material.dispose();
    });
  }
  // Set current object
  attach(e) {
    return this.object = e, this.visible = !0, this;
  }
  // Detach from object
  detach() {
    return this.object = void 0, this.visible = !1, this.axis = null, this;
  }
  reset() {
    this.enabled && this.dragging && (this.object.position.copy(this._positionStart), this.object.quaternion.copy(this._quaternionStart), this.object.scale.copy(this._scaleStart), this.dispatchEvent(j5), this.dispatchEvent(SC), this.pointStart.copy(this.pointEnd));
  }
  getRaycaster() {
    return _h;
  }
  // TODO: deprecate
  getMode() {
    return this.mode;
  }
  setMode(e) {
    this.mode = e;
  }
  setTranslationSnap(e) {
    this.translationSnap = e;
  }
  setRotationSnap(e) {
    this.rotationSnap = e;
  }
  setScaleSnap(e) {
    this.scaleSnap = e;
  }
  setSize(e) {
    this.size = e;
  }
  setSpace(e) {
    this.space = e;
  }
};
function Hte(s) {
  if (this.domElement.ownerDocument.pointerLockElement)
    return {
      x: 0,
      y: 0,
      button: s.button
    };
  {
    const e = this.domElement.getBoundingClientRect();
    return {
      x: (s.clientX - e.left) / e.width * 2 - 1,
      y: -(s.clientY - e.top) / e.height * 2 + 1,
      button: s.button
    };
  }
}
function Vte(s) {
  if (this.enabled)
    switch (s.pointerType) {
      case "mouse":
      case "pen":
        this.pointerHover(this._getPointer(s));
        break;
    }
}
function Wte(s) {
  this.enabled && (document.pointerLockElement || this.domElement.setPointerCapture(s.pointerId), this.domElement.addEventListener("pointermove", this._onPointerMove), this.pointerHover(this._getPointer(s)), this.pointerDown(this._getPointer(s)));
}
function Xte(s) {
  this.enabled && this.pointerMove(this._getPointer(s));
}
function jte(s) {
  this.enabled && (this.domElement.releasePointerCapture(s.pointerId), this.domElement.removeEventListener("pointermove", this._onPointerMove), this.pointerUp(this._getPointer(s)));
}
function q5(s, e, t) {
  const i = e.intersectObject(s, !0);
  for (let n = 0; n < i.length; n++)
    if (i[n].object.visible || t)
      return i[n];
  return !1;
}
const Jv = new Mi(), mn = new _(0, 1, 0), wC = new _(0, 0, 0), EC = new et(), Qv = new St(), Ny = new St(), So = new _(), TC = new et(), o1 = new _(1, 0, 0), Yh = new _(0, 1, 0), l1 = new _(0, 0, 1), $v = new _(), Rm = new _(), Dm = new _();
let qte = class extends mt {
  constructor() {
    super(), this.isTransformControlsGizmo = !0, this.type = "TransformControlsGizmo";
    const e = new kt({
      depthTest: !1,
      depthWrite: !1,
      fog: !1,
      toneMapped: !1,
      transparent: !0
    }), t = new Bi({
      depthTest: !1,
      depthWrite: !1,
      fog: !1,
      toneMapped: !1,
      transparent: !0
    }), i = e.clone();
    i.opacity = 0.15;
    const n = t.clone();
    n.opacity = 0.5;
    const r = e.clone();
    r.color.setHex(16711680);
    const a = e.clone();
    a.color.setHex(65280);
    const o = e.clone();
    o.color.setHex(255);
    const l = e.clone();
    l.color.setHex(16711680), l.opacity = 0.5;
    const c = e.clone();
    c.color.setHex(65280), c.opacity = 0.5;
    const h = e.clone();
    h.color.setHex(255), h.opacity = 0.5;
    const u = e.clone();
    u.opacity = 0.25;
    const d = e.clone();
    d.color.setHex(16776960), d.opacity = 0.25, e.clone().color.setHex(16776960);
    const p = e.clone();
    p.color.setHex(7895160);
    const v = new Ei(0, 0.04, 0.1, 12);
    v.translate(0, 0.05, 0);
    const g = new oi(0.08, 0.08, 0.08);
    g.translate(0, 0.04, 0);
    const m = new Pt();
    m.setAttribute("position", new gt([0, 0, 0, 1, 0, 0], 3));
    const x = new Ei(75e-4, 75e-4, 0.5, 3);
    x.translate(0, 0.25, 0);
    function y(H, U) {
      const W = new nr(H, 75e-4, 3, 64, U * Math.PI * 2);
      return W.rotateY(Math.PI / 2), W.rotateX(Math.PI / 2), W;
    }
    function A() {
      const H = new Pt();
      return H.setAttribute("position", new gt([0, 0, 0, 1, 1, 1], 3)), H;
    }
    const b = {
      X: [
        [new Re(v, r), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(v, r), [-0.5, 0, 0], [0, 0, Math.PI / 2]],
        [new Re(x, r), [0, 0, 0], [0, 0, -Math.PI / 2]]
      ],
      Y: [
        [new Re(v, a), [0, 0.5, 0]],
        [new Re(v, a), [0, -0.5, 0], [Math.PI, 0, 0]],
        [new Re(x, a)]
      ],
      Z: [
        [new Re(v, o), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
        [new Re(v, o), [0, 0, -0.5], [-Math.PI / 2, 0, 0]],
        [new Re(x, o), null, [Math.PI / 2, 0, 0]]
      ],
      XYZ: [
        [
          new Re(new Nr(0.1, 0), u.clone()),
          [0, 0, 0]
        ]
      ],
      XY: [
        [
          new Re(new oi(0.15, 0.15, 0.01), h.clone()),
          [0.15, 0.15, 0]
        ]
      ],
      YZ: [
        [
          new Re(new oi(0.15, 0.15, 0.01), l.clone()),
          [0, 0.15, 0.15],
          [0, Math.PI / 2, 0]
        ]
      ],
      XZ: [
        [
          new Re(new oi(0.15, 0.15, 0.01), c.clone()),
          [0.15, 0, 0.15],
          [-Math.PI / 2, 0, 0]
        ]
      ]
    }, M = {
      X: [
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0.3, 0, 0],
          [0, 0, -Math.PI / 2]
        ],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [-0.3, 0, 0],
          [0, 0, Math.PI / 2]
        ]
      ],
      Y: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0.3, 0]],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, -0.3, 0],
          [0, 0, Math.PI]
        ]
      ],
      Z: [
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, 0, 0.3],
          [Math.PI / 2, 0, 0]
        ],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, 0, -0.3],
          [-Math.PI / 2, 0, 0]
        ]
      ],
      XYZ: [[new Re(new Nr(0.2, 0), i)]],
      XY: [[new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0.15, 0]]],
      YZ: [
        [
          new Re(new oi(0.2, 0.2, 0.01), i),
          [0, 0.15, 0.15],
          [0, Math.PI / 2, 0]
        ]
      ],
      XZ: [
        [
          new Re(new oi(0.2, 0.2, 0.01), i),
          [0.15, 0, 0.15],
          [-Math.PI / 2, 0, 0]
        ]
      ]
    }, w = {
      START: [
        [
          new Re(new Nr(0.01, 2), n),
          null,
          null,
          null,
          "helper"
        ]
      ],
      END: [
        [
          new Re(new Nr(0.01, 2), n),
          null,
          null,
          null,
          "helper"
        ]
      ],
      DELTA: [[new ti(A(), n), null, null, null, "helper"]],
      X: [
        [
          new ti(m, n.clone()),
          [-1e3, 0, 0],
          null,
          [1e6, 1, 1],
          "helper"
        ]
      ],
      Y: [
        [
          new ti(m, n.clone()),
          [0, -1e3, 0],
          [0, 0, Math.PI / 2],
          [1e6, 1, 1],
          "helper"
        ]
      ],
      Z: [
        [
          new ti(m, n.clone()),
          [0, 0, -1e3],
          [0, -Math.PI / 2, 0],
          [1e6, 1, 1],
          "helper"
        ]
      ]
    }, C = {
      XYZE: [[new Re(y(0.5, 1), p), null, [0, Math.PI / 2, 0]]],
      X: [[new Re(y(0.5, 0.5), r)]],
      Y: [[new Re(y(0.5, 0.5), a), null, [0, 0, -Math.PI / 2]]],
      Z: [[new Re(y(0.5, 0.5), o), null, [0, Math.PI / 2, 0]]],
      E: [
        [new Re(y(0.75, 1), d), null, [0, Math.PI / 2, 0]]
      ]
    }, T = {
      AXIS: [
        [
          new ti(m, n.clone()),
          [-1e3, 0, 0],
          null,
          [1e6, 1, 1],
          "helper"
        ]
      ]
    }, E = {
      XYZE: [[new Re(new Hs(0.25, 10, 8), i)]],
      X: [
        [
          new Re(new nr(0.5, 0.1, 4, 24), i),
          [0, 0, 0],
          [0, -Math.PI / 2, -Math.PI / 2]
        ]
      ],
      Y: [
        [
          new Re(new nr(0.5, 0.1, 4, 24), i),
          [0, 0, 0],
          [Math.PI / 2, 0, 0]
        ]
      ],
      Z: [
        [
          new Re(new nr(0.5, 0.1, 4, 24), i),
          [0, 0, 0],
          [0, 0, -Math.PI / 2]
        ]
      ],
      E: [[new Re(new nr(0.75, 0.1, 2, 24), i)]]
    }, P = {
      X: [
        [new Re(g, r), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(x, r), [0, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(g, r), [-0.5, 0, 0], [0, 0, Math.PI / 2]]
      ],
      Y: [
        [new Re(g, a), [0, 0.5, 0]],
        [new Re(x, a)],
        [new Re(g, a), [0, -0.5, 0], [0, 0, Math.PI]]
      ],
      Z: [
        [new Re(g, o), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
        [new Re(x, o), [0, 0, 0], [Math.PI / 2, 0, 0]],
        [new Re(g, o), [0, 0, -0.5], [-Math.PI / 2, 0, 0]]
      ],
      XY: [
        [
          new Re(new oi(0.15, 0.15, 0.01), h),
          [0.15, 0.15, 0]
        ]
      ],
      YZ: [
        [
          new Re(new oi(0.15, 0.15, 0.01), l),
          [0, 0.15, 0.15],
          [0, Math.PI / 2, 0]
        ]
      ],
      XZ: [
        [
          new Re(new oi(0.15, 0.15, 0.01), c),
          [0.15, 0, 0.15],
          [-Math.PI / 2, 0, 0]
        ]
      ],
      XYZ: [[new Re(new oi(0.1, 0.1, 0.1), u.clone())]]
    }, L = {
      X: [
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0.3, 0, 0],
          [0, 0, -Math.PI / 2]
        ],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [-0.3, 0, 0],
          [0, 0, Math.PI / 2]
        ]
      ],
      Y: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0.3, 0]],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, -0.3, 0],
          [0, 0, Math.PI]
        ]
      ],
      Z: [
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, 0, 0.3],
          [Math.PI / 2, 0, 0]
        ],
        [
          new Re(new Ei(0.2, 0, 0.6, 4), i),
          [0, 0, -0.3],
          [-Math.PI / 2, 0, 0]
        ]
      ],
      XY: [[new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0.15, 0]]],
      YZ: [
        [
          new Re(new oi(0.2, 0.2, 0.01), i),
          [0, 0.15, 0.15],
          [0, Math.PI / 2, 0]
        ]
      ],
      XZ: [
        [
          new Re(new oi(0.2, 0.2, 0.01), i),
          [0.15, 0, 0.15],
          [-Math.PI / 2, 0, 0]
        ]
      ],
      XYZ: [[new Re(new oi(0.2, 0.2, 0.2), i), [0, 0, 0]]]
    }, I = {
      X: [
        [
          new ti(m, n.clone()),
          [-1e3, 0, 0],
          null,
          [1e6, 1, 1],
          "helper"
        ]
      ],
      Y: [
        [
          new ti(m, n.clone()),
          [0, -1e3, 0],
          [0, 0, Math.PI / 2],
          [1e6, 1, 1],
          "helper"
        ]
      ],
      Z: [
        [
          new ti(m, n.clone()),
          [0, 0, -1e3],
          [0, -Math.PI / 2, 0],
          [1e6, 1, 1],
          "helper"
        ]
      ]
    };
    function N(H) {
      const U = new mt();
      for (const W in H)
        for (let X = H[W].length; X--; ) {
          const K = H[W][X][0].clone(), te = H[W][X][1], re = H[W][X][2], be = H[W][X][3], Pe = H[W][X][4];
          K.name = W, K.tag = Pe, te && K.position.set(te[0], te[1], te[2]), re && K.rotation.set(re[0], re[1], re[2]), be && K.scale.set(be[0], be[1], be[2]), K.updateMatrix();
          const J = K.geometry.clone();
          J.applyMatrix4(K.matrix), K.geometry = J, K.renderOrder = 1 / 0, K.position.set(0, 0, 0), K.rotation.set(0, 0, 0), K.scale.set(1, 1, 1), U.add(K);
        }
      return U;
    }
    this.gizmo = {}, this.picker = {}, this.helper = {}, this.add(this.gizmo.translate = N(b)), this.add(this.gizmo.rotate = N(C)), this.add(this.gizmo.scale = N(P)), this.add(this.picker.translate = N(M)), this.add(this.picker.rotate = N(E)), this.add(this.picker.scale = N(L)), this.add(this.helper.translate = N(w)), this.add(this.helper.rotate = N(T)), this.add(this.helper.scale = N(I)), this.picker.translate.visible = !1, this.picker.rotate.visible = !1, this.picker.scale.visible = !1;
  }
  // updateMatrixWorld will update transformations and appearance of individual handles
  updateMatrixWorld(e) {
    const i = (this.mode === "scale" ? "local" : this.space) === "local" ? this.worldQuaternion : Ny;
    this.gizmo.translate.visible = this.mode === "translate", this.gizmo.rotate.visible = this.mode === "rotate", this.gizmo.scale.visible = this.mode === "scale", this.helper.translate.visible = this.mode === "translate", this.helper.rotate.visible = this.mode === "rotate", this.helper.scale.visible = this.mode === "scale";
    let n = [];
    n = n.concat(this.picker[this.mode].children), n = n.concat(this.gizmo[this.mode].children), n = n.concat(this.helper[this.mode].children);
    for (let r = 0; r < n.length; r++) {
      const a = n[r];
      a.visible = !0, a.rotation.set(0, 0, 0), a.position.copy(this.worldPosition);
      let o;
      if (this.camera.isOrthographicCamera ? o = (this.camera.top - this.camera.bottom) / this.camera.zoom : o = this.worldPosition.distanceTo(this.cameraPosition) * Math.min(1.9 * Math.tan(Math.PI * this.camera.fov / 360) / this.camera.zoom, 7), a.scale.set(1, 1, 1).multiplyScalar(o * this.size / 4), a.tag === "helper") {
        a.visible = !1, a.name === "AXIS" ? (a.position.copy(this.worldPositionStart), a.visible = !!this.axis, this.axis === "X" && (Dn.setFromEuler(Jv.set(0, 0, 0)), a.quaternion.copy(i).multiply(Dn), Math.abs(mn.copy(o1).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "Y" && (Dn.setFromEuler(Jv.set(0, 0, Math.PI / 2)), a.quaternion.copy(i).multiply(Dn), Math.abs(mn.copy(Yh).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "Z" && (Dn.setFromEuler(Jv.set(0, Math.PI / 2, 0)), a.quaternion.copy(i).multiply(Dn), Math.abs(mn.copy(l1).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "XYZE" && (Dn.setFromEuler(Jv.set(0, Math.PI / 2, 0)), mn.copy(this.rotationAxis), a.quaternion.setFromRotationMatrix(
          EC.lookAt(wC, mn, Yh)
        ), a.quaternion.multiply(Dn), a.visible = this.dragging), this.axis === "E" && (a.visible = !1)) : a.name === "START" ? (a.position.copy(this.worldPositionStart), a.visible = this.dragging) : a.name === "END" ? (a.position.copy(this.worldPosition), a.visible = this.dragging) : a.name === "DELTA" ? (a.position.copy(this.worldPositionStart), a.quaternion.copy(this.worldQuaternionStart), Dr.set(1e-10, 1e-10, 1e-10).add(this.worldPositionStart).sub(this.worldPosition).multiplyScalar(-1), Dr.applyQuaternion(this.worldQuaternionStart.clone().invert()), a.scale.copy(Dr), a.visible = this.dragging) : (a.quaternion.copy(i), this.dragging ? a.position.copy(this.worldPositionStart) : a.position.copy(this.worldPosition), this.axis && (a.visible = this.axis.search(a.name) !== -1));
        continue;
      }
      a.quaternion.copy(i), this.mode === "translate" || this.mode === "scale" ? (a.name === "X" && Math.abs(mn.copy(o1).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "Y" && Math.abs(mn.copy(Yh).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "Z" && Math.abs(mn.copy(l1).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "XY" && Math.abs(mn.copy(l1).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "YZ" && Math.abs(mn.copy(o1).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "XZ" && Math.abs(mn.copy(Yh).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1)) : this.mode === "rotate" && (Qv.copy(i), mn.copy(this.eye).applyQuaternion(Dn.copy(i).invert()), a.name.search("E") !== -1 && a.quaternion.setFromRotationMatrix(
        EC.lookAt(this.eye, wC, Yh)
      ), a.name === "X" && (Dn.setFromAxisAngle(o1, Math.atan2(-mn.y, mn.z)), Dn.multiplyQuaternions(Qv, Dn), a.quaternion.copy(Dn)), a.name === "Y" && (Dn.setFromAxisAngle(Yh, Math.atan2(mn.x, mn.z)), Dn.multiplyQuaternions(Qv, Dn), a.quaternion.copy(Dn)), a.name === "Z" && (Dn.setFromAxisAngle(l1, Math.atan2(mn.y, mn.x)), Dn.multiplyQuaternions(Qv, Dn), a.quaternion.copy(Dn))), a.visible = a.visible && (a.name.indexOf("X") === -1 || this.showX), a.visible = a.visible && (a.name.indexOf("Y") === -1 || this.showY), a.visible = a.visible && (a.name.indexOf("Z") === -1 || this.showZ), a.visible = a.visible && (a.name.indexOf("E") === -1 || this.showX && this.showY && this.showZ), a.material._color = a.material._color || a.material.color.clone(), a.material._opacity = a.material._opacity || a.material.opacity, a.material.color.copy(a.material._color), a.material.opacity = a.material._opacity, this.enabled && this.axis && (a.name === this.axis || this.axis.split("").some(function(l) {
        return a.name === l;
      })) && (a.material.color.setHex(16776960), a.material.opacity = 1);
    }
    super.updateMatrixWorld(e);
  }
}, Yte = class extends Re {
  constructor() {
    super(
      new $n(1e5, 1e5, 2, 2),
      new kt({
        visible: !1,
        wireframe: !0,
        side: ui,
        transparent: !0,
        opacity: 0.1,
        toneMapped: !1
      })
    ), this.isTransformControlsPlane = !0, this.type = "TransformControlsPlane";
  }
  updateMatrixWorld(e) {
    let t = this.space;
    switch (this.position.copy(this.worldPosition), this.mode === "scale" && (t = "local"), $v.copy(o1).applyQuaternion(t === "local" ? this.worldQuaternion : Ny), Rm.copy(Yh).applyQuaternion(t === "local" ? this.worldQuaternion : Ny), Dm.copy(l1).applyQuaternion(t === "local" ? this.worldQuaternion : Ny), mn.copy(Rm), this.mode) {
      case "translate":
      case "scale":
        switch (this.axis) {
          case "X":
            mn.copy(this.eye).cross($v), So.copy($v).cross(mn);
            break;
          case "Y":
            mn.copy(this.eye).cross(Rm), So.copy(Rm).cross(mn);
            break;
          case "Z":
            mn.copy(this.eye).cross(Dm), So.copy(Dm).cross(mn);
            break;
          case "XY":
            So.copy(Dm);
            break;
          case "YZ":
            So.copy($v);
            break;
          case "XZ":
            mn.copy(Dm), So.copy(Rm);
            break;
          case "XYZ":
          case "E":
            So.set(0, 0, 0);
            break;
        }
        break;
      case "rotate":
      default:
        So.set(0, 0, 0);
    }
    So.length() === 0 ? this.quaternion.copy(this.cameraQuaternion) : (TC.lookAt(Dr.set(0, 0, 0), So, mn), this.quaternion.setFromRotationMatrix(TC)), super.updateMatrixWorld(e);
  }
};
var cN = /* @__PURE__ */ ((s) => (s.practical = "practical", s.uniform = "uniform", s.logarithmic = "logarithmic", s.custom = "custom", s))(cN || {});
class Zte extends mt {
  constructor(t) {
    super();
    S(this, "type", "ParticleEmitter");
    S(this, "system");
    //interleavedBuffer: InterleavedBuffer;
    S(this, "emitterGizmo", new ci());
    this.system = t, this.add(this.emitterGizmo);
  }
  setGizmo(t) {
    this.emitterGizmo.clear(), this.emitterGizmo.add(t);
  }
  clone() {
    const t = this.system.clone();
    return t.emitter.copy(this, !0), t.emitter;
  }
  dispose() {
  }
  // extract data from the cache hash
  // remove metadata on each item
  // and return as array
  extractFromCache(t) {
    const i = [];
    for (const n in t) {
      const r = t[n];
      delete r.metadata, i.push(r);
    }
    return i;
  }
  toJSON(t, i = {}) {
    const n = t === void 0 || typeof t == "string", r = {};
    n && (t = {
      geometries: {},
      materials: {},
      textures: {},
      images: {},
      shapes: {},
      skeletons: {},
      animations: {},
      nodes: {}
    }, r.metadata = {
      version: 4.5,
      type: "Object",
      generator: "Object3D.toJSON"
    });
    const a = {};
    if (a.uuid = this.uuid, a.type = this.type, this.name !== "" && (a.name = this.name), this.castShadow === !0 && (a.castShadow = !0), this.receiveShadow === !0 && (a.receiveShadow = !0), this.visible === !1 && (a.visible = !1), this.frustumCulled === !1 && (a.frustumCulled = !1), this.renderOrder !== 0 && (a.renderOrder = this.renderOrder), JSON.stringify(this.userData) !== "{}" && (a.userData = this.userData), a.layers = this.layers.mask, a.matrix = this.matrix.toArray(), this.matrixAutoUpdate === !1 && (a.matrixAutoUpdate = !1), this.system !== null && (a.ps = this.system.toJSON(t, i)), this.children.length > 0) {
      a.children = [];
      for (let o = 0; o < this.children.length; o++)
        this.children[o].type !== "ParticleSystemPreview" && a.children.push(this.children[o].toJSON(t).object);
    }
    if (n) {
      const o = this.extractFromCache(t.geometries), l = this.extractFromCache(t.materials), c = this.extractFromCache(t.textures), h = this.extractFromCache(t.images);
      o.length > 0 && (r.geometries = o), l.length > 0 && (r.materials = l), c.length > 0 && (r.textures = c), h.length > 0 && (r.images = h);
    }
    return r.object = a, r;
  }
}
class Y5 {
  constructor(e) {
    S(this, "data");
    S(this, "next");
    S(this, "prev");
    this.data = e, this.next = null, this.prev = null;
  }
  hasPrev() {
    return this.prev !== null;
  }
  hasNext() {
    return this.next !== null;
  }
}
class Kte {
  constructor() {
    S(this, "length");
    S(this, "head");
    S(this, "tail");
    this.length = 0, this.head = this.tail = null;
  }
  isEmpty() {
    return this.head === null;
  }
  clear() {
    this.length = 0, this.head = this.tail = null;
  }
  front() {
    return this.head === null ? null : this.head.data;
  }
  back() {
    return this.tail === null ? null : this.tail.data;
  }
  /**
   * remove at head in O(1)
   */
  dequeue() {
    if (this.head) {
      const e = this.head.data;
      return this.head = this.head.next, this.head ? this.head.prev = null : this.tail = null, this.length--, e;
    }
  }
  /**
   * remove at tail in O(1)
   */
  pop() {
    if (this.tail) {
      const e = this.tail.data;
      return this.tail = this.tail.prev, this.tail ? this.tail.next = null : this.head = null, this.length--, e;
    }
  }
  /**
   * add at head in O(1)
   */
  queue(e) {
    const t = new Y5(e);
    this.tail || (this.tail = t), this.head && (this.head.prev = t, t.next = this.head), this.head = t, this.length++;
  }
  /**
   * add at tail in O(1)
   */
  push(e) {
    const t = new Y5(e);
    this.head || (this.head = t), this.tail && (this.tail.next = t, t.prev = this.tail), this.tail = t, this.length++;
  }
  insertBefore(e, t) {
    const i = new Y5(t);
    i.next = e, i.prev = e.prev, i.prev !== null && (i.prev.next = i), i.next.prev = i, e == this.head && (this.head = i), this.length++;
  }
  remove(e) {
    if (this.head === null || this.tail === null)
      return;
    let t = this.head;
    for (e === this.head.data && (this.head = this.head.next), e === this.tail.data && (this.tail = this.tail.prev); t.next !== null && t.data !== e; )
      t = t.next;
    t.data === e && (t.prev !== null && (t.prev.next = t.next), t.next !== null && (t.next.prev = t.prev), this.length--);
  }
  /**
   * Returns an iterator over the values
   */
  *values() {
    let e = this.head;
    for (; e !== null; )
      yield e.data, e = e.next;
  }
}
class Jte {
  constructor() {
    S(this, "parentMatrix");
    // CPU
    S(this, "startSpeed", 0);
    S(this, "startColor", new Yt());
    S(this, "startSize", 1);
    S(this, "position", new _());
    S(this, "velocity", new _());
    S(this, "age", 0);
    S(this, "life", 1);
    S(this, "size", 1);
    S(this, "speedModifier", 1);
    // extra
    S(this, "angularVelocity");
    // GPU
    S(this, "rotation", 0);
    S(this, "color", new Yt());
    S(this, "uvTile", 0);
  }
  get died() {
    return this.age >= this.life;
  }
}
class Qte {
  constructor(e, t, i) {
    this.position = e, this.size = t, this.color = i;
  }
}
class xb {
  constructor() {
    S(this, "parentMatrix");
    S(this, "startSpeed", 0);
    S(this, "startColor", new Yt());
    S(this, "startSize", 1);
    S(this, "position", new _());
    S(this, "localPosition");
    S(this, "velocity", new _());
    S(this, "age", 0);
    S(this, "life", 1);
    S(this, "size", 1);
    S(this, "length", 100);
    S(this, "speedModifier", 1);
    // GPU
    S(this, "color", new Yt());
    // use link list instead
    S(this, "previous", new Kte());
    S(this, "uvTile", 0);
  }
  update() {
    for (this.age <= this.life ? this.previous.push(new Qte(this.position.clone(), this.size, this.color.clone())) : this.previous.length > 0 && this.previous.dequeue(); this.previous.length > this.length; )
      this.previous.dequeue();
  }
  get died() {
    return this.age >= this.life;
  }
  reset() {
    this.previous.clear();
  }
}
class pu {
  constructor(e, t, i, n) {
    S(this, "p");
    this.p = [e, t, i, n];
  }
  genValue(e) {
    const t = e * e, i = e * e * e, n = 1 - e, r = n * n, a = r * n;
    return this.p[0] * a + this.p[1] * r * e * 3 + this.p[2] * n * t * 3 + this.p[3] * i;
  }
  // get the coefficients of the polynomial's derivatives
  derivativeCoefficients(e) {
    const t = [];
    for (let i = e, n = i.length - 1; n > 0; n--) {
      const r = [];
      for (let a = 0; a < n; a++) {
        const o = n * (i[a + 1] - i[a]);
        r.push(o);
      }
      t.push(r), i = r;
    }
    return t;
  }
  // calculate the slope
  getSlope(e) {
    const t = this.derivativeCoefficients(this.p)[0], i = 1 - e, n = i * i, r = i * e * 2, a = e * e;
    return n * t[0] + r * t[1] + a * t[2];
  }
  // derivative(0) = (p[1] - p[0]) * 3
  // derivative(1) = (p[3] - p[2]) * 3
  controlCurve(e, t) {
    this.p[1] = e / 3 + this.p[0], this.p[2] = this.p[3] - t / 3;
  }
  hull(e) {
    let t = this.p, i = [], n, r = 0, a = 0, o = 0;
    const l = [];
    for (l[r++] = t[0], l[r++] = t[1], l[r++] = t[2], l[r++] = t[3]; t.length > 1; ) {
      for (i = [], a = 0, o = t.length - 1; a < o; a++)
        n = e * t[a] + (1 - e) * t[a + 1], l[r++] = n, i.push(n);
      t = i;
    }
    return l;
  }
  split(e) {
    const t = this.hull(e);
    return {
      left: new pu(t[0], t[4], t[7], t[9]),
      right: new pu(t[9], t[8], t[6], t[3]),
      span: t
    };
  }
  clone() {
    return new pu(this.p[0], this.p[1], this.p[2], this.p[3]);
  }
  toJSON() {
    return {
      p0: this.p[0],
      p1: this.p[1],
      p2: this.p[2],
      p3: this.p[3]
    };
  }
  static fromJSON(e) {
    return new pu(e.p0, e.p1, e.p2, e.p3);
  }
}
const ug = (s) => ({ r: s.x, g: s.y, b: s.z, a: s.w }), dg = (s) => new Yt(s.r, s.g, s.b, s.a), $te = (s, e) => {
  switch (e) {
    case "Vector3":
      return new _(s.x, s.y, s.z);
    case "Vector4":
      return new Yt(s.x, s.y, s.z, s.w);
    case "Color":
      return new _(s.r, s.g, s.b);
    case "Number":
      return s;
    default:
      return s;
  }
}, eie = (s, e) => {
  switch (e) {
    case "Vector3":
      return { x: s.x, y: s.y, z: s.z };
    case "Vector4":
      return { x: s.x, y: s.y, z: s.z, w: s.w };
    case "Color":
      return { r: s.x, g: s.y, b: s.z };
    case "Number":
      return s;
    default:
      return s;
  }
};
class ax {
  constructor(e, t) {
    S(this, "type");
    this.a = e, this.b = t, this.type = "value";
  }
  genColor(e) {
    const t = Math.random();
    return e.copy(this.a).lerp(this.b, t);
  }
  toJSON() {
    return {
      type: "RandomColor",
      a: ug(this.a),
      b: ug(this.b)
    };
  }
  static fromJSON(e) {
    return new ax(dg(e.a), dg(e.b));
  }
  clone() {
    return new ax(this.a.clone(), this.b.clone());
  }
}
class fp {
  constructor(e, t) {
    S(this, "type");
    this.a = e, this.b = t, this.type = "value";
  }
  genColor(e, t) {
    return e.copy(this.a).lerp(this.b, Math.random());
  }
  toJSON() {
    return {
      type: "ColorRange",
      a: ug(this.a),
      b: ug(this.b)
    };
  }
  static fromJSON(e) {
    return new fp(dg(e.a), dg(e.b));
  }
  clone() {
    return new fp(this.a.clone(), this.b.clone());
  }
}
class Cc {
  // default linear bezier
  constructor(e, t) {
    S(this, "keys");
    S(this, "type");
    S(this, "subType");
    this.subType = t, this.type = "function", this.keys = e;
  }
  findKey(e) {
    let t = 0, i = 0, n = this.keys.length - 1;
    for (; i + 1 < n; )
      if (t = Math.floor((i + n) / 2), e < this.getStartX(t))
        n = t - 1;
      else if (e > this.getEndX(t))
        i = t + 1;
      else
        return t;
    for (let r = i; r <= n; r++)
      if (e >= this.getStartX(r) && e <= this.getEndX(r))
        return r;
    return -1;
  }
  getStartX(e) {
    return this.keys[e][1];
  }
  getEndX(e) {
    return e + 1 < this.keys.length ? this.keys[e + 1][1] : 1;
  }
  genValue(e, t) {
    const i = this.findKey(t);
    return this.subType === "Number" ? i === -1 ? this.keys[0][0] : i + 1 >= this.keys.length ? this.keys[this.keys.length - 1][0] : (this.keys[i + 1][0] - this.keys[i][0]) * ((t - this.getStartX(i)) / (this.getEndX(i) - this.getStartX(i))) + this.keys[i][0] : i === -1 ? e.copy(this.keys[0][0]) : i + 1 >= this.keys.length ? e.copy(this.keys[this.keys.length - 1][0]) : e.copy(this.keys[i][0]).lerp(
      this.keys[i + 1][0],
      (t - this.getStartX(i)) / (this.getEndX(i) - this.getStartX(i))
    );
  }
  toJSON() {
    return this.keys[0][0].constructor.name, {
      type: "CLinearFunction",
      subType: this.subType,
      keys: this.keys.map(([e, t]) => ({
        value: eie(e, this.subType),
        pos: t
      }))
    };
  }
  static fromJSON(e) {
    return new Cc(
      e.keys.map((t) => [$te(t.value, e.subType), t.pos]),
      e.subType
    );
  }
  clone() {
    return this.subType === "Number" ? new Cc(
      this.keys.map(([e, t]) => [e, t]),
      this.subType
    ) : new Cc(
      this.keys.map(([e, t]) => [e.clone(), t]),
      this.subType
    );
  }
}
const e3 = new _();
class Ic {
  // default linear bezier
  constructor(e = [
    [new _(0, 0, 0), 0],
    [new _(1, 1, 1), 0]
  ], t = [
    [1, 0],
    [1, 1]
  ]) {
    S(this, "type");
    S(this, "color");
    S(this, "alpha");
    this.type = "function", e.sort((i, n) => i[1] - n[1]), t.sort((i, n) => i[1] - n[1]), this.color = new Cc(e, "Color"), this.alpha = new Cc(t, "Number");
  }
  genColor(e, t) {
    return this.color.genValue(e3, t), e.set(e3.x, e3.y, e3.z, this.alpha.genValue(1, t));
  }
  toJSON() {
    return {
      type: "Gradient",
      color: this.color.toJSON(),
      alpha: this.alpha.toJSON()
    };
  }
  static fromJSON(e) {
    if (e.functions) {
      const t = e.functions.map((i) => [
        fp.fromJSON(i.function).a,
        i.start
      ]);
      return e.functions.length > 0 && t.push([fp.fromJSON(e.functions[e.functions.length - 1].function).b, 1]), new Ic(
        t.map((i) => [new _(i[0].x, i[0].y, i[0].z), i[1]]),
        t.map((i) => [i[0].w, i[1]])
      );
    } else {
      const t = new Ic();
      return t.alpha = Cc.fromJSON(e.alpha), t.color = Cc.fromJSON(e.color), t;
    }
  }
  clone() {
    const e = new Ic();
    return e.alpha = this.alpha.clone(), e.color = this.color.clone(), e;
  }
}
const Z5 = new Yt();
class ox {
  constructor(e, t) {
    S(this, "gradient1");
    S(this, "gradient2");
    S(this, "type");
    this.type = "memorizedFunction", this.gradient1 = e, this.gradient2 = t;
  }
  startGen(e) {
    e.rand = Math.random();
  }
  genColor(e, t, i) {
    return this.gradient1.genColor(e, t), this.gradient2.genColor(Z5, t), i && i.rand ? e.lerp(Z5, i.rand) : e.lerp(Z5, Math.random()), e;
  }
  toJSON() {
    return {
      type: "RandomColorBetweenGradient",
      gradient1: this.gradient1.toJSON(),
      gradient2: this.gradient2.toJSON()
    };
  }
  static fromJSON(e) {
    return new ox(
      Ic.fromJSON(e.gradient1),
      Ic.fromJSON(e.gradient2)
    );
  }
  clone() {
    return new ox(
      this.gradient1.clone(),
      this.gradient2.clone()
    );
  }
}
class Vc {
  constructor(e) {
    S(this, "type");
    this.color = e, this.type = "value";
  }
  genColor(e) {
    return e.copy(this.color);
  }
  toJSON() {
    return {
      type: "ConstantColor",
      color: ug(this.color)
    };
  }
  static fromJSON(e) {
    return new Vc(dg(e.color));
  }
  clone() {
    return new Vc(this.color.clone());
  }
}
function FS(s) {
  switch (s.type) {
    case "ConstantColor":
      return Vc.fromJSON(s);
    case "ColorRange":
      return fp.fromJSON(s);
    case "RandomColor":
      return ax.fromJSON(s);
    case "Gradient":
      return Ic.fromJSON(s);
    case "RandomColorBetweenGradient":
      return ox.fromJSON(s);
    default:
      return new Vc(new Yt(1, 1, 1, 1));
  }
}
class Ai {
  constructor(e) {
    S(this, "type");
    this.value = e, this.type = "value";
  }
  genValue() {
    return this.value;
  }
  toJSON() {
    return {
      type: "ConstantValue",
      value: this.value
    };
  }
  static fromJSON(e) {
    return new Ai(e.value);
  }
  clone() {
    return new Ai(this.value);
  }
}
class sr {
  constructor(e, t) {
    S(this, "type");
    this.a = e, this.b = t, this.type = "value";
  }
  genValue() {
    return nt.lerp(this.a, this.b, Math.random());
  }
  toJSON() {
    return {
      type: "IntervalValue",
      a: this.a,
      b: this.b
    };
  }
  static fromJSON(e) {
    return new sr(e.a, e.b);
  }
  clone() {
    return new sr(this.a, this.b);
  }
}
class tie {
  constructor() {
    S(this, "functions");
    this.functions = new Array();
  }
  findFunction(e) {
    let t = 0, i = 0, n = this.functions.length - 1;
    for (; i + 1 < n; )
      if (t = Math.floor((i + n) / 2), e < this.getStartX(t))
        n = t - 1;
      else if (e > this.getEndX(t))
        i = t + 1;
      else
        return t;
    for (let r = i; r <= n; r++)
      if (e >= this.functions[r][1] && e <= this.getEndX(r))
        return r;
    return -1;
  }
  getStartX(e) {
    return this.functions[e][1];
  }
  setStartX(e, t) {
    e > 0 && (this.functions[e][1] = t);
  }
  getEndX(e) {
    return e + 1 < this.functions.length ? this.functions[e + 1][1] : 1;
  }
  setEndX(e, t) {
    e + 1 < this.functions.length && (this.functions[e + 1][1] = t);
  }
  insertFunction(e, t) {
    const i = this.findFunction(e);
    this.functions.splice(i + 1, 0, [t, e]);
  }
  removeFunction(e) {
    return this.functions.splice(e, 1)[0][0];
  }
  getFunction(e) {
    return this.functions[e][0];
  }
  setFunction(e, t) {
    this.functions[e][0] = t;
  }
  get numOfFunctions() {
    return this.functions.length;
  }
}
class fg extends tie {
  // default linear bezier
  constructor(t = [[new pu(0, 1 / 3, 1 / 3 * 2, 1), 0]]) {
    super();
    S(this, "type");
    this.type = "function", this.functions = t;
  }
  genValue(t = 0) {
    const i = this.findFunction(t);
    return i === -1 ? 0 : this.functions[i][0].genValue(
      (t - this.getStartX(i)) / (this.getEndX(i) - this.getStartX(i))
    );
  }
  toSVG(t, i) {
    if (i < 1)
      return "";
    let n = ["M", 0, this.functions[0][0].p[0]].join(" ");
    for (let r = 1 / i; r <= 1; r += 1 / i)
      n = [n, "L", r * t, this.genValue(r)].join(" ");
    return n;
  }
  toJSON() {
    return {
      type: "PiecewiseBezier",
      functions: this.functions.map(([t, i]) => ({
        function: t.toJSON(),
        start: i
      }))
    };
  }
  static fromJSON(t) {
    return new fg(
      t.functions.map((i) => [
        pu.fromJSON(i.function),
        i.start
      ])
    );
  }
  clone() {
    return new fg(this.functions.map(([t, i]) => [t.clone(), i]));
  }
}
const K5 = (s) => Math.abs(s) <= 1e-5, iie = (s, e, t) => s * (1 - t) + e * t, J5 = (s) => JSON.parse(JSON.stringify(s));
var Ab = /* @__PURE__ */ ((s) => (s.None = "None", s.In = "In", s.Out = "Out", s.Both = "Both", s))(Ab || {}), Gf = /* @__PURE__ */ ((s) => (s.Constant = "Constant", s.Linaer = "Linear", s.Cubic = "Cubic", s))(Gf || {}), bb = /* @__PURE__ */ ((s) => (s.Auto = "Auto", s.User = "User", s.Break = "Break", s.None = "None", s))(bb || {});
const nie = (s, e) => (s.weightMode === "None" || s.weightMode === "In") && (e.weightMode === "None" || e.weightMode === "Out"), sie = (s) => s.weightMode == "In" || s.weightMode == "Both", rie = (s) => s.weightMode == "Out" || s.weightMode == "Both";
function aie(s, e, t, i, n) {
  const r = t / i, a = e / i, o = s / i, l = r * r, c = 1 / 3 * (-1 / 3 * l + a), h = 1 / 2 * (2 / 27 * r * l - 1 / 3 * r * a + o), u = c * c * c, d = h * h + u;
  let f = 0;
  if (_C(d)) {
    if (_C(h))
      return n[0] = 0, 1;
    {
      const v = Math.cbrt(-h);
      return n[0] = 2 * v, n[1] = -v, 2;
    }
  } else if (d < 0) {
    const v = 0.3333333333333333 * Math.acos(-h / Math.sqrt(-u)), g = 2 * Math.sqrt(-c);
    n[0] = g * Math.cos(v), n[1] = -g * Math.cos(v + Math.PI / 3), n[2] = -g * Math.cos(v - Math.PI / 3), f = 3;
  } else {
    const v = Math.sqrt(d), g = Math.cbrt(v - h), m = -Math.cbrt(v + h);
    n[0] = g + m, f = 1;
  }
  const p = 1 / 3 * r;
  for (let v = 0; v < f; ++v)
    n[v] -= p;
  return f;
}
const CC = 1e-9;
function _C(s) {
  return s > -CC && s < CC;
}
class oie {
  constructor() {
    S(this, "listeners", []);
    S(this, "active", !0);
    S(this, "on", (e) => {
      this.listeners.push(e);
    });
    S(this, "addListener", this.on);
    S(this, "emit", (...e) => {
      if (this.active)
        for (const t of this.listeners)
          t.apply(this, e);
    });
    S(this, "off", (e) => {
      for (let t = 0; t < this.listeners.length; t++)
        e === this.listeners[t] && this.listeners.splice(t, 1);
    });
    S(this, "removeListener", this.off);
    S(this, "removeAllListeners", () => {
      this.listeners = [];
    });
    S(this, "once", (e) => {
      const t = (...i) => {
        this.off(t), e.apply(this, i);
      };
      this.on(t);
    });
  }
}
var Mb = /* @__PURE__ */ ((s) => (s.Linaer = "Linaer", s.Constant = "Constant", s.Repeat = "Repeat", s.RepeatWithOffset = "RepeatWithOffset", s.PingPong = "PingPong", s))(Mb || {});
class Xo {
  constructor() {
    S(this, "type", "function");
    S(this, "keys", []);
    S(this, "preExtrapolation", "Constant");
    S(this, "postExtrapolation", "Constant");
    S(this, "signals", {
      curveChanged: new oie()
    });
    S(this, "addKey", (e) => {
      let t = 0;
      for (; t < this.keys.length && e.time > this.keys[t].time; t++)
        ;
      return this.keys.splice(t, 0, e), t;
    });
    S(this, "getKey", (e) => this.keys[e]);
    /** remove key at index, then add key into keys and sort them. */
    S(this, "moveKey", (e, t) => {
      this.keys.splice(e, 1), this.addKey(t), this.signals.curveChanged.emit();
    });
    S(this, "removeKey", (e) => {
      this.keys.splice(e, 1), this.signals.curveChanged.emit();
    });
    S(this, "setKeyTangentMode", (e, t) => {
      this.keys[e].tangentMode, this.signals.curveChanged.emit();
    });
    S(this, "genValue", (e) => {
      const [i, n] = this.RemapTimeValue(e, 0);
      let r = 0;
      const a = this.length;
      if (a === 0)
        r = 0;
      else if (a < 2 || i < this.keys[0].time)
        if (a > 1 && this.preExtrapolation === "Linaer") {
          const o = this.keys[1].time - this.keys[0].time;
          K5(o) ? r = this.keys[0].value : r = (this.keys[1].value - this.keys[0].value) / o * (i - this.keys[0].time) + this.keys[0].value;
        } else
          r = this.keys[0].value;
      else if (i <= this.keys[a - 1].time) {
        let o = 1, l = this.keys.length - 1;
        for (; o <= l; ) {
          const c = l + o >> 1;
          this.keys[c].time >= i ? l = c - 1 : o = c + 1;
        }
        r = this.evalTwoKeys(this.keys[o - 1], this.keys[o], i);
      } else if (this.postExtrapolation === "Linaer") {
        const o = this.keys[a - 1].time - this.keys[a - 2].time;
        K5(o) ? r = this.keys[a - 1].value : r = (this.keys[a - 1].value - this.keys[a - 2].value) / o * (i - this.keys[a - 1].time) + this.keys[a - 1].value;
      } else
        r = this.keys[a - 1].value;
      return r + n;
    });
    S(this, "RemapTimeValue", (e, t) => {
      const i = this.length;
      if (i < 2)
        return [e, t];
      if (this.preExtrapolation === "Linaer" || this.preExtrapolation === "Constant")
        return [e, t];
      const n = this.keys[0], r = this.keys[i - 1], a = n.time, o = r.time;
      if (e < this.keys[0].time) {
        let [l, c] = RC(a, o, e);
        if (this.preExtrapolation === "Repeat")
          return [l, t];
        if (this.preExtrapolation === "RepeatWithOffset")
          return t = (r.value - n.value) * c, [l, t];
        if (this.preExtrapolation === "PingPong" && c % 2 === 1)
          return l = a + (o - l), [l, t];
      } else if (e > r.time) {
        let [l, c] = RC(a, o, e);
        if (this.preExtrapolation === "Repeat")
          return [l, t];
        if (this.preExtrapolation === "RepeatWithOffset")
          return t = -(r.value - n.value) * c, [l, t];
        if (this.preExtrapolation === "PingPong" && c % 2 === 1)
          return l = a + (o - l), [l, t];
      }
      return [e, t];
    });
    S(this, "evalTwoKeys", (e, t, i) => {
      const n = t.time - e.time;
      if (K5(n))
        return e.value;
      switch (e.interpMode) {
        case Gf.Constant:
          return e.value;
        case Gf.Linaer: {
          const r = (i - e.time) / n, a = e.value, o = t.value;
          return iie(a, o, r);
        }
        case Gf.Cubic: {
          const r = (i - e.time) / n, a = e.value, o = t.value;
          if (nie(e, t)) {
            const l = 0.3333333333333333, c = a + e.outTangent * n * l, h = o - t.inTangent * n * l;
            return PC(a, c, h, o, r);
          }
          return this.weightedEvalForTwoKeys(e, t, i);
        }
      }
    });
    S(this, "weightedEvalForTwoKeys", (e, t, i) => {
      const n = e.time, r = t.time, a = r - n, o = (i - n) / a, l = 1 / 3;
      let c = 0;
      if (rie(e))
        c = e.outWeight;
      else {
        const I = a, N = I * e.outTangent;
        c = Math.sqrt(I * I + N * N) * l;
      }
      const h = Math.atan(e.outTangent), u = Math.cos(h) * c + n, d = Math.sin(h) * c + e.value;
      let f = 0;
      if (sie(t))
        f = t.inWeight;
      else {
        const I = a, N = I * t.inTangent;
        f = Math.sqrt(I * I + N * N) * l;
      }
      const p = Math.atan(t.inTangent), v = r - Math.cos(p) * f, g = t.value - Math.sin(p) * f, m = a, x = (u - n) / m, y = (v - n) / m, A = d, b = g, M = 0, w = 3 * x, C = 3 * y - 6 * x, T = 3 * (x - y) + 1, E = [0, 0, 0], P = aie(M - o, w, C, T, E);
      let L = o;
      if (P === 1)
        L = E[0];
      else {
        L = -1 / 0;
        for (const I of E)
          I >= 0 && I <= 1 && I > L && (L = I);
        L === -1 / 0 && (L = 0);
      }
      return PC(e.value, A, b, t.value, L);
    });
  }
  get length() {
    return this.keys.length;
  }
  static fromJSON(e) {
    const t = new Xo();
    return t.keys.push(...J5(e.keys)), t.preExtrapolation = e.preExtrapolation, t.postExtrapolation = e.postExtrapolation, t;
  }
  toJSON() {
    return {
      type: "AnimationCurve",
      keys: J5(this.keys),
      preExtrapolation: this.preExtrapolation,
      postExtrapolation: this.postExtrapolation
    };
  }
  clone() {
    const e = new Xo();
    return e.keys.push(...J5(this.keys)), e.preExtrapolation = this.preExtrapolation, e.postExtrapolation = this.postExtrapolation, e;
  }
}
const PC = (s, e, t, i, n) => {
  const r = 1 - n, a = r * r * r, o = 3 * r * r * n, l = 3 * r * n * n, c = n * n * n;
  return a * s + o * e + l * t + c * i;
}, RC = (s, e, t) => {
  const i = t;
  let n = 0;
  const r = e - s;
  return t > e ? (n = Math.floor((t - e) / r), t = t - n * r) : i < s && (n = Math.floor((s - t) / r), t = t + n * r), i < s && t == e ? t = s : i > e && t == s && (t = e), [t, n];
};
function Ri(s) {
  switch (s.type) {
    case "ConstantValue":
      return Ai.fromJSON(s);
    case "IntervalValue":
      return sr.fromJSON(s);
    case "PiecewiseBezier":
      return fg.fromJSON(s);
    case "AnimationCurve":
      return Xo.fromJSON(s);
    default:
      return new Ai(0);
  }
}
class Iu {
  constructor() {
    S(this, "type");
    this.type = "rotation";
  }
  genValue(e, t) {
    let i, n, r, a, o, l;
    do
      i = Math.random() * 2 - 1, n = Math.random() * 2 - 1, r = i * i + n * n;
    while (r > 1);
    do
      a = Math.random() * 2 - 1, o = Math.random() * 2 - 1, l = a * a + o * o;
    while (l > 1);
    const c = Math.sqrt((1 - r) / l);
    return e.set(i, n, c * a, c * o), e;
  }
  toJSON() {
    return {
      type: "RandomQuat"
    };
  }
  static fromJSON(e) {
    return new Iu();
  }
  clone() {
    return new Iu();
  }
}
class pg {
  constructor(e, t) {
    S(this, "type");
    this.axis = e, this.angle = t, this.type = "rotation";
  }
  genValue(e, t) {
    return e.setFromAxisAngle(this.axis, this.angle.genValue(t));
  }
  toJSON() {
    return {
      type: "AxisAngle",
      axis: { x: this.axis.x, y: this.axis.y, z: this.axis.z },
      angle: this.angle.toJSON()
    };
  }
  static fromJSON(e) {
    return new pg(
      new _(e.axis.x, e.axis.y, e.axis.z),
      Ri(e.angle)
    );
  }
  clone() {
    return new pg(this.axis.clone(), this.angle.clone());
  }
}
class lx {
  constructor(e, t, i, n) {
    S(this, "type");
    S(this, "eular");
    this.angleX = e, this.angleY = t, this.angleZ = i, this.type = "rotation", this.eular = new Mi(0, 0, 0, n);
  }
  genValue(e, t) {
    return this.eular.set(this.angleX.genValue(t), this.angleY.genValue(t), this.angleZ.genValue(t)), e.setFromEuler(this.eular);
  }
  toJSON() {
    return {
      type: "Euler",
      angleX: this.angleX.toJSON(),
      angleY: this.angleY.toJSON(),
      angleZ: this.angleZ.toJSON(),
      eulerOrder: this.eular.order
    };
  }
  static fromJSON(e) {
    return new lx(
      Ri(e.angleX),
      Ri(e.angleY),
      Ri(e.angleZ),
      e.eulerOrder
    );
  }
  clone() {
    return new lx(this.angleX, this.angleY, this.angleZ, this.eular.order);
  }
}
function hN(s) {
  switch (s.type) {
    case "AxisAngle":
      return pg.fromJSON(s);
    case "Euler":
      return lx.fromJSON(s);
    case "RandomQuat":
      return Iu.fromJSON(s);
    default:
      return new Iu();
  }
}
function lie(s) {
  switch (s.type) {
    case "ConstantValue":
    case "IntervalValue":
    case "PiecewiseBezier":
    case "AnimationCurve":
      return Ri(s);
    case "AxisAngle":
    case "RandomQuat":
    case "Euler":
      return hN(s);
    default:
      return new Ai(0);
  }
}
class cx {
  constructor(e = []) {
    S(this, "colors", []);
    S(this, "type");
    this.type = "value", this.colors = e;
  }
  genColor(e) {
    let t = Math.floor(Math.random() * this.colors.length);
    return e.copy(this.colors[t]);
  }
  toJSON() {
    return {
      type: "RandomColor",
      colors: this.colors.map((e) => e.toArray())
    };
  }
  static fromJSON(e) {
    const t = e.colors.map((i) => new Yt().fromArray(i));
    return new cx(t);
  }
  clone() {
    return new cx([...this.colors]);
  }
}
class pp {
  constructor(e) {
    S(this, "type", "ColorOverLife");
    S(this, "enable", !1);
    this.color = e;
  }
  initialize(e) {
    this.color.type === "memorizedFunction" && (e.colorOverLifeMemory = {}, this.color.startGen(
      e.colorOverLifeMemory
    ));
  }
  update(e, t) {
    this.color.type === "memorizedFunction" ? this.color.genColor(
      e.color,
      e.age / e.life,
      e.colorOverLifeMemory
    ) : this.color.genColor(e.color, e.age / e.life), e.color.x *= e.startColor.x, e.color.y *= e.startColor.y, e.color.z *= e.startColor.z, e.color.w *= e.startColor.w;
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      color: this.color.toJSON()
    };
  }
  static fromJSON(e) {
    return new pp(FS(e.color));
  }
  clone() {
    return new pp(this.color.clone());
  }
  reset() {
  }
}
class mp {
  constructor(e) {
    S(this, "type", "RotationOverLife");
    S(this, "enable", !1);
    S(this, "dynamic");
    this.angularVelocity = e, this.dynamic = !(e instanceof Ai || e instanceof sr);
  }
  initialize(e) {
    this.dynamic = !(this.angularVelocity instanceof Ai || this.angularVelocity instanceof sr), !this.dynamic && typeof e.rotation == "number" && (e.angularVelocity = this.angularVelocity.genValue());
  }
  update(e, t) {
    this.dynamic ? typeof e.rotation == "number" && (e.rotation += t * this.angularVelocity.genValue(e.age / e.life)) : typeof e.rotation == "number" && (e.rotation += t * e.angularVelocity);
  }
  toJSON() {
    return {
      type: this.type,
      angularVelocity: this.angularVelocity.toJSON()
    };
  }
  static fromJSON(e) {
    return new mp(
      Ri(e.angularVelocity)
    );
  }
  frameUpdate(e) {
  }
  clone() {
    return new mp(this.angularVelocity.clone());
  }
  reset() {
  }
}
const DC = new St();
class mg {
  constructor(e) {
    S(this, "type", "Rotation3DOverLife");
    S(this, "enable", !1);
    S(this, "tempQuat", new St());
    S(this, "dynamic");
    this.angularVelocity = e, this.dynamic = !(e instanceof Iu);
  }
  initialize(e) {
    this.dynamic = !(this.angularVelocity instanceof Iu), e.rotation instanceof St && (e.angularVelocity = new St(), this.angularVelocity.genValue(
      e.angularVelocity
    ));
  }
  update(e, t) {
    e.rotation instanceof St && (this.dynamic ? (this.angularVelocity.genValue(
      this.tempQuat,
      e.age / e.life
    ), this.tempQuat.slerpQuaternions(DC, this.tempQuat, t), e.rotation.multiply(this.tempQuat)) : (this.tempQuat.slerpQuaternions(
      DC,
      e.angularVelocity,
      t
    ), e.rotation.multiply(this.tempQuat)));
  }
  toJSON() {
    return {
      type: this.type,
      angularVelocity: this.angularVelocity.toJSON()
    };
  }
  static fromJSON(e) {
    return new mg(hN(e.angularVelocity));
  }
  frameUpdate(e) {
  }
  clone() {
    return new mg(this.angularVelocity.clone());
  }
  reset() {
  }
}
class gg {
  constructor(e, t, i) {
    S(this, "type", "ForceOverLife");
    S(this, "enable", !1);
    S(this, "_temp", new _());
    this.x = e, this.y = t, this.z = i;
  }
  initialize(e) {
  }
  update(e, t) {
    this._temp.set(
      this.x.genValue(e.age / e.life),
      this.y.genValue(e.age / e.life),
      this.z.genValue(e.age / e.life)
    ), e.velocity.addScaledVector(this._temp, t);
  }
  toJSON() {
    return {
      type: this.type,
      x: this.x.toJSON(),
      y: this.y.toJSON(),
      z: this.z.toJSON()
    };
  }
  static fromJSON(e) {
    return new gg(
      Ri(e.x),
      Ri(e.y),
      Ri(e.z)
    );
  }
  frameUpdate(e) {
  }
  clone() {
    return new gg(this.x.clone(), this.y.clone(), this.z.clone());
  }
  reset() {
  }
}
class gp {
  constructor(e) {
    S(this, "type", "SizeOverLife");
    S(this, "enable", !1);
    this.size = e;
  }
  initialize(e) {
  }
  update(e) {
    e.size = e.startSize * this.size.genValue(e.age / e.life);
  }
  toJSON() {
    return {
      type: this.type,
      size: this.size.toJSON()
    };
  }
  static fromJSON(e) {
    return new gp(Ri(e.size));
  }
  frameUpdate(e) {
  }
  clone() {
    return new gp(this.size.clone());
  }
  reset() {
  }
}
class vp {
  constructor(e) {
    S(this, "type", "SpeedOverLife");
    S(this, "enable", !1);
    this.speed = e;
  }
  initialize(e) {
  }
  update(e) {
    e.speedModifier = this.speed.genValue(e.age / e.life);
  }
  toJSON() {
    return {
      type: this.type,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new vp(Ri(e.speed));
  }
  frameUpdate(e) {
  }
  clone() {
    return new vp(this.speed.clone());
  }
  reset() {
  }
}
class yp {
  constructor(e) {
    S(this, "type", "FrameOverLife");
    S(this, "enable", !1);
    this.frame = e;
  }
  initialize(e) {
    this.isCurve() || (e.uvTile = Math.floor(this.frame.genValue(0) + 1e-3));
  }
  update(e, t) {
    this.isCurve() && (e.uvTile = Math.floor(this.frame.genValue(e.age / e.life) + 1e-3));
  }
  frameUpdate(e) {
  }
  isCurve() {
    return this.frame instanceof fg || this.frame instanceof Xo;
  }
  toJSON() {
    return {
      type: this.type,
      frame: this.frame.toJSON()
    };
  }
  static fromJSON(e) {
    return new yp(Ri(e.frame));
  }
  clone() {
    return new yp(this.frame.clone());
  }
  reset() {
  }
}
new _(0, 0, 1);
class vg {
  constructor(e, t = new _(0, 1, 0)) {
    S(this, "type", "OrbitOverLife");
    S(this, "enable", !1);
    S(this, "rotation");
    S(this, "line");
    S(this, "temp", new _());
    this.orbitSpeed = e, this.axis = t, this.rotation = new St(), this.line = new Qs();
  }
  initialize(e) {
  }
  update(e, t) {
    this.line.set(new _(0, 0, 0), this.axis), this.line.closestPointToPoint(e.position, !1, this.temp), this.rotation.setFromAxisAngle(
      this.axis,
      this.orbitSpeed.genValue(e.age / e.life) * t
    ), e.position.sub(this.temp), e.position.applyQuaternion(this.rotation), e.position.add(this.temp);
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      orbitSpeed: this.orbitSpeed.toJSON(),
      axis: [this.axis.x, this.axis.y, this.axis.z]
    };
  }
  static fromJSON(e) {
    return new vg(
      Ri(e.orbitSpeed),
      e.axis ? new _(e.axis[0], e.axis[1], e.axis[2]) : void 0
    );
  }
  clone() {
    return new vg(this.orbitSpeed.clone());
  }
  reset() {
  }
}
class yg {
  constructor(e) {
    S(this, "type", "WidthOverLength");
    S(this, "enable", !1);
    this.width = e;
  }
  initialize(e) {
  }
  update(e) {
    if (e instanceof xb) {
      const t = e.previous.values();
      for (let i = 0; i < e.previous.length; i++) {
        const n = t.next();
        n.value.size = this.width.genValue(
          (e.previous.length - i) / e.length
        );
      }
    }
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      width: this.width.toJSON()
    };
  }
  static fromJSON(e) {
    return new yg(Ri(e.width));
  }
  clone() {
    return new yg(this.width.clone());
  }
  reset() {
  }
}
class xg {
  constructor(e, t) {
    S(this, "type", "ApplyForce");
    S(this, "enable", !1);
    S(this, "magnitudeValue");
    this.direction = e, this.magnitude = t, this.magnitudeValue = this.magnitude.genValue();
  }
  initialize(e) {
  }
  update(e, t) {
    e.velocity.addScaledVector(this.direction, this.magnitudeValue * t);
  }
  frameUpdate(e) {
    this.magnitudeValue = this.magnitude.genValue();
  }
  toJSON() {
    return {
      type: this.type,
      direction: [this.direction.x, this.direction.y, this.direction.z],
      magnitude: this.magnitude.toJSON()
    };
  }
  static fromJSON(e) {
    return new xg(
      new _(e.direction[0], e.direction[1], e.direction[2]),
      Ri(e.magnitude ?? e.force)
    );
  }
  clone() {
    return new xg(this.direction.clone(), this.magnitude.clone());
  }
  reset() {
  }
}
class Ag {
  constructor(e, t) {
    S(this, "type", "GravityForce");
    S(this, "enable", !1);
    S(this, "temp", new _());
    this.center = e, this.magnitude = t;
  }
  initialize(e) {
  }
  update(e, t) {
    this.temp.copy(this.center).sub(e.position).normalize(), e.velocity.addScaledVector(
      this.temp,
      this.magnitude / e.position.distanceToSquared(this.center) * t
    );
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      center: [this.center.x, this.center.y, this.center.z],
      magnitude: this.magnitude
    };
  }
  static fromJSON(e) {
    return new Ag(
      new _(e.center[0], e.center[1], e.center[2]),
      e.magnitude
    );
  }
  clone() {
    return new Ag(this.center.clone(), this.magnitude);
  }
  reset() {
  }
}
new _(0, 0, 1);
class bg {
  constructor(e) {
    S(this, "type", "ChangeEmitDirection");
    S(this, "enable", !1);
    S(this, "_temp", new _());
    S(this, "_q", new St());
    this.angle = e;
  }
  initialize(e) {
    const t = e.velocity.length();
    t != 0 && (e.velocity.normalize(), e.velocity.x === 0 && e.velocity.y === 0 ? this._temp.set(0, e.velocity.z, 0) : this._temp.set(-e.velocity.y, e.velocity.x, 0), this._q.setFromAxisAngle(this._temp.normalize(), this.angle.genValue()), this._temp.copy(e.velocity), e.velocity.applyQuaternion(this._q), this._q.setFromAxisAngle(this._temp, Math.random() * Math.PI * 2), e.velocity.applyQuaternion(this._q), e.velocity.setLength(t));
  }
  update(e, t) {
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      angle: this.angle.toJSON()
    };
  }
  static fromJSON(e) {
    return new bg(Ri(e.angle));
  }
  clone() {
    return new bg(this.angle);
  }
  reset() {
  }
}
const cie = new _(1, 1, 1), LC = new _(0, 0, 1);
class Mg {
  constructor(e, t, i, n = 2, r = 1) {
    S(this, "type", "EmitSubParticleSystem");
    S(this, "enable", !1);
    //private matrix_ = new Matrix4();
    S(this, "q_", new St());
    S(this, "v_", new _());
    S(this, "v2_", new _());
    S(this, "subEmissions", new Array());
    this.particleSystem = e, this.useVelocityAsBasis = t, this.subParticleSystem = i, this.mode = n, this.emitProbability = r, this.subParticleSystem && this.subParticleSystem.system && (this.subParticleSystem.system.onlyUsedByOther = !0);
  }
  initialize(e) {
  }
  update(e, t) {
    this.mode === 2 ? this.emit(e, t) : this.mode === 1 && e.age === 0 ? this.emit(e, t) : this.mode === 0 && e.age + t >= e.life && this.emit(e, t);
  }
  emit(e, t) {
    if (!this.subParticleSystem || Math.random() > this.emitProbability)
      return;
    const i = new et();
    this.setMatrixFromParticle(i, e), this.subEmissions.push({
      burstIndex: 0,
      burstWaveIndex: 0,
      time: 0,
      waitEmiting: 0,
      matrix: i,
      travelDistance: 0,
      particle: e
    });
  }
  frameUpdate(e) {
    if (this.subParticleSystem)
      for (let t = 0; t < this.subEmissions.length; t++)
        if (this.subEmissions[t].time >= this.subParticleSystem.system.duration)
          this.subEmissions[t] = this.subEmissions[this.subEmissions.length - 1], this.subEmissions.length = this.subEmissions.length - 1, t--;
        else {
          let i = this.subEmissions[t];
          i.particle && i.particle.age < i.particle.life ? this.setMatrixFromParticle(i.matrix, i.particle) : i.particle = void 0, this.subParticleSystem.system.emit(
            e,
            i,
            i.matrix
          );
        }
  }
  toJSON() {
    return {
      type: this.type,
      subParticleSystem: this.subParticleSystem ? this.subParticleSystem.uuid : "",
      useVelocityAsBasis: this.useVelocityAsBasis,
      mode: this.mode,
      emitProbability: this.emitProbability
    };
  }
  static fromJSON(e, t) {
    return new Mg(
      t,
      e.useVelocityAsBasis,
      e.subParticleSystem,
      e.mode,
      e.emitProbability
    );
  }
  clone() {
    return new Mg(
      this.particleSystem,
      this.useVelocityAsBasis,
      this.subParticleSystem,
      this.mode,
      this.emitProbability
    );
  }
  reset() {
  }
  setMatrixFromParticle(e, t) {
    let i;
    if (t.rotation === void 0 || this.useVelocityAsBasis)
      if (t.velocity.x === 0 && t.velocity.y === 0 && (t.velocity.z === 1 || t.velocity.z === 0))
        e.set(
          1,
          0,
          0,
          t.position.x,
          0,
          1,
          0,
          t.position.y,
          0,
          0,
          1,
          t.position.z,
          0,
          0,
          0,
          1
        );
      else {
        this.v_.copy(LC).cross(t.velocity), this.v2_.copy(t.velocity).cross(this.v_);
        const n = this.v_.length(), r = this.v2_.length();
        e.set(
          this.v_.x / n,
          this.v2_.x / r,
          t.velocity.x,
          t.position.x,
          this.v_.y / n,
          this.v2_.y / r,
          t.velocity.y,
          t.position.y,
          this.v_.z / n,
          this.v2_.z / r,
          t.velocity.z,
          t.position.z,
          0,
          0,
          0,
          1
        );
      }
    else
      t.rotation instanceof St ? i = t.rotation : (this.q_.setFromAxisAngle(LC, t.rotation), i = this.q_), e.compose(t.position, i, cie);
    this.particleSystem.worldSpace || e.multiplyMatrices(this.particleSystem.emitter.matrixWorld, e);
  }
}
const hie = 0.5 * (Math.sqrt(3) - 1), Lm = (3 - Math.sqrt(3)) / 6, uie = 1 / 3, wo = 1 / 6, die = (Math.sqrt(5) - 1) / 4, Ns = (5 - Math.sqrt(5)) / 20, Ms = new Float32Array([
  1,
  1,
  0,
  -1,
  1,
  0,
  1,
  -1,
  0,
  -1,
  -1,
  0,
  1,
  0,
  1,
  -1,
  0,
  1,
  1,
  0,
  -1,
  -1,
  0,
  -1,
  0,
  1,
  1,
  0,
  -1,
  1,
  0,
  1,
  -1,
  0,
  -1,
  -1
]), ns = new Float32Array([
  0,
  1,
  1,
  1,
  0,
  1,
  1,
  -1,
  0,
  1,
  -1,
  1,
  0,
  1,
  -1,
  -1,
  0,
  -1,
  1,
  1,
  0,
  -1,
  1,
  -1,
  0,
  -1,
  -1,
  1,
  0,
  -1,
  -1,
  -1,
  1,
  0,
  1,
  1,
  1,
  0,
  1,
  -1,
  1,
  0,
  -1,
  1,
  1,
  0,
  -1,
  -1,
  -1,
  0,
  1,
  1,
  -1,
  0,
  1,
  -1,
  -1,
  0,
  -1,
  1,
  -1,
  0,
  -1,
  -1,
  1,
  1,
  0,
  1,
  1,
  1,
  0,
  -1,
  1,
  -1,
  0,
  1,
  1,
  -1,
  0,
  -1,
  -1,
  1,
  0,
  1,
  -1,
  1,
  0,
  -1,
  -1,
  -1,
  0,
  1,
  -1,
  -1,
  0,
  -1,
  1,
  1,
  1,
  0,
  1,
  1,
  -1,
  0,
  1,
  -1,
  1,
  0,
  1,
  -1,
  -1,
  0,
  -1,
  1,
  1,
  0,
  -1,
  1,
  -1,
  0,
  -1,
  -1,
  1,
  0,
  -1,
  -1,
  -1,
  0
]);
class uN {
  /**
   * Creates a new `SimplexNoise` instance.
   * This involves some setup. You can save a few cpu cycles by reusing the same instance.
   * @param randomOrSeed A random number generator or a seed (string|number).
   * Defaults to Math.random (random irreproducible initialization).
   */
  constructor(e = Math.random) {
    S(this, "p");
    S(this, "perm");
    S(this, "permMod12");
    const t = typeof e == "function" ? e : pie(e);
    this.p = fie(t), this.perm = new Uint8Array(512), this.permMod12 = new Uint8Array(512);
    for (let i = 0; i < 512; i++)
      this.perm[i] = this.p[i & 255], this.permMod12[i] = this.perm[i] % 12;
  }
  /**
   * Samples the noise field in 2 dimensions
   * @param x
   * @param y
   * @returns a number in the interval [-1, 1]
   */
  noise2D(e, t) {
    const i = this.permMod12, n = this.perm;
    let r = 0, a = 0, o = 0;
    const l = (e + t) * hie, c = Math.floor(e + l), h = Math.floor(t + l), u = (c + h) * Lm, d = c - u, f = h - u, p = e - d, v = t - f;
    let g, m;
    p > v ? (g = 1, m = 0) : (g = 0, m = 1);
    const x = p - g + Lm, y = v - m + Lm, A = p - 1 + 2 * Lm, b = v - 1 + 2 * Lm, M = c & 255, w = h & 255;
    let C = 0.5 - p * p - v * v;
    if (C >= 0) {
      const P = i[M + n[w]] * 3;
      C *= C, r = C * C * (Ms[P] * p + Ms[P + 1] * v);
    }
    let T = 0.5 - x * x - y * y;
    if (T >= 0) {
      const P = i[M + g + n[w + m]] * 3;
      T *= T, a = T * T * (Ms[P] * x + Ms[P + 1] * y);
    }
    let E = 0.5 - A * A - b * b;
    if (E >= 0) {
      const P = i[M + 1 + n[w + 1]] * 3;
      E *= E, o = E * E * (Ms[P] * A + Ms[P + 1] * b);
    }
    return 70 * (r + a + o);
  }
  /**
   * Samples the noise field in 3 dimensions
   * @param x
   * @param y
   * @param z
   * @returns a number in the interval [-1, 1]
   */
  noise3D(e, t, i) {
    const n = this.permMod12, r = this.perm;
    let a, o, l, c;
    const h = (e + t + i) * uie, u = Math.floor(e + h), d = Math.floor(t + h), f = Math.floor(i + h), p = (u + d + f) * wo, v = u - p, g = d - p, m = f - p, x = e - v, y = t - g, A = i - m;
    let b, M, w, C, T, E;
    x >= y ? y >= A ? (b = 1, M = 0, w = 0, C = 1, T = 1, E = 0) : x >= A ? (b = 1, M = 0, w = 0, C = 1, T = 0, E = 1) : (b = 0, M = 0, w = 1, C = 1, T = 0, E = 1) : y < A ? (b = 0, M = 0, w = 1, C = 0, T = 1, E = 1) : x < A ? (b = 0, M = 1, w = 0, C = 0, T = 1, E = 1) : (b = 0, M = 1, w = 0, C = 1, T = 1, E = 0);
    const P = x - b + wo, L = y - M + wo, I = A - w + wo, N = x - C + 2 * wo, H = y - T + 2 * wo, U = A - E + 2 * wo, W = x - 1 + 3 * wo, X = y - 1 + 3 * wo, K = A - 1 + 3 * wo, te = u & 255, re = d & 255, be = f & 255;
    let Pe = 0.6 - x * x - y * y - A * A;
    if (Pe < 0)
      a = 0;
    else {
      const ye = n[te + r[re + r[be]]] * 3;
      Pe *= Pe, a = Pe * Pe * (Ms[ye] * x + Ms[ye + 1] * y + Ms[ye + 2] * A);
    }
    let J = 0.6 - P * P - L * L - I * I;
    if (J < 0)
      o = 0;
    else {
      const ye = n[te + b + r[re + M + r[be + w]]] * 3;
      J *= J, o = J * J * (Ms[ye] * P + Ms[ye + 1] * L + Ms[ye + 2] * I);
    }
    let fe = 0.6 - N * N - H * H - U * U;
    if (fe < 0)
      l = 0;
    else {
      const ye = n[te + C + r[re + T + r[be + E]]] * 3;
      fe *= fe, l = fe * fe * (Ms[ye] * N + Ms[ye + 1] * H + Ms[ye + 2] * U);
    }
    let Me = 0.6 - W * W - X * X - K * K;
    if (Me < 0)
      c = 0;
    else {
      const ye = n[te + 1 + r[re + 1 + r[be + 1]]] * 3;
      Me *= Me, c = Me * Me * (Ms[ye] * W + Ms[ye + 1] * X + Ms[ye + 2] * K);
    }
    return 32 * (a + o + l + c);
  }
  /**
   * Samples the noise field in 4 dimensions
   * @param x
   * @param y
   * @param z
   * @returns a number in the interval [-1, 1]
   */
  noise4D(e, t, i, n) {
    const r = this.perm;
    let a, o, l, c, h;
    const u = (e + t + i + n) * die, d = Math.floor(e + u), f = Math.floor(t + u), p = Math.floor(i + u), v = Math.floor(n + u), g = (d + f + p + v) * Ns, m = d - g, x = f - g, y = p - g, A = v - g, b = e - m, M = t - x, w = i - y, C = n - A;
    let T = 0, E = 0, P = 0, L = 0;
    b > M ? T++ : E++, b > w ? T++ : P++, b > C ? T++ : L++, M > w ? E++ : P++, M > C ? E++ : L++, w > C ? P++ : L++;
    const I = T >= 3 ? 1 : 0, N = E >= 3 ? 1 : 0, H = P >= 3 ? 1 : 0, U = L >= 3 ? 1 : 0, W = T >= 2 ? 1 : 0, X = E >= 2 ? 1 : 0, K = P >= 2 ? 1 : 0, te = L >= 2 ? 1 : 0, re = T >= 1 ? 1 : 0, be = E >= 1 ? 1 : 0, Pe = P >= 1 ? 1 : 0, J = L >= 1 ? 1 : 0, fe = b - I + Ns, Me = M - N + Ns, ye = w - H + Ns, Ye = C - U + Ns, Je = b - W + 2 * Ns, rt = M - X + 2 * Ns, wt = w - K + 2 * Ns, k = C - te + 2 * Ns, Fe = b - re + 3 * Ns, Ge = M - be + 3 * Ns, qe = w - Pe + 3 * Ns, Ie = C - J + 3 * Ns, vt = b - 1 + 4 * Ns, Ve = M - 1 + 4 * Ns, Ze = w - 1 + 4 * Ns, G = C - 1 + 4 * Ns, D = d & 255, ne = f & 255, ve = p & 255, Y = v & 255;
    let ee = 0.6 - b * b - M * M - w * w - C * C;
    if (ee < 0)
      a = 0;
    else {
      const q = r[D + r[ne + r[ve + r[Y]]]] % 32 * 4;
      ee *= ee, a = ee * ee * (ns[q] * b + ns[q + 1] * M + ns[q + 2] * w + ns[q + 3] * C);
    }
    let we = 0.6 - fe * fe - Me * Me - ye * ye - Ye * Ye;
    if (we < 0)
      o = 0;
    else {
      const q = r[D + I + r[ne + N + r[ve + H + r[Y + U]]]] % 32 * 4;
      we *= we, o = we * we * (ns[q] * fe + ns[q + 1] * Me + ns[q + 2] * ye + ns[q + 3] * Ye);
    }
    let de = 0.6 - Je * Je - rt * rt - wt * wt - k * k;
    if (de < 0)
      l = 0;
    else {
      const q = r[D + W + r[ne + X + r[ve + K + r[Y + te]]]] % 32 * 4;
      de *= de, l = de * de * (ns[q] * Je + ns[q + 1] * rt + ns[q + 2] * wt + ns[q + 3] * k);
    }
    let xe = 0.6 - Fe * Fe - Ge * Ge - qe * qe - Ie * Ie;
    if (xe < 0)
      c = 0;
    else {
      const q = r[D + re + r[ne + be + r[ve + Pe + r[Y + J]]]] % 32 * 4;
      xe *= xe, c = xe * xe * (ns[q] * Fe + ns[q + 1] * Ge + ns[q + 2] * qe + ns[q + 3] * Ie);
    }
    let it = 0.6 - vt * vt - Ve * Ve - Ze * Ze - G * G;
    if (it < 0)
      h = 0;
    else {
      const q = r[D + 1 + r[ne + 1 + r[ve + 1 + r[Y + 1]]]] % 32 * 4;
      it *= it, h = it * it * (ns[q] * vt + ns[q + 1] * Ve + ns[q + 2] * Ze + ns[q + 3] * G);
    }
    return 27 * (a + o + l + c + h);
  }
}
function fie(s) {
  const e = new Uint8Array(256);
  for (let t = 0; t < 256; t++)
    e[t] = t;
  for (let t = 0; t < 255; t++) {
    const i = t + ~~(s() * (256 - t)), n = e[t];
    e[t] = e[i], e[i] = n;
  }
  return e;
}
function pie(s) {
  let e = 0, t = 0, i = 0, n = 1;
  const r = mie();
  return e = r(" "), t = r(" "), i = r(" "), e -= r(s), e < 0 && (e += 1), t -= r(s), t < 0 && (t += 1), i -= r(s), i < 0 && (i += 1), function() {
    const a = 2091639 * e + n * 23283064365386963e-26;
    return e = t, t = i, i = a - (n = a | 0);
  };
}
function mie() {
  let s = 4022871197;
  return function(e) {
    e = e.toString();
    for (let t = 0; t < e.length; t++) {
      s += e.charCodeAt(t);
      let i = 0.02519603282416938 * s;
      s = i >>> 0, i -= s, i *= s, s = i >>> 0, i -= s, s += i * 4294967296;
    }
    return (s >>> 0) * 23283064365386963e-26;
  };
}
class Sg {
  constructor(e, t, i, n) {
    S(this, "type", "TurbulenceField");
    S(this, "enable", !1);
    S(this, "generator", new uN());
    S(this, "timeOffset", new _());
    S(this, "temp", new _());
    S(this, "temp2", new _());
    this.scale = e, this.octaves = t, this.velocityMultiplier = i, this.timeScale = n, this.timeOffset.x = Math.random() / this.scale.x * this.timeScale.x, this.timeOffset.y = Math.random() / this.scale.y * this.timeScale.y, this.timeOffset.z = Math.random() / this.scale.z * this.timeScale.z;
  }
  initialize(e) {
  }
  update(e, t) {
    const i = e.position.x / this.scale.x, n = e.position.y / this.scale.y, r = e.position.z / this.scale.z;
    this.temp.set(0, 0, 0);
    let a = 1;
    for (let o = 0; o < this.octaves; o++)
      this.temp2.set(
        this.generator.noise4D(i * a, n * a, r * a, this.timeOffset.x * a) / a,
        this.generator.noise4D(i * a, n * a, r * a, this.timeOffset.y * a) / a,
        this.generator.noise4D(i * a, n * a, r * a, this.timeOffset.z * a) / a
      ), this.temp.add(this.temp2), a *= 2;
    this.temp.multiply(this.velocityMultiplier), e.velocity.addScaledVector(this.temp, t);
  }
  toJSON() {
    return {
      type: this.type,
      scale: [this.scale.x, this.scale.y, this.scale.z],
      octaves: this.octaves,
      velocityMultiplier: [
        this.velocityMultiplier.x,
        this.velocityMultiplier.y,
        this.velocityMultiplier.z
      ],
      timeScale: [this.timeScale.x, this.timeScale.y, this.timeScale.z]
    };
  }
  frameUpdate(e) {
    this.timeOffset.x += e * this.timeScale.x, this.timeOffset.y += e * this.timeScale.y, this.timeOffset.z += e * this.timeScale.z;
  }
  static fromJSON(e) {
    return new Sg(
      new _(e.scale[0], e.scale[1], e.scale[2]),
      e.octaves,
      new _(
        e.velocityMultiplier[0],
        e.velocityMultiplier[1],
        e.velocityMultiplier[2]
      ),
      new _(e.timeScale[0], e.timeScale[1], e.timeScale[2])
    );
  }
  clone() {
    return new Sg(
      this.scale.clone(),
      this.octaves,
      this.velocityMultiplier.clone(),
      this.timeScale.clone()
    );
  }
  reset() {
  }
}
function t3(s, e) {
  return Math.floor(Math.random() * (e - s)) + s;
}
const Eo = [], Q5 = new _(), $5 = new St();
class wg {
  constructor(e, t, i = new Ai(1), n = new Ai(0)) {
    S(this, "type", "Noise");
    S(this, "enable", !1);
    S(this, "duration", 0);
    if (this.frequency = e, this.power = t, this.positionAmount = i, this.rotationAmount = n, Eo.length === 0)
      for (let r = 0; r < 100; r++)
        Eo.push(new uN());
  }
  initialize(e) {
    e.lastPosNoise = new _(), typeof e.rotation == "number" ? e.lastRotNoise = 0 : e.lastRotNoise = new St(), e.generatorIndex = [
      t3(0, 100),
      t3(0, 100),
      t3(0, 100),
      t3(0, 100)
    ];
  }
  update(e, t) {
    let i = this.frequency.genValue(e.age / e.life), n = this.power.genValue(e.age / e.life), r = this.positionAmount.genValue(e.age / e.life), a = this.rotationAmount.genValue(e.age / e.life);
    r > 0 && e.lastPosNoise !== void 0 && (e.position.sub(e.lastPosNoise), Q5.set(
      Eo[e.generatorIndex[0]].noise2D(0, e.age * i) * n * r,
      Eo[e.generatorIndex[1]].noise2D(0, e.age * i) * n * r,
      Eo[e.generatorIndex[2]].noise2D(0, e.age * i) * n * r
    ), e.position.add(Q5), e.lastPosNoise.copy(Q5)), a > 0 && e.lastRotNoise !== void 0 && (typeof e.rotation == "number" ? (e.rotation -= e.lastRotNoise, e.rotation += Eo[e.generatorIndex[3]].noise2D(0, e.age * i) * Math.PI * n * a) : (e.lastRotNoise.invert(), e.rotation.multiply(e.lastRotNoise), $5.set(
      Eo[e.generatorIndex[0]].noise2D(0, e.age * i) * n * a,
      Eo[e.generatorIndex[1]].noise2D(0, e.age * i) * n * a,
      Eo[e.generatorIndex[2]].noise2D(0, e.age * i) * n * a,
      Eo[e.generatorIndex[3]].noise2D(0, e.age * i) * n * a
    ).normalize(), e.rotation.multiply($5), e.lastRotNoise.copy($5)));
  }
  toJSON() {
    return {
      type: this.type,
      frequency: this.frequency.toJSON(),
      power: this.power.toJSON(),
      positionAmount: this.positionAmount.toJSON(),
      rotationAmount: this.rotationAmount.toJSON()
    };
  }
  frameUpdate(e) {
    this.duration += e;
  }
  static fromJSON(e) {
    return new wg(
      Ri(e.frequency),
      Ri(e.power),
      Ri(e.positionAmount),
      Ri(e.rotationAmount)
    );
  }
  clone() {
    return new wg(
      this.frequency.clone(),
      this.power.clone(),
      this.positionAmount.clone(),
      this.rotationAmount.clone()
    );
  }
  reset() {
  }
}
class Eg {
  constructor(e, t) {
    S(this, "type", "ColorBySpeed");
    S(this, "enable", !1);
    this.color = e, this.speedRange = t;
  }
  initialize(e) {
    this.color.type === "memorizedFunction" && (e.colorOverLifeMemory = {}, this.color.startGen(
      e.colorOverLifeMemory
    ));
  }
  update(e, t) {
    const i = (e.startSpeed - this.speedRange.a) / (this.speedRange.b - this.speedRange.a);
    this.color.type === "memorizedFunction" ? this.color.genColor(
      e.color,
      i,
      e.colorOverLifeMemory
    ) : this.color.genColor(e.color, i), e.color.x *= e.startColor.x, e.color.y *= e.startColor.y, e.color.z *= e.startColor.z, e.color.w *= e.startColor.w;
  }
  frameUpdate(e) {
  }
  toJSON() {
    return {
      type: this.type,
      color: this.color.toJSON(),
      speedRange: this.speedRange.toJSON()
    };
  }
  static fromJSON(e) {
    return new Eg(
      FS(e.color),
      sr.fromJSON(e.speedRange)
    );
  }
  clone() {
    return new Eg(this.color.clone(), this.speedRange.clone());
  }
  reset() {
  }
}
class Tg {
  constructor(e, t) {
    S(this, "type", "SizeBySpeed");
    S(this, "enable", !1);
    this.size = e, this.speedRange = t;
  }
  initialize(e) {
  }
  update(e) {
    const t = (e.startSpeed - this.speedRange.a) / (this.speedRange.b - this.speedRange.a);
    e.size = e.startSize * this.size.genValue(t);
  }
  toJSON() {
    return {
      type: this.type,
      size: this.size.toJSON(),
      speedRange: this.speedRange.toJSON()
    };
  }
  static fromJSON(e) {
    return new Tg(
      Ri(e.size),
      sr.fromJSON(e.speedRange)
    );
  }
  frameUpdate(e) {
  }
  clone() {
    return new Tg(this.size.clone(), this.speedRange.clone());
  }
  reset() {
  }
}
class Cg {
  constructor(e, t) {
    S(this, "type", "RotationBySpeed");
    S(this, "enable", !1);
    S(this, "tempQuat", new St());
    this.angularVelocity = e, this.speedRange = t;
  }
  initialize(e) {
  }
  update(e, t) {
    const i = (e.startSpeed - this.speedRange.a) / (this.speedRange.b - this.speedRange.a);
    e.rotation += t * this.angularVelocity.genValue(i);
  }
  toJSON() {
    return {
      type: this.type,
      angularVelocity: this.angularVelocity.toJSON(),
      speedRange: this.speedRange.toJSON()
    };
  }
  static fromJSON(e) {
    return new Cg(
      Ri(e.angularVelocity),
      sr.fromJSON(e.speedRange)
    );
  }
  frameUpdate(e) {
  }
  clone() {
    return new Cg(
      this.angularVelocity.clone(),
      this.speedRange.clone()
    );
  }
  reset() {
  }
}
class _g {
  constructor(e, t) {
    S(this, "type", "LimitSpeedOverLife");
    S(this, "enable", !1);
    this.speed = e, this.dampen = t;
  }
  initialize(e) {
  }
  update(e, t) {
    let i = e.velocity.length(), n = this.speed.genValue(e.age / e.life);
    if (i > n) {
      const r = (i - n) / i;
      e.velocity.multiplyScalar(1 - r * this.dampen * t * 20);
    }
  }
  toJSON() {
    return {
      type: this.type,
      speed: this.speed.toJSON(),
      dampen: this.dampen
    };
  }
  static fromJSON(e) {
    return new _g(
      Ri(e.speed),
      e.dampen
    );
  }
  frameUpdate(e) {
  }
  clone() {
    return new _g(this.speed.clone(), this.dampen);
  }
  reset() {
  }
}
const gie = {
  ApplyForce: {
    type: "ApplyForce",
    constructor: xg,
    params: [
      ["direction", "vec3"],
      ["magnitude", "value"]
    ],
    loadJSON: xg.fromJSON
  },
  Noise: {
    type: "Noise",
    constructor: wg,
    params: [
      ["frequency", "value"],
      ["power", "value"],
      ["positionAmount", "value"],
      ["rotationAmount", "value"]
    ],
    loadJSON: wg.fromJSON
  },
  TurbulenceField: {
    type: "TurbulenceField",
    constructor: Sg,
    params: [
      ["scale", "vec3"],
      ["octaves", "number"],
      ["velocityMultiplier", "vec3"],
      ["timeScale", "vec3"]
    ],
    loadJSON: Sg.fromJSON
  },
  GravityForce: {
    type: "GravityForce",
    constructor: Ag,
    params: [
      ["center", "vec3"],
      ["magnitude", "number"]
    ],
    loadJSON: Ag.fromJSON
  },
  ColorOverLife: {
    type: "ColorOverLife",
    constructor: pp,
    params: [["color", "colorFunc"]],
    loadJSON: pp.fromJSON
  },
  RotationOverLife: {
    type: "RotationOverLife",
    constructor: mp,
    params: [["angularVelocity", "valueFunc"]],
    loadJSON: mp.fromJSON
  },
  Rotation3DOverLife: {
    type: "Rotation3DOverLife",
    constructor: mg,
    params: [["angularVelocity", "rotationFunc"]],
    loadJSON: mg.fromJSON
  },
  SizeOverLife: {
    type: "SizeOverLife",
    constructor: gp,
    params: [["size", "valueFunc"]],
    loadJSON: gp.fromJSON
  },
  ColorBySpeed: {
    type: "ColorBySpeed",
    constructor: Eg,
    params: [
      ["color", "colorFunc"],
      ["speedRange", "range"]
    ],
    loadJSON: Eg.fromJSON
  },
  RotationBySpeed: {
    type: "RotationBySpeed",
    constructor: Cg,
    params: [
      ["angularVelocity", "valueFunc"],
      ["speedRange", "range"]
    ],
    loadJSON: Cg.fromJSON
  },
  SizeBySpeed: {
    type: "SizeBySpeed",
    constructor: Tg,
    params: [
      ["size", "valueFunc"],
      ["speedRange", "range"]
    ],
    loadJSON: Tg.fromJSON
  },
  SpeedOverLife: {
    type: "SpeedOverLife",
    constructor: vp,
    params: [["speed", "valueFunc"]],
    loadJSON: vp.fromJSON
  },
  FrameOverLife: {
    type: "FrameOverLife",
    constructor: yp,
    params: [["frame", "valueFunc"]],
    loadJSON: yp.fromJSON
  },
  ForceOverLife: {
    type: "ForceOverLife",
    constructor: gg,
    params: [
      ["x", "valueFunc"],
      ["y", "valueFunc"],
      ["z", "valueFunc"]
    ],
    loadJSON: gg.fromJSON
  },
  OrbitOverLife: {
    type: "OrbitOverLife",
    constructor: vg,
    params: [
      ["orbitSpeed", "valueFunc"],
      ["axis", "vec3"]
    ],
    loadJSON: vg.fromJSON
  },
  WidthOverLength: {
    type: "WidthOverLength",
    constructor: yg,
    params: [["width", "valueFunc"]],
    loadJSON: yg.fromJSON
  },
  ChangeEmitDirection: {
    type: "ChangeEmitDirection",
    constructor: bg,
    params: [["angle", "value"]],
    loadJSON: bg.fromJSON
  },
  EmitSubParticleSystem: {
    type: "EmitSubParticleSystem",
    constructor: Mg,
    params: [
      ["particleSystem", "self"],
      ["useVelocityAsBasis", "boolean"],
      ["subParticleSystem", "particleSystem"],
      ["mode", "number"],
      ["emitProbability", "number"]
    ],
    loadJSON: Mg.fromJSON
  },
  LimitSpeedOverLife: {
    type: "LimitSpeedOverLife",
    constructor: _g,
    params: [
      ["speed", "valueFunc"],
      ["dampen", "number"]
    ],
    loadJSON: _g.fromJSON
  }
};
function vie(s, e) {
  return gie[s.type].loadJSON(s, e);
}
var Br = /* @__PURE__ */ ((s) => (s[s.Random = 0] = "Random", s[s.Loop = 1] = "Loop", s[s.PingPong = 2] = "PingPong", s[s.Burst = 3] = "Burst", s[s.Uniform = 4] = "Uniform", s))(Br || {});
function D2(s, e, t) {
  let i;
  switch (s === 0 && (e = Math.random()), t > 0 ? i = Math.floor(e / t) * t : i = e, s) {
    case 1:
      i = i % 1;
      break;
    case 2:
      i = Math.abs(i % 2 - 1);
      break;
  }
  return i;
}
class xp {
  constructor(e = {}) {
    S(this, "type", "cone");
    S(this, "radius");
    S(this, "arc");
    // [0, Math.PI * 2]
    S(this, "thickness");
    // [0, 1]
    S(this, "angle");
    // [0, Math.PI / 2]
    S(this, "mode");
    S(this, "spread");
    S(this, "speed");
    S(this, "currentValue", 0);
    this.radius = e.radius ?? 10, this.arc = e.arc ?? 2 * Math.PI, this.thickness = e.thickness ?? 1, this.angle = e.angle ?? Math.PI / 6, this.mode = e.mode ?? Br.Random, this.spread = e.spread ?? 0, this.speed = e.speed ?? new Ai(1);
  }
  update(e, t) {
    Br.Random != this.mode && (this.currentValue += this.speed.genValue(e.emissionState.time / e.duration) * t);
  }
  initialize(e) {
    const t = D2(this.mode, this.currentValue, this.spread), i = nt.lerp(1 - this.thickness, 1, Math.random()), n = t * this.arc, r = Math.sqrt(i), a = Math.sin(n), o = Math.cos(n);
    e.position.x = r * o, e.position.y = r * a, e.position.z = 0;
    const l = this.angle * r;
    e.velocity.set(0, 0, Math.cos(l)).addScaledVector(e.position, Math.sin(l)).multiplyScalar(e.startSpeed), e.position.multiplyScalar(this.radius);
  }
  toJSON() {
    return {
      type: "cone",
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      angle: this.angle,
      mode: this.mode,
      spread: this.spread,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new xp({
      radius: e.radius,
      arc: e.arc,
      thickness: e.thickness,
      angle: e.angle,
      mode: e.mode,
      speed: e.speed ? Ri(e.speed) : void 0,
      spread: e.spread
    });
  }
  clone() {
    return new xp({
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      angle: this.angle,
      mode: this.mode,
      speed: this.speed.clone(),
      spread: this.spread
    });
  }
}
class Ap {
  constructor(e = {}) {
    S(this, "type", "circle");
    S(this, "radius");
    S(this, "arc");
    // [0, Math.PI * 2]
    S(this, "thickness");
    // [0, 1]
    S(this, "mode");
    S(this, "spread");
    S(this, "speed");
    S(this, "currentValue", 0);
    this.radius = e.radius ?? 10, this.arc = e.arc ?? 2 * Math.PI, this.thickness = e.thickness ?? 1, this.mode = e.mode ?? Br.Random, this.spread = e.spread ?? 0, this.speed = e.speed ?? new Ai(1);
  }
  update(e, t) {
    this.currentValue += this.speed.genValue(e.emissionState.time / e.duration) * t;
  }
  initialize(e, t) {
    let i = 0, n = nt.lerp(1 - this.thickness, 1, Math.random());
    Br.Uniform === this.mode ? (i = t ? (t == null ? void 0 : t.index) / (t == null ? void 0 : t.count) : 0, n = 1) : i = D2(this.mode, this.currentValue, this.spread);
    const r = i * this.arc;
    e.position.x = Math.cos(r), e.position.y = Math.sin(r), e.position.z = 0, e.velocity.copy(e.position).multiplyScalar(e.startSpeed), e.position.multiplyScalar(this.radius * n);
  }
  toJSON() {
    return {
      type: "circle",
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      spread: this.spread,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new Ap({
      radius: e.radius,
      arc: e.arc,
      thickness: e.thickness,
      mode: e.mode,
      speed: e.speed ? Ri(e.speed) : void 0,
      spread: e.spread
    });
  }
  clone() {
    return new Ap({
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      speed: this.speed.clone(),
      spread: this.spread
    });
  }
}
class Pg {
  constructor(e = {}) {
    S(this, "type", "donut");
    S(this, "radius");
    S(this, "donutRadius");
    S(this, "arc");
    // [0, Math.PI * 2]
    S(this, "thickness");
    S(this, "mode");
    S(this, "spread");
    S(this, "speed");
    S(this, "currentValue", 0);
    this.radius = e.radius ?? 10, this.arc = e.arc ?? 2 * Math.PI, this.thickness = e.thickness ?? 1, this.donutRadius = e.donutRadius ?? this.radius * 0.2, this.mode = e.mode ?? Br.Random, this.spread = e.spread ?? 0, this.speed = e.speed ?? new Ai(1);
  }
  update(e, t) {
    Br.Random != this.mode && (this.currentValue += this.speed.genValue(e.emissionState.time / e.duration) * t);
  }
  initialize(e) {
    const t = D2(this.mode, this.currentValue, this.spread), i = Math.random(), n = nt.lerp(1 - this.thickness, 1, Math.random()), r = t * this.arc, a = i * Math.PI * 2, o = Math.sin(r), l = Math.cos(r);
    e.position.x = this.radius * l, e.position.y = this.radius * o, e.position.z = 0, e.velocity.z = this.donutRadius * n * Math.sin(a), e.velocity.x = this.donutRadius * n * Math.cos(a) * l, e.velocity.y = this.donutRadius * n * Math.cos(a) * o, e.position.add(e.velocity), e.velocity.normalize().multiplyScalar(e.startSpeed);
  }
  toJSON() {
    return {
      type: "donut",
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      donutRadius: this.donutRadius,
      mode: this.mode,
      spread: this.spread,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new Pg({
      radius: e.radius,
      arc: e.arc,
      thickness: e.thickness,
      donutRadius: e.donutRadius,
      mode: e.mode,
      speed: e.speed ? Ri(e.speed) : void 0,
      spread: e.spread
    });
  }
  clone() {
    return new Pg({
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      donutRadius: this.donutRadius,
      mode: this.mode,
      speed: this.speed.clone(),
      spread: this.spread
    });
  }
}
class bp {
  constructor() {
    S(this, "type", "point");
  }
  update(e, t) {
  }
  initialize(e) {
    const t = Math.random(), i = Math.random(), n = t * Math.PI * 2, r = Math.acos(2 * i - 1), a = Math.cbrt(Math.random()), o = Math.sin(n), l = Math.cos(n), c = Math.sin(r), h = Math.cos(r);
    e.velocity.x = a * c * l, e.velocity.y = a * c * o, e.velocity.z = a * h, e.velocity.multiplyScalar(e.startSpeed), e.position.setScalar(0);
  }
  toJSON() {
    return {
      type: "point"
    };
  }
  static fromJSON(e) {
    return new bp();
  }
  clone() {
    return new bp();
  }
}
class Ou {
  constructor(e = {}) {
    S(this, "type", "sphere");
    S(this, "radius");
    S(this, "arc");
    S(this, "thickness");
    //[0, 1]
    S(this, "mode");
    S(this, "spread");
    S(this, "speed");
    S(this, "currentValue", 0);
    this.radius = e.radius ?? 10, this.arc = e.arc ?? 2 * Math.PI, this.thickness = e.thickness ?? 1, this.mode = e.mode ?? Br.Random, this.spread = e.spread ?? 0, this.speed = e.speed ?? new Ai(1);
  }
  update(e, t) {
    Br.Random != this.mode && (this.currentValue += this.speed.genValue(e.emissionState.time / e.duration) * t);
  }
  initialize(e) {
    const t = D2(this.mode, this.currentValue, this.spread), i = Math.random(), n = nt.lerp(1 - this.thickness, 1, Math.random()), r = t * this.arc, a = Math.acos(2 * i - 1), o = Math.sin(r), l = Math.cos(r), c = Math.sin(a), h = Math.cos(a);
    e.position.x = c * l, e.position.y = c * o, e.position.z = h, e.velocity.copy(e.position).multiplyScalar(e.startSpeed), e.position.multiplyScalar(this.radius * n);
  }
  toJSON() {
    return {
      type: "sphere",
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      spread: this.spread,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new Ou({
      radius: e.radius,
      arc: e.arc,
      thickness: e.thickness,
      mode: e.mode,
      speed: e.speed ? Ri(e.speed) : void 0,
      spread: e.spread
    });
  }
  clone() {
    return new Ou({
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      speed: this.speed.clone(),
      spread: this.spread
    });
  }
}
class Mp {
  constructor(e = {}) {
    S(this, "type", "sphere");
    S(this, "radius");
    S(this, "arc");
    S(this, "thickness");
    //[0, 1]
    S(this, "mode");
    S(this, "spread");
    S(this, "speed");
    S(this, "currentValue", 0);
    this.radius = e.radius ?? 10, this.arc = e.arc ?? 2 * Math.PI, this.thickness = e.thickness ?? 1, this.mode = e.mode ?? Br.Random, this.spread = e.spread ?? 0, this.speed = e.speed ?? new Ai(1);
  }
  update(e, t) {
    Br.Random != this.mode && (this.currentValue += this.speed.genValue(e.emissionState.time / e.duration) * t);
  }
  initialize(e) {
    const t = D2(this.mode, this.currentValue, this.spread), i = Math.random(), n = nt.lerp(1 - this.thickness, 1, Math.random()), r = t * this.arc, a = Math.acos(i), o = Math.sin(r), l = Math.cos(r), c = Math.sin(a), h = Math.cos(a);
    e.position.x = c * l, e.position.y = c * o, e.position.z = h, e.velocity.copy(e.position).multiplyScalar(e.startSpeed), e.position.multiplyScalar(this.radius * n);
  }
  toJSON() {
    return {
      type: "hemisphere",
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      spread: this.spread,
      speed: this.speed.toJSON()
    };
  }
  static fromJSON(e) {
    return new Mp({
      radius: e.radius,
      arc: e.arc,
      thickness: e.thickness,
      mode: e.mode,
      speed: e.speed ? Ri(e.speed) : void 0,
      spread: e.spread
    });
  }
  clone() {
    return new Mp({
      radius: this.radius,
      arc: this.arc,
      thickness: this.thickness,
      mode: this.mode,
      speed: this.speed.clone(),
      spread: this.spread
    });
  }
}
class Sp {
  // [0, Math.PI / 2]
  constructor(e = {}) {
    S(this, "type", "grid");
    S(this, "width");
    S(this, "height");
    // [0, Math.PI * 2]
    S(this, "column");
    // [0, 1]
    S(this, "row");
    this.width = e.width ?? 1, this.height = e.height ?? 1, this.column = e.column ?? 10, this.row = e.row ?? 10;
  }
  initialize(e) {
    const t = Math.floor(Math.random() * this.row), i = Math.floor(Math.random() * this.column);
    e.position.x = i * this.width / this.column - this.width / 2, e.position.y = t * this.height / this.row - this.height / 2, e.position.z = 0, e.velocity.set(0, 0, e.startSpeed);
  }
  toJSON() {
    return {
      type: "grid",
      width: this.width,
      height: this.height,
      column: this.column,
      row: this.row
    };
  }
  static fromJSON(e) {
    return new Sp(e);
  }
  clone() {
    return new Sp({
      width: this.width,
      height: this.height,
      column: this.column,
      row: this.row
    });
  }
  update(e, t) {
  }
}
class Rg {
  constructor(e) {
    S(this, "type", "mesh_surface");
    S(this, "_triangleIndexToArea", []);
    S(this, "_geometry");
    S(this, "_tempA", new _());
    S(this, "_tempB", new _());
    S(this, "_tempC", new _());
    e && (this.geometry = e);
  }
  get geometry() {
    return this._geometry;
  }
  set geometry(e) {
    if (this._geometry = e, e === void 0 || typeof e == "string")
      return;
    const t = new Wo();
    this._triangleIndexToArea.length = 0;
    let i = 0;
    if (!e.getIndex())
      return;
    const n = e.getIndex().array, r = n.length / 3;
    this._triangleIndexToArea.push(0);
    for (let a = 0; a < r; a++)
      t.setFromAttributeAndIndices(
        e.getAttribute("position"),
        n[a * 3],
        n[a * 3 + 1],
        n[a * 3 + 2]
      ), i += t.getArea(), this._triangleIndexToArea.push(i);
    e.userData.triangleIndexToArea = this._triangleIndexToArea;
  }
  initialize(e) {
    const t = this._geometry;
    if (!t || t.getIndex() === null) {
      e.position.set(0, 0, 0), e.velocity.set(0, 0, 1).multiplyScalar(e.startSpeed);
      return;
    }
    const i = this._triangleIndexToArea.length - 1;
    let n = 0, r = i;
    const a = Math.random() * this._triangleIndexToArea[i];
    for (; n + 1 < r; ) {
      const f = Math.floor((n + r) / 2);
      a < this._triangleIndexToArea[f] ? r = f : n = f;
    }
    let o = Math.random(), l = Math.random();
    o + l > 1 && (o = 1 - o, l = 1 - l);
    const c = t.getIndex().array[n * 3], h = t.getIndex().array[n * 3 + 1], u = t.getIndex().array[n * 3 + 2], d = t.getAttribute("position");
    this._tempA.fromBufferAttribute(d, c), this._tempB.fromBufferAttribute(d, h), this._tempC.fromBufferAttribute(d, u), this._tempB.sub(this._tempA), this._tempC.sub(this._tempA), this._tempA.addScaledVector(this._tempB, o).addScaledVector(this._tempC, l), e.position.copy(this._tempA), this._tempA.copy(this._tempB).cross(this._tempC).normalize(), e.velocity.copy(this._tempA).normalize().multiplyScalar(e.startSpeed);
  }
  toJSON() {
    return {
      type: "mesh_surface",
      mesh: this._geometry ? this._geometry.uuid : ""
    };
  }
  static fromJSON(e, t) {
    return new Rg(t.geometries[e.geometry]);
  }
  clone() {
    return new Rg(this._geometry);
  }
  update(e, t) {
  }
}
const yie = {
  circle: {
    type: "circle",
    params: [
      ["radius", "number"],
      ["arc", "radian"],
      ["thickness", "number"],
      ["mode", "emitterMode"],
      ["spread", "number"],
      ["speed", "valueFunc"]
    ],
    constructor: Ap,
    loadJSON: Ap.fromJSON
  },
  cone: {
    type: "cone",
    params: [
      ["radius", "number"],
      ["arc", "radian"],
      ["thickness", "number"],
      ["angle", "radian"],
      ["mode", "emitterMode"],
      ["spread", "number"],
      ["speed", "valueFunc"]
    ],
    constructor: xp,
    loadJSON: xp.fromJSON
  },
  donut: {
    type: "donut",
    params: [
      ["radius", "number"],
      ["arc", "radian"],
      ["thickness", "number"],
      ["donutRadius", "number"],
      ["mode", "emitterMode"],
      ["spread", "number"],
      ["speed", "valueFunc"]
    ],
    constructor: Pg,
    loadJSON: Pg.fromJSON
  },
  point: { type: "point", params: [], constructor: bp, loadJSON: bp.fromJSON },
  sphere: {
    type: "sphere",
    params: [
      ["radius", "number"],
      ["arc", "radian"],
      ["thickness", "number"],
      ["angle", "radian"],
      ["mode", "emitterMode"],
      ["spread", "number"],
      ["speed", "valueFunc"]
    ],
    constructor: Ou,
    loadJSON: Ou.fromJSON
  },
  hemisphere: {
    type: "hemisphere",
    params: [
      ["radius", "number"],
      ["arc", "radian"],
      ["thickness", "number"],
      ["angle", "radian"],
      ["mode", "emitterMode"],
      ["spread", "number"],
      ["speed", "valueFunc"]
    ],
    constructor: Mp,
    loadJSON: Mp.fromJSON
  },
  grid: {
    type: "grid",
    params: [
      ["width", "number"],
      ["height", "number"],
      ["rows", "number"],
      ["column", "number"]
    ],
    constructor: Sp,
    loadJSON: Sp.fromJSON
  },
  mesh_surface: {
    type: "mesh_surface",
    params: [["geometry", "geometry"]],
    constructor: Rg,
    loadJSON: Rg.fromJSON
  }
};
function xie(s, e) {
  return yie[s.type].loadJSON(s, e);
}
class US {
  constructor(e) {
    S(this, "type", "Box");
    S(this, "size", new _());
    this.size = e.size;
  }
  initialize(e, t) {
    e.position.x = nt.randFloat(-this.size.x * 0.5, this.size.x * 0.5), e.position.y = nt.randFloat(-this.size.y * 0.5, this.size.y * 0.5), e.position.z = nt.randFloat(-this.size.z * 0.5, this.size.z * 0.5), e.velocity.copy(e.position.clone().normalize()).multiplyScalar(e.startSpeed);
  }
  toJSON() {
    throw new Error("Method not implemented.");
  }
  update(e, t) {
  }
  clone() {
    const e = new US({ size: this.size.clone() });
    return e.size = this.size.clone(), e;
  }
}
function wp(s = 1, e = 32, t = 0, i = Math.PI * 2) {
  const n = [], r = [], a = [], o = new _(), l = new Se();
  for (let c = 0, h = 3; c <= e; c++, h += 3) {
    const u = t + c / e * i;
    o.x = s * Math.cos(u), o.y = s * Math.sin(u), n.push(o.x, o.y, o.z), r.push(0, 0, 1), l.x = (n[h] / s + 1) / 2, l.y = (n[h + 1] / s + 1) / 2, a.push(l.x, l.y);
  }
  return {
    vertices: n,
    normals: r,
    uvs: a
  };
}
class Aie extends mt {
  constructor(e) {
    super();
    const { vertices: t } = wp(e.radius), i = new Pt();
    i.setAttribute("position", new gt(t, 3));
    const n = new ti(
      i,
      new Bi({
        color: new _e(16776960)
      })
    );
    this.add(n);
  }
}
class bie extends mt {
  constructor(e) {
    super();
    const { vertices: t } = wp(e.radius), i = new Pt();
    i.setAttribute("position", new gt(t, 3));
    const n = new ti(
      i,
      new Bi({
        color: new _e(16776960)
      })
    ), r = e.distance ?? 10, a = Math.sin(e.angle) * r + e.radius, { vertices: o } = wp(a), l = new Pt();
    l.setAttribute("position", new gt(o, 3));
    const c = new ti(
      l,
      new Bi({
        color: new _e(16776960)
      })
    );
    c.position.set(0, 0, r), this.add(n), this.add(c);
    const h = new Pt(), u = [
      new _(e.radius, 0, 0),
      new _(a, 0, r),
      new _(0, e.radius, 0),
      new _(0, a, r),
      new _(-e.radius, 0, 0),
      new _(-a, 0, r),
      new _(0, -e.radius, 0),
      new _(0, -a, r)
    ];
    h.setFromPoints(u);
    const d = new _n(
      h,
      new Bi({
        color: new _e(16776960)
      })
    );
    this.add(d);
  }
}
class Mie extends mt {
  constructor(e) {
    super();
    const t = e.size.clone().multiplyScalar(0.5);
    this.add(
      new L4(
        new zt(
          new _(-t.x, -t.y, -t.z),
          new _(t.x, t.y, t.z)
        )
      )
    );
  }
}
class Sie extends mt {
  constructor(e) {
    super();
    const t = new ci(), { vertices: i } = wp(e.radius, 32, 0, Math.PI), n = new Pt();
    n.setAttribute("position", new gt(i, 3));
    const r = new ti(
      n,
      new Bi({
        color: new _e(16776960)
      })
    ), a = r.clone();
    a.rotateY(Math.PI / 2), t.add(r), t.add(a);
    const { vertices: o } = wp(e.radius), l = new Pt();
    l.setAttribute("position", new gt(o, 3));
    const c = new ti(
      l,
      new Bi({
        color: new _e(16776960)
      })
    );
    c.rotateX(Math.PI / 2), t.add(c), t.rotateX(Math.PI / 2), this.add(t);
  }
}
class wie extends mt {
  constructor(e) {
    super();
    const { vertices: t } = wp(e.radius), i = new Pt();
    i.setAttribute("position", new gt(t, 3));
    const n = new ti(
      i,
      new Bi({
        color: new _e(16776960)
      })
    ), r = n.clone();
    r.rotateX(Math.PI / 2);
    const a = n.clone();
    a.rotateY(Math.PI / 2), this.add(n), this.add(r), this.add(a);
  }
}
class Eie extends mt {
  constructor() {
    super();
    const e = new Re(new Hs());
    e.material.color = new _e(16776960), this.add(e);
  }
}
class Tie extends mt {
  constructor(e) {
    super();
    const t = [];
    let i = -e.height / e.row;
    for (let a = 0; a <= e.row; ++a)
      t.push(-e.width / 2, e.height / 2 + a * i, 0), t.push(e.width / 2, e.height / 2 + a * i, 0);
    i = -e.width / e.column;
    for (let a = 0; a <= e.column; ++a)
      t.push(e.width / 2 + a * i, e.height / 2, 0), t.push(e.width / 2 + a * i, -e.height / 2, 0);
    const n = new Pt();
    n.setAttribute("position", new gt(t, 3));
    const r = new _n(
      n,
      new Bi({
        color: new _e(16776960)
      })
    );
    this.add(r);
  }
}
var Ft = /* @__PURE__ */ ((s) => (s[s.BillBoard = 0] = "BillBoard", s[s.StretchedBillBoard = 1] = "StretchedBillBoard", s[s.Mesh = 2] = "Mesh", s[s.Trail = 3] = "Trail", s[s.HorizontalBillBoard = 4] = "HorizontalBillBoard", s[s.VerticalBillBoard = 5] = "VerticalBillBoard", s))(Ft || {});
class dN extends Re {
  constructor(t) {
    super();
    S(this, "type", "VFXBatch");
    S(this, "systems");
    S(this, "settings");
    S(this, "maxParticles");
    this.maxParticles = 1e3, this.systems = /* @__PURE__ */ new Set();
    const i = new e0();
    i.mask = t.layers.mask;
    const n = t.material.clone();
    n.defines = {}, Object.assign(n.defines, t.material.defines), this.settings = {
      instancingGeometry: t.instancingGeometry,
      renderMode: t.renderMode,
      renderOrder: t.renderOrder,
      material: n,
      uTileCount: t.uTileCount,
      vTileCount: t.vTileCount,
      layers: i
    }, this.frustumCulled = !1, this.renderOrder = this.settings.renderOrder;
  }
  addSystem(t) {
    this.systems.add(t);
  }
  removeSystem(t) {
    this.systems.delete(t);
  }
  /*
    clone() {
        let system = this.system.clone();
        return system.emitter as any;
    }*/
}
const Cie = new _(0, 0, 1), eA = new St(), _ie = new _(), Pie = new _();
new _();
const IC = 60, Rie = new $n(1, 1, 1, 1);
class hx extends es {
  constructor(t) {
    super();
    /**
     * Determines whether the ParticleSystem should be automatically disposed when it finishes emitting particles.
     *
     * @type {boolean}
     */
    S(this, "autoDestroy");
    /**
     * Determines whether a looping ParticleSystem should prewarm, i.e., the Particle System looks like it has already simulated for one loop when first becoming visible.
     *
     * @type {boolean}
     */
    S(this, "prewarm");
    /**
     * Determines whether the ParticleSystem should loop, i.e., restart emitting particles after the duration of the particle system is expired.
     *
     * @type {boolean}
     */
    S(this, "looping");
    /**
     * The duration of the ParticleSystem in seconds.
     *
     * @type {number}
     */
    S(this, "duration");
    /**
     * The value generator or function value generator for the starting life of particles.
     *
     * @type {ValueGenerator | FunctionValueGenerator}
     */
    S(this, "startLife");
    /**
     * The value generator or function value generator for the starting speed of particles.
     *
     * @type {ValueGenerator | FunctionValueGenerator}
     */
    S(this, "startSpeed");
    /**
     * The value generator or function value generator or rotation generator for the starting rotation of particles.
     *
     * @type {ValueGenerator | FunctionValueGenerator | RotationGenerator}
     */
    S(this, "startRotation");
    /**
     * The value generator or function value generator for the starting size of particles.
     *
     * @type {ValueGenerator | FunctionValueGenerator}
     */
    S(this, "startSize");
    /**
     * The color generator or function color generator for the starting color of particles.
     *
     * @type {ColorGenerator | FunctionColorGenerator}
     */
    S(this, "startColor");
    /**
     * The value generator for the starting tile index of particles.
     *
     * @type {ValueGenerator}
     */
    S(this, "startTileIndex");
    /**
     * The renderer emitter settings for the ParticleSystem.
     *
     * @type {TrailSettings | MeshSettings | BillBoardSettings | StretchedBillBoardSettings}
     */
    S(this, "rendererEmitterSettings");
    /**
     * The value generator or function value generator for the emission rate of particles over time.
     *
     * @type {ValueGenerator | FunctionValueGenerator}
     */
    S(this, "emissionOverTime");
    /**
     * The value generator or function value generator for the emission rate of particles over distance.
     *
     * @type {ValueGenerator | FunctionValueGenerator}
     */
    S(this, "emissionOverDistance");
    /**
     * An array of burst parameters for the ParticleSystem.
     *
     * @type {Array<BurstParameters>}
     */
    S(this, "emissionBursts");
    /**
     * Determines whether the ParticleSystem is only used by other ParticleSystems.
     *
     * @type {boolean}
     */
    S(this, "onlyUsedByOther");
    /**
     * Determines whether the ParticleSystem is in world space or local space.
     *
     * @type {boolean}
     */
    S(this, "worldSpace");
    /**
     * The number of particles in the ParticleSystem.
     *
     * @type {number}
     */
    S(this, "particleNum");
    /**
     * Determines whether the ParticleSystem is paused.
     *
     * @type {boolean}
     */
    S(this, "paused");
    /**
     * All the particles in the ParticleSystem.
     *
     * @type {Array<Particle>}
     */
    S(this, "particles");
    /**
     * the shape of the emitter.
     *
     * @type {EmitterShape}
     */
    S(this, "emitterShape");
    /**
     * the emitter object that should be added in the scene.
     *
     * @type {ParticleEmitter<Object3DEventMap>}
     */
    S(this, "emitter");
    /**
     * the VFX renderer settings for the batch renderer
     *
     * @type {VFXBatchSettings}
     */
    S(this, "rendererSettings");
    /**
     * whether needs to update renderer settings for the batch renderer
     *
     * @type {boolean}
     */
    S(this, "neededToUpdateRender");
    /**
     * a list of particle behaviors in the particle system
     *
     * @type {Array<Behavior>}
     */
    S(this, "behaviors");
    S(this, "name", "particle");
    S(this, "id", nt.generateUUID());
    // 默认无重力
    S(this, "gravity", new _());
    // 默认重力大小0
    S(this, "gravityValue", new Ai(0));
    S(this, "delay", 0);
    S(this, "emissionState");
    S(this, "prewarmed");
    S(this, "emitEnded");
    S(this, "markForDestroy");
    S(this, "previousWorldPos");
    S(this, "temp", new _());
    S(this, "travelDistance", 0);
    S(this, "normalMatrix", new Gt());
    /** @internal **/
    S(this, "_renderer");
    S(this, "firstTimeUpdate", !0);
    if (this.autoDestroy = t.autoDestroy === void 0 ? !1 : t.autoDestroy, this.duration = t.duration ?? 1, this.looping = t.looping === void 0 ? !0 : t.looping, this.prewarm = t.prewarm === void 0 ? !1 : t.prewarm, this.startLife = t.startLife ?? new Ai(5), this.startSpeed = t.startSpeed ?? new Ai(0), this.startRotation = t.startRotation ?? new Ai(0), this.startSize = t.startSize ?? new Ai(1), this.startColor = t.startColor ?? new Vc(new Yt(1, 1, 1, 1)), this.emissionOverTime = t.emissionOverTime ?? new Ai(10), this.emissionOverDistance = t.emissionOverDistance ?? new Ai(0), this.emissionBursts = t.emissionBursts ?? [], this.onlyUsedByOther = t.onlyUsedByOther ?? !1, this.emitterShape = t.shape ?? new Ou(), this.behaviors = t.behaviors ?? new Array(), this.worldSpace = t.worldSpace ?? !1, this.rendererEmitterSettings = t.rendererEmitterSettings ?? {}, t.renderMode === Ft.StretchedBillBoard) {
      const i = this.rendererEmitterSettings;
      t.speedFactor !== void 0 && (i.speedFactor = t.speedFactor), i.speedFactor = i.speedFactor ?? 0, i.lengthFactor = i.lengthFactor ?? 0;
    }
    this.rendererSettings = {
      instancingGeometry: t.instancingGeometry ?? Rie,
      renderMode: t.renderMode ?? Ft.BillBoard,
      renderOrder: t.renderOrder ?? 0,
      material: t.material,
      uTileCount: t.uTileCount ?? 1,
      vTileCount: t.vTileCount ?? 1,
      layers: t.layers ?? new e0()
    }, this.neededToUpdateRender = !0, this.particles = new Array(), this.startTileIndex = t.startTileIndex || new Ai(0), this.emitter = new Zte(this), this.paused = !1, this.particleNum = 0, this.emissionState = {
      burstIndex: 0,
      burstWaveIndex: 0,
      time: 0,
      waitEmiting: 0,
      travelDistance: 0
    }, this.emitEnded = !1, this.markForDestroy = !1, this.prewarmed = !1;
  }
  set time(t) {
    this.emissionState.time = t;
  }
  get time() {
    return this.emissionState.time;
  }
  // currently if you change the layers setting, you need manually set this.neededToUpdateRender = true;
  get layers() {
    return this.rendererSettings.layers;
  }
  get texture() {
    return this.rendererSettings.material.map;
  }
  set texture(t) {
    this.rendererSettings.material.map = t, this.neededToUpdateRender = !0;
  }
  get material() {
    return this.rendererSettings.material;
  }
  set material(t) {
    this.rendererSettings.material = t, this.neededToUpdateRender = !0;
  }
  get uTileCount() {
    return this.rendererSettings.uTileCount;
  }
  set uTileCount(t) {
    this.rendererSettings.uTileCount = t, this.neededToUpdateRender = !0;
  }
  get vTileCount() {
    return this.rendererSettings.vTileCount;
  }
  set vTileCount(t) {
    this.rendererSettings.vTileCount = t, this.neededToUpdateRender = !0;
  }
  get instancingGeometry() {
    return this.rendererSettings.instancingGeometry;
  }
  set instancingGeometry(t) {
    this.restart(), this.particles.length = 0, this.rendererSettings.instancingGeometry = t, this.neededToUpdateRender = !0;
  }
  get renderMode() {
    return this.rendererSettings.renderMode;
  }
  set renderMode(t) {
    if ((this.rendererSettings.renderMode != Ft.Trail && t === Ft.Trail || this.rendererSettings.renderMode == Ft.Trail && t !== Ft.Trail) && (this.restart(), this.particles.length = 0), this.rendererSettings.renderMode !== t)
      switch (t) {
        case Ft.Trail:
          this.rendererEmitterSettings = {
            startLength: new Ai(30),
            followLocalOrigin: !1
          };
          break;
        case Ft.Mesh:
          this.rendererEmitterSettings = {
            geometry: new $n(1, 1)
          }, this.startRotation = new pg(new _(0, 1, 0), new Ai(0));
          break;
        case Ft.StretchedBillBoard:
          this.rendererEmitterSettings = { speedFactor: 0, lengthFactor: 2 }, this.rendererSettings.renderMode === Ft.Mesh && (this.startRotation = new Ai(0));
          break;
        case Ft.BillBoard:
        case Ft.VerticalBillBoard:
        case Ft.HorizontalBillBoard:
          this.rendererEmitterSettings = {}, this.rendererSettings.renderMode === Ft.Mesh && (this.startRotation = new Ai(0));
          break;
      }
    this.rendererSettings.renderMode = t, this.neededToUpdateRender = !0;
  }
  get renderOrder() {
    return this.rendererSettings.renderOrder;
  }
  set renderOrder(t) {
    this.rendererSettings.renderOrder = t, this.neededToUpdateRender = !0;
  }
  get blending() {
    return this.rendererSettings.material.blending;
  }
  set blending(t) {
    this.rendererSettings.material.blending = t, this.neededToUpdateRender = !0;
  }
  pause() {
    this.paused = !0;
  }
  play() {
    this.paused = !1;
  }
  spawn(t, i, n) {
    eA.setFromRotationMatrix(n);
    const r = _ie, a = eA, o = Pie;
    n.decompose(r, a, o);
    for (let l = 0; l < t; l++) {
      for (this.particleNum++; this.particles.length < this.particleNum; )
        this.rendererSettings.renderMode === Ft.Trail ? this.particles.push(new xb()) : this.particles.push(new Jte());
      const c = this.particles[this.particleNum - 1];
      if (c.speedModifier = 1, this.startColor.genColor(c.startColor, this.emissionState.time / this.duration, {}), c.color.copy(c.startColor), c.startSpeed = this.startSpeed.genValue(i.time / this.duration), c.life = this.startLife.genValue(i.time / this.duration), c.age = 0, c.startSize = this.startSize.genValue(i.time / this.duration), c.uvTile = Math.floor(this.startTileIndex.genValue() + 1e-3), c.size = c.startSize, this.rendererSettings.renderMode === Ft.Mesh || this.rendererSettings.renderMode === Ft.BillBoard || this.rendererSettings.renderMode === Ft.VerticalBillBoard || this.rendererSettings.renderMode === Ft.HorizontalBillBoard || this.rendererSettings.renderMode === Ft.StretchedBillBoard) {
        const h = c;
        this.rendererSettings.renderMode === Ft.Mesh ? (h.rotation instanceof St || (h.rotation = new St()), this.startRotation.type === "rotation" ? this.startRotation.genValue(
          h.rotation,
          i.time / this.duration
        ) : h.rotation.setFromAxisAngle(
          Cie,
          this.startRotation.genValue(i.time / this.duration)
        )) : this.startRotation.type === "rotation" ? h.rotation = 0 : h.rotation = this.startRotation.genValue(i.time / this.duration);
      } else if (this.rendererSettings.renderMode === Ft.Trail) {
        const h = c;
        h.length = this.rendererEmitterSettings.startLength.genValue(
          i.time / this.duration
        );
      }
      if (this.emitterShape.initialize(c, {
        index: l,
        count: t
      }), this.rendererSettings.renderMode === Ft.Trail && this.rendererEmitterSettings.followLocalOrigin) {
        const h = c;
        h.localPosition = new _().copy(h.position);
      }
      this.worldSpace ? (c.position.applyMatrix4(n), c.startSize = c.startSize * (Math.abs(o.x) + Math.abs(o.y) + Math.abs(o.z)) / 3, c.size = c.startSize, c.velocity.multiply(o).applyMatrix3(this.normalMatrix), c.rotation && c.rotation instanceof St && c.rotation.multiplyQuaternions(eA, c.rotation)) : this.onlyUsedByOther && (c.parentMatrix = n);
      for (let h = 0; h < this.behaviors.length; h++)
        this.behaviors[h].enable && this.behaviors[h].initialize(c);
    }
  }
  endEmit() {
    this.emitEnded = !0, this.autoDestroy && (this.markForDestroy = !0), !this.looping && this.dispatchEvent("EndEmit", {
      id: this.id,
      name: this.name
    });
  }
  dispose() {
    this._renderer && this._renderer.deleteSystem(this), this.emitter.dispose(), this.emitter.parent && this.emitter.parent.remove(this.emitter);
  }
  restart() {
    this.paused = !1, this.particleNum = 0, this.emissionState.burstIndex = 0, this.emissionState.burstWaveIndex = 0, this.emissionState.time = 0, this.emissionState.waitEmiting = 0, this.behaviors.forEach((t) => {
      t.reset();
    }), this.emitEnded = !1, this.markForDestroy = !1, this.prewarmed = !1;
  }
  update(t) {
    if (this.paused)
      return;
    let i = this.emitter;
    for (; i.parent; )
      i = i.parent;
    if (i.type !== "Scene") {
      this.dispose();
      return;
    }
    if (this.firstTimeUpdate && (this.firstTimeUpdate = !1, this.emitter.updateWorldMatrix(!0, !1)), this.emitEnded && this.particleNum === 0)
      return;
    if (this.looping && this.prewarm && !this.prewarmed) {
      this.prewarmed = !0;
      for (let o = 0; o < this.duration * IC; o++)
        this.update(1 / IC);
    }
    t > 0.1 && (t = 0.1), this.neededToUpdateRender && (this._renderer && this._renderer.updateSystem(this), this.neededToUpdateRender = !1), this.onlyUsedByOther || this.emit(t, this.emissionState, this.emitter.matrixWorld), this.emitterShape.update(this, t);
    for (let o = 0; o < this.behaviors.length; o++) {
      for (let l = 0; l < this.particleNum; l++)
        this.particles[l].died || this.behaviors[o].enable && this.behaviors[o].update(this.particles[l], t);
      this.behaviors[o].frameUpdate(t);
    }
    const n = this.gravityValue.genValue(this.time / this.duration), r = this.gravity.clone(), a = new St();
    a.setFromEuler(this.emitter.rotation), a.invert(), r.applyQuaternion(a).normalize();
    for (let o = 0; o < this.particleNum; o++) {
      if (this.rendererEmitterSettings.followLocalOrigin && this.particles[o].localPosition)
        this.particles[o].position.copy(this.particles[o].localPosition), this.particles[o].parentMatrix ? this.particles[o].position.applyMatrix4(this.particles[o].parentMatrix) : this.particles[o].position.applyMatrix4(this.emitter.matrixWorld);
      else {
        const l = r.clone().multiplyScalar(0.5).multiplyScalar(n).multiplyScalar(this.particles[o].age * this.particles[o].age);
        this.particles[o].position.addScaledVector(
          this.particles[o].velocity,
          t * this.particles[o].speedModifier
        ), this.particles[o].position.add(l);
      }
      this.particles[o].age += t;
    }
    if (this.rendererSettings.renderMode === Ft.Trail)
      for (let o = 0; o < this.particleNum; o++)
        this.particles[o].update();
    for (let o = 0; o < this.particleNum; o++) {
      const l = this.particles[o];
      l.died && (!(l instanceof xb) || l.previous.length === 0) && (this.particles[o] = this.particles[this.particleNum - 1], this.particles[this.particleNum - 1] = l, this.particleNum--, o--);
    }
  }
  emit(t, i, n) {
    if (i.time > this.duration && (this.looping ? (i.time %= this.duration, i.burstIndex = 0, this.behaviors.forEach((a) => {
      a.reset();
    })) : !this.emitEnded && !this.onlyUsedByOther && this.endEmit()), this.normalMatrix.getNormalMatrix(n), i.time += t, i.time < this.delay)
      return;
    const r = Math.ceil(i.waitEmiting);
    for (this.spawn(r, i, n), i.waitEmiting -= r; i.burstIndex < this.emissionBursts.length && this.emissionBursts[i.burstIndex].time <= i.time; ) {
      if (Math.random() < this.emissionBursts[i.burstIndex].probability) {
        const a = this.emissionBursts[i.burstIndex].count.genValue(this.time);
        this.spawn(a, i, n);
      }
      i.burstIndex++;
    }
    if (!this.emitEnded && (i.waitEmiting += t * this.emissionOverTime.genValue(i.time / this.duration), i.previousWorldPos != null)) {
      this.temp.set(
        n.elements[12],
        n.elements[13],
        n.elements[14]
      ), i.travelDistance += i.previousWorldPos.distanceTo(this.temp);
      const a = this.emissionOverDistance.genValue(i.time / this.duration);
      if (i.travelDistance * a > 0) {
        const o = Math.floor(i.travelDistance * a);
        i.travelDistance -= o / a, i.waitEmiting += o;
      }
    }
    i.previousWorldPos === void 0 && (i.previousWorldPos = new _()), i.previousWorldPos.set(
      n.elements[12],
      n.elements[13],
      n.elements[14]
    );
  }
  toJSON(t, i = {}) {
    if ((t === void 0 || typeof t == "string") && (t = {
      geometries: {},
      materials: {},
      textures: {},
      images: {},
      shapes: {},
      skeletons: {},
      animations: {},
      nodes: {}
    }), t.materials[this.rendererSettings.material.uuid] = this.rendererSettings.material.toJSON(t), i.useUrlForImage && this.texture.source !== void 0) {
      const o = this.texture.source;
      t.images[o.uuid] = {
        uuid: o.uuid,
        url: this.texture.image.url
      };
    }
    let r;
    this.renderMode === Ft.Trail ? r = {
      startLength: this.rendererEmitterSettings.startLength.toJSON(),
      followLocalOrigin: this.rendererEmitterSettings.followLocalOrigin
    } : this.renderMode === Ft.Mesh ? r = {} : this.renderMode === Ft.StretchedBillBoard ? r = {
      speedFactor: this.rendererEmitterSettings.speedFactor,
      lengthFactor: this.rendererEmitterSettings.lengthFactor
    } : r = {};
    const a = this.rendererSettings.instancingGeometry;
    return t.geometries && !t.geometries[a.uuid] && (t.geometries[a.uuid] = a.toJSON()), {
      version: "2.0",
      autoDestroy: this.autoDestroy,
      looping: this.looping,
      prewarm: this.prewarm,
      duration: this.duration,
      shape: this.emitterShape.toJSON(),
      startLife: this.startLife.toJSON(),
      startSpeed: this.startSpeed.toJSON(),
      startRotation: this.startRotation.toJSON(),
      startSize: this.startSize.toJSON(),
      startColor: this.startColor.toJSON(),
      emissionOverTime: this.emissionOverTime.toJSON(),
      emissionOverDistance: this.emissionOverDistance.toJSON(),
      emissionBursts: this.emissionBursts.map((o) => ({
        time: o.time,
        count: o.count.toJSON(),
        probability: o.probability,
        interval: o.interval,
        cycle: o.cycle
      })),
      onlyUsedByOther: this.onlyUsedByOther,
      instancingGeometry: this.rendererSettings.instancingGeometry.uuid,
      //Array.from(this.emitter.interleavedBuffer.array as Float32Array),
      renderOrder: this.renderOrder,
      renderMode: this.renderMode,
      rendererEmitterSettings: r,
      //speedFactor: this.renderMode === RenderMode.StretchedBillBoard ? this.speedFactor : 0,
      //texture: this.texture.uuid,
      material: this.rendererSettings.material.uuid,
      layers: this.layers.mask,
      startTileIndex: this.startTileIndex.toJSON(),
      uTileCount: this.uTileCount,
      vTileCount: this.vTileCount,
      behaviors: this.behaviors.map((o) => o.toJSON()),
      worldSpace: this.worldSpace
    };
  }
  static fromJSON(t, i, n) {
    var c;
    const r = xie(t.shape, i);
    let a;
    if (t.renderMode === Ft.Trail) {
      let h = t.rendererEmitterSettings;
      a = {
        startLength: h.startLength != null ? Ri(h.startLength) : new Ai(30),
        followLocalOrigin: h.followLocalOrigin
      };
    } else
      t.renderMode === Ft.Mesh ? a = {} : t.renderMode === Ft.StretchedBillBoard ? (a = t.rendererEmitterSettings, t.speedFactor != null && (a.speedFactor = t.speedFactor)) : a = {};
    const o = new e0();
    t.layers && (o.mask = t.layers);
    const l = new hx({
      autoDestroy: t.autoDestroy,
      looping: t.looping,
      prewarm: t.prewarm,
      duration: t.duration,
      shape: r,
      startLife: Ri(t.startLife),
      startSpeed: Ri(t.startSpeed),
      startRotation: lie(t.startRotation),
      startSize: Ri(t.startSize),
      startColor: FS(t.startColor),
      emissionOverTime: Ri(t.emissionOverTime),
      emissionOverDistance: Ri(t.emissionOverDistance),
      emissionBursts: (c = t.emissionBursts) == null ? void 0 : c.map((h) => ({
        time: h.time,
        // backward compatibility
        count: typeof h.count == "number" ? new Ai(h.count) : Ri(h.count),
        probability: h.probability,
        interval: h.interval,
        cycle: h.cycle
      })),
      onlyUsedByOther: t.onlyUsedByOther,
      instancingGeometry: i.geometries[t.instancingGeometry],
      renderMode: t.renderMode,
      rendererEmitterSettings: a,
      renderOrder: t.renderOrder,
      layers: o,
      material: t.material ? i.materials[t.material] : t.texture ? new kt({
        map: i.textures[t.texture],
        transparent: t.transparent ?? !0,
        blending: t.blending,
        side: ui
      }) : new kt({
        color: 16777215,
        transparent: !0,
        blending: oo,
        side: ui
      }),
      startTileIndex: typeof t.startTileIndex == "number" ? new Ai(t.startTileIndex) : Ri(t.startTileIndex),
      uTileCount: t.uTileCount,
      vTileCount: t.vTileCount,
      behaviors: [],
      worldSpace: t.worldSpace
    });
    return l.behaviors = t.behaviors.map((h) => {
      const u = vie(h, l);
      return u.type === "EmitSubParticleSystem" && (n[h.subParticleSystem] = u), u;
    }), l;
  }
  addBehavior(t) {
    this.behaviors.push(t);
  }
  getRendererSettings() {
    return this.rendererSettings;
  }
  clone() {
    const t = [];
    for (const a of this.emissionBursts) {
      const o = {};
      Object.assign(o, a), t.push(o);
    }
    const i = [];
    for (const a of this.behaviors)
      i.push(a.clone());
    let n;
    this.renderMode === Ft.Trail ? n = {
      startLength: this.rendererEmitterSettings.startLength.clone(),
      followLocalOrigin: this.rendererEmitterSettings.followLocalOrigin
    } : n = {};
    const r = new e0();
    return r.mask = this.layers.mask, new hx({
      autoDestroy: this.autoDestroy,
      looping: this.looping,
      duration: this.duration,
      shape: this.emitterShape.clone(),
      startLife: this.startLife.clone(),
      startSpeed: this.startSpeed.clone(),
      startRotation: this.startRotation.clone(),
      startSize: this.startSize.clone(),
      startColor: this.startColor.clone(),
      emissionOverTime: this.emissionOverTime.clone(),
      emissionOverDistance: this.emissionOverDistance.clone(),
      emissionBursts: t,
      onlyUsedByOther: this.onlyUsedByOther,
      instancingGeometry: this.rendererSettings.instancingGeometry,
      //.interleavedBuffer.array,
      renderMode: this.renderMode,
      renderOrder: this.renderOrder,
      rendererEmitterSettings: n,
      material: this.rendererSettings.material,
      startTileIndex: this.startTileIndex,
      uTileCount: this.uTileCount,
      vTileCount: this.vTileCount,
      behaviors: i,
      worldSpace: this.worldSpace,
      layers: r
    });
  }
}
const tA = (
  /* glsl */
  `

#include <common>
#include <uv_pars_fragment>
#include <color_pars_fragment>
#include <map_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
#include <alphatest_pars_fragment>

void main() {

    #include <clipping_planes_fragment>
    
    vec3 outgoingLight = vec3( 0.0 );
    vec4 diffuseColor = vColor;
    
    #include <logdepthbuf_fragment>
    
    #ifdef USE_MAP
    diffuseColor *= texture2D( map, vMapUv);
    #endif
    
    #include <alphatest_fragment>

    outgoingLight = diffuseColor.rgb;
    
    #ifdef USE_COLOR_AS_ALPHA
    gl_FragColor = vec4( outgoingLight, diffuseColor.r );
    #else
    gl_FragColor = vec4( outgoingLight, diffuseColor.a );
    #endif
    
    
    #include <tonemapping_fragment>

}
`
), Die = (
  /* glsl */
  `
#define STANDARD
#ifdef PHYSICAL
#define IOR
#define SPECULAR
#endif
uniform vec3 diffuse;
uniform vec3 emissive;
uniform float roughness;
uniform float metalness;
uniform float opacity;
#ifdef IOR
uniform float ior;
#endif
#ifdef SPECULAR
uniform float specularIntensity;
uniform vec3 specularColor;
#ifdef USE_SPECULARINTENSITYMAP
uniform sampler2D specularIntensityMap;
#endif
#ifdef USE_SPECULARCOLORMAP
uniform sampler2D specularColorMap;
#endif
#endif
#ifdef USE_CLEARCOAT
uniform float clearcoat;
uniform float clearcoatRoughness;
#endif
#ifdef USE_IRIDESCENCE
uniform float iridescence;
uniform float iridescenceIOR;
uniform float iridescenceThicknessMinimum;
uniform float iridescenceThicknessMaximum;
#endif
#ifdef USE_SHEEN
uniform vec3 sheenColor;
uniform float sheenRoughness;
#ifdef USE_SHEENCOLORMAP
uniform sampler2D sheenColorMap;
#endif
#ifdef USE_SHEENROUGHNESSMAP
uniform sampler2D sheenRoughnessMap;
#endif
#endif

varying vec3 vViewPosition;
#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <bsdfs>
#include <iridescence_fragment>
#include <cube_uv_reflection_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_physical_pars_fragment>
#include <fog_pars_fragment>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_physical_pars_fragment>
#include <transmission_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <clearcoat_pars_fragment>
#include <iridescence_pars_fragment>
#include <roughnessmap_pars_fragment>
#include <metalnessmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {
    #include <clipping_planes_fragment>
    vec4 diffuseColor = vec4( diffuse, opacity );
    ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
    vec3 totalEmissiveRadiance = emissive;
    #include <logdepthbuf_fragment>
    #include <map_fragment>
    #include <color_fragment>
    #include <alphamap_fragment>
    #include <alphatest_fragment>
    #include <roughnessmap_fragment>
    #include <metalnessmap_fragment>
    #include <normal_fragment_begin>
    #include <normal_fragment_maps>
    #include <clearcoat_normal_fragment_begin>
    #include <clearcoat_normal_fragment_maps>
    #include <emissivemap_fragment>
    // accumulation
    #include <lights_physical_fragment>
    #include <lights_fragment_begin>
    #include <lights_fragment_maps>
    #include <lights_fragment_end>
    // modulation
    #include <aomap_fragment>
    vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
    vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;
    #include <transmission_fragment>
    vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;
    #ifdef USE_SHEEN
    // Sheen energy compensation approximation calculation can be found at the end of
        // https://drive.google.com/file/d/1T0D1VSyR4AllqIJTQAraEIzjlb5h4FKH/view?usp=sharing
        float sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );
        outgoingLight = outgoingLight * sheenEnergyComp + sheenSpecular;
    #endif
    #ifdef USE_CLEARCOAT
        float dotNVcc = saturate( dot( geometry.clearcoatNormal, geometry.viewDir ) );
        vec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );
        outgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + clearcoatSpecular * material.clearcoat;
    #endif
    #include <output_fragment>
    #include <tonemapping_fragment>
    #include <encodings_fragment>
    #include <fog_fragment>
    #include <premultiplied_alpha_fragment>
    #include <dithering_fragment>
}`
), L2 = (
  /* glsl */
  `

    #ifdef UV_TILE
        float col = mod(uvTile, tileCount.x);
        float row = (tileCount.y - floor(uvTile / tileCount.x) - 1.0);
        
        mat3 tileTransform = mat3(
          1.0 / tileCount.x, 0.0, 0.0,
          0.0, 1.0 / tileCount.y, 0.0, 
          col / tileCount.x, row / tileCount.y, 1.0);
    #else
        mat3 tileTransform = mat3(1.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 1.0);
    #endif

#if defined( USE_UV ) || defined( USE_ANISOTROPY )

vUv = (tileTransform *vec3( uv, 1 )).xy;

#endif
#ifdef USE_MAP

vMapUv = ( tileTransform * (mapTransform * vec3( MAP_UV, 1 ) )).xy;

#endif
#ifdef USE_ALPHAMAP

vAlphaMapUv = ( tileTransform * (alphaMapTransform * vec3( ALPHAMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_LIGHTMAP

vLightMapUv = ( tileTransform * (lightMapTransform * vec3( LIGHTMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_AOMAP

vAoMapUv = ( tileTransform * (aoMapTransform * vec3( AOMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_BUMPMAP

vBumpMapUv = ( tileTransform * (bumpMapTransform * vec3( BUMPMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_NORMALMAP

vNormalMapUv = ( tileTransform * (normalMapTransform * vec3( NORMALMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_DISPLACEMENTMAP

vDisplacementMapUv = ( tileTransform * (displacementMapTransform * vec3( DISPLACEMENTMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_EMISSIVEMAP

vEmissiveMapUv = ( tileTransform * (emissiveMapTransform * vec3( EMISSIVEMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_METALNESSMAP

vMetalnessMapUv = ( tileTransform * (metalnessMapTransform * vec3( METALNESSMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_ROUGHNESSMAP

vRoughnessMapUv = ( tileTransform * (roughnessMapTransform * vec3( ROUGHNESSMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_ANISOTROPYMAP

vAnisotropyMapUv = ( tileTransform * (anisotropyMapTransform * vec3( ANISOTROPYMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_CLEARCOATMAP

vClearcoatMapUv = ( tileTransform * (clearcoatMapTransform * vec3( CLEARCOATMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_CLEARCOAT_NORMALMAP

vClearcoatNormalMapUv = ( tileTransform * (clearcoatNormalMapTransform * vec3( CLEARCOAT_NORMALMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP

vClearcoatRoughnessMapUv = ( tileTransform * (clearcoatRoughnessMapTransform * vec3( CLEARCOAT_ROUGHNESSMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_IRIDESCENCEMAP

vIridescenceMapUv = ( tileTransform * (iridescenceMapTransform * vec3( IRIDESCENCEMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP

vIridescenceThicknessMapUv = ( tileTransform * (iridescenceThicknessMapTransform * vec3( IRIDESCENCE_THICKNESSMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_SHEEN_COLORMAP

vSheenColorMapUv = ( tileTransform * (sheenColorMapTransform * vec3( SHEEN_COLORMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_SHEEN_ROUGHNESSMAP

vSheenRoughnessMapUv = ( tileTransform * (sheenRoughnessMapTransform * vec3( SHEEN_ROUGHNESSMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_SPECULARMAP

vSpecularMapUv = ( tileTransform * (specularMapTransform * vec3( SPECULARMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_SPECULAR_COLORMAP

vSpecularColorMapUv = ( tileTransform * (specularColorMapTransform * vec3( SPECULAR_COLORMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_SPECULAR_INTENSITYMAP

vSpecularIntensityMapUv = ( tileTransform * (specularIntensityMapTransform * vec3( SPECULAR_INTENSITYMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_TRANSMISSIONMAP

vTransmissionMapUv = ( tileTransform * transmissionMapTransform * vec3( TRANSMISSIONMAP_UV, 1 ) )).xy;

#endif
#ifdef USE_THICKNESSMAP

vThicknessMapUv = ( tileTransform * thicknessMapTransform * vec3( THICKNESSMAP_UV, 1 ) )).xy;

#endif
`
), Lie = (
  /* glsl */
  `
#include <common>
#include <color_pars_vertex>
#include <uv_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

attribute vec3 offset;
attribute float rotation;
attribute float size;
attribute float uvTile;

#ifdef UV_TILE
uniform vec2 tileCount;
#endif

void main() {

    ${L2}
	
    vec2 alignedPosition = ( position.xy ) * size;
    
    vec2 rotatedPosition;
    rotatedPosition.x = cos( rotation ) * alignedPosition.x - sin( rotation ) * alignedPosition.y;
    rotatedPosition.y = sin( rotation ) * alignedPosition.x + cos( rotation ) * alignedPosition.y;
#ifdef HORIZONTAL
    vec4 mvPosition = modelMatrix * vec4( offset, 1.0 );
    mvPosition.x += rotatedPosition.x;
    mvPosition.z -= rotatedPosition.y;
    mvPosition = viewMatrix * mvPosition;
#elif defined(VERTICAL)
    vec4 mvPosition = modelMatrix * vec4( offset, 1.0 );
    mvPosition.y += rotatedPosition.y;
    mvPosition = viewMatrix * mvPosition;
    mvPosition.x += rotatedPosition.x;
#else
    vec4 mvPosition = modelViewMatrix * vec4( offset, 1.0 );
    mvPosition.xy += rotatedPosition;
#endif

	vColor = color;

	gl_Position = projectionMatrix * mvPosition;

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

}
`
), Iie = (
  /* glsl */
  `
#include <common>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

attribute vec3 offset;
attribute vec4 rotation;
attribute float size;
// attribute vec4 color;
attribute float uvTile;

#ifdef UV_TILE
uniform vec2 tileCount;
#endif

void main() {

    ${L2}
    
    float x2 = rotation.x + rotation.x, y2 = rotation.y + rotation.y, z2 = rotation.z + rotation.z;
    float xx = rotation.x * x2, xy = rotation.x * y2, xz = rotation.x * z2;
    float yy = rotation.y * y2, yz = rotation.y * z2, zz = rotation.z * z2;
    float wx = rotation.w * x2, wy = rotation.w * y2, wz = rotation.w * z2;
    float sx = size, sy = size, sz = size;
    
    mat4 matrix = mat4(( 1.0 - ( yy + zz ) ) * sx, ( xy + wz ) * sx, ( xz - wy ) * sx, 0.0,  // 1. column
                      ( xy - wz ) * sy, ( 1.0 - ( xx + zz ) ) * sy, ( yz + wx ) * sy, 0.0,  // 2. column
                      ( xz + wy ) * sz, ( yz - wx ) * sz, ( 1.0 - ( xx + yy ) ) * sz, 0.0,  // 3. column
                      offset.x, offset.y, offset.z, 1.0);
    
    vec4 mvPosition = modelViewMatrix * (matrix * vec4( position, 1.0 ));

	vColor = color;

	gl_Position = projectionMatrix * mvPosition;

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

}
`
), Oie = (
  /* glsl */
  `
#define STANDARD
varying vec3 vViewPosition;
#ifdef USE_TRANSMISSION
	varying vec3 vWorldPosition;
#endif
#include <common>
#include <uv_pars_vertex>

attribute vec3 offset;
attribute vec4 rotation;
attribute float size;
attribute float uvTile;

#ifdef UV_TILE
uniform vec2 tileCount;
#endif

#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {
    ${L2}

    float x2 = rotation.x + rotation.x, y2 = rotation.y + rotation.y, z2 = rotation.z + rotation.z;
    float xx = rotation.x * x2, xy = rotation.x * y2, xz = rotation.x * z2;
    float yy = rotation.y * y2, yz = rotation.y * z2, zz = rotation.z * z2;
    float wx = rotation.w * x2, wy = rotation.w * y2, wz = rotation.w * z2;
    float sx = size, sy = size, sz = size;

    mat4 particleMatrix = mat4(( 1.0 - ( yy + zz ) ) * sx, ( xy + wz ) * sx, ( xz - wy ) * sx, 0.0,  // 1. column
                      ( xy - wz ) * sy, ( 1.0 - ( xx + zz ) ) * sy, ( yz + wx ) * sy, 0.0,  // 2. column
                      ( xz + wy ) * sz, ( yz - wx ) * sz, ( 1.0 - ( xx + yy ) ) * sz, 0.0,  // 3. column
                      offset.x, offset.y, offset.z, 1.0);

	#include <color_vertex>
	#include <morphcolor_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>

	// replace defaultnormal_vertex
	vec3 transformedNormal = objectNormal;
    mat3 m = mat3( particleMatrix );
    transformedNormal /= vec3( dot( m[ 0 ], m[ 0 ] ), dot( m[ 1 ], m[ 1 ] ), dot( m[ 2 ], m[ 2 ] ) );
    transformedNormal = m * transformedNormal;
    transformedNormal = normalMatrix * transformedNormal;
    #ifdef FLIP_SIDED
        transformedNormal = - transformedNormal;
    #endif
    #ifdef USE_TANGENT
        vec3 transformedTangent = ( modelViewMatrix * vec4( objectTangent, 0.0 ) ).xyz;
        #ifdef FLIP_SIDED
        transformedTangent = - transformedTangent;
        #endif
    #endif

	#include <normal_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>

	// replace include <project_vertex>
  vec4 mvPosition = vec4( transformed, 1.0 );
  mvPosition = modelViewMatrix * (particleMatrix * mvPosition);
	gl_Position = projectionMatrix * mvPosition;

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	
	vViewPosition = - mvPosition.xyz;
	
	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>
#ifdef USE_TRANSMISSION
	vWorldPosition = worldPosition.xyz;
#endif
}
`
), Nie = (
  /* glsl */
  `
#include <common>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

attribute vec3 offset;
attribute float rotation;
attribute float size;
attribute vec4 velocity;
attribute float uvTile;

#ifdef UV_TILE
uniform vec2 tileCount;
#endif

uniform float speedFactor;

void main() {

    ${L2}
    
    float lengthFactor = velocity.w;
#ifdef USE_SKEW
    vec4 mvPosition = modelViewMatrix * vec4( offset, 1.0 );
    vec3 viewVelocity = normalMatrix * velocity.xyz;

    vec3 scaledPos = vec3(position.xy * size, position.z);
    float vlength = length(viewVelocity);
    vec3 projVelocity =  dot(scaledPos, viewVelocity) * viewVelocity / vlength;
    mvPosition.xyz += scaledPos + projVelocity * (speedFactor / size + lengthFactor / vlength);
#else
    vec4 mvPosition = modelViewMatrix * vec4( offset, 1.0 );
    vec3 viewVelocity = normalMatrix * velocity.xyz;
    float vlength = length(viewVelocity); 
    mvPosition.xyz += position.y * normalize(cross(mvPosition.xyz, viewVelocity)) * size; // switch the cross to  match unity implementation
    mvPosition.xyz -= (position.x + 0.5) * viewVelocity * (1.0 + lengthFactor / vlength) * size; // minus position.x to match unity implementation
#endif
	vColor = color;
	gl_Position = projectionMatrix * mvPosition;
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
}
`
);
function Sb(s) {
  return s === 0 ? "uv" : `uv${s}`;
}
new _(0, 0, 1);
class Bie extends dN {
  constructor(t) {
    super(t);
    S(this, "offsetBuffer");
    S(this, "rotationBuffer");
    S(this, "sizeBuffer");
    S(this, "colorBuffer");
    S(this, "uvTileBuffer");
    S(this, "velocityBuffer");
    S(this, "vector_", new _());
    S(this, "vector2_", new _());
    S(this, "vector3_", new _());
    S(this, "quaternion_", new St());
    S(this, "quaternion2_", new St());
    S(this, "quaternion3_", new St());
    S(this, "rotationMat_", new Gt());
    S(this, "rotationMat2_", new Gt());
    this.maxParticles = 1e3, this.setupBuffers(), this.rebuildMaterial();
  }
  buildExpandableBuffers() {
    this.offsetBuffer = new hs(new Float32Array(this.maxParticles * 3), 3), this.offsetBuffer.setUsage(Js), this.geometry.setAttribute("offset", this.offsetBuffer), this.colorBuffer = new hs(new Float32Array(this.maxParticles * 4), 4), this.colorBuffer.setUsage(Js), this.geometry.setAttribute("color", this.colorBuffer), this.settings.renderMode === Ft.Mesh ? (this.rotationBuffer = new hs(
      new Float32Array(this.maxParticles * 4),
      4
    ), this.rotationBuffer.setUsage(Js), this.geometry.setAttribute("rotation", this.rotationBuffer)) : (this.settings.renderMode === Ft.BillBoard || this.settings.renderMode === Ft.HorizontalBillBoard || this.settings.renderMode === Ft.VerticalBillBoard || this.settings.renderMode === Ft.StretchedBillBoard) && (this.rotationBuffer = new hs(new Float32Array(this.maxParticles), 1), this.rotationBuffer.setUsage(Js), this.geometry.setAttribute("rotation", this.rotationBuffer)), this.sizeBuffer = new hs(new Float32Array(this.maxParticles), 1), this.sizeBuffer.setUsage(Js), this.geometry.setAttribute("size", this.sizeBuffer), this.uvTileBuffer = new hs(new Float32Array(this.maxParticles), 1), this.uvTileBuffer.setUsage(Js), this.geometry.setAttribute("uvTile", this.uvTileBuffer), this.settings.renderMode === Ft.StretchedBillBoard && (this.velocityBuffer = new hs(
      new Float32Array(this.maxParticles * 4),
      4
    ), this.velocityBuffer.setUsage(Js), this.geometry.setAttribute("velocity", this.velocityBuffer));
  }
  setupBuffers() {
    this.geometry && this.geometry.dispose(), this.geometry = new eS(), this.geometry.setIndex(this.settings.instancingGeometry.getIndex()), this.settings.instancingGeometry.hasAttribute("normal") && this.geometry.setAttribute("normal", this.settings.instancingGeometry.getAttribute("normal")), this.geometry.setAttribute(
      "position",
      this.settings.instancingGeometry.getAttribute("position")
    ), this.geometry.setAttribute("uv", this.settings.instancingGeometry.getAttribute("uv")), this.buildExpandableBuffers();
  }
  expandBuffers(t) {
    for (; t >= this.maxParticles; )
      this.maxParticles *= 2;
    this.setupBuffers();
  }
  rebuildMaterial() {
    this.layers.mask = this.settings.layers.mask;
    let t;
    const i = {};
    if (this.settings.material.type === "MeshStandardMaterial" || this.settings.material.type === "MeshPhysicalMaterial") {
      const o = this.settings.material;
      t = rs.merge([
        bt.common,
        bt.envmap,
        bt.aomap,
        bt.lightmap,
        bt.emissivemap,
        bt.bumpmap,
        bt.normalmap,
        bt.displacementmap,
        bt.roughnessmap,
        bt.metalnessmap,
        bt.fog,
        bt.lights,
        {
          emissive: { value: /* @__PURE__ */ new _e(0) },
          roughness: { value: 1 },
          metalness: { value: 0 },
          envMapIntensity: { value: 1 }
          // temporary
        }
      ]), t.diffuse.value = o.color, t.opacity.value = o.opacity, t.emissive.value = o.emissive, t.roughness.value = o.roughness, t.metalness.value = o.metalness, o.envMap && (t.envMap.value = o.envMap, t.envMapIntensity.value = o.envMapIntensity), o.normalMap && (t.normalMap.value = o.normalMap, t.normalScale.value = o.normalScale), o.roughnessMap && (t.roughnessMap.value = o.roughnessMap), o.metalnessMap && (t.metalnessMap.value = o.metalnessMap), o.map && (t.map = new ya(o.map));
    } else
      t = {}, t.map = new ya(this.settings.material.map);
    this.settings.material.alphaTest && (i.USE_ALPHATEST = "", t.alphaTest = new ya(this.settings.material.alphaTest)), i.USE_UV = "";
    const n = this.settings.uTileCount, r = this.settings.vTileCount;
    (n > 1 || r > 1) && (i.UV_TILE = "", t.tileCount = new ya(new Se(n, r))), this.settings.material.defines && this.settings.material.defines.USE_COLOR_AS_ALPHA !== void 0 && (i.USE_COLOR_AS_ALPHA = ""), this.settings.material.normalMap && (i.USE_NORMALMAP = "", i.NORMALMAP_UV = Sb(
      this.settings.material.normalMap.channel
    ), t.normalMapTransform = new ya(
      new Gt().copy(this.settings.material.normalMap.matrix)
    )), this.settings.material.map && (i.USE_MAP = "", i.MAP_UV = Sb(this.settings.material.map.channel), t.mapTransform = new ya(
      new Gt().copy(this.settings.material.map.matrix)
    )), i.USE_COLOR_ALPHA = "";
    let a = !1;
    if (this.settings.renderMode === Ft.BillBoard || this.settings.renderMode === Ft.VerticalBillBoard || this.settings.renderMode === Ft.HorizontalBillBoard || this.settings.renderMode === Ft.Mesh) {
      let o, l;
      this.settings.renderMode === Ft.Mesh ? this.settings.material.type === "MeshStandardMaterial" || this.settings.material.type === "MeshPhysicalMaterial" ? (i.USE_COLOR = "", o = Oie, l = Die, a = !0) : (o = Iie, l = tA) : (o = Lie, l = tA), this.settings.renderMode === Ft.VerticalBillBoard ? i.VERTICAL = "" : this.settings.renderMode === Ft.HorizontalBillBoard && (i.HORIZONTAL = ""), this.material = new Jt({
        uniforms: t,
        defines: i,
        vertexShader: o,
        fragmentShader: l,
        transparent: this.settings.material.transparent,
        depthWrite: !this.settings.material.transparent,
        blending: this.settings.material.blending,
        side: this.settings.material.side,
        alphaTest: this.settings.material.alphaTest,
        lights: a
      });
    } else if (this.settings.renderMode === Ft.StretchedBillBoard)
      t.speedFactor = new ya(1), this.material = new Jt({
        uniforms: t,
        defines: i,
        vertexShader: Nie,
        fragmentShader: tA,
        transparent: this.settings.material.transparent,
        depthWrite: !this.settings.material.transparent,
        blending: this.settings.material.blending,
        side: this.settings.material.side,
        alphaTest: this.settings.material.alphaTest
      });
    else
      throw new Error("render mode unavailable");
  }
  update() {
    let t = 0, i = 0;
    this.systems.forEach((n) => {
      i += n.particleNum;
    }), i > this.maxParticles && this.expandBuffers(i), this.systems.forEach((n) => {
      const r = n.particles, a = n.particleNum, o = this.quaternion2_, l = this.vector2_, c = this.vector3_;
      n.emitter.matrixWorld.decompose(l, o, c), this.rotationMat_.setFromMatrix4(n.emitter.matrixWorld);
      for (let h = 0; h < a; h++, t++) {
        const u = r[h];
        if (this.settings.renderMode === Ft.Mesh) {
          let f;
          if (n.worldSpace)
            f = u.rotation;
          else {
            let p;
            u.parentMatrix ? p = this.quaternion3_.setFromRotationMatrix(u.parentMatrix) : p = o, f = this.quaternion_, f.copy(u.rotation).multiply(p);
          }
          this.rotationBuffer.setXYZW(t, f.x, f.y, f.z, f.w);
        } else
          (this.settings.renderMode === Ft.StretchedBillBoard || this.settings.renderMode === Ft.VerticalBillBoard || this.settings.renderMode === Ft.HorizontalBillBoard || this.settings.renderMode === Ft.BillBoard) && this.rotationBuffer.setX(t, u.rotation);
        let d;
        if (n.worldSpace ? d = u.position : (d = this.vector_, u.parentMatrix ? d.copy(u.position).applyMatrix4(u.parentMatrix) : d.copy(u.position).applyMatrix4(n.emitter.matrixWorld)), this.offsetBuffer.setXYZ(t, d.x, d.y, d.z), this.colorBuffer.setXYZW(
          t,
          u.color.x,
          u.color.y,
          u.color.z,
          u.color.w
        ), n.worldSpace ? this.sizeBuffer.setX(t, u.size) : u.parentMatrix ? this.sizeBuffer.setX(t, u.size) : this.sizeBuffer.setX(
          t,
          u.size * (Math.abs(c.x) + Math.abs(c.y) + Math.abs(c.z)) / 3
        ), this.uvTileBuffer.setX(t, u.uvTile), this.settings.renderMode === Ft.StretchedBillBoard && this.velocityBuffer) {
          let f = n.rendererEmitterSettings.speedFactor;
          f === 0 && (f = 1e-3);
          const p = n.rendererEmitterSettings.lengthFactor;
          let v;
          n.worldSpace ? v = u.velocity : (v = this.vector_, u.parentMatrix ? (this.rotationMat2_.setFromMatrix4(u.parentMatrix), v.copy(u.velocity).applyMatrix3(this.rotationMat2_)) : v.copy(u.velocity).applyMatrix3(this.rotationMat_)), this.velocityBuffer.setXYZW(
            t,
            v.x * f,
            v.y * f,
            v.z * f,
            p
          );
        }
      }
    }), this.geometry.instanceCount = t, t > 0 && (this.offsetBuffer.updateRange.count = t * 3, this.offsetBuffer.needsUpdate = !0, this.sizeBuffer.updateRange.count = t, this.sizeBuffer.needsUpdate = !0, this.colorBuffer.updateRange.count = t * 4, this.colorBuffer.needsUpdate = !0, this.uvTileBuffer.updateRange.count = t, this.uvTileBuffer.needsUpdate = !0, this.settings.renderMode === Ft.StretchedBillBoard && this.velocityBuffer && (this.velocityBuffer.updateRange.count = t * 4, this.velocityBuffer.needsUpdate = !0), this.settings.renderMode === Ft.Mesh ? (this.rotationBuffer.updateRange.count = t * 4, this.rotationBuffer.needsUpdate = !0) : (this.settings.renderMode === Ft.StretchedBillBoard || this.settings.renderMode === Ft.HorizontalBillBoard || this.settings.renderMode === Ft.VerticalBillBoard || this.settings.renderMode === Ft.BillBoard) && (this.rotationBuffer.updateRange.count = t, this.rotationBuffer.needsUpdate = !0));
  }
  dispose() {
    this.geometry.dispose();
  }
}
const Fie = (
  /* glsl */
  `

#include <common>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

uniform sampler2D alphaMap;
uniform float useAlphaMap;
uniform float visibility;
uniform float alphaTest;
uniform vec2 repeat;

varying vec4 vColor;
    
void main() {
    #include <clipping_planes_fragment>
    #include <logdepthbuf_fragment>

    vec4 c = vColor;
    
    #ifdef USE_MAP
    #ifdef USE_COLOR_AS_ALPHA
    vec4 tex = texture2D( map, vUv * repeat );
    c *= vec4(tex.rgb, tex.r);
    #else
    c *= texture2D( map, vUv * repeat );
    #endif
    #endif
    if( useAlphaMap == 1. ) c.a *= texture2D( alphaMap, vUv * repeat ).a;
    if( c.a < alphaTest ) discard;
    gl_FragColor = c;

    #include <fog_fragment>
    #include <tonemapping_fragment>
}`
), Uie = (
  /* glsl */
  `
#include <common>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <clipping_planes_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <fog_pars_vertex>

attribute vec3 previous;
attribute vec3 next;
attribute float side;
attribute float width;

uniform vec2 resolution;
uniform float lineWidth;
uniform float sizeAttenuation;
    
vec2 fix(vec4 i, float aspect) {
    vec2 res = i.xy / i.w;
    res.x *= aspect;
    return res;
}
    
void main() {

    ${L2}
    
    float aspect = resolution.x / resolution.y;

    vColor = color;

    mat4 m = projectionMatrix * modelViewMatrix;
    vec4 finalPosition = m * vec4( position, 1.0 );
    vec4 prevPos = m * vec4( previous, 1.0 );
    vec4 nextPos = m * vec4( next, 1.0 );

    vec2 currentP = fix( finalPosition, aspect );
    vec2 prevP = fix( prevPos, aspect );
    vec2 nextP = fix( nextPos, aspect );

    float w = lineWidth * width;

    vec2 dir;
    if( nextP == currentP ) dir = normalize( currentP - prevP );
    else if( prevP == currentP ) dir = normalize( nextP - currentP );
    else {
        vec2 dir1 = normalize( currentP - prevP );
        vec2 dir2 = normalize( nextP - currentP );
        dir = normalize( dir1 + dir2 );

        vec2 perp = vec2( -dir1.y, dir1.x );
        vec2 miter = vec2( -dir.y, dir.x );
        //w = clamp( w / dot( miter, perp ), 0., 4., * lineWidth * width );

    }

    //vec2 normal = ( cross( vec3( dir, 0. ) vec3( 0., 0., 1. ) ) ).xy;
    vec4 normal = vec4( -dir.y, dir.x, 0., 1. );
    normal.xy *= .5 * w;
    normal *= projectionMatrix;
    if( sizeAttenuation == 0. ) {
        normal.xy *= finalPosition.w;
        normal.xy /= ( vec4( resolution, 0., 1. ) * projectionMatrix ).xy;
    }

    finalPosition.xy += normal.xy * side;

    gl_Position = finalPosition;

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	
    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
    
	#include <fog_vertex>
}`
);
new _(0, 0, 1);
class zie extends dN {
  constructor(t) {
    super(t);
    S(this, "positionBuffer");
    S(this, "previousBuffer");
    S(this, "nextBuffer");
    S(this, "uvBuffer");
    S(this, "sideBuffer");
    S(this, "widthBuffer");
    S(this, "colorBuffer");
    S(this, "indexBuffer");
    /*
      clone() {
          let system = this.system.clone();
          return system.emitter as any;
      }*/
    S(this, "vector_", new _());
    S(this, "vector2_", new _());
    S(this, "vector3_", new _());
    S(this, "quaternion_", new St());
    this.maxParticles = 1e4, this.setupBuffers(), this.rebuildMaterial();
  }
  setupBuffers() {
    this.geometry && this.geometry.dispose(), this.geometry = new Pt(), this.indexBuffer = new Ht(new Uint32Array(this.maxParticles * 6), 1), this.indexBuffer.setUsage(Js), this.geometry.setIndex(this.indexBuffer), this.positionBuffer = new Ht(new Float32Array(this.maxParticles * 6), 3), this.positionBuffer.setUsage(Js), this.geometry.setAttribute("position", this.positionBuffer), this.previousBuffer = new Ht(new Float32Array(this.maxParticles * 6), 3), this.previousBuffer.setUsage(Js), this.geometry.setAttribute("previous", this.previousBuffer), this.nextBuffer = new Ht(new Float32Array(this.maxParticles * 6), 3), this.nextBuffer.setUsage(Js), this.geometry.setAttribute("next", this.nextBuffer), this.widthBuffer = new Ht(new Float32Array(this.maxParticles * 2), 1), this.widthBuffer.setUsage(Js), this.geometry.setAttribute("width", this.widthBuffer), this.sideBuffer = new Ht(new Float32Array(this.maxParticles * 2), 1), this.sideBuffer.setUsage(Js), this.geometry.setAttribute("side", this.sideBuffer), this.uvBuffer = new Ht(new Float32Array(this.maxParticles * 4), 2), this.uvBuffer.setUsage(Js), this.geometry.setAttribute("uv", this.uvBuffer), this.colorBuffer = new Ht(new Float32Array(this.maxParticles * 8), 4), this.colorBuffer.setUsage(Js), this.geometry.setAttribute("color", this.colorBuffer);
  }
  expandBuffers(t) {
    for (; t >= this.maxParticles; )
      this.maxParticles *= 2;
    this.setupBuffers();
  }
  rebuildMaterial() {
    this.layers.mask = this.settings.layers.mask;
    const t = {
      lineWidth: { value: 1 },
      map: { value: null },
      useMap: { value: 0 },
      alphaMap: { value: null },
      useAlphaMap: { value: 0 },
      resolution: { value: new Se(1, 1) },
      sizeAttenuation: { value: 1 },
      visibility: { value: 1 },
      alphaTest: { value: 0 },
      repeat: { value: new Se(1, 1) }
    }, i = {};
    if (i.USE_UV = "", i.USE_COLOR_ALPHA = "", this.settings.material.map && (i.USE_MAP = "", i.MAP_UV = Sb(this.settings.material.map.channel), t.map = new ya(this.settings.material.map), t.mapTransform = new ya(
      new Gt().copy(this.settings.material.map.matrix)
    )), this.settings.material.defines && this.settings.material.defines.USE_COLOR_AS_ALPHA !== void 0 && (i.USE_COLOR_AS_ALPHA = ""), this.settings.renderMode === Ft.Trail)
      this.material = new Jt({
        uniforms: t,
        defines: i,
        vertexShader: Uie,
        fragmentShader: Fie,
        transparent: this.settings.material.transparent,
        depthWrite: !this.settings.material.transparent,
        side: this.settings.material.side,
        blending: this.settings.material.blending || oo
      });
    else
      throw new Error("render mode unavailable");
  }
  update() {
    let t = 0, i = 0, n = 0;
    this.systems.forEach((r) => {
      for (let a = 0; a < r.particleNum; a++)
        n += r.particles[a].previous.length * 2;
    }), n > this.maxParticles && this.expandBuffers(n), this.systems.forEach((r) => {
      const a = this.quaternion_, o = this.vector2_, l = this.vector3_;
      r.emitter.matrixWorld.decompose(o, a, l);
      const c = r.particles, h = r.particleNum, u = this.settings.uTileCount, d = this.settings.vTileCount, f = 1 / u, p = 1 / d;
      for (let v = 0; v < h; v++) {
        const g = c[v], m = g.uvTile % d, x = Math.floor(g.uvTile / d + 1e-3), y = g.previous.values();
        let A = y.next(), b = A.value, M = b;
        A.done || (A = y.next());
        let w;
        A.value !== void 0 ? w = A.value : w = M;
        for (let C = 0; C < g.previous.length; C++, t += 2) {
          if (this.positionBuffer.setXYZ(
            t,
            M.position.x,
            M.position.y,
            M.position.z
          ), this.positionBuffer.setXYZ(
            t + 1,
            M.position.x,
            M.position.y,
            M.position.z
          ), r.worldSpace ? (this.positionBuffer.setXYZ(
            t,
            M.position.x,
            M.position.y,
            M.position.z
          ), this.positionBuffer.setXYZ(
            t + 1,
            M.position.x,
            M.position.y,
            M.position.z
          )) : (g.parentMatrix ? this.vector_.copy(M.position).applyMatrix4(g.parentMatrix) : this.vector_.copy(M.position).applyMatrix4(r.emitter.matrixWorld), this.positionBuffer.setXYZ(t, this.vector_.x, this.vector_.y, this.vector_.z), this.positionBuffer.setXYZ(t + 1, this.vector_.x, this.vector_.y, this.vector_.z)), r.worldSpace ? (this.previousBuffer.setXYZ(
            t,
            b.position.x,
            b.position.y,
            b.position.z
          ), this.previousBuffer.setXYZ(
            t + 1,
            b.position.x,
            b.position.y,
            b.position.z
          )) : (g.parentMatrix ? this.vector_.copy(b.position).applyMatrix4(g.parentMatrix) : this.vector_.copy(b.position).applyMatrix4(r.emitter.matrixWorld), this.previousBuffer.setXYZ(t, this.vector_.x, this.vector_.y, this.vector_.z), this.previousBuffer.setXYZ(t + 1, this.vector_.x, this.vector_.y, this.vector_.z)), r.worldSpace ? (this.nextBuffer.setXYZ(t, w.position.x, w.position.y, w.position.z), this.nextBuffer.setXYZ(t + 1, w.position.x, w.position.y, w.position.z)) : (g.parentMatrix ? this.vector_.copy(w.position).applyMatrix4(g.parentMatrix) : this.vector_.copy(w.position).applyMatrix4(r.emitter.matrixWorld), this.nextBuffer.setXYZ(t, this.vector_.x, this.vector_.y, this.vector_.z), this.nextBuffer.setXYZ(t + 1, this.vector_.x, this.vector_.y, this.vector_.z)), this.sideBuffer.setX(t, -1), this.sideBuffer.setX(t + 1, 1), r.worldSpace)
            this.widthBuffer.setX(t, M.size), this.widthBuffer.setX(t + 1, M.size);
          else if (g.parentMatrix)
            this.widthBuffer.setX(t, M.size), this.widthBuffer.setX(t + 1, M.size);
          else {
            const T = (Math.abs(l.x) + Math.abs(l.y) + Math.abs(l.z)) / 3;
            this.widthBuffer.setX(t, M.size * T), this.widthBuffer.setX(t + 1, M.size * T);
          }
          this.uvBuffer.setXY(
            t,
            (C / g.previous.length + m) * f,
            (d - x - 1) * p
          ), this.uvBuffer.setXY(
            t + 1,
            (C / g.previous.length + m) * f,
            (d - x) * p
          ), this.colorBuffer.setXYZW(
            t,
            M.color.x,
            M.color.y,
            M.color.z,
            M.color.w
          ), this.colorBuffer.setXYZW(
            t + 1,
            M.color.x,
            M.color.y,
            M.color.z,
            M.color.w
          ), C + 1 < g.previous.length && (this.indexBuffer.setX(i * 3, t), this.indexBuffer.setX(i * 3 + 1, t + 1), this.indexBuffer.setX(i * 3 + 2, t + 2), i++, this.indexBuffer.setX(i * 3, t + 2), this.indexBuffer.setX(i * 3 + 1, t + 1), this.indexBuffer.setX(i * 3 + 2, t + 3), i++), b = M, M = w, A.done || (A = y.next(), A.value !== void 0 && (w = A.value));
        }
      }
    }), this.positionBuffer.updateRange.count = t * 3, this.positionBuffer.needsUpdate = !0, this.previousBuffer.updateRange.count = t * 3, this.previousBuffer.needsUpdate = !0, this.nextBuffer.updateRange.count = t * 3, this.nextBuffer.needsUpdate = !0, this.sideBuffer.updateRange.count = t, this.sideBuffer.needsUpdate = !0, this.widthBuffer.updateRange.count = t, this.widthBuffer.needsUpdate = !0, this.uvBuffer.updateRange.count = t * 2, this.uvBuffer.needsUpdate = !0, this.colorBuffer.updateRange.count = t * 4, this.colorBuffer.needsUpdate = !0, this.indexBuffer.updateRange.count = i * 3, this.indexBuffer.needsUpdate = !0, this.geometry.setDrawRange(0, i * 3);
  }
  dispose() {
    this.geometry.dispose();
  }
}
class zS extends mt {
  constructor() {
    super();
    S(this, "batches", []);
    S(this, "systemToBatchIndex", /* @__PURE__ */ new Map());
    S(this, "type", "BatchedRenderer");
  }
  static equals(t, i) {
    return t.material.side === i.material.side && t.material.blending === i.material.blending && t.material.transparent === i.material.transparent && t.material.type === i.material.type && t.material.alphaTest === i.material.alphaTest && t.material.map === i.material.map && t.renderMode === i.renderMode && t.uTileCount === i.uTileCount && t.vTileCount === i.vTileCount && t.instancingGeometry === i.instancingGeometry && t.renderOrder === i.renderOrder && t.layers.mask === i.layers.mask;
  }
  addSystem(t) {
    t._renderer = this;
    const i = t.getRendererSettings();
    for (let r = 0; r < this.batches.length; r++)
      if (zS.equals(this.batches[r].settings, i))
        return this.batches[r].addSystem(t), this.systemToBatchIndex.set(t, r), this.batches[r];
    let n;
    switch (i.renderMode) {
      case Ft.Trail:
        n = new zie(i);
        break;
      case Ft.Mesh:
      case Ft.BillBoard:
      case Ft.VerticalBillBoard:
      case Ft.HorizontalBillBoard:
      case Ft.StretchedBillBoard:
        n = new Bie(i);
        break;
    }
    return n.addSystem(t), this.batches.push(n), this.systemToBatchIndex.set(t, this.batches.length - 1), this.add(n), n;
  }
  deleteSystem(t) {
    const i = this.systemToBatchIndex.get(t);
    i != null && (this.batches[i].removeSystem(t), this.systemToBatchIndex.delete(t));
  }
  updateSystem(t) {
    this.deleteSystem(t), this.addSystem(t);
  }
  update(t) {
    this.systemToBatchIndex.forEach((i, n) => {
      n.update(t);
    });
    for (let i = 0; i < this.batches.length; i++)
      this.batches[i].update();
  }
}
const kie = zS;
var me = /* @__PURE__ */ ((s) => (s[s.Number = 0] = "Number", s[s.Vec2 = 1] = "Vec2", s[s.Vec3 = 2] = "Vec3", s[s.Vec4 = 3] = "Vec4", s[s.Boolean = 4] = "Boolean", s[s.AnyType = 5] = "AnyType", s[s.NullableAnyType = 6] = "NullableAnyType", s[s.EventStream = 7] = "EventStream", s))(me || {});
class Kt {
  constructor(e) {
    S(this, "name");
    S(this, "nodeTypeSignatures", []);
    this.name = e;
  }
  addSignature(e, t, i) {
    this.nodeTypeSignatures.push({
      inputTypes: e,
      outputTypes: t,
      func: i
    });
  }
}
const e8 = new Kt("add");
e8.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0] + t[1];
  }
);
e8.addSignature(
  [me.Vec2, me.Vec2],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].addVectors(t[0], t[1]);
  }
);
e8.addSignature(
  [me.Vec3, me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].addVectors(t[0], t[1]);
  }
);
e8.addSignature(
  [me.Vec4, me.Vec4],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].addVectors(t[0], t[1]);
  }
);
const t8 = new Kt("sub");
t8.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0] - t[1];
  }
);
t8.addSignature(
  [me.Vec2, me.Vec2],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].subVectors(t[0], t[1]);
  }
);
t8.addSignature(
  [me.Vec3, me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].subVectors(t[0], t[1]);
  }
);
t8.addSignature(
  [me.Vec4, me.Vec4],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].subVectors(t[0], t[1]);
  }
);
const i8 = new Kt("mul");
i8.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0] * t[1];
  }
);
i8.addSignature(
  [me.Vec2, me.Number],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].copy(t[0]).multiplyScalar(t[1]);
  }
);
i8.addSignature(
  [me.Vec3, me.Number],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].copy(t[0]).multiplyScalar(t[1]);
  }
);
i8.addSignature(
  [me.Vec4, me.Number],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].copy(t[0]).multiplyScalar(t[1]);
  }
);
const n8 = new Kt("div");
n8.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0] / t[1];
  }
);
n8.addSignature(
  [me.Vec2, me.Number],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].copy(t[0]).divideScalar(t[1]);
  }
);
n8.addSignature(
  [me.Vec3, me.Number],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].copy(t[0]).divideScalar(t[1]);
  }
);
n8.addSignature(
  [me.Vec4, me.Number],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].copy(t[0]).divideScalar(t[1]);
  }
);
const Gie = new Kt("sin");
Gie.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.sin(t[0]);
  }
);
const Hie = new Kt("cos");
Hie.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.cos(t[0]);
  }
);
const Vie = new Kt("tan");
Vie.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.tan(t[0]);
  }
);
const Wie = new Kt("abs");
Wie.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.abs(t[0]);
  }
);
const Xie = new Kt("min");
Xie.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.min(t[0], t[1]);
  }
);
const jie = new Kt("max");
jie.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.max(t[0], t[1]);
  }
);
const kS = new Kt("dot");
kS.addSignature(
  [me.Vec2, me.Vec2],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].dot(t[1]);
  }
);
kS.addSignature(
  [me.Vec3, me.Vec3],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].dot(t[1]);
  }
);
kS.addSignature(
  [me.Vec4, me.Vec4],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].dot(t[1]);
  }
);
const qie = new Kt("cross");
qie.addSignature(
  [me.Vec3, me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].crossVectors(t[0], t[1]);
  }
);
const GS = new Kt("length");
GS.addSignature(
  [me.Vec2],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].length();
  }
);
GS.addSignature(
  [me.Vec3],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].length();
  }
);
GS.addSignature(
  [me.Vec4],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].length();
  }
);
const HS = new Kt("lengthSq");
HS.addSignature(
  [me.Vec2],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].lengthSq();
  }
);
HS.addSignature(
  [me.Vec3],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].lengthSq();
  }
);
HS.addSignature(
  [me.Vec4],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].lengthSq();
  }
);
const VS = new Kt("normalize");
VS.addSignature(
  [me.Vec2],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].copy(t[0]).normalize();
  }
);
VS.addSignature(
  [me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].copy(t[0]).normalize();
  }
);
VS.addSignature(
  [me.Vec4],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].copy(t[0]).normalize();
  }
);
const fN = new Kt("distance");
fN.addSignature(
  [me.Vec2, me.Vec2],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].distanceTo(t[1]);
  }
);
fN.addSignature(
  [me.Vec3, me.Vec3],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0].distanceTo(t[1]);
  }
);
const Yie = new Kt("and");
Yie.addSignature(
  [me.Boolean, me.Boolean],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] && t[1];
  }
);
const Zie = new Kt("or");
Zie.addSignature(
  [me.Boolean, me.Boolean],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] || t[1];
  }
);
const Kie = new Kt("not");
Kie.addSignature(
  [me.Boolean],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = !t[0];
  }
);
const s8 = new Kt("equal");
s8.addSignature(
  [me.Number, me.Number],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] === t[1];
  }
);
s8.addSignature(
  [me.Vec2, me.Vec2],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0].equals(t[1]);
  }
);
s8.addSignature(
  [me.Vec3, me.Vec3],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0].equals(t[1]);
  }
);
s8.addSignature(
  [me.Vec4, me.Vec4],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0].equals(t[1]);
  }
);
const Jie = new Kt("lessThan");
Jie.addSignature(
  [me.Number, me.Number],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] < t[1];
  }
);
const Qie = new Kt("greaterThan");
Qie.addSignature(
  [me.Number, me.Number],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] > t[1];
  }
);
const $ie = new Kt("lessThanOrEqual");
$ie.addSignature(
  [me.Number, me.Number],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] <= t[1];
  }
);
const ene = new Kt("greaterThanOrEqual");
ene.addSignature(
  [me.Number, me.Number],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0] >= t[1];
  }
);
const tne = new Kt("if");
tne.addSignature(
  [me.Boolean, me.AnyType, me.AnyType],
  [me.AnyType],
  (s, e, t, i) => {
    i[0] = t[0] ? t[1] : t[2];
  }
);
const ine = new Kt("number");
ine.addSignature([], [me.Number], (s, e, t, i) => {
  i[0] = e.value;
});
const nne = new Kt("vec2");
nne.addSignature(
  [me.Number, me.Number],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].x = t[0], i[0].y = t[1];
  }
);
const sne = new Kt("vec3");
sne.addSignature(
  [me.Number, me.Number, me.Number],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].x = t[0], i[0].y = t[1], i[0].z = t[2];
  }
);
const rne = new Kt("vec4");
rne.addSignature(
  [me.Number, me.Number, me.Number, me.Number],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].x = t[0], i[0].y = t[1], i[0].z = t[2], i[0].w = t[3];
  }
);
const ane = new Kt("splitVec2");
ane.addSignature(
  [me.Vec2],
  [me.Number, me.Number],
  (s, e, t, i) => {
    i[0] = t[0].x, i[1] = t[0].y;
  }
);
const one = new Kt("splitVec3");
one.addSignature(
  [me.Vec3],
  [me.Number, me.Number, me.Number],
  (s, e, t, i) => {
    i[0] = t[0].x, i[1] = t[0].y, i[2] = t[0].z;
  }
);
const lne = new Kt("splitVec4");
lne.addSignature(
  [me.Vec4],
  [me.Number, me.Number, me.Number, me.Number],
  (s, e, t, i) => {
    i[0] = t[0].x, i[1] = t[0].y, i[2] = t[0].z, i[3] = t[0].w;
  }
);
const cne = new Kt("bool");
cne.addSignature([], [me.Boolean], (s, e, t, i) => {
  i[0] = e.value;
});
const hne = new Kt("particleProperty");
hne.addSignature(
  [me.NullableAnyType],
  [me.NullableAnyType],
  (s, e, t, i) => {
    t[0] !== void 0 && (typeof t[0] == "object" ? s.particle[e.property].copy(t[0]) : s.particle[e.property] = t[0]), s.particle[e.property] !== void 0 && (typeof i[0] == "object" ? i[0].copy(s.particle[e.property]) : i[0] = s.particle[e.property]);
  }
);
const une = new Kt("emit");
une.addSignature([me.EventStream], [], (s, e, t, i) => {
  const n = t[0];
  for (let r = 0; r < n.length; r++)
    s.signal(r, n[r]);
});
const dne = new Kt("graphProperty");
dne.addSignature(
  [me.NullableAnyType],
  [me.NullableAnyType],
  (s, e, t, i) => {
    t[0] !== void 0 && (typeof t[0] == "object" ? s.graph[e.property].copy(t[0]) : s.graph[e.property] = t[0]), s.graph[e.property] !== void 0 && (typeof i[0] == "object" ? i[0].copy(s.graph[e.property]) : i[0] = s.graph[e.property]);
  }
);
const fne = new Kt("startEvent");
fne.addSignature([], [me.EventStream], (s, e, t, i) => {
  i[0] = [{ type: "start" }];
});
const pne = new Kt("repeater");
pne.addSignature(
  [me.EventStream, me.Number],
  [me.EventStream],
  (s, e, t, i) => {
    const n = t[0], r = t[1], a = [];
    for (let o = 0; o < n.length; o++)
      for (let l = 0; l < r; l++)
        a.push(n[o]);
    i[0] = a;
  }
);
const mne = new Kt("time");
mne.addSignature([], [me.Number], (s, e, t, i) => {
  i[0] = s.emissionState.time;
});
const gne = new Kt("delta");
gne.addSignature([], [me.Number], (s, e, t, i) => {
  i[0] = s.delta;
});
const I2 = new Kt("output");
I2.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0];
  }
);
I2.addSignature(
  [me.Vec2],
  [me.Vec2],
  (s, e, t, i) => {
    i[0] = t[0];
  }
);
I2.addSignature(
  [me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    i[0] = t[0];
  }
);
I2.addSignature(
  [me.Vec4],
  [me.Vec4],
  (s, e, t, i) => {
    i[0] = t[0];
  }
);
I2.addSignature(
  [me.Boolean],
  [me.Boolean],
  (s, e, t, i) => {
    i[0] = t[0];
  }
);
const r8 = new Kt("lerp");
r8.addSignature(
  [me.Number, me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = t[0] * (1 - t[2]) + t[1] * t[2];
  }
);
r8.addSignature(
  [me.Vec2, me.Vec2, me.Number],
  [me.Vec2],
  (s, e, t, i) => {
    i[0].lerpVectors(
      t[0],
      t[1],
      t[2]
    );
  }
);
r8.addSignature(
  [me.Vec3, me.Vec3, me.Number],
  [me.Vec3],
  (s, e, t, i) => {
    i[0].lerpVectors(
      t[0],
      t[1],
      t[2]
    );
  }
);
r8.addSignature(
  [me.Vec4, me.Vec4, me.Number],
  [me.Vec4],
  (s, e, t, i) => {
    i[0].lerpVectors(
      t[0],
      t[1],
      t[2]
    );
  }
);
const vne = (s) => 1 / Math.sqrt(2 * Math.PI) * Math.exp(s * s * -0.5), yne = new Kt("normDistrib");
yne.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = vne(t[0]);
  }
);
const xne = new Kt("normcdf");
xne.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    let n = t[0];
    const r = 0.254829592, a = -0.284496736, o = 1.421413741, l = -1.453152027, c = 1.061405429, h = 0.3275911;
    let u = 1;
    n < 0 && (u = -1), n = Math.abs(n) / Math.sqrt(2);
    const d = 1 / (1 + h * n), f = 1 - ((((c * d + l) * d + o) * d + a) * d + r) * d * Math.exp(-n * n);
    i[0] = 0.5 * (1 + u * f);
  }
);
const Ane = new Kt("normcdfInv"), OC = (s) => {
  const e = [2.515517, 0.802853, 0.010328], t = [1.432788, 0.189269, 1308e-6];
  return s - ((e[2] * s + e[1]) * s + e[0]) / (((t[2] * s + t[1]) * s + t[0]) * s + 1);
}, jo = (s) => s < 0.5 ? -OC(Math.sqrt(-2 * Math.log(s))) : OC(Math.sqrt(-2 * Math.log(1 - s)));
Ane.addSignature(
  [me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = jo(t[0]);
  }
);
const bne = new Kt("clamp");
bne.addSignature(
  [me.Number, me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.max(Math.min(t[0], t[2]), t[1]);
  }
);
const Mne = new Kt("smoothstep");
Mne.addSignature(
  [me.Number, me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    const n = Math.max(Math.min(t[0], t[2]), t[1]);
    i[0] = n * n * (3 - 2 * n);
  }
);
const a8 = new Kt("random");
a8.addSignature(
  [me.Number, me.Number],
  [me.Number],
  (s, e, t, i) => {
    i[0] = Math.random() * (t[1] - t[0]) + t[0];
  }
);
a8.addSignature(
  [me.Vec2, me.Vec2],
  [me.Vec2],
  (s, e, t, i) => {
    let n = Math.random();
    i[0].lerpVectors(t[0], t[1], n);
  }
);
a8.addSignature(
  [me.Vec3, me.Vec3],
  [me.Vec3],
  (s, e, t, i) => {
    let n = Math.random();
    i[0].lerpVectors(t[0], t[1], n);
  }
);
a8.addSignature(
  [me.Vec4, me.Vec4],
  [me.Vec4],
  (s, e, t, i) => {
    let n = Math.random();
    i[0].lerpVectors(t[0], t[1], n);
  }
);
const WS = new Kt("vrand");
WS.addSignature([], [me.Vec2], (s, e, t, i) => {
  let n = jo(Math.random()), r = jo(Math.random());
  const a = Math.sqrt(n * n + r * r);
  i[0].set(n / a, r / a);
});
WS.addSignature([], [me.Vec3], (s, e, t, i) => {
  let n = jo(Math.random()), r = jo(Math.random()), a = jo(Math.random());
  const o = Math.sqrt(n * n + r * r + a * a);
  i[0].set(n / o, r / o, a / o);
});
WS.addSignature([], [me.Vec4], (s, e, t, i) => {
  let n = jo(Math.random()), r = jo(Math.random()), a = jo(Math.random()), o = jo(Math.random());
  const l = Math.sqrt(n * n + r * r + a * a + o * o);
  i[0].set(n / l, r / l, a / l, o / l);
});
const Sne = new Kt("bsdf");
Sne.addSignature(
  [me.Vec3, me.Vec3, me.Vec3, me.Number],
  [],
  (s, e, t, i) => {
  }
);
new _(0, 0, 1);
new _();
new _();
new $n(1, 1, 1, 1);
class wne {
  constructor(e) {
    S(this, "_camera");
    S(this, "_scene");
    S(this, "_renderLoop");
    S(this, "_copyMaterial");
    S(this, "_mesh");
    S(this, "_baseCopyMaterial");
    S(this, "_copyUniforms");
    this._camera = new Xn(-1, 1, 1, -1, 0, 1), this._scene = new Da(), this._renderLoop = e, this._baseCopyMaterial = new Jt({
      vertexShader: ia.vertexShader,
      fragmentShader: ia.fragmentShader,
      premultipliedAlpha: !0,
      transparent: !0,
      blending: oo,
      depthTest: !1,
      depthWrite: !1
    }), this._copyMaterial = this._baseCopyMaterial.clone(), this._copyUniforms = this._copyMaterial.uniforms, this._mesh = new Re(new $n(2, 2), this._copyMaterial), this._scene.add(this._mesh);
  }
  dispose() {
    this._copyMaterial.dispose();
  }
  copy(e, t, i = this._copyMaterial) {
    this._mesh.material = i, this._mesh.material.uniforms.tDiffuse = { value: e.texture }, this._mesh.material.uniforms.opacity = { value: 1 };
    const { threeRenderer: n } = this._renderLoop;
    n.setClearColor(0, 0), n.setRenderTarget(t), n.render(this._scene, this._camera);
  }
}
class Ene extends Ao {
  constructor(t, i) {
    super();
    S(this, "_baseReprojectionMaterial");
    S(this, "_jitterIndex");
    S(this, "_jitterOffsets");
    S(this, "_scratchBuffer");
    S(this, "_vecRenderTarget");
    S(this, "_targetCopier");
    S(this, "_vecRenderer");
    S(this, "_oldFrameTarget");
    S(this, "_reprojectionMaterial");
    S(this, "_numFrames", 3);
    S(this, "_colorScale", 10);
    S(this, "_weight", 0.3);
    S(this, "taaGroup", new ci());
    // private _speed: Vector2 = new Vector2(0.0, 0.2);
    S(this, "_speed", new Se(0.3, 0.4));
    S(this, "opacity", 0.1);
    this.renderer = t, this.scene = i, this.renderToScreen = !1, this._baseReprojectionMaterial = new Jt({
      transparent: !0,
      blending: oo,
      depthTest: !1,
      depthWrite: !1,
      vertexShader: cC.vertexShader,
      fragmentShader: cC.fragmentShader
    }), this._reprojectionMaterial = this._baseReprojectionMaterial.clone(), this._targetCopier = new wne(t), this._vecRenderer = new Tee(t), this._jitterIndex = 0, this._jitterOffsets = this._generateHaltonJiters(3);
    const { x: n, y: r } = t.getSize();
    this._scratchBuffer = new Xi(n, r, {
      format: dn,
      type: xn
    }), this._vecRenderTarget = new Xi(n, r, {
      format: dn,
      type: xn
    });
  }
  get colorScale() {
    return this._colorScale;
  }
  set colorScale(t) {
    this._colorScale = t;
  }
  get weight() {
    return this._weight;
  }
  set weight(t) {
    this._weight = t;
  }
  updateConfig(t) {
  }
  render(t, i, n) {
    this.taaGroup.children.length = 0, this.taaGroup.children.push(
      ...this.scene.children.filter((v) => v.eType !== "World")
    );
    const r = t.autoClear;
    t.autoClear = !0;
    const { x: a, y: o } = this.renderer.getSize(), l = this.renderer.mainCamera, c = t.getClearColor(new _e()), h = t.getClearAlpha();
    if (!this._oldFrameTarget) {
      this._oldFrameTarget = new Xi(a, o, {
        format: dn,
        type: xn
      }), t.setClearColor(0, 0), t.setRenderTarget(this._oldFrameTarget), t.render(this.taaGroup, l);
      for (let v = 0; v < this._jitterOffsets.length - 1; v++)
        this.render(t, i, n);
    }
    this._vecRenderer.renderMotionMap(this._colorScale, this._vecRenderTarget, this.taaGroup);
    const [u, d] = this._jitterOffsets[this._jitterIndex];
    l.projectionMatrix.elements[8] = u / a, l.projectionMatrix.elements[9] = d / o, t.setClearColor(0, 0), t.setRenderTarget(n), l.updateProjectionMatrix(), this._jitterIndex = (this._jitterIndex + 1) % this._jitterOffsets.length;
    const { uniforms: f } = this._reprojectionMaterial;
    f.numFrames = { value: this._numFrames }, f.opacity = { value: this.opacity }, f.tMotion = { value: this._vecRenderTarget.texture }, f.tLastFrame = { value: this._oldFrameTarget.texture }, f.height = { value: n.height }, f.width = { value: n.width }, f.speed = { value: this._speed }, f.weight = { value: this._weight }, this._targetCopier.copy(n, this._scratchBuffer, this._reprojectionMaterial), this._targetCopier.copy(this._scratchBuffer, this._oldFrameTarget);
    const p = this.renderToScreen ? null : i;
    p && this._targetCopier.copy(this._oldFrameTarget, p), t.setClearColor(c), t.setClearAlpha(h), t.autoClear = r;
  }
  _generateHaltonJiters(t) {
    const i = [];
    for (let n = 1; n <= t; n++)
      i.push([(this._haltonNumber(2, n) - 0.5) * 2, (this._haltonNumber(3, n) - 0.5) * 2]);
    return i;
  }
  _haltonNumber(t, i) {
    let n = 0, r = 1;
    for (; i > 0; )
      r /= t, n += r * (i % t), i = Math.floor(i / t);
    return n;
  }
  dispose() {
    this._oldFrameTarget && this._oldFrameTarget.dispose(), this._reprojectionMaterial.dispose(), this._scratchBuffer.dispose(), this._targetCopier.dispose(), this._vecRenderer.dispose(), this._vecRenderTarget.dispose();
  }
}
var pN = /* @__PURE__ */ ((s) => (s[s.Default = 0] = "Default", s[s.SSAO = 1] = "SSAO", s[s.Blur = 2] = "Blur", s[s.Beauty = 3] = "Beauty", s[s.Depth = 4] = "Depth", s[s.Normal = 5] = "Normal", s))(pN || {});
const Z0 = class Z0 extends Ao {
  constructor(t, i, n = 512, r = 512) {
    super();
    S(this, "kernelRadius", 8);
    S(this, "kernelSize", 512);
    S(this, "kernel", []);
    S(this, "noiseTexture", null);
    S(this, "output", 0);
    S(this, "minDistance", 5e-3);
    S(this, "maxDistance", 0.1);
    S(this, "blur", 2);
    S(this, "depthOffset", 0);
    S(this, "intensity", 1);
    S(this, "_visibilityCache", /* @__PURE__ */ new Map());
    S(this, "beautyRenderTarget");
    S(this, "normalRenderTarget");
    S(this, "ssaoRenderTarget");
    S(this, "blurRenderTarget");
    S(this, "ssaoMaterial");
    S(this, "normalMaterial");
    S(this, "blurMaterial");
    S(this, "depthRenderMaterial");
    S(this, "copyMaterial");
    S(this, "fsQuad");
    S(this, "originalClearColor");
    this.scene = t, this.camera = i, this.width = n, this.height = r, this.clear = !0, this.generateSampleKernel(), this.generateRandomKernelRotations();
    const a = new r2(n, r);
    a.format = yl, a.type = vl, this.beautyRenderTarget = new Xi(this.width, this.height), this.normalRenderTarget = new Xi(this.width, this.height, {
      minFilter: Ni,
      magFilter: Ni,
      depthTexture: a
    }), this.ssaoRenderTarget = new Xi(this.width, this.height), this.blurRenderTarget = this.ssaoRenderTarget.clone(), Sm === void 0 && console.error("SSAOPass: The pass relies on SSAOShader."), this.ssaoMaterial = new Jt({
      defines: { ...Sm.defines },
      uniforms: rs.clone(Sm.uniforms),
      vertexShader: Sm.vertexShader,
      fragmentShader: Sm.fragmentShader,
      blending: vn
    }), this.ssaoMaterial.uniforms.tDiffuse.value = this.beautyRenderTarget.texture, this.ssaoMaterial.uniforms.tNormal.value = this.normalRenderTarget.texture, this.ssaoMaterial.uniforms.tDepth.value = this.normalRenderTarget.depthTexture, this.ssaoMaterial.uniforms.tNoise.value = this.noiseTexture, this.ssaoMaterial.uniforms.kernel.value = this.kernel, this.ssaoMaterial.uniforms.cameraNear.value = this.camera.near, this.ssaoMaterial.uniforms.cameraFar.value = this.camera.far, this.ssaoMaterial.uniforms.resolution.value.set(this.width, this.height), this.ssaoMaterial.uniforms.cameraProjectionMatrix.value.copy(this.camera.projectionMatrix), this.ssaoMaterial.uniforms.cameraInverseProjectionMatrix.value.copy(
      this.camera.projectionMatrixInverse
    ), this.normalMaterial = new P4(), this.normalMaterial.blending = vn, this.blurMaterial = new Jt({
      // defines: {  ...SSAOBlurShader.defines },
      uniforms: rs.clone(I5.uniforms),
      vertexShader: I5.vertexShader,
      fragmentShader: I5.fragmentShader
    }), this.blurMaterial.uniforms.tDiffuse.value = this.ssaoRenderTarget.texture, this.blurMaterial.uniforms.blur.value = this.blur, this.blurMaterial.uniforms.resolution.value.set(this.width, this.height), this.ssaoMaterial.uniforms.intensity.value = this.intensity, this.depthRenderMaterial = new Jt({
      defines: { ...Wv.defines },
      uniforms: rs.clone(Wv.uniforms),
      vertexShader: Wv.vertexShader,
      fragmentShader: Wv.fragmentShader,
      blending: vn
    }), this.depthRenderMaterial.uniforms.tDepth.value = this.normalRenderTarget.depthTexture, this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near, this.depthRenderMaterial.uniforms.depthOffset.value = this.depthOffset, this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far, this.copyMaterial = new Jt({
      uniforms: rs.clone(ia.uniforms),
      vertexShader: ia.vertexShader,
      fragmentShader: ia.fragmentShader,
      transparent: !0,
      depthTest: !1,
      depthWrite: !1,
      blendSrc: f4,
      blendDst: ep,
      blendEquation: Ma,
      blendSrcAlpha: d4,
      blendDstAlpha: ep,
      blendEquationAlpha: Ma
    }), this.fsQuad = new Eee(void 0), this.originalClearColor = new _e();
  }
  generateSampleKernel() {
    const { kernelSize: t, kernel: i } = this;
    for (let n = 0; n < t; n++) {
      const r = new _();
      r.x = Math.random() * 2 - 1, r.y = Math.random() * 2 - 1, r.z = Math.random(), r.normalize();
      let a = n / t;
      a = nt.lerp(0.1, 1, a * a), r.multiplyScalar(a), i.push(r);
    }
  }
  generateRandomKernelRotations() {
    const n = new oN(), r = 4 * 4, a = new Float32Array(r);
    for (let o = 0; o < r; o++) {
      const l = Math.random() * 2 - 1, c = Math.random() * 2 - 1, h = 0;
      a[o] = n.noise3d(l, c, h);
    }
    this.noiseTexture = new Qn(a, 4, 4, Vu, xn), this.noiseTexture.wrapS = un, this.noiseTexture.wrapT = un, this.noiseTexture.needsUpdate = !0;
  }
  render(t, i) {
    t.setRenderTarget(this.beautyRenderTarget), t.clear(), t.render(this.scene, this.camera), console.log("renderer "), this.depthRenderMaterial.uniforms.depthOffset.value = this.depthOffset, this.blurMaterial.uniforms.blur.value = this.blur, this.overrideVisibility(), this.renderOverride(
      t,
      this.normalMaterial,
      this.normalRenderTarget,
      new _e(7829503),
      1
    ), this.restoreVisibility(), this.ssaoMaterial.uniforms.kernelRadius.value = this.kernelRadius, this.ssaoMaterial.uniforms.minDistance.value = this.minDistance, this.ssaoMaterial.uniforms.maxDistance.value = this.maxDistance, this.ssaoMaterial.uniforms.intensity.value = this.intensity, this.renderPass(t, this.ssaoMaterial, this.ssaoRenderTarget), this.renderPass(t, this.blurMaterial, this.blurRenderTarget);
    const n = this.renderToScreen ? null : i;
    switch (this.output) {
      case Z0.OUTPUT.SSAO:
        this.copyMaterial.uniforms.tDiffuse.value = this.ssaoRenderTarget.texture, this.copyMaterial.blending = vn, n && this.renderPass(t, this.copyMaterial, n);
        break;
      case Z0.OUTPUT.Blur:
        this.copyMaterial.uniforms.tDiffuse.value = this.blurRenderTarget.texture, this.copyMaterial.blending = vn, n && this.renderPass(t, this.copyMaterial, n);
        break;
      case Z0.OUTPUT.Beauty:
        this.copyMaterial.uniforms.tDiffuse.value = this.beautyRenderTarget.texture, this.copyMaterial.blending = vn, n && this.renderPass(t, this.copyMaterial, n);
        break;
      case Z0.OUTPUT.Depth:
        n && n && this.renderPass(t, this.depthRenderMaterial, n);
        break;
      case Z0.OUTPUT.Normal:
        this.copyMaterial.uniforms.tDiffuse.value = this.normalRenderTarget.texture, this.copyMaterial.blending = vn, n && this.renderPass(t, this.copyMaterial, n);
        break;
      case Z0.OUTPUT.Default:
        this.copyMaterial.uniforms.tDiffuse.value = this.beautyRenderTarget.texture, this.copyMaterial.blending = vn, n && this.renderPass(t, this.copyMaterial, n), this.copyMaterial.uniforms.tDiffuse.value = this.blurRenderTarget.texture, this.copyMaterial.blending = u4, n && this.renderPass(t, this.copyMaterial, n);
        break;
      default:
        console.warn("THREE.SSAOPass: Unknown output type.");
    }
  }
  renderPass(t, i, n, r, a) {
    const o = t;
    o.getClearColor(this.originalClearColor);
    const l = o.getClearAlpha(), c = o.autoClear;
    o.setRenderTarget(n), o.autoClear = !1, r != null && (o.setClearColor(r), o.setClearAlpha(a || 0), o.clear()), this.fsQuad.material = i, this.fsQuad.render(o), o.autoClear = c, o.setClearColor(this.originalClearColor), o.setClearAlpha(l);
  }
  overrideVisibility() {
    const { scene: t } = this, i = this._visibilityCache;
    t.traverse((n) => {
      const r = n;
      i.set(r, r.visible), (r.isPoints || r.isLine) && (r.visible = !1);
    });
  }
  renderOverride(t, i, n, r, a) {
    const o = t;
    o.getClearColor(this.originalClearColor);
    const l = o.getClearAlpha(), c = o.autoClear;
    o.setRenderTarget(n), o.autoClear = !1, r !== void 0 && a !== null && (t.setClearColor(r), t.setClearAlpha(a || 0), t.clear()), this.scene.overrideMaterial = i, o.render(this.scene, this.camera), this.scene.overrideMaterial = null, o.autoClear = c, o.setClearColor(this.originalClearColor), o.setClearAlpha(l);
  }
  restoreVisibility() {
    const { scene: t } = this, i = this._visibilityCache;
    t.traverse((n) => {
      const r = n, a = i.get(r);
      r.visible = a;
    }), i.clear();
  }
  dispose() {
    this.beautyRenderTarget.dispose(), this.normalRenderTarget.dispose(), this.ssaoRenderTarget.dispose(), this.blurRenderTarget.dispose(), this.normalMaterial.dispose(), this.blurMaterial.dispose(), this.copyMaterial.dispose(), this.depthRenderMaterial.dispose(), this.fsQuad.dispose();
  }
};
S(Z0, "OUTPUT", pN);
class _c extends Ao {
  constructor(e, t, i, n) {
    super(), this.renderScene = t, this.renderCamera = i, this.selectedObjects = n !== void 0 ? n : [], this.visibleEdgeColor = new _e(1, 1, 1), this.hiddenEdgeColor = new _e(0.1, 0.04, 0.02), this.edgeGlow = 0, this.usePatternTexture = !1, this.edgeThickness = 1, this.edgeStrength = 3, this.downSampleRatio = 2, this.pulsePeriod = 0, this._visibilityCache = /* @__PURE__ */ new Map(), this.resolution = e !== void 0 ? new Se(e.x, e.y) : new Se(256, 256);
    const r = Math.round(this.resolution.x / this.downSampleRatio), a = Math.round(this.resolution.y / this.downSampleRatio);
    this.renderTargetMaskBuffer = new Xi(this.resolution.x, this.resolution.y), this.renderTargetMaskBuffer.texture.name = "OutlinePass.mask", this.renderTargetMaskBuffer.texture.generateMipmaps = !1, this.depthMaterial = new w4(), this.depthMaterial.side = ui, this.depthMaterial.depthPacking = LM, this.depthMaterial.blending = vn, this.prepareMaskMaterial = this.getPrepareMaskMaterial(), this.prepareMaskMaterial.side = ui, this.prepareMaskMaterial.fragmentShader = h(
      this.prepareMaskMaterial.fragmentShader,
      this.renderCamera
    ), this.renderTargetDepthBuffer = new Xi(this.resolution.x, this.resolution.y, {
      type: on
    }), this.renderTargetDepthBuffer.texture.name = "OutlinePass.depth", this.renderTargetDepthBuffer.texture.generateMipmaps = !1, this.renderTargetMaskDownSampleBuffer = new Xi(r, a, {
      type: on
    }), this.renderTargetMaskDownSampleBuffer.texture.name = "OutlinePass.depthDownSample", this.renderTargetMaskDownSampleBuffer.texture.generateMipmaps = !1, this.renderTargetBlurBuffer1 = new Xi(r, a, { type: on }), this.renderTargetBlurBuffer1.texture.name = "OutlinePass.blur1", this.renderTargetBlurBuffer1.texture.generateMipmaps = !1, this.renderTargetBlurBuffer2 = new Xi(
      Math.round(r / 2),
      Math.round(a / 2),
      { type: on }
    ), this.renderTargetBlurBuffer2.texture.name = "OutlinePass.blur2", this.renderTargetBlurBuffer2.texture.generateMipmaps = !1, this.edgeDetectionMaterial = this.getEdgeDetectionMaterial(), this.renderTargetEdgeBuffer1 = new Xi(r, a, { type: on }), this.renderTargetEdgeBuffer1.texture.name = "OutlinePass.edge1", this.renderTargetEdgeBuffer1.texture.generateMipmaps = !1, this.renderTargetEdgeBuffer2 = new Xi(
      Math.round(r / 2),
      Math.round(a / 2),
      { type: on }
    ), this.renderTargetEdgeBuffer2.texture.name = "OutlinePass.edge2", this.renderTargetEdgeBuffer2.texture.generateMipmaps = !1;
    const o = 4, l = 4;
    this.separableBlurMaterial1 = this.getSeperableBlurMaterial(o), this.separableBlurMaterial1.uniforms.texSize.value.set(r, a), this.separableBlurMaterial1.uniforms.kernelRadius.value = 1, this.separableBlurMaterial2 = this.getSeperableBlurMaterial(l), this.separableBlurMaterial2.uniforms.texSize.value.set(
      Math.round(r / 2),
      Math.round(a / 2)
    ), this.separableBlurMaterial2.uniforms.kernelRadius.value = l, this.overlayMaterial = this.getOverlayMaterial();
    const c = ia;
    this.copyUniforms = rs.clone(c.uniforms), this.materialCopy = new Jt({
      uniforms: this.copyUniforms,
      vertexShader: c.vertexShader,
      fragmentShader: c.fragmentShader,
      blending: vn,
      depthTest: !1,
      depthWrite: !1
    }), this.enabled = !0, this.needsSwap = !1, this._oldClearColor = new _e(), this.oldClearAlpha = 1, this.fsQuad = new P2(null), this.tempPulseColor1 = new _e(), this.tempPulseColor2 = new _e(), this.textureMatrix = new et();
    function h(u, d) {
      const f = d.isPerspectiveCamera ? "perspective" : "orthographic";
      return u.replace(/DEPTH_TO_VIEW_Z/g, f + "DepthToViewZ");
    }
    this.outLineAlpha = 1;
  }
  dispose() {
    this.renderTargetMaskBuffer.dispose(), this.renderTargetDepthBuffer.dispose(), this.renderTargetMaskDownSampleBuffer.dispose(), this.renderTargetBlurBuffer1.dispose(), this.renderTargetBlurBuffer2.dispose(), this.renderTargetEdgeBuffer1.dispose(), this.renderTargetEdgeBuffer2.dispose(), this.depthMaterial.dispose(), this.prepareMaskMaterial.dispose(), this.edgeDetectionMaterial.dispose(), this.separableBlurMaterial1.dispose(), this.separableBlurMaterial2.dispose(), this.overlayMaterial.dispose(), this.materialCopy.dispose(), this.fsQuad.dispose();
  }
  setSize(e, t) {
    this.renderTargetMaskBuffer.setSize(e, t), this.renderTargetDepthBuffer.setSize(e, t);
    let i = Math.round(e / this.downSampleRatio), n = Math.round(t / this.downSampleRatio);
    this.renderTargetMaskDownSampleBuffer.setSize(i, n), this.renderTargetBlurBuffer1.setSize(i, n), this.renderTargetEdgeBuffer1.setSize(i, n), this.separableBlurMaterial1.uniforms.texSize.value.set(i, n), i = Math.round(i / 2), n = Math.round(n / 2), this.renderTargetBlurBuffer2.setSize(i, n), this.renderTargetEdgeBuffer2.setSize(i, n), this.separableBlurMaterial2.uniforms.texSize.value.set(i, n);
  }
  changeVisibilityOfSelectedObjects(e) {
    const t = this._visibilityCache;
    function i(n) {
      n.isMesh && (e === !0 ? n.visible = t.get(n) : (t.set(n, n.visible), n.visible = e));
    }
    for (let n = 0; n < this.selectedObjects.length; n++)
      this.selectedObjects[n].traverse(i);
  }
  changeVisibilityOfNonSelectedObjects(e) {
    const t = this._visibilityCache, i = [];
    function n(a) {
      a.isMesh && i.push(a);
    }
    for (let a = 0; a < this.selectedObjects.length; a++)
      this.selectedObjects[a].traverse(n);
    function r(a) {
      if (a.isMesh || a.isSprite || a.isTransformControls) {
        let o = !1;
        for (let l = 0; l < i.length; l++)
          if (i[l].id === a.id) {
            o = !0;
            break;
          }
        if (o === !1) {
          const l = a.visible;
          (e === !1 || t.get(a) === !0) && (a.visible = e), t.set(a, l);
        }
      } else
        (a.isPoints || a.isLine) && (e === !0 ? a.visible = t.get(a) : (t.set(a, a.visible), a.visible = e));
    }
    this.renderScene.traverse(r);
  }
  updateTextureMatrix() {
    this.textureMatrix.set(
      0.5,
      0,
      0,
      0.5,
      0,
      0.5,
      0,
      0.5,
      0,
      0,
      0.5,
      0.5,
      0,
      0,
      0,
      1
    ), this.textureMatrix.multiply(this.renderCamera.projectionMatrix), this.textureMatrix.multiply(this.renderCamera.matrixWorldInverse);
  }
  render(e, t, i, n, r) {
    if (this.selectedObjects.length > 0) {
      e.getClearColor(this._oldClearColor), this.oldClearAlpha = e.getClearAlpha();
      const a = e.autoClear;
      e.autoClear = !1, r && e.state.buffers.stencil.setTest(!1), e.setClearColor(16777215, 1), this.changeVisibilityOfSelectedObjects(!1);
      const o = this.renderScene.background;
      if (this.renderScene.background = null, this.renderScene.overrideMaterial = this.depthMaterial, e.setRenderTarget(this.renderTargetDepthBuffer), e.clear(), e.render(this.renderScene, this.renderCamera), this.changeVisibilityOfSelectedObjects(!0), this._visibilityCache.clear(), this.updateTextureMatrix(), this.changeVisibilityOfNonSelectedObjects(!1), this.renderScene.overrideMaterial = this.prepareMaskMaterial, this.prepareMaskMaterial.uniforms.cameraNearFar.value.set(
        this.renderCamera.near,
        this.renderCamera.far
      ), this.prepareMaskMaterial.uniforms.depthTexture.value = this.renderTargetDepthBuffer.texture, this.prepareMaskMaterial.uniforms.textureMatrix.value = this.textureMatrix, e.setRenderTarget(this.renderTargetMaskBuffer), e.clear(), e.render(this.renderScene, this.renderCamera), this.renderScene.overrideMaterial = null, this.changeVisibilityOfNonSelectedObjects(!0), this._visibilityCache.clear(), this.renderScene.background = o, this.fsQuad.material = this.materialCopy, this.copyUniforms.tDiffuse.value = this.renderTargetMaskBuffer.texture, e.setRenderTarget(this.renderTargetMaskDownSampleBuffer), e.clear(), this.fsQuad.render(e), this.tempPulseColor1.copy(this.visibleEdgeColor), this.tempPulseColor2.copy(this.hiddenEdgeColor), this.pulsePeriod > 0) {
        const l = 0.625 + Math.cos(performance.now() * 0.01 / this.pulsePeriod) * 0.75 / 2;
        this.tempPulseColor1.multiplyScalar(l), this.tempPulseColor2.multiplyScalar(l);
      }
      this.fsQuad.material = this.edgeDetectionMaterial, this.edgeDetectionMaterial.uniforms.maskTexture.value = this.renderTargetMaskDownSampleBuffer.texture, this.edgeDetectionMaterial.uniforms.texSize.value.set(
        this.renderTargetMaskDownSampleBuffer.width,
        this.renderTargetMaskDownSampleBuffer.height
      ), this.edgeDetectionMaterial.uniforms.visibleEdgeColor.value = this.tempPulseColor1, this.edgeDetectionMaterial.uniforms.hiddenEdgeColor.value = this.tempPulseColor2, this.edgeDetectionMaterial.uniforms.outLineAlpha.value = this.outLineAlpha, e.setRenderTarget(this.renderTargetEdgeBuffer1), e.clear(), this.fsQuad.render(e), this.fsQuad.material = this.separableBlurMaterial1, this.separableBlurMaterial1.uniforms.colorTexture.value = this.renderTargetEdgeBuffer1.texture, this.separableBlurMaterial1.uniforms.direction.value = _c.BlurDirectionX, this.separableBlurMaterial1.uniforms.kernelRadius.value = this.edgeThickness, e.setRenderTarget(this.renderTargetBlurBuffer1), e.clear(), this.fsQuad.render(e), this.separableBlurMaterial1.uniforms.colorTexture.value = this.renderTargetBlurBuffer1.texture, this.separableBlurMaterial1.uniforms.direction.value = _c.BlurDirectionY, e.setRenderTarget(this.renderTargetEdgeBuffer1), e.clear(), this.fsQuad.render(e), this.fsQuad.material = this.separableBlurMaterial2, this.separableBlurMaterial2.uniforms.colorTexture.value = this.renderTargetEdgeBuffer1.texture, this.separableBlurMaterial2.uniforms.direction.value = _c.BlurDirectionX, e.setRenderTarget(this.renderTargetBlurBuffer2), e.clear(), this.fsQuad.render(e), this.separableBlurMaterial2.uniforms.colorTexture.value = this.renderTargetBlurBuffer2.texture, this.separableBlurMaterial2.uniforms.direction.value = _c.BlurDirectionY, e.setRenderTarget(this.renderTargetEdgeBuffer2), e.clear(), this.fsQuad.render(e), this.fsQuad.material = this.overlayMaterial, this.overlayMaterial.uniforms.maskTexture.value = this.renderTargetMaskBuffer.texture, this.overlayMaterial.uniforms.edgeTexture1.value = this.renderTargetEdgeBuffer1.texture, this.overlayMaterial.uniforms.edgeTexture2.value = this.renderTargetEdgeBuffer2.texture, this.overlayMaterial.uniforms.patternTexture.value = this.patternTexture, this.overlayMaterial.uniforms.edgeStrength.value = this.edgeStrength, this.overlayMaterial.uniforms.edgeGlow.value = this.edgeGlow, this.overlayMaterial.uniforms.usePatternTexture.value = this.usePatternTexture, r && e.state.buffers.stencil.setTest(!0), e.setRenderTarget(i), this.fsQuad.render(e), e.setClearColor(this._oldClearColor, this.oldClearAlpha), e.autoClear = a;
    }
    this.renderToScreen && (this.fsQuad.material = this.materialCopy, this.copyUniforms.tDiffuse.value = i.texture, e.setRenderTarget(null), this.fsQuad.render(e));
  }
  getPrepareMaskMaterial() {
    return new Jt({
      uniforms: {
        depthTexture: { value: null },
        cameraNearFar: { value: new Se(0.5, 0.5) },
        textureMatrix: { value: null }
      },
      vertexShader: `#include <morphtarget_pars_vertex>
				#include <skinning_pars_vertex>

				varying vec4 projTexCoord;
				varying vec4 vPosition;
				uniform mat4 textureMatrix;

				void main() {

					#include <skinbase_vertex>
					#include <begin_vertex>
					#include <morphtarget_vertex>
					#include <skinning_vertex>
					#include <project_vertex>

					vPosition = mvPosition;

					vec4 worldPosition = vec4( transformed, 1.0 );

					#ifdef USE_INSTANCING

						worldPosition = instanceMatrix * worldPosition;

					#endif
					
					worldPosition = modelMatrix * worldPosition;

					projTexCoord = textureMatrix * worldPosition;

				}`,
      fragmentShader: `#include <packing>
				varying vec4 vPosition;
				varying vec4 projTexCoord;
				uniform sampler2D depthTexture;
				uniform vec2 cameraNearFar;

				void main() {

					float depth = unpackRGBAToDepth(texture2DProj( depthTexture, projTexCoord ));
					float viewZ = - DEPTH_TO_VIEW_Z( depth, cameraNearFar.x, cameraNearFar.y );
					float depthTest = (-vPosition.z > viewZ) ? 1.0 : 0.0;
					gl_FragColor = vec4(0.0, depthTest, 1.0, 1.0);

				}`
    });
  }
  getEdgeDetectionMaterial() {
    return new Jt({
      uniforms: {
        maskTexture: { value: null },
        texSize: { value: new Se(0.5, 0.5) },
        visibleEdgeColor: { value: new _(1, 1, 1) },
        hiddenEdgeColor: { value: new _(1, 1, 1) },
        outLineAlpha: { value: this.outLineAlpha }
      },
      vertexShader: `varying vec2 vUv;

				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `varying vec2 vUv;

				uniform sampler2D maskTexture;
				uniform vec2 texSize;
				uniform vec3 visibleEdgeColor;
				uniform vec3 hiddenEdgeColor;
        
        uniform float outLineAlpha;

				void main() {
					vec2 invSize = 1.0 / texSize;
					vec4 uvOffset = vec4(1.0, 0.0, 0.0, 1.0) * vec4(invSize, invSize);
					vec4 c1 = texture2D( maskTexture, vUv + uvOffset.xy);
					vec4 c2 = texture2D( maskTexture, vUv - uvOffset.xy);
					vec4 c3 = texture2D( maskTexture, vUv + uvOffset.yw);
					vec4 c4 = texture2D( maskTexture, vUv - uvOffset.yw);
					float diff1 = (c1.r - c2.r)*0.5;
					float diff2 = (c3.r - c4.r)*0.5;
					float d = length( vec2(diff1, diff2) );
					float a1 = min(c1.g, c2.g);
					float a2 = min(c3.g, c4.g);
					float visibilityFactor = min(a1, a2);
					vec3 edgeColor = 1.0 - visibilityFactor > 0.001 ? visibleEdgeColor : hiddenEdgeColor;
					gl_FragColor = vec4(edgeColor, outLineAlpha) * vec4(d);
				}`
    });
  }
  getSeperableBlurMaterial(e) {
    return new Jt({
      defines: {
        MAX_RADIUS: e
      },
      uniforms: {
        colorTexture: { value: null },
        texSize: { value: new Se(0.5, 0.5) },
        direction: { value: new Se(0.5, 0.5) },
        kernelRadius: { value: 1 }
      },
      vertexShader: `varying vec2 vUv;

				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `#include <common>
				varying vec2 vUv;
				uniform sampler2D colorTexture;
				uniform vec2 texSize;
				uniform vec2 direction;
				uniform float kernelRadius;

				float gaussianPdf(in float x, in float sigma) {
					return 0.39894 * exp( -0.5 * x * x/( sigma * sigma))/sigma;
				}

				void main() {
					vec2 invSize = 1.0 / texSize;
					float sigma = kernelRadius/2.0;
					float weightSum = gaussianPdf(0.0, sigma);
					vec4 diffuseSum = texture2D( colorTexture, vUv) * weightSum;
					vec2 delta = direction * invSize * kernelRadius/float(MAX_RADIUS);
					vec2 uvOffset = delta;
					for( int i = 1; i <= MAX_RADIUS; i ++ ) {
						float x = kernelRadius * float(i) / float(MAX_RADIUS);
						float w = gaussianPdf(x, sigma);
						vec4 sample1 = texture2D( colorTexture, vUv + uvOffset);
						vec4 sample2 = texture2D( colorTexture, vUv - uvOffset);
						diffuseSum += ((sample1 + sample2) * w);
						weightSum += (2.0 * w);
						uvOffset += delta;
					}
					gl_FragColor = diffuseSum/weightSum;
				}`
    });
  }
  getOverlayMaterial() {
    return new Jt({
      uniforms: {
        maskTexture: { value: null },
        edgeTexture1: { value: null },
        edgeTexture2: { value: null },
        patternTexture: { value: null },
        edgeStrength: { value: 1 },
        edgeGlow: { value: 1 },
        usePatternTexture: { value: 0 }
      },
      vertexShader: `varying vec2 vUv;

				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `varying vec2 vUv;

				uniform sampler2D maskTexture;
				uniform sampler2D edgeTexture1;
				uniform sampler2D edgeTexture2;
				uniform sampler2D patternTexture;
				uniform float edgeStrength;
				uniform float edgeGlow;
				uniform bool usePatternTexture;

				void main() {
					vec4 edgeValue1 = texture2D(edgeTexture1, vUv);
					vec4 edgeValue2 = texture2D(edgeTexture2, vUv);
					vec4 maskColor = texture2D(maskTexture, vUv);
					vec4 patternColor = texture2D(patternTexture, 6.0 * vUv);
					float visibilityFactor = 1.0 - maskColor.g > 0.0 ? 1.0 : 0.5;
					vec4 edgeValue = edgeValue1 + edgeValue2 * edgeGlow;
					vec4 finalColor = edgeStrength * maskColor.r * edgeValue;
					if(usePatternTexture)
						finalColor += + visibilityFactor * (1.0 - maskColor.r) * (1.0 - patternColor.r);
					gl_FragColor = finalColor;
				}`,
      blending: oo,
      depthTest: !1,
      depthWrite: !1,
      transparent: !0
    });
  }
}
_c.BlurDirectionX = new Se(1, 0);
_c.BlurDirectionY = new Se(0, 1);
const Tne = () => {
  const s = (o) => o == null || o === void 0 || o === "", e = () => !!(window.EngineDebugInfo.enableConsole || location.host.includes("127.0.0.1")), t = (o, l, c) => {
    console.log(
      `%c ${o} %c ${l} %c`,
      `background:${c};border:1px solid ${c}; padding: 1px; border-radius: 2px 0 0 2px; color: #fff;`,
      `border:1px solid ${c}; padding: 1px; border-radius: 0 2px 2px 0; color: ${c};`,
      "background:transparent"
    );
  };
  return {
    info: (o = "") => {
      if (!e())
        return;
      const l = Object.getPrototypeOf(o).constructor.name, c = s(o) ? "Info" : l, h = s(o) ? l : o;
      t(c, h, "#909399");
    },
    error: (o = "") => {
      if (!e())
        return;
      const l = Object.getPrototypeOf(o).constructor.name, c = s(o) ? "Error" : l, h = s(o) ? l : o;
      t(c, h, "#F56C6C");
    },
    warning: (o = "") => {
      if (!e())
        return;
      const l = Object.getPrototypeOf(o).constructor.name, c = s(o) ? "Warning" : l, h = s(o) ? l : o;
      t(c, h, "#E6A23C");
    },
    log: (o = "") => {
      if (!e())
        return;
      const l = Object.getPrototypeOf(o).constructor.name, c = s(o) ? "Success " : l, h = s(o) ? l : o;
      t(c, h, "#67C23A");
    }
  };
}, Cne = {
  "Linear.None": Ci.Linear.None,
  "Quadratic.In": Ci.Quadratic.In,
  "Quadratic.Out": Ci.Quadratic.Out,
  "Quadratic.InOut": Ci.Quadratic.InOut,
  "Cubic.In": Ci.Cubic.In,
  "Cubic.Out": Ci.Cubic.Out,
  "Cubic.InOut": Ci.Cubic.InOut,
  "Quartic.In": Ci.Quartic.In,
  "Quartic.Out": Ci.Quartic.Out,
  "Quartic.InOut": Ci.Quartic.InOut,
  "Quintic.In": Ci.Quintic.In,
  "Quintic.Out": Ci.Quintic.Out,
  "Quintic.InOut": Ci.Quintic.InOut,
  "Sinusoidal.In": Ci.Sinusoidal.In,
  "Sinusoidal.Out": Ci.Sinusoidal.Out,
  "Sinusoidal.InOut": Ci.Sinusoidal.InOut,
  "Exponential.In": Ci.Exponential.In,
  "Exponential.Out": Ci.Exponential.Out,
  "Exponential.InOut": Ci.Exponential.InOut,
  "Circular.In": Ci.Circular.In,
  "Circular.Out": Ci.Circular.Out,
  "Circular.InOut": Ci.Circular.InOut,
  "Elastic.In": Ci.Elastic.In,
  "Elastic.Out": Ci.Elastic.Out,
  "Elastic.InOut": Ci.Elastic.InOut,
  "Back.In": Ci.Back.In,
  "Back.Out": Ci.Back.Out,
  "Back.InOut": Ci.Back.InOut,
  "Bounce.In": Ci.Bounce.In,
  "Bounce.Out": Ci.Bounce.Out,
  "Bounce.InOut": Ci.Bounce.InOut
}, Ya = "twin-wireframe";
var mN = /* @__PURE__ */ ((s) => (s[s.VIEW_LAYER = 0] = "VIEW_LAYER", s[s.BLOOM_LAYER = 1] = "BLOOM_LAYER", s[s.NO_EFFECT = 2] = "NO_EFFECT", s))(mN || {}), Zh = /* @__PURE__ */ ((s) => (s[s.X = 0] = "X", s[s.Y = 1] = "Y", s[s.Z = 2] = "Z", s))(Zh || {}), Ac = /* @__PURE__ */ ((s) => (s.NoBlending = "NoBlending", s.NormalBlending = "NormalBlending", s.AdditiveBlending = "AdditiveBlending", s.SubtractiveBlending = "SubtractiveBlending", s.MultiplyBlending = "MultiplyBlending", s))(Ac || {});
class jn {
  static createNumberAnimate(e) {
    return jn.createTweenHandler(e);
  }
  static createTweenHandler(e) {
    const {
      source: t,
      target: i,
      duration: n = 1e3,
      delay: r = 0,
      execute: a,
      start: o,
      complete: l,
      mode: c = "Quadratic.InOut",
      repeat: h = 0,
      immediately: u = !0
    } = e, d = new nq(t).to(i, n).easing(Cne[c]).onUpdate((f, p) => {
      a && a(f, p);
    }).onStart(o).repeat(h).delay(r).onComplete((f) => {
      l && l(f, n);
    });
    return u ? d.start() : d.stop(), d;
  }
  static removeTweenHandler(e) {
    Yi(e);
  }
}
class XS {
  constructor() {
    S(this, "_tweenHandlers", []);
  }
  get tweenHandlers() {
    return this._tweenHandlers;
  }
  has(e) {
    return !!this.tweenHandlers.find(({ id: t }) => t === e);
  }
  addTweenAnimation(e) {
    const { repeat: t, duration: i, mode: n, execute: r, complete: a, start: o, id: l, delay: c } = e, h = jn.createTweenHandler({
      source: { value: 0 },
      target: { value: 1 },
      repeat: t,
      immediately: !1,
      duration: i,
      mode: n,
      delay: c,
      start: o,
      execute: r,
      complete: a
    });
    return this._tweenHandlers.push({ id: l, tween: h }), h;
  }
  chain(e = !0) {
    for (let i = 1; i < this._tweenHandlers.length; i++)
      this._tweenHandlers[i - 1].tween.chain(this._tweenHandlers[i].tween);
    const { length: t } = this._tweenHandlers;
    return t < 1 ? this._tweenHandlers[0].tween : (e && this._tweenHandlers[t - 1].tween.chain(this._tweenHandlers[0].tween), this._tweenHandlers[t - 1].tween);
  }
  setDuration(e, t) {
    const i = this._tweenHandlers.find((n) => n.id === e);
    i && i.tween.duration(t);
  }
  start() {
    const [e] = this._tweenHandlers;
    e && e.tween.start();
  }
  startAll() {
    this._tweenHandlers.forEach((e) => e.tween.start());
  }
  resume() {
    this._tweenHandlers.forEach((e) => e.tween.resume());
  }
  stop() {
    this._tweenHandlers.forEach((e) => {
      e.tween.stop();
    });
  }
  clear() {
    this._tweenHandlers.forEach(({ tween: e }) => {
      e.stop(), Yi(e);
    }), this._tweenHandlers.length = 0;
  }
  delete(e) {
    const t = this.tweenHandlers.findIndex((i) => i.id === e);
    this.tweenHandlers[t] && (this.tweenHandlers[t].tween.stop(), Yi(this.tweenHandlers[t].tween), this.tweenHandlers.splice(t, 1));
  }
  isPaused() {
    let e = !1;
    return this._tweenHandlers.forEach((t) => {
      e || (e = t.tween.isPaused());
    }), e;
  }
  isPlaying() {
    let e = !1;
    return this._tweenHandlers.forEach((t) => {
      e || (e = t.tween.isPlaying());
    }), e;
  }
  pause() {
    this._tweenHandlers.forEach((e) => e.tween.pause());
  }
  dispose() {
    this.clear();
  }
}
var to = /* @__PURE__ */ ((s) => (s.None = "none", s.StartDraw = "start-draw", s.EndDraw = "end-draw", s))(to || {}), bn = /* @__PURE__ */ ((s) => (s.Create = "create", s.Move = "move", s))(bn || {});
const _ne = `
data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAfhJREFUOE+1lT1oU1EUx38nkzq1jVOlYnFwUVDBSSoOlmxN3OJk1K2LXxAHhaagg4Fipm6ldIpbX938GETBSdDBQUExEOxUrVO75cj/Ju81ff1IGvBC4OW+e3/3f8495/+MXYa7DwHXgcvAWeBEZ1kD+AxEZra0215LT7p7AVgEBN1vCF5Jg7cB3b0G3Bbl+1fnyyf9YP13mzs6BqPHIZc3RrLJ1lkzq8QnJ7PursmZzQ3n5Yrz/s3+8iYmITeV4fCRsK5mZnf1EIDuXlKYgs1XndVmj2A7r6V4upxAr5pZFAN/KvFRvdVTWfooKS0UM5r+C4xbrO7PmvPkgfcnLbVqumycPBW03RFwGSjUF1p8/DAQj4krRuFaAEYChnDnKq2+c5c+duQoPHwawm4IGOK8f6s1mLzOrrmFAOS/AEPIj8utpIAPKvXYGNyrbIUcLiWqq5gHu+ULF43iza1LUavVfnxrF/Ug41E1w3A27CwphzIBhT208tx59/pg0EuTRr4Y1DXMbDzuFDnM8uYGzFf7Lx/lTq13qN3PJTlPtzk8U6UL+upFb6VSlpuyGCYbm03MIc6bu8/I4/R/fY3gOr+asNpsp2E4a5w5B6fPq9WSbCdOswPY5TwCxy691z3JDG7IYboX7HDsLrWytPwen4C3wJKZCbpt/ANYycvyro8HTwAAAABJRU5ErkJggg==
`, BC = `
data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAFbSURBVHgBhVIxTsNAENw9WwRZArlKBzIVjkjhigJe4RfgB0TCXaCyXEVUwRI94QV5BRRpcAEKobKggsoSAomIeNk9YgNSEo901vju5vZuZxD+wInIXreKkFAdAZAjcwSQ8jdVMxU/nGJW7sWSuL2vgEzV5502LAAfkCNRPOka55WwdTYLC8S+8P0tgM4hQqsJsNEAGD0DDO+Ix/wAwuCxi1fo9sghk26lkgg6B7AQFzc8rklXnr7jjioMiETkt2GpSCBrchu+or1mwbFCJE8W/DZCHeRG+n1IgeJqXvm2OrjNijqqZG+f9cLNxi9n4Y8349d64eiJSpoqQhgIk47VobJEAmGZkDDPxa9VYlkb3muaS4p0m9jLgAy6FO7vSYe5Wduo3z1+Ie2hHKxRYDg5waTyYJfTw32OmNpLiuacmlBSIz//zJMUSSAQtLfefDpjwWD6AUkWY17u/QZqFoLgl64FnwAAAABJRU5ErkJggg==
`, Pne = `
data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEISURBVHgBjVI7CsJAFHxrYyWkshO0FCy0sdBTeAJzAEFvoDcQwV49gacwRZqkCGgX0S6NolWq9U2ymw/k48Aks3mZ5f2IMpBSGswN05cpHOaB2aUicMBkvmQ5EFvp/4Uy4cMW2n5I2ltE14DoGxKNO0SzASi0xxRCnIRKwWEa+0tsKsJiwpxG5jez1+DHGqazV24CELOfkTSYSxiHOJ09qgUy0ukmRnVbJW5BIrsNrVrNeuMnTDWMd4h+u96IDiu4MB6hVMcqkRmJi3GgSz5hHFauATngYoyE4nGM9AKY/DpAo7sYDZqFulECTJk0V7wAu+SE7flj5eaF6WCL1EI7GYPPXKuSEvwAUkHMBZR27nQAAAAASUVORK5CYII=
`, Rne = `
data:image/jpeg;base64,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
`, Dne = `
data:image/png;base64,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
`, Lne = "data:image/png;base64,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";
function i3(s) {
  throw new Error('Could not dynamically require "' + s + '". Please configure the dynamicRequireTargets or/and ignoreDynamicRequires option of @rollup/plugin-commonjs appropriately for this require call to work.');
}
var gN = { exports: {} };
/*!
    localForage -- Offline Storage, Improved
    Version 1.10.0
    https://localforage.github.io/localForage
    (c) 2013-2017 Mozilla, Apache License 2.0
*/
(function(s, e) {
  (function(t) {
    s.exports = t();
  })(function() {
    return function t(i, n, r) {
      function a(c, h) {
        if (!n[c]) {
          if (!i[c]) {
            var u = typeof i3 == "function" && i3;
            if (!h && u)
              return u(c, !0);
            if (o)
              return o(c, !0);
            var d = new Error("Cannot find module '" + c + "'");
            throw d.code = "MODULE_NOT_FOUND", d;
          }
          var f = n[c] = { exports: {} };
          i[c][0].call(f.exports, function(p) {
            var v = i[c][1][p];
            return a(v || p);
          }, f, f.exports, t, i, n, r);
        }
        return n[c].exports;
      }
      for (var o = typeof i3 == "function" && i3, l = 0; l < r.length; l++)
        a(r[l]);
      return a;
    }({ 1: [function(t, i, n) {
      (function(r) {
        var a = r.MutationObserver || r.WebKitMutationObserver, o;
        if (a) {
          var l = 0, c = new a(p), h = r.document.createTextNode("");
          c.observe(h, {
            characterData: !0
          }), o = function() {
            h.data = l = ++l % 2;
          };
        } else if (!r.setImmediate && typeof r.MessageChannel < "u") {
          var u = new r.MessageChannel();
          u.port1.onmessage = p, o = function() {
            u.port2.postMessage(0);
          };
        } else
          "document" in r && "onreadystatechange" in r.document.createElement("script") ? o = function() {
            var g = r.document.createElement("script");
            g.onreadystatechange = function() {
              p(), g.onreadystatechange = null, g.parentNode.removeChild(g), g = null;
            }, r.document.documentElement.appendChild(g);
          } : o = function() {
            setTimeout(p, 0);
          };
        var d, f = [];
        function p() {
          d = !0;
          for (var g, m, x = f.length; x; ) {
            for (m = f, f = [], g = -1; ++g < x; )
              m[g]();
            x = f.length;
          }
          d = !1;
        }
        i.exports = v;
        function v(g) {
          f.push(g) === 1 && !d && o();
        }
      }).call(this, typeof Sc < "u" ? Sc : typeof self < "u" ? self : typeof window < "u" ? window : {});
    }, {}], 2: [function(t, i, n) {
      var r = t(1);
      function a() {
      }
      var o = {}, l = ["REJECTED"], c = ["FULFILLED"], h = ["PENDING"];
      i.exports = u;
      function u(b) {
        if (typeof b != "function")
          throw new TypeError("resolver must be a function");
        this.state = h, this.queue = [], this.outcome = void 0, b !== a && v(this, b);
      }
      u.prototype.catch = function(b) {
        return this.then(null, b);
      }, u.prototype.then = function(b, M) {
        if (typeof b != "function" && this.state === c || typeof M != "function" && this.state === l)
          return this;
        var w = new this.constructor(a);
        if (this.state !== h) {
          var C = this.state === c ? b : M;
          f(w, C, this.outcome);
        } else
          this.queue.push(new d(w, b, M));
        return w;
      };
      function d(b, M, w) {
        this.promise = b, typeof M == "function" && (this.onFulfilled = M, this.callFulfilled = this.otherCallFulfilled), typeof w == "function" && (this.onRejected = w, this.callRejected = this.otherCallRejected);
      }
      d.prototype.callFulfilled = function(b) {
        o.resolve(this.promise, b);
      }, d.prototype.otherCallFulfilled = function(b) {
        f(this.promise, this.onFulfilled, b);
      }, d.prototype.callRejected = function(b) {
        o.reject(this.promise, b);
      }, d.prototype.otherCallRejected = function(b) {
        f(this.promise, this.onRejected, b);
      };
      function f(b, M, w) {
        r(function() {
          var C;
          try {
            C = M(w);
          } catch (T) {
            return o.reject(b, T);
          }
          C === b ? o.reject(b, new TypeError("Cannot resolve promise with itself")) : o.resolve(b, C);
        });
      }
      o.resolve = function(b, M) {
        var w = g(p, M);
        if (w.status === "error")
          return o.reject(b, w.value);
        var C = w.value;
        if (C)
          v(b, C);
        else {
          b.state = c, b.outcome = M;
          for (var T = -1, E = b.queue.length; ++T < E; )
            b.queue[T].callFulfilled(M);
        }
        return b;
      }, o.reject = function(b, M) {
        b.state = l, b.outcome = M;
        for (var w = -1, C = b.queue.length; ++w < C; )
          b.queue[w].callRejected(M);
        return b;
      };
      function p(b) {
        var M = b && b.then;
        if (b && (typeof b == "object" || typeof b == "function") && typeof M == "function")
          return function() {
            M.apply(b, arguments);
          };
      }
      function v(b, M) {
        var w = !1;
        function C(L) {
          w || (w = !0, o.reject(b, L));
        }
        function T(L) {
          w || (w = !0, o.resolve(b, L));
        }
        function E() {
          M(T, C);
        }
        var P = g(E);
        P.status === "error" && C(P.value);
      }
      function g(b, M) {
        var w = {};
        try {
          w.value = b(M), w.status = "success";
        } catch (C) {
          w.status = "error", w.value = C;
        }
        return w;
      }
      u.resolve = m;
      function m(b) {
        return b instanceof this ? b : o.resolve(new this(a), b);
      }
      u.reject = x;
      function x(b) {
        var M = new this(a);
        return o.reject(M, b);
      }
      u.all = y;
      function y(b) {
        var M = this;
        if (Object.prototype.toString.call(b) !== "[object Array]")
          return this.reject(new TypeError("must be an array"));
        var w = b.length, C = !1;
        if (!w)
          return this.resolve([]);
        for (var T = new Array(w), E = 0, P = -1, L = new this(a); ++P < w; )
          I(b[P], P);
        return L;
        function I(N, H) {
          M.resolve(N).then(U, function(W) {
            C || (C = !0, o.reject(L, W));
          });
          function U(W) {
            T[H] = W, ++E === w && !C && (C = !0, o.resolve(L, T));
          }
        }
      }
      u.race = A;
      function A(b) {
        var M = this;
        if (Object.prototype.toString.call(b) !== "[object Array]")
          return this.reject(new TypeError("must be an array"));
        var w = b.length, C = !1;
        if (!w)
          return this.resolve([]);
        for (var T = -1, E = new this(a); ++T < w; )
          P(b[T]);
        return E;
        function P(L) {
          M.resolve(L).then(function(I) {
            C || (C = !0, o.resolve(E, I));
          }, function(I) {
            C || (C = !0, o.reject(E, I));
          });
        }
      }
    }, { 1: 1 }], 3: [function(t, i, n) {
      (function(r) {
        typeof r.Promise != "function" && (r.Promise = t(2));
      }).call(this, typeof Sc < "u" ? Sc : typeof self < "u" ? self : typeof window < "u" ? window : {});
    }, { 2: 2 }], 4: [function(t, i, n) {
      var r = typeof Symbol == "function" && typeof Symbol.iterator == "symbol" ? function(V) {
        return typeof V;
      } : function(V) {
        return V && typeof Symbol == "function" && V.constructor === Symbol && V !== Symbol.prototype ? "symbol" : typeof V;
      };
      function a(V, le) {
        if (!(V instanceof le))
          throw new TypeError("Cannot call a class as a function");
      }
      function o() {
        try {
          if (typeof indexedDB < "u")
            return indexedDB;
          if (typeof webkitIndexedDB < "u")
            return webkitIndexedDB;
          if (typeof mozIndexedDB < "u")
            return mozIndexedDB;
          if (typeof OIndexedDB < "u")
            return OIndexedDB;
          if (typeof msIndexedDB < "u")
            return msIndexedDB;
        } catch {
          return;
        }
      }
      var l = o();
      function c() {
        try {
          if (!l || !l.open)
            return !1;
          var V = typeof openDatabase < "u" && /(Safari|iPhone|iPad|iPod)/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && !/BlackBerry/.test(navigator.platform), le = typeof fetch == "function" && fetch.toString().indexOf("[native code") !== -1;
          return (!V || le) && typeof indexedDB < "u" && // some outdated implementations of IDB that appear on Samsung
          // and HTC Android devices <4.4 are missing IDBKeyRange
          // See: https://github.com/mozilla/localForage/issues/128
          // See: https://github.com/mozilla/localForage/issues/272
          typeof IDBKeyRange < "u";
        } catch {
          return !1;
        }
      }
      function h(V, le) {
        V = V || [], le = le || {};
        try {
          return new Blob(V, le);
        } catch (oe) {
          if (oe.name !== "TypeError")
            throw oe;
          for (var ie = typeof BlobBuilder < "u" ? BlobBuilder : typeof MSBlobBuilder < "u" ? MSBlobBuilder : typeof MozBlobBuilder < "u" ? MozBlobBuilder : WebKitBlobBuilder, ge = new ie(), Ce = 0; Ce < V.length; Ce += 1)
            ge.append(V[Ce]);
          return ge.getBlob(le.type);
        }
      }
      typeof Promise > "u" && t(3);
      var u = Promise;
      function d(V, le) {
        le && V.then(function(ie) {
          le(null, ie);
        }, function(ie) {
          le(ie);
        });
      }
      function f(V, le, ie) {
        typeof le == "function" && V.then(le), typeof ie == "function" && V.catch(ie);
      }
      function p(V) {
        return typeof V != "string" && (console.warn(V + " used as a key, but it is not a string."), V = String(V)), V;
      }
      function v() {
        if (arguments.length && typeof arguments[arguments.length - 1] == "function")
          return arguments[arguments.length - 1];
      }
      var g = "local-forage-detect-blob-support", m = void 0, x = {}, y = Object.prototype.toString, A = "readonly", b = "readwrite";
      function M(V) {
        for (var le = V.length, ie = new ArrayBuffer(le), ge = new Uint8Array(ie), Ce = 0; Ce < le; Ce++)
          ge[Ce] = V.charCodeAt(Ce);
        return ie;
      }
      function w(V) {
        return new u(function(le) {
          var ie = V.transaction(g, b), ge = h([""]);
          ie.objectStore(g).put(ge, "key"), ie.onabort = function(Ce) {
            Ce.preventDefault(), Ce.stopPropagation(), le(!1);
          }, ie.oncomplete = function() {
            var Ce = navigator.userAgent.match(/Chrome\/(\d+)/), oe = navigator.userAgent.match(/Edge\//);
            le(oe || !Ce || parseInt(Ce[1], 10) >= 43);
          };
        }).catch(function() {
          return !1;
        });
      }
      function C(V) {
        return typeof m == "boolean" ? u.resolve(m) : w(V).then(function(le) {
          return m = le, m;
        });
      }
      function T(V) {
        var le = x[V.name], ie = {};
        ie.promise = new u(function(ge, Ce) {
          ie.resolve = ge, ie.reject = Ce;
        }), le.deferredOperations.push(ie), le.dbReady ? le.dbReady = le.dbReady.then(function() {
          return ie.promise;
        }) : le.dbReady = ie.promise;
      }
      function E(V) {
        var le = x[V.name], ie = le.deferredOperations.pop();
        if (ie)
          return ie.resolve(), ie.promise;
      }
      function P(V, le) {
        var ie = x[V.name], ge = ie.deferredOperations.pop();
        if (ge)
          return ge.reject(le), ge.promise;
      }
      function L(V, le) {
        return new u(function(ie, ge) {
          if (x[V.name] = x[V.name] || be(), V.db)
            if (le)
              T(V), V.db.close();
            else
              return ie(V.db);
          var Ce = [V.name];
          le && Ce.push(V.version);
          var oe = l.open.apply(l, Ce);
          le && (oe.onupgradeneeded = function(He) {
            var ct = oe.result;
            try {
              ct.createObjectStore(V.storeName), He.oldVersion <= 1 && ct.createObjectStore(g);
            } catch (Ke) {
              if (Ke.name === "ConstraintError")
                console.warn('The database "' + V.name + '" has been upgraded from version ' + He.oldVersion + " to version " + He.newVersion + ', but the storage "' + V.storeName + '" already exists.');
              else
                throw Ke;
            }
          }), oe.onerror = function(He) {
            He.preventDefault(), ge(oe.error);
          }, oe.onsuccess = function() {
            var He = oe.result;
            He.onversionchange = function(ct) {
              ct.target.close();
            }, ie(He), E(V);
          };
        });
      }
      function I(V) {
        return L(V, !1);
      }
      function N(V) {
        return L(V, !0);
      }
      function H(V, le) {
        if (!V.db)
          return !0;
        var ie = !V.db.objectStoreNames.contains(V.storeName), ge = V.version < V.db.version, Ce = V.version > V.db.version;
        if (ge && (V.version !== le && console.warn('The database "' + V.name + `" can't be downgraded from version ` + V.db.version + " to version " + V.version + "."), V.version = V.db.version), Ce || ie) {
          if (ie) {
            var oe = V.db.version + 1;
            oe > V.version && (V.version = oe);
          }
          return !0;
        }
        return !1;
      }
      function U(V) {
        return new u(function(le, ie) {
          var ge = new FileReader();
          ge.onerror = ie, ge.onloadend = function(Ce) {
            var oe = btoa(Ce.target.result || "");
            le({
              __local_forage_encoded_blob: !0,
              data: oe,
              type: V.type
            });
          }, ge.readAsBinaryString(V);
        });
      }
      function W(V) {
        var le = M(atob(V.data));
        return h([le], { type: V.type });
      }
      function X(V) {
        return V && V.__local_forage_encoded_blob;
      }
      function K(V) {
        var le = this, ie = le._initReady().then(function() {
          var ge = x[le._dbInfo.name];
          if (ge && ge.dbReady)
            return ge.dbReady;
        });
        return f(ie, V, V), ie;
      }
      function te(V) {
        T(V);
        for (var le = x[V.name], ie = le.forages, ge = 0; ge < ie.length; ge++) {
          var Ce = ie[ge];
          Ce._dbInfo.db && (Ce._dbInfo.db.close(), Ce._dbInfo.db = null);
        }
        return V.db = null, I(V).then(function(oe) {
          return V.db = oe, H(V) ? N(V) : oe;
        }).then(function(oe) {
          V.db = le.db = oe;
          for (var He = 0; He < ie.length; He++)
            ie[He]._dbInfo.db = oe;
        }).catch(function(oe) {
          throw P(V, oe), oe;
        });
      }
      function re(V, le, ie, ge) {
        ge === void 0 && (ge = 1);
        try {
          var Ce = V.db.transaction(V.storeName, le);
          ie(null, Ce);
        } catch (oe) {
          if (ge > 0 && (!V.db || oe.name === "InvalidStateError" || oe.name === "NotFoundError"))
            return u.resolve().then(function() {
              if (!V.db || oe.name === "NotFoundError" && !V.db.objectStoreNames.contains(V.storeName) && V.version <= V.db.version)
                return V.db && (V.version = V.db.version + 1), N(V);
            }).then(function() {
              return te(V).then(function() {
                re(V, le, ie, ge - 1);
              });
            }).catch(ie);
          ie(oe);
        }
      }
      function be() {
        return {
          // Running localForages sharing a database.
          forages: [],
          // Shared database.
          db: null,
          // Database readiness (promise).
          dbReady: null,
          // Deferred operations on the database.
          deferredOperations: []
        };
      }
      function Pe(V) {
        var le = this, ie = {
          db: null
        };
        if (V)
          for (var ge in V)
            ie[ge] = V[ge];
        var Ce = x[ie.name];
        Ce || (Ce = be(), x[ie.name] = Ce), Ce.forages.push(le), le._initReady || (le._initReady = le.ready, le.ready = K);
        var oe = [];
        function He() {
          return u.resolve();
        }
        for (var ct = 0; ct < Ce.forages.length; ct++) {
          var Ke = Ce.forages[ct];
          Ke !== le && oe.push(Ke._initReady().catch(He));
        }
        var tt = Ce.forages.slice(0);
        return u.all(oe).then(function() {
          return ie.db = Ce.db, I(ie);
        }).then(function(ut) {
          return ie.db = ut, H(ie, le._defaultConfig.version) ? N(ie) : ut;
        }).then(function(ut) {
          ie.db = Ce.db = ut, le._dbInfo = ie;
          for (var _t = 0; _t < tt.length; _t++) {
            var ri = tt[_t];
            ri !== le && (ri._dbInfo.db = ie.db, ri._dbInfo.version = ie.version);
          }
        });
      }
      function J(V, le) {
        var ie = this;
        V = p(V);
        var ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            re(ie._dbInfo, A, function(He, ct) {
              if (He)
                return oe(He);
              try {
                var Ke = ct.objectStore(ie._dbInfo.storeName), tt = Ke.get(V);
                tt.onsuccess = function() {
                  var ut = tt.result;
                  ut === void 0 && (ut = null), X(ut) && (ut = W(ut)), Ce(ut);
                }, tt.onerror = function() {
                  oe(tt.error);
                };
              } catch (ut) {
                oe(ut);
              }
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function fe(V, le) {
        var ie = this, ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            re(ie._dbInfo, A, function(He, ct) {
              if (He)
                return oe(He);
              try {
                var Ke = ct.objectStore(ie._dbInfo.storeName), tt = Ke.openCursor(), ut = 1;
                tt.onsuccess = function() {
                  var _t = tt.result;
                  if (_t) {
                    var ri = _t.value;
                    X(ri) && (ri = W(ri));
                    var fi = V(ri, _t.key, ut++);
                    fi !== void 0 ? Ce(fi) : _t.continue();
                  } else
                    Ce();
                }, tt.onerror = function() {
                  oe(tt.error);
                };
              } catch (_t) {
                oe(_t);
              }
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function Me(V, le, ie) {
        var ge = this;
        V = p(V);
        var Ce = new u(function(oe, He) {
          var ct;
          ge.ready().then(function() {
            return ct = ge._dbInfo, y.call(le) === "[object Blob]" ? C(ct.db).then(function(Ke) {
              return Ke ? le : U(le);
            }) : le;
          }).then(function(Ke) {
            re(ge._dbInfo, b, function(tt, ut) {
              if (tt)
                return He(tt);
              try {
                var _t = ut.objectStore(ge._dbInfo.storeName);
                Ke === null && (Ke = void 0);
                var ri = _t.put(Ke, V);
                ut.oncomplete = function() {
                  Ke === void 0 && (Ke = null), oe(Ke);
                }, ut.onabort = ut.onerror = function() {
                  var fi = ri.error ? ri.error : ri.transaction.error;
                  He(fi);
                };
              } catch (fi) {
                He(fi);
              }
            });
          }).catch(He);
        });
        return d(Ce, ie), Ce;
      }
      function ye(V, le) {
        var ie = this;
        V = p(V);
        var ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            re(ie._dbInfo, b, function(He, ct) {
              if (He)
                return oe(He);
              try {
                var Ke = ct.objectStore(ie._dbInfo.storeName), tt = Ke.delete(V);
                ct.oncomplete = function() {
                  Ce();
                }, ct.onerror = function() {
                  oe(tt.error);
                }, ct.onabort = function() {
                  var ut = tt.error ? tt.error : tt.transaction.error;
                  oe(ut);
                };
              } catch (ut) {
                oe(ut);
              }
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function Ye(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            re(le._dbInfo, b, function(oe, He) {
              if (oe)
                return Ce(oe);
              try {
                var ct = He.objectStore(le._dbInfo.storeName), Ke = ct.clear();
                He.oncomplete = function() {
                  ge();
                }, He.onabort = He.onerror = function() {
                  var tt = Ke.error ? Ke.error : Ke.transaction.error;
                  Ce(tt);
                };
              } catch (tt) {
                Ce(tt);
              }
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function Je(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            re(le._dbInfo, A, function(oe, He) {
              if (oe)
                return Ce(oe);
              try {
                var ct = He.objectStore(le._dbInfo.storeName), Ke = ct.count();
                Ke.onsuccess = function() {
                  ge(Ke.result);
                }, Ke.onerror = function() {
                  Ce(Ke.error);
                };
              } catch (tt) {
                Ce(tt);
              }
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function rt(V, le) {
        var ie = this, ge = new u(function(Ce, oe) {
          if (V < 0) {
            Ce(null);
            return;
          }
          ie.ready().then(function() {
            re(ie._dbInfo, A, function(He, ct) {
              if (He)
                return oe(He);
              try {
                var Ke = ct.objectStore(ie._dbInfo.storeName), tt = !1, ut = Ke.openKeyCursor();
                ut.onsuccess = function() {
                  var _t = ut.result;
                  if (!_t) {
                    Ce(null);
                    return;
                  }
                  V === 0 || tt ? Ce(_t.key) : (tt = !0, _t.advance(V));
                }, ut.onerror = function() {
                  oe(ut.error);
                };
              } catch (_t) {
                oe(_t);
              }
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function wt(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            re(le._dbInfo, A, function(oe, He) {
              if (oe)
                return Ce(oe);
              try {
                var ct = He.objectStore(le._dbInfo.storeName), Ke = ct.openKeyCursor(), tt = [];
                Ke.onsuccess = function() {
                  var ut = Ke.result;
                  if (!ut) {
                    ge(tt);
                    return;
                  }
                  tt.push(ut.key), ut.continue();
                }, Ke.onerror = function() {
                  Ce(Ke.error);
                };
              } catch (ut) {
                Ce(ut);
              }
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function k(V, le) {
        le = v.apply(this, arguments);
        var ie = this.config();
        V = typeof V != "function" && V || {}, V.name || (V.name = V.name || ie.name, V.storeName = V.storeName || ie.storeName);
        var ge = this, Ce;
        if (!V.name)
          Ce = u.reject("Invalid arguments");
        else {
          var oe = V.name === ie.name && ge._dbInfo.db, He = oe ? u.resolve(ge._dbInfo.db) : I(V).then(function(ct) {
            var Ke = x[V.name], tt = Ke.forages;
            Ke.db = ct;
            for (var ut = 0; ut < tt.length; ut++)
              tt[ut]._dbInfo.db = ct;
            return ct;
          });
          V.storeName ? Ce = He.then(function(ct) {
            if (ct.objectStoreNames.contains(V.storeName)) {
              var Ke = ct.version + 1;
              T(V);
              var tt = x[V.name], ut = tt.forages;
              ct.close();
              for (var _t = 0; _t < ut.length; _t++) {
                var ri = ut[_t];
                ri._dbInfo.db = null, ri._dbInfo.version = Ke;
              }
              var fi = new u(function(Ui, ts) {
                var qn = l.open(V.name, Ke);
                qn.onerror = function(oa) {
                  var am = qn.result;
                  am.close(), ts(oa);
                }, qn.onupgradeneeded = function() {
                  var oa = qn.result;
                  oa.deleteObjectStore(V.storeName);
                }, qn.onsuccess = function() {
                  var oa = qn.result;
                  oa.close(), Ui(oa);
                };
              });
              return fi.then(function(Ui) {
                tt.db = Ui;
                for (var ts = 0; ts < ut.length; ts++) {
                  var qn = ut[ts];
                  qn._dbInfo.db = Ui, E(qn._dbInfo);
                }
              }).catch(function(Ui) {
                throw (P(V, Ui) || u.resolve()).catch(function() {
                }), Ui;
              });
            }
          }) : Ce = He.then(function(ct) {
            T(V);
            var Ke = x[V.name], tt = Ke.forages;
            ct.close();
            for (var ut = 0; ut < tt.length; ut++) {
              var _t = tt[ut];
              _t._dbInfo.db = null;
            }
            var ri = new u(function(fi, Ui) {
              var ts = l.deleteDatabase(V.name);
              ts.onerror = function() {
                var qn = ts.result;
                qn && qn.close(), Ui(ts.error);
              }, ts.onblocked = function() {
                console.warn('dropInstance blocked for database "' + V.name + '" until all open connections are closed');
              }, ts.onsuccess = function() {
                var qn = ts.result;
                qn && qn.close(), fi(qn);
              };
            });
            return ri.then(function(fi) {
              Ke.db = fi;
              for (var Ui = 0; Ui < tt.length; Ui++) {
                var ts = tt[Ui];
                E(ts._dbInfo);
              }
            }).catch(function(fi) {
              throw (P(V, fi) || u.resolve()).catch(function() {
              }), fi;
            });
          });
        }
        return d(Ce, le), Ce;
      }
      var Fe = {
        _driver: "asyncStorage",
        _initStorage: Pe,
        _support: c(),
        iterate: fe,
        getItem: J,
        setItem: Me,
        removeItem: ye,
        clear: Ye,
        length: Je,
        key: rt,
        keys: wt,
        dropInstance: k
      };
      function Ge() {
        return typeof openDatabase == "function";
      }
      var qe = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/", Ie = "~~local_forage_type~", vt = /^~~local_forage_type~([^~]+)~/, Ve = "__lfsc__:", Ze = Ve.length, G = "arbf", D = "blob", ne = "si08", ve = "ui08", Y = "uic8", ee = "si16", we = "si32", de = "ur16", xe = "ui32", it = "fl32", q = "fl64", ae = Ze + G.length, Ee = Object.prototype.toString;
      function Le(V) {
        var le = V.length * 0.75, ie = V.length, ge, Ce = 0, oe, He, ct, Ke;
        V[V.length - 1] === "=" && (le--, V[V.length - 2] === "=" && le--);
        var tt = new ArrayBuffer(le), ut = new Uint8Array(tt);
        for (ge = 0; ge < ie; ge += 4)
          oe = qe.indexOf(V[ge]), He = qe.indexOf(V[ge + 1]), ct = qe.indexOf(V[ge + 2]), Ke = qe.indexOf(V[ge + 3]), ut[Ce++] = oe << 2 | He >> 4, ut[Ce++] = (He & 15) << 4 | ct >> 2, ut[Ce++] = (ct & 3) << 6 | Ke & 63;
        return tt;
      }
      function Ae(V) {
        var le = new Uint8Array(V), ie = "", ge;
        for (ge = 0; ge < le.length; ge += 3)
          ie += qe[le[ge] >> 2], ie += qe[(le[ge] & 3) << 4 | le[ge + 1] >> 4], ie += qe[(le[ge + 1] & 15) << 2 | le[ge + 2] >> 6], ie += qe[le[ge + 2] & 63];
        return le.length % 3 === 2 ? ie = ie.substring(0, ie.length - 1) + "=" : le.length % 3 === 1 && (ie = ie.substring(0, ie.length - 2) + "=="), ie;
      }
      function We(V, le) {
        var ie = "";
        if (V && (ie = Ee.call(V)), V && (ie === "[object ArrayBuffer]" || V.buffer && Ee.call(V.buffer) === "[object ArrayBuffer]")) {
          var ge, Ce = Ve;
          V instanceof ArrayBuffer ? (ge = V, Ce += G) : (ge = V.buffer, ie === "[object Int8Array]" ? Ce += ne : ie === "[object Uint8Array]" ? Ce += ve : ie === "[object Uint8ClampedArray]" ? Ce += Y : ie === "[object Int16Array]" ? Ce += ee : ie === "[object Uint16Array]" ? Ce += de : ie === "[object Int32Array]" ? Ce += we : ie === "[object Uint32Array]" ? Ce += xe : ie === "[object Float32Array]" ? Ce += it : ie === "[object Float64Array]" ? Ce += q : le(new Error("Failed to get type for BinaryArray"))), le(Ce + Ae(ge));
        } else if (ie === "[object Blob]") {
          var oe = new FileReader();
          oe.onload = function() {
            var He = Ie + V.type + "~" + Ae(this.result);
            le(Ve + D + He);
          }, oe.readAsArrayBuffer(V);
        } else
          try {
            le(JSON.stringify(V));
          } catch (He) {
            console.error("Couldn't convert value into a JSON string: ", V), le(null, He);
          }
      }
      function ot(V) {
        if (V.substring(0, Ze) !== Ve)
          return JSON.parse(V);
        var le = V.substring(ae), ie = V.substring(Ze, ae), ge;
        if (ie === D && vt.test(le)) {
          var Ce = le.match(vt);
          ge = Ce[1], le = le.substring(Ce[0].length);
        }
        var oe = Le(le);
        switch (ie) {
          case G:
            return oe;
          case D:
            return h([oe], { type: ge });
          case ne:
            return new Int8Array(oe);
          case ve:
            return new Uint8Array(oe);
          case Y:
            return new Uint8ClampedArray(oe);
          case ee:
            return new Int16Array(oe);
          case de:
            return new Uint16Array(oe);
          case we:
            return new Int32Array(oe);
          case xe:
            return new Uint32Array(oe);
          case it:
            return new Float32Array(oe);
          case q:
            return new Float64Array(oe);
          default:
            throw new Error("Unkown type: " + ie);
        }
      }
      var Ct = {
        serialize: We,
        deserialize: ot,
        stringToBuffer: Le,
        bufferToString: Ae
      };
      function F(V, le, ie, ge) {
        V.executeSql("CREATE TABLE IF NOT EXISTS " + le.storeName + " (id INTEGER PRIMARY KEY, key unique, value)", [], ie, ge);
      }
      function ue(V) {
        var le = this, ie = {
          db: null
        };
        if (V)
          for (var ge in V)
            ie[ge] = typeof V[ge] != "string" ? V[ge].toString() : V[ge];
        var Ce = new u(function(oe, He) {
          try {
            ie.db = openDatabase(ie.name, String(ie.version), ie.description, ie.size);
          } catch (ct) {
            return He(ct);
          }
          ie.db.transaction(function(ct) {
            F(ct, ie, function() {
              le._dbInfo = ie, oe();
            }, function(Ke, tt) {
              He(tt);
            });
          }, He);
        });
        return ie.serializer = Ct, Ce;
      }
      function Q(V, le, ie, ge, Ce, oe) {
        V.executeSql(ie, ge, Ce, function(He, ct) {
          ct.code === ct.SYNTAX_ERR ? He.executeSql("SELECT name FROM sqlite_master WHERE type='table' AND name = ?", [le.storeName], function(Ke, tt) {
            tt.rows.length ? oe(Ke, ct) : F(Ke, le, function() {
              Ke.executeSql(ie, ge, Ce, oe);
            }, oe);
          }, oe) : oe(He, ct);
        }, oe);
      }
      function he(V, le) {
        var ie = this;
        V = p(V);
        var ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            var He = ie._dbInfo;
            He.db.transaction(function(ct) {
              Q(ct, He, "SELECT * FROM " + He.storeName + " WHERE key = ? LIMIT 1", [V], function(Ke, tt) {
                var ut = tt.rows.length ? tt.rows.item(0).value : null;
                ut && (ut = He.serializer.deserialize(ut)), Ce(ut);
              }, function(Ke, tt) {
                oe(tt);
              });
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function Be(V, le) {
        var ie = this, ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            var He = ie._dbInfo;
            He.db.transaction(function(ct) {
              Q(ct, He, "SELECT * FROM " + He.storeName, [], function(Ke, tt) {
                for (var ut = tt.rows, _t = ut.length, ri = 0; ri < _t; ri++) {
                  var fi = ut.item(ri), Ui = fi.value;
                  if (Ui && (Ui = He.serializer.deserialize(Ui)), Ui = V(Ui, fi.key, ri + 1), Ui !== void 0) {
                    Ce(Ui);
                    return;
                  }
                }
                Ce();
              }, function(Ke, tt) {
                oe(tt);
              });
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function ft(V, le, ie, ge) {
        var Ce = this;
        V = p(V);
        var oe = new u(function(He, ct) {
          Ce.ready().then(function() {
            le === void 0 && (le = null);
            var Ke = le, tt = Ce._dbInfo;
            tt.serializer.serialize(le, function(ut, _t) {
              _t ? ct(_t) : tt.db.transaction(function(ri) {
                Q(ri, tt, "INSERT OR REPLACE INTO " + tt.storeName + " (key, value) VALUES (?, ?)", [V, ut], function() {
                  He(Ke);
                }, function(fi, Ui) {
                  ct(Ui);
                });
              }, function(ri) {
                if (ri.code === ri.QUOTA_ERR) {
                  if (ge > 0) {
                    He(ft.apply(Ce, [V, Ke, ie, ge - 1]));
                    return;
                  }
                  ct(ri);
                }
              });
            });
          }).catch(ct);
        });
        return d(oe, ie), oe;
      }
      function Et(V, le, ie) {
        return ft.apply(this, [V, le, ie, 1]);
      }
      function ei(V, le) {
        var ie = this;
        V = p(V);
        var ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            var He = ie._dbInfo;
            He.db.transaction(function(ct) {
              Q(ct, He, "DELETE FROM " + He.storeName + " WHERE key = ?", [V], function() {
                Ce();
              }, function(Ke, tt) {
                oe(tt);
              });
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function di(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            var oe = le._dbInfo;
            oe.db.transaction(function(He) {
              Q(He, oe, "DELETE FROM " + oe.storeName, [], function() {
                ge();
              }, function(ct, Ke) {
                Ce(Ke);
              });
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function Ot(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            var oe = le._dbInfo;
            oe.db.transaction(function(He) {
              Q(He, oe, "SELECT COUNT(key) as c FROM " + oe.storeName, [], function(ct, Ke) {
                var tt = Ke.rows.item(0).c;
                ge(tt);
              }, function(ct, Ke) {
                Ce(Ke);
              });
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function Wi(V, le) {
        var ie = this, ge = new u(function(Ce, oe) {
          ie.ready().then(function() {
            var He = ie._dbInfo;
            He.db.transaction(function(ct) {
              Q(ct, He, "SELECT key FROM " + He.storeName + " WHERE id = ? LIMIT 1", [V + 1], function(Ke, tt) {
                var ut = tt.rows.length ? tt.rows.item(0).key : null;
                Ce(ut);
              }, function(Ke, tt) {
                oe(tt);
              });
            });
          }).catch(oe);
        });
        return d(ge, le), ge;
      }
      function Li(V) {
        var le = this, ie = new u(function(ge, Ce) {
          le.ready().then(function() {
            var oe = le._dbInfo;
            oe.db.transaction(function(He) {
              Q(He, oe, "SELECT key FROM " + oe.storeName, [], function(ct, Ke) {
                for (var tt = [], ut = 0; ut < Ke.rows.length; ut++)
                  tt.push(Ke.rows.item(ut).key);
                ge(tt);
              }, function(ct, Ke) {
                Ce(Ke);
              });
            });
          }).catch(Ce);
        });
        return d(ie, V), ie;
      }
      function cr(V) {
        return new u(function(le, ie) {
          V.transaction(function(ge) {
            ge.executeSql("SELECT name FROM sqlite_master WHERE type='table' AND name <> '__WebKitDatabaseInfoTable__'", [], function(Ce, oe) {
              for (var He = [], ct = 0; ct < oe.rows.length; ct++)
                He.push(oe.rows.item(ct).name);
              le({
                db: V,
                storeNames: He
              });
            }, function(Ce, oe) {
              ie(oe);
            });
          }, function(ge) {
            ie(ge);
          });
        });
      }
      function sn(V, le) {
        le = v.apply(this, arguments);
        var ie = this.config();
        V = typeof V != "function" && V || {}, V.name || (V.name = V.name || ie.name, V.storeName = V.storeName || ie.storeName);
        var ge = this, Ce;
        return V.name ? Ce = new u(function(oe) {
          var He;
          V.name === ie.name ? He = ge._dbInfo.db : He = openDatabase(V.name, "", "", 0), V.storeName ? oe({
            db: He,
            storeNames: [V.storeName]
          }) : oe(cr(He));
        }).then(function(oe) {
          return new u(function(He, ct) {
            oe.db.transaction(function(Ke) {
              function tt(fi) {
                return new u(function(Ui, ts) {
                  Ke.executeSql("DROP TABLE IF EXISTS " + fi, [], function() {
                    Ui();
                  }, function(qn, oa) {
                    ts(oa);
                  });
                });
              }
              for (var ut = [], _t = 0, ri = oe.storeNames.length; _t < ri; _t++)
                ut.push(tt(oe.storeNames[_t]));
              u.all(ut).then(function() {
                He();
              }).catch(function(fi) {
                ct(fi);
              });
            }, function(Ke) {
              ct(Ke);
            });
          });
        }) : Ce = u.reject("Invalid arguments"), d(Ce, le), Ce;
      }
      var Os = {
        _driver: "webSQLStorage",
        _initStorage: ue,
        _support: Ge(),
        iterate: Be,
        getItem: he,
        setItem: Et,
        removeItem: ei,
        clear: di,
        length: Ot,
        key: Wi,
        keys: Li,
        dropInstance: sn
      };
      function br() {
        try {
          return typeof localStorage < "u" && "setItem" in localStorage && // in IE8 typeof localStorage.setItem === 'object'
          !!localStorage.setItem;
        } catch {
          return !1;
        }
      }
      function kr(V, le) {
        var ie = V.name + "/";
        return V.storeName !== le.storeName && (ie += V.storeName + "/"), ie;
      }
      function Mr() {
        var V = "_localforage_support_test";
        try {
          return localStorage.setItem(V, !0), localStorage.removeItem(V), !1;
        } catch {
          return !0;
        }
      }
      function A0() {
        return !Mr() || localStorage.length > 0;
      }
      function Gr(V) {
        var le = this, ie = {};
        if (V)
          for (var ge in V)
            ie[ge] = V[ge];
        return ie.keyPrefix = kr(V, le._defaultConfig), A0() ? (le._dbInfo = ie, ie.serializer = Ct, u.resolve()) : u.reject();
      }
      function b0(V) {
        var le = this, ie = le.ready().then(function() {
          for (var ge = le._dbInfo.keyPrefix, Ce = localStorage.length - 1; Ce >= 0; Ce--) {
            var oe = localStorage.key(Ce);
            oe.indexOf(ge) === 0 && localStorage.removeItem(oe);
          }
        });
        return d(ie, V), ie;
      }
      function M0(V, le) {
        var ie = this;
        V = p(V);
        var ge = ie.ready().then(function() {
          var Ce = ie._dbInfo, oe = localStorage.getItem(Ce.keyPrefix + V);
          return oe && (oe = Ce.serializer.deserialize(oe)), oe;
        });
        return d(ge, le), ge;
      }
      function Ll(V, le) {
        var ie = this, ge = ie.ready().then(function() {
          for (var Ce = ie._dbInfo, oe = Ce.keyPrefix, He = oe.length, ct = localStorage.length, Ke = 1, tt = 0; tt < ct; tt++) {
            var ut = localStorage.key(tt);
            if (ut.indexOf(oe) === 0) {
              var _t = localStorage.getItem(ut);
              if (_t && (_t = Ce.serializer.deserialize(_t)), _t = V(_t, ut.substring(He), Ke++), _t !== void 0)
                return _t;
            }
          }
        });
        return d(ge, le), ge;
      }
      function Il(V, le) {
        var ie = this, ge = ie.ready().then(function() {
          var Ce = ie._dbInfo, oe;
          try {
            oe = localStorage.key(V);
          } catch {
            oe = null;
          }
          return oe && (oe = oe.substring(Ce.keyPrefix.length)), oe;
        });
        return d(ge, le), ge;
      }
      function dh(V) {
        var le = this, ie = le.ready().then(function() {
          for (var ge = le._dbInfo, Ce = localStorage.length, oe = [], He = 0; He < Ce; He++) {
            var ct = localStorage.key(He);
            ct.indexOf(ge.keyPrefix) === 0 && oe.push(ct.substring(ge.keyPrefix.length));
          }
          return oe;
        });
        return d(ie, V), ie;
      }
      function O(V) {
        var le = this, ie = le.keys().then(function(ge) {
          return ge.length;
        });
        return d(ie, V), ie;
      }
      function Z(V, le) {
        var ie = this;
        V = p(V);
        var ge = ie.ready().then(function() {
          var Ce = ie._dbInfo;
          localStorage.removeItem(Ce.keyPrefix + V);
        });
        return d(ge, le), ge;
      }
      function R(V, le, ie) {
        var ge = this;
        V = p(V);
        var Ce = ge.ready().then(function() {
          le === void 0 && (le = null);
          var oe = le;
          return new u(function(He, ct) {
            var Ke = ge._dbInfo;
            Ke.serializer.serialize(le, function(tt, ut) {
              if (ut)
                ct(ut);
              else
                try {
                  localStorage.setItem(Ke.keyPrefix + V, tt), He(oe);
                } catch (_t) {
                  (_t.name === "QuotaExceededError" || _t.name === "NS_ERROR_DOM_QUOTA_REACHED") && ct(_t), ct(_t);
                }
            });
          });
        });
        return d(Ce, ie), Ce;
      }
      function B(V, le) {
        if (le = v.apply(this, arguments), V = typeof V != "function" && V || {}, !V.name) {
          var ie = this.config();
          V.name = V.name || ie.name, V.storeName = V.storeName || ie.storeName;
        }
        var ge = this, Ce;
        return V.name ? Ce = new u(function(oe) {
          V.storeName ? oe(kr(V, ge._defaultConfig)) : oe(V.name + "/");
        }).then(function(oe) {
          for (var He = localStorage.length - 1; He >= 0; He--) {
            var ct = localStorage.key(He);
            ct.indexOf(oe) === 0 && localStorage.removeItem(ct);
          }
        }) : Ce = u.reject("Invalid arguments"), d(Ce, le), Ce;
      }
      var z = {
        _driver: "localStorageWrapper",
        _initStorage: Gr,
        _support: br(),
        iterate: Ll,
        getItem: M0,
        setItem: R,
        removeItem: Z,
        clear: b0,
        length: O,
        key: Il,
        keys: dh,
        dropInstance: B
      }, $ = function(le, ie) {
        return le === ie || typeof le == "number" && typeof ie == "number" && isNaN(le) && isNaN(ie);
      }, ce = function(le, ie) {
        for (var ge = le.length, Ce = 0; Ce < ge; ) {
          if ($(le[Ce], ie))
            return !0;
          Ce++;
        }
        return !1;
      }, se = Array.isArray || function(V) {
        return Object.prototype.toString.call(V) === "[object Array]";
      }, pe = {}, Ne = {}, De = {
        INDEXEDDB: Fe,
        WEBSQL: Os,
        LOCALSTORAGE: z
      }, Te = [De.INDEXEDDB._driver, De.WEBSQL._driver, De.LOCALSTORAGE._driver], Ue = ["dropInstance"], at = ["clear", "getItem", "iterate", "key", "keys", "length", "removeItem", "setItem"].concat(Ue), ht = {
        description: "",
        driver: Te.slice(),
        name: "localforage",
        // Default DB size is _JUST UNDER_ 5MB, as it's the highest size
        // we can use without a prompt.
        size: 4980736,
        storeName: "keyvaluepairs",
        version: 1
      };
      function At(V, le) {
        V[le] = function() {
          var ie = arguments;
          return V.ready().then(function() {
            return V[le].apply(V, ie);
          });
        };
      }
      function lt() {
        for (var V = 1; V < arguments.length; V++) {
          var le = arguments[V];
          if (le)
            for (var ie in le)
              le.hasOwnProperty(ie) && (se(le[ie]) ? arguments[0][ie] = le[ie].slice() : arguments[0][ie] = le[ie]);
        }
        return arguments[0];
      }
      var Oe = function() {
        function V(le) {
          a(this, V);
          for (var ie in De)
            if (De.hasOwnProperty(ie)) {
              var ge = De[ie], Ce = ge._driver;
              this[ie] = Ce, pe[Ce] || this.defineDriver(ge);
            }
          this._defaultConfig = lt({}, ht), this._config = lt({}, this._defaultConfig, le), this._driverSet = null, this._initDriver = null, this._ready = !1, this._dbInfo = null, this._wrapLibraryMethodsWithReady(), this.setDriver(this._config.driver).catch(function() {
          });
        }
        return V.prototype.config = function(ie) {
          if ((typeof ie > "u" ? "undefined" : r(ie)) === "object") {
            if (this._ready)
              return new Error("Can't call config() after localforage has been used.");
            for (var ge in ie) {
              if (ge === "storeName" && (ie[ge] = ie[ge].replace(/\W/g, "_")), ge === "version" && typeof ie[ge] != "number")
                return new Error("Database version must be a number.");
              this._config[ge] = ie[ge];
            }
            return "driver" in ie && ie.driver ? this.setDriver(this._config.driver) : !0;
          } else
            return typeof ie == "string" ? this._config[ie] : this._config;
        }, V.prototype.defineDriver = function(ie, ge, Ce) {
          var oe = new u(function(He, ct) {
            try {
              var Ke = ie._driver, tt = new Error("Custom driver not compliant; see https://mozilla.github.io/localForage/#definedriver");
              if (!ie._driver) {
                ct(tt);
                return;
              }
              for (var ut = at.concat("_initStorage"), _t = 0, ri = ut.length; _t < ri; _t++) {
                var fi = ut[_t], Ui = !ce(Ue, fi);
                if ((Ui || ie[fi]) && typeof ie[fi] != "function") {
                  ct(tt);
                  return;
                }
              }
              var ts = function() {
                for (var am = function(fz) {
                  return function() {
                    var pz = new Error("Method " + fz + " is not implemented by the current driver"), Gw = u.reject(pz);
                    return d(Gw, arguments[arguments.length - 1]), Gw;
                  };
                }, x8 = 0, dz = Ue.length; x8 < dz; x8++) {
                  var A8 = Ue[x8];
                  ie[A8] || (ie[A8] = am(A8));
                }
              };
              ts();
              var qn = function(am) {
                pe[Ke] && console.info("Redefining LocalForage driver: " + Ke), pe[Ke] = ie, Ne[Ke] = am, He();
              };
              "_support" in ie ? ie._support && typeof ie._support == "function" ? ie._support().then(qn, ct) : qn(!!ie._support) : qn(!0);
            } catch (oa) {
              ct(oa);
            }
          });
          return f(oe, ge, Ce), oe;
        }, V.prototype.driver = function() {
          return this._driver || null;
        }, V.prototype.getDriver = function(ie, ge, Ce) {
          var oe = pe[ie] ? u.resolve(pe[ie]) : u.reject(new Error("Driver not found."));
          return f(oe, ge, Ce), oe;
        }, V.prototype.getSerializer = function(ie) {
          var ge = u.resolve(Ct);
          return f(ge, ie), ge;
        }, V.prototype.ready = function(ie) {
          var ge = this, Ce = ge._driverSet.then(function() {
            return ge._ready === null && (ge._ready = ge._initDriver()), ge._ready;
          });
          return f(Ce, ie, ie), Ce;
        }, V.prototype.setDriver = function(ie, ge, Ce) {
          var oe = this;
          se(ie) || (ie = [ie]);
          var He = this._getSupportedDrivers(ie);
          function ct() {
            oe._config.driver = oe.driver();
          }
          function Ke(_t) {
            return oe._extend(_t), ct(), oe._ready = oe._initStorage(oe._config), oe._ready;
          }
          function tt(_t) {
            return function() {
              var ri = 0;
              function fi() {
                for (; ri < _t.length; ) {
                  var Ui = _t[ri];
                  return ri++, oe._dbInfo = null, oe._ready = null, oe.getDriver(Ui).then(Ke).catch(fi);
                }
                ct();
                var ts = new Error("No available storage method found.");
                return oe._driverSet = u.reject(ts), oe._driverSet;
              }
              return fi();
            };
          }
          var ut = this._driverSet !== null ? this._driverSet.catch(function() {
            return u.resolve();
          }) : u.resolve();
          return this._driverSet = ut.then(function() {
            var _t = He[0];
            return oe._dbInfo = null, oe._ready = null, oe.getDriver(_t).then(function(ri) {
              oe._driver = ri._driver, ct(), oe._wrapLibraryMethodsWithReady(), oe._initDriver = tt(He);
            });
          }).catch(function() {
            ct();
            var _t = new Error("No available storage method found.");
            return oe._driverSet = u.reject(_t), oe._driverSet;
          }), f(this._driverSet, ge, Ce), this._driverSet;
        }, V.prototype.supports = function(ie) {
          return !!Ne[ie];
        }, V.prototype._extend = function(ie) {
          lt(this, ie);
        }, V.prototype._getSupportedDrivers = function(ie) {
          for (var ge = [], Ce = 0, oe = ie.length; Ce < oe; Ce++) {
            var He = ie[Ce];
            this.supports(He) && ge.push(He);
          }
          return ge;
        }, V.prototype._wrapLibraryMethodsWithReady = function() {
          for (var ie = 0, ge = at.length; ie < ge; ie++)
            At(this, at[ie]);
        }, V.prototype.createInstance = function(ie) {
          return new V(ie);
        }, V;
      }(), yt = new Oe();
      i.exports = yt;
    }, { 3: 3 }] }, {}, [4])(4);
  });
})(gN);
var ux = gN.exports;
const dx = /* @__PURE__ */ fM(ux);
function Ine(s) {
  return s && s.__esModule && Object.prototype.hasOwnProperty.call(s, "default") ? s.default : s;
}
var vN = { exports: {} };
(function(s, e) {
  (function(t) {
    var i = /^(?=((?:[a-zA-Z0-9+\-.]+:)?))\1(?=((?:\/\/[^\/?#]*)?))\2(?=((?:(?:[^?#\/]*\/)*[^;?#\/]*)?))\3((?:;[^?#]*)?)(\?[^#]*)?(#[^]*)?$/, n = /^(?=([^\/?#]*))\1([^]*)$/, r = /(?:\/|^)\.(?=\/)/g, a = /(?:\/|^)\.\.\/(?!\.\.\/)[^\/]*(?=\/)/g, o = {
      // If opts.alwaysNormalize is true then the path will always be normalized even when it starts with / or //
      // E.g
      // With opts.alwaysNormalize = false (default, spec compliant)
      // http://a.com/b/cd + /e/f/../g => http://a.com/e/f/../g
      // With opts.alwaysNormalize = true (not spec compliant)
      // http://a.com/b/cd + /e/f/../g => http://a.com/e/g
      buildAbsoluteURL: function(l, c, h) {
        if (h = h || {}, l = l.trim(), c = c.trim(), !c) {
          if (!h.alwaysNormalize)
            return l;
          var u = o.parseURL(l);
          if (!u)
            throw new Error("Error trying to parse base URL.");
          return u.path = o.normalizePath(
            u.path
          ), o.buildURLFromParts(u);
        }
        var d = o.parseURL(c);
        if (!d)
          throw new Error("Error trying to parse relative URL.");
        if (d.scheme)
          return h.alwaysNormalize ? (d.path = o.normalizePath(d.path), o.buildURLFromParts(d)) : c;
        var f = o.parseURL(l);
        if (!f)
          throw new Error("Error trying to parse base URL.");
        if (!f.netLoc && f.path && f.path[0] !== "/") {
          var p = n.exec(f.path);
          f.netLoc = p[1], f.path = p[2];
        }
        f.netLoc && !f.path && (f.path = "/");
        var v = {
          // 2c) Otherwise, the embedded URL inherits the scheme of
          // the base URL.
          scheme: f.scheme,
          netLoc: d.netLoc,
          path: null,
          params: d.params,
          query: d.query,
          fragment: d.fragment
        };
        if (!d.netLoc && (v.netLoc = f.netLoc, d.path[0] !== "/"))
          if (!d.path)
            v.path = f.path, d.params || (v.params = f.params, d.query || (v.query = f.query));
          else {
            var g = f.path, m = g.substring(0, g.lastIndexOf("/") + 1) + d.path;
            v.path = o.normalizePath(m);
          }
        return v.path === null && (v.path = h.alwaysNormalize ? o.normalizePath(d.path) : d.path), o.buildURLFromParts(v);
      },
      parseURL: function(l) {
        var c = i.exec(l);
        return c ? {
          scheme: c[1] || "",
          netLoc: c[2] || "",
          path: c[3] || "",
          params: c[4] || "",
          query: c[5] || "",
          fragment: c[6] || ""
        } : null;
      },
      normalizePath: function(l) {
        for (l = l.split("").reverse().join("").replace(r, ""); l.length !== (l = l.replace(a, "")).length; )
          ;
        return l.split("").reverse().join("");
      },
      buildURLFromParts: function(l) {
        return l.scheme + l.netLoc + l.path + l.params + l.query + l.fragment;
      }
    };
    s.exports = o;
  })();
})(vN);
var jS = vN.exports;
function FC(s, e) {
  var t = Object.keys(s);
  if (Object.getOwnPropertySymbols) {
    var i = Object.getOwnPropertySymbols(s);
    e && (i = i.filter(function(n) {
      return Object.getOwnPropertyDescriptor(s, n).enumerable;
    })), t.push.apply(t, i);
  }
  return t;
}
function Ls(s) {
  for (var e = 1; e < arguments.length; e++) {
    var t = arguments[e] != null ? arguments[e] : {};
    e % 2 ? FC(Object(t), !0).forEach(function(i) {
      Bne(s, i, t[i]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(s, Object.getOwnPropertyDescriptors(t)) : FC(Object(t)).forEach(function(i) {
      Object.defineProperty(s, i, Object.getOwnPropertyDescriptor(t, i));
    });
  }
  return s;
}
function One(s, e) {
  if (typeof s != "object" || !s)
    return s;
  var t = s[Symbol.toPrimitive];
  if (t !== void 0) {
    var i = t.call(s, e || "default");
    if (typeof i != "object")
      return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (e === "string" ? String : Number)(s);
}
function Nne(s) {
  var e = One(s, "string");
  return typeof e == "symbol" ? e : String(e);
}
function Bne(s, e, t) {
  return e = Nne(e), e in s ? Object.defineProperty(s, e, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : s[e] = t, s;
}
function as() {
  return as = Object.assign ? Object.assign.bind() : function(s) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var i in t)
        Object.prototype.hasOwnProperty.call(t, i) && (s[i] = t[i]);
    }
    return s;
  }, as.apply(this, arguments);
}
const Bt = Number.isFinite || function(s) {
  return typeof s == "number" && isFinite(s);
}, Fne = Number.isSafeInteger || function(s) {
  return typeof s == "number" && Math.abs(s) <= Une;
}, Une = Number.MAX_SAFE_INTEGER || 9007199254740991;
let j = /* @__PURE__ */ function(s) {
  return s.MEDIA_ATTACHING = "hlsMediaAttaching", s.MEDIA_ATTACHED = "hlsMediaAttached", s.MEDIA_DETACHING = "hlsMediaDetaching", s.MEDIA_DETACHED = "hlsMediaDetached", s.BUFFER_RESET = "hlsBufferReset", s.BUFFER_CODECS = "hlsBufferCodecs", s.BUFFER_CREATED = "hlsBufferCreated", s.BUFFER_APPENDING = "hlsBufferAppending", s.BUFFER_APPENDED = "hlsBufferAppended", s.BUFFER_EOS = "hlsBufferEos", s.BUFFER_FLUSHING = "hlsBufferFlushing", s.BUFFER_FLUSHED = "hlsBufferFlushed", s.MANIFEST_LOADING = "hlsManifestLoading", s.MANIFEST_LOADED = "hlsManifestLoaded", s.MANIFEST_PARSED = "hlsManifestParsed", s.LEVEL_SWITCHING = "hlsLevelSwitching", s.LEVEL_SWITCHED = "hlsLevelSwitched", s.LEVEL_LOADING = "hlsLevelLoading", s.LEVEL_LOADED = "hlsLevelLoaded", s.LEVEL_UPDATED = "hlsLevelUpdated", s.LEVEL_PTS_UPDATED = "hlsLevelPtsUpdated", s.LEVELS_UPDATED = "hlsLevelsUpdated", s.AUDIO_TRACKS_UPDATED = "hlsAudioTracksUpdated", s.AUDIO_TRACK_SWITCHING = "hlsAudioTrackSwitching", s.AUDIO_TRACK_SWITCHED = "hlsAudioTrackSwitched", s.AUDIO_TRACK_LOADING = "hlsAudioTrackLoading", s.AUDIO_TRACK_LOADED = "hlsAudioTrackLoaded", s.SUBTITLE_TRACKS_UPDATED = "hlsSubtitleTracksUpdated", s.SUBTITLE_TRACKS_CLEARED = "hlsSubtitleTracksCleared", s.SUBTITLE_TRACK_SWITCH = "hlsSubtitleTrackSwitch", s.SUBTITLE_TRACK_LOADING = "hlsSubtitleTrackLoading", s.SUBTITLE_TRACK_LOADED = "hlsSubtitleTrackLoaded", s.SUBTITLE_FRAG_PROCESSED = "hlsSubtitleFragProcessed", s.CUES_PARSED = "hlsCuesParsed", s.NON_NATIVE_TEXT_TRACKS_FOUND = "hlsNonNativeTextTracksFound", s.INIT_PTS_FOUND = "hlsInitPtsFound", s.FRAG_LOADING = "hlsFragLoading", s.FRAG_LOAD_EMERGENCY_ABORTED = "hlsFragLoadEmergencyAborted", s.FRAG_LOADED = "hlsFragLoaded", s.FRAG_DECRYPTED = "hlsFragDecrypted", s.FRAG_PARSING_INIT_SEGMENT = "hlsFragParsingInitSegment", s.FRAG_PARSING_USERDATA = "hlsFragParsingUserdata", s.FRAG_PARSING_METADATA = "hlsFragParsingMetadata", s.FRAG_PARSED = "hlsFragParsed", s.FRAG_BUFFERED = "hlsFragBuffered", s.FRAG_CHANGED = "hlsFragChanged", s.FPS_DROP = "hlsFpsDrop", s.FPS_DROP_LEVEL_CAPPING = "hlsFpsDropLevelCapping", s.MAX_AUTO_LEVEL_UPDATED = "hlsMaxAutoLevelUpdated", s.ERROR = "hlsError", s.DESTROYING = "hlsDestroying", s.KEY_LOADING = "hlsKeyLoading", s.KEY_LOADED = "hlsKeyLoaded", s.LIVE_BACK_BUFFER_REACHED = "hlsLiveBackBufferReached", s.BACK_BUFFER_REACHED = "hlsBackBufferReached", s.STEERING_MANIFEST_LOADED = "hlsSteeringManifestLoaded", s;
}({}), qt = /* @__PURE__ */ function(s) {
  return s.NETWORK_ERROR = "networkError", s.MEDIA_ERROR = "mediaError", s.KEY_SYSTEM_ERROR = "keySystemError", s.MUX_ERROR = "muxError", s.OTHER_ERROR = "otherError", s;
}({}), Xe = /* @__PURE__ */ function(s) {
  return s.KEY_SYSTEM_NO_KEYS = "keySystemNoKeys", s.KEY_SYSTEM_NO_ACCESS = "keySystemNoAccess", s.KEY_SYSTEM_NO_SESSION = "keySystemNoSession", s.KEY_SYSTEM_NO_CONFIGURED_LICENSE = "keySystemNoConfiguredLicense", s.KEY_SYSTEM_LICENSE_REQUEST_FAILED = "keySystemLicenseRequestFailed", s.KEY_SYSTEM_SERVER_CERTIFICATE_REQUEST_FAILED = "keySystemServerCertificateRequestFailed", s.KEY_SYSTEM_SERVER_CERTIFICATE_UPDATE_FAILED = "keySystemServerCertificateUpdateFailed", s.KEY_SYSTEM_SESSION_UPDATE_FAILED = "keySystemSessionUpdateFailed", s.KEY_SYSTEM_STATUS_OUTPUT_RESTRICTED = "keySystemStatusOutputRestricted", s.KEY_SYSTEM_STATUS_INTERNAL_ERROR = "keySystemStatusInternalError", s.MANIFEST_LOAD_ERROR = "manifestLoadError", s.MANIFEST_LOAD_TIMEOUT = "manifestLoadTimeOut", s.MANIFEST_PARSING_ERROR = "manifestParsingError", s.MANIFEST_INCOMPATIBLE_CODECS_ERROR = "manifestIncompatibleCodecsError", s.LEVEL_EMPTY_ERROR = "levelEmptyError", s.LEVEL_LOAD_ERROR = "levelLoadError", s.LEVEL_LOAD_TIMEOUT = "levelLoadTimeOut", s.LEVEL_PARSING_ERROR = "levelParsingError", s.LEVEL_SWITCH_ERROR = "levelSwitchError", s.AUDIO_TRACK_LOAD_ERROR = "audioTrackLoadError", s.AUDIO_TRACK_LOAD_TIMEOUT = "audioTrackLoadTimeOut", s.SUBTITLE_LOAD_ERROR = "subtitleTrackLoadError", s.SUBTITLE_TRACK_LOAD_TIMEOUT = "subtitleTrackLoadTimeOut", s.FRAG_LOAD_ERROR = "fragLoadError", s.FRAG_LOAD_TIMEOUT = "fragLoadTimeOut", s.FRAG_DECRYPT_ERROR = "fragDecryptError", s.FRAG_PARSING_ERROR = "fragParsingError", s.FRAG_GAP = "fragGap", s.REMUX_ALLOC_ERROR = "remuxAllocError", s.KEY_LOAD_ERROR = "keyLoadError", s.KEY_LOAD_TIMEOUT = "keyLoadTimeOut", s.BUFFER_ADD_CODEC_ERROR = "bufferAddCodecError", s.BUFFER_INCOMPATIBLE_CODECS_ERROR = "bufferIncompatibleCodecsError", s.BUFFER_APPEND_ERROR = "bufferAppendError", s.BUFFER_APPENDING_ERROR = "bufferAppendingError", s.BUFFER_STALLED_ERROR = "bufferStalledError", s.BUFFER_FULL_ERROR = "bufferFullError", s.BUFFER_SEEK_OVER_HOLE = "bufferSeekOverHole", s.BUFFER_NUDGE_ON_STALL = "bufferNudgeOnStall", s.INTERNAL_EXCEPTION = "internalException", s.INTERNAL_ABORTED = "aborted", s.UNKNOWN = "unknown", s;
}({});
const Kh = function() {
}, wb = {
  trace: Kh,
  debug: Kh,
  log: Kh,
  warn: Kh,
  info: Kh,
  error: Kh
};
let O1 = wb;
function zne(s) {
  const e = self.console[s];
  return e ? e.bind(self.console, `[${s}] >`) : Kh;
}
function kne(s, ...e) {
  e.forEach(function(t) {
    O1[t] = s[t] ? s[t].bind(s) : zne(t);
  });
}
function Gne(s, e) {
  if (typeof console == "object" && s === !0 || typeof s == "object") {
    kne(
      s,
      // Remove out from list here to hard-disable a log-level
      // 'trace',
      "debug",
      "log",
      "info",
      "warn",
      "error"
    );
    try {
      O1.log(`Debug logs enabled for "${e}" in hls.js version 1.5.8`);
    } catch {
      O1 = wb;
    }
  } else
    O1 = wb;
}
const ze = O1, Hne = /^(\d+)x(\d+)$/, UC = /(.+?)=(".*?"|.*?)(?:,|$)/g;
class Bn {
  constructor(e) {
    typeof e == "string" && (e = Bn.parseAttrList(e)), as(this, e);
  }
  get clientAttrs() {
    return Object.keys(this).filter((e) => e.substring(0, 2) === "X-");
  }
  decimalInteger(e) {
    const t = parseInt(this[e], 10);
    return t > Number.MAX_SAFE_INTEGER ? 1 / 0 : t;
  }
  hexadecimalInteger(e) {
    if (this[e]) {
      let t = (this[e] || "0x").slice(2);
      t = (t.length & 1 ? "0" : "") + t;
      const i = new Uint8Array(t.length / 2);
      for (let n = 0; n < t.length / 2; n++)
        i[n] = parseInt(t.slice(n * 2, n * 2 + 2), 16);
      return i;
    } else
      return null;
  }
  hexadecimalIntegerAsNumber(e) {
    const t = parseInt(this[e], 16);
    return t > Number.MAX_SAFE_INTEGER ? 1 / 0 : t;
  }
  decimalFloatingPoint(e) {
    return parseFloat(this[e]);
  }
  optionalFloat(e, t) {
    const i = this[e];
    return i ? parseFloat(i) : t;
  }
  enumeratedString(e) {
    return this[e];
  }
  bool(e) {
    return this[e] === "YES";
  }
  decimalResolution(e) {
    const t = Hne.exec(this[e]);
    if (t !== null)
      return {
        width: parseInt(t[1], 10),
        height: parseInt(t[2], 10)
      };
  }
  static parseAttrList(e) {
    let t;
    const i = {}, n = '"';
    for (UC.lastIndex = 0; (t = UC.exec(e)) !== null; ) {
      let r = t[2];
      r.indexOf(n) === 0 && r.lastIndexOf(n) === r.length - 1 && (r = r.slice(1, -1));
      const a = t[1].trim();
      i[a] = r;
    }
    return i;
  }
}
function Vne(s) {
  return s !== "ID" && s !== "CLASS" && s !== "START-DATE" && s !== "DURATION" && s !== "END-DATE" && s !== "END-ON-NEXT";
}
function Wne(s) {
  return s === "SCTE35-OUT" || s === "SCTE35-IN";
}
class yN {
  constructor(e, t) {
    if (this.attr = void 0, this._startDate = void 0, this._endDate = void 0, this._badValueForSameId = void 0, t) {
      const i = t.attr;
      for (const n in i)
        if (Object.prototype.hasOwnProperty.call(e, n) && e[n] !== i[n]) {
          ze.warn(`DATERANGE tag attribute: "${n}" does not match for tags with ID: "${e.ID}"`), this._badValueForSameId = n;
          break;
        }
      e = as(new Bn({}), i, e);
    }
    if (this.attr = e, this._startDate = new Date(e["START-DATE"]), "END-DATE" in this.attr) {
      const i = new Date(this.attr["END-DATE"]);
      Bt(i.getTime()) && (this._endDate = i);
    }
  }
  get id() {
    return this.attr.ID;
  }
  get class() {
    return this.attr.CLASS;
  }
  get startDate() {
    return this._startDate;
  }
  get endDate() {
    if (this._endDate)
      return this._endDate;
    const e = this.duration;
    return e !== null ? new Date(this._startDate.getTime() + e * 1e3) : null;
  }
  get duration() {
    if ("DURATION" in this.attr) {
      const e = this.attr.decimalFloatingPoint("DURATION");
      if (Bt(e))
        return e;
    } else if (this._endDate)
      return (this._endDate.getTime() - this._startDate.getTime()) / 1e3;
    return null;
  }
  get plannedDuration() {
    return "PLANNED-DURATION" in this.attr ? this.attr.decimalFloatingPoint("PLANNED-DURATION") : null;
  }
  get endOnNext() {
    return this.attr.bool("END-ON-NEXT");
  }
  get isValid() {
    return !!this.id && !this._badValueForSameId && Bt(this.startDate.getTime()) && (this.duration === null || this.duration >= 0) && (!this.endOnNext || !!this.class);
  }
}
class o8 {
  constructor() {
    this.aborted = !1, this.loaded = 0, this.retry = 0, this.total = 0, this.chunkCount = 0, this.bwEstimate = 0, this.loading = {
      start: 0,
      first: 0,
      end: 0
    }, this.parsing = {
      start: 0,
      end: 0
    }, this.buffering = {
      start: 0,
      first: 0,
      end: 0
    };
  }
}
var an = {
  AUDIO: "audio",
  VIDEO: "video",
  AUDIOVIDEO: "audiovideo"
};
class xN {
  constructor(e) {
    this._byteRange = null, this._url = null, this.baseurl = void 0, this.relurl = void 0, this.elementaryStreams = {
      [an.AUDIO]: null,
      [an.VIDEO]: null,
      [an.AUDIOVIDEO]: null
    }, this.baseurl = e;
  }
  // setByteRange converts a EXT-X-BYTERANGE attribute into a two element array
  setByteRange(e, t) {
    const i = e.split("@", 2);
    let n;
    i.length === 1 ? n = (t == null ? void 0 : t.byteRangeEndOffset) || 0 : n = parseInt(i[1]), this._byteRange = [n, parseInt(i[0]) + n];
  }
  get byteRange() {
    return this._byteRange ? this._byteRange : [];
  }
  get byteRangeStartOffset() {
    return this.byteRange[0];
  }
  get byteRangeEndOffset() {
    return this.byteRange[1];
  }
  get url() {
    return !this._url && this.baseurl && this.relurl && (this._url = jS.buildAbsoluteURL(this.baseurl, this.relurl, {
      alwaysNormalize: !0
    })), this._url || "";
  }
  set url(e) {
    this._url = e;
  }
}
class iA extends xN {
  constructor(e, t) {
    super(t), this._decryptdata = null, this.rawProgramDateTime = null, this.programDateTime = null, this.tagList = [], this.duration = 0, this.sn = 0, this.levelkeys = void 0, this.type = void 0, this.loader = null, this.keyLoader = null, this.level = -1, this.cc = 0, this.startPTS = void 0, this.endPTS = void 0, this.startDTS = void 0, this.endDTS = void 0, this.start = 0, this.deltaPTS = void 0, this.maxStartPTS = void 0, this.minEndPTS = void 0, this.stats = new o8(), this.data = void 0, this.bitrateTest = !1, this.title = null, this.initSegment = null, this.endList = void 0, this.gap = void 0, this.urlId = 0, this.type = e;
  }
  get decryptdata() {
    const {
      levelkeys: e
    } = this;
    if (!e && !this._decryptdata)
      return null;
    if (!this._decryptdata && this.levelkeys && !this.levelkeys.NONE) {
      const t = this.levelkeys.identity;
      if (t)
        this._decryptdata = t.getDecryptData(this.sn);
      else {
        const i = Object.keys(this.levelkeys);
        if (i.length === 1)
          return this._decryptdata = this.levelkeys[i[0]].getDecryptData(this.sn);
      }
    }
    return this._decryptdata;
  }
  get end() {
    return this.start + this.duration;
  }
  get endProgramDateTime() {
    if (this.programDateTime === null || !Bt(this.programDateTime))
      return null;
    const e = Bt(this.duration) ? this.duration : 0;
    return this.programDateTime + e * 1e3;
  }
  get encrypted() {
    var e;
    if ((e = this._decryptdata) != null && e.encrypted)
      return !0;
    if (this.levelkeys) {
      const t = Object.keys(this.levelkeys), i = t.length;
      if (i > 1 || i === 1 && this.levelkeys[t[0]].encrypted)
        return !0;
    }
    return !1;
  }
  setKeyFormat(e) {
    if (this.levelkeys) {
      const t = this.levelkeys[e];
      t && !this._decryptdata && (this._decryptdata = t.getDecryptData(this.sn));
    }
  }
  abortRequests() {
    var e, t;
    (e = this.loader) == null || e.abort(), (t = this.keyLoader) == null || t.abort();
  }
  setElementaryStreamInfo(e, t, i, n, r, a = !1) {
    const {
      elementaryStreams: o
    } = this, l = o[e];
    if (!l) {
      o[e] = {
        startPTS: t,
        endPTS: i,
        startDTS: n,
        endDTS: r,
        partial: a
      };
      return;
    }
    l.startPTS = Math.min(l.startPTS, t), l.endPTS = Math.max(l.endPTS, i), l.startDTS = Math.min(l.startDTS, n), l.endDTS = Math.max(l.endDTS, r);
  }
  clearElementaryStreamInfo() {
    const {
      elementaryStreams: e
    } = this;
    e[an.AUDIO] = null, e[an.VIDEO] = null, e[an.AUDIOVIDEO] = null;
  }
}
class Xne extends xN {
  constructor(e, t, i, n, r) {
    super(i), this.fragOffset = 0, this.duration = 0, this.gap = !1, this.independent = !1, this.relurl = void 0, this.fragment = void 0, this.index = void 0, this.stats = new o8(), this.duration = e.decimalFloatingPoint("DURATION"), this.gap = e.bool("GAP"), this.independent = e.bool("INDEPENDENT"), this.relurl = e.enumeratedString("URI"), this.fragment = t, this.index = n;
    const a = e.enumeratedString("BYTERANGE");
    a && this.setByteRange(a, r), r && (this.fragOffset = r.fragOffset + r.duration);
  }
  get start() {
    return this.fragment.start + this.fragOffset;
  }
  get end() {
    return this.start + this.duration;
  }
  get loaded() {
    const {
      elementaryStreams: e
    } = this;
    return !!(e.audio || e.video || e.audiovideo);
  }
}
const jne = 10;
class qne {
  constructor(e) {
    this.PTSKnown = !1, this.alignedSliding = !1, this.averagetargetduration = void 0, this.endCC = 0, this.endSN = 0, this.fragments = void 0, this.fragmentHint = void 0, this.partList = null, this.dateRanges = void 0, this.live = !0, this.ageHeader = 0, this.advancedDateTime = void 0, this.updated = !0, this.advanced = !0, this.availabilityDelay = void 0, this.misses = 0, this.startCC = 0, this.startSN = 0, this.startTimeOffset = null, this.targetduration = 0, this.totalduration = 0, this.type = null, this.url = void 0, this.m3u8 = "", this.version = null, this.canBlockReload = !1, this.canSkipUntil = 0, this.canSkipDateRanges = !1, this.skippedSegments = 0, this.recentlyRemovedDateranges = void 0, this.partHoldBack = 0, this.holdBack = 0, this.partTarget = 0, this.preloadHint = void 0, this.renditionReports = void 0, this.tuneInGoal = 0, this.deltaUpdateFailed = void 0, this.driftStartTime = 0, this.driftEndTime = 0, this.driftStart = 0, this.driftEnd = 0, this.encryptedFragments = void 0, this.playlistParsingError = null, this.variableList = null, this.hasVariableRefs = !1, this.fragments = [], this.encryptedFragments = [], this.dateRanges = {}, this.url = e;
  }
  reloaded(e) {
    if (!e) {
      this.advanced = !0, this.updated = !0;
      return;
    }
    const t = this.lastPartSn - e.lastPartSn, i = this.lastPartIndex - e.lastPartIndex;
    this.updated = this.endSN !== e.endSN || !!i || !!t || !this.live, this.advanced = this.endSN > e.endSN || t > 0 || t === 0 && i > 0, this.updated || this.advanced ? this.misses = Math.floor(e.misses * 0.6) : this.misses = e.misses + 1, this.availabilityDelay = e.availabilityDelay;
  }
  get hasProgramDateTime() {
    return this.fragments.length ? Bt(this.fragments[this.fragments.length - 1].programDateTime) : !1;
  }
  get levelTargetDuration() {
    return this.averagetargetduration || this.targetduration || jne;
  }
  get drift() {
    const e = this.driftEndTime - this.driftStartTime;
    return e > 0 ? (this.driftEnd - this.driftStart) * 1e3 / e : 1;
  }
  get edge() {
    return this.partEnd || this.fragmentEnd;
  }
  get partEnd() {
    var e;
    return (e = this.partList) != null && e.length ? this.partList[this.partList.length - 1].end : this.fragmentEnd;
  }
  get fragmentEnd() {
    var e;
    return (e = this.fragments) != null && e.length ? this.fragments[this.fragments.length - 1].end : 0;
  }
  get age() {
    return this.advancedDateTime ? Math.max(Date.now() - this.advancedDateTime, 0) / 1e3 : 0;
  }
  get lastPartIndex() {
    var e;
    return (e = this.partList) != null && e.length ? this.partList[this.partList.length - 1].index : -1;
  }
  get lastPartSn() {
    var e;
    return (e = this.partList) != null && e.length ? this.partList[this.partList.length - 1].fragment.sn : this.endSN;
  }
}
function qS(s) {
  return Uint8Array.from(atob(s), (e) => e.charCodeAt(0));
}
function Yne(s) {
  const e = Eb(s).subarray(0, 16), t = new Uint8Array(16);
  return t.set(e, 16 - e.length), t;
}
function Zne(s) {
  const e = function(i, n, r) {
    const a = i[n];
    i[n] = i[r], i[r] = a;
  };
  e(s, 0, 3), e(s, 1, 2), e(s, 4, 5), e(s, 6, 7);
}
function Kne(s) {
  const e = s.split(":");
  let t = null;
  if (e[0] === "data" && e.length === 2) {
    const i = e[1].split(";"), n = i[i.length - 1].split(",");
    if (n.length === 2) {
      const r = n[0] === "base64", a = n[1];
      r ? (i.splice(-1, 1), t = qS(a)) : t = Yne(a);
    }
  }
  return t;
}
function Eb(s) {
  return Uint8Array.from(unescape(encodeURIComponent(s)), (e) => e.charCodeAt(0));
}
const Ep = typeof self < "u" ? self : void 0;
var On = {
  CLEARKEY: "org.w3.clearkey",
  FAIRPLAY: "com.apple.fps",
  PLAYREADY: "com.microsoft.playready",
  WIDEVINE: "com.widevine.alpha"
}, Ir = {
  CLEARKEY: "org.w3.clearkey",
  FAIRPLAY: "com.apple.streamingkeydelivery",
  PLAYREADY: "com.microsoft.playready",
  WIDEVINE: "urn:uuid:edef8ba9-79d6-4ace-a3c8-27dcd51d21ed"
};
function zC(s) {
  switch (s) {
    case Ir.FAIRPLAY:
      return On.FAIRPLAY;
    case Ir.PLAYREADY:
      return On.PLAYREADY;
    case Ir.WIDEVINE:
      return On.WIDEVINE;
    case Ir.CLEARKEY:
      return On.CLEARKEY;
  }
}
var AN = {
  WIDEVINE: "edef8ba979d64acea3c827dcd51d21ed"
};
function Jne(s) {
  if (s === AN.WIDEVINE)
    return On.WIDEVINE;
}
function kC(s) {
  switch (s) {
    case On.FAIRPLAY:
      return Ir.FAIRPLAY;
    case On.PLAYREADY:
      return Ir.PLAYREADY;
    case On.WIDEVINE:
      return Ir.WIDEVINE;
    case On.CLEARKEY:
      return Ir.CLEARKEY;
  }
}
function nA(s) {
  const {
    drmSystems: e,
    widevineLicenseUrl: t
  } = s, i = e ? [On.FAIRPLAY, On.WIDEVINE, On.PLAYREADY, On.CLEARKEY].filter((n) => !!e[n]) : [];
  return !i[On.WIDEVINE] && t && i.push(On.WIDEVINE), i;
}
const bN = function(s) {
  return Ep != null && (s = Ep.navigator) != null && s.requestMediaKeySystemAccess ? self.navigator.requestMediaKeySystemAccess.bind(self.navigator) : null;
}();
function Qne(s, e, t, i) {
  let n;
  switch (s) {
    case On.FAIRPLAY:
      n = ["cenc", "sinf"];
      break;
    case On.WIDEVINE:
    case On.PLAYREADY:
      n = ["cenc"];
      break;
    case On.CLEARKEY:
      n = ["cenc", "keyids"];
      break;
    default:
      throw new Error(`Unknown key-system: ${s}`);
  }
  return $ne(n, e, t, i);
}
function $ne(s, e, t, i) {
  return [{
    initDataTypes: s,
    persistentState: i.persistentState || "optional",
    distinctiveIdentifier: i.distinctiveIdentifier || "optional",
    sessionTypes: i.sessionTypes || [i.sessionType || "temporary"],
    audioCapabilities: e.map((r) => ({
      contentType: `audio/mp4; codecs="${r}"`,
      robustness: i.audioRobustness || "",
      encryptionScheme: i.audioEncryptionScheme || null
    })),
    videoCapabilities: t.map((r) => ({
      contentType: `video/mp4; codecs="${r}"`,
      robustness: i.videoRobustness || "",
      encryptionScheme: i.videoEncryptionScheme || null
    }))
  }];
}
function Eu(s, e, t) {
  return Uint8Array.prototype.slice ? s.slice(e, t) : new Uint8Array(Array.prototype.slice.call(s, e, t));
}
const YS = (s, e) => e + 10 <= s.length && s[e] === 73 && s[e + 1] === 68 && s[e + 2] === 51 && s[e + 3] < 255 && s[e + 4] < 255 && s[e + 6] < 128 && s[e + 7] < 128 && s[e + 8] < 128 && s[e + 9] < 128, MN = (s, e) => e + 10 <= s.length && s[e] === 51 && s[e + 1] === 68 && s[e + 2] === 73 && s[e + 3] < 255 && s[e + 4] < 255 && s[e + 6] < 128 && s[e + 7] < 128 && s[e + 8] < 128 && s[e + 9] < 128, Dg = (s, e) => {
  const t = e;
  let i = 0;
  for (; YS(s, e); ) {
    i += 10;
    const n = l8(s, e + 6);
    i += n, MN(s, e + 10) && (i += 10), e += i;
  }
  if (i > 0)
    return s.subarray(t, t + i);
}, l8 = (s, e) => {
  let t = 0;
  return t = (s[e] & 127) << 21, t |= (s[e + 1] & 127) << 14, t |= (s[e + 2] & 127) << 7, t |= s[e + 3] & 127, t;
}, ese = (s, e) => YS(s, e) && l8(s, e + 6) + 10 <= s.length - e, ZS = (s) => {
  const e = wN(s);
  for (let t = 0; t < e.length; t++) {
    const i = e[t];
    if (SN(i))
      return ase(i);
  }
}, SN = (s) => s && s.key === "PRIV" && s.info === "com.apple.streaming.transportStreamTimestamp", tse = (s) => {
  const e = String.fromCharCode(s[0], s[1], s[2], s[3]), t = l8(s, 4), i = 10;
  return {
    type: e,
    size: t,
    data: s.subarray(i, i + t)
  };
}, wN = (s) => {
  let e = 0;
  const t = [];
  for (; YS(s, e); ) {
    const i = l8(s, e + 6);
    e += 10;
    const n = e + i;
    for (; e + 8 < n; ) {
      const r = tse(s.subarray(e)), a = ise(r);
      a && t.push(a), e += r.size + 10;
    }
    MN(s, e) && (e += 10);
  }
  return t;
}, ise = (s) => s.type === "PRIV" ? nse(s) : s.type[0] === "W" ? rse(s) : sse(s), nse = (s) => {
  if (s.size < 2)
    return;
  const e = t0(s.data, !0), t = new Uint8Array(s.data.subarray(e.length + 1));
  return {
    key: s.type,
    info: e,
    data: t.buffer
  };
}, sse = (s) => {
  if (s.size < 2)
    return;
  if (s.type === "TXXX") {
    let t = 1;
    const i = t0(s.data.subarray(t), !0);
    t += i.length + 1;
    const n = t0(s.data.subarray(t));
    return {
      key: s.type,
      info: i,
      data: n
    };
  }
  const e = t0(s.data.subarray(1));
  return {
    key: s.type,
    data: e
  };
}, rse = (s) => {
  if (s.type === "WXXX") {
    if (s.size < 2)
      return;
    let t = 1;
    const i = t0(s.data.subarray(t), !0);
    t += i.length + 1;
    const n = t0(s.data.subarray(t));
    return {
      key: s.type,
      info: i,
      data: n
    };
  }
  const e = t0(s.data);
  return {
    key: s.type,
    data: e
  };
}, ase = (s) => {
  if (s.data.byteLength === 8) {
    const e = new Uint8Array(s.data), t = e[3] & 1;
    let i = (e[4] << 23) + (e[5] << 15) + (e[6] << 7) + e[7];
    return i /= 45, t && (i += 4772185884e-2), Math.round(i);
  }
}, t0 = (s, e = !1) => {
  const t = ose();
  if (t) {
    const c = t.decode(s);
    if (e) {
      const h = c.indexOf("\0");
      return h !== -1 ? c.substring(0, h) : c;
    }
    return c.replace(/\0/g, "");
  }
  const i = s.length;
  let n, r, a, o = "", l = 0;
  for (; l < i; ) {
    if (n = s[l++], n === 0 && e)
      return o;
    if (n === 0 || n === 3)
      continue;
    switch (n >> 4) {
      case 0:
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
        o += String.fromCharCode(n);
        break;
      case 12:
      case 13:
        r = s[l++], o += String.fromCharCode((n & 31) << 6 | r & 63);
        break;
      case 14:
        r = s[l++], a = s[l++], o += String.fromCharCode((n & 15) << 12 | (r & 63) << 6 | (a & 63) << 0);
        break;
    }
  }
  return o;
};
let sA;
function ose() {
  if (!navigator.userAgent.includes("PlayStation 4"))
    return !sA && typeof self.TextDecoder < "u" && (sA = new self.TextDecoder("utf-8")), sA;
}
const Io = {
  hexDump: function(s) {
    let e = "";
    for (let t = 0; t < s.length; t++) {
      let i = s[t].toString(16);
      i.length < 2 && (i = "0" + i), e += i;
    }
    return e;
  }
}, fx = Math.pow(2, 32) - 1, lse = [].push, EN = {
  video: 1,
  audio: 2,
  id3: 3,
  text: 4
};
function ls(s) {
  return String.fromCharCode.apply(null, s);
}
function TN(s, e) {
  const t = s[e] << 8 | s[e + 1];
  return t < 0 ? 65536 + t : t;
}
function Xt(s, e) {
  const t = CN(s, e);
  return t < 0 ? 4294967296 + t : t;
}
function GC(s, e) {
  let t = Xt(s, e);
  return t *= Math.pow(2, 32), t += Xt(s, e + 4), t;
}
function CN(s, e) {
  return s[e] << 24 | s[e + 1] << 16 | s[e + 2] << 8 | s[e + 3];
}
function rA(s, e, t) {
  s[e] = t >> 24, s[e + 1] = t >> 16 & 255, s[e + 2] = t >> 8 & 255, s[e + 3] = t & 255;
}
function cse(s) {
  const e = s.byteLength;
  for (let t = 0; t < e; ) {
    const i = Xt(s, t);
    if (i > 8 && s[t + 4] === 109 && s[t + 5] === 111 && s[t + 6] === 111 && s[t + 7] === 102)
      return !0;
    t = i > 1 ? t + i : e;
  }
  return !1;
}
function li(s, e) {
  const t = [];
  if (!e.length)
    return t;
  const i = s.byteLength;
  for (let n = 0; n < i; ) {
    const r = Xt(s, n), a = ls(s.subarray(n + 4, n + 8)), o = r > 1 ? n + r : i;
    if (a === e[0])
      if (e.length === 1)
        t.push(s.subarray(n + 8, o));
      else {
        const l = li(s.subarray(n + 8, o), e.slice(1));
        l.length && lse.apply(t, l);
      }
    n = o;
  }
  return t;
}
function hse(s) {
  const e = [], t = s[0];
  let i = 8;
  const n = Xt(s, i);
  i += 4;
  let r = 0, a = 0;
  t === 0 ? (r = Xt(s, i), a = Xt(s, i + 4), i += 8) : (r = GC(s, i), a = GC(s, i + 8), i += 16), i += 2;
  let o = s.length + a;
  const l = TN(s, i);
  i += 2;
  for (let c = 0; c < l; c++) {
    let h = i;
    const u = Xt(s, h);
    h += 4;
    const d = u & 2147483647;
    if ((u & 2147483648) >>> 31 === 1)
      return ze.warn("SIDX has hierarchical references (not supported)"), null;
    const p = Xt(s, h);
    h += 4, e.push({
      referenceSize: d,
      subsegmentDuration: p,
      // unscaled
      info: {
        duration: p / n,
        start: o,
        end: o + d - 1
      }
    }), o += d, h += 4, i = h;
  }
  return {
    earliestPresentationTime: r,
    timescale: n,
    version: t,
    referencesCount: l,
    references: e
  };
}
function _N(s) {
  const e = [], t = li(s, ["moov", "trak"]);
  for (let n = 0; n < t.length; n++) {
    const r = t[n], a = li(r, ["tkhd"])[0];
    if (a) {
      let o = a[0];
      const l = Xt(a, o === 0 ? 12 : 20), c = li(r, ["mdia", "mdhd"])[0];
      if (c) {
        o = c[0];
        const h = Xt(c, o === 0 ? 12 : 20), u = li(r, ["mdia", "hdlr"])[0];
        if (u) {
          const d = ls(u.subarray(8, 12)), f = {
            soun: an.AUDIO,
            vide: an.VIDEO
          }[d];
          if (f) {
            const p = li(r, ["mdia", "minf", "stbl", "stsd"])[0], v = use(p);
            e[l] = {
              timescale: h,
              type: f
            }, e[f] = Ls({
              timescale: h,
              id: l
            }, v);
          }
        }
      }
    }
  }
  return li(s, ["moov", "mvex", "trex"]).forEach((n) => {
    const r = Xt(n, 4), a = e[r];
    a && (a.default = {
      duration: Xt(n, 12),
      flags: Xt(n, 20)
    });
  }), e;
}
function use(s) {
  const e = s.subarray(8), t = e.subarray(8 + 78), i = ls(e.subarray(4, 8));
  let n = i;
  const r = i === "enca" || i === "encv";
  if (r) {
    const o = li(e, [i])[0].subarray(i === "enca" ? 28 : 78);
    li(o, ["sinf"]).forEach((c) => {
      const h = li(c, ["schm"])[0];
      if (h) {
        const u = ls(h.subarray(4, 8));
        if (u === "cbcs" || u === "cenc") {
          const d = li(c, ["frma"])[0];
          d && (n = ls(d));
        }
      }
    });
  }
  switch (n) {
    case "avc1":
    case "avc2":
    case "avc3":
    case "avc4": {
      const a = li(t, ["avcC"])[0];
      n += "." + n3(a[1]) + n3(a[2]) + n3(a[3]);
      break;
    }
    case "mp4a": {
      const a = li(e, [i])[0], o = li(a.subarray(28), ["esds"])[0];
      if (o && o.length > 12) {
        let l = 4;
        if (o[l++] !== 3)
          break;
        l = aA(o, l), l += 2;
        const c = o[l++];
        if (c & 128 && (l += 2), c & 64 && (l += o[l++]), o[l++] !== 4)
          break;
        l = aA(o, l);
        const h = o[l++];
        if (h === 64)
          n += "." + n3(h);
        else
          break;
        if (l += 12, o[l++] !== 5)
          break;
        l = aA(o, l);
        const u = o[l++];
        let d = (u & 248) >> 3;
        d === 31 && (d += 1 + ((u & 7) << 3) + ((o[l] & 224) >> 5)), n += "." + d;
      }
      break;
    }
    case "hvc1":
    case "hev1": {
      const a = li(t, ["hvcC"])[0], o = a[1], l = ["", "A", "B", "C"][o >> 6], c = o & 31, h = Xt(a, 2), u = (o & 32) >> 5 ? "H" : "L", d = a[12], f = a.subarray(6, 12);
      n += "." + l + c, n += "." + h.toString(16).toUpperCase(), n += "." + u + d;
      let p = "";
      for (let v = f.length; v--; ) {
        const g = f[v];
        (g || p) && (p = "." + g.toString(16).toUpperCase() + p);
      }
      n += p;
      break;
    }
    case "dvh1":
    case "dvhe": {
      const a = li(t, ["dvcC"])[0], o = a[2] >> 1 & 127, l = a[2] << 5 & 32 | a[3] >> 3 & 31;
      n += "." + To(o) + "." + To(l);
      break;
    }
    case "vp09": {
      const a = li(t, ["vpcC"])[0], o = a[4], l = a[5], c = a[6] >> 4 & 15;
      n += "." + To(o) + "." + To(l) + "." + To(c);
      break;
    }
    case "av01": {
      const a = li(t, ["av1C"])[0], o = a[1] >>> 5, l = a[1] & 31, c = a[2] >>> 7 ? "H" : "M", h = (a[2] & 64) >> 6, u = (a[2] & 32) >> 5, d = o === 2 && h ? u ? 12 : 10 : h ? 10 : 8, f = (a[2] & 16) >> 4, p = (a[2] & 8) >> 3, v = (a[2] & 4) >> 2, g = a[2] & 3, m = 1, x = 1, y = 1, A = 0;
      n += "." + o + "." + To(l) + c + "." + To(d) + "." + f + "." + p + v + g + "." + To(m) + "." + To(x) + "." + To(y) + "." + A;
      break;
    }
  }
  return {
    codec: n,
    encrypted: r
  };
}
function aA(s, e) {
  const t = e + 5;
  for (; s[e++] & 128 && e < t; )
    ;
  return e;
}
function n3(s) {
  return ("0" + s.toString(16).toUpperCase()).slice(-2);
}
function To(s) {
  return (s < 10 ? "0" : "") + s;
}
function dse(s, e) {
  if (!s || !e)
    return s;
  const t = e.keyId;
  return t && e.isCommonEncryption && li(s, ["moov", "trak"]).forEach((n) => {
    const a = li(n, ["mdia", "minf", "stbl", "stsd"])[0].subarray(8);
    let o = li(a, ["enca"]);
    const l = o.length > 0;
    l || (o = li(a, ["encv"])), o.forEach((c) => {
      const h = l ? c.subarray(28) : c.subarray(78);
      li(h, ["sinf"]).forEach((d) => {
        const f = PN(d);
        if (f) {
          const p = f.subarray(8, 24);
          p.some((v) => v !== 0) || (ze.log(`[eme] Patching keyId in 'enc${l ? "a" : "v"}>sinf>>tenc' box: ${Io.hexDump(p)} -> ${Io.hexDump(t)}`), f.set(t, 8));
        }
      });
    });
  }), s;
}
function PN(s) {
  const e = li(s, ["schm"])[0];
  if (e) {
    const t = ls(e.subarray(4, 8));
    if (t === "cbcs" || t === "cenc")
      return li(s, ["schi", "tenc"])[0];
  }
  return ze.error("[eme] missing 'schm' box"), null;
}
function fse(s, e) {
  return li(e, ["moof", "traf"]).reduce((t, i) => {
    const n = li(i, ["tfdt"])[0], r = n[0], a = li(i, ["tfhd"]).reduce((o, l) => {
      const c = Xt(l, 4), h = s[c];
      if (h) {
        let u = Xt(n, 4);
        if (r === 1) {
          if (u === fx)
            return ze.warn("[mp4-demuxer]: Ignoring assumed invalid signed 64-bit track fragment decode time"), o;
          u *= fx + 1, u += Xt(n, 8);
        }
        const d = h.timescale || 9e4, f = u / d;
        if (Bt(f) && (o === null || f < o))
          return f;
      }
      return o;
    }, null);
    return a !== null && Bt(a) && (t === null || a < t) ? a : t;
  }, null);
}
function pse(s, e) {
  let t = 0, i = 0, n = 0;
  const r = li(s, ["moof", "traf"]);
  for (let a = 0; a < r.length; a++) {
    const o = r[a], l = li(o, ["tfhd"])[0], c = Xt(l, 4), h = e[c];
    if (!h)
      continue;
    const u = h.default, d = Xt(l, 0) | (u == null ? void 0 : u.flags);
    let f = u == null ? void 0 : u.duration;
    d & 8 && (d & 2 ? f = Xt(l, 12) : f = Xt(l, 8));
    const p = h.timescale || 9e4, v = li(o, ["trun"]);
    for (let g = 0; g < v.length; g++) {
      if (t = mse(v[g]), !t && f) {
        const m = Xt(v[g], 4);
        t = f * m;
      }
      h.type === an.VIDEO ? i += t / p : h.type === an.AUDIO && (n += t / p);
    }
  }
  if (i === 0 && n === 0) {
    let a = 1 / 0, o = 0, l = 0;
    const c = li(s, ["sidx"]);
    for (let h = 0; h < c.length; h++) {
      const u = hse(c[h]);
      if (u != null && u.references) {
        a = Math.min(a, u.earliestPresentationTime / u.timescale);
        const d = u.references.reduce((f, p) => f + p.info.duration || 0, 0);
        o = Math.max(o, d + u.earliestPresentationTime / u.timescale), l = o - a;
      }
    }
    if (l && Bt(l))
      return l;
  }
  return i || n;
}
function mse(s) {
  const e = Xt(s, 0);
  let t = 8;
  e & 1 && (t += 4), e & 4 && (t += 4);
  let i = 0;
  const n = Xt(s, 4);
  for (let r = 0; r < n; r++) {
    if (e & 256) {
      const a = Xt(s, t);
      i += a, t += 4;
    }
    e & 512 && (t += 4), e & 1024 && (t += 4), e & 2048 && (t += 4);
  }
  return i;
}
function gse(s, e, t) {
  li(e, ["moof", "traf"]).forEach((i) => {
    li(i, ["tfhd"]).forEach((n) => {
      const r = Xt(n, 4), a = s[r];
      if (!a)
        return;
      const o = a.timescale || 9e4;
      li(i, ["tfdt"]).forEach((l) => {
        const c = l[0], h = t * o;
        if (h) {
          let u = Xt(l, 4);
          if (c === 0)
            u -= h, u = Math.max(u, 0), rA(l, 4, u);
          else {
            u *= Math.pow(2, 32), u += Xt(l, 8), u -= h, u = Math.max(u, 0);
            const d = Math.floor(u / (fx + 1)), f = Math.floor(u % (fx + 1));
            rA(l, 4, d), rA(l, 8, f);
          }
        }
      });
    });
  });
}
function vse(s) {
  const e = {
    valid: null,
    remainder: null
  }, t = li(s, ["moof"]);
  if (t.length < 2)
    return e.remainder = s, e;
  const i = t[t.length - 1];
  return e.valid = Eu(s, 0, i.byteOffset - 8), e.remainder = Eu(s, i.byteOffset - 8), e;
}
function Pa(s, e) {
  const t = new Uint8Array(s.length + e.length);
  return t.set(s), t.set(e, s.length), t;
}
function HC(s, e) {
  const t = [], i = e.samples, n = e.timescale, r = e.id;
  let a = !1;
  return li(i, ["moof"]).map((l) => {
    const c = l.byteOffset - 8;
    li(l, ["traf"]).map((u) => {
      const d = li(u, ["tfdt"]).map((f) => {
        const p = f[0];
        let v = Xt(f, 4);
        return p === 1 && (v *= Math.pow(2, 32), v += Xt(f, 8)), v / n;
      })[0];
      return d !== void 0 && (s = d), li(u, ["tfhd"]).map((f) => {
        const p = Xt(f, 4), v = Xt(f, 0) & 16777215, g = (v & 1) !== 0, m = (v & 2) !== 0, x = (v & 8) !== 0;
        let y = 0;
        const A = (v & 16) !== 0;
        let b = 0;
        const M = (v & 32) !== 0;
        let w = 8;
        p === r && (g && (w += 8), m && (w += 4), x && (y = Xt(f, w), w += 4), A && (b = Xt(f, w), w += 4), M && (w += 4), e.type === "video" && (a = yse(e.codec)), li(u, ["trun"]).map((C) => {
          const T = C[0], E = Xt(C, 0) & 16777215, P = (E & 1) !== 0;
          let L = 0;
          const I = (E & 4) !== 0, N = (E & 256) !== 0;
          let H = 0;
          const U = (E & 512) !== 0;
          let W = 0;
          const X = (E & 1024) !== 0, K = (E & 2048) !== 0;
          let te = 0;
          const re = Xt(C, 4);
          let be = 8;
          P && (L = Xt(C, be), be += 4), I && (be += 4);
          let Pe = L + c;
          for (let J = 0; J < re; J++) {
            if (N ? (H = Xt(C, be), be += 4) : H = y, U ? (W = Xt(C, be), be += 4) : W = b, X && (be += 4), K && (T === 0 ? te = Xt(C, be) : te = CN(C, be), be += 4), e.type === an.VIDEO) {
              let fe = 0;
              for (; fe < W; ) {
                const Me = Xt(i, Pe);
                if (Pe += 4, xse(a, i[Pe])) {
                  const ye = i.subarray(Pe, Pe + Me);
                  RN(ye, a ? 2 : 1, s + te / n, t);
                }
                Pe += Me, fe += Me + 4;
              }
            }
            s += H / n;
          }
        }));
      });
    });
  }), t;
}
function yse(s) {
  if (!s)
    return !1;
  const e = s.indexOf("."), t = e < 0 ? s : s.substring(0, e);
  return t === "hvc1" || t === "hev1" || // Dolby Vision
  t === "dvh1" || t === "dvhe";
}
function xse(s, e) {
  if (s) {
    const t = e >> 1 & 63;
    return t === 39 || t === 40;
  } else
    return (e & 31) === 6;
}
function RN(s, e, t, i) {
  const n = DN(s);
  let r = 0;
  r += e;
  let a = 0, o = 0, l = 0;
  for (; r < n.length; ) {
    a = 0;
    do {
      if (r >= n.length)
        break;
      l = n[r++], a += l;
    } while (l === 255);
    o = 0;
    do {
      if (r >= n.length)
        break;
      l = n[r++], o += l;
    } while (l === 255);
    const c = n.length - r;
    let h = r;
    if (o < c)
      r += o;
    else if (o > c) {
      ze.error(`Malformed SEI payload. ${o} is too small, only ${c} bytes left to parse.`);
      break;
    }
    if (a === 4) {
      if (n[h++] === 181) {
        const d = TN(n, h);
        if (h += 2, d === 49) {
          const f = Xt(n, h);
          if (h += 4, f === 1195456820) {
            const p = n[h++];
            if (p === 3) {
              const v = n[h++], g = 31 & v, m = 64 & v, x = m ? 2 + g * 3 : 0, y = new Uint8Array(x);
              if (m) {
                y[0] = v;
                for (let A = 1; A < x; A++)
                  y[A] = n[h++];
              }
              i.push({
                type: p,
                payloadType: a,
                pts: t,
                bytes: y
              });
            }
          }
        }
      }
    } else if (a === 5 && o > 16) {
      const u = [];
      for (let p = 0; p < 16; p++) {
        const v = n[h++].toString(16);
        u.push(v.length == 1 ? "0" + v : v), (p === 3 || p === 5 || p === 7 || p === 9) && u.push("-");
      }
      const d = o - 16, f = new Uint8Array(d);
      for (let p = 0; p < d; p++)
        f[p] = n[h++];
      i.push({
        payloadType: a,
        pts: t,
        uuid: u.join(""),
        userData: t0(f),
        userDataBytes: f
      });
    }
  }
}
function DN(s) {
  const e = s.byteLength, t = [];
  let i = 1;
  for (; i < e - 2; )
    s[i] === 0 && s[i + 1] === 0 && s[i + 2] === 3 ? (t.push(i + 2), i += 2) : i++;
  if (t.length === 0)
    return s;
  const n = e - t.length, r = new Uint8Array(n);
  let a = 0;
  for (i = 0; i < n; a++, i++)
    a === t[0] && (a++, t.shift()), r[i] = s[a];
  return r;
}
function Ase(s) {
  const e = s[0];
  let t = "", i = "", n = 0, r = 0, a = 0, o = 0, l = 0, c = 0;
  if (e === 0) {
    for (; ls(s.subarray(c, c + 1)) !== "\0"; )
      t += ls(s.subarray(c, c + 1)), c += 1;
    for (t += ls(s.subarray(c, c + 1)), c += 1; ls(s.subarray(c, c + 1)) !== "\0"; )
      i += ls(s.subarray(c, c + 1)), c += 1;
    i += ls(s.subarray(c, c + 1)), c += 1, n = Xt(s, 12), r = Xt(s, 16), o = Xt(s, 20), l = Xt(s, 24), c = 28;
  } else if (e === 1) {
    c += 4, n = Xt(s, c), c += 4;
    const u = Xt(s, c);
    c += 4;
    const d = Xt(s, c);
    for (c += 4, a = 2 ** 32 * u + d, Fne(a) || (a = Number.MAX_SAFE_INTEGER, ze.warn("Presentation time exceeds safe integer limit and wrapped to max safe integer in parsing emsg box")), o = Xt(s, c), c += 4, l = Xt(s, c), c += 4; ls(s.subarray(c, c + 1)) !== "\0"; )
      t += ls(s.subarray(c, c + 1)), c += 1;
    for (t += ls(s.subarray(c, c + 1)), c += 1; ls(s.subarray(c, c + 1)) !== "\0"; )
      i += ls(s.subarray(c, c + 1)), c += 1;
    i += ls(s.subarray(c, c + 1)), c += 1;
  }
  const h = s.subarray(c, s.byteLength);
  return {
    schemeIdUri: t,
    value: i,
    timeScale: n,
    presentationTime: a,
    presentationTimeDelta: r,
    eventDuration: o,
    id: l,
    payload: h
  };
}
function bse(s, ...e) {
  const t = e.length;
  let i = 8, n = t;
  for (; n--; )
    i += e[n].byteLength;
  const r = new Uint8Array(i);
  for (r[0] = i >> 24 & 255, r[1] = i >> 16 & 255, r[2] = i >> 8 & 255, r[3] = i & 255, r.set(s, 4), n = 0, i = 8; n < t; n++)
    r.set(e[n], i), i += e[n].byteLength;
  return r;
}
function Mse(s, e, t) {
  if (s.byteLength !== 16)
    throw new RangeError("Invalid system id");
  let i, n;
  i = 0, n = new Uint8Array();
  let r;
  i > 0 ? (r = new Uint8Array(4), e.length > 0 && new DataView(r.buffer).setUint32(0, e.length, !1)) : r = new Uint8Array();
  const a = new Uint8Array(4);
  return t && t.byteLength > 0 && new DataView(a.buffer).setUint32(0, t.byteLength, !1), bse(
    [112, 115, 115, 104],
    new Uint8Array([
      i,
      0,
      0,
      0
      // Flags
    ]),
    s,
    // 16 bytes
    r,
    n,
    a,
    t || new Uint8Array()
  );
}
function Sse(s) {
  if (!(s instanceof ArrayBuffer) || s.byteLength < 32)
    return null;
  const e = {
    version: 0,
    systemId: "",
    kids: null,
    data: null
  }, t = new DataView(s), i = t.getUint32(0);
  if (s.byteLength !== i && i > 44 || t.getUint32(4) !== 1886614376 || (e.version = t.getUint32(8) >>> 24, e.version > 1))
    return null;
  e.systemId = Io.hexDump(new Uint8Array(s, 12, 16));
  const r = t.getUint32(28);
  if (e.version === 0) {
    if (i - 32 < r)
      return null;
    e.data = new Uint8Array(s, 32, r);
  } else if (e.version === 1) {
    e.kids = [];
    for (let a = 0; a < r; a++)
      e.kids.push(new Uint8Array(s, 32 + a * 16, 16));
  }
  return e;
}
let s3 = {};
class Lg {
  static clearKeyUriToKeyIdMap() {
    s3 = {};
  }
  constructor(e, t, i, n = [1], r = null) {
    this.uri = void 0, this.method = void 0, this.keyFormat = void 0, this.keyFormatVersions = void 0, this.encrypted = void 0, this.isCommonEncryption = void 0, this.iv = null, this.key = null, this.keyId = null, this.pssh = null, this.method = e, this.uri = t, this.keyFormat = i, this.keyFormatVersions = n, this.iv = r, this.encrypted = e ? e !== "NONE" : !1, this.isCommonEncryption = this.encrypted && e !== "AES-128";
  }
  isSupported() {
    if (this.method) {
      if (this.method === "AES-128" || this.method === "NONE")
        return !0;
      if (this.keyFormat === "identity")
        return this.method === "SAMPLE-AES";
      switch (this.keyFormat) {
        case Ir.FAIRPLAY:
        case Ir.WIDEVINE:
        case Ir.PLAYREADY:
        case Ir.CLEARKEY:
          return ["ISO-23001-7", "SAMPLE-AES", "SAMPLE-AES-CENC", "SAMPLE-AES-CTR"].indexOf(this.method) !== -1;
      }
    }
    return !1;
  }
  getDecryptData(e) {
    if (!this.encrypted || !this.uri)
      return null;
    if (this.method === "AES-128" && this.uri && !this.iv) {
      typeof e != "number" && (this.method === "AES-128" && !this.iv && ze.warn(`missing IV for initialization segment with method="${this.method}" - compliance issue`), e = 0);
      const i = wse(e);
      return new Lg(this.method, this.uri, "identity", this.keyFormatVersions, i);
    }
    const t = Kne(this.uri);
    if (t)
      switch (this.keyFormat) {
        case Ir.WIDEVINE:
          this.pssh = t, t.length >= 22 && (this.keyId = t.subarray(t.length - 22, t.length - 6));
          break;
        case Ir.PLAYREADY: {
          const i = new Uint8Array([154, 4, 240, 121, 152, 64, 66, 134, 171, 146, 230, 91, 224, 136, 95, 149]);
          this.pssh = Mse(i, null, t);
          const n = new Uint16Array(t.buffer, t.byteOffset, t.byteLength / 2), r = String.fromCharCode.apply(null, Array.from(n)), a = r.substring(r.indexOf("<"), r.length), c = new DOMParser().parseFromString(a, "text/xml").getElementsByTagName("KID")[0];
          if (c) {
            const h = c.childNodes[0] ? c.childNodes[0].nodeValue : c.getAttribute("VALUE");
            if (h) {
              const u = qS(h).subarray(0, 16);
              Zne(u), this.keyId = u;
            }
          }
          break;
        }
        default: {
          let i = t.subarray(0, 16);
          if (i.length !== 16) {
            const n = new Uint8Array(16);
            n.set(i, 16 - i.length), i = n;
          }
          this.keyId = i;
          break;
        }
      }
    if (!this.keyId || this.keyId.byteLength !== 16) {
      let i = s3[this.uri];
      if (!i) {
        const n = Object.keys(s3).length % Number.MAX_SAFE_INTEGER;
        i = new Uint8Array(16), new DataView(i.buffer, 12, 4).setUint32(0, n), s3[this.uri] = i;
      }
      this.keyId = i;
    }
    return this;
  }
}
function wse(s) {
  const e = new Uint8Array(16);
  for (let t = 12; t < 16; t++)
    e[t] = s >> 8 * (15 - t) & 255;
  return e;
}
const LN = /\{\$([a-zA-Z0-9-_]+)\}/g;
function VC(s) {
  return LN.test(s);
}
function Tr(s, e, t) {
  if (s.variableList !== null || s.hasVariableRefs)
    for (let i = t.length; i--; ) {
      const n = t[i], r = e[n];
      r && (e[n] = Tb(s, r));
    }
}
function Tb(s, e) {
  if (s.variableList !== null || s.hasVariableRefs) {
    const t = s.variableList;
    return e.replace(LN, (i) => {
      const n = i.substring(2, i.length - 1), r = t == null ? void 0 : t[n];
      return r === void 0 ? (s.playlistParsingError || (s.playlistParsingError = new Error(`Missing preceding EXT-X-DEFINE tag for Variable Reference: "${n}"`)), i) : r;
    });
  }
  return e;
}
function WC(s, e, t) {
  let i = s.variableList;
  i || (s.variableList = i = {});
  let n, r;
  if ("QUERYPARAM" in e) {
    n = e.QUERYPARAM;
    try {
      const a = new self.URL(t).searchParams;
      if (a.has(n))
        r = a.get(n);
      else
        throw new Error(`"${n}" does not match any query parameter in URI: "${t}"`);
    } catch (a) {
      s.playlistParsingError || (s.playlistParsingError = new Error(`EXT-X-DEFINE QUERYPARAM: ${a.message}`));
    }
  } else
    n = e.NAME, r = e.VALUE;
  n in i ? s.playlistParsingError || (s.playlistParsingError = new Error(`EXT-X-DEFINE duplicate Variable Name declarations: "${n}"`)) : i[n] = r || "";
}
function Ese(s, e, t) {
  const i = e.IMPORT;
  if (t && i in t) {
    let n = s.variableList;
    n || (s.variableList = n = {}), n[i] = t[i];
  } else
    s.playlistParsingError || (s.playlistParsingError = new Error(`EXT-X-DEFINE IMPORT attribute not found in Multivariant Playlist: "${i}"`));
}
function Nu(s = !0) {
  return typeof self > "u" ? void 0 : (s || !self.MediaSource) && self.ManagedMediaSource || self.MediaSource || self.WebKitMediaSource;
}
function Tse(s) {
  return typeof self < "u" && s === self.ManagedMediaSource;
}
const px = {
  audio: {
    a3ds: 1,
    "ac-3": 0.95,
    "ac-4": 1,
    alac: 0.9,
    alaw: 1,
    dra1: 1,
    "dts+": 1,
    "dts-": 1,
    dtsc: 1,
    dtse: 1,
    dtsh: 1,
    "ec-3": 0.9,
    enca: 1,
    fLaC: 0.9,
    // MP4-RA listed codec entry for FLAC
    flac: 0.9,
    // legacy browser codec name for FLAC
    FLAC: 0.9,
    // some manifests may list "FLAC" with Apple's tools
    g719: 1,
    g726: 1,
    m4ae: 1,
    mha1: 1,
    mha2: 1,
    mhm1: 1,
    mhm2: 1,
    mlpa: 1,
    mp4a: 1,
    "raw ": 1,
    Opus: 1,
    opus: 1,
    // browsers expect this to be lowercase despite MP4RA says 'Opus'
    samr: 1,
    sawb: 1,
    sawp: 1,
    sevc: 1,
    sqcp: 1,
    ssmv: 1,
    twos: 1,
    ulaw: 1
  },
  video: {
    avc1: 1,
    avc2: 1,
    avc3: 1,
    avc4: 1,
    avcp: 1,
    av01: 0.8,
    drac: 1,
    dva1: 1,
    dvav: 1,
    dvh1: 0.7,
    dvhe: 0.7,
    encv: 1,
    hev1: 0.75,
    hvc1: 0.75,
    mjp2: 1,
    mp4v: 1,
    mvc1: 1,
    mvc2: 1,
    mvc3: 1,
    mvc4: 1,
    resv: 1,
    rv60: 1,
    s263: 1,
    svc1: 1,
    svc2: 1,
    "vc-1": 1,
    vp08: 1,
    vp09: 0.9
  },
  text: {
    stpp: 1,
    wvtt: 1
  }
};
function Cse(s, e) {
  const t = px[e];
  return !!t && !!t[s.slice(0, 4)];
}
function oA(s, e, t = !0) {
  return !s.split(",").some((i) => !IN(i, e, t));
}
function IN(s, e, t = !0) {
  var i;
  const n = Nu(t);
  return (i = n == null ? void 0 : n.isTypeSupported(Ig(s, e))) != null ? i : !1;
}
function Ig(s, e) {
  return `${e}/mp4;codecs="${s}"`;
}
function XC(s) {
  if (s) {
    const e = s.substring(0, 4);
    return px.video[e];
  }
  return 2;
}
function mx(s) {
  return s.split(",").reduce((e, t) => {
    const i = px.video[t];
    return i ? (i * 2 + e) / (e ? 3 : 2) : (px.audio[t] + e) / (e ? 2 : 1);
  }, 0);
}
const lA = {};
function _se(s, e = !0) {
  if (lA[s])
    return lA[s];
  const t = {
    flac: ["flac", "fLaC", "FLAC"],
    opus: ["opus", "Opus"]
  }[s];
  for (let i = 0; i < t.length; i++)
    if (IN(t[i], "audio", e))
      return lA[s] = t[i], t[i];
  return s;
}
const Pse = /flac|opus/i;
function gx(s, e = !0) {
  return s.replace(Pse, (t) => _se(t.toLowerCase(), e));
}
function jC(s, e) {
  return s && s !== "mp4a" ? s : e && e.split(",")[0];
}
function Rse(s) {
  const e = s.split(".");
  if (e.length > 2) {
    let t = e.shift() + ".";
    return t += parseInt(e.shift()).toString(16), t += ("000" + parseInt(e.shift()).toString(16)).slice(-4), t;
  }
  return s;
}
const qC = /#EXT-X-STREAM-INF:([^\r\n]*)(?:[\r\n](?:#[^\r\n]*)?)*([^\r\n]+)|#EXT-X-(SESSION-DATA|SESSION-KEY|DEFINE|CONTENT-STEERING|START):([^\r\n]*)[\r\n]+/g, YC = /#EXT-X-MEDIA:(.*)/g, Dse = /^#EXT(?:INF|-X-TARGETDURATION):/m, ZC = new RegExp([
  /#EXTINF:\s*(\d*(?:\.\d+)?)(?:,(.*)\s+)?/.source,
  // duration (#EXTINF:<duration>,<title>), group 1 => duration, group 2 => title
  /(?!#) *(\S[\S ]*)/.source,
  // segment URI, group 3 => the URI (note newline is not eaten)
  /#EXT-X-BYTERANGE:*(.+)/.source,
  // next segment's byterange, group 4 => range spec (x@y)
  /#EXT-X-PROGRAM-DATE-TIME:(.+)/.source,
  // next segment's program date/time group 5 => the datetime spec
  /#.*/.source
  // All other non-segment oriented tags will match with all groups empty
].join("|"), "g"), Lse = new RegExp([/#(EXTM3U)/.source, /#EXT-X-(DATERANGE|DEFINE|KEY|MAP|PART|PART-INF|PLAYLIST-TYPE|PRELOAD-HINT|RENDITION-REPORT|SERVER-CONTROL|SKIP|START):(.+)/.source, /#EXT-X-(BITRATE|DISCONTINUITY-SEQUENCE|MEDIA-SEQUENCE|TARGETDURATION|VERSION): *(\d+)/.source, /#EXT-X-(DISCONTINUITY|ENDLIST|GAP|INDEPENDENT-SEGMENTS)/.source, /(#)([^:]*):(.*)/.source, /(#)(.*)(?:.*)\r?\n?/.source].join("|"));
class qo {
  static findGroup(e, t) {
    for (let i = 0; i < e.length; i++) {
      const n = e[i];
      if (n.id === t)
        return n;
    }
  }
  static resolve(e, t) {
    return jS.buildAbsoluteURL(t, e, {
      alwaysNormalize: !0
    });
  }
  static isMediaPlaylist(e) {
    return Dse.test(e);
  }
  static parseMasterPlaylist(e, t) {
    const i = VC(e), n = {
      contentSteering: null,
      levels: [],
      playlistParsingError: null,
      sessionData: null,
      sessionKeys: null,
      startTimeOffset: null,
      variableList: null,
      hasVariableRefs: i
    }, r = [];
    qC.lastIndex = 0;
    let a;
    for (; (a = qC.exec(e)) != null; )
      if (a[1]) {
        var o;
        const c = new Bn(a[1]);
        Tr(n, c, ["CODECS", "SUPPLEMENTAL-CODECS", "ALLOWED-CPC", "PATHWAY-ID", "STABLE-VARIANT-ID", "AUDIO", "VIDEO", "SUBTITLES", "CLOSED-CAPTIONS", "NAME"]);
        const h = Tb(n, a[2]), u = {
          attrs: c,
          bitrate: c.decimalInteger("BANDWIDTH") || c.decimalInteger("AVERAGE-BANDWIDTH"),
          name: c.NAME,
          url: qo.resolve(h, t)
        }, d = c.decimalResolution("RESOLUTION");
        d && (u.width = d.width, u.height = d.height), Ise(c.CODECS, u), (o = u.unknownCodecs) != null && o.length || r.push(u), n.levels.push(u);
      } else if (a[3]) {
        const c = a[3], h = a[4];
        switch (c) {
          case "SESSION-DATA": {
            const u = new Bn(h);
            Tr(n, u, ["DATA-ID", "LANGUAGE", "VALUE", "URI"]);
            const d = u["DATA-ID"];
            d && (n.sessionData === null && (n.sessionData = {}), n.sessionData[d] = u);
            break;
          }
          case "SESSION-KEY": {
            const u = KC(h, t, n);
            u.encrypted && u.isSupported() ? (n.sessionKeys === null && (n.sessionKeys = []), n.sessionKeys.push(u)) : ze.warn(`[Keys] Ignoring invalid EXT-X-SESSION-KEY tag: "${h}"`);
            break;
          }
          case "DEFINE": {
            {
              const u = new Bn(h);
              Tr(n, u, ["NAME", "VALUE", "QUERYPARAM"]), WC(n, u, t);
            }
            break;
          }
          case "CONTENT-STEERING": {
            const u = new Bn(h);
            Tr(n, u, ["SERVER-URI", "PATHWAY-ID"]), n.contentSteering = {
              uri: qo.resolve(u["SERVER-URI"], t),
              pathwayId: u["PATHWAY-ID"] || "."
            };
            break;
          }
          case "START": {
            n.startTimeOffset = JC(h);
            break;
          }
        }
      }
    const l = r.length > 0 && r.length < n.levels.length;
    return n.levels = l ? r : n.levels, n.levels.length === 0 && (n.playlistParsingError = new Error("no levels found in manifest")), n;
  }
  static parseMasterPlaylistMedia(e, t, i) {
    let n;
    const r = {}, a = i.levels, o = {
      AUDIO: a.map((c) => ({
        id: c.attrs.AUDIO,
        audioCodec: c.audioCodec
      })),
      SUBTITLES: a.map((c) => ({
        id: c.attrs.SUBTITLES,
        textCodec: c.textCodec
      })),
      "CLOSED-CAPTIONS": []
    };
    let l = 0;
    for (YC.lastIndex = 0; (n = YC.exec(e)) !== null; ) {
      const c = new Bn(n[1]), h = c.TYPE;
      if (h) {
        const u = o[h], d = r[h] || [];
        r[h] = d, Tr(i, c, ["URI", "GROUP-ID", "LANGUAGE", "ASSOC-LANGUAGE", "STABLE-RENDITION-ID", "NAME", "INSTREAM-ID", "CHARACTERISTICS", "CHANNELS"]);
        const f = c.LANGUAGE, p = c["ASSOC-LANGUAGE"], v = c.CHANNELS, g = c.CHARACTERISTICS, m = c["INSTREAM-ID"], x = {
          attrs: c,
          bitrate: 0,
          id: l++,
          groupId: c["GROUP-ID"] || "",
          name: c.NAME || f || "",
          type: h,
          default: c.bool("DEFAULT"),
          autoselect: c.bool("AUTOSELECT"),
          forced: c.bool("FORCED"),
          lang: f,
          url: c.URI ? qo.resolve(c.URI, t) : ""
        };
        if (p && (x.assocLang = p), v && (x.channels = v), g && (x.characteristics = g), m && (x.instreamId = m), u != null && u.length) {
          const y = qo.findGroup(u, x.groupId) || u[0];
          QC(x, y, "audioCodec"), QC(x, y, "textCodec");
        }
        d.push(x);
      }
    }
    return r;
  }
  static parseLevelPlaylist(e, t, i, n, r, a) {
    const o = new qne(t), l = o.fragments;
    let c = null, h = 0, u = 0, d = 0, f = 0, p = null, v = new iA(n, t), g, m, x, y = -1, A = !1, b = null;
    for (ZC.lastIndex = 0, o.m3u8 = e, o.hasVariableRefs = VC(e); (g = ZC.exec(e)) !== null; ) {
      A && (A = !1, v = new iA(n, t), v.start = d, v.sn = h, v.cc = f, v.level = i, c && (v.initSegment = c, v.rawProgramDateTime = c.rawProgramDateTime, c.rawProgramDateTime = null, b && (v.setByteRange(b), b = null)));
      const T = g[1];
      if (T) {
        v.duration = parseFloat(T);
        const E = (" " + g[2]).slice(1);
        v.title = E || null, v.tagList.push(E ? ["INF", T, E] : ["INF", T]);
      } else if (g[3]) {
        if (Bt(v.duration)) {
          v.start = d, x && t_(v, x, o), v.sn = h, v.level = i, v.cc = f, l.push(v);
          const E = (" " + g[3]).slice(1);
          v.relurl = Tb(o, E), $C(v, p), p = v, d += v.duration, h++, u = 0, A = !0;
        }
      } else if (g[4]) {
        const E = (" " + g[4]).slice(1);
        p ? v.setByteRange(E, p) : v.setByteRange(E);
      } else if (g[5])
        v.rawProgramDateTime = (" " + g[5]).slice(1), v.tagList.push(["PROGRAM-DATE-TIME", v.rawProgramDateTime]), y === -1 && (y = l.length);
      else {
        if (g = g[0].match(Lse), !g) {
          ze.warn("No matches on slow regex match for level playlist!");
          continue;
        }
        for (m = 1; m < g.length && !(typeof g[m] < "u"); m++)
          ;
        const E = (" " + g[m]).slice(1), P = (" " + g[m + 1]).slice(1), L = g[m + 2] ? (" " + g[m + 2]).slice(1) : "";
        switch (E) {
          case "PLAYLIST-TYPE":
            o.type = P.toUpperCase();
            break;
          case "MEDIA-SEQUENCE":
            h = o.startSN = parseInt(P);
            break;
          case "SKIP": {
            const I = new Bn(P);
            Tr(o, I, ["RECENTLY-REMOVED-DATERANGES"]);
            const N = I.decimalInteger("SKIPPED-SEGMENTS");
            if (Bt(N)) {
              o.skippedSegments = N;
              for (let U = N; U--; )
                l.unshift(null);
              h += N;
            }
            const H = I.enumeratedString("RECENTLY-REMOVED-DATERANGES");
            H && (o.recentlyRemovedDateranges = H.split("	"));
            break;
          }
          case "TARGETDURATION":
            o.targetduration = Math.max(parseInt(P), 1);
            break;
          case "VERSION":
            o.version = parseInt(P);
            break;
          case "INDEPENDENT-SEGMENTS":
          case "EXTM3U":
            break;
          case "ENDLIST":
            o.live = !1;
            break;
          case "#":
            (P || L) && v.tagList.push(L ? [P, L] : [P]);
            break;
          case "DISCONTINUITY":
            f++, v.tagList.push(["DIS"]);
            break;
          case "GAP":
            v.gap = !0, v.tagList.push([E]);
            break;
          case "BITRATE":
            v.tagList.push([E, P]);
            break;
          case "DATERANGE": {
            const I = new Bn(P);
            Tr(o, I, ["ID", "CLASS", "START-DATE", "END-DATE", "SCTE35-CMD", "SCTE35-OUT", "SCTE35-IN"]), Tr(o, I, I.clientAttrs);
            const N = new yN(I, o.dateRanges[I.ID]);
            N.isValid || o.skippedSegments ? o.dateRanges[N.id] = N : ze.warn(`Ignoring invalid DATERANGE tag: "${P}"`), v.tagList.push(["EXT-X-DATERANGE", P]);
            break;
          }
          case "DEFINE": {
            {
              const I = new Bn(P);
              Tr(o, I, ["NAME", "VALUE", "IMPORT", "QUERYPARAM"]), "IMPORT" in I ? Ese(o, I, a) : WC(o, I, t);
            }
            break;
          }
          case "DISCONTINUITY-SEQUENCE":
            f = parseInt(P);
            break;
          case "KEY": {
            const I = KC(P, t, o);
            if (I.isSupported()) {
              if (I.method === "NONE") {
                x = void 0;
                break;
              }
              x || (x = {}), x[I.keyFormat] && (x = as({}, x)), x[I.keyFormat] = I;
            } else
              ze.warn(`[Keys] Ignoring invalid EXT-X-KEY tag: "${P}"`);
            break;
          }
          case "START":
            o.startTimeOffset = JC(P);
            break;
          case "MAP": {
            const I = new Bn(P);
            if (Tr(o, I, ["BYTERANGE", "URI"]), v.duration) {
              const N = new iA(n, t);
              e_(N, I, i, x), c = N, v.initSegment = c, c.rawProgramDateTime && !v.rawProgramDateTime && (v.rawProgramDateTime = c.rawProgramDateTime);
            } else {
              const N = v.byteRangeEndOffset;
              if (N) {
                const H = v.byteRangeStartOffset;
                b = `${N - H}@${H}`;
              } else
                b = null;
              e_(v, I, i, x), c = v, A = !0;
            }
            break;
          }
          case "SERVER-CONTROL": {
            const I = new Bn(P);
            o.canBlockReload = I.bool("CAN-BLOCK-RELOAD"), o.canSkipUntil = I.optionalFloat("CAN-SKIP-UNTIL", 0), o.canSkipDateRanges = o.canSkipUntil > 0 && I.bool("CAN-SKIP-DATERANGES"), o.partHoldBack = I.optionalFloat("PART-HOLD-BACK", 0), o.holdBack = I.optionalFloat("HOLD-BACK", 0);
            break;
          }
          case "PART-INF": {
            const I = new Bn(P);
            o.partTarget = I.decimalFloatingPoint("PART-TARGET");
            break;
          }
          case "PART": {
            let I = o.partList;
            I || (I = o.partList = []);
            const N = u > 0 ? I[I.length - 1] : void 0, H = u++, U = new Bn(P);
            Tr(o, U, ["BYTERANGE", "URI"]);
            const W = new Xne(U, v, t, H, N);
            I.push(W), v.duration += W.duration;
            break;
          }
          case "PRELOAD-HINT": {
            const I = new Bn(P);
            Tr(o, I, ["URI"]), o.preloadHint = I;
            break;
          }
          case "RENDITION-REPORT": {
            const I = new Bn(P);
            Tr(o, I, ["URI"]), o.renditionReports = o.renditionReports || [], o.renditionReports.push(I);
            break;
          }
          default:
            ze.warn(`line parsed but not handled: ${g}`);
            break;
        }
      }
    }
    p && !p.relurl ? (l.pop(), d -= p.duration, o.partList && (o.fragmentHint = p)) : o.partList && ($C(v, p), v.cc = f, o.fragmentHint = v, x && t_(v, x, o));
    const M = l.length, w = l[0], C = l[M - 1];
    if (d += o.skippedSegments * o.targetduration, d > 0 && M && C) {
      o.averagetargetduration = d / M;
      const T = C.sn;
      o.endSN = T !== "initSegment" ? T : 0, o.live || (C.endList = !0), w && (o.startCC = w.cc);
    } else
      o.endSN = 0, o.startCC = 0;
    return o.fragmentHint && (d += o.fragmentHint.duration), o.totalduration = d, o.endCC = f, y > 0 && Ose(l, y), o;
  }
}
function KC(s, e, t) {
  var i, n;
  const r = new Bn(s);
  Tr(t, r, ["KEYFORMAT", "KEYFORMATVERSIONS", "URI", "IV", "URI"]);
  const a = (i = r.METHOD) != null ? i : "", o = r.URI, l = r.hexadecimalInteger("IV"), c = r.KEYFORMATVERSIONS, h = (n = r.KEYFORMAT) != null ? n : "identity";
  o && r.IV && !l && ze.error(`Invalid IV: ${r.IV}`);
  const u = o ? qo.resolve(o, e) : "", d = (c || "1").split("/").map(Number).filter(Number.isFinite);
  return new Lg(a, u, h, d, l);
}
function JC(s) {
  const t = new Bn(s).decimalFloatingPoint("TIME-OFFSET");
  return Bt(t) ? t : null;
}
function Ise(s, e) {
  let t = (s || "").split(/[ ,]+/).filter((i) => i);
  ["video", "audio", "text"].forEach((i) => {
    const n = t.filter((r) => Cse(r, i));
    n.length && (e[`${i}Codec`] = n.join(","), t = t.filter((r) => n.indexOf(r) === -1));
  }), e.unknownCodecs = t;
}
function QC(s, e, t) {
  const i = e[t];
  i && (s[t] = i);
}
function Ose(s, e) {
  let t = s[e];
  for (let i = e; i--; ) {
    const n = s[i];
    if (!n)
      return;
    n.programDateTime = t.programDateTime - n.duration * 1e3, t = n;
  }
}
function $C(s, e) {
  s.rawProgramDateTime ? s.programDateTime = Date.parse(s.rawProgramDateTime) : e != null && e.programDateTime && (s.programDateTime = e.endProgramDateTime), Bt(s.programDateTime) || (s.programDateTime = null, s.rawProgramDateTime = null);
}
function e_(s, e, t, i) {
  s.relurl = e.URI, e.BYTERANGE && s.setByteRange(e.BYTERANGE), s.level = t, s.sn = "initSegment", i && (s.levelkeys = i), s.initSegment = null;
}
function t_(s, e, t) {
  s.levelkeys = e;
  const {
    encryptedFragments: i
  } = t;
  (!i.length || i[i.length - 1].levelkeys !== e) && Object.keys(e).some((n) => e[n].isCommonEncryption) && i.push(s);
}
var ki = {
  MANIFEST: "manifest",
  LEVEL: "level",
  AUDIO_TRACK: "audioTrack",
  SUBTITLE_TRACK: "subtitleTrack"
}, jt = {
  MAIN: "main",
  AUDIO: "audio",
  SUBTITLE: "subtitle"
};
function i_(s) {
  const {
    type: e
  } = s;
  switch (e) {
    case ki.AUDIO_TRACK:
      return jt.AUDIO;
    case ki.SUBTITLE_TRACK:
      return jt.SUBTITLE;
    default:
      return jt.MAIN;
  }
}
function cA(s, e) {
  let t = s.url;
  return (t === void 0 || t.indexOf("data:") === 0) && (t = e.url), t;
}
class Nse {
  constructor(e) {
    this.hls = void 0, this.loaders = /* @__PURE__ */ Object.create(null), this.variableList = null, this.hls = e, this.registerListeners();
  }
  startLoad(e) {
  }
  stopLoad() {
    this.destroyInternalLoaders();
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.LEVEL_LOADING, this.onLevelLoading, this), e.on(j.AUDIO_TRACK_LOADING, this.onAudioTrackLoading, this), e.on(j.SUBTITLE_TRACK_LOADING, this.onSubtitleTrackLoading, this);
  }
  unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.LEVEL_LOADING, this.onLevelLoading, this), e.off(j.AUDIO_TRACK_LOADING, this.onAudioTrackLoading, this), e.off(j.SUBTITLE_TRACK_LOADING, this.onSubtitleTrackLoading, this);
  }
  /**
   * Returns defaults or configured loader-type overloads (pLoader and loader config params)
   */
  createInternalLoader(e) {
    const t = this.hls.config, i = t.pLoader, n = t.loader, r = i || n, a = new r(t);
    return this.loaders[e.type] = a, a;
  }
  getInternalLoader(e) {
    return this.loaders[e.type];
  }
  resetInternalLoader(e) {
    this.loaders[e] && delete this.loaders[e];
  }
  /**
   * Call `destroy` on all internal loader instances mapped (one per context type)
   */
  destroyInternalLoaders() {
    for (const e in this.loaders) {
      const t = this.loaders[e];
      t && t.destroy(), this.resetInternalLoader(e);
    }
  }
  destroy() {
    this.variableList = null, this.unregisterListeners(), this.destroyInternalLoaders();
  }
  onManifestLoading(e, t) {
    const {
      url: i
    } = t;
    this.variableList = null, this.load({
      id: null,
      level: 0,
      responseType: "text",
      type: ki.MANIFEST,
      url: i,
      deliveryDirectives: null
    });
  }
  onLevelLoading(e, t) {
    const {
      id: i,
      level: n,
      pathwayId: r,
      url: a,
      deliveryDirectives: o
    } = t;
    this.load({
      id: i,
      level: n,
      pathwayId: r,
      responseType: "text",
      type: ki.LEVEL,
      url: a,
      deliveryDirectives: o
    });
  }
  onAudioTrackLoading(e, t) {
    const {
      id: i,
      groupId: n,
      url: r,
      deliveryDirectives: a
    } = t;
    this.load({
      id: i,
      groupId: n,
      level: null,
      responseType: "text",
      type: ki.AUDIO_TRACK,
      url: r,
      deliveryDirectives: a
    });
  }
  onSubtitleTrackLoading(e, t) {
    const {
      id: i,
      groupId: n,
      url: r,
      deliveryDirectives: a
    } = t;
    this.load({
      id: i,
      groupId: n,
      level: null,
      responseType: "text",
      type: ki.SUBTITLE_TRACK,
      url: r,
      deliveryDirectives: a
    });
  }
  load(e) {
    var t;
    const i = this.hls.config;
    let n = this.getInternalLoader(e);
    if (n) {
      const c = n.context;
      if (c && c.url === e.url && c.level === e.level) {
        ze.trace("[playlist-loader]: playlist request ongoing");
        return;
      }
      ze.log(`[playlist-loader]: aborting previous loader for type: ${e.type}`), n.abort();
    }
    let r;
    if (e.type === ki.MANIFEST ? r = i.manifestLoadPolicy.default : r = as({}, i.playlistLoadPolicy.default, {
      timeoutRetry: null,
      errorRetry: null
    }), n = this.createInternalLoader(e), Bt((t = e.deliveryDirectives) == null ? void 0 : t.part)) {
      let c;
      if (e.type === ki.LEVEL && e.level !== null ? c = this.hls.levels[e.level].details : e.type === ki.AUDIO_TRACK && e.id !== null ? c = this.hls.audioTracks[e.id].details : e.type === ki.SUBTITLE_TRACK && e.id !== null && (c = this.hls.subtitleTracks[e.id].details), c) {
        const h = c.partTarget, u = c.targetduration;
        if (h && u) {
          const d = Math.max(h * 3, u * 0.8) * 1e3;
          r = as({}, r, {
            maxTimeToFirstByteMs: Math.min(d, r.maxTimeToFirstByteMs),
            maxLoadTimeMs: Math.min(d, r.maxTimeToFirstByteMs)
          });
        }
      }
    }
    const a = r.errorRetry || r.timeoutRetry || {}, o = {
      loadPolicy: r,
      timeout: r.maxLoadTimeMs,
      maxRetry: a.maxNumRetry || 0,
      retryDelay: a.retryDelayMs || 0,
      maxRetryDelay: a.maxRetryDelayMs || 0
    }, l = {
      onSuccess: (c, h, u, d) => {
        const f = this.getInternalLoader(u);
        this.resetInternalLoader(u.type);
        const p = c.data;
        if (p.indexOf("#EXTM3U") !== 0) {
          this.handleManifestParsingError(c, u, new Error("no EXTM3U delimiter"), d || null, h);
          return;
        }
        h.parsing.start = performance.now(), qo.isMediaPlaylist(p) ? this.handleTrackOrLevelPlaylist(c, h, u, d || null, f) : this.handleMasterPlaylist(c, h, u, d);
      },
      onError: (c, h, u, d) => {
        this.handleNetworkError(h, u, !1, c, d);
      },
      onTimeout: (c, h, u) => {
        this.handleNetworkError(h, u, !0, void 0, c);
      }
    };
    n.load(e, o, l);
  }
  handleMasterPlaylist(e, t, i, n) {
    const r = this.hls, a = e.data, o = cA(e, i), l = qo.parseMasterPlaylist(a, o);
    if (l.playlistParsingError) {
      this.handleManifestParsingError(e, i, l.playlistParsingError, n, t);
      return;
    }
    const {
      contentSteering: c,
      levels: h,
      sessionData: u,
      sessionKeys: d,
      startTimeOffset: f,
      variableList: p
    } = l;
    this.variableList = p;
    const {
      AUDIO: v = [],
      SUBTITLES: g,
      "CLOSED-CAPTIONS": m
    } = qo.parseMasterPlaylistMedia(a, o, l);
    v.length && !v.some((y) => !y.url) && h[0].audioCodec && !h[0].attrs.AUDIO && (ze.log("[playlist-loader]: audio codec signaled in quality level, but no embedded audio track signaled, create one"), v.unshift({
      type: "main",
      name: "main",
      groupId: "main",
      default: !1,
      autoselect: !1,
      forced: !1,
      id: -1,
      attrs: new Bn({}),
      bitrate: 0,
      url: ""
    })), r.trigger(j.MANIFEST_LOADED, {
      levels: h,
      audioTracks: v,
      subtitles: g,
      captions: m,
      contentSteering: c,
      url: o,
      stats: t,
      networkDetails: n,
      sessionData: u,
      sessionKeys: d,
      startTimeOffset: f,
      variableList: p
    });
  }
  handleTrackOrLevelPlaylist(e, t, i, n, r) {
    const a = this.hls, {
      id: o,
      level: l,
      type: c
    } = i, h = cA(e, i), u = 0, d = Bt(l) ? l : Bt(o) ? o : 0, f = i_(i), p = qo.parseLevelPlaylist(e.data, h, d, f, u, this.variableList);
    if (c === ki.MANIFEST) {
      const v = {
        attrs: new Bn({}),
        bitrate: 0,
        details: p,
        name: "",
        url: h
      };
      a.trigger(j.MANIFEST_LOADED, {
        levels: [v],
        audioTracks: [],
        url: h,
        stats: t,
        networkDetails: n,
        sessionData: null,
        sessionKeys: null,
        contentSteering: null,
        startTimeOffset: null,
        variableList: null
      });
    }
    t.parsing.end = performance.now(), i.levelDetails = p, this.handlePlaylistLoaded(p, e, t, i, n, r);
  }
  handleManifestParsingError(e, t, i, n, r) {
    this.hls.trigger(j.ERROR, {
      type: qt.NETWORK_ERROR,
      details: Xe.MANIFEST_PARSING_ERROR,
      fatal: t.type === ki.MANIFEST,
      url: e.url,
      err: i,
      error: i,
      reason: i.message,
      response: e,
      context: t,
      networkDetails: n,
      stats: r
    });
  }
  handleNetworkError(e, t, i = !1, n, r) {
    let a = `A network ${i ? "timeout" : "error" + (n ? " (status " + n.code + ")" : "")} occurred while loading ${e.type}`;
    e.type === ki.LEVEL ? a += `: ${e.level} id: ${e.id}` : (e.type === ki.AUDIO_TRACK || e.type === ki.SUBTITLE_TRACK) && (a += ` id: ${e.id} group-id: "${e.groupId}"`);
    const o = new Error(a);
    ze.warn(`[playlist-loader]: ${a}`);
    let l = Xe.UNKNOWN, c = !1;
    const h = this.getInternalLoader(e);
    switch (e.type) {
      case ki.MANIFEST:
        l = i ? Xe.MANIFEST_LOAD_TIMEOUT : Xe.MANIFEST_LOAD_ERROR, c = !0;
        break;
      case ki.LEVEL:
        l = i ? Xe.LEVEL_LOAD_TIMEOUT : Xe.LEVEL_LOAD_ERROR, c = !1;
        break;
      case ki.AUDIO_TRACK:
        l = i ? Xe.AUDIO_TRACK_LOAD_TIMEOUT : Xe.AUDIO_TRACK_LOAD_ERROR, c = !1;
        break;
      case ki.SUBTITLE_TRACK:
        l = i ? Xe.SUBTITLE_TRACK_LOAD_TIMEOUT : Xe.SUBTITLE_LOAD_ERROR, c = !1;
        break;
    }
    h && this.resetInternalLoader(e.type);
    const u = {
      type: qt.NETWORK_ERROR,
      details: l,
      fatal: c,
      url: e.url,
      loader: h,
      context: e,
      error: o,
      networkDetails: t,
      stats: r
    };
    if (n) {
      const d = (t == null ? void 0 : t.url) || e.url;
      u.response = Ls({
        url: d,
        data: void 0
      }, n);
    }
    this.hls.trigger(j.ERROR, u);
  }
  handlePlaylistLoaded(e, t, i, n, r, a) {
    const o = this.hls, {
      type: l,
      level: c,
      id: h,
      groupId: u,
      deliveryDirectives: d
    } = n, f = cA(t, n), p = i_(n), v = typeof n.level == "number" && p === jt.MAIN ? c : void 0;
    if (!e.fragments.length) {
      const m = new Error("No Segments found in Playlist");
      o.trigger(j.ERROR, {
        type: qt.NETWORK_ERROR,
        details: Xe.LEVEL_EMPTY_ERROR,
        fatal: !1,
        url: f,
        error: m,
        reason: m.message,
        response: t,
        context: n,
        level: v,
        parent: p,
        networkDetails: r,
        stats: i
      });
      return;
    }
    e.targetduration || (e.playlistParsingError = new Error("Missing Target Duration"));
    const g = e.playlistParsingError;
    if (g) {
      o.trigger(j.ERROR, {
        type: qt.NETWORK_ERROR,
        details: Xe.LEVEL_PARSING_ERROR,
        fatal: !1,
        url: f,
        error: g,
        reason: g.message,
        response: t,
        context: n,
        level: v,
        parent: p,
        networkDetails: r,
        stats: i
      });
      return;
    }
    switch (e.live && a && (a.getCacheAge && (e.ageHeader = a.getCacheAge() || 0), (!a.getCacheAge || isNaN(e.ageHeader)) && (e.ageHeader = 0)), l) {
      case ki.MANIFEST:
      case ki.LEVEL:
        o.trigger(j.LEVEL_LOADED, {
          details: e,
          level: v || 0,
          id: h || 0,
          stats: i,
          networkDetails: r,
          deliveryDirectives: d
        });
        break;
      case ki.AUDIO_TRACK:
        o.trigger(j.AUDIO_TRACK_LOADED, {
          details: e,
          id: h || 0,
          groupId: u || "",
          stats: i,
          networkDetails: r,
          deliveryDirectives: d
        });
        break;
      case ki.SUBTITLE_TRACK:
        o.trigger(j.SUBTITLE_TRACK_LOADED, {
          details: e,
          id: h || 0,
          groupId: u || "",
          stats: i,
          networkDetails: r,
          deliveryDirectives: d
        });
        break;
    }
  }
}
function ON(s, e) {
  let t;
  try {
    t = new Event("addtrack");
  } catch {
    t = document.createEvent("Event"), t.initEvent("addtrack", !1, !1);
  }
  t.track = s, e.dispatchEvent(t);
}
function NN(s, e) {
  const t = s.mode;
  if (t === "disabled" && (s.mode = "hidden"), s.cues && !s.cues.getCueById(e.id))
    try {
      if (s.addCue(e), !s.cues.getCueById(e.id))
        throw new Error(`addCue is failed for: ${e}`);
    } catch (i) {
      ze.debug(`[texttrack-utils]: ${i}`);
      try {
        const n = new self.TextTrackCue(e.startTime, e.endTime, e.text);
        n.id = e.id, s.addCue(n);
      } catch (n) {
        ze.debug(`[texttrack-utils]: Legacy TextTrackCue fallback failed: ${n}`);
      }
    }
  t === "disabled" && (s.mode = t);
}
function wf(s) {
  const e = s.mode;
  if (e === "disabled" && (s.mode = "hidden"), s.cues)
    for (let t = s.cues.length; t--; )
      s.removeCue(s.cues[t]);
  e === "disabled" && (s.mode = e);
}
function Cb(s, e, t, i) {
  const n = s.mode;
  if (n === "disabled" && (s.mode = "hidden"), s.cues && s.cues.length > 0) {
    const r = Fse(s.cues, e, t);
    for (let a = 0; a < r.length; a++)
      (!i || i(r[a])) && s.removeCue(r[a]);
  }
  n === "disabled" && (s.mode = n);
}
function Bse(s, e) {
  if (e < s[0].startTime)
    return 0;
  const t = s.length - 1;
  if (e > s[t].endTime)
    return -1;
  let i = 0, n = t;
  for (; i <= n; ) {
    const r = Math.floor((n + i) / 2);
    if (e < s[r].startTime)
      n = r - 1;
    else if (e > s[r].startTime && i < t)
      i = r + 1;
    else
      return r;
  }
  return s[i].startTime - e < e - s[n].startTime ? i : n;
}
function Fse(s, e, t) {
  const i = [], n = Bse(s, e);
  if (n > -1)
    for (let r = n, a = s.length; r < a; r++) {
      const o = s[r];
      if (o.startTime >= e && o.endTime <= t)
        i.push(o);
      else if (o.startTime > t)
        return i;
    }
  return i;
}
function By(s) {
  const e = [];
  for (let t = 0; t < s.length; t++) {
    const i = s[t];
    (i.kind === "subtitles" || i.kind === "captions") && i.label && e.push(s[t]);
  }
  return e;
}
var Ka = {
  audioId3: "org.id3",
  dateRange: "com.apple.quicktime.HLS",
  emsg: "https://aomedia.org/emsg/ID3"
};
const Use = 0.25;
function _b() {
  if (!(typeof self > "u"))
    return self.VTTCue || self.TextTrackCue;
}
function n_(s, e, t, i, n) {
  let r = new s(e, t, "");
  try {
    r.value = i, n && (r.type = n);
  } catch {
    r = new s(e, t, JSON.stringify(n ? Ls({
      type: n
    }, i) : i));
  }
  return r;
}
const r3 = (() => {
  const s = _b();
  try {
    s && new s(0, Number.POSITIVE_INFINITY, "");
  } catch {
    return Number.MAX_VALUE;
  }
  return Number.POSITIVE_INFINITY;
})();
function hA(s, e) {
  return s.getTime() / 1e3 - e;
}
function zse(s) {
  return Uint8Array.from(s.replace(/^0x/, "").replace(/([\da-fA-F]{2}) ?/g, "0x$1 ").replace(/ +$/, "").split(" ")).buffer;
}
class kse {
  constructor(e) {
    this.hls = void 0, this.id3Track = null, this.media = null, this.dateRangeCuesAppended = {}, this.hls = e, this._registerListeners();
  }
  destroy() {
    this._unregisterListeners(), this.id3Track = null, this.media = null, this.dateRangeCuesAppended = {}, this.hls = null;
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.FRAG_PARSING_METADATA, this.onFragParsingMetadata, this), e.on(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.on(j.LEVEL_UPDATED, this.onLevelUpdated, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.FRAG_PARSING_METADATA, this.onFragParsingMetadata, this), e.off(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.off(j.LEVEL_UPDATED, this.onLevelUpdated, this);
  }
  // Add ID3 metatadata text track.
  onMediaAttached(e, t) {
    this.media = t.media;
  }
  onMediaDetaching() {
    this.id3Track && (wf(this.id3Track), this.id3Track = null, this.media = null, this.dateRangeCuesAppended = {});
  }
  onManifestLoading() {
    this.dateRangeCuesAppended = {};
  }
  createTrack(e) {
    const t = this.getID3Track(e.textTracks);
    return t.mode = "hidden", t;
  }
  getID3Track(e) {
    if (this.media) {
      for (let t = 0; t < e.length; t++) {
        const i = e[t];
        if (i.kind === "metadata" && i.label === "id3")
          return ON(i, this.media), i;
      }
      return this.media.addTextTrack("metadata", "id3");
    }
  }
  onFragParsingMetadata(e, t) {
    if (!this.media)
      return;
    const {
      hls: {
        config: {
          enableEmsgMetadataCues: i,
          enableID3MetadataCues: n
        }
      }
    } = this;
    if (!i && !n)
      return;
    const {
      samples: r
    } = t;
    this.id3Track || (this.id3Track = this.createTrack(this.media));
    const a = _b();
    if (a)
      for (let o = 0; o < r.length; o++) {
        const l = r[o].type;
        if (l === Ka.emsg && !i || !n)
          continue;
        const c = wN(r[o].data);
        if (c) {
          const h = r[o].pts;
          let u = h + r[o].duration;
          u > r3 && (u = r3), u - h <= 0 && (u = h + Use);
          for (let f = 0; f < c.length; f++) {
            const p = c[f];
            if (!SN(p)) {
              this.updateId3CueEnds(h, l);
              const v = n_(a, h, u, p, l);
              v && this.id3Track.addCue(v);
            }
          }
        }
      }
  }
  updateId3CueEnds(e, t) {
    var i;
    const n = (i = this.id3Track) == null ? void 0 : i.cues;
    if (n)
      for (let r = n.length; r--; ) {
        const a = n[r];
        a.type === t && a.startTime < e && a.endTime === r3 && (a.endTime = e);
      }
  }
  onBufferFlushing(e, {
    startOffset: t,
    endOffset: i,
    type: n
  }) {
    const {
      id3Track: r,
      hls: a
    } = this;
    if (!a)
      return;
    const {
      config: {
        enableEmsgMetadataCues: o,
        enableID3MetadataCues: l
      }
    } = a;
    if (r && (o || l)) {
      let c;
      n === "audio" ? c = (h) => h.type === Ka.audioId3 && l : n === "video" ? c = (h) => h.type === Ka.emsg && o : c = (h) => h.type === Ka.audioId3 && l || h.type === Ka.emsg && o, Cb(r, t, i, c);
    }
  }
  onLevelUpdated(e, {
    details: t
  }) {
    if (!this.media || !t.hasProgramDateTime || !this.hls.config.enableDateRangeMetadataCues)
      return;
    const {
      dateRangeCuesAppended: i,
      id3Track: n
    } = this, {
      dateRanges: r
    } = t, a = Object.keys(r);
    if (n) {
      const h = Object.keys(i).filter((u) => !a.includes(u));
      for (let u = h.length; u--; ) {
        const d = h[u];
        Object.keys(i[d].cues).forEach((f) => {
          n.removeCue(i[d].cues[f]);
        }), delete i[d];
      }
    }
    const o = t.fragments[t.fragments.length - 1];
    if (a.length === 0 || !Bt(o == null ? void 0 : o.programDateTime))
      return;
    this.id3Track || (this.id3Track = this.createTrack(this.media));
    const l = o.programDateTime / 1e3 - o.start, c = _b();
    for (let h = 0; h < a.length; h++) {
      const u = a[h], d = r[u], f = hA(d.startDate, l), p = i[u], v = (p == null ? void 0 : p.cues) || {};
      let g = (p == null ? void 0 : p.durationKnown) || !1, m = r3;
      const x = d.endDate;
      if (x)
        m = hA(x, l), g = !0;
      else if (d.endOnNext && !g) {
        const A = a.reduce((b, M) => {
          if (M !== d.id) {
            const w = r[M];
            if (w.class === d.class && w.startDate > d.startDate && (!b || d.startDate < b.startDate))
              return w;
          }
          return b;
        }, null);
        A && (m = hA(A.startDate, l), g = !0);
      }
      const y = Object.keys(d.attr);
      for (let A = 0; A < y.length; A++) {
        const b = y[A];
        if (!Vne(b))
          continue;
        const M = v[b];
        if (M)
          g && !p.durationKnown && (M.endTime = m);
        else if (c) {
          let w = d.attr[b];
          Wne(b) && (w = zse(w));
          const C = n_(c, f, m, {
            key: b,
            data: w
          }, Ka.dateRange);
          C && (C.id = u, this.id3Track.addCue(C), v[b] = C);
        }
      }
      i[u] = {
        cues: v,
        dateRange: d,
        durationKnown: g
      };
    }
  }
}
class Gse {
  constructor(e) {
    this.hls = void 0, this.config = void 0, this.media = null, this.levelDetails = null, this.currentTime = 0, this.stallCount = 0, this._latency = null, this.timeupdateHandler = () => this.timeupdate(), this.hls = e, this.config = e.config, this.registerListeners();
  }
  get latency() {
    return this._latency || 0;
  }
  get maxLatency() {
    const {
      config: e,
      levelDetails: t
    } = this;
    return e.liveMaxLatencyDuration !== void 0 ? e.liveMaxLatencyDuration : t ? e.liveMaxLatencyDurationCount * t.targetduration : 0;
  }
  get targetLatency() {
    const {
      levelDetails: e
    } = this;
    if (e === null)
      return null;
    const {
      holdBack: t,
      partHoldBack: i,
      targetduration: n
    } = e, {
      liveSyncDuration: r,
      liveSyncDurationCount: a,
      lowLatencyMode: o
    } = this.config, l = this.hls.userConfig;
    let c = o && i || t;
    (l.liveSyncDuration || l.liveSyncDurationCount || c === 0) && (c = r !== void 0 ? r : a * n);
    const h = n, u = 1;
    return c + Math.min(this.stallCount * u, h);
  }
  get liveSyncPosition() {
    const e = this.estimateLiveEdge(), t = this.targetLatency, i = this.levelDetails;
    if (e === null || t === null || i === null)
      return null;
    const n = i.edge, r = e - t - this.edgeStalled, a = n - i.totalduration, o = n - (this.config.lowLatencyMode && i.partTarget || i.targetduration);
    return Math.min(Math.max(a, r), o);
  }
  get drift() {
    const {
      levelDetails: e
    } = this;
    return e === null ? 1 : e.drift;
  }
  get edgeStalled() {
    const {
      levelDetails: e
    } = this;
    if (e === null)
      return 0;
    const t = (this.config.lowLatencyMode && e.partTarget || e.targetduration) * 3;
    return Math.max(e.age - t, 0);
  }
  get forwardBufferLength() {
    const {
      media: e,
      levelDetails: t
    } = this;
    if (!e || !t)
      return 0;
    const i = e.buffered.length;
    return (i ? e.buffered.end(i - 1) : t.edge) - this.currentTime;
  }
  destroy() {
    this.unregisterListeners(), this.onMediaDetaching(), this.levelDetails = null, this.hls = this.timeupdateHandler = null;
  }
  registerListeners() {
    this.hls.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), this.hls.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), this.hls.on(j.MANIFEST_LOADING, this.onManifestLoading, this), this.hls.on(j.LEVEL_UPDATED, this.onLevelUpdated, this), this.hls.on(j.ERROR, this.onError, this);
  }
  unregisterListeners() {
    this.hls.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), this.hls.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), this.hls.off(j.MANIFEST_LOADING, this.onManifestLoading, this), this.hls.off(j.LEVEL_UPDATED, this.onLevelUpdated, this), this.hls.off(j.ERROR, this.onError, this);
  }
  onMediaAttached(e, t) {
    this.media = t.media, this.media.addEventListener("timeupdate", this.timeupdateHandler);
  }
  onMediaDetaching() {
    this.media && (this.media.removeEventListener("timeupdate", this.timeupdateHandler), this.media = null);
  }
  onManifestLoading() {
    this.levelDetails = null, this._latency = null, this.stallCount = 0;
  }
  onLevelUpdated(e, {
    details: t
  }) {
    this.levelDetails = t, t.advanced && this.timeupdate(), !t.live && this.media && this.media.removeEventListener("timeupdate", this.timeupdateHandler);
  }
  onError(e, t) {
    var i;
    t.details === Xe.BUFFER_STALLED_ERROR && (this.stallCount++, (i = this.levelDetails) != null && i.live && ze.warn("[playback-rate-controller]: Stall detected, adjusting target latency"));
  }
  timeupdate() {
    const {
      media: e,
      levelDetails: t
    } = this;
    if (!e || !t)
      return;
    this.currentTime = e.currentTime;
    const i = this.computeLatency();
    if (i === null)
      return;
    this._latency = i;
    const {
      lowLatencyMode: n,
      maxLiveSyncPlaybackRate: r
    } = this.config;
    if (!n || r === 1 || !t.live)
      return;
    const a = this.targetLatency;
    if (a === null)
      return;
    const o = i - a, l = Math.min(this.maxLatency, a + t.targetduration);
    if (o < l && o > 0.05 && this.forwardBufferLength > 1) {
      const h = Math.min(2, Math.max(1, r)), u = Math.round(2 / (1 + Math.exp(-0.75 * o - this.edgeStalled)) * 20) / 20;
      e.playbackRate = Math.min(h, Math.max(1, u));
    } else
      e.playbackRate !== 1 && e.playbackRate !== 0 && (e.playbackRate = 1);
  }
  estimateLiveEdge() {
    const {
      levelDetails: e
    } = this;
    return e === null ? null : e.edge + e.age;
  }
  computeLatency() {
    const e = this.estimateLiveEdge();
    return e === null ? null : e - this.currentTime;
  }
}
const Pb = ["NONE", "TYPE-0", "TYPE-1", null];
function Hse(s) {
  return Pb.indexOf(s) > -1;
}
const vx = ["SDR", "PQ", "HLG"];
function Vse(s) {
  return !!s && vx.indexOf(s) > -1;
}
var Fy = {
  No: "",
  Yes: "YES",
  v2: "v2"
};
function s_(s) {
  const {
    canSkipUntil: e,
    canSkipDateRanges: t,
    age: i
  } = s, n = i < e / 2;
  return e && n ? t ? Fy.v2 : Fy.Yes : Fy.No;
}
class r_ {
  constructor(e, t, i) {
    this.msn = void 0, this.part = void 0, this.skip = void 0, this.msn = e, this.part = t, this.skip = i;
  }
  addDirectives(e) {
    const t = new self.URL(e);
    return this.msn !== void 0 && t.searchParams.set("_HLS_msn", this.msn.toString()), this.part !== void 0 && t.searchParams.set("_HLS_part", this.part.toString()), this.skip && t.searchParams.set("_HLS_skip", this.skip), t.href;
  }
}
class Tp {
  constructor(e) {
    this._attrs = void 0, this.audioCodec = void 0, this.bitrate = void 0, this.codecSet = void 0, this.url = void 0, this.frameRate = void 0, this.height = void 0, this.id = void 0, this.name = void 0, this.videoCodec = void 0, this.width = void 0, this.details = void 0, this.fragmentError = 0, this.loadError = 0, this.loaded = void 0, this.realBitrate = 0, this.supportedPromise = void 0, this.supportedResult = void 0, this._avgBitrate = 0, this._audioGroups = void 0, this._subtitleGroups = void 0, this._urlId = 0, this.url = [e.url], this._attrs = [e.attrs], this.bitrate = e.bitrate, e.details && (this.details = e.details), this.id = e.id || 0, this.name = e.name, this.width = e.width || 0, this.height = e.height || 0, this.frameRate = e.attrs.optionalFloat("FRAME-RATE", 0), this._avgBitrate = e.attrs.decimalInteger("AVERAGE-BANDWIDTH"), this.audioCodec = e.audioCodec, this.videoCodec = e.videoCodec, this.codecSet = [e.videoCodec, e.audioCodec].filter((t) => !!t).map((t) => t.substring(0, 4)).join(","), this.addGroupId("audio", e.attrs.AUDIO), this.addGroupId("text", e.attrs.SUBTITLES);
  }
  get maxBitrate() {
    return Math.max(this.realBitrate, this.bitrate);
  }
  get averageBitrate() {
    return this._avgBitrate || this.realBitrate || this.bitrate;
  }
  get attrs() {
    return this._attrs[0];
  }
  get codecs() {
    return this.attrs.CODECS || "";
  }
  get pathwayId() {
    return this.attrs["PATHWAY-ID"] || ".";
  }
  get videoRange() {
    return this.attrs["VIDEO-RANGE"] || "SDR";
  }
  get score() {
    return this.attrs.optionalFloat("SCORE", 0);
  }
  get uri() {
    return this.url[0] || "";
  }
  hasAudioGroup(e) {
    return a_(this._audioGroups, e);
  }
  hasSubtitleGroup(e) {
    return a_(this._subtitleGroups, e);
  }
  get audioGroups() {
    return this._audioGroups;
  }
  get subtitleGroups() {
    return this._subtitleGroups;
  }
  addGroupId(e, t) {
    if (t) {
      if (e === "audio") {
        let i = this._audioGroups;
        i || (i = this._audioGroups = []), i.indexOf(t) === -1 && i.push(t);
      } else if (e === "text") {
        let i = this._subtitleGroups;
        i || (i = this._subtitleGroups = []), i.indexOf(t) === -1 && i.push(t);
      }
    }
  }
  // Deprecated methods (retained for backwards compatibility)
  get urlId() {
    return 0;
  }
  set urlId(e) {
  }
  get audioGroupIds() {
    return this.audioGroups ? [this.audioGroupId] : void 0;
  }
  get textGroupIds() {
    return this.subtitleGroups ? [this.textGroupId] : void 0;
  }
  get audioGroupId() {
    var e;
    return (e = this.audioGroups) == null ? void 0 : e[0];
  }
  get textGroupId() {
    var e;
    return (e = this.subtitleGroups) == null ? void 0 : e[0];
  }
  addFallback() {
  }
}
function a_(s, e) {
  return !e || !s ? !1 : s.indexOf(e) !== -1;
}
function uA(s, e) {
  const t = e.startPTS;
  if (Bt(t)) {
    let i = 0, n;
    e.sn > s.sn ? (i = t - s.start, n = s) : (i = s.start - t, n = e), n.duration !== i && (n.duration = i);
  } else
    e.sn > s.sn ? s.cc === e.cc && s.minEndPTS ? e.start = s.start + (s.minEndPTS - s.start) : e.start = s.start + s.duration : e.start = Math.max(s.start - e.duration, 0);
}
function BN(s, e, t, i, n, r) {
  i - t <= 0 && (ze.warn("Fragment should have a positive duration", e), i = t + e.duration, r = n + e.duration);
  let o = t, l = i;
  const c = e.startPTS, h = e.endPTS;
  if (Bt(c)) {
    const g = Math.abs(c - t);
    Bt(e.deltaPTS) ? e.deltaPTS = Math.max(g, e.deltaPTS) : e.deltaPTS = g, o = Math.max(t, c), t = Math.min(t, c), n = Math.min(n, e.startDTS), l = Math.min(i, h), i = Math.max(i, h), r = Math.max(r, e.endDTS);
  }
  const u = t - e.start;
  e.start !== 0 && (e.start = t), e.duration = i - e.start, e.startPTS = t, e.maxStartPTS = o, e.startDTS = n, e.endPTS = i, e.minEndPTS = l, e.endDTS = r;
  const d = e.sn;
  if (!s || d < s.startSN || d > s.endSN)
    return 0;
  let f;
  const p = d - s.startSN, v = s.fragments;
  for (v[p] = e, f = p; f > 0; f--)
    uA(v[f], v[f - 1]);
  for (f = p; f < v.length - 1; f++)
    uA(v[f], v[f + 1]);
  return s.fragmentHint && uA(v[v.length - 1], s.fragmentHint), s.PTSKnown = s.alignedSliding = !0, u;
}
function Wse(s, e) {
  let t = null;
  const i = s.fragments;
  for (let l = i.length - 1; l >= 0; l--) {
    const c = i[l].initSegment;
    if (c) {
      t = c;
      break;
    }
  }
  s.fragmentHint && delete s.fragmentHint.endPTS;
  let n = 0, r;
  if (qse(s, e, (l, c) => {
    l.relurl && (n = l.cc - c.cc), Bt(l.startPTS) && Bt(l.endPTS) && (c.start = c.startPTS = l.startPTS, c.startDTS = l.startDTS, c.maxStartPTS = l.maxStartPTS, c.endPTS = l.endPTS, c.endDTS = l.endDTS, c.minEndPTS = l.minEndPTS, c.duration = l.endPTS - l.startPTS, c.duration && (r = c), e.PTSKnown = e.alignedSliding = !0), c.elementaryStreams = l.elementaryStreams, c.loader = l.loader, c.stats = l.stats, l.initSegment && (c.initSegment = l.initSegment, t = l.initSegment);
  }), t && (e.fragmentHint ? e.fragments.concat(e.fragmentHint) : e.fragments).forEach((c) => {
    var h;
    c && (!c.initSegment || c.initSegment.relurl === ((h = t) == null ? void 0 : h.relurl)) && (c.initSegment = t);
  }), e.skippedSegments)
    if (e.deltaUpdateFailed = e.fragments.some((l) => !l), e.deltaUpdateFailed) {
      ze.warn("[level-helper] Previous playlist missing segments skipped in delta playlist");
      for (let l = e.skippedSegments; l--; )
        e.fragments.shift();
      e.startSN = e.fragments[0].sn, e.startCC = e.fragments[0].cc;
    } else
      e.canSkipDateRanges && (e.dateRanges = Xse(s.dateRanges, e.dateRanges, e.recentlyRemovedDateranges));
  const a = e.fragments;
  if (n) {
    ze.warn("discontinuity sliding from playlist, take drift into account");
    for (let l = 0; l < a.length; l++)
      a[l].cc += n;
  }
  e.skippedSegments && (e.startCC = e.fragments[0].cc), jse(s.partList, e.partList, (l, c) => {
    c.elementaryStreams = l.elementaryStreams, c.stats = l.stats;
  }), r ? BN(e, r, r.startPTS, r.endPTS, r.startDTS, r.endDTS) : FN(s, e), a.length && (e.totalduration = e.edge - a[0].start), e.driftStartTime = s.driftStartTime, e.driftStart = s.driftStart;
  const o = e.advancedDateTime;
  if (e.advanced && o) {
    const l = e.edge;
    e.driftStart || (e.driftStartTime = o, e.driftStart = l), e.driftEndTime = o, e.driftEnd = l;
  } else
    e.driftEndTime = s.driftEndTime, e.driftEnd = s.driftEnd, e.advancedDateTime = s.advancedDateTime;
}
function Xse(s, e, t) {
  const i = as({}, s);
  return t && t.forEach((n) => {
    delete i[n];
  }), Object.keys(e).forEach((n) => {
    const r = new yN(e[n].attr, i[n]);
    r.isValid ? i[n] = r : ze.warn(`Ignoring invalid Playlist Delta Update DATERANGE tag: "${JSON.stringify(e[n].attr)}"`);
  }), i;
}
function jse(s, e, t) {
  if (s && e) {
    let i = 0;
    for (let n = 0, r = s.length; n <= r; n++) {
      const a = s[n], o = e[n + i];
      a && o && a.index === o.index && a.fragment.sn === o.fragment.sn ? t(a, o) : i--;
    }
  }
}
function qse(s, e, t) {
  const i = e.skippedSegments, n = Math.max(s.startSN, e.startSN) - e.startSN, r = (s.fragmentHint ? 1 : 0) + (i ? e.endSN : Math.min(s.endSN, e.endSN)) - e.startSN, a = e.startSN - s.startSN, o = e.fragmentHint ? e.fragments.concat(e.fragmentHint) : e.fragments, l = s.fragmentHint ? s.fragments.concat(s.fragmentHint) : s.fragments;
  for (let c = n; c <= r; c++) {
    const h = l[a + c];
    let u = o[c];
    i && !u && c < i && (u = e.fragments[c] = h), h && u && t(h, u);
  }
}
function FN(s, e) {
  const t = e.startSN + e.skippedSegments - s.startSN, i = s.fragments;
  t < 0 || t >= i.length || Rb(e, i[t].start);
}
function Rb(s, e) {
  if (e) {
    const t = s.fragments;
    for (let i = s.skippedSegments; i < t.length; i++)
      t[i].start += e;
    s.fragmentHint && (s.fragmentHint.start += e);
  }
}
function Yse(s, e = 1 / 0) {
  let t = 1e3 * s.targetduration;
  if (s.updated) {
    const i = s.fragments, n = 4;
    if (i.length && t * n > e) {
      const r = i[i.length - 1].duration * 1e3;
      r < t && (t = r);
    }
  } else
    t /= 2;
  return Math.round(t);
}
function Zse(s, e, t) {
  if (!(s != null && s.details))
    return null;
  const i = s.details;
  let n = i.fragments[e - i.startSN];
  return n || (n = i.fragmentHint, n && n.sn === e) ? n : e < i.startSN && t && t.sn === e ? t : null;
}
function o_(s, e, t) {
  var i;
  return s != null && s.details ? UN((i = s.details) == null ? void 0 : i.partList, e, t) : null;
}
function UN(s, e, t) {
  if (s)
    for (let i = s.length; i--; ) {
      const n = s[i];
      if (n.index === t && n.fragment.sn === e)
        return n;
    }
  return null;
}
function zN(s) {
  s.forEach((e, t) => {
    const {
      details: i
    } = e;
    i != null && i.fragments && i.fragments.forEach((n) => {
      n.level = t;
    });
  });
}
function yx(s) {
  switch (s.details) {
    case Xe.FRAG_LOAD_TIMEOUT:
    case Xe.KEY_LOAD_TIMEOUT:
    case Xe.LEVEL_LOAD_TIMEOUT:
    case Xe.MANIFEST_LOAD_TIMEOUT:
      return !0;
  }
  return !1;
}
function l_(s, e) {
  const t = yx(e);
  return s.default[`${t ? "timeout" : "error"}Retry`];
}
function KS(s, e) {
  const t = s.backoff === "linear" ? 1 : Math.pow(2, e);
  return Math.min(t * s.retryDelayMs, s.maxRetryDelayMs);
}
function c_(s) {
  return Ls(Ls({}, s), {
    errorRetry: null,
    timeoutRetry: null
  });
}
function xx(s, e, t, i) {
  if (!s)
    return !1;
  const n = i == null ? void 0 : i.code, r = e < s.maxNumRetry && (Kse(n) || !!t);
  return s.shouldRetry ? s.shouldRetry(s, e, t, i, r) : r;
}
function Kse(s) {
  return s === 0 && navigator.onLine === !1 || !!s && (s < 400 || s > 499);
}
const kN = {
  /**
   * Searches for an item in an array which matches a certain condition.
   * This requires the condition to only match one item in the array,
   * and for the array to be ordered.
   *
   * @param list The array to search.
   * @param comparisonFn
   *      Called and provided a candidate item as the first argument.
   *      Should return:
   *          > -1 if the item should be located at a lower index than the provided item.
   *          > 1 if the item should be located at a higher index than the provided item.
   *          > 0 if the item is the item you're looking for.
   *
   * @returns the object if found, otherwise returns null
   */
  search: function(s, e) {
    let t = 0, i = s.length - 1, n = null, r = null;
    for (; t <= i; ) {
      n = (t + i) / 2 | 0, r = s[n];
      const a = e(r);
      if (a > 0)
        t = n + 1;
      else if (a < 0)
        i = n - 1;
      else
        return r;
    }
    return null;
  }
};
function Jse(s, e, t) {
  if (e === null || !Array.isArray(s) || !s.length || !Bt(e))
    return null;
  const i = s[0].programDateTime;
  if (e < (i || 0))
    return null;
  const n = s[s.length - 1].endProgramDateTime;
  if (e >= (n || 0))
    return null;
  t = t || 0;
  for (let r = 0; r < s.length; ++r) {
    const a = s[r];
    if (Qse(e, t, a))
      return a;
  }
  return null;
}
function Ax(s, e, t = 0, i = 0) {
  let n = null;
  if (s) {
    n = e[s.sn - e[0].sn + 1] || null;
    const a = s.endDTS - t;
    a > 0 && a < 15e-7 && (t += 15e-7);
  } else
    t === 0 && e[0].start === 0 && (n = e[0]);
  if (n && (!s || s.level === n.level) && Db(t, i, n) === 0)
    return n;
  const r = kN.search(e, Db.bind(null, t, i));
  return r && (r !== s || !n) ? r : n;
}
function Db(s = 0, e = 0, t) {
  if (t.start <= s && t.start + t.duration > s)
    return 0;
  const i = Math.min(e, t.duration + (t.deltaPTS ? t.deltaPTS : 0));
  return t.start + t.duration - i <= s ? 1 : t.start - i > s && t.start ? -1 : 0;
}
function Qse(s, e, t) {
  const i = Math.min(e, t.duration + (t.deltaPTS ? t.deltaPTS : 0)) * 1e3;
  return (t.endProgramDateTime || 0) - i > s;
}
function $se(s, e) {
  return kN.search(s, (t) => t.cc < e ? 1 : t.cc > e ? -1 : 0);
}
var Us = {
  DoNothing: 0,
  SendEndCallback: 1,
  SendAlternateToPenaltyBox: 2,
  RemoveAlternatePermanently: 3,
  InsertDiscontinuity: 4,
  RetryRequest: 5
}, Va = {
  None: 0,
  MoveAllAlternatesMatchingHost: 1,
  MoveAllAlternatesMatchingHDCP: 2,
  SwitchToSDR: 4
};
class ere {
  constructor(e) {
    this.hls = void 0, this.playlistError = 0, this.penalizedRenditions = {}, this.log = void 0, this.warn = void 0, this.error = void 0, this.hls = e, this.log = ze.log.bind(ze, "[info]:"), this.warn = ze.warn.bind(ze, "[warning]:"), this.error = ze.error.bind(ze, "[error]:"), this.registerListeners();
  }
  registerListeners() {
    const e = this.hls;
    e.on(j.ERROR, this.onError, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.LEVEL_UPDATED, this.onLevelUpdated, this);
  }
  unregisterListeners() {
    const e = this.hls;
    e && (e.off(j.ERROR, this.onError, this), e.off(j.ERROR, this.onErrorOut, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.LEVEL_UPDATED, this.onLevelUpdated, this));
  }
  destroy() {
    this.unregisterListeners(), this.hls = null, this.penalizedRenditions = {};
  }
  startLoad(e) {
  }
  stopLoad() {
    this.playlistError = 0;
  }
  getVariantLevelIndex(e) {
    return (e == null ? void 0 : e.type) === jt.MAIN ? e.level : this.hls.loadLevel;
  }
  onManifestLoading() {
    this.playlistError = 0, this.penalizedRenditions = {};
  }
  onLevelUpdated() {
    this.playlistError = 0;
  }
  onError(e, t) {
    var i, n;
    if (t.fatal)
      return;
    const r = this.hls, a = t.context;
    switch (t.details) {
      case Xe.FRAG_LOAD_ERROR:
      case Xe.FRAG_LOAD_TIMEOUT:
      case Xe.KEY_LOAD_ERROR:
      case Xe.KEY_LOAD_TIMEOUT:
        t.errorAction = this.getFragRetryOrSwitchAction(t);
        return;
      case Xe.FRAG_PARSING_ERROR:
        if ((i = t.frag) != null && i.gap) {
          t.errorAction = {
            action: Us.DoNothing,
            flags: Va.None
          };
          return;
        }
      case Xe.FRAG_GAP:
      case Xe.FRAG_DECRYPT_ERROR: {
        t.errorAction = this.getFragRetryOrSwitchAction(t), t.errorAction.action = Us.SendAlternateToPenaltyBox;
        return;
      }
      case Xe.LEVEL_EMPTY_ERROR:
      case Xe.LEVEL_PARSING_ERROR:
        {
          var o, l;
          const c = t.parent === jt.MAIN ? t.level : r.loadLevel;
          t.details === Xe.LEVEL_EMPTY_ERROR && ((o = t.context) != null && (l = o.levelDetails) != null && l.live) ? t.errorAction = this.getPlaylistRetryOrSwitchAction(t, c) : (t.levelRetry = !1, t.errorAction = this.getLevelSwitchAction(t, c));
        }
        return;
      case Xe.LEVEL_LOAD_ERROR:
      case Xe.LEVEL_LOAD_TIMEOUT:
        typeof (a == null ? void 0 : a.level) == "number" && (t.errorAction = this.getPlaylistRetryOrSwitchAction(t, a.level));
        return;
      case Xe.AUDIO_TRACK_LOAD_ERROR:
      case Xe.AUDIO_TRACK_LOAD_TIMEOUT:
      case Xe.SUBTITLE_LOAD_ERROR:
      case Xe.SUBTITLE_TRACK_LOAD_TIMEOUT:
        if (a) {
          const c = r.levels[r.loadLevel];
          if (c && (a.type === ki.AUDIO_TRACK && c.hasAudioGroup(a.groupId) || a.type === ki.SUBTITLE_TRACK && c.hasSubtitleGroup(a.groupId))) {
            t.errorAction = this.getPlaylistRetryOrSwitchAction(t, r.loadLevel), t.errorAction.action = Us.SendAlternateToPenaltyBox, t.errorAction.flags = Va.MoveAllAlternatesMatchingHost;
            return;
          }
        }
        return;
      case Xe.KEY_SYSTEM_STATUS_OUTPUT_RESTRICTED:
        {
          const c = r.levels[r.loadLevel], h = c == null ? void 0 : c.attrs["HDCP-LEVEL"];
          h ? t.errorAction = {
            action: Us.SendAlternateToPenaltyBox,
            flags: Va.MoveAllAlternatesMatchingHDCP,
            hdcpLevel: h
          } : this.keySystemError(t);
        }
        return;
      case Xe.BUFFER_ADD_CODEC_ERROR:
      case Xe.REMUX_ALLOC_ERROR:
      case Xe.BUFFER_APPEND_ERROR:
        t.errorAction = this.getLevelSwitchAction(t, (n = t.level) != null ? n : r.loadLevel);
        return;
      case Xe.INTERNAL_EXCEPTION:
      case Xe.BUFFER_APPENDING_ERROR:
      case Xe.BUFFER_FULL_ERROR:
      case Xe.LEVEL_SWITCH_ERROR:
      case Xe.BUFFER_STALLED_ERROR:
      case Xe.BUFFER_SEEK_OVER_HOLE:
      case Xe.BUFFER_NUDGE_ON_STALL:
        t.errorAction = {
          action: Us.DoNothing,
          flags: Va.None
        };
        return;
    }
    t.type === qt.KEY_SYSTEM_ERROR && this.keySystemError(t);
  }
  keySystemError(e) {
    const t = this.getVariantLevelIndex(e.frag);
    e.levelRetry = !1, e.errorAction = this.getLevelSwitchAction(e, t);
  }
  getPlaylistRetryOrSwitchAction(e, t) {
    const i = this.hls, n = l_(i.config.playlistLoadPolicy, e), r = this.playlistError++;
    if (xx(n, r, yx(e), e.response))
      return {
        action: Us.RetryRequest,
        flags: Va.None,
        retryConfig: n,
        retryCount: r
      };
    const o = this.getLevelSwitchAction(e, t);
    return n && (o.retryConfig = n, o.retryCount = r), o;
  }
  getFragRetryOrSwitchAction(e) {
    const t = this.hls, i = this.getVariantLevelIndex(e.frag), n = t.levels[i], {
      fragLoadPolicy: r,
      keyLoadPolicy: a
    } = t.config, o = l_(e.details.startsWith("key") ? a : r, e), l = t.levels.reduce((h, u) => h + u.fragmentError, 0);
    if (n && (e.details !== Xe.FRAG_GAP && n.fragmentError++, xx(o, l, yx(e), e.response)))
      return {
        action: Us.RetryRequest,
        flags: Va.None,
        retryConfig: o,
        retryCount: l
      };
    const c = this.getLevelSwitchAction(e, i);
    return o && (c.retryConfig = o, c.retryCount = l), c;
  }
  getLevelSwitchAction(e, t) {
    const i = this.hls;
    t == null && (t = i.loadLevel);
    const n = this.hls.levels[t];
    if (n) {
      var r, a;
      const c = e.details;
      n.loadError++, c === Xe.BUFFER_APPEND_ERROR && n.fragmentError++;
      let h = -1;
      const {
        levels: u,
        loadLevel: d,
        minAutoLevel: f,
        maxAutoLevel: p
      } = i;
      i.autoLevelEnabled || (i.loadLevel = -1);
      const v = (r = e.frag) == null ? void 0 : r.type, m = (v === jt.AUDIO && c === Xe.FRAG_PARSING_ERROR || e.sourceBufferName === "audio" && (c === Xe.BUFFER_ADD_CODEC_ERROR || c === Xe.BUFFER_APPEND_ERROR)) && u.some(({
        audioCodec: M
      }) => n.audioCodec !== M), y = e.sourceBufferName === "video" && (c === Xe.BUFFER_ADD_CODEC_ERROR || c === Xe.BUFFER_APPEND_ERROR) && u.some(({
        codecSet: M,
        audioCodec: w
      }) => n.codecSet !== M && n.audioCodec === w), {
        type: A,
        groupId: b
      } = (a = e.context) != null ? a : {};
      for (let M = u.length; M--; ) {
        const w = (M + d) % u.length;
        if (w !== d && w >= f && w <= p && u[w].loadError === 0) {
          var o, l;
          const C = u[w];
          if (c === Xe.FRAG_GAP && e.frag) {
            const T = u[w].details;
            if (T) {
              const E = Ax(e.frag, T.fragments, e.frag.start);
              if (E != null && E.gap)
                continue;
            }
          } else {
            if (A === ki.AUDIO_TRACK && C.hasAudioGroup(b) || A === ki.SUBTITLE_TRACK && C.hasSubtitleGroup(b))
              continue;
            if (v === jt.AUDIO && (o = n.audioGroups) != null && o.some((T) => C.hasAudioGroup(T)) || v === jt.SUBTITLE && (l = n.subtitleGroups) != null && l.some((T) => C.hasSubtitleGroup(T)) || m && n.audioCodec === C.audioCodec || !m && n.audioCodec !== C.audioCodec || y && n.codecSet === C.codecSet)
              continue;
          }
          h = w;
          break;
        }
      }
      if (h > -1 && i.loadLevel !== h)
        return e.levelRetry = !0, this.playlistError = 0, {
          action: Us.SendAlternateToPenaltyBox,
          flags: Va.None,
          nextAutoLevel: h
        };
    }
    return {
      action: Us.SendAlternateToPenaltyBox,
      flags: Va.MoveAllAlternatesMatchingHost
    };
  }
  onErrorOut(e, t) {
    var i;
    switch ((i = t.errorAction) == null ? void 0 : i.action) {
      case Us.DoNothing:
        break;
      case Us.SendAlternateToPenaltyBox:
        this.sendAlternateToPenaltyBox(t), !t.errorAction.resolved && t.details !== Xe.FRAG_GAP ? t.fatal = !0 : /MediaSource readyState: ended/.test(t.error.message) && (this.warn(`MediaSource ended after "${t.sourceBufferName}" sourceBuffer append error. Attempting to recover from media error.`), this.hls.recoverMediaError());
        break;
    }
    if (t.fatal) {
      this.hls.stopLoad();
      return;
    }
  }
  sendAlternateToPenaltyBox(e) {
    const t = this.hls, i = e.errorAction;
    if (!i)
      return;
    const {
      flags: n,
      hdcpLevel: r,
      nextAutoLevel: a
    } = i;
    switch (n) {
      case Va.None:
        this.switchLevel(e, a);
        break;
      case Va.MoveAllAlternatesMatchingHDCP:
        r && (t.maxHdcpLevel = Pb[Pb.indexOf(r) - 1], i.resolved = !0), this.warn(`Restricting playback to HDCP-LEVEL of "${t.maxHdcpLevel}" or lower`);
        break;
    }
    i.resolved || this.switchLevel(e, a);
  }
  switchLevel(e, t) {
    t !== void 0 && e.errorAction && (this.warn(`switching to level ${t} after ${e.details}`), this.hls.nextAutoLevel = t, e.errorAction.resolved = !0, this.hls.nextLoadLevel = this.hls.nextAutoLevel);
  }
}
class JS {
  constructor(e, t) {
    this.hls = void 0, this.timer = -1, this.requestScheduled = -1, this.canLoad = !1, this.log = void 0, this.warn = void 0, this.log = ze.log.bind(ze, `${t}:`), this.warn = ze.warn.bind(ze, `${t}:`), this.hls = e;
  }
  destroy() {
    this.clearTimer(), this.hls = this.log = this.warn = null;
  }
  clearTimer() {
    this.timer !== -1 && (self.clearTimeout(this.timer), this.timer = -1);
  }
  startLoad() {
    this.canLoad = !0, this.requestScheduled = -1, this.loadPlaylist();
  }
  stopLoad() {
    this.canLoad = !1, this.clearTimer();
  }
  switchParams(e, t, i) {
    const n = t == null ? void 0 : t.renditionReports;
    if (n) {
      let r = -1;
      for (let a = 0; a < n.length; a++) {
        const o = n[a];
        let l;
        try {
          l = new self.URL(o.URI, t.url).href;
        } catch (c) {
          ze.warn(`Could not construct new URL for Rendition Report: ${c}`), l = o.URI || "";
        }
        if (l === e) {
          r = a;
          break;
        } else
          l === e.substring(0, l.length) && (r = a);
      }
      if (r !== -1) {
        const a = n[r], o = parseInt(a["LAST-MSN"]) || (t == null ? void 0 : t.lastPartSn);
        let l = parseInt(a["LAST-PART"]) || (t == null ? void 0 : t.lastPartIndex);
        if (this.hls.config.lowLatencyMode) {
          const h = Math.min(t.age - t.partTarget, t.targetduration);
          l >= 0 && h > t.partTarget && (l += 1);
        }
        const c = i && s_(i);
        return new r_(o, l >= 0 ? l : void 0, c);
      }
    }
  }
  loadPlaylist(e) {
    this.requestScheduled === -1 && (this.requestScheduled = self.performance.now());
  }
  shouldLoadPlaylist(e) {
    return this.canLoad && !!e && !!e.url && (!e.details || e.details.live);
  }
  shouldReloadPlaylist(e) {
    return this.timer === -1 && this.requestScheduled === -1 && this.shouldLoadPlaylist(e);
  }
  playlistLoaded(e, t, i) {
    const {
      details: n,
      stats: r
    } = t, a = self.performance.now(), o = r.loading.first ? Math.max(0, a - r.loading.first) : 0;
    if (n.advancedDateTime = Date.now() - o, n.live || i != null && i.live) {
      if (n.reloaded(i), i && this.log(`live playlist ${e} ${n.advanced ? "REFRESHED " + n.lastPartSn + "-" + n.lastPartIndex : n.updated ? "UPDATED" : "MISSED"}`), i && n.fragments.length > 0 && Wse(i, n), !this.canLoad || !n.live)
        return;
      let l, c, h;
      if (n.canBlockReload && n.endSN && n.advanced) {
        const g = this.hls.config.lowLatencyMode, m = n.lastPartSn, x = n.endSN, y = n.lastPartIndex, A = y !== -1, b = m === x, M = g ? 0 : y;
        A ? (c = b ? x + 1 : m, h = b ? M : y + 1) : c = x + 1;
        const w = n.age, C = w + n.ageHeader;
        let T = Math.min(C - n.partTarget, n.targetduration * 1.5);
        if (T > 0) {
          if (i && T > i.tuneInGoal)
            this.warn(`CDN Tune-in goal increased from: ${i.tuneInGoal} to: ${T} with playlist age: ${n.age}`), T = 0;
          else {
            const E = Math.floor(T / n.targetduration);
            if (c += E, h !== void 0) {
              const P = Math.round(T % n.targetduration / n.partTarget);
              h += P;
            }
            this.log(`CDN Tune-in age: ${n.ageHeader}s last advanced ${w.toFixed(2)}s goal: ${T} skip sn ${E} to part ${h}`);
          }
          n.tuneInGoal = T;
        }
        if (l = this.getDeliveryDirectives(n, t.deliveryDirectives, c, h), g || !b) {
          this.loadPlaylist(l);
          return;
        }
      } else
        (n.canBlockReload || n.canSkipUntil) && (l = this.getDeliveryDirectives(n, t.deliveryDirectives, c, h));
      const u = this.hls.mainForwardBufferInfo, d = u ? u.end - u.len : 0, f = (n.edge - d) * 1e3, p = Yse(n, f);
      n.updated && a > this.requestScheduled + p && (this.requestScheduled = r.loading.start), c !== void 0 && n.canBlockReload ? this.requestScheduled = r.loading.first + p - (n.partTarget * 1e3 || 1e3) : this.requestScheduled === -1 || this.requestScheduled + p < a ? this.requestScheduled = a : this.requestScheduled - a <= 0 && (this.requestScheduled += p);
      let v = this.requestScheduled - a;
      v = Math.max(0, v), this.log(`reload live playlist ${e} in ${Math.round(v)} ms`), this.timer = self.setTimeout(() => this.loadPlaylist(l), v);
    } else
      this.clearTimer();
  }
  getDeliveryDirectives(e, t, i, n) {
    let r = s_(e);
    return t != null && t.skip && e.deltaUpdateFailed && (i = t.msn, n = t.part, r = Fy.No), new r_(i, n, r);
  }
  checkRetry(e) {
    const t = e.details, i = yx(e), n = e.errorAction, {
      action: r,
      retryCount: a = 0,
      retryConfig: o
    } = n || {}, l = !!n && !!o && (r === Us.RetryRequest || !n.resolved && r === Us.SendAlternateToPenaltyBox);
    if (l) {
      var c;
      if (this.requestScheduled = -1, a >= o.maxNumRetry)
        return !1;
      if (i && (c = e.context) != null && c.deliveryDirectives)
        this.warn(`Retrying playlist loading ${a + 1}/${o.maxNumRetry} after "${t}" without delivery-directives`), this.loadPlaylist();
      else {
        const h = KS(o, a);
        this.timer = self.setTimeout(() => this.loadPlaylist(), h), this.warn(`Retrying playlist loading ${a + 1}/${o.maxNumRetry} after "${t}" in ${h}ms`);
      }
      e.levelRetry = !0, n.resolved = !0;
    }
    return l;
  }
}
class _d {
  //  About half of the estimated value will be from the last |halfLife| samples by weight.
  constructor(e, t = 0, i = 0) {
    this.halfLife = void 0, this.alpha_ = void 0, this.estimate_ = void 0, this.totalWeight_ = void 0, this.halfLife = e, this.alpha_ = e ? Math.exp(Math.log(0.5) / e) : 0, this.estimate_ = t, this.totalWeight_ = i;
  }
  sample(e, t) {
    const i = Math.pow(this.alpha_, e);
    this.estimate_ = t * (1 - i) + i * this.estimate_, this.totalWeight_ += e;
  }
  getTotalWeight() {
    return this.totalWeight_;
  }
  getEstimate() {
    if (this.alpha_) {
      const e = 1 - Math.pow(this.alpha_, this.totalWeight_);
      if (e)
        return this.estimate_ / e;
    }
    return this.estimate_;
  }
}
class tre {
  constructor(e, t, i, n = 100) {
    this.defaultEstimate_ = void 0, this.minWeight_ = void 0, this.minDelayMs_ = void 0, this.slow_ = void 0, this.fast_ = void 0, this.defaultTTFB_ = void 0, this.ttfb_ = void 0, this.defaultEstimate_ = i, this.minWeight_ = 1e-3, this.minDelayMs_ = 50, this.slow_ = new _d(e), this.fast_ = new _d(t), this.defaultTTFB_ = n, this.ttfb_ = new _d(e);
  }
  update(e, t) {
    const {
      slow_: i,
      fast_: n,
      ttfb_: r
    } = this;
    i.halfLife !== e && (this.slow_ = new _d(e, i.getEstimate(), i.getTotalWeight())), n.halfLife !== t && (this.fast_ = new _d(t, n.getEstimate(), n.getTotalWeight())), r.halfLife !== e && (this.ttfb_ = new _d(e, r.getEstimate(), r.getTotalWeight()));
  }
  sample(e, t) {
    e = Math.max(e, this.minDelayMs_);
    const i = 8 * t, n = e / 1e3, r = i / n;
    this.fast_.sample(n, r), this.slow_.sample(n, r);
  }
  sampleTTFB(e) {
    const t = e / 1e3, i = Math.sqrt(2) * Math.exp(-Math.pow(t, 2) / 2);
    this.ttfb_.sample(i, Math.max(e, 5));
  }
  canEstimate() {
    return this.fast_.getTotalWeight() >= this.minWeight_;
  }
  getEstimate() {
    return this.canEstimate() ? Math.min(this.fast_.getEstimate(), this.slow_.getEstimate()) : this.defaultEstimate_;
  }
  getEstimateTTFB() {
    return this.ttfb_.getTotalWeight() >= this.minWeight_ ? this.ttfb_.getEstimate() : this.defaultTTFB_;
  }
  destroy() {
  }
}
const GN = {
  supported: !0,
  configurations: [],
  decodingInfoResults: [{
    supported: !0,
    powerEfficient: !0,
    smooth: !0
  }]
}, h_ = {};
function ire(s, e, t, i, n, r) {
  const a = s.audioCodec ? s.audioGroups : null, o = r == null ? void 0 : r.audioCodec, l = r == null ? void 0 : r.channels, c = l ? parseInt(l) : o ? 1 / 0 : 2;
  let h = null;
  if (a != null && a.length)
    try {
      a.length === 1 && a[0] ? h = e.groups[a[0]].channels : h = a.reduce((u, d) => {
        if (d) {
          const f = e.groups[d];
          if (!f)
            throw new Error(`Audio track group ${d} not found`);
          Object.keys(f.channels).forEach((p) => {
            u[p] = (u[p] || 0) + f.channels[p];
          });
        }
        return u;
      }, {
        2: 0
      });
    } catch {
      return !0;
    }
  return s.videoCodec !== void 0 && (s.width > 1920 && s.height > 1088 || s.height > 1920 && s.width > 1088 || s.frameRate > Math.max(i, 30) || s.videoRange !== "SDR" && s.videoRange !== t || s.bitrate > Math.max(n, 8e6)) || !!h && Bt(c) && Object.keys(h).some((u) => parseInt(u) > c);
}
function nre(s, e, t) {
  const i = s.videoCodec, n = s.audioCodec;
  if (!i || !n || !t)
    return Promise.resolve(GN);
  const r = {
    width: s.width,
    height: s.height,
    bitrate: Math.ceil(Math.max(s.bitrate * 0.9, s.averageBitrate)),
    // Assume a framerate of 30fps since MediaCapabilities will not accept Level default of 0.
    framerate: s.frameRate || 30
  }, a = s.videoRange;
  a !== "SDR" && (r.transferFunction = a.toLowerCase());
  const o = i.split(",").map((l) => ({
    type: "media-source",
    video: Ls(Ls({}, r), {}, {
      contentType: Ig(l, "video")
    })
  }));
  return n && s.audioGroups && s.audioGroups.forEach((l) => {
    var c;
    l && ((c = e.groups[l]) == null || c.tracks.forEach((h) => {
      if (h.groupId === l) {
        const u = h.channels || "", d = parseFloat(u);
        Bt(d) && d > 2 && o.push.apply(o, n.split(",").map((f) => ({
          type: "media-source",
          audio: {
            contentType: Ig(f, "audio"),
            channels: "" + d
            // spatialRendering:
            //   audioCodec === 'ec-3' && channels.indexOf('JOC'),
          }
        })));
      }
    }));
  }), Promise.all(o.map((l) => {
    const c = sre(l);
    return h_[c] || (h_[c] = t.decodingInfo(l));
  })).then((l) => ({
    supported: !l.some((c) => !c.supported),
    configurations: o,
    decodingInfoResults: l
  })).catch((l) => ({
    supported: !1,
    configurations: o,
    decodingInfoResults: [],
    error: l
  }));
}
function sre(s) {
  const {
    audio: e,
    video: t
  } = s, i = t || e;
  if (i) {
    const n = i.contentType.split('"')[1];
    if (t)
      return `r${t.height}x${t.width}f${Math.ceil(t.framerate)}${t.transferFunction || "sd"}_${n}_${Math.ceil(t.bitrate / 1e5)}`;
    if (e)
      return `c${e.channels}${e.spatialRendering ? "s" : "n"}_${n}`;
  }
  return "";
}
function rre() {
  if (typeof matchMedia == "function") {
    const s = matchMedia("(dynamic-range: high)"), e = matchMedia("bad query");
    if (s.media !== e.media)
      return s.matches === !0;
  }
  return !1;
}
function are(s, e) {
  let t = !1, i = [];
  return s && (t = s !== "SDR", i = [s]), e && (i = e.allowedVideoRanges || vx.slice(0), t = e.preferHDR !== void 0 ? e.preferHDR : rre(), t ? i = i.filter((n) => n !== "SDR") : i = ["SDR"]), {
    preferHDR: t,
    allowedVideoRanges: i
  };
}
function ore(s, e, t, i, n) {
  const r = Object.keys(s), a = i == null ? void 0 : i.channels, o = i == null ? void 0 : i.audioCodec, l = a && parseInt(a) === 2;
  let c = !0, h = !1, u = 1 / 0, d = 1 / 0, f = 1 / 0, p = 0, v = [];
  const {
    preferHDR: g,
    allowedVideoRanges: m
  } = are(e, n);
  for (let b = r.length; b--; ) {
    const M = s[r[b]];
    c = M.channels[2] > 0, u = Math.min(u, M.minHeight), d = Math.min(d, M.minFramerate), f = Math.min(f, M.minBitrate);
    const w = m.filter((C) => M.videoRanges[C] > 0);
    w.length > 0 && (h = !0, v = w);
  }
  u = Bt(u) ? u : 0, d = Bt(d) ? d : 0;
  const x = Math.max(1080, u), y = Math.max(30, d);
  return f = Bt(f) ? f : t, t = Math.max(f, t), h || (e = void 0, v = []), {
    codecSet: r.reduce((b, M) => {
      const w = s[M];
      if (M === b)
        return b;
      if (w.minBitrate > t)
        return D0(M, `min bitrate of ${w.minBitrate} > current estimate of ${t}`), b;
      if (!w.hasDefaultAudio)
        return D0(M, "no renditions with default or auto-select sound found"), b;
      if (o && M.indexOf(o.substring(0, 4)) % 5 !== 0)
        return D0(M, `audio codec preference "${o}" not found`), b;
      if (a && !l) {
        if (!w.channels[a])
          return D0(M, `no renditions with ${a} channel sound found (channels options: ${Object.keys(w.channels)})`), b;
      } else if ((!o || l) && c && w.channels[2] === 0)
        return D0(M, "no renditions with stereo sound found"), b;
      return w.minHeight > x ? (D0(M, `min resolution of ${w.minHeight} > maximum of ${x}`), b) : w.minFramerate > y ? (D0(M, `min framerate of ${w.minFramerate} > maximum of ${y}`), b) : v.some((C) => w.videoRanges[C] > 0) ? w.maxScore < p ? (D0(M, `max score of ${w.maxScore} < selected max of ${p}`), b) : b && (mx(M) >= mx(b) || w.fragmentError > s[b].fragmentError) ? b : (p = w.maxScore, M) : (D0(M, `no variants with VIDEO-RANGE of ${JSON.stringify(v)} found`), b);
    }, void 0),
    videoRanges: v,
    preferHDR: g,
    minFramerate: d,
    minBitrate: f
  };
}
function D0(s, e) {
  ze.log(`[abr] start candidates with "${s}" ignored because ${e}`);
}
function lre(s) {
  return s.reduce((e, t) => {
    let i = e.groups[t.groupId];
    i || (i = e.groups[t.groupId] = {
      tracks: [],
      channels: {
        2: 0
      },
      hasDefault: !1,
      hasAutoSelect: !1
    }), i.tracks.push(t);
    const n = t.channels || "2";
    return i.channels[n] = (i.channels[n] || 0) + 1, i.hasDefault = i.hasDefault || t.default, i.hasAutoSelect = i.hasAutoSelect || t.autoselect, i.hasDefault && (e.hasDefaultAudio = !0), i.hasAutoSelect && (e.hasAutoSelectAudio = !0), e;
  }, {
    hasDefaultAudio: !1,
    hasAutoSelectAudio: !1,
    groups: {}
  });
}
function cre(s, e, t, i) {
  return s.slice(t, i + 1).reduce((n, r) => {
    if (!r.codecSet)
      return n;
    const a = r.audioGroups;
    let o = n[r.codecSet];
    o || (n[r.codecSet] = o = {
      minBitrate: 1 / 0,
      minHeight: 1 / 0,
      minFramerate: 1 / 0,
      maxScore: 0,
      videoRanges: {
        SDR: 0
      },
      channels: {
        2: 0
      },
      hasDefaultAudio: !a,
      fragmentError: 0
    }), o.minBitrate = Math.min(o.minBitrate, r.bitrate);
    const l = Math.min(r.height, r.width);
    return o.minHeight = Math.min(o.minHeight, l), o.minFramerate = Math.min(o.minFramerate, r.frameRate), o.maxScore = Math.max(o.maxScore, r.score), o.fragmentError += r.fragmentError, o.videoRanges[r.videoRange] = (o.videoRanges[r.videoRange] || 0) + 1, a && a.forEach((c) => {
      if (!c)
        return;
      const h = e.groups[c];
      h && (o.hasDefaultAudio = o.hasDefaultAudio || e.hasDefaultAudio ? h.hasDefault : h.hasAutoSelect || !e.hasDefaultAudio && !e.hasAutoSelectAudio, Object.keys(h.channels).forEach((u) => {
        o.channels[u] = (o.channels[u] || 0) + h.channels[u];
      }));
    }), n;
  }, {});
}
function Yo(s, e, t) {
  if ("attrs" in s) {
    const i = e.indexOf(s);
    if (i !== -1)
      return i;
  }
  for (let i = 0; i < e.length; i++) {
    const n = e[i];
    if (Hf(s, n, t))
      return i;
  }
  return -1;
}
function Hf(s, e, t) {
  const {
    groupId: i,
    name: n,
    lang: r,
    assocLang: a,
    characteristics: o,
    default: l
  } = s, c = s.forced;
  return (i === void 0 || e.groupId === i) && (n === void 0 || e.name === n) && (r === void 0 || e.lang === r) && (r === void 0 || e.assocLang === a) && (l === void 0 || e.default === l) && (c === void 0 || e.forced === c) && (o === void 0 || hre(o, e.characteristics)) && (t === void 0 || t(s, e));
}
function hre(s, e = "") {
  const t = s.split(","), i = e.split(",");
  return t.length === i.length && !t.some((n) => i.indexOf(n) === -1);
}
function Pd(s, e) {
  const {
    audioCodec: t,
    channels: i
  } = s;
  return (t === void 0 || (e.audioCodec || "").substring(0, 4) === t.substring(0, 4)) && (i === void 0 || i === (e.channels || "2"));
}
function ure(s, e, t, i, n) {
  const r = e[i], o = e.reduce((d, f, p) => {
    const v = f.uri;
    return (d[v] || (d[v] = [])).push(p), d;
  }, {})[r.uri];
  o.length > 1 && (i = Math.max.apply(Math, o));
  const l = r.videoRange, c = r.frameRate, h = r.codecSet.substring(0, 4), u = u_(e, i, (d) => {
    if (d.videoRange !== l || d.frameRate !== c || d.codecSet.substring(0, 4) !== h)
      return !1;
    const f = d.audioGroups, p = t.filter((v) => !f || f.indexOf(v.groupId) !== -1);
    return Yo(s, p, n) > -1;
  });
  return u > -1 ? u : u_(e, i, (d) => {
    const f = d.audioGroups, p = t.filter((v) => !f || f.indexOf(v.groupId) !== -1);
    return Yo(s, p, n) > -1;
  });
}
function u_(s, e, t) {
  for (let i = e; i; i--)
    if (t(s[i]))
      return i;
  for (let i = e + 1; i < s.length; i++)
    if (t(s[i]))
      return i;
  return -1;
}
class dre {
  constructor(e) {
    this.hls = void 0, this.lastLevelLoadSec = 0, this.lastLoadedFragLevel = -1, this.firstSelection = -1, this._nextAutoLevel = -1, this.nextAutoLevelKey = "", this.audioTracksByGroup = null, this.codecTiers = null, this.timer = -1, this.fragCurrent = null, this.partCurrent = null, this.bitrateTestDelay = 0, this.bwEstimator = void 0, this._abandonRulesCheck = () => {
      const {
        fragCurrent: t,
        partCurrent: i,
        hls: n
      } = this, {
        autoLevelEnabled: r,
        media: a
      } = n;
      if (!t || !a)
        return;
      const o = performance.now(), l = i ? i.stats : t.stats, c = i ? i.duration : t.duration, h = o - l.loading.start, u = n.minAutoLevel;
      if (l.aborted || l.loaded && l.loaded === l.total || t.level <= u) {
        this.clearTimer(), this._nextAutoLevel = -1;
        return;
      }
      if (!r || a.paused || !a.playbackRate || !a.readyState)
        return;
      const d = n.mainForwardBufferInfo;
      if (d === null)
        return;
      const f = this.bwEstimator.getEstimateTTFB(), p = Math.abs(a.playbackRate);
      if (h <= Math.max(f, 1e3 * (c / (p * 2))))
        return;
      const v = d.len / p, g = l.loading.first ? l.loading.first - l.loading.start : -1, m = l.loaded && g > -1, x = this.getBwEstimate(), y = n.levels, A = y[t.level], b = l.total || Math.max(l.loaded, Math.round(c * A.averageBitrate / 8));
      let M = m ? h - g : h;
      M < 1 && m && (M = Math.min(h, l.loaded * 8 / x));
      const w = m ? l.loaded * 1e3 / M : 0, C = w ? (b - l.loaded) / w : b * 8 / x + f / 1e3;
      if (C <= v)
        return;
      const T = w ? w * 8 : x;
      let E = Number.POSITIVE_INFINITY, P;
      for (P = t.level - 1; P > u; P--) {
        const I = y[P].maxBitrate;
        if (E = this.getTimeToLoadFrag(f / 1e3, T, c * I, !y[P].details), E < v)
          break;
      }
      if (E >= C || E > c * 10)
        return;
      n.nextLoadLevel = n.nextAutoLevel = P, m ? this.bwEstimator.sample(h - Math.min(f, g), l.loaded) : this.bwEstimator.sampleTTFB(h);
      const L = y[P].maxBitrate;
      this.getBwEstimate() * this.hls.config.abrBandWidthUpFactor > L && this.resetEstimator(L), this.clearTimer(), ze.warn(`[abr] Fragment ${t.sn}${i ? " part " + i.index : ""} of level ${t.level} is loading too slowly;
      Time to underbuffer: ${v.toFixed(3)} s
      Estimated load time for current fragment: ${C.toFixed(3)} s
      Estimated load time for down switch fragment: ${E.toFixed(3)} s
      TTFB estimate: ${g | 0} ms
      Current BW estimate: ${Bt(x) ? x | 0 : "Unknown"} bps
      New BW estimate: ${this.getBwEstimate() | 0} bps
      Switching to level ${P} @ ${L | 0} bps`), n.trigger(j.FRAG_LOAD_EMERGENCY_ABORTED, {
        frag: t,
        part: i,
        stats: l
      });
    }, this.hls = e, this.bwEstimator = this.initEstimator(), this.registerListeners();
  }
  resetEstimator(e) {
    e && (ze.log(`setting initial bwe to ${e}`), this.hls.config.abrEwmaDefaultEstimate = e), this.firstSelection = -1, this.bwEstimator = this.initEstimator();
  }
  initEstimator() {
    const e = this.hls.config;
    return new tre(e.abrEwmaSlowVoD, e.abrEwmaFastVoD, e.abrEwmaDefaultEstimate);
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.FRAG_LOADING, this.onFragLoading, this), e.on(j.FRAG_LOADED, this.onFragLoaded, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this), e.on(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.on(j.LEVEL_LOADED, this.onLevelLoaded, this), e.on(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.on(j.MAX_AUTO_LEVEL_UPDATED, this.onMaxAutoLevelUpdated, this), e.on(j.ERROR, this.onError, this);
  }
  unregisterListeners() {
    const {
      hls: e
    } = this;
    e && (e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.FRAG_LOADING, this.onFragLoading, this), e.off(j.FRAG_LOADED, this.onFragLoaded, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this), e.off(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.off(j.LEVEL_LOADED, this.onLevelLoaded, this), e.off(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.off(j.MAX_AUTO_LEVEL_UPDATED, this.onMaxAutoLevelUpdated, this), e.off(j.ERROR, this.onError, this));
  }
  destroy() {
    this.unregisterListeners(), this.clearTimer(), this.hls = this._abandonRulesCheck = null, this.fragCurrent = this.partCurrent = null;
  }
  onManifestLoading(e, t) {
    this.lastLoadedFragLevel = -1, this.firstSelection = -1, this.lastLevelLoadSec = 0, this.fragCurrent = this.partCurrent = null, this.onLevelsUpdated(), this.clearTimer();
  }
  onLevelsUpdated() {
    this.lastLoadedFragLevel > -1 && this.fragCurrent && (this.lastLoadedFragLevel = this.fragCurrent.level), this._nextAutoLevel = -1, this.onMaxAutoLevelUpdated(), this.codecTiers = null, this.audioTracksByGroup = null;
  }
  onMaxAutoLevelUpdated() {
    this.firstSelection = -1, this.nextAutoLevelKey = "";
  }
  onFragLoading(e, t) {
    const i = t.frag;
    if (!this.ignoreFragment(i)) {
      if (!i.bitrateTest) {
        var n;
        this.fragCurrent = i, this.partCurrent = (n = t.part) != null ? n : null;
      }
      this.clearTimer(), this.timer = self.setInterval(this._abandonRulesCheck, 100);
    }
  }
  onLevelSwitching(e, t) {
    this.clearTimer();
  }
  onError(e, t) {
    if (!t.fatal)
      switch (t.details) {
        case Xe.BUFFER_ADD_CODEC_ERROR:
        case Xe.BUFFER_APPEND_ERROR:
          this.lastLoadedFragLevel = -1, this.firstSelection = -1;
          break;
        case Xe.FRAG_LOAD_TIMEOUT: {
          const i = t.frag, {
            fragCurrent: n,
            partCurrent: r
          } = this;
          if (i && n && i.sn === n.sn && i.level === n.level) {
            const a = performance.now(), o = r ? r.stats : i.stats, l = a - o.loading.start, c = o.loading.first ? o.loading.first - o.loading.start : -1;
            if (o.loaded && c > -1) {
              const u = this.bwEstimator.getEstimateTTFB();
              this.bwEstimator.sample(l - Math.min(u, c), o.loaded);
            } else
              this.bwEstimator.sampleTTFB(l);
          }
          break;
        }
      }
  }
  getTimeToLoadFrag(e, t, i, n) {
    const r = e + i / t, a = n ? this.lastLevelLoadSec : 0;
    return r + a;
  }
  onLevelLoaded(e, t) {
    const i = this.hls.config, {
      loading: n
    } = t.stats, r = n.end - n.start;
    Bt(r) && (this.lastLevelLoadSec = r / 1e3), t.details.live ? this.bwEstimator.update(i.abrEwmaSlowLive, i.abrEwmaFastLive) : this.bwEstimator.update(i.abrEwmaSlowVoD, i.abrEwmaFastVoD);
  }
  onFragLoaded(e, {
    frag: t,
    part: i
  }) {
    const n = i ? i.stats : t.stats;
    if (t.type === jt.MAIN && this.bwEstimator.sampleTTFB(n.loading.first - n.loading.start), !this.ignoreFragment(t)) {
      if (this.clearTimer(), t.level === this._nextAutoLevel && (this._nextAutoLevel = -1), this.firstSelection = -1, this.hls.config.abrMaxWithRealBitrate) {
        const r = i ? i.duration : t.duration, a = this.hls.levels[t.level], o = (a.loaded ? a.loaded.bytes : 0) + n.loaded, l = (a.loaded ? a.loaded.duration : 0) + r;
        a.loaded = {
          bytes: o,
          duration: l
        }, a.realBitrate = Math.round(8 * o / l);
      }
      if (t.bitrateTest) {
        const r = {
          stats: n,
          frag: t,
          part: i,
          id: t.type
        };
        this.onFragBuffered(j.FRAG_BUFFERED, r), t.bitrateTest = !1;
      } else
        this.lastLoadedFragLevel = t.level;
    }
  }
  onFragBuffered(e, t) {
    const {
      frag: i,
      part: n
    } = t, r = n != null && n.stats.loaded ? n.stats : i.stats;
    if (r.aborted || this.ignoreFragment(i))
      return;
    const a = r.parsing.end - r.loading.start - Math.min(r.loading.first - r.loading.start, this.bwEstimator.getEstimateTTFB());
    this.bwEstimator.sample(a, r.loaded), r.bwEstimate = this.getBwEstimate(), i.bitrateTest ? this.bitrateTestDelay = a / 1e3 : this.bitrateTestDelay = 0;
  }
  ignoreFragment(e) {
    return e.type !== jt.MAIN || e.sn === "initSegment";
  }
  clearTimer() {
    this.timer > -1 && (self.clearInterval(this.timer), this.timer = -1);
  }
  get firstAutoLevel() {
    const {
      maxAutoLevel: e,
      minAutoLevel: t
    } = this.hls, i = this.getBwEstimate(), n = this.hls.config.maxStarvationDelay, r = this.findBestLevel(i, t, e, 0, n, 1, 1);
    if (r > -1)
      return r;
    const a = this.hls.firstLevel, o = Math.min(Math.max(a, t), e);
    return ze.warn(`[abr] Could not find best starting auto level. Defaulting to first in playlist ${a} clamped to ${o}`), o;
  }
  get forcedAutoLevel() {
    return this.nextAutoLevelKey ? -1 : this._nextAutoLevel;
  }
  // return next auto level
  get nextAutoLevel() {
    const e = this.forcedAutoLevel, i = this.bwEstimator.canEstimate(), n = this.lastLoadedFragLevel > -1;
    if (e !== -1 && (!i || !n || this.nextAutoLevelKey === this.getAutoLevelKey()))
      return e;
    const r = i && n ? this.getNextABRAutoLevel() : this.firstAutoLevel;
    if (e !== -1) {
      const a = this.hls.levels;
      if (a.length > Math.max(e, r) && a[e].loadError <= a[r].loadError)
        return e;
    }
    return this._nextAutoLevel = r, this.nextAutoLevelKey = this.getAutoLevelKey(), r;
  }
  getAutoLevelKey() {
    return `${this.getBwEstimate()}_${this.getStarvationDelay().toFixed(2)}`;
  }
  getNextABRAutoLevel() {
    const {
      fragCurrent: e,
      partCurrent: t,
      hls: i
    } = this, {
      maxAutoLevel: n,
      config: r,
      minAutoLevel: a
    } = i, o = t ? t.duration : e ? e.duration : 0, l = this.getBwEstimate(), c = this.getStarvationDelay();
    let h = r.abrBandWidthFactor, u = r.abrBandWidthUpFactor;
    if (c) {
      const g = this.findBestLevel(l, a, n, c, 0, h, u);
      if (g >= 0)
        return g;
    }
    let d = o ? Math.min(o, r.maxStarvationDelay) : r.maxStarvationDelay;
    if (!c) {
      const g = this.bitrateTestDelay;
      g && (d = (o ? Math.min(o, r.maxLoadingDelay) : r.maxLoadingDelay) - g, ze.info(`[abr] bitrate test took ${Math.round(1e3 * g)}ms, set first fragment max fetchDuration to ${Math.round(1e3 * d)} ms`), h = u = 1);
    }
    const f = this.findBestLevel(l, a, n, c, d, h, u);
    if (ze.info(`[abr] ${c ? "rebuffering expected" : "buffer is empty"}, optimal quality level ${f}`), f > -1)
      return f;
    const p = i.levels[a], v = i.levels[i.loadLevel];
    return (p == null ? void 0 : p.bitrate) < (v == null ? void 0 : v.bitrate) ? a : i.loadLevel;
  }
  getStarvationDelay() {
    const e = this.hls, t = e.media;
    if (!t)
      return 1 / 0;
    const i = t && t.playbackRate !== 0 ? Math.abs(t.playbackRate) : 1, n = e.mainForwardBufferInfo;
    return (n ? n.len : 0) / i;
  }
  getBwEstimate() {
    return this.bwEstimator.canEstimate() ? this.bwEstimator.getEstimate() : this.hls.config.abrEwmaDefaultEstimate;
  }
  findBestLevel(e, t, i, n, r, a, o) {
    var l;
    const c = n + r, h = this.lastLoadedFragLevel, u = h === -1 ? this.hls.firstLevel : h, {
      fragCurrent: d,
      partCurrent: f
    } = this, {
      levels: p,
      allAudioTracks: v,
      loadLevel: g,
      config: m
    } = this.hls;
    if (p.length === 1)
      return 0;
    const x = p[u], y = !!(x != null && (l = x.details) != null && l.live), A = g === -1 || h === -1;
    let b, M = "SDR", w = (x == null ? void 0 : x.frameRate) || 0;
    const {
      audioPreference: C,
      videoPreference: T
    } = m, E = this.audioTracksByGroup || (this.audioTracksByGroup = lre(v));
    if (A) {
      if (this.firstSelection !== -1)
        return this.firstSelection;
      const H = this.codecTiers || (this.codecTiers = cre(p, E, t, i)), U = ore(H, M, e, C, T), {
        codecSet: W,
        videoRanges: X,
        minFramerate: K,
        minBitrate: te,
        preferHDR: re
      } = U;
      b = W, M = re ? X[X.length - 1] : X[0], w = K, e = Math.max(e, te), ze.log(`[abr] picked start tier ${JSON.stringify(U)}`);
    } else
      b = x == null ? void 0 : x.codecSet, M = x == null ? void 0 : x.videoRange;
    const P = f ? f.duration : d ? d.duration : 0, L = this.bwEstimator.getEstimateTTFB() / 1e3, I = [];
    for (let H = i; H >= t; H--) {
      var N;
      const U = p[H], W = H > u;
      if (!U)
        continue;
      if (m.useMediaCapabilities && !U.supportedResult && !U.supportedPromise) {
        const J = navigator.mediaCapabilities;
        typeof (J == null ? void 0 : J.decodingInfo) == "function" && ire(U, E, M, w, e, C) ? (U.supportedPromise = nre(U, E, J), U.supportedPromise.then((fe) => {
          if (!this.hls)
            return;
          U.supportedResult = fe;
          const Me = this.hls.levels, ye = Me.indexOf(U);
          fe.error ? ze.warn(`[abr] MediaCapabilities decodingInfo error: "${fe.error}" for level ${ye} ${JSON.stringify(fe)}`) : fe.supported || (ze.warn(`[abr] Unsupported MediaCapabilities decodingInfo result for level ${ye} ${JSON.stringify(fe)}`), ye > -1 && Me.length > 1 && (ze.log(`[abr] Removing unsupported level ${ye}`), this.hls.removeLevel(ye)));
        })) : U.supportedResult = GN;
      }
      if (b && U.codecSet !== b || M && U.videoRange !== M || W && w > U.frameRate || !W && w > 0 && w < U.frameRate || U.supportedResult && !((N = U.supportedResult.decodingInfoResults) != null && N[0].smooth)) {
        I.push(H);
        continue;
      }
      const X = U.details, K = (f ? X == null ? void 0 : X.partTarget : X == null ? void 0 : X.averagetargetduration) || P;
      let te;
      W ? te = o * e : te = a * e;
      const re = P && n >= P * 2 && r === 0 ? p[H].averageBitrate : p[H].maxBitrate, be = this.getTimeToLoadFrag(L, te, re * K, X === void 0);
      if (
        // if adjusted bw is greater than level bitrate AND
        te >= re && // no level change, or new level has no error history
        (H === h || U.loadError === 0 && U.fragmentError === 0) && // fragment fetchDuration unknown OR live stream OR fragment fetchDuration less than max allowed fetch duration, then this level matches
        // we don't account for max Fetch Duration for live streams, this is to avoid switching down when near the edge of live sliding window ...
        // special case to support startLevel = -1 (bitrateTest) on live streams : in that case we should not exit loop so that findBestLevel will return -1
        (be <= L || !Bt(be) || y && !this.bitrateTestDelay || be < c)
      ) {
        const J = this.forcedAutoLevel;
        return H !== g && (J === -1 || J !== g) && (I.length && ze.trace(`[abr] Skipped level(s) ${I.join(",")} of ${i} max with CODECS and VIDEO-RANGE:"${p[I[0]].codecs}" ${p[I[0]].videoRange}; not compatible with "${x.codecs}" ${M}`), ze.info(`[abr] switch candidate:${u}->${H} adjustedbw(${Math.round(te)})-bitrate=${Math.round(te - re)} ttfb:${L.toFixed(1)} avgDuration:${K.toFixed(1)} maxFetchDuration:${c.toFixed(1)} fetchDuration:${be.toFixed(1)} firstSelection:${A} codecSet:${b} videoRange:${M} hls.loadLevel:${g}`)), A && (this.firstSelection = H), H;
      }
    }
    return -1;
  }
  set nextAutoLevel(e) {
    const {
      maxAutoLevel: t,
      minAutoLevel: i
    } = this.hls, n = Math.min(Math.max(e, i), t);
    this._nextAutoLevel !== n && (this.nextAutoLevelKey = "", this._nextAutoLevel = n);
  }
}
class fre {
  constructor() {
    this._boundTick = void 0, this._tickTimer = null, this._tickInterval = null, this._tickCallCount = 0, this._boundTick = this.tick.bind(this);
  }
  destroy() {
    this.onHandlerDestroying(), this.onHandlerDestroyed();
  }
  onHandlerDestroying() {
    this.clearNextTick(), this.clearInterval();
  }
  onHandlerDestroyed() {
  }
  hasInterval() {
    return !!this._tickInterval;
  }
  hasNextTick() {
    return !!this._tickTimer;
  }
  /**
   * @param millis - Interval time (ms)
   * @eturns True when interval has been scheduled, false when already scheduled (no effect)
   */
  setInterval(e) {
    return this._tickInterval ? !1 : (this._tickCallCount = 0, this._tickInterval = self.setInterval(this._boundTick, e), !0);
  }
  /**
   * @returns True when interval was cleared, false when none was set (no effect)
   */
  clearInterval() {
    return this._tickInterval ? (self.clearInterval(this._tickInterval), this._tickInterval = null, !0) : !1;
  }
  /**
   * @returns True when timeout was cleared, false when none was set (no effect)
   */
  clearNextTick() {
    return this._tickTimer ? (self.clearTimeout(this._tickTimer), this._tickTimer = null, !0) : !1;
  }
  /**
   * Will call the subclass doTick implementation in this main loop tick
   * or in the next one (via setTimeout(,0)) in case it has already been called
   * in this tick (in case this is a re-entrant call).
   */
  tick() {
    this._tickCallCount++, this._tickCallCount === 1 && (this.doTick(), this._tickCallCount > 1 && this.tickImmediate(), this._tickCallCount = 0);
  }
  tickImmediate() {
    this.clearNextTick(), this._tickTimer = self.setTimeout(this._boundTick, 0);
  }
  /**
   * For subclass to implement task logic
   * @abstract
   */
  doTick() {
  }
}
var Ts = {
  NOT_LOADED: "NOT_LOADED",
  APPENDING: "APPENDING",
  PARTIAL: "PARTIAL",
  OK: "OK"
};
class pre {
  constructor(e) {
    this.activePartLists = /* @__PURE__ */ Object.create(null), this.endListFragments = /* @__PURE__ */ Object.create(null), this.fragments = /* @__PURE__ */ Object.create(null), this.timeRanges = /* @__PURE__ */ Object.create(null), this.bufferPadding = 0.2, this.hls = void 0, this.hasGaps = !1, this.hls = e, this._registerListeners();
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.BUFFER_APPENDED, this.onBufferAppended, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this), e.on(j.FRAG_LOADED, this.onFragLoaded, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.BUFFER_APPENDED, this.onBufferAppended, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this), e.off(j.FRAG_LOADED, this.onFragLoaded, this);
  }
  destroy() {
    this._unregisterListeners(), this.fragments = // @ts-ignore
    this.activePartLists = // @ts-ignore
    this.endListFragments = this.timeRanges = null;
  }
  /**
   * Return a Fragment or Part with an appended range that matches the position and levelType
   * Otherwise, return null
   */
  getAppendedFrag(e, t) {
    const i = this.activePartLists[t];
    if (i)
      for (let n = i.length; n--; ) {
        const r = i[n];
        if (!r)
          break;
        const a = r.end;
        if (r.start <= e && a !== null && e <= a)
          return r;
      }
    return this.getBufferedFrag(e, t);
  }
  /**
   * Return a buffered Fragment that matches the position and levelType.
   * A buffered Fragment is one whose loading, parsing and appending is done (completed or "partial" meaning aborted).
   * If not found any Fragment, return null
   */
  getBufferedFrag(e, t) {
    const {
      fragments: i
    } = this, n = Object.keys(i);
    for (let r = n.length; r--; ) {
      const a = i[n[r]];
      if ((a == null ? void 0 : a.body.type) === t && a.buffered) {
        const o = a.body;
        if (o.start <= e && e <= o.end)
          return o;
      }
    }
    return null;
  }
  /**
   * Partial fragments effected by coded frame eviction will be removed
   * The browser will unload parts of the buffer to free up memory for new buffer data
   * Fragments will need to be reloaded when the buffer is freed up, removing partial fragments will allow them to reload(since there might be parts that are still playable)
   */
  detectEvictedFragments(e, t, i, n) {
    this.timeRanges && (this.timeRanges[e] = t);
    const r = (n == null ? void 0 : n.fragment.sn) || -1;
    Object.keys(this.fragments).forEach((a) => {
      const o = this.fragments[a];
      if (!o || r >= o.body.sn)
        return;
      if (!o.buffered && !o.loaded) {
        o.body.type === i && this.removeFragment(o.body);
        return;
      }
      const l = o.range[e];
      l && l.time.some((c) => {
        const h = !this.isTimeBuffered(c.startPTS, c.endPTS, t);
        return h && this.removeFragment(o.body), h;
      });
    });
  }
  /**
   * Checks if the fragment passed in is loaded in the buffer properly
   * Partially loaded fragments will be registered as a partial fragment
   */
  detectPartialFragments(e) {
    const t = this.timeRanges, {
      frag: i,
      part: n
    } = e;
    if (!t || i.sn === "initSegment")
      return;
    const r = Rd(i), a = this.fragments[r];
    if (!a || a.buffered && i.gap)
      return;
    const o = !i.relurl;
    Object.keys(t).forEach((l) => {
      const c = i.elementaryStreams[l];
      if (!c)
        return;
      const h = t[l], u = o || c.partial === !0;
      a.range[l] = this.getBufferedTimes(i, n, u, h);
    }), a.loaded = null, Object.keys(a.range).length ? (a.buffered = !0, (a.body.endList = i.endList || a.body.endList) && (this.endListFragments[a.body.type] = a), a3(a) || this.removeParts(i.sn - 1, i.type)) : this.removeFragment(a.body);
  }
  removeParts(e, t) {
    const i = this.activePartLists[t];
    i && (this.activePartLists[t] = i.filter((n) => n.fragment.sn >= e));
  }
  fragBuffered(e, t) {
    const i = Rd(e);
    let n = this.fragments[i];
    !n && t && (n = this.fragments[i] = {
      body: e,
      appendedPTS: null,
      loaded: null,
      buffered: !1,
      range: /* @__PURE__ */ Object.create(null)
    }, e.gap && (this.hasGaps = !0)), n && (n.loaded = null, n.buffered = !0);
  }
  getBufferedTimes(e, t, i, n) {
    const r = {
      time: [],
      partial: i
    }, a = e.start, o = e.end, l = e.minEndPTS || o, c = e.maxStartPTS || a;
    for (let h = 0; h < n.length; h++) {
      const u = n.start(h) - this.bufferPadding, d = n.end(h) + this.bufferPadding;
      if (c >= u && l <= d) {
        r.time.push({
          startPTS: Math.max(a, n.start(h)),
          endPTS: Math.min(o, n.end(h))
        });
        break;
      } else if (a < d && o > u) {
        const f = Math.max(a, n.start(h)), p = Math.min(o, n.end(h));
        p > f && (r.partial = !0, r.time.push({
          startPTS: f,
          endPTS: p
        }));
      } else if (o <= u)
        break;
    }
    return r;
  }
  /**
   * Gets the partial fragment for a certain time
   */
  getPartialFragment(e) {
    let t = null, i, n, r, a = 0;
    const {
      bufferPadding: o,
      fragments: l
    } = this;
    return Object.keys(l).forEach((c) => {
      const h = l[c];
      h && a3(h) && (n = h.body.start - o, r = h.body.end + o, e >= n && e <= r && (i = Math.min(e - n, r - e), a <= i && (t = h.body, a = i)));
    }), t;
  }
  isEndListAppended(e) {
    const t = this.endListFragments[e];
    return t !== void 0 && (t.buffered || a3(t));
  }
  getState(e) {
    const t = Rd(e), i = this.fragments[t];
    return i ? i.buffered ? a3(i) ? Ts.PARTIAL : Ts.OK : Ts.APPENDING : Ts.NOT_LOADED;
  }
  isTimeBuffered(e, t, i) {
    let n, r;
    for (let a = 0; a < i.length; a++) {
      if (n = i.start(a) - this.bufferPadding, r = i.end(a) + this.bufferPadding, e >= n && t <= r)
        return !0;
      if (t <= n)
        return !1;
    }
    return !1;
  }
  onFragLoaded(e, t) {
    const {
      frag: i,
      part: n
    } = t;
    if (i.sn === "initSegment" || i.bitrateTest)
      return;
    const r = n ? null : t, a = Rd(i);
    this.fragments[a] = {
      body: i,
      appendedPTS: null,
      loaded: r,
      buffered: !1,
      range: /* @__PURE__ */ Object.create(null)
    };
  }
  onBufferAppended(e, t) {
    const {
      frag: i,
      part: n,
      timeRanges: r
    } = t;
    if (i.sn === "initSegment")
      return;
    const a = i.type;
    if (n) {
      let o = this.activePartLists[a];
      o || (this.activePartLists[a] = o = []), o.push(n);
    }
    this.timeRanges = r, Object.keys(r).forEach((o) => {
      const l = r[o];
      this.detectEvictedFragments(o, l, a, n);
    });
  }
  onFragBuffered(e, t) {
    this.detectPartialFragments(t);
  }
  hasFragment(e) {
    const t = Rd(e);
    return !!this.fragments[t];
  }
  hasParts(e) {
    var t;
    return !!((t = this.activePartLists[e]) != null && t.length);
  }
  removeFragmentsInRange(e, t, i, n, r) {
    n && !this.hasGaps || Object.keys(this.fragments).forEach((a) => {
      const o = this.fragments[a];
      if (!o)
        return;
      const l = o.body;
      l.type !== i || n && !l.gap || l.start < t && l.end > e && (o.buffered || r) && this.removeFragment(l);
    });
  }
  removeFragment(e) {
    const t = Rd(e);
    e.stats.loaded = 0, e.clearElementaryStreamInfo();
    const i = this.activePartLists[e.type];
    if (i) {
      const n = e.sn;
      this.activePartLists[e.type] = i.filter((r) => r.fragment.sn !== n);
    }
    delete this.fragments[t], e.endList && delete this.endListFragments[e.type];
  }
  removeAllFragments() {
    this.fragments = /* @__PURE__ */ Object.create(null), this.endListFragments = /* @__PURE__ */ Object.create(null), this.activePartLists = /* @__PURE__ */ Object.create(null), this.hasGaps = !1;
  }
}
function a3(s) {
  var e, t, i;
  return s.buffered && (s.body.gap || ((e = s.range.video) == null ? void 0 : e.partial) || ((t = s.range.audio) == null ? void 0 : t.partial) || ((i = s.range.audiovideo) == null ? void 0 : i.partial));
}
function Rd(s) {
  return `${s.type}_${s.level}_${s.sn}`;
}
const mre = {
  length: 0,
  start: () => 0,
  end: () => 0
};
class Mn {
  /**
   * Return true if `media`'s buffered include `position`
   */
  static isBuffered(e, t) {
    try {
      if (e) {
        const i = Mn.getBuffered(e);
        for (let n = 0; n < i.length; n++)
          if (t >= i.start(n) && t <= i.end(n))
            return !0;
      }
    } catch {
    }
    return !1;
  }
  static bufferInfo(e, t, i) {
    try {
      if (e) {
        const n = Mn.getBuffered(e), r = [];
        let a;
        for (a = 0; a < n.length; a++)
          r.push({
            start: n.start(a),
            end: n.end(a)
          });
        return this.bufferedInfo(r, t, i);
      }
    } catch {
    }
    return {
      len: 0,
      start: t,
      end: t,
      nextStart: void 0
    };
  }
  static bufferedInfo(e, t, i) {
    t = Math.max(0, t), e.sort(function(c, h) {
      const u = c.start - h.start;
      return u || h.end - c.end;
    });
    let n = [];
    if (i)
      for (let c = 0; c < e.length; c++) {
        const h = n.length;
        if (h) {
          const u = n[h - 1].end;
          e[c].start - u < i ? e[c].end > u && (n[h - 1].end = e[c].end) : n.push(e[c]);
        } else
          n.push(e[c]);
      }
    else
      n = e;
    let r = 0, a, o = t, l = t;
    for (let c = 0; c < n.length; c++) {
      const h = n[c].start, u = n[c].end;
      if (t + i >= h && t < u)
        o = h, l = u, r = l - t;
      else if (t + i < h) {
        a = h;
        break;
      }
    }
    return {
      len: r,
      start: o || 0,
      end: l || 0,
      nextStart: a
    };
  }
  /**
   * Safe method to get buffered property.
   * SourceBuffer.buffered may throw if SourceBuffer is removed from it's MediaSource
   */
  static getBuffered(e) {
    try {
      return e.buffered;
    } catch (t) {
      return ze.log("failed to get media.buffered", t), mre;
    }
  }
}
class QS {
  constructor(e, t, i, n = 0, r = -1, a = !1) {
    this.level = void 0, this.sn = void 0, this.part = void 0, this.id = void 0, this.size = void 0, this.partial = void 0, this.transmuxing = o3(), this.buffering = {
      audio: o3(),
      video: o3(),
      audiovideo: o3()
    }, this.level = e, this.sn = t, this.id = i, this.size = n, this.part = r, this.partial = a;
  }
}
function o3() {
  return {
    start: 0,
    executeStart: 0,
    executeEnd: 0,
    end: 0
  };
}
function Uy(s, e) {
  for (let i = 0, n = s.length; i < n; i++) {
    var t;
    if (((t = s[i]) == null ? void 0 : t.cc) === e)
      return s[i];
  }
  return null;
}
function gre(s, e, t) {
  return !!(e && (t.endCC > t.startCC || s && s.cc < t.startCC));
}
function vre(s, e) {
  const t = s.fragments, i = e.fragments;
  if (!i.length || !t.length) {
    ze.log("No fragments to align");
    return;
  }
  const n = Uy(t, i[0].cc);
  if (!n || n && !n.startPTS) {
    ze.log("No frag in previous level to align on");
    return;
  }
  return n;
}
function d_(s, e) {
  if (s) {
    const t = s.start + e;
    s.start = s.startPTS = t, s.endPTS = t + s.duration;
  }
}
function HN(s, e) {
  const t = e.fragments;
  for (let i = 0, n = t.length; i < n; i++)
    d_(t[i], s);
  e.fragmentHint && d_(e.fragmentHint, s), e.alignedSliding = !0;
}
function yre(s, e, t) {
  e && (xre(s, t, e), !t.alignedSliding && e && bx(t, e), !t.alignedSliding && e && !t.skippedSegments && FN(e, t));
}
function xre(s, e, t) {
  if (gre(s, t, e)) {
    const i = vre(t, e);
    i && Bt(i.start) && (ze.log(`Adjusting PTS using last level due to CC increase within current level ${e.url}`), HN(i.start, e));
  }
}
function bx(s, e) {
  if (!s.hasProgramDateTime || !e.hasProgramDateTime)
    return;
  const t = s.fragments, i = e.fragments;
  if (!t.length || !i.length)
    return;
  let n, r;
  const a = Math.min(e.endCC, s.endCC);
  e.startCC < a && s.startCC < a && (n = Uy(i, a), r = Uy(t, a)), (!n || !r) && (n = i[Math.floor(i.length / 2)], r = Uy(t, n.cc) || t[Math.floor(t.length / 2)]);
  const o = n.programDateTime, l = r.programDateTime;
  if (!o || !l)
    return;
  const c = (l - o) / 1e3 - (r.start - n.start);
  HN(c, s);
}
const f_ = Math.pow(2, 17);
class Are {
  constructor(e) {
    this.config = void 0, this.loader = null, this.partLoadTimeout = -1, this.config = e;
  }
  destroy() {
    this.loader && (this.loader.destroy(), this.loader = null);
  }
  abort() {
    this.loader && this.loader.abort();
  }
  load(e, t) {
    const i = e.url;
    if (!i)
      return Promise.reject(new q0({
        type: qt.NETWORK_ERROR,
        details: Xe.FRAG_LOAD_ERROR,
        fatal: !1,
        frag: e,
        error: new Error(`Fragment does not have a ${i ? "part list" : "url"}`),
        networkDetails: null
      }));
    this.abort();
    const n = this.config, r = n.fLoader, a = n.loader;
    return new Promise((o, l) => {
      if (this.loader && this.loader.destroy(), e.gap)
        if (e.tagList.some((f) => f[0] === "GAP")) {
          l(m_(e));
          return;
        } else
          e.gap = !1;
      const c = this.loader = e.loader = r ? new r(n) : new a(n), h = p_(e), u = c_(n.fragLoadPolicy.default), d = {
        loadPolicy: u,
        timeout: u.maxLoadTimeMs,
        maxRetry: 0,
        retryDelay: 0,
        maxRetryDelay: 0,
        highWaterMark: e.sn === "initSegment" ? 1 / 0 : f_
      };
      e.stats = c.stats, c.load(h, d, {
        onSuccess: (f, p, v, g) => {
          this.resetLoader(e, c);
          let m = f.data;
          v.resetIV && e.decryptdata && (e.decryptdata.iv = new Uint8Array(m.slice(0, 16)), m = m.slice(16)), o({
            frag: e,
            part: null,
            payload: m,
            networkDetails: g
          });
        },
        onError: (f, p, v, g) => {
          this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.FRAG_LOAD_ERROR,
            fatal: !1,
            frag: e,
            response: Ls({
              url: i,
              data: void 0
            }, f),
            error: new Error(`HTTP Error ${f.code} ${f.text}`),
            networkDetails: v,
            stats: g
          }));
        },
        onAbort: (f, p, v) => {
          this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.INTERNAL_ABORTED,
            fatal: !1,
            frag: e,
            error: new Error("Aborted"),
            networkDetails: v,
            stats: f
          }));
        },
        onTimeout: (f, p, v) => {
          this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.FRAG_LOAD_TIMEOUT,
            fatal: !1,
            frag: e,
            error: new Error(`Timeout after ${d.timeout}ms`),
            networkDetails: v,
            stats: f
          }));
        },
        onProgress: (f, p, v, g) => {
          t && t({
            frag: e,
            part: null,
            payload: v,
            networkDetails: g
          });
        }
      });
    });
  }
  loadPart(e, t, i) {
    this.abort();
    const n = this.config, r = n.fLoader, a = n.loader;
    return new Promise((o, l) => {
      if (this.loader && this.loader.destroy(), e.gap || t.gap) {
        l(m_(e, t));
        return;
      }
      const c = this.loader = e.loader = r ? new r(n) : new a(n), h = p_(e, t), u = c_(n.fragLoadPolicy.default), d = {
        loadPolicy: u,
        timeout: u.maxLoadTimeMs,
        maxRetry: 0,
        retryDelay: 0,
        maxRetryDelay: 0,
        highWaterMark: f_
      };
      t.stats = c.stats, c.load(h, d, {
        onSuccess: (f, p, v, g) => {
          this.resetLoader(e, c), this.updateStatsFromPart(e, t);
          const m = {
            frag: e,
            part: t,
            payload: f.data,
            networkDetails: g
          };
          i(m), o(m);
        },
        onError: (f, p, v, g) => {
          this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.FRAG_LOAD_ERROR,
            fatal: !1,
            frag: e,
            part: t,
            response: Ls({
              url: h.url,
              data: void 0
            }, f),
            error: new Error(`HTTP Error ${f.code} ${f.text}`),
            networkDetails: v,
            stats: g
          }));
        },
        onAbort: (f, p, v) => {
          e.stats.aborted = t.stats.aborted, this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.INTERNAL_ABORTED,
            fatal: !1,
            frag: e,
            part: t,
            error: new Error("Aborted"),
            networkDetails: v,
            stats: f
          }));
        },
        onTimeout: (f, p, v) => {
          this.resetLoader(e, c), l(new q0({
            type: qt.NETWORK_ERROR,
            details: Xe.FRAG_LOAD_TIMEOUT,
            fatal: !1,
            frag: e,
            part: t,
            error: new Error(`Timeout after ${d.timeout}ms`),
            networkDetails: v,
            stats: f
          }));
        }
      });
    });
  }
  updateStatsFromPart(e, t) {
    const i = e.stats, n = t.stats, r = n.total;
    if (i.loaded += n.loaded, r) {
      const l = Math.round(e.duration / t.duration), c = Math.min(Math.round(i.loaded / r), l), u = (l - c) * Math.round(i.loaded / c);
      i.total = i.loaded + u;
    } else
      i.total = Math.max(i.loaded, i.total);
    const a = i.loading, o = n.loading;
    a.start ? a.first += o.first - o.start : (a.start = o.start, a.first = o.first), a.end = o.end;
  }
  resetLoader(e, t) {
    e.loader = null, this.loader === t && (self.clearTimeout(this.partLoadTimeout), this.loader = null), t.destroy();
  }
}
function p_(s, e = null) {
  const t = e || s, i = {
    frag: s,
    part: e,
    responseType: "arraybuffer",
    url: t.url,
    headers: {},
    rangeStart: 0,
    rangeEnd: 0
  }, n = t.byteRangeStartOffset, r = t.byteRangeEndOffset;
  if (Bt(n) && Bt(r)) {
    var a;
    let o = n, l = r;
    if (s.sn === "initSegment" && ((a = s.decryptdata) == null ? void 0 : a.method) === "AES-128") {
      const c = r - n;
      c % 16 && (l = r + (16 - c % 16)), n !== 0 && (i.resetIV = !0, o = n - 16);
    }
    i.rangeStart = o, i.rangeEnd = l;
  }
  return i;
}
function m_(s, e) {
  const t = new Error(`GAP ${s.gap ? "tag" : "attribute"} found`), i = {
    type: qt.MEDIA_ERROR,
    details: Xe.FRAG_GAP,
    fatal: !1,
    frag: s,
    error: t,
    networkDetails: null
  };
  return e && (i.part = e), (e || s).stats.aborted = !0, new q0(i);
}
class q0 extends Error {
  constructor(e) {
    super(e.error.message), this.data = void 0, this.data = e;
  }
}
class bre {
  constructor(e, t) {
    this.subtle = void 0, this.aesIV = void 0, this.subtle = e, this.aesIV = t;
  }
  decrypt(e, t) {
    return this.subtle.decrypt({
      name: "AES-CBC",
      iv: this.aesIV
    }, t, e);
  }
}
class Mre {
  constructor(e, t) {
    this.subtle = void 0, this.key = void 0, this.subtle = e, this.key = t;
  }
  expandKey() {
    return this.subtle.importKey("raw", this.key, {
      name: "AES-CBC"
    }, !1, ["encrypt", "decrypt"]);
  }
}
function Sre(s) {
  const e = s.byteLength, t = e && new DataView(s.buffer).getUint8(e - 1);
  return t ? Eu(s, 0, e - t) : s;
}
class wre {
  constructor() {
    this.rcon = [0, 1, 2, 4, 8, 16, 32, 64, 128, 27, 54], this.subMix = [new Uint32Array(256), new Uint32Array(256), new Uint32Array(256), new Uint32Array(256)], this.invSubMix = [new Uint32Array(256), new Uint32Array(256), new Uint32Array(256), new Uint32Array(256)], this.sBox = new Uint32Array(256), this.invSBox = new Uint32Array(256), this.key = new Uint32Array(0), this.ksRows = 0, this.keySize = 0, this.keySchedule = void 0, this.invKeySchedule = void 0, this.initTable();
  }
  // Using view.getUint32() also swaps the byte order.
  uint8ArrayToUint32Array_(e) {
    const t = new DataView(e), i = new Uint32Array(4);
    for (let n = 0; n < 4; n++)
      i[n] = t.getUint32(n * 4);
    return i;
  }
  initTable() {
    const e = this.sBox, t = this.invSBox, i = this.subMix, n = i[0], r = i[1], a = i[2], o = i[3], l = this.invSubMix, c = l[0], h = l[1], u = l[2], d = l[3], f = new Uint32Array(256);
    let p = 0, v = 0, g = 0;
    for (g = 0; g < 256; g++)
      g < 128 ? f[g] = g << 1 : f[g] = g << 1 ^ 283;
    for (g = 0; g < 256; g++) {
      let m = v ^ v << 1 ^ v << 2 ^ v << 3 ^ v << 4;
      m = m >>> 8 ^ m & 255 ^ 99, e[p] = m, t[m] = p;
      const x = f[p], y = f[x], A = f[y];
      let b = f[m] * 257 ^ m * 16843008;
      n[p] = b << 24 | b >>> 8, r[p] = b << 16 | b >>> 16, a[p] = b << 8 | b >>> 24, o[p] = b, b = A * 16843009 ^ y * 65537 ^ x * 257 ^ p * 16843008, c[m] = b << 24 | b >>> 8, h[m] = b << 16 | b >>> 16, u[m] = b << 8 | b >>> 24, d[m] = b, p ? (p = x ^ f[f[f[A ^ x]]], v ^= f[f[v]]) : p = v = 1;
    }
  }
  expandKey(e) {
    const t = this.uint8ArrayToUint32Array_(e);
    let i = !0, n = 0;
    for (; n < t.length && i; )
      i = t[n] === this.key[n], n++;
    if (i)
      return;
    this.key = t;
    const r = this.keySize = t.length;
    if (r !== 4 && r !== 6 && r !== 8)
      throw new Error("Invalid aes key size=" + r);
    const a = this.ksRows = (r + 6 + 1) * 4;
    let o, l;
    const c = this.keySchedule = new Uint32Array(a), h = this.invKeySchedule = new Uint32Array(a), u = this.sBox, d = this.rcon, f = this.invSubMix, p = f[0], v = f[1], g = f[2], m = f[3];
    let x, y;
    for (o = 0; o < a; o++) {
      if (o < r) {
        x = c[o] = t[o];
        continue;
      }
      y = x, o % r === 0 ? (y = y << 8 | y >>> 24, y = u[y >>> 24] << 24 | u[y >>> 16 & 255] << 16 | u[y >>> 8 & 255] << 8 | u[y & 255], y ^= d[o / r | 0] << 24) : r > 6 && o % r === 4 && (y = u[y >>> 24] << 24 | u[y >>> 16 & 255] << 16 | u[y >>> 8 & 255] << 8 | u[y & 255]), c[o] = x = (c[o - r] ^ y) >>> 0;
    }
    for (l = 0; l < a; l++)
      o = a - l, l & 3 ? y = c[o] : y = c[o - 4], l < 4 || o <= 4 ? h[l] = y : h[l] = p[u[y >>> 24]] ^ v[u[y >>> 16 & 255]] ^ g[u[y >>> 8 & 255]] ^ m[u[y & 255]], h[l] = h[l] >>> 0;
  }
  // Adding this as a method greatly improves performance.
  networkToHostOrderSwap(e) {
    return e << 24 | (e & 65280) << 8 | (e & 16711680) >> 8 | e >>> 24;
  }
  decrypt(e, t, i) {
    const n = this.keySize + 6, r = this.invKeySchedule, a = this.invSBox, o = this.invSubMix, l = o[0], c = o[1], h = o[2], u = o[3], d = this.uint8ArrayToUint32Array_(i);
    let f = d[0], p = d[1], v = d[2], g = d[3];
    const m = new Int32Array(e), x = new Int32Array(m.length);
    let y, A, b, M, w, C, T, E, P, L, I, N, H, U;
    const W = this.networkToHostOrderSwap;
    for (; t < m.length; ) {
      for (P = W(m[t]), L = W(m[t + 1]), I = W(m[t + 2]), N = W(m[t + 3]), w = P ^ r[0], C = N ^ r[1], T = I ^ r[2], E = L ^ r[3], H = 4, U = 1; U < n; U++)
        y = l[w >>> 24] ^ c[C >> 16 & 255] ^ h[T >> 8 & 255] ^ u[E & 255] ^ r[H], A = l[C >>> 24] ^ c[T >> 16 & 255] ^ h[E >> 8 & 255] ^ u[w & 255] ^ r[H + 1], b = l[T >>> 24] ^ c[E >> 16 & 255] ^ h[w >> 8 & 255] ^ u[C & 255] ^ r[H + 2], M = l[E >>> 24] ^ c[w >> 16 & 255] ^ h[C >> 8 & 255] ^ u[T & 255] ^ r[H + 3], w = y, C = A, T = b, E = M, H = H + 4;
      y = a[w >>> 24] << 24 ^ a[C >> 16 & 255] << 16 ^ a[T >> 8 & 255] << 8 ^ a[E & 255] ^ r[H], A = a[C >>> 24] << 24 ^ a[T >> 16 & 255] << 16 ^ a[E >> 8 & 255] << 8 ^ a[w & 255] ^ r[H + 1], b = a[T >>> 24] << 24 ^ a[E >> 16 & 255] << 16 ^ a[w >> 8 & 255] << 8 ^ a[C & 255] ^ r[H + 2], M = a[E >>> 24] << 24 ^ a[w >> 16 & 255] << 16 ^ a[C >> 8 & 255] << 8 ^ a[T & 255] ^ r[H + 3], x[t] = W(y ^ f), x[t + 1] = W(M ^ p), x[t + 2] = W(b ^ v), x[t + 3] = W(A ^ g), f = P, p = L, v = I, g = N, t = t + 4;
    }
    return x.buffer;
  }
}
const Ere = 16;
class $S {
  constructor(e, {
    removePKCS7Padding: t = !0
  } = {}) {
    if (this.logEnabled = !0, this.removePKCS7Padding = void 0, this.subtle = null, this.softwareDecrypter = null, this.key = null, this.fastAesKey = null, this.remainderData = null, this.currentIV = null, this.currentResult = null, this.useSoftware = void 0, this.useSoftware = e.enableSoftwareAES, this.removePKCS7Padding = t, t)
      try {
        const i = self.crypto;
        i && (this.subtle = i.subtle || i.webkitSubtle);
      } catch {
      }
    this.subtle === null && (this.useSoftware = !0);
  }
  destroy() {
    this.subtle = null, this.softwareDecrypter = null, this.key = null, this.fastAesKey = null, this.remainderData = null, this.currentIV = null, this.currentResult = null;
  }
  isSync() {
    return this.useSoftware;
  }
  flush() {
    const {
      currentResult: e,
      remainderData: t
    } = this;
    if (!e || t)
      return this.reset(), null;
    const i = new Uint8Array(e);
    return this.reset(), this.removePKCS7Padding ? Sre(i) : i;
  }
  reset() {
    this.currentResult = null, this.currentIV = null, this.remainderData = null, this.softwareDecrypter && (this.softwareDecrypter = null);
  }
  decrypt(e, t, i) {
    return this.useSoftware ? new Promise((n, r) => {
      this.softwareDecrypt(new Uint8Array(e), t, i);
      const a = this.flush();
      a ? n(a.buffer) : r(new Error("[softwareDecrypt] Failed to decrypt data"));
    }) : this.webCryptoDecrypt(new Uint8Array(e), t, i);
  }
  // Software decryption is progressive. Progressive decryption may not return a result on each call. Any cached
  // data is handled in the flush() call
  softwareDecrypt(e, t, i) {
    const {
      currentIV: n,
      currentResult: r,
      remainderData: a
    } = this;
    this.logOnce("JS AES decrypt"), a && (e = Pa(a, e), this.remainderData = null);
    const o = this.getValidChunk(e);
    if (!o.length)
      return null;
    n && (i = n);
    let l = this.softwareDecrypter;
    l || (l = this.softwareDecrypter = new wre()), l.expandKey(t);
    const c = r;
    return this.currentResult = l.decrypt(o.buffer, 0, i), this.currentIV = Eu(o, -16).buffer, c || null;
  }
  webCryptoDecrypt(e, t, i) {
    const n = this.subtle;
    return (this.key !== t || !this.fastAesKey) && (this.key = t, this.fastAesKey = new Mre(n, t)), this.fastAesKey.expandKey().then((r) => n ? (this.logOnce("WebCrypto AES decrypt"), new bre(n, new Uint8Array(i)).decrypt(e.buffer, r)) : Promise.reject(new Error("web crypto not initialized"))).catch((r) => (ze.warn(`[decrypter]: WebCrypto Error, disable WebCrypto API, ${r.name}: ${r.message}`), this.onWebCryptoError(e, t, i)));
  }
  onWebCryptoError(e, t, i) {
    this.useSoftware = !0, this.logEnabled = !0, this.softwareDecrypt(e, t, i);
    const n = this.flush();
    if (n)
      return n.buffer;
    throw new Error("WebCrypto and softwareDecrypt: failed to decrypt data");
  }
  getValidChunk(e) {
    let t = e;
    const i = e.length - e.length % Ere;
    return i !== e.length && (t = Eu(e, 0, i), this.remainderData = Eu(e, i)), t;
  }
  logOnce(e) {
    this.logEnabled && (ze.log(`[decrypter]: ${e}`), this.logEnabled = !1);
  }
}
const Tre = {
  toString: function(s) {
    let e = "";
    const t = s.length;
    for (let i = 0; i < t; i++)
      e += `[${s.start(i).toFixed(3)}-${s.end(i).toFixed(3)}]`;
    return e;
  }
}, pt = {
  STOPPED: "STOPPED",
  IDLE: "IDLE",
  KEY_LOADING: "KEY_LOADING",
  FRAG_LOADING: "FRAG_LOADING",
  FRAG_LOADING_WAITING_RETRY: "FRAG_LOADING_WAITING_RETRY",
  WAITING_TRACK: "WAITING_TRACK",
  PARSING: "PARSING",
  PARSED: "PARSED",
  ENDED: "ENDED",
  ERROR: "ERROR",
  WAITING_INIT_PTS: "WAITING_INIT_PTS",
  WAITING_LEVEL: "WAITING_LEVEL"
};
class ew extends fre {
  constructor(e, t, i, n, r) {
    super(), this.hls = void 0, this.fragPrevious = null, this.fragCurrent = null, this.fragmentTracker = void 0, this.transmuxer = null, this._state = pt.STOPPED, this.playlistType = void 0, this.media = null, this.mediaBuffer = null, this.config = void 0, this.bitrateTest = !1, this.lastCurrentTime = 0, this.nextLoadPosition = 0, this.startPosition = 0, this.startTimeOffset = null, this.loadedmetadata = !1, this.retryDate = 0, this.levels = null, this.fragmentLoader = void 0, this.keyLoader = void 0, this.levelLastLoaded = null, this.startFragRequested = !1, this.decrypter = void 0, this.initPTS = [], this.onvseeking = null, this.onvended = null, this.logPrefix = "", this.log = void 0, this.warn = void 0, this.playlistType = r, this.logPrefix = n, this.log = ze.log.bind(ze, `${n}:`), this.warn = ze.warn.bind(ze, `${n}:`), this.hls = e, this.fragmentLoader = new Are(e.config), this.keyLoader = i, this.fragmentTracker = t, this.config = e.config, this.decrypter = new $S(e.config), e.on(j.MANIFEST_LOADED, this.onManifestLoaded, this);
  }
  doTick() {
    this.onTickEnd();
  }
  onTickEnd() {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startLoad(e) {
  }
  stopLoad() {
    this.fragmentLoader.abort(), this.keyLoader.abort(this.playlistType);
    const e = this.fragCurrent;
    e != null && e.loader && (e.abortRequests(), this.fragmentTracker.removeFragment(e)), this.resetTransmuxer(), this.fragCurrent = null, this.fragPrevious = null, this.clearInterval(), this.clearNextTick(), this.state = pt.STOPPED;
  }
  _streamEnded(e, t) {
    if (t.live || e.nextStart || !e.end || !this.media)
      return !1;
    const i = t.partList;
    if (i != null && i.length) {
      const r = i[i.length - 1];
      return Mn.isBuffered(this.media, r.start + r.duration / 2);
    }
    const n = t.fragments[t.fragments.length - 1].type;
    return this.fragmentTracker.isEndListAppended(n);
  }
  getLevelDetails() {
    if (this.levels && this.levelLastLoaded !== null) {
      var e;
      return (e = this.levelLastLoaded) == null ? void 0 : e.details;
    }
  }
  onMediaAttached(e, t) {
    const i = this.media = this.mediaBuffer = t.media;
    this.onvseeking = this.onMediaSeeking.bind(this), this.onvended = this.onMediaEnded.bind(this), i.addEventListener("seeking", this.onvseeking), i.addEventListener("ended", this.onvended);
    const n = this.config;
    this.levels && n.autoStartLoad && this.state === pt.STOPPED && this.startLoad(n.startPosition);
  }
  onMediaDetaching() {
    const e = this.media;
    e != null && e.ended && (this.log("MSE detaching and video ended, reset startPosition"), this.startPosition = this.lastCurrentTime = 0), e && this.onvseeking && this.onvended && (e.removeEventListener("seeking", this.onvseeking), e.removeEventListener("ended", this.onvended), this.onvseeking = this.onvended = null), this.keyLoader && this.keyLoader.detach(), this.media = this.mediaBuffer = null, this.loadedmetadata = !1, this.fragmentTracker.removeAllFragments(), this.stopLoad();
  }
  onMediaSeeking() {
    const {
      config: e,
      fragCurrent: t,
      media: i,
      mediaBuffer: n,
      state: r
    } = this, a = i ? i.currentTime : 0, o = Mn.bufferInfo(n || i, a, e.maxBufferHole);
    if (this.log(`media seeking to ${Bt(a) ? a.toFixed(3) : a}, state: ${r}`), this.state === pt.ENDED)
      this.resetLoadingState();
    else if (t) {
      const l = e.maxFragLookUpTolerance, c = t.start - l, h = t.start + t.duration + l;
      if (!o.len || h < o.start || c > o.end) {
        const u = a > h;
        (a < c || u) && (u && t.loader && (this.log("seeking outside of buffer while fragment load in progress, cancel fragment load"), t.abortRequests(), this.resetLoadingState()), this.fragPrevious = null);
      }
    }
    i && (this.fragmentTracker.removeFragmentsInRange(a, 1 / 0, this.playlistType, !0), this.lastCurrentTime = a), !this.loadedmetadata && !o.len && (this.nextLoadPosition = this.startPosition = a), this.tickImmediate();
  }
  onMediaEnded() {
    this.startPosition = this.lastCurrentTime = 0;
  }
  onManifestLoaded(e, t) {
    this.startTimeOffset = t.startTimeOffset, this.initPTS = [];
  }
  onHandlerDestroying() {
    this.hls.off(j.MANIFEST_LOADED, this.onManifestLoaded, this), this.stopLoad(), super.onHandlerDestroying(), this.hls = null;
  }
  onHandlerDestroyed() {
    this.state = pt.STOPPED, this.fragmentLoader && this.fragmentLoader.destroy(), this.keyLoader && this.keyLoader.destroy(), this.decrypter && this.decrypter.destroy(), this.hls = this.log = this.warn = this.decrypter = this.keyLoader = this.fragmentLoader = this.fragmentTracker = null, super.onHandlerDestroyed();
  }
  loadFragment(e, t, i) {
    this._loadFragForPlayback(e, t, i);
  }
  _loadFragForPlayback(e, t, i) {
    const n = (r) => {
      if (this.fragContextChanged(e)) {
        this.warn(`Fragment ${e.sn}${r.part ? " p: " + r.part.index : ""} of level ${e.level} was dropped during download.`), this.fragmentTracker.removeFragment(e);
        return;
      }
      e.stats.chunkCount++, this._handleFragmentLoadProgress(r);
    };
    this._doFragLoad(e, t, i, n).then((r) => {
      if (!r)
        return;
      const a = this.state;
      if (this.fragContextChanged(e)) {
        (a === pt.FRAG_LOADING || !this.fragCurrent && a === pt.PARSING) && (this.fragmentTracker.removeFragment(e), this.state = pt.IDLE);
        return;
      }
      "payload" in r && (this.log(`Loaded fragment ${e.sn} of level ${e.level}`), this.hls.trigger(j.FRAG_LOADED, r)), this._handleFragmentLoadComplete(r);
    }).catch((r) => {
      this.state === pt.STOPPED || this.state === pt.ERROR || (this.warn(r), this.resetFragmentLoading(e));
    });
  }
  clearTrackerIfNeeded(e) {
    var t;
    const {
      fragmentTracker: i
    } = this;
    if (i.getState(e) === Ts.APPENDING) {
      const r = e.type, a = this.getFwdBufferInfo(this.mediaBuffer, r), o = Math.max(e.duration, a ? a.len : this.config.maxBufferLength);
      this.reduceMaxBufferLength(o) && i.removeFragment(e);
    } else
      ((t = this.mediaBuffer) == null ? void 0 : t.buffered.length) === 0 ? i.removeAllFragments() : i.hasParts(e.type) && (i.detectPartialFragments({
        frag: e,
        part: null,
        stats: e.stats,
        id: e.type
      }), i.getState(e) === Ts.PARTIAL && i.removeFragment(e));
  }
  checkLiveUpdate(e) {
    if (e.updated && !e.live) {
      const t = e.fragments[e.fragments.length - 1];
      this.fragmentTracker.detectPartialFragments({
        frag: t,
        part: null,
        stats: t.stats,
        id: t.type
      });
    }
    e.fragments[0] || (e.deltaUpdateFailed = !0);
  }
  flushMainBuffer(e, t, i = null) {
    if (!(e - t))
      return;
    const n = {
      startOffset: e,
      endOffset: t,
      type: i
    };
    this.hls.trigger(j.BUFFER_FLUSHING, n);
  }
  _loadInitSegment(e, t) {
    this._doFragLoad(e, t).then((i) => {
      if (!i || this.fragContextChanged(e) || !this.levels)
        throw new Error("init load aborted");
      return i;
    }).then((i) => {
      const {
        hls: n
      } = this, {
        payload: r
      } = i, a = e.decryptdata;
      if (r && r.byteLength > 0 && a != null && a.key && a.iv && a.method === "AES-128") {
        const o = self.performance.now();
        return this.decrypter.decrypt(new Uint8Array(r), a.key.buffer, a.iv.buffer).catch((l) => {
          throw n.trigger(j.ERROR, {
            type: qt.MEDIA_ERROR,
            details: Xe.FRAG_DECRYPT_ERROR,
            fatal: !1,
            error: l,
            reason: l.message,
            frag: e
          }), l;
        }).then((l) => {
          const c = self.performance.now();
          return n.trigger(j.FRAG_DECRYPTED, {
            frag: e,
            payload: l,
            stats: {
              tstart: o,
              tdecrypt: c
            }
          }), i.payload = l, this.completeInitSegmentLoad(i);
        });
      }
      return this.completeInitSegmentLoad(i);
    }).catch((i) => {
      this.state === pt.STOPPED || this.state === pt.ERROR || (this.warn(i), this.resetFragmentLoading(e));
    });
  }
  completeInitSegmentLoad(e) {
    const {
      levels: t
    } = this;
    if (!t)
      throw new Error("init load aborted, missing levels");
    const i = e.frag.stats;
    this.state = pt.IDLE, e.frag.data = new Uint8Array(e.payload), i.parsing.start = i.buffering.start = self.performance.now(), i.parsing.end = i.buffering.end = self.performance.now(), this.tick();
  }
  fragContextChanged(e) {
    const {
      fragCurrent: t
    } = this;
    return !e || !t || e.sn !== t.sn || e.level !== t.level;
  }
  fragBufferedComplete(e, t) {
    var i, n, r, a;
    const o = this.mediaBuffer ? this.mediaBuffer : this.media;
    if (this.log(`Buffered ${e.type} sn: ${e.sn}${t ? " part: " + t.index : ""} of ${this.playlistType === jt.MAIN ? "level" : "track"} ${e.level} (frag:[${((i = e.startPTS) != null ? i : NaN).toFixed(3)}-${((n = e.endPTS) != null ? n : NaN).toFixed(3)}] > buffer:${o ? Tre.toString(Mn.getBuffered(o)) : "(detached)"})`), e.sn !== "initSegment") {
      var l;
      if (e.type !== jt.SUBTITLE) {
        const h = e.elementaryStreams;
        if (!Object.keys(h).some((u) => !!h[u])) {
          this.state = pt.IDLE;
          return;
        }
      }
      const c = (l = this.levels) == null ? void 0 : l[e.level];
      c != null && c.fragmentError && (this.log(`Resetting level fragment error count of ${c.fragmentError} on frag buffered`), c.fragmentError = 0);
    }
    this.state = pt.IDLE, o && (!this.loadedmetadata && e.type == jt.MAIN && o.buffered.length && ((r = this.fragCurrent) == null ? void 0 : r.sn) === ((a = this.fragPrevious) == null ? void 0 : a.sn) && (this.loadedmetadata = !0, this.seekToStartPos()), this.tick());
  }
  seekToStartPos() {
  }
  _handleFragmentLoadComplete(e) {
    const {
      transmuxer: t
    } = this;
    if (!t)
      return;
    const {
      frag: i,
      part: n,
      partsLoaded: r
    } = e, a = !r || r.length === 0 || r.some((l) => !l), o = new QS(i.level, i.sn, i.stats.chunkCount + 1, 0, n ? n.index : -1, !a);
    t.flush(o);
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _handleFragmentLoadProgress(e) {
  }
  _doFragLoad(e, t, i = null, n) {
    var r;
    const a = t == null ? void 0 : t.details;
    if (!this.levels || !a)
      throw new Error(`frag load aborted, missing level${a ? "" : " detail"}s`);
    let o = null;
    if (e.encrypted && !((r = e.decryptdata) != null && r.key) ? (this.log(`Loading key for ${e.sn} of [${a.startSN}-${a.endSN}], ${this.logPrefix === "[stream-controller]" ? "level" : "track"} ${e.level}`), this.state = pt.KEY_LOADING, this.fragCurrent = e, o = this.keyLoader.load(e).then((h) => {
      if (!this.fragContextChanged(h.frag))
        return this.hls.trigger(j.KEY_LOADED, h), this.state === pt.KEY_LOADING && (this.state = pt.IDLE), h;
    }), this.hls.trigger(j.KEY_LOADING, {
      frag: e
    }), this.fragCurrent === null && (o = Promise.reject(new Error("frag load aborted, context changed in KEY_LOADING")))) : !e.encrypted && a.encryptedFragments.length && this.keyLoader.loadClear(e, a.encryptedFragments), i = Math.max(e.start, i || 0), this.config.lowLatencyMode && e.sn !== "initSegment") {
      const h = a.partList;
      if (h && n) {
        i > e.end && a.fragmentHint && (e = a.fragmentHint);
        const u = this.getNextPart(h, e, i);
        if (u > -1) {
          const d = h[u];
          this.log(`Loading part sn: ${e.sn} p: ${d.index} cc: ${e.cc} of playlist [${a.startSN}-${a.endSN}] parts [0-${u}-${h.length - 1}] ${this.logPrefix === "[stream-controller]" ? "level" : "track"}: ${e.level}, target: ${parseFloat(i.toFixed(3))}`), this.nextLoadPosition = d.start + d.duration, this.state = pt.FRAG_LOADING;
          let f;
          return o ? f = o.then((p) => !p || this.fragContextChanged(p.frag) ? null : this.doFragPartsLoad(e, d, t, n)).catch((p) => this.handleFragLoadError(p)) : f = this.doFragPartsLoad(e, d, t, n).catch((p) => this.handleFragLoadError(p)), this.hls.trigger(j.FRAG_LOADING, {
            frag: e,
            part: d,
            targetBufferTime: i
          }), this.fragCurrent === null ? Promise.reject(new Error("frag load aborted, context changed in FRAG_LOADING parts")) : f;
        } else if (!e.url || this.loadedEndOfParts(h, i))
          return Promise.resolve(null);
      }
    }
    this.log(`Loading fragment ${e.sn} cc: ${e.cc} ${a ? "of [" + a.startSN + "-" + a.endSN + "] " : ""}${this.logPrefix === "[stream-controller]" ? "level" : "track"}: ${e.level}, target: ${parseFloat(i.toFixed(3))}`), Bt(e.sn) && !this.bitrateTest && (this.nextLoadPosition = e.start + e.duration), this.state = pt.FRAG_LOADING;
    const l = this.config.progressive;
    let c;
    return l && o ? c = o.then((h) => !h || this.fragContextChanged(h == null ? void 0 : h.frag) ? null : this.fragmentLoader.load(e, n)).catch((h) => this.handleFragLoadError(h)) : c = Promise.all([this.fragmentLoader.load(e, l ? n : void 0), o]).then(([h]) => (!l && h && n && n(h), h)).catch((h) => this.handleFragLoadError(h)), this.hls.trigger(j.FRAG_LOADING, {
      frag: e,
      targetBufferTime: i
    }), this.fragCurrent === null ? Promise.reject(new Error("frag load aborted, context changed in FRAG_LOADING")) : c;
  }
  doFragPartsLoad(e, t, i, n) {
    return new Promise((r, a) => {
      var o;
      const l = [], c = (o = i.details) == null ? void 0 : o.partList, h = (u) => {
        this.fragmentLoader.loadPart(e, u, n).then((d) => {
          l[u.index] = d;
          const f = d.part;
          this.hls.trigger(j.FRAG_LOADED, d);
          const p = o_(i, e.sn, u.index + 1) || UN(c, e.sn, u.index + 1);
          if (p)
            h(p);
          else
            return r({
              frag: e,
              part: f,
              partsLoaded: l
            });
        }).catch(a);
      };
      h(t);
    });
  }
  handleFragLoadError(e) {
    if ("data" in e) {
      const t = e.data;
      e.data && t.details === Xe.INTERNAL_ABORTED ? this.handleFragLoadAborted(t.frag, t.part) : this.hls.trigger(j.ERROR, t);
    } else
      this.hls.trigger(j.ERROR, {
        type: qt.OTHER_ERROR,
        details: Xe.INTERNAL_EXCEPTION,
        err: e,
        error: e,
        fatal: !0
      });
    return null;
  }
  _handleTransmuxerFlush(e) {
    const t = this.getCurrentContext(e);
    if (!t || this.state !== pt.PARSING) {
      !this.fragCurrent && this.state !== pt.STOPPED && this.state !== pt.ERROR && (this.state = pt.IDLE);
      return;
    }
    const {
      frag: i,
      part: n,
      level: r
    } = t, a = self.performance.now();
    i.stats.parsing.end = a, n && (n.stats.parsing.end = a), this.updateLevelTiming(i, n, r, e.partial);
  }
  getCurrentContext(e) {
    const {
      levels: t,
      fragCurrent: i
    } = this, {
      level: n,
      sn: r,
      part: a
    } = e;
    if (!(t != null && t[n]))
      return this.warn(`Levels object was unset while buffering fragment ${r} of level ${n}. The current chunk will not be buffered.`), null;
    const o = t[n], l = a > -1 ? o_(o, r, a) : null, c = l ? l.fragment : Zse(o, r, i);
    return c ? (i && i !== c && (c.stats = i.stats), {
      frag: c,
      part: l,
      level: o
    }) : null;
  }
  bufferFragmentData(e, t, i, n, r) {
    var a;
    if (!e || this.state !== pt.PARSING)
      return;
    const {
      data1: o,
      data2: l
    } = e;
    let c = o;
    if (o && l && (c = Pa(o, l)), !((a = c) != null && a.length))
      return;
    const h = {
      type: e.type,
      frag: t,
      part: i,
      chunkMeta: n,
      parent: t.type,
      data: c
    };
    if (this.hls.trigger(j.BUFFER_APPENDING, h), e.dropped && e.independent && !i) {
      if (r)
        return;
      this.flushBufferGap(t);
    }
  }
  flushBufferGap(e) {
    const t = this.media;
    if (!t)
      return;
    if (!Mn.isBuffered(t, t.currentTime)) {
      this.flushMainBuffer(0, e.start);
      return;
    }
    const i = t.currentTime, n = Mn.bufferInfo(t, i, 0), r = e.duration, a = Math.min(this.config.maxFragLookUpTolerance * 2, r * 0.25), o = Math.max(Math.min(e.start - a, n.end - a), i + a);
    e.start - o > a && this.flushMainBuffer(o, e.start);
  }
  getFwdBufferInfo(e, t) {
    const i = this.getLoadPosition();
    return Bt(i) ? this.getFwdBufferInfoAtPos(e, i, t) : null;
  }
  getFwdBufferInfoAtPos(e, t, i) {
    const {
      config: {
        maxBufferHole: n
      }
    } = this, r = Mn.bufferInfo(e, t, n);
    if (r.len === 0 && r.nextStart !== void 0) {
      const a = this.fragmentTracker.getBufferedFrag(t, i);
      if (a && r.nextStart < a.end)
        return Mn.bufferInfo(e, t, Math.max(r.nextStart, n));
    }
    return r;
  }
  getMaxBufferLength(e) {
    const {
      config: t
    } = this;
    let i;
    return e ? i = Math.max(8 * t.maxBufferSize / e, t.maxBufferLength) : i = t.maxBufferLength, Math.min(i, t.maxMaxBufferLength);
  }
  reduceMaxBufferLength(e) {
    const t = this.config, i = e || t.maxBufferLength;
    return t.maxMaxBufferLength >= i ? (t.maxMaxBufferLength /= 2, this.warn(`Reduce max buffer length to ${t.maxMaxBufferLength}s`), !0) : !1;
  }
  getAppendedFrag(e, t = jt.MAIN) {
    const i = this.fragmentTracker.getAppendedFrag(e, jt.MAIN);
    return i && "fragment" in i ? i.fragment : i;
  }
  getNextFragment(e, t) {
    const i = t.fragments, n = i.length;
    if (!n)
      return null;
    const {
      config: r
    } = this, a = i[0].start;
    let o;
    if (t.live) {
      const l = r.initialLiveManifestSize;
      if (n < l)
        return this.warn(`Not enough fragments to start playback (have: ${n}, need: ${l})`), null;
      (!t.PTSKnown && !this.startFragRequested && this.startPosition === -1 || e < a) && (o = this.getInitialLiveFragment(t, i), this.startPosition = this.nextLoadPosition = o ? this.hls.liveSyncPosition || o.start : e);
    } else
      e <= a && (o = i[0]);
    if (!o) {
      const l = r.lowLatencyMode ? t.partEnd : t.fragmentEnd;
      o = this.getFragmentAtPosition(e, l, t);
    }
    return this.mapToInitFragWhenRequired(o);
  }
  isLoopLoading(e, t) {
    const i = this.fragmentTracker.getState(e);
    return (i === Ts.OK || i === Ts.PARTIAL && !!e.gap) && this.nextLoadPosition > t;
  }
  getNextFragmentLoopLoading(e, t, i, n, r) {
    const a = e.gap, o = this.getNextFragment(this.nextLoadPosition, t);
    if (o === null)
      return o;
    if (e = o, a && e && !e.gap && i.nextStart) {
      const l = this.getFwdBufferInfoAtPos(this.mediaBuffer ? this.mediaBuffer : this.media, i.nextStart, n);
      if (l !== null && i.len + l.len >= r)
        return this.log(`buffer full after gaps in "${n}" playlist starting at sn: ${e.sn}`), null;
    }
    return e;
  }
  mapToInitFragWhenRequired(e) {
    return e != null && e.initSegment && !(e != null && e.initSegment.data) && !this.bitrateTest ? e.initSegment : e;
  }
  getNextPart(e, t, i) {
    let n = -1, r = !1, a = !0;
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o];
      if (a = a && !c.independent, n > -1 && i < c.start)
        break;
      const h = c.loaded;
      h ? n = -1 : (r || c.independent || a) && c.fragment === t && (n = o), r = h;
    }
    return n;
  }
  loadedEndOfParts(e, t) {
    const i = e[e.length - 1];
    return i && t > i.start && i.loaded;
  }
  /*
   This method is used find the best matching first fragment for a live playlist. This fragment is used to calculate the
   "sliding" of the playlist, which is its offset from the start of playback. After sliding we can compute the real
   start and end times for each fragment in the playlist (after which this method will not need to be called).
  */
  getInitialLiveFragment(e, t) {
    const i = this.fragPrevious;
    let n = null;
    if (i) {
      if (e.hasProgramDateTime && (this.log(`Live playlist, switching playlist, load frag with same PDT: ${i.programDateTime}`), n = Jse(t, i.endProgramDateTime, this.config.maxFragLookUpTolerance)), !n) {
        const r = i.sn + 1;
        if (r >= e.startSN && r <= e.endSN) {
          const a = t[r - e.startSN];
          i.cc === a.cc && (n = a, this.log(`Live playlist, switching playlist, load frag with next SN: ${n.sn}`));
        }
        n || (n = $se(t, i.cc), n && this.log(`Live playlist, switching playlist, load frag with same CC: ${n.sn}`));
      }
    } else {
      const r = this.hls.liveSyncPosition;
      r !== null && (n = this.getFragmentAtPosition(r, this.bitrateTest ? e.fragmentEnd : e.edge, e));
    }
    return n;
  }
  /*
  This method finds the best matching fragment given the provided position.
   */
  getFragmentAtPosition(e, t, i) {
    const {
      config: n
    } = this;
    let {
      fragPrevious: r
    } = this, {
      fragments: a,
      endSN: o
    } = i;
    const {
      fragmentHint: l
    } = i, c = n.maxFragLookUpTolerance, h = i.partList, u = !!(n.lowLatencyMode && h != null && h.length && l);
    u && l && !this.bitrateTest && (a = a.concat(l), o = l.sn);
    let d;
    if (e < t) {
      const f = e > t - c ? 0 : c;
      d = Ax(r, a, e, f);
    } else
      d = a[a.length - 1];
    if (d) {
      const f = d.sn - i.startSN, p = this.fragmentTracker.getState(d);
      if ((p === Ts.OK || p === Ts.PARTIAL && d.gap) && (r = d), r && d.sn === r.sn && (!u || h[0].fragment.sn > d.sn) && r && d.level === r.level) {
        const g = a[f + 1];
        d.sn < o && this.fragmentTracker.getState(g) !== Ts.OK ? d = g : d = null;
      }
    }
    return d;
  }
  synchronizeToLiveEdge(e) {
    const {
      config: t,
      media: i
    } = this;
    if (!i)
      return;
    const n = this.hls.liveSyncPosition, r = i.currentTime, a = e.fragments[0].start, o = e.edge, l = r >= a - t.maxFragLookUpTolerance && r <= o;
    if (n !== null && i.duration > n && (r < n || !l)) {
      const c = t.liveMaxLatencyDuration !== void 0 ? t.liveMaxLatencyDuration : t.liveMaxLatencyDurationCount * e.targetduration;
      (!l && i.readyState < 4 || r < o - c) && (this.loadedmetadata || (this.nextLoadPosition = n), i.readyState && (this.warn(`Playback: ${r.toFixed(3)} is located too far from the end of live sliding playlist: ${o}, reset currentTime to : ${n.toFixed(3)}`), i.currentTime = n));
    }
  }
  alignPlaylists(e, t, i) {
    const n = e.fragments.length;
    if (!n)
      return this.warn("No fragments in live playlist"), 0;
    const r = e.fragments[0].start, a = !t, o = e.alignedSliding && Bt(r);
    if (a || !o && !r) {
      const {
        fragPrevious: l
      } = this;
      yre(l, i, e);
      const c = e.fragments[0].start;
      return this.log(`Live playlist sliding: ${c.toFixed(2)} start-sn: ${t ? t.startSN : "na"}->${e.startSN} prev-sn: ${l ? l.sn : "na"} fragments: ${n}`), c;
    }
    return r;
  }
  waitForCdnTuneIn(e) {
    return e.live && e.canBlockReload && e.partTarget && e.tuneInGoal > Math.max(e.partHoldBack, e.partTarget * 3);
  }
  setStartPosition(e, t) {
    let i = this.startPosition;
    if (i < t && (i = -1), i === -1 || this.lastCurrentTime === -1) {
      const n = this.startTimeOffset !== null, r = n ? this.startTimeOffset : e.startTimeOffset;
      r !== null && Bt(r) ? (i = t + r, r < 0 && (i += e.totalduration), i = Math.min(Math.max(t, i), t + e.totalduration), this.log(`Start time offset ${r} found in ${n ? "multivariant" : "media"} playlist, adjust startPosition to ${i}`), this.startPosition = i) : e.live ? i = this.hls.liveSyncPosition || t : this.startPosition = i = 0, this.lastCurrentTime = i;
    }
    this.nextLoadPosition = i;
  }
  getLoadPosition() {
    const {
      media: e
    } = this;
    let t = 0;
    return this.loadedmetadata && e ? t = e.currentTime : this.nextLoadPosition && (t = this.nextLoadPosition), t;
  }
  handleFragLoadAborted(e, t) {
    this.transmuxer && e.sn !== "initSegment" && e.stats.aborted && (this.warn(`Fragment ${e.sn}${t ? " part " + t.index : ""} of level ${e.level} was aborted`), this.resetFragmentLoading(e));
  }
  resetFragmentLoading(e) {
    (!this.fragCurrent || !this.fragContextChanged(e) && this.state !== pt.FRAG_LOADING_WAITING_RETRY) && (this.state = pt.IDLE);
  }
  onFragmentOrKeyLoadError(e, t) {
    if (t.chunkMeta && !t.frag) {
      const h = this.getCurrentContext(t.chunkMeta);
      h && (t.frag = h.frag);
    }
    const i = t.frag;
    if (!i || i.type !== e || !this.levels)
      return;
    if (this.fragContextChanged(i)) {
      var n;
      this.warn(`Frag load error must match current frag to retry ${i.url} > ${(n = this.fragCurrent) == null ? void 0 : n.url}`);
      return;
    }
    const r = t.details === Xe.FRAG_GAP;
    r && this.fragmentTracker.fragBuffered(i, !0);
    const a = t.errorAction, {
      action: o,
      retryCount: l = 0,
      retryConfig: c
    } = a || {};
    if (a && o === Us.RetryRequest && c) {
      this.resetStartWhenNotLoaded(this.levelLastLoaded);
      const h = KS(c, l);
      this.warn(`Fragment ${i.sn} of ${e} ${i.level} errored with ${t.details}, retrying loading ${l + 1}/${c.maxNumRetry} in ${h}ms`), a.resolved = !0, this.retryDate = self.performance.now() + h, this.state = pt.FRAG_LOADING_WAITING_RETRY;
    } else if (c && a)
      if (this.resetFragmentErrors(e), l < c.maxNumRetry)
        !r && o !== Us.RemoveAlternatePermanently && (a.resolved = !0);
      else {
        ze.warn(`${t.details} reached or exceeded max retry (${l})`);
        return;
      }
    else
      (a == null ? void 0 : a.action) === Us.SendAlternateToPenaltyBox ? this.state = pt.WAITING_LEVEL : this.state = pt.ERROR;
    this.tickImmediate();
  }
  reduceLengthAndFlushBuffer(e) {
    if (this.state === pt.PARSING || this.state === pt.PARSED) {
      const t = e.parent, i = this.getFwdBufferInfo(this.mediaBuffer, t), n = i && i.len > 0.5;
      n && this.reduceMaxBufferLength(i.len);
      const r = !n;
      return r && this.warn(`Buffer full error while media.currentTime is not buffered, flush ${t} buffer`), e.frag && (this.fragmentTracker.removeFragment(e.frag), this.nextLoadPosition = e.frag.start), this.resetLoadingState(), r;
    }
    return !1;
  }
  resetFragmentErrors(e) {
    e === jt.AUDIO && (this.fragCurrent = null), this.loadedmetadata || (this.startFragRequested = !1), this.state !== pt.STOPPED && (this.state = pt.IDLE);
  }
  afterBufferFlushed(e, t, i) {
    if (!e)
      return;
    const n = Mn.getBuffered(e);
    this.fragmentTracker.detectEvictedFragments(t, n, i), this.state === pt.ENDED && this.resetLoadingState();
  }
  resetLoadingState() {
    this.log("Reset loading state"), this.fragCurrent = null, this.fragPrevious = null, this.state = pt.IDLE;
  }
  resetStartWhenNotLoaded(e) {
    if (!this.loadedmetadata) {
      this.startFragRequested = !1;
      const t = e ? e.details : null;
      t != null && t.live ? (this.startPosition = -1, this.setStartPosition(t, 0), this.resetLoadingState()) : this.nextLoadPosition = this.startPosition;
    }
  }
  resetWhenMissingContext(e) {
    this.warn(`The loading context changed while buffering fragment ${e.sn} of level ${e.level}. This chunk will not be buffered.`), this.removeUnbufferedFrags(), this.resetStartWhenNotLoaded(this.levelLastLoaded), this.resetLoadingState();
  }
  removeUnbufferedFrags(e = 0) {
    this.fragmentTracker.removeFragmentsInRange(e, 1 / 0, this.playlistType, !1, !0);
  }
  updateLevelTiming(e, t, i, n) {
    var r;
    const a = i.details;
    if (!a) {
      this.warn("level.details undefined");
      return;
    }
    if (!Object.keys(e.elementaryStreams).reduce((l, c) => {
      const h = e.elementaryStreams[c];
      if (h) {
        const u = h.endPTS - h.startPTS;
        if (u <= 0)
          return this.warn(`Could not parse fragment ${e.sn} ${c} duration reliably (${u})`), l || !1;
        const d = n ? 0 : BN(a, e, h.startPTS, h.endPTS, h.startDTS, h.endDTS);
        return this.hls.trigger(j.LEVEL_PTS_UPDATED, {
          details: a,
          level: i,
          drift: d,
          type: c,
          frag: e,
          start: h.startPTS,
          end: h.endPTS
        }), !0;
      }
      return l;
    }, !1) && ((r = this.transmuxer) == null ? void 0 : r.error) === null) {
      const l = new Error(`Found no media in fragment ${e.sn} of level ${e.level} resetting transmuxer to fallback to playlist timing`);
      if (i.fragmentError === 0 && (i.fragmentError++, e.gap = !0, this.fragmentTracker.removeFragment(e), this.fragmentTracker.fragBuffered(e, !0)), this.warn(l.message), this.hls.trigger(j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.FRAG_PARSING_ERROR,
        fatal: !1,
        error: l,
        frag: e,
        reason: `Found no media in msn ${e.sn} of level "${i.url}"`
      }), !this.hls)
        return;
      this.resetTransmuxer();
    }
    this.state = pt.PARSED, this.hls.trigger(j.FRAG_PARSED, {
      frag: e,
      part: t
    });
  }
  resetTransmuxer() {
    this.transmuxer && (this.transmuxer.destroy(), this.transmuxer = null);
  }
  recoverWorkerError(e) {
    e.event === "demuxerWorker" && (this.fragmentTracker.removeAllFragments(), this.resetTransmuxer(), this.resetStartWhenNotLoaded(this.levelLastLoaded), this.resetLoadingState());
  }
  set state(e) {
    const t = this._state;
    t !== e && (this._state = e, this.log(`${t}->${e}`));
  }
  get state() {
    return this._state;
  }
}
class VN {
  constructor() {
    this.chunks = [], this.dataLength = 0;
  }
  push(e) {
    this.chunks.push(e), this.dataLength += e.length;
  }
  flush() {
    const {
      chunks: e,
      dataLength: t
    } = this;
    let i;
    if (e.length)
      e.length === 1 ? i = e[0] : i = Cre(e, t);
    else
      return new Uint8Array(0);
    return this.reset(), i;
  }
  reset() {
    this.chunks.length = 0, this.dataLength = 0;
  }
}
function Cre(s, e) {
  const t = new Uint8Array(e);
  let i = 0;
  for (let n = 0; n < s.length; n++) {
    const r = s[n];
    t.set(r, i), i += r.length;
  }
  return t;
}
function _re() {
  return typeof __HLS_WORKER_BUNDLE__ == "function";
}
function Pre() {
  const s = new self.Blob([`var exports={};var module={exports:exports};function define(f){f()};define.amd=true;(${__HLS_WORKER_BUNDLE__.toString()})(true);`], {
    type: "text/javascript"
  }), e = self.URL.createObjectURL(s);
  return {
    worker: new self.Worker(e),
    objectURL: e
  };
}
function Rre(s) {
  const e = new self.URL(s, self.location.href).href;
  return {
    worker: new self.Worker(e),
    scriptURL: e
  };
}
function Bo(s = "", e = 9e4) {
  return {
    type: s,
    id: -1,
    pid: -1,
    inputTimeScale: e,
    sequenceNumber: -1,
    samples: [],
    dropped: 0
  };
}
class tw {
  constructor() {
    this._audioTrack = void 0, this._id3Track = void 0, this.frameIndex = 0, this.cachedData = null, this.basePTS = null, this.initPTS = null, this.lastPTS = null;
  }
  resetInitSegment(e, t, i, n) {
    this._id3Track = {
      type: "id3",
      id: 3,
      pid: -1,
      inputTimeScale: 9e4,
      sequenceNumber: 0,
      samples: [],
      dropped: 0
    };
  }
  resetTimeStamp(e) {
    this.initPTS = e, this.resetContiguity();
  }
  resetContiguity() {
    this.basePTS = null, this.lastPTS = null, this.frameIndex = 0;
  }
  canParse(e, t) {
    return !1;
  }
  appendFrame(e, t, i) {
  }
  // feed incoming data to the front of the parsing pipeline
  demux(e, t) {
    this.cachedData && (e = Pa(this.cachedData, e), this.cachedData = null);
    let i = Dg(e, 0), n = i ? i.length : 0, r;
    const a = this._audioTrack, o = this._id3Track, l = i ? ZS(i) : void 0, c = e.length;
    for ((this.basePTS === null || this.frameIndex === 0 && Bt(l)) && (this.basePTS = Dre(l, t, this.initPTS), this.lastPTS = this.basePTS), this.lastPTS === null && (this.lastPTS = this.basePTS), i && i.length > 0 && o.samples.push({
      pts: this.lastPTS,
      dts: this.lastPTS,
      data: i,
      type: Ka.audioId3,
      duration: Number.POSITIVE_INFINITY
    }); n < c; ) {
      if (this.canParse(e, n)) {
        const h = this.appendFrame(a, e, n);
        h ? (this.frameIndex++, this.lastPTS = h.sample.pts, n += h.length, r = n) : n = c;
      } else
        ese(e, n) ? (i = Dg(e, n), o.samples.push({
          pts: this.lastPTS,
          dts: this.lastPTS,
          data: i,
          type: Ka.audioId3,
          duration: Number.POSITIVE_INFINITY
        }), n += i.length, r = n) : n++;
      if (n === c && r !== c) {
        const h = Eu(e, r);
        this.cachedData ? this.cachedData = Pa(this.cachedData, h) : this.cachedData = h;
      }
    }
    return {
      audioTrack: a,
      videoTrack: Bo(),
      id3Track: o,
      textTrack: Bo()
    };
  }
  demuxSampleAes(e, t, i) {
    return Promise.reject(new Error(`[${this}] This demuxer does not support Sample-AES decryption`));
  }
  flush(e) {
    const t = this.cachedData;
    return t && (this.cachedData = null, this.demux(t, 0)), {
      audioTrack: this._audioTrack,
      videoTrack: Bo(),
      id3Track: this._id3Track,
      textTrack: Bo()
    };
  }
  destroy() {
  }
}
const Dre = (s, e, t) => {
  if (Bt(s))
    return s * 90;
  const i = t ? t.baseTime * 9e4 / t.timescale : 0;
  return e * 9e4 + i;
};
function Lre(s, e, t, i) {
  let n, r, a, o;
  const l = navigator.userAgent.toLowerCase(), c = i, h = [96e3, 88200, 64e3, 48e3, 44100, 32e3, 24e3, 22050, 16e3, 12e3, 11025, 8e3, 7350];
  n = ((e[t + 2] & 192) >>> 6) + 1;
  const u = (e[t + 2] & 60) >>> 2;
  if (u > h.length - 1) {
    const d = new Error(`invalid ADTS sampling index:${u}`);
    s.emit(j.ERROR, j.ERROR, {
      type: qt.MEDIA_ERROR,
      details: Xe.FRAG_PARSING_ERROR,
      fatal: !0,
      error: d,
      reason: d.message
    });
    return;
  }
  return a = (e[t + 2] & 1) << 2, a |= (e[t + 3] & 192) >>> 6, ze.log(`manifest codec:${i}, ADTS type:${n}, samplingIndex:${u}`), /firefox/i.test(l) ? u >= 6 ? (n = 5, o = new Array(4), r = u - 3) : (n = 2, o = new Array(2), r = u) : l.indexOf("android") !== -1 ? (n = 2, o = new Array(2), r = u) : (n = 5, o = new Array(4), i && (i.indexOf("mp4a.40.29") !== -1 || i.indexOf("mp4a.40.5") !== -1) || !i && u >= 6 ? r = u - 3 : ((i && i.indexOf("mp4a.40.2") !== -1 && (u >= 6 && a === 1 || /vivaldi/i.test(l)) || !i && a === 1) && (n = 2, o = new Array(2)), r = u)), o[0] = n << 3, o[0] |= (u & 14) >> 1, o[1] |= (u & 1) << 7, o[1] |= a << 3, n === 5 && (o[1] |= (r & 14) >> 1, o[2] = (r & 1) << 7, o[2] |= 8, o[3] = 0), {
    config: o,
    samplerate: h[u],
    channelCount: a,
    codec: "mp4a.40." + n,
    manifestCodec: c
  };
}
function WN(s, e) {
  return s[e] === 255 && (s[e + 1] & 246) === 240;
}
function XN(s, e) {
  return s[e + 1] & 1 ? 7 : 9;
}
function iw(s, e) {
  return (s[e + 3] & 3) << 11 | s[e + 4] << 3 | (s[e + 5] & 224) >>> 5;
}
function Ire(s, e) {
  return e + 5 < s.length;
}
function Mx(s, e) {
  return e + 1 < s.length && WN(s, e);
}
function Ore(s, e) {
  return Ire(s, e) && WN(s, e) && iw(s, e) <= s.length - e;
}
function Nre(s, e) {
  if (Mx(s, e)) {
    const t = XN(s, e);
    if (e + t >= s.length)
      return !1;
    const i = iw(s, e);
    if (i <= t)
      return !1;
    const n = e + i;
    return n === s.length || Mx(s, n);
  }
  return !1;
}
function jN(s, e, t, i, n) {
  if (!s.samplerate) {
    const r = Lre(e, t, i, n);
    if (!r)
      return;
    s.config = r.config, s.samplerate = r.samplerate, s.channelCount = r.channelCount, s.codec = r.codec, s.manifestCodec = r.manifestCodec, ze.log(`parsed codec:${s.codec}, rate:${r.samplerate}, channels:${r.channelCount}`);
  }
}
function qN(s) {
  return 1024 * 9e4 / s;
}
function Bre(s, e) {
  const t = XN(s, e);
  if (e + t <= s.length) {
    const i = iw(s, e) - t;
    if (i > 0)
      return {
        headerLength: t,
        frameLength: i
      };
  }
}
function YN(s, e, t, i, n) {
  const r = qN(s.samplerate), a = i + n * r, o = Bre(e, t);
  let l;
  if (o) {
    const {
      frameLength: u,
      headerLength: d
    } = o, f = d + u, p = Math.max(0, t + f - e.length);
    p ? (l = new Uint8Array(f - d), l.set(e.subarray(t + d, e.length), 0)) : l = e.subarray(t + d, t + f);
    const v = {
      unit: l,
      pts: a
    };
    return p || s.samples.push(v), {
      sample: v,
      length: f,
      missing: p
    };
  }
  const c = e.length - t;
  return l = new Uint8Array(c), l.set(e.subarray(t, e.length), 0), {
    sample: {
      unit: l,
      pts: a
    },
    length: c,
    missing: -1
  };
}
let l3 = null;
const Fre = [32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160], Ure = [44100, 48e3, 32e3, 22050, 24e3, 16e3, 11025, 12e3, 8e3], zre = [
  // MPEG 2.5
  [
    0,
    // Reserved
    72,
    // Layer3
    144,
    // Layer2
    12
    // Layer1
  ],
  // Reserved
  [
    0,
    // Reserved
    0,
    // Layer3
    0,
    // Layer2
    0
    // Layer1
  ],
  // MPEG 2
  [
    0,
    // Reserved
    72,
    // Layer3
    144,
    // Layer2
    12
    // Layer1
  ],
  // MPEG 1
  [
    0,
    // Reserved
    144,
    // Layer3
    144,
    // Layer2
    12
    // Layer1
  ]
], kre = [
  0,
  // Reserved
  1,
  // Layer3
  1,
  // Layer2
  4
  // Layer1
];
function ZN(s, e, t, i, n) {
  if (t + 24 > e.length)
    return;
  const r = KN(e, t);
  if (r && t + r.frameLength <= e.length) {
    const a = r.samplesPerFrame * 9e4 / r.sampleRate, o = i + n * a, l = {
      unit: e.subarray(t, t + r.frameLength),
      pts: o,
      dts: o
    };
    return s.config = [], s.channelCount = r.channelCount, s.samplerate = r.sampleRate, s.samples.push(l), {
      sample: l,
      length: r.frameLength,
      missing: 0
    };
  }
}
function KN(s, e) {
  const t = s[e + 1] >> 3 & 3, i = s[e + 1] >> 1 & 3, n = s[e + 2] >> 4 & 15, r = s[e + 2] >> 2 & 3;
  if (t !== 1 && n !== 0 && n !== 15 && r !== 3) {
    const a = s[e + 2] >> 1 & 1, o = s[e + 3] >> 6, l = t === 3 ? 3 - i : i === 3 ? 3 : 4, c = Fre[l * 14 + n - 1] * 1e3, u = Ure[(t === 3 ? 0 : t === 2 ? 1 : 2) * 3 + r], d = o === 3 ? 1 : 2, f = zre[t][i], p = kre[i], v = f * 8 * p, g = Math.floor(f * c / u + a) * p;
    if (l3 === null) {
      const y = (navigator.userAgent || "").match(/Chrome\/(\d+)/i);
      l3 = y ? parseInt(y[1]) : 0;
    }
    return !!l3 && l3 <= 87 && i === 2 && c >= 224e3 && o === 0 && (s[e + 3] = s[e + 3] | 128), {
      sampleRate: u,
      channelCount: d,
      frameLength: g,
      samplesPerFrame: v
    };
  }
}
function nw(s, e) {
  return s[e] === 255 && (s[e + 1] & 224) === 224 && (s[e + 1] & 6) !== 0;
}
function JN(s, e) {
  return e + 1 < s.length && nw(s, e);
}
function Gre(s, e) {
  return nw(s, e) && 4 <= s.length - e;
}
function QN(s, e) {
  if (e + 1 < s.length && nw(s, e)) {
    const i = KN(s, e);
    let n = 4;
    i != null && i.frameLength && (n = i.frameLength);
    const r = e + n;
    return r === s.length || JN(s, r);
  }
  return !1;
}
class Hre extends tw {
  constructor(e, t) {
    super(), this.observer = void 0, this.config = void 0, this.observer = e, this.config = t;
  }
  resetInitSegment(e, t, i, n) {
    super.resetInitSegment(e, t, i, n), this._audioTrack = {
      container: "audio/adts",
      type: "audio",
      id: 2,
      pid: -1,
      sequenceNumber: 0,
      segmentCodec: "aac",
      samples: [],
      manifestCodec: t,
      duration: n,
      inputTimeScale: 9e4,
      dropped: 0
    };
  }
  // Source for probe info - https://wiki.multimedia.cx/index.php?title=ADTS
  static probe(e) {
    if (!e)
      return !1;
    const t = Dg(e, 0);
    let i = (t == null ? void 0 : t.length) || 0;
    if (QN(e, i))
      return !1;
    for (let n = e.length; i < n; i++)
      if (Nre(e, i))
        return ze.log("ADTS sync word found !"), !0;
    return !1;
  }
  canParse(e, t) {
    return Ore(e, t);
  }
  appendFrame(e, t, i) {
    jN(e, this.observer, t, i, e.manifestCodec);
    const n = YN(e, t, i, this.basePTS, this.frameIndex);
    if (n && n.missing === 0)
      return n;
  }
}
const Vre = /\/emsg[-/]ID3/i;
class Wre {
  constructor(e, t) {
    this.remainderData = null, this.timeOffset = 0, this.config = void 0, this.videoTrack = void 0, this.audioTrack = void 0, this.id3Track = void 0, this.txtTrack = void 0, this.config = t;
  }
  resetTimeStamp() {
  }
  resetInitSegment(e, t, i, n) {
    const r = this.videoTrack = Bo("video", 1), a = this.audioTrack = Bo("audio", 1), o = this.txtTrack = Bo("text", 1);
    if (this.id3Track = Bo("id3", 1), this.timeOffset = 0, !(e != null && e.byteLength))
      return;
    const l = _N(e);
    if (l.video) {
      const {
        id: c,
        timescale: h,
        codec: u
      } = l.video;
      r.id = c, r.timescale = o.timescale = h, r.codec = u;
    }
    if (l.audio) {
      const {
        id: c,
        timescale: h,
        codec: u
      } = l.audio;
      a.id = c, a.timescale = h, a.codec = u;
    }
    o.id = EN.text, r.sampleDuration = 0, r.duration = a.duration = n;
  }
  resetContiguity() {
    this.remainderData = null;
  }
  static probe(e) {
    return cse(e);
  }
  demux(e, t) {
    this.timeOffset = t;
    let i = e;
    const n = this.videoTrack, r = this.txtTrack;
    if (this.config.progressive) {
      this.remainderData && (i = Pa(this.remainderData, e));
      const o = vse(i);
      this.remainderData = o.remainder, n.samples = o.valid || new Uint8Array();
    } else
      n.samples = i;
    const a = this.extractID3Track(n, t);
    return r.samples = HC(t, n), {
      videoTrack: n,
      audioTrack: this.audioTrack,
      id3Track: a,
      textTrack: this.txtTrack
    };
  }
  flush() {
    const e = this.timeOffset, t = this.videoTrack, i = this.txtTrack;
    t.samples = this.remainderData || new Uint8Array(), this.remainderData = null;
    const n = this.extractID3Track(t, this.timeOffset);
    return i.samples = HC(e, t), {
      videoTrack: t,
      audioTrack: Bo(),
      id3Track: n,
      textTrack: Bo()
    };
  }
  extractID3Track(e, t) {
    const i = this.id3Track;
    if (e.samples.length) {
      const n = li(e.samples, ["emsg"]);
      n && n.forEach((r) => {
        const a = Ase(r);
        if (Vre.test(a.schemeIdUri)) {
          const o = Bt(a.presentationTime) ? a.presentationTime / a.timeScale : t + a.presentationTimeDelta / a.timeScale;
          let l = a.eventDuration === 4294967295 ? Number.POSITIVE_INFINITY : a.eventDuration / a.timeScale;
          l <= 1e-3 && (l = Number.POSITIVE_INFINITY);
          const c = a.payload;
          i.samples.push({
            data: c,
            len: c.byteLength,
            dts: o,
            pts: o,
            type: Ka.emsg,
            duration: l
          });
        }
      });
    }
    return i;
  }
  demuxSampleAes(e, t, i) {
    return Promise.reject(new Error("The MP4 demuxer does not support SAMPLE-AES decryption"));
  }
  destroy() {
  }
}
const $N = (s, e) => {
  let t = 0, i = 5;
  e += i;
  const n = new Uint32Array(1), r = new Uint32Array(1), a = new Uint8Array(1);
  for (; i > 0; ) {
    a[0] = s[e];
    const o = Math.min(i, 8), l = 8 - o;
    r[0] = 4278190080 >>> 24 + l << l, n[0] = (a[0] & r[0]) >> l, t = t ? t << o | n[0] : n[0], e += 1, i -= o;
  }
  return t;
};
class Xre extends tw {
  constructor(e) {
    super(), this.observer = void 0, this.observer = e;
  }
  resetInitSegment(e, t, i, n) {
    super.resetInitSegment(e, t, i, n), this._audioTrack = {
      container: "audio/ac-3",
      type: "audio",
      id: 2,
      pid: -1,
      sequenceNumber: 0,
      segmentCodec: "ac3",
      samples: [],
      manifestCodec: t,
      duration: n,
      inputTimeScale: 9e4,
      dropped: 0
    };
  }
  canParse(e, t) {
    return t + 64 < e.length;
  }
  appendFrame(e, t, i) {
    const n = eB(e, t, i, this.basePTS, this.frameIndex);
    if (n !== -1)
      return {
        sample: e.samples[e.samples.length - 1],
        length: n,
        missing: 0
      };
  }
  static probe(e) {
    if (!e)
      return !1;
    const t = Dg(e, 0);
    if (!t)
      return !1;
    const i = t.length;
    return e[i] === 11 && e[i + 1] === 119 && ZS(t) !== void 0 && // check the bsid to confirm ac-3
    $N(e, i) < 16;
  }
}
function eB(s, e, t, i, n) {
  if (t + 8 > e.length || e[t] !== 11 || e[t + 1] !== 119)
    return -1;
  const r = e[t + 4] >> 6;
  if (r >= 3)
    return -1;
  const o = [48e3, 44100, 32e3][r], l = e[t + 4] & 63, h = [64, 69, 96, 64, 70, 96, 80, 87, 120, 80, 88, 120, 96, 104, 144, 96, 105, 144, 112, 121, 168, 112, 122, 168, 128, 139, 192, 128, 140, 192, 160, 174, 240, 160, 175, 240, 192, 208, 288, 192, 209, 288, 224, 243, 336, 224, 244, 336, 256, 278, 384, 256, 279, 384, 320, 348, 480, 320, 349, 480, 384, 417, 576, 384, 418, 576, 448, 487, 672, 448, 488, 672, 512, 557, 768, 512, 558, 768, 640, 696, 960, 640, 697, 960, 768, 835, 1152, 768, 836, 1152, 896, 975, 1344, 896, 976, 1344, 1024, 1114, 1536, 1024, 1115, 1536, 1152, 1253, 1728, 1152, 1254, 1728, 1280, 1393, 1920, 1280, 1394, 1920][l * 3 + r] * 2;
  if (t + h > e.length)
    return -1;
  const u = e[t + 6] >> 5;
  let d = 0;
  u === 2 ? d += 2 : (u & 1 && u !== 1 && (d += 2), u & 4 && (d += 2));
  const f = (e[t + 6] << 8 | e[t + 7]) >> 12 - d & 1, v = [2, 1, 2, 3, 3, 4, 4, 5][u] + f, g = e[t + 5] >> 3, m = e[t + 5] & 7, x = new Uint8Array([r << 6 | g << 1 | m >> 2, (m & 3) << 6 | u << 3 | f << 2 | l >> 4, l << 4 & 224]), y = 1536 / o * 9e4, A = i + n * y, b = e.subarray(t, t + h);
  return s.config = x, s.channelCount = v, s.samplerate = o, s.samples.push({
    unit: b,
    pts: A
  }), h;
}
class jre {
  constructor() {
    this.VideoSample = null;
  }
  createVideoSample(e, t, i, n) {
    return {
      key: e,
      frame: !1,
      pts: t,
      dts: i,
      units: [],
      debug: n,
      length: 0
    };
  }
  getLastNalUnit(e) {
    var t;
    let i = this.VideoSample, n;
    if ((!i || i.units.length === 0) && (i = e[e.length - 1]), (t = i) != null && t.units) {
      const r = i.units;
      n = r[r.length - 1];
    }
    return n;
  }
  pushAccessUnit(e, t) {
    if (e.units.length && e.frame) {
      if (e.pts === void 0) {
        const i = t.samples, n = i.length;
        if (n) {
          const r = i[n - 1];
          e.pts = r.pts, e.dts = r.dts;
        } else {
          t.dropped++;
          return;
        }
      }
      t.samples.push(e);
    }
    e.debug.length && ze.log(e.pts + "/" + e.dts + ":" + e.debug);
  }
}
class g_ {
  constructor(e) {
    this.data = void 0, this.bytesAvailable = void 0, this.word = void 0, this.bitsAvailable = void 0, this.data = e, this.bytesAvailable = e.byteLength, this.word = 0, this.bitsAvailable = 0;
  }
  // ():void
  loadWord() {
    const e = this.data, t = this.bytesAvailable, i = e.byteLength - t, n = new Uint8Array(4), r = Math.min(4, t);
    if (r === 0)
      throw new Error("no bytes available");
    n.set(e.subarray(i, i + r)), this.word = new DataView(n.buffer).getUint32(0), this.bitsAvailable = r * 8, this.bytesAvailable -= r;
  }
  // (count:int):void
  skipBits(e) {
    let t;
    e = Math.min(e, this.bytesAvailable * 8 + this.bitsAvailable), this.bitsAvailable > e ? (this.word <<= e, this.bitsAvailable -= e) : (e -= this.bitsAvailable, t = e >> 3, e -= t << 3, this.bytesAvailable -= t, this.loadWord(), this.word <<= e, this.bitsAvailable -= e);
  }
  // (size:int):uint
  readBits(e) {
    let t = Math.min(this.bitsAvailable, e);
    const i = this.word >>> 32 - t;
    if (e > 32 && ze.error("Cannot read more than 32 bits at a time"), this.bitsAvailable -= t, this.bitsAvailable > 0)
      this.word <<= t;
    else if (this.bytesAvailable > 0)
      this.loadWord();
    else
      throw new Error("no bits available");
    return t = e - t, t > 0 && this.bitsAvailable ? i << t | this.readBits(t) : i;
  }
  // ():uint
  skipLZ() {
    let e;
    for (e = 0; e < this.bitsAvailable; ++e)
      if (this.word & 2147483648 >>> e)
        return this.word <<= e, this.bitsAvailable -= e, e;
    return this.loadWord(), e + this.skipLZ();
  }
  // ():void
  skipUEG() {
    this.skipBits(1 + this.skipLZ());
  }
  // ():void
  skipEG() {
    this.skipBits(1 + this.skipLZ());
  }
  // ():uint
  readUEG() {
    const e = this.skipLZ();
    return this.readBits(e + 1) - 1;
  }
  // ():int
  readEG() {
    const e = this.readUEG();
    return 1 & e ? 1 + e >>> 1 : -1 * (e >>> 1);
  }
  // Some convenience functions
  // :Boolean
  readBoolean() {
    return this.readBits(1) === 1;
  }
  // ():int
  readUByte() {
    return this.readBits(8);
  }
  // ():int
  readUShort() {
    return this.readBits(16);
  }
  // ():int
  readUInt() {
    return this.readBits(32);
  }
  /**
   * Advance the ExpGolomb decoder past a scaling list. The scaling
   * list is optionally transmitted as part of a sequence parameter
   * set and is not relevant to transmuxing.
   * @param count the number of entries in this scaling list
   * @see Recommendation ITU-T H.264, Section 7.3.2.1.1.1
   */
  skipScalingList(e) {
    let t = 8, i = 8, n;
    for (let r = 0; r < e; r++)
      i !== 0 && (n = this.readEG(), i = (t + n + 256) % 256), t = i === 0 ? t : i;
  }
  /**
   * Read a sequence parameter set and return some interesting video
   * properties. A sequence parameter set is the H264 metadata that
   * describes the properties of upcoming video frames.
   * @returns an object with configuration parsed from the
   * sequence parameter set, including the dimensions of the
   * associated video frames.
   */
  readSPS() {
    let e = 0, t = 0, i = 0, n = 0, r, a, o;
    const l = this.readUByte.bind(this), c = this.readBits.bind(this), h = this.readUEG.bind(this), u = this.readBoolean.bind(this), d = this.skipBits.bind(this), f = this.skipEG.bind(this), p = this.skipUEG.bind(this), v = this.skipScalingList.bind(this);
    l();
    const g = l();
    if (c(5), d(3), l(), p(), g === 100 || g === 110 || g === 122 || g === 244 || g === 44 || g === 83 || g === 86 || g === 118 || g === 128) {
      const M = h();
      if (M === 3 && d(1), p(), p(), d(1), u())
        for (a = M !== 3 ? 8 : 12, o = 0; o < a; o++)
          u() && (o < 6 ? v(16) : v(64));
    }
    p();
    const m = h();
    if (m === 0)
      h();
    else if (m === 1)
      for (d(1), f(), f(), r = h(), o = 0; o < r; o++)
        f();
    p(), d(1);
    const x = h(), y = h(), A = c(1);
    A === 0 && d(1), d(1), u() && (e = h(), t = h(), i = h(), n = h());
    let b = [1, 1];
    if (u() && u())
      switch (l()) {
        case 1:
          b = [1, 1];
          break;
        case 2:
          b = [12, 11];
          break;
        case 3:
          b = [10, 11];
          break;
        case 4:
          b = [16, 11];
          break;
        case 5:
          b = [40, 33];
          break;
        case 6:
          b = [24, 11];
          break;
        case 7:
          b = [20, 11];
          break;
        case 8:
          b = [32, 11];
          break;
        case 9:
          b = [80, 33];
          break;
        case 10:
          b = [18, 11];
          break;
        case 11:
          b = [15, 11];
          break;
        case 12:
          b = [64, 33];
          break;
        case 13:
          b = [160, 99];
          break;
        case 14:
          b = [4, 3];
          break;
        case 15:
          b = [3, 2];
          break;
        case 16:
          b = [2, 1];
          break;
        case 255: {
          b = [l() << 8 | l(), l() << 8 | l()];
          break;
        }
      }
    return {
      width: Math.ceil((x + 1) * 16 - e * 2 - t * 2),
      height: (2 - A) * (y + 1) * 16 - (A ? 2 : 4) * (i + n),
      pixelRatio: b
    };
  }
  readSliceType() {
    return this.readUByte(), this.readUEG(), this.readUEG();
  }
}
class qre extends jre {
  parseAVCPES(e, t, i, n, r) {
    const a = this.parseAVCNALu(e, i.data);
    let o = this.VideoSample, l, c = !1;
    i.data = null, o && a.length && !e.audFound && (this.pushAccessUnit(o, e), o = this.VideoSample = this.createVideoSample(!1, i.pts, i.dts, "")), a.forEach((h) => {
      var u;
      switch (h.type) {
        case 1: {
          let v = !1;
          l = !0;
          const g = h.data;
          if (c && g.length > 4) {
            const m = new g_(g).readSliceType();
            (m === 2 || m === 4 || m === 7 || m === 9) && (v = !0);
          }
          if (v) {
            var d;
            (d = o) != null && d.frame && !o.key && (this.pushAccessUnit(o, e), o = this.VideoSample = null);
          }
          o || (o = this.VideoSample = this.createVideoSample(!0, i.pts, i.dts, "")), o.frame = !0, o.key = v;
          break;
        }
        case 5:
          l = !0, (u = o) != null && u.frame && !o.key && (this.pushAccessUnit(o, e), o = this.VideoSample = null), o || (o = this.VideoSample = this.createVideoSample(!0, i.pts, i.dts, "")), o.key = !0, o.frame = !0;
          break;
        case 6: {
          l = !0, RN(h.data, 1, i.pts, t.samples);
          break;
        }
        case 7: {
          var f, p;
          l = !0, c = !0;
          const v = h.data, m = new g_(v).readSPS();
          if (!e.sps || e.width !== m.width || e.height !== m.height || ((f = e.pixelRatio) == null ? void 0 : f[0]) !== m.pixelRatio[0] || ((p = e.pixelRatio) == null ? void 0 : p[1]) !== m.pixelRatio[1]) {
            e.width = m.width, e.height = m.height, e.pixelRatio = m.pixelRatio, e.sps = [v], e.duration = r;
            const x = v.subarray(1, 4);
            let y = "avc1.";
            for (let A = 0; A < 3; A++) {
              let b = x[A].toString(16);
              b.length < 2 && (b = "0" + b), y += b;
            }
            e.codec = y;
          }
          break;
        }
        case 8:
          l = !0, e.pps = [h.data];
          break;
        case 9:
          l = !0, e.audFound = !0, o && this.pushAccessUnit(o, e), o = this.VideoSample = this.createVideoSample(!1, i.pts, i.dts, "");
          break;
        case 12:
          l = !0;
          break;
        default:
          l = !1, o && (o.debug += "unknown NAL " + h.type + " ");
          break;
      }
      o && l && o.units.push(h);
    }), n && o && (this.pushAccessUnit(o, e), this.VideoSample = null);
  }
  parseAVCNALu(e, t) {
    const i = t.byteLength;
    let n = e.naluState || 0;
    const r = n, a = [];
    let o = 0, l, c, h, u = -1, d = 0;
    for (n === -1 && (u = 0, d = t[0] & 31, n = 0, o = 1); o < i; ) {
      if (l = t[o++], !n) {
        n = l ? 0 : 1;
        continue;
      }
      if (n === 1) {
        n = l ? 0 : 2;
        continue;
      }
      if (!l)
        n = 3;
      else if (l === 1) {
        if (c = o - n - 1, u >= 0) {
          const f = {
            data: t.subarray(u, c),
            type: d
          };
          a.push(f);
        } else {
          const f = this.getLastNalUnit(e.samples);
          f && (r && o <= 4 - r && f.state && (f.data = f.data.subarray(0, f.data.byteLength - r)), c > 0 && (f.data = Pa(f.data, t.subarray(0, c)), f.state = 0));
        }
        o < i ? (h = t[o] & 31, u = o, d = h, n = 0) : n = -1;
      } else
        n = 0;
    }
    if (u >= 0 && n >= 0) {
      const f = {
        data: t.subarray(u, i),
        type: d,
        state: n
      };
      a.push(f);
    }
    if (a.length === 0) {
      const f = this.getLastNalUnit(e.samples);
      f && (f.data = Pa(f.data, t));
    }
    return e.naluState = n, a;
  }
}
class Yre {
  constructor(e, t, i) {
    this.keyData = void 0, this.decrypter = void 0, this.keyData = i, this.decrypter = new $S(t, {
      removePKCS7Padding: !1
    });
  }
  decryptBuffer(e) {
    return this.decrypter.decrypt(e, this.keyData.key.buffer, this.keyData.iv.buffer);
  }
  // AAC - encrypt all full 16 bytes blocks starting from offset 16
  decryptAacSample(e, t, i) {
    const n = e[t].unit;
    if (n.length <= 16)
      return;
    const r = n.subarray(16, n.length - n.length % 16), a = r.buffer.slice(r.byteOffset, r.byteOffset + r.length);
    this.decryptBuffer(a).then((o) => {
      const l = new Uint8Array(o);
      n.set(l, 16), this.decrypter.isSync() || this.decryptAacSamples(e, t + 1, i);
    });
  }
  decryptAacSamples(e, t, i) {
    for (; ; t++) {
      if (t >= e.length) {
        i();
        return;
      }
      if (!(e[t].unit.length < 32) && (this.decryptAacSample(e, t, i), !this.decrypter.isSync()))
        return;
    }
  }
  // AVC - encrypt one 16 bytes block out of ten, starting from offset 32
  getAvcEncryptedData(e) {
    const t = Math.floor((e.length - 48) / 160) * 16 + 16, i = new Int8Array(t);
    let n = 0;
    for (let r = 32; r < e.length - 16; r += 160, n += 16)
      i.set(e.subarray(r, r + 16), n);
    return i;
  }
  getAvcDecryptedUnit(e, t) {
    const i = new Uint8Array(t);
    let n = 0;
    for (let r = 32; r < e.length - 16; r += 160, n += 16)
      e.set(i.subarray(n, n + 16), r);
    return e;
  }
  decryptAvcSample(e, t, i, n, r) {
    const a = DN(r.data), o = this.getAvcEncryptedData(a);
    this.decryptBuffer(o.buffer).then((l) => {
      r.data = this.getAvcDecryptedUnit(a, l), this.decrypter.isSync() || this.decryptAvcSamples(e, t, i + 1, n);
    });
  }
  decryptAvcSamples(e, t, i, n) {
    if (e instanceof Uint8Array)
      throw new Error("Cannot decrypt samples of type Uint8Array");
    for (; ; t++, i = 0) {
      if (t >= e.length) {
        n();
        return;
      }
      const r = e[t].units;
      for (; !(i >= r.length); i++) {
        const a = r[i];
        if (!(a.data.length <= 48 || a.type !== 1 && a.type !== 5) && (this.decryptAvcSample(e, t, i, n, a), !this.decrypter.isSync()))
          return;
      }
    }
  }
}
const Ss = 188;
class oc {
  constructor(e, t, i) {
    this.observer = void 0, this.config = void 0, this.typeSupported = void 0, this.sampleAes = null, this.pmtParsed = !1, this.audioCodec = void 0, this.videoCodec = void 0, this._duration = 0, this._pmtId = -1, this._videoTrack = void 0, this._audioTrack = void 0, this._id3Track = void 0, this._txtTrack = void 0, this.aacOverFlow = null, this.remainderData = null, this.videoParser = void 0, this.observer = e, this.config = t, this.typeSupported = i, this.videoParser = new qre();
  }
  static probe(e) {
    const t = oc.syncOffset(e);
    return t > 0 && ze.warn(`MPEG2-TS detected but first sync word found @ offset ${t}`), t !== -1;
  }
  static syncOffset(e) {
    const t = e.length;
    let i = Math.min(Ss * 5, t - Ss) + 1, n = 0;
    for (; n < i; ) {
      let r = !1, a = -1, o = 0;
      for (let l = n; l < t; l += Ss)
        if (e[l] === 71 && (t - l === Ss || e[l + Ss] === 71)) {
          if (o++, a === -1 && (a = l, a !== 0 && (i = Math.min(a + Ss * 99, e.length - Ss) + 1)), r || (r = Lb(e, l) === 0), r && o > 1 && (a === 0 && o > 2 || l + Ss > i))
            return a;
        } else {
          if (o)
            return -1;
          break;
        }
      n++;
    }
    return -1;
  }
  /**
   * Creates a track model internal to demuxer used to drive remuxing input
   */
  static createTrack(e, t) {
    return {
      container: e === "video" || e === "audio" ? "video/mp2t" : void 0,
      type: e,
      id: EN[e],
      pid: -1,
      inputTimeScale: 9e4,
      sequenceNumber: 0,
      samples: [],
      dropped: 0,
      duration: e === "audio" ? t : void 0
    };
  }
  /**
   * Initializes a new init segment on the demuxer/remuxer interface. Needed for discontinuities/track-switches (or at stream start)
   * Resets all internal track instances of the demuxer.
   */
  resetInitSegment(e, t, i, n) {
    this.pmtParsed = !1, this._pmtId = -1, this._videoTrack = oc.createTrack("video"), this._audioTrack = oc.createTrack("audio", n), this._id3Track = oc.createTrack("id3"), this._txtTrack = oc.createTrack("text"), this._audioTrack.segmentCodec = "aac", this.aacOverFlow = null, this.remainderData = null, this.audioCodec = t, this.videoCodec = i, this._duration = n;
  }
  resetTimeStamp() {
  }
  resetContiguity() {
    const {
      _audioTrack: e,
      _videoTrack: t,
      _id3Track: i
    } = this;
    e && (e.pesData = null), t && (t.pesData = null), i && (i.pesData = null), this.aacOverFlow = null, this.remainderData = null;
  }
  demux(e, t, i = !1, n = !1) {
    i || (this.sampleAes = null);
    let r;
    const a = this._videoTrack, o = this._audioTrack, l = this._id3Track, c = this._txtTrack;
    let h = a.pid, u = a.pesData, d = o.pid, f = l.pid, p = o.pesData, v = l.pesData, g = null, m = this.pmtParsed, x = this._pmtId, y = e.length;
    if (this.remainderData && (e = Pa(this.remainderData, e), y = e.length, this.remainderData = null), y < Ss && !n)
      return this.remainderData = e, {
        audioTrack: o,
        videoTrack: a,
        id3Track: l,
        textTrack: c
      };
    const A = Math.max(0, oc.syncOffset(e));
    y -= (y - A) % Ss, y < e.byteLength && !n && (this.remainderData = new Uint8Array(e.buffer, y, e.buffer.byteLength - y));
    let b = 0;
    for (let w = A; w < y; w += Ss)
      if (e[w] === 71) {
        const C = !!(e[w + 1] & 64), T = Lb(e, w), E = (e[w + 3] & 48) >> 4;
        let P;
        if (E > 1) {
          if (P = w + 5 + e[w + 4], P === w + Ss)
            continue;
        } else
          P = w + 4;
        switch (T) {
          case h:
            C && (u && (r = Dd(u)) && this.videoParser.parseAVCPES(a, c, r, !1, this._duration), u = {
              data: [],
              size: 0
            }), u && (u.data.push(e.subarray(P, w + Ss)), u.size += w + Ss - P);
            break;
          case d:
            if (C) {
              if (p && (r = Dd(p)))
                switch (o.segmentCodec) {
                  case "aac":
                    this.parseAACPES(o, r);
                    break;
                  case "mp3":
                    this.parseMPEGPES(o, r);
                    break;
                  case "ac3":
                    this.parseAC3PES(o, r);
                    break;
                }
              p = {
                data: [],
                size: 0
              };
            }
            p && (p.data.push(e.subarray(P, w + Ss)), p.size += w + Ss - P);
            break;
          case f:
            C && (v && (r = Dd(v)) && this.parseID3PES(l, r), v = {
              data: [],
              size: 0
            }), v && (v.data.push(e.subarray(P, w + Ss)), v.size += w + Ss - P);
            break;
          case 0:
            C && (P += e[P] + 1), x = this._pmtId = Zre(e, P);
            break;
          case x: {
            C && (P += e[P] + 1);
            const L = Kre(e, P, this.typeSupported, i);
            h = L.videoPid, h > 0 && (a.pid = h, a.segmentCodec = L.segmentVideoCodec), d = L.audioPid, d > 0 && (o.pid = d, o.segmentCodec = L.segmentAudioCodec), f = L.id3Pid, f > 0 && (l.pid = f), g !== null && !m && (ze.warn(`MPEG-TS PMT found at ${w} after unknown PID '${g}'. Backtracking to sync byte @${A} to parse all TS packets.`), g = null, w = A - 188), m = this.pmtParsed = !0;
            break;
          }
          case 17:
          case 8191:
            break;
          default:
            g = T;
            break;
        }
      } else
        b++;
    if (b > 0) {
      const w = new Error(`Found ${b} TS packet/s that do not start with 0x47`);
      this.observer.emit(j.ERROR, j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.FRAG_PARSING_ERROR,
        fatal: !1,
        error: w,
        reason: w.message
      });
    }
    a.pesData = u, o.pesData = p, l.pesData = v;
    const M = {
      audioTrack: o,
      videoTrack: a,
      id3Track: l,
      textTrack: c
    };
    return n && this.extractRemainingSamples(M), M;
  }
  flush() {
    const {
      remainderData: e
    } = this;
    this.remainderData = null;
    let t;
    return e ? t = this.demux(e, -1, !1, !0) : t = {
      videoTrack: this._videoTrack,
      audioTrack: this._audioTrack,
      id3Track: this._id3Track,
      textTrack: this._txtTrack
    }, this.extractRemainingSamples(t), this.sampleAes ? this.decrypt(t, this.sampleAes) : t;
  }
  extractRemainingSamples(e) {
    const {
      audioTrack: t,
      videoTrack: i,
      id3Track: n,
      textTrack: r
    } = e, a = i.pesData, o = t.pesData, l = n.pesData;
    let c;
    if (a && (c = Dd(a)) ? (this.videoParser.parseAVCPES(i, r, c, !0, this._duration), i.pesData = null) : i.pesData = a, o && (c = Dd(o))) {
      switch (t.segmentCodec) {
        case "aac":
          this.parseAACPES(t, c);
          break;
        case "mp3":
          this.parseMPEGPES(t, c);
          break;
        case "ac3":
          this.parseAC3PES(t, c);
          break;
      }
      t.pesData = null;
    } else
      o != null && o.size && ze.log("last AAC PES packet truncated,might overlap between fragments"), t.pesData = o;
    l && (c = Dd(l)) ? (this.parseID3PES(n, c), n.pesData = null) : n.pesData = l;
  }
  demuxSampleAes(e, t, i) {
    const n = this.demux(e, i, !0, !this.config.progressive), r = this.sampleAes = new Yre(this.observer, this.config, t);
    return this.decrypt(n, r);
  }
  decrypt(e, t) {
    return new Promise((i) => {
      const {
        audioTrack: n,
        videoTrack: r
      } = e;
      n.samples && n.segmentCodec === "aac" ? t.decryptAacSamples(n.samples, 0, () => {
        r.samples ? t.decryptAvcSamples(r.samples, 0, 0, () => {
          i(e);
        }) : i(e);
      }) : r.samples && t.decryptAvcSamples(r.samples, 0, 0, () => {
        i(e);
      });
    });
  }
  destroy() {
    this._duration = 0;
  }
  parseAACPES(e, t) {
    let i = 0;
    const n = this.aacOverFlow;
    let r = t.data;
    if (n) {
      this.aacOverFlow = null;
      const u = n.missing, d = n.sample.unit.byteLength;
      if (u === -1)
        r = Pa(n.sample.unit, r);
      else {
        const f = d - u;
        n.sample.unit.set(r.subarray(0, u), f), e.samples.push(n.sample), i = n.missing;
      }
    }
    let a, o;
    for (a = i, o = r.length; a < o - 1 && !Mx(r, a); a++)
      ;
    if (a !== i) {
      let u;
      const d = a < o - 1;
      d ? u = `AAC PES did not start with ADTS header,offset:${a}` : u = "No ADTS header found in AAC PES";
      const f = new Error(u);
      if (ze.warn(`parsing error: ${u}`), this.observer.emit(j.ERROR, j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.FRAG_PARSING_ERROR,
        fatal: !1,
        levelRetry: d,
        error: f,
        reason: u
      }), !d)
        return;
    }
    jN(e, this.observer, r, a, this.audioCodec);
    let l;
    if (t.pts !== void 0)
      l = t.pts;
    else if (n) {
      const u = qN(e.samplerate);
      l = n.sample.pts + u;
    } else {
      ze.warn("[tsdemuxer]: AAC PES unknown PTS");
      return;
    }
    let c = 0, h;
    for (; a < o; )
      if (h = YN(e, r, a, l, c), a += h.length, h.missing) {
        this.aacOverFlow = h;
        break;
      } else
        for (c++; a < o - 1 && !Mx(r, a); a++)
          ;
  }
  parseMPEGPES(e, t) {
    const i = t.data, n = i.length;
    let r = 0, a = 0;
    const o = t.pts;
    if (o === void 0) {
      ze.warn("[tsdemuxer]: MPEG PES unknown PTS");
      return;
    }
    for (; a < n; )
      if (JN(i, a)) {
        const l = ZN(e, i, a, o, r);
        if (l)
          a += l.length, r++;
        else
          break;
      } else
        a++;
  }
  parseAC3PES(e, t) {
    {
      const i = t.data, n = t.pts;
      if (n === void 0) {
        ze.warn("[tsdemuxer]: AC3 PES unknown PTS");
        return;
      }
      const r = i.length;
      let a = 0, o = 0, l;
      for (; o < r && (l = eB(e, i, o, n, a++)) > 0; )
        o += l;
    }
  }
  parseID3PES(e, t) {
    if (t.pts === void 0) {
      ze.warn("[tsdemuxer]: ID3 PES unknown PTS");
      return;
    }
    const i = as({}, t, {
      type: this._videoTrack ? Ka.emsg : Ka.audioId3,
      duration: Number.POSITIVE_INFINITY
    });
    e.samples.push(i);
  }
}
function Lb(s, e) {
  return ((s[e + 1] & 31) << 8) + s[e + 2];
}
function Zre(s, e) {
  return (s[e + 10] & 31) << 8 | s[e + 11];
}
function Kre(s, e, t, i) {
  const n = {
    audioPid: -1,
    videoPid: -1,
    id3Pid: -1,
    segmentVideoCodec: "avc",
    segmentAudioCodec: "aac"
  }, r = (s[e + 1] & 15) << 8 | s[e + 2], a = e + 3 + r - 4, o = (s[e + 10] & 15) << 8 | s[e + 11];
  for (e += 12 + o; e < a; ) {
    const l = Lb(s, e), c = (s[e + 3] & 15) << 8 | s[e + 4];
    switch (s[e]) {
      case 207:
        if (!i) {
          dA("ADTS AAC");
          break;
        }
      case 15:
        n.audioPid === -1 && (n.audioPid = l);
        break;
      case 21:
        n.id3Pid === -1 && (n.id3Pid = l);
        break;
      case 219:
        if (!i) {
          dA("H.264");
          break;
        }
      case 27:
        n.videoPid === -1 && (n.videoPid = l, n.segmentVideoCodec = "avc");
        break;
      case 3:
      case 4:
        !t.mpeg && !t.mp3 ? ze.log("MPEG audio found, not supported in this browser") : n.audioPid === -1 && (n.audioPid = l, n.segmentAudioCodec = "mp3");
        break;
      case 193:
        if (!i) {
          dA("AC-3");
          break;
        }
      case 129:
        t.ac3 ? n.audioPid === -1 && (n.audioPid = l, n.segmentAudioCodec = "ac3") : ze.log("AC-3 audio found, not supported in this browser");
        break;
      case 6:
        if (n.audioPid === -1 && c > 0) {
          let h = e + 5, u = c;
          for (; u > 2; ) {
            switch (s[h]) {
              case 106:
                t.ac3 !== !0 ? ze.log("AC-3 audio found, not supported in this browser for now") : (n.audioPid = l, n.segmentAudioCodec = "ac3");
                break;
            }
            const f = s[h + 1] + 2;
            h += f, u -= f;
          }
        }
        break;
      case 194:
      case 135:
        ze.warn("Unsupported EC-3 in M2TS found");
        break;
      case 36:
        ze.warn("Unsupported HEVC in M2TS found");
        break;
    }
    e += c + 5;
  }
  return n;
}
function dA(s) {
  ze.log(`${s} with AES-128-CBC encryption found in unencrypted stream`);
}
function Dd(s) {
  let e = 0, t, i, n, r, a;
  const o = s.data;
  if (!s || s.size === 0)
    return null;
  for (; o[0].length < 19 && o.length > 1; )
    o[0] = Pa(o[0], o[1]), o.splice(1, 1);
  if (t = o[0], (t[0] << 16) + (t[1] << 8) + t[2] === 1) {
    if (i = (t[4] << 8) + t[5], i && i > s.size - 6)
      return null;
    const c = t[7];
    c & 192 && (r = (t[9] & 14) * 536870912 + // 1 << 29
    (t[10] & 255) * 4194304 + // 1 << 22
    (t[11] & 254) * 16384 + // 1 << 14
    (t[12] & 255) * 128 + // 1 << 7
    (t[13] & 254) / 2, c & 64 ? (a = (t[14] & 14) * 536870912 + // 1 << 29
    (t[15] & 255) * 4194304 + // 1 << 22
    (t[16] & 254) * 16384 + // 1 << 14
    (t[17] & 255) * 128 + // 1 << 7
    (t[18] & 254) / 2, r - a > 60 * 9e4 && (ze.warn(`${Math.round((r - a) / 9e4)}s delta between PTS and DTS, align them`), r = a)) : a = r), n = t[8];
    let h = n + 9;
    if (s.size <= h)
      return null;
    s.size -= h;
    const u = new Uint8Array(s.size);
    for (let d = 0, f = o.length; d < f; d++) {
      t = o[d];
      let p = t.byteLength;
      if (h)
        if (h > p) {
          h -= p;
          continue;
        } else
          t = t.subarray(h), p -= h, h = 0;
      u.set(t, e), e += p;
    }
    return i && (i -= n + 3), {
      data: u,
      pts: r,
      dts: a,
      len: i
    };
  }
  return null;
}
class Jre extends tw {
  resetInitSegment(e, t, i, n) {
    super.resetInitSegment(e, t, i, n), this._audioTrack = {
      container: "audio/mpeg",
      type: "audio",
      id: 2,
      pid: -1,
      sequenceNumber: 0,
      segmentCodec: "mp3",
      samples: [],
      manifestCodec: t,
      duration: n,
      inputTimeScale: 9e4,
      dropped: 0
    };
  }
  static probe(e) {
    if (!e)
      return !1;
    const t = Dg(e, 0);
    let i = (t == null ? void 0 : t.length) || 0;
    if (t && e[i] === 11 && e[i + 1] === 119 && ZS(t) !== void 0 && // check the bsid to confirm ac-3 or ec-3 (not mp3)
    $N(e, i) <= 16)
      return !1;
    for (let n = e.length; i < n; i++)
      if (QN(e, i))
        return ze.log("MPEG Audio sync word found !"), !0;
    return !1;
  }
  canParse(e, t) {
    return Gre(e, t);
  }
  appendFrame(e, t, i) {
    if (this.basePTS !== null)
      return ZN(e, t, i, this.basePTS, this.frameIndex);
  }
}
class v_ {
  static getSilentFrame(e, t) {
    switch (e) {
      case "mp4a.40.2":
        if (t === 1)
          return new Uint8Array([0, 200, 0, 128, 35, 128]);
        if (t === 2)
          return new Uint8Array([33, 0, 73, 144, 2, 25, 0, 35, 128]);
        if (t === 3)
          return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 142]);
        if (t === 4)
          return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 128, 44, 128, 8, 2, 56]);
        if (t === 5)
          return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 130, 48, 4, 153, 0, 33, 144, 2, 56]);
        if (t === 6)
          return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 130, 48, 4, 153, 0, 33, 144, 2, 0, 178, 0, 32, 8, 224]);
        break;
      default:
        if (t === 1)
          return new Uint8Array([1, 64, 34, 128, 163, 78, 230, 128, 186, 8, 0, 0, 0, 28, 6, 241, 193, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
        if (t === 2)
          return new Uint8Array([1, 64, 34, 128, 163, 94, 230, 128, 186, 8, 0, 0, 0, 0, 149, 0, 6, 241, 161, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
        if (t === 3)
          return new Uint8Array([1, 64, 34, 128, 163, 94, 230, 128, 186, 8, 0, 0, 0, 0, 149, 0, 6, 241, 161, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
        break;
    }
  }
}
const Xl = Math.pow(2, 32) - 1;
class ke {
  static init() {
    ke.types = {
      avc1: [],
      // codingname
      avcC: [],
      btrt: [],
      dinf: [],
      dref: [],
      esds: [],
      ftyp: [],
      hdlr: [],
      mdat: [],
      mdhd: [],
      mdia: [],
      mfhd: [],
      minf: [],
      moof: [],
      moov: [],
      mp4a: [],
      ".mp3": [],
      dac3: [],
      "ac-3": [],
      mvex: [],
      mvhd: [],
      pasp: [],
      sdtp: [],
      stbl: [],
      stco: [],
      stsc: [],
      stsd: [],
      stsz: [],
      stts: [],
      tfdt: [],
      tfhd: [],
      traf: [],
      trak: [],
      trun: [],
      trex: [],
      tkhd: [],
      vmhd: [],
      smhd: []
    };
    let e;
    for (e in ke.types)
      ke.types.hasOwnProperty(e) && (ke.types[e] = [e.charCodeAt(0), e.charCodeAt(1), e.charCodeAt(2), e.charCodeAt(3)]);
    const t = new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0,
      // pre_defined
      118,
      105,
      100,
      101,
      // handler_type: 'vide'
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      86,
      105,
      100,
      101,
      111,
      72,
      97,
      110,
      100,
      108,
      101,
      114,
      0
      // name: 'VideoHandler'
    ]), i = new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0,
      // pre_defined
      115,
      111,
      117,
      110,
      // handler_type: 'soun'
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      83,
      111,
      117,
      110,
      100,
      72,
      97,
      110,
      100,
      108,
      101,
      114,
      0
      // name: 'SoundHandler'
    ]);
    ke.HDLR_TYPES = {
      video: t,
      audio: i
    };
    const n = new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      1,
      // entry_count
      0,
      0,
      0,
      12,
      // entry_size
      117,
      114,
      108,
      32,
      // 'url' type
      0,
      // version 0
      0,
      0,
      1
      // entry_flags
    ]), r = new Uint8Array([
      0,
      // version
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0
      // entry_count
    ]);
    ke.STTS = ke.STSC = ke.STCO = r, ke.STSZ = new Uint8Array([
      0,
      // version
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0,
      // sample_size
      0,
      0,
      0,
      0
      // sample_count
    ]), ke.VMHD = new Uint8Array([
      0,
      // version
      0,
      0,
      1,
      // flags
      0,
      0,
      // graphicsmode
      0,
      0,
      0,
      0,
      0,
      0
      // opcolor
    ]), ke.SMHD = new Uint8Array([
      0,
      // version
      0,
      0,
      0,
      // flags
      0,
      0,
      // balance
      0,
      0
      // reserved
    ]), ke.STSD = new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      1
    ]);
    const a = new Uint8Array([105, 115, 111, 109]), o = new Uint8Array([97, 118, 99, 49]), l = new Uint8Array([0, 0, 0, 1]);
    ke.FTYP = ke.box(ke.types.ftyp, a, l, a, o), ke.DINF = ke.box(ke.types.dinf, ke.box(ke.types.dref, n));
  }
  static box(e, ...t) {
    let i = 8, n = t.length;
    const r = n;
    for (; n--; )
      i += t[n].byteLength;
    const a = new Uint8Array(i);
    for (a[0] = i >> 24 & 255, a[1] = i >> 16 & 255, a[2] = i >> 8 & 255, a[3] = i & 255, a.set(e, 4), n = 0, i = 8; n < r; n++)
      a.set(t[n], i), i += t[n].byteLength;
    return a;
  }
  static hdlr(e) {
    return ke.box(ke.types.hdlr, ke.HDLR_TYPES[e]);
  }
  static mdat(e) {
    return ke.box(ke.types.mdat, e);
  }
  static mdhd(e, t) {
    t *= e;
    const i = Math.floor(t / (Xl + 1)), n = Math.floor(t % (Xl + 1));
    return ke.box(ke.types.mdhd, new Uint8Array([
      1,
      // version 1
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      2,
      // creation_time
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      3,
      // modification_time
      e >> 24 & 255,
      e >> 16 & 255,
      e >> 8 & 255,
      e & 255,
      // timescale
      i >> 24,
      i >> 16 & 255,
      i >> 8 & 255,
      i & 255,
      n >> 24,
      n >> 16 & 255,
      n >> 8 & 255,
      n & 255,
      85,
      196,
      // 'und' language (undetermined)
      0,
      0
    ]));
  }
  static mdia(e) {
    return ke.box(ke.types.mdia, ke.mdhd(e.timescale, e.duration), ke.hdlr(e.type), ke.minf(e));
  }
  static mfhd(e) {
    return ke.box(ke.types.mfhd, new Uint8Array([
      0,
      0,
      0,
      0,
      // flags
      e >> 24,
      e >> 16 & 255,
      e >> 8 & 255,
      e & 255
      // sequence_number
    ]));
  }
  static minf(e) {
    return e.type === "audio" ? ke.box(ke.types.minf, ke.box(ke.types.smhd, ke.SMHD), ke.DINF, ke.stbl(e)) : ke.box(ke.types.minf, ke.box(ke.types.vmhd, ke.VMHD), ke.DINF, ke.stbl(e));
  }
  static moof(e, t, i) {
    return ke.box(ke.types.moof, ke.mfhd(e), ke.traf(i, t));
  }
  static moov(e) {
    let t = e.length;
    const i = [];
    for (; t--; )
      i[t] = ke.trak(e[t]);
    return ke.box.apply(null, [ke.types.moov, ke.mvhd(e[0].timescale, e[0].duration)].concat(i).concat(ke.mvex(e)));
  }
  static mvex(e) {
    let t = e.length;
    const i = [];
    for (; t--; )
      i[t] = ke.trex(e[t]);
    return ke.box.apply(null, [ke.types.mvex, ...i]);
  }
  static mvhd(e, t) {
    t *= e;
    const i = Math.floor(t / (Xl + 1)), n = Math.floor(t % (Xl + 1)), r = new Uint8Array([
      1,
      // version 1
      0,
      0,
      0,
      // flags
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      2,
      // creation_time
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      3,
      // modification_time
      e >> 24 & 255,
      e >> 16 & 255,
      e >> 8 & 255,
      e & 255,
      // timescale
      i >> 24,
      i >> 16 & 255,
      i >> 8 & 255,
      i & 255,
      n >> 24,
      n >> 16 & 255,
      n >> 8 & 255,
      n & 255,
      0,
      1,
      0,
      0,
      // 1.0 rate
      1,
      0,
      // 1.0 volume
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      0,
      // reserved
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      64,
      0,
      0,
      0,
      // transformation: unity matrix
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      // pre_defined
      255,
      255,
      255,
      255
      // next_track_ID
    ]);
    return ke.box(ke.types.mvhd, r);
  }
  static sdtp(e) {
    const t = e.samples || [], i = new Uint8Array(4 + t.length);
    let n, r;
    for (n = 0; n < t.length; n++)
      r = t[n].flags, i[n + 4] = r.dependsOn << 4 | r.isDependedOn << 2 | r.hasRedundancy;
    return ke.box(ke.types.sdtp, i);
  }
  static stbl(e) {
    return ke.box(ke.types.stbl, ke.stsd(e), ke.box(ke.types.stts, ke.STTS), ke.box(ke.types.stsc, ke.STSC), ke.box(ke.types.stsz, ke.STSZ), ke.box(ke.types.stco, ke.STCO));
  }
  static avc1(e) {
    let t = [], i = [], n, r, a;
    for (n = 0; n < e.sps.length; n++)
      r = e.sps[n], a = r.byteLength, t.push(a >>> 8 & 255), t.push(a & 255), t = t.concat(Array.prototype.slice.call(r));
    for (n = 0; n < e.pps.length; n++)
      r = e.pps[n], a = r.byteLength, i.push(a >>> 8 & 255), i.push(a & 255), i = i.concat(Array.prototype.slice.call(r));
    const o = ke.box(ke.types.avcC, new Uint8Array([
      1,
      // version
      t[3],
      // profile
      t[4],
      // profile compat
      t[5],
      // level
      255,
      // lengthSizeMinusOne, hard-coded to 4 bytes
      224 | e.sps.length
      // 3bit reserved (111) + numOfSequenceParameterSets
    ].concat(t).concat([
      e.pps.length
      // numOfPictureParameterSets
    ]).concat(i))), l = e.width, c = e.height, h = e.pixelRatio[0], u = e.pixelRatio[1];
    return ke.box(
      ke.types.avc1,
      new Uint8Array([
        0,
        0,
        0,
        // reserved
        0,
        0,
        0,
        // reserved
        0,
        1,
        // data_reference_index
        0,
        0,
        // pre_defined
        0,
        0,
        // reserved
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        // pre_defined
        l >> 8 & 255,
        l & 255,
        // width
        c >> 8 & 255,
        c & 255,
        // height
        0,
        72,
        0,
        0,
        // horizresolution
        0,
        72,
        0,
        0,
        // vertresolution
        0,
        0,
        0,
        0,
        // reserved
        0,
        1,
        // frame_count
        18,
        100,
        97,
        105,
        108,
        // dailymotion/hls.js
        121,
        109,
        111,
        116,
        105,
        111,
        110,
        47,
        104,
        108,
        115,
        46,
        106,
        115,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        // compressorname
        0,
        24,
        // depth = 24
        17,
        17
      ]),
      // pre_defined = -1
      o,
      ke.box(ke.types.btrt, new Uint8Array([
        0,
        28,
        156,
        128,
        // bufferSizeDB
        0,
        45,
        198,
        192,
        // maxBitrate
        0,
        45,
        198,
        192
      ])),
      // avgBitrate
      ke.box(ke.types.pasp, new Uint8Array([
        h >> 24,
        // hSpacing
        h >> 16 & 255,
        h >> 8 & 255,
        h & 255,
        u >> 24,
        // vSpacing
        u >> 16 & 255,
        u >> 8 & 255,
        u & 255
      ]))
    );
  }
  static esds(e) {
    const t = e.config.length;
    return new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      3,
      // descriptor_type
      23 + t,
      // length
      0,
      1,
      // es_id
      0,
      // stream_priority
      4,
      // descriptor_type
      15 + t,
      // length
      64,
      // codec : mpeg4_audio
      21,
      // stream_type
      0,
      0,
      0,
      // buffer_size
      0,
      0,
      0,
      0,
      // maxBitrate
      0,
      0,
      0,
      0,
      // avgBitrate
      5
      // descriptor_type
    ].concat([t]).concat(e.config).concat([6, 1, 2]));
  }
  static audioStsd(e) {
    const t = e.samplerate;
    return new Uint8Array([
      0,
      0,
      0,
      // reserved
      0,
      0,
      0,
      // reserved
      0,
      1,
      // data_reference_index
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      // reserved
      0,
      e.channelCount,
      // channelcount
      0,
      16,
      // sampleSize:16bits
      0,
      0,
      0,
      0,
      // reserved2
      t >> 8 & 255,
      t & 255,
      //
      0,
      0
    ]);
  }
  static mp4a(e) {
    return ke.box(ke.types.mp4a, ke.audioStsd(e), ke.box(ke.types.esds, ke.esds(e)));
  }
  static mp3(e) {
    return ke.box(ke.types[".mp3"], ke.audioStsd(e));
  }
  static ac3(e) {
    return ke.box(ke.types["ac-3"], ke.audioStsd(e), ke.box(ke.types.dac3, e.config));
  }
  static stsd(e) {
    return e.type === "audio" ? e.segmentCodec === "mp3" && e.codec === "mp3" ? ke.box(ke.types.stsd, ke.STSD, ke.mp3(e)) : e.segmentCodec === "ac3" ? ke.box(ke.types.stsd, ke.STSD, ke.ac3(e)) : ke.box(ke.types.stsd, ke.STSD, ke.mp4a(e)) : ke.box(ke.types.stsd, ke.STSD, ke.avc1(e));
  }
  static tkhd(e) {
    const t = e.id, i = e.duration * e.timescale, n = e.width, r = e.height, a = Math.floor(i / (Xl + 1)), o = Math.floor(i % (Xl + 1));
    return ke.box(ke.types.tkhd, new Uint8Array([
      1,
      // version 1
      0,
      0,
      7,
      // flags
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      2,
      // creation_time
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      3,
      // modification_time
      t >> 24 & 255,
      t >> 16 & 255,
      t >> 8 & 255,
      t & 255,
      // track_ID
      0,
      0,
      0,
      0,
      // reserved
      a >> 24,
      a >> 16 & 255,
      a >> 8 & 255,
      a & 255,
      o >> 24,
      o >> 16 & 255,
      o >> 8 & 255,
      o & 255,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      // reserved
      0,
      0,
      // layer
      0,
      0,
      // alternate_group
      0,
      0,
      // non-audio track volume
      0,
      0,
      // reserved
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      64,
      0,
      0,
      0,
      // transformation: unity matrix
      n >> 8 & 255,
      n & 255,
      0,
      0,
      // width
      r >> 8 & 255,
      r & 255,
      0,
      0
      // height
    ]));
  }
  static traf(e, t) {
    const i = ke.sdtp(e), n = e.id, r = Math.floor(t / (Xl + 1)), a = Math.floor(t % (Xl + 1));
    return ke.box(
      ke.types.traf,
      ke.box(ke.types.tfhd, new Uint8Array([
        0,
        // version 0
        0,
        0,
        0,
        // flags
        n >> 24,
        n >> 16 & 255,
        n >> 8 & 255,
        n & 255
        // track_ID
      ])),
      ke.box(ke.types.tfdt, new Uint8Array([
        1,
        // version 1
        0,
        0,
        0,
        // flags
        r >> 24,
        r >> 16 & 255,
        r >> 8 & 255,
        r & 255,
        a >> 24,
        a >> 16 & 255,
        a >> 8 & 255,
        a & 255
      ])),
      ke.trun(e, i.length + 16 + // tfhd
      20 + // tfdt
      8 + // traf header
      16 + // mfhd
      8 + // moof header
      8),
      // mdat header
      i
    );
  }
  /**
   * Generate a track box.
   * @param track a track definition
   */
  static trak(e) {
    return e.duration = e.duration || 4294967295, ke.box(ke.types.trak, ke.tkhd(e), ke.mdia(e));
  }
  static trex(e) {
    const t = e.id;
    return ke.box(ke.types.trex, new Uint8Array([
      0,
      // version 0
      0,
      0,
      0,
      // flags
      t >> 24,
      t >> 16 & 255,
      t >> 8 & 255,
      t & 255,
      // track_ID
      0,
      0,
      0,
      1,
      // default_sample_description_index
      0,
      0,
      0,
      0,
      // default_sample_duration
      0,
      0,
      0,
      0,
      // default_sample_size
      0,
      1,
      0,
      1
      // default_sample_flags
    ]));
  }
  static trun(e, t) {
    const i = e.samples || [], n = i.length, r = 12 + 16 * n, a = new Uint8Array(r);
    let o, l, c, h, u, d;
    for (t += 8 + r, a.set([
      e.type === "video" ? 1 : 0,
      // version 1 for video with signed-int sample_composition_time_offset
      0,
      15,
      1,
      // flags
      n >>> 24 & 255,
      n >>> 16 & 255,
      n >>> 8 & 255,
      n & 255,
      // sample_count
      t >>> 24 & 255,
      t >>> 16 & 255,
      t >>> 8 & 255,
      t & 255
      // data_offset
    ], 0), o = 0; o < n; o++)
      l = i[o], c = l.duration, h = l.size, u = l.flags, d = l.cts, a.set([
        c >>> 24 & 255,
        c >>> 16 & 255,
        c >>> 8 & 255,
        c & 255,
        // sample_duration
        h >>> 24 & 255,
        h >>> 16 & 255,
        h >>> 8 & 255,
        h & 255,
        // sample_size
        u.isLeading << 2 | u.dependsOn,
        u.isDependedOn << 6 | u.hasRedundancy << 4 | u.paddingValue << 1 | u.isNonSync,
        u.degradPrio & 61440,
        u.degradPrio & 15,
        // sample_flags
        d >>> 24 & 255,
        d >>> 16 & 255,
        d >>> 8 & 255,
        d & 255
        // sample_composition_time_offset
      ], 12 + 16 * o);
    return ke.box(ke.types.trun, a);
  }
  static initSegment(e) {
    ke.types || ke.init();
    const t = ke.moov(e);
    return Pa(ke.FTYP, t);
  }
}
ke.types = void 0;
ke.HDLR_TYPES = void 0;
ke.STTS = void 0;
ke.STSC = void 0;
ke.STCO = void 0;
ke.STSZ = void 0;
ke.VMHD = void 0;
ke.SMHD = void 0;
ke.STSD = void 0;
ke.FTYP = void 0;
ke.DINF = void 0;
const tB = 9e4;
function sw(s, e, t = 1, i = !1) {
  const n = s * e * t;
  return i ? Math.round(n) : n;
}
function Qre(s, e, t = 1, i = !1) {
  return sw(s, e, 1 / t, i);
}
function Im(s, e = !1) {
  return sw(s, 1e3, 1 / tB, e);
}
function $re(s, e = 1) {
  return sw(s, tB, 1 / e);
}
const eae = 10 * 1e3, y_ = 1024, tae = 1152, iae = 1536;
let Ld = null, fA = null;
class zy {
  constructor(e, t, i, n = "") {
    if (this.observer = void 0, this.config = void 0, this.typeSupported = void 0, this.ISGenerated = !1, this._initPTS = null, this._initDTS = null, this.nextAvcDts = null, this.nextAudioPts = null, this.videoSampleDuration = null, this.isAudioContiguous = !1, this.isVideoContiguous = !1, this.videoTrackConfig = void 0, this.observer = e, this.config = t, this.typeSupported = i, this.ISGenerated = !1, Ld === null) {
      const a = (navigator.userAgent || "").match(/Chrome\/(\d+)/i);
      Ld = a ? parseInt(a[1]) : 0;
    }
    if (fA === null) {
      const r = navigator.userAgent.match(/Safari\/(\d+)/i);
      fA = r ? parseInt(r[1]) : 0;
    }
  }
  destroy() {
    this.config = this.videoTrackConfig = this._initPTS = this._initDTS = null;
  }
  resetTimeStamp(e) {
    ze.log("[mp4-remuxer]: initPTS & initDTS reset"), this._initPTS = this._initDTS = e;
  }
  resetNextTimestamp() {
    ze.log("[mp4-remuxer]: reset next timestamp"), this.isVideoContiguous = !1, this.isAudioContiguous = !1;
  }
  resetInitSegment() {
    ze.log("[mp4-remuxer]: ISGenerated flag reset"), this.ISGenerated = !1, this.videoTrackConfig = void 0;
  }
  getVideoStartPts(e) {
    let t = !1;
    const i = e.reduce((n, r) => {
      const a = r.pts - n;
      return a < -4294967296 ? (t = !0, va(n, r.pts)) : a > 0 ? n : r.pts;
    }, e[0].pts);
    return t && ze.debug("PTS rollover detected"), i;
  }
  remux(e, t, i, n, r, a, o, l) {
    let c, h, u, d, f, p, v = r, g = r;
    const m = e.pid > -1, x = t.pid > -1, y = t.samples.length, A = e.samples.length > 0, b = o && y > 0 || y > 1;
    if ((!m || A) && (!x || b) || this.ISGenerated || o) {
      if (this.ISGenerated) {
        var w, C, T, E;
        const N = this.videoTrackConfig;
        N && (t.width !== N.width || t.height !== N.height || ((w = t.pixelRatio) == null ? void 0 : w[0]) !== ((C = N.pixelRatio) == null ? void 0 : C[0]) || ((T = t.pixelRatio) == null ? void 0 : T[1]) !== ((E = N.pixelRatio) == null ? void 0 : E[1])) && this.resetInitSegment();
      } else
        u = this.generateIS(e, t, r, a);
      const P = this.isVideoContiguous;
      let L = -1, I;
      if (b && (L = nae(t.samples), !P && this.config.forceKeyFrameOnDiscontinuity))
        if (p = !0, L > 0) {
          ze.warn(`[mp4-remuxer]: Dropped ${L} out of ${y} video samples due to a missing keyframe`);
          const N = this.getVideoStartPts(t.samples);
          t.samples = t.samples.slice(L), t.dropped += L, g += (t.samples[0].pts - N) / t.inputTimeScale, I = g;
        } else
          L === -1 && (ze.warn(`[mp4-remuxer]: No keyframe found out of ${y} video samples`), p = !1);
      if (this.ISGenerated) {
        if (A && b) {
          const N = this.getVideoStartPts(t.samples), U = (va(e.samples[0].pts, N) - N) / t.inputTimeScale;
          v += Math.max(0, U), g += Math.max(0, -U);
        }
        if (A) {
          if (e.samplerate || (ze.warn("[mp4-remuxer]: regenerate InitSegment as audio detected"), u = this.generateIS(e, t, r, a)), h = this.remuxAudio(e, v, this.isAudioContiguous, a, x || b || l === jt.AUDIO ? g : void 0), b) {
            const N = h ? h.endPTS - h.startPTS : 0;
            t.inputTimeScale || (ze.warn("[mp4-remuxer]: regenerate InitSegment as video detected"), u = this.generateIS(e, t, r, a)), c = this.remuxVideo(t, g, P, N);
          }
        } else
          b && (c = this.remuxVideo(t, g, P, 0));
        c && (c.firstKeyFrame = L, c.independent = L !== -1, c.firstKeyFramePTS = I);
      }
    }
    return this.ISGenerated && this._initPTS && this._initDTS && (i.samples.length && (f = iB(i, r, this._initPTS, this._initDTS)), n.samples.length && (d = nB(n, r, this._initPTS))), {
      audio: h,
      video: c,
      initSegment: u,
      independent: p,
      text: d,
      id3: f
    };
  }
  generateIS(e, t, i, n) {
    const r = e.samples, a = t.samples, o = this.typeSupported, l = {}, c = this._initPTS;
    let h = !c || n, u = "audio/mp4", d, f, p;
    if (h && (d = f = 1 / 0), e.config && r.length) {
      switch (e.timescale = e.samplerate, e.segmentCodec) {
        case "mp3":
          o.mpeg ? (u = "audio/mpeg", e.codec = "") : o.mp3 && (e.codec = "mp3");
          break;
        case "ac3":
          e.codec = "ac-3";
          break;
      }
      l.audio = {
        id: "audio",
        container: u,
        codec: e.codec,
        initSegment: e.segmentCodec === "mp3" && o.mpeg ? new Uint8Array(0) : ke.initSegment([e]),
        metadata: {
          channelCount: e.channelCount
        }
      }, h && (p = e.inputTimeScale, !c || p !== c.timescale ? d = f = r[0].pts - Math.round(p * i) : h = !1);
    }
    if (t.sps && t.pps && a.length) {
      if (t.timescale = t.inputTimeScale, l.video = {
        id: "main",
        container: "video/mp4",
        codec: t.codec,
        initSegment: ke.initSegment([t]),
        metadata: {
          width: t.width,
          height: t.height
        }
      }, h)
        if (p = t.inputTimeScale, !c || p !== c.timescale) {
          const v = this.getVideoStartPts(a), g = Math.round(p * i);
          f = Math.min(f, va(a[0].dts, v) - g), d = Math.min(d, v - g);
        } else
          h = !1;
      this.videoTrackConfig = {
        width: t.width,
        height: t.height,
        pixelRatio: t.pixelRatio
      };
    }
    if (Object.keys(l).length)
      return this.ISGenerated = !0, h ? (this._initPTS = {
        baseTime: d,
        timescale: p
      }, this._initDTS = {
        baseTime: f,
        timescale: p
      }) : d = p = void 0, {
        tracks: l,
        initPTS: d,
        timescale: p
      };
  }
  remuxVideo(e, t, i, n) {
    const r = e.inputTimeScale, a = e.samples, o = [], l = a.length, c = this._initPTS;
    let h = this.nextAvcDts, u = 8, d = this.videoSampleDuration, f, p, v = Number.POSITIVE_INFINITY, g = Number.NEGATIVE_INFINITY, m = !1;
    if (!i || h === null) {
      const K = t * r, te = a[0].pts - va(a[0].dts, a[0].pts);
      Ld && h !== null && Math.abs(K - te - h) < 15e3 ? i = !0 : h = K - te;
    }
    const x = c.baseTime * r / c.timescale;
    for (let K = 0; K < l; K++) {
      const te = a[K];
      te.pts = va(te.pts - x, h), te.dts = va(te.dts - x, h), te.dts < a[K > 0 ? K - 1 : K].dts && (m = !0);
    }
    m && a.sort(function(K, te) {
      const re = K.dts - te.dts, be = K.pts - te.pts;
      return re || be;
    }), f = a[0].dts, p = a[a.length - 1].dts;
    const y = p - f, A = y ? Math.round(y / (l - 1)) : d || e.inputTimeScale / 30;
    if (i) {
      const K = f - h, te = K > A, re = K < -1;
      if ((te || re) && (te ? ze.warn(`AVC: ${Im(K, !0)} ms (${K}dts) hole between fragments detected at ${t.toFixed(3)}`) : ze.warn(`AVC: ${Im(-K, !0)} ms (${K}dts) overlapping between fragments detected at ${t.toFixed(3)}`), !re || h >= a[0].pts || Ld)) {
        f = h;
        const be = a[0].pts - K;
        if (te)
          a[0].dts = f, a[0].pts = be;
        else
          for (let Pe = 0; Pe < a.length && !(a[Pe].dts > be); Pe++)
            a[Pe].dts -= K, a[Pe].pts -= K;
        ze.log(`Video: Initial PTS/DTS adjusted: ${Im(be, !0)}/${Im(f, !0)}, delta: ${Im(K, !0)} ms`);
      }
    }
    f = Math.max(0, f);
    let b = 0, M = 0, w = f;
    for (let K = 0; K < l; K++) {
      const te = a[K], re = te.units, be = re.length;
      let Pe = 0;
      for (let J = 0; J < be; J++)
        Pe += re[J].data.length;
      M += Pe, b += be, te.length = Pe, te.dts < w ? (te.dts = w, w += A / 4 | 0 || 1) : w = te.dts, v = Math.min(te.pts, v), g = Math.max(te.pts, g);
    }
    p = a[l - 1].dts;
    const C = M + 4 * b + 8;
    let T;
    try {
      T = new Uint8Array(C);
    } catch (K) {
      this.observer.emit(j.ERROR, j.ERROR, {
        type: qt.MUX_ERROR,
        details: Xe.REMUX_ALLOC_ERROR,
        fatal: !1,
        error: K,
        bytes: C,
        reason: `fail allocating video mdat ${C}`
      });
      return;
    }
    const E = new DataView(T.buffer);
    E.setUint32(0, C), T.set(ke.types.mdat, 4);
    let P = !1, L = Number.POSITIVE_INFINITY, I = Number.POSITIVE_INFINITY, N = Number.NEGATIVE_INFINITY, H = Number.NEGATIVE_INFINITY;
    for (let K = 0; K < l; K++) {
      const te = a[K], re = te.units;
      let be = 0;
      for (let fe = 0, Me = re.length; fe < Me; fe++) {
        const ye = re[fe], Ye = ye.data, Je = ye.data.byteLength;
        E.setUint32(u, Je), u += 4, T.set(Ye, u), u += Je, be += 4 + Je;
      }
      let Pe;
      if (K < l - 1)
        d = a[K + 1].dts - te.dts, Pe = a[K + 1].pts - te.pts;
      else {
        const fe = this.config, Me = K > 0 ? te.dts - a[K - 1].dts : A;
        if (Pe = K > 0 ? te.pts - a[K - 1].pts : A, fe.stretchShortVideoTrack && this.nextAudioPts !== null) {
          const ye = Math.floor(fe.maxBufferHole * r), Ye = (n ? v + n * r : this.nextAudioPts) - te.pts;
          Ye > ye ? (d = Ye - Me, d < 0 ? d = Me : P = !0, ze.log(`[mp4-remuxer]: It is approximately ${Ye / 90} ms to the next segment; using duration ${d / 90} ms for the last video frame.`)) : d = Me;
        } else
          d = Me;
      }
      const J = Math.round(te.pts - te.dts);
      L = Math.min(L, d), N = Math.max(N, d), I = Math.min(I, Pe), H = Math.max(H, Pe), o.push(new x_(te.key, d, be, J));
    }
    if (o.length) {
      if (Ld) {
        if (Ld < 70) {
          const K = o[0].flags;
          K.dependsOn = 2, K.isNonSync = 0;
        }
      } else if (fA && H - I < N - L && A / N < 0.025 && o[0].cts === 0) {
        ze.warn("Found irregular gaps in sample duration. Using PTS instead of DTS to determine MP4 sample duration.");
        let K = f;
        for (let te = 0, re = o.length; te < re; te++) {
          const be = K + o[te].duration, Pe = K + o[te].cts;
          if (te < re - 1) {
            const J = be + o[te + 1].cts;
            o[te].duration = J - Pe;
          } else
            o[te].duration = te ? o[te - 1].duration : A;
          o[te].cts = 0, K = be;
        }
      }
    }
    d = P || !d ? A : d, this.nextAvcDts = h = p + d, this.videoSampleDuration = d, this.isVideoContiguous = !0;
    const U = ke.moof(e.sequenceNumber++, f, as({}, e, {
      samples: o
    })), W = "video", X = {
      data1: U,
      data2: T,
      startPTS: v / r,
      endPTS: (g + d) / r,
      startDTS: f / r,
      endDTS: h / r,
      type: W,
      hasAudio: !1,
      hasVideo: !0,
      nb: o.length,
      dropped: e.dropped
    };
    return e.samples = [], e.dropped = 0, X;
  }
  getSamplesPerFrame(e) {
    switch (e.segmentCodec) {
      case "mp3":
        return tae;
      case "ac3":
        return iae;
      default:
        return y_;
    }
  }
  remuxAudio(e, t, i, n, r) {
    const a = e.inputTimeScale, o = e.samplerate ? e.samplerate : a, l = a / o, c = this.getSamplesPerFrame(e), h = c * l, u = this._initPTS, d = e.segmentCodec === "mp3" && this.typeSupported.mpeg, f = [], p = r !== void 0;
    let v = e.samples, g = d ? 0 : 8, m = this.nextAudioPts || -1;
    const x = t * a, y = u.baseTime * a / u.timescale;
    if (this.isAudioContiguous = i = i || v.length && m > 0 && (n && Math.abs(x - m) < 9e3 || Math.abs(va(v[0].pts - y, x) - m) < 20 * h), v.forEach(function(U) {
      U.pts = va(U.pts - y, x);
    }), !i || m < 0) {
      if (v = v.filter((U) => U.pts >= 0), !v.length)
        return;
      r === 0 ? m = 0 : n && !p ? m = Math.max(0, x) : m = v[0].pts;
    }
    if (e.segmentCodec === "aac") {
      const U = this.config.maxAudioFramesDrift;
      for (let W = 0, X = m; W < v.length; W++) {
        const K = v[W], te = K.pts, re = te - X, be = Math.abs(1e3 * re / a);
        if (re <= -U * h && p)
          W === 0 && (ze.warn(`Audio frame @ ${(te / a).toFixed(3)}s overlaps nextAudioPts by ${Math.round(1e3 * re / a)} ms.`), this.nextAudioPts = m = X = te);
        else if (re >= U * h && be < eae && p) {
          let Pe = Math.round(re / h);
          X = te - Pe * h, X < 0 && (Pe--, X += h), W === 0 && (this.nextAudioPts = m = X), ze.warn(`[mp4-remuxer]: Injecting ${Pe} audio frame @ ${(X / a).toFixed(3)}s due to ${Math.round(1e3 * re / a)} ms gap.`);
          for (let J = 0; J < Pe; J++) {
            const fe = Math.max(X, 0);
            let Me = v_.getSilentFrame(e.manifestCodec || e.codec, e.channelCount);
            Me || (ze.log("[mp4-remuxer]: Unable to get silent frame for given audio codec; duplicating last frame instead."), Me = K.unit.subarray()), v.splice(W, 0, {
              unit: Me,
              pts: fe
            }), X += h, W++;
          }
        }
        K.pts = X, X += h;
      }
    }
    let A = null, b = null, M, w = 0, C = v.length;
    for (; C--; )
      w += v[C].unit.byteLength;
    for (let U = 0, W = v.length; U < W; U++) {
      const X = v[U], K = X.unit;
      let te = X.pts;
      if (b !== null) {
        const be = f[U - 1];
        be.duration = Math.round((te - b) / l);
      } else if (i && e.segmentCodec === "aac" && (te = m), A = te, w > 0) {
        w += g;
        try {
          M = new Uint8Array(w);
        } catch (be) {
          this.observer.emit(j.ERROR, j.ERROR, {
            type: qt.MUX_ERROR,
            details: Xe.REMUX_ALLOC_ERROR,
            fatal: !1,
            error: be,
            bytes: w,
            reason: `fail allocating audio mdat ${w}`
          });
          return;
        }
        d || (new DataView(M.buffer).setUint32(0, w), M.set(ke.types.mdat, 4));
      } else
        return;
      M.set(K, g);
      const re = K.byteLength;
      g += re, f.push(new x_(!0, c, re, 0)), b = te;
    }
    const T = f.length;
    if (!T)
      return;
    const E = f[f.length - 1];
    this.nextAudioPts = m = b + l * E.duration;
    const P = d ? new Uint8Array(0) : ke.moof(e.sequenceNumber++, A / l, as({}, e, {
      samples: f
    }));
    e.samples = [];
    const L = A / a, I = m / a, H = {
      data1: P,
      data2: M,
      startPTS: L,
      endPTS: I,
      startDTS: L,
      endDTS: I,
      type: "audio",
      hasAudio: !0,
      hasVideo: !1,
      nb: T
    };
    return this.isAudioContiguous = !0, H;
  }
  remuxEmptyAudio(e, t, i, n) {
    const r = e.inputTimeScale, a = e.samplerate ? e.samplerate : r, o = r / a, l = this.nextAudioPts, c = this._initDTS, h = c.baseTime * 9e4 / c.timescale, u = (l !== null ? l : n.startDTS * r) + h, d = n.endDTS * r + h, f = o * y_, p = Math.ceil((d - u) / f), v = v_.getSilentFrame(e.manifestCodec || e.codec, e.channelCount);
    if (ze.warn("[mp4-remuxer]: remux empty Audio"), !v) {
      ze.trace("[mp4-remuxer]: Unable to remuxEmptyAudio since we were unable to get a silent frame for given audio codec");
      return;
    }
    const g = [];
    for (let m = 0; m < p; m++) {
      const x = u + m * f;
      g.push({
        unit: v,
        pts: x,
        dts: x
      });
    }
    return e.samples = g, this.remuxAudio(e, t, i, !1);
  }
}
function va(s, e) {
  let t;
  if (e === null)
    return s;
  for (e < s ? t = -8589934592 : t = 8589934592; Math.abs(s - e) > 4294967296; )
    s += t;
  return s;
}
function nae(s) {
  for (let e = 0; e < s.length; e++)
    if (s[e].key)
      return e;
  return -1;
}
function iB(s, e, t, i) {
  const n = s.samples.length;
  if (!n)
    return;
  const r = s.inputTimeScale;
  for (let o = 0; o < n; o++) {
    const l = s.samples[o];
    l.pts = va(l.pts - t.baseTime * r / t.timescale, e * r) / r, l.dts = va(l.dts - i.baseTime * r / i.timescale, e * r) / r;
  }
  const a = s.samples;
  return s.samples = [], {
    samples: a
  };
}
function nB(s, e, t) {
  const i = s.samples.length;
  if (!i)
    return;
  const n = s.inputTimeScale;
  for (let a = 0; a < i; a++) {
    const o = s.samples[a];
    o.pts = va(o.pts - t.baseTime * n / t.timescale, e * n) / n;
  }
  s.samples.sort((a, o) => a.pts - o.pts);
  const r = s.samples;
  return s.samples = [], {
    samples: r
  };
}
class x_ {
  constructor(e, t, i, n) {
    this.size = void 0, this.duration = void 0, this.cts = void 0, this.flags = void 0, this.duration = t, this.size = i, this.cts = n, this.flags = {
      isLeading: 0,
      isDependedOn: 0,
      hasRedundancy: 0,
      degradPrio: 0,
      dependsOn: e ? 2 : 1,
      isNonSync: e ? 0 : 1
    };
  }
}
class sae {
  constructor() {
    this.emitInitSegment = !1, this.audioCodec = void 0, this.videoCodec = void 0, this.initData = void 0, this.initPTS = null, this.initTracks = void 0, this.lastEndTime = null;
  }
  destroy() {
  }
  resetTimeStamp(e) {
    this.initPTS = e, this.lastEndTime = null;
  }
  resetNextTimestamp() {
    this.lastEndTime = null;
  }
  resetInitSegment(e, t, i, n) {
    this.audioCodec = t, this.videoCodec = i, this.generateInitSegment(dse(e, n)), this.emitInitSegment = !0;
  }
  generateInitSegment(e) {
    let {
      audioCodec: t,
      videoCodec: i
    } = this;
    if (!(e != null && e.byteLength)) {
      this.initTracks = void 0, this.initData = void 0;
      return;
    }
    const n = this.initData = _N(e);
    n.audio && (t = A_(n.audio, an.AUDIO)), n.video && (i = A_(n.video, an.VIDEO));
    const r = {};
    n.audio && n.video ? r.audiovideo = {
      container: "video/mp4",
      codec: t + "," + i,
      initSegment: e,
      id: "main"
    } : n.audio ? r.audio = {
      container: "audio/mp4",
      codec: t,
      initSegment: e,
      id: "audio"
    } : n.video ? r.video = {
      container: "video/mp4",
      codec: i,
      initSegment: e,
      id: "main"
    } : ze.warn("[passthrough-remuxer.ts]: initSegment does not contain moov or trak boxes."), this.initTracks = r;
  }
  remux(e, t, i, n, r, a) {
    var o, l;
    let {
      initPTS: c,
      lastEndTime: h
    } = this;
    const u = {
      audio: void 0,
      video: void 0,
      text: n,
      id3: i,
      initSegment: void 0
    };
    Bt(h) || (h = this.lastEndTime = r || 0);
    const d = t.samples;
    if (!(d != null && d.length))
      return u;
    const f = {
      initPTS: void 0,
      timescale: 1
    };
    let p = this.initData;
    if ((o = p) != null && o.length || (this.generateInitSegment(d), p = this.initData), !((l = p) != null && l.length))
      return ze.warn("[passthrough-remuxer.ts]: Failed to generate initSegment."), u;
    this.emitInitSegment && (f.tracks = this.initTracks, this.emitInitSegment = !1);
    const v = pse(d, p), g = fse(p, d), m = g === null ? r : g;
    (rae(c, m, r, v) || f.timescale !== c.timescale && a) && (f.initPTS = m - r, c && c.timescale === 1 && ze.warn(`Adjusting initPTS by ${f.initPTS - c.baseTime}`), this.initPTS = c = {
      baseTime: f.initPTS,
      timescale: 1
    });
    const x = e ? m - c.baseTime / c.timescale : h, y = x + v;
    gse(p, d, c.baseTime / c.timescale), v > 0 ? this.lastEndTime = y : (ze.warn("Duration parsed from mp4 should be greater than zero"), this.resetNextTimestamp());
    const A = !!p.audio, b = !!p.video;
    let M = "";
    A && (M += "audio"), b && (M += "video");
    const w = {
      data1: d,
      startPTS: x,
      startDTS: x,
      endPTS: y,
      endDTS: y,
      type: M,
      hasAudio: A,
      hasVideo: b,
      nb: 1,
      dropped: 0
    };
    return u.audio = w.type === "audio" ? w : void 0, u.video = w.type !== "audio" ? w : void 0, u.initSegment = f, u.id3 = iB(i, r, c, c), n.samples.length && (u.text = nB(n, r, c)), u;
  }
}
function rae(s, e, t, i) {
  if (s === null)
    return !0;
  const n = Math.max(i, 1), r = e - s.baseTime / s.timescale;
  return Math.abs(r - t) > n;
}
function A_(s, e) {
  const t = s == null ? void 0 : s.codec;
  if (t && t.length > 4)
    return t;
  if (e === an.AUDIO) {
    if (t === "ec-3" || t === "ac-3" || t === "alac")
      return t;
    if (t === "fLaC" || t === "Opus")
      return gx(t, !1);
    const i = "mp4a.40.5";
    return ze.info(`Parsed audio codec "${t}" or audio object type not handled. Using "${i}"`), i;
  }
  return ze.warn(`Unhandled video codec "${t}"`), t === "hvc1" || t === "hev1" ? "hvc1.1.6.L120.90" : t === "av01" ? "av01.0.04M.08" : "avc1.42e01e";
}
let Y0;
try {
  Y0 = self.performance.now.bind(self.performance);
} catch {
  ze.debug("Unable to use Performance API on this environment"), Y0 = Ep == null ? void 0 : Ep.Date.now;
}
const ky = [{
  demux: Wre,
  remux: sae
}, {
  demux: oc,
  remux: zy
}, {
  demux: Hre,
  remux: zy
}, {
  demux: Jre,
  remux: zy
}];
ky.splice(2, 0, {
  demux: Xre,
  remux: zy
});
class b_ {
  constructor(e, t, i, n, r) {
    this.async = !1, this.observer = void 0, this.typeSupported = void 0, this.config = void 0, this.vendor = void 0, this.id = void 0, this.demuxer = void 0, this.remuxer = void 0, this.decrypter = void 0, this.probe = void 0, this.decryptionPromise = null, this.transmuxConfig = void 0, this.currentTransmuxState = void 0, this.observer = e, this.typeSupported = t, this.config = i, this.vendor = n, this.id = r;
  }
  configure(e) {
    this.transmuxConfig = e, this.decrypter && this.decrypter.reset();
  }
  push(e, t, i, n) {
    const r = i.transmuxing;
    r.executeStart = Y0();
    let a = new Uint8Array(e);
    const {
      currentTransmuxState: o,
      transmuxConfig: l
    } = this;
    n && (this.currentTransmuxState = n);
    const {
      contiguous: c,
      discontinuity: h,
      trackSwitch: u,
      accurateTimeOffset: d,
      timeOffset: f,
      initSegmentChange: p
    } = n || o, {
      audioCodec: v,
      videoCodec: g,
      defaultInitPts: m,
      duration: x,
      initSegmentData: y
    } = l, A = aae(a, t);
    if (A && A.method === "AES-128") {
      const C = this.getDecrypter();
      if (C.isSync()) {
        let T = C.softwareDecrypt(a, A.key.buffer, A.iv.buffer);
        if (i.part > -1 && (T = C.flush()), !T)
          return r.executeEnd = Y0(), pA(i);
        a = new Uint8Array(T);
      } else
        return this.decryptionPromise = C.webCryptoDecrypt(a, A.key.buffer, A.iv.buffer).then((T) => {
          const E = this.push(T, null, i);
          return this.decryptionPromise = null, E;
        }), this.decryptionPromise;
    }
    const b = this.needsProbing(h, u);
    if (b) {
      const C = this.configureTransmuxer(a);
      if (C)
        return ze.warn(`[transmuxer] ${C.message}`), this.observer.emit(j.ERROR, j.ERROR, {
          type: qt.MEDIA_ERROR,
          details: Xe.FRAG_PARSING_ERROR,
          fatal: !1,
          error: C,
          reason: C.message
        }), r.executeEnd = Y0(), pA(i);
    }
    (h || u || p || b) && this.resetInitSegment(y, v, g, x, t), (h || p || b) && this.resetInitialTimestamp(m), c || this.resetContiguity();
    const M = this.transmux(a, A, f, d, i), w = this.currentTransmuxState;
    return w.contiguous = !0, w.discontinuity = !1, w.trackSwitch = !1, r.executeEnd = Y0(), M;
  }
  // Due to data caching, flush calls can produce more than one TransmuxerResult (hence the Array type)
  flush(e) {
    const t = e.transmuxing;
    t.executeStart = Y0();
    const {
      decrypter: i,
      currentTransmuxState: n,
      decryptionPromise: r
    } = this;
    if (r)
      return r.then(() => this.flush(e));
    const a = [], {
      timeOffset: o
    } = n;
    if (i) {
      const u = i.flush();
      u && a.push(this.push(u, null, e));
    }
    const {
      demuxer: l,
      remuxer: c
    } = this;
    if (!l || !c)
      return t.executeEnd = Y0(), [pA(e)];
    const h = l.flush(o);
    return Gy(h) ? h.then((u) => (this.flushRemux(a, u, e), a)) : (this.flushRemux(a, h, e), a);
  }
  flushRemux(e, t, i) {
    const {
      audioTrack: n,
      videoTrack: r,
      id3Track: a,
      textTrack: o
    } = t, {
      accurateTimeOffset: l,
      timeOffset: c
    } = this.currentTransmuxState;
    ze.log(`[transmuxer.ts]: Flushed fragment ${i.sn}${i.part > -1 ? " p: " + i.part : ""} of level ${i.level}`);
    const h = this.remuxer.remux(n, r, a, o, c, l, !0, this.id);
    e.push({
      remuxResult: h,
      chunkMeta: i
    }), i.transmuxing.executeEnd = Y0();
  }
  resetInitialTimestamp(e) {
    const {
      demuxer: t,
      remuxer: i
    } = this;
    !t || !i || (t.resetTimeStamp(e), i.resetTimeStamp(e));
  }
  resetContiguity() {
    const {
      demuxer: e,
      remuxer: t
    } = this;
    !e || !t || (e.resetContiguity(), t.resetNextTimestamp());
  }
  resetInitSegment(e, t, i, n, r) {
    const {
      demuxer: a,
      remuxer: o
    } = this;
    !a || !o || (a.resetInitSegment(e, t, i, n), o.resetInitSegment(e, t, i, r));
  }
  destroy() {
    this.demuxer && (this.demuxer.destroy(), this.demuxer = void 0), this.remuxer && (this.remuxer.destroy(), this.remuxer = void 0);
  }
  transmux(e, t, i, n, r) {
    let a;
    return t && t.method === "SAMPLE-AES" ? a = this.transmuxSampleAes(e, t, i, n, r) : a = this.transmuxUnencrypted(e, i, n, r), a;
  }
  transmuxUnencrypted(e, t, i, n) {
    const {
      audioTrack: r,
      videoTrack: a,
      id3Track: o,
      textTrack: l
    } = this.demuxer.demux(e, t, !1, !this.config.progressive);
    return {
      remuxResult: this.remuxer.remux(r, a, o, l, t, i, !1, this.id),
      chunkMeta: n
    };
  }
  transmuxSampleAes(e, t, i, n, r) {
    return this.demuxer.demuxSampleAes(e, t, i).then((a) => ({
      remuxResult: this.remuxer.remux(a.audioTrack, a.videoTrack, a.id3Track, a.textTrack, i, n, !1, this.id),
      chunkMeta: r
    }));
  }
  configureTransmuxer(e) {
    const {
      config: t,
      observer: i,
      typeSupported: n,
      vendor: r
    } = this;
    let a;
    for (let d = 0, f = ky.length; d < f; d++) {
      var o;
      if ((o = ky[d].demux) != null && o.probe(e)) {
        a = ky[d];
        break;
      }
    }
    if (!a)
      return new Error("Failed to find demuxer by probing fragment data");
    const l = this.demuxer, c = this.remuxer, h = a.remux, u = a.demux;
    (!c || !(c instanceof h)) && (this.remuxer = new h(i, t, n, r)), (!l || !(l instanceof u)) && (this.demuxer = new u(i, t, n), this.probe = u.probe);
  }
  needsProbing(e, t) {
    return !this.demuxer || !this.remuxer || e || t;
  }
  getDecrypter() {
    let e = this.decrypter;
    return e || (e = this.decrypter = new $S(this.config)), e;
  }
}
function aae(s, e) {
  let t = null;
  return s.byteLength > 0 && (e == null ? void 0 : e.key) != null && e.iv !== null && e.method != null && (t = e), t;
}
const pA = (s) => ({
  remuxResult: {},
  chunkMeta: s
});
function Gy(s) {
  return "then" in s && s.then instanceof Function;
}
class oae {
  constructor(e, t, i, n, r) {
    this.audioCodec = void 0, this.videoCodec = void 0, this.initSegmentData = void 0, this.duration = void 0, this.defaultInitPts = void 0, this.audioCodec = e, this.videoCodec = t, this.initSegmentData = i, this.duration = n, this.defaultInitPts = r || null;
  }
}
class lae {
  constructor(e, t, i, n, r, a) {
    this.discontinuity = void 0, this.contiguous = void 0, this.accurateTimeOffset = void 0, this.trackSwitch = void 0, this.timeOffset = void 0, this.initSegmentChange = void 0, this.discontinuity = e, this.contiguous = t, this.accurateTimeOffset = i, this.trackSwitch = n, this.timeOffset = r, this.initSegmentChange = a;
  }
}
var sB = { exports: {} };
(function(s) {
  var e = Object.prototype.hasOwnProperty, t = "~";
  function i() {
  }
  Object.create && (i.prototype = /* @__PURE__ */ Object.create(null), new i().__proto__ || (t = !1));
  function n(l, c, h) {
    this.fn = l, this.context = c, this.once = h || !1;
  }
  function r(l, c, h, u, d) {
    if (typeof h != "function")
      throw new TypeError("The listener must be a function");
    var f = new n(h, u || l, d), p = t ? t + c : c;
    return l._events[p] ? l._events[p].fn ? l._events[p] = [l._events[p], f] : l._events[p].push(f) : (l._events[p] = f, l._eventsCount++), l;
  }
  function a(l, c) {
    --l._eventsCount === 0 ? l._events = new i() : delete l._events[c];
  }
  function o() {
    this._events = new i(), this._eventsCount = 0;
  }
  o.prototype.eventNames = function() {
    var c = [], h, u;
    if (this._eventsCount === 0)
      return c;
    for (u in h = this._events)
      e.call(h, u) && c.push(t ? u.slice(1) : u);
    return Object.getOwnPropertySymbols ? c.concat(Object.getOwnPropertySymbols(h)) : c;
  }, o.prototype.listeners = function(c) {
    var h = t ? t + c : c, u = this._events[h];
    if (!u)
      return [];
    if (u.fn)
      return [u.fn];
    for (var d = 0, f = u.length, p = new Array(f); d < f; d++)
      p[d] = u[d].fn;
    return p;
  }, o.prototype.listenerCount = function(c) {
    var h = t ? t + c : c, u = this._events[h];
    return u ? u.fn ? 1 : u.length : 0;
  }, o.prototype.emit = function(c, h, u, d, f, p) {
    var v = t ? t + c : c;
    if (!this._events[v])
      return !1;
    var g = this._events[v], m = arguments.length, x, y;
    if (g.fn) {
      switch (g.once && this.removeListener(c, g.fn, void 0, !0), m) {
        case 1:
          return g.fn.call(g.context), !0;
        case 2:
          return g.fn.call(g.context, h), !0;
        case 3:
          return g.fn.call(g.context, h, u), !0;
        case 4:
          return g.fn.call(g.context, h, u, d), !0;
        case 5:
          return g.fn.call(g.context, h, u, d, f), !0;
        case 6:
          return g.fn.call(g.context, h, u, d, f, p), !0;
      }
      for (y = 1, x = new Array(m - 1); y < m; y++)
        x[y - 1] = arguments[y];
      g.fn.apply(g.context, x);
    } else {
      var A = g.length, b;
      for (y = 0; y < A; y++)
        switch (g[y].once && this.removeListener(c, g[y].fn, void 0, !0), m) {
          case 1:
            g[y].fn.call(g[y].context);
            break;
          case 2:
            g[y].fn.call(g[y].context, h);
            break;
          case 3:
            g[y].fn.call(g[y].context, h, u);
            break;
          case 4:
            g[y].fn.call(g[y].context, h, u, d);
            break;
          default:
            if (!x)
              for (b = 1, x = new Array(m - 1); b < m; b++)
                x[b - 1] = arguments[b];
            g[y].fn.apply(g[y].context, x);
        }
    }
    return !0;
  }, o.prototype.on = function(c, h, u) {
    return r(this, c, h, u, !1);
  }, o.prototype.once = function(c, h, u) {
    return r(this, c, h, u, !0);
  }, o.prototype.removeListener = function(c, h, u, d) {
    var f = t ? t + c : c;
    if (!this._events[f])
      return this;
    if (!h)
      return a(this, f), this;
    var p = this._events[f];
    if (p.fn)
      p.fn === h && (!d || p.once) && (!u || p.context === u) && a(this, f);
    else {
      for (var v = 0, g = [], m = p.length; v < m; v++)
        (p[v].fn !== h || d && !p[v].once || u && p[v].context !== u) && g.push(p[v]);
      g.length ? this._events[f] = g.length === 1 ? g[0] : g : a(this, f);
    }
    return this;
  }, o.prototype.removeAllListeners = function(c) {
    var h;
    return c ? (h = t ? t + c : c, this._events[h] && a(this, h)) : (this._events = new i(), this._eventsCount = 0), this;
  }, o.prototype.off = o.prototype.removeListener, o.prototype.addListener = o.prototype.on, o.prefixed = t, o.EventEmitter = o, s.exports = o;
})(sB);
var cae = sB.exports, rw = /* @__PURE__ */ Ine(cae);
class rB {
  constructor(e, t, i, n) {
    this.error = null, this.hls = void 0, this.id = void 0, this.observer = void 0, this.frag = null, this.part = null, this.useWorker = void 0, this.workerContext = null, this.onwmsg = void 0, this.transmuxer = null, this.onTransmuxComplete = void 0, this.onFlush = void 0;
    const r = e.config;
    this.hls = e, this.id = t, this.useWorker = !!r.enableWorker, this.onTransmuxComplete = i, this.onFlush = n;
    const a = (h, u) => {
      u = u || {}, u.frag = this.frag, u.id = this.id, h === j.ERROR && (this.error = u.error), this.hls.trigger(h, u);
    };
    this.observer = new rw(), this.observer.on(j.FRAG_DECRYPTED, a), this.observer.on(j.ERROR, a);
    const o = Nu(r.preferManagedMediaSource) || {
      isTypeSupported: () => !1
    }, l = {
      mpeg: o.isTypeSupported("audio/mpeg"),
      mp3: o.isTypeSupported('audio/mp4; codecs="mp3"'),
      ac3: o.isTypeSupported('audio/mp4; codecs="ac-3"')
    }, c = navigator.vendor;
    if (this.useWorker && typeof Worker < "u" && (r.workerPath || _re())) {
      try {
        r.workerPath ? (ze.log(`loading Web Worker ${r.workerPath} for "${t}"`), this.workerContext = Rre(r.workerPath)) : (ze.log(`injecting Web Worker for "${t}"`), this.workerContext = Pre()), this.onwmsg = (d) => this.onWorkerMessage(d);
        const {
          worker: u
        } = this.workerContext;
        u.addEventListener("message", this.onwmsg), u.onerror = (d) => {
          const f = new Error(`${d.message}  (${d.filename}:${d.lineno})`);
          r.enableWorker = !1, ze.warn(`Error in "${t}" Web Worker, fallback to inline`), this.hls.trigger(j.ERROR, {
            type: qt.OTHER_ERROR,
            details: Xe.INTERNAL_EXCEPTION,
            fatal: !1,
            event: "demuxerWorker",
            error: f
          });
        }, u.postMessage({
          cmd: "init",
          typeSupported: l,
          vendor: c,
          id: t,
          config: JSON.stringify(r)
        });
      } catch (u) {
        ze.warn(`Error setting up "${t}" Web Worker, fallback to inline`, u), this.resetWorker(), this.error = null, this.transmuxer = new b_(this.observer, l, r, c, t);
      }
      return;
    }
    this.transmuxer = new b_(this.observer, l, r, c, t);
  }
  resetWorker() {
    if (this.workerContext) {
      const {
        worker: e,
        objectURL: t
      } = this.workerContext;
      t && self.URL.revokeObjectURL(t), e.removeEventListener("message", this.onwmsg), e.onerror = null, e.terminate(), this.workerContext = null;
    }
  }
  destroy() {
    if (this.workerContext)
      this.resetWorker(), this.onwmsg = void 0;
    else {
      const t = this.transmuxer;
      t && (t.destroy(), this.transmuxer = null);
    }
    const e = this.observer;
    e && e.removeAllListeners(), this.frag = null, this.observer = null, this.hls = null;
  }
  push(e, t, i, n, r, a, o, l, c, h) {
    var u, d;
    c.transmuxing.start = self.performance.now();
    const {
      transmuxer: f
    } = this, p = a ? a.start : r.start, v = r.decryptdata, g = this.frag, m = !(g && r.cc === g.cc), x = !(g && c.level === g.level), y = g ? c.sn - g.sn : -1, A = this.part ? c.part - this.part.index : -1, b = y === 0 && c.id > 1 && c.id === (g == null ? void 0 : g.stats.chunkCount), M = !x && (y === 1 || y === 0 && (A === 1 || b && A <= 0)), w = self.performance.now();
    (x || y || r.stats.parsing.start === 0) && (r.stats.parsing.start = w), a && (A || !M) && (a.stats.parsing.start = w);
    const C = !(g && ((u = r.initSegment) == null ? void 0 : u.url) === ((d = g.initSegment) == null ? void 0 : d.url)), T = new lae(m, M, l, x, p, C);
    if (!M || m || C) {
      ze.log(`[transmuxer-interface, ${r.type}]: Starting new transmux session for sn: ${c.sn} p: ${c.part} level: ${c.level} id: ${c.id}
        discontinuity: ${m}
        trackSwitch: ${x}
        contiguous: ${M}
        accurateTimeOffset: ${l}
        timeOffset: ${p}
        initSegmentChange: ${C}`);
      const E = new oae(i, n, t, o, h);
      this.configureTransmuxer(E);
    }
    if (this.frag = r, this.part = a, this.workerContext)
      this.workerContext.worker.postMessage({
        cmd: "demux",
        data: e,
        decryptdata: v,
        chunkMeta: c,
        state: T
      }, e instanceof ArrayBuffer ? [e] : []);
    else if (f) {
      const E = f.push(e, v, c, T);
      Gy(E) ? (f.async = !0, E.then((P) => {
        this.handleTransmuxComplete(P);
      }).catch((P) => {
        this.transmuxerError(P, c, "transmuxer-interface push error");
      })) : (f.async = !1, this.handleTransmuxComplete(E));
    }
  }
  flush(e) {
    e.transmuxing.start = self.performance.now();
    const {
      transmuxer: t
    } = this;
    if (this.workerContext)
      this.workerContext.worker.postMessage({
        cmd: "flush",
        chunkMeta: e
      });
    else if (t) {
      let i = t.flush(e);
      Gy(i) || t.async ? (Gy(i) || (i = Promise.resolve(i)), i.then((r) => {
        this.handleFlushResult(r, e);
      }).catch((r) => {
        this.transmuxerError(r, e, "transmuxer-interface flush error");
      })) : this.handleFlushResult(i, e);
    }
  }
  transmuxerError(e, t, i) {
    this.hls && (this.error = e, this.hls.trigger(j.ERROR, {
      type: qt.MEDIA_ERROR,
      details: Xe.FRAG_PARSING_ERROR,
      chunkMeta: t,
      fatal: !1,
      error: e,
      err: e,
      reason: i
    }));
  }
  handleFlushResult(e, t) {
    e.forEach((i) => {
      this.handleTransmuxComplete(i);
    }), this.onFlush(t);
  }
  onWorkerMessage(e) {
    const t = e.data, i = this.hls;
    switch (t.event) {
      case "init": {
        var n;
        const r = (n = this.workerContext) == null ? void 0 : n.objectURL;
        r && self.URL.revokeObjectURL(r);
        break;
      }
      case "transmuxComplete": {
        this.handleTransmuxComplete(t.data);
        break;
      }
      case "flush": {
        this.onFlush(t.data);
        break;
      }
      case "workerLog":
        ze[t.data.logType] && ze[t.data.logType](t.data.message);
        break;
      default: {
        t.data = t.data || {}, t.data.frag = this.frag, t.data.id = this.id, i.trigger(t.event, t.data);
        break;
      }
    }
  }
  configureTransmuxer(e) {
    const {
      transmuxer: t
    } = this;
    this.workerContext ? this.workerContext.worker.postMessage({
      cmd: "configure",
      config: e
    }) : t && t.configure(e);
  }
  handleTransmuxComplete(e) {
    e.chunkMeta.transmuxing.end = self.performance.now(), this.onTransmuxComplete(e);
  }
}
function aB(s, e) {
  if (s.length !== e.length)
    return !1;
  for (let t = 0; t < s.length; t++)
    if (!Cp(s[t].attrs, e[t].attrs))
      return !1;
  return !0;
}
function Cp(s, e, t) {
  const i = s["STABLE-RENDITION-ID"];
  return i && !t ? i === e["STABLE-RENDITION-ID"] : !(t || ["LANGUAGE", "NAME", "CHARACTERISTICS", "AUTOSELECT", "DEFAULT", "FORCED", "ASSOC-LANGUAGE"]).some((n) => s[n] !== e[n]);
}
function Ib(s, e) {
  return e.label.toLowerCase() === s.name.toLowerCase() && (!e.language || e.language.toLowerCase() === (s.lang || "").toLowerCase());
}
const M_ = 100;
class hae extends ew {
  constructor(e, t, i) {
    super(e, t, i, "[audio-stream-controller]", jt.AUDIO), this.videoBuffer = null, this.videoTrackCC = -1, this.waitingVideoCC = -1, this.bufferedTrack = null, this.switchingTrack = null, this.trackId = -1, this.waitingData = null, this.mainDetails = null, this.flushing = !1, this.bufferFlushed = !1, this.cachedTrackLoadedData = null, this._registerListeners();
  }
  onHandlerDestroying() {
    this._unregisterListeners(), super.onHandlerDestroying(), this.mainDetails = null, this.bufferedTrack = null, this.switchingTrack = null;
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.LEVEL_LOADED, this.onLevelLoaded, this), e.on(j.AUDIO_TRACKS_UPDATED, this.onAudioTracksUpdated, this), e.on(j.AUDIO_TRACK_SWITCHING, this.onAudioTrackSwitching, this), e.on(j.AUDIO_TRACK_LOADED, this.onAudioTrackLoaded, this), e.on(j.ERROR, this.onError, this), e.on(j.BUFFER_RESET, this.onBufferReset, this), e.on(j.BUFFER_CREATED, this.onBufferCreated, this), e.on(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.on(j.BUFFER_FLUSHED, this.onBufferFlushed, this), e.on(j.INIT_PTS_FOUND, this.onInitPtsFound, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.LEVEL_LOADED, this.onLevelLoaded, this), e.off(j.AUDIO_TRACKS_UPDATED, this.onAudioTracksUpdated, this), e.off(j.AUDIO_TRACK_SWITCHING, this.onAudioTrackSwitching, this), e.off(j.AUDIO_TRACK_LOADED, this.onAudioTrackLoaded, this), e.off(j.ERROR, this.onError, this), e.off(j.BUFFER_RESET, this.onBufferReset, this), e.off(j.BUFFER_CREATED, this.onBufferCreated, this), e.off(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.off(j.BUFFER_FLUSHED, this.onBufferFlushed, this), e.off(j.INIT_PTS_FOUND, this.onInitPtsFound, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  // INIT_PTS_FOUND is triggered when the video track parsed in the stream-controller has a new PTS value
  onInitPtsFound(e, {
    frag: t,
    id: i,
    initPTS: n,
    timescale: r
  }) {
    if (i === "main") {
      const a = t.cc;
      this.initPTS[t.cc] = {
        baseTime: n,
        timescale: r
      }, this.log(`InitPTS for cc: ${a} found from main: ${n}`), this.videoTrackCC = a, this.state === pt.WAITING_INIT_PTS && this.tick();
    }
  }
  startLoad(e) {
    if (!this.levels) {
      this.startPosition = e, this.state = pt.STOPPED;
      return;
    }
    const t = this.lastCurrentTime;
    this.stopLoad(), this.setInterval(M_), t > 0 && e === -1 ? (this.log(`Override startPosition with lastCurrentTime @${t.toFixed(3)}`), e = t, this.state = pt.IDLE) : (this.loadedmetadata = !1, this.state = pt.WAITING_TRACK), this.nextLoadPosition = this.startPosition = this.lastCurrentTime = e, this.tick();
  }
  doTick() {
    switch (this.state) {
      case pt.IDLE:
        this.doTickIdle();
        break;
      case pt.WAITING_TRACK: {
        var e;
        const {
          levels: i,
          trackId: n
        } = this, r = i == null || (e = i[n]) == null ? void 0 : e.details;
        if (r) {
          if (this.waitForCdnTuneIn(r))
            break;
          this.state = pt.WAITING_INIT_PTS;
        }
        break;
      }
      case pt.FRAG_LOADING_WAITING_RETRY: {
        var t;
        const i = performance.now(), n = this.retryDate;
        if (!n || i >= n || (t = this.media) != null && t.seeking) {
          const {
            levels: r,
            trackId: a
          } = this;
          this.log("RetryDate reached, switch back to IDLE state"), this.resetStartWhenNotLoaded((r == null ? void 0 : r[a]) || null), this.state = pt.IDLE;
        }
        break;
      }
      case pt.WAITING_INIT_PTS: {
        const i = this.waitingData;
        if (i) {
          const {
            frag: n,
            part: r,
            cache: a,
            complete: o
          } = i;
          if (this.initPTS[n.cc] !== void 0) {
            this.waitingData = null, this.waitingVideoCC = -1, this.state = pt.FRAG_LOADING;
            const l = a.flush(), c = {
              frag: n,
              part: r,
              payload: l,
              networkDetails: null
            };
            this._handleFragmentLoadProgress(c), o && super._handleFragmentLoadComplete(c);
          } else if (this.videoTrackCC !== this.waitingVideoCC)
            this.log(`Waiting fragment cc (${n.cc}) cancelled because video is at cc ${this.videoTrackCC}`), this.clearWaitingFragment();
          else {
            const l = this.getLoadPosition(), c = Mn.bufferInfo(this.mediaBuffer, l, this.config.maxBufferHole);
            Db(c.end, this.config.maxFragLookUpTolerance, n) < 0 && (this.log(`Waiting fragment cc (${n.cc}) @ ${n.start} cancelled because another fragment at ${c.end} is needed`), this.clearWaitingFragment());
          }
        } else
          this.state = pt.IDLE;
      }
    }
    this.onTickEnd();
  }
  clearWaitingFragment() {
    const e = this.waitingData;
    e && (this.fragmentTracker.removeFragment(e.frag), this.waitingData = null, this.waitingVideoCC = -1, this.state = pt.IDLE);
  }
  resetLoadingState() {
    this.clearWaitingFragment(), super.resetLoadingState();
  }
  onTickEnd() {
    const {
      media: e
    } = this;
    e != null && e.readyState && (this.lastCurrentTime = e.currentTime);
  }
  doTickIdle() {
    const {
      hls: e,
      levels: t,
      media: i,
      trackId: n
    } = this, r = e.config;
    if (!i && (this.startFragRequested || !r.startFragPrefetch) || !(t != null && t[n]))
      return;
    const a = t[n], o = a.details;
    if (!o || o.live && this.levelLastLoaded !== a || this.waitForCdnTuneIn(o)) {
      this.state = pt.WAITING_TRACK;
      return;
    }
    const l = this.mediaBuffer ? this.mediaBuffer : this.media;
    this.bufferFlushed && l && (this.bufferFlushed = !1, this.afterBufferFlushed(l, an.AUDIO, jt.AUDIO));
    const c = this.getFwdBufferInfo(l, jt.AUDIO);
    if (c === null)
      return;
    const {
      bufferedTrack: h,
      switchingTrack: u
    } = this;
    if (!u && this._streamEnded(c, o)) {
      e.trigger(j.BUFFER_EOS, {
        type: "audio"
      }), this.state = pt.ENDED;
      return;
    }
    const d = this.getFwdBufferInfo(this.videoBuffer ? this.videoBuffer : this.media, jt.MAIN), f = c.len, p = this.getMaxBufferLength(d == null ? void 0 : d.len), v = o.fragments, g = v[0].start;
    let m = this.flushing ? this.getLoadPosition() : c.end;
    if (u && i) {
      const b = this.getLoadPosition();
      h && !Cp(u.attrs, h.attrs) && (m = b), o.PTSKnown && b < g && (c.end > g || c.nextStart) && (this.log("Alt audio track ahead of main track, seek to start of alt audio track"), i.currentTime = g + 0.05);
    }
    if (f >= p && !u && m < v[v.length - 1].start)
      return;
    let x = this.getNextFragment(m, o), y = !1;
    if (x && this.isLoopLoading(x, m) && (y = !!x.gap, x = this.getNextFragmentLoopLoading(x, o, c, jt.MAIN, p)), !x) {
      this.bufferFlushed = !0;
      return;
    }
    const A = d && x.start > d.end + o.targetduration;
    if (A || // Or wait for main buffer after buffing some audio
    !(d != null && d.len) && c.len) {
      const b = this.getAppendedFrag(x.start, jt.MAIN);
      if (b === null || (y || (y = !!b.gap || !!A && d.len === 0), A && !y || y && c.nextStart && c.nextStart < b.end))
        return;
    }
    this.loadFragment(x, a, m);
  }
  getMaxBufferLength(e) {
    const t = super.getMaxBufferLength();
    return e ? Math.min(Math.max(t, e), this.config.maxMaxBufferLength) : t;
  }
  onMediaDetaching() {
    this.videoBuffer = null, this.bufferFlushed = this.flushing = !1, super.onMediaDetaching();
  }
  onAudioTracksUpdated(e, {
    audioTracks: t
  }) {
    this.resetTransmuxer(), this.levels = t.map((i) => new Tp(i));
  }
  onAudioTrackSwitching(e, t) {
    const i = !!t.url;
    this.trackId = t.id;
    const {
      fragCurrent: n
    } = this;
    n && (n.abortRequests(), this.removeUnbufferedFrags(n.start)), this.resetLoadingState(), i ? this.setInterval(M_) : this.resetTransmuxer(), i ? (this.switchingTrack = t, this.state = pt.IDLE, this.flushAudioIfNeeded(t)) : (this.switchingTrack = null, this.bufferedTrack = t, this.state = pt.STOPPED), this.tick();
  }
  onManifestLoading() {
    this.fragmentTracker.removeAllFragments(), this.startPosition = this.lastCurrentTime = 0, this.bufferFlushed = this.flushing = !1, this.levels = this.mainDetails = this.waitingData = this.bufferedTrack = this.cachedTrackLoadedData = this.switchingTrack = null, this.startFragRequested = !1, this.trackId = this.videoTrackCC = this.waitingVideoCC = -1;
  }
  onLevelLoaded(e, t) {
    this.mainDetails = t.details, this.cachedTrackLoadedData !== null && (this.hls.trigger(j.AUDIO_TRACK_LOADED, this.cachedTrackLoadedData), this.cachedTrackLoadedData = null);
  }
  onAudioTrackLoaded(e, t) {
    var i;
    if (this.mainDetails == null) {
      this.cachedTrackLoadedData = t;
      return;
    }
    const {
      levels: n
    } = this, {
      details: r,
      id: a
    } = t;
    if (!n) {
      this.warn(`Audio tracks were reset while loading level ${a}`);
      return;
    }
    this.log(`Audio track ${a} loaded [${r.startSN},${r.endSN}]${r.lastPartSn ? `[part-${r.lastPartSn}-${r.lastPartIndex}]` : ""},duration:${r.totalduration}`);
    const o = n[a];
    let l = 0;
    if (r.live || (i = o.details) != null && i.live) {
      this.checkLiveUpdate(r);
      const h = this.mainDetails;
      if (r.deltaUpdateFailed || !h)
        return;
      if (!o.details && r.hasProgramDateTime && h.hasProgramDateTime)
        bx(r, h), l = r.fragments[0].start;
      else {
        var c;
        l = this.alignPlaylists(r, o.details, (c = this.levelLastLoaded) == null ? void 0 : c.details);
      }
    }
    o.details = r, this.levelLastLoaded = o, !this.startFragRequested && (this.mainDetails || !r.live) && this.setStartPosition(this.mainDetails || r, l), this.state === pt.WAITING_TRACK && !this.waitForCdnTuneIn(r) && (this.state = pt.IDLE), this.tick();
  }
  _handleFragmentLoadProgress(e) {
    var t;
    const {
      frag: i,
      part: n,
      payload: r
    } = e, {
      config: a,
      trackId: o,
      levels: l
    } = this;
    if (!l) {
      this.warn(`Audio tracks were reset while fragment load was in progress. Fragment ${i.sn} of level ${i.level} will not be buffered`);
      return;
    }
    const c = l[o];
    if (!c) {
      this.warn("Audio track is undefined on fragment load progress");
      return;
    }
    const h = c.details;
    if (!h) {
      this.warn("Audio track details undefined on fragment load progress"), this.removeUnbufferedFrags(i.start);
      return;
    }
    const u = a.defaultAudioCodec || c.audioCodec || "mp4a.40.2";
    let d = this.transmuxer;
    d || (d = this.transmuxer = new rB(this.hls, jt.AUDIO, this._handleTransmuxComplete.bind(this), this._handleTransmuxerFlush.bind(this)));
    const f = this.initPTS[i.cc], p = (t = i.initSegment) == null ? void 0 : t.data;
    if (f !== void 0) {
      const g = n ? n.index : -1, m = g !== -1, x = new QS(i.level, i.sn, i.stats.chunkCount, r.byteLength, g, m);
      d.push(r, p, u, "", i, n, h.totalduration, !1, x, f);
    } else {
      this.log(`Unknown video PTS for cc ${i.cc}, waiting for video PTS before demuxing audio frag ${i.sn} of [${h.startSN} ,${h.endSN}],track ${o}`);
      const {
        cache: v
      } = this.waitingData = this.waitingData || {
        frag: i,
        part: n,
        cache: new VN(),
        complete: !1
      };
      v.push(new Uint8Array(r)), this.waitingVideoCC = this.videoTrackCC, this.state = pt.WAITING_INIT_PTS;
    }
  }
  _handleFragmentLoadComplete(e) {
    if (this.waitingData) {
      this.waitingData.complete = !0;
      return;
    }
    super._handleFragmentLoadComplete(e);
  }
  onBufferReset() {
    this.mediaBuffer = this.videoBuffer = null, this.loadedmetadata = !1;
  }
  onBufferCreated(e, t) {
    const i = t.tracks.audio;
    i && (this.mediaBuffer = i.buffer || null), t.tracks.video && (this.videoBuffer = t.tracks.video.buffer || null);
  }
  onFragBuffered(e, t) {
    const {
      frag: i,
      part: n
    } = t;
    if (i.type !== jt.AUDIO) {
      if (!this.loadedmetadata && i.type === jt.MAIN) {
        const r = this.videoBuffer || this.media;
        r && Mn.getBuffered(r).length && (this.loadedmetadata = !0);
      }
      return;
    }
    if (this.fragContextChanged(i)) {
      this.warn(`Fragment ${i.sn}${n ? " p: " + n.index : ""} of level ${i.level} finished buffering, but was aborted. state: ${this.state}, audioSwitch: ${this.switchingTrack ? this.switchingTrack.name : "false"}`);
      return;
    }
    if (i.sn !== "initSegment") {
      this.fragPrevious = i;
      const r = this.switchingTrack;
      r && (this.bufferedTrack = r, this.switchingTrack = null, this.hls.trigger(j.AUDIO_TRACK_SWITCHED, Ls({}, r)));
    }
    this.fragBufferedComplete(i, n);
  }
  onError(e, t) {
    var i;
    if (t.fatal) {
      this.state = pt.ERROR;
      return;
    }
    switch (t.details) {
      case Xe.FRAG_GAP:
      case Xe.FRAG_PARSING_ERROR:
      case Xe.FRAG_DECRYPT_ERROR:
      case Xe.FRAG_LOAD_ERROR:
      case Xe.FRAG_LOAD_TIMEOUT:
      case Xe.KEY_LOAD_ERROR:
      case Xe.KEY_LOAD_TIMEOUT:
        this.onFragmentOrKeyLoadError(jt.AUDIO, t);
        break;
      case Xe.AUDIO_TRACK_LOAD_ERROR:
      case Xe.AUDIO_TRACK_LOAD_TIMEOUT:
      case Xe.LEVEL_PARSING_ERROR:
        !t.levelRetry && this.state === pt.WAITING_TRACK && ((i = t.context) == null ? void 0 : i.type) === ki.AUDIO_TRACK && (this.state = pt.IDLE);
        break;
      case Xe.BUFFER_APPEND_ERROR:
      case Xe.BUFFER_FULL_ERROR:
        if (!t.parent || t.parent !== "audio")
          return;
        if (t.details === Xe.BUFFER_APPEND_ERROR) {
          this.resetLoadingState();
          return;
        }
        this.reduceLengthAndFlushBuffer(t) && (this.bufferedTrack = null, super.flushMainBuffer(0, Number.POSITIVE_INFINITY, "audio"));
        break;
      case Xe.INTERNAL_EXCEPTION:
        this.recoverWorkerError(t);
        break;
    }
  }
  onBufferFlushing(e, {
    type: t
  }) {
    t !== an.VIDEO && (this.flushing = !0);
  }
  onBufferFlushed(e, {
    type: t
  }) {
    if (t !== an.VIDEO) {
      this.flushing = !1, this.bufferFlushed = !0, this.state === pt.ENDED && (this.state = pt.IDLE);
      const i = this.mediaBuffer || this.media;
      i && (this.afterBufferFlushed(i, t, jt.AUDIO), this.tick());
    }
  }
  _handleTransmuxComplete(e) {
    var t;
    const i = "audio", {
      hls: n
    } = this, {
      remuxResult: r,
      chunkMeta: a
    } = e, o = this.getCurrentContext(a);
    if (!o) {
      this.resetWhenMissingContext(a);
      return;
    }
    const {
      frag: l,
      part: c,
      level: h
    } = o, {
      details: u
    } = h, {
      audio: d,
      text: f,
      id3: p,
      initSegment: v
    } = r;
    if (this.fragContextChanged(l) || !u) {
      this.fragmentTracker.removeFragment(l);
      return;
    }
    if (this.state = pt.PARSING, this.switchingTrack && d && this.completeAudioSwitch(this.switchingTrack), v != null && v.tracks) {
      const g = l.initSegment || l;
      this._bufferInitSegment(h, v.tracks, g, a), n.trigger(j.FRAG_PARSING_INIT_SEGMENT, {
        frag: g,
        id: i,
        tracks: v.tracks
      });
    }
    if (d) {
      const {
        startPTS: g,
        endPTS: m,
        startDTS: x,
        endDTS: y
      } = d;
      c && (c.elementaryStreams[an.AUDIO] = {
        startPTS: g,
        endPTS: m,
        startDTS: x,
        endDTS: y
      }), l.setElementaryStreamInfo(an.AUDIO, g, m, x, y), this.bufferFragmentData(d, l, c, a);
    }
    if (p != null && (t = p.samples) != null && t.length) {
      const g = as({
        id: i,
        frag: l,
        details: u
      }, p);
      n.trigger(j.FRAG_PARSING_METADATA, g);
    }
    if (f) {
      const g = as({
        id: i,
        frag: l,
        details: u
      }, f);
      n.trigger(j.FRAG_PARSING_USERDATA, g);
    }
  }
  _bufferInitSegment(e, t, i, n) {
    if (this.state !== pt.PARSING)
      return;
    t.video && delete t.video;
    const r = t.audio;
    if (!r)
      return;
    r.id = "audio";
    const a = e.audioCodec;
    this.log(`Init audio buffer, container:${r.container}, codecs[level/parsed]=[${a}/${r.codec}]`), a && a.split(",").length === 1 && (r.levelCodec = a), this.hls.trigger(j.BUFFER_CODECS, t);
    const o = r.initSegment;
    if (o != null && o.byteLength) {
      const l = {
        type: "audio",
        frag: i,
        part: null,
        chunkMeta: n,
        parent: i.type,
        data: o
      };
      this.hls.trigger(j.BUFFER_APPENDING, l);
    }
    this.tickImmediate();
  }
  loadFragment(e, t, i) {
    const n = this.fragmentTracker.getState(e);
    if (this.fragCurrent = e, this.switchingTrack || n === Ts.NOT_LOADED || n === Ts.PARTIAL) {
      var r;
      if (e.sn === "initSegment")
        this._loadInitSegment(e, t);
      else if ((r = t.details) != null && r.live && !this.initPTS[e.cc]) {
        this.log(`Waiting for video PTS in continuity counter ${e.cc} of live stream before loading audio fragment ${e.sn} of level ${this.trackId}`), this.state = pt.WAITING_INIT_PTS;
        const a = this.mainDetails;
        a && a.fragments[0].start !== t.details.fragments[0].start && bx(t.details, a);
      } else
        this.startFragRequested = !0, super.loadFragment(e, t, i);
    } else
      this.clearTrackerIfNeeded(e);
  }
  flushAudioIfNeeded(e) {
    const {
      media: t,
      bufferedTrack: i
    } = this, n = i == null ? void 0 : i.attrs, r = e.attrs;
    t && n && (n.CHANNELS !== r.CHANNELS || i.name !== e.name || i.lang !== e.lang) && (this.log("Switching audio track : flushing all audio"), super.flushMainBuffer(0, Number.POSITIVE_INFINITY, "audio"), this.bufferedTrack = null);
  }
  completeAudioSwitch(e) {
    const {
      hls: t
    } = this;
    this.flushAudioIfNeeded(e), this.bufferedTrack = e, this.switchingTrack = null, t.trigger(j.AUDIO_TRACK_SWITCHED, Ls({}, e));
  }
}
class uae extends JS {
  constructor(e) {
    super(e, "[audio-track-controller]"), this.tracks = [], this.groupIds = null, this.tracksInGroup = [], this.trackId = -1, this.currentTrack = null, this.selectDefaultTrack = !0, this.registerListeners();
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.LEVEL_LOADING, this.onLevelLoading, this), e.on(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.on(j.AUDIO_TRACK_LOADED, this.onAudioTrackLoaded, this), e.on(j.ERROR, this.onError, this);
  }
  unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.LEVEL_LOADING, this.onLevelLoading, this), e.off(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.off(j.AUDIO_TRACK_LOADED, this.onAudioTrackLoaded, this), e.off(j.ERROR, this.onError, this);
  }
  destroy() {
    this.unregisterListeners(), this.tracks.length = 0, this.tracksInGroup.length = 0, this.currentTrack = null, super.destroy();
  }
  onManifestLoading() {
    this.tracks = [], this.tracksInGroup = [], this.groupIds = null, this.currentTrack = null, this.trackId = -1, this.selectDefaultTrack = !0;
  }
  onManifestParsed(e, t) {
    this.tracks = t.audioTracks || [];
  }
  onAudioTrackLoaded(e, t) {
    const {
      id: i,
      groupId: n,
      details: r
    } = t, a = this.tracksInGroup[i];
    if (!a || a.groupId !== n) {
      this.warn(`Audio track with id:${i} and group:${n} not found in active group ${a == null ? void 0 : a.groupId}`);
      return;
    }
    const o = a.details;
    a.details = t.details, this.log(`Audio track ${i} "${a.name}" lang:${a.lang} group:${n} loaded [${r.startSN}-${r.endSN}]`), i === this.trackId && this.playlistLoaded(i, t, o);
  }
  onLevelLoading(e, t) {
    this.switchLevel(t.level);
  }
  onLevelSwitching(e, t) {
    this.switchLevel(t.level);
  }
  switchLevel(e) {
    const t = this.hls.levels[e];
    if (!t)
      return;
    const i = t.audioGroups || null, n = this.groupIds;
    let r = this.currentTrack;
    if (!i || (n == null ? void 0 : n.length) !== (i == null ? void 0 : i.length) || i != null && i.some((o) => (n == null ? void 0 : n.indexOf(o)) === -1)) {
      this.groupIds = i, this.trackId = -1, this.currentTrack = null;
      const o = this.tracks.filter((d) => !i || i.indexOf(d.groupId) !== -1);
      if (o.length)
        this.selectDefaultTrack && !o.some((d) => d.default) && (this.selectDefaultTrack = !1), o.forEach((d, f) => {
          d.id = f;
        });
      else if (!r && !this.tracksInGroup.length)
        return;
      this.tracksInGroup = o;
      const l = this.hls.config.audioPreference;
      if (!r && l) {
        const d = Yo(l, o, Pd);
        if (d > -1)
          r = o[d];
        else {
          const f = Yo(l, this.tracks);
          r = this.tracks[f];
        }
      }
      let c = this.findTrackId(r);
      c === -1 && r && (c = this.findTrackId(null));
      const h = {
        audioTracks: o
      };
      this.log(`Updating audio tracks, ${o.length} track(s) found in group(s): ${i == null ? void 0 : i.join(",")}`), this.hls.trigger(j.AUDIO_TRACKS_UPDATED, h);
      const u = this.trackId;
      if (c !== -1 && u === -1)
        this.setAudioTrack(c);
      else if (o.length && u === -1) {
        var a;
        const d = new Error(`No audio track selected for current audio group-ID(s): ${(a = this.groupIds) == null ? void 0 : a.join(",")} track count: ${o.length}`);
        this.warn(d.message), this.hls.trigger(j.ERROR, {
          type: qt.MEDIA_ERROR,
          details: Xe.AUDIO_TRACK_LOAD_ERROR,
          fatal: !0,
          error: d
        });
      }
    } else
      this.shouldReloadPlaylist(r) && this.setAudioTrack(this.trackId);
  }
  onError(e, t) {
    t.fatal || !t.context || t.context.type === ki.AUDIO_TRACK && t.context.id === this.trackId && (!this.groupIds || this.groupIds.indexOf(t.context.groupId) !== -1) && (this.requestScheduled = -1, this.checkRetry(t));
  }
  get allAudioTracks() {
    return this.tracks;
  }
  get audioTracks() {
    return this.tracksInGroup;
  }
  get audioTrack() {
    return this.trackId;
  }
  set audioTrack(e) {
    this.selectDefaultTrack = !1, this.setAudioTrack(e);
  }
  setAudioOption(e) {
    const t = this.hls;
    if (t.config.audioPreference = e, e) {
      const i = this.allAudioTracks;
      if (this.selectDefaultTrack = !1, i.length) {
        const n = this.currentTrack;
        if (n && Hf(e, n, Pd))
          return n;
        const r = Yo(e, this.tracksInGroup, Pd);
        if (r > -1) {
          const a = this.tracksInGroup[r];
          return this.setAudioTrack(r), a;
        } else if (n) {
          let a = t.loadLevel;
          a === -1 && (a = t.firstAutoLevel);
          const o = ure(e, t.levels, i, a, Pd);
          if (o === -1)
            return null;
          t.nextLoadLevel = o;
        }
        if (e.channels || e.audioCodec) {
          const a = Yo(e, i);
          if (a > -1)
            return i[a];
        }
      }
    }
    return null;
  }
  setAudioTrack(e) {
    const t = this.tracksInGroup;
    if (e < 0 || e >= t.length) {
      this.warn(`Invalid audio track id: ${e}`);
      return;
    }
    this.clearTimer(), this.selectDefaultTrack = !1;
    const i = this.currentTrack, n = t[e], r = n.details && !n.details.live;
    if (e === this.trackId && n === i && r || (this.log(`Switching to audio-track ${e} "${n.name}" lang:${n.lang} group:${n.groupId} channels:${n.channels}`), this.trackId = e, this.currentTrack = n, this.hls.trigger(j.AUDIO_TRACK_SWITCHING, Ls({}, n)), r))
      return;
    const a = this.switchParams(n.url, i == null ? void 0 : i.details, n.details);
    this.loadPlaylist(a);
  }
  findTrackId(e) {
    const t = this.tracksInGroup;
    for (let i = 0; i < t.length; i++) {
      const n = t[i];
      if (!(this.selectDefaultTrack && !n.default) && (!e || Hf(e, n, Pd)))
        return i;
    }
    if (e) {
      const {
        name: i,
        lang: n,
        assocLang: r,
        characteristics: a,
        audioCodec: o,
        channels: l
      } = e;
      for (let c = 0; c < t.length; c++) {
        const h = t[c];
        if (Hf({
          name: i,
          lang: n,
          assocLang: r,
          characteristics: a,
          audioCodec: o,
          channels: l
        }, h, Pd))
          return c;
      }
      for (let c = 0; c < t.length; c++) {
        const h = t[c];
        if (Cp(e.attrs, h.attrs, ["LANGUAGE", "ASSOC-LANGUAGE", "CHARACTERISTICS"]))
          return c;
      }
      for (let c = 0; c < t.length; c++) {
        const h = t[c];
        if (Cp(e.attrs, h.attrs, ["LANGUAGE"]))
          return c;
      }
    }
    return -1;
  }
  loadPlaylist(e) {
    const t = this.currentTrack;
    if (this.shouldLoadPlaylist(t) && t) {
      super.loadPlaylist();
      const i = t.id, n = t.groupId;
      let r = t.url;
      if (e)
        try {
          r = e.addDirectives(r);
        } catch (a) {
          this.warn(`Could not construct new URL with HLS Delivery Directives: ${a}`);
        }
      this.log(`loading audio-track playlist ${i} "${t.name}" lang:${t.lang} group:${n}`), this.clearTimer(), this.hls.trigger(j.AUDIO_TRACK_LOADING, {
        url: r,
        id: i,
        groupId: n,
        deliveryDirectives: e || null
      });
    }
  }
}
const S_ = 500;
class dae extends ew {
  constructor(e, t, i) {
    super(e, t, i, "[subtitle-stream-controller]", jt.SUBTITLE), this.currentTrackId = -1, this.tracksBuffered = [], this.mainDetails = null, this._registerListeners();
  }
  onHandlerDestroying() {
    this._unregisterListeners(), super.onHandlerDestroying(), this.mainDetails = null;
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.LEVEL_LOADED, this.onLevelLoaded, this), e.on(j.ERROR, this.onError, this), e.on(j.SUBTITLE_TRACKS_UPDATED, this.onSubtitleTracksUpdated, this), e.on(j.SUBTITLE_TRACK_SWITCH, this.onSubtitleTrackSwitch, this), e.on(j.SUBTITLE_TRACK_LOADED, this.onSubtitleTrackLoaded, this), e.on(j.SUBTITLE_FRAG_PROCESSED, this.onSubtitleFragProcessed, this), e.on(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.LEVEL_LOADED, this.onLevelLoaded, this), e.off(j.ERROR, this.onError, this), e.off(j.SUBTITLE_TRACKS_UPDATED, this.onSubtitleTracksUpdated, this), e.off(j.SUBTITLE_TRACK_SWITCH, this.onSubtitleTrackSwitch, this), e.off(j.SUBTITLE_TRACK_LOADED, this.onSubtitleTrackLoaded, this), e.off(j.SUBTITLE_FRAG_PROCESSED, this.onSubtitleFragProcessed, this), e.off(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  startLoad(e) {
    this.stopLoad(), this.state = pt.IDLE, this.setInterval(S_), this.nextLoadPosition = this.startPosition = this.lastCurrentTime = e, this.tick();
  }
  onManifestLoading() {
    this.mainDetails = null, this.fragmentTracker.removeAllFragments();
  }
  onMediaDetaching() {
    this.tracksBuffered = [], super.onMediaDetaching();
  }
  onLevelLoaded(e, t) {
    this.mainDetails = t.details;
  }
  onSubtitleFragProcessed(e, t) {
    const {
      frag: i,
      success: n
    } = t;
    if (this.fragPrevious = i, this.state = pt.IDLE, !n)
      return;
    const r = this.tracksBuffered[this.currentTrackId];
    if (!r)
      return;
    let a;
    const o = i.start;
    for (let c = 0; c < r.length; c++)
      if (o >= r[c].start && o <= r[c].end) {
        a = r[c];
        break;
      }
    const l = i.start + i.duration;
    a ? a.end = l : (a = {
      start: o,
      end: l
    }, r.push(a)), this.fragmentTracker.fragBuffered(i), this.fragBufferedComplete(i, null);
  }
  onBufferFlushing(e, t) {
    const {
      startOffset: i,
      endOffset: n
    } = t;
    if (i === 0 && n !== Number.POSITIVE_INFINITY) {
      const r = n - 1;
      if (r <= 0)
        return;
      t.endOffsetSubtitles = Math.max(0, r), this.tracksBuffered.forEach((a) => {
        for (let o = 0; o < a.length; ) {
          if (a[o].end <= r) {
            a.shift();
            continue;
          } else if (a[o].start < r)
            a[o].start = r;
          else
            break;
          o++;
        }
      }), this.fragmentTracker.removeFragmentsInRange(i, r, jt.SUBTITLE);
    }
  }
  onFragBuffered(e, t) {
    if (!this.loadedmetadata && t.frag.type === jt.MAIN) {
      var i;
      (i = this.media) != null && i.buffered.length && (this.loadedmetadata = !0);
    }
  }
  // If something goes wrong, proceed to next frag, if we were processing one.
  onError(e, t) {
    const i = t.frag;
    (i == null ? void 0 : i.type) === jt.SUBTITLE && (this.fragCurrent && this.fragCurrent.abortRequests(), this.state !== pt.STOPPED && (this.state = pt.IDLE));
  }
  // Got all new subtitle levels.
  onSubtitleTracksUpdated(e, {
    subtitleTracks: t
  }) {
    if (this.levels && aB(this.levels, t)) {
      this.levels = t.map((i) => new Tp(i));
      return;
    }
    this.tracksBuffered = [], this.levels = t.map((i) => {
      const n = new Tp(i);
      return this.tracksBuffered[n.id] = [], n;
    }), this.fragmentTracker.removeFragmentsInRange(0, Number.POSITIVE_INFINITY, jt.SUBTITLE), this.fragPrevious = null, this.mediaBuffer = null;
  }
  onSubtitleTrackSwitch(e, t) {
    var i;
    if (this.currentTrackId = t.id, !((i = this.levels) != null && i.length) || this.currentTrackId === -1) {
      this.clearInterval();
      return;
    }
    const n = this.levels[this.currentTrackId];
    n != null && n.details ? this.mediaBuffer = this.mediaBufferTimeRanges : this.mediaBuffer = null, n && this.setInterval(S_);
  }
  // Got a new set of subtitle fragments.
  onSubtitleTrackLoaded(e, t) {
    var i;
    const {
      currentTrackId: n,
      levels: r
    } = this, {
      details: a,
      id: o
    } = t;
    if (!r) {
      this.warn(`Subtitle tracks were reset while loading level ${o}`);
      return;
    }
    const l = r[n];
    if (o >= r.length || o !== n || !l)
      return;
    this.log(`Subtitle track ${o} loaded [${a.startSN},${a.endSN}]${a.lastPartSn ? `[part-${a.lastPartSn}-${a.lastPartIndex}]` : ""},duration:${a.totalduration}`), this.mediaBuffer = this.mediaBufferTimeRanges;
    let c = 0;
    if (a.live || (i = l.details) != null && i.live) {
      const u = this.mainDetails;
      if (a.deltaUpdateFailed || !u)
        return;
      const d = u.fragments[0];
      if (!l.details)
        a.hasProgramDateTime && u.hasProgramDateTime ? (bx(a, u), c = a.fragments[0].start) : d && (c = d.start, Rb(a, c));
      else {
        var h;
        c = this.alignPlaylists(a, l.details, (h = this.levelLastLoaded) == null ? void 0 : h.details), c === 0 && d && (c = d.start, Rb(a, c));
      }
    }
    l.details = a, this.levelLastLoaded = l, !this.startFragRequested && (this.mainDetails || !a.live) && this.setStartPosition(this.mainDetails || a, c), this.tick(), a.live && !this.fragCurrent && this.media && this.state === pt.IDLE && (Ax(null, a.fragments, this.media.currentTime, 0) || (this.warn("Subtitle playlist not aligned with playback"), l.details = void 0));
  }
  _handleFragmentLoadComplete(e) {
    const {
      frag: t,
      payload: i
    } = e, n = t.decryptdata, r = this.hls;
    if (!this.fragContextChanged(t) && i && i.byteLength > 0 && n != null && n.key && n.iv && n.method === "AES-128") {
      const a = performance.now();
      this.decrypter.decrypt(new Uint8Array(i), n.key.buffer, n.iv.buffer).catch((o) => {
        throw r.trigger(j.ERROR, {
          type: qt.MEDIA_ERROR,
          details: Xe.FRAG_DECRYPT_ERROR,
          fatal: !1,
          error: o,
          reason: o.message,
          frag: t
        }), o;
      }).then((o) => {
        const l = performance.now();
        r.trigger(j.FRAG_DECRYPTED, {
          frag: t,
          payload: o,
          stats: {
            tstart: a,
            tdecrypt: l
          }
        });
      }).catch((o) => {
        this.warn(`${o.name}: ${o.message}`), this.state = pt.IDLE;
      });
    }
  }
  doTick() {
    if (!this.media) {
      this.state = pt.IDLE;
      return;
    }
    if (this.state === pt.IDLE) {
      const {
        currentTrackId: e,
        levels: t
      } = this, i = t == null ? void 0 : t[e];
      if (!i || !t.length || !i.details)
        return;
      const {
        config: n
      } = this, r = this.getLoadPosition(), a = Mn.bufferedInfo(this.tracksBuffered[this.currentTrackId] || [], r, n.maxBufferHole), {
        end: o,
        len: l
      } = a, c = this.getFwdBufferInfo(this.media, jt.MAIN), h = i.details, u = this.getMaxBufferLength(c == null ? void 0 : c.len) + h.levelTargetDuration;
      if (l > u)
        return;
      const d = h.fragments, f = d.length, p = h.edge;
      let v = null;
      const g = this.fragPrevious;
      if (o < p) {
        const m = n.maxFragLookUpTolerance, x = o > p - m ? 0 : m;
        v = Ax(g, d, Math.max(d[0].start, o), x), !v && g && g.start < d[0].start && (v = d[0]);
      } else
        v = d[f - 1];
      if (!v)
        return;
      if (v = this.mapToInitFragWhenRequired(v), v.sn !== "initSegment") {
        const m = v.sn - h.startSN, x = d[m - 1];
        x && x.cc === v.cc && this.fragmentTracker.getState(x) === Ts.NOT_LOADED && (v = x);
      }
      this.fragmentTracker.getState(v) === Ts.NOT_LOADED && this.loadFragment(v, i, o);
    }
  }
  getMaxBufferLength(e) {
    const t = super.getMaxBufferLength();
    return e ? Math.max(t, e) : t;
  }
  loadFragment(e, t, i) {
    this.fragCurrent = e, e.sn === "initSegment" ? this._loadInitSegment(e, t) : (this.startFragRequested = !0, super.loadFragment(e, t, i));
  }
  get mediaBufferTimeRanges() {
    return new fae(this.tracksBuffered[this.currentTrackId] || []);
  }
}
class fae {
  constructor(e) {
    this.buffered = void 0;
    const t = (i, n, r) => {
      if (n = n >>> 0, n > r - 1)
        throw new DOMException(`Failed to execute '${i}' on 'TimeRanges': The index provided (${n}) is greater than the maximum bound (${r})`);
      return e[n][i];
    };
    this.buffered = {
      get length() {
        return e.length;
      },
      end(i) {
        return t("end", i, e.length);
      },
      start(i) {
        return t("start", i, e.length);
      }
    };
  }
}
class pae extends JS {
  constructor(e) {
    super(e, "[subtitle-track-controller]"), this.media = null, this.tracks = [], this.groupIds = null, this.tracksInGroup = [], this.trackId = -1, this.currentTrack = null, this.selectDefaultTrack = !0, this.queuedDefaultTrack = -1, this.asyncPollTrackChange = () => this.pollTrackChange(0), this.useTextTrackPolling = !1, this.subtitlePollingInterval = -1, this._subtitleDisplay = !0, this.onTextTracksChanged = () => {
      if (this.useTextTrackPolling || self.clearInterval(this.subtitlePollingInterval), !this.media || !this.hls.config.renderTextTracksNatively)
        return;
      let t = null;
      const i = By(this.media.textTracks);
      for (let r = 0; r < i.length; r++)
        if (i[r].mode === "hidden")
          t = i[r];
        else if (i[r].mode === "showing") {
          t = i[r];
          break;
        }
      const n = this.findTrackForTextTrack(t);
      this.subtitleTrack !== n && this.setSubtitleTrack(n);
    }, this.registerListeners();
  }
  destroy() {
    this.unregisterListeners(), this.tracks.length = 0, this.tracksInGroup.length = 0, this.currentTrack = null, this.onTextTracksChanged = this.asyncPollTrackChange = null, super.destroy();
  }
  get subtitleDisplay() {
    return this._subtitleDisplay;
  }
  set subtitleDisplay(e) {
    this._subtitleDisplay = e, this.trackId > -1 && this.toggleTrackModes();
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.LEVEL_LOADING, this.onLevelLoading, this), e.on(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.on(j.SUBTITLE_TRACK_LOADED, this.onSubtitleTrackLoaded, this), e.on(j.ERROR, this.onError, this);
  }
  unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.LEVEL_LOADING, this.onLevelLoading, this), e.off(j.LEVEL_SWITCHING, this.onLevelSwitching, this), e.off(j.SUBTITLE_TRACK_LOADED, this.onSubtitleTrackLoaded, this), e.off(j.ERROR, this.onError, this);
  }
  // Listen for subtitle track change, then extract the current track ID.
  onMediaAttached(e, t) {
    this.media = t.media, this.media && (this.queuedDefaultTrack > -1 && (this.subtitleTrack = this.queuedDefaultTrack, this.queuedDefaultTrack = -1), this.useTextTrackPolling = !(this.media.textTracks && "onchange" in this.media.textTracks), this.useTextTrackPolling ? this.pollTrackChange(500) : this.media.textTracks.addEventListener("change", this.asyncPollTrackChange));
  }
  pollTrackChange(e) {
    self.clearInterval(this.subtitlePollingInterval), this.subtitlePollingInterval = self.setInterval(this.onTextTracksChanged, e);
  }
  onMediaDetaching() {
    if (!this.media)
      return;
    self.clearInterval(this.subtitlePollingInterval), this.useTextTrackPolling || this.media.textTracks.removeEventListener("change", this.asyncPollTrackChange), this.trackId > -1 && (this.queuedDefaultTrack = this.trackId), By(this.media.textTracks).forEach((t) => {
      wf(t);
    }), this.subtitleTrack = -1, this.media = null;
  }
  onManifestLoading() {
    this.tracks = [], this.groupIds = null, this.tracksInGroup = [], this.trackId = -1, this.currentTrack = null, this.selectDefaultTrack = !0;
  }
  // Fired whenever a new manifest is loaded.
  onManifestParsed(e, t) {
    this.tracks = t.subtitleTracks;
  }
  onSubtitleTrackLoaded(e, t) {
    const {
      id: i,
      groupId: n,
      details: r
    } = t, a = this.tracksInGroup[i];
    if (!a || a.groupId !== n) {
      this.warn(`Subtitle track with id:${i} and group:${n} not found in active group ${a == null ? void 0 : a.groupId}`);
      return;
    }
    const o = a.details;
    a.details = t.details, this.log(`Subtitle track ${i} "${a.name}" lang:${a.lang} group:${n} loaded [${r.startSN}-${r.endSN}]`), i === this.trackId && this.playlistLoaded(i, t, o);
  }
  onLevelLoading(e, t) {
    this.switchLevel(t.level);
  }
  onLevelSwitching(e, t) {
    this.switchLevel(t.level);
  }
  switchLevel(e) {
    const t = this.hls.levels[e];
    if (!t)
      return;
    const i = t.subtitleGroups || null, n = this.groupIds;
    let r = this.currentTrack;
    if (!i || (n == null ? void 0 : n.length) !== (i == null ? void 0 : i.length) || i != null && i.some((a) => (n == null ? void 0 : n.indexOf(a)) === -1)) {
      this.groupIds = i, this.trackId = -1, this.currentTrack = null;
      const a = this.tracks.filter((h) => !i || i.indexOf(h.groupId) !== -1);
      if (a.length)
        this.selectDefaultTrack && !a.some((h) => h.default) && (this.selectDefaultTrack = !1), a.forEach((h, u) => {
          h.id = u;
        });
      else if (!r && !this.tracksInGroup.length)
        return;
      this.tracksInGroup = a;
      const o = this.hls.config.subtitlePreference;
      if (!r && o) {
        this.selectDefaultTrack = !1;
        const h = Yo(o, a);
        if (h > -1)
          r = a[h];
        else {
          const u = Yo(o, this.tracks);
          r = this.tracks[u];
        }
      }
      let l = this.findTrackId(r);
      l === -1 && r && (l = this.findTrackId(null));
      const c = {
        subtitleTracks: a
      };
      this.log(`Updating subtitle tracks, ${a.length} track(s) found in "${i == null ? void 0 : i.join(",")}" group-id`), this.hls.trigger(j.SUBTITLE_TRACKS_UPDATED, c), l !== -1 && this.trackId === -1 && this.setSubtitleTrack(l);
    } else
      this.shouldReloadPlaylist(r) && this.setSubtitleTrack(this.trackId);
  }
  findTrackId(e) {
    const t = this.tracksInGroup, i = this.selectDefaultTrack;
    for (let n = 0; n < t.length; n++) {
      const r = t[n];
      if (!(i && !r.default || !i && !e) && (!e || Hf(r, e)))
        return n;
    }
    if (e) {
      for (let n = 0; n < t.length; n++) {
        const r = t[n];
        if (Cp(e.attrs, r.attrs, ["LANGUAGE", "ASSOC-LANGUAGE", "CHARACTERISTICS"]))
          return n;
      }
      for (let n = 0; n < t.length; n++) {
        const r = t[n];
        if (Cp(e.attrs, r.attrs, ["LANGUAGE"]))
          return n;
      }
    }
    return -1;
  }
  findTrackForTextTrack(e) {
    if (e) {
      const t = this.tracksInGroup;
      for (let i = 0; i < t.length; i++) {
        const n = t[i];
        if (Ib(n, e))
          return i;
      }
    }
    return -1;
  }
  onError(e, t) {
    t.fatal || !t.context || t.context.type === ki.SUBTITLE_TRACK && t.context.id === this.trackId && (!this.groupIds || this.groupIds.indexOf(t.context.groupId) !== -1) && this.checkRetry(t);
  }
  get allSubtitleTracks() {
    return this.tracks;
  }
  /** get alternate subtitle tracks list from playlist **/
  get subtitleTracks() {
    return this.tracksInGroup;
  }
  /** get/set index of the selected subtitle track (based on index in subtitle track lists) **/
  get subtitleTrack() {
    return this.trackId;
  }
  set subtitleTrack(e) {
    this.selectDefaultTrack = !1, this.setSubtitleTrack(e);
  }
  setSubtitleOption(e) {
    if (this.hls.config.subtitlePreference = e, e) {
      const t = this.allSubtitleTracks;
      if (this.selectDefaultTrack = !1, t.length) {
        const i = this.currentTrack;
        if (i && Hf(e, i))
          return i;
        const n = Yo(e, this.tracksInGroup);
        if (n > -1) {
          const r = this.tracksInGroup[n];
          return this.setSubtitleTrack(n), r;
        } else {
          if (i)
            return null;
          {
            const r = Yo(e, t);
            if (r > -1)
              return t[r];
          }
        }
      }
    }
    return null;
  }
  loadPlaylist(e) {
    super.loadPlaylist();
    const t = this.currentTrack;
    if (this.shouldLoadPlaylist(t) && t) {
      const i = t.id, n = t.groupId;
      let r = t.url;
      if (e)
        try {
          r = e.addDirectives(r);
        } catch (a) {
          this.warn(`Could not construct new URL with HLS Delivery Directives: ${a}`);
        }
      this.log(`Loading subtitle playlist for id ${i}`), this.hls.trigger(j.SUBTITLE_TRACK_LOADING, {
        url: r,
        id: i,
        groupId: n,
        deliveryDirectives: e || null
      });
    }
  }
  /**
   * Disables the old subtitleTrack and sets current mode on the next subtitleTrack.
   * This operates on the DOM textTracks.
   * A value of -1 will disable all subtitle tracks.
   */
  toggleTrackModes() {
    const {
      media: e
    } = this;
    if (!e)
      return;
    const t = By(e.textTracks), i = this.currentTrack;
    let n;
    if (i && (n = t.filter((r) => Ib(i, r))[0], n || this.warn(`Unable to find subtitle TextTrack with name "${i.name}" and language "${i.lang}"`)), [].slice.call(t).forEach((r) => {
      r.mode !== "disabled" && r !== n && (r.mode = "disabled");
    }), n) {
      const r = this.subtitleDisplay ? "showing" : "hidden";
      n.mode !== r && (n.mode = r);
    }
  }
  /**
   * This method is responsible for validating the subtitle index and periodically reloading if live.
   * Dispatches the SUBTITLE_TRACK_SWITCH event, which instructs the subtitle-stream-controller to load the selected track.
   */
  setSubtitleTrack(e) {
    const t = this.tracksInGroup;
    if (!this.media) {
      this.queuedDefaultTrack = e;
      return;
    }
    if (e < -1 || e >= t.length || !Bt(e)) {
      this.warn(`Invalid subtitle track id: ${e}`);
      return;
    }
    this.clearTimer(), this.selectDefaultTrack = !1;
    const i = this.currentTrack, n = t[e] || null;
    if (this.trackId = e, this.currentTrack = n, this.toggleTrackModes(), !n) {
      this.hls.trigger(j.SUBTITLE_TRACK_SWITCH, {
        id: e
      });
      return;
    }
    const r = !!n.details && !n.details.live;
    if (e === this.trackId && n === i && r)
      return;
    this.log(`Switching to subtitle-track ${e}` + (n ? ` "${n.name}" lang:${n.lang} group:${n.groupId}` : ""));
    const {
      id: a,
      groupId: o = "",
      name: l,
      type: c,
      url: h
    } = n;
    this.hls.trigger(j.SUBTITLE_TRACK_SWITCH, {
      id: a,
      groupId: o,
      name: l,
      type: c,
      url: h
    });
    const u = this.switchParams(n.url, i == null ? void 0 : i.details, n.details);
    this.loadPlaylist(u);
  }
}
class mae {
  constructor(e) {
    this.buffers = void 0, this.queues = {
      video: [],
      audio: [],
      audiovideo: []
    }, this.buffers = e;
  }
  append(e, t, i) {
    const n = this.queues[t];
    n.push(e), n.length === 1 && !i && this.executeNext(t);
  }
  insertAbort(e, t) {
    this.queues[t].unshift(e), this.executeNext(t);
  }
  appendBlocker(e) {
    let t;
    const i = new Promise((r) => {
      t = r;
    }), n = {
      execute: t,
      onStart: () => {
      },
      onComplete: () => {
      },
      onError: () => {
      }
    };
    return this.append(n, e), i;
  }
  executeNext(e) {
    const t = this.queues[e];
    if (t.length) {
      const i = t[0];
      try {
        i.execute();
      } catch (n) {
        ze.warn(`[buffer-operation-queue]: Exception executing "${e}" SourceBuffer operation: ${n}`), i.onError(n);
        const r = this.buffers[e];
        r != null && r.updating || this.shiftAndExecuteNext(e);
      }
    }
  }
  shiftAndExecuteNext(e) {
    this.queues[e].shift(), this.executeNext(e);
  }
  current(e) {
    return this.queues[e][0];
  }
}
const w_ = /(avc[1234]|hvc1|hev1|dvh[1e]|vp09|av01)(?:\.[^.,]+)+/;
class gae {
  constructor(e) {
    this.details = null, this._objectUrl = null, this.operationQueue = void 0, this.listeners = void 0, this.hls = void 0, this.bufferCodecEventsExpected = 0, this._bufferCodecEventsTotal = 0, this.media = null, this.mediaSource = null, this.lastMpegAudioChunk = null, this.appendSource = void 0, this.appendErrors = {
      audio: 0,
      video: 0,
      audiovideo: 0
    }, this.tracks = {}, this.pendingTracks = {}, this.sourceBuffer = void 0, this.log = void 0, this.warn = void 0, this.error = void 0, this._onEndStreaming = (i) => {
      this.hls && this.hls.pauseBuffering();
    }, this._onStartStreaming = (i) => {
      this.hls && this.hls.resumeBuffering();
    }, this._onMediaSourceOpen = () => {
      const {
        media: i,
        mediaSource: n
      } = this;
      this.log("Media source opened"), i && (i.removeEventListener("emptied", this._onMediaEmptied), this.updateMediaElementDuration(), this.hls.trigger(j.MEDIA_ATTACHED, {
        media: i,
        mediaSource: n
      })), n && n.removeEventListener("sourceopen", this._onMediaSourceOpen), this.checkPendingTracks();
    }, this._onMediaSourceClose = () => {
      this.log("Media source closed");
    }, this._onMediaSourceEnded = () => {
      this.log("Media source ended");
    }, this._onMediaEmptied = () => {
      const {
        mediaSrc: i,
        _objectUrl: n
      } = this;
      i !== n && ze.error(`Media element src was set while attaching MediaSource (${n} > ${i})`);
    }, this.hls = e;
    const t = "[buffer-controller]";
    this.appendSource = Tse(Nu(e.config.preferManagedMediaSource)), this.log = ze.log.bind(ze, t), this.warn = ze.warn.bind(ze, t), this.error = ze.error.bind(ze, t), this._initSourceBuffer(), this.registerListeners();
  }
  hasSourceTypes() {
    return this.getSourceBufferTypes().length > 0 || Object.keys(this.pendingTracks).length > 0;
  }
  destroy() {
    this.unregisterListeners(), this.details = null, this.lastMpegAudioChunk = null, this.hls = null;
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.BUFFER_RESET, this.onBufferReset, this), e.on(j.BUFFER_APPENDING, this.onBufferAppending, this), e.on(j.BUFFER_CODECS, this.onBufferCodecs, this), e.on(j.BUFFER_EOS, this.onBufferEos, this), e.on(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.on(j.LEVEL_UPDATED, this.onLevelUpdated, this), e.on(j.FRAG_PARSED, this.onFragParsed, this), e.on(j.FRAG_CHANGED, this.onFragChanged, this);
  }
  unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.BUFFER_RESET, this.onBufferReset, this), e.off(j.BUFFER_APPENDING, this.onBufferAppending, this), e.off(j.BUFFER_CODECS, this.onBufferCodecs, this), e.off(j.BUFFER_EOS, this.onBufferEos, this), e.off(j.BUFFER_FLUSHING, this.onBufferFlushing, this), e.off(j.LEVEL_UPDATED, this.onLevelUpdated, this), e.off(j.FRAG_PARSED, this.onFragParsed, this), e.off(j.FRAG_CHANGED, this.onFragChanged, this);
  }
  _initSourceBuffer() {
    this.sourceBuffer = {}, this.operationQueue = new mae(this.sourceBuffer), this.listeners = {
      audio: [],
      video: [],
      audiovideo: []
    }, this.appendErrors = {
      audio: 0,
      video: 0,
      audiovideo: 0
    }, this.lastMpegAudioChunk = null;
  }
  onManifestLoading() {
    this.bufferCodecEventsExpected = this._bufferCodecEventsTotal = 0, this.details = null;
  }
  onManifestParsed(e, t) {
    let i = 2;
    (t.audio && !t.video || !t.altAudio) && (i = 1), this.bufferCodecEventsExpected = this._bufferCodecEventsTotal = i, this.log(`${this.bufferCodecEventsExpected} bufferCodec event(s) expected`);
  }
  onMediaAttaching(e, t) {
    const i = this.media = t.media, n = Nu(this.appendSource);
    if (i && n) {
      var r;
      const a = this.mediaSource = new n();
      this.log(`created media source: ${(r = a.constructor) == null ? void 0 : r.name}`), a.addEventListener("sourceopen", this._onMediaSourceOpen), a.addEventListener("sourceended", this._onMediaSourceEnded), a.addEventListener("sourceclose", this._onMediaSourceClose), this.appendSource && (a.addEventListener("startstreaming", this._onStartStreaming), a.addEventListener("endstreaming", this._onEndStreaming));
      const o = this._objectUrl = self.URL.createObjectURL(a);
      if (this.appendSource)
        try {
          i.removeAttribute("src");
          const l = self.ManagedMediaSource;
          i.disableRemotePlayback = i.disableRemotePlayback || l && a instanceof l, E_(i), vae(i, o), i.load();
        } catch {
          i.src = o;
        }
      else
        i.src = o;
      i.addEventListener("emptied", this._onMediaEmptied);
    }
  }
  onMediaDetaching() {
    const {
      media: e,
      mediaSource: t,
      _objectUrl: i
    } = this;
    if (t) {
      if (this.log("media source detaching"), t.readyState === "open")
        try {
          t.endOfStream();
        } catch (n) {
          this.warn(`onMediaDetaching: ${n.message} while calling endOfStream`);
        }
      this.onBufferReset(), t.removeEventListener("sourceopen", this._onMediaSourceOpen), t.removeEventListener("sourceended", this._onMediaSourceEnded), t.removeEventListener("sourceclose", this._onMediaSourceClose), this.appendSource && (t.removeEventListener("startstreaming", this._onStartStreaming), t.removeEventListener("endstreaming", this._onEndStreaming)), e && (e.removeEventListener("emptied", this._onMediaEmptied), i && self.URL.revokeObjectURL(i), this.mediaSrc === i ? (e.removeAttribute("src"), this.appendSource && E_(e), e.load()) : this.warn("media|source.src was changed by a third party - skip cleanup")), this.mediaSource = null, this.media = null, this._objectUrl = null, this.bufferCodecEventsExpected = this._bufferCodecEventsTotal, this.pendingTracks = {}, this.tracks = {};
    }
    this.hls.trigger(j.MEDIA_DETACHED, void 0);
  }
  onBufferReset() {
    this.getSourceBufferTypes().forEach((e) => {
      this.resetBuffer(e);
    }), this._initSourceBuffer();
  }
  resetBuffer(e) {
    const t = this.sourceBuffer[e];
    try {
      if (t) {
        var i;
        this.removeBufferListeners(e), this.sourceBuffer[e] = void 0, (i = this.mediaSource) != null && i.sourceBuffers.length && this.mediaSource.removeSourceBuffer(t);
      }
    } catch (n) {
      this.warn(`onBufferReset ${e}`, n);
    }
  }
  onBufferCodecs(e, t) {
    const i = this.getSourceBufferTypes().length, n = Object.keys(t);
    if (n.forEach((a) => {
      if (i) {
        const l = this.tracks[a];
        if (l && typeof l.buffer.changeType == "function") {
          var o;
          const {
            id: c,
            codec: h,
            levelCodec: u,
            container: d,
            metadata: f
          } = t[a], p = jC(l.codec, l.levelCodec), v = p == null ? void 0 : p.replace(w_, "$1");
          let g = jC(h, u);
          const m = (o = g) == null ? void 0 : o.replace(w_, "$1");
          if (g && v !== m) {
            a.slice(0, 5) === "audio" && (g = gx(g, this.appendSource));
            const x = `${d};codecs=${g}`;
            this.appendChangeType(a, x), this.log(`switching codec ${p} to ${g}`), this.tracks[a] = {
              buffer: l.buffer,
              codec: h,
              container: d,
              levelCodec: u,
              metadata: f,
              id: c
            };
          }
        }
      } else
        this.pendingTracks[a] = t[a];
    }), i)
      return;
    const r = Math.max(this.bufferCodecEventsExpected - 1, 0);
    this.bufferCodecEventsExpected !== r && (this.log(`${r} bufferCodec event(s) expected ${n.join(",")}`), this.bufferCodecEventsExpected = r), this.mediaSource && this.mediaSource.readyState === "open" && this.checkPendingTracks();
  }
  appendChangeType(e, t) {
    const {
      operationQueue: i
    } = this, n = {
      execute: () => {
        const r = this.sourceBuffer[e];
        r && (this.log(`changing ${e} sourceBuffer type to ${t}`), r.changeType(t)), i.shiftAndExecuteNext(e);
      },
      onStart: () => {
      },
      onComplete: () => {
      },
      onError: (r) => {
        this.warn(`Failed to change ${e} SourceBuffer type`, r);
      }
    };
    i.append(n, e, !!this.pendingTracks[e]);
  }
  onBufferAppending(e, t) {
    const {
      hls: i,
      operationQueue: n,
      tracks: r
    } = this, {
      data: a,
      type: o,
      frag: l,
      part: c,
      chunkMeta: h
    } = t, u = h.buffering[o], d = self.performance.now();
    u.start = d;
    const f = l.stats.buffering, p = c ? c.stats.buffering : null;
    f.start === 0 && (f.start = d), p && p.start === 0 && (p.start = d);
    const v = r.audio;
    let g = !1;
    o === "audio" && (v == null ? void 0 : v.container) === "audio/mpeg" && (g = !this.lastMpegAudioChunk || h.id === 1 || this.lastMpegAudioChunk.sn !== h.sn, this.lastMpegAudioChunk = h);
    const m = l.start, x = {
      execute: () => {
        if (u.executeStart = self.performance.now(), g) {
          const y = this.sourceBuffer[o];
          if (y) {
            const A = m - y.timestampOffset;
            Math.abs(A) >= 0.1 && (this.log(`Updating audio SourceBuffer timestampOffset to ${m} (delta: ${A}) sn: ${l.sn})`), y.timestampOffset = m);
          }
        }
        this.appendExecutor(a, o);
      },
      onStart: () => {
      },
      onComplete: () => {
        const y = self.performance.now();
        u.executeEnd = u.end = y, f.first === 0 && (f.first = y), p && p.first === 0 && (p.first = y);
        const {
          sourceBuffer: A
        } = this, b = {};
        for (const M in A)
          b[M] = Mn.getBuffered(A[M]);
        this.appendErrors[o] = 0, o === "audio" || o === "video" ? this.appendErrors.audiovideo = 0 : (this.appendErrors.audio = 0, this.appendErrors.video = 0), this.hls.trigger(j.BUFFER_APPENDED, {
          type: o,
          frag: l,
          part: c,
          chunkMeta: h,
          parent: l.type,
          timeRanges: b
        });
      },
      onError: (y) => {
        const A = {
          type: qt.MEDIA_ERROR,
          parent: l.type,
          details: Xe.BUFFER_APPEND_ERROR,
          sourceBufferName: o,
          frag: l,
          part: c,
          chunkMeta: h,
          error: y,
          err: y,
          fatal: !1
        };
        if (y.code === DOMException.QUOTA_EXCEEDED_ERR)
          A.details = Xe.BUFFER_FULL_ERROR;
        else {
          const b = ++this.appendErrors[o];
          A.details = Xe.BUFFER_APPEND_ERROR, this.warn(`Failed ${b}/${i.config.appendErrorMaxRetry} times to append segment in "${o}" sourceBuffer`), b >= i.config.appendErrorMaxRetry && (A.fatal = !0);
        }
        i.trigger(j.ERROR, A);
      }
    };
    n.append(x, o, !!this.pendingTracks[o]);
  }
  onBufferFlushing(e, t) {
    const {
      operationQueue: i
    } = this, n = (r) => ({
      execute: this.removeExecutor.bind(this, r, t.startOffset, t.endOffset),
      onStart: () => {
      },
      onComplete: () => {
        this.hls.trigger(j.BUFFER_FLUSHED, {
          type: r
        });
      },
      onError: (a) => {
        this.warn(`Failed to remove from ${r} SourceBuffer`, a);
      }
    });
    t.type ? i.append(n(t.type), t.type) : this.getSourceBufferTypes().forEach((r) => {
      i.append(n(r), r);
    });
  }
  onFragParsed(e, t) {
    const {
      frag: i,
      part: n
    } = t, r = [], a = n ? n.elementaryStreams : i.elementaryStreams;
    a[an.AUDIOVIDEO] ? r.push("audiovideo") : (a[an.AUDIO] && r.push("audio"), a[an.VIDEO] && r.push("video"));
    const o = () => {
      const l = self.performance.now();
      i.stats.buffering.end = l, n && (n.stats.buffering.end = l);
      const c = n ? n.stats : i.stats;
      this.hls.trigger(j.FRAG_BUFFERED, {
        frag: i,
        part: n,
        stats: c,
        id: i.type
      });
    };
    r.length === 0 && this.warn(`Fragments must have at least one ElementaryStreamType set. type: ${i.type} level: ${i.level} sn: ${i.sn}`), this.blockBuffers(o, r);
  }
  onFragChanged(e, t) {
    this.trimBuffers();
  }
  // on BUFFER_EOS mark matching sourcebuffer(s) as ended and trigger checkEos()
  // an undefined data.type will mark all buffers as EOS.
  onBufferEos(e, t) {
    this.getSourceBufferTypes().reduce((n, r) => {
      const a = this.sourceBuffer[r];
      return a && (!t.type || t.type === r) && (a.ending = !0, a.ended || (a.ended = !0, this.log(`${r} sourceBuffer now EOS`))), n && !!(!a || a.ended);
    }, !0) && (this.log("Queueing mediaSource.endOfStream()"), this.blockBuffers(() => {
      this.getSourceBufferTypes().forEach((r) => {
        const a = this.sourceBuffer[r];
        a && (a.ending = !1);
      });
      const {
        mediaSource: n
      } = this;
      if (!n || n.readyState !== "open") {
        n && this.log(`Could not call mediaSource.endOfStream(). mediaSource.readyState: ${n.readyState}`);
        return;
      }
      this.log("Calling mediaSource.endOfStream()"), n.endOfStream();
    }));
  }
  onLevelUpdated(e, {
    details: t
  }) {
    t.fragments.length && (this.details = t, this.getSourceBufferTypes().length ? this.blockBuffers(this.updateMediaElementDuration.bind(this)) : this.updateMediaElementDuration());
  }
  trimBuffers() {
    const {
      hls: e,
      details: t,
      media: i
    } = this;
    if (!i || t === null || !this.getSourceBufferTypes().length)
      return;
    const r = e.config, a = i.currentTime, o = t.levelTargetDuration, l = t.live && r.liveBackBufferLength !== null ? r.liveBackBufferLength : r.backBufferLength;
    if (Bt(l) && l > 0) {
      const c = Math.max(l, o), h = Math.floor(a / o) * o - c;
      this.flushBackBuffer(a, o, h);
    }
    if (Bt(r.frontBufferFlushThreshold) && r.frontBufferFlushThreshold > 0) {
      const c = Math.max(r.maxBufferLength, r.frontBufferFlushThreshold), h = Math.max(c, o), u = Math.floor(a / o) * o + h;
      this.flushFrontBuffer(a, o, u);
    }
  }
  flushBackBuffer(e, t, i) {
    const {
      details: n,
      sourceBuffer: r
    } = this;
    this.getSourceBufferTypes().forEach((o) => {
      const l = r[o];
      if (l) {
        const c = Mn.getBuffered(l);
        if (c.length > 0 && i > c.start(0)) {
          if (this.hls.trigger(j.BACK_BUFFER_REACHED, {
            bufferEnd: i
          }), n != null && n.live)
            this.hls.trigger(j.LIVE_BACK_BUFFER_REACHED, {
              bufferEnd: i
            });
          else if (l.ended && c.end(c.length - 1) - e < t * 2) {
            this.log(`Cannot flush ${o} back buffer while SourceBuffer is in ended state`);
            return;
          }
          this.hls.trigger(j.BUFFER_FLUSHING, {
            startOffset: 0,
            endOffset: i,
            type: o
          });
        }
      }
    });
  }
  flushFrontBuffer(e, t, i) {
    const {
      sourceBuffer: n
    } = this;
    this.getSourceBufferTypes().forEach((a) => {
      const o = n[a];
      if (o) {
        const l = Mn.getBuffered(o), c = l.length;
        if (c < 2)
          return;
        const h = l.start(c - 1), u = l.end(c - 1);
        if (i > h || e >= h && e <= u)
          return;
        if (o.ended && e - u < 2 * t) {
          this.log(`Cannot flush ${a} front buffer while SourceBuffer is in ended state`);
          return;
        }
        this.hls.trigger(j.BUFFER_FLUSHING, {
          startOffset: h,
          endOffset: 1 / 0,
          type: a
        });
      }
    });
  }
  /**
   * Update Media Source duration to current level duration or override to Infinity if configuration parameter
   * 'liveDurationInfinity` is set to `true`
   * More details: https://github.com/video-dev/hls.js/issues/355
   */
  updateMediaElementDuration() {
    if (!this.details || !this.media || !this.mediaSource || this.mediaSource.readyState !== "open")
      return;
    const {
      details: e,
      hls: t,
      media: i,
      mediaSource: n
    } = this, r = e.fragments[0].start + e.totalduration, a = i.duration, o = Bt(n.duration) ? n.duration : 0;
    e.live && t.config.liveDurationInfinity ? (n.duration = 1 / 0, this.updateSeekableRange(e)) : (r > o && r > a || !Bt(a)) && (this.log(`Updating Media Source duration to ${r.toFixed(3)}`), n.duration = r);
  }
  updateSeekableRange(e) {
    const t = this.mediaSource, i = e.fragments;
    if (i.length && e.live && t != null && t.setLiveSeekableRange) {
      const r = Math.max(0, i[0].start), a = Math.max(r, r + e.totalduration);
      this.log(`Media Source duration is set to ${t.duration}. Setting seekable range to ${r}-${a}.`), t.setLiveSeekableRange(r, a);
    }
  }
  checkPendingTracks() {
    const {
      bufferCodecEventsExpected: e,
      operationQueue: t,
      pendingTracks: i
    } = this, n = Object.keys(i).length;
    if (n && (!e || n === 2 || "audiovideo" in i)) {
      this.createSourceBuffers(i), this.pendingTracks = {};
      const r = this.getSourceBufferTypes();
      if (r.length)
        this.hls.trigger(j.BUFFER_CREATED, {
          tracks: this.tracks
        }), r.forEach((a) => {
          t.executeNext(a);
        });
      else {
        const a = new Error("could not create source buffer for media codec(s)");
        this.hls.trigger(j.ERROR, {
          type: qt.MEDIA_ERROR,
          details: Xe.BUFFER_INCOMPATIBLE_CODECS_ERROR,
          fatal: !0,
          error: a,
          reason: a.message
        });
      }
    }
  }
  createSourceBuffers(e) {
    const {
      sourceBuffer: t,
      mediaSource: i
    } = this;
    if (!i)
      throw Error("createSourceBuffers called when mediaSource was null");
    for (const r in e)
      if (!t[r]) {
        var n;
        const a = e[r];
        if (!a)
          throw Error(`source buffer exists for track ${r}, however track does not`);
        let o = ((n = a.levelCodec) == null ? void 0 : n.indexOf(",")) === -1 ? a.levelCodec : a.codec;
        o && r.slice(0, 5) === "audio" && (o = gx(o, this.appendSource));
        const l = `${a.container};codecs=${o}`;
        this.log(`creating sourceBuffer(${l})`);
        try {
          const c = t[r] = i.addSourceBuffer(l), h = r;
          this.addBufferListener(h, "updatestart", this._onSBUpdateStart), this.addBufferListener(h, "updateend", this._onSBUpdateEnd), this.addBufferListener(h, "error", this._onSBUpdateError), this.appendSource && this.addBufferListener(h, "bufferedchange", (u, d) => {
            const f = d.removedRanges;
            f != null && f.length && this.hls.trigger(j.BUFFER_FLUSHED, {
              type: r
            });
          }), this.tracks[r] = {
            buffer: c,
            codec: o,
            container: a.container,
            levelCodec: a.levelCodec,
            metadata: a.metadata,
            id: a.id
          };
        } catch (c) {
          this.error(`error while trying to add sourceBuffer: ${c.message}`), this.hls.trigger(j.ERROR, {
            type: qt.MEDIA_ERROR,
            details: Xe.BUFFER_ADD_CODEC_ERROR,
            fatal: !1,
            error: c,
            sourceBufferName: r,
            mimeType: l
          });
        }
      }
  }
  get mediaSrc() {
    var e;
    const t = ((e = this.media) == null ? void 0 : e.firstChild) || this.media;
    return t == null ? void 0 : t.src;
  }
  _onSBUpdateStart(e) {
    const {
      operationQueue: t
    } = this;
    t.current(e).onStart();
  }
  _onSBUpdateEnd(e) {
    var t;
    if (((t = this.mediaSource) == null ? void 0 : t.readyState) === "closed") {
      this.resetBuffer(e);
      return;
    }
    const {
      operationQueue: i
    } = this;
    i.current(e).onComplete(), i.shiftAndExecuteNext(e);
  }
  _onSBUpdateError(e, t) {
    var i;
    const n = new Error(`${e} SourceBuffer error. MediaSource readyState: ${(i = this.mediaSource) == null ? void 0 : i.readyState}`);
    this.error(`${n}`, t), this.hls.trigger(j.ERROR, {
      type: qt.MEDIA_ERROR,
      details: Xe.BUFFER_APPENDING_ERROR,
      sourceBufferName: e,
      error: n,
      fatal: !1
    });
    const r = this.operationQueue.current(e);
    r && r.onError(n);
  }
  // This method must result in an updateend event; if remove is not called, _onSBUpdateEnd must be called manually
  removeExecutor(e, t, i) {
    const {
      media: n,
      mediaSource: r,
      operationQueue: a,
      sourceBuffer: o
    } = this, l = o[e];
    if (!n || !r || !l) {
      this.warn(`Attempting to remove from the ${e} SourceBuffer, but it does not exist`), a.shiftAndExecuteNext(e);
      return;
    }
    const c = Bt(n.duration) ? n.duration : 1 / 0, h = Bt(r.duration) ? r.duration : 1 / 0, u = Math.max(0, t), d = Math.min(i, c, h);
    d > u && (!l.ending || l.ended) ? (l.ended = !1, this.log(`Removing [${u},${d}] from the ${e} SourceBuffer`), l.remove(u, d)) : a.shiftAndExecuteNext(e);
  }
  // This method must result in an updateend event; if append is not called, _onSBUpdateEnd must be called manually
  appendExecutor(e, t) {
    const i = this.sourceBuffer[t];
    if (!i) {
      if (!this.pendingTracks[t])
        throw new Error(`Attempting to append to the ${t} SourceBuffer, but it does not exist`);
      return;
    }
    i.ended = !1, i.appendBuffer(e);
  }
  // Enqueues an operation to each SourceBuffer queue which, upon execution, resolves a promise. When all promises
  // resolve, the onUnblocked function is executed. Functions calling this method do not need to unblock the queue
  // upon completion, since we already do it here
  blockBuffers(e, t = this.getSourceBufferTypes()) {
    if (!t.length) {
      this.log("Blocking operation requested, but no SourceBuffers exist"), Promise.resolve().then(e);
      return;
    }
    const {
      operationQueue: i
    } = this, n = t.map((r) => i.appendBlocker(r));
    Promise.all(n).then(() => {
      e(), t.forEach((r) => {
        const a = this.sourceBuffer[r];
        a != null && a.updating || i.shiftAndExecuteNext(r);
      });
    });
  }
  getSourceBufferTypes() {
    return Object.keys(this.sourceBuffer);
  }
  addBufferListener(e, t, i) {
    const n = this.sourceBuffer[e];
    if (!n)
      return;
    const r = i.bind(this, e);
    this.listeners[e].push({
      event: t,
      listener: r
    }), n.addEventListener(t, r);
  }
  removeBufferListeners(e) {
    const t = this.sourceBuffer[e];
    t && this.listeners[e].forEach((i) => {
      t.removeEventListener(i.event, i.listener);
    });
  }
}
function E_(s) {
  const e = s.querySelectorAll("source");
  [].slice.call(e).forEach((t) => {
    s.removeChild(t);
  });
}
function vae(s, e) {
  const t = self.document.createElement("source");
  t.type = "video/mp4", t.src = e, s.appendChild(t);
}
const T_ = {
  42: 225,
  // lowercase a, acute accent
  92: 233,
  // lowercase e, acute accent
  94: 237,
  // lowercase i, acute accent
  95: 243,
  // lowercase o, acute accent
  96: 250,
  // lowercase u, acute accent
  123: 231,
  // lowercase c with cedilla
  124: 247,
  // division symbol
  125: 209,
  // uppercase N tilde
  126: 241,
  // lowercase n tilde
  127: 9608,
  // Full block
  // THIS BLOCK INCLUDES THE 16 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
  // THAT COME FROM HI BYTE=0x11 AND LOW BETWEEN 0x30 AND 0x3F
  // THIS MEANS THAT \x50 MUST BE ADDED TO THE VALUES
  128: 174,
  // Registered symbol (R)
  129: 176,
  // degree sign
  130: 189,
  // 1/2 symbol
  131: 191,
  // Inverted (open) question mark
  132: 8482,
  // Trademark symbol (TM)
  133: 162,
  // Cents symbol
  134: 163,
  // Pounds sterling
  135: 9834,
  // Music 8'th note
  136: 224,
  // lowercase a, grave accent
  137: 32,
  // transparent space (regular)
  138: 232,
  // lowercase e, grave accent
  139: 226,
  // lowercase a, circumflex accent
  140: 234,
  // lowercase e, circumflex accent
  141: 238,
  // lowercase i, circumflex accent
  142: 244,
  // lowercase o, circumflex accent
  143: 251,
  // lowercase u, circumflex accent
  // THIS BLOCK INCLUDES THE 32 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
  // THAT COME FROM HI BYTE=0x12 AND LOW BETWEEN 0x20 AND 0x3F
  144: 193,
  // capital letter A with acute
  145: 201,
  // capital letter E with acute
  146: 211,
  // capital letter O with acute
  147: 218,
  // capital letter U with acute
  148: 220,
  // capital letter U with diaresis
  149: 252,
  // lowercase letter U with diaeresis
  150: 8216,
  // opening single quote
  151: 161,
  // inverted exclamation mark
  152: 42,
  // asterisk
  153: 8217,
  // closing single quote
  154: 9473,
  // box drawings heavy horizontal
  155: 169,
  // copyright sign
  156: 8480,
  // Service mark
  157: 8226,
  // (round) bullet
  158: 8220,
  // Left double quotation mark
  159: 8221,
  // Right double quotation mark
  160: 192,
  // uppercase A, grave accent
  161: 194,
  // uppercase A, circumflex
  162: 199,
  // uppercase C with cedilla
  163: 200,
  // uppercase E, grave accent
  164: 202,
  // uppercase E, circumflex
  165: 203,
  // capital letter E with diaresis
  166: 235,
  // lowercase letter e with diaresis
  167: 206,
  // uppercase I, circumflex
  168: 207,
  // uppercase I, with diaresis
  169: 239,
  // lowercase i, with diaresis
  170: 212,
  // uppercase O, circumflex
  171: 217,
  // uppercase U, grave accent
  172: 249,
  // lowercase u, grave accent
  173: 219,
  // uppercase U, circumflex
  174: 171,
  // left-pointing double angle quotation mark
  175: 187,
  // right-pointing double angle quotation mark
  // THIS BLOCK INCLUDES THE 32 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
  // THAT COME FROM HI BYTE=0x13 AND LOW BETWEEN 0x20 AND 0x3F
  176: 195,
  // Uppercase A, tilde
  177: 227,
  // Lowercase a, tilde
  178: 205,
  // Uppercase I, acute accent
  179: 204,
  // Uppercase I, grave accent
  180: 236,
  // Lowercase i, grave accent
  181: 210,
  // Uppercase O, grave accent
  182: 242,
  // Lowercase o, grave accent
  183: 213,
  // Uppercase O, tilde
  184: 245,
  // Lowercase o, tilde
  185: 123,
  // Open curly brace
  186: 125,
  // Closing curly brace
  187: 92,
  // Backslash
  188: 94,
  // Caret
  189: 95,
  // Underscore
  190: 124,
  // Pipe (vertical line)
  191: 8764,
  // Tilde operator
  192: 196,
  // Uppercase A, umlaut
  193: 228,
  // Lowercase A, umlaut
  194: 214,
  // Uppercase O, umlaut
  195: 246,
  // Lowercase o, umlaut
  196: 223,
  // Esszett (sharp S)
  197: 165,
  // Yen symbol
  198: 164,
  // Generic currency sign
  199: 9475,
  // Box drawings heavy vertical
  200: 197,
  // Uppercase A, ring
  201: 229,
  // Lowercase A, ring
  202: 216,
  // Uppercase O, stroke
  203: 248,
  // Lowercase o, strok
  204: 9487,
  // Box drawings heavy down and right
  205: 9491,
  // Box drawings heavy down and left
  206: 9495,
  // Box drawings heavy up and right
  207: 9499
  // Box drawings heavy up and left
}, oB = function(e) {
  let t = e;
  return T_.hasOwnProperty(e) && (t = T_[e]), String.fromCharCode(t);
}, Wa = 15, L0 = 100, yae = {
  17: 1,
  18: 3,
  21: 5,
  22: 7,
  23: 9,
  16: 11,
  19: 12,
  20: 14
}, xae = {
  17: 2,
  18: 4,
  21: 6,
  22: 8,
  23: 10,
  19: 13,
  20: 15
}, Aae = {
  25: 1,
  26: 3,
  29: 5,
  30: 7,
  31: 9,
  24: 11,
  27: 12,
  28: 14
}, bae = {
  25: 2,
  26: 4,
  29: 6,
  30: 8,
  31: 10,
  27: 13,
  28: 15
}, Mae = ["white", "green", "blue", "cyan", "red", "yellow", "magenta", "black", "transparent"];
class Sae {
  constructor() {
    this.time = null, this.verboseLevel = 0;
  }
  log(e, t) {
    if (this.verboseLevel >= e) {
      const i = typeof t == "function" ? t() : t;
      ze.log(`${this.time} [${e}] ${i}`);
    }
  }
}
const Ph = function(e) {
  const t = [];
  for (let i = 0; i < e.length; i++)
    t.push(e[i].toString(16));
  return t;
};
class lB {
  constructor() {
    this.foreground = "white", this.underline = !1, this.italics = !1, this.background = "black", this.flash = !1;
  }
  reset() {
    this.foreground = "white", this.underline = !1, this.italics = !1, this.background = "black", this.flash = !1;
  }
  setStyles(e) {
    const t = ["foreground", "underline", "italics", "background", "flash"];
    for (let i = 0; i < t.length; i++) {
      const n = t[i];
      e.hasOwnProperty(n) && (this[n] = e[n]);
    }
  }
  isDefault() {
    return this.foreground === "white" && !this.underline && !this.italics && this.background === "black" && !this.flash;
  }
  equals(e) {
    return this.foreground === e.foreground && this.underline === e.underline && this.italics === e.italics && this.background === e.background && this.flash === e.flash;
  }
  copy(e) {
    this.foreground = e.foreground, this.underline = e.underline, this.italics = e.italics, this.background = e.background, this.flash = e.flash;
  }
  toString() {
    return "color=" + this.foreground + ", underline=" + this.underline + ", italics=" + this.italics + ", background=" + this.background + ", flash=" + this.flash;
  }
}
class wae {
  constructor() {
    this.uchar = " ", this.penState = new lB();
  }
  reset() {
    this.uchar = " ", this.penState.reset();
  }
  setChar(e, t) {
    this.uchar = e, this.penState.copy(t);
  }
  setPenState(e) {
    this.penState.copy(e);
  }
  equals(e) {
    return this.uchar === e.uchar && this.penState.equals(e.penState);
  }
  copy(e) {
    this.uchar = e.uchar, this.penState.copy(e.penState);
  }
  isEmpty() {
    return this.uchar === " " && this.penState.isDefault();
  }
}
class Eae {
  constructor(e) {
    this.chars = [], this.pos = 0, this.currPenState = new lB(), this.cueStartTime = null, this.logger = void 0;
    for (let t = 0; t < L0; t++)
      this.chars.push(new wae());
    this.logger = e;
  }
  equals(e) {
    for (let t = 0; t < L0; t++)
      if (!this.chars[t].equals(e.chars[t]))
        return !1;
    return !0;
  }
  copy(e) {
    for (let t = 0; t < L0; t++)
      this.chars[t].copy(e.chars[t]);
  }
  isEmpty() {
    let e = !0;
    for (let t = 0; t < L0; t++)
      if (!this.chars[t].isEmpty()) {
        e = !1;
        break;
      }
    return e;
  }
  /**
   *  Set the cursor to a valid column.
   */
  setCursor(e) {
    this.pos !== e && (this.pos = e), this.pos < 0 ? (this.logger.log(3, "Negative cursor position " + this.pos), this.pos = 0) : this.pos > L0 && (this.logger.log(3, "Too large cursor position " + this.pos), this.pos = L0);
  }
  /**
   * Move the cursor relative to current position.
   */
  moveCursor(e) {
    const t = this.pos + e;
    if (e > 1)
      for (let i = this.pos + 1; i < t + 1; i++)
        this.chars[i].setPenState(this.currPenState);
    this.setCursor(t);
  }
  /**
   * Backspace, move one step back and clear character.
   */
  backSpace() {
    this.moveCursor(-1), this.chars[this.pos].setChar(" ", this.currPenState);
  }
  insertChar(e) {
    e >= 144 && this.backSpace();
    const t = oB(e);
    if (this.pos >= L0) {
      this.logger.log(0, () => "Cannot insert " + e.toString(16) + " (" + t + ") at position " + this.pos + ". Skipping it!");
      return;
    }
    this.chars[this.pos].setChar(t, this.currPenState), this.moveCursor(1);
  }
  clearFromPos(e) {
    let t;
    for (t = e; t < L0; t++)
      this.chars[t].reset();
  }
  clear() {
    this.clearFromPos(0), this.pos = 0, this.currPenState.reset();
  }
  clearToEndOfRow() {
    this.clearFromPos(this.pos);
  }
  getTextString() {
    const e = [];
    let t = !0;
    for (let i = 0; i < L0; i++) {
      const n = this.chars[i].uchar;
      n !== " " && (t = !1), e.push(n);
    }
    return t ? "" : e.join("");
  }
  setPenStyles(e) {
    this.currPenState.setStyles(e), this.chars[this.pos].setPenState(this.currPenState);
  }
}
class mA {
  constructor(e) {
    this.rows = [], this.currRow = Wa - 1, this.nrRollUpRows = null, this.lastOutputScreen = null, this.logger = void 0;
    for (let t = 0; t < Wa; t++)
      this.rows.push(new Eae(e));
    this.logger = e;
  }
  reset() {
    for (let e = 0; e < Wa; e++)
      this.rows[e].clear();
    this.currRow = Wa - 1;
  }
  equals(e) {
    let t = !0;
    for (let i = 0; i < Wa; i++)
      if (!this.rows[i].equals(e.rows[i])) {
        t = !1;
        break;
      }
    return t;
  }
  copy(e) {
    for (let t = 0; t < Wa; t++)
      this.rows[t].copy(e.rows[t]);
  }
  isEmpty() {
    let e = !0;
    for (let t = 0; t < Wa; t++)
      if (!this.rows[t].isEmpty()) {
        e = !1;
        break;
      }
    return e;
  }
  backSpace() {
    this.rows[this.currRow].backSpace();
  }
  clearToEndOfRow() {
    this.rows[this.currRow].clearToEndOfRow();
  }
  /**
   * Insert a character (without styling) in the current row.
   */
  insertChar(e) {
    this.rows[this.currRow].insertChar(e);
  }
  setPen(e) {
    this.rows[this.currRow].setPenStyles(e);
  }
  moveCursor(e) {
    this.rows[this.currRow].moveCursor(e);
  }
  setCursor(e) {
    this.logger.log(2, "setCursor: " + e), this.rows[this.currRow].setCursor(e);
  }
  setPAC(e) {
    this.logger.log(2, () => "pacData = " + JSON.stringify(e));
    let t = e.row - 1;
    if (this.nrRollUpRows && t < this.nrRollUpRows - 1 && (t = this.nrRollUpRows - 1), this.nrRollUpRows && this.currRow !== t) {
      for (let o = 0; o < Wa; o++)
        this.rows[o].clear();
      const r = this.currRow + 1 - this.nrRollUpRows, a = this.lastOutputScreen;
      if (a) {
        const o = a.rows[r].cueStartTime, l = this.logger.time;
        if (o !== null && l !== null && o < l)
          for (let c = 0; c < this.nrRollUpRows; c++)
            this.rows[t - this.nrRollUpRows + c + 1].copy(a.rows[r + c]);
      }
    }
    this.currRow = t;
    const i = this.rows[this.currRow];
    if (e.indent !== null) {
      const r = e.indent, a = Math.max(r - 1, 0);
      i.setCursor(e.indent), e.color = i.chars[a].penState.foreground;
    }
    const n = {
      foreground: e.color,
      underline: e.underline,
      italics: e.italics,
      background: "black",
      flash: !1
    };
    this.setPen(n);
  }
  /**
   * Set background/extra foreground, but first do back_space, and then insert space (backwards compatibility).
   */
  setBkgData(e) {
    this.logger.log(2, () => "bkgData = " + JSON.stringify(e)), this.backSpace(), this.setPen(e), this.insertChar(32);
  }
  setRollUpRows(e) {
    this.nrRollUpRows = e;
  }
  rollUp() {
    if (this.nrRollUpRows === null) {
      this.logger.log(3, "roll_up but nrRollUpRows not set yet");
      return;
    }
    this.logger.log(1, () => this.getDisplayText());
    const e = this.currRow + 1 - this.nrRollUpRows, t = this.rows.splice(e, 1)[0];
    t.clear(), this.rows.splice(this.currRow, 0, t), this.logger.log(2, "Rolling up");
  }
  /**
   * Get all non-empty rows with as unicode text.
   */
  getDisplayText(e) {
    e = e || !1;
    const t = [];
    let i = "", n = -1;
    for (let r = 0; r < Wa; r++) {
      const a = this.rows[r].getTextString();
      a && (n = r + 1, e ? t.push("Row " + n + ": '" + a + "'") : t.push(a.trim()));
    }
    return t.length > 0 && (e ? i = "[" + t.join(" | ") + "]" : i = t.join(`
`)), i;
  }
  getTextAndFormat() {
    return this.rows;
  }
}
class C_ {
  constructor(e, t, i) {
    this.chNr = void 0, this.outputFilter = void 0, this.mode = void 0, this.verbose = void 0, this.displayedMemory = void 0, this.nonDisplayedMemory = void 0, this.lastOutputScreen = void 0, this.currRollUpRow = void 0, this.writeScreen = void 0, this.cueStartTime = void 0, this.logger = void 0, this.chNr = e, this.outputFilter = t, this.mode = null, this.verbose = 0, this.displayedMemory = new mA(i), this.nonDisplayedMemory = new mA(i), this.lastOutputScreen = new mA(i), this.currRollUpRow = this.displayedMemory.rows[Wa - 1], this.writeScreen = this.displayedMemory, this.mode = null, this.cueStartTime = null, this.logger = i;
  }
  reset() {
    this.mode = null, this.displayedMemory.reset(), this.nonDisplayedMemory.reset(), this.lastOutputScreen.reset(), this.outputFilter.reset(), this.currRollUpRow = this.displayedMemory.rows[Wa - 1], this.writeScreen = this.displayedMemory, this.mode = null, this.cueStartTime = null;
  }
  getHandler() {
    return this.outputFilter;
  }
  setHandler(e) {
    this.outputFilter = e;
  }
  setPAC(e) {
    this.writeScreen.setPAC(e);
  }
  setBkgData(e) {
    this.writeScreen.setBkgData(e);
  }
  setMode(e) {
    e !== this.mode && (this.mode = e, this.logger.log(2, () => "MODE=" + e), this.mode === "MODE_POP-ON" ? this.writeScreen = this.nonDisplayedMemory : (this.writeScreen = this.displayedMemory, this.writeScreen.reset()), this.mode !== "MODE_ROLL-UP" && (this.displayedMemory.nrRollUpRows = null, this.nonDisplayedMemory.nrRollUpRows = null), this.mode = e);
  }
  insertChars(e) {
    for (let i = 0; i < e.length; i++)
      this.writeScreen.insertChar(e[i]);
    const t = this.writeScreen === this.displayedMemory ? "DISP" : "NON_DISP";
    this.logger.log(2, () => t + ": " + this.writeScreen.getDisplayText(!0)), (this.mode === "MODE_PAINT-ON" || this.mode === "MODE_ROLL-UP") && (this.logger.log(1, () => "DISPLAYED: " + this.displayedMemory.getDisplayText(!0)), this.outputDataUpdate());
  }
  ccRCL() {
    this.logger.log(2, "RCL - Resume Caption Loading"), this.setMode("MODE_POP-ON");
  }
  ccBS() {
    this.logger.log(2, "BS - BackSpace"), this.mode !== "MODE_TEXT" && (this.writeScreen.backSpace(), this.writeScreen === this.displayedMemory && this.outputDataUpdate());
  }
  ccAOF() {
  }
  ccAON() {
  }
  ccDER() {
    this.logger.log(2, "DER- Delete to End of Row"), this.writeScreen.clearToEndOfRow(), this.outputDataUpdate();
  }
  ccRU(e) {
    this.logger.log(2, "RU(" + e + ") - Roll Up"), this.writeScreen = this.displayedMemory, this.setMode("MODE_ROLL-UP"), this.writeScreen.setRollUpRows(e);
  }
  ccFON() {
    this.logger.log(2, "FON - Flash On"), this.writeScreen.setPen({
      flash: !0
    });
  }
  ccRDC() {
    this.logger.log(2, "RDC - Resume Direct Captioning"), this.setMode("MODE_PAINT-ON");
  }
  ccTR() {
    this.logger.log(2, "TR"), this.setMode("MODE_TEXT");
  }
  ccRTD() {
    this.logger.log(2, "RTD"), this.setMode("MODE_TEXT");
  }
  ccEDM() {
    this.logger.log(2, "EDM - Erase Displayed Memory"), this.displayedMemory.reset(), this.outputDataUpdate(!0);
  }
  ccCR() {
    this.logger.log(2, "CR - Carriage Return"), this.writeScreen.rollUp(), this.outputDataUpdate(!0);
  }
  ccENM() {
    this.logger.log(2, "ENM - Erase Non-displayed Memory"), this.nonDisplayedMemory.reset();
  }
  ccEOC() {
    if (this.logger.log(2, "EOC - End Of Caption"), this.mode === "MODE_POP-ON") {
      const e = this.displayedMemory;
      this.displayedMemory = this.nonDisplayedMemory, this.nonDisplayedMemory = e, this.writeScreen = this.nonDisplayedMemory, this.logger.log(1, () => "DISP: " + this.displayedMemory.getDisplayText());
    }
    this.outputDataUpdate(!0);
  }
  ccTO(e) {
    this.logger.log(2, "TO(" + e + ") - Tab Offset"), this.writeScreen.moveCursor(e);
  }
  ccMIDROW(e) {
    const t = {
      flash: !1
    };
    if (t.underline = e % 2 === 1, t.italics = e >= 46, t.italics)
      t.foreground = "white";
    else {
      const i = Math.floor(e / 2) - 16, n = ["white", "green", "blue", "cyan", "red", "yellow", "magenta"];
      t.foreground = n[i];
    }
    this.logger.log(2, "MIDROW: " + JSON.stringify(t)), this.writeScreen.setPen(t);
  }
  outputDataUpdate(e = !1) {
    const t = this.logger.time;
    t !== null && this.outputFilter && (this.cueStartTime === null && !this.displayedMemory.isEmpty() ? this.cueStartTime = t : this.displayedMemory.equals(this.lastOutputScreen) || (this.outputFilter.newCue(this.cueStartTime, t, this.lastOutputScreen), e && this.outputFilter.dispatchCue && this.outputFilter.dispatchCue(), this.cueStartTime = this.displayedMemory.isEmpty() ? null : t), this.lastOutputScreen.copy(this.displayedMemory));
  }
  cueSplitAtTime(e) {
    this.outputFilter && (this.displayedMemory.isEmpty() || (this.outputFilter.newCue && this.outputFilter.newCue(this.cueStartTime, e, this.displayedMemory), this.cueStartTime = e));
  }
}
class __ {
  constructor(e, t, i) {
    this.channels = void 0, this.currentChannel = 0, this.cmdHistory = R_(), this.logger = void 0;
    const n = this.logger = new Sae();
    this.channels = [null, new C_(e, t, n), new C_(e + 1, i, n)];
  }
  getHandler(e) {
    return this.channels[e].getHandler();
  }
  setHandler(e, t) {
    this.channels[e].setHandler(t);
  }
  /**
   * Add data for time t in forms of list of bytes (unsigned ints). The bytes are treated as pairs.
   */
  addData(e, t) {
    let i, n, r, a = !1;
    this.logger.time = e;
    for (let o = 0; o < t.length; o += 2)
      if (n = t[o] & 127, r = t[o + 1] & 127, !(n === 0 && r === 0)) {
        if (this.logger.log(3, "[" + Ph([t[o], t[o + 1]]) + "] -> (" + Ph([n, r]) + ")"), i = this.parseCmd(n, r), i || (i = this.parseMidrow(n, r)), i || (i = this.parsePAC(n, r)), i || (i = this.parseBackgroundAttributes(n, r)), !i && (a = this.parseChars(n, r), a)) {
          const l = this.currentChannel;
          l && l > 0 ? this.channels[l].insertChars(a) : this.logger.log(2, "No channel found yet. TEXT-MODE?");
        }
        !i && !a && this.logger.log(2, "Couldn't parse cleaned data " + Ph([n, r]) + " orig: " + Ph([t[o], t[o + 1]]));
      }
  }
  /**
   * Parse Command.
   * @returns True if a command was found
   */
  parseCmd(e, t) {
    const {
      cmdHistory: i
    } = this, n = (e === 20 || e === 28 || e === 21 || e === 29) && t >= 32 && t <= 47, r = (e === 23 || e === 31) && t >= 33 && t <= 35;
    if (!(n || r))
      return !1;
    if (P_(e, t, i))
      return Id(null, null, i), this.logger.log(3, "Repeated command (" + Ph([e, t]) + ") is dropped"), !0;
    const a = e === 20 || e === 21 || e === 23 ? 1 : 2, o = this.channels[a];
    return e === 20 || e === 21 || e === 28 || e === 29 ? t === 32 ? o.ccRCL() : t === 33 ? o.ccBS() : t === 34 ? o.ccAOF() : t === 35 ? o.ccAON() : t === 36 ? o.ccDER() : t === 37 ? o.ccRU(2) : t === 38 ? o.ccRU(3) : t === 39 ? o.ccRU(4) : t === 40 ? o.ccFON() : t === 41 ? o.ccRDC() : t === 42 ? o.ccTR() : t === 43 ? o.ccRTD() : t === 44 ? o.ccEDM() : t === 45 ? o.ccCR() : t === 46 ? o.ccENM() : t === 47 && o.ccEOC() : o.ccTO(t - 32), Id(e, t, i), this.currentChannel = a, !0;
  }
  /**
   * Parse midrow styling command
   */
  parseMidrow(e, t) {
    let i = 0;
    if ((e === 17 || e === 25) && t >= 32 && t <= 47) {
      if (e === 17 ? i = 1 : i = 2, i !== this.currentChannel)
        return this.logger.log(0, "Mismatch channel in midrow parsing"), !1;
      const n = this.channels[i];
      return n ? (n.ccMIDROW(t), this.logger.log(3, "MIDROW (" + Ph([e, t]) + ")"), !0) : !1;
    }
    return !1;
  }
  /**
   * Parse Preable Access Codes (Table 53).
   * @returns {Boolean} Tells if PAC found
   */
  parsePAC(e, t) {
    let i;
    const n = this.cmdHistory, r = (e >= 17 && e <= 23 || e >= 25 && e <= 31) && t >= 64 && t <= 127, a = (e === 16 || e === 24) && t >= 64 && t <= 95;
    if (!(r || a))
      return !1;
    if (P_(e, t, n))
      return Id(null, null, n), !0;
    const o = e <= 23 ? 1 : 2;
    t >= 64 && t <= 95 ? i = o === 1 ? yae[e] : Aae[e] : i = o === 1 ? xae[e] : bae[e];
    const l = this.channels[o];
    return l ? (l.setPAC(this.interpretPAC(i, t)), Id(e, t, n), this.currentChannel = o, !0) : !1;
  }
  /**
   * Interpret the second byte of the pac, and return the information.
   * @returns pacData with style parameters
   */
  interpretPAC(e, t) {
    let i;
    const n = {
      color: null,
      italics: !1,
      indent: null,
      underline: !1,
      row: e
    };
    return t > 95 ? i = t - 96 : i = t - 64, n.underline = (i & 1) === 1, i <= 13 ? n.color = ["white", "green", "blue", "cyan", "red", "yellow", "magenta", "white"][Math.floor(i / 2)] : i <= 15 ? (n.italics = !0, n.color = "white") : n.indent = Math.floor((i - 16) / 2) * 4, n;
  }
  /**
   * Parse characters.
   * @returns An array with 1 to 2 codes corresponding to chars, if found. null otherwise.
   */
  parseChars(e, t) {
    let i, n = null, r = null;
    if (e >= 25 ? (i = 2, r = e - 8) : (i = 1, r = e), r >= 17 && r <= 19) {
      let a;
      r === 17 ? a = t + 80 : r === 18 ? a = t + 112 : a = t + 144, this.logger.log(2, "Special char '" + oB(a) + "' in channel " + i), n = [a];
    } else
      e >= 32 && e <= 127 && (n = t === 0 ? [e] : [e, t]);
    if (n) {
      const a = Ph(n);
      this.logger.log(3, "Char codes =  " + a.join(",")), Id(e, t, this.cmdHistory);
    }
    return n;
  }
  /**
   * Parse extended background attributes as well as new foreground color black.
   * @returns True if background attributes are found
   */
  parseBackgroundAttributes(e, t) {
    const i = (e === 16 || e === 24) && t >= 32 && t <= 47, n = (e === 23 || e === 31) && t >= 45 && t <= 47;
    if (!(i || n))
      return !1;
    let r;
    const a = {};
    e === 16 || e === 24 ? (r = Math.floor((t - 32) / 2), a.background = Mae[r], t % 2 === 1 && (a.background = a.background + "_semi")) : t === 45 ? a.background = "transparent" : (a.foreground = "black", t === 47 && (a.underline = !0));
    const o = e <= 23 ? 1 : 2;
    return this.channels[o].setBkgData(a), Id(e, t, this.cmdHistory), !0;
  }
  /**
   * Reset state of parser and its channels.
   */
  reset() {
    for (let e = 0; e < Object.keys(this.channels).length; e++) {
      const t = this.channels[e];
      t && t.reset();
    }
    this.cmdHistory = R_();
  }
  /**
   * Trigger the generation of a cue, and the start of a new one if displayScreens are not empty.
   */
  cueSplitAtTime(e) {
    for (let t = 0; t < this.channels.length; t++) {
      const i = this.channels[t];
      i && i.cueSplitAtTime(e);
    }
  }
}
function Id(s, e, t) {
  t.a = s, t.b = e;
}
function P_(s, e, t) {
  return t.a === s && t.b === e;
}
function R_() {
  return {
    a: null,
    b: null
  };
}
class c3 {
  constructor(e, t) {
    this.timelineController = void 0, this.cueRanges = [], this.trackName = void 0, this.startTime = null, this.endTime = null, this.screen = null, this.timelineController = e, this.trackName = t;
  }
  dispatchCue() {
    this.startTime !== null && (this.timelineController.addCues(this.trackName, this.startTime, this.endTime, this.screen, this.cueRanges), this.startTime = null);
  }
  newCue(e, t, i) {
    (this.startTime === null || this.startTime > e) && (this.startTime = e), this.endTime = t, this.screen = i, this.timelineController.createCaptionsTrack(this.trackName);
  }
  reset() {
    this.cueRanges = [], this.startTime = null;
  }
}
var aw = function() {
  if (Ep != null && Ep.VTTCue)
    return self.VTTCue;
  const s = ["", "lr", "rl"], e = ["start", "middle", "end", "left", "right"];
  function t(o, l) {
    if (typeof l != "string" || !Array.isArray(o))
      return !1;
    const c = l.toLowerCase();
    return ~o.indexOf(c) ? c : !1;
  }
  function i(o) {
    return t(s, o);
  }
  function n(o) {
    return t(e, o);
  }
  function r(o, ...l) {
    let c = 1;
    for (; c < arguments.length; c++) {
      const h = arguments[c];
      for (const u in h)
        o[u] = h[u];
    }
    return o;
  }
  function a(o, l, c) {
    const h = this, u = {
      enumerable: !0
    };
    h.hasBeenReset = !1;
    let d = "", f = !1, p = o, v = l, g = c, m = null, x = "", y = !0, A = "auto", b = "start", M = 50, w = "middle", C = 50, T = "middle";
    Object.defineProperty(h, "id", r({}, u, {
      get: function() {
        return d;
      },
      set: function(E) {
        d = "" + E;
      }
    })), Object.defineProperty(h, "pauseOnExit", r({}, u, {
      get: function() {
        return f;
      },
      set: function(E) {
        f = !!E;
      }
    })), Object.defineProperty(h, "startTime", r({}, u, {
      get: function() {
        return p;
      },
      set: function(E) {
        if (typeof E != "number")
          throw new TypeError("Start time must be set to a number.");
        p = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "endTime", r({}, u, {
      get: function() {
        return v;
      },
      set: function(E) {
        if (typeof E != "number")
          throw new TypeError("End time must be set to a number.");
        v = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "text", r({}, u, {
      get: function() {
        return g;
      },
      set: function(E) {
        g = "" + E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "region", r({}, u, {
      get: function() {
        return m;
      },
      set: function(E) {
        m = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "vertical", r({}, u, {
      get: function() {
        return x;
      },
      set: function(E) {
        const P = i(E);
        if (P === !1)
          throw new SyntaxError("An invalid or illegal string was specified.");
        x = P, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "snapToLines", r({}, u, {
      get: function() {
        return y;
      },
      set: function(E) {
        y = !!E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "line", r({}, u, {
      get: function() {
        return A;
      },
      set: function(E) {
        if (typeof E != "number" && E !== "auto")
          throw new SyntaxError("An invalid number or illegal string was specified.");
        A = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "lineAlign", r({}, u, {
      get: function() {
        return b;
      },
      set: function(E) {
        const P = n(E);
        if (!P)
          throw new SyntaxError("An invalid or illegal string was specified.");
        b = P, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "position", r({}, u, {
      get: function() {
        return M;
      },
      set: function(E) {
        if (E < 0 || E > 100)
          throw new Error("Position must be between 0 and 100.");
        M = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "positionAlign", r({}, u, {
      get: function() {
        return w;
      },
      set: function(E) {
        const P = n(E);
        if (!P)
          throw new SyntaxError("An invalid or illegal string was specified.");
        w = P, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "size", r({}, u, {
      get: function() {
        return C;
      },
      set: function(E) {
        if (E < 0 || E > 100)
          throw new Error("Size must be between 0 and 100.");
        C = E, this.hasBeenReset = !0;
      }
    })), Object.defineProperty(h, "align", r({}, u, {
      get: function() {
        return T;
      },
      set: function(E) {
        const P = n(E);
        if (!P)
          throw new SyntaxError("An invalid or illegal string was specified.");
        T = P, this.hasBeenReset = !0;
      }
    })), h.displayState = void 0;
  }
  return a.prototype.getCueAsHTML = function() {
    return self.WebVTT.convertCueToDOMTree(self, this.text);
  }, a;
}();
class Tae {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  decode(e, t) {
    if (!e)
      return "";
    if (typeof e != "string")
      throw new Error("Error - expected string data.");
    return decodeURIComponent(encodeURIComponent(e));
  }
}
function cB(s) {
  function e(i, n, r, a) {
    return (i | 0) * 3600 + (n | 0) * 60 + (r | 0) + parseFloat(a || 0);
  }
  const t = s.match(/^(?:(\d+):)?(\d{2}):(\d{2})(\.\d+)?/);
  return t ? parseFloat(t[2]) > 59 ? e(t[2], t[3], 0, t[4]) : e(t[1], t[2], t[3], t[4]) : null;
}
class Cae {
  constructor() {
    this.values = /* @__PURE__ */ Object.create(null);
  }
  // Only accept the first assignment to any key.
  set(e, t) {
    !this.get(e) && t !== "" && (this.values[e] = t);
  }
  // Return the value for a key, or a default value.
  // If 'defaultKey' is passed then 'dflt' is assumed to be an object with
  // a number of possible default values as properties where 'defaultKey' is
  // the key of the property that will be chosen; otherwise it's assumed to be
  // a single value.
  get(e, t, i) {
    return i ? this.has(e) ? this.values[e] : t[i] : this.has(e) ? this.values[e] : t;
  }
  // Check whether we have a value for a key.
  has(e) {
    return e in this.values;
  }
  // Accept a setting if its one of the given alternatives.
  alt(e, t, i) {
    for (let n = 0; n < i.length; ++n)
      if (t === i[n]) {
        this.set(e, t);
        break;
      }
  }
  // Accept a setting if its a valid (signed) integer.
  integer(e, t) {
    /^-?\d+$/.test(t) && this.set(e, parseInt(t, 10));
  }
  // Accept a setting if its a valid percentage.
  percent(e, t) {
    if (/^([\d]{1,3})(\.[\d]*)?%$/.test(t)) {
      const i = parseFloat(t);
      if (i >= 0 && i <= 100)
        return this.set(e, i), !0;
    }
    return !1;
  }
}
function hB(s, e, t, i) {
  const n = i ? s.split(i) : [s];
  for (const r in n) {
    if (typeof n[r] != "string")
      continue;
    const a = n[r].split(t);
    if (a.length !== 2)
      continue;
    const o = a[0], l = a[1];
    e(o, l);
  }
}
const Ob = new aw(0, 0, ""), h3 = Ob.align === "middle" ? "middle" : "center";
function _ae(s, e, t) {
  const i = s;
  function n() {
    const o = cB(s);
    if (o === null)
      throw new Error("Malformed timestamp: " + i);
    return s = s.replace(/^[^\sa-zA-Z-]+/, ""), o;
  }
  function r(o, l) {
    const c = new Cae();
    hB(o, function(d, f) {
      let p;
      switch (d) {
        case "region":
          for (let v = t.length - 1; v >= 0; v--)
            if (t[v].id === f) {
              c.set(d, t[v].region);
              break;
            }
          break;
        case "vertical":
          c.alt(d, f, ["rl", "lr"]);
          break;
        case "line":
          p = f.split(","), c.integer(d, p[0]), c.percent(d, p[0]) && c.set("snapToLines", !1), c.alt(d, p[0], ["auto"]), p.length === 2 && c.alt("lineAlign", p[1], ["start", h3, "end"]);
          break;
        case "position":
          p = f.split(","), c.percent(d, p[0]), p.length === 2 && c.alt("positionAlign", p[1], ["start", h3, "end", "line-left", "line-right", "auto"]);
          break;
        case "size":
          c.percent(d, f);
          break;
        case "align":
          c.alt(d, f, ["start", h3, "end", "left", "right"]);
          break;
      }
    }, /:/, /\s/), l.region = c.get("region", null), l.vertical = c.get("vertical", "");
    let h = c.get("line", "auto");
    h === "auto" && Ob.line === -1 && (h = -1), l.line = h, l.lineAlign = c.get("lineAlign", "start"), l.snapToLines = c.get("snapToLines", !0), l.size = c.get("size", 100), l.align = c.get("align", h3);
    let u = c.get("position", "auto");
    u === "auto" && Ob.position === 50 && (u = l.align === "start" || l.align === "left" ? 0 : l.align === "end" || l.align === "right" ? 100 : 50), l.position = u;
  }
  function a() {
    s = s.replace(/^\s+/, "");
  }
  if (a(), e.startTime = n(), a(), s.slice(0, 3) !== "-->")
    throw new Error("Malformed time stamp (time stamps must be separated by '-->'): " + i);
  s = s.slice(3), a(), e.endTime = n(), a(), r(s, e);
}
function uB(s) {
  return s.replace(/<br(?: \/)?>/gi, `
`);
}
class Pae {
  constructor() {
    this.state = "INITIAL", this.buffer = "", this.decoder = new Tae(), this.regionList = [], this.cue = null, this.oncue = void 0, this.onparsingerror = void 0, this.onflush = void 0;
  }
  parse(e) {
    const t = this;
    e && (t.buffer += t.decoder.decode(e, {
      stream: !0
    }));
    function i() {
      let r = t.buffer, a = 0;
      for (r = uB(r); a < r.length && r[a] !== "\r" && r[a] !== `
`; )
        ++a;
      const o = r.slice(0, a);
      return r[a] === "\r" && ++a, r[a] === `
` && ++a, t.buffer = r.slice(a), o;
    }
    function n(r) {
      hB(r, function(a, o) {
      }, /:/);
    }
    try {
      let r = "";
      if (t.state === "INITIAL") {
        if (!/\r\n|\n/.test(t.buffer))
          return this;
        r = i();
        const o = r.match(/^(ï»¿)?WEBVTT([ \t].*)?$/);
        if (!(o != null && o[0]))
          throw new Error("Malformed WebVTT signature.");
        t.state = "HEADER";
      }
      let a = !1;
      for (; t.buffer; ) {
        if (!/\r\n|\n/.test(t.buffer))
          return this;
        switch (a ? a = !1 : r = i(), t.state) {
          case "HEADER":
            /:/.test(r) ? n(r) : r || (t.state = "ID");
            continue;
          case "NOTE":
            r || (t.state = "ID");
            continue;
          case "ID":
            if (/^NOTE($|[ \t])/.test(r)) {
              t.state = "NOTE";
              break;
            }
            if (!r)
              continue;
            if (t.cue = new aw(0, 0, ""), t.state = "CUE", r.indexOf("-->") === -1) {
              t.cue.id = r;
              continue;
            }
          case "CUE":
            if (!t.cue) {
              t.state = "BADCUE";
              continue;
            }
            try {
              _ae(r, t.cue, t.regionList);
            } catch {
              t.cue = null, t.state = "BADCUE";
              continue;
            }
            t.state = "CUETEXT";
            continue;
          case "CUETEXT":
            {
              const o = r.indexOf("-->") !== -1;
              if (!r || o && (a = !0)) {
                t.oncue && t.cue && t.oncue(t.cue), t.cue = null, t.state = "ID";
                continue;
              }
              if (t.cue === null)
                continue;
              t.cue.text && (t.cue.text += `
`), t.cue.text += r;
            }
            continue;
          case "BADCUE":
            r || (t.state = "ID");
        }
      }
    } catch {
      t.state === "CUETEXT" && t.cue && t.oncue && t.oncue(t.cue), t.cue = null, t.state = t.state === "INITIAL" ? "BADWEBVTT" : "BADCUE";
    }
    return this;
  }
  flush() {
    const e = this;
    try {
      if ((e.cue || e.state === "HEADER") && (e.buffer += `

`, e.parse()), e.state === "INITIAL" || e.state === "BADWEBVTT")
        throw new Error("Malformed WebVTT signature.");
    } catch (t) {
      e.onparsingerror && e.onparsingerror(t);
    }
    return e.onflush && e.onflush(), this;
  }
}
const Rae = /\r\n|\n\r|\n|\r/g, gA = function(e, t, i = 0) {
  return e.slice(i, i + t.length) === t;
}, Dae = function(e) {
  let t = parseInt(e.slice(-3));
  const i = parseInt(e.slice(-6, -4)), n = parseInt(e.slice(-9, -7)), r = e.length > 9 ? parseInt(e.substring(0, e.indexOf(":"))) : 0;
  if (!Bt(t) || !Bt(i) || !Bt(n) || !Bt(r))
    throw Error(`Malformed X-TIMESTAMP-MAP: Local:${e}`);
  return t += 1e3 * i, t += 60 * 1e3 * n, t += 60 * 60 * 1e3 * r, t;
}, vA = function(e) {
  let t = 5381, i = e.length;
  for (; i; )
    t = t * 33 ^ e.charCodeAt(--i);
  return (t >>> 0).toString();
};
function ow(s, e, t) {
  return vA(s.toString()) + vA(e.toString()) + vA(t);
}
const Lae = function(e, t, i) {
  let n = e[t], r = e[n.prevCC];
  if (!r || !r.new && n.new) {
    e.ccOffset = e.presentationOffset = n.start, n.new = !1;
    return;
  }
  for (; (a = r) != null && a.new; ) {
    var a;
    e.ccOffset += n.start - r.start, n.new = !1, n = r, r = e[n.prevCC];
  }
  e.presentationOffset = i;
};
function Iae(s, e, t, i, n, r, a) {
  const o = new Pae(), l = t0(new Uint8Array(s)).trim().replace(Rae, `
`).split(`
`), c = [], h = e ? $re(e.baseTime, e.timescale) : 0;
  let u = "00:00.000", d = 0, f = 0, p, v = !0;
  o.oncue = function(g) {
    const m = t[i];
    let x = t.ccOffset;
    const y = (d - h) / 9e4;
    if (m != null && m.new && (f !== void 0 ? x = t.ccOffset = m.start : Lae(t, i, y)), y) {
      if (!e) {
        p = new Error("Missing initPTS for VTT MPEGTS");
        return;
      }
      x = y - t.presentationOffset;
    }
    const A = g.endTime - g.startTime, b = va((g.startTime + x - f) * 9e4, n * 9e4) / 9e4;
    g.startTime = Math.max(b, 0), g.endTime = Math.max(b + A, 0);
    const M = g.text.trim();
    g.text = decodeURIComponent(encodeURIComponent(M)), g.id || (g.id = ow(g.startTime, g.endTime, M)), g.endTime > 0 && c.push(g);
  }, o.onparsingerror = function(g) {
    p = g;
  }, o.onflush = function() {
    if (p) {
      a(p);
      return;
    }
    r(c);
  }, l.forEach((g) => {
    if (v)
      if (gA(g, "X-TIMESTAMP-MAP=")) {
        v = !1, g.slice(16).split(",").forEach((m) => {
          gA(m, "LOCAL:") ? u = m.slice(6) : gA(m, "MPEGTS:") && (d = parseInt(m.slice(7)));
        });
        try {
          f = Dae(u) / 1e3;
        } catch (m) {
          p = m;
        }
        return;
      } else
        g === "" && (v = !1);
    o.parse(g + `
`);
  }), o.flush();
}
const yA = "stpp.ttml.im1t", dB = /^(\d{2,}):(\d{2}):(\d{2}):(\d{2})\.?(\d+)?$/, fB = /^(\d*(?:\.\d*)?)(h|m|s|ms|f|t)$/, Oae = {
  left: "start",
  center: "center",
  right: "end",
  start: "start",
  end: "end"
};
function D_(s, e, t, i) {
  const n = li(new Uint8Array(s), ["mdat"]);
  if (n.length === 0) {
    i(new Error("Could not parse IMSC1 mdat"));
    return;
  }
  const r = n.map((o) => t0(o)), a = Qre(e.baseTime, 1, e.timescale);
  try {
    r.forEach((o) => t(Nae(o, a)));
  } catch (o) {
    i(o);
  }
}
function Nae(s, e) {
  const n = new DOMParser().parseFromString(s, "text/xml").getElementsByTagName("tt")[0];
  if (!n)
    throw new Error("Invalid ttml");
  const r = {
    frameRate: 30,
    subFrameRate: 1,
    frameRateMultiplier: 0,
    tickRate: 0
  }, a = Object.keys(r).reduce((u, d) => (u[d] = n.getAttribute(`ttp:${d}`) || r[d], u), {}), o = n.getAttribute("xml:space") !== "preserve", l = L_(xA(n, "styling", "style")), c = L_(xA(n, "layout", "region")), h = xA(n, "body", "[begin]");
  return [].map.call(h, (u) => {
    const d = pB(u, o);
    if (!d || !u.hasAttribute("begin"))
      return null;
    const f = bA(u.getAttribute("begin"), a), p = bA(u.getAttribute("dur"), a);
    let v = bA(u.getAttribute("end"), a);
    if (f === null)
      throw I_(u);
    if (v === null) {
      if (p === null)
        throw I_(u);
      v = f + p;
    }
    const g = new aw(f - e, v - e, d);
    g.id = ow(g.startTime, g.endTime, g.text);
    const m = c[u.getAttribute("region")], x = l[u.getAttribute("style")], y = Bae(m, x, l), {
      textAlign: A
    } = y;
    if (A) {
      const b = Oae[A];
      b && (g.lineAlign = b), g.align = A;
    }
    return as(g, y), g;
  }).filter((u) => u !== null);
}
function xA(s, e, t) {
  const i = s.getElementsByTagName(e)[0];
  return i ? [].slice.call(i.querySelectorAll(t)) : [];
}
function L_(s) {
  return s.reduce((e, t) => {
    const i = t.getAttribute("xml:id");
    return i && (e[i] = t), e;
  }, {});
}
function pB(s, e) {
  return [].slice.call(s.childNodes).reduce((t, i, n) => {
    var r;
    return i.nodeName === "br" && n ? t + `
` : (r = i.childNodes) != null && r.length ? pB(i, e) : e ? t + i.textContent.trim().replace(/\s+/g, " ") : t + i.textContent;
  }, "");
}
function Bae(s, e, t) {
  const i = "http://www.w3.org/ns/ttml#styling";
  let n = null;
  const r = [
    "displayAlign",
    "textAlign",
    "color",
    "backgroundColor",
    "fontSize",
    "fontFamily"
    // 'fontWeight',
    // 'lineHeight',
    // 'wrapOption',
    // 'fontStyle',
    // 'direction',
    // 'writingMode'
  ], a = s != null && s.hasAttribute("style") ? s.getAttribute("style") : null;
  return a && t.hasOwnProperty(a) && (n = t[a]), r.reduce((o, l) => {
    const c = AA(e, i, l) || AA(s, i, l) || AA(n, i, l);
    return c && (o[l] = c), o;
  }, {});
}
function AA(s, e, t) {
  return s && s.hasAttributeNS(e, t) ? s.getAttributeNS(e, t) : null;
}
function I_(s) {
  return new Error(`Could not parse ttml timestamp ${s}`);
}
function bA(s, e) {
  if (!s)
    return null;
  let t = cB(s);
  return t === null && (dB.test(s) ? t = Fae(s, e) : fB.test(s) && (t = Uae(s, e))), t;
}
function Fae(s, e) {
  const t = dB.exec(s), i = (t[4] | 0) + (t[5] | 0) / e.subFrameRate;
  return (t[1] | 0) * 3600 + (t[2] | 0) * 60 + (t[3] | 0) + i / e.frameRate;
}
function Uae(s, e) {
  const t = fB.exec(s), i = Number(t[1]);
  switch (t[2]) {
    case "h":
      return i * 3600;
    case "m":
      return i * 60;
    case "ms":
      return i * 1e3;
    case "f":
      return i / e.frameRate;
    case "t":
      return i / e.tickRate;
  }
  return i;
}
class zae {
  constructor(e) {
    this.hls = void 0, this.media = null, this.config = void 0, this.enabled = !0, this.Cues = void 0, this.textTracks = [], this.tracks = [], this.initPTS = [], this.unparsedVttFrags = [], this.captionsTracks = {}, this.nonNativeCaptionsTracks = {}, this.cea608Parser1 = void 0, this.cea608Parser2 = void 0, this.lastCc = -1, this.lastSn = -1, this.lastPartIndex = -1, this.prevCC = -1, this.vttCCs = N_(), this.captionsProperties = void 0, this.hls = e, this.config = e.config, this.Cues = e.config.cueHandler, this.captionsProperties = {
      textTrack1: {
        label: this.config.captionsTextTrack1Label,
        languageCode: this.config.captionsTextTrack1LanguageCode
      },
      textTrack2: {
        label: this.config.captionsTextTrack2Label,
        languageCode: this.config.captionsTextTrack2LanguageCode
      },
      textTrack3: {
        label: this.config.captionsTextTrack3Label,
        languageCode: this.config.captionsTextTrack3LanguageCode
      },
      textTrack4: {
        label: this.config.captionsTextTrack4Label,
        languageCode: this.config.captionsTextTrack4LanguageCode
      }
    }, e.on(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.on(j.SUBTITLE_TRACKS_UPDATED, this.onSubtitleTracksUpdated, this), e.on(j.FRAG_LOADING, this.onFragLoading, this), e.on(j.FRAG_LOADED, this.onFragLoaded, this), e.on(j.FRAG_PARSING_USERDATA, this.onFragParsingUserdata, this), e.on(j.FRAG_DECRYPTED, this.onFragDecrypted, this), e.on(j.INIT_PTS_FOUND, this.onInitPtsFound, this), e.on(j.SUBTITLE_TRACKS_CLEARED, this.onSubtitleTracksCleared, this), e.on(j.BUFFER_FLUSHING, this.onBufferFlushing, this);
  }
  destroy() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.off(j.SUBTITLE_TRACKS_UPDATED, this.onSubtitleTracksUpdated, this), e.off(j.FRAG_LOADING, this.onFragLoading, this), e.off(j.FRAG_LOADED, this.onFragLoaded, this), e.off(j.FRAG_PARSING_USERDATA, this.onFragParsingUserdata, this), e.off(j.FRAG_DECRYPTED, this.onFragDecrypted, this), e.off(j.INIT_PTS_FOUND, this.onInitPtsFound, this), e.off(j.SUBTITLE_TRACKS_CLEARED, this.onSubtitleTracksCleared, this), e.off(j.BUFFER_FLUSHING, this.onBufferFlushing, this), this.hls = this.config = null, this.cea608Parser1 = this.cea608Parser2 = void 0;
  }
  initCea608Parsers() {
    if (this.config.enableCEA708Captions && (!this.cea608Parser1 || !this.cea608Parser2)) {
      const e = new c3(this, "textTrack1"), t = new c3(this, "textTrack2"), i = new c3(this, "textTrack3"), n = new c3(this, "textTrack4");
      this.cea608Parser1 = new __(1, e, t), this.cea608Parser2 = new __(3, i, n);
    }
  }
  addCues(e, t, i, n, r) {
    let a = !1;
    for (let o = r.length; o--; ) {
      const l = r[o], c = kae(l[0], l[1], t, i);
      if (c >= 0 && (l[0] = Math.min(l[0], t), l[1] = Math.max(l[1], i), a = !0, c / (i - t) > 0.5))
        return;
    }
    if (a || r.push([t, i]), this.config.renderTextTracksNatively) {
      const o = this.captionsTracks[e];
      this.Cues.newCue(o, t, i, n);
    } else {
      const o = this.Cues.newCue(null, t, i, n);
      this.hls.trigger(j.CUES_PARSED, {
        type: "captions",
        cues: o,
        track: e
      });
    }
  }
  // Triggered when an initial PTS is found; used for synchronisation of WebVTT.
  onInitPtsFound(e, {
    frag: t,
    id: i,
    initPTS: n,
    timescale: r
  }) {
    const {
      unparsedVttFrags: a
    } = this;
    i === "main" && (this.initPTS[t.cc] = {
      baseTime: n,
      timescale: r
    }), a.length && (this.unparsedVttFrags = [], a.forEach((o) => {
      this.onFragLoaded(j.FRAG_LOADED, o);
    }));
  }
  getExistingTrack(e, t) {
    const {
      media: i
    } = this;
    if (i)
      for (let n = 0; n < i.textTracks.length; n++) {
        const r = i.textTracks[n];
        if (O_(r, {
          name: e,
          lang: t,
          attrs: {}
        }))
          return r;
      }
    return null;
  }
  createCaptionsTrack(e) {
    this.config.renderTextTracksNatively ? this.createNativeTrack(e) : this.createNonNativeTrack(e);
  }
  createNativeTrack(e) {
    if (this.captionsTracks[e])
      return;
    const {
      captionsProperties: t,
      captionsTracks: i,
      media: n
    } = this, {
      label: r,
      languageCode: a
    } = t[e], o = this.getExistingTrack(r, a);
    if (o)
      i[e] = o, wf(i[e]), ON(i[e], n);
    else {
      const l = this.createTextTrack("captions", r, a);
      l && (l[e] = !0, i[e] = l);
    }
  }
  createNonNativeTrack(e) {
    if (this.nonNativeCaptionsTracks[e])
      return;
    const t = this.captionsProperties[e];
    if (!t)
      return;
    const i = t.label, n = {
      _id: e,
      label: i,
      kind: "captions",
      default: t.media ? !!t.media.default : !1,
      closedCaptions: t.media
    };
    this.nonNativeCaptionsTracks[e] = n, this.hls.trigger(j.NON_NATIVE_TEXT_TRACKS_FOUND, {
      tracks: [n]
    });
  }
  createTextTrack(e, t, i) {
    const n = this.media;
    if (n)
      return n.addTextTrack(e, t, i);
  }
  onMediaAttaching(e, t) {
    this.media = t.media, this._cleanTracks();
  }
  onMediaDetaching() {
    const {
      captionsTracks: e
    } = this;
    Object.keys(e).forEach((t) => {
      wf(e[t]), delete e[t];
    }), this.nonNativeCaptionsTracks = {};
  }
  onManifestLoading() {
    this.lastCc = -1, this.lastSn = -1, this.lastPartIndex = -1, this.prevCC = -1, this.vttCCs = N_(), this._cleanTracks(), this.tracks = [], this.captionsTracks = {}, this.nonNativeCaptionsTracks = {}, this.textTracks = [], this.unparsedVttFrags = [], this.initPTS = [], this.cea608Parser1 && this.cea608Parser2 && (this.cea608Parser1.reset(), this.cea608Parser2.reset());
  }
  _cleanTracks() {
    const {
      media: e
    } = this;
    if (!e)
      return;
    const t = e.textTracks;
    if (t)
      for (let i = 0; i < t.length; i++)
        wf(t[i]);
  }
  onSubtitleTracksUpdated(e, t) {
    const i = t.subtitleTracks || [], n = i.some((r) => r.textCodec === yA);
    if (this.config.enableWebVTT || n && this.config.enableIMSC1) {
      if (aB(this.tracks, i)) {
        this.tracks = i;
        return;
      }
      if (this.textTracks = [], this.tracks = i, this.config.renderTextTracksNatively) {
        const a = this.media, o = a ? By(a.textTracks) : null;
        if (this.tracks.forEach((l, c) => {
          let h;
          if (o) {
            let u = null;
            for (let d = 0; d < o.length; d++)
              if (o[d] && O_(o[d], l)) {
                u = o[d], o[d] = null;
                break;
              }
            u && (h = u);
          }
          if (h)
            wf(h);
          else {
            const u = mB(l);
            h = this.createTextTrack(u, l.name, l.lang), h && (h.mode = "disabled");
          }
          h && this.textTracks.push(h);
        }), o != null && o.length) {
          const l = o.filter((c) => c !== null).map((c) => c.label);
          l.length && ze.warn(`Media element contains unused subtitle tracks: ${l.join(", ")}. Replace media element for each source to clear TextTracks and captions menu.`);
        }
      } else if (this.tracks.length) {
        const a = this.tracks.map((o) => ({
          label: o.name,
          kind: o.type.toLowerCase(),
          default: o.default,
          subtitleTrack: o
        }));
        this.hls.trigger(j.NON_NATIVE_TEXT_TRACKS_FOUND, {
          tracks: a
        });
      }
    }
  }
  onManifestLoaded(e, t) {
    this.config.enableCEA708Captions && t.captions && t.captions.forEach((i) => {
      const n = /(?:CC|SERVICE)([1-4])/.exec(i.instreamId);
      if (!n)
        return;
      const r = `textTrack${n[1]}`, a = this.captionsProperties[r];
      a && (a.label = i.name, i.lang && (a.languageCode = i.lang), a.media = i);
    });
  }
  closedCaptionsForLevel(e) {
    const t = this.hls.levels[e.level];
    return t == null ? void 0 : t.attrs["CLOSED-CAPTIONS"];
  }
  onFragLoading(e, t) {
    this.initCea608Parsers();
    const {
      cea608Parser1: i,
      cea608Parser2: n,
      lastCc: r,
      lastSn: a,
      lastPartIndex: o
    } = this;
    if (!(!this.enabled || !i || !n) && t.frag.type === jt.MAIN) {
      var l, c;
      const {
        cc: h,
        sn: u
      } = t.frag, d = (l = t == null || (c = t.part) == null ? void 0 : c.index) != null ? l : -1;
      u === a + 1 || u === a && d === o + 1 || h === r || (i.reset(), n.reset()), this.lastCc = h, this.lastSn = u, this.lastPartIndex = d;
    }
  }
  onFragLoaded(e, t) {
    const {
      frag: i,
      payload: n
    } = t;
    if (i.type === jt.SUBTITLE)
      if (n.byteLength) {
        const r = i.decryptdata, a = "stats" in t;
        if (r == null || !r.encrypted || a) {
          const o = this.tracks[i.level], l = this.vttCCs;
          l[i.cc] || (l[i.cc] = {
            start: i.start,
            prevCC: this.prevCC,
            new: !0
          }, this.prevCC = i.cc), o && o.textCodec === yA ? this._parseIMSC1(i, n) : this._parseVTTs(t);
        }
      } else
        this.hls.trigger(j.SUBTITLE_FRAG_PROCESSED, {
          success: !1,
          frag: i,
          error: new Error("Empty subtitle payload")
        });
  }
  _parseIMSC1(e, t) {
    const i = this.hls;
    D_(t, this.initPTS[e.cc], (n) => {
      this._appendCues(n, e.level), i.trigger(j.SUBTITLE_FRAG_PROCESSED, {
        success: !0,
        frag: e
      });
    }, (n) => {
      ze.log(`Failed to parse IMSC1: ${n}`), i.trigger(j.SUBTITLE_FRAG_PROCESSED, {
        success: !1,
        frag: e,
        error: n
      });
    });
  }
  _parseVTTs(e) {
    var t;
    const {
      frag: i,
      payload: n
    } = e, {
      initPTS: r,
      unparsedVttFrags: a
    } = this, o = r.length - 1;
    if (!r[i.cc] && o === -1) {
      a.push(e);
      return;
    }
    const l = this.hls, c = (t = i.initSegment) != null && t.data ? Pa(i.initSegment.data, new Uint8Array(n)) : n;
    Iae(c, this.initPTS[i.cc], this.vttCCs, i.cc, i.start, (h) => {
      this._appendCues(h, i.level), l.trigger(j.SUBTITLE_FRAG_PROCESSED, {
        success: !0,
        frag: i
      });
    }, (h) => {
      const u = h.message === "Missing initPTS for VTT MPEGTS";
      u ? a.push(e) : this._fallbackToIMSC1(i, n), ze.log(`Failed to parse VTT cue: ${h}`), !(u && o > i.cc) && l.trigger(j.SUBTITLE_FRAG_PROCESSED, {
        success: !1,
        frag: i,
        error: h
      });
    });
  }
  _fallbackToIMSC1(e, t) {
    const i = this.tracks[e.level];
    i.textCodec || D_(t, this.initPTS[e.cc], () => {
      i.textCodec = yA, this._parseIMSC1(e, t);
    }, () => {
      i.textCodec = "wvtt";
    });
  }
  _appendCues(e, t) {
    const i = this.hls;
    if (this.config.renderTextTracksNatively) {
      const n = this.textTracks[t];
      if (!n || n.mode === "disabled")
        return;
      e.forEach((r) => NN(n, r));
    } else {
      const n = this.tracks[t];
      if (!n)
        return;
      const r = n.default ? "default" : "subtitles" + t;
      i.trigger(j.CUES_PARSED, {
        type: "subtitles",
        cues: e,
        track: r
      });
    }
  }
  onFragDecrypted(e, t) {
    const {
      frag: i
    } = t;
    i.type === jt.SUBTITLE && this.onFragLoaded(j.FRAG_LOADED, t);
  }
  onSubtitleTracksCleared() {
    this.tracks = [], this.captionsTracks = {};
  }
  onFragParsingUserdata(e, t) {
    this.initCea608Parsers();
    const {
      cea608Parser1: i,
      cea608Parser2: n
    } = this;
    if (!this.enabled || !i || !n)
      return;
    const {
      frag: r,
      samples: a
    } = t;
    if (!(r.type === jt.MAIN && this.closedCaptionsForLevel(r) === "NONE"))
      for (let o = 0; o < a.length; o++) {
        const l = a[o].bytes;
        if (l) {
          const c = this.extractCea608Data(l);
          i.addData(a[o].pts, c[0]), n.addData(a[o].pts, c[1]);
        }
      }
  }
  onBufferFlushing(e, {
    startOffset: t,
    endOffset: i,
    endOffsetSubtitles: n,
    type: r
  }) {
    const {
      media: a
    } = this;
    if (!(!a || a.currentTime < i)) {
      if (!r || r === "video") {
        const {
          captionsTracks: o
        } = this;
        Object.keys(o).forEach((l) => Cb(o[l], t, i));
      }
      if (this.config.renderTextTracksNatively && t === 0 && n !== void 0) {
        const {
          textTracks: o
        } = this;
        Object.keys(o).forEach((l) => Cb(o[l], t, n));
      }
    }
  }
  extractCea608Data(e) {
    const t = [[], []], i = e[0] & 31;
    let n = 2;
    for (let r = 0; r < i; r++) {
      const a = e[n++], o = 127 & e[n++], l = 127 & e[n++];
      if (o === 0 && l === 0)
        continue;
      if ((4 & a) !== 0) {
        const h = 3 & a;
        (h === 0 || h === 1) && (t[h].push(o), t[h].push(l));
      }
    }
    return t;
  }
}
function mB(s) {
  return s.characteristics && /transcribes-spoken-dialog/gi.test(s.characteristics) && /describes-music-and-sound/gi.test(s.characteristics) ? "captions" : "subtitles";
}
function O_(s, e) {
  return !!s && s.kind === mB(e) && Ib(e, s);
}
function kae(s, e, t, i) {
  return Math.min(e, i) - Math.max(s, t);
}
function N_() {
  return {
    ccOffset: 0,
    presentationOffset: 0,
    0: {
      start: 0,
      prevCC: -1,
      new: !0
    }
  };
}
class lw {
  constructor(e) {
    this.hls = void 0, this.autoLevelCapping = void 0, this.firstLevel = void 0, this.media = void 0, this.restrictedLevels = void 0, this.timer = void 0, this.clientRect = void 0, this.streamController = void 0, this.hls = e, this.autoLevelCapping = Number.POSITIVE_INFINITY, this.firstLevel = -1, this.media = null, this.restrictedLevels = [], this.timer = void 0, this.clientRect = null, this.registerListeners();
  }
  setStreamController(e) {
    this.streamController = e;
  }
  destroy() {
    this.hls && this.unregisterListener(), this.timer && this.stopCapping(), this.media = null, this.clientRect = null, this.hls = this.streamController = null;
  }
  registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.FPS_DROP_LEVEL_CAPPING, this.onFpsDropLevelCapping, this), e.on(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.on(j.BUFFER_CODECS, this.onBufferCodecs, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this);
  }
  unregisterListener() {
    const {
      hls: e
    } = this;
    e.off(j.FPS_DROP_LEVEL_CAPPING, this.onFpsDropLevelCapping, this), e.off(j.MEDIA_ATTACHING, this.onMediaAttaching, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.off(j.BUFFER_CODECS, this.onBufferCodecs, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this);
  }
  onFpsDropLevelCapping(e, t) {
    const i = this.hls.levels[t.droppedLevel];
    this.isLevelAllowed(i) && this.restrictedLevels.push({
      bitrate: i.bitrate,
      height: i.height,
      width: i.width
    });
  }
  onMediaAttaching(e, t) {
    this.media = t.media instanceof HTMLVideoElement ? t.media : null, this.clientRect = null, this.timer && this.hls.levels.length && this.detectPlayerSize();
  }
  onManifestParsed(e, t) {
    const i = this.hls;
    this.restrictedLevels = [], this.firstLevel = t.firstLevel, i.config.capLevelToPlayerSize && t.video && this.startCapping();
  }
  onLevelsUpdated(e, t) {
    this.timer && Bt(this.autoLevelCapping) && this.detectPlayerSize();
  }
  // Only activate capping when playing a video stream; otherwise, multi-bitrate audio-only streams will be restricted
  // to the first level
  onBufferCodecs(e, t) {
    this.hls.config.capLevelToPlayerSize && t.video && this.startCapping();
  }
  onMediaDetaching() {
    this.stopCapping();
  }
  detectPlayerSize() {
    if (this.media) {
      if (this.mediaHeight <= 0 || this.mediaWidth <= 0) {
        this.clientRect = null;
        return;
      }
      const e = this.hls.levels;
      if (e.length) {
        const t = this.hls, i = this.getMaxLevel(e.length - 1);
        i !== this.autoLevelCapping && ze.log(`Setting autoLevelCapping to ${i}: ${e[i].height}p@${e[i].bitrate} for media ${this.mediaWidth}x${this.mediaHeight}`), t.autoLevelCapping = i, t.autoLevelCapping > this.autoLevelCapping && this.streamController && this.streamController.nextLevelSwitch(), this.autoLevelCapping = t.autoLevelCapping;
      }
    }
  }
  /*
   * returns level should be the one with the dimensions equal or greater than the media (player) dimensions (so the video will be downscaled)
   */
  getMaxLevel(e) {
    const t = this.hls.levels;
    if (!t.length)
      return -1;
    const i = t.filter((n, r) => this.isLevelAllowed(n) && r <= e);
    return this.clientRect = null, lw.getMaxLevelByMediaSize(i, this.mediaWidth, this.mediaHeight);
  }
  startCapping() {
    this.timer || (this.autoLevelCapping = Number.POSITIVE_INFINITY, self.clearInterval(this.timer), this.timer = self.setInterval(this.detectPlayerSize.bind(this), 1e3), this.detectPlayerSize());
  }
  stopCapping() {
    this.restrictedLevels = [], this.firstLevel = -1, this.autoLevelCapping = Number.POSITIVE_INFINITY, this.timer && (self.clearInterval(this.timer), this.timer = void 0);
  }
  getDimensions() {
    if (this.clientRect)
      return this.clientRect;
    const e = this.media, t = {
      width: 0,
      height: 0
    };
    if (e) {
      const i = e.getBoundingClientRect();
      t.width = i.width, t.height = i.height, !t.width && !t.height && (t.width = i.right - i.left || e.width || 0, t.height = i.bottom - i.top || e.height || 0);
    }
    return this.clientRect = t, t;
  }
  get mediaWidth() {
    return this.getDimensions().width * this.contentScaleFactor;
  }
  get mediaHeight() {
    return this.getDimensions().height * this.contentScaleFactor;
  }
  get contentScaleFactor() {
    let e = 1;
    if (!this.hls.config.ignoreDevicePixelRatio)
      try {
        e = self.devicePixelRatio;
      } catch {
      }
    return e;
  }
  isLevelAllowed(e) {
    return !this.restrictedLevels.some((i) => e.bitrate === i.bitrate && e.width === i.width && e.height === i.height);
  }
  static getMaxLevelByMediaSize(e, t, i) {
    if (!(e != null && e.length))
      return -1;
    const n = (o, l) => l ? o.width !== l.width || o.height !== l.height : !0;
    let r = e.length - 1;
    const a = Math.max(t, i);
    for (let o = 0; o < e.length; o += 1) {
      const l = e[o];
      if ((l.width >= a || l.height >= a) && n(l, e[o + 1])) {
        r = o;
        break;
      }
    }
    return r;
  }
}
class Gae {
  constructor(e) {
    this.hls = void 0, this.isVideoPlaybackQualityAvailable = !1, this.timer = void 0, this.media = null, this.lastTime = void 0, this.lastDroppedFrames = 0, this.lastDecodedFrames = 0, this.streamController = void 0, this.hls = e, this.registerListeners();
  }
  setStreamController(e) {
    this.streamController = e;
  }
  registerListeners() {
    this.hls.on(j.MEDIA_ATTACHING, this.onMediaAttaching, this);
  }
  unregisterListeners() {
    this.hls.off(j.MEDIA_ATTACHING, this.onMediaAttaching, this);
  }
  destroy() {
    this.timer && clearInterval(this.timer), this.unregisterListeners(), this.isVideoPlaybackQualityAvailable = !1, this.media = null;
  }
  onMediaAttaching(e, t) {
    const i = this.hls.config;
    if (i.capLevelOnFPSDrop) {
      const n = t.media instanceof self.HTMLVideoElement ? t.media : null;
      this.media = n, n && typeof n.getVideoPlaybackQuality == "function" && (this.isVideoPlaybackQualityAvailable = !0), self.clearInterval(this.timer), this.timer = self.setInterval(this.checkFPSInterval.bind(this), i.fpsDroppedMonitoringPeriod);
    }
  }
  checkFPS(e, t, i) {
    const n = performance.now();
    if (t) {
      if (this.lastTime) {
        const r = n - this.lastTime, a = i - this.lastDroppedFrames, o = t - this.lastDecodedFrames, l = 1e3 * a / r, c = this.hls;
        if (c.trigger(j.FPS_DROP, {
          currentDropped: a,
          currentDecoded: o,
          totalDroppedFrames: i
        }), l > 0 && a > c.config.fpsDroppedMonitoringThreshold * o) {
          let h = c.currentLevel;
          ze.warn("drop FPS ratio greater than max allowed value for currentLevel: " + h), h > 0 && (c.autoLevelCapping === -1 || c.autoLevelCapping >= h) && (h = h - 1, c.trigger(j.FPS_DROP_LEVEL_CAPPING, {
            level: h,
            droppedLevel: c.currentLevel
          }), c.autoLevelCapping = h, this.streamController.nextLevelSwitch());
        }
      }
      this.lastTime = n, this.lastDroppedFrames = i, this.lastDecodedFrames = t;
    }
  }
  checkFPSInterval() {
    const e = this.media;
    if (e)
      if (this.isVideoPlaybackQualityAvailable) {
        const t = e.getVideoPlaybackQuality();
        this.checkFPS(e, t.totalVideoFrames, t.droppedVideoFrames);
      } else
        this.checkFPS(e, e.webkitDecodedFrameCount, e.webkitDroppedFrameCount);
  }
}
const u3 = "[eme]";
class Vf {
  constructor(e) {
    this.hls = void 0, this.config = void 0, this.media = null, this.keyFormatPromise = null, this.keySystemAccessPromises = {}, this._requestLicenseFailureCount = 0, this.mediaKeySessions = [], this.keyIdToKeySessionPromise = {}, this.setMediaKeysQueue = Vf.CDMCleanupPromise ? [Vf.CDMCleanupPromise] : [], this.onMediaEncrypted = this._onMediaEncrypted.bind(this), this.onWaitingForKey = this._onWaitingForKey.bind(this), this.debug = ze.debug.bind(ze, u3), this.log = ze.log.bind(ze, u3), this.warn = ze.warn.bind(ze, u3), this.error = ze.error.bind(ze, u3), this.hls = e, this.config = e.config, this.registerListeners();
  }
  destroy() {
    this.unregisterListeners(), this.onMediaDetached();
    const e = this.config;
    e.requestMediaKeySystemAccessFunc = null, e.licenseXhrSetup = e.licenseResponseCallback = void 0, e.drmSystems = e.drmSystemOptions = {}, this.hls = this.onMediaEncrypted = this.onWaitingForKey = this.keyIdToKeySessionPromise = null, this.config = null;
  }
  registerListeners() {
    this.hls.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), this.hls.on(j.MEDIA_DETACHED, this.onMediaDetached, this), this.hls.on(j.MANIFEST_LOADING, this.onManifestLoading, this), this.hls.on(j.MANIFEST_LOADED, this.onManifestLoaded, this);
  }
  unregisterListeners() {
    this.hls.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), this.hls.off(j.MEDIA_DETACHED, this.onMediaDetached, this), this.hls.off(j.MANIFEST_LOADING, this.onManifestLoading, this), this.hls.off(j.MANIFEST_LOADED, this.onManifestLoaded, this);
  }
  getLicenseServerUrl(e) {
    const {
      drmSystems: t,
      widevineLicenseUrl: i
    } = this.config, n = t[e];
    if (n)
      return n.licenseUrl;
    if (e === On.WIDEVINE && i)
      return i;
    throw new Error(`no license server URL configured for key-system "${e}"`);
  }
  getServerCertificateUrl(e) {
    const {
      drmSystems: t
    } = this.config, i = t[e];
    if (i)
      return i.serverCertificateUrl;
    this.log(`No Server Certificate in config.drmSystems["${e}"]`);
  }
  attemptKeySystemAccess(e) {
    const t = this.hls.levels, i = (a, o, l) => !!a && l.indexOf(a) === o, n = t.map((a) => a.audioCodec).filter(i), r = t.map((a) => a.videoCodec).filter(i);
    return n.length + r.length === 0 && r.push("avc1.42e01e"), new Promise((a, o) => {
      const l = (c) => {
        const h = c.shift();
        this.getMediaKeysPromise(h, n, r).then((u) => a({
          keySystem: h,
          mediaKeys: u
        })).catch((u) => {
          c.length ? l(c) : u instanceof ca ? o(u) : o(new ca({
            type: qt.KEY_SYSTEM_ERROR,
            details: Xe.KEY_SYSTEM_NO_ACCESS,
            error: u,
            fatal: !0
          }, u.message));
        });
      };
      l(e);
    });
  }
  requestMediaKeySystemAccess(e, t) {
    const {
      requestMediaKeySystemAccessFunc: i
    } = this.config;
    if (typeof i != "function") {
      let n = `Configured requestMediaKeySystemAccess is not a function ${i}`;
      return bN === null && self.location.protocol === "http:" && (n = `navigator.requestMediaKeySystemAccess is not available over insecure protocol ${location.protocol}`), Promise.reject(new Error(n));
    }
    return i(e, t);
  }
  getMediaKeysPromise(e, t, i) {
    const n = Qne(e, t, i, this.config.drmSystemOptions), r = this.keySystemAccessPromises[e];
    let a = r == null ? void 0 : r.keySystemAccess;
    if (!a) {
      this.log(`Requesting encrypted media "${e}" key-system access with config: ${JSON.stringify(n)}`), a = this.requestMediaKeySystemAccess(e, n);
      const o = this.keySystemAccessPromises[e] = {
        keySystemAccess: a
      };
      return a.catch((l) => {
        this.log(`Failed to obtain access to key-system "${e}": ${l}`);
      }), a.then((l) => {
        this.log(`Access for key-system "${l.keySystem}" obtained`);
        const c = this.fetchServerCertificate(e);
        return this.log(`Create media-keys for "${e}"`), o.mediaKeys = l.createMediaKeys().then((h) => (this.log(`Media-keys created for "${e}"`), c.then((u) => u ? this.setMediaKeysServerCertificate(h, e, u) : h))), o.mediaKeys.catch((h) => {
          this.error(`Failed to create media-keys for "${e}"}: ${h}`);
        }), o.mediaKeys;
      });
    }
    return a.then(() => r.mediaKeys);
  }
  createMediaKeySessionContext({
    decryptdata: e,
    keySystem: t,
    mediaKeys: i
  }) {
    this.log(`Creating key-system session "${t}" keyId: ${Io.hexDump(e.keyId || [])}`);
    const n = i.createSession(), r = {
      decryptdata: e,
      keySystem: t,
      mediaKeys: i,
      mediaKeysSession: n,
      keyStatus: "status-pending"
    };
    return this.mediaKeySessions.push(r), r;
  }
  renewKeySession(e) {
    const t = e.decryptdata;
    if (t.pssh) {
      const i = this.createMediaKeySessionContext(e), n = this.getKeyIdString(t), r = "cenc";
      this.keyIdToKeySessionPromise[n] = this.generateRequestWithPreferredKeySession(i, r, t.pssh, "expired");
    } else
      this.warn("Could not renew expired session. Missing pssh initData.");
    this.removeSession(e);
  }
  getKeyIdString(e) {
    if (!e)
      throw new Error("Could not read keyId of undefined decryptdata");
    if (e.keyId === null)
      throw new Error("keyId is null");
    return Io.hexDump(e.keyId);
  }
  updateKeySession(e, t) {
    var i;
    const n = e.mediaKeysSession;
    return this.log(`Updating key-session "${n.sessionId}" for keyID ${Io.hexDump(((i = e.decryptdata) == null ? void 0 : i.keyId) || [])}
      } (data length: ${t && t.byteLength})`), n.update(t);
  }
  selectKeySystemFormat(e) {
    const t = Object.keys(e.levelkeys || {});
    return this.keyFormatPromise || (this.log(`Selecting key-system from fragment (sn: ${e.sn} ${e.type}: ${e.level}) key formats ${t.join(", ")}`), this.keyFormatPromise = this.getKeyFormatPromise(t)), this.keyFormatPromise;
  }
  getKeyFormatPromise(e) {
    return new Promise((t, i) => {
      const n = nA(this.config), r = e.map(zC).filter((a) => !!a && n.indexOf(a) !== -1);
      return this.getKeySystemSelectionPromise(r).then(({
        keySystem: a
      }) => {
        const o = kC(a);
        o ? t(o) : i(new Error(`Unable to find format for key-system "${a}"`));
      }).catch(i);
    });
  }
  loadKey(e) {
    const t = e.keyInfo.decryptdata, i = this.getKeyIdString(t), n = `(keyId: ${i} format: "${t.keyFormat}" method: ${t.method} uri: ${t.uri})`;
    this.log(`Starting session for key ${n}`);
    let r = this.keyIdToKeySessionPromise[i];
    return r || (r = this.keyIdToKeySessionPromise[i] = this.getKeySystemForKeyPromise(t).then(({
      keySystem: a,
      mediaKeys: o
    }) => (this.throwIfDestroyed(), this.log(`Handle encrypted media sn: ${e.frag.sn} ${e.frag.type}: ${e.frag.level} using key ${n}`), this.attemptSetMediaKeys(a, o).then(() => {
      this.throwIfDestroyed();
      const l = this.createMediaKeySessionContext({
        keySystem: a,
        mediaKeys: o,
        decryptdata: t
      }), c = "cenc";
      return this.generateRequestWithPreferredKeySession(l, c, t.pssh, "playlist-key");
    }))), r.catch((a) => this.handleError(a))), r;
  }
  throwIfDestroyed(e = "Invalid state") {
    if (!this.hls)
      throw new Error("invalid state");
  }
  handleError(e) {
    this.hls && (this.error(e.message), e instanceof ca ? this.hls.trigger(j.ERROR, e.data) : this.hls.trigger(j.ERROR, {
      type: qt.KEY_SYSTEM_ERROR,
      details: Xe.KEY_SYSTEM_NO_KEYS,
      error: e,
      fatal: !0
    }));
  }
  getKeySystemForKeyPromise(e) {
    const t = this.getKeyIdString(e), i = this.keyIdToKeySessionPromise[t];
    if (!i) {
      const n = zC(e.keyFormat), r = n ? [n] : nA(this.config);
      return this.attemptKeySystemAccess(r);
    }
    return i;
  }
  getKeySystemSelectionPromise(e) {
    if (e.length || (e = nA(this.config)), e.length === 0)
      throw new ca({
        type: qt.KEY_SYSTEM_ERROR,
        details: Xe.KEY_SYSTEM_NO_CONFIGURED_LICENSE,
        fatal: !0
      }, `Missing key-system license configuration options ${JSON.stringify({
        drmSystems: this.config.drmSystems
      })}`);
    return this.attemptKeySystemAccess(e);
  }
  _onMediaEncrypted(e) {
    const {
      initDataType: t,
      initData: i
    } = e;
    if (this.debug(`"${e.type}" event: init data type: "${t}"`), i === null)
      return;
    let n, r;
    if (t === "sinf" && this.config.drmSystems[On.FAIRPLAY]) {
      const h = ls(new Uint8Array(i));
      try {
        const u = qS(JSON.parse(h).sinf), d = PN(new Uint8Array(u));
        if (!d)
          return;
        n = d.subarray(8, 24), r = On.FAIRPLAY;
      } catch {
        this.warn('Failed to parse sinf "encrypted" event message initData');
        return;
      }
    } else {
      const h = Sse(i);
      if (h === null)
        return;
      h.version === 0 && h.systemId === AN.WIDEVINE && h.data && (n = h.data.subarray(8, 24)), r = Jne(h.systemId);
    }
    if (!r || !n)
      return;
    const a = Io.hexDump(n), {
      keyIdToKeySessionPromise: o,
      mediaKeySessions: l
    } = this;
    let c = o[a];
    for (let h = 0; h < l.length; h++) {
      const u = l[h], d = u.decryptdata;
      if (d.pssh || !d.keyId)
        continue;
      const f = Io.hexDump(d.keyId);
      if (a === f || d.uri.replace(/-/g, "").indexOf(a) !== -1) {
        c = o[f], delete o[f], d.pssh = new Uint8Array(i), d.keyId = n, c = o[a] = c.then(() => this.generateRequestWithPreferredKeySession(u, t, i, "encrypted-event-key-match"));
        break;
      }
    }
    c || (c = o[a] = this.getKeySystemSelectionPromise([r]).then(({
      keySystem: h,
      mediaKeys: u
    }) => {
      var d;
      this.throwIfDestroyed();
      const f = new Lg("ISO-23001-7", a, (d = kC(h)) != null ? d : "");
      return f.pssh = new Uint8Array(i), f.keyId = n, this.attemptSetMediaKeys(h, u).then(() => {
        this.throwIfDestroyed();
        const p = this.createMediaKeySessionContext({
          decryptdata: f,
          keySystem: h,
          mediaKeys: u
        });
        return this.generateRequestWithPreferredKeySession(p, t, i, "encrypted-event-no-match");
      });
    })), c.catch((h) => this.handleError(h));
  }
  _onWaitingForKey(e) {
    this.log(`"${e.type}" event`);
  }
  attemptSetMediaKeys(e, t) {
    const i = this.setMediaKeysQueue.slice();
    this.log(`Setting media-keys for "${e}"`);
    const n = Promise.all(i).then(() => {
      if (!this.media)
        throw new Error("Attempted to set mediaKeys without media element attached");
      return this.media.setMediaKeys(t);
    });
    return this.setMediaKeysQueue.push(n), n.then(() => {
      this.log(`Media-keys set for "${e}"`), i.push(n), this.setMediaKeysQueue = this.setMediaKeysQueue.filter((r) => i.indexOf(r) === -1);
    });
  }
  generateRequestWithPreferredKeySession(e, t, i, n) {
    var r, a;
    const o = (r = this.config.drmSystems) == null || (a = r[e.keySystem]) == null ? void 0 : a.generateRequest;
    if (o)
      try {
        const p = o.call(this.hls, t, i, e);
        if (!p)
          throw new Error("Invalid response from configured generateRequest filter");
        t = p.initDataType, i = e.decryptdata.pssh = p.initData ? new Uint8Array(p.initData) : null;
      } catch (p) {
        var l;
        if (this.warn(p.message), (l = this.hls) != null && l.config.debug)
          throw p;
      }
    if (i === null)
      return this.log(`Skipping key-session request for "${n}" (no initData)`), Promise.resolve(e);
    const c = this.getKeyIdString(e.decryptdata);
    this.log(`Generating key-session request for "${n}": ${c} (init data type: ${t} length: ${i ? i.byteLength : null})`);
    const h = new rw(), u = e._onmessage = (p) => {
      const v = e.mediaKeysSession;
      if (!v) {
        h.emit("error", new Error("invalid state"));
        return;
      }
      const {
        messageType: g,
        message: m
      } = p;
      this.log(`"${g}" message event for session "${v.sessionId}" message size: ${m.byteLength}`), g === "license-request" || g === "license-renewal" ? this.renewLicense(e, m).catch((x) => {
        this.handleError(x), h.emit("error", x);
      }) : g === "license-release" ? e.keySystem === On.FAIRPLAY && (this.updateKeySession(e, Eb("acknowledged")), this.removeSession(e)) : this.warn(`unhandled media key message type "${g}"`);
    }, d = e._onkeystatuseschange = (p) => {
      if (!e.mediaKeysSession) {
        h.emit("error", new Error("invalid state"));
        return;
      }
      this.onKeyStatusChange(e);
      const g = e.keyStatus;
      h.emit("keyStatus", g), g === "expired" && (this.warn(`${e.keySystem} expired for key ${c}`), this.renewKeySession(e));
    };
    e.mediaKeysSession.addEventListener("message", u), e.mediaKeysSession.addEventListener("keystatuseschange", d);
    const f = new Promise((p, v) => {
      h.on("error", v), h.on("keyStatus", (g) => {
        g.startsWith("usable") ? p() : g === "output-restricted" ? v(new ca({
          type: qt.KEY_SYSTEM_ERROR,
          details: Xe.KEY_SYSTEM_STATUS_OUTPUT_RESTRICTED,
          fatal: !1
        }, "HDCP level output restricted")) : g === "internal-error" ? v(new ca({
          type: qt.KEY_SYSTEM_ERROR,
          details: Xe.KEY_SYSTEM_STATUS_INTERNAL_ERROR,
          fatal: !0
        }, `key status changed to "${g}"`)) : g === "expired" ? v(new Error("key expired while generating request")) : this.warn(`unhandled key status change "${g}"`);
      });
    });
    return e.mediaKeysSession.generateRequest(t, i).then(() => {
      var p;
      this.log(`Request generated for key-session "${(p = e.mediaKeysSession) == null ? void 0 : p.sessionId}" keyId: ${c}`);
    }).catch((p) => {
      throw new ca({
        type: qt.KEY_SYSTEM_ERROR,
        details: Xe.KEY_SYSTEM_NO_SESSION,
        error: p,
        fatal: !1
      }, `Error generating key-session request: ${p}`);
    }).then(() => f).catch((p) => {
      throw h.removeAllListeners(), this.removeSession(e), p;
    }).then(() => (h.removeAllListeners(), e));
  }
  onKeyStatusChange(e) {
    e.mediaKeysSession.keyStatuses.forEach((t, i) => {
      this.log(`key status change "${t}" for keyStatuses keyId: ${Io.hexDump("buffer" in i ? new Uint8Array(i.buffer, i.byteOffset, i.byteLength) : new Uint8Array(i))} session keyId: ${Io.hexDump(new Uint8Array(e.decryptdata.keyId || []))} uri: ${e.decryptdata.uri}`), e.keyStatus = t;
    });
  }
  fetchServerCertificate(e) {
    const t = this.config, i = t.loader, n = new i(t), r = this.getServerCertificateUrl(e);
    return r ? (this.log(`Fetching server certificate for "${e}"`), new Promise((a, o) => {
      const l = {
        responseType: "arraybuffer",
        url: r
      }, c = t.certLoadPolicy.default, h = {
        loadPolicy: c,
        timeout: c.maxLoadTimeMs,
        maxRetry: 0,
        retryDelay: 0,
        maxRetryDelay: 0
      }, u = {
        onSuccess: (d, f, p, v) => {
          a(d.data);
        },
        onError: (d, f, p, v) => {
          o(new ca({
            type: qt.KEY_SYSTEM_ERROR,
            details: Xe.KEY_SYSTEM_SERVER_CERTIFICATE_REQUEST_FAILED,
            fatal: !0,
            networkDetails: p,
            response: Ls({
              url: l.url,
              data: void 0
            }, d)
          }, `"${e}" certificate request failed (${r}). Status: ${d.code} (${d.text})`));
        },
        onTimeout: (d, f, p) => {
          o(new ca({
            type: qt.KEY_SYSTEM_ERROR,
            details: Xe.KEY_SYSTEM_SERVER_CERTIFICATE_REQUEST_FAILED,
            fatal: !0,
            networkDetails: p,
            response: {
              url: l.url,
              data: void 0
            }
          }, `"${e}" certificate request timed out (${r})`));
        },
        onAbort: (d, f, p) => {
          o(new Error("aborted"));
        }
      };
      n.load(l, h, u);
    })) : Promise.resolve();
  }
  setMediaKeysServerCertificate(e, t, i) {
    return new Promise((n, r) => {
      e.setServerCertificate(i).then((a) => {
        this.log(`setServerCertificate ${a ? "success" : "not supported by CDM"} (${i == null ? void 0 : i.byteLength}) on "${t}"`), n(e);
      }).catch((a) => {
        r(new ca({
          type: qt.KEY_SYSTEM_ERROR,
          details: Xe.KEY_SYSTEM_SERVER_CERTIFICATE_UPDATE_FAILED,
          error: a,
          fatal: !0
        }, a.message));
      });
    });
  }
  renewLicense(e, t) {
    return this.requestLicense(e, new Uint8Array(t)).then((i) => this.updateKeySession(e, new Uint8Array(i)).catch((n) => {
      throw new ca({
        type: qt.KEY_SYSTEM_ERROR,
        details: Xe.KEY_SYSTEM_SESSION_UPDATE_FAILED,
        error: n,
        fatal: !0
      }, n.message);
    }));
  }
  unpackPlayReadyKeyMessage(e, t) {
    const i = String.fromCharCode.apply(null, new Uint16Array(t.buffer));
    if (!i.includes("PlayReadyKeyMessage"))
      return e.setRequestHeader("Content-Type", "text/xml; charset=utf-8"), t;
    const n = new DOMParser().parseFromString(i, "application/xml"), r = n.querySelectorAll("HttpHeader");
    if (r.length > 0) {
      let h;
      for (let u = 0, d = r.length; u < d; u++) {
        var a, o;
        h = r[u];
        const f = (a = h.querySelector("name")) == null ? void 0 : a.textContent, p = (o = h.querySelector("value")) == null ? void 0 : o.textContent;
        f && p && e.setRequestHeader(f, p);
      }
    }
    const l = n.querySelector("Challenge"), c = l == null ? void 0 : l.textContent;
    if (!c)
      throw new Error("Cannot find <Challenge> in key message");
    return Eb(atob(c));
  }
  setupLicenseXHR(e, t, i, n) {
    const r = this.config.licenseXhrSetup;
    return r ? Promise.resolve().then(() => {
      if (!i.decryptdata)
        throw new Error("Key removed");
      return r.call(this.hls, e, t, i, n);
    }).catch((a) => {
      if (!i.decryptdata)
        throw a;
      return e.open("POST", t, !0), r.call(this.hls, e, t, i, n);
    }).then((a) => (e.readyState || e.open("POST", t, !0), {
      xhr: e,
      licenseChallenge: a || n
    })) : (e.open("POST", t, !0), Promise.resolve({
      xhr: e,
      licenseChallenge: n
    }));
  }
  requestLicense(e, t) {
    const i = this.config.keyLoadPolicy.default;
    return new Promise((n, r) => {
      const a = this.getLicenseServerUrl(e.keySystem);
      this.log(`Sending license request to URL: ${a}`);
      const o = new XMLHttpRequest();
      o.responseType = "arraybuffer", o.onreadystatechange = () => {
        if (!this.hls || !e.mediaKeysSession)
          return r(new Error("invalid state"));
        if (o.readyState === 4)
          if (o.status === 200) {
            this._requestLicenseFailureCount = 0;
            let l = o.response;
            this.log(`License received ${l instanceof ArrayBuffer ? l.byteLength : l}`);
            const c = this.config.licenseResponseCallback;
            if (c)
              try {
                l = c.call(this.hls, o, a, e);
              } catch (h) {
                this.error(h);
              }
            n(l);
          } else {
            const l = i.errorRetry, c = l ? l.maxNumRetry : 0;
            if (this._requestLicenseFailureCount++, this._requestLicenseFailureCount > c || o.status >= 400 && o.status < 500)
              r(new ca({
                type: qt.KEY_SYSTEM_ERROR,
                details: Xe.KEY_SYSTEM_LICENSE_REQUEST_FAILED,
                fatal: !0,
                networkDetails: o,
                response: {
                  url: a,
                  data: void 0,
                  code: o.status,
                  text: o.statusText
                }
              }, `License Request XHR failed (${a}). Status: ${o.status} (${o.statusText})`));
            else {
              const h = c - this._requestLicenseFailureCount + 1;
              this.warn(`Retrying license request, ${h} attempts left`), this.requestLicense(e, t).then(n, r);
            }
          }
      }, e.licenseXhr && e.licenseXhr.readyState !== XMLHttpRequest.DONE && e.licenseXhr.abort(), e.licenseXhr = o, this.setupLicenseXHR(o, a, e, t).then(({
        xhr: l,
        licenseChallenge: c
      }) => {
        e.keySystem == On.PLAYREADY && (c = this.unpackPlayReadyKeyMessage(l, c)), l.send(c);
      });
    });
  }
  onMediaAttached(e, t) {
    if (!this.config.emeEnabled)
      return;
    const i = t.media;
    this.media = i, i.addEventListener("encrypted", this.onMediaEncrypted), i.addEventListener("waitingforkey", this.onWaitingForKey);
  }
  onMediaDetached() {
    const e = this.media, t = this.mediaKeySessions;
    e && (e.removeEventListener("encrypted", this.onMediaEncrypted), e.removeEventListener("waitingforkey", this.onWaitingForKey), this.media = null), this._requestLicenseFailureCount = 0, this.setMediaKeysQueue = [], this.mediaKeySessions = [], this.keyIdToKeySessionPromise = {}, Lg.clearKeyUriToKeyIdMap();
    const i = t.length;
    Vf.CDMCleanupPromise = Promise.all(t.map((n) => this.removeSession(n)).concat(e == null ? void 0 : e.setMediaKeys(null).catch((n) => {
      this.log(`Could not clear media keys: ${n}`);
    }))).then(() => {
      i && (this.log("finished closing key sessions and clearing media keys"), t.length = 0);
    }).catch((n) => {
      this.log(`Could not close sessions and clear media keys: ${n}`);
    });
  }
  onManifestLoading() {
    this.keyFormatPromise = null;
  }
  onManifestLoaded(e, {
    sessionKeys: t
  }) {
    if (!(!t || !this.config.emeEnabled) && !this.keyFormatPromise) {
      const i = t.reduce((n, r) => (n.indexOf(r.keyFormat) === -1 && n.push(r.keyFormat), n), []);
      this.log(`Selecting key-system from session-keys ${i.join(", ")}`), this.keyFormatPromise = this.getKeyFormatPromise(i);
    }
  }
  removeSession(e) {
    const {
      mediaKeysSession: t,
      licenseXhr: i
    } = e;
    if (t) {
      this.log(`Remove licenses and keys and close session ${t.sessionId}`), e._onmessage && (t.removeEventListener("message", e._onmessage), e._onmessage = void 0), e._onkeystatuseschange && (t.removeEventListener("keystatuseschange", e._onkeystatuseschange), e._onkeystatuseschange = void 0), i && i.readyState !== XMLHttpRequest.DONE && i.abort(), e.mediaKeysSession = e.decryptdata = e.licenseXhr = void 0;
      const n = this.mediaKeySessions.indexOf(e);
      return n > -1 && this.mediaKeySessions.splice(n, 1), t.remove().catch((r) => {
        this.log(`Could not remove session: ${r}`);
      }).then(() => t.close()).catch((r) => {
        this.log(`Could not close session: ${r}`);
      });
    }
  }
}
Vf.CDMCleanupPromise = void 0;
class ca extends Error {
  constructor(e, t) {
    super(t), this.data = void 0, e.error || (e.error = new Error(t)), this.data = e, e.err = e.error;
  }
}
var Zs;
(function(s) {
  s.MANIFEST = "m", s.AUDIO = "a", s.VIDEO = "v", s.MUXED = "av", s.INIT = "i", s.CAPTION = "c", s.TIMED_TEXT = "tt", s.KEY = "k", s.OTHER = "o";
})(Zs || (Zs = {}));
var Nb;
(function(s) {
  s.DASH = "d", s.HLS = "h", s.SMOOTH = "s", s.OTHER = "o";
})(Nb || (Nb = {}));
var mu;
(function(s) {
  s.OBJECT = "CMCD-Object", s.REQUEST = "CMCD-Request", s.SESSION = "CMCD-Session", s.STATUS = "CMCD-Status";
})(mu || (mu = {}));
const Hae = {
  [mu.OBJECT]: ["br", "d", "ot", "tb"],
  [mu.REQUEST]: ["bl", "dl", "mtp", "nor", "nrr", "su"],
  [mu.SESSION]: ["cid", "pr", "sf", "sid", "st", "v"],
  [mu.STATUS]: ["bs", "rtp"]
};
class _p {
  constructor(e, t) {
    this.value = void 0, this.params = void 0, Array.isArray(e) && (e = e.map((i) => i instanceof _p ? i : new _p(i))), this.value = e, this.params = t;
  }
}
class gB {
  constructor(e) {
    this.description = void 0, this.description = e;
  }
}
const Vae = "Dict";
function Wae(s) {
  return Array.isArray(s) ? JSON.stringify(s) : s instanceof Map ? "Map{}" : s instanceof Set ? "Set{}" : typeof s == "object" ? JSON.stringify(s) : String(s);
}
function Xae(s, e, t, i) {
  return new Error(`failed to ${s} "${Wae(e)}" as ${t}`, {
    cause: i
  });
}
const B_ = "Bare Item", jae = "Boolean", qae = "Byte Sequence", Yae = "Decimal", Zae = "Integer";
function Kae(s) {
  return s < -999999999999999 || 999999999999999 < s;
}
const Jae = /[\x00-\x1f\x7f]+/, Qae = "Token", $ae = "Key";
function f0(s, e, t) {
  return Xae("serialize", s, e, t);
}
function eoe(s) {
  if (typeof s != "boolean")
    throw f0(s, jae);
  return s ? "?1" : "?0";
}
function toe(s) {
  return btoa(String.fromCharCode(...s));
}
function ioe(s) {
  if (ArrayBuffer.isView(s) === !1)
    throw f0(s, qae);
  return `:${toe(s)}:`;
}
function vB(s) {
  if (Kae(s))
    throw f0(s, Zae);
  return s.toString();
}
function noe(s) {
  return `@${vB(s.getTime() / 1e3)}`;
}
function yB(s, e) {
  if (s < 0)
    return -yB(-s, e);
  const t = Math.pow(10, e);
  if (Math.abs(s * t % 1 - 0.5) < Number.EPSILON) {
    const n = Math.floor(s * t);
    return (n % 2 === 0 ? n : n + 1) / t;
  } else
    return Math.round(s * t) / t;
}
function soe(s) {
  const e = yB(s, 3);
  if (Math.floor(Math.abs(e)).toString().length > 12)
    throw f0(s, Yae);
  const t = e.toString();
  return t.includes(".") ? t : `${t}.0`;
}
const roe = "String";
function aoe(s) {
  if (Jae.test(s))
    throw f0(s, roe);
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}
function ooe(s) {
  return s.description || s.toString().slice(7, -1);
}
function F_(s) {
  const e = ooe(s);
  if (/^([a-zA-Z*])([!#$%&'*+\-.^_`|~\w:/]*)$/.test(e) === !1)
    throw f0(e, Qae);
  return e;
}
function Bb(s) {
  switch (typeof s) {
    case "number":
      if (!Bt(s))
        throw f0(s, B_);
      return Number.isInteger(s) ? vB(s) : soe(s);
    case "string":
      return aoe(s);
    case "symbol":
      return F_(s);
    case "boolean":
      return eoe(s);
    case "object":
      if (s instanceof Date)
        return noe(s);
      if (s instanceof Uint8Array)
        return ioe(s);
      if (s instanceof gB)
        return F_(s);
    default:
      throw f0(s, B_);
  }
}
function Fb(s) {
  if (/^[a-z*][a-z0-9\-_.*]*$/.test(s) === !1)
    throw f0(s, $ae);
  return s;
}
function cw(s) {
  return s == null ? "" : Object.entries(s).map(([e, t]) => t === !0 ? `;${Fb(e)}` : `;${Fb(e)}=${Bb(t)}`).join("");
}
function xB(s) {
  return s instanceof _p ? `${Bb(s.value)}${cw(s.params)}` : Bb(s);
}
function loe(s) {
  return `(${s.value.map(xB).join(" ")})${cw(s.params)}`;
}
function coe(s, e = {
  whitespace: !0
}) {
  if (typeof s != "object")
    throw f0(s, Vae);
  const t = s instanceof Map ? s.entries() : Object.entries(s), i = e != null && e.whitespace ? " " : "";
  return Array.from(t).map(([n, r]) => {
    r instanceof _p || (r = new _p(r));
    let a = Fb(n);
    return r.value === !0 ? a += cw(r.params) : (a += "=", Array.isArray(r.value) ? a += loe(r) : a += xB(r)), a;
  }).join(`,${i}`);
}
function hoe(s, e) {
  return coe(s, e);
}
const uoe = (s) => s === "ot" || s === "sf" || s === "st", doe = (s) => typeof s == "number" ? Bt(s) : s != null && s !== "" && s !== !1;
function foe(s, e) {
  const t = new URL(s), i = new URL(e);
  if (t.origin !== i.origin)
    return s;
  const n = t.pathname.split("/").slice(1), r = i.pathname.split("/").slice(1, -1);
  for (; n[0] === r[0]; )
    n.shift(), r.shift();
  for (; r.length; )
    r.shift(), n.unshift("..");
  return n.join("/");
}
function poe() {
  try {
    return crypto.randomUUID();
  } catch {
    try {
      const e = URL.createObjectURL(new Blob()), t = e.toString();
      return URL.revokeObjectURL(e), t.slice(t.lastIndexOf("/") + 1);
    } catch {
      let t = (/* @__PURE__ */ new Date()).getTime();
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (n) => {
        const r = (t + Math.random() * 16) % 16 | 0;
        return t = Math.floor(t / 16), (n == "x" ? r : r & 3 | 8).toString(16);
      });
    }
  }
}
const Hy = (s) => Math.round(s), moe = (s, e) => (e != null && e.baseUrl && (s = foe(s, e.baseUrl)), encodeURIComponent(s)), d3 = (s) => Hy(s / 100) * 100, goe = {
  /**
   * Bitrate (kbps) rounded integer
   */
  br: Hy,
  /**
   * Duration (milliseconds) rounded integer
   */
  d: Hy,
  /**
   * Buffer Length (milliseconds) rounded nearest 100ms
   */
  bl: d3,
  /**
   * Deadline (milliseconds) rounded nearest 100ms
   */
  dl: d3,
  /**
   * Measured Throughput (kbps) rounded nearest 100kbps
   */
  mtp: d3,
  /**
   * Next Object Request URL encoded
   */
  nor: moe,
  /**
   * Requested maximum throughput (kbps) rounded nearest 100kbps
   */
  rtp: d3,
  /**
   * Top Bitrate (kbps) rounded integer
   */
  tb: Hy
};
function voe(s, e) {
  const t = {};
  if (s == null || typeof s != "object")
    return t;
  const i = Object.keys(s).sort(), n = as({}, goe, e == null ? void 0 : e.formatters), r = e == null ? void 0 : e.filter;
  return i.forEach((a) => {
    if (r != null && r(a))
      return;
    let o = s[a];
    const l = n[a];
    l && (o = l(o, e)), !(a === "v" && o === 1) && (a == "pr" && o === 1 || doe(o) && (uoe(a) && typeof o == "string" && (o = new gB(o)), t[a] = o));
  }), t;
}
function AB(s, e = {}) {
  return s ? hoe(voe(s, e), as({
    whitespace: !1
  }, e)) : "";
}
function yoe(s, e = {}) {
  if (!s)
    return {};
  const t = Object.entries(s), i = Object.entries(Hae).concat(Object.entries((e == null ? void 0 : e.customHeaderMap) || {})), n = t.reduce((r, a) => {
    var o;
    const [c, h] = a, u = ((o = i.find((d) => d[1].includes(c))) == null ? void 0 : o[0]) || mu.REQUEST;
    return (r[u]) != null || (r[u] = {}), r[u][c] = h, r;
  }, {});
  return Object.entries(n).reduce((r, [a, o]) => (r[a] = AB(o, e), r), {});
}
function xoe(s, e, t) {
  return as(s, yoe(e, t));
}
const Aoe = "CMCD";
function boe(s, e = {}) {
  if (!s)
    return "";
  const t = AB(s, e);
  return `${Aoe}=${encodeURIComponent(t)}`;
}
const U_ = /CMCD=[^&#]+/;
function Moe(s, e, t) {
  const i = boe(e, t);
  if (!i)
    return s;
  if (U_.test(s))
    return s.replace(U_, i);
  const n = s.includes("?") ? "&" : "?";
  return `${s}${n}${i}`;
}
class Soe {
  // eslint-disable-line no-restricted-globals
  constructor(e) {
    this.hls = void 0, this.config = void 0, this.media = void 0, this.sid = void 0, this.cid = void 0, this.useHeaders = !1, this.includeKeys = void 0, this.initialized = !1, this.starved = !1, this.buffering = !0, this.audioBuffer = void 0, this.videoBuffer = void 0, this.onWaiting = () => {
      this.initialized && (this.starved = !0), this.buffering = !0;
    }, this.onPlaying = () => {
      this.initialized || (this.initialized = !0), this.buffering = !1;
    }, this.applyPlaylistData = (n) => {
      try {
        this.apply(n, {
          ot: Zs.MANIFEST,
          su: !this.initialized
        });
      } catch (r) {
        ze.warn("Could not generate manifest CMCD data.", r);
      }
    }, this.applyFragmentData = (n) => {
      try {
        const r = n.frag, a = this.hls.levels[r.level], o = this.getObjectType(r), l = {
          d: r.duration * 1e3,
          ot: o
        };
        (o === Zs.VIDEO || o === Zs.AUDIO || o == Zs.MUXED) && (l.br = a.bitrate / 1e3, l.tb = this.getTopBandwidth(o) / 1e3, l.bl = this.getBufferLength(o)), this.apply(n, l);
      } catch (r) {
        ze.warn("Could not generate segment CMCD data.", r);
      }
    }, this.hls = e;
    const t = this.config = e.config, {
      cmcd: i
    } = t;
    i != null && (t.pLoader = this.createPlaylistLoader(), t.fLoader = this.createFragmentLoader(), this.sid = i.sessionId || poe(), this.cid = i.contentId, this.useHeaders = i.useHeaders === !0, this.includeKeys = i.includeKeys, this.registerListeners());
  }
  registerListeners() {
    const e = this.hls;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHED, this.onMediaDetached, this), e.on(j.BUFFER_CREATED, this.onBufferCreated, this);
  }
  unregisterListeners() {
    const e = this.hls;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHED, this.onMediaDetached, this), e.off(j.BUFFER_CREATED, this.onBufferCreated, this);
  }
  destroy() {
    this.unregisterListeners(), this.onMediaDetached(), this.hls = this.config = this.audioBuffer = this.videoBuffer = null, this.onWaiting = this.onPlaying = null;
  }
  onMediaAttached(e, t) {
    this.media = t.media, this.media.addEventListener("waiting", this.onWaiting), this.media.addEventListener("playing", this.onPlaying);
  }
  onMediaDetached() {
    this.media && (this.media.removeEventListener("waiting", this.onWaiting), this.media.removeEventListener("playing", this.onPlaying), this.media = null);
  }
  onBufferCreated(e, t) {
    var i, n;
    this.audioBuffer = (i = t.tracks.audio) == null ? void 0 : i.buffer, this.videoBuffer = (n = t.tracks.video) == null ? void 0 : n.buffer;
  }
  /**
   * Create baseline CMCD data
   */
  createData() {
    var e;
    return {
      v: 1,
      sf: Nb.HLS,
      sid: this.sid,
      cid: this.cid,
      pr: (e = this.media) == null ? void 0 : e.playbackRate,
      mtp: this.hls.bandwidthEstimate / 1e3
    };
  }
  /**
   * Apply CMCD data to a request.
   */
  apply(e, t = {}) {
    as(t, this.createData());
    const i = t.ot === Zs.INIT || t.ot === Zs.VIDEO || t.ot === Zs.MUXED;
    this.starved && i && (t.bs = !0, t.su = !0, this.starved = !1), t.su == null && (t.su = this.buffering);
    const {
      includeKeys: n
    } = this;
    n && (t = Object.keys(t).reduce((r, a) => (n.includes(a) && (r[a] = t[a]), r), {})), this.useHeaders ? (e.headers || (e.headers = {}), xoe(e.headers, t)) : e.url = Moe(e.url, t);
  }
  /**
   * The CMCD object type.
   */
  getObjectType(e) {
    const {
      type: t
    } = e;
    if (t === "subtitle")
      return Zs.TIMED_TEXT;
    if (e.sn === "initSegment")
      return Zs.INIT;
    if (t === "audio")
      return Zs.AUDIO;
    if (t === "main")
      return this.hls.audioTracks.length ? Zs.VIDEO : Zs.MUXED;
  }
  /**
   * Get the highest bitrate.
   */
  getTopBandwidth(e) {
    let t = 0, i;
    const n = this.hls;
    if (e === Zs.AUDIO)
      i = n.audioTracks;
    else {
      const r = n.maxAutoLevel, a = r > -1 ? r + 1 : n.levels.length;
      i = n.levels.slice(0, a);
    }
    for (const r of i)
      r.bitrate > t && (t = r.bitrate);
    return t > 0 ? t : NaN;
  }
  /**
   * Get the buffer length for a media type in milliseconds
   */
  getBufferLength(e) {
    const t = this.hls.media, i = e === Zs.AUDIO ? this.audioBuffer : this.videoBuffer;
    return !i || !t ? NaN : Mn.bufferInfo(i, t.currentTime, this.config.maxBufferHole).len * 1e3;
  }
  /**
   * Create a playlist loader
   */
  createPlaylistLoader() {
    const {
      pLoader: e
    } = this.config, t = this.applyPlaylistData, i = e || this.config.loader;
    return class {
      constructor(r) {
        this.loader = void 0, this.loader = new i(r);
      }
      get stats() {
        return this.loader.stats;
      }
      get context() {
        return this.loader.context;
      }
      destroy() {
        this.loader.destroy();
      }
      abort() {
        this.loader.abort();
      }
      load(r, a, o) {
        t(r), this.loader.load(r, a, o);
      }
    };
  }
  /**
   * Create a playlist loader
   */
  createFragmentLoader() {
    const {
      fLoader: e
    } = this.config, t = this.applyFragmentData, i = e || this.config.loader;
    return class {
      constructor(r) {
        this.loader = void 0, this.loader = new i(r);
      }
      get stats() {
        return this.loader.stats;
      }
      get context() {
        return this.loader.context;
      }
      destroy() {
        this.loader.destroy();
      }
      abort() {
        this.loader.abort();
      }
      load(r, a, o) {
        t(r), this.loader.load(r, a, o);
      }
    };
  }
}
const woe = 3e5;
class Eoe {
  constructor(e) {
    this.hls = void 0, this.log = void 0, this.loader = null, this.uri = null, this.pathwayId = ".", this.pathwayPriority = null, this.timeToLoad = 300, this.reloadTimer = -1, this.updated = 0, this.started = !1, this.enabled = !0, this.levels = null, this.audioTracks = null, this.subtitleTracks = null, this.penalizedPathways = {}, this.hls = e, this.log = ze.log.bind(ze, "[content-steering]:"), this.registerListeners();
  }
  registerListeners() {
    const e = this.hls;
    e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.ERROR, this.onError, this);
  }
  unregisterListeners() {
    const e = this.hls;
    e && (e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.ERROR, this.onError, this));
  }
  startLoad() {
    if (this.started = !0, this.clearTimeout(), this.enabled && this.uri) {
      if (this.updated) {
        const e = this.timeToLoad * 1e3 - (performance.now() - this.updated);
        if (e > 0) {
          this.scheduleRefresh(this.uri, e);
          return;
        }
      }
      this.loadSteeringManifest(this.uri);
    }
  }
  stopLoad() {
    this.started = !1, this.loader && (this.loader.destroy(), this.loader = null), this.clearTimeout();
  }
  clearTimeout() {
    this.reloadTimer !== -1 && (self.clearTimeout(this.reloadTimer), this.reloadTimer = -1);
  }
  destroy() {
    this.unregisterListeners(), this.stopLoad(), this.hls = null, this.levels = this.audioTracks = this.subtitleTracks = null;
  }
  removeLevel(e) {
    const t = this.levels;
    t && (this.levels = t.filter((i) => i !== e));
  }
  onManifestLoading() {
    this.stopLoad(), this.enabled = !0, this.timeToLoad = 300, this.updated = 0, this.uri = null, this.pathwayId = ".", this.levels = this.audioTracks = this.subtitleTracks = null;
  }
  onManifestLoaded(e, t) {
    const {
      contentSteering: i
    } = t;
    i !== null && (this.pathwayId = i.pathwayId, this.uri = i.uri, this.started && this.startLoad());
  }
  onManifestParsed(e, t) {
    this.audioTracks = t.audioTracks, this.subtitleTracks = t.subtitleTracks;
  }
  onError(e, t) {
    const {
      errorAction: i
    } = t;
    if ((i == null ? void 0 : i.action) === Us.SendAlternateToPenaltyBox && i.flags === Va.MoveAllAlternatesMatchingHost) {
      const n = this.levels;
      let r = this.pathwayPriority, a = this.pathwayId;
      if (t.context) {
        const {
          groupId: o,
          pathwayId: l,
          type: c
        } = t.context;
        o && n ? a = this.getPathwayForGroupId(o, c, a) : l && (a = l);
      }
      a in this.penalizedPathways || (this.penalizedPathways[a] = performance.now()), !r && n && (r = n.reduce((o, l) => (o.indexOf(l.pathwayId) === -1 && o.push(l.pathwayId), o), [])), r && r.length > 1 && (this.updatePathwayPriority(r), i.resolved = this.pathwayId !== a), i.resolved || ze.warn(`Could not resolve ${t.details} ("${t.error.message}") with content-steering for Pathway: ${a} levels: ${n && n.length} priorities: ${JSON.stringify(r)} penalized: ${JSON.stringify(this.penalizedPathways)}`);
    }
  }
  filterParsedLevels(e) {
    this.levels = e;
    let t = this.getLevelsForPathway(this.pathwayId);
    if (t.length === 0) {
      const i = e[0].pathwayId;
      this.log(`No levels found in Pathway ${this.pathwayId}. Setting initial Pathway to "${i}"`), t = this.getLevelsForPathway(i), this.pathwayId = i;
    }
    return t.length !== e.length ? (this.log(`Found ${t.length}/${e.length} levels in Pathway "${this.pathwayId}"`), t) : e;
  }
  getLevelsForPathway(e) {
    return this.levels === null ? [] : this.levels.filter((t) => e === t.pathwayId);
  }
  updatePathwayPriority(e) {
    this.pathwayPriority = e;
    let t;
    const i = this.penalizedPathways, n = performance.now();
    Object.keys(i).forEach((r) => {
      n - i[r] > woe && delete i[r];
    });
    for (let r = 0; r < e.length; r++) {
      const a = e[r];
      if (a in i)
        continue;
      if (a === this.pathwayId)
        return;
      const o = this.hls.nextLoadLevel, l = this.hls.levels[o];
      if (t = this.getLevelsForPathway(a), t.length > 0) {
        this.log(`Setting Pathway to "${a}"`), this.pathwayId = a, zN(t), this.hls.trigger(j.LEVELS_UPDATED, {
          levels: t
        });
        const c = this.hls.levels[o];
        l && c && this.levels && (c.attrs["STABLE-VARIANT-ID"] !== l.attrs["STABLE-VARIANT-ID"] && c.bitrate !== l.bitrate && this.log(`Unstable Pathways change from bitrate ${l.bitrate} to ${c.bitrate}`), this.hls.nextLoadLevel = o);
        break;
      }
    }
  }
  getPathwayForGroupId(e, t, i) {
    const n = this.getLevelsForPathway(i).concat(this.levels || []);
    for (let r = 0; r < n.length; r++)
      if (t === ki.AUDIO_TRACK && n[r].hasAudioGroup(e) || t === ki.SUBTITLE_TRACK && n[r].hasSubtitleGroup(e))
        return n[r].pathwayId;
    return i;
  }
  clonePathways(e) {
    const t = this.levels;
    if (!t)
      return;
    const i = {}, n = {};
    e.forEach((r) => {
      const {
        ID: a,
        "BASE-ID": o,
        "URI-REPLACEMENT": l
      } = r;
      if (t.some((h) => h.pathwayId === a))
        return;
      const c = this.getLevelsForPathway(o).map((h) => {
        const u = new Bn(h.attrs);
        u["PATHWAY-ID"] = a;
        const d = u.AUDIO && `${u.AUDIO}_clone_${a}`, f = u.SUBTITLES && `${u.SUBTITLES}_clone_${a}`;
        d && (i[u.AUDIO] = d, u.AUDIO = d), f && (n[u.SUBTITLES] = f, u.SUBTITLES = f);
        const p = bB(h.uri, u["STABLE-VARIANT-ID"], "PER-VARIANT-URIS", l), v = new Tp({
          attrs: u,
          audioCodec: h.audioCodec,
          bitrate: h.bitrate,
          height: h.height,
          name: h.name,
          url: p,
          videoCodec: h.videoCodec,
          width: h.width
        });
        if (h.audioGroups)
          for (let g = 1; g < h.audioGroups.length; g++)
            v.addGroupId("audio", `${h.audioGroups[g]}_clone_${a}`);
        if (h.subtitleGroups)
          for (let g = 1; g < h.subtitleGroups.length; g++)
            v.addGroupId("text", `${h.subtitleGroups[g]}_clone_${a}`);
        return v;
      });
      t.push(...c), z_(this.audioTracks, i, l, a), z_(this.subtitleTracks, n, l, a);
    });
  }
  loadSteeringManifest(e) {
    const t = this.hls.config, i = t.loader;
    this.loader && this.loader.destroy(), this.loader = new i(t);
    let n;
    try {
      n = new self.URL(e);
    } catch {
      this.enabled = !1, this.log(`Failed to parse Steering Manifest URI: ${e}`);
      return;
    }
    if (n.protocol !== "data:") {
      const h = (this.hls.bandwidthEstimate || t.abrEwmaDefaultEstimate) | 0;
      n.searchParams.set("_HLS_pathway", this.pathwayId), n.searchParams.set("_HLS_throughput", "" + h);
    }
    const r = {
      responseType: "json",
      url: n.href
    }, a = t.steeringManifestLoadPolicy.default, o = a.errorRetry || a.timeoutRetry || {}, l = {
      loadPolicy: a,
      timeout: a.maxLoadTimeMs,
      maxRetry: o.maxNumRetry || 0,
      retryDelay: o.retryDelayMs || 0,
      maxRetryDelay: o.maxRetryDelayMs || 0
    }, c = {
      onSuccess: (h, u, d, f) => {
        this.log(`Loaded steering manifest: "${n}"`);
        const p = h.data;
        if (p.VERSION !== 1) {
          this.log(`Steering VERSION ${p.VERSION} not supported!`);
          return;
        }
        this.updated = performance.now(), this.timeToLoad = p.TTL;
        const {
          "RELOAD-URI": v,
          "PATHWAY-CLONES": g,
          "PATHWAY-PRIORITY": m
        } = p;
        if (v)
          try {
            this.uri = new self.URL(v, n).href;
          } catch {
            this.enabled = !1, this.log(`Failed to parse Steering Manifest RELOAD-URI: ${v}`);
            return;
          }
        this.scheduleRefresh(this.uri || d.url), g && this.clonePathways(g);
        const x = {
          steeringManifest: p,
          url: n.toString()
        };
        this.hls.trigger(j.STEERING_MANIFEST_LOADED, x), m && this.updatePathwayPriority(m);
      },
      onError: (h, u, d, f) => {
        if (this.log(`Error loading steering manifest: ${h.code} ${h.text} (${u.url})`), this.stopLoad(), h.code === 410) {
          this.enabled = !1, this.log(`Steering manifest ${u.url} no longer available`);
          return;
        }
        let p = this.timeToLoad * 1e3;
        if (h.code === 429) {
          const v = this.loader;
          if (typeof (v == null ? void 0 : v.getResponseHeader) == "function") {
            const g = v.getResponseHeader("Retry-After");
            g && (p = parseFloat(g) * 1e3);
          }
          this.log(`Steering manifest ${u.url} rate limited`);
          return;
        }
        this.scheduleRefresh(this.uri || u.url, p);
      },
      onTimeout: (h, u, d) => {
        this.log(`Timeout loading steering manifest (${u.url})`), this.scheduleRefresh(this.uri || u.url);
      }
    };
    this.log(`Requesting steering manifest: ${n}`), this.loader.load(r, l, c);
  }
  scheduleRefresh(e, t = this.timeToLoad * 1e3) {
    this.clearTimeout(), this.reloadTimer = self.setTimeout(() => {
      var i;
      const n = (i = this.hls) == null ? void 0 : i.media;
      if (n && !n.ended) {
        this.loadSteeringManifest(e);
        return;
      }
      this.scheduleRefresh(e, this.timeToLoad * 1e3);
    }, t);
  }
}
function z_(s, e, t, i) {
  s && Object.keys(e).forEach((n) => {
    const r = s.filter((a) => a.groupId === n).map((a) => {
      const o = as({}, a);
      return o.details = void 0, o.attrs = new Bn(o.attrs), o.url = o.attrs.URI = bB(a.url, a.attrs["STABLE-RENDITION-ID"], "PER-RENDITION-URIS", t), o.groupId = o.attrs["GROUP-ID"] = e[n], o.attrs["PATHWAY-ID"] = i, o;
    });
    s.push(...r);
  });
}
function bB(s, e, t, i) {
  const {
    HOST: n,
    PARAMS: r,
    [t]: a
  } = i;
  let o;
  e && (o = a == null ? void 0 : a[e], o && (s = o));
  const l = new self.URL(s);
  return n && !o && (l.host = n), r && Object.keys(r).sort().forEach((c) => {
    c && l.searchParams.set(c, r[c]);
  }), l.href;
}
const Toe = /^age:\s*[\d.]+\s*$/im;
class MB {
  constructor(e) {
    this.xhrSetup = void 0, this.requestTimeout = void 0, this.retryTimeout = void 0, this.retryDelay = void 0, this.config = null, this.callbacks = null, this.context = null, this.loader = null, this.stats = void 0, this.xhrSetup = e && e.xhrSetup || null, this.stats = new o8(), this.retryDelay = 0;
  }
  destroy() {
    this.callbacks = null, this.abortInternal(), this.loader = null, this.config = null, this.context = null, this.xhrSetup = null, this.stats = null;
  }
  abortInternal() {
    const e = this.loader;
    self.clearTimeout(this.requestTimeout), self.clearTimeout(this.retryTimeout), e && (e.onreadystatechange = null, e.onprogress = null, e.readyState !== 4 && (this.stats.aborted = !0, e.abort()));
  }
  abort() {
    var e;
    this.abortInternal(), (e = this.callbacks) != null && e.onAbort && this.callbacks.onAbort(this.stats, this.context, this.loader);
  }
  load(e, t, i) {
    if (this.stats.loading.start)
      throw new Error("Loader can only be used once.");
    this.stats.loading.start = self.performance.now(), this.context = e, this.config = t, this.callbacks = i, this.loadInternal();
  }
  loadInternal() {
    const {
      config: e,
      context: t
    } = this;
    if (!e || !t)
      return;
    const i = this.loader = new self.XMLHttpRequest(), n = this.stats;
    n.loading.first = 0, n.loaded = 0, n.aborted = !1;
    const r = this.xhrSetup;
    r ? Promise.resolve().then(() => {
      if (!this.stats.aborted)
        return r(i, t.url);
    }).catch((a) => (i.open("GET", t.url, !0), r(i, t.url))).then(() => {
      this.stats.aborted || this.openAndSendXhr(i, t, e);
    }).catch((a) => {
      this.callbacks.onError({
        code: i.status,
        text: a.message
      }, t, i, n);
    }) : this.openAndSendXhr(i, t, e);
  }
  openAndSendXhr(e, t, i) {
    e.readyState || e.open("GET", t.url, !0);
    const n = t.headers, {
      maxTimeToFirstByteMs: r,
      maxLoadTimeMs: a
    } = i.loadPolicy;
    if (n)
      for (const o in n)
        e.setRequestHeader(o, n[o]);
    t.rangeEnd && e.setRequestHeader("Range", "bytes=" + t.rangeStart + "-" + (t.rangeEnd - 1)), e.onreadystatechange = this.readystatechange.bind(this), e.onprogress = this.loadprogress.bind(this), e.responseType = t.responseType, self.clearTimeout(this.requestTimeout), i.timeout = r && Bt(r) ? r : a, this.requestTimeout = self.setTimeout(this.loadtimeout.bind(this), i.timeout), e.send();
  }
  readystatechange() {
    const {
      context: e,
      loader: t,
      stats: i
    } = this;
    if (!e || !t)
      return;
    const n = t.readyState, r = this.config;
    if (!i.aborted && n >= 2 && (i.loading.first === 0 && (i.loading.first = Math.max(self.performance.now(), i.loading.start), r.timeout !== r.loadPolicy.maxLoadTimeMs && (self.clearTimeout(this.requestTimeout), r.timeout = r.loadPolicy.maxLoadTimeMs, this.requestTimeout = self.setTimeout(this.loadtimeout.bind(this), r.loadPolicy.maxLoadTimeMs - (i.loading.first - i.loading.start)))), n === 4)) {
      self.clearTimeout(this.requestTimeout), t.onreadystatechange = null, t.onprogress = null;
      const a = t.status, o = t.responseType !== "text";
      if (a >= 200 && a < 300 && (o && t.response || t.responseText !== null)) {
        i.loading.end = Math.max(self.performance.now(), i.loading.first);
        const l = o ? t.response : t.responseText, c = t.responseType === "arraybuffer" ? l.byteLength : l.length;
        if (i.loaded = i.total = c, i.bwEstimate = i.total * 8e3 / (i.loading.end - i.loading.first), !this.callbacks)
          return;
        const h = this.callbacks.onProgress;
        if (h && h(i, e, l, t), !this.callbacks)
          return;
        const u = {
          url: t.responseURL,
          data: l,
          code: a
        };
        this.callbacks.onSuccess(u, i, e, t);
      } else {
        const l = r.loadPolicy.errorRetry, c = i.retry, h = {
          url: e.url,
          data: void 0,
          code: a
        };
        xx(l, c, !1, h) ? this.retry(l) : (ze.error(`${a} while loading ${e.url}`), this.callbacks.onError({
          code: a,
          text: t.statusText
        }, e, t, i));
      }
    }
  }
  loadtimeout() {
    var e;
    const t = (e = this.config) == null ? void 0 : e.loadPolicy.timeoutRetry, i = this.stats.retry;
    if (xx(t, i, !0))
      this.retry(t);
    else {
      var n;
      ze.warn(`timeout while loading ${(n = this.context) == null ? void 0 : n.url}`);
      const r = this.callbacks;
      r && (this.abortInternal(), r.onTimeout(this.stats, this.context, this.loader));
    }
  }
  retry(e) {
    const {
      context: t,
      stats: i
    } = this;
    this.retryDelay = KS(e, i.retry), i.retry++, ze.warn(`${status ? "HTTP Status " + status : "Timeout"} while loading ${t == null ? void 0 : t.url}, retrying ${i.retry}/${e.maxNumRetry} in ${this.retryDelay}ms`), this.abortInternal(), this.loader = null, self.clearTimeout(this.retryTimeout), this.retryTimeout = self.setTimeout(this.loadInternal.bind(this), this.retryDelay);
  }
  loadprogress(e) {
    const t = this.stats;
    t.loaded = e.loaded, e.lengthComputable && (t.total = e.total);
  }
  getCacheAge() {
    let e = null;
    if (this.loader && Toe.test(this.loader.getAllResponseHeaders())) {
      const t = this.loader.getResponseHeader("age");
      e = t ? parseFloat(t) : null;
    }
    return e;
  }
  getResponseHeader(e) {
    return this.loader && new RegExp(`^${e}:\\s*[\\d.]+\\s*$`, "im").test(this.loader.getAllResponseHeaders()) ? this.loader.getResponseHeader(e) : null;
  }
}
function Coe() {
  if (
    // @ts-ignore
    self.fetch && self.AbortController && self.ReadableStream && self.Request
  )
    try {
      return new self.ReadableStream({}), !0;
    } catch {
    }
  return !1;
}
const _oe = /(\d+)-(\d+)\/(\d+)/;
class k_ {
  constructor(e) {
    this.fetchSetup = void 0, this.requestTimeout = void 0, this.request = null, this.response = null, this.controller = void 0, this.context = null, this.config = null, this.callbacks = null, this.stats = void 0, this.loader = null, this.fetchSetup = e.fetchSetup || Loe, this.controller = new self.AbortController(), this.stats = new o8();
  }
  destroy() {
    this.loader = this.callbacks = this.context = this.config = this.request = null, this.abortInternal(), this.response = null, this.fetchSetup = this.controller = this.stats = null;
  }
  abortInternal() {
    this.controller && !this.stats.loading.end && (this.stats.aborted = !0, this.controller.abort());
  }
  abort() {
    var e;
    this.abortInternal(), (e = this.callbacks) != null && e.onAbort && this.callbacks.onAbort(this.stats, this.context, this.response);
  }
  load(e, t, i) {
    const n = this.stats;
    if (n.loading.start)
      throw new Error("Loader can only be used once.");
    n.loading.start = self.performance.now();
    const r = Poe(e, this.controller.signal), a = i.onProgress, o = e.responseType === "arraybuffer", l = o ? "byteLength" : "length", {
      maxTimeToFirstByteMs: c,
      maxLoadTimeMs: h
    } = t.loadPolicy;
    this.context = e, this.config = t, this.callbacks = i, this.request = this.fetchSetup(e, r), self.clearTimeout(this.requestTimeout), t.timeout = c && Bt(c) ? c : h, this.requestTimeout = self.setTimeout(() => {
      this.abortInternal(), i.onTimeout(n, e, this.response);
    }, t.timeout), self.fetch(this.request).then((u) => {
      this.response = this.loader = u;
      const d = Math.max(self.performance.now(), n.loading.start);
      if (self.clearTimeout(this.requestTimeout), t.timeout = h, this.requestTimeout = self.setTimeout(() => {
        this.abortInternal(), i.onTimeout(n, e, this.response);
      }, h - (d - n.loading.start)), !u.ok) {
        const {
          status: f,
          statusText: p
        } = u;
        throw new Ioe(p || "fetch, bad network response", f, u);
      }
      return n.loading.first = d, n.total = Doe(u.headers) || n.total, a && Bt(t.highWaterMark) ? this.loadProgressively(u, n, e, t.highWaterMark, a) : o ? u.arrayBuffer() : e.responseType === "json" ? u.json() : u.text();
    }).then((u) => {
      const d = this.response;
      if (!d)
        throw new Error("loader destroyed");
      self.clearTimeout(this.requestTimeout), n.loading.end = Math.max(self.performance.now(), n.loading.first);
      const f = u[l];
      f && (n.loaded = n.total = f);
      const p = {
        url: d.url,
        data: u,
        code: d.status
      };
      a && !Bt(t.highWaterMark) && a(n, e, u, d), i.onSuccess(p, n, e, d);
    }).catch((u) => {
      if (self.clearTimeout(this.requestTimeout), n.aborted)
        return;
      const d = u && u.code || 0, f = u ? u.message : null;
      i.onError({
        code: d,
        text: f
      }, e, u ? u.details : null, n);
    });
  }
  getCacheAge() {
    let e = null;
    if (this.response) {
      const t = this.response.headers.get("age");
      e = t ? parseFloat(t) : null;
    }
    return e;
  }
  getResponseHeader(e) {
    return this.response ? this.response.headers.get(e) : null;
  }
  loadProgressively(e, t, i, n = 0, r) {
    const a = new VN(), o = e.body.getReader(), l = () => o.read().then((c) => {
      if (c.done)
        return a.dataLength && r(t, i, a.flush(), e), Promise.resolve(new ArrayBuffer(0));
      const h = c.value, u = h.length;
      return t.loaded += u, u < n || a.dataLength ? (a.push(h), a.dataLength >= n && r(t, i, a.flush(), e)) : r(t, i, h, e), l();
    }).catch(() => Promise.reject());
    return l();
  }
}
function Poe(s, e) {
  const t = {
    method: "GET",
    mode: "cors",
    credentials: "same-origin",
    signal: e,
    headers: new self.Headers(as({}, s.headers))
  };
  return s.rangeEnd && t.headers.set("Range", "bytes=" + s.rangeStart + "-" + String(s.rangeEnd - 1)), t;
}
function Roe(s) {
  const e = _oe.exec(s);
  if (e)
    return parseInt(e[2]) - parseInt(e[1]) + 1;
}
function Doe(s) {
  const e = s.get("Content-Range");
  if (e) {
    const i = Roe(e);
    if (Bt(i))
      return i;
  }
  const t = s.get("Content-Length");
  if (t)
    return parseInt(t);
}
function Loe(s, e) {
  return new self.Request(s.url, e);
}
class Ioe extends Error {
  constructor(e, t, i) {
    super(e), this.code = void 0, this.details = void 0, this.code = t, this.details = i;
  }
}
const Ooe = /\s/, Noe = {
  newCue(s, e, t, i) {
    const n = [];
    let r, a, o, l, c;
    const h = self.VTTCue || self.TextTrackCue;
    for (let d = 0; d < i.rows.length; d++)
      if (r = i.rows[d], o = !0, l = 0, c = "", !r.isEmpty()) {
        var u;
        for (let v = 0; v < r.chars.length; v++)
          Ooe.test(r.chars[v].uchar) && o ? l++ : (c += r.chars[v].uchar, o = !1);
        r.cueStartTime = e, e === t && (t += 1e-4), l >= 16 ? l-- : l++;
        const f = uB(c.trim()), p = ow(e, t, f);
        s != null && (u = s.cues) != null && u.getCueById(p) || (a = new h(e, t, f), a.id = p, a.line = d + 1, a.align = "left", a.position = 10 + Math.min(80, Math.floor(l * 8 / 32) * 10), n.push(a));
      }
    return s && n.length && (n.sort((d, f) => d.line === "auto" || f.line === "auto" ? 0 : d.line > 8 && f.line > 8 ? f.line - d.line : d.line - f.line), n.forEach((d) => NN(s, d))), n;
  }
}, Boe = {
  maxTimeToFirstByteMs: 8e3,
  maxLoadTimeMs: 2e4,
  timeoutRetry: null,
  errorRetry: null
}, SB = Ls(Ls({
  autoStartLoad: !0,
  // used by stream-controller
  startPosition: -1,
  // used by stream-controller
  defaultAudioCodec: void 0,
  // used by stream-controller
  debug: !1,
  // used by logger
  capLevelOnFPSDrop: !1,
  // used by fps-controller
  capLevelToPlayerSize: !1,
  // used by cap-level-controller
  ignoreDevicePixelRatio: !1,
  // used by cap-level-controller
  preferManagedMediaSource: !0,
  initialLiveManifestSize: 1,
  // used by stream-controller
  maxBufferLength: 30,
  // used by stream-controller
  backBufferLength: 1 / 0,
  // used by buffer-controller
  frontBufferFlushThreshold: 1 / 0,
  maxBufferSize: 60 * 1e3 * 1e3,
  // used by stream-controller
  maxBufferHole: 0.1,
  // used by stream-controller
  highBufferWatchdogPeriod: 2,
  // used by stream-controller
  nudgeOffset: 0.1,
  // used by stream-controller
  nudgeMaxRetry: 3,
  // used by stream-controller
  maxFragLookUpTolerance: 0.25,
  // used by stream-controller
  liveSyncDurationCount: 3,
  // used by latency-controller
  liveMaxLatencyDurationCount: 1 / 0,
  // used by latency-controller
  liveSyncDuration: void 0,
  // used by latency-controller
  liveMaxLatencyDuration: void 0,
  // used by latency-controller
  maxLiveSyncPlaybackRate: 1,
  // used by latency-controller
  liveDurationInfinity: !1,
  // used by buffer-controller
  /**
   * @deprecated use backBufferLength
   */
  liveBackBufferLength: null,
  // used by buffer-controller
  maxMaxBufferLength: 600,
  // used by stream-controller
  enableWorker: !0,
  // used by transmuxer
  workerPath: null,
  // used by transmuxer
  enableSoftwareAES: !0,
  // used by decrypter
  startLevel: void 0,
  // used by level-controller
  startFragPrefetch: !1,
  // used by stream-controller
  fpsDroppedMonitoringPeriod: 5e3,
  // used by fps-controller
  fpsDroppedMonitoringThreshold: 0.2,
  // used by fps-controller
  appendErrorMaxRetry: 3,
  // used by buffer-controller
  loader: MB,
  // loader: FetchLoader,
  fLoader: void 0,
  // used by fragment-loader
  pLoader: void 0,
  // used by playlist-loader
  xhrSetup: void 0,
  // used by xhr-loader
  licenseXhrSetup: void 0,
  // used by eme-controller
  licenseResponseCallback: void 0,
  // used by eme-controller
  abrController: dre,
  bufferController: gae,
  capLevelController: lw,
  errorController: ere,
  fpsController: Gae,
  stretchShortVideoTrack: !1,
  // used by mp4-remuxer
  maxAudioFramesDrift: 1,
  // used by mp4-remuxer
  forceKeyFrameOnDiscontinuity: !0,
  // used by ts-demuxer
  abrEwmaFastLive: 3,
  // used by abr-controller
  abrEwmaSlowLive: 9,
  // used by abr-controller
  abrEwmaFastVoD: 3,
  // used by abr-controller
  abrEwmaSlowVoD: 9,
  // used by abr-controller
  abrEwmaDefaultEstimate: 5e5,
  // 500 kbps  // used by abr-controller
  abrEwmaDefaultEstimateMax: 5e6,
  // 5 mbps
  abrBandWidthFactor: 0.95,
  // used by abr-controller
  abrBandWidthUpFactor: 0.7,
  // used by abr-controller
  abrMaxWithRealBitrate: !1,
  // used by abr-controller
  maxStarvationDelay: 4,
  // used by abr-controller
  maxLoadingDelay: 4,
  // used by abr-controller
  minAutoBitrate: 0,
  // used by hls
  emeEnabled: !1,
  // used by eme-controller
  widevineLicenseUrl: void 0,
  // used by eme-controller
  drmSystems: {},
  // used by eme-controller
  drmSystemOptions: {},
  // used by eme-controller
  requestMediaKeySystemAccessFunc: bN,
  // used by eme-controller
  testBandwidth: !0,
  progressive: !1,
  lowLatencyMode: !0,
  cmcd: void 0,
  enableDateRangeMetadataCues: !0,
  enableEmsgMetadataCues: !0,
  enableID3MetadataCues: !0,
  useMediaCapabilities: !0,
  certLoadPolicy: {
    default: Boe
  },
  keyLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 8e3,
      maxLoadTimeMs: 2e4,
      timeoutRetry: {
        maxNumRetry: 1,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 2e4,
        backoff: "linear"
      },
      errorRetry: {
        maxNumRetry: 8,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 2e4,
        backoff: "linear"
      }
    }
  },
  manifestLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 1 / 0,
      maxLoadTimeMs: 2e4,
      timeoutRetry: {
        maxNumRetry: 2,
        retryDelayMs: 0,
        maxRetryDelayMs: 0
      },
      errorRetry: {
        maxNumRetry: 1,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 8e3
      }
    }
  },
  playlistLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 1e4,
      maxLoadTimeMs: 2e4,
      timeoutRetry: {
        maxNumRetry: 2,
        retryDelayMs: 0,
        maxRetryDelayMs: 0
      },
      errorRetry: {
        maxNumRetry: 2,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 8e3
      }
    }
  },
  fragLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 1e4,
      maxLoadTimeMs: 12e4,
      timeoutRetry: {
        maxNumRetry: 4,
        retryDelayMs: 0,
        maxRetryDelayMs: 0
      },
      errorRetry: {
        maxNumRetry: 6,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 8e3
      }
    }
  },
  steeringManifestLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 1e4,
      maxLoadTimeMs: 2e4,
      timeoutRetry: {
        maxNumRetry: 2,
        retryDelayMs: 0,
        maxRetryDelayMs: 0
      },
      errorRetry: {
        maxNumRetry: 1,
        retryDelayMs: 1e3,
        maxRetryDelayMs: 8e3
      }
    }
  },
  // These default settings are deprecated in favor of the above policies
  // and are maintained for backwards compatibility
  manifestLoadingTimeOut: 1e4,
  manifestLoadingMaxRetry: 1,
  manifestLoadingRetryDelay: 1e3,
  manifestLoadingMaxRetryTimeout: 64e3,
  levelLoadingTimeOut: 1e4,
  levelLoadingMaxRetry: 4,
  levelLoadingRetryDelay: 1e3,
  levelLoadingMaxRetryTimeout: 64e3,
  fragLoadingTimeOut: 2e4,
  fragLoadingMaxRetry: 6,
  fragLoadingRetryDelay: 1e3,
  fragLoadingMaxRetryTimeout: 64e3
}, Foe()), {}, {
  subtitleStreamController: dae,
  subtitleTrackController: pae,
  timelineController: zae,
  audioStreamController: hae,
  audioTrackController: uae,
  emeController: Vf,
  cmcdController: Soe,
  contentSteeringController: Eoe
});
function Foe() {
  return {
    cueHandler: Noe,
    // used by timeline-controller
    enableWebVTT: !0,
    // used by timeline-controller
    enableIMSC1: !0,
    // used by timeline-controller
    enableCEA708Captions: !0,
    // used by timeline-controller
    captionsTextTrack1Label: "English",
    // used by timeline-controller
    captionsTextTrack1LanguageCode: "en",
    // used by timeline-controller
    captionsTextTrack2Label: "Spanish",
    // used by timeline-controller
    captionsTextTrack2LanguageCode: "es",
    // used by timeline-controller
    captionsTextTrack3Label: "Unknown CC",
    // used by timeline-controller
    captionsTextTrack3LanguageCode: "",
    // used by timeline-controller
    captionsTextTrack4Label: "Unknown CC",
    // used by timeline-controller
    captionsTextTrack4LanguageCode: "",
    // used by timeline-controller
    renderTextTracksNatively: !0
  };
}
function Uoe(s, e) {
  if ((e.liveSyncDurationCount || e.liveMaxLatencyDurationCount) && (e.liveSyncDuration || e.liveMaxLatencyDuration))
    throw new Error("Illegal hls.js config: don't mix up liveSyncDurationCount/liveMaxLatencyDurationCount and liveSyncDuration/liveMaxLatencyDuration");
  if (e.liveMaxLatencyDurationCount !== void 0 && (e.liveSyncDurationCount === void 0 || e.liveMaxLatencyDurationCount <= e.liveSyncDurationCount))
    throw new Error('Illegal hls.js config: "liveMaxLatencyDurationCount" must be greater than "liveSyncDurationCount"');
  if (e.liveMaxLatencyDuration !== void 0 && (e.liveSyncDuration === void 0 || e.liveMaxLatencyDuration <= e.liveSyncDuration))
    throw new Error('Illegal hls.js config: "liveMaxLatencyDuration" must be greater than "liveSyncDuration"');
  const t = Ub(s), i = ["manifest", "level", "frag"], n = ["TimeOut", "MaxRetry", "RetryDelay", "MaxRetryTimeout"];
  return i.forEach((r) => {
    const a = `${r === "level" ? "playlist" : r}LoadPolicy`, o = e[a] === void 0, l = [];
    n.forEach((c) => {
      const h = `${r}Loading${c}`, u = e[h];
      if (u !== void 0 && o) {
        l.push(h);
        const d = t[a].default;
        switch (e[a] = {
          default: d
        }, c) {
          case "TimeOut":
            d.maxLoadTimeMs = u, d.maxTimeToFirstByteMs = u;
            break;
          case "MaxRetry":
            d.errorRetry.maxNumRetry = u, d.timeoutRetry.maxNumRetry = u;
            break;
          case "RetryDelay":
            d.errorRetry.retryDelayMs = u, d.timeoutRetry.retryDelayMs = u;
            break;
          case "MaxRetryTimeout":
            d.errorRetry.maxRetryDelayMs = u, d.timeoutRetry.maxRetryDelayMs = u;
            break;
        }
      }
    }), l.length && ze.warn(`hls.js config: "${l.join('", "')}" setting(s) are deprecated, use "${a}": ${JSON.stringify(e[a])}`);
  }), Ls(Ls({}, t), e);
}
function Ub(s) {
  return s && typeof s == "object" ? Array.isArray(s) ? s.map(Ub) : Object.keys(s).reduce((e, t) => (e[t] = Ub(s[t]), e), {}) : s;
}
function zoe(s) {
  const e = s.loader;
  e !== k_ && e !== MB ? (ze.log("[config]: Custom loader detected, cannot enable progressive streaming"), s.progressive = !1) : Coe() && (s.loader = k_, s.progressive = !0, s.enableSoftwareAES = !0, ze.log("[config]: Progressive streaming enabled, using FetchLoader"));
}
let MA;
class koe extends JS {
  constructor(e, t) {
    super(e, "[level-controller]"), this._levels = [], this._firstLevel = -1, this._maxAutoLevel = -1, this._startLevel = void 0, this.currentLevel = null, this.currentLevelIndex = -1, this.manualLevelIndex = -1, this.steering = void 0, this.onParsedComplete = void 0, this.steering = t, this._registerListeners();
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.on(j.LEVEL_LOADED, this.onLevelLoaded, this), e.on(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this), e.on(j.ERROR, this.onError, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_LOADED, this.onManifestLoaded, this), e.off(j.LEVEL_LOADED, this.onLevelLoaded, this), e.off(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this), e.off(j.ERROR, this.onError, this);
  }
  destroy() {
    this._unregisterListeners(), this.steering = null, this.resetLevels(), super.destroy();
  }
  stopLoad() {
    this._levels.forEach((t) => {
      t.loadError = 0, t.fragmentError = 0;
    }), super.stopLoad();
  }
  resetLevels() {
    this._startLevel = void 0, this.manualLevelIndex = -1, this.currentLevelIndex = -1, this.currentLevel = null, this._levels = [], this._maxAutoLevel = -1;
  }
  onManifestLoading(e, t) {
    this.resetLevels();
  }
  onManifestLoaded(e, t) {
    const i = this.hls.config.preferManagedMediaSource, n = [], r = {}, a = {};
    let o = !1, l = !1, c = !1;
    t.levels.forEach((h) => {
      var u, d;
      const f = h.attrs;
      let {
        audioCodec: p,
        videoCodec: v
      } = h;
      ((u = p) == null ? void 0 : u.indexOf("mp4a.40.34")) !== -1 && (MA || (MA = /chrome|firefox/i.test(navigator.userAgent)), MA && (h.audioCodec = p = void 0)), p && (h.audioCodec = p = gx(p, i)), ((d = v) == null ? void 0 : d.indexOf("avc1")) === 0 && (v = h.videoCodec = Rse(v));
      const {
        width: g,
        height: m,
        unknownCodecs: x
      } = h;
      if (o || (o = !!(g && m)), l || (l = !!v), c || (c = !!p), x != null && x.length || p && !oA(p, "audio", i) || v && !oA(v, "video", i))
        return;
      const {
        CODECS: y,
        "FRAME-RATE": A,
        "HDCP-LEVEL": b,
        "PATHWAY-ID": M,
        RESOLUTION: w,
        "VIDEO-RANGE": C
      } = f, E = `${`${M || "."}-`}${h.bitrate}-${w}-${A}-${y}-${C}-${b}`;
      if (r[E])
        if (r[E].uri !== h.url && !h.attrs["PATHWAY-ID"]) {
          const P = a[E] += 1;
          h.attrs["PATHWAY-ID"] = new Array(P + 1).join(".");
          const L = new Tp(h);
          r[E] = L, n.push(L);
        } else
          r[E].addGroupId("audio", f.AUDIO), r[E].addGroupId("text", f.SUBTITLES);
      else {
        const P = new Tp(h);
        r[E] = P, a[E] = 1, n.push(P);
      }
    }), this.filterAndSortMediaOptions(n, t, o, l, c);
  }
  filterAndSortMediaOptions(e, t, i, n, r) {
    let a = [], o = [], l = e;
    if ((i || n) && r && (l = l.filter(({
      videoCodec: p,
      videoRange: v,
      width: g,
      height: m
    }) => (!!p || !!(g && m)) && Vse(v))), l.length === 0) {
      Promise.resolve().then(() => {
        if (this.hls) {
          t.levels.length && this.warn(`One or more CODECS in variant not supported: ${JSON.stringify(t.levels[0].attrs)}`);
          const p = new Error("no level with compatible codecs found in manifest");
          this.hls.trigger(j.ERROR, {
            type: qt.MEDIA_ERROR,
            details: Xe.MANIFEST_INCOMPATIBLE_CODECS_ERROR,
            fatal: !0,
            url: t.url,
            error: p,
            reason: p.message
          });
        }
      });
      return;
    }
    if (t.audioTracks) {
      const {
        preferManagedMediaSource: p
      } = this.hls.config;
      a = t.audioTracks.filter((v) => !v.audioCodec || oA(v.audioCodec, "audio", p)), G_(a);
    }
    t.subtitles && (o = t.subtitles, G_(o));
    const c = l.slice(0);
    l.sort((p, v) => {
      if (p.attrs["HDCP-LEVEL"] !== v.attrs["HDCP-LEVEL"])
        return (p.attrs["HDCP-LEVEL"] || "") > (v.attrs["HDCP-LEVEL"] || "") ? 1 : -1;
      if (i && p.height !== v.height)
        return p.height - v.height;
      if (p.frameRate !== v.frameRate)
        return p.frameRate - v.frameRate;
      if (p.videoRange !== v.videoRange)
        return vx.indexOf(p.videoRange) - vx.indexOf(v.videoRange);
      if (p.videoCodec !== v.videoCodec) {
        const g = XC(p.videoCodec), m = XC(v.videoCodec);
        if (g !== m)
          return m - g;
      }
      if (p.uri === v.uri && p.codecSet !== v.codecSet) {
        const g = mx(p.codecSet), m = mx(v.codecSet);
        if (g !== m)
          return m - g;
      }
      return p.averageBitrate !== v.averageBitrate ? p.averageBitrate - v.averageBitrate : 0;
    });
    let h = c[0];
    if (this.steering && (l = this.steering.filterParsedLevels(l), l.length !== c.length)) {
      for (let p = 0; p < c.length; p++)
        if (c[p].pathwayId === l[0].pathwayId) {
          h = c[p];
          break;
        }
    }
    this._levels = l;
    for (let p = 0; p < l.length; p++)
      if (l[p] === h) {
        var u;
        this._firstLevel = p;
        const v = h.bitrate, g = this.hls.bandwidthEstimate;
        if (this.log(`manifest loaded, ${l.length} level(s) found, first bitrate: ${v}`), ((u = this.hls.userConfig) == null ? void 0 : u.abrEwmaDefaultEstimate) === void 0) {
          const m = Math.min(v, this.hls.config.abrEwmaDefaultEstimateMax);
          m > g && g === SB.abrEwmaDefaultEstimate && (this.hls.bandwidthEstimate = m);
        }
        break;
      }
    const d = r && !n, f = {
      levels: l,
      audioTracks: a,
      subtitleTracks: o,
      sessionData: t.sessionData,
      sessionKeys: t.sessionKeys,
      firstLevel: this._firstLevel,
      stats: t.stats,
      audio: r,
      video: n,
      altAudio: !d && a.some((p) => !!p.url)
    };
    this.hls.trigger(j.MANIFEST_PARSED, f), (this.hls.config.autoStartLoad || this.hls.forceStartLoad) && this.hls.startLoad(this.hls.config.startPosition);
  }
  get levels() {
    return this._levels.length === 0 ? null : this._levels;
  }
  get level() {
    return this.currentLevelIndex;
  }
  set level(e) {
    const t = this._levels;
    if (t.length === 0)
      return;
    if (e < 0 || e >= t.length) {
      const h = new Error("invalid level idx"), u = e < 0;
      if (this.hls.trigger(j.ERROR, {
        type: qt.OTHER_ERROR,
        details: Xe.LEVEL_SWITCH_ERROR,
        level: e,
        fatal: u,
        error: h,
        reason: h.message
      }), u)
        return;
      e = Math.min(e, t.length - 1);
    }
    const i = this.currentLevelIndex, n = this.currentLevel, r = n ? n.attrs["PATHWAY-ID"] : void 0, a = t[e], o = a.attrs["PATHWAY-ID"];
    if (this.currentLevelIndex = e, this.currentLevel = a, i === e && a.details && n && r === o)
      return;
    this.log(`Switching to level ${e} (${a.height ? a.height + "p " : ""}${a.videoRange ? a.videoRange + " " : ""}${a.codecSet ? a.codecSet + " " : ""}@${a.bitrate})${o ? " with Pathway " + o : ""} from level ${i}${r ? " with Pathway " + r : ""}`);
    const l = {
      level: e,
      attrs: a.attrs,
      details: a.details,
      bitrate: a.bitrate,
      averageBitrate: a.averageBitrate,
      maxBitrate: a.maxBitrate,
      realBitrate: a.realBitrate,
      width: a.width,
      height: a.height,
      codecSet: a.codecSet,
      audioCodec: a.audioCodec,
      videoCodec: a.videoCodec,
      audioGroups: a.audioGroups,
      subtitleGroups: a.subtitleGroups,
      loaded: a.loaded,
      loadError: a.loadError,
      fragmentError: a.fragmentError,
      name: a.name,
      id: a.id,
      uri: a.uri,
      url: a.url,
      urlId: 0,
      audioGroupIds: a.audioGroupIds,
      textGroupIds: a.textGroupIds
    };
    this.hls.trigger(j.LEVEL_SWITCHING, l);
    const c = a.details;
    if (!c || c.live) {
      const h = this.switchParams(a.uri, n == null ? void 0 : n.details, c);
      this.loadPlaylist(h);
    }
  }
  get manualLevel() {
    return this.manualLevelIndex;
  }
  set manualLevel(e) {
    this.manualLevelIndex = e, this._startLevel === void 0 && (this._startLevel = e), e !== -1 && (this.level = e);
  }
  get firstLevel() {
    return this._firstLevel;
  }
  set firstLevel(e) {
    this._firstLevel = e;
  }
  get startLevel() {
    if (this._startLevel === void 0) {
      const e = this.hls.config.startLevel;
      return e !== void 0 ? e : this.hls.firstAutoLevel;
    }
    return this._startLevel;
  }
  set startLevel(e) {
    this._startLevel = e;
  }
  onError(e, t) {
    t.fatal || !t.context || t.context.type === ki.LEVEL && t.context.level === this.level && this.checkRetry(t);
  }
  // reset errors on the successful load of a fragment
  onFragBuffered(e, {
    frag: t
  }) {
    if (t !== void 0 && t.type === jt.MAIN) {
      const i = t.elementaryStreams;
      if (!Object.keys(i).some((r) => !!i[r]))
        return;
      const n = this._levels[t.level];
      n != null && n.loadError && (this.log(`Resetting level error count of ${n.loadError} on frag buffered`), n.loadError = 0);
    }
  }
  onLevelLoaded(e, t) {
    var i;
    const {
      level: n,
      details: r
    } = t, a = this._levels[n];
    if (!a) {
      var o;
      this.warn(`Invalid level index ${n}`), (o = t.deliveryDirectives) != null && o.skip && (r.deltaUpdateFailed = !0);
      return;
    }
    n === this.currentLevelIndex ? (a.fragmentError === 0 && (a.loadError = 0), this.playlistLoaded(n, t, a.details)) : (i = t.deliveryDirectives) != null && i.skip && (r.deltaUpdateFailed = !0);
  }
  loadPlaylist(e) {
    super.loadPlaylist();
    const t = this.currentLevelIndex, i = this.currentLevel;
    if (i && this.shouldLoadPlaylist(i)) {
      let n = i.uri;
      if (e)
        try {
          n = e.addDirectives(n);
        } catch (a) {
          this.warn(`Could not construct new URL with HLS Delivery Directives: ${a}`);
        }
      const r = i.attrs["PATHWAY-ID"];
      this.log(`Loading level index ${t}${(e == null ? void 0 : e.msn) !== void 0 ? " at sn " + e.msn + " part " + e.part : ""} with${r ? " Pathway " + r : ""} ${n}`), this.clearTimer(), this.hls.trigger(j.LEVEL_LOADING, {
        url: n,
        level: t,
        pathwayId: i.attrs["PATHWAY-ID"],
        id: 0,
        // Deprecated Level urlId
        deliveryDirectives: e || null
      });
    }
  }
  get nextLoadLevel() {
    return this.manualLevelIndex !== -1 ? this.manualLevelIndex : this.hls.nextAutoLevel;
  }
  set nextLoadLevel(e) {
    this.level = e, this.manualLevelIndex === -1 && (this.hls.nextAutoLevel = e);
  }
  removeLevel(e) {
    var t;
    const i = this._levels.filter((n, r) => r !== e ? !0 : (this.steering && this.steering.removeLevel(n), n === this.currentLevel && (this.currentLevel = null, this.currentLevelIndex = -1, n.details && n.details.fragments.forEach((a) => a.level = -1)), !1));
    zN(i), this._levels = i, this.currentLevelIndex > -1 && (t = this.currentLevel) != null && t.details && (this.currentLevelIndex = this.currentLevel.details.fragments[0].level), this.hls.trigger(j.LEVELS_UPDATED, {
      levels: i
    });
  }
  onLevelsUpdated(e, {
    levels: t
  }) {
    this._levels = t;
  }
  checkMaxAutoUpdated() {
    const {
      autoLevelCapping: e,
      maxAutoLevel: t,
      maxHdcpLevel: i
    } = this.hls;
    this._maxAutoLevel !== t && (this._maxAutoLevel = t, this.hls.trigger(j.MAX_AUTO_LEVEL_UPDATED, {
      autoLevelCapping: e,
      levels: this.levels,
      maxAutoLevel: t,
      minAutoLevel: this.hls.minAutoLevel,
      maxHdcpLevel: i
    }));
  }
}
function G_(s) {
  const e = {};
  s.forEach((t) => {
    const i = t.groupId || "";
    t.id = e[i] = e[i] || 0, e[i]++;
  });
}
class Goe {
  constructor(e) {
    this.config = void 0, this.keyUriToKeyInfo = {}, this.emeController = null, this.config = e;
  }
  abort(e) {
    for (const i in this.keyUriToKeyInfo) {
      const n = this.keyUriToKeyInfo[i].loader;
      if (n) {
        var t;
        if (e && e !== ((t = n.context) == null ? void 0 : t.frag.type))
          return;
        n.abort();
      }
    }
  }
  detach() {
    for (const e in this.keyUriToKeyInfo) {
      const t = this.keyUriToKeyInfo[e];
      (t.mediaKeySessionContext || t.decryptdata.isCommonEncryption) && delete this.keyUriToKeyInfo[e];
    }
  }
  destroy() {
    this.detach();
    for (const e in this.keyUriToKeyInfo) {
      const t = this.keyUriToKeyInfo[e].loader;
      t && t.destroy();
    }
    this.keyUriToKeyInfo = {};
  }
  createKeyLoadError(e, t = Xe.KEY_LOAD_ERROR, i, n, r) {
    return new q0({
      type: qt.NETWORK_ERROR,
      details: t,
      fatal: !1,
      frag: e,
      response: r,
      error: i,
      networkDetails: n
    });
  }
  loadClear(e, t) {
    if (this.emeController && this.config.emeEnabled) {
      const {
        sn: i,
        cc: n
      } = e;
      for (let r = 0; r < t.length; r++) {
        const a = t[r];
        if (n <= a.cc && (i === "initSegment" || a.sn === "initSegment" || i < a.sn)) {
          this.emeController.selectKeySystemFormat(a).then((o) => {
            a.setKeyFormat(o);
          });
          break;
        }
      }
    }
  }
  load(e) {
    return !e.decryptdata && e.encrypted && this.emeController ? this.emeController.selectKeySystemFormat(e).then((t) => this.loadInternal(e, t)) : this.loadInternal(e);
  }
  loadInternal(e, t) {
    var i, n;
    t && e.setKeyFormat(t);
    const r = e.decryptdata;
    if (!r) {
      const c = new Error(t ? `Expected frag.decryptdata to be defined after setting format ${t}` : "Missing decryption data on fragment in onKeyLoading");
      return Promise.reject(this.createKeyLoadError(e, Xe.KEY_LOAD_ERROR, c));
    }
    const a = r.uri;
    if (!a)
      return Promise.reject(this.createKeyLoadError(e, Xe.KEY_LOAD_ERROR, new Error(`Invalid key URI: "${a}"`)));
    let o = this.keyUriToKeyInfo[a];
    if ((i = o) != null && i.decryptdata.key)
      return r.key = o.decryptdata.key, Promise.resolve({
        frag: e,
        keyInfo: o
      });
    if ((n = o) != null && n.keyLoadPromise) {
      var l;
      switch ((l = o.mediaKeySessionContext) == null ? void 0 : l.keyStatus) {
        case void 0:
        case "status-pending":
        case "usable":
        case "usable-in-future":
          return o.keyLoadPromise.then((c) => (r.key = c.keyInfo.decryptdata.key, {
            frag: e,
            keyInfo: o
          }));
      }
    }
    switch (o = this.keyUriToKeyInfo[a] = {
      decryptdata: r,
      keyLoadPromise: null,
      loader: null,
      mediaKeySessionContext: null
    }, r.method) {
      case "ISO-23001-7":
      case "SAMPLE-AES":
      case "SAMPLE-AES-CENC":
      case "SAMPLE-AES-CTR":
        return r.keyFormat === "identity" ? this.loadKeyHTTP(o, e) : this.loadKeyEME(o, e);
      case "AES-128":
        return this.loadKeyHTTP(o, e);
      default:
        return Promise.reject(this.createKeyLoadError(e, Xe.KEY_LOAD_ERROR, new Error(`Key supplied with unsupported METHOD: "${r.method}"`)));
    }
  }
  loadKeyEME(e, t) {
    const i = {
      frag: t,
      keyInfo: e
    };
    if (this.emeController && this.config.emeEnabled) {
      const n = this.emeController.loadKey(i);
      if (n)
        return (e.keyLoadPromise = n.then((r) => (e.mediaKeySessionContext = r, i))).catch((r) => {
          throw e.keyLoadPromise = null, r;
        });
    }
    return Promise.resolve(i);
  }
  loadKeyHTTP(e, t) {
    const i = this.config, n = i.loader, r = new n(i);
    return t.keyLoader = e.loader = r, e.keyLoadPromise = new Promise((a, o) => {
      const l = {
        keyInfo: e,
        frag: t,
        responseType: "arraybuffer",
        url: e.decryptdata.uri
      }, c = i.keyLoadPolicy.default, h = {
        loadPolicy: c,
        timeout: c.maxLoadTimeMs,
        maxRetry: 0,
        retryDelay: 0,
        maxRetryDelay: 0
      }, u = {
        onSuccess: (d, f, p, v) => {
          const {
            frag: g,
            keyInfo: m,
            url: x
          } = p;
          if (!g.decryptdata || m !== this.keyUriToKeyInfo[x])
            return o(this.createKeyLoadError(g, Xe.KEY_LOAD_ERROR, new Error("after key load, decryptdata unset or changed"), v));
          m.decryptdata.key = g.decryptdata.key = new Uint8Array(d.data), g.keyLoader = null, m.loader = null, a({
            frag: g,
            keyInfo: m
          });
        },
        onError: (d, f, p, v) => {
          this.resetLoader(f), o(this.createKeyLoadError(t, Xe.KEY_LOAD_ERROR, new Error(`HTTP Error ${d.code} loading key ${d.text}`), p, Ls({
            url: l.url,
            data: void 0
          }, d)));
        },
        onTimeout: (d, f, p) => {
          this.resetLoader(f), o(this.createKeyLoadError(t, Xe.KEY_LOAD_TIMEOUT, new Error("key loading timed out"), p));
        },
        onAbort: (d, f, p) => {
          this.resetLoader(f), o(this.createKeyLoadError(t, Xe.INTERNAL_ABORTED, new Error("key loading aborted"), p));
        }
      };
      r.load(l, h, u);
    });
  }
  resetLoader(e) {
    const {
      frag: t,
      keyInfo: i,
      url: n
    } = e, r = i.loader;
    t.keyLoader === r && (t.keyLoader = null, i.loader = null), delete this.keyUriToKeyInfo[n], r && r.destroy();
  }
}
function wB() {
  return self.SourceBuffer || self.WebKitSourceBuffer;
}
function EB() {
  if (!Nu())
    return !1;
  const e = wB();
  return !e || e.prototype && typeof e.prototype.appendBuffer == "function" && typeof e.prototype.remove == "function";
}
function Hoe() {
  if (!EB())
    return !1;
  const s = Nu();
  return typeof (s == null ? void 0 : s.isTypeSupported) == "function" && (["avc1.42E01E,mp4a.40.2", "av01.0.01M.08", "vp09.00.50.08"].some((e) => s.isTypeSupported(Ig(e, "video"))) || ["mp4a.40.2", "fLaC"].some((e) => s.isTypeSupported(Ig(e, "audio"))));
}
function Voe() {
  var s;
  const e = wB();
  return typeof (e == null || (s = e.prototype) == null ? void 0 : s.changeType) == "function";
}
const Woe = 250, Vy = 2, Xoe = 0.1, joe = 0.05;
class qoe {
  constructor(e, t, i, n) {
    this.config = void 0, this.media = null, this.fragmentTracker = void 0, this.hls = void 0, this.nudgeRetry = 0, this.stallReported = !1, this.stalled = null, this.moved = !1, this.seeking = !1, this.config = e, this.media = t, this.fragmentTracker = i, this.hls = n;
  }
  destroy() {
    this.media = null, this.hls = this.fragmentTracker = null;
  }
  /**
   * Checks if the playhead is stuck within a gap, and if so, attempts to free it.
   * A gap is an unbuffered range between two buffered ranges (or the start and the first buffered range).
   *
   * @param lastCurrentTime - Previously read playhead position
   */
  poll(e, t) {
    const {
      config: i,
      media: n,
      stalled: r
    } = this;
    if (n === null)
      return;
    const {
      currentTime: a,
      seeking: o
    } = n, l = this.seeking && !o, c = !this.seeking && o;
    if (this.seeking = o, a !== e) {
      if (this.moved = !0, o || (this.nudgeRetry = 0), r !== null) {
        if (this.stallReported) {
          const g = self.performance.now() - r;
          ze.warn(`playback not stuck anymore @${a}, after ${Math.round(g)}ms`), this.stallReported = !1;
        }
        this.stalled = null;
      }
      return;
    }
    if (c || l) {
      this.stalled = null;
      return;
    }
    if (n.paused && !o || n.ended || n.playbackRate === 0 || !Mn.getBuffered(n).length) {
      this.nudgeRetry = 0;
      return;
    }
    const h = Mn.bufferInfo(n, a, 0), u = h.nextStart || 0;
    if (o) {
      const g = h.len > Vy, m = !u || t && t.start <= a || u - a > Vy && !this.fragmentTracker.getPartialFragment(a);
      if (g || m)
        return;
      this.moved = !1;
    }
    if (!this.moved && this.stalled !== null) {
      var d;
      if (!(h.len > 0) && !u)
        return;
      const m = Math.max(u, h.start || 0) - a, x = this.hls.levels ? this.hls.levels[this.hls.currentLevel] : null, A = (x == null || (d = x.details) == null ? void 0 : d.live) ? x.details.targetduration * 2 : Vy, b = this.fragmentTracker.getPartialFragment(a);
      if (m > 0 && (m <= A || b)) {
        n.paused || this._trySkipBufferHole(b);
        return;
      }
    }
    const f = self.performance.now();
    if (r === null) {
      this.stalled = f;
      return;
    }
    const p = f - r;
    if (!o && p >= Woe && (this._reportStall(h), !this.media))
      return;
    const v = Mn.bufferInfo(n, a, i.maxBufferHole);
    this._tryFixBufferStall(v, p);
  }
  /**
   * Detects and attempts to fix known buffer stalling issues.
   * @param bufferInfo - The properties of the current buffer.
   * @param stalledDurationMs - The amount of time Hls.js has been stalling for.
   * @private
   */
  _tryFixBufferStall(e, t) {
    const {
      config: i,
      fragmentTracker: n,
      media: r
    } = this;
    if (r === null)
      return;
    const a = r.currentTime, o = n.getPartialFragment(a);
    o && (this._trySkipBufferHole(o) || !this.media) || (e.len > i.maxBufferHole || e.nextStart && e.nextStart - a < i.maxBufferHole) && t > i.highBufferWatchdogPeriod * 1e3 && (ze.warn("Trying to nudge playhead over buffer-hole"), this.stalled = null, this._tryNudgeBuffer());
  }
  /**
   * Triggers a BUFFER_STALLED_ERROR event, but only once per stall period.
   * @param bufferLen - The playhead distance from the end of the current buffer segment.
   * @private
   */
  _reportStall(e) {
    const {
      hls: t,
      media: i,
      stallReported: n
    } = this;
    if (!n && i) {
      this.stallReported = !0;
      const r = new Error(`Playback stalling at @${i.currentTime} due to low buffer (${JSON.stringify(e)})`);
      ze.warn(r.message), t.trigger(j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.BUFFER_STALLED_ERROR,
        fatal: !1,
        error: r,
        buffer: e.len
      });
    }
  }
  /**
   * Attempts to fix buffer stalls by jumping over known gaps caused by partial fragments
   * @param partial - The partial fragment found at the current time (where playback is stalling).
   * @private
   */
  _trySkipBufferHole(e) {
    const {
      config: t,
      hls: i,
      media: n
    } = this;
    if (n === null)
      return 0;
    const r = n.currentTime, a = Mn.bufferInfo(n, r, 0), o = r < a.start ? a.start : a.nextStart;
    if (o) {
      const l = a.len <= t.maxBufferHole, c = a.len > 0 && a.len < 1 && n.readyState < 3, h = o - r;
      if (h > 0 && (l || c)) {
        if (h > t.maxBufferHole) {
          const {
            fragmentTracker: d
          } = this;
          let f = !1;
          if (r === 0) {
            const p = d.getAppendedFrag(0, jt.MAIN);
            p && o < p.end && (f = !0);
          }
          if (!f) {
            const p = e || d.getAppendedFrag(r, jt.MAIN);
            if (p) {
              let v = !1, g = p.end;
              for (; g < o; ) {
                const m = d.getPartialFragment(g);
                if (m)
                  g += m.duration;
                else {
                  v = !0;
                  break;
                }
              }
              if (v)
                return 0;
            }
          }
        }
        const u = Math.max(o + joe, r + Xoe);
        if (ze.warn(`skipping hole, adjusting currentTime from ${r} to ${u}`), this.moved = !0, this.stalled = null, n.currentTime = u, e && !e.gap) {
          const d = new Error(`fragment loaded with buffer holes, seeking from ${r} to ${u}`);
          i.trigger(j.ERROR, {
            type: qt.MEDIA_ERROR,
            details: Xe.BUFFER_SEEK_OVER_HOLE,
            fatal: !1,
            error: d,
            reason: d.message,
            frag: e
          });
        }
        return u;
      }
    }
    return 0;
  }
  /**
   * Attempts to fix buffer stalls by advancing the mediaElement's current time by a small amount.
   * @private
   */
  _tryNudgeBuffer() {
    const {
      config: e,
      hls: t,
      media: i,
      nudgeRetry: n
    } = this;
    if (i === null)
      return;
    const r = i.currentTime;
    if (this.nudgeRetry++, n < e.nudgeMaxRetry) {
      const a = r + (n + 1) * e.nudgeOffset, o = new Error(`Nudging 'currentTime' from ${r} to ${a}`);
      ze.warn(o.message), i.currentTime = a, t.trigger(j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.BUFFER_NUDGE_ON_STALL,
        error: o,
        fatal: !1
      });
    } else {
      const a = new Error(`Playhead still not moving while enough data buffered @${r} after ${e.nudgeMaxRetry} nudges`);
      ze.error(a.message), t.trigger(j.ERROR, {
        type: qt.MEDIA_ERROR,
        details: Xe.BUFFER_STALLED_ERROR,
        error: a,
        fatal: !0
      });
    }
  }
}
const Yoe = 100;
class Zoe extends ew {
  constructor(e, t, i) {
    super(e, t, i, "[stream-controller]", jt.MAIN), this.audioCodecSwap = !1, this.gapController = null, this.level = -1, this._forceStartLoad = !1, this.altAudio = !1, this.audioOnly = !1, this.fragPlaying = null, this.onvplaying = null, this.onvseeked = null, this.fragLastKbps = 0, this.couldBacktrack = !1, this.backtrackFragment = null, this.audioCodecSwitch = !1, this.videoBuffer = null, this._registerListeners();
  }
  _registerListeners() {
    const {
      hls: e
    } = this;
    e.on(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.on(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.on(j.MANIFEST_LOADING, this.onManifestLoading, this), e.on(j.MANIFEST_PARSED, this.onManifestParsed, this), e.on(j.LEVEL_LOADING, this.onLevelLoading, this), e.on(j.LEVEL_LOADED, this.onLevelLoaded, this), e.on(j.FRAG_LOAD_EMERGENCY_ABORTED, this.onFragLoadEmergencyAborted, this), e.on(j.ERROR, this.onError, this), e.on(j.AUDIO_TRACK_SWITCHING, this.onAudioTrackSwitching, this), e.on(j.AUDIO_TRACK_SWITCHED, this.onAudioTrackSwitched, this), e.on(j.BUFFER_CREATED, this.onBufferCreated, this), e.on(j.BUFFER_FLUSHED, this.onBufferFlushed, this), e.on(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.on(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  _unregisterListeners() {
    const {
      hls: e
    } = this;
    e.off(j.MEDIA_ATTACHED, this.onMediaAttached, this), e.off(j.MEDIA_DETACHING, this.onMediaDetaching, this), e.off(j.MANIFEST_LOADING, this.onManifestLoading, this), e.off(j.MANIFEST_PARSED, this.onManifestParsed, this), e.off(j.LEVEL_LOADED, this.onLevelLoaded, this), e.off(j.FRAG_LOAD_EMERGENCY_ABORTED, this.onFragLoadEmergencyAborted, this), e.off(j.ERROR, this.onError, this), e.off(j.AUDIO_TRACK_SWITCHING, this.onAudioTrackSwitching, this), e.off(j.AUDIO_TRACK_SWITCHED, this.onAudioTrackSwitched, this), e.off(j.BUFFER_CREATED, this.onBufferCreated, this), e.off(j.BUFFER_FLUSHED, this.onBufferFlushed, this), e.off(j.LEVELS_UPDATED, this.onLevelsUpdated, this), e.off(j.FRAG_BUFFERED, this.onFragBuffered, this);
  }
  onHandlerDestroying() {
    this._unregisterListeners(), super.onHandlerDestroying();
  }
  startLoad(e) {
    if (this.levels) {
      const {
        lastCurrentTime: t,
        hls: i
      } = this;
      if (this.stopLoad(), this.setInterval(Yoe), this.level = -1, !this.startFragRequested) {
        let n = i.startLevel;
        n === -1 && (i.config.testBandwidth && this.levels.length > 1 ? (n = 0, this.bitrateTest = !0) : n = i.firstAutoLevel), i.nextLoadLevel = n, this.level = i.loadLevel, this.loadedmetadata = !1;
      }
      t > 0 && e === -1 && (this.log(`Override startPosition with lastCurrentTime @${t.toFixed(3)}`), e = t), this.state = pt.IDLE, this.nextLoadPosition = this.startPosition = this.lastCurrentTime = e, this.tick();
    } else
      this._forceStartLoad = !0, this.state = pt.STOPPED;
  }
  stopLoad() {
    this._forceStartLoad = !1, super.stopLoad();
  }
  doTick() {
    switch (this.state) {
      case pt.WAITING_LEVEL: {
        const {
          levels: t,
          level: i
        } = this, n = t == null ? void 0 : t[i], r = n == null ? void 0 : n.details;
        if (r && (!r.live || this.levelLastLoaded === n)) {
          if (this.waitForCdnTuneIn(r))
            break;
          this.state = pt.IDLE;
          break;
        } else if (this.hls.nextLoadLevel !== this.level) {
          this.state = pt.IDLE;
          break;
        }
        break;
      }
      case pt.FRAG_LOADING_WAITING_RETRY:
        {
          var e;
          const t = self.performance.now(), i = this.retryDate;
          if (!i || t >= i || (e = this.media) != null && e.seeking) {
            const {
              levels: n,
              level: r
            } = this, a = n == null ? void 0 : n[r];
            this.resetStartWhenNotLoaded(a || null), this.state = pt.IDLE;
          }
        }
        break;
    }
    this.state === pt.IDLE && this.doTickIdle(), this.onTickEnd();
  }
  onTickEnd() {
    super.onTickEnd(), this.checkBuffer(), this.checkFragmentChanged();
  }
  doTickIdle() {
    const {
      hls: e,
      levelLastLoaded: t,
      levels: i,
      media: n
    } = this;
    if (t === null || !n && (this.startFragRequested || !e.config.startFragPrefetch) || this.altAudio && this.audioOnly)
      return;
    const r = e.nextLoadLevel;
    if (!(i != null && i[r]))
      return;
    const a = i[r], o = this.getMainFwdBufferInfo();
    if (o === null)
      return;
    const l = this.getLevelDetails();
    if (l && this._streamEnded(o, l)) {
      const v = {};
      this.altAudio && (v.type = "video"), this.hls.trigger(j.BUFFER_EOS, v), this.state = pt.ENDED;
      return;
    }
    e.loadLevel !== r && e.manualLevel === -1 && this.log(`Adapting to level ${r} from level ${this.level}`), this.level = e.nextLoadLevel = r;
    const c = a.details;
    if (!c || this.state === pt.WAITING_LEVEL || c.live && this.levelLastLoaded !== a) {
      this.level = r, this.state = pt.WAITING_LEVEL;
      return;
    }
    const h = o.len, u = this.getMaxBufferLength(a.maxBitrate);
    if (h >= u)
      return;
    this.backtrackFragment && this.backtrackFragment.start > o.end && (this.backtrackFragment = null);
    const d = this.backtrackFragment ? this.backtrackFragment.start : o.end;
    let f = this.getNextFragment(d, c);
    if (this.couldBacktrack && !this.fragPrevious && f && f.sn !== "initSegment" && this.fragmentTracker.getState(f) !== Ts.OK) {
      var p;
      const g = ((p = this.backtrackFragment) != null ? p : f).sn - c.startSN, m = c.fragments[g - 1];
      m && f.cc === m.cc && (f = m, this.fragmentTracker.removeFragment(m));
    } else
      this.backtrackFragment && o.len && (this.backtrackFragment = null);
    if (f && this.isLoopLoading(f, d)) {
      if (!f.gap) {
        const g = this.audioOnly && !this.altAudio ? an.AUDIO : an.VIDEO, m = (g === an.VIDEO ? this.videoBuffer : this.mediaBuffer) || this.media;
        m && this.afterBufferFlushed(m, g, jt.MAIN);
      }
      f = this.getNextFragmentLoopLoading(f, c, o, jt.MAIN, u);
    }
    f && (f.initSegment && !f.initSegment.data && !this.bitrateTest && (f = f.initSegment), this.loadFragment(f, a, d));
  }
  loadFragment(e, t, i) {
    const n = this.fragmentTracker.getState(e);
    this.fragCurrent = e, n === Ts.NOT_LOADED || n === Ts.PARTIAL ? e.sn === "initSegment" ? this._loadInitSegment(e, t) : this.bitrateTest ? (this.log(`Fragment ${e.sn} of level ${e.level} is being downloaded to test bitrate and will not be buffered`), this._loadBitrateTestFrag(e, t)) : (this.startFragRequested = !0, super.loadFragment(e, t, i)) : this.clearTrackerIfNeeded(e);
  }
  getBufferedFrag(e) {
    return this.fragmentTracker.getBufferedFrag(e, jt.MAIN);
  }
  followingBufferedFrag(e) {
    return e ? this.getBufferedFrag(e.end + 0.5) : null;
  }
  /*
    on immediate level switch :
     - pause playback if playing
     - cancel any pending load request
     - and trigger a buffer flush
  */
  immediateLevelSwitch() {
    this.abortCurrentFrag(), this.flushMainBuffer(0, Number.POSITIVE_INFINITY);
  }
  /**
   * try to switch ASAP without breaking video playback:
   * in order to ensure smooth but quick level switching,
   * we need to find the next flushable buffer range
   * we should take into account new segment fetch time
   */
  nextLevelSwitch() {
    const {
      levels: e,
      media: t
    } = this;
    if (t != null && t.readyState) {
      let i;
      const n = this.getAppendedFrag(t.currentTime);
      n && n.start > 1 && this.flushMainBuffer(0, n.start - 1);
      const r = this.getLevelDetails();
      if (r != null && r.live) {
        const o = this.getMainFwdBufferInfo();
        if (!o || o.len < r.targetduration * 2)
          return;
      }
      if (!t.paused && e) {
        const o = this.hls.nextLoadLevel, l = e[o], c = this.fragLastKbps;
        c && this.fragCurrent ? i = this.fragCurrent.duration * l.maxBitrate / (1e3 * c) + 1 : i = 0;
      } else
        i = 0;
      const a = this.getBufferedFrag(t.currentTime + i);
      if (a) {
        const o = this.followingBufferedFrag(a);
        if (o) {
          this.abortCurrentFrag();
          const l = o.maxStartPTS ? o.maxStartPTS : o.start, c = o.duration, h = Math.max(a.end, l + Math.min(Math.max(c - this.config.maxFragLookUpTolerance, c * (this.couldBacktrack ? 0.5 : 0.125)), c * (this.couldBacktrack ? 0.75 : 0.25)));
          this.flushMainBuffer(h, Number.POSITIVE_INFINITY);
        }
      }
    }
  }
  abortCurrentFrag() {
    const e = this.fragCurrent;
    switch (this.fragCurrent = null, this.backtrackFragment = null, e && (e.abortRequests(), this.fragmentTracker.removeFragment(e)), this.state) {
      case pt.KEY_LOADING:
      case pt.FRAG_LOADING:
      case pt.FRAG_LOADING_WAITING_RETRY:
      case pt.PARSING:
      case pt.PARSED:
        this.state = pt.IDLE;
        break;
    }
    this.nextLoadPosition = this.getLoadPosition();
  }
  flushMainBuffer(e, t) {
    super.flushMainBuffer(e, t, this.altAudio ? "video" : null);
  }
  onMediaAttached(e, t) {
    super.onMediaAttached(e, t);
    const i = t.media;
    this.onvplaying = this.onMediaPlaying.bind(this), this.onvseeked = this.onMediaSeeked.bind(this), i.addEventListener("playing", this.onvplaying), i.addEventListener("seeked", this.onvseeked), this.gapController = new qoe(this.config, i, this.fragmentTracker, this.hls);
  }
  onMediaDetaching() {
    const {
      media: e
    } = this;
    e && this.onvplaying && this.onvseeked && (e.removeEventListener("playing", this.onvplaying), e.removeEventListener("seeked", this.onvseeked), this.onvplaying = this.onvseeked = null, this.videoBuffer = null), this.fragPlaying = null, this.gapController && (this.gapController.destroy(), this.gapController = null), super.onMediaDetaching();
  }
  onMediaPlaying() {
    this.tick();
  }
  onMediaSeeked() {
    const e = this.media, t = e ? e.currentTime : null;
    Bt(t) && this.log(`Media seeked to ${t.toFixed(3)}`);
    const i = this.getMainFwdBufferInfo();
    if (i === null || i.len === 0) {
      this.warn(`Main forward buffer length on "seeked" event ${i ? i.len : "empty"})`);
      return;
    }
    this.tick();
  }
  onManifestLoading() {
    this.log("Trigger BUFFER_RESET"), this.hls.trigger(j.BUFFER_RESET, void 0), this.fragmentTracker.removeAllFragments(), this.couldBacktrack = !1, this.startPosition = this.lastCurrentTime = this.fragLastKbps = 0, this.levels = this.fragPlaying = this.backtrackFragment = this.levelLastLoaded = null, this.altAudio = this.audioOnly = this.startFragRequested = !1;
  }
  onManifestParsed(e, t) {
    let i = !1, n = !1;
    t.levels.forEach((r) => {
      const a = r.audioCodec;
      a && (i = i || a.indexOf("mp4a.40.2") !== -1, n = n || a.indexOf("mp4a.40.5") !== -1);
    }), this.audioCodecSwitch = i && n && !Voe(), this.audioCodecSwitch && this.log("Both AAC/HE-AAC audio found in levels; declaring level codec as HE-AAC"), this.levels = t.levels, this.startFragRequested = !1;
  }
  onLevelLoading(e, t) {
    const {
      levels: i
    } = this;
    if (!i || this.state !== pt.IDLE)
      return;
    const n = i[t.level];
    (!n.details || n.details.live && this.levelLastLoaded !== n || this.waitForCdnTuneIn(n.details)) && (this.state = pt.WAITING_LEVEL);
  }
  onLevelLoaded(e, t) {
    var i;
    const {
      levels: n
    } = this, r = t.level, a = t.details, o = a.totalduration;
    if (!n) {
      this.warn(`Levels were reset while loading level ${r}`);
      return;
    }
    this.log(`Level ${r} loaded [${a.startSN},${a.endSN}]${a.lastPartSn ? `[part-${a.lastPartSn}-${a.lastPartIndex}]` : ""}, cc [${a.startCC}, ${a.endCC}] duration:${o}`);
    const l = n[r], c = this.fragCurrent;
    c && (this.state === pt.FRAG_LOADING || this.state === pt.FRAG_LOADING_WAITING_RETRY) && c.level !== t.level && c.loader && this.abortCurrentFrag();
    let h = 0;
    if (a.live || (i = l.details) != null && i.live) {
      var u;
      if (this.checkLiveUpdate(a), a.deltaUpdateFailed)
        return;
      h = this.alignPlaylists(a, l.details, (u = this.levelLastLoaded) == null ? void 0 : u.details);
    }
    if (l.details = a, this.levelLastLoaded = l, this.hls.trigger(j.LEVEL_UPDATED, {
      details: a,
      level: r
    }), this.state === pt.WAITING_LEVEL) {
      if (this.waitForCdnTuneIn(a))
        return;
      this.state = pt.IDLE;
    }
    this.startFragRequested ? a.live && this.synchronizeToLiveEdge(a) : this.setStartPosition(a, h), this.tick();
  }
  _handleFragmentLoadProgress(e) {
    var t;
    const {
      frag: i,
      part: n,
      payload: r
    } = e, {
      levels: a
    } = this;
    if (!a) {
      this.warn(`Levels were reset while fragment load was in progress. Fragment ${i.sn} of level ${i.level} will not be buffered`);
      return;
    }
    const o = a[i.level], l = o.details;
    if (!l) {
      this.warn(`Dropping fragment ${i.sn} of level ${i.level} after level details were reset`), this.fragmentTracker.removeFragment(i);
      return;
    }
    const c = o.videoCodec, h = l.PTSKnown || !l.live, u = (t = i.initSegment) == null ? void 0 : t.data, d = this._getAudioCodec(o), f = this.transmuxer = this.transmuxer || new rB(this.hls, jt.MAIN, this._handleTransmuxComplete.bind(this), this._handleTransmuxerFlush.bind(this)), p = n ? n.index : -1, v = p !== -1, g = new QS(i.level, i.sn, i.stats.chunkCount, r.byteLength, p, v), m = this.initPTS[i.cc];
    f.push(r, u, d, c, i, n, l.totalduration, h, g, m);
  }
  onAudioTrackSwitching(e, t) {
    const i = this.altAudio;
    if (!!!t.url) {
      if (this.mediaBuffer !== this.media) {
        this.log("Switching on main audio, use media.buffered to schedule main fragment loading"), this.mediaBuffer = this.media;
        const a = this.fragCurrent;
        a && (this.log("Switching to main audio track, cancel main fragment load"), a.abortRequests(), this.fragmentTracker.removeFragment(a)), this.resetTransmuxer(), this.resetLoadingState();
      } else
        this.audioOnly && this.resetTransmuxer();
      const r = this.hls;
      i && (r.trigger(j.BUFFER_FLUSHING, {
        startOffset: 0,
        endOffset: Number.POSITIVE_INFINITY,
        type: null
      }), this.fragmentTracker.removeAllFragments()), r.trigger(j.AUDIO_TRACK_SWITCHED, t);
    }
  }
  onAudioTrackSwitched(e, t) {
    const i = t.id, n = !!this.hls.audioTracks[i].url;
    if (n) {
      const r = this.videoBuffer;
      r && this.mediaBuffer !== r && (this.log("Switching on alternate audio, use video.buffered to schedule main fragment loading"), this.mediaBuffer = r);
    }
    this.altAudio = n, this.tick();
  }
  onBufferCreated(e, t) {
    const i = t.tracks;
    let n, r, a = !1;
    for (const o in i) {
      const l = i[o];
      if (l.id === "main") {
        if (r = o, n = l, o === "video") {
          const c = i[o];
          c && (this.videoBuffer = c.buffer);
        }
      } else
        a = !0;
    }
    a && n ? (this.log(`Alternate track found, use ${r}.buffered to schedule main fragment loading`), this.mediaBuffer = n.buffer) : this.mediaBuffer = this.media;
  }
  onFragBuffered(e, t) {
    const {
      frag: i,
      part: n
    } = t;
    if (i && i.type !== jt.MAIN)
      return;
    if (this.fragContextChanged(i)) {
      this.warn(`Fragment ${i.sn}${n ? " p: " + n.index : ""} of level ${i.level} finished buffering, but was aborted. state: ${this.state}`), this.state === pt.PARSED && (this.state = pt.IDLE);
      return;
    }
    const r = n ? n.stats : i.stats;
    this.fragLastKbps = Math.round(8 * r.total / (r.buffering.end - r.loading.first)), i.sn !== "initSegment" && (this.fragPrevious = i), this.fragBufferedComplete(i, n);
  }
  onError(e, t) {
    var i;
    if (t.fatal) {
      this.state = pt.ERROR;
      return;
    }
    switch (t.details) {
      case Xe.FRAG_GAP:
      case Xe.FRAG_PARSING_ERROR:
      case Xe.FRAG_DECRYPT_ERROR:
      case Xe.FRAG_LOAD_ERROR:
      case Xe.FRAG_LOAD_TIMEOUT:
      case Xe.KEY_LOAD_ERROR:
      case Xe.KEY_LOAD_TIMEOUT:
        this.onFragmentOrKeyLoadError(jt.MAIN, t);
        break;
      case Xe.LEVEL_LOAD_ERROR:
      case Xe.LEVEL_LOAD_TIMEOUT:
      case Xe.LEVEL_PARSING_ERROR:
        !t.levelRetry && this.state === pt.WAITING_LEVEL && ((i = t.context) == null ? void 0 : i.type) === ki.LEVEL && (this.state = pt.IDLE);
        break;
      case Xe.BUFFER_APPEND_ERROR:
      case Xe.BUFFER_FULL_ERROR:
        if (!t.parent || t.parent !== "main")
          return;
        if (t.details === Xe.BUFFER_APPEND_ERROR) {
          this.resetLoadingState();
          return;
        }
        this.reduceLengthAndFlushBuffer(t) && this.flushMainBuffer(0, Number.POSITIVE_INFINITY);
        break;
      case Xe.INTERNAL_EXCEPTION:
        this.recoverWorkerError(t);
        break;
    }
  }
  // Checks the health of the buffer and attempts to resolve playback stalls.
  checkBuffer() {
    const {
      media: e,
      gapController: t
    } = this;
    if (!(!e || !t || !e.readyState)) {
      if (this.loadedmetadata || !Mn.getBuffered(e).length) {
        const i = this.state !== pt.IDLE ? this.fragCurrent : null;
        t.poll(this.lastCurrentTime, i);
      }
      this.lastCurrentTime = e.currentTime;
    }
  }
  onFragLoadEmergencyAborted() {
    this.state = pt.IDLE, this.loadedmetadata || (this.startFragRequested = !1, this.nextLoadPosition = this.startPosition), this.tickImmediate();
  }
  onBufferFlushed(e, {
    type: t
  }) {
    if (t !== an.AUDIO || this.audioOnly && !this.altAudio) {
      const i = (t === an.VIDEO ? this.videoBuffer : this.mediaBuffer) || this.media;
      this.afterBufferFlushed(i, t, jt.MAIN), this.tick();
    }
  }
  onLevelsUpdated(e, t) {
    this.level > -1 && this.fragCurrent && (this.level = this.fragCurrent.level), this.levels = t.levels;
  }
  swapAudioCodec() {
    this.audioCodecSwap = !this.audioCodecSwap;
  }
  /**
   * Seeks to the set startPosition if not equal to the mediaElement's current time.
   */
  seekToStartPos() {
    const {
      media: e
    } = this;
    if (!e)
      return;
    const t = e.currentTime;
    let i = this.startPosition;
    if (i >= 0 && t < i) {
      if (e.seeking) {
        this.log(`could not seek to ${i}, already seeking at ${t}`);
        return;
      }
      const n = Mn.getBuffered(e), a = (n.length ? n.start(0) : 0) - i;
      a > 0 && (a < this.config.maxBufferHole || a < this.config.maxFragLookUpTolerance) && (this.log(`adjusting start position by ${a} to match buffer start`), i += a, this.startPosition = i), this.log(`seek to target start position ${i} from current time ${t}`), e.currentTime = i;
    }
  }
  _getAudioCodec(e) {
    let t = this.config.defaultAudioCodec || e.audioCodec;
    return this.audioCodecSwap && t && (this.log("Swapping audio codec"), t.indexOf("mp4a.40.5") !== -1 ? t = "mp4a.40.2" : t = "mp4a.40.5"), t;
  }
  _loadBitrateTestFrag(e, t) {
    e.bitrateTest = !0, this._doFragLoad(e, t).then((i) => {
      const {
        hls: n
      } = this;
      if (!i || this.fragContextChanged(e))
        return;
      t.fragmentError = 0, this.state = pt.IDLE, this.startFragRequested = !1, this.bitrateTest = !1;
      const r = e.stats;
      r.parsing.start = r.parsing.end = r.buffering.start = r.buffering.end = self.performance.now(), n.trigger(j.FRAG_LOADED, i), e.bitrateTest = !1;
    });
  }
  _handleTransmuxComplete(e) {
    var t;
    const i = "main", {
      hls: n
    } = this, {
      remuxResult: r,
      chunkMeta: a
    } = e, o = this.getCurrentContext(a);
    if (!o) {
      this.resetWhenMissingContext(a);
      return;
    }
    const {
      frag: l,
      part: c,
      level: h
    } = o, {
      video: u,
      text: d,
      id3: f,
      initSegment: p
    } = r, {
      details: v
    } = h, g = this.altAudio ? void 0 : r.audio;
    if (this.fragContextChanged(l)) {
      this.fragmentTracker.removeFragment(l);
      return;
    }
    if (this.state = pt.PARSING, p) {
      if (p != null && p.tracks) {
        const y = l.initSegment || l;
        this._bufferInitSegment(h, p.tracks, y, a), n.trigger(j.FRAG_PARSING_INIT_SEGMENT, {
          frag: y,
          id: i,
          tracks: p.tracks
        });
      }
      const m = p.initPTS, x = p.timescale;
      Bt(m) && (this.initPTS[l.cc] = {
        baseTime: m,
        timescale: x
      }, n.trigger(j.INIT_PTS_FOUND, {
        frag: l,
        id: i,
        initPTS: m,
        timescale: x
      }));
    }
    if (u && v && l.sn !== "initSegment") {
      const m = v.fragments[l.sn - 1 - v.startSN], x = l.sn === v.startSN, y = !m || l.cc > m.cc;
      if (r.independent !== !1) {
        const {
          startPTS: A,
          endPTS: b,
          startDTS: M,
          endDTS: w
        } = u;
        if (c)
          c.elementaryStreams[u.type] = {
            startPTS: A,
            endPTS: b,
            startDTS: M,
            endDTS: w
          };
        else if (u.firstKeyFrame && u.independent && a.id === 1 && !y && (this.couldBacktrack = !0), u.dropped && u.independent) {
          const C = this.getMainFwdBufferInfo(), T = (C ? C.end : this.getLoadPosition()) + this.config.maxBufferHole, E = u.firstKeyFramePTS ? u.firstKeyFramePTS : A;
          if (!x && T < E - this.config.maxBufferHole && !y) {
            this.backtrack(l);
            return;
          } else
            y && (l.gap = !0);
          l.setElementaryStreamInfo(u.type, l.start, b, l.start, w, !0);
        } else
          x && A > Vy && (l.gap = !0);
        l.setElementaryStreamInfo(u.type, A, b, M, w), this.backtrackFragment && (this.backtrackFragment = l), this.bufferFragmentData(u, l, c, a, x || y);
      } else if (x || y)
        l.gap = !0;
      else {
        this.backtrack(l);
        return;
      }
    }
    if (g) {
      const {
        startPTS: m,
        endPTS: x,
        startDTS: y,
        endDTS: A
      } = g;
      c && (c.elementaryStreams[an.AUDIO] = {
        startPTS: m,
        endPTS: x,
        startDTS: y,
        endDTS: A
      }), l.setElementaryStreamInfo(an.AUDIO, m, x, y, A), this.bufferFragmentData(g, l, c, a);
    }
    if (v && f != null && (t = f.samples) != null && t.length) {
      const m = {
        id: i,
        frag: l,
        details: v,
        samples: f.samples
      };
      n.trigger(j.FRAG_PARSING_METADATA, m);
    }
    if (v && d) {
      const m = {
        id: i,
        frag: l,
        details: v,
        samples: d.samples
      };
      n.trigger(j.FRAG_PARSING_USERDATA, m);
    }
  }
  _bufferInitSegment(e, t, i, n) {
    if (this.state !== pt.PARSING)
      return;
    this.audioOnly = !!t.audio && !t.video, this.altAudio && !this.audioOnly && delete t.audio;
    const {
      audio: r,
      video: a,
      audiovideo: o
    } = t;
    if (r) {
      let l = e.audioCodec;
      const c = navigator.userAgent.toLowerCase();
      this.audioCodecSwitch && (l && (l.indexOf("mp4a.40.5") !== -1 ? l = "mp4a.40.2" : l = "mp4a.40.5"), r.metadata.channelCount !== 1 && c.indexOf("firefox") === -1 && (l = "mp4a.40.5")), l && l.indexOf("mp4a.40.5") !== -1 && c.indexOf("android") !== -1 && r.container !== "audio/mpeg" && (l = "mp4a.40.2", this.log(`Android: force audio codec to ${l}`)), e.audioCodec && e.audioCodec !== l && this.log(`Swapping manifest audio codec "${e.audioCodec}" for "${l}"`), r.levelCodec = l, r.id = "main", this.log(`Init audio buffer, container:${r.container}, codecs[selected/level/parsed]=[${l || ""}/${e.audioCodec || ""}/${r.codec}]`);
    }
    a && (a.levelCodec = e.videoCodec, a.id = "main", this.log(`Init video buffer, container:${a.container}, codecs[level/parsed]=[${e.videoCodec || ""}/${a.codec}]`)), o && this.log(`Init audiovideo buffer, container:${o.container}, codecs[level/parsed]=[${e.codecs}/${o.codec}]`), this.hls.trigger(j.BUFFER_CODECS, t), Object.keys(t).forEach((l) => {
      const h = t[l].initSegment;
      h != null && h.byteLength && this.hls.trigger(j.BUFFER_APPENDING, {
        type: l,
        data: h,
        frag: i,
        part: null,
        chunkMeta: n,
        parent: i.type
      });
    }), this.tickImmediate();
  }
  getMainFwdBufferInfo() {
    return this.getFwdBufferInfo(this.mediaBuffer ? this.mediaBuffer : this.media, jt.MAIN);
  }
  backtrack(e) {
    this.couldBacktrack = !0, this.backtrackFragment = e, this.resetTransmuxer(), this.flushBufferGap(e), this.fragmentTracker.removeFragment(e), this.fragPrevious = null, this.nextLoadPosition = e.start, this.state = pt.IDLE;
  }
  checkFragmentChanged() {
    const e = this.media;
    let t = null;
    if (e && e.readyState > 1 && e.seeking === !1) {
      const i = e.currentTime;
      if (Mn.isBuffered(e, i) ? t = this.getAppendedFrag(i) : Mn.isBuffered(e, i + 0.1) && (t = this.getAppendedFrag(i + 0.1)), t) {
        this.backtrackFragment = null;
        const n = this.fragPlaying, r = t.level;
        (!n || t.sn !== n.sn || n.level !== r) && (this.fragPlaying = t, this.hls.trigger(j.FRAG_CHANGED, {
          frag: t
        }), (!n || n.level !== r) && this.hls.trigger(j.LEVEL_SWITCHED, {
          level: r
        }));
      }
    }
  }
  get nextLevel() {
    const e = this.nextBufferedFrag;
    return e ? e.level : -1;
  }
  get currentFrag() {
    const e = this.media;
    return e ? this.fragPlaying || this.getAppendedFrag(e.currentTime) : null;
  }
  get currentProgramDateTime() {
    const e = this.media;
    if (e) {
      const t = e.currentTime, i = this.currentFrag;
      if (i && Bt(t) && Bt(i.programDateTime)) {
        const n = i.programDateTime + (t - i.start) * 1e3;
        return new Date(n);
      }
    }
    return null;
  }
  get currentLevel() {
    const e = this.currentFrag;
    return e ? e.level : -1;
  }
  get nextBufferedFrag() {
    const e = this.currentFrag;
    return e ? this.followingBufferedFrag(e) : null;
  }
  get forceStartLoad() {
    return this._forceStartLoad;
  }
}
class Zo {
  /**
   * Get the video-dev/hls.js package version.
   */
  static get version() {
    return "1.5.8";
  }
  /**
   * Check if the required MediaSource Extensions are available.
   */
  static isMSESupported() {
    return EB();
  }
  /**
   * Check if MediaSource Extensions are available and isTypeSupported checks pass for any baseline codecs.
   */
  static isSupported() {
    return Hoe();
  }
  /**
   * Get the MediaSource global used for MSE playback (ManagedMediaSource, MediaSource, or WebKitMediaSource).
   */
  static getMediaSource() {
    return Nu();
  }
  static get Events() {
    return j;
  }
  static get ErrorTypes() {
    return qt;
  }
  static get ErrorDetails() {
    return Xe;
  }
  /**
   * Get the default configuration applied to new instances.
   */
  static get DefaultConfig() {
    return Zo.defaultConfig ? Zo.defaultConfig : SB;
  }
  /**
   * Replace the default configuration applied to new instances.
   */
  static set DefaultConfig(e) {
    Zo.defaultConfig = e;
  }
  /**
   * Creates an instance of an HLS client that can attach to exactly one `HTMLMediaElement`.
   * @param userConfig - Configuration options applied over `Hls.DefaultConfig`
   */
  constructor(e = {}) {
    this.config = void 0, this.userConfig = void 0, this.coreComponents = void 0, this.networkControllers = void 0, this.started = !1, this._emitter = new rw(), this._autoLevelCapping = -1, this._maxHdcpLevel = null, this.abrController = void 0, this.bufferController = void 0, this.capLevelController = void 0, this.latencyController = void 0, this.levelController = void 0, this.streamController = void 0, this.audioTrackController = void 0, this.subtitleTrackController = void 0, this.emeController = void 0, this.cmcdController = void 0, this._media = null, this.url = null, this.triggeringException = void 0, Gne(e.debug || !1, "Hls instance");
    const t = this.config = Uoe(Zo.DefaultConfig, e);
    this.userConfig = e, t.progressive && zoe(t);
    const {
      abrController: i,
      bufferController: n,
      capLevelController: r,
      errorController: a,
      fpsController: o
    } = t, l = new a(this), c = this.abrController = new i(this), h = this.bufferController = new n(this), u = this.capLevelController = new r(this), d = new o(this), f = new Nse(this), p = new kse(this), v = t.contentSteeringController, g = v ? new v(this) : null, m = this.levelController = new koe(this, g), x = new pre(this), y = new Goe(this.config), A = this.streamController = new Zoe(this, x, y);
    u.setStreamController(A), d.setStreamController(A);
    const b = [f, m, A];
    g && b.splice(1, 0, g), this.networkControllers = b;
    const M = [c, h, u, d, p, x];
    this.audioTrackController = this.createController(t.audioTrackController, b);
    const w = t.audioStreamController;
    w && b.push(new w(this, x, y)), this.subtitleTrackController = this.createController(t.subtitleTrackController, b);
    const C = t.subtitleStreamController;
    C && b.push(new C(this, x, y)), this.createController(t.timelineController, M), y.emeController = this.emeController = this.createController(t.emeController, M), this.cmcdController = this.createController(t.cmcdController, M), this.latencyController = this.createController(Gse, M), this.coreComponents = M, b.push(l);
    const T = l.onErrorOut;
    typeof T == "function" && this.on(j.ERROR, T, l);
  }
  createController(e, t) {
    if (e) {
      const i = new e(this);
      return t && t.push(i), i;
    }
    return null;
  }
  // Delegate the EventEmitter through the public API of Hls.js
  on(e, t, i = this) {
    this._emitter.on(e, t, i);
  }
  once(e, t, i = this) {
    this._emitter.once(e, t, i);
  }
  removeAllListeners(e) {
    this._emitter.removeAllListeners(e);
  }
  off(e, t, i = this, n) {
    this._emitter.off(e, t, i, n);
  }
  listeners(e) {
    return this._emitter.listeners(e);
  }
  emit(e, t, i) {
    return this._emitter.emit(e, t, i);
  }
  trigger(e, t) {
    if (this.config.debug)
      return this.emit(e, e, t);
    try {
      return this.emit(e, e, t);
    } catch (i) {
      if (ze.error("An internal error happened while handling event " + e + '. Error message: "' + i.message + '". Here is a stacktrace:', i), !this.triggeringException) {
        this.triggeringException = !0;
        const n = e === j.ERROR;
        this.trigger(j.ERROR, {
          type: qt.OTHER_ERROR,
          details: Xe.INTERNAL_EXCEPTION,
          fatal: n,
          event: e,
          error: i
        }), this.triggeringException = !1;
      }
    }
    return !1;
  }
  listenerCount(e) {
    return this._emitter.listenerCount(e);
  }
  /**
   * Dispose of the instance
   */
  destroy() {
    ze.log("destroy"), this.trigger(j.DESTROYING, void 0), this.detachMedia(), this.removeAllListeners(), this._autoLevelCapping = -1, this.url = null, this.networkControllers.forEach((t) => t.destroy()), this.networkControllers.length = 0, this.coreComponents.forEach((t) => t.destroy()), this.coreComponents.length = 0;
    const e = this.config;
    e.xhrSetup = e.fetchSetup = void 0, this.userConfig = null;
  }
  /**
   * Attaches Hls.js to a media element
   */
  attachMedia(e) {
    ze.log("attachMedia"), this._media = e, this.trigger(j.MEDIA_ATTACHING, {
      media: e
    });
  }
  /**
   * Detach Hls.js from the media
   */
  detachMedia() {
    ze.log("detachMedia"), this.trigger(j.MEDIA_DETACHING, void 0), this._media = null;
  }
  /**
   * Set the source URL. Can be relative or absolute.
   */
  loadSource(e) {
    this.stopLoad();
    const t = this.media, i = this.url, n = this.url = jS.buildAbsoluteURL(self.location.href, e, {
      alwaysNormalize: !0
    });
    this._autoLevelCapping = -1, this._maxHdcpLevel = null, ze.log(`loadSource:${n}`), t && i && (i !== n || this.bufferController.hasSourceTypes()) && (this.detachMedia(), this.attachMedia(t)), this.trigger(j.MANIFEST_LOADING, {
      url: e
    });
  }
  /**
   * Start loading data from the stream source.
   * Depending on default config, client starts loading automatically when a source is set.
   *
   * @param startPosition - Set the start position to stream from.
   * Defaults to -1 (None: starts from earliest point)
   */
  startLoad(e = -1) {
    ze.log(`startLoad(${e})`), this.started = !0, this.networkControllers.forEach((t) => {
      t.startLoad(e);
    });
  }
  /**
   * Stop loading of any stream data.
   */
  stopLoad() {
    ze.log("stopLoad"), this.started = !1, this.networkControllers.forEach((e) => {
      e.stopLoad();
    });
  }
  /**
   * Resumes stream controller segment loading if previously started.
   */
  resumeBuffering() {
    this.started && this.networkControllers.forEach((e) => {
      "fragmentLoader" in e && e.startLoad(-1);
    });
  }
  /**
   * Stops stream controller segment loading without changing 'started' state like stopLoad().
   * This allows for media buffering to be paused without interupting playlist loading.
   */
  pauseBuffering() {
    this.networkControllers.forEach((e) => {
      "fragmentLoader" in e && e.stopLoad();
    });
  }
  /**
   * Swap through possible audio codecs in the stream (for example to switch from stereo to 5.1)
   */
  swapAudioCodec() {
    ze.log("swapAudioCodec"), this.streamController.swapAudioCodec();
  }
  /**
   * When the media-element fails, this allows to detach and then re-attach it
   * as one call (convenience method).
   *
   * Automatic recovery of media-errors by this process is configurable.
   */
  recoverMediaError() {
    ze.log("recoverMediaError");
    const e = this._media;
    this.detachMedia(), e && this.attachMedia(e);
  }
  removeLevel(e) {
    this.levelController.removeLevel(e);
  }
  /**
   * @returns an array of levels (variants) sorted by HDCP-LEVEL, RESOLUTION (height), FRAME-RATE, CODECS, VIDEO-RANGE, and BANDWIDTH
   */
  get levels() {
    const e = this.levelController.levels;
    return e || [];
  }
  /**
   * Index of quality level (variant) currently played
   */
  get currentLevel() {
    return this.streamController.currentLevel;
  }
  /**
   * Set quality level index immediately. This will flush the current buffer to replace the quality asap. That means playback will interrupt at least shortly to re-buffer and re-sync eventually. Set to -1 for automatic level selection.
   */
  set currentLevel(e) {
    ze.log(`set currentLevel:${e}`), this.levelController.manualLevel = e, this.streamController.immediateLevelSwitch();
  }
  /**
   * Index of next quality level loaded as scheduled by stream controller.
   */
  get nextLevel() {
    return this.streamController.nextLevel;
  }
  /**
   * Set quality level index for next loaded data.
   * This will switch the video quality asap, without interrupting playback.
   * May abort current loading of data, and flush parts of buffer (outside currently played fragment region).
   * @param newLevel - Pass -1 for automatic level selection
   */
  set nextLevel(e) {
    ze.log(`set nextLevel:${e}`), this.levelController.manualLevel = e, this.streamController.nextLevelSwitch();
  }
  /**
   * Return the quality level of the currently or last (of none is loaded currently) segment
   */
  get loadLevel() {
    return this.levelController.level;
  }
  /**
   * Set quality level index for next loaded data in a conservative way.
   * This will switch the quality without flushing, but interrupt current loading.
   * Thus the moment when the quality switch will appear in effect will only be after the already existing buffer.
   * @param newLevel - Pass -1 for automatic level selection
   */
  set loadLevel(e) {
    ze.log(`set loadLevel:${e}`), this.levelController.manualLevel = e;
  }
  /**
   * get next quality level loaded
   */
  get nextLoadLevel() {
    return this.levelController.nextLoadLevel;
  }
  /**
   * Set quality level of next loaded segment in a fully "non-destructive" way.
   * Same as `loadLevel` but will wait for next switch (until current loading is done).
   */
  set nextLoadLevel(e) {
    this.levelController.nextLoadLevel = e;
  }
  /**
   * Return "first level": like a default level, if not set,
   * falls back to index of first level referenced in manifest
   */
  get firstLevel() {
    return Math.max(this.levelController.firstLevel, this.minAutoLevel);
  }
  /**
   * Sets "first-level", see getter.
   */
  set firstLevel(e) {
    ze.log(`set firstLevel:${e}`), this.levelController.firstLevel = e;
  }
  /**
   * Return the desired start level for the first fragment that will be loaded.
   * The default value of -1 indicates automatic start level selection.
   * Setting hls.nextAutoLevel without setting a startLevel will result in
   * the nextAutoLevel value being used for one fragment load.
   */
  get startLevel() {
    const e = this.levelController.startLevel;
    return e === -1 && this.abrController.forcedAutoLevel > -1 ? this.abrController.forcedAutoLevel : e;
  }
  /**
   * set  start level (level of first fragment that will be played back)
   * if not overrided by user, first level appearing in manifest will be used as start level
   * if -1 : automatic start level selection, playback will start from level matching download bandwidth
   * (determined from download of first segment)
   */
  set startLevel(e) {
    ze.log(`set startLevel:${e}`), e !== -1 && (e = Math.max(e, this.minAutoLevel)), this.levelController.startLevel = e;
  }
  /**
   * Whether level capping is enabled.
   * Default value is set via `config.capLevelToPlayerSize`.
   */
  get capLevelToPlayerSize() {
    return this.config.capLevelToPlayerSize;
  }
  /**
   * Enables or disables level capping. If disabled after previously enabled, `nextLevelSwitch` will be immediately called.
   */
  set capLevelToPlayerSize(e) {
    const t = !!e;
    t !== this.config.capLevelToPlayerSize && (t ? this.capLevelController.startCapping() : (this.capLevelController.stopCapping(), this.autoLevelCapping = -1, this.streamController.nextLevelSwitch()), this.config.capLevelToPlayerSize = t);
  }
  /**
   * Capping/max level value that should be used by automatic level selection algorithm (`ABRController`)
   */
  get autoLevelCapping() {
    return this._autoLevelCapping;
  }
  /**
   * Returns the current bandwidth estimate in bits per second, when available. Otherwise, `NaN` is returned.
   */
  get bandwidthEstimate() {
    const {
      bwEstimator: e
    } = this.abrController;
    return e ? e.getEstimate() : NaN;
  }
  set bandwidthEstimate(e) {
    this.abrController.resetEstimator(e);
  }
  /**
   * get time to first byte estimate
   * @type {number}
   */
  get ttfbEstimate() {
    const {
      bwEstimator: e
    } = this.abrController;
    return e ? e.getEstimateTTFB() : NaN;
  }
  /**
   * Capping/max level value that should be used by automatic level selection algorithm (`ABRController`)
   */
  set autoLevelCapping(e) {
    this._autoLevelCapping !== e && (ze.log(`set autoLevelCapping:${e}`), this._autoLevelCapping = e, this.levelController.checkMaxAutoUpdated());
  }
  get maxHdcpLevel() {
    return this._maxHdcpLevel;
  }
  set maxHdcpLevel(e) {
    Hse(e) && this._maxHdcpLevel !== e && (this._maxHdcpLevel = e, this.levelController.checkMaxAutoUpdated());
  }
  /**
   * True when automatic level selection enabled
   */
  get autoLevelEnabled() {
    return this.levelController.manualLevel === -1;
  }
  /**
   * Level set manually (if any)
   */
  get manualLevel() {
    return this.levelController.manualLevel;
  }
  /**
   * min level selectable in auto mode according to config.minAutoBitrate
   */
  get minAutoLevel() {
    const {
      levels: e,
      config: {
        minAutoBitrate: t
      }
    } = this;
    if (!e)
      return 0;
    const i = e.length;
    for (let n = 0; n < i; n++)
      if (e[n].maxBitrate >= t)
        return n;
    return 0;
  }
  /**
   * max level selectable in auto mode according to autoLevelCapping
   */
  get maxAutoLevel() {
    const {
      levels: e,
      autoLevelCapping: t,
      maxHdcpLevel: i
    } = this;
    let n;
    if (t === -1 && e != null && e.length ? n = e.length - 1 : n = t, i)
      for (let r = n; r--; ) {
        const a = e[r].attrs["HDCP-LEVEL"];
        if (a && a <= i)
          return r;
      }
    return n;
  }
  get firstAutoLevel() {
    return this.abrController.firstAutoLevel;
  }
  /**
   * next automatically selected quality level
   */
  get nextAutoLevel() {
    return this.abrController.nextAutoLevel;
  }
  /**
   * this setter is used to force next auto level.
   * this is useful to force a switch down in auto mode:
   * in case of load error on level N, hls.js can set nextAutoLevel to N-1 for example)
   * forced value is valid for one fragment. upon successful frag loading at forced level,
   * this value will be resetted to -1 by ABR controller.
   */
  set nextAutoLevel(e) {
    this.abrController.nextAutoLevel = e;
  }
  /**
   * get the datetime value relative to media.currentTime for the active level Program Date Time if present
   */
  get playingDate() {
    return this.streamController.currentProgramDateTime;
  }
  get mainForwardBufferInfo() {
    return this.streamController.getMainFwdBufferInfo();
  }
  /**
   * Find and select the best matching audio track, making a level switch when a Group change is necessary.
   * Updates `hls.config.audioPreference`. Returns the selected track, or null when no matching track is found.
   */
  setAudioOption(e) {
    var t;
    return (t = this.audioTrackController) == null ? void 0 : t.setAudioOption(e);
  }
  /**
   * Find and select the best matching subtitle track, making a level switch when a Group change is necessary.
   * Updates `hls.config.subtitlePreference`. Returns the selected track, or null when no matching track is found.
   */
  setSubtitleOption(e) {
    var t;
    return (t = this.subtitleTrackController) == null || t.setSubtitleOption(e), null;
  }
  /**
   * Get the complete list of audio tracks across all media groups
   */
  get allAudioTracks() {
    const e = this.audioTrackController;
    return e ? e.allAudioTracks : [];
  }
  /**
   * Get the list of selectable audio tracks
   */
  get audioTracks() {
    const e = this.audioTrackController;
    return e ? e.audioTracks : [];
  }
  /**
   * index of the selected audio track (index in audio track lists)
   */
  get audioTrack() {
    const e = this.audioTrackController;
    return e ? e.audioTrack : -1;
  }
  /**
   * selects an audio track, based on its index in audio track lists
   */
  set audioTrack(e) {
    const t = this.audioTrackController;
    t && (t.audioTrack = e);
  }
  /**
   * get the complete list of subtitle tracks across all media groups
   */
  get allSubtitleTracks() {
    const e = this.subtitleTrackController;
    return e ? e.allSubtitleTracks : [];
  }
  /**
   * get alternate subtitle tracks list from playlist
   */
  get subtitleTracks() {
    const e = this.subtitleTrackController;
    return e ? e.subtitleTracks : [];
  }
  /**
   * index of the selected subtitle track (index in subtitle track lists)
   */
  get subtitleTrack() {
    const e = this.subtitleTrackController;
    return e ? e.subtitleTrack : -1;
  }
  get media() {
    return this._media;
  }
  /**
   * select an subtitle track, based on its index in subtitle track lists
   */
  set subtitleTrack(e) {
    const t = this.subtitleTrackController;
    t && (t.subtitleTrack = e);
  }
  /**
   * Whether subtitle display is enabled or not
   */
  get subtitleDisplay() {
    const e = this.subtitleTrackController;
    return e ? e.subtitleDisplay : !1;
  }
  /**
   * Enable/disable subtitle display rendering
   */
  set subtitleDisplay(e) {
    const t = this.subtitleTrackController;
    t && (t.subtitleDisplay = e);
  }
  /**
   * get mode for Low-Latency HLS loading
   */
  get lowLatencyMode() {
    return this.config.lowLatencyMode;
  }
  /**
   * Enable/disable Low-Latency HLS part playlist and segment loading, and start live streams at playlist PART-HOLD-BACK rather than HOLD-BACK.
   */
  set lowLatencyMode(e) {
    this.config.lowLatencyMode = e;
  }
  /**
   * Position (in seconds) of live sync point (ie edge of live position minus safety delay defined by ```hls.config.liveSyncDuration```)
   * @returns null prior to loading live Playlist
   */
  get liveSyncPosition() {
    return this.latencyController.liveSyncPosition;
  }
  /**
   * Estimated position (in seconds) of live edge (ie edge of live playlist plus time sync playlist advanced)
   * @returns 0 before first playlist is loaded
   */
  get latency() {
    return this.latencyController.latency;
  }
  /**
   * maximum distance from the edge before the player seeks forward to ```hls.liveSyncPosition```
   * configured using ```liveMaxLatencyDurationCount``` (multiple of target duration) or ```liveMaxLatencyDuration```
   * @returns 0 before first playlist is loaded
   */
  get maxLatency() {
    return this.latencyController.maxLatency;
  }
  /**
   * target distance from the edge as calculated by the latency controller
   */
  get targetLatency() {
    return this.latencyController.targetLatency;
  }
  /**
   * the rate at which the edge of the current live playlist is advancing or 1 if there is none
   */
  get drift() {
    return this.latencyController.drift;
  }
  /**
   * set to true when startLoad is called before MANIFEST_PARSED event
   */
  get forceStartLoad() {
    return this.streamController.forceStartLoad;
  }
}
Zo.defaultConfig = void 0;
var TB = { exports: {} };
(function(s, e) {
  (function(i, n) {
    s.exports = n();
  })(self, function() {
    return (
      /******/
      function() {
        var t = {
          /***/
          "./node_modules/es6-promise/dist/es6-promise.js": (
            /*!******************************************************!*\
              !*** ./node_modules/es6-promise/dist/es6-promise.js ***!
              \******************************************************/
            /***/
            function(a, o, l) {
              /*!
               * @overview es6-promise - a tiny implementation of Promises/A+.
               * @copyright Copyright (c) 2014 Yehuda Katz, Tom Dale, Stefan Penner and contributors (Conversion to ES6 API by Jake Archibald)
               * @license   Licensed under MIT license
               *            See https://raw.githubusercontent.com/stefanpenner/es6-promise/master/LICENSE
               * @version   v4.2.8+1e68dce6
               */
              (function(c, h) {
                a.exports = h();
              })(this, function() {
                function c(q) {
                  var ae = typeof q;
                  return q !== null && (ae === "object" || ae === "function");
                }
                function h(q) {
                  return typeof q == "function";
                }
                var u = void 0;
                Array.isArray ? u = Array.isArray : u = function(q) {
                  return Object.prototype.toString.call(q) === "[object Array]";
                };
                var d = u, f = 0, p = void 0, v = void 0, g = function(ae, Ee) {
                  I[f] = ae, I[f + 1] = Ee, f += 2, f === 2 && (v ? v(N) : U());
                };
                function m(q) {
                  v = q;
                }
                function x(q) {
                  g = q;
                }
                var y = typeof window < "u" ? window : void 0, A = y || {}, b = A.MutationObserver || A.WebKitMutationObserver, M = typeof self > "u" && typeof process < "u" && {}.toString.call(process) === "[object process]", w = typeof Uint8ClampedArray < "u" && typeof importScripts < "u" && typeof MessageChannel < "u";
                function C() {
                  return function() {
                    return process.nextTick(N);
                  };
                }
                function T() {
                  return typeof p < "u" ? function() {
                    p(N);
                  } : L();
                }
                function E() {
                  var q = 0, ae = new b(N), Ee = document.createTextNode("");
                  return ae.observe(Ee, { characterData: !0 }), function() {
                    Ee.data = q = ++q % 2;
                  };
                }
                function P() {
                  var q = new MessageChannel();
                  return q.port1.onmessage = N, function() {
                    return q.port2.postMessage(0);
                  };
                }
                function L() {
                  var q = setTimeout;
                  return function() {
                    return q(N, 1);
                  };
                }
                var I = new Array(1e3);
                function N() {
                  for (var q = 0; q < f; q += 2) {
                    var ae = I[q], Ee = I[q + 1];
                    ae(Ee), I[q] = void 0, I[q + 1] = void 0;
                  }
                  f = 0;
                }
                function H() {
                  try {
                    var q = Function("return this")().require("vertx");
                    return p = q.runOnLoop || q.runOnContext, T();
                  } catch {
                    return L();
                  }
                }
                var U = void 0;
                M ? U = C() : b ? U = E() : w ? U = P() : y === void 0 ? U = H() : U = L();
                function W(q, ae) {
                  var Ee = this, Le = new this.constructor(te);
                  Le[K] === void 0 && G(Le);
                  var Ae = Ee._state;
                  if (Ae) {
                    var We = arguments[Ae - 1];
                    g(function() {
                      return Ie(Ae, Le, We, Ee._result);
                    });
                  } else
                    Ge(Ee, Le, q, ae);
                  return Le;
                }
                function X(q) {
                  var ae = this;
                  if (q && typeof q == "object" && q.constructor === ae)
                    return q;
                  var Ee = new ae(te);
                  return rt(Ee, q), Ee;
                }
                var K = Math.random().toString(36).substring(2);
                function te() {
                }
                var re = void 0, be = 1, Pe = 2;
                function J() {
                  return new TypeError("You cannot resolve a promise with itself");
                }
                function fe() {
                  return new TypeError("A promises callback cannot return that same promise.");
                }
                function Me(q, ae, Ee, Le) {
                  try {
                    q.call(ae, Ee, Le);
                  } catch (Ae) {
                    return Ae;
                  }
                }
                function ye(q, ae, Ee) {
                  g(function(Le) {
                    var Ae = !1, We = Me(Ee, ae, function(ot) {
                      Ae || (Ae = !0, ae !== ot ? rt(Le, ot) : k(Le, ot));
                    }, function(ot) {
                      Ae || (Ae = !0, Fe(Le, ot));
                    }, "Settle: " + (Le._label || " unknown promise"));
                    !Ae && We && (Ae = !0, Fe(Le, We));
                  }, q);
                }
                function Ye(q, ae) {
                  ae._state === be ? k(q, ae._result) : ae._state === Pe ? Fe(q, ae._result) : Ge(ae, void 0, function(Ee) {
                    return rt(q, Ee);
                  }, function(Ee) {
                    return Fe(q, Ee);
                  });
                }
                function Je(q, ae, Ee) {
                  ae.constructor === q.constructor && Ee === W && ae.constructor.resolve === X ? Ye(q, ae) : Ee === void 0 ? k(q, ae) : h(Ee) ? ye(q, ae, Ee) : k(q, ae);
                }
                function rt(q, ae) {
                  if (q === ae)
                    Fe(q, J());
                  else if (c(ae)) {
                    var Ee = void 0;
                    try {
                      Ee = ae.then;
                    } catch (Le) {
                      Fe(q, Le);
                      return;
                    }
                    Je(q, ae, Ee);
                  } else
                    k(q, ae);
                }
                function wt(q) {
                  q._onerror && q._onerror(q._result), qe(q);
                }
                function k(q, ae) {
                  q._state === re && (q._result = ae, q._state = be, q._subscribers.length !== 0 && g(qe, q));
                }
                function Fe(q, ae) {
                  q._state === re && (q._state = Pe, q._result = ae, g(wt, q));
                }
                function Ge(q, ae, Ee, Le) {
                  var Ae = q._subscribers, We = Ae.length;
                  q._onerror = null, Ae[We] = ae, Ae[We + be] = Ee, Ae[We + Pe] = Le, We === 0 && q._state && g(qe, q);
                }
                function qe(q) {
                  var ae = q._subscribers, Ee = q._state;
                  if (ae.length !== 0) {
                    for (var Le = void 0, Ae = void 0, We = q._result, ot = 0; ot < ae.length; ot += 3)
                      Le = ae[ot], Ae = ae[ot + Ee], Le ? Ie(Ee, Le, Ae, We) : Ae(We);
                    q._subscribers.length = 0;
                  }
                }
                function Ie(q, ae, Ee, Le) {
                  var Ae = h(Ee), We = void 0, ot = void 0, Ct = !0;
                  if (Ae) {
                    try {
                      We = Ee(Le);
                    } catch (F) {
                      Ct = !1, ot = F;
                    }
                    if (ae === We) {
                      Fe(ae, fe());
                      return;
                    }
                  } else
                    We = Le;
                  ae._state !== re || (Ae && Ct ? rt(ae, We) : Ct === !1 ? Fe(ae, ot) : q === be ? k(ae, We) : q === Pe && Fe(ae, We));
                }
                function vt(q, ae) {
                  try {
                    ae(function(Le) {
                      rt(q, Le);
                    }, function(Le) {
                      Fe(q, Le);
                    });
                  } catch (Ee) {
                    Fe(q, Ee);
                  }
                }
                var Ve = 0;
                function Ze() {
                  return Ve++;
                }
                function G(q) {
                  q[K] = Ve++, q._state = void 0, q._result = void 0, q._subscribers = [];
                }
                function D() {
                  return new Error("Array Methods must be provided an Array");
                }
                var ne = function() {
                  function q(ae, Ee) {
                    this._instanceConstructor = ae, this.promise = new ae(te), this.promise[K] || G(this.promise), d(Ee) ? (this.length = Ee.length, this._remaining = Ee.length, this._result = new Array(this.length), this.length === 0 ? k(this.promise, this._result) : (this.length = this.length || 0, this._enumerate(Ee), this._remaining === 0 && k(this.promise, this._result))) : Fe(this.promise, D());
                  }
                  return q.prototype._enumerate = function(Ee) {
                    for (var Le = 0; this._state === re && Le < Ee.length; Le++)
                      this._eachEntry(Ee[Le], Le);
                  }, q.prototype._eachEntry = function(Ee, Le) {
                    var Ae = this._instanceConstructor, We = Ae.resolve;
                    if (We === X) {
                      var ot = void 0, Ct = void 0, F = !1;
                      try {
                        ot = Ee.then;
                      } catch (Q) {
                        F = !0, Ct = Q;
                      }
                      if (ot === W && Ee._state !== re)
                        this._settledAt(Ee._state, Le, Ee._result);
                      else if (typeof ot != "function")
                        this._remaining--, this._result[Le] = Ee;
                      else if (Ae === xe) {
                        var ue = new Ae(te);
                        F ? Fe(ue, Ct) : Je(ue, Ee, ot), this._willSettleAt(ue, Le);
                      } else
                        this._willSettleAt(new Ae(function(Q) {
                          return Q(Ee);
                        }), Le);
                    } else
                      this._willSettleAt(We(Ee), Le);
                  }, q.prototype._settledAt = function(Ee, Le, Ae) {
                    var We = this.promise;
                    We._state === re && (this._remaining--, Ee === Pe ? Fe(We, Ae) : this._result[Le] = Ae), this._remaining === 0 && k(We, this._result);
                  }, q.prototype._willSettleAt = function(Ee, Le) {
                    var Ae = this;
                    Ge(Ee, void 0, function(We) {
                      return Ae._settledAt(be, Le, We);
                    }, function(We) {
                      return Ae._settledAt(Pe, Le, We);
                    });
                  }, q;
                }();
                function ve(q) {
                  return new ne(this, q).promise;
                }
                function Y(q) {
                  var ae = this;
                  return d(q) ? new ae(function(Ee, Le) {
                    for (var Ae = q.length, We = 0; We < Ae; We++)
                      ae.resolve(q[We]).then(Ee, Le);
                  }) : new ae(function(Ee, Le) {
                    return Le(new TypeError("You must pass an array to race."));
                  });
                }
                function ee(q) {
                  var ae = this, Ee = new ae(te);
                  return Fe(Ee, q), Ee;
                }
                function we() {
                  throw new TypeError("You must pass a resolver function as the first argument to the promise constructor");
                }
                function de() {
                  throw new TypeError("Failed to construct 'Promise': Please use the 'new' operator, this object constructor cannot be called as a function.");
                }
                var xe = function() {
                  function q(ae) {
                    this[K] = Ze(), this._result = this._state = void 0, this._subscribers = [], te !== ae && (typeof ae != "function" && we(), this instanceof q ? vt(this, ae) : de());
                  }
                  return q.prototype.catch = function(Ee) {
                    return this.then(null, Ee);
                  }, q.prototype.finally = function(Ee) {
                    var Le = this, Ae = Le.constructor;
                    return h(Ee) ? Le.then(function(We) {
                      return Ae.resolve(Ee()).then(function() {
                        return We;
                      });
                    }, function(We) {
                      return Ae.resolve(Ee()).then(function() {
                        throw We;
                      });
                    }) : Le.then(Ee, Ee);
                  }, q;
                }();
                xe.prototype.then = W, xe.all = ve, xe.race = Y, xe.resolve = X, xe.reject = ee, xe._setScheduler = m, xe._setAsap = x, xe._asap = g;
                function it() {
                  var q = void 0;
                  if (typeof l.g < "u")
                    q = l.g;
                  else if (typeof self < "u")
                    q = self;
                  else
                    try {
                      q = Function("return this")();
                    } catch {
                      throw new Error("polyfill failed because global object is unavailable in this environment");
                    }
                  var ae = q.Promise;
                  if (ae) {
                    var Ee = null;
                    try {
                      Ee = Object.prototype.toString.call(ae.resolve());
                    } catch {
                    }
                    if (Ee === "[object Promise]" && !ae.cast)
                      return;
                  }
                  q.Promise = xe;
                }
                return xe.polyfill = it, xe.Promise = xe, xe;
              });
            }
          ),
          /***/
          "./node_modules/events/events.js": (
            /*!***************************************!*\
              !*** ./node_modules/events/events.js ***!
              \***************************************/
            /***/
            function(a) {
              var o = typeof Reflect == "object" ? Reflect : null, l = o && typeof o.apply == "function" ? o.apply : function(L, I, N) {
                return Function.prototype.apply.call(L, I, N);
              }, c;
              o && typeof o.ownKeys == "function" ? c = o.ownKeys : Object.getOwnPropertySymbols ? c = function(L) {
                return Object.getOwnPropertyNames(L).concat(Object.getOwnPropertySymbols(L));
              } : c = function(L) {
                return Object.getOwnPropertyNames(L);
              };
              function h(P) {
                console && console.warn && console.warn(P);
              }
              var u = Number.isNaN || function(L) {
                return L !== L;
              };
              function d() {
                d.init.call(this);
              }
              a.exports = d, a.exports.once = C, d.EventEmitter = d, d.prototype._events = void 0, d.prototype._eventsCount = 0, d.prototype._maxListeners = void 0;
              var f = 10;
              function p(P) {
                if (typeof P != "function")
                  throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof P);
              }
              Object.defineProperty(d, "defaultMaxListeners", {
                enumerable: !0,
                get: function() {
                  return f;
                },
                set: function(P) {
                  if (typeof P != "number" || P < 0 || u(P))
                    throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + P + ".");
                  f = P;
                }
              }), d.init = function() {
                (this._events === void 0 || this._events === Object.getPrototypeOf(this)._events) && (this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0), this._maxListeners = this._maxListeners || void 0;
              }, d.prototype.setMaxListeners = function(L) {
                if (typeof L != "number" || L < 0 || u(L))
                  throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + L + ".");
                return this._maxListeners = L, this;
              };
              function v(P) {
                return P._maxListeners === void 0 ? d.defaultMaxListeners : P._maxListeners;
              }
              d.prototype.getMaxListeners = function() {
                return v(this);
              }, d.prototype.emit = function(L) {
                for (var I = [], N = 1; N < arguments.length; N++)
                  I.push(arguments[N]);
                var H = L === "error", U = this._events;
                if (U !== void 0)
                  H = H && U.error === void 0;
                else if (!H)
                  return !1;
                if (H) {
                  var W;
                  if (I.length > 0 && (W = I[0]), W instanceof Error)
                    throw W;
                  var X = new Error("Unhandled error." + (W ? " (" + W.message + ")" : ""));
                  throw X.context = W, X;
                }
                var K = U[L];
                if (K === void 0)
                  return !1;
                if (typeof K == "function")
                  l(K, this, I);
                else
                  for (var te = K.length, re = b(K, te), N = 0; N < te; ++N)
                    l(re[N], this, I);
                return !0;
              };
              function g(P, L, I, N) {
                var H, U, W;
                if (p(I), U = P._events, U === void 0 ? (U = P._events = /* @__PURE__ */ Object.create(null), P._eventsCount = 0) : (U.newListener !== void 0 && (P.emit("newListener", L, I.listener ? I.listener : I), U = P._events), W = U[L]), W === void 0)
                  W = U[L] = I, ++P._eventsCount;
                else if (typeof W == "function" ? W = U[L] = N ? [I, W] : [W, I] : N ? W.unshift(I) : W.push(I), H = v(P), H > 0 && W.length > H && !W.warned) {
                  W.warned = !0;
                  var X = new Error("Possible EventEmitter memory leak detected. " + W.length + " " + String(L) + " listeners added. Use emitter.setMaxListeners() to increase limit");
                  X.name = "MaxListenersExceededWarning", X.emitter = P, X.type = L, X.count = W.length, h(X);
                }
                return P;
              }
              d.prototype.addListener = function(L, I) {
                return g(this, L, I, !1);
              }, d.prototype.on = d.prototype.addListener, d.prototype.prependListener = function(L, I) {
                return g(this, L, I, !0);
              };
              function m() {
                if (!this.fired)
                  return this.target.removeListener(this.type, this.wrapFn), this.fired = !0, arguments.length === 0 ? this.listener.call(this.target) : this.listener.apply(this.target, arguments);
              }
              function x(P, L, I) {
                var N = { fired: !1, wrapFn: void 0, target: P, type: L, listener: I }, H = m.bind(N);
                return H.listener = I, N.wrapFn = H, H;
              }
              d.prototype.once = function(L, I) {
                return p(I), this.on(L, x(this, L, I)), this;
              }, d.prototype.prependOnceListener = function(L, I) {
                return p(I), this.prependListener(L, x(this, L, I)), this;
              }, d.prototype.removeListener = function(L, I) {
                var N, H, U, W, X;
                if (p(I), H = this._events, H === void 0)
                  return this;
                if (N = H[L], N === void 0)
                  return this;
                if (N === I || N.listener === I)
                  --this._eventsCount === 0 ? this._events = /* @__PURE__ */ Object.create(null) : (delete H[L], H.removeListener && this.emit("removeListener", L, N.listener || I));
                else if (typeof N != "function") {
                  for (U = -1, W = N.length - 1; W >= 0; W--)
                    if (N[W] === I || N[W].listener === I) {
                      X = N[W].listener, U = W;
                      break;
                    }
                  if (U < 0)
                    return this;
                  U === 0 ? N.shift() : M(N, U), N.length === 1 && (H[L] = N[0]), H.removeListener !== void 0 && this.emit("removeListener", L, X || I);
                }
                return this;
              }, d.prototype.off = d.prototype.removeListener, d.prototype.removeAllListeners = function(L) {
                var I, N, H;
                if (N = this._events, N === void 0)
                  return this;
                if (N.removeListener === void 0)
                  return arguments.length === 0 ? (this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0) : N[L] !== void 0 && (--this._eventsCount === 0 ? this._events = /* @__PURE__ */ Object.create(null) : delete N[L]), this;
                if (arguments.length === 0) {
                  var U = Object.keys(N), W;
                  for (H = 0; H < U.length; ++H)
                    W = U[H], W !== "removeListener" && this.removeAllListeners(W);
                  return this.removeAllListeners("removeListener"), this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0, this;
                }
                if (I = N[L], typeof I == "function")
                  this.removeListener(L, I);
                else if (I !== void 0)
                  for (H = I.length - 1; H >= 0; H--)
                    this.removeListener(L, I[H]);
                return this;
              };
              function y(P, L, I) {
                var N = P._events;
                if (N === void 0)
                  return [];
                var H = N[L];
                return H === void 0 ? [] : typeof H == "function" ? I ? [H.listener || H] : [H] : I ? w(H) : b(H, H.length);
              }
              d.prototype.listeners = function(L) {
                return y(this, L, !0);
              }, d.prototype.rawListeners = function(L) {
                return y(this, L, !1);
              }, d.listenerCount = function(P, L) {
                return typeof P.listenerCount == "function" ? P.listenerCount(L) : A.call(P, L);
              }, d.prototype.listenerCount = A;
              function A(P) {
                var L = this._events;
                if (L !== void 0) {
                  var I = L[P];
                  if (typeof I == "function")
                    return 1;
                  if (I !== void 0)
                    return I.length;
                }
                return 0;
              }
              d.prototype.eventNames = function() {
                return this._eventsCount > 0 ? c(this._events) : [];
              };
              function b(P, L) {
                for (var I = new Array(L), N = 0; N < L; ++N)
                  I[N] = P[N];
                return I;
              }
              function M(P, L) {
                for (; L + 1 < P.length; L++)
                  P[L] = P[L + 1];
                P.pop();
              }
              function w(P) {
                for (var L = new Array(P.length), I = 0; I < L.length; ++I)
                  L[I] = P[I].listener || P[I];
                return L;
              }
              function C(P, L) {
                return new Promise(function(I, N) {
                  function H(W) {
                    P.removeListener(L, U), N(W);
                  }
                  function U() {
                    typeof P.removeListener == "function" && P.removeListener("error", H), I([].slice.call(arguments));
                  }
                  E(P, L, U, { once: !0 }), L !== "error" && T(P, H, { once: !0 });
                });
              }
              function T(P, L, I) {
                typeof P.on == "function" && E(P, "error", L, I);
              }
              function E(P, L, I, N) {
                if (typeof P.on == "function")
                  N.once ? P.once(L, I) : P.on(L, I);
                else if (typeof P.addEventListener == "function")
                  P.addEventListener(L, function H(U) {
                    N.once && P.removeEventListener(L, H), I(U);
                  });
                else
                  throw new TypeError('The "emitter" argument must be of type EventEmitter. Received type ' + typeof P);
              }
            }
          ),
          /***/
          "./node_modules/webworkify-webpack/index.js": (
            /*!**************************************************!*\
              !*** ./node_modules/webworkify-webpack/index.js ***!
              \**************************************************/
            /***/
            function(a, o, l) {
              function c(m) {
                var x = {};
                function y(b) {
                  if (x[b])
                    return x[b].exports;
                  var M = x[b] = {
                    /******/
                    i: b,
                    /******/
                    l: !1,
                    /******/
                    exports: {}
                    /******/
                  };
                  return m[b].call(M.exports, M, M.exports, y), M.l = !0, M.exports;
                }
                y.m = m, y.c = x, y.i = function(b) {
                  return b;
                }, y.d = function(b, M, w) {
                  y.o(b, M) || Object.defineProperty(b, M, {
                    /******/
                    configurable: !1,
                    /******/
                    enumerable: !0,
                    /******/
                    get: w
                    /******/
                  });
                }, y.r = function(b) {
                  Object.defineProperty(b, "__esModule", { value: !0 });
                }, y.n = function(b) {
                  var M = b && b.__esModule ? (
                    /******/
                    function() {
                      return b.default;
                    }
                  ) : (
                    /******/
                    function() {
                      return b;
                    }
                  );
                  return y.d(M, "a", M), M;
                }, y.o = function(b, M) {
                  return Object.prototype.hasOwnProperty.call(b, M);
                }, y.p = "/", y.oe = function(b) {
                  throw console.error(b), b;
                };
                var A = y(y.s = ENTRY_MODULE);
                return A.default || A;
              }
              var h = "[\\.|\\-|\\+|\\w|/|@]+", u = "\\(\\s*(/\\*.*?\\*/)?\\s*.*?(" + h + ").*?\\)";
              function d(m) {
                return (m + "").replace(/[.?*+^$[\]\\(){}|-]/g, "\\$&");
              }
              function f(m) {
                return !isNaN(1 * m);
              }
              function p(m, x, y) {
                var A = {};
                A[y] = [];
                var b = x.toString(), M = b.match(/^function\s?\w*\(\w+,\s*\w+,\s*(\w+)\)/);
                if (!M)
                  return A;
                for (var w = M[1], C = new RegExp("(\\\\n|\\W)" + d(w) + u, "g"), T; T = C.exec(b); )
                  T[3] !== "dll-reference" && A[y].push(T[3]);
                for (C = new RegExp("\\(" + d(w) + '\\("(dll-reference\\s(' + h + '))"\\)\\)' + u, "g"); T = C.exec(b); )
                  m[T[2]] || (A[y].push(T[1]), m[T[2]] = l(T[1]).m), A[T[2]] = A[T[2]] || [], A[T[2]].push(T[4]);
                for (var E = Object.keys(A), P = 0; P < E.length; P++)
                  for (var L = 0; L < A[E[P]].length; L++)
                    f(A[E[P]][L]) && (A[E[P]][L] = 1 * A[E[P]][L]);
                return A;
              }
              function v(m) {
                var x = Object.keys(m);
                return x.reduce(function(y, A) {
                  return y || m[A].length > 0;
                }, !1);
              }
              function g(m, x) {
                for (var y = {
                  main: [x]
                }, A = {
                  main: []
                }, b = {
                  main: {}
                }; v(y); )
                  for (var M = Object.keys(y), w = 0; w < M.length; w++) {
                    var C = M[w], T = y[C], E = T.pop();
                    if (b[C] = b[C] || {}, !(b[C][E] || !m[C][E])) {
                      b[C][E] = !0, A[C] = A[C] || [], A[C].push(E);
                      for (var P = p(m, m[C][E], C), L = Object.keys(P), I = 0; I < L.length; I++)
                        y[L[I]] = y[L[I]] || [], y[L[I]] = y[L[I]].concat(P[L[I]]);
                    }
                  }
                return A;
              }
              a.exports = function(m, x) {
                x = x || {};
                var y = {
                  main: l.m
                }, A = x.all ? { main: Object.keys(y.main) } : g(y, m), b = "";
                Object.keys(A).filter(function(E) {
                  return E !== "main";
                }).forEach(function(E) {
                  for (var P = 0; A[E][P]; )
                    P++;
                  A[E].push(P), y[E][P] = "(function(module, exports, __webpack_require__) { module.exports = __webpack_require__; })", b = b + "var " + E + " = (" + c.toString().replace("ENTRY_MODULE", JSON.stringify(P)) + ")({" + A[E].map(function(L) {
                    return "" + JSON.stringify(L) + ": " + y[E][L].toString();
                  }).join(",") + `});
`;
                }), b = b + "new ((" + c.toString().replace("ENTRY_MODULE", JSON.stringify(m)) + ")({" + A.main.map(function(E) {
                  return "" + JSON.stringify(E) + ": " + y.main[E].toString();
                }).join(",") + "}))(self);";
                var M = new window.Blob([b], { type: "text/javascript" });
                if (x.bare)
                  return M;
                var w = window.URL || window.webkitURL || window.mozURL || window.msURL, C = w.createObjectURL(M), T = new window.Worker(C);
                return T.objectURL = C, T;
              };
            }
          ),
          /***/
          "./src/config.js": (
            /*!***********************!*\
              !*** ./src/config.js ***!
              \***********************/
            /***/
            function(a, o, l) {
              l.r(o), l.d(o, {
                /* harmony export */
                defaultConfig: function() {
                  return (
                    /* binding */
                    c
                  );
                },
                /* harmony export */
                createDefaultConfig: function() {
                  return (
                    /* binding */
                    h
                  );
                }
                /* harmony export */
              });
              var c = {
                enableWorker: !1,
                enableStashBuffer: !0,
                stashInitialSize: void 0,
                isLive: !1,
                lazyLoad: !0,
                lazyLoadMaxDuration: 3 * 60,
                lazyLoadRecoverDuration: 30,
                deferLoadAfterSourceOpen: !0,
                // autoCleanupSourceBuffer: default as false, leave unspecified
                autoCleanupMaxBackwardDuration: 3 * 60,
                autoCleanupMinBackwardDuration: 2 * 60,
                statisticsInfoReportInterval: 600,
                fixAudioTimestampGap: !0,
                accurateSeek: !1,
                seekType: "range",
                seekParamStart: "bstart",
                seekParamEnd: "bend",
                rangeLoadZeroStart: !1,
                customSeekHandler: void 0,
                reuseRedirectedURL: !1,
                // referrerPolicy: leave as unspecified
                headers: void 0,
                customLoader: void 0
              };
              function h() {
                return Object.assign({}, c);
              }
            }
          ),
          /***/
          "./src/core/features.js": (
            /*!******************************!*\
              !*** ./src/core/features.js ***!
              \******************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../io/io-controller.js */
                "./src/io/io-controller.js"
              ), h = l(
                /*! ../config.js */
                "./src/config.js"
              ), u = (
                /** @class */
                function() {
                  function d() {
                  }
                  return d.supportMSEH264Playback = function() {
                    return window.MediaSource && window.MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');
                  }, d.supportNetworkStreamIO = function() {
                    var f = new c.default({}, (0, h.createDefaultConfig)()), p = f.loaderType;
                    return f.destroy(), p == "fetch-stream-loader" || p == "xhr-moz-chunked-loader";
                  }, d.getNetworkLoaderTypeName = function() {
                    var f = new c.default({}, (0, h.createDefaultConfig)()), p = f.loaderType;
                    return f.destroy(), p;
                  }, d.supportNativeMediaPlayback = function(f) {
                    d.videoElement == null && (d.videoElement = window.document.createElement("video"));
                    var p = d.videoElement.canPlayType(f);
                    return p === "probably" || p == "maybe";
                  }, d.getFeatureList = function() {
                    var f = {
                      mseFlvPlayback: !1,
                      mseLiveFlvPlayback: !1,
                      networkStreamIO: !1,
                      networkLoaderName: "",
                      nativeMP4H264Playback: !1,
                      nativeWebmVP8Playback: !1,
                      nativeWebmVP9Playback: !1
                    };
                    return f.mseFlvPlayback = d.supportMSEH264Playback(), f.networkStreamIO = d.supportNetworkStreamIO(), f.networkLoaderName = d.getNetworkLoaderTypeName(), f.mseLiveFlvPlayback = f.mseFlvPlayback && f.networkStreamIO, f.nativeMP4H264Playback = d.supportNativeMediaPlayback('video/mp4; codecs="avc1.42001E, mp4a.40.2"'), f.nativeWebmVP8Playback = d.supportNativeMediaPlayback('video/webm; codecs="vp8.0, vorbis"'), f.nativeWebmVP9Playback = d.supportNativeMediaPlayback('video/webm; codecs="vp9"'), f;
                  }, d;
                }()
              );
              o.default = u;
            }
          ),
          /***/
          "./src/core/media-info.js": (
            /*!********************************!*\
              !*** ./src/core/media-info.js ***!
              \********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h() {
                    this.mimeType = null, this.duration = null, this.hasAudio = null, this.hasVideo = null, this.audioCodec = null, this.videoCodec = null, this.audioDataRate = null, this.videoDataRate = null, this.audioSampleRate = null, this.audioChannelCount = null, this.width = null, this.height = null, this.fps = null, this.profile = null, this.level = null, this.refFrames = null, this.chromaFormat = null, this.sarNum = null, this.sarDen = null, this.metadata = null, this.segments = null, this.segmentCount = null, this.hasKeyframesIndex = null, this.keyframesIndex = null;
                  }
                  return h.prototype.isComplete = function() {
                    var u = this.hasAudio === !1 || this.hasAudio === !0 && this.audioCodec != null && this.audioSampleRate != null && this.audioChannelCount != null, d = this.hasVideo === !1 || this.hasVideo === !0 && this.videoCodec != null && this.width != null && this.height != null && this.fps != null && this.profile != null && this.level != null && this.refFrames != null && this.chromaFormat != null && this.sarNum != null && this.sarDen != null;
                    return this.mimeType != null && this.duration != null && this.metadata != null && this.hasKeyframesIndex != null && u && d;
                  }, h.prototype.isSeekable = function() {
                    return this.hasKeyframesIndex === !0;
                  }, h.prototype.getNearestKeyframe = function(u) {
                    if (this.keyframesIndex == null)
                      return null;
                    var d = this.keyframesIndex, f = this._search(d.times, u);
                    return {
                      index: f,
                      milliseconds: d.times[f],
                      fileposition: d.filepositions[f]
                    };
                  }, h.prototype._search = function(u, d) {
                    var f = 0, p = u.length - 1, v = 0, g = 0, m = p;
                    for (d < u[0] && (f = 0, g = m + 1); g <= m; )
                      if (v = g + Math.floor((m - g) / 2), v === p || d >= u[v] && d < u[v + 1]) {
                        f = v;
                        break;
                      } else
                        u[v] < d ? g = v + 1 : m = v - 1;
                    return f;
                  }, h;
                }()
              );
              o.default = c;
            }
          ),
          /***/
          "./src/core/media-segment-info.js": (
            /*!****************************************!*\
              !*** ./src/core/media-segment-info.js ***!
              \****************************************/
            /***/
            function(a, o, l) {
              l.r(o), l.d(o, {
                /* harmony export */
                SampleInfo: function() {
                  return (
                    /* binding */
                    c
                  );
                },
                /* harmony export */
                MediaSegmentInfo: function() {
                  return (
                    /* binding */
                    h
                  );
                },
                /* harmony export */
                IDRSampleList: function() {
                  return (
                    /* binding */
                    u
                  );
                },
                /* harmony export */
                MediaSegmentInfoList: function() {
                  return (
                    /* binding */
                    d
                  );
                }
                /* harmony export */
              });
              var c = (
                /** @class */
                function() {
                  function f(p, v, g, m, x) {
                    this.dts = p, this.pts = v, this.duration = g, this.originalDts = m, this.isSyncPoint = x, this.fileposition = null;
                  }
                  return f;
                }()
              ), h = (
                /** @class */
                function() {
                  function f() {
                    this.beginDts = 0, this.endDts = 0, this.beginPts = 0, this.endPts = 0, this.originalBeginDts = 0, this.originalEndDts = 0, this.syncPoints = [], this.firstSample = null, this.lastSample = null;
                  }
                  return f.prototype.appendSyncPoint = function(p) {
                    p.isSyncPoint = !0, this.syncPoints.push(p);
                  }, f;
                }()
              ), u = (
                /** @class */
                function() {
                  function f() {
                    this._list = [];
                  }
                  return f.prototype.clear = function() {
                    this._list = [];
                  }, f.prototype.appendArray = function(p) {
                    var v = this._list;
                    p.length !== 0 && (v.length > 0 && p[0].originalDts < v[v.length - 1].originalDts && this.clear(), Array.prototype.push.apply(v, p));
                  }, f.prototype.getLastSyncPointBeforeDts = function(p) {
                    if (this._list.length == 0)
                      return null;
                    var v = this._list, g = 0, m = v.length - 1, x = 0, y = 0, A = m;
                    for (p < v[0].dts && (g = 0, y = A + 1); y <= A; )
                      if (x = y + Math.floor((A - y) / 2), x === m || p >= v[x].dts && p < v[x + 1].dts) {
                        g = x;
                        break;
                      } else
                        v[x].dts < p ? y = x + 1 : A = x - 1;
                    return this._list[g];
                  }, f;
                }()
              ), d = (
                /** @class */
                function() {
                  function f(p) {
                    this._type = p, this._list = [], this._lastAppendLocation = -1;
                  }
                  return Object.defineProperty(f.prototype, "type", {
                    get: function() {
                      return this._type;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "length", {
                    get: function() {
                      return this._list.length;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), f.prototype.isEmpty = function() {
                    return this._list.length === 0;
                  }, f.prototype.clear = function() {
                    this._list = [], this._lastAppendLocation = -1;
                  }, f.prototype._searchNearestSegmentBefore = function(p) {
                    var v = this._list;
                    if (v.length === 0)
                      return -2;
                    var g = v.length - 1, m = 0, x = 0, y = g, A = 0;
                    if (p < v[0].originalBeginDts)
                      return A = -1, A;
                    for (; x <= y; )
                      if (m = x + Math.floor((y - x) / 2), m === g || p > v[m].lastSample.originalDts && p < v[m + 1].originalBeginDts) {
                        A = m;
                        break;
                      } else
                        v[m].originalBeginDts < p ? x = m + 1 : y = m - 1;
                    return A;
                  }, f.prototype._searchNearestSegmentAfter = function(p) {
                    return this._searchNearestSegmentBefore(p) + 1;
                  }, f.prototype.append = function(p) {
                    var v = this._list, g = p, m = this._lastAppendLocation, x = 0;
                    m !== -1 && m < v.length && g.originalBeginDts >= v[m].lastSample.originalDts && (m === v.length - 1 || m < v.length - 1 && g.originalBeginDts < v[m + 1].originalBeginDts) ? x = m + 1 : v.length > 0 && (x = this._searchNearestSegmentBefore(g.originalBeginDts) + 1), this._lastAppendLocation = x, this._list.splice(x, 0, g);
                  }, f.prototype.getLastSegmentBefore = function(p) {
                    var v = this._searchNearestSegmentBefore(p);
                    return v >= 0 ? this._list[v] : null;
                  }, f.prototype.getLastSampleBefore = function(p) {
                    var v = this.getLastSegmentBefore(p);
                    return v != null ? v.lastSample : null;
                  }, f.prototype.getLastSyncPointBefore = function(p) {
                    for (var v = this._searchNearestSegmentBefore(p), g = this._list[v].syncPoints; g.length === 0 && v > 0; )
                      v--, g = this._list[v].syncPoints;
                    return g.length > 0 ? g[g.length - 1] : null;
                  }, f;
                }()
              );
            }
          ),
          /***/
          "./src/core/mse-controller.js": (
            /*!************************************!*\
              !*** ./src/core/mse-controller.js ***!
              \************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), d = l(
                /*! ../utils/browser.js */
                "./src/utils/browser.js"
              ), f = l(
                /*! ./mse-events.js */
                "./src/core/mse-events.js"
              ), p = l(
                /*! ./media-segment-info.js */
                "./src/core/media-segment-info.js"
              ), v = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), g = (
                /** @class */
                function() {
                  function m(x) {
                    this.TAG = "MSEController", this._config = x, this._emitter = new (h())(), this._config.isLive && this._config.autoCleanupSourceBuffer == null && (this._config.autoCleanupSourceBuffer = !0), this.e = {
                      onSourceOpen: this._onSourceOpen.bind(this),
                      onSourceEnded: this._onSourceEnded.bind(this),
                      onSourceClose: this._onSourceClose.bind(this),
                      onSourceBufferError: this._onSourceBufferError.bind(this),
                      onSourceBufferUpdateEnd: this._onSourceBufferUpdateEnd.bind(this)
                    }, this._mediaSource = null, this._mediaSourceObjectURL = null, this._mediaElement = null, this._isBufferFull = !1, this._hasPendingEos = !1, this._requireSetMediaDuration = !1, this._pendingMediaDuration = 0, this._pendingSourceBufferInit = [], this._mimeTypes = {
                      video: null,
                      audio: null
                    }, this._sourceBuffers = {
                      video: null,
                      audio: null
                    }, this._lastInitSegments = {
                      video: null,
                      audio: null
                    }, this._pendingSegments = {
                      video: [],
                      audio: []
                    }, this._pendingRemoveRanges = {
                      video: [],
                      audio: []
                    }, this._idrList = new p.IDRSampleList();
                  }
                  return m.prototype.destroy = function() {
                    (this._mediaElement || this._mediaSource) && this.detachMediaElement(), this.e = null, this._emitter.removeAllListeners(), this._emitter = null;
                  }, m.prototype.on = function(x, y) {
                    this._emitter.addListener(x, y);
                  }, m.prototype.off = function(x, y) {
                    this._emitter.removeListener(x, y);
                  }, m.prototype.attachMediaElement = function(x) {
                    if (this._mediaSource)
                      throw new v.IllegalStateException("MediaSource has been attached to an HTMLMediaElement!");
                    var y = this._mediaSource = new window.MediaSource();
                    y.addEventListener("sourceopen", this.e.onSourceOpen), y.addEventListener("sourceended", this.e.onSourceEnded), y.addEventListener("sourceclose", this.e.onSourceClose), this._mediaElement = x, this._mediaSourceObjectURL = window.URL.createObjectURL(this._mediaSource), x.src = this._mediaSourceObjectURL;
                  }, m.prototype.detachMediaElement = function() {
                    if (this._mediaSource) {
                      var x = this._mediaSource;
                      for (var y in this._sourceBuffers) {
                        var A = this._pendingSegments[y];
                        A.splice(0, A.length), this._pendingSegments[y] = null, this._pendingRemoveRanges[y] = null, this._lastInitSegments[y] = null;
                        var b = this._sourceBuffers[y];
                        if (b) {
                          if (x.readyState !== "closed") {
                            try {
                              x.removeSourceBuffer(b);
                            } catch (M) {
                              u.default.e(this.TAG, M.message);
                            }
                            b.removeEventListener("error", this.e.onSourceBufferError), b.removeEventListener("updateend", this.e.onSourceBufferUpdateEnd);
                          }
                          this._mimeTypes[y] = null, this._sourceBuffers[y] = null;
                        }
                      }
                      if (x.readyState === "open")
                        try {
                          x.endOfStream();
                        } catch (M) {
                          u.default.e(this.TAG, M.message);
                        }
                      x.removeEventListener("sourceopen", this.e.onSourceOpen), x.removeEventListener("sourceended", this.e.onSourceEnded), x.removeEventListener("sourceclose", this.e.onSourceClose), this._pendingSourceBufferInit = [], this._isBufferFull = !1, this._idrList.clear(), this._mediaSource = null;
                    }
                    this._mediaElement && (this._mediaElement.src = "", this._mediaElement.removeAttribute("src"), this._mediaElement = null), this._mediaSourceObjectURL && (window.URL.revokeObjectURL(this._mediaSourceObjectURL), this._mediaSourceObjectURL = null);
                  }, m.prototype.appendInitSegment = function(x, y) {
                    if (!this._mediaSource || this._mediaSource.readyState !== "open") {
                      this._pendingSourceBufferInit.push(x), this._pendingSegments[x.type].push(x);
                      return;
                    }
                    var A = x, b = "" + A.container;
                    A.codec && A.codec.length > 0 && (b += ";codecs=" + A.codec);
                    var M = !1;
                    if (u.default.v(this.TAG, "Received Initialization Segment, mimeType: " + b), this._lastInitSegments[A.type] = A, b !== this._mimeTypes[A.type]) {
                      if (this._mimeTypes[A.type])
                        u.default.v(this.TAG, "Notice: " + A.type + " mimeType changed, origin: " + this._mimeTypes[A.type] + ", target: " + b);
                      else {
                        M = !0;
                        try {
                          var w = this._sourceBuffers[A.type] = this._mediaSource.addSourceBuffer(b);
                          w.addEventListener("error", this.e.onSourceBufferError), w.addEventListener("updateend", this.e.onSourceBufferUpdateEnd);
                        } catch (C) {
                          u.default.e(this.TAG, C.message), this._emitter.emit(f.default.ERROR, { code: C.code, msg: C.message });
                          return;
                        }
                      }
                      this._mimeTypes[A.type] = b;
                    }
                    y || this._pendingSegments[A.type].push(A), M || this._sourceBuffers[A.type] && !this._sourceBuffers[A.type].updating && this._doAppendSegments(), d.default.safari && A.container === "audio/mpeg" && A.mediaDuration > 0 && (this._requireSetMediaDuration = !0, this._pendingMediaDuration = A.mediaDuration / 1e3, this._updateMediaSourceDuration());
                  }, m.prototype.appendMediaSegment = function(x) {
                    var y = x;
                    this._pendingSegments[y.type].push(y), this._config.autoCleanupSourceBuffer && this._needCleanupSourceBuffer() && this._doCleanupSourceBuffer();
                    var A = this._sourceBuffers[y.type];
                    A && !A.updating && !this._hasPendingRemoveRanges() && this._doAppendSegments();
                  }, m.prototype.seek = function(x) {
                    for (var y in this._sourceBuffers)
                      if (this._sourceBuffers[y]) {
                        var A = this._sourceBuffers[y];
                        if (this._mediaSource.readyState === "open")
                          try {
                            A.abort();
                          } catch (E) {
                            u.default.e(this.TAG, E.message);
                          }
                        this._idrList.clear();
                        var b = this._pendingSegments[y];
                        if (b.splice(0, b.length), this._mediaSource.readyState !== "closed") {
                          for (var M = 0; M < A.buffered.length; M++) {
                            var w = A.buffered.start(M), C = A.buffered.end(M);
                            this._pendingRemoveRanges[y].push({ start: w, end: C });
                          }
                          if (A.updating || this._doRemoveRanges(), d.default.safari) {
                            var T = this._lastInitSegments[y];
                            T && (this._pendingSegments[y].push(T), A.updating || this._doAppendSegments());
                          }
                        }
                      }
                  }, m.prototype.endOfStream = function() {
                    var x = this._mediaSource, y = this._sourceBuffers;
                    if (!x || x.readyState !== "open") {
                      x && x.readyState === "closed" && this._hasPendingSegments() && (this._hasPendingEos = !0);
                      return;
                    }
                    y.video && y.video.updating || y.audio && y.audio.updating ? this._hasPendingEos = !0 : (this._hasPendingEos = !1, x.endOfStream());
                  }, m.prototype.getNearestKeyframe = function(x) {
                    return this._idrList.getLastSyncPointBeforeDts(x);
                  }, m.prototype._needCleanupSourceBuffer = function() {
                    if (!this._config.autoCleanupSourceBuffer)
                      return !1;
                    var x = this._mediaElement.currentTime;
                    for (var y in this._sourceBuffers) {
                      var A = this._sourceBuffers[y];
                      if (A) {
                        var b = A.buffered;
                        if (b.length >= 1 && x - b.start(0) >= this._config.autoCleanupMaxBackwardDuration)
                          return !0;
                      }
                    }
                    return !1;
                  }, m.prototype._doCleanupSourceBuffer = function() {
                    var x = this._mediaElement.currentTime;
                    for (var y in this._sourceBuffers) {
                      var A = this._sourceBuffers[y];
                      if (A) {
                        for (var b = A.buffered, M = !1, w = 0; w < b.length; w++) {
                          var C = b.start(w), T = b.end(w);
                          if (C <= x && x < T + 3) {
                            if (x - C >= this._config.autoCleanupMaxBackwardDuration) {
                              M = !0;
                              var E = x - this._config.autoCleanupMinBackwardDuration;
                              this._pendingRemoveRanges[y].push({ start: C, end: E });
                            }
                          } else
                            T < x && (M = !0, this._pendingRemoveRanges[y].push({ start: C, end: T }));
                        }
                        M && !A.updating && this._doRemoveRanges();
                      }
                    }
                  }, m.prototype._updateMediaSourceDuration = function() {
                    var x = this._sourceBuffers;
                    if (!(this._mediaElement.readyState === 0 || this._mediaSource.readyState !== "open") && !(x.video && x.video.updating || x.audio && x.audio.updating)) {
                      var y = this._mediaSource.duration, A = this._pendingMediaDuration;
                      A > 0 && (isNaN(y) || A > y) && (u.default.v(this.TAG, "Update MediaSource duration from " + y + " to " + A), this._mediaSource.duration = A), this._requireSetMediaDuration = !1, this._pendingMediaDuration = 0;
                    }
                  }, m.prototype._doRemoveRanges = function() {
                    for (var x in this._pendingRemoveRanges)
                      if (!(!this._sourceBuffers[x] || this._sourceBuffers[x].updating))
                        for (var y = this._sourceBuffers[x], A = this._pendingRemoveRanges[x]; A.length && !y.updating; ) {
                          var b = A.shift();
                          y.remove(b.start, b.end);
                        }
                  }, m.prototype._doAppendSegments = function() {
                    var x = this._pendingSegments;
                    for (var y in x)
                      if (!(!this._sourceBuffers[y] || this._sourceBuffers[y].updating) && x[y].length > 0) {
                        var A = x[y].shift();
                        if (A.timestampOffset) {
                          var b = this._sourceBuffers[y].timestampOffset, M = A.timestampOffset / 1e3, w = Math.abs(b - M);
                          w > 0.1 && (u.default.v(this.TAG, "Update MPEG audio timestampOffset from " + b + " to " + M), this._sourceBuffers[y].timestampOffset = M), delete A.timestampOffset;
                        }
                        if (!A.data || A.data.byteLength === 0)
                          continue;
                        try {
                          this._sourceBuffers[y].appendBuffer(A.data), this._isBufferFull = !1, y === "video" && A.hasOwnProperty("info") && this._idrList.appendArray(A.info.syncPoints);
                        } catch (C) {
                          this._pendingSegments[y].unshift(A), C.code === 22 ? (this._isBufferFull || this._emitter.emit(f.default.BUFFER_FULL), this._isBufferFull = !0) : (u.default.e(this.TAG, C.message), this._emitter.emit(f.default.ERROR, { code: C.code, msg: C.message }));
                        }
                      }
                  }, m.prototype._onSourceOpen = function() {
                    if (u.default.v(this.TAG, "MediaSource onSourceOpen"), this._mediaSource.removeEventListener("sourceopen", this.e.onSourceOpen), this._pendingSourceBufferInit.length > 0)
                      for (var x = this._pendingSourceBufferInit; x.length; ) {
                        var y = x.shift();
                        this.appendInitSegment(y, !0);
                      }
                    this._hasPendingSegments() && this._doAppendSegments(), this._emitter.emit(f.default.SOURCE_OPEN);
                  }, m.prototype._onSourceEnded = function() {
                    u.default.v(this.TAG, "MediaSource onSourceEnded");
                  }, m.prototype._onSourceClose = function() {
                    u.default.v(this.TAG, "MediaSource onSourceClose"), this._mediaSource && this.e != null && (this._mediaSource.removeEventListener("sourceopen", this.e.onSourceOpen), this._mediaSource.removeEventListener("sourceended", this.e.onSourceEnded), this._mediaSource.removeEventListener("sourceclose", this.e.onSourceClose));
                  }, m.prototype._hasPendingSegments = function() {
                    var x = this._pendingSegments;
                    return x.video.length > 0 || x.audio.length > 0;
                  }, m.prototype._hasPendingRemoveRanges = function() {
                    var x = this._pendingRemoveRanges;
                    return x.video.length > 0 || x.audio.length > 0;
                  }, m.prototype._onSourceBufferUpdateEnd = function() {
                    this._requireSetMediaDuration ? this._updateMediaSourceDuration() : this._hasPendingRemoveRanges() ? this._doRemoveRanges() : this._hasPendingSegments() ? this._doAppendSegments() : this._hasPendingEos && this.endOfStream(), this._emitter.emit(f.default.UPDATE_END);
                  }, m.prototype._onSourceBufferError = function(x) {
                    u.default.e(this.TAG, "SourceBuffer Error: " + x);
                  }, m;
                }()
              );
              o.default = g;
            }
          ),
          /***/
          "./src/core/mse-events.js": (
            /*!********************************!*\
              !*** ./src/core/mse-events.js ***!
              \********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = {
                ERROR: "error",
                SOURCE_OPEN: "source_open",
                UPDATE_END: "update_end",
                BUFFER_FULL: "buffer_full"
              };
              o.default = c;
            }
          ),
          /***/
          "./src/core/transmuxer.js": (
            /*!********************************!*\
              !*** ./src/core/transmuxer.js ***!
              \********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! webworkify-webpack */
                "./node_modules/webworkify-webpack/index.js"
              ), d = /* @__PURE__ */ l.n(u), f = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), p = l(
                /*! ../utils/logging-control.js */
                "./src/utils/logging-control.js"
              ), v = l(
                /*! ./transmuxing-controller.js */
                "./src/core/transmuxing-controller.js"
              ), g = l(
                /*! ./transmuxing-events.js */
                "./src/core/transmuxing-events.js"
              ), m = l(
                /*! ./media-info.js */
                "./src/core/media-info.js"
              ), x = (
                /** @class */
                function() {
                  function y(A, b) {
                    if (this.TAG = "Transmuxer", this._emitter = new (h())(), b.enableWorker && typeof Worker < "u")
                      try {
                        this._worker = d()(
                          /*require.resolve*/
                          /*! ./transmuxing-worker */
                          "./src/core/transmuxing-worker.js"
                        ), this._workerDestroying = !1, this._worker.addEventListener("message", this._onWorkerMessage.bind(this)), this._worker.postMessage({ cmd: "init", param: [A, b] }), this.e = {
                          onLoggingConfigChanged: this._onLoggingConfigChanged.bind(this)
                        }, p.default.registerListener(this.e.onLoggingConfigChanged), this._worker.postMessage({ cmd: "logging_config", param: p.default.getConfig() });
                      } catch {
                        f.default.e(this.TAG, "Error while initialize transmuxing worker, fallback to inline transmuxing"), this._worker = null, this._controller = new v.default(A, b);
                      }
                    else
                      this._controller = new v.default(A, b);
                    if (this._controller) {
                      var M = this._controller;
                      M.on(g.default.IO_ERROR, this._onIOError.bind(this)), M.on(g.default.DEMUX_ERROR, this._onDemuxError.bind(this)), M.on(g.default.INIT_SEGMENT, this._onInitSegment.bind(this)), M.on(g.default.MEDIA_SEGMENT, this._onMediaSegment.bind(this)), M.on(g.default.LOADING_COMPLETE, this._onLoadingComplete.bind(this)), M.on(g.default.RECOVERED_EARLY_EOF, this._onRecoveredEarlyEof.bind(this)), M.on(g.default.MEDIA_INFO, this._onMediaInfo.bind(this)), M.on(g.default.METADATA_ARRIVED, this._onMetaDataArrived.bind(this)), M.on(g.default.SCRIPTDATA_ARRIVED, this._onScriptDataArrived.bind(this)), M.on(g.default.STATISTICS_INFO, this._onStatisticsInfo.bind(this)), M.on(g.default.RECOMMEND_SEEKPOINT, this._onRecommendSeekpoint.bind(this));
                    }
                  }
                  return y.prototype.destroy = function() {
                    this._worker ? this._workerDestroying || (this._workerDestroying = !0, this._worker.postMessage({ cmd: "destroy" }), p.default.removeListener(this.e.onLoggingConfigChanged), this.e = null) : (this._controller.destroy(), this._controller = null), this._emitter.removeAllListeners(), this._emitter = null;
                  }, y.prototype.on = function(A, b) {
                    this._emitter.addListener(A, b);
                  }, y.prototype.off = function(A, b) {
                    this._emitter.removeListener(A, b);
                  }, y.prototype.hasWorker = function() {
                    return this._worker != null;
                  }, y.prototype.open = function() {
                    this._worker ? this._worker.postMessage({ cmd: "start" }) : this._controller.start();
                  }, y.prototype.close = function() {
                    this._worker ? this._worker.postMessage({ cmd: "stop" }) : this._controller.stop();
                  }, y.prototype.seek = function(A) {
                    this._worker ? this._worker.postMessage({ cmd: "seek", param: A }) : this._controller.seek(A);
                  }, y.prototype.pause = function() {
                    this._worker ? this._worker.postMessage({ cmd: "pause" }) : this._controller.pause();
                  }, y.prototype.resume = function() {
                    this._worker ? this._worker.postMessage({ cmd: "resume" }) : this._controller.resume();
                  }, y.prototype._onInitSegment = function(A, b) {
                    var M = this;
                    Promise.resolve().then(function() {
                      M._emitter.emit(g.default.INIT_SEGMENT, A, b);
                    });
                  }, y.prototype._onMediaSegment = function(A, b) {
                    var M = this;
                    Promise.resolve().then(function() {
                      M._emitter.emit(g.default.MEDIA_SEGMENT, A, b);
                    });
                  }, y.prototype._onLoadingComplete = function() {
                    var A = this;
                    Promise.resolve().then(function() {
                      A._emitter.emit(g.default.LOADING_COMPLETE);
                    });
                  }, y.prototype._onRecoveredEarlyEof = function() {
                    var A = this;
                    Promise.resolve().then(function() {
                      A._emitter.emit(g.default.RECOVERED_EARLY_EOF);
                    });
                  }, y.prototype._onMediaInfo = function(A) {
                    var b = this;
                    Promise.resolve().then(function() {
                      b._emitter.emit(g.default.MEDIA_INFO, A);
                    });
                  }, y.prototype._onMetaDataArrived = function(A) {
                    var b = this;
                    Promise.resolve().then(function() {
                      b._emitter.emit(g.default.METADATA_ARRIVED, A);
                    });
                  }, y.prototype._onScriptDataArrived = function(A) {
                    var b = this;
                    Promise.resolve().then(function() {
                      b._emitter.emit(g.default.SCRIPTDATA_ARRIVED, A);
                    });
                  }, y.prototype._onStatisticsInfo = function(A) {
                    var b = this;
                    Promise.resolve().then(function() {
                      b._emitter.emit(g.default.STATISTICS_INFO, A);
                    });
                  }, y.prototype._onIOError = function(A, b) {
                    var M = this;
                    Promise.resolve().then(function() {
                      M._emitter.emit(g.default.IO_ERROR, A, b);
                    });
                  }, y.prototype._onDemuxError = function(A, b) {
                    var M = this;
                    Promise.resolve().then(function() {
                      M._emitter.emit(g.default.DEMUX_ERROR, A, b);
                    });
                  }, y.prototype._onRecommendSeekpoint = function(A) {
                    var b = this;
                    Promise.resolve().then(function() {
                      b._emitter.emit(g.default.RECOMMEND_SEEKPOINT, A);
                    });
                  }, y.prototype._onLoggingConfigChanged = function(A) {
                    this._worker && this._worker.postMessage({ cmd: "logging_config", param: A });
                  }, y.prototype._onWorkerMessage = function(A) {
                    var b = A.data, M = b.data;
                    if (b.msg === "destroyed" || this._workerDestroying) {
                      this._workerDestroying = !1, this._worker.terminate(), this._worker = null;
                      return;
                    }
                    switch (b.msg) {
                      case g.default.INIT_SEGMENT:
                      case g.default.MEDIA_SEGMENT:
                        this._emitter.emit(b.msg, M.type, M.data);
                        break;
                      case g.default.LOADING_COMPLETE:
                      case g.default.RECOVERED_EARLY_EOF:
                        this._emitter.emit(b.msg);
                        break;
                      case g.default.MEDIA_INFO:
                        Object.setPrototypeOf(M, m.default.prototype), this._emitter.emit(b.msg, M);
                        break;
                      case g.default.METADATA_ARRIVED:
                      case g.default.SCRIPTDATA_ARRIVED:
                      case g.default.STATISTICS_INFO:
                        this._emitter.emit(b.msg, M);
                        break;
                      case g.default.IO_ERROR:
                      case g.default.DEMUX_ERROR:
                        this._emitter.emit(b.msg, M.type, M.info);
                        break;
                      case g.default.RECOMMEND_SEEKPOINT:
                        this._emitter.emit(b.msg, M);
                        break;
                      case "logcat_callback":
                        f.default.emitter.emit("log", M.type, M.logcat);
                        break;
                    }
                  }, y;
                }()
              );
              o.default = x;
            }
          ),
          /***/
          "./src/core/transmuxing-controller.js": (
            /*!********************************************!*\
              !*** ./src/core/transmuxing-controller.js ***!
              \********************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), d = l(
                /*! ../utils/browser.js */
                "./src/utils/browser.js"
              ), f = l(
                /*! ./media-info.js */
                "./src/core/media-info.js"
              ), p = l(
                /*! ../demux/flv-demuxer.js */
                "./src/demux/flv-demuxer.js"
              ), v = l(
                /*! ../remux/mp4-remuxer.js */
                "./src/remux/mp4-remuxer.js"
              ), g = l(
                /*! ../demux/demux-errors.js */
                "./src/demux/demux-errors.js"
              ), m = l(
                /*! ../io/io-controller.js */
                "./src/io/io-controller.js"
              ), x = l(
                /*! ./transmuxing-events.js */
                "./src/core/transmuxing-events.js"
              ), y = (
                /** @class */
                function() {
                  function A(b, M) {
                    this.TAG = "TransmuxingController", this._emitter = new (h())(), this._config = M, b.segments || (b.segments = [{
                      duration: b.duration,
                      filesize: b.filesize,
                      url: b.url
                    }]), typeof b.cors != "boolean" && (b.cors = !0), typeof b.withCredentials != "boolean" && (b.withCredentials = !1), this._mediaDataSource = b, this._currentSegmentIndex = 0;
                    var w = 0;
                    this._mediaDataSource.segments.forEach(function(C) {
                      C.timestampBase = w, w += C.duration, C.cors = b.cors, C.withCredentials = b.withCredentials, M.referrerPolicy && (C.referrerPolicy = M.referrerPolicy);
                    }), !isNaN(w) && this._mediaDataSource.duration !== w && (this._mediaDataSource.duration = w), this._mediaInfo = null, this._demuxer = null, this._remuxer = null, this._ioctl = null, this._pendingSeekTime = null, this._pendingResolveSeekPoint = null, this._statisticsReporter = null;
                  }
                  return A.prototype.destroy = function() {
                    this._mediaInfo = null, this._mediaDataSource = null, this._statisticsReporter && this._disableStatisticsReporter(), this._ioctl && (this._ioctl.destroy(), this._ioctl = null), this._demuxer && (this._demuxer.destroy(), this._demuxer = null), this._remuxer && (this._remuxer.destroy(), this._remuxer = null), this._emitter.removeAllListeners(), this._emitter = null;
                  }, A.prototype.on = function(b, M) {
                    this._emitter.addListener(b, M);
                  }, A.prototype.off = function(b, M) {
                    this._emitter.removeListener(b, M);
                  }, A.prototype.start = function() {
                    this._loadSegment(0), this._enableStatisticsReporter();
                  }, A.prototype._loadSegment = function(b, M) {
                    this._currentSegmentIndex = b;
                    var w = this._mediaDataSource.segments[b], C = this._ioctl = new m.default(w, this._config, b);
                    C.onError = this._onIOException.bind(this), C.onSeeked = this._onIOSeeked.bind(this), C.onComplete = this._onIOComplete.bind(this), C.onRedirect = this._onIORedirect.bind(this), C.onRecoveredEarlyEof = this._onIORecoveredEarlyEof.bind(this), M ? this._demuxer.bindDataSource(this._ioctl) : C.onDataArrival = this._onInitChunkArrival.bind(this), C.open(M);
                  }, A.prototype.stop = function() {
                    this._internalAbort(), this._disableStatisticsReporter();
                  }, A.prototype._internalAbort = function() {
                    this._ioctl && (this._ioctl.destroy(), this._ioctl = null);
                  }, A.prototype.pause = function() {
                    this._ioctl && this._ioctl.isWorking() && (this._ioctl.pause(), this._disableStatisticsReporter());
                  }, A.prototype.resume = function() {
                    this._ioctl && this._ioctl.isPaused() && (this._ioctl.resume(), this._enableStatisticsReporter());
                  }, A.prototype.seek = function(b) {
                    if (!(this._mediaInfo == null || !this._mediaInfo.isSeekable())) {
                      var M = this._searchSegmentIndexContains(b);
                      if (M === this._currentSegmentIndex) {
                        var w = this._mediaInfo.segments[M];
                        if (w == null)
                          this._pendingSeekTime = b;
                        else {
                          var C = w.getNearestKeyframe(b);
                          this._remuxer.seek(C.milliseconds), this._ioctl.seek(C.fileposition), this._pendingResolveSeekPoint = C.milliseconds;
                        }
                      } else {
                        var T = this._mediaInfo.segments[M];
                        if (T == null)
                          this._pendingSeekTime = b, this._internalAbort(), this._remuxer.seek(), this._remuxer.insertDiscontinuity(), this._loadSegment(M);
                        else {
                          var C = T.getNearestKeyframe(b);
                          this._internalAbort(), this._remuxer.seek(b), this._remuxer.insertDiscontinuity(), this._demuxer.resetMediaInfo(), this._demuxer.timestampBase = this._mediaDataSource.segments[M].timestampBase, this._loadSegment(M, C.fileposition), this._pendingResolveSeekPoint = C.milliseconds, this._reportSegmentMediaInfo(M);
                        }
                      }
                      this._enableStatisticsReporter();
                    }
                  }, A.prototype._searchSegmentIndexContains = function(b) {
                    for (var M = this._mediaDataSource.segments, w = M.length - 1, C = 0; C < M.length; C++)
                      if (b < M[C].timestampBase) {
                        w = C - 1;
                        break;
                      }
                    return w;
                  }, A.prototype._onInitChunkArrival = function(b, M) {
                    var w = this, C = null, T = 0;
                    if (M > 0)
                      this._demuxer.bindDataSource(this._ioctl), this._demuxer.timestampBase = this._mediaDataSource.segments[this._currentSegmentIndex].timestampBase, T = this._demuxer.parseChunks(b, M);
                    else if ((C = p.default.probe(b)).match) {
                      this._demuxer = new p.default(C, this._config), this._remuxer || (this._remuxer = new v.default(this._config));
                      var E = this._mediaDataSource;
                      E.duration != null && !isNaN(E.duration) && (this._demuxer.overridedDuration = E.duration), typeof E.hasAudio == "boolean" && (this._demuxer.overridedHasAudio = E.hasAudio), typeof E.hasVideo == "boolean" && (this._demuxer.overridedHasVideo = E.hasVideo), this._demuxer.timestampBase = E.segments[this._currentSegmentIndex].timestampBase, this._demuxer.onError = this._onDemuxException.bind(this), this._demuxer.onMediaInfo = this._onMediaInfo.bind(this), this._demuxer.onMetaDataArrived = this._onMetaDataArrived.bind(this), this._demuxer.onScriptDataArrived = this._onScriptDataArrived.bind(this), this._remuxer.bindDataSource(this._demuxer.bindDataSource(this._ioctl)), this._remuxer.onInitSegment = this._onRemuxerInitSegmentArrival.bind(this), this._remuxer.onMediaSegment = this._onRemuxerMediaSegmentArrival.bind(this), T = this._demuxer.parseChunks(b, M);
                    } else
                      C = null, u.default.e(this.TAG, "Non-FLV, Unsupported media type!"), Promise.resolve().then(function() {
                        w._internalAbort();
                      }), this._emitter.emit(x.default.DEMUX_ERROR, g.default.FORMAT_UNSUPPORTED, "Non-FLV, Unsupported media type"), T = 0;
                    return T;
                  }, A.prototype._onMediaInfo = function(b) {
                    var M = this;
                    this._mediaInfo == null && (this._mediaInfo = Object.assign({}, b), this._mediaInfo.keyframesIndex = null, this._mediaInfo.segments = [], this._mediaInfo.segmentCount = this._mediaDataSource.segments.length, Object.setPrototypeOf(this._mediaInfo, f.default.prototype));
                    var w = Object.assign({}, b);
                    Object.setPrototypeOf(w, f.default.prototype), this._mediaInfo.segments[this._currentSegmentIndex] = w, this._reportSegmentMediaInfo(this._currentSegmentIndex), this._pendingSeekTime != null && Promise.resolve().then(function() {
                      var C = M._pendingSeekTime;
                      M._pendingSeekTime = null, M.seek(C);
                    });
                  }, A.prototype._onMetaDataArrived = function(b) {
                    this._emitter.emit(x.default.METADATA_ARRIVED, b);
                  }, A.prototype._onScriptDataArrived = function(b) {
                    this._emitter.emit(x.default.SCRIPTDATA_ARRIVED, b);
                  }, A.prototype._onIOSeeked = function() {
                    this._remuxer.insertDiscontinuity();
                  }, A.prototype._onIOComplete = function(b) {
                    var M = b, w = M + 1;
                    w < this._mediaDataSource.segments.length ? (this._internalAbort(), this._remuxer.flushStashedSamples(), this._loadSegment(w)) : (this._remuxer.flushStashedSamples(), this._emitter.emit(x.default.LOADING_COMPLETE), this._disableStatisticsReporter());
                  }, A.prototype._onIORedirect = function(b) {
                    var M = this._ioctl.extraData;
                    this._mediaDataSource.segments[M].redirectedURL = b;
                  }, A.prototype._onIORecoveredEarlyEof = function() {
                    this._emitter.emit(x.default.RECOVERED_EARLY_EOF);
                  }, A.prototype._onIOException = function(b, M) {
                    u.default.e(this.TAG, "IOException: type = " + b + ", code = " + M.code + ", msg = " + M.msg), this._emitter.emit(x.default.IO_ERROR, b, M), this._disableStatisticsReporter();
                  }, A.prototype._onDemuxException = function(b, M) {
                    u.default.e(this.TAG, "DemuxException: type = " + b + ", info = " + M), this._emitter.emit(x.default.DEMUX_ERROR, b, M);
                  }, A.prototype._onRemuxerInitSegmentArrival = function(b, M) {
                    this._emitter.emit(x.default.INIT_SEGMENT, b, M);
                  }, A.prototype._onRemuxerMediaSegmentArrival = function(b, M) {
                    if (this._pendingSeekTime == null && (this._emitter.emit(x.default.MEDIA_SEGMENT, b, M), this._pendingResolveSeekPoint != null && b === "video")) {
                      var w = M.info.syncPoints, C = this._pendingResolveSeekPoint;
                      this._pendingResolveSeekPoint = null, d.default.safari && w.length > 0 && w[0].originalDts === C && (C = w[0].pts), this._emitter.emit(x.default.RECOMMEND_SEEKPOINT, C);
                    }
                  }, A.prototype._enableStatisticsReporter = function() {
                    this._statisticsReporter == null && (this._statisticsReporter = self.setInterval(this._reportStatisticsInfo.bind(this), this._config.statisticsInfoReportInterval));
                  }, A.prototype._disableStatisticsReporter = function() {
                    this._statisticsReporter && (self.clearInterval(this._statisticsReporter), this._statisticsReporter = null);
                  }, A.prototype._reportSegmentMediaInfo = function(b) {
                    var M = this._mediaInfo.segments[b], w = Object.assign({}, M);
                    w.duration = this._mediaInfo.duration, w.segmentCount = this._mediaInfo.segmentCount, delete w.segments, delete w.keyframesIndex, this._emitter.emit(x.default.MEDIA_INFO, w);
                  }, A.prototype._reportStatisticsInfo = function() {
                    var b = {};
                    b.url = this._ioctl.currentURL, b.hasRedirect = this._ioctl.hasRedirect, b.hasRedirect && (b.redirectedURL = this._ioctl.currentRedirectedURL), b.speed = this._ioctl.currentSpeed, b.loaderType = this._ioctl.loaderType, b.currentSegmentIndex = this._currentSegmentIndex, b.totalSegmentCount = this._mediaDataSource.segments.length, this._emitter.emit(x.default.STATISTICS_INFO, b);
                  }, A;
                }()
              );
              o.default = y;
            }
          ),
          /***/
          "./src/core/transmuxing-events.js": (
            /*!****************************************!*\
              !*** ./src/core/transmuxing-events.js ***!
              \****************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = {
                IO_ERROR: "io_error",
                DEMUX_ERROR: "demux_error",
                INIT_SEGMENT: "init_segment",
                MEDIA_SEGMENT: "media_segment",
                LOADING_COMPLETE: "loading_complete",
                RECOVERED_EARLY_EOF: "recovered_early_eof",
                MEDIA_INFO: "media_info",
                METADATA_ARRIVED: "metadata_arrived",
                SCRIPTDATA_ARRIVED: "scriptdata_arrived",
                STATISTICS_INFO: "statistics_info",
                RECOMMEND_SEEKPOINT: "recommend_seekpoint"
              };
              o.default = c;
            }
          ),
          /***/
          "./src/core/transmuxing-worker.js": (
            /*!****************************************!*\
              !*** ./src/core/transmuxing-worker.js ***!
              \****************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logging-control.js */
                "./src/utils/logging-control.js"
              ), h = l(
                /*! ../utils/polyfill.js */
                "./src/utils/polyfill.js"
              ), u = l(
                /*! ./transmuxing-controller.js */
                "./src/core/transmuxing-controller.js"
              ), d = l(
                /*! ./transmuxing-events.js */
                "./src/core/transmuxing-events.js"
              ), f = function(p) {
                var v = null, g = L.bind(this);
                h.default.install(), p.addEventListener("message", function(I) {
                  switch (I.data.cmd) {
                    case "init":
                      v = new u.default(I.data.param[0], I.data.param[1]), v.on(d.default.IO_ERROR, T.bind(this)), v.on(d.default.DEMUX_ERROR, E.bind(this)), v.on(d.default.INIT_SEGMENT, m.bind(this)), v.on(d.default.MEDIA_SEGMENT, x.bind(this)), v.on(d.default.LOADING_COMPLETE, y.bind(this)), v.on(d.default.RECOVERED_EARLY_EOF, A.bind(this)), v.on(d.default.MEDIA_INFO, b.bind(this)), v.on(d.default.METADATA_ARRIVED, M.bind(this)), v.on(d.default.SCRIPTDATA_ARRIVED, w.bind(this)), v.on(d.default.STATISTICS_INFO, C.bind(this)), v.on(d.default.RECOMMEND_SEEKPOINT, P.bind(this));
                      break;
                    case "destroy":
                      v && (v.destroy(), v = null), p.postMessage({ msg: "destroyed" });
                      break;
                    case "start":
                      v.start();
                      break;
                    case "stop":
                      v.stop();
                      break;
                    case "seek":
                      v.seek(I.data.param);
                      break;
                    case "pause":
                      v.pause();
                      break;
                    case "resume":
                      v.resume();
                      break;
                    case "logging_config": {
                      var N = I.data.param;
                      c.default.applyConfig(N), N.enableCallback === !0 ? c.default.addLogListener(g) : c.default.removeLogListener(g);
                      break;
                    }
                  }
                });
                function m(I, N) {
                  var H = {
                    msg: d.default.INIT_SEGMENT,
                    data: {
                      type: I,
                      data: N
                    }
                  };
                  p.postMessage(H, [N.data]);
                }
                function x(I, N) {
                  var H = {
                    msg: d.default.MEDIA_SEGMENT,
                    data: {
                      type: I,
                      data: N
                    }
                  };
                  p.postMessage(H, [N.data]);
                }
                function y() {
                  var I = {
                    msg: d.default.LOADING_COMPLETE
                  };
                  p.postMessage(I);
                }
                function A() {
                  var I = {
                    msg: d.default.RECOVERED_EARLY_EOF
                  };
                  p.postMessage(I);
                }
                function b(I) {
                  var N = {
                    msg: d.default.MEDIA_INFO,
                    data: I
                  };
                  p.postMessage(N);
                }
                function M(I) {
                  var N = {
                    msg: d.default.METADATA_ARRIVED,
                    data: I
                  };
                  p.postMessage(N);
                }
                function w(I) {
                  var N = {
                    msg: d.default.SCRIPTDATA_ARRIVED,
                    data: I
                  };
                  p.postMessage(N);
                }
                function C(I) {
                  var N = {
                    msg: d.default.STATISTICS_INFO,
                    data: I
                  };
                  p.postMessage(N);
                }
                function T(I, N) {
                  p.postMessage({
                    msg: d.default.IO_ERROR,
                    data: {
                      type: I,
                      info: N
                    }
                  });
                }
                function E(I, N) {
                  p.postMessage({
                    msg: d.default.DEMUX_ERROR,
                    data: {
                      type: I,
                      info: N
                    }
                  });
                }
                function P(I) {
                  p.postMessage({
                    msg: d.default.RECOMMEND_SEEKPOINT,
                    data: I
                  });
                }
                function L(I, N) {
                  p.postMessage({
                    msg: "logcat_callback",
                    data: {
                      type: I,
                      logcat: N
                    }
                  });
                }
              };
              o.default = f;
            }
          ),
          /***/
          "./src/demux/amf-parser.js": (
            /*!*********************************!*\
              !*** ./src/demux/amf-parser.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ../utils/utf8-conv.js */
                "./src/utils/utf8-conv.js"
              ), u = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), d = function() {
                var p = new ArrayBuffer(2);
                return new DataView(p).setInt16(0, 256, !0), new Int16Array(p)[0] === 256;
              }(), f = (
                /** @class */
                function() {
                  function p() {
                  }
                  return p.parseScriptData = function(v, g, m) {
                    var x = {};
                    try {
                      var y = p.parseValue(v, g, m), A = p.parseValue(v, g + y.size, m - y.size);
                      x[y.data] = A.data;
                    } catch (b) {
                      c.default.e("AMF", b.toString());
                    }
                    return x;
                  }, p.parseObject = function(v, g, m) {
                    if (m < 3)
                      throw new u.IllegalStateException("Data not enough when parse ScriptDataObject");
                    var x = p.parseString(v, g, m), y = p.parseValue(v, g + x.size, m - x.size), A = y.objectEnd;
                    return {
                      data: {
                        name: x.data,
                        value: y.data
                      },
                      size: x.size + y.size,
                      objectEnd: A
                    };
                  }, p.parseVariable = function(v, g, m) {
                    return p.parseObject(v, g, m);
                  }, p.parseString = function(v, g, m) {
                    if (m < 2)
                      throw new u.IllegalStateException("Data not enough when parse String");
                    var x = new DataView(v, g, m), y = x.getUint16(0, !d), A;
                    return y > 0 ? A = (0, h.default)(new Uint8Array(v, g + 2, y)) : A = "", {
                      data: A,
                      size: 2 + y
                    };
                  }, p.parseLongString = function(v, g, m) {
                    if (m < 4)
                      throw new u.IllegalStateException("Data not enough when parse LongString");
                    var x = new DataView(v, g, m), y = x.getUint32(0, !d), A;
                    return y > 0 ? A = (0, h.default)(new Uint8Array(v, g + 4, y)) : A = "", {
                      data: A,
                      size: 4 + y
                    };
                  }, p.parseDate = function(v, g, m) {
                    if (m < 10)
                      throw new u.IllegalStateException("Data size invalid when parse Date");
                    var x = new DataView(v, g, m), y = x.getFloat64(0, !d), A = x.getInt16(8, !d);
                    return y += A * 60 * 1e3, {
                      data: new Date(y),
                      size: 8 + 2
                    };
                  }, p.parseValue = function(v, g, m) {
                    if (m < 1)
                      throw new u.IllegalStateException("Data not enough when parse Value");
                    var x = new DataView(v, g, m), y = 1, A = x.getUint8(0), b, M = !1;
                    try {
                      switch (A) {
                        case 0:
                          b = x.getFloat64(1, !d), y += 8;
                          break;
                        case 1: {
                          var w = x.getUint8(1);
                          b = !!w, y += 1;
                          break;
                        }
                        case 2: {
                          var C = p.parseString(v, g + 1, m - 1);
                          b = C.data, y += C.size;
                          break;
                        }
                        case 3: {
                          b = {};
                          var T = 0;
                          for ((x.getUint32(m - 4, !d) & 16777215) === 9 && (T = 3); y < m - 4; ) {
                            var E = p.parseObject(v, g + y, m - y - T);
                            if (E.objectEnd)
                              break;
                            b[E.data.name] = E.data.value, y += E.size;
                          }
                          if (y <= m - 3) {
                            var P = x.getUint32(y - 1, !d) & 16777215;
                            P === 9 && (y += 3);
                          }
                          break;
                        }
                        case 8: {
                          b = {}, y += 4;
                          var T = 0;
                          for ((x.getUint32(m - 4, !d) & 16777215) === 9 && (T = 3); y < m - 8; ) {
                            var L = p.parseVariable(v, g + y, m - y - T);
                            if (L.objectEnd)
                              break;
                            b[L.data.name] = L.data.value, y += L.size;
                          }
                          if (y <= m - 3) {
                            var P = x.getUint32(y - 1, !d) & 16777215;
                            P === 9 && (y += 3);
                          }
                          break;
                        }
                        case 9:
                          b = void 0, y = 1, M = !0;
                          break;
                        case 10: {
                          b = [];
                          var I = x.getUint32(1, !d);
                          y += 4;
                          for (var N = 0; N < I; N++) {
                            var H = p.parseValue(v, g + y, m - y);
                            b.push(H.data), y += H.size;
                          }
                          break;
                        }
                        case 11: {
                          var U = p.parseDate(v, g + 1, m - 1);
                          b = U.data, y += U.size;
                          break;
                        }
                        case 12: {
                          var W = p.parseString(v, g + 1, m - 1);
                          b = W.data, y += W.size;
                          break;
                        }
                        default:
                          y = m, c.default.w("AMF", "Unsupported AMF value type " + A);
                      }
                    } catch (X) {
                      c.default.e("AMF", X.toString());
                    }
                    return {
                      data: b,
                      size: y,
                      objectEnd: M
                    };
                  }, p;
                }()
              );
              o.default = f;
            }
          ),
          /***/
          "./src/demux/demux-errors.js": (
            /*!***********************************!*\
              !*** ./src/demux/demux-errors.js ***!
              \***********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = {
                OK: "OK",
                FORMAT_ERROR: "FormatError",
                FORMAT_UNSUPPORTED: "FormatUnsupported",
                CODEC_UNSUPPORTED: "CodecUnsupported"
              };
              o.default = c;
            }
          ),
          /***/
          "./src/demux/exp-golomb.js": (
            /*!*********************************!*\
              !*** ./src/demux/exp-golomb.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), h = (
                /** @class */
                function() {
                  function u(d) {
                    this.TAG = "ExpGolomb", this._buffer = d, this._buffer_index = 0, this._total_bytes = d.byteLength, this._total_bits = d.byteLength * 8, this._current_word = 0, this._current_word_bits_left = 0;
                  }
                  return u.prototype.destroy = function() {
                    this._buffer = null;
                  }, u.prototype._fillCurrentWord = function() {
                    var d = this._total_bytes - this._buffer_index;
                    if (d <= 0)
                      throw new c.IllegalStateException("ExpGolomb: _fillCurrentWord() but no bytes available");
                    var f = Math.min(4, d), p = new Uint8Array(4);
                    p.set(this._buffer.subarray(this._buffer_index, this._buffer_index + f)), this._current_word = new DataView(p.buffer).getUint32(0, !1), this._buffer_index += f, this._current_word_bits_left = f * 8;
                  }, u.prototype.readBits = function(d) {
                    if (d > 32)
                      throw new c.InvalidArgumentException("ExpGolomb: readBits() bits exceeded max 32bits!");
                    if (d <= this._current_word_bits_left) {
                      var f = this._current_word >>> 32 - d;
                      return this._current_word <<= d, this._current_word_bits_left -= d, f;
                    }
                    var p = this._current_word_bits_left ? this._current_word : 0;
                    p = p >>> 32 - this._current_word_bits_left;
                    var v = d - this._current_word_bits_left;
                    this._fillCurrentWord();
                    var g = Math.min(v, this._current_word_bits_left), m = this._current_word >>> 32 - g;
                    return this._current_word <<= g, this._current_word_bits_left -= g, p = p << g | m, p;
                  }, u.prototype.readBool = function() {
                    return this.readBits(1) === 1;
                  }, u.prototype.readByte = function() {
                    return this.readBits(8);
                  }, u.prototype._skipLeadingZero = function() {
                    var d;
                    for (d = 0; d < this._current_word_bits_left; d++)
                      if (this._current_word & 2147483648 >>> d)
                        return this._current_word <<= d, this._current_word_bits_left -= d, d;
                    return this._fillCurrentWord(), d + this._skipLeadingZero();
                  }, u.prototype.readUEG = function() {
                    var d = this._skipLeadingZero();
                    return this.readBits(d + 1) - 1;
                  }, u.prototype.readSEG = function() {
                    var d = this.readUEG();
                    return d & 1 ? d + 1 >>> 1 : -1 * (d >>> 1);
                  }, u;
                }()
              );
              o.default = h;
            }
          ),
          /***/
          "./src/demux/flv-demuxer.js": (
            /*!**********************************!*\
              !*** ./src/demux/flv-demuxer.js ***!
              \**********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ./amf-parser.js */
                "./src/demux/amf-parser.js"
              ), u = l(
                /*! ./sps-parser.js */
                "./src/demux/sps-parser.js"
              ), d = l(
                /*! ./demux-errors.js */
                "./src/demux/demux-errors.js"
              ), f = l(
                /*! ../core/media-info.js */
                "./src/core/media-info.js"
              ), p = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              );
              function v(m, x) {
                return m[x] << 24 | m[x + 1] << 16 | m[x + 2] << 8 | m[x + 3];
              }
              var g = (
                /** @class */
                function() {
                  function m(x, y) {
                    this.TAG = "FLVDemuxer", this._config = y, this._onError = null, this._onMediaInfo = null, this._onMetaDataArrived = null, this._onScriptDataArrived = null, this._onTrackMetadata = null, this._onDataAvailable = null, this._dataOffset = x.dataOffset, this._firstParse = !0, this._dispatch = !1, this._hasAudio = x.hasAudioTrack, this._hasVideo = x.hasVideoTrack, this._hasAudioFlagOverrided = !1, this._hasVideoFlagOverrided = !1, this._audioInitialMetadataDispatched = !1, this._videoInitialMetadataDispatched = !1, this._mediaInfo = new f.default(), this._mediaInfo.hasAudio = this._hasAudio, this._mediaInfo.hasVideo = this._hasVideo, this._metadata = null, this._audioMetadata = null, this._videoMetadata = null, this._naluLengthSize = 4, this._timestampBase = 0, this._timescale = 1e3, this._duration = 0, this._durationOverrided = !1, this._referenceFrameRate = {
                      fixed: !0,
                      fps: 23.976,
                      fps_num: 23976,
                      fps_den: 1e3
                    }, this._flvSoundRateTable = [5500, 11025, 22050, 44100, 48e3], this._mpegSamplingRates = [
                      96e3,
                      88200,
                      64e3,
                      48e3,
                      44100,
                      32e3,
                      24e3,
                      22050,
                      16e3,
                      12e3,
                      11025,
                      8e3,
                      7350
                    ], this._mpegAudioV10SampleRateTable = [44100, 48e3, 32e3, 0], this._mpegAudioV20SampleRateTable = [22050, 24e3, 16e3, 0], this._mpegAudioV25SampleRateTable = [11025, 12e3, 8e3, 0], this._mpegAudioL1BitRateTable = [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, -1], this._mpegAudioL2BitRateTable = [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384, -1], this._mpegAudioL3BitRateTable = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, -1], this._videoTrack = { type: "video", id: 1, sequenceNumber: 0, samples: [], length: 0 }, this._audioTrack = { type: "audio", id: 2, sequenceNumber: 0, samples: [], length: 0 }, this._littleEndian = function() {
                      var A = new ArrayBuffer(2);
                      return new DataView(A).setInt16(0, 256, !0), new Int16Array(A)[0] === 256;
                    }();
                  }
                  return m.prototype.destroy = function() {
                    this._mediaInfo = null, this._metadata = null, this._audioMetadata = null, this._videoMetadata = null, this._videoTrack = null, this._audioTrack = null, this._onError = null, this._onMediaInfo = null, this._onMetaDataArrived = null, this._onScriptDataArrived = null, this._onTrackMetadata = null, this._onDataAvailable = null;
                  }, m.probe = function(x) {
                    var y = new Uint8Array(x), A = { match: !1 };
                    if (y[0] !== 70 || y[1] !== 76 || y[2] !== 86 || y[3] !== 1)
                      return A;
                    var b = (y[4] & 4) >>> 2 !== 0, M = (y[4] & 1) !== 0, w = v(y, 5);
                    return w < 9 ? A : {
                      match: !0,
                      consumed: w,
                      dataOffset: w,
                      hasAudioTrack: b,
                      hasVideoTrack: M
                    };
                  }, m.prototype.bindDataSource = function(x) {
                    return x.onDataArrival = this.parseChunks.bind(this), this;
                  }, Object.defineProperty(m.prototype, "onTrackMetadata", {
                    // prototype: function(type: string, metadata: any): void
                    get: function() {
                      return this._onTrackMetadata;
                    },
                    set: function(x) {
                      this._onTrackMetadata = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "onMediaInfo", {
                    // prototype: function(mediaInfo: MediaInfo): void
                    get: function() {
                      return this._onMediaInfo;
                    },
                    set: function(x) {
                      this._onMediaInfo = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "onMetaDataArrived", {
                    get: function() {
                      return this._onMetaDataArrived;
                    },
                    set: function(x) {
                      this._onMetaDataArrived = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "onScriptDataArrived", {
                    get: function() {
                      return this._onScriptDataArrived;
                    },
                    set: function(x) {
                      this._onScriptDataArrived = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "onError", {
                    // prototype: function(type: number, info: string): void
                    get: function() {
                      return this._onError;
                    },
                    set: function(x) {
                      this._onError = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "onDataAvailable", {
                    // prototype: function(videoTrack: any, audioTrack: any): void
                    get: function() {
                      return this._onDataAvailable;
                    },
                    set: function(x) {
                      this._onDataAvailable = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "timestampBase", {
                    // timestamp base for output samples, must be in milliseconds
                    get: function() {
                      return this._timestampBase;
                    },
                    set: function(x) {
                      this._timestampBase = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "overridedDuration", {
                    get: function() {
                      return this._duration;
                    },
                    // Force-override media duration. Must be in milliseconds, int32
                    set: function(x) {
                      this._durationOverrided = !0, this._duration = x, this._mediaInfo.duration = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "overridedHasAudio", {
                    // Force-override audio track present flag, boolean
                    set: function(x) {
                      this._hasAudioFlagOverrided = !0, this._hasAudio = x, this._mediaInfo.hasAudio = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(m.prototype, "overridedHasVideo", {
                    // Force-override video track present flag, boolean
                    set: function(x) {
                      this._hasVideoFlagOverrided = !0, this._hasVideo = x, this._mediaInfo.hasVideo = x;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), m.prototype.resetMediaInfo = function() {
                    this._mediaInfo = new f.default();
                  }, m.prototype._isInitialMetadataDispatched = function() {
                    return this._hasAudio && this._hasVideo ? this._audioInitialMetadataDispatched && this._videoInitialMetadataDispatched : this._hasAudio && !this._hasVideo ? this._audioInitialMetadataDispatched : !this._hasAudio && this._hasVideo ? this._videoInitialMetadataDispatched : !1;
                  }, m.prototype.parseChunks = function(x, y) {
                    if (!this._onError || !this._onMediaInfo || !this._onTrackMetadata || !this._onDataAvailable)
                      throw new p.IllegalStateException("Flv: onError & onMediaInfo & onTrackMetadata & onDataAvailable callback must be specified");
                    var A = 0, b = this._littleEndian;
                    if (y === 0)
                      if (x.byteLength > 13) {
                        var M = m.probe(x);
                        A = M.dataOffset;
                      } else
                        return 0;
                    if (this._firstParse) {
                      this._firstParse = !1, y + A !== this._dataOffset && c.default.w(this.TAG, "First time parsing but chunk byteStart invalid!");
                      var w = new DataView(x, A), C = w.getUint32(0, !b);
                      C !== 0 && c.default.w(this.TAG, "PrevTagSize0 !== 0 !!!"), A += 4;
                    }
                    for (; A < x.byteLength; ) {
                      this._dispatch = !0;
                      var w = new DataView(x, A);
                      if (A + 11 + 4 > x.byteLength)
                        break;
                      var T = w.getUint8(0), E = w.getUint32(0, !b) & 16777215;
                      if (A + 11 + E + 4 > x.byteLength)
                        break;
                      if (T !== 8 && T !== 9 && T !== 18) {
                        c.default.w(this.TAG, "Unsupported tag type " + T + ", skipped"), A += 11 + E + 4;
                        continue;
                      }
                      var P = w.getUint8(4), L = w.getUint8(5), I = w.getUint8(6), N = w.getUint8(7), H = I | L << 8 | P << 16 | N << 24, U = w.getUint32(7, !b) & 16777215;
                      U !== 0 && c.default.w(this.TAG, "Meet tag which has StreamID != 0!");
                      var W = A + 11;
                      switch (T) {
                        case 8:
                          this._parseAudioData(x, W, E, H);
                          break;
                        case 9:
                          this._parseVideoData(x, W, E, H, y + A);
                          break;
                        case 18:
                          this._parseScriptData(x, W, E);
                          break;
                      }
                      var X = w.getUint32(11 + E, !b);
                      X !== 11 + E && c.default.w(this.TAG, "Invalid PrevTagSize " + X), A += 11 + E + 4;
                    }
                    return this._isInitialMetadataDispatched() && this._dispatch && (this._audioTrack.length || this._videoTrack.length) && this._onDataAvailable(this._audioTrack, this._videoTrack), A;
                  }, m.prototype._parseScriptData = function(x, y, A) {
                    var b = h.default.parseScriptData(x, y, A);
                    if (b.hasOwnProperty("onMetaData")) {
                      if (b.onMetaData == null || typeof b.onMetaData != "object") {
                        c.default.w(this.TAG, "Invalid onMetaData structure!");
                        return;
                      }
                      this._metadata && c.default.w(this.TAG, "Found another onMetaData tag!"), this._metadata = b;
                      var M = this._metadata.onMetaData;
                      if (this._onMetaDataArrived && this._onMetaDataArrived(Object.assign({}, M)), typeof M.hasAudio == "boolean" && this._hasAudioFlagOverrided === !1 && (this._hasAudio = M.hasAudio, this._mediaInfo.hasAudio = this._hasAudio), typeof M.hasVideo == "boolean" && this._hasVideoFlagOverrided === !1 && (this._hasVideo = M.hasVideo, this._mediaInfo.hasVideo = this._hasVideo), typeof M.audiodatarate == "number" && (this._mediaInfo.audioDataRate = M.audiodatarate), typeof M.videodatarate == "number" && (this._mediaInfo.videoDataRate = M.videodatarate), typeof M.width == "number" && (this._mediaInfo.width = M.width), typeof M.height == "number" && (this._mediaInfo.height = M.height), typeof M.duration == "number") {
                        if (!this._durationOverrided) {
                          var w = Math.floor(M.duration * this._timescale);
                          this._duration = w, this._mediaInfo.duration = w;
                        }
                      } else
                        this._mediaInfo.duration = 0;
                      if (typeof M.framerate == "number") {
                        var C = Math.floor(M.framerate * 1e3);
                        if (C > 0) {
                          var T = C / 1e3;
                          this._referenceFrameRate.fixed = !0, this._referenceFrameRate.fps = T, this._referenceFrameRate.fps_num = C, this._referenceFrameRate.fps_den = 1e3, this._mediaInfo.fps = T;
                        }
                      }
                      if (typeof M.keyframes == "object") {
                        this._mediaInfo.hasKeyframesIndex = !0;
                        var E = M.keyframes;
                        this._mediaInfo.keyframesIndex = this._parseKeyframesIndex(E), M.keyframes = null;
                      } else
                        this._mediaInfo.hasKeyframesIndex = !1;
                      this._dispatch = !1, this._mediaInfo.metadata = M, c.default.v(this.TAG, "Parsed onMetaData"), this._mediaInfo.isComplete() && this._onMediaInfo(this._mediaInfo);
                    }
                    Object.keys(b).length > 0 && this._onScriptDataArrived && this._onScriptDataArrived(Object.assign({}, b));
                  }, m.prototype._parseKeyframesIndex = function(x) {
                    for (var y = [], A = [], b = 1; b < x.times.length; b++) {
                      var M = this._timestampBase + Math.floor(x.times[b] * 1e3);
                      y.push(M), A.push(x.filepositions[b]);
                    }
                    return {
                      times: y,
                      filepositions: A
                    };
                  }, m.prototype._parseAudioData = function(x, y, A, b) {
                    if (A <= 1) {
                      c.default.w(this.TAG, "Flv: Invalid audio packet, missing SoundData payload!");
                      return;
                    }
                    if (!(this._hasAudioFlagOverrided === !0 && this._hasAudio === !1)) {
                      this._littleEndian;
                      var M = new DataView(x, y, A), w = M.getUint8(0), C = w >>> 4;
                      if (C !== 2 && C !== 10) {
                        this._onError(d.default.CODEC_UNSUPPORTED, "Flv: Unsupported audio codec idx: " + C);
                        return;
                      }
                      var T = 0, E = (w & 12) >>> 2;
                      if (E >= 0 && E <= 4)
                        T = this._flvSoundRateTable[E];
                      else {
                        this._onError(d.default.FORMAT_ERROR, "Flv: Invalid audio sample rate idx: " + E);
                        return;
                      }
                      var P = w & 1, L = this._audioMetadata, I = this._audioTrack;
                      if (L || (this._hasAudio === !1 && this._hasAudioFlagOverrided === !1 && (this._hasAudio = !0, this._mediaInfo.hasAudio = !0), L = this._audioMetadata = {}, L.type = "audio", L.id = I.id, L.timescale = this._timescale, L.duration = this._duration, L.audioSampleRate = T, L.channelCount = P === 0 ? 1 : 2), C === 10) {
                        var N = this._parseAACAudioData(x, y + 1, A - 1);
                        if (N == null)
                          return;
                        if (N.packetType === 0) {
                          L.config && c.default.w(this.TAG, "Found another AudioSpecificConfig!");
                          var H = N.data;
                          L.audioSampleRate = H.samplingRate, L.channelCount = H.channelCount, L.codec = H.codec, L.originalCodec = H.originalCodec, L.config = H.config, L.refSampleDuration = 1024 / L.audioSampleRate * L.timescale, c.default.v(this.TAG, "Parsed AudioSpecificConfig"), this._isInitialMetadataDispatched() ? this._dispatch && (this._audioTrack.length || this._videoTrack.length) && this._onDataAvailable(this._audioTrack, this._videoTrack) : this._audioInitialMetadataDispatched = !0, this._dispatch = !1, this._onTrackMetadata("audio", L);
                          var U = this._mediaInfo;
                          U.audioCodec = L.originalCodec, U.audioSampleRate = L.audioSampleRate, U.audioChannelCount = L.channelCount, U.hasVideo ? U.videoCodec != null && (U.mimeType = 'video/x-flv; codecs="' + U.videoCodec + "," + U.audioCodec + '"') : U.mimeType = 'video/x-flv; codecs="' + U.audioCodec + '"', U.isComplete() && this._onMediaInfo(U);
                        } else if (N.packetType === 1) {
                          var W = this._timestampBase + b, X = { unit: N.data, length: N.data.byteLength, dts: W, pts: W };
                          I.samples.push(X), I.length += N.data.length;
                        } else
                          c.default.e(this.TAG, "Flv: Unsupported AAC data type " + N.packetType);
                      } else if (C === 2) {
                        if (!L.codec) {
                          var H = this._parseMP3AudioData(x, y + 1, A - 1, !0);
                          if (H == null)
                            return;
                          L.audioSampleRate = H.samplingRate, L.channelCount = H.channelCount, L.codec = H.codec, L.originalCodec = H.originalCodec, L.refSampleDuration = 1152 / L.audioSampleRate * L.timescale, c.default.v(this.TAG, "Parsed MPEG Audio Frame Header"), this._audioInitialMetadataDispatched = !0, this._onTrackMetadata("audio", L);
                          var U = this._mediaInfo;
                          U.audioCodec = L.codec, U.audioSampleRate = L.audioSampleRate, U.audioChannelCount = L.channelCount, U.audioDataRate = H.bitRate, U.hasVideo ? U.videoCodec != null && (U.mimeType = 'video/x-flv; codecs="' + U.videoCodec + "," + U.audioCodec + '"') : U.mimeType = 'video/x-flv; codecs="' + U.audioCodec + '"', U.isComplete() && this._onMediaInfo(U);
                        }
                        var K = this._parseMP3AudioData(x, y + 1, A - 1, !1);
                        if (K == null)
                          return;
                        var W = this._timestampBase + b, te = { unit: K, length: K.byteLength, dts: W, pts: W };
                        I.samples.push(te), I.length += K.length;
                      }
                    }
                  }, m.prototype._parseAACAudioData = function(x, y, A) {
                    if (A <= 1) {
                      c.default.w(this.TAG, "Flv: Invalid AAC packet, missing AACPacketType or/and Data!");
                      return;
                    }
                    var b = {}, M = new Uint8Array(x, y, A);
                    return b.packetType = M[0], M[0] === 0 ? b.data = this._parseAACAudioSpecificConfig(x, y + 1, A - 1) : b.data = M.subarray(1), b;
                  }, m.prototype._parseAACAudioSpecificConfig = function(x, y, A) {
                    var b = new Uint8Array(x, y, A), M = null, w = 0, C = 0, T = 0, E = null;
                    if (w = C = b[0] >>> 3, T = (b[0] & 7) << 1 | b[1] >>> 7, T < 0 || T >= this._mpegSamplingRates.length) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: AAC invalid sampling frequency index!");
                      return;
                    }
                    var P = this._mpegSamplingRates[T], L = (b[1] & 120) >>> 3;
                    if (L < 0 || L >= 8) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: AAC invalid channel configuration");
                      return;
                    }
                    w === 5 && (E = (b[1] & 7) << 1 | b[2] >>> 7, (b[2] & 124) >>> 2);
                    var I = self.navigator.userAgent.toLowerCase();
                    return I.indexOf("firefox") !== -1 ? T >= 6 ? (w = 5, M = new Array(4), E = T - 3) : (w = 2, M = new Array(2), E = T) : I.indexOf("android") !== -1 ? (w = 2, M = new Array(2), E = T) : (w = 5, E = T, M = new Array(4), T >= 6 ? E = T - 3 : L === 1 && (w = 2, M = new Array(2), E = T)), M[0] = w << 3, M[0] |= (T & 15) >>> 1, M[1] = (T & 15) << 7, M[1] |= (L & 15) << 3, w === 5 && (M[1] |= (E & 15) >>> 1, M[2] = (E & 1) << 7, M[2] |= 8, M[3] = 0), {
                      config: M,
                      samplingRate: P,
                      channelCount: L,
                      codec: "mp4a.40." + w,
                      originalCodec: "mp4a.40." + C
                    };
                  }, m.prototype._parseMP3AudioData = function(x, y, A, b) {
                    if (A < 4) {
                      c.default.w(this.TAG, "Flv: Invalid MP3 packet, header missing!");
                      return;
                    }
                    this._littleEndian;
                    var M = new Uint8Array(x, y, A), w = null;
                    if (b) {
                      if (M[0] !== 255)
                        return;
                      var C = M[1] >>> 3 & 3, T = (M[1] & 6) >> 1, E = (M[2] & 240) >>> 4, P = (M[2] & 12) >>> 2, L = M[3] >>> 6 & 3, I = L !== 3 ? 2 : 1, N = 0, H = 0, U = "mp3";
                      switch (C) {
                        case 0:
                          N = this._mpegAudioV25SampleRateTable[P];
                          break;
                        case 2:
                          N = this._mpegAudioV20SampleRateTable[P];
                          break;
                        case 3:
                          N = this._mpegAudioV10SampleRateTable[P];
                          break;
                      }
                      switch (T) {
                        case 1:
                          E < this._mpegAudioL3BitRateTable.length && (H = this._mpegAudioL3BitRateTable[E]);
                          break;
                        case 2:
                          E < this._mpegAudioL2BitRateTable.length && (H = this._mpegAudioL2BitRateTable[E]);
                          break;
                        case 3:
                          E < this._mpegAudioL1BitRateTable.length && (H = this._mpegAudioL1BitRateTable[E]);
                          break;
                      }
                      w = {
                        bitRate: H,
                        samplingRate: N,
                        channelCount: I,
                        codec: U,
                        originalCodec: U
                      };
                    } else
                      w = M;
                    return w;
                  }, m.prototype._parseVideoData = function(x, y, A, b, M) {
                    if (A <= 1) {
                      c.default.w(this.TAG, "Flv: Invalid video packet, missing VideoData payload!");
                      return;
                    }
                    if (!(this._hasVideoFlagOverrided === !0 && this._hasVideo === !1)) {
                      var w = new Uint8Array(x, y, A)[0], C = (w & 240) >>> 4, T = w & 15;
                      if (T !== 7) {
                        this._onError(d.default.CODEC_UNSUPPORTED, "Flv: Unsupported codec in video frame: " + T);
                        return;
                      }
                      this._parseAVCVideoPacket(x, y + 1, A - 1, b, M, C);
                    }
                  }, m.prototype._parseAVCVideoPacket = function(x, y, A, b, M, w) {
                    if (A < 4) {
                      c.default.w(this.TAG, "Flv: Invalid AVC packet, missing AVCPacketType or/and CompositionTime");
                      return;
                    }
                    var C = this._littleEndian, T = new DataView(x, y, A), E = T.getUint8(0), P = T.getUint32(0, !C) & 16777215, L = P << 8 >> 8;
                    if (E === 0)
                      this._parseAVCDecoderConfigurationRecord(x, y + 4, A - 4);
                    else if (E === 1)
                      this._parseAVCVideoData(x, y + 4, A - 4, b, M, w, L);
                    else if (E !== 2) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: Invalid video packet type " + E);
                      return;
                    }
                  }, m.prototype._parseAVCDecoderConfigurationRecord = function(x, y, A) {
                    if (A < 7) {
                      c.default.w(this.TAG, "Flv: Invalid AVCDecoderConfigurationRecord, lack of data!");
                      return;
                    }
                    var b = this._videoMetadata, M = this._videoTrack, w = this._littleEndian, C = new DataView(x, y, A);
                    b ? typeof b.avcc < "u" && c.default.w(this.TAG, "Found another AVCDecoderConfigurationRecord!") : (this._hasVideo === !1 && this._hasVideoFlagOverrided === !1 && (this._hasVideo = !0, this._mediaInfo.hasVideo = !0), b = this._videoMetadata = {}, b.type = "video", b.id = M.id, b.timescale = this._timescale, b.duration = this._duration);
                    var T = C.getUint8(0), E = C.getUint8(1);
                    if (C.getUint8(2), C.getUint8(3), T !== 1 || E === 0) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: Invalid AVCDecoderConfigurationRecord");
                      return;
                    }
                    if (this._naluLengthSize = (C.getUint8(4) & 3) + 1, this._naluLengthSize !== 3 && this._naluLengthSize !== 4) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: Strange NaluLengthSizeMinusOne: " + (this._naluLengthSize - 1));
                      return;
                    }
                    var P = C.getUint8(5) & 31;
                    if (P === 0) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: Invalid AVCDecoderConfigurationRecord: No SPS");
                      return;
                    } else
                      P > 1 && c.default.w(this.TAG, "Flv: Strange AVCDecoderConfigurationRecord: SPS Count = " + P);
                    for (var L = 6, I = 0; I < P; I++) {
                      var N = C.getUint16(L, !w);
                      if (L += 2, N !== 0) {
                        var H = new Uint8Array(x, y + L, N);
                        L += N;
                        var U = u.default.parseSPS(H);
                        if (I === 0) {
                          b.codecWidth = U.codec_size.width, b.codecHeight = U.codec_size.height, b.presentWidth = U.present_size.width, b.presentHeight = U.present_size.height, b.profile = U.profile_string, b.level = U.level_string, b.bitDepth = U.bit_depth, b.chromaFormat = U.chroma_format, b.sarRatio = U.sar_ratio, b.frameRate = U.frame_rate, (U.frame_rate.fixed === !1 || U.frame_rate.fps_num === 0 || U.frame_rate.fps_den === 0) && (b.frameRate = this._referenceFrameRate);
                          var W = b.frameRate.fps_den, X = b.frameRate.fps_num;
                          b.refSampleDuration = b.timescale * (W / X);
                          for (var K = H.subarray(1, 4), te = "avc1.", re = 0; re < 3; re++) {
                            var be = K[re].toString(16);
                            be.length < 2 && (be = "0" + be), te += be;
                          }
                          b.codec = te;
                          var Pe = this._mediaInfo;
                          Pe.width = b.codecWidth, Pe.height = b.codecHeight, Pe.fps = b.frameRate.fps, Pe.profile = b.profile, Pe.level = b.level, Pe.refFrames = U.ref_frames, Pe.chromaFormat = U.chroma_format_string, Pe.sarNum = b.sarRatio.width, Pe.sarDen = b.sarRatio.height, Pe.videoCodec = te, Pe.hasAudio ? Pe.audioCodec != null && (Pe.mimeType = 'video/x-flv; codecs="' + Pe.videoCodec + "," + Pe.audioCodec + '"') : Pe.mimeType = 'video/x-flv; codecs="' + Pe.videoCodec + '"', Pe.isComplete() && this._onMediaInfo(Pe);
                        }
                      }
                    }
                    var J = C.getUint8(L);
                    if (J === 0) {
                      this._onError(d.default.FORMAT_ERROR, "Flv: Invalid AVCDecoderConfigurationRecord: No PPS");
                      return;
                    } else
                      J > 1 && c.default.w(this.TAG, "Flv: Strange AVCDecoderConfigurationRecord: PPS Count = " + J);
                    L++;
                    for (var I = 0; I < J; I++) {
                      var N = C.getUint16(L, !w);
                      L += 2, N !== 0 && (L += N);
                    }
                    b.avcc = new Uint8Array(A), b.avcc.set(new Uint8Array(x, y, A), 0), c.default.v(this.TAG, "Parsed AVCDecoderConfigurationRecord"), this._isInitialMetadataDispatched() ? this._dispatch && (this._audioTrack.length || this._videoTrack.length) && this._onDataAvailable(this._audioTrack, this._videoTrack) : this._videoInitialMetadataDispatched = !0, this._dispatch = !1, this._onTrackMetadata("video", b);
                  }, m.prototype._parseAVCVideoData = function(x, y, A, b, M, w, C) {
                    for (var T = this._littleEndian, E = new DataView(x, y, A), P = [], L = 0, I = 0, N = this._naluLengthSize, H = this._timestampBase + b, U = w === 1; I < A; ) {
                      if (I + 4 >= A) {
                        c.default.w(this.TAG, "Malformed Nalu near timestamp " + H + ", offset = " + I + ", dataSize = " + A);
                        break;
                      }
                      var W = E.getUint32(I, !T);
                      if (N === 3 && (W >>>= 8), W > A - N) {
                        c.default.w(this.TAG, "Malformed Nalus near timestamp " + H + ", NaluSize > DataSize!");
                        return;
                      }
                      var X = E.getUint8(I + N) & 31;
                      X === 5 && (U = !0);
                      var K = new Uint8Array(x, y + I, N + W), te = { type: X, data: K };
                      P.push(te), L += K.byteLength, I += N + W;
                    }
                    if (P.length) {
                      var re = this._videoTrack, be = {
                        units: P,
                        length: L,
                        isKeyframe: U,
                        dts: H,
                        cts: C,
                        pts: H + C
                      };
                      U && (be.fileposition = M), re.samples.push(be), re.length += L;
                    }
                  }, m;
                }()
              );
              o.default = g;
            }
          ),
          /***/
          "./src/demux/sps-parser.js": (
            /*!*********************************!*\
              !*** ./src/demux/sps-parser.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ./exp-golomb.js */
                "./src/demux/exp-golomb.js"
              ), h = (
                /** @class */
                function() {
                  function u() {
                  }
                  return u._ebsp2rbsp = function(d) {
                    for (var f = d, p = f.byteLength, v = new Uint8Array(p), g = 0, m = 0; m < p; m++)
                      m >= 2 && f[m] === 3 && f[m - 1] === 0 && f[m - 2] === 0 || (v[g] = f[m], g++);
                    return new Uint8Array(v.buffer, 0, g);
                  }, u.parseSPS = function(d) {
                    var f = u._ebsp2rbsp(d), p = new c.default(f);
                    p.readByte();
                    var v = p.readByte();
                    p.readByte();
                    var g = p.readByte();
                    p.readUEG();
                    var m = u.getProfileString(v), x = u.getLevelString(g), y = 1, A = 420, b = [0, 420, 422, 444], M = 8;
                    if ((v === 100 || v === 110 || v === 122 || v === 244 || v === 44 || v === 83 || v === 86 || v === 118 || v === 128 || v === 138 || v === 144) && (y = p.readUEG(), y === 3 && p.readBits(1), y <= 3 && (A = b[y]), M = p.readUEG() + 8, p.readUEG(), p.readBits(1), p.readBool()))
                      for (var w = y !== 3 ? 8 : 12, C = 0; C < w; C++)
                        p.readBool() && (C < 6 ? u._skipScalingList(p, 16) : u._skipScalingList(p, 64));
                    p.readUEG();
                    var T = p.readUEG();
                    if (T === 0)
                      p.readUEG();
                    else if (T === 1) {
                      p.readBits(1), p.readSEG(), p.readSEG();
                      for (var E = p.readUEG(), C = 0; C < E; C++)
                        p.readSEG();
                    }
                    var P = p.readUEG();
                    p.readBits(1);
                    var L = p.readUEG(), I = p.readUEG(), N = p.readBits(1);
                    N === 0 && p.readBits(1), p.readBits(1);
                    var H = 0, U = 0, W = 0, X = 0, K = p.readBool();
                    K && (H = p.readUEG(), U = p.readUEG(), W = p.readUEG(), X = p.readUEG());
                    var te = 1, re = 1, be = 0, Pe = !0, J = 0, fe = 0, Me = p.readBool();
                    if (Me) {
                      if (p.readBool()) {
                        var ye = p.readByte(), Ye = [1, 12, 10, 16, 40, 24, 20, 32, 80, 18, 15, 64, 160, 4, 3, 2], Je = [1, 11, 11, 11, 33, 11, 11, 11, 33, 11, 11, 33, 99, 3, 2, 1];
                        ye > 0 && ye < 16 ? (te = Ye[ye - 1], re = Je[ye - 1]) : ye === 255 && (te = p.readByte() << 8 | p.readByte(), re = p.readByte() << 8 | p.readByte());
                      }
                      if (p.readBool() && p.readBool(), p.readBool() && (p.readBits(4), p.readBool() && p.readBits(24)), p.readBool() && (p.readUEG(), p.readUEG()), p.readBool()) {
                        var rt = p.readBits(32), wt = p.readBits(32);
                        Pe = p.readBool(), J = wt, fe = rt * 2, be = J / fe;
                      }
                    }
                    var k = 1;
                    (te !== 1 || re !== 1) && (k = te / re);
                    var Fe = 0, Ge = 0;
                    if (y === 0)
                      Fe = 1, Ge = 2 - N;
                    else {
                      var qe = y === 3 ? 1 : 2, Ie = y === 1 ? 2 : 1;
                      Fe = qe, Ge = Ie * (2 - N);
                    }
                    var vt = (L + 1) * 16, Ve = (2 - N) * ((I + 1) * 16);
                    vt -= (H + U) * Fe, Ve -= (W + X) * Ge;
                    var Ze = Math.ceil(vt * k);
                    return p.destroy(), p = null, {
                      profile_string: m,
                      level_string: x,
                      bit_depth: M,
                      ref_frames: P,
                      chroma_format: A,
                      chroma_format_string: u.getChromaFormatString(A),
                      frame_rate: {
                        fixed: Pe,
                        fps: be,
                        fps_den: fe,
                        fps_num: J
                      },
                      sar_ratio: {
                        width: te,
                        height: re
                      },
                      codec_size: {
                        width: vt,
                        height: Ve
                      },
                      present_size: {
                        width: Ze,
                        height: Ve
                      }
                    };
                  }, u._skipScalingList = function(d, f) {
                    for (var p = 8, v = 8, g = 0, m = 0; m < f; m++)
                      v !== 0 && (g = d.readSEG(), v = (p + g + 256) % 256), p = v === 0 ? p : v;
                  }, u.getProfileString = function(d) {
                    switch (d) {
                      case 66:
                        return "Baseline";
                      case 77:
                        return "Main";
                      case 88:
                        return "Extended";
                      case 100:
                        return "High";
                      case 110:
                        return "High10";
                      case 122:
                        return "High422";
                      case 244:
                        return "High444";
                      default:
                        return "Unknown";
                    }
                  }, u.getLevelString = function(d) {
                    return (d / 10).toFixed(1);
                  }, u.getChromaFormatString = function(d) {
                    switch (d) {
                      case 420:
                        return "4:2:0";
                      case 422:
                        return "4:2:2";
                      case 444:
                        return "4:4:4";
                      default:
                        return "Unknown";
                    }
                  }, u;
                }()
              );
              o.default = h;
            }
          ),
          /***/
          "./src/flv.js": (
            /*!********************!*\
              !*** ./src/flv.js ***!
              \********************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ./utils/polyfill.js */
                "./src/utils/polyfill.js"
              ), h = l(
                /*! ./core/features.js */
                "./src/core/features.js"
              ), u = l(
                /*! ./io/loader.js */
                "./src/io/loader.js"
              ), d = l(
                /*! ./player/flv-player.js */
                "./src/player/flv-player.js"
              ), f = l(
                /*! ./player/native-player.js */
                "./src/player/native-player.js"
              ), p = l(
                /*! ./player/player-events.js */
                "./src/player/player-events.js"
              ), v = l(
                /*! ./player/player-errors.js */
                "./src/player/player-errors.js"
              ), g = l(
                /*! ./utils/logging-control.js */
                "./src/utils/logging-control.js"
              ), m = l(
                /*! ./utils/exception.js */
                "./src/utils/exception.js"
              );
              c.default.install();
              function x(M, w) {
                var C = M;
                if (C == null || typeof C != "object")
                  throw new m.InvalidArgumentException("MediaDataSource must be an javascript object!");
                if (!C.hasOwnProperty("type"))
                  throw new m.InvalidArgumentException("MediaDataSource must has type field to indicate video file type!");
                switch (C.type) {
                  case "flv":
                    return new d.default(C, w);
                  default:
                    return new f.default(C, w);
                }
              }
              function y() {
                return h.default.supportMSEH264Playback();
              }
              function A() {
                return h.default.getFeatureList();
              }
              var b = {};
              b.createPlayer = x, b.isSupported = y, b.getFeatureList = A, b.BaseLoader = u.BaseLoader, b.LoaderStatus = u.LoaderStatus, b.LoaderErrors = u.LoaderErrors, b.Events = p.default, b.ErrorTypes = v.ErrorTypes, b.ErrorDetails = v.ErrorDetails, b.FlvPlayer = d.default, b.NativePlayer = f.default, b.LoggingControl = g.default, Object.defineProperty(b, "version", {
                enumerable: !0,
                get: function() {
                  return "1.6.2";
                }
              }), o.default = b;
            }
          ),
          /***/
          "./src/index.js": (
            /*!**********************!*\
              !*** ./src/index.js ***!
              \**********************/
            /***/
            function(a, o, l) {
              a.exports = l(
                /*! ./flv.js */
                "./src/flv.js"
              ).default;
            }
          ),
          /***/
          "./src/io/fetch-stream-loader.js": (
            /*!***************************************!*\
              !*** ./src/io/fetch-stream-loader.js ***!
              \***************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/browser.js */
                "./src/utils/browser.js"
              ), h = l(
                /*! ./loader.js */
                "./src/io/loader.js"
              ), u = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), d = function() {
                var p = function(v, g) {
                  return p = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(m, x) {
                    m.__proto__ = x;
                  } || function(m, x) {
                    for (var y in x)
                      Object.prototype.hasOwnProperty.call(x, y) && (m[y] = x[y]);
                  }, p(v, g);
                };
                return function(v, g) {
                  if (typeof g != "function" && g !== null)
                    throw new TypeError("Class extends value " + String(g) + " is not a constructor or null");
                  p(v, g);
                  function m() {
                    this.constructor = v;
                  }
                  v.prototype = g === null ? Object.create(g) : (m.prototype = g.prototype, new m());
                };
              }(), f = (
                /** @class */
                function(p) {
                  d(v, p);
                  function v(g, m) {
                    var x = p.call(this, "fetch-stream-loader") || this;
                    return x.TAG = "FetchStreamLoader", x._seekHandler = g, x._config = m, x._needStash = !0, x._requestAbort = !1, x._contentLength = null, x._receivedLength = 0, x;
                  }
                  return v.isSupported = function() {
                    try {
                      var g = c.default.msedge && c.default.version.minor >= 15048, m = c.default.msedge ? g : !0;
                      return self.fetch && self.ReadableStream && m;
                    } catch {
                      return !1;
                    }
                  }, v.prototype.destroy = function() {
                    this.isWorking() && this.abort(), p.prototype.destroy.call(this);
                  }, v.prototype.open = function(g, m) {
                    var x = this;
                    this._dataSource = g, this._range = m;
                    var y = g.url;
                    this._config.reuseRedirectedURL && g.redirectedURL != null && (y = g.redirectedURL);
                    var A = this._seekHandler.getConfig(y, m), b = new self.Headers();
                    if (typeof A.headers == "object") {
                      var M = A.headers;
                      for (var w in M)
                        M.hasOwnProperty(w) && b.append(w, M[w]);
                    }
                    var C = {
                      method: "GET",
                      headers: b,
                      mode: "cors",
                      cache: "default",
                      // The default policy of Fetch API in the whatwg standard
                      // Safari incorrectly indicates 'no-referrer' as default policy, fuck it
                      referrerPolicy: "no-referrer-when-downgrade"
                    };
                    if (typeof this._config.headers == "object")
                      for (var w in this._config.headers)
                        b.append(w, this._config.headers[w]);
                    g.cors === !1 && (C.mode = "same-origin"), g.withCredentials && (C.credentials = "include"), g.referrerPolicy && (C.referrerPolicy = g.referrerPolicy), self.AbortController && (this._abortController = new self.AbortController(), C.signal = this._abortController.signal), this._status = h.LoaderStatus.kConnecting, self.fetch(A.url, C).then(function(T) {
                      if (x._requestAbort) {
                        x._status = h.LoaderStatus.kIdle, T.body.cancel();
                        return;
                      }
                      if (T.ok && T.status >= 200 && T.status <= 299) {
                        if (T.url !== A.url && x._onURLRedirect) {
                          var E = x._seekHandler.removeURLParameters(T.url);
                          x._onURLRedirect(E);
                        }
                        var P = T.headers.get("Content-Length");
                        return P != null && (x._contentLength = parseInt(P), x._contentLength !== 0 && x._onContentLengthKnown && x._onContentLengthKnown(x._contentLength)), x._pump.call(x, T.body.getReader());
                      } else if (x._status = h.LoaderStatus.kError, x._onError)
                        x._onError(h.LoaderErrors.HTTP_STATUS_CODE_INVALID, { code: T.status, msg: T.statusText });
                      else
                        throw new u.RuntimeException("FetchStreamLoader: Http code invalid, " + T.status + " " + T.statusText);
                    }).catch(function(T) {
                      if (!(x._abortController && x._abortController.signal.aborted))
                        if (x._status = h.LoaderStatus.kError, x._onError)
                          x._onError(h.LoaderErrors.EXCEPTION, { code: -1, msg: T.message });
                        else
                          throw T;
                    });
                  }, v.prototype.abort = function() {
                    if (this._requestAbort = !0, (this._status !== h.LoaderStatus.kBuffering || !c.default.chrome) && this._abortController)
                      try {
                        this._abortController.abort();
                      } catch {
                      }
                  }, v.prototype._pump = function(g) {
                    var m = this;
                    return g.read().then(function(x) {
                      if (x.done)
                        if (m._contentLength !== null && m._receivedLength < m._contentLength) {
                          m._status = h.LoaderStatus.kError;
                          var y = h.LoaderErrors.EARLY_EOF, A = { code: -1, msg: "Fetch stream meet Early-EOF" };
                          if (m._onError)
                            m._onError(y, A);
                          else
                            throw new u.RuntimeException(A.msg);
                        } else
                          m._status = h.LoaderStatus.kComplete, m._onComplete && m._onComplete(m._range.from, m._range.from + m._receivedLength - 1);
                      else {
                        if (m._abortController && m._abortController.signal.aborted) {
                          m._status = h.LoaderStatus.kComplete;
                          return;
                        } else if (m._requestAbort === !0)
                          return m._status = h.LoaderStatus.kComplete, g.cancel();
                        m._status = h.LoaderStatus.kBuffering;
                        var b = x.value.buffer, M = m._range.from + m._receivedLength;
                        m._receivedLength += b.byteLength, m._onDataArrival && m._onDataArrival(b, M, m._receivedLength), m._pump(g);
                      }
                    }).catch(function(x) {
                      if (m._abortController && m._abortController.signal.aborted) {
                        m._status = h.LoaderStatus.kComplete;
                        return;
                      }
                      if (!(x.code === 11 && c.default.msedge)) {
                        m._status = h.LoaderStatus.kError;
                        var y = 0, A = null;
                        if ((x.code === 19 || x.message === "network error") && // NETWORK_ERR
                        (m._contentLength === null || m._contentLength !== null && m._receivedLength < m._contentLength) ? (y = h.LoaderErrors.EARLY_EOF, A = { code: x.code, msg: "Fetch stream meet Early-EOF" }) : (y = h.LoaderErrors.EXCEPTION, A = { code: x.code, msg: x.message }), m._onError)
                          m._onError(y, A);
                        else
                          throw new u.RuntimeException(A.msg);
                      }
                    });
                  }, v;
                }(h.BaseLoader)
              );
              o.default = f;
            }
          ),
          /***/
          "./src/io/io-controller.js": (
            /*!*********************************!*\
              !*** ./src/io/io-controller.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ./speed-sampler.js */
                "./src/io/speed-sampler.js"
              ), u = l(
                /*! ./loader.js */
                "./src/io/loader.js"
              ), d = l(
                /*! ./fetch-stream-loader.js */
                "./src/io/fetch-stream-loader.js"
              ), f = l(
                /*! ./xhr-moz-chunked-loader.js */
                "./src/io/xhr-moz-chunked-loader.js"
              ), p = l(
                /*! ./xhr-range-loader.js */
                "./src/io/xhr-range-loader.js"
              ), v = l(
                /*! ./websocket-loader.js */
                "./src/io/websocket-loader.js"
              ), g = l(
                /*! ./range-seek-handler.js */
                "./src/io/range-seek-handler.js"
              ), m = l(
                /*! ./param-seek-handler.js */
                "./src/io/param-seek-handler.js"
              ), x = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), y = (
                /** @class */
                function() {
                  function A(b, M, w) {
                    this.TAG = "IOController", this._config = M, this._extraData = w, this._stashInitialSize = 1024 * 384, M.stashInitialSize != null && M.stashInitialSize > 0 && (this._stashInitialSize = M.stashInitialSize), this._stashUsed = 0, this._stashSize = this._stashInitialSize, this._bufferSize = 1024 * 1024 * 3, this._stashBuffer = new ArrayBuffer(this._bufferSize), this._stashByteStart = 0, this._enableStash = !0, M.enableStashBuffer === !1 && (this._enableStash = !1), this._loader = null, this._loaderClass = null, this._seekHandler = null, this._dataSource = b, this._isWebSocketURL = /wss?:\/\/(.+?)/.test(b.url), this._refTotalLength = b.filesize ? b.filesize : null, this._totalLength = this._refTotalLength, this._fullRequestFlag = !1, this._currentRange = null, this._redirectedURL = null, this._speedNormalized = 0, this._speedSampler = new h.default(), this._speedNormalizeList = [64, 128, 256, 384, 512, 768, 1024, 1536, 2048, 3072, 4096], this._isEarlyEofReconnecting = !1, this._paused = !1, this._resumeFrom = 0, this._onDataArrival = null, this._onSeeked = null, this._onError = null, this._onComplete = null, this._onRedirect = null, this._onRecoveredEarlyEof = null, this._selectSeekHandler(), this._selectLoader(), this._createLoader();
                  }
                  return A.prototype.destroy = function() {
                    this._loader.isWorking() && this._loader.abort(), this._loader.destroy(), this._loader = null, this._loaderClass = null, this._dataSource = null, this._stashBuffer = null, this._stashUsed = this._stashSize = this._bufferSize = this._stashByteStart = 0, this._currentRange = null, this._speedSampler = null, this._isEarlyEofReconnecting = !1, this._onDataArrival = null, this._onSeeked = null, this._onError = null, this._onComplete = null, this._onRedirect = null, this._onRecoveredEarlyEof = null, this._extraData = null;
                  }, A.prototype.isWorking = function() {
                    return this._loader && this._loader.isWorking() && !this._paused;
                  }, A.prototype.isPaused = function() {
                    return this._paused;
                  }, Object.defineProperty(A.prototype, "status", {
                    get: function() {
                      return this._loader.status;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "extraData", {
                    get: function() {
                      return this._extraData;
                    },
                    set: function(b) {
                      this._extraData = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onDataArrival", {
                    // prototype: function onDataArrival(chunks: ArrayBuffer, byteStart: number): number
                    get: function() {
                      return this._onDataArrival;
                    },
                    set: function(b) {
                      this._onDataArrival = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onSeeked", {
                    get: function() {
                      return this._onSeeked;
                    },
                    set: function(b) {
                      this._onSeeked = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onError", {
                    // prototype: function onError(type: number, info: {code: number, msg: string}): void
                    get: function() {
                      return this._onError;
                    },
                    set: function(b) {
                      this._onError = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onComplete", {
                    get: function() {
                      return this._onComplete;
                    },
                    set: function(b) {
                      this._onComplete = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onRedirect", {
                    get: function() {
                      return this._onRedirect;
                    },
                    set: function(b) {
                      this._onRedirect = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "onRecoveredEarlyEof", {
                    get: function() {
                      return this._onRecoveredEarlyEof;
                    },
                    set: function(b) {
                      this._onRecoveredEarlyEof = b;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "currentURL", {
                    get: function() {
                      return this._dataSource.url;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "hasRedirect", {
                    get: function() {
                      return this._redirectedURL != null || this._dataSource.redirectedURL != null;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "currentRedirectedURL", {
                    get: function() {
                      return this._redirectedURL || this._dataSource.redirectedURL;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "currentSpeed", {
                    // in KB/s
                    get: function() {
                      return this._loaderClass === p.default ? this._loader.currentSpeed : this._speedSampler.lastSecondKBps;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(A.prototype, "loaderType", {
                    get: function() {
                      return this._loader.type;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), A.prototype._selectSeekHandler = function() {
                    var b = this._config;
                    if (b.seekType === "range")
                      this._seekHandler = new g.default(this._config.rangeLoadZeroStart);
                    else if (b.seekType === "param") {
                      var M = b.seekParamStart || "bstart", w = b.seekParamEnd || "bend";
                      this._seekHandler = new m.default(M, w);
                    } else if (b.seekType === "custom") {
                      if (typeof b.customSeekHandler != "function")
                        throw new x.InvalidArgumentException("Custom seekType specified in config but invalid customSeekHandler!");
                      this._seekHandler = new b.customSeekHandler();
                    } else
                      throw new x.InvalidArgumentException("Invalid seekType in config: " + b.seekType);
                  }, A.prototype._selectLoader = function() {
                    if (this._config.customLoader != null)
                      this._loaderClass = this._config.customLoader;
                    else if (this._isWebSocketURL)
                      this._loaderClass = v.default;
                    else if (d.default.isSupported())
                      this._loaderClass = d.default;
                    else if (f.default.isSupported())
                      this._loaderClass = f.default;
                    else if (p.default.isSupported())
                      this._loaderClass = p.default;
                    else
                      throw new x.RuntimeException("Your browser doesn't support xhr with arraybuffer responseType!");
                  }, A.prototype._createLoader = function() {
                    this._loader = new this._loaderClass(this._seekHandler, this._config), this._loader.needStashBuffer === !1 && (this._enableStash = !1), this._loader.onContentLengthKnown = this._onContentLengthKnown.bind(this), this._loader.onURLRedirect = this._onURLRedirect.bind(this), this._loader.onDataArrival = this._onLoaderChunkArrival.bind(this), this._loader.onComplete = this._onLoaderComplete.bind(this), this._loader.onError = this._onLoaderError.bind(this);
                  }, A.prototype.open = function(b) {
                    this._currentRange = { from: 0, to: -1 }, b && (this._currentRange.from = b), this._speedSampler.reset(), b || (this._fullRequestFlag = !0), this._loader.open(this._dataSource, Object.assign({}, this._currentRange));
                  }, A.prototype.abort = function() {
                    this._loader.abort(), this._paused && (this._paused = !1, this._resumeFrom = 0);
                  }, A.prototype.pause = function() {
                    this.isWorking() && (this._loader.abort(), this._stashUsed !== 0 ? (this._resumeFrom = this._stashByteStart, this._currentRange.to = this._stashByteStart - 1) : this._resumeFrom = this._currentRange.to + 1, this._stashUsed = 0, this._stashByteStart = 0, this._paused = !0);
                  }, A.prototype.resume = function() {
                    if (this._paused) {
                      this._paused = !1;
                      var b = this._resumeFrom;
                      this._resumeFrom = 0, this._internalSeek(b, !0);
                    }
                  }, A.prototype.seek = function(b) {
                    this._paused = !1, this._stashUsed = 0, this._stashByteStart = 0, this._internalSeek(b, !0);
                  }, A.prototype._internalSeek = function(b, M) {
                    this._loader.isWorking() && this._loader.abort(), this._flushStashBuffer(M), this._loader.destroy(), this._loader = null;
                    var w = { from: b, to: -1 };
                    this._currentRange = { from: w.from, to: -1 }, this._speedSampler.reset(), this._stashSize = this._stashInitialSize, this._createLoader(), this._loader.open(this._dataSource, w), this._onSeeked && this._onSeeked();
                  }, A.prototype.updateUrl = function(b) {
                    if (!b || typeof b != "string" || b.length === 0)
                      throw new x.InvalidArgumentException("Url must be a non-empty string!");
                    this._dataSource.url = b;
                  }, A.prototype._expandBuffer = function(b) {
                    for (var M = this._stashSize; M + 1024 * 1024 * 1 < b; )
                      M *= 2;
                    if (M += 1024 * 1024 * 1, M !== this._bufferSize) {
                      var w = new ArrayBuffer(M);
                      if (this._stashUsed > 0) {
                        var C = new Uint8Array(this._stashBuffer, 0, this._stashUsed), T = new Uint8Array(w, 0, M);
                        T.set(C, 0);
                      }
                      this._stashBuffer = w, this._bufferSize = M;
                    }
                  }, A.prototype._normalizeSpeed = function(b) {
                    var M = this._speedNormalizeList, w = M.length - 1, C = 0, T = 0, E = w;
                    if (b < M[0])
                      return M[0];
                    for (; T <= E; ) {
                      if (C = T + Math.floor((E - T) / 2), C === w || b >= M[C] && b < M[C + 1])
                        return M[C];
                      M[C] < b ? T = C + 1 : E = C - 1;
                    }
                  }, A.prototype._adjustStashSize = function(b) {
                    var M = 0;
                    this._config.isLive || b < 512 ? M = b : b >= 512 && b <= 1024 ? M = Math.floor(b * 1.5) : M = b * 2, M > 8192 && (M = 8192);
                    var w = M * 1024 + 1024 * 1024 * 1;
                    this._bufferSize < w && this._expandBuffer(w), this._stashSize = M * 1024;
                  }, A.prototype._dispatchChunks = function(b, M) {
                    return this._currentRange.to = M + b.byteLength - 1, this._onDataArrival(b, M);
                  }, A.prototype._onURLRedirect = function(b) {
                    this._redirectedURL = b, this._onRedirect && this._onRedirect(b);
                  }, A.prototype._onContentLengthKnown = function(b) {
                    b && this._fullRequestFlag && (this._totalLength = b, this._fullRequestFlag = !1);
                  }, A.prototype._onLoaderChunkArrival = function(b, M, w) {
                    if (!this._onDataArrival)
                      throw new x.IllegalStateException("IOController: No existing consumer (onDataArrival) callback!");
                    if (!this._paused) {
                      this._isEarlyEofReconnecting && (this._isEarlyEofReconnecting = !1, this._onRecoveredEarlyEof && this._onRecoveredEarlyEof()), this._speedSampler.addBytes(b.byteLength);
                      var C = this._speedSampler.lastSecondKBps;
                      if (C !== 0) {
                        var T = this._normalizeSpeed(C);
                        this._speedNormalized !== T && (this._speedNormalized = T, this._adjustStashSize(T));
                      }
                      if (this._enableStash)
                        if (this._stashUsed === 0 && this._stashByteStart === 0 && (this._stashByteStart = M), this._stashUsed + b.byteLength <= this._stashSize) {
                          var L = new Uint8Array(this._stashBuffer, 0, this._stashSize);
                          L.set(new Uint8Array(b), this._stashUsed), this._stashUsed += b.byteLength;
                        } else {
                          var L = new Uint8Array(this._stashBuffer, 0, this._bufferSize);
                          if (this._stashUsed > 0) {
                            var N = this._stashBuffer.slice(0, this._stashUsed), E = this._dispatchChunks(N, this._stashByteStart);
                            if (E < N.byteLength) {
                              if (E > 0) {
                                var I = new Uint8Array(N, E);
                                L.set(I, 0), this._stashUsed = I.byteLength, this._stashByteStart += E;
                              }
                            } else
                              this._stashUsed = 0, this._stashByteStart += E;
                            this._stashUsed + b.byteLength > this._bufferSize && (this._expandBuffer(this._stashUsed + b.byteLength), L = new Uint8Array(this._stashBuffer, 0, this._bufferSize)), L.set(new Uint8Array(b), this._stashUsed), this._stashUsed += b.byteLength;
                          } else {
                            var E = this._dispatchChunks(b, M);
                            if (E < b.byteLength) {
                              var P = b.byteLength - E;
                              P > this._bufferSize && (this._expandBuffer(P), L = new Uint8Array(this._stashBuffer, 0, this._bufferSize)), L.set(new Uint8Array(b, E), 0), this._stashUsed += P, this._stashByteStart = M + E;
                            }
                          }
                        }
                      else if (this._stashUsed === 0) {
                        var E = this._dispatchChunks(b, M);
                        if (E < b.byteLength) {
                          var P = b.byteLength - E;
                          P > this._bufferSize && this._expandBuffer(P);
                          var L = new Uint8Array(this._stashBuffer, 0, this._bufferSize);
                          L.set(new Uint8Array(b, E), 0), this._stashUsed += P, this._stashByteStart = M + E;
                        }
                      } else {
                        this._stashUsed + b.byteLength > this._bufferSize && this._expandBuffer(this._stashUsed + b.byteLength);
                        var L = new Uint8Array(this._stashBuffer, 0, this._bufferSize);
                        L.set(new Uint8Array(b), this._stashUsed), this._stashUsed += b.byteLength;
                        var E = this._dispatchChunks(this._stashBuffer.slice(0, this._stashUsed), this._stashByteStart);
                        if (E < this._stashUsed && E > 0) {
                          var I = new Uint8Array(this._stashBuffer, E);
                          L.set(I, 0);
                        }
                        this._stashUsed -= E, this._stashByteStart += E;
                      }
                    }
                  }, A.prototype._flushStashBuffer = function(b) {
                    if (this._stashUsed > 0) {
                      var M = this._stashBuffer.slice(0, this._stashUsed), w = this._dispatchChunks(M, this._stashByteStart), C = M.byteLength - w;
                      if (w < M.byteLength)
                        if (b)
                          c.default.w(this.TAG, C + " bytes unconsumed data remain when flush buffer, dropped");
                        else {
                          if (w > 0) {
                            var T = new Uint8Array(this._stashBuffer, 0, this._bufferSize), E = new Uint8Array(M, w);
                            T.set(E, 0), this._stashUsed = E.byteLength, this._stashByteStart += w;
                          }
                          return 0;
                        }
                      return this._stashUsed = 0, this._stashByteStart = 0, C;
                    }
                    return 0;
                  }, A.prototype._onLoaderComplete = function(b, M) {
                    this._flushStashBuffer(!0), this._onComplete && this._onComplete(this._extraData);
                  }, A.prototype._onLoaderError = function(b, M) {
                    switch (c.default.e(this.TAG, "Loader error, code = " + M.code + ", msg = " + M.msg), this._flushStashBuffer(!1), this._isEarlyEofReconnecting && (this._isEarlyEofReconnecting = !1, b = u.LoaderErrors.UNRECOVERABLE_EARLY_EOF), b) {
                      case u.LoaderErrors.EARLY_EOF: {
                        if (!this._config.isLive && this._totalLength) {
                          var w = this._currentRange.to + 1;
                          w < this._totalLength && (c.default.w(this.TAG, "Connection lost, trying reconnect..."), this._isEarlyEofReconnecting = !0, this._internalSeek(w, !1));
                          return;
                        }
                        b = u.LoaderErrors.UNRECOVERABLE_EARLY_EOF;
                        break;
                      }
                      case u.LoaderErrors.UNRECOVERABLE_EARLY_EOF:
                      case u.LoaderErrors.CONNECTING_TIMEOUT:
                      case u.LoaderErrors.HTTP_STATUS_CODE_INVALID:
                      case u.LoaderErrors.EXCEPTION:
                        break;
                    }
                    if (this._onError)
                      this._onError(b, M);
                    else
                      throw new x.RuntimeException("IOException: " + M.msg);
                  }, A;
                }()
              );
              o.default = y;
            }
          ),
          /***/
          "./src/io/loader.js": (
            /*!**************************!*\
              !*** ./src/io/loader.js ***!
              \**************************/
            /***/
            function(a, o, l) {
              l.r(o), l.d(o, {
                /* harmony export */
                LoaderStatus: function() {
                  return (
                    /* binding */
                    h
                  );
                },
                /* harmony export */
                LoaderErrors: function() {
                  return (
                    /* binding */
                    u
                  );
                },
                /* harmony export */
                BaseLoader: function() {
                  return (
                    /* binding */
                    d
                  );
                }
                /* harmony export */
              });
              var c = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), h = {
                kIdle: 0,
                kConnecting: 1,
                kBuffering: 2,
                kError: 3,
                kComplete: 4
              }, u = {
                OK: "OK",
                EXCEPTION: "Exception",
                HTTP_STATUS_CODE_INVALID: "HttpStatusCodeInvalid",
                CONNECTING_TIMEOUT: "ConnectingTimeout",
                EARLY_EOF: "EarlyEof",
                UNRECOVERABLE_EARLY_EOF: "UnrecoverableEarlyEof"
              }, d = (
                /** @class */
                function() {
                  function f(p) {
                    this._type = p || "undefined", this._status = h.kIdle, this._needStash = !1, this._onContentLengthKnown = null, this._onURLRedirect = null, this._onDataArrival = null, this._onError = null, this._onComplete = null;
                  }
                  return f.prototype.destroy = function() {
                    this._status = h.kIdle, this._onContentLengthKnown = null, this._onURLRedirect = null, this._onDataArrival = null, this._onError = null, this._onComplete = null;
                  }, f.prototype.isWorking = function() {
                    return this._status === h.kConnecting || this._status === h.kBuffering;
                  }, Object.defineProperty(f.prototype, "type", {
                    get: function() {
                      return this._type;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "status", {
                    get: function() {
                      return this._status;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "needStashBuffer", {
                    get: function() {
                      return this._needStash;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "onContentLengthKnown", {
                    get: function() {
                      return this._onContentLengthKnown;
                    },
                    set: function(p) {
                      this._onContentLengthKnown = p;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "onURLRedirect", {
                    get: function() {
                      return this._onURLRedirect;
                    },
                    set: function(p) {
                      this._onURLRedirect = p;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "onDataArrival", {
                    get: function() {
                      return this._onDataArrival;
                    },
                    set: function(p) {
                      this._onDataArrival = p;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "onError", {
                    get: function() {
                      return this._onError;
                    },
                    set: function(p) {
                      this._onError = p;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f.prototype, "onComplete", {
                    get: function() {
                      return this._onComplete;
                    },
                    set: function(p) {
                      this._onComplete = p;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), f.prototype.open = function(p, v) {
                    throw new c.NotImplementedException("Unimplemented abstract function!");
                  }, f.prototype.abort = function() {
                    throw new c.NotImplementedException("Unimplemented abstract function!");
                  }, f;
                }()
              );
            }
          ),
          /***/
          "./src/io/param-seek-handler.js": (
            /*!**************************************!*\
              !*** ./src/io/param-seek-handler.js ***!
              \**************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h(u, d) {
                    this._startName = u, this._endName = d;
                  }
                  return h.prototype.getConfig = function(u, d) {
                    var f = u;
                    if (d.from !== 0 || d.to !== -1) {
                      var p = !0;
                      f.indexOf("?") === -1 && (f += "?", p = !1), p && (f += "&"), f += this._startName + "=" + d.from.toString(), d.to !== -1 && (f += "&" + this._endName + "=" + d.to.toString());
                    }
                    return {
                      url: f,
                      headers: {}
                    };
                  }, h.prototype.removeURLParameters = function(u) {
                    var d = u.split("?")[0], f = void 0, p = u.indexOf("?");
                    p !== -1 && (f = u.substring(p + 1));
                    var v = "";
                    if (f != null && f.length > 0)
                      for (var g = f.split("&"), m = 0; m < g.length; m++) {
                        var x = g[m].split("="), y = m > 0;
                        x[0] !== this._startName && x[0] !== this._endName && (y && (v += "&"), v += g[m]);
                      }
                    return v.length === 0 ? d : d + "?" + v;
                  }, h;
                }()
              );
              o.default = c;
            }
          ),
          /***/
          "./src/io/range-seek-handler.js": (
            /*!**************************************!*\
              !*** ./src/io/range-seek-handler.js ***!
              \**************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h(u) {
                    this._zeroStart = u || !1;
                  }
                  return h.prototype.getConfig = function(u, d) {
                    var f = {};
                    if (d.from !== 0 || d.to !== -1) {
                      var p = void 0;
                      d.to !== -1 ? p = "bytes=" + d.from.toString() + "-" + d.to.toString() : p = "bytes=" + d.from.toString() + "-", f.Range = p;
                    } else
                      this._zeroStart && (f.Range = "bytes=0-");
                    return {
                      url: u,
                      headers: f
                    };
                  }, h.prototype.removeURLParameters = function(u) {
                    return u;
                  }, h;
                }()
              );
              o.default = c;
            }
          ),
          /***/
          "./src/io/speed-sampler.js": (
            /*!*********************************!*\
              !*** ./src/io/speed-sampler.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h() {
                    this._firstCheckpoint = 0, this._lastCheckpoint = 0, this._intervalBytes = 0, this._totalBytes = 0, this._lastSecondBytes = 0, self.performance && self.performance.now ? this._now = self.performance.now.bind(self.performance) : this._now = Date.now;
                  }
                  return h.prototype.reset = function() {
                    this._firstCheckpoint = this._lastCheckpoint = 0, this._totalBytes = this._intervalBytes = 0, this._lastSecondBytes = 0;
                  }, h.prototype.addBytes = function(u) {
                    this._firstCheckpoint === 0 ? (this._firstCheckpoint = this._now(), this._lastCheckpoint = this._firstCheckpoint, this._intervalBytes += u, this._totalBytes += u) : this._now() - this._lastCheckpoint < 1e3 ? (this._intervalBytes += u, this._totalBytes += u) : (this._lastSecondBytes = this._intervalBytes, this._intervalBytes = u, this._totalBytes += u, this._lastCheckpoint = this._now());
                  }, Object.defineProperty(h.prototype, "currentKBps", {
                    get: function() {
                      this.addBytes(0);
                      var u = (this._now() - this._lastCheckpoint) / 1e3;
                      return u == 0 && (u = 1), this._intervalBytes / u / 1024;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(h.prototype, "lastSecondKBps", {
                    get: function() {
                      return this.addBytes(0), this._lastSecondBytes !== 0 ? this._lastSecondBytes / 1024 : this._now() - this._lastCheckpoint >= 500 ? this.currentKBps : 0;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(h.prototype, "averageKBps", {
                    get: function() {
                      var u = (this._now() - this._firstCheckpoint) / 1e3;
                      return this._totalBytes / u / 1024;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), h;
                }()
              );
              o.default = c;
            }
          ),
          /***/
          "./src/io/websocket-loader.js": (
            /*!************************************!*\
              !*** ./src/io/websocket-loader.js ***!
              \************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ./loader.js */
                "./src/io/loader.js"
              ), h = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), u = function() {
                var f = function(p, v) {
                  return f = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(g, m) {
                    g.__proto__ = m;
                  } || function(g, m) {
                    for (var x in m)
                      Object.prototype.hasOwnProperty.call(m, x) && (g[x] = m[x]);
                  }, f(p, v);
                };
                return function(p, v) {
                  if (typeof v != "function" && v !== null)
                    throw new TypeError("Class extends value " + String(v) + " is not a constructor or null");
                  f(p, v);
                  function g() {
                    this.constructor = p;
                  }
                  p.prototype = v === null ? Object.create(v) : (g.prototype = v.prototype, new g());
                };
              }(), d = (
                /** @class */
                function(f) {
                  u(p, f);
                  function p() {
                    var v = f.call(this, "websocket-loader") || this;
                    return v.TAG = "WebSocketLoader", v._needStash = !0, v._ws = null, v._requestAbort = !1, v._receivedLength = 0, v;
                  }
                  return p.isSupported = function() {
                    try {
                      return typeof self.WebSocket < "u";
                    } catch {
                      return !1;
                    }
                  }, p.prototype.destroy = function() {
                    this._ws && this.abort(), f.prototype.destroy.call(this);
                  }, p.prototype.open = function(v) {
                    try {
                      var g = this._ws = new self.WebSocket(v.url);
                      g.binaryType = "arraybuffer", g.onopen = this._onWebSocketOpen.bind(this), g.onclose = this._onWebSocketClose.bind(this), g.onmessage = this._onWebSocketMessage.bind(this), g.onerror = this._onWebSocketError.bind(this), this._status = c.LoaderStatus.kConnecting;
                    } catch (x) {
                      this._status = c.LoaderStatus.kError;
                      var m = { code: x.code, msg: x.message };
                      if (this._onError)
                        this._onError(c.LoaderErrors.EXCEPTION, m);
                      else
                        throw new h.RuntimeException(m.msg);
                    }
                  }, p.prototype.abort = function() {
                    var v = this._ws;
                    v && (v.readyState === 0 || v.readyState === 1) && (this._requestAbort = !0, v.close()), this._ws = null, this._status = c.LoaderStatus.kComplete;
                  }, p.prototype._onWebSocketOpen = function(v) {
                    this._status = c.LoaderStatus.kBuffering;
                  }, p.prototype._onWebSocketClose = function(v) {
                    if (this._requestAbort === !0) {
                      this._requestAbort = !1;
                      return;
                    }
                    this._status = c.LoaderStatus.kComplete, this._onComplete && this._onComplete(0, this._receivedLength - 1);
                  }, p.prototype._onWebSocketMessage = function(v) {
                    var g = this;
                    if (v.data instanceof ArrayBuffer)
                      this._dispatchArrayBuffer(v.data);
                    else if (v.data instanceof Blob) {
                      var m = new FileReader();
                      m.onload = function() {
                        g._dispatchArrayBuffer(m.result);
                      }, m.readAsArrayBuffer(v.data);
                    } else {
                      this._status = c.LoaderStatus.kError;
                      var x = { code: -1, msg: "Unsupported WebSocket message type: " + v.data.constructor.name };
                      if (this._onError)
                        this._onError(c.LoaderErrors.EXCEPTION, x);
                      else
                        throw new h.RuntimeException(x.msg);
                    }
                  }, p.prototype._dispatchArrayBuffer = function(v) {
                    var g = v, m = this._receivedLength;
                    this._receivedLength += g.byteLength, this._onDataArrival && this._onDataArrival(g, m, this._receivedLength);
                  }, p.prototype._onWebSocketError = function(v) {
                    this._status = c.LoaderStatus.kError;
                    var g = {
                      code: v.code,
                      msg: v.message
                    };
                    if (this._onError)
                      this._onError(c.LoaderErrors.EXCEPTION, g);
                    else
                      throw new h.RuntimeException(g.msg);
                  }, p;
                }(c.BaseLoader)
              );
              o.default = d;
            }
          ),
          /***/
          "./src/io/xhr-moz-chunked-loader.js": (
            /*!******************************************!*\
              !*** ./src/io/xhr-moz-chunked-loader.js ***!
              \******************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ./loader.js */
                "./src/io/loader.js"
              ), u = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), d = function() {
                var p = function(v, g) {
                  return p = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(m, x) {
                    m.__proto__ = x;
                  } || function(m, x) {
                    for (var y in x)
                      Object.prototype.hasOwnProperty.call(x, y) && (m[y] = x[y]);
                  }, p(v, g);
                };
                return function(v, g) {
                  if (typeof g != "function" && g !== null)
                    throw new TypeError("Class extends value " + String(g) + " is not a constructor or null");
                  p(v, g);
                  function m() {
                    this.constructor = v;
                  }
                  v.prototype = g === null ? Object.create(g) : (m.prototype = g.prototype, new m());
                };
              }(), f = (
                /** @class */
                function(p) {
                  d(v, p);
                  function v(g, m) {
                    var x = p.call(this, "xhr-moz-chunked-loader") || this;
                    return x.TAG = "MozChunkedLoader", x._seekHandler = g, x._config = m, x._needStash = !0, x._xhr = null, x._requestAbort = !1, x._contentLength = null, x._receivedLength = 0, x;
                  }
                  return v.isSupported = function() {
                    try {
                      var g = new XMLHttpRequest();
                      return g.open("GET", "https://example.com", !0), g.responseType = "moz-chunked-arraybuffer", g.responseType === "moz-chunked-arraybuffer";
                    } catch (m) {
                      return c.default.w("MozChunkedLoader", m.message), !1;
                    }
                  }, v.prototype.destroy = function() {
                    this.isWorking() && this.abort(), this._xhr && (this._xhr.onreadystatechange = null, this._xhr.onprogress = null, this._xhr.onloadend = null, this._xhr.onerror = null, this._xhr = null), p.prototype.destroy.call(this);
                  }, v.prototype.open = function(g, m) {
                    this._dataSource = g, this._range = m;
                    var x = g.url;
                    this._config.reuseRedirectedURL && g.redirectedURL != null && (x = g.redirectedURL);
                    var y = this._seekHandler.getConfig(x, m);
                    this._requestURL = y.url;
                    var A = this._xhr = new XMLHttpRequest();
                    if (A.open("GET", y.url, !0), A.responseType = "moz-chunked-arraybuffer", A.onreadystatechange = this._onReadyStateChange.bind(this), A.onprogress = this._onProgress.bind(this), A.onloadend = this._onLoadEnd.bind(this), A.onerror = this._onXhrError.bind(this), g.withCredentials && (A.withCredentials = !0), typeof y.headers == "object") {
                      var b = y.headers;
                      for (var M in b)
                        b.hasOwnProperty(M) && A.setRequestHeader(M, b[M]);
                    }
                    if (typeof this._config.headers == "object") {
                      var b = this._config.headers;
                      for (var M in b)
                        b.hasOwnProperty(M) && A.setRequestHeader(M, b[M]);
                    }
                    this._status = h.LoaderStatus.kConnecting, A.send();
                  }, v.prototype.abort = function() {
                    this._requestAbort = !0, this._xhr && this._xhr.abort(), this._status = h.LoaderStatus.kComplete;
                  }, v.prototype._onReadyStateChange = function(g) {
                    var m = g.target;
                    if (m.readyState === 2) {
                      if (m.responseURL != null && m.responseURL !== this._requestURL && this._onURLRedirect) {
                        var x = this._seekHandler.removeURLParameters(m.responseURL);
                        this._onURLRedirect(x);
                      }
                      if (m.status !== 0 && (m.status < 200 || m.status > 299))
                        if (this._status = h.LoaderStatus.kError, this._onError)
                          this._onError(h.LoaderErrors.HTTP_STATUS_CODE_INVALID, { code: m.status, msg: m.statusText });
                        else
                          throw new u.RuntimeException("MozChunkedLoader: Http code invalid, " + m.status + " " + m.statusText);
                      else
                        this._status = h.LoaderStatus.kBuffering;
                    }
                  }, v.prototype._onProgress = function(g) {
                    if (this._status !== h.LoaderStatus.kError) {
                      this._contentLength === null && g.total !== null && g.total !== 0 && (this._contentLength = g.total, this._onContentLengthKnown && this._onContentLengthKnown(this._contentLength));
                      var m = g.target.response, x = this._range.from + this._receivedLength;
                      this._receivedLength += m.byteLength, this._onDataArrival && this._onDataArrival(m, x, this._receivedLength);
                    }
                  }, v.prototype._onLoadEnd = function(g) {
                    if (this._requestAbort === !0) {
                      this._requestAbort = !1;
                      return;
                    } else if (this._status === h.LoaderStatus.kError)
                      return;
                    this._status = h.LoaderStatus.kComplete, this._onComplete && this._onComplete(this._range.from, this._range.from + this._receivedLength - 1);
                  }, v.prototype._onXhrError = function(g) {
                    this._status = h.LoaderStatus.kError;
                    var m = 0, x = null;
                    if (this._contentLength && g.loaded < this._contentLength ? (m = h.LoaderErrors.EARLY_EOF, x = { code: -1, msg: "Moz-Chunked stream meet Early-Eof" }) : (m = h.LoaderErrors.EXCEPTION, x = { code: -1, msg: g.constructor.name + " " + g.type }), this._onError)
                      this._onError(m, x);
                    else
                      throw new u.RuntimeException(x.msg);
                  }, v;
                }(h.BaseLoader)
              );
              o.default = f;
            }
          ),
          /***/
          "./src/io/xhr-range-loader.js": (
            /*!************************************!*\
              !*** ./src/io/xhr-range-loader.js ***!
              \************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ./speed-sampler.js */
                "./src/io/speed-sampler.js"
              ), u = l(
                /*! ./loader.js */
                "./src/io/loader.js"
              ), d = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), f = function() {
                var v = function(g, m) {
                  return v = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(x, y) {
                    x.__proto__ = y;
                  } || function(x, y) {
                    for (var A in y)
                      Object.prototype.hasOwnProperty.call(y, A) && (x[A] = y[A]);
                  }, v(g, m);
                };
                return function(g, m) {
                  if (typeof m != "function" && m !== null)
                    throw new TypeError("Class extends value " + String(m) + " is not a constructor or null");
                  v(g, m);
                  function x() {
                    this.constructor = g;
                  }
                  g.prototype = m === null ? Object.create(m) : (x.prototype = m.prototype, new x());
                };
              }(), p = (
                /** @class */
                function(v) {
                  f(g, v);
                  function g(m, x) {
                    var y = v.call(this, "xhr-range-loader") || this;
                    return y.TAG = "RangeLoader", y._seekHandler = m, y._config = x, y._needStash = !1, y._chunkSizeKBList = [
                      128,
                      256,
                      384,
                      512,
                      768,
                      1024,
                      1536,
                      2048,
                      3072,
                      4096,
                      5120,
                      6144,
                      7168,
                      8192
                    ], y._currentChunkSizeKB = 384, y._currentSpeedNormalized = 0, y._zeroSpeedChunkCount = 0, y._xhr = null, y._speedSampler = new h.default(), y._requestAbort = !1, y._waitForTotalLength = !1, y._totalLengthReceived = !1, y._currentRequestURL = null, y._currentRedirectedURL = null, y._currentRequestRange = null, y._totalLength = null, y._contentLength = null, y._receivedLength = 0, y._lastTimeLoaded = 0, y;
                  }
                  return g.isSupported = function() {
                    try {
                      var m = new XMLHttpRequest();
                      return m.open("GET", "https://example.com", !0), m.responseType = "arraybuffer", m.responseType === "arraybuffer";
                    } catch (x) {
                      return c.default.w("RangeLoader", x.message), !1;
                    }
                  }, g.prototype.destroy = function() {
                    this.isWorking() && this.abort(), this._xhr && (this._xhr.onreadystatechange = null, this._xhr.onprogress = null, this._xhr.onload = null, this._xhr.onerror = null, this._xhr = null), v.prototype.destroy.call(this);
                  }, Object.defineProperty(g.prototype, "currentSpeed", {
                    get: function() {
                      return this._speedSampler.lastSecondKBps;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), g.prototype.open = function(m, x) {
                    this._dataSource = m, this._range = x, this._status = u.LoaderStatus.kConnecting;
                    var y = !1;
                    this._dataSource.filesize != null && this._dataSource.filesize !== 0 && (y = !0, this._totalLength = this._dataSource.filesize), !this._totalLengthReceived && !y ? (this._waitForTotalLength = !0, this._internalOpen(this._dataSource, { from: 0, to: -1 })) : this._openSubRange();
                  }, g.prototype._openSubRange = function() {
                    var m = this._currentChunkSizeKB * 1024, x = this._range.from + this._receivedLength, y = x + m;
                    this._contentLength != null && y - this._range.from >= this._contentLength && (y = this._range.from + this._contentLength - 1), this._currentRequestRange = { from: x, to: y }, this._internalOpen(this._dataSource, this._currentRequestRange);
                  }, g.prototype._internalOpen = function(m, x) {
                    this._lastTimeLoaded = 0;
                    var y = m.url;
                    this._config.reuseRedirectedURL && (this._currentRedirectedURL != null ? y = this._currentRedirectedURL : m.redirectedURL != null && (y = m.redirectedURL));
                    var A = this._seekHandler.getConfig(y, x);
                    this._currentRequestURL = A.url;
                    var b = this._xhr = new XMLHttpRequest();
                    if (b.open("GET", A.url, !0), b.responseType = "arraybuffer", b.onreadystatechange = this._onReadyStateChange.bind(this), b.onprogress = this._onProgress.bind(this), b.onload = this._onLoad.bind(this), b.onerror = this._onXhrError.bind(this), m.withCredentials && (b.withCredentials = !0), typeof A.headers == "object") {
                      var M = A.headers;
                      for (var w in M)
                        M.hasOwnProperty(w) && b.setRequestHeader(w, M[w]);
                    }
                    if (typeof this._config.headers == "object") {
                      var M = this._config.headers;
                      for (var w in M)
                        M.hasOwnProperty(w) && b.setRequestHeader(w, M[w]);
                    }
                    b.send();
                  }, g.prototype.abort = function() {
                    this._requestAbort = !0, this._internalAbort(), this._status = u.LoaderStatus.kComplete;
                  }, g.prototype._internalAbort = function() {
                    this._xhr && (this._xhr.onreadystatechange = null, this._xhr.onprogress = null, this._xhr.onload = null, this._xhr.onerror = null, this._xhr.abort(), this._xhr = null);
                  }, g.prototype._onReadyStateChange = function(m) {
                    var x = m.target;
                    if (x.readyState === 2) {
                      if (x.responseURL != null) {
                        var y = this._seekHandler.removeURLParameters(x.responseURL);
                        x.responseURL !== this._currentRequestURL && y !== this._currentRedirectedURL && (this._currentRedirectedURL = y, this._onURLRedirect && this._onURLRedirect(y));
                      }
                      if (x.status >= 200 && x.status <= 299) {
                        if (this._waitForTotalLength)
                          return;
                        this._status = u.LoaderStatus.kBuffering;
                      } else if (this._status = u.LoaderStatus.kError, this._onError)
                        this._onError(u.LoaderErrors.HTTP_STATUS_CODE_INVALID, { code: x.status, msg: x.statusText });
                      else
                        throw new d.RuntimeException("RangeLoader: Http code invalid, " + x.status + " " + x.statusText);
                    }
                  }, g.prototype._onProgress = function(m) {
                    if (this._status !== u.LoaderStatus.kError) {
                      if (this._contentLength === null) {
                        var x = !1;
                        if (this._waitForTotalLength) {
                          this._waitForTotalLength = !1, this._totalLengthReceived = !0, x = !0;
                          var y = m.total;
                          this._internalAbort(), y != null & y !== 0 && (this._totalLength = y);
                        }
                        if (this._range.to === -1 ? this._contentLength = this._totalLength - this._range.from : this._contentLength = this._range.to - this._range.from + 1, x) {
                          this._openSubRange();
                          return;
                        }
                        this._onContentLengthKnown && this._onContentLengthKnown(this._contentLength);
                      }
                      var A = m.loaded - this._lastTimeLoaded;
                      this._lastTimeLoaded = m.loaded, this._speedSampler.addBytes(A);
                    }
                  }, g.prototype._normalizeSpeed = function(m) {
                    var x = this._chunkSizeKBList, y = x.length - 1, A = 0, b = 0, M = y;
                    if (m < x[0])
                      return x[0];
                    for (; b <= M; ) {
                      if (A = b + Math.floor((M - b) / 2), A === y || m >= x[A] && m < x[A + 1])
                        return x[A];
                      x[A] < m ? b = A + 1 : M = A - 1;
                    }
                  }, g.prototype._onLoad = function(m) {
                    if (this._status !== u.LoaderStatus.kError) {
                      if (this._waitForTotalLength) {
                        this._waitForTotalLength = !1;
                        return;
                      }
                      this._lastTimeLoaded = 0;
                      var x = this._speedSampler.lastSecondKBps;
                      if (x === 0 && (this._zeroSpeedChunkCount++, this._zeroSpeedChunkCount >= 3 && (x = this._speedSampler.currentKBps)), x !== 0) {
                        var y = this._normalizeSpeed(x);
                        this._currentSpeedNormalized !== y && (this._currentSpeedNormalized = y, this._currentChunkSizeKB = y);
                      }
                      var A = m.target.response, b = this._range.from + this._receivedLength;
                      this._receivedLength += A.byteLength;
                      var M = !1;
                      this._contentLength != null && this._receivedLength < this._contentLength ? this._openSubRange() : M = !0, this._onDataArrival && this._onDataArrival(A, b, this._receivedLength), M && (this._status = u.LoaderStatus.kComplete, this._onComplete && this._onComplete(this._range.from, this._range.from + this._receivedLength - 1));
                    }
                  }, g.prototype._onXhrError = function(m) {
                    this._status = u.LoaderStatus.kError;
                    var x = 0, y = null;
                    if (this._contentLength && this._receivedLength > 0 && this._receivedLength < this._contentLength ? (x = u.LoaderErrors.EARLY_EOF, y = { code: -1, msg: "RangeLoader meet Early-Eof" }) : (x = u.LoaderErrors.EXCEPTION, y = { code: -1, msg: m.constructor.name + " " + m.type }), this._onError)
                      this._onError(x, y);
                    else
                      throw new d.RuntimeException(y.msg);
                  }, g;
                }(u.BaseLoader)
              );
              o.default = p;
            }
          ),
          /***/
          "./src/player/flv-player.js": (
            /*!**********************************!*\
              !*** ./src/player/flv-player.js ***!
              \**********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), d = l(
                /*! ../utils/browser.js */
                "./src/utils/browser.js"
              ), f = l(
                /*! ./player-events.js */
                "./src/player/player-events.js"
              ), p = l(
                /*! ../core/transmuxer.js */
                "./src/core/transmuxer.js"
              ), v = l(
                /*! ../core/transmuxing-events.js */
                "./src/core/transmuxing-events.js"
              ), g = l(
                /*! ../core/mse-controller.js */
                "./src/core/mse-controller.js"
              ), m = l(
                /*! ../core/mse-events.js */
                "./src/core/mse-events.js"
              ), x = l(
                /*! ./player-errors.js */
                "./src/player/player-errors.js"
              ), y = l(
                /*! ../config.js */
                "./src/config.js"
              ), A = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), b = (
                /** @class */
                function() {
                  function M(w, C) {
                    if (this.TAG = "FlvPlayer", this._type = "FlvPlayer", this._emitter = new (h())(), this._config = (0, y.createDefaultConfig)(), typeof C == "object" && Object.assign(this._config, C), w.type.toLowerCase() !== "flv")
                      throw new A.InvalidArgumentException("FlvPlayer requires an flv MediaDataSource input!");
                    w.isLive === !0 && (this._config.isLive = !0), this.e = {
                      onvLoadedMetadata: this._onvLoadedMetadata.bind(this),
                      onvSeeking: this._onvSeeking.bind(this),
                      onvCanPlay: this._onvCanPlay.bind(this),
                      onvStalled: this._onvStalled.bind(this),
                      onvProgress: this._onvProgress.bind(this)
                    }, self.performance && self.performance.now ? this._now = self.performance.now.bind(self.performance) : this._now = Date.now, this._pendingSeekTime = null, this._requestSetTime = !1, this._seekpointRecord = null, this._progressChecker = null, this._mediaDataSource = w, this._mediaElement = null, this._msectl = null, this._transmuxer = null, this._mseSourceOpened = !1, this._hasPendingLoad = !1, this._receivedCanPlay = !1, this._mediaInfo = null, this._statisticsInfo = null;
                    var T = d.default.chrome && (d.default.version.major < 50 || d.default.version.major === 50 && d.default.version.build < 2661);
                    this._alwaysSeekKeyframe = !!(T || d.default.msedge || d.default.msie), this._alwaysSeekKeyframe && (this._config.accurateSeek = !1);
                  }
                  return M.prototype.destroy = function() {
                    this._progressChecker != null && (window.clearInterval(this._progressChecker), this._progressChecker = null), this._transmuxer && this.unload(), this._mediaElement && this.detachMediaElement(), this.e = null, this._mediaDataSource = null, this._emitter.removeAllListeners(), this._emitter = null;
                  }, M.prototype.on = function(w, C) {
                    var T = this;
                    w === f.default.MEDIA_INFO ? this._mediaInfo != null && Promise.resolve().then(function() {
                      T._emitter.emit(f.default.MEDIA_INFO, T.mediaInfo);
                    }) : w === f.default.STATISTICS_INFO && this._statisticsInfo != null && Promise.resolve().then(function() {
                      T._emitter.emit(f.default.STATISTICS_INFO, T.statisticsInfo);
                    }), this._emitter.addListener(w, C);
                  }, M.prototype.off = function(w, C) {
                    this._emitter.removeListener(w, C);
                  }, M.prototype.attachMediaElement = function(w) {
                    var C = this;
                    if (this._mediaElement = w, w.addEventListener("loadedmetadata", this.e.onvLoadedMetadata), w.addEventListener("seeking", this.e.onvSeeking), w.addEventListener("canplay", this.e.onvCanPlay), w.addEventListener("stalled", this.e.onvStalled), w.addEventListener("progress", this.e.onvProgress), this._msectl = new g.default(this._config), this._msectl.on(m.default.UPDATE_END, this._onmseUpdateEnd.bind(this)), this._msectl.on(m.default.BUFFER_FULL, this._onmseBufferFull.bind(this)), this._msectl.on(m.default.SOURCE_OPEN, function() {
                      C._mseSourceOpened = !0, C._hasPendingLoad && (C._hasPendingLoad = !1, C.load());
                    }), this._msectl.on(m.default.ERROR, function(T) {
                      C._emitter.emit(f.default.ERROR, x.ErrorTypes.MEDIA_ERROR, x.ErrorDetails.MEDIA_MSE_ERROR, T);
                    }), this._msectl.attachMediaElement(w), this._pendingSeekTime != null)
                      try {
                        w.currentTime = this._pendingSeekTime, this._pendingSeekTime = null;
                      } catch {
                      }
                  }, M.prototype.detachMediaElement = function() {
                    this._mediaElement && (this._msectl.detachMediaElement(), this._mediaElement.removeEventListener("loadedmetadata", this.e.onvLoadedMetadata), this._mediaElement.removeEventListener("seeking", this.e.onvSeeking), this._mediaElement.removeEventListener("canplay", this.e.onvCanPlay), this._mediaElement.removeEventListener("stalled", this.e.onvStalled), this._mediaElement.removeEventListener("progress", this.e.onvProgress), this._mediaElement = null), this._msectl && (this._msectl.destroy(), this._msectl = null);
                  }, M.prototype.load = function() {
                    var w = this;
                    if (!this._mediaElement)
                      throw new A.IllegalStateException("HTMLMediaElement must be attached before load()!");
                    if (this._transmuxer)
                      throw new A.IllegalStateException("FlvPlayer.load() has been called, please call unload() first!");
                    if (!this._hasPendingLoad) {
                      if (this._config.deferLoadAfterSourceOpen && this._mseSourceOpened === !1) {
                        this._hasPendingLoad = !0;
                        return;
                      }
                      this._mediaElement.readyState > 0 && (this._requestSetTime = !0, this._mediaElement.currentTime = 0), this._transmuxer = new p.default(this._mediaDataSource, this._config), this._transmuxer.on(v.default.INIT_SEGMENT, function(C, T) {
                        w._msectl.appendInitSegment(T);
                      }), this._transmuxer.on(v.default.MEDIA_SEGMENT, function(C, T) {
                        if (w._msectl.appendMediaSegment(T), w._config.lazyLoad && !w._config.isLive) {
                          var E = w._mediaElement.currentTime;
                          T.info.endDts >= (E + w._config.lazyLoadMaxDuration) * 1e3 && w._progressChecker == null && (u.default.v(w.TAG, "Maximum buffering duration exceeded, suspend transmuxing task"), w._suspendTransmuxer());
                        }
                      }), this._transmuxer.on(v.default.LOADING_COMPLETE, function() {
                        w._msectl.endOfStream(), w._emitter.emit(f.default.LOADING_COMPLETE);
                      }), this._transmuxer.on(v.default.RECOVERED_EARLY_EOF, function() {
                        w._emitter.emit(f.default.RECOVERED_EARLY_EOF);
                      }), this._transmuxer.on(v.default.IO_ERROR, function(C, T) {
                        w._emitter.emit(f.default.ERROR, x.ErrorTypes.NETWORK_ERROR, C, T);
                      }), this._transmuxer.on(v.default.DEMUX_ERROR, function(C, T) {
                        w._emitter.emit(f.default.ERROR, x.ErrorTypes.MEDIA_ERROR, C, { code: -1, msg: T });
                      }), this._transmuxer.on(v.default.MEDIA_INFO, function(C) {
                        w._mediaInfo = C, w._emitter.emit(f.default.MEDIA_INFO, Object.assign({}, C));
                      }), this._transmuxer.on(v.default.METADATA_ARRIVED, function(C) {
                        w._emitter.emit(f.default.METADATA_ARRIVED, C);
                      }), this._transmuxer.on(v.default.SCRIPTDATA_ARRIVED, function(C) {
                        w._emitter.emit(f.default.SCRIPTDATA_ARRIVED, C);
                      }), this._transmuxer.on(v.default.STATISTICS_INFO, function(C) {
                        w._statisticsInfo = w._fillStatisticsInfo(C), w._emitter.emit(f.default.STATISTICS_INFO, Object.assign({}, w._statisticsInfo));
                      }), this._transmuxer.on(v.default.RECOMMEND_SEEKPOINT, function(C) {
                        w._mediaElement && !w._config.accurateSeek && (w._requestSetTime = !0, w._mediaElement.currentTime = C / 1e3);
                      }), this._transmuxer.open();
                    }
                  }, M.prototype.unload = function() {
                    this._mediaElement && this._mediaElement.pause(), this._msectl && this._msectl.seek(0), this._transmuxer && (this._transmuxer.close(), this._transmuxer.destroy(), this._transmuxer = null);
                  }, M.prototype.play = function() {
                    return this._mediaElement.play();
                  }, M.prototype.pause = function() {
                    this._mediaElement.pause();
                  }, Object.defineProperty(M.prototype, "type", {
                    get: function() {
                      return this._type;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "buffered", {
                    get: function() {
                      return this._mediaElement.buffered;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "duration", {
                    get: function() {
                      return this._mediaElement.duration;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "volume", {
                    get: function() {
                      return this._mediaElement.volume;
                    },
                    set: function(w) {
                      this._mediaElement.volume = w;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "muted", {
                    get: function() {
                      return this._mediaElement.muted;
                    },
                    set: function(w) {
                      this._mediaElement.muted = w;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "currentTime", {
                    get: function() {
                      return this._mediaElement ? this._mediaElement.currentTime : 0;
                    },
                    set: function(w) {
                      this._mediaElement ? this._internalSeek(w) : this._pendingSeekTime = w;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "mediaInfo", {
                    get: function() {
                      return Object.assign({}, this._mediaInfo);
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(M.prototype, "statisticsInfo", {
                    get: function() {
                      return this._statisticsInfo == null && (this._statisticsInfo = {}), this._statisticsInfo = this._fillStatisticsInfo(this._statisticsInfo), Object.assign({}, this._statisticsInfo);
                    },
                    enumerable: !1,
                    configurable: !0
                  }), M.prototype._fillStatisticsInfo = function(w) {
                    if (w.playerType = this._type, !(this._mediaElement instanceof HTMLVideoElement))
                      return w;
                    var C = !0, T = 0, E = 0;
                    if (this._mediaElement.getVideoPlaybackQuality) {
                      var P = this._mediaElement.getVideoPlaybackQuality();
                      T = P.totalVideoFrames, E = P.droppedVideoFrames;
                    } else
                      this._mediaElement.webkitDecodedFrameCount != null ? (T = this._mediaElement.webkitDecodedFrameCount, E = this._mediaElement.webkitDroppedFrameCount) : C = !1;
                    return C && (w.decodedFrames = T, w.droppedFrames = E), w;
                  }, M.prototype._onmseUpdateEnd = function() {
                    if (!(!this._config.lazyLoad || this._config.isLive)) {
                      for (var w = this._mediaElement.buffered, C = this._mediaElement.currentTime, T = 0, E = 0; E < w.length; E++) {
                        var P = w.start(E), L = w.end(E);
                        if (P <= C && C < L) {
                          T = L;
                          break;
                        }
                      }
                      T >= C + this._config.lazyLoadMaxDuration && this._progressChecker == null && (u.default.v(this.TAG, "Maximum buffering duration exceeded, suspend transmuxing task"), this._suspendTransmuxer());
                    }
                  }, M.prototype._onmseBufferFull = function() {
                    u.default.v(this.TAG, "MSE SourceBuffer is full, suspend transmuxing task"), this._progressChecker == null && this._suspendTransmuxer();
                  }, M.prototype._suspendTransmuxer = function() {
                    this._transmuxer && (this._transmuxer.pause(), this._progressChecker == null && (this._progressChecker = window.setInterval(this._checkProgressAndResume.bind(this), 1e3)));
                  }, M.prototype._checkProgressAndResume = function() {
                    for (var w = this._mediaElement.currentTime, C = this._mediaElement.buffered, T = !1, E = 0; E < C.length; E++) {
                      var P = C.start(E), L = C.end(E);
                      if (w >= P && w < L) {
                        w >= L - this._config.lazyLoadRecoverDuration && (T = !0);
                        break;
                      }
                    }
                    T && (window.clearInterval(this._progressChecker), this._progressChecker = null, T && (u.default.v(this.TAG, "Continue loading from paused position"), this._transmuxer.resume()));
                  }, M.prototype._isTimepointBuffered = function(w) {
                    for (var C = this._mediaElement.buffered, T = 0; T < C.length; T++) {
                      var E = C.start(T), P = C.end(T);
                      if (w >= E && w < P)
                        return !0;
                    }
                    return !1;
                  }, M.prototype._internalSeek = function(w) {
                    var C = this._isTimepointBuffered(w), T = !1, E = 0;
                    if (w < 1 && this._mediaElement.buffered.length > 0) {
                      var P = this._mediaElement.buffered.start(0);
                      (P < 1 && w < P || d.default.safari) && (T = !0, E = d.default.safari ? 0.1 : P);
                    }
                    if (T)
                      this._requestSetTime = !0, this._mediaElement.currentTime = E;
                    else if (C) {
                      if (!this._alwaysSeekKeyframe)
                        this._requestSetTime = !0, this._mediaElement.currentTime = w;
                      else {
                        var L = this._msectl.getNearestKeyframe(Math.floor(w * 1e3));
                        this._requestSetTime = !0, L != null ? this._mediaElement.currentTime = L.dts / 1e3 : this._mediaElement.currentTime = w;
                      }
                      this._progressChecker != null && this._checkProgressAndResume();
                    } else
                      this._progressChecker != null && (window.clearInterval(this._progressChecker), this._progressChecker = null), this._msectl.seek(w), this._transmuxer.seek(Math.floor(w * 1e3)), this._config.accurateSeek && (this._requestSetTime = !0, this._mediaElement.currentTime = w);
                  }, M.prototype._checkAndApplyUnbufferedSeekpoint = function() {
                    if (this._seekpointRecord)
                      if (this._seekpointRecord.recordTime <= this._now() - 100) {
                        var w = this._mediaElement.currentTime;
                        this._seekpointRecord = null, this._isTimepointBuffered(w) || (this._progressChecker != null && (window.clearTimeout(this._progressChecker), this._progressChecker = null), this._msectl.seek(w), this._transmuxer.seek(Math.floor(w * 1e3)), this._config.accurateSeek && (this._requestSetTime = !0, this._mediaElement.currentTime = w));
                      } else
                        window.setTimeout(this._checkAndApplyUnbufferedSeekpoint.bind(this), 50);
                  }, M.prototype._checkAndResumeStuckPlayback = function(w) {
                    var C = this._mediaElement;
                    if (w || !this._receivedCanPlay || C.readyState < 2) {
                      var T = C.buffered;
                      T.length > 0 && C.currentTime < T.start(0) && (u.default.w(this.TAG, "Playback seems stuck at " + C.currentTime + ", seek to " + T.start(0)), this._requestSetTime = !0, this._mediaElement.currentTime = T.start(0), this._mediaElement.removeEventListener("progress", this.e.onvProgress));
                    } else
                      this._mediaElement.removeEventListener("progress", this.e.onvProgress);
                  }, M.prototype._onvLoadedMetadata = function(w) {
                    this._pendingSeekTime != null && (this._mediaElement.currentTime = this._pendingSeekTime, this._pendingSeekTime = null);
                  }, M.prototype._onvSeeking = function(w) {
                    var C = this._mediaElement.currentTime, T = this._mediaElement.buffered;
                    if (this._requestSetTime) {
                      this._requestSetTime = !1;
                      return;
                    }
                    if (C < 1 && T.length > 0) {
                      var E = T.start(0);
                      if (E < 1 && C < E || d.default.safari) {
                        this._requestSetTime = !0, this._mediaElement.currentTime = d.default.safari ? 0.1 : E;
                        return;
                      }
                    }
                    if (this._isTimepointBuffered(C)) {
                      if (this._alwaysSeekKeyframe) {
                        var P = this._msectl.getNearestKeyframe(Math.floor(C * 1e3));
                        P != null && (this._requestSetTime = !0, this._mediaElement.currentTime = P.dts / 1e3);
                      }
                      this._progressChecker != null && this._checkProgressAndResume();
                      return;
                    }
                    this._seekpointRecord = {
                      seekPoint: C,
                      recordTime: this._now()
                    }, window.setTimeout(this._checkAndApplyUnbufferedSeekpoint.bind(this), 50);
                  }, M.prototype._onvCanPlay = function(w) {
                    this._receivedCanPlay = !0, this._mediaElement.removeEventListener("canplay", this.e.onvCanPlay);
                  }, M.prototype._onvStalled = function(w) {
                    this._checkAndResumeStuckPlayback(!0);
                  }, M.prototype._onvProgress = function(w) {
                    this._checkAndResumeStuckPlayback();
                  }, M;
                }()
              );
              o.default = b;
            }
          ),
          /***/
          "./src/player/native-player.js": (
            /*!*************************************!*\
              !*** ./src/player/native-player.js ***!
              \*************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! ./player-events.js */
                "./src/player/player-events.js"
              ), d = l(
                /*! ../config.js */
                "./src/config.js"
              ), f = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), p = (
                /** @class */
                function() {
                  function v(g, m) {
                    if (this.TAG = "NativePlayer", this._type = "NativePlayer", this._emitter = new (h())(), this._config = (0, d.createDefaultConfig)(), typeof m == "object" && Object.assign(this._config, m), g.type.toLowerCase() === "flv")
                      throw new f.InvalidArgumentException("NativePlayer does't support flv MediaDataSource input!");
                    if (g.hasOwnProperty("segments"))
                      throw new f.InvalidArgumentException("NativePlayer(" + g.type + ") doesn't support multipart playback!");
                    this.e = {
                      onvLoadedMetadata: this._onvLoadedMetadata.bind(this)
                    }, this._pendingSeekTime = null, this._statisticsReporter = null, this._mediaDataSource = g, this._mediaElement = null;
                  }
                  return v.prototype.destroy = function() {
                    this._mediaElement && (this.unload(), this.detachMediaElement()), this.e = null, this._mediaDataSource = null, this._emitter.removeAllListeners(), this._emitter = null;
                  }, v.prototype.on = function(g, m) {
                    var x = this;
                    g === u.default.MEDIA_INFO ? this._mediaElement != null && this._mediaElement.readyState !== 0 && Promise.resolve().then(function() {
                      x._emitter.emit(u.default.MEDIA_INFO, x.mediaInfo);
                    }) : g === u.default.STATISTICS_INFO && this._mediaElement != null && this._mediaElement.readyState !== 0 && Promise.resolve().then(function() {
                      x._emitter.emit(u.default.STATISTICS_INFO, x.statisticsInfo);
                    }), this._emitter.addListener(g, m);
                  }, v.prototype.off = function(g, m) {
                    this._emitter.removeListener(g, m);
                  }, v.prototype.attachMediaElement = function(g) {
                    if (this._mediaElement = g, g.addEventListener("loadedmetadata", this.e.onvLoadedMetadata), this._pendingSeekTime != null)
                      try {
                        g.currentTime = this._pendingSeekTime, this._pendingSeekTime = null;
                      } catch {
                      }
                  }, v.prototype.detachMediaElement = function() {
                    this._mediaElement && (this._mediaElement.src = "", this._mediaElement.removeAttribute("src"), this._mediaElement.removeEventListener("loadedmetadata", this.e.onvLoadedMetadata), this._mediaElement = null), this._statisticsReporter != null && (window.clearInterval(this._statisticsReporter), this._statisticsReporter = null);
                  }, v.prototype.load = function() {
                    if (!this._mediaElement)
                      throw new f.IllegalStateException("HTMLMediaElement must be attached before load()!");
                    this._mediaElement.src = this._mediaDataSource.url, this._mediaElement.readyState > 0 && (this._mediaElement.currentTime = 0), this._mediaElement.preload = "auto", this._mediaElement.load(), this._statisticsReporter = window.setInterval(this._reportStatisticsInfo.bind(this), this._config.statisticsInfoReportInterval);
                  }, v.prototype.unload = function() {
                    this._mediaElement && (this._mediaElement.src = "", this._mediaElement.removeAttribute("src")), this._statisticsReporter != null && (window.clearInterval(this._statisticsReporter), this._statisticsReporter = null);
                  }, v.prototype.play = function() {
                    return this._mediaElement.play();
                  }, v.prototype.pause = function() {
                    this._mediaElement.pause();
                  }, Object.defineProperty(v.prototype, "type", {
                    get: function() {
                      return this._type;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "buffered", {
                    get: function() {
                      return this._mediaElement.buffered;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "duration", {
                    get: function() {
                      return this._mediaElement.duration;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "volume", {
                    get: function() {
                      return this._mediaElement.volume;
                    },
                    set: function(g) {
                      this._mediaElement.volume = g;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "muted", {
                    get: function() {
                      return this._mediaElement.muted;
                    },
                    set: function(g) {
                      this._mediaElement.muted = g;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "currentTime", {
                    get: function() {
                      return this._mediaElement ? this._mediaElement.currentTime : 0;
                    },
                    set: function(g) {
                      this._mediaElement ? this._mediaElement.currentTime = g : this._pendingSeekTime = g;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "mediaInfo", {
                    get: function() {
                      var g = this._mediaElement instanceof HTMLAudioElement ? "audio/" : "video/", m = {
                        mimeType: g + this._mediaDataSource.type
                      };
                      return this._mediaElement && (m.duration = Math.floor(this._mediaElement.duration * 1e3), this._mediaElement instanceof HTMLVideoElement && (m.width = this._mediaElement.videoWidth, m.height = this._mediaElement.videoHeight)), m;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(v.prototype, "statisticsInfo", {
                    get: function() {
                      var g = {
                        playerType: this._type,
                        url: this._mediaDataSource.url
                      };
                      if (!(this._mediaElement instanceof HTMLVideoElement))
                        return g;
                      var m = !0, x = 0, y = 0;
                      if (this._mediaElement.getVideoPlaybackQuality) {
                        var A = this._mediaElement.getVideoPlaybackQuality();
                        x = A.totalVideoFrames, y = A.droppedVideoFrames;
                      } else
                        this._mediaElement.webkitDecodedFrameCount != null ? (x = this._mediaElement.webkitDecodedFrameCount, y = this._mediaElement.webkitDroppedFrameCount) : m = !1;
                      return m && (g.decodedFrames = x, g.droppedFrames = y), g;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), v.prototype._onvLoadedMetadata = function(g) {
                    this._pendingSeekTime != null && (this._mediaElement.currentTime = this._pendingSeekTime, this._pendingSeekTime = null), this._emitter.emit(u.default.MEDIA_INFO, this.mediaInfo);
                  }, v.prototype._reportStatisticsInfo = function() {
                    this._emitter.emit(u.default.STATISTICS_INFO, this.statisticsInfo);
                  }, v;
                }()
              );
              o.default = p;
            }
          ),
          /***/
          "./src/player/player-errors.js": (
            /*!*************************************!*\
              !*** ./src/player/player-errors.js ***!
              \*************************************/
            /***/
            function(a, o, l) {
              l.r(o), l.d(o, {
                /* harmony export */
                ErrorTypes: function() {
                  return (
                    /* binding */
                    u
                  );
                },
                /* harmony export */
                ErrorDetails: function() {
                  return (
                    /* binding */
                    d
                  );
                }
                /* harmony export */
              });
              var c = l(
                /*! ../io/loader.js */
                "./src/io/loader.js"
              ), h = l(
                /*! ../demux/demux-errors.js */
                "./src/demux/demux-errors.js"
              ), u = {
                NETWORK_ERROR: "NetworkError",
                MEDIA_ERROR: "MediaError",
                OTHER_ERROR: "OtherError"
              }, d = {
                NETWORK_EXCEPTION: c.LoaderErrors.EXCEPTION,
                NETWORK_STATUS_CODE_INVALID: c.LoaderErrors.HTTP_STATUS_CODE_INVALID,
                NETWORK_TIMEOUT: c.LoaderErrors.CONNECTING_TIMEOUT,
                NETWORK_UNRECOVERABLE_EARLY_EOF: c.LoaderErrors.UNRECOVERABLE_EARLY_EOF,
                MEDIA_MSE_ERROR: "MediaMSEError",
                MEDIA_FORMAT_ERROR: h.default.FORMAT_ERROR,
                MEDIA_FORMAT_UNSUPPORTED: h.default.FORMAT_UNSUPPORTED,
                MEDIA_CODEC_UNSUPPORTED: h.default.CODEC_UNSUPPORTED
              };
            }
          ),
          /***/
          "./src/player/player-events.js": (
            /*!*************************************!*\
              !*** ./src/player/player-events.js ***!
              \*************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = {
                ERROR: "error",
                LOADING_COMPLETE: "loading_complete",
                RECOVERED_EARLY_EOF: "recovered_early_eof",
                MEDIA_INFO: "media_info",
                METADATA_ARRIVED: "metadata_arrived",
                SCRIPTDATA_ARRIVED: "scriptdata_arrived",
                STATISTICS_INFO: "statistics_info"
              };
              o.default = c;
            }
          ),
          /***/
          "./src/remux/aac-silent.js": (
            /*!*********************************!*\
              !*** ./src/remux/aac-silent.js ***!
              \*********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h() {
                  }
                  return h.getSilentFrame = function(u, d) {
                    if (u === "mp4a.40.2") {
                      if (d === 1)
                        return new Uint8Array([0, 200, 0, 128, 35, 128]);
                      if (d === 2)
                        return new Uint8Array([33, 0, 73, 144, 2, 25, 0, 35, 128]);
                      if (d === 3)
                        return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 142]);
                      if (d === 4)
                        return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 128, 44, 128, 8, 2, 56]);
                      if (d === 5)
                        return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 130, 48, 4, 153, 0, 33, 144, 2, 56]);
                      if (d === 6)
                        return new Uint8Array([0, 200, 0, 128, 32, 132, 1, 38, 64, 8, 100, 0, 130, 48, 4, 153, 0, 33, 144, 2, 0, 178, 0, 32, 8, 224]);
                    } else {
                      if (d === 1)
                        return new Uint8Array([1, 64, 34, 128, 163, 78, 230, 128, 186, 8, 0, 0, 0, 28, 6, 241, 193, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
                      if (d === 2)
                        return new Uint8Array([1, 64, 34, 128, 163, 94, 230, 128, 186, 8, 0, 0, 0, 0, 149, 0, 6, 241, 161, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
                      if (d === 3)
                        return new Uint8Array([1, 64, 34, 128, 163, 94, 230, 128, 186, 8, 0, 0, 0, 0, 149, 0, 6, 241, 161, 10, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 94]);
                    }
                    return null;
                  }, h;
                }()
              );
              o.default = c;
            }
          ),
          /***/
          "./src/remux/mp4-generator.js": (
            /*!************************************!*\
              !*** ./src/remux/mp4-generator.js ***!
              \************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h() {
                  }
                  return h.init = function() {
                    h.types = {
                      avc1: [],
                      avcC: [],
                      btrt: [],
                      dinf: [],
                      dref: [],
                      esds: [],
                      ftyp: [],
                      hdlr: [],
                      mdat: [],
                      mdhd: [],
                      mdia: [],
                      mfhd: [],
                      minf: [],
                      moof: [],
                      moov: [],
                      mp4a: [],
                      mvex: [],
                      mvhd: [],
                      sdtp: [],
                      stbl: [],
                      stco: [],
                      stsc: [],
                      stsd: [],
                      stsz: [],
                      stts: [],
                      tfdt: [],
                      tfhd: [],
                      traf: [],
                      trak: [],
                      trun: [],
                      trex: [],
                      tkhd: [],
                      vmhd: [],
                      smhd: [],
                      ".mp3": []
                    };
                    for (var u in h.types)
                      h.types.hasOwnProperty(u) && (h.types[u] = [
                        u.charCodeAt(0),
                        u.charCodeAt(1),
                        u.charCodeAt(2),
                        u.charCodeAt(3)
                      ]);
                    var d = h.constants = {};
                    d.FTYP = new Uint8Array([
                      105,
                      115,
                      111,
                      109,
                      0,
                      0,
                      0,
                      1,
                      105,
                      115,
                      111,
                      109,
                      97,
                      118,
                      99,
                      49
                      // avc1
                    ]), d.STSD_PREFIX = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1
                      // entry_count
                    ]), d.STTS = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0
                      // entry_count
                    ]), d.STSC = d.STCO = d.STTS, d.STSZ = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0
                      // sample_count
                    ]), d.HDLR_VIDEO = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      118,
                      105,
                      100,
                      101,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      86,
                      105,
                      100,
                      101,
                      111,
                      72,
                      97,
                      110,
                      100,
                      108,
                      101,
                      114,
                      0
                      // name: VideoHandler
                    ]), d.HDLR_AUDIO = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      115,
                      111,
                      117,
                      110,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      83,
                      111,
                      117,
                      110,
                      100,
                      72,
                      97,
                      110,
                      100,
                      108,
                      101,
                      114,
                      0
                      // name: SoundHandler
                    ]), d.DREF = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      12,
                      117,
                      114,
                      108,
                      32,
                      0,
                      0,
                      0,
                      1
                      // version(0) + flags
                    ]), d.SMHD = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0
                      // balance(2) + reserved(2)
                    ]), d.VMHD = new Uint8Array([
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0
                    ]);
                  }, h.box = function(u) {
                    for (var d = 8, f = null, p = Array.prototype.slice.call(arguments, 1), v = p.length, g = 0; g < v; g++)
                      d += p[g].byteLength;
                    f = new Uint8Array(d), f[0] = d >>> 24 & 255, f[1] = d >>> 16 & 255, f[2] = d >>> 8 & 255, f[3] = d & 255, f.set(u, 4);
                    for (var m = 8, g = 0; g < v; g++)
                      f.set(p[g], m), m += p[g].byteLength;
                    return f;
                  }, h.generateInitSegment = function(u) {
                    var d = h.box(h.types.ftyp, h.constants.FTYP), f = h.moov(u), p = new Uint8Array(d.byteLength + f.byteLength);
                    return p.set(d, 0), p.set(f, d.byteLength), p;
                  }, h.moov = function(u) {
                    var d = h.mvhd(u.timescale, u.duration), f = h.trak(u), p = h.mvex(u);
                    return h.box(h.types.moov, d, f, p);
                  }, h.mvhd = function(u, d) {
                    return h.box(h.types.mvhd, new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      u >>> 24 & 255,
                      u >>> 16 & 255,
                      u >>> 8 & 255,
                      u & 255,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255,
                      0,
                      1,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      64,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      255,
                      255,
                      255,
                      255
                      // next_track_ID
                    ]));
                  }, h.trak = function(u) {
                    return h.box(h.types.trak, h.tkhd(u), h.mdia(u));
                  }, h.tkhd = function(u) {
                    var d = u.id, f = u.duration, p = u.presentWidth, v = u.presentHeight;
                    return h.box(h.types.tkhd, new Uint8Array([
                      0,
                      0,
                      0,
                      7,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255,
                      0,
                      0,
                      0,
                      0,
                      f >>> 24 & 255,
                      f >>> 16 & 255,
                      f >>> 8 & 255,
                      f & 255,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      64,
                      0,
                      0,
                      0,
                      p >>> 8 & 255,
                      p & 255,
                      0,
                      0,
                      v >>> 8 & 255,
                      v & 255,
                      0,
                      0
                    ]));
                  }, h.mdia = function(u) {
                    return h.box(h.types.mdia, h.mdhd(u), h.hdlr(u), h.minf(u));
                  }, h.mdhd = function(u) {
                    var d = u.timescale, f = u.duration;
                    return h.box(h.types.mdhd, new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255,
                      f >>> 24 & 255,
                      f >>> 16 & 255,
                      f >>> 8 & 255,
                      f & 255,
                      85,
                      196,
                      0,
                      0
                      // pre_defined = 0
                    ]));
                  }, h.hdlr = function(u) {
                    var d = null;
                    return u.type === "audio" ? d = h.constants.HDLR_AUDIO : d = h.constants.HDLR_VIDEO, h.box(h.types.hdlr, d);
                  }, h.minf = function(u) {
                    var d = null;
                    return u.type === "audio" ? d = h.box(h.types.smhd, h.constants.SMHD) : d = h.box(h.types.vmhd, h.constants.VMHD), h.box(h.types.minf, d, h.dinf(), h.stbl(u));
                  }, h.dinf = function() {
                    var u = h.box(h.types.dinf, h.box(h.types.dref, h.constants.DREF));
                    return u;
                  }, h.stbl = function(u) {
                    var d = h.box(
                      h.types.stbl,
                      // type: stbl
                      h.stsd(u),
                      // Sample Description Table
                      h.box(h.types.stts, h.constants.STTS),
                      // Time-To-Sample
                      h.box(h.types.stsc, h.constants.STSC),
                      // Sample-To-Chunk
                      h.box(h.types.stsz, h.constants.STSZ),
                      // Sample size
                      h.box(h.types.stco, h.constants.STCO)
                      // Chunk offset
                    );
                    return d;
                  }, h.stsd = function(u) {
                    return u.type === "audio" ? u.codec === "mp3" ? h.box(h.types.stsd, h.constants.STSD_PREFIX, h.mp3(u)) : h.box(h.types.stsd, h.constants.STSD_PREFIX, h.mp4a(u)) : h.box(h.types.stsd, h.constants.STSD_PREFIX, h.avc1(u));
                  }, h.mp3 = function(u) {
                    var d = u.channelCount, f = u.audioSampleRate, p = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      d,
                      0,
                      16,
                      0,
                      0,
                      0,
                      0,
                      f >>> 8 & 255,
                      f & 255,
                      0,
                      0
                    ]);
                    return h.box(h.types[".mp3"], p);
                  }, h.mp4a = function(u) {
                    var d = u.channelCount, f = u.audioSampleRate, p = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      d,
                      0,
                      16,
                      0,
                      0,
                      0,
                      0,
                      f >>> 8 & 255,
                      f & 255,
                      0,
                      0
                    ]);
                    return h.box(h.types.mp4a, p, h.esds(u));
                  }, h.esds = function(u) {
                    var d = u.config || [], f = d.length, p = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      3,
                      23 + f,
                      0,
                      1,
                      0,
                      4,
                      15 + f,
                      64,
                      21,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      5
                      // descriptor_type
                    ].concat([
                      f
                    ]).concat(d).concat([
                      6,
                      1,
                      2
                      // GASpecificConfig
                    ]));
                    return h.box(h.types.esds, p);
                  }, h.avc1 = function(u) {
                    var d = u.avcc, f = u.codecWidth, p = u.codecHeight, v = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      f >>> 8 & 255,
                      f & 255,
                      p >>> 8 & 255,
                      p & 255,
                      0,
                      72,
                      0,
                      0,
                      0,
                      72,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      10,
                      120,
                      113,
                      113,
                      47,
                      102,
                      108,
                      118,
                      46,
                      106,
                      115,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      24,
                      255,
                      255
                      // pre_defined = -1
                    ]);
                    return h.box(h.types.avc1, v, h.box(h.types.avcC, d));
                  }, h.mvex = function(u) {
                    return h.box(h.types.mvex, h.trex(u));
                  }, h.trex = function(u) {
                    var d = u.id, f = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255,
                      0,
                      0,
                      0,
                      1,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      0,
                      1,
                      0,
                      1
                      // default_sample_flags
                    ]);
                    return h.box(h.types.trex, f);
                  }, h.moof = function(u, d) {
                    return h.box(h.types.moof, h.mfhd(u.sequenceNumber), h.traf(u, d));
                  }, h.mfhd = function(u) {
                    var d = new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      u >>> 24 & 255,
                      u >>> 16 & 255,
                      u >>> 8 & 255,
                      u & 255
                    ]);
                    return h.box(h.types.mfhd, d);
                  }, h.traf = function(u, d) {
                    var f = u.id, p = h.box(h.types.tfhd, new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      f >>> 24 & 255,
                      f >>> 16 & 255,
                      f >>> 8 & 255,
                      f & 255
                    ])), v = h.box(h.types.tfdt, new Uint8Array([
                      0,
                      0,
                      0,
                      0,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255
                    ])), g = h.sdtp(u), m = h.trun(u, g.byteLength + 16 + 16 + 8 + 16 + 8 + 8);
                    return h.box(h.types.traf, p, v, m, g);
                  }, h.sdtp = function(u) {
                    for (var d = u.samples || [], f = d.length, p = new Uint8Array(4 + f), v = 0; v < f; v++) {
                      var g = d[v].flags;
                      p[v + 4] = g.isLeading << 6 | g.dependsOn << 4 | g.isDependedOn << 2 | g.hasRedundancy;
                    }
                    return h.box(h.types.sdtp, p);
                  }, h.trun = function(u, d) {
                    var f = u.samples || [], p = f.length, v = 12 + 16 * p, g = new Uint8Array(v);
                    d += 8 + v, g.set([
                      0,
                      0,
                      15,
                      1,
                      p >>> 24 & 255,
                      p >>> 16 & 255,
                      p >>> 8 & 255,
                      p & 255,
                      d >>> 24 & 255,
                      d >>> 16 & 255,
                      d >>> 8 & 255,
                      d & 255
                    ], 0);
                    for (var m = 0; m < p; m++) {
                      var x = f[m].duration, y = f[m].size, A = f[m].flags, b = f[m].cts;
                      g.set([
                        x >>> 24 & 255,
                        x >>> 16 & 255,
                        x >>> 8 & 255,
                        x & 255,
                        y >>> 24 & 255,
                        y >>> 16 & 255,
                        y >>> 8 & 255,
                        y & 255,
                        A.isLeading << 2 | A.dependsOn,
                        A.isDependedOn << 6 | A.hasRedundancy << 4 | A.isNonSync,
                        0,
                        0,
                        b >>> 24 & 255,
                        b >>> 16 & 255,
                        b >>> 8 & 255,
                        b & 255
                      ], 12 + 16 * m);
                    }
                    return h.box(h.types.trun, g);
                  }, h.mdat = function(u) {
                    return h.box(h.types.mdat, u);
                  }, h;
                }()
              );
              c.init(), o.default = c;
            }
          ),
          /***/
          "./src/remux/mp4-remuxer.js": (
            /*!**********************************!*\
              !*** ./src/remux/mp4-remuxer.js ***!
              \**********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! ../utils/logger.js */
                "./src/utils/logger.js"
              ), h = l(
                /*! ./mp4-generator.js */
                "./src/remux/mp4-generator.js"
              ), u = l(
                /*! ./aac-silent.js */
                "./src/remux/aac-silent.js"
              ), d = l(
                /*! ../utils/browser.js */
                "./src/utils/browser.js"
              ), f = l(
                /*! ../core/media-segment-info.js */
                "./src/core/media-segment-info.js"
              ), p = l(
                /*! ../utils/exception.js */
                "./src/utils/exception.js"
              ), v = (
                /** @class */
                function() {
                  function g(m) {
                    this.TAG = "MP4Remuxer", this._config = m, this._isLive = m.isLive === !0, this._dtsBase = -1, this._dtsBaseInited = !1, this._audioDtsBase = 1 / 0, this._videoDtsBase = 1 / 0, this._audioNextDts = void 0, this._videoNextDts = void 0, this._audioStashedLastSample = null, this._videoStashedLastSample = null, this._audioMeta = null, this._videoMeta = null, this._audioSegmentInfoList = new f.MediaSegmentInfoList("audio"), this._videoSegmentInfoList = new f.MediaSegmentInfoList("video"), this._onInitSegment = null, this._onMediaSegment = null, this._forceFirstIDR = !!(d.default.chrome && (d.default.version.major < 50 || d.default.version.major === 50 && d.default.version.build < 2661)), this._fillSilentAfterSeek = d.default.msedge || d.default.msie, this._mp3UseMpegAudio = !d.default.firefox, this._fillAudioTimestampGap = this._config.fixAudioTimestampGap;
                  }
                  return g.prototype.destroy = function() {
                    this._dtsBase = -1, this._dtsBaseInited = !1, this._audioMeta = null, this._videoMeta = null, this._audioSegmentInfoList.clear(), this._audioSegmentInfoList = null, this._videoSegmentInfoList.clear(), this._videoSegmentInfoList = null, this._onInitSegment = null, this._onMediaSegment = null;
                  }, g.prototype.bindDataSource = function(m) {
                    return m.onDataAvailable = this.remux.bind(this), m.onTrackMetadata = this._onTrackMetadataReceived.bind(this), this;
                  }, Object.defineProperty(g.prototype, "onInitSegment", {
                    /* prototype: function onInitSegment(type: string, initSegment: ArrayBuffer): void
                       InitSegment: {
                           type: string,
                           data: ArrayBuffer,
                           codec: string,
                           container: string
                       }
                    */
                    get: function() {
                      return this._onInitSegment;
                    },
                    set: function(m) {
                      this._onInitSegment = m;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(g.prototype, "onMediaSegment", {
                    /* prototype: function onMediaSegment(type: string, mediaSegment: MediaSegment): void
                       MediaSegment: {
                           type: string,
                           data: ArrayBuffer,
                           sampleCount: int32
                           info: MediaSegmentInfo
                       }
                    */
                    get: function() {
                      return this._onMediaSegment;
                    },
                    set: function(m) {
                      this._onMediaSegment = m;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), g.prototype.insertDiscontinuity = function() {
                    this._audioNextDts = this._videoNextDts = void 0;
                  }, g.prototype.seek = function(m) {
                    this._audioStashedLastSample = null, this._videoStashedLastSample = null, this._videoSegmentInfoList.clear(), this._audioSegmentInfoList.clear();
                  }, g.prototype.remux = function(m, x) {
                    if (!this._onMediaSegment)
                      throw new p.IllegalStateException("MP4Remuxer: onMediaSegment callback must be specificed!");
                    this._dtsBaseInited || this._calculateDtsBase(m, x), this._remuxVideo(x), this._remuxAudio(m);
                  }, g.prototype._onTrackMetadataReceived = function(m, x) {
                    var y = null, A = "mp4", b = x.codec;
                    if (m === "audio")
                      this._audioMeta = x, x.codec === "mp3" && this._mp3UseMpegAudio ? (A = "mpeg", b = "", y = new Uint8Array()) : y = h.default.generateInitSegment(x);
                    else if (m === "video")
                      this._videoMeta = x, y = h.default.generateInitSegment(x);
                    else
                      return;
                    if (!this._onInitSegment)
                      throw new p.IllegalStateException("MP4Remuxer: onInitSegment callback must be specified!");
                    this._onInitSegment(m, {
                      type: m,
                      data: y.buffer,
                      codec: b,
                      container: m + "/" + A,
                      mediaDuration: x.duration
                      // in timescale 1000 (milliseconds)
                    });
                  }, g.prototype._calculateDtsBase = function(m, x) {
                    this._dtsBaseInited || (m.samples && m.samples.length && (this._audioDtsBase = m.samples[0].dts), x.samples && x.samples.length && (this._videoDtsBase = x.samples[0].dts), this._dtsBase = Math.min(this._audioDtsBase, this._videoDtsBase), this._dtsBaseInited = !0);
                  }, g.prototype.flushStashedSamples = function() {
                    var m = this._videoStashedLastSample, x = this._audioStashedLastSample, y = {
                      type: "video",
                      id: 1,
                      sequenceNumber: 0,
                      samples: [],
                      length: 0
                    };
                    m != null && (y.samples.push(m), y.length = m.length);
                    var A = {
                      type: "audio",
                      id: 2,
                      sequenceNumber: 0,
                      samples: [],
                      length: 0
                    };
                    x != null && (A.samples.push(x), A.length = x.length), this._videoStashedLastSample = null, this._audioStashedLastSample = null, this._remuxVideo(y, !0), this._remuxAudio(A, !0);
                  }, g.prototype._remuxAudio = function(m, x) {
                    if (this._audioMeta != null) {
                      var y = m, A = y.samples, b = void 0, M = -1, w = -1, C = this._audioMeta.refSampleDuration, T = this._audioMeta.codec === "mp3" && this._mp3UseMpegAudio, E = this._dtsBaseInited && this._audioNextDts === void 0, P = !1;
                      if (!(!A || A.length === 0) && !(A.length === 1 && !x)) {
                        var L = 0, I = null, N = 0;
                        T ? (L = 0, N = y.length) : (L = 8, N = 8 + y.length);
                        var H = null;
                        if (A.length > 1 && (H = A.pop(), N -= H.length), this._audioStashedLastSample != null) {
                          var U = this._audioStashedLastSample;
                          this._audioStashedLastSample = null, A.unshift(U), N += U.length;
                        }
                        H != null && (this._audioStashedLastSample = H);
                        var W = A[0].dts - this._dtsBase;
                        if (this._audioNextDts)
                          b = W - this._audioNextDts;
                        else if (this._audioSegmentInfoList.isEmpty())
                          b = 0, this._fillSilentAfterSeek && !this._videoSegmentInfoList.isEmpty() && this._audioMeta.originalCodec !== "mp3" && (P = !0);
                        else {
                          var X = this._audioSegmentInfoList.getLastSampleBefore(W);
                          if (X != null) {
                            var K = W - (X.originalDts + X.duration);
                            K <= 3 && (K = 0);
                            var te = X.dts + X.duration + K;
                            b = W - te;
                          } else
                            b = 0;
                        }
                        if (P) {
                          var re = W - b, be = this._videoSegmentInfoList.getLastSegmentBefore(W);
                          if (be != null && be.beginDts < re) {
                            var Pe = u.default.getSilentFrame(this._audioMeta.originalCodec, this._audioMeta.channelCount);
                            if (Pe) {
                              var J = be.beginDts, fe = re - be.beginDts;
                              c.default.v(this.TAG, "InsertPrefixSilentAudio: dts: " + J + ", duration: " + fe), A.unshift({ unit: Pe, dts: J, pts: J }), N += Pe.byteLength;
                            }
                          } else
                            P = !1;
                        }
                        for (var Me = [], ye = 0; ye < A.length; ye++) {
                          var U = A[ye], Ye = U.unit, Je = U.dts - this._dtsBase, J = Je, rt = !1, wt = null, k = 0;
                          if (!(Je < -1e-3)) {
                            if (this._audioMeta.codec !== "mp3") {
                              var Fe = Je, Ge = 3;
                              if (this._audioNextDts && (Fe = this._audioNextDts), b = Je - Fe, b <= -Ge * C) {
                                c.default.w(this.TAG, "Dropping 1 audio frame (originalDts: " + Je + " ms ,curRefDts: " + Fe + " ms)  due to dtsCorrection: " + b + " ms overlap.");
                                continue;
                              } else if (b >= Ge * C && this._fillAudioTimestampGap && !d.default.safari) {
                                rt = !0;
                                var qe = Math.floor(b / C);
                                c.default.w(this.TAG, `Large audio timestamp gap detected, may cause AV sync to drift. Silent frames will be generated to avoid unsync.
` + ("originalDts: " + Je + " ms, curRefDts: " + Fe + " ms, ") + ("dtsCorrection: " + Math.round(b) + " ms, generate: " + qe + " frames")), J = Math.floor(Fe), k = Math.floor(Fe + C) - J;
                                var Pe = u.default.getSilentFrame(this._audioMeta.originalCodec, this._audioMeta.channelCount);
                                Pe == null && (c.default.w(this.TAG, "Unable to generate silent frame for " + (this._audioMeta.originalCodec + " with " + this._audioMeta.channelCount + " channels, repeat last frame")), Pe = Ye), wt = [];
                                for (var Ie = 0; Ie < qe; Ie++) {
                                  Fe = Fe + C;
                                  var vt = Math.floor(Fe), Ve = Math.floor(Fe + C) - vt, Ze = {
                                    dts: vt,
                                    pts: vt,
                                    cts: 0,
                                    unit: Pe,
                                    size: Pe.byteLength,
                                    duration: Ve,
                                    originalDts: Je,
                                    flags: {
                                      isLeading: 0,
                                      dependsOn: 1,
                                      isDependedOn: 0,
                                      hasRedundancy: 0
                                    }
                                  };
                                  wt.push(Ze), N += Ze.size;
                                }
                                this._audioNextDts = Fe + C;
                              } else
                                J = Math.floor(Fe), k = Math.floor(Fe + C) - J, this._audioNextDts = Fe + C;
                            } else {
                              if (J = Je - b, ye !== A.length - 1) {
                                var G = A[ye + 1].dts - this._dtsBase - b;
                                k = G - J;
                              } else if (H != null) {
                                var G = H.dts - this._dtsBase - b;
                                k = G - J;
                              } else
                                Me.length >= 1 ? k = Me[Me.length - 1].duration : k = Math.floor(C);
                              this._audioNextDts = J + k;
                            }
                            M === -1 && (M = J), Me.push({
                              dts: J,
                              pts: J,
                              cts: 0,
                              unit: U.unit,
                              size: U.unit.byteLength,
                              duration: k,
                              originalDts: Je,
                              flags: {
                                isLeading: 0,
                                dependsOn: 1,
                                isDependedOn: 0,
                                hasRedundancy: 0
                              }
                            }), rt && Me.push.apply(Me, wt);
                          }
                        }
                        if (Me.length === 0) {
                          y.samples = [], y.length = 0;
                          return;
                        }
                        T ? I = new Uint8Array(N) : (I = new Uint8Array(N), I[0] = N >>> 24 & 255, I[1] = N >>> 16 & 255, I[2] = N >>> 8 & 255, I[3] = N & 255, I.set(h.default.types.mdat, 4));
                        for (var ye = 0; ye < Me.length; ye++) {
                          var Ye = Me[ye].unit;
                          I.set(Ye, L), L += Ye.byteLength;
                        }
                        var D = Me[Me.length - 1];
                        w = D.dts + D.duration;
                        var ne = new f.MediaSegmentInfo();
                        ne.beginDts = M, ne.endDts = w, ne.beginPts = M, ne.endPts = w, ne.originalBeginDts = Me[0].originalDts, ne.originalEndDts = D.originalDts + D.duration, ne.firstSample = new f.SampleInfo(Me[0].dts, Me[0].pts, Me[0].duration, Me[0].originalDts, !1), ne.lastSample = new f.SampleInfo(D.dts, D.pts, D.duration, D.originalDts, !1), this._isLive || this._audioSegmentInfoList.append(ne), y.samples = Me, y.sequenceNumber++;
                        var ve = null;
                        T ? ve = new Uint8Array() : ve = h.default.moof(y, M), y.samples = [], y.length = 0;
                        var Y = {
                          type: "audio",
                          data: this._mergeBoxes(ve, I).buffer,
                          sampleCount: Me.length,
                          info: ne
                        };
                        T && E && (Y.timestampOffset = M), this._onMediaSegment("audio", Y);
                      }
                    }
                  }, g.prototype._remuxVideo = function(m, x) {
                    if (this._videoMeta != null) {
                      var y = m, A = y.samples, b = void 0, M = -1, w = -1, C = -1, T = -1;
                      if (!(!A || A.length === 0) && !(A.length === 1 && !x)) {
                        var E = 8, P = null, L = 8 + m.length, I = null;
                        if (A.length > 1 && (I = A.pop(), L -= I.length), this._videoStashedLastSample != null) {
                          var N = this._videoStashedLastSample;
                          this._videoStashedLastSample = null, A.unshift(N), L += N.length;
                        }
                        I != null && (this._videoStashedLastSample = I);
                        var H = A[0].dts - this._dtsBase;
                        if (this._videoNextDts)
                          b = H - this._videoNextDts;
                        else if (this._videoSegmentInfoList.isEmpty())
                          b = 0;
                        else {
                          var U = this._videoSegmentInfoList.getLastSampleBefore(H);
                          if (U != null) {
                            var W = H - (U.originalDts + U.duration);
                            W <= 3 && (W = 0);
                            var X = U.dts + U.duration + W;
                            b = H - X;
                          } else
                            b = 0;
                        }
                        for (var K = new f.MediaSegmentInfo(), te = [], re = 0; re < A.length; re++) {
                          var N = A[re], be = N.dts - this._dtsBase, Pe = N.isKeyframe, J = be - b, fe = N.cts, Me = J + fe;
                          M === -1 && (M = J, C = Me);
                          var ye = 0;
                          if (re !== A.length - 1) {
                            var Ye = A[re + 1].dts - this._dtsBase - b;
                            ye = Ye - J;
                          } else if (I != null) {
                            var Ye = I.dts - this._dtsBase - b;
                            ye = Ye - J;
                          } else
                            te.length >= 1 ? ye = te[te.length - 1].duration : ye = Math.floor(this._videoMeta.refSampleDuration);
                          if (Pe) {
                            var Je = new f.SampleInfo(J, Me, ye, N.dts, !0);
                            Je.fileposition = N.fileposition, K.appendSyncPoint(Je);
                          }
                          te.push({
                            dts: J,
                            pts: Me,
                            cts: fe,
                            units: N.units,
                            size: N.length,
                            isKeyframe: Pe,
                            duration: ye,
                            originalDts: be,
                            flags: {
                              isLeading: 0,
                              dependsOn: Pe ? 2 : 1,
                              isDependedOn: Pe ? 1 : 0,
                              hasRedundancy: 0,
                              isNonSync: Pe ? 0 : 1
                            }
                          });
                        }
                        P = new Uint8Array(L), P[0] = L >>> 24 & 255, P[1] = L >>> 16 & 255, P[2] = L >>> 8 & 255, P[3] = L & 255, P.set(h.default.types.mdat, 4);
                        for (var re = 0; re < te.length; re++)
                          for (var rt = te[re].units; rt.length; ) {
                            var wt = rt.shift(), k = wt.data;
                            P.set(k, E), E += k.byteLength;
                          }
                        var Fe = te[te.length - 1];
                        if (w = Fe.dts + Fe.duration, T = Fe.pts + Fe.duration, this._videoNextDts = w, K.beginDts = M, K.endDts = w, K.beginPts = C, K.endPts = T, K.originalBeginDts = te[0].originalDts, K.originalEndDts = Fe.originalDts + Fe.duration, K.firstSample = new f.SampleInfo(te[0].dts, te[0].pts, te[0].duration, te[0].originalDts, te[0].isKeyframe), K.lastSample = new f.SampleInfo(Fe.dts, Fe.pts, Fe.duration, Fe.originalDts, Fe.isKeyframe), this._isLive || this._videoSegmentInfoList.append(K), y.samples = te, y.sequenceNumber++, this._forceFirstIDR) {
                          var Ge = te[0].flags;
                          Ge.dependsOn = 2, Ge.isNonSync = 0;
                        }
                        var qe = h.default.moof(y, M);
                        y.samples = [], y.length = 0, this._onMediaSegment("video", {
                          type: "video",
                          data: this._mergeBoxes(qe, P).buffer,
                          sampleCount: te.length,
                          info: K
                        });
                      }
                    }
                  }, g.prototype._mergeBoxes = function(m, x) {
                    var y = new Uint8Array(m.byteLength + x.byteLength);
                    return y.set(m, 0), y.set(x, m.byteLength), y;
                  }, g;
                }()
              );
              o.default = v;
            }
          ),
          /***/
          "./src/utils/browser.js": (
            /*!******************************!*\
              !*** ./src/utils/browser.js ***!
              \******************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = {};
              function h() {
                var u = self.navigator.userAgent.toLowerCase(), d = /(edge)\/([\w.]+)/.exec(u) || /(opr)[\/]([\w.]+)/.exec(u) || /(chrome)[ \/]([\w.]+)/.exec(u) || /(iemobile)[\/]([\w.]+)/.exec(u) || /(version)(applewebkit)[ \/]([\w.]+).*(safari)[ \/]([\w.]+)/.exec(u) || /(webkit)[ \/]([\w.]+).*(version)[ \/]([\w.]+).*(safari)[ \/]([\w.]+)/.exec(u) || /(webkit)[ \/]([\w.]+)/.exec(u) || /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(u) || /(msie) ([\w.]+)/.exec(u) || u.indexOf("trident") >= 0 && /(rv)(?::| )([\w.]+)/.exec(u) || u.indexOf("compatible") < 0 && /(firefox)[ \/]([\w.]+)/.exec(u) || [], f = /(ipad)/.exec(u) || /(ipod)/.exec(u) || /(windows phone)/.exec(u) || /(iphone)/.exec(u) || /(kindle)/.exec(u) || /(android)/.exec(u) || /(windows)/.exec(u) || /(mac)/.exec(u) || /(linux)/.exec(u) || /(cros)/.exec(u) || [], p = {
                  browser: d[5] || d[3] || d[1] || "",
                  version: d[2] || d[4] || "0",
                  majorVersion: d[4] || d[2] || "0",
                  platform: f[0] || ""
                }, v = {};
                if (p.browser) {
                  v[p.browser] = !0;
                  var g = p.majorVersion.split(".");
                  v.version = {
                    major: parseInt(p.majorVersion, 10),
                    string: p.version
                  }, g.length > 1 && (v.version.minor = parseInt(g[1], 10)), g.length > 2 && (v.version.build = parseInt(g[2], 10));
                }
                if (p.platform && (v[p.platform] = !0), (v.chrome || v.opr || v.safari) && (v.webkit = !0), v.rv || v.iemobile) {
                  v.rv && delete v.rv;
                  var m = "msie";
                  p.browser = m, v[m] = !0;
                }
                if (v.edge) {
                  delete v.edge;
                  var x = "msedge";
                  p.browser = x, v[x] = !0;
                }
                if (v.opr) {
                  var y = "opera";
                  p.browser = y, v[y] = !0;
                }
                if (v.safari && v.android) {
                  var A = "android";
                  p.browser = A, v[A] = !0;
                }
                v.name = p.browser, v.platform = p.platform;
                for (var b in c)
                  c.hasOwnProperty(b) && delete c[b];
                Object.assign(c, v);
              }
              h(), o.default = c;
            }
          ),
          /***/
          "./src/utils/exception.js": (
            /*!********************************!*\
              !*** ./src/utils/exception.js ***!
              \********************************/
            /***/
            function(a, o, l) {
              l.r(o), l.d(o, {
                /* harmony export */
                RuntimeException: function() {
                  return (
                    /* binding */
                    h
                  );
                },
                /* harmony export */
                IllegalStateException: function() {
                  return (
                    /* binding */
                    u
                  );
                },
                /* harmony export */
                InvalidArgumentException: function() {
                  return (
                    /* binding */
                    d
                  );
                },
                /* harmony export */
                NotImplementedException: function() {
                  return (
                    /* binding */
                    f
                  );
                }
                /* harmony export */
              });
              var c = function() {
                var p = function(v, g) {
                  return p = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(m, x) {
                    m.__proto__ = x;
                  } || function(m, x) {
                    for (var y in x)
                      Object.prototype.hasOwnProperty.call(x, y) && (m[y] = x[y]);
                  }, p(v, g);
                };
                return function(v, g) {
                  if (typeof g != "function" && g !== null)
                    throw new TypeError("Class extends value " + String(g) + " is not a constructor or null");
                  p(v, g);
                  function m() {
                    this.constructor = v;
                  }
                  v.prototype = g === null ? Object.create(g) : (m.prototype = g.prototype, new m());
                };
              }(), h = (
                /** @class */
                function() {
                  function p(v) {
                    this._message = v;
                  }
                  return Object.defineProperty(p.prototype, "name", {
                    get: function() {
                      return "RuntimeException";
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(p.prototype, "message", {
                    get: function() {
                      return this._message;
                    },
                    enumerable: !1,
                    configurable: !0
                  }), p.prototype.toString = function() {
                    return this.name + ": " + this.message;
                  }, p;
                }()
              ), u = (
                /** @class */
                function(p) {
                  c(v, p);
                  function v(g) {
                    return p.call(this, g) || this;
                  }
                  return Object.defineProperty(v.prototype, "name", {
                    get: function() {
                      return "IllegalStateException";
                    },
                    enumerable: !1,
                    configurable: !0
                  }), v;
                }(h)
              ), d = (
                /** @class */
                function(p) {
                  c(v, p);
                  function v(g) {
                    return p.call(this, g) || this;
                  }
                  return Object.defineProperty(v.prototype, "name", {
                    get: function() {
                      return "InvalidArgumentException";
                    },
                    enumerable: !1,
                    configurable: !0
                  }), v;
                }(h)
              ), f = (
                /** @class */
                function(p) {
                  c(v, p);
                  function v(g) {
                    return p.call(this, g) || this;
                  }
                  return Object.defineProperty(v.prototype, "name", {
                    get: function() {
                      return "NotImplementedException";
                    },
                    enumerable: !1,
                    configurable: !0
                  }), v;
                }(h)
              );
            }
          ),
          /***/
          "./src/utils/logger.js": (
            /*!*****************************!*\
              !*** ./src/utils/logger.js ***!
              \*****************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = (
                /** @class */
                function() {
                  function d() {
                  }
                  return d.e = function(f, p) {
                    (!f || d.FORCE_GLOBAL_TAG) && (f = d.GLOBAL_TAG);
                    var v = "[" + f + "] > " + p;
                    d.ENABLE_CALLBACK && d.emitter.emit("log", "error", v), d.ENABLE_ERROR && (console.error ? console.error(v) : console.warn ? console.warn(v) : console.log(v));
                  }, d.i = function(f, p) {
                    (!f || d.FORCE_GLOBAL_TAG) && (f = d.GLOBAL_TAG);
                    var v = "[" + f + "] > " + p;
                    d.ENABLE_CALLBACK && d.emitter.emit("log", "info", v), d.ENABLE_INFO && (console.info ? console.info(v) : console.log(v));
                  }, d.w = function(f, p) {
                    (!f || d.FORCE_GLOBAL_TAG) && (f = d.GLOBAL_TAG);
                    var v = "[" + f + "] > " + p;
                    d.ENABLE_CALLBACK && d.emitter.emit("log", "warn", v), d.ENABLE_WARN && (console.warn ? console.warn(v) : console.log(v));
                  }, d.d = function(f, p) {
                    (!f || d.FORCE_GLOBAL_TAG) && (f = d.GLOBAL_TAG);
                    var v = "[" + f + "] > " + p;
                    d.ENABLE_CALLBACK && d.emitter.emit("log", "debug", v), d.ENABLE_DEBUG && (console.debug ? console.debug(v) : console.log(v));
                  }, d.v = function(f, p) {
                    (!f || d.FORCE_GLOBAL_TAG) && (f = d.GLOBAL_TAG);
                    var v = "[" + f + "] > " + p;
                    d.ENABLE_CALLBACK && d.emitter.emit("log", "verbose", v), d.ENABLE_VERBOSE && console.log(v);
                  }, d;
                }()
              );
              u.GLOBAL_TAG = "flv.js", u.FORCE_GLOBAL_TAG = !1, u.ENABLE_ERROR = !0, u.ENABLE_INFO = !0, u.ENABLE_WARN = !0, u.ENABLE_DEBUG = !0, u.ENABLE_VERBOSE = !0, u.ENABLE_CALLBACK = !1, u.emitter = new (h())(), o.default = u;
            }
          ),
          /***/
          "./src/utils/logging-control.js": (
            /*!**************************************!*\
              !*** ./src/utils/logging-control.js ***!
              \**************************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = l(
                /*! events */
                "./node_modules/events/events.js"
              ), h = /* @__PURE__ */ l.n(c), u = l(
                /*! ./logger.js */
                "./src/utils/logger.js"
              ), d = (
                /** @class */
                function() {
                  function f() {
                  }
                  return Object.defineProperty(f, "forceGlobalTag", {
                    get: function() {
                      return u.default.FORCE_GLOBAL_TAG;
                    },
                    set: function(p) {
                      u.default.FORCE_GLOBAL_TAG = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "globalTag", {
                    get: function() {
                      return u.default.GLOBAL_TAG;
                    },
                    set: function(p) {
                      u.default.GLOBAL_TAG = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableAll", {
                    get: function() {
                      return u.default.ENABLE_VERBOSE && u.default.ENABLE_DEBUG && u.default.ENABLE_INFO && u.default.ENABLE_WARN && u.default.ENABLE_ERROR;
                    },
                    set: function(p) {
                      u.default.ENABLE_VERBOSE = p, u.default.ENABLE_DEBUG = p, u.default.ENABLE_INFO = p, u.default.ENABLE_WARN = p, u.default.ENABLE_ERROR = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableDebug", {
                    get: function() {
                      return u.default.ENABLE_DEBUG;
                    },
                    set: function(p) {
                      u.default.ENABLE_DEBUG = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableVerbose", {
                    get: function() {
                      return u.default.ENABLE_VERBOSE;
                    },
                    set: function(p) {
                      u.default.ENABLE_VERBOSE = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableInfo", {
                    get: function() {
                      return u.default.ENABLE_INFO;
                    },
                    set: function(p) {
                      u.default.ENABLE_INFO = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableWarn", {
                    get: function() {
                      return u.default.ENABLE_WARN;
                    },
                    set: function(p) {
                      u.default.ENABLE_WARN = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), Object.defineProperty(f, "enableError", {
                    get: function() {
                      return u.default.ENABLE_ERROR;
                    },
                    set: function(p) {
                      u.default.ENABLE_ERROR = p, f._notifyChange();
                    },
                    enumerable: !1,
                    configurable: !0
                  }), f.getConfig = function() {
                    return {
                      globalTag: u.default.GLOBAL_TAG,
                      forceGlobalTag: u.default.FORCE_GLOBAL_TAG,
                      enableVerbose: u.default.ENABLE_VERBOSE,
                      enableDebug: u.default.ENABLE_DEBUG,
                      enableInfo: u.default.ENABLE_INFO,
                      enableWarn: u.default.ENABLE_WARN,
                      enableError: u.default.ENABLE_ERROR,
                      enableCallback: u.default.ENABLE_CALLBACK
                    };
                  }, f.applyConfig = function(p) {
                    u.default.GLOBAL_TAG = p.globalTag, u.default.FORCE_GLOBAL_TAG = p.forceGlobalTag, u.default.ENABLE_VERBOSE = p.enableVerbose, u.default.ENABLE_DEBUG = p.enableDebug, u.default.ENABLE_INFO = p.enableInfo, u.default.ENABLE_WARN = p.enableWarn, u.default.ENABLE_ERROR = p.enableError, u.default.ENABLE_CALLBACK = p.enableCallback;
                  }, f._notifyChange = function() {
                    var p = f.emitter;
                    if (p.listenerCount("change") > 0) {
                      var v = f.getConfig();
                      p.emit("change", v);
                    }
                  }, f.registerListener = function(p) {
                    f.emitter.addListener("change", p);
                  }, f.removeListener = function(p) {
                    f.emitter.removeListener("change", p);
                  }, f.addLogListener = function(p) {
                    u.default.emitter.addListener("log", p), u.default.emitter.listenerCount("log") > 0 && (u.default.ENABLE_CALLBACK = !0, f._notifyChange());
                  }, f.removeLogListener = function(p) {
                    u.default.emitter.removeListener("log", p), u.default.emitter.listenerCount("log") === 0 && (u.default.ENABLE_CALLBACK = !1, f._notifyChange());
                  }, f;
                }()
              );
              d.emitter = new (h())(), o.default = d;
            }
          ),
          /***/
          "./src/utils/polyfill.js": (
            /*!*******************************!*\
              !*** ./src/utils/polyfill.js ***!
              \*******************************/
            /***/
            function(a, o, l) {
              l.r(o);
              var c = (
                /** @class */
                function() {
                  function h() {
                  }
                  return h.install = function() {
                    Object.setPrototypeOf = Object.setPrototypeOf || function(u, d) {
                      return u.__proto__ = d, u;
                    }, Object.assign = Object.assign || function(u) {
                      if (u == null)
                        throw new TypeError("Cannot convert undefined or null to object");
                      for (var d = Object(u), f = 1; f < arguments.length; f++) {
                        var p = arguments[f];
                        if (p != null)
                          for (var v in p)
                            p.hasOwnProperty(v) && (d[v] = p[v]);
                      }
                      return d;
                    }, typeof self.Promise != "function" && l(
                      /*! es6-promise */
                      "./node_modules/es6-promise/dist/es6-promise.js"
                    ).polyfill();
                  }, h;
                }()
              );
              c.install(), o.default = c;
            }
          ),
          /***/
          "./src/utils/utf8-conv.js": (
            /*!********************************!*\
              !*** ./src/utils/utf8-conv.js ***!
              \********************************/
            /***/
            function(a, o, l) {
              l.r(o);
              function c(u, d, f) {
                var p = u;
                if (d + f < p.length) {
                  for (; f--; )
                    if ((p[++d] & 192) !== 128)
                      return !1;
                  return !0;
                } else
                  return !1;
              }
              function h(u) {
                for (var d = [], f = u, p = 0, v = u.length; p < v; ) {
                  if (f[p] < 128) {
                    d.push(String.fromCharCode(f[p])), ++p;
                    continue;
                  } else if (!(f[p] < 192)) {
                    if (f[p] < 224) {
                      if (c(f, p, 1)) {
                        var g = (f[p] & 31) << 6 | f[p + 1] & 63;
                        if (g >= 128) {
                          d.push(String.fromCharCode(g & 65535)), p += 2;
                          continue;
                        }
                      }
                    } else if (f[p] < 240) {
                      if (c(f, p, 2)) {
                        var g = (f[p] & 15) << 12 | (f[p + 1] & 63) << 6 | f[p + 2] & 63;
                        if (g >= 2048 && (g & 63488) !== 55296) {
                          d.push(String.fromCharCode(g & 65535)), p += 3;
                          continue;
                        }
                      }
                    } else if (f[p] < 248 && c(f, p, 3)) {
                      var g = (f[p] & 7) << 18 | (f[p + 1] & 63) << 12 | (f[p + 2] & 63) << 6 | f[p + 3] & 63;
                      if (g > 65536 && g < 1114112) {
                        g -= 65536, d.push(String.fromCharCode(g >>> 10 | 55296)), d.push(String.fromCharCode(g & 1023 | 56320)), p += 4;
                        continue;
                      }
                    }
                  }
                  d.push(String.fromCharCode(65533)), ++p;
                }
                return d.join("");
              }
              o.default = h;
            }
          )
          /******/
        }, i = {};
        function n(a) {
          var o = i[a];
          if (o !== void 0)
            return o.exports;
          var l = i[a] = {
            /******/
            // no module.id needed
            /******/
            // no module.loaded needed
            /******/
            exports: {}
            /******/
          };
          return t[a].call(l.exports, l, l.exports, n), l.exports;
        }
        n.m = t, function() {
          n.n = function(a) {
            var o = a && a.__esModule ? (
              /******/
              function() {
                return a.default;
              }
            ) : (
              /******/
              function() {
                return a;
              }
            );
            return n.d(o, { a: o }), o;
          };
        }(), function() {
          n.d = function(a, o) {
            for (var l in o)
              n.o(o, l) && !n.o(a, l) && Object.defineProperty(a, l, { enumerable: !0, get: o[l] });
          };
        }(), function() {
          n.g = function() {
            if (typeof globalThis == "object")
              return globalThis;
            try {
              return this || new Function("return this")();
            } catch {
              if (typeof window == "object")
                return window;
            }
          }();
        }(), function() {
          n.o = function(a, o) {
            return Object.prototype.hasOwnProperty.call(a, o);
          };
        }(), function() {
          n.r = function(a) {
            typeof Symbol < "u" && Symbol.toStringTag && Object.defineProperty(a, Symbol.toStringTag, { value: "Module" }), Object.defineProperty(a, "__esModule", { value: !0 });
          };
        }();
        var r = n("./src/index.js");
        return r;
      }()
    );
  });
})(TB);
var Koe = TB.exports;
const Joe = /* @__PURE__ */ fM(Koe);
var CB = { exports: {} };
(function(s, e) {
  (function(t) {
    s.exports = t();
  })(function(t) {
    var i = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "a", "b", "c", "d", "e", "f"];
    function n(m, x) {
      var y = m[0], A = m[1], b = m[2], M = m[3];
      y += (A & b | ~A & M) + x[0] - 680876936 | 0, y = (y << 7 | y >>> 25) + A | 0, M += (y & A | ~y & b) + x[1] - 389564586 | 0, M = (M << 12 | M >>> 20) + y | 0, b += (M & y | ~M & A) + x[2] + 606105819 | 0, b = (b << 17 | b >>> 15) + M | 0, A += (b & M | ~b & y) + x[3] - 1044525330 | 0, A = (A << 22 | A >>> 10) + b | 0, y += (A & b | ~A & M) + x[4] - 176418897 | 0, y = (y << 7 | y >>> 25) + A | 0, M += (y & A | ~y & b) + x[5] + 1200080426 | 0, M = (M << 12 | M >>> 20) + y | 0, b += (M & y | ~M & A) + x[6] - 1473231341 | 0, b = (b << 17 | b >>> 15) + M | 0, A += (b & M | ~b & y) + x[7] - 45705983 | 0, A = (A << 22 | A >>> 10) + b | 0, y += (A & b | ~A & M) + x[8] + 1770035416 | 0, y = (y << 7 | y >>> 25) + A | 0, M += (y & A | ~y & b) + x[9] - 1958414417 | 0, M = (M << 12 | M >>> 20) + y | 0, b += (M & y | ~M & A) + x[10] - 42063 | 0, b = (b << 17 | b >>> 15) + M | 0, A += (b & M | ~b & y) + x[11] - 1990404162 | 0, A = (A << 22 | A >>> 10) + b | 0, y += (A & b | ~A & M) + x[12] + 1804603682 | 0, y = (y << 7 | y >>> 25) + A | 0, M += (y & A | ~y & b) + x[13] - 40341101 | 0, M = (M << 12 | M >>> 20) + y | 0, b += (M & y | ~M & A) + x[14] - 1502002290 | 0, b = (b << 17 | b >>> 15) + M | 0, A += (b & M | ~b & y) + x[15] + 1236535329 | 0, A = (A << 22 | A >>> 10) + b | 0, y += (A & M | b & ~M) + x[1] - 165796510 | 0, y = (y << 5 | y >>> 27) + A | 0, M += (y & b | A & ~b) + x[6] - 1069501632 | 0, M = (M << 9 | M >>> 23) + y | 0, b += (M & A | y & ~A) + x[11] + 643717713 | 0, b = (b << 14 | b >>> 18) + M | 0, A += (b & y | M & ~y) + x[0] - 373897302 | 0, A = (A << 20 | A >>> 12) + b | 0, y += (A & M | b & ~M) + x[5] - 701558691 | 0, y = (y << 5 | y >>> 27) + A | 0, M += (y & b | A & ~b) + x[10] + 38016083 | 0, M = (M << 9 | M >>> 23) + y | 0, b += (M & A | y & ~A) + x[15] - 660478335 | 0, b = (b << 14 | b >>> 18) + M | 0, A += (b & y | M & ~y) + x[4] - 405537848 | 0, A = (A << 20 | A >>> 12) + b | 0, y += (A & M | b & ~M) + x[9] + 568446438 | 0, y = (y << 5 | y >>> 27) + A | 0, M += (y & b | A & ~b) + x[14] - 1019803690 | 0, M = (M << 9 | M >>> 23) + y | 0, b += (M & A | y & ~A) + x[3] - 187363961 | 0, b = (b << 14 | b >>> 18) + M | 0, A += (b & y | M & ~y) + x[8] + 1163531501 | 0, A = (A << 20 | A >>> 12) + b | 0, y += (A & M | b & ~M) + x[13] - 1444681467 | 0, y = (y << 5 | y >>> 27) + A | 0, M += (y & b | A & ~b) + x[2] - 51403784 | 0, M = (M << 9 | M >>> 23) + y | 0, b += (M & A | y & ~A) + x[7] + 1735328473 | 0, b = (b << 14 | b >>> 18) + M | 0, A += (b & y | M & ~y) + x[12] - 1926607734 | 0, A = (A << 20 | A >>> 12) + b | 0, y += (A ^ b ^ M) + x[5] - 378558 | 0, y = (y << 4 | y >>> 28) + A | 0, M += (y ^ A ^ b) + x[8] - 2022574463 | 0, M = (M << 11 | M >>> 21) + y | 0, b += (M ^ y ^ A) + x[11] + 1839030562 | 0, b = (b << 16 | b >>> 16) + M | 0, A += (b ^ M ^ y) + x[14] - 35309556 | 0, A = (A << 23 | A >>> 9) + b | 0, y += (A ^ b ^ M) + x[1] - 1530992060 | 0, y = (y << 4 | y >>> 28) + A | 0, M += (y ^ A ^ b) + x[4] + 1272893353 | 0, M = (M << 11 | M >>> 21) + y | 0, b += (M ^ y ^ A) + x[7] - 155497632 | 0, b = (b << 16 | b >>> 16) + M | 0, A += (b ^ M ^ y) + x[10] - 1094730640 | 0, A = (A << 23 | A >>> 9) + b | 0, y += (A ^ b ^ M) + x[13] + 681279174 | 0, y = (y << 4 | y >>> 28) + A | 0, M += (y ^ A ^ b) + x[0] - 358537222 | 0, M = (M << 11 | M >>> 21) + y | 0, b += (M ^ y ^ A) + x[3] - 722521979 | 0, b = (b << 16 | b >>> 16) + M | 0, A += (b ^ M ^ y) + x[6] + 76029189 | 0, A = (A << 23 | A >>> 9) + b | 0, y += (A ^ b ^ M) + x[9] - 640364487 | 0, y = (y << 4 | y >>> 28) + A | 0, M += (y ^ A ^ b) + x[12] - 421815835 | 0, M = (M << 11 | M >>> 21) + y | 0, b += (M ^ y ^ A) + x[15] + 530742520 | 0, b = (b << 16 | b >>> 16) + M | 0, A += (b ^ M ^ y) + x[2] - 995338651 | 0, A = (A << 23 | A >>> 9) + b | 0, y += (b ^ (A | ~M)) + x[0] - 198630844 | 0, y = (y << 6 | y >>> 26) + A | 0, M += (A ^ (y | ~b)) + x[7] + 1126891415 | 0, M = (M << 10 | M >>> 22) + y | 0, b += (y ^ (M | ~A)) + x[14] - 1416354905 | 0, b = (b << 15 | b >>> 17) + M | 0, A += (M ^ (b | ~y)) + x[5] - 57434055 | 0, A = (A << 21 | A >>> 11) + b | 0, y += (b ^ (A | ~M)) + x[12] + 1700485571 | 0, y = (y << 6 | y >>> 26) + A | 0, M += (A ^ (y | ~b)) + x[3] - 1894986606 | 0, M = (M << 10 | M >>> 22) + y | 0, b += (y ^ (M | ~A)) + x[10] - 1051523 | 0, b = (b << 15 | b >>> 17) + M | 0, A += (M ^ (b | ~y)) + x[1] - 2054922799 | 0, A = (A << 21 | A >>> 11) + b | 0, y += (b ^ (A | ~M)) + x[8] + 1873313359 | 0, y = (y << 6 | y >>> 26) + A | 0, M += (A ^ (y | ~b)) + x[15] - 30611744 | 0, M = (M << 10 | M >>> 22) + y | 0, b += (y ^ (M | ~A)) + x[6] - 1560198380 | 0, b = (b << 15 | b >>> 17) + M | 0, A += (M ^ (b | ~y)) + x[13] + 1309151649 | 0, A = (A << 21 | A >>> 11) + b | 0, y += (b ^ (A | ~M)) + x[4] - 145523070 | 0, y = (y << 6 | y >>> 26) + A | 0, M += (A ^ (y | ~b)) + x[11] - 1120210379 | 0, M = (M << 10 | M >>> 22) + y | 0, b += (y ^ (M | ~A)) + x[2] + 718787259 | 0, b = (b << 15 | b >>> 17) + M | 0, A += (M ^ (b | ~y)) + x[9] - 343485551 | 0, A = (A << 21 | A >>> 11) + b | 0, m[0] = y + m[0] | 0, m[1] = A + m[1] | 0, m[2] = b + m[2] | 0, m[3] = M + m[3] | 0;
    }
    function r(m) {
      var x = [], y;
      for (y = 0; y < 64; y += 4)
        x[y >> 2] = m.charCodeAt(y) + (m.charCodeAt(y + 1) << 8) + (m.charCodeAt(y + 2) << 16) + (m.charCodeAt(y + 3) << 24);
      return x;
    }
    function a(m) {
      var x = [], y;
      for (y = 0; y < 64; y += 4)
        x[y >> 2] = m[y] + (m[y + 1] << 8) + (m[y + 2] << 16) + (m[y + 3] << 24);
      return x;
    }
    function o(m) {
      var x = m.length, y = [1732584193, -271733879, -1732584194, 271733878], A, b, M, w, C, T;
      for (A = 64; A <= x; A += 64)
        n(y, r(m.substring(A - 64, A)));
      for (m = m.substring(A - 64), b = m.length, M = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], A = 0; A < b; A += 1)
        M[A >> 2] |= m.charCodeAt(A) << (A % 4 << 3);
      if (M[A >> 2] |= 128 << (A % 4 << 3), A > 55)
        for (n(y, M), A = 0; A < 16; A += 1)
          M[A] = 0;
      return w = x * 8, w = w.toString(16).match(/(.*?)(.{0,8})$/), C = parseInt(w[2], 16), T = parseInt(w[1], 16) || 0, M[14] = C, M[15] = T, n(y, M), y;
    }
    function l(m) {
      var x = m.length, y = [1732584193, -271733879, -1732584194, 271733878], A, b, M, w, C, T;
      for (A = 64; A <= x; A += 64)
        n(y, a(m.subarray(A - 64, A)));
      for (m = A - 64 < x ? m.subarray(A - 64) : new Uint8Array(0), b = m.length, M = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], A = 0; A < b; A += 1)
        M[A >> 2] |= m[A] << (A % 4 << 3);
      if (M[A >> 2] |= 128 << (A % 4 << 3), A > 55)
        for (n(y, M), A = 0; A < 16; A += 1)
          M[A] = 0;
      return w = x * 8, w = w.toString(16).match(/(.*?)(.{0,8})$/), C = parseInt(w[2], 16), T = parseInt(w[1], 16) || 0, M[14] = C, M[15] = T, n(y, M), y;
    }
    function c(m) {
      var x = "", y;
      for (y = 0; y < 4; y += 1)
        x += i[m >> y * 8 + 4 & 15] + i[m >> y * 8 & 15];
      return x;
    }
    function h(m) {
      var x;
      for (x = 0; x < m.length; x += 1)
        m[x] = c(m[x]);
      return m.join("");
    }
    h(o("hello")), typeof ArrayBuffer < "u" && !ArrayBuffer.prototype.slice && function() {
      function m(x, y) {
        return x = x | 0 || 0, x < 0 ? Math.max(x + y, 0) : Math.min(x, y);
      }
      ArrayBuffer.prototype.slice = function(x, y) {
        var A = this.byteLength, b = m(x, A), M = A, w, C, T, E;
        return y !== t && (M = m(y, A)), b > M ? new ArrayBuffer(0) : (w = M - b, C = new ArrayBuffer(w), T = new Uint8Array(C), E = new Uint8Array(this, b, w), T.set(E), C);
      };
    }();
    function u(m) {
      return /[\u0080-\uFFFF]/.test(m) && (m = unescape(encodeURIComponent(m))), m;
    }
    function d(m, x) {
      var y = m.length, A = new ArrayBuffer(y), b = new Uint8Array(A), M;
      for (M = 0; M < y; M += 1)
        b[M] = m.charCodeAt(M);
      return x ? b : A;
    }
    function f(m) {
      return String.fromCharCode.apply(null, new Uint8Array(m));
    }
    function p(m, x, y) {
      var A = new Uint8Array(m.byteLength + x.byteLength);
      return A.set(new Uint8Array(m)), A.set(new Uint8Array(x), m.byteLength), A ;
    }
    function v(m) {
      var x = [], y = m.length, A;
      for (A = 0; A < y - 1; A += 2)
        x.push(parseInt(m.substr(A, 2), 16));
      return String.fromCharCode.apply(String, x);
    }
    function g() {
      this.reset();
    }
    return g.prototype.append = function(m) {
      return this.appendBinary(u(m)), this;
    }, g.prototype.appendBinary = function(m) {
      this._buff += m, this._length += m.length;
      var x = this._buff.length, y;
      for (y = 64; y <= x; y += 64)
        n(this._hash, r(this._buff.substring(y - 64, y)));
      return this._buff = this._buff.substring(y - 64), this;
    }, g.prototype.end = function(m) {
      var x = this._buff, y = x.length, A, b = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], M;
      for (A = 0; A < y; A += 1)
        b[A >> 2] |= x.charCodeAt(A) << (A % 4 << 3);
      return this._finish(b, y), M = h(this._hash), m && (M = v(M)), this.reset(), M;
    }, g.prototype.reset = function() {
      return this._buff = "", this._length = 0, this._hash = [1732584193, -271733879, -1732584194, 271733878], this;
    }, g.prototype.getState = function() {
      return {
        buff: this._buff,
        length: this._length,
        hash: this._hash.slice()
      };
    }, g.prototype.setState = function(m) {
      return this._buff = m.buff, this._length = m.length, this._hash = m.hash, this;
    }, g.prototype.destroy = function() {
      delete this._hash, delete this._buff, delete this._length;
    }, g.prototype._finish = function(m, x) {
      var y = x, A, b, M;
      if (m[y >> 2] |= 128 << (y % 4 << 3), y > 55)
        for (n(this._hash, m), y = 0; y < 16; y += 1)
          m[y] = 0;
      A = this._length * 8, A = A.toString(16).match(/(.*?)(.{0,8})$/), b = parseInt(A[2], 16), M = parseInt(A[1], 16) || 0, m[14] = b, m[15] = M, n(this._hash, m);
    }, g.hash = function(m, x) {
      return g.hashBinary(u(m), x);
    }, g.hashBinary = function(m, x) {
      var y = o(m), A = h(y);
      return x ? v(A) : A;
    }, g.ArrayBuffer = function() {
      this.reset();
    }, g.ArrayBuffer.prototype.append = function(m) {
      var x = p(this._buff.buffer, m), y = x.length, A;
      for (this._length += m.byteLength, A = 64; A <= y; A += 64)
        n(this._hash, a(x.subarray(A - 64, A)));
      return this._buff = A - 64 < y ? new Uint8Array(x.buffer.slice(A - 64)) : new Uint8Array(0), this;
    }, g.ArrayBuffer.prototype.end = function(m) {
      var x = this._buff, y = x.length, A = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], b, M;
      for (b = 0; b < y; b += 1)
        A[b >> 2] |= x[b] << (b % 4 << 3);
      return this._finish(A, y), M = h(this._hash), m && (M = v(M)), this.reset(), M;
    }, g.ArrayBuffer.prototype.reset = function() {
      return this._buff = new Uint8Array(0), this._length = 0, this._hash = [1732584193, -271733879, -1732584194, 271733878], this;
    }, g.ArrayBuffer.prototype.getState = function() {
      var m = g.prototype.getState.call(this);
      return m.buff = f(m.buff), m;
    }, g.ArrayBuffer.prototype.setState = function(m) {
      return m.buff = d(m.buff, !0), g.prototype.setState.call(this, m);
    }, g.ArrayBuffer.prototype.destroy = g.prototype.destroy, g.ArrayBuffer.prototype._finish = g.prototype._finish, g.ArrayBuffer.hash = function(m, x) {
      var y = l(new Uint8Array(m)), A = h(y);
      return x ? v(A) : A;
    }, g;
  });
})(CB);
var Qoe = CB.exports;
function $oe(s, e, t, i = "clip") {
  const n = s.width, r = s.height, a = t.width, o = t.height;
  let l;
  i === "clip" ? l = Math.max(n / a, r / o) : l = Math.min(n / a, r / o);
  let c = a * l, h = o * l, u = (n - c) / 2, d = (r - h) / 2;
  e.drawImage(t, u, d, c, h);
}
class e0e {
  constructor() {
    S(this, "canvas");
    const e = document.createElement("canvas");
    e.width = 12, e.height = 12, e.style.cursor = "pointer", e.style.marginRight = "5px", e.style.border = "1px solid #888", this.canvas = e;
  }
  getThumbnail(e) {
    const t = this.canvas.getContext("2d");
    return t && t.clearRect(0, 0, this.canvas.width, this.canvas.height), e != null && e.width > 0 ? $oe(this.canvas, t, e, "clip") : console.log("getThumbnail error"), this.canvas.toDataURL();
  }
}
const i4 = class i4 extends wa {
  constructor(t) {
    super(t);
    S(this, "url");
    S(this, "indexId");
    S(this, "name");
    S(this, "serverId");
    S(this, "thumbnail");
    S(this, "originFileSize");
    S(this, "originFileMimeType");
    this.name = "", this.thumbnail = i4.textureImageThumbnail.getThumbnail(t);
  }
  toJSON(t) {
    return {
      url: this.url ?? "",
      indexId: this.indexId ?? "",
      name: this.name
    };
  }
};
S(i4, "textureImageThumbnail", new e0e());
let Pp = i4;
const _B = (s, e, t = []) => {
  const i = Object.getOwnPropertyDescriptors(e);
  for (let n of t)
    delete i[n];
  Object.defineProperties(s, i);
}, Og = (s, e = [s]) => {
  const t = Object.getPrototypeOf(s);
  return t === null ? e : Og(t, [...e, t]);
}, t0e = (...s) => {
  if (s.length === 0)
    return;
  let e;
  const t = s.map((i) => Og(i));
  for (; t.every((i) => i.length > 0); ) {
    const i = t.map((r) => r.pop()), n = i[0];
    if (i.every((r) => r === n))
      e = n;
    else
      break;
  }
  return e;
}, H_ = (s, e, t = []) => {
  var i;
  const n = (i = t0e(...s)) !== null && i !== void 0 ? i : Object.prototype, r = Object.create(n), a = Og(n);
  for (let o of s) {
    let l = Og(o);
    for (let c = l.length - 1; c >= 0; c--) {
      let h = l[c];
      a.indexOf(h) === -1 && (_B(r, h, ["constructor", ...t]), a.push(h));
    }
  }
  return r.constructor = e, r;
}, zb = (s) => s.filter((e, t) => s.indexOf(e) == t), PB = /* @__PURE__ */ new WeakMap(), i0e = (s) => PB.get(s), n0e = (s, e) => PB.set(s, e), V_ = (s, e) => {
  var t, i;
  const n = zb([...Object.getOwnPropertyNames(s), ...Object.getOwnPropertyNames(e)]), r = {};
  for (let a of n)
    r[a] = zb([...(t = s == null ? void 0 : s[a]) !== null && t !== void 0 ? t : [], ...(i = e == null ? void 0 : e[a]) !== null && i !== void 0 ? i : []]);
  return r;
}, W_ = (s, e) => {
  var t, i, n, r;
  return {
    property: V_((t = s == null ? void 0 : s.property) !== null && t !== void 0 ? t : {}, (i = e == null ? void 0 : e.property) !== null && i !== void 0 ? i : {}),
    method: V_((n = s == null ? void 0 : s.method) !== null && n !== void 0 ? n : {}, (r = e == null ? void 0 : e.method) !== null && r !== void 0 ? r : {})
  };
}, s0e = (s, e) => {
  var t, i, n, r, a, o;
  return {
    class: zb([...(t = s == null ? void 0 : s.class) !== null && t !== void 0 ? t : [], ...(i = e == null ? void 0 : e.class) !== null && i !== void 0 ? i : []]),
    static: W_((n = s == null ? void 0 : s.static) !== null && n !== void 0 ? n : {}, (r = e == null ? void 0 : e.static) !== null && r !== void 0 ? r : {}),
    instance: W_((a = s == null ? void 0 : s.instance) !== null && a !== void 0 ? a : {}, (o = e == null ? void 0 : e.instance) !== null && o !== void 0 ? o : {})
  };
}, r0e = /* @__PURE__ */ new Map(), a0e = (...s) => {
  var e;
  const t = /* @__PURE__ */ new Set(), i = /* @__PURE__ */ new Set([...s]);
  for (; i.size > 0; )
    for (let n of i) {
      const r = Og(n.prototype).map((c) => c.constructor), a = (e = i0e(n)) !== null && e !== void 0 ? e : [], l = [...r, ...a].filter((c) => !t.has(c));
      for (let c of l)
        i.add(c);
      t.add(n), i.delete(n);
    }
  return [...t];
}, o0e = (...s) => {
  const e = a0e(...s).map((t) => r0e.get(t)).filter((t) => !!t);
  return e.length == 0 ? {} : e.length == 1 ? e[0] : e.reduce((t, i) => s0e(t, i));
};
function hw(...s) {
  var e, t, i;
  const n = s.map((o) => o.prototype);
  function r(...o) {
    for (const l of s)
      _B(this, new l(...o));
  }
  r.prototype = H_(n, r), Object.setPrototypeOf(
    r,
    H_(s, null, ["prototype"])
  );
  let a = r;
  {
    const o = o0e(...s);
    for (let l of (e = o == null ? void 0 : o.class) !== null && e !== void 0 ? e : []) {
      const c = l(a);
      c && (a = c);
    }
    X_((t = o == null ? void 0 : o.static) !== null && t !== void 0 ? t : {}, a), X_((i = o == null ? void 0 : o.instance) !== null && i !== void 0 ? i : {}, a.prototype);
  }
  return n0e(a, s), a;
}
const X_ = (s, e) => {
  const t = s.property, i = s.method;
  if (t)
    for (let n in t)
      for (let r of t[n])
        r(e, n);
  if (i)
    for (let n in i)
      for (let r of i[n])
        r(e, n, Object.getOwnPropertyDescriptor(e, n));
};
class $p {
  constructor() {
    S(this, "url", "");
    S(this, "filename", "");
    S(this, "indexId", "");
    S(this, "fileType", "");
  }
  toJSON() {
    return {
      url: this.url,
      filename: this.filename,
      indexId: this.indexId,
      fileType: this.fileType
    };
  }
  fromJSON(e) {
    this.url = e.url ?? "", this.indexId = e.indexId ?? "";
  }
  clone() {
    const e = new $p();
    return e.fileType = this.fileType, e.indexId = this.indexId, e.filename = this.filename, e.url = this.url, e;
  }
}
class uw extends Re {
  constructor() {
    super(...arguments);
    S(this, "source", new $p());
    S(this, "extendType", "TwinMesh");
    S(this, "isTwinMesh", !0);
  }
  copy(t, i) {
    return super.copy(t, i), this.extendType === "TwinMesh" && (this.source = t.source.clone()), this;
  }
}
class dw extends ho {
  constructor() {
    super(...arguments);
    S(this, "source", new $p());
    S(this, "extendType", "TwinInstancedMesh");
    S(this, "isTwinInstancedMesh", !0);
  }
  copy(t, i) {
    return super.copy(t, i), this.extendType === "TwinInstancedMesh" && (this.source = t.source.clone()), this;
  }
}
class fw extends a2 {
  constructor() {
    super(...arguments);
    S(this, "source", new $p());
    S(this, "extendType", "TwinSkinnedMesh");
    S(this, "isTwinSkinnedMesh", !0);
  }
  copy(t, i) {
    return super.copy(t, i), this.extendType === "TwinSkinnedMesh" && (this.source = t.source.clone()), this;
  }
}
class Wc extends Ut {
  constructor() {
    super(...arguments);
    S(this, "imageSource", new $p());
  }
}
function pw(s) {
  return JSON.parse(JSON.stringify(s));
}
function mw(s) {
  const e = {
    metadata: {
      version: 4.6,
      type: "Texture",
      generator: "Texture.toJSON"
    },
    name: s.name,
    mapping: s.mapping,
    channel: s.channel,
    repeat: [s.repeat.x, s.repeat.y],
    offset: [s.offset.x, s.offset.y],
    center: [s.center.x, s.center.y],
    rotation: s.rotation,
    wrap: [s.wrapS, s.wrapT],
    format: s.format,
    internalFormat: s.internalFormat,
    type: s.type,
    colorSpace: s.colorSpace,
    minFilter: s.minFilter,
    magFilter: s.magFilter,
    anisotropy: s.anisotropy,
    flipY: s.flipY,
    generateMipmaps: s.generateMipmaps,
    premultiplyAlpha: s.premultiplyAlpha,
    unpackAlignment: s.unpackAlignment
  };
  if (s.imageSource) {
    const t = s.imageSource;
    e.source = {
      url: t.url,
      filename: t.filename,
      fileType: t.fileType
    };
  }
  return s.userData.transform || (s.userData.transform = {
    repeat: [s.repeat.x, s.repeat.y],
    offset: [s.offset.x, s.offset.y],
    center: [s.center.x, s.center.y],
    rotation: s.rotation,
    offsetSpeed: [0, 0],
    rotationSpeed: 0
  }), Object.keys(s.userData).length > 0 && (e.userData = s.userData), e;
}
function O2(s) {
  const e = new Wc();
  return e.name = s.name, e.mapping = s.mapping, e.channel = s.channel, e.repeat = new Se(s.repeat[0], s.repeat[1]), e.offset = new Se(s.offset[0], s.offset[1]), e.center = new Se(s.center[0], s.center[1]), e.rotation = s.rotation, e.wrapS = s.wrap[0], e.wrapT = s.wrap[1], e.format = s.format, e.internalFormat = s.internalFormat, e.type = s.type, e.colorSpace = s.colorSpace, e.minFilter = s.minFilter, e.magFilter = s.magFilter, e.anisotropy = s.anisotropy, e.flipY = s.flipY, e.generateMipmaps = s.generateMipmaps, e.premultiplyAlpha = s.premultiplyAlpha, e.unpackAlignment = s.unpackAlignment, s.userData && Object.keys(s.userData).length > 0 && (e.userData = pw(s.userData)), s.source && (e.imageSource.url = s.source.url, e.imageSource.filename = s.source.filename, e.imageSource.fileType = s.source.fileType), e;
}
function l0e(s) {
  const e = new Wc();
  return e.name = s.name, e.mapping = s.mapping, e.channel = s.channel, e.wrapS = s.wrap[0], e.wrapT = s.wrap[1], e.format = s.format, e.internalFormat = s.internalFormat, e.type = s.type, e.colorSpace = s.colorSpace, e.minFilter = s.minFilter, e.magFilter = s.magFilter, e.anisotropy = s.anisotropy, e.flipY = s.flipY, e.generateMipmaps = s.generateMipmaps, e.premultiplyAlpha = s.premultiplyAlpha, e.unpackAlignment = s.unpackAlignment, Object.keys(s.userData).length > 0 && (e.userData = pw(s.userData)), e;
}
function j_(s, e) {
  return s.name = e.name, s.mapping = e.mapping, s.channel = e.channel, s.wrapS = e.wrapS, s.wrapT = e.wrapT, s.magFilter = e.magFilter, s.minFilter = e.minFilter, s.anisotropy = e.anisotropy, s.format = e.format, s.internalFormat = e.internalFormat, s.type = e.type, s.offset.copy(e.offset), s.repeat.copy(e.repeat), s.center.copy(e.center), s.rotation = e.rotation, s.matrixAutoUpdate = e.matrixAutoUpdate, s.matrix.copy(e.matrix), s.generateMipmaps = e.generateMipmaps, s.premultiplyAlpha = e.premultiplyAlpha, s.flipY = e.flipY, s.unpackAlignment = e.unpackAlignment, s.colorSpace = e.colorSpace, s.userData = JSON.parse(JSON.stringify(e.userData)), s.needsUpdate = !0, s;
}
class gw {
  constructor() {
    S(this, "offsetSpeed", new Se());
    S(this, "rotationSpeed", 0);
    S(this, "offset", new Se());
    S(this, "repeat", new Se(0, 0));
    S(this, "center", new Se());
    S(this, "rotation", 0);
  }
  clone() {
    const e = new gw();
    return e.offsetSpeed.copy(this.offsetSpeed), e.rotationSpeed = this.rotationSpeed, e.offset.copy(this.offset), e.repeat.copy(this.repeat), e.center.copy(this.center), e.rotation = this.rotation, e;
  }
  toJSON() {
    return {
      offsetSpeed: this.offsetSpeed.toArray(),
      rotationSpeed: this.rotationSpeed,
      offset: this.offset.toArray(),
      repeat: this.repeat.toArray(),
      center: this.center.toArray(),
      rotation: this.rotation
    };
  }
}
class vw {
  constructor() {
    S(this, "saturation", 1);
    S(this, "contrast", 1);
    S(this, "brightness", 0);
  }
  clone() {
    const e = new vw();
    return e.saturation = this.saturation, e.contrast = this.contrast, e.brightness = this.brightness, e;
  }
  toJSON() {
    return {
      saturation: this.saturation,
      contrast: this.contrast,
      brightness: this.brightness
    };
  }
}
const If = class If {
  constructor() {
    S(this, "serverData", {
      id: "",
      type: "privateMaterial",
      name: "",
      tag: ""
    });
    // 存储服务器端id，方便更新服务器端材质配置项
    S(this, "defaultConfig", {});
    // 存储材质的默认配置项
    S(this, "uvAnimationData", new gw());
    S(this, "mapTextureParams", new vw());
    S(this, "uniforms", {
      map_saturation: {
        value: 1
      },
      map_contrast: {
        value: 1
      },
      map_brightness: {
        value: 0
      }
    });
    S(this, "needUpdateThumbnail", !0);
    this.updateMapParamsUniform();
  }
  set MapBrightness(e) {
    this.mapTextureParams.brightness = e, this.uniforms.map_brightness.value = e;
  }
  set MapContrast(e) {
    this.mapTextureParams.contrast = e, this.uniforms.map_contrast.value = e;
  }
  set MapSaturation(e) {
    this.mapTextureParams.saturation = e, this.uniforms.map_saturation.value = e;
  }
  updateMapParamsUniform() {
    this.uniforms.map_brightness.value = this.mapTextureParams.brightness, this.uniforms.map_contrast.value = this.mapTextureParams.contrast, this.uniforms.map_saturation.value = this.mapTextureParams.saturation;
  }
  addMapColorAdjustShaderCode(e) {
    e.uniforms = { ...e.uniforms, ...this.uniforms }, this.uniforms = e.uniforms, e.fragmentShader = e.fragmentShader.replace(
      "void main() {",
      `
    uniform float map_saturation;
    uniform float map_contrast;
    uniform float map_brightness;
    void main() {
    `
    ), e.fragmentShader = e.fragmentShader.replace(
      "#include <map_fragment>",
      `
    #include <map_fragment>
    
    // Begin MapColorAdjust
    vec3 rgb = diffuseColor.rgb;
    rgb = mix(vec3(dot(vec3(.2125, .7154, .0721), rgb)), rgb, map_saturation);
    rgb = mix(vec3(.5), rgb, map_contrast);
    diffuseColor.rgb = rgb + map_brightness;
    // End MapColorAdjust
    `
    );
  }
  addVideoProjectShaderCode(e) {
    this.uniforms = { ...e.uniforms, ...If.sharedUniforms }, e.uniforms = this.uniforms, e.vertexShader = q_(e.vertexShader, {
      header: (
        /* glsl */
        `
          #define numVideos 5

          struct ProjectCamera {
            mat4 viewMatrixCamera;
            mat4 projectionMatrixCamera;
            vec3 projPosition;
            vec3 projDirection;
            bool visible;
          };
          // sampler2D尽量不要放在struct中
          uniform sampler2D videoTextures[numVideos];
          uniform ProjectCamera projectCameras[numVideos];

					varying vec3 vSavedNormal;
					varying vec4 vTexCoords[numVideos];
          varying vec4 vWorldPosition2;
				`
      ),
      main: (
        /* glsl */
        `
          #ifdef USE_INSTANCING
            mat4 modelTransformMatrix = instanceMatrix;
          #else
            mat4 modelTransformMatrix = modelMatrix;
          #endif
					vSavedNormal = mat3(modelTransformMatrix) * normal;
          for (int i = 0; i < numVideos; ++i) {
            vTexCoords[i] = projectCameras[i].projectionMatrixCamera * projectCameras[i].viewMatrixCamera * modelTransformMatrix * vec4(position, 1.0);
          }
          vWorldPosition2 = modelTransformMatrix * vec4(position, 1.0);
				`
      )
    }), e.fragmentShader = q_(e.fragmentShader, {
      header: (
        /* glsl */
        `
          #define numVideos 5
          
          struct ProjectCamera {
            mat4 viewMatrixCamera;
            mat4 projectionMatrixCamera;
            vec3 projPosition;
            vec3 projDirection;
            bool visible;
          };

          uniform sampler2D videoTextures[numVideos];
          uniform ProjectCamera projectCameras[numVideos];

					uniform bool isTextureLoaded;
					uniform bool isTextureProjected;
					uniform float backgroundOpacity;
					uniform bool frontFacesOnly;

					varying vec3 vSavedNormal;
					varying vec4 vTexCoords[numVideos];
          varying vec4 vWorldPosition2;

          vec4 getProjectVideoColor(vec4 vTexCoord, ProjectCamera projectCamera, sampler2D videoTexture) {

            float w = max(vTexCoord.w, 0.0);
            
            vec2 uv = (vTexCoord.xy / w) * 0.5 + 0.5;
            
            // this makes sure we don't sample out of the texture
            bool isInTexture = (max(uv.x, uv.y) <= 1.0 && min(uv.x, uv.y) >= 0.0);
            
            vec3 projectorDirection = normalize(projectCamera.projPosition - vWorldPosition2.xyz);
            float dotProduct = dot(vSavedNormal, projectorDirection);
            bool isFacingProjector = frontFacesOnly ? dotProduct > 0.0000001 : true;
            
            vec4 diffuseColor = vec4(0.0, 0.0, 0.0, 0.0);
            
            if (isFacingProjector && isInTexture && isTextureLoaded && isTextureProjected) {
              vec4 textureColor = texture2D(videoTexture, uv);
              diffuseColor = vec4(textureColor.xyz, 1.0);
            }

            return diffuseColor;
          }
				`
      ),
      "// End MapColorAdjust": (
        /* glsl */
        `
          // End MapColorAdjust

          // Begin ProjectVideo
          vec4 color0 = projectCameras[0].visible ? getProjectVideoColor(vTexCoords[0], projectCameras[0], videoTextures[0]) : vec4(0.0, 0.0, 0.0, 0.0);
          vec4 color1 = projectCameras[1].visible ? getProjectVideoColor(vTexCoords[1], projectCameras[1], videoTextures[1]) : vec4(0.0, 0.0, 0.0, 0.0);
          vec4 color2 = projectCameras[2].visible ? getProjectVideoColor(vTexCoords[2], projectCameras[2], videoTextures[2]) : vec4(0.0, 0.0, 0.0, 0.0);
          vec4 color3 = projectCameras[3].visible ? getProjectVideoColor(vTexCoords[3], projectCameras[3], videoTextures[3]) : vec4(0.0, 0.0, 0.0, 0.0);
          vec4 color4 = projectCameras[4].visible ? getProjectVideoColor(vTexCoords[4], projectCameras[4], videoTextures[4]) : vec4(0.0, 0.0, 0.0, 0.0);
          vec4 textureColor = (color0 + color1 + color2 + color3 + color4);
          diffuseColor = textureColor * clamp(textureColor.a, 0.0, 1.0) + diffuseColor * (1.0 - clamp(textureColor.a, 0.0, 1.0));
          // End ProjectVideo
				`
      )
    });
  }
  static getMaterialConfig(e) {
    const { json: t, textures: i } = e.toJSON(), n = {};
    for (const [r, a] of Object.entries(i))
      a instanceof Ut && (n[r] = mw(a), a instanceof Wc && (n[r].source = a.imageSource.toJSON()));
    for (const [r, a] of Object.entries(t))
      r.toLowerCase().endsWith("map") && a !== void 0 && n[a] && (t[r] = n[a]);
    return e.serverData.type === "privateMaterial" && (e.defaultConfig && Object.keys(e.defaultConfig).length > 0 ? t.defaultConfig = JSON.parse(JSON.stringify(e.defaultConfig)) : (t.defaultConfig = JSON.parse(JSON.stringify(t)), e.defaultConfig = t.defaultConfig)), t;
  }
  addMaterialTexturesToMeta(e) {
    const t = {};
    for (const [, i] of Object.entries(this))
      i instanceof Ut && (t[i.uuid] = i);
    return (!e || !e.textures) && (e = e || {}, e.textures = {}), e.textures = { ...e.textures, ...t }, { meta: e, textureCache: t };
  }
  addTwinMaterialDataToJson(e, t) {
    e.userData = JSON.parse(JSON.stringify(e.userData ?? {})), e.uvAnimationData = this.uvAnimationData.toJSON(), e.mapParams = this.mapTextureParams.toJSON(), e.extendType = t.extendType, delete e.userData.defaultConfig, delete e.userData.serverData, delete e.userData.source;
  }
  updateMaterialTextureImage(e, t) {
    const i = this;
    if (!t) {
      i[e] = null, i.needsUpdate = !0;
      return;
    }
    const n = u0e(t);
    if (i[e] instanceof Ut)
      j_(n, i[e]), n.name = t.name, i[e].dispose();
    else {
      const r = i.defaultConfig ?? i.userData.defaultConfig, a = r == null ? void 0 : r[e];
      let o = a ? l0e(a) : new Ut();
      j_(n, o), n.name = t.name;
    }
    i[e] = n, n.needsUpdate = !0, i.needsUpdate = !0;
  }
  restoreDefault() {
    if (Object.keys(this.defaultConfig).length > 0) {
      const t = Xc(
        this.defaultConfig,
        st.assetManager.textureManager.texturesMap
      ), i = { ...this.serverData };
      this.copy(t), this.serverData = i, this.name = i.name;
    }
  }
};
S(If, "projectVideoConfigs", []), S(If, "textures", []), S(If, "sharedUniforms", {
  projectCameras: { value: [] },
  videoTextures: { value: [] },
  isTextureLoaded: { value: !0 },
  isTextureProjected: { value: !0 },
  backgroundOpacity: { value: 1 },
  frontFacesOnly: { value: !0 }
});
let yr = If;
function c0e(s) {
  const e = s, t = document.createElement("video");
  t.crossOrigin = "anonymous", t.playsInline = !0, t.loop = !0, t.muted = !0, t.play();
  const i = document.createElement("source");
  return i.src = e, i.type = "video/mp4", t.appendChild(i), new q9(t);
}
function h0e() {
  for (let t = 0; t < 5; t++) {
    const i = new Zt();
    i.updateProjectionMatrix(), i.updateMatrixWorld(), i.updateWorldMatrix(!0, !1), yr.projectVideoConfigs.push({
      camera: i,
      texture: null
    });
  }
  yr.sharedUniforms.projectCameras.value = yr.projectVideoConfigs.map(
    (t, i) => {
      t.camera.updateProjectionMatrix(), t.camera.updateMatrixWorld(), t.camera.updateWorldMatrix(!0, !1);
      const n = t.camera.matrixWorldInverse.clone(), r = t.camera.projectionMatrix.clone(), a = t.camera.matrixWorld.clone(), o = t.camera.position.clone(), l = new _().set(0, 0, 1).applyMatrix4(a);
      return {
        viewMatrixCamera: n,
        projectionMatrixCamera: r,
        projPosition: o,
        projDirection: l,
        visible: !1
      };
    }
  );
  const s = c0e(""), e = [];
  for (let t = 0; t < 5; t++)
    yr.textures.push(s), e.push(null);
  yr.sharedUniforms.videoTextures.value = e;
}
h0e();
function u0e(s) {
  let e = s.data;
  e || na(s.url) === "glb" && (e = st.assetManager.modelManager.getImageByUrlAndIndexId(
    s.url,
    s.indexId
  ));
  const t = new Wc(e);
  return t.imageSource.url = s.url, t.imageSource.indexId = s.indexId, t.imageSource.filename = s.name, t;
}
function q_(s, { defines: e = {}, header: t = "", main: i = "", ...n }) {
  let r = s;
  const a = (l, c, h) => l.split(c).join(h);
  return Object.keys(n).forEach((l) => {
    r = a(r, l, n[l]);
  }), r = r.replace(
    "void main() {",
    `
			${t}
			void main() {
				${i}
		`
  ), `
		${Object.keys(e).map((l) => `#define ${l} ${e[l]}`).join(`
`)}
		${r}
	`;
}
class bl extends hw(kt, yr) {
  constructor() {
    super();
    S(this, "extendType", "TwinMeshBasicMaterial");
  }
  onBeforeCompile(t) {
    this.addMapColorAdjustShaderCode(t);
  }
  copy(t) {
    return super.copy(t), this.userData = JSON.parse(JSON.stringify(t.userData)), t instanceof bl && (this.uvAnimationData = t.uvAnimationData.clone(), this.mapTextureParams = t.mapTextureParams.clone()), this;
  }
  toJSON(t) {
    const { meta: i, textureCache: n } = this.addMaterialTexturesToMeta(t), r = super.toJSON(i);
    return this.addTwinMaterialDataToJson(r, this), {
      json: r,
      textures: n
    };
  }
  updateToServer() {
    const t = yr.getMaterialConfig(this), { id: i, name: n, type: r } = this.serverData;
    return Qe.ApiServer.updateMaterial(i, {
      name: n,
      config: t,
      type: r
    });
  }
}
class Ml extends hw(or, yr) {
  constructor() {
    super();
    S(this, "extendType", "TwinMeshStandardMaterial");
  }
  onBeforeCompile(t) {
    this.addMapColorAdjustShaderCode(t);
  }
  copy(t) {
    return super.copy(t), t instanceof Ml && (this.uvAnimationData = t.uvAnimationData.clone(), this.mapTextureParams = t.mapTextureParams.clone()), this;
  }
  toJSON(t) {
    const { meta: i, textureCache: n } = this.addMaterialTexturesToMeta(t), r = super.toJSON(i);
    return this.addTwinMaterialDataToJson(r, this), {
      json: r,
      textures: n
    };
  }
  updateToServer() {
    const t = yr.getMaterialConfig(this), { id: i, name: n, type: r } = this.serverData;
    return Qe.ApiServer.updateMaterial(i, {
      name: n,
      config: t,
      type: r
    });
  }
}
class em extends hw(nn, yr) {
  constructor() {
    super();
    S(this, "extendType", "TwinMeshPhysicalMaterial");
  }
  onBeforeCompile(t) {
    this.addMapColorAdjustShaderCode(t);
  }
  copy(t) {
    return super.copy(t), t instanceof em && (this.uvAnimationData = t.uvAnimationData.clone(), this.mapTextureParams = t.mapTextureParams.clone()), this;
  }
  toJSON(t) {
    const { meta: i, textureCache: n } = this.addMaterialTexturesToMeta(t), r = super.toJSON(i);
    return this.addTwinMaterialDataToJson(r, this), {
      json: r,
      textures: n
    };
  }
  updateToServer() {
    const t = yr.getMaterialConfig(this), { id: i, name: n, type: r } = this.serverData;
    return Qe.ApiServer.updateMaterial(i, {
      name: n,
      config: t,
      type: r
    });
  }
}
class d0e {
  constructor() {
    S(this, "_materials", /* @__PURE__ */ new Map());
    S(this, "_materialThumbnails", /* @__PURE__ */ new Map());
  }
  get materialsMap() {
    return this._materials;
  }
  async loadMaterial(e, t) {
    const i = this._materials.get(e);
    if (i)
      return i;
    {
      const n = [], r = /* @__PURE__ */ new Map();
      for (const [o, l] of Object.entries(t))
        if (o.toLowerCase().endsWith("map")) {
          if (typeof l == "string") {
            const c = st.assetManager.textureManager.texturesMap.get(l);
            c && (n.push(kb(c)), t[o] = c.uuid, r.set(c.uuid, c));
          } else if (l && typeof l == "object") {
            const c = O2(l);
            n.push(kb(c)), t[o] = c.uuid, r.set(c.uuid, c);
          }
        }
      await Promise.all(n);
      const a = Xc(t, r);
      if (f0e(t, a), a)
        return a.uuid = e, this.addMaterial(e, a), a;
      console.error(`Failed to create material from JSON for UUID ${e}`);
      return;
    }
  }
  addMaterial(e, t) {
    this._materials.set(e, t), st.assetManager.animMaterialManager.addMaterial(e, t);
  }
  getMaterialByUUID(e) {
    return this._materials.get(e);
  }
  findMaterialByServerId(e) {
    for (const [, t] of this._materials.entries()) {
      const i = t.userData;
      if (i.serverData && i.serverData.id === e)
        return t;
    }
  }
  findMaterialByName(e, t) {
    for (const [, i] of this._materials.entries()) {
      const n = i.userData;
      if (n.serverData && n.serverData.type === t && i.name === e)
        return i;
    }
  }
  removeMaterials(e) {
    e.forEach((t) => {
      this._materials.has(t.uuid) && this._materials.delete(t.uuid);
    });
  }
  refreshMaterialThumbnail(e) {
    const t = this._materials.get(e);
    if (t && Wf(t) && t.needUpdateThumbnail) {
      const i = st.assetManager.createMaterialThumbImageURLObject(t);
      this._materialThumbnails.set(e, i), t.needUpdateThumbnail = !1;
    }
  }
  getMaterialThumbnailsByUUID(e) {
    const t = [];
    return e.forEach((i) => {
      const n = st.assetManager.materialManager.getMaterialByUUID(i);
      if (Wf(n) && n.needUpdateThumbnail) {
        const a = st.assetManager.createMaterialThumbImageURLObject(n);
        this._materialThumbnails.set(i, a), n.needUpdateThumbnail = !1;
      }
      const r = this._materialThumbnails.get(i);
      if (r)
        t.push(r);
      else {
        const a = this._materials.get(i);
        if (a) {
          const o = st.assetManager.createMaterialThumbImageURLObject(a);
          this._materialThumbnails.set(i, o), t.push(o);
        } else
          t.push(
            "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAABNVJREFUWEftlrtvHEUcx787O/u4h8924hCUKCIoKYIA0aSNsk6BEKQwElWqQJE/gArR3KaIqOiQaBGKgAIJihRGinQXRIUoQ4FkhAQIiSSAH+fbx7zQb2b2bMvnc+ImTWytTrK19/vM9zEzAZ7yT/CU5+MZwBMp8HDlatZb6l1maZwFnGVAAGMAaA1Vi6Gq5HA8Ku+d+Prb4eNa+9gAxY1r/TCOc8ZDBGGIwL5JAMYCGK2hpYKqJWRRDdcfrd88s3r3UJBDAf5552q/tTCX8yQG4xxByEDTA0fgAIyxAEaqCYQoK5Sb4/zkndWbs9SYCfBw5c1+a6E7Gc44AxibDHcENJ8UMDBKQysFLSRZAlHWKLbG+enVuwdCHAjwx+tX+t3FXh61EoQRh5OeIWgA6E3yH8aqMAFoVBASshKoiwrbo3F+bvj9VIipAB/xpezdNy4Okk4KnkQOoBnOyAKyP5gKYEgByoJUkI0K2xVeHAynzpr6x7VLlwbdhU4Wp7Ed7gBIAfJ+NwDpbzWw8jsVFJS1gQAkRC1QFTVGW+P81Z9+3KfCPoAvl85mF186M2h3UxAA9/JbC5gDsPmzCpD8NomuBRMA7QCEByhrbG9X+HdjvLy8dn9PM/YBDM6/Mjh1cj5rtRPECSkQIuSNBYHPgKug838vgPZBdBYQgERV1SiKGptbZX557f4eFfYB/HDhtcFir2UBojgCjwkgBGPM5YBW7ivoe2ghbA21gdbkv7YZEFYBAhAoyhqjcT288uvPy7trOU0BMz+Xot2KERNAxMEJICSAnQwEtAm5AExaoMkGpaHo8QC1BxiXAptFhbd+/2XPzH0A3529YOY7OwBRFO4AUAWZe2X3RtTUcDeAtAAKBFDWAgSwVdZ4+6+12QDfnDpvFtopOq0YScwR2wyECEkBD9BswzsWkPT+URpSKUipIaREXSsUHmCzFLj26LfZAJ8fe8Ec76TotmKkHoAsaAAYNcFJMNmKSQHbAG0grQXKftZCoaolilpiVAlslDVubP45G+DTudOD460k66YR2klkFYg8QEhBnGoBnUeuhkprt3qlLEApJMYeYL2oh+9Xf88O4S2+1H++08p7HiDhIeIJQOBsoCI0NfQHkvIW0MrpIYCKFBAKo1piqxJ4UJfDW+q/2QDXkWYvJ/ODxTRGN+FIOUdCQQwZQhaAVPAnsWsTncZkgbdBeIBaKpRSofAAm0LggSyXP8Fo9kZE3/kBW+ifiNO8F3OkEUfCGSILwMDtbuib4M8jG0BD8hu7coKold4BkBLrSg4/Nut7Vt9sZ1OP6w/ZMbPAI3SiEKm1gYY7Feyvj5K7DhioBkCT/BqV1CikwraS2NISD0y1fBvlvgvKgcfxe2j3nwuSvBtypCGzELT6MAjA7IXE34jgGqAMbAAFqaA1SqVQaI1tLbEBmX+G8eMfx40k19HuLwZR3glCpMwB8MBB7AHwOaDBwngAo1EYhQ2o/PYBw2da0EBcQ9rvgecpQiSUATiIxga7BwDWAjscGpXRKKExhsq/Qnn0K1kDsQKezSHqpwiziAAQgNkAuhORADRouHsqqPyLQwY3333opXR3QleQZjFwOUSQpQgyB+C6KKCHFTAsIe/dgTz0NnwkgFm326P+74kUOOqQWe89A/gfMWETToAvvlcAAAAASUVORK5CYII="
          );
      }
    }), t;
  }
  async createMaterialsOnServer(e) {
    return await RB(e);
  }
  async deleteMaterialsOnServer(e, t) {
    const i = e.flatMap((n) => {
      const r = n.userData;
      return r.serverData && r.serverData.id !== void 0 ? [r.serverData.id] : [];
    });
    try {
      i.length > 0 && (await Qe.ApiServer.deleteResources({
        ids: i,
        type: t
      }), this.removeMaterials(e));
    } catch (n) {
      throw console.error("Failed to delete materials on the server:", n), new Error(`Failed to delete materials due to ${n}`);
    }
  }
  async copyModelMaterialsToSceneMaterialList(e) {
    const t = e.map((i) => ({
      material: Gb(i, /* @__PURE__ */ new Map()),
      type: "publicMaterial"
    }));
    await this.createMaterialsOnServer(t), t.forEach((i) => {
      this.addMaterial(i.material.uuid, i.material);
    }), dt.SceneMaterialsStore.updateSceneMaterialList(), await this.createMaterialTextureImages(t);
  }
  dispose() {
    this._materials.forEach((e) => {
      for (const t of Object.values(e))
        t instanceof Ut && t.dispose();
      e.dispose();
    }), this._materials.clear();
  }
  async createMaterialTextureImages(e) {
    const t = /* @__PURE__ */ new Map();
    st.assetManager.textureManager.imagesMap.forEach((o) => {
      const l = this.createTextureKey(o);
      t.set(l, o);
    });
    const i = /* @__PURE__ */ new Map();
    e.forEach((o) => {
      b0e(o.material).forEach((l) => {
        const c = this.createTextureKey(l);
        i.has(c) || i.set(c, l);
      });
    });
    const n = Array.from(i.values()).filter((o) => {
      const l = this.createTextureKey(o);
      return !t.has(l);
    }), r = n.map((o) => {
      const l = o.toJSON();
      return {
        name: o.name,
        config: l,
        type: "image"
      };
    }), a = await Qe.ApiServer.createTextures(r);
    for (let o = 0; o < a.length; o++) {
      const l = n[o];
      l.uuid = a[o].tag, l.serverId = a[o].id, st.assetManager.textureManager.addTextureImage(l.uuid, l);
    }
    dt.SceneTextureImagesStore.updateSceneTextureImageList();
  }
  createTextureKey(e) {
    return `${e.url}@${e.indexId}@${e.name}`;
  }
}
function f0e(s, e) {
  var t;
  if (p0e(s.metadata.twinVersion ?? "1.6.0", "1.7.0"))
    if (s.defaultConfig)
      Y_(e, s.defaultConfig);
    else {
      const i = N1(e);
      (t = i == null ? void 0 : i.userData) == null || delete t.defaultConfig, Y_(e, i);
    }
}
function p0e(s, e) {
  const [t, i, n] = s.split(".").map(Number), [r, a, o] = e.split(".").map(Number);
  return t < r ? !0 : t > r ? !1 : i < a ? !0 : i > a ? !1 : n < o;
}
function m0e(s, e) {
  s.forEach((t, i) => {
    const { material: n, type: r } = t;
    n.uuid = e[i].tag, Ng(n, {
      id: e[i].id,
      name: n.name,
      tag: n.uuid,
      type: r
    });
  });
}
async function RB(s) {
  const e = s.map((i) => {
    const { material: n, type: r } = i, a = yr.getMaterialConfig(n);
    return {
      name: n.name,
      config: a,
      type: r,
      componentId: i.componentId
    };
  }), t = await Qe.ApiServer.createMaterials(e);
  return m0e(s, t), s;
}
function Ng(s, e) {
  return s.userData = {
    ...s.userData,
    serverData: e
  }, Kr(s) && (s.serverData = {
    ...s.serverData,
    ...e
  }), s;
}
function Kr(s) {
  return s instanceof bl || s instanceof Ml || s instanceof em;
}
function Y_(s, e) {
  return s.userData = {
    ...s.userData,
    defaultConfig: e
  }, Kr(s) && (s.defaultConfig = e), s;
}
async function kb(s) {
  return st.assetManager.textureManager.loadTextureImage(s);
}
function N1(s, e) {
  const t = {
    metadata: {
      type: "Material",
      generator: "materialToJSON",
      twinVersion: "1.7.0"
    }
  };
  function i(n) {
    if (n instanceof Ut)
      return mw(n);
  }
  return t.type = s.type, s.name !== "" && (t.name = s.name), s.color && s.color.isColor && (t.color = s.color.getHex()), s.roughness !== void 0 && (t.roughness = s.roughness), s.metalness !== void 0 && (t.metalness = s.metalness), s.sheen !== void 0 && (t.sheen = s.sheen), s.sheenColor && s.sheenColor.isColor && (t.sheenColor = s.sheenColor.getHex()), s.sheenRoughness !== void 0 && (t.sheenRoughness = s.sheenRoughness), s.emissive && s.emissive.isColor && (t.emissive = s.emissive.getHex()), s.emissiveIntensity && s.emissiveIntensity !== 1 && (t.emissiveIntensity = s.emissiveIntensity), s.specular && s.specular.isColor && (t.specular = s.specular.getHex()), s.specularIntensity !== void 0 && (t.specularIntensity = s.specularIntensity), s.specularColor && s.specularColor.isColor && (t.specularColor = s.specularColor.getHex()), s.shininess !== void 0 && (t.shininess = s.shininess), s.clearcoat !== void 0 && (t.clearcoat = s.clearcoat), s.clearcoatRoughness !== void 0 && (t.clearcoatRoughness = s.clearcoatRoughness), s.clearcoatMap && s.clearcoatMap.isTexture && (t.clearcoatMap = i(s.clearcoatMap)), s.clearcoatRoughnessMap && s.clearcoatRoughnessMap.isTexture && (t.clearcoatRoughnessMap = i(s.clearcoatRoughnessMap)), s.clearcoatNormalMap && s.clearcoatNormalMap.isTexture && (t.clearcoatNormalMap = i(s.clearcoatNormalMap), t.clearcoatNormalScale = s.clearcoatNormalScale.toArray()), s.iridescence !== void 0 && (t.iridescence = s.iridescence), s.iridescenceIOR !== void 0 && (t.iridescenceIOR = s.iridescenceIOR), s.iridescenceThicknessRange !== void 0 && (t.iridescenceThicknessRange = s.iridescenceThicknessRange), s.iridescenceMap && s.iridescenceMap.isTexture && (t.iridescenceMap = i(s.iridescenceMap)), s.iridescenceThicknessMap && s.iridescenceThicknessMap.isTexture && (t.iridescenceThicknessMap = i(s.iridescenceThicknessMap)), s.anisotropy !== void 0 && (t.anisotropy = s.anisotropy), s.anisotropyRotation !== void 0 && (t.anisotropyRotation = s.anisotropyRotation), s.anisotropyMap && s.anisotropyMap.isTexture && (t.anisotropyMap = i(s.anisotropyMap)), s.map && s.map.isTexture && (t.map = i(s.map)), s.matcap && s.matcap.isTexture && (t.matcap = i(s.matcap)), s.alphaMap && s.alphaMap.isTexture && (t.alphaMap = i(s.alphaMap)), s.lightMap && s.lightMap.isTexture && (t.lightMap = i(s.lightMap), t.lightMapIntensity = s.lightMapIntensity), s.aoMap && s.aoMap.isTexture && (t.aoMap = i(s.aoMap), t.aoMapIntensity = s.aoMapIntensity), s.bumpMap && s.bumpMap.isTexture && (t.bumpMap = i(s.bumpMap), t.bumpScale = s.bumpScale), s.normalMap && s.normalMap.isTexture && (t.normalMap = i(s.normalMap), t.normalMapType = s.normalMapType, t.normalScale = s.normalScale.toArray()), s.displacementMap && s.displacementMap.isTexture && (t.displacementMap = i(s.displacementMap), t.displacementScale = s.displacementScale, t.displacementBias = s.displacementBias), s.roughnessMap && s.roughnessMap.isTexture && (t.roughnessMap = i(s.roughnessMap)), s.metalnessMap && s.metalnessMap.isTexture && (t.metalnessMap = i(s.metalnessMap)), s.emissiveMap && s.emissiveMap.isTexture && (t.emissiveMap = i(s.emissiveMap)), s.specularMap && s.specularMap.isTexture && (t.specularMap = i(s.specularMap)), s.specularIntensityMap && s.specularIntensityMap.isTexture && (t.specularIntensityMap = i(s.specularIntensityMap)), s.specularColorMap && s.specularColorMap.isTexture && (t.specularColorMap = i(s.specularColorMap)), s.envMap && s.envMap.isTexture && (t.envMap = i(s.envMap), s.combine !== void 0 && (t.combine = s.combine)), s.envMapIntensity !== void 0 && (t.envMapIntensity = s.envMapIntensity), s.reflectivity !== void 0 && (t.reflectivity = s.reflectivity), s.refractionRatio !== void 0 && (t.refractionRatio = s.refractionRatio), s.gradientMap && s.gradientMap.isTexture && (t.gradientMap = i(s.gradientMap)), s.transmission !== void 0 && (t.transmission = s.transmission), s.transmissionMap && s.transmissionMap.isTexture && (t.transmissionMap = i(s.transmissionMap)), s.thickness !== void 0 && (t.thickness = s.thickness), s.thicknessMap && s.thicknessMap.isTexture && (t.thicknessMap = i(s.thicknessMap)), s.attenuationDistance !== void 0 && s.attenuationDistance !== 1 / 0 && (t.attenuationDistance = s.attenuationDistance), s.attenuationColor !== void 0 && (t.attenuationColor = s.attenuationColor.getHex()), s.size !== void 0 && (t.size = s.size), s.shadowSide !== null && (t.shadowSide = s.shadowSide), s.sizeAttenuation !== void 0 && (t.sizeAttenuation = s.sizeAttenuation), s.blending !== so && (t.blending = s.blending), s.side !== sa && (t.side = s.side), s.vertexColors && (t.vertexColors = !0), t.opacity = s.opacity, s.transparent === !0 && (t.transparent = s.transparent), t.depthFunc = s.depthFunc, t.depthTest = s.depthTest, t.depthWrite = s.depthWrite, t.colorWrite = s.colorWrite, t.stencilWrite = s.stencilWrite, t.stencilWriteMask = s.stencilWriteMask, t.stencilFunc = s.stencilFunc, t.stencilRef = s.stencilRef, t.stencilFuncMask = s.stencilFuncMask, t.stencilFail = s.stencilFail, t.stencilZFail = s.stencilZFail, t.stencilZPass = s.stencilZPass, s.rotation !== void 0 && s.rotation !== 0 && (t.rotation = s.rotation), s.polygonOffset === !0 && (t.polygonOffset = !0), s.polygonOffsetFactor !== 0 && (t.polygonOffsetFactor = s.polygonOffsetFactor), s.polygonOffsetUnits !== 0 && (t.polygonOffsetUnits = s.polygonOffsetUnits), s.linewidth !== void 0 && s.linewidth !== 1 && (t.linewidth = s.linewidth), s.dashSize !== void 0 && (t.dashSize = s.dashSize), s.gapSize !== void 0 && (t.gapSize = s.gapSize), s.scale !== void 0 && (t.scale = s.scale), s.dithering === !0 && (t.dithering = !0), s.alphaTest > 0 && (t.alphaTest = s.alphaTest), s.alphaToCoverage === !0 && (t.alphaToCoverage = s.alphaToCoverage), s.premultipliedAlpha === !0 && (t.premultipliedAlpha = s.premultipliedAlpha), s.forceSinglePass === !0 && (t.forceSinglePass = s.forceSinglePass), s.wireframe === !0 && (t.wireframe = s.wireframe), s.wireframeLinewidth > 1 && (t.wireframeLinewidth = s.wireframeLinewidth), s.wireframeLinecap !== "round" && (t.wireframeLinecap = s.wireframeLinecap), s.wireframeLinejoin !== "round" && (t.wireframeLinejoin = s.wireframeLinejoin), s.flatShading === !0 && (t.flatShading = s.flatShading), s.visible === !1 && (t.visible = !1), s.toneMapped === !1 && (t.toneMapped = !1), s.fog === !1 && (t.fog = !1), Object.keys(s.userData).length > 0 && (t.userData = JSON.parse(JSON.stringify(s.userData))), Kr(s) && (t.uvAnimationData = s.uvAnimationData.toJSON(), t.defaultConfig = s.defaultConfig, t.mapParams = s.mapTextureParams.toJSON()), t;
}
function g0e(s) {
  switch (s) {
    case "MeshBasicMaterial":
      return new bl();
    case "MeshStandardMaterial":
      return new Ml();
    case "MeshPhysicalMaterial":
      return new em();
    default:
      return console.warn(`Unsupported material type: ${s}`), new bl();
  }
}
function Xc(s, e, t) {
  function i(o) {
    if (typeof o == "string") {
      const l = e.get(o);
      return l && (t == null || t(l)), l;
    } else {
      const l = O2(o);
      return t == null || t(l), l;
    }
  }
  const n = g0e(s.type);
  if (s.name !== void 0 && (n.name = s.name), s.color !== void 0 && n.color !== void 0 && n.color.setHex(s.color), s.roughness !== void 0 && (n.roughness = s.roughness), s.metalness !== void 0 && (n.metalness = s.metalness), s.sheen !== void 0 && (n.sheen = s.sheen), s.sheenColor !== void 0 && (n.sheenColor = new _e().setHex(s.sheenColor)), s.sheenRoughness !== void 0 && (n.sheenRoughness = s.sheenRoughness), s.emissive !== void 0 && n.emissive !== void 0 && n.emissive.setHex(s.emissive), s.specular !== void 0 && n.specular !== void 0 && n.specular.setHex(s.specular), s.specularIntensity !== void 0 && (n.specularIntensity = s.specularIntensity), s.specularColor !== void 0 && n.specularColor !== void 0 && n.specularColor.setHex(s.specularColor), s.shininess !== void 0 && (n.shininess = s.shininess), s.clearcoat !== void 0 && (n.clearcoat = s.clearcoat), s.clearcoatRoughness !== void 0 && (n.clearcoatRoughness = s.clearcoatRoughness), s.iridescence !== void 0 && (n.iridescence = s.iridescence), s.iridescenceIOR !== void 0 && (n.iridescenceIOR = s.iridescenceIOR), s.iridescenceThicknessRange !== void 0 && (n.iridescenceThicknessRange = s.iridescenceThicknessRange), s.thickness !== void 0 && (n.thickness = s.thickness), s.attenuationDistance !== void 0 && (n.attenuationDistance = s.attenuationDistance), s.attenuationColor !== void 0 && n.attenuationColor !== void 0 && n.attenuationColor.setHex(s.attenuationColor), s.anisotropy !== void 0 && (n.anisotropy = s.anisotropy), s.anisotropyRotation !== void 0 && (n.anisotropyRotation = s.anisotropyRotation), s.fog !== void 0 && (n.fog = s.fog), s.flatShading !== void 0 && (n.flatShading = s.flatShading), s.blending !== void 0 && (n.blending = s.blending), s.combine !== void 0 && (n.combine = s.combine), s.side !== void 0 && (n.side = s.side), s.shadowSide !== void 0 && (n.shadowSide = s.shadowSide), s.opacity !== void 0 && (n.opacity = s.opacity), s.transparent !== void 0 && (n.transparent = s.transparent), s.alphaTest !== void 0 && (n.alphaTest = s.alphaTest), s.depthTest !== void 0 && (n.depthTest = s.depthTest), s.depthWrite !== void 0 && (n.depthWrite = s.depthWrite), s.colorWrite !== void 0 && (n.colorWrite = s.colorWrite), s.stencilWrite !== void 0 && (n.stencilWrite = s.stencilWrite), s.stencilWriteMask !== void 0 && (n.stencilWriteMask = s.stencilWriteMask), s.stencilFunc !== void 0 && (n.stencilFunc = s.stencilFunc), s.stencilRef !== void 0 && (n.stencilRef = s.stencilRef), s.stencilFuncMask !== void 0 && (n.stencilFuncMask = s.stencilFuncMask), s.stencilFail !== void 0 && (n.stencilFail = s.stencilFail), s.stencilZFail !== void 0 && (n.stencilZFail = s.stencilZFail), s.stencilZPass !== void 0 && (n.stencilZPass = s.stencilZPass), s.wireframe !== void 0 && (n.wireframe = s.wireframe), s.wireframeLinewidth !== void 0 && (n.wireframeLinewidth = s.wireframeLinewidth), s.wireframeLinecap !== void 0 && (n.wireframeLinecap = s.wireframeLinecap), s.wireframeLinejoin !== void 0 && (n.wireframeLinejoin = s.wireframeLinejoin), s.rotation !== void 0 && (n.rotation = s.rotation), s.linewidth !== 1 && (n.linewidth = s.linewidth), s.dashSize !== void 0 && (n.dashSize = s.dashSize), s.gapSize !== void 0 && (n.gapSize = s.gapSize), s.scale !== void 0 && (n.scale = s.scale), s.polygonOffset !== void 0 && (n.polygonOffset = s.polygonOffset), s.polygonOffsetFactor !== void 0 && (n.polygonOffsetFactor = s.polygonOffsetFactor), s.polygonOffsetUnits !== void 0 && (n.polygonOffsetUnits = s.polygonOffsetUnits), s.dithering !== void 0 && (n.dithering = s.dithering), s.alphaToCoverage !== void 0 && (n.alphaToCoverage = s.alphaToCoverage), s.premultipliedAlpha !== void 0 && (n.premultipliedAlpha = s.premultipliedAlpha), s.forceSinglePass !== void 0 && (n.forceSinglePass = s.forceSinglePass), s.visible !== void 0 && (n.visible = s.visible), s.toneMapped !== void 0 && (n.toneMapped = s.toneMapped), s.userData !== void 0 && (n.userData = pw(s.userData)), s.vertexColors !== void 0 && (typeof s.vertexColors == "number" ? n.vertexColors = s.vertexColors > 0 : n.vertexColors = s.vertexColors), s.uniforms !== void 0)
    for (const o in s.uniforms) {
      const l = s.uniforms[o];
      switch (n.uniforms[o] = {}, l.type) {
        case "t":
          n.uniforms[o].value = i(l.value);
          break;
        case "c":
          n.uniforms[o].value = new _e().setHex(l.value);
          break;
        case "v2":
          n.uniforms[o].value = new Se().fromArray(l.value);
          break;
        case "v3":
          n.uniforms[o].value = new _().fromArray(l.value);
          break;
        case "v4":
          n.uniforms[o].value = new Yt().fromArray(l.value);
          break;
        case "m3":
          n.uniforms[o].value = new Gt().fromArray(l.value);
          break;
        case "m4":
          n.uniforms[o].value = new et().fromArray(l.value);
          break;
        default:
          n.uniforms[o].value = l.value;
      }
    }
  if (s.defines !== void 0 && (n.defines = s.defines), s.vertexShader !== void 0 && (n.vertexShader = s.vertexShader), s.fragmentShader !== void 0 && (n.fragmentShader = s.fragmentShader), s.glslVersion !== void 0 && (n.glslVersion = s.glslVersion), s.extensions !== void 0)
    for (const o in s.extensions)
      n.extensions[o] = s.extensions[o];
  if (s.lights !== void 0 && (n.lights = s.lights), s.clipping !== void 0 && (n.clipping = s.clipping), s.size !== void 0 && (n.size = s.size), s.sizeAttenuation !== void 0 && (n.sizeAttenuation = s.sizeAttenuation), s.map !== void 0 && (n.map = i(s.map)), s.matcap !== void 0 && (n.matcap = i(s.matcap)), s.alphaMap !== void 0 && (n.alphaMap = i(s.alphaMap)), s.bumpMap !== void 0 && (n.bumpMap = i(s.bumpMap)), s.bumpScale !== void 0 && (n.bumpScale = s.bumpScale), s.normalMap !== void 0 && (n.normalMap = i(s.normalMap)), s.normalMapType !== void 0 && (n.normalMapType = s.normalMapType), s.normalScale !== void 0) {
    let { normalScale: o } = s;
    Array.isArray(o) === !1 && (o = [o, o]), n.normalScale = new Se().fromArray(o);
  }
  s.displacementMap !== void 0 && (n.displacementMap = i(s.displacementMap)), s.displacementScale !== void 0 && (n.displacementScale = s.displacementScale), s.displacementBias !== void 0 && (n.displacementBias = s.displacementBias), s.roughnessMap !== void 0 && (n.roughnessMap = i(s.roughnessMap)), s.metalnessMap !== void 0 && (n.metalnessMap = i(s.metalnessMap)), s.emissiveMap !== void 0 && (n.emissiveMap = i(s.emissiveMap)), s.emissiveIntensity !== void 0 && (n.emissiveIntensity = s.emissiveIntensity), s.specularMap !== void 0 && (n.specularMap = i(s.specularMap)), s.specularIntensityMap !== void 0 && (n.specularIntensityMap = i(s.specularIntensityMap)), s.specularColorMap !== void 0 && (n.specularColorMap = i(s.specularColorMap)), s.envMap !== void 0 && (n.envMap = i(s.envMap)), s.envMapIntensity !== void 0 && (n.envMapIntensity = s.envMapIntensity), s.reflectivity !== void 0 && (n.reflectivity = s.reflectivity), s.refractionRatio !== void 0 && (n.refractionRatio = s.refractionRatio), s.lightMap !== void 0 && (n.lightMap = i(s.lightMap)), s.lightMapIntensity !== void 0 && (n.lightMapIntensity = s.lightMapIntensity), s.aoMap !== void 0 && (n.aoMap = i(s.aoMap)), s.aoMapIntensity !== void 0 && (n.aoMapIntensity = s.aoMapIntensity), s.gradientMap !== void 0 && (n.gradientMap = i(s.gradientMap)), s.clearcoatMap !== void 0 && (n.clearcoatMap = i(s.clearcoatMap)), s.clearcoatRoughnessMap !== void 0 && (n.clearcoatRoughnessMap = i(s.clearcoatRoughnessMap)), s.clearcoatNormalMap !== void 0 && (n.clearcoatNormalMap = i(s.clearcoatNormalMap)), s.clearcoatNormalScale !== void 0 && (n.clearcoatNormalScale = new Se().fromArray(s.clearcoatNormalScale)), s.iridescenceMap !== void 0 && (n.iridescenceMap = i(s.iridescenceMap)), s.iridescenceThicknessMap !== void 0 && (n.iridescenceThicknessMap = i(s.iridescenceThicknessMap)), s.transmissionMap !== void 0 && (n.transmissionMap = i(s.transmissionMap)), s.thicknessMap !== void 0 && (n.thicknessMap = i(s.thicknessMap)), s.anisotropyMap !== void 0 && (n.anisotropyMap = i(s.anisotropyMap)), s.sheenColorMap !== void 0 && (n.sheenColorMap = i(s.sheenColorMap)), s.sheenRoughnessMap !== void 0 && (n.sheenRoughnessMap = i(s.sheenRoughnessMap));
  const r = s, a = n;
  return y0e(r, a), v0e(r, a), a.defaultConfig = r.defaultConfig, n;
}
function v0e(s, e) {
  var t, i, n;
  e.MapBrightness = ((t = s == null ? void 0 : s.mapParams) == null ? void 0 : t.brightness) ?? 0, e.MapContrast = ((i = s == null ? void 0 : s.mapParams) == null ? void 0 : i.contrast) ?? 1, e.MapSaturation = ((n = s == null ? void 0 : s.mapParams) == null ? void 0 : n.saturation) ?? 1;
}
function y0e(s, e) {
  var t, i, n, r;
  if (s.uvAnimationData) {
    const { offset: a, offsetSpeed: o, center: l, repeat: c, rotationSpeed: h, rotation: u } = s.uvAnimationData;
    e.uvAnimationData.center.fromArray(l), e.uvAnimationData.offset.fromArray(a), e.uvAnimationData.offsetSpeed.fromArray(o), e.uvAnimationData.repeat.fromArray(c), e.uvAnimationData.rotation = u, e.uvAnimationData.rotationSpeed = h;
    for (const [, d] of Object.entries(e))
      d instanceof Ut && (a && Array.isArray(a) && d.offset.copy(new Se().fromArray(a)), typeof u == "number" && (d.rotation = u), l && Array.isArray(l) && d.center.copy(new Se().fromArray(l)), c && Array.isArray(c) && (d.repeat.copy(new Se().fromArray(c)), d.repeat.x = c[0] + (((i = (t = d.userData) == null ? void 0 : t.transform) == null ? void 0 : i.repeat[0]) ?? 1), d.repeat.y = c[1] + (((r = (n = d.userData) == null ? void 0 : n.transform) == null ? void 0 : r.repeat[1]) ?? 1)), d.needsUpdate = !0);
  }
}
function DB(s, e) {
  e.name = s.name, e.blending = s.blending, e.side = s.side, e.vertexColors = s.vertexColors, e.opacity = s.opacity, e.transparent = s.transparent, e.blendSrc = s.blendSrc, e.blendDst = s.blendDst, e.blendEquation = s.blendEquation, e.blendSrcAlpha = s.blendSrcAlpha, e.blendDstAlpha = s.blendDstAlpha, e.blendEquationAlpha = s.blendEquationAlpha, e.depthFunc = s.depthFunc, e.depthTest = s.depthTest, e.depthWrite = s.depthWrite, e.stencilWriteMask = s.stencilWriteMask, e.stencilFunc = s.stencilFunc, e.stencilRef = s.stencilRef, e.stencilFuncMask = s.stencilFuncMask, e.stencilFail = s.stencilFail, e.stencilZFail = s.stencilZFail, e.stencilZPass = s.stencilZPass, e.stencilWrite = s.stencilWrite;
  const t = s.clippingPlanes;
  let i = null;
  if (t !== null) {
    const n = t.length;
    i = new Array(n);
    for (let r = 0; r !== n; ++r)
      i[r] = t[r].clone();
  }
  e.clippingPlanes = i, e.clipIntersection = s.clipIntersection, e.clipShadows = s.clipShadows, e.shadowSide = s.shadowSide, e.colorWrite = s.colorWrite, e.precision = s.precision, e.polygonOffset = s.polygonOffset, e.polygonOffsetFactor = s.polygonOffsetFactor, e.polygonOffsetUnits = s.polygonOffsetUnits, e.dithering = s.dithering, e.alphaTest = s.alphaTest, e.alphaHash = s.alphaHash, e.alphaToCoverage = s.alphaToCoverage, e.premultipliedAlpha = s.premultipliedAlpha, e.forceSinglePass = s.forceSinglePass, e.visible = s.visible, e.toneMapped = s.toneMapped, e.color.copy(s.color), e.roughness = s.roughness, e.metalness = s.metalness, e.map = s.map, e.lightMap = s.lightMap, e.lightMapIntensity = s.lightMapIntensity, e.aoMap = s.aoMap, e.aoMapIntensity = s.aoMapIntensity, e.emissive.copy(s.emissive), e.emissiveMap = s.emissiveMap, e.emissiveIntensity = s.emissiveIntensity, e.bumpMap = s.bumpMap, e.bumpScale = s.bumpScale, e.normalMap = s.normalMap, e.normalMapType = s.normalMapType, e.normalScale.copy(s.normalScale), e.displacementMap = s.displacementMap, e.displacementScale = s.displacementScale, e.displacementBias = s.displacementBias, e.roughnessMap = s.roughnessMap, e.metalnessMap = s.metalnessMap, e.alphaMap = s.alphaMap, e.envMap = s.envMap, e.envMapIntensity = s.envMapIntensity, e.wireframe = s.wireframe, e.wireframeLinewidth = s.wireframeLinewidth, e.wireframeLinecap = s.wireframeLinecap, e.wireframeLinejoin = s.wireframeLinejoin, e.flatShading = s.flatShading, e.fog = s.fog;
}
function x0e(s) {
  switch (s.type) {
    case "MeshBasicMaterial":
      return new bl().copy(s);
    case "MeshStandardMaterial":
      return new Ml().copy(s);
    case "MeshPhysicalMaterial":
      return new em().copy(s);
    default:
      return console.warn(`Unsupported material type: ${s.type}`), new bl().copy(s);
  }
}
function A0e(s) {
  const e = new Wc(s.source.data);
  return e.name = s.name, e.mipmaps = s.mipmaps.slice(0), e.mapping = s.mapping, e.channel = s.channel, e.wrapS = s.wrapS, e.wrapT = s.wrapT, e.magFilter = s.magFilter, e.minFilter = s.minFilter, e.anisotropy = s.anisotropy, e.format = s.format, e.internalFormat = s.internalFormat, e.type = s.type, e.offset.copy(s.offset), e.repeat.copy(s.repeat), e.center.copy(s.center), e.rotation = s.rotation, e.matrixAutoUpdate = s.matrixAutoUpdate, e.matrix.copy(s.matrix), e.generateMipmaps = s.generateMipmaps, e.premultiplyAlpha = s.premultiplyAlpha, e.flipY = s.flipY, e.unpackAlignment = s.unpackAlignment, e.colorSpace = s.colorSpace, e.userData = JSON.parse(JSON.stringify(s.userData)), e.needsUpdate = !0, s instanceof Wc && (e.imageSource = s.imageSource.clone()), e;
}
function Gb(s, e) {
  const t = x0e(s);
  for (const [i, n] of Object.entries(t))
    n instanceof Ut && (e.has(n.uuid) || e.set(n.uuid, A0e(n)), t[i] = e.get(n.uuid));
  return t;
}
function b0e(s) {
  const e = [];
  for (const [, t] of Object.entries(s))
    if (t instanceof Ut) {
      const { url: i, indexId: n } = t.imageSource ?? t.userData.source, r = new Pp(t.source.data);
      r.url = i, r.indexId = n ?? "0", r.name = t.name, e.push(r);
    }
  return e;
}
function M0e(s) {
  return s.isMeshBasicMaterial || s.isMeshStandardMaterial || s.isMeshPhysicalMaterial;
}
function Wf(s) {
  return s instanceof bl || s instanceof Ml || s instanceof em;
}
function Z_(s) {
  const e = s.clone();
  for (const [t, i] of Object.entries(s))
    i instanceof Ut && (e[t] = i.clone());
  return e;
}
function K_(s) {
  for (const [, e] of Object.entries(s))
    e instanceof Ut && e.dispose();
}
let f3;
const S0e = () => {
  if (f3)
    return f3;
  const s = "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", e = "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", t = new Uint8Array([
    0,
    97,
    115,
    109,
    1,
    0,
    0,
    0,
    1,
    4,
    1,
    96,
    0,
    0,
    3,
    3,
    2,
    0,
    0,
    5,
    3,
    1,
    0,
    1,
    12,
    1,
    0,
    10,
    22,
    2,
    12,
    0,
    65,
    0,
    65,
    0,
    65,
    0,
    252,
    10,
    0,
    0,
    11,
    7,
    0,
    65,
    0,
    253,
    15,
    26,
    11
  ]), i = new Uint8Array([
    32,
    0,
    65,
    253,
    3,
    1,
    2,
    34,
    4,
    106,
    6,
    5,
    11,
    8,
    7,
    20,
    13,
    33,
    12,
    16,
    128,
    9,
    116,
    64,
    19,
    113,
    127,
    15,
    10,
    21,
    22,
    14,
    255,
    66,
    24,
    54,
    136,
    107,
    18,
    23,
    192,
    26,
    114,
    118,
    132,
    17,
    77,
    101,
    130,
    144,
    27,
    87,
    131,
    44,
    45,
    74,
    156,
    154,
    70,
    167
  ]);
  if (typeof WebAssembly != "object")
    return {
      supported: !1
    };
  let n = s;
  WebAssembly.validate(t) && (n = e);
  let r;
  const a = WebAssembly.instantiate(o(n), {}).then((u) => {
    r = u.instance, r.exports.__wasm_call_ctors();
  });
  function o(u) {
    const d = new Uint8Array(u.length);
    for (let p = 0; p < u.length; ++p) {
      const v = u.charCodeAt(p);
      d[p] = v > 96 ? v - 71 : v > 64 ? v - 65 : v > 47 ? v + 4 : v > 46 ? 63 : 62;
    }
    let f = 0;
    for (let p = 0; p < u.length; ++p)
      d[f++] = d[p] < 60 ? i[d[p]] : (d[p] - 60) * 64 + d[++p];
    return d.buffer.slice(0, f);
  }
  function l(u, d, f, p, v, g) {
    const m = r.exports.sbrk, x = f + 3 & -4, y = m(x * p), A = m(v.length), b = new Uint8Array(r.exports.memory.buffer);
    b.set(v, A);
    const M = u(y, f, p, A, v.length);
    if (M === 0 && g && g(y, x, p), d.set(b.subarray(y, y + f * p)), m(y - m(0)), M !== 0)
      throw new Error(`Malformed buffer data: ${M}`);
  }
  const c = {
    // legacy index-based enums for glTF
    0: "",
    1: "meshopt_decodeFilterOct",
    2: "meshopt_decodeFilterQuat",
    3: "meshopt_decodeFilterExp",
    // string-based enums for glTF
    NONE: "",
    OCTAHEDRAL: "meshopt_decodeFilterOct",
    QUATERNION: "meshopt_decodeFilterQuat",
    EXPONENTIAL: "meshopt_decodeFilterExp"
  }, h = {
    // legacy index-based enums for glTF
    0: "meshopt_decodeVertexBuffer",
    1: "meshopt_decodeIndexBuffer",
    2: "meshopt_decodeIndexSequence",
    // string-based enums for glTF
    ATTRIBUTES: "meshopt_decodeVertexBuffer",
    TRIANGLES: "meshopt_decodeIndexBuffer",
    INDICES: "meshopt_decodeIndexSequence"
  };
  return f3 = {
    ready: a,
    supported: !0,
    decodeVertexBuffer(u, d, f, p, v) {
      l(
        r.exports.meshopt_decodeVertexBuffer,
        u,
        d,
        f,
        p,
        r.exports[c[v]]
      );
    },
    decodeIndexBuffer(u, d, f, p) {
      l(r.exports.meshopt_decodeIndexBuffer, u, d, f, p);
    },
    decodeIndexSequence(u, d, f, p) {
      l(r.exports.meshopt_decodeIndexSequence, u, d, f, p);
    },
    decodeGltfBuffer(u, d, f, p, v, g) {
      l(
        r.exports[h[v]],
        u,
        d,
        f,
        p,
        r.exports[c[g]]
      );
    }
  }, f3;
};
let w0e = class extends As {
  constructor(e) {
    super(e), this.dracoLoader = null, this.ktx2Loader = null, this.meshoptDecoder = null, this.pluginCallbacks = [], this.register(function(t) {
      return new P0e(t);
    }), this.register(function(t) {
      return new F0e(t);
    }), this.register(function(t) {
      return new U0e(t);
    }), this.register(function(t) {
      return new z0e(t);
    }), this.register(function(t) {
      return new D0e(t);
    }), this.register(function(t) {
      return new L0e(t);
    }), this.register(function(t) {
      return new I0e(t);
    }), this.register(function(t) {
      return new O0e(t);
    }), this.register(function(t) {
      return new _0e(t);
    }), this.register(function(t) {
      return new N0e(t);
    }), this.register(function(t) {
      return new R0e(t);
    }), this.register(function(t) {
      return new B0e(t);
    }), this.register(function(t) {
      return new T0e(t);
    }), this.register(function(t) {
      return new k0e(t);
    }), this.register(function(t) {
      return new G0e(t);
    });
  }
  load(e, t, i, n) {
    const r = this;
    let a;
    this.resourcePath !== "" ? a = this.resourcePath : this.path !== "" ? a = this.path : a = Rs.extractUrlBase(e), this.manager.itemStart(e);
    const o = function(c) {
      n ? n(c) : console.error(c), r.manager.itemError(e), r.manager.itemEnd(e);
    }, l = new Nn(this.manager);
    l.setPath(this.path), l.setResponseType("arraybuffer"), l.setRequestHeader(this.requestHeader), l.setWithCredentials(this.withCredentials), l.load(
      e,
      function(c) {
        try {
          r.parse(
            c,
            a,
            function(h) {
              t(h), r.manager.itemEnd(e);
            },
            o
          );
        } catch (h) {
          o(h);
        }
      },
      i,
      o
    );
  }
  setDRACOLoader(e) {
    return this.dracoLoader = e, this;
  }
  setDDSLoader() {
    throw new Error(
      'THREE.GLTFLoader: "MSFT_texture_dds" no longer supported. Please update to "KHR_texture_basisu".'
    );
  }
  setKTX2Loader(e) {
    return this.ktx2Loader = e, this;
  }
  setMeshoptDecoder(e) {
    return this.meshoptDecoder = e, this;
  }
  register(e) {
    return this.pluginCallbacks.indexOf(e) === -1 && this.pluginCallbacks.push(e), this;
  }
  unregister(e) {
    return this.pluginCallbacks.indexOf(e) !== -1 && this.pluginCallbacks.splice(this.pluginCallbacks.indexOf(e), 1), this;
  }
  parse(e, t, i, n) {
    let r;
    const a = {}, o = {}, l = new TextDecoder();
    if (typeof e == "string")
      r = JSON.parse(e);
    else if (e instanceof ArrayBuffer)
      if (l.decode(new Uint8Array(e, 0, 4)) === LB) {
        try {
          a[Ii.KHR_BINARY_GLTF] = new H0e(e);
        } catch (u) {
          n && n(u);
          return;
        }
        r = JSON.parse(a[Ii.KHR_BINARY_GLTF].content);
      } else
        r = JSON.parse(l.decode(e));
    else
      r = e;
    if (r.asset === void 0 || r.asset.version[0] < 2) {
      n && n(
        new Error("THREE.GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported.")
      );
      return;
    }
    const c = new tle(r, {
      path: t || this.resourcePath || "",
      crossOrigin: this.crossOrigin,
      requestHeader: this.requestHeader,
      manager: this.manager,
      ktx2Loader: this.ktx2Loader,
      meshoptDecoder: this.meshoptDecoder
    });
    c.fileLoader.setRequestHeader(this.requestHeader);
    for (let h = 0; h < this.pluginCallbacks.length; h++) {
      const u = this.pluginCallbacks[h](c);
      o[u.name] = u, a[u.name] = !0;
    }
    if (r.extensionsUsed)
      for (let h = 0; h < r.extensionsUsed.length; ++h) {
        const u = r.extensionsUsed[h], d = r.extensionsRequired || [];
        switch (u) {
          case Ii.KHR_MATERIALS_UNLIT:
            a[u] = new C0e();
            break;
          case Ii.KHR_DRACO_MESH_COMPRESSION:
            a[u] = new V0e(
              r,
              this.dracoLoader
            );
            break;
          case Ii.KHR_TEXTURE_TRANSFORM:
            a[u] = new W0e();
            break;
          case Ii.KHR_MESH_QUANTIZATION:
            a[u] = new X0e();
            break;
          default:
            d.indexOf(u) >= 0 && o[u] === void 0 && console.warn('THREE.GLTFLoader: Unknown extension "' + u + '".');
        }
      }
    c.setExtensions(a), c.setPlugins(o), c.parse(i, n);
  }
  parseAsync(e, t) {
    const i = this;
    return new Promise(function(n, r) {
      i.parse(e, t, n, r);
    });
  }
};
function E0e() {
  let s = {};
  return {
    get: function(e) {
      return s[e];
    },
    add: function(e, t) {
      s[e] = t;
    },
    remove: function(e) {
      delete s[e];
    },
    removeAll: function() {
      s = {};
    }
  };
}
const Ii = {
  KHR_BINARY_GLTF: "KHR_binary_glTF",
  KHR_DRACO_MESH_COMPRESSION: "KHR_draco_mesh_compression",
  KHR_LIGHTS_PUNCTUAL: "KHR_lights_punctual",
  KHR_MATERIALS_CLEARCOAT: "KHR_materials_clearcoat",
  KHR_MATERIALS_IOR: "KHR_materials_ior",
  KHR_MATERIALS_SHEEN: "KHR_materials_sheen",
  KHR_MATERIALS_SPECULAR: "KHR_materials_specular",
  KHR_MATERIALS_TRANSMISSION: "KHR_materials_transmission",
  KHR_MATERIALS_IRIDESCENCE: "KHR_materials_iridescence",
  KHR_MATERIALS_ANISOTROPY: "KHR_materials_anisotropy",
  KHR_MATERIALS_UNLIT: "KHR_materials_unlit",
  KHR_MATERIALS_VOLUME: "KHR_materials_volume",
  KHR_TEXTURE_BASISU: "KHR_texture_basisu",
  KHR_TEXTURE_TRANSFORM: "KHR_texture_transform",
  KHR_MESH_QUANTIZATION: "KHR_mesh_quantization",
  KHR_MATERIALS_EMISSIVE_STRENGTH: "KHR_materials_emissive_strength",
  EXT_TEXTURE_WEBP: "EXT_texture_webp",
  EXT_TEXTURE_AVIF: "EXT_texture_avif",
  EXT_MESHOPT_COMPRESSION: "EXT_meshopt_compression",
  EXT_MESH_GPU_INSTANCING: "EXT_mesh_gpu_instancing"
};
let T0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_LIGHTS_PUNCTUAL, this.cache = { refs: {}, uses: {} };
  }
  _markDefs() {
    const e = this.parser, t = this.parser.json.nodes || [];
    for (let i = 0, n = t.length; i < n; i++) {
      const r = t[i];
      r.extensions && r.extensions[this.name] && r.extensions[this.name].light !== void 0 && e._addNodeRef(this.cache, r.extensions[this.name].light);
    }
  }
  _loadLight(e) {
    const t = this.parser, i = "light:" + e;
    let n = t.cache.get(i);
    if (n)
      return n;
    const r = t.json, l = ((r.extensions && r.extensions[this.name] || {}).lights || [])[e];
    let c;
    const h = new _e(16777215);
    l.color !== void 0 && h.setRGB(l.color[0], l.color[1], l.color[2], Si);
    const u = l.range !== void 0 ? l.range : 0;
    switch (l.type) {
      case "directional":
        c = new _a(h), c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      case "point":
        c = new Tl(h), c.distance = u;
        break;
      case "spot":
        c = new oh(h), c.distance = u, l.spot = l.spot || {}, l.spot.innerConeAngle = l.spot.innerConeAngle !== void 0 ? l.spot.innerConeAngle : 0, l.spot.outerConeAngle = l.spot.outerConeAngle !== void 0 ? l.spot.outerConeAngle : Math.PI / 4, c.angle = l.spot.outerConeAngle, c.penumbra = 1 - l.spot.innerConeAngle / l.spot.outerConeAngle, c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      default:
        throw new Error("THREE.GLTFLoader: Unexpected light type: " + l.type);
    }
    return c.position.set(0, 0, 0), c.decay = 2, lc(c, l), l.intensity !== void 0 && (c.intensity = l.intensity), c.name = t.createUniqueName(l.name || "light_" + e), n = Promise.resolve(c), t.cache.add(i, n), n;
  }
  getDependency(e, t) {
    if (e === "light")
      return this._loadLight(t);
  }
  createNodeAttachment(e) {
    const t = this, i = this.parser, r = i.json.nodes[e], o = (r.extensions && r.extensions[this.name] || {}).light;
    return o === void 0 ? null : this._loadLight(o).then(function(l) {
      return i._getNodeRef(t.cache, o, l);
    });
  }
}, C0e = class {
  constructor() {
    this.name = Ii.KHR_MATERIALS_UNLIT;
  }
  getMaterialType() {
    return kt;
  }
  extendParams(e, t, i) {
    const n = [];
    e.color = new _e(1, 1, 1), e.opacity = 1;
    const r = t.pbrMetallicRoughness;
    if (r) {
      if (Array.isArray(r.baseColorFactor)) {
        const a = r.baseColorFactor;
        e.color.setRGB(a[0], a[1], a[2], Si), e.opacity = a[3];
      }
      r.baseColorTexture !== void 0 && n.push(
        i.assignTexture(
          e,
          "map",
          r.baseColorTexture,
          qi
        )
      );
    }
    return Promise.all(n);
  }
}, _0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_EMISSIVE_STRENGTH;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name].emissiveStrength;
    return r !== void 0 && (t.emissiveIntensity = r), Promise.resolve();
  }
}, P0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_CLEARCOAT;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    if (a.clearcoatFactor !== void 0 && (t.clearcoat = a.clearcoatFactor), a.clearcoatTexture !== void 0 && r.push(
      i.assignTexture(t, "clearcoatMap", a.clearcoatTexture)
    ), a.clearcoatRoughnessFactor !== void 0 && (t.clearcoatRoughness = a.clearcoatRoughnessFactor), a.clearcoatRoughnessTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "clearcoatRoughnessMap",
        a.clearcoatRoughnessTexture
      )
    ), a.clearcoatNormalTexture !== void 0 && (r.push(
      i.assignTexture(
        t,
        "clearcoatNormalMap",
        a.clearcoatNormalTexture
      )
    ), a.clearcoatNormalTexture.scale !== void 0)) {
      const o = a.clearcoatNormalTexture.scale;
      t.clearcoatNormalScale = new Se(o, o);
    }
    return Promise.all(r);
  }
}, R0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_IRIDESCENCE;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.iridescenceFactor !== void 0 && (t.iridescence = a.iridescenceFactor), a.iridescenceTexture !== void 0 && r.push(
      i.assignTexture(t, "iridescenceMap", a.iridescenceTexture)
    ), a.iridescenceIor !== void 0 && (t.iridescenceIOR = a.iridescenceIor), t.iridescenceThicknessRange === void 0 && (t.iridescenceThicknessRange = [100, 400]), a.iridescenceThicknessMinimum !== void 0 && (t.iridescenceThicknessRange[0] = a.iridescenceThicknessMinimum), a.iridescenceThicknessMaximum !== void 0 && (t.iridescenceThicknessRange[1] = a.iridescenceThicknessMaximum), a.iridescenceThicknessTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "iridescenceThicknessMap",
        a.iridescenceThicknessTexture
      )
    ), Promise.all(r);
  }
}, D0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_SHEEN;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [];
    t.sheenColor = new _e(0, 0, 0), t.sheenRoughness = 0, t.sheen = 1;
    const a = n.extensions[this.name];
    if (a.sheenColorFactor !== void 0) {
      const o = a.sheenColorFactor;
      t.sheenColor.setRGB(
        o[0],
        o[1],
        o[2],
        Si
      );
    }
    return a.sheenRoughnessFactor !== void 0 && (t.sheenRoughness = a.sheenRoughnessFactor), a.sheenColorTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "sheenColorMap",
        a.sheenColorTexture,
        qi
      )
    ), a.sheenRoughnessTexture !== void 0 && r.push(
      i.assignTexture(t, "sheenRoughnessMap", a.sheenRoughnessTexture)
    ), Promise.all(r);
  }
}, L0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_TRANSMISSION;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.transmissionFactor !== void 0 && (t.transmission = 0), a.transmissionTexture !== void 0 && r.push(
      i.assignTexture(t, "transmissionMap", a.transmissionTexture)
    ), Promise.all(r);
  }
}, I0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_VOLUME;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.thickness = a.thicknessFactor !== void 0 ? a.thicknessFactor : 0, a.thicknessTexture !== void 0 && r.push(
      i.assignTexture(t, "thicknessMap", a.thicknessTexture)
    ), t.attenuationDistance = a.attenuationDistance || 1 / 0;
    const o = a.attenuationColor || [1, 1, 1];
    return t.attenuationColor = new _e().setRGB(
      o[0],
      o[1],
      o[2],
      Si
    ), Promise.all(r);
  }
}, O0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_IOR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name];
    return t.ior = r.ior !== void 0 ? r.ior : 1.5, Promise.resolve();
  }
}, N0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_SPECULAR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.specularIntensity = a.specularFactor !== void 0 ? a.specularFactor : 1, a.specularTexture !== void 0 && r.push(
      i.assignTexture(t, "specularIntensityMap", a.specularTexture)
    );
    const o = a.specularColorFactor || [1, 1, 1];
    return t.specularColor = new _e().setRGB(
      o[0],
      o[1],
      o[2],
      Si
    ), a.specularColorTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "specularColorMap",
        a.specularColorTexture,
        qi
      )
    ), Promise.all(r);
  }
}, B0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_MATERIALS_ANISOTROPY;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.anisotropyStrength !== void 0 && (t.anisotropy = a.anisotropyStrength), a.anisotropyRotation !== void 0 && (t.anisotropyRotation = a.anisotropyRotation), a.anisotropyTexture !== void 0 && r.push(
      i.assignTexture(t, "anisotropyMap", a.anisotropyTexture)
    ), Promise.all(r);
  }
}, F0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.KHR_TEXTURE_BASISU;
  }
  loadTexture(e) {
    const t = this.parser, i = t.json, n = i.textures[e];
    if (!n.extensions || !n.extensions[this.name])
      return null;
    const r = n.extensions[this.name], a = t.options.ktx2Loader;
    if (!a) {
      if (i.extensionsRequired && i.extensionsRequired.indexOf(this.name) >= 0)
        throw new Error(
          "THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures"
        );
      return null;
    }
    return t.loadTextureImage(e, r.source, a);
  }
}, U0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.EXT_TEXTURE_WEBP, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: WebP required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}, z0e = class {
  constructor(e) {
    this.parser = e, this.name = Ii.EXT_TEXTURE_AVIF, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: AVIF required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}, k0e = class {
  constructor(e) {
    this.name = Ii.EXT_MESHOPT_COMPRESSION, this.parser = e;
  }
  loadBufferView(e) {
    const t = this.parser.json, i = t.bufferViews[e];
    if (i.extensions && i.extensions[this.name]) {
      const n = i.extensions[this.name], r = this.parser.getDependency("buffer", n.buffer), a = this.parser.options.meshoptDecoder;
      if (!a || !a.supported) {
        if (t.extensionsRequired && t.extensionsRequired.indexOf(this.name) >= 0)
          throw new Error(
            "THREE.GLTFLoader: setMeshoptDecoder must be called before loading compressed files"
          );
        return null;
      }
      return r.then(function(o) {
        const l = n.byteOffset || 0, c = n.byteLength || 0, h = n.count, u = n.byteStride, d = new Uint8Array(o, l, c);
        return a.decodeGltfBufferAsync ? a.decodeGltfBufferAsync(h, u, d, n.mode, n.filter).then(function(f) {
          return f.buffer;
        }) : a.ready.then(function() {
          const f = new ArrayBuffer(h * u);
          return a.decodeGltfBuffer(
            new Uint8Array(f),
            h,
            u,
            d,
            n.mode,
            n.filter
          ), f;
        });
      });
    } else
      return null;
  }
}, G0e = class {
  constructor(e) {
    this.name = Ii.EXT_MESH_GPU_INSTANCING, this.parser = e;
  }
  createNodeMesh(e) {
    const t = this.parser.json, i = t.nodes[e];
    if (!i.extensions || !i.extensions[this.name] || i.mesh === void 0)
      return null;
    const n = t.meshes[i.mesh];
    for (const c of n.primitives)
      if (c.mode !== ma.TRIANGLES && c.mode !== ma.TRIANGLE_STRIP && c.mode !== ma.TRIANGLE_FAN && c.mode !== void 0)
        return null;
    const a = i.extensions[this.name].attributes, o = [], l = {};
    for (const c in a)
      o.push(
        this.parser.getDependency("accessor", a[c]).then((h) => (l[c] = h, l[c]))
      );
    return o.length < 1 ? null : (o.push(this.parser.createNodeMesh(e)), Promise.all(o).then((c) => {
      const h = c.pop(), u = h.isGroup ? h.children : [h], d = c[0].count, f = [];
      for (const p of u) {
        const v = new et(), g = new _(), m = new St(), x = new _(1, 1, 1), y = new dw(p.geometry, p.material, d);
        ale(this.parser, p, y);
        for (let b = 0; b < d; b++)
          l.TRANSLATION && g.fromBufferAttribute(l.TRANSLATION, b), l.ROTATION && m.fromBufferAttribute(l.ROTATION, b), l.SCALE && x.fromBufferAttribute(l.SCALE, b), y.setMatrixAt(b, v.compose(g, m, x));
        for (const b in l)
          b !== "TRANSLATION" && b !== "ROTATION" && b !== "SCALE" && p.geometry.setAttribute(b, l[b]);
        mt.prototype.copy.call(y, p);
        const A = JSON.parse(JSON.stringify(p.userData));
        rle(y, A.source ?? {}), this.parser.assignFinalMaterial(y), f.push(y);
      }
      return h.isGroup ? (h.clear(), h.add(...f), h) : f[0];
    }));
  }
};
const LB = "glTF", Om = 12, J_ = { JSON: 1313821514, BIN: 5130562 };
let H0e = class {
  constructor(e) {
    this.name = Ii.KHR_BINARY_GLTF, this.content = null, this.body = null;
    const t = new DataView(e, 0, Om), i = new TextDecoder();
    if (this.header = {
      magic: i.decode(new Uint8Array(e.slice(0, 4))),
      version: t.getUint32(4, !0),
      length: t.getUint32(8, !0)
    }, this.header.magic !== LB)
      throw new Error("THREE.GLTFLoader: Unsupported glTF-Binary header.");
    if (this.header.version < 2)
      throw new Error("THREE.GLTFLoader: Legacy binary file detected.");
    const n = this.header.length - Om, r = new DataView(e, Om);
    let a = 0;
    for (; a < n; ) {
      const o = r.getUint32(a, !0);
      a += 4;
      const l = r.getUint32(a, !0);
      if (a += 4, l === J_.JSON) {
        const c = new Uint8Array(
          e,
          Om + a,
          o
        );
        this.content = i.decode(c);
      } else if (l === J_.BIN) {
        const c = Om + a;
        this.body = e.slice(c, c + o);
      }
      a += o;
    }
    if (this.content === null)
      throw new Error("THREE.GLTFLoader: JSON content not found.");
  }
}, V0e = class {
  constructor(e, t) {
    if (!t)
      throw new Error("THREE.GLTFLoader: No DRACOLoader instance provided.");
    this.name = Ii.KHR_DRACO_MESH_COMPRESSION, this.json = e, this.dracoLoader = t, this.dracoLoader.preload();
  }
  decodePrimitive(e, t) {
    const i = this.json, n = this.dracoLoader, r = e.extensions[this.name].bufferView, a = e.extensions[this.name].attributes, o = {}, l = {}, c = {};
    for (const h in a) {
      const u = Hb[h] || h.toLowerCase();
      o[u] = a[h];
    }
    for (const h in e.attributes) {
      const u = Hb[h] || h.toLowerCase();
      if (a[h] !== void 0) {
        const d = i.accessors[e.attributes[h]], f = Xf[d.componentType];
        c[u] = f.name, l[u] = d.normalized === !0;
      }
    }
    return t.getDependency("bufferView", r).then(function(h) {
      return new Promise(function(u) {
        n.decodeDracoFile(
          h,
          function(d) {
            for (const f in d.attributes) {
              const p = d.attributes[f], v = l[f];
              v !== void 0 && (p.normalized = v);
            }
            u(d);
          },
          o,
          c
        );
      });
    });
  }
}, W0e = class {
  constructor() {
    this.name = Ii.KHR_TEXTURE_TRANSFORM;
  }
  extendTexture(e, t) {
    return (t.texCoord === void 0 || t.texCoord === e.channel) && t.offset === void 0 && t.rotation === void 0 && t.scale === void 0 || (e = e.clone(), t.texCoord !== void 0 && (e.channel = t.texCoord), t.offset !== void 0 && e.offset.fromArray(t.offset), t.rotation !== void 0 && (e.rotation = t.rotation), t.scale !== void 0 && e.repeat.fromArray(t.scale), e.needsUpdate = !0), e;
  }
}, X0e = class {
  constructor() {
    this.name = Ii.KHR_MESH_QUANTIZATION;
  }
}, IB = class extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  copySampleValue_(e) {
    const t = this.resultBuffer, i = this.sampleValues, n = this.valueSize, r = e * n * 3 + n;
    for (let a = 0; a !== n; a++)
      t[a] = i[r + a];
    return t;
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = o * 2, c = o * 3, h = n - t, u = (i - t) / h, d = u * u, f = d * u, p = e * c, v = p - c, g = -2 * f + 3 * d, m = f - d, x = 1 - g, y = m - d + u;
    for (let A = 0; A !== o; A++) {
      const b = a[v + A + o], M = a[v + A + l] * h, w = a[p + A + o], C = a[p + A] * h;
      r[A] = x * b + y * M + g * w + m * C;
    }
    return r;
  }
};
const j0e = new St();
let q0e = class extends IB {
  interpolate_(e, t, i, n) {
    const r = super.interpolate_(e, t, i, n);
    return j0e.fromArray(r).normalize().toArray(r), r;
  }
};
const ma = {
  FLOAT: 5126,
  //FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123
}, Xf = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array
}, Q_ = {
  9728: Ni,
  9729: ii,
  9984: $c,
  9985: $o,
  9986: $a,
  9987: ds
}, $_ = {
  33071: yn,
  33648: co,
  10497: un
}, SA = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16
}, Hb = {
  POSITION: "position",
  NORMAL: "normal",
  TANGENT: "tangent",
  TEXCOORD_0: "uv",
  TEXCOORD_1: "uv1",
  TEXCOORD_2: "uv2",
  TEXCOORD_3: "uv3",
  COLOR_0: "color",
  WEIGHTS_0: "skinWeight",
  JOINTS_0: "skinIndex"
}, jl = {
  scale: "scale",
  translation: "position",
  rotation: "quaternion",
  weights: "morphTargetInfluences"
}, Y0e = {
  CUBICSPLINE: void 0,
  // We use a custom interpolant (GLTFCubicSplineInterpolation) for CUBICSPLINE tracks. Each
  // keyframe track will be initialized with a default interpolation type, then modified.
  LINEAR: l0,
  STEP: Uc
}, wA = {
  OPAQUE: "OPAQUE",
  MASK: "MASK",
  BLEND: "BLEND"
};
function Z0e(s) {
  return s.DefaultMaterial === void 0 && (s.DefaultMaterial = new or({
    color: 16777215,
    emissive: 0,
    metalness: 1,
    roughness: 1,
    transparent: !1,
    depthTest: !0,
    side: sa
  }), Wb(s.DefaultMaterial, {
    url: "",
    indexId: "-1"
  })), s.DefaultMaterial;
}
function Rh(s, e, t) {
  for (const i in t.extensions)
    s[i] === void 0 && (e.userData.gltfExtensions = e.userData.gltfExtensions || {}, e.userData.gltfExtensions[i] = t.extensions[i]);
}
function lc(s, e) {
  e.extras !== void 0 && (typeof e.extras == "object" ? Object.assign(s.userData, e.extras) : console.warn("THREE.GLTFLoader: Ignoring primitive type .extras, " + e.extras));
}
function K0e(s, e, t) {
  let i = !1, n = !1, r = !1;
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (u.POSITION !== void 0 && (i = !0), u.NORMAL !== void 0 && (n = !0), u.COLOR_0 !== void 0 && (r = !0), i && n && r)
      break;
  }
  if (!i && !n && !r)
    return Promise.resolve(s);
  const a = [], o = [], l = [];
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (i) {
      const d = u.POSITION !== void 0 ? t.getDependency("accessor", u.POSITION) : s.attributes.position;
      a.push(d);
    }
    if (n) {
      const d = u.NORMAL !== void 0 ? t.getDependency("accessor", u.NORMAL) : s.attributes.normal;
      o.push(d);
    }
    if (r) {
      const d = u.COLOR_0 !== void 0 ? t.getDependency("accessor", u.COLOR_0) : s.attributes.color;
      l.push(d);
    }
  }
  return Promise.all([
    Promise.all(a),
    Promise.all(o),
    Promise.all(l)
  ]).then(function(c) {
    const h = c[0], u = c[1], d = c[2];
    return i && (s.morphAttributes.position = h), n && (s.morphAttributes.normal = u), r && (s.morphAttributes.color = d), s.morphTargetsRelative = !0, s;
  });
}
function J0e(s, e) {
  if (s.updateMorphTargets(), e.weights !== void 0)
    for (let t = 0, i = e.weights.length; t < i; t++)
      s.morphTargetInfluences[t] = e.weights[t];
  if (e.extras && Array.isArray(e.extras.targetNames)) {
    const t = e.extras.targetNames;
    if (s.morphTargetInfluences.length === t.length) {
      s.morphTargetDictionary = {};
      for (let i = 0, n = t.length; i < n; i++)
        s.morphTargetDictionary[t[i]] = i;
    } else
      console.warn("THREE.GLTFLoader: Invalid extras.targetNames length. Ignoring names.");
  }
}
function Q0e(s) {
  let e;
  const t = s.extensions && s.extensions[Ii.KHR_DRACO_MESH_COMPRESSION];
  if (t ? e = "draco:" + t.bufferView + ":" + t.indices + ":" + EA(t.attributes) : e = s.indices + ":" + EA(s.attributes) + ":" + s.mode, s.targets !== void 0)
    for (let i = 0, n = s.targets.length; i < n; i++)
      e += ":" + EA(s.targets[i]);
  return e;
}
function EA(s) {
  let e = "";
  const t = Object.keys(s).sort();
  for (let i = 0, n = t.length; i < n; i++)
    e += t[i] + ":" + s[t[i]] + ";";
  return e;
}
function Vb(s) {
  switch (s) {
    case Int8Array:
      return 1 / 127;
    case Uint8Array:
      return 1 / 255;
    case Int16Array:
      return 1 / 32767;
    case Uint16Array:
      return 1 / 65535;
    default:
      throw new Error("THREE.GLTFLoader: Unsupported normalized accessor component type.");
  }
}
function $0e(s) {
  return s.search(/\.jpe?g($|\?)/i) > 0 || s.search(/^data\:image\/jpeg/) === 0 ? "image/jpeg" : s.search(/\.webp($|\?)/i) > 0 || s.search(/^data\:image\/webp/) === 0 ? "image/webp" : "image/png";
}
const ele = new et();
let tle = class {
  constructor(e = {}, t = {}) {
    this.json = e, this.extensions = {}, this.plugins = {}, this.options = t, this.cache = new E0e(), this.associations = /* @__PURE__ */ new Map(), this.primitiveCache = {}, this.nodeCache = {}, this.meshCache = { refs: {}, uses: {} }, this.cameraCache = { refs: {}, uses: {} }, this.lightCache = { refs: {}, uses: {} }, this.sourceCache = {}, this.textureCache = {}, this.nodeNamesUsed = {};
    let i = !1, n = !1, r = -1;
    typeof navigator < "u" && (i = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) === !0, n = navigator.userAgent.indexOf("Firefox") > -1, r = n ? navigator.userAgent.match(/Firefox\/([0-9]+)\./)[1] : -1), typeof createImageBitmap > "u" || i || n && r < 98 ? this.textureLoader = new rh(this.options.manager) : this.textureLoader = new A2(this.options.manager), this.textureLoader.setCrossOrigin(this.options.crossOrigin), this.textureLoader.setRequestHeader(this.options.requestHeader), this.fileLoader = new Nn(this.options.manager), this.fileLoader.setResponseType("arraybuffer"), this.options.crossOrigin === "use-credentials" && this.fileLoader.setWithCredentials(!0);
  }
  setExtensions(e) {
    this.extensions = e;
  }
  setPlugins(e) {
    this.plugins = e;
  }
  parse(e, t) {
    const i = this, n = this.json, r = this.extensions;
    this.cache.removeAll(), this.nodeCache = {}, this._invokeAll(function(a) {
      return a._markDefs && a._markDefs();
    }), Promise.all(
      this._invokeAll(function(a) {
        return a.beforeRoot && a.beforeRoot();
      })
    ).then(function() {
      return Promise.all([
        i.getDependencies("scene"),
        i.getDependencies("animation"),
        i.getDependencies("camera")
      ]);
    }).then(function(a) {
      const o = {
        scene: a[0][n.scene || 0],
        scenes: a[0],
        animations: a[1],
        cameras: a[2],
        asset: n.asset,
        parser: i,
        userData: {}
      };
      Rh(r, o, n), lc(o, n), Promise.all(
        i._invokeAll(function(l) {
          return l.afterRoot && l.afterRoot(o);
        })
      ).then(function() {
        e(o);
      });
    }).catch(t);
  }
  /**
   * Marks the special nodes/meshes in json for efficient parse.
   */
  _markDefs() {
    const e = this.json.nodes || [], t = this.json.skins || [], i = this.json.meshes || [];
    for (let n = 0, r = t.length; n < r; n++) {
      const a = t[n].joints;
      for (let o = 0, l = a.length; o < l; o++)
        e[a[o]].isBone = !0;
    }
    for (let n = 0, r = e.length; n < r; n++) {
      const a = e[n];
      a.mesh !== void 0 && (this._addNodeRef(this.meshCache, a.mesh), a.skin !== void 0 && (i[a.mesh].isSkinnedMesh = !0)), a.camera !== void 0 && this._addNodeRef(this.cameraCache, a.camera);
    }
  }
  /**
   * Counts references to shared node / Object3D resources. These resources
   * can be reused, or "instantiated", at multiple nodes in the scene
   * hierarchy. Mesh, Camera, and Light instances are instantiated and must
   * be marked. Non-scenegraph resources (like Materials, Geometries, and
   * Textures) can be reused directly and are not marked here.
   *
   * Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
   */
  _addNodeRef(e, t) {
    t !== void 0 && (e.refs[t] === void 0 && (e.refs[t] = e.uses[t] = 0), e.refs[t]++);
  }
  /** Returns a reference to a shared resource, cloning it if necessary. */
  _getNodeRef(e, t, i) {
    if (e.refs[t] <= 1)
      return i;
    const n = i.clone(), r = (a, o) => {
      const l = this.associations.get(a);
      l != null && this.associations.set(o, l);
      for (const [c, h] of a.children.entries())
        r(h, o.children[c]);
    };
    return r(i, n), n.name += "_instance_" + e.uses[t]++, n;
  }
  _invokeOne(e) {
    const t = Object.values(this.plugins);
    t.push(this);
    for (let i = 0; i < t.length; i++) {
      const n = e(t[i]);
      if (n)
        return n;
    }
    return null;
  }
  _invokeAll(e) {
    const t = Object.values(this.plugins);
    t.unshift(this);
    const i = [];
    for (let n = 0; n < t.length; n++) {
      const r = e(t[n]);
      r && i.push(r);
    }
    return i;
  }
  /**
   * Requests the specified dependency asynchronously, with caching.
   * @param {string} type
   * @param {number} index
   * @return {Promise<Object3D|Material|THREE.Texture|AnimationClip|ArrayBuffer|Object>}
   */
  getDependency(e, t) {
    const i = e + ":" + t;
    let n = this.cache.get(i);
    if (!n) {
      switch (e) {
        case "scene":
          n = this.loadScene(t);
          break;
        case "node":
          n = this._invokeOne(function(r) {
            return r.loadNode && r.loadNode(t);
          });
          break;
        case "mesh":
          n = this._invokeOne(function(r) {
            return r.loadMesh && r.loadMesh(t);
          });
          break;
        case "accessor":
          n = this.loadAccessor(t);
          break;
        case "bufferView":
          n = this._invokeOne(function(r) {
            return r.loadBufferView && r.loadBufferView(t);
          });
          break;
        case "buffer":
          n = this.loadBuffer(t);
          break;
        case "material":
          n = this._invokeOne(function(r) {
            return r.loadMaterial && r.loadMaterial(t);
          });
          break;
        case "texture":
          n = this._invokeOne(function(r) {
            return r.loadTexture && r.loadTexture(t);
          });
          break;
        case "skin":
          n = this.loadSkin(t);
          break;
        case "animation":
          n = this._invokeOne(function(r) {
            return r.loadAnimation && r.loadAnimation(t);
          });
          break;
        case "camera":
          n = this.loadCamera(t);
          break;
        default:
          if (n = this._invokeOne(function(r) {
            return r != this && r.getDependency && r.getDependency(e, t);
          }), !n)
            throw new Error("Unknown type: " + e);
          break;
      }
      this.cache.add(i, n);
    }
    return n;
  }
  /**
   * Requests all dependencies of the specified type asynchronously, with caching.
   * @param {string} type
   * @return {Promise<Array<Object>>}
   */
  getDependencies(e) {
    let t = this.cache.get(e);
    if (!t) {
      const i = this, n = this.json[e + (e === "mesh" ? "es" : "s")] || [];
      t = Promise.all(
        n.map(function(r, a) {
          return i.getDependency(e, a);
        })
      ), this.cache.add(e, t);
    }
    return t;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBuffer(e) {
    const t = this.json.buffers[e], i = this.fileLoader;
    if (t.type && t.type !== "arraybuffer")
      throw new Error("THREE.GLTFLoader: " + t.type + " buffer type is not supported.");
    if (t.uri === void 0 && e === 0)
      return Promise.resolve(this.extensions[Ii.KHR_BINARY_GLTF].body);
    const n = this.options;
    return new Promise(function(r, a) {
      i.load(
        Rs.resolveURL(t.uri, n.path),
        r,
        void 0,
        function() {
          a(new Error('THREE.GLTFLoader: Failed to load buffer "' + t.uri + '".'));
        }
      );
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferViewIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBufferView(e) {
    const t = this.json.bufferViews[e];
    return this.getDependency("buffer", t.buffer).then(function(i) {
      const n = t.byteLength || 0, r = t.byteOffset || 0;
      return i.slice(r, r + n);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
   * @param {number} accessorIndex
   * @return {Promise<BufferAttribute|InterleavedBufferAttribute>}
   */
  loadAccessor(e) {
    const t = this, i = this.json, n = this.json.accessors[e];
    if (n.bufferView === void 0 && n.sparse === void 0) {
      const a = SA[n.type], o = Xf[n.componentType], l = n.normalized === !0, c = new o(n.count * a);
      return Promise.resolve(new Ht(c, a, l));
    }
    const r = [];
    return n.bufferView !== void 0 ? r.push(this.getDependency("bufferView", n.bufferView)) : r.push(null), n.sparse !== void 0 && (r.push(
      this.getDependency("bufferView", n.sparse.indices.bufferView)
    ), r.push(
      this.getDependency("bufferView", n.sparse.values.bufferView)
    )), Promise.all(r).then(function(a) {
      const o = a[0], l = SA[n.type], c = Xf[n.componentType], h = c.BYTES_PER_ELEMENT, u = h * l, d = n.byteOffset || 0, f = n.bufferView !== void 0 ? i.bufferViews[n.bufferView].byteStride : void 0, p = n.normalized === !0;
      let v, g;
      if (f && f !== u) {
        const m = Math.floor(d / f), x = "InterleavedBuffer:" + n.bufferView + ":" + n.componentType + ":" + m + ":" + n.count;
        let y = t.cache.get(x);
        y || (v = new c(
          o,
          m * f,
          n.count * f / h
        ), y = new Xu(v, f / h), t.cache.add(x, y)), g = new c0(
          y,
          l,
          d % f / h,
          p
        );
      } else
        o === null ? v = new c(n.count * l) : v = new c(o, d, n.count * l), g = new Ht(v, l, p);
      if (n.sparse !== void 0) {
        const m = SA.SCALAR, x = Xf[n.sparse.indices.componentType], y = n.sparse.indices.byteOffset || 0, A = n.sparse.values.byteOffset || 0, b = new x(
          a[1],
          y,
          n.sparse.count * m
        ), M = new c(
          a[2],
          A,
          n.sparse.count * l
        );
        o !== null && (g = new Ht(
          g.array.slice(),
          g.itemSize,
          g.normalized
        ));
        for (let w = 0, C = b.length; w < C; w++) {
          const T = b[w];
          if (g.setX(T, M[w * l]), l >= 2 && g.setY(T, M[w * l + 1]), l >= 3 && g.setZ(T, M[w * l + 2]), l >= 4 && g.setW(T, M[w * l + 3]), l >= 5)
            throw new Error("THREE.GLTFLoader: Unsupported itemSize in sparse BufferAttribute.");
        }
      }
      return g;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
   * @param {number} textureIndex
   * @return {Promise<THREE.Texture|null>}
   */
  loadTexture(e) {
    const t = this.json, i = this.options, r = t.textures[e].source, a = t.images[r];
    let o = this.textureLoader;
    if (a.uri) {
      const l = i.manager.getHandler(a.uri);
      l !== null && (o = l);
    }
    return this.loadTextureImage(e, r, o);
  }
  loadTextureImage(e, t, i) {
    const n = this, r = this.json, a = r.textures[e], o = r.images[t], l = (o.uri || o.bufferView) + ":" + a.sampler;
    if (this.textureCache[l])
      return this.textureCache[l];
    const c = this.loadImageSource(t, i).then(function(h) {
      h.flipY = !1, h.name = a.name || o.name || `_img${t}`, h.name === "" && typeof o.uri == "string" && o.uri.startsWith("data:image/") === !1 && (h.name = o.uri);
      const d = (r.samplers || {})[a.sampler] || {};
      return h.magFilter = Q_[d.magFilter] || ii, h.minFilter = Q_[d.minFilter] || ds, h.wrapS = $_[d.wrapS] || un, h.wrapT = $_[d.wrapT] || un, n.associations.set(h, { textures: e }), h;
    }).catch(function() {
      return null;
    });
    return this.textureCache[l] = c, c;
  }
  loadImageSource(e, t) {
    const i = this, n = this.json, r = this.options;
    if (this.sourceCache[e] !== void 0)
      return this.sourceCache[e].then((u) => u.clone());
    const a = n.images[e], o = self.URL || self.webkitURL;
    let l = a.uri || "", c = !1;
    if (a.bufferView !== void 0)
      l = i.getDependency("bufferView", a.bufferView).then(function(u) {
        c = !0;
        const d = new Blob([u], { type: a.mimeType });
        return l = o.createObjectURL(d), l;
      });
    else if (a.uri === void 0)
      throw new Error("THREE.GLTFLoader: Image " + e + " is missing URI and bufferView");
    const h = Promise.resolve(l).then(function(u) {
      return new Promise(function(d, f) {
        let p = d;
        t.isImageBitmapLoader === !0 && (p = function(v) {
          const g = new Ut(v), m = `${e}`;
          nle(g, {
            image: {
              indexId: m
            },
            url: "",
            indexId: m
          }), g.needsUpdate = !0, d(g);
        }), t.load(Rs.resolveURL(u, r.path), p, void 0, f);
      });
    }).then(function(u) {
      return c === !0 && o.revokeObjectURL(l), u.userData.mimeType = a.mimeType || $0e(a.uri), u;
    }).catch(function(u) {
      throw console.error("THREE.GLTFLoader: Couldn't load texture", l), u;
    });
    return this.sourceCache[e] = h, h;
  }
  /**
   * Asynchronously assigns a texture to the given material parameters.
   * @param {Object} materialParams
   * @param {string} mapName
   * @param {Object} mapDef
   * @return {Promise<Texture>}
   */
  assignTexture(e, t, i, n) {
    const r = this;
    return this.getDependency("texture", i.index).then(function(a) {
      if (!a)
        return null;
      if (i.texCoord !== void 0 && i.texCoord > 0 && (a = a.clone(), a.channel = i.texCoord), r.extensions[Ii.KHR_TEXTURE_TRANSFORM]) {
        const o = i.extensions !== void 0 ? i.extensions[Ii.KHR_TEXTURE_TRANSFORM] : void 0;
        if (o) {
          const l = r.associations.get(a);
          a = r.extensions[Ii.KHR_TEXTURE_TRANSFORM].extendTexture(
            a,
            o
          ), r.associations.set(a, l);
        }
      }
      return n !== void 0 && (a.colorSpace = n), e[t] = a, a;
    });
  }
  /**
   * Assigns final material to a Mesh, Line, or Points instance. The instance
   * already has a material (generated from the glTF material options alone)
   * but reuse of the same glTF material may require multiple threejs materials
   * to accommodate different primitive types, defines, etc. New materials will
   * be created if necessary, and reused from a cache.
   * @param  {Object3D} mesh Mesh, Line, or Points instance.
   */
  assignFinalMaterial(e) {
    const t = e.geometry;
    let i = e.material;
    const n = t.attributes.tangent === void 0, r = t.attributes.color !== void 0, a = t.attributes.normal === void 0;
    if (e.isPoints) {
      const o = "PointsMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new qu(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, l.sizeAttenuation = !1, this.cache.add(o, l)), i = l;
    } else if (e.isLine) {
      const o = "LineBasicMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new Bi(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, this.cache.add(o, l)), i = l;
    }
    if (n || r || a) {
      let o = "ClonedMaterial:" + i.uuid + ":";
      n && (o += "derivative-tangents:"), r && (o += "vertex-colors:"), a && (o += "flat-shading:");
      let l = this.cache.get(o);
      if (!l) {
        l = i.clone(), r && (l.vertexColors = !0), a && (l.flatShading = !0), n && (l.normalScale && (l.normalScale.y *= -1), l.clearcoatNormalScale && (l.clearcoatNormalScale.y *= -1)), this.cache.add(o, l);
        const c = o.split(`${"ClonedMaterial:" + i.uuid + ":"}`)[1];
        Wb(l, {
          indexId: `${i.userData.source.indexId}_${c}`
        }), this.associations.set(l, this.associations.get(i));
      }
      i = l;
    }
    e.material = i;
  }
  getMaterialType() {
    return or;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
   * @param {number} materialIndex
   * @return {Promise<Material>}
   */
  loadMaterial(e) {
    const t = this, i = this.json, n = this.extensions, r = i.materials[e];
    let a;
    const o = {}, l = r.extensions || {}, c = [];
    if (l[Ii.KHR_MATERIALS_UNLIT]) {
      const u = n[Ii.KHR_MATERIALS_UNLIT];
      a = u.getMaterialType(), c.push(u.extendParams(o, r, t));
    } else {
      const u = r.pbrMetallicRoughness || {};
      if (o.color = new _e(1, 1, 1), o.opacity = 1, Array.isArray(u.baseColorFactor)) {
        const d = u.baseColorFactor;
        o.color.setRGB(d[0], d[1], d[2], Si), o.opacity = d[3];
      }
      u.baseColorTexture !== void 0 && c.push(
        t.assignTexture(
          o,
          "map",
          u.baseColorTexture,
          qi
        )
      ), o.metalness = u.metallicFactor !== void 0 ? u.metallicFactor : 1, o.roughness = u.roughnessFactor !== void 0 ? u.roughnessFactor : 1, u.metallicRoughnessTexture !== void 0 && (c.push(
        t.assignTexture(
          o,
          "metalnessMap",
          u.metallicRoughnessTexture
        )
      ), c.push(
        t.assignTexture(
          o,
          "roughnessMap",
          u.metallicRoughnessTexture
        )
      )), a = this._invokeOne(function(d) {
        return d.getMaterialType && d.getMaterialType(e);
      }), c.push(
        Promise.all(
          this._invokeAll(function(d) {
            return d.extendMaterialParams && d.extendMaterialParams(e, o);
          })
        )
      );
    }
    r.doubleSided === !0 && (o.side = ui);
    const h = r.alphaMode || wA.OPAQUE;
    if (h === wA.BLEND ? (o.transparent = !0, o.depthWrite = !1) : (o.transparent = !1, h === wA.MASK && (o.alphaTest = r.alphaCutoff !== void 0 ? r.alphaCutoff : 0.5)), r.normalTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "normalMap", r.normalTexture)), o.normalScale = new Se(1, 1), r.normalTexture.scale !== void 0)) {
      const u = r.normalTexture.scale;
      o.normalScale.set(u, u);
    }
    if (r.occlusionTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "aoMap", r.occlusionTexture)), r.occlusionTexture.strength !== void 0 && (o.aoMapIntensity = r.occlusionTexture.strength)), r.emissiveFactor !== void 0 && a !== kt) {
      const u = r.emissiveFactor;
      o.emissive = new _e().setRGB(
        u[0],
        u[1],
        u[2],
        Si
      );
    }
    return r.emissiveTexture !== void 0 && a !== kt && c.push(
      t.assignTexture(
        o,
        "emissiveMap",
        r.emissiveTexture,
        qi
      )
    ), Promise.all(c).then(function() {
      const u = new a(o);
      return r.name && (u.name = r.name), lc(u, r), t.associations.set(u, { materials: e }), Wb(u, {
        url: "",
        indexId: `${e}`
      }), r.extensions && Rh(n, u, r), u;
    });
  }
  /** When Object3D instances are targeted by animation, they need unique names. */
  createUniqueName(e) {
    const t = Pi.sanitizeNodeName(e || "");
    return t in this.nodeNamesUsed ? t + "_" + ++this.nodeNamesUsed[t] : (this.nodeNamesUsed[t] = 0, t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
   *
   * Creates BufferGeometries from primitives.
   *
   * @param {Array<GLTF.Primitive>} primitives
   * @return {Promise<Array<BufferGeometry>>}
   */
  loadGeometries(e) {
    const t = this, i = this.extensions, n = this.primitiveCache;
    function r(o) {
      return i[Ii.KHR_DRACO_MESH_COMPRESSION].decodePrimitive(o, t).then(function(l) {
        return eP(l, o, t);
      });
    }
    const a = [];
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o], h = Q0e(c), u = n[h];
      if (u)
        a.push(u.promise);
      else {
        let d;
        c.extensions && c.extensions[Ii.KHR_DRACO_MESH_COMPRESSION] ? d = r(c) : d = eP(new Pt(), c, t), n[h] = { primitive: c, promise: d }, a.push(d);
      }
    }
    return Promise.all(a);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
   * @param {number} meshIndex
   * @return {Promise<Group|Mesh|SkinnedMesh>}
   */
  loadMesh(e) {
    const t = this, i = this.json, n = this.extensions, r = i.meshes[e], a = r.primitives, o = [];
    for (let l = 0, c = a.length; l < c; l++) {
      const h = a[l].material === void 0 ? Z0e(this.cache) : this.getDependency("material", a[l].material);
      o.push(h);
    }
    return o.push(t.loadGeometries(a)), Promise.all(o).then(function(l) {
      const c = l.slice(0, l.length - 1), h = l[l.length - 1], u = [];
      for (let f = 0, p = h.length; f < p; f++) {
        const v = h[f], g = a[f];
        let m;
        const x = c[f];
        if (g.mode === ma.TRIANGLES || g.mode === ma.TRIANGLE_STRIP || g.mode === ma.TRIANGLE_FAN || g.mode === void 0)
          m = r.isSkinnedMesh === !0 ? new fw(v, x) : new uw(v, x), m.isSkinnedMesh === !0 && m.normalizeSkinWeights(), g.mode === ma.TRIANGLE_STRIP ? m.geometry = up(m.geometry, i2) : g.mode === ma.TRIANGLE_FAN && (m.geometry = up(m.geometry, ip));
        else if (g.mode === ma.LINES)
          m = new _n(v, x);
        else if (g.mode === ma.LINE_STRIP)
          m = new ti(v, x);
        else if (g.mode === ma.LINE_LOOP)
          m = new ju(v, x);
        else if (g.mode === ma.POINTS)
          m = new sh(v, x);
        else
          throw new Error("THREE.GLTFLoader: Primitive mode unsupported: " + g.mode);
        Object.keys(m.geometry.morphAttributes).length > 0 && J0e(m, r), m.name = t.createUniqueName(r.name || "mesh_" + e), lc(m, r), g.extensions && Rh(n, m, g), t.assignFinalMaterial(m), u.push(m);
      }
      for (let f = 0, p = u.length; f < p; f++)
        t.associations.set(u[f], {
          meshes: e,
          primitives: f
        }), sle(u[f], {
          url: "",
          indexId: `${e}_${f}`
        });
      if (u.length === 1)
        return r.extensions && Rh(n, u[0], r), u[0];
      const d = new ci();
      r.extensions && Rh(n, d, r), t.associations.set(d, { meshes: e });
      for (let f = 0, p = u.length; f < p; f++)
        d.add(u[f]);
      return d;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#cameras
   * @param {number} cameraIndex
   * @return {Promise<THREE.Camera>}
   */
  loadCamera(e) {
    let t;
    const i = this.json.cameras[e], n = i[i.type];
    if (!n) {
      console.warn("THREE.GLTFLoader: Missing camera parameters.");
      return;
    }
    return i.type === "perspective" ? t = new Zt(
      nt.radToDeg(n.yfov),
      n.aspectRatio || 1,
      n.znear || 1,
      n.zfar || 2e6
    ) : i.type === "orthographic" && (t = new Xn(
      -n.xmag,
      n.xmag,
      n.ymag,
      -n.ymag,
      n.znear,
      n.zfar
    )), i.name && (t.name = this.createUniqueName(i.name)), lc(t, i), Promise.resolve(t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#skins
   * @param {number} skinIndex
   * @return {Promise<Skeleton>}
   */
  loadSkin(e) {
    const t = this.json.skins[e], i = [];
    for (let n = 0, r = t.joints.length; n < r; n++)
      i.push(this._loadNodeShallow(t.joints[n]));
    return t.inverseBindMatrices !== void 0 ? i.push(this.getDependency("accessor", t.inverseBindMatrices)) : i.push(null), Promise.all(i).then(function(n) {
      const r = n.pop(), a = n, o = [], l = [];
      for (let c = 0, h = a.length; c < h; c++) {
        const u = a[c];
        if (u) {
          o.push(u);
          const d = new et();
          r !== null && d.fromArray(r.array, c * 16), l.push(d);
        } else
          console.warn('THREE.GLTFLoader: Joint "%s" could not be found.', t.joints[c]);
      }
      return new nh(o, l);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#animations
   * @param {number} animationIndex
   * @return {Promise<AnimationClip>}
   */
  loadAnimation(e) {
    const t = this.json, i = this, n = t.animations[e], r = n.name ? n.name : "animation_" + e, a = [], o = [], l = [], c = [], h = [];
    for (let u = 0, d = n.channels.length; u < d; u++) {
      const f = n.channels[u], p = n.samplers[f.sampler], v = f.target, g = v.node, m = n.parameters !== void 0 ? n.parameters[p.input] : p.input, x = n.parameters !== void 0 ? n.parameters[p.output] : p.output;
      v.node !== void 0 && (a.push(this.getDependency("node", g)), o.push(this.getDependency("accessor", m)), l.push(this.getDependency("accessor", x)), c.push(p), h.push(v));
    }
    return Promise.all([
      Promise.all(a),
      Promise.all(o),
      Promise.all(l),
      Promise.all(c),
      Promise.all(h)
    ]).then(function(u) {
      const d = u[0], f = u[1], p = u[2], v = u[3], g = u[4], m = [];
      for (let x = 0, y = d.length; x < y; x++) {
        const A = d[x], b = f[x], M = p[x], w = v[x], C = g[x];
        if (A === void 0)
          continue;
        A.updateMatrix && A.updateMatrix();
        const T = i._createAnimationTracks(
          A,
          b,
          M,
          w,
          C
        );
        if (T)
          for (let E = 0; E < T.length; E++)
            m.push(T[E]);
      }
      return new xl(r, void 0, m);
    });
  }
  createNodeMesh(e) {
    const t = this.json, i = this, n = t.nodes[e];
    return n.mesh === void 0 ? null : i.getDependency("mesh", n.mesh).then(function(r) {
      const a = i._getNodeRef(i.meshCache, n.mesh, r);
      return n.weights !== void 0 && a.traverse(function(o) {
        if (o.isMesh)
          for (let l = 0, c = n.weights.length; l < c; l++)
            o.morphTargetInfluences[l] = n.weights[l];
      }), a;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
   * @param {number} nodeIndex
   * @return {Promise<Object3D>}
   */
  loadNode(e) {
    const t = this.json, i = this, n = t.nodes[e], r = i._loadNodeShallow(e), a = [], o = n.children || [];
    for (let c = 0, h = o.length; c < h; c++)
      a.push(i.getDependency("node", o[c]));
    const l = n.skin === void 0 ? Promise.resolve(null) : i.getDependency("skin", n.skin);
    return Promise.all([r, Promise.all(a), l]).then(
      function(c) {
        const h = c[0], u = c[1], d = c[2];
        d !== null && h.traverse(function(f) {
          f.isSkinnedMesh && f.bind(d, ele);
        });
        for (let f = 0, p = u.length; f < p; f++)
          h.add(u[f]);
        return h;
      }
    );
  }
  // ._loadNodeShallow() parses a single node.
  // skin and child nodes are created and added in .loadNode() (no '_' prefix).
  _loadNodeShallow(e) {
    const t = this.json, i = this.extensions, n = this;
    if (this.nodeCache[e] !== void 0)
      return this.nodeCache[e];
    const r = t.nodes[e], a = r.name ? n.createUniqueName(r.name) : "", o = [], l = n._invokeOne(function(c) {
      return c.createNodeMesh && c.createNodeMesh(e);
    });
    return l && o.push(l), r.camera !== void 0 && o.push(
      n.getDependency("camera", r.camera).then(function(c) {
        return n._getNodeRef(n.cameraCache, r.camera, c);
      })
    ), n._invokeAll(function(c) {
      return c.createNodeAttachment && c.createNodeAttachment(e);
    }).forEach(function(c) {
      o.push(c);
    }), this.nodeCache[e] = Promise.all(o).then(function(c) {
      let h;
      if (r.isBone === !0 ? h = new ih() : c.length > 1 ? h = new ci() : c.length === 1 ? h = c[0] : h = new mt(), h !== c[0])
        for (let u = 0, d = c.length; u < d; u++)
          h.add(c[u]);
      if (r.name && (h.userData.name = r.name, h.name = a), lc(h, r), r.extensions && Rh(i, h, r), r.matrix !== void 0) {
        const u = new et();
        u.fromArray(r.matrix), h.applyMatrix4(u);
      } else
        r.translation !== void 0 && h.position.fromArray(r.translation), r.rotation !== void 0 && h.quaternion.fromArray(r.rotation), r.scale !== void 0 && h.scale.fromArray(r.scale);
      return n.associations.has(h) || n.associations.set(h, {}), n.associations.get(h).nodes = e, h;
    }), this.nodeCache[e];
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
   * @param {number} sceneIndex
   * @return {Promise<Group>}
   */
  loadScene(e) {
    const t = this.extensions, i = this.json.scenes[e], n = this, r = new ci();
    i.name && (r.name = n.createUniqueName(i.name)), lc(r, i), i.extensions && Rh(t, r, i);
    const a = i.nodes || [], o = [];
    for (let l = 0, c = a.length; l < c; l++)
      o.push(n.getDependency("node", a[l]));
    return Promise.all(o).then(function(l) {
      for (let h = 0, u = l.length; h < u; h++)
        r.add(l[h]);
      const c = (h) => {
        const u = /* @__PURE__ */ new Map();
        for (const [d, f] of n.associations)
          (d instanceof Cn || d instanceof Ut) && u.set(d, f);
        return h.traverse((d) => {
          const f = n.associations.get(d);
          f != null && u.set(d, f);
        }), u;
      };
      return n.associations = c(r), r;
    });
  }
  _createAnimationTracks(e, t, i, n, r) {
    const a = [], o = e.name ? e.name : e.uuid, l = [];
    jl[r.path] === jl.weights ? e.traverse(function(d) {
      d.morphTargetInfluences && l.push(d.name ? d.name : d.uuid);
    }) : l.push(o);
    let c;
    switch (jl[r.path]) {
      case jl.weights:
        c = uo;
        break;
      case jl.rotation:
        c = fo;
        break;
      case jl.position:
      case jl.scale:
        c = po;
        break;
      default:
        switch (i.itemSize) {
          case 1:
            c = uo;
            break;
          case 2:
          case 3:
          default:
            c = po;
            break;
        }
        break;
    }
    const h = n.interpolation !== void 0 ? Y0e[n.interpolation] : l0, u = this._getArrayFromAccessor(i);
    for (let d = 0, f = l.length; d < f; d++) {
      const p = new c(
        l[d] + "." + jl[r.path],
        t.array,
        u,
        h
      );
      n.interpolation === "CUBICSPLINE" && this._createCubicSplineTrackInterpolant(p), a.push(p);
    }
    return a;
  }
  _getArrayFromAccessor(e) {
    let t = e.array;
    if (e.normalized) {
      const i = Vb(t.constructor), n = new Float32Array(t.length);
      for (let r = 0, a = t.length; r < a; r++)
        n[r] = t[r] * i;
      t = n;
    }
    return t;
  }
  _createCubicSplineTrackInterpolant(e) {
    e.createInterpolant = function(i) {
      const n = this instanceof fo ? q0e : IB;
      return new n(this.times, this.values, this.getValueSize() / 3, i);
    }, e.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline = !0;
  }
};
function ile(s, e, t) {
  const i = e.attributes, n = new zt();
  if (i.POSITION !== void 0) {
    const o = t.json.accessors[i.POSITION], l = o.min, c = o.max;
    if (l !== void 0 && c !== void 0) {
      if (n.set(new _(l[0], l[1], l[2]), new _(c[0], c[1], c[2])), o.normalized) {
        const h = Vb(Xf[o.componentType]);
        n.min.multiplyScalar(h), n.max.multiplyScalar(h);
      }
    } else {
      console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      return;
    }
  } else
    return;
  const r = e.targets;
  if (r !== void 0) {
    const o = new _(), l = new _();
    for (let c = 0, h = r.length; c < h; c++) {
      const u = r[c];
      if (u.POSITION !== void 0) {
        const d = t.json.accessors[u.POSITION], f = d.min, p = d.max;
        if (f !== void 0 && p !== void 0) {
          if (l.setX(Math.max(Math.abs(f[0]), Math.abs(p[0]))), l.setY(Math.max(Math.abs(f[1]), Math.abs(p[1]))), l.setZ(Math.max(Math.abs(f[2]), Math.abs(p[2]))), d.normalized) {
            const v = Vb(
              Xf[d.componentType]
            );
            l.multiplyScalar(v);
          }
          o.max(l);
        } else
          console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      }
    }
    n.expandByVector(o);
  }
  s.boundingBox = n;
  const a = new Tn();
  n.getCenter(a.center), a.radius = n.min.distanceTo(n.max) / 2, s.boundingSphere = a;
}
function eP(s, e, t) {
  const i = e.attributes, n = [];
  function r(a, o) {
    return t.getDependency("accessor", a).then(function(l) {
      s.setAttribute(o, l);
    });
  }
  for (const a in i) {
    const o = Hb[a] || a.toLowerCase();
    o in s.attributes || n.push(r(i[a], o));
  }
  if (e.indices !== void 0 && !s.index) {
    const a = t.getDependency("accessor", e.indices).then(function(o) {
      s.setIndex(o);
    });
    n.push(a);
  }
  return bi.workingColorSpace !== Si && "COLOR_0" in i && console.warn(
    `THREE.GLTFLoader: Converting vertex colors from "srgb-linear" to "${bi.workingColorSpace}" not supported.`
  ), lc(s, e), ile(s, e, t), Promise.all(n).then(function() {
    return e.targets !== void 0 ? K0e(s, e.targets, t) : s;
  });
}
function Wb(s, e) {
  s.userData.source = e;
}
function nle(s, e) {
  s.userData.source = e;
}
function sle(s, e) {
  s.userData.source = e, s.source || (s.source = {}), s.source.indexId = e.indexId;
}
function rle(s, e) {
  s.userData.source = e, s.source.indexId = e.indexId;
}
function ale(s, e, t) {
  const i = s.associations.get(e);
  s.associations.set(t, i);
}
function p0(s) {
  return s.isMesh || s.isSkinnedMesh || s.isInstancedMesh || Bg(s);
}
function Bg(s) {
  return s.isTwinMesh || s.isTwinSkinnedMesh || s.isTwinInstancedMesh;
}
function ole(s, e) {
  s.forEach((t) => {
    t.userData.source.url = e, Bg(t) && (t.source.url = e);
  });
}
function lle(s) {
  return s.isBufferGeometry;
}
function OB(s, e) {
  const t = e === void 0 || typeof e == "string", i = {};
  t && (e = {
    geometries: {},
    materials: {},
    textures: {},
    images: {},
    shapes: {},
    skeletons: {},
    animations: {},
    nodes: {}
  }, i.metadata = {
    version: 4.6,
    type: "Object",
    generator: "Object3D.toJSON"
  });
  const n = {};
  if (n.uuid = s.uuid, n.type = s.type, s.name !== "" && (n.name = s.name), s.castShadow === !0 && (n.castShadow = !0), s.receiveShadow === !0 && (n.receiveShadow = !0), s.visible === !1 && (n.visible = !1), s.frustumCulled === !1 && (n.frustumCulled = !1), s.renderOrder !== 0 && (n.renderOrder = s.renderOrder), Object.keys(s.userData).length > 0) {
    n.userData = {};
    for (const o in s.userData)
      o !== Ya && (n.userData[o] = s.userData[o]);
  }
  n.layers = s.layers.mask, n.matrix = s.matrix.toArray(), n.matrix = s.matrix.toArray(), n.position = s.position.toArray(), n.rotation = s.rotation.toArray(), n.quaternion = s.quaternion.toArray(), n.scale = s.scale.toArray(), n.up = s.up.toArray(), s.matrixAutoUpdate === !1 && (n.matrixAutoUpdate = !1), s.isInstancedMesh && (n.type = "InstancedMesh", n.count = s.count, n.instanceMatrix = s.instanceMatrix.toJSON(), s.instanceColor !== null && (n.instanceColor = s.instanceColor.toJSON()), cle(s, n)), s.isBatchedMesh && (n.type = "BatchedMesh", n.perObjectFrustumCulled = s.perObjectFrustumCulled, n.drawRanges = s._drawRanges, n.reservedRanges = s._reservedRanges, n.visible = s._visible, n.active = s._active, n.bounds = s._bounds.map((o) => ({
    boxInitialized: o.boxInitialized,
    boxMin: o.box.min.toArray(),
    boxMax: o.box.max.toArray(),
    sphereInitialized: o.sphereInitialized,
    sphereRadius: o.sphere.radius,
    sphereCenter: o.sphere.center.toArray()
  })), n.maxGeometryCount = s._maxGeometryCount, n.maxVertexCount = s._maxVertexCount, n.maxIndexCount = s._maxIndexCount, n.geometryInitialized = s._geometryInitialized, n.geometryCount = s._geometryCount, n.matricesTexture = s._matricesTexture.toJSON(e), s.boundingSphere !== null && (n.boundingSphere = {
    center: n.boundingSphere.center.toArray(),
    radius: n.boundingSphere.radius
  }), s.boundingBox !== null && (n.boundingBox = {
    min: n.boundingBox.min.toArray(),
    max: n.boundingBox.max.toArray()
  }));
  function r(o, l) {
    return o[l.uuid] === void 0 && (lle(l) || M0e(l) ? o[l.uuid] = {
      uuid: l.uuid,
      type: l.type
    } : o[l.uuid] = l.toJSON(e)), l.uuid;
  }
  if (s.isScene)
    s.background && (s.background.isColor ? n.background = s.background.toJSON() : s.background.isTexture && (n.background = s.background.toJSON(e).uuid)), s.environment && s.environment.isTexture && s.environment.isRenderTargetTexture !== !0 && (n.environment = s.environment.toJSON(e).uuid);
  else if (s.isMesh || s.isLine || s.isPoints) {
    n.geometry = r(e.geometries, s.geometry);
    const o = s.geometry.parameters;
    if (o !== void 0 && o.shapes !== void 0) {
      const l = o.shapes;
      if (Array.isArray(l))
        for (let c = 0, h = l.length; c < h; c++) {
          const u = l[c];
          r(e.shapes, u);
        }
      else
        r(e.shapes, l);
    }
    hle(s, n);
  }
  if (s.isSkinnedMesh && (n.bindMode = s.bindMode, n.bindMatrix = s.bindMatrix.toArray(), s.skeleton !== void 0 && (r(e.skeletons, s.skeleton), n.skeleton = s.skeleton.uuid), ule(s, n)), s.material !== void 0)
    if (Array.isArray(s.material)) {
      const o = [];
      for (let l = 0, c = s.material.length; l < c; l++)
        o.push(r(e.materials, s.material[l]));
      n.material = o;
    } else
      n.material = r(e.materials, s.material);
  if (s.children.length > 0) {
    n.children = [];
    for (let o = 0; o < s.children.length; o++)
      s.children[o].userData.skipSerialization || n.children.push(OB(s.children[o], e).object);
  }
  if (s.animations.length > 0) {
    n.animations = [];
    for (let o = 0; o < s.animations.length; o++) {
      const l = s.animations[o];
      n.animations.push(r(e.animations, l));
    }
  }
  if (t) {
    const o = a(e.geometries), l = a(e.materials), c = a(e.textures), h = a(e.images), u = a(e.shapes), d = a(e.skeletons), f = a(e.animations), p = a(e.nodes);
    o.length > 0 && (i.geometries = o), l.length > 0 && (i.materials = l), c.length > 0 && (i.textures = c), h.length > 0 && (i.images = h), u.length > 0 && (i.shapes = u), d.length > 0 && (i.skeletons = d), f.length > 0 && (i.animations = f), p.length > 0 && (i.nodes = p);
  }
  return i.object = n, i;
  function a(o) {
    const l = [];
    for (const c in o) {
      const h = o[c];
      delete h.metadata, l.push(h);
    }
    return l;
  }
}
function cle(s, e) {
  s.extendType === "TwinInstancedMesh" && (e.source = s.source.toJSON(), e.extendType = "TwinInstancedMesh");
}
function hle(s, e) {
  s.extendType === "TwinMesh" && (e.source = s.source.toJSON(), e.extendType = "TwinMesh");
}
function ule(s, e) {
  s.extendType === "TwinSkinnedMesh" && (e.source = s.source.toJSON(), e.extendType = "TwinSkinnedMesh");
}
function Xb(s, e, t, i, n) {
  let r;
  e = e ?? {}, t = t ?? {}, i = i ?? {}, n = n ?? {};
  function a(u) {
    return e[u] === void 0 && console.warn("THREE.ObjectLoader: Undefined geometry", u), e[u];
  }
  function o(u) {
    if (u !== void 0) {
      if (Array.isArray(u)) {
        const d = [];
        for (let f = 0, p = u.length; f < p; f++) {
          const v = u[f];
          t[v] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", v), d.push(t[v]);
        }
        return d;
      }
      return t[u] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", u), t[u];
    }
  }
  function l(u) {
    return i[u] === void 0 && console.warn("THREE.ObjectLoader: Undefined texture", u), i[u];
  }
  let c, h;
  switch (s.type) {
    case "Scene":
      r = new Da(), s.background !== void 0 && (Number.isInteger(s.background) ? r.background = new _e(s.background) : r.background = l(s.background)), s.environment !== void 0 && (r.environment = l(s.environment)), s.fog !== void 0 && (s.fog.type === "Fog" ? r.fog = new Wu(s.fog.color, s.fog.near, s.fog.far) : s.fog.type === "FogExp2" && (r.fog = new Xp(s.fog.color, s.fog.density)), s.fog.name !== "" && (r.fog.name = s.fog.name)), s.backgroundBlurriness !== void 0 && (r.backgroundBlurriness = s.backgroundBlurriness), s.backgroundIntensity !== void 0 && (r.backgroundIntensity = s.backgroundIntensity);
      break;
    case "PerspectiveCamera":
      r = new Zt(s.fov, s.aspect, s.near, s.far), s.focus !== void 0 && (r.focus = s.focus), s.zoom !== void 0 && (r.zoom = s.zoom), s.filmGauge !== void 0 && (r.filmGauge = s.filmGauge), s.filmOffset !== void 0 && (r.filmOffset = s.filmOffset), s.view !== void 0 && (r.view = Object.assign({}, s.view));
      break;
    case "OrthographicCamera":
      r = new Xn(
        s.left,
        s.right,
        s.top,
        s.bottom,
        s.near,
        s.far
      ), s.zoom !== void 0 && (r.zoom = s.zoom), s.view !== void 0 && (r.view = Object.assign({}, s.view));
      break;
    case "AmbientLight":
      r = new Gc(s.color, s.intensity);
      break;
    case "DirectionalLight":
      r = new _a(s.color, s.intensity);
      break;
    case "PointLight":
      r = new Tl(s.color, s.intensity, s.distance, s.decay);
      break;
    case "RectAreaLight":
      r = new y2(s.color, s.intensity, s.width, s.height);
      break;
    case "SpotLight":
      r = new oh(
        s.color,
        s.intensity,
        s.distance,
        s.angle,
        s.penumbra,
        s.decay
      );
      break;
    case "HemisphereLight":
      r = new v2(s.color, s.groundColor, s.intensity);
      break;
    case "LightProbe":
      r = new D4().fromJSON(s);
      break;
    case "SkinnedMesh":
      c = a(s.geometry), h = o(s.material), r = dle(s, r, c, h), s.bindMode !== void 0 && (r.bindMode = s.bindMode), s.bindMatrix !== void 0 && r.bindMatrix.fromArray(s.bindMatrix), s.skeleton !== void 0 && (r.skeleton = s.skeleton);
      break;
    case "Mesh":
      c = a(s.geometry), h = o(s.material), r = ple(s, r, c, h);
      break;
    case "InstancedMesh":
      c = a(s.geometry), h = o(s.material);
      const u = s.count, d = s.instanceMatrix, f = s.instanceColor;
      r = fle(s, r, c, h, u), r.instanceMatrix = new hs(
        new Float32Array(d.array),
        16
      ), f !== void 0 && (r.instanceColor = new hs(
        new Float32Array(f.array),
        f.itemSize
      ));
      break;
    case "BatchedMesh":
      r = new mt();
      break;
    case "LOD":
      r = new E4();
      break;
    case "Line":
      r = new ti(a(s.geometry), o(s.material));
      break;
    case "LineLoop":
      r = new ju(a(s.geometry), o(s.material));
      break;
    case "LineSegments":
      r = new _n(a(s.geometry), o(s.material));
      break;
    case "PointCloud":
    case "Points":
      r = new sh(a(s.geometry), o(s.material));
      break;
    case "Sprite":
      r = new wn(o(s.material));
      break;
    case "Group":
      r = new ci();
      break;
    case "Bone":
      r = new ih();
      break;
    default:
      r = new mt();
  }
  if (r.uuid = s.uuid, s.name !== void 0 && (r.name = s.name), s.matrix !== void 0 ? (r.matrix.fromArray(s.matrix), s.matrixAutoUpdate !== void 0 && (r.matrixAutoUpdate = s.matrixAutoUpdate), r.matrixAutoUpdate && r.matrix.decompose(r.position, r.quaternion, r.scale)) : (s.position !== void 0 && r.position.fromArray(s.position), s.rotation !== void 0 && r.rotation.fromArray(s.rotation), s.quaternion !== void 0 && r.quaternion.fromArray(s.quaternion), s.scale !== void 0 && r.scale.fromArray(s.scale)), s.up !== void 0 && r.up.fromArray(s.up), s.castShadow !== void 0 && (r.castShadow = s.castShadow), s.receiveShadow !== void 0 && (r.receiveShadow = s.receiveShadow), s.shadow && (s.shadow.bias !== void 0 && (r.shadow.bias = s.shadow.bias), s.shadow.normalBias !== void 0 && (r.shadow.normalBias = s.shadow.normalBias), s.shadow.radius !== void 0 && (r.shadow.radius = s.shadow.radius), s.shadow.mapSize !== void 0 && r.shadow.mapSize.fromArray(s.shadow.mapSize), s.shadow.camera !== void 0 && (r.shadow.camera = Xb(
    s.shadow.camera,
    e,
    t,
    i,
    n
  ))), s.visible !== void 0 && (r.visible = s.visible), s.frustumCulled !== void 0 && (r.frustumCulled = s.frustumCulled), s.renderOrder !== void 0 && (r.renderOrder = s.renderOrder), s.userData !== void 0 && (r.userData = { ...s.userData }, delete r.userData["twin-wireframe"]), s.layers !== void 0 && (r.layers.mask = s.layers), s.children !== void 0) {
    const u = s.children;
    for (let d = 0; d < u.length; d++)
      r.add(Xb(u[d], e, t, i, n));
  }
  if (s.animations !== void 0) {
    const u = s.animations;
    for (let d = 0; d < u.length; d++) {
      const f = u[d];
      r.animations.push(n[f]);
    }
  }
  if (s.type === "LOD") {
    s.autoUpdate !== void 0 && (r.autoUpdate = s.autoUpdate);
    const u = s.levels;
    for (let d = 0; d < u.length; d++) {
      const f = u[d], p = r.getObjectByProperty("uuid", f.object);
      p !== void 0 && r.addLevel(p, f.distance, f.hysteresis);
    }
  }
  return r;
}
function dle(s, e, t, i) {
  return e = new fw(t, i), s.extendType === "TwinSkinnedMesh" ? e.source.fromJSON(s.source) : (e.source.url = s.userData.source.url, e.source.indexId = s.userData.source.indexId), e;
}
function fle(s, e, t, i, n) {
  return e = new dw(t, i, n), s.extendType === "TwinInstancedMesh" ? e.source.fromJSON(s.source) : (e.source.url = s.userData.source.url, e.source.indexId = s.userData.source.indexId), e;
}
function ple(s, e, t, i) {
  return e = new uw(t, i), s.extendType === "TwinMesh" ? e.source.fromJSON(s.source) : (e.source.url = s.userData.source.url, e.source.indexId = s.userData.source.indexId), e;
}
function Sx(s) {
  const e = /* @__PURE__ */ new Map(), t = /* @__PURE__ */ new Map(), i = s.clone();
  return NB(s, i, function(n, r) {
    e.set(r, n), t.set(n, r);
  }), i.traverse(function(n) {
    if (!n.isSkinnedMesh)
      return;
    const r = n, a = e.get(n), o = a.skeleton.bones;
    r.skeleton = a.skeleton.clone(), r.bindMatrix.copy(a.bindMatrix), r.skeleton.bones = o.map(function(l) {
      return t.get(l);
    }), r.bind(r.skeleton, r.bindMatrix);
  }), i;
}
function NB(s, e, t) {
  t(s, e);
  for (let i = 0; i < s.children.length; i++)
    NB(s.children[i], e.children[i], t);
}
function wx(s, e) {
  let t = "-1";
  if (s.userData && (t = `${s.userData.indexId}`), t === e)
    return s;
  for (let i = 0, n = s.children.length; i < n; i++) {
    const r = s.children[i], a = wx(r, e);
    if (a !== void 0)
      return a;
  }
}
function mle(s) {
  return Sx(s);
}
function BB(s) {
  const e = mle(s), t = /* @__PURE__ */ new Map(), i = /* @__PURE__ */ new Map(), n = /* @__PURE__ */ new Map();
  return e.traverse((r) => {
    if (p0(r))
      if (Array.isArray(r.material)) {
        const a = [];
        r.material.forEach((o) => {
          if (i.has(o.uuid))
            a.push(i.get(o.uuid));
          else {
            const l = Gb(o, /* @__PURE__ */ new Map());
            i.set(o.uuid, l), a.push(l);
          }
        }), r.material = a;
      } else {
        if (!i.has(r.material.uuid)) {
          const a = Gb(r.material, /* @__PURE__ */ new Map());
          i.set(r.material.uuid, a);
        }
        r.material = i.get(r.material.uuid), n.set(r.material.uuid, [
          ...n.get(r.material.uuid) || [],
          r
        ]);
      }
  }), {
    scene: e,
    textures: [...t.values()],
    materials: [...i.values()],
    meshSetMap: n
  };
}
class gle extends As {
  constructor(e) {
    super(e), this.dracoLoader = null, this.ktx2Loader = null, this.meshoptDecoder = null, this.pluginCallbacks = [], this.register(function(t) {
      return new ble(t);
    }), this.register(function(t) {
      return new Ple(t);
    }), this.register(function(t) {
      return new Rle(t);
    }), this.register(function(t) {
      return new Dle(t);
    }), this.register(function(t) {
      return new Sle(t);
    }), this.register(function(t) {
      return new wle(t);
    }), this.register(function(t) {
      return new Ele(t);
    }), this.register(function(t) {
      return new Tle(t);
    }), this.register(function(t) {
      return new Ale(t);
    }), this.register(function(t) {
      return new Cle(t);
    }), this.register(function(t) {
      return new Mle(t);
    }), this.register(function(t) {
      return new _le(t);
    }), this.register(function(t) {
      return new yle(t);
    }), this.register(function(t) {
      return new Lle(t);
    }), this.register(function(t) {
      return new Ile(t);
    });
  }
  load(e, t, i, n) {
    const r = this;
    let a;
    if (this.resourcePath !== "")
      a = this.resourcePath;
    else if (this.path !== "") {
      const c = Rs.extractUrlBase(e);
      a = Rs.resolveURL(c, this.path);
    } else
      a = Rs.extractUrlBase(e);
    this.manager.itemStart(e);
    const o = function(c) {
      n ? n(c) : console.error(c), r.manager.itemError(e), r.manager.itemEnd(e);
    }, l = new Nn(this.manager);
    l.setPath(this.path), l.setResponseType("arraybuffer"), l.setRequestHeader(this.requestHeader), l.setWithCredentials(this.withCredentials), l.load(
      e,
      function(c) {
        try {
          r.parse(
            c,
            a,
            function(h) {
              t(h), r.manager.itemEnd(e);
            },
            o
          );
        } catch (h) {
          o(h);
        }
      },
      i,
      o
    );
  }
  setDRACOLoader(e) {
    return this.dracoLoader = e, this;
  }
  setDDSLoader() {
    throw new Error(
      'THREE.GLTFLoader: "MSFT_texture_dds" no longer supported. Please update to "KHR_texture_basisu".'
    );
  }
  setKTX2Loader(e) {
    return this.ktx2Loader = e, this;
  }
  setMeshoptDecoder(e) {
    return this.meshoptDecoder = e, this;
  }
  register(e) {
    return this.pluginCallbacks.indexOf(e) === -1 && this.pluginCallbacks.push(e), this;
  }
  unregister(e) {
    return this.pluginCallbacks.indexOf(e) !== -1 && this.pluginCallbacks.splice(this.pluginCallbacks.indexOf(e), 1), this;
  }
  parse(e, t, i, n) {
    let r;
    const a = {}, o = {}, l = new TextDecoder();
    if (typeof e == "string")
      r = JSON.parse(e);
    else if (e instanceof ArrayBuffer)
      if (l.decode(new Uint8Array(e, 0, 4)) === FB) {
        try {
          a[Oi.KHR_BINARY_GLTF] = new Ole(e);
        } catch (u) {
          n && n(u);
          return;
        }
        r = JSON.parse(a[Oi.KHR_BINARY_GLTF].content);
      } else
        r = JSON.parse(l.decode(e));
    else
      r = e;
    if (r.asset === void 0 || r.asset.version[0] < 2) {
      n && n(
        new Error("THREE.GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported.")
      );
      return;
    }
    const c = new zB(r, {
      path: t || this.resourcePath || "",
      crossOrigin: this.crossOrigin,
      requestHeader: this.requestHeader,
      manager: this.manager,
      ktx2Loader: this.ktx2Loader,
      meshoptDecoder: this.meshoptDecoder
    });
    c.fileLoader.setRequestHeader(this.requestHeader);
    for (let h = 0; h < this.pluginCallbacks.length; h++) {
      const u = this.pluginCallbacks[h](c);
      u.name || console.error("THREE.GLTFLoader: Invalid plugin found: missing name"), o[u.name] = u, a[u.name] = !0;
    }
    if (r.extensionsUsed)
      for (let h = 0; h < r.extensionsUsed.length; ++h) {
        const u = r.extensionsUsed[h], d = r.extensionsRequired || [];
        switch (u) {
          case Oi.KHR_MATERIALS_UNLIT:
            a[u] = new xle();
            break;
          case Oi.KHR_DRACO_MESH_COMPRESSION:
            a[u] = new Nle(
              r,
              this.dracoLoader
            );
            break;
          case Oi.KHR_TEXTURE_TRANSFORM:
            a[u] = new Ble();
            break;
          case Oi.KHR_MESH_QUANTIZATION:
            a[u] = new Fle();
            break;
          default:
            d.indexOf(u) >= 0 && o[u] === void 0 && console.warn('THREE.GLTFLoader: Unknown extension "' + u + '".');
        }
      }
    c.setExtensions(a), c.setPlugins(o), c.parse(i, n);
  }
  parseAsync(e, t) {
    const i = this;
    return new Promise(function(n, r) {
      i.parse(e, t, n, r);
    });
  }
}
function vle() {
  let s = {};
  return {
    get: function(e) {
      return s[e];
    },
    add: function(e, t) {
      s[e] = t;
    },
    remove: function(e) {
      delete s[e];
    },
    removeAll: function() {
      s = {};
    }
  };
}
const Oi = {
  KHR_BINARY_GLTF: "KHR_binary_glTF",
  KHR_DRACO_MESH_COMPRESSION: "KHR_draco_mesh_compression",
  KHR_LIGHTS_PUNCTUAL: "KHR_lights_punctual",
  KHR_MATERIALS_CLEARCOAT: "KHR_materials_clearcoat",
  KHR_MATERIALS_IOR: "KHR_materials_ior",
  KHR_MATERIALS_SHEEN: "KHR_materials_sheen",
  KHR_MATERIALS_SPECULAR: "KHR_materials_specular",
  KHR_MATERIALS_TRANSMISSION: "KHR_materials_transmission",
  KHR_MATERIALS_IRIDESCENCE: "KHR_materials_iridescence",
  KHR_MATERIALS_ANISOTROPY: "KHR_materials_anisotropy",
  KHR_MATERIALS_UNLIT: "KHR_materials_unlit",
  KHR_MATERIALS_VOLUME: "KHR_materials_volume",
  KHR_TEXTURE_BASISU: "KHR_texture_basisu",
  KHR_TEXTURE_TRANSFORM: "KHR_texture_transform",
  KHR_MESH_QUANTIZATION: "KHR_mesh_quantization",
  KHR_MATERIALS_EMISSIVE_STRENGTH: "KHR_materials_emissive_strength",
  EXT_TEXTURE_WEBP: "EXT_texture_webp",
  EXT_TEXTURE_AVIF: "EXT_texture_avif",
  EXT_MESHOPT_COMPRESSION: "EXT_meshopt_compression",
  EXT_MESH_GPU_INSTANCING: "EXT_mesh_gpu_instancing"
};
class yle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_LIGHTS_PUNCTUAL, this.cache = { refs: {}, uses: {} };
  }
  _markDefs() {
    const e = this.parser, t = this.parser.json.nodes || [];
    for (let i = 0, n = t.length; i < n; i++) {
      const r = t[i];
      r.extensions && r.extensions[this.name] && r.extensions[this.name].light !== void 0 && e._addNodeRef(this.cache, r.extensions[this.name].light);
    }
  }
  _loadLight(e) {
    const t = this.parser, i = "light:" + e;
    let n = t.cache.get(i);
    if (n)
      return n;
    const r = t.json, l = ((r.extensions && r.extensions[this.name] || {}).lights || [])[e];
    let c;
    const h = new _e(16777215);
    l.color !== void 0 && h.setRGB(l.color[0], l.color[1], l.color[2], Si);
    const u = l.range !== void 0 ? l.range : 0;
    switch (l.type) {
      case "directional":
        c = new _a(h), c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      case "point":
        c = new Tl(h), c.distance = u;
        break;
      case "spot":
        c = new oh(h), c.distance = u, l.spot = l.spot || {}, l.spot.innerConeAngle = l.spot.innerConeAngle !== void 0 ? l.spot.innerConeAngle : 0, l.spot.outerConeAngle = l.spot.outerConeAngle !== void 0 ? l.spot.outerConeAngle : Math.PI / 4, c.angle = l.spot.outerConeAngle, c.penumbra = 1 - l.spot.innerConeAngle / l.spot.outerConeAngle, c.target.position.set(0, 0, -1), c.add(c.target);
        break;
      default:
        throw new Error("THREE.GLTFLoader: Unexpected light type: " + l.type);
    }
    return c.position.set(0, 0, 0), c.decay = 2, cc(c, l), l.intensity !== void 0 && (c.intensity = l.intensity), c.name = t.createUniqueName(l.name || "light_" + e), n = Promise.resolve(c), t.cache.add(i, n), n;
  }
  getDependency(e, t) {
    if (e === "light")
      return this._loadLight(t);
  }
  createNodeAttachment(e) {
    const t = this, i = this.parser, r = i.json.nodes[e], o = (r.extensions && r.extensions[this.name] || {}).light;
    return o === void 0 ? null : this._loadLight(o).then(function(l) {
      return i._getNodeRef(t.cache, o, l);
    });
  }
}
class xle {
  constructor() {
    this.name = Oi.KHR_MATERIALS_UNLIT;
  }
  getMaterialType() {
    return kt;
  }
  extendParams(e, t, i) {
    const n = [];
    e.color = new _e(1, 1, 1), e.opacity = 1;
    const r = t.pbrMetallicRoughness;
    if (r) {
      if (Array.isArray(r.baseColorFactor)) {
        const a = r.baseColorFactor;
        e.color.setRGB(a[0], a[1], a[2], Si), e.opacity = a[3];
      }
      r.baseColorTexture !== void 0 && n.push(
        i.assignTexture(
          e,
          "map",
          r.baseColorTexture,
          qi
        )
      );
    }
    return Promise.all(n);
  }
}
class Ale {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_EMISSIVE_STRENGTH;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name].emissiveStrength;
    return r !== void 0 && (t.emissiveIntensity = r), Promise.resolve();
  }
}
class ble {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_CLEARCOAT;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    if (a.clearcoatFactor !== void 0 && (t.clearcoat = a.clearcoatFactor), a.clearcoatTexture !== void 0 && r.push(
      i.assignTexture(t, "clearcoatMap", a.clearcoatTexture)
    ), a.clearcoatRoughnessFactor !== void 0 && (t.clearcoatRoughness = a.clearcoatRoughnessFactor), a.clearcoatRoughnessTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "clearcoatRoughnessMap",
        a.clearcoatRoughnessTexture
      )
    ), a.clearcoatNormalTexture !== void 0 && (r.push(
      i.assignTexture(
        t,
        "clearcoatNormalMap",
        a.clearcoatNormalTexture
      )
    ), a.clearcoatNormalTexture.scale !== void 0)) {
      const o = a.clearcoatNormalTexture.scale;
      t.clearcoatNormalScale = new Se(o, o);
    }
    return Promise.all(r);
  }
}
class Mle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_IRIDESCENCE;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.iridescenceFactor !== void 0 && (t.iridescence = a.iridescenceFactor), a.iridescenceTexture !== void 0 && r.push(
      i.assignTexture(t, "iridescenceMap", a.iridescenceTexture)
    ), a.iridescenceIor !== void 0 && (t.iridescenceIOR = a.iridescenceIor), t.iridescenceThicknessRange === void 0 && (t.iridescenceThicknessRange = [100, 400]), a.iridescenceThicknessMinimum !== void 0 && (t.iridescenceThicknessRange[0] = a.iridescenceThicknessMinimum), a.iridescenceThicknessMaximum !== void 0 && (t.iridescenceThicknessRange[1] = a.iridescenceThicknessMaximum), a.iridescenceThicknessTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "iridescenceThicknessMap",
        a.iridescenceThicknessTexture
      )
    ), Promise.all(r);
  }
}
class Sle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_SHEEN;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [];
    t.sheenColor = new _e(0, 0, 0), t.sheenRoughness = 0, t.sheen = 1;
    const a = n.extensions[this.name];
    if (a.sheenColorFactor !== void 0) {
      const o = a.sheenColorFactor;
      t.sheenColor.setRGB(
        o[0],
        o[1],
        o[2],
        Si
      );
    }
    return a.sheenRoughnessFactor !== void 0 && (t.sheenRoughness = a.sheenRoughnessFactor), a.sheenColorTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "sheenColorMap",
        a.sheenColorTexture,
        qi
      )
    ), a.sheenRoughnessTexture !== void 0 && r.push(
      i.assignTexture(t, "sheenRoughnessMap", a.sheenRoughnessTexture)
    ), Promise.all(r);
  }
}
class wle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_TRANSMISSION;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.transmissionFactor !== void 0 && (t.transmission = a.transmissionFactor), a.transmissionTexture !== void 0 && r.push(
      i.assignTexture(t, "transmissionMap", a.transmissionTexture)
    ), Promise.all(r);
  }
}
class Ele {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_VOLUME;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.thickness = a.thicknessFactor !== void 0 ? a.thicknessFactor : 0, a.thicknessTexture !== void 0 && r.push(
      i.assignTexture(t, "thicknessMap", a.thicknessTexture)
    ), t.attenuationDistance = a.attenuationDistance || 1 / 0;
    const o = a.attenuationColor || [1, 1, 1];
    return t.attenuationColor = new _e().setRGB(
      o[0],
      o[1],
      o[2],
      Si
    ), Promise.all(r);
  }
}
class Tle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_IOR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const n = this.parser.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = n.extensions[this.name];
    return t.ior = r.ior !== void 0 ? r.ior : 1.5, Promise.resolve();
  }
}
class Cle {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_SPECULAR;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    t.specularIntensity = a.specularFactor !== void 0 ? a.specularFactor : 1, a.specularTexture !== void 0 && r.push(
      i.assignTexture(t, "specularIntensityMap", a.specularTexture)
    );
    const o = a.specularColorFactor || [1, 1, 1];
    return t.specularColor = new _e().setRGB(
      o[0],
      o[1],
      o[2],
      Si
    ), a.specularColorTexture !== void 0 && r.push(
      i.assignTexture(
        t,
        "specularColorMap",
        a.specularColorTexture,
        qi
      )
    ), Promise.all(r);
  }
}
class _le {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_MATERIALS_ANISOTROPY;
  }
  getMaterialType(e) {
    const i = this.parser.json.materials[e];
    return !i.extensions || !i.extensions[this.name] ? null : nn;
  }
  extendMaterialParams(e, t) {
    const i = this.parser, n = i.json.materials[e];
    if (!n.extensions || !n.extensions[this.name])
      return Promise.resolve();
    const r = [], a = n.extensions[this.name];
    return a.anisotropyStrength !== void 0 && (t.anisotropy = a.anisotropyStrength), a.anisotropyRotation !== void 0 && (t.anisotropyRotation = a.anisotropyRotation), a.anisotropyTexture !== void 0 && r.push(
      i.assignTexture(t, "anisotropyMap", a.anisotropyTexture)
    ), Promise.all(r);
  }
}
class Ple {
  constructor(e) {
    this.parser = e, this.name = Oi.KHR_TEXTURE_BASISU;
  }
  loadTexture(e) {
    const t = this.parser, i = t.json, n = i.textures[e];
    if (!n.extensions || !n.extensions[this.name])
      return null;
    const r = n.extensions[this.name], a = t.options.ktx2Loader;
    if (!a) {
      if (i.extensionsRequired && i.extensionsRequired.indexOf(this.name) >= 0)
        throw new Error(
          "THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures"
        );
      return null;
    }
    return t.loadTextureImage(e, r.source, a);
  }
}
class Rle {
  constructor(e) {
    this.parser = e, this.name = Oi.EXT_TEXTURE_WEBP, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: WebP required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}
class Dle {
  constructor(e) {
    this.parser = e, this.name = Oi.EXT_TEXTURE_AVIF, this.isSupported = null;
  }
  loadTexture(e) {
    const t = this.name, i = this.parser, n = i.json, r = n.textures[e];
    if (!r.extensions || !r.extensions[t])
      return null;
    const a = r.extensions[t], o = n.images[a.source];
    let l = i.textureLoader;
    if (o.uri) {
      const c = i.options.manager.getHandler(o.uri);
      c !== null && (l = c);
    }
    return this.detectSupport().then(function(c) {
      if (c)
        return i.loadTextureImage(e, a.source, l);
      if (n.extensionsRequired && n.extensionsRequired.indexOf(t) >= 0)
        throw new Error("THREE.GLTFLoader: AVIF required by asset but unsupported.");
      return i.loadTexture(e);
    });
  }
  detectSupport() {
    return this.isSupported || (this.isSupported = new Promise(function(e) {
      const t = new Image();
      t.src = "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=", t.onload = t.onerror = function() {
        e(t.height === 1);
      };
    })), this.isSupported;
  }
}
class Lle {
  constructor(e) {
    this.name = Oi.EXT_MESHOPT_COMPRESSION, this.parser = e;
  }
  loadBufferView(e) {
    const t = this.parser.json, i = t.bufferViews[e];
    if (i.extensions && i.extensions[this.name]) {
      const n = i.extensions[this.name], r = this.parser.getDependency("buffer", n.buffer), a = this.parser.options.meshoptDecoder;
      if (!a || !a.supported) {
        if (t.extensionsRequired && t.extensionsRequired.indexOf(this.name) >= 0)
          throw new Error(
            "THREE.GLTFLoader: setMeshoptDecoder must be called before loading compressed files"
          );
        return null;
      }
      return r.then(function(o) {
        const l = n.byteOffset || 0, c = n.byteLength || 0, h = n.count, u = n.byteStride, d = new Uint8Array(o, l, c);
        return a.decodeGltfBufferAsync ? a.decodeGltfBufferAsync(h, u, d, n.mode, n.filter).then(function(f) {
          return f.buffer;
        }) : a.ready.then(function() {
          const f = new ArrayBuffer(h * u);
          return a.decodeGltfBuffer(
            new Uint8Array(f),
            h,
            u,
            d,
            n.mode,
            n.filter
          ), f;
        });
      });
    } else
      return null;
  }
}
class Ile {
  constructor(e) {
    this.name = Oi.EXT_MESH_GPU_INSTANCING, this.parser = e;
  }
  createNodeMesh(e) {
    const t = this.parser.json, i = t.nodes[e];
    if (!i.extensions || !i.extensions[this.name] || i.mesh === void 0)
      return null;
    const n = t.meshes[i.mesh];
    for (const c of n.primitives)
      if (c.mode !== ga.TRIANGLES && c.mode !== ga.TRIANGLE_STRIP && c.mode !== ga.TRIANGLE_FAN && c.mode !== void 0)
        return null;
    const a = i.extensions[this.name].attributes, o = [], l = {};
    for (const c in a)
      o.push(
        this.parser.getDependency("accessor", a[c]).then((h) => (l[c] = h, l[c]))
      );
    return o.length < 1 ? null : (o.push(this.parser.createNodeMesh(e)), Promise.all(o).then((c) => {
      const h = c.pop(), u = h.isGroup ? h.children : [h], d = c[0].count, f = [];
      for (const p of u) {
        const v = new et(), g = new _(), m = new St(), x = new _(1, 1, 1), y = new ho(p.geometry, p.material, d);
        for (let A = 0; A < d; A++)
          l.TRANSLATION && g.fromBufferAttribute(l.TRANSLATION, A), l.ROTATION && m.fromBufferAttribute(l.ROTATION, A), l.SCALE && x.fromBufferAttribute(l.SCALE, A), y.setMatrixAt(A, v.compose(g, m, x));
        for (const A in l)
          if (A === "_COLOR_0") {
            const b = l[A];
            y.instanceColor = new hs(
              b.array,
              b.itemSize,
              b.normalized
            );
          } else
            A !== "TRANSLATION" && A !== "ROTATION" && A !== "SCALE" && p.geometry.setAttribute(A, l[A]);
        mt.prototype.copy.call(y, p), this.parser.assignFinalMaterial(y), f.push(y);
      }
      return h.isGroup ? (h.clear(), h.add(...f), h) : f[0];
    }));
  }
}
const FB = "glTF", Nm = 12, tP = { JSON: 1313821514, BIN: 5130562 };
class Ole {
  constructor(e) {
    this.name = Oi.KHR_BINARY_GLTF, this.content = null, this.body = null;
    const t = new DataView(e, 0, Nm), i = new TextDecoder();
    if (this.header = {
      magic: i.decode(new Uint8Array(e.slice(0, 4))),
      version: t.getUint32(4, !0),
      length: t.getUint32(8, !0)
    }, this.header.magic !== FB)
      throw new Error("THREE.GLTFLoader: Unsupported glTF-Binary header.");
    if (this.header.version < 2)
      throw new Error("THREE.GLTFLoader: Legacy binary file detected.");
    const n = this.header.length - Nm, r = new DataView(e, Nm);
    let a = 0;
    for (; a < n; ) {
      const o = r.getUint32(a, !0);
      a += 4;
      const l = r.getUint32(a, !0);
      if (a += 4, l === tP.JSON) {
        const c = new Uint8Array(
          e,
          Nm + a,
          o
        );
        this.content = i.decode(c);
      } else if (l === tP.BIN) {
        const c = Nm + a;
        this.body = e.slice(c, c + o);
      }
      a += o;
    }
    if (this.content === null)
      throw new Error("THREE.GLTFLoader: JSON content not found.");
  }
}
class Nle {
  constructor(e, t) {
    if (!t)
      throw new Error("THREE.GLTFLoader: No DRACOLoader instance provided.");
    this.name = Oi.KHR_DRACO_MESH_COMPRESSION, this.json = e, this.dracoLoader = t, this.dracoLoader.preload();
  }
  decodePrimitive(e, t) {
    const i = this.json, n = this.dracoLoader, r = e.extensions[this.name].bufferView, a = e.extensions[this.name].attributes, o = {}, l = {}, c = {};
    for (const h in a) {
      const u = jb[h] || h.toLowerCase();
      o[u] = a[h];
    }
    for (const h in e.attributes) {
      const u = jb[h] || h.toLowerCase();
      if (a[h] !== void 0) {
        const d = i.accessors[e.attributes[h]], f = jf[d.componentType];
        c[u] = f.name, l[u] = d.normalized === !0;
      }
    }
    return t.getDependency("bufferView", r).then(function(h) {
      return new Promise(function(u) {
        n.decodeDracoFile(
          h,
          function(d) {
            for (const f in d.attributes) {
              const p = d.attributes[f], v = l[f];
              v !== void 0 && (p.normalized = v);
            }
            u(d);
          },
          o,
          c
        );
      });
    });
  }
}
class Ble {
  constructor() {
    this.name = Oi.KHR_TEXTURE_TRANSFORM;
  }
  extendTexture(e, t) {
    return (t.texCoord === void 0 || t.texCoord === e.channel) && t.offset === void 0 && t.rotation === void 0 && t.scale === void 0 || (e = e.clone(), t.texCoord !== void 0 && (e.channel = t.texCoord), t.offset !== void 0 && e.offset.fromArray(t.offset), t.rotation !== void 0 && (e.rotation = t.rotation), t.scale !== void 0 && e.repeat.fromArray(t.scale), e.needsUpdate = !0), e;
  }
}
class Fle {
  constructor() {
    this.name = Oi.KHR_MESH_QUANTIZATION;
  }
}
class UB extends Is {
  constructor(e, t, i, n) {
    super(e, t, i, n);
  }
  copySampleValue_(e) {
    const t = this.resultBuffer, i = this.sampleValues, n = this.valueSize, r = e * n * 3 + n;
    for (let a = 0; a !== n; a++)
      t[a] = i[r + a];
    return t;
  }
  interpolate_(e, t, i, n) {
    const r = this.resultBuffer, a = this.sampleValues, o = this.valueSize, l = o * 2, c = o * 3, h = n - t, u = (i - t) / h, d = u * u, f = d * u, p = e * c, v = p - c, g = -2 * f + 3 * d, m = f - d, x = 1 - g, y = m - d + u;
    for (let A = 0; A !== o; A++) {
      const b = a[v + A + o], M = a[v + A + l] * h, w = a[p + A + o], C = a[p + A] * h;
      r[A] = x * b + y * M + g * w + m * C;
    }
    return r;
  }
}
const Ule = new St();
class zle extends UB {
  interpolate_(e, t, i, n) {
    const r = super.interpolate_(e, t, i, n);
    return Ule.fromArray(r).normalize().toArray(r), r;
  }
}
const ga = {
  FLOAT: 5126,
  //FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123
}, jf = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array
}, iP = {
  9728: Ni,
  9729: ii,
  9984: $c,
  9985: $o,
  9986: $a,
  9987: ds
}, nP = {
  33071: yn,
  33648: co,
  10497: un
}, TA = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16
}, jb = {
  POSITION: "position",
  NORMAL: "normal",
  TANGENT: "tangent",
  TEXCOORD_0: "uv",
  TEXCOORD_1: "uv1",
  TEXCOORD_2: "uv2",
  TEXCOORD_3: "uv3",
  COLOR_0: "color",
  WEIGHTS_0: "skinWeight",
  JOINTS_0: "skinIndex"
}, ql = {
  scale: "scale",
  translation: "position",
  rotation: "quaternion",
  weights: "morphTargetInfluences"
}, kle = {
  CUBICSPLINE: void 0,
  // We use a custom interpolant (GLTFCubicSplineInterpolation) for CUBICSPLINE tracks. Each
  // keyframe track will be initialized with a default interpolation type, then modified.
  LINEAR: l0,
  STEP: Uc
}, CA = {
  OPAQUE: "OPAQUE",
  MASK: "MASK",
  BLEND: "BLEND"
};
function Gle(s) {
  return s.DefaultMaterial === void 0 && (s.DefaultMaterial = new or({
    color: 16777215,
    emissive: 0,
    metalness: 1,
    roughness: 1,
    transparent: !1,
    depthTest: !0,
    side: sa
  })), s.DefaultMaterial;
}
function Dh(s, e, t) {
  for (const i in t.extensions)
    s[i] === void 0 && (e.userData.gltfExtensions = e.userData.gltfExtensions || {}, e.userData.gltfExtensions[i] = t.extensions[i]);
}
function cc(s, e) {
  e.extras !== void 0 && (typeof e.extras == "object" ? Object.assign(s.userData, e.extras) : console.warn("THREE.GLTFLoader: Ignoring primitive type .extras, " + e.extras));
}
function Hle(s, e, t) {
  let i = !1, n = !1, r = !1;
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (u.POSITION !== void 0 && (i = !0), u.NORMAL !== void 0 && (n = !0), u.COLOR_0 !== void 0 && (r = !0), i && n && r)
      break;
  }
  if (!i && !n && !r)
    return Promise.resolve(s);
  const a = [], o = [], l = [];
  for (let c = 0, h = e.length; c < h; c++) {
    const u = e[c];
    if (i) {
      const d = u.POSITION !== void 0 ? t.getDependency("accessor", u.POSITION) : s.attributes.position;
      a.push(d);
    }
    if (n) {
      const d = u.NORMAL !== void 0 ? t.getDependency("accessor", u.NORMAL) : s.attributes.normal;
      o.push(d);
    }
    if (r) {
      const d = u.COLOR_0 !== void 0 ? t.getDependency("accessor", u.COLOR_0) : s.attributes.color;
      l.push(d);
    }
  }
  return Promise.all([
    Promise.all(a),
    Promise.all(o),
    Promise.all(l)
  ]).then(function(c) {
    const h = c[0], u = c[1], d = c[2];
    return i && (s.morphAttributes.position = h), n && (s.morphAttributes.normal = u), r && (s.morphAttributes.color = d), s.morphTargetsRelative = !0, s;
  });
}
function Vle(s, e) {
  if (s.updateMorphTargets(), e.weights !== void 0)
    for (let t = 0, i = e.weights.length; t < i; t++)
      s.morphTargetInfluences[t] = e.weights[t];
  if (e.extras && Array.isArray(e.extras.targetNames)) {
    const t = e.extras.targetNames;
    if (s.morphTargetInfluences.length === t.length) {
      s.morphTargetDictionary = {};
      for (let i = 0, n = t.length; i < n; i++)
        s.morphTargetDictionary[t[i]] = i;
    } else
      console.warn("THREE.GLTFLoader: Invalid extras.targetNames length. Ignoring names.");
  }
}
function Wle(s) {
  let e;
  const t = s.extensions && s.extensions[Oi.KHR_DRACO_MESH_COMPRESSION];
  if (t ? e = "draco:" + t.bufferView + ":" + t.indices + ":" + _A(t.attributes) : e = s.indices + ":" + _A(s.attributes) + ":" + s.mode, s.targets !== void 0)
    for (let i = 0, n = s.targets.length; i < n; i++)
      e += ":" + _A(s.targets[i]);
  return e;
}
function _A(s) {
  let e = "";
  const t = Object.keys(s).sort();
  for (let i = 0, n = t.length; i < n; i++)
    e += t[i] + ":" + s[t[i]] + ";";
  return e;
}
function qb(s) {
  switch (s) {
    case Int8Array:
      return 1 / 127;
    case Uint8Array:
      return 1 / 255;
    case Int16Array:
      return 1 / 32767;
    case Uint16Array:
      return 1 / 65535;
    default:
      throw new Error("THREE.GLTFLoader: Unsupported normalized accessor component type.");
  }
}
function Xle(s) {
  return s.search(/\.jpe?g($|\?)/i) > 0 || s.search(/^data\:image\/jpeg/) === 0 ? "image/jpeg" : s.search(/\.webp($|\?)/i) > 0 || s.search(/^data\:image\/webp/) === 0 ? "image/webp" : "image/png";
}
const jle = new et();
class zB {
  constructor(e = {}, t = {}) {
    this.json = e, this.extensions = {}, this.plugins = {}, this.options = t, this.cache = new vle(), this.associations = /* @__PURE__ */ new Map(), this.primitiveCache = {}, this.nodeCache = {}, this.meshCache = { refs: {}, uses: {} }, this.cameraCache = { refs: {}, uses: {} }, this.lightCache = { refs: {}, uses: {} }, this.sourceCache = {}, this.textureCache = {}, this.nodeNamesUsed = {};
    let i = !1, n = !1, r = -1;
    typeof navigator < "u" && (i = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) === !0, n = navigator.userAgent.indexOf("Firefox") > -1, r = n ? navigator.userAgent.match(/Firefox\/([0-9]+)\./)[1] : -1), typeof createImageBitmap > "u" || i || n && r < 98 ? this.textureLoader = new rh(this.options.manager) : this.textureLoader = new A2(this.options.manager), this.textureLoader.setCrossOrigin(this.options.crossOrigin), this.textureLoader.setRequestHeader(this.options.requestHeader), this.fileLoader = new Nn(this.options.manager), this.fileLoader.setResponseType("arraybuffer"), this.options.crossOrigin === "use-credentials" && this.fileLoader.setWithCredentials(!0);
  }
  setExtensions(e) {
    this.extensions = e;
  }
  setPlugins(e) {
    this.plugins = e;
  }
  parse(e, t) {
    const i = this, n = this.json, r = this.extensions;
    this.cache.removeAll(), this.nodeCache = {}, this._invokeAll(function(a) {
      return a._markDefs && a._markDefs();
    }), Promise.all(
      this._invokeAll(function(a) {
        return a.beforeRoot && a.beforeRoot();
      })
    ).then(function() {
      return Promise.all([
        i.getDependencies("scene"),
        i.getDependencies("animation"),
        i.getDependencies("camera")
      ]);
    }).then(function(a) {
      const o = {
        scene: a[0][n.scene || 0],
        scenes: a[0],
        animations: a[1],
        cameras: a[2],
        asset: n.asset,
        parser: i,
        userData: {}
      };
      return Dh(r, o, n), cc(o, n), Promise.all(
        i._invokeAll(function(l) {
          return l.afterRoot && l.afterRoot(o);
        })
      ).then(function() {
        e(o);
      });
    }).catch(t);
  }
  /**
   * Marks the special nodes/meshes in json for efficient parse.
   */
  _markDefs() {
    const e = this.json.nodes || [], t = this.json.skins || [], i = this.json.meshes || [];
    for (let n = 0, r = t.length; n < r; n++) {
      const a = t[n].joints;
      for (let o = 0, l = a.length; o < l; o++)
        e[a[o]].isBone = !0;
    }
    for (let n = 0, r = e.length; n < r; n++) {
      const a = e[n];
      a.mesh !== void 0 && (this._addNodeRef(this.meshCache, a.mesh), a.skin !== void 0 && (i[a.mesh].isSkinnedMesh = !0)), a.camera !== void 0 && this._addNodeRef(this.cameraCache, a.camera);
    }
  }
  /**
   * Counts references to shared node / Object3D resources. These resources
   * can be reused, or "instantiated", at multiple nodes in the scene
   * hierarchy. Mesh, Camera, and Light instances are instantiated and must
   * be marked. Non-scenegraph resources (like Materials, Geometries, and
   * Textures) can be reused directly and are not marked here.
   *
   * Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
   */
  _addNodeRef(e, t) {
    t !== void 0 && (e.refs[t] === void 0 && (e.refs[t] = e.uses[t] = 0), e.refs[t]++);
  }
  /** Returns a reference to a shared resource, cloning it if necessary. */
  _getNodeRef(e, t, i) {
    if (e.refs[t] <= 1)
      return i;
    const n = i.clone(), r = (a, o) => {
      const l = this.associations.get(a);
      l != null && this.associations.set(o, l);
      for (const [c, h] of a.children.entries())
        r(h, o.children[c]);
    };
    return r(i, n), n.name += "_instance_" + e.uses[t]++, n;
  }
  _invokeOne(e) {
    const t = Object.values(this.plugins);
    t.push(this);
    for (let i = 0; i < t.length; i++) {
      const n = e(t[i]);
      if (n)
        return n;
    }
    return null;
  }
  _invokeAll(e) {
    const t = Object.values(this.plugins);
    t.unshift(this);
    const i = [];
    for (let n = 0; n < t.length; n++) {
      const r = e(t[n]);
      r && i.push(r);
    }
    return i;
  }
  /**
   * Requests the specified dependency asynchronously, with caching.
   * @param {string} type
   * @param {number} index
   * @return {Promise<Object3D|Material|THREE.Texture|AnimationClip|ArrayBuffer|Object>}
   */
  getDependency(e, t) {
    const i = e + ":" + t;
    let n = this.cache.get(i);
    if (!n) {
      switch (e) {
        case "scene":
          n = this.loadScene(t);
          break;
        case "node":
          n = this._invokeOne(function(r) {
            return r.loadNode && r.loadNode(t);
          });
          break;
        case "mesh":
          n = this._invokeOne(function(r) {
            return r.loadMesh && r.loadMesh(t);
          });
          break;
        case "accessor":
          n = this.loadAccessor(t);
          break;
        case "bufferView":
          n = this._invokeOne(function(r) {
            return r.loadBufferView && r.loadBufferView(t);
          });
          break;
        case "buffer":
          n = this.loadBuffer(t);
          break;
        case "material":
          n = this._invokeOne(function(r) {
            return r.loadMaterial && r.loadMaterial(t);
          });
          break;
        case "texture":
          n = this._invokeOne(function(r) {
            return r.loadTexture && r.loadTexture(t);
          });
          break;
        case "skin":
          n = this.loadSkin(t);
          break;
        case "animation":
          n = this._invokeOne(function(r) {
            return r.loadAnimation && r.loadAnimation(t);
          });
          break;
        case "camera":
          n = this.loadCamera(t);
          break;
        default:
          if (n = this._invokeOne(function(r) {
            return r != this && r.getDependency && r.getDependency(e, t);
          }), !n)
            throw new Error("Unknown type: " + e);
          break;
      }
      this.cache.add(i, n);
    }
    return n;
  }
  /**
   * Requests all dependencies of the specified type asynchronously, with caching.
   * @param {string} type
   * @return {Promise<Array<Object>>}
   */
  getDependencies(e) {
    let t = this.cache.get(e);
    if (!t) {
      const i = this, n = this.json[e + (e === "mesh" ? "es" : "s")] || [];
      t = Promise.all(
        n.map(function(r, a) {
          return i.getDependency(e, a);
        })
      ), this.cache.add(e, t);
    }
    return t;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBuffer(e) {
    const t = this.json.buffers[e], i = this.fileLoader;
    if (t.type && t.type !== "arraybuffer")
      throw new Error("THREE.GLTFLoader: " + t.type + " buffer type is not supported.");
    if (t.uri === void 0 && e === 0)
      return Promise.resolve(this.extensions[Oi.KHR_BINARY_GLTF].body);
    const n = this.options;
    return new Promise(function(r, a) {
      i.load(
        Rs.resolveURL(t.uri, n.path),
        r,
        void 0,
        function() {
          a(new Error('THREE.GLTFLoader: Failed to load buffer "' + t.uri + '".'));
        }
      );
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferViewIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBufferView(e) {
    const t = this.json.bufferViews[e];
    return this.getDependency("buffer", t.buffer).then(function(i) {
      const n = t.byteLength || 0, r = t.byteOffset || 0;
      return i.slice(r, r + n);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
   * @param {number} accessorIndex
   * @return {Promise<BufferAttribute|InterleavedBufferAttribute>}
   */
  loadAccessor(e) {
    const t = this, i = this.json, n = this.json.accessors[e];
    if (n.bufferView === void 0 && n.sparse === void 0) {
      const a = TA[n.type], o = jf[n.componentType], l = n.normalized === !0, c = new o(n.count * a);
      return Promise.resolve(new Ht(c, a, l));
    }
    const r = [];
    return n.bufferView !== void 0 ? r.push(this.getDependency("bufferView", n.bufferView)) : r.push(null), n.sparse !== void 0 && (r.push(
      this.getDependency("bufferView", n.sparse.indices.bufferView)
    ), r.push(
      this.getDependency("bufferView", n.sparse.values.bufferView)
    )), Promise.all(r).then(function(a) {
      const o = a[0], l = TA[n.type], c = jf[n.componentType], h = c.BYTES_PER_ELEMENT, u = h * l, d = n.byteOffset || 0, f = n.bufferView !== void 0 ? i.bufferViews[n.bufferView].byteStride : void 0, p = n.normalized === !0;
      let v, g;
      if (f && f !== u) {
        const m = Math.floor(d / f), x = "InterleavedBuffer:" + n.bufferView + ":" + n.componentType + ":" + m + ":" + n.count;
        let y = t.cache.get(x);
        y || (v = new c(
          o,
          m * f,
          n.count * f / h
        ), y = new Xu(v, f / h), t.cache.add(x, y)), g = new c0(
          y,
          l,
          d % f / h,
          p
        );
      } else
        o === null ? v = new c(n.count * l) : v = new c(o, d, n.count * l), g = new Ht(v, l, p);
      if (n.sparse !== void 0) {
        const m = TA.SCALAR, x = jf[n.sparse.indices.componentType], y = n.sparse.indices.byteOffset || 0, A = n.sparse.values.byteOffset || 0, b = new x(
          a[1],
          y,
          n.sparse.count * m
        ), M = new c(
          a[2],
          A,
          n.sparse.count * l
        );
        o !== null && (g = new Ht(
          g.array.slice(),
          g.itemSize,
          g.normalized
        ));
        for (let w = 0, C = b.length; w < C; w++) {
          const T = b[w];
          if (g.setX(T, M[w * l]), l >= 2 && g.setY(T, M[w * l + 1]), l >= 3 && g.setZ(T, M[w * l + 2]), l >= 4 && g.setW(T, M[w * l + 3]), l >= 5)
            throw new Error("THREE.GLTFLoader: Unsupported itemSize in sparse BufferAttribute.");
        }
      }
      return g;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
   * @param {number} textureIndex
   * @return {Promise<THREE.Texture|null>}
   */
  loadTexture(e) {
    const t = this.json, i = this.options, r = t.textures[e].source, a = t.images[r];
    let o = this.textureLoader;
    if (a.uri) {
      const l = i.manager.getHandler(a.uri);
      l !== null && (o = l);
    }
    return this.loadTextureImage(e, r, o);
  }
  loadTextureImage(e, t, i) {
    const n = this, r = this.json, a = r.textures[e], o = r.images[t], l = (o.uri || o.bufferView) + ":" + a.sampler;
    if (this.textureCache[l])
      return this.textureCache[l];
    const c = this.loadImageSource(t, i).then(function(h) {
      h.flipY = !1, h.name = a.name || o.name || "", h.name === "" && typeof o.uri == "string" && o.uri.startsWith("data:image/") === !1 && (h.name = o.uri);
      const d = (r.samplers || {})[a.sampler] || {};
      return h.magFilter = iP[d.magFilter] || ii, h.minFilter = iP[d.minFilter] || ds, h.wrapS = nP[d.wrapS] || un, h.wrapT = nP[d.wrapT] || un, n.associations.set(h, { textures: e }), h;
    }).catch(function() {
      return null;
    });
    return this.textureCache[l] = c, c;
  }
  loadImageSource(e, t) {
    const i = this, n = this.json, r = this.options;
    if (this.sourceCache[e] !== void 0)
      return this.sourceCache[e].then((u) => u.clone());
    const a = n.images[e], o = self.URL || self.webkitURL;
    let l = a.uri || "", c = !1;
    if (a.bufferView !== void 0)
      l = i.getDependency("bufferView", a.bufferView).then(function(u) {
        c = !0;
        const d = new Blob([u], { type: a.mimeType });
        return l = o.createObjectURL(d), l;
      });
    else if (a.uri === void 0)
      throw new Error("THREE.GLTFLoader: Image " + e + " is missing URI and bufferView");
    const h = Promise.resolve(l).then(function(u) {
      return new Promise(function(d, f) {
        let p = d;
        t.isImageBitmapLoader === !0 && (p = function(v) {
          const g = new Ut(v);
          g.needsUpdate = !0, d(g);
        }), t.load(Rs.resolveURL(u, r.path), p, void 0, f);
      });
    }).then(function(u) {
      return c === !0 && o.revokeObjectURL(l), u.userData.mimeType = a.mimeType || Xle(a.uri), u;
    }).catch(function(u) {
      throw console.error("THREE.GLTFLoader: Couldn't load texture", l), u;
    });
    return this.sourceCache[e] = h, h;
  }
  /**
   * Asynchronously assigns a texture to the given material parameters.
   * @param {Object} materialParams
   * @param {string} mapName
   * @param {Object} mapDef
   * @return {Promise<Texture>}
   */
  assignTexture(e, t, i, n) {
    const r = this;
    return this.getDependency("texture", i.index).then(function(a) {
      if (!a)
        return null;
      if (i.texCoord !== void 0 && i.texCoord > 0 && (a = a.clone(), a.channel = i.texCoord), r.extensions[Oi.KHR_TEXTURE_TRANSFORM]) {
        const o = i.extensions !== void 0 ? i.extensions[Oi.KHR_TEXTURE_TRANSFORM] : void 0;
        if (o) {
          const l = r.associations.get(a);
          a = r.extensions[Oi.KHR_TEXTURE_TRANSFORM].extendTexture(
            a,
            o
          ), r.associations.set(a, l);
        }
      }
      return n !== void 0 && (a.colorSpace = n), e[t] = a, a;
    });
  }
  /**
   * Assigns final material to a Mesh, Line, or Points instance. The instance
   * already has a material (generated from the glTF material options alone)
   * but reuse of the same glTF material may require multiple threejs materials
   * to accommodate different primitive types, defines, etc. New materials will
   * be created if necessary, and reused from a cache.
   * @param  {Object3D} mesh Mesh, Line, or Points instance.
   */
  assignFinalMaterial(e) {
    const t = e.geometry;
    let i = e.material;
    const n = t.attributes.tangent === void 0, r = t.attributes.color !== void 0, a = t.attributes.normal === void 0;
    if (e.isPoints) {
      const o = "PointsMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new qu(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, l.sizeAttenuation = !1, this.cache.add(o, l)), i = l;
    } else if (e.isLine) {
      const o = "LineBasicMaterial:" + i.uuid;
      let l = this.cache.get(o);
      l || (l = new Bi(), Cn.prototype.copy.call(l, i), l.color.copy(i.color), l.map = i.map, this.cache.add(o, l)), i = l;
    }
    if (n || r || a) {
      let o = "ClonedMaterial:" + i.uuid + ":";
      n && (o += "derivative-tangents:"), r && (o += "vertex-colors:"), a && (o += "flat-shading:");
      let l = this.cache.get(o);
      l || (l = i.clone(), r && (l.vertexColors = !0), a && (l.flatShading = !0), n && (l.normalScale && (l.normalScale.y *= -1), l.clearcoatNormalScale && (l.clearcoatNormalScale.y *= -1)), this.cache.add(o, l), this.associations.set(l, this.associations.get(i))), i = l;
    }
    e.material = i;
  }
  getMaterialType() {
    return or;
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
   * @param {number} materialIndex
   * @return {Promise<Material>}
   */
  loadMaterial(e) {
    const t = this, i = this.json, n = this.extensions, r = i.materials[e];
    let a;
    const o = {}, l = r.extensions || {}, c = [];
    if (l[Oi.KHR_MATERIALS_UNLIT]) {
      const u = n[Oi.KHR_MATERIALS_UNLIT];
      a = u.getMaterialType(), c.push(u.extendParams(o, r, t));
    } else {
      const u = r.pbrMetallicRoughness || {};
      if (o.color = new _e(1, 1, 1), o.opacity = 1, Array.isArray(u.baseColorFactor)) {
        const d = u.baseColorFactor;
        o.color.setRGB(d[0], d[1], d[2], Si), o.opacity = d[3];
      }
      u.baseColorTexture !== void 0 && c.push(
        t.assignTexture(
          o,
          "map",
          u.baseColorTexture,
          qi
        )
      ), o.metalness = u.metallicFactor !== void 0 ? u.metallicFactor : 1, o.roughness = u.roughnessFactor !== void 0 ? u.roughnessFactor : 1, u.metallicRoughnessTexture !== void 0 && (c.push(
        t.assignTexture(
          o,
          "metalnessMap",
          u.metallicRoughnessTexture
        )
      ), c.push(
        t.assignTexture(
          o,
          "roughnessMap",
          u.metallicRoughnessTexture
        )
      )), a = this._invokeOne(function(d) {
        return d.getMaterialType && d.getMaterialType(e);
      }), c.push(
        Promise.all(
          this._invokeAll(function(d) {
            return d.extendMaterialParams && d.extendMaterialParams(e, o);
          })
        )
      );
    }
    r.doubleSided === !0 && (o.side = ui);
    const h = r.alphaMode || CA.OPAQUE;
    if (h === CA.BLEND ? (o.transparent = !0, o.depthWrite = !1) : (o.transparent = !1, h === CA.MASK && (o.alphaTest = r.alphaCutoff !== void 0 ? r.alphaCutoff : 0.5)), r.normalTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "normalMap", r.normalTexture)), o.normalScale = new Se(1, 1), r.normalTexture.scale !== void 0)) {
      const u = r.normalTexture.scale;
      o.normalScale.set(u, u);
    }
    if (r.occlusionTexture !== void 0 && a !== kt && (c.push(t.assignTexture(o, "aoMap", r.occlusionTexture)), r.occlusionTexture.strength !== void 0 && (o.aoMapIntensity = r.occlusionTexture.strength)), r.emissiveFactor !== void 0 && a !== kt) {
      const u = r.emissiveFactor;
      o.emissive = new _e().setRGB(
        u[0],
        u[1],
        u[2],
        Si
      );
    }
    return r.emissiveTexture !== void 0 && a !== kt && c.push(
      t.assignTexture(
        o,
        "emissiveMap",
        r.emissiveTexture,
        qi
      )
    ), Promise.all(c).then(function() {
      const u = new a(o);
      return r.name && (u.name = r.name), cc(u, r), t.associations.set(u, { materials: e }), r.extensions && Dh(n, u, r), u;
    });
  }
  /** When Object3D instances are targeted by animation, they need unique names. */
  createUniqueName(e) {
    const t = Pi.sanitizeNodeName(e || "");
    return t in this.nodeNamesUsed ? t + "_" + ++this.nodeNamesUsed[t] : (this.nodeNamesUsed[t] = 0, t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
   *
   * Creates BufferGeometries from primitives.
   *
   * @param {Array<GLTF.Primitive>} primitives
   * @return {Promise<Array<BufferGeometry>>}
   */
  loadGeometries(e) {
    const t = this, i = this.extensions, n = this.primitiveCache;
    function r(o) {
      return i[Oi.KHR_DRACO_MESH_COMPRESSION].decodePrimitive(o, t).then(function(l) {
        return sP(l, o, t);
      });
    }
    const a = [];
    for (let o = 0, l = e.length; o < l; o++) {
      const c = e[o], h = Wle(c), u = n[h];
      if (u)
        a.push(u.promise);
      else {
        let d;
        c.extensions && c.extensions[Oi.KHR_DRACO_MESH_COMPRESSION] ? d = r(c) : d = sP(new Pt(), c, t), n[h] = { primitive: c, promise: d }, a.push(d);
      }
    }
    return Promise.all(a);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
   * @param {number} meshIndex
   * @return {Promise<Group|Mesh|SkinnedMesh>}
   */
  loadMesh(e) {
    const t = this, i = this.json, n = this.extensions, r = i.meshes[e], a = r.primitives, o = [];
    for (let l = 0, c = a.length; l < c; l++) {
      const h = a[l].material === void 0 ? Gle(this.cache) : this.getDependency("material", a[l].material);
      o.push(h);
    }
    return o.push(t.loadGeometries(a)), Promise.all(o).then(function(l) {
      const c = l.slice(0, l.length - 1), h = l[l.length - 1], u = [];
      for (let f = 0, p = h.length; f < p; f++) {
        const v = h[f], g = a[f];
        let m;
        const x = c[f];
        if (g.mode === ga.TRIANGLES || g.mode === ga.TRIANGLE_STRIP || g.mode === ga.TRIANGLE_FAN || g.mode === void 0)
          m = r.isSkinnedMesh === !0 ? new a2(v, x) : new Re(v, x), m.isSkinnedMesh === !0 && m.normalizeSkinWeights(), g.mode === ga.TRIANGLE_STRIP ? m.geometry = up(m.geometry, i2) : g.mode === ga.TRIANGLE_FAN && (m.geometry = up(m.geometry, ip));
        else if (g.mode === ga.LINES)
          m = new _n(v, x);
        else if (g.mode === ga.LINE_STRIP)
          m = new ti(v, x);
        else if (g.mode === ga.LINE_LOOP)
          m = new ju(v, x);
        else if (g.mode === ga.POINTS)
          m = new sh(v, x);
        else
          throw new Error("THREE.GLTFLoader: Primitive mode unsupported: " + g.mode);
        Object.keys(m.geometry.morphAttributes).length > 0 && Vle(m, r), m.name = t.createUniqueName(r.name || "mesh_" + e), cc(m, r), g.extensions && Dh(n, m, g), t.assignFinalMaterial(m), u.push(m);
      }
      for (let f = 0, p = u.length; f < p; f++)
        t.associations.set(u[f], {
          meshes: e,
          primitives: f
        });
      if (u.length === 1)
        return r.extensions && Dh(n, u[0], r), u[0];
      const d = new ci();
      r.extensions && Dh(n, d, r), t.associations.set(d, { meshes: e });
      for (let f = 0, p = u.length; f < p; f++)
        d.add(u[f]);
      return d;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#cameras
   * @param {number} cameraIndex
   * @return {Promise<THREE.Camera>}
   */
  loadCamera(e) {
    let t;
    const i = this.json.cameras[e], n = i[i.type];
    if (!n) {
      console.warn("THREE.GLTFLoader: Missing camera parameters.");
      return;
    }
    return i.type === "perspective" ? t = new Zt(
      nt.radToDeg(n.yfov),
      n.aspectRatio || 1,
      n.znear || 1,
      n.zfar || 2e6
    ) : i.type === "orthographic" && (t = new Xn(
      -n.xmag,
      n.xmag,
      n.ymag,
      -n.ymag,
      n.znear,
      n.zfar
    )), i.name && (t.name = this.createUniqueName(i.name)), cc(t, i), Promise.resolve(t);
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#skins
   * @param {number} skinIndex
   * @return {Promise<Skeleton>}
   */
  loadSkin(e) {
    const t = this.json.skins[e], i = [];
    for (let n = 0, r = t.joints.length; n < r; n++)
      i.push(this._loadNodeShallow(t.joints[n]));
    return t.inverseBindMatrices !== void 0 ? i.push(this.getDependency("accessor", t.inverseBindMatrices)) : i.push(null), Promise.all(i).then(function(n) {
      const r = n.pop(), a = n, o = [], l = [];
      for (let c = 0, h = a.length; c < h; c++) {
        const u = a[c];
        if (u) {
          o.push(u);
          const d = new et();
          r !== null && d.fromArray(r.array, c * 16), l.push(d);
        } else
          console.warn('THREE.GLTFLoader: Joint "%s" could not be found.', t.joints[c]);
      }
      return new nh(o, l);
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#animations
   * @param {number} animationIndex
   * @return {Promise<AnimationClip>}
   */
  loadAnimation(e) {
    const t = this.json, i = this, n = t.animations[e], r = n.name ? n.name : "animation_" + e, a = [], o = [], l = [], c = [], h = [];
    for (let u = 0, d = n.channels.length; u < d; u++) {
      const f = n.channels[u], p = n.samplers[f.sampler], v = f.target, g = v.node, m = n.parameters !== void 0 ? n.parameters[p.input] : p.input, x = n.parameters !== void 0 ? n.parameters[p.output] : p.output;
      v.node !== void 0 && (a.push(this.getDependency("node", g)), o.push(this.getDependency("accessor", m)), l.push(this.getDependency("accessor", x)), c.push(p), h.push(v));
    }
    return Promise.all([
      Promise.all(a),
      Promise.all(o),
      Promise.all(l),
      Promise.all(c),
      Promise.all(h)
    ]).then(function(u) {
      const d = u[0], f = u[1], p = u[2], v = u[3], g = u[4], m = [];
      for (let x = 0, y = d.length; x < y; x++) {
        const A = d[x], b = f[x], M = p[x], w = v[x], C = g[x];
        if (A === void 0)
          continue;
        A.updateMatrix && A.updateMatrix();
        const T = i._createAnimationTracks(
          A,
          b,
          M,
          w,
          C
        );
        if (T)
          for (let E = 0; E < T.length; E++)
            m.push(T[E]);
      }
      return new xl(r, void 0, m);
    });
  }
  createNodeMesh(e) {
    const t = this.json, i = this, n = t.nodes[e];
    return n.mesh === void 0 ? null : i.getDependency("mesh", n.mesh).then(function(r) {
      const a = i._getNodeRef(i.meshCache, n.mesh, r);
      return n.weights !== void 0 && a.traverse(function(o) {
        if (o.isMesh)
          for (let l = 0, c = n.weights.length; l < c; l++)
            o.morphTargetInfluences[l] = n.weights[l];
      }), a;
    });
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
   * @param {number} nodeIndex
   * @return {Promise<Object3D>}
   */
  loadNode(e) {
    const t = this.json, i = this, n = t.nodes[e], r = i._loadNodeShallow(e), a = [], o = n.children || [];
    for (let c = 0, h = o.length; c < h; c++)
      a.push(i.getDependency("node", o[c]));
    const l = n.skin === void 0 ? Promise.resolve(null) : i.getDependency("skin", n.skin);
    return Promise.all([r, Promise.all(a), l]).then(
      function(c) {
        const h = c[0], u = c[1], d = c[2];
        d !== null && h.traverse(function(f) {
          f.isSkinnedMesh && f.bind(d, jle);
        });
        for (let f = 0, p = u.length; f < p; f++)
          h.add(u[f]);
        return h;
      }
    );
  }
  // ._loadNodeShallow() parses a single node.
  // skin and child nodes are created and added in .loadNode() (no '_' prefix).
  _loadNodeShallow(e) {
    const t = this.json, i = this.extensions, n = this;
    if (this.nodeCache[e] !== void 0)
      return this.nodeCache[e];
    const r = t.nodes[e], a = r.name ? n.createUniqueName(r.name) : "", o = [], l = n._invokeOne(function(c) {
      return c.createNodeMesh && c.createNodeMesh(e);
    });
    return l && o.push(l), r.camera !== void 0 && o.push(
      n.getDependency("camera", r.camera).then(function(c) {
        return n._getNodeRef(n.cameraCache, r.camera, c);
      })
    ), n._invokeAll(function(c) {
      return c.createNodeAttachment && c.createNodeAttachment(e);
    }).forEach(function(c) {
      o.push(c);
    }), this.nodeCache[e] = Promise.all(o).then(function(c) {
      let h;
      if (r.isBone === !0 ? h = new ih() : c.length > 1 ? h = new ci() : c.length === 1 ? h = c[0] : h = new mt(), h !== c[0])
        for (let u = 0, d = c.length; u < d; u++)
          h.add(c[u]);
      if (r.name && (h.userData.name = r.name, h.name = a), cc(h, r), r.extensions && Dh(i, h, r), r.matrix !== void 0) {
        const u = new et();
        u.fromArray(r.matrix), h.applyMatrix4(u);
      } else
        r.translation !== void 0 && h.position.fromArray(r.translation), r.rotation !== void 0 && h.quaternion.fromArray(r.rotation), r.scale !== void 0 && h.scale.fromArray(r.scale);
      return n.associations.has(h) || n.associations.set(h, {}), n.associations.get(h).nodes = e, h;
    }), this.nodeCache[e];
  }
  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
   * @param {number} sceneIndex
   * @return {Promise<Group>}
   */
  loadScene(e) {
    const t = this.extensions, i = this.json.scenes[e], n = this, r = new ci();
    i.name && (r.name = n.createUniqueName(i.name)), cc(r, i), i.extensions && Dh(t, r, i);
    const a = i.nodes || [], o = [];
    for (let l = 0, c = a.length; l < c; l++)
      o.push(n.getDependency("node", a[l]));
    return Promise.all(o).then(function(l) {
      for (let h = 0, u = l.length; h < u; h++)
        r.add(l[h]);
      const c = (h) => {
        const u = /* @__PURE__ */ new Map();
        for (const [d, f] of n.associations)
          (d instanceof Cn || d instanceof Ut) && u.set(d, f);
        return h.traverse((d) => {
          const f = n.associations.get(d);
          f != null && u.set(d, f);
        }), u;
      };
      return n.associations = c(r), r;
    });
  }
  _createAnimationTracks(e, t, i, n, r) {
    const a = [], o = e.name ? e.name : e.uuid, l = [];
    ql[r.path] === ql.weights ? e.traverse(function(d) {
      d.morphTargetInfluences && l.push(d.name ? d.name : d.uuid);
    }) : l.push(o);
    let c;
    switch (ql[r.path]) {
      case ql.weights:
        c = uo;
        break;
      case ql.rotation:
        c = fo;
        break;
      case ql.position:
      case ql.scale:
        c = po;
        break;
      default:
        switch (i.itemSize) {
          case 1:
            c = uo;
            break;
          case 2:
          case 3:
          default:
            c = po;
            break;
        }
        break;
    }
    const h = n.interpolation !== void 0 ? kle[n.interpolation] : l0, u = this._getArrayFromAccessor(i);
    for (let d = 0, f = l.length; d < f; d++) {
      const p = new c(
        l[d] + "." + ql[r.path],
        t.array,
        u,
        h
      );
      n.interpolation === "CUBICSPLINE" && this._createCubicSplineTrackInterpolant(p), a.push(p);
    }
    return a;
  }
  _getArrayFromAccessor(e) {
    let t = e.array;
    if (e.normalized) {
      const i = qb(t.constructor), n = new Float32Array(t.length);
      for (let r = 0, a = t.length; r < a; r++)
        n[r] = t[r] * i;
      t = n;
    }
    return t;
  }
  _createCubicSplineTrackInterpolant(e) {
    e.createInterpolant = function(i) {
      const n = this instanceof fo ? zle : UB;
      return new n(this.times, this.values, this.getValueSize() / 3, i);
    }, e.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline = !0;
  }
}
function qle(s, e, t) {
  const i = e.attributes, n = new zt();
  if (i.POSITION !== void 0) {
    const o = t.json.accessors[i.POSITION], l = o.min, c = o.max;
    if (l !== void 0 && c !== void 0) {
      if (n.set(new _(l[0], l[1], l[2]), new _(c[0], c[1], c[2])), o.normalized) {
        const h = qb(jf[o.componentType]);
        n.min.multiplyScalar(h), n.max.multiplyScalar(h);
      }
    } else {
      console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      return;
    }
  } else
    return;
  const r = e.targets;
  if (r !== void 0) {
    const o = new _(), l = new _();
    for (let c = 0, h = r.length; c < h; c++) {
      const u = r[c];
      if (u.POSITION !== void 0) {
        const d = t.json.accessors[u.POSITION], f = d.min, p = d.max;
        if (f !== void 0 && p !== void 0) {
          if (l.setX(Math.max(Math.abs(f[0]), Math.abs(p[0]))), l.setY(Math.max(Math.abs(f[1]), Math.abs(p[1]))), l.setZ(Math.max(Math.abs(f[2]), Math.abs(p[2]))), d.normalized) {
            const v = qb(
              jf[d.componentType]
            );
            l.multiplyScalar(v);
          }
          o.max(l);
        } else
          console.warn("THREE.GLTFLoader: Missing min/max properties for accessor POSITION.");
      }
    }
    n.expandByVector(o);
  }
  s.boundingBox = n;
  const a = new Tn();
  n.getCenter(a.center), a.radius = n.min.distanceTo(n.max) / 2, s.boundingSphere = a;
}
function sP(s, e, t) {
  const i = e.attributes, n = [];
  function r(a, o) {
    return t.getDependency("accessor", a).then(function(l) {
      s.setAttribute(o, l);
    });
  }
  for (const a in i) {
    const o = jb[a] || a.toLowerCase();
    o in s.attributes || n.push(r(i[a], o));
  }
  if (e.indices !== void 0 && !s.index) {
    const a = t.getDependency("accessor", e.indices).then(function(o) {
      s.setIndex(o);
    });
    n.push(a);
  }
  return bi.workingColorSpace !== Si && "COLOR_0" in i && console.warn(
    `THREE.GLTFLoader: Converting vertex colors from "srgb-linear" to "${bi.workingColorSpace}" not supported.`
  ), cc(s, e), qle(s, e, t), Promise.all(n).then(function() {
    return e.targets !== void 0 ? Hle(s, e.targets, t) : s;
  });
}
class Yle extends zB {
  constructor(e, t) {
    super(), this.json = e, this.setExtensions({
      KHR_binary_glTF: {
        content: e,
        body: t
      }
    });
  }
}
class Zle {
  constructor() {
    S(this, "json", {});
    S(this, "buffers", []);
    S(this, "byteOffset", 0);
  }
  write(e) {
    e.forEach((i) => {
      const { attributes: n, morphAttributes: r } = i, a = {
        name: i.name,
        metadata: {
          version: 4.6,
          type: "BufferGeometry",
          generator: "BufferGeometry.toJSON"
        },
        type: i.type,
        userData: {},
        data: {
          attributes: {},
          boundingSphere: {}
        }
      };
      a.type = i.type, i.name !== "" && (a.name = i.name), Object.keys(i.userData).length > 0 && (a.userData = i.userData);
      for (const o in n) {
        const l = n[o], c = this.createAccessorForAttribute(l, i);
        a.data.attributes[o] = c;
      }
      if (i.index) {
        const o = this.createAccessorForAttribute(i.index, i);
        a.data.index = o;
      }
      i.groups.length > 0 && (a.data.groups = JSON.parse(JSON.stringify(i.groups))), i.boundingSphere !== null && (a.data.boundingSphere = {
        center: i.boundingSphere.center.toArray(),
        radius: i.boundingSphere.radius
      }), this.json.geometries || (this.json.geometries = []), this.json.geometries.push(a);
    });
    const t = new Blob(this.buffers, { type: "application/octet-stream" });
    return new Promise((i, n) => {
      const r = new FileReader();
      r.readAsArrayBuffer(t), r.onloadend = () => {
        const a = oP(r.result), o = new DataView(new ArrayBuffer(aP));
        o.setUint32(0, a.byteLength, !0), o.setUint32(4, $le, !0);
        const l = oP(
          ece(JSON.stringify(this.json)),
          32
        ), c = new DataView(new ArrayBuffer(aP));
        c.setUint32(0, l.byteLength, !0), c.setUint32(4, Qle, !0);
        const h = new ArrayBuffer(rP), u = new DataView(h);
        u.setUint32(0, Kle, !0), u.setUint32(4, Jle, !0);
        const d = rP + c.byteLength + l.byteLength + o.byteLength + a.byteLength;
        u.setUint32(8, d, !0);
        const f = new Blob(
          [h, c, l, o, a],
          { type: "application/octet-stream" }
        ), p = new FileReader();
        p.readAsArrayBuffer(f), p.onloadend = function() {
          i(p.result);
        }, p.onerror = (v) => {
          n(v);
        };
      }, r.onerror = (a) => {
        n(a);
      };
    });
  }
  createAccessorForAttribute(e, t, i, n) {
    const r = this.json;
    let a;
    if (a = tce(e), i === void 0 && (i = 0), (n === void 0 || n === 1 / 0) && (n = e.count), n === 0)
      return null;
    const o = ice(e, i, n);
    let l;
    t !== void 0 && (l = e === t.index ? Zn.ELEMENT_ARRAY_BUFFER : Zn.ARRAY_BUFFER);
    const c = this.createBufferViewForAttribute(
      e,
      a,
      i,
      n,
      l
    ), h = {
      bufferView: c.id,
      byteOffset: c.byteOffset,
      componentType: a,
      count: n,
      max: o.max,
      min: o.min,
      type: nce(e)
    };
    return e.normalized === !0 && (h.normalized = !0), r.accessors || (r.accessors = []), r.accessors.push(h) - 1;
  }
  createBufferViewForAttribute(e, t, i, n, r) {
    const a = this.json;
    a.bufferViews || (a.bufferViews = []);
    let o;
    switch (t) {
      case Zn.BYTE:
      case Zn.UNSIGNED_BYTE:
        o = 1;
        break;
      case Zn.SHORT:
      case Zn.UNSIGNED_SHORT:
        o = 2;
        break;
      default:
        o = 4;
    }
    const l = kB(n * e.itemSize * o), c = new DataView(new ArrayBuffer(l));
    let h = 0;
    for (let f = i; f < i + n; f++)
      for (let p = 0; p < e.itemSize; p++) {
        let v = 0;
        e.itemSize > 4 ? v = e.array[f * e.itemSize + p] : (p === 0 ? v = e.getX(f) : p === 1 ? v = e.getY(f) : p === 2 ? v = e.getZ(f) : p === 3 && (v = e.getW(f)), e.normalized === !0 && (v = nt.normalize(v, e.array))), t === Zn.FLOAT ? c.setFloat32(h, v, !0) : t === Zn.INT ? c.setInt32(h, v, !0) : t === Zn.UNSIGNED_INT ? c.setUint32(h, v, !0) : t === Zn.SHORT ? c.setInt16(h, v, !0) : t === Zn.UNSIGNED_SHORT ? c.setUint16(h, v, !0) : t === Zn.BYTE ? c.setInt8(h, v) : t === Zn.UNSIGNED_BYTE && c.setUint8(h, v), h += o;
      }
    const u = {
      buffer: this.processBuffer(c.buffer),
      byteOffset: this.byteOffset,
      byteLength: l
    };
    return r !== void 0 && (u.target = r), r === Zn.ARRAY_BUFFER && (u.byteStride = e.itemSize * o), this.byteOffset += l, a.bufferViews.push(u), {
      id: a.bufferViews.length - 1,
      byteLength: 0
    };
  }
  processBuffer(e) {
    const t = this.json, i = this.buffers;
    return t.buffers || (t.buffers = [{ byteLength: 0 }]), i.push(e), 0;
  }
}
const rP = 12, Kle = 1415006542, Jle = 1, aP = 8, Qle = 1313821514, $le = 5130562, Zn = {
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  BYTE: 5120,
  UNSIGNED_BYTE: 5121,
  SHORT: 5122,
  UNSIGNED_SHORT: 5123,
  INT: 5124,
  UNSIGNED_INT: 5125,
  FLOAT: 5126,
  ARRAY_BUFFER: 34962,
  ELEMENT_ARRAY_BUFFER: 34963,
  NEAREST: 9728,
  LINEAR: 9729,
  NEAREST_MIPMAP_NEAREST: 9984,
  LINEAR_MIPMAP_NEAREST: 9985,
  NEAREST_MIPMAP_LINEAR: 9986,
  LINEAR_MIPMAP_LINEAR: 9987,
  CLAMP_TO_EDGE: 33071,
  MIRRORED_REPEAT: 33648,
  REPEAT: 10497
};
function ece(s) {
  return new TextEncoder().encode(s).buffer;
}
function kB(s) {
  return Math.ceil(s / 4) * 4;
}
function oP(s, e = 0) {
  const t = kB(s.byteLength);
  if (t !== s.byteLength) {
    const i = new Uint8Array(t);
    if (i.set(new Uint8Array(s)), e !== 0)
      for (let n = s.byteLength; n < t; n++)
        i[n] = e;
    return i.buffer;
  }
  return s;
}
function tce(s) {
  let e;
  if (s.array.constructor === Float32Array)
    e = Zn.FLOAT;
  else if (s.array.constructor === Int32Array)
    e = Zn.INT;
  else if (s.array.constructor === Uint32Array)
    e = Zn.UNSIGNED_INT;
  else if (s.array.constructor === Int16Array)
    e = Zn.SHORT;
  else if (s.array.constructor === Uint16Array)
    e = Zn.UNSIGNED_SHORT;
  else if (s.array.constructor === Int8Array)
    e = Zn.BYTE;
  else if (s.array.constructor === Uint8Array)
    e = Zn.UNSIGNED_BYTE;
  else
    throw new Error(
      "THREE.GLTFExporter: Unsupported bufferAttribute component type: " + s.array.constructor.name
    );
  return e;
}
function ice(s, e, t) {
  const i = {
    min: new Array(s.itemSize).fill(Number.POSITIVE_INFINITY),
    max: new Array(s.itemSize).fill(Number.NEGATIVE_INFINITY)
  };
  for (let n = e; n < e + t; n++)
    for (let r = 0; r < s.itemSize; r++) {
      let a = 0;
      s.itemSize > 4 ? a = s.array[n * s.itemSize + r] : (r === 0 ? a = s.getX(n) : r === 1 ? a = s.getY(n) : r === 2 ? a = s.getZ(n) : r === 3 && (a = s.getW(n)), s.normalized === !0 && (a = nt.normalize(a, s.array))), i.min[r] = Math.min(i.min[r], a), i.max[r] = Math.max(i.max[r], a);
    }
  return i;
}
function nce(s) {
  const e = {
    1: "SCALAR",
    2: "VEC2",
    3: "VEC3",
    4: "VEC4",
    9: "MAT3",
    16: "MAT4"
  }, t = s.itemSize;
  return e[t] || "UNKNOWN";
}
function sce(s) {
  return s.isMesh || s.isSkinnedMesh || s.isInstancedMesh || s.isLine || s.isLineSegments || s.isLineLoop || s.isPoints;
}
function GB(s) {
  return s.type === "Mesh" || s.type === "SkinnedMesh" || s.type === "InstancedMesh" || s.type === "Line" || s.type === "LineSegments" || s.type === "LineLoop" || s.type === "Points";
}
async function rce(s, e, t = "image/jpeg") {
  const i = document.createElement("canvas"), n = i.getContext("2d");
  return i.width = s.width, i.height = s.height, n.drawImage(s, 0, 0), new Promise((r, a) => {
    i.toBlob(
      (o) => {
        o ? r({
          name: e,
          file: o
        }) : a(new Error("Failed to convert ImageBitmap to Blob"));
      },
      t
      /*, qualityArgument 可选，比如压缩质量系数，在JPEG情况下 */
    );
  });
}
class ace {
  constructor() {
    S(this, "loader");
    const e = new gle(), t = new J4();
    t.setDecoderPath("./assets/libs/draco/"), e.setDRACOLoader(t), this.loader = e;
  }
  async importGLB(e) {
    const { scene: t, animations: i } = await this.loader.parseAsync(e, "");
    t.updateMatrixWorld(!0);
    const n = /* @__PURE__ */ new Set(), r = {}, a = /* @__PURE__ */ new Set(), o = {}, l = /* @__PURE__ */ new Set(), c = {}, h = {};
    t.traverse((U) => {
      if (sce(U))
        if (n.add(U.geometry), r[U.geometry.uuid] = U.geometry, Array.isArray(U.material))
          U.material.forEach((W) => {
            const { images: X, textures: K } = lP(W);
            X.forEach((te) => {
              a.add(te);
            }), K.forEach((te) => {
              h[te.uuid] = te;
            }), l.add(W), c[W.uuid] = W;
          });
        else {
          const { images: W, textures: X } = lP(U.material);
          W.forEach((K) => {
            a.add(K);
          }), X.forEach((K) => {
            h[K.uuid] = K;
          }), l.add(U.material), c[U.material.uuid] = U.material;
        }
    });
    const u = [...n], f = await new Zle().write(u);
    u.forEach((U, W) => {
      U.geometrySource = {
        index: 0,
        geomIndex: W
      };
    });
    let p = "geom.bin", v = "application/octet-stream", g = new Blob([f], { type: v });
    const x = [new File([g], p, { type: v })], y = [
      {
        url: ""
      }
    ], A = [...a], b = [];
    A.forEach((U, W) => {
      U.imageSource = {
        index: W
      }, b.push({
        name: "",
        url: "",
        mimetype: ""
      });
    });
    const M = {};
    for (const [, U] of Object.entries(h))
      if (U instanceof Ut) {
        const W = {};
        W[U.source.uuid] = U.source;
        const X = U.toJSON({ images: W, textures: {} });
        X.image = U.source.imageSource, M[U.uuid] = X, U.source.mimeType = U.userData.mimeType ?? "image/png";
      }
    const w = await Promise.all(
      A.map(async (U, W) => {
        let X = `_${W}`;
        const K = U.mimeType ?? "image/png";
        K === "image/png" ? X += ".png" : X += ".jpg", b[W].mimetype = K, b[W].name = X;
        const te = await rce(U.data, X, K);
        return new File([te.file], X, { type: K });
      })
    ), C = [...l], T = [];
    C.forEach((U, W) => {
      U.materialSource = {
        index: W
      };
      const X = U.toJSON({
        textures: h
      });
      for (const [K, te] of Object.entries(X))
        K.toLowerCase().endsWith("map") && (X[K] = M[te]);
      T.push(X);
    });
    const E = {
      geometries: r,
      materials: c,
      textures: h,
      images: o,
      shapes: {},
      skeletons: {},
      animations: {},
      nodes: {}
    }, P = t.toJSON(E).object, L = E.skeletons, I = [];
    for (let U in L)
      I.push(L[U]);
    const N = [];
    return i.forEach((U) => {
      N.push(U.toJSON(U));
    }), HB(P, r), {
      geometries: x,
      images: w,
      scene: {
        object: P,
        resources: {
          materials: T,
          geometries: y,
          images: b,
          skeletons: I,
          animations: N
        }
      }
    };
  }
}
function HB(s, e, t) {
  if (GB(s)) {
    const i = e[s.geometry];
    s.geometry = i.geometrySource;
  }
  if (s.children && s.children.length > 0)
    for (let i = 0; i < s.children.length; i++) {
      const n = s.children[i];
      HB(n, e);
    }
}
function lP(s) {
  const e = /* @__PURE__ */ new Set(), t = /* @__PURE__ */ new Set();
  for (const [i, n] of Object.entries(s))
    n instanceof Ut && (e.add(n.source), t.add(n));
  return {
    images: [...e],
    textures: [...t]
  };
}
const oce = {
  Int8Array,
  Uint8Array,
  Uint8ClampedArray,
  Int16Array,
  Uint16Array,
  Int32Array,
  Uint32Array,
  Float32Array,
  Float64Array
}, cP = {
  BoxGeometry: oi,
  CapsuleGeometry: _u,
  CircleGeometry: o2,
  ConeGeometry: qp,
  CylinderGeometry: Ei,
  DodecahedronGeometry: l2,
  EdgesGeometry: ap,
  ExtrudeGeometry: c2,
  IcosahedronGeometry: h2,
  LatheGeometry: jp,
  OctahedronGeometry: Nr,
  PlaneGeometry: $n,
  PolyhedronGeometry: El,
  RingGeometry: u2,
  ShapeGeometry: d2,
  SphereGeometry: Hs,
  TetrahedronGeometry: f2,
  TorusGeometry: nr,
  TorusKnotGeometry: p2,
  TubeGeometry: kc,
  WireframeGeometry: ZM
};
function hP(s, e) {
  return new oce[s](e);
}
class lce extends As {
  constructor(e) {
    super(e);
  }
  load(e, t, i, n) {
    const r = this, a = this.path === "" ? Rs.extractUrlBase(e) : this.path;
    this.resourcePath = this.resourcePath || a;
    const o = new Nn(this.manager);
    o.setPath(this.path), o.setRequestHeader(this.requestHeader), o.setWithCredentials(this.withCredentials), o.load(
      e,
      function(l) {
        let c = null;
        try {
          c = JSON.parse(l);
        } catch (u) {
          n !== void 0 && n(u), console.error("THREE:ObjectLoader: Can't parse " + e + ".", u.message);
          return;
        }
        const h = c.metadata;
        if (h === void 0 || h.type === void 0 || h.type.toLowerCase() === "geometry") {
          n !== void 0 && n(new Error("THREE.ObjectLoader: Can't load " + e)), console.error("THREE.ObjectLoader: Can't load " + e);
          return;
        }
        r.parse(c, t);
      },
      i,
      n
    );
  }
  async loadAsync(e, t) {
    const i = this, n = this.path === "" ? Rs.extractUrlBase(e) : this.path;
    this.resourcePath = this.resourcePath || n;
    const r = new Nn(this.manager);
    r.setPath(this.path), r.setRequestHeader(this.requestHeader), r.setWithCredentials(this.withCredentials);
    const a = await r.loadAsync(e, t), o = JSON.parse(a), l = o.metadata;
    if (l === void 0 || l.type === void 0 || l.type.toLowerCase() === "geometry")
      throw new Error("THREE.ObjectLoader: Can't load " + e);
    return await i.parseAsync(o);
  }
  parse(e, t) {
    const i = this.parseAnimations(e.animations), n = this.parseShapes(e.shapes), r = this.parseGeometries(e.geometries, n), a = this.parseImages(e.images, function() {
      t !== void 0 && t(c);
    }), o = this.parseTextures(e.textures, a), l = this.parseMaterials(e.materials, o), c = this.parseObject(e.object, r, l, o, i), h = this.parseSkeletons(e.skeletons, c);
    if (this.bindSkeletons(c, h), t !== void 0) {
      let u = !1;
      for (const d in a)
        if (a[d].data instanceof HTMLImageElement) {
          u = !0;
          break;
        }
      u === !1 && t(c);
    }
    return c;
  }
  async parseAsync(e) {
    const t = this.parseAnimations(e.animations), i = this.parseShapes(e.shapes), n = this.parseGeometries(e.geometries, i), r = await this.parseImagesAsync(e.images), a = this.parseTextures(e.textures, r), o = this.parseMaterials(e.materials, a), l = this.parseObject(e.object, n, o, a, t), c = this.parseSkeletons(e.skeletons, l);
    return this.bindSkeletons(l, c), l;
  }
  parseShapes(e) {
    const t = {};
    if (e !== void 0)
      for (let i = 0, n = e.length; i < n; i++) {
        const r = new ll().fromJSON(e[i]);
        t[r.uuid] = r;
      }
    return t;
  }
  parseSkeletons(e, t) {
    const i = {}, n = {};
    if (t.traverse(function(r) {
      r.isBone && (n[r.uuid] = r);
    }), e !== void 0)
      for (let r = 0, a = e.length; r < a; r++) {
        const o = new nh().fromJSON(e[r], n);
        i[o.uuid] = o;
      }
    return i;
  }
  parseGeometries(e, t) {
    const i = {};
    if (e !== void 0) {
      const n = new tS();
      for (let r = 0, a = e.length; r < a; r++) {
        let o;
        const l = e[r];
        switch (l.type) {
          case "BufferGeometry":
          case "InstancedBufferGeometry":
            o = n.parse(l);
            break;
          default:
            l.type in cP ? o = cP[l.type].fromJSON(l, t) : console.warn(`THREE.ObjectLoader: Unsupported geometry type "${l.type}"`);
        }
        o.uuid = l.uuid, l.name !== void 0 && (o.name = l.name), l.userData !== void 0 && (o.userData = l.userData), i[l.uuid] = o;
      }
    }
    return i;
  }
  parseMaterials(e, t) {
    const i = {}, n = {};
    if (e !== void 0) {
      const r = new x2();
      r.setTextures(t);
      for (let a = 0, o = e.length; a < o; a++) {
        const l = e[a];
        i[l.uuid] === void 0 && (i[l.uuid] = r.parse(l)), n[l.uuid] = i[l.uuid];
      }
    }
    return n;
  }
  parseAnimations(e) {
    const t = {};
    if (e !== void 0)
      for (let i = 0; i < e.length; i++) {
        const n = e[i], r = xl.parse(n);
        t[r.uuid] = r;
      }
    return t;
  }
  parseImages(e, t) {
    const i = this, n = {};
    let r;
    function a(l) {
      return i.manager.itemStart(l), r.load(
        l,
        function() {
          i.manager.itemEnd(l);
        },
        void 0,
        function() {
          i.manager.itemError(l), i.manager.itemEnd(l);
        }
      );
    }
    function o(l) {
      if (typeof l == "string") {
        const c = l, h = /^(\/\/)|([a-z]+:(\/\/)?)/i.test(c) ? c : i.resourcePath + c;
        return a(h);
      } else
        return l.data ? {
          data: hP(l.type, l.data),
          width: l.width,
          height: l.height
        } : null;
    }
    if (e !== void 0 && e.length > 0) {
      const l = new g2(t);
      r = new Ru(l), r.setCrossOrigin(this.crossOrigin);
      for (let c = 0, h = e.length; c < h; c++) {
        const u = e[c], d = u.url;
        if (Array.isArray(d)) {
          const f = [];
          for (let p = 0, v = d.length; p < v; p++) {
            const g = d[p], m = o(g);
            m !== null && (m instanceof HTMLImageElement ? f.push(m) : f.push(
              new Qn(
                m.data,
                m.width,
                m.height
              )
            ));
          }
          n[u.uuid] = new wa(f);
        } else {
          const f = o(u.url);
          n[u.uuid] = new wa(f);
        }
      }
    }
    return n;
  }
  async parseImagesAsync(e) {
    const t = this, i = {};
    let n;
    async function r(a) {
      if (typeof a == "string") {
        const o = a, l = /^(\/\/)|([a-z]+:(\/\/)?)/i.test(o) ? o : t.resourcePath + o;
        return await n.loadAsync(l);
      } else
        return a.data ? {
          data: hP(a.type, a.data),
          width: a.width,
          height: a.height
        } : null;
    }
    if (e !== void 0 && e.length > 0) {
      n = new Ru(this.manager), n.setCrossOrigin(this.crossOrigin);
      for (let a = 0, o = e.length; a < o; a++) {
        const l = e[a], c = l.url;
        if (Array.isArray(c)) {
          const h = [];
          for (let u = 0, d = c.length; u < d; u++) {
            const f = c[u], p = await r(f);
            p !== null && (p instanceof HTMLImageElement ? h.push(p) : h.push(
              new Qn(
                p.data,
                p.width,
                p.height
              )
            ));
          }
          i[l.uuid] = new wa(h);
        } else {
          const h = await r(l.url);
          i[l.uuid] = new wa(h);
        }
      }
    }
    return i;
  }
  parseTextures(e, t) {
    function i(r, a) {
      return typeof r == "number" ? r : (console.warn("THREE.ObjectLoader.parseTexture: Constant should be in numeric form.", r), a[r]);
    }
    const n = {};
    if (e !== void 0)
      for (let r = 0, a = e.length; r < a; r++) {
        const o = e[r];
        o.image === void 0 && console.warn('THREE.ObjectLoader: No "image" specified for', o.uuid), t[o.image] === void 0 && console.warn("THREE.ObjectLoader: Undefined image", o.image);
        const l = t[o.image], c = l.data;
        let h;
        Array.isArray(c) ? (h = new Vp(), c.length === 6 && (h.needsUpdate = !0)) : (c && c.data ? h = new Qn() : h = new Ut(), c && (h.needsUpdate = !0)), h.source = l, h.uuid = o.uuid, o.name !== void 0 && (h.name = o.name), o.mapping !== void 0 && (h.mapping = i(o.mapping, cce)), o.channel !== void 0 && (h.channel = o.channel), o.offset !== void 0 && h.offset.fromArray(o.offset), o.repeat !== void 0 && h.repeat.fromArray(o.repeat), o.center !== void 0 && h.center.fromArray(o.center), o.rotation !== void 0 && (h.rotation = o.rotation), o.wrap !== void 0 && (h.wrapS = i(o.wrap[0], uP), h.wrapT = i(o.wrap[1], uP)), o.format !== void 0 && (h.format = o.format), o.internalFormat !== void 0 && (h.internalFormat = o.internalFormat), o.type !== void 0 && (h.type = o.type), o.colorSpace !== void 0 && (h.colorSpace = o.colorSpace), o.minFilter !== void 0 && (h.minFilter = i(o.minFilter, dP)), o.magFilter !== void 0 && (h.magFilter = i(o.magFilter, dP)), o.anisotropy !== void 0 && (h.anisotropy = o.anisotropy), o.flipY !== void 0 && (h.flipY = o.flipY), o.generateMipmaps !== void 0 && (h.generateMipmaps = o.generateMipmaps), o.premultiplyAlpha !== void 0 && (h.premultiplyAlpha = o.premultiplyAlpha), o.unpackAlignment !== void 0 && (h.unpackAlignment = o.unpackAlignment), o.compareFunction !== void 0 && (h.compareFunction = o.compareFunction), o.userData !== void 0 && (h.userData = o.userData), n[o.uuid] = h;
      }
    return n;
  }
  parseObject(e, t, i, n, r) {
    let a;
    function o(d) {
      return t[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined geometry", d), t[d];
    }
    function l(d) {
      if (d !== void 0) {
        if (Array.isArray(d)) {
          const f = [];
          for (let p = 0, v = d.length; p < v; p++) {
            const g = d[p];
            i[g] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", g), f.push(i[g]);
          }
          return f;
        }
        return i[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined material", d), i[d];
      }
    }
    function c(d) {
      return n[d] === void 0 && console.warn("THREE.ObjectLoader: Undefined texture", d), n[d];
    }
    let h, u;
    switch (e.type) {
      case "Scene":
        a = new Da(), e.background !== void 0 && (Number.isInteger(e.background) ? a.background = new _e(e.background) : a.background = c(e.background)), e.environment !== void 0 && (a.environment = c(e.environment)), e.fog !== void 0 && (e.fog.type === "Fog" ? a.fog = new Wu(e.fog.color, e.fog.near, e.fog.far) : e.fog.type === "FogExp2" && (a.fog = new Xp(e.fog.color, e.fog.density)), e.fog.name !== "" && (a.fog.name = e.fog.name)), e.backgroundBlurriness !== void 0 && (a.backgroundBlurriness = e.backgroundBlurriness), e.backgroundIntensity !== void 0 && (a.backgroundIntensity = e.backgroundIntensity), e.backgroundRotation !== void 0 && a.backgroundRotation.fromArray(e.backgroundRotation), e.environmentRotation !== void 0 && a.environmentRotation.fromArray(e.environmentRotation);
        break;
      case "PerspectiveCamera":
        a = new Zt(e.fov, e.aspect, e.near, e.far), e.focus !== void 0 && (a.focus = e.focus), e.zoom !== void 0 && (a.zoom = e.zoom), e.filmGauge !== void 0 && (a.filmGauge = e.filmGauge), e.filmOffset !== void 0 && (a.filmOffset = e.filmOffset), e.view !== void 0 && (a.view = Object.assign({}, e.view));
        break;
      case "OrthographicCamera":
        a = new Xn(
          e.left,
          e.right,
          e.top,
          e.bottom,
          e.near,
          e.far
        ), e.zoom !== void 0 && (a.zoom = e.zoom), e.view !== void 0 && (a.view = Object.assign({}, e.view));
        break;
      case "AmbientLight":
        a = new Gc(e.color, e.intensity);
        break;
      case "DirectionalLight":
        a = new _a(e.color, e.intensity);
        break;
      case "PointLight":
        a = new Tl(e.color, e.intensity, e.distance, e.decay);
        break;
      case "RectAreaLight":
        a = new y2(e.color, e.intensity, e.width, e.height);
        break;
      case "SpotLight":
        a = new oh(
          e.color,
          e.intensity,
          e.distance,
          e.angle,
          e.penumbra,
          e.decay
        );
        break;
      case "HemisphereLight":
        a = new v2(e.color, e.groundColor, e.intensity);
        break;
      case "LightProbe":
        a = new D4().fromJSON(e);
        break;
      case "SkinnedMesh":
        h = o(e.geometry), u = l(e.material), a = hce(a, h, u), e.bindMode !== void 0 && (a.bindMode = e.bindMode), e.bindMatrix !== void 0 && a.bindMatrix.fromArray(e.bindMatrix), e.skeleton !== void 0 && (a.skeleton = e.skeleton);
        break;
      case "Mesh":
        h = o(e.geometry), u = l(e.material), a = uce(a, h, u);
        break;
      case "InstancedMesh":
        h = o(e.geometry), u = l(e.material);
        const d = e.count, f = e.instanceMatrix, p = e.instanceColor;
        a = dce(a, h, u, d), a.instanceMatrix = new hs(
          new Float32Array(f.array),
          16
        ), p !== void 0 && (a.instanceColor = new hs(
          new Float32Array(p.array),
          p.itemSize
        ));
        break;
      case "BatchedMesh":
        a = new THREE.Object3D();
        break;
      case "LOD":
        a = new E4();
        break;
      case "Line":
        a = new ti(o(e.geometry), l(e.material));
        break;
      case "LineLoop":
        a = new ju(o(e.geometry), l(e.material));
        break;
      case "LineSegments":
        a = new _n(o(e.geometry), l(e.material));
        break;
      case "PointCloud":
      case "Points":
        a = new sh(o(e.geometry), l(e.material));
        break;
      case "Sprite":
        a = new wn(l(e.material));
        break;
      case "Group":
        a = new ci();
        break;
      case "Bone":
        a = new ih();
        break;
      default:
        a = new mt();
    }
    if (a.uuid = e.uuid, e.name !== void 0 && (a.name = e.name), e.matrix !== void 0 ? (a.matrix.fromArray(e.matrix), e.matrixAutoUpdate !== void 0 && (a.matrixAutoUpdate = e.matrixAutoUpdate), a.matrixAutoUpdate && a.matrix.decompose(a.position, a.quaternion, a.scale)) : (e.position !== void 0 && a.position.fromArray(e.position), e.rotation !== void 0 && a.rotation.fromArray(e.rotation), e.quaternion !== void 0 && a.quaternion.fromArray(e.quaternion), e.scale !== void 0 && a.scale.fromArray(e.scale)), e.up !== void 0 && a.up.fromArray(e.up), e.castShadow !== void 0 && (a.castShadow = e.castShadow), e.receiveShadow !== void 0 && (a.receiveShadow = e.receiveShadow), e.shadow && (e.shadow.bias !== void 0 && (a.shadow.bias = e.shadow.bias), e.shadow.normalBias !== void 0 && (a.shadow.normalBias = e.shadow.normalBias), e.shadow.radius !== void 0 && (a.shadow.radius = e.shadow.radius), e.shadow.mapSize !== void 0 && a.shadow.mapSize.fromArray(e.shadow.mapSize), e.shadow.camera !== void 0 && (a.shadow.camera = this.parseObject(e.shadow.camera))), e.visible !== void 0 && (a.visible = e.visible), e.frustumCulled !== void 0 && (a.frustumCulled = e.frustumCulled), e.renderOrder !== void 0 && (a.renderOrder = e.renderOrder), e.userData !== void 0 && (a.userData = e.userData), e.layers !== void 0 && (a.layers.mask = e.layers), e.children !== void 0) {
      const d = e.children;
      for (let f = 0; f < d.length; f++)
        a.add(this.parseObject(d[f], t, i, n, r));
    }
    if (e.animations !== void 0) {
      const d = e.animations;
      for (let f = 0; f < d.length; f++) {
        const p = d[f];
        a.animations.push(r[p]);
      }
    }
    if (e.type === "LOD") {
      e.autoUpdate !== void 0 && (a.autoUpdate = e.autoUpdate);
      const d = e.levels;
      for (let f = 0; f < d.length; f++) {
        const p = d[f], v = a.getObjectByProperty("uuid", p.object);
        v !== void 0 && a.addLevel(v, p.distance, p.hysteresis);
      }
    }
    return a;
  }
  bindSkeletons(e, t) {
    Object.keys(t).length !== 0 && e.traverse(function(i) {
      if (i.isSkinnedMesh === !0 && i.skeleton !== void 0) {
        const n = t[i.skeleton];
        n === void 0 ? console.warn("THREE.ObjectLoader: No skeleton found with UUID:", i.skeleton) : i.bind(n, i.bindMatrix);
      }
    });
  }
}
const cce = {
  UVMapping: Ho,
  CubeReflectionMapping: lo,
  CubeRefractionMapping: a0,
  EquirectangularReflectionMapping: ml,
  EquirectangularRefractionMapping: Fc,
  CubeUVReflectionMapping: Qc
}, uP = {
  RepeatWrapping: un,
  ClampToEdgeWrapping: yn,
  MirroredRepeatWrapping: co
}, dP = {
  NearestFilter: Ni,
  NearestMipmapNearestFilter: $c,
  NearestMipmapLinearFilter: $a,
  LinearFilter: ii,
  LinearMipmapNearestFilter: $o,
  LinearMipmapLinearFilter: ds
};
function hce(s, e, t) {
  return s = new fw(e, t), e.__geomIndex !== void 0 && (s.source.indexId = e.__geomIndex), e.__url && (s.source.url = e.__url, s.source.fileType = "bin", s.source.filename = "geom.bin"), s;
}
function uce(s, e, t) {
  return s = new uw(e, t), e.__geomIndex !== void 0 && (s.source.indexId = e.__geomIndex), e.__url && (s.source.url = e.__url, s.source.fileType = "bin", s.source.filename = "geom.bin"), s;
}
function dce(s, e, t, i) {
  return s = new dw(e, t, i), e.__geomIndex !== void 0 && (s.source.indexId = e.__geomIndex), e.__url && (s.source.url = e.__url, s.source.fileType = "bin", s.source.filename = "geom.bin"), s;
}
class fce {
  constructor() {
    S(this, "cache", /* @__PURE__ */ new Map());
  }
}
class pce {
  constructor() {
    S(this, "cache", /* @__PURE__ */ new Map());
  }
}
const _r = class _r {
  constructor() {
    S(this, "_path", "");
  }
  setPath(e) {
    this._path = e;
  }
  async loadAsync(e) {
    const t = JSON.parse(JSON.stringify(e)), i = new Nn();
    i.setResponseType("arraybuffer");
    const n = await Promise.all(
      t.resources.geometries.map(async (p) => {
        const v = Qr(this._path, p.url), g = Wy(this._path, v);
        if (_r.geometryCache.cache.has(g))
          return _r.geometryCache.cache.get(g);
        {
          const m = await dx.getItem(v);
          if (m) {
            const x = await this.parseGeometry(m);
            return _r.geometryCache.cache.set(g, x), x.forEach((y, A) => {
              y.__url = g, y.__geomIndex = A;
            }), x;
          } else {
            const x = await i.loadAsync(v), y = await this.parseGeometry(x);
            return _r.geometryCache.cache.set(g, y), y.forEach((A, b) => {
              A.__url = g, A.__geomIndex = b;
            }), dx.setItem(v, x), y;
          }
        }
      })
    ), r = new A2(), a = await Promise.all(
      t.resources.images.map(async (p) => {
        const v = Qr(this._path, p.url), g = Wy(this._path, v);
        if (_r.imageCache.cache.has(g))
          return _r.imageCache.cache.get(g);
        {
          const m = await r.loadAsync(v);
          return _r.imageCache.cache.set(g, m), m.__url = g, m;
        }
      })
    ), o = t.resources.materials.map((p) => {
      const v = {}, g = /* @__PURE__ */ new Map();
      for (const [x, y] of Object.entries(p))
        if (x.toLowerCase().endsWith("map") && y !== void 0) {
          const A = a[y.image.index], b = this.loadTexture(y, A);
          b.userData.source = {
            url: A.__url,
            indexId: 0,
            image: {
              indexId: 0
            }
          };
          const M = na(A.__url), w = Jce(A.__url);
          b.imageSource.url = A.__url, b.imageSource.indexId = "0", b.imageSource.filename = w ?? "", b.imageSource.fileType = M, v[b.uuid] = b, g.set(b.uuid, b), p[x] = b.uuid;
        }
      const m = Xc(p, g);
      return m.uuid = p.uuid, m;
    });
    VB(t.object, n);
    const l = new lce(), c = {};
    n.forEach((p) => {
      p.forEach((v) => {
        c[v.uuid] = v;
      });
    });
    const h = {};
    o.forEach((p) => {
      h[p.uuid] = p;
    });
    const u = l.parseObject(t.object, c, h, {}), d = l.parseSkeletons(t.resources.skeletons, u);
    return l.bindSkeletons(u, d), this.parseAnimations(l, t), BB(u);
  }
  parseAnimations(e, t) {
    const i = e.parseAnimations(t.resources.animations), n = [];
    for (let r in i)
      n.push(i[r]);
    return i;
  }
  async loadGeometry(e) {
    const t = Qr(this._path, e), i = Wy(this._path, t), n = new Nn();
    if (n.setResponseType("arraybuffer"), _r.geometryCache.cache.has(i))
      return _r.geometryCache.cache.get(i);
    {
      const r = await n.loadAsync(t), a = await this.parseGeometry(r);
      return _r.geometryCache.cache.set(i, a), a.forEach((o, l) => {
        o.__url = i, o.__geomIndex = l;
      }), a;
    }
  }
  async parseGeometry(e) {
    let { content: t, body: i } = this.parseBuffer(e);
    const n = JSON.parse(t), r = new Yle(n, i);
    return await Promise.all(
      n.geometries.map(async (o) => {
        const l = new Pt();
        function c(p, v) {
          return r.getDependency("accessor", p).then(function(g) {
            l.setAttribute(v, g);
          });
        }
        const h = o.data.attributes, u = [];
        for (const p in h)
          u.push(c(h[p], p));
        if (o.data.index !== void 0 && !l.index) {
          const p = r.getDependency("accessor", o.data.index).then(function(v) {
            l.setIndex(v);
          });
          u.push(p);
        }
        const d = o.data.groups || o.data.drawcalls || o.data.offsets;
        if (d !== void 0)
          for (let p = 0, v = d.length; p !== v; ++p) {
            const g = d[p];
            l.addGroup(g.start, g.count, g.materialIndex);
          }
        const f = o.data.boundingSphere;
        if (f !== void 0) {
          const p = new _();
          f.center !== void 0 && p.fromArray(f.center), l.boundingSphere = new Tn(p, f.radius);
        }
        return n.name && (l.name = n.name), n.userData && (l.userData = n.userData), await Promise.all(u), l;
      })
    );
  }
  parseBuffer(e) {
    const t = new TextDecoder();
    t.decode(new Uint8Array(e, 0, 4));
    const i = new DataView(e, 0, Bm), r = {
      magic: t.decode(new Uint8Array(e.slice(0, 4))),
      version: i.getUint32(4, !0),
      length: i.getUint32(8, !0)
    }.length - Bm, a = new DataView(e, Bm);
    let o = 0, l, c;
    for (; o < r; ) {
      const h = a.getUint32(o, !0);
      o += 4;
      const u = a.getUint32(o, !0);
      if (o += 4, u === fP.JSON) {
        const d = new Uint8Array(
          e,
          Bm + o,
          h
        );
        l = t.decode(d);
      } else if (u === fP.BIN) {
        const d = Bm + o;
        c = e.slice(d, d + h);
      }
      o += h;
    }
    return { content: l, body: c };
  }
  loadTexture(e, t) {
    const i = new Wc(t);
    return t && (i.needsUpdate = !0), i.uuid = e.uuid, e.name !== void 0 && (i.name = e.name), e.mapping !== void 0 && (i.mapping = Fm(e.mapping, mce)), e.channel !== void 0 && (i.channel = e.channel), e.offset !== void 0 && i.offset.fromArray(e.offset), e.repeat !== void 0 && i.repeat.fromArray(e.repeat), e.center !== void 0 && i.center.fromArray(e.center), e.rotation !== void 0 && (i.rotation = e.rotation), e.wrap !== void 0 && (i.wrapS = Fm(e.wrap[0], pP)), i.wrapT = Fm(e.wrap[1], pP), e.format !== void 0 && (i.format = e.format), e.internalFormat !== void 0 && (i.internalFormat = e.internalFormat), e.type !== void 0 && (i.type = e.type), e.colorSpace !== void 0 && (i.colorSpace = e.colorSpace), e.minFilter !== void 0 && (i.minFilter = Fm(e.minFilter, mP)), e.magFilter !== void 0 && (i.magFilter = Fm(e.magFilter, mP)), e.anisotropy !== void 0 && (i.anisotropy = e.anisotropy), e.flipY !== void 0 && (i.flipY = e.flipY), e.generateMipmaps !== void 0 && (i.generateMipmaps = e.generateMipmaps), e.premultiplyAlpha !== void 0 && (i.premultiplyAlpha = e.premultiplyAlpha), e.unpackAlignment !== void 0 && (i.unpackAlignment = e.unpackAlignment), e.compareFunction !== void 0 && (i.compareFunction = e.compareFunction), e.userData !== void 0 && (i.userData = e.userData), i;
  }
};
S(_r, "geometryCache", new fce()), S(_r, "imageCache", new pce());
let Fg = _r;
const Bm = 12, fP = { JSON: 1313821514, BIN: 5130562 };
function VB(s, e, t) {
  if (GB(s)) {
    const { index: i, geomIndex: n } = s.geometry;
    s.geometry = e[i][n].uuid;
  }
  if (s.children && s.children.length > 0)
    for (let i = 0; i < s.children.length; i++) {
      const n = s.children[i];
      VB(n, e);
    }
}
function Fm(s, e) {
  return typeof s == "number" ? s : (console.warn("THREE.ObjectLoader.parseTexture: Constant should be in numeric form.", s), e[s]);
}
const mce = {
  UVMapping: Ho,
  CubeReflectionMapping: lo,
  CubeRefractionMapping: a0,
  EquirectangularReflectionMapping: ml,
  EquirectangularRefractionMapping: Fc,
  CubeUVReflectionMapping: Qc
}, pP = {
  RepeatWrapping: un,
  ClampToEdgeWrapping: yn,
  MirroredRepeatWrapping: co
}, mP = {
  NearestFilter: Ni,
  NearestMipmapNearestFilter: $c,
  NearestMipmapLinearFilter: $a,
  LinearFilter: ii,
  LinearMipmapNearestFilter: $o,
  LinearMipmapLinearFilter: ds
};
function gce(s) {
  const e = {
    geometries: {},
    materials: {},
    textures: {},
    images: {},
    shapes: {},
    skeletons: {},
    animations: {},
    nodes: {}
  };
  return s.traverse((t) => {
    if (p0(t)) {
      const i = t;
      e.geometries[i.geometry.uuid] = i.geometry, Array.isArray(i.material) || (e.materials[i.material.uuid] = i.material, Object.entries(i.material).forEach(([n, r]) => {
        r instanceof Ut && (e.textures[r.uuid] = r);
      }));
    }
  }), e;
}
function gP(s) {
  return s != null && s.url !== "" && s.indexId !== "";
}
function vP(s, e) {
  e.geometries[s.geometry] || (gP(s.source) ? e.geometries[s.geometry] = {
    source: s.source
  } : gP(s.userData.source) ? e.geometries[s.geometry] = {
    source: s.userData.source
  } : console.warn("模型mesh数据对glb引用丢失!")), e.materials[s.material] || (e.materials[s.material] = {});
}
function WB(s, e) {
  if ((s.type === "Mesh" || s.type === "SkinnedMesh" || s.type === "InstancedMesh") && (e.geometries[s.geometry] || vP(s, e)), s.children && s.children.length > 0)
    for (let t = 0; t < s.children.length; t++) {
      const i = s.children[t];
      (i.type === "Mesh" || i.type === "SkinnedMesh" || i.type === "InstancedMesh") && vP(i, e), WB(i, e);
    }
}
function vce(s) {
  const e = {
    geometries: {},
    materials: {},
    textures: {},
    images: {},
    shapes: {},
    skeletons: s.skeletons ?? {},
    animations: {},
    nodes: {}
  };
  return WB(s.object, e), e;
}
async function yce(s) {
  Object.entries(s.materials).forEach(([e, t]) => {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i ? s.materials[e] = i : s.materials[e] = new kt();
  });
}
async function xce(s) {
  const e = Object.entries(s.geometries);
  for (let t of e) {
    const i = t[1];
    try {
      if (i.source.url !== void 0 && i.source.indexId !== void 0) {
        const n = na(i.source.url);
        if (n === "glb") {
          const r = st.assetManager.modelManager.getMeshByUrlAndIndexId(
            i.source.url,
            i.source.indexId
          );
          s.geometries[t[0]] = r == null ? void 0 : r.geometry;
        } else if (n === "bin") {
          const r = Fg.geometryCache.cache.get(i.source.url);
          if (r) {
            const a = r[i.source.indexId];
            s.geometries[t[0]] = a;
          }
        }
      }
    } catch {
      throw s.geometries[t[0]] = new Pt(), new Error("load geometry error!");
    }
  }
}
async function Ace(s) {
  const e = vce(s);
  await yce(e), await xce(e), s = { ...s, ...e };
  const t = new SI(), i = Xb(
    s.object,
    s.geometries,
    s.materials,
    s.textures,
    void 0
  ), n = t.parseSkeletons(Object.values(s.skeletons), i);
  return t.bindSkeletons(i, n), i;
}
function XB(s) {
  const e = gce(s);
  return s.updateMatrixWorld(!0), {
    json: OB(s, e),
    resource: e
  };
}
class jB {
  constructor(e, t, i) {
    /**
     * 材质槽关联的Mesh对象
     */
    S(this, "_meshes", []);
    /**
     * 当前材质引用的材质对象
     */
    S(this, "material");
    /**
     * 当前材质Id
     */
    S(this, "materialId");
    /**
     * 初始材质
     */
    S(this, "initMaterial");
    /**
     * 初始材质Id
     */
    S(this, "initMaterialId");
    /**
     * 材质槽名称
     */
    S(this, "slotName", "");
    /**
     * 材质槽Id
     */
    S(this, "slotId", "");
    this.slotName = e, this.slotId = t, this.material = i, this.materialId = i.uuid, this.initMaterial = i, this.initMaterialId = i.uuid;
  }
  set meshes(e) {
    this._meshes = e;
  }
  get meshes() {
    return this._meshes;
  }
  get modelMesh() {
    return this._meshes;
  }
  get id() {
    return this.slotId;
  }
  get name() {
    return this.slotName;
  }
  setMaterial(e) {
    this.material = e, this.materialId = e.uuid, this.assignMeshMaterial();
  }
  setMeshes(e) {
    this._meshes = e, this.assignMeshMaterial();
  }
  assignMeshMaterial() {
    this.material !== void 0 && this._meshes.forEach((e) => {
      e.material = this.material;
    });
  }
  setInitMaterial(e) {
    this.initMaterial = e, this.initMaterialId = e.uuid;
  }
  updateReferencedId() {
    var e, t;
    this.materialId = (e = this.material) == null ? void 0 : e.uuid, this.initMaterialId = (t = this.initMaterial) == null ? void 0 : t.uuid;
  }
  toJSON() {
    var e, t, i;
    return {
      initMaterialId: ((e = this.initMaterial) == null ? void 0 : e.uuid) ?? "",
      materialId: ((t = this.material) == null ? void 0 : t.uuid) ?? "",
      slotId: this.slotId,
      slotName: this.slotName,
      materialName: ((i = this.material) == null ? void 0 : i.name) ?? "",
      meshes: this._meshes.map((n) => ({
        uuid: n.uuid
      }))
    };
  }
}
class jc {
  constructor() {
    S(this, "url");
    S(this, "scene", new ci());
    S(this, "materials", []);
    S(this, "materialSlots", []);
    S(this, "clips", []);
    S(this, "actions", []);
    S(this, "mixer");
    S(this, "geometries", []);
    S(this, "_cachedMeshList", null);
  }
  /**
   * 创建组件
   * @param url
   * @returns
   */
  static async createFromModelUrl(e) {
    const t = await st.assetManager.modelManager.loadAndCacheModelByUrl(e);
    return await jc.createFromLoadedData(t, e);
  }
  /**
   * 加载组件
   * @param json
   * @returns
   */
  static async createFromJSON(e) {
    const t = new jc();
    t.url = e.url;
    const i = await Ace(e.scene);
    return t.scene = i, t.materialSlots = this.createMaterialSlots(e.materialSlots, t.scene), t.scene.traverse((n) => {
      n.receiveProjectVideo = !0;
    }), t;
  }
  setSlotMaterial(e, t) {
    const i = this.findMaterialSlotById(e);
    i && i.setMaterial(t);
  }
  copyMaterialSlotMaterialToInitMaterial(e) {
    const t = this.findMaterialSlotById(e);
    if (t) {
      const i = t.material, n = t.initMaterial;
      if (i && n && i.uuid !== n.uuid) {
        const { name: r, userData: a } = n;
        i.type !== n.type ? n.type === "MeshPhysicalMaterial" && DB(
          i,
          n
        ) : n.copy(i), n.name = r, n.userData = a, n.needsUpdate = !0, t.setMaterial(n);
      }
    }
  }
  async resetSlotInitMaterial(e) {
    const t = this.findMaterialSlotById(e);
    if (t) {
      const i = t.initMaterial;
      i && (i.defaultConfig || i.userData.defaultConfig) && (Mce(i), await wce(i), t.setMaterial(i));
    }
  }
  findMaterialSlotById(e) {
    return this.materialSlots.find((t) => t.slotId === e);
  }
  getMeshList() {
    if (this._cachedMeshList)
      return this._cachedMeshList;
    const e = [];
    return this.scene.traverse((t) => {
      xw(t) && e.push(t);
    }), this._cachedMeshList = e, e;
  }
  toJSON() {
    const { json: e, resource: t } = XB(this.scene);
    return {
      asset: {
        url: this.url ?? ""
      },
      materialSlots: this.materialSlots.map((i) => i.toJSON()),
      sceneGraph: qB(this.scene),
      scene: {
        skeletons: t.skeletons,
        ...e
      }
    };
  }
  dispose() {
    this.clearResources();
  }
  static async createFromLoadedData(e, t) {
    const i = qc.InstantiateModelComponentRenderResources(e);
    return i.url = t, i.scene.traverse((n) => {
      n.receiveProjectVideo = !0;
    }), await Sce(i), yw(i.materials), i.materialSlots.forEach((n) => n.updateReferencedId()), i;
  }
  static createMaterialSlots(e, t) {
    const { meshIndexIdMap: i, meshUUIDMap: n } = Ece(t), r = [];
    for (const a of e) {
      const o = st.assetManager.materialManager.getMaterialByUUID(
        a.initMaterialId
      ), l = st.assetManager.materialManager.getMaterialByUUID(
        a.materialId
      );
      if (o && l) {
        const c = new jB(a.slotName, a.slotId, o);
        bce(a, n, i, c), c.setMaterial(l), r.push(c);
      }
    }
    return r;
  }
  clearResources() {
    this.scene.clear(), this.materials = [], this.materialSlots = [], this.clips = [], this.actions = [], this.mixer = void 0;
  }
}
function bce(s, e, t, i) {
  const n = /* @__PURE__ */ new Set();
  s.meshes.forEach((r) => {
    if (r.uuid !== void 0) {
      const a = e.get(r.uuid);
      a && n.add(a);
    } else if (r.indexId !== void 0) {
      const a = t.get(r.indexId);
      a && n.add(a);
    }
  }), i.meshes = [...n];
}
function Mce(s) {
  Wf(s) && s.restoreDefault();
}
async function Sce(s) {
  const e = s.materials.map((t) => ({
    material: t,
    type: "privateMaterial"
  }));
  await st.assetManager.materialManager.createMaterialsOnServer(e);
}
async function wce(s) {
  for (const [e, t] of Object.entries(s))
    t instanceof Ut && st.assetManager.textureManager.loadTextureImage(t);
  return s.needsUpdate = !0, s;
}
function Ece(s) {
  const e = /* @__PURE__ */ new Map(), t = /* @__PURE__ */ new Map();
  return s.traverse((i) => {
    var n, r;
    if (xw(i)) {
      const a = i.uuid;
      t.set(a, i);
      const o = (r = (n = i.userData) == null ? void 0 : n.source) == null ? void 0 : r.indexId;
      o && e.set(o, i);
    }
  }), {
    meshIndexIdMap: e,
    meshUUIDMap: t
  };
}
function yw(s) {
  s.forEach((e) => {
    st.assetManager.materialManager.addMaterial(e.uuid, e);
  });
}
function xw(s) {
  return s.type === "Mesh" || s.type === "SkinnedMesh" || s.type === "InstancedMesh";
}
function qB(s) {
  const e = {};
  for (const i in s.userData)
    i !== Ya && (e[i] = s.userData[i]);
  const t = {
    name: s.name,
    type: s.type,
    isMesh: xw(s),
    transform: {
      position: [s.position.x, s.position.y, s.position.z],
      rotation: [s.rotation.x, s.rotation.y, s.rotation.z],
      scale: [s.scale.x, s.scale.y, s.scale.z]
    },
    userData: e,
    indexId: `${s.userData.indexId}`,
    children: []
  };
  if (s.children.length > 0)
    for (let i = 0; i < s.children.length; i++) {
      const n = s.children[i];
      n.userData.skipSerialization || t.children.push(qB(n));
    }
  return t;
}
new _();
new Mi();
new _();
class qc extends ra {
  constructor() {
    super(...arguments);
    S(this, "_dracoLoader", new J4());
    S(this, "_path", "");
    S(this, "_meshopt", S0e());
  }
  setDecoderPath(t) {
    this._dracoLoader.setDecoderPath(t);
  }
  setPath(t) {
    this._path = t;
  }
  getAbsoluteUrl(t) {
    return Qr(this._path, t);
  }
  async loadGLBAsync(t, i) {
    const n = this.getLoader(), r = Qr(this._path, t), a = Wy(this._path, r);
    try {
      let o = await this.loadAndParseGLB(r, n, i);
      const { imagesMap: l, textureSourcesMap: c, materials: h, meshesMap: u, meshes: d } = await _ce(o);
      return Pce(h, a), ole(d, a), {
        scene: o.scene,
        imagesMap: l,
        textureSourcesMap: c,
        materials: h,
        meshesMap: u,
        animations: o.animations
      };
    } catch (o) {
      throw console.error(`Failed to load and parse GLB at ${t}:`, o), o;
    }
  }
  async loadGLBFromArrayBuffer(t) {
    return await this.getLoader().parseAsync(t, "");
  }
  static bindAnimationsToModelRenderResource(t, i) {
    if (i.animations.length > 0) {
      t.clips = i.animations.map((r) => r.clone());
      const n = new rS(t.scene);
      t.actions = t.clips.map((r) => n.clipAction(r)), t.mixer = n;
    }
  }
  static InstantiateModelComponentRenderResources(t) {
    const { scene: i, materials: n, meshSetMap: r } = BB(t.scene);
    this.assignIndexId(i);
    const a = new jc();
    return a.scene = i, a.materials = n, YB(a, a.materials, r), this.bindAnimationsToModelRenderResource(a, t), a;
  }
  static assignIndexId(t) {
    let i = 0;
    t.traverse((n) => {
      n.userData = { ...n.userData, indexId: `${i++}` };
    });
  }
  async loadAndParseGLB(t, i, n) {
    const r = await dx.getItem(t);
    let a;
    if (r)
      a = await i.parseAsync(r, "");
    else {
      const o = new Nn();
      o.setResponseType("arraybuffer");
      const l = await o.loadAsync(t, (c) => {
        n == null || n(c);
      });
      a = await i.parseAsync(l, t), dx.setItem(t, l);
    }
    return a;
  }
  getLoader() {
    const t = new w0e();
    return t.setDRACOLoader(this._dracoLoader), t.setMeshoptDecoder(this._meshopt), t;
  }
  dispose() {
    this._dracoLoader.dispose();
  }
}
function YB(s, e, t) {
  s.materialSlots = e.map((i, n) => {
    const r = `${n}`, a = new jB(i.name, r, i);
    return a.meshes = t.get(i.uuid) || [], a;
  }).sort((i, n) => Number(i.slotId) > Number(n.slotId) ? 1 : -1);
}
async function Tce(s) {
  const e = s.parser.sourceCache, t = Object.entries(e).map(([a, o]) => o), i = await Promise.all(t), n = /* @__PURE__ */ new Map(), r = /* @__PURE__ */ new Map();
  return i.forEach((a) => {
    const o = a.userData.source.indexId ?? a.userData.source.image.indexId;
    n.set(`${o}`, a.source.data), r.set(`${o}`, a.source);
  }), { imagesMap: n, textureSourcesMap: r };
}
function Cce(s) {
  const e = /* @__PURE__ */ new Map(), t = /* @__PURE__ */ new Map(), i = /* @__PURE__ */ new Set();
  return s.scene.traverse((n) => {
    if (p0(n)) {
      const { material: r } = n, a = n.userData.source.indexId;
      e.set(a, n), i.add(n), Array.isArray(r) ? r.forEach((o) => {
        t.set(o.uuid, o);
      }) : t.set(r.uuid, r);
    }
  }), {
    meshesMap: e,
    materials: [...t.values()],
    meshes: [...i]
  };
}
async function _ce(s) {
  const { imagesMap: e, textureSourcesMap: t } = await Tce(s), { meshesMap: i, materials: n, meshes: r } = Cce(s);
  return {
    imagesMap: e,
    textureSourcesMap: t,
    meshesMap: i,
    materials: n,
    meshes: r
  };
}
function Wy(s, e) {
  return e.startsWith(s) ? e.slice(s.length) : e;
}
function Pce(s, e) {
  s.forEach((t) => {
    t.userData.source.url = e, Object.entries(t).forEach(([i, n]) => {
      n instanceof Ut && (n.userData.source.url = e);
    });
  });
}
class Rce {
  constructor(e) {
    S(this, "cache", /* @__PURE__ */ new Map());
    S(this, "loader");
    this.loader = e;
  }
  async loadAndCacheModel(e) {
    const t = this.generateKey(e);
    if (this.cache.has(t))
      return this.cache.get(t);
    try {
      const i = await this.loader.loadGLBAsync(e);
      return this.cache.set(t, i), i;
    } catch (i) {
      throw console.error(`Failed to load model at URL ${e}:`, i), i;
    }
  }
  get(e) {
    const t = this.generateKey(e);
    return this.cache.get(t);
  }
  clear() {
    this.cache.forEach((e) => {
      e.materials.forEach((t) => {
        for (const i of Object.values(t))
          i instanceof Ut && i.dispose();
        t.dispose();
      }), [...e.meshesMap.values()].forEach((t) => t.geometry.dispose());
    }), this.cache.clear();
  }
  generateKey(e) {
    return this.loader.getAbsoluteUrl(e);
  }
}
class Dce {
  constructor() {
    S(this, "modelCache");
    S(this, "loader");
    this.loader = new qc(), this.modelCache = new Rce(this.loader);
  }
  setDecoderPath(e) {
    this.loader.setDecoderPath(e);
  }
  setPath(e) {
    this.loader.setPath(e);
  }
  async loadAndCacheModelByUrl(e) {
    return this.modelCache.loadAndCacheModel(e);
  }
  getModelByUrl(e) {
    return this.modelCache.get(e);
  }
  getMeshByUrlAndIndexId(e, t) {
    const i = this.modelCache.get(e);
    if (i)
      return i.meshesMap.get(t);
  }
  getImageByUrlAndIndexId(e, t) {
    var n;
    const i = this.modelCache.get(e);
    if (i)
      return (n = i.textureSourcesMap.get(t)) == null ? void 0 : n.data;
  }
  loadModelByArrayBufferAsync(e) {
    return this.loader.loadGLBFromArrayBuffer(e);
  }
  dispose() {
    this.modelCache.clear();
  }
}
class ZB {
  constructor(e) {
    S(this, "_dataView");
    this._dataView = new DataView(e);
  }
  static concatUint8Arrays(e) {
    let t = e.reduce((a, o) => a + o.length, 0), i = new ArrayBuffer(t), n = new Uint8Array(i), r = 0;
    for (let a of e)
      n.set(a, r), r += a.length;
    return n;
  }
  readUInt32LE(e) {
    return this._dataView.getUint32(e, !0);
  }
  toString(e, t, i) {
    const n = this._dataView.buffer.slice(t ?? 0, i);
    return new TextDecoder(e).decode(n);
  }
  slice(e = 0, t) {
    return e = Math.max(e || 0, 0), t = t !== void 0 ? Math.min(t, this._dataView.buffer.byteLength) : this._dataView.buffer.byteLength, new Uint8Array(this._dataView.buffer, e, t - e);
  }
}
const yP = {
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/ktx": ["ktx"],
  "image/ktx2": ["ktx2"],
  "image/webp": ["webp"],
  "image/vnd-ms.dds": ["dds"],
  "text/plain": ["glsl", "vert", "vs", "frag", "fs", "txt"],
  "audio/wav": ["wav"],
  "application/gltf-buffer": ["glbuf", "glbin"],
  "application/octet-stream": ["bin"]
};
function Lce(s) {
  return s.length < 5 ? !1 : s.substring(0, 5) === "data:";
}
function Ice(s) {
  return Buffer.from(s.split(",")[1], "base64");
}
function Oce(s, e) {
  if (s.uri == null)
    return null;
  if (Lce(s.uri)) {
    const t = s.uri.indexOf(";");
    return t > 0 ? { mimeType: s.uri.substring(5, t), buffer: Ice(s.uri) } : null;
  }
  throw new Error("glb has an external buffer!");
}
function xP(s) {
  return Object.prototype.hasOwnProperty.call(yP, s) ? { extension: "." + yP[s][0], found: !0 } : { extension: ".bin", found: !1 };
}
function p3(s, e, t, i = null) {
  const n = s.buffers[e], r = Oce(n);
  return r != null ? r.buffer : i;
}
function Nce(s) {
  if (s == 0)
    return s;
  const t = s % 4;
  return t === 0 ? s : s + (4 - t);
}
function Bce(s) {
  const e = {
    files: {},
    json: {},
    jsonString: ""
  }, t = "./", i = "./", n = s.readUInt32LE(12), r = s.toString("utf8", 20, n + 20), a = JSON.parse(r), o = s.slice(n + 28), l = [], c = [];
  function h(w) {
    return a.images !== void 0 && a.images instanceof Array ? a.images.filter((C) => C.bufferView === w) : [];
  }
  function u(w, C, T) {
    const E = a.bufferViews[C], P = E.byteOffset === void 0 ? 0 : E.byteOffset, L = E.byteLength, I = w[0], N = xP(I.mimeType), H = N.extension, U = a.images.indexOf(I), W = t + "_img" + U.toString() + H, X = p3(a, E.buffer, i, T);
    if (X === null)
      throw new Error("Content of bufferId " + E.bufferId + " not found.");
    e.files[W] = X.slice(P, P + L), w.forEach((K) => {
      delete K.bufferView, N.found && delete K.mimeType, K.uri = `${W}`;
    });
  }
  function d(w) {
    return a.shaders && a.shaders instanceof Array ? a.shaders.filter((C) => C.bufferView === w) : [];
  }
  function f(w, C, T) {
    const E = a.bufferViews[C], P = E.byteOffset === void 0 ? 0 : E.byteOffset, L = E.byteLength;
    let I = ".glsl";
    const N = 35633, H = 35632, U = w[0];
    U.type == N ? I = ".vert" : U.type == H && (I = ".frag");
    const W = a.shaders.indexOf(U), X = t + "_shader" + W.toString() + I, K = p3(a, E.buffer, i, T);
    if (K === null)
      throw new Error("Content of bufferId " + E.bufferId + " not found.");
    e.files[X] = K.slice(P, P + L), w.forEach((te) => {
      delete te.bufferView, delete te.mimeType, te.uri = `${X}`;
    });
  }
  function p(w, C, T) {
    const E = a.bufferViews[C], P = E.byteOffset === void 0 ? 0 : E.byteOffset, L = E.byteLength, I = w[0], N = xP(I.buffer.mimeType), H = N.extension, U = t + "_" + I.name + "_" + C.toString() + H, W = p3(a, E.buffer, i, T);
    if (W === null)
      throw new Error("Content of bufferId " + E.bufferId + " not found.");
    e.files[U] = W.slice(P, P + L), w.forEach((X) => {
      delete X.buffer.bufferView, N.found && delete X.buffer.mimeType, X.buffer.uri = `${U}`;
    });
  }
  function v(w, C) {
    const T = [];
    if (w.extensions)
      for (const E in w.extensions) {
        const P = w.extensions[E];
        for (const L in P) {
          const I = P[L];
          if (I instanceof Array) {
            const N = E + "_" + L, H = I.filter(
              (U) => U.bufferView === C
            );
            for (const U in H)
              T.push({ buffer: H[U], name: N });
          }
        }
      }
    return T;
  }
  function g(w, C) {
    const T = a.bufferViews[w], E = T.byteOffset === void 0 ? 0 : T.byteOffset, P = T.byteLength, L = Nce(P);
    let I;
    const N = p3(a, T.buffer, i, C);
    if (N === null)
      throw new Error("Content of bufferId " + T.bufferId + " not found.");
    P == L ? I = N.slice(E, E + P) : (I = new Uint8Array(L), I.set(N.slice(E, E + P))), l.push(w), c.push(I);
  }
  if (a.bufferViews)
    for (let w = 0; w < a.bufferViews.length; w++) {
      const C = h(w);
      if (C.length > 0) {
        u(C, w, o);
        continue;
      }
      const T = d(w);
      if (T.length > 0) {
        f(T, w, o);
        continue;
      }
      const E = v(a, w);
      if (E.length > 0) {
        p(E, w, o);
        continue;
      }
      g(w, o);
    }
  const m = [];
  let x = 0;
  for (let w = 0; w < l.length; w++) {
    const C = a.bufferViews[l[w]], T = c[w].length;
    C.buffer = 0, C.byteOffset = x, C.byteLength = T, m.push(C), x += T;
  }
  m.length > 0 ? a.bufferViews = m : delete a.bufferViews;
  function y(w) {
    const C = l.indexOf(w);
    if (C < 0)
      throw new Error("Problem mapping bufferView indices.");
    return C;
  }
  if (a.accessors)
    for (const w of a.accessors)
      w.bufferView !== void 0 && (w.bufferView = y(w.bufferView)), w.sparse && (w.sparse.indices && w.sparse.indices.bufferView !== void 0 && (w.sparse.indices.bufferView = y(
        w.sparse.indices.bufferView
      )), w.sparse.values && w.sparse.values.bufferView !== void 0 && (w.sparse.values.bufferView = y(
        w.sparse.values.bufferView
      )));
  if (a.meshes)
    for (const w of a.meshes)
      for (const C of w.primitives)
        C.extensions && C.extensions.KHR_draco_mesh_compression && (C.extensions.KHR_draco_mesh_compression.bufferView = y(
          C.extensions.KHR_draco_mesh_compression.bufferView
        ));
  const A = t + "_data.bin", b = ZB.concatUint8Arrays(c);
  b.length > 0 ? (e.files[A] = b, a.buffers = [
    {
      uri: `${A}`,
      byteLength: b.length
    }
  ]) : delete a.buffers;
  const M = JSON.stringify(a, null, "  ");
  return e.json = a, e.jsonString = M, e;
}
function Fce(s) {
  const e = new ZB(s);
  return Bce(e);
}
class Uce {
  async checkGLBAsync(e) {
    return {};
  }
  async importGLBAsync(e) {
    const t = await Fce(e);
    return {
      originFile: new Uint8Array(e),
      files: t.files,
      json: t.json
    };
  }
}
function zce(s) {
  return s == null;
}
class kce {
  constructor() {
    S(this, "textures", /* @__PURE__ */ new Map());
    S(this, "images", /* @__PURE__ */ new Map());
    S(this, "_path", "");
  }
  get texturesMap() {
    return this.textures;
  }
  get imagesMap() {
    return this.images;
  }
  setPath(e) {
    this._path = e;
  }
  addTextureImage(e, t) {
    this.images.set(e, t);
  }
  removeTextureImage(e) {
    const t = [];
    e.forEach((i) => {
      const n = this.images.get(i);
      n && t.push(n.serverId), this.images.delete(i);
    }), this.deleteTextureImageOnServer(t);
  }
  getTextureImageByUUID(e) {
    return this.images.get(e);
  }
  getTextureByUUID(e) {
    return this.textures.get(e);
  }
  dispose() {
    this.textures.forEach((e) => {
      e.dispose();
    }), this.textures.clear(), this.images.clear();
  }
  async deleteTextureImageOnServer(e) {
    try {
      e.length > 0 && await Qe.ApiServer.deleteResources({
        ids: e,
        type: "image"
      });
    } catch (t) {
      throw console.error("Failed to delete textureImages on the server:", t), new Error(`Failed to delete textureImages due to ${t}`);
    }
  }
  async loadTextureImageFromUrl(e) {
    const t = new rh(), i = Qr(this._path, e), n = await t.loadAsync(i), r = new Pp(n.image);
    return r.url = e, r;
  }
  async loadTextureImage(e) {
    var t;
    if (!e.image || !e.source.data) {
      const i = e.userData.source ?? { url: "" };
      if (e instanceof Wc && e.imageSource.url !== "" && (i.url = e.imageSource.url), i) {
        const n = na(i.url);
        if (n === "glb" || n === "") {
          const r = ((t = i == null ? void 0 : i.image) == null ? void 0 : t.indexId) ?? i.indexId;
          if (!zce(r)) {
            const a = await st.assetManager.modelManager.getImageByUrlAndIndexId(
              i.url,
              `${r}`
            );
            a && (e.image = a, e.needsUpdate = !0);
          }
        } else {
          const r = await this.loadTextureImageFromUrl(i.url);
          e.image = r.data, e.needsUpdate = !0;
        }
      }
    }
    return e;
  }
}
class KB extends Da {
  constructor(e = null) {
    super();
    const t = new oi();
    t.deleteAttribute("uv");
    const i = new or({ side: Ps }), n = new or(), r = new Tl(16777215, 900, 28, 2);
    r.position.set(0.418, 16.199, 0.3), this.add(r);
    const a = new Re(t, i);
    a.position.set(-0.757, 13.219, 0.717), a.scale.set(31.713, 28.305, 28.591), this.add(a);
    const o = new Re(t, n);
    o.position.set(-10.906, 2.009, 1.846), o.rotation.set(0, -0.195, 0), o.scale.set(2.328, 7.905, 4.651), this.add(o);
    const l = new Re(t, n);
    l.position.set(-5.607, -0.754, -0.758), l.rotation.set(0, 0.994, 0), l.scale.set(1.97, 1.534, 3.955), this.add(l);
    const c = new Re(t, n);
    c.position.set(6.167, 0.857, 7.803), c.rotation.set(0, 0.561, 0), c.scale.set(3.927, 6.285, 3.687), this.add(c);
    const h = new Re(t, n);
    h.position.set(-2.017, 0.018, 6.124), h.rotation.set(0, 0.333, 0), h.scale.set(2.002, 4.566, 2.064), this.add(h);
    const u = new Re(t, n);
    u.position.set(2.291, -0.756, -2.621), u.rotation.set(0, -0.286, 0), u.scale.set(1.546, 1.552, 1.496), this.add(u);
    const d = new Re(t, n);
    d.position.set(-2.193, -0.369, -5.547), d.rotation.set(0, 0.516, 0), d.scale.set(3.875, 3.487, 2.986), this.add(d);
    const f = new Re(t, Od(50));
    f.position.set(-16.116, 14.37, 8.208), f.scale.set(0.1, 2.428, 2.739), this.add(f);
    const p = new Re(t, Od(50));
    p.position.set(-16.109, 18.021, -8.207), p.scale.set(0.1, 2.425, 2.751), this.add(p);
    const v = new Re(t, Od(17));
    v.position.set(14.904, 12.198, -1.832), v.scale.set(0.15, 4.265, 6.331), this.add(v);
    const g = new Re(t, Od(43));
    g.position.set(-0.462, 8.89, 14.52), g.scale.set(4.38, 5.441, 0.088), this.add(g);
    const m = new Re(t, Od(20));
    m.position.set(3.235, 11.486, -12.541), m.scale.set(2.5, 2, 0.1), this.add(m);
    const x = new Re(t, Od(100));
    x.position.set(0, 20, 0), x.scale.set(1, 0.1, 1), this.add(x);
  }
  dispose() {
    const e = /* @__PURE__ */ new Set();
    this.traverse((t) => {
      t.isMesh && (e.add(t.geometry), e.add(t.material));
    });
    for (const t of e)
      t.dispose();
  }
}
function Od(s) {
  const e = new kt();
  return e.color.setScalar(s), e;
}
function Hce(s, e) {
  if (p0(s) && (e.components++, s.geometry instanceof Pt)) {
    const t = s.geometry;
    if (t.index && t.index.count)
      e.index += t.index.count;
    else if (t.attributes.position) {
      const i = t.attributes.position;
      i.count && i.itemSize && (e.index += Math.round(i.count / i.itemSize));
    }
  }
}
function JB(s) {
  const e = [], t = [], i = /* @__PURE__ */ new Set(), n = /* @__PURE__ */ new Set(), r = /* @__PURE__ */ new Set();
  s.traverse((m) => {
    if (p0(m)) {
      const { material: x } = m;
      Array.isArray(x) ? x.forEach((y) => i.add(y)) : i.add(x), n.add(m);
    } else
      r.add(m);
  });
  const a = /* @__PURE__ */ new Set();
  i.forEach((m) => {
    let x;
    for (x in m)
      m[x] instanceof Ut && a.add(m[x]);
  });
  const o = [];
  a.forEach((m) => {
    const { width: x, height: y } = m.image;
    (x > 2048 || y > 2048) && t.push({
      message: `${m.name}: 纹理尺寸超过2048x2048，将影响性能`,
      canOptimize: !0
    }), o.push({
      name: m.name,
      width: x,
      height: y
    });
  }), s.updateMatrixWorld();
  const l = new zt().setFromObject(s), c = new _();
  l.getSize(c);
  const h = new _();
  l.getCenter(h);
  const u = { components: 0, index: 0 };
  n.forEach((m) => {
    Hce(m, u);
  });
  const { json: d, resource: f } = XB(s), p = JSON.stringify({
    skeletons: f.skeletons,
    ...d
  });
  return new Blob([p], { type: "application/json" }).size / (1024 * 1024) * 2 > 6 && e.push({
    message: "模型节点超过2000个，请优化模型内部结构",
    canOptimize: !0
  }), {
    size: c,
    center: h,
    materialCount: i.size,
    textureCount: a.size,
    textures: o,
    triangleCount: u.index / 3,
    meshCount: n.size,
    nodeCount: r.size,
    fileSize: 0,
    errors: e,
    warnings: t
  };
}
class QB {
  constructor() {
    S(this, "scene");
    S(this, "renderer");
    S(this, "camera");
    S(this, "width", 300);
    S(this, "height", 300);
    S(this, "_sphere");
    S(this, "_sphereDefaultMaterial", new or());
    const e = new HM({
      antialias: !0,
      alpha: !0
    });
    e.setClearColor(new _e(0, 0, 0)), e.setClearAlpha(0), e.toneMapping = Hp;
    const t = new Da(), i = new Gc(16777215, 0.5);
    t.add(i);
    const n = new rp(e);
    n.compileEquirectangularShader(), t.environment = n.fromScene(new KB(), 0.04).texture, e.setSize(this.width, this.height);
    const r = new Zt(60, 1, 0.1, 100);
    r.aspect = this.width / this.height, r.updateProjectionMatrix(), this.scene = t, this.renderer = e, this.camera = r, this.camera.position.set(0, 0, 2.5), this._sphere = new Re(new Hs(), this._sphereDefaultMaterial), this._sphere.visible = !1, this._createMultiUV(this._sphere), this.scene.add(this._sphere);
  }
  setSize(e, t) {
    this.renderer.setSize(e, t), this.camera.aspect = this.width / this.height, this.camera.updateProjectionMatrix();
  }
  getModelInfo(e) {
    return JB(e);
  }
  getModelBoundingBox(e) {
  }
  renderModelAndGetBase64Image(e, t) {
    t && this.setSize(t.x, t.y);
    const i = new _();
    e.updateMatrixWorld();
    const n = new zt().setFromObject(e), r = new _();
    n.getSize(r);
    const a = r.length(), o = new _();
    n.getCenter(o);
    const l = new _(-0.2, 0.4, 1.4), { camera: c } = this;
    i.copy(e.position), e.position.x = -o.x, e.position.y = -o.y, e.position.z = -o.z, c.position.copy(o);
    const h = 0.8;
    c.position.x += a * l.x * h, c.position.y += a * l.y * h, c.position.z += a * l.z * h, c.near = a / 100, c.far = a * 100, c.updateProjectionMatrix(), c.lookAt(o);
    const { parent: u } = e;
    this.scene.add(e), this.renderer.render(this.scene, this.camera), this.scene.remove(e), e.position.copy(i), u == null || u.add(e);
    const d = this.renderer.domElement.toDataURL();
    return this.setSize(this.width, this.height), d;
  }
  async renderMaterialAndGetImageBlob(e, t) {
    return t && this.setSize(t.x, t.y), this._sphere.visible = !0, this._sphere.material = Z_(e), this.renderer.render(this.scene, this.camera), K_(this._sphere.material), this._sphere.material.dispose(), this._sphere.visible = !1, this._sphere.material = this._sphereDefaultMaterial, new Promise((i, n) => {
      this.renderer.domElement.toBlob((r) => {
        r ? i(r) : n(new Error("Failed to convert ImageBitmap to Blob")), this.setSize(this.width, this.height);
      });
    });
  }
  renderMaterialAndGetImageURLObject(e, t) {
    return t && this.setSize(t.x, t.y), this._sphere.visible = !0, this._sphere.material = Z_(e), this.renderer.render(this.scene, this.camera), K_(this._sphere.material), this._sphere.material.dispose(), this._sphere.visible = !1, this._sphere.material = this._sphereDefaultMaterial, this.renderer.domElement.toDataURL();
  }
  dispose() {
    this.renderer.dispose();
  }
  _createMultiUV(e) {
    const t = e.geometry.getAttribute("uv");
    e.geometry.setAttribute("uv1", t), e.geometry.setAttribute("uv2", t), e.geometry.setAttribute("uv3", t);
  }
}
new QB();
class Vce {
  constructor() {
    S(this, "materials", /* @__PURE__ */ new Map());
    S(this, "updateBound");
    S(this, "clock");
    this.updateBound = this.update.bind(this), this.clock = new Ju(), requestAnimationFrame(this.updateBound);
  }
  addMaterial(e, t) {
    this.materials.set(e, t);
  }
  removeMaterial(e) {
    this.materials.has(e) && this.materials.delete(e);
  }
  update(e) {
    const t = this.clock.getDelta(), i = [
      "map",
      "aoMap",
      "metalnessMap",
      "roughnessMap",
      "alphaMap",
      "bumpMap",
      "normalMap",
      "displacementMap",
      "emissiveMap",
      "specularMap",
      "envMap",
      "lightMap"
    ];
    this.materials.forEach((n) => {
      if (Kr(n)) {
        const { offsetSpeed: r, rotationSpeed: a } = n.uvAnimationData;
        i.forEach((o) => {
          var l, c, h, u;
          if (n[o]) {
            const d = n[o], f = ((c = (l = d.userData) == null ? void 0 : l.transform) == null ? void 0 : c.offsetSpeed) ?? [0, 0], p = ((u = (h = d.userData) == null ? void 0 : h.transform) == null ? void 0 : u.rotationSpeed) ?? 0;
            d.offset.x += (r.x + f[0]) * t, d.offset.y += (r.y + f[1]) * t, d.offset.x %= 1, d.offset.y %= 1, d.rotation += (a + p) * t / 1e3;
          }
        });
      }
    }), requestAnimationFrame(this.updateBound);
  }
  dispose() {
    this.materials.clear();
  }
}
class $B {
  constructor() {
    S(this, "_modelManager");
    S(this, "_materialManager");
    S(this, "_textureManager");
    S(this, "_modelImporter");
    S(this, "_assetRenderer");
    S(this, "_animMaterialManager");
    S(this, "_path", "");
    // TODO: 方便删除场景材质时调用world的方法来取消场景中模型进行取消场景材质关联
    S(this, "world");
    S(this, "dracoPath", "");
    this._modelManager = new Dce(), this._materialManager = new d0e(), this._modelImporter = new Uce(), this._textureManager = new kce(), this._assetRenderer = new QB(), this._animMaterialManager = new Vce();
  }
  get modelManager() {
    return this._modelManager;
  }
  get materialManager() {
    return this._materialManager;
  }
  get textureManager() {
    return this._textureManager;
  }
  get modelImporter() {
    return this._modelImporter;
  }
  get animMaterialManager() {
    return this._animMaterialManager;
  }
  /**
   * @deprecated
   */
  get materialsMap() {
    return this._materialManager.materialsMap;
  }
  setDracoPath(e) {
    this._modelManager.setDecoderPath(e), this.dracoPath = e;
  }
  setPath(e) {
    this._modelManager.setPath(e), this._textureManager.setPath(e), this._path = e;
  }
  InstantiateModelComponentRenderResources(e) {
    return qc.InstantiateModelComponentRenderResources(e);
  }
  /**
   * @deprecated
   * @param url
   * @returns
   */
  async getGLBMaterialSlotListAsync(e) {
    const t = await st.assetManager.modelManager.loadAndCacheModelByUrl(e);
    return qc.InstantiateModelComponentRenderResources(t).materialSlots.map((n) => n.toJSON());
  }
  async loadAndParseSceneResources(e, t) {
    const i = qce(
      e.resources,
      e.components
    );
    jce(e, i);
    let n = Yce(i, e.components);
    const r = AP(n, ["glb", ""]), a = await this.loadAndCacheGLBList(r, (h) => {
      t == null || t({
        loadedCount: h.loadedCount,
        totalCount: h.totalCount + h.totalCount * 0.2
      });
    }), o = AP(n, ["bin"]);
    await Promise.all(
      o.map((h) => this.loadTwinGeometry(h))
    );
    const l = i.filter((h) => eF(h));
    return Wce(i).forEach((h) => {
      st.assetManager.textureManager.texturesMap.set(h.uuid, h);
    }), await this.loadTextureImages(i), await this.loadMaterials(l), t == null || t({
      loadedCount: 1,
      totalCount: 1
    }), a;
  }
  async loadTextureImages(e) {
    const t = [], i = [];
    e.forEach((n) => {
      if (n.type === "image") {
        const r = n.config, a = na(r.url);
        a === "glb" ? t.push(n) : (a === "png" || a === "jpg" || a === "jpeg") && i.push(n);
      }
    }), t.forEach((n) => {
      const r = n.config, a = st.assetManager.modelManager.getImageByUrlAndIndexId(
        r.url,
        r.indexId
      ), o = new Pp(a);
      o.uuid = n.tag, o.url = n.config.url, o.indexId = n.config.indexId, o.name = n.name ?? n.config.name, o.serverId = n.id, o.originFileMimeType = na(r.url), st.assetManager.textureManager.addTextureImage(o.uuid, o);
    });
    for (let n = 0; n < i.length; ++n) {
      const r = i[n], a = r.config, o = await this.textureManager.loadTextureImageFromUrl(a.url);
      o.uuid = r.tag, o.url = r.config.url, o.indexId = r.config.indexId, o.name = r.name ?? r.config.name, o.serverId = r.id, o.originFileMimeType = na(a.url), st.assetManager.textureManager.addTextureImage(o.uuid, o);
    }
    dt.SceneTextureImagesStore.updateSceneTextureImageList();
  }
  async loadAndCacheModel(e) {
    return this._modelManager.loadAndCacheModelByUrl(e);
  }
  async createModelRenderResourcesAsync(e) {
    return jc.createFromModelUrl(e);
  }
  async syncAssets() {
    const e = await Qe.ApiServer.getSceneResources();
    e.filter((i) => i.type === "texture").forEach((i) => {
      if (!st.assetManager.textureManager.texturesMap.has(i.tag)) {
        const n = O2(i.config);
        n.uuid = i.tag, st.assetManager.textureManager.texturesMap.set(n.uuid, n);
      }
    });
    const t = st.assetManager.textureManager.texturesMap;
    e.filter((i) => i.type === "privateMaterial" || i.type === "publicMaterial").forEach((i) => {
      if (!st.assetManager.materialManager.materialsMap.has(i.tag)) {
        const n = Xc(i.config, t, Xce);
        n.uuid = i.tag, Ng(n, {
          id: i.id,
          name: i.name,
          type: i.type,
          tag: i.tag
        }), st.assetManager.materialManager.addMaterial(n.uuid, n);
      }
    });
  }
  async createModelThumbImage(e, t) {
    const i = await this._modelManager.loadModelByArrayBufferAsync(e);
    return this._assetRenderer.renderModelAndGetBase64Image(i.scene, t == null ? void 0 : t.size);
  }
  async getModelInfo(e) {
    const t = await this._modelManager.loadModelByArrayBufferAsync(e);
    return this._assetRenderer.getModelInfo(t.scene);
  }
  async createMaterialThumbImage(e, t) {
    return this._assetRenderer.renderMaterialAndGetImageBlob(
      e,
      (t == null ? void 0 : t.size) ?? new Se(104, 104)
    );
  }
  createMaterialThumbImageURLObject(e, t) {
    return this._assetRenderer.renderMaterialAndGetImageURLObject(
      e,
      (t == null ? void 0 : t.size) ?? new Se(32, 32)
    );
  }
  async loadAndCacheGLBResourceAsync(e) {
    throw new Error("Method not implemented.");
  }
  async convertGLBToTwinModel(e) {
    const i = await new ace().importGLB(e), n = Qoe.ArrayBuffer.hash(e);
    return i.fileHash = n, i;
  }
  async loadJson(e) {
    const t = Qr(this._path, e);
    return await (await fetch(t)).json();
  }
  async loadTwinModel(e) {
    const t = new Fg();
    return t.setPath(this._path), await t.loadAsync(e);
  }
  async loadTwinGeometry(e) {
    const t = new Fg();
    return t.setPath(this._path), await t.loadGeometry(e);
  }
  dispose() {
    this._materialManager.dispose(), this._modelManager.dispose(), this._textureManager.dispose(), this._animMaterialManager.dispose();
  }
  async loadAndCacheGLBList(e, t) {
    const i = /* @__PURE__ */ new Map();
    let n = 0;
    for (const r of e)
      if (!i.has(r)) {
        const a = this._modelManager.loadAndCacheModelByUrl(r);
        t && a.then(() => {
          n++, t({
            loadedCount: n,
            totalCount: e.length
          });
        }), i.set(r, a);
      }
    return await Promise.all([...i.values()]);
  }
  async loadMaterials(e) {
    const t = /* @__PURE__ */ new Map();
    for (const n of e)
      if (!t.has(n.tag)) {
        const r = this._materialManager.loadMaterial(
          n.tag,
          n.config
        );
        r && t.set(n.tag, r);
      }
    const i = await Promise.all([...t.values()]);
    return i.forEach((n, r) => {
      const a = {
        id: e[r].id,
        name: e[r].name,
        tag: e[r].tag,
        type: e[r].type
      };
      if (n && Ng(n, a), n) {
        for (const [o, l] of Object.entries(n))
          if (l instanceof Ut) {
            const c = l.userData.transform;
            if (c) {
              const h = n.uvAnimationData;
              l.offset.x = c.offset[0] + h.offset.x, l.offset.y = c.offset[1] + h.offset.y, l.repeat.x = c.repeat[0] + h.repeat.x, l.repeat.y = c.repeat[1] + h.repeat.y, l.rotation = c.rotation + (h.rotation ?? 0);
            } else {
              const h = n.uvAnimationData;
              l.offset.x = h.offset.x, l.offset.y = h.offset.y, l.repeat.x = h.repeat.x, l.repeat.y = h.repeat.y, l.rotation = h.rotation ?? 0;
            }
          }
      }
    }), i.forEach((n, r) => {
      n && this._animMaterialManager.addMaterial(n.uuid, n);
    }), i;
  }
}
function Wce(s) {
  return s.filter((e) => e.type === "texture").map((e) => {
    const t = O2(e.config);
    return t.uuid = e.tag, t;
  });
}
async function Xce(s) {
  return kb(s);
}
function jce(s, e) {
  const t = s.resources.reduce((i, n) => (e.some((r) => r.id === n.id) || i.push(n.id), i), []);
  t.length > 0 ? console.log(`${t.length} unused resources deleted`) : console.log("No unused resources to delete.");
}
function qce(s, e) {
  return s;
}
function Yce(s, e) {
  const t = /* @__PURE__ */ new Set();
  return s.forEach((i) => {
    if (eF(i))
      Zce(i.config).forEach((n) => {
        t.add(n);
      });
    else if (i.type === "texture") {
      const n = Kce(i.config);
      n && t.add(n);
    }
  }), e.filter(({ type: i }) => ["Model", "InstanceModel", "BatchModel"].includes(i)).forEach(({ type: i, data: n, states: r }) => {
    var o, l, c, h;
    let a;
    switch (i) {
      case "Model":
      case "InstanceModel":
        a = (o = n == null ? void 0 : n.asset) == null ? void 0 : o.url;
        break;
      case "BatchModel":
        a = (h = (c = (l = r == null ? void 0 : r[0]) == null ? void 0 : l.config) == null ? void 0 : c.asset) == null ? void 0 : h.url;
        break;
    }
    a && t.add(a);
  }), [...t];
}
function eF(s) {
  return ["privateMaterial", "publicMaterial"].includes(s.type);
}
function Zce(s) {
  var t, i;
  const e = [];
  for (const n in s) {
    const r = s[n];
    n.toLowerCase().endsWith("map") && typeof r == "object" && ((i = (t = r.userData) == null ? void 0 : t.source) != null && i.url) && e.push(r.userData.source.url);
  }
  return e;
}
function Kce(s) {
  var e, t;
  if (typeof s == "object" && ((t = (e = s.userData) == null ? void 0 : e.source) != null && t.url))
    return s.userData.source.url;
}
var Jh = /* @__PURE__ */ ((s) => (s[s.loading = 0] = "loading", s[s.loaded = 1] = "loaded", s[s.error = 2] = "error", s))(Jh || {});
function na(s) {
  const e = s.lastIndexOf(".");
  return e === -1 ? "" : s.slice(e + 1);
}
function AP(s, e) {
  const t = new RegExp(
    `(${e.filter((i) => i !== "").join("|")})$|^$`,
    "i"
  );
  return s.filter((i) => {
    const n = na(i);
    return t.test(n);
  });
}
function Jce(s) {
  const e = s.split("/");
  return e[e.length - 1];
}
class Qce {
  static updateSceneOptions(e) {
    return this.cloneAnimationOptionsToModelOptions(e), this._updateCustomAnimationStateConfig(e), e;
  }
  static cloneAnimationOptionsToModelOptions(e) {
    const t = /* @__PURE__ */ new Map();
    e.components.forEach((i) => {
      t.set(i.objId, [...t.get(i.objId) || [], i]);
    }), t.forEach((i) => {
      const n = i.find((a) => a.type === "Animation"), r = i.find((a) => a.type === "Model");
      n && r && !r.data.animation && (r.data.animation = n.data.animation.action);
    });
  }
  // 之前的状态数据 迁移到1.7
  static _updateCustomAnimationStateConfig(e) {
    const { components: t } = e;
    t.filter(
      (n) => n.type === "CustomAnimation"
    ).forEach((n) => {
      let r = t.find(
        (a) => (a.type === "Model" || a.type === "EngineGroup" || a.type === "InstanceModel") && a.objId === n.objId
      );
      if (r && r.states) {
        const [a] = r.states, { transform: o } = (a == null ? void 0 : a.config) ?? {}, { states: l } = n;
        l == null || l.forEach((c) => {
          const h = c.config;
          h.children.forEach((u, d) => {
            if (!$ce(u)) {
              const { times: f } = u;
              if (!f) {
                const p = {
                  times: [],
                  values: [],
                  type: "translate",
                  name: u.name,
                  id: u.id
                };
                if ("mode" in u && "startTime" in u && "endTime" in u && (p.times = [
                  {
                    mode: u.mode,
                    start: u.startTime,
                    end: u.endTime
                  }
                ], "scale" in u && (p.values = [], o && p.values.push({
                  value: o.scale,
                  name: "缩放0"
                }), p.values.push({
                  value: u.scale,
                  name: `缩放${p.values.length}`
                }), p.type = "scale"), "rotation" in u && !("positions" in u) && (p.values = [], o && p.values.push({
                  value: o.rotation,
                  name: "旋转"
                }), p.values.push({
                  value: u.rotation,
                  name: `旋转${p.values.length}`
                }), p.type = "rotate"), "positions" in u)) {
                  p.times = [], p.values = [], o && p.values.push({
                    value: o.position,
                    name: "平移0"
                  });
                  const v = u.positions;
                  v.forEach((g, m) => {
                    p.values.push({
                      value: g,
                      name: `平移${p.values.length}`
                    }), p.times.push({
                      mode: u.mode,
                      start: Number(u.endTime) / v.length * m,
                      end: Number(u.endTime) / v.length * (m + 1)
                    });
                  }), v.length === 1 && p.times.push({
                    mode: u.mode,
                    start: u.startTime,
                    end: Number(u.endTime)
                  }), p.type = "translate";
                }
                h.children[d] = p;
              }
            }
          });
        });
      }
    });
  }
}
function $ce(s) {
  return "axialFactorArr" in s;
}
class ehe {
  constructor() {
    S(this, "textureMap", /* @__PURE__ */ new Map());
    S(this, "textureLoader", new rh());
    S(this, "dracoLoader", new J4());
    S(this, "rgbeLoader", new _te());
    S(this, "exrLoader", new Cte());
    S(this, "dataTextureLoader", new R4());
    S(this, "fileLoader", new Nn());
    S(this, "proxy", {
      Oss: "https://dt-easyv-test.oss-cn-hangzhou.aliyuncs.com"
    });
    S(this, "assetManager", new $B());
    S(this, "videoProjectDefaultTexture");
    S(this, "videoProjectDefaultMaskTexture");
    this.fileLoader.setResponseType("arraybuffer");
  }
  setOssProxy(e) {
    this.proxy.Oss = e, this.proxy.Oss.endsWith("/") || (this.proxy.Oss = `${this.proxy.Oss}/`), this.assetManager.setPath(this.proxy.Oss);
  }
  setDracoPath(e) {
    this.assetManager.setDracoPath(e);
  }
  getOssUrl(e) {
    return Qr(this.proxy.Oss, e);
  }
  getVideoTexture(e) {
    let t = Qr(this.proxy.Oss, e), i = this.textureMap.get(t);
    if (i)
      return i;
    const n = document.createElement("video");
    n.crossOrigin = "anonymous", n.playsInline = !0, n.loop = !0, n.muted = !0;
    const r = na(t);
    if (["m3u8"].includes(r)) {
      t = e;
      const a = new Zo();
      a.loadSource(e), a.attachMedia(n), a.on(Zo.Events.MANIFEST_PARSED, function() {
        n.play();
      });
    } else if (["flv"].includes(r)) {
      t = e;
      const a = Joe.createPlayer({
        type: "flv",
        url: e
      });
      a.attachMediaElement(n), a.load(), a.play();
    } else {
      n.play();
      const a = document.createElement("source");
      a.src = t, a.type = "video/mp4", n.appendChild(a);
    }
    return i = new q9(n), this.textureMap.set(t, i), i;
  }
  getVideoStreamTexture(e) {
    const t = Qr(this.proxy.Oss, e);
    let i = this.textureMap.get(t);
    if (i)
      return i;
    const n = document.createElement("video");
    n.crossOrigin = "anonymous", n.playsInline = !0, n.loop = !0, n.muted = !0;
    const r = new Zo();
    return r.loadSource(t), r.attachMedia(n), r.on(Zo.Events.MANIFEST_PARSED, function() {
      n.play();
    }), i = new q9(n), this.textureMap.set(t, i), i;
  }
  async getVideoProjectDefaultTexture() {
    if (!this.videoProjectDefaultTexture) {
      const e = document.createElement("canvas");
      e.width = 1, e.height = 1;
      const t = e.getContext("2d");
      t.fillStyle = "rgb(63, 72, 204)", t.fillRect(0, 0, 1, 1);
      const i = new Image();
      i.src = e.toDataURL();
      const n = new Promise((r) => {
        const a = new Ut(i);
        i.onload = function() {
          a.needsUpdate = !0, r(a);
        };
      });
      this.videoProjectDefaultTexture = await n;
    }
    return this.videoProjectDefaultTexture;
  }
  async getVideoProjectDefaultMaskTexture() {
    if (!this.videoProjectDefaultMaskTexture) {
      const e = document.createElement("canvas");
      e.width = 1, e.height = 1;
      const t = e.getContext("2d");
      t.fillStyle = "rgba(0, 0, 0, 1)", t.fillRect(0, 0, 1, 1);
      const i = new Image();
      i.src = e.toDataURL();
      const n = new Promise((r) => {
        const a = new Ut(i);
        i.onload = function() {
          a.needsUpdate = !0, r(a);
        };
      });
      this.videoProjectDefaultMaskTexture = await n;
    }
    return this.videoProjectDefaultMaskTexture;
  }
  async loadTexture(e, t = !1) {
    if (e === void 0 || e === "")
      return new Ut();
    const i = Qr(this.proxy.Oss, e);
    let n = this.textureMap.get(i);
    return t && n || (n = await this.textureLoader.loadAsync(i, (r) => {
    }), this.textureMap.set(i, n)), n;
  }
  async loadRgbeTexture(e, t = !1) {
    const i = Qr(this.proxy.Oss, e);
    let n = this.textureMap.get(i);
    if (t && n)
      return n;
    const r = (o) => {
      const l = new Qn(), c = this.rgbeLoader.parse(o);
      if (c.image !== void 0)
        l.image = c.image;
      else if (c.data !== void 0) {
        const { image: h } = l;
        h.width = c.width, h.height = c.height, h.data = c.data;
      }
      return l.wrapS = c.wrapS !== void 0 ? c.wrapS : yn, l.wrapT = c.wrapT !== void 0 ? c.wrapT : yn, l.magFilter = c.magFilter !== void 0 ? c.magFilter : ii, l.minFilter = c.minFilter !== void 0 ? c.minFilter : ii, l.anisotropy = c.anisotropy !== void 0 ? c.anisotropy : 1, c.encoding !== void 0 && (l.colorSpace = c.colorSpace), c.flipY !== void 0 ? l.flipY = c.flipY : l.flipY = !0, c.format !== void 0 && (l.format = c.format), c.type !== void 0 && (l.type = c.type), c.mipmaps !== void 0 && (l.mipmaps = c.mipmaps, l.minFilter = ds), c.mipmapCount === 1 && (l.minFilter = ii), l.needsUpdate = !0, l.mapping = ml, l;
    };
    let a = await ux.getItem(i);
    return a || (a = await this.fileLoader.loadAsync(i), ux.setItem(i, a)), n = r(a), n.mapping = ml, this.textureMap.set(i, n), n;
  }
  async loadExrTexture(e, t = !1) {
    const i = Qr(this.proxy.Oss, e);
    let n = this.textureMap.get(i);
    return t && n || (n = await this.exrLoader.loadAsync(i), n.mapping = Fc, n.colorSpace = "srgb-linear", this.textureMap.set(i, n)), n;
  }
  /**
   * EasyV组件中使用
   * @param resources
   */
  async parseSceneResources(e, t, i) {
    const n = JSON.parse(JSON.stringify(t)), r = JSON.parse(JSON.stringify(e));
    await this.loadAndParseSceneResources(
      {
        entities: r.entities,
        components: r.components,
        resources: n
      },
      i
    );
  }
  async loadObjectsAndSceneResources(e) {
    const { objs: t, components: i } = await Qe.ApiServer.getObjects(e.getSceneId()) ?? {}, n = await Qe.ApiServer.getSceneResources();
    return {
      entities: t,
      components: i,
      resources: n
    };
  }
  async loadWorld(e, t, i = { mode: "Editor" }) {
    const n = Qe.createWorld({ sceneId: e, containerId: t });
    Qe.runningInEditor = !0, n.renderer.rootScene.uuid = e, Qe.ApiServer.sceneId = e, this.assetManager.world = n;
    try {
      const r = await this.loadObjectsAndSceneResources(n);
      let a = r.entities, o = r.components, l = r.resources;
      Qe.mode = i.mode, i.mode === "Editor" && (a = a == null ? void 0 : a.filter((c) => c.type !== "VideoProject")), a && o && l && (await this.loadAndParseSceneResources(
        {
          entities: a,
          components: o,
          resources: l
        },
        n
      ), await this.parseSceneData(t, { entities: a, components: o }, i.mode));
    } catch (r) {
      n.entityManager.updateWorldListStore(), console.error(r);
    }
    return n;
  }
  async loadAndParseSceneResources(e, t) {
    var n, r, a;
    const i = e.components.find((o) => o.type === "Scene");
    if (i) {
      const o = i.data;
      let l = this.getOssUrl(
        ((n = o == null ? void 0 : o.loadingScreen) == null ? void 0 : n.imageUrl) ?? "/easytwin/system/assets/lQLPJxjIe793FrPNAqPNBLCwAoUxBzzWt_cFrlyAt0HAAA_1200_675.png"
      );
      const c = (((r = o == null ? void 0 : o.loadingScreen) == null ? void 0 : r.show) && ((a = o == null ? void 0 : o.loadingScreen) == null ? void 0 : a.imageUrl) !== "") ?? !1, h = document.getElementById(t.containerId);
      !Qe.runningInEditor && c && Qe.loadingScreen.show({
        imageUrl: l,
        parentDom: h
      });
    }
    await this.assetManager.loadAndParseSceneResources(e, (o) => {
      Qe.loadingScreen.setProgress(o.loadedCount / o.totalCount), o.loadedCount === o.totalCount && Qe.loadingScreen.hide();
    }), dt.SceneMaterialsStore.updateSceneMaterialList();
  }
  async parseSceneData(e, t, i = "Easyv") {
    const n = Qe.getWorld(e), r = await Qe.ApiServer.batchGetComponentRel([]);
    try {
      if (n) {
        Qe.mode = i, Qce.updateSceneOptions(t);
        const a = t.entities, { components: o } = t, l = /* @__PURE__ */ new Map();
        o.forEach((u) => {
          const d = l.get(u.objId);
          d ? d.push(u) : l.set(u.objId, [u]);
        });
        const c = [];
        a.forEach((u) => {
          if (u.name !== "初始化实体") {
            const d = n.entityManager.parseEntity({
              id: u.id,
              parentId: u.parentObjId ?? void 0,
              type: u.type,
              name: u.name,
              mPath: u.mpath,
              visible: u.hierarchyConfig.visible,
              inActive: u.hierarchyConfig.inActive,
              commonConfig: u.commonConfig
            });
            Qe.mode === "VideoProjectEditor" && u.type !== "VideoProject" && (d.canSelectedInWorld = !1), n.entityManager.registerCommonConfig(
              u,
              u.commonConfig
            ), d.userData.config.isCollapsed = u.hierarchyConfig.isCollapsed;
            const f = l.get(u.id);
            f == null || f.forEach((p) => {
              Object.assign(p, { rels: r }), n.componentManager.parseComponentOptions(n, d, p, !0);
            }), f && c.push({
              entity: d,
              components: f
            });
          } else {
            const d = l.get(u.id);
            n.setId(u.id), d && n.componentManager.handleInitComponents(n, d);
          }
        }), n.entityManager.handleEntityHierarchy(), this.setEntityHierarchy(n), n.componentManager.setComponentVisible(n), n.entityManager.updateWorldListStore(), Tt.enabled && Qe.ApiServer.getDeletedObjects(n.entityManager.rootScene.uuid).then(
          (u) => {
            u && dt.RecycleEntity.setState(
              n.entityManager.traverseDeleteDataToTree(u)
            );
          }
        );
        const h = setInterval(() => {
          let u = !0;
          n.entityManager.entityMap.forEach((d) => {
            u && (u = d.isInitComplete);
          }), u && (n.isInitComplete = !0, clearInterval(h));
        }, 200);
      }
    } catch (a) {
      console.error(a);
    }
    dt.WorldInitStore.setState(!0);
  }
  setEntityHierarchy(e) {
    e.entityManager.entityMap.forEach((t) => {
      if (t.parentId) {
        const i = e.entityManager.entityMap.get(t.parentId);
        i && i.add(t);
      }
    });
  }
  clear() {
    this.textureMap.forEach((e) => e.dispose()), this.textureMap.clear();
  }
}
const st = new ehe();
const Xy = "engine-draw-point";
let ul = class extends mt {
  constructor(t = "", i, n, r = 0.015, a = "", o = Xy) {
    super();
    S(this, "type", "hollow");
    S(this, "sprite", new wn());
    this.uuid = i, this.name = o, this.sprite.scale.set(r, r, 1), this.sprite.userData.drawId = t, this.userData.drawId = t, this.add(this.sprite), this.sprite.position.copy(n), this.renderOrder = Number.MAX_SAFE_INTEGER, this.setTexture(a), this.type = a, this.sprite.material = new ar({
      sizeAttenuation: !1,
      transparent: !0,
      alphaTest: 0.2,
      depthTest: !1,
      depthWrite: !1
    });
  }
  setTexture(t) {
    if (this.type === t)
      return;
    let i = BC;
    switch (t) {
      case "hollow":
        i = BC;
        break;
      case "solid":
        i = Pne;
        break;
    }
    st.loadTexture(i).then((n) => {
      n.colorSpace = qi, this.sprite.material.map = n, this.sprite.material.needsUpdate = !0;
    }), this.type = t;
  }
  dispose() {
  }
};
class the extends Rl {
  constructor(t, i, n, r) {
    super(x0.DrawUtils, "DrawAddCommand");
    S(this, "dragPoint");
    this.drawUtil = t, this.position = i, this.dragPoint = new ul(r, n ?? nt.generateUUID(), i), this.drawUtil.drawRootPoint.add(this.dragPoint);
  }
  async execute() {
    this.drawUtil.dispatchEvent("add-point", { point: this.dragPoint });
  }
  undo() {
  }
  redo() {
  }
  resetDrawMoveLine() {
    const { geometry: t } = this.drawUtil.drawMoveLine, { length: i } = this.drawUtil.drawRootPoint.children;
    if (i > 0 && (this.drawUtil.mode === "line" || this.drawUtil.mode === "multi-line")) {
      const n = t.getAttribute("position"), r = new _(
        n.array[3],
        n.array[4],
        n.array[5]
      );
      t.setFromPoints([this.drawUtil.drawRootPoint.children[i - 1].position, r]);
    }
  }
  toJSON() {
  }
}
class ihe extends Rl {
  constructor(t, i, n, r) {
    super(x0.DrawUtils, "DrawAddCommand");
    S(this, "drawPoints", []);
    S(this, "group", new mt());
    this.drawUtil = t, this.points = i, this.root = n, i.forEach((a) => {
      const o = new ul(r, nt.generateUUID(), a);
      this.drawPoints.push(o), this.group.add(o);
    }), console.log(n, "root"), n.add(this.group);
  }
  async execute() {
    this.drawUtil.dispatchEvent("add-points", {
      points: this.drawPoints,
      position: this.root.position,
      group: this.group
    });
  }
  undo() {
  }
  redo() {
  }
  toJSON() {
  }
}
const nhe = (s, e) => new s(e);
class bP {
  constructor(e) {
    this.data = e;
  }
  setData(e) {
    this.data = e;
  }
}
class tm {
  constructor(e, t) {
    S(this, "currentState");
    S(this, "listeners", []);
    this.StateClass = t, this.currentState = new bP(e);
  }
  // public reducer() {}
  subscribe(e) {
    return this.listeners.push(e), () => {
      this.listeners.filter((t) => t !== e);
    };
  }
  getSnapshot() {
    return this.currentState;
  }
  // public dispatch(action) {
  //   store.currentState = store.reducer(action);
  //   this.emit();
  //   return action;
  // }
  setState(e) {
    this.currentState = new bP(e), this.emit();
  }
  emit() {
    this.listeners.forEach((e) => e(this.currentState));
  }
  dispose() {
    this.currentState = nhe(this.StateClass, {});
  }
}
class m3 {
  constructor() {
    S(this, "_data", []);
  }
  get data() {
    return this._data;
  }
  set data(e) {
    this._data = e;
  }
  setData(e) {
    this.data = e;
  }
}
class she extends tm {
  constructor() {
    super([], m3);
    S(this, "currentState", new m3());
    S(this, "_enabled", !0);
  }
  get enabled() {
    return this._enabled;
  }
  set enabled(t) {
    this._enabled = t;
  }
  setState(t) {
    this.enabled && (this.currentState = new m3(), this.currentState.setData([...t]), this.emit());
  }
  refreshState() {
    if (!this.enabled)
      return;
    const t = [...this.currentState.data];
    this.currentState = new m3(), this.currentState.setData(t), this.emit();
  }
}
class PA {
  constructor() {
    S(this, "data", []);
  }
  setData(e) {
    this.data = e;
  }
}
class rhe extends tm {
  constructor() {
    super([], PA);
    S(this, "currentState", new PA());
  }
  setState(t) {
    this.currentState = new PA(), this.currentState.setData(t), this.emit();
  }
}
class g3 {
  constructor() {
    S(this, "data", { id: "", name: "", components: [] });
  }
  setData(e) {
    this.data = e;
  }
}
class ahe extends tm {
  constructor() {
    super({ id: "", name: "", components: [] }, g3);
    S(this, "currentState", new g3());
  }
  setState(t) {
    this.currentState = new g3(), this.currentState.setData(t), this.emit();
  }
  refresh() {
    const { data: t } = this.currentState;
    this.currentState = new g3(), this.currentState.setData({ ...t }), this.emit();
  }
}
class RA {
  constructor() {
    S(this, "data", !1);
  }
  setData(e) {
    this.data = e;
  }
}
class ohe extends tm {
  constructor() {
    super(!1, RA);
    S(this, "currentState", new RA());
  }
  setState(t) {
    this.currentState = new RA(), this.currentState.setData(t), this.emit();
  }
}
function lhe(s) {
  return {
    metadata: {
      version: 4.6,
      type: "Texture",
      generator: "Texture.toJSON"
    },
    uuid: s.uuid,
    name: s.name,
    // image: this.source.toJSON( meta ).uuid,
    mapping: s.mapping,
    channel: s.channel,
    repeat: [s.repeat.x, s.repeat.y],
    offset: [s.offset.x, s.offset.y],
    center: [s.center.x, s.center.y],
    rotation: s.rotation,
    wrap: [s.wrapS, s.wrapT],
    format: s.format,
    internalFormat: s.internalFormat,
    type: s.type,
    colorSpace: s.colorSpace,
    minFilter: s.minFilter,
    magFilter: s.magFilter,
    anisotropy: s.anisotropy,
    flipY: s.flipY,
    generateMipmaps: s.generateMipmaps,
    premultiplyAlpha: s.premultiplyAlpha,
    unpackAlignment: s.unpackAlignment
  };
}
async function Yb(s, e, t = "image/jpeg") {
  const i = document.createElement("canvas"), n = i.getContext("2d");
  return i.width = s.width, i.height = s.height, n.drawImage(s, 0, 0), new Promise((r, a) => {
    i.toBlob(
      (o) => {
        o ? r({
          name: e,
          file: o
        }) : a(new Error("Failed to convert ImageBitmap to Blob"));
      },
      t
      /*, qualityArgument 可选，比如压缩质量系数，在JPEG情况下 */
    );
  });
}
function che(s) {
  let e = "image/png";
  if (s.userData.mimeType && (e = s.userData.mimeType), s.image instanceof Image || s.image instanceof HTMLImageElement) {
    const t = na(s.image.src);
    t === "png" ? e = "image/png" : (t === "jpg" || t === "jpeg") && (e = "image/jpeg");
  }
  return e;
}
async function hhe(s) {
  const { json: e, textures: t } = s.toJSON(), i = /* @__PURE__ */ new Set(), n = {};
  for (const [o, l] of Object.entries(t))
    if (l instanceof Ut && l.image) {
      const c = che(l);
      l.image.mimeType = c, i.add(l.image), n[o] = lhe(l), n[o].image = {
        url: i.size - 1
      };
    }
  for (const [o, l] of Object.entries(e))
    o.toLowerCase().endsWith("map") && l !== void 0 && n[l] && (e[o] = n[l]);
  let r = [...i];
  r = (await Promise.all(
    r.map((o, l) => {
      let c = `${l}.png`, h = o.mimeType ?? "image/png";
      return o.mimeType === "image/png" ? c = `${l}.png` : o.mimeType === "image/jpeg" && (c = `${l}.jpg`), delete o.mimeType, Yb(o, c, h);
    })
  )).map((o) => new File([o.file], o.name));
  const a = await st.assetManager.createMaterialThumbImage(s);
  return {
    json: e,
    images: r,
    thumbImage: new File([a], `${s.name}_thumb.png`)
  };
}
class uhe {
  constructor() {
    S(this, "listeners", []);
    S(this, "_data", []);
    this._data = [];
  }
  subscribe(e) {
    return this.listeners = [...this.listeners, e], () => {
      this.listeners = this.listeners.filter((t) => t !== e);
    };
  }
  getMaterialThumbnailsByUUID(e) {
    return st.assetManager.materialManager.getMaterialThumbnailsByUUID(e);
  }
  updateSceneMaterialList() {
    const e = [
      ...st.assetManager.materialManager.materialsMap
    ].map(([t, i]) => {
      const { type: n, id: r, tag: a } = i.serverData ?? i.userData.serverData;
      return {
        name: i.name,
        uuid: i.uuid,
        id: r,
        tag: a,
        type: n
      };
    });
    this._setData(e.filter((t) => t.type === "publicMaterial")), this._emitChange();
  }
  async addSceneMaterial(e, t) {
    let i = new Ml();
    if (t) {
      const a = JSON.parse(JSON.stringify(t)), o = /* @__PURE__ */ new Map();
      for (const [l, c] of Object.entries(a))
        if (l.toLowerCase().endsWith("map") && c !== void 0) {
          const h = st.assetManager.textureManager.getTextureImageByUUID(
            c
          );
          if (h) {
            const u = new Ut(h.data);
            o.set(u.uuid, u), a[l] = u.uuid, u.userData.source = { url: h.url, indexId: h.indexId }, u.name = h.name;
          }
        }
      i = Xc(a, o);
    }
    i.name = e ?? "新建材质";
    const n = N1(i);
    (await Qe.ApiServer.createMaterials([
      {
        name: i.name,
        config: n,
        type: "publicMaterial"
      }
    ])).forEach((a) => {
      i.uuid = a.tag, Ng(i, {
        id: a.id,
        name: i.name,
        tag: a.tag,
        type: "publicMaterial"
      }), st.assetManager.materialManager.addMaterial(i.uuid, i);
    }), this.updateSceneMaterialList();
  }
  async createResourceLibMaterialOptions(e) {
    if (e) {
      const t = JSON.parse(JSON.stringify(e)), i = JSON.parse(JSON.stringify(e)), n = /* @__PURE__ */ new Map();
      for (const [o, l] of Object.entries(t))
        if (o.toLowerCase().endsWith("map") && l !== void 0) {
          const c = st.assetManager.textureManager.getTextureImageByUUID(
            l
          );
          if (c) {
            const h = new Ut(c.data);
            n.set(h.uuid, h), h.name = c.name;
            const u = mw(h);
            u.image = {
              url: c.url
            }, t[o] = u, i[o] = h.uuid;
          }
        }
      const r = Xc(i, n), a = await st.assetManager.createMaterialThumbImage(r);
      return {
        json: t,
        images: [],
        thumbImage: new File([a], `${r.name}_thumb.png`)
      };
    }
  }
  async copyModelMaterials(e) {
    const t = e.flatMap((i) => {
      const n = st.assetManager.materialManager.getMaterialByUUID(i);
      return n ? [n] : [];
    });
    t.length > 0 && (await st.assetManager.materialManager.copyModelMaterialsToSceneMaterialList(
      t
    ), this.updateSceneMaterialList());
  }
  async copySceneMaterial(e) {
    const t = st.assetManager.materialManager.getMaterialByUUID(e);
    if (!t)
      return;
    const i = t.clone();
    i.name = `${i.name} 副本`;
    const n = N1(i);
    (await Qe.ApiServer.createMaterials([
      {
        name: i.name,
        config: n,
        type: "publicMaterial"
      }
    ])).forEach((a) => {
      i.uuid = a.tag, Ng(i, {
        id: a.id,
        name: i.name,
        tag: a.tag,
        type: "publicMaterial"
      }), st.assetManager.materialManager.addMaterial(i.uuid, i);
    }), this.updateSceneMaterialList();
  }
  async removeSceneMaterial(e) {
    const { world: t } = st.assetManager;
    t && t.unLinkAllComponentMaterials(e);
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i && (await st.assetManager.materialManager.deleteMaterialsOnServer(
      [i],
      "publicMaterial"
    ), this.updateSceneMaterialList());
  }
  // async removeModelInitMaterials(uuids: string[]) {
  //   // 删除材质
  //   const materialIds: string[] = [];
  //   const textureIdSet: Set<string> = new Set();
  //   uuids.forEach((uuid) => {
  //     const material = EasyTwinLoader.assetManager.materialsMap.get(uuid);
  //     const { serverData } = material?.userData as MaterialUserData;
  //     if (material && serverData.type === 'privateMaterial') {
  //       // 获取初始材质上的纹理对象id
  //       Object.entries(material).forEach(([key, value]) => {
  //         if (value instanceof THREE.Texture) {
  //           const texture = EasyTwinLoader.assetManager.texturesMap.get(value.uuid);
  //           if (texture) {
  //             const { serverData } = texture.userData as TextureUserData;
  //             textureIdSet.add(serverData.id);
  //             EasyTwinLoader.assetManager.texturesMap.delete(value.uuid);
  //           }
  //         }
  //       });
  //       EasyTwinLoader.assetManager.materialsMap.delete(uuid);
  //       materialIds.push(serverData.id);
  //     }
  //   });
  //   const serverIds = materialIds.concat([...textureIdSet]);
  //   if (serverIds.length > 0) {
  //     await EasyTwinEngine.ApiServer.deleteResources(serverIds);
  //   }
  //   this.updateSceneMaterialList();
  // }
  // async removeUnReferencedTextures() {
  //   const referencedTextures: Map<string, THREE.Texture> = new Map();
  //   EasyTwinLoader.assetManagerV2.materialManager.materialsMap.forEach((item) => {
  //     const material = item;
  //     if (material) {
  //       Object.entries(material).forEach(([key, value]) => {
  //         if (value instanceof THREE.Texture) {
  //           const textureRes = EasyTwinLoader.assetManager.texturesMap.get(value.uuid);
  //           if (textureRes) {
  //             referencedTextures.set(textureRes.uuid, textureRes);
  //           }
  //         }
  //       });
  //     }
  //   });
  //   const unReferencedTextures: Set<string> = new Set();
  //   const textureUUIDs: string[] = [];
  //   // EasyTwinLoader.assetManager.texturesMap.forEach((item) => {
  //   //   const texture = item;
  //   //   if (!referencedTextures.has(texture.uuid)) {
  //   //     const { serverData } = texture.userData as TextureUserData;
  //   //     unReferencedTextures.add(serverData.id);
  //   //     textureUUIDs.push(texture.uuid);
  //   //   }
  //   // });
  //   // 删除缓存中纹理
  //   textureUUIDs.forEach((item) => {
  //     EasyTwinLoader.assetManager.texturesMap.delete(item);
  //   });
  //   // 删除服务器数据
  //   if (unReferencedTextures.size > 0) {
  //     await EasyTwinEngine.ApiServer.deleteResources([...unReferencedTextures]);
  //   }
  // }
  async batchUpdateMaterialConfig2Server(e) {
    const t = [];
    e.forEach((i) => {
      const n = st.assetManager.materialManager.getMaterialByUUID(i);
      if (n) {
        const r = n.serverData ?? n.userData.serverData, a = N1(n);
        t.push({
          name: r.name,
          id: r.id,
          config: a,
          type: r.type
        });
      }
    }), Qe.ApiServer.batchUpdateMaterials(t);
  }
  async copyMaterialToResourceLib(e) {
    const t = st.assetManager.materialManager.getMaterialByUUID(e);
    if (t)
      return hhe(t);
  }
  getSnapshot() {
    return this._data;
  }
  _emitChange() {
    this.listeners.forEach((e) => e());
  }
  _setData(e) {
    this._data = e;
  }
  dispose() {
    this._data = [], this._emitChange();
  }
}
class dhe {
  constructor() {
    S(this, "listeners", []);
    S(this, "_data");
    this.setBlankData();
  }
  subscribe(e) {
    return this.listeners = [...this.listeners, e], () => {
      this.listeners = this.listeners.filter((t) => t !== e);
    };
  }
  getSnapshot() {
    return this._data;
  }
  materialToJSON(e) {
    return N1(e);
  }
  /**
   * 更新材质名称
   * @param uuid
   * @param name
   */
  updateMaterialName(e, t) {
    this.updateMaterialProperty(e, "name", t), dt.SceneMaterialsStore.updateSceneMaterialList();
  }
  /**
   * 更新材质颜色
   * @param uuid
   * @param color
   */
  updateMaterialColor(e, t) {
    this.updateMaterialProperty(e, "color", new _e(t));
  }
  /**
   * 更新金属度
   * @param uuid
   * @param metalness
   */
  updateMaterialMetalness(e, t) {
    this.updateMaterialProperty(e, "metalness", t);
  }
  updateMaterialRoughness(e, t) {
    this.updateMaterialProperty(e, "roughness", t);
  }
  updateMaterialEmissive(e, t) {
    this.updateMaterialProperty(e, "emissive", new _e(t));
  }
  updateMaterialEmissiveIntensity(e, t) {
    this.updateMaterialProperty(e, "emissiveIntensity", t);
  }
  /**
   * 更新材质不透明度
   * @param uuid
   * @param opacity
   */
  updateMaterialOpacity(e, t) {
    this.updateMaterialProperty(e, "opacity", t);
  }
  /**
   * 设置材质是否透明材质
   * @param uuid
   * @param transparent
   */
  updateMaterialTransparent(e, t) {
    this.updateMaterialProperty(e, "transparent", t);
  }
  /**
   *
   * @param uuid
   * @param url
   */
  updateMaterialMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "map");
  }
  updateMaterialMetalnessMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "metalnessMap");
  }
  updateMaterialRoughnessMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "roughnessMap");
  }
  updateMaterialEmissiveMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "emissiveMap");
  }
  updateMaterialNormalMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "normalMap");
  }
  updateMaterialAlphaMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "alphaMap");
  }
  updateMaterialAoMap(e, t) {
    t = t ?? "defaultImage", this.updateMaterialMapProperty(e, t, "aoMap");
  }
  updateMaterialNormalScale(e, t) {
    this.updateMaterialProperty(e, "normalScale", new Se(t.x, t.y));
  }
  updateMaterialAoMapIntensity(e, t) {
    this.updateMaterialProperty(e, "aoMapIntensity", t);
  }
  updateUVRotation(e, t) {
    var n, r;
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    if (i) {
      if (Kr(i)) {
        i.uvAnimationData.rotation = t;
        for (const [, a] of Object.entries(i))
          a instanceof Ut && (a.rotation = i.uvAnimationData.rotation + (((r = (n = a.userData) == null ? void 0 : n.transform) == null ? void 0 : r.rotation) ?? 0));
      }
      this.updateStoreAndServerData(i);
    }
  }
  updateUVScale(e, t) {
    var n, r, a, o;
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    if (i) {
      if (Kr(i)) {
        i.uvAnimationData.repeat = new Se(t.x, t.y);
        for (const [, l] of Object.entries(i))
          l instanceof Ut && (l.repeat.x = i.uvAnimationData.repeat.x + (((r = (n = l.userData) == null ? void 0 : n.transform) == null ? void 0 : r.repeat[0]) ?? 1), l.repeat.y = i.uvAnimationData.repeat.y + (((o = (a = l.userData) == null ? void 0 : a.transform) == null ? void 0 : o.repeat[1]) ?? 1));
      }
      this.updateStoreAndServerData(i);
    }
  }
  updateUVOffset(e, t) {
    var n, r, a, o;
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    if (i) {
      if (Kr(i)) {
        i.uvAnimationData.offset = new Se(t.x, t.y);
        for (const [, l] of Object.entries(i))
          l instanceof Ut && (l.offset.x = i.uvAnimationData.offset.x + (((r = (n = l.userData) == null ? void 0 : n.transform) == null ? void 0 : r.offset[0]) ?? 0), l.offset.y = i.uvAnimationData.offset.y + (((o = (a = l.userData) == null ? void 0 : a.transform) == null ? void 0 : o.offset[1]) ?? 0));
      }
      this.updateStoreAndServerData(i);
    }
  }
  updateUVOffsetSpeed(e, t) {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    if (i) {
      if (Kr(i)) {
        i.uvAnimationData.offsetSpeed = new Se(t.x, t.y);
        for (const [, n] of Object.entries(i))
          n instanceof Ut && (n.offset.setX(t.x === 0 ? 0 : n.offset.x), n.offset.setY(t.y === 0 ? 0 : n.offset.y));
      }
      this.updateStoreAndServerData(i);
    }
  }
  updateUVRotationSpeed(e, t) {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i && (Kr(i) && (i.uvAnimationData.rotationSpeed = t), this.updateStoreAndServerData(i));
  }
  updateMapTextureContrast(e, t) {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i && (Kr(i) && (i.MapContrast = t), this.updateStoreAndServerData(i));
  }
  updateMapTextureBrightness(e, t) {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i && (Kr(i) && (i.MapBrightness = t), this.updateStoreAndServerData(i));
  }
  updateMapTextureSaturation(e, t) {
    const i = st.assetManager.materialManager.getMaterialByUUID(e);
    i && (Kr(i) && (i.MapSaturation = t), this.updateStoreAndServerData(i));
  }
  /**
   * 更新材质纹理UV变换
   * @param materialUUID
   * @param mapSlot
   * @param transform
   */
  updateTextureUVTransform(e, t, i) {
    const n = st.assetManager.materialManager.getMaterialByUUID(e);
    if (n) {
      if (Kr(n)) {
        const r = n[t];
        r && (r.userData.transform = {
          offset: [i.offset.x, i.offset.y],
          repeat: [i.repeat.x, i.repeat.y],
          offsetSpeed: [i.offsetSpeed.x, i.offsetSpeed.y],
          rotationSpeed: i.rotationSpeed,
          rotation: i.rotation
        }, r.offset.x = i.offset.x + n.uvAnimationData.offset.x, r.offset.y = i.offset.y + n.uvAnimationData.offset.y, r.rotation = i.rotation + n.uvAnimationData.rotation, r.repeat.x = i.repeat.x + n.uvAnimationData.repeat.x, r.repeat.y = i.repeat.y + n.uvAnimationData.repeat.y);
      }
      this.updateStoreAndServerData(n);
    }
  }
  /**
   * 设置材质对象
   * @param id
   */
  setMaterialById(e) {
    if (e === void 0) {
      this.setBlankData();
      return;
    }
    const t = st.assetManager.materialManager.getMaterialByUUID(e);
    t ? this.updateStoreData(t) : this.setBlankData();
  }
  _emitChange() {
    this.listeners.forEach((e) => e());
  }
  _setData(e) {
    this._data = {
      ...e,
      uuid: e.tag
    };
  }
  dispose() {
    this.setBlankData();
  }
  setBlankData() {
    const e = {
      type: "privateMaterial",
      uuid: "",
      name: "",
      id: "",
      tag: "",
      config: {}
    };
    this._setData(e), this._emitChange();
  }
  updateMaterialMapProperty(e, t, i) {
    const n = st.assetManager.materialManager.getMaterialByUUID(e), r = st.assetManager.textureManager.getTextureImageByUUID(t);
    if (n) {
      const a = n;
      Wf(a) && a.updateMaterialTextureImage(i, r), a.needsUpdate = !0, this.updateStoreAndServerData(n);
    }
  }
  updateMaterialProperty(e, t, i) {
    const n = st.assetManager.materialManager.getMaterialByUUID(
      e
    );
    n && (n[t] = i, t === "transparent" && (n.needsUpdate = !0), t === "name" && (n.serverData.name = i), this.updateStoreAndServerData(n));
  }
  updateStoreAndServerData(e) {
    Wf(e) && (this.updateStoreData(e), e.needUpdateThumbnail = !0, e.updateToServer(), e.serverData.type === "privateMaterial" && Qe.dispatchEvent("ModelMaterialUpdate", {
      material: e
    })), dt.SceneMaterialsStore.updateSceneMaterialList();
  }
  updateStoreData(e) {
    if (Wf(e)) {
      const t = JSON.parse(JSON.stringify(yr.getMaterialConfig(e))), { type: i, tag: n, id: r } = e.serverData;
      for (const [a, o] of Object.entries(t))
        if (a.toLowerCase().endsWith("map") && o !== void 0) {
          const l = o.userData.transform;
          l ? (o.offset = l.offset, o.rotation = l.rotation, o.repeat = l.repeat, o.offsetSpeed = l.offsetSpeed, o.rotationSpeed = l.rotationSpeed) : (o.offsetSpeed = [0, 0], o.rotationSpeed = 0);
        }
      this._setData({
        type: i,
        tag: n,
        name: e.name,
        id: r,
        config: t
      }), this._emitChange();
    }
  }
}
class v3 {
  constructor() {
    S(this, "_data", {
      drawState: to.None,
      drawEditMode: bn.Create,
      componentId: ""
    });
  }
  get data() {
    return this._data;
  }
  set data(e) {
    this._data = e;
  }
  setData(e) {
    this.data = e;
  }
}
class fhe extends tm {
  constructor() {
    super(
      {
        drawState: to.None,
        drawEditMode: bn.Create,
        componentId: ""
      },
      v3
    );
    S(this, "currentState", new v3());
    S(this, "_enabled", !0);
  }
  get enabled() {
    return this._enabled;
  }
  set enabled(t) {
    this._enabled = t;
  }
  setState(t) {
    this.enabled && (this.currentState = new v3(), this.currentState.setData(t), this.emit());
  }
  refreshState() {
    if (!this.enabled)
      return;
    const { data: t } = this.currentState;
    this.currentState = new v3(), this.currentState.setData(t), this.emit();
  }
}
class phe {
  constructor() {
    S(this, "listeners", []);
    S(this, "_data", []);
    this._data = [];
  }
  updateSceneTextureImageList() {
    const e = [
      ...st.assetManager.textureManager.imagesMap
    ].map(([t, i]) => ({
      name: i.name,
      uuid: i.uuid,
      id: i.uuid,
      tag: i.uuid,
      type: "image",
      url: i.url,
      thumbnail: i.thumbnail,
      originImage: i.data
    }));
    this._setData(e), this._emitChange();
  }
  async addTextureImage(e, t, i) {
    const n = new Pp(i);
    n.url = t, n.name = e;
    const r = n.toJSON(), a = await Qe.ApiServer.createTextures([
      {
        name: e,
        config: r,
        type: "image"
      }
    ]);
    a && a[0] && (n.uuid = a[0].tag, n.serverId = a[0].id, st.assetManager.textureManager.addTextureImage(n.uuid, n)), this.updateSceneTextureImageList();
  }
  removeTextureImages(e) {
    st.assetManager.textureManager.removeTextureImage(e), this.updateSceneTextureImageList();
  }
  extractImage(e) {
    this.updateSceneTextureImageList();
  }
  async copyImageToResourceLib(e) {
    const t = st.assetManager.textureManager.getTextureImageByUUID(e);
    if (!t)
      throw new Error(`Texture image not found for UUID: ${e}`);
    const i = na(t.url);
    if (i === "glb") {
      const a = await Yb(
        t.data,
        `${t.name}.png`,
        "image/png"
      );
      return { file: new File([a.file], `${t.name}.png`) };
    }
    let n;
    i === "png" ? n = "image/png" : i === "jpg" || i === "jpeg" ? n = "image/jpeg" : (n = "image/jpeg", console.warn(`Unsupported file format. Falling back to JPEG for ${t.url}`));
    const r = await Yb(
      t.data,
      t.name,
      n
    );
    return { file: new File([r.file], t.name, { type: n }) };
  }
  subscribe(e) {
    return this.listeners = [...this.listeners, e], () => {
      this.listeners = this.listeners.filter((t) => t !== e);
    };
  }
  getSnapshot() {
    return this._data;
  }
  _emitChange() {
    this.listeners.forEach((e) => e());
  }
  _setData(e) {
    this._data = e;
  }
  dispose() {
    this._data = [], this._emitChange();
  }
}
class DA {
  constructor() {
    S(this, "data", []);
  }
  setData(e) {
    this.data = e;
  }
}
class mhe extends tm {
  constructor() {
    super([], DA);
    S(this, "currentState", new DA());
  }
  setState(t) {
    t = t.filter((i) => i.type !== "BuildingsAnimation" && i.type !== "VideoProject"), this.currentState = new DA(), this.currentState.setData(t), this.emit();
  }
}
const pr = class pr {
  static dispose() {
    Object.values(pr).forEach((e) => {
      "dispose" in e && e.dispose();
    });
  }
  static getStore(e) {
    return pr.storeMap.get(e);
  }
  static setStore(e, t) {
    pr.storeMap.set(e, t);
  }
};
S(pr, "Entity", new ahe()), S(pr, "World", new rhe()), S(pr, "RecycleEntity", new mhe()), S(pr, "DrawUtils", new fhe()), S(pr, "Select", new she()), S(pr, "WorldInitStore", new ohe()), S(pr, "SceneMaterialsStore", new uhe()), S(pr, "SelectMaterialStore", new dhe()), S(pr, "SceneTextureImagesStore", new phe()), S(pr, "storeMap", /* @__PURE__ */ new Map());
let dt = pr;
const ghe = `
  cursor: url('data:image/svg+xml;utf8, <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.4151 6.73532L13.5253 6.27278L12.4144 5.81194L2.41436 1.66365L1.28843 1.19658L1.76207 2.31976L5.97893 12.3198L6.45252 13.4428L6.90407 12.3107L8.47272 8.37787L12.4151 6.73532ZM15.2228 15.6255H15.7228V15.1255V11.6255H16.7228V15.1255V15.6255H17.2228H20.7228V16.6255H17.2228H16.7228V17.1255V20.6255H15.7228V17.1255V16.6255H15.2228H11.7228V15.6255H15.2228ZM16.2228 22.6255C19.8126 22.6255 22.7228 19.7153 22.7228 16.1255C22.7228 12.5356 19.8126 9.62549 16.2228 9.62549C12.6329 9.62549 9.72278 12.5356 9.72278 16.1255C9.72278 19.7153 12.6329 22.6255 16.2228 22.6255Z" fill="white" stroke="black"/> </svg>')
  0 0,
  auto !important;
`, vhe = `
  cursor: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAHKSURBVHgBtVW7cYNAEF3JDhyizOGFzkwHQKaMEpArAFUArgBRASWohBtXQKoMXAEoc3beFXvMSRaybgRv5o049rj9vN0TQI8NUsAMWPLvO7JGlkgfZoCLVHmeKyGEYmcR0oEJIff7vSJIKVUUReSohT4rARPARygTdV2rLMt0VhL6rB5CS9FfQ1mWigKAvnw53JnV08X6xXEcf71e/9noui5gJnA8Hp3D4fCGr16RX8gfsICzWq1axBA5PVP0nudpTVJ4UPw8TdPBAYtdIT2YCD6WaXDAAouRvYKdZ2AJSWJTaaDvnmugMtVJkuggErBASB3DdR9rzR0drtuZsoZ+YO8CRUeC1jf2SOwsVVXV6QZgByFYgLqlvPI+5kgFsjKmPjXsQm9ewjgK5KexFhz1Dnpd/Iv9ZN+RHTWhzDN6+Tx+PnTM4XC8NkQcx7Ddbh2dHZaG7hhYLBYbWqNup3UQBGnTNN0tB2fAgwR9rA8Zwsbp7rrubE0MwxCKorCaHap7fXHLtoYGVDZJnUfis9164gV/GPNaGg70/4c07HB3iRgN8oOfSWzfsGXIb2QAE0FS/+sLkafZag7+g4BzDXKYAT5PcAUz4man/AI8/AveKdu17gAAAABJRU5ErkJggg==')
  0 0,
  auto !important;
`, yhe = `
  cursor: default auto !important;
`, xhe = `data:image/png;base64,
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
`, Ahe = `data:image/png;base64,
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
`, bhe = `data:image/png;base64,
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
`, Mhe = `data:image/png;base64,
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
`, She = `data:image/png;base64,
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
`, whe = `data:image/png;base64,
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
`, tF = `data:image/png;base64,
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
`, iF = `data:image/png;base64,
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
`, Ehe = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAVOSURBVHgB7ZbdThtHFMfP2OuPgGlNKiUQICzEBhTxZaDlolKaSG0v2pte9Kq9gSdo8wSgvkDzBoU36E0uetGCopaGr9ACSuQiwlLVGFMExlBIwbvTc2ZmnbWx8YqPVpV8pNHZnZ3Z+e1/zjmzABWrWMX+38bOesg5D6P7Att9bDo2A9s4tknGmAH/gpUERDgd3UT8t1U9Hl+Bvcw+1ISqoaM9Ch0dUQOfjWEbv2pQVgKOlFuYnpnXl5ZfiC7Lshi3OFicQwhB3x6IIWzEuGrQUoAjL18aoz9OzYg7y0Q4G5IMQU28rqmphoG+Xmi/QlBWBE5HN/H48Xd6Zv+A7oVqBCWuLSt3b3FL+FBNCPr7uqG97fJBiwGOrP/+x+j8/ALCCKWoU4AJLlROqGpxJvvkNeFXV1ex/r4eaIveMS4LlBXA6egmvv/hiX5wcCCUElRopopBG4jUAwVM15ZSWCiKMdoX64ZotNW4KGgh4FBiI/nN4uKyUstktmpye+W2CkVtNUVIihsmnktVxYeQojECjZwftBBwbernmebDw0OwE4FzKwfb0HALkslNOD4+ycHasSmVlcmEF2DmZX0VxHq6IHIOUOaA07PZ7NpPU0/BfjFXCQEyg4EANzYI8FiAW7nEeZ1AtldzmGXDYyWoxq3v7emEyJ0WA58/RMhvywFqzhvTNMGnaXJBkIqoxZmHMdjcTHF0oGle7PMoMAljUiniHjXHI+d4PDYws5gH/n71ik9NzbC6mzd0jNM0uLAcIEmOL0sHAoEwKknKMa6y1BSZ61UQHAiWKyiV4TidYHJweSXJy712HLNIpJkj3BOcMOkG0FNwP97U1CgU0rzYfBrXNI35fD7u9XqZD/t9so+eY78mx6KX1z7lVT96nybn2n1dnR0UVqPg0gqTRBxxqVSqeSO5aSuYq4F0baqMtrPXeQRyaVI5Mc5ZASxOHx/r7VoH+cNxlj1ChdOnABWkjm4CE0FPpbZgZ2cnDxIzl+VOEeTMlRVnUVcJ5jweKV7fu/cuSySS+OORyQ8DlWShUAh6uu8auH7MBtROSSrTv4VqIn7xyE0M6K2tLdhNp8UiXKqDECrWMObsQu3FaLNMdV4rtUWiYGGva7gB+3h0Pp2eE8mI8QtZMyuUzWYt0ffB+/dIsHEbrqiCRRQdQjdCihJoOr0nMpxbKjtzYWAXcXCc15ZdEWDwnQGYnX0Gm6k/CZxLSBOyJyYjX1V1DT7/7FMS54GzRnrKAeLgMWwtfr9/uLGx0Whri8Jb168zFfgyYahRouSSQzZKLr/fxxtu1cPR0RHs4/EZDAZ5MOBnAb9swWAAgtcCMDjYD1CkgJdVsJSiJycn+vb2NinKX8dorm7aZ7ZQubu7E9aMdVHkqYTR9goFScmsyQIBP3z80YdGoXrnAnSA7pJH0DCBZjKZvDPbPvrCtWFovn3bQDD918UlfvjXETMtE+NObi3GLO/sugtNjQ1fIdxo4Tplt7gE3BC6NL6wFrdxuL6+3mhtbWW1tbXg9crttWtffV0dTRnG64dt0YjYUjwM2LVgALc6AG+G32AIR6VnzM3CYXBh9FOhIPOgqR+TiSeTSR6Px4VHm3SMmUgkEnxh4Rc+N/eMT0/PWljKaMyom0V1WsDFuKGzxjlBqaHdL1hjd3V1lS8tLfPnz19w9bE6XCLgKfXOAP2ySP8nBL6ysmLt7e25U88NIG0/tq+xLcAFDd/xSMG5U88BaE8q1nYpntzGaZm1wuqdo+XGFh51BrYHJcamnUfQRYzeg3AxN+8rdRZfubn92HPVwf/EVFwMQcUqVrE8+wepn/qwkgj5tgAAAABJRU5ErkJggg==", The = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAAgCAYAAAAffCjxAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAOVSURBVHgBpVXPa5xVFL33vfdlpoWYuBBECE0KKnVTFTfioknUtRPcKEit/gX+Q2L/ASXduKmYaTbiaiKCUVPoCEK7CCSGNnbme+9ez3ljo/2Vgc4b7sw378d555573vtUntD2dj9ZVcufmsqqup0TFxXxgWb5qWS79upbX28+ukYfPLi7/v7z5yuxab8UAmBE1TiCscIJ4sVci4uNdZjbsn7h7c3hY0B7e5ffCO7fh5AWEBqCYqXha+RuWd1bsWLiGdFiQQ6HNrK1V97Z3DkB2h18tDx3Ng1CbBZi7GqMZyQGwAoBjkHm2K2MtJSWrACGrjHWjsPB+FjevLC2OQwEih3dUtXFGBpNqSupmZem86I2zXOS0hlJkf3iCIkJgoWiCNAozzdN+aoy+u2Xj3sx+jcRk5vmrGCxN3MvSKfzmrbtr8juNtgcSil3wWgkJWexbG4jZD5SRBB0rydU5QN18BEKWyAwBPBjzXkXz3clRMOvTjSjFPh2dgWXSSkMjOPlhJHXqVQIhiljaHIf8Rcm35NYQUYYLlKYEYWIGMqOvVFmNW7hqOVqwqyLtcRkU11wT1kW1cS9NZBuMqc9AniX6injTK2WMLjNdCUJ4LAL+9RQjgDPoNQoeSDMJAeBuBglA+ECZxf+ggvmKXNNiCEGzpEfPmLGCAxaUjKgOLGUombG8sP0/sAGYEPpfIdp971UivhfnL+l5OoZRtuOEGPJuWXF3FrMg8OxA4GqGKC+E5DVVchDqnCuK93LwBFAtIhMEK/P94t6W7yM4HgAKqVD1p79WnX2zcGHWxpsVVOp5YapJaSqJsUGSSwkaxyP6p8xg1LSNfHW+feun0/V2VI+s1YHkGLRWd5ILbyapuC8sZpIiSWrR4NnzSpIOAxtXH/o0P7548ZFbNKXUBZhfRjLqKTUClHRyoiHtWqDggEk6trK2vWdh4DYbv3QW0bVtgC0rLwvanb27xXiBIWQ1UY3ujp35aV3v/3jsWvk/+3mdq+HbXvmzaXofy/TsFg8BM4NKHb15fe/68vTLrYnte3t7UudTqcPtf32nTvrGxsb/afNTacBLS0t1Z329/dP3XAqENvCwoLMz88zuVNbmDJ+wqTb7c4E9B+RnJ8dKOesJ0gpPTtQSqni4A6ZJtHU1KpIvNRmBpqgzciIGsnkVTsbI8rLtPiOmalqULu+qHhWp1VtqrNJqjKatR0dHV05ODj4Ytq8fwCe8lgovXUPRwAAAABJRU5ErkJggg==";
var Che = Object.defineProperty, _he = Object.getOwnPropertyDescriptor, Phe = (s, e, t, i) => {
  for (var n = _he(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && Che(e, t, n), n;
};
class nF {
  constructor() {
    S(this, "id", "engine-cursor-style");
    // 记录第一次设置鼠标样式, 防止tick 中 不断设置成默认样式
    S(this, "_fromMap", /* @__PURE__ */ new Map());
    S(this, "_getFromSetKey", (e, t) => `${e}-${t}`);
    try {
      const e = document.createElement("style");
      document.body.appendChild(e);
      const t = `.${this.id}_default {${yhe}}`, i = `.${this.id}_dragging {${vhe}}`, n = `.${this.id}_create {${ghe}}`;
      e.appendChild(document.createTextNode(t)), e.appendChild(document.createTextNode(i)), e.appendChild(document.createTextNode(n));
    } catch (e) {
      console.error(e);
    }
  }
  setCursorCssText(e, t, i = "draw") {
    switch (t) {
      case "pointer":
        e.classList.forEach((n) => {
          n.includes(this.id) && e.classList.remove(n);
        }), e.style.cursor = t;
        break;
      default:
        e.classList.forEach((n) => {
          n.includes(this.id) && e.classList.remove(n);
        }), e.classList.add(`${this.id}_${t}`), e.style.cursor = "default";
        break;
    }
  }
}
Phe([
  Qp(0.1)
], nF.prototype, "setCursorCssText");
const cs = new nF();
class Rhe {
  constructor() {
    S(this, "id", "engine-style");
    try {
      const e = document.createElement("style");
      document.body.appendChild(e), this._addTransitionRule(e);
    } catch (e) {
      console.error(e);
    }
  }
  _addTransitionRule(e) {
    const t = `
      .${this.id}_transition_show {
        opacity: 1;
        transform-origin: center bottom;
        transition-property: opacity;
        transition-duration: 0.5s;
      }
    `;
    e.appendChild(document.createTextNode(t));
    const i = `
      .${this.id}_transition_hide {
        opacity: 0;
        transform-origin: center bottom;
        transition-property: opacity;
        transition-duration: 0.5s;
      }
    `;
    e.appendChild(document.createTextNode(i));
  }
  setTransitionCssText(e, t) {
    const i = `${this.id}_transition_${t}`;
    e.classList.forEach((n) => {
      n.includes(`${this.id}_transition`) && e.classList.remove(n);
    }), e.classList.add(i);
  }
}
const B1 = new Rhe(), LA = "engine-draw-move-rect", Zb = "engine-draw-line-mid-point";
class Dhe extends es {
  constructor(t, i, n) {
    super();
    S(this, "handleMouseDownBindThis", this.handleMouseDown.bind(this));
    S(this, "handleMouseMoveBindThis", this.handleMouseMove.bind(this));
    S(this, "handleMouseUpBindThis", this.handleMouseUp.bind(this));
    S(this, "ray", new lr());
    S(this, "drawRootPoint", new mt());
    S(this, "intersectParams", []);
    S(this, "drawMoveLine", new ti(
      new Pt(),
      new Bi({ color: 16777215 })
    ));
    S(this, "_mode", "multi-point");
    S(this, "_activeComponent");
    S(this, "_lastUpTime", performance.now());
    // 记录上一次的时间
    S(this, "_lineMidPoint", new ul(
      "",
      nt.generateUUID(),
      new _(),
      0.01,
      "solid",
      Zb
    ));
    S(this, "_moveRectMesh", new DF({
      id: nt.generateUUID(),
      name: LA,
      width: Ox.x,
      height: Ox.y,
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      customAttribute: {},
      constrainedScale: !1
    }));
    S(this, "_transformControls");
    S(this, "_polygonUtils", new sF(this));
    S(this, "root", new mt());
    S(this, "_state", to.None);
    S(this, "_editMode", bn.Create);
    this.world = t, this.domElement = i, this.camera = n, this.world.renderer.rootScene.add(this.root), this._lineMidPoint.visible = !1, this._moveRectMesh.visible = !1, this._transformControls = new BS(this.camera, this.domElement), this.world.add(this._transformControls), this._handleTransformControlsEvent(), window.addEventListener("beforeunload", () => {
      this.activeComponent && (this.activeComponent.needDataUpdateServer = !0);
    });
  }
  get activeComponent() {
    return this._activeComponent;
  }
  get transformControls() {
    return this._transformControls;
  }
  get polygonUtils() {
    return this._polygonUtils;
  }
  getLineMidPoint() {
    return this._lineMidPoint;
  }
  setActiveComponent(t) {
    this._activeComponent = t, dt.DrawUtils.setState({
      drawEditMode: this._editMode,
      drawState: this.state,
      componentId: this._activeComponent ? this._activeComponent.id : ""
    });
  }
  get mode() {
    return this._mode;
  }
  set mode(t) {
    this._state !== to.None && t !== this._mode && (this.endDraw(), this.startDraw()), this._mode = t;
  }
  set state(t) {
    this._state = t, dt.DrawUtils.setState({
      drawEditMode: this._editMode,
      drawState: this.state,
      componentId: this._activeComponent ? this._activeComponent.id : ""
    });
  }
  get state() {
    return this._state;
  }
  set editMode(t) {
    switch (this._editMode = t, this._moveRectMesh.visible = !1, dt.DrawUtils.setState({
      drawEditMode: this._editMode,
      drawState: this.state,
      componentId: this._activeComponent ? this._activeComponent.id : ""
    }), this.drawRootPoint.children.forEach((i, n) => {
      i instanceof ul && i.setTexture("hollow");
    }), t) {
      case bn.Create:
        cs.setCursorCssText(this.domElement, "create");
        break;
      case bn.Move:
        cs.setCursorCssText(this.domElement, "dragging");
        break;
    }
    this.dispatchEvent("switch-edit-mode", t);
  }
  get editMode() {
    return this._editMode;
  }
  _handleTransformControlsEvent() {
    let t = !1;
    this._transformControls.addEventListener("dragging-changed", (i) => {
      this.world.renderer.cameraControls.enabled = !i.value, t ? cs.setCursorCssText(this.domElement, "dragging") : (cs.setCursorCssText(this.domElement, "create"), this.dispatchEvent("end-drag", {})), t = !t;
    }), this._transformControls.addEventListener("objectChange", () => {
      var n, r, a;
      this._lineMidPoint.visible = !1;
      const i = this._transformControls.object;
      this.dispatchEvent("drag", {
        point: i,
        worldPosition: ((n = this._transformControls.object) == null ? void 0 : n.position) ?? new _(),
        points: ((a = (r = i.parent) == null ? void 0 : r.parent) == null ? void 0 : a.children) ?? []
      });
    });
  }
  /**
   *
   * @param domElement 绘制监听所在的画布
   */
  startDraw() {
    switch (this.world.selection.enable = !1, this.root.position.equals(new _()) || (this.root = new mt(), this.world.renderer.rootScene.add(this.root)), cs.setCursorCssText(this.domElement, "create"), this.unBoundDrawDomElementEvents(this.domElement), this.bindDrawDomElementEvents(this.domElement), this.state = to.StartDraw, this.dispatchEvent("start-draw", {}), this._mode) {
      case "point":
      case "multi-point":
        this._startDrawPointMode();
        break;
      case "line":
      case "multi-line":
        this._startDrawLineMode();
        break;
      case "multi-rect":
        this._startDrawMultiRectMode();
        break;
      case "multi-polygon":
        this._polygonUtils.startDraw();
        break;
    }
  }
  showDrawPoint(t = this.root) {
    t.add(this.drawRootPoint);
  }
  setDrawPoint(t) {
    this.root.add(t);
  }
  removeDrawPoint(t) {
    this.root.remove(t);
  }
  showLineMidPoint(t, i = this.root) {
    this._lineMidPoint.visible = !0, this._lineMidPoint.position.copy(t), i.add(this._lineMidPoint);
  }
  showMoveLine(t = this.root) {
    this.drawMoveLine.geometry.setFromPoints([]), t.add(this.drawMoveLine);
  }
  showDrawMoveRect(t = this.root) {
    this._moveRectMesh.visible = !0, this._moveRectMesh.scale.set(1, 0, 1), t.add(this._moveRectMesh);
  }
  endDraw() {
    var i, n, r, a;
    cs.setCursorCssText(this.domElement, "default"), this._state = to.None, this._editMode = bn.Create, this.setActiveComponent(), this.dispatchEvent("end-draw", {}), this._unbindDrawEvents(), this.unBoundDrawDomElementEvents(this.domElement), this._transformControls.detach(), this.drawRootPoint.remove(...this.drawRootPoint.children), this.drawMoveLine.geometry.setFromPoints([]), (i = this.drawRootPoint.parent) == null || i.remove(this.drawRootPoint), (n = this.drawMoveLine.parent) == null || n.remove(this.drawMoveLine), (r = this._lineMidPoint.parent) == null || r.remove(this._lineMidPoint), this._lineMidPoint.userData = { drawId: "", index: -1 }, this._lineMidPoint.sprite.userData = { drawId: "" }, (a = this._moveRectMesh.parent) == null || a.remove(this._moveRectMesh), this.root.remove(...this.root.children);
    const t = [];
    this.world.renderer.rootScene.children.forEach((o) => {
      o.traverse((l) => {
        (l.name.includes(Xy) || l.name.includes(LA)) && l.parent && t.push({ parent: l.parent, child: l });
      });
    }), t.forEach(({ parent: o, child: l }) => {
      o.remove(l);
    }), this.world.selection.enable = !0, this._polygonUtils.endDraw();
  }
  bindDrawDomElementEvents(t) {
    t.addEventListener("mousedown", this.handleMouseDownBindThis), t.addEventListener("mousemove", this.handleMouseMoveBindThis), t.addEventListener("mouseup", this.handleMouseUpBindThis);
  }
  unBoundDrawDomElementEvents(t) {
    t.removeEventListener("mousedown", this.handleMouseDownBindThis), t.removeEventListener("mousemove", this.handleMouseMoveBindThis), t.removeEventListener("mouseup", this.handleMouseUpBindThis);
  }
  handleMouseDown(t) {
    this.dispatchEvent("mouse-down", t);
  }
  handleMouseMove(t) {
    this.dispatchEvent("mouse-move", t);
  }
  handleMouseUp(t) {
    this.dispatchEvent("mouse-up", t);
  }
  raycaster(t, i) {
    if (this.ray.setFromCamera(t, this.camera), i instanceof mt)
      return this.ray.intersectObject(i)[0];
    {
      const n = [];
      this.world.renderer.rootScene.children.forEach((a) => {
        a.traverse((o) => {
          o.name.includes(i) && n.push(o);
        });
      });
      const r = [];
      return n.forEach((a) => {
        const o = this.ray.intersectObject(a);
        o[0] && r.push(o[0]);
      }), r.sort((a, o) => a.distance - o.distance), r[0];
    }
  }
  raycasterComponent(t) {
    if (this._editMode === bn.Move && this._activeComponent) {
      const i = this._activeComponent, n = this.world.selection.raycasterSelect(t, i);
      this.intersectParams.length = 0, n && n.object && (this.world.selection.radioSelectById(
        i.entity.uuid,
        i.id,
        n.object.uuid,
        n.instanceId
      ), this.world.selection.showSelectHelpers(), this.world.selection.dispatchSelectEvent(this.world.selection.intersectionParams));
    }
  }
  executeDrawCommand(t, i) {
    const n = (a) => {
      const { position: o, id: l, drawId: c } = a;
      Qe.history.execute(new the(this, o, l, c)), this.drawRootPoint.children.forEach((h, u) => {
        h instanceof ul && (u === this.drawRootPoint.children.length - 1 ? h.setTexture("solid") : h.setTexture("hollow"));
      });
    }, r = (a) => {
      const { root: o, points: l, drawId: c } = a;
      Qe.history.execute(new ihe(this, l, o, c));
    };
    switch (t) {
      case "add-point":
        n(i);
        break;
      case "add-points":
        r(i);
        break;
    }
    this.mode === "multi-polygon" && this._polygonUtils.executeAddPoint(i);
  }
  getPositionCoord(t, i, n = 0) {
    const r = $r(t, i, this.domElement);
    this.ray.setFromCamera(r, this.camera);
    const a = this.world.renderer.rootScene.children.filter((c) => "enablePick" in c), [o] = this.ray.intersectObjects(a);
    return o ? {
      position: o.point,
      coord: r
    } : {
      position: Y4(this.camera, t, i, this.domElement, n),
      coord: r
    };
  }
  _startDrawPointMode() {
    this.addEventListener("mouse-down", this._handleDrawPointMouseDown);
  }
  _startDrawMultiRectMode() {
    let t, i = new _(NaN, NaN, NaN);
    const n = (a) => {
      var u, d;
      const o = a.clientX, l = a.clientY, { position: c, coord: h } = this.getPositionCoord(o, l);
      if (this._editMode === bn.Create) {
        this._moveRectMesh.visible = !0;
        const f = $r(o, l, this.domElement);
        this.raycaster(f, LA) || this._moveRectMesh.position.copy(c);
      } else
        this._moveRectMesh.visible = !1;
      if (this.editMode === bn.Move && t) {
        if (this.world.renderer.cameraControls.enabled = !1, !isNaN(i.x)) {
          const f = c.clone().sub(i);
          f.applyQuaternion(
            new St().setFromRotationMatrix(t.matrixWorld).invert()
          ), t.position.add(f);
        }
        i.copy(c), this.dispatchEvent("drag", {
          point: t,
          worldPosition: c.clone(),
          points: ((d = (u = t.parent) == null ? void 0 : u.parent) == null ? void 0 : d.children) ?? []
        });
      } else
        this.world.renderer.cameraControls.enabled = !0, i.set(NaN, NaN, NaN);
    }, r = (a) => {
      const o = $r(
        a.clientX,
        a.clientY,
        this.domElement
      ), l = this.raycaster(o, Xy);
      l && (t = l.object, this.domElement.style.cursor = "ew-resize", this.dispatchEvent("start-drag", { point: t }));
      const c = (h) => {
        if (a.clientX === h.clientX && a.clientY === h.clientY && this._activeComponent) {
          this.ray.setFromCamera(o, this.camera);
          const [u] = this._activeComponent.intersectObject(this.ray, h);
          if (!u && this.editMode === bn.Create) {
            const d = h.clientX, f = h.clientY, { position: p, coord: v } = this.getPositionCoord(d, f);
            this.dispatchEvent("draw-rect", { position: p, coord: v });
          }
          this.raycasterComponent(h);
        }
        this.domElement.style.cursor = "default", t = void 0, this.dispatchEvent("end-drag", {}), this.removeEventListener("mouse-up", c);
      };
      this.addEventListener("mouse-up", c);
    };
    this.addEventListener("mouse-down", r), this.addEventListener("mouse-move", n);
  }
  _startDrawLineMode() {
    const t = [];
    let i = !1;
    const n = new _(), r = (o) => {
      if (Qe.Info.gridAxisPlane !== "xz")
        return;
      this.drawMoveLine.visible = !0;
      const l = o.clientX, c = o.clientY, { position: h, coord: u } = this.getPositionCoord(l, c);
      if (n.setFromMatrixPosition(this.drawRootPoint.matrixWorld), h.sub(n), t.length > 0 && this.drawRootPoint.children.length > 0) {
        const { length: d } = this.drawRootPoint.children, f = this.drawRootPoint.children[d - 1];
        i ? this.drawMoveLine.geometry.setFromPoints([]) : this.drawMoveLine.geometry.setFromPoints([
          f.sprite.position,
          h
        ]);
      }
      if (this._editMode === bn.Move && this._activeComponent) {
        const d = this._activeComponent, f = this.world.selection.raycasterSelect(o, d);
        f != null && f.point && f.object ? (this._lineMidPoint.userData.drawId = f.object.uuid, this._lineMidPoint.sprite.userData.drawId = f.object.uuid, this.dispatchEvent("hover-component", {
          position: f.point,
          id: f.object.uuid
        }), cs.setCursorCssText(this.domElement, "create")) : (this._lineMidPoint.visible = !1, cs.setCursorCssText(this.domElement, "dragging"));
      }
      this._editMode === bn.Move && (this.drawMoveLine.visible = !1);
    }, a = (o) => {
      const l = (c) => {
        if (i = !1, cs.setCursorCssText(this.domElement, "create"), o.clientX === c.clientX && o.clientY === c.clientY) {
          this._transformControls.detach();
          const h = $r(o.clientX, o.clientY, this.domElement), u = this.raycaster(h, Xy);
          u && this.editMode === bn.Move && u.object && this._transformControls.attach(u.object), t.length = 0, this._handleDrawLine(o.clientX, o.clientY, t), this.raycasterComponent(o), this._lastUpTime > performance.now() - 250 && (this.dispatchEvent("double-click", {}), this.drawMoveLine.geometry.setFromPoints([]), i = !0), this._lastUpTime = performance.now();
        }
        this.removeEventListener("mouse-up", l);
      };
      this.addEventListener("mouse-up", l);
    };
    this.addEventListener("mouse-down", a), this.addEventListener("mouse-move", r);
  }
  _unbindDrawEvents() {
    this.removeEventListener("mouse-down"), this.removeEventListener("mouse-up"), this.removeEventListener("mouse-move");
  }
  _handleDrawPointMouseDown(t) {
    const i = (n) => {
      t.clientX === n.clientX && t.clientY === n.clientY && (this._handleDrawPoint(t.clientX, t.clientY), this.raycasterComponent(t)), this.removeEventListener("mouse-up", i);
    };
    this.addEventListener("mouse-up", i);
  }
  _handleDrawPoint(t, i) {
    if (this.domElement && this.camera) {
      if (this.editMode !== bn.Create)
        return;
      const { position: n, coord: r } = this.getPositionCoord(t, i);
      switch (this.mode) {
        case "point":
          this.dispatchEvent("draw-point", { position: n, coord: r }), this.endDraw();
          break;
        case "multi-point":
          this.dispatchEvent("draw-point", { position: n, coord: r });
          break;
      }
    }
  }
  _handleDrawLine(t, i, n) {
    if (Qe.Info.gridAxisPlane !== "xz")
      return [];
    if (this.editMode !== bn.Create && !this._lineMidPoint.visible)
      return [];
    const { position: r, coord: a } = this.getPositionCoord(t, i);
    if (this._activeComponent) {
      const o = new _().setFromMatrixPosition(this.drawRootPoint.matrixWorld);
      r.sub(o);
    }
    switch (n.push(r), this.mode) {
      case "line":
        this.dispatchEvent("draw-line", { position: r, coord: a }), n.length > 1 && this.endDraw();
        break;
      case "multi-line":
        this.dispatchEvent("draw-line", { position: r, coord: a });
        break;
    }
    return n;
  }
}
class MP extends mt {
  constructor(t, i = [], n = []) {
    super();
    S(this, "meshIndex", []);
    S(this, "points", []);
    S(this, "wireFrame", new Re());
    S(this, "_lastLength", this.points.length);
    S(this, "polygon", new Re(
      new Pt(),
      new kt({ color: new _e(0.23, 0.51, 1), transparent: !0, side: ui })
    ));
    this.uuid = t, this.add(this.wireFrame), this.add(this.polygon), i.length && this.updatePoints(i, n);
  }
  updatePoints(t, i = []) {
    if (this._lastLength !== t.length && (this._lastLength = t.length), this.points = [...t], i.length > 0)
      this.meshIndex = [...i], this.polygon.geometry.setFromPoints(t), this.polygon.geometry.setIndex(this.meshIndex), this.polygon.geometry.computeVertexNormals();
    else {
      const n = [], r = [], a = [], o = [], l = new ll(this.points.map((f) => new Se(f.x, f.z)));
      this.points.length = 0;
      const c = l.extractPoints(12);
      let h = c.shape;
      const u = c.holes;
      ta.isClockWise(h) === !1 && (h = h.reverse());
      for (let f = 0, p = u.length; f < p; f++) {
        const v = u[f];
        ta.isClockWise(v) === !0 && (u[f] = v.reverse());
      }
      const d = ta.triangulateShape(h, u);
      for (let f = 0, p = u.length; f < p; f++) {
        const v = u[f];
        h = h.concat(v);
      }
      for (let f = 0, p = h.length; f < p; f++) {
        const v = h[f];
        r.push(v.x, 0, v.y), this.points.push(new _(v.x, 0, v.y)), a.push(0, 1, 0), o.push(v.x, v.y);
      }
      for (let f = 0, p = d.length; f < p; f++) {
        const v = d[f], g = v[0], m = v[1], x = v[2];
        n.push(g, m, x);
      }
      this.meshIndex = [...n], console.log("aaa", this.meshIndex), this.polygon.geometry.setIndex(n), this.polygon.geometry.setAttribute("position", new gt(r, 3)), this.polygon.geometry.setAttribute("normal", new gt(a, 3)), this.polygon.geometry.setAttribute("uv", new gt(o, 2));
    }
  }
}
class Lhe extends Re {
  updatePoints(e) {
  }
}
var Ihe = Object.defineProperty, Ohe = Object.getOwnPropertyDescriptor, Nhe = (s, e, t, i) => {
  for (var n = Ohe(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && Ihe(e, t, n), n;
};
class sF {
  constructor(e) {
    S(this, "drawPointGroup", new mt());
    S(this, "drawPointChildren", []);
    S(this, "polygonMeshGroup", new mt());
    S(this, "lineGroup", new mt());
    S(this, "_handleMouseDownBindThis", this._handleMouseDown.bind(this));
    S(this, "_handleMouseUpBindThis", this._handleMouseUp.bind(this));
    S(this, "_handleMouseMoveBindThis", this._handleMouseMove.bind(this));
    S(this, "_lastUpTime", performance.now());
    S(this, "_lastAttachObject");
    this.drawUtils = e;
  }
  startDraw() {
    this._addDrawMeshGroup(), this._addMouseEvent();
  }
  endDraw() {
    this._removeMouseEvent(), this._removeDrawMesh();
  }
  setDrawGroupsVisible(e, t) {
    e.forEach((i) => {
      switch (i) {
        case "line":
          this.lineGroup.visible = t;
          break;
        case "polygon":
          this.polygonMeshGroup.visible = t;
          break;
        case "point":
          this.drawPointGroup.visible = t;
          break;
      }
    });
  }
  // 这里代表的是执行绘制中 添加point 的操作
  executeAddPoint(e) {
    const t = this.addDrawPoint(e);
    this.drawUtils.dispatchEvent("add-point", { point: t }), this._executeAddPolygon(), this._addDrawMoveLine();
  }
  // 这个是直接生成点的操作
  addDrawPoint(e) {
    const { id: t = nt.generateUUID(), position: i, drawId: n, polygonMesh: r, index: a } = e, o = new ul(n, t, i);
    return r && o.position.copy(r.position), this.drawPointGroup.add(o), a ? this.drawPointChildren.splice(a, 0, o) : this.drawPointChildren.push(o), o;
  }
  // 这个是直接生成面的操作
  addDrawPolygon(e, t = new _(), i, n = []) {
    if (i.length < 3)
      return;
    this._handleDrawMeshMatrix();
    const r = new MP(e, i, n);
    return r.position.copy(t), this.polygonMeshGroup.add(r), i.forEach((a) => {
      this.addDrawPoint({
        position: a,
        drawId: r.uuid,
        // 传入polygon mesh 是为了标识这个点属于哪一个面, 面是有独立位置的
        polygonMesh: r
      });
    }), r;
  }
  getDrawPointsByProperty(e, t) {
    return this.drawPointChildren.filter(
      (i) => i[e] === t
    );
  }
  getDrawPolygonByProperty(e, t) {
    return this.polygonMeshGroup.getObjectByProperty(e, t);
  }
  // 可以在point 更新后自动更新 polygon mesh 数据
  updatePolygonMeshByDrawPointGroup(e) {
    const t = this.getDrawPolygonByProperty("uuid", e), i = this._removeSamePoint(this._getDrawPointPositionsByDrawId(e));
    return t == null || t.updatePoints(i), t;
  }
  attach(e) {
    this.drawUtils.transformControls.attach(e), e instanceof ul || (this.drawPointChildren.filter(
      (i) => i.userData.drawId === e.uuid
    ).forEach((i) => e.attach(i)), this._lastAttachObject = e);
  }
  _getDrawPointPositionsByDrawId(e) {
    const t = [];
    return this.drawPointChildren.forEach((i) => {
      i.userData.drawId === e && t.push(i.sprite.position);
    }), t;
  }
  _removeSamePoint(e) {
    return e.filter((t, i, n) => n.findIndex((r) => r.x === t.x && r.y === t.y && r.z === t.z) === i);
  }
  _addDrawMeshGroup() {
    this.setDrawGroupsVisible(["line", "point", "polygon"], !1), this.drawUtils.world.renderer.rootScene.add(this.drawPointGroup), this.drawUtils.world.renderer.rootScene.add(this.polygonMeshGroup), this.drawUtils.world.renderer.rootScene.add(this.lineGroup);
  }
  _executeAddPolygon() {
    const { length: e } = this.drawPointChildren;
    if (e < 3)
      return;
    const t = this.drawPointChildren[e - 1].userData.drawId;
    let i = this.polygonMeshGroup.children.find((n) => n.uuid === t);
    i || (i = new MP(t), this.polygonMeshGroup.add(i)), i.updatePoints(
      this._removeSamePoint(this._getDrawPointPositionsByDrawId(t))
    ), this.drawUtils.dispatchEvent("add-polygon", { polygon: i });
  }
  _addDrawMoveLine() {
    if (this.drawPointChildren.length < 1)
      return;
    const e = this.drawPointChildren.length >= 2 ? 2 : 1;
    this.lineGroup.remove(...this.lineGroup.children);
    for (let t = 0; t < e; t++)
      this.lineGroup.add(new Lhe());
  }
  _removeDrawMesh() {
    this.drawPointGroup.remove(...this.drawPointGroup.children), this.drawPointChildren.length = 0, this.polygonMeshGroup.remove(...this.polygonMeshGroup.children), this.lineGroup.remove(...this.lineGroup.children), this.drawUtils.root.remove(this.drawPointGroup), this.drawUtils.root.remove(this.polygonMeshGroup), this.drawUtils.root.remove(this.lineGroup);
  }
  _addMouseEvent() {
    this.drawUtils.addEventListener("mouse-down", this._handleMouseDownBindThis), this.drawUtils.addEventListener("mouse-up", this._handleMouseUpBindThis), this.drawUtils.addEventListener("mouse-move", this._handleMouseMoveBindThis);
  }
  _removeMouseEvent() {
    this.drawUtils.removeEventListener("mouse-down", this._handleMouseDownBindThis), this.drawUtils.removeEventListener("mouse-up", this._handleMouseUpBindThis), this.drawUtils.removeEventListener("mouse-move", this._handleMouseMoveBindThis);
  }
  _handleMouseDown(e) {
    const t = (i) => {
      e.clientX === i.clientX && e.clientY === i.clientY && (this._handleMouseClick(i), this._handleDoubleClick(i)), this.drawUtils.removeEventListener("mouse-up", t);
    };
    this.drawUtils.addEventListener("mouse-up", t);
  }
  _handleDoubleClick(e) {
    this._lastUpTime > performance.now() - 250 && this.drawUtils.editMode === bn.Create && this.drawUtils.dispatchEvent("double-click", {}), this._lastUpTime = performance.now();
  }
  _getMouse3dPosition(e, t = 0) {
    return this.drawUtils.getPositionCoord(e.clientX, e.clientY, t);
  }
  _handleMouseClick(e) {
    switch (this.drawUtils.editMode) {
      case bn.Create:
        this._handleMouseClickInCreateMode(e);
        break;
      case bn.Move:
        this._handleMouseClickInMoveMode(e);
        break;
    }
  }
  _handleMouseClickInCreateMode(e) {
    this._handleDrawMeshMatrix();
    const { position: t, coord: i } = this._getMouse3dPosition(e, this.drawPointGroup.position.y);
    t.sub(this.drawPointGroup.position), this.drawUtils.dispatchEvent("draw-point", { position: t, coord: i });
  }
  _handleMouseClickInMoveMode(e) {
    var n, r;
    const t = this._raycasterDrawPointGroup(e);
    this.drawUtils.transformControls.detach(), this._lastAttachObject && this._lastAttachObject.uuid !== this.drawPointGroup.uuid && this._lastAttachObject.children.forEach((a) => {
      a instanceof ul && (new _(), this.drawPointGroup.attach(a));
    }), t && this.drawUtils.transformControls.attach(t);
    const i = this._raycasterDrawPolygonMeshOrMidPoint(e);
    if (i)
      if (i.name === Zb || ((n = i.parent) == null ? void 0 : n.name) === Zb) {
        const a = this.drawUtils.getLineMidPoint();
        if (this.drawPointChildren.findIndex(
          (l) => {
            var c;
            return (c = l.children[0]) == null ? void 0 : c.getWorldPosition(new _()).equals(a.position);
          }
        ) === -1) {
          const l = nt.generateUUID(), c = (r = this.drawUtils.activeComponent) == null ? void 0 : r.group.children.find(
            (d) => d.uuid === a.userData.drawId
          ), h = this.polygonMeshGroup.children.find(
            (d) => d.uuid === a.userData.drawId
          ), u = new _();
          c == null || c.getWorldPosition(u), this.executeAddPoint({
            id: l,
            position: a.position.clone().sub(u),
            drawId: a.userData.drawId,
            polygonMesh: h,
            index: a.userData.index
          });
        }
      } else
        this.attach(i);
  }
  _raycasterDrawPolygonMeshOrMidPoint(e) {
    var r;
    const { coord: t } = this._getMouse3dPosition(e, this.drawPointGroup.position.y);
    this.drawUtils.ray.setFromCamera(t, this.drawUtils.camera);
    const i = (r = this.drawUtils.activeComponent) == null ? void 0 : r.group;
    let n;
    if (i) {
      const [a] = this.drawUtils.ray.intersectObject(i);
      n = a == null ? void 0 : a.object;
      const [o] = this.drawUtils.ray.intersectObject(
        this.drawUtils.getLineMidPoint()
      );
      o && (n = o.object);
    }
    return n;
  }
  _raycasterDrawPointGroup(e) {
    const { coord: t } = this._getMouse3dPosition(e, this.drawPointGroup.position.y);
    this.drawUtils.ray.setFromCamera(t, this.drawUtils.camera);
    const [i] = this.drawUtils.ray.intersectObject(this.drawPointGroup);
    return i == null ? void 0 : i.object;
  }
  _handleDrawMeshMatrix() {
    if (this.drawUtils.activeComponent) {
      const { group: e } = this.drawUtils.activeComponent;
      e.matrixWorld.decompose(
        this.drawPointGroup.position,
        this.drawPointGroup.quaternion,
        this.drawPointGroup.scale
      ), e.matrixWorld.decompose(
        this.polygonMeshGroup.position,
        this.polygonMeshGroup.quaternion,
        this.polygonMeshGroup.scale
      );
    }
  }
  _handleMouseUp(e) {
  }
  _handleMouseMove(e) {
    const [t, i] = this.lineGroup.children, { position: n } = this._getMouse3dPosition(e), { length: r } = this.drawPointChildren;
    t && t.updatePoints([
      n,
      this.drawPointChildren[r - 1].position
    ]), i && i.updatePoints([
      n,
      this.drawPointChildren[r - 2].position
    ]), this._handleMidPoint(e);
  }
  _handleMidPoint(e) {
    if (this.drawUtils.editMode === bn.Move && this.drawUtils.activeComponent) {
      const t = this.drawUtils.activeComponent, i = this.drawUtils.world.selection.raycasterSelect(e, t);
      if (i != null && i.point && i.object) {
        this.drawUtils.getLineMidPoint().userData.drawId = i.object.uuid, this.drawUtils.getLineMidPoint().sprite.userData.drawId = i.object.uuid;
        const n = [];
        let r;
        const a = [];
        n.push(
          this.drawPointChildren[0].sprite.getWorldPosition(new _())
        );
        const o = [...this.drawPointChildren, this.drawPointChildren[0]];
        for (let c = 1; c < o.length; c++) {
          const h = new _(), u = new _();
          o[c - 1].sprite.getWorldPosition(h), o[c].sprite.getWorldPosition(u), n.push(u.clone());
          const d = new _().subVectors(u, h).normalize();
          if (i.point) {
            const f = i.point.clone().sub(h).dot(d), p = h.clone().add(d.multiplyScalar(f));
            a.push({ d: p.distanceTo(i.point), index: c - 1 });
          }
          r = o[c].parent;
        }
        a.sort((c, h) => c.d - h.d);
        const [l] = a;
        if (l) {
          const c = new _().addVectors(n[l.index], n[l.index + 1]).multiplyScalar(0.5);
          this.drawUtils.getLineMidPoint().userData.index = l.index, r && this.drawUtils.showLineMidPoint(c);
        }
        cs.setCursorCssText(this.drawUtils.domElement, "create"), this._setLineMidPointVisible(!0);
      } else
        this._setLineMidPointVisible(), cs.setCursorCssText(this.drawUtils.domElement, "dragging");
    }
  }
  _setLineMidPointVisible(e = !1) {
    this.drawUtils.getLineMidPoint().visible = e;
  }
}
Nhe([
  Hme(1)
], sF.prototype, "_setLineMidPointVisible");
class Rp extends Pl {
  /**
   *
   * @param root 包围盒生成的父物体
   * @param objects 包围盒是否union物体
   * @param id
   */
  constructor(t, i = [], n) {
    super({
      id: n ?? nt.generateUUID(),
      type: "EngineGroup"
    });
    S(this, "box", new zt());
    S(this, "borderLine");
    this.box.setFromObject(t), i.forEach((r) => {
      this.box.union(new zt().setFromObject(r));
    }), this.borderLine = this.createBorderLine(this.box), this.add(this.borderLine), t.attach(this);
  }
  static setFromBox(t, i) {
    const n = new Rp(t);
    return n.box = i, n.borderLine = n.createBorderLine(n.box), n.add(n.borderLine), n;
  }
  updateFromBox(t = this.box) {
    this.box = t, this.updateBorderLine(t, this.borderLine);
  }
  setColor(t, i, n) {
    const { geometry: r } = this.borderLine, a = r.getAttribute("color"), o = [];
    for (let c = 0; c < a.count; c++)
      o.push(t, i, n);
    const l = new gt(o, 3);
    r.setAttribute("color", l);
  }
  show() {
    this.visible = !0;
  }
  hide() {
    this.visible = !1;
  }
  createBorderLine(t) {
    const i = [
      [-1, -1, -1],
      [1, -1, -1],
      [-1, 1, -1],
      [-1, -1, 1],
      [1, 1, 1],
      [-1, 1, 1],
      [1, -1, 1],
      [1, 1, -1]
    ], n = [0, 2, 1, 7, 6, 4, 3, 5, 0, 3, 1, 6, 7, 4, 2, 5, 0, 1, 3, 6, 5, 4, 2, 7], r = [];
    n.forEach((u) => {
      r.push(...i[u]);
    });
    const a = [], o = [];
    for (let u = 0; u < r.length / 3; u++)
      a.push(0.211, 0.443, 0.878), o.push(u);
    const l = new Pt();
    l.setIndex(new Ht(new Uint16Array(o), 1)), l.setAttribute("position", new gt(r, 3)), l.setAttribute("color", new gt(a, 3));
    const c = new Bi({ vertexColors: !0, toneMapped: !1 }), h = new _n(l, c);
    return this.updateBorderLine(t, h), h;
  }
  updateBorderLine(t = this.box, i = this.borderLine) {
    t.getCenter(i.position), t.getSize(i.scale), i.scale.multiplyScalar(0.5);
  }
}
class Bhe extends mt {
  constructor() {
    super();
    S(this, "divisions", 1e3);
    S(this, "unit", 10);
    S(this, "relativeUnit", 1);
    S(this, "gridScale", 10);
    S(this, "majorGridMaterial", new Jt(bee));
    S(this, "minorGridMaterial", new Jt(Mee));
    S(this, "majorGrid");
    S(this, "minorGrid");
    S(this, "raycasterMesh");
    S(this, "forceFadeFactor", !1);
    this.name = "InfiniteGridMeshV2", this.renderOrder = 0, this.majorGrid = new _n(void 0, this.majorGridMaterial), this.minorGrid = new _n(void 0, this.minorGridMaterial), this.raycasterMesh = new Re(
      new $n(this.unit * this.divisions, this.unit * this.divisions)
    );
    const t = this.unit * this.divisions / 2, i = [];
    for (let r = 0, a = -t; r <= this.divisions; r++, a += this.unit)
      r % 10 !== 0 && (i.push(-t, 0, a, t, 0, a), i.push(a, 0, -t, a, 0, t));
    this.minorGrid.geometry.setAttribute(
      "position",
      new gt(i, 3)
    );
    const n = [];
    for (let r = 0, a = -t; r <= this.divisions / 10; r++, a += this.unit * 10)
      n.push(-t, 0, a, t, 0, a), n.push(a, 0, -t, a, 0, t);
    this.majorGrid.geometry.setAttribute(
      "position",
      new gt(n, 3)
    ), this.add(this.minorGrid, this.majorGrid);
  }
  setGridScale(t, i, n) {
    let r = 1 / 0, a = "x";
    t === "xz" ? (r = 9, a = "y") : t === "xy" ? (r = 10, a = "z") : (r = 8, a = "y"), this.majorGrid.material.uniforms.mode.value = r;
    let o;
    const l = new _(), c = new _(), h = new _(), u = 20 * Math.PI / 180, d = 2 * Math.PI / 180, f = i.position.distanceTo(n);
    if (i instanceof Zt) {
      l.setFromMatrixPosition(i.matrixWorld), o = Math.min(1, Math.abs(l[a] / f * 5));
      const g = i.matrixWorld.elements;
      c.set(g[8], g[9], g[10]);
      const m = l.sub(c.multiplyScalar(f)), x = Math.abs(m[a]);
      this.gridScale = (f + 3 * x) / 1e3 * 10, this.majorGrid.material.uniforms.gridScale.value = this.gridScale, this.minorGrid.material.uniforms.gridScale.value = this.gridScale, m[a] = 0, h.copy(m);
    } else {
      const g = Math.abs(Math.PI / 2 - Math.acos(i.matrixWorld.elements[r]));
      g < u ? o = Math.max(g - d, 0) / (u - d) : o = 1, this.gridScale += 1 / i.zoom, this.majorGrid.material.uniforms.gridScale.value = 1 / 0, this.minorGrid.material.uniforms.gridScale.value = 1 / 0;
    }
    this.forceFadeFactor ? (this.majorGrid.material.uniforms.angleFadeFactor.value = 1, this.minorGrid.material.uniforms.angleFadeFactor.value = 1) : (this.majorGrid.material.uniforms.angleFadeFactor.value = o, this.minorGrid.material.uniforms.angleFadeFactor.value = o);
    const p = Math.log10(this.gridScale);
    this.scale.setScalar(10 ** Math.floor(p)), p >= 0 ? this.minorGrid.material.uniforms.opacityFactor.value = 0.4 * (1 - p % 1) : this.minorGrid.material.uniforms.opacityFactor.value = 0.4 * -(p % 1), h.divideScalar(this.scale.x);
    const v = this.unit * 10;
    h.x = Math.round(h.x / v) * v, h.y = Math.round(h.y / v) * v, h.z = Math.round(h.z / v) * v, t === "xz" ? (this.majorGrid.material.uniforms.offset.value.set(-h.x, -h.z), this.position.set(h.x, 0, h.z).multiplyScalar(this.scale.x)) : t === "xy" ? (this.majorGrid.material.uniforms.offset.value.set(-h.x, -h.y), this.position.set(h.x, h.y, 0).multiplyScalar(this.scale.x)) : (this.majorGrid.material.uniforms.offset.value.set(-h.y, -h.z), this.position.set(0, h.y, h.z).multiplyScalar(this.scale.x)), this.minorGrid.material.uniforms.opacityFactor.value > 0.2 ? this.relativeUnit = this.divisions * this.scale.x / 10 : this.relativeUnit = this.divisions * this.scale.x, this.majorGrid.material.uniformsNeedUpdate = !0, this.minorGrid.material.uniformsNeedUpdate = !0;
  }
}
function Fhe(s, e) {
  return rF(s, e) * s.aspect;
}
function rF(s, e) {
  const t = s.fov * Math.PI / 180;
  return Math.tan(t / 2) * e * 2;
}
function Ex(s = !1) {
  const e = new wn(
    new ar({
      depthTest: !1,
      transparent: !0,
      side: ui,
      alphaTest: 0.1
    })
  );
  return e.visible = s, e;
}
function Ug(s, e = "#FAF65C") {
  return new ti(
    new Pt().setFromPoints(s),
    new Bi({
      color: e,
      linecap: "round",
      linejoin: "round",
      transparent: !0,
      opacity: 0.6,
      dithering: !0
    })
  );
}
function Uhe(s, e, t = "#FAF65C") {
  const i = [];
  for (let n = 0; n <= 36; n++) {
    const r = Math.sin(n * (Math.PI / 18)), a = Math.cos(n * (Math.PI / 18));
    i.push(new _(r * s + e.x, e.y, a * s + e.z));
  }
  return i;
}
function zhe(s, e, t) {
  s.userData[e] = t;
}
function Lh(s, e) {
  return s.userData[e];
}
class khe extends es {
  constructor() {
    super(...arguments);
    S(this, "handleMouseUpBindThis", this.handleMouseUp.bind(this));
    S(this, "handleMouseDownBindThis", this.handleMouseDown.bind(this));
    S(this, "handleMouseMoveBindThis", this.handleMouseMove.bind(this));
    S(this, "handleMouseDoubleClickBindThis", this.handleMouseDoubleClick.bind(this));
    S(this, "_isActive", !1);
  }
  active(t) {
    this._isActive === !0 && this.deActive(t), t.addEventListener("mouseup", this.handleMouseUpBindThis), t.addEventListener("mousedown", this.handleMouseDownBindThis), t.addEventListener("mousemove", this.handleMouseMoveBindThis), t.addEventListener("dblclick", this.handleMouseDoubleClickBindThis), this.handleMouseDragEvent(), this._isActive = !0;
  }
  deActive(t) {
    t.removeEventListener("mouseup", this.handleMouseUpBindThis), t.removeEventListener("mousedown", this.handleMouseDownBindThis), t.removeEventListener("mousemove", this.handleMouseMoveBindThis), t.removeEventListener("dblclick", this.handleMouseDoubleClickBindThis), this.removeAllEventListeners(), this._isActive = !1;
  }
  handleMouseDragEvent() {
    this.addEventListener("mousedown", (t) => {
      const i = (r) => {
        this.dispatchEvent("drag", r);
      }, n = (r) => {
        this.removeEventListener("mousemove", i), this.removeEventListener("mouseup", n), this.dispatchEvent("dragEnd", r);
      };
      this.addEventListener("mousemove", i), this.addEventListener("mouseup", n), this.dispatchEvent("dragStart", t);
    });
  }
  handleMouseUp(t) {
    t.target instanceof HTMLInputElement || this.dispatchEvent("mouseup", t);
  }
  handleMouseDown(t) {
    if (t.target instanceof HTMLInputElement)
      return;
    const i = (n) => {
      t.clientX === n.clientX && t.clientY === n.clientY && this.dispatchEvent("click", n), this.removeEventListener("mouseup", i);
    };
    this.addEventListener("mouseup", i), this.dispatchEvent("mousedown", t);
  }
  handleMouseMove(t) {
    t.target instanceof HTMLInputElement || this.dispatchEvent("mousemove", t);
  }
  handleMouseDoubleClick(t) {
    t.target instanceof HTMLInputElement || this.dispatchEvent("doubleClick", t);
  }
  handleMouseDrag(t) {
    t.target instanceof HTMLInputElement || this.dispatchEvent("drag", t);
  }
}
class aF extends es {
  constructor() {
    super(...arguments);
    S(this, "handleKeyUpBindThis", this.handleKeyUp.bind(this));
    S(this, "handleKeyDownBindThis", this.handleKeyDown.bind(this));
    S(this, "handleKeyPressBindThis", this.handleKeyPress.bind(this));
    S(this, "_isActive", !1);
    S(this, "_isHolding", !1);
    S(this, "_animateId", 0);
  }
  active() {
    this._isActive === !0 && this.deActive(), document.addEventListener("keyup", this.handleKeyUpBindThis), document.addEventListener("keydown", this.handleKeyDownBindThis), document.addEventListener("keypress", this.handleKeyPressBindThis), this._isActive = !0;
  }
  deActive() {
    document.removeEventListener("keyup", this.handleKeyUpBindThis), document.removeEventListener("keydown", this.handleKeyDownBindThis), document.removeEventListener("keypress", this.handleKeyPressBindThis), this._isActive = !1;
  }
  handleKeyUp(t) {
    if (!(t.target instanceof HTMLInputElement)) {
      switch (t.key) {
        case "1":
          this.dispatchEvent("key1", t);
          break;
        case "2":
          this.dispatchEvent("key2", t);
          break;
        case "3":
          this.dispatchEvent("key3", t);
          break;
        case "Escape":
          this.dispatchEvent("esc", t);
          break;
        case "Shift":
          this.dispatchEvent("shift", t);
          break;
        case "g":
          this.dispatchEvent("g", t);
          break;
      }
      this.dispatchEvent("keyup", t);
    }
  }
  handleKeyDown(t) {
    if (t.target instanceof HTMLInputElement)
      return;
    this.dispatchEvent("keydown", t);
    const i = () => {
      cancelAnimationFrame(this._animateId), this.removeEventListener("keyup", i), this._isHolding = !1;
    }, n = () => {
      this.dispatchEvent("hold", t), this._animateId = requestAnimationFrame(n);
    };
    this._isHolding || (n(), this._isHolding = !0, this.addEventListener("keyup", i));
  }
  handleKeyPress(t) {
    t.target instanceof HTMLInputElement || this.dispatchEvent("keypress", t);
  }
}
class Ghe extends es {
  constructor() {
    super(...arguments);
    S(this, "cache", /* @__PURE__ */ new Map());
    S(this, "enable", !1);
    S(this, "adsorbType", "face");
    S(this, "adsorbBasePointType", "near");
    S(this, "_minDistance", 0.5);
  }
  get minDistance() {
    return this._minDistance;
  }
  setMinAdsorbDistance(t) {
    this._minDistance = t;
  }
  getUnionBox3(t) {
    const i = new zt();
    return t.forEach((n) => {
      i.union(this.setCache(n).box);
    }), i;
  }
  setCache(t) {
    const i = this.cache.get(t.uuid);
    if (i) {
      const n = t.matrix.clone().setPosition(0, 0, 0);
      return n.equals(i.matrix) || (i.matrix.copy(n), i.box.setFromObject(t)), i;
    } else {
      const n = t.matrixWorld.clone();
      return n.setPosition(0, 0, 0), {
        box: new zt().setFromObject(t),
        matrix: n
      };
    }
  }
  getAbsorbBasePointPositions(t, i) {
    const n = this.getUnionBox3(t), r = [];
    switch (this.adsorbBasePointType) {
      case "near":
        r.push(...this.getBoxBorderPoint(n));
        break;
      case "center":
        r.push(n.getCenter(new _()));
        break;
    }
    return r.forEach((a) => a.add(i)), r;
  }
  /**
   * @param obj1 正在移动需要判断是否吸附的物体
   * @param obj2 吸附对象
   * @param nextOffset 下一帧偏移的距离
   */
  getAdsorbResult(t, i, n) {
    const r = new _(), a = new _();
    if (!this.enable)
      return [!1, a, n];
    let o = !1;
    const l = this.getAbsorbBasePointPositions(t, n);
    return i.forEach((c) => {
      const h = this.getUnionBox3([c]), [u, d, f] = this.getAdsorbPoints(l, h);
      u && (o = !0, r.subVectors(d, f), a.copy(d));
    }), [o, a, r];
  }
  /**
   * @function 获取吸附的点
   * @returns 是否吸附、吸附于那个点、吸附的基础点
   */
  getAdsorbPoints(t, i) {
    const n = [!1, new _(), new _()];
    switch (this.adsorbType) {
      case "point":
        this.getPointAdsorbPoints(t, this.getBoxBorderPoint(i), n);
        break;
      case "line":
        this.getLineAdsorbPoints(t, this.getLineFromBox(i), n);
        break;
      case "face":
        this.getBoxAdsorbPoints(t, i, n);
        break;
    }
    return n;
  }
  getLineAdsorbPoints(t, i, n) {
    let r = Number.MAX_VALUE;
    const a = new _(), o = new _(), l = new _();
    return t.forEach((c) => {
      i.forEach((h) => {
        const u = h.closestPointToPoint(c, !0, l), d = c.distanceTo(u);
        d < r && (r = d, a.copy(u), o.copy(c));
      });
    }), r < this._minDistance ? (n[0] = !0, n[1] = a, n[2] = o) : n[0] = !1, n;
  }
  getPointAdsorbPoints(t, i, n) {
    let r = Number.MAX_VALUE;
    const a = new _(), o = new _();
    return t.forEach((l) => {
      i.forEach((c) => {
        const h = l.distanceTo(c);
        h < r && (r = h, a.copy(c), o.copy(l));
      });
    }), r < this._minDistance ? (n[0] = !0, n[1] = a, n[2] = o) : n[0] = !1, n;
  }
  getBoxAdsorbPoints(t, i, n) {
    let r = Number.MAX_VALUE;
    const a = new _(), o = new _();
    return t.forEach((l) => {
      const c = i.distanceToPoint(l);
      c < r && (r = c, o.copy(l), a.copy(l).clamp(i.min, i.max));
    }), r < this._minDistance ? (n[0] = !0, n[1] = a, n[2] = o) : n[0] = !1, n;
  }
  getLineFromBox(t) {
    return [
      // 以长方形为基准 这是它的四根长
      new Qs(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.max.x, t.min.y, t.min.z)
      ),
      new Qs(
        new _(t.min.x, t.min.y, t.max.z),
        new _(t.max.x, t.min.y, t.max.z)
      ),
      new Qs(
        new _(t.min.x, t.max.y, t.min.z),
        new _(t.max.x, t.max.y, t.min.z)
      ),
      new Qs(
        new _(t.min.x, t.max.y, t.max.z),
        new _(t.max.x, t.max.y, t.max.z)
      ),
      new Qs(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.min.x, t.max.y, t.min.z)
      ),
      new Qs(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.max.x, t.max.y, t.min.z)
      ),
      new Qs(
        new _(t.min.x, t.min.y, t.max.z),
        new _(t.min.x, t.max.y, t.max.z)
      ),
      new Qs(
        new _(t.min.x, t.min.y, t.max.z),
        new _(t.max.x, t.max.y, t.max.z)
      )
    ];
  }
  getBoxBorderPoint(t) {
    return [
      new _(t.min.x, t.min.y, t.min.z),
      new _(t.max.x, t.min.y, t.min.z),
      new _(t.min.x, t.max.y, t.min.z),
      new _(t.max.x, t.max.y, t.min.z),
      new _(t.max.x, t.max.y, t.max.z),
      new _(t.min.x, t.max.y, t.max.z),
      new _(t.max.x, t.min.y, t.max.z),
      new _(t.min.x, t.min.y, t.max.z)
    ];
  }
  getPlaneFromBox(t) {
    return [
      // 前后
      new ln().setFromCoplanarPoints(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.max.x, t.min.y, t.min.z),
        new _(t.min.x, t.max.y, t.min.z)
      ),
      new ln().setFromCoplanarPoints(
        new _(t.min.x, t.min.y, t.max.z),
        new _(t.max.x, t.min.y, t.max.z),
        new _(t.min.x, t.max.y, t.max.z)
      ),
      // 上下
      new ln().setFromCoplanarPoints(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.max.x, t.min.y, t.max.z),
        new _(t.max.x, t.min.y, t.max.z)
      ),
      new ln().setFromCoplanarPoints(
        new _(t.min.x, t.max.y, t.min.z),
        new _(t.max.x, t.max.y, t.max.z),
        new _(t.max.x, t.max.y, t.max.z)
      ),
      // 左右
      new ln().setFromCoplanarPoints(
        new _(t.min.x, t.min.y, t.min.z),
        new _(t.min.x, t.min.y, t.max.z),
        new _(t.min.x, t.max.y, t.max.z)
      ),
      new ln().setFromCoplanarPoints(
        new _(t.max.x, t.min.y, t.min.z),
        new _(t.max.x, t.min.y, t.max.z),
        new _(t.max.x, t.max.y, t.max.z)
      )
    ];
  }
  dispose() {
    this.cache.clear(), this.enable = !1;
  }
}
class Hhe extends Rl {
  constructor(t) {
    super(x0.Select, "TransformCommand");
    S(this, "lastMatrixMap", /* @__PURE__ */ new Map());
    this.objects = t;
  }
  async execute() {
    this.objects.forEach((t) => {
      t.updateMatrix(), this.lastMatrixMap.set(t.uuid, t.matrix.clone());
    });
  }
  undo() {
    this.objects.forEach((t) => {
      const i = t.matrix.clone(), n = this.lastMatrixMap.get(t.uuid);
      n && (n.decompose(t.position, t.quaternion, t.scale), this.lastMatrixMap.set(t.uuid, i));
    });
  }
  redo() {
    this.objects.forEach((t) => {
      const i = t.matrix.clone(), n = this.lastMatrixMap.get(t.uuid);
      n && (n.decompose(t.position, t.quaternion, t.scale), this.lastMatrixMap.set(t.uuid, i));
    });
  }
  toJSON() {
  }
}
class Vhe extends Rl {
  constructor(t, i, n) {
    super(x0.Select, "SelectCommand");
    S(this, "lastMatrixMap", /* @__PURE__ */ new Map());
    this.selectCallback = t, this.objects = i, this.newObjects = n;
  }
  undo() {
    this.selectCallback(this.objects);
  }
  redo() {
    this.selectCallback(this.newObjects);
  }
  toJSON() {
  }
}
class Whe {
  constructor(e) {
    S(this, "_objectStep", 1);
    this.selection = e;
  }
  setObjectStep(e) {
    this._objectStep = e;
  }
  // @smooth()
  translateObject(e) {
    if (this.selection.transformControls && this.selection.transformControls.mode !== "translate")
      return;
    const t = new _();
    switch (e) {
      case "w":
        t.set(0, 0, -1 * this._objectStep);
        break;
      case "s":
        t.set(0, 0, 1 * this._objectStep);
        break;
      case "a":
        t.set(-1 * this._objectStep, 0, 0);
        break;
      case "d":
        t.set(1 * this._objectStep, 0, 0);
        break;
      case "q":
        t.set(0, -1 * this._objectStep, 0);
        break;
      case "e":
        t.set(0, 1 * this._objectStep, 0);
        break;
    }
    this.selection.dragPoint.position.add(t), this.selection.intersectionParams.forEach(({ object: i, inActive: n }) => {
      !n && i && i.position.add(t);
    }), this.selection.dragPoint.lastPosition.copy(this.selection.dragPoint.position), this.selection.updateBoundingBox();
  }
  rotateObject(e, t) {
    if (!(this.selection.transformControls && this.selection.transformControls.mode !== "rotate")) {
      switch (t) {
        case "x":
          this.selection.dragPoint.rotateX(e);
          break;
        case "y":
          this.selection.dragPoint.rotateY(e);
          break;
        case "z":
          this.selection.dragPoint.rotateZ(e);
          break;
      }
      this.selection.intersectionParams.forEach(({ object: i, inActive: n }) => {
        !n && i && this.selection.dragPoint.matrixWorld.decompose(
          new _(),
          i.quaternion,
          i.scale
        );
      });
    }
  }
}
class Xhe {
  constructor(e, t) {
    S(this, "element");
    S(this, "dom");
    S(this, "startPoint");
    S(this, "pointTopLeft");
    S(this, "pointBottomRight");
    this.element = document.createElement("div"), this.element.classList.add(t), this.element.style.pointerEvents = "none", this.dom = e, this.startPoint = new Se(), this.pointTopLeft = new Se(), this.pointBottomRight = new Se(), this.element.style.backgroundColor = "rgba(75, 160, 255, 0.3)", this.element.style.position = "fixed", this.element.style.border = "1px solid #55aaff";
  }
  dispose() {
  }
  onSelectStart(e, t, i) {
    var n;
    this.dom = i, this.element.style.display = "none", (n = this.dom.parentElement) == null || n.appendChild(this.element), this.element.style.left = `${e}px`, this.element.style.top = `${t}px`, this.element.style.width = "0px", this.element.style.height = "0px", this.startPoint.x = e, this.startPoint.y = t;
  }
  onSelectMove(e, t) {
    this.element.style.display = "block", this.pointBottomRight.x = Math.max(this.startPoint.x, e), this.pointBottomRight.y = Math.max(this.startPoint.y, t), this.pointTopLeft.x = Math.min(this.startPoint.x, e), this.pointTopLeft.y = Math.min(this.startPoint.y, t), this.element.style.left = `${this.pointTopLeft.x}px`, this.element.style.top = `${this.pointTopLeft.y}px`, this.element.style.width = `${this.pointBottomRight.x - this.pointTopLeft.x}px`, this.element.style.height = `${this.pointBottomRight.y - this.pointTopLeft.y}px`;
  }
  onSelectOver() {
    var e;
    (e = this.element.parentElement) == null || e.removeChild(this.element);
  }
}
class jhe extends es {
  constructor(t, i, n = Number.MAX_VALUE) {
    super();
    S(this, "frustum", new th());
    S(this, "_center", new _());
    S(this, "_tmpPoint", new _());
    S(this, "_vecNear", new _());
    S(this, "_vecTopLeft", new _());
    S(this, "_vecTopRight", new _());
    S(this, "_vecDownRight", new _());
    S(this, "_vecDownLeft", new _());
    S(this, "_vecFarTopLeft", new _());
    S(this, "_vecFarTopRight", new _());
    S(this, "_vecFarDownRight", new _());
    S(this, "_vecFarDownLeft", new _());
    S(this, "_vecTemp1", new _());
    S(this, "_vecTemp2", new _());
    S(this, "_vecTemp3", new _());
    S(this, "_boxTemp", new zt());
    S(this, "_matrix", new et());
    S(this, "_quaternion", new St());
    S(this, "_scale", new _());
    S(this, "startPoint", new _());
    S(this, "endPoint", new _());
    S(this, "collection", []);
    S(this, "instances", {});
    S(this, "helper");
    this.camera = t, this.deep = n, this.helper = new Xhe(i, "easytwin-box-selection-helper");
  }
  setCamera(t) {
    this.camera = t;
  }
  setStartPoint(t, i, n, r) {
    this.startPoint.x = t, this.startPoint.y = i, this.startPoint.z = n;
  }
  setEndPoint(t, i, n) {
    this.endPoint.x = t, this.endPoint.y = i, this.endPoint.z = n;
  }
  select(t, i = !0, n, r) {
    return this.startPoint = n || this.startPoint, this.endPoint = r || this.endPoint, this.collection = [], this.updateFrustum(this.startPoint, this.endPoint), i ? this.searchChildInFrustum(this.frustum, t) : this.searchObjectsInFrustum(this.frustum, t.children), this.collection;
  }
  updateFrustum(t, i) {
    if (t = t || this.startPoint, i = i || this.endPoint, t.x === i.x && (i.x += Number.EPSILON), t.y === i.y && (i.y += Number.EPSILON), this.camera.updateProjectionMatrix(), this.camera.updateMatrixWorld(), this.camera instanceof Zt) {
      this._tmpPoint.copy(t), this._tmpPoint.x = Math.min(t.x, i.x), this._tmpPoint.y = Math.max(t.y, i.y), i.x = Math.max(t.x, i.x), i.y = Math.min(t.y, i.y), this._vecNear.setFromMatrixPosition(this.camera.matrixWorld), this._vecTopLeft.copy(this._tmpPoint), this._vecTopRight.set(i.x, this._tmpPoint.y, 0), this._vecDownRight.copy(i), this._vecDownLeft.set(this._tmpPoint.x, i.y, 0), this._vecTopLeft.unproject(this.camera), this._vecTopRight.unproject(this.camera), this._vecDownRight.unproject(this.camera), this._vecDownLeft.unproject(this.camera), this._vecTemp1.copy(this._vecTopLeft).sub(this._vecNear), this._vecTemp2.copy(this._vecTopRight).sub(this._vecNear), this._vecTemp3.copy(this._vecDownRight).sub(this._vecNear), this._vecTemp1.normalize(), this._vecTemp2.normalize(), this._vecTemp3.normalize(), this._vecTemp1.multiplyScalar(this.deep), this._vecTemp2.multiplyScalar(this.deep), this._vecTemp3.multiplyScalar(this.deep), this._vecTemp1.add(this._vecNear), this._vecTemp2.add(this._vecNear), this._vecTemp3.add(this._vecNear);
      const { planes: n } = this.frustum;
      n[0].setFromCoplanarPoints(this._vecNear, this._vecTopLeft, this._vecTopRight), n[1].setFromCoplanarPoints(this._vecNear, this._vecTopRight, this._vecDownRight), n[2].setFromCoplanarPoints(this._vecDownRight, this._vecDownLeft, this._vecNear), n[3].setFromCoplanarPoints(this._vecDownLeft, this._vecTopLeft, this._vecNear), n[4].setFromCoplanarPoints(this._vecTopRight, this._vecDownRight, this._vecDownLeft), n[5].setFromCoplanarPoints(this._vecTemp3, this._vecTemp2, this._vecTemp1), n[5].normal.multiplyScalar(-1);
    } else
      console.error("THREE.SelectionBox: Unsupported camera type.");
  }
  searchObjectsInFrustum(t, i) {
    i.forEach((n) => {
      this._boxTemp.setFromObject(n), this._boxTemp.getCenter(this._center), n instanceof wn && this._center.applyMatrix4(n.matrixWorld), t.containsPoint(this._center) && this.collection.push(n);
    });
  }
  searchChildInFrustum(t, i) {
    if (i.isMesh || i.isLine || i.isPoints || i.isSprite)
      if (i.isInstancedMesh) {
        this.instances[i.uuid] = [];
        for (let n = 0; n < i.count; n++)
          i.getMatrixAt(n, this._matrix), this._matrix.decompose(this._center, this._quaternion, this._scale), this._center.applyMatrix4(i.matrixWorld), t.containsPoint(this._center) && this.instances[i.uuid].push(n);
      } else
        i.geometry.boundingSphere === null && i.geometry.computeBoundingSphere(), this._center.copy(i.geometry.boundingSphere.center), this._center.applyMatrix4(i.matrixWorld), i.geometry.getAttribute("position") && t.containsPoint(this._center) && this.collection.push(i);
    if (i.children.length > 0)
      for (let n = 0; n < i.children.length; n++)
        this.searchChildInFrustum(t, i.children[n]);
  }
}
class qhe extends mt {
  constructor() {
    super(...arguments);
    S(this, "isDragged", !1);
    S(this, "lastPosition", new _());
  }
  getOffset() {
    return this.position.clone().sub(this.lastPosition);
  }
}
function Um(s, e) {
  if (s.target instanceof HTMLCanvasElement) {
    const t = s.target.getAttribute("data-engine"), i = s.target.parentElement;
    if (t && i && t.indexOf("three.js r") === 0 && i.id === e)
      return !0;
  }
  return document.pointerLockElement !== null;
}
const K0 = class K0 extends es {
  constructor(t, i, n) {
    super();
    S(this, "dragPoint", new qhe());
    S(this, "transformObjectControls", new Whe(
      this
    ));
    S(this, "config", {
      // 模型不会返回点击的mesh, 这里直接让它返回group
      enablePushObjectType: [
        "EngineGroup",
        // 'Model',
        // 'BatchModel',
        "MiniMap",
        "DirectLight",
        "SunLight",
        "SpotLight",
        "RectAreaLight",
        "BatchCreateModelTool",
        "VideoProject",
        "ClipModel"
      ]
    });
    S(this, "htmlEvent", new khe());
    S(this, "_rayCaster", new lr());
    S(this, "_boxSelection");
    S(this, "_transformAdsorb");
    S(this, "_entityHasMouseInteraction", []);
    S(this, "_boundingBox");
    // 模型的导出原点
    S(this, "_originalPoint", new mt());
    S(this, "_transformControlsShowMode", "center");
    S(this, "_pivotMode", "BoundingBoxCenter");
    S(this, "_transformOrientation", "Global");
    S(this, "customPivotTransform", new mt());
    S(this, "pivotObjectStartTransform", new mt());
    S(this, "_transformControls");
    S(this, "_intersectionParams", []);
    S(this, "_enable", !0);
    S(this, "_handlePointLockClick", (t) => {
    });
    this.world = t, this._camera = i, this._canvas = n, this._boxSelection = new jhe(this._camera, this._canvas), this._transformControls = new BS(this._camera, this._canvas), this._transformAdsorb = new Ghe(), this._boundingBox = new Rp(this.world), this._boundingBox.visible = !1, this._handleTransformControlsInitEvent(), this.customPivotTransform.position.set(15, 0, 5), this.customPivotTransform.rotation.set(30 * nt.DEG2RAD, 0, 0);
  }
  get rayCaster() {
    return this._rayCaster;
  }
  get transformAdsorb() {
    return this._transformAdsorb;
  }
  get transformControls() {
    return this._transformControls;
  }
  set transformControls(t) {
    this._transformControls = t;
  }
  get intersectionParams() {
    return this._intersectionParams;
  }
  get enable() {
    return this._enable;
  }
  set enable(t) {
    this._enable = t;
  }
  _getSelectItems(t) {
    const i = [];
    return t.forEach(({ object: n, component: r, instanceId: a, entity: o, meshId: l }) => {
      var f;
      const c = (n == null ? void 0 : n.uuid) === (r == null ? void 0 : r.group.uuid) ? "" : (n == null ? void 0 : n.uuid) ?? "", h = r == null ? void 0 : r.getObjectById(c, a), u = (h == null ? void 0 : h.position) ?? new _(), d = {
        entityId: o.uuid,
        componentId: (r == null ? void 0 : r.id) ?? "",
        objId: c,
        customAttribute: (h == null ? void 0 : h.userData) ?? {},
        componentType: (r == null ? void 0 : r.type) ?? "EngineGroup",
        id: (h == null ? void 0 : h.uuid) ?? "",
        // 在编辑器中 修改和显示的是实体名字, 所以在传出数据是需要的也是实体名字
        name: o.name ?? "",
        position: {
          x: u.x,
          y: u.y,
          z: u.z
        }
      };
      (r == null ? void 0 : r.type) === "Model" && l && (d.meshName = (f = n == null ? void 0 : n.getObjectById(l)) == null ? void 0 : f.name, d.sceneGraph = r.getModelSceneGraph()), i.push(d);
    }), i;
  }
  _handleInitEvent() {
    this.htmlEvent.removeAllEventListeners(), this.htmlEvent.addEventListener("click", this._handleMouseClick.bind(this)), document.addEventListener("pointerlockchange", () => {
      document.pointerLockElement ? (console.log("Pointer locked"), document.addEventListener("mouseup", this._handlePointLockClick)) : (console.log("Pointer unlocked"), document.removeEventListener("mouseup", this._handlePointLockClick));
    }), this.htmlEvent.addEventListener("dragStart", this._handleMouseDragStart.bind(this)), this.htmlEvent.addEventListener("mousemove", (t) => {
      if (Um(t, this.world.containerId)) {
        const i = $r(
          t.clientX,
          t.clientY,
          t.target
        );
        K0.MouseParams.clientX = t.clientX, K0.MouseParams.clientY = t.clientY, K0.MouseParams.coord = i;
      }
    }), this.htmlEvent.active(document.body);
  }
  _handleTransformControlsInitEvent() {
    this._transformControls.addEventListener("dragging-changed", (t) => {
      if (this.world.renderer.cameraControls.enabled = !t.value, this.dragPoint.isDragged)
        this._intersectionParams.forEach(({ component: i }) => {
          i && this.setComponentUpdateFlag(i);
        });
      else {
        const i = [];
        this._intersectionParams.forEach(({ object: n }) => {
          n && i.push(n);
        }), i.push(this.dragPoint, this._boundingBox.borderLine), Qe.history.execute(new Hhe(i));
      }
      this.dragPoint.isDragged = !this.dragPoint.isDragged, this.dragPoint.lastPosition.copy(this.dragPoint.position), this._updatePivotObject();
    }), this._transformControls.addEventListener("objectChange", () => {
      this.handleObjectChange();
    });
  }
  setComponentUpdateFlag(t) {
    t.type !== "Model" && QAe(t), t.needsUpdate = !0, t.onDragEnd();
  }
  _getAdsorbResult(t) {
    const i = [], n = [];
    this._intersectionParams.forEach(({ object: o, component: l }) => {
      o && i.push(o), l && n.push(l.entity);
    });
    const a = this.world.entityManager.findModelComponentEntity().filter((o) => !n.some((l) => l.uuid === o.uuid));
    return this._transformAdsorb.getAdsorbResult(i, a, t);
  }
  _handleMouseClick(t) {
    this.enable && Um(t, this.world.containerId) && this._handleRaycasterSelect(t);
  }
  _handleMouseDragStart(t) {
    var i;
    if (this.enable && Um(t, this.world.containerId) && t.shiftKey) {
      this.world.renderer.cameraControls.enabled = !1;
      const n = t.target, r = (i = n == null ? void 0 : n.parentElement) == null ? void 0 : i.id;
      if (!(n instanceof HTMLCanvasElement) && r !== "drag-cssObject")
        return;
      const a = $r(t.clientX, t.clientY, n);
      this._boxSelection.setStartPoint(a.x, a.y, 0.5, this._canvas), this._boxSelection.helper.onSelectStart(t.clientX, t.clientY, this._canvas);
      const o = (c) => {
        this._boxSelection.helper.onSelectMove(c.clientX, c.clientY);
      }, l = (c) => {
        this.htmlEvent.removeEventListener("dragEnd", l), this.htmlEvent.removeEventListener("drag", o);
        const h = $r(c.clientX, c.clientY, n);
        this._boxSelection.setEndPoint(h.x, h.y, 0.5), this._handleBoxSelect(), this._boxSelection.helper.onSelectOver(), this.world.renderer.cameraControls.enabled = !0;
      };
      this.htmlEvent.addEventListener("dragEnd", l), this.htmlEvent.addEventListener("drag", o);
    }
  }
  _handleRaycasterSelect(t) {
    var o;
    this.hideTransformControls(), this.hideBoundingBox(), t.shiftKey || (this._intersectionParams.length = 0);
    const i = [...this.intersectionParams], n = this._getSelectComponents();
    if (Um(t, this.world.containerId)) {
      const l = $r(
        t.clientX,
        t.clientY,
        t.target
      );
      this._rayCaster.setFromCamera(
        l,
        this.world.renderer.activeCamera ?? this.world.renderer.mainCamera
      ), this.world.event.dispatchEvent("click", { ev: t, coord: l });
      const c = [];
      n.forEach((h) => {
        var f;
        const [u] = h.intersectObject(this._rayCaster, t);
        u && h.onClick(u, t);
        const d = this._getSelectComponentExtendInActive(h.entity);
        u && (u != null && u.object.visible) && c.push({
          entity: h.entity,
          ...u,
          component: h,
          inActive: d,
          meshId: (f = u.object) == null ? void 0 : f.id,
          // 这里还没有赋予 transform control, 那么drag point 的位置还没有更新,所以矩阵只能初始化而不能赋值
          dragMatrix: new et(),
          instanceId: u.instanceId,
          matrixInPivot: new et(),
          originFrame: new mt()
        });
      }), this._handleSortObjects(c), c.length && this._pushIntersectionParamsType({
        ...c[0]
      });
    }
    const r = t.target, a = (o = r == null ? void 0 : r.parentElement) == null ? void 0 : o.id;
    a === "drag-cssObject" && n.forEach((l) => {
      const c = r.getAttribute("data-component-id"), h = r.getAttribute("data-id");
      if (h && c === l.id) {
        const u = this._getSelectComponentExtendInActive(l.entity), d = l.getObjectById(h);
        this._pushIntersectionParamsType({
          object: d,
          entity: l.entity,
          component: l,
          inActive: u,
          // 这里还没有赋予 transform control, 那么drag point 的位置还没有更新,所以矩阵只能初始化而不能赋值
          dragMatrix: new et(),
          matrixInPivot: new et(),
          originFrame: new mt()
        });
      }
    }), (Um(t, this.world.containerId) || a === "drag-cssObject") && (this.showSelectHelpers(), Qe.history.execute(
      new Vhe(
        (l) => {
          this._intersectionParams.length = 0, l.forEach((c) => {
            this._pushIntersectionParamsType(c);
          }), this.showSelectHelpers();
        },
        i,
        this.intersectionParams
      )
    ), console.log("sssssssss", this.intersectionParams), this.dispatchSelectEvent(this.intersectionParams));
  }
  // 处理点击中, 距离为0的物体
  _handleSortObjects(t) {
    const i = {
      zeroIntersects: [],
      distanceIntersects: []
    };
    return t.forEach((n) => {
      var r;
      if (console.log("result", "------"), n.object && (n.object instanceof Re || n.object instanceof wn) && n.object.material && n.component && n.component instanceof Kg && !n.object.material.depthTest && (n.distance = 0), !n.distance || n.distance === 0) {
        i.zeroIntersects.push(n);
        const a = ((r = n.object) == null ? void 0 : r.position) ?? new _();
        n.distance = a.distanceToSquared(this._rayCaster.camera.position);
      } else
        i.distanceIntersects.push(n);
    }), i.zeroIntersects.sort((n, r) => (n.distance ?? 0) - (r.distance ?? 0)), t.length = 0, i.zeroIntersects.forEach((n, r) => n.distance = r + 1), i.distanceIntersects.sort((n, r) => (n.distance ?? 0) - (r.distance ?? 0)), t.push(...i.zeroIntersects, ...i.distanceIntersects), t;
  }
  _handleBoxSelect() {
    this.hideBoundingBox(), this.hideTransformControls(), this._getBoxSelectionResult().forEach((i) => {
      this._pushIntersectionParamsType(i);
    }), this.showSelectHelpers(), this.dispatchSelectEvent(this.intersectionParams);
  }
  _getBoxSelectionResult() {
    const t = [];
    return this.world.drawUtils.state !== to.None || Tt.enabled && this._getSelectComponents().forEach((n) => {
      const r = this._boxSelection.select(n.entity), a = this._getSelectComponentExtendInActive(n.entity);
      if (r.length > 0) {
        const o = /* @__PURE__ */ new Map(), l = /* @__PURE__ */ new Set();
        o.set(n, l), r.forEach((c) => {
          const h = n.getObjectById(c.uuid);
          h && l.add(h);
        }), o.forEach((c, h) => {
          c.forEach((u) => {
            t.push({
              object: u,
              entity: h.entity,
              component: h,
              dragMatrix: new et(),
              inActive: a,
              matrixInPivot: new et(),
              originFrame: new mt()
            });
          });
        });
      }
    }), t;
  }
  _pushIntersectionParamsType(t, i) {
    const { component: n, entity: r } = t;
    return r.canSelectedInWorld && (n ? (t.object && (t.object = n.getObjectById(t.object.uuid, t.instanceId)), !this._checkEnablePushObjectIntersectionParams(n, i) && (t.object = n.group), this._intersectionParams.push(t)) : this._intersectionParams.push(t)), [...this._intersectionParams];
  }
  /**
   *
   * @param component
   * @param objId 传入obj id 代表需要返回obj id 作为前端参数
   * @returns
   */
  _checkEnablePushObjectIntersectionParams(t, i) {
    return !!(this.config.enablePushObjectType.includes(t.type) || i || !Tt.enabled);
  }
  // 获取 需要参与相交判断的 components
  _getSelectComponents() {
    const t = [];
    return this.world.entityManager.entityMap.forEach((i) => {
      i.componentMap.forEach((n) => {
        n.getCanAttach() && this._getSelectComponentExtendVisible(n.group) && t.push(n);
      });
    }), t.filter((i) => !i.disablePick);
  }
  _getSelectComponentExtendVisible(t) {
    const i = (n, r) => n.parent ? i(n.parent, r && n.parent.visible) : r;
    return i(t, t.visible);
  }
  _getSelectComponentExtendInActive(t) {
    const { mPath: i } = t, n = i.split(".");
    let r = t.inActive;
    return n.forEach((a) => {
      const o = this.world.entityManager.getEntity(a);
      o != null && o.inActive && (r = !0);
    }), r;
  }
  initialize() {
    return this.world.add(this._transformControls), this.world.add(this.dragPoint), this._handleInitEvent(), this;
  }
  setEntityMouseInteraction(t) {
    this._entityHasMouseInteraction.push(...t);
  }
  tick() {
    let t = !1;
    this._entityHasMouseInteraction.forEach((i) => {
      const n = this.world.entityManager.getEntity(i);
      if (n) {
        const r = [], a = /* @__PURE__ */ new Set(), o = (l) => {
          a.add(l.uuid), this.world.entityManager.entityMap.forEach((c) => {
            c.parentId === l.uuid && (r.push(c), c.hasComponent("EngineGroup") && !a.has(c.uuid) && o(c));
          });
        };
        n.hasComponent("EngineGroup") ? o(n) : r.push(n), r.forEach((l) => {
          this._getSelectComponentExtendInActive(l) || l.componentMap.forEach((c) => {
            if (this._getSelectComponentExtendVisible(c.group)) {
              this._rayCaster.setFromCamera(
                K0.MouseParams.coord,
                this.world.renderer.activeCamera ?? this.world.renderer.mainCamera
              );
              const [h] = c.intersectObject(this._rayCaster, {
                clientX: K0.MouseParams.clientX,
                clientY: K0.MouseParams.clientY
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              });
              h && (t = !0);
            }
          });
        });
      }
    }), this.world.drawUtils.state !== to.StartDraw && (t && cs.setCursorCssText(this._canvas, "pointer", "selection"), !t && cs.setCursorCssText(this._canvas, "default", "selection"));
  }
  setTransformControlsShowMode(t) {
    t && (this._transformControlsShowMode = t, this.updateBoundingBox());
  }
  getTransformControlsShowMode() {
    return this._transformControlsShowMode;
  }
  radioSelectWithOutComponentId(t, i, n) {
    this.clearIntersections();
    const r = this.world.entityManager.getEntity(t);
    if (r) {
      let a;
      if (r.componentMap.forEach((o) => o.getCanAttach() && (a = o)), a) {
        const o = a.getObjectById(i ?? "");
        this._pushIntersectionParamsType({
          object: o,
          component: a,
          entity: a.entity,
          inActive: this._getSelectComponentExtendInActive(r),
          dragMatrix: new et(),
          instanceId: n,
          matrixInPivot: new et(),
          originFrame: new mt()
        });
      }
      this.dispatchSelectEvent(this.intersectionParams);
    }
  }
  radioSelectById(t, i, n, r) {
    if (this.clearIntersections(), t) {
      const a = this.world.entityManager.getEntity(t);
      if (a) {
        let o;
        i ? o = a.componentMap.get(i) : o = a.getEntityComponentByCommandName(p7.Transform);
        const l = o == null ? void 0 : o.getObjectById(n ?? "", r), c = this._getSelectComponentExtendInActive(a);
        o && this._pushIntersectionParamsType(
          {
            object: l,
            component: o,
            inActive: c,
            entity: o.entity,
            // 这里还没有赋予 transform control, 那么drag point 的位置还没有更新,所以矩阵只能初始化而不能赋值
            dragMatrix: new et(),
            instanceId: r,
            matrixInPivot: new et(),
            originFrame: new mt()
          },
          n
        );
      }
    }
  }
  // 从世界大纲中选中
  selectById(t = !1, i, n, r, a) {
    if (t || this.clearIntersections(), i) {
      const o = this.world.entityManager.getEntity(i);
      if (o) {
        let l;
        n ? l = o.componentMap.get(n) : l = o.getEntityComponentByCommandName(p7.Transform);
        const c = l == null ? void 0 : l.getObjectById(r ?? ""), h = this._getSelectComponentExtendInActive(o);
        this._pushIntersectionParamsType(
          {
            object: c,
            component: l,
            entity: o,
            inActive: h,
            // 这里还没有赋予 transform control, 那么drag point 的位置还没有更新,所以矩阵只能初始化而不能赋值
            dragMatrix: new et(),
            instanceId: a,
            matrixInPivot: new et(),
            originFrame: new mt()
          },
          r
        ), this.showSelectHelpers();
      }
    }
    this.dispatchSelectEvent(this.intersectionParams);
  }
  removeSelectById(t) {
    const i = this.intersectionParams.findIndex(
      (o) => {
        var l;
        return ((l = o.component) == null ? void 0 : l.entity.uuid) === t;
      }
    );
    i !== -1 && this.intersectionParams.splice(i, 1);
    const { length: n } = this.intersectionParams, { object: r } = this.intersectionParams[i - 1 < 0 ? 0 : i - 1];
    r && n > 0 && this.dragPoint.position.copy(new _().setFromMatrixPosition(r.matrixWorld));
    const a = [];
    this.intersectionParams.forEach((o) => {
      o.object && a.push(o.object);
    }), this.dispatchEvent("select", {
      objs: a,
      selectItems: this._getSelectItems(this.intersectionParams)
    }), this.refresh();
  }
  multiSelectById(t) {
  }
  dispose() {
    this.htmlEvent.removeAllEventListeners(), this.htmlEvent.deActive(document.body);
  }
  showSelectHelpers() {
    if (this.intersectionParams.length === 0)
      return;
    let t = !0;
    this.world.drawUtils.state !== to.None && this.world.drawUtils.editMode === bn.Create && (t = !1), Tt.enabled || (t = !1), t && (this.showBoundingBox(), this.showTransformControls());
  }
  attachDragPoint() {
    this.showTransformControls(), this.showBoundingBox();
  }
  detachDragPoint() {
    this.hideTransformControls(), this.hideBoundingBox();
  }
  handleDragEnd() {
    this.intersectionParams.forEach(({ component: t }) => {
      t && (t.needDataUpdateServer = !0, t.needsUpdate = !0, t.onDragEnd());
    });
  }
  dispatchSelectEvent(t) {
    const i = [];
    t.forEach((n) => {
      n.object && i.push(n.object);
    }), this.dispatchEvent("select", {
      objs: i,
      selectItems: this._getSelectItems(t)
    });
  }
  // 只有出现了拖动辅助器,才会出现拖动, 所以对于选中物体的矩阵更新放在这个函数
  showTransformControls() {
    if (this._intersectionParams.length === 0)
      return;
    console.log("show transform");
    let t = !1;
    if (this.world.drawUtils.editMode === bn.Move && this.world.drawUtils.mode === "multi-polygon") {
      const [i] = this._intersectionParams;
      i.object && this.world.drawUtils.polygonUtils.attach(i.object), t = !0;
    }
    if (!t) {
      this._updatePivotObject();
      let i = !0;
      this._intersectionParams.forEach(
        ({ object: n, dragMatrix: r, inActive: a, component: o, matrixInPivot: l, originFrame: c }) => {
          const h = new et().copy(this.dragPoint.matrix).invert();
          n && r.copy(n.matrix.clone().premultiply(h)), i && (i = !a), o ? (i && (i = o.getCanAttach()), o.showHelper()) : i = !1, n && "attachTransformControls" in n && n.attachTransformControls();
        }
      ), i && this._transformControls.attach(this.dragPoint);
    }
  }
  /**
   *
   * @param entity
   * @param object 射线相交的实际物体，不是最上层那个
   */
  executeAutoFocus(t, i, n) {
    if (Qe.Info.autoFocus.model) {
      const r = t.getComponentByType("Model");
      r && this.executeAutoFocusOnModel(r, i, n);
    }
  }
  executeAutoFocusOnModel(t, i, n) {
    const r = new zt();
    i instanceof ho ? n !== void 0 && r.copy(yee(i, n, new zt())) : r.setFromObject(t.group);
    const { distance: a, duration: o } = Qe.Info.autoFocus;
    a && r.expandByScalar(a), this.world.flyToBox(r, { duration: o });
  }
  getObjects() {
    const t = [];
    return this._intersectionParams.forEach((i) => {
      i.object ? t.push(i.object) : i.component && t.push(i.component.group);
    }), t;
  }
  hideTransformControls() {
    this.intersectionParams.forEach(({ object: t, component: i }) => {
      t && "attachTransformControls" in t && t.detachTransformControls(), i == null || i.hideHelper();
    }), this._transformControls.detach();
  }
  showBoundingBox() {
    this.updateBoundingBox(), this._boundingBox.show();
  }
  hideBoundingBox() {
    this._boundingBox.box.makeEmpty(), this._boundingBox.hide();
  }
  clearIntersections() {
    this.hideTransformControls(), this.hideBoundingBox(), this._intersectionParams.length = 0;
  }
  refresh() {
    this._intersectionParams.forEach((t) => {
      const { component: i, object: n } = t;
      if (i) {
        const r = this._getSelectComponentExtendInActive(i.entity);
        t.inActive = r;
      }
    }), this.hideTransformControls(), this.hideBoundingBox(), this.showSelectHelpers();
  }
  updateBoundingBox() {
    this._boundingBox.setColor(0.211, 0.443, 0.878), this._boundingBox.box.makeEmpty();
    const t = new _(), i = new Mi();
    switch (this._intersectionParams.forEach(({ component: n, dragMatrix: r, inActive: a, object: o }) => {
      var c;
      a && this._boundingBox.setColor(1, 0, 0);
      const l = new _();
      o ? (o.updateWorldMatrix(!1, !1), (n == null ? void 0 : n.type) === "Model" || (n == null ? void 0 : n.type) === "BatchModel" ? (this._boundingBox.box.expandByObject(o), (c = o.children[0]) == null || c.updateMatrixWorld(), o.children[0] && t.add(
        new _().setFromMatrixPosition(o.children[0].matrixWorld)
      ), o.children[0] && i.copy(new Mi().setFromRotationMatrix(o.children[0].matrixWorld))) : n instanceof t4 || n instanceof Vg ? (this._boundingBox.box.expandByObject(o), this._boundingBox.box.getCenter(l)) : (this._boundingBox.box.setFromCenterAndSize(
        new _().setFromMatrixPosition(o.matrixWorld),
        new _()
      ), this._boundingBox.box.getCenter(l)), t.add(l)) : n && (n.type === "Model" || n.type === "BatchModel") ? (this._boundingBox.box.expandByObject(n.group), n.group.children[0] && t.add(
        new _().setFromMatrixPosition(n.group.children[0].matrixWorld)
      ), i.copy(n.group.rotation)) : n && t.add(n.group.position);
    }), this._transformControlsShowMode) {
      case "center":
        this._boundingBox.box.getCenter(this._originalPoint.position);
        break;
      case "origin":
        this._originalPoint.position.copy(
          t.divideScalar(this.intersectionParams.length)
        );
        break;
    }
    this._boundingBox.updateBorderLine();
  }
  raycasterSelect(t, i) {
    if (this.clearIntersections(), t.target instanceof HTMLCanvasElement) {
      const n = $r(t.clientX, t.clientY, t.target);
      this._rayCaster.setFromCamera(
        n,
        this.world.renderer.activeCamera ?? this.world.renderer.mainCamera
      );
      const [r] = i.intersectObject(this._rayCaster, t);
      if (r)
        return i.onClick(r, t), this.executeAutoFocus(i.entity, r.object), {
          ...r,
          object: i.getObjectById(r.object.uuid, r.instanceId),
          component: i,
          entity: i.entity,
          inActive: this._getSelectComponentExtendInActive(i.entity),
          dragMatrix: new et(),
          matrixInPivot: new et(),
          originFrame: new mt()
        };
    }
  }
  setTransformMode(t) {
    this.transformControls && (this.transformControls.mode = t);
  }
  setSpace(t) {
    this._transformOrientation = t, t === "Global" ? this.transformControls.setSpace("world") : t === "Local" && this.transformControls.setSpace("local"), this._updatePivotObject();
  }
  setTransformPivotMode(t) {
    this._pivotMode = t, this._updatePivotObject();
  }
  handleObjectChange() {
    const t = this.dragPoint.getOffset(), [i, n, r] = this._getAdsorbResult(t.clone());
    if (i) {
      this._transformControls.enabled = !1, t.add(r), this.dragPoint.position.add(r);
      const o = setTimeout(() => {
        this._transformControls.enabled = !0, clearTimeout(o);
      }, 200);
    }
    this._boundingBox.box.makeEmpty(), new _(), new _(), this.dragPoint.updateWorldMatrix(!1, !1);
    const a = [...this._intersectionParams];
    switch (this._intersectionParams.forEach(({ component: o }) => {
      if (o) {
        const l = this._intersectionParams.findIndex(
          (c) => c.entity.mPath.includes(o.entity.uuid) && c.entity.uuid !== o.entity.uuid
        );
        l !== -1 && a.splice(l, 1);
      }
    }), this.dragPoint.updateMatrixWorld(!0), this._transformOrientation) {
      case "Global": {
        switch (this._pivotMode) {
          case "BoundingBoxCenter":
          case "CustomPosition":
            a.forEach(
              ({ object: o, dragMatrix: l, inActive: c, component: h, matrixInPivot: u, originFrame: d }) => {
                var f;
                if (o && !c) {
                  const v = this.dragPoint.matrixWorld.clone().multiply(u).premultiply(
                    ((f = o.parent) == null ? void 0 : f.matrixWorld.clone().invert()) ?? new et()
                  ), g = new _(), m = new St(), x = new _();
                  v.decompose(g, m, x), o.position.copy(g), o.quaternion.copy(m), o.scale.copy(x), o.updateMatrixWorld(!0), h && h.updateChildPosition(
                    o.uuid,
                    new _().setFromMatrixPosition(o.matrixWorld.clone())
                  ), h == null || h.onObjectChange();
                }
              }
            );
            break;
          case "IndividualOrigins":
            a.forEach(
              ({ object: o, dragMatrix: l, inActive: c, component: h, matrixInPivot: u, originFrame: d }) => {
                var f;
                if (o && !c) {
                  const p = this.dragPoint.position.clone().sub(this.pivotObjectStartTransform.position), v = this.dragPoint.quaternion.clone(), g = this.dragPoint.scale.clone(), m = d.clone();
                  if (m.position.copy(p.add(d.position)), m.quaternion.copy(v), m.scale.copy(g), m.updateMatrixWorld(!0), m.children[0]) {
                    const x = m.children[0].matrixWorld.clone().premultiply(((f = o.parent) == null ? void 0 : f.matrixWorld.clone().invert()) ?? new et()), y = new _(), A = new St(), b = new _();
                    x.decompose(y, A, b), o.position.copy(y), o.quaternion.copy(A), o.scale.copy(b), o.updateMatrixWorld(!0);
                  }
                  h && h.updateChildPosition(
                    o.uuid,
                    new _().setFromMatrixPosition(o.matrixWorld.clone())
                  ), h == null || h.onObjectChange();
                }
              }
            );
            break;
        }
        break;
      }
      case "Local": {
        switch (this._pivotMode) {
          case "BoundingBoxCenter":
          case "CustomPosition":
            a.forEach(
              ({ object: o, dragMatrix: l, inActive: c, component: h, matrixInPivot: u, originFrame: d }) => {
                var f;
                if (o && !c) {
                  const v = this.dragPoint.matrixWorld.clone().multiply(u).premultiply(
                    ((f = o.parent) == null ? void 0 : f.matrixWorld.clone().invert()) ?? new et()
                  ), g = new _(), m = new St(), x = new _();
                  v.decompose(g, m, x), o.position.copy(g), o.quaternion.copy(m), o.scale.copy(x), o.updateMatrixWorld(!0), h && h.updateChildPosition(
                    o.uuid,
                    new _().setFromMatrixPosition(o.matrixWorld.clone())
                  ), h == null || h.onObjectChange();
                }
              }
            );
            break;
          case "IndividualOrigins":
            a.forEach(
              ({ object: o, dragMatrix: l, inActive: c, component: h, matrixInPivot: u, originFrame: d }) => {
                var f;
                if (o && !c) {
                  const p = this.dragPoint.position.clone().sub(this.pivotObjectStartTransform.position), v = this.dragPoint.quaternion.clone(), g = this.dragPoint.scale.clone(), m = d.clone();
                  if (m.position.copy(p.add(d.position)), m.quaternion.copy(v), m.scale.copy(g), m.updateMatrixWorld(!0), m.children[0]) {
                    const x = m.children[0].matrixWorld.clone().premultiply(((f = o.parent) == null ? void 0 : f.matrixWorld.clone().invert()) ?? new et()), y = new _(), A = new St(), b = new _();
                    x.decompose(y, A, b), o.position.copy(y), o.quaternion.copy(A), o.scale.copy(b), o.updateMatrixWorld(!0);
                  }
                  h && h.updateChildPosition(
                    o.uuid,
                    new _().setFromMatrixPosition(o.matrixWorld.clone())
                  ), h == null || h.onObjectChange();
                }
              }
            );
            break;
        }
        break;
      }
    }
    this.updateBoundingBox(), this.dragPoint.lastPosition.copy(this.dragPoint.position);
  }
  /**
   * 根据变换空间和枢轴点模式，更新变换枢轴点位置和方向
   */
  _updatePivotObject() {
    this.dragPoint.position.set(0, 0, 0), this.dragPoint.scale.set(1, 1, 1), this.dragPoint.rotation.set(0, 0, 0), this._transformOrientation === "Global" ? this._setPivotForGlobalOrientation() : this._transformOrientation === "Local" && this._setPivotForLocalOrientation(), this.dragPoint.updateMatrixWorld(!0), this.pivotObjectStartTransform = this.dragPoint.clone(), this._updateObjectMatrixInPivot();
  }
  _setPivotForGlobalOrientation() {
    switch (this._pivotMode) {
      case "IndividualOrigins":
      case "BoundingBoxCenter":
        this._setPositionToCenter();
        break;
      case "CustomPosition":
        this._setPositionAndRotationFromCustomPivot();
        break;
    }
  }
  _setPivotForLocalOrientation() {
    switch (this._pivotMode) {
      case "IndividualOrigins":
      case "BoundingBoxCenter":
        this._setPositionToCenter(), this._setRotationFromFirstIntersectedObject();
        break;
      case "CustomPosition":
        this._setPositionAndRotationFromCustomPivot();
        break;
    }
  }
  _setPositionToCenter() {
    const t = this._originalPoint.position;
    this.dragPoint.position.copy(t);
  }
  _setRotationFromFirstIntersectedObject() {
    const t = this._intersectionParams[0];
    if (t && t.object) {
      const i = new St(), n = t.object.getWorldQuaternion(i);
      this.dragPoint.quaternion.copy(n);
    }
  }
  _setPositionAndRotationFromCustomPivot() {
    this.dragPoint.position.copy(this.customPivotTransform.position), this.dragPoint.rotation.copy(this.customPivotTransform.rotation);
  }
  _updateObjectMatrixInPivot() {
    this._intersectionParams.forEach(({ object: t, matrixInPivot: i, originFrame: n }) => {
      if (t) {
        t.updateMatrixWorld(!0);
        const r = t.matrixWorld.clone().premultiply(this.dragPoint.matrixWorld.clone().invert());
        i.copy(r);
        const a = new mt();
        a.position.copy(t.getWorldPosition(new _())), a.updateMatrixWorld(!0);
        const o = new mt();
        t.matrixWorld.decompose(o.position, o.quaternion, o.scale), o.updateMatrixWorld(!0), a.attach(o), n.copy(a);
      }
    });
  }
};
S(K0, "MouseParams", {
  clientX: NaN,
  clientY: NaN,
  coord: new Se()
});
let io = K0;
function Yhe(s) {
  s("EPSG:4326", "+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees"), s("EPSG:4269", "+title=NAD83 (long/lat) +proj=longlat +a=6378137.0 +b=6356752.31414036 +ellps=GRS80 +datum=NAD83 +units=degrees"), s("EPSG:3857", "+title=WGS 84 / Pseudo-Mercator +proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs");
  for (var e = 1; e <= 60; ++e)
    s("EPSG:" + (32600 + e), "+proj=utm +zone=" + e + " +datum=WGS84 +units=m"), s("EPSG:" + (32700 + e), "+proj=utm +zone=" + e + " +south +datum=WGS84 +units=m");
  s.WGS84 = s["EPSG:4326"], s["EPSG:3785"] = s["EPSG:3857"], s.GOOGLE = s["EPSG:3857"], s["EPSG:900913"] = s["EPSG:3857"], s["EPSG:102113"] = s["EPSG:3857"];
}
var Bu = 1, Fu = 2, qf = 3, Zhe = 4, Kb = 5, SP = 6378137, Khe = 6356752314e-3, wP = 0.0066943799901413165, F1 = 484813681109536e-20, xt = Math.PI / 2, Jhe = 0.16666666666666666, Qhe = 0.04722222222222222, $he = 0.022156084656084655, Dt = 1e-10, zs = 0.017453292519943295, Ko = 57.29577951308232, $i = Math.PI / 4, zg = Math.PI * 2, Jn = 3.14159265359, zr = {};
zr.greenwich = 0;
zr.lisbon = -9.131906111111;
zr.paris = 2.337229166667;
zr.bogota = -74.080916666667;
zr.madrid = -3.687938888889;
zr.rome = 12.452333333333;
zr.bern = 7.439583333333;
zr.jakarta = 106.807719444444;
zr.ferro = -17.666666666667;
zr.brussels = 4.367975;
zr.stockholm = 18.058277777778;
zr.athens = 23.7163375;
zr.oslo = 10.722916666667;
const eue = {
  mm: { to_meter: 1e-3 },
  cm: { to_meter: 0.01 },
  ft: { to_meter: 0.3048 },
  "us-ft": { to_meter: 1200 / 3937 },
  fath: { to_meter: 1.8288 },
  kmi: { to_meter: 1852 },
  "us-ch": { to_meter: 20.1168402336805 },
  "us-mi": { to_meter: 1609.34721869444 },
  km: { to_meter: 1e3 },
  "ind-ft": { to_meter: 0.30479841 },
  "ind-yd": { to_meter: 0.91439523 },
  mi: { to_meter: 1609.344 },
  yd: { to_meter: 0.9144 },
  ch: { to_meter: 20.1168 },
  link: { to_meter: 0.201168 },
  dm: { to_meter: 0.01 },
  in: { to_meter: 0.0254 },
  "ind-ch": { to_meter: 20.11669506 },
  "us-in": { to_meter: 0.025400050800101 },
  "us-yd": { to_meter: 0.914401828803658 }
};
var EP = /[\s_\-\/\(\)]/g;
function Yc(s, e) {
  if (s[e])
    return s[e];
  for (var t = Object.keys(s), i = e.toLowerCase().replace(EP, ""), n = -1, r, a; ++n < t.length; )
    if (r = t[n], a = r.toLowerCase().replace(EP, ""), a === i)
      return s[r];
}
function Jb(s) {
  var e = {}, t = s.split("+").map(function(o) {
    return o.trim();
  }).filter(function(o) {
    return o;
  }).reduce(function(o, l) {
    var c = l.split("=");
    return c.push(!0), o[c[0].toLowerCase()] = c[1], o;
  }, {}), i, n, r, a = {
    proj: "projName",
    datum: "datumCode",
    rf: function(o) {
      e.rf = parseFloat(o);
    },
    lat_0: function(o) {
      e.lat0 = o * zs;
    },
    lat_1: function(o) {
      e.lat1 = o * zs;
    },
    lat_2: function(o) {
      e.lat2 = o * zs;
    },
    lat_ts: function(o) {
      e.lat_ts = o * zs;
    },
    lon_0: function(o) {
      e.long0 = o * zs;
    },
    lon_1: function(o) {
      e.long1 = o * zs;
    },
    lon_2: function(o) {
      e.long2 = o * zs;
    },
    alpha: function(o) {
      e.alpha = parseFloat(o) * zs;
    },
    gamma: function(o) {
      e.rectified_grid_angle = parseFloat(o);
    },
    lonc: function(o) {
      e.longc = o * zs;
    },
    x_0: function(o) {
      e.x0 = parseFloat(o);
    },
    y_0: function(o) {
      e.y0 = parseFloat(o);
    },
    k_0: function(o) {
      e.k0 = parseFloat(o);
    },
    k: function(o) {
      e.k0 = parseFloat(o);
    },
    a: function(o) {
      e.a = parseFloat(o);
    },
    b: function(o) {
      e.b = parseFloat(o);
    },
    r: function(o) {
      e.a = e.b = parseFloat(o);
    },
    r_a: function() {
      e.R_A = !0;
    },
    zone: function(o) {
      e.zone = parseInt(o, 10);
    },
    south: function() {
      e.utmSouth = !0;
    },
    towgs84: function(o) {
      e.datum_params = o.split(",").map(function(l) {
        return parseFloat(l);
      });
    },
    to_meter: function(o) {
      e.to_meter = parseFloat(o);
    },
    units: function(o) {
      e.units = o;
      var l = Yc(eue, o);
      l && (e.to_meter = l.to_meter);
    },
    from_greenwich: function(o) {
      e.from_greenwich = o * zs;
    },
    pm: function(o) {
      var l = Yc(zr, o);
      e.from_greenwich = (l || parseFloat(o)) * zs;
    },
    nadgrids: function(o) {
      o === "@null" ? e.datumCode = "none" : e.nadgrids = o;
    },
    axis: function(o) {
      var l = "ewnsud";
      o.length === 3 && l.indexOf(o.substr(0, 1)) !== -1 && l.indexOf(o.substr(1, 1)) !== -1 && l.indexOf(o.substr(2, 1)) !== -1 && (e.axis = o);
    },
    approx: function() {
      e.approx = !0;
    }
  };
  for (i in t)
    n = t[i], i in a ? (r = a[i], typeof r == "function" ? r(n) : e[r] = n) : e[i] = n;
  return typeof e.datumCode == "string" && e.datumCode !== "WGS84" && (e.datumCode = e.datumCode.toLowerCase()), e;
}
var kg = 1, oF = 2, lF = 3, Tx = 4, cF = 5, Aw = -1, tue = /\s/, iue = /[A-Za-z]/, nue = /[A-Za-z84_]/, c8 = /[,\]]/, hF = /[\d\.E\-\+]/;
function Dl(s) {
  if (typeof s != "string")
    throw new Error("not a string");
  this.text = s.trim(), this.level = 0, this.place = 0, this.root = null, this.stack = [], this.currentObject = null, this.state = kg;
}
Dl.prototype.readCharicter = function() {
  var s = this.text[this.place++];
  if (this.state !== Tx)
    for (; tue.test(s); ) {
      if (this.place >= this.text.length)
        return;
      s = this.text[this.place++];
    }
  switch (this.state) {
    case kg:
      return this.neutral(s);
    case oF:
      return this.keyword(s);
    case Tx:
      return this.quoted(s);
    case cF:
      return this.afterquote(s);
    case lF:
      return this.number(s);
    case Aw:
      return;
  }
};
Dl.prototype.afterquote = function(s) {
  if (s === '"') {
    this.word += '"', this.state = Tx;
    return;
  }
  if (c8.test(s)) {
    this.word = this.word.trim(), this.afterItem(s);
    return;
  }
  throw new Error(`havn't handled "` + s + '" in afterquote yet, index ' + this.place);
};
Dl.prototype.afterItem = function(s) {
  if (s === ",") {
    this.word !== null && this.currentObject.push(this.word), this.word = null, this.state = kg;
    return;
  }
  if (s === "]") {
    this.level--, this.word !== null && (this.currentObject.push(this.word), this.word = null), this.state = kg, this.currentObject = this.stack.pop(), this.currentObject || (this.state = Aw);
    return;
  }
};
Dl.prototype.number = function(s) {
  if (hF.test(s)) {
    this.word += s;
    return;
  }
  if (c8.test(s)) {
    this.word = parseFloat(this.word), this.afterItem(s);
    return;
  }
  throw new Error(`havn't handled "` + s + '" in number yet, index ' + this.place);
};
Dl.prototype.quoted = function(s) {
  if (s === '"') {
    this.state = cF;
    return;
  }
  this.word += s;
};
Dl.prototype.keyword = function(s) {
  if (nue.test(s)) {
    this.word += s;
    return;
  }
  if (s === "[") {
    var e = [];
    e.push(this.word), this.level++, this.root === null ? this.root = e : this.currentObject.push(e), this.stack.push(this.currentObject), this.currentObject = e, this.state = kg;
    return;
  }
  if (c8.test(s)) {
    this.afterItem(s);
    return;
  }
  throw new Error(`havn't handled "` + s + '" in keyword yet, index ' + this.place);
};
Dl.prototype.neutral = function(s) {
  if (iue.test(s)) {
    this.word = s, this.state = oF;
    return;
  }
  if (s === '"') {
    this.word = "", this.state = Tx;
    return;
  }
  if (hF.test(s)) {
    this.word = s, this.state = lF;
    return;
  }
  if (c8.test(s)) {
    this.afterItem(s);
    return;
  }
  throw new Error(`havn't handled "` + s + '" in neutral yet, index ' + this.place);
};
Dl.prototype.output = function() {
  for (; this.place < this.text.length; )
    this.readCharicter();
  if (this.state === Aw)
    return this.root;
  throw new Error('unable to parse string "' + this.text + '". State is ' + this.state);
};
function sue(s) {
  var e = new Dl(s);
  return e.output();
}
function IA(s, e, t) {
  Array.isArray(e) && (t.unshift(e), e = null);
  var i = e ? {} : s, n = t.reduce(function(r, a) {
    return Ef(a, r), r;
  }, i);
  e && (s[e] = n);
}
function Ef(s, e) {
  if (!Array.isArray(s)) {
    e[s] = !0;
    return;
  }
  var t = s.shift();
  if (t === "PARAMETER" && (t = s.shift()), s.length === 1) {
    if (Array.isArray(s[0])) {
      e[t] = {}, Ef(s[0], e[t]);
      return;
    }
    e[t] = s[0];
    return;
  }
  if (!s.length) {
    e[t] = !0;
    return;
  }
  if (t === "TOWGS84") {
    e[t] = s;
    return;
  }
  if (t === "AXIS") {
    t in e || (e[t] = []), e[t].push(s);
    return;
  }
  Array.isArray(t) || (e[t] = {});
  var i;
  switch (t) {
    case "UNIT":
    case "PRIMEM":
    case "VERT_DATUM":
      e[t] = {
        name: s[0].toLowerCase(),
        convert: s[1]
      }, s.length === 3 && Ef(s[2], e[t]);
      return;
    case "SPHEROID":
    case "ELLIPSOID":
      e[t] = {
        name: s[0],
        a: s[1],
        rf: s[2]
      }, s.length === 4 && Ef(s[3], e[t]);
      return;
    case "EDATUM":
    case "ENGINEERINGDATUM":
    case "LOCAL_DATUM":
    case "DATUM":
    case "VERT_CS":
    case "VERTCRS":
    case "VERTICALCRS":
      s[0] = ["name", s[0]], IA(e, t, s);
      return;
    case "COMPD_CS":
    case "COMPOUNDCRS":
    case "FITTED_CS":
    case "PROJECTEDCRS":
    case "PROJCRS":
    case "GEOGCS":
    case "GEOCCS":
    case "PROJCS":
    case "LOCAL_CS":
    case "GEODCRS":
    case "GEODETICCRS":
    case "GEODETICDATUM":
    case "ENGCRS":
    case "ENGINEERINGCRS":
      s[0] = ["name", s[0]], IA(e, t, s), e[t].type = t;
      return;
    default:
      for (i = -1; ++i < s.length; )
        if (!Array.isArray(s[i]))
          return Ef(s, e[t]);
      return IA(e, t, s);
  }
}
var rue = 0.017453292519943295, aue = [
  "PROJECTEDCRS",
  "PROJCRS",
  "GEOGCS",
  "GEOCCS",
  "PROJCS",
  "LOCAL_CS",
  "GEODCRS",
  "GEODETICCRS",
  "GEODETICDATUM",
  "ENGCRS",
  "ENGINEERINGCRS"
];
function oue(s, e) {
  var t = e[0], i = e[1];
  !(t in s) && i in s && (s[t] = s[i], e.length === 3 && (s[t] = e[2](s[t])));
}
function Co(s) {
  return s * rue;
}
function uF(s) {
  for (var e = Object.keys(s), t = 0, i = e.length; t < i; ++t) {
    var n = e[t];
    aue.indexOf(n) !== -1 && lue(s[n]), typeof s[n] == "object" && uF(s[n]);
  }
}
function lue(s) {
  if (s.AUTHORITY) {
    var e = Object.keys(s.AUTHORITY)[0];
    e && e in s.AUTHORITY && (s.title = e + ":" + s.AUTHORITY[e]);
  }
  if (s.type === "GEOGCS" ? s.projName = "longlat" : s.type === "LOCAL_CS" ? (s.projName = "identity", s.local = !0) : typeof s.PROJECTION == "object" ? s.projName = Object.keys(s.PROJECTION)[0] : s.projName = s.PROJECTION, s.AXIS) {
    for (var t = "", i = 0, n = s.AXIS.length; i < n; ++i) {
      var r = [s.AXIS[i][0].toLowerCase(), s.AXIS[i][1].toLowerCase()];
      r[0].indexOf("north") !== -1 || (r[0] === "y" || r[0] === "lat") && r[1] === "north" ? t += "n" : r[0].indexOf("south") !== -1 || (r[0] === "y" || r[0] === "lat") && r[1] === "south" ? t += "s" : r[0].indexOf("east") !== -1 || (r[0] === "x" || r[0] === "lon") && r[1] === "east" ? t += "e" : (r[0].indexOf("west") !== -1 || (r[0] === "x" || r[0] === "lon") && r[1] === "west") && (t += "w");
    }
    t.length === 2 && (t += "u"), t.length === 3 && (s.axis = t);
  }
  s.UNIT && (s.units = s.UNIT.name.toLowerCase(), s.units === "metre" && (s.units = "meter"), s.UNIT.convert && (s.type === "GEOGCS" ? s.DATUM && s.DATUM.SPHEROID && (s.to_meter = s.UNIT.convert * s.DATUM.SPHEROID.a) : s.to_meter = s.UNIT.convert));
  var a = s.GEOGCS;
  s.type === "GEOGCS" && (a = s), a && (a.DATUM ? s.datumCode = a.DATUM.name.toLowerCase() : s.datumCode = a.name.toLowerCase(), s.datumCode.slice(0, 2) === "d_" && (s.datumCode = s.datumCode.slice(2)), s.datumCode === "new_zealand_1949" && (s.datumCode = "nzgd49"), (s.datumCode === "wgs_1984" || s.datumCode === "world_geodetic_system_1984") && (s.PROJECTION === "Mercator_Auxiliary_Sphere" && (s.sphere = !0), s.datumCode = "wgs84"), s.datumCode === "belge_1972" && (s.datumCode = "rnb72"), a.DATUM && a.DATUM.SPHEROID && (s.ellps = a.DATUM.SPHEROID.name.replace("_19", "").replace(/[Cc]larke\_18/, "clrk"), s.ellps.toLowerCase().slice(0, 13) === "international" && (s.ellps = "intl"), s.a = a.DATUM.SPHEROID.a, s.rf = parseFloat(a.DATUM.SPHEROID.rf, 10)), a.DATUM && a.DATUM.TOWGS84 && (s.datum_params = a.DATUM.TOWGS84), ~s.datumCode.indexOf("osgb_1936") && (s.datumCode = "osgb36"), ~s.datumCode.indexOf("osni_1952") && (s.datumCode = "osni52"), (~s.datumCode.indexOf("tm65") || ~s.datumCode.indexOf("geodetic_datum_of_1965")) && (s.datumCode = "ire65"), s.datumCode === "ch1903+" && (s.datumCode = "ch1903"), ~s.datumCode.indexOf("israel") && (s.datumCode = "isr93")), s.b && !isFinite(s.b) && (s.b = s.a);
  function o(h) {
    var u = s.to_meter || 1;
    return h * u;
  }
  var l = function(h) {
    return oue(s, h);
  }, c = [
    ["standard_parallel_1", "Standard_Parallel_1"],
    ["standard_parallel_1", "Latitude of 1st standard parallel"],
    ["standard_parallel_2", "Standard_Parallel_2"],
    ["standard_parallel_2", "Latitude of 2nd standard parallel"],
    ["false_easting", "False_Easting"],
    ["false_easting", "False easting"],
    ["false-easting", "Easting at false origin"],
    ["false_northing", "False_Northing"],
    ["false_northing", "False northing"],
    ["false_northing", "Northing at false origin"],
    ["central_meridian", "Central_Meridian"],
    ["central_meridian", "Longitude of natural origin"],
    ["central_meridian", "Longitude of false origin"],
    ["latitude_of_origin", "Latitude_Of_Origin"],
    ["latitude_of_origin", "Central_Parallel"],
    ["latitude_of_origin", "Latitude of natural origin"],
    ["latitude_of_origin", "Latitude of false origin"],
    ["scale_factor", "Scale_Factor"],
    ["k0", "scale_factor"],
    ["latitude_of_center", "Latitude_Of_Center"],
    ["latitude_of_center", "Latitude_of_center"],
    ["lat0", "latitude_of_center", Co],
    ["longitude_of_center", "Longitude_Of_Center"],
    ["longitude_of_center", "Longitude_of_center"],
    ["longc", "longitude_of_center", Co],
    ["x0", "false_easting", o],
    ["y0", "false_northing", o],
    ["long0", "central_meridian", Co],
    ["lat0", "latitude_of_origin", Co],
    ["lat0", "standard_parallel_1", Co],
    ["lat1", "standard_parallel_1", Co],
    ["lat2", "standard_parallel_2", Co],
    ["azimuth", "Azimuth"],
    ["alpha", "azimuth", Co],
    ["srsCode", "name"]
  ];
  c.forEach(l), !s.long0 && s.longc && (s.projName === "Albers_Conic_Equal_Area" || s.projName === "Lambert_Azimuthal_Equal_Area") && (s.long0 = s.longc), !s.lat_ts && s.lat1 && (s.projName === "Stereographic_South_Pole" || s.projName === "Polar Stereographic (variant B)") ? (s.lat0 = Co(s.lat1 > 0 ? 90 : -90), s.lat_ts = s.lat1) : !s.lat_ts && s.lat0 && s.projName === "Polar_Stereographic" && (s.lat_ts = s.lat0, s.lat0 = Co(s.lat0 > 0 ? 90 : -90));
}
function dF(s) {
  var e = sue(s), t = e[0], i = {};
  return Ef(e, i), uF(i), i[t];
}
function mr(s) {
  var e = this;
  if (arguments.length === 2) {
    var t = arguments[1];
    typeof t == "string" ? t.charAt(0) === "+" ? mr[s] = Jb(arguments[1]) : mr[s] = dF(arguments[1]) : mr[s] = t;
  } else if (arguments.length === 1) {
    if (Array.isArray(s))
      return s.map(function(i) {
        Array.isArray(i) ? mr.apply(e, i) : mr(i);
      });
    if (typeof s == "string") {
      if (s in mr)
        return mr[s];
    } else
      "EPSG" in s ? mr["EPSG:" + s.EPSG] = s : "ESRI" in s ? mr["ESRI:" + s.ESRI] = s : "IAU2000" in s ? mr["IAU2000:" + s.IAU2000] = s : console.log(s);
    return;
  }
}
Yhe(mr);
function cue(s) {
  return typeof s == "string";
}
function hue(s) {
  return s in mr;
}
var uue = ["PROJECTEDCRS", "PROJCRS", "GEOGCS", "GEOCCS", "PROJCS", "LOCAL_CS", "GEODCRS", "GEODETICCRS", "GEODETICDATUM", "ENGCRS", "ENGINEERINGCRS"];
function due(s) {
  return uue.some(function(e) {
    return s.indexOf(e) > -1;
  });
}
var fue = ["3857", "900913", "3785", "102113"];
function pue(s) {
  var e = Yc(s, "authority");
  if (e) {
    var t = Yc(e, "epsg");
    return t && fue.indexOf(t) > -1;
  }
}
function mue(s) {
  var e = Yc(s, "extension");
  if (e)
    return Yc(e, "proj4");
}
function gue(s) {
  return s[0] === "+";
}
function vue(s) {
  if (cue(s)) {
    if (hue(s))
      return mr[s];
    if (due(s)) {
      var e = dF(s);
      if (pue(e))
        return mr["EPSG:3857"];
      var t = mue(e);
      return t ? Jb(t) : e;
    }
    if (gue(s))
      return Jb(s);
  } else
    return s;
}
function TP(s, e) {
  s = s || {};
  var t, i;
  if (!e)
    return s;
  for (i in e)
    t = e[i], t !== void 0 && (s[i] = t);
  return s;
}
function m0(s, e, t) {
  var i = s * e;
  return t / Math.sqrt(1 - i * i);
}
function N2(s) {
  return s < 0 ? -1 : 1;
}
function Lt(s) {
  return Math.abs(s) <= Jn ? s : s - N2(s) * zg;
}
function ro(s, e, t) {
  var i = s * t, n = 0.5 * s;
  return i = Math.pow((1 - i) / (1 + i), n), Math.tan(0.5 * (xt - e)) / i;
}
function Gg(s, e) {
  for (var t = 0.5 * s, i, n, r = xt - 2 * Math.atan(e), a = 0; a <= 15; a++)
    if (i = s * Math.sin(r), n = xt - 2 * Math.atan(e * Math.pow((1 - i) / (1 + i), t)) - r, r += n, Math.abs(n) <= 1e-10)
      return r;
  return -9999;
}
function yue() {
  var s = this.b / this.a;
  this.es = 1 - s * s, "x0" in this || (this.x0 = 0), "y0" in this || (this.y0 = 0), this.e = Math.sqrt(this.es), this.lat_ts ? this.sphere ? this.k0 = Math.cos(this.lat_ts) : this.k0 = m0(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts)) : this.k0 || (this.k ? this.k0 = this.k : this.k0 = 1);
}
function xue(s) {
  var e = s.x, t = s.y;
  if (t * Ko > 90 && t * Ko < -90 && e * Ko > 180 && e * Ko < -180)
    return null;
  var i, n;
  if (Math.abs(Math.abs(t) - xt) <= Dt)
    return null;
  if (this.sphere)
    i = this.x0 + this.a * this.k0 * Lt(e - this.long0), n = this.y0 + this.a * this.k0 * Math.log(Math.tan($i + 0.5 * t));
  else {
    var r = Math.sin(t), a = ro(this.e, t, r);
    i = this.x0 + this.a * this.k0 * Lt(e - this.long0), n = this.y0 - this.a * this.k0 * Math.log(a);
  }
  return s.x = i, s.y = n, s;
}
function Aue(s) {
  var e = s.x - this.x0, t = s.y - this.y0, i, n;
  if (this.sphere)
    n = xt - 2 * Math.atan(Math.exp(-t / (this.a * this.k0)));
  else {
    var r = Math.exp(-t / (this.a * this.k0));
    if (n = Gg(this.e, r), n === -9999)
      return null;
  }
  return i = Lt(this.long0 + e / (this.a * this.k0)), s.x = i, s.y = n, s;
}
var bue = ["Mercator", "Popular Visualisation Pseudo Mercator", "Mercator_1SP", "Mercator_Auxiliary_Sphere", "merc"];
const Mue = {
  init: yue,
  forward: xue,
  inverse: Aue,
  names: bue
};
function Sue() {
}
function CP(s) {
  return s;
}
var wue = ["longlat", "identity"];
const Eue = {
  init: Sue,
  forward: CP,
  inverse: CP,
  names: wue
};
var Tue = [Mue, Eue], jy = {}, Cx = [];
function fF(s, e) {
  var t = Cx.length;
  return s.names ? (Cx[t] = s, s.names.forEach(function(i) {
    jy[i.toLowerCase()] = t;
  }), this) : (console.log(e), !0);
}
function Cue(s) {
  if (!s)
    return !1;
  var e = s.toLowerCase();
  if (typeof jy[e] < "u" && Cx[jy[e]])
    return Cx[jy[e]];
}
function _ue() {
  Tue.forEach(fF);
}
const Pue = {
  start: _ue,
  add: fF,
  get: Cue
};
var si = {};
si.MERIT = {
  a: 6378137,
  rf: 298.257,
  ellipseName: "MERIT 1983"
};
si.SGS85 = {
  a: 6378136,
  rf: 298.257,
  ellipseName: "Soviet Geodetic System 85"
};
si.GRS80 = {
  a: 6378137,
  rf: 298.257222101,
  ellipseName: "GRS 1980(IUGG, 1980)"
};
si.IAU76 = {
  a: 6378140,
  rf: 298.257,
  ellipseName: "IAU 1976"
};
si.airy = {
  a: 6377563396e-3,
  b: 635625691e-2,
  ellipseName: "Airy 1830"
};
si.APL4 = {
  a: 6378137,
  rf: 298.25,
  ellipseName: "Appl. Physics. 1965"
};
si.NWL9D = {
  a: 6378145,
  rf: 298.25,
  ellipseName: "Naval Weapons Lab., 1965"
};
si.mod_airy = {
  a: 6377340189e-3,
  b: 6356034446e-3,
  ellipseName: "Modified Airy"
};
si.andrae = {
  a: 637710443e-2,
  rf: 300,
  ellipseName: "Andrae 1876 (Den., Iclnd.)"
};
si.aust_SA = {
  a: 6378160,
  rf: 298.25,
  ellipseName: "Australian Natl & S. Amer. 1969"
};
si.GRS67 = {
  a: 6378160,
  rf: 298.247167427,
  ellipseName: "GRS 67(IUGG 1967)"
};
si.bessel = {
  a: 6377397155e-3,
  rf: 299.1528128,
  ellipseName: "Bessel 1841"
};
si.bess_nam = {
  a: 6377483865e-3,
  rf: 299.1528128,
  ellipseName: "Bessel 1841 (Namibia)"
};
si.clrk66 = {
  a: 63782064e-1,
  b: 63565838e-1,
  ellipseName: "Clarke 1866"
};
si.clrk80 = {
  a: 6378249145e-3,
  rf: 293.4663,
  ellipseName: "Clarke 1880 mod."
};
si.clrk80ign = {
  a: 63782492e-1,
  b: 6356515,
  rf: 293.4660213,
  ellipseName: "Clarke 1880 (IGN)"
};
si.clrk58 = {
  a: 6378293645208759e-9,
  rf: 294.2606763692654,
  ellipseName: "Clarke 1858"
};
si.CPM = {
  a: 63757387e-1,
  rf: 334.29,
  ellipseName: "Comm. des Poids et Mesures 1799"
};
si.delmbr = {
  a: 6376428,
  rf: 311.5,
  ellipseName: "Delambre 1810 (Belgium)"
};
si.engelis = {
  a: 637813605e-2,
  rf: 298.2566,
  ellipseName: "Engelis 1985"
};
si.evrst30 = {
  a: 6377276345e-3,
  rf: 300.8017,
  ellipseName: "Everest 1830"
};
si.evrst48 = {
  a: 6377304063e-3,
  rf: 300.8017,
  ellipseName: "Everest 1948"
};
si.evrst56 = {
  a: 6377301243e-3,
  rf: 300.8017,
  ellipseName: "Everest 1956"
};
si.evrst69 = {
  a: 6377295664e-3,
  rf: 300.8017,
  ellipseName: "Everest 1969"
};
si.evrstSS = {
  a: 6377298556e-3,
  rf: 300.8017,
  ellipseName: "Everest (Sabah & Sarawak)"
};
si.fschr60 = {
  a: 6378166,
  rf: 298.3,
  ellipseName: "Fischer (Mercury Datum) 1960"
};
si.fschr60m = {
  a: 6378155,
  rf: 298.3,
  ellipseName: "Fischer 1960"
};
si.fschr68 = {
  a: 6378150,
  rf: 298.3,
  ellipseName: "Fischer 1968"
};
si.helmert = {
  a: 6378200,
  rf: 298.3,
  ellipseName: "Helmert 1906"
};
si.hough = {
  a: 6378270,
  rf: 297,
  ellipseName: "Hough"
};
si.intl = {
  a: 6378388,
  rf: 297,
  ellipseName: "International 1909 (Hayford)"
};
si.kaula = {
  a: 6378163,
  rf: 298.24,
  ellipseName: "Kaula 1961"
};
si.lerch = {
  a: 6378139,
  rf: 298.257,
  ellipseName: "Lerch 1979"
};
si.mprts = {
  a: 6397300,
  rf: 191,
  ellipseName: "Maupertius 1738"
};
si.new_intl = {
  a: 63781575e-1,
  b: 63567722e-1,
  ellipseName: "New International 1967"
};
si.plessis = {
  a: 6376523,
  rf: 6355863,
  ellipseName: "Plessis 1817 (France)"
};
si.krass = {
  a: 6378245,
  rf: 298.3,
  ellipseName: "Krassovsky, 1942"
};
si.SEasia = {
  a: 6378155,
  b: 63567733205e-4,
  ellipseName: "Southeast Asia"
};
si.walbeck = {
  a: 6376896,
  b: 63558348467e-4,
  ellipseName: "Walbeck"
};
si.WGS60 = {
  a: 6378165,
  rf: 298.3,
  ellipseName: "WGS 60"
};
si.WGS66 = {
  a: 6378145,
  rf: 298.25,
  ellipseName: "WGS 66"
};
si.WGS7 = {
  a: 6378135,
  rf: 298.26,
  ellipseName: "WGS 72"
};
var Rue = si.WGS84 = {
  a: 6378137,
  rf: 298.257223563,
  ellipseName: "WGS 84"
};
si.sphere = {
  a: 6370997,
  b: 6370997,
  ellipseName: "Normal Sphere (r=6370997)"
};
function Due(s, e, t, i) {
  var n = s * s, r = e * e, a = (n - r) / n, o = 0;
  i ? (s *= 1 - a * (Jhe + a * (Qhe + a * $he)), n = s * s, a = 0) : o = Math.sqrt(a);
  var l = (n - r) / r;
  return {
    es: a,
    e: o,
    ep2: l
  };
}
function Lue(s, e, t, i, n) {
  if (!s) {
    var r = Yc(si, i);
    r || (r = Rue), s = r.a, e = r.b, t = r.rf;
  }
  return t && !e && (e = (1 - 1 / t) * s), (t === 0 || Math.abs(s - e) < Dt) && (n = !0, e = s), {
    a: s,
    b: e,
    rf: t,
    sphere: n
  };
}
var qy = {
  wgs84: {
    towgs84: "0,0,0",
    ellipse: "WGS84",
    datumName: "WGS84"
  },
  ch1903: {
    towgs84: "674.374,15.056,405.346",
    ellipse: "bessel",
    datumName: "swiss"
  },
  ggrs87: {
    towgs84: "-199.87,74.79,246.62",
    ellipse: "GRS80",
    datumName: "Greek_Geodetic_Reference_System_1987"
  },
  nad83: {
    towgs84: "0,0,0",
    ellipse: "GRS80",
    datumName: "North_American_Datum_1983"
  },
  nad27: {
    nadgrids: "@conus,@alaska,@ntv2_0.gsb,@ntv1_can.dat",
    ellipse: "clrk66",
    datumName: "North_American_Datum_1927"
  },
  potsdam: {
    towgs84: "598.1,73.7,418.2,0.202,0.045,-2.455,6.7",
    ellipse: "bessel",
    datumName: "Potsdam Rauenberg 1950 DHDN"
  },
  carthage: {
    towgs84: "-263.0,6.0,431.0",
    ellipse: "clark80",
    datumName: "Carthage 1934 Tunisia"
  },
  hermannskogel: {
    towgs84: "577.326,90.129,463.919,5.137,1.474,5.297,2.4232",
    ellipse: "bessel",
    datumName: "Hermannskogel"
  },
  mgi: {
    towgs84: "577.326,90.129,463.919,5.137,1.474,5.297,2.4232",
    ellipse: "bessel",
    datumName: "Militar-Geographische Institut"
  },
  osni52: {
    towgs84: "482.530,-130.596,564.557,-1.042,-0.214,-0.631,8.15",
    ellipse: "airy",
    datumName: "Irish National"
  },
  ire65: {
    towgs84: "482.530,-130.596,564.557,-1.042,-0.214,-0.631,8.15",
    ellipse: "mod_airy",
    datumName: "Ireland 1965"
  },
  rassadiran: {
    towgs84: "-133.63,-157.5,-158.62",
    ellipse: "intl",
    datumName: "Rassadiran"
  },
  nzgd49: {
    towgs84: "59.47,-5.04,187.44,0.47,-0.1,1.024,-4.5993",
    ellipse: "intl",
    datumName: "New Zealand Geodetic Datum 1949"
  },
  osgb36: {
    towgs84: "446.448,-125.157,542.060,0.1502,0.2470,0.8421,-20.4894",
    ellipse: "airy",
    datumName: "Ordnance Survey of Great Britain 1936"
  },
  s_jtsk: {
    towgs84: "589,76,480",
    ellipse: "bessel",
    datumName: "S-JTSK (Ferro)"
  },
  beduaram: {
    towgs84: "-106,-87,188",
    ellipse: "clrk80",
    datumName: "Beduaram"
  },
  gunung_segara: {
    towgs84: "-403,684,41",
    ellipse: "bessel",
    datumName: "Gunung Segara Jakarta"
  },
  rnb72: {
    towgs84: "106.869,-52.2978,103.724,-0.33657,0.456955,-1.84218,1",
    ellipse: "intl",
    datumName: "Reseau National Belge 1972"
  }
};
for (var Iue in qy) {
  var _P = qy[Iue];
  qy[_P.datumName] = _P;
}
function Oue(s, e, t, i, n, r, a) {
  var o = {};
  return s === void 0 || s === "none" ? o.datum_type = Kb : o.datum_type = Zhe, e && (o.datum_params = e.map(parseFloat), (o.datum_params[0] !== 0 || o.datum_params[1] !== 0 || o.datum_params[2] !== 0) && (o.datum_type = Bu), o.datum_params.length > 3 && (o.datum_params[3] !== 0 || o.datum_params[4] !== 0 || o.datum_params[5] !== 0 || o.datum_params[6] !== 0) && (o.datum_type = Fu, o.datum_params[3] *= F1, o.datum_params[4] *= F1, o.datum_params[5] *= F1, o.datum_params[6] = o.datum_params[6] / 1e6 + 1)), a && (o.datum_type = qf, o.grids = a), o.a = t, o.b = i, o.es = n, o.ep2 = r, o;
}
var pF = {};
function Nue(s, e) {
  var t = new DataView(e), i = Uue(t), n = zue(t, i), r = kue(t, n, i), a = { header: n, subgrids: r };
  return pF[s] = a, a;
}
function Bue(s) {
  if (s === void 0)
    return null;
  var e = s.split(",");
  return e.map(Fue);
}
function Fue(s) {
  if (s.length === 0)
    return null;
  var e = s[0] === "@";
  return e && (s = s.slice(1)), s === "null" ? { name: "null", mandatory: !e, grid: null, isNull: !0 } : {
    name: s,
    mandatory: !e,
    grid: pF[s] || null,
    isNull: !1
  };
}
function Tf(s) {
  return s / 3600 * Math.PI / 180;
}
function Uue(s) {
  var e = s.getInt32(8, !1);
  return e === 11 ? !1 : (e = s.getInt32(8, !0), e !== 11 && console.warn("Failed to detect nadgrid endian-ness, defaulting to little-endian"), !0);
}
function zue(s, e) {
  return {
    nFields: s.getInt32(8, e),
    nSubgridFields: s.getInt32(24, e),
    nSubgrids: s.getInt32(40, e),
    shiftType: Qb(s, 56, 56 + 8).trim(),
    fromSemiMajorAxis: s.getFloat64(120, e),
    fromSemiMinorAxis: s.getFloat64(136, e),
    toSemiMajorAxis: s.getFloat64(152, e),
    toSemiMinorAxis: s.getFloat64(168, e)
  };
}
function Qb(s, e, t) {
  return String.fromCharCode.apply(null, new Uint8Array(s.buffer.slice(e, t)));
}
function kue(s, e, t) {
  for (var i = 176, n = [], r = 0; r < e.nSubgrids; r++) {
    var a = Hue(s, i, t), o = Vue(s, i, a, t), l = Math.round(
      1 + (a.upperLongitude - a.lowerLongitude) / a.longitudeInterval
    ), c = Math.round(
      1 + (a.upperLatitude - a.lowerLatitude) / a.latitudeInterval
    );
    n.push({
      ll: [Tf(a.lowerLongitude), Tf(a.lowerLatitude)],
      del: [Tf(a.longitudeInterval), Tf(a.latitudeInterval)],
      lim: [l, c],
      count: a.gridNodeCount,
      cvs: Gue(o)
    }), i += 176 + a.gridNodeCount * 16;
  }
  return n;
}
function Gue(s) {
  return s.map(function(e) {
    return [Tf(e.longitudeShift), Tf(e.latitudeShift)];
  });
}
function Hue(s, e, t) {
  return {
    name: Qb(s, e + 8, e + 16).trim(),
    parent: Qb(s, e + 24, e + 24 + 8).trim(),
    lowerLatitude: s.getFloat64(e + 72, t),
    upperLatitude: s.getFloat64(e + 88, t),
    lowerLongitude: s.getFloat64(e + 104, t),
    upperLongitude: s.getFloat64(e + 120, t),
    latitudeInterval: s.getFloat64(e + 136, t),
    longitudeInterval: s.getFloat64(e + 152, t),
    gridNodeCount: s.getInt32(e + 168, t)
  };
}
function Vue(s, e, t, i) {
  for (var n = e + 176, r = 16, a = [], o = 0; o < t.gridNodeCount; o++) {
    var l = {
      latitudeShift: s.getFloat32(n + o * r, i),
      longitudeShift: s.getFloat32(n + o * r + 4, i),
      latitudeAccuracy: s.getFloat32(n + o * r + 8, i),
      longitudeAccuracy: s.getFloat32(n + o * r + 12, i)
    };
    a.push(l);
  }
  return a;
}
function i0(s, e) {
  if (!(this instanceof i0))
    return new i0(s);
  e = e || function(c) {
    if (c)
      throw c;
  };
  var t = vue(s);
  if (typeof t != "object") {
    e("Could not parse to valid json: " + s);
    return;
  }
  var i = i0.projections.get(t.projName);
  if (!i) {
    e("Could not get projection name from: " + s);
    return;
  }
  if (t.datumCode && t.datumCode !== "none") {
    var n = Yc(qy, t.datumCode);
    n && (t.datum_params = t.datum_params || (n.towgs84 ? n.towgs84.split(",") : null), t.ellps = n.ellipse, t.datumName = n.datumName ? n.datumName : t.datumCode);
  }
  t.k0 = t.k0 || 1, t.axis = t.axis || "enu", t.ellps = t.ellps || "wgs84", t.lat1 = t.lat1 || t.lat0;
  var r = Lue(t.a, t.b, t.rf, t.ellps, t.sphere), a = Due(r.a, r.b, r.rf, t.R_A), o = Bue(t.nadgrids), l = t.datum || Oue(
    t.datumCode,
    t.datum_params,
    r.a,
    r.b,
    a.es,
    a.ep2,
    o
  );
  TP(this, t), TP(this, i), this.a = r.a, this.b = r.b, this.rf = r.rf, this.sphere = r.sphere, this.es = a.es, this.e = a.e, this.ep2 = a.ep2, this.datum = l, this.init(), e(null, this);
}
i0.projections = Pue;
i0.projections.start();
function Wue(s, e) {
  return s.datum_type !== e.datum_type || s.a !== e.a || Math.abs(s.es - e.es) > 5e-11 ? !1 : s.datum_type === Bu ? s.datum_params[0] === e.datum_params[0] && s.datum_params[1] === e.datum_params[1] && s.datum_params[2] === e.datum_params[2] : s.datum_type === Fu ? s.datum_params[0] === e.datum_params[0] && s.datum_params[1] === e.datum_params[1] && s.datum_params[2] === e.datum_params[2] && s.datum_params[3] === e.datum_params[3] && s.datum_params[4] === e.datum_params[4] && s.datum_params[5] === e.datum_params[5] && s.datum_params[6] === e.datum_params[6] : !0;
}
function mF(s, e, t) {
  var i = s.x, n = s.y, r = s.z ? s.z : 0, a, o, l, c;
  if (n < -xt && n > -1.001 * xt)
    n = -xt;
  else if (n > xt && n < 1.001 * xt)
    n = xt;
  else {
    if (n < -xt)
      return { x: -1 / 0, y: -1 / 0, z: s.z };
    if (n > xt)
      return { x: 1 / 0, y: 1 / 0, z: s.z };
  }
  return i > Math.PI && (i -= 2 * Math.PI), o = Math.sin(n), c = Math.cos(n), l = o * o, a = t / Math.sqrt(1 - e * l), {
    x: (a + r) * c * Math.cos(i),
    y: (a + r) * c * Math.sin(i),
    z: (a * (1 - e) + r) * o
  };
}
function gF(s, e, t, i) {
  var n = 1e-12, r = n * n, a = 30, o, l, c, h, u, d, f, p, v, g, m, x, y, A = s.x, b = s.y, M = s.z ? s.z : 0, w, C, T;
  if (o = Math.sqrt(A * A + b * b), l = Math.sqrt(A * A + b * b + M * M), o / t < n) {
    if (w = 0, l / t < n)
      return C = xt, T = -i, {
        x: s.x,
        y: s.y,
        z: s.z
      };
  } else
    w = Math.atan2(b, A);
  c = M / l, h = o / l, u = 1 / Math.sqrt(1 - e * (2 - e) * h * h), p = h * (1 - e) * u, v = c * u, y = 0;
  do
    y++, f = t / Math.sqrt(1 - e * v * v), T = o * p + M * v - f * (1 - e * v * v), d = e * f / (f + T), u = 1 / Math.sqrt(1 - d * (2 - d) * h * h), g = h * (1 - d) * u, m = c * u, x = m * p - g * v, p = g, v = m;
  while (x * x > r && y < a);
  return C = Math.atan(m / Math.abs(g)), {
    x: w,
    y: C,
    z: T
  };
}
function Xue(s, e, t) {
  if (e === Bu)
    return {
      x: s.x + t[0],
      y: s.y + t[1],
      z: s.z + t[2]
    };
  if (e === Fu) {
    var i = t[0], n = t[1], r = t[2], a = t[3], o = t[4], l = t[5], c = t[6];
    return {
      x: c * (s.x - l * s.y + o * s.z) + i,
      y: c * (l * s.x + s.y - a * s.z) + n,
      z: c * (-o * s.x + a * s.y + s.z) + r
    };
  }
}
function jue(s, e, t) {
  if (e === Bu)
    return {
      x: s.x - t[0],
      y: s.y - t[1],
      z: s.z - t[2]
    };
  if (e === Fu) {
    var i = t[0], n = t[1], r = t[2], a = t[3], o = t[4], l = t[5], c = t[6], h = (s.x - i) / c, u = (s.y - n) / c, d = (s.z - r) / c;
    return {
      x: h + l * u - o * d,
      y: -l * h + u + a * d,
      z: o * h - a * u + d
    };
  }
}
function y3(s) {
  return s === Bu || s === Fu;
}
function que(s, e, t) {
  if (Wue(s, e) || s.datum_type === Kb || e.datum_type === Kb)
    return t;
  var i = s.a, n = s.es;
  if (s.datum_type === qf) {
    var r = PP(s, !1, t);
    if (r !== 0)
      return;
    i = SP, n = wP;
  }
  var a = e.a, o = e.b, l = e.es;
  if (e.datum_type === qf && (a = SP, o = Khe, l = wP), n === l && i === a && !y3(s.datum_type) && !y3(e.datum_type))
    return t;
  if (t = mF(t, n, i), y3(s.datum_type) && (t = Xue(t, s.datum_type, s.datum_params)), y3(e.datum_type) && (t = jue(t, e.datum_type, e.datum_params)), t = gF(t, l, a, o), e.datum_type === qf) {
    var c = PP(e, !0, t);
    if (c !== 0)
      return;
  }
  return t;
}
function PP(s, e, t) {
  if (s.grids === null || s.grids.length === 0)
    return console.log("Grid shift grids not found"), -1;
  var i = { x: -t.x, y: t.y }, n = { x: Number.NaN, y: Number.NaN }, r = [];
  e:
    for (var a = 0; a < s.grids.length; a++) {
      var o = s.grids[a];
      if (r.push(o.name), o.isNull) {
        n = i;
        break;
      }
      if (o.mandatory, o.grid === null) {
        if (o.mandatory)
          return console.log("Unable to find mandatory grid '" + o.name + "'"), -1;
        continue;
      }
      for (var l = o.grid.subgrids, c = 0, h = l.length; c < h; c++) {
        var u = l[c], d = (Math.abs(u.del[1]) + Math.abs(u.del[0])) / 1e4, f = u.ll[0] - d, p = u.ll[1] - d, v = u.ll[0] + (u.lim[0] - 1) * u.del[0] + d, g = u.ll[1] + (u.lim[1] - 1) * u.del[1] + d;
        if (!(p > i.y || f > i.x || g < i.y || v < i.x) && (n = Yue(i, e, u), !isNaN(n.x)))
          break e;
      }
    }
  return isNaN(n.x) ? (console.log("Failed to find a grid shift table for location '" + -i.x * Ko + " " + i.y * Ko + " tried: '" + r + "'"), -1) : (t.x = -n.x, t.y = n.y, 0);
}
function Yue(s, e, t) {
  var i = { x: Number.NaN, y: Number.NaN };
  if (isNaN(s.x))
    return i;
  var n = { x: s.x, y: s.y };
  n.x -= t.ll[0], n.y -= t.ll[1], n.x = Lt(n.x - Math.PI) + Math.PI;
  var r = RP(n, t);
  if (e) {
    if (isNaN(r.x))
      return i;
    r.x = n.x - r.x, r.y = n.y - r.y;
    var a = 9, o = 1e-12, l, c;
    do {
      if (c = RP(r, t), isNaN(c.x)) {
        console.log("Inverse grid shift iteration failed, presumably at grid edge.  Using first approximation.");
        break;
      }
      l = { x: n.x - (c.x + r.x), y: n.y - (c.y + r.y) }, r.x += l.x, r.y += l.y;
    } while (a-- && Math.abs(l.x) > o && Math.abs(l.y) > o);
    if (a < 0)
      return console.log("Inverse grid shift iterator failed to converge."), i;
    i.x = Lt(r.x + t.ll[0]), i.y = r.y + t.ll[1];
  } else
    isNaN(r.x) || (i.x = s.x + r.x, i.y = s.y + r.y);
  return i;
}
function RP(s, e) {
  var t = { x: s.x / e.del[0], y: s.y / e.del[1] }, i = { x: Math.floor(t.x), y: Math.floor(t.y) }, n = { x: t.x - 1 * i.x, y: t.y - 1 * i.y }, r = { x: Number.NaN, y: Number.NaN }, a;
  if (i.x < 0 || i.x >= e.lim[0] || i.y < 0 || i.y >= e.lim[1])
    return r;
  a = i.y * e.lim[0] + i.x;
  var o = { x: e.cvs[a][0], y: e.cvs[a][1] };
  a++;
  var l = { x: e.cvs[a][0], y: e.cvs[a][1] };
  a += e.lim[0];
  var c = { x: e.cvs[a][0], y: e.cvs[a][1] };
  a--;
  var h = { x: e.cvs[a][0], y: e.cvs[a][1] }, u = n.x * n.y, d = n.x * (1 - n.y), f = (1 - n.x) * (1 - n.y), p = (1 - n.x) * n.y;
  return r.x = f * o.x + d * l.x + p * h.x + u * c.x, r.y = f * o.y + d * l.y + p * h.y + u * c.y, r;
}
function DP(s, e, t) {
  var i = t.x, n = t.y, r = t.z || 0, a, o, l, c = {};
  for (l = 0; l < 3; l++)
    if (!(e && l === 2 && t.z === void 0))
      switch (l === 0 ? (a = i, "ew".indexOf(s.axis[l]) !== -1 ? o = "x" : o = "y") : l === 1 ? (a = n, "ns".indexOf(s.axis[l]) !== -1 ? o = "y" : o = "x") : (a = r, o = "z"), s.axis[l]) {
        case "e":
          c[o] = a;
          break;
        case "w":
          c[o] = -a;
          break;
        case "n":
          c[o] = a;
          break;
        case "s":
          c[o] = -a;
          break;
        case "u":
          t[o] !== void 0 && (c.z = a);
          break;
        case "d":
          t[o] !== void 0 && (c.z = -a);
          break;
        default:
          return null;
      }
  return c;
}
function vF(s) {
  var e = {
    x: s[0],
    y: s[1]
  };
  return s.length > 2 && (e.z = s[2]), s.length > 3 && (e.m = s[3]), e;
}
function Zue(s) {
  LP(s.x), LP(s.y);
}
function LP(s) {
  if (typeof Number.isFinite == "function") {
    if (Number.isFinite(s))
      return;
    throw new TypeError("coordinates must be finite numbers");
  }
  if (typeof s != "number" || s !== s || !isFinite(s))
    throw new TypeError("coordinates must be finite numbers");
}
function Kue(s, e) {
  return (s.datum.datum_type === Bu || s.datum.datum_type === Fu || s.datum.datum_type === qf) && e.datumCode !== "WGS84" || (e.datum.datum_type === Bu || e.datum.datum_type === Fu || e.datum.datum_type === qf) && s.datumCode !== "WGS84";
}
function _x(s, e, t, i) {
  var n;
  Array.isArray(t) ? t = vF(t) : t = {
    x: t.x,
    y: t.y,
    z: t.z,
    m: t.m
  };
  var r = t.z !== void 0;
  if (Zue(t), s.datum && e.datum && Kue(s, e) && (n = new i0("WGS84"), t = _x(s, n, t, i), s = n), i && s.axis !== "enu" && (t = DP(s, !1, t)), s.projName === "longlat")
    t = {
      x: t.x * zs,
      y: t.y * zs,
      z: t.z || 0
    };
  else if (s.to_meter && (t = {
    x: t.x * s.to_meter,
    y: t.y * s.to_meter,
    z: t.z || 0
  }), t = s.inverse(t), !t)
    return;
  if (s.from_greenwich && (t.x += s.from_greenwich), t = que(s.datum, e.datum, t), !!t)
    return e.from_greenwich && (t = {
      x: t.x - e.from_greenwich,
      y: t.y,
      z: t.z || 0
    }), e.projName === "longlat" ? t = {
      x: t.x * Ko,
      y: t.y * Ko,
      z: t.z || 0
    } : (t = e.forward(t), e.to_meter && (t = {
      x: t.x / e.to_meter,
      y: t.y / e.to_meter,
      z: t.z || 0
    })), i && e.axis !== "enu" ? DP(e, !0, t) : (t && !r && delete t.z, t);
}
var IP = i0("WGS84");
function OA(s, e, t, i) {
  var n, r, a;
  return Array.isArray(t) ? (n = _x(s, e, t, i) || { x: NaN, y: NaN }, t.length > 2 ? typeof s.name < "u" && s.name === "geocent" || typeof e.name < "u" && e.name === "geocent" ? typeof n.z == "number" ? [n.x, n.y, n.z].concat(t.slice(3)) : [n.x, n.y, t[2]].concat(t.slice(3)) : [n.x, n.y].concat(t.slice(2)) : [n.x, n.y]) : (r = _x(s, e, t, i), a = Object.keys(t), a.length === 2 || a.forEach(function(o) {
    if (typeof s.name < "u" && s.name === "geocent" || typeof e.name < "u" && e.name === "geocent") {
      if (o === "x" || o === "y" || o === "z")
        return;
    } else if (o === "x" || o === "y")
      return;
    r[o] = t[o];
  }), r);
}
function OP(s) {
  return s instanceof i0 ? s : s.oProj ? s.oProj : i0(s);
}
function Ar(s, e, t) {
  s = OP(s);
  var i = !1, n;
  return typeof e > "u" ? (e = s, s = IP, i = !0) : (typeof e.x < "u" || Array.isArray(e)) && (t = e, e = s, s = IP, i = !0), e = OP(e), t ? OA(s, e, t) : (n = {
    forward: function(r, a) {
      return OA(s, e, r, a);
    },
    inverse: function(r, a) {
      return OA(e, s, r, a);
    }
  }, i && (n.oProj = e), n);
}
var NP = 6, yF = "AJSAJS", xF = "AFAFAF", Cf = 65, Cr = 73, fa = 79, c1 = 86, h1 = 90;
const Jue = {
  forward: AF,
  inverse: Que,
  toPoint: bF
};
function AF(s, e) {
  return e = e || 5, tde($ue({
    lat: s[1],
    lon: s[0]
  }), e);
}
function Que(s) {
  var e = bw(SF(s.toUpperCase()));
  return e.lat && e.lon ? [e.lon, e.lat, e.lon, e.lat] : [e.left, e.bottom, e.right, e.top];
}
function bF(s) {
  var e = bw(SF(s.toUpperCase()));
  return e.lat && e.lon ? [e.lon, e.lat] : [(e.left + e.right) / 2, (e.top + e.bottom) / 2];
}
function NA(s) {
  return s * (Math.PI / 180);
}
function BP(s) {
  return 180 * (s / Math.PI);
}
function $ue(s) {
  var e = s.lat, t = s.lon, i = 6378137, n = 669438e-8, r = 0.9996, a, o, l, c, h, u, d, f = NA(e), p = NA(t), v, g;
  g = Math.floor((t + 180) / 6) + 1, t === 180 && (g = 60), e >= 56 && e < 64 && t >= 3 && t < 12 && (g = 32), e >= 72 && e < 84 && (t >= 0 && t < 9 ? g = 31 : t >= 9 && t < 21 ? g = 33 : t >= 21 && t < 33 ? g = 35 : t >= 33 && t < 42 && (g = 37)), a = (g - 1) * 6 - 180 + 3, v = NA(a), o = n / (1 - n), l = i / Math.sqrt(1 - n * Math.sin(f) * Math.sin(f)), c = Math.tan(f) * Math.tan(f), h = o * Math.cos(f) * Math.cos(f), u = Math.cos(f) * (p - v), d = i * ((1 - n / 4 - 3 * n * n / 64 - 5 * n * n * n / 256) * f - (3 * n / 8 + 3 * n * n / 32 + 45 * n * n * n / 1024) * Math.sin(2 * f) + (15 * n * n / 256 + 45 * n * n * n / 1024) * Math.sin(4 * f) - 35 * n * n * n / 3072 * Math.sin(6 * f));
  var m = r * l * (u + (1 - c + h) * u * u * u / 6 + (5 - 18 * c + c * c + 72 * h - 58 * o) * u * u * u * u * u / 120) + 5e5, x = r * (d + l * Math.tan(f) * (u * u / 2 + (5 - c + 9 * h + 4 * h * h) * u * u * u * u / 24 + (61 - 58 * c + c * c + 600 * h - 330 * o) * u * u * u * u * u * u / 720));
  return e < 0 && (x += 1e7), {
    northing: Math.round(x),
    easting: Math.round(m),
    zoneNumber: g,
    zoneLetter: ede(e)
  };
}
function bw(s) {
  var e = s.northing, t = s.easting, i = s.zoneLetter, n = s.zoneNumber;
  if (n < 0 || n > 60)
    return null;
  var r = 0.9996, a = 6378137, o = 669438e-8, l, c = (1 - Math.sqrt(1 - o)) / (1 + Math.sqrt(1 - o)), h, u, d, f, p, v, g, m, x, y = t - 5e5, A = e;
  i < "N" && (A -= 1e7), g = (n - 1) * 6 - 180 + 3, l = o / (1 - o), v = A / r, m = v / (a * (1 - o / 4 - 3 * o * o / 64 - 5 * o * o * o / 256)), x = m + (3 * c / 2 - 27 * c * c * c / 32) * Math.sin(2 * m) + (21 * c * c / 16 - 55 * c * c * c * c / 32) * Math.sin(4 * m) + 151 * c * c * c / 96 * Math.sin(6 * m), h = a / Math.sqrt(1 - o * Math.sin(x) * Math.sin(x)), u = Math.tan(x) * Math.tan(x), d = l * Math.cos(x) * Math.cos(x), f = a * (1 - o) / Math.pow(1 - o * Math.sin(x) * Math.sin(x), 1.5), p = y / (h * r);
  var b = x - h * Math.tan(x) / f * (p * p / 2 - (5 + 3 * u + 10 * d - 4 * d * d - 9 * l) * p * p * p * p / 24 + (61 + 90 * u + 298 * d + 45 * u * u - 252 * l - 3 * d * d) * p * p * p * p * p * p / 720);
  b = BP(b);
  var M = (p - (1 + 2 * u + d) * p * p * p / 6 + (5 - 2 * d + 28 * u - 3 * d * d + 8 * l + 24 * u * u) * p * p * p * p * p / 120) / Math.cos(x);
  M = g + BP(M);
  var w;
  if (s.accuracy) {
    var C = bw({
      northing: s.northing + s.accuracy,
      easting: s.easting + s.accuracy,
      zoneLetter: s.zoneLetter,
      zoneNumber: s.zoneNumber
    });
    w = {
      top: C.lat,
      right: C.lon,
      bottom: b,
      left: M
    };
  } else
    w = {
      lat: b,
      lon: M
    };
  return w;
}
function ede(s) {
  var e = "Z";
  return 84 >= s && s >= 72 ? e = "X" : 72 > s && s >= 64 ? e = "W" : 64 > s && s >= 56 ? e = "V" : 56 > s && s >= 48 ? e = "U" : 48 > s && s >= 40 ? e = "T" : 40 > s && s >= 32 ? e = "S" : 32 > s && s >= 24 ? e = "R" : 24 > s && s >= 16 ? e = "Q" : 16 > s && s >= 8 ? e = "P" : 8 > s && s >= 0 ? e = "N" : 0 > s && s >= -8 ? e = "M" : -8 > s && s >= -16 ? e = "L" : -16 > s && s >= -24 ? e = "K" : -24 > s && s >= -32 ? e = "J" : -32 > s && s >= -40 ? e = "H" : -40 > s && s >= -48 ? e = "G" : -48 > s && s >= -56 ? e = "F" : -56 > s && s >= -64 ? e = "E" : -64 > s && s >= -72 ? e = "D" : -72 > s && s >= -80 && (e = "C"), e;
}
function tde(s, e) {
  var t = "00000" + s.easting, i = "00000" + s.northing;
  return s.zoneNumber + s.zoneLetter + ide(s.easting, s.northing, s.zoneNumber) + t.substr(t.length - 5, e) + i.substr(i.length - 5, e);
}
function ide(s, e, t) {
  var i = MF(t), n = Math.floor(s / 1e5), r = Math.floor(e / 1e5) % 20;
  return nde(n, r, i);
}
function MF(s) {
  var e = s % NP;
  return e === 0 && (e = NP), e;
}
function nde(s, e, t) {
  var i = t - 1, n = yF.charCodeAt(i), r = xF.charCodeAt(i), a = n + s - 1, o = r + e, l = !1;
  a > h1 && (a = a - h1 + Cf - 1, l = !0), (a === Cr || n < Cr && a > Cr || (a > Cr || n < Cr) && l) && a++, (a === fa || n < fa && a > fa || (a > fa || n < fa) && l) && (a++, a === Cr && a++), a > h1 && (a = a - h1 + Cf - 1), o > c1 ? (o = o - c1 + Cf - 1, l = !0) : l = !1, (o === Cr || r < Cr && o > Cr || (o > Cr || r < Cr) && l) && o++, (o === fa || r < fa && o > fa || (o > fa || r < fa) && l) && (o++, o === Cr && o++), o > c1 && (o = o - c1 + Cf - 1);
  var c = String.fromCharCode(a) + String.fromCharCode(o);
  return c;
}
function SF(s) {
  if (s && s.length === 0)
    throw "MGRSPoint coverting from nothing";
  for (var e = s.length, t = null, i = "", n, r = 0; !/[A-Z]/.test(n = s.charAt(r)); ) {
    if (r >= 2)
      throw "MGRSPoint bad conversion from: " + s;
    i += n, r++;
  }
  var a = parseInt(i, 10);
  if (r === 0 || r + 3 > e)
    throw "MGRSPoint bad conversion from: " + s;
  var o = s.charAt(r++);
  if (o <= "A" || o === "B" || o === "Y" || o >= "Z" || o === "I" || o === "O")
    throw "MGRSPoint zone letter " + o + " not handled: " + s;
  t = s.substring(r, r += 2);
  for (var l = MF(a), c = sde(t.charAt(0), l), h = rde(t.charAt(1), l); h < ade(o); )
    h += 2e6;
  var u = e - r;
  if (u % 2 !== 0)
    throw `MGRSPoint has to have an even number 
of digits after the zone letter and two 100km letters - front 
half for easting meters, second half for 
northing meters` + s;
  var d = u / 2, f = 0, p = 0, v, g, m, x, y;
  return d > 0 && (v = 1e5 / Math.pow(10, d), g = s.substring(r, r + d), f = parseFloat(g) * v, m = s.substring(r + d), p = parseFloat(m) * v), x = f + c, y = p + h, {
    easting: x,
    northing: y,
    zoneLetter: o,
    zoneNumber: a,
    accuracy: v
  };
}
function sde(s, e) {
  for (var t = yF.charCodeAt(e - 1), i = 1e5, n = !1; t !== s.charCodeAt(0); ) {
    if (t++, t === Cr && t++, t === fa && t++, t > h1) {
      if (n)
        throw "Bad character: " + s;
      t = Cf, n = !0;
    }
    i += 1e5;
  }
  return i;
}
function rde(s, e) {
  if (s > "V")
    throw "MGRSPoint given invalid Northing " + s;
  for (var t = xF.charCodeAt(e - 1), i = 0, n = !1; t !== s.charCodeAt(0); ) {
    if (t++, t === Cr && t++, t === fa && t++, t > c1) {
      if (n)
        throw "Bad character: " + s;
      t = Cf, n = !0;
    }
    i += 1e5;
  }
  return i;
}
function ade(s) {
  var e;
  switch (s) {
    case "C":
      e = 11e5;
      break;
    case "D":
      e = 2e6;
      break;
    case "E":
      e = 28e5;
      break;
    case "F":
      e = 37e5;
      break;
    case "G":
      e = 46e5;
      break;
    case "H":
      e = 55e5;
      break;
    case "J":
      e = 64e5;
      break;
    case "K":
      e = 73e5;
      break;
    case "L":
      e = 82e5;
      break;
    case "M":
      e = 91e5;
      break;
    case "N":
      e = 0;
      break;
    case "P":
      e = 8e5;
      break;
    case "Q":
      e = 17e5;
      break;
    case "R":
      e = 26e5;
      break;
    case "S":
      e = 35e5;
      break;
    case "T":
      e = 44e5;
      break;
    case "U":
      e = 53e5;
      break;
    case "V":
      e = 62e5;
      break;
    case "W":
      e = 7e6;
      break;
    case "X":
      e = 79e5;
      break;
    default:
      e = -1;
  }
  if (e >= 0)
    return e;
  throw "Invalid zone letter: " + s;
}
function Dp(s, e, t) {
  if (!(this instanceof Dp))
    return new Dp(s, e, t);
  if (Array.isArray(s))
    this.x = s[0], this.y = s[1], this.z = s[2] || 0;
  else if (typeof s == "object")
    this.x = s.x, this.y = s.y, this.z = s.z || 0;
  else if (typeof s == "string" && typeof e > "u") {
    var i = s.split(",");
    this.x = parseFloat(i[0], 10), this.y = parseFloat(i[1], 10), this.z = parseFloat(i[2], 10) || 0;
  } else
    this.x = s, this.y = e, this.z = t || 0;
  console.warn("proj4.Point will be removed in version 3, use proj4.toPoint");
}
Dp.fromMGRS = function(s) {
  return new Dp(bF(s));
};
Dp.prototype.toMGRS = function(s) {
  return AF([this.x, this.y], s);
};
var ode = 1, lde = 0.25, FP = 0.046875, UP = 0.01953125, zP = 0.01068115234375, cde = 0.75, hde = 0.46875, ude = 0.013020833333333334, dde = 0.007120768229166667, fde = 0.3645833333333333, pde = 0.005696614583333333, mde = 0.3076171875;
function Mw(s) {
  var e = [];
  e[0] = ode - s * (lde + s * (FP + s * (UP + s * zP))), e[1] = s * (cde - s * (FP + s * (UP + s * zP)));
  var t = s * s;
  return e[2] = t * (hde - s * (ude + s * dde)), t *= s, e[3] = t * (fde - s * pde), e[4] = t * s * mde, e;
}
function im(s, e, t, i) {
  return t *= e, e *= e, i[0] * s - t * (i[1] + e * (i[2] + e * (i[3] + e * i[4])));
}
var gde = 20;
function Sw(s, e, t) {
  for (var i = 1 / (1 - e), n = s, r = gde; r; --r) {
    var a = Math.sin(n), o = 1 - e * a * a;
    if (o = (im(n, a, Math.cos(n), t) - s) * (o * Math.sqrt(o)) * i, n -= o, Math.abs(o) < Dt)
      return n;
  }
  return n;
}
function vde() {
  this.x0 = this.x0 !== void 0 ? this.x0 : 0, this.y0 = this.y0 !== void 0 ? this.y0 : 0, this.long0 = this.long0 !== void 0 ? this.long0 : 0, this.lat0 = this.lat0 !== void 0 ? this.lat0 : 0, this.es && (this.en = Mw(this.es), this.ml0 = im(this.lat0, Math.sin(this.lat0), Math.cos(this.lat0), this.en));
}
function yde(s) {
  var e = s.x, t = s.y, i = Lt(e - this.long0), n, r, a, o = Math.sin(t), l = Math.cos(t);
  if (this.es) {
    var h = l * i, u = Math.pow(h, 2), d = this.ep2 * Math.pow(l, 2), f = Math.pow(d, 2), p = Math.abs(l) > Dt ? Math.tan(t) : 0, v = Math.pow(p, 2), g = Math.pow(v, 2);
    n = 1 - this.es * Math.pow(o, 2), h = h / Math.sqrt(n);
    var m = im(t, o, l, this.en);
    r = this.a * (this.k0 * h * (1 + u / 6 * (1 - v + d + u / 20 * (5 - 18 * v + g + 14 * d - 58 * v * d + u / 42 * (61 + 179 * g - g * v - 479 * v))))) + this.x0, a = this.a * (this.k0 * (m - this.ml0 + o * i * h / 2 * (1 + u / 12 * (5 - v + 9 * d + 4 * f + u / 30 * (61 + g - 58 * v + 270 * d - 330 * v * d + u / 56 * (1385 + 543 * g - g * v - 3111 * v)))))) + this.y0;
  } else {
    var c = l * Math.sin(i);
    if (Math.abs(Math.abs(c) - 1) < Dt)
      return 93;
    if (r = 0.5 * this.a * this.k0 * Math.log((1 + c) / (1 - c)) + this.x0, a = l * Math.cos(i) / Math.sqrt(1 - Math.pow(c, 2)), c = Math.abs(a), c >= 1) {
      if (c - 1 > Dt)
        return 93;
      a = 0;
    } else
      a = Math.acos(a);
    t < 0 && (a = -a), a = this.a * this.k0 * (a - this.lat0) + this.y0;
  }
  return s.x = r, s.y = a, s;
}
function xde(s) {
  var e, t, i, n, r = (s.x - this.x0) * (1 / this.a), a = (s.y - this.y0) * (1 / this.a);
  if (this.es)
    if (e = this.ml0 + a / this.k0, t = Sw(e, this.es, this.en), Math.abs(t) < xt) {
      var u = Math.sin(t), d = Math.cos(t), f = Math.abs(d) > Dt ? Math.tan(t) : 0, p = this.ep2 * Math.pow(d, 2), v = Math.pow(p, 2), g = Math.pow(f, 2), m = Math.pow(g, 2);
      e = 1 - this.es * Math.pow(u, 2);
      var x = r * Math.sqrt(e) / this.k0, y = Math.pow(x, 2);
      e = e * f, i = t - e * y / (1 - this.es) * 0.5 * (1 - y / 12 * (5 + 3 * g - 9 * p * g + p - 4 * v - y / 30 * (61 + 90 * g - 252 * p * g + 45 * m + 46 * p - y / 56 * (1385 + 3633 * g + 4095 * m + 1574 * m * g)))), n = Lt(this.long0 + x * (1 - y / 6 * (1 + 2 * g + p - y / 20 * (5 + 28 * g + 24 * m + 8 * p * g + 6 * p - y / 42 * (61 + 662 * g + 1320 * m + 720 * m * g)))) / d);
    } else
      i = xt * N2(a), n = 0;
  else {
    var o = Math.exp(r / this.k0), l = 0.5 * (o - 1 / o), c = this.lat0 + a / this.k0, h = Math.cos(c);
    e = Math.sqrt((1 - Math.pow(h, 2)) / (1 + Math.pow(l, 2))), i = Math.asin(e), a < 0 && (i = -i), l === 0 && h === 0 ? n = 0 : n = Lt(Math.atan2(l, h) + this.long0);
  }
  return s.x = n, s.y = i, s;
}
var Ade = ["Fast_Transverse_Mercator", "Fast Transverse Mercator"];
const Yy = {
  init: vde,
  forward: yde,
  inverse: xde,
  names: Ade
};
function wF(s) {
  var e = Math.exp(s);
  return e = (e - 1 / e) / 2, e;
}
function Or(s, e) {
  s = Math.abs(s), e = Math.abs(e);
  var t = Math.max(s, e), i = Math.min(s, e) / (t || 1);
  return t * Math.sqrt(1 + Math.pow(i, 2));
}
function bde(s) {
  var e = 1 + s, t = e - 1;
  return t === 0 ? s : s * Math.log(e) / t;
}
function Mde(s) {
  var e = Math.abs(s);
  return e = bde(e * (1 + e / (Or(1, e) + 1))), s < 0 ? -e : e;
}
function ww(s, e) {
  for (var t = 2 * Math.cos(2 * e), i = s.length - 1, n = s[i], r = 0, a; --i >= 0; )
    a = -r + t * n + s[i], r = n, n = a;
  return e + a * Math.sin(2 * e);
}
function Sde(s, e) {
  for (var t = 2 * Math.cos(e), i = s.length - 1, n = s[i], r = 0, a; --i >= 0; )
    a = -r + t * n + s[i], r = n, n = a;
  return Math.sin(e) * a;
}
function wde(s) {
  var e = Math.exp(s);
  return e = (e + 1 / e) / 2, e;
}
function EF(s, e, t) {
  for (var i = Math.sin(e), n = Math.cos(e), r = wF(t), a = wde(t), o = 2 * n * a, l = -2 * i * r, c = s.length - 1, h = s[c], u = 0, d = 0, f = 0, p, v; --c >= 0; )
    p = d, v = u, d = h, u = f, h = -p + o * d - l * u + s[c], f = -v + l * d + o * u;
  return o = i * a, l = n * r, [o * h - l * f, o * f + l * h];
}
function Ede() {
  if (!this.approx && (isNaN(this.es) || this.es <= 0))
    throw new Error('Incorrect elliptical usage. Try using the +approx option in the proj string, or PROJECTION["Fast_Transverse_Mercator"] in the WKT.');
  this.approx && (Yy.init.apply(this), this.forward = Yy.forward, this.inverse = Yy.inverse), this.x0 = this.x0 !== void 0 ? this.x0 : 0, this.y0 = this.y0 !== void 0 ? this.y0 : 0, this.long0 = this.long0 !== void 0 ? this.long0 : 0, this.lat0 = this.lat0 !== void 0 ? this.lat0 : 0, this.cgb = [], this.cbg = [], this.utg = [], this.gtu = [];
  var s = this.es / (1 + Math.sqrt(1 - this.es)), e = s / (2 - s), t = e;
  this.cgb[0] = e * (2 + e * (-2 / 3 + e * (-2 + e * (116 / 45 + e * (26 / 45 + e * (-2854 / 675)))))), this.cbg[0] = e * (-2 + e * (2 / 3 + e * (4 / 3 + e * (-82 / 45 + e * (32 / 45 + e * (4642 / 4725)))))), t = t * e, this.cgb[1] = t * (7 / 3 + e * (-8 / 5 + e * (-227 / 45 + e * (2704 / 315 + e * (2323 / 945))))), this.cbg[1] = t * (5 / 3 + e * (-16 / 15 + e * (-13 / 9 + e * (904 / 315 + e * (-1522 / 945))))), t = t * e, this.cgb[2] = t * (56 / 15 + e * (-136 / 35 + e * (-1262 / 105 + e * (73814 / 2835)))), this.cbg[2] = t * (-26 / 15 + e * (34 / 21 + e * (8 / 5 + e * (-12686 / 2835)))), t = t * e, this.cgb[3] = t * (4279 / 630 + e * (-332 / 35 + e * (-399572 / 14175))), this.cbg[3] = t * (1237 / 630 + e * (-12 / 5 + e * (-24832 / 14175))), t = t * e, this.cgb[4] = t * (4174 / 315 + e * (-144838 / 6237)), this.cbg[4] = t * (-734 / 315 + e * (109598 / 31185)), t = t * e, this.cgb[5] = t * (601676 / 22275), this.cbg[5] = t * (444337 / 155925), t = Math.pow(e, 2), this.Qn = this.k0 / (1 + e) * (1 + t * (1 / 4 + t * (1 / 64 + t / 256))), this.utg[0] = e * (-0.5 + e * (2 / 3 + e * (-37 / 96 + e * (1 / 360 + e * (81 / 512 + e * (-96199 / 604800)))))), this.gtu[0] = e * (0.5 + e * (-2 / 3 + e * (5 / 16 + e * (41 / 180 + e * (-127 / 288 + e * (7891 / 37800)))))), this.utg[1] = t * (-1 / 48 + e * (-1 / 15 + e * (437 / 1440 + e * (-46 / 105 + e * (1118711 / 3870720))))), this.gtu[1] = t * (13 / 48 + e * (-3 / 5 + e * (557 / 1440 + e * (281 / 630 + e * (-1983433 / 1935360))))), t = t * e, this.utg[2] = t * (-17 / 480 + e * (37 / 840 + e * (209 / 4480 + e * (-5569 / 90720)))), this.gtu[2] = t * (61 / 240 + e * (-103 / 140 + e * (15061 / 26880 + e * (167603 / 181440)))), t = t * e, this.utg[3] = t * (-4397 / 161280 + e * (11 / 504 + e * (830251 / 7257600))), this.gtu[3] = t * (49561 / 161280 + e * (-179 / 168 + e * (6601661 / 7257600))), t = t * e, this.utg[4] = t * (-4583 / 161280 + e * (108847 / 3991680)), this.gtu[4] = t * (34729 / 80640 + e * (-3418889 / 1995840)), t = t * e, this.utg[5] = t * (-20648693 / 638668800), this.gtu[5] = t * (212378941 / 319334400);
  var i = ww(this.cbg, this.lat0);
  this.Zb = -this.Qn * (i + Sde(this.gtu, 2 * i));
}
function Tde(s) {
  var e = Lt(s.x - this.long0), t = s.y;
  t = ww(this.cbg, t);
  var i = Math.sin(t), n = Math.cos(t), r = Math.sin(e), a = Math.cos(e);
  t = Math.atan2(i, a * n), e = Math.atan2(r * n, Or(i, n * a)), e = Mde(Math.tan(e));
  var o = EF(this.gtu, 2 * t, 2 * e);
  t = t + o[0], e = e + o[1];
  var l, c;
  return Math.abs(e) <= 2.623395162778 ? (l = this.a * (this.Qn * e) + this.x0, c = this.a * (this.Qn * t + this.Zb) + this.y0) : (l = 1 / 0, c = 1 / 0), s.x = l, s.y = c, s;
}
function Cde(s) {
  var e = (s.x - this.x0) * (1 / this.a), t = (s.y - this.y0) * (1 / this.a);
  t = (t - this.Zb) / this.Qn, e = e / this.Qn;
  var i, n;
  if (Math.abs(e) <= 2.623395162778) {
    var r = EF(this.utg, 2 * t, 2 * e);
    t = t + r[0], e = e + r[1], e = Math.atan(wF(e));
    var a = Math.sin(t), o = Math.cos(t), l = Math.sin(e), c = Math.cos(e);
    t = Math.atan2(a * c, Or(l, c * o)), e = Math.atan2(l, c * o), i = Lt(e + this.long0), n = ww(this.cgb, t);
  } else
    i = 1 / 0, n = 1 / 0;
  return s.x = i, s.y = n, s;
}
var _de = ["Extended_Transverse_Mercator", "Extended Transverse Mercator", "etmerc", "Transverse_Mercator", "Transverse Mercator", "Gauss Kruger", "Gauss_Kruger", "tmerc"];
const Zy = {
  init: Ede,
  forward: Tde,
  inverse: Cde,
  names: _de
};
function Pde(s, e) {
  if (s === void 0) {
    if (s = Math.floor((Lt(e) + Math.PI) * 30 / Math.PI) + 1, s < 0)
      return 0;
    if (s > 60)
      return 60;
  }
  return s;
}
var Rde = "etmerc";
function Dde() {
  var s = Pde(this.zone, this.long0);
  if (s === void 0)
    throw new Error("unknown utm zone");
  this.lat0 = 0, this.long0 = (6 * Math.abs(s) - 183) * zs, this.x0 = 5e5, this.y0 = this.utmSouth ? 1e7 : 0, this.k0 = 0.9996, Zy.init.apply(this), this.forward = Zy.forward, this.inverse = Zy.inverse;
}
var Lde = ["Universal Transverse Mercator System", "utm"];
const Ide = {
  init: Dde,
  names: Lde,
  dependsOn: Rde
};
function Ew(s, e) {
  return Math.pow((1 - s) / (1 + s), e);
}
var Ode = 20;
function Nde() {
  var s = Math.sin(this.lat0), e = Math.cos(this.lat0);
  e *= e, this.rc = Math.sqrt(1 - this.es) / (1 - this.es * s * s), this.C = Math.sqrt(1 + this.es * e * e / (1 - this.es)), this.phic0 = Math.asin(s / this.C), this.ratexp = 0.5 * this.C * this.e, this.K = Math.tan(0.5 * this.phic0 + $i) / (Math.pow(Math.tan(0.5 * this.lat0 + $i), this.C) * Ew(this.e * s, this.ratexp));
}
function Bde(s) {
  var e = s.x, t = s.y;
  return s.y = 2 * Math.atan(this.K * Math.pow(Math.tan(0.5 * t + $i), this.C) * Ew(this.e * Math.sin(t), this.ratexp)) - xt, s.x = this.C * e, s;
}
function Fde(s) {
  for (var e = 1e-14, t = s.x / this.C, i = s.y, n = Math.pow(Math.tan(0.5 * i + $i) / this.K, 1 / this.C), r = Ode; r > 0 && (i = 2 * Math.atan(n * Ew(this.e * Math.sin(s.y), -0.5 * this.e)) - xt, !(Math.abs(i - s.y) < e)); --r)
    s.y = i;
  return r ? (s.x = t, s.y = i, s) : null;
}
var Ude = ["gauss"];
const Tw = {
  init: Nde,
  forward: Bde,
  inverse: Fde,
  names: Ude
};
function zde() {
  Tw.init.apply(this), this.rc && (this.sinc0 = Math.sin(this.phic0), this.cosc0 = Math.cos(this.phic0), this.R2 = 2 * this.rc, this.title || (this.title = "Oblique Stereographic Alternative"));
}
function kde(s) {
  var e, t, i, n;
  return s.x = Lt(s.x - this.long0), Tw.forward.apply(this, [s]), e = Math.sin(s.y), t = Math.cos(s.y), i = Math.cos(s.x), n = this.k0 * this.R2 / (1 + this.sinc0 * e + this.cosc0 * t * i), s.x = n * t * Math.sin(s.x), s.y = n * (this.cosc0 * e - this.sinc0 * t * i), s.x = this.a * s.x + this.x0, s.y = this.a * s.y + this.y0, s;
}
function Gde(s) {
  var e, t, i, n, r;
  if (s.x = (s.x - this.x0) / this.a, s.y = (s.y - this.y0) / this.a, s.x /= this.k0, s.y /= this.k0, r = Or(s.x, s.y)) {
    var a = 2 * Math.atan2(r, this.R2);
    e = Math.sin(a), t = Math.cos(a), n = Math.asin(t * this.sinc0 + s.y * e * this.cosc0 / r), i = Math.atan2(s.x * e, r * this.cosc0 * t - s.y * this.sinc0 * e);
  } else
    n = this.phic0, i = 0;
  return s.x = i, s.y = n, Tw.inverse.apply(this, [s]), s.x = Lt(s.x + this.long0), s;
}
var Hde = ["Stereographic_North_Pole", "Oblique_Stereographic", "sterea", "Oblique Stereographic Alternative", "Double_Stereographic"];
const Vde = {
  init: zde,
  forward: kde,
  inverse: Gde,
  names: Hde
};
function Wde(s, e, t) {
  return e *= t, Math.tan(0.5 * (xt + s)) * Math.pow((1 - e) / (1 + e), 0.5 * t);
}
function Xde() {
  this.x0 = this.x0 || 0, this.y0 = this.y0 || 0, this.lat0 = this.lat0 || 0, this.long0 = this.long0 || 0, this.coslat0 = Math.cos(this.lat0), this.sinlat0 = Math.sin(this.lat0), this.sphere ? this.k0 === 1 && !isNaN(this.lat_ts) && Math.abs(this.coslat0) <= Dt && (this.k0 = 0.5 * (1 + N2(this.lat0) * Math.sin(this.lat_ts))) : (Math.abs(this.coslat0) <= Dt && (this.lat0 > 0 ? this.con = 1 : this.con = -1), this.cons = Math.sqrt(Math.pow(1 + this.e, 1 + this.e) * Math.pow(1 - this.e, 1 - this.e)), this.k0 === 1 && !isNaN(this.lat_ts) && Math.abs(this.coslat0) <= Dt && Math.abs(Math.cos(this.lat_ts)) > Dt && (this.k0 = 0.5 * this.cons * m0(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts)) / ro(this.e, this.con * this.lat_ts, this.con * Math.sin(this.lat_ts))), this.ms1 = m0(this.e, this.sinlat0, this.coslat0), this.X0 = 2 * Math.atan(this.ssfn_(this.lat0, this.sinlat0, this.e)) - xt, this.cosX0 = Math.cos(this.X0), this.sinX0 = Math.sin(this.X0));
}
function jde(s) {
  var e = s.x, t = s.y, i = Math.sin(t), n = Math.cos(t), r, a, o, l, c, h, u = Lt(e - this.long0);
  return Math.abs(Math.abs(e - this.long0) - Math.PI) <= Dt && Math.abs(t + this.lat0) <= Dt ? (s.x = NaN, s.y = NaN, s) : this.sphere ? (r = 2 * this.k0 / (1 + this.sinlat0 * i + this.coslat0 * n * Math.cos(u)), s.x = this.a * r * n * Math.sin(u) + this.x0, s.y = this.a * r * (this.coslat0 * i - this.sinlat0 * n * Math.cos(u)) + this.y0, s) : (a = 2 * Math.atan(this.ssfn_(t, i, this.e)) - xt, l = Math.cos(a), o = Math.sin(a), Math.abs(this.coslat0) <= Dt ? (c = ro(this.e, t * this.con, this.con * i), h = 2 * this.a * this.k0 * c / this.cons, s.x = this.x0 + h * Math.sin(e - this.long0), s.y = this.y0 - this.con * h * Math.cos(e - this.long0), s) : (Math.abs(this.sinlat0) < Dt ? (r = 2 * this.a * this.k0 / (1 + l * Math.cos(u)), s.y = r * o) : (r = 2 * this.a * this.k0 * this.ms1 / (this.cosX0 * (1 + this.sinX0 * o + this.cosX0 * l * Math.cos(u))), s.y = r * (this.cosX0 * o - this.sinX0 * l * Math.cos(u)) + this.y0), s.x = r * l * Math.sin(u) + this.x0, s));
}
function qde(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e, t, i, n, r, a = Math.sqrt(s.x * s.x + s.y * s.y);
  if (this.sphere) {
    var o = 2 * Math.atan(a / (2 * this.a * this.k0));
    return e = this.long0, t = this.lat0, a <= Dt ? (s.x = e, s.y = t, s) : (t = Math.asin(Math.cos(o) * this.sinlat0 + s.y * Math.sin(o) * this.coslat0 / a), Math.abs(this.coslat0) < Dt ? this.lat0 > 0 ? e = Lt(this.long0 + Math.atan2(s.x, -1 * s.y)) : e = Lt(this.long0 + Math.atan2(s.x, s.y)) : e = Lt(this.long0 + Math.atan2(s.x * Math.sin(o), a * this.coslat0 * Math.cos(o) - s.y * this.sinlat0 * Math.sin(o))), s.x = e, s.y = t, s);
  } else if (Math.abs(this.coslat0) <= Dt) {
    if (a <= Dt)
      return t = this.lat0, e = this.long0, s.x = e, s.y = t, s;
    s.x *= this.con, s.y *= this.con, i = a * this.cons / (2 * this.a * this.k0), t = this.con * Gg(this.e, i), e = this.con * Lt(this.con * this.long0 + Math.atan2(s.x, -1 * s.y));
  } else
    n = 2 * Math.atan(a * this.cosX0 / (2 * this.a * this.k0 * this.ms1)), e = this.long0, a <= Dt ? r = this.X0 : (r = Math.asin(Math.cos(n) * this.sinX0 + s.y * Math.sin(n) * this.cosX0 / a), e = Lt(this.long0 + Math.atan2(s.x * Math.sin(n), a * this.cosX0 * Math.cos(n) - s.y * this.sinX0 * Math.sin(n)))), t = -1 * Gg(this.e, Math.tan(0.5 * (xt + r)));
  return s.x = e, s.y = t, s;
}
var Yde = ["stere", "Stereographic_South_Pole", "Polar Stereographic (variant B)", "Polar_Stereographic"];
const Zde = {
  init: Xde,
  forward: jde,
  inverse: qde,
  names: Yde,
  ssfn_: Wde
};
function Kde() {
  var s = this.lat0;
  this.lambda0 = this.long0;
  var e = Math.sin(s), t = this.a, i = this.rf, n = 1 / i, r = 2 * n - Math.pow(n, 2), a = this.e = Math.sqrt(r);
  this.R = this.k0 * t * Math.sqrt(1 - r) / (1 - r * Math.pow(e, 2)), this.alpha = Math.sqrt(1 + r / (1 - r) * Math.pow(Math.cos(s), 4)), this.b0 = Math.asin(e / this.alpha);
  var o = Math.log(Math.tan(Math.PI / 4 + this.b0 / 2)), l = Math.log(Math.tan(Math.PI / 4 + s / 2)), c = Math.log((1 + a * e) / (1 - a * e));
  this.K = o - this.alpha * l + this.alpha * a / 2 * c;
}
function Jde(s) {
  var e = Math.log(Math.tan(Math.PI / 4 - s.y / 2)), t = this.e / 2 * Math.log((1 + this.e * Math.sin(s.y)) / (1 - this.e * Math.sin(s.y))), i = -this.alpha * (e + t) + this.K, n = 2 * (Math.atan(Math.exp(i)) - Math.PI / 4), r = this.alpha * (s.x - this.lambda0), a = Math.atan(Math.sin(r) / (Math.sin(this.b0) * Math.tan(n) + Math.cos(this.b0) * Math.cos(r))), o = Math.asin(Math.cos(this.b0) * Math.sin(n) - Math.sin(this.b0) * Math.cos(n) * Math.cos(r));
  return s.y = this.R / 2 * Math.log((1 + Math.sin(o)) / (1 - Math.sin(o))) + this.y0, s.x = this.R * a + this.x0, s;
}
function Qde(s) {
  for (var e = s.x - this.x0, t = s.y - this.y0, i = e / this.R, n = 2 * (Math.atan(Math.exp(t / this.R)) - Math.PI / 4), r = Math.asin(Math.cos(this.b0) * Math.sin(n) + Math.sin(this.b0) * Math.cos(n) * Math.cos(i)), a = Math.atan(Math.sin(i) / (Math.cos(this.b0) * Math.cos(i) - Math.sin(this.b0) * Math.tan(n))), o = this.lambda0 + a / this.alpha, l = 0, c = r, h = -1e3, u = 0; Math.abs(c - h) > 1e-7; ) {
    if (++u > 20)
      return;
    l = 1 / this.alpha * (Math.log(Math.tan(Math.PI / 4 + r / 2)) - this.K) + this.e * Math.log(Math.tan(Math.PI / 4 + Math.asin(this.e * Math.sin(c)) / 2)), h = c, c = 2 * Math.atan(Math.exp(l)) - Math.PI / 2;
  }
  return s.x = o, s.y = c, s;
}
var $de = ["somerc"];
const efe = {
  init: Kde,
  forward: Jde,
  inverse: Qde,
  names: $de
};
var vf = 1e-7;
function tfe(s) {
  var e = ["Hotine_Oblique_Mercator", "Hotine_Oblique_Mercator_Azimuth_Natural_Origin"], t = typeof s.PROJECTION == "object" ? Object.keys(s.PROJECTION)[0] : s.PROJECTION;
  return "no_uoff" in s || "no_off" in s || e.indexOf(t) !== -1;
}
function ife() {
  var s, e, t, i, n, r, a, o, l, c, h = 0, u, d = 0, f = 0, p = 0, v = 0, g = 0, m = 0;
  this.no_off = tfe(this), this.no_rot = "no_rot" in this;
  var x = !1;
  "alpha" in this && (x = !0);
  var y = !1;
  if ("rectified_grid_angle" in this && (y = !0), x && (m = this.alpha), y && (h = this.rectified_grid_angle * zs), x || y)
    d = this.longc;
  else if (f = this.long1, v = this.lat1, p = this.long2, g = this.lat2, Math.abs(v - g) <= vf || (s = Math.abs(v)) <= vf || Math.abs(s - xt) <= vf || Math.abs(Math.abs(this.lat0) - xt) <= vf || Math.abs(Math.abs(g) - xt) <= vf)
    throw new Error();
  var A = 1 - this.es;
  e = Math.sqrt(A), Math.abs(this.lat0) > Dt ? (o = Math.sin(this.lat0), t = Math.cos(this.lat0), s = 1 - this.es * o * o, this.B = t * t, this.B = Math.sqrt(1 + this.es * this.B * this.B / A), this.A = this.B * this.k0 * e / s, i = this.B * e / (t * Math.sqrt(s)), n = i * i - 1, n <= 0 ? n = 0 : (n = Math.sqrt(n), this.lat0 < 0 && (n = -n)), this.E = n += i, this.E *= Math.pow(ro(this.e, this.lat0, o), this.B)) : (this.B = 1 / e, this.A = this.k0, this.E = i = n = 1), x || y ? (x ? (u = Math.asin(Math.sin(m) / i), y || (h = m)) : (u = h, m = Math.asin(i * Math.sin(u))), this.lam0 = d - Math.asin(0.5 * (n - 1 / n) * Math.tan(u)) / this.B) : (r = Math.pow(ro(this.e, v, Math.sin(v)), this.B), a = Math.pow(ro(this.e, g, Math.sin(g)), this.B), n = this.E / r, l = (a - r) / (a + r), c = this.E * this.E, c = (c - a * r) / (c + a * r), s = f - p, s < -Math.pi ? p -= zg : s > Math.pi && (p += zg), this.lam0 = Lt(0.5 * (f + p) - Math.atan(c * Math.tan(0.5 * this.B * (f - p)) / l) / this.B), u = Math.atan(2 * Math.sin(this.B * Lt(f - this.lam0)) / (n - 1 / n)), h = m = Math.asin(i * Math.sin(u))), this.singam = Math.sin(u), this.cosgam = Math.cos(u), this.sinrot = Math.sin(h), this.cosrot = Math.cos(h), this.rB = 1 / this.B, this.ArB = this.A * this.rB, this.BrA = 1 / this.ArB, this.A * this.B, this.no_off ? this.u_0 = 0 : (this.u_0 = Math.abs(this.ArB * Math.atan(Math.sqrt(i * i - 1) / Math.cos(m))), this.lat0 < 0 && (this.u_0 = -this.u_0)), n = 0.5 * u, this.v_pole_n = this.ArB * Math.log(Math.tan($i - n)), this.v_pole_s = this.ArB * Math.log(Math.tan($i + n));
}
function nfe(s) {
  var e = {}, t, i, n, r, a, o, l, c;
  if (s.x = s.x - this.lam0, Math.abs(Math.abs(s.y) - xt) > Dt) {
    if (a = this.E / Math.pow(ro(this.e, s.y, Math.sin(s.y)), this.B), o = 1 / a, t = 0.5 * (a - o), i = 0.5 * (a + o), r = Math.sin(this.B * s.x), n = (t * this.singam - r * this.cosgam) / i, Math.abs(Math.abs(n) - 1) < Dt)
      throw new Error();
    c = 0.5 * this.ArB * Math.log((1 - n) / (1 + n)), o = Math.cos(this.B * s.x), Math.abs(o) < vf ? l = this.A * s.x : l = this.ArB * Math.atan2(t * this.cosgam + r * this.singam, o);
  } else
    c = s.y > 0 ? this.v_pole_n : this.v_pole_s, l = this.ArB * s.y;
  return this.no_rot ? (e.x = l, e.y = c) : (l -= this.u_0, e.x = c * this.cosrot + l * this.sinrot, e.y = l * this.cosrot - c * this.sinrot), e.x = this.a * e.x + this.x0, e.y = this.a * e.y + this.y0, e;
}
function sfe(s) {
  var e, t, i, n, r, a, o, l = {};
  if (s.x = (s.x - this.x0) * (1 / this.a), s.y = (s.y - this.y0) * (1 / this.a), this.no_rot ? (t = s.y, e = s.x) : (t = s.x * this.cosrot - s.y * this.sinrot, e = s.y * this.cosrot + s.x * this.sinrot + this.u_0), i = Math.exp(-this.BrA * t), n = 0.5 * (i - 1 / i), r = 0.5 * (i + 1 / i), a = Math.sin(this.BrA * e), o = (a * this.cosgam + n * this.singam) / r, Math.abs(Math.abs(o) - 1) < Dt)
    l.x = 0, l.y = o < 0 ? -xt : xt;
  else {
    if (l.y = this.E / Math.sqrt((1 + o) / (1 - o)), l.y = Gg(this.e, Math.pow(l.y, 1 / this.B)), l.y === 1 / 0)
      throw new Error();
    l.x = -this.rB * Math.atan2(n * this.cosgam - a * this.singam, Math.cos(this.BrA * e));
  }
  return l.x += this.lam0, l;
}
var rfe = ["Hotine_Oblique_Mercator", "Hotine Oblique Mercator", "Hotine_Oblique_Mercator_Azimuth_Natural_Origin", "Hotine_Oblique_Mercator_Two_Point_Natural_Origin", "Hotine_Oblique_Mercator_Azimuth_Center", "Oblique_Mercator", "omerc"];
const afe = {
  init: ife,
  forward: nfe,
  inverse: sfe,
  names: rfe
};
function ofe() {
  if (this.lat2 || (this.lat2 = this.lat1), this.k0 || (this.k0 = 1), this.x0 = this.x0 || 0, this.y0 = this.y0 || 0, !(Math.abs(this.lat1 + this.lat2) < Dt)) {
    var s = this.b / this.a;
    this.e = Math.sqrt(1 - s * s);
    var e = Math.sin(this.lat1), t = Math.cos(this.lat1), i = m0(this.e, e, t), n = ro(this.e, this.lat1, e), r = Math.sin(this.lat2), a = Math.cos(this.lat2), o = m0(this.e, r, a), l = ro(this.e, this.lat2, r), c = ro(this.e, this.lat0, Math.sin(this.lat0));
    Math.abs(this.lat1 - this.lat2) > Dt ? this.ns = Math.log(i / o) / Math.log(n / l) : this.ns = e, isNaN(this.ns) && (this.ns = e), this.f0 = i / (this.ns * Math.pow(n, this.ns)), this.rh = this.a * this.f0 * Math.pow(c, this.ns), this.title || (this.title = "Lambert Conformal Conic");
  }
}
function lfe(s) {
  var e = s.x, t = s.y;
  Math.abs(2 * Math.abs(t) - Math.PI) <= Dt && (t = N2(t) * (xt - 2 * Dt));
  var i = Math.abs(Math.abs(t) - xt), n, r;
  if (i > Dt)
    n = ro(this.e, t, Math.sin(t)), r = this.a * this.f0 * Math.pow(n, this.ns);
  else {
    if (i = t * this.ns, i <= 0)
      return null;
    r = 0;
  }
  var a = this.ns * Lt(e - this.long0);
  return s.x = this.k0 * (r * Math.sin(a)) + this.x0, s.y = this.k0 * (this.rh - r * Math.cos(a)) + this.y0, s;
}
function cfe(s) {
  var e, t, i, n, r, a = (s.x - this.x0) / this.k0, o = this.rh - (s.y - this.y0) / this.k0;
  this.ns > 0 ? (e = Math.sqrt(a * a + o * o), t = 1) : (e = -Math.sqrt(a * a + o * o), t = -1);
  var l = 0;
  if (e !== 0 && (l = Math.atan2(t * a, t * o)), e !== 0 || this.ns > 0) {
    if (t = 1 / this.ns, i = Math.pow(e / (this.a * this.f0), t), n = Gg(this.e, i), n === -9999)
      return null;
  } else
    n = -xt;
  return r = Lt(l / this.ns + this.long0), s.x = r, s.y = n, s;
}
var hfe = [
  "Lambert Tangential Conformal Conic Projection",
  "Lambert_Conformal_Conic",
  "Lambert_Conformal_Conic_1SP",
  "Lambert_Conformal_Conic_2SP",
  "lcc",
  "Lambert Conic Conformal (1SP)",
  "Lambert Conic Conformal (2SP)"
];
const ufe = {
  init: ofe,
  forward: lfe,
  inverse: cfe,
  names: hfe
};
function dfe() {
  this.a = 6377397155e-3, this.es = 0.006674372230614, this.e = Math.sqrt(this.es), this.lat0 || (this.lat0 = 0.863937979737193), this.long0 || (this.long0 = 0.7417649320975901 - 0.308341501185665), this.k0 || (this.k0 = 0.9999), this.s45 = 0.785398163397448, this.s90 = 2 * this.s45, this.fi0 = this.lat0, this.e2 = this.es, this.e = Math.sqrt(this.e2), this.alfa = Math.sqrt(1 + this.e2 * Math.pow(Math.cos(this.fi0), 4) / (1 - this.e2)), this.uq = 1.04216856380474, this.u0 = Math.asin(Math.sin(this.fi0) / this.alfa), this.g = Math.pow((1 + this.e * Math.sin(this.fi0)) / (1 - this.e * Math.sin(this.fi0)), this.alfa * this.e / 2), this.k = Math.tan(this.u0 / 2 + this.s45) / Math.pow(Math.tan(this.fi0 / 2 + this.s45), this.alfa) * this.g, this.k1 = this.k0, this.n0 = this.a * Math.sqrt(1 - this.e2) / (1 - this.e2 * Math.pow(Math.sin(this.fi0), 2)), this.s0 = 1.37008346281555, this.n = Math.sin(this.s0), this.ro0 = this.k1 * this.n0 / Math.tan(this.s0), this.ad = this.s90 - this.uq;
}
function ffe(s) {
  var e, t, i, n, r, a, o, l = s.x, c = s.y, h = Lt(l - this.long0);
  return e = Math.pow((1 + this.e * Math.sin(c)) / (1 - this.e * Math.sin(c)), this.alfa * this.e / 2), t = 2 * (Math.atan(this.k * Math.pow(Math.tan(c / 2 + this.s45), this.alfa) / e) - this.s45), i = -h * this.alfa, n = Math.asin(Math.cos(this.ad) * Math.sin(t) + Math.sin(this.ad) * Math.cos(t) * Math.cos(i)), r = Math.asin(Math.cos(t) * Math.sin(i) / Math.cos(n)), a = this.n * r, o = this.ro0 * Math.pow(Math.tan(this.s0 / 2 + this.s45), this.n) / Math.pow(Math.tan(n / 2 + this.s45), this.n), s.y = o * Math.cos(a) / 1, s.x = o * Math.sin(a) / 1, this.czech || (s.y *= -1, s.x *= -1), s;
}
function pfe(s) {
  var e, t, i, n, r, a, o, l, c = s.x;
  s.x = s.y, s.y = c, this.czech || (s.y *= -1, s.x *= -1), a = Math.sqrt(s.x * s.x + s.y * s.y), r = Math.atan2(s.y, s.x), n = r / Math.sin(this.s0), i = 2 * (Math.atan(Math.pow(this.ro0 / a, 1 / this.n) * Math.tan(this.s0 / 2 + this.s45)) - this.s45), e = Math.asin(Math.cos(this.ad) * Math.sin(i) - Math.sin(this.ad) * Math.cos(i) * Math.cos(n)), t = Math.asin(Math.cos(i) * Math.sin(n) / Math.cos(e)), s.x = this.long0 - t / this.alfa, o = e, l = 0;
  var h = 0;
  do
    s.y = 2 * (Math.atan(Math.pow(this.k, -1 / this.alfa) * Math.pow(Math.tan(e / 2 + this.s45), 1 / this.alfa) * Math.pow((1 + this.e * Math.sin(o)) / (1 - this.e * Math.sin(o)), this.e / 2)) - this.s45), Math.abs(o - s.y) < 1e-10 && (l = 1), o = s.y, h += 1;
  while (l === 0 && h < 15);
  return h >= 15 ? null : s;
}
var mfe = ["Krovak", "krovak"];
const gfe = {
  init: dfe,
  forward: ffe,
  inverse: pfe,
  names: mfe
};
function xr(s, e, t, i, n) {
  return s * n - e * Math.sin(2 * n) + t * Math.sin(4 * n) - i * Math.sin(6 * n);
}
function B2(s) {
  return 1 - 0.25 * s * (1 + s / 16 * (3 + 1.25 * s));
}
function F2(s) {
  return 0.375 * s * (1 + 0.25 * s * (1 + 0.46875 * s));
}
function U2(s) {
  return 0.05859375 * s * s * (1 + 0.75 * s);
}
function z2(s) {
  return s * s * s * (35 / 3072);
}
function Lp(s, e, t) {
  var i = e * t;
  return s / Math.sqrt(1 - i * i);
}
function ch(s) {
  return Math.abs(s) < xt ? s : s - N2(s) * Math.PI;
}
function Px(s, e, t, i, n) {
  var r, a;
  r = s / e;
  for (var o = 0; o < 15; o++)
    if (a = (s - (e * r - t * Math.sin(2 * r) + i * Math.sin(4 * r) - n * Math.sin(6 * r))) / (e - 2 * t * Math.cos(2 * r) + 4 * i * Math.cos(4 * r) - 6 * n * Math.cos(6 * r)), r += a, Math.abs(a) <= 1e-10)
      return r;
  return NaN;
}
function vfe() {
  this.sphere || (this.e0 = B2(this.es), this.e1 = F2(this.es), this.e2 = U2(this.es), this.e3 = z2(this.es), this.ml0 = this.a * xr(this.e0, this.e1, this.e2, this.e3, this.lat0));
}
function yfe(s) {
  var e, t, i = s.x, n = s.y;
  if (i = Lt(i - this.long0), this.sphere)
    e = this.a * Math.asin(Math.cos(n) * Math.sin(i)), t = this.a * (Math.atan2(Math.tan(n), Math.cos(i)) - this.lat0);
  else {
    var r = Math.sin(n), a = Math.cos(n), o = Lp(this.a, this.e, r), l = Math.tan(n) * Math.tan(n), c = i * Math.cos(n), h = c * c, u = this.es * a * a / (1 - this.es), d = this.a * xr(this.e0, this.e1, this.e2, this.e3, n);
    e = o * c * (1 - h * l * (1 / 6 - (8 - l + 8 * u) * h / 120)), t = d - this.ml0 + o * r / a * h * (0.5 + (5 - l + 6 * u) * h / 24);
  }
  return s.x = e + this.x0, s.y = t + this.y0, s;
}
function xfe(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e = s.x / this.a, t = s.y / this.a, i, n;
  if (this.sphere) {
    var r = t + this.lat0;
    i = Math.asin(Math.sin(r) * Math.cos(e)), n = Math.atan2(Math.tan(e), Math.cos(r));
  } else {
    var a = this.ml0 / this.a + t, o = Px(a, this.e0, this.e1, this.e2, this.e3);
    if (Math.abs(Math.abs(o) - xt) <= Dt)
      return s.x = this.long0, s.y = xt, t < 0 && (s.y *= -1), s;
    var l = Lp(this.a, this.e, Math.sin(o)), c = l * l * l / this.a / this.a * (1 - this.es), h = Math.pow(Math.tan(o), 2), u = e * this.a / l, d = u * u;
    i = o - l * Math.tan(o) / c * u * u * (0.5 - (1 + 3 * h) * u * u / 24), n = u * (1 - d * (h / 3 + (1 + 3 * h) * h * d / 15)) / Math.cos(o);
  }
  return s.x = Lt(n + this.long0), s.y = ch(i), s;
}
var Afe = ["Cassini", "Cassini_Soldner", "cass"];
const bfe = {
  init: vfe,
  forward: yfe,
  inverse: xfe,
  names: Afe
};
function Oc(s, e) {
  var t;
  return s > 1e-7 ? (t = s * e, (1 - s * s) * (e / (1 - t * t) - 0.5 / s * Math.log((1 - t) / (1 + t)))) : 2 * e;
}
var Mfe = 1, Sfe = 2, wfe = 3, Efe = 4;
function Tfe() {
  var s = Math.abs(this.lat0);
  if (Math.abs(s - xt) < Dt ? this.mode = this.lat0 < 0 ? this.S_POLE : this.N_POLE : Math.abs(s) < Dt ? this.mode = this.EQUIT : this.mode = this.OBLIQ, this.es > 0) {
    var e;
    switch (this.qp = Oc(this.e, 1), this.mmf = 0.5 / (1 - this.es), this.apa = Nfe(this.es), this.mode) {
      case this.N_POLE:
        this.dd = 1;
        break;
      case this.S_POLE:
        this.dd = 1;
        break;
      case this.EQUIT:
        this.rq = Math.sqrt(0.5 * this.qp), this.dd = 1 / this.rq, this.xmf = 1, this.ymf = 0.5 * this.qp;
        break;
      case this.OBLIQ:
        this.rq = Math.sqrt(0.5 * this.qp), e = Math.sin(this.lat0), this.sinb1 = Oc(this.e, e) / this.qp, this.cosb1 = Math.sqrt(1 - this.sinb1 * this.sinb1), this.dd = Math.cos(this.lat0) / (Math.sqrt(1 - this.es * e * e) * this.rq * this.cosb1), this.ymf = (this.xmf = this.rq) / this.dd, this.xmf *= this.dd;
        break;
    }
  } else
    this.mode === this.OBLIQ && (this.sinph0 = Math.sin(this.lat0), this.cosph0 = Math.cos(this.lat0));
}
function Cfe(s) {
  var e, t, i, n, r, a, o, l, c, h, u = s.x, d = s.y;
  if (u = Lt(u - this.long0), this.sphere) {
    if (r = Math.sin(d), h = Math.cos(d), i = Math.cos(u), this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      if (t = this.mode === this.EQUIT ? 1 + h * i : 1 + this.sinph0 * r + this.cosph0 * h * i, t <= Dt)
        return null;
      t = Math.sqrt(2 / t), e = t * h * Math.sin(u), t *= this.mode === this.EQUIT ? r : this.cosph0 * r - this.sinph0 * h * i;
    } else if (this.mode === this.N_POLE || this.mode === this.S_POLE) {
      if (this.mode === this.N_POLE && (i = -i), Math.abs(d + this.lat0) < Dt)
        return null;
      t = $i - d * 0.5, t = 2 * (this.mode === this.S_POLE ? Math.cos(t) : Math.sin(t)), e = t * Math.sin(u), t *= i;
    }
  } else {
    switch (o = 0, l = 0, c = 0, i = Math.cos(u), n = Math.sin(u), r = Math.sin(d), a = Oc(this.e, r), (this.mode === this.OBLIQ || this.mode === this.EQUIT) && (o = a / this.qp, l = Math.sqrt(1 - o * o)), this.mode) {
      case this.OBLIQ:
        c = 1 + this.sinb1 * o + this.cosb1 * l * i;
        break;
      case this.EQUIT:
        c = 1 + l * i;
        break;
      case this.N_POLE:
        c = xt + d, a = this.qp - a;
        break;
      case this.S_POLE:
        c = d - xt, a = this.qp + a;
        break;
    }
    if (Math.abs(c) < Dt)
      return null;
    switch (this.mode) {
      case this.OBLIQ:
      case this.EQUIT:
        c = Math.sqrt(2 / c), this.mode === this.OBLIQ ? t = this.ymf * c * (this.cosb1 * o - this.sinb1 * l * i) : t = (c = Math.sqrt(2 / (1 + l * i))) * o * this.ymf, e = this.xmf * c * l * n;
        break;
      case this.N_POLE:
      case this.S_POLE:
        a >= 0 ? (e = (c = Math.sqrt(a)) * n, t = i * (this.mode === this.S_POLE ? c : -c)) : e = t = 0;
        break;
    }
  }
  return s.x = this.a * e + this.x0, s.y = this.a * t + this.y0, s;
}
function _fe(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e = s.x / this.a, t = s.y / this.a, i, n, r, a, o, l, c;
  if (this.sphere) {
    var h = 0, u, d = 0;
    if (u = Math.sqrt(e * e + t * t), n = u * 0.5, n > 1)
      return null;
    switch (n = 2 * Math.asin(n), (this.mode === this.OBLIQ || this.mode === this.EQUIT) && (d = Math.sin(n), h = Math.cos(n)), this.mode) {
      case this.EQUIT:
        n = Math.abs(u) <= Dt ? 0 : Math.asin(t * d / u), e *= d, t = h * u;
        break;
      case this.OBLIQ:
        n = Math.abs(u) <= Dt ? this.lat0 : Math.asin(h * this.sinph0 + t * d * this.cosph0 / u), e *= d * this.cosph0, t = (h - Math.sin(n) * this.sinph0) * u;
        break;
      case this.N_POLE:
        t = -t, n = xt - n;
        break;
      case this.S_POLE:
        n -= xt;
        break;
    }
    i = t === 0 && (this.mode === this.EQUIT || this.mode === this.OBLIQ) ? 0 : Math.atan2(e, t);
  } else {
    if (c = 0, this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      if (e /= this.dd, t *= this.dd, l = Math.sqrt(e * e + t * t), l < Dt)
        return s.x = this.long0, s.y = this.lat0, s;
      a = 2 * Math.asin(0.5 * l / this.rq), r = Math.cos(a), e *= a = Math.sin(a), this.mode === this.OBLIQ ? (c = r * this.sinb1 + t * a * this.cosb1 / l, o = this.qp * c, t = l * this.cosb1 * r - t * this.sinb1 * a) : (c = t * a / l, o = this.qp * c, t = l * r);
    } else if (this.mode === this.N_POLE || this.mode === this.S_POLE) {
      if (this.mode === this.N_POLE && (t = -t), o = e * e + t * t, !o)
        return s.x = this.long0, s.y = this.lat0, s;
      c = 1 - o / this.qp, this.mode === this.S_POLE && (c = -c);
    }
    i = Math.atan2(e, t), n = Bfe(Math.asin(c), this.apa);
  }
  return s.x = Lt(this.long0 + i), s.y = n, s;
}
var Pfe = 0.3333333333333333, Rfe = 0.17222222222222222, Dfe = 0.10257936507936508, Lfe = 0.06388888888888888, Ife = 0.0664021164021164, Ofe = 0.016415012942191543;
function Nfe(s) {
  var e, t = [];
  return t[0] = s * Pfe, e = s * s, t[0] += e * Rfe, t[1] = e * Lfe, e *= s, t[0] += e * Dfe, t[1] += e * Ife, t[2] = e * Ofe, t;
}
function Bfe(s, e) {
  var t = s + s;
  return s + e[0] * Math.sin(t) + e[1] * Math.sin(t + t) + e[2] * Math.sin(t + t + t);
}
var Ffe = ["Lambert Azimuthal Equal Area", "Lambert_Azimuthal_Equal_Area", "laea"];
const Ufe = {
  init: Tfe,
  forward: Cfe,
  inverse: _fe,
  names: Ffe,
  S_POLE: Mfe,
  N_POLE: Sfe,
  EQUIT: wfe,
  OBLIQ: Efe
};
function Zc(s) {
  return Math.abs(s) > 1 && (s = s > 1 ? 1 : -1), Math.asin(s);
}
function zfe() {
  Math.abs(this.lat1 + this.lat2) < Dt || (this.temp = this.b / this.a, this.es = 1 - Math.pow(this.temp, 2), this.e3 = Math.sqrt(this.es), this.sin_po = Math.sin(this.lat1), this.cos_po = Math.cos(this.lat1), this.t1 = this.sin_po, this.con = this.sin_po, this.ms1 = m0(this.e3, this.sin_po, this.cos_po), this.qs1 = Oc(this.e3, this.sin_po), this.sin_po = Math.sin(this.lat2), this.cos_po = Math.cos(this.lat2), this.t2 = this.sin_po, this.ms2 = m0(this.e3, this.sin_po, this.cos_po), this.qs2 = Oc(this.e3, this.sin_po), this.sin_po = Math.sin(this.lat0), this.cos_po = Math.cos(this.lat0), this.t3 = this.sin_po, this.qs0 = Oc(this.e3, this.sin_po), Math.abs(this.lat1 - this.lat2) > Dt ? this.ns0 = (this.ms1 * this.ms1 - this.ms2 * this.ms2) / (this.qs2 - this.qs1) : this.ns0 = this.con, this.c = this.ms1 * this.ms1 + this.ns0 * this.qs1, this.rh = this.a * Math.sqrt(this.c - this.ns0 * this.qs0) / this.ns0);
}
function kfe(s) {
  var e = s.x, t = s.y;
  this.sin_phi = Math.sin(t), this.cos_phi = Math.cos(t);
  var i = Oc(this.e3, this.sin_phi), n = this.a * Math.sqrt(this.c - this.ns0 * i) / this.ns0, r = this.ns0 * Lt(e - this.long0), a = n * Math.sin(r) + this.x0, o = this.rh - n * Math.cos(r) + this.y0;
  return s.x = a, s.y = o, s;
}
function Gfe(s) {
  var e, t, i, n, r, a;
  return s.x -= this.x0, s.y = this.rh - s.y + this.y0, this.ns0 >= 0 ? (e = Math.sqrt(s.x * s.x + s.y * s.y), i = 1) : (e = -Math.sqrt(s.x * s.x + s.y * s.y), i = -1), n = 0, e !== 0 && (n = Math.atan2(i * s.x, i * s.y)), i = e * this.ns0 / this.a, this.sphere ? a = Math.asin((this.c - i * i) / (2 * this.ns0)) : (t = (this.c - i * i) / this.ns0, a = this.phi1z(this.e3, t)), r = Lt(n / this.ns0 + this.long0), s.x = r, s.y = a, s;
}
function Hfe(s, e) {
  var t, i, n, r, a, o = Zc(0.5 * e);
  if (s < Dt)
    return o;
  for (var l = s * s, c = 1; c <= 25; c++)
    if (t = Math.sin(o), i = Math.cos(o), n = s * t, r = 1 - n * n, a = 0.5 * r * r / i * (e / (1 - l) - t / r + 0.5 / s * Math.log((1 - n) / (1 + n))), o = o + a, Math.abs(a) <= 1e-7)
      return o;
  return null;
}
var Vfe = ["Albers_Conic_Equal_Area", "Albers", "aea"];
const Wfe = {
  init: zfe,
  forward: kfe,
  inverse: Gfe,
  names: Vfe,
  phi1z: Hfe
};
function Xfe() {
  this.sin_p14 = Math.sin(this.lat0), this.cos_p14 = Math.cos(this.lat0), this.infinity_dist = 1e3 * this.a, this.rc = 1;
}
function jfe(s) {
  var e, t, i, n, r, a, o, l, c = s.x, h = s.y;
  return i = Lt(c - this.long0), e = Math.sin(h), t = Math.cos(h), n = Math.cos(i), a = this.sin_p14 * e + this.cos_p14 * t * n, r = 1, a > 0 || Math.abs(a) <= Dt ? (o = this.x0 + this.a * r * t * Math.sin(i) / a, l = this.y0 + this.a * r * (this.cos_p14 * e - this.sin_p14 * t * n) / a) : (o = this.x0 + this.infinity_dist * t * Math.sin(i), l = this.y0 + this.infinity_dist * (this.cos_p14 * e - this.sin_p14 * t * n)), s.x = o, s.y = l, s;
}
function qfe(s) {
  var e, t, i, n, r, a;
  return s.x = (s.x - this.x0) / this.a, s.y = (s.y - this.y0) / this.a, s.x /= this.k0, s.y /= this.k0, (e = Math.sqrt(s.x * s.x + s.y * s.y)) ? (n = Math.atan2(e, this.rc), t = Math.sin(n), i = Math.cos(n), a = Zc(i * this.sin_p14 + s.y * t * this.cos_p14 / e), r = Math.atan2(s.x * t, e * this.cos_p14 * i - s.y * this.sin_p14 * t), r = Lt(this.long0 + r)) : (a = this.phic0, r = 0), s.x = r, s.y = a, s;
}
var Yfe = ["gnom"];
const Zfe = {
  init: Xfe,
  forward: jfe,
  inverse: qfe,
  names: Yfe
};
function Kfe(s, e) {
  var t = 1 - (1 - s * s) / (2 * s) * Math.log((1 - s) / (1 + s));
  if (Math.abs(Math.abs(e) - t) < 1e-6)
    return e < 0 ? -1 * xt : xt;
  for (var i = Math.asin(0.5 * e), n, r, a, o, l = 0; l < 30; l++)
    if (r = Math.sin(i), a = Math.cos(i), o = s * r, n = Math.pow(1 - o * o, 2) / (2 * a) * (e / (1 - s * s) - r / (1 - o * o) + 0.5 / s * Math.log((1 - o) / (1 + o))), i += n, Math.abs(n) <= 1e-10)
      return i;
  return NaN;
}
function Jfe() {
  this.sphere || (this.k0 = m0(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts)));
}
function Qfe(s) {
  var e = s.x, t = s.y, i, n, r = Lt(e - this.long0);
  if (this.sphere)
    i = this.x0 + this.a * r * Math.cos(this.lat_ts), n = this.y0 + this.a * Math.sin(t) / Math.cos(this.lat_ts);
  else {
    var a = Oc(this.e, Math.sin(t));
    i = this.x0 + this.a * this.k0 * r, n = this.y0 + this.a * a * 0.5 / this.k0;
  }
  return s.x = i, s.y = n, s;
}
function $fe(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e, t;
  return this.sphere ? (e = Lt(this.long0 + s.x / this.a / Math.cos(this.lat_ts)), t = Math.asin(s.y / this.a * Math.cos(this.lat_ts))) : (t = Kfe(this.e, 2 * s.y * this.k0 / this.a), e = Lt(this.long0 + s.x / (this.a * this.k0))), s.x = e, s.y = t, s;
}
var epe = ["cea"];
const tpe = {
  init: Jfe,
  forward: Qfe,
  inverse: $fe,
  names: epe
};
function ipe() {
  this.x0 = this.x0 || 0, this.y0 = this.y0 || 0, this.lat0 = this.lat0 || 0, this.long0 = this.long0 || 0, this.lat_ts = this.lat_ts || 0, this.title = this.title || "Equidistant Cylindrical (Plate Carre)", this.rc = Math.cos(this.lat_ts);
}
function npe(s) {
  var e = s.x, t = s.y, i = Lt(e - this.long0), n = ch(t - this.lat0);
  return s.x = this.x0 + this.a * i * this.rc, s.y = this.y0 + this.a * n, s;
}
function spe(s) {
  var e = s.x, t = s.y;
  return s.x = Lt(this.long0 + (e - this.x0) / (this.a * this.rc)), s.y = ch(this.lat0 + (t - this.y0) / this.a), s;
}
var rpe = ["Equirectangular", "Equidistant_Cylindrical", "eqc"];
const ape = {
  init: ipe,
  forward: npe,
  inverse: spe,
  names: rpe
};
var kP = 20;
function ope() {
  this.temp = this.b / this.a, this.es = 1 - Math.pow(this.temp, 2), this.e = Math.sqrt(this.es), this.e0 = B2(this.es), this.e1 = F2(this.es), this.e2 = U2(this.es), this.e3 = z2(this.es), this.ml0 = this.a * xr(this.e0, this.e1, this.e2, this.e3, this.lat0);
}
function lpe(s) {
  var e = s.x, t = s.y, i, n, r, a = Lt(e - this.long0);
  if (r = a * Math.sin(t), this.sphere)
    Math.abs(t) <= Dt ? (i = this.a * a, n = -1 * this.a * this.lat0) : (i = this.a * Math.sin(r) / Math.tan(t), n = this.a * (ch(t - this.lat0) + (1 - Math.cos(r)) / Math.tan(t)));
  else if (Math.abs(t) <= Dt)
    i = this.a * a, n = -1 * this.ml0;
  else {
    var o = Lp(this.a, this.e, Math.sin(t)) / Math.tan(t);
    i = o * Math.sin(r), n = this.a * xr(this.e0, this.e1, this.e2, this.e3, t) - this.ml0 + o * (1 - Math.cos(r));
  }
  return s.x = i + this.x0, s.y = n + this.y0, s;
}
function cpe(s) {
  var e, t, i, n, r, a, o, l, c;
  if (i = s.x - this.x0, n = s.y - this.y0, this.sphere)
    if (Math.abs(n + this.a * this.lat0) <= Dt)
      e = Lt(i / this.a + this.long0), t = 0;
    else {
      a = this.lat0 + n / this.a, o = i * i / this.a / this.a + a * a, l = a;
      var h;
      for (r = kP; r; --r)
        if (h = Math.tan(l), c = -1 * (a * (l * h + 1) - l - 0.5 * (l * l + o) * h) / ((l - a) / h - 1), l += c, Math.abs(c) <= Dt) {
          t = l;
          break;
        }
      e = Lt(this.long0 + Math.asin(i * Math.tan(l) / this.a) / Math.sin(t));
    }
  else if (Math.abs(n + this.ml0) <= Dt)
    t = 0, e = Lt(this.long0 + i / this.a);
  else {
    a = (this.ml0 + n) / this.a, o = i * i / this.a / this.a + a * a, l = a;
    var u, d, f, p, v;
    for (r = kP; r; --r)
      if (v = this.e * Math.sin(l), u = Math.sqrt(1 - v * v) * Math.tan(l), d = this.a * xr(this.e0, this.e1, this.e2, this.e3, l), f = this.e0 - 2 * this.e1 * Math.cos(2 * l) + 4 * this.e2 * Math.cos(4 * l) - 6 * this.e3 * Math.cos(6 * l), p = d / this.a, c = (a * (u * p + 1) - p - 0.5 * u * (p * p + o)) / (this.es * Math.sin(2 * l) * (p * p + o - 2 * a * p) / (4 * u) + (a - p) * (u * f - 2 / Math.sin(2 * l)) - f), l -= c, Math.abs(c) <= Dt) {
        t = l;
        break;
      }
    u = Math.sqrt(1 - this.es * Math.pow(Math.sin(t), 2)) * Math.tan(t), e = Lt(this.long0 + Math.asin(i * u / this.a) / Math.sin(t));
  }
  return s.x = e, s.y = t, s;
}
var hpe = ["Polyconic", "poly"];
const upe = {
  init: ope,
  forward: lpe,
  inverse: cpe,
  names: hpe
};
function dpe() {
  this.A = [], this.A[1] = 0.6399175073, this.A[2] = -0.1358797613, this.A[3] = 0.063294409, this.A[4] = -0.02526853, this.A[5] = 0.0117879, this.A[6] = -55161e-7, this.A[7] = 26906e-7, this.A[8] = -1333e-6, this.A[9] = 67e-5, this.A[10] = -34e-5, this.B_re = [], this.B_im = [], this.B_re[1] = 0.7557853228, this.B_im[1] = 0, this.B_re[2] = 0.249204646, this.B_im[2] = 3371507e-9, this.B_re[3] = -1541739e-9, this.B_im[3] = 0.04105856, this.B_re[4] = -0.10162907, this.B_im[4] = 0.01727609, this.B_re[5] = -0.26623489, this.B_im[5] = -0.36249218, this.B_re[6] = -0.6870983, this.B_im[6] = -1.1651967, this.C_re = [], this.C_im = [], this.C_re[1] = 1.3231270439, this.C_im[1] = 0, this.C_re[2] = -0.577245789, this.C_im[2] = -7809598e-9, this.C_re[3] = 0.508307513, this.C_im[3] = -0.112208952, this.C_re[4] = -0.15094762, this.C_im[4] = 0.18200602, this.C_re[5] = 1.01418179, this.C_im[5] = 1.64497696, this.C_re[6] = 1.9660549, this.C_im[6] = 2.5127645, this.D = [], this.D[1] = 1.5627014243, this.D[2] = 0.5185406398, this.D[3] = -0.03333098, this.D[4] = -0.1052906, this.D[5] = -0.0368594, this.D[6] = 7317e-6, this.D[7] = 0.0122, this.D[8] = 394e-5, this.D[9] = -13e-4;
}
function fpe(s) {
  var e, t = s.x, i = s.y, n = i - this.lat0, r = t - this.long0, a = n / F1 * 1e-5, o = r, l = 1, c = 0;
  for (e = 1; e <= 10; e++)
    l = l * a, c = c + this.A[e] * l;
  var h = c, u = o, d = 1, f = 0, p, v, g = 0, m = 0;
  for (e = 1; e <= 6; e++)
    p = d * h - f * u, v = f * h + d * u, d = p, f = v, g = g + this.B_re[e] * d - this.B_im[e] * f, m = m + this.B_im[e] * d + this.B_re[e] * f;
  return s.x = m * this.a + this.x0, s.y = g * this.a + this.y0, s;
}
function ppe(s) {
  var e, t = s.x, i = s.y, n = t - this.x0, r = i - this.y0, a = r / this.a, o = n / this.a, l = 1, c = 0, h, u, d = 0, f = 0;
  for (e = 1; e <= 6; e++)
    h = l * a - c * o, u = c * a + l * o, l = h, c = u, d = d + this.C_re[e] * l - this.C_im[e] * c, f = f + this.C_im[e] * l + this.C_re[e] * c;
  for (var p = 0; p < this.iterations; p++) {
    var v = d, g = f, m, x, y = a, A = o;
    for (e = 2; e <= 6; e++)
      m = v * d - g * f, x = g * d + v * f, v = m, g = x, y = y + (e - 1) * (this.B_re[e] * v - this.B_im[e] * g), A = A + (e - 1) * (this.B_im[e] * v + this.B_re[e] * g);
    v = 1, g = 0;
    var b = this.B_re[1], M = this.B_im[1];
    for (e = 2; e <= 6; e++)
      m = v * d - g * f, x = g * d + v * f, v = m, g = x, b = b + e * (this.B_re[e] * v - this.B_im[e] * g), M = M + e * (this.B_im[e] * v + this.B_re[e] * g);
    var w = b * b + M * M;
    d = (y * b + A * M) / w, f = (A * b - y * M) / w;
  }
  var C = d, T = f, E = 1, P = 0;
  for (e = 1; e <= 9; e++)
    E = E * C, P = P + this.D[e] * E;
  var L = this.lat0 + P * F1 * 1e5, I = this.long0 + T;
  return s.x = I, s.y = L, s;
}
var mpe = ["New_Zealand_Map_Grid", "nzmg"];
const gpe = {
  init: dpe,
  forward: fpe,
  inverse: ppe,
  names: mpe
};
function vpe() {
}
function ype(s) {
  var e = s.x, t = s.y, i = Lt(e - this.long0), n = this.x0 + this.a * i, r = this.y0 + this.a * Math.log(Math.tan(Math.PI / 4 + t / 2.5)) * 1.25;
  return s.x = n, s.y = r, s;
}
function xpe(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e = Lt(this.long0 + s.x / this.a), t = 2.5 * (Math.atan(Math.exp(0.8 * s.y / this.a)) - Math.PI / 4);
  return s.x = e, s.y = t, s;
}
var Ape = ["Miller_Cylindrical", "mill"];
const bpe = {
  init: vpe,
  forward: ype,
  inverse: xpe,
  names: Ape
};
var Mpe = 20;
function Spe() {
  this.sphere ? (this.n = 1, this.m = 0, this.es = 0, this.C_y = Math.sqrt((this.m + 1) / this.n), this.C_x = this.C_y / (this.m + 1)) : this.en = Mw(this.es);
}
function wpe(s) {
  var e, t, i = s.x, n = s.y;
  if (i = Lt(i - this.long0), this.sphere) {
    if (!this.m)
      n = this.n !== 1 ? Math.asin(this.n * Math.sin(n)) : n;
    else
      for (var r = this.n * Math.sin(n), a = Mpe; a; --a) {
        var o = (this.m * n + Math.sin(n) - r) / (this.m + Math.cos(n));
        if (n -= o, Math.abs(o) < Dt)
          break;
      }
    e = this.a * this.C_x * i * (this.m + Math.cos(n)), t = this.a * this.C_y * n;
  } else {
    var l = Math.sin(n), c = Math.cos(n);
    t = this.a * im(n, l, c, this.en), e = this.a * i * c / Math.sqrt(1 - this.es * l * l);
  }
  return s.x = e, s.y = t, s;
}
function Epe(s) {
  var e, t, i, n;
  return s.x -= this.x0, i = s.x / this.a, s.y -= this.y0, e = s.y / this.a, this.sphere ? (e /= this.C_y, i = i / (this.C_x * (this.m + Math.cos(e))), this.m ? e = Zc((this.m * e + Math.sin(e)) / this.n) : this.n !== 1 && (e = Zc(Math.sin(e) / this.n)), i = Lt(i + this.long0), e = ch(e)) : (e = Sw(s.y / this.a, this.es, this.en), n = Math.abs(e), n < xt ? (n = Math.sin(e), t = this.long0 + s.x * Math.sqrt(1 - this.es * n * n) / (this.a * Math.cos(e)), i = Lt(t)) : n - Dt < xt && (i = this.long0)), s.x = i, s.y = e, s;
}
var Tpe = ["Sinusoidal", "sinu"];
const Cpe = {
  init: Spe,
  forward: wpe,
  inverse: Epe,
  names: Tpe
};
function _pe() {
}
function Ppe(s) {
  for (var e = s.x, t = s.y, i = Lt(e - this.long0), n = t, r = Math.PI * Math.sin(t); ; ) {
    var a = -(n + Math.sin(n) - r) / (1 + Math.cos(n));
    if (n += a, Math.abs(a) < Dt)
      break;
  }
  n /= 2, Math.PI / 2 - Math.abs(t) < Dt && (i = 0);
  var o = 0.900316316158 * this.a * i * Math.cos(n) + this.x0, l = 1.4142135623731 * this.a * Math.sin(n) + this.y0;
  return s.x = o, s.y = l, s;
}
function Rpe(s) {
  var e, t;
  s.x -= this.x0, s.y -= this.y0, t = s.y / (1.4142135623731 * this.a), Math.abs(t) > 0.999999999999 && (t = 0.999999999999), e = Math.asin(t);
  var i = Lt(this.long0 + s.x / (0.900316316158 * this.a * Math.cos(e)));
  i < -Math.PI && (i = -Math.PI), i > Math.PI && (i = Math.PI), t = (2 * e + Math.sin(2 * e)) / Math.PI, Math.abs(t) > 1 && (t = 1);
  var n = Math.asin(t);
  return s.x = i, s.y = n, s;
}
var Dpe = ["Mollweide", "moll"];
const Lpe = {
  init: _pe,
  forward: Ppe,
  inverse: Rpe,
  names: Dpe
};
function Ipe() {
  Math.abs(this.lat1 + this.lat2) < Dt || (this.lat2 = this.lat2 || this.lat1, this.temp = this.b / this.a, this.es = 1 - Math.pow(this.temp, 2), this.e = Math.sqrt(this.es), this.e0 = B2(this.es), this.e1 = F2(this.es), this.e2 = U2(this.es), this.e3 = z2(this.es), this.sinphi = Math.sin(this.lat1), this.cosphi = Math.cos(this.lat1), this.ms1 = m0(this.e, this.sinphi, this.cosphi), this.ml1 = xr(this.e0, this.e1, this.e2, this.e3, this.lat1), Math.abs(this.lat1 - this.lat2) < Dt ? this.ns = this.sinphi : (this.sinphi = Math.sin(this.lat2), this.cosphi = Math.cos(this.lat2), this.ms2 = m0(this.e, this.sinphi, this.cosphi), this.ml2 = xr(this.e0, this.e1, this.e2, this.e3, this.lat2), this.ns = (this.ms1 - this.ms2) / (this.ml2 - this.ml1)), this.g = this.ml1 + this.ms1 / this.ns, this.ml0 = xr(this.e0, this.e1, this.e2, this.e3, this.lat0), this.rh = this.a * (this.g - this.ml0));
}
function Ope(s) {
  var e = s.x, t = s.y, i;
  if (this.sphere)
    i = this.a * (this.g - t);
  else {
    var n = xr(this.e0, this.e1, this.e2, this.e3, t);
    i = this.a * (this.g - n);
  }
  var r = this.ns * Lt(e - this.long0), a = this.x0 + i * Math.sin(r), o = this.y0 + this.rh - i * Math.cos(r);
  return s.x = a, s.y = o, s;
}
function Npe(s) {
  s.x -= this.x0, s.y = this.rh - s.y + this.y0;
  var e, t, i, n;
  this.ns >= 0 ? (t = Math.sqrt(s.x * s.x + s.y * s.y), e = 1) : (t = -Math.sqrt(s.x * s.x + s.y * s.y), e = -1);
  var r = 0;
  if (t !== 0 && (r = Math.atan2(e * s.x, e * s.y)), this.sphere)
    return n = Lt(this.long0 + r / this.ns), i = ch(this.g - t / this.a), s.x = n, s.y = i, s;
  var a = this.g - t / this.a;
  return i = Px(a, this.e0, this.e1, this.e2, this.e3), n = Lt(this.long0 + r / this.ns), s.x = n, s.y = i, s;
}
var Bpe = ["Equidistant_Conic", "eqdc"];
const Fpe = {
  init: Ipe,
  forward: Ope,
  inverse: Npe,
  names: Bpe
};
function Upe() {
  this.R = this.a;
}
function zpe(s) {
  var e = s.x, t = s.y, i = Lt(e - this.long0), n, r;
  Math.abs(t) <= Dt && (n = this.x0 + this.R * i, r = this.y0);
  var a = Zc(2 * Math.abs(t / Math.PI));
  (Math.abs(i) <= Dt || Math.abs(Math.abs(t) - xt) <= Dt) && (n = this.x0, t >= 0 ? r = this.y0 + Math.PI * this.R * Math.tan(0.5 * a) : r = this.y0 + Math.PI * this.R * -Math.tan(0.5 * a));
  var o = 0.5 * Math.abs(Math.PI / i - i / Math.PI), l = o * o, c = Math.sin(a), h = Math.cos(a), u = h / (c + h - 1), d = u * u, f = u * (2 / c - 1), p = f * f, v = Math.PI * this.R * (o * (u - p) + Math.sqrt(l * (u - p) * (u - p) - (p + l) * (d - p))) / (p + l);
  i < 0 && (v = -v), n = this.x0 + v;
  var g = l + u;
  return v = Math.PI * this.R * (f * g - o * Math.sqrt((p + l) * (l + 1) - g * g)) / (p + l), t >= 0 ? r = this.y0 + v : r = this.y0 - v, s.x = n, s.y = r, s;
}
function kpe(s) {
  var e, t, i, n, r, a, o, l, c, h, u, d, f;
  return s.x -= this.x0, s.y -= this.y0, u = Math.PI * this.R, i = s.x / u, n = s.y / u, r = i * i + n * n, a = -Math.abs(n) * (1 + r), o = a - 2 * n * n + i * i, l = -2 * a + 1 + 2 * n * n + r * r, f = n * n / l + (2 * o * o * o / l / l / l - 9 * a * o / l / l) / 27, c = (a - o * o / 3 / l) / l, h = 2 * Math.sqrt(-c / 3), u = 3 * f / c / h, Math.abs(u) > 1 && (u >= 0 ? u = 1 : u = -1), d = Math.acos(u) / 3, s.y >= 0 ? t = (-h * Math.cos(d + Math.PI / 3) - o / 3 / l) * Math.PI : t = -(-h * Math.cos(d + Math.PI / 3) - o / 3 / l) * Math.PI, Math.abs(i) < Dt ? e = this.long0 : e = Lt(this.long0 + Math.PI * (r - 1 + Math.sqrt(1 + 2 * (i * i - n * n) + r * r)) / 2 / i), s.x = e, s.y = t, s;
}
var Gpe = ["Van_der_Grinten_I", "VanDerGrinten", "vandg"];
const Hpe = {
  init: Upe,
  forward: zpe,
  inverse: kpe,
  names: Gpe
};
function Vpe() {
  this.sin_p12 = Math.sin(this.lat0), this.cos_p12 = Math.cos(this.lat0);
}
function Wpe(s) {
  var e = s.x, t = s.y, i = Math.sin(s.y), n = Math.cos(s.y), r = Lt(e - this.long0), a, o, l, c, h, u, d, f, p, v, g, m, x, y, A, b, M, w, C, T, E, P, L;
  return this.sphere ? Math.abs(this.sin_p12 - 1) <= Dt ? (s.x = this.x0 + this.a * (xt - t) * Math.sin(r), s.y = this.y0 - this.a * (xt - t) * Math.cos(r), s) : Math.abs(this.sin_p12 + 1) <= Dt ? (s.x = this.x0 + this.a * (xt + t) * Math.sin(r), s.y = this.y0 + this.a * (xt + t) * Math.cos(r), s) : (w = this.sin_p12 * i + this.cos_p12 * n * Math.cos(r), b = Math.acos(w), M = b ? b / Math.sin(b) : 1, s.x = this.x0 + this.a * M * n * Math.sin(r), s.y = this.y0 + this.a * M * (this.cos_p12 * i - this.sin_p12 * n * Math.cos(r)), s) : (a = B2(this.es), o = F2(this.es), l = U2(this.es), c = z2(this.es), Math.abs(this.sin_p12 - 1) <= Dt ? (h = this.a * xr(a, o, l, c, xt), u = this.a * xr(a, o, l, c, t), s.x = this.x0 + (h - u) * Math.sin(r), s.y = this.y0 - (h - u) * Math.cos(r), s) : Math.abs(this.sin_p12 + 1) <= Dt ? (h = this.a * xr(a, o, l, c, xt), u = this.a * xr(a, o, l, c, t), s.x = this.x0 + (h + u) * Math.sin(r), s.y = this.y0 + (h + u) * Math.cos(r), s) : (d = i / n, f = Lp(this.a, this.e, this.sin_p12), p = Lp(this.a, this.e, i), v = Math.atan((1 - this.es) * d + this.es * f * this.sin_p12 / (p * n)), g = Math.atan2(Math.sin(r), this.cos_p12 * Math.tan(v) - this.sin_p12 * Math.cos(r)), g === 0 ? C = Math.asin(this.cos_p12 * Math.sin(v) - this.sin_p12 * Math.cos(v)) : Math.abs(Math.abs(g) - Math.PI) <= Dt ? C = -Math.asin(this.cos_p12 * Math.sin(v) - this.sin_p12 * Math.cos(v)) : C = Math.asin(Math.sin(r) * Math.cos(v) / Math.sin(g)), m = this.e * this.sin_p12 / Math.sqrt(1 - this.es), x = this.e * this.cos_p12 * Math.cos(g) / Math.sqrt(1 - this.es), y = m * x, A = x * x, T = C * C, E = T * C, P = E * C, L = P * C, b = f * C * (1 - T * A * (1 - A) / 6 + E / 8 * y * (1 - 2 * A) + P / 120 * (A * (4 - 7 * A) - 3 * m * m * (1 - 7 * A)) - L / 48 * y), s.x = this.x0 + b * Math.sin(g), s.y = this.y0 + b * Math.cos(g), s));
}
function Xpe(s) {
  s.x -= this.x0, s.y -= this.y0;
  var e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g, m, x, y, A, b, M, w, C;
  return this.sphere ? (e = Math.sqrt(s.x * s.x + s.y * s.y), e > 2 * xt * this.a ? void 0 : (t = e / this.a, i = Math.sin(t), n = Math.cos(t), r = this.long0, Math.abs(e) <= Dt ? a = this.lat0 : (a = Zc(n * this.sin_p12 + s.y * i * this.cos_p12 / e), o = Math.abs(this.lat0) - xt, Math.abs(o) <= Dt ? this.lat0 >= 0 ? r = Lt(this.long0 + Math.atan2(s.x, -s.y)) : r = Lt(this.long0 - Math.atan2(-s.x, s.y)) : r = Lt(this.long0 + Math.atan2(s.x * i, e * this.cos_p12 * n - s.y * this.sin_p12 * i))), s.x = r, s.y = a, s)) : (l = B2(this.es), c = F2(this.es), h = U2(this.es), u = z2(this.es), Math.abs(this.sin_p12 - 1) <= Dt ? (d = this.a * xr(l, c, h, u, xt), e = Math.sqrt(s.x * s.x + s.y * s.y), f = d - e, a = Px(f / this.a, l, c, h, u), r = Lt(this.long0 + Math.atan2(s.x, -1 * s.y)), s.x = r, s.y = a, s) : Math.abs(this.sin_p12 + 1) <= Dt ? (d = this.a * xr(l, c, h, u, xt), e = Math.sqrt(s.x * s.x + s.y * s.y), f = e - d, a = Px(f / this.a, l, c, h, u), r = Lt(this.long0 + Math.atan2(s.x, s.y)), s.x = r, s.y = a, s) : (e = Math.sqrt(s.x * s.x + s.y * s.y), g = Math.atan2(s.x, s.y), p = Lp(this.a, this.e, this.sin_p12), m = Math.cos(g), x = this.e * this.cos_p12 * m, y = -x * x / (1 - this.es), A = 3 * this.es * (1 - y) * this.sin_p12 * this.cos_p12 * m / (1 - this.es), b = e / p, M = b - y * (1 + y) * Math.pow(b, 3) / 6 - A * (1 + 3 * y) * Math.pow(b, 4) / 24, w = 1 - y * M * M / 2 - b * M * M * M / 6, v = Math.asin(this.sin_p12 * Math.cos(M) + this.cos_p12 * Math.sin(M) * m), r = Lt(this.long0 + Math.asin(Math.sin(g) * Math.sin(M) / Math.cos(v))), C = Math.sin(v), a = Math.atan2((C - this.es * w * this.sin_p12) * Math.tan(v), C * (1 - this.es)), s.x = r, s.y = a, s));
}
var jpe = ["Azimuthal_Equidistant", "aeqd"];
const qpe = {
  init: Vpe,
  forward: Wpe,
  inverse: Xpe,
  names: jpe
};
function Ype() {
  this.sin_p14 = Math.sin(this.lat0), this.cos_p14 = Math.cos(this.lat0);
}
function Zpe(s) {
  var e, t, i, n, r, a, o, l, c = s.x, h = s.y;
  return i = Lt(c - this.long0), e = Math.sin(h), t = Math.cos(h), n = Math.cos(i), a = this.sin_p14 * e + this.cos_p14 * t * n, r = 1, (a > 0 || Math.abs(a) <= Dt) && (o = this.a * r * t * Math.sin(i), l = this.y0 + this.a * r * (this.cos_p14 * e - this.sin_p14 * t * n)), s.x = o, s.y = l, s;
}
function Kpe(s) {
  var e, t, i, n, r, a, o;
  return s.x -= this.x0, s.y -= this.y0, e = Math.sqrt(s.x * s.x + s.y * s.y), t = Zc(e / this.a), i = Math.sin(t), n = Math.cos(t), a = this.long0, Math.abs(e) <= Dt ? (o = this.lat0, s.x = a, s.y = o, s) : (o = Zc(n * this.sin_p14 + s.y * i * this.cos_p14 / e), r = Math.abs(this.lat0) - xt, Math.abs(r) <= Dt ? (this.lat0 >= 0 ? a = Lt(this.long0 + Math.atan2(s.x, -s.y)) : a = Lt(this.long0 - Math.atan2(-s.x, s.y)), s.x = a, s.y = o, s) : (a = Lt(this.long0 + Math.atan2(s.x * i, e * this.cos_p14 * n - s.y * this.sin_p14 * i)), s.x = a, s.y = o, s));
}
var Jpe = ["ortho"];
const Qpe = {
  init: Ype,
  forward: Zpe,
  inverse: Kpe,
  names: Jpe
};
var Fn = {
  FRONT: 1,
  RIGHT: 2,
  BACK: 3,
  LEFT: 4,
  TOP: 5,
  BOTTOM: 6
}, en = {
  AREA_0: 1,
  AREA_1: 2,
  AREA_2: 3,
  AREA_3: 4
};
function $pe() {
  this.x0 = this.x0 || 0, this.y0 = this.y0 || 0, this.lat0 = this.lat0 || 0, this.long0 = this.long0 || 0, this.lat_ts = this.lat_ts || 0, this.title = this.title || "Quadrilateralized Spherical Cube", this.lat0 >= xt - $i / 2 ? this.face = Fn.TOP : this.lat0 <= -(xt - $i / 2) ? this.face = Fn.BOTTOM : Math.abs(this.long0) <= $i ? this.face = Fn.FRONT : Math.abs(this.long0) <= xt + $i ? this.face = this.long0 > 0 ? Fn.RIGHT : Fn.LEFT : this.face = Fn.BACK, this.es !== 0 && (this.one_minus_f = 1 - (this.a - this.b) / this.a, this.one_minus_f_squared = this.one_minus_f * this.one_minus_f);
}
function eme(s) {
  var e = { x: 0, y: 0 }, t, i, n, r, a, o, l = { value: 0 };
  if (s.x -= this.long0, this.es !== 0 ? t = Math.atan(this.one_minus_f_squared * Math.tan(s.y)) : t = s.y, i = s.x, this.face === Fn.TOP)
    r = xt - t, i >= $i && i <= xt + $i ? (l.value = en.AREA_0, n = i - xt) : i > xt + $i || i <= -(xt + $i) ? (l.value = en.AREA_1, n = i > 0 ? i - Jn : i + Jn) : i > -(xt + $i) && i <= -$i ? (l.value = en.AREA_2, n = i + xt) : (l.value = en.AREA_3, n = i);
  else if (this.face === Fn.BOTTOM)
    r = xt + t, i >= $i && i <= xt + $i ? (l.value = en.AREA_0, n = -i + xt) : i < $i && i >= -$i ? (l.value = en.AREA_1, n = -i) : i < -$i && i >= -(xt + $i) ? (l.value = en.AREA_2, n = -i - xt) : (l.value = en.AREA_3, n = i > 0 ? -i + Jn : -i - Jn);
  else {
    var c, h, u, d, f, p, v;
    this.face === Fn.RIGHT ? i = Yf(i, +xt) : this.face === Fn.BACK ? i = Yf(i, +Jn) : this.face === Fn.LEFT && (i = Yf(i, -xt)), d = Math.sin(t), f = Math.cos(t), p = Math.sin(i), v = Math.cos(i), c = f * v, h = f * p, u = d, this.face === Fn.FRONT ? (r = Math.acos(c), n = x3(r, u, h, l)) : this.face === Fn.RIGHT ? (r = Math.acos(h), n = x3(r, u, -c, l)) : this.face === Fn.BACK ? (r = Math.acos(-c), n = x3(r, u, -h, l)) : this.face === Fn.LEFT ? (r = Math.acos(-h), n = x3(r, u, c, l)) : (r = n = 0, l.value = en.AREA_0);
  }
  return o = Math.atan(12 / Jn * (n + Math.acos(Math.sin(n) * Math.cos($i)) - xt)), a = Math.sqrt((1 - Math.cos(r)) / (Math.cos(o) * Math.cos(o)) / (1 - Math.cos(Math.atan(1 / Math.cos(n))))), l.value === en.AREA_1 ? o += xt : l.value === en.AREA_2 ? o += Jn : l.value === en.AREA_3 && (o += 1.5 * Jn), e.x = a * Math.cos(o), e.y = a * Math.sin(o), e.x = e.x * this.a + this.x0, e.y = e.y * this.a + this.y0, s.x = e.x, s.y = e.y, s;
}
function tme(s) {
  var e = { lam: 0, phi: 0 }, t, i, n, r, a, o, l, c, h, u = { value: 0 };
  if (s.x = (s.x - this.x0) / this.a, s.y = (s.y - this.y0) / this.a, i = Math.atan(Math.sqrt(s.x * s.x + s.y * s.y)), t = Math.atan2(s.y, s.x), s.x >= 0 && s.x >= Math.abs(s.y) ? u.value = en.AREA_0 : s.y >= 0 && s.y >= Math.abs(s.x) ? (u.value = en.AREA_1, t -= xt) : s.x < 0 && -s.x >= Math.abs(s.y) ? (u.value = en.AREA_2, t = t < 0 ? t + Jn : t - Jn) : (u.value = en.AREA_3, t += xt), h = Jn / 12 * Math.tan(t), a = Math.sin(h) / (Math.cos(h) - 1 / Math.sqrt(2)), o = Math.atan(a), n = Math.cos(t), r = Math.tan(i), l = 1 - n * n * r * r * (1 - Math.cos(Math.atan(1 / Math.cos(o)))), l < -1 ? l = -1 : l > 1 && (l = 1), this.face === Fn.TOP)
    c = Math.acos(l), e.phi = xt - c, u.value === en.AREA_0 ? e.lam = o + xt : u.value === en.AREA_1 ? e.lam = o < 0 ? o + Jn : o - Jn : u.value === en.AREA_2 ? e.lam = o - xt : e.lam = o;
  else if (this.face === Fn.BOTTOM)
    c = Math.acos(l), e.phi = c - xt, u.value === en.AREA_0 ? e.lam = -o + xt : u.value === en.AREA_1 ? e.lam = -o : u.value === en.AREA_2 ? e.lam = -o - xt : e.lam = o < 0 ? -o - Jn : -o + Jn;
  else {
    var d, f, p;
    d = l, h = d * d, h >= 1 ? p = 0 : p = Math.sqrt(1 - h) * Math.sin(o), h += p * p, h >= 1 ? f = 0 : f = Math.sqrt(1 - h), u.value === en.AREA_1 ? (h = f, f = -p, p = h) : u.value === en.AREA_2 ? (f = -f, p = -p) : u.value === en.AREA_3 && (h = f, f = p, p = -h), this.face === Fn.RIGHT ? (h = d, d = -f, f = h) : this.face === Fn.BACK ? (d = -d, f = -f) : this.face === Fn.LEFT && (h = d, d = f, f = -h), e.phi = Math.acos(-p) - xt, e.lam = Math.atan2(f, d), this.face === Fn.RIGHT ? e.lam = Yf(e.lam, -xt) : this.face === Fn.BACK ? e.lam = Yf(e.lam, -Jn) : this.face === Fn.LEFT && (e.lam = Yf(e.lam, +xt));
  }
  if (this.es !== 0) {
    var v, g, m;
    v = e.phi < 0 ? 1 : 0, g = Math.tan(e.phi), m = this.b / Math.sqrt(g * g + this.one_minus_f_squared), e.phi = Math.atan(Math.sqrt(this.a * this.a - m * m) / (this.one_minus_f * m)), v && (e.phi = -e.phi);
  }
  return e.lam += this.long0, s.x = e.lam, s.y = e.phi, s;
}
function x3(s, e, t, i) {
  var n;
  return s < Dt ? (i.value = en.AREA_0, n = 0) : (n = Math.atan2(e, t), Math.abs(n) <= $i ? i.value = en.AREA_0 : n > $i && n <= xt + $i ? (i.value = en.AREA_1, n -= xt) : n > xt + $i || n <= -(xt + $i) ? (i.value = en.AREA_2, n = n >= 0 ? n - Jn : n + Jn) : (i.value = en.AREA_3, n += xt)), n;
}
function Yf(s, e) {
  var t = s + e;
  return t < -Jn ? t += zg : t > +Jn && (t -= zg), t;
}
var ime = ["Quadrilateralized Spherical Cube", "Quadrilateralized_Spherical_Cube", "qsc"];
const nme = {
  init: $pe,
  forward: eme,
  inverse: tme,
  names: ime
};
var $b = [
  [1, 22199e-21, -715515e-10, 31103e-10],
  [0.9986, -482243e-9, -24897e-9, -13309e-10],
  [0.9954, -83103e-8, -448605e-10, -986701e-12],
  [0.99, -135364e-8, -59661e-9, 36777e-10],
  [0.9822, -167442e-8, -449547e-11, -572411e-11],
  [0.973, -214868e-8, -903571e-10, 18736e-12],
  [0.96, -305085e-8, -900761e-10, 164917e-11],
  [0.9427, -382792e-8, -653386e-10, -26154e-10],
  [0.9216, -467746e-8, -10457e-8, 481243e-11],
  [0.8962, -536223e-8, -323831e-10, -543432e-11],
  [0.8679, -609363e-8, -113898e-9, 332484e-11],
  [0.835, -698325e-8, -640253e-10, 934959e-12],
  [0.7986, -755338e-8, -500009e-10, 935324e-12],
  [0.7597, -798324e-8, -35971e-9, -227626e-11],
  [0.7186, -851367e-8, -701149e-10, -86303e-10],
  [0.6732, -986209e-8, -199569e-9, 191974e-10],
  [0.6213, -0.010418, 883923e-10, 624051e-11],
  [0.5722, -906601e-8, 182e-6, 624051e-11],
  [0.5322, -677797e-8, 275608e-9, 624051e-11]
], u1 = [
  [-520417e-23, 0.0124, 121431e-23, -845284e-16],
  [0.062, 0.0124, -126793e-14, 422642e-15],
  [0.124, 0.0124, 507171e-14, -160604e-14],
  [0.186, 0.0123999, -190189e-13, 600152e-14],
  [0.248, 0.0124002, 710039e-13, -224e-10],
  [0.31, 0.0123992, -264997e-12, 835986e-13],
  [0.372, 0.0124029, 988983e-12, -311994e-12],
  [0.434, 0.0123893, -369093e-11, -435621e-12],
  [0.4958, 0.0123198, -102252e-10, -345523e-12],
  [0.5571, 0.0121916, -154081e-10, -582288e-12],
  [0.6176, 0.0119938, -241424e-10, -525327e-12],
  [0.6769, 0.011713, -320223e-10, -516405e-12],
  [0.7346, 0.0113541, -397684e-10, -609052e-12],
  [0.7903, 0.0109107, -489042e-10, -104739e-11],
  [0.8435, 0.0103431, -64615e-9, -140374e-14],
  [0.8936, 969686e-8, -64636e-9, -8547e-9],
  [0.9394, 840947e-8, -192841e-9, -42106e-10],
  [0.9761, 616527e-8, -256e-6, -42106e-10],
  [1, 328947e-8, -319159e-9, -42106e-10]
], TF = 0.8487, CF = 1.3523, _F = Ko / 5, sme = 1 / _F, _f = 18, Rx = function(s, e) {
  return s[0] + e * (s[1] + e * (s[2] + e * s[3]));
}, rme = function(s, e) {
  return s[1] + e * (2 * s[2] + e * 3 * s[3]);
};
function ame(s, e, t, i) {
  for (var n = e; i; --i) {
    var r = s(n);
    if (n -= r, Math.abs(r) < t)
      break;
  }
  return n;
}
function ome() {
  this.x0 = this.x0 || 0, this.y0 = this.y0 || 0, this.long0 = this.long0 || 0, this.es = 0, this.title = this.title || "Robinson";
}
function lme(s) {
  var e = Lt(s.x - this.long0), t = Math.abs(s.y), i = Math.floor(t * _F);
  i < 0 ? i = 0 : i >= _f && (i = _f - 1), t = Ko * (t - sme * i);
  var n = {
    x: Rx($b[i], t) * e,
    y: Rx(u1[i], t)
  };
  return s.y < 0 && (n.y = -n.y), n.x = n.x * this.a * TF + this.x0, n.y = n.y * this.a * CF + this.y0, n;
}
function cme(s) {
  var e = {
    x: (s.x - this.x0) / (this.a * TF),
    y: Math.abs(s.y - this.y0) / (this.a * CF)
  };
  if (e.y >= 1)
    e.x /= $b[_f][0], e.y = s.y < 0 ? -xt : xt;
  else {
    var t = Math.floor(e.y * _f);
    for (t < 0 ? t = 0 : t >= _f && (t = _f - 1); ; )
      if (u1[t][0] > e.y)
        --t;
      else if (u1[t + 1][0] <= e.y)
        ++t;
      else
        break;
    var i = u1[t], n = 5 * (e.y - i[0]) / (u1[t + 1][0] - i[0]);
    n = ame(function(r) {
      return (Rx(i, r) - e.y) / rme(i, r);
    }, n, Dt, 100), e.x /= Rx($b[t], n), e.y = (5 * t + n) * zs, s.y < 0 && (e.y = -e.y);
  }
  return e.x = Lt(e.x + this.long0), e;
}
var hme = ["Robinson", "robin"];
const ume = {
  init: ome,
  forward: lme,
  inverse: cme,
  names: hme
};
function dme() {
  this.name = "geocent";
}
function fme(s) {
  var e = mF(s, this.es, this.a);
  return e;
}
function pme(s) {
  var e = gF(s, this.es, this.a, this.b);
  return e;
}
var mme = ["Geocentric", "geocentric", "geocent", "Geocent"];
const gme = {
  init: dme,
  forward: fme,
  inverse: pme,
  names: mme
};
var $s = {
  N_POLE: 0,
  S_POLE: 1,
  EQUIT: 2,
  OBLIQ: 3
}, zm = {
  h: { def: 1e5, num: !0 },
  // default is Karman line, no default in PROJ.7
  azi: { def: 0, num: !0, degrees: !0 },
  // default is North
  tilt: { def: 0, num: !0, degrees: !0 },
  // default is Nadir
  long0: { def: 0, num: !0 },
  // default is Greenwich, conversion to rad is automatic
  lat0: { def: 0, num: !0 }
  // default is Equator, conversion to rad is automatic
};
function vme() {
  if (Object.keys(zm).forEach((function(t) {
    if (typeof this[t] > "u")
      this[t] = zm[t].def;
    else {
      if (zm[t].num && isNaN(this[t]))
        throw new Error("Invalid parameter value, must be numeric " + t + " = " + this[t]);
      zm[t].num && (this[t] = parseFloat(this[t]));
    }
    zm[t].degrees && (this[t] = this[t] * zs);
  }).bind(this)), Math.abs(Math.abs(this.lat0) - xt) < Dt ? this.mode = this.lat0 < 0 ? $s.S_POLE : $s.N_POLE : Math.abs(this.lat0) < Dt ? this.mode = $s.EQUIT : (this.mode = $s.OBLIQ, this.sinph0 = Math.sin(this.lat0), this.cosph0 = Math.cos(this.lat0)), this.pn1 = this.h / this.a, this.pn1 <= 0 || this.pn1 > 1e10)
    throw new Error("Invalid height");
  this.p = 1 + this.pn1, this.rp = 1 / this.p, this.h1 = 1 / this.pn1, this.pfact = (this.p + 1) * this.h1, this.es = 0;
  var s = this.tilt, e = this.azi;
  this.cg = Math.cos(e), this.sg = Math.sin(e), this.cw = Math.cos(s), this.sw = Math.sin(s);
}
function yme(s) {
  s.x -= this.long0;
  var e = Math.sin(s.y), t = Math.cos(s.y), i = Math.cos(s.x), n, r;
  switch (this.mode) {
    case $s.OBLIQ:
      r = this.sinph0 * e + this.cosph0 * t * i;
      break;
    case $s.EQUIT:
      r = t * i;
      break;
    case $s.S_POLE:
      r = -e;
      break;
    case $s.N_POLE:
      r = e;
      break;
  }
  switch (r = this.pn1 / (this.p - r), n = r * t * Math.sin(s.x), this.mode) {
    case $s.OBLIQ:
      r *= this.cosph0 * e - this.sinph0 * t * i;
      break;
    case $s.EQUIT:
      r *= e;
      break;
    case $s.N_POLE:
      r *= -(t * i);
      break;
    case $s.S_POLE:
      r *= t * i;
      break;
  }
  var a, o;
  return a = r * this.cg + n * this.sg, o = 1 / (a * this.sw * this.h1 + this.cw), n = (n * this.cg - r * this.sg) * this.cw * o, r = a * o, s.x = n * this.a, s.y = r * this.a, s;
}
function xme(s) {
  s.x /= this.a, s.y /= this.a;
  var e = { x: s.x, y: s.y }, t, i, n;
  n = 1 / (this.pn1 - s.y * this.sw), t = this.pn1 * s.x * n, i = this.pn1 * s.y * this.cw * n, s.x = t * this.cg + i * this.sg, s.y = i * this.cg - t * this.sg;
  var r = Or(s.x, s.y);
  if (Math.abs(r) < Dt)
    e.x = 0, e.y = s.y;
  else {
    var a, o;
    switch (o = 1 - r * r * this.pfact, o = (this.p - Math.sqrt(o)) / (this.pn1 / r + r / this.pn1), a = Math.sqrt(1 - o * o), this.mode) {
      case $s.OBLIQ:
        e.y = Math.asin(a * this.sinph0 + s.y * o * this.cosph0 / r), s.y = (a - this.sinph0 * Math.sin(e.y)) * r, s.x *= o * this.cosph0;
        break;
      case $s.EQUIT:
        e.y = Math.asin(s.y * o / r), s.y = a * r, s.x *= o;
        break;
      case $s.N_POLE:
        e.y = Math.asin(a), s.y = -s.y;
        break;
      case $s.S_POLE:
        e.y = -Math.asin(a);
        break;
    }
    e.x = Math.atan2(s.x, s.y);
  }
  return s.x = e.x + this.long0, s.y = e.y, s;
}
var Ame = ["Tilted_Perspective", "tpers"];
const bme = {
  init: vme,
  forward: yme,
  inverse: xme,
  names: Ame
};
function Mme() {
  if (this.flip_axis = this.sweep === "x" ? 1 : 0, this.h = Number(this.h), this.radius_g_1 = this.h / this.a, this.radius_g_1 <= 0 || this.radius_g_1 > 1e10)
    throw new Error();
  if (this.radius_g = 1 + this.radius_g_1, this.C = this.radius_g * this.radius_g - 1, this.es !== 0) {
    var s = 1 - this.es, e = 1 / s;
    this.radius_p = Math.sqrt(s), this.radius_p2 = s, this.radius_p_inv2 = e, this.shape = "ellipse";
  } else
    this.radius_p = 1, this.radius_p2 = 1, this.radius_p_inv2 = 1, this.shape = "sphere";
  this.title || (this.title = "Geostationary Satellite View");
}
function Sme(s) {
  var e = s.x, t = s.y, i, n, r, a;
  if (e = e - this.long0, this.shape === "ellipse") {
    t = Math.atan(this.radius_p2 * Math.tan(t));
    var o = this.radius_p / Or(this.radius_p * Math.cos(t), Math.sin(t));
    if (n = o * Math.cos(e) * Math.cos(t), r = o * Math.sin(e) * Math.cos(t), a = o * Math.sin(t), (this.radius_g - n) * n - r * r - a * a * this.radius_p_inv2 < 0)
      return s.x = Number.NaN, s.y = Number.NaN, s;
    i = this.radius_g - n, this.flip_axis ? (s.x = this.radius_g_1 * Math.atan(r / Or(a, i)), s.y = this.radius_g_1 * Math.atan(a / i)) : (s.x = this.radius_g_1 * Math.atan(r / i), s.y = this.radius_g_1 * Math.atan(a / Or(r, i)));
  } else
    this.shape === "sphere" && (i = Math.cos(t), n = Math.cos(e) * i, r = Math.sin(e) * i, a = Math.sin(t), i = this.radius_g - n, this.flip_axis ? (s.x = this.radius_g_1 * Math.atan(r / Or(a, i)), s.y = this.radius_g_1 * Math.atan(a / i)) : (s.x = this.radius_g_1 * Math.atan(r / i), s.y = this.radius_g_1 * Math.atan(a / Or(r, i))));
  return s.x = s.x * this.a, s.y = s.y * this.a, s;
}
function wme(s) {
  var e = -1, t = 0, i = 0, n, r, a, o;
  if (s.x = s.x / this.a, s.y = s.y / this.a, this.shape === "ellipse") {
    this.flip_axis ? (i = Math.tan(s.y / this.radius_g_1), t = Math.tan(s.x / this.radius_g_1) * Or(1, i)) : (t = Math.tan(s.x / this.radius_g_1), i = Math.tan(s.y / this.radius_g_1) * Or(1, t));
    var l = i / this.radius_p;
    if (n = t * t + l * l + e * e, r = 2 * this.radius_g * e, a = r * r - 4 * n * this.C, a < 0)
      return s.x = Number.NaN, s.y = Number.NaN, s;
    o = (-r - Math.sqrt(a)) / (2 * n), e = this.radius_g + o * e, t *= o, i *= o, s.x = Math.atan2(t, e), s.y = Math.atan(i * Math.cos(s.x) / e), s.y = Math.atan(this.radius_p_inv2 * Math.tan(s.y));
  } else if (this.shape === "sphere") {
    if (this.flip_axis ? (i = Math.tan(s.y / this.radius_g_1), t = Math.tan(s.x / this.radius_g_1) * Math.sqrt(1 + i * i)) : (t = Math.tan(s.x / this.radius_g_1), i = Math.tan(s.y / this.radius_g_1) * Math.sqrt(1 + t * t)), n = t * t + i * i + e * e, r = 2 * this.radius_g * e, a = r * r - 4 * n * this.C, a < 0)
      return s.x = Number.NaN, s.y = Number.NaN, s;
    o = (-r - Math.sqrt(a)) / (2 * n), e = this.radius_g + o * e, t *= o, i *= o, s.x = Math.atan2(t, e), s.y = Math.atan(i * Math.cos(s.x) / e);
  }
  return s.x = s.x + this.long0, s;
}
var Eme = ["Geostationary Satellite View", "Geostationary_Satellite", "geos"];
const Tme = {
  init: Mme,
  forward: Sme,
  inverse: wme,
  names: Eme
};
var U1 = 1.340264, z1 = -0.081106, k1 = 893e-6, G1 = 3796e-6, Dx = Math.sqrt(3) / 2;
function Cme() {
  this.es = 0, this.long0 = this.long0 !== void 0 ? this.long0 : 0;
}
function _me(s) {
  var e = Lt(s.x - this.long0), t = s.y, i = Math.asin(Dx * Math.sin(t)), n = i * i, r = n * n * n;
  return s.x = e * Math.cos(i) / (Dx * (U1 + 3 * z1 * n + r * (7 * k1 + 9 * G1 * n))), s.y = i * (U1 + z1 * n + r * (k1 + G1 * n)), s.x = this.a * s.x + this.x0, s.y = this.a * s.y + this.y0, s;
}
function Pme(s) {
  s.x = (s.x - this.x0) / this.a, s.y = (s.y - this.y0) / this.a;
  var e = 1e-9, t = 12, i = s.y, n, r, a, o, l, c;
  for (c = 0; c < t && (n = i * i, r = n * n * n, a = i * (U1 + z1 * n + r * (k1 + G1 * n)) - s.y, o = U1 + 3 * z1 * n + r * (7 * k1 + 9 * G1 * n), i -= l = a / o, !(Math.abs(l) < e)); ++c)
    ;
  return n = i * i, r = n * n * n, s.x = Dx * s.x * (U1 + 3 * z1 * n + r * (7 * k1 + 9 * G1 * n)) / Math.cos(i), s.y = Math.asin(Math.sin(i) / Dx), s.x = Lt(s.x + this.long0), s;
}
var Rme = ["eqearth", "Equal Earth", "Equal_Earth"];
const Dme = {
  init: Cme,
  forward: _me,
  inverse: Pme,
  names: Rme
};
var Hg = 1e-10;
function Lme() {
  var s;
  if (this.phi1 = this.lat1, Math.abs(this.phi1) < Hg)
    throw new Error();
  this.es ? (this.en = Mw(this.es), this.m1 = im(
    this.phi1,
    this.am1 = Math.sin(this.phi1),
    s = Math.cos(this.phi1),
    this.en
  ), this.am1 = s / (Math.sqrt(1 - this.es * this.am1 * this.am1) * this.am1), this.inverse = Ome, this.forward = Ime) : (Math.abs(this.phi1) + Hg >= xt ? this.cphi1 = 0 : this.cphi1 = 1 / Math.tan(this.phi1), this.inverse = Bme, this.forward = Nme);
}
function Ime(s) {
  var e = Lt(s.x - (this.long0 || 0)), t = s.y, i, n, r;
  return i = this.am1 + this.m1 - im(t, n = Math.sin(t), r = Math.cos(t), this.en), n = r * e / (i * Math.sqrt(1 - this.es * n * n)), s.x = i * Math.sin(n), s.y = this.am1 - i * Math.cos(n), s.x = this.a * s.x + (this.x0 || 0), s.y = this.a * s.y + (this.y0 || 0), s;
}
function Ome(s) {
  s.x = (s.x - (this.x0 || 0)) / this.a, s.y = (s.y - (this.y0 || 0)) / this.a;
  var e, t, i, n;
  if (t = Or(s.x, s.y = this.am1 - s.y), n = Sw(this.am1 + this.m1 - t, this.es, this.en), (e = Math.abs(n)) < xt)
    e = Math.sin(n), i = t * Math.atan2(s.x, s.y) * Math.sqrt(1 - this.es * e * e) / Math.cos(n);
  else if (Math.abs(e - xt) <= Hg)
    i = 0;
  else
    throw new Error();
  return s.x = Lt(i + (this.long0 || 0)), s.y = ch(n), s;
}
function Nme(s) {
  var e = Lt(s.x - (this.long0 || 0)), t = s.y, i, n;
  return n = this.cphi1 + this.phi1 - t, Math.abs(n) > Hg ? (s.x = n * Math.sin(i = e * Math.cos(t) / n), s.y = this.cphi1 - n * Math.cos(i)) : s.x = s.y = 0, s.x = this.a * s.x + (this.x0 || 0), s.y = this.a * s.y + (this.y0 || 0), s;
}
function Bme(s) {
  s.x = (s.x - (this.x0 || 0)) / this.a, s.y = (s.y - (this.y0 || 0)) / this.a;
  var e, t, i = Or(s.x, s.y = this.cphi1 - s.y);
  if (t = this.cphi1 + this.phi1 - i, Math.abs(t) > xt)
    throw new Error();
  return Math.abs(Math.abs(t) - xt) <= Hg ? e = 0 : e = i * Math.atan2(s.x, s.y) / Math.cos(t), s.x = Lt(e + (this.long0 || 0)), s.y = ch(t), s;
}
var Fme = ["bonne", "Bonne (Werner lat_1=90)"];
const Ume = {
  init: Lme,
  names: Fme
};
function zme(s) {
  s.Proj.projections.add(Yy), s.Proj.projections.add(Zy), s.Proj.projections.add(Ide), s.Proj.projections.add(Vde), s.Proj.projections.add(Zde), s.Proj.projections.add(efe), s.Proj.projections.add(afe), s.Proj.projections.add(ufe), s.Proj.projections.add(gfe), s.Proj.projections.add(bfe), s.Proj.projections.add(Ufe), s.Proj.projections.add(Wfe), s.Proj.projections.add(Zfe), s.Proj.projections.add(tpe), s.Proj.projections.add(ape), s.Proj.projections.add(upe), s.Proj.projections.add(gpe), s.Proj.projections.add(bpe), s.Proj.projections.add(Cpe), s.Proj.projections.add(Lpe), s.Proj.projections.add(Fpe), s.Proj.projections.add(Hpe), s.Proj.projections.add(qpe), s.Proj.projections.add(Qpe), s.Proj.projections.add(nme), s.Proj.projections.add(ume), s.Proj.projections.add(gme), s.Proj.projections.add(bme), s.Proj.projections.add(Tme), s.Proj.projections.add(Dme), s.Proj.projections.add(Ume);
}
Ar.defaultDatum = "WGS84";
Ar.Proj = i0;
Ar.WGS84 = new Ar.Proj("WGS84");
Ar.Point = Dp;
Ar.toPoint = vF;
Ar.defs = mr;
Ar.nadgrid = Nue;
Ar.transform = _x;
Ar.mgrs = Jue;
Ar.version = "__VERSION__";
zme(Ar);
class PF extends mt {
  constructor(t, i = {}) {
    super();
    S(this, "params", {
      cylinder: {
        height: 5,
        radius: 0.5
      },
      sphere: {
        radius: 2.5,
        color: "#8476FF"
      }
    });
    this.add(this._initCylinderMesh(t)), this.add(this._initSphereMesh(t, i.sphereColor ?? this.params.sphere.color)), this.add(this._initTextMesh(t, i)), this.userData.referenceName = t;
  }
  _initCylinderMesh(t) {
    const i = new Ei(
      this.params.cylinder.radius,
      this.params.cylinder.radius,
      this.params.cylinder.height,
      32
    ), n = new kt({ color: 16777215 }), r = new Re(i, n);
    return r.userData.referenceName = t, r;
  }
  _initSphereMesh(t, i) {
    const n = new Hs(this.params.sphere.radius), r = new or({ color: i, roughness: 0 }), a = new Re(n, r);
    return a.position.set(0, this.params.cylinder.height, 0), a.userData.referenceName = t, a;
  }
  _initTextMesh(t, i) {
    const n = i.size ?? 50, r = document.createElement("canvas"), a = new ar({
      map: new zc(r),
      depthTest: !1,
      transparent: !0
    }), o = r.getContext("2d");
    if (r.width = 256, r.height = 256, o) {
      o.font = "30px Arial", o.fillStyle = "white";
      const c = i.text ? i.text : `参考点${Number(t) + 1}`;
      o.fillText(c, 70, 50);
    }
    const l = new wn(a);
    return l.scale.set(n, n, 1), l.position.set(0, this.params.cylinder.height * 1.2, 0), l.userData.referenceName = t, l;
  }
}
const BA = {
  WGS84: {
    source: "+proj=utm +zone=21 +datum=WGS84 +units=m +no_defs",
    target: "+proj=longlat +datum=WGS84 +units=degrees +no_defs"
  },
  GCJ02: {
    source: "+proj=utm +zone=21 +datum=GCJ02 +units=m +no_defs",
    target: "+proj=longlat +datum=WGS84 +units=degrees +no_defs"
  },
  BD09: {
    source: "+proj=utm +zone=21 +datum=BD09 +units=m +no_defs",
    target: "+proj=longlat +datum=WGS84 +units=degrees +no_defs"
  },
  CGCS2000: {
    source: "+proj=utm +zone=21 +datum=CGCS2000 +units=m +no_defs",
    target: "+proj=longlat +datum=WGS84 +units=degrees +no_defs"
  }
};
class kme extends es {
  constructor(t) {
    super();
    S(this, "_rayCaster", new lr());
    S(this, "_handleWorldClickBindThis", this._handleWorldClick.bind(this));
    S(this, "_transformControls");
    S(this, "_utmReferencePoints", [
      [0, 0],
      [0, 0]
    ]);
    S(this, "groupName", "group-geographic-projection-reference-point");
    S(this, "referencePoints", [new mt(), new mt()]);
    S(this, "projectOptions", {
      enable: !1,
      projection: "WGS84",
      nwReferencePoint: {
        x: -500,
        y: 0,
        z: -500,
        lon: 120.140675,
        lat: 30.25775,
        alt: 0
      },
      seReferencePoint: {
        x: 500,
        y: 0,
        z: 500,
        lon: 120.169525,
        lat: 30.29045,
        alt: 0
      },
      mode: "geographic",
      tileSetUrl: "",
      mapCenter: {
        lat: 30,
        lon: 120
      },
      tileSetMapCenter: {
        lat: 30,
        lon: 120
      },
      mapUrl: "https://fly.maptiles.arcgis.com/arcgis/rest/services/World_Imagery_Firefly/MapServer/tile/{z}/{y}/{x}",
      maxZoom: 18,
      minZoom: 0,
      tileSetHeightOffsetInMap: 0,
      tileSetScaleInMap: 1
    });
    this.world = t, this._transformControls = new BS(
      t.renderer.mainCamera,
      t.renderer.threeRenderer.domElement
    ), this._transformControls.showY = !1, this.referencePoints.forEach((i, n) => {
      i.visible = !1, i.name = `${n}`, i.add(new PF(i.name)), t.renderer.registerGroup(this.groupName).add(i);
    }), this._handleTransformControlsInitEvent(), t.renderer.registerGroup(this.groupName).add(this._transformControls), this._updateUtmReferencePoints();
  }
  _updateUtmReferencePoints() {
    const { source: t, target: i } = BA[this.projectOptions.projection], n = Ar(t, i);
    this._utmReferencePoints[0] = n.inverse([
      this.projectOptions.nwReferencePoint.lon,
      this.projectOptions.nwReferencePoint.lat
    ]), this._utmReferencePoints[1] = n.inverse([
      this.projectOptions.seReferencePoint.lon,
      this.projectOptions.seReferencePoint.lat
    ]);
  }
  _setReferencePointPosition(t, i) {
    const { x: n, y: r, z: a } = i;
    this.referencePoints[t].position.set(n, r, a);
  }
  _handleWorldClick(t) {
    this._transformControls.detach();
    const i = $r(
      t.clientX,
      t.clientY,
      t.target
    );
    this._rayCaster.setFromCamera(i, this.world.renderer.mainCamera);
    const [n] = this._rayCaster.intersectObjects(this.referencePoints);
    if (console.log(n), n && n.object) {
      let r = -1;
      const a = this.referencePoints.find(
        (o, l) => o.name === n.object.userData.referenceName && (r = l)
      );
      a && this._transformControls.attach(a), r !== -1 && this.dispatchEvent("reference-point-click", { index: r });
    }
  }
  // 把three 的笛卡尔坐标转换为 地理信息坐标
  _getUtmPosition(t, i, n) {
    const r = { x: t, y: i, z: n }, a = this._getDirUnit(
      t,
      this.projectOptions.nwReferencePoint.x,
      this.projectOptions.seReferencePoint.x
    ), o = this._getDirUnit(
      n,
      this.projectOptions.nwReferencePoint.z,
      this.projectOptions.seReferencePoint.z
    );
    return r.x = this._getValueByLonUnit(
      a,
      this._utmReferencePoints[0][0],
      this._utmReferencePoints[1][0]
    ), r.z = this._getValueByLatUnit(
      o,
      this._utmReferencePoints[0][1],
      this._utmReferencePoints[1][1]
    ), r;
  }
  // 获取带方向和值的向量, 求得 点 在 a, b线段的比值
  _getDirUnit(t, i, n) {
    const r = Math.min(i, n), a = Math.max(i, n);
    return (t - r) / (a - r);
  }
  // 通过带方向和值的向量, 获取该值在 a, b 直线上的位置
  _getValueByLonUnit(t, i, n) {
    const r = Math.min(i, n), a = Math.max(i, n);
    return t * (r - a) + a;
  }
  _getValueByLatUnit(t, i, n) {
    const r = Math.min(i, n), a = Math.max(i, n);
    return t * (a - r) + r;
  }
  // 把 地理信息坐标转换为 three 的笛卡尔坐标
  _getPosition(t, i, n) {
    const r = { x: t, y: i, z: n }, a = this._getDirUnit(t, this._utmReferencePoints[0][0], this._utmReferencePoints[1][0]), o = this._getDirUnit(n, this._utmReferencePoints[0][1], this._utmReferencePoints[1][1]);
    return r.x = this._getValueByLonUnit(
      a,
      this.projectOptions.nwReferencePoint.x,
      this.projectOptions.seReferencePoint.x
    ), r.z = this._getValueByLatUnit(
      o,
      this.projectOptions.nwReferencePoint.z,
      this.projectOptions.seReferencePoint.z
    ), r;
  }
  _handleTransformControlsInitEvent() {
    this._transformControls.addEventListener("dragging-changed", (t) => {
      if (this.world.renderer.cameraControls.enabled = !t.value, !t.value) {
        const i = Number(t.target.object.name);
        i === 0 ? (this.projectOptions.nwReferencePoint.x = this.referencePoints[i].position.x, this.projectOptions.nwReferencePoint.y = this.referencePoints[i].position.y, this.projectOptions.nwReferencePoint.z = this.referencePoints[i].position.z) : i === 1 && (this.projectOptions.seReferencePoint.x = this.referencePoints[i].position.x, this.projectOptions.seReferencePoint.y = this.referencePoints[i].position.y, this.projectOptions.seReferencePoint.z = this.referencePoints[i].position.z), dt.Entity.refresh();
      }
    }), this._transformControls.addEventListener("objectChange", () => {
    });
  }
  setProjectionOptions(t) {
    Object.assign(this.projectOptions, t), t.enable && Tt.enabled ? (this.referencePoints.forEach((i) => {
      i.visible = !0;
    }), this._setReferencePointPosition(0, t.nwReferencePoint), this._setReferencePointPosition(1, t.seReferencePoint)) : this.referencePoints.forEach((i) => {
      i.visible = !1;
    }), this._updateUtmReferencePoints(), dt.Entity.refresh();
  }
  flyToReferencePoint(t) {
    const i = new zt().setFromObject(this.referencePoints[t]);
    this.world.flyToBox(i, {});
  }
  projectPositionToGeographic(t, i, n) {
    const a = {
      geographic: () => {
        const { source: o, target: l } = BA[this.projectOptions.projection], c = Ar(o, l), h = this._getUtmPosition(t, i, n), [u, d] = c.forward([h.x, h.z]);
        return { lon: u, lat: d, alt: i };
      },
      tileset: () => {
        const o = this.world.getComponentByType("Scene");
        if (o) {
          const l = o.tilesetComponent.getLocalToCartographic(t, i, n);
          return { lon: l.lon, lat: l.lat, alt: l.height };
        }
        return { lon: 0, lat: 0, alt: 0 };
      },
      xyz: () => {
        const o = this.world.getComponentByType("Scene");
        if (o) {
          const l = o.mapComponent.getLocalToCartographic(t, i, n);
          return { lon: l.lon, lat: l.lat, alt: l.height };
        }
        return { lon: 0, lat: 0, alt: 0 };
      },
      xyz_tileset: () => {
        const o = this.world.getComponentByType("Scene");
        if (o) {
          const l = o.tilesetComponent.getLocalToCartographic(t, i, n);
          return { lon: l.lon, lat: l.lat, alt: l.height };
        }
        return { lon: 0, lat: 0, alt: 0 };
      }
    }[this.projectOptions.mode];
    if (a)
      return a();
    throw new Error(`Unsupported mode: ${this.projectOptions.mode}`);
  }
  projectGeographicToPosition(t, i, n) {
    const a = {
      geographic: () => {
        const { source: o, target: l } = BA[this.projectOptions.projection], c = Ar(o, l), [h, u] = c.inverse([t, i]);
        return this._getPosition(h, n, u);
      },
      tileset: () => {
        const o = this.world.getComponentByType("Scene");
        return (o == null ? void 0 : o.tilesetComponent.getCartographicToLocal(t, i, n)) ?? {
          x: 0,
          y: 0,
          z: 0
        };
      },
      xyz: () => {
        const o = this.world.getComponentByType("Scene");
        if (o) {
          const l = o.mapComponent.getCartographicToLocal(t, i, n);
          return { x: l.x, y: l.y, z: l.z };
        }
        return { x: 0, y: 0, z: 0 };
      },
      xyz_tileset: () => {
        const o = this.world.getComponentByType("Scene");
        return (o == null ? void 0 : o.tilesetComponent.getCartographicToLocal(t, i, n)) ?? {
          x: 0,
          y: 0,
          z: 0
        };
      }
    }[this.projectOptions.mode];
    if (a)
      return a();
    throw new Error(`Unsupported mode: ${this.projectOptions.mode}`);
  }
  startAdjustReferencePoint() {
    this.world.selection.enable = !1, this.world.selection.htmlEvent.addEventListener("click", this._handleWorldClickBindThis), this._transformControls.attach(this.referencePoints[0]);
  }
  endAdjustReferencePoint() {
    this.world.selection.enable = !0, this.world.selection.htmlEvent.removeEventListener("click", this._handleWorldClickBindThis), this._transformControls.detach();
    const t = this.world.getComponentByType("Scene");
    t && (t.needDataUpdateServer = !0);
  }
  dispose() {
    this._transformControls.dispose();
  }
}
function Gme(s) {
  return s.includes("http") || s.includes("base64") || s.startsWith(".");
}
function Qr(s, e) {
  if (!Gme(e)) {
    const t = s.endsWith("/") ? s.slice(0, -1) : s, i = e.startsWith("/") ? e.slice(1) : e;
    return `${t}/${i}`;
  }
  return e;
}
const e7 = /* @__PURE__ */ new Map();
function _s(s, e, t, i) {
  const n = `${s}_${e}_${t}`, r = (a) => i(a.detail);
  e7.set(n, r), window.addEventListener(n, r);
}
function Zf(s, e, t) {
  const i = `${s}_${e}_${t}`, n = e7.get(i);
  e7.delete(i), n && window.removeEventListener(i, n);
}
function Lx(s) {
  let e = so;
  switch (s) {
    case Ac.NoBlending:
      e = vn;
      break;
    case Ac.AdditiveBlending:
      e = oo;
      break;
    case Ac.NormalBlending:
      e = so;
      break;
    case Ac.SubtractiveBlending:
      e = m9;
      break;
    case Ac.MultiplyBlending:
      e = g9;
      break;
    default:
      e = so;
      break;
  }
  return e;
}
function Hme(s) {
  return function(e, t, i) {
    let n;
    const r = i.value;
    return i.value = function(...a) {
      const o = this;
      clearTimeout(n), n = setTimeout(() => {
        r.apply(o, a);
      }, s * 1e3);
    }, i;
  };
}
function Vme(s) {
  return function(e, t, i) {
    const n = i.value;
    return i.value = function(...r) {
      let a = 0, o = !1;
      const l = (c) => document.getElementsByClassName(c).length > 0;
      if (s.forEach((c) => l(c) && (o = !0)), o)
        n.apply(this, r);
      else {
        const c = setInterval(() => {
          a++, s.forEach((h) => l(h) && (o = !0)), o && (n.apply(this, r), clearInterval(c)), a > 100 && clearInterval(c);
        }, 50);
      }
    }, i;
  };
}
class Fi extends es {
  constructor(t, i, n) {
    super();
    S(this, "updateBindThis", this.update.bind(this));
    S(this, "states", new hee(this));
    S(this, "_disablePick", !1);
    S(this, "version", "1.7.0");
    S(this, "_needUpdateServer", !1);
    S(this, "type");
    // public readonly event : EasyTwinEvent = new EasyTwinEvent();
    S(this, "group", new ci());
    S(this, "isAdded", !1);
    // 初始化标记，只触发一次
    S(this, "isInit", !1);
    S(this, "needsUpdate", !1);
    S(this, "box", new zt());
    S(this, "_positionMode", "xyz");
    S(this, "tweenHandler");
    this.id = t, this.name = i, this.entity = n, this.group.name = i, this.group.uuid = t;
  }
  get disablePick() {
    return this._disablePick;
  }
  set disablePick(t) {
    this._disablePick = t;
  }
  get needDataUpdateServer() {
    return this._needUpdateServer;
  }
  set needDataUpdateServer(t) {
    this._needUpdateServer = t;
  }
  get positionMode() {
    return this._positionMode;
  }
  set positionMode(t) {
    this._positionMode = t;
  }
  getBox(t = "") {
    return this.box;
  }
  render(t, i) {
  }
  initialize(...t) {
  }
  getBlueprintCommonConfig() {
  }
  update(t) {
    this.dispatchEvent(_i.UpdateState, { stateId: this.getCurStateId() });
  }
  dispose() {
  }
  setPositionMode(t) {
    this._positionMode = t, this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  updateChildPosition(t, i) {
  }
  toJson(t = {
    updateModelSceneData: !1
    // 模型组件数据字段配置项太多存在互相影响的字段，需要能够独立进行更新
  }) {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      version: this.version,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(t, i) {
  }
  updateStateConfig(t, i, n) {
    this.states.deepGet(t) && (this.states.curStateId = t, this.states.updateStateConfig(t, i, n), this.needsUpdate = !0);
  }
  showHelper() {
  }
  hideHelper() {
  }
  setData(t) {
  }
  onClick(t, i) {
  }
  tick() {
  }
  intersectObject(t, i) {
    return t.intersectObjects(this.group.children);
  }
  setCustomAttribute(t, i) {
    this.group.traverse((n) => {
      if (n.uuid === i || i === void 0) {
        for (let r in t)
          t[r] === void 0 && (t[r] = "");
        Object.assign(n.userData, t), this.needDataUpdateServer = !0;
      }
    }), dt.Entity.refresh();
  }
  easyvSetCustomAttribute(t, i) {
    this.group.traverse((n) => {
      (n.uuid === i || i === void 0) && Object.assign(n.userData, t);
    });
  }
  updateCustomAttributeKey(t, i) {
    this.group.traverse((n) => {
      const r = n.userData[t];
      delete n.userData[t], n.userData[i] = r;
    }), dt.Entity.refresh();
  }
  updateDataById(t, i, n) {
    this.group.children.forEach((r) => {
      r.uuid === t && (r.name = i, r.position.set(n.x, n.y, n.z), this.needDataUpdateServer = !0);
    }), dt.Entity.refresh();
  }
  /**
   *
   * @param key 没有key 就是删除整个
   * @param id
   */
  removeCustomAttribute(t, i) {
    this.group.traverse((n) => {
      (n.uuid === i || i === void 0) && (t ? delete n.userData[t] : n.userData = {}, this.needDataUpdateServer = !0);
    }), dt.Entity.refresh();
  }
  setVisible(t, i) {
    i ? this.group.children.forEach((n) => {
      n.uuid === i && (n.visible = t);
    }) : this.group.visible = t;
  }
  setCurrentStateById(t) {
    return this.states.setState(t), this.needsUpdate = !0, dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((i) => i.toJson())
    }), this;
  }
  // 通过状态更新 组件的某些空间属性
  easyvSetStateByAttributes(t, i) {
  }
  filterByAttribute(t, i) {
    const n = [];
    return t.forEach((r) => {
      Object.keys(i).forEach((a) => {
        let o;
        "userData" in r && (o = r.userData[a]), "customAttribute" in r && (o = r.customAttribute[a]), o && (i[a] instanceof Array ? i[a].forEach((l) => {
          l === o && n.push(r);
        }) : o === i[a] && n.push(r));
      });
    }), n;
  }
  getDataLabels() {
    return [];
  }
  getCanAttach() {
    let t = !1;
    switch (this.type) {
      case "Model":
      case "ClusteringHeatmap":
      case "AreaHeatmap":
      case "InstanceModel":
      case "PoiPanel":
      case "PoiPanelV2":
      case "Point":
      case "PointLight":
      case "DirectLight":
      case "SpotLight":
      case "SunLight":
      case "RectAreaLight":
      case "Iframe":
      case "IframeV2":
      case "Light":
      case "FlowFence":
      case "BatchModel":
      case "FlowRoute":
      case "Floor":
      case "MiniMap":
      case "Water":
      case "EngineGroup":
      case "ParticleSystem":
      case "CameraFirstPerson":
      case "CameraSurround":
        t = !0;
        break;
      default:
        t = !1;
        break;
    }
    return t;
  }
  getSize() {
    return this.box.expandByObject(this.group), this.box.getSize(new _());
  }
  traverseChild(t) {
    this.group.children.forEach((i) => {
      t(i);
    });
  }
  initStates(t) {
    return this.states.initStates(t), this;
  }
  getStateList() {
    const t = Array.from(this.states.stateList.values());
    return JSON.parse(JSON.stringify(t));
  }
  addState(t, i) {
    const n = nt.generateUUID(), r = {
      ...t,
      id: n
    };
    if (this.states.add(r, i), Tt.enabled) {
      const a = () => {
        Qe.ApiServer.addState(this.entity.uuid, this.id, r).then((o) => {
          o && (this.states.updateStateId(r.id, o.id), this.states.setState(o.id), this.needsUpdate = !0), dt.Entity.setState({
            id: this.entity.uuid,
            name: this.entity.name,
            components: this.entity.getComponentArray().map((l) => l.toJson())
          }), this.dispatchEvent(_i.AddState, {});
        }), this.removeEventListener(_i.Add, a);
      };
      !this.isAdded && Tt.enabled ? this.addEventListener(_i.Add, a) : a();
    } else
      this.dispatchEvent(_i.AddState, {});
    return dt.Entity.refresh(), r;
  }
  // 状态在操作中暂不存在批量删除情况
  removeState(t) {
    this.states.clear(t), this.needsUpdate = !0, Qe.ApiServer.deleteState(this.entity.uuid, this.id, [t]), dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((i) => i.toJson())
    });
  }
  setName(t) {
    this.name = t, this.needDataUpdateServer = !0;
  }
  getCurStateId() {
    return this.states.curStateId ?? "";
  }
  // 获取组件中的空间对象
  getObjectById(t, i) {
    return t === "" ? this.group : this.group.getObjectByProperty("uuid", t);
  }
  // 删除组件中的空间对象
  removeObjectById(t) {
  }
  // public getSortedStateArray(): EasyTwinStateOptions<K>[] {
  //   return this.states.getSortedStateArray();
  // }
  setId(t) {
    this.id = t, this.group.uuid = t;
  }
  execute(t, i, ...n) {
  }
  onDragEnd() {
    dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((t) => t.toJson())
    });
  }
  onObjectChange() {
  }
  // ---1.7.0 新增 ---
  getLastVersion() {
    const t = [];
    return Hc.getInstance().filterComponentsWithClass(
      this.type
    ).forEach((n) => {
      const r = n.split("@")[1] ?? "1.7.0";
      t.push(r);
    }), t.sort(ZO).reverse(), t[0] ?? "1.7.0";
  }
  async updateVersion(t) {
  }
  // --- 1.7.0---
}
const GP = {
  x: 120,
  y: 90
}, A3 = 512, t7 = {
  x: 0.5,
  y: 0.5
}, Ky = {
  x: 128,
  y: 128
};
class Wme extends Pl {
  constructor(t, i, n, r, a) {
    super({
      id: t,
      name: i,
      position: n.position,
      components: [],
      type: "PoiPanel"
    });
    S(this, "backgroundMesh");
    S(this, "rowMeshMap", /* @__PURE__ */ new Map());
    this.options = n, this.backgroundMesh = this.createBackgroundMesh(n.imgUrl, n.size, n.offset), this.backgroundMesh.renderOrder = 10, this.backgroundMesh.material.depthTest = !n.alwaysTop, this.add(this.backgroundMesh), a == null || a.forEach((o) => {
      this.addRowMesh(r, o);
    });
  }
  addRowMesh(t, i) {
    const { alwaysTop: n = !0, size: r = GP, offset: a } = this.options, o = new ar({
      depthTest: !1,
      transparent: !0,
      alphaTest: 0.1,
      side: ui
    }), l = document.createElement("canvas");
    l.width = A3, l.height = A3, o.map = new zc(l), o.map.colorSpace = "srgb-linear", o.depthTest = !n;
    const c = new wn(o);
    c.renderOrder = 10, c.scale.set(Ky.x, Ky.y, 1);
    const h = a ?? t7;
    h && c.center.set(h.x, h.y), this.add(c), this.drawCanvas({ styleOptions: i, dataOptions: t, mesh: c, canvas: l, size: r }), this.rowMeshMap.set(i.id, { styleOptions: i, dataOptions: t, mesh: c, canvas: l, size: r });
  }
  drawCanvas(t) {
    const i = t.canvas.getContext("2d");
    if (i) {
      i.clearRect(0, 0, A3, A3);
      const { style: n, independentRowStyle: r, position: a, id: o, enableIndependent: l, name: c, label: h } = t.styleOptions, u = t.dataOptions[h] ?? "", d = l ? r : n, f = d.label, p = d.value, { padding: v = { x: 46, y: -1 }, size: g = GP } = this.options;
      let m = v.x + a.x;
      const x = v.y + a.y + f.fontSize;
      if (f.textAlign === "right" && (m += f.width), i.font = `${f.fontSize}px ${f.fontFamily}`, i.textAlign = f.textAlign, i.fillStyle = f.fontColor, i.globalAlpha = f.fontOpacity, i.fillText(c, m, x), u) {
        let y = v.x + a.x + f.width + p.leftPadding;
        const A = f.fontSize - p.fontSize, b = v.y + a.y + p.fontSize + A - (A === 0 ? 0 : A / Math.abs(A));
        p.textAlign === "right" && (y = g.x - v.x), i.font = `${p.fontSize}px ${p.fontFamily}`, i.textAlign = p.textAlign, i.fillStyle = p.fontColor, i.globalAlpha = p.fontOpacity, i.fillText(u, y, b);
      }
      t.mesh.material.map && (t.mesh.material.map.colorSpace = "srgb", t.mesh.material.map.needsUpdate = !0);
    }
  }
  createBackgroundMesh(t, i = Ky, n = t7) {
    const r = new ar({
      depthTest: !1,
      transparent: !0,
      side: ui,
      alphaTest: 0.1
    }), a = new wn(r);
    return a.scale.set(i.x, i.y, 1), a.center.set(n.x, n.y), st.loadTexture(t).then((o) => {
      o.colorSpace = "srgb", a.material.map = o, a.material.needsUpdate = !0;
    }), a;
  }
  updateConfig(t) {
    Object.assign(this.options, t), st.loadTexture(t.imgUrl).then((i) => {
      i.colorSpace = "srgb", this.backgroundMesh.material.map = i, this.backgroundMesh.material.needsUpdate = !0;
    }), this.backgroundMesh.scale.set(t.size.x, t.size.y, 1), this.backgroundMesh.center.set(t.offset.x, t.offset.y), this.backgroundMesh.material.depthTest = !t.alwaysTop;
  }
  updateRowData(t, i) {
  }
  removeRow(t) {
    const i = this.rowMeshMap.get(t);
    return i == null || i.mesh.geometry.dispose(), i == null || i.mesh.material.dispose(), this.rowMeshMap.delete(t), this;
  }
  getRowDataOptions() {
    const t = {};
    return this.rowMeshMap.forEach((i) => {
      Object.assign(t, i.dataOptions);
    }), t;
  }
  updateRowStyle(t) {
    if (t.length !== this.rowMeshMap.size) {
      this.rowMeshMap.forEach((n) => {
        n.mesh.geometry.dispose(), n.mesh.material.dispose(), this.remove(n.mesh);
      });
      const i = this.getRowDataOptions();
      this.rowMeshMap.clear(), t.forEach((n) => {
        this.addRowMesh(i, n);
      });
    } else {
      const i = {};
      t.forEach((n) => {
        const r = this.rowMeshMap.get(n.id);
        r && (r.styleOptions = { ...n }, i[n.label] = r.dataOptions[n.label] ?? "");
      }), this.rowMeshMap.forEach((n) => {
        n.dataOptions = i, this.drawCanvas(n);
      });
    }
  }
}
const Gn = 128;
class FA extends Pl {
  constructor(t, i, n) {
    var r;
    super({
      id: t,
      name: i,
      components: [],
      type: "FlowFence"
    });
    S(this, "fenceMesh", new Re());
    S(this, "tweenUpdateBindThis", this.updateTweenAnimation.bind(this));
    S(this, "_tweenHandler", jn.createTweenHandler({
      source: { value: 1 },
      target: { value: -0.5 },
      repeat: 1 / 0,
      immediately: !1,
      duration: 1e3,
      execute: this.tweenUpdateBindThis
    }));
    this.options = n, this.fenceMesh.material = [
      this._createBackgroundMaterial(n.fenceStyle),
      // new MeshBasicMaterial({
      //   transparent: true,
      //   side: DoubleSide,
      //   depthWrite: false,
      //   color: 'red',
      //   opacity: 0.2
      // }),
      this._createVideoMaterial(
        n.customStyle.videoUrl,
        n.customStyle.videoWidth,
        (r = n.customStyle.adjustColor) == null ? void 0 : r.pure
      ),
      this._createCanvasMaterial(n.fenceStyle.animateStyle)
    ], this.position.set(n.position.x, n.position.y, n.position.z), n.points.length > 0 && (this.fenceMesh.geometry = this.createGeometryByPoints(n.points, n.height)), n.type === "custom" && this.fenceMesh.geometry.groups[0] && (this.fenceMesh.geometry.groups[0].materialIndex = 1), n.closed && this.closeGeometry(), this._tweenHandler.duration(1e3 / this.options.fenceStyle.animateStyle.speed), this.options.fenceStyle.hasAnimate && this._tweenHandler.start(), this.updateMaterialBlendMode(n.blendMode), this.add(this.fenceMesh);
  }
  getPoints() {
    return this.options.points.map((t) => ({ x: t.x, y: t.y, z: t.z }));
  }
  getVec3Points() {
    return this.options.points.map((t) => new _(t.x, t.y, t.z));
  }
  setPoints(t) {
    this.options.points = t.map((i) => ({ x: i.x, y: i.y, z: i.z })), this.fenceMesh.geometry.dispose(), this.fenceMesh.geometry = this.createGeometryByPoints(t, this.options.height);
  }
  pushPoint(t) {
    this.options.points.push(t), this.fenceMesh.geometry.dispose(), this.fenceMesh.geometry = this.createGeometryByPoints(this.options.points, this.options.height);
  }
  popPoint() {
    this.options.points.pop(), this.fenceMesh.geometry.dispose(), this.fenceMesh.geometry = this.createGeometryByPoints(this.options.points, this.options.height), console.log("this.options.points", this.options.points);
  }
  updateMaterialBlendMode(t) {
    "length" in this.fenceMesh.material && this.fenceMesh.material.forEach((i) => {
      i.blending = Lx(t);
    });
  }
  createGeometryByPoints(t, i) {
    const n = [], r = [], a = [];
    t.forEach((c) => {
      r.push(c.x, c.y, c.z), r.push(c.x, c.y + i, c.z);
    });
    const o = t.length * 2 - 2;
    for (let c = 0; c < o; c += 2)
      n.push(c, c + 1, c + 3), n.push(c + 2, c, c + 3), a.push(0, 0, 0, 1, 1, 0, 1, 1);
    const l = new Pt();
    return l.setIndex(n), l.setAttribute("position", new gt(r, 3)), l.setAttribute("uv", new gt(a, 2)), this._addGeometryGroup(l, n.length), l;
  }
  _addGeometryGroup(t, i) {
    const { type: n } = this.options;
    n === "custom" ? (t.addGroup(0, i, 1), t.addGroup(0, i, 2)) : (t.addGroup(0, i, 0), t.addGroup(0, i, 2));
  }
  _createBackgroundMaterial(t) {
    const i = new kt({
      transparent: !0,
      side: ui
    }), n = document.createElement("canvas");
    n.width = Gn, n.height = Gn;
    const r = n.getContext("2d");
    if (r && t && "color" in t) {
      const o = r.createLinearGradient(0, 0, 0, Gn);
      if (t && "opacity" in t) {
        const { linear: l } = t.color;
        l.forEach((c) => {
          const h = new _e(c.color), u = `rgba(${h.r * 255},${h.g * 255},${h.b * 255},${c.alpha})`;
          o.addColorStop(c.offset, u), c.alpha !== 1 && (i.depthWrite = !1);
        }), t.opacity && (i.opacity = t.opacity, t.opacity !== 1 && (i.depthWrite = !1));
      } else
        o.addColorStop(0, "rgba(255,255,255,0.9)"), o.addColorStop(1, "rgba(255,255,255,0.1)");
      r.fillStyle = o, r.fillRect(0, 0, Gn, Gn);
    }
    const a = new zc(n);
    return a.colorSpace = "srgb", i.map = a, i;
  }
  _createVideoMaterial(t, i, n) {
    const r = st.getVideoTexture(t);
    r.image && (r.image.width = i);
    const a = new kt({
      map: r,
      transparent: !0,
      depthWrite: !1,
      side: ui
    });
    return n && (a.color = new _e(n)), a;
  }
  _createCanvasMaterial(t) {
    const i = new kt({
      transparent: !0,
      // depthWrite: false,
      side: ui
    });
    i.depthWrite = t.opacity === 1;
    const n = document.createElement("canvas");
    n.width = Gn, n.height = Gn;
    const r = n.getContext("2d");
    if (r) {
      r.clearRect(0, 0, Gn, Gn), r.globalAlpha = t.opacity ?? 1, r.fillStyle = t.color ?? "#ffffff";
      const o = t.proportion ?? 0.25, l = Gn * o, c = Gn * (1 - o) / 2;
      r.fillRect(0, c, Gn, l);
    }
    const a = new zc(n);
    return a.colorSpace = "srgb", a.offset.setY(-1), i.map = a, i;
  }
  updateVideoMaterial(t) {
    const { videoUrl: i, videoWidth: n, adjustColor: r } = t, a = this.fenceMesh.material;
    if ("map" in a[1]) {
      const o = st.getVideoTexture(i);
      a[1].map = o, o.image.width = n, o.needsUpdate = !0, r && (a[1].color = new _e(r.pure)), a[1].needsUpdate = !0;
    }
  }
  // 闭合几何体
  closeGeometry() {
    this.fenceMesh.geometry.dispose();
    const t = this.getPoints();
    t.length > 1 && t.push(t[0]), this.fenceMesh.geometry = this.createGeometryByPoints(t, this.options.height);
  }
  // 还原几何至不闭合的状态
  openGeometry() {
    this.fenceMesh.geometry.dispose(), this.fenceMesh.geometry = this.createGeometryByPoints(this.getPoints(), this.options.height);
  }
  updateCanvasMaterial(t) {
    const { proportion: i = 0.25, color: n, opacity: r, speed: a } = t, o = this.fenceMesh.material;
    if ("map" in o[2]) {
      const l = o[2].map, c = l.image.getContext("2d");
      if (c) {
        c.clearRect(0, 0, Gn, Gn), c.globalAlpha = r ?? 1, c.fillStyle = n ?? "#ffffff";
        const h = Gn * i, u = Gn * (1 - i) / 2;
        c.fillRect(0, u, Gn, h);
      }
      l.needsUpdate = !0, this._tweenHandler.duration(1e3 / a);
    }
  }
  updateBackgroundMaterial(t) {
    const i = this.fenceMesh.material;
    if ("map" in i[0]) {
      const r = i[0].map.image;
      if (r) {
        const a = r.getContext("2d");
        if (a) {
          const o = a.createLinearGradient(0, 0, 0, Gn);
          if ("color" in t) {
            a.clearRect(0, 0, Gn, Gn);
            const { linear: l } = t.color;
            l.forEach((c) => {
              const h = new _e(c.color), u = `rgba(${h.r * 255},${h.g * 255},${h.b * 255},${c.alpha})`;
              o.addColorStop(c.offset, u);
            }), a.fillStyle = o, a.fillRect(0, 0, Gn, Gn), i[0].map && (i[0].map.needsUpdate = !0);
          }
        }
        i[0].map && (i[0].map.needsUpdate = !0);
      }
    }
  }
  updateTweenAnimation(t) {
    var n, r;
    const { hasAnimate: i } = this.options.fenceStyle;
    i && this.options.type === "default" ? (n = this.fenceMesh.material[2].map) == null || n.offset.setY(t.value) : (this._tweenHandler.stop(), (r = this.fenceMesh.material[2].map) == null || r.offset.setY(-1));
  }
  updateConfig(t) {
    Object.assign(this.options, t);
    const {
      fenceStyle: i,
      // true 封闭， 默认不封闭
      closed: n,
      type: r,
      customStyle: a
    } = t, { hasAnimate: o } = i;
    o && this._tweenHandler.start(), this.fenceMesh.geometry.groups[0] && (r === "custom" ? this.fenceMesh.geometry.groups[0].materialIndex = 1 : this.fenceMesh.geometry.groups[0].materialIndex = 0), this.updateBackgroundMaterial(i), this.updateCanvasMaterial(i.animateStyle), this.updateVideoMaterial(a), this.updateMaterialBlendMode(t.blendMode), n ? this.closeGeometry() : this.openGeometry();
  }
}
class Xme extends Pl {
  constructor(t, i, n) {
    super({
      id: t,
      name: i,
      position: n.position,
      components: [],
      type: "PoiPanel"
    });
    S(this, "backgroundMesh");
    this.options = n, this.backgroundMesh = this.createBackgroundMesh(n.imgUrl, n.size, n.offset), this.backgroundMesh.renderOrder = 10, this.backgroundMesh.material.depthTest = !n.alwaysTop, this.add(this.backgroundMesh);
  }
  createBackgroundMesh(t, i = Ky, n = t7) {
    const r = new ar({
      depthTest: !1,
      transparent: !0,
      side: ui,
      alphaTest: 0.1
    }), a = new wn(r);
    return a.scale.set(i.x, i.y, 1), a.center.set(n.x, n.y), st.loadTexture(t).then((o) => {
      o.colorSpace = "srgb", a.material.map = o, a.material.needsUpdate = !0;
    }), a;
  }
  updateConfig(t) {
    Object.assign(this.options, t), st.loadTexture(t.imgUrl).then((i) => {
      i.colorSpace = "srgb", this.backgroundMesh.material.map = i, this.backgroundMesh.material.needsUpdate = !0;
    }), this.backgroundMesh.scale.set(t.size.x, t.size.y, 1), this.backgroundMesh.center.set(t.offset.x, t.offset.y), this.backgroundMesh.material.depthTest = !t.alwaysTop;
  }
}
const jme = new ln(new _(0, 1, 0));
class h8 extends cp {
  constructor(t, i, n, r, a, o, l, c) {
    super({ id: t, name: i, type: "Iframe", inActive: !1 });
    S(this, "element");
    S(this, "shaderDom");
    S(this, "url");
    this.cameraElement = o, this.url = a.url;
    const h = document.createElement("div");
    this.element = h, this.element.style.position = "absolute", this.element.style.pointerEvents = "none", this.element.style.opacity = "1.0";
    const u = document.createElement("div");
    u.style.position = "absolute", u.style.pointerEvents = "all", u.style.width = c ? `${c.x}px` : "400px", u.style.height = c ? `${c.y}px` : "300px", u.id = t, u.dataset.id = t, u.dataset.entityId = n, u.dataset.componentId = r, this.shaderDom = u, this.element.appendChild(u), this.element.id = "drag-cssObject";
    const { position: d, url: f, faceToCamera: p = !0, autoResize: v = !0 } = a;
    if (this.faceToCamera = p, this.autoResize = v, d && this.position.set(d.x, d.y, d.z), f) {
      const g = document.createElement("iframe");
      g.style.position = "absolute", g.style.pointerEvents = "none", g.style.width = "400px", g.style.height = "300px", g.style.border = "none", this.element.appendChild(g), g.src = f;
    }
    this.setSize(a.size ? a.size.x : 400, a.size ? a.size.y : 300), this.addEventListener("removed", (g) => {
      this.traverse((m) => {
        m instanceof h8 && m.element.parentNode !== null && this.dispose();
      });
    });
  }
  restoreOpacity() {
    this.element.style.opacity === "0.2" && (this.element.style.opacity = "1.0", this.shaderDom.style.pointerEvents = "all");
  }
  setIframeOpacity(t) {
    this.element.style.opacity = t.toString();
  }
  setPointerEventNone() {
    this.shaderDom.style.pointerEvents = "none";
  }
  raycast(t, i) {
    this.restoreOpacity();
    const n = this.shaderDom.getBoundingClientRect(), r = this.cameraElement.getBoundingClientRect(), [a, o, l, c] = [n.left, n.top, n.right, n.bottom], h = new _(a / r.width * 2 - 1, o / r.height * -2 + 1, -1), u = new _(l / r.width * 2 - 1, c / r.height * -2 + 1, -1);
    h.unproject(t.camera), u.unproject(t.camera), h.sub(t.camera.position).normalize(), u.sub(t.camera.position).normalize();
    const d = -t.camera.position.y / h.y, f = -t.camera.position.y / u.y, p = t.camera.position.clone().add(h.multiplyScalar(d)), v = t.camera.position.clone().add(u.multiplyScalar(f)), g = t.ray.intersectPlane(jme, new _()), m = t.ray.origin.distanceTo(g ?? this.position), x = Math.min(p.x, v.x), y = Math.min(p.z, v.z), A = Math.max(p.x, v.x), b = Math.max(p.z, v.z);
    g && g.x >= x && g.x <= A && g.z >= y && g.z <= b && (i.push({
      distance: m,
      point: new _(),
      object: this
    }), this.element.style.opacity = "0.2", this.shaderDom.style.pointerEvents = "none");
  }
  updateConfig(t) {
    const { faceToCamera: i, autoResize: n, size: r } = t;
    this.faceToCamera = i, this.autoResize = n, this.setSize(r.x, r.y);
  }
  updateUrl(t) {
    this.url = t;
    const i = this.getIframe();
    i && (i.src = t);
  }
  getIframe() {
    return this.element.querySelector("iframe");
  }
  getShader() {
    return this.element.querySelector("div");
  }
  setSize(t, i) {
    this.element.style.width = `${t}px`, this.element.style.height = `${i}px`;
    const n = this.getIframe();
    n && (n.style.width = `${t}px`, n.style.height = `${i}px`);
    const r = this.getShader();
    r && (r.style.width = `${t}px`, r.style.height = `${i}px`);
  }
  setVisible(t) {
    this.visible = t, this.visible && (this.element.style.display = "flow"), !this.visible && (this.element.style.display = "none");
  }
  attachTransformControls() {
    this.setIframeOpacity(0.2), this.setPointerEventNone();
  }
  detachTransformControls() {
    this.restoreOpacity();
  }
  dispose() {
    var t;
    (t = this.element.parentNode) == null || t.removeChild(this.element);
  }
}
var qme = Object.defineProperty, Yme = Object.getOwnPropertyDescriptor, Zme = (s, e, t, i) => {
  for (var n = Yme(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && qme(e, t, n), n;
};
const b3 = new Se(), HP = {
  enable: !1,
  position: "rightBottom",
  size: 128,
  padding: 16
};
class RF extends mt {
  constructor(t, i, n, r = HP) {
    super();
    S(this, "render");
    S(this, "handleClick");
    S(this, "animating", !1);
    S(this, "update");
    S(this, "event", new es());
    S(this, "shadeDom", document.createElement("div"));
    S(this, "padding", 0);
    S(this, "size", 0);
    S(this, "viewLocation", "rightBottom");
    S(this, "toolTip", document.createElement("div"));
    S(this, "raycaster");
    S(this, "interactiveObjects");
    S(this, "mouse");
    S(this, "camera");
    S(this, "keyEventUtils", new aF());
    S(this, "toggleView");
    this.editorCamera = t, this.dom = i, this.controls = n;
    const { enable: a, position: o, size: l, padding: c } = { ...HP, ...r };
    this.viewLocation = o, this.size = l, this.padding = c, this.shadeDom.addEventListener("pointerup", (be) => {
      be.stopPropagation(), this.handleClick(be);
    }), this.shadeDom.addEventListener("pointerdown", (be) => {
      be.stopPropagation();
    }), this.shadeDom.addEventListener("mousemove", (be) => {
      this.handleShadeDomMouseMove(be);
    }), this.shadeDom.addEventListener("mouseout", (be) => {
      this.toolTip.style.visibility = "hidden", this.setBorderFillVisible(!1, !1);
    }), this.visible = a;
    const h = new _e("#ffffff");
    this.mouse = new Se(), this.raycaster = new lr(), this.interactiveObjects = [], this.camera = new Xn(-2, 2, 2, -2, 0, 4), this.camera.position.set(0, 0, 2);
    const u = new Pt().setFromPoints([new _(0, -0.35, 0), new _(0, 0.35, 0)]).translate(0, 0.4, 0), d = new ti(u, this.getAxisMaterial(h)), f = new ti(u, this.getAxisMaterial(h)), p = new ti(u, this.getAxisMaterial(h));
    d.userData.type = "AxisX", f.userData.type = "AxisY", p.userData.type = "AxisZ", d.rotation.z = -Math.PI / 2, p.rotation.x = Math.PI / 2, this.add(d), this.add(p), this.add(f);
    const v = new _e("#E6645F"), g = new _e("#27C346"), m = new _e("#3A89FE"), x = new Pt().setFromPoints([new _(0, -0.7, 0), new _(0, 0.7, 0)]).translate(0, 0, 0), y = new ti(x, this.getAxisMaterial(v)), A = new ti(x, this.getAxisMaterial(g)), b = new ti(x, this.getAxisMaterial(m));
    y.rotation.z = -Math.PI / 2, b.rotation.x = Math.PI / 2, y.userData.type = "HoverAxisX", A.userData.type = "HoverAxisY", b.userData.type = "HoverAxisZ", y.visible = !1, A.visible = !1, b.visible = !1, this.add(y), this.add(A), this.add(b);
    const M = new wn(this.getSpriteMaterial(h, "X"));
    M.userData.type = "posX";
    const w = new wn(this.getSpriteMaterial(h, "Y"));
    w.userData.type = "posY";
    const C = new wn(this.getSpriteMaterial(h, "Z"));
    C.userData.type = "posZ";
    const T = new wn(this.getSpriteMaterial(h));
    T.userData.type = "negX";
    const E = new wn(this.getSpriteMaterial(h));
    E.userData.type = "negY";
    const P = new wn(this.getSpriteMaterial(h));
    P.userData.type = "negZ", M.position.x = 1, w.position.y = 1, C.position.z = 1, T.position.x = -1, T.scale.setScalar(0.8), E.position.y = -1, E.scale.setScalar(0.8), P.position.z = -1, P.scale.setScalar(0.8), this.add(M), this.add(w), this.add(C), this.add(T), this.add(E), this.add(P), this.interactiveObjects.push(M), this.interactiveObjects.push(w), this.interactiveObjects.push(C), this.interactiveObjects.push(T), this.interactiveObjects.push(E), this.interactiveObjects.push(P);
    const L = new _(), I = 2 * Math.PI, N = new mt();
    this.render = (be) => {
      if (!this.visible)
        return;
      this.quaternion.copy(this.editorCamera.quaternion).invert(), this.updateMatrixWorld(), L.set(0, 0, 1), L.applyQuaternion(this.editorCamera.quaternion);
      const [Pe, J] = this.getViewHelperPosition(o, l, c);
      (Pe !== b3.x || J !== b3.y) && (b3.x = Pe, b3.y = J, this.shadeDom.style.left = `${Pe}px`, this.shadeDom.style.bottom = `${J}px`), be.clearDepth(), be.setViewport(Pe, J, l, l), this.animating && this.update(), be.render(this, this.camera);
    };
    const H = new _(), U = new St(), W = new St(), X = new St();
    let K = 0;
    this.handleClick = (be) => {
      if (this.animating === !0)
        return !1;
      const Pe = this.getIntersection(be);
      if (Pe.length > 0) {
        const J = Pe[0], { object: fe } = J;
        return re(fe, this.controls.getTarget(new _())), this.animating = !0, this.event.dispatchEvent("axisClick", fe.userData.type), !0;
      } else
        return !1;
    }, this.update = (be = 0.01) => {
      const Pe = be * I, J = this.controls.getTarget(new _());
      W.rotateTowards(X, Pe);
      const fe = this.editorCamera.position.clone();
      fe.set(0, 0, 1).applyQuaternion(W).multiplyScalar(K).add(J), this.editorCamera.quaternion.rotateTowards(U, Pe), this.controls.setPosition(fe.x, fe.y, fe.z), W.angleTo(X) === 0 ? (this.animating = !1, this.event.dispatchEvent("toggleViewEnd", null)) : this.event.dispatchEvent("toggleViewAnimation", { quaternion: U, step: Pe });
    }, this.toggleView = (be) => {
      const Pe = () => {
        const J = this.controls.getTarget(new _());
        K = this.editorCamera.position.distanceTo(J), H.multiplyScalar(K).add(J), N.position.copy(J), N.lookAt(this.editorCamera.position), W.copy(N.quaternion), N.lookAt(H), X.copy(N.quaternion);
      };
      switch (be) {
        case "posX":
          H.set(1, 0, 0), U.setFromEuler(new Mi(0, Math.PI * 0.5, 0)), this.event.dispatchEvent("toggleViewStart", "posX"), Pe(), this.animating = !0;
          break;
        case "posY":
          H.set(0, 1, 0), U.setFromEuler(new Mi()), this.event.dispatchEvent("toggleViewStart", "posY"), Pe(), this.animating = !0;
          break;
        case "posZ":
          H.set(0, 0, 1), U.setFromEuler(new Mi()), this.event.dispatchEvent("toggleViewStart", "posZ"), Pe(), this.animating = !0;
          break;
      }
    };
    const te = this;
    function re(be, Pe) {
      switch (be.userData.type) {
        case "posX":
          H.set(1, 0, 0), U.setFromEuler(new Mi(0, Math.PI * 0.5, 0)), te.event.dispatchEvent("toggleViewStart", "posX");
          break;
        case "posY":
          H.set(0, 1, 0), U.setFromEuler(new Mi(-Math.PI * 0.5, 0, 0)), te.event.dispatchEvent("toggleViewStart", "posY");
          break;
        case "posZ":
          H.set(0, 0, 1), U.setFromEuler(new Mi()), te.event.dispatchEvent("toggleViewStart", "posZ");
          break;
        case "negX":
          H.set(-1, 0, 0), U.setFromEuler(new Mi(0, -Math.PI * 0.5, 0)), te.event.dispatchEvent("toggleViewStart", "negX");
          break;
        case "negY":
          H.set(0, -1, 0), U.setFromEuler(new Mi(Math.PI * 0.5, 0, 0)), te.event.dispatchEvent("toggleViewStart", "negY");
          break;
        case "negZ":
          H.set(0, 0, -1), U.setFromEuler(new Mi(0, Math.PI, 0)), te.event.dispatchEvent("toggleViewStart", "negZ");
          break;
        default:
          console.error("ViewHelper: Invalid axis.");
      }
      K = t.position.distanceTo(Pe), H.multiplyScalar(K).add(Pe), N.position.copy(Pe), N.lookAt(t.position), W.copy(N.quaternion), N.lookAt(H), X.copy(N.quaternion);
    }
  }
  appendShade(t) {
    t.appendChild(this.shadeDom), this.shadeDom.id = "viewHelperShade";
    const [i, n] = this.getViewHelperPosition(this.viewLocation, this.size, this.padding);
    this.shadeDom.style.cssText = `
      position: absolute; 
      left: ${i}px; 
      bottom: ${n}px; 
      width: ${this.size}px; 
      height: ${this.size}px;
    `, t.appendChild(this.toolTip), this.toolTip.style.cssText = `
      background: black;
      border-radius: 5px;
      bottom: ${n + this.size}px; 
      width: 180px;
      right: 1vw;
      position: absolute;
      padding: 6px;
      // white-space: pre;
      visibility: hidden;
    `, this.keyEventUtils.active();
  }
  getIntersection(t) {
    const i = this.shadeDom.getBoundingClientRect();
    return this.mouse.x = (t.clientX - i.left) / this.size * 2 - 1, this.mouse.y = -((t.clientY - i.top) / this.size) * 2 + 1, this.raycaster.setFromCamera(this.mouse, this.camera), this.raycaster.intersectObjects(this.interactiveObjects);
  }
  getViewHelperPosition(t, i, n) {
    let r = 0, a = 0;
    switch (t) {
      case "leftTop":
        r = n, a = this.dom.offsetHeight - i - n;
        break;
      case "leftBottom":
        r = n, a = n;
        break;
      case "rightTop":
        r = this.dom.offsetWidth - i - n, a = this.dom.offsetHeight - i - n;
        break;
      case "rightBottom":
        r = this.dom.offsetWidth - i - n, a = n;
        break;
    }
    return [r, a];
  }
  getAxisMaterial(t) {
    return new Bi({ color: t, toneMapped: !1, depthWrite: !1 });
  }
  getSpriteMaterial(t, i = null, n = !1, r = !1, a = "#ffffff", o) {
    var f;
    let l;
    o && (l = (f = o.map) == null ? void 0 : f.image, o.map && (o.map.needsUpdate = !0)), !l && (l = document.createElement("canvas")), l.width = 80, l.height = 80;
    const c = l.getContext("2d");
    if (!c)
      return;
    c.beginPath(), c.arc(40, 40, r ? 18 : 20, 0, 2 * Math.PI), c.closePath();
    const { r: h, g: u, b: d } = t;
    if (r && (c.lineWidth = 4, c.strokeStyle = `rgba(${h * 255}, ${u * 255}, ${d * 255}, 1)`, c.stroke()), n && (c.fillStyle = `rgba(${h * 255}, ${u * 255}, ${d * 255}, ${0.3})`, c.fill()), i !== null && (c.font = "24px Arial", c.textAlign = "center", c.fillStyle = a, c.fillText(i, 40, 49)), !o) {
      const p = new zc(l);
      return new ar({ map: p, toneMapped: !1 });
    }
    return o;
  }
  getTextAxis(t) {
    return t.slice(3, 4);
  }
  handleShadeDomMouseMove(t) {
    const i = this.getIntersection(t);
    this.setBorderFillVisible(!1, !0), this.showTooltip(i), this.setHoverColor(i);
  }
  setBorderFillVisible(t = !1, i = !1) {
    const r = this.getAverageLuminance(this.dom) < 70 ? "#ffffff" : "#000000";
    this.children.forEach((a) => {
      const o = a, { type: l } = a.userData;
      switch (l) {
        case "posX":
        case "posY":
        case "posZ":
          this.getSpriteMaterial(
            new _e(r),
            this.getTextAxis(l),
            t,
            i,
            r,
            o.material
          );
          break;
        case "negX":
        case "negY":
        case "negZ":
          this.getSpriteMaterial(new _e(r), null, t, i, r, o.material);
          break;
      }
      l.includes("Axis") && (l.includes("Hover") ? a.visible = !1 : a.visible = !0);
    });
  }
  getColor(t) {
    let i = "#ffffff";
    switch (t) {
      case "posX":
      case "negX":
        i = "#E6645F";
        break;
      case "posY":
      case "negY":
        i = "#27C346";
        break;
      case "posZ":
      case "negZ":
        i = "#3A89FE";
        break;
    }
    return i;
  }
  setHoverColor(t) {
    const [i] = t, r = this.getAverageLuminance(this.dom) < 70 ? "#ffffff" : "#000000";
    if (i) {
      const a = i.object, o = this.getTextAxis(a.userData.type);
      this.children.forEach((l) => {
        const c = l, { type: h } = c.userData;
        if (h.includes(o)) {
          const u = new _e(this.getColor(a.userData.type));
          h.includes("pos") ? this.getSpriteMaterial(u, o, !0, !0, r, c.material) : c.userData.type.includes("neg") && this.getSpriteMaterial(u, null, !0, !0, r, c.material), h.includes("Axis") && (h.includes("Hover") ? l.visible = !0 : l.visible = !1);
        }
      });
    }
  }
  showTooltip(t) {
    if (t.length > 0) {
      this.toolTip.style.visibility = "visible";
      const i = t[0], { object: n } = i;
      let r = 1;
      switch (n.userData.type) {
        case "posX":
          r = 1;
          break;
        case "posY":
          r = 2;
          break;
        case "posZ":
          r = 3;
          break;
        case "negX":
          r = 4;
          break;
        case "negY":
          r = 5;
          break;
        case "negZ":
          r = 6;
          break;
        default:
          console.error("ViewHelper: Invalid axis.");
      }
      r < 4 ? this.toolTip.textContent = `
        单击: 使用一个预设观察点
        快捷键: 数字键盘${r}
      ` : this.toolTip.style.visibility = "hidden";
    } else
      this.toolTip.style.visibility = "hidden";
  }
  getAverageLuminance(t) {
    const i = t.getContext("2d"), n = t.width, r = t.height;
    let a = 0;
    if (i) {
      const l = i.getImageData(0, 0, 1, 1), c = i.getImageData(n - 1, 0, 1, 1), h = i.getImageData(0, r - 1, 1, 1), u = i.getImageData(n - 1, r - 1, 1, 1), d = (f) => {
        const p = f.data;
        return 0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2];
      };
      a += d(l), a += d(c), a += d(h), a += d(u);
    }
    return a / 4;
  }
  dispose() {
    this.traverse((t) => {
      var i;
      "material" in t && ("map" in t.material && ((i = t.material.map) == null || i.dispose(), t.material.map = void 0), t.material.dispose()), "geometry" in t && t.geometry.dispose();
    }), this.children.length = 0, this.event.removeAllEventListeners(), this.keyEventUtils.deActive(), this.keyEventUtils.removeAllEventListeners();
  }
}
Zme([
  Qp(0.2)
], RF.prototype, "handleShadeDomMouseMove");
var Kme = Object.defineProperty, Jme = Object.getOwnPropertyDescriptor, Qme = (s, e, t, i) => {
  for (var n = Jme(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && Kme(e, t, n), n;
};
new mt();
const Wr = 10;
class Ix extends cp {
  constructor(t, i, n, r, a = { x: 0, y: 0, z: 0 }, o = !0) {
    super({
      id: t,
      name: i,
      position: a,
      type: "PoiPanelV2"
    });
    S(this, "backgroundImageElement", document.createElement("img"));
    S(this, "backgroundVideoElement", document.createElement("video"));
    S(this, "panelElement", document.createElement("div"));
    S(this, "rootElement", document.createElement("div"));
    S(this, "locationPointElement", document.createElement("div"));
    S(this, "rowElementArray", []);
    S(this, "rowLabelElementArray", []);
    S(this, "rowValueElementArray", []);
    S(this, "rowPostfixElementArray", []);
    this.component = r, this.userData = n, this.element.appendChild(this.rootElement), this.element.style.position = "absolute", this.rootElement.className = "root-twin-css3d", this.rootElement.appendChild(this.locationPointElement), this.locationPointElement.style.zIndex = "-1", this.panelElement.classList.add(
      o ? "engine-style_transition_show" : "engine-style_transition_hide"
    ), this.initPanelElement(this.panelElement), this.initBackgroundElement(this.backgroundImageElement), this.initBackgroundElement(this.backgroundVideoElement), this.scale.set(1 / Wr, 1 / Wr, 1), this.component.entity.addEventListener("removed", () => {
      this.element.remove();
    });
  }
  initPanelElement(t) {
    this.rootElement.appendChild(t), this._setData(t);
  }
  initBackgroundElement(t) {
    t.style.width = "100%", t.style.height = "100%", t.style.position = "absolute", t.style.left = "0px", t.style.top = "0px", this._setData(t), this.panelElement.appendChild(t);
  }
  setVisible(t) {
    this.visible = t, this.visible && (this.element.style.visibility = "visible"), !this.visible && (this.element.style.visibility = "hidden"), this.component.interactionAnimationOptions.type === "none" && this.setPanelVisible(t);
  }
  setPanelVisible(t) {
    t ? B1.setTransitionCssText(this.panelElement, "show") : B1.setTransitionCssText(this.panelElement, "hide");
  }
  getElementShow(t) {
    return t.className.includes("show");
  }
  updateBackgroundStyle(t) {
    const { imgUrl: i, location: n, size: r, offset: a } = t, o = st.getOssUrl(i);
    this.backgroundImageElement.src = o, this.rootElement.style.width = `${r.x * Wr}px`, this.rootElement.style.height = `${r.y * Wr}px`, this.rootElement.style.position = "absolute", this.panelElement.style.transform = `translate(${-n.x * 100}%, -${n.y * 100}%) translate(${a.x}px,${a.y}px)`, this.panelElement.style.width = "inherit", this.panelElement.style.height = "inherit";
  }
  updateLocationPointStyle(t) {
    const { imgUrl: i, size: n } = t, r = st.getOssUrl(i);
    this.locationPointElement.style.position = "absolute", this.locationPointElement.style.background = `url(${r}) no-repeat 50% 50% / 100% 100%`, this.locationPointElement.style.transform = `translate(-${0.5 * 100}%, -${0.5 * 100}%) translate(0px,0px)`, this.locationPointElement.style.width = `${n.x * Wr}px`, this.locationPointElement.style.height = `${n.y * Wr}px`;
  }
  updateRowStyle(t, i = this.userData) {
    this.rowElementArray.forEach((n) => n.style.display = "none"), t.forEach((n, r) => {
      var m;
      const { labelKey: a, labelValue: o, position: l, labelStyle: c, valueStyle: h, postfixStyle: u } = n, { textAlign: d = "left" } = h, f = i[a], p = this._getElement(this.rowElementArray, r);
      p.style.display = "flex", p.style.flexDirection = "row", p.style.width = "100%", p.style.left = `${l.x * Wr}px`, p.style.top = `${l.y * Wr}px`, p.style.position = "relative";
      const v = this._getElement(this.rowLabelElementArray, r, "div", p);
      v.innerHTML = (o == null ? void 0 : o.replace(/\s/g, "&nbsp;")) ?? "", v.style.display = "flex", this._setTextCssStyle(c, v);
      const g = this._getElement(this.rowValueElementArray, r, "div", p);
      if (g.innerHTML = (f == null ? void 0 : f.replace(/\s/g, "&nbsp;")) ?? "", this.getTextLeft(g, h.leftPadding * Wr, d).then(
        (x) => {
          g.style.left = `${x}px`;
        }
      ), g.style.position = "relative", g.style.display = "flex", this._setTextCssStyle(h, g), u.visible) {
        const x = this._getElement(this.rowPostfixElementArray, r, "div", g);
        x.style.marginLeft = `${u.leftPadding * Wr}px`, x.innerHTML = (m = u.content) == null ? void 0 : m.replace(/\s/g, "&nbsp;"), this._setTextCssStyle(u, x);
      }
    });
  }
  async getTextLeft(t, i, n) {
    const r = () => {
      const o = t.clientWidth;
      let l = 0;
      switch (n) {
        case "center":
          l = i - o / 2;
          break;
        case "left":
          l = i;
          break;
        case "right":
          l = i - o;
          break;
      }
      return l;
    };
    let a = 0;
    return new Promise((o) => {
      const l = setInterval(() => {
        (t.clientWidth > 0 || a > 10) && (o(r()), clearInterval(l)), a++;
      }, 100);
    });
  }
  _setData(t) {
    t.dataset.id = this.uuid, t.dataset.entityId = this.component.entity.uuid, t.dataset.componentId = this.component.id;
  }
  _setTextCssStyle(t, i) {
    i.style.fontFamily = t.fontFamily, i.style.fontSize = `${t.fontSize * Wr}px`, i.style.color = t.fontColor, i.style.opacity = t.fontOpacity.toString(), i.style.letterSpacing = `${Number(t.letterSpacing) * Wr}px`, i.style.fontStyle = t.fontStyle, i.style.fontWeight = t.fontWeight, i.style.lineHeight = `${Number(t.lineHeight) * Wr}px`;
  }
  _getElement(t, i, n = "div", r = this.panelElement) {
    let a = t[i];
    return a ? (r.appendChild(a), a) : (a = document.createElement(n), this._setData(a), t.push(a), r.appendChild(a), a);
  }
  intersectObject(t, i, n, r) {
    if (this.panelElement.className.includes("show")) {
      const o = this.panelElement.getBoundingClientRect();
      i >= o.left && i <= o.right && n >= o.top && n <= o.bottom && r.push({
        distance: 0,
        point: this.position.clone(),
        object: this
      });
    }
    const a = this.locationPointElement.getBoundingClientRect();
    i >= a.left && i <= a.right && n >= a.top && n <= a.bottom && r.push({
      distance: 0,
      point: this.position.clone(),
      object: this
    });
  }
  pointInElement(t, i, n) {
    const r = n.getBoundingClientRect();
    return t >= r.left && t <= r.right && i >= r.top && i <= r.bottom;
  }
  attachTransformControls() {
    this.element.style.opacity = "0.8";
  }
  detachTransformControls() {
    this.element.style.opacity = "1.0";
  }
  dispose() {
    this.element.remove();
  }
}
Qme([
  Vme(["engine-style_transition_show", " engine-style_transition_hide"])
], Ix.prototype, "setPanelVisible");
const Ox = new Se(25, 25);
class DF extends Pl {
  constructor(t) {
    super({
      id: t.id,
      name: t.name,
      position: t.position,
      type: "Floor"
    });
    S(this, "planeMesh", new Re());
    S(this, "constrainedScale", !1);
    this.add(this.planeMesh), this.planeMesh.material = new kt({
      color: "red",
      side: ui,
      transparent: !0,
      depthWrite: !1
    }), this.planeMesh.geometry = new $n(1, 1), this.planeMesh.geometry.rotateX(Math.PI / 2), this.userData = t.customAttribute, this.constrainedScale = t.constrainedScale, this.rotation.set(t.rotation.x, t.rotation.y, t.rotation.z), console.log(this.rotation), this.createGeometry(t.width, t.height);
  }
  createGeometry(t, i) {
    this.planeMesh.scale.set(t, 1, i), this.planeMesh.geometry.computeBoundingBox();
  }
  updateMaterial(t) {
    const { fillType: i, imgUrl: n, color: r, imgRepeatCount: a, opacity: o, blendMode: l, adjustColor: c } = t;
    if (this.planeMesh.material.blending = Lx(l), i === "color" && (this.planeMesh.material.color = new _e(r), this.planeMesh.material.opacity = Number(o), this.planeMesh.material.map = null), i === "texture") {
      let h = this.planeMesh.visible;
      this.planeMesh.visible = !1, st.loadTexture(n).then((u) => {
        this.planeMesh.material.map = u, u.wrapS = un, u.wrapT = un, u.colorSpace = "srgb", c != null && c.pure ? this.planeMesh.material.color = new _e(c.pure) : this.planeMesh.material.color = new _e("#ffffff"), u.repeat.set(a.x, a.y), this.planeMesh.visible = h;
      });
    }
  }
  getWidth() {
    return this.planeMesh.scale.x;
  }
  getHeight() {
    return this.planeMesh.scale.z;
  }
}
class $me extends Pl {
  constructor(t, i, n, r) {
    super({ id: t, name: i, type: "IframeV2", inActive: !1, position: n });
    S(this, "url", "");
    S(this, "backgroundImageElement", document.createElement("img"));
    S(this, "iframeElement", document.createElement("iframe"));
    S(this, "rootElement", document.createElement("div"));
    S(this, "locationPointElement", document.createElement("div"));
    S(this, "_iframeVisible", !0);
    S(this, "iframeEntity", new cp({
      id: nt.generateUUID(),
      type: "IframeV2"
    }));
    S(this, "locationPointEntity", new cp({
      id: nt.generateUUID(),
      type: "Point"
    }));
    this.component = r, this.iframeEntity.element.appendChild(this.iframeElement), this.locationPointEntity.element.appendChild(this.locationPointElement), this.component.entity.addEventListener("added", () => {
      this.iframeEntity.element.appendChild(this.iframeElement), this.locationPointEntity.element.appendChild(this.locationPointElement);
    }), this.rootElement.style.position = "absolute", this.add(this.iframeEntity), this.add(this.locationPointEntity), this.initElementStyle(this.locationPointElement), this.initElementStyle(this.iframeElement), this.component.entity.addEventListener("removed", () => {
      this.iframeElement.remove(), this.locationPointElement.remove();
    });
  }
  get iframeVisible() {
    return this._iframeVisible;
  }
  initElementStyle(t) {
    t.style.position = "absolute", t.style.zIndex = "-1", t.style.width = "100%", t.style.height = "100%", t.style.left = "0px", t.style.top = "0px";
  }
  setRootElementStyle(t) {
    const { location: i, size: n } = t;
    this.iframeElement.style.width = `${n.x}px`, this.iframeElement.style.height = `${n.y}px`, this.iframeElement.style.transform = `translate(-${i.x * 100}%, -${i.y * 100}%) translate(0px,0px)`;
  }
  setIframeStyle(t) {
    const { authority: i, enablePointEvent: n, offset: r = { x: 0, y: 0 }, autoResize: a = !0 } = t;
    this.iframeElement.style.left = `${r.x}px`, this.iframeElement.style.top = `${r.y}px`, n ? this.iframeElement.style.pointerEvents = "all" : this.iframeElement.style.pointerEvents = "none", Tt.enabled && (this.iframeElement.style.pointerEvents = "none");
    const { camera: o, microphone: l } = i;
    let c = "";
    o && (c += "camera;"), l && (c += "microphone;"), this.iframeElement.allow = c, this.iframeEntity.autoResize = a;
  }
  setIframeUrl(t) {
    this.url = t, this.iframeElement.src = t;
  }
  intersectObject(t, i, n, r) {
    if (this.iframeElement.className.includes("show") && this.visible) {
      const o = this.iframeElement.getBoundingClientRect();
      i >= o.left && i <= o.right && n >= o.top && n <= o.bottom && r.push({
        distance: 0,
        point: this.position.clone(),
        object: this
      });
    }
    const a = this.locationPointElement.getBoundingClientRect();
    i >= a.left && i <= a.right && n >= a.top && n <= a.bottom && r.push({
      distance: 0,
      point: this.position.clone(),
      object: this
    });
  }
  setVisible(t) {
    this.visible = t, t ? (this.iframeElement.style.display = "flex", this.locationPointElement.style.display = "flex") : (this.iframeElement.style.display = "none", this.locationPointElement.style.display = "none"), this._iframeVisible = t;
  }
  setIframeVisible(t) {
    Tt.enabled ? t ? (B1.setTransitionCssText(this.iframeElement, "show"), this.iframeElement.style.opacity = "1.0") : (B1.setTransitionCssText(this.iframeElement, "hide"), this.iframeElement.style.opacity = "0.0") : (B1.setTransitionCssText(this.iframeElement, t ? "show" : "hide"), this.iframeElement.style.opacity = t ? "1.0" : "0.0", this.iframeElement.style.display = t ? "flex" : "none"), this._iframeVisible = t;
  }
  getElementShow(t) {
    return t.className.includes("show");
  }
  updateLocationPointStyle(t) {
    const { imgUrl: i, size: n } = t, r = st.getOssUrl(i);
    this.locationPointElement.style.position = "absolute", this.locationPointElement.style.background = `url(${r}) no-repeat 50% 50% / 100% 100%`, this.locationPointElement.style.transform = `translate(-${0.5 * 100}%, -${0.5 * 100}%) translate(0px,0px)`, this.locationPointElement.style.width = `${n.x}px`, this.locationPointElement.style.height = `${n.y}px`;
  }
  updateConfig(t) {
    this.setIframeStyle(t), this.setRootElementStyle(t);
  }
  pointInElement(t, i, n) {
    const r = n.getBoundingClientRect();
    return t >= r.left && t <= r.right && i >= r.top && i <= r.bottom;
  }
  attachTransformControls() {
    this.iframeElement.style.opacity = "0.2", this.locationPointElement.style.opacity = "0.2";
  }
  detachTransformControls() {
    Tt.enabled && (this.iframeElement.style.opacity = this._iframeVisible ? "1.0" : "0.0", this.locationPointElement.style.opacity = "1.0");
  }
  dispose() {
    this.iframeElement.remove(), this.locationPointElement.remove();
  }
}
const LF = 1e-5, km = Math.PI / 180;
function Xa(s, e, t) {
  return Math.max(e, Math.min(t, s));
}
function An(s, e = LF) {
  return Math.abs(s) < e;
}
function Qi(s, e, t = LF) {
  return An(s - e, t);
}
function VP(s, e) {
  return Math.round(s / e) * e;
}
function Gm(s) {
  return isFinite(s) ? s : s < 0 ? -Number.MAX_VALUE : Number.MAX_VALUE;
}
function Hm(s) {
  return Math.abs(s) < Number.MAX_VALUE ? s : s * (1 / 0);
}
function M3(s, e, t, i, n = 1 / 0, r = 0) {
  i = Math.max(1e-4, i);
  const a = 2 / i, o = a * r, l = 1 / (1 + o + 0.48 * o * o + 0.235 * o * o * o);
  let c = s - e;
  const h = e, u = n * i;
  c = Xa(c, -u, u), e = s - c;
  const d = (t.value + a * c) * r;
  t.value = (t.value - a * d) * l;
  let f = e + (c + d) * l;
  return h - s > 0 == f > h && (f = h, t.value = (f - h) / r), f;
}
function WP(s, e, t, i, n = 1 / 0, r = 0, a = new _()) {
  i = Math.max(1e-4, i);
  const o = 2 / i, l = o * r, c = 1 / (1 + l + 0.48 * l * l + 0.235 * l * l * l);
  let h = e.x, u = e.y, d = e.z, f = s.x - h, p = s.y - u, v = s.z - d;
  const g = h, m = u, x = d, y = n * i, A = y * y, b = f * f + p * p + v * v;
  if (b > A) {
    const H = Math.sqrt(b);
    f = f / H * y, p = p / H * y, v = v / H * y;
  }
  h = s.x - f, u = s.y - p, d = s.z - v;
  const M = (t.x + o * f) * r, w = (t.y + o * p) * r, C = (t.z + o * v) * r;
  t.x = (t.x - o * M) * c, t.y = (t.y - o * w) * c, t.z = (t.z - o * C) * c, a.x = h + (f + M) * c, a.y = u + (p + w) * c, a.z = d + (v + C) * c;
  const T = g - s.x, E = m - s.y, P = x - s.z, L = a.x - g, I = a.y - m, N = a.z - x;
  return T * L + E * I + P * N > 0 && (a.x = g, a.y = m, a.z = x, t.x = (a.x - g) / r, t.y = (a.y - m) / r, t.z = (a.z - x) / r), a;
}
const XP = new _(), jP = new _();
class u8 extends Fi {
  constructor() {
    super(...arguments);
    S(this, "_lastDistance", -1);
    S(this, "enableScale", !1);
    S(this, "scaleDelta", 1e-4);
    S(this, "scaleRange", [0, 1e4]);
    S(this, "scaleOptions", {
      enable: !1,
      minScale: 1,
      minScaleScope: 1,
      maxScale: 10,
      maxScaleScope: 1e4
    });
    S(this, "scaleOffset", new _());
    S(this, "lastScale", 1);
  }
  setScaleOptions(t, i = !0) {
    const { enable: n, minScale: r, minScaleScope: a, maxScale: o, maxScaleScope: l } = t;
    Object.assign(this.scaleOptions, t), this.enableScale = n;
    const c = o > r ? o - r : 1, h = l > a ? l - a : 1;
    this.scaleDelta = c / h, this.scaleRange = [a, l], dt.Entity.refresh(), this.needDataUpdateServer = i, n || this.group.children.forEach((u) => {
      u.scale.sub(this.scaleOffset);
    }), this.scaleOffset.set(0, 0, 0);
  }
  updateSpriteScale(t) {
    this.enableScale && (t.getPosition(XP), this.group.children.forEach((i) => {
      jP.setFromMatrixPosition(i.matrixWorld);
      let n = XP.distanceTo(jP), r = this.scaleDelta * n;
      n < this.scaleOptions.minScaleScope && (r = this.scaleOptions.minScale), n > this.scaleOptions.maxScaleScope && (r = this.scaleOptions.maxScale), r = Xa(r, this.scaleOptions.minScale, this.scaleOptions.maxScale), Tt.enabled || (r /= 10), i.scale.set(r, r, 1), r !== 0 && (this.lastScale = r);
    }));
  }
}
var i1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const n1e = "PoiPanel";
let Nx = class extends u8 {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  static getName() {
    return "信息面板组件";
  }
  add(e, t, i, n) {
    if (this.states.curStateId) {
      const r = this.states.deepGet(this.states.curStateId), { alwaysTop: a, imgUrl: o, size: l, offset: c, padding: h, rowStyleOptions: u } = r == null ? void 0 : r.config, d = new Wme(
        e,
        n ?? `信息面板${this.childMap.size}`,
        {
          imgUrl: o,
          alwaysTop: a,
          size: l,
          offset: c,
          position: t,
          padding: h
        },
        i,
        u
      );
      d.userData = i, this.childMap.set(e, d), this.group.attach(d);
    }
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    this.childMap.forEach((i) => {
      i.updateConfig(t), i.updateRowStyle(t.rowStyleOptions);
    }), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    );
  }
  getSize() {
    const e = this.states.getCurrentStateConfig(), t = new _(1, 1, 1);
    return e && t.set(e.size.x, e.size.y, 1), t;
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(
      Array.from(this.childMap.values()),
      t
    );
    i && n.forEach((r) => {
      r.updateConfig(i.config);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  // poiPanel 的id 不是行的
  updatePanelRowValue(e, t, i) {
    const n = this.getObjectById(e);
    n && n.rowMeshMap.forEach((r) => {
      r.dataOptions[t] = i;
    });
  }
  removePanelRow(e, t, i) {
    const n = this.getObjectById(e);
    n.rowMeshMap.forEach((r) => {
      delete r.dataOptions[t];
    }), n.rowMeshMap.delete(i);
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  setData(e) {
    this.childMap.forEach((t) => this.group.remove(t)), this.childMap.clear(), e.children.forEach((t) => {
      const { id: i, name: n, position: r, customAttribute: a } = t;
      this.add(i, r, a, n);
    });
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      scaleOptions: this.scaleOptions,
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        rows: t.getRowDataOptions(),
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach((d) => this.add(d.id, d.position, d.rows, d.name));
    const { scaleOptions: l } = this;
    Object.assign(l, o.scaleOptions), this.setScaleOptions(l), this.positionMode = o.positionMode ?? "xyz";
    const { x: c, y: h, z: u } = uh(o, this);
    c && this.group.position.setX(c), h && this.group.position.setY(h), u && this.group.position.setZ(u), this.isInit = !0;
  }
  draw(e, t, i) {
    e.root = this.entity, e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this);
    const n = /* @__PURE__ */ new Set(), r = (c) => {
      const { position: h, coord: u } = c, d = e.raycaster(u, this.group);
      let f = !1;
      if (!d)
        f = !0;
      else {
        const p = this.getObjectById(d.object.uuid);
        if (p) {
          const v = this.childMap.get(p.uuid);
          v && (f = !v.visible);
        }
      }
      f && e.executeDrawCommand("add-point", { position: h });
    }, a = (c) => {
      const h = this.getObjectById(c.point.uuid);
      h && (h.visible = !1), h && n.add(h.uuid);
    }, o = (c) => {
      if (n.has(c.point.uuid)) {
        const u = this.getObjectById(c.point.uuid);
        u && (u.visible = !0), n.delete(c.point.uuid);
      } else
        this.add(c.point.uuid, c.point.sprite.position, t);
    }, l = () => {
      n.forEach((c) => {
        this.removeObjectById(c);
      }), i && i(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", r), e.removeEventListener("end-draw", l), e.removeEventListener("add-point", o), e.removeEventListener("remove-point", a);
    };
    e.addEventListener("draw-point", r), e.addEventListener("end-draw", l), e.addEventListener("add-point", o), e.addEventListener("remove-point", a);
  }
  dispose() {
    this.childMap.forEach((e) => {
      var t;
      (t = e.parent) == null || t.remove(e), e.dispose();
    });
  }
};
Nx = i1e([
  ni(n1e)
], Nx);
var a1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const o1e = "InfiniteGrid"; nt.generateUUID(); const l1e = new St().setFromEuler(new Mi(0, Math.PI * 0.5, 0)), c1e = new St().setFromEuler(new Mi(0, 0, 0));
let i7 = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "infiniteGridMesh", new Bhe());
    S(this, "_isOrthographicClicked", !1);
  }
  initialize() {
    this.group.add(this.infiniteGridMesh), this.group.name = "InfiniteGrid", this.handleViewHelperAxisClick(), this.entity.renderer.cameraControls.addEventListener("update", () => {
      this._updateGrid();
    });
  }
  _updateGrid() {
    const { quaternion: e, rotation: t } = this.entity.renderer.mainCamera;
    e.angleTo(l1e) < 0.1 && this._isOrthographicClicked ? (this.infiniteGridMesh.forceFadeFactor = !0, Qe.Info.gridAxisPlane = "yz", this.infiniteGridMesh.quaternion.set(0, 0, 0.7, 0.7)) : e.angleTo(c1e) < 0.1 && this._isOrthographicClicked ? (Qe.Info.gridAxisPlane = "xy", this.infiniteGridMesh.forceFadeFactor = !0, this.infiniteGridMesh.quaternion.set(2.7, 0, 0, 1)) : (this.infiniteGridMesh.forceFadeFactor = !1, this.infiniteGridMesh.quaternion.set(0, 0, 0, 1), Qe.Info.gridAxisPlane = "xz", this._isOrthographicClicked = !1), this.infiniteGridMesh.setGridScale(
      Qe.Info.gridAxisPlane,
      this.entity.getCurCamera(),
      this.entity.renderer.cameraControls.getTarget(new _())
    );
  }
  getUnit() {
    return this.infiniteGridMesh.relativeUnit;
  }
  // 点击辅助器的轴切换正交单独处理
  handleViewHelperAxisClick() {
    const e = (t) => {
      const i = () => {
        switch (t) {
          case "posX":
          case "negX":
            Qe.Info.gridAxisPlane = "yz";
            break;
          case "posY":
          case "negY":
            Qe.Info.gridAxisPlane = "xz";
            break;
          case "posZ":
          case "negZ":
            Qe.Info.gridAxisPlane = "xy";
            break;
        }
        this._isOrthographicClicked = !0, this._updateGrid();
      };
      this.entity.renderer.viewHelper.event.once("toggleViewEnd", i);
    };
    this.entity.renderer.viewHelper.event.removeEventListener("axisClick", e), this.entity.renderer.viewHelper.event.addEventListener("axisClick", e);
  }
};
i7 = a1e([
  ni(o1e)
], i7);
var d1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const f1e = "FlowFence";
let Vg = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  static getName() {
    return "围栏组件";
  }
  add(e, t = !0) {
    if (this.states.curStateId) {
      const { id: i, points: n, position: r = { x: 0, y: 0, z: 0 }, userData: a = {}, name: o } = e, l = this.states.deepGet(this.states.curStateId), {
        height: c = 10,
        fenceStyle: h,
        customStyle: u,
        type: d,
        closed: f = !1,
        blendMode: p = Ac.NormalBlending
      } = l == null ? void 0 : l.config, v = new FA(i, o ?? `围栏${this.childMap.size}`, {
        points: n,
        position: r,
        height: c,
        type: d,
        fenceStyle: h,
        customStyle: u,
        closed: f,
        blendMode: p,
        groupPosition: { x: 0, y: 0, z: 0 }
      });
      v.userData = a, this.childMap.set(i, v), t ? this.group.attach(v) : this.group.add(v);
    }
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(
      Array.from(this.childMap.values()),
      t
    );
    i && n.forEach((r) => {
      r.updateConfig(i.config);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  setData(e) {
    this.childMap.forEach((t) => {
      this.group.remove(t), t.dispose();
    }), this.childMap.clear(), e.children.forEach((t) => {
      const { id: i, name: n, points: r, position: a } = t;
      this.add({
        id: i,
        points: r,
        position: a,
        userData: t,
        name: n
      });
    });
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  getPointsById(e) {
    const t = this.getObjectById(e);
    return t instanceof FA ? t == null ? void 0 : t.getPoints().map((n) => new _(n.x, n.y, n.z)) : [];
  }
  updatePointsById(e, t) {
    const i = this.getObjectById(e);
    i instanceof FA && (i == null || i.setPoints(t));
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0, this.dispatchEvent(_i.RemoveChild, { id: e });
    }
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    t && this.childMap.forEach((i) => i.updateConfig(t)), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    );
  }
  updateDataById(e, t, i, n = []) {
    const r = this.childMap.get(e);
    if (this.states.getCurrentStateConfig() && r) {
      const o = n.map(
        (l) => new _(l.x, l.y, l.z)
      );
      r.name = t, r.position.set(i.x, i.y, i.z), r.setPoints(o), this.needDataUpdateServer = !0, dt.Entity.refresh();
    }
  }
  draw(e, t, i) {
    e.mode = "multi-line", e.showDrawPoint(this.group), e.showMoveLine(this.group), e.setActiveComponent(this), e.startDraw();
    let n = nt.generateUUID();
    const r = {
      index: -1,
      position: new _(),
      id: ""
    }, a = /* @__PURE__ */ new Map();
    e.disableType("add-point"), this.childMap.forEach((x) => {
      const y = x.getPoints(), A = new hg();
      a.set(x.uuid, A), y.forEach((b) => {
        const M = nt.generateUUID(), w = new _(b.x, b.y, b.z);
        A.set(M, w), e.executeDrawCommand("add-point", {
          position: w,
          id: M,
          drawId: x.uuid
        });
      });
    }), e.drawRootPoint.children.forEach((x) => {
      const { drawId: y } = x.userData, A = this.childMap.get(y);
      A && x.position.copy(A.position);
    }), e.enableType("add-point");
    const o = (x) => {
      let { position: y, coord: A } = x;
      this.childMap.get(n) || u();
      const b = e.raycaster(A, this.group);
      let M = !1;
      const w = e.getLineMidPoint();
      (!b || w.visible) && (M = !0);
      const C = e.ray.intersectObject(e.getLineMidPoint());
      w.visible && b && C[0].object.userData.drawId !== r.id && (M = !1), w.visible && b && (n = r.id, y = w.position), !w.visible && (r.index = -1), M && (e.executeDrawCommand("add-point", { position: y, drawId: n }), e.transformControls.detach());
    }, l = (x) => {
      const y = x.point.parent, A = this.childMap.get(y.userData.drawId), b = a.get(y.userData.drawId), M = b == null ? void 0 : b.get(y.uuid);
      M && M.copy(y.sprite.position), A && b && A.setPoints(b.values());
    }, c = (x) => {
      const { id: y } = x;
      [...e.drawRootPoint.children].forEach((A) => {
        A.userData.drawId === y && e.drawRootPoint.remove(A);
      });
    }, h = (x) => {
      e.getLineMidPoint().visible = !1;
      const y = x.point;
      this.childMap.get(n) || u();
      const A = this.childMap.get(n);
      if (A) {
        const b = a.get(A.uuid);
        b && (r.index !== -1 ? (b.insert(r.index, y.uuid, r.position.clone()), b.size > 1 && A.setPoints(b.values()), r.index = -1) : (b.set(y.uuid, y.sprite.position.clone()), b.size > 1 && A.setPoints(b.values())));
      }
    }, u = () => {
      const x = this.states.getCurrentStateConfig();
      let y = this.childMap.get(n);
      x != null && x.closed && this.childMap.forEach((b) => b.closeGeometry()), y && (n = nt.generateUUID()), this.add(
        {
          id: n,
          points: []
        },
        !1
      ), y = this.childMap.get(n);
      const A = new hg();
      a.set(n, A);
    }, d = () => {
      n = nt.generateUUID();
    }, f = new Qs(), p = (x, y, A) => (f.set(y, A), f.closestPointToPoint(x, !0, new _()).distanceTo(x)), v = (x) => {
      const y = this.childMap.get(x.id), A = y == null ? void 0 : y.getVec3Points();
      let b = 1 / 0, M = -1;
      if (A) {
        A.forEach((C, T) => {
          if (T > 0) {
            const E = p(x.position, A[T - 1], C);
            b > E && (b = E, M = T);
          }
        });
        const w = new _().addVectors(A[M - 1], A[M]).multiplyScalar(0.5);
        e.showLineMidPoint(w, this.group), r.index = M, r.position = w, r.id = x.id;
      }
    }, g = () => {
      this.needDataUpdateServer = !0;
    }, m = () => {
      i && i(), a.clear(), r.index = -1, e.removeEventListener("draw-line", o), e.removeEventListener("end-draw", m), e.removeEventListener("add-point", h), e.removeEventListener("drag", l), e.addEventListener("end-drag", g), this.removeEventListener(_i.RemoveChild, c), e.removeEventListener("double-click", d), e.removeEventListener("hover-component", v);
      const x = this.states.getCurrentStateConfig();
      x != null && x.closed && this.childMap.forEach((y) => y.closeGeometry()), this.needDataUpdateServer = !0;
    };
    e.addEventListener("draw-line", o), e.addEventListener("drag", l), e.addEventListener("end-drag", g), e.addEventListener("end-draw", m), e.addEventListener("add-point", h), this.addEventListener(_i.RemoveChild, c), e.addEventListener("double-click", d), e.addEventListener("hover-component", v);
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        points: t.getPoints(),
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (u) => this.add({
        id: u.id,
        points: u.points,
        position: u.position,
        userData: u.customAttribute
      })
    );
    const { x: l, y: c, z: h } = uh(o, this);
    l && this.group.position.setX(l), c && this.group.position.setY(c), h && this.group.position.setZ(h), this.positionMode = o.positionMode ?? "xyz", this.isInit = !0;
  }
};
Vg = d1e([
  ni(f1e)
], Vg);
var p1e = Object.defineProperty, m1e = Object.getOwnPropertyDescriptor, IF = (s, e, t, i) => {
  for (var n = i > 1 ? void 0 : i ? m1e(e, t) : e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (i ? a(e, t, n) : a(n)) || n);
  return i && n && p1e(e, t, n), n;
};
const n7 = "IframeV2";
let Wg = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "_interactionAnimationOptions", {
      type: "none",
      loop: !1,
      interval: 5
    });
    S(this, "_time", performance.now());
    S(this, "_showIndex", 0);
    S(this, "_loopEnable", !0);
    S(this, "_isDrawing", !1);
    S(this, "childMap", /* @__PURE__ */ new Map());
    S(this, "getExtendVisible", (e, t) => e.parent ? this.getExtendVisible(e.parent, e.parent.visible && t) : t);
  }
  static getName() {
    return "iframe组件";
  }
  add(e, t, i = "", n = {}, r) {
    if (this.states.curStateId) {
      const a = this.states.deepGet(this.states.curStateId);
      if (a) {
        let o = this.childMap.get(e);
        if (o)
          return;
        o = new $me(e, r ?? `iframe${this.childMap.size}`, t, this), o.updateConfig(a.config), o.setIframeUrl(i), o.updateLocationPointStyle(a.config.afterInteractionLocationPoint), o.userData = n, this.childMap.set(e, o), this.group.attach(o);
      }
    }
  }
  getBox(e = "") {
    const t = this.getObjectById(e), i = this.states.getCurrentStateConfig(), { size: n } = i;
    if (n) {
      if (t instanceof Ix)
        return new zt().setFromCenterAndSize(t.position, new _(n.x, n.y, 1));
      {
        const r = new zt();
        return this.childMap.forEach((a) => {
          r.union(
            new zt().setFromCenterAndSize(a.position, new _(n.x, n.y, 1))
          );
        }), r;
      }
    }
    return new zt();
  }
  dispatchToggleEvent(e) {
    this.dispatchEvent(
      "switch",
      Object.assign(JSON.parse(JSON.stringify(e.userData)), {
        id: e.uuid,
        name: e.name,
        position: {
          x: e.position.x,
          y: e.position.y,
          z: e.position.z
        }
      })
    );
  }
  intersectObject(e, t) {
    const i = [], { clientX: n, clientY: r } = t;
    return this.childMap.forEach((a) => {
      a.intersectObject(e, n, r, i);
    }), i.sort((a, o) => a.distance - o.distance), i;
  }
  onClick(e, t) {
    !this._isDrawing && this._handleLocationPointClick(t.clientX, t.clientY, e.object);
  }
  _handleLocationPointClick(e, t, i) {
    if (this._interactionAnimationOptions.type === "click") {
      if (i.pointInElement(e, t, i.locationPointElement)) {
        this.childMap.forEach((r) => {
          r.uuid !== i.uuid && r.setIframeVisible(!1);
        });
        const n = i.iframeVisible;
        i.setIframeVisible(!n), this.dispatchToggleEvent(i), this._loopEnable = n;
      }
      this._updateLocationPointStyle();
    }
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(
      Array.from(this.childMap.values()),
      t
    );
    i && n.forEach((r) => {
      r.updateConfig(i.config);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  setVisible(e) {
    const t = (n, r) => n.parent ? t(n.parent, n.parent.visible && r) : r, i = t(this.group, this.group.visible);
    return this.childMap.forEach((n) => {
      n.setVisible(e && i);
    }), this;
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  setData(e) {
    this.childMap.forEach((i) => {
      i.dispose(), this.group.remove(i);
    }), this.childMap.clear();
    const t = this.getExtendVisible(this.group, this.group.visible);
    e.children.forEach((i) => {
      const { id: n, name: r, position: a, customAttribute: o, url: l } = i;
      this.add(n, a, l, o, r);
    }), t ? (this.setInteractionAnimationOptions(this._interactionAnimationOptions, !1), this._updateLocationPointStyle()) : this.setVisible(!1);
  }
  easyvClosePanelElement(e) {
    const t = this.states.getCurrentStateConfig();
    e ? this.filterByAttribute(
      Array.from(this.childMap.values()),
      e
    ).forEach((n) => {
      n.setIframeVisible(!1), this.dispatchToggleEvent(n);
    }) : this.childMap.forEach((i) => {
      i.setIframeVisible(!1), i.updateLocationPointStyle(t.defaultLocationPoint), this.dispatchToggleEvent(i);
    });
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  updateDataById(e, t, i, n) {
    this.childMap.forEach((r) => {
      r.uuid === e && (r.name = t, r.position.set(i.x, i.y, i.z), n && r.setIframeUrl(n), this.needDataUpdateServer = !0);
    }), dt.Entity.refresh();
  }
  render(e, t) {
    var i;
    if (this.childMap.size > 0) {
      const n = performance.now();
      this._interactionAnimationOptions.loop && this.entity.visible && n - this._time > this._interactionAnimationOptions.interval * 1e3 && (!this._isDrawing && this._loopEnable && this._loop(), this._time = n), this._interactionAnimationOptions.type === "hover" && this.entity.visible && !this._isDrawing && this._hover(), ((i = this._interactionAnimationOptions) == null ? void 0 : i.type) !== "none" && this.entity.visible && this._setMouseInteractionStyle();
    }
  }
  _setMouseInteractionStyle() {
    var e;
    if (!Number.isNaN(io.MouseParams.clientX)) {
      const t = (e = document.getElementById(qg.ContainerId)) == null ? void 0 : e.querySelector("canvas"), { clientX: i, clientY: n, coord: r } = io.MouseParams;
      let a = !1;
      !Tt.enabled && t && this.childMap.forEach((o) => {
        o.pointInElement(i, n, o.locationPointElement) && (cs.setCursorCssText(t, "pointer", n7), a = !0);
      }), !a && !Tt.enabled && t && cs.setCursorCssText(t, "default", n7);
    }
  }
  _hover() {
    if (!Number.isNaN(io.MouseParams.clientX)) {
      const { clientX: e, clientY: t, coord: i } = io.MouseParams;
      let n;
      this.childMap.forEach((r) => {
        r.pointInElement(e, t, r.locationPointElement) && (n = r, this._loopEnable = !1);
      }), n ? (this.childMap.forEach((r) => {
        r.uuid === (n == null ? void 0 : n.uuid) ? r.setIframeVisible(!0) : r.setIframeVisible(!1);
      }), this._updateLocationPointStyle()) : this._loopEnable = !0;
    }
  }
  _loop() {
    if (!this.entity.visible)
      return;
    const e = Array.from(this.childMap.values());
    if (this.childMap.size === 1) {
      const t = e[0], i = t.getElementShow(t.iframeElement);
      t.setIframeVisible(!i), this.dispatchToggleEvent(t);
    } else {
      const t = this._getShowIndex();
      this.childMap.forEach((n) => {
        n.setIframeVisible(!1);
      });
      const i = e[t];
      i && (i.setIframeVisible(!0), this.dispatchToggleEvent(i));
    }
    this._updateLocationPointStyle();
  }
  _getShowIndex() {
    return this._showIndex + 1 > this.childMap.size ? (this._showIndex = 0, 0) : this._showIndex++;
  }
  setInteractionAnimationOptions(e, t = !0) {
    this._interactionAnimationOptions = e, (e.type === "click" || e.type === "hover") && this.childMap.forEach((i) => i.setIframeVisible(!1)), e.type === "none" && this.childMap.forEach((i) => {
      i.setIframeVisible(!0);
    }), this._updateLocationPointStyle(), this.needDataUpdateServer = !0, dt.Entity.refresh(), this.needDataUpdateServer = t;
  }
  _updateLocationPointStyle() {
    const e = this.states.getCurrentStateConfig();
    this.childMap.forEach((t) => {
      t.getElementShow(t.iframeElement) ? t.updateLocationPointStyle(e.afterInteractionLocationPoint) : t.updateLocationPointStyle(e.defaultLocationPoint);
    });
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      interactionAnimationOptions: this._interactionAnimationOptions,
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        url: t.url ?? "",
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      // attribute: this.customAttributes,
      version: this.version,
      states: this.states.toJson(),
      data: e
    };
  }
  onDragEnd() {
    const e = this.states.getCurrentStateConfig();
    e.groupPosition.x = this.group.position.x, e.groupPosition.y = this.group.position.y, e.groupPosition.z = this.group.position.z, this.states.needsUpdateStateSet.add(this.getCurStateId()), dt.Entity.refresh();
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t;
    this.initialize(e.renderer.cssRenderer.domElement);
    const a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (u) => this.add(u.id, u.position, u.url, u.customAttribute, u.name)
    ), this.addEventListener("switch", (u) => {
      e.emitFunc && e.emitFunc(
        "switch",
        {
          data: u
        },
        this.entity.uuid
      );
    }), this.setVisible(this.entity.visible), this.setInteractionAnimationOptions(o.interactionAnimationOptions, !1);
    const { x: l, y: c, z: h } = uh(o, this);
    l && this.group.position.setX(l), c && this.group.position.setY(c), h && this.group.position.setZ(h), this.entity.eType = "IframeV2", this.positionMode = o.positionMode ?? "xyz", this.isInit = !0;
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    t && (this.childMap.forEach((i) => {
      i.updateConfig(t), i.getElementShow(i.iframeElement) ? i.updateLocationPointStyle(t.afterInteractionLocationPoint) : i.updateLocationPointStyle(t.defaultLocationPoint);
    }), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    ));
  }
  draw(e, t, i) {
    e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this), this._isDrawing = !0;
    const n = /* @__PURE__ */ new Set(), r = (c) => {
      const { position: h, coord: u } = c, d = e.raycaster(u, this.group);
      let f = !1;
      if (!d)
        f = !0;
      else {
        const p = this.getObjectById(d.object.uuid);
        if (p) {
          const v = this.childMap.get(p.uuid);
          v && (f = !v.visible);
        }
      }
      f && e.executeDrawCommand("add-point", { position: h });
    }, a = (c) => {
      const h = this.getObjectById(c.point.uuid);
      if (h) {
        const u = this.childMap.get(h.uuid);
        u == null || u.setVisible(!1), n.add(h.uuid);
      }
    }, o = (c) => {
      if (n.has(c.point.uuid)) {
        const u = this.getObjectById(c.point.uuid);
        u && (u.visible = !0), n.delete(c.point.uuid);
      } else
        this.add(c.point.uuid, c.point.sprite.position, t), this.setInteractionAnimationOptions(this._interactionAnimationOptions);
    }, l = () => {
      n.forEach((c) => {
        this.removeObjectById(c);
      }), this.setInteractionAnimationOptions(this._interactionAnimationOptions), this._isDrawing = !1, i && i(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", r), e.removeEventListener("end-draw", l), e.removeEventListener("add-point", o), e.removeEventListener("remove-point", a);
    };
    e.addEventListener("draw-point", r), e.addEventListener("end-draw", l), e.addEventListener("add-point", o), e.addEventListener("remove-point", a);
  }
  dispose() {
    this.childMap.forEach((e) => e.dispose()), this.childMap.clear();
  }
};
IF([
  Qp(0.3)
], Wg.prototype, "_hover", 1);
Wg = IF([
  ni(n7)
], Wg);
var y1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const x1e = "Iframe";
let Bx = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "cameraElement");
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  static getName() {
    return "iframe组件";
  }
  initialize(e) {
    this.cameraElement = e;
  }
  add(e, t, i = "", n = {}, r) {
    if (!this.cameraElement) {
      console.error(`iframe组件${this.id}未完成初始化`);
      return;
    }
    if (this.states.curStateId) {
      const a = this.states.deepGet(this.states.curStateId);
      if (a) {
        const { faceToCamera: o, autoResize: l, size: c } = a.config;
        let h = this.childMap.get(e);
        if (h)
          return;
        h = new h8(
          e,
          r ?? `iframe${this.childMap.size}`,
          this.entity.uuid,
          this.id,
          {
            url: i,
            faceToCamera: o,
            autoResize: l,
            position: t,
            size: c
          },
          this.cameraElement
        ), h.userData = n, this.childMap.set(e, h), this.group.attach(h), console.log("iframe", h.visible);
      }
    }
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(
      Array.from(this.childMap.values()),
      t
    );
    i && n.forEach((r) => {
      r.updateConfig(i.config);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  setVisible(e) {
    const t = (n, r) => n.parent ? t(n.parent, n.parent.visible && r) : r, i = t(this.group, this.group.visible);
    return this.childMap.forEach((n) => {
      n.setVisible(e && i);
    }), this;
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  setData(e) {
    this.childMap.forEach((t) => this.group.remove(t)), this.childMap.clear(), e.children.forEach((t) => {
      const { id: i, name: n, position: r, url: a } = t;
      this.add(i, r, a, t, n);
    });
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        url: t.url ?? "",
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      // attribute: this.customAttributes,
      version: this.version,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t;
    this.initialize(e.renderer.cssRenderer.domElement);
    const a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (u) => this.add(u.id, u.position, u.url, u.customAttribute, u.name)
    ), this.setVisible(this.entity.visible), this.positionMode = o.positionMode ?? "xyz";
    const { x: l, y: c, z: h } = uh(o, this);
    l && this.group.position.setX(l), c && this.group.position.setY(c), h && this.group.position.setZ(h), this.isInit = !0;
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    t && this.childMap.forEach((i) => {
      i.updateConfig(t);
    }), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    );
  }
  updateDataById(e, t, i, n) {
    this.childMap.forEach((r) => {
      r.uuid === e && (r.name = t, r.position.set(i.x, i.y, i.z), n && r.updateUrl(n), this.needDataUpdateServer = !0);
    }), dt.Entity.refresh();
  }
  draw(e, t, i) {
    e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this);
    const n = /* @__PURE__ */ new Set(), r = (c) => {
      const { position: h, coord: u } = c, d = e.raycaster(u, this.group);
      let f = !1;
      if (!d)
        f = !0;
      else {
        const p = this.getObjectById(d.object.uuid);
        if (p) {
          const v = this.childMap.get(p.uuid);
          v && (f = !v.visible);
        }
      }
      f && e.executeDrawCommand("add-point", { position: h });
    }, a = (c) => {
      const h = this.getObjectById(c.point.uuid);
      if (h) {
        const u = this.childMap.get(h.uuid);
        u == null || u.setVisible(!1), n.add(h.uuid);
      }
    }, o = (c) => {
      if (n.has(c.point.uuid)) {
        const u = this.getObjectById(c.point.uuid);
        u && (u.visible = !0), n.delete(c.point.uuid);
      } else
        this.add(c.point.uuid, c.point.sprite.position, t);
    }, l = () => {
      n.forEach((c) => {
        this.removeObjectById(c);
      }), i && i(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", r), e.removeEventListener("end-draw", l), e.removeEventListener("add-point", o), e.removeEventListener("remove-point", a);
    };
    e.addEventListener("draw-point", r), e.addEventListener("end-draw", l), e.addEventListener("add-point", o), e.addEventListener("remove-point", a);
  }
  dispose() {
    this.childMap.forEach((e) => e.dispose()), this.childMap.clear();
  }
};
Bx = y1e([
  ni(x1e)
], Bx);
var A1e = Object.defineProperty, b1e = Object.getOwnPropertyDescriptor, OF = (s, e, t, i) => {
  for (var n = i > 1 ? void 0 : i ? b1e(e, t) : e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (i ? a(e, t, n) : a(n)) || n);
  return i && n && A1e(e, t, n), n;
};
const M1e = "PoiPanelV2";
let Fx = class extends u8 {
  constructor() {
    super(...arguments);
    S(this, "_interactionAnimationOptions", {
      type: "none",
      loop: !1,
      interval: 5
    });
    S(this, "_time", performance.now());
    S(this, "_loopEnable", !0);
    S(this, "_showIndex", 0);
    S(this, "childMap", /* @__PURE__ */ new Map());
    S(this, "getExtendVisible", (e, t) => e.parent ? this.getExtendVisible(e.parent, e.parent.visible && t) : t);
  }
  get interactionAnimationOptions() {
    return this._interactionAnimationOptions;
  }
  getBox(e = "") {
    const t = this.getObjectById(e), i = this.states.getCurrentStateConfig(), { size: n } = i.background;
    if (n) {
      if (t instanceof Ix)
        return new zt().setFromCenterAndSize(t.position, new _(n.x, n.y, 1));
      {
        const r = new zt();
        return this.childMap.forEach((a) => {
          r.union(
            new zt().setFromCenterAndSize(a.position, new _(n.x, n.y, 1))
          );
        }), r;
      }
    }
    return new zt();
  }
  add(e, t, i, n, r = !0, a) {
    if (this.states.curStateId) {
      const o = this.states.deepGet(this.states.curStateId), l = new Ix(
        e,
        n ?? `信息面板${this.childMap.size}`,
        i,
        this,
        t
      );
      o && (l.updateBackgroundStyle(o.config.background), l.updateRowStyle(o.config.rowStyles), (a == null ? void 0 : a.type) === "none" ? l.updateLocationPointStyle(o.config.afterInteractionLocationPoint) : l.updateLocationPointStyle(o.config.defaultLocationPoint)), this.childMap.set(e, l), r ? this.group.attach(l) : this.group.add(l);
    }
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    t && (this.childMap.forEach((i) => {
      i.updateBackgroundStyle(t.background), i.updateRowStyle(t.rowStyles), i.getElementShow(i.panelElement) ? i.updateLocationPointStyle(t.afterInteractionLocationPoint) : i.updateLocationPointStyle(t.defaultLocationPoint);
    }), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    ), super.update(e));
  }
  setCustomAttribute(e, t) {
    super.setCustomAttribute(e, t), this.update(0);
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(
      Array.from(this.childMap.values()),
      t
    );
    i && n.forEach((r) => {
      r.updateLocationPointStyle(i.config.defaultLocationPoint), r.updateBackgroundStyle(i.config.background), r.updateRowStyle(i.config.rowStyles);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  setVisible(e) {
    const t = this.getExtendVisible(this.group, this.group.visible);
    return this.childMap.forEach((i) => {
      i.setVisible(e && t), this._interactionAnimationOptions.type === "none" && i.setPanelVisible(e && t);
    }), this;
  }
  render(e, t) {
    var i, n, r;
    if (this.childMap.size > 0) {
      const a = performance.now();
      (i = this._interactionAnimationOptions) != null && i.loop && this.entity.visible && a - this._time > this._interactionAnimationOptions.interval * 1e3 && (this._loopEnable && this._loop(), this._time = a), this.entity.visible && (((n = this._interactionAnimationOptions) == null ? void 0 : n.type) === "hover" && this._hover(), ((r = this._interactionAnimationOptions) == null ? void 0 : r.type) !== "none" && this._setMouseInteractionStyle());
    }
  }
  _setMouseInteractionStyle() {
    var e;
    if (!Number.isNaN(io.MouseParams.clientX)) {
      const t = (e = document.getElementById(qg.ContainerId)) == null ? void 0 : e.querySelector("canvas"), { clientX: i, clientY: n, coord: r } = io.MouseParams;
      let a = !1;
      !Tt.enabled && t && this.childMap.forEach((o) => {
        o.pointInElement(i, n, o.locationPointElement) && (cs.setCursorCssText(t, "pointer", "poiPanelV2"), a = !0);
      }), !a && !Tt.enabled && t && cs.setCursorCssText(t, "default", "poiPanelV2");
    }
  }
  _hover() {
    if (!Number.isNaN(io.MouseParams.clientX)) {
      const { clientX: e, clientY: t, coord: i } = io.MouseParams;
      let n;
      this.childMap.forEach((r) => {
        r.pointInElement(e, t, r.locationPointElement) && (n = r, this._loopEnable = !1);
      }), n ? (this.childMap.forEach((r) => {
        r.uuid === (n == null ? void 0 : n.uuid) ? r.setPanelVisible(!0) : r.setPanelVisible(!1);
      }), this._updateLocationPointStyle()) : this._loopEnable = !0;
    }
  }
  _updateLocationPointStyle() {
    const e = this.states.getCurrentStateConfig();
    this.childMap.forEach((t) => {
      t.getElementShow(t.panelElement) ? t.updateLocationPointStyle(e.afterInteractionLocationPoint) : t.updateLocationPointStyle(e.defaultLocationPoint);
    });
  }
  _loop() {
    if (!this.entity.visible)
      return;
    const e = Array.from(this.childMap.values());
    if (this.childMap.size === 1) {
      const t = e[0], i = t.getElementShow(t.panelElement);
      t.setPanelVisible(!i), this.dispatchToggleEvent();
    } else {
      const t = this._getShowIndex();
      this.childMap.forEach((n) => {
        n.setPanelVisible(!1);
      });
      const i = e[t];
      i && (i.setPanelVisible(!0), this.dispatchToggleEvent());
    }
    this._updateLocationPointStyle();
  }
  _handleLocationPointClick(e, t, i) {
    if (this._interactionAnimationOptions.type === "click") {
      if (i.pointInElement(e, t, i.locationPointElement)) {
        this.childMap.forEach((r) => {
          r.uuid !== i.uuid && r.setPanelVisible(!1);
        });
        const n = i.getElementShow(i.panelElement);
        i.setPanelVisible(!n), this.dispatchToggleEvent(), this._loopEnable = n;
      }
      this._updateLocationPointStyle();
    }
  }
  dispatchToggleEvent() {
    const e = [];
    this.childMap.forEach((t) => {
      t.getElementShow(t.panelElement) && e.push(
        Object.assign(t.userData, {
          id: t.uuid,
          name: t.name,
          position: {
            x: t.position.x,
            y: t.position.y,
            z: t.position.z
          }
        })
      );
    }), this.dispatchEvent("switch", e);
  }
  intersectObject(e, t) {
    const i = [], { clientX: n, clientY: r } = t;
    return this.childMap.forEach((a) => {
      a.intersectObject(e, n, r, i);
    }), i.sort((a, o) => a.distance - o.distance), i;
  }
  onClick(e, t) {
    this._handleLocationPointClick(t.clientX, t.clientY, e.object);
  }
  _getShowIndex() {
    return this._showIndex + 1 > this.childMap.size ? (this._showIndex = 0, this._showIndex) : this._showIndex++;
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  setData(e) {
    this.childMap.forEach((i) => {
      i.dispose(), this.group.remove(i);
    }), this.childMap.clear();
    const t = this.getExtendVisible(this.group, this.group.visible);
    e.children.forEach((i) => {
      const { id: n, name: r, position: a, customAttribute: o } = i;
      if ("lon" in a && "lat" in a) {
        const { x: c, z: h } = Lu(
          this.entity.world,
          a.lon,
          a.lat,
          a.y ?? 0
        );
        a.x = c, a.z = h;
      }
      this.add(n, a, o, r, !1);
      const l = this.childMap.get(n);
      t || l && l.setVisible(t), l && l.setPanelVisible(t);
    });
  }
  setInteractionAnimationOptions(e, t = !0) {
    Object.assign(this._interactionAnimationOptions, e);
    const i = this.states.getCurrentStateConfig();
    (this._interactionAnimationOptions.type === "click" || this._interactionAnimationOptions.type === "hover") && this.childMap.forEach((n) => {
      n.setPanelVisible(!1), n.updateLocationPointStyle(i.defaultLocationPoint);
    }), this._interactionAnimationOptions.type === "none" && this.childMap.forEach((n) => {
      n.setPanelVisible(!0);
    }), dt.Entity.refresh(), this.needDataUpdateServer = t;
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      scaleOptions: this.scaleOptions,
      interactionAnimationOptions: this._interactionAnimationOptions,
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        // rows: poiPanel.getRowDataOptions(),
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (d) => this.add(
        d.id,
        d.position,
        d.customAttribute,
        d.name,
        !0,
        o.interactionAnimationOptions
      )
    ), this.setVisible(this.entity.visible), this.addEventListener("switch", (d) => {
      e.emitFunc && e.emitFunc("switch", d, this.entity.uuid);
    });
    const { scaleOptions: l } = this;
    Object.assign(l, o.scaleOptions), this.setScaleOptions(l, !1), this.setInteractionAnimationOptions(o.interactionAnimationOptions, !1);
    const { x: c, y: h, z: u } = uh(o, this);
    c && this.group.position.setX(c), h && this.group.position.setY(h), u && this.group.position.setZ(u), this.positionMode = o.positionMode ?? "xyz", this.isInit = !0;
  }
  draw(e, t, i) {
    e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this);
    const n = /* @__PURE__ */ new Set(), r = (c) => {
      const { position: h, coord: u } = c, d = e.raycaster(u, this.group);
      let f = !1;
      if (!d)
        f = !0;
      else {
        const p = this.getObjectById(d.object.uuid);
        if (p) {
          const v = this.childMap.get(p.uuid);
          v && (f = !v.visible);
        }
      }
      f && e.executeDrawCommand("add-point", { position: h });
    }, a = (c) => {
      const h = this.getObjectById(c.point.uuid);
      h && (h.visible = !1), h && n.add(h.uuid);
    }, o = (c) => {
      if (n.has(c.point.uuid)) {
        const u = this.getObjectById(c.point.uuid);
        u && (u.visible = !0), n.delete(c.point.uuid);
      } else {
        this.add(
          c.point.uuid,
          c.point.sprite.position,
          JSON.parse(JSON.stringify(t)),
          `信息面板${this.childMap.size}`,
          !0,
          {
            type: "none",
            loop: !1,
            interval: 0
          }
        );
        const u = this.childMap.get(c.point.uuid);
        u == null || u.setPanelVisible(!0);
      }
    }, l = () => {
      n.forEach((c) => {
        this.removeObjectById(c);
      }), i && i(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", r), e.removeEventListener("end-draw", l), e.removeEventListener("add-point", o), e.removeEventListener("remove-point", a);
    };
    e.addEventListener("draw-point", r), e.addEventListener("end-draw", l), e.addEventListener("add-point", o), e.addEventListener("remove-point", a);
  }
  dispose() {
    this.childMap.forEach((e) => {
      var t;
      (t = e.parent) == null || t.remove(e), e.dispose();
    });
  }
  easyvClosePanelElement(e) {
    const t = this.states.getCurrentStateConfig();
    e ? this.filterByAttribute(
      Array.from(this.childMap.values()),
      e
    ).forEach((n) => n.setPanelVisible(!1)) : this.childMap.forEach((i) => {
      i.setPanelVisible(!1), i.updateLocationPointStyle(t.defaultLocationPoint);
    }), this.dispatchToggleEvent();
  }
};
OF([
  Qp(0.3)
], Fx.prototype, "_hover", 1);
Fx = OF([
  ni(M1e)
], Fx);
class S1e extends Fi {
  constructor() {
    super(...arguments);
    S(this, "hasRender", !0);
  }
}
class w1e {
  constructor(e) {
    S(this, "_mesh");
    S(this, "_camera");
    this._camera = new Xn(-1, 1, 1, -1, 0, 1);
    const t = new $n(2, 2);
    this._mesh = new Re(t, e);
  }
  get material() {
    return this._mesh.material;
  }
  set material(e) {
    this._mesh.material = e;
  }
  render(e) {
    e.render(this._mesh, this._camera);
  }
  dispose() {
  }
}
const Pr = class Pr {
  constructor(e, t, i, n) {
    S(this, "strength");
    S(this, "radius");
    S(this, "threshold");
    S(this, "resolution");
    S(this, "clearColor");
    S(this, "renderTargetsHorizontal");
    S(this, "renderTargetsVertical");
    S(this, "nMips");
    S(this, "renderTargetBright");
    S(this, "highPassUniforms");
    S(this, "materialHighPassFilter");
    S(this, "separableBlurMaterials");
    S(this, "compositeMaterial");
    S(this, "bloomTintColors");
    S(this, "copyUniforms");
    S(this, "materialCopy");
    S(this, "needsSwap");
    S(this, "_oldClearColor");
    S(this, "oldClearAlpha");
    S(this, "fsQuad");
    S(this, "basic");
    S(this, "enabled");
    S(this, "clear");
    S(this, "renderToScreen", !1);
    S(this, "smoothWidth", 0.01);
    S(this, "coefficientScattering", 1);
    // public bloomObjects: Object3D[] = [];
    S(this, "originMaterial", /* @__PURE__ */ new Map());
    this.strength = t !== void 0 ? t : 1, this.radius = i, this.threshold = n, this.clear = !1, this.resolution = e !== void 0 ? new Se(e.x, e.y) : new Se(256, 256), Pr.layer.set(mN.BLOOM_LAYER), this.clearColor = new _e(0, 0, 0);
    const r = {
      minFilter: ii,
      magFilter: ii,
      format: dn
    };
    this.renderTargetsHorizontal = [], this.renderTargetsVertical = [], this.nMips = 5;
    let a = Math.round(this.resolution.x / 2), o = Math.round(this.resolution.y / 2);
    this.renderTargetBright = new Xi(a, o, r), this.renderTargetBright.texture.name = "UnrealBloomPass.bright", this.renderTargetBright.texture.generateMipmaps = !1;
    for (let d = 0; d < this.nMips; d++) {
      const f = new Xi(a, o, r);
      f.texture.name = `UnrealBloomPass.h${d}`, f.texture.generateMipmaps = !1, this.renderTargetsHorizontal.push(f);
      const p = new Xi(a, o, r);
      p.texture.name = `UnrealBloomPass.v${d}`, p.texture.generateMipmaps = !1, this.renderTargetsVertical.push(p), a = Math.round(a / 2), o = Math.round(o / 2);
    }
    oC === void 0 && console.error("UnrealBloomPass relies on LuminosityHighPassShader");
    const l = oC;
    this.highPassUniforms = rs.clone(l.uniforms), this.highPassUniforms.luminosityThreshold.value = n, this.highPassUniforms.smoothWidth.value = this.smoothWidth, this.materialHighPassFilter = new Jt({
      uniforms: this.highPassUniforms,
      vertexShader: l.vertexShader,
      fragmentShader: l.fragmentShader,
      defines: {}
    }), this.separableBlurMaterials = [];
    const c = [3, 5, 7, 9, 11];
    a = Math.round(this.resolution.x / 2), o = Math.round(this.resolution.y / 2);
    for (let d = 0; d < this.nMips; d++)
      this.separableBlurMaterials.push(this.getSeperableBlurMaterial(c[d])), this.separableBlurMaterials[d].uniforms.texSize.value = new Se(a, o), a = Math.round(a / 2), o = Math.round(o / 2);
    this.compositeMaterial = this.getCompositeMaterial(this.nMips), this.compositeMaterial.uniforms.blurTexture1.value = this.renderTargetsVertical[0].texture, this.compositeMaterial.uniforms.blurTexture2.value = this.renderTargetsVertical[1].texture, this.compositeMaterial.uniforms.blurTexture3.value = this.renderTargetsVertical[2].texture, this.compositeMaterial.uniforms.blurTexture4.value = this.renderTargetsVertical[3].texture, this.compositeMaterial.uniforms.blurTexture5.value = this.renderTargetsVertical[4].texture, this.compositeMaterial.uniforms.bloomStrength.value = t, this.compositeMaterial.uniforms.bloomRadius.value = 0.1, this.compositeMaterial.needsUpdate = !0;
    const h = [1, 0.5, 0.25, 0.125, 0.0625];
    this.compositeMaterial.uniforms.bloomFactors.value = h, this.bloomTintColors = [
      new _(1, 1, 1),
      new _(1, 1, 1),
      new _(1, 1, 1),
      new _(1, 1, 1),
      new _(1, 1, 1)
    ], this.compositeMaterial.uniforms.bloomTintColors.value = this.bloomTintColors, ia === void 0 && console.error("UnrealBloomPass relies on CopyShader");
    const u = ia;
    this.copyUniforms = rs.clone(u.uniforms), this.copyUniforms.opacity.value = 1, this.materialCopy = new Jt({
      uniforms: this.copyUniforms,
      vertexShader: u.vertexShader,
      fragmentShader: u.fragmentShader,
      blending: oo,
      depthTest: !1,
      depthWrite: !1,
      transparent: !0
    }), this.enabled = !0, this.needsSwap = !1, this._oldClearColor = new _e(), this.oldClearAlpha = 1, this.basic = new kt(), this.fsQuad = new w1e(void 0);
  }
  dispose() {
    for (let e = 0; e < this.renderTargetsHorizontal.length; e++)
      this.renderTargetsHorizontal[e].dispose();
    for (let e = 0; e < this.renderTargetsVertical.length; e++)
      this.renderTargetsVertical[e].dispose();
    this.renderTargetBright.dispose();
  }
  setSize(e, t) {
    let i = Math.round(e / 2), n = Math.round(t / 2);
    this.renderTargetBright.setSize(i, n);
    for (let r = 0; r < this.nMips; r++)
      this.renderTargetsHorizontal[r].setSize(i, n), this.renderTargetsVertical[r].setSize(i, n), this.separableBlurMaterials[r].uniforms.texSize.value = new Se(i, n), i = Math.round(i / 2), n = Math.round(n / 2);
  }
  render(e, t, i, n, r) {
    e.getClearColor(this._oldClearColor), this.oldClearAlpha = e.getClearAlpha();
    const a = e.autoClear;
    e.autoClear = !1, e.setClearColor(this.clearColor, 0), r && e.state.buffers.stencil.setTest(!1), this.renderToScreen && (this.fsQuad.material = this.basic, this.basic.map = i.texture, e.setRenderTarget(null), e.clear(), this.fsQuad.render(e)), this.highPassUniforms.tDiffuse.value = i.texture, this.highPassUniforms.luminosityThreshold.value = this.threshold, this.highPassUniforms.smoothWidth.value = this.smoothWidth, this.fsQuad.material = this.materialHighPassFilter, e.setRenderTarget(this.renderTargetBright), e.clear(), this.fsQuad.render(e);
    let o = this.renderTargetBright;
    for (let l = 0; l < this.nMips; l++)
      this.fsQuad.material = this.separableBlurMaterials[l], this.separableBlurMaterials[l].uniforms.colorTexture.value = o.texture, this.separableBlurMaterials[l].uniforms.direction.value = Pr.BlurDirectionX, this.separableBlurMaterials[l].uniforms.coefficient.value = this.coefficientScattering, e.setRenderTarget(this.renderTargetsHorizontal[l]), e.clear(), this.fsQuad.render(e), this.separableBlurMaterials[l].uniforms.colorTexture.value = this.renderTargetsHorizontal[l].texture, this.separableBlurMaterials[l].uniforms.direction.value = Pr.BlurDirectionY, e.setRenderTarget(this.renderTargetsVertical[l]), e.clear(), this.fsQuad.render(e), o = this.renderTargetsVertical[l];
    this.fsQuad.material = this.compositeMaterial, this.compositeMaterial.uniforms.bloomStrength.value = this.strength, this.compositeMaterial.uniforms.bloomRadius.value = this.radius, this.compositeMaterial.uniforms.bloomTintColors.value = this.bloomTintColors, e.setRenderTarget(this.renderTargetsHorizontal[0]), e.clear(), this.fsQuad.render(e), this.fsQuad.material = this.materialCopy, this.copyUniforms.tDiffuse.value = this.renderTargetsHorizontal[0].texture, r && e.state.buffers.stencil.setTest(!0), this.renderToScreen ? (e.setRenderTarget(null), this.fsQuad.render(e)) : (e.setRenderTarget(i), this.fsQuad.render(e)), e.setClearColor(this._oldClearColor, this.oldClearAlpha), e.autoClear = a;
  }
  setBackgroundOpacity(e) {
    this.separableBlurMaterials.forEach((t) => {
      t.uniforms.backgroundOpacity.value = e;
    });
  }
  darkNoneBloomObject(e) {
    (e.isMesh || e.isSprite) && Pr.layer.test(e.layers) === !1 && (this.originMaterial.set(e.uuid, e.material), e.isMesh ? e.material = Pr.DarkMaterial : (e.isSprite, e.material = Pr.DarkSpriteMaterial));
  }
  restoreObjectMaterial(e) {
    const t = this.originMaterial.get(e.uuid);
    t && (e.material = t, this.originMaterial.delete(e.uuid));
  }
  getSeperableBlurMaterial(e) {
    return new Jt({
      defines: {
        KERNEL_RADIUS: e,
        SIGMA: e
      },
      uniforms: {
        colorTexture: { value: null },
        texSize: { value: new Se(0.5, 0.5) },
        direction: { value: new Se(0.5, 0.5) },
        backgroundOpacity: { value: 0.1 },
        coefficient: { value: 0.1 }
      },
      vertexShader: `varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `#include <common>
				varying vec2 vUv;
				uniform sampler2D colorTexture;
				uniform vec2 texSize;
				uniform vec2 direction;
        uniform float backgroundOpacity;
        uniform float coefficient;

				float gaussianPdf(in float x, in float sigma) {
					return 0.39894 * exp( -0.5 * x * x/( sigma * sigma))/sigma;
				}
        void main() {
          vec2 invSize = 1.0 / texSize;
          float fSigma = float(SIGMA);
          float alphaNum = 0.0; 
          float weightSum = gaussianPdf(0.0, fSigma);
          vec3 diffuseSum = texture2D( colorTexture, vUv).rgb * weightSum;
          for( int i = 1; i < KERNEL_RADIUS; i ++ ) {
            float x = float(i);
            float w = gaussianPdf(x, fSigma);
            vec2 uvOffset = direction * invSize * x;
            vec3 sample1 = texture2D( colorTexture, vUv + uvOffset).rgb;
            vec3 sample2 = texture2D( colorTexture, vUv - uvOffset).rgb;
            diffuseSum += (sample1 + sample2) * w;
            weightSum += 2.0 * w;
            // alphaNum += (texture2D( colorTexture, vUv + uvOffset).a + texture2D( colorTexture, vUv - uvOffset).a) * w;
          }
          float alpha = min(alphaNum/weightSum, 0.1);
          gl_FragColor = vec4(diffuseSum/weightSum, alpha);
          // 为加强hdr 的bloom 效果
          // gl_FragColor = vec4(diffuseSum/weightSum, max(backgroundOpacity, 0.0));
				}`
    });
  }
  getCompositeMaterial(e) {
    return new Jt({
      defines: {
        NUM_MIPS: e
      },
      uniforms: {
        blurTexture1: { value: null },
        blurTexture2: { value: null },
        blurTexture3: { value: null },
        blurTexture4: { value: null },
        blurTexture5: { value: null },
        dirtTexture: { value: null },
        bloomStrength: { value: 1 },
        bloomFactors: { value: null },
        bloomTintColors: { value: null },
        bloomRadius: { value: 0 }
      },
      vertexShader: `varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}`,
      fragmentShader: `varying vec2 vUv;
				uniform sampler2D blurTexture1;
				uniform sampler2D blurTexture2;
				uniform sampler2D blurTexture3;
				uniform sampler2D blurTexture4;
				uniform sampler2D blurTexture5;
				uniform sampler2D dirtTexture;
				uniform float bloomStrength;
				uniform float bloomRadius;
				uniform float bloomFactors[NUM_MIPS];
				uniform vec3 bloomTintColors[NUM_MIPS];

				float lerpBloomFactor(const in float factor) {
					float mirrorFactor = 1.2 - factor;
					return mix(factor, mirrorFactor, bloomRadius);
				}

				void main() {
					gl_FragColor = bloomStrength * ( lerpBloomFactor(bloomFactors[0]) * vec4(bloomTintColors[0], 1.0) * texture2D(blurTexture1, vUv) +
						lerpBloomFactor(bloomFactors[1]) * vec4(bloomTintColors[1], 1.0) * texture2D(blurTexture2, vUv) +
						lerpBloomFactor(bloomFactors[2]) * vec4(bloomTintColors[2], 1.0) * texture2D(blurTexture3, vUv) +
						lerpBloomFactor(bloomFactors[3]) * vec4(bloomTintColors[3], 1.0) * texture2D(blurTexture4, vUv) +
						lerpBloomFactor(bloomFactors[4]) * vec4(bloomTintColors[4], 1.0) * texture2D(blurTexture5, vUv) );
				}`
    });
  }
};
S(Pr, "BlurDirectionX"), S(Pr, "BlurDirectionY"), S(Pr, "layer", new e0()), S(Pr, "DarkMaterial", new kt({ color: "black", visible: !1 })), S(Pr, "DarkSpriteMaterial", new kt({
  color: "black",
  visible: !1
}));
let Xg = Pr;
Xg.BlurDirectionX = new Se(1, 0);
Xg.BlurDirectionY = new Se(0, 1);
var C1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const _1e = "FinalComposer";
let s7 = class extends S1e {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "passArray", []);
    S(this, "composer");
    S(this, "bloomComposer");
    S(this, "bloomPass");
    this.id = e, this.name = t, this.entity = i, this.composer = new dC(this.entity.threeRenderer), this.composer.renderTarget1.samples = 4, this.composer.renderTarget2.samples = 4, this.composer.setPixelRatio(window.devicePixelRatio), this.bloomComposer = new dC(this.entity.threeRenderer), this.bloomComposer.setPixelRatio(window.devicePixelRatio), this.bloomPass = new Xg(this.entity.threeRenderer.getSize(new Se()), 2, 0.4, 0.1);
  }
  initialize(e, t, i) {
    const n = new Dee(t, i);
    this.composer.addPass(n), e.forEach((a) => this.composer.addPass(a));
    const r = new Nte();
    this.composer.addPass(r);
  }
  render(e, t) {
    const { threeRenderer: i } = this.entity;
    i.autoClear = !1;
    const n = i.getSize(new Se());
    this.composer.setSize(n.x, n.y), this.composer.passes.forEach((r) => {
      "camera" in r && (r.camera = t), "renderCamera" in r && (r.renderCamera = t), r.setSize(n.x, n.y);
    }), this.composer.render(), i.autoClear = !0;
  }
};
s7 = C1e([
  ni(_1e)
], s7);
var D1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const L1e = "CameraCruise", S3 = "camera-cruise-action";
new _(0, 1, 0);
const I1e = new _(), qP = new _(), O1e = new _(), Ua = new Ds(), w3 = new Ds(), E3 = new Ds();
let r7 = class extends Fi {
  constructor() {
    super(...arguments);
    // public tweenAnimation: TweenAnimation = new TweenAnimation();
    S(this, "camera", new Zt(60, 1, 0.5, 5e4));
    S(this, "cameraHelperMap", /* @__PURE__ */ new Map());
    // public points: CameraCruiseComponentStateConfigOptions = [];
    S(this, "_yAxisUpSpace", new St());
    S(this, "_yAxisUpSpaceInverse", new St());
    S(this, "_spherical", new Ds());
    S(this, "_cameraControls");
    S(this, "_target", new _());
    S(this, "mixer", new rm());
    S(this, "_curRenderCamera", this.camera);
  }
  initialize(e) {
    return this._cameraControls = e, this;
  }
  setCameraPosition(e, t) {
    const i = new _(t.x, t.y, t.z);
    return Ua.setFromVector3(
      new _(e.x, e.y, e.z).sub(i).applyQuaternion(this._yAxisUpSpace)
    ), E3.set(Ua.radius, Ua.phi, Ua.theta), this.camera.position.setFromSpherical(E3).applyQuaternion(this._yAxisUpSpaceInverse).add(i), this.camera.lookAt(i), this._target.copy(i), this;
  }
  start(e, t = 0) {
    const { curCamera: i } = e;
    if (this.mixer.isPlaying)
      return this;
    if (this.mixer.setTime(t), this.mixer.isPause)
      this.mixer.play(), this.dispatchEvent("start", {});
    else {
      this.camera.near = i.near, this.camera.far = i.far, this.camera instanceof Zt && i instanceof Zt && (this.camera.fov = i.fov, this.camera.aspect = i.aspect), this.camera.updateProjectionMatrix();
      const n = this.states.getCurrentStateConfig();
      if (!n)
        return this;
      const [r] = n.children;
      if (r) {
        const { position: a, target: o } = r;
        this.setCameraPosition(a, o), this.dispatchEvent("start", {}), this.dispatchEvent("restart", {});
      }
      if (this._cameraControls) {
        const { repeat: a = 0 } = n, o = [this.camera], l = this.mixer.addAction(S3, !0), c = [];
        n.children.forEach((d, f) => {
          c.push({
            p: new _(d.position.x, d.position.y, d.position.z),
            t: new _(d.target.x, d.target.y, d.target.z)
          });
        });
        const h = [];
        let u = 0;
        n.children.forEach((d, f) => {
          h.push({
            start: u + d.interval,
            end: u + d.interval + d.duration
          }), u += d.interval + d.duration;
        }), this.mixer.setRepeat(a), n.children.forEach((d, f) => {
          f > 0 && (l.addTrack({
            name: d.name,
            propertyKey: "position",
            roots: o,
            times: [h[f - 1].start, h[f - 1].end],
            values: [c[f - 1].p, c[f].p],
            mode: "Linear.None"
          }), l.addTrack({
            name: d.name,
            propertyKey: "lookAt",
            roots: o,
            times: [h[f - 1].start, h[f - 1].end],
            values: [c[f - 1].t, c[f].t],
            mode: "Linear.None"
          }));
        }), this.mixer.play(), this.mixer.once("stop", () => {
          this.stop();
        });
      }
    }
    return this;
  }
  stop() {
    return this.mixer.stop(!1), this.dispatchEvent("end", {}), this;
  }
  /**
   *
   * @param id 当前状态id
   * @param time
   */
  easyvStart(e, t = 0) {
    this.states.setState(e), this.dispatchEvent("easyvStart", void 0), this.start(
      {
        curCamera: this._curRenderCamera,
        fromStart: !1
      },
      t
    );
  }
  pausePlay() {
    this.mixer.pause();
    const e = this.states.getCurrentStateConfig();
    return this.dispatchEvent("pause", {
      lock: (e == null ? void 0 : e.lock) ?? !1
    }), this;
  }
  execute(e, t, i) {
    const { id: n } = t;
    switch (this.setCurrentStateById(n), e) {
      case "playAnimation":
        i && this.start(i);
        break;
      case "stopAnimation":
        this.stop();
        break;
      case "pauseAnimation":
        this.pausePlay();
        break;
    }
  }
  lerpLookAt(e, t, i, n, r, a) {
    const o = I1e.set(t.x, t.y, t.z), l = qP.set(e.x, e.y, e.z);
    Ua.setFromVector3(l.sub(o).applyQuaternion(this._yAxisUpSpace));
    const c = O1e.set(n.x, n.y, n.z), h = qP.set(i.x, i.y, i.z);
    w3.setFromVector3(h.sub(c).applyQuaternion(this._yAxisUpSpace));
    const u = o.lerp(c, r), d = w3.theta - Ua.theta, f = w3.phi - Ua.phi, p = w3.radius - Ua.radius;
    E3.set(
      Ua.radius + p * r,
      Ua.phi + f * r,
      Ua.theta + d * r
    ), a.position.setFromSpherical(E3).applyQuaternion(this._yAxisUpSpaceInverse).add(u), this.camera.lookAt(u), this._target.copy(u);
  }
  updateCamera(e, t, i, n, r = this.states.curStateId ?? "") {
    var o;
    console.log("updateCamera");
    const a = (o = this.states.stateList.get(r)) == null ? void 0 : o.config;
    if (a) {
      const l = a.children.find((c) => c.id === e);
      l && (l.position = t, l.target = i, l.name = n);
    }
  }
  // public sortCameraOptions(
  //   id: string,
  //   upperId: string,
  //   stateId: string = this.states.curStateId ?? '',
  // ): void {
  //   const config = this.states.stateList.get(stateId)?.config;
  //   if (config) {
  //     const upperIndex = config.children.findIndex((child) => child.id === upperId);
  //     const index = config.children.findIndex((child) => child.id === id);
  //     const { children } = config;
  //     if (upperIndex !== -1 && index !== -1) {
  //       [children[index], children[upperIndex]] = [children[upperIndex], children[index]];
  //     }
  //   }
  // }
  focusCurStateCamera(e, t = this.states.curStateId ?? "") {
    var n;
    const i = this._cameraControls;
    if (i) {
      const r = (n = this.states.stateList.get(t)) == null ? void 0 : n.config;
      if (console.log("focusCurStateCamera", r), r) {
        const a = new _(), o = new _();
        i.getTarget(o), i.getPosition(a);
        const l = r.children.find((c) => c.id === e);
        if (l) {
          const { position: c, target: h, duration: u } = l, d = new _(c.x, c.y, c.z), f = new _(h.x, h.y, h.z), p = this.mixer.addAction(S3, !0);
          p.addTrack({
            name: S3,
            propertyKey: "setPosition",
            roots: [i],
            times: [0, u],
            values: [a, d],
            mode: "Linear.None"
          }), p.addTrack({
            name: S3,
            propertyKey: "setTarget",
            roots: [i],
            times: [0, u],
            values: [o, f],
            mode: "Linear.None"
          }), this.mixer.play();
        }
      }
    }
  }
  preview(e, t) {
    this.mixer.preview(e, void 0, t);
  }
  tick() {
    this.mixer.tick();
  }
  dispose() {
    this.mixer.removeAll();
  }
  toJson() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      version: this.version,
      // attribute: [...this.customAttributes],
      data: {}
    };
  }
  async updateFromJson(e, t) {
    this.initialize(e.renderer.cameraControls);
    const i = t.states;
    i == null || i.forEach((n) => {
      n.config.children.forEach((r, a) => {
        const { name: o } = r;
        (!o || o === "") && (r.name = `镜头${a}`, this.needsUpdate = !0);
      });
    }), i && this.states.initStates(i), e.setCameraCruiseComponentEventsActions(this), this.isInit = !0;
  }
};
r7 = D1e([
  ni(L1e)
], r7);
function N1e(s, e) {
  const t = new s2();
  t.position.copy(s), t.lookAt(e);
  const i = s.clone().sub(e).length();
  return {
    position: t.position,
    rotation: t.rotation,
    targetOffset: i
  };
}
function B1e(s, e, t = 0) {
  const i = new mt();
  i.position.copy(s), i.rotation.copy(e);
  const n = new _();
  i.getWorldDirection(n).negate();
  const r = i.position.clone().add(n.multiplyScalar(t));
  return {
    position: i.position,
    target: r
  };
}
var z1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const k1e = "Camera";
let Kf = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "controls");
    S(this, "tweenSource", {
      px: 0,
      py: 0,
      pz: 0,
      tx: 0,
      ty: 0,
      tz: 0,
      focalLength: 0
    });
    S(this, "tweenTarget", {
      px: 0,
      py: 0,
      pz: 0,
      tx: 0,
      ty: 0,
      tz: 0,
      focalLength: 0
    });
    S(this, "duration", 1e3);
  }
  updateCamera(e) {
    var t, i;
    (t = this.controls) == null || t.setPosition(e.px, e.py, e.pz), (i = this.controls) == null || i.setTarget(e.tx, e.ty, e.tz);
  }
  initialize(e) {
    return this.controls = e, this;
  }
  focusCurStateCamera(e = this.states.curStateId ?? "", t = !1) {
    var n, r;
    const i = this.states.stateList.get(e);
    if (i && this.controls) {
      this.needsUpdate = !0;
      const { position: a, rotation: o, focalLength: l, targetOffset: c } = i.config, { target: h } = B1e(
        new _(a.x, a.y, a.z),
        new Mi(o.x, o.y, o.z),
        c
      );
      if (t)
        (n = this.controls) == null || n.setPosition(a.x, a.y, a.z), (r = this.controls) == null || r.setTarget(h.x, h.y, h.z);
      else {
        const u = new _(), d = new _();
        this.controls.getPosition(u), this.controls.getTarget(d), this.tweenSource.px = u.x, this.tweenSource.py = u.y, this.tweenSource.pz = u.z, this.tweenSource.tx = d.x, this.tweenSource.ty = d.y, this.tweenSource.tz = d.z, this.tweenSource.focalLength = this.controls.getCameraFocusLength(), this.tweenTarget.px = a.x, this.tweenTarget.py = a.y, this.tweenTarget.pz = a.z, this.tweenTarget.tx = h.x, this.tweenTarget.ty = h.y, this.tweenTarget.tz = h.z, this.tweenTarget.focalLength = l;
        const f = jn.createTweenHandler({
          source: this.tweenSource,
          target: this.tweenTarget,
          execute: this.updateCamera.bind(this),
          complete: () => {
            this.tweenSource = { px: 0, py: 0, pz: 0, tx: 0, ty: 0, tz: 0, focalLength: 0 }, this.tweenTarget = { px: 0, py: 0, pz: 0, tx: 0, ty: 0, tz: 0, focalLength: 0 }, Yi(f);
          }
        });
      }
    }
    return this;
  }
  update(e) {
    this.needsUpdate && this.states.curStateId && this.controls;
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t;
    this.initialize(e.renderer.cameraControls);
    const a = r;
    a && (this.preProcessCameraPointComponentOptions(t), this.initStates(a)), this.isInit = !0;
  }
  preProcessCameraPointComponentOptions(e) {
    e.type === "Camera" && e.states.forEach(
      (t) => {
        const i = t, { config: n } = i;
        if (n.focalLength = n.focalLength ?? 35, n.targetOffset = n.targetOffset ?? 10, !n.rotation) {
          const { position: r, target: a } = n;
          if (a) {
            const o = N1e(
              new _(r.x, r.y, r.z),
              new _(a.x, a.y, a.z)
            );
            n.rotation = {
              x: o.rotation.x,
              y: o.rotation.y,
              z: o.rotation.z
            };
          } else
            n.rotation = {
              x: 0,
              y: 0,
              z: 0
            };
        }
      }
    );
  }
  dispose() {
  }
};
Kf = z1e([
  ni(k1e)
], Kf);
var V1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const W1e = "KeyboardRoma";
new Se();
let a7 = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "keyDownSet", /* @__PURE__ */ new Set());
    S(this, "cameraStep", 10);
    S(this, "enable", !0);
    S(this, "wKey");
    S(this, "aKey");
    S(this, "sKey");
    S(this, "dKey");
    S(this, "qKey");
    S(this, "eKey");
  }
  initialize() {
  }
  setCameraStep(e) {
    this.cameraStep = e;
  }
  checkTarget(e) {
    return e.target instanceof HTMLInputElement;
  }
  dispose() {
    this.wKey = void 0, this.sKey = void 0, this.aKey = void 0, this.dKey = void 0, this.qKey = void 0, this.eKey = void 0;
  }
};
a7 = V1e([
  ni(W1e)
], a7);
var q1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const NF = "CameraSurround", YP = "camera-surround-action";
let o7 = class extends Fi {
  constructor() {
    super(...arguments);
    // public tweenAnimation: TweenAnimation = new TweenAnimation();
    // private _tween: Tween.Tween<{ value: number }> | undefined;
    S(this, "camera", new Zt(60, 1, 0.5, 5e4));
    S(this, "cameraHelperMap", /* @__PURE__ */ new Map());
    S(this, "mixer", new rm());
    // private _angle: number = 0;
    S(this, "_cameraControls");
  }
  initialize(t) {
    return this._cameraControls = t, this;
  }
  // public updateAnimate(delta: NumberState): void {
  //   if (this._cameraControls) {
  //     this._cameraControls.rotate(this._angle, 0, false);
  //   }
  // }
  /**
   *
   * @param id 当前状态id
   * @param time
   */
  easyvStart(t, i = 0) {
    this.states.setState(t), this.start(i);
  }
  start(t = 0) {
    const i = this.states.getCurrentStateConfig();
    if (this._cameraControls && i) {
      this._cameraControls.setPosition(i.position.x, i.position.y, i.position.z), this._cameraControls.setTarget(i.target.x, i.target.y, i.target.z);
      const n = new Se(i.center.x, i.center.z), r = new Se(i.position.x, i.position.z), a = n.distanceTo(r), o = Math.PI * a * 2;
      if (this.mixer.isPause)
        this.mixer.play(), this.dispatchEvent("start", {});
      else {
        this.mixer.setTime(t);
        const l = this.mixer.addAction(YP, !0), c = Math.PI / 180 * a, h = o / c * i.speed / a * (180 / Math.PI), u = Math.sqrt(
          Math.pow(i.position.x - i.center.x, 2) + Math.pow(i.position.z - i.center.z, 2)
        ), d = Math.asin((i.position.x - i.center.x) / u);
        l.addTrack({
          name: "rotate",
          propertyKey: "rotate",
          roots: [this._cameraControls],
          times: [0, 90 / h, 180 / h, 270 / h, 360 / h],
          values: [
            d,
            d + Math.PI / 2,
            d + Math.PI,
            d + Math.PI * 1.5,
            d + Math.PI * 2
          ],
          mode: "Linear.None"
        }), this.mixer.setRepeat(-1), this.mixer.play(), this.dispatchEvent("start", {}), this.dispatchEvent("restart", {});
      }
    }
  }
  preview(t, i) {
    this.mixer.preview(t, void 0, i);
  }
  updateCamera(t, i) {
    const n = this.states.getCurrentStateConfig();
    n && (n.position = t, n.target = i);
  }
  focusCurStateCamera(t = this.states.curStateId ?? "") {
    var n;
    const i = this._cameraControls;
    if (i) {
      const r = (n = this.states.stateList.get(t)) == null ? void 0 : n.config;
      if (r) {
        const a = new _(), o = new _();
        i.getTarget(o), i.getPosition(a);
        const { position: l, target: c } = r, h = new _(l.x, l.y, l.z), u = new _(c.x, c.y, c.z), d = this.mixer.addAction(YP, !0);
        d.addTrack({
          name: "setPosition",
          propertyKey: "setPosition",
          roots: [i],
          times: [0, 1],
          values: [a, h],
          mode: "Linear.None"
        }), d.addTrack({
          name: "setTarget",
          propertyKey: "setTarget",
          roots: [i],
          times: [0, 1],
          values: [o, u],
          mode: "Linear.None"
        }), this.mixer.play();
      }
    }
  }
  tick() {
    this.mixer.tick();
  }
  execute(t, i) {
    const { id: n } = i;
    switch (this.setCurrentStateById(n), t) {
      case "playAnimation":
        this.start();
        break;
      case "stopAnimation":
        this.stop();
        break;
      case "pauseAnimation":
        this.pause();
        break;
    }
  }
  stop() {
    (this.mixer.isPlaying || this.mixer.isPause) && this.mixer.stop(), this.dispatchEvent("end", {});
  }
  pause() {
    this.mixer.pause(), this.dispatchEvent("pause", {
      lock: !1
    });
  }
  dispose() {
    this.mixer.dispose();
  }
  toJson() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      version: this.version,
      // attribute: [...this.customAttributes],
      data: {}
    };
  }
  async updateFromJson(t, i) {
    this.initialize(t.renderer.cameraControls);
    const n = i.states;
    n && this.states.initStates(n), t.setCameraSurroundComponentEventsActions(this), this.isInit = !0;
  }
  async updateVersion(t) {
    const i = this.getLastVersion(), n = Hc.getInstance().resolve(
      `${this.type}@${i}`
    );
    if (i !== this.version && n) {
      this.entity.removeComponent(this.id, !1);
      const r = await this.entity.addComponentAsync(
        this.id,
        this.name,
        n.ctor,
        !0
      );
      console.log(r), r.updateFromJson(t, this.toJson());
      const a = this.toJson();
      await Qe.ApiServer.updateVersions([
        {
          id: this.entity.uuid,
          commonConfig: this.entity.commonConfig,
          components: [
            {
              componentId: this.id,
              version: i,
              data: a.data,
              states: a.states
            }
          ]
        }
      ]), dt.Entity.refresh();
    }
  }
};
o7 = q1e([
  ni(NF)
], o7);
var K1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Cw = "CameraSurround", ZP = "camera-surround-action";
let Ux = class extends Fi {
  constructor() {
    super(...arguments);
    // public tweenAnimation: TweenAnimation = new TweenAnimation();
    // private _tween: Tween.Tween<{ value: number }> | undefined;
    S(this, "_camera", new Zt(60, 1, 0.5, 5e4));
    S(this, "referencePoint", new PF(Cw, {
      text: "环绕中心",
      size: 20,
      sphereColor: "#ffff00"
    }));
    // public cameraHelperMap: Map<string, CameraHelper> = new Map();
    S(this, "mixer", new rm());
    // private _angle: number = 0;
    S(this, "_cameraControls");
  }
  initialize(e) {
    return this._cameraControls = e, this.group.add(this.referencePoint), this.referencePoint.scale.set(0.4, 0.4, 0.4), this.referencePoint.visible = Tt.enabled, this;
  }
  update(e) {
    const { center: t, showCenter: i } = this.states.getCurrentStateConfig();
    this.group.position.x = t.x, this.group.position.y = t.y, this.group.position.z = t.z, this.referencePoint.visible = i && Tt.enabled;
  }
  onDragEnd() {
    const e = this.states.getCurrentStateConfig();
    e.center.x = this.group.position.x, e.center.y = this.group.position.y, e.center.z = this.group.position.z, dt.Entity.refresh(), this.states.updateStateConfig(this.getCurStateId(), e);
  }
  // public updateAnimate(delta: NumberState): void {
  //   if (this._cameraControls) {
  //     this._cameraControls.rotate(this._angle, 0, false);
  //   }
  // }
  /**
   *
   * @param id 当前状态id
   * @param time
   */
  easyvStart(e, t = 0) {
    this.states.setState(e), this.start(t);
  }
  flyToSurroundStart() {
    const e = this.states.getCurrentStateConfig();
    if (this._cameraControls && e) {
      const t = new _(), i = new _();
      this._cameraControls.getPosition(t), this._cameraControls.getTarget(i);
      const { center: n, position: r } = e, a = jn.createTweenHandler({
        duration: 1e3,
        source: {
          px: t.x,
          py: t.y,
          pz: t.z,
          tx: i.x,
          ty: i.y,
          tz: i.z
        },
        target: {
          px: r.x,
          py: r.y,
          pz: r.z,
          tx: n.x,
          ty: n.y,
          tz: n.z
        },
        execute: (o) => {
          var l, c;
          (l = this._cameraControls) == null || l.setPosition(o.px, o.py, o.pz), (c = this._cameraControls) == null || c.setTarget(o.tx, o.ty, o.tz);
        },
        complete: () => {
          Yi(a);
        }
      });
      a.start();
    }
  }
  setCurrentStateById(e) {
    if (!Tt.enabled) {
      const t = setTimeout(() => {
        this.start(), clearTimeout(t);
      }, 200);
    }
    return super.setCurrentStateById(e);
  }
  start(e = 0) {
    const t = this.states.getCurrentStateConfig();
    if (this._cameraControls && t)
      if (this._camera.near = this._cameraControls.camera.near, this._camera.far = this._cameraControls.camera.far, this._camera.fov = this._cameraControls.camera.fov, this._camera.aspect = this._cameraControls.camera.aspect, this._camera.updateProjectionMatrix(), this.mixer.isPause)
        this.mixer.play(), this.dispatchEvent("start", {});
      else {
        const i = () => {
          if (this._cameraControls && t) {
            const n = this.mixer.addAction(ZP, !0), r = [], a = [], o = [], l = new _(t.center.x, t.center.y, t.center.z);
            this._camera.position.set(t.position.x, t.position.y, t.position.z), this._camera.lookAt(l);
            const c = Math.sqrt(
              Math.pow(t.position.x - t.center.x, 2) + Math.pow(t.position.z - t.center.z, 2)
            ), h = (t.position.x - t.center.x) / c, u = (t.position.z - t.center.z) / c;
            let d = Math.atan2(h, u);
            d < 0 && (d += Math.PI * 2);
            for (let f = 0; f <= 360; f += 2) {
              const p = f / 180 * Math.PI + d;
              r.push(f / t.speed), a.push(
                l.x + c * Math.sin(p),
                this._camera.position.y,
                l.z + c * Math.cos(p)
              ), o.push(t.center.x, t.center.y, t.center.z);
            }
            console.log(
              "aaa",
              nt.radToDeg(d),
              a[0],
              a[2],
              t.position
            ), n.addTrack({
              name: "position",
              propertyKey: "position",
              roots: [this._camera],
              times: r,
              values: a,
              mode: "Linear.None"
            }), n.addTrack({
              name: "lookAt",
              propertyKey: "lookAt",
              roots: [this._camera],
              times: r,
              values: o,
              mode: "Linear.None"
            }), this.mixer.setRepeat(-1), this.mixer.play(), this.dispatchEvent("start", {}), this.dispatchEvent("restart", {});
          }
        };
        this.mixer.setTime(e), i();
      }
  }
  preview(e, t) {
    this.mixer.preview(e, void 0, t);
  }
  lookAtCenter() {
    var t, i;
    const e = new _();
    (t = this._cameraControls) == null || t.getPosition(e), (i = this._cameraControls) == null || i.setLookAt(
      e.x,
      e.y,
      e.z,
      this.group.position.x,
      this.group.position.y,
      this.group.position.z,
      !0
    );
  }
  updateCamera(e) {
    const t = this.states.getCurrentStateConfig();
    t && (t.position = e);
  }
  focusCurStateCamera(e = this.states.curStateId ?? "") {
    var i;
    const t = this._cameraControls;
    if (t) {
      const n = (i = this.states.stateList.get(e)) == null ? void 0 : i.config;
      if (n) {
        const r = new _(), a = new _();
        t.getTarget(a), t.getPosition(r);
        const { position: o, center: l } = n, c = new _(o.x, o.y, o.z), h = new _(l.x, l.y, l.z), u = this.mixer.addAction(ZP, !0);
        u.addTrack({
          name: "setPosition",
          propertyKey: "setPosition",
          roots: [t],
          times: [0, 1],
          values: [r, c],
          mode: "Linear.None"
        }), u.addTrack({
          name: "setTarget",
          propertyKey: "setTarget",
          roots: [t],
          times: [0, 1],
          values: [a, h],
          mode: "Linear.None"
        }), this.mixer.play();
      }
    }
  }
  tick() {
    this.mixer.tick();
  }
  execute(e, t) {
    const { id: i } = t;
    switch (this.setCurrentStateById(i), e) {
      case "playAnimation":
        this.start();
        break;
      case "stopAnimation":
        this.stop();
        break;
      case "pauseAnimation":
        this.pause();
        break;
    }
  }
  stop() {
    (this.mixer.isPlaying || this.mixer.isPause) && this.mixer.stop(), this.dispatchEvent("end", {});
  }
  pause() {
    this.mixer.pause(), this.dispatchEvent("pause", {
      lock: !1
    });
  }
  dispose() {
    this.mixer.dispose();
  }
  toJson() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      version: this.version,
      // attribute: [...this.customAttributes],
      data: {}
    };
  }
  initCameraEvent(e) {
    this.addEventListener("start", () => {
      const { cameraControls: t } = e.renderer;
      this._camera.near = t.camera.near, this._camera.far = t.camera.far, e.renderer.activeCamera = this._camera, e.renderer.cameraControls.enabled = !1, Tt.enabled && (e.selection.enable = !1), e.selection.hideTransformControls();
    }), this.addEventListener("end", () => {
      e.renderer.activeCamera = void 0, e.renderer.cameraControls.enabled = !0, e.selection.enable = !0, e.selection.showTransformControls();
    });
  }
  async updateFromJson(e, t) {
    this.initialize(e.renderer.cameraControls);
    const i = t.states;
    i && this.states.initStates(i), e.setCameraSurroundComponentEventsActions(this);
    const { center: n, showCenter: r } = this.states.getCurrentStateConfig();
    this.group.position.set(n.x, n.y, n.z), this.referencePoint.visible = r && Tt.enabled, this.initCameraEvent(e), this.isInit = !0;
  }
};
Ux = K1e([
  ni(Cw, { version: "2.5.0" })
], Ux);
var $1e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const ege = "PathCruise", tge = new _(0, 1, 0), ige = new _(), KP = new _(), nge = new _(), Ih = new Ds(), T3 = new Ds(), JP = new Ds();
let zx = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "tweenAnimation", new XS());
    S(this, "camera", new Zt(60, 1, 0.5, 5e4));
    S(this, "cameraHelperMap", /* @__PURE__ */ new Map());
    // public points: PathCruiseComponentStateConfigOptions = [];
    S(this, "_yAxisUpSpace", new St());
    S(this, "_yAxisUpSpaceInverse", new St());
    S(this, "_spherical", new Ds());
    S(this, "_cameraControls");
  }
  initialize(e) {
    return this._cameraControls = e, this;
  }
  start(e) {
    const { curCamera: t } = e;
    if (this.tweenAnimation.isPaused())
      this.tweenAnimation.resume();
    else {
      this.camera.near = t.near, this.camera.far = t.far, this.camera instanceof Zt && t instanceof Zt && (this.camera.fov = t.fov, this.camera.aspect = t.aspect), this.camera.updateProjectionMatrix();
      const i = this.states.getCurrentStateConfig();
      if (!i)
        return this;
      const [n] = i.children;
      if (n) {
        const { position: r, rotation: a } = n;
        this.camera.position.set(r.x, r.y, r.z), this._yAxisUpSpace.setFromUnitVectors(this.camera.up, tge), this._yAxisUpSpaceInverse = this._yAxisUpSpace.clone().invert(), this._spherical.setFromVector3(
          this.camera.position.clone().applyQuaternion(this._yAxisUpSpace)
        );
        const o = new _(a.x, a.y, a.z);
        this.camera.position.setFromSpherical(this._spherical).applyQuaternion(this._yAxisUpSpaceInverse).add(o), this.camera.lookAt(o);
      }
      this.tweenAnimation.clear(), i.children.forEach((r, a) => {
        if (a > 0) {
          const o = i.children[a - 1].position, l = i.children[a - 1].rotation, c = i.children[a].position, h = i.children[a].rotation, u = i.children[a - 1].duration * 1e3, d = i.children[a - 1].interval * 1e3;
          this.tweenAnimation.addTweenAnimation({
            id: r.id,
            duration: u,
            repeat: i.children.length > 2 ? 0 : 1 / 0,
            delay: d,
            execute: ({ value: f }) => {
              this.lerpLookAt(o, l, c, h, f, this.camera);
            }
          });
        }
      }), i.children.length > 1 && this.tweenAnimation.chain(), this.tweenAnimation.start(), this.dispatchEvent("start", {});
    }
    return this;
  }
  stop() {
    return this.tweenAnimation.stop(), this.tweenAnimation.clear(), this.dispatchEvent("end", {}), this;
  }
  pausePlay() {
    return this.tweenAnimation.pause(), this;
  }
  lerpLookAt(e, t, i, n, r, a) {
    const o = ige.set(t.x, t.y, t.z), l = KP.set(e.x, e.y, e.z);
    Ih.setFromVector3(l.sub(o).applyQuaternion(this._yAxisUpSpace));
    const c = nge.set(n.x, n.y, n.z), h = KP.set(i.x, i.y, i.z);
    T3.setFromVector3(h.sub(c).applyQuaternion(this._yAxisUpSpace));
    const u = o.lerp(c, r), d = T3.theta - Ih.theta, f = T3.phi - Ih.phi, p = T3.radius - Ih.radius;
    JP.set(
      Ih.radius + p * r,
      Ih.phi + f * r,
      Ih.theta + d * r
    ), a.position.setFromSpherical(JP).applyQuaternion(this._yAxisUpSpaceInverse).add(u), this.camera.lookAt(u);
  }
  updateCamera(e, t, i, n = this.states.curStateId ?? "") {
    var a;
    const r = (a = this.states.stateList.get(n)) == null ? void 0 : a.config;
    if (r) {
      const o = r.children.find((l) => l.id === e);
      o && (o.position = t, o.rotation = i);
    }
  }
  focusCurStateCamera(e, t = this.states.curStateId ?? "") {
    var n;
    const i = this._cameraControls;
    if (i) {
      const r = (n = this.states.stateList.get(t)) == null ? void 0 : n.config;
      if (r) {
        const a = new _(), o = new _();
        i.getTarget(o), i.getPosition(a);
        const l = r.children.find((c) => c.id === e);
        if (l) {
          const { position: c, rotation: h } = l, u = new _(c.x, c.y, c.z), d = new _(h.x, h.y, h.z), f = this.tweenAnimation.addTweenAnimation({
            id: XO(),
            duration: 1e3,
            repeat: 0,
            execute: ({ value: p }) => {
              const v = a.clone().lerp(u, p), g = o.clone().lerp(d, p);
              i.setPosition(v.x, v.y, v.z), i.setTarget(g.x, g.y, g.z);
            },
            complete: () => {
              Yi(f);
            }
          });
          f.start();
        }
      }
    }
  }
  dispose() {
    this.tweenAnimation.dispose();
  }
  toJson() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      version: this.version,
      // attribute: [...this.customAttributes],
      data: {}
    };
  }
  draw(e, t) {
    e.mode = "multi-point", e.startDraw();
    const i = /* @__PURE__ */ new Set(), n = (l) => {
      const { position: c, coord: h } = l, u = e.raycaster(h, this.group);
      let d = !1;
      u || (d = !0), d && e.executeDrawCommand("add-point", { position: c });
    }, r = (l) => {
    }, a = (l) => {
      const c = new Hs(5), h = new kt({ color: "red" }), u = new Re(c, h);
      this.group.add(u);
    }, o = () => {
      i.forEach((l) => {
        this.removeObjectById(l);
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", n), e.removeEventListener("end-draw", o), e.removeEventListener("add-point", a), e.removeEventListener("remove-point", r);
    };
    e.addEventListener("draw-point", n), e.addEventListener("end-draw", o), e.addEventListener("add-point", a), e.addEventListener("remove-point", r);
  }
};
zx = $1e([
  ni(ege)
], zx);
var sge = Object.defineProperty, rge = Object.getOwnPropertyDescriptor, BF = (s, e, t, i) => {
  for (var n = i > 1 ? void 0 : i ? rge(e, t) : e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (i ? a(e, t, n) : a(n)) || n);
  return i && n && sge(e, t, n), n;
};
const Oh = new _();
new _();
const Nd = new Se();
new Mi();
new Ds();
new Ds();
new Ds();
class lge extends es {
  constructor() {
    super(...arguments);
    S(this, "_element");
    S(this, "_lastMousePosition", new Se(NaN, NaN));
    S(this, "_handleMouseMove", (t) => {
      if (this._element) {
        Nd.x = t.offsetX - this._element.clientWidth / 2, Nd.y = t.offsetY - this._element.clientHeight / 2, Nd.x /= this._element.clientWidth, Nd.y /= this._element.clientHeight;
        const i = t.movementX < 0 ? -1 : 1, n = t.movementY < 0 ? -1 : 1;
        this.dispatchEvent("move", Nd.set(t.movementX, t.movementY)), this.dispatchEvent(
          "moveOffset",
          Nd.set(
            i * Math.min(Math.abs(t.movementX), 10),
            n * Math.min(Math.abs(t.movementY), 10)
          )
        ), this._lastMousePosition.set(t.offsetX, t.offsetY);
      }
    });
    S(this, "_handleClick", (t) => {
      this.dispatchEvent("click", t);
    });
  }
  bindElement(t) {
    this._element && this.deActive(), this._element = t;
  }
  active() {
    var t;
    (t = this._element) == null || t.addEventListener("click", this._handleClick), document.addEventListener("pointerlockchange", () => {
      document.pointerLockElement ? document.addEventListener("mousemove", this._handleMouseMove) : document.removeEventListener("mousemove", this._handleMouseMove);
    });
  }
  deActive() {
    var t;
    (t = this._element) == null || t.removeEventListener("click", this._handleClick);
  }
}
const d1 = "CameraFirstPerson";
let kx = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "_camera", new Zt());
    S(this, "_cameraRotateConfig", {
      damping: 0,
      v: new Se()
    });
    // 挂载相机 和 胶囊碰撞体
    S(this, "_rootObject", new mt());
    S(this, "_physicService");
    S(this, "_capsuleMesh", new _n());
    S(this, "_cameraDirectionMesh", new _n());
    S(this, "_keyboardEvent", new aF());
    S(this, "_mouseEvent", new lge());
    S(this, "_keySet", /* @__PURE__ */ new Set());
    S(this, "_enable", !1);
    S(this, "_freezeCamera", !1);
    S(this, "_lon", 0);
    S(this, "_lat", 0);
    S(this, "_mouseOffset", new Se());
    S(this, "_curFrame", performance.now());
    // private _cameraDir: THREE.Vector3 = new THREE.Vector3();
    S(this, "_physicConfig", {
      gravityConfig: {
        enable: !1
      },
      colliderConfig: {
        enable: !1,
        radius: 10,
        height: 10
      }
    });
    S(this, "_speedConfig", {
      move: 1,
      rotation: 1,
      jump: 1,
      run: 1,
      maxHeightStep: 1
    });
    S(this, "_cameraRotateTarget", new Se());
    S(this, "_handleKeyDown", (e) => {
      e.code === "KeyG" && this._freezeCamera != this._freezeCamera, e.code === "Escape" ? this.end() : this._keySet.add(e.code);
    });
    S(this, "_handleKeyUp", (e) => {
      this._keySet.delete(e.code);
    });
    S(this, "_handleMouseMove", (e) => {
      this._handleCameraRotate(e.x, e.y);
    });
    S(this, "setData", (e) => {
      this._rootObject.position.set(e.position.x, e.position.y, e.position.z), this._rootObject.rotation.set(e.rotation.x, e.rotation.y, e.rotation.z);
      const { cameraConfig: t } = this.states.getCurrentStateConfig();
      Object.assign(JSON.parse(JSON.stringify(t)), e), this.setCameraConfig(t);
    });
    S(this, "end", () => {
      this._keyboardEvent.deActive(), this._mouseEvent.deActive(), this.removeCenterImage(), this._cameraRotateTarget.set(0, 0), this._camera.rotation.set(0, 0, 0), Tt.enabled && (this.group.visible = !0), Tt.enabled && (this._capsuleMesh.visible = !0), this._enable = !1, this._physicService.setPlayerController({
        start: this.group.position,
        end: this.group.position,
        radius: 1,
        enable: !1,
        object: this._capsuleMesh
      }), this.dispatchEvent("end", void 0), this.update(), this.entity.world.emitFunc && this.entity.world.emitFunc("cameraFirstPersonEnd", {}, this.entity.uuid);
    });
    this.id = e, this.name = t, this.entity = i, Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  static async addCameraFirstPersonComponent(e, t, i) {
    nt.generateUUID();
    const n = {
      cameraConfig: {
        focalLength: 18,
        height: 0,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        target: { x: 0, y: 0, z: 0 },
        nearFarClip: {
          enable: !1,
          near: 0.1,
          far: 1e3
        }
      },
      speedConfig: {
        move: 10,
        moveDamping: 0,
        rotation: 1,
        rotationDamping: 0,
        jump: 2,
        run: 1.2,
        maxHeightStep: 1
      },
      physicConfig: {
        gravityConfig: {
          enable: !0,
          gravity: 9.8
        },
        colliderConfig: {
          enable: !0,
          radius: 1,
          height: 1
        }
      },
      interactionConfig: {
        mode: "none",
        interaction: {
          imgUrl: "",
          size: {
            x: 10,
            y: 10
          }
        }
      }
    }, r = {
      id: "",
      name: "状态1",
      rank: 0,
      createdAt: (/* @__PURE__ */ new Date()).toDateString(),
      config: n
    };
    return (await e.entityManager.addEntityWithComponents(
      {
        id: "",
        name: t,
        parentId: i,
        visible: !0,
        type: "CameraFirstPerson",
        commonConfig: {
          version: "2.8.0"
        }
      },
      [
        {
          id: "",
          type: "CameraFirstPerson",
          name: "第一人称",
          visible: !1,
          inActive: !1,
          version: "2.4.0",
          data: {},
          states: [r]
        }
      ]
    )).getComponentByType("CameraFirstPerson");
  }
  _bindKeyboardEvent() {
    this._keyboardEvent.addEventListener("keydown", this._handleKeyDown), this._keyboardEvent.addEventListener("keyup", this._handleKeyUp);
  }
  _unBoundKeyboardEvent() {
    this._keyboardEvent.removeEventListener("keydown", this._handleKeyDown), this._keyboardEvent.removeEventListener("keyup", this._handleKeyUp);
  }
  _bindMouseEvent() {
    this._mouseEvent.addEventListener("move", this._handleMouseMove);
  }
  _unBoundMouseEvent() {
    this._mouseEvent.removeEventListener("move", this._handleMouseMove);
  }
  _handleCameraRotate(e, t) {
    this._cameraRotateTarget.y -= e / 500 * this._speedConfig.rotation, this._cameraRotateTarget.x -= t / 500 * this._speedConfig.rotation;
  }
  _initThreeObject() {
    const { physicConfig: e, cameraConfig: t } = this.states.getCurrentStateConfig(), i = new ap(
      new _u(
        e.colliderConfig.radius,
        e.colliderConfig.height
      )
    ), n = new Bi({ color: "#FAF65C" });
    this._capsuleMesh = new _n(i, n), this._cameraDirectionMesh.geometry.setFromPoints([
      new _(0, 0, 0),
      new _(
        0,
        0,
        (-e.colliderConfig.radius - e.colliderConfig.height) * 5
      )
    ]), this._cameraDirectionMesh.position.setY(t.height), this._cameraDirectionMesh.material = n, this.group.add(this._rootObject), this._rootObject.add(this._capsuleMesh), this._capsuleMesh.add(this._camera), this._rootObject.add(this._cameraDirectionMesh), this._capsuleMesh.visible = Tt.enabled, this._cameraDirectionMesh.visible = Tt.enabled;
  }
  // private setCameraPosition(position: IVector3, iTarget: IVector3): void {
  //   const target = new THREE.Vector3(iTarget.x, iTarget.y, iTarget.z);
  //   sphericalA.setFromVector3(
  //     new THREE.Vector3(position.x, position.y, position.z).sub(target),
  //     // .applyQuaternion(this._yAxisUpSpace),
  //   );
  //   sphericalC.set(sphericalA.radius, sphericalA.phi, sphericalA.theta);
  //   this._camera.position
  //     .setFromSpherical(sphericalC)
  //     // .applyQuaternion(this._yAxisUpSpaceInverse)
  //     .add(target);
  //   this._camera.lookAt(target);
  // }
  _getMoveDirection(e) {
    const t = this._camera.quaternion, i = new _();
    return e.forEach((n) => {
      switch (n) {
        case "KeyA":
          Oh.set(-1, 0, 0).applyQuaternion(t);
          break;
        case "KeyD":
          Oh.set(1, 0, 0).applyQuaternion(t);
          break;
        case "KeyW":
          Oh.set(0, 0, -1).applyQuaternion(t);
          break;
        case "KeyS":
          Oh.set(0, 0, 1).applyQuaternion(t);
          break;
        default:
          Oh.set(0, 0, 0);
          break;
      }
      i.add(Oh);
    }), i;
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s(d1, "end", e, this.end), _s(d1, "setData", e, this.setData), _s(d1, "freezeCamera", e, () => {
      this._enable && (this._freezeCamera = !0, document.exitPointerLock());
    }), _s(d1, "unFreezeCamera", e, () => {
      this._enable && (document.body.requestPointerLock(), this._freezeCamera = !1);
    });
  }
  initMouseEvent(e) {
    this._mouseEvent.bindElement(e.renderer.threeRenderer.domElement);
  }
  getCanAttach() {
    return !this._enable;
  }
  initCameraEvent(e) {
    this._initThreeObject();
    const t = () => {
      this._freezeCamera ? (this.entity.world.emitFunc && this.entity.world.emitFunc("cameraFirstPersonUnFreeze", {}, this.entity.uuid), document.body.requestPointerLock(), this._freezeCamera = !1) : (this.entity.world.emitFunc && this.entity.world.emitFunc("cameraFirstPersonFreeze", {}, this.entity.uuid), this._freezeCamera = !0, document.exitPointerLock());
    }, i = () => {
      !this._freezeCamera && document.pointerLockElement === null && this.end();
    };
    this.addEventListener("start", () => {
      var o;
      const { cameraControls: n } = e.renderer, { cameraConfig: r } = this.states.getCurrentStateConfig();
      n.getTarget(Oh), (o = r.nearFarClip) != null && o.enable ? (this._camera.near = 0.1, this._camera.far = 1e3) : (this._camera.near = n.camera.near, this._camera.far = n.camera.far), this._camera.aspect = n.camera.aspect, this._camera.fov = n.camera.fov, this._camera.rotation.order = "YXZ", this._camera.updateProjectionMatrix(), this.update(), e.renderer.activeCamera = this._camera, e.renderer.cameraControls.enabled = !1, Tt.enabled && (e.selection.enable = !1), e.selection.hideTransformControls(), document.addEventListener("pointerlockchange", i), this._curFrame = performance.now();
      const a = () => {
        this._freezeCamera = !1, document.body.requestPointerLock(), this._mouseEvent.removeEventListener("click", a);
      };
      this._mouseEvent.addEventListener("click", a), this._keyboardEvent.addEventListener("g", t);
    }), this.addEventListener("end", () => {
      e.renderer.activeCamera = void 0, e.renderer.cameraControls.enabled = !0, e.selection.enable = !0, document.exitPointerLock(), Tt.enabled && e.selection.showTransformControls(), this._keyboardEvent.removeEventListener("g", t), document.removeEventListener("pointerlockchange", i);
    }), this._bindKeyboardEvent(), this._bindMouseEvent(), this.initMouseEvent(e);
  }
  update() {
    const { cameraConfig: e, speedConfig: t, physicConfig: i } = this.states.getCurrentStateConfig();
    Object.assign(this._speedConfig, t), this.setPhysicConfig(i), this._enable || this.setCameraConfig(e), this.dispatchEvent("update", { comp: this });
  }
  setPhysicConfig(e) {
    Object.assign(this._physicConfig, e);
    const t = new ap(
      new _u(
        this._physicConfig.colliderConfig.radius,
        this._physicConfig.colliderConfig.height
      )
    );
    this._capsuleMesh.geometry.dispose(), this._capsuleMesh.geometry = t, this._physicService.gravityCoefficient = e.gravityConfig.gravity ?? 9.8, this._physicService.playerCollision.enableGravity = e.gravityConfig.enable;
  }
  setCameraConfig(e) {
    const { position: t, rotation: i, focalLength: n, height: r } = e;
    this.group.position.set(t.x, t.y, t.z), this.group.rotation.set(i.x ?? 0, i.y ?? 0, i.z ?? 0), this._camera.setFocalLength(n), this.group.add(this._capsuleMesh), this._capsuleMesh.position.set(0, 0, 0), this._capsuleMesh.quaternion.copy(this.group.quaternion.clone().invert()), this._cameraDirectionMesh.position.copy(
      new _(0, r, 0).applyQuaternion(this._capsuleMesh.quaternion)
    ), this._camera.position.set(0, r, 0), this._camera.updateProjectionMatrix();
  }
  async updateFromJson(e, t) {
    const { states: i } = t;
    this.initStates(i), this.initCameraEvent(e), this.update(), this.isInit = !0;
  }
  getCapsuleMeshConfig() {
    return this._enable ? {
      position: {
        x: this._capsuleMesh.position.x,
        y: this._capsuleMesh.position.y,
        z: this._capsuleMesh.position.z
      },
      rotation: {
        x: this._camera.rotation.x,
        y: this._camera.rotation.y,
        z: this._camera.rotation.z
      }
    } : {
      position: {
        x: this.group.position.x,
        y: this.group.position.y,
        z: this.group.position.z
      },
      rotation: {
        x: this.group.rotation.x,
        y: this.group.rotation.y,
        z: this.group.rotation.z
      }
    };
  }
  setCurrentStateById(e) {
    if (!Tt.enabled) {
      const t = setTimeout(() => {
        this.start(e), clearTimeout(t);
      }, 200);
    }
    return super.setCurrentStateById(e);
  }
  onDragEnd() {
    const e = this.states.getCurrentStateConfig(), { cameraConfig: t } = e, { position: i, rotation: n } = this.group;
    t.position.x = i.x, t.position.y = i.y, t.position.z = i.z, t.rotation.x = n.x, t.rotation.y = n.y, t.rotation.z = n.z, this.update(), this.states.updateStateConfig(this.getCurStateId(), e), dt.Entity.refresh();
  }
  tick() {
    if (this._enable && !this._freezeCamera) {
      this.move(this._getMoveDirection(this._keySet));
      const e = this._speedConfig.rotationDamping ?? 0, t = 53 / 25 * Math.pow(e, 2) - 3 * e + 1;
      this._camera.rotation.x += (this._cameraRotateTarget.x - this._camera.rotation.x) * t, this._camera.rotation.y += (this._cameraRotateTarget.y - this._camera.rotation.y) * t;
    }
    this._curFrame = performance.now();
  }
  move(e) {
    if (this._physicConfig.colliderConfig.enable) {
      let t = this._keySet.has("ShiftLeft") || this._keySet.has("ShiftRight") ? this._speedConfig.run * this._speedConfig.move : this._speedConfig.move;
      t *= (performance.now() - this._curFrame) / 1e3, this._physicService.translate(this._capsuleMesh, e, t), this._keySet.has("Space") && this._physicService.jump(this._capsuleMesh, (this._speedConfig.jump ?? 0) * 5);
    } else
      this._capsuleMesh.position.add(
        e.multiplyScalar(
          this._keySet.has("shift") ? this._speedConfig.run * this._speedConfig.move : this._speedConfig.move
        )
      );
    this.dispatchEvent("move", {
      position: {
        x: this._capsuleMesh.position.x,
        y: this._capsuleMesh.position.y,
        z: this._capsuleMesh.position.z
      },
      direction: {
        x: this._camera.rotation.x,
        y: this._camera.rotation.z,
        z: this._camera.rotation.y
      }
    });
  }
  addCenterImage(e, t) {
    var c, h;
    const i = "first-person-center", n = document.getElementById(i), r = this.entity.world.renderer.threeRenderer.domElement;
    n && ((c = r.parentElement) == null || c.removeChild(n));
    const a = r.clientWidth / 2, o = r.clientHeight / 2, l = document.createElement("img");
    l.id = i, (h = r.parentElement) == null || h.appendChild(l), l.src = st.getOssUrl(e), l.style.position = "absolute", l.style.pointerEvents = "none", l.style.left = `${a - t.x / 2}px`, l.style.top = `${o - t.y / 2}px`, l.style.width = `${t.x}px`, l.style.height = `${t.y}px`;
  }
  removeCenterImage() {
    var n;
    const e = "first-person-center", t = document.getElementById(e), i = this.entity.world.renderer.threeRenderer.domElement;
    t && ((n = i.parentElement) == null || n.removeChild(t));
  }
  onObjectChange() {
    this._capsuleMesh.quaternion.copy(this.group.quaternion.clone().invert());
  }
  start(e = this.getCurStateId()) {
    const { speedConfig: t, cameraConfig: i, physicConfig: n, interactionConfig: r } = this.states.getStateConfigById(e) ?? {};
    Object.assign(this._speedConfig, t), this._keySet.clear(), this._keyboardEvent.active(), this._mouseEvent.active(), this._enable = !0, this._camera.rotation.order = "YXZ", this._camera.rotation.set(this.group.rotation.x, this.group.rotation.y, this.group.rotation.z), this._cameraRotateTarget.set(this._camera.rotation.x, this._camera.rotation.y), this.dispatchEvent("start", void 0), this.group.visible = !1, this._capsuleMesh.visible = !1, this.entity.attach(this._capsuleMesh), this._camera.position.y = (i == null ? void 0 : i.height) ?? 0, this._capsuleMesh.quaternion.copy(new St());
    const a = this.group.position.clone(), o = this.group.position.clone();
    a.y -= (n == null ? void 0 : n.colliderConfig.height) ?? 0, o.y += (n == null ? void 0 : n.colliderConfig.height) ?? 0, this._physicService.setPlayerController({
      start: a,
      end: o,
      radius: (n == null ? void 0 : n.colliderConfig.radius) ?? 1,
      enable: !0,
      object: this._capsuleMesh
    }), this._physicService.setMaxStep((t == null ? void 0 : t.maxHeightStep) ?? 1), this._physicService.updateObjectPosition(this._capsuleMesh, this.group.position), this._physicService.playerCollision.moveDamping = (t == null ? void 0 : t.moveDamping) ?? 0, r && r.mode === "center" && r.interaction && this.addCenterImage(
      r.interaction.imgUrl,
      r.interaction.size
    );
  }
  // public getObjectById(
  //   id: string,
  //   instanceId?: number | undefined,
  // ): THREE.Object3D<THREE.Object3DEventMap> | undefined {
  //   return this._rootObject;
  // }
  dispose() {
    this._unBoundKeyboardEvent(), this._unBoundMouseEvent();
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
};
BF([
  KO("EasyTwinPhysicService")
], kx.prototype, "_physicService", 2);
kx = BF([
  ni(d1, { version: "2.4.0" })
], kx);
new g0(); new ln(); Math.cos(70 * nt.DEG2RAD);
class UF extends Fi {
  constructor(t, i, n) {
    super(t, i, n);
    S(this, "modelRenderResource");
    S(this, "animationHandler");
    S(this, "url", "");
    this.modelRenderResource = new jc();
  }
  get materials() {
    return [];
  }
  get triangles() {
    return 0;
  }
  getMaterialSlots() {
    return this.modelRenderResource.materialSlots;
  }
  /**
   * @deprecated
   * @returns
   */
  getMaterialSlotInfo() {
    return this.modelRenderResource.materialSlots.map((t) => t.toJSON());
  }
  updateMaterialSlot(t, i) {
    const n = st.assetManager.materialManager.getMaterialByUUID(i);
    n && this.modelRenderResource.setSlotMaterial(t, n), dt.Entity.refresh(), this.syncComponentDataConfig();
  }
  /**
   * 拷贝材质库材质
   * @param slotId
   * @param options
   */
  async updateMaterialSlotByMaterialOptions(t, i) {
    const n = JSON.parse(JSON.stringify(i));
    if (n) {
      const r = /* @__PURE__ */ new Map();
      for (const [l, c] of Object.entries(n))
        if (l.toLowerCase().endsWith("map") && c !== void 0 && c.image) {
          const h = c.image.url, u = await st.assetManager.textureManager.loadTextureImageFromUrl(h);
          if (u) {
            const d = O2(c);
            d.image = u.data, r.set(d.uuid, d), n[l] = d.uuid, d.userData.source = { url: u.url, indexId: u.indexId }, d.imageSource.url = u.url, d.imageSource.indexId = u.indexId, d.name = c.name ?? u.name;
          }
        }
      const a = Xc(n, r), o = this.modelRenderResource.findMaterialSlotById(t);
      if (o) {
        const l = o.initMaterial;
        if (a && l && a.uuid !== l.uuid) {
          const { name: c, userData: h } = l;
          a.type !== l.type ? l.type === "MeshPhysicalMaterial" && DB(
            a,
            l
          ) : l.copy(a), l.name = c, l.userData = h, l.needsUpdate = !0, l.needUpdateThumbnail = !0, o.setMaterial(l);
        }
      }
      dt.Entity.refresh(), this.syncComponentDataConfig(), this.syncMaterialSlotInitMaterialConfig(t);
    }
  }
  unLinkMaterialSlot(t) {
    this.modelRenderResource.copyMaterialSlotMaterialToInitMaterial(t), dt.Entity.refresh(), this.syncComponentDataConfig(), this.syncMaterialSlotInitMaterialConfig(t);
  }
  async copyMaterialSlotMaterialToScene(t) {
    const i = this.modelRenderResource.findMaterialSlotById(t);
    i && i.material && await st.assetManager.materialManager.copyModelMaterialsToSceneMaterialList([
      i.material
    ]);
  }
  async resetMaterialsInEditor(t) {
    const i = [];
    t = t ?? this.getMaterialSlots().map((n) => n.slotId), await Promise.all(
      (t || []).map(async (n) => {
        await this.modelRenderResource.resetSlotInitMaterial(n);
        const r = this.modelRenderResource.findMaterialSlotById(n);
        r && r.initMaterial && i.push(r.initMaterial.uuid);
      })
    ), dt.Entity.refresh(), this.syncComponentDataConfig(), dt.SceneMaterialsStore.batchUpdateMaterialConfig2Server(i);
  }
  updateInitDataOptions() {
  }
  updateInstance() {
  }
  async replaceModelByMaterialMappingAsync(t, i, n) {
    const r = this.modelRenderResource, a = await l7(i);
    await c7(a, this.id), this.modelRenderResource = a, this.createAnimationActions(), await this.syncComponentDataConfig({
      updateModelSceneData: !0
    }), this.updateInitDataOptions(), await this.clearModelMaterials(r), this.addObject2Renderer(), this.startAnimationLoop(), dt.Entity.refresh();
  }
  async loadRenderResourceFromJson(t) {
    const i = await jc.createFromJSON({
      url: t.asset.url,
      materialSlots: t.materialSlots,
      sceneGraph: t,
      scene: t.scene
    });
    return i.url = t.asset.url, this.modelRenderResource = i, i;
  }
  addObject2Renderer(t) {
  }
  startAnimationLoop() {
  }
  createAnimationActions() {
  }
  async syncComponentDataConfig(t = {
    updateModelSceneData: !1
    // 模型组件数据字段配置项太多存在互相影响的字段，需要能够独立进行更新
  }) {
    await Qe.ApiServer.updateComponent(this.entity.uuid, this.toJson(t));
  }
  async syncMaterialSlotInitMaterialConfig(t) {
    const i = this.modelRenderResource.findMaterialSlotById(t);
    i && i.initMaterial && await dt.SceneMaterialsStore.batchUpdateMaterialConfig2Server([
      i.initMaterial.uuid
    ]);
  }
  async clearModelMaterials(t) {
    const i = [];
    t.materialSlots.forEach((n) => {
      if (n.initMaterialId) {
        const r = st.assetManager.materialManager.getMaterialByUUID(
          n.initMaterialId
        );
        r && i.push(r);
      }
    }), await st.assetManager.materialManager.deleteMaterialsOnServer(
      i,
      "privateMaterial"
    );
  }
}
async function l7(s) {
  const e = await st.assetManager.modelManager.loadAndCacheModelByUrl(s), t = qc.InstantiateModelComponentRenderResources(e);
  return t.url = s, t;
}
async function c7(s, e) {
  const t = s.materials.map((i) => ({
    material: i,
    type: "privateMaterial",
    componentId: e
  }));
  await RB(t), s.materialSlots.forEach((i) => i.updateReferencedId()), yw(s.materials);
}
var qa = /* @__PURE__ */ ((s) => (s[s.None = 0] = "None", s[s.Point = 1] = "Point", s[s.MultiPoint = 2] = "MultiPoint", s[s.MultiLine = 3] = "MultiLine", s))(qa || {}), Qh = /* @__PURE__ */ ((s) => (s[s.None = 0] = "None", s[s.DrawStart = 1] = "DrawStart", s[s.Drawing = 2] = "Drawing", s))(Qh || {}), bc = /* @__PURE__ */ ((s) => (s.Add = "draw_add", s.End = "draw_end", s.Start = "draw_start", s))(bc || {});
class dge extends Rl {
  constructor(t, i, n = !0) {
    super(x0.DrawUtils, "DrawAddCommand");
    S(this, "name", "DrawAddCommand");
    this.group = t, this.pointSprite = i, this.isEnd = n;
  }
  async execute() {
    this.isEnd ? this.group.add(this.pointSprite) : this.group.unshift(this.pointSprite);
  }
  undo() {
    this.group.remove(this.pointSprite);
  }
  redo() {
    this.isEnd ? this.group.add(this.pointSprite) : this.group.unshift(this.pointSprite);
  }
  toJSON() {
  }
}
class fge extends wn {
  constructor(e) {
    super(), this.position.copy(e), this.scale.set(0.05, 0.05, 1), st.loadTexture(_ne).then((t) => {
      t.colorSpace = "srgb", this.material = new ar({
        map: t,
        sizeAttenuation: !1,
        transparent: !0,
        alphaTest: 0.2
      });
    });
  }
}
const Yl = new _(), Vm = (s, e) => {
  let t = s.target;
  return t instanceof HTMLCanvasElement || (t = e.entity.renderer.threeRenderer.domElement), t;
}, _3 = (s, e) => new _n(
  new Pt().setFromPoints([s, e]),
  new Bi({ color: "white" })
);
class pge extends Fi {
  constructor() {
    super(...arguments);
    S(this, "_handleMouseDownBindThis", this._handleMouseDown.bind(this));
    S(this, "_handleKeyDownBindThis", this._handleKeyDown.bind(this));
    S(this, "_pointGroup", new nC());
    S(this, "_lineGroup", new nC());
    S(this, "_moveLine", _3(new _(), new _()));
    S(this, "_raycaster", new lr());
    S(this, "_isEnd", !0);
    S(this, "history", new jO());
    S(this, "_mode", qa.None);
    S(this, "_relationItem");
    S(this, "_drawState", Qh.None);
    S(this, "canvas", document.createElement("canvas"));
  }
  get mode() {
    return this._mode;
  }
  get drawState() {
    return this._drawState;
  }
  initialize(t) {
    this.canvas = t, this._pointGroup.name = "draw_point_group", this._lineGroup.name = "draw_line", this.entity.getObjectById(this._pointGroup.id) || this.entity.add(this._pointGroup), this.entity.getObjectById(this._lineGroup.id) || this.entity.add(this._lineGroup), this.entity.getObjectById(this._moveLine.id) || this.entity.add(this._moveLine);
  }
  setMode(t) {
    this._drawState !== Qh.None && this.endDraw(), this._mode = t;
  }
  startDraw() {
    this.clearDraw(), document.addEventListener("mousedown", this._handleMouseDownBindThis), this._drawState = Qh.DrawStart, this.dispatchEvent(bc.Start, {}), console.log("startDraw");
  }
  startDrawFromComponent(t, i, n) {
    switch (t.type) {
      case "Point":
      case "PoiPanel":
      case "Iframe":
        this.setMode(qa.MultiPoint), this.startDraw();
        break;
      case "FlowFence":
        this.setMode(qa.MultiLine), this.startDraw(), n && this.drawFromPoints(t.getPointsById(n));
        break;
    }
    this._relationItem = { component: t, objId: n, data: i };
    const r = () => {
      this.removeEventListener(bc.End, r), t.needDataUpdateServer = !0;
    };
    this.addEventListener(bc.End, r);
  }
  handleComponentDrawByMultiLine(t) {
    switch (t.component.type) {
      case "FlowFence":
        this.handleFlowFenceComponentDrawByMultiLine(
          t.component,
          t.objId
        );
        break;
    }
    t.component.needDataUpdateServer = !0;
  }
  handleFlowFenceComponentDrawByMultiLine(t, i) {
    const n = this._pointGroup.children.map((r) => r.position);
    if (i)
      t.updatePointsById(i, n);
    else {
      t.group.children;
      const r = nt.generateUUID();
      this._relationItem && (this._relationItem.objId = r);
    }
  }
  handleComponentDrawByMultiPoint(t, i) {
    var r, a;
    const { length: n } = t.group.children;
    switch (t.type) {
      case "Point":
        t.add(
          nt.generateUUID(),
          i,
          {},
          `散点${n}`
        );
        break;
      case "Iframe":
        t.add(
          nt.generateUUID(),
          i,
          (r = this._relationItem) == null ? void 0 : r.data,
          {},
          `Iframe${n}`
        );
        break;
      case "PoiPanel":
        t.add(
          nt.generateUUID(),
          i,
          (a = this._relationItem) == null ? void 0 : a.data,
          // {},
          `信息面板${n}`
        );
        break;
    }
  }
  endDraw() {
    document.removeEventListener("mousedown", this._handleMouseDownBindThis), this._drawState = Qh.None;
    const t = this._pointGroup.children.map((i) => ({
      id: i.uuid,
      position: i.position
    }));
    this._moveLine.geometry.setFromPoints([Yl, Yl]), this.dispatchEvent(bc.End, t), document.removeEventListener("keydown", this._handleKeyDownBindThis);
  }
  clearDraw() {
    this._pointGroup.remove(...this._pointGroup.children), this._lineGroup.remove(...this._lineGroup.children), this._relationItem = void 0;
  }
  handlePoint(t) {
    const i = (n) => {
      if (t.clientX === n.clientX && t.clientY === n.clientY) {
        console.log("handlePoint"), document.removeEventListener("mouseup", i);
        const r = this.entity.projectCanvasPositionToScene(
          t.clientX,
          t.clientY,
          Vm(t, this)
        );
        this.drawPoint(r), this.endDraw();
      }
    };
    document.addEventListener("mouseup", i);
  }
  handleMultiPoint(t) {
    const i = (n) => {
      if (t.clientX === n.clientX && t.clientY === n.clientY) {
        console.log("drawMultiPoint"), document.removeEventListener("mouseup", i);
        const r = this.entity.projectCanvasPositionToScene(
          t.clientX,
          t.clientY,
          Vm(t, this)
        );
        this.dispatchEvent(bc.Add, { position: r }), this._relationItem ? this.handleComponentDrawByMultiPoint(this._relationItem.component, r) : this.drawPoint(r);
      }
    };
    document.addEventListener("mouseup", i);
  }
  handleMultiLine(t) {
    console.log("handleMultiLine");
    const i = (r) => {
      if (this._drawState !== Qh.None) {
        const a = this.raycaster(r);
        if (a && a.object.type === "LineSegments")
          console.log("选中线");
        else {
          const { length: o } = this._pointGroup.children, l = this._isEnd ? o - 1 : 0;
          if (o > 0) {
            const c = this._pointGroup.children[l].position, h = this.entity.projectCanvasPositionToScene(
              r.clientX,
              r.clientY,
              Vm(t, this)
            );
            this._moveLine.geometry.setFromPoints([c, h]);
          }
        }
      } else
        document.removeEventListener("mousemove", i);
    }, n = (r) => {
      if (t.clientX === r.clientX && t.clientY === r.clientY) {
        const a = this.entity.projectCanvasPositionToScene(
          t.clientX,
          t.clientY,
          Vm(t, this)
        );
        this.drawPoint(a), this.drawLine(a), this._relationItem && this.handleComponentDrawByMultiLine(this._relationItem);
      }
      document.removeEventListener("mouseup", n), this._mode === qa.None && document.removeEventListener("mousemove", i);
    };
    document.addEventListener("mouseup", n), document.addEventListener("mousemove", i);
  }
  raycaster(t) {
    let i = t.target;
    i instanceof HTMLCanvasElement || (i = this.entity.renderer.threeRenderer.domElement);
    const n = $r(t.clientX, t.clientY, i);
    return this._raycaster.setFromCamera(n, this.entity.renderer.mainCamera), this._raycaster.intersectObjects(
      [...this._pointGroup.children, ...this._lineGroup.children],
      !1
    )[0];
  }
  drawFromPoints(t) {
    t.forEach((i) => {
      this.drawPoint(i), this.mode === qa.MultiLine && this.drawLine(i);
    });
  }
  drawPoint(t) {
    this.history.execute(
      new dge(this._pointGroup, new fge(t), this._isEnd)
    );
  }
  drawLine(t) {
    if (t) {
      const i = this._pointGroup.children.length;
      i > 1 && (this._isEnd ? this._lineGroup.add(
        _3(t, this._pointGroup.children[i - 2].position)
      ) : this._lineGroup.add(
        _3(
          this._pointGroup.children[0].position,
          this._pointGroup.children[1].position
        )
      ));
    } else {
      const i = this._pointGroup.children.map((n) => n.position);
      this._lineGroup.remove(...this._lineGroup.children);
      for (let n = 1; n < i.length; n++)
        this._lineGroup.add(_3(i[n - 1], i[n]));
    }
  }
  _handleKeyDown(t) {
    const { key: i, ctrlKey: n, shiftKey: r } = t;
    console.log(t), n && i === "z" && (this.history.undo(), this.drawLine(), this._moveLine.geometry.setFromPoints([Yl, Yl])), n && i === "Z" && r && (this.history.redo(), this.drawLine(), this._moveLine.geometry.setFromPoints([Yl, Yl])), i === "Escape" && (document.removeEventListener("keydown", this._handleKeyDownBindThis), this._relationItem ? (this.clearDraw(), this._drawState !== Qh.None && this.endDraw()) : this.endDraw());
  }
  _handleMouseDown(t) {
    if (!(t.target instanceof HTMLCanvasElement))
      return;
    document.addEventListener("keydown", this._handleKeyDownBindThis);
    const i = this.raycaster(t);
    console.log("_handleMouseDown draw start");
    const n = (a) => {
      const o = this.entity.projectCanvasPositionToScene(
        a.clientX,
        a.clientY,
        Vm(t, this)
      );
      if (i && i.object.type === "Sprite") {
        i.object.position.copy(o);
        const l = this._pointGroup.children.findIndex(
          (c) => c.id === i.object.id
        );
        l !== -1 && this._updateLineSegments(l);
      }
    }, r = (a) => {
      this.entity.renderer.cameraControls.enabled = !0, this._moveLine.visible = !0, this._relationItem && this.handleComponentDrawByMultiLine(this._relationItem), document.removeEventListener("mouseup", r), document.removeEventListener("mousemove", n);
    };
    if (i)
      this._pointGroup.children.findIndex(
        (o) => o.id === i.object.id
      ) === 0 ? this._isEnd = !1 : this._isEnd = !0, this.entity.renderer.cameraControls.enabled = !1, this._moveLine.visible = !1, this._moveLine.geometry.setFromPoints([Yl, Yl]), document.addEventListener("mouseup", r), document.addEventListener("mousemove", n);
    else
      switch (this.mode) {
        case qa.Point:
          this.handlePoint(t);
          break;
        case qa.MultiPoint:
          this.handleMultiPoint(t);
          break;
        case qa.MultiLine:
          this.handleMultiLine(t);
          break;
      }
  }
  _updateLineSegments(t) {
    const i = t - 1 > 0 ? t - 1 : t, n = this._lineGroup.children[i];
    if (!n)
      return;
    const { length: r } = this._pointGroup.children;
    if (t === 0)
      n.geometry.setFromPoints([
        this._pointGroup.children[0].position,
        this._pointGroup.children[1].position
      ]);
    else if (t === r - 1)
      n.geometry.setFromPoints([
        this._pointGroup.children[r - 2].position,
        this._pointGroup.children[r - 1].position
      ]);
    else {
      const a = this._lineGroup.children[t], o = this._lineGroup.children[t - 1];
      a && o.geometry.setFromPoints([
        this._pointGroup.children[t - 1].position,
        this._pointGroup.children[t].position
      ]), o && a.geometry.setFromPoints([
        this._pointGroup.children[t].position,
        this._pointGroup.children[t + 1].position
      ]);
    }
  }
  dispose() {
  }
}
var vge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const yge = "Light";
let Uu = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "normalSprite", Ex());
    S(this, "highlightSprite", Ex());
    S(this, "helper", new mt());
  }
  showHelper() {
    this.helper.visible = !0, this.highlightSprite.visible = !0, this.normalSprite.visible = !1;
  }
  hideHelper() {
    this.helper.visible = !1, this.highlightSprite.visible = !1, this.normalSprite.visible = !0;
  }
  setHelperVisible(e) {
    this.normalSprite.visible = e;
  }
  initSprite(e, t) {
    const i = document.createElement("canvas");
    i.width = 64, i.height = 64, i.style.width = "64px", i.style.height = "64px";
    const n = i.getContext("2d"), r = new Image();
    r.src = e, r.onload = () => {
      n == null || n.drawImage(r, 0, 0, 64, 64);
      const a = new zc(i);
      t.material.map = a;
    };
  }
  getObjectById(e) {
    let t;
    return this.group.traverse((i) => {
      i.uuid === e && (t = this.group);
    }), e === "" && (t = this.group), t;
  }
  dispose() {
    var e, t;
    (e = this.normalSprite.material.map) == null || e.dispose(), (t = this.highlightSprite.material.map) == null || t.dispose(), this.group.remove(...this.group.children);
  }
  easyvSwitchComponentStateWithTWEEN(e, t) {
    throw new Error("Method not implemented.");
  }
};
Uu = vge([
  ni(yge)
], Uu);
var bge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const zF = "SunLight";
let h7 = class extends Uu {
  constructor() {
    super(...arguments);
    S(this, "target", new mt());
    S(this, "lineSegments", Ug([]));
    S(this, "csm", new Ite({
      mode: cN.logarithmic,
      lightFar: 5e3,
      lightNear: 1,
      shadowMapSize: 2048,
      cascades: 4,
      lightMargin: 100,
      maxFar: 1e3,
      lightDirection: new _(0, -1, 0),
      parent: this.group,
      camera: new Zt()
    }));
  }
  initialize(t) {
    this.group.add(this.helper), this.group.add(this.target), this.initSprite(tF, this.normalSprite), this.initSprite(iF, this.highlightSprite), this.group.add(this.normalSprite), this.group.add(this.highlightSprite), this.helper.visible = !1;
    const i = this.states.deepGet(this.getCurStateId());
    i && this.updateLightOptions(i.config), this.helper.add(this.lineSegments), this.csm.camera = t;
  }
  updateLightOptions(t) {
    const {
      color: i,
      position: n,
      rotation: r,
      intensity: a,
      enable: o,
      castShadow: l = !1,
      bias: c,
      kelvin: h
    } = t;
    this.group.position.set(n.x, n.y, n.z), this.group.rotation.set(r.x, r.y, r.z);
    let u = new _e();
    !isNaN(h) && i === "#ffffff" ? u = d0(h, u) : u = new _e(i), this.lineSegments.geometry.dispose();
    const { value: d, unit: f } = a;
    this.lineSegments.geometry.setFromPoints([new _(), new _(0, -25, 0)]), this.csm.fade = !0, this.csm.lights.forEach((p) => {
      p.shadow.camera.near = 0.5, p.shadow.camera.far = 500, p.shadow.bias = c, p.color = u, p.shadow.camera.updateProjectionMatrix(), p.castShadow = l, p.intensity = rx(d) / 4, p.visible = o;
    });
  }
  updateShadowMapSize(t) {
    this.csm.lights.forEach((i) => {
      i.shadow.mapSize = t;
    });
  }
  onDragEnd() {
    const t = this.states.stateList.get(this.getCurStateId());
    t && (t.config.position = {
      x: this.group.position.x,
      y: this.group.position.y,
      z: this.group.position.z
    }, t.config.rotation = {
      x: this.group.rotation.x,
      y: this.group.rotation.y,
      z: this.group.rotation.z
    }, Qe.ApiServer.updateState(this.entity.uuid, this.id, t), this.needDataUpdateServer = !1, dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((i) => i.toJson())
    }));
  }
  update(t) {
    const i = this.states.deepGet(this.getCurStateId());
    i && this.updateLightOptions(i.config);
  }
  render(t, i) {
    this.csm.parent = t, this.csm.camera = i, this.isInit && this.csm.update();
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(t, i) {
    const n = i.states;
    n && this.states.initStates(n), this.initialize(t.renderer.mainCamera), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.isInit = !0;
  }
  /**
   * 使用过渡动画切换模型组件状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(t, i) {
    const n = this.states.getStateConfigById(
      t
    );
    if (n) {
      this.states.setState(t), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), this._setLightVisible(!0);
      const r = {
        position_x: this.group.position.x,
        position_y: this.group.position.y,
        position_z: this.group.position.z,
        rotation_x: this.group.rotation.x,
        rotation_y: this.group.rotation.y,
        rotation_z: this.group.rotation.z,
        intensity: this._getLightVisible() ? this._getLightIntensity() : 0,
        color: this._getLightColor(),
        bias: this._getLightShadowBias()
      }, a = {
        position_x: n.position.x,
        position_y: n.position.y,
        position_z: n.position.z,
        rotation_x: n.rotation.x,
        rotation_y: n.rotation.y,
        rotation_z: n.rotation.z,
        intensity: n.enable ? n.intensity.value : 0,
        color: new _e(n.color),
        bias: n.bias
      };
      !isNaN(n.kelvin) && n.color === "#ffffff" ? a.color = d0(n.kelvin, new _e()) : a.color = new _e(n.color);
      const o = jn.createTweenHandler({
        mode: i.mode,
        duration: i.duration,
        source: r,
        target: a,
        execute: (l, c) => {
          this.group.position.x = l.position_x, this.group.position.y = l.position_y, this.group.position.z = l.position_z, this.group.rotation.x = l.rotation_x, this.group.rotation.y = l.rotation_y, this.group.rotation.z = l.rotation_z, this._setLightColor(
            r.color.lerpColors(r.color, a.color, c)
          ), this._setLightIntensity(l.intensity), this._setLightShadowBias(l.bias);
        },
        complete: () => {
          this.updateLightOptions(n), Yi(o), this.tweenHandler = void 0;
        }
      });
      this.tweenHandler = o;
    }
  }
  _getLightVisible() {
    return this.csm.lights[0].visible;
  }
  _setLightVisible(t) {
    this.csm.lights.forEach((i) => {
      i.visible = t;
    });
  }
  _setLightIntensity(t) {
    this.csm.lights.forEach((i) => {
      i.intensity = rx(t) / this.csm.lights.length;
    });
  }
  _getLightIntensity() {
    return this.csm.lights.reduce((t, i) => t + i.intensity, 0);
  }
  _getLightColor() {
    return this.csm.lights[0].color;
  }
  _setLightColor(t) {
    this.csm.lights.forEach((i) => {
      i.color.copy(t);
    });
  }
  _setLightShadowBias(t) {
    this.csm.lights.forEach((i) => {
      i.shadow.bias = t;
    });
  }
  _getLightShadowBias() {
    return this.csm.lights[0].shadow.bias;
  }
  async updateVersion(t) {
    const i = this.getLastVersion(), n = Hc.getInstance().resolve(
      `${this.type}@${i}`
    );
    if (i !== this.version && n) {
      this.entity.removeComponent(this.id, !1);
      const r = await this.entity.addComponentAsync(
        this.id,
        this.name,
        n.ctor,
        !0
      );
      console.log(r), r.updateFromJson(t, this.toJson());
      const a = this.toJson();
      await Qe.ApiServer.updateVersions([
        {
          id: this.entity.uuid,
          commonConfig: this.entity.commonConfig,
          components: [
            {
              componentId: this.id,
              version: i,
              data: a.data,
              states: a.states
            }
          ]
        }
      ]), dt.Entity.refresh();
    }
  }
};
h7 = bge([
  ni(zF)
], h7);
var wge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const kF = "SunLight";
let Gx = class extends Uu {
  constructor() {
    super(...arguments);
    S(this, "target", new mt());
    S(this, "lineSegments", Ug([]));
    S(this, "light", new _a());
    S(this, "dragObject", new mt());
    // public lightObject = new Mesh(new SphereGeometry(10), new MeshBasicMaterial({color: 'blue'}));
    S(this, "shadowHelper", new b2(new Xn()));
  }
  initialize(e) {
    this.group.add(this.target), this.group.add(this.dragObject), this.dragObject.add(this.shadowHelper), this.dragObject.add(this.helper), this.dragObject.uuid = `obj${this.light.uuid.slice(5, this.light.uuid.length)}`, this.initSprite(tF, this.normalSprite), this.initSprite(iF, this.highlightSprite), this.dragObject.add(this.normalSprite), this.dragObject.add(this.highlightSprite), this.helper.visible = !1, this.shadowHelper.visible = !1, this.shadowHelper.renderOrder = 1e3;
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config), this.shadowHelper.camera.position.setY(1), this.shadowHelper.camera.rotateX(-Math.PI / 2), this.shadowHelper.camera.updateMatrixWorld(), this.shadowHelper.camera.updateProjectionMatrix(), this.shadowHelper.update(), this.helper.add(this.lineSegments);
  }
  updateLightOptions(e) {
    const {
      color: t,
      position: i,
      rotation: n,
      intensity: r,
      enable: a,
      castShadow: o = !1,
      bias: l,
      kelvin: c,
      shadowArea: h,
      shadowRange: u,
      radius: d,
      shadowHelperVisible: f
    } = e;
    console.log(e), this.group.remove(this.light), this.light.dispose(), this.light = new _a(), this.light.target = this.target, this.group.add(this.light), this.dragObject.position.set(i.x, i.y, i.z), this.dragObject.rotation.set(n.x, n.y, n.z), this.dragObject.quaternion.setFromEuler(this.dragObject.rotation), this.light.position.set(i.x, i.y, i.z), this.setTargetPosition();
    let p = new _e();
    !isNaN(c) && t === "#ffffff" ? p = d0(c, p) : p = new _e(t), this.lineSegments.geometry.dispose();
    const { value: v, unit: g } = r;
    this.lineSegments.geometry.setFromPoints([new _(), new _(0, -25, 0)]), this.lineSegments.renderOrder = 10, this.light.shadow.bias = l, this.light.color = p, this.light.castShadow = o, this.light.intensity = rx(v) * 1.25, this.light.shadow.mapSize.height = u, this.light.shadow.mapSize.width = u, this.light.shadow.radius = d, this.light.visible = a, Tt.enabled && (this.shadowHelper.visible = f), this.light.shadow.camera.updateProjectionMatrix();
    const { near: m, far: x, size: y } = h ?? { near: 30, far: 500, size: 1e3 }, A = this.light.shadow.camera, b = y / 2;
    this.updateShadowCamera(A, b, m, x), this.updateShadowCamera(this.shadowHelper.camera, b, m, x), this.shadowHelper.update();
  }
  updateShadowCamera(e, t, i, n) {
    e.top = t, e.bottom = -t, e.left = -t, e.right = t, e.near = i, e.far = n, e.updateProjectionMatrix();
  }
  updateShadowMapSize(e) {
    this.light.shadow.mapSize = e;
  }
  setTargetPosition() {
    const e = this.dragObject.position.clone().add(new _(0, -25, 0).applyQuaternion(this.dragObject.quaternion));
    this.target.position.set(e.x, e.y, e.z);
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    if (e) {
      const t = e.config;
      this.light.position.set(
        this.dragObject.position.x,
        this.dragObject.position.y,
        this.dragObject.position.z
      ), this.setTargetPosition(), t.rotation = {
        x: this.dragObject.rotation.x,
        y: this.dragObject.rotation.y,
        z: this.dragObject.rotation.z
      }, t.position = {
        x: this.dragObject.position.x,
        y: this.dragObject.position.y,
        z: this.dragObject.position.z
      }, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), this.update(0), this.needDataUpdateServer = !1, dt.Entity.setState({
        id: this.entity.uuid,
        name: this.entity.name,
        components: this.entity.getComponentArray().map((i) => i.toJson())
      });
    }
  }
  update(e) {
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config);
  }
  getObjectById(e) {
    let t;
    return this.group.traverse((i) => {
      i.uuid === e && (t = this.dragObject);
    }), e === "" && (t = this.dragObject), t;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(e, t) {
    const i = t.states;
    i == null || i.forEach((n) => {
      n.config.shadowHelperVisible === void 0 && (n.config.shadowHelperVisible = !1), n.config.shadowArea === void 0 && (n.config.shadowArea = {
        near: 30,
        far: 500,
        size: 1e3
      });
    }), i && this.states.initStates(i), this.initialize(e.renderer.mainCamera), this.update(0), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.isInit = !0;
  }
  /**
   * 使用过渡动画切换模型组件状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.states.getStateConfigById(
      e
    );
    if (i) {
      const n = this.states.getCurrentStateConfig();
      this.states.setState(e), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), this._setLightVisible(!0);
      const r = {
        intensity: this._getLightVisible() ? n.intensity.value : 0,
        color: this._getLightColor(),
        bias: this._getLightShadowBias()
      }, a = {
        intensity: i.enable ? i.intensity.value : 0,
        color: new _e(i.color),
        bias: i.bias
      };
      !isNaN(i.kelvin) && i.color === "#ffffff" ? a.color = d0(i.kelvin, new _e()) : a.color = new _e(i.color);
      const o = this.light.position.clone(), l = new _(
        i.position.x,
        i.position.y,
        i.position.z
      ), c = this.light.target.position.clone(), h = l.clone().add(
        new _(0, -25, 0).applyQuaternion(
          new St().setFromEuler(
            new Mi(
              i.rotation.x,
              i.rotation.y,
              i.rotation.z
            )
          )
        )
      ), u = jn.createTweenHandler({
        mode: t.mode,
        duration: t.duration,
        source: r,
        target: a,
        execute: (d, f) => {
          this.light.position.lerpVectors(o, l, f), this.target.position.lerpVectors(c, h, f), this._setLightColor(
            r.color.lerpColors(r.color, a.color, f)
          ), this._setLightIntensity(d.intensity), this._setLightShadowBias(d.bias);
        },
        complete: () => {
          Yi(u), this.tweenHandler = void 0;
        }
      });
      this.tweenHandler = u;
    }
  }
  _getLightVisible() {
    return this.light.visible;
  }
  _setLightVisible(e) {
    this.light.visible = e;
  }
  _setLightIntensity(e) {
    this.light.intensity = rx(e);
  }
  _getLightIntensity() {
    return this.light.intensity;
  }
  _getLightColor() {
    return this.light.color;
  }
  _setLightColor(e) {
    this.light.color.copy(e);
  }
  _setLightShadowBias(e) {
    this.light.shadow.bias = e;
  }
  _getLightShadowBias() {
    return this.light.shadow.bias;
  }
};
Gx = wge([
  ni(kF, { version: "1.8.0" })
], Gx);
var Cge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const _ge = "DirectLight";
let tR = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "helper");
    S(this, "cameraHelper");
    S(this, "target", new mt());
    S(this, "light", new _a(new _e("#ffffff"), 1));
    S(this, "color", "white");
  }
  initialize() {
    this.light.target = this.target, this.helper = new aS(this.light, 5), this.cameraHelper = new b2(this.light.shadow.camera), this.cameraHelper.visible = !1, this.group.add(this.light), this.group.add(this.helper), this.group.add(this.cameraHelper), this.group.add(this.target);
    const e = this.states.deepGet(this.getCurStateId());
    return e && this.updateLightOptions(e.config), this;
  }
  updateLightOptions(e) {
    var l, c;
    const { color: t, intensity: i = 2, position: n, target: r, castShadow: a } = e;
    this.color = t, this.light.color = new _e(t), this.light.intensity = i;
    const o = new _(Number(n.x), Number(n.y), Number(n.z));
    (l = this.group.children[0]) == null || l.position.copy(o), this.target.position.set(Number(r.x), Number(r.y), Number(r.z)), this.target.updateMatrixWorld(), this.light.castShadow = a, a && this.cameraHelper ? this.cameraHelper.visible = !0 : this.cameraHelper && (this.cameraHelper.visible = !1), console.log("camera", this.cameraHelper), this.light.shadow.camera.near = 1, this.light.shadow.camera.far = 100, this.light.shadow.camera.updateProjectionMatrix(), this.light.shadow.mapSize.set(1024, 1024), (c = this.helper) == null || c.update();
  }
  update(e) {
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config);
  }
  onDragEnd() {
    var t;
    const e = this.states.stateList.get(this.getCurStateId());
    if (e) {
      e.config.position = {
        x: this.light.position.x,
        y: this.light.position.y,
        z: this.light.position.z
      };
      const i = this.light.position.clone().sub(this.target.position).applyQuaternion(this.light.quaternion), n = this.light.position.clone().sub(i);
      this.target.position.set(n.x, n.y, n.z), this.light.quaternion.set(0, 0, 0, 1), e.config.target = {
        x: n.x,
        y: n.y,
        z: n.z
      }, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), this.needDataUpdateServer = !1, (t = this.helper) == null || t.update(), dt.Entity.setState({
        id: this.entity.uuid,
        name: this.entity.name,
        components: this.entity.getComponentArray().map((r) => r.toJson())
      });
    }
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      version: this.version,
      // attribute: this.customAttributes,
      data: {
        color: this.color,
        intensity: this.light.intensity,
        castShadow: this.light.castShadow,
        near: this.light.shadow.camera.near,
        far: this.light.shadow.camera.far,
        width: this.light.shadow.mapSize.x,
        height: this.light.shadow.mapSize.y
      }
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a), this.initialize(), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.states.setState(r[0].id), this.needsUpdate = !0, this.isInit = !0;
  }
  setHelperVisible(e) {
    this.helper && (this.helper.visible = e), this.cameraHelper && (this.cameraHelper.visible = e);
  }
  getObjectById(e) {
    let t;
    return this.group.traverse((i) => {
      i.uuid === e && (t = this.light);
    }), e === "" && (t = this.light), t;
  }
};
tR = Cge([
  ni(_ge)
], tR);
const js = ["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "0a", "0b", "0c", "0d", "0e", "0f", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "1a", "1b", "1c", "1d", "1e", "1f", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "2a", "2b", "2c", "2d", "2e", "2f", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "3a", "3b", "3c", "3d", "3e", "3f", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "4a", "4b", "4c", "4d", "4e", "4f", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "5a", "5b", "5c", "5d", "5e", "5f", "60", "61", "62", "63", "64", "65", "66", "67", "68", "69", "6a", "6b", "6c", "6d", "6e", "6f", "70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "7a", "7b", "7c", "7d", "7e", "7f", "80", "81", "82", "83", "84", "85", "86", "87", "88", "89", "8a", "8b", "8c", "8d", "8e", "8f", "90", "91", "92", "93", "94", "95", "96", "97", "98", "99", "9a", "9b", "9c", "9d", "9e", "9f", "a0", "a1", "a2", "a3", "a4", "a5", "a6", "a7", "a8", "a9", "aa", "ab", "ac", "ad", "ae", "af", "b0", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "b8", "b9", "ba", "bb", "bc", "bd", "be", "bf", "c0", "c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8", "c9", "ca", "cb", "cc", "cd", "ce", "cf", "d0", "d1", "d2", "d3", "d4", "d5", "d6", "d7", "d8", "d9", "da", "db", "dc", "dd", "de", "df", "e0", "e1", "e2", "e3", "e4", "e5", "e6", "e7", "e8", "e9", "ea", "eb", "ec", "ed", "ee", "ef", "f0", "f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8", "f9", "fa", "fb", "fc", "fd", "fe", "ff"], zA = Math.PI / 180, u7 = 180 / Math.PI;
function k2() {
  const s = Math.random() * 4294967295 | 0, e = Math.random() * 4294967295 | 0, t = Math.random() * 4294967295 | 0, i = Math.random() * 4294967295 | 0;
  return (js[s & 255] + js[s >> 8 & 255] + js[s >> 16 & 255] + js[s >> 24 & 255] + "-" + js[e & 255] + js[e >> 8 & 255] + "-" + js[e >> 16 & 15 | 64] + js[e >> 24 & 255] + "-" + js[t & 63 | 128] + js[t >> 8 & 255] + "-" + js[t >> 16 & 255] + js[t >> 24 & 255] + js[i & 255] + js[i >> 8 & 255] + js[i >> 16 & 255] + js[i >> 24 & 255]).toLowerCase();
}
function tr(s, e, t) {
  return Math.max(e, Math.min(t, s));
}
function Pge(s, e) {
  return (s % e + e) % e;
}
function kA(s, e, t) {
  return (1 - t) * s + t * e;
}
function Wm(s, e) {
  switch (e.constructor) {
    case Float32Array:
      return s;
    case Uint32Array:
      return s / 4294967295;
    case Uint16Array:
      return s / 65535;
    case Uint8Array:
      return s / 255;
    case Int32Array:
      return Math.max(s / 2147483647, -1);
    case Int16Array:
      return Math.max(s / 32767, -1);
    case Int8Array:
      return Math.max(s / 127, -1);
    default:
      throw new Error("Invalid component type.");
  }
}
function Sr(s, e) {
  switch (e.constructor) {
    case Float32Array:
      return s;
    case Uint32Array:
      return Math.round(s * 4294967295);
    case Uint16Array:
      return Math.round(s * 65535);
    case Uint8Array:
      return Math.round(s * 255);
    case Int32Array:
      return Math.round(s * 2147483647);
    case Int16Array:
      return Math.round(s * 32767);
    case Int8Array:
      return Math.round(s * 127);
    default:
      throw new Error("Invalid component type.");
  }
}
var Lge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Ige = "PointLight";
let iR = class extends Uu {
  constructor() {
    super(...arguments);
    S(this, "light", new Tl());
    S(this, "color", "white");
    S(this, "lumen");
  }
  initialize() {
    this.group.add(this.light), this.group.add(this.helper);
    const e = new kt({
      color: "#FAF65C",
      side: ui,
      transparent: !0,
      wireframe: !0
    }), t = new nr(1, 0.1, 1, 100), i = new Re(t, e), n = new Re(t, e), r = new Re(t, e);
    n.rotateX(Math.PI / 2), r.rotateY(Math.PI / 2), this.helper.add(i), this.helper.add(n), this.helper.add(r), this.helper.visible = !1, this.initSprite(Ahe, this.normalSprite), this.initSprite(xhe, this.highlightSprite), this.group.add(this.normalSprite), this.group.add(this.highlightSprite), this.highlightSprite.visible = !1;
    const a = this.states.deepGet(this.getCurStateId());
    a && this.updateLightOptions(a.config);
  }
  updateLightOptions(e) {
    const {
      color: t,
      intensity: i = 2,
      distance: n = 100,
      decay: r = 2,
      bias: a = 0,
      position: o,
      kelvin: l,
      lumen: c,
      enable: h,
      castShadow: u,
      shadowRange: d,
      normalBias: f = 0.1
    } = e;
    this.color = t, this.lumen = c, !isNaN(l) && t === "#ffffff" ? this.light.color = d0(l, new _e()) : this.light.color = new _e(t), c ? this.light.intensity = Lc(c) ?? 1 : this.light.intensity = i ?? 1, h !== void 0 && (this.light.visible = h), this.light.distance = n, this.light.decay = r, this.light.castShadow = u;
    const p = tr(d ** 2 * 1e3, 256, 1024);
    this.light.shadow.camera.near = 0.5, this.light.shadow.camera.far = 500, this.light.shadow.camera.fov = 90, this.light.shadow.bias = a, this.light.frustumCulled = !0, this.light.shadow.normalBias = f, this.light.shadow.camera.updateProjectionMatrix(), this.light.shadow.mapSize.set(p, p), this.helper.scale.set(n, n, n), this.group.position.set(Number(o.x), Number(o.y), Number(o.z));
  }
  update(e) {
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config);
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    e && (e.config.position = {
      x: this.group.position.x,
      y: this.group.position.y,
      z: this.group.position.z
    }, this.needDataUpdateServer = !1, this.states.needsUpdateStateSet.add(e.id), dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((t) => t.toJson())
    }));
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version ?? 0,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      // attribute: this.customAttributes,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a), r[0].config && this.initialize(), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.states.setState(r[0].id), this.needsUpdate = !0, this.isInit = !0;
  }
  /**
   * 使用过渡动画切换模型组件状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.states.getStateConfigById(e);
    if (i) {
      this.states.setState(e), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), this.light.visible = !0;
      const n = {
        position_x: this.group.position.x,
        position_y: this.group.position.y,
        position_z: this.group.position.z,
        intensity: this.light.visible ? this.light.intensity : 0,
        color: this.light.color,
        bias: this.light.shadow.bias,
        normalBias: this.light.shadow.normalBias,
        distance: this.light.distance,
        decay: this.light.decay
      }, r = {
        position_x: i.position.x,
        position_y: i.position.y,
        position_z: i.position.z,
        intensity: i.enable ? i.intensity : 0,
        color: new _e(i.color),
        bias: i.bias,
        normalBias: i.normalBias,
        distance: i.distance,
        decay: i.decay
      };
      !isNaN(i.kelvin) && i.color === "#ffffff" ? r.color = d0(i.kelvin, new _e()) : r.color = new _e(i.color), i.lumen !== void 0 ? this.light.intensity = Lc(i.lumen) ?? 1 : this.light.intensity = i.intensity ?? 1;
      const a = jn.createTweenHandler({
        mode: t.mode,
        duration: t.duration,
        source: n,
        target: r,
        execute: (o, l) => {
          this.group.position.x = o.position_x, this.group.position.y = o.position_y, this.group.position.z = o.position_z, this.light.color = n.color.lerpColors(
            n.color,
            r.color,
            l
          ), this.light.intensity = o.intensity, this.light.distance = o.distance, this.light.decay = o.decay, this.light.shadow.bias = o.bias, this.light.shadow.normalBias = o.normalBias;
        },
        complete: () => {
          this.updateLightOptions(i), Yi(a), this.tweenHandler = void 0;
        }
      });
      this.tweenHandler = a;
    }
  }
};
iR = Lge([
  ni(Ige, { version: "1.7.0" })
], iR);
var Bge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Fge = "SpotLight";
let d7 = class extends Uu {
  constructor() {
    super(...arguments);
    S(this, "light", new oh());
    S(this, "lineSegments", Ug([]));
    S(this, "circleLine", Ug([]));
    S(this, "target", new mt());
  }
  initialize(e) {
    if (this.light.name = "SpotLight", this.light.target = this.target, !this.group.getObjectByName(this.light.name)) {
      this.group.add(this.light), this.group.add(this.helper), this.group.attach(this.target), this.initSprite(bhe, this.normalSprite), this.initSprite(Mhe, this.highlightSprite), this.group.add(this.normalSprite), this.group.add(this.highlightSprite), this.highlightSprite.visible = !1, this.helper.visible = !1;
      const i = this.states.deepGet(this.getCurStateId());
      i && this.updateLightOptions(i.config), this.lineSegments.onBeforeRender = () => {
        this.lineSegments.rotation.y = e.camera.rotation.y;
      }, this.helper.add(this.lineSegments), this.helper.add(this.circleLine);
    }
  }
  updateLightOptions(e) {
    const {
      position: t,
      rotation: i,
      shadowRange: n,
      castShadow: r,
      decay: a,
      angle: o = 60,
      intensity: l,
      kelvin: c,
      color: h,
      distance: u = 100,
      enable: d,
      bias: f = 0,
      radius: p = 0,
      penumbra: v = 0
    } = e, g = o * (Math.PI / 360);
    this.group.position.set(t.x, t.y, t.z), this.group.rotation.set(i.x, i.y, i.z), this.light.castShadow = r, this.light.decay = a, this.light.angle = g, this.light.distance = u, this.light.penumbra = v;
    const m = tr(n ** 2 * 1e3, 256, 1024);
    this.light.shadow.camera.near = 0.5, this.light.shadow.camera.far = n, this.light.shadow.camera.fov = 36, this.light.shadow.bias = f, this.light.shadow.radius = p, this.light.shadow.focus = 1, this.light.shadow.camera.updateProjectionMatrix(), this.light.shadow.mapSize.set(m, m), this.light.visible = d, d0(c, this.light.color), h !== "#ffffff" && (this.light.color = new _e(h));
    const { value: x, unit: y } = l;
    switch (y) {
      case "lumen":
        this.light.intensity = Lc(x);
        break;
      default:
        this.light.intensity = x;
        break;
    }
    this.lineSegments.geometry.dispose(), this.circleLine.geometry.dispose();
    const A = new _(), b = u / Math.cos(g), M = Math.sin(g) * b;
    this.lineSegments.geometry.setFromPoints([
      A,
      new _(M, -u, 0),
      A,
      new _(-M, -u, 0)
    ]), this.circleLine.geometry.setFromPoints(Uhe(M, new _(0, -u, 0)));
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    e && (e.config.position = {
      x: this.group.position.x,
      y: this.group.position.y,
      z: this.group.position.z
    }, e.config.rotation = {
      x: this.group.rotation.x,
      y: this.group.rotation.y,
      z: this.group.rotation.z
    }, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), this.needDataUpdateServer = !1, dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((t) => t.toJson())
    }));
  }
  update(e) {
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config);
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a), this.initialize(e.renderer.cameraControls), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.isInit = !0;
  }
  /**
   * 使用过渡动画切换模型组件状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.states.getStateConfigById(e);
    if (i) {
      this.states.setState(e), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), this.light.visible = !0;
      const n = {
        position_x: this.group.position.x,
        position_y: this.group.position.y,
        position_z: this.group.position.z,
        rotation_x: this.group.rotation.x,
        rotation_y: this.group.rotation.y,
        rotation_z: this.group.rotation.z,
        intensity: this.light.visible ? this.light.intensity : 0,
        color: this.light.color,
        angle: this.light.angle,
        distance: this.light.distance,
        penumbra: this.light.penumbra,
        decay: this.light.decay,
        shadowRange: this.light.shadow.camera.far,
        bias: this.light.shadow.bias,
        radius: this.light.shadow.radius
      }, r = {
        position_x: i.position.x,
        position_y: i.position.y,
        position_z: i.position.z,
        rotation_x: i.rotation.x,
        rotation_y: i.rotation.y,
        rotation_z: i.rotation.z,
        intensity: i.enable ? Lc(i.intensity.value) : 0,
        color: new _e(i.color),
        angle: i.angle * 0.5 * nt.DEG2RAD,
        distance: i.distance,
        penumbra: i.penumbra,
        decay: i.decay,
        shadowRange: i.shadowRange,
        bias: i.bias,
        radius: i.radius
      };
      !isNaN(i.kelvin) && i.color === "#ffffff" ? r.color = d0(i.kelvin, new _e()) : r.color = new _e(i.color);
      const { value: a, unit: o } = i.intensity;
      switch (o) {
        case "lumen":
          r.intensity = Lc(a);
          break;
        default:
          r.intensity = a;
          break;
      }
      const l = jn.createTweenHandler({
        mode: t.mode,
        duration: t.duration,
        source: n,
        target: r,
        execute: (c, h) => {
          this.group.position.x = c.position_x, this.group.position.y = c.position_y, this.group.position.z = c.position_z, this.group.rotation.x = c.rotation_x, this.group.rotation.y = c.rotation_y, this.group.rotation.z = c.rotation_z, this.light.color = n.color.lerpColors(
            n.color,
            r.color,
            h
          ), this.light.intensity = c.intensity, this.light.distance = c.distance, this.light.angle = c.angle, this.light.penumbra = c.penumbra, this.light.decay = c.decay, this.light.shadow.camera.far = c.shadowRange, this.light.shadow.bias = c.bias, this.light.shadow.radius = c.radius;
        },
        complete: () => {
          this.updateLightOptions(i), Yi(l), this.tweenHandler = void 0;
        }
      });
      this.tweenHandler = l;
    }
  }
};
d7 = Bge([
  ni(Fge)
], d7);
var kge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Gge = "RectAreaLight";
let f7 = class extends Uu {
  constructor() {
    super(...arguments);
    S(this, "light", new y2());
    S(this, "rectLineSegments", Ug([]));
  }
  initialize() {
    Dte.init(), this.group.add(this.light), this.group.add(this.helper), this.initSprite(She, this.normalSprite), this.initSprite(whe, this.highlightSprite), this.group.add(this.normalSprite), this.group.add(this.highlightSprite), this.helper.visible = !1;
    const e = this.states.deepGet(this.getCurStateId());
    e && this.updateLightOptions(e.config);
    const t = [1, 1, 0, -1, 1, 0, -1, -1, 0, 1, -1, 0, 1, 1, 0];
    this.rectLineSegments.geometry = new Pt(), this.rectLineSegments.geometry.setAttribute(
      "position",
      new gt(t, 3)
    ), this.rectLineSegments.geometry.computeBoundingSphere(), this.helper.add(this.rectLineSegments);
  }
  updateLightOptions(e) {
    const { position: t, rotation: i, intensity: n, width: r, height: a, shape: o, color: l, kelvin: c, enable: h } = e;
    this.group.position.set(t.x, t.y, t.z), this.group.rotation.set(i.x, i.y, i.z), d0(c, this.light.color), l !== "#ffffff" && (this.light.color = new _e(l)), this.light.visible = h, this.light.width = r, this.light.height = a, this.rectLineSegments.scale.set(r / 2, a / 2, 1);
    const { value: u, unit: d } = n;
    switch (d) {
      case "lumen":
        this.light.intensity = Lc(u);
        break;
      default:
        this.light.intensity = u;
        break;
    }
  }
  update(e) {
    const t = this.states.deepGet(this.getCurStateId());
    t && this.updateLightOptions(t.config);
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    e && (e.config.position = {
      x: this.group.position.x,
      y: this.group.position.y,
      z: this.group.position.z
    }, e.config.rotation = {
      x: this.group.rotation.x,
      y: this.group.rotation.y,
      z: this.group.rotation.z
    }, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), this.needDataUpdateServer = !1, dt.Entity.setState({
      id: this.entity.uuid,
      name: this.entity.name,
      components: this.entity.getComponentArray().map((t) => t.toJson())
    }));
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a), this.initialize(), Tt.enabled ? this.setHelperVisible(!0) : this.setHelperVisible(!1), this.isInit = !0;
  }
  /**
   * 使用过渡动画切换模型组件状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.states.getStateConfigById(e);
    if (i) {
      this.states.setState(e), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), this.light.visible = !0;
      const n = {
        position_x: this.group.position.x,
        position_y: this.group.position.y,
        position_z: this.group.position.z,
        rotation_x: this.group.rotation.x,
        rotation_y: this.group.rotation.y,
        rotation_z: this.group.rotation.z,
        intensity: this.light.visible ? this.light.intensity : 0,
        color: this.light.color,
        width: this.light.width,
        height: this.light.height
      }, r = {
        position_x: i.position.x,
        position_y: i.position.y,
        position_z: i.position.z,
        rotation_x: i.rotation.x,
        rotation_y: i.rotation.y,
        rotation_z: i.rotation.z,
        intensity: i.enable ? Lc(i.intensity.value) : 0,
        color: new _e(i.color),
        width: i.width,
        height: i.height
      };
      !isNaN(i.kelvin) && i.color === "#ffffff" ? r.color = d0(i.kelvin, new _e()) : r.color = new _e(i.color);
      const { value: a, unit: o } = i.intensity;
      switch (o) {
        case "lumen":
          r.intensity = Lc(a);
          break;
        default:
          r.intensity = a;
          break;
      }
      const l = jn.createTweenHandler({
        mode: t.mode,
        duration: t.duration,
        source: n,
        target: r,
        execute: (c, h) => {
          this.group.position.x = c.position_x, this.group.position.y = c.position_y, this.group.position.z = c.position_z, this.group.rotation.x = c.rotation_x, this.group.rotation.y = c.rotation_y, this.group.rotation.z = c.rotation_z, this.light.intensity = c.intensity, this.light.width = c.width, this.light.height = c.height, this.light.color = n.color.lerpColors(
            n.color,
            r.color,
            h
          );
        },
        complete: () => {
          this.updateLightOptions(i), Yi(l), this.tweenHandler = void 0;
        }
      });
      this.tweenHandler = l;
    }
  }
};
f7 = kge([
  ni(Gge)
], f7);
class Hge {
  addComponent(e, t, i) {
    const n = new Fi(e, t, i);
    return i.componentMap.set(e, n), n;
  }
  getComponent(e, t) {
    return t.componentMap.get(e);
  }
  clearComponent(e, t) {
    t.componentMap.delete(e);
  }
  addInfiniteGridComponent(e, t) {
    const i = t.addComponent(
      e,
      "InfiniteGridComponent",
      i7,
      !0
    );
    i.initialize(), i.isInit = !0;
  }
  loadSceneComponent(e, t, i) {
    const n = i.addComponent(e, "Scene", Qx, !0);
    return n.initialize(t), n.isInit = !0, n;
  }
  addDrawComponent(e, t, i) {
    const n = i.addComponent(e, "Draw", pge, !0);
    return n.initialize(t), n.isInit = !0, n;
  }
  getComponentClass(e) {
    return Hc.getInstance().resolve(
      `${e.type}@${e.version ?? "1.7.0"}`
    );
  }
  async parseComponentOptions(e, t, i, n = !1) {
    const { type: r, id: a, version: o } = i;
    let l = t.componentMap.get(a);
    if (l)
      return l;
    const { ctor: c } = this.getComponentClass(i);
    return l = t.addComponent(a, i.name ?? "", c, n), await l.updateFromJson(e, i), l;
  }
  // 处理场景初始化产生的默认组件
  handleInitComponents(e, t) {
    t.forEach((i) => {
      var r, a;
      const { states: n } = i;
      switch (i.type) {
        case "Scene":
          this.initSceneComponent(e, i);
          break;
        case "Camera": {
          const o = e.addComponent(
            i.id,
            i.name,
            Kf,
            !0
          );
          o.initialize(e.renderer.cameraControls), o.preProcessCameraPointComponentOptions(i), o.initStates(
            n
          ), o.setCurrentStateById((r = n[0]) == null ? void 0 : r.id), o.focusCurStateCamera((a = n[0]) == null ? void 0 : a.id, !0).isInit = !0;
          break;
        }
        case "BuildingsAnimation":
          e.addComponent(
            i.id,
            i.name ?? "",
            this.getComponentClass(i).ctor,
            !0
          ).updateFromJson(e, i);
          break;
      }
    });
  }
  initSceneComponent(e, t) {
    const { id: i, states: n, data: r } = t, a = this.loadSceneComponent(i, e.renderer, e);
    a.initStates(n), a.updateSceneData(r), a.isInit = !0;
  }
  loadPathCruiseComponent(e, t, i = !1) {
    const { id: n, name: r, states: a } = t, o = e.addComponent(
      n,
      r ?? "",
      zx,
      i
    ), l = a;
    return l && o.states.initStates(l), o;
  }
  addPathCruiseComponent(e, t, i, n = !1) {
    const r = e.addComponent(
      t,
      i ?? "",
      zx,
      n
    );
    return r.isInit = !0, r;
  }
  addClusteringHeatmapComponent(e, t, i, n, r = !1) {
    const a = e.addComponent(
      t,
      n ?? "",
      $x,
      r
    );
    return a.initialize(i), a.isInit = !0, a;
  }
  addCustomAnimationComponent(e, t, i, n = !1) {
    const r = e.addComponent(
      t,
      i ?? "",
      Yx,
      n
    );
    return r.isInit = !0, r;
  }
  addSunLightComponent(e, t, i, n, r = !1) {
    const a = t.addComponent(
      i,
      n ?? "",
      Gx,
      r
    );
    return a.initialize(e.renderer.mainCamera), a.isInit = !0, a;
  }
  addSpotLightComponent(e, t, i, n, r = !1) {
    const a = t.addComponent(
      i,
      n ?? "",
      d7,
      r
    );
    return a.initialize(e.renderer.cameraControls), a.isInit = !0, a;
  }
  addRectAreaLightComponent(e, t, i, n = !1) {
    const r = e.addComponent(
      t,
      i ?? "",
      f7,
      n
    );
    return r.initialize(), r.isInit = !0, r;
  }
  loadAnimationComponent(e, t, i, n = !1) {
    const { id: r, name: a } = i, o = t.addComponent(
      r,
      a ?? "AnimationComponent",
      a8e,
      n
    );
    return o.fromJson(i, () => {
      o.animationMixer && o._actionProxies.length > 0 && o.startAnimationLoop();
    }), o;
  }
  // 组件在层级设置后, 重新更新组件的显示隐藏
  setComponentVisible(e) {
    const t = (i) => i.visible && i.parent ? i.parent.visible ? t(i.parent) : !1 : i.visible;
    e.entityManager.entityMap.forEach((i) => {
      const n = t(i);
      i.componentMap.forEach((r) => {
        r.setVisible(n);
      });
    });
  }
  async transferModelComponentToInstance(e) {
    const t = e.toJson().data, n = {
      id: "",
      type: "InstanceModel",
      name: "实例模型",
      visible: !0,
      inActive: !1,
      version: "1.7.0",
      data: {
        ...t,
        children: [
          {
            id: nt.generateUUID(),
            position: t.transform.position,
            rotation: {
              x: 0,
              y: 0,
              z: 0
            },
            scale: {
              x: 0,
              y: 0,
              z: 0
            },
            constrainedScale: !1,
            name: e.group.name,
            customAttribute: t.customAttribute
          }
        ],
        castShadow: !1,
        receiveShadow: !1,
        disablePick: !1,
        positionMode: "xyz",
        groupPosition: { x: 0, y: 0, z: 0 },
        groupRotation: { x: 0, y: 0, z: 0 },
        groupScale: { x: 1, y: 1, z: 1 },
        groupConstrainedScale: !1,
        childTransform: {
          rotation: t.transform.rotation,
          scale: t.transform.scale,
          constrainedScale: !1
        }
      },
      states: []
    }, { entity: r } = e, a = [];
    r.componentMap.forEach((l) => {
      a.push(l.id);
    });
    const [o] = await Qe.ApiServer.replaceComponent(
      e.entity.uuid,
      a,
      [n]
    );
    if (await Qe.ApiServer.updateVersions([
      {
        id: r.uuid,
        commonConfig: {
          version: "2.6.0"
        },
        components: []
      }
    ]), r.removeComponent(a, !1), o) {
      const l = r.addComponent(
        o.id,
        e.name,
        Jg,
        !0
      );
      await l.fromJson(n), l.isInit = !0;
    }
  }
}
class Vge {
  // public readonly entityGroup = new Group();
  constructor(e) {
    S(this, "entityMap", /* @__PURE__ */ new Map());
    // 这里记录 已经从场景中移除,放入回收站的实体
    S(this, "recycleEntityMap", /* @__PURE__ */ new Map());
    S(this, "rootScene");
    this.world = e, this.rootScene = e.renderer.rootScene;
  }
  registerCommonConfig(e, t) {
    const { version: i } = t;
    if (i) {
      const n = Qe.EntityCommonConfigVersionMap.get(e.type);
      if (n)
        n.add(i);
      else {
        const r = /* @__PURE__ */ new Set();
        r.add(i), Qe.EntityCommonConfigVersionMap.set(e.type, r);
      }
    }
  }
  setEntityCollapsed(e, t) {
    const i = this.entityMap.get(e);
    i && (i.userData.config.isCollapsed = t), i && Qe.ApiServer.updateObjectHierarchy(
      this.rootScene.uuid,
      [i.uuid],
      "isCollapsed",
      i.userData.config.isCollapsed
    ), this.updateWorldListStore();
  }
  setEntityName(e, t) {
    const i = this.entityMap.get(e);
    i && (i.name = t), i && Qe.ApiServer.updateObjectName(this.rootScene.uuid, i.uuid, i.name), this.updateWorldListStore();
  }
  findModelComponentEntity() {
    const e = [];
    return this.entityMap.forEach((t) => {
      t.getComponentByType("Model") && e.push(t);
    }), e;
  }
  setEntityVisible(e, t, i = !0) {
    const n = this.entityMap.get(e);
    n && (t !== void 0 ? n.visible = t : n.visible = !n.visible, n.componentMap.forEach((r) => {
      r.setVisible(n.visible);
    }), n.getComponentByType("EngineGroup") && n.traverse((r) => {
      "eType" in r && r.componentMap.forEach((o) => {
        o.setVisible(n.visible);
      });
    }), i && (Qe.ApiServer.updateObjectHierarchy(
      this.rootScene.uuid,
      [n.uuid],
      "visible",
      n.visible
    ), this.updateWorldListStore())), this.world.getSelectComponent().refresh();
  }
  async updateEntitySort(e) {
    await Qe.ApiServer.updateObjectSort(this.rootScene.uuid, e);
    const t = new Map(this.entityMap);
    this.entityMap.clear();
    const i = await Qe.ApiServer.getObjects(this.rootScene.uuid);
    i == null || i.objs.forEach((n) => {
      const r = t.get(n.id);
      if (r)
        if (r.parentId = n.parentObjId, r.mPath = n.mpath, this.entityMap.set(n.id, r), r.parentId) {
          const a = t.get(r.parentId);
          a == null || a.add(r);
        } else
          this.rootScene.add(r);
    }), this.entityMap.forEach((n) => {
      const r = n.getComponentByType("Iframe");
      r && r.setVisible(n.visible);
    }), this.updateWorldListStore();
  }
  async copyEntity(e, t, i, n) {
    let r;
    t !== Qe.ApiServer.sceneId ? r = await Qe.ApiServer.copyEntitiesFromOtherScene(
      e,
      t,
      i,
      n
    ) : r = await Qe.ApiServer.copyObjects(
      this.rootScene.uuid,
      e,
      i,
      n
    );
    const a = /* @__PURE__ */ new Map();
    r.forEach(({ objId: u }) => a.set(u, u));
    const o = await Qe.ApiServer.getObjects(this.rootScene.uuid), l = [], c = [];
    o == null || o.objs.forEach((u) => {
      a.has(u.id) && l.push(u);
    }), o == null || o.components.forEach((u) => {
      a.has(u.objId) && c.push(u);
    }), await Wge(c), await st.assetManager.syncAssets(), dt.SceneMaterialsStore.updateSceneMaterialList(), st.parseSceneData(this.world.containerId, {
      entities: l,
      components: c
    });
    const h = new Map(this.entityMap);
    return o && (this.entityMap.clear(), o.objs.forEach((u) => {
      const d = h.get(u.id);
      d && this.entityMap.set(u.id, d);
    })), this.updateWorldListStore(), await new Promise((u) => {
      const d = () => {
        let f = !0;
        return this.entityMap.forEach((p) => {
          f && (f = p.isInitComplete);
        }), f;
      };
      if (d())
        u(void 0);
      else {
        let f = 10;
        const p = setInterval(() => {
          (d() || f < 0) && (clearInterval(p), u(void 0)), f--;
        }, 200);
      }
    }), r;
  }
  setEntityComponentObjectsVisible(e, t) {
    const { visible: i, ids: n, attributes: r } = t, a = this.entityMap.get(e);
    a && a.componentMap.forEach((o) => {
      const l = (c, h) => {
        c.visible = h, "setVisible" in c && c.setVisible(h);
      };
      if (r) {
        const c = o.filterByAttribute(o.group.children, r);
        c.forEach((h) => {
          const u = i !== void 0 ? i : !h.visible;
          l(h, u);
        }), o.group.children.forEach((h) => {
          c.indexOf(h) === -1 && a.visible === !1 && i === !0 && l(h, !1);
        }), i && (a.visible = i);
      }
      n == null || n.forEach((c) => {
        const h = o.getObjectById(c);
        if (h) {
          const u = i !== void 0 ? i : !h.visible;
          l(h, u);
        }
      }), o instanceof Jg && o.updateObjectVisible(n, r, i);
    });
  }
  setEntitiesVisible(e, t) {
    e.forEach((i) => {
      this.setEntityVisible(i, t, !1);
    }), this.updateWorldListStore(), Qe.ApiServer.updateObjectHierarchy(this.rootScene.uuid, e, "visible", t);
  }
  async setEntitiesCommonConfig(e) {
    const t = e.map(({ id: i, version: n }) => ({ id: i, commonConfig: { version: n }, components: [] }));
    return e.forEach(({ id: i, version: n }) => {
      const r = this.entityMap.get(i);
      r && (r.commonConfig.version = n);
    }), await Qe.ApiServer.updateVersions(t);
  }
  setEntitiesInActive(e, t) {
    e.forEach((i) => {
      const n = this.entityMap.get(i);
      n && (n.inActive = t);
    }), this.world.getSelectComponent().refresh(), this.updateWorldListStore(), Qe.ApiServer.updateObjectHierarchy(this.rootScene.uuid, e, "inActive", t);
  }
  setEntityInActive(e, t) {
    const i = this.entityMap.get(e);
    i && (i.inActive = t), i && Qe.ApiServer.updateObjectHierarchy(
      this.rootScene.uuid,
      [i.uuid],
      "inActive",
      i.inActive
    ), this.world.getSelectComponent().refresh(), this.updateWorldListStore();
  }
  // 切换 实体显示隐藏
  switchEntityVisible(e) {
    const t = this.entityMap.get(e);
    t && (t.visible = !t.visible), t && Qe.ApiServer.updateObjectHierarchy(
      this.rootScene.uuid,
      [t.uuid],
      "visible",
      t.visible
    ), this.updateWorldListStore();
  }
  async addEntityAsync(e, t = [], i = !Tt.enabled) {
    let n = [];
    if (!i) {
      const a = await Qe.ApiServer.addObject(
        this.rootScene.uuid,
        {
          id: "",
          type: e.type,
          name: e.name,
          visible: !0,
          inActive: !1,
          parentId: e.parentId,
          commonConfig: e.commonConfig
        },
        t
      );
      e.id = a.objId, a.components && (n = a.components);
    }
    !e.id && (e.id = nt.generateUUID());
    const r = this.createEntity(e);
    return this._addEntityToScene(r, i), r.isAdded = !0, r.dispatchEvent({
      type: Iy.Add
    }), [r, n];
  }
  async addEntityWithComponents(e, t = []) {
    const i = await Qe.ApiServer.addObject(
      this.rootScene.uuid,
      {
        id: "",
        type: e.type,
        name: e.name,
        visible: !0,
        inActive: !1,
        parentId: e.parentId,
        commonConfig: e.commonConfig
      },
      t
    );
    e.id = i.objId;
    const n = this.createEntity(e);
    this._addEntityToScene(n), n.isAdded = !0, n.dispatchEvent({
      type: Iy.Add
    });
    try {
      t.forEach((r, a) => {
        r.id = i.components[a].id, r.states.forEach((o, l) => {
          o.id = i.components[a].componentStateIds[l];
        });
      });
    } catch (r) {
      console.error(r);
    }
    return t.forEach((r) => {
      this.world.componentManager.parseComponentOptions(this.world, n, r, !0);
    }), n;
  }
  _addEntityToScene(e, t = !Tt.enabled) {
    Qe.history.execute(new nR(this.world, e, t));
  }
  createEntity(e) {
    if ("visible" in e) {
      const t = new Pl(e);
      return t.world = this.world, t;
    } else {
      const t = new ed(e);
      return t.world = this.world, t;
    }
  }
  handleEntityHierarchy() {
    this.entityMap.forEach((e) => {
      if (e.parentId) {
        const t = this.entityMap.get(e.parentId);
        t == null || t.add(e);
      }
    });
  }
  parseEntity(e) {
    let t = this.entityMap.get(e.id);
    return t || (t = this.createEntity(e), this.entityMap.set(t.uuid, t), this.rootScene.add(t), this.recycleEntityMap.delete(t.uuid), t.isAdded = !0, this.updateWorldListStore(), t);
  }
  addEntity(e, t = !Tt.enabled) {
    let i = this.entityMap.get(e.id);
    return i || (i = this.createEntity(e), this._addEntityToScene(i, t), !i.isAdded && Tt.enabled && Qe.ApiServer.addObject(this.rootScene.uuid, e).then(({ objId: n }) => {
      if (!t) {
        const r = this.entityMap.get(e.id);
        r && (r.setId(n), this.entityMap.set(n, r), this.entityMap.delete(e.id), r.isAdded = !0, r.dispatchEvent({
          type: Iy.Add
        }), this.updateWorldListStore());
      }
    }), i);
  }
  async restoreEntityById(e) {
    const t = [];
    return e instanceof Array ? t.push(...e) : t.push(e), Qe.history.execute(new Xge(this.world, t));
  }
  removeEntityById(e) {
    Qe.history.execute(new jge(this.world, e)).then(() => {
      Qe.ApiServer.getDeletedObjects(this.rootScene.uuid).then((t) => {
        t && (this.recycleEntityMap.forEach((i) => {
          var n;
          i.deletedAt = (n = t.find((r) => r.id === i.uuid)) == null ? void 0 : n.deletedAt, t.find((r) => r.id === i.uuid) || t.push({
            id: i.uuid,
            name: i.name,
            type: i.eType,
            deletedAt: i.deletedAt ?? It().format(),
            parentObjId: i.parentId ?? null
          });
        }), dt.RecycleEntity.setState(this.traverseDeleteDataToTree(t)), dt.Entity.refresh());
      });
    });
  }
  traverseDeleteDataToTree(e) {
    const t = () => {
      const o = [], l = /* @__PURE__ */ new Map(), c = e.map((h) => h.id);
      return e.forEach((h) => {
        const u = {
          entityId: h.id,
          type: h.type,
          deletedAt: h.deletedAt,
          label: h.name,
          parentEntityId: h.parentObjId,
          mPath: "",
          visible: !1,
          inActive: !1,
          isCollapsed: !1,
          children: []
        };
        if (h.parentObjId && c.includes(h.parentObjId)) {
          const d = l.get(h.parentObjId);
          d ? d.push(u) : l.set(h.parentObjId, [u]);
        } else
          o.push(u);
      }), [o, l];
    }, [i, n] = t(), r = (o) => {
      const l = [], c = n.get(o.entityId);
      return c && l.push(...c), c == null || c.forEach((h) => {
        h.children = r(h);
      }), l;
    }, a = [];
    return i.forEach((o) => {
      o.children = r(o), a.push(o);
    }), a;
  }
  async removeRecycleEntityById(e) {
    return e.forEach((t) => {
      this.recycleEntityMap.delete(t);
    }), dt.RecycleEntity.setState(this.traverseWorldEntityMapToTree(this.recycleEntityMap)), await Qe.ApiServer.deleteObjects(this.rootScene.uuid, e, [], !0);
  }
  getEntity(e) {
    return this.entityMap.get(e);
  }
  getEntityByProperty(e, t) {
    const i = [];
    return this.entityMap.forEach((n) => {
      n[e] === t && i.push(n);
    }), i;
  }
  getRootChildrenEntity(e) {
    const t = /* @__PURE__ */ new Map(), i = /* @__PURE__ */ new Map();
    return e.forEach((n) => {
      if (n.parentId && n.parentId !== null && e.get(n.parentId)) {
        if (n.isAdded) {
          const r = i.get(n.parentId);
          r ? r.push(n) : i.set(n.parentId, [n]);
        }
      } else
        n.isAdded && t.set(n.uuid, n);
    }), [t, i];
  }
  clearEntity(e) {
    const t = this.entityMap.get(e);
    t && t.dispose(), this.entityMap.delete(e);
  }
  clear() {
    this.entityMap.forEach((e) => {
      e.dispose();
    }), this.entityMap.clear();
  }
  delete() {
    const e = [];
    this.entityMap.forEach((t) => {
      t.isAdded && e.push(t.uuid), t.dispose();
    }), Qe.ApiServer.deleteObjects(this.rootScene.uuid, e), this.entityMap.clear(), this.updateWorldListStore();
  }
  toJson() {
    const e = [], t = [];
    return this.entityMap.forEach((i) => {
      e.push({
        id: i.uuid,
        parentId: i.parentId,
        components: Array.from(i.componentMap.keys()),
        type: i.type
      }), i.componentMap.forEach((n) => {
        t.push(n.toJson());
      });
    }), [e, t];
  }
  updateWorldListStore() {
    let e = this.traverseWorldEntityMapToTree(this.entityMap);
    Tt.enabled || e.unshift({
      entityId: this.world.uuid,
      type: "EngineGroup",
      label: "基础实体",
      mPath: this.world.mPath,
      visible: !0,
      inActive: !1,
      isCollapsed: !0,
      children: []
    }), Qe.mode === "VideoProjectEditor" && (e = e.filter((t) => t.type === "VideoProject")), e = e.filter(
      (t) => t.type !== "BuildingsAnimation" && t.type !== "ClipModel"
    ), dt.World.setState(e);
  }
  /**
   *
   * @param entityMap
   * @param force 无论是否加载完成都显示
   * @returns
   */
  traverseWorldEntityMapToTree(e, t = !1) {
    const i = [], [n, r] = this.getRootChildrenEntity(e), a = (o, l) => {
      const c = [], h = r.get(l.uuid);
      return h && h.forEach((u) => {
        const d = this.getEntityArchetype(u);
        if (u.eType = d, u.isInitComplete || t) {
          const f = {
            entityId: u.uuid,
            mPath: u.mPath,
            parentEntityId: u.parentId,
            visible: u.visible,
            inActive: u.inActive,
            label: u.name,
            isCollapsed: u.userData.config.isCollapsed,
            type: d,
            children: [],
            deletedAt: u.deletedAt
          };
          c.push(f), f.children = a(o, u);
        }
      }), c;
    };
    return n.forEach((o) => {
      const l = this.getEntityArchetype(o);
      if (o.eType = l, o.isInitComplete || t) {
        const c = {
          entityId: o.uuid,
          parentEntityId: o.parentId,
          label: o.name,
          visible: o.visible,
          inActive: o.inActive,
          mPath: o.mPath,
          isCollapsed: o.userData.config.isCollapsed,
          type: this.getEntityArchetype(o),
          deletedAt: o.deletedAt,
          children: []
        };
        c.children = a(c, o), i.push(c);
      }
    }), i;
  }
  getEntityArchetype(e) {
    return e.getComponentByType("Model") ? "Model" : e.getComponentByType("InstanceModel") ? "InstanceModel" : e.getComponentByType("Point") ? "Point" : e.getComponentByType("PoiPanel") ? "PoiPanel" : e.getComponentByType("PoiPanelV2") ? "PoiPanelV2" : e.getComponentByType("Floor") ? "Floor" : e.getComponentByType("PointLight") ? "PointLight" : e.getComponentByType("DirectLight") ? "DirectLight" : e.getComponentByType("SpotLight") ? "SpotLight" : e.getComponentByType("RectAreaLight") ? "RectAreaLight" : e.getComponentByType("SunLight") ? "SunLight" : e.getComponentByType("Iframe") ? "Iframe" : e.getComponentByType("IframeV2") ? "IframeV2" : e.getComponentByType("FlowFence") ? "FlowFence" : e.getComponentByType("BatchModel") ? "BatchModel" : e.getComponentByType("ClusteringHeatmap") ? "ClusteringHeatmap" : e.getComponentByType("AreaHeatmap") ? "AreaHeatmap" : e.getComponentByType("Water") ? "Water" : e.getComponentByType("FlowRoute") ? "FlowRoute" : e.getComponentByType("CameraCruise") ? "CameraCruise" : e.getComponentByType("CameraSurround") ? "CameraSurround" : e.getComponentByType("CameraFirstPerson") ? "CameraFirstPerson" : e.getComponentByType("VideoProject") ? "VideoProject" : e.getComponentByType("Camera") ? "Camera" : e.getComponentByType("BuildingsAnimation") ? "BuildingsAnimation" : e.getComponentByType("ParticleSystem") ? "ParticleSystem" : e.getComponentByType("ClipModel") ? "ClipModel" : e.getComponentByType("MiniMap") ? "MiniMap" : (e.componentMap.size === 0 || e.getComponentByType("EngineGroup"), "EngineGroup");
  }
  isEntityDigital(e) {
    return !!(e.getComponentByType("Point") || e.getComponentByType("PoiPanel") || e.getComponentByType("PoiPanelV2") || e.getComponentByType("FlowFence") || e.getComponentByType("Iframe") || e.getComponentByType("ClusteringHeatmap") || e.getComponentByType("AreaHeatmap") || e.getComponentByType("FlowRoute") || e.getComponentByType("Floor") || e.getComponentByType("Water") || e.getComponentByType("InstanceModel"));
  }
  execute(e) {
    switch (e.command) {
      case "updateVisible":
        this.setEntityVisible(
          e.targetId,
          e.data.visible,
          !1
        );
        break;
      case "updateComponentObjectsVisible":
        this.setEntityComponentObjectsVisible(
          e.targetId,
          e.data
        );
        break;
    }
  }
  getEntityOperations() {
    const e = [];
    return Qe.history.commandMap.forEach((t) => {
      if (t instanceof nR) {
        const { uuid: i, eType: n, name: r } = t.entity;
        e.push({
          id: i,
          type: n,
          name: r
        });
      }
    }), e;
  }
}
async function Wge(s) {
  const e = /* @__PURE__ */ new Set();
  s.forEach((i) => {
    if (i.type === "Model" || i.type === "InstanceModel") {
      const n = i.data.asset.url;
      e.add(n);
    } else if (i.type === "BatchModel") {
      const r = i.states[0].config.asset.url;
      e.add(r);
    }
  });
  const t = [...e].map(async (i) => {
    await st.assetManager.modelManager.loadAndCacheModelByUrl(i);
  });
  await Promise.all(t);
}
var p7 = /* @__PURE__ */ ((s) => (s.None = "None", s.Transform = "transform", s))(p7 || {});
const Za = (s, e) => `${s}-${e}`;
class nR extends Rl {
  constructor(e, t, i) {
    super(x0.AddEntity, "AddEntityCommand"), this.world = e, this.entity = t, this.isAdded = i;
  }
  async execute() {
    this.world.entityManager.entityMap.set(this.entity.uuid, this.entity), this.world.entityManager.rootScene.add(this.entity), this.world.entityManager.recycleEntityMap.delete(this.entity.uuid), this.entity.isAdded = this.isAdded, this.entity.isAdded && this.world.entityManager.updateWorldListStore();
  }
  undo() {
    this.world.entityManager.entityMap.delete(this.entity.uuid), this.world.entityManager.recycleEntityMap.set(this.entity.uuid, this.entity), this.world.entityManager.rootScene.remove(this.entity), this.world.entityManager.updateWorldListStore(), Qe.ApiServer.deleteObjects(this.world.entityManager.rootScene.uuid, [
      this.entity.uuid
    ]);
  }
  redo() {
    const { rootScene: e } = this.world.entityManager;
    Qe.ApiServer.recoverObject(e.uuid, [this.entity.uuid]).then(() => {
      this.world.entityManager.entityMap.set(this.entity.uuid, this.entity), this.world.entityManager.rootScene.add(this.entity), this.world.entityManager.recycleEntityMap.delete(this.entity.uuid), this.entity.isAdded = !0, this.world.entityManager.updateWorldListStore();
    });
  }
}
class Xge extends Rl {
  constructor(e, t) {
    super(x0.RestoreEntity, "RestoreEntityCommand"), this.world = e, this.entityIds = t;
  }
  async execute() {
    const { rootScene: e, recycleEntityMap: t, entityMap: i } = this.world.entityManager;
    await Qe.ApiServer.recoverObject(e.uuid, [...this.entityIds]), this.entityIds.forEach((n) => {
      const r = this.world.entityManager.recycleEntityMap.get(n);
      r && (i.set(r.uuid, r), t.delete(n));
    }), this.loadDeletedEntity();
  }
  undo() {
  }
  redo() {
  }
  loadDeletedEntity() {
    const { rootScene: e, recycleEntityMap: t, entityMap: i } = this.world.entityManager;
    let n = !1;
    this.entityIds.forEach((r) => {
      t.get(r) || (n = !0);
    }), n ? Qe.ApiServer.getObjects(e.uuid).then((r) => {
      if (r) {
        const { objs: a, components: o } = r, l = a == null ? void 0 : a.filter((c) => this.entityIds.includes(c.id));
        l.forEach((c) => {
          const h = new ed({
            id: c.id,
            type: c.type,
            name: c.name,
            parentId: c.parentObjId,
            mPath: c.mpath,
            visible: c.hierarchyConfig.visible,
            inActive: c.hierarchyConfig.inActive
          });
          h.isAdded = !0, h.world = this.world, h.deletedAt = c.deletedAt ?? void 0;
          const u = o.filter(
            (d) => d.objId === c.id
          );
          u == null || u.forEach((d) => {
            this.world.componentManager.parseComponentOptions(
              this.world,
              h,
              d,
              !0
            );
          }), i.set(h.uuid, h);
        }), l.forEach(({ id: c, parentObjId: h }) => {
          const u = i.get(c);
          if (u)
            if (h) {
              const d = i.get(h);
              d == null || d.add(u);
            } else
              e.add(u);
        }), this.world.entityManager.updateWorldListStore(), Qe.ApiServer.getDeletedObjects(e.uuid).then((c) => {
          c && dt.RecycleEntity.setState(
            this.world.entityManager.traverseDeleteDataToTree(c)
          );
        });
      }
    }) : this.world.entityManager.updateWorldListStore();
  }
}
class jge extends Rl {
  constructor(t, i) {
    super(x0.RemoveEntity, "RemoveEntityCommand");
    S(this, "entityTree", []);
    S(this, "deleteFinish", !0);
    this.world = t, this.entityIds = i;
  }
  async execute() {
    this.deleteEntities();
  }
  deleteEntities() {
    const t = this.getModelPrivateMaterialIds();
    Qe.ApiServer.deleteObjects(
      this.world.entityManager.rootScene.uuid,
      this.entityIds,
      t
    ).then(() => {
      this.world.entityManager.updateWorldListStore();
    });
    const { entityMap: i, recycleEntityMap: n, rootScene: r } = this.world.entityManager, a = [];
    function o(l) {
      l == null || l.getComponentArray().forEach((c) => c.dispose());
    }
    this.entityIds.forEach((l) => {
      var u, d;
      const c = i.get(l);
      c && a.push({
        entityId: l,
        parentEntityId: (u = c.parent) == null ? void 0 : u.uuid,
        mPath: c.mPath,
        type: c.eType,
        label: "",
        visible: c.visible,
        inActive: c.inActive,
        isCollapsed: !1,
        deletedAt: "",
        children: []
      }), (d = c == null ? void 0 : c.parent) == null || d.remove(c), o(c), i.delete(l), c && n.set(l, c), (c == null ? void 0 : c.mPath) && i.forEach((f) => {
        var p, v;
        f.mPath.includes(l) && (i.delete(f.uuid), o(f), n.set(f.uuid, f), (p = f == null ? void 0 : f.parent) == null || p.remove(f), f && a.push({
          entityId: f.uuid,
          parentEntityId: (v = f.parent) == null ? void 0 : v.uuid,
          mPath: "",
          type: "EngineGroup",
          deletedAt: "",
          label: "",
          visible: c.visible,
          inActive: c.inActive,
          isCollapsed: !1,
          children: []
        }));
      });
    }), this.world.getSelectComponent().clearIntersections(), Qe.ApiServer.getDeletedObjects(r.uuid).then((l) => {
      if (l) {
        const c = l.map((h) => h.id);
        this.entityIds = this.entityIds.filter((h) => c.includes(h)), this.entityTree = this.convertToTree(a.filter((h) => c.includes(h.entityId))), l.forEach((h) => {
          const u = n.get(h.id);
          u && (u.deletedAt = h.deletedAt);
        }), this.deleteFinish = !0;
      }
    });
  }
  convertToTree(t) {
    const i = t.map((o) => o.entityId), n = [], r = [];
    t.forEach(
      (o) => (i.includes(o.parentEntityId ?? "") ? r : n).push({
        entityId: o.entityId,
        children: [],
        parentEntityId: o.parentEntityId,
        mPath: o.mPath,
        type: o.type,
        label: o.label,
        visible: o.visible,
        inActive: o.inActive,
        isCollapsed: o.isCollapsed,
        deletedAt: ""
      })
    );
    const a = [...n];
    for (; r.length; ) {
      const o = a.map((h) => h.entityId), l = r.find((h) => o.includes(h.parentEntityId ?? "")), c = a.find((h) => h.parentEntityId === (l == null ? void 0 : l.parentEntityId));
      l && r.splice(r.indexOf(l), 1), c != null && c.children ? l && c.children.push(l) : l && c && (c.children = [l]), l && a.push(l);
    }
    return n;
  }
  traverseEntityTree(t, i) {
    i(t), t.children.length && t.children.forEach((n) => this.traverseEntityTree(n, i));
  }
  undo() {
    const t = () => {
      const { entityMap: i, recycleEntityMap: n, rootScene: r } = this.world.entityManager;
      Qe.ApiServer.recoverObject(r.uuid, [...this.entityIds]).then(() => {
        this.entityTree.forEach((a) => {
          this.traverseEntityTree(a, (o) => {
            const { entityId: l, parentEntityId: c } = o;
            let h;
            c && (h = i.get(c), h || (h = n.get(c), n.delete(c))), h || (h = this.world.entityManager.rootScene);
            const u = n.get(l);
            u && (h == null || h.add(u), i.set(u.uuid, u), n.delete(u.uuid));
          });
        }), this.world.entityManager.updateWorldListStore();
      });
    };
    if (this.deleteFinish)
      t();
    else {
      const i = setInterval(() => {
        this.deleteFinish ? (clearInterval(i), t()) : console.warn(`${[...this.entityIds]}删除还未结束`);
      }, 60);
    }
  }
  redo() {
    this.deleteEntities();
  }
  getModelPrivateMaterialIds() {
    const t = [], i = /* @__PURE__ */ new Set();
    this.entityIds.forEach((r) => {
      const a = this.world.entityManager.entityMap.get(r);
      a && (i.add(a.uuid), a.traverse((o) => {
        this.world.entityManager.entityMap.has(o.uuid) && i.add(o.uuid);
      }));
    }), i.forEach((r) => {
      const a = this.world.entityManager.entityMap.get(r);
      if (a) {
        const o = a.getComponentByType("Model");
        o && o.getMaterialSlots().forEach((c) => {
          c.initMaterialId && t.push({
            uuid: c.initMaterialId,
            componentId: o.id
          });
        });
        const l = a.getComponentByType("InstanceModel");
        l && l.getMaterialSlots().forEach((c) => {
          c.initMaterialId && t.push({
            uuid: c.initMaterialId,
            componentId: l.id
          });
        });
      }
    });
    const n = [];
    return t.forEach(({ uuid: r, componentId: a }) => {
      const o = st.assetManager.materialManager.getMaterialByUUID(r);
      if (o) {
        const { serverData: l } = o;
        n.push({
          id: l.id,
          componentId: a,
          uuid: r
        });
      }
    }), n;
  }
}
class qge {
  constructor(e, t) {
    S(this, "_enable", !0);
    S(this, "_cameraKeyMap", /* @__PURE__ */ new Map());
    S(this, "_transformKeyMap", /* @__PURE__ */ new Map());
    this.selection = e, this.renderer = t;
  }
  startRomaCamera(e, t) {
    this._cameraKeyMap.set(e, { step: t }), this._enable = !0;
  }
  startTransform(e, t) {
    this._transformKeyMap.set(e, { step: t }), this._enable = !0;
  }
  animate() {
    const { cameraControls: e } = this.renderer;
    this._cameraKeyMap.forEach(({ step: t }, i) => {
      switch (i) {
        case "a":
          e.truck(-0.1 * t, 0, !1);
          break;
        case "d":
          e.truck(0.1 * t, 0, !1);
          break;
        case "w":
          e.forward(0.1 * t, !1);
          break;
        case "s":
          e.forward(-0.1 * t, !1);
          break;
        case "q":
          e.truck(0, 0.1 * t, !1);
          break;
        case "e":
          e.truck(0, -0.1 * t, !1);
          break;
      }
    }), !(this.selection.transformControls && this.selection.transformControls.mode !== "translate") && this._transformKeyMap.forEach(({ step: t }, i) => {
      const n = new _();
      switch (i) {
        case "w":
          n.set(0, 0, -1 * t);
          break;
        case "s":
          n.set(0, 0, 1 * t);
          break;
        case "a":
          n.set(-1 * t, 0, 0);
          break;
        case "d":
          n.set(1 * t, 0, 0);
          break;
        case "q":
          n.set(0, -1 * t, 0);
          break;
        case "e":
          n.set(0, 1 * t, 0);
          break;
      }
      this.selection.dragPoint.position.add(n), this.selection.intersectionParams.forEach(({ object: r, inActive: a }) => {
        !a && r && r.position.add(n);
      }), this.selection.dragPoint.lastPosition.copy(this.selection.dragPoint.position), this.selection.updateBoundingBox();
    });
  }
  endAnimate(e) {
    this._cameraKeyMap.delete(e), this._transformKeyMap.delete(e), this._cameraKeyMap.size === 0 && this._transformKeyMap.size === 0 && (this._enable = !1), this.selection.handleDragEnd(), Tne().log("endAnimate");
  }
  tick() {
    this._enable && this.animate();
  }
}
const Yge = {
  name: "GammaCorrectionShader",
  uniforms: {
    tDiffuse: { value: null }
  },
  vertexShader: (
    /* glsl */
    `

		varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`
  ),
  fragmentShader: (
    /* glsl */
    `

		uniform sampler2D tDiffuse;

		varying vec2 vUv;

		void main() {

			vec4 tex = texture2D( tDiffuse, vUv );

			gl_FragColor = sRGBTransferOETF( tex );

		}`
  )
};
class td extends Fi {
  constructor() {
    super(...arguments);
    S(this, "pass", new Ao());
  }
}
var Jge = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Qge = "GammaCorrection";
let sR = class extends td {
  initialize() {
    this.pass = new Z4(Yge);
  }
  setOptions(s) {
    const { contrast: e, gamma: t } = s;
    e !== void 0 && (this.pass.material.uniforms.contrast.value = e), t !== void 0 && (this.pass.material.uniforms.gamma.value = t), this.pass.material.needsUpdate = !0, this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      inActive: !1,
      data: {},
      states: [],
      version: this.version
      // attribute: [],
    };
  }
};
sR = Jge([
  ni(Qge)
], sR);
var t2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
let m7 = class extends td {
  constructor() {
    super(...arguments);
    S(this, "_data");
  }
  initialize(e, t, i, n) {
    this.pass = new dp(e, t, i, n), this.pass.enabled = !1, this._data = {
      strength: t,
      radius: i,
      brightnessThreshold: n,
      enable: !1
    };
  }
  setOptions(e) {
    const t = this.pass;
    t.enabled = e.enable ?? !1, this.pass.strength = e.strength ?? 1, this.pass.radius = e.radius ?? 0, this.pass.threshold = e.brightnessThreshold ?? 1, this._data = e, this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      inActive: !1,
      data: {
        ...this._data
      },
      states: [],
      version: this.version
    };
  }
};
m7 = t2e([
  ni("BloomEffectComponent")
], m7);
var s2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const r2e = "Outline";
let g7 = class extends td {
  initialize(s, e, t) {
    const i = new _c(s, e, t);
    i.edgeGlow = 0, i.edgeThickness = 1, this.pass = i;
  }
  setOptions(s) {
  }
  setObjects(s) {
    this.pass.selectedObjects = s;
  }
};
g7 = s2e([
  ni(r2e)
], g7);
var l2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const c2e = "SSAO";
let v7 = class extends td {
  initialize(s, e) {
    this.pass = new lN(s, e), this.pass.enabled = !1;
  }
  setOptions(s) {
    const { intensity: e, shadowOffset: t, blur: i, sharp: n, radius: r, enable: a = !1 } = s;
    this.pass.kernelRadius = 18, this.pass.maxDistance = 0.3, this.pass.minDistance = 1e-3, this.pass.enabled = a, this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      version: this.version,
      inActive: !1,
      data: {
        // intensity: this.pass.intensity,
        // shadowOffset: this.pass.depthOffset,
        // blur: this.pass.blur,
        sharp: this.pass.maxDistance,
        radius: this.pass.kernelRadius,
        enabled: this.pass.enabled
      },
      states: []
      // attribute: [],
    };
  }
};
v7 = l2e([
  ni(c2e)
], v7);
var d2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const f2e = "Taa";
let y7 = class extends td {
  initialize(s) {
    this.pass = new Ene(s, s.rootScene), this.pass.enabled = !1;
  }
  setOptions(s) {
    this.pass.updateConfig(s), this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      version: this.version,
      inActive: !1,
      data: {
        colorScale: this.pass.colorScale,
        mixWeight: this.pass.weight,
        enabled: this.pass.enabled
      },
      states: []
      // attribute: [],
    };
  }
};
y7 = d2e([
  ni(f2e)
], y7);
var g2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const v2e = "ImageQuality";
let x7 = class extends td {
  initialize() {
    this.pass = new Z4(Aee);
  }
  setOptions(s) {
    const { colorFilter: e = "#000", hue: t, exposure: i, enable: n } = s;
    this.pass.material.uniforms.colorFilter.value = new _e(e), this.pass.material.uniforms.hue.value = t, this.pass.material.uniforms.exposure.value = i, this.pass.enabled = n, this.pass.material.needsUpdate = !0, this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      inActive: !1,
      version: this.version,
      data: {
        colorFilter: `#${this.pass.material.uniforms.colorFilter.value.getHexString()}`,
        hue: this.pass.material.uniforms.hue.value,
        exposure: this.pass.material.uniforms.exposure.value,
        enabled: this.pass.enabled
      },
      states: []
      // attribute: [],
    };
  }
};
x7 = g2e([
  ni(v2e)
], x7);
const $t = {
  LEFT: 1,
  RIGHT: 2,
  MIDDLE: 4
}, $e = Object.freeze({
  NONE: 0,
  ROTATE: 1,
  TRUCK: 2,
  OFFSET: 4,
  DOLLY: 8,
  ZOOM: 16,
  TOUCH_ROTATE: 32,
  TOUCH_TRUCK: 64,
  TOUCH_OFFSET: 128,
  TOUCH_DOLLY: 256,
  TOUCH_ZOOM: 512,
  TOUCH_DOLLY_TRUCK: 1024,
  TOUCH_DOLLY_OFFSET: 2048,
  TOUCH_DOLLY_ROTATE: 4096,
  TOUCH_ZOOM_TRUCK: 8192,
  TOUCH_ZOOM_OFFSET: 16384,
  TOUCH_ZOOM_ROTATE: 32768
}), Bd = {
  NONE: 0,
  IN: 1,
  OUT: -1
};
function Nh(s) {
  return s.isPerspectiveCamera;
}
function rc(s) {
  return s.isOrthographicCamera;
}
const Fd = Math.PI * 2, rR = Math.PI / 2;
function GA(s, e) {
  e.set(0, 0), s.forEach((t) => {
    e.x += t.clientX, e.y += t.clientY;
  }), e.x /= s.length, e.y /= s.length;
}
function HA(s, e) {
  return rc(s) ? (console.warn(`${e} is not supported in OrthographicCamera`), !0) : !1;
}
let y2e = class {
  constructor() {
    S(this, "_listeners", {});
  }
  /**
   * Adds the specified event listener.
   * @param type event name
   * @param listener handler function
   * @category Methods
   */
  addEventListener(e, t) {
    const i = this._listeners;
    i[e] === void 0 && (i[e] = []), i[e].indexOf(t) === -1 && i[e].push(t);
  }
  /**
   * Presence of the specified event listener.
   * @param type event name
   * @param listener handler function
   * @category Methods
   */
  hasEventListener(e, t) {
    const i = this._listeners;
    return i[e] !== void 0 && i[e].indexOf(t) !== -1;
  }
  /**
   * Removes the specified event listener
   * @param type event name
   * @param listener handler function
   * @category Methods
   */
  removeEventListener(e, t) {
    const n = this._listeners[e];
    if (n !== void 0) {
      const r = n.indexOf(t);
      r !== -1 && n.splice(r, 1);
    }
  }
  /**
   * Removes all event listeners
   * @param type event name
   * @category Methods
   */
  removeAllEventListeners(e) {
    if (!e) {
      this._listeners = {};
      return;
    }
    Array.isArray(this._listeners[e]) && (this._listeners[e].length = 0);
  }
  /**
   * Fire an event type.
   * @param event DispatcherEvent
   * @category Methods
   */
  dispatchEvent(e) {
    const i = this._listeners[e.type];
    if (i !== void 0) {
      e.target = this;
      const n = i.slice(0);
      for (let r = 0, a = n.length; r < a; r++)
        n[r].call(this, e);
    }
  }
};
class x2e {
  static getNearestPointToCamera(e, t) {
    var a;
    const i = [];
    e.traverse((o) => {
      Bg(o) && i.push(o);
    }), t.updateMatrixWorld();
    const n = new lr();
    return n.setFromCamera(new Se(0, 0), t), (a = n.intersectObjects(i)[0]) == null ? void 0 : a.point;
  }
}
const A2e = "__VERSION", P3 = 1 / 8, GF = typeof window < "u", b2e = GF && /Mac/.test(navigator.platform), M2e = !(GF && "PointerEvent" in window);
let Wt, aR, R3, VA, wr, vi, ji, Ud, Xm, _o, Po, Bh, oR, lR, ha, jm, zd, cR, WA, hR, XA, jA, D3;
class Ip extends y2e {
  /**
   * Creates a `CameraControls` instance.
   *
   * Note:
   * You **must install** three.js before using camera-controls. see [#install](#install)
   * Not doing so will lead to runtime errors (`undefined` references to THREE).
   *
   * e.g.
   * ```
   * CameraControls.install( { THREE } );
   * const cameraControls = new CameraControls( camera, domElement );
   * ```
   *
   * @param camera A `THREE.PerspectiveCamera` or `THREE.OrthographicCamera` to be controlled.
   * @param domElement A `HTMLElement` for the draggable area, usually `renderer.domElement`.
   * @category Constructor
   */
  constructor(t, i) {
    super();
    /**
     * Minimum vertical angle in radians.
     * The angle has to be between `0` and `.maxPolarAngle` inclusive.
     * The default value is `0`.
     *
     * e.g.
     * ```
     * cameraControls.maxPolarAngle = 0;
     * ```
     * @category Properties
     */
    S(this, "minPolarAngle", 0);
    // radians
    /**
     * Maximum vertical angle in radians.
     * The angle has to be between `.maxPolarAngle` and `Math.PI` inclusive.
     * The default value is `Math.PI`.
     *
     * e.g.
     * ```
     * cameraControls.maxPolarAngle = Math.PI;
     * ```
     * @category Properties
     */
    S(this, "maxPolarAngle", Math.PI);
    // radians
    /**
     * Minimum horizontal angle in radians.
     * The angle has to be less than `.maxAzimuthAngle`.
     * The default value is `- Infinity`.
     *
     * e.g.
     * ```
     * cameraControls.minAzimuthAngle = - Infinity;
     * ```
     * @category Properties
     */
    S(this, "minAzimuthAngle", -1 / 0);
    // radians
    /**
     * Maximum horizontal angle in radians.
     * The angle has to be greater than `.minAzimuthAngle`.
     * The default value is `Infinity`.
     *
     * e.g.
     * ```
     * cameraControls.maxAzimuthAngle = Infinity;
     * ```
     * @category Properties
     */
    S(this, "maxAzimuthAngle", 1 / 0);
    // radians
    // How far you can dolly in and out ( PerspectiveCamera only )
    /**
     * Minimum distance for dolly. The value must be higher than `0`. Default is `Number.EPSILON`.
     * PerspectiveCamera only.
     * @category Properties
     */
    S(this, "minDistance", Number.EPSILON);
    S(this, "maxTarget", new Wt.Vector3(1 / 0, 1 / 0, 1 / 0));
    S(this, "minTarget", new Wt.Vector3(-1 / 0, -1 / 0, -1 / 0));
    S(this, "zeroVector", new Wt.Vector3());
    /**
     * Maximum distance for dolly. The value must be higher than `minDistance`. Default is `Infinity`.
     * PerspectiveCamera only.
     * @category Properties
     */
    S(this, "maxDistance", 1 / 0);
    /**
     * `true` to enable Infinity Dolly for wheel and pinch. Use this with `minDistance` and `maxDistance`
     * If the Dolly distance is less (or over) than the `minDistance` (or `maxDistance`), `infinityDolly` will keep the distance and pushes the target position instead.
     * @category Properties
     */
    S(this, "infinityDolly", !1);
    /**
     * Minimum camera zoom.
     * @category Properties
     */
    S(this, "minZoom", 0.01);
    /**
     * Maximum camera zoom.
     * @category Properties
     */
    S(this, "maxZoom", 1 / 0);
    /**
     * Approximate time in seconds to reach the target. A smaller value will reach the target faster.
     * @category Properties
     */
    S(this, "smoothTime", 0.25);
    /**
     * the smoothTime while dragging
     * @category Properties
     */
    S(this, "draggingSmoothTime", 0.125);
    /**
     * Max transition speed in unit-per-seconds
     * @category Properties
     */
    S(this, "maxSpeed", 1 / 0);
    /**
     * Speed of azimuth (horizontal) rotation.
     * @category Properties
     */
    S(this, "azimuthRotateSpeed", 1);
    /**
     * Speed of polar (vertical) rotation.
     * @category Properties
     */
    S(this, "polarRotateSpeed", 1);
    /**
     * Speed of mouse-wheel dollying.
     * @category Properties
     */
    S(this, "dollySpeed", 1);
    /**
     * `true` to invert direction when dollying or zooming via drag
     * @category Properties
     */
    S(this, "dollyDragInverted", !1);
    /**
     * Speed of drag for truck and pedestal.
     * @category Properties
     */
    S(this, "truckSpeed", 2);
    /**
     * `true` to enable Dolly-in to the mouse cursor coords.
     * @category Properties
     */
    S(this, "dollyToCursor", !1);
    /**
     * @category Properties
     */
    S(this, "dragToOffset", !1);
    /**
     * The same as `.screenSpacePanning` in three.js's OrbitControls.
     * @category Properties
     */
    S(this, "verticalDragToForward", !1);
    /**
     * Friction ratio of the boundary.
     * @category Properties
     */
    S(this, "boundaryFriction", 0);
    /**
     * Controls how soon the `rest` event fires as the camera slows.
     * @category Properties
     */
    S(this, "restThreshold", 0.01);
    /**
     * An array of Meshes to collide with camera.
     * Be aware colliderMeshes may decrease performance. The collision test uses 4 raycasters from the camera since the near plane has 4 corners.
     * @category Properties
     */
    S(this, "colliderMeshes", []);
    // button configs
    /**
     * User's mouse input config.
     *
     * | button to assign      | behavior |
     * | --------------------- | -------- |
     * | `mouseButtons.left`   | `CameraControls.ACTION.ROTATE`* \| `CameraControls.ACTION.TRUCK` \| `CameraControls.ACTION.OFFSET` \| `CameraControls.ACTION.DOLLY` \| `CameraControls.ACTION.ZOOM` \| `CameraControls.ACTION.NONE` |
     * | `mouseButtons.right`  | `CameraControls.ACTION.ROTATE` \| `CameraControls.ACTION.TRUCK`* \| `CameraControls.ACTION.OFFSET` \| `CameraControls.ACTION.DOLLY` \| `CameraControls.ACTION.ZOOM` \| `CameraControls.ACTION.NONE` |
     * | `mouseButtons.wheel` ¹ | `CameraControls.ACTION.ROTATE` \| `CameraControls.ACTION.TRUCK` \| `CameraControls.ACTION.OFFSET` \| `CameraControls.ACTION.DOLLY` \| `CameraControls.ACTION.ZOOM` \| `CameraControls.ACTION.NONE` |
     * | `mouseButtons.middle` ² | `CameraControls.ACTION.ROTATE` \| `CameraControls.ACTION.TRUCK` \| `CameraControls.ACTION.OFFSET` \| `CameraControls.ACTION.DOLLY`* \| `CameraControls.ACTION.ZOOM` \| `CameraControls.ACTION.NONE` |
     *
     * 1. Mouse wheel event for scroll "up/down" on mac "up/down/left/right"
     * 2. Mouse click on wheel event "button"
     * - \* is the default.
     * - The default of `mouseButtons.wheel` is:
     *   - `DOLLY` for Perspective camera.
     *   - `ZOOM` for Orthographic camera, and can't set `DOLLY`.
     * @category Properties
     */
    S(this, "mouseButtons");
    /**
     * User's touch input config.
     *
     * | fingers to assign     | behavior |
     * | --------------------- | -------- |
     * | `touches.one` | `CameraControls.ACTION.TOUCH_ROTATE`* \| `CameraControls.ACTION.TOUCH_TRUCK` \| `CameraControls.ACTION.TOUCH_OFFSET` \| `CameraControls.ACTION.DOLLY` | `CameraControls.ACTION.ZOOM` | `CameraControls.ACTION.NONE` |
     * | `touches.two` | `ACTION.TOUCH_DOLLY_TRUCK` \| `ACTION.TOUCH_DOLLY_OFFSET` \| `ACTION.TOUCH_DOLLY_ROTATE` \| `ACTION.TOUCH_ZOOM_TRUCK` \| `ACTION.TOUCH_ZOOM_OFFSET` \| `ACTION.TOUCH_ZOOM_ROTATE` \| `ACTION.TOUCH_DOLLY` \| `ACTION.TOUCH_ZOOM` \| `CameraControls.ACTION.TOUCH_ROTATE` \| `CameraControls.ACTION.TOUCH_TRUCK` \| `CameraControls.ACTION.TOUCH_OFFSET` \| `CameraControls.ACTION.NONE` |
     * | `touches.three` | `ACTION.TOUCH_DOLLY_TRUCK` \| `ACTION.TOUCH_DOLLY_OFFSET` \| `ACTION.TOUCH_DOLLY_ROTATE` \| `ACTION.TOUCH_ZOOM_TRUCK` \| `ACTION.TOUCH_ZOOM_OFFSET` \| `ACTION.TOUCH_ZOOM_ROTATE` \| `CameraControls.ACTION.TOUCH_ROTATE` \| `CameraControls.ACTION.TOUCH_TRUCK` \| `CameraControls.ACTION.TOUCH_OFFSET` \| `CameraControls.ACTION.NONE` |
     *
     * - \* is the default.
     * - The default of `touches.two` and `touches.three` is:
     *   - `TOUCH_DOLLY_TRUCK` for Perspective camera.
     *   - `TOUCH_ZOOM_TRUCK` for Orthographic camera, and can't set `TOUCH_DOLLY_TRUCK` and `TOUCH_DOLLY`.
     * @category Properties
     */
    S(this, "touches");
    /**
     * Force cancel user dragging.
     * @category Methods
     */
    // cancel will be overwritten in the constructor.
    S(this, "cancel", () => {
    });
    /**
     * Still an experimental feature.
     * This could change at any time.
     * @category Methods
     */
    S(this, "lockPointer");
    /**
     * Still an experimental feature.
     * This could change at any time.
     * @category Methods
     */
    S(this, "unlockPointer");
    S(this, "_enabled", !0);
    S(this, "_camera");
    S(this, "_yAxisUpSpace");
    S(this, "_yAxisUpSpaceInverse");
    S(this, "_state", $e.NONE);
    S(this, "_domElement");
    S(this, "_viewport", null);
    // the location of focus, where the object orbits around
    S(this, "_target");
    S(this, "_targetEnd");
    S(this, "_focalOffset");
    S(this, "_focalOffsetEnd");
    // rotation and dolly distance
    S(this, "_spherical");
    S(this, "_sphericalEnd");
    S(this, "_lastDistance");
    S(this, "_zoom");
    S(this, "_zoomEnd");
    S(this, "_lastZoom");
    // reset
    S(this, "_cameraUp0");
    S(this, "_target0");
    S(this, "_position0");
    S(this, "_zoom0");
    S(this, "_focalOffset0");
    S(this, "_dollyControlCoord");
    S(this, "_changedDolly", 0);
    S(this, "_changedZoom", 0);
    // collisionTest uses nearPlane. ( PerspectiveCamera only )
    S(this, "_nearPlaneCorners");
    S(this, "_hasRested", !0);
    S(this, "_boundary");
    S(this, "_boundaryEnclosesCamera", !1);
    S(this, "_needsUpdate", !0);
    S(this, "_updatedLastTime", !1);
    S(this, "_elementRect", new DOMRect());
    S(this, "_isDragging", !1);
    S(this, "_activePointers", []);
    S(this, "_lockedPointer", null);
    S(this, "_interactiveArea", new DOMRect(0, 0, 1, 1));
    // Use draggingSmoothTime over smoothTime while true.
    // set automatically true on user-dragging start.
    // set automatically false on programmable methods call.
    S(this, "_isUserControllingRotate", !1);
    S(this, "_isUserControllingDolly", !1);
    S(this, "_isUserControllingTruck", !1);
    S(this, "_isUserControllingOffset", !1);
    S(this, "_isUserControllingZoom", !1);
    S(this, "_lastDollyDirection", Bd.NONE);
    // velocities for smoothDamp
    S(this, "_thetaVelocity", { value: 0 });
    S(this, "_phiVelocity", { value: 0 });
    S(this, "_radiusVelocity", { value: 0 });
    S(this, "_targetVelocity", new Wt.Vector3());
    S(this, "_focalOffsetVelocity", new Wt.Vector3());
    S(this, "_zoomVelocity", { value: 0 });
    S(this, "_focalLength0", 0);
    S(this, "scene", null);
    S(this, "autoAdjustTargetPoint", !1);
    S(this, "debuggerPoint", null);
    S(this, "_truckInternal", (t, i, n) => {
      let r, a;
      if (Nh(this._camera)) {
        const o = vi.copy(this._camera.position).sub(this._target), l = this._camera.getEffectiveFOV() * km, c = o.length() * Math.tan(l * 0.5);
        r = this.truckSpeed * t * c / this._elementRect.height, a = this.truckSpeed * i * c / this._elementRect.height;
      } else if (rc(this._camera)) {
        const o = this._camera;
        r = t * (o.right - o.left) / o.zoom / this._elementRect.width, a = i * (o.top - o.bottom) / o.zoom / this._elementRect.height;
      } else
        return;
      this.verticalDragToForward ? (n ? this.setFocalOffset(
        this._focalOffsetEnd.x + r,
        this._focalOffsetEnd.y,
        this._focalOffsetEnd.z,
        !0
      ) : this.truck(r, 0, !0), this.forward(-a, !0)) : n ? this.setFocalOffset(
        this._focalOffsetEnd.x + r,
        this._focalOffsetEnd.y + a,
        this._focalOffsetEnd.z,
        !0
      ) : this.truck(r, a, !0);
    });
    S(this, "_rotateInternal", (t, i) => {
      const n = Fd * this.azimuthRotateSpeed * t / this._elementRect.height, r = Fd * this.polarRotateSpeed * i / this._elementRect.height;
      this.rotate(n, r, !0);
    });
    S(this, "_dollyInternal", (t, i, n) => {
      const r = 0.95 ** (-t * this.dollySpeed), a = this._sphericalEnd.radius, o = this._sphericalEnd.radius * r, l = Wt.MathUtils.clamp(o, this.minDistance, this.maxDistance), c = l - o;
      this.infinityDolly && this.dollyToCursor ? this._dollyToNoClamp(o, !0) : this.infinityDolly && !this.dollyToCursor ? (this.dollyInFixed(c, !0), this._dollyToNoClamp(l, !0)) : this._dollyToNoClamp(l, !0), this.dollyToCursor && (this._changedDolly += (this.infinityDolly ? o : l) - a, this._dollyControlCoord.set(i, n)), this._lastDollyDirection = Math.sign(-t);
    });
    S(this, "_zoomInternal", (t, i, n) => {
      const r = 0.95 ** (t * this.dollySpeed), a = this._zoom, o = this._zoom * r;
      this.zoomTo(o, !0), this.dollyToCursor && (this._changedZoom += o - a, this._dollyControlCoord.set(i, n));
    });
    typeof Wt > "u" && console.error(
      "camera-controls: `THREE` is undefined. You must first run `CameraControls.install( { THREE: THREE } )`. Check the docs for further information."
    ), this._camera = t, this._yAxisUpSpace = new Wt.Quaternion().setFromUnitVectors(this._camera.up, R3), this._yAxisUpSpaceInverse = this._yAxisUpSpace.clone().invert(), this._state = $e.NONE, this._target = new Wt.Vector3(), this._targetEnd = this._target.clone(), this._focalOffset = new Wt.Vector3(), this._focalOffsetEnd = this._focalOffset.clone(), this._spherical = new Wt.Spherical().setFromVector3(
      vi.copy(this._camera.position).applyQuaternion(this._yAxisUpSpace)
    ), this._sphericalEnd = this._spherical.clone(), this._lastDistance = this._spherical.radius, this._zoom = this._camera.zoom, this._zoomEnd = this._zoom, this._lastZoom = this._zoom, this._nearPlaneCorners = [
      new Wt.Vector3(),
      new Wt.Vector3(),
      new Wt.Vector3(),
      new Wt.Vector3()
    ], this._updateNearPlaneCorners(), this._boundary = new Wt.Box3(
      new Wt.Vector3(-1 / 0, -1 / 0, -1 / 0),
      new Wt.Vector3(1 / 0, 1 / 0, 1 / 0)
    ), this._cameraUp0 = this._camera.up.clone(), this._target0 = this._target.clone(), this._position0 = this._camera.position.clone(), this._zoom0 = this._zoom, this._focalOffset0 = this._focalOffset.clone(), this._focalLength0 = this._camera.getFocalLength(), this._dollyControlCoord = new Wt.Vector2(), this.mouseButtons = {
      left: $e.ROTATE,
      middle: $e.DOLLY,
      right: $e.TRUCK,
      wheel: Nh(this._camera) ? $e.DOLLY : rc(this._camera) ? $e.ZOOM : $e.NONE
    }, this.touches = {
      one: $e.TOUCH_ROTATE,
      two: Nh(this._camera) ? $e.TOUCH_DOLLY_TRUCK : rc(this._camera) ? $e.TOUCH_ZOOM_TRUCK : $e.NONE,
      three: $e.TOUCH_TRUCK
    };
    const n = new Wt.Vector2(), r = new Wt.Vector2(), a = new Wt.Vector2(), o = (b) => {
      if (!this._enabled || !this._domElement)
        return;
      if (this._interactiveArea.left !== 0 || this._interactiveArea.top !== 0 || this._interactiveArea.width !== 1 || this._interactiveArea.height !== 1) {
        const C = this._domElement.getBoundingClientRect(), T = b.clientX / C.width, E = b.clientY / C.height;
        if (T < this._interactiveArea.left || T > this._interactiveArea.right || E < this._interactiveArea.top || E > this._interactiveArea.bottom)
          return;
      }
      const M = b.pointerType !== "mouse" ? null : (b.buttons & $t.LEFT) === $t.LEFT ? $t.LEFT : (b.buttons & $t.MIDDLE) === $t.MIDDLE ? $t.MIDDLE : (b.buttons & $t.RIGHT) === $t.RIGHT ? $t.RIGHT : null;
      if (M !== null) {
        const C = this._findPointerByMouseButton(M);
        C && this._disposePointer(C);
      }
      if ((b.buttons & $t.LEFT) === $t.LEFT && this._lockedPointer)
        return;
      const w = {
        pointerId: b.pointerId,
        clientX: b.clientX,
        clientY: b.clientY,
        deltaX: 0,
        deltaY: 0,
        mouseButton: M
      };
      this._activePointers.push(w), this._domElement.ownerDocument.removeEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.removeEventListener("pointerup", u), this._domElement.ownerDocument.addEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.addEventListener("pointerup", u), this._isDragging = !0, g(b);
    }, l = (b) => {
      if (!this._enabled || !this._domElement || this._lockedPointer)
        return;
      if (this._interactiveArea.left !== 0 || this._interactiveArea.top !== 0 || this._interactiveArea.width !== 1 || this._interactiveArea.height !== 1) {
        const C = this._domElement.getBoundingClientRect(), T = b.clientX / C.width, E = b.clientY / C.height;
        if (T < this._interactiveArea.left || T > this._interactiveArea.right || E < this._interactiveArea.top || E > this._interactiveArea.bottom)
          return;
      }
      const M = (b.buttons & $t.LEFT) === $t.LEFT ? $t.LEFT : (b.buttons & $t.MIDDLE) === $t.MIDDLE ? $t.MIDDLE : (b.buttons & $t.RIGHT) === $t.RIGHT ? $t.RIGHT : null;
      if (M !== null) {
        const C = this._findPointerByMouseButton(M);
        C && this._disposePointer(C);
      }
      const w = {
        pointerId: 1,
        clientX: b.clientX,
        clientY: b.clientY,
        deltaX: 0,
        deltaY: 0,
        mouseButton: (b.buttons & $t.LEFT) === $t.LEFT ? $t.LEFT : (b.buttons & $t.MIDDLE) === $t.LEFT ? $t.MIDDLE : (b.buttons & $t.RIGHT) === $t.LEFT ? $t.RIGHT : null
      };
      this._activePointers.push(w), this._domElement.ownerDocument.removeEventListener("mousemove", h), this._domElement.ownerDocument.removeEventListener("mouseup", d), this._domElement.ownerDocument.addEventListener("mousemove", h), this._domElement.ownerDocument.addEventListener("mouseup", d), this._isDragging = !0, g(b);
    }, c = (b) => {
      b.cancelable && b.preventDefault();
      const { pointerId: M } = b, w = this._lockedPointer || this._findPointerById(M);
      if (w) {
        if (w.clientX = b.clientX, w.clientY = b.clientY, w.deltaX = b.movementX, w.deltaY = b.movementY, this._state = 0, b.pointerType === "touch")
          switch (this._activePointers.length) {
            case 1:
              this._state = this.touches.one;
              break;
            case 2:
              this._state = this.touches.two;
              break;
            case 3:
              this._state = this.touches.three;
              break;
          }
        else
          (!this._isDragging && this._lockedPointer || this._isDragging && (b.buttons & $t.LEFT) === $t.LEFT) && (this._state |= this.mouseButtons.left), this._isDragging && (b.buttons & $t.MIDDLE) === $t.MIDDLE && (this._state |= this.mouseButtons.middle), this._isDragging && (b.buttons & $t.RIGHT) === $t.RIGHT && (this._state |= this.mouseButtons.right);
        m();
      }
    }, h = (b) => {
      const M = this._lockedPointer || this._findPointerById(1);
      M && (M.clientX = b.clientX, M.clientY = b.clientY, M.deltaX = b.movementX, M.deltaY = b.movementY, this._state = 0, (this._lockedPointer || (b.buttons & $t.LEFT) === $t.LEFT) && (this._state |= this.mouseButtons.left), (b.buttons & $t.MIDDLE) === $t.MIDDLE && (this._state |= this.mouseButtons.middle), (b.buttons & $t.RIGHT) === $t.RIGHT && (this._state |= this.mouseButtons.right), m());
    }, u = (b) => {
      const M = this._findPointerById(b.pointerId);
      if (!(M && M === this._lockedPointer)) {
        if (M && this._disposePointer(M), b.pointerType === "touch")
          switch (this._activePointers.length) {
            case 0:
              this._state = $e.NONE;
              break;
            case 1:
              this._state = this.touches.one;
              break;
            case 2:
              this._state = this.touches.two;
              break;
            case 3:
              this._state = this.touches.three;
              break;
          }
        else
          this._state = $e.NONE;
        x();
      }
    }, d = () => {
      const b = this._findPointerById(1);
      b && b === this._lockedPointer || (b && this._disposePointer(b), this._state = $e.NONE, x());
    };
    let f = -1;
    const p = (b) => {
      if (!this._domElement || !this._enabled || this.mouseButtons.wheel === $e.NONE)
        return;
      if (this._interactiveArea.left !== 0 || this._interactiveArea.top !== 0 || this._interactiveArea.width !== 1 || this._interactiveArea.height !== 1) {
        const E = this._domElement.getBoundingClientRect(), P = b.clientX / E.width, L = b.clientY / E.height;
        if (P < this._interactiveArea.left || P > this._interactiveArea.right || L < this._interactiveArea.top || L > this._interactiveArea.bottom)
          return;
      }
      if (b.preventDefault(), this.dollyToCursor || this.mouseButtons.wheel === $e.ROTATE || this.mouseButtons.wheel === $e.TRUCK) {
        const E = performance.now();
        f - E < 1e3 && this._getClientRect(this._elementRect), f = E;
      }
      const M = b2e ? -1 : -3, w = b.deltaMode === 1 ? b.deltaY / M : b.deltaY / (M * 10), C = this.dollyToCursor ? (b.clientX - this._elementRect.x) / this._elementRect.width * 2 - 1 : 0, T = this.dollyToCursor ? (b.clientY - this._elementRect.y) / this._elementRect.height * -2 + 1 : 0;
      switch (this.mouseButtons.wheel) {
        case $e.ROTATE: {
          this._rotateInternal(b.deltaX, b.deltaY), this._isUserControllingRotate = !0;
          break;
        }
        case $e.TRUCK: {
          this._truckInternal(b.deltaX, b.deltaY, !1), this._isUserControllingTruck = !0;
          break;
        }
        case $e.OFFSET: {
          this._truckInternal(b.deltaX, b.deltaY, !0), this._isUserControllingOffset = !0;
          break;
        }
        case $e.DOLLY: {
          this._dollyInternal(-w, C, T), this._isUserControllingDolly = !0;
          break;
        }
        case $e.ZOOM: {
          this._zoomInternal(-w, C, T), this._isUserControllingZoom = !0;
          break;
        }
      }
      this.resetCameraFocusLength(), this.dispatchEvent({ type: "control" });
    }, v = (b) => {
      if (!(!this._domElement || !this._enabled)) {
        if (this.mouseButtons.right === Ip.ACTION.NONE) {
          const M = b instanceof PointerEvent ? b.pointerId : (0), w = this._findPointerById(M);
          w && this._disposePointer(w), this._domElement.ownerDocument.removeEventListener("pointermove", c, {
            passive: !1
          }), this._domElement.ownerDocument.removeEventListener("pointerup", u), this._domElement.ownerDocument.removeEventListener("mousemove", h), this._domElement.ownerDocument.removeEventListener("mouseup", d);
          return;
        }
        b.preventDefault();
      }
    }, g = (b) => {
      if (!this._enabled)
        return;
      if (this.resetCameraFocusLength(), GA(this._activePointers, wr), this._getClientRect(this._elementRect), n.copy(wr), r.copy(wr), this._activePointers.length >= 2) {
        const w = wr.x - this._activePointers[1].clientX, C = wr.y - this._activePointers[1].clientY, T = Math.sqrt(w * w + C * C);
        a.set(0, T);
        const E = (this._activePointers[0].clientX + this._activePointers[1].clientX) * 0.5, P = (this._activePointers[0].clientY + this._activePointers[1].clientY) * 0.5;
        r.set(E, P);
      }
      if (this._state = 0, !b)
        this._lockedPointer && (this._state |= this.mouseButtons.left);
      else if ("pointerType" in b && b.pointerType === "touch")
        switch (this._activePointers.length) {
          case 1:
            this._state = this.touches.one;
            break;
          case 2:
            this._state = this.touches.two;
            break;
          case 3:
            this._state = this.touches.three;
            break;
        }
      else
        !this._lockedPointer && (b.buttons & $t.LEFT) === $t.LEFT && (this._state |= this.mouseButtons.left), (b.buttons & $t.MIDDLE) === $t.MIDDLE && (this._state |= this.mouseButtons.middle), (b.buttons & $t.RIGHT) === $t.RIGHT && (this._state |= this.mouseButtons.right);
      ((this._state & $e.ROTATE) === $e.ROTATE || (this._state & $e.TOUCH_ROTATE) === $e.TOUCH_ROTATE || (this._state & $e.TOUCH_DOLLY_ROTATE) === $e.TOUCH_DOLLY_ROTATE || (this._state & $e.TOUCH_ZOOM_ROTATE) === $e.TOUCH_ZOOM_ROTATE) && (this._sphericalEnd.theta = this._spherical.theta, this._sphericalEnd.phi = this._spherical.phi, this._thetaVelocity.value = 0, this._phiVelocity.value = 0), ((this._state & $e.TRUCK) === $e.TRUCK || (this._state & $e.TOUCH_TRUCK) === $e.TOUCH_TRUCK || (this._state & $e.TOUCH_DOLLY_TRUCK) === $e.TOUCH_DOLLY_TRUCK || (this._state & $e.TOUCH_ZOOM_TRUCK) === $e.TOUCH_ZOOM_TRUCK) && (this._targetEnd.copy(this._target), this._targetVelocity.set(0, 0, 0)), ((this._state & $e.DOLLY) === $e.DOLLY || (this._state & $e.TOUCH_DOLLY) === $e.TOUCH_DOLLY || (this._state & $e.TOUCH_DOLLY_TRUCK) === $e.TOUCH_DOLLY_TRUCK || (this._state & $e.TOUCH_DOLLY_OFFSET) === $e.TOUCH_DOLLY_OFFSET || (this._state & $e.TOUCH_DOLLY_ROTATE) === $e.TOUCH_DOLLY_ROTATE) && (this._sphericalEnd.radius = this._spherical.radius, this._radiusVelocity.value = 0), ((this._state & $e.ZOOM) === $e.ZOOM || (this._state & $e.TOUCH_ZOOM) === $e.TOUCH_ZOOM || (this._state & $e.TOUCH_ZOOM_TRUCK) === $e.TOUCH_ZOOM_TRUCK || (this._state & $e.TOUCH_ZOOM_OFFSET) === $e.TOUCH_ZOOM_OFFSET || (this._state & $e.TOUCH_ZOOM_ROTATE) === $e.TOUCH_ZOOM_ROTATE) && (this._zoomEnd = this._zoom, this._zoomVelocity.value = 0), ((this._state & $e.OFFSET) === $e.OFFSET || (this._state & $e.TOUCH_OFFSET) === $e.TOUCH_OFFSET || (this._state & $e.TOUCH_DOLLY_OFFSET) === $e.TOUCH_DOLLY_OFFSET || (this._state & $e.TOUCH_ZOOM_OFFSET) === $e.TOUCH_ZOOM_OFFSET) && (this._focalOffsetEnd.copy(this._focalOffset), this._focalOffsetVelocity.set(0, 0, 0)), this.dispatchEvent({ type: "controlstart" });
    }, m = () => {
      if (!this._enabled)
        return;
      this.resetCameraFocusLength(), GA(this._activePointers, wr);
      const M = this._domElement && document.pointerLockElement === this._domElement ? this._lockedPointer || this._activePointers[0] : null, w = M ? -M.deltaX : r.x - wr.x, C = M ? -M.deltaY : r.y - wr.y;
      if (r.copy(wr), ((this._state & $e.ROTATE) === $e.ROTATE || (this._state & $e.TOUCH_ROTATE) === $e.TOUCH_ROTATE || (this._state & $e.TOUCH_DOLLY_ROTATE) === $e.TOUCH_DOLLY_ROTATE || (this._state & $e.TOUCH_ZOOM_ROTATE) === $e.TOUCH_ZOOM_ROTATE) && (this._rotateInternal(w, C), this._isUserControllingRotate = !0), (this._state & $e.DOLLY) === $e.DOLLY || (this._state & $e.ZOOM) === $e.ZOOM) {
        const T = this.dollyToCursor ? (n.x - this._elementRect.x) / this._elementRect.width * 2 - 1 : 0, E = this.dollyToCursor ? (n.y - this._elementRect.y) / this._elementRect.height * -2 + 1 : 0, P = this.dollyDragInverted ? -1 : 1;
        (this._state & $e.DOLLY) === $e.DOLLY ? (this._dollyInternal(P * C * P3, T, E), this._isUserControllingDolly = !0) : (this._zoomInternal(P * C * P3, T, E), this._isUserControllingZoom = !0);
      }
      if ((this._state & $e.TOUCH_DOLLY) === $e.TOUCH_DOLLY || (this._state & $e.TOUCH_ZOOM) === $e.TOUCH_ZOOM || (this._state & $e.TOUCH_DOLLY_TRUCK) === $e.TOUCH_DOLLY_TRUCK || (this._state & $e.TOUCH_ZOOM_TRUCK) === $e.TOUCH_ZOOM_TRUCK || (this._state & $e.TOUCH_DOLLY_OFFSET) === $e.TOUCH_DOLLY_OFFSET || (this._state & $e.TOUCH_ZOOM_OFFSET) === $e.TOUCH_ZOOM_OFFSET || (this._state & $e.TOUCH_DOLLY_ROTATE) === $e.TOUCH_DOLLY_ROTATE || (this._state & $e.TOUCH_ZOOM_ROTATE) === $e.TOUCH_ZOOM_ROTATE) {
        const T = wr.x - this._activePointers[1].clientX, E = wr.y - this._activePointers[1].clientY, P = Math.sqrt(T * T + E * E), L = a.y - P;
        a.set(0, P);
        const I = this.dollyToCursor ? (r.x - this._elementRect.x) / this._elementRect.width * 2 - 1 : 0, N = this.dollyToCursor ? (r.y - this._elementRect.y) / this._elementRect.height * -2 + 1 : 0;
        (this._state & $e.TOUCH_DOLLY) === $e.TOUCH_DOLLY || (this._state & $e.TOUCH_DOLLY_ROTATE) === $e.TOUCH_DOLLY_ROTATE || (this._state & $e.TOUCH_DOLLY_TRUCK) === $e.TOUCH_DOLLY_TRUCK || (this._state & $e.TOUCH_DOLLY_OFFSET) === $e.TOUCH_DOLLY_OFFSET ? (this._dollyInternal(L * P3, I, N), this._isUserControllingDolly = !0) : (this._zoomInternal(L * P3, I, N), this._isUserControllingZoom = !0);
      }
      ((this._state & $e.TRUCK) === $e.TRUCK || (this._state & $e.TOUCH_TRUCK) === $e.TOUCH_TRUCK || (this._state & $e.TOUCH_DOLLY_TRUCK) === $e.TOUCH_DOLLY_TRUCK || (this._state & $e.TOUCH_ZOOM_TRUCK) === $e.TOUCH_ZOOM_TRUCK) && (this._truckInternal(w, C, !1), this._isUserControllingTruck = !0), ((this._state & $e.OFFSET) === $e.OFFSET || (this._state & $e.TOUCH_OFFSET) === $e.TOUCH_OFFSET || (this._state & $e.TOUCH_DOLLY_OFFSET) === $e.TOUCH_DOLLY_OFFSET || (this._state & $e.TOUCH_ZOOM_OFFSET) === $e.TOUCH_ZOOM_OFFSET) && (this._truckInternal(w, C, !0), this._isUserControllingOffset = !0), this.dispatchEvent({ type: "control" });
    }, x = () => {
      if (this.resetCameraFocusLength(), GA(this._activePointers, wr), r.copy(wr), (this._activePointers.length === 0 || this._activePointers.length === 1 && this._activePointers[0] === this._lockedPointer) && (this._isDragging = !1), this.autoAdjustTargetPoint && this.scene) {
        const b = x2e.getNearestPointToCamera(this.scene, this.camera);
        b && (this.moveTo(b.x, b.y, b.z, !1), this.distance = this.camera.position.distanceTo(b));
      }
      this._activePointers.length === 0 && this._domElement && (this._domElement.ownerDocument.removeEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.removeEventListener("mousemove", h), this._domElement.ownerDocument.removeEventListener("pointerup", u), this._domElement.ownerDocument.removeEventListener("mouseup", d), this.dispatchEvent({ type: "controlend" }));
    };
    this.lockPointer = () => {
      !this._enabled || !this._domElement || (this.cancel(), this._lockedPointer = {
        pointerId: -1,
        clientX: 0,
        clientY: 0,
        deltaX: 0,
        deltaY: 0,
        mouseButton: null
      }, this._activePointers.push(this._lockedPointer), this._domElement.ownerDocument.removeEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.removeEventListener("pointerup", u), this._domElement.requestPointerLock(), this._domElement.ownerDocument.addEventListener("pointerlockchange", y), this._domElement.ownerDocument.addEventListener("pointerlockerror", A), this._domElement.ownerDocument.addEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.addEventListener("pointerup", u), g());
    }, this.unlockPointer = () => {
      this._lockedPointer !== null && (this._disposePointer(this._lockedPointer), this._lockedPointer = null), document.exitPointerLock(), this.cancel(), this._domElement && (this._domElement.ownerDocument.removeEventListener("pointerlockchange", y), this._domElement.ownerDocument.removeEventListener("pointerlockerror", A));
    };
    const y = () => {
      this._domElement && this._domElement.ownerDocument.pointerLockElement === this._domElement || this.unlockPointer();
    }, A = () => {
      this.unlockPointer();
    };
    this._addAllEventListeners = (b) => {
      this._domElement = b, this._domElement.style.touchAction = "none", this._domElement.style.userSelect = "none", this._domElement.style.webkitUserSelect = "none", this._domElement.addEventListener("pointerdown", o), M2e && this._domElement.addEventListener("mousedown", l), this._domElement.addEventListener("pointercancel", u), this._domElement.addEventListener("wheel", p, { passive: !1 }), this._domElement.addEventListener("contextmenu", v);
    }, this._removeAllEventListeners = () => {
      this._domElement && (this._domElement.style.touchAction = "", this._domElement.style.userSelect = "", this._domElement.style.webkitUserSelect = "", this._domElement.removeEventListener("pointerdown", o), this._domElement.removeEventListener("mousedown", l), this._domElement.removeEventListener("pointercancel", u), this._domElement.removeEventListener("wheel", p, {
        passive: !1
      }), this._domElement.removeEventListener("contextmenu", v), this._domElement.ownerDocument.removeEventListener("pointermove", c, {
        passive: !1
      }), this._domElement.ownerDocument.removeEventListener("mousemove", h), this._domElement.ownerDocument.removeEventListener("pointerup", u), this._domElement.ownerDocument.removeEventListener("mouseup", d), this._domElement.ownerDocument.removeEventListener("pointerlockchange", y), this._domElement.ownerDocument.removeEventListener("pointerlockerror", A));
    }, this.cancel = () => {
      this._state !== $e.NONE && (this._state = $e.NONE, this._activePointers.length = 0, x());
    }, i && this.connect(i), this.update(0);
  }
  /**
  	 * Injects THREE as the dependency. You can then proceed to use CameraControls.
  	 *
  	 * e.g
  	 * ```javascript
  	 * CameraControls.install( { THREE: THREE } );
  	 * ```
  	 *
  	 * Note: If you do not wish to use enter three.js to reduce file size(tree-shaking for example), make a subset to install.
  	 *
  	 * ```js
  	 * import {
  	 * 	Vector2,
  	 * 	Vector3,
  	 * 	Vector4,
  	 * 	Quaternion,
  	 * 	Matrix4,
  	 * 	Spherical,
  	 * 	Box3,
  	 * 	Sphere,
  	 * 	Raycaster,
  	 * 	MathUtils,
  	 * } from 'three';
  	 *
  	 * const subsetOfTHREE = {
  	 * 	Vector2   : Vector2,
  	 * 	Vector3   : Vector3,
  	 * 	Vector4   : Vector4,
  	 * 	Quaternion: Quaternion,
  	 * 	Matrix4   : Matrix4,
  	 * 	Spherical : Spherical,
  	 * 	Box3      : Box3,
  	 * 	Sphere    : Sphere,
  	 * 	Raycaster : Raycaster,
  	 * };
  
  	 * CameraControls.install( { THREE: subsetOfTHREE } );
  	 * ```
  	 * @category Statics
  	 */
  static install(t) {
    Wt = t.THREE, aR = Object.freeze(new Wt.Vector3(0, 0, 0)), R3 = Object.freeze(new Wt.Vector3(0, 1, 0)), VA = Object.freeze(new Wt.Vector3(0, 0, 1)), wr = new Wt.Vector2(), vi = new Wt.Vector3(), ji = new Wt.Vector3(), Ud = new Wt.Vector3(), Xm = new Wt.Vector3(), _o = new Wt.Vector3(), Po = new Wt.Vector3(), Bh = new Wt.Vector3(), oR = new Wt.Vector3(), lR = new Wt.Vector3(), ha = new Wt.Spherical(), jm = new Wt.Spherical(), zd = new Wt.Box3(), cR = new Wt.Box3(), WA = new Wt.Sphere(), hR = new Wt.Quaternion(), XA = new Wt.Quaternion(), jA = new Wt.Matrix4(), D3 = new Wt.Raycaster();
  }
  /**
   * list all ACTIONs
   * @category Statics
   */
  static get ACTION() {
    return $e;
  }
  get state() {
    return this._state;
  }
  /**
   * The camera to be controlled
   * @category Properties
   */
  get camera() {
    return this._camera;
  }
  set camera(t) {
    this._camera = t, this.updateCameraUp(), this._camera.updateProjectionMatrix(), this._updateNearPlaneCorners(), this._needsUpdate = !0;
  }
  /**
   * Whether or not the controls are enabled.
   * `false` to disable user dragging/touch-move, but all methods works.
   * @category Properties
   */
  get enabled() {
    return this._enabled;
  }
  set enabled(t) {
    this._enabled = t, this._domElement && (t ? (this._domElement.style.touchAction = "none", this._domElement.style.userSelect = "none", this._domElement.style.webkitUserSelect = "none") : (this.cancel(), this._domElement.style.touchAction = "", this._domElement.style.userSelect = "", this._domElement.style.webkitUserSelect = ""));
  }
  /**
   * Returns `true` if the controls are active updating.
   * readonly value.
   * @category Properties
   */
  get active() {
    return !this._hasRested;
  }
  /**
   * Getter for the current `ACTION`.
   * readonly value.
   * @category Properties
   */
  get currentAction() {
    return this._state;
  }
  /**
   * get/set Current distance.
   * @category Properties
   */
  get distance() {
    return this._spherical.radius;
  }
  set distance(t) {
    this._spherical.radius === t && this._sphericalEnd.radius === t || (this._spherical.radius = t, this._sphericalEnd.radius = t, this._needsUpdate = !0);
  }
  // horizontal angle
  /**
   * get/set the azimuth angle (horizontal) in radians.
   * Every 360 degrees turn is added to `.azimuthAngle` value, which is accumulative.
   * @category Properties
   */
  get azimuthAngle() {
    return this._spherical.theta;
  }
  set azimuthAngle(t) {
    this._spherical.theta === t && this._sphericalEnd.theta === t || (this._spherical.theta = t, this._sphericalEnd.theta = t, this._needsUpdate = !0);
  }
  // vertical angle
  /**
   * get/set the polar angle (vertical) in radians.
   * @category Properties
   */
  get polarAngle() {
    return this._spherical.phi;
  }
  set polarAngle(t) {
    this._spherical.phi === t && this._sphericalEnd.phi === t || (this._spherical.phi = t, this._sphericalEnd.phi = t, this._needsUpdate = !0);
  }
  /**
   * Whether camera position should be enclosed in the boundary or not.
   * @category Properties
   */
  get boundaryEnclosesCamera() {
    return this._boundaryEnclosesCamera;
  }
  set boundaryEnclosesCamera(t) {
    this._boundaryEnclosesCamera = t, this._needsUpdate = !0;
  }
  /**
   * Set drag-start, touches and wheel enable area in the domElement.
   * each values are between `0` and `1` inclusive, where `0` is left/top and `1` is right/bottom of the screen.
   * e.g. `{ x: 0, y: 0, width: 1, height: 1 }` for entire area.
   * @category Properties
   */
  set interactiveArea(t) {
    this._interactiveArea.width = Xa(t.width, 0, 1), this._interactiveArea.height = Xa(t.height, 0, 1), this._interactiveArea.x = Xa(t.x, 0, 1 - this._interactiveArea.width), this._interactiveArea.y = Xa(t.x, 0, 1 - this._interactiveArea.height);
  }
  /**
   * Adds the specified event listener.
   * Applicable event types (which is `K`) are:
   * | Event name          | Timing |
   * | ------------------- | ------ |
   * | `'controlstart'`    | When the user starts to control the camera via mouse / touches. ¹ |
   * | `'control'`         | When the user controls the camera (dragging). |
   * | `'controlend'`      | When the user ends to control the camera. ¹ |
   * | `'transitionstart'` | When any kind of transition starts, either user control or using a method with `enableTransition = true` |
   * | `'update'`          | When the camera position is updated. |
   * | `'wake'`            | When the camera starts moving. |
   * | `'rest'`            | When the camera movement is below `.restThreshold` ². |
   * | `'sleep'`           | When the camera end moving. |
   *
   * 1. `mouseButtons.wheel` (Mouse wheel control) does not emit `'controlstart'` and `'controlend'`. `mouseButtons.wheel` uses scroll-event internally, and scroll-event happens intermittently. That means "start" and "end" cannot be detected.
   * 2. Due to damping, `sleep` will usually fire a few seconds after the camera _appears_ to have stopped moving. If you want to do something (e.g. enable UI, perform another transition) at the point when the camera has stopped, you probably want the `rest` event. This can be fine tuned using the `.restThreshold` parameter. See the [Rest and Sleep Example](https://yomotsu.github.io/camera-controls/examples/rest-and-sleep.html).
   *
   * e.g.
   * ```
   * cameraControl.addEventListener( 'controlstart', myCallbackFunction );
   * ```
   * @param type event name
   * @param listener handler function
   * @category Methods
   */
  addEventListener(t, i) {
    super.addEventListener(t, i);
  }
  /**
   * Removes the specified event listener
   * e.g.
   * ```
   * cameraControl.addEventListener( 'controlstart', myCallbackFunction );
   * ```
   * @param type event name
   * @param listener handler function
   * @category Methods
   */
  removeEventListener(t, i) {
    super.removeEventListener(t, i);
  }
  /**
   * Rotate azimuthal angle(horizontal) and polar angle(vertical).
   * Every value is added to the current value.
   * @param azimuthAngle Azimuth rotate angle. In radian.
   * @param polarAngle Polar rotate angle. In radian.
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  rotate(t, i, n = !1) {
    return this.rotateTo(
      this._sphericalEnd.theta + t,
      this._sphericalEnd.phi + i,
      n
    );
  }
  /**
   * Rotate azimuthal angle(horizontal) to the given angle and keep the same polar angle(vertical) target.
   *
   * e.g.
   * ```
   * cameraControls.rotateAzimuthTo( 30 * THREE.MathUtils.DEG2RAD, true );
   * ```
   * @param azimuthAngle Azimuth rotate angle. In radian.
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  rotateAzimuthTo(t, i = !1) {
    return this.rotateTo(t, this._sphericalEnd.phi, i);
  }
  /**
   * Rotate polar angle(vertical) to the given angle and keep the same azimuthal angle(horizontal) target.
   *
   * e.g.
   * ```
   * cameraControls.rotatePolarTo( 30 * THREE.MathUtils.DEG2RAD, true );
   * ```
   * @param polarAngle Polar rotate angle. In radian.
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  rotatePolarTo(t, i = !1) {
    return this.rotateTo(this._sphericalEnd.theta, t, i);
  }
  /**
   * Rotate azimuthal angle(horizontal) and polar angle(vertical) to the given angle.
   * Camera view will rotate over the orbit pivot absolutely:
   *
   * azimuthAngle
   * ```
   *       0º
   *         \
   * 90º -----+----- -90º
   *           \
   *           180º
   * ```
   * | direction | angle                  |
   * | --------- | ---------------------- |
   * | front     | 0º                     |
   * | left      | 90º (`Math.PI / 2`)    |
   * | right     | -90º (`- Math.PI / 2`) |
   * | back      | 180º (`Math.PI`)       |
   *
   * polarAngle
   * ```
   *     180º
   *      |
   *      90º
   *      |
   *      0º
   * ```
   * | direction            | angle                  |
   * | -------------------- | ---------------------- |
   * | top/sky              | 180º (`Math.PI`)       |
   * | horizontal from view | 90º (`Math.PI / 2`)    |
   * | bottom/floor         | 0º                     |
   *
   * @param azimuthAngle Azimuth rotate angle to. In radian.
   * @param polarAngle Polar rotate angle to. In radian.
   * @param enableTransition  Whether to move smoothly or immediately
   * @category Methods
   */
  rotateTo(t, i, n = !1) {
    this._isUserControllingRotate = !1;
    const r = Xa(t, this.minAzimuthAngle, this.maxAzimuthAngle), a = Xa(i, this.minPolarAngle, this.maxPolarAngle);
    this._sphericalEnd.theta = r, this._sphericalEnd.phi = a, this._sphericalEnd.makeSafe(), this._needsUpdate = !0, n || (this._spherical.theta = this._sphericalEnd.theta, this._spherical.phi = this._sphericalEnd.phi);
    const o = !n || Qi(this._spherical.theta, this._sphericalEnd.theta, this.restThreshold) && Qi(this._spherical.phi, this._sphericalEnd.phi, this.restThreshold);
    return this._createOnRestPromise(o);
  }
  /**
   * Dolly in/out camera position.
   * @param distance Distance of dollyIn. Negative number for dollyOut.
   * @param enableTransition Whether to move smoothly or immediately.
   * @category Methods
   */
  dolly(t, i = !1) {
    return this.dollyTo(this._sphericalEnd.radius - t, i);
  }
  /**
   * Dolly in/out camera position to given distance.
   * @param distance Distance of dolly.
   * @param enableTransition Whether to move smoothly or immediately.
   * @category Methods
   */
  dollyTo(t, i = !1) {
    return this._isUserControllingDolly = !1, this._lastDollyDirection = Bd.NONE, this._changedDolly = 0, this._dollyToNoClamp(
      Xa(t, this.minDistance, this.maxDistance),
      i
    );
  }
  _dollyToNoClamp(t, i = !1) {
    const n = this._sphericalEnd.radius;
    if (this.colliderMeshes.length >= 1) {
      const o = this._collisionTest(), l = Qi(o, this._spherical.radius);
      if (!(n > t) && l)
        return Promise.resolve();
      this._sphericalEnd.radius = Math.min(t, o);
    } else
      this._sphericalEnd.radius = t;
    this._needsUpdate = !0, i || (this._spherical.radius = this._sphericalEnd.radius);
    const a = !i || Qi(this._spherical.radius, this._sphericalEnd.radius, this.restThreshold);
    return this._createOnRestPromise(a);
  }
  /**
   * Dolly in, but does not change the distance between the target and the camera, and moves the target position instead.
   * Specify a negative value for dolly out.
   * @param distance Distance of dolly.
   * @param enableTransition Whether to move smoothly or immediately.
   * @category Methods
   */
  dollyInFixed(t, i = !1) {
    this._targetEnd.add(this._getCameraDirection(Xm).multiplyScalar(t)), i || this._target.copy(this._targetEnd);
    const n = !i || Qi(this._target.x, this._targetEnd.x, this.restThreshold) && Qi(this._target.y, this._targetEnd.y, this.restThreshold) && Qi(this._target.z, this._targetEnd.z, this.restThreshold);
    return this._createOnRestPromise(n);
  }
  /**
   * Zoom in/out camera. The value is added to camera zoom.
   * Limits set with `.minZoom` and `.maxZoom`
   * @param zoomStep zoom scale
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  zoom(t, i = !1) {
    return this.zoomTo(this._zoomEnd + t, i);
  }
  /**
   * Zoom in/out camera to given scale. The value overwrites camera zoom.
   * Limits set with .minZoom and .maxZoom
   * @param zoom
   * @param enableTransition
   * @category Methods
   */
  zoomTo(t, i = !1) {
    this._isUserControllingZoom = !1, this._zoomEnd = Xa(t, this.minZoom, this.maxZoom), this._needsUpdate = !0, i || (this._zoom = this._zoomEnd);
    const n = !i || Qi(this._zoom, this._zoomEnd, this.restThreshold);
    return this._changedZoom = 0, this._createOnRestPromise(n);
  }
  /**
   * @deprecated `pan()` has been renamed to `truck()`
   * @category Methods
   */
  pan(t, i, n = !1) {
    return console.warn("`pan` has been renamed to `truck`"), this.truck(t, i, n);
  }
  /**
   * Truck and pedestal camera using current azimuthal angle
   * @param x Horizontal translate amount
   * @param y Vertical translate amount
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  truck(t, i, n = !1) {
    this._camera.updateMatrix(), _o.setFromMatrixColumn(this._camera.matrix, 0), Po.setFromMatrixColumn(this._camera.matrix, 1), _o.multiplyScalar(t), Po.multiplyScalar(-i);
    const r = vi.copy(_o).add(Po), a = ji.copy(this._targetEnd).add(r);
    return this.moveTo(a.x, a.y, a.z, n);
  }
  /**
   * Move forward / backward.
   * @param distance Amount to move forward / backward. Negative value to move backward
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  forward(t, i = !1) {
    vi.setFromMatrixColumn(this._camera.matrix, 0), vi.crossVectors(this._camera.up, vi), vi.multiplyScalar(t);
    const n = ji.copy(this._targetEnd).add(vi);
    return this.moveTo(n.x, n.y, n.z, i);
  }
  /**
   * Move up / down.
   * @param height Amount to move up / down. Negative value to move down
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  elevate(t, i = !1) {
    return vi.copy(this._camera.up).multiplyScalar(t), this.moveTo(
      this._targetEnd.x + vi.x,
      this._targetEnd.y + vi.y,
      this._targetEnd.z + vi.z,
      i
    );
  }
  /**
   * Move target position to given point.
   * @param x x coord to move center position
   * @param y y coord to move center position
   * @param z z coord to move center position
   * @param enableTransition Whether to move smoothly or immediately
   * @category Methods
   */
  moveTo(t, i, n, r = !1) {
    this._isUserControllingTruck = !1;
    const a = vi.set(t, i, n).sub(this._targetEnd);
    this._encloseToBoundary(this._targetEnd, a, this.boundaryFriction), this._needsUpdate = !0, r || this._target.copy(this._targetEnd);
    const o = !r || Qi(this._target.x, this._targetEnd.x, this.restThreshold) && Qi(this._target.y, this._targetEnd.y, this.restThreshold) && Qi(this._target.z, this._targetEnd.z, this.restThreshold);
    return this._createOnRestPromise(o);
  }
  /**
   * Look in the given point direction.
   * @param x point x.
   * @param y point y.
   * @param z point z.
   * @param enableTransition Whether to move smoothly or immediately.
   * @returns Transition end promise
   * @category Methods
   */
  lookInDirectionOf(t, i, n, r = !1) {
    const l = vi.set(t, i, n).sub(this._targetEnd).normalize().multiplyScalar(-this._sphericalEnd.radius);
    return this.setPosition(l.x, l.y, l.z, r);
  }
  /**
   * Fit the viewport to the box or the bounding box of the object, using the nearest axis. paddings are in unit.
   * set `cover: true` to fill enter screen.
   * e.g.
   * ```
   * cameraControls.fitToBox( myMesh );
   * ```
   * @param box3OrObject Axis aligned bounding box to fit the view.
   * @param enableTransition Whether to move smoothly or immediately.
   * @param options | `<object>` { cover: boolean, paddingTop: number, paddingLeft: number, paddingBottom: number, paddingRight: number }
   * @returns Transition end promise
   * @category Methods
   */
  fitToBox(t, i, {
    cover: n = !1,
    paddingLeft: r = 0,
    paddingRight: a = 0,
    paddingBottom: o = 0,
    paddingTop: l = 0
  } = {}) {
    const c = [], h = t.isBox3 ? zd.copy(t) : zd.setFromObject(t);
    h.isEmpty() && (console.warn("camera-controls: fitTo() cannot be used with an empty box. Aborting"), Promise.resolve());
    const u = VP(this._sphericalEnd.theta, rR), d = VP(this._sphericalEnd.phi, rR);
    c.push(this.rotateTo(u, d, i));
    const f = vi.setFromSpherical(this._sphericalEnd).normalize(), p = hR.setFromUnitVectors(f, VA), v = Qi(Math.abs(f.y), 1);
    v && p.multiply(XA.setFromAxisAngle(R3, u)), p.multiply(this._yAxisUpSpaceInverse);
    const g = cR.makeEmpty();
    ji.copy(h.min).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.min).setX(h.max.x).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.min).setY(h.max.y).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.max).setZ(h.min.z).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.min).setZ(h.max.z).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.max).setY(h.min.y).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.max).setX(h.min.x).applyQuaternion(p), g.expandByPoint(ji), ji.copy(h.max).applyQuaternion(p), g.expandByPoint(ji), g.min.x -= r, g.min.y -= o, g.max.x += a, g.max.y += l, p.setFromUnitVectors(VA, f), v && p.premultiply(XA.invert()), p.premultiply(this._yAxisUpSpace);
    const m = g.getSize(vi), x = g.getCenter(ji).applyQuaternion(p);
    if (Nh(this._camera)) {
      const y = this.getDistanceToFitBox(m.x, m.y, m.z, n);
      c.push(this.moveTo(x.x, x.y, x.z, i)), c.push(this.dollyTo(y, i)), c.push(this.setFocalOffset(0, 0, 0, i));
    } else if (rc(this._camera)) {
      const y = this._camera, A = y.right - y.left, b = y.top - y.bottom, M = n ? Math.max(A / m.x, b / m.y) : Math.min(A / m.x, b / m.y);
      c.push(this.moveTo(x.x, x.y, x.z, i)), c.push(this.zoomTo(M, i)), c.push(this.setFocalOffset(0, 0, 0, i));
    }
    return Promise.all(c);
  }
  /**
   * Fit the viewport to the sphere or the bounding sphere of the object.
   * @param sphereOrMesh
   * @param enableTransition
   * @category Methods
   */
  fitToSphere(t, i) {
    const n = [], a = t instanceof Wt.Sphere ? WA.copy(t) : Ip.createBoundingSphere(t, WA);
    if (n.push(
      this.moveTo(
        a.center.x,
        a.center.y,
        a.center.z,
        i
      )
    ), Nh(this._camera)) {
      const o = this.getDistanceToFitSphere(a.radius);
      n.push(this.dollyTo(o, i));
    } else if (rc(this._camera)) {
      const o = this._camera.right - this._camera.left, l = this._camera.top - this._camera.bottom, c = 2 * a.radius, h = Math.min(o / c, l / c);
      n.push(this.zoomTo(h, i));
    }
    return n.push(this.setFocalOffset(0, 0, 0, i)), Promise.all(n);
  }
  /**
   * Look at the `target` from the `position`.
   * @param positionX
   * @param positionY
   * @param positionZ
   * @param targetX
   * @param targetY
   * @param targetZ
   * @param enableTransition
   * @category Methods
   */
  setLookAt(t, i, n, r, a, o, l = !1) {
    this._isUserControllingRotate = !1, this._isUserControllingDolly = !1, this._isUserControllingTruck = !1, this._lastDollyDirection = Bd.NONE, this._changedDolly = 0;
    const c = ji.set(r, a, o), h = vi.set(t, i, n);
    this._targetEnd.copy(c), this._sphericalEnd.setFromVector3(h.sub(c).applyQuaternion(this._yAxisUpSpace)), this.normalizeRotations(), this._needsUpdate = !0, l || (this._target.copy(this._targetEnd), this._spherical.copy(this._sphericalEnd));
    const u = !l || Qi(this._target.x, this._targetEnd.x, this.restThreshold) && Qi(this._target.y, this._targetEnd.y, this.restThreshold) && Qi(this._target.z, this._targetEnd.z, this.restThreshold) && Qi(this._spherical.theta, this._sphericalEnd.theta, this.restThreshold) && Qi(this._spherical.phi, this._sphericalEnd.phi, this.restThreshold) && Qi(this._spherical.radius, this._sphericalEnd.radius, this.restThreshold);
    return this._createOnRestPromise(u);
  }
  /**
   * Similar to setLookAt, but it interpolates between two states.
   * @param positionAX
   * @param positionAY
   * @param positionAZ
   * @param targetAX
   * @param targetAY
   * @param targetAZ
   * @param positionBX
   * @param positionBY
   * @param positionBZ
   * @param targetBX
   * @param targetBY
   * @param targetBZ
   * @param t
   * @param enableTransition
   * @category Methods
   */
  lerpLookAt(t, i, n, r, a, o, l, c, h, u, d, f, p, v = !1) {
    this._isUserControllingRotate = !1, this._isUserControllingDolly = !1, this._isUserControllingTruck = !1, this._lastDollyDirection = Bd.NONE, this._changedDolly = 0;
    const g = vi.set(r, a, o), m = ji.set(t, i, n);
    ha.setFromVector3(m.sub(g).applyQuaternion(this._yAxisUpSpace));
    const x = Ud.set(u, d, f), y = ji.set(l, c, h);
    jm.setFromVector3(y.sub(x).applyQuaternion(this._yAxisUpSpace)), this._targetEnd.copy(g.lerp(x, p));
    const A = jm.theta - ha.theta, b = jm.phi - ha.phi, M = jm.radius - ha.radius;
    this._sphericalEnd.set(
      ha.radius + M * p,
      ha.phi + b * p,
      ha.theta + A * p
    ), this.normalizeRotations(), this._needsUpdate = !0, v || (this._target.copy(this._targetEnd), this._spherical.copy(this._sphericalEnd));
    const w = !v || Qi(this._target.x, this._targetEnd.x, this.restThreshold) && Qi(this._target.y, this._targetEnd.y, this.restThreshold) && Qi(this._target.z, this._targetEnd.z, this.restThreshold) && Qi(this._spherical.theta, this._sphericalEnd.theta, this.restThreshold) && Qi(this._spherical.phi, this._sphericalEnd.phi, this.restThreshold) && Qi(this._spherical.radius, this._sphericalEnd.radius, this.restThreshold);
    return this._createOnRestPromise(w);
  }
  /**
   * Set angle and distance by given position.
   * An alias of `setLookAt()`, without target change. Thus keep gazing at the current target
   * @param positionX
   * @param positionY
   * @param positionZ
   * @param enableTransition
   * @category Methods
   */
  setPosition(t, i, n, r = !1) {
    return this.setLookAt(
      t,
      i,
      n,
      this._targetEnd.x,
      this._targetEnd.y,
      this._targetEnd.z,
      r
    );
  }
  /**
   * Set the target position where gaze at.
   * An alias of `setLookAt()`, without position change. Thus keep the same position.
   * @param targetX
   * @param targetY
   * @param targetZ
   * @param enableTransition
   * @category Methods
   */
  setTarget(t, i, n, r = !1) {
    const a = this.getPosition(vi), o = this.setLookAt(
      a.x,
      a.y,
      a.z,
      t,
      i,
      n,
      r
    );
    return this._sphericalEnd.phi = Xa(this._sphericalEnd.phi, this.minPolarAngle, this.maxPolarAngle), o;
  }
  /**
   * Set focal offset using the screen parallel coordinates. z doesn't affect in Orthographic as with Dolly.
   * @param x
   * @param y
   * @param z
   * @param enableTransition
   * @category Methods
   */
  setFocalOffset(t, i, n, r = !1) {
    this._isUserControllingOffset = !1, this._focalOffsetEnd.set(t, i, n), this._needsUpdate = !0, r || this._focalOffset.copy(this._focalOffsetEnd);
    const a = !r || Qi(this._focalOffset.x, this._focalOffsetEnd.x, this.restThreshold) && Qi(this._focalOffset.y, this._focalOffsetEnd.y, this.restThreshold) && Qi(this._focalOffset.z, this._focalOffsetEnd.z, this.restThreshold);
    return this._createOnRestPromise(a);
  }
  /**
   * Set orbit point without moving the camera.
   * SHOULD NOT RUN DURING ANIMATIONS. `setOrbitPoint()` will immediately fix the positions.
   * @param targetX
   * @param targetY
   * @param targetZ
   * @category Methods
   */
  setOrbitPoint(t, i, n) {
    this._camera.updateMatrixWorld(), _o.setFromMatrixColumn(this._camera.matrixWorldInverse, 0), Po.setFromMatrixColumn(this._camera.matrixWorldInverse, 1), Bh.setFromMatrixColumn(this._camera.matrixWorldInverse, 2);
    const r = vi.set(t, i, n), a = r.distanceTo(this._camera.position), o = r.sub(this._camera.position);
    _o.multiplyScalar(o.x), Po.multiplyScalar(o.y), Bh.multiplyScalar(o.z), vi.copy(_o).add(Po).add(Bh), vi.z += a, this.dollyTo(a, !1), this.setFocalOffset(-vi.x, vi.y, -vi.z, !1), this.moveTo(t, i, n, !1);
  }
  /**
   * Set the boundary box that encloses the target of the camera. box3 is in THREE.Box3
   * @param box3
   * @category Methods
   */
  setBoundary(t) {
    if (!t) {
      this._boundary.min.set(-1 / 0, -1 / 0, -1 / 0), this._boundary.max.set(1 / 0, 1 / 0, 1 / 0), this._needsUpdate = !0;
      return;
    }
    this._boundary.copy(t), this._boundary.clampPoint(this._targetEnd, this._targetEnd), this._needsUpdate = !0;
  }
  /**
   * Set (or unset) the current viewport.
   * Set this when you want to use renderer viewport and .dollyToCursor feature at the same time.
   * @param viewportOrX
   * @param y
   * @param width
   * @param height
   * @category Methods
   */
  setViewport(t, i, n, r) {
    if (t === null) {
      this._viewport = null;
      return;
    }
    this._viewport = this._viewport || new Wt.Vector4(), typeof t == "number" ? this._viewport.set(t, i, n, r) : this._viewport.copy(t);
  }
  /**
   * Calculate the distance to fit the box.
   * @param width box width
   * @param height box height
   * @param depth box depth
   * @returns distance
   * @category Methods
   */
  getDistanceToFitBox(t, i, n, r = !1) {
    if (HA(this._camera, "getDistanceToFitBox"))
      return this._spherical.radius;
    const a = t / i, o = this._camera.getEffectiveFOV() * km, { aspect: l } = this._camera;
    return ((r ? a > l : a < l) ? i : t / l) * 0.5 / Math.tan(o * 0.5) + n * 0.5;
  }
  /**
   * Calculate the distance to fit the sphere.
   * @param radius sphere radius
   * @returns distance
   * @category Methods
   */
  getDistanceToFitSphere(t) {
    if (HA(this._camera, "getDistanceToFitSphere"))
      return this._spherical.radius;
    const i = this._camera.getEffectiveFOV() * km, n = Math.atan(Math.tan(i * 0.5) * this._camera.aspect) * 2, r = this._camera.aspect > 1 ? i : n;
    return t / Math.sin(r * 0.5);
  }
  /**
   * Returns the orbit center position, where the camera looking at.
   * @param out The receiving Vector3 instance to copy the result
   * @param receiveEndValue Whether receive the transition end coords or current. default is `true`
   * @category Methods
   */
  getTarget(t, i = !0) {
    return (t && t.isVector3 ? t : new Wt.Vector3()).copy(i ? this._targetEnd : this._target);
  }
  /**
   * Returns the camera position.
   * @param out The receiving Vector3 instance to copy the result
   * @param receiveEndValue Whether receive the transition end coords or current. default is `true`
   * @category Methods
   */
  getPosition(t, i = !0) {
    return (t && t.isVector3 ? t : new Wt.Vector3()).setFromSpherical(i ? this._sphericalEnd : this._spherical).applyQuaternion(this._yAxisUpSpaceInverse).add(i ? this._targetEnd : this._target);
  }
  /**
   * Returns the spherical coordinates of the orbit.
   * @param out The receiving Spherical instance to copy the result
   * @param receiveEndValue Whether receive the transition end coords or current. default is `true`
   * @category Methods
   */
  getSpherical(t, i = !0) {
    return (t && t instanceof Wt.Spherical ? t : new Wt.Spherical()).copy(i ? this._sphericalEnd : this._spherical);
  }
  /**
   * Returns the focal offset, which is how much the camera appears to be translated in screen parallel coordinates.
   * @param out The receiving Vector3 instance to copy the result
   * @param receiveEndValue Whether receive the transition end coords or current. default is `true`
   * @category Methods
   */
  getFocalOffset(t, i = !0) {
    return (t && t.isVector3 ? t : new Wt.Vector3()).copy(i ? this._focalOffsetEnd : this._focalOffset);
  }
  /**
   * Normalize camera azimuth angle rotation between 0 and 360 degrees.
   * @category Methods
   */
  normalizeRotations() {
    this._sphericalEnd.theta = this._sphericalEnd.theta % Fd, this._sphericalEnd.theta < 0 && (this._sphericalEnd.theta += Fd), this._spherical.theta += Fd * Math.round((this._sphericalEnd.theta - this._spherical.theta) / Fd);
  }
  /**
   * Reset all rotation and position to defaults.
   * @param enableTransition
   * @category Methods
   */
  reset(t = !1) {
    if (!Qi(this._camera.up.x, this._cameraUp0.x) || !Qi(this._camera.up.y, this._cameraUp0.y) || !Qi(this._camera.up.z, this._cameraUp0.z)) {
      this._camera.up.copy(this._cameraUp0);
      const n = this.getPosition(vi);
      this.updateCameraUp(), this.setPosition(n.x, n.y, n.z);
    }
    const i = [
      this.setLookAt(
        this._position0.x,
        this._position0.y,
        this._position0.z,
        this._target0.x,
        this._target0.y,
        this._target0.z,
        t
      ),
      this.setFocalOffset(
        this._focalOffset0.x,
        this._focalOffset0.y,
        this._focalOffset0.z,
        t
      ),
      this.zoomTo(this._zoom0, t)
    ];
    return Promise.all(i);
  }
  /**
   * Set current camera position as the default position.
   * @category Methods
   */
  saveState() {
    this._cameraUp0.copy(this._camera.up), this.getTarget(this._target0), this.getPosition(this._position0), this._zoom0 = this._zoom, this._focalOffset0.copy(this._focalOffset);
  }
  setCameraFocusLength(t) {
    this._camera.type === "PerspectiveCamera" && this._camera.setFocalLength(t);
  }
  getCameraFocusLength() {
    return this._camera.type === "PerspectiveCamera" ? this._camera.getFocalLength() : 0;
  }
  resetCameraFocusLength() {
  }
  saveCameraFocalLength() {
    this._camera.type === "PerspectiveCamera" && (this._focalLength0 = this._camera.getFocalLength());
  }
  /**
   * Sync camera-up direction.
   * When camera-up vector is changed, `.updateCameraUp()` must be called.
   * @category Methods
   */
  updateCameraUp() {
    this._yAxisUpSpace.setFromUnitVectors(this._camera.up, R3), this._yAxisUpSpaceInverse.copy(this._yAxisUpSpace).invert();
  }
  /**
   * Apply current camera-up direction to the camera.
   * The orbit system will be re-initialized with the current position.
   * @category Methods
   */
  applyCameraUp() {
    const t = vi.subVectors(this._target, this._camera.position).normalize(), i = ji.crossVectors(t, this._camera.up);
    this._camera.up.crossVectors(i, t), this._camera.updateMatrixWorld();
    const n = this.getPosition(vi);
    this.updateCameraUp(), this.setPosition(n.x, n.y, n.z);
  }
  /**
   * Update camera position and directions.
   * This should be called in your tick loop every time, and returns true if re-rendering is needed.
   * @param delta
   * @returns updated
   * @category Methods
   */
  update(t) {
    const i = this._sphericalEnd.theta - this._spherical.theta, n = this._sphericalEnd.phi - this._spherical.phi, r = this._sphericalEnd.radius - this._spherical.radius, a = oR.subVectors(this._targetEnd, this._target), o = lR.subVectors(this._focalOffsetEnd, this._focalOffset), l = this._zoomEnd - this._zoom;
    if (An(i))
      this._thetaVelocity.value = 0, this._spherical.theta = this._sphericalEnd.theta;
    else {
      const d = this._isUserControllingRotate ? this.draggingSmoothTime : this.smoothTime;
      this._spherical.theta = M3(
        this._spherical.theta,
        this._sphericalEnd.theta,
        this._thetaVelocity,
        d,
        1 / 0,
        t
      ), this._needsUpdate = !0;
    }
    if (An(n))
      this._phiVelocity.value = 0, this._spherical.phi = this._sphericalEnd.phi;
    else {
      const d = this._isUserControllingRotate ? this.draggingSmoothTime : this.smoothTime;
      this._spherical.phi = M3(
        this._spherical.phi,
        this._sphericalEnd.phi,
        this._phiVelocity,
        d,
        1 / 0,
        t
      ), this._needsUpdate = !0;
    }
    if (An(r))
      this._radiusVelocity.value = 0, this._spherical.radius = this._sphericalEnd.radius;
    else {
      const d = this._isUserControllingDolly ? this.draggingSmoothTime : this.smoothTime;
      this._spherical.radius = M3(
        this._spherical.radius,
        this._sphericalEnd.radius,
        this._radiusVelocity,
        d,
        this.maxSpeed,
        t
      ), this._needsUpdate = !0;
    }
    if (An(a.x) && An(a.y) && An(a.z))
      this._targetVelocity.set(0, 0, 0), this._target.copy(this._targetEnd);
    else {
      const d = this._isUserControllingTruck ? this.draggingSmoothTime : this.smoothTime;
      WP(
        this._target,
        this._targetEnd,
        this._targetVelocity,
        d,
        this.maxSpeed,
        t,
        this._target
      ), this._needsUpdate = !0;
    }
    if (An(o.x) && An(o.y) && An(o.z))
      this._focalOffsetVelocity.set(0, 0, 0), this._focalOffset.copy(this._focalOffsetEnd);
    else {
      const d = this._isUserControllingOffset ? this.draggingSmoothTime : this.smoothTime;
      WP(
        this._focalOffset,
        this._focalOffsetEnd,
        this._focalOffsetVelocity,
        d,
        this.maxSpeed,
        t,
        this._focalOffset
      ), this._needsUpdate = !0;
    }
    if (An(l))
      this._zoomVelocity.value = 0, this._zoom = this._zoomEnd;
    else {
      const d = this._isUserControllingZoom ? this.draggingSmoothTime : this.smoothTime;
      this._zoom = M3(
        this._zoom,
        this._zoomEnd,
        this._zoomVelocity,
        d,
        1 / 0,
        t
      );
    }
    if (this.dollyToCursor) {
      if (Nh(this._camera) && this._changedDolly !== 0) {
        const d = this._spherical.radius - this._lastDistance, f = this._camera, p = this._getCameraDirection(Xm), v = vi.copy(p).cross(f.up).normalize();
        v.lengthSq() === 0 && (v.x = 1);
        const g = ji.crossVectors(v, p), m = this._sphericalEnd.radius * Math.tan(f.getEffectiveFOV() * km * 0.5), y = (this._sphericalEnd.radius - d - this._sphericalEnd.radius) / this._sphericalEnd.radius, A = Ud.copy(this._targetEnd).add(v.multiplyScalar(this._dollyControlCoord.x * m * f.aspect)).add(g.multiplyScalar(this._dollyControlCoord.y * m)), b = vi.copy(this._targetEnd).lerp(A, y), M = this._lastDollyDirection === Bd.IN && this._spherical.radius <= this.minDistance, w = this._lastDollyDirection === Bd.OUT && this.maxDistance <= this._spherical.radius;
        if (this.infinityDolly && (M || w)) {
          this._sphericalEnd.radius -= d, this._spherical.radius -= d;
          const T = ji.copy(p).multiplyScalar(-d);
          b.add(T);
        }
        this._boundary.clampPoint(b, b);
        const C = ji.subVectors(b, this._targetEnd);
        this._targetEnd.copy(b), this._target.add(C), this._changedDolly -= d, An(this._changedDolly) && (this._changedDolly = 0);
      } else if (rc(this._camera) && this._changedZoom !== 0) {
        const d = this._zoom - this._lastZoom, f = this._camera, p = vi.set(
          this._dollyControlCoord.x,
          this._dollyControlCoord.y,
          (f.near + f.far) / (f.near - f.far)
        ).unproject(f), v = ji.set(0, 0, -1).applyQuaternion(f.quaternion), g = Ud.copy(p).add(v.multiplyScalar(-p.dot(f.up))), x = -(this._zoom - d - this._zoom) / this._zoom, y = this._getCameraDirection(Xm), A = this._targetEnd.dot(y), b = vi.copy(this._targetEnd).lerp(g, x), M = b.dot(y), w = y.multiplyScalar(M - A);
        b.sub(w), this._boundary.clampPoint(b, b);
        const C = ji.subVectors(b, this._targetEnd);
        this._targetEnd.copy(b), this._target.add(C), this._changedZoom -= d, An(this._changedZoom) && (this._changedZoom = 0);
      }
    }
    this._camera.zoom !== this._zoom && (this._camera.zoom = this._zoom, this._camera.updateProjectionMatrix(), this._updateNearPlaneCorners(), this._needsUpdate = !0);
    const c = this._collisionTest();
    this._spherical.radius = Math.min(this._spherical.radius, c), this._target.clamp(this.minTarget, this.maxTarget), this._spherical.makeSafe(), this._camera.position.setFromSpherical(this._spherical).applyQuaternion(this._yAxisUpSpaceInverse).add(this._target), this._camera.lookAt(this._target), (!An(this._focalOffset.x) || !An(this._focalOffset.y) || !An(this._focalOffset.z)) && (this._camera.updateMatrixWorld(), _o.setFromMatrixColumn(this._camera.matrix, 0), Po.setFromMatrixColumn(this._camera.matrix, 1), Bh.setFromMatrixColumn(this._camera.matrix, 2), _o.multiplyScalar(this._focalOffset.x), Po.multiplyScalar(-this._focalOffset.y), Bh.multiplyScalar(this._focalOffset.z), vi.copy(_o).add(Po).add(Bh), this._camera.position.add(vi)), this._boundaryEnclosesCamera && this._encloseToBoundary(
      this._camera.position.copy(this._target),
      vi.setFromSpherical(this._spherical).applyQuaternion(this._yAxisUpSpaceInverse),
      1
    );
    const u = this._needsUpdate;
    return u && !this._updatedLastTime ? (this._hasRested = !1, this.dispatchEvent({ type: "wake" }), this.dispatchEvent({ type: "update" })) : u ? (this.dispatchEvent({ type: "update" }), An(i, this.restThreshold) && An(n, this.restThreshold) && An(r, this.restThreshold) && An(a.x, this.restThreshold) && An(a.y, this.restThreshold) && An(a.z, this.restThreshold) && An(o.x, this.restThreshold) && An(o.y, this.restThreshold) && An(o.z, this.restThreshold) && An(l, this.restThreshold) && !this._hasRested && (this._hasRested = !0, this.dispatchEvent({ type: "rest" }))) : !u && this._updatedLastTime && this.dispatchEvent({ type: "sleep" }), this._lastDistance = this._spherical.radius, this._lastZoom = this._zoom, this._updatedLastTime = u, this._needsUpdate = !1, this.debuggerPoint && this.debuggerPoint.position.copy(this._targetEnd), u;
  }
  /**
   * Get all state in JSON string
   * @category Methods
   */
  toJSON() {
    return JSON.stringify({
      enabled: this._enabled,
      minDistance: this.minDistance,
      maxDistance: Gm(this.maxDistance),
      minZoom: this.minZoom,
      maxZoom: Gm(this.maxZoom),
      minPolarAngle: this.minPolarAngle,
      maxPolarAngle: Gm(this.maxPolarAngle),
      minAzimuthAngle: Gm(this.minAzimuthAngle),
      maxAzimuthAngle: Gm(this.maxAzimuthAngle),
      smoothTime: this.smoothTime,
      draggingSmoothTime: this.draggingSmoothTime,
      dollySpeed: this.dollySpeed,
      truckSpeed: this.truckSpeed,
      dollyToCursor: this.dollyToCursor,
      verticalDragToForward: this.verticalDragToForward,
      target: this._targetEnd.toArray(),
      position: vi.setFromSpherical(this._sphericalEnd).add(this._targetEnd).toArray(),
      zoom: this._zoomEnd,
      focalOffset: this._focalOffsetEnd.toArray(),
      target0: this._target0.toArray(),
      position0: this._position0.toArray(),
      zoom0: this._zoom0,
      focalOffset0: this._focalOffset0.toArray()
    });
  }
  /**
   * Reproduce the control state with JSON. enableTransition is where anim or not in a boolean.
   * @param json
   * @param enableTransition
   * @category Methods
   */
  fromJSON(t, i = !1) {
    const n = JSON.parse(t);
    this.enabled = n.enabled, this.minDistance = n.minDistance, this.maxDistance = Hm(n.maxDistance), this.minZoom = n.minZoom, this.maxZoom = Hm(n.maxZoom), this.minPolarAngle = n.minPolarAngle, this.maxPolarAngle = Hm(n.maxPolarAngle), this.minAzimuthAngle = Hm(n.minAzimuthAngle), this.maxAzimuthAngle = Hm(n.maxAzimuthAngle), this.smoothTime = n.smoothTime, this.draggingSmoothTime = n.draggingSmoothTime, this.dollySpeed = n.dollySpeed, this.truckSpeed = n.truckSpeed, this.dollyToCursor = n.dollyToCursor, this.verticalDragToForward = n.verticalDragToForward, this._target0.fromArray(n.target0), this._position0.fromArray(n.position0), this._zoom0 = n.zoom0, this._focalOffset0.fromArray(n.focalOffset0), this.moveTo(n.target[0], n.target[1], n.target[2], i), ha.setFromVector3(
      vi.fromArray(n.position).sub(this._targetEnd).applyQuaternion(this._yAxisUpSpace)
    ), this.rotateTo(ha.theta, ha.phi, i), this.dollyTo(ha.radius, i), this.zoomTo(n.zoom, i), this.setFocalOffset(
      n.focalOffset[0],
      n.focalOffset[1],
      n.focalOffset[2],
      i
    ), this._needsUpdate = !0;
  }
  /**
   * Attach all internal event handlers to enable drag control.
   * @category Methods
   */
  connect(t) {
    if (this._domElement) {
      console.warn("camera-controls is already connected.");
      return;
    }
    t.setAttribute("data-camera-controls-version", A2e), this._addAllEventListeners(t);
  }
  /**
   * Detach all internal event handlers to disable drag control.
   */
  disconnect() {
    this.cancel(), this._removeAllEventListeners(), this._domElement && (this._domElement.removeAttribute("data-camera-controls-version"), this._domElement = void 0);
  }
  /**
   * Dispose the cameraControls instance itself, remove all eventListeners.
   * @category Methods
   */
  dispose() {
    this.removeAllEventListeners(), this.disconnect();
  }
  // it's okay to expose public though
  _getTargetDirection(t) {
    return t.setFromSpherical(this._spherical).divideScalar(this._spherical.radius).applyQuaternion(this._yAxisUpSpaceInverse);
  }
  // it's okay to expose public though
  _getCameraDirection(t) {
    return this._getTargetDirection(t).negate();
  }
  _findPointerById(t) {
    return this._activePointers.find((i) => i.pointerId === t);
  }
  _findPointerByMouseButton(t) {
    return this._activePointers.find((i) => i.mouseButton === t);
  }
  _disposePointer(t) {
    this._activePointers.splice(this._activePointers.indexOf(t), 1);
  }
  _encloseToBoundary(t, i, n) {
    const r = i.lengthSq();
    if (r === 0)
      return t;
    const a = ji.copy(i).add(t), l = this._boundary.clampPoint(a, Ud).sub(a), c = l.lengthSq();
    if (c === 0)
      return t.add(i);
    if (c === r)
      return t;
    if (n === 0)
      return t.add(i).add(l);
    {
      const h = 1 + n * c / i.dot(l);
      return t.add(ji.copy(i).multiplyScalar(h)).add(l.multiplyScalar(1 - n));
    }
  }
  _updateNearPlaneCorners() {
    if (Nh(this._camera)) {
      const t = this._camera, { near: i } = t, n = t.getEffectiveFOV() * km, r = Math.tan(n * 0.5) * i, a = r * t.aspect;
      this._nearPlaneCorners[0].set(-a, -r, 0), this._nearPlaneCorners[1].set(a, -r, 0), this._nearPlaneCorners[2].set(a, r, 0), this._nearPlaneCorners[3].set(-a, r, 0);
    } else if (rc(this._camera)) {
      const t = this._camera, i = 1 / t.zoom, n = t.left * i, r = t.right * i, a = t.top * i, o = t.bottom * i;
      this._nearPlaneCorners[0].set(n, a, 0), this._nearPlaneCorners[1].set(r, a, 0), this._nearPlaneCorners[2].set(r, o, 0), this._nearPlaneCorners[3].set(n, o, 0);
    }
  }
  // lateUpdate
  _collisionTest() {
    let t = 1 / 0;
    if (!(this.colliderMeshes.length >= 1) || HA(this._camera, "_collisionTest"))
      return t;
    const n = this._getTargetDirection(Xm);
    jA.lookAt(aR, n, this._camera.up);
    for (let r = 0; r < 4; r++) {
      const a = ji.copy(this._nearPlaneCorners[r]);
      a.applyMatrix4(jA);
      const o = Ud.addVectors(this._target, a);
      D3.set(o, n), D3.far = this._spherical.radius + 1;
      const l = D3.intersectObjects(this.colliderMeshes);
      l.length !== 0 && l[0].distance < t && (t = l[0].distance);
    }
    return t;
  }
  /**
   * Get its client rect and package into given `DOMRect` .
   */
  _getClientRect(t) {
    if (!this._domElement)
      return;
    const i = this._domElement.getBoundingClientRect();
    return t.x = i.left, t.y = i.top, this._viewport ? (t.x += this._viewport.x, t.y += i.height - this._viewport.w - this._viewport.y, t.width = this._viewport.z, t.height = this._viewport.w) : (t.width = i.width, t.height = i.height), t;
  }
  _createOnRestPromise(t) {
    return t ? Promise.resolve() : (this._hasRested = !1, this.dispatchEvent({ type: "transitionstart" }), new Promise((i) => {
      const n = () => {
        this.removeEventListener("rest", n), i();
      };
      this.addEventListener("rest", n);
    }));
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _addAllEventListeners(t) {
  }
  _removeAllEventListeners() {
  }
  /**
   * backward compatible
   * @deprecated use smoothTime (in seconds) instead
   * @category Properties
   */
  get dampingFactor() {
    return console.warn(".dampingFactor has been deprecated. use smoothTime (in seconds) instead."), 0;
  }
  /**
   * backward compatible
   * @deprecated use smoothTime (in seconds) instead
   * @category Properties
   */
  set dampingFactor(t) {
    console.warn(".dampingFactor has been deprecated. use smoothTime (in seconds) instead.");
  }
  /**
   * backward compatible
   * @deprecated use draggingSmoothTime (in seconds) instead
   * @category Properties
   */
  get draggingDampingFactor() {
    return console.warn(
      ".draggingDampingFactor has been deprecated. use draggingSmoothTime (in seconds) instead."
    ), 0;
  }
  /**
   * backward compatible
   * @deprecated use draggingSmoothTime (in seconds) instead
   * @category Properties
   */
  set draggingDampingFactor(t) {
    console.warn(
      ".draggingDampingFactor has been deprecated. use draggingSmoothTime (in seconds) instead."
    );
  }
  static createBoundingSphere(t, i = new Wt.Sphere()) {
    const n = i, { center: r } = n;
    zd.makeEmpty(), t.traverseVisible((o) => {
      o.isMesh && zd.expandByObject(o);
    }), zd.getCenter(r);
    let a = 0;
    return t.traverseVisible((o) => {
      if (!o.isMesh)
        return;
      const l = o, c = l.geometry.clone();
      c.applyMatrix4(l.matrixWorld);
      const u = c.attributes.position;
      for (let d = 0, f = u.count; d < f; d++)
        vi.fromBufferAttribute(u, d), a = Math.max(a, r.distanceToSquared(vi));
    }), n.radius = Math.sqrt(a), n;
  }
}
var E2e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const T2e = "ColorAdjustmentComponent", C2e = {
  uniforms: {
    tDiffuse: { value: null },
    gamma: { value: 1 },
    saturation: { value: 1 },
    contrast: { value: 1 },
    brightness: { value: 1 },
    hue: { value: 0 },
    color: { value: new Yt(1, 1, 1, 1) }
  },
  vertexShader: `
  varying vec2 vTextureCoord;

	void main() {

      vTextureCoord = uv;

			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

	}
  `,
  fragmentShader: `
varying vec2 vTextureCoord;
uniform sampler2D tDiffuse;
uniform float gamma;
uniform float contrast;
uniform float saturation;
uniform float brightness;
uniform float hue;
uniform vec4 color;

vec3 czm_hue(vec3 rgb, float adjustment)
{
    const mat3 toYIQ = mat3(0.299,     0.587,     0.114,
                            0.595716, -0.274453, -0.321263,
                            0.211456, -0.522591,  0.311135);
    const mat3 toRGB = mat3(1.0,  0.9563,  0.6210,
                            1.0, -0.2721, -0.6474,
                            1.0, -1.107,   1.7046);
    
    vec3 yiq = toYIQ * rgb;
    float hue = atan(yiq.z, yiq.y) + adjustment;
    float chroma = sqrt(yiq.z * yiq.z + yiq.y * yiq.y);
    
    vec3 color = vec3(yiq.x, chroma * cos(hue), chroma * sin(hue));
    return toRGB * color;
}

void main(void)
{
    vec4 c = texture2D(tDiffuse, vTextureCoord);

    if (c.a > 0.0) {
        c.rgb /= c.a;

        vec3 rgb = pow(c.rgb, vec3(1. / gamma));
        rgb = mix(vec3(.5), mix(vec3(dot(vec3(.2125, .7154, .0721), rgb)), rgb, saturation), contrast);
        rgb = czm_hue(rgb, hue);

        rgb.r *= color.r;
        rgb.g *= color.g;
        rgb.b *= color.b;
        c.rgb = rgb * brightness;

        c.rgb *= c.a;
    }

    gl_FragColor = c * color.a;
}
  `
};
let A7 = class extends td {
  constructor() {
    super(...arguments);
    S(this, "_data");
  }
  initialize() {
    this.pass = new Z4(C2e), this.pass.enabled = !1, this._data = {
      gamma: 1,
      contrast: 1,
      // 对比度
      saturation: 1,
      // 饱和度
      brightness: 1,
      // 亮度
      hue: 0,
      red: 1,
      green: 1,
      blue: 1,
      alpha: 1,
      enable: !1
    };
  }
  setOptions(e) {
    const t = this.pass;
    t.uniforms.gamma.value = e.gamma ?? 1, t.uniforms.contrast.value = e.contrast ?? 1, t.uniforms.saturation.value = e.saturation ?? 1, t.uniforms.brightness.value = e.brightness ?? 1, t.uniforms.hue.value = e.hue ?? 0, t.uniforms.color.value = new Yt(
      e.red ?? 1,
      e.green ?? 1,
      e.blue ?? 1,
      e.alpha ?? 1
    ), t.enabled = e.enable ?? !1, this._data = e, this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      inActive: !1,
      data: {
        ...this._data
      },
      states: [],
      version: this.version
    };
  }
};
A7 = E2e([
  ni(T2e)
], A7);
var _2e = Object.defineProperty, P2e = Object.getOwnPropertyDescriptor, R2e = (s, e, t, i) => {
  for (var n = P2e(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && _2e(e, t, n), n;
};
function D2e(s) {
  let e = 0;
  if ("geometry" in s) {
    const t = s.geometry;
    for (const n in t.attributes) {
      const r = t.getAttribute(n);
      if (r instanceof Ht) {
        const { array: a } = r;
        "BYTES_PER_ELEMENT" in a && (e += r.count * r.itemSize * a.BYTES_PER_ELEMENT);
      }
    }
    const i = t.getIndex();
    if (i) {
      const { array: n } = i;
      "BYTES_PER_ELEMENT" in n && (e += i ? i.count * i.itemSize * n.BYTES_PER_ELEMENT : 0);
    }
  }
  return e / 8192;
}
var gr;
const HF = (gr = class {
  static loadFromCache(e, t) {
  }
  static add(e, t) {
    const i = new mt(), n = [];
    n.push({ parent: e, object: i, ancestor: e });
    let r = t;
    t.traverse((l) => {
      const c = l.parent ?? i;
      "material" in l && l.parent === null ? n.unshift({ parent: c, object: l, ancestor: e }) : (n.push({ parent: c, object: l, ancestor: e }), r = l);
    });
    const a = [];
    let o = 0;
    n.forEach(({ parent: l, object: c }, h) => {
      c.children.length > 0 && (c.children.length = 0), o += D2e(c), o > gr.PerFrameMemory && (a.push(h), o = 0);
    }), a.push(n.length), a.forEach((l, c) => {
      const h = c === 0 ? 0 : a[c - 1], u = l;
      gr.queue.push(n.slice(h, u));
    }), gr.endObjectIdSet.set(r, t);
  }
  render() {
    (gr.queue.shift() ?? []).forEach(({ parent: t, object: i, ancestor: n }) => {
      t && i && (t.add(i), gr.endObjectIdSet.get(i) && (gr.endObjectIdSet.delete(i), n.dispatchEvent({ type: "object-queue-render-end" })));
    });
  }
  dispose() {
    gr.queue.length = 0;
  }
}, S(gr, "queue", []), S(gr, "event", new es()), // kb
S(gr, "PerFrameMemory", 100), S(gr, "endObjectIdSet", /* @__PURE__ */ new Map()), gr);
R2e([
  Qp(0.01)
], HF.prototype, "render");
let VF = HF;
var gu = /* @__PURE__ */ ((s) => (s[s.Normal = 0] = "Normal", s[s.FourView = 1] = "FourView", s))(gu || {});
class L2e {
  constructor(e, t, i) {
    S(this, "camera");
    S(this, "canvas", document.createElement("canvas"));
    S(this, "containerDom");
    S(this, "needsUpdate", !0);
    S(this, "renderSize", new Se());
    this.containerId = e, this.cameraView = t, this.camera = i.clone(), this.containerDom = document.getElementById(this.containerId) ?? document.createElement("div"), this.renderSize.set(this.containerDom.clientWidth, this.containerDom.clientHeight);
  }
  dispose() {
  }
  appendChild() {
    this.containerDom.appendChild(this.canvas), this.updateSize();
  }
  removeParent() {
    const e = document.getElementById(this.containerId);
    e == null || e.removeChild(this.canvas);
  }
  updateSize() {
    (this.containerDom.clientWidth !== this.renderSize.x || this.containerDom.clientHeight !== this.renderSize.y) && (this.renderSize.set(this.containerDom.clientWidth, this.containerDom.clientHeight), this.canvas.width = this.renderSize.x * window.devicePixelRatio, this.canvas.height = this.renderSize.y * window.devicePixelRatio, this.camera instanceof Zt && (this.camera.aspect = this.renderSize.x / this.renderSize.y), this.camera.updateProjectionMatrix());
  }
  updateCamera(e) {
    switch (this.camera.position.copy(e.position), this.camera.quaternion.copy(e.quaternion), this.cameraView) {
      case 3:
        this.transformToUpView(this.camera);
        break;
      case 1:
        this.transformToFrontView(this.camera);
        break;
      case 2:
        this.transformToRightView(this.camera);
        break;
    }
  }
  draw(e) {
    if (this.updateSize(), this.renderSize.x === 0 || this.renderSize.y === 0)
      return;
    const t = this.canvas.getContext("2d");
    t == null || t.clearRect(
      0,
      0,
      this.renderSize.x * window.devicePixelRatio,
      this.renderSize.y * window.devicePixelRatio
    ), t == null || t.drawImage(
      e,
      0,
      0,
      this.renderSize.x * window.devicePixelRatio,
      this.renderSize.y * window.devicePixelRatio
    );
  }
  // 切换到透视视图
  transformToPerspectiveView(e) {
  }
  // 切换到顶视图
  transformToUpView(e) {
    e.position.x = 0, e.position.z = 0, e.quaternion.copy(new St()), e.lookAt(new _());
  }
  // 切换到右视图
  transformToRightView(e) {
    e.position.z = 0, e.position.y = 0, e.quaternion.copy(new St()), e.lookAt(new _(-1, 0, 0));
  }
  // 切换到正视图
  transformToFrontView(e) {
    e.position.y = 0, e.position.x = 0, e.quaternion.copy(new St()), e.lookAt(new _(0, 0, -1));
  }
}
function Qt(s) {
  return Math.abs(s) < 1e-10 ? 0 : s;
}
class I2e {
  constructor(e) {
    S(this, "_widthHalf", 0);
    S(this, "_heightHalf", 0);
    S(this, "vector", new _());
    S(this, "viewMatrix", new et());
    S(this, "viewProjectionMatrix", new et());
    S(this, "cache", {
      camera: { fov: 0, style: "" },
      objects: /* @__PURE__ */ new WeakMap()
    });
    S(this, "cameraElementMap", /* @__PURE__ */ new Map());
    this.domElement = e;
    const t = e;
    t.style.overflow = "hidden", t.style.display = "flex", this.cameraElementMap.set("autoResize-faceToCamera", this.initCameraElement()), this.cameraElementMap.set("autoResize", this.initCameraElement()), this.cameraElementMap.set("faceToCamera", this.initCameraElement()), this.cameraElementMap.forEach((i) => {
      e.appendChild(i);
    }), e.classList.add("css-render");
  }
  initCameraElement() {
    const e = document.createElement("div");
    return e.id = "css-camera", e.style.transformStyle = "preserve-3d", e.style.position = "absolute", e.style.top = "0", e.style.pointerEvents = "none", e;
  }
  getCameraElement(e, t) {
    let i;
    return e && !t && (i = this.cameraElementMap.get("autoResize")), t && !e && (i = this.cameraElementMap.get("faceToCamera")), i || (i = this.cameraElementMap.get("autoResize-faceToCamera")), i || document.createElement("div");
  }
  setSize(e, t) {
    this._widthHalf = e / 2, this._heightHalf = t / 2, this.domElement.style.width = `${e}px`, this.domElement.style.height = `${t}px`, this.cameraElementMap.forEach((i) => {
      const n = i;
      n.style.width = `${e}px`, n.style.height = `${t}px`;
    });
  }
  filterAndFlatten(e) {
    const t = [];
    return e.traverse((i) => {
      (i instanceof h8 || i instanceof cp) && t.push(i);
    }), t;
  }
  getDistanceToSquared() {
    const e = new _(), t = new _();
    return function(i, n) {
      return e.setFromMatrixPosition(i.matrixWorld), t.setFromMatrixPosition(n.matrixWorld), e.distanceToSquared(t);
    };
  }
  renderObject(e, t, i) {
    if (e instanceof cp) {
      if (!e.autoResize && e.faceToCamera) {
        this.vector.setFromMatrixPosition(e.matrixWorld), this.vector.applyMatrix4(this.viewProjectionMatrix);
        const { element: r } = e, a = `translate(-50%,-100%) translate(${this.vector.x * this._widthHalf + this._widthHalf}px,${-this.vector.y * this._heightHalf + this._heightHalf}px)`;
        r.style.transform = a;
        const o = {
          distanceToCameraSquared: this.getDistanceToSquared()(i, e)
        };
        this.cache.objects.set(e, o);
      } else {
        let r = "";
        e.autoResize && e.faceToCamera ? r = this.getObjectFacetoCameraCSSMatrix(e, i) : r = this.getObjectCSSMatrix(e);
        const { element: a } = e;
        a.style.transform = r;
        const o = {
          style: r,
          distanceToCameraSquared: 0
        };
        this.cache.objects.set(e, o);
      }
      const n = this.getCameraElement(e.autoResize, e.faceToCamera);
      e.element.parentNode !== n && n.appendChild(e.element);
    }
    for (let n = 0, r = e.children.length; n < r; n++)
      this.renderObject(e.children[n], t, i);
  }
  getObjectCSSMatrix(e) {
    const t = e.matrixWorld.elements;
    return `translate(-50%,-50%)${`matrix3d(${Qt(t[0])},${Qt(
      t[1]
    )},${Qt(t[2])},${Qt(t[3])},${Qt(
      -t[4]
    )},${Qt(-t[5])},${Qt(-t[6])},${Qt(
      -t[7]
    )},${Qt(t[8])},${Qt(t[9])},${Qt(
      t[10]
    )},${Qt(t[11])},${Qt(t[12])},${Qt(
      t[13]
    )},${Qt(t[14])},${Qt(t[15])})`}`;
  }
  getObjectFacetoCameraCSSMatrix(e, t) {
    const i = t.quaternion.clone();
    e.quaternion.set(i.x, i.y, i.z, i.w);
    const n = e.matrixWorld.elements;
    return `translate(-50.014%,-50.014%)${`matrix3d(${Qt(n[0])},${Qt(
      n[1]
    )},${Qt(n[2])},${Qt(n[3])},${Qt(
      -n[4]
    )},${Qt(-n[5])},${Qt(-n[6])},${Qt(
      -n[7]
    )},${Qt(n[8])},${Qt(n[9])},${Qt(
      n[10]
    )},${Qt(n[11])},${Qt(n[12])},${Qt(
      n[13]
    )},${Qt(n[14])},${Qt(n[15])})`}`;
  }
  getCameraCSSMatrix(e) {
    const { elements: t } = e;
    return `matrix3d(${Qt(t[0])},${Qt(-t[1])},${Qt(
      t[2]
    )},${Qt(t[3])},${Qt(t[4])},${Qt(-t[5])},${Qt(
      t[6]
    )},${Qt(t[7])},${Qt(t[8])},${Qt(-t[9])},${Qt(
      t[10]
    )},${Qt(t[11])},${Qt(t[12])},${Qt(-t[13])},${Qt(
      t[14]
    )},${Qt(t[15])})`;
  }
  render(e, t) {
    const i = t.projectionMatrix.elements[5] * this._heightHalf;
    this.cache.camera.fov !== i && (this.domElement.style.perspective = `${i}px`, this.cache.camera.fov = i);
    const n = this.getCameraElement(!0, !0), r = this.getCameraElement(!0, !1), a = this.getCameraElement(!1, !0);
    if (n.hasChildNodes()) {
      const l = `${`translateZ(${i}px)${this.getCameraCSSMatrix(
        t.matrixWorldInverse
      )}`}translate(${this._widthHalf}px,${this._heightHalf}px)`;
      n.style.transform = l, this.cache.camera.style = l;
    }
    if (r.hasChildNodes()) {
      const l = `${`translateZ(${i}px)${this.getCameraCSSMatrix(
        t.matrixWorldInverse
      )}`}translate(${this._widthHalf}px,${this._heightHalf}px)`;
      r.style.transform = l, this.cache.camera.style = l;
    }
    a.hasChildNodes() && (this.viewMatrix.copy(t.matrixWorldInverse), this.viewProjectionMatrix.multiplyMatrices(t.projectionMatrix, this.viewMatrix)), this.renderObject(e, e, t), a.hasChildNodes() && this.zOrder(e);
  }
  zOrder(e) {
    const t = this.filterAndFlatten(e).sort((n, r) => {
      const a = this.cache.objects.get(n).distanceToCameraSquared, o = this.cache.objects.get(r).distanceToCameraSquared;
      return a - o;
    }), i = t.length;
    for (let n = 0, r = t.length; n < r; n++)
      t[n].element.style.zIndex = `${i - n}`;
  }
}
const O2e = "166", N2e = 0, uR = 1, B2e = 2, WF = 1, F2e = 2, V0 = 3, Kc = 0, Fr = 1, Q0 = 2, Nc = 0, Jf = 1, dR = 2, fR = 3, pR = 4, U2e = 5, ru = 100, z2e = 101, k2e = 102, G2e = 103, H2e = 104, V2e = 200, W2e = 201, X2e = 202, j2e = 203, b7 = 204, M7 = 205, q2e = 206, Y2e = 207, Z2e = 208, K2e = 209, J2e = 210, Q2e = 211, $2e = 212, eve = 213, tve = 214, ive = 0, nve = 1, sve = 2, Hx = 3, rve = 4, ave = 5, ove = 6, lve = 7, XF = 0, cve = 1, hve = 2, Bc = 0, uve = 1, dve = 2, fve = 3, pve = 4, mve = 5, gve = 6, vve = 7, jF = 300, Op = 301, Np = 302, S7 = 303, w7 = 304, d8 = 306, E7 = 1e3, vu = 1001, T7 = 1002, Ea = 1003, yve = 1004, L3 = 1005, Qa = 1006, qA = 1007, yu = 1008, Sl = 1009, qF = 1010, YF = 1011, jg = 1012, _w = 1013, zu = 1014, sl = 1015, G2 = 1016, Pw = 1017, Rw = 1018, Bp = 1020, ZF = 35902, KF = 1021, JF = 1022, no = 1023, QF = 1024, $F = 1025, Qf = 1026, Fp = 1027, eU = 1028, Dw = 1029, tU = 1030, Lw = 1031, Iw = 1033, Jy = 33776, Qy = 33777, $y = 33778, e9 = 33779, C7 = 35840, _7 = 35841, P7 = 35842, R7 = 35843, D7 = 36196, L7 = 37492, I7 = 37496, O7 = 37808, N7 = 37809, B7 = 37810, F7 = 37811, U7 = 37812, z7 = 37813, k7 = 37814, G7 = 37815, H7 = 37816, V7 = 37817, W7 = 37818, X7 = 37819, j7 = 37820, q7 = 37821, t9 = 36492, Y7 = 36494, Z7 = 36495, iU = 36283, K7 = 36284, J7 = 36285, Q7 = 36286, xve = 3200, Ave = 3201, bve = 0, Mve = 1, Mc = "", Oo = "srgb", hh = "srgb-linear", Ow = "display-p3", f8 = "display-p3-linear", Vx = "linear", Sn = "srgb", Wx = "rec709", Xx = "p3", kd = 7680, mR = 519, Sve = 512, wve = 513, Eve = 514, nU = 515, Tve = 516, Cve = 517, _ve = 518, Pve = 519, gR = 35044, vR = "300 es", rl = 2e3, jx = 2001;
class xi {
  constructor(e, t, i, n, r, a, o, l, c) {
    xi.prototype.isMatrix3 = !0, this.elements = [
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1
    ], e !== void 0 && this.set(e, t, i, n, r, a, o, l, c);
  }
  set(e, t, i, n, r, a, o, l, c) {
    const h = this.elements;
    return h[0] = e, h[1] = n, h[2] = o, h[3] = t, h[4] = r, h[5] = l, h[6] = i, h[7] = a, h[8] = c, this;
  }
  identity() {
    return this.set(
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1
    ), this;
  }
  copy(e) {
    const t = this.elements, i = e.elements;
    return t[0] = i[0], t[1] = i[1], t[2] = i[2], t[3] = i[3], t[4] = i[4], t[5] = i[5], t[6] = i[6], t[7] = i[7], t[8] = i[8], this;
  }
  extractBasis(e, t, i) {
    return e.setFromMatrix3Column(this, 0), t.setFromMatrix3Column(this, 1), i.setFromMatrix3Column(this, 2), this;
  }
  setFromMatrix4(e) {
    const t = e.elements;
    return this.set(
      t[0],
      t[4],
      t[8],
      t[1],
      t[5],
      t[9],
      t[2],
      t[6],
      t[10]
    ), this;
  }
  multiply(e) {
    return this.multiplyMatrices(this, e);
  }
  premultiply(e) {
    return this.multiplyMatrices(e, this);
  }
  multiplyMatrices(e, t) {
    const i = e.elements, n = t.elements, r = this.elements, a = i[0], o = i[3], l = i[6], c = i[1], h = i[4], u = i[7], d = i[2], f = i[5], p = i[8], v = n[0], g = n[3], m = n[6], x = n[1], y = n[4], A = n[7], b = n[2], M = n[5], w = n[8];
    return r[0] = a * v + o * x + l * b, r[3] = a * g + o * y + l * M, r[6] = a * m + o * A + l * w, r[1] = c * v + h * x + u * b, r[4] = c * g + h * y + u * M, r[7] = c * m + h * A + u * w, r[2] = d * v + f * x + p * b, r[5] = d * g + f * y + p * M, r[8] = d * m + f * A + p * w, this;
  }
  multiplyScalar(e) {
    const t = this.elements;
    return t[0] *= e, t[3] *= e, t[6] *= e, t[1] *= e, t[4] *= e, t[7] *= e, t[2] *= e, t[5] *= e, t[8] *= e, this;
  }
  determinant() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8];
    return t * a * h - t * o * c - i * r * h + i * o * l + n * r * c - n * a * l;
  }
  invert() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8], u = h * a - o * c, d = o * l - h * r, f = c * r - a * l, p = t * u + i * d + n * f;
    if (p === 0)
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0);
    const v = 1 / p;
    return e[0] = u * v, e[1] = (n * c - h * i) * v, e[2] = (o * i - n * a) * v, e[3] = d * v, e[4] = (h * t - n * l) * v, e[5] = (n * r - o * t) * v, e[6] = f * v, e[7] = (i * l - c * t) * v, e[8] = (a * t - i * r) * v, this;
  }
  transpose() {
    let e;
    const t = this.elements;
    return e = t[1], t[1] = t[3], t[3] = e, e = t[2], t[2] = t[6], t[6] = e, e = t[5], t[5] = t[7], t[7] = e, this;
  }
  getNormalMatrix(e) {
    return this.setFromMatrix4(e).invert().transpose();
  }
  transposeIntoArray(e) {
    const t = this.elements;
    return e[0] = t[0], e[1] = t[3], e[2] = t[6], e[3] = t[1], e[4] = t[4], e[5] = t[7], e[6] = t[2], e[7] = t[5], e[8] = t[8], this;
  }
  setUvTransform(e, t, i, n, r, a, o) {
    const l = Math.cos(r), c = Math.sin(r);
    return this.set(
      i * l,
      i * c,
      -i * (l * a + c * o) + a + e,
      -n * c,
      n * l,
      -n * (-c * a + l * o) + o + t,
      0,
      0,
      1
    ), this;
  }
  //
  scale(e, t) {
    return this.premultiply(YA.makeScale(e, t)), this;
  }
  rotate(e) {
    return this.premultiply(YA.makeRotation(-e)), this;
  }
  translate(e, t) {
    return this.premultiply(YA.makeTranslation(e, t)), this;
  }
  // for 2D Transforms
  makeTranslation(e, t) {
    return e.isVector2 ? this.set(
      1,
      0,
      e.x,
      0,
      1,
      e.y,
      0,
      0,
      1
    ) : this.set(
      1,
      0,
      e,
      0,
      1,
      t,
      0,
      0,
      1
    ), this;
  }
  makeRotation(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      -i,
      0,
      i,
      t,
      0,
      0,
      0,
      1
    ), this;
  }
  makeScale(e, t) {
    return this.set(
      e,
      0,
      0,
      0,
      t,
      0,
      0,
      0,
      1
    ), this;
  }
  //
  equals(e) {
    const t = this.elements, i = e.elements;
    for (let n = 0; n < 9; n++)
      if (t[n] !== i[n])
        return !1;
    return !0;
  }
  fromArray(e, t = 0) {
    for (let i = 0; i < 9; i++)
      this.elements[i] = e[i + t];
    return this;
  }
  toArray(e = [], t = 0) {
    const i = this.elements;
    return e[t] = i[0], e[t + 1] = i[1], e[t + 2] = i[2], e[t + 3] = i[3], e[t + 4] = i[4], e[t + 5] = i[5], e[t + 6] = i[6], e[t + 7] = i[7], e[t + 8] = i[8], e;
  }
  clone() {
    return new this.constructor().fromArray(this.elements);
  }
}
const YA = /* @__PURE__ */ new xi(), yR = /* @__PURE__ */ new xi().set(
  0.8224621,
  0.177538,
  0,
  0.0331941,
  0.9668058,
  0,
  0.0170827,
  0.0723974,
  0.9105199
), xR = /* @__PURE__ */ new xi().set(
  1.2249401,
  -0.2249404,
  0,
  -0.0420569,
  1.0420571,
  0,
  -0.0196376,
  -0.0786361,
  1.0982735
), I3 = {
  [hh]: {
    transfer: Vx,
    primaries: Wx,
    toReference: (s) => s,
    fromReference: (s) => s
  },
  [Oo]: {
    transfer: Sn,
    primaries: Wx,
    toReference: (s) => s.convertSRGBToLinear(),
    fromReference: (s) => s.convertLinearToSRGB()
  },
  [f8]: {
    transfer: Vx,
    primaries: Xx,
    toReference: (s) => s.applyMatrix3(xR),
    fromReference: (s) => s.applyMatrix3(yR)
  },
  [Ow]: {
    transfer: Sn,
    primaries: Xx,
    toReference: (s) => s.convertSRGBToLinear().applyMatrix3(xR),
    fromReference: (s) => s.applyMatrix3(yR).convertLinearToSRGB()
  }
}, Rve = /* @__PURE__ */ new Set([hh, f8]), rn = {
  enabled: !0,
  _workingColorSpace: hh,
  get workingColorSpace() {
    return this._workingColorSpace;
  },
  set workingColorSpace(s) {
    if (!Rve.has(s))
      throw new Error(`Unsupported working color space, "${s}".`);
    this._workingColorSpace = s;
  },
  convert: function(s, e, t) {
    if (this.enabled === !1 || e === t || !e || !t)
      return s;
    const i = I3[e].toReference, n = I3[t].fromReference;
    return n(i(s));
  },
  fromWorkingColorSpace: function(s, e) {
    return this.convert(s, this._workingColorSpace, e);
  },
  toWorkingColorSpace: function(s, e) {
    return this.convert(s, e, this._workingColorSpace);
  },
  getPrimaries: function(s) {
    return I3[s].primaries;
  },
  getTransfer: function(s) {
    return s === Mc ? Vx : I3[s].transfer;
  }
};
function $f(s) {
  return s < 0.04045 ? s * 0.0773993808 : Math.pow(s * 0.9478672986 + 0.0521327014, 2.4);
}
function ZA(s) {
  return s < 31308e-7 ? s * 12.92 : 1.055 * Math.pow(s, 0.41666) - 0.055;
}
const sU = {
  aliceblue: 15792383,
  antiquewhite: 16444375,
  aqua: 65535,
  aquamarine: 8388564,
  azure: 15794175,
  beige: 16119260,
  bisque: 16770244,
  black: 0,
  blanchedalmond: 16772045,
  blue: 255,
  blueviolet: 9055202,
  brown: 10824234,
  burlywood: 14596231,
  cadetblue: 6266528,
  chartreuse: 8388352,
  chocolate: 13789470,
  coral: 16744272,
  cornflowerblue: 6591981,
  cornsilk: 16775388,
  crimson: 14423100,
  cyan: 65535,
  darkblue: 139,
  darkcyan: 35723,
  darkgoldenrod: 12092939,
  darkgray: 11119017,
  darkgreen: 25600,
  darkgrey: 11119017,
  darkkhaki: 12433259,
  darkmagenta: 9109643,
  darkolivegreen: 5597999,
  darkorange: 16747520,
  darkorchid: 10040012,
  darkred: 9109504,
  darksalmon: 15308410,
  darkseagreen: 9419919,
  darkslateblue: 4734347,
  darkslategray: 3100495,
  darkslategrey: 3100495,
  darkturquoise: 52945,
  darkviolet: 9699539,
  deeppink: 16716947,
  deepskyblue: 49151,
  dimgray: 6908265,
  dimgrey: 6908265,
  dodgerblue: 2003199,
  firebrick: 11674146,
  floralwhite: 16775920,
  forestgreen: 2263842,
  fuchsia: 16711935,
  gainsboro: 14474460,
  ghostwhite: 16316671,
  gold: 16766720,
  goldenrod: 14329120,
  gray: 8421504,
  green: 32768,
  greenyellow: 11403055,
  grey: 8421504,
  honeydew: 15794160,
  hotpink: 16738740,
  indianred: 13458524,
  indigo: 4915330,
  ivory: 16777200,
  khaki: 15787660,
  lavender: 15132410,
  lavenderblush: 16773365,
  lawngreen: 8190976,
  lemonchiffon: 16775885,
  lightblue: 11393254,
  lightcoral: 15761536,
  lightcyan: 14745599,
  lightgoldenrodyellow: 16448210,
  lightgray: 13882323,
  lightgreen: 9498256,
  lightgrey: 13882323,
  lightpink: 16758465,
  lightsalmon: 16752762,
  lightseagreen: 2142890,
  lightskyblue: 8900346,
  lightslategray: 7833753,
  lightslategrey: 7833753,
  lightsteelblue: 11584734,
  lightyellow: 16777184,
  lime: 65280,
  limegreen: 3329330,
  linen: 16445670,
  magenta: 16711935,
  maroon: 8388608,
  mediumaquamarine: 6737322,
  mediumblue: 205,
  mediumorchid: 12211667,
  mediumpurple: 9662683,
  mediumseagreen: 3978097,
  mediumslateblue: 8087790,
  mediumspringgreen: 64154,
  mediumturquoise: 4772300,
  mediumvioletred: 13047173,
  midnightblue: 1644912,
  mintcream: 16121850,
  mistyrose: 16770273,
  moccasin: 16770229,
  navajowhite: 16768685,
  navy: 128,
  oldlace: 16643558,
  olive: 8421376,
  olivedrab: 7048739,
  orange: 16753920,
  orangered: 16729344,
  orchid: 14315734,
  palegoldenrod: 15657130,
  palegreen: 10025880,
  paleturquoise: 11529966,
  palevioletred: 14381203,
  papayawhip: 16773077,
  peachpuff: 16767673,
  peru: 13468991,
  pink: 16761035,
  plum: 14524637,
  powderblue: 11591910,
  purple: 8388736,
  rebeccapurple: 6697881,
  red: 16711680,
  rosybrown: 12357519,
  royalblue: 4286945,
  saddlebrown: 9127187,
  salmon: 16416882,
  sandybrown: 16032864,
  seagreen: 3050327,
  seashell: 16774638,
  sienna: 10506797,
  silver: 12632256,
  skyblue: 8900331,
  slateblue: 6970061,
  slategray: 7372944,
  slategrey: 7372944,
  snow: 16775930,
  springgreen: 65407,
  steelblue: 4620980,
  tan: 13808780,
  teal: 32896,
  thistle: 14204888,
  tomato: 16737095,
  turquoise: 4251856,
  violet: 15631086,
  wheat: 16113331,
  white: 16777215,
  whitesmoke: 16119285,
  yellow: 16776960,
  yellowgreen: 10145074
}, Zl = { h: 0, s: 0, l: 0 }, O3 = { h: 0, s: 0, l: 0 };
function KA(s, e, t) {
  return t < 0 && (t += 1), t > 1 && (t -= 1), t < 1 / 6 ? s + (e - s) * 6 * t : t < 1 / 2 ? e : t < 2 / 3 ? s + (e - s) * 6 * (2 / 3 - t) : s;
}
class hn {
  constructor(e, t, i) {
    return this.isColor = !0, this.r = 1, this.g = 1, this.b = 1, this.set(e, t, i);
  }
  set(e, t, i) {
    if (t === void 0 && i === void 0) {
      const n = e;
      n && n.isColor ? this.copy(n) : typeof n == "number" ? this.setHex(n) : typeof n == "string" && this.setStyle(n);
    } else
      this.setRGB(e, t, i);
    return this;
  }
  setScalar(e) {
    return this.r = e, this.g = e, this.b = e, this;
  }
  setHex(e, t = Oo) {
    return e = Math.floor(e), this.r = (e >> 16 & 255) / 255, this.g = (e >> 8 & 255) / 255, this.b = (e & 255) / 255, rn.toWorkingColorSpace(this, t), this;
  }
  setRGB(e, t, i, n = rn.workingColorSpace) {
    return this.r = e, this.g = t, this.b = i, rn.toWorkingColorSpace(this, n), this;
  }
  setHSL(e, t, i, n = rn.workingColorSpace) {
    if (e = Pge(e, 1), t = tr(t, 0, 1), i = tr(i, 0, 1), t === 0)
      this.r = this.g = this.b = i;
    else {
      const r = i <= 0.5 ? i * (1 + t) : i + t - i * t, a = 2 * i - r;
      this.r = KA(a, r, e + 1 / 3), this.g = KA(a, r, e), this.b = KA(a, r, e - 1 / 3);
    }
    return rn.toWorkingColorSpace(this, n), this;
  }
  setStyle(e, t = Oo) {
    function i(r) {
      r !== void 0 && parseFloat(r) < 1 && console.warn("THREE.Color: Alpha component of " + e + " will be ignored.");
    }
    let n;
    if (n = /^(\w+)\(([^\)]*)\)/.exec(e)) {
      let r;
      const a = n[1], o = n[2];
      switch (a) {
        case "rgb":
        case "rgba":
          if (r = /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setRGB(
              Math.min(255, parseInt(r[1], 10)) / 255,
              Math.min(255, parseInt(r[2], 10)) / 255,
              Math.min(255, parseInt(r[3], 10)) / 255,
              t
            );
          if (r = /^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setRGB(
              Math.min(100, parseInt(r[1], 10)) / 100,
              Math.min(100, parseInt(r[2], 10)) / 100,
              Math.min(100, parseInt(r[3], 10)) / 100,
              t
            );
          break;
        case "hsl":
        case "hsla":
          if (r = /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(o))
            return i(r[4]), this.setHSL(
              parseFloat(r[1]) / 360,
              parseFloat(r[2]) / 100,
              parseFloat(r[3]) / 100,
              t
            );
          break;
        default:
          console.warn("THREE.Color: Unknown color model " + e);
      }
    } else if (n = /^\#([A-Fa-f\d]+)$/.exec(e)) {
      const r = n[1], a = r.length;
      if (a === 3)
        return this.setRGB(
          parseInt(r.charAt(0), 16) / 15,
          parseInt(r.charAt(1), 16) / 15,
          parseInt(r.charAt(2), 16) / 15,
          t
        );
      if (a === 6)
        return this.setHex(parseInt(r, 16), t);
      console.warn("THREE.Color: Invalid hex color " + e);
    } else if (e && e.length > 0)
      return this.setColorName(e, t);
    return this;
  }
  setColorName(e, t = Oo) {
    const i = sU[e.toLowerCase()];
    return i !== void 0 ? this.setHex(i, t) : console.warn("THREE.Color: Unknown color " + e), this;
  }
  clone() {
    return new this.constructor(this.r, this.g, this.b);
  }
  copy(e) {
    return this.r = e.r, this.g = e.g, this.b = e.b, this;
  }
  copySRGBToLinear(e) {
    return this.r = $f(e.r), this.g = $f(e.g), this.b = $f(e.b), this;
  }
  copyLinearToSRGB(e) {
    return this.r = ZA(e.r), this.g = ZA(e.g), this.b = ZA(e.b), this;
  }
  convertSRGBToLinear() {
    return this.copySRGBToLinear(this), this;
  }
  convertLinearToSRGB() {
    return this.copyLinearToSRGB(this), this;
  }
  getHex(e = Oo) {
    return rn.fromWorkingColorSpace(qs.copy(this), e), Math.round(tr(qs.r * 255, 0, 255)) * 65536 + Math.round(tr(qs.g * 255, 0, 255)) * 256 + Math.round(tr(qs.b * 255, 0, 255));
  }
  getHexString(e = Oo) {
    return ("000000" + this.getHex(e).toString(16)).slice(-6);
  }
  getHSL(e, t = rn.workingColorSpace) {
    rn.fromWorkingColorSpace(qs.copy(this), t);
    const i = qs.r, n = qs.g, r = qs.b, a = Math.max(i, n, r), o = Math.min(i, n, r);
    let l, c;
    const h = (o + a) / 2;
    if (o === a)
      l = 0, c = 0;
    else {
      const u = a - o;
      switch (c = h <= 0.5 ? u / (a + o) : u / (2 - a - o), a) {
        case i:
          l = (n - r) / u + (n < r ? 6 : 0);
          break;
        case n:
          l = (r - i) / u + 2;
          break;
        case r:
          l = (i - n) / u + 4;
          break;
      }
      l /= 6;
    }
    return e.h = l, e.s = c, e.l = h, e;
  }
  getRGB(e, t = rn.workingColorSpace) {
    return rn.fromWorkingColorSpace(qs.copy(this), t), e.r = qs.r, e.g = qs.g, e.b = qs.b, e;
  }
  getStyle(e = Oo) {
    rn.fromWorkingColorSpace(qs.copy(this), e);
    const t = qs.r, i = qs.g, n = qs.b;
    return e !== Oo ? `color(${e} ${t.toFixed(3)} ${i.toFixed(3)} ${n.toFixed(3)})` : `rgb(${Math.round(t * 255)},${Math.round(i * 255)},${Math.round(n * 255)})`;
  }
  offsetHSL(e, t, i) {
    return this.getHSL(Zl), this.setHSL(Zl.h + e, Zl.s + t, Zl.l + i);
  }
  add(e) {
    return this.r += e.r, this.g += e.g, this.b += e.b, this;
  }
  addColors(e, t) {
    return this.r = e.r + t.r, this.g = e.g + t.g, this.b = e.b + t.b, this;
  }
  addScalar(e) {
    return this.r += e, this.g += e, this.b += e, this;
  }
  sub(e) {
    return this.r = Math.max(0, this.r - e.r), this.g = Math.max(0, this.g - e.g), this.b = Math.max(0, this.b - e.b), this;
  }
  multiply(e) {
    return this.r *= e.r, this.g *= e.g, this.b *= e.b, this;
  }
  multiplyScalar(e) {
    return this.r *= e, this.g *= e, this.b *= e, this;
  }
  lerp(e, t) {
    return this.r += (e.r - this.r) * t, this.g += (e.g - this.g) * t, this.b += (e.b - this.b) * t, this;
  }
  lerpColors(e, t, i) {
    return this.r = e.r + (t.r - e.r) * i, this.g = e.g + (t.g - e.g) * i, this.b = e.b + (t.b - e.b) * i, this;
  }
  lerpHSL(e, t) {
    this.getHSL(Zl), e.getHSL(O3);
    const i = kA(Zl.h, O3.h, t), n = kA(Zl.s, O3.s, t), r = kA(Zl.l, O3.l, t);
    return this.setHSL(i, n, r), this;
  }
  setFromVector3(e) {
    return this.r = e.x, this.g = e.y, this.b = e.z, this;
  }
  applyMatrix3(e) {
    const t = this.r, i = this.g, n = this.b, r = e.elements;
    return this.r = r[0] * t + r[3] * i + r[6] * n, this.g = r[1] * t + r[4] * i + r[7] * n, this.b = r[2] * t + r[5] * i + r[8] * n, this;
  }
  equals(e) {
    return e.r === this.r && e.g === this.g && e.b === this.b;
  }
  fromArray(e, t = 0) {
    return this.r = e[t], this.g = e[t + 1], this.b = e[t + 2], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.r, e[t + 1] = this.g, e[t + 2] = this.b, e;
  }
  fromBufferAttribute(e, t) {
    return this.r = e.getX(t), this.g = e.getY(t), this.b = e.getZ(t), this;
  }
  toJSON() {
    return this.getHex();
  }
  *[Symbol.iterator]() {
    yield this.r, yield this.g, yield this.b;
  }
}
const qs = /* @__PURE__ */ new hn();
hn.NAMES = sU;
class H2 {
  constructor(e = 0, t = 0, i = 0, n = 1) {
    this.isQuaternion = !0, this._x = e, this._y = t, this._z = i, this._w = n;
  }
  static slerpFlat(e, t, i, n, r, a, o) {
    let l = i[n + 0], c = i[n + 1], h = i[n + 2], u = i[n + 3];
    const d = r[a + 0], f = r[a + 1], p = r[a + 2], v = r[a + 3];
    if (o === 0) {
      e[t + 0] = l, e[t + 1] = c, e[t + 2] = h, e[t + 3] = u;
      return;
    }
    if (o === 1) {
      e[t + 0] = d, e[t + 1] = f, e[t + 2] = p, e[t + 3] = v;
      return;
    }
    if (u !== v || l !== d || c !== f || h !== p) {
      let g = 1 - o;
      const m = l * d + c * f + h * p + u * v, x = m >= 0 ? 1 : -1, y = 1 - m * m;
      if (y > Number.EPSILON) {
        const b = Math.sqrt(y), M = Math.atan2(b, m * x);
        g = Math.sin(g * M) / b, o = Math.sin(o * M) / b;
      }
      const A = o * x;
      if (l = l * g + d * A, c = c * g + f * A, h = h * g + p * A, u = u * g + v * A, g === 1 - o) {
        const b = 1 / Math.sqrt(l * l + c * c + h * h + u * u);
        l *= b, c *= b, h *= b, u *= b;
      }
    }
    e[t] = l, e[t + 1] = c, e[t + 2] = h, e[t + 3] = u;
  }
  static multiplyQuaternionsFlat(e, t, i, n, r, a) {
    const o = i[n], l = i[n + 1], c = i[n + 2], h = i[n + 3], u = r[a], d = r[a + 1], f = r[a + 2], p = r[a + 3];
    return e[t] = o * p + h * u + l * f - c * d, e[t + 1] = l * p + h * d + c * u - o * f, e[t + 2] = c * p + h * f + o * d - l * u, e[t + 3] = h * p - o * u - l * d - c * f, e;
  }
  get x() {
    return this._x;
  }
  set x(e) {
    this._x = e, this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(e) {
    this._y = e, this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(e) {
    this._z = e, this._onChangeCallback();
  }
  get w() {
    return this._w;
  }
  set w(e) {
    this._w = e, this._onChangeCallback();
  }
  set(e, t, i, n) {
    return this._x = e, this._y = t, this._z = i, this._w = n, this._onChangeCallback(), this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._w);
  }
  copy(e) {
    return this._x = e.x, this._y = e.y, this._z = e.z, this._w = e.w, this._onChangeCallback(), this;
  }
  setFromEuler(e, t = !0) {
    const i = e._x, n = e._y, r = e._z, a = e._order, o = Math.cos, l = Math.sin, c = o(i / 2), h = o(n / 2), u = o(r / 2), d = l(i / 2), f = l(n / 2), p = l(r / 2);
    switch (a) {
      case "XYZ":
        this._x = d * h * u + c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "YXZ":
        this._x = d * h * u + c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u + d * f * p;
        break;
      case "ZXY":
        this._x = d * h * u - c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "ZYX":
        this._x = d * h * u - c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u + d * f * p;
        break;
      case "YZX":
        this._x = d * h * u + c * f * p, this._y = c * f * u + d * h * p, this._z = c * h * p - d * f * u, this._w = c * h * u - d * f * p;
        break;
      case "XZY":
        this._x = d * h * u - c * f * p, this._y = c * f * u - d * h * p, this._z = c * h * p + d * f * u, this._w = c * h * u + d * f * p;
        break;
      default:
        console.warn("THREE.Quaternion: .setFromEuler() encountered an unknown order: " + a);
    }
    return t === !0 && this._onChangeCallback(), this;
  }
  setFromAxisAngle(e, t) {
    const i = t / 2, n = Math.sin(i);
    return this._x = e.x * n, this._y = e.y * n, this._z = e.z * n, this._w = Math.cos(i), this._onChangeCallback(), this;
  }
  setFromRotationMatrix(e) {
    const t = e.elements, i = t[0], n = t[4], r = t[8], a = t[1], o = t[5], l = t[9], c = t[2], h = t[6], u = t[10], d = i + o + u;
    if (d > 0) {
      const f = 0.5 / Math.sqrt(d + 1);
      this._w = 0.25 / f, this._x = (h - l) * f, this._y = (r - c) * f, this._z = (a - n) * f;
    } else if (i > o && i > u) {
      const f = 2 * Math.sqrt(1 + i - o - u);
      this._w = (h - l) / f, this._x = 0.25 * f, this._y = (n + a) / f, this._z = (r + c) / f;
    } else if (o > u) {
      const f = 2 * Math.sqrt(1 + o - i - u);
      this._w = (r - c) / f, this._x = (n + a) / f, this._y = 0.25 * f, this._z = (l + h) / f;
    } else {
      const f = 2 * Math.sqrt(1 + u - i - o);
      this._w = (a - n) / f, this._x = (r + c) / f, this._y = (l + h) / f, this._z = 0.25 * f;
    }
    return this._onChangeCallback(), this;
  }
  setFromUnitVectors(e, t) {
    let i = e.dot(t) + 1;
    return i < Number.EPSILON ? (i = 0, Math.abs(e.x) > Math.abs(e.z) ? (this._x = -e.y, this._y = e.x, this._z = 0, this._w = i) : (this._x = 0, this._y = -e.z, this._z = e.y, this._w = i)) : (this._x = e.y * t.z - e.z * t.y, this._y = e.z * t.x - e.x * t.z, this._z = e.x * t.y - e.y * t.x, this._w = i), this.normalize();
  }
  angleTo(e) {
    return 2 * Math.acos(Math.abs(tr(this.dot(e), -1, 1)));
  }
  rotateTowards(e, t) {
    const i = this.angleTo(e);
    if (i === 0)
      return this;
    const n = Math.min(1, t / i);
    return this.slerp(e, n), this;
  }
  identity() {
    return this.set(0, 0, 0, 1);
  }
  invert() {
    return this.conjugate();
  }
  conjugate() {
    return this._x *= -1, this._y *= -1, this._z *= -1, this._onChangeCallback(), this;
  }
  dot(e) {
    return this._x * e._x + this._y * e._y + this._z * e._z + this._w * e._w;
  }
  lengthSq() {
    return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;
  }
  length() {
    return Math.sqrt(this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w);
  }
  normalize() {
    let e = this.length();
    return e === 0 ? (this._x = 0, this._y = 0, this._z = 0, this._w = 1) : (e = 1 / e, this._x = this._x * e, this._y = this._y * e, this._z = this._z * e, this._w = this._w * e), this._onChangeCallback(), this;
  }
  multiply(e) {
    return this.multiplyQuaternions(this, e);
  }
  premultiply(e) {
    return this.multiplyQuaternions(e, this);
  }
  multiplyQuaternions(e, t) {
    const i = e._x, n = e._y, r = e._z, a = e._w, o = t._x, l = t._y, c = t._z, h = t._w;
    return this._x = i * h + a * o + n * c - r * l, this._y = n * h + a * l + r * o - i * c, this._z = r * h + a * c + i * l - n * o, this._w = a * h - i * o - n * l - r * c, this._onChangeCallback(), this;
  }
  slerp(e, t) {
    if (t === 0)
      return this;
    if (t === 1)
      return this.copy(e);
    const i = this._x, n = this._y, r = this._z, a = this._w;
    let o = a * e._w + i * e._x + n * e._y + r * e._z;
    if (o < 0 ? (this._w = -e._w, this._x = -e._x, this._y = -e._y, this._z = -e._z, o = -o) : this.copy(e), o >= 1)
      return this._w = a, this._x = i, this._y = n, this._z = r, this;
    const l = 1 - o * o;
    if (l <= Number.EPSILON) {
      const f = 1 - t;
      return this._w = f * a + t * this._w, this._x = f * i + t * this._x, this._y = f * n + t * this._y, this._z = f * r + t * this._z, this.normalize(), this;
    }
    const c = Math.sqrt(l), h = Math.atan2(c, o), u = Math.sin((1 - t) * h) / c, d = Math.sin(t * h) / c;
    return this._w = a * u + this._w * d, this._x = i * u + this._x * d, this._y = n * u + this._y * d, this._z = r * u + this._z * d, this._onChangeCallback(), this;
  }
  slerpQuaternions(e, t, i) {
    return this.copy(e).slerp(t, i);
  }
  random() {
    const e = 2 * Math.PI * Math.random(), t = 2 * Math.PI * Math.random(), i = Math.random(), n = Math.sqrt(1 - i), r = Math.sqrt(i);
    return this.set(
      n * Math.sin(e),
      n * Math.cos(e),
      r * Math.sin(t),
      r * Math.cos(t)
    );
  }
  equals(e) {
    return e._x === this._x && e._y === this._y && e._z === this._z && e._w === this._w;
  }
  fromArray(e, t = 0) {
    return this._x = e[t], this._y = e[t + 1], this._z = e[t + 2], this._w = e[t + 3], this._onChangeCallback(), this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this._x, e[t + 1] = this._y, e[t + 2] = this._z, e[t + 3] = this._w, e;
  }
  fromBufferAttribute(e, t) {
    return this._x = e.getX(t), this._y = e.getY(t), this._z = e.getZ(t), this._w = e.getW(t), this._onChangeCallback(), this;
  }
  toJSON() {
    return this.toArray();
  }
  _onChange(e) {
    return this._onChangeCallback = e, this;
  }
  _onChangeCallback() {
  }
  *[Symbol.iterator]() {
    yield this._x, yield this._y, yield this._z, yield this._w;
  }
}
class je {
  constructor(e = 0, t = 0, i = 0) {
    je.prototype.isVector3 = !0, this.x = e, this.y = t, this.z = i;
  }
  set(e, t, i) {
    return i === void 0 && (i = this.z), this.x = e, this.y = t, this.z = i, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this.z = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setZ(e) {
    return this.z = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      case 2:
        this.z = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this.z = e.z, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this.z += e.z, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this.z += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this.z = e.z + t.z, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this.z += e.z * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this.z -= e.z, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this.z -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this.z = e.z - t.z, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this.z *= e.z, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this.z *= e, this;
  }
  multiplyVectors(e, t) {
    return this.x = e.x * t.x, this.y = e.y * t.y, this.z = e.z * t.z, this;
  }
  applyEuler(e) {
    return this.applyQuaternion(AR.setFromEuler(e));
  }
  applyAxisAngle(e, t) {
    return this.applyQuaternion(AR.setFromAxisAngle(e, t));
  }
  applyMatrix3(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements;
    return this.x = r[0] * t + r[3] * i + r[6] * n, this.y = r[1] * t + r[4] * i + r[7] * n, this.z = r[2] * t + r[5] * i + r[8] * n, this;
  }
  applyNormalMatrix(e) {
    return this.applyMatrix3(e).normalize();
  }
  applyMatrix4(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements, a = 1 / (r[3] * t + r[7] * i + r[11] * n + r[15]);
    return this.x = (r[0] * t + r[4] * i + r[8] * n + r[12]) * a, this.y = (r[1] * t + r[5] * i + r[9] * n + r[13]) * a, this.z = (r[2] * t + r[6] * i + r[10] * n + r[14]) * a, this;
  }
  applyQuaternion(e) {
    const t = this.x, i = this.y, n = this.z, r = e.x, a = e.y, o = e.z, l = e.w, c = 2 * (a * n - o * i), h = 2 * (o * t - r * n), u = 2 * (r * i - a * t);
    return this.x = t + l * c + a * u - o * h, this.y = i + l * h + o * c - r * u, this.z = n + l * u + r * h - a * c, this;
  }
  project(e) {
    return this.applyMatrix4(e.matrixWorldInverse).applyMatrix4(e.projectionMatrix);
  }
  unproject(e) {
    return this.applyMatrix4(e.projectionMatrixInverse).applyMatrix4(e.matrixWorld);
  }
  transformDirection(e) {
    const t = this.x, i = this.y, n = this.z, r = e.elements;
    return this.x = r[0] * t + r[4] * i + r[8] * n, this.y = r[1] * t + r[5] * i + r[9] * n, this.z = r[2] * t + r[6] * i + r[10] * n, this.normalize();
  }
  divide(e) {
    return this.x /= e.x, this.y /= e.y, this.z /= e.z, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this.z = Math.min(this.z, e.z), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this.z = Math.max(this.z, e.z), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this.z = Math.max(e.z, Math.min(t.z, this.z)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this.z = Math.max(e, Math.min(t, this.z)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this.z = Math.floor(this.z), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this.z = Math.ceil(this.z), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this.z = Math.round(this.z), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this.z = Math.trunc(this.z), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this.z = -this.z, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y + this.z * e.z;
  }
  // TODO lengthSquared?
  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this.z += (e.z - this.z) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this.z = e.z + (t.z - e.z) * i, this;
  }
  cross(e) {
    return this.crossVectors(this, e);
  }
  crossVectors(e, t) {
    const i = e.x, n = e.y, r = e.z, a = t.x, o = t.y, l = t.z;
    return this.x = n * l - r * o, this.y = r * a - i * l, this.z = i * o - n * a, this;
  }
  projectOnVector(e) {
    const t = e.lengthSq();
    if (t === 0)
      return this.set(0, 0, 0);
    const i = e.dot(this) / t;
    return this.copy(e).multiplyScalar(i);
  }
  projectOnPlane(e) {
    return JA.copy(this).projectOnVector(e), this.sub(JA);
  }
  reflect(e) {
    return this.sub(JA.copy(e).multiplyScalar(2 * this.dot(e)));
  }
  angleTo(e) {
    const t = Math.sqrt(this.lengthSq() * e.lengthSq());
    if (t === 0)
      return Math.PI / 2;
    const i = this.dot(e) / t;
    return Math.acos(tr(i, -1, 1));
  }
  distanceTo(e) {
    return Math.sqrt(this.distanceToSquared(e));
  }
  distanceToSquared(e) {
    const t = this.x - e.x, i = this.y - e.y, n = this.z - e.z;
    return t * t + i * i + n * n;
  }
  manhattanDistanceTo(e) {
    return Math.abs(this.x - e.x) + Math.abs(this.y - e.y) + Math.abs(this.z - e.z);
  }
  setFromSpherical(e) {
    return this.setFromSphericalCoords(e.radius, e.phi, e.theta);
  }
  setFromSphericalCoords(e, t, i) {
    const n = Math.sin(t) * e;
    return this.x = n * Math.sin(i), this.y = Math.cos(t) * e, this.z = n * Math.cos(i), this;
  }
  setFromCylindrical(e) {
    return this.setFromCylindricalCoords(e.radius, e.theta, e.y);
  }
  setFromCylindricalCoords(e, t, i) {
    return this.x = e * Math.sin(t), this.y = i, this.z = e * Math.cos(t), this;
  }
  setFromMatrixPosition(e) {
    const t = e.elements;
    return this.x = t[12], this.y = t[13], this.z = t[14], this;
  }
  setFromMatrixScale(e) {
    const t = this.setFromMatrixColumn(e, 0).length(), i = this.setFromMatrixColumn(e, 1).length(), n = this.setFromMatrixColumn(e, 2).length();
    return this.x = t, this.y = i, this.z = n, this;
  }
  setFromMatrixColumn(e, t) {
    return this.fromArray(e.elements, t * 4);
  }
  setFromMatrix3Column(e, t) {
    return this.fromArray(e.elements, t * 3);
  }
  setFromEuler(e) {
    return this.x = e._x, this.y = e._y, this.z = e._z, this;
  }
  setFromColor(e) {
    return this.x = e.r, this.y = e.g, this.z = e.b, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y && e.z === this.z;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this.z = e[t + 2], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e[t + 2] = this.z, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this.z = e.getZ(t), this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this.z = Math.random(), this;
  }
  randomDirection() {
    const e = Math.random() * Math.PI * 2, t = Math.random() * 2 - 1, i = Math.sqrt(1 - t * t);
    return this.x = i * Math.cos(e), this.y = t, this.z = i * Math.sin(e), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y, yield this.z;
  }
}
const JA = /* @__PURE__ */ new je(), AR = /* @__PURE__ */ new H2();
class V2 {
  constructor(e = new je(1 / 0, 1 / 0, 1 / 0), t = new je(-1 / 0, -1 / 0, -1 / 0)) {
    this.isBox3 = !0, this.min = e, this.max = t;
  }
  set(e, t) {
    return this.min.copy(e), this.max.copy(t), this;
  }
  setFromArray(e) {
    this.makeEmpty();
    for (let t = 0, i = e.length; t < i; t += 3)
      this.expandByPoint(za.fromArray(e, t));
    return this;
  }
  setFromBufferAttribute(e) {
    this.makeEmpty();
    for (let t = 0, i = e.count; t < i; t++)
      this.expandByPoint(za.fromBufferAttribute(e, t));
    return this;
  }
  setFromPoints(e) {
    this.makeEmpty();
    for (let t = 0, i = e.length; t < i; t++)
      this.expandByPoint(e[t]);
    return this;
  }
  setFromCenterAndSize(e, t) {
    const i = za.copy(t).multiplyScalar(0.5);
    return this.min.copy(e).sub(i), this.max.copy(e).add(i), this;
  }
  setFromObject(e, t = !1) {
    return this.makeEmpty(), this.expandByObject(e, t);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.min.copy(e.min), this.max.copy(e.max), this;
  }
  makeEmpty() {
    return this.min.x = this.min.y = this.min.z = 1 / 0, this.max.x = this.max.y = this.max.z = -1 / 0, this;
  }
  isEmpty() {
    return this.max.x < this.min.x || this.max.y < this.min.y || this.max.z < this.min.z;
  }
  getCenter(e) {
    return this.isEmpty() ? e.set(0, 0, 0) : e.addVectors(this.min, this.max).multiplyScalar(0.5);
  }
  getSize(e) {
    return this.isEmpty() ? e.set(0, 0, 0) : e.subVectors(this.max, this.min);
  }
  expandByPoint(e) {
    return this.min.min(e), this.max.max(e), this;
  }
  expandByVector(e) {
    return this.min.sub(e), this.max.add(e), this;
  }
  expandByScalar(e) {
    return this.min.addScalar(-e), this.max.addScalar(e), this;
  }
  expandByObject(e, t = !1) {
    e.updateWorldMatrix(!1, !1);
    const i = e.geometry;
    if (i !== void 0) {
      const r = i.getAttribute("position");
      if (t === !0 && r !== void 0 && e.isInstancedMesh !== !0)
        for (let a = 0, o = r.count; a < o; a++)
          e.isMesh === !0 ? e.getVertexPosition(a, za) : za.fromBufferAttribute(r, a), za.applyMatrix4(e.matrixWorld), this.expandByPoint(za);
      else
        e.boundingBox !== void 0 ? (e.boundingBox === null && e.computeBoundingBox(), N3.copy(e.boundingBox)) : (i.boundingBox === null && i.computeBoundingBox(), N3.copy(i.boundingBox)), N3.applyMatrix4(e.matrixWorld), this.union(N3);
    }
    const n = e.children;
    for (let r = 0, a = n.length; r < a; r++)
      this.expandByObject(n[r], t);
    return this;
  }
  containsPoint(e) {
    return !(e.x < this.min.x || e.x > this.max.x || e.y < this.min.y || e.y > this.max.y || e.z < this.min.z || e.z > this.max.z);
  }
  containsBox(e) {
    return this.min.x <= e.min.x && e.max.x <= this.max.x && this.min.y <= e.min.y && e.max.y <= this.max.y && this.min.z <= e.min.z && e.max.z <= this.max.z;
  }
  getParameter(e, t) {
    return t.set(
      (e.x - this.min.x) / (this.max.x - this.min.x),
      (e.y - this.min.y) / (this.max.y - this.min.y),
      (e.z - this.min.z) / (this.max.z - this.min.z)
    );
  }
  intersectsBox(e) {
    return !(e.max.x < this.min.x || e.min.x > this.max.x || e.max.y < this.min.y || e.min.y > this.max.y || e.max.z < this.min.z || e.min.z > this.max.z);
  }
  intersectsSphere(e) {
    return this.clampPoint(e.center, za), za.distanceToSquared(e.center) <= e.radius * e.radius;
  }
  intersectsPlane(e) {
    let t, i;
    return e.normal.x > 0 ? (t = e.normal.x * this.min.x, i = e.normal.x * this.max.x) : (t = e.normal.x * this.max.x, i = e.normal.x * this.min.x), e.normal.y > 0 ? (t += e.normal.y * this.min.y, i += e.normal.y * this.max.y) : (t += e.normal.y * this.max.y, i += e.normal.y * this.min.y), e.normal.z > 0 ? (t += e.normal.z * this.min.z, i += e.normal.z * this.max.z) : (t += e.normal.z * this.max.z, i += e.normal.z * this.min.z), t <= -e.constant && i >= -e.constant;
  }
  intersectsTriangle(e) {
    if (this.isEmpty())
      return !1;
    this.getCenter(qm), B3.subVectors(this.max, qm), Gd.subVectors(e.a, qm), Hd.subVectors(e.b, qm), Vd.subVectors(e.c, qm), Kl.subVectors(Hd, Gd), Jl.subVectors(Vd, Hd), Fh.subVectors(Gd, Vd);
    let t = [
      0,
      -Kl.z,
      Kl.y,
      0,
      -Jl.z,
      Jl.y,
      0,
      -Fh.z,
      Fh.y,
      Kl.z,
      0,
      -Kl.x,
      Jl.z,
      0,
      -Jl.x,
      Fh.z,
      0,
      -Fh.x,
      -Kl.y,
      Kl.x,
      0,
      -Jl.y,
      Jl.x,
      0,
      -Fh.y,
      Fh.x,
      0
    ];
    return !QA(t, Gd, Hd, Vd, B3) || (t = [1, 0, 0, 0, 1, 0, 0, 0, 1], !QA(t, Gd, Hd, Vd, B3)) ? !1 : (F3.crossVectors(Kl, Jl), t = [F3.x, F3.y, F3.z], QA(t, Gd, Hd, Vd, B3));
  }
  clampPoint(e, t) {
    return t.copy(e).clamp(this.min, this.max);
  }
  distanceToPoint(e) {
    return this.clampPoint(e, za).distanceTo(e);
  }
  getBoundingSphere(e) {
    return this.isEmpty() ? e.makeEmpty() : (this.getCenter(e.center), e.radius = this.getSize(za).length() * 0.5), e;
  }
  intersect(e) {
    return this.min.max(e.min), this.max.min(e.max), this.isEmpty() && this.makeEmpty(), this;
  }
  union(e) {
    return this.min.min(e.min), this.max.max(e.max), this;
  }
  applyMatrix4(e) {
    return this.isEmpty() ? this : (I0[0].set(this.min.x, this.min.y, this.min.z).applyMatrix4(e), I0[1].set(this.min.x, this.min.y, this.max.z).applyMatrix4(e), I0[2].set(this.min.x, this.max.y, this.min.z).applyMatrix4(e), I0[3].set(this.min.x, this.max.y, this.max.z).applyMatrix4(e), I0[4].set(this.max.x, this.min.y, this.min.z).applyMatrix4(e), I0[5].set(this.max.x, this.min.y, this.max.z).applyMatrix4(e), I0[6].set(this.max.x, this.max.y, this.min.z).applyMatrix4(e), I0[7].set(this.max.x, this.max.y, this.max.z).applyMatrix4(e), this.setFromPoints(I0), this);
  }
  translate(e) {
    return this.min.add(e), this.max.add(e), this;
  }
  equals(e) {
    return e.min.equals(this.min) && e.max.equals(this.max);
  }
}
const I0 = [
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je(),
  /* @__PURE__ */ new je()
], za = /* @__PURE__ */ new je(), N3 = /* @__PURE__ */ new V2(), Gd = /* @__PURE__ */ new je(), Hd = /* @__PURE__ */ new je(), Vd = /* @__PURE__ */ new je(), Kl = /* @__PURE__ */ new je(), Jl = /* @__PURE__ */ new je(), Fh = /* @__PURE__ */ new je(), qm = /* @__PURE__ */ new je(), B3 = /* @__PURE__ */ new je(), F3 = /* @__PURE__ */ new je(), Uh = /* @__PURE__ */ new je();
function QA(s, e, t, i, n) {
  for (let r = 0, a = s.length - 3; r <= a; r += 3) {
    Uh.fromArray(s, r);
    const o = n.x * Math.abs(Uh.x) + n.y * Math.abs(Uh.y) + n.z * Math.abs(Uh.z), l = e.dot(Uh), c = t.dot(Uh), h = i.dot(Uh);
    if (Math.max(-Math.max(l, c, h), Math.min(l, c, h)) > o)
      return !1;
  }
  return !0;
}
const Dve = /* @__PURE__ */ new V2(), Ym = /* @__PURE__ */ new je(), $A = /* @__PURE__ */ new je();
class Nw {
  constructor(e = new je(), t = -1) {
    this.isSphere = !0, this.center = e, this.radius = t;
  }
  set(e, t) {
    return this.center.copy(e), this.radius = t, this;
  }
  setFromPoints(e, t) {
    const i = this.center;
    t !== void 0 ? i.copy(t) : Dve.setFromPoints(e).getCenter(i);
    let n = 0;
    for (let r = 0, a = e.length; r < a; r++)
      n = Math.max(n, i.distanceToSquared(e[r]));
    return this.radius = Math.sqrt(n), this;
  }
  copy(e) {
    return this.center.copy(e.center), this.radius = e.radius, this;
  }
  isEmpty() {
    return this.radius < 0;
  }
  makeEmpty() {
    return this.center.set(0, 0, 0), this.radius = -1, this;
  }
  containsPoint(e) {
    return e.distanceToSquared(this.center) <= this.radius * this.radius;
  }
  distanceToPoint(e) {
    return e.distanceTo(this.center) - this.radius;
  }
  intersectsSphere(e) {
    const t = this.radius + e.radius;
    return e.center.distanceToSquared(this.center) <= t * t;
  }
  intersectsBox(e) {
    return e.intersectsSphere(this);
  }
  intersectsPlane(e) {
    return Math.abs(e.distanceToPoint(this.center)) <= this.radius;
  }
  clampPoint(e, t) {
    const i = this.center.distanceToSquared(e);
    return t.copy(e), i > this.radius * this.radius && (t.sub(this.center).normalize(), t.multiplyScalar(this.radius).add(this.center)), t;
  }
  getBoundingBox(e) {
    return this.isEmpty() ? (e.makeEmpty(), e) : (e.set(this.center, this.center), e.expandByScalar(this.radius), e);
  }
  applyMatrix4(e) {
    return this.center.applyMatrix4(e), this.radius = this.radius * e.getMaxScaleOnAxis(), this;
  }
  translate(e) {
    return this.center.add(e), this;
  }
  expandByPoint(e) {
    if (this.isEmpty())
      return this.center.copy(e), this.radius = 0, this;
    Ym.subVectors(e, this.center);
    const t = Ym.lengthSq();
    if (t > this.radius * this.radius) {
      const i = Math.sqrt(t), n = (i - this.radius) * 0.5;
      this.center.addScaledVector(Ym, n / i), this.radius += n;
    }
    return this;
  }
  union(e) {
    return e.isEmpty() ? this : this.isEmpty() ? (this.copy(e), this) : (this.center.equals(e.center) === !0 ? this.radius = Math.max(this.radius, e.radius) : ($A.subVectors(e.center, this.center).setLength(e.radius), this.expandByPoint(Ym.copy(e.center).add($A)), this.expandByPoint(Ym.copy(e.center).sub($A))), this);
  }
  equals(e) {
    return e.center.equals(this.center) && e.radius === this.radius;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const e6 = /* @__PURE__ */ new je(), Lve = /* @__PURE__ */ new je(), Ive = /* @__PURE__ */ new xi();
class $h {
  constructor(e = new je(1, 0, 0), t = 0) {
    this.isPlane = !0, this.normal = e, this.constant = t;
  }
  set(e, t) {
    return this.normal.copy(e), this.constant = t, this;
  }
  setComponents(e, t, i, n) {
    return this.normal.set(e, t, i), this.constant = n, this;
  }
  setFromNormalAndCoplanarPoint(e, t) {
    return this.normal.copy(e), this.constant = -t.dot(this.normal), this;
  }
  setFromCoplanarPoints(e, t, i) {
    const n = e6.subVectors(i, t).cross(Lve.subVectors(e, t)).normalize();
    return this.setFromNormalAndCoplanarPoint(n, e), this;
  }
  copy(e) {
    return this.normal.copy(e.normal), this.constant = e.constant, this;
  }
  normalize() {
    const e = 1 / this.normal.length();
    return this.normal.multiplyScalar(e), this.constant *= e, this;
  }
  negate() {
    return this.constant *= -1, this.normal.negate(), this;
  }
  distanceToPoint(e) {
    return this.normal.dot(e) + this.constant;
  }
  distanceToSphere(e) {
    return this.distanceToPoint(e.center) - e.radius;
  }
  projectPoint(e, t) {
    return t.copy(e).addScaledVector(this.normal, -this.distanceToPoint(e));
  }
  intersectLine(e, t) {
    const i = e.delta(e6), n = this.normal.dot(i);
    if (n === 0)
      return this.distanceToPoint(e.start) === 0 ? t.copy(e.start) : null;
    const r = -(e.start.dot(this.normal) + this.constant) / n;
    return r < 0 || r > 1 ? null : t.copy(e.start).addScaledVector(i, r);
  }
  intersectsLine(e) {
    const t = this.distanceToPoint(e.start), i = this.distanceToPoint(e.end);
    return t < 0 && i > 0 || i < 0 && t > 0;
  }
  intersectsBox(e) {
    return e.intersectsPlane(this);
  }
  intersectsSphere(e) {
    return e.intersectsPlane(this);
  }
  coplanarPoint(e) {
    return e.copy(this.normal).multiplyScalar(-this.constant);
  }
  applyMatrix4(e, t) {
    const i = t || Ive.getNormalMatrix(e), n = this.coplanarPoint(e6).applyMatrix4(e), r = this.normal.applyMatrix3(i).normalize();
    return this.constant = -n.dot(r), this;
  }
  translate(e) {
    return this.constant -= e.dot(this.normal), this;
  }
  equals(e) {
    return e.normal.equals(this.normal) && e.constant === this.constant;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const zh = /* @__PURE__ */ new Nw(), U3 = /* @__PURE__ */ new je();
class rU {
  constructor(e = new $h(), t = new $h(), i = new $h(), n = new $h(), r = new $h(), a = new $h()) {
    this.planes = [e, t, i, n, r, a];
  }
  set(e, t, i, n, r, a) {
    const o = this.planes;
    return o[0].copy(e), o[1].copy(t), o[2].copy(i), o[3].copy(n), o[4].copy(r), o[5].copy(a), this;
  }
  copy(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++)
      t[i].copy(e.planes[i]);
    return this;
  }
  setFromProjectionMatrix(e, t = rl) {
    const i = this.planes, n = e.elements, r = n[0], a = n[1], o = n[2], l = n[3], c = n[4], h = n[5], u = n[6], d = n[7], f = n[8], p = n[9], v = n[10], g = n[11], m = n[12], x = n[13], y = n[14], A = n[15];
    if (i[0].setComponents(l - r, d - c, g - f, A - m).normalize(), i[1].setComponents(l + r, d + c, g + f, A + m).normalize(), i[2].setComponents(l + a, d + h, g + p, A + x).normalize(), i[3].setComponents(l - a, d - h, g - p, A - x).normalize(), i[4].setComponents(l - o, d - u, g - v, A - y).normalize(), t === rl)
      i[5].setComponents(l + o, d + u, g + v, A + y).normalize();
    else if (t === jx)
      i[5].setComponents(o, u, v, y).normalize();
    else
      throw new Error("THREE.Frustum.setFromProjectionMatrix(): Invalid coordinate system: " + t);
    return this;
  }
  intersectsObject(e) {
    if (e.boundingSphere !== void 0)
      e.boundingSphere === null && e.computeBoundingSphere(), zh.copy(e.boundingSphere).applyMatrix4(e.matrixWorld);
    else {
      const t = e.geometry;
      t.boundingSphere === null && t.computeBoundingSphere(), zh.copy(t.boundingSphere).applyMatrix4(e.matrixWorld);
    }
    return this.intersectsSphere(zh);
  }
  intersectsSprite(e) {
    return zh.center.set(0, 0, 0), zh.radius = 0.7071067811865476, zh.applyMatrix4(e.matrixWorld), this.intersectsSphere(zh);
  }
  intersectsSphere(e) {
    const t = this.planes, i = e.center, n = -e.radius;
    for (let r = 0; r < 6; r++)
      if (t[r].distanceToPoint(i) < n)
        return !1;
    return !0;
  }
  intersectsBox(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++) {
      const n = t[i];
      if (U3.x = n.normal.x > 0 ? e.max.x : e.min.x, U3.y = n.normal.y > 0 ? e.max.y : e.min.y, U3.z = n.normal.z > 0 ? e.max.z : e.min.z, n.distanceToPoint(U3) < 0)
        return !1;
    }
    return !0;
  }
  containsPoint(e) {
    const t = this.planes;
    for (let i = 0; i < 6; i++)
      if (t[i].distanceToPoint(e) < 0)
        return !1;
    return !0;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
class ps {
  constructor(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g) {
    ps.prototype.isMatrix4 = !0, this.elements = [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ], e !== void 0 && this.set(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g);
  }
  set(e, t, i, n, r, a, o, l, c, h, u, d, f, p, v, g) {
    const m = this.elements;
    return m[0] = e, m[4] = t, m[8] = i, m[12] = n, m[1] = r, m[5] = a, m[9] = o, m[13] = l, m[2] = c, m[6] = h, m[10] = u, m[14] = d, m[3] = f, m[7] = p, m[11] = v, m[15] = g, this;
  }
  identity() {
    return this.set(
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  clone() {
    return new ps().fromArray(this.elements);
  }
  copy(e) {
    const t = this.elements, i = e.elements;
    return t[0] = i[0], t[1] = i[1], t[2] = i[2], t[3] = i[3], t[4] = i[4], t[5] = i[5], t[6] = i[6], t[7] = i[7], t[8] = i[8], t[9] = i[9], t[10] = i[10], t[11] = i[11], t[12] = i[12], t[13] = i[13], t[14] = i[14], t[15] = i[15], this;
  }
  copyPosition(e) {
    const t = this.elements, i = e.elements;
    return t[12] = i[12], t[13] = i[13], t[14] = i[14], this;
  }
  setFromMatrix3(e) {
    const t = e.elements;
    return this.set(
      t[0],
      t[3],
      t[6],
      0,
      t[1],
      t[4],
      t[7],
      0,
      t[2],
      t[5],
      t[8],
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  extractBasis(e, t, i) {
    return e.setFromMatrixColumn(this, 0), t.setFromMatrixColumn(this, 1), i.setFromMatrixColumn(this, 2), this;
  }
  makeBasis(e, t, i) {
    return this.set(
      e.x,
      t.x,
      i.x,
      0,
      e.y,
      t.y,
      i.y,
      0,
      e.z,
      t.z,
      i.z,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  extractRotation(e) {
    const t = this.elements, i = e.elements, n = 1 / Wd.setFromMatrixColumn(e, 0).length(), r = 1 / Wd.setFromMatrixColumn(e, 1).length(), a = 1 / Wd.setFromMatrixColumn(e, 2).length();
    return t[0] = i[0] * n, t[1] = i[1] * n, t[2] = i[2] * n, t[3] = 0, t[4] = i[4] * r, t[5] = i[5] * r, t[6] = i[6] * r, t[7] = 0, t[8] = i[8] * a, t[9] = i[9] * a, t[10] = i[10] * a, t[11] = 0, t[12] = 0, t[13] = 0, t[14] = 0, t[15] = 1, this;
  }
  makeRotationFromEuler(e) {
    const t = this.elements, i = e.x, n = e.y, r = e.z, a = Math.cos(i), o = Math.sin(i), l = Math.cos(n), c = Math.sin(n), h = Math.cos(r), u = Math.sin(r);
    if (e.order === "XYZ") {
      const d = a * h, f = a * u, p = o * h, v = o * u;
      t[0] = l * h, t[4] = -l * u, t[8] = c, t[1] = f + p * c, t[5] = d - v * c, t[9] = -o * l, t[2] = v - d * c, t[6] = p + f * c, t[10] = a * l;
    } else if (e.order === "YXZ") {
      const d = l * h, f = l * u, p = c * h, v = c * u;
      t[0] = d + v * o, t[4] = p * o - f, t[8] = a * c, t[1] = a * u, t[5] = a * h, t[9] = -o, t[2] = f * o - p, t[6] = v + d * o, t[10] = a * l;
    } else if (e.order === "ZXY") {
      const d = l * h, f = l * u, p = c * h, v = c * u;
      t[0] = d - v * o, t[4] = -a * u, t[8] = p + f * o, t[1] = f + p * o, t[5] = a * h, t[9] = v - d * o, t[2] = -a * c, t[6] = o, t[10] = a * l;
    } else if (e.order === "ZYX") {
      const d = a * h, f = a * u, p = o * h, v = o * u;
      t[0] = l * h, t[4] = p * c - f, t[8] = d * c + v, t[1] = l * u, t[5] = v * c + d, t[9] = f * c - p, t[2] = -c, t[6] = o * l, t[10] = a * l;
    } else if (e.order === "YZX") {
      const d = a * l, f = a * c, p = o * l, v = o * c;
      t[0] = l * h, t[4] = v - d * u, t[8] = p * u + f, t[1] = u, t[5] = a * h, t[9] = -o * h, t[2] = -c * h, t[6] = f * u + p, t[10] = d - v * u;
    } else if (e.order === "XZY") {
      const d = a * l, f = a * c, p = o * l, v = o * c;
      t[0] = l * h, t[4] = -u, t[8] = c * h, t[1] = d * u + v, t[5] = a * h, t[9] = f * u - p, t[2] = p * u - f, t[6] = o * h, t[10] = v * u + d;
    }
    return t[3] = 0, t[7] = 0, t[11] = 0, t[12] = 0, t[13] = 0, t[14] = 0, t[15] = 1, this;
  }
  makeRotationFromQuaternion(e) {
    return this.compose(Ove, e, Nve);
  }
  lookAt(e, t, i) {
    const n = this.elements;
    return Xr.subVectors(e, t), Xr.lengthSq() === 0 && (Xr.z = 1), Xr.normalize(), Ql.crossVectors(i, Xr), Ql.lengthSq() === 0 && (Math.abs(i.z) === 1 ? Xr.x += 1e-4 : Xr.z += 1e-4, Xr.normalize(), Ql.crossVectors(i, Xr)), Ql.normalize(), z3.crossVectors(Xr, Ql), n[0] = Ql.x, n[4] = z3.x, n[8] = Xr.x, n[1] = Ql.y, n[5] = z3.y, n[9] = Xr.y, n[2] = Ql.z, n[6] = z3.z, n[10] = Xr.z, this;
  }
  multiply(e) {
    return this.multiplyMatrices(this, e);
  }
  premultiply(e) {
    return this.multiplyMatrices(e, this);
  }
  multiplyMatrices(e, t) {
    const i = e.elements, n = t.elements, r = this.elements, a = i[0], o = i[4], l = i[8], c = i[12], h = i[1], u = i[5], d = i[9], f = i[13], p = i[2], v = i[6], g = i[10], m = i[14], x = i[3], y = i[7], A = i[11], b = i[15], M = n[0], w = n[4], C = n[8], T = n[12], E = n[1], P = n[5], L = n[9], I = n[13], N = n[2], H = n[6], U = n[10], W = n[14], X = n[3], K = n[7], te = n[11], re = n[15];
    return r[0] = a * M + o * E + l * N + c * X, r[4] = a * w + o * P + l * H + c * K, r[8] = a * C + o * L + l * U + c * te, r[12] = a * T + o * I + l * W + c * re, r[1] = h * M + u * E + d * N + f * X, r[5] = h * w + u * P + d * H + f * K, r[9] = h * C + u * L + d * U + f * te, r[13] = h * T + u * I + d * W + f * re, r[2] = p * M + v * E + g * N + m * X, r[6] = p * w + v * P + g * H + m * K, r[10] = p * C + v * L + g * U + m * te, r[14] = p * T + v * I + g * W + m * re, r[3] = x * M + y * E + A * N + b * X, r[7] = x * w + y * P + A * H + b * K, r[11] = x * C + y * L + A * U + b * te, r[15] = x * T + y * I + A * W + b * re, this;
  }
  multiplyScalar(e) {
    const t = this.elements;
    return t[0] *= e, t[4] *= e, t[8] *= e, t[12] *= e, t[1] *= e, t[5] *= e, t[9] *= e, t[13] *= e, t[2] *= e, t[6] *= e, t[10] *= e, t[14] *= e, t[3] *= e, t[7] *= e, t[11] *= e, t[15] *= e, this;
  }
  determinant() {
    const e = this.elements, t = e[0], i = e[4], n = e[8], r = e[12], a = e[1], o = e[5], l = e[9], c = e[13], h = e[2], u = e[6], d = e[10], f = e[14], p = e[3], v = e[7], g = e[11], m = e[15];
    return p * (+r * l * u - n * c * u - r * o * d + i * c * d + n * o * f - i * l * f) + v * (+t * l * f - t * c * d + r * a * d - n * a * f + n * c * h - r * l * h) + g * (+t * c * u - t * o * f - r * a * u + i * a * f + r * o * h - i * c * h) + m * (-n * o * h - t * l * u + t * o * d + n * a * u - i * a * d + i * l * h);
  }
  transpose() {
    const e = this.elements;
    let t;
    return t = e[1], e[1] = e[4], e[4] = t, t = e[2], e[2] = e[8], e[8] = t, t = e[6], e[6] = e[9], e[9] = t, t = e[3], e[3] = e[12], e[12] = t, t = e[7], e[7] = e[13], e[13] = t, t = e[11], e[11] = e[14], e[14] = t, this;
  }
  setPosition(e, t, i) {
    const n = this.elements;
    return e.isVector3 ? (n[12] = e.x, n[13] = e.y, n[14] = e.z) : (n[12] = e, n[13] = t, n[14] = i), this;
  }
  invert() {
    const e = this.elements, t = e[0], i = e[1], n = e[2], r = e[3], a = e[4], o = e[5], l = e[6], c = e[7], h = e[8], u = e[9], d = e[10], f = e[11], p = e[12], v = e[13], g = e[14], m = e[15], x = u * g * c - v * d * c + v * l * f - o * g * f - u * l * m + o * d * m, y = p * d * c - h * g * c - p * l * f + a * g * f + h * l * m - a * d * m, A = h * v * c - p * u * c + p * o * f - a * v * f - h * o * m + a * u * m, b = p * u * l - h * v * l - p * o * d + a * v * d + h * o * g - a * u * g, M = t * x + i * y + n * A + r * b;
    if (M === 0)
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    const w = 1 / M;
    return e[0] = x * w, e[1] = (v * d * r - u * g * r - v * n * f + i * g * f + u * n * m - i * d * m) * w, e[2] = (o * g * r - v * l * r + v * n * c - i * g * c - o * n * m + i * l * m) * w, e[3] = (u * l * r - o * d * r - u * n * c + i * d * c + o * n * f - i * l * f) * w, e[4] = y * w, e[5] = (h * g * r - p * d * r + p * n * f - t * g * f - h * n * m + t * d * m) * w, e[6] = (p * l * r - a * g * r - p * n * c + t * g * c + a * n * m - t * l * m) * w, e[7] = (a * d * r - h * l * r + h * n * c - t * d * c - a * n * f + t * l * f) * w, e[8] = A * w, e[9] = (p * u * r - h * v * r - p * i * f + t * v * f + h * i * m - t * u * m) * w, e[10] = (a * v * r - p * o * r + p * i * c - t * v * c - a * i * m + t * o * m) * w, e[11] = (h * o * r - a * u * r - h * i * c + t * u * c + a * i * f - t * o * f) * w, e[12] = b * w, e[13] = (h * v * n - p * u * n + p * i * d - t * v * d - h * i * g + t * u * g) * w, e[14] = (p * o * n - a * v * n - p * i * l + t * v * l + a * i * g - t * o * g) * w, e[15] = (a * u * n - h * o * n + h * i * l - t * u * l - a * i * d + t * o * d) * w, this;
  }
  scale(e) {
    const t = this.elements, i = e.x, n = e.y, r = e.z;
    return t[0] *= i, t[4] *= n, t[8] *= r, t[1] *= i, t[5] *= n, t[9] *= r, t[2] *= i, t[6] *= n, t[10] *= r, t[3] *= i, t[7] *= n, t[11] *= r, this;
  }
  getMaxScaleOnAxis() {
    const e = this.elements, t = e[0] * e[0] + e[1] * e[1] + e[2] * e[2], i = e[4] * e[4] + e[5] * e[5] + e[6] * e[6], n = e[8] * e[8] + e[9] * e[9] + e[10] * e[10];
    return Math.sqrt(Math.max(t, i, n));
  }
  makeTranslation(e, t, i) {
    return e.isVector3 ? this.set(
      1,
      0,
      0,
      e.x,
      0,
      1,
      0,
      e.y,
      0,
      0,
      1,
      e.z,
      0,
      0,
      0,
      1
    ) : this.set(
      1,
      0,
      0,
      e,
      0,
      1,
      0,
      t,
      0,
      0,
      1,
      i,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationX(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      1,
      0,
      0,
      0,
      0,
      t,
      -i,
      0,
      0,
      i,
      t,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationY(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      0,
      i,
      0,
      0,
      1,
      0,
      0,
      -i,
      0,
      t,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationZ(e) {
    const t = Math.cos(e), i = Math.sin(e);
    return this.set(
      t,
      -i,
      0,
      0,
      i,
      t,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeRotationAxis(e, t) {
    const i = Math.cos(t), n = Math.sin(t), r = 1 - i, a = e.x, o = e.y, l = e.z, c = r * a, h = r * o;
    return this.set(
      c * a + i,
      c * o - n * l,
      c * l + n * o,
      0,
      c * o + n * l,
      h * o + i,
      h * l - n * a,
      0,
      c * l - n * o,
      h * l + n * a,
      r * l * l + i,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeScale(e, t, i) {
    return this.set(
      e,
      0,
      0,
      0,
      0,
      t,
      0,
      0,
      0,
      0,
      i,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  makeShear(e, t, i, n, r, a) {
    return this.set(
      1,
      i,
      r,
      0,
      e,
      1,
      a,
      0,
      t,
      n,
      1,
      0,
      0,
      0,
      0,
      1
    ), this;
  }
  compose(e, t, i) {
    const n = this.elements, r = t._x, a = t._y, o = t._z, l = t._w, c = r + r, h = a + a, u = o + o, d = r * c, f = r * h, p = r * u, v = a * h, g = a * u, m = o * u, x = l * c, y = l * h, A = l * u, b = i.x, M = i.y, w = i.z;
    return n[0] = (1 - (v + m)) * b, n[1] = (f + A) * b, n[2] = (p - y) * b, n[3] = 0, n[4] = (f - A) * M, n[5] = (1 - (d + m)) * M, n[6] = (g + x) * M, n[7] = 0, n[8] = (p + y) * w, n[9] = (g - x) * w, n[10] = (1 - (d + v)) * w, n[11] = 0, n[12] = e.x, n[13] = e.y, n[14] = e.z, n[15] = 1, this;
  }
  decompose(e, t, i) {
    const n = this.elements;
    let r = Wd.set(n[0], n[1], n[2]).length();
    const a = Wd.set(n[4], n[5], n[6]).length(), o = Wd.set(n[8], n[9], n[10]).length();
    this.determinant() < 0 && (r = -r), e.x = n[12], e.y = n[13], e.z = n[14], ka.copy(this);
    const c = 1 / r, h = 1 / a, u = 1 / o;
    return ka.elements[0] *= c, ka.elements[1] *= c, ka.elements[2] *= c, ka.elements[4] *= h, ka.elements[5] *= h, ka.elements[6] *= h, ka.elements[8] *= u, ka.elements[9] *= u, ka.elements[10] *= u, t.setFromRotationMatrix(ka), i.x = r, i.y = a, i.z = o, this;
  }
  makePerspective(e, t, i, n, r, a, o = rl) {
    const l = this.elements, c = 2 * r / (t - e), h = 2 * r / (i - n), u = (t + e) / (t - e), d = (i + n) / (i - n);
    let f, p;
    if (o === rl)
      f = -(a + r) / (a - r), p = -2 * a * r / (a - r);
    else if (o === jx)
      f = -a / (a - r), p = -a * r / (a - r);
    else
      throw new Error("THREE.Matrix4.makePerspective(): Invalid coordinate system: " + o);
    return l[0] = c, l[4] = 0, l[8] = u, l[12] = 0, l[1] = 0, l[5] = h, l[9] = d, l[13] = 0, l[2] = 0, l[6] = 0, l[10] = f, l[14] = p, l[3] = 0, l[7] = 0, l[11] = -1, l[15] = 0, this;
  }
  makeOrthographic(e, t, i, n, r, a, o = rl) {
    const l = this.elements, c = 1 / (t - e), h = 1 / (i - n), u = 1 / (a - r), d = (t + e) * c, f = (i + n) * h;
    let p, v;
    if (o === rl)
      p = (a + r) * u, v = -2 * u;
    else if (o === jx)
      p = r * u, v = -1 * u;
    else
      throw new Error("THREE.Matrix4.makeOrthographic(): Invalid coordinate system: " + o);
    return l[0] = 2 * c, l[4] = 0, l[8] = 0, l[12] = -d, l[1] = 0, l[5] = 2 * h, l[9] = 0, l[13] = -f, l[2] = 0, l[6] = 0, l[10] = v, l[14] = -p, l[3] = 0, l[7] = 0, l[11] = 0, l[15] = 1, this;
  }
  equals(e) {
    const t = this.elements, i = e.elements;
    for (let n = 0; n < 16; n++)
      if (t[n] !== i[n])
        return !1;
    return !0;
  }
  fromArray(e, t = 0) {
    for (let i = 0; i < 16; i++)
      this.elements[i] = e[i + t];
    return this;
  }
  toArray(e = [], t = 0) {
    const i = this.elements;
    return e[t] = i[0], e[t + 1] = i[1], e[t + 2] = i[2], e[t + 3] = i[3], e[t + 4] = i[4], e[t + 5] = i[5], e[t + 6] = i[6], e[t + 7] = i[7], e[t + 8] = i[8], e[t + 9] = i[9], e[t + 10] = i[10], e[t + 11] = i[11], e[t + 12] = i[12], e[t + 13] = i[13], e[t + 14] = i[14], e[t + 15] = i[15], e;
  }
}
const Wd = /* @__PURE__ */ new je(), ka = /* @__PURE__ */ new ps(), Ove = /* @__PURE__ */ new je(0, 0, 0), Nve = /* @__PURE__ */ new je(1, 1, 1), Ql = /* @__PURE__ */ new je(), z3 = /* @__PURE__ */ new je(), Xr = /* @__PURE__ */ new je();
class Cs {
  constructor(e = 0, t = 0, i = 0, n = 1) {
    Cs.prototype.isVector4 = !0, this.x = e, this.y = t, this.z = i, this.w = n;
  }
  get width() {
    return this.z;
  }
  set width(e) {
    this.z = e;
  }
  get height() {
    return this.w;
  }
  set height(e) {
    this.w = e;
  }
  set(e, t, i, n) {
    return this.x = e, this.y = t, this.z = i, this.w = n, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this.z = e, this.w = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setZ(e) {
    return this.z = e, this;
  }
  setW(e) {
    return this.w = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      case 2:
        this.z = t;
        break;
      case 3:
        this.w = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      case 3:
        return this.w;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z, this.w);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this.z = e.z, this.w = e.w !== void 0 ? e.w : 1, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this.z += e.z, this.w += e.w, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this.z += e, this.w += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this.z = e.z + t.z, this.w = e.w + t.w, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this.z += e.z * t, this.w += e.w * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this.z -= e.z, this.w -= e.w, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this.z -= e, this.w -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this.z = e.z - t.z, this.w = e.w - t.w, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this.z *= e.z, this.w *= e.w, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this.z *= e, this.w *= e, this;
  }
  applyMatrix4(e) {
    const t = this.x, i = this.y, n = this.z, r = this.w, a = e.elements;
    return this.x = a[0] * t + a[4] * i + a[8] * n + a[12] * r, this.y = a[1] * t + a[5] * i + a[9] * n + a[13] * r, this.z = a[2] * t + a[6] * i + a[10] * n + a[14] * r, this.w = a[3] * t + a[7] * i + a[11] * n + a[15] * r, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  setAxisAngleFromQuaternion(e) {
    this.w = 2 * Math.acos(e.w);
    const t = Math.sqrt(1 - e.w * e.w);
    return t < 1e-4 ? (this.x = 1, this.y = 0, this.z = 0) : (this.x = e.x / t, this.y = e.y / t, this.z = e.z / t), this;
  }
  setAxisAngleFromRotationMatrix(e) {
    let t, i, n, r;
    const l = e.elements, c = l[0], h = l[4], u = l[8], d = l[1], f = l[5], p = l[9], v = l[2], g = l[6], m = l[10];
    if (Math.abs(h - d) < 0.01 && Math.abs(u - v) < 0.01 && Math.abs(p - g) < 0.01) {
      if (Math.abs(h + d) < 0.1 && Math.abs(u + v) < 0.1 && Math.abs(p + g) < 0.1 && Math.abs(c + f + m - 3) < 0.1)
        return this.set(1, 0, 0, 0), this;
      t = Math.PI;
      const y = (c + 1) / 2, A = (f + 1) / 2, b = (m + 1) / 2, M = (h + d) / 4, w = (u + v) / 4, C = (p + g) / 4;
      return y > A && y > b ? y < 0.01 ? (i = 0, n = 0.707106781, r = 0.707106781) : (i = Math.sqrt(y), n = M / i, r = w / i) : A > b ? A < 0.01 ? (i = 0.707106781, n = 0, r = 0.707106781) : (n = Math.sqrt(A), i = M / n, r = C / n) : b < 0.01 ? (i = 0.707106781, n = 0.707106781, r = 0) : (r = Math.sqrt(b), i = w / r, n = C / r), this.set(i, n, r, t), this;
    }
    let x = Math.sqrt((g - p) * (g - p) + (u - v) * (u - v) + (d - h) * (d - h));
    return Math.abs(x) < 1e-3 && (x = 1), this.x = (g - p) / x, this.y = (u - v) / x, this.z = (d - h) / x, this.w = Math.acos((c + f + m - 1) / 2), this;
  }
  setFromMatrixPosition(e) {
    const t = e.elements;
    return this.x = t[12], this.y = t[13], this.z = t[14], this.w = t[15], this;
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this.z = Math.min(this.z, e.z), this.w = Math.min(this.w, e.w), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this.z = Math.max(this.z, e.z), this.w = Math.max(this.w, e.w), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this.z = Math.max(e.z, Math.min(t.z, this.z)), this.w = Math.max(e.w, Math.min(t.w, this.w)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this.z = Math.max(e, Math.min(t, this.z)), this.w = Math.max(e, Math.min(t, this.w)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this.z = Math.floor(this.z), this.w = Math.floor(this.w), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this.z = Math.ceil(this.z), this.w = Math.ceil(this.w), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this.z = Math.round(this.z), this.w = Math.round(this.w), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this.z = Math.trunc(this.z), this.w = Math.trunc(this.w), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this.z = -this.z, this.w = -this.w, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y + this.z * e.z + this.w * e.w;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z) + Math.abs(this.w);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this.z += (e.z - this.z) * t, this.w += (e.w - this.w) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this.z = e.z + (t.z - e.z) * i, this.w = e.w + (t.w - e.w) * i, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y && e.z === this.z && e.w === this.w;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this.z = e[t + 2], this.w = e[t + 3], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e[t + 2] = this.z, e[t + 3] = this.w, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this.z = e.getZ(t), this.w = e.getW(t), this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this.z = Math.random(), this.w = Math.random(), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y, yield this.z, yield this.w;
  }
}
function aU() {
  let s = null, e = !1, t = null, i = null;
  function n(r, a) {
    t(r, a), i = s.requestAnimationFrame(n);
  }
  return {
    start: function() {
      e !== !0 && t !== null && (i = s.requestAnimationFrame(n), e = !0);
    },
    stop: function() {
      s.cancelAnimationFrame(i), e = !1;
    },
    setAnimationLoop: function(r) {
      t = r;
    },
    setContext: function(r) {
      s = r;
    }
  };
}
function Bve(s) {
  const e = /* @__PURE__ */ new WeakMap();
  function t(o, l) {
    const c = o.array, h = o.usage, u = c.byteLength, d = s.createBuffer();
    s.bindBuffer(l, d), s.bufferData(l, c, h), o.onUploadCallback();
    let f;
    if (c instanceof Float32Array)
      f = s.FLOAT;
    else if (c instanceof Uint16Array)
      o.isFloat16BufferAttribute ? f = s.HALF_FLOAT : f = s.UNSIGNED_SHORT;
    else if (c instanceof Int16Array)
      f = s.SHORT;
    else if (c instanceof Uint32Array)
      f = s.UNSIGNED_INT;
    else if (c instanceof Int32Array)
      f = s.INT;
    else if (c instanceof Int8Array)
      f = s.BYTE;
    else if (c instanceof Uint8Array)
      f = s.UNSIGNED_BYTE;
    else if (c instanceof Uint8ClampedArray)
      f = s.UNSIGNED_BYTE;
    else
      throw new Error("THREE.WebGLAttributes: Unsupported buffer data format: " + c);
    return {
      buffer: d,
      type: f,
      bytesPerElement: c.BYTES_PER_ELEMENT,
      version: o.version,
      size: u
    };
  }
  function i(o, l, c) {
    const h = l.array, u = l._updateRange, d = l.updateRanges;
    if (s.bindBuffer(c, o), u.count === -1 && d.length === 0 && s.bufferSubData(c, 0, h), d.length !== 0) {
      for (let f = 0, p = d.length; f < p; f++) {
        const v = d[f];
        s.bufferSubData(
          c,
          v.start * h.BYTES_PER_ELEMENT,
          h,
          v.start,
          v.count
        );
      }
      l.clearUpdateRanges();
    }
    u.count !== -1 && (s.bufferSubData(
      c,
      u.offset * h.BYTES_PER_ELEMENT,
      h,
      u.offset,
      u.count
    ), u.count = -1), l.onUploadCallback();
  }
  function n(o) {
    return o.isInterleavedBufferAttribute && (o = o.data), e.get(o);
  }
  function r(o) {
    o.isInterleavedBufferAttribute && (o = o.data);
    const l = e.get(o);
    l && (s.deleteBuffer(l.buffer), e.delete(o));
  }
  function a(o, l) {
    if (o.isGLBufferAttribute) {
      const h = e.get(o);
      (!h || h.version < o.version) && e.set(o, {
        buffer: o.buffer,
        type: o.type,
        bytesPerElement: o.elementSize,
        version: o.version
      });
      return;
    }
    o.isInterleavedBufferAttribute && (o = o.data);
    const c = e.get(o);
    if (c === void 0)
      e.set(o, t(o, l));
    else if (c.version < o.version) {
      if (c.size !== o.array.byteLength)
        throw new Error("THREE.WebGLAttributes: The size of the buffer attribute's array buffer does not match the original size. Resizing buffer attributes is not supported.");
      i(c.buffer, o, l), c.version = o.version;
    }
  }
  return {
    get: n,
    remove: r,
    update: a
  };
}
class tn {
  constructor(e = 0, t = 0) {
    tn.prototype.isVector2 = !0, this.x = e, this.y = t;
  }
  get width() {
    return this.x;
  }
  set width(e) {
    this.x = e;
  }
  get height() {
    return this.y;
  }
  set height(e) {
    this.y = e;
  }
  set(e, t) {
    return this.x = e, this.y = t, this;
  }
  setScalar(e) {
    return this.x = e, this.y = e, this;
  }
  setX(e) {
    return this.x = e, this;
  }
  setY(e) {
    return this.y = e, this;
  }
  setComponent(e, t) {
    switch (e) {
      case 0:
        this.x = t;
        break;
      case 1:
        this.y = t;
        break;
      default:
        throw new Error("index is out of range: " + e);
    }
    return this;
  }
  getComponent(e) {
    switch (e) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      default:
        throw new Error("index is out of range: " + e);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y);
  }
  copy(e) {
    return this.x = e.x, this.y = e.y, this;
  }
  add(e) {
    return this.x += e.x, this.y += e.y, this;
  }
  addScalar(e) {
    return this.x += e, this.y += e, this;
  }
  addVectors(e, t) {
    return this.x = e.x + t.x, this.y = e.y + t.y, this;
  }
  addScaledVector(e, t) {
    return this.x += e.x * t, this.y += e.y * t, this;
  }
  sub(e) {
    return this.x -= e.x, this.y -= e.y, this;
  }
  subScalar(e) {
    return this.x -= e, this.y -= e, this;
  }
  subVectors(e, t) {
    return this.x = e.x - t.x, this.y = e.y - t.y, this;
  }
  multiply(e) {
    return this.x *= e.x, this.y *= e.y, this;
  }
  multiplyScalar(e) {
    return this.x *= e, this.y *= e, this;
  }
  divide(e) {
    return this.x /= e.x, this.y /= e.y, this;
  }
  divideScalar(e) {
    return this.multiplyScalar(1 / e);
  }
  applyMatrix3(e) {
    const t = this.x, i = this.y, n = e.elements;
    return this.x = n[0] * t + n[3] * i + n[6], this.y = n[1] * t + n[4] * i + n[7], this;
  }
  min(e) {
    return this.x = Math.min(this.x, e.x), this.y = Math.min(this.y, e.y), this;
  }
  max(e) {
    return this.x = Math.max(this.x, e.x), this.y = Math.max(this.y, e.y), this;
  }
  clamp(e, t) {
    return this.x = Math.max(e.x, Math.min(t.x, this.x)), this.y = Math.max(e.y, Math.min(t.y, this.y)), this;
  }
  clampScalar(e, t) {
    return this.x = Math.max(e, Math.min(t, this.x)), this.y = Math.max(e, Math.min(t, this.y)), this;
  }
  clampLength(e, t) {
    const i = this.length();
    return this.divideScalar(i || 1).multiplyScalar(Math.max(e, Math.min(t, i)));
  }
  floor() {
    return this.x = Math.floor(this.x), this.y = Math.floor(this.y), this;
  }
  ceil() {
    return this.x = Math.ceil(this.x), this.y = Math.ceil(this.y), this;
  }
  round() {
    return this.x = Math.round(this.x), this.y = Math.round(this.y), this;
  }
  roundToZero() {
    return this.x = Math.trunc(this.x), this.y = Math.trunc(this.y), this;
  }
  negate() {
    return this.x = -this.x, this.y = -this.y, this;
  }
  dot(e) {
    return this.x * e.x + this.y * e.y;
  }
  cross(e) {
    return this.x * e.y - this.y * e.x;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  angle() {
    return Math.atan2(-this.y, -this.x) + Math.PI;
  }
  angleTo(e) {
    const t = Math.sqrt(this.lengthSq() * e.lengthSq());
    if (t === 0)
      return Math.PI / 2;
    const i = this.dot(e) / t;
    return Math.acos(tr(i, -1, 1));
  }
  distanceTo(e) {
    return Math.sqrt(this.distanceToSquared(e));
  }
  distanceToSquared(e) {
    const t = this.x - e.x, i = this.y - e.y;
    return t * t + i * i;
  }
  manhattanDistanceTo(e) {
    return Math.abs(this.x - e.x) + Math.abs(this.y - e.y);
  }
  setLength(e) {
    return this.normalize().multiplyScalar(e);
  }
  lerp(e, t) {
    return this.x += (e.x - this.x) * t, this.y += (e.y - this.y) * t, this;
  }
  lerpVectors(e, t, i) {
    return this.x = e.x + (t.x - e.x) * i, this.y = e.y + (t.y - e.y) * i, this;
  }
  equals(e) {
    return e.x === this.x && e.y === this.y;
  }
  fromArray(e, t = 0) {
    return this.x = e[t], this.y = e[t + 1], this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this.x, e[t + 1] = this.y, e;
  }
  fromBufferAttribute(e, t) {
    return this.x = e.getX(t), this.y = e.getY(t), this;
  }
  rotateAround(e, t) {
    const i = Math.cos(t), n = Math.sin(t), r = this.x - e.x, a = this.y - e.y;
    return this.x = r * i - a * n + e.x, this.y = r * n + a * i + e.y, this;
  }
  random() {
    return this.x = Math.random(), this.y = Math.random(), this;
  }
  *[Symbol.iterator]() {
    yield this.x, yield this.y;
  }
}
class nm {
  addEventListener(e, t) {
    this._listeners === void 0 && (this._listeners = {});
    const i = this._listeners;
    i[e] === void 0 && (i[e] = []), i[e].indexOf(t) === -1 && i[e].push(t);
  }
  hasEventListener(e, t) {
    if (this._listeners === void 0)
      return !1;
    const i = this._listeners;
    return i[e] !== void 0 && i[e].indexOf(t) !== -1;
  }
  removeEventListener(e, t) {
    if (this._listeners === void 0)
      return;
    const n = this._listeners[e];
    if (n !== void 0) {
      const r = n.indexOf(t);
      r !== -1 && n.splice(r, 1);
    }
  }
  dispatchEvent(e) {
    if (this._listeners === void 0)
      return;
    const i = this._listeners[e.type];
    if (i !== void 0) {
      e.target = this;
      const n = i.slice(0);
      for (let r = 0, a = n.length; r < a; r++)
        n[r].call(this, e);
      e.target = null;
    }
  }
}
function oU(s) {
  for (let e = s.length - 1; e >= 0; --e)
    if (s[e] >= 65535)
      return !0;
  return !1;
}
function qx(s) {
  return document.createElementNS("http://www.w3.org/1999/xhtml", s);
}
function Fve() {
  const s = qx("canvas");
  return s.style.display = "block", s;
}
const bR = {};
function lU(s) {
  s in bR || (bR[s] = !0, console.warn(s));
}
const ss = /* @__PURE__ */ new je(), k3 = /* @__PURE__ */ new tn();
class n0 {
  constructor(e, t, i = !1) {
    if (Array.isArray(e))
      throw new TypeError("THREE.BufferAttribute: array should be a Typed Array.");
    this.isBufferAttribute = !0, this.name = "", this.array = e, this.itemSize = t, this.count = e !== void 0 ? e.length / t : 0, this.normalized = i, this.usage = gR, this._updateRange = { offset: 0, count: -1 }, this.updateRanges = [], this.gpuType = sl, this.version = 0;
  }
  onUploadCallback() {
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  get updateRange() {
    return lU("THREE.BufferAttribute: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead."), this._updateRange;
  }
  setUsage(e) {
    return this.usage = e, this;
  }
  addUpdateRange(e, t) {
    this.updateRanges.push({ start: e, count: t });
  }
  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }
  copy(e) {
    return this.name = e.name, this.array = new e.array.constructor(e.array), this.itemSize = e.itemSize, this.count = e.count, this.normalized = e.normalized, this.usage = e.usage, this.gpuType = e.gpuType, this;
  }
  copyAt(e, t, i) {
    e *= this.itemSize, i *= t.itemSize;
    for (let n = 0, r = this.itemSize; n < r; n++)
      this.array[e + n] = t.array[i + n];
    return this;
  }
  copyArray(e) {
    return this.array.set(e), this;
  }
  applyMatrix3(e) {
    if (this.itemSize === 2)
      for (let t = 0, i = this.count; t < i; t++)
        k3.fromBufferAttribute(this, t), k3.applyMatrix3(e), this.setXY(t, k3.x, k3.y);
    else if (this.itemSize === 3)
      for (let t = 0, i = this.count; t < i; t++)
        ss.fromBufferAttribute(this, t), ss.applyMatrix3(e), this.setXYZ(t, ss.x, ss.y, ss.z);
    return this;
  }
  applyMatrix4(e) {
    for (let t = 0, i = this.count; t < i; t++)
      ss.fromBufferAttribute(this, t), ss.applyMatrix4(e), this.setXYZ(t, ss.x, ss.y, ss.z);
    return this;
  }
  applyNormalMatrix(e) {
    for (let t = 0, i = this.count; t < i; t++)
      ss.fromBufferAttribute(this, t), ss.applyNormalMatrix(e), this.setXYZ(t, ss.x, ss.y, ss.z);
    return this;
  }
  transformDirection(e) {
    for (let t = 0, i = this.count; t < i; t++)
      ss.fromBufferAttribute(this, t), ss.transformDirection(e), this.setXYZ(t, ss.x, ss.y, ss.z);
    return this;
  }
  set(e, t = 0) {
    return this.array.set(e, t), this;
  }
  getComponent(e, t) {
    let i = this.array[e * this.itemSize + t];
    return this.normalized && (i = Wm(i, this.array)), i;
  }
  setComponent(e, t, i) {
    return this.normalized && (i = Sr(i, this.array)), this.array[e * this.itemSize + t] = i, this;
  }
  getX(e) {
    let t = this.array[e * this.itemSize];
    return this.normalized && (t = Wm(t, this.array)), t;
  }
  setX(e, t) {
    return this.normalized && (t = Sr(t, this.array)), this.array[e * this.itemSize] = t, this;
  }
  getY(e) {
    let t = this.array[e * this.itemSize + 1];
    return this.normalized && (t = Wm(t, this.array)), t;
  }
  setY(e, t) {
    return this.normalized && (t = Sr(t, this.array)), this.array[e * this.itemSize + 1] = t, this;
  }
  getZ(e) {
    let t = this.array[e * this.itemSize + 2];
    return this.normalized && (t = Wm(t, this.array)), t;
  }
  setZ(e, t) {
    return this.normalized && (t = Sr(t, this.array)), this.array[e * this.itemSize + 2] = t, this;
  }
  getW(e) {
    let t = this.array[e * this.itemSize + 3];
    return this.normalized && (t = Wm(t, this.array)), t;
  }
  setW(e, t) {
    return this.normalized && (t = Sr(t, this.array)), this.array[e * this.itemSize + 3] = t, this;
  }
  setXY(e, t, i) {
    return e *= this.itemSize, this.normalized && (t = Sr(t, this.array), i = Sr(i, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this;
  }
  setXYZ(e, t, i, n) {
    return e *= this.itemSize, this.normalized && (t = Sr(t, this.array), i = Sr(i, this.array), n = Sr(n, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this.array[e + 2] = n, this;
  }
  setXYZW(e, t, i, n, r) {
    return e *= this.itemSize, this.normalized && (t = Sr(t, this.array), i = Sr(i, this.array), n = Sr(n, this.array), r = Sr(r, this.array)), this.array[e + 0] = t, this.array[e + 1] = i, this.array[e + 2] = n, this.array[e + 3] = r, this;
  }
  onUpload(e) {
    return this.onUploadCallback = e, this;
  }
  clone() {
    return new this.constructor(this.array, this.itemSize).copy(this);
  }
  toJSON() {
    const e = {
      itemSize: this.itemSize,
      type: this.array.constructor.name,
      array: Array.from(this.array),
      normalized: this.normalized
    };
    return this.name !== "" && (e.name = this.name), this.usage !== gR && (e.usage = this.usage), e;
  }
}
class cU extends n0 {
  constructor(e, t, i) {
    super(new Uint16Array(e), t, i);
  }
}
class hU extends n0 {
  constructor(e, t, i) {
    super(new Uint32Array(e), t, i);
  }
}
class Tu extends n0 {
  constructor(e, t, i) {
    super(new Float32Array(e), t, i);
  }
}
const MR = /* @__PURE__ */ new ps(), SR = /* @__PURE__ */ new H2();
class id {
  constructor(e = 0, t = 0, i = 0, n = id.DEFAULT_ORDER) {
    this.isEuler = !0, this._x = e, this._y = t, this._z = i, this._order = n;
  }
  get x() {
    return this._x;
  }
  set x(e) {
    this._x = e, this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(e) {
    this._y = e, this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(e) {
    this._z = e, this._onChangeCallback();
  }
  get order() {
    return this._order;
  }
  set order(e) {
    this._order = e, this._onChangeCallback();
  }
  set(e, t, i, n = this._order) {
    return this._x = e, this._y = t, this._z = i, this._order = n, this._onChangeCallback(), this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._order);
  }
  copy(e) {
    return this._x = e._x, this._y = e._y, this._z = e._z, this._order = e._order, this._onChangeCallback(), this;
  }
  setFromRotationMatrix(e, t = this._order, i = !0) {
    const n = e.elements, r = n[0], a = n[4], o = n[8], l = n[1], c = n[5], h = n[9], u = n[2], d = n[6], f = n[10];
    switch (t) {
      case "XYZ":
        this._y = Math.asin(tr(o, -1, 1)), Math.abs(o) < 0.9999999 ? (this._x = Math.atan2(-h, f), this._z = Math.atan2(-a, r)) : (this._x = Math.atan2(d, c), this._z = 0);
        break;
      case "YXZ":
        this._x = Math.asin(-tr(h, -1, 1)), Math.abs(h) < 0.9999999 ? (this._y = Math.atan2(o, f), this._z = Math.atan2(l, c)) : (this._y = Math.atan2(-u, r), this._z = 0);
        break;
      case "ZXY":
        this._x = Math.asin(tr(d, -1, 1)), Math.abs(d) < 0.9999999 ? (this._y = Math.atan2(-u, f), this._z = Math.atan2(-a, c)) : (this._y = 0, this._z = Math.atan2(l, r));
        break;
      case "ZYX":
        this._y = Math.asin(-tr(u, -1, 1)), Math.abs(u) < 0.9999999 ? (this._x = Math.atan2(d, f), this._z = Math.atan2(l, r)) : (this._x = 0, this._z = Math.atan2(-a, c));
        break;
      case "YZX":
        this._z = Math.asin(tr(l, -1, 1)), Math.abs(l) < 0.9999999 ? (this._x = Math.atan2(-h, c), this._y = Math.atan2(-u, r)) : (this._x = 0, this._y = Math.atan2(o, f));
        break;
      case "XZY":
        this._z = Math.asin(-tr(a, -1, 1)), Math.abs(a) < 0.9999999 ? (this._x = Math.atan2(d, c), this._y = Math.atan2(o, r)) : (this._x = Math.atan2(-h, f), this._y = 0);
        break;
      default:
        console.warn("THREE.Euler: .setFromRotationMatrix() encountered an unknown order: " + t);
    }
    return this._order = t, i === !0 && this._onChangeCallback(), this;
  }
  setFromQuaternion(e, t, i) {
    return MR.makeRotationFromQuaternion(e), this.setFromRotationMatrix(MR, t, i);
  }
  setFromVector3(e, t = this._order) {
    return this.set(e.x, e.y, e.z, t);
  }
  reorder(e) {
    return SR.setFromEuler(this), this.setFromQuaternion(SR, e);
  }
  equals(e) {
    return e._x === this._x && e._y === this._y && e._z === this._z && e._order === this._order;
  }
  fromArray(e) {
    return this._x = e[0], this._y = e[1], this._z = e[2], e[3] !== void 0 && (this._order = e[3]), this._onChangeCallback(), this;
  }
  toArray(e = [], t = 0) {
    return e[t] = this._x, e[t + 1] = this._y, e[t + 2] = this._z, e[t + 3] = this._order, e;
  }
  _onChange(e) {
    return this._onChangeCallback = e, this;
  }
  _onChangeCallback() {
  }
  *[Symbol.iterator]() {
    yield this._x, yield this._y, yield this._z, yield this._order;
  }
}
id.DEFAULT_ORDER = "XYZ";
class uU {
  constructor() {
    this.mask = 1;
  }
  set(e) {
    this.mask = (1 << e | 0) >>> 0;
  }
  enable(e) {
    this.mask |= 1 << e | 0;
  }
  enableAll() {
    this.mask = -1;
  }
  toggle(e) {
    this.mask ^= 1 << e | 0;
  }
  disable(e) {
    this.mask &= ~(1 << e | 0);
  }
  disableAll() {
    this.mask = 0;
  }
  test(e) {
    return (this.mask & e.mask) !== 0;
  }
  isEnabled(e) {
    return (this.mask & (1 << e | 0)) !== 0;
  }
}
let Uve = 0;
const wR = /* @__PURE__ */ new je(), Xd = /* @__PURE__ */ new H2(), O0 = /* @__PURE__ */ new ps(), G3 = /* @__PURE__ */ new je(), Zm = /* @__PURE__ */ new je(), zve = /* @__PURE__ */ new je(), kve = /* @__PURE__ */ new H2(), ER = /* @__PURE__ */ new je(1, 0, 0), TR = /* @__PURE__ */ new je(0, 1, 0), CR = /* @__PURE__ */ new je(0, 0, 1), _R = { type: "added" }, Gve = { type: "removed" }, jd = { type: "childadded", child: null }, t6 = { type: "childremoved", child: null };
class Ta extends nm {
  constructor() {
    super(), this.isObject3D = !0, Object.defineProperty(this, "id", { value: Uve++ }), this.uuid = k2(), this.name = "", this.type = "Object3D", this.parent = null, this.children = [], this.up = Ta.DEFAULT_UP.clone();
    const e = new je(), t = new id(), i = new H2(), n = new je(1, 1, 1);
    function r() {
      i.setFromEuler(t, !1);
    }
    function a() {
      t.setFromQuaternion(i, void 0, !1);
    }
    t._onChange(r), i._onChange(a), Object.defineProperties(this, {
      position: {
        configurable: !0,
        enumerable: !0,
        value: e
      },
      rotation: {
        configurable: !0,
        enumerable: !0,
        value: t
      },
      quaternion: {
        configurable: !0,
        enumerable: !0,
        value: i
      },
      scale: {
        configurable: !0,
        enumerable: !0,
        value: n
      },
      modelViewMatrix: {
        value: new ps()
      },
      normalMatrix: {
        value: new xi()
      }
    }), this.matrix = new ps(), this.matrixWorld = new ps(), this.matrixAutoUpdate = Ta.DEFAULT_MATRIX_AUTO_UPDATE, this.matrixWorldAutoUpdate = Ta.DEFAULT_MATRIX_WORLD_AUTO_UPDATE, this.matrixWorldNeedsUpdate = !1, this.layers = new uU(), this.visible = !0, this.castShadow = !1, this.receiveShadow = !1, this.frustumCulled = !0, this.renderOrder = 0, this.animations = [], this.userData = {};
  }
  onBeforeShadow() {
  }
  onAfterShadow() {
  }
  onBeforeRender() {
  }
  onAfterRender() {
  }
  applyMatrix4(e) {
    this.matrixAutoUpdate && this.updateMatrix(), this.matrix.premultiply(e), this.matrix.decompose(this.position, this.quaternion, this.scale);
  }
  applyQuaternion(e) {
    return this.quaternion.premultiply(e), this;
  }
  setRotationFromAxisAngle(e, t) {
    this.quaternion.setFromAxisAngle(e, t);
  }
  setRotationFromEuler(e) {
    this.quaternion.setFromEuler(e, !0);
  }
  setRotationFromMatrix(e) {
    this.quaternion.setFromRotationMatrix(e);
  }
  setRotationFromQuaternion(e) {
    this.quaternion.copy(e);
  }
  rotateOnAxis(e, t) {
    return Xd.setFromAxisAngle(e, t), this.quaternion.multiply(Xd), this;
  }
  rotateOnWorldAxis(e, t) {
    return Xd.setFromAxisAngle(e, t), this.quaternion.premultiply(Xd), this;
  }
  rotateX(e) {
    return this.rotateOnAxis(ER, e);
  }
  rotateY(e) {
    return this.rotateOnAxis(TR, e);
  }
  rotateZ(e) {
    return this.rotateOnAxis(CR, e);
  }
  translateOnAxis(e, t) {
    return wR.copy(e).applyQuaternion(this.quaternion), this.position.add(wR.multiplyScalar(t)), this;
  }
  translateX(e) {
    return this.translateOnAxis(ER, e);
  }
  translateY(e) {
    return this.translateOnAxis(TR, e);
  }
  translateZ(e) {
    return this.translateOnAxis(CR, e);
  }
  localToWorld(e) {
    return this.updateWorldMatrix(!0, !1), e.applyMatrix4(this.matrixWorld);
  }
  worldToLocal(e) {
    return this.updateWorldMatrix(!0, !1), e.applyMatrix4(O0.copy(this.matrixWorld).invert());
  }
  lookAt(e, t, i) {
    e.isVector3 ? G3.copy(e) : G3.set(e, t, i);
    const n = this.parent;
    this.updateWorldMatrix(!0, !1), Zm.setFromMatrixPosition(this.matrixWorld), this.isCamera || this.isLight ? O0.lookAt(Zm, G3, this.up) : O0.lookAt(G3, Zm, this.up), this.quaternion.setFromRotationMatrix(O0), n && (O0.extractRotation(n.matrixWorld), Xd.setFromRotationMatrix(O0), this.quaternion.premultiply(Xd.invert()));
  }
  add(e) {
    if (arguments.length > 1) {
      for (let t = 0; t < arguments.length; t++)
        this.add(arguments[t]);
      return this;
    }
    return e === this ? (console.error("THREE.Object3D.add: object can't be added as a child of itself.", e), this) : (e && e.isObject3D ? (e.removeFromParent(), e.parent = this, this.children.push(e), e.dispatchEvent(_R), jd.child = e, this.dispatchEvent(jd), jd.child = null) : console.error("THREE.Object3D.add: object not an instance of THREE.Object3D.", e), this);
  }
  remove(e) {
    if (arguments.length > 1) {
      for (let i = 0; i < arguments.length; i++)
        this.remove(arguments[i]);
      return this;
    }
    const t = this.children.indexOf(e);
    return t !== -1 && (e.parent = null, this.children.splice(t, 1), e.dispatchEvent(Gve), t6.child = e, this.dispatchEvent(t6), t6.child = null), this;
  }
  removeFromParent() {
    const e = this.parent;
    return e !== null && e.remove(this), this;
  }
  clear() {
    return this.remove(...this.children);
  }
  attach(e) {
    return this.updateWorldMatrix(!0, !1), O0.copy(this.matrixWorld).invert(), e.parent !== null && (e.parent.updateWorldMatrix(!0, !1), O0.multiply(e.parent.matrixWorld)), e.applyMatrix4(O0), e.removeFromParent(), e.parent = this, this.children.push(e), e.updateWorldMatrix(!1, !0), e.dispatchEvent(_R), jd.child = e, this.dispatchEvent(jd), jd.child = null, this;
  }
  getObjectById(e) {
    return this.getObjectByProperty("id", e);
  }
  getObjectByName(e) {
    return this.getObjectByProperty("name", e);
  }
  getObjectByProperty(e, t) {
    if (this[e] === t)
      return this;
    for (let i = 0, n = this.children.length; i < n; i++) {
      const a = this.children[i].getObjectByProperty(e, t);
      if (a !== void 0)
        return a;
    }
  }
  getObjectsByProperty(e, t, i = []) {
    this[e] === t && i.push(this);
    const n = this.children;
    for (let r = 0, a = n.length; r < a; r++)
      n[r].getObjectsByProperty(e, t, i);
    return i;
  }
  getWorldPosition(e) {
    return this.updateWorldMatrix(!0, !1), e.setFromMatrixPosition(this.matrixWorld);
  }
  getWorldQuaternion(e) {
    return this.updateWorldMatrix(!0, !1), this.matrixWorld.decompose(Zm, e, zve), e;
  }
  getWorldScale(e) {
    return this.updateWorldMatrix(!0, !1), this.matrixWorld.decompose(Zm, kve, e), e;
  }
  getWorldDirection(e) {
    this.updateWorldMatrix(!0, !1);
    const t = this.matrixWorld.elements;
    return e.set(t[8], t[9], t[10]).normalize();
  }
  raycast() {
  }
  traverse(e) {
    e(this);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].traverse(e);
  }
  traverseVisible(e) {
    if (this.visible === !1)
      return;
    e(this);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].traverseVisible(e);
  }
  traverseAncestors(e) {
    const t = this.parent;
    t !== null && (e(t), t.traverseAncestors(e));
  }
  updateMatrix() {
    this.matrix.compose(this.position, this.quaternion, this.scale), this.matrixWorldNeedsUpdate = !0;
  }
  updateMatrixWorld(e) {
    this.matrixAutoUpdate && this.updateMatrix(), (this.matrixWorldNeedsUpdate || e) && (this.matrixWorldAutoUpdate === !0 && (this.parent === null ? this.matrixWorld.copy(this.matrix) : this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix)), this.matrixWorldNeedsUpdate = !1, e = !0);
    const t = this.children;
    for (let i = 0, n = t.length; i < n; i++)
      t[i].updateMatrixWorld(e);
  }
  updateWorldMatrix(e, t) {
    const i = this.parent;
    if (e === !0 && i !== null && i.updateWorldMatrix(!0, !1), this.matrixAutoUpdate && this.updateMatrix(), this.matrixWorldAutoUpdate === !0 && (this.parent === null ? this.matrixWorld.copy(this.matrix) : this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix)), t === !0) {
      const n = this.children;
      for (let r = 0, a = n.length; r < a; r++)
        n[r].updateWorldMatrix(!1, !0);
    }
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string", i = {};
    t && (e = {
      geometries: {},
      materials: {},
      textures: {},
      images: {},
      shapes: {},
      skeletons: {},
      animations: {},
      nodes: {}
    }, i.metadata = {
      version: 4.6,
      type: "Object",
      generator: "Object3D.toJSON"
    });
    const n = {};
    n.uuid = this.uuid, n.type = this.type, this.name !== "" && (n.name = this.name), this.castShadow === !0 && (n.castShadow = !0), this.receiveShadow === !0 && (n.receiveShadow = !0), this.visible === !1 && (n.visible = !1), this.frustumCulled === !1 && (n.frustumCulled = !1), this.renderOrder !== 0 && (n.renderOrder = this.renderOrder), Object.keys(this.userData).length > 0 && (n.userData = this.userData), n.layers = this.layers.mask, n.matrix = this.matrix.toArray(), n.up = this.up.toArray(), this.matrixAutoUpdate === !1 && (n.matrixAutoUpdate = !1), this.isInstancedMesh && (n.type = "InstancedMesh", n.count = this.count, n.instanceMatrix = this.instanceMatrix.toJSON(), this.instanceColor !== null && (n.instanceColor = this.instanceColor.toJSON())), this.isBatchedMesh && (n.type = "BatchedMesh", n.perObjectFrustumCulled = this.perObjectFrustumCulled, n.sortObjects = this.sortObjects, n.drawRanges = this._drawRanges, n.reservedRanges = this._reservedRanges, n.visibility = this._visibility, n.active = this._active, n.bounds = this._bounds.map((o) => ({
      boxInitialized: o.boxInitialized,
      boxMin: o.box.min.toArray(),
      boxMax: o.box.max.toArray(),
      sphereInitialized: o.sphereInitialized,
      sphereRadius: o.sphere.radius,
      sphereCenter: o.sphere.center.toArray()
    })), n.maxInstanceCount = this._maxInstanceCount, n.maxVertexCount = this._maxVertexCount, n.maxIndexCount = this._maxIndexCount, n.geometryInitialized = this._geometryInitialized, n.geometryCount = this._geometryCount, n.matricesTexture = this._matricesTexture.toJSON(e), this._colorsTexture !== null && (n.colorsTexture = this._colorsTexture.toJSON(e)), this.boundingSphere !== null && (n.boundingSphere = {
      center: n.boundingSphere.center.toArray(),
      radius: n.boundingSphere.radius
    }), this.boundingBox !== null && (n.boundingBox = {
      min: n.boundingBox.min.toArray(),
      max: n.boundingBox.max.toArray()
    }));
    function r(o, l) {
      return o[l.uuid] === void 0 && (o[l.uuid] = l.toJSON(e)), l.uuid;
    }
    if (this.isScene)
      this.background && (this.background.isColor ? n.background = this.background.toJSON() : this.background.isTexture && (n.background = this.background.toJSON(e).uuid)), this.environment && this.environment.isTexture && this.environment.isRenderTargetTexture !== !0 && (n.environment = this.environment.toJSON(e).uuid);
    else if (this.isMesh || this.isLine || this.isPoints) {
      n.geometry = r(e.geometries, this.geometry);
      const o = this.geometry.parameters;
      if (o !== void 0 && o.shapes !== void 0) {
        const l = o.shapes;
        if (Array.isArray(l))
          for (let c = 0, h = l.length; c < h; c++) {
            const u = l[c];
            r(e.shapes, u);
          }
        else
          r(e.shapes, l);
      }
    }
    if (this.isSkinnedMesh && (n.bindMode = this.bindMode, n.bindMatrix = this.bindMatrix.toArray(), this.skeleton !== void 0 && (r(e.skeletons, this.skeleton), n.skeleton = this.skeleton.uuid)), this.material !== void 0)
      if (Array.isArray(this.material)) {
        const o = [];
        for (let l = 0, c = this.material.length; l < c; l++)
          o.push(r(e.materials, this.material[l]));
        n.material = o;
      } else
        n.material = r(e.materials, this.material);
    if (this.children.length > 0) {
      n.children = [];
      for (let o = 0; o < this.children.length; o++)
        n.children.push(this.children[o].toJSON(e).object);
    }
    if (this.animations.length > 0) {
      n.animations = [];
      for (let o = 0; o < this.animations.length; o++) {
        const l = this.animations[o];
        n.animations.push(r(e.animations, l));
      }
    }
    if (t) {
      const o = a(e.geometries), l = a(e.materials), c = a(e.textures), h = a(e.images), u = a(e.shapes), d = a(e.skeletons), f = a(e.animations), p = a(e.nodes);
      o.length > 0 && (i.geometries = o), l.length > 0 && (i.materials = l), c.length > 0 && (i.textures = c), h.length > 0 && (i.images = h), u.length > 0 && (i.shapes = u), d.length > 0 && (i.skeletons = d), f.length > 0 && (i.animations = f), p.length > 0 && (i.nodes = p);
    }
    return i.object = n, i;
    function a(o) {
      const l = [];
      for (const c in o) {
        const h = o[c];
        delete h.metadata, l.push(h);
      }
      return l;
    }
  }
  clone(e) {
    return new this.constructor().copy(this, e);
  }
  copy(e, t = !0) {
    if (this.name = e.name, this.up.copy(e.up), this.position.copy(e.position), this.rotation.order = e.rotation.order, this.quaternion.copy(e.quaternion), this.scale.copy(e.scale), this.matrix.copy(e.matrix), this.matrixWorld.copy(e.matrixWorld), this.matrixAutoUpdate = e.matrixAutoUpdate, this.matrixWorldAutoUpdate = e.matrixWorldAutoUpdate, this.matrixWorldNeedsUpdate = e.matrixWorldNeedsUpdate, this.layers.mask = e.layers.mask, this.visible = e.visible, this.castShadow = e.castShadow, this.receiveShadow = e.receiveShadow, this.frustumCulled = e.frustumCulled, this.renderOrder = e.renderOrder, this.animations = e.animations.slice(), this.userData = JSON.parse(JSON.stringify(e.userData)), t === !0)
      for (let i = 0; i < e.children.length; i++) {
        const n = e.children[i];
        this.add(n.clone());
      }
    return this;
  }
}
Ta.DEFAULT_UP = /* @__PURE__ */ new je(0, 1, 0);
Ta.DEFAULT_MATRIX_AUTO_UPDATE = !0;
Ta.DEFAULT_MATRIX_WORLD_AUTO_UPDATE = !0;
let Hve = 0;
const ua = /* @__PURE__ */ new ps(), i6 = /* @__PURE__ */ new Ta(), qd = /* @__PURE__ */ new je(), jr = /* @__PURE__ */ new V2(), Km = /* @__PURE__ */ new V2(), ws = /* @__PURE__ */ new je();
class nd extends nm {
  constructor() {
    super(), this.isBufferGeometry = !0, Object.defineProperty(this, "id", { value: Hve++ }), this.uuid = k2(), this.name = "", this.type = "BufferGeometry", this.index = null, this.attributes = {}, this.morphAttributes = {}, this.morphTargetsRelative = !1, this.groups = [], this.boundingBox = null, this.boundingSphere = null, this.drawRange = { start: 0, count: 1 / 0 }, this.userData = {};
  }
  getIndex() {
    return this.index;
  }
  setIndex(e) {
    return Array.isArray(e) ? this.index = new (oU(e) ? hU : cU)(e, 1) : this.index = e, this;
  }
  getAttribute(e) {
    return this.attributes[e];
  }
  setAttribute(e, t) {
    return this.attributes[e] = t, this;
  }
  deleteAttribute(e) {
    return delete this.attributes[e], this;
  }
  hasAttribute(e) {
    return this.attributes[e] !== void 0;
  }
  addGroup(e, t, i = 0) {
    this.groups.push({
      start: e,
      count: t,
      materialIndex: i
    });
  }
  clearGroups() {
    this.groups = [];
  }
  setDrawRange(e, t) {
    this.drawRange.start = e, this.drawRange.count = t;
  }
  applyMatrix4(e) {
    const t = this.attributes.position;
    t !== void 0 && (t.applyMatrix4(e), t.needsUpdate = !0);
    const i = this.attributes.normal;
    if (i !== void 0) {
      const r = new xi().getNormalMatrix(e);
      i.applyNormalMatrix(r), i.needsUpdate = !0;
    }
    const n = this.attributes.tangent;
    return n !== void 0 && (n.transformDirection(e), n.needsUpdate = !0), this.boundingBox !== null && this.computeBoundingBox(), this.boundingSphere !== null && this.computeBoundingSphere(), this;
  }
  applyQuaternion(e) {
    return ua.makeRotationFromQuaternion(e), this.applyMatrix4(ua), this;
  }
  rotateX(e) {
    return ua.makeRotationX(e), this.applyMatrix4(ua), this;
  }
  rotateY(e) {
    return ua.makeRotationY(e), this.applyMatrix4(ua), this;
  }
  rotateZ(e) {
    return ua.makeRotationZ(e), this.applyMatrix4(ua), this;
  }
  translate(e, t, i) {
    return ua.makeTranslation(e, t, i), this.applyMatrix4(ua), this;
  }
  scale(e, t, i) {
    return ua.makeScale(e, t, i), this.applyMatrix4(ua), this;
  }
  lookAt(e) {
    return i6.lookAt(e), i6.updateMatrix(), this.applyMatrix4(i6.matrix), this;
  }
  center() {
    return this.computeBoundingBox(), this.boundingBox.getCenter(qd).negate(), this.translate(qd.x, qd.y, qd.z), this;
  }
  setFromPoints(e) {
    const t = [];
    for (let i = 0, n = e.length; i < n; i++) {
      const r = e[i];
      t.push(r.x, r.y, r.z || 0);
    }
    return this.setAttribute("position", new Tu(t, 3)), this;
  }
  computeBoundingBox() {
    this.boundingBox === null && (this.boundingBox = new V2());
    const e = this.attributes.position, t = this.morphAttributes.position;
    if (e && e.isGLBufferAttribute) {
      console.error("THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.", this), this.boundingBox.set(
        new je(-1 / 0, -1 / 0, -1 / 0),
        new je(1 / 0, 1 / 0, 1 / 0)
      );
      return;
    }
    if (e !== void 0) {
      if (this.boundingBox.setFromBufferAttribute(e), t)
        for (let i = 0, n = t.length; i < n; i++) {
          const r = t[i];
          jr.setFromBufferAttribute(r), this.morphTargetsRelative ? (ws.addVectors(this.boundingBox.min, jr.min), this.boundingBox.expandByPoint(ws), ws.addVectors(this.boundingBox.max, jr.max), this.boundingBox.expandByPoint(ws)) : (this.boundingBox.expandByPoint(jr.min), this.boundingBox.expandByPoint(jr.max));
        }
    } else
      this.boundingBox.makeEmpty();
    (isNaN(this.boundingBox.min.x) || isNaN(this.boundingBox.min.y) || isNaN(this.boundingBox.min.z)) && console.error('THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.', this);
  }
  computeBoundingSphere() {
    this.boundingSphere === null && (this.boundingSphere = new Nw());
    const e = this.attributes.position, t = this.morphAttributes.position;
    if (e && e.isGLBufferAttribute) {
      console.error("THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.", this), this.boundingSphere.set(new je(), 1 / 0);
      return;
    }
    if (e) {
      const i = this.boundingSphere.center;
      if (jr.setFromBufferAttribute(e), t)
        for (let r = 0, a = t.length; r < a; r++) {
          const o = t[r];
          Km.setFromBufferAttribute(o), this.morphTargetsRelative ? (ws.addVectors(jr.min, Km.min), jr.expandByPoint(ws), ws.addVectors(jr.max, Km.max), jr.expandByPoint(ws)) : (jr.expandByPoint(Km.min), jr.expandByPoint(Km.max));
        }
      jr.getCenter(i);
      let n = 0;
      for (let r = 0, a = e.count; r < a; r++)
        ws.fromBufferAttribute(e, r), n = Math.max(n, i.distanceToSquared(ws));
      if (t)
        for (let r = 0, a = t.length; r < a; r++) {
          const o = t[r], l = this.morphTargetsRelative;
          for (let c = 0, h = o.count; c < h; c++)
            ws.fromBufferAttribute(o, c), l && (qd.fromBufferAttribute(e, c), ws.add(qd)), n = Math.max(n, i.distanceToSquared(ws));
        }
      this.boundingSphere.radius = Math.sqrt(n), isNaN(this.boundingSphere.radius) && console.error('THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.', this);
    }
  }
  computeTangents() {
    const e = this.index, t = this.attributes;
    if (e === null || t.position === void 0 || t.normal === void 0 || t.uv === void 0) {
      console.error("THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)");
      return;
    }
    const i = t.position, n = t.normal, r = t.uv;
    this.hasAttribute("tangent") === !1 && this.setAttribute("tangent", new n0(new Float32Array(4 * i.count), 4));
    const a = this.getAttribute("tangent"), o = [], l = [];
    for (let C = 0; C < i.count; C++)
      o[C] = new je(), l[C] = new je();
    const c = new je(), h = new je(), u = new je(), d = new tn(), f = new tn(), p = new tn(), v = new je(), g = new je();
    function m(C, T, E) {
      c.fromBufferAttribute(i, C), h.fromBufferAttribute(i, T), u.fromBufferAttribute(i, E), d.fromBufferAttribute(r, C), f.fromBufferAttribute(r, T), p.fromBufferAttribute(r, E), h.sub(c), u.sub(c), f.sub(d), p.sub(d);
      const P = 1 / (f.x * p.y - p.x * f.y);
      isFinite(P) && (v.copy(h).multiplyScalar(p.y).addScaledVector(u, -f.y).multiplyScalar(P), g.copy(u).multiplyScalar(f.x).addScaledVector(h, -p.x).multiplyScalar(P), o[C].add(v), o[T].add(v), o[E].add(v), l[C].add(g), l[T].add(g), l[E].add(g));
    }
    let x = this.groups;
    x.length === 0 && (x = [{
      start: 0,
      count: e.count
    }]);
    for (let C = 0, T = x.length; C < T; ++C) {
      const E = x[C], P = E.start, L = E.count;
      for (let I = P, N = P + L; I < N; I += 3)
        m(
          e.getX(I + 0),
          e.getX(I + 1),
          e.getX(I + 2)
        );
    }
    const y = new je(), A = new je(), b = new je(), M = new je();
    function w(C) {
      b.fromBufferAttribute(n, C), M.copy(b);
      const T = o[C];
      y.copy(T), y.sub(b.multiplyScalar(b.dot(T))).normalize(), A.crossVectors(M, T);
      const P = A.dot(l[C]) < 0 ? -1 : 1;
      a.setXYZW(C, y.x, y.y, y.z, P);
    }
    for (let C = 0, T = x.length; C < T; ++C) {
      const E = x[C], P = E.start, L = E.count;
      for (let I = P, N = P + L; I < N; I += 3)
        w(e.getX(I + 0)), w(e.getX(I + 1)), w(e.getX(I + 2));
    }
  }
  computeVertexNormals() {
    const e = this.index, t = this.getAttribute("position");
    if (t !== void 0) {
      let i = this.getAttribute("normal");
      if (i === void 0)
        i = new n0(new Float32Array(t.count * 3), 3), this.setAttribute("normal", i);
      else
        for (let d = 0, f = i.count; d < f; d++)
          i.setXYZ(d, 0, 0, 0);
      const n = new je(), r = new je(), a = new je(), o = new je(), l = new je(), c = new je(), h = new je(), u = new je();
      if (e)
        for (let d = 0, f = e.count; d < f; d += 3) {
          const p = e.getX(d + 0), v = e.getX(d + 1), g = e.getX(d + 2);
          n.fromBufferAttribute(t, p), r.fromBufferAttribute(t, v), a.fromBufferAttribute(t, g), h.subVectors(a, r), u.subVectors(n, r), h.cross(u), o.fromBufferAttribute(i, p), l.fromBufferAttribute(i, v), c.fromBufferAttribute(i, g), o.add(h), l.add(h), c.add(h), i.setXYZ(p, o.x, o.y, o.z), i.setXYZ(v, l.x, l.y, l.z), i.setXYZ(g, c.x, c.y, c.z);
        }
      else
        for (let d = 0, f = t.count; d < f; d += 3)
          n.fromBufferAttribute(t, d + 0), r.fromBufferAttribute(t, d + 1), a.fromBufferAttribute(t, d + 2), h.subVectors(a, r), u.subVectors(n, r), h.cross(u), i.setXYZ(d + 0, h.x, h.y, h.z), i.setXYZ(d + 1, h.x, h.y, h.z), i.setXYZ(d + 2, h.x, h.y, h.z);
      this.normalizeNormals(), i.needsUpdate = !0;
    }
  }
  normalizeNormals() {
    const e = this.attributes.normal;
    for (let t = 0, i = e.count; t < i; t++)
      ws.fromBufferAttribute(e, t), ws.normalize(), e.setXYZ(t, ws.x, ws.y, ws.z);
  }
  toNonIndexed() {
    function e(o, l) {
      const c = o.array, h = o.itemSize, u = o.normalized, d = new c.constructor(l.length * h);
      let f = 0, p = 0;
      for (let v = 0, g = l.length; v < g; v++) {
        o.isInterleavedBufferAttribute ? f = l[v] * o.data.stride + o.offset : f = l[v] * h;
        for (let m = 0; m < h; m++)
          d[p++] = c[f++];
      }
      return new n0(d, h, u);
    }
    if (this.index === null)
      return console.warn("THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed."), this;
    const t = new nd(), i = this.index.array, n = this.attributes;
    for (const o in n) {
      const l = n[o], c = e(l, i);
      t.setAttribute(o, c);
    }
    const r = this.morphAttributes;
    for (const o in r) {
      const l = [], c = r[o];
      for (let h = 0, u = c.length; h < u; h++) {
        const d = c[h], f = e(d, i);
        l.push(f);
      }
      t.morphAttributes[o] = l;
    }
    t.morphTargetsRelative = this.morphTargetsRelative;
    const a = this.groups;
    for (let o = 0, l = a.length; o < l; o++) {
      const c = a[o];
      t.addGroup(c.start, c.count, c.materialIndex);
    }
    return t;
  }
  toJSON() {
    const e = {
      metadata: {
        version: 4.6,
        type: "BufferGeometry",
        generator: "BufferGeometry.toJSON"
      }
    };
    if (e.uuid = this.uuid, e.type = this.type, this.name !== "" && (e.name = this.name), Object.keys(this.userData).length > 0 && (e.userData = this.userData), this.parameters !== void 0) {
      const l = this.parameters;
      for (const c in l)
        l[c] !== void 0 && (e[c] = l[c]);
      return e;
    }
    e.data = { attributes: {} };
    const t = this.index;
    t !== null && (e.data.index = {
      type: t.array.constructor.name,
      array: Array.prototype.slice.call(t.array)
    });
    const i = this.attributes;
    for (const l in i) {
      const c = i[l];
      e.data.attributes[l] = c.toJSON(e.data);
    }
    const n = {};
    let r = !1;
    for (const l in this.morphAttributes) {
      const c = this.morphAttributes[l], h = [];
      for (let u = 0, d = c.length; u < d; u++) {
        const f = c[u];
        h.push(f.toJSON(e.data));
      }
      h.length > 0 && (n[l] = h, r = !0);
    }
    r && (e.data.morphAttributes = n, e.data.morphTargetsRelative = this.morphTargetsRelative);
    const a = this.groups;
    a.length > 0 && (e.data.groups = JSON.parse(JSON.stringify(a)));
    const o = this.boundingSphere;
    return o !== null && (e.data.boundingSphere = {
      center: o.center.toArray(),
      radius: o.radius
    }), e;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.index = null, this.attributes = {}, this.morphAttributes = {}, this.groups = [], this.boundingBox = null, this.boundingSphere = null;
    const t = {};
    this.name = e.name;
    const i = e.index;
    i !== null && this.setIndex(i.clone(t));
    const n = e.attributes;
    for (const c in n) {
      const h = n[c];
      this.setAttribute(c, h.clone(t));
    }
    const r = e.morphAttributes;
    for (const c in r) {
      const h = [], u = r[c];
      for (let d = 0, f = u.length; d < f; d++)
        h.push(u[d].clone(t));
      this.morphAttributes[c] = h;
    }
    this.morphTargetsRelative = e.morphTargetsRelative;
    const a = e.groups;
    for (let c = 0, h = a.length; c < h; c++) {
      const u = a[c];
      this.addGroup(u.start, u.count, u.materialIndex);
    }
    const o = e.boundingBox;
    o !== null && (this.boundingBox = o.clone());
    const l = e.boundingSphere;
    return l !== null && (this.boundingSphere = l.clone()), this.drawRange.start = e.drawRange.start, this.drawRange.count = e.drawRange.count, this.userData = e.userData, this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
}
class W2 extends nd {
  constructor(e = 1, t = 1, i = 1, n = 1, r = 1, a = 1) {
    super(), this.type = "BoxGeometry", this.parameters = {
      width: e,
      height: t,
      depth: i,
      widthSegments: n,
      heightSegments: r,
      depthSegments: a
    };
    const o = this;
    n = Math.floor(n), r = Math.floor(r), a = Math.floor(a);
    const l = [], c = [], h = [], u = [];
    let d = 0, f = 0;
    p("z", "y", "x", -1, -1, i, t, e, a, r, 0), p("z", "y", "x", 1, -1, i, t, -e, a, r, 1), p("x", "z", "y", 1, 1, e, i, t, n, a, 2), p("x", "z", "y", 1, -1, e, i, -t, n, a, 3), p("x", "y", "z", 1, -1, e, t, i, n, r, 4), p("x", "y", "z", -1, -1, e, t, -i, n, r, 5), this.setIndex(l), this.setAttribute("position", new Tu(c, 3)), this.setAttribute("normal", new Tu(h, 3)), this.setAttribute("uv", new Tu(u, 2));
    function p(v, g, m, x, y, A, b, M, w, C, T) {
      const E = A / w, P = b / C, L = A / 2, I = b / 2, N = M / 2, H = w + 1, U = C + 1;
      let W = 0, X = 0;
      const K = new je();
      for (let te = 0; te < U; te++) {
        const re = te * P - I;
        for (let be = 0; be < H; be++) {
          const Pe = be * E - L;
          K[v] = Pe * x, K[g] = re * y, K[m] = N, c.push(K.x, K.y, K.z), K[v] = 0, K[g] = 0, K[m] = M > 0 ? 1 : -1, h.push(K.x, K.y, K.z), u.push(be / w), u.push(1 - te / C), W += 1;
        }
      }
      for (let te = 0; te < C; te++)
        for (let re = 0; re < w; re++) {
          const be = d + re + H * te, Pe = d + re + H * (te + 1), J = d + (re + 1) + H * (te + 1), fe = d + (re + 1) + H * te;
          l.push(be, Pe, fe), l.push(Pe, J, fe), X += 6;
        }
      o.addGroup(f, X, T), f += X, d += W;
    }
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new W2(e.width, e.height, e.depth, e.widthSegments, e.heightSegments, e.depthSegments);
  }
}
class p8 extends nd {
  constructor(e = 1, t = 1, i = 1, n = 1) {
    super(), this.type = "PlaneGeometry", this.parameters = {
      width: e,
      height: t,
      widthSegments: i,
      heightSegments: n
    };
    const r = e / 2, a = t / 2, o = Math.floor(i), l = Math.floor(n), c = o + 1, h = l + 1, u = e / o, d = t / l, f = [], p = [], v = [], g = [];
    for (let m = 0; m < h; m++) {
      const x = m * d - a;
      for (let y = 0; y < c; y++) {
        const A = y * u - r;
        p.push(A, -x, 0), v.push(0, 0, 1), g.push(y / o), g.push(1 - m / l);
      }
    }
    for (let m = 0; m < l; m++)
      for (let x = 0; x < o; x++) {
        const y = x + c * m, A = x + c * (m + 1), b = x + 1 + c * (m + 1), M = x + 1 + c * m;
        f.push(y, A, M), f.push(A, b, M);
      }
    this.setIndex(f), this.setAttribute("position", new Tu(p, 3)), this.setAttribute("normal", new Tu(v, 3)), this.setAttribute("uv", new Tu(g, 2));
  }
  copy(e) {
    return super.copy(e), this.parameters = Object.assign({}, e.parameters), this;
  }
  static fromJSON(e) {
    return new p8(e.width, e.height, e.widthSegments, e.heightSegments);
  }
}
let Vve = 0;
class m8 extends nm {
  constructor() {
    super(), this.isMaterial = !0, Object.defineProperty(this, "id", { value: Vve++ }), this.uuid = k2(), this.name = "", this.type = "Material", this.blending = Jf, this.side = Kc, this.vertexColors = !1, this.opacity = 1, this.transparent = !1, this.alphaHash = !1, this.blendSrc = b7, this.blendDst = M7, this.blendEquation = ru, this.blendSrcAlpha = null, this.blendDstAlpha = null, this.blendEquationAlpha = null, this.blendColor = new hn(0, 0, 0), this.blendAlpha = 0, this.depthFunc = Hx, this.depthTest = !0, this.depthWrite = !0, this.stencilWriteMask = 255, this.stencilFunc = mR, this.stencilRef = 0, this.stencilFuncMask = 255, this.stencilFail = kd, this.stencilZFail = kd, this.stencilZPass = kd, this.stencilWrite = !1, this.clippingPlanes = null, this.clipIntersection = !1, this.clipShadows = !1, this.shadowSide = null, this.colorWrite = !0, this.precision = null, this.polygonOffset = !1, this.polygonOffsetFactor = 0, this.polygonOffsetUnits = 0, this.dithering = !1, this.alphaToCoverage = !1, this.premultipliedAlpha = !1, this.forceSinglePass = !1, this.visible = !0, this.toneMapped = !0, this.userData = {}, this.version = 0, this._alphaTest = 0;
  }
  get alphaTest() {
    return this._alphaTest;
  }
  set alphaTest(e) {
    this._alphaTest > 0 != e > 0 && this.version++, this._alphaTest = e;
  }
  onBeforeCompile() {
  }
  customProgramCacheKey() {
    return this.onBeforeCompile.toString();
  }
  setValues(e) {
    if (e !== void 0)
      for (const t in e) {
        const i = e[t];
        if (i === void 0) {
          console.warn(`THREE.Material: parameter '${t}' has value of undefined.`);
          continue;
        }
        const n = this[t];
        if (n === void 0) {
          console.warn(`THREE.Material: '${t}' is not a property of THREE.${this.type}.`);
          continue;
        }
        n && n.isColor ? n.set(i) : n && n.isVector3 && i && i.isVector3 ? n.copy(i) : this[t] = i;
      }
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    t && (e = {
      textures: {},
      images: {}
    });
    const i = {
      metadata: {
        version: 4.6,
        type: "Material",
        generator: "Material.toJSON"
      }
    };
    i.uuid = this.uuid, i.type = this.type, this.name !== "" && (i.name = this.name), this.color && this.color.isColor && (i.color = this.color.getHex()), this.roughness !== void 0 && (i.roughness = this.roughness), this.metalness !== void 0 && (i.metalness = this.metalness), this.sheen !== void 0 && (i.sheen = this.sheen), this.sheenColor && this.sheenColor.isColor && (i.sheenColor = this.sheenColor.getHex()), this.sheenRoughness !== void 0 && (i.sheenRoughness = this.sheenRoughness), this.emissive && this.emissive.isColor && (i.emissive = this.emissive.getHex()), this.emissiveIntensity !== void 0 && this.emissiveIntensity !== 1 && (i.emissiveIntensity = this.emissiveIntensity), this.specular && this.specular.isColor && (i.specular = this.specular.getHex()), this.specularIntensity !== void 0 && (i.specularIntensity = this.specularIntensity), this.specularColor && this.specularColor.isColor && (i.specularColor = this.specularColor.getHex()), this.shininess !== void 0 && (i.shininess = this.shininess), this.clearcoat !== void 0 && (i.clearcoat = this.clearcoat), this.clearcoatRoughness !== void 0 && (i.clearcoatRoughness = this.clearcoatRoughness), this.clearcoatMap && this.clearcoatMap.isTexture && (i.clearcoatMap = this.clearcoatMap.toJSON(e).uuid), this.clearcoatRoughnessMap && this.clearcoatRoughnessMap.isTexture && (i.clearcoatRoughnessMap = this.clearcoatRoughnessMap.toJSON(e).uuid), this.clearcoatNormalMap && this.clearcoatNormalMap.isTexture && (i.clearcoatNormalMap = this.clearcoatNormalMap.toJSON(e).uuid, i.clearcoatNormalScale = this.clearcoatNormalScale.toArray()), this.dispersion !== void 0 && (i.dispersion = this.dispersion), this.iridescence !== void 0 && (i.iridescence = this.iridescence), this.iridescenceIOR !== void 0 && (i.iridescenceIOR = this.iridescenceIOR), this.iridescenceThicknessRange !== void 0 && (i.iridescenceThicknessRange = this.iridescenceThicknessRange), this.iridescenceMap && this.iridescenceMap.isTexture && (i.iridescenceMap = this.iridescenceMap.toJSON(e).uuid), this.iridescenceThicknessMap && this.iridescenceThicknessMap.isTexture && (i.iridescenceThicknessMap = this.iridescenceThicknessMap.toJSON(e).uuid), this.anisotropy !== void 0 && (i.anisotropy = this.anisotropy), this.anisotropyRotation !== void 0 && (i.anisotropyRotation = this.anisotropyRotation), this.anisotropyMap && this.anisotropyMap.isTexture && (i.anisotropyMap = this.anisotropyMap.toJSON(e).uuid), this.map && this.map.isTexture && (i.map = this.map.toJSON(e).uuid), this.matcap && this.matcap.isTexture && (i.matcap = this.matcap.toJSON(e).uuid), this.alphaMap && this.alphaMap.isTexture && (i.alphaMap = this.alphaMap.toJSON(e).uuid), this.lightMap && this.lightMap.isTexture && (i.lightMap = this.lightMap.toJSON(e).uuid, i.lightMapIntensity = this.lightMapIntensity), this.aoMap && this.aoMap.isTexture && (i.aoMap = this.aoMap.toJSON(e).uuid, i.aoMapIntensity = this.aoMapIntensity), this.bumpMap && this.bumpMap.isTexture && (i.bumpMap = this.bumpMap.toJSON(e).uuid, i.bumpScale = this.bumpScale), this.normalMap && this.normalMap.isTexture && (i.normalMap = this.normalMap.toJSON(e).uuid, i.normalMapType = this.normalMapType, i.normalScale = this.normalScale.toArray()), this.displacementMap && this.displacementMap.isTexture && (i.displacementMap = this.displacementMap.toJSON(e).uuid, i.displacementScale = this.displacementScale, i.displacementBias = this.displacementBias), this.roughnessMap && this.roughnessMap.isTexture && (i.roughnessMap = this.roughnessMap.toJSON(e).uuid), this.metalnessMap && this.metalnessMap.isTexture && (i.metalnessMap = this.metalnessMap.toJSON(e).uuid), this.emissiveMap && this.emissiveMap.isTexture && (i.emissiveMap = this.emissiveMap.toJSON(e).uuid), this.specularMap && this.specularMap.isTexture && (i.specularMap = this.specularMap.toJSON(e).uuid), this.specularIntensityMap && this.specularIntensityMap.isTexture && (i.specularIntensityMap = this.specularIntensityMap.toJSON(e).uuid), this.specularColorMap && this.specularColorMap.isTexture && (i.specularColorMap = this.specularColorMap.toJSON(e).uuid), this.envMap && this.envMap.isTexture && (i.envMap = this.envMap.toJSON(e).uuid, this.combine !== void 0 && (i.combine = this.combine)), this.envMapRotation !== void 0 && (i.envMapRotation = this.envMapRotation.toArray()), this.envMapIntensity !== void 0 && (i.envMapIntensity = this.envMapIntensity), this.reflectivity !== void 0 && (i.reflectivity = this.reflectivity), this.refractionRatio !== void 0 && (i.refractionRatio = this.refractionRatio), this.gradientMap && this.gradientMap.isTexture && (i.gradientMap = this.gradientMap.toJSON(e).uuid), this.transmission !== void 0 && (i.transmission = this.transmission), this.transmissionMap && this.transmissionMap.isTexture && (i.transmissionMap = this.transmissionMap.toJSON(e).uuid), this.thickness !== void 0 && (i.thickness = this.thickness), this.thicknessMap && this.thicknessMap.isTexture && (i.thicknessMap = this.thicknessMap.toJSON(e).uuid), this.attenuationDistance !== void 0 && this.attenuationDistance !== 1 / 0 && (i.attenuationDistance = this.attenuationDistance), this.attenuationColor !== void 0 && (i.attenuationColor = this.attenuationColor.getHex()), this.size !== void 0 && (i.size = this.size), this.shadowSide !== null && (i.shadowSide = this.shadowSide), this.sizeAttenuation !== void 0 && (i.sizeAttenuation = this.sizeAttenuation), this.blending !== Jf && (i.blending = this.blending), this.side !== Kc && (i.side = this.side), this.vertexColors === !0 && (i.vertexColors = !0), this.opacity < 1 && (i.opacity = this.opacity), this.transparent === !0 && (i.transparent = !0), this.blendSrc !== b7 && (i.blendSrc = this.blendSrc), this.blendDst !== M7 && (i.blendDst = this.blendDst), this.blendEquation !== ru && (i.blendEquation = this.blendEquation), this.blendSrcAlpha !== null && (i.blendSrcAlpha = this.blendSrcAlpha), this.blendDstAlpha !== null && (i.blendDstAlpha = this.blendDstAlpha), this.blendEquationAlpha !== null && (i.blendEquationAlpha = this.blendEquationAlpha), this.blendColor && this.blendColor.isColor && (i.blendColor = this.blendColor.getHex()), this.blendAlpha !== 0 && (i.blendAlpha = this.blendAlpha), this.depthFunc !== Hx && (i.depthFunc = this.depthFunc), this.depthTest === !1 && (i.depthTest = this.depthTest), this.depthWrite === !1 && (i.depthWrite = this.depthWrite), this.colorWrite === !1 && (i.colorWrite = this.colorWrite), this.stencilWriteMask !== 255 && (i.stencilWriteMask = this.stencilWriteMask), this.stencilFunc !== mR && (i.stencilFunc = this.stencilFunc), this.stencilRef !== 0 && (i.stencilRef = this.stencilRef), this.stencilFuncMask !== 255 && (i.stencilFuncMask = this.stencilFuncMask), this.stencilFail !== kd && (i.stencilFail = this.stencilFail), this.stencilZFail !== kd && (i.stencilZFail = this.stencilZFail), this.stencilZPass !== kd && (i.stencilZPass = this.stencilZPass), this.stencilWrite === !0 && (i.stencilWrite = this.stencilWrite), this.rotation !== void 0 && this.rotation !== 0 && (i.rotation = this.rotation), this.polygonOffset === !0 && (i.polygonOffset = !0), this.polygonOffsetFactor !== 0 && (i.polygonOffsetFactor = this.polygonOffsetFactor), this.polygonOffsetUnits !== 0 && (i.polygonOffsetUnits = this.polygonOffsetUnits), this.linewidth !== void 0 && this.linewidth !== 1 && (i.linewidth = this.linewidth), this.dashSize !== void 0 && (i.dashSize = this.dashSize), this.gapSize !== void 0 && (i.gapSize = this.gapSize), this.scale !== void 0 && (i.scale = this.scale), this.dithering === !0 && (i.dithering = !0), this.alphaTest > 0 && (i.alphaTest = this.alphaTest), this.alphaHash === !0 && (i.alphaHash = !0), this.alphaToCoverage === !0 && (i.alphaToCoverage = !0), this.premultipliedAlpha === !0 && (i.premultipliedAlpha = !0), this.forceSinglePass === !0 && (i.forceSinglePass = !0), this.wireframe === !0 && (i.wireframe = !0), this.wireframeLinewidth > 1 && (i.wireframeLinewidth = this.wireframeLinewidth), this.wireframeLinecap !== "round" && (i.wireframeLinecap = this.wireframeLinecap), this.wireframeLinejoin !== "round" && (i.wireframeLinejoin = this.wireframeLinejoin), this.flatShading === !0 && (i.flatShading = !0), this.visible === !1 && (i.visible = !1), this.toneMapped === !1 && (i.toneMapped = !1), this.fog === !1 && (i.fog = !1), Object.keys(this.userData).length > 0 && (i.userData = this.userData);
    function n(r) {
      const a = [];
      for (const o in r) {
        const l = r[o];
        delete l.metadata, a.push(l);
      }
      return a;
    }
    if (t) {
      const r = n(e.textures), a = n(e.images);
      r.length > 0 && (i.textures = r), a.length > 0 && (i.images = a);
    }
    return i;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.name = e.name, this.blending = e.blending, this.side = e.side, this.vertexColors = e.vertexColors, this.opacity = e.opacity, this.transparent = e.transparent, this.blendSrc = e.blendSrc, this.blendDst = e.blendDst, this.blendEquation = e.blendEquation, this.blendSrcAlpha = e.blendSrcAlpha, this.blendDstAlpha = e.blendDstAlpha, this.blendEquationAlpha = e.blendEquationAlpha, this.blendColor.copy(e.blendColor), this.blendAlpha = e.blendAlpha, this.depthFunc = e.depthFunc, this.depthTest = e.depthTest, this.depthWrite = e.depthWrite, this.stencilWriteMask = e.stencilWriteMask, this.stencilFunc = e.stencilFunc, this.stencilRef = e.stencilRef, this.stencilFuncMask = e.stencilFuncMask, this.stencilFail = e.stencilFail, this.stencilZFail = e.stencilZFail, this.stencilZPass = e.stencilZPass, this.stencilWrite = e.stencilWrite;
    const t = e.clippingPlanes;
    let i = null;
    if (t !== null) {
      const n = t.length;
      i = new Array(n);
      for (let r = 0; r !== n; ++r)
        i[r] = t[r].clone();
    }
    return this.clippingPlanes = i, this.clipIntersection = e.clipIntersection, this.clipShadows = e.clipShadows, this.shadowSide = e.shadowSide, this.colorWrite = e.colorWrite, this.precision = e.precision, this.polygonOffset = e.polygonOffset, this.polygonOffsetFactor = e.polygonOffsetFactor, this.polygonOffsetUnits = e.polygonOffsetUnits, this.dithering = e.dithering, this.alphaTest = e.alphaTest, this.alphaHash = e.alphaHash, this.alphaToCoverage = e.alphaToCoverage, this.premultipliedAlpha = e.premultipliedAlpha, this.forceSinglePass = e.forceSinglePass, this.visible = e.visible, this.toneMapped = e.toneMapped, this.userData = JSON.parse(JSON.stringify(e.userData)), this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  onBuild() {
    console.warn("Material: onBuild() has been removed.");
  }
  onBeforeRender() {
    console.warn("Material: onBeforeRender() has been removed.");
  }
}
function Up(s) {
  const e = {};
  for (const t in s) {
    e[t] = {};
    for (const i in s[t]) {
      const n = s[t][i];
      n && (n.isColor || n.isMatrix3 || n.isMatrix4 || n.isVector2 || n.isVector3 || n.isVector4 || n.isTexture || n.isQuaternion) ? n.isRenderTargetTexture ? (console.warn("UniformsUtils: Textures of render targets cannot be cloned via cloneUniforms() or mergeUniforms()."), e[t][i] = null) : e[t][i] = n.clone() : Array.isArray(n) ? e[t][i] = n.slice() : e[t][i] = n;
    }
  }
  return e;
}
function fr(s) {
  const e = {};
  for (let t = 0; t < s.length; t++) {
    const i = Up(s[t]);
    for (const n in i)
      e[n] = i[n];
  }
  return e;
}
function Wve(s) {
  const e = [];
  for (let t = 0; t < s.length; t++)
    e.push(s[t].clone());
  return e;
}
function dU(s) {
  const e = s.getRenderTarget();
  return e === null ? s.outputColorSpace : e.isXRRenderTarget === !0 ? e.texture.colorSpace : rn.workingColorSpace;
}
const Xve = { clone: Up, merge: fr }, jve = (
  /* glsl */
  `
void main() {
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`
), qve = (
  /* glsl */
  `
void main() {
	gl_FragColor = vec4( 1.0, 0.0, 0.0, 1.0 );
}
`
);
class Jc extends m8 {
  constructor(e) {
    super(), this.isShaderMaterial = !0, this.type = "ShaderMaterial", this.defines = {}, this.uniforms = {}, this.uniformsGroups = [], this.vertexShader = jve, this.fragmentShader = qve, this.linewidth = 1, this.wireframe = !1, this.wireframeLinewidth = 1, this.fog = !1, this.lights = !1, this.clipping = !1, this.forceSinglePass = !0, this.extensions = {
      clipCullDistance: !1,
      // set to use vertex shader clipping
      multiDraw: !1
      // set to use vertex shader multi_draw / enable gl_DrawID
    }, this.defaultAttributeValues = {
      color: [1, 1, 1],
      uv: [0, 0],
      uv1: [0, 0]
    }, this.index0AttributeName = void 0, this.uniformsNeedUpdate = !1, this.glslVersion = null, e !== void 0 && this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.fragmentShader = e.fragmentShader, this.vertexShader = e.vertexShader, this.uniforms = Up(e.uniforms), this.uniformsGroups = Wve(e.uniformsGroups), this.defines = Object.assign({}, e.defines), this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.fog = e.fog, this.lights = e.lights, this.clipping = e.clipping, this.extensions = Object.assign({}, e.extensions), this.glslVersion = e.glslVersion, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    t.glslVersion = this.glslVersion, t.uniforms = {};
    for (const n in this.uniforms) {
      const a = this.uniforms[n].value;
      a && a.isTexture ? t.uniforms[n] = {
        type: "t",
        value: a.toJSON(e).uuid
      } : a && a.isColor ? t.uniforms[n] = {
        type: "c",
        value: a.getHex()
      } : a && a.isVector2 ? t.uniforms[n] = {
        type: "v2",
        value: a.toArray()
      } : a && a.isVector3 ? t.uniforms[n] = {
        type: "v3",
        value: a.toArray()
      } : a && a.isVector4 ? t.uniforms[n] = {
        type: "v4",
        value: a.toArray()
      } : a && a.isMatrix3 ? t.uniforms[n] = {
        type: "m3",
        value: a.toArray()
      } : a && a.isMatrix4 ? t.uniforms[n] = {
        type: "m4",
        value: a.toArray()
      } : t.uniforms[n] = {
        value: a
      };
    }
    Object.keys(this.defines).length > 0 && (t.defines = this.defines), t.vertexShader = this.vertexShader, t.fragmentShader = this.fragmentShader, t.lights = this.lights, t.clipping = this.clipping;
    const i = {};
    for (const n in this.extensions)
      this.extensions[n] === !0 && (i[n] = !0);
    return Object.keys(i).length > 0 && (t.extensions = i), t;
  }
}
const N0 = /* @__PURE__ */ new je(), n6 = /* @__PURE__ */ new je(), H3 = /* @__PURE__ */ new je(), $l = /* @__PURE__ */ new je(), s6 = /* @__PURE__ */ new je(), V3 = /* @__PURE__ */ new je(), r6 = /* @__PURE__ */ new je();
class Yve {
  constructor(e = new je(), t = new je(0, 0, -1)) {
    this.origin = e, this.direction = t;
  }
  set(e, t) {
    return this.origin.copy(e), this.direction.copy(t), this;
  }
  copy(e) {
    return this.origin.copy(e.origin), this.direction.copy(e.direction), this;
  }
  at(e, t) {
    return t.copy(this.origin).addScaledVector(this.direction, e);
  }
  lookAt(e) {
    return this.direction.copy(e).sub(this.origin).normalize(), this;
  }
  recast(e) {
    return this.origin.copy(this.at(e, N0)), this;
  }
  closestPointToPoint(e, t) {
    t.subVectors(e, this.origin);
    const i = t.dot(this.direction);
    return i < 0 ? t.copy(this.origin) : t.copy(this.origin).addScaledVector(this.direction, i);
  }
  distanceToPoint(e) {
    return Math.sqrt(this.distanceSqToPoint(e));
  }
  distanceSqToPoint(e) {
    const t = N0.subVectors(e, this.origin).dot(this.direction);
    return t < 0 ? this.origin.distanceToSquared(e) : (N0.copy(this.origin).addScaledVector(this.direction, t), N0.distanceToSquared(e));
  }
  distanceSqToSegment(e, t, i, n) {
    n6.copy(e).add(t).multiplyScalar(0.5), H3.copy(t).sub(e).normalize(), $l.copy(this.origin).sub(n6);
    const r = e.distanceTo(t) * 0.5, a = -this.direction.dot(H3), o = $l.dot(this.direction), l = -$l.dot(H3), c = $l.lengthSq(), h = Math.abs(1 - a * a);
    let u, d, f, p;
    if (h > 0)
      if (u = a * l - o, d = a * o - l, p = r * h, u >= 0)
        if (d >= -p)
          if (d <= p) {
            const v = 1 / h;
            u *= v, d *= v, f = u * (u + a * d + 2 * o) + d * (a * u + d + 2 * l) + c;
          } else
            d = r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
        else
          d = -r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
      else
        d <= -p ? (u = Math.max(0, -(-a * r + o)), d = u > 0 ? -r : Math.min(Math.max(-r, -l), r), f = -u * u + d * (d + 2 * l) + c) : d <= p ? (u = 0, d = Math.min(Math.max(-r, -l), r), f = d * (d + 2 * l) + c) : (u = Math.max(0, -(a * r + o)), d = u > 0 ? r : Math.min(Math.max(-r, -l), r), f = -u * u + d * (d + 2 * l) + c);
    else
      d = a > 0 ? -r : r, u = Math.max(0, -(a * d + o)), f = -u * u + d * (d + 2 * l) + c;
    return i && i.copy(this.origin).addScaledVector(this.direction, u), n && n.copy(n6).addScaledVector(H3, d), f;
  }
  intersectSphere(e, t) {
    N0.subVectors(e.center, this.origin);
    const i = N0.dot(this.direction), n = N0.dot(N0) - i * i, r = e.radius * e.radius;
    if (n > r)
      return null;
    const a = Math.sqrt(r - n), o = i - a, l = i + a;
    return l < 0 ? null : o < 0 ? this.at(l, t) : this.at(o, t);
  }
  intersectsSphere(e) {
    return this.distanceSqToPoint(e.center) <= e.radius * e.radius;
  }
  distanceToPlane(e) {
    const t = e.normal.dot(this.direction);
    if (t === 0)
      return e.distanceToPoint(this.origin) === 0 ? 0 : null;
    const i = -(this.origin.dot(e.normal) + e.constant) / t;
    return i >= 0 ? i : null;
  }
  intersectPlane(e, t) {
    const i = this.distanceToPlane(e);
    return i === null ? null : this.at(i, t);
  }
  intersectsPlane(e) {
    const t = e.distanceToPoint(this.origin);
    return t === 0 || e.normal.dot(this.direction) * t < 0;
  }
  intersectBox(e, t) {
    let i, n, r, a, o, l;
    const c = 1 / this.direction.x, h = 1 / this.direction.y, u = 1 / this.direction.z, d = this.origin;
    return c >= 0 ? (i = (e.min.x - d.x) * c, n = (e.max.x - d.x) * c) : (i = (e.max.x - d.x) * c, n = (e.min.x - d.x) * c), h >= 0 ? (r = (e.min.y - d.y) * h, a = (e.max.y - d.y) * h) : (r = (e.max.y - d.y) * h, a = (e.min.y - d.y) * h), i > a || r > n || ((r > i || isNaN(i)) && (i = r), (a < n || isNaN(n)) && (n = a), u >= 0 ? (o = (e.min.z - d.z) * u, l = (e.max.z - d.z) * u) : (o = (e.max.z - d.z) * u, l = (e.min.z - d.z) * u), i > l || o > n) || ((o > i || i !== i) && (i = o), (l < n || n !== n) && (n = l), n < 0) ? null : this.at(i >= 0 ? i : n, t);
  }
  intersectsBox(e) {
    return this.intersectBox(e, N0) !== null;
  }
  intersectTriangle(e, t, i, n, r) {
    s6.subVectors(t, e), V3.subVectors(i, e), r6.crossVectors(s6, V3);
    let a = this.direction.dot(r6), o;
    if (a > 0) {
      if (n)
        return null;
      o = 1;
    } else if (a < 0)
      o = -1, a = -a;
    else
      return null;
    $l.subVectors(this.origin, e);
    const l = o * this.direction.dot(V3.crossVectors($l, V3));
    if (l < 0)
      return null;
    const c = o * this.direction.dot(s6.cross($l));
    if (c < 0 || l + c > a)
      return null;
    const h = -o * $l.dot(r6);
    return h < 0 ? null : this.at(h / a, r);
  }
  applyMatrix4(e) {
    return this.origin.applyMatrix4(e), this.direction.transformDirection(e), this;
  }
  equals(e) {
    return e.origin.equals(this.origin) && e.direction.equals(this.direction);
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const Ga = /* @__PURE__ */ new je(), B0 = /* @__PURE__ */ new je(), a6 = /* @__PURE__ */ new je(), F0 = /* @__PURE__ */ new je(), Yd = /* @__PURE__ */ new je(), Zd = /* @__PURE__ */ new je(), PR = /* @__PURE__ */ new je(), o6 = /* @__PURE__ */ new je(), l6 = /* @__PURE__ */ new je(), c6 = /* @__PURE__ */ new je();
class ko {
  constructor(e = new je(), t = new je(), i = new je()) {
    this.a = e, this.b = t, this.c = i;
  }
  static getNormal(e, t, i, n) {
    n.subVectors(i, t), Ga.subVectors(e, t), n.cross(Ga);
    const r = n.lengthSq();
    return r > 0 ? n.multiplyScalar(1 / Math.sqrt(r)) : n.set(0, 0, 0);
  }
  // static/instance method to calculate barycentric coordinates
  // based on: http://www.blackpawn.com/texts/pointinpoly/default.html
  static getBarycoord(e, t, i, n, r) {
    Ga.subVectors(n, t), B0.subVectors(i, t), a6.subVectors(e, t);
    const a = Ga.dot(Ga), o = Ga.dot(B0), l = Ga.dot(a6), c = B0.dot(B0), h = B0.dot(a6), u = a * c - o * o;
    if (u === 0)
      return r.set(0, 0, 0), null;
    const d = 1 / u, f = (c * l - o * h) * d, p = (a * h - o * l) * d;
    return r.set(1 - f - p, p, f);
  }
  static containsPoint(e, t, i, n) {
    return this.getBarycoord(e, t, i, n, F0) === null ? !1 : F0.x >= 0 && F0.y >= 0 && F0.x + F0.y <= 1;
  }
  static getInterpolation(e, t, i, n, r, a, o, l) {
    return this.getBarycoord(e, t, i, n, F0) === null ? (l.x = 0, l.y = 0, "z" in l && (l.z = 0), "w" in l && (l.w = 0), null) : (l.setScalar(0), l.addScaledVector(r, F0.x), l.addScaledVector(a, F0.y), l.addScaledVector(o, F0.z), l);
  }
  static isFrontFacing(e, t, i, n) {
    return Ga.subVectors(i, t), B0.subVectors(e, t), Ga.cross(B0).dot(n) < 0;
  }
  set(e, t, i) {
    return this.a.copy(e), this.b.copy(t), this.c.copy(i), this;
  }
  setFromPointsAndIndices(e, t, i, n) {
    return this.a.copy(e[t]), this.b.copy(e[i]), this.c.copy(e[n]), this;
  }
  setFromAttributeAndIndices(e, t, i, n) {
    return this.a.fromBufferAttribute(e, t), this.b.fromBufferAttribute(e, i), this.c.fromBufferAttribute(e, n), this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.a.copy(e.a), this.b.copy(e.b), this.c.copy(e.c), this;
  }
  getArea() {
    return Ga.subVectors(this.c, this.b), B0.subVectors(this.a, this.b), Ga.cross(B0).length() * 0.5;
  }
  getMidpoint(e) {
    return e.addVectors(this.a, this.b).add(this.c).multiplyScalar(1 / 3);
  }
  getNormal(e) {
    return ko.getNormal(this.a, this.b, this.c, e);
  }
  getPlane(e) {
    return e.setFromCoplanarPoints(this.a, this.b, this.c);
  }
  getBarycoord(e, t) {
    return ko.getBarycoord(e, this.a, this.b, this.c, t);
  }
  getInterpolation(e, t, i, n, r) {
    return ko.getInterpolation(e, this.a, this.b, this.c, t, i, n, r);
  }
  containsPoint(e) {
    return ko.containsPoint(e, this.a, this.b, this.c);
  }
  isFrontFacing(e) {
    return ko.isFrontFacing(this.a, this.b, this.c, e);
  }
  intersectsBox(e) {
    return e.intersectsTriangle(this);
  }
  closestPointToPoint(e, t) {
    const i = this.a, n = this.b, r = this.c;
    let a, o;
    Yd.subVectors(n, i), Zd.subVectors(r, i), o6.subVectors(e, i);
    const l = Yd.dot(o6), c = Zd.dot(o6);
    if (l <= 0 && c <= 0)
      return t.copy(i);
    l6.subVectors(e, n);
    const h = Yd.dot(l6), u = Zd.dot(l6);
    if (h >= 0 && u <= h)
      return t.copy(n);
    const d = l * u - h * c;
    if (d <= 0 && l >= 0 && h <= 0)
      return a = l / (l - h), t.copy(i).addScaledVector(Yd, a);
    c6.subVectors(e, r);
    const f = Yd.dot(c6), p = Zd.dot(c6);
    if (p >= 0 && f <= p)
      return t.copy(r);
    const v = f * c - l * p;
    if (v <= 0 && c >= 0 && p <= 0)
      return o = c / (c - p), t.copy(i).addScaledVector(Zd, o);
    const g = h * p - f * u;
    if (g <= 0 && u - h >= 0 && f - p >= 0)
      return PR.subVectors(r, n), o = (u - h) / (u - h + (f - p)), t.copy(n).addScaledVector(PR, o);
    const m = 1 / (g + v + d);
    return a = v * m, o = d * m, t.copy(i).addScaledVector(Yd, a).addScaledVector(Zd, o);
  }
  equals(e) {
    return e.a.equals(this.a) && e.b.equals(this.b) && e.c.equals(this.c);
  }
}
class fU extends m8 {
  constructor(e) {
    super(), this.isMeshBasicMaterial = !0, this.type = "MeshBasicMaterial", this.color = new hn(16777215), this.map = null, this.lightMap = null, this.lightMapIntensity = 1, this.aoMap = null, this.aoMapIntensity = 1, this.specularMap = null, this.alphaMap = null, this.envMap = null, this.envMapRotation = new id(), this.combine = XF, this.reflectivity = 1, this.refractionRatio = 0.98, this.wireframe = !1, this.wireframeLinewidth = 1, this.wireframeLinecap = "round", this.wireframeLinejoin = "round", this.fog = !0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.color.copy(e.color), this.map = e.map, this.lightMap = e.lightMap, this.lightMapIntensity = e.lightMapIntensity, this.aoMap = e.aoMap, this.aoMapIntensity = e.aoMapIntensity, this.specularMap = e.specularMap, this.alphaMap = e.alphaMap, this.envMap = e.envMap, this.envMapRotation.copy(e.envMapRotation), this.combine = e.combine, this.reflectivity = e.reflectivity, this.refractionRatio = e.refractionRatio, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this.wireframeLinecap = e.wireframeLinecap, this.wireframeLinejoin = e.wireframeLinejoin, this.fog = e.fog, this;
  }
}
const RR = /* @__PURE__ */ new ps(), kh = /* @__PURE__ */ new Yve(), W3 = /* @__PURE__ */ new Nw(), DR = /* @__PURE__ */ new je(), Kd = /* @__PURE__ */ new je(), Jd = /* @__PURE__ */ new je(), Qd = /* @__PURE__ */ new je(), h6 = /* @__PURE__ */ new je(), X3 = /* @__PURE__ */ new je(), j3 = /* @__PURE__ */ new tn(), q3 = /* @__PURE__ */ new tn(), Y3 = /* @__PURE__ */ new tn(), LR = /* @__PURE__ */ new je(), IR = /* @__PURE__ */ new je(), OR = /* @__PURE__ */ new je(), Z3 = /* @__PURE__ */ new je(), K3 = /* @__PURE__ */ new je();
class al extends Ta {
  constructor(e = new nd(), t = new fU()) {
    super(), this.isMesh = !0, this.type = "Mesh", this.geometry = e, this.material = t, this.updateMorphTargets();
  }
  copy(e, t) {
    return super.copy(e, t), e.morphTargetInfluences !== void 0 && (this.morphTargetInfluences = e.morphTargetInfluences.slice()), e.morphTargetDictionary !== void 0 && (this.morphTargetDictionary = Object.assign({}, e.morphTargetDictionary)), this.material = Array.isArray(e.material) ? e.material.slice() : e.material, this.geometry = e.geometry, this;
  }
  updateMorphTargets() {
    const t = this.geometry.morphAttributes, i = Object.keys(t);
    if (i.length > 0) {
      const n = t[i[0]];
      if (n !== void 0) {
        this.morphTargetInfluences = [], this.morphTargetDictionary = {};
        for (let r = 0, a = n.length; r < a; r++) {
          const o = n[r].name || String(r);
          this.morphTargetInfluences.push(0), this.morphTargetDictionary[o] = r;
        }
      }
    }
  }
  getVertexPosition(e, t) {
    const i = this.geometry, n = i.attributes.position, r = i.morphAttributes.position, a = i.morphTargetsRelative;
    t.fromBufferAttribute(n, e);
    const o = this.morphTargetInfluences;
    if (r && o) {
      X3.set(0, 0, 0);
      for (let l = 0, c = r.length; l < c; l++) {
        const h = o[l], u = r[l];
        h !== 0 && (h6.fromBufferAttribute(u, e), a ? X3.addScaledVector(h6, h) : X3.addScaledVector(h6.sub(t), h));
      }
      t.add(X3);
    }
    return t;
  }
  raycast(e, t) {
    const i = this.geometry, n = this.material, r = this.matrixWorld;
    n !== void 0 && (i.boundingSphere === null && i.computeBoundingSphere(), W3.copy(i.boundingSphere), W3.applyMatrix4(r), kh.copy(e.ray).recast(e.near), !(W3.containsPoint(kh.origin) === !1 && (kh.intersectSphere(W3, DR) === null || kh.origin.distanceToSquared(DR) > (e.far - e.near) ** 2)) && (RR.copy(r).invert(), kh.copy(e.ray).applyMatrix4(RR), !(i.boundingBox !== null && kh.intersectsBox(i.boundingBox) === !1) && this._computeIntersections(e, t, kh)));
  }
  _computeIntersections(e, t, i) {
    let n;
    const r = this.geometry, a = this.material, o = r.index, l = r.attributes.position, c = r.attributes.uv, h = r.attributes.uv1, u = r.attributes.normal, d = r.groups, f = r.drawRange;
    if (o !== null)
      if (Array.isArray(a))
        for (let p = 0, v = d.length; p < v; p++) {
          const g = d[p], m = a[g.materialIndex], x = Math.max(g.start, f.start), y = Math.min(o.count, Math.min(g.start + g.count, f.start + f.count));
          for (let A = x, b = y; A < b; A += 3) {
            const M = o.getX(A), w = o.getX(A + 1), C = o.getX(A + 2);
            n = J3(this, m, e, i, c, h, u, M, w, C), n && (n.faceIndex = Math.floor(A / 3), n.face.materialIndex = g.materialIndex, t.push(n));
          }
        }
      else {
        const p = Math.max(0, f.start), v = Math.min(o.count, f.start + f.count);
        for (let g = p, m = v; g < m; g += 3) {
          const x = o.getX(g), y = o.getX(g + 1), A = o.getX(g + 2);
          n = J3(this, a, e, i, c, h, u, x, y, A), n && (n.faceIndex = Math.floor(g / 3), t.push(n));
        }
      }
    else if (l !== void 0)
      if (Array.isArray(a))
        for (let p = 0, v = d.length; p < v; p++) {
          const g = d[p], m = a[g.materialIndex], x = Math.max(g.start, f.start), y = Math.min(l.count, Math.min(g.start + g.count, f.start + f.count));
          for (let A = x, b = y; A < b; A += 3) {
            const M = A, w = A + 1, C = A + 2;
            n = J3(this, m, e, i, c, h, u, M, w, C), n && (n.faceIndex = Math.floor(A / 3), n.face.materialIndex = g.materialIndex, t.push(n));
          }
        }
      else {
        const p = Math.max(0, f.start), v = Math.min(l.count, f.start + f.count);
        for (let g = p, m = v; g < m; g += 3) {
          const x = g, y = g + 1, A = g + 2;
          n = J3(this, a, e, i, c, h, u, x, y, A), n && (n.faceIndex = Math.floor(g / 3), t.push(n));
        }
      }
  }
}
function Zve(s, e, t, i, n, r, a, o) {
  let l;
  if (e.side === Fr ? l = i.intersectTriangle(a, r, n, !0, o) : l = i.intersectTriangle(n, r, a, e.side === Kc, o), l === null)
    return null;
  K3.copy(o), K3.applyMatrix4(s.matrixWorld);
  const c = t.ray.origin.distanceTo(K3);
  return c < t.near || c > t.far ? null : {
    distance: c,
    point: K3.clone(),
    object: s
  };
}
function J3(s, e, t, i, n, r, a, o, l, c) {
  s.getVertexPosition(o, Kd), s.getVertexPosition(l, Jd), s.getVertexPosition(c, Qd);
  const h = Zve(s, e, t, i, Kd, Jd, Qd, Z3);
  if (h) {
    n && (j3.fromBufferAttribute(n, o), q3.fromBufferAttribute(n, l), Y3.fromBufferAttribute(n, c), h.uv = ko.getInterpolation(Z3, Kd, Jd, Qd, j3, q3, Y3, new tn())), r && (j3.fromBufferAttribute(r, o), q3.fromBufferAttribute(r, l), Y3.fromBufferAttribute(r, c), h.uv1 = ko.getInterpolation(Z3, Kd, Jd, Qd, j3, q3, Y3, new tn())), a && (LR.fromBufferAttribute(a, o), IR.fromBufferAttribute(a, l), OR.fromBufferAttribute(a, c), h.normal = ko.getInterpolation(Z3, Kd, Jd, Qd, LR, IR, OR, new je()), h.normal.dot(i.direction) > 0 && h.normal.multiplyScalar(-1));
    const u = {
      a: o,
      b: l,
      c,
      normal: new je(),
      materialIndex: 0
    };
    ko.getNormal(Kd, Jd, Qd, u.normal), h.face = u;
  }
  return h;
}
const Kve = (
  /* glsl */
  `
#ifdef USE_ALPHAHASH

	if ( diffuseColor.a < getAlphaHashThreshold( vPosition ) ) discard;

#endif
`
), Jve = (
  /* glsl */
  `
#ifdef USE_ALPHAHASH

	/**
	 * See: https://casual-effects.com/research/Wyman2017Hashed/index.html
	 */

	const float ALPHA_HASH_SCALE = 0.05; // Derived from trials only, and may be changed.

	float hash2D( vec2 value ) {

		return fract( 1.0e4 * sin( 17.0 * value.x + 0.1 * value.y ) * ( 0.1 + abs( sin( 13.0 * value.y + value.x ) ) ) );

	}

	float hash3D( vec3 value ) {

		return hash2D( vec2( hash2D( value.xy ), value.z ) );

	}

	float getAlphaHashThreshold( vec3 position ) {

		// Find the discretized derivatives of our coordinates
		float maxDeriv = max(
			length( dFdx( position.xyz ) ),
			length( dFdy( position.xyz ) )
		);
		float pixScale = 1.0 / ( ALPHA_HASH_SCALE * maxDeriv );

		// Find two nearest log-discretized noise scales
		vec2 pixScales = vec2(
			exp2( floor( log2( pixScale ) ) ),
			exp2( ceil( log2( pixScale ) ) )
		);

		// Compute alpha thresholds at our two noise scales
		vec2 alpha = vec2(
			hash3D( floor( pixScales.x * position.xyz ) ),
			hash3D( floor( pixScales.y * position.xyz ) )
		);

		// Factor to interpolate lerp with
		float lerpFactor = fract( log2( pixScale ) );

		// Interpolate alpha threshold from noise at two scales
		float x = ( 1.0 - lerpFactor ) * alpha.x + lerpFactor * alpha.y;

		// Pass into CDF to compute uniformly distrib threshold
		float a = min( lerpFactor, 1.0 - lerpFactor );
		vec3 cases = vec3(
			x * x / ( 2.0 * a * ( 1.0 - a ) ),
			( x - 0.5 * a ) / ( 1.0 - a ),
			1.0 - ( ( 1.0 - x ) * ( 1.0 - x ) / ( 2.0 * a * ( 1.0 - a ) ) )
		);

		// Find our final, uniformly distributed alpha threshold (ατ)
		float threshold = ( x < ( 1.0 - a ) )
			? ( ( x < a ) ? cases.x : cases.y )
			: cases.z;

		// Avoids ατ == 0. Could also do ατ =1-ατ
		return clamp( threshold , 1.0e-6, 1.0 );

	}

#endif
`
), Qve = (
  /* glsl */
  `
#ifdef USE_ALPHAMAP

	diffuseColor.a *= texture2D( alphaMap, vAlphaMapUv ).g;

#endif
`
), $ve = (
  /* glsl */
  `
#ifdef USE_ALPHAMAP

	uniform sampler2D alphaMap;

#endif
`
), e3e = (
  /* glsl */
  `
#ifdef USE_ALPHATEST

	#ifdef ALPHA_TO_COVERAGE

	diffuseColor.a = smoothstep( alphaTest, alphaTest + fwidth( diffuseColor.a ), diffuseColor.a );
	if ( diffuseColor.a == 0.0 ) discard;

	#else

	if ( diffuseColor.a < alphaTest ) discard;

	#endif

#endif
`
), t3e = (
  /* glsl */
  `
#ifdef USE_ALPHATEST
	uniform float alphaTest;
#endif
`
), i3e = (
  /* glsl */
  `
#ifdef USE_AOMAP

	// reads channel R, compatible with a combined OcclusionRoughnessMetallic (RGB) texture
	float ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;

	reflectedLight.indirectDiffuse *= ambientOcclusion;

	#if defined( USE_CLEARCOAT ) 
		clearcoatSpecularIndirect *= ambientOcclusion;
	#endif

	#if defined( USE_SHEEN ) 
		sheenSpecularIndirect *= ambientOcclusion;
	#endif

	#if defined( USE_ENVMAP ) && defined( STANDARD )

		float dotNV = saturate( dot( geometryNormal, geometryViewDir ) );

		reflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV, ambientOcclusion, material.roughness );

	#endif

#endif
`
), n3e = (
  /* glsl */
  `
#ifdef USE_AOMAP

	uniform sampler2D aoMap;
	uniform float aoMapIntensity;

#endif
`
), s3e = (
  /* glsl */
  `
#ifdef USE_BATCHING
	#if ! defined( GL_ANGLE_multi_draw )
	#define gl_DrawID _gl_DrawID
	uniform int _gl_DrawID;
	#endif

	uniform highp sampler2D batchingTexture;
	uniform highp usampler2D batchingIdTexture;
	mat4 getBatchingMatrix( const in float i ) {

		int size = textureSize( batchingTexture, 0 ).x;
		int j = int( i ) * 4;
		int x = j % size;
		int y = j / size;
		vec4 v1 = texelFetch( batchingTexture, ivec2( x, y ), 0 );
		vec4 v2 = texelFetch( batchingTexture, ivec2( x + 1, y ), 0 );
		vec4 v3 = texelFetch( batchingTexture, ivec2( x + 2, y ), 0 );
		vec4 v4 = texelFetch( batchingTexture, ivec2( x + 3, y ), 0 );
		return mat4( v1, v2, v3, v4 );

	}

	float getIndirectIndex( const in int i ) {

		int size = textureSize( batchingIdTexture, 0 ).x;
		int x = i % size;
		int y = i / size;
		return float( texelFetch( batchingIdTexture, ivec2( x, y ), 0 ).r );

	}

#endif

#ifdef USE_BATCHING_COLOR

	uniform sampler2D batchingColorTexture;
	vec3 getBatchingColor( const in float i ) {

		int size = textureSize( batchingColorTexture, 0 ).x;
		int j = int( i );
		int x = j % size;
		int y = j / size;
		return texelFetch( batchingColorTexture, ivec2( x, y ), 0 ).rgb;

	}

#endif
`
), r3e = (
  /* glsl */
  `
#ifdef USE_BATCHING
	mat4 batchingMatrix = getBatchingMatrix( getIndirectIndex( gl_DrawID ) );
#endif
`
), a3e = (
  /* glsl */
  `
vec3 transformed = vec3( position );

#ifdef USE_ALPHAHASH

	vPosition = vec3( position );

#endif
`
), o3e = (
  /* glsl */
  `
vec3 objectNormal = vec3( normal );

#ifdef USE_TANGENT

	vec3 objectTangent = vec3( tangent.xyz );

#endif
`
), l3e = (
  /* glsl */
  `

float G_BlinnPhong_Implicit( /* const in float dotNL, const in float dotNV */ ) {

	// geometry term is (n dot l)(n dot v) / 4(n dot l)(n dot v)
	return 0.25;

}

float D_BlinnPhong( const in float shininess, const in float dotNH ) {

	return RECIPROCAL_PI * ( shininess * 0.5 + 1.0 ) * pow( dotNH, shininess );

}

vec3 BRDF_BlinnPhong( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 specularColor, const in float shininess ) {

	vec3 halfDir = normalize( lightDir + viewDir );

	float dotNH = saturate( dot( normal, halfDir ) );
	float dotVH = saturate( dot( viewDir, halfDir ) );

	vec3 F = F_Schlick( specularColor, 1.0, dotVH );

	float G = G_BlinnPhong_Implicit( /* dotNL, dotNV */ );

	float D = D_BlinnPhong( shininess, dotNH );

	return F * ( G * D );

} // validated

`
), c3e = (
  /* glsl */
  `

#ifdef USE_IRIDESCENCE

	// XYZ to linear-sRGB color space
	const mat3 XYZ_TO_REC709 = mat3(
		 3.2404542, -0.9692660,  0.0556434,
		-1.5371385,  1.8760108, -0.2040259,
		-0.4985314,  0.0415560,  1.0572252
	);

	// Assume air interface for top
	// Note: We don't handle the case fresnel0 == 1
	vec3 Fresnel0ToIor( vec3 fresnel0 ) {

		vec3 sqrtF0 = sqrt( fresnel0 );
		return ( vec3( 1.0 ) + sqrtF0 ) / ( vec3( 1.0 ) - sqrtF0 );

	}

	// Conversion FO/IOR
	vec3 IorToFresnel0( vec3 transmittedIor, float incidentIor ) {

		return pow2( ( transmittedIor - vec3( incidentIor ) ) / ( transmittedIor + vec3( incidentIor ) ) );

	}

	// ior is a value between 1.0 and 3.0. 1.0 is air interface
	float IorToFresnel0( float transmittedIor, float incidentIor ) {

		return pow2( ( transmittedIor - incidentIor ) / ( transmittedIor + incidentIor ));

	}

	// Fresnel equations for dielectric/dielectric interfaces.
	// Ref: https://belcour.github.io/blog/research/2017/05/01/brdf-thin-film.html
	// Evaluation XYZ sensitivity curves in Fourier space
	vec3 evalSensitivity( float OPD, vec3 shift ) {

		float phase = 2.0 * PI * OPD * 1.0e-9;
		vec3 val = vec3( 5.4856e-13, 4.4201e-13, 5.2481e-13 );
		vec3 pos = vec3( 1.6810e+06, 1.7953e+06, 2.2084e+06 );
		vec3 var = vec3( 4.3278e+09, 9.3046e+09, 6.6121e+09 );

		vec3 xyz = val * sqrt( 2.0 * PI * var ) * cos( pos * phase + shift ) * exp( - pow2( phase ) * var );
		xyz.x += 9.7470e-14 * sqrt( 2.0 * PI * 4.5282e+09 ) * cos( 2.2399e+06 * phase + shift[ 0 ] ) * exp( - 4.5282e+09 * pow2( phase ) );
		xyz /= 1.0685e-7;

		vec3 rgb = XYZ_TO_REC709 * xyz;
		return rgb;

	}

	vec3 evalIridescence( float outsideIOR, float eta2, float cosTheta1, float thinFilmThickness, vec3 baseF0 ) {

		vec3 I;

		// Force iridescenceIOR -> outsideIOR when thinFilmThickness -> 0.0
		float iridescenceIOR = mix( outsideIOR, eta2, smoothstep( 0.0, 0.03, thinFilmThickness ) );
		// Evaluate the cosTheta on the base layer (Snell law)
		float sinTheta2Sq = pow2( outsideIOR / iridescenceIOR ) * ( 1.0 - pow2( cosTheta1 ) );

		// Handle TIR:
		float cosTheta2Sq = 1.0 - sinTheta2Sq;
		if ( cosTheta2Sq < 0.0 ) {

			return vec3( 1.0 );

		}

		float cosTheta2 = sqrt( cosTheta2Sq );

		// First interface
		float R0 = IorToFresnel0( iridescenceIOR, outsideIOR );
		float R12 = F_Schlick( R0, 1.0, cosTheta1 );
		float T121 = 1.0 - R12;
		float phi12 = 0.0;
		if ( iridescenceIOR < outsideIOR ) phi12 = PI;
		float phi21 = PI - phi12;

		// Second interface
		vec3 baseIOR = Fresnel0ToIor( clamp( baseF0, 0.0, 0.9999 ) ); // guard against 1.0
		vec3 R1 = IorToFresnel0( baseIOR, iridescenceIOR );
		vec3 R23 = F_Schlick( R1, 1.0, cosTheta2 );
		vec3 phi23 = vec3( 0.0 );
		if ( baseIOR[ 0 ] < iridescenceIOR ) phi23[ 0 ] = PI;
		if ( baseIOR[ 1 ] < iridescenceIOR ) phi23[ 1 ] = PI;
		if ( baseIOR[ 2 ] < iridescenceIOR ) phi23[ 2 ] = PI;

		// Phase shift
		float OPD = 2.0 * iridescenceIOR * thinFilmThickness * cosTheta2;
		vec3 phi = vec3( phi21 ) + phi23;

		// Compound terms
		vec3 R123 = clamp( R12 * R23, 1e-5, 0.9999 );
		vec3 r123 = sqrt( R123 );
		vec3 Rs = pow2( T121 ) * R23 / ( vec3( 1.0 ) - R123 );

		// Reflectance term for m = 0 (DC term amplitude)
		vec3 C0 = R12 + Rs;
		I = C0;

		// Reflectance term for m > 0 (pairs of diracs)
		vec3 Cm = Rs - T121;
		for ( int m = 1; m <= 2; ++ m ) {

			Cm *= r123;
			vec3 Sm = 2.0 * evalSensitivity( float( m ) * OPD, float( m ) * phi );
			I += Cm * Sm;

		}

		// Since out of gamut colors might be produced, negative color values are clamped to 0.
		return max( I, vec3( 0.0 ) );

	}

#endif

`
), h3e = (
  /* glsl */
  `
#ifdef USE_BUMPMAP

	uniform sampler2D bumpMap;
	uniform float bumpScale;

	// Bump Mapping Unparametrized Surfaces on the GPU by Morten S. Mikkelsen
	// https://mmikk.github.io/papers3d/mm_sfgrad_bump.pdf

	// Evaluate the derivative of the height w.r.t. screen-space using forward differencing (listing 2)

	vec2 dHdxy_fwd() {

		vec2 dSTdx = dFdx( vBumpMapUv );
		vec2 dSTdy = dFdy( vBumpMapUv );

		float Hll = bumpScale * texture2D( bumpMap, vBumpMapUv ).x;
		float dBx = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdx ).x - Hll;
		float dBy = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdy ).x - Hll;

		return vec2( dBx, dBy );

	}

	vec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {

		// normalize is done to ensure that the bump map looks the same regardless of the texture's scale
		vec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );
		vec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );
		vec3 vN = surf_norm; // normalized

		vec3 R1 = cross( vSigmaY, vN );
		vec3 R2 = cross( vN, vSigmaX );

		float fDet = dot( vSigmaX, R1 ) * faceDirection;

		vec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );
		return normalize( abs( fDet ) * surf_norm - vGrad );

	}

#endif
`
), u3e = (
  /* glsl */
  `
#if NUM_CLIPPING_PLANES > 0

	vec4 plane;

	#ifdef ALPHA_TO_COVERAGE

		float distanceToPlane, distanceGradient;
		float clipOpacity = 1.0;

		#pragma unroll_loop_start
		for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {

			plane = clippingPlanes[ i ];
			distanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;
			distanceGradient = fwidth( distanceToPlane ) / 2.0;
			clipOpacity *= smoothstep( - distanceGradient, distanceGradient, distanceToPlane );

			if ( clipOpacity == 0.0 ) discard;

		}
		#pragma unroll_loop_end

		#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES

			float unionClipOpacity = 1.0;

			#pragma unroll_loop_start
			for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {

				plane = clippingPlanes[ i ];
				distanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;
				distanceGradient = fwidth( distanceToPlane ) / 2.0;
				unionClipOpacity *= 1.0 - smoothstep( - distanceGradient, distanceGradient, distanceToPlane );

			}
			#pragma unroll_loop_end

			clipOpacity *= 1.0 - unionClipOpacity;

		#endif

		diffuseColor.a *= clipOpacity;

		if ( diffuseColor.a == 0.0 ) discard;

	#else

		#pragma unroll_loop_start
		for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {

			plane = clippingPlanes[ i ];
			if ( dot( vClipPosition, plane.xyz ) > plane.w ) discard;

		}
		#pragma unroll_loop_end

		#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES

			bool clipped = true;

			#pragma unroll_loop_start
			for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {

				plane = clippingPlanes[ i ];
				clipped = ( dot( vClipPosition, plane.xyz ) > plane.w ) && clipped;

			}
			#pragma unroll_loop_end

			if ( clipped ) discard;

		#endif

	#endif

#endif
`
), d3e = (
  /* glsl */
  `
#if NUM_CLIPPING_PLANES > 0

	varying vec3 vClipPosition;

	uniform vec4 clippingPlanes[ NUM_CLIPPING_PLANES ];

#endif
`
), f3e = (
  /* glsl */
  `
#if NUM_CLIPPING_PLANES > 0

	varying vec3 vClipPosition;

#endif
`
), p3e = (
  /* glsl */
  `
#if NUM_CLIPPING_PLANES > 0

	vClipPosition = - mvPosition.xyz;

#endif
`
), m3e = (
  /* glsl */
  `
#if defined( USE_COLOR_ALPHA )

	diffuseColor *= vColor;

#elif defined( USE_COLOR )

	diffuseColor.rgb *= vColor;

#endif
`
), g3e = (
  /* glsl */
  `
#if defined( USE_COLOR_ALPHA )

	varying vec4 vColor;

#elif defined( USE_COLOR )

	varying vec3 vColor;

#endif
`
), v3e = (
  /* glsl */
  `
#if defined( USE_COLOR_ALPHA )

	varying vec4 vColor;

#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )

	varying vec3 vColor;

#endif
`
), y3e = (
  /* glsl */
  `
#if defined( USE_COLOR_ALPHA )

	vColor = vec4( 1.0 );

#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )

	vColor = vec3( 1.0 );

#endif

#ifdef USE_COLOR

	vColor *= color;

#endif

#ifdef USE_INSTANCING_COLOR

	vColor.xyz *= instanceColor.xyz;

#endif

#ifdef USE_BATCHING_COLOR

	vec3 batchingColor = getBatchingColor( getIndirectIndex( gl_DrawID ) );

	vColor.xyz *= batchingColor.xyz;

#endif
`
), x3e = (
  /* glsl */
  `
#define PI 3.141592653589793
#define PI2 6.283185307179586
#define PI_HALF 1.5707963267948966
#define RECIPROCAL_PI 0.3183098861837907
#define RECIPROCAL_PI2 0.15915494309189535
#define EPSILON 1e-6

#ifndef saturate
// <tonemapping_pars_fragment> may have defined saturate() already
#define saturate( a ) clamp( a, 0.0, 1.0 )
#endif
#define whiteComplement( a ) ( 1.0 - saturate( a ) )

float pow2( const in float x ) { return x*x; }
vec3 pow2( const in vec3 x ) { return x*x; }
float pow3( const in float x ) { return x*x*x; }
float pow4( const in float x ) { float x2 = x*x; return x2*x2; }
float max3( const in vec3 v ) { return max( max( v.x, v.y ), v.z ); }
float average( const in vec3 v ) { return dot( v, vec3( 0.3333333 ) ); }

// expects values in the range of [0,1]x[0,1], returns values in the [0,1] range.
// do not collapse into a single function per: http://byteblacksmith.com/improvements-to-the-canonical-one-liner-glsl-rand-for-opengl-es-2-0/
highp float rand( const in vec2 uv ) {

	const highp float a = 12.9898, b = 78.233, c = 43758.5453;
	highp float dt = dot( uv.xy, vec2( a,b ) ), sn = mod( dt, PI );

	return fract( sin( sn ) * c );

}

#ifdef HIGH_PRECISION
	float precisionSafeLength( vec3 v ) { return length( v ); }
#else
	float precisionSafeLength( vec3 v ) {
		float maxComponent = max3( abs( v ) );
		return length( v / maxComponent ) * maxComponent;
	}
#endif

struct IncidentLight {
	vec3 color;
	vec3 direction;
	bool visible;
};

struct ReflectedLight {
	vec3 directDiffuse;
	vec3 directSpecular;
	vec3 indirectDiffuse;
	vec3 indirectSpecular;
};

#ifdef USE_ALPHAHASH

	varying vec3 vPosition;

#endif

vec3 transformDirection( in vec3 dir, in mat4 matrix ) {

	return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );

}

vec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {

	// dir can be either a direction vector or a normal vector
	// upper-left 3x3 of matrix is assumed to be orthogonal

	return normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );

}

mat3 transposeMat3( const in mat3 m ) {

	mat3 tmp;

	tmp[ 0 ] = vec3( m[ 0 ].x, m[ 1 ].x, m[ 2 ].x );
	tmp[ 1 ] = vec3( m[ 0 ].y, m[ 1 ].y, m[ 2 ].y );
	tmp[ 2 ] = vec3( m[ 0 ].z, m[ 1 ].z, m[ 2 ].z );

	return tmp;

}

float luminance( const in vec3 rgb ) {

	// assumes rgb is in linear color space with sRGB primaries and D65 white point

	const vec3 weights = vec3( 0.2126729, 0.7151522, 0.0721750 );

	return dot( weights, rgb );

}

bool isPerspectiveMatrix( mat4 m ) {

	return m[ 2 ][ 3 ] == - 1.0;

}

vec2 equirectUv( in vec3 dir ) {

	// dir is assumed to be unit length

	float u = atan( dir.z, dir.x ) * RECIPROCAL_PI2 + 0.5;

	float v = asin( clamp( dir.y, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;

	return vec2( u, v );

}

vec3 BRDF_Lambert( const in vec3 diffuseColor ) {

	return RECIPROCAL_PI * diffuseColor;

} // validated

vec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {

	// Original approximation by Christophe Schlick '94
	// float fresnel = pow( 1.0 - dotVH, 5.0 );

	// Optimized variant (presented by Epic at SIGGRAPH '13)
	// https://cdn2.unrealengine.com/Resources/files/2013SiggraphPresentationsNotes-26915738.pdf
	float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );

	return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );

} // validated

float F_Schlick( const in float f0, const in float f90, const in float dotVH ) {

	// Original approximation by Christophe Schlick '94
	// float fresnel = pow( 1.0 - dotVH, 5.0 );

	// Optimized variant (presented by Epic at SIGGRAPH '13)
	// https://cdn2.unrealengine.com/Resources/files/2013SiggraphPresentationsNotes-26915738.pdf
	float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );

	return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );

} // validated
`
), A3e = (
  /* glsl */
  `
#ifdef ENVMAP_TYPE_CUBE_UV

	#define cubeUV_minMipLevel 4.0
	#define cubeUV_minTileSize 16.0

	// These shader functions convert between the UV coordinates of a single face of
	// a cubemap, the 0-5 integer index of a cube face, and the direction vector for
	// sampling a textureCube (not generally normalized ).

	float getFace( vec3 direction ) {

		vec3 absDirection = abs( direction );

		float face = - 1.0;

		if ( absDirection.x > absDirection.z ) {

			if ( absDirection.x > absDirection.y )

				face = direction.x > 0.0 ? 0.0 : 3.0;

			else

				face = direction.y > 0.0 ? 1.0 : 4.0;

		} else {

			if ( absDirection.z > absDirection.y )

				face = direction.z > 0.0 ? 2.0 : 5.0;

			else

				face = direction.y > 0.0 ? 1.0 : 4.0;

		}

		return face;

	}

	// RH coordinate system; PMREM face-indexing convention
	vec2 getUV( vec3 direction, float face ) {

		vec2 uv;

		if ( face == 0.0 ) {

			uv = vec2( direction.z, direction.y ) / abs( direction.x ); // pos x

		} else if ( face == 1.0 ) {

			uv = vec2( - direction.x, - direction.z ) / abs( direction.y ); // pos y

		} else if ( face == 2.0 ) {

			uv = vec2( - direction.x, direction.y ) / abs( direction.z ); // pos z

		} else if ( face == 3.0 ) {

			uv = vec2( - direction.z, direction.y ) / abs( direction.x ); // neg x

		} else if ( face == 4.0 ) {

			uv = vec2( - direction.x, direction.z ) / abs( direction.y ); // neg y

		} else {

			uv = vec2( direction.x, direction.y ) / abs( direction.z ); // neg z

		}

		return 0.5 * ( uv + 1.0 );

	}

	vec3 bilinearCubeUV( sampler2D envMap, vec3 direction, float mipInt ) {

		float face = getFace( direction );

		float filterInt = max( cubeUV_minMipLevel - mipInt, 0.0 );

		mipInt = max( mipInt, cubeUV_minMipLevel );

		float faceSize = exp2( mipInt );

		highp vec2 uv = getUV( direction, face ) * ( faceSize - 2.0 ) + 1.0; // #25071

		if ( face > 2.0 ) {

			uv.y += faceSize;

			face -= 3.0;

		}

		uv.x += face * faceSize;

		uv.x += filterInt * 3.0 * cubeUV_minTileSize;

		uv.y += 4.0 * ( exp2( CUBEUV_MAX_MIP ) - faceSize );

		uv.x *= CUBEUV_TEXEL_WIDTH;
		uv.y *= CUBEUV_TEXEL_HEIGHT;

		#ifdef texture2DGradEXT

			return texture2DGradEXT( envMap, uv, vec2( 0.0 ), vec2( 0.0 ) ).rgb; // disable anisotropic filtering

		#else

			return texture2D( envMap, uv ).rgb;

		#endif

	}

	// These defines must match with PMREMGenerator

	#define cubeUV_r0 1.0
	#define cubeUV_m0 - 2.0
	#define cubeUV_r1 0.8
	#define cubeUV_m1 - 1.0
	#define cubeUV_r4 0.4
	#define cubeUV_m4 2.0
	#define cubeUV_r5 0.305
	#define cubeUV_m5 3.0
	#define cubeUV_r6 0.21
	#define cubeUV_m6 4.0

	float roughnessToMip( float roughness ) {

		float mip = 0.0;

		if ( roughness >= cubeUV_r1 ) {

			mip = ( cubeUV_r0 - roughness ) * ( cubeUV_m1 - cubeUV_m0 ) / ( cubeUV_r0 - cubeUV_r1 ) + cubeUV_m0;

		} else if ( roughness >= cubeUV_r4 ) {

			mip = ( cubeUV_r1 - roughness ) * ( cubeUV_m4 - cubeUV_m1 ) / ( cubeUV_r1 - cubeUV_r4 ) + cubeUV_m1;

		} else if ( roughness >= cubeUV_r5 ) {

			mip = ( cubeUV_r4 - roughness ) * ( cubeUV_m5 - cubeUV_m4 ) / ( cubeUV_r4 - cubeUV_r5 ) + cubeUV_m4;

		} else if ( roughness >= cubeUV_r6 ) {

			mip = ( cubeUV_r5 - roughness ) * ( cubeUV_m6 - cubeUV_m5 ) / ( cubeUV_r5 - cubeUV_r6 ) + cubeUV_m5;

		} else {

			mip = - 2.0 * log2( 1.16 * roughness ); // 1.16 = 1.79^0.25
		}

		return mip;

	}

	vec4 textureCubeUV( sampler2D envMap, vec3 sampleDir, float roughness ) {

		float mip = clamp( roughnessToMip( roughness ), cubeUV_m0, CUBEUV_MAX_MIP );

		float mipF = fract( mip );

		float mipInt = floor( mip );

		vec3 color0 = bilinearCubeUV( envMap, sampleDir, mipInt );

		if ( mipF == 0.0 ) {

			return vec4( color0, 1.0 );

		} else {

			vec3 color1 = bilinearCubeUV( envMap, sampleDir, mipInt + 1.0 );

			return vec4( mix( color0, color1, mipF ), 1.0 );

		}

	}

#endif
`
), b3e = (
  /* glsl */
  `

vec3 transformedNormal = objectNormal;
#ifdef USE_TANGENT

	vec3 transformedTangent = objectTangent;

#endif

#ifdef USE_BATCHING

	// this is in lieu of a per-instance normal-matrix
	// shear transforms in the instance matrix are not supported

	mat3 bm = mat3( batchingMatrix );
	transformedNormal /= vec3( dot( bm[ 0 ], bm[ 0 ] ), dot( bm[ 1 ], bm[ 1 ] ), dot( bm[ 2 ], bm[ 2 ] ) );
	transformedNormal = bm * transformedNormal;

	#ifdef USE_TANGENT

		transformedTangent = bm * transformedTangent;

	#endif

#endif

#ifdef USE_INSTANCING

	// this is in lieu of a per-instance normal-matrix
	// shear transforms in the instance matrix are not supported

	mat3 im = mat3( instanceMatrix );
	transformedNormal /= vec3( dot( im[ 0 ], im[ 0 ] ), dot( im[ 1 ], im[ 1 ] ), dot( im[ 2 ], im[ 2 ] ) );
	transformedNormal = im * transformedNormal;

	#ifdef USE_TANGENT

		transformedTangent = im * transformedTangent;

	#endif

#endif

transformedNormal = normalMatrix * transformedNormal;

#ifdef FLIP_SIDED

	transformedNormal = - transformedNormal;

#endif

#ifdef USE_TANGENT

	transformedTangent = ( modelViewMatrix * vec4( transformedTangent, 0.0 ) ).xyz;

	#ifdef FLIP_SIDED

		transformedTangent = - transformedTangent;

	#endif

#endif
`
), M3e = (
  /* glsl */
  `
#ifdef USE_DISPLACEMENTMAP

	uniform sampler2D displacementMap;
	uniform float displacementScale;
	uniform float displacementBias;

#endif
`
), S3e = (
  /* glsl */
  `
#ifdef USE_DISPLACEMENTMAP

	transformed += normalize( objectNormal ) * ( texture2D( displacementMap, vDisplacementMapUv ).x * displacementScale + displacementBias );

#endif
`
), w3e = (
  /* glsl */
  `
#ifdef USE_EMISSIVEMAP

	vec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );

	totalEmissiveRadiance *= emissiveColor.rgb;

#endif
`
), E3e = (
  /* glsl */
  `
#ifdef USE_EMISSIVEMAP

	uniform sampler2D emissiveMap;

#endif
`
), T3e = (
  /* glsl */
  `
gl_FragColor = linearToOutputTexel( gl_FragColor );
`
), C3e = (
  /* glsl */
  `

// http://www.russellcottrell.com/photo/matrixCalculator.htm

// Linear sRGB => XYZ => Linear Display P3
const mat3 LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = mat3(
	vec3( 0.8224621, 0.177538, 0.0 ),
	vec3( 0.0331941, 0.9668058, 0.0 ),
	vec3( 0.0170827, 0.0723974, 0.9105199 )
);

// Linear Display P3 => XYZ => Linear sRGB
const mat3 LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = mat3(
	vec3( 1.2249401, - 0.2249404, 0.0 ),
	vec3( - 0.0420569, 1.0420571, 0.0 ),
	vec3( - 0.0196376, - 0.0786361, 1.0982735 )
);

vec4 LinearSRGBToLinearDisplayP3( in vec4 value ) {
	return vec4( value.rgb * LINEAR_SRGB_TO_LINEAR_DISPLAY_P3, value.a );
}

vec4 LinearDisplayP3ToLinearSRGB( in vec4 value ) {
	return vec4( value.rgb * LINEAR_DISPLAY_P3_TO_LINEAR_SRGB, value.a );
}

vec4 LinearTransferOETF( in vec4 value ) {
	return value;
}

vec4 sRGBTransferOETF( in vec4 value ) {
	return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
}

// @deprecated, r156
vec4 LinearToLinear( in vec4 value ) {
	return value;
}

// @deprecated, r156
vec4 LinearTosRGB( in vec4 value ) {
	return sRGBTransferOETF( value );
}
`
), _3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	#ifdef ENV_WORLDPOS

		vec3 cameraToFrag;

		if ( isOrthographic ) {

			cameraToFrag = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );

		} else {

			cameraToFrag = normalize( vWorldPosition - cameraPosition );

		}

		// Transforming Normal Vectors with the Inverse Transformation
		vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );

		#ifdef ENVMAP_MODE_REFLECTION

			vec3 reflectVec = reflect( cameraToFrag, worldNormal );

		#else

			vec3 reflectVec = refract( cameraToFrag, worldNormal, refractionRatio );

		#endif

	#else

		vec3 reflectVec = vReflect;

	#endif

	#ifdef ENVMAP_TYPE_CUBE

		vec4 envColor = textureCube( envMap, envMapRotation * vec3( flipEnvMap * reflectVec.x, reflectVec.yz ) );

	#else

		vec4 envColor = vec4( 0.0 );

	#endif

	#ifdef ENVMAP_BLENDING_MULTIPLY

		outgoingLight = mix( outgoingLight, outgoingLight * envColor.xyz, specularStrength * reflectivity );

	#elif defined( ENVMAP_BLENDING_MIX )

		outgoingLight = mix( outgoingLight, envColor.xyz, specularStrength * reflectivity );

	#elif defined( ENVMAP_BLENDING_ADD )

		outgoingLight += envColor.xyz * specularStrength * reflectivity;

	#endif

#endif
`
), P3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	uniform float envMapIntensity;
	uniform float flipEnvMap;
	uniform mat3 envMapRotation;

	#ifdef ENVMAP_TYPE_CUBE
		uniform samplerCube envMap;
	#else
		uniform sampler2D envMap;
	#endif
	
#endif
`
), R3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	uniform float reflectivity;

	#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )

		#define ENV_WORLDPOS

	#endif

	#ifdef ENV_WORLDPOS

		varying vec3 vWorldPosition;
		uniform float refractionRatio;
	#else
		varying vec3 vReflect;
	#endif

#endif
`
), D3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )

		#define ENV_WORLDPOS

	#endif

	#ifdef ENV_WORLDPOS
		
		varying vec3 vWorldPosition;

	#else

		varying vec3 vReflect;
		uniform float refractionRatio;

	#endif

#endif
`
), L3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	#ifdef ENV_WORLDPOS

		vWorldPosition = worldPosition.xyz;

	#else

		vec3 cameraToVertex;

		if ( isOrthographic ) {

			cameraToVertex = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );

		} else {

			cameraToVertex = normalize( worldPosition.xyz - cameraPosition );

		}

		vec3 worldNormal = inverseTransformDirection( transformedNormal, viewMatrix );

		#ifdef ENVMAP_MODE_REFLECTION

			vReflect = reflect( cameraToVertex, worldNormal );

		#else

			vReflect = refract( cameraToVertex, worldNormal, refractionRatio );

		#endif

	#endif

#endif
`
), I3e = (
  /* glsl */
  `
#ifdef USE_FOG

	vFogDepth = - mvPosition.z;

#endif
`
), O3e = (
  /* glsl */
  `
#ifdef USE_FOG

	varying float vFogDepth;

#endif
`
), N3e = (
  /* glsl */
  `
#ifdef USE_FOG

	#ifdef FOG_EXP2

		float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );

	#else

		float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );

	#endif

	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );

#endif
`
), B3e = (
  /* glsl */
  `
#ifdef USE_FOG

	uniform vec3 fogColor;
	varying float vFogDepth;

	#ifdef FOG_EXP2

		uniform float fogDensity;

	#else

		uniform float fogNear;
		uniform float fogFar;

	#endif

#endif
`
), F3e = (
  /* glsl */
  `

#ifdef USE_GRADIENTMAP

	uniform sampler2D gradientMap;

#endif

vec3 getGradientIrradiance( vec3 normal, vec3 lightDirection ) {

	// dotNL will be from -1.0 to 1.0
	float dotNL = dot( normal, lightDirection );
	vec2 coord = vec2( dotNL * 0.5 + 0.5, 0.0 );

	#ifdef USE_GRADIENTMAP

		return vec3( texture2D( gradientMap, coord ).r );

	#else

		vec2 fw = fwidth( coord ) * 0.5;
		return mix( vec3( 0.7 ), vec3( 1.0 ), smoothstep( 0.7 - fw.x, 0.7 + fw.x, coord.x ) );

	#endif

}
`
), U3e = (
  /* glsl */
  `
#ifdef USE_LIGHTMAP

	uniform sampler2D lightMap;
	uniform float lightMapIntensity;

#endif
`
), z3e = (
  /* glsl */
  `
LambertMaterial material;
material.diffuseColor = diffuseColor.rgb;
material.specularStrength = specularStrength;
`
), k3e = (
  /* glsl */
  `
varying vec3 vViewPosition;

struct LambertMaterial {

	vec3 diffuseColor;
	float specularStrength;

};

void RE_Direct_Lambert( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {

	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
	vec3 irradiance = dotNL * directLight.color;

	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

void RE_IndirectDiffuse_Lambert( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {

	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

#define RE_Direct				RE_Direct_Lambert
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Lambert
`
), G3e = (
  /* glsl */
  `
uniform bool receiveShadow;
uniform vec3 ambientLightColor;

#if defined( USE_LIGHT_PROBES )

	uniform vec3 lightProbe[ 9 ];

#endif

// get the irradiance (radiance convolved with cosine lobe) at the point 'normal' on the unit sphere
// source: https://graphics.stanford.edu/papers/envmap/envmap.pdf
vec3 shGetIrradianceAt( in vec3 normal, in vec3 shCoefficients[ 9 ] ) {

	// normal is assumed to have unit length

	float x = normal.x, y = normal.y, z = normal.z;

	// band 0
	vec3 result = shCoefficients[ 0 ] * 0.886227;

	// band 1
	result += shCoefficients[ 1 ] * 2.0 * 0.511664 * y;
	result += shCoefficients[ 2 ] * 2.0 * 0.511664 * z;
	result += shCoefficients[ 3 ] * 2.0 * 0.511664 * x;

	// band 2
	result += shCoefficients[ 4 ] * 2.0 * 0.429043 * x * y;
	result += shCoefficients[ 5 ] * 2.0 * 0.429043 * y * z;
	result += shCoefficients[ 6 ] * ( 0.743125 * z * z - 0.247708 );
	result += shCoefficients[ 7 ] * 2.0 * 0.429043 * x * z;
	result += shCoefficients[ 8 ] * 0.429043 * ( x * x - y * y );

	return result;

}

vec3 getLightProbeIrradiance( const in vec3 lightProbe[ 9 ], const in vec3 normal ) {

	vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );

	vec3 irradiance = shGetIrradianceAt( worldNormal, lightProbe );

	return irradiance;

}

vec3 getAmbientLightIrradiance( const in vec3 ambientLightColor ) {

	vec3 irradiance = ambientLightColor;

	return irradiance;

}

float getDistanceAttenuation( const in float lightDistance, const in float cutoffDistance, const in float decayExponent ) {

	// based upon Frostbite 3 Moving to Physically-based Rendering
	// page 32, equation 26: E[window1]
	// https://seblagarde.files.wordpress.com/2015/07/course_notes_moving_frostbite_to_pbr_v32.pdf
	float distanceFalloff = 1.0 / max( pow( lightDistance, decayExponent ), 0.01 );

	if ( cutoffDistance > 0.0 ) {

		distanceFalloff *= pow2( saturate( 1.0 - pow4( lightDistance / cutoffDistance ) ) );

	}

	return distanceFalloff;

}

float getSpotAttenuation( const in float coneCosine, const in float penumbraCosine, const in float angleCosine ) {

	return smoothstep( coneCosine, penumbraCosine, angleCosine );

}

#if NUM_DIR_LIGHTS > 0

	struct DirectionalLight {
		vec3 direction;
		vec3 color;
	};

	uniform DirectionalLight directionalLights[ NUM_DIR_LIGHTS ];

	void getDirectionalLightInfo( const in DirectionalLight directionalLight, out IncidentLight light ) {

		light.color = directionalLight.color;
		light.direction = directionalLight.direction;
		light.visible = true;

	}

#endif


#if NUM_POINT_LIGHTS > 0

	struct PointLight {
		vec3 position;
		vec3 color;
		float distance;
		float decay;
	};

	uniform PointLight pointLights[ NUM_POINT_LIGHTS ];

	// light is an out parameter as having it as a return value caused compiler errors on some devices
	void getPointLightInfo( const in PointLight pointLight, const in vec3 geometryPosition, out IncidentLight light ) {

		vec3 lVector = pointLight.position - geometryPosition;

		light.direction = normalize( lVector );

		float lightDistance = length( lVector );

		light.color = pointLight.color;
		light.color *= getDistanceAttenuation( lightDistance, pointLight.distance, pointLight.decay );
		light.visible = ( light.color != vec3( 0.0 ) );

	}

#endif


#if NUM_SPOT_LIGHTS > 0

	struct SpotLight {
		vec3 position;
		vec3 direction;
		vec3 color;
		float distance;
		float decay;
		float coneCos;
		float penumbraCos;
	};

	uniform SpotLight spotLights[ NUM_SPOT_LIGHTS ];

	// light is an out parameter as having it as a return value caused compiler errors on some devices
	void getSpotLightInfo( const in SpotLight spotLight, const in vec3 geometryPosition, out IncidentLight light ) {

		vec3 lVector = spotLight.position - geometryPosition;

		light.direction = normalize( lVector );

		float angleCos = dot( light.direction, spotLight.direction );

		float spotAttenuation = getSpotAttenuation( spotLight.coneCos, spotLight.penumbraCos, angleCos );

		if ( spotAttenuation > 0.0 ) {

			float lightDistance = length( lVector );

			light.color = spotLight.color * spotAttenuation;
			light.color *= getDistanceAttenuation( lightDistance, spotLight.distance, spotLight.decay );
			light.visible = ( light.color != vec3( 0.0 ) );

		} else {

			light.color = vec3( 0.0 );
			light.visible = false;

		}

	}

#endif


#if NUM_RECT_AREA_LIGHTS > 0

	struct RectAreaLight {
		vec3 color;
		vec3 position;
		vec3 halfWidth;
		vec3 halfHeight;
	};

	// Pre-computed values of LinearTransformedCosine approximation of BRDF
	// BRDF approximation Texture is 64x64
	uniform sampler2D ltc_1; // RGBA Float
	uniform sampler2D ltc_2; // RGBA Float

	uniform RectAreaLight rectAreaLights[ NUM_RECT_AREA_LIGHTS ];

#endif


#if NUM_HEMI_LIGHTS > 0

	struct HemisphereLight {
		vec3 direction;
		vec3 skyColor;
		vec3 groundColor;
	};

	uniform HemisphereLight hemisphereLights[ NUM_HEMI_LIGHTS ];

	vec3 getHemisphereLightIrradiance( const in HemisphereLight hemiLight, const in vec3 normal ) {

		float dotNL = dot( normal, hemiLight.direction );
		float hemiDiffuseWeight = 0.5 * dotNL + 0.5;

		vec3 irradiance = mix( hemiLight.groundColor, hemiLight.skyColor, hemiDiffuseWeight );

		return irradiance;

	}

#endif
`
), H3e = (
  /* glsl */
  `
#ifdef USE_ENVMAP

	vec3 getIBLIrradiance( const in vec3 normal ) {

		#ifdef ENVMAP_TYPE_CUBE_UV

			vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );

			vec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, 1.0 );

			return PI * envMapColor.rgb * envMapIntensity;

		#else

			return vec3( 0.0 );

		#endif

	}

	vec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {

		#ifdef ENVMAP_TYPE_CUBE_UV

			vec3 reflectVec = reflect( - viewDir, normal );

			// Mixing the reflection with the normal is more accurate and keeps rough objects from gathering light from behind their tangent plane.
			reflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );

			reflectVec = inverseTransformDirection( reflectVec, viewMatrix );

			vec4 envMapColor = textureCubeUV( envMap, envMapRotation * reflectVec, roughness );

			return envMapColor.rgb * envMapIntensity;

		#else

			return vec3( 0.0 );

		#endif

	}

	#ifdef USE_ANISOTROPY

		vec3 getIBLAnisotropyRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness, const in vec3 bitangent, const in float anisotropy ) {

			#ifdef ENVMAP_TYPE_CUBE_UV

			  // https://google.github.io/filament/Filament.md.html#lighting/imagebasedlights/anisotropy
				vec3 bentNormal = cross( bitangent, viewDir );
				bentNormal = normalize( cross( bentNormal, bitangent ) );
				bentNormal = normalize( mix( bentNormal, normal, pow2( pow2( 1.0 - anisotropy * ( 1.0 - roughness ) ) ) ) );

				return getIBLRadiance( viewDir, bentNormal, roughness );

			#else

				return vec3( 0.0 );

			#endif

		}

	#endif

#endif
`
), V3e = (
  /* glsl */
  `
ToonMaterial material;
material.diffuseColor = diffuseColor.rgb;
`
), W3e = (
  /* glsl */
  `
varying vec3 vViewPosition;

struct ToonMaterial {

	vec3 diffuseColor;

};

void RE_Direct_Toon( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {

	vec3 irradiance = getGradientIrradiance( geometryNormal, directLight.direction ) * directLight.color;

	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

void RE_IndirectDiffuse_Toon( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {

	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

#define RE_Direct				RE_Direct_Toon
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Toon
`
), X3e = (
  /* glsl */
  `
BlinnPhongMaterial material;
material.diffuseColor = diffuseColor.rgb;
material.specularColor = specular;
material.specularShininess = shininess;
material.specularStrength = specularStrength;
`
), j3e = (
  /* glsl */
  `
varying vec3 vViewPosition;

struct BlinnPhongMaterial {

	vec3 diffuseColor;
	vec3 specularColor;
	float specularShininess;
	float specularStrength;

};

void RE_Direct_BlinnPhong( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {

	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
	vec3 irradiance = dotNL * directLight.color;

	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

	reflectedLight.directSpecular += irradiance * BRDF_BlinnPhong( directLight.direction, geometryViewDir, geometryNormal, material.specularColor, material.specularShininess ) * material.specularStrength;

}

void RE_IndirectDiffuse_BlinnPhong( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {

	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

#define RE_Direct				RE_Direct_BlinnPhong
#define RE_IndirectDiffuse		RE_IndirectDiffuse_BlinnPhong
`
), q3e = (
  /* glsl */
  `
PhysicalMaterial material;
material.diffuseColor = diffuseColor.rgb * ( 1.0 - metalnessFactor );

vec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );
float geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );

material.roughness = max( roughnessFactor, 0.0525 );// 0.0525 corresponds to the base mip of a 256 cubemap.
material.roughness += geometryRoughness;
material.roughness = min( material.roughness, 1.0 );

#ifdef IOR

	material.ior = ior;

	#ifdef USE_SPECULAR

		float specularIntensityFactor = specularIntensity;
		vec3 specularColorFactor = specularColor;

		#ifdef USE_SPECULAR_COLORMAP

			specularColorFactor *= texture2D( specularColorMap, vSpecularColorMapUv ).rgb;

		#endif

		#ifdef USE_SPECULAR_INTENSITYMAP

			specularIntensityFactor *= texture2D( specularIntensityMap, vSpecularIntensityMapUv ).a;

		#endif

		material.specularF90 = mix( specularIntensityFactor, 1.0, metalnessFactor );

	#else

		float specularIntensityFactor = 1.0;
		vec3 specularColorFactor = vec3( 1.0 );
		material.specularF90 = 1.0;

	#endif

	material.specularColor = mix( min( pow2( ( material.ior - 1.0 ) / ( material.ior + 1.0 ) ) * specularColorFactor, vec3( 1.0 ) ) * specularIntensityFactor, diffuseColor.rgb, metalnessFactor );

#else

	material.specularColor = mix( vec3( 0.04 ), diffuseColor.rgb, metalnessFactor );
	material.specularF90 = 1.0;

#endif

#ifdef USE_CLEARCOAT

	material.clearcoat = clearcoat;
	material.clearcoatRoughness = clearcoatRoughness;
	material.clearcoatF0 = vec3( 0.04 );
	material.clearcoatF90 = 1.0;

	#ifdef USE_CLEARCOATMAP

		material.clearcoat *= texture2D( clearcoatMap, vClearcoatMapUv ).x;

	#endif

	#ifdef USE_CLEARCOAT_ROUGHNESSMAP

		material.clearcoatRoughness *= texture2D( clearcoatRoughnessMap, vClearcoatRoughnessMapUv ).y;

	#endif

	material.clearcoat = saturate( material.clearcoat ); // Burley clearcoat model
	material.clearcoatRoughness = max( material.clearcoatRoughness, 0.0525 );
	material.clearcoatRoughness += geometryRoughness;
	material.clearcoatRoughness = min( material.clearcoatRoughness, 1.0 );

#endif

#ifdef USE_DISPERSION

	material.dispersion = dispersion;

#endif

#ifdef USE_IRIDESCENCE

	material.iridescence = iridescence;
	material.iridescenceIOR = iridescenceIOR;

	#ifdef USE_IRIDESCENCEMAP

		material.iridescence *= texture2D( iridescenceMap, vIridescenceMapUv ).r;

	#endif

	#ifdef USE_IRIDESCENCE_THICKNESSMAP

		material.iridescenceThickness = (iridescenceThicknessMaximum - iridescenceThicknessMinimum) * texture2D( iridescenceThicknessMap, vIridescenceThicknessMapUv ).g + iridescenceThicknessMinimum;

	#else

		material.iridescenceThickness = iridescenceThicknessMaximum;

	#endif

#endif

#ifdef USE_SHEEN

	material.sheenColor = sheenColor;

	#ifdef USE_SHEEN_COLORMAP

		material.sheenColor *= texture2D( sheenColorMap, vSheenColorMapUv ).rgb;

	#endif

	material.sheenRoughness = clamp( sheenRoughness, 0.07, 1.0 );

	#ifdef USE_SHEEN_ROUGHNESSMAP

		material.sheenRoughness *= texture2D( sheenRoughnessMap, vSheenRoughnessMapUv ).a;

	#endif

#endif

#ifdef USE_ANISOTROPY

	#ifdef USE_ANISOTROPYMAP

		mat2 anisotropyMat = mat2( anisotropyVector.x, anisotropyVector.y, - anisotropyVector.y, anisotropyVector.x );
		vec3 anisotropyPolar = texture2D( anisotropyMap, vAnisotropyMapUv ).rgb;
		vec2 anisotropyV = anisotropyMat * normalize( 2.0 * anisotropyPolar.rg - vec2( 1.0 ) ) * anisotropyPolar.b;

	#else

		vec2 anisotropyV = anisotropyVector;

	#endif

	material.anisotropy = length( anisotropyV );

	if( material.anisotropy == 0.0 ) {
		anisotropyV = vec2( 1.0, 0.0 );
	} else {
		anisotropyV /= material.anisotropy;
		material.anisotropy = saturate( material.anisotropy );
	}

	// Roughness along the anisotropy bitangent is the material roughness, while the tangent roughness increases with anisotropy.
	material.alphaT = mix( pow2( material.roughness ), 1.0, pow2( material.anisotropy ) );

	material.anisotropyT = tbn[ 0 ] * anisotropyV.x + tbn[ 1 ] * anisotropyV.y;
	material.anisotropyB = tbn[ 1 ] * anisotropyV.x - tbn[ 0 ] * anisotropyV.y;

#endif
`
), Y3e = (
  /* glsl */
  `

struct PhysicalMaterial {

	vec3 diffuseColor;
	float roughness;
	vec3 specularColor;
	float specularF90;
	float dispersion;

	#ifdef USE_CLEARCOAT
		float clearcoat;
		float clearcoatRoughness;
		vec3 clearcoatF0;
		float clearcoatF90;
	#endif

	#ifdef USE_IRIDESCENCE
		float iridescence;
		float iridescenceIOR;
		float iridescenceThickness;
		vec3 iridescenceFresnel;
		vec3 iridescenceF0;
	#endif

	#ifdef USE_SHEEN
		vec3 sheenColor;
		float sheenRoughness;
	#endif

	#ifdef IOR
		float ior;
	#endif

	#ifdef USE_TRANSMISSION
		float transmission;
		float transmissionAlpha;
		float thickness;
		float attenuationDistance;
		vec3 attenuationColor;
	#endif

	#ifdef USE_ANISOTROPY
		float anisotropy;
		float alphaT;
		vec3 anisotropyT;
		vec3 anisotropyB;
	#endif

};

// temporary
vec3 clearcoatSpecularDirect = vec3( 0.0 );
vec3 clearcoatSpecularIndirect = vec3( 0.0 );
vec3 sheenSpecularDirect = vec3( 0.0 );
vec3 sheenSpecularIndirect = vec3(0.0 );

vec3 Schlick_to_F0( const in vec3 f, const in float f90, const in float dotVH ) {
    float x = clamp( 1.0 - dotVH, 0.0, 1.0 );
    float x2 = x * x;
    float x5 = clamp( x * x2 * x2, 0.0, 0.9999 );

    return ( f - vec3( f90 ) * x5 ) / ( 1.0 - x5 );
}

// Moving Frostbite to Physically Based Rendering 3.0 - page 12, listing 2
// https://seblagarde.files.wordpress.com/2015/07/course_notes_moving_frostbite_to_pbr_v32.pdf
float V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {

	float a2 = pow2( alpha );

	float gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );
	float gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );

	return 0.5 / max( gv + gl, EPSILON );

}

// Microfacet Models for Refraction through Rough Surfaces - equation (33)
// http://graphicrants.blogspot.com/2013/08/specular-brdf-reference.html
// alpha is "roughness squared" in Disney’s reparameterization
float D_GGX( const in float alpha, const in float dotNH ) {

	float a2 = pow2( alpha );

	float denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0; // avoid alpha = 0 with dotNH = 1

	return RECIPROCAL_PI * a2 / pow2( denom );

}

// https://google.github.io/filament/Filament.md.html#materialsystem/anisotropicmodel/anisotropicspecularbrdf
#ifdef USE_ANISOTROPY

	float V_GGX_SmithCorrelated_Anisotropic( const in float alphaT, const in float alphaB, const in float dotTV, const in float dotBV, const in float dotTL, const in float dotBL, const in float dotNV, const in float dotNL ) {

		float gv = dotNL * length( vec3( alphaT * dotTV, alphaB * dotBV, dotNV ) );
		float gl = dotNV * length( vec3( alphaT * dotTL, alphaB * dotBL, dotNL ) );
		float v = 0.5 / ( gv + gl );

		return saturate(v);

	}

	float D_GGX_Anisotropic( const in float alphaT, const in float alphaB, const in float dotNH, const in float dotTH, const in float dotBH ) {

		float a2 = alphaT * alphaB;
		highp vec3 v = vec3( alphaB * dotTH, alphaT * dotBH, a2 * dotNH );
		highp float v2 = dot( v, v );
		float w2 = a2 / v2;

		return RECIPROCAL_PI * a2 * pow2 ( w2 );

	}

#endif

#ifdef USE_CLEARCOAT

	// GGX Distribution, Schlick Fresnel, GGX_SmithCorrelated Visibility
	vec3 BRDF_GGX_Clearcoat( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material) {

		vec3 f0 = material.clearcoatF0;
		float f90 = material.clearcoatF90;
		float roughness = material.clearcoatRoughness;

		float alpha = pow2( roughness ); // UE4's roughness

		vec3 halfDir = normalize( lightDir + viewDir );

		float dotNL = saturate( dot( normal, lightDir ) );
		float dotNV = saturate( dot( normal, viewDir ) );
		float dotNH = saturate( dot( normal, halfDir ) );
		float dotVH = saturate( dot( viewDir, halfDir ) );

		vec3 F = F_Schlick( f0, f90, dotVH );

		float V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );

		float D = D_GGX( alpha, dotNH );

		return F * ( V * D );

	}

#endif

vec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material ) {

	vec3 f0 = material.specularColor;
	float f90 = material.specularF90;
	float roughness = material.roughness;

	float alpha = pow2( roughness ); // UE4's roughness

	vec3 halfDir = normalize( lightDir + viewDir );

	float dotNL = saturate( dot( normal, lightDir ) );
	float dotNV = saturate( dot( normal, viewDir ) );
	float dotNH = saturate( dot( normal, halfDir ) );
	float dotVH = saturate( dot( viewDir, halfDir ) );

	vec3 F = F_Schlick( f0, f90, dotVH );

	#ifdef USE_IRIDESCENCE

		F = mix( F, material.iridescenceFresnel, material.iridescence );

	#endif

	#ifdef USE_ANISOTROPY

		float dotTL = dot( material.anisotropyT, lightDir );
		float dotTV = dot( material.anisotropyT, viewDir );
		float dotTH = dot( material.anisotropyT, halfDir );
		float dotBL = dot( material.anisotropyB, lightDir );
		float dotBV = dot( material.anisotropyB, viewDir );
		float dotBH = dot( material.anisotropyB, halfDir );

		float V = V_GGX_SmithCorrelated_Anisotropic( material.alphaT, alpha, dotTV, dotBV, dotTL, dotBL, dotNV, dotNL );

		float D = D_GGX_Anisotropic( material.alphaT, alpha, dotNH, dotTH, dotBH );

	#else

		float V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );

		float D = D_GGX( alpha, dotNH );

	#endif

	return F * ( V * D );

}

// Rect Area Light

// Real-Time Polygonal-Light Shading with Linearly Transformed Cosines
// by Eric Heitz, Jonathan Dupuy, Stephen Hill and David Neubelt
// code: https://github.com/selfshadow/ltc_code/

vec2 LTC_Uv( const in vec3 N, const in vec3 V, const in float roughness ) {

	const float LUT_SIZE = 64.0;
	const float LUT_SCALE = ( LUT_SIZE - 1.0 ) / LUT_SIZE;
	const float LUT_BIAS = 0.5 / LUT_SIZE;

	float dotNV = saturate( dot( N, V ) );

	// texture parameterized by sqrt( GGX alpha ) and sqrt( 1 - cos( theta ) )
	vec2 uv = vec2( roughness, sqrt( 1.0 - dotNV ) );

	uv = uv * LUT_SCALE + LUT_BIAS;

	return uv;

}

float LTC_ClippedSphereFormFactor( const in vec3 f ) {

	// Real-Time Area Lighting: a Journey from Research to Production (p.102)
	// An approximation of the form factor of a horizon-clipped rectangle.

	float l = length( f );

	return max( ( l * l + f.z ) / ( l + 1.0 ), 0.0 );

}

vec3 LTC_EdgeVectorFormFactor( const in vec3 v1, const in vec3 v2 ) {

	float x = dot( v1, v2 );

	float y = abs( x );

	// rational polynomial approximation to theta / sin( theta ) / 2PI
	float a = 0.8543985 + ( 0.4965155 + 0.0145206 * y ) * y;
	float b = 3.4175940 + ( 4.1616724 + y ) * y;
	float v = a / b;

	float theta_sintheta = ( x > 0.0 ) ? v : 0.5 * inversesqrt( max( 1.0 - x * x, 1e-7 ) ) - v;

	return cross( v1, v2 ) * theta_sintheta;

}

vec3 LTC_Evaluate( const in vec3 N, const in vec3 V, const in vec3 P, const in mat3 mInv, const in vec3 rectCoords[ 4 ] ) {

	// bail if point is on back side of plane of light
	// assumes ccw winding order of light vertices
	vec3 v1 = rectCoords[ 1 ] - rectCoords[ 0 ];
	vec3 v2 = rectCoords[ 3 ] - rectCoords[ 0 ];
	vec3 lightNormal = cross( v1, v2 );

	if( dot( lightNormal, P - rectCoords[ 0 ] ) < 0.0 ) return vec3( 0.0 );

	// construct orthonormal basis around N
	vec3 T1, T2;
	T1 = normalize( V - N * dot( V, N ) );
	T2 = - cross( N, T1 ); // negated from paper; possibly due to a different handedness of world coordinate system

	// compute transform
	mat3 mat = mInv * transposeMat3( mat3( T1, T2, N ) );

	// transform rect
	vec3 coords[ 4 ];
	coords[ 0 ] = mat * ( rectCoords[ 0 ] - P );
	coords[ 1 ] = mat * ( rectCoords[ 1 ] - P );
	coords[ 2 ] = mat * ( rectCoords[ 2 ] - P );
	coords[ 3 ] = mat * ( rectCoords[ 3 ] - P );

	// project rect onto sphere
	coords[ 0 ] = normalize( coords[ 0 ] );
	coords[ 1 ] = normalize( coords[ 1 ] );
	coords[ 2 ] = normalize( coords[ 2 ] );
	coords[ 3 ] = normalize( coords[ 3 ] );

	// calculate vector form factor
	vec3 vectorFormFactor = vec3( 0.0 );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 0 ], coords[ 1 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 1 ], coords[ 2 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 2 ], coords[ 3 ] );
	vectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 3 ], coords[ 0 ] );

	// adjust for horizon clipping
	float result = LTC_ClippedSphereFormFactor( vectorFormFactor );

/*
	// alternate method of adjusting for horizon clipping (see referece)
	// refactoring required
	float len = length( vectorFormFactor );
	float z = vectorFormFactor.z / len;

	const float LUT_SIZE = 64.0;
	const float LUT_SCALE = ( LUT_SIZE - 1.0 ) / LUT_SIZE;
	const float LUT_BIAS = 0.5 / LUT_SIZE;

	// tabulated horizon-clipped sphere, apparently...
	vec2 uv = vec2( z * 0.5 + 0.5, len );
	uv = uv * LUT_SCALE + LUT_BIAS;

	float scale = texture2D( ltc_2, uv ).w;

	float result = len * scale;
*/

	return vec3( result );

}

// End Rect Area Light

#if defined( USE_SHEEN )

// https://github.com/google/filament/blob/master/shaders/src/brdf.fs
float D_Charlie( float roughness, float dotNH ) {

	float alpha = pow2( roughness );

	// Estevez and Kulla 2017, "Production Friendly Microfacet Sheen BRDF"
	float invAlpha = 1.0 / alpha;
	float cos2h = dotNH * dotNH;
	float sin2h = max( 1.0 - cos2h, 0.0078125 ); // 2^(-14/2), so sin2h^2 > 0 in fp16

	return ( 2.0 + invAlpha ) * pow( sin2h, invAlpha * 0.5 ) / ( 2.0 * PI );

}

// https://github.com/google/filament/blob/master/shaders/src/brdf.fs
float V_Neubelt( float dotNV, float dotNL ) {

	// Neubelt and Pettineo 2013, "Crafting a Next-gen Material Pipeline for The Order: 1886"
	return saturate( 1.0 / ( 4.0 * ( dotNL + dotNV - dotNL * dotNV ) ) );

}

vec3 BRDF_Sheen( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, vec3 sheenColor, const in float sheenRoughness ) {

	vec3 halfDir = normalize( lightDir + viewDir );

	float dotNL = saturate( dot( normal, lightDir ) );
	float dotNV = saturate( dot( normal, viewDir ) );
	float dotNH = saturate( dot( normal, halfDir ) );

	float D = D_Charlie( sheenRoughness, dotNH );
	float V = V_Neubelt( dotNV, dotNL );

	return sheenColor * ( D * V );

}

#endif

// This is a curve-fit approxmation to the "Charlie sheen" BRDF integrated over the hemisphere from 
// Estevez and Kulla 2017, "Production Friendly Microfacet Sheen BRDF". The analysis can be found
// in the Sheen section of https://drive.google.com/file/d/1T0D1VSyR4AllqIJTQAraEIzjlb5h4FKH/view?usp=sharing
float IBLSheenBRDF( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {

	float dotNV = saturate( dot( normal, viewDir ) );

	float r2 = roughness * roughness;

	float a = roughness < 0.25 ? -339.2 * r2 + 161.4 * roughness - 25.9 : -8.48 * r2 + 14.3 * roughness - 9.95;

	float b = roughness < 0.25 ? 44.0 * r2 - 23.7 * roughness + 3.26 : 1.97 * r2 - 3.27 * roughness + 0.72;

	float DG = exp( a * dotNV + b ) + ( roughness < 0.25 ? 0.0 : 0.1 * ( roughness - 0.25 ) );

	return saturate( DG * RECIPROCAL_PI );

}

// Analytical approximation of the DFG LUT, one half of the
// split-sum approximation used in indirect specular lighting.
// via 'environmentBRDF' from "Physically Based Shading on Mobile"
// https://www.unrealengine.com/blog/physically-based-shading-on-mobile
vec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {

	float dotNV = saturate( dot( normal, viewDir ) );

	const vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );

	const vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );

	vec4 r = roughness * c0 + c1;

	float a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;

	vec2 fab = vec2( - 1.04, 1.04 ) * a004 + r.zw;

	return fab;

}

vec3 EnvironmentBRDF( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness ) {

	vec2 fab = DFGApprox( normal, viewDir, roughness );

	return specularColor * fab.x + specularF90 * fab.y;

}

// Fdez-Agüera's "Multiple-Scattering Microfacet Model for Real-Time Image Based Lighting"
// Approximates multiscattering in order to preserve energy.
// http://www.jcgt.org/published/0008/01/03/
#ifdef USE_IRIDESCENCE
void computeMultiscatteringIridescence( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float iridescence, const in vec3 iridescenceF0, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {
#else
void computeMultiscattering( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {
#endif

	vec2 fab = DFGApprox( normal, viewDir, roughness );

	#ifdef USE_IRIDESCENCE

		vec3 Fr = mix( specularColor, iridescenceF0, iridescence );

	#else

		vec3 Fr = specularColor;

	#endif

	vec3 FssEss = Fr * fab.x + specularF90 * fab.y;

	float Ess = fab.x + fab.y;
	float Ems = 1.0 - Ess;

	vec3 Favg = Fr + ( 1.0 - Fr ) * 0.047619; // 1/21
	vec3 Fms = FssEss * Favg / ( 1.0 - Ems * Favg );

	singleScatter += FssEss;
	multiScatter += Fms * Ems;

}

#if NUM_RECT_AREA_LIGHTS > 0

	void RE_Direct_RectArea_Physical( const in RectAreaLight rectAreaLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {

		vec3 normal = geometryNormal;
		vec3 viewDir = geometryViewDir;
		vec3 position = geometryPosition;
		vec3 lightPos = rectAreaLight.position;
		vec3 halfWidth = rectAreaLight.halfWidth;
		vec3 halfHeight = rectAreaLight.halfHeight;
		vec3 lightColor = rectAreaLight.color;
		float roughness = material.roughness;

		vec3 rectCoords[ 4 ];
		rectCoords[ 0 ] = lightPos + halfWidth - halfHeight; // counterclockwise; light shines in local neg z direction
		rectCoords[ 1 ] = lightPos - halfWidth - halfHeight;
		rectCoords[ 2 ] = lightPos - halfWidth + halfHeight;
		rectCoords[ 3 ] = lightPos + halfWidth + halfHeight;

		vec2 uv = LTC_Uv( normal, viewDir, roughness );

		vec4 t1 = texture2D( ltc_1, uv );
		vec4 t2 = texture2D( ltc_2, uv );

		mat3 mInv = mat3(
			vec3( t1.x, 0, t1.y ),
			vec3(    0, 1,    0 ),
			vec3( t1.z, 0, t1.w )
		);

		// LTC Fresnel Approximation by Stephen Hill
		// http://blog.selfshadow.com/publications/s2016-advances/s2016_ltc_fresnel.pdf
		vec3 fresnel = ( material.specularColor * t2.x + ( vec3( 1.0 ) - material.specularColor ) * t2.y );

		reflectedLight.directSpecular += lightColor * fresnel * LTC_Evaluate( normal, viewDir, position, mInv, rectCoords );

		reflectedLight.directDiffuse += lightColor * material.diffuseColor * LTC_Evaluate( normal, viewDir, position, mat3( 1.0 ), rectCoords );

	}

#endif

void RE_Direct_Physical( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {

	float dotNL = saturate( dot( geometryNormal, directLight.direction ) );

	vec3 irradiance = dotNL * directLight.color;

	#ifdef USE_CLEARCOAT

		float dotNLcc = saturate( dot( geometryClearcoatNormal, directLight.direction ) );

		vec3 ccIrradiance = dotNLcc * directLight.color;

		clearcoatSpecularDirect += ccIrradiance * BRDF_GGX_Clearcoat( directLight.direction, geometryViewDir, geometryClearcoatNormal, material );

	#endif

	#ifdef USE_SHEEN

		sheenSpecularDirect += irradiance * BRDF_Sheen( directLight.direction, geometryViewDir, geometryNormal, material.sheenColor, material.sheenRoughness );

	#endif

	reflectedLight.directSpecular += irradiance * BRDF_GGX( directLight.direction, geometryViewDir, geometryNormal, material );

	reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );
}

void RE_IndirectDiffuse_Physical( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {

	reflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );

}

void RE_IndirectSpecular_Physical( const in vec3 radiance, const in vec3 irradiance, const in vec3 clearcoatRadiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {

	#ifdef USE_CLEARCOAT

		clearcoatSpecularIndirect += clearcoatRadiance * EnvironmentBRDF( geometryClearcoatNormal, geometryViewDir, material.clearcoatF0, material.clearcoatF90, material.clearcoatRoughness );

	#endif

	#ifdef USE_SHEEN

		sheenSpecularIndirect += irradiance * material.sheenColor * IBLSheenBRDF( geometryNormal, geometryViewDir, material.sheenRoughness );

	#endif

	// Both indirect specular and indirect diffuse light accumulate here

	vec3 singleScattering = vec3( 0.0 );
	vec3 multiScattering = vec3( 0.0 );
	vec3 cosineWeightedIrradiance = irradiance * RECIPROCAL_PI;

	#ifdef USE_IRIDESCENCE

		computeMultiscatteringIridescence( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.iridescence, material.iridescenceFresnel, material.roughness, singleScattering, multiScattering );

	#else

		computeMultiscattering( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.roughness, singleScattering, multiScattering );

	#endif

	vec3 totalScattering = singleScattering + multiScattering;
	vec3 diffuse = material.diffuseColor * ( 1.0 - max( max( totalScattering.r, totalScattering.g ), totalScattering.b ) );

	reflectedLight.indirectSpecular += radiance * singleScattering;
	reflectedLight.indirectSpecular += multiScattering * cosineWeightedIrradiance;

	reflectedLight.indirectDiffuse += diffuse * cosineWeightedIrradiance;

}

#define RE_Direct				RE_Direct_Physical
#define RE_Direct_RectArea		RE_Direct_RectArea_Physical
#define RE_IndirectDiffuse		RE_IndirectDiffuse_Physical
#define RE_IndirectSpecular		RE_IndirectSpecular_Physical

// ref: https://seblagarde.files.wordpress.com/2015/07/course_notes_moving_frostbite_to_pbr_v32.pdf
float computeSpecularOcclusion( const in float dotNV, const in float ambientOcclusion, const in float roughness ) {

	return saturate( pow( dotNV + ambientOcclusion, exp2( - 16.0 * roughness - 1.0 ) ) - 1.0 + ambientOcclusion );

}
`
), Z3e = (
  /* glsl */
  `
/**
 * This is a template that can be used to light a material, it uses pluggable
 * RenderEquations (RE)for specific lighting scenarios.
 *
 * Instructions for use:
 * - Ensure that both RE_Direct, RE_IndirectDiffuse and RE_IndirectSpecular are defined
 * - Create a material parameter that is to be passed as the third parameter to your lighting functions.
 *
 * TODO:
 * - Add area light support.
 * - Add sphere light support.
 * - Add diffuse light probe (irradiance cubemap) support.
 */

vec3 geometryPosition = - vViewPosition;
vec3 geometryNormal = normal;
vec3 geometryViewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );

vec3 geometryClearcoatNormal = vec3( 0.0 );

#ifdef USE_CLEARCOAT

	geometryClearcoatNormal = clearcoatNormal;

#endif

#ifdef USE_IRIDESCENCE

	float dotNVi = saturate( dot( normal, geometryViewDir ) );

	if ( material.iridescenceThickness == 0.0 ) {

		material.iridescence = 0.0;

	} else {

		material.iridescence = saturate( material.iridescence );

	}

	if ( material.iridescence > 0.0 ) {

		material.iridescenceFresnel = evalIridescence( 1.0, material.iridescenceIOR, dotNVi, material.iridescenceThickness, material.specularColor );

		// Iridescence F0 approximation
		material.iridescenceF0 = Schlick_to_F0( material.iridescenceFresnel, 1.0, dotNVi );

	}

#endif

IncidentLight directLight;

#if ( NUM_POINT_LIGHTS > 0 ) && defined( RE_Direct )

	PointLight pointLight;
	#if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0
	PointLightShadow pointLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {

		pointLight = pointLights[ i ];

		getPointLightInfo( pointLight, geometryPosition, directLight );

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )
		pointLightShadow = pointLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getPointShadow( pointShadowMap[ i ], pointLightShadow.shadowMapSize, pointLightShadow.shadowIntensity, pointLightShadow.shadowBias, pointLightShadow.shadowRadius, vPointShadowCoord[ i ], pointLightShadow.shadowCameraNear, pointLightShadow.shadowCameraFar ) : 1.0;
		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_SPOT_LIGHTS > 0 ) && defined( RE_Direct )

	SpotLight spotLight;
	vec4 spotColor;
	vec3 spotLightCoord;
	bool inSpotLightMap;

	#if defined( USE_SHADOWMAP ) && NUM_SPOT_LIGHT_SHADOWS > 0
	SpotLightShadow spotLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {

		spotLight = spotLights[ i ];

		getSpotLightInfo( spotLight, geometryPosition, directLight );

		// spot lights are ordered [shadows with maps, shadows without maps, maps without shadows, none]
		#if ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#define SPOT_LIGHT_MAP_INDEX UNROLLED_LOOP_INDEX
		#elif ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		#define SPOT_LIGHT_MAP_INDEX NUM_SPOT_LIGHT_MAPS
		#else
		#define SPOT_LIGHT_MAP_INDEX ( UNROLLED_LOOP_INDEX - NUM_SPOT_LIGHT_SHADOWS + NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )
		#endif

		#if ( SPOT_LIGHT_MAP_INDEX < NUM_SPOT_LIGHT_MAPS )
			spotLightCoord = vSpotLightCoord[ i ].xyz / vSpotLightCoord[ i ].w;
			inSpotLightMap = all( lessThan( abs( spotLightCoord * 2. - 1. ), vec3( 1.0 ) ) );
			spotColor = texture2D( spotLightMap[ SPOT_LIGHT_MAP_INDEX ], spotLightCoord.xy );
			directLight.color = inSpotLightMap ? directLight.color * spotColor.rgb : directLight.color;
		#endif

		#undef SPOT_LIGHT_MAP_INDEX

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
		spotLightShadow = spotLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( spotShadowMap[ i ], spotLightShadow.shadowMapSize, spotLightShadow.shadowIntensity, spotLightShadow.shadowBias, spotLightShadow.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;
		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct )

	DirectionalLight directionalLight;
	#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
	DirectionalLightShadow directionalLightShadow;
	#endif

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {

		directionalLight = directionalLights[ i ];

		getDirectionalLightInfo( directionalLight, directLight );

		#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )
		directionalLightShadow = directionalLightShadows[ i ];
		directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;
		#endif

		RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if ( NUM_RECT_AREA_LIGHTS > 0 ) && defined( RE_Direct_RectArea )

	RectAreaLight rectAreaLight;

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_RECT_AREA_LIGHTS; i ++ ) {

		rectAreaLight = rectAreaLights[ i ];
		RE_Direct_RectArea( rectAreaLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

	}
	#pragma unroll_loop_end

#endif

#if defined( RE_IndirectDiffuse )

	vec3 iblIrradiance = vec3( 0.0 );

	vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );

	#if defined( USE_LIGHT_PROBES )

		irradiance += getLightProbeIrradiance( lightProbe, geometryNormal );

	#endif

	#if ( NUM_HEMI_LIGHTS > 0 )

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {

			irradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal );

		}
		#pragma unroll_loop_end

	#endif

#endif

#if defined( RE_IndirectSpecular )

	vec3 radiance = vec3( 0.0 );
	vec3 clearcoatRadiance = vec3( 0.0 );

#endif
`
), K3e = (
  /* glsl */
  `
#if defined( RE_IndirectDiffuse )

	#ifdef USE_LIGHTMAP

		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		vec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;

		irradiance += lightMapIrradiance;

	#endif

	#if defined( USE_ENVMAP ) && defined( STANDARD ) && defined( ENVMAP_TYPE_CUBE_UV )

		iblIrradiance += getIBLIrradiance( geometryNormal );

	#endif

#endif

#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )

	#ifdef USE_ANISOTROPY

		radiance += getIBLAnisotropyRadiance( geometryViewDir, geometryNormal, material.roughness, material.anisotropyB, material.anisotropy );

	#else

		radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );

	#endif

	#ifdef USE_CLEARCOAT

		clearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );

	#endif

#endif
`
), J3e = (
  /* glsl */
  `
#if defined( RE_IndirectDiffuse )

	RE_IndirectDiffuse( irradiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

#endif

#if defined( RE_IndirectSpecular )

	RE_IndirectSpecular( radiance, iblIrradiance, clearcoatRadiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );

#endif
`
), Q3e = (
  /* glsl */
  `
#if defined( USE_LOGDEPTHBUF )

	// Doing a strict comparison with == 1.0 can cause noise artifacts
	// on some platforms. See issue #17623.
	gl_FragDepth = vIsPerspective == 0.0 ? gl_FragCoord.z : log2( vFragDepth ) * logDepthBufFC * 0.5;

#endif
`
), $3e = (
  /* glsl */
  `
#if defined( USE_LOGDEPTHBUF )

	uniform float logDepthBufFC;
	varying float vFragDepth;
	varying float vIsPerspective;

#endif
`
), eye = (
  /* glsl */
  `
#ifdef USE_LOGDEPTHBUF

	varying float vFragDepth;
	varying float vIsPerspective;

#endif
`
), tye = (
  /* glsl */
  `
#ifdef USE_LOGDEPTHBUF

	vFragDepth = 1.0 + gl_Position.w;
	vIsPerspective = float( isPerspectiveMatrix( projectionMatrix ) );

#endif
`
), iye = (
  /* glsl */
  `
#ifdef USE_MAP

	vec4 sampledDiffuseColor = texture2D( map, vMapUv );

	#ifdef DECODE_VIDEO_TEXTURE

		// use inline sRGB decode until browsers properly support SRGB8_ALPHA8 with video textures (#26516)

		sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );
	
	#endif

	diffuseColor *= sampledDiffuseColor;

#endif
`
), nye = (
  /* glsl */
  `
#ifdef USE_MAP

	uniform sampler2D map;

#endif
`
), sye = (
  /* glsl */
  `
#if defined( USE_MAP ) || defined( USE_ALPHAMAP )

	#if defined( USE_POINTS_UV )

		vec2 uv = vUv;

	#else

		vec2 uv = ( uvTransform * vec3( gl_PointCoord.x, 1.0 - gl_PointCoord.y, 1 ) ).xy;

	#endif

#endif

#ifdef USE_MAP

	diffuseColor *= texture2D( map, uv );

#endif

#ifdef USE_ALPHAMAP

	diffuseColor.a *= texture2D( alphaMap, uv ).g;

#endif
`
), rye = (
  /* glsl */
  `
#if defined( USE_POINTS_UV )

	varying vec2 vUv;

#else

	#if defined( USE_MAP ) || defined( USE_ALPHAMAP )

		uniform mat3 uvTransform;

	#endif

#endif

#ifdef USE_MAP

	uniform sampler2D map;

#endif

#ifdef USE_ALPHAMAP

	uniform sampler2D alphaMap;

#endif
`
), aye = (
  /* glsl */
  `
float metalnessFactor = metalness;

#ifdef USE_METALNESSMAP

	vec4 texelMetalness = texture2D( metalnessMap, vMetalnessMapUv );

	// reads channel B, compatible with a combined OcclusionRoughnessMetallic (RGB) texture
	metalnessFactor *= texelMetalness.b;

#endif
`
), oye = (
  /* glsl */
  `
#ifdef USE_METALNESSMAP

	uniform sampler2D metalnessMap;

#endif
`
), lye = (
  /* glsl */
  `
#ifdef USE_INSTANCING_MORPH

	float morphTargetInfluences[ MORPHTARGETS_COUNT ];

	float morphTargetBaseInfluence = texelFetch( morphTexture, ivec2( 0, gl_InstanceID ), 0 ).r;

	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {

		morphTargetInfluences[i] =  texelFetch( morphTexture, ivec2( i + 1, gl_InstanceID ), 0 ).r;

	}
#endif
`
), cye = (
  /* glsl */
  `
#if defined( USE_MORPHCOLORS )

	// morphTargetBaseInfluence is set based on BufferGeometry.morphTargetsRelative value:
	// When morphTargetsRelative is false, this is set to 1 - sum(influences); this results in normal = sum((target - base) * influence)
	// When morphTargetsRelative is true, this is set to 1; as a result, all morph targets are simply added to the base after weighting
	vColor *= morphTargetBaseInfluence;

	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {

		#if defined( USE_COLOR_ALPHA )

			if ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ) * morphTargetInfluences[ i ];

		#elif defined( USE_COLOR )

			if ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ).rgb * morphTargetInfluences[ i ];

		#endif

	}

#endif
`
), hye = (
  /* glsl */
  `
#ifdef USE_MORPHNORMALS

	// morphTargetBaseInfluence is set based on BufferGeometry.morphTargetsRelative value:
	// When morphTargetsRelative is false, this is set to 1 - sum(influences); this results in normal = sum((target - base) * influence)
	// When morphTargetsRelative is true, this is set to 1; as a result, all morph targets are simply added to the base after weighting
	objectNormal *= morphTargetBaseInfluence;

	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {

		if ( morphTargetInfluences[ i ] != 0.0 ) objectNormal += getMorph( gl_VertexID, i, 1 ).xyz * morphTargetInfluences[ i ];

	}

#endif
`
), uye = (
  /* glsl */
  `
#ifdef USE_MORPHTARGETS

	#ifndef USE_INSTANCING_MORPH

		uniform float morphTargetBaseInfluence;
		uniform float morphTargetInfluences[ MORPHTARGETS_COUNT ];

	#endif

	uniform sampler2DArray morphTargetsTexture;
	uniform ivec2 morphTargetsTextureSize;

	vec4 getMorph( const in int vertexIndex, const in int morphTargetIndex, const in int offset ) {

		int texelIndex = vertexIndex * MORPHTARGETS_TEXTURE_STRIDE + offset;
		int y = texelIndex / morphTargetsTextureSize.x;
		int x = texelIndex - y * morphTargetsTextureSize.x;

		ivec3 morphUV = ivec3( x, y, morphTargetIndex );
		return texelFetch( morphTargetsTexture, morphUV, 0 );

	}

#endif
`
), dye = (
  /* glsl */
  `
#ifdef USE_MORPHTARGETS

	// morphTargetBaseInfluence is set based on BufferGeometry.morphTargetsRelative value:
	// When morphTargetsRelative is false, this is set to 1 - sum(influences); this results in position = sum((target - base) * influence)
	// When morphTargetsRelative is true, this is set to 1; as a result, all morph targets are simply added to the base after weighting
	transformed *= morphTargetBaseInfluence;

	for ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {

		if ( morphTargetInfluences[ i ] != 0.0 ) transformed += getMorph( gl_VertexID, i, 0 ).xyz * morphTargetInfluences[ i ];

	}

#endif
`
), fye = (
  /* glsl */
  `
float faceDirection = gl_FrontFacing ? 1.0 : - 1.0;

#ifdef FLAT_SHADED

	vec3 fdx = dFdx( vViewPosition );
	vec3 fdy = dFdy( vViewPosition );
	vec3 normal = normalize( cross( fdx, fdy ) );

#else

	vec3 normal = normalize( vNormal );

	#ifdef DOUBLE_SIDED

		normal *= faceDirection;

	#endif

#endif

#if defined( USE_NORMALMAP_TANGENTSPACE ) || defined( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY )

	#ifdef USE_TANGENT

		mat3 tbn = mat3( normalize( vTangent ), normalize( vBitangent ), normal );

	#else

		mat3 tbn = getTangentFrame( - vViewPosition, normal,
		#if defined( USE_NORMALMAP )
			vNormalMapUv
		#elif defined( USE_CLEARCOAT_NORMALMAP )
			vClearcoatNormalMapUv
		#else
			vUv
		#endif
		);

	#endif

	#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )

		tbn[0] *= faceDirection;
		tbn[1] *= faceDirection;

	#endif

#endif

#ifdef USE_CLEARCOAT_NORMALMAP

	#ifdef USE_TANGENT

		mat3 tbn2 = mat3( normalize( vTangent ), normalize( vBitangent ), normal );

	#else

		mat3 tbn2 = getTangentFrame( - vViewPosition, normal, vClearcoatNormalMapUv );

	#endif

	#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )

		tbn2[0] *= faceDirection;
		tbn2[1] *= faceDirection;

	#endif

#endif

// non perturbed normal for clearcoat among others

vec3 nonPerturbedNormal = normal;

`
), pye = (
  /* glsl */
  `

#ifdef USE_NORMALMAP_OBJECTSPACE

	normal = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0; // overrides both flatShading and attribute normals

	#ifdef FLIP_SIDED

		normal = - normal;

	#endif

	#ifdef DOUBLE_SIDED

		normal = normal * faceDirection;

	#endif

	normal = normalize( normalMatrix * normal );

#elif defined( USE_NORMALMAP_TANGENTSPACE )

	vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
	mapN.xy *= normalScale;

	normal = normalize( tbn * mapN );

#elif defined( USE_BUMPMAP )

	normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );

#endif
`
), mye = (
  /* glsl */
  `
#ifndef FLAT_SHADED

	varying vec3 vNormal;

	#ifdef USE_TANGENT

		varying vec3 vTangent;
		varying vec3 vBitangent;

	#endif

#endif
`
), gye = (
  /* glsl */
  `
#ifndef FLAT_SHADED

	varying vec3 vNormal;

	#ifdef USE_TANGENT

		varying vec3 vTangent;
		varying vec3 vBitangent;

	#endif

#endif
`
), vye = (
  /* glsl */
  `
#ifndef FLAT_SHADED // normal is computed with derivatives when FLAT_SHADED

	vNormal = normalize( transformedNormal );

	#ifdef USE_TANGENT

		vTangent = normalize( transformedTangent );
		vBitangent = normalize( cross( vNormal, vTangent ) * tangent.w );

	#endif

#endif
`
), yye = (
  /* glsl */
  `
#ifdef USE_NORMALMAP

	uniform sampler2D normalMap;
	uniform vec2 normalScale;

#endif

#ifdef USE_NORMALMAP_OBJECTSPACE

	uniform mat3 normalMatrix;

#endif

#if ! defined ( USE_TANGENT ) && ( defined ( USE_NORMALMAP_TANGENTSPACE ) || defined ( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY ) )

	// Normal Mapping Without Precomputed Tangents
	// http://www.thetenthplanet.de/archives/1180

	mat3 getTangentFrame( vec3 eye_pos, vec3 surf_norm, vec2 uv ) {

		vec3 q0 = dFdx( eye_pos.xyz );
		vec3 q1 = dFdy( eye_pos.xyz );
		vec2 st0 = dFdx( uv.st );
		vec2 st1 = dFdy( uv.st );

		vec3 N = surf_norm; // normalized

		vec3 q1perp = cross( q1, N );
		vec3 q0perp = cross( N, q0 );

		vec3 T = q1perp * st0.x + q0perp * st1.x;
		vec3 B = q1perp * st0.y + q0perp * st1.y;

		float det = max( dot( T, T ), dot( B, B ) );
		float scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );

		return mat3( T * scale, B * scale, N );

	}

#endif
`
), xye = (
  /* glsl */
  `
#ifdef USE_CLEARCOAT

	vec3 clearcoatNormal = nonPerturbedNormal;

#endif
`
), Aye = (
  /* glsl */
  `
#ifdef USE_CLEARCOAT_NORMALMAP

	vec3 clearcoatMapN = texture2D( clearcoatNormalMap, vClearcoatNormalMapUv ).xyz * 2.0 - 1.0;
	clearcoatMapN.xy *= clearcoatNormalScale;

	clearcoatNormal = normalize( tbn2 * clearcoatMapN );

#endif
`
), bye = (
  /* glsl */
  `

#ifdef USE_CLEARCOATMAP

	uniform sampler2D clearcoatMap;

#endif

#ifdef USE_CLEARCOAT_NORMALMAP

	uniform sampler2D clearcoatNormalMap;
	uniform vec2 clearcoatNormalScale;

#endif

#ifdef USE_CLEARCOAT_ROUGHNESSMAP

	uniform sampler2D clearcoatRoughnessMap;

#endif
`
), Mye = (
  /* glsl */
  `

#ifdef USE_IRIDESCENCEMAP

	uniform sampler2D iridescenceMap;

#endif

#ifdef USE_IRIDESCENCE_THICKNESSMAP

	uniform sampler2D iridescenceThicknessMap;

#endif
`
), Sye = (
  /* glsl */
  `
#ifdef OPAQUE
diffuseColor.a = 1.0;
#endif

#ifdef USE_TRANSMISSION
diffuseColor.a *= material.transmissionAlpha;
#endif

gl_FragColor = vec4( outgoingLight, diffuseColor.a );
`
), wye = (
  /* glsl */
  `
vec3 packNormalToRGB( const in vec3 normal ) {
	return normalize( normal ) * 0.5 + 0.5;
}

vec3 unpackRGBToNormal( const in vec3 rgb ) {
	return 2.0 * rgb.xyz - 1.0;
}

const float PackUpscale = 256. / 255.; // fraction -> 0..1 (including 1)
const float UnpackDownscale = 255. / 256.; // 0..1 -> fraction (excluding 1)

const vec3 PackFactors = vec3( 256. * 256. * 256., 256. * 256., 256. );
const vec4 UnpackFactors = UnpackDownscale / vec4( PackFactors, 1. );

const float ShiftRight8 = 1. / 256.;

vec4 packDepthToRGBA( const in float v ) {
	vec4 r = vec4( fract( v * PackFactors ), v );
	r.yzw -= r.xyz * ShiftRight8; // tidy overflow
	return r * PackUpscale;
}

float unpackRGBAToDepth( const in vec4 v ) {
	return dot( v, UnpackFactors );
}

vec2 packDepthToRG( in highp float v ) {
	return packDepthToRGBA( v ).yx;
}

float unpackRGToDepth( const in highp vec2 v ) {
	return unpackRGBAToDepth( vec4( v.xy, 0.0, 0.0 ) );
}

vec4 pack2HalfToRGBA( vec2 v ) {
	vec4 r = vec4( v.x, fract( v.x * 255.0 ), v.y, fract( v.y * 255.0 ) );
	return vec4( r.x - r.y / 255.0, r.y, r.z - r.w / 255.0, r.w );
}

vec2 unpackRGBATo2Half( vec4 v ) {
	return vec2( v.x + ( v.y / 255.0 ), v.z + ( v.w / 255.0 ) );
}

// NOTE: viewZ, the z-coordinate in camera space, is negative for points in front of the camera

float viewZToOrthographicDepth( const in float viewZ, const in float near, const in float far ) {
	// -near maps to 0; -far maps to 1
	return ( viewZ + near ) / ( near - far );
}

float orthographicDepthToViewZ( const in float depth, const in float near, const in float far ) {
	// maps orthographic depth in [ 0, 1 ] to viewZ
	return depth * ( near - far ) - near;
}

// NOTE: https://twitter.com/gonnavis/status/1377183786949959682

float viewZToPerspectiveDepth( const in float viewZ, const in float near, const in float far ) {
	// -near maps to 0; -far maps to 1
	return ( ( near + viewZ ) * far ) / ( ( far - near ) * viewZ );
}

float perspectiveDepthToViewZ( const in float depth, const in float near, const in float far ) {
	// maps perspective depth in [ 0, 1 ] to viewZ
	return ( near * far ) / ( ( far - near ) * depth - far );
}
`
), Eye = (
  /* glsl */
  `
#ifdef PREMULTIPLIED_ALPHA

	// Get get normal blending with premultipled, use with CustomBlending, OneFactor, OneMinusSrcAlphaFactor, AddEquation.
	gl_FragColor.rgb *= gl_FragColor.a;

#endif
`
), Tye = (
  /* glsl */
  `
vec4 mvPosition = vec4( transformed, 1.0 );

#ifdef USE_BATCHING

	mvPosition = batchingMatrix * mvPosition;

#endif

#ifdef USE_INSTANCING

	mvPosition = instanceMatrix * mvPosition;

#endif

mvPosition = modelViewMatrix * mvPosition;

gl_Position = projectionMatrix * mvPosition;
`
), Cye = (
  /* glsl */
  `
#ifdef DITHERING

	gl_FragColor.rgb = dithering( gl_FragColor.rgb );

#endif
`
), _ye = (
  /* glsl */
  `
#ifdef DITHERING

	// based on https://www.shadertoy.com/view/MslGR8
	vec3 dithering( vec3 color ) {
		//Calculate grid position
		float grid_position = rand( gl_FragCoord.xy );

		//Shift the individual colors differently, thus making it even harder to see the dithering pattern
		vec3 dither_shift_RGB = vec3( 0.25 / 255.0, -0.25 / 255.0, 0.25 / 255.0 );

		//modify shift according to grid position.
		dither_shift_RGB = mix( 2.0 * dither_shift_RGB, -2.0 * dither_shift_RGB, grid_position );

		//shift the color by dither_shift
		return color + dither_shift_RGB;
	}

#endif
`
), Pye = (
  /* glsl */
  `
float roughnessFactor = roughness;

#ifdef USE_ROUGHNESSMAP

	vec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );

	// reads channel G, compatible with a combined OcclusionRoughnessMetallic (RGB) texture
	roughnessFactor *= texelRoughness.g;

#endif
`
), Rye = (
  /* glsl */
  `
#ifdef USE_ROUGHNESSMAP

	uniform sampler2D roughnessMap;

#endif
`
), Dye = (
  /* glsl */
  `
#if NUM_SPOT_LIGHT_COORDS > 0

	varying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];

#endif

#if NUM_SPOT_LIGHT_MAPS > 0

	uniform sampler2D spotLightMap[ NUM_SPOT_LIGHT_MAPS ];

#endif

#ifdef USE_SHADOWMAP

	#if NUM_DIR_LIGHT_SHADOWS > 0

		uniform sampler2D directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];
		varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];

		struct DirectionalLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};

		uniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];

	#endif

	#if NUM_SPOT_LIGHT_SHADOWS > 0

		uniform sampler2D spotShadowMap[ NUM_SPOT_LIGHT_SHADOWS ];

		struct SpotLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};

		uniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];

	#endif

	#if NUM_POINT_LIGHT_SHADOWS > 0

		uniform sampler2D pointShadowMap[ NUM_POINT_LIGHT_SHADOWS ];
		varying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];

		struct PointLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
			float shadowCameraNear;
			float shadowCameraFar;
		};

		uniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];

	#endif

	/*
	#if NUM_RECT_AREA_LIGHTS > 0

		// TODO (abelnation): create uniforms for area light shadows

	#endif
	*/

	float texture2DCompare( sampler2D depths, vec2 uv, float compare ) {

		return step( compare, unpackRGBAToDepth( texture2D( depths, uv ) ) );

	}

	vec2 texture2DDistribution( sampler2D shadow, vec2 uv ) {

		return unpackRGBATo2Half( texture2D( shadow, uv ) );

	}

	float VSMShadow (sampler2D shadow, vec2 uv, float compare ){

		float occlusion = 1.0;

		vec2 distribution = texture2DDistribution( shadow, uv );

		float hard_shadow = step( compare , distribution.x ); // Hard Shadow

		if (hard_shadow != 1.0 ) {

			float distance = compare - distribution.x ;
			float variance = max( 0.00000, distribution.y * distribution.y );
			float softness_probability = variance / (variance + distance * distance ); // Chebeyshevs inequality
			softness_probability = clamp( ( softness_probability - 0.3 ) / ( 0.95 - 0.3 ), 0.0, 1.0 ); // 0.3 reduces light bleed
			occlusion = clamp( max( hard_shadow, softness_probability ), 0.0, 1.0 );

		}
		return occlusion;

	}

	float getShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord ) {

		float shadow = 1.0;

		shadowCoord.xyz /= shadowCoord.w;
		shadowCoord.z += shadowBias;

		bool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;
		bool frustumTest = inFrustum && shadowCoord.z <= 1.0;

		if ( frustumTest ) {

		#if defined( SHADOWMAP_TYPE_PCF )

			vec2 texelSize = vec2( 1.0 ) / shadowMapSize;

			float dx0 = - texelSize.x * shadowRadius;
			float dy0 = - texelSize.y * shadowRadius;
			float dx1 = + texelSize.x * shadowRadius;
			float dy1 = + texelSize.y * shadowRadius;
			float dx2 = dx0 / 2.0;
			float dy2 = dy0 / 2.0;
			float dx3 = dx1 / 2.0;
			float dy3 = dy1 / 2.0;

			shadow = (
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy2 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy3 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy1 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy1 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy1 ), shadowCoord.z )
			) * ( 1.0 / 17.0 );

		#elif defined( SHADOWMAP_TYPE_PCF_SOFT )

			vec2 texelSize = vec2( 1.0 ) / shadowMapSize;
			float dx = texelSize.x;
			float dy = texelSize.y;

			vec2 uv = shadowCoord.xy;
			vec2 f = fract( uv * shadowMapSize + 0.5 );
			uv -= f * texelSize;

			shadow = (
				texture2DCompare( shadowMap, uv, shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + vec2( dx, 0.0 ), shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + vec2( 0.0, dy ), shadowCoord.z ) +
				texture2DCompare( shadowMap, uv + texelSize, shadowCoord.z ) +
				mix( texture2DCompare( shadowMap, uv + vec2( -dx, 0.0 ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 0.0 ), shadowCoord.z ),
					 f.x ) +
				mix( texture2DCompare( shadowMap, uv + vec2( -dx, dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, dy ), shadowCoord.z ),
					 f.x ) +
				mix( texture2DCompare( shadowMap, uv + vec2( 0.0, -dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( 0.0, 2.0 * dy ), shadowCoord.z ),
					 f.y ) +
				mix( texture2DCompare( shadowMap, uv + vec2( dx, -dy ), shadowCoord.z ),
					 texture2DCompare( shadowMap, uv + vec2( dx, 2.0 * dy ), shadowCoord.z ),
					 f.y ) +
				mix( mix( texture2DCompare( shadowMap, uv + vec2( -dx, -dy ), shadowCoord.z ),
						  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, -dy ), shadowCoord.z ),
						  f.x ),
					 mix( texture2DCompare( shadowMap, uv + vec2( -dx, 2.0 * dy ), shadowCoord.z ),
						  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 2.0 * dy ), shadowCoord.z ),
						  f.x ),
					 f.y )
			) * ( 1.0 / 9.0 );

		#elif defined( SHADOWMAP_TYPE_VSM )

			shadow = VSMShadow( shadowMap, shadowCoord.xy, shadowCoord.z );

		#else // no percentage-closer filtering:

			shadow = texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z );

		#endif

		}

		return mix( 1.0, shadow, shadowIntensity );

	}

	// cubeToUV() maps a 3D direction vector suitable for cube texture mapping to a 2D
	// vector suitable for 2D texture mapping. This code uses the following layout for the
	// 2D texture:
	//
	// xzXZ
	//  y Y
	//
	// Y - Positive y direction
	// y - Negative y direction
	// X - Positive x direction
	// x - Negative x direction
	// Z - Positive z direction
	// z - Negative z direction
	//
	// Source and test bed:
	// https://gist.github.com/tschw/da10c43c467ce8afd0c4

	vec2 cubeToUV( vec3 v, float texelSizeY ) {

		// Number of texels to avoid at the edge of each square

		vec3 absV = abs( v );

		// Intersect unit cube

		float scaleToCube = 1.0 / max( absV.x, max( absV.y, absV.z ) );
		absV *= scaleToCube;

		// Apply scale to avoid seams

		// two texels less per square (one texel will do for NEAREST)
		v *= scaleToCube * ( 1.0 - 2.0 * texelSizeY );

		// Unwrap

		// space: -1 ... 1 range for each square
		//
		// #X##		dim    := ( 4 , 2 )
		//  # #		center := ( 1 , 1 )

		vec2 planar = v.xy;

		float almostATexel = 1.5 * texelSizeY;
		float almostOne = 1.0 - almostATexel;

		if ( absV.z >= almostOne ) {

			if ( v.z > 0.0 )
				planar.x = 4.0 - v.x;

		} else if ( absV.x >= almostOne ) {

			float signX = sign( v.x );
			planar.x = v.z * signX + 2.0 * signX;

		} else if ( absV.y >= almostOne ) {

			float signY = sign( v.y );
			planar.x = v.x + 2.0 * signY + 2.0;
			planar.y = v.z * signY - 2.0;

		}

		// Transform to UV space

		// scale := 0.5 / dim
		// translate := ( center + 0.5 ) / dim
		return vec2( 0.125, 0.25 ) * planar + vec2( 0.375, 0.75 );

	}

	float getPointShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord, float shadowCameraNear, float shadowCameraFar ) {

		float shadow = 1.0;

		// for point lights, the uniform @vShadowCoord is re-purposed to hold
		// the vector from the light to the world-space position of the fragment.
		vec3 lightToPosition = shadowCoord.xyz;
		
		float lightToPositionLength = length( lightToPosition );

		if ( lightToPositionLength - shadowCameraFar <= 0.0 && lightToPositionLength - shadowCameraNear >= 0.0 ) {

			// dp = normalized distance from light to fragment position
			float dp = ( lightToPositionLength - shadowCameraNear ) / ( shadowCameraFar - shadowCameraNear ); // need to clamp?
			dp += shadowBias;

			// bd3D = base direction 3D
			vec3 bd3D = normalize( lightToPosition );

			vec2 texelSize = vec2( 1.0 ) / ( shadowMapSize * vec2( 4.0, 2.0 ) );

			#if defined( SHADOWMAP_TYPE_PCF ) || defined( SHADOWMAP_TYPE_PCF_SOFT ) || defined( SHADOWMAP_TYPE_VSM )

				vec2 offset = vec2( - 1, 1 ) * shadowRadius * texelSize.y;

				shadow = (
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxy, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxx, texelSize.y ), dp ) +
					texture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxx, texelSize.y ), dp )
				) * ( 1.0 / 9.0 );

			#else // no percentage-closer filtering

				shadow = texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp );

			#endif

		}

		return mix( 1.0, shadow, shadowIntensity );

	}

#endif
`
), Lye = (
  /* glsl */
  `

#if NUM_SPOT_LIGHT_COORDS > 0

	uniform mat4 spotLightMatrix[ NUM_SPOT_LIGHT_COORDS ];
	varying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];

#endif

#ifdef USE_SHADOWMAP

	#if NUM_DIR_LIGHT_SHADOWS > 0

		uniform mat4 directionalShadowMatrix[ NUM_DIR_LIGHT_SHADOWS ];
		varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];

		struct DirectionalLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};

		uniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];

	#endif

	#if NUM_SPOT_LIGHT_SHADOWS > 0

		struct SpotLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
		};

		uniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];

	#endif

	#if NUM_POINT_LIGHT_SHADOWS > 0

		uniform mat4 pointShadowMatrix[ NUM_POINT_LIGHT_SHADOWS ];
		varying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];

		struct PointLightShadow {
			float shadowIntensity;
			float shadowBias;
			float shadowNormalBias;
			float shadowRadius;
			vec2 shadowMapSize;
			float shadowCameraNear;
			float shadowCameraFar;
		};

		uniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];

	#endif

	/*
	#if NUM_RECT_AREA_LIGHTS > 0

		// TODO (abelnation): uniforms for area light shadows

	#endif
	*/

#endif
`
), Iye = (
  /* glsl */
  `

#if ( defined( USE_SHADOWMAP ) && ( NUM_DIR_LIGHT_SHADOWS > 0 || NUM_POINT_LIGHT_SHADOWS > 0 ) ) || ( NUM_SPOT_LIGHT_COORDS > 0 )

	// Offsetting the position used for querying occlusion along the world normal can be used to reduce shadow acne.
	vec3 shadowWorldNormal = inverseTransformDirection( transformedNormal, viewMatrix );
	vec4 shadowWorldPosition;

#endif

#if defined( USE_SHADOWMAP )

	#if NUM_DIR_LIGHT_SHADOWS > 0

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {

			shadowWorldPosition = worldPosition + vec4( shadowWorldNormal * directionalLightShadows[ i ].shadowNormalBias, 0 );
			vDirectionalShadowCoord[ i ] = directionalShadowMatrix[ i ] * shadowWorldPosition;

		}
		#pragma unroll_loop_end

	#endif

	#if NUM_POINT_LIGHT_SHADOWS > 0

		#pragma unroll_loop_start
		for ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {

			shadowWorldPosition = worldPosition + vec4( shadowWorldNormal * pointLightShadows[ i ].shadowNormalBias, 0 );
			vPointShadowCoord[ i ] = pointShadowMatrix[ i ] * shadowWorldPosition;

		}
		#pragma unroll_loop_end

	#endif

	/*
	#if NUM_RECT_AREA_LIGHTS > 0

		// TODO (abelnation): update vAreaShadowCoord with area light info

	#endif
	*/

#endif

// spot lights can be evaluated without active shadow mapping (when SpotLight.map is used)

#if NUM_SPOT_LIGHT_COORDS > 0

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHT_COORDS; i ++ ) {

		shadowWorldPosition = worldPosition;
		#if ( defined( USE_SHADOWMAP ) && UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )
			shadowWorldPosition.xyz += shadowWorldNormal * spotLightShadows[ i ].shadowNormalBias;
		#endif
		vSpotLightCoord[ i ] = spotLightMatrix[ i ] * shadowWorldPosition;

	}
	#pragma unroll_loop_end

#endif


`
), Oye = (
  /* glsl */
  `
float getShadowMask() {

	float shadow = 1.0;

	#ifdef USE_SHADOWMAP

	#if NUM_DIR_LIGHT_SHADOWS > 0

	DirectionalLightShadow directionalLight;

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {

		directionalLight = directionalLightShadows[ i ];
		shadow *= receiveShadow ? getShadow( directionalShadowMap[ i ], directionalLight.shadowMapSize, directionalLight.shadowIntensity, directionalLight.shadowBias, directionalLight.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;

	}
	#pragma unroll_loop_end

	#endif

	#if NUM_SPOT_LIGHT_SHADOWS > 0

	SpotLightShadow spotLight;

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_SPOT_LIGHT_SHADOWS; i ++ ) {

		spotLight = spotLightShadows[ i ];
		shadow *= receiveShadow ? getShadow( spotShadowMap[ i ], spotLight.shadowMapSize, spotLight.shadowIntensity, spotLight.shadowBias, spotLight.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;

	}
	#pragma unroll_loop_end

	#endif

	#if NUM_POINT_LIGHT_SHADOWS > 0

	PointLightShadow pointLight;

	#pragma unroll_loop_start
	for ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {

		pointLight = pointLightShadows[ i ];
		shadow *= receiveShadow ? getPointShadow( pointShadowMap[ i ], pointLight.shadowMapSize, pointLight.shadowIntensity, pointLight.shadowBias, pointLight.shadowRadius, vPointShadowCoord[ i ], pointLight.shadowCameraNear, pointLight.shadowCameraFar ) : 1.0;

	}
	#pragma unroll_loop_end

	#endif

	/*
	#if NUM_RECT_AREA_LIGHTS > 0

		// TODO (abelnation): update shadow for Area light

	#endif
	*/

	#endif

	return shadow;

}
`
), Nye = (
  /* glsl */
  `
#ifdef USE_SKINNING

	mat4 boneMatX = getBoneMatrix( skinIndex.x );
	mat4 boneMatY = getBoneMatrix( skinIndex.y );
	mat4 boneMatZ = getBoneMatrix( skinIndex.z );
	mat4 boneMatW = getBoneMatrix( skinIndex.w );

#endif
`
), Bye = (
  /* glsl */
  `
#ifdef USE_SKINNING

	uniform mat4 bindMatrix;
	uniform mat4 bindMatrixInverse;

	uniform highp sampler2D boneTexture;

	mat4 getBoneMatrix( const in float i ) {

		int size = textureSize( boneTexture, 0 ).x;
		int j = int( i ) * 4;
		int x = j % size;
		int y = j / size;
		vec4 v1 = texelFetch( boneTexture, ivec2( x, y ), 0 );
		vec4 v2 = texelFetch( boneTexture, ivec2( x + 1, y ), 0 );
		vec4 v3 = texelFetch( boneTexture, ivec2( x + 2, y ), 0 );
		vec4 v4 = texelFetch( boneTexture, ivec2( x + 3, y ), 0 );

		return mat4( v1, v2, v3, v4 );

	}

#endif
`
), Fye = (
  /* glsl */
  `
#ifdef USE_SKINNING

	vec4 skinVertex = bindMatrix * vec4( transformed, 1.0 );

	vec4 skinned = vec4( 0.0 );
	skinned += boneMatX * skinVertex * skinWeight.x;
	skinned += boneMatY * skinVertex * skinWeight.y;
	skinned += boneMatZ * skinVertex * skinWeight.z;
	skinned += boneMatW * skinVertex * skinWeight.w;

	transformed = ( bindMatrixInverse * skinned ).xyz;

#endif
`
), Uye = (
  /* glsl */
  `
#ifdef USE_SKINNING

	mat4 skinMatrix = mat4( 0.0 );
	skinMatrix += skinWeight.x * boneMatX;
	skinMatrix += skinWeight.y * boneMatY;
	skinMatrix += skinWeight.z * boneMatZ;
	skinMatrix += skinWeight.w * boneMatW;
	skinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;

	objectNormal = vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz;

	#ifdef USE_TANGENT

		objectTangent = vec4( skinMatrix * vec4( objectTangent, 0.0 ) ).xyz;

	#endif

#endif
`
), zye = (
  /* glsl */
  `
float specularStrength;

#ifdef USE_SPECULARMAP

	vec4 texelSpecular = texture2D( specularMap, vSpecularMapUv );
	specularStrength = texelSpecular.r;

#else

	specularStrength = 1.0;

#endif
`
), kye = (
  /* glsl */
  `
#ifdef USE_SPECULARMAP

	uniform sampler2D specularMap;

#endif
`
), Gye = (
  /* glsl */
  `
#if defined( TONE_MAPPING )

	gl_FragColor.rgb = toneMapping( gl_FragColor.rgb );

#endif
`
), Hye = (
  /* glsl */
  `
#ifndef saturate
// <common> may have defined saturate() already
#define saturate( a ) clamp( a, 0.0, 1.0 )
#endif

uniform float toneMappingExposure;

// exposure only
vec3 LinearToneMapping( vec3 color ) {

	return saturate( toneMappingExposure * color );

}

// source: https://www.cs.utah.edu/docs/techreports/2002/pdf/UUCS-02-001.pdf
vec3 ReinhardToneMapping( vec3 color ) {

	color *= toneMappingExposure;
	return saturate( color / ( vec3( 1.0 ) + color ) );

}

// source: http://filmicworlds.com/blog/filmic-tonemapping-operators/
vec3 OptimizedCineonToneMapping( vec3 color ) {

	// optimized filmic operator by Jim Hejl and Richard Burgess-Dawson
	color *= toneMappingExposure;
	color = max( vec3( 0.0 ), color - 0.004 );
	return pow( ( color * ( 6.2 * color + 0.5 ) ) / ( color * ( 6.2 * color + 1.7 ) + 0.06 ), vec3( 2.2 ) );

}

// source: https://github.com/selfshadow/ltc_code/blob/master/webgl/shaders/ltc/ltc_blit.fs
vec3 RRTAndODTFit( vec3 v ) {

	vec3 a = v * ( v + 0.0245786 ) - 0.000090537;
	vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;
	return a / b;

}

// this implementation of ACES is modified to accommodate a brighter viewing environment.
// the scale factor of 1/0.6 is subjective. see discussion in #19621.

vec3 ACESFilmicToneMapping( vec3 color ) {

	// sRGB => XYZ => D65_2_D60 => AP1 => RRT_SAT
	const mat3 ACESInputMat = mat3(
		vec3( 0.59719, 0.07600, 0.02840 ), // transposed from source
		vec3( 0.35458, 0.90834, 0.13383 ),
		vec3( 0.04823, 0.01566, 0.83777 )
	);

	// ODT_SAT => XYZ => D60_2_D65 => sRGB
	const mat3 ACESOutputMat = mat3(
		vec3(  1.60475, -0.10208, -0.00327 ), // transposed from source
		vec3( -0.53108,  1.10813, -0.07276 ),
		vec3( -0.07367, -0.00605,  1.07602 )
	);

	color *= toneMappingExposure / 0.6;

	color = ACESInputMat * color;

	// Apply RRT and ODT
	color = RRTAndODTFit( color );

	color = ACESOutputMat * color;

	// Clamp to [0, 1]
	return saturate( color );

}

// Matrices for rec 2020 <> rec 709 color space conversion
// matrix provided in row-major order so it has been transposed
// https://www.itu.int/pub/R-REP-BT.2407-2017
const mat3 LINEAR_REC2020_TO_LINEAR_SRGB = mat3(
	vec3( 1.6605, - 0.1246, - 0.0182 ),
	vec3( - 0.5876, 1.1329, - 0.1006 ),
	vec3( - 0.0728, - 0.0083, 1.1187 )
);

const mat3 LINEAR_SRGB_TO_LINEAR_REC2020 = mat3(
	vec3( 0.6274, 0.0691, 0.0164 ),
	vec3( 0.3293, 0.9195, 0.0880 ),
	vec3( 0.0433, 0.0113, 0.8956 )
);

// https://iolite-engine.com/blog_posts/minimal_agx_implementation
// Mean error^2: 3.6705141e-06
vec3 agxDefaultContrastApprox( vec3 x ) {

	vec3 x2 = x * x;
	vec3 x4 = x2 * x2;

	return + 15.5 * x4 * x2
		- 40.14 * x4 * x
		+ 31.96 * x4
		- 6.868 * x2 * x
		+ 0.4298 * x2
		+ 0.1191 * x
		- 0.00232;

}

// AgX Tone Mapping implementation based on Filament, which in turn is based
// on Blender's implementation using rec 2020 primaries
// https://github.com/google/filament/pull/7236
// Inputs and outputs are encoded as Linear-sRGB.

vec3 AgXToneMapping( vec3 color ) {

	// AgX constants
	const mat3 AgXInsetMatrix = mat3(
		vec3( 0.856627153315983, 0.137318972929847, 0.11189821299995 ),
		vec3( 0.0951212405381588, 0.761241990602591, 0.0767994186031903 ),
		vec3( 0.0482516061458583, 0.101439036467562, 0.811302368396859 )
	);

	// explicit AgXOutsetMatrix generated from Filaments AgXOutsetMatrixInv
	const mat3 AgXOutsetMatrix = mat3(
		vec3( 1.1271005818144368, - 0.1413297634984383, - 0.14132976349843826 ),
		vec3( - 0.11060664309660323, 1.157823702216272, - 0.11060664309660294 ),
		vec3( - 0.016493938717834573, - 0.016493938717834257, 1.2519364065950405 )
	);

	// LOG2_MIN      = -10.0
	// LOG2_MAX      =  +6.5
	// MIDDLE_GRAY   =  0.18
	const float AgxMinEv = - 12.47393;  // log2( pow( 2, LOG2_MIN ) * MIDDLE_GRAY )
	const float AgxMaxEv = 4.026069;    // log2( pow( 2, LOG2_MAX ) * MIDDLE_GRAY )

	color *= toneMappingExposure;

	color = LINEAR_SRGB_TO_LINEAR_REC2020 * color;

	color = AgXInsetMatrix * color;

	// Log2 encoding
	color = max( color, 1e-10 ); // avoid 0 or negative numbers for log2
	color = log2( color );
	color = ( color - AgxMinEv ) / ( AgxMaxEv - AgxMinEv );

	color = clamp( color, 0.0, 1.0 );

	// Apply sigmoid
	color = agxDefaultContrastApprox( color );

	// Apply AgX look
	// v = agxLook(v, look);

	color = AgXOutsetMatrix * color;

	// Linearize
	color = pow( max( vec3( 0.0 ), color ), vec3( 2.2 ) );

	color = LINEAR_REC2020_TO_LINEAR_SRGB * color;

	// Gamut mapping. Simple clamp for now.
	color = clamp( color, 0.0, 1.0 );

	return color;

}

// https://modelviewer.dev/examples/tone-mapping

vec3 NeutralToneMapping( vec3 color ) {

	const float StartCompression = 0.8 - 0.04;
	const float Desaturation = 0.15;

	color *= toneMappingExposure;

	float x = min( color.r, min( color.g, color.b ) );

	float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;

	color -= offset;

	float peak = max( color.r, max( color.g, color.b ) );

	if ( peak < StartCompression ) return color;

	float d = 1. - StartCompression;

	float newPeak = 1. - d * d / ( peak + d - StartCompression );

	color *= newPeak / peak;

	float g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. );

	return mix( color, vec3( newPeak ), g );

}

vec3 CustomToneMapping( vec3 color ) { return color; }
`
), Vye = (
  /* glsl */
  `
#ifdef USE_TRANSMISSION

	material.transmission = transmission;
	material.transmissionAlpha = 1.0;
	material.thickness = thickness;
	material.attenuationDistance = attenuationDistance;
	material.attenuationColor = attenuationColor;

	#ifdef USE_TRANSMISSIONMAP

		material.transmission *= texture2D( transmissionMap, vTransmissionMapUv ).r;

	#endif

	#ifdef USE_THICKNESSMAP

		material.thickness *= texture2D( thicknessMap, vThicknessMapUv ).g;

	#endif

	vec3 pos = vWorldPosition;
	vec3 v = normalize( cameraPosition - pos );
	vec3 n = inverseTransformDirection( normal, viewMatrix );

	vec4 transmitted = getIBLVolumeRefraction(
		n, v, material.roughness, material.diffuseColor, material.specularColor, material.specularF90,
		pos, modelMatrix, viewMatrix, projectionMatrix, material.dispersion, material.ior, material.thickness,
		material.attenuationColor, material.attenuationDistance );

	material.transmissionAlpha = mix( material.transmissionAlpha, transmitted.a, material.transmission );

	totalDiffuse = mix( totalDiffuse, transmitted.rgb, material.transmission );

#endif
`
), Wye = (
  /* glsl */
  `
#ifdef USE_TRANSMISSION

	// Transmission code is based on glTF-Sampler-Viewer
	// https://github.com/KhronosGroup/glTF-Sample-Viewer

	uniform float transmission;
	uniform float thickness;
	uniform float attenuationDistance;
	uniform vec3 attenuationColor;

	#ifdef USE_TRANSMISSIONMAP

		uniform sampler2D transmissionMap;

	#endif

	#ifdef USE_THICKNESSMAP

		uniform sampler2D thicknessMap;

	#endif

	uniform vec2 transmissionSamplerSize;
	uniform sampler2D transmissionSamplerMap;

	uniform mat4 modelMatrix;
	uniform mat4 projectionMatrix;

	varying vec3 vWorldPosition;

	// Mipped Bicubic Texture Filtering by N8
	// https://www.shadertoy.com/view/Dl2SDW

	float w0( float a ) {

		return ( 1.0 / 6.0 ) * ( a * ( a * ( - a + 3.0 ) - 3.0 ) + 1.0 );

	}

	float w1( float a ) {

		return ( 1.0 / 6.0 ) * ( a *  a * ( 3.0 * a - 6.0 ) + 4.0 );

	}

	float w2( float a ){

		return ( 1.0 / 6.0 ) * ( a * ( a * ( - 3.0 * a + 3.0 ) + 3.0 ) + 1.0 );

	}

	float w3( float a ) {

		return ( 1.0 / 6.0 ) * ( a * a * a );

	}

	// g0 and g1 are the two amplitude functions
	float g0( float a ) {

		return w0( a ) + w1( a );

	}

	float g1( float a ) {

		return w2( a ) + w3( a );

	}

	// h0 and h1 are the two offset functions
	float h0( float a ) {

		return - 1.0 + w1( a ) / ( w0( a ) + w1( a ) );

	}

	float h1( float a ) {

		return 1.0 + w3( a ) / ( w2( a ) + w3( a ) );

	}

	vec4 bicubic( sampler2D tex, vec2 uv, vec4 texelSize, float lod ) {

		uv = uv * texelSize.zw + 0.5;

		vec2 iuv = floor( uv );
		vec2 fuv = fract( uv );

		float g0x = g0( fuv.x );
		float g1x = g1( fuv.x );
		float h0x = h0( fuv.x );
		float h1x = h1( fuv.x );
		float h0y = h0( fuv.y );
		float h1y = h1( fuv.y );

		vec2 p0 = ( vec2( iuv.x + h0x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;
		vec2 p1 = ( vec2( iuv.x + h1x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;
		vec2 p2 = ( vec2( iuv.x + h0x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;
		vec2 p3 = ( vec2( iuv.x + h1x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;

		return g0( fuv.y ) * ( g0x * textureLod( tex, p0, lod ) + g1x * textureLod( tex, p1, lod ) ) +
			g1( fuv.y ) * ( g0x * textureLod( tex, p2, lod ) + g1x * textureLod( tex, p3, lod ) );

	}

	vec4 textureBicubic( sampler2D sampler, vec2 uv, float lod ) {

		vec2 fLodSize = vec2( textureSize( sampler, int( lod ) ) );
		vec2 cLodSize = vec2( textureSize( sampler, int( lod + 1.0 ) ) );
		vec2 fLodSizeInv = 1.0 / fLodSize;
		vec2 cLodSizeInv = 1.0 / cLodSize;
		vec4 fSample = bicubic( sampler, uv, vec4( fLodSizeInv, fLodSize ), floor( lod ) );
		vec4 cSample = bicubic( sampler, uv, vec4( cLodSizeInv, cLodSize ), ceil( lod ) );
		return mix( fSample, cSample, fract( lod ) );

	}

	vec3 getVolumeTransmissionRay( const in vec3 n, const in vec3 v, const in float thickness, const in float ior, const in mat4 modelMatrix ) {

		// Direction of refracted light.
		vec3 refractionVector = refract( - v, normalize( n ), 1.0 / ior );

		// Compute rotation-independant scaling of the model matrix.
		vec3 modelScale;
		modelScale.x = length( vec3( modelMatrix[ 0 ].xyz ) );
		modelScale.y = length( vec3( modelMatrix[ 1 ].xyz ) );
		modelScale.z = length( vec3( modelMatrix[ 2 ].xyz ) );

		// The thickness is specified in local space.
		return normalize( refractionVector ) * thickness * modelScale;

	}

	float applyIorToRoughness( const in float roughness, const in float ior ) {

		// Scale roughness with IOR so that an IOR of 1.0 results in no microfacet refraction and
		// an IOR of 1.5 results in the default amount of microfacet refraction.
		return roughness * clamp( ior * 2.0 - 2.0, 0.0, 1.0 );

	}

	vec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {

		float lod = log2( transmissionSamplerSize.x ) * applyIorToRoughness( roughness, ior );
		return textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );

	}

	vec3 volumeAttenuation( const in float transmissionDistance, const in vec3 attenuationColor, const in float attenuationDistance ) {

		if ( isinf( attenuationDistance ) ) {

			// Attenuation distance is +∞, i.e. the transmitted color is not attenuated at all.
			return vec3( 1.0 );

		} else {

			// Compute light attenuation using Beer's law.
			vec3 attenuationCoefficient = -log( attenuationColor ) / attenuationDistance;
			vec3 transmittance = exp( - attenuationCoefficient * transmissionDistance ); // Beer's law
			return transmittance;

		}

	}

	vec4 getIBLVolumeRefraction( const in vec3 n, const in vec3 v, const in float roughness, const in vec3 diffuseColor,
		const in vec3 specularColor, const in float specularF90, const in vec3 position, const in mat4 modelMatrix,
		const in mat4 viewMatrix, const in mat4 projMatrix, const in float dispersion, const in float ior, const in float thickness,
		const in vec3 attenuationColor, const in float attenuationDistance ) {

		vec4 transmittedLight;
		vec3 transmittance;

		#ifdef USE_DISPERSION

			float halfSpread = ( ior - 1.0 ) * 0.025 * dispersion;
			vec3 iors = vec3( ior - halfSpread, ior, ior + halfSpread );

			for ( int i = 0; i < 3; i ++ ) {

				vec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, iors[ i ], modelMatrix );
				vec3 refractedRayExit = position + transmissionRay;
		
				// Project refracted vector on the framebuffer, while mapping to normalized device coordinates.
				vec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );
				vec2 refractionCoords = ndcPos.xy / ndcPos.w;
				refractionCoords += 1.0;
				refractionCoords /= 2.0;
		
				// Sample framebuffer to get pixel the refracted ray hits.
				vec4 transmissionSample = getTransmissionSample( refractionCoords, roughness, iors[ i ] );
				transmittedLight[ i ] = transmissionSample[ i ];
				transmittedLight.a += transmissionSample.a;

				transmittance[ i ] = diffuseColor[ i ] * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance )[ i ];

			}

			transmittedLight.a /= 3.0;
		
		#else
		
			vec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, ior, modelMatrix );
			vec3 refractedRayExit = position + transmissionRay;

			// Project refracted vector on the framebuffer, while mapping to normalized device coordinates.
			vec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );
			vec2 refractionCoords = ndcPos.xy / ndcPos.w;
			refractionCoords += 1.0;
			refractionCoords /= 2.0;

			// Sample framebuffer to get pixel the refracted ray hits.
			transmittedLight = getTransmissionSample( refractionCoords, roughness, ior );
			transmittance = diffuseColor * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance );
		
		#endif

		vec3 attenuatedColor = transmittance * transmittedLight.rgb;

		// Get the specular component.
		vec3 F = EnvironmentBRDF( n, v, specularColor, specularF90, roughness );

		// As less light is transmitted, the opacity should be increased. This simple approximation does a decent job 
		// of modulating a CSS background, and has no effect when the buffer is opaque, due to a solid object or clear color.
		float transmittanceFactor = ( transmittance.r + transmittance.g + transmittance.b ) / 3.0;

		return vec4( ( 1.0 - F ) * attenuatedColor, 1.0 - ( 1.0 - transmittedLight.a ) * transmittanceFactor );

	}
#endif
`
), Xye = (
  /* glsl */
  `
#if defined( USE_UV ) || defined( USE_ANISOTROPY )

	varying vec2 vUv;

#endif
#ifdef USE_MAP

	varying vec2 vMapUv;

#endif
#ifdef USE_ALPHAMAP

	varying vec2 vAlphaMapUv;

#endif
#ifdef USE_LIGHTMAP

	varying vec2 vLightMapUv;

#endif
#ifdef USE_AOMAP

	varying vec2 vAoMapUv;

#endif
#ifdef USE_BUMPMAP

	varying vec2 vBumpMapUv;

#endif
#ifdef USE_NORMALMAP

	varying vec2 vNormalMapUv;

#endif
#ifdef USE_EMISSIVEMAP

	varying vec2 vEmissiveMapUv;

#endif
#ifdef USE_METALNESSMAP

	varying vec2 vMetalnessMapUv;

#endif
#ifdef USE_ROUGHNESSMAP

	varying vec2 vRoughnessMapUv;

#endif
#ifdef USE_ANISOTROPYMAP

	varying vec2 vAnisotropyMapUv;

#endif
#ifdef USE_CLEARCOATMAP

	varying vec2 vClearcoatMapUv;

#endif
#ifdef USE_CLEARCOAT_NORMALMAP

	varying vec2 vClearcoatNormalMapUv;

#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP

	varying vec2 vClearcoatRoughnessMapUv;

#endif
#ifdef USE_IRIDESCENCEMAP

	varying vec2 vIridescenceMapUv;

#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP

	varying vec2 vIridescenceThicknessMapUv;

#endif
#ifdef USE_SHEEN_COLORMAP

	varying vec2 vSheenColorMapUv;

#endif
#ifdef USE_SHEEN_ROUGHNESSMAP

	varying vec2 vSheenRoughnessMapUv;

#endif
#ifdef USE_SPECULARMAP

	varying vec2 vSpecularMapUv;

#endif
#ifdef USE_SPECULAR_COLORMAP

	varying vec2 vSpecularColorMapUv;

#endif
#ifdef USE_SPECULAR_INTENSITYMAP

	varying vec2 vSpecularIntensityMapUv;

#endif
#ifdef USE_TRANSMISSIONMAP

	uniform mat3 transmissionMapTransform;
	varying vec2 vTransmissionMapUv;

#endif
#ifdef USE_THICKNESSMAP

	uniform mat3 thicknessMapTransform;
	varying vec2 vThicknessMapUv;

#endif
`
), jye = (
  /* glsl */
  `
#if defined( USE_UV ) || defined( USE_ANISOTROPY )

	varying vec2 vUv;

#endif
#ifdef USE_MAP

	uniform mat3 mapTransform;
	varying vec2 vMapUv;

#endif
#ifdef USE_ALPHAMAP

	uniform mat3 alphaMapTransform;
	varying vec2 vAlphaMapUv;

#endif
#ifdef USE_LIGHTMAP

	uniform mat3 lightMapTransform;
	varying vec2 vLightMapUv;

#endif
#ifdef USE_AOMAP

	uniform mat3 aoMapTransform;
	varying vec2 vAoMapUv;

#endif
#ifdef USE_BUMPMAP

	uniform mat3 bumpMapTransform;
	varying vec2 vBumpMapUv;

#endif
#ifdef USE_NORMALMAP

	uniform mat3 normalMapTransform;
	varying vec2 vNormalMapUv;

#endif
#ifdef USE_DISPLACEMENTMAP

	uniform mat3 displacementMapTransform;
	varying vec2 vDisplacementMapUv;

#endif
#ifdef USE_EMISSIVEMAP

	uniform mat3 emissiveMapTransform;
	varying vec2 vEmissiveMapUv;

#endif
#ifdef USE_METALNESSMAP

	uniform mat3 metalnessMapTransform;
	varying vec2 vMetalnessMapUv;

#endif
#ifdef USE_ROUGHNESSMAP

	uniform mat3 roughnessMapTransform;
	varying vec2 vRoughnessMapUv;

#endif
#ifdef USE_ANISOTROPYMAP

	uniform mat3 anisotropyMapTransform;
	varying vec2 vAnisotropyMapUv;

#endif
#ifdef USE_CLEARCOATMAP

	uniform mat3 clearcoatMapTransform;
	varying vec2 vClearcoatMapUv;

#endif
#ifdef USE_CLEARCOAT_NORMALMAP

	uniform mat3 clearcoatNormalMapTransform;
	varying vec2 vClearcoatNormalMapUv;

#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP

	uniform mat3 clearcoatRoughnessMapTransform;
	varying vec2 vClearcoatRoughnessMapUv;

#endif
#ifdef USE_SHEEN_COLORMAP

	uniform mat3 sheenColorMapTransform;
	varying vec2 vSheenColorMapUv;

#endif
#ifdef USE_SHEEN_ROUGHNESSMAP

	uniform mat3 sheenRoughnessMapTransform;
	varying vec2 vSheenRoughnessMapUv;

#endif
#ifdef USE_IRIDESCENCEMAP

	uniform mat3 iridescenceMapTransform;
	varying vec2 vIridescenceMapUv;

#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP

	uniform mat3 iridescenceThicknessMapTransform;
	varying vec2 vIridescenceThicknessMapUv;

#endif
#ifdef USE_SPECULARMAP

	uniform mat3 specularMapTransform;
	varying vec2 vSpecularMapUv;

#endif
#ifdef USE_SPECULAR_COLORMAP

	uniform mat3 specularColorMapTransform;
	varying vec2 vSpecularColorMapUv;

#endif
#ifdef USE_SPECULAR_INTENSITYMAP

	uniform mat3 specularIntensityMapTransform;
	varying vec2 vSpecularIntensityMapUv;

#endif
#ifdef USE_TRANSMISSIONMAP

	uniform mat3 transmissionMapTransform;
	varying vec2 vTransmissionMapUv;

#endif
#ifdef USE_THICKNESSMAP

	uniform mat3 thicknessMapTransform;
	varying vec2 vThicknessMapUv;

#endif
`
), qye = (
  /* glsl */
  `
#if defined( USE_UV ) || defined( USE_ANISOTROPY )

	vUv = vec3( uv, 1 ).xy;

#endif
#ifdef USE_MAP

	vMapUv = ( mapTransform * vec3( MAP_UV, 1 ) ).xy;

#endif
#ifdef USE_ALPHAMAP

	vAlphaMapUv = ( alphaMapTransform * vec3( ALPHAMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_LIGHTMAP

	vLightMapUv = ( lightMapTransform * vec3( LIGHTMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_AOMAP

	vAoMapUv = ( aoMapTransform * vec3( AOMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_BUMPMAP

	vBumpMapUv = ( bumpMapTransform * vec3( BUMPMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_NORMALMAP

	vNormalMapUv = ( normalMapTransform * vec3( NORMALMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_DISPLACEMENTMAP

	vDisplacementMapUv = ( displacementMapTransform * vec3( DISPLACEMENTMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_EMISSIVEMAP

	vEmissiveMapUv = ( emissiveMapTransform * vec3( EMISSIVEMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_METALNESSMAP

	vMetalnessMapUv = ( metalnessMapTransform * vec3( METALNESSMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_ROUGHNESSMAP

	vRoughnessMapUv = ( roughnessMapTransform * vec3( ROUGHNESSMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_ANISOTROPYMAP

	vAnisotropyMapUv = ( anisotropyMapTransform * vec3( ANISOTROPYMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_CLEARCOATMAP

	vClearcoatMapUv = ( clearcoatMapTransform * vec3( CLEARCOATMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_CLEARCOAT_NORMALMAP

	vClearcoatNormalMapUv = ( clearcoatNormalMapTransform * vec3( CLEARCOAT_NORMALMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP

	vClearcoatRoughnessMapUv = ( clearcoatRoughnessMapTransform * vec3( CLEARCOAT_ROUGHNESSMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_IRIDESCENCEMAP

	vIridescenceMapUv = ( iridescenceMapTransform * vec3( IRIDESCENCEMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP

	vIridescenceThicknessMapUv = ( iridescenceThicknessMapTransform * vec3( IRIDESCENCE_THICKNESSMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_SHEEN_COLORMAP

	vSheenColorMapUv = ( sheenColorMapTransform * vec3( SHEEN_COLORMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_SHEEN_ROUGHNESSMAP

	vSheenRoughnessMapUv = ( sheenRoughnessMapTransform * vec3( SHEEN_ROUGHNESSMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_SPECULARMAP

	vSpecularMapUv = ( specularMapTransform * vec3( SPECULARMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_SPECULAR_COLORMAP

	vSpecularColorMapUv = ( specularColorMapTransform * vec3( SPECULAR_COLORMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_SPECULAR_INTENSITYMAP

	vSpecularIntensityMapUv = ( specularIntensityMapTransform * vec3( SPECULAR_INTENSITYMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_TRANSMISSIONMAP

	vTransmissionMapUv = ( transmissionMapTransform * vec3( TRANSMISSIONMAP_UV, 1 ) ).xy;

#endif
#ifdef USE_THICKNESSMAP

	vThicknessMapUv = ( thicknessMapTransform * vec3( THICKNESSMAP_UV, 1 ) ).xy;

#endif
`
), Yye = (
  /* glsl */
  `
#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0

	vec4 worldPosition = vec4( transformed, 1.0 );

	#ifdef USE_BATCHING

		worldPosition = batchingMatrix * worldPosition;

	#endif

	#ifdef USE_INSTANCING

		worldPosition = instanceMatrix * worldPosition;

	#endif

	worldPosition = modelMatrix * worldPosition;

#endif
`
), Zye = (
  /* glsl */
  `
varying vec2 vUv;
uniform mat3 uvTransform;

void main() {

	vUv = ( uvTransform * vec3( uv, 1 ) ).xy;

	gl_Position = vec4( position.xy, 1.0, 1.0 );

}
`
), Kye = (
  /* glsl */
  `
uniform sampler2D t2D;
uniform float backgroundIntensity;

varying vec2 vUv;

void main() {

	vec4 texColor = texture2D( t2D, vUv );

	#ifdef DECODE_VIDEO_TEXTURE

		// use inline sRGB decode until browsers properly support SRGB8_APLHA8 with video textures

		texColor = vec4( mix( pow( texColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), texColor.rgb * 0.0773993808, vec3( lessThanEqual( texColor.rgb, vec3( 0.04045 ) ) ) ), texColor.w );

	#endif

	texColor.rgb *= backgroundIntensity;

	gl_FragColor = texColor;

	#include <tonemapping_fragment>
	#include <colorspace_fragment>

}
`
), Jye = (
  /* glsl */
  `
varying vec3 vWorldDirection;

#include <common>

void main() {

	vWorldDirection = transformDirection( position, modelMatrix );

	#include <begin_vertex>
	#include <project_vertex>

	gl_Position.z = gl_Position.w; // set z to camera.far

}
`
), Qye = (
  /* glsl */
  `

#ifdef ENVMAP_TYPE_CUBE

	uniform samplerCube envMap;

#elif defined( ENVMAP_TYPE_CUBE_UV )

	uniform sampler2D envMap;

#endif

uniform float flipEnvMap;
uniform float backgroundBlurriness;
uniform float backgroundIntensity;
uniform mat3 backgroundRotation;

varying vec3 vWorldDirection;

#include <cube_uv_reflection_fragment>

void main() {

	#ifdef ENVMAP_TYPE_CUBE

		vec4 texColor = textureCube( envMap, backgroundRotation * vec3( flipEnvMap * vWorldDirection.x, vWorldDirection.yz ) );

	#elif defined( ENVMAP_TYPE_CUBE_UV )

		vec4 texColor = textureCubeUV( envMap, backgroundRotation * vWorldDirection, backgroundBlurriness );

	#else

		vec4 texColor = vec4( 0.0, 0.0, 0.0, 1.0 );

	#endif

	texColor.rgb *= backgroundIntensity;

	gl_FragColor = texColor;

	#include <tonemapping_fragment>
	#include <colorspace_fragment>

}
`
), $ye = (
  /* glsl */
  `
varying vec3 vWorldDirection;

#include <common>

void main() {

	vWorldDirection = transformDirection( position, modelMatrix );

	#include <begin_vertex>
	#include <project_vertex>

	gl_Position.z = gl_Position.w; // set z to camera.far

}
`
), e9e = (
  /* glsl */
  `
uniform samplerCube tCube;
uniform float tFlip;
uniform float opacity;

varying vec3 vWorldDirection;

void main() {

	vec4 texColor = textureCube( tCube, vec3( tFlip * vWorldDirection.x, vWorldDirection.yz ) );

	gl_FragColor = texColor;
	gl_FragColor.a *= opacity;

	#include <tonemapping_fragment>
	#include <colorspace_fragment>

}
`
), t9e = (
  /* glsl */
  `
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

// This is used for computing an equivalent of gl_FragCoord.z that is as high precision as possible.
// Some platforms compute gl_FragCoord at a lower precision which makes the manually computed value better for
// depth-based postprocessing effects. Reproduced on iPad with A10 processor / iPadOS 13.3.1.
varying vec2 vHighPrecisionZW;

void main() {

	#include <uv_vertex>

	#include <batching_vertex>
	#include <skinbase_vertex>

	#include <morphinstance_vertex>

	#ifdef USE_DISPLACEMENTMAP

		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinnormal_vertex>

	#endif

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vHighPrecisionZW = gl_Position.zw;

}
`
), i9e = (
  /* glsl */
  `
#if DEPTH_PACKING == 3200

	uniform float opacity;

#endif

#include <common>
#include <packing>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

varying vec2 vHighPrecisionZW;

void main() {

	vec4 diffuseColor = vec4( 1.0 );
	#include <clipping_planes_fragment>

	#if DEPTH_PACKING == 3200

		diffuseColor.a = opacity;

	#endif

	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>

	#include <logdepthbuf_fragment>

	// Higher precision equivalent of gl_FragCoord.z. This assumes depthRange has been left to its default values.
	float fragCoordZ = 0.5 * vHighPrecisionZW[0] / vHighPrecisionZW[1] + 0.5;

	#if DEPTH_PACKING == 3200

		gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );

	#elif DEPTH_PACKING == 3201

		gl_FragColor = packDepthToRGBA( fragCoordZ );

	#endif

}
`
), n9e = (
  /* glsl */
  `
#define DISTANCE

varying vec3 vWorldPosition;

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>

	#include <batching_vertex>
	#include <skinbase_vertex>

	#include <morphinstance_vertex>

	#ifdef USE_DISPLACEMENTMAP

		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinnormal_vertex>

	#endif

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <worldpos_vertex>
	#include <clipping_planes_vertex>

	vWorldPosition = worldPosition.xyz;

}
`
), s9e = (
  /* glsl */
  `
#define DISTANCE

uniform vec3 referencePosition;
uniform float nearDistance;
uniform float farDistance;
varying vec3 vWorldPosition;

#include <common>
#include <packing>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <clipping_planes_pars_fragment>

void main () {

	vec4 diffuseColor = vec4( 1.0 );
	#include <clipping_planes_fragment>

	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>

	float dist = length( vWorldPosition - referencePosition );
	dist = ( dist - nearDistance ) / ( farDistance - nearDistance );
	dist = saturate( dist ); // clamp to [ 0, 1 ]

	gl_FragColor = packDepthToRGBA( dist );

}
`
), r9e = (
  /* glsl */
  `
varying vec3 vWorldDirection;

#include <common>

void main() {

	vWorldDirection = transformDirection( position, modelMatrix );

	#include <begin_vertex>
	#include <project_vertex>

}
`
), a9e = (
  /* glsl */
  `
uniform sampler2D tEquirect;

varying vec3 vWorldDirection;

#include <common>

void main() {

	vec3 direction = normalize( vWorldDirection );

	vec2 sampleUV = equirectUv( direction );

	gl_FragColor = texture2D( tEquirect, sampleUV );

	#include <tonemapping_fragment>
	#include <colorspace_fragment>

}
`
), o9e = (
  /* glsl */
  `
uniform float scale;
attribute float lineDistance;

varying float vLineDistance;

#include <common>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	vLineDistance = scale * lineDistance;

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>

}
`
), l9e = (
  /* glsl */
  `
uniform vec3 diffuse;
uniform float opacity;

uniform float dashSize;
uniform float totalSize;

varying float vLineDistance;

#include <common>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	if ( mod( vLineDistance, totalSize ) > dashSize ) {

		discard;

	}

	vec3 outgoingLight = vec3( 0.0 );

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>

	outgoingLight = diffuseColor.rgb; // simple shader

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>

}
`
), c9e = (
  /* glsl */
  `
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#if defined ( USE_ENVMAP ) || defined ( USE_SKINNING )

		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinbase_vertex>
		#include <skinnormal_vertex>
		#include <defaultnormal_vertex>

	#endif

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <fog_vertex>

}
`
), h9e = (
  /* glsl */
  `
uniform vec3 diffuse;
uniform float opacity;

#ifndef FLAT_SHADED

	varying vec3 vNormal;

#endif

#include <common>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>

	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );

	// accumulation (baked indirect lighting only)
	#ifdef USE_LIGHTMAP

		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		reflectedLight.indirectDiffuse += lightMapTexel.rgb * lightMapIntensity * RECIPROCAL_PI;

	#else

		reflectedLight.indirectDiffuse += vec3( 1.0 );

	#endif

	// modulation
	#include <aomap_fragment>

	reflectedLight.indirectDiffuse *= diffuseColor.rgb;

	vec3 outgoingLight = reflectedLight.indirectDiffuse;

	#include <envmap_fragment>

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), u9e = (
  /* glsl */
  `
#define LAMBERT

varying vec3 vViewPosition;

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vViewPosition = - mvPosition.xyz;

	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>

}
`
), d9e = (
  /* glsl */
  `
#define LAMBERT

uniform vec3 diffuse;
uniform vec3 emissive;
uniform float opacity;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_lambert_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>

	// accumulation
	#include <lights_lambert_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>

	// modulation
	#include <aomap_fragment>

	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;

	#include <envmap_fragment>
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), f9e = (
  /* glsl */
  `
#define MATCAP

varying vec3 vViewPosition;

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <color_pars_vertex>
#include <displacementmap_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>

#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>

	vViewPosition = - mvPosition.xyz;

}
`
), p9e = (
  /* glsl */
  `
#define MATCAP

uniform vec3 diffuse;
uniform float opacity;
uniform sampler2D matcap;

varying vec3 vViewPosition;

#include <common>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <normal_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>

	vec3 viewDir = normalize( vViewPosition );
	vec3 x = normalize( vec3( viewDir.z, 0.0, - viewDir.x ) );
	vec3 y = cross( viewDir, x );
	vec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5; // 0.495 to remove artifacts caused by undersized matcap disks

	#ifdef USE_MATCAP

		vec4 matcapColor = texture2D( matcap, uv );

	#else

		vec4 matcapColor = vec4( vec3( mix( 0.2, 0.8, uv.y ) ), 1.0 ); // default if matcap is missing

	#endif

	vec3 outgoingLight = diffuseColor.rgb * matcapColor.rgb;

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), m9e = (
  /* glsl */
  `
#define NORMAL

#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )

	varying vec3 vViewPosition;

#endif

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )

	vViewPosition = - mvPosition.xyz;

#endif

}
`
), g9e = (
  /* glsl */
  `
#define NORMAL

uniform float opacity;

#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )

	varying vec3 vViewPosition;

#endif

#include <packing>
#include <uv_pars_fragment>
#include <normal_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( 0.0, 0.0, 0.0, opacity );

	#include <clipping_planes_fragment>
	#include <logdepthbuf_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>

	gl_FragColor = vec4( packNormalToRGB( normal ), diffuseColor.a );

	#ifdef OPAQUE

		gl_FragColor.a = 1.0;

	#endif

}
`
), v9e = (
  /* glsl */
  `
#define PHONG

varying vec3 vViewPosition;

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <envmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vViewPosition = - mvPosition.xyz;

	#include <worldpos_vertex>
	#include <envmap_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>

}
`
), y9e = (
  /* glsl */
  `
#define PHONG

uniform vec3 diffuse;
uniform vec3 emissive;
uniform vec3 specular;
uniform float shininess;
uniform float opacity;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_phong_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <specularmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <specularmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>

	// accumulation
	#include <lights_phong_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>

	// modulation
	#include <aomap_fragment>

	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;

	#include <envmap_fragment>
	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), x9e = (
  /* glsl */
  `
#define STANDARD

varying vec3 vViewPosition;

#ifdef USE_TRANSMISSION

	varying vec3 vWorldPosition;

#endif

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vViewPosition = - mvPosition.xyz;

	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>

#ifdef USE_TRANSMISSION

	vWorldPosition = worldPosition.xyz;

#endif
}
`
), A9e = (
  /* glsl */
  `
#define STANDARD

#ifdef PHYSICAL
	#define IOR
	#define USE_SPECULAR
#endif

uniform vec3 diffuse;
uniform vec3 emissive;
uniform float roughness;
uniform float metalness;
uniform float opacity;

#ifdef IOR
	uniform float ior;
#endif

#ifdef USE_SPECULAR
	uniform float specularIntensity;
	uniform vec3 specularColor;

	#ifdef USE_SPECULAR_COLORMAP
		uniform sampler2D specularColorMap;
	#endif

	#ifdef USE_SPECULAR_INTENSITYMAP
		uniform sampler2D specularIntensityMap;
	#endif
#endif

#ifdef USE_CLEARCOAT
	uniform float clearcoat;
	uniform float clearcoatRoughness;
#endif

#ifdef USE_DISPERSION
	uniform float dispersion;
#endif

#ifdef USE_IRIDESCENCE
	uniform float iridescence;
	uniform float iridescenceIOR;
	uniform float iridescenceThicknessMinimum;
	uniform float iridescenceThicknessMaximum;
#endif

#ifdef USE_SHEEN
	uniform vec3 sheenColor;
	uniform float sheenRoughness;

	#ifdef USE_SHEEN_COLORMAP
		uniform sampler2D sheenColorMap;
	#endif

	#ifdef USE_SHEEN_ROUGHNESSMAP
		uniform sampler2D sheenRoughnessMap;
	#endif
#endif

#ifdef USE_ANISOTROPY
	uniform vec2 anisotropyVector;

	#ifdef USE_ANISOTROPYMAP
		uniform sampler2D anisotropyMap;
	#endif
#endif

varying vec3 vViewPosition;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <iridescence_fragment>
#include <cube_uv_reflection_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_physical_pars_fragment>
#include <fog_pars_fragment>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_physical_pars_fragment>
#include <transmission_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <clearcoat_pars_fragment>
#include <iridescence_pars_fragment>
#include <roughnessmap_pars_fragment>
#include <metalnessmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <roughnessmap_fragment>
	#include <metalnessmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <clearcoat_normal_fragment_begin>
	#include <clearcoat_normal_fragment_maps>
	#include <emissivemap_fragment>

	// accumulation
	#include <lights_physical_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>

	// modulation
	#include <aomap_fragment>

	vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
	vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;

	#include <transmission_fragment>

	vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;

	#ifdef USE_SHEEN

		// Sheen energy compensation approximation calculation can be found at the end of
		// https://drive.google.com/file/d/1T0D1VSyR4AllqIJTQAraEIzjlb5h4FKH/view?usp=sharing
		float sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );

		outgoingLight = outgoingLight * sheenEnergyComp + sheenSpecularDirect + sheenSpecularIndirect;

	#endif

	#ifdef USE_CLEARCOAT

		float dotNVcc = saturate( dot( geometryClearcoatNormal, geometryViewDir ) );

		vec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );

		outgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * material.clearcoat;

	#endif

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), b9e = (
  /* glsl */
  `
#define TOON

varying vec3 vViewPosition;

#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vViewPosition = - mvPosition.xyz;

	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>

}
`
), M9e = (
  /* glsl */
  `
#define TOON

uniform vec3 diffuse;
uniform vec3 emissive;
uniform float opacity;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <gradientmap_pars_fragment>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_toon_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <emissivemap_fragment>

	// accumulation
	#include <lights_toon_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>

	// modulation
	#include <aomap_fragment>

	vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
), S9e = (
  /* glsl */
  `
uniform float size;
uniform float scale;

#include <common>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

#ifdef USE_POINTS_UV

	varying vec2 vUv;
	uniform mat3 uvTransform;

#endif

void main() {

	#ifdef USE_POINTS_UV

		vUv = ( uvTransform * vec3( uv, 1 ) ).xy;

	#endif

	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <project_vertex>

	gl_PointSize = size;

	#ifdef USE_SIZEATTENUATION

		bool isPerspective = isPerspectiveMatrix( projectionMatrix );

		if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );

	#endif

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <worldpos_vertex>
	#include <fog_vertex>

}
`
), w9e = (
  /* glsl */
  `
uniform vec3 diffuse;
uniform float opacity;

#include <common>
#include <color_pars_fragment>
#include <map_particle_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	vec3 outgoingLight = vec3( 0.0 );

	#include <logdepthbuf_fragment>
	#include <map_particle_fragment>
	#include <color_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>

	outgoingLight = diffuseColor.rgb;

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>

}
`
), E9e = (
  /* glsl */
  `
#include <common>
#include <batching_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <shadowmap_pars_vertex>

void main() {

	#include <batching_vertex>

	#include <beginnormal_vertex>
	#include <morphinstance_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>

	#include <worldpos_vertex>
	#include <shadowmap_vertex>
	#include <fog_vertex>

}
`
), T9e = (
  /* glsl */
  `
uniform vec3 color;
uniform float opacity;

#include <common>
#include <packing>
#include <fog_pars_fragment>
#include <bsdfs>
#include <lights_pars_begin>
#include <logdepthbuf_pars_fragment>
#include <shadowmap_pars_fragment>
#include <shadowmask_pars_fragment>

void main() {

	#include <logdepthbuf_fragment>

	gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );

	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>

}
`
), C9e = (
  /* glsl */
  `
uniform float rotation;
uniform vec2 center;

#include <common>
#include <uv_pars_vertex>
#include <fog_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>

	vec4 mvPosition = modelViewMatrix * vec4( 0.0, 0.0, 0.0, 1.0 );

	vec2 scale;
	scale.x = length( vec3( modelMatrix[ 0 ].x, modelMatrix[ 0 ].y, modelMatrix[ 0 ].z ) );
	scale.y = length( vec3( modelMatrix[ 1 ].x, modelMatrix[ 1 ].y, modelMatrix[ 1 ].z ) );

	#ifndef USE_SIZEATTENUATION

		bool isPerspective = isPerspectiveMatrix( projectionMatrix );

		if ( isPerspective ) scale *= - mvPosition.z;

	#endif

	vec2 alignedPosition = ( position.xy - ( center - vec2( 0.5 ) ) ) * scale;

	vec2 rotatedPosition;
	rotatedPosition.x = cos( rotation ) * alignedPosition.x - sin( rotation ) * alignedPosition.y;
	rotatedPosition.y = sin( rotation ) * alignedPosition.x + cos( rotation ) * alignedPosition.y;

	mvPosition.xy += rotatedPosition;

	gl_Position = projectionMatrix * mvPosition;

	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	#include <fog_vertex>

}
`
), _9e = (
  /* glsl */
  `
uniform vec3 diffuse;
uniform float opacity;

#include <common>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <alphahash_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

	vec4 diffuseColor = vec4( diffuse, opacity );
	#include <clipping_planes_fragment>

	vec3 outgoingLight = vec3( 0.0 );

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <alphahash_fragment>

	outgoingLight = diffuseColor.rgb;

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>

}
`
), yi = {
  alphahash_fragment: Kve,
  alphahash_pars_fragment: Jve,
  alphamap_fragment: Qve,
  alphamap_pars_fragment: $ve,
  alphatest_fragment: e3e,
  alphatest_pars_fragment: t3e,
  aomap_fragment: i3e,
  aomap_pars_fragment: n3e,
  batching_pars_vertex: s3e,
  batching_vertex: r3e,
  begin_vertex: a3e,
  beginnormal_vertex: o3e,
  bsdfs: l3e,
  iridescence_fragment: c3e,
  bumpmap_pars_fragment: h3e,
  clipping_planes_fragment: u3e,
  clipping_planes_pars_fragment: d3e,
  clipping_planes_pars_vertex: f3e,
  clipping_planes_vertex: p3e,
  color_fragment: m3e,
  color_pars_fragment: g3e,
  color_pars_vertex: v3e,
  color_vertex: y3e,
  common: x3e,
  cube_uv_reflection_fragment: A3e,
  defaultnormal_vertex: b3e,
  displacementmap_pars_vertex: M3e,
  displacementmap_vertex: S3e,
  emissivemap_fragment: w3e,
  emissivemap_pars_fragment: E3e,
  colorspace_fragment: T3e,
  colorspace_pars_fragment: C3e,
  envmap_fragment: _3e,
  envmap_common_pars_fragment: P3e,
  envmap_pars_fragment: R3e,
  envmap_pars_vertex: D3e,
  envmap_physical_pars_fragment: H3e,
  envmap_vertex: L3e,
  fog_vertex: I3e,
  fog_pars_vertex: O3e,
  fog_fragment: N3e,
  fog_pars_fragment: B3e,
  gradientmap_pars_fragment: F3e,
  lightmap_pars_fragment: U3e,
  lights_lambert_fragment: z3e,
  lights_lambert_pars_fragment: k3e,
  lights_pars_begin: G3e,
  lights_toon_fragment: V3e,
  lights_toon_pars_fragment: W3e,
  lights_phong_fragment: X3e,
  lights_phong_pars_fragment: j3e,
  lights_physical_fragment: q3e,
  lights_physical_pars_fragment: Y3e,
  lights_fragment_begin: Z3e,
  lights_fragment_maps: K3e,
  lights_fragment_end: J3e,
  logdepthbuf_fragment: Q3e,
  logdepthbuf_pars_fragment: $3e,
  logdepthbuf_pars_vertex: eye,
  logdepthbuf_vertex: tye,
  map_fragment: iye,
  map_pars_fragment: nye,
  map_particle_fragment: sye,
  map_particle_pars_fragment: rye,
  metalnessmap_fragment: aye,
  metalnessmap_pars_fragment: oye,
  morphinstance_vertex: lye,
  morphcolor_vertex: cye,
  morphnormal_vertex: hye,
  morphtarget_pars_vertex: uye,
  morphtarget_vertex: dye,
  normal_fragment_begin: fye,
  normal_fragment_maps: pye,
  normal_pars_fragment: mye,
  normal_pars_vertex: gye,
  normal_vertex: vye,
  normalmap_pars_fragment: yye,
  clearcoat_normal_fragment_begin: xye,
  clearcoat_normal_fragment_maps: Aye,
  clearcoat_pars_fragment: bye,
  iridescence_pars_fragment: Mye,
  opaque_fragment: Sye,
  packing: wye,
  premultiplied_alpha_fragment: Eye,
  project_vertex: Tye,
  dithering_fragment: Cye,
  dithering_pars_fragment: _ye,
  roughnessmap_fragment: Pye,
  roughnessmap_pars_fragment: Rye,
  shadowmap_pars_fragment: Dye,
  shadowmap_pars_vertex: Lye,
  shadowmap_vertex: Iye,
  shadowmask_pars_fragment: Oye,
  skinbase_vertex: Nye,
  skinning_pars_vertex: Bye,
  skinning_vertex: Fye,
  skinnormal_vertex: Uye,
  specularmap_fragment: zye,
  specularmap_pars_fragment: kye,
  tonemapping_fragment: Gye,
  tonemapping_pars_fragment: Hye,
  transmission_fragment: Vye,
  transmission_pars_fragment: Wye,
  uv_pars_fragment: Xye,
  uv_pars_vertex: jye,
  uv_vertex: qye,
  worldpos_vertex: Yye,
  background_vert: Zye,
  background_frag: Kye,
  backgroundCube_vert: Jye,
  backgroundCube_frag: Qye,
  cube_vert: $ye,
  cube_frag: e9e,
  depth_vert: t9e,
  depth_frag: i9e,
  distanceRGBA_vert: n9e,
  distanceRGBA_frag: s9e,
  equirect_vert: r9e,
  equirect_frag: a9e,
  linedashed_vert: o9e,
  linedashed_frag: l9e,
  meshbasic_vert: c9e,
  meshbasic_frag: h9e,
  meshlambert_vert: u9e,
  meshlambert_frag: d9e,
  meshmatcap_vert: f9e,
  meshmatcap_frag: p9e,
  meshnormal_vert: m9e,
  meshnormal_frag: g9e,
  meshphong_vert: v9e,
  meshphong_frag: y9e,
  meshphysical_vert: x9e,
  meshphysical_frag: A9e,
  meshtoon_vert: b9e,
  meshtoon_frag: M9e,
  points_vert: S9e,
  points_frag: w9e,
  shadow_vert: E9e,
  shadow_frag: T9e,
  sprite_vert: C9e,
  sprite_frag: _9e
}, Rt = {
  common: {
    diffuse: { value: /* @__PURE__ */ new hn(16777215) },
    opacity: { value: 1 },
    map: { value: null },
    mapTransform: { value: /* @__PURE__ */ new xi() },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new xi() },
    alphaTest: { value: 0 }
  },
  specularmap: {
    specularMap: { value: null },
    specularMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  envmap: {
    envMap: { value: null },
    envMapRotation: { value: /* @__PURE__ */ new xi() },
    flipEnvMap: { value: -1 },
    reflectivity: { value: 1 },
    // basic, lambert, phong
    ior: { value: 1.5 },
    // physical
    refractionRatio: { value: 0.98 }
    // basic, lambert, phong
  },
  aomap: {
    aoMap: { value: null },
    aoMapIntensity: { value: 1 },
    aoMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  lightmap: {
    lightMap: { value: null },
    lightMapIntensity: { value: 1 },
    lightMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  bumpmap: {
    bumpMap: { value: null },
    bumpMapTransform: { value: /* @__PURE__ */ new xi() },
    bumpScale: { value: 1 }
  },
  normalmap: {
    normalMap: { value: null },
    normalMapTransform: { value: /* @__PURE__ */ new xi() },
    normalScale: { value: /* @__PURE__ */ new tn(1, 1) }
  },
  displacementmap: {
    displacementMap: { value: null },
    displacementMapTransform: { value: /* @__PURE__ */ new xi() },
    displacementScale: { value: 1 },
    displacementBias: { value: 0 }
  },
  emissivemap: {
    emissiveMap: { value: null },
    emissiveMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  metalnessmap: {
    metalnessMap: { value: null },
    metalnessMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  roughnessmap: {
    roughnessMap: { value: null },
    roughnessMapTransform: { value: /* @__PURE__ */ new xi() }
  },
  gradientmap: {
    gradientMap: { value: null }
  },
  fog: {
    fogDensity: { value: 25e-5 },
    fogNear: { value: 1 },
    fogFar: { value: 2e3 },
    fogColor: { value: /* @__PURE__ */ new hn(16777215) }
  },
  lights: {
    ambientLightColor: { value: [] },
    lightProbe: { value: [] },
    directionalLights: { value: [], properties: {
      direction: {},
      color: {}
    } },
    directionalLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {}
    } },
    directionalShadowMap: { value: [] },
    directionalShadowMatrix: { value: [] },
    spotLights: { value: [], properties: {
      color: {},
      position: {},
      direction: {},
      distance: {},
      coneCos: {},
      penumbraCos: {},
      decay: {}
    } },
    spotLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {}
    } },
    spotLightMap: { value: [] },
    spotShadowMap: { value: [] },
    spotLightMatrix: { value: [] },
    pointLights: { value: [], properties: {
      color: {},
      position: {},
      decay: {},
      distance: {}
    } },
    pointLightShadows: { value: [], properties: {
      shadowIntensity: 1,
      shadowBias: {},
      shadowNormalBias: {},
      shadowRadius: {},
      shadowMapSize: {},
      shadowCameraNear: {},
      shadowCameraFar: {}
    } },
    pointShadowMap: { value: [] },
    pointShadowMatrix: { value: [] },
    hemisphereLights: { value: [], properties: {
      direction: {},
      skyColor: {},
      groundColor: {}
    } },
    // TODO (abelnation): RectAreaLight BRDF data needs to be moved from example to main src
    rectAreaLights: { value: [], properties: {
      color: {},
      position: {},
      width: {},
      height: {}
    } },
    ltc_1: { value: null },
    ltc_2: { value: null }
  },
  points: {
    diffuse: { value: /* @__PURE__ */ new hn(16777215) },
    opacity: { value: 1 },
    size: { value: 1 },
    scale: { value: 1 },
    map: { value: null },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new xi() },
    alphaTest: { value: 0 },
    uvTransform: { value: /* @__PURE__ */ new xi() }
  },
  sprite: {
    diffuse: { value: /* @__PURE__ */ new hn(16777215) },
    opacity: { value: 1 },
    center: { value: /* @__PURE__ */ new tn(0.5, 0.5) },
    rotation: { value: 0 },
    map: { value: null },
    mapTransform: { value: /* @__PURE__ */ new xi() },
    alphaMap: { value: null },
    alphaMapTransform: { value: /* @__PURE__ */ new xi() },
    alphaTest: { value: 0 }
  }
}, Fo = {
  basic: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.specularmap,
      Rt.envmap,
      Rt.aomap,
      Rt.lightmap,
      Rt.fog
    ]),
    vertexShader: yi.meshbasic_vert,
    fragmentShader: yi.meshbasic_frag
  },
  lambert: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.specularmap,
      Rt.envmap,
      Rt.aomap,
      Rt.lightmap,
      Rt.emissivemap,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      Rt.fog,
      Rt.lights,
      {
        emissive: { value: /* @__PURE__ */ new hn(0) }
      }
    ]),
    vertexShader: yi.meshlambert_vert,
    fragmentShader: yi.meshlambert_frag
  },
  phong: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.specularmap,
      Rt.envmap,
      Rt.aomap,
      Rt.lightmap,
      Rt.emissivemap,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      Rt.fog,
      Rt.lights,
      {
        emissive: { value: /* @__PURE__ */ new hn(0) },
        specular: { value: /* @__PURE__ */ new hn(1118481) },
        shininess: { value: 30 }
      }
    ]),
    vertexShader: yi.meshphong_vert,
    fragmentShader: yi.meshphong_frag
  },
  standard: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.envmap,
      Rt.aomap,
      Rt.lightmap,
      Rt.emissivemap,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      Rt.roughnessmap,
      Rt.metalnessmap,
      Rt.fog,
      Rt.lights,
      {
        emissive: { value: /* @__PURE__ */ new hn(0) },
        roughness: { value: 1 },
        metalness: { value: 0 },
        envMapIntensity: { value: 1 }
      }
    ]),
    vertexShader: yi.meshphysical_vert,
    fragmentShader: yi.meshphysical_frag
  },
  toon: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.aomap,
      Rt.lightmap,
      Rt.emissivemap,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      Rt.gradientmap,
      Rt.fog,
      Rt.lights,
      {
        emissive: { value: /* @__PURE__ */ new hn(0) }
      }
    ]),
    vertexShader: yi.meshtoon_vert,
    fragmentShader: yi.meshtoon_frag
  },
  matcap: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      Rt.fog,
      {
        matcap: { value: null }
      }
    ]),
    vertexShader: yi.meshmatcap_vert,
    fragmentShader: yi.meshmatcap_frag
  },
  points: {
    uniforms: /* @__PURE__ */ fr([
      Rt.points,
      Rt.fog
    ]),
    vertexShader: yi.points_vert,
    fragmentShader: yi.points_frag
  },
  dashed: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.fog,
      {
        scale: { value: 1 },
        dashSize: { value: 1 },
        totalSize: { value: 2 }
      }
    ]),
    vertexShader: yi.linedashed_vert,
    fragmentShader: yi.linedashed_frag
  },
  depth: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.displacementmap
    ]),
    vertexShader: yi.depth_vert,
    fragmentShader: yi.depth_frag
  },
  normal: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.bumpmap,
      Rt.normalmap,
      Rt.displacementmap,
      {
        opacity: { value: 1 }
      }
    ]),
    vertexShader: yi.meshnormal_vert,
    fragmentShader: yi.meshnormal_frag
  },
  sprite: {
    uniforms: /* @__PURE__ */ fr([
      Rt.sprite,
      Rt.fog
    ]),
    vertexShader: yi.sprite_vert,
    fragmentShader: yi.sprite_frag
  },
  background: {
    uniforms: {
      uvTransform: { value: /* @__PURE__ */ new xi() },
      t2D: { value: null },
      backgroundIntensity: { value: 1 }
    },
    vertexShader: yi.background_vert,
    fragmentShader: yi.background_frag
  },
  backgroundCube: {
    uniforms: {
      envMap: { value: null },
      flipEnvMap: { value: -1 },
      backgroundBlurriness: { value: 0 },
      backgroundIntensity: { value: 1 },
      backgroundRotation: { value: /* @__PURE__ */ new xi() }
    },
    vertexShader: yi.backgroundCube_vert,
    fragmentShader: yi.backgroundCube_frag
  },
  cube: {
    uniforms: {
      tCube: { value: null },
      tFlip: { value: -1 },
      opacity: { value: 1 }
    },
    vertexShader: yi.cube_vert,
    fragmentShader: yi.cube_frag
  },
  equirect: {
    uniforms: {
      tEquirect: { value: null }
    },
    vertexShader: yi.equirect_vert,
    fragmentShader: yi.equirect_frag
  },
  distanceRGBA: {
    uniforms: /* @__PURE__ */ fr([
      Rt.common,
      Rt.displacementmap,
      {
        referencePosition: { value: /* @__PURE__ */ new je() },
        nearDistance: { value: 1 },
        farDistance: { value: 1e3 }
      }
    ]),
    vertexShader: yi.distanceRGBA_vert,
    fragmentShader: yi.distanceRGBA_frag
  },
  shadow: {
    uniforms: /* @__PURE__ */ fr([
      Rt.lights,
      Rt.fog,
      {
        color: { value: /* @__PURE__ */ new hn(0) },
        opacity: { value: 1 }
      }
    ]),
    vertexShader: yi.shadow_vert,
    fragmentShader: yi.shadow_frag
  }
};
Fo.physical = {
  uniforms: /* @__PURE__ */ fr([
    Fo.standard.uniforms,
    {
      clearcoat: { value: 0 },
      clearcoatMap: { value: null },
      clearcoatMapTransform: { value: /* @__PURE__ */ new xi() },
      clearcoatNormalMap: { value: null },
      clearcoatNormalMapTransform: { value: /* @__PURE__ */ new xi() },
      clearcoatNormalScale: { value: /* @__PURE__ */ new tn(1, 1) },
      clearcoatRoughness: { value: 0 },
      clearcoatRoughnessMap: { value: null },
      clearcoatRoughnessMapTransform: { value: /* @__PURE__ */ new xi() },
      dispersion: { value: 0 },
      iridescence: { value: 0 },
      iridescenceMap: { value: null },
      iridescenceMapTransform: { value: /* @__PURE__ */ new xi() },
      iridescenceIOR: { value: 1.3 },
      iridescenceThicknessMinimum: { value: 100 },
      iridescenceThicknessMaximum: { value: 400 },
      iridescenceThicknessMap: { value: null },
      iridescenceThicknessMapTransform: { value: /* @__PURE__ */ new xi() },
      sheen: { value: 0 },
      sheenColor: { value: /* @__PURE__ */ new hn(0) },
      sheenColorMap: { value: null },
      sheenColorMapTransform: { value: /* @__PURE__ */ new xi() },
      sheenRoughness: { value: 1 },
      sheenRoughnessMap: { value: null },
      sheenRoughnessMapTransform: { value: /* @__PURE__ */ new xi() },
      transmission: { value: 0 },
      transmissionMap: { value: null },
      transmissionMapTransform: { value: /* @__PURE__ */ new xi() },
      transmissionSamplerSize: { value: /* @__PURE__ */ new tn() },
      transmissionSamplerMap: { value: null },
      thickness: { value: 0 },
      thicknessMap: { value: null },
      thicknessMapTransform: { value: /* @__PURE__ */ new xi() },
      attenuationDistance: { value: 0 },
      attenuationColor: { value: /* @__PURE__ */ new hn(0) },
      specularColor: { value: /* @__PURE__ */ new hn(1, 1, 1) },
      specularColorMap: { value: null },
      specularColorMapTransform: { value: /* @__PURE__ */ new xi() },
      specularIntensity: { value: 1 },
      specularIntensityMap: { value: null },
      specularIntensityMapTransform: { value: /* @__PURE__ */ new xi() },
      anisotropyVector: { value: /* @__PURE__ */ new tn() },
      anisotropyMap: { value: null },
      anisotropyMapTransform: { value: /* @__PURE__ */ new xi() }
    }
  ]),
  vertexShader: yi.meshphysical_vert,
  fragmentShader: yi.meshphysical_frag
};
const Q3 = { r: 0, b: 0, g: 0 }, Gh = /* @__PURE__ */ new id(), P9e = /* @__PURE__ */ new ps();
function R9e(s, e, t, i, n, r, a) {
  const o = new hn(0);
  let l = r === !0 ? 0 : 1, c, h, u = null, d = 0, f = null;
  function p(x) {
    let y = x.isScene === !0 ? x.background : null;
    return y && y.isTexture && (y = (x.backgroundBlurriness > 0 ? t : e).get(y)), y;
  }
  function v(x) {
    let y = !1;
    const A = p(x);
    A === null ? m(o, l) : A && A.isColor && (m(A, 1), y = !0);
    const b = s.xr.getEnvironmentBlendMode();
    b === "additive" ? i.buffers.color.setClear(0, 0, 0, 1, a) : b === "alpha-blend" && i.buffers.color.setClear(0, 0, 0, 0, a), (s.autoClear || y) && (i.buffers.depth.setTest(!0), i.buffers.depth.setMask(!0), i.buffers.color.setMask(!0), s.clear(s.autoClearColor, s.autoClearDepth, s.autoClearStencil));
  }
  function g(x, y) {
    const A = p(y);
    A && (A.isCubeTexture || A.mapping === d8) ? (h === void 0 && (h = new al(
      new W2(1, 1, 1),
      new Jc({
        name: "BackgroundCubeMaterial",
        uniforms: Up(Fo.backgroundCube.uniforms),
        vertexShader: Fo.backgroundCube.vertexShader,
        fragmentShader: Fo.backgroundCube.fragmentShader,
        side: Fr,
        depthTest: !1,
        depthWrite: !1,
        fog: !1
      })
    ), h.geometry.deleteAttribute("normal"), h.geometry.deleteAttribute("uv"), h.onBeforeRender = function(b, M, w) {
      this.matrixWorld.copyPosition(w.matrixWorld);
    }, Object.defineProperty(h.material, "envMap", {
      get: function() {
        return this.uniforms.envMap.value;
      }
    }), n.update(h)), Gh.copy(y.backgroundRotation), Gh.x *= -1, Gh.y *= -1, Gh.z *= -1, A.isCubeTexture && A.isRenderTargetTexture === !1 && (Gh.y *= -1, Gh.z *= -1), h.material.uniforms.envMap.value = A, h.material.uniforms.flipEnvMap.value = A.isCubeTexture && A.isRenderTargetTexture === !1 ? -1 : 1, h.material.uniforms.backgroundBlurriness.value = y.backgroundBlurriness, h.material.uniforms.backgroundIntensity.value = y.backgroundIntensity, h.material.uniforms.backgroundRotation.value.setFromMatrix4(P9e.makeRotationFromEuler(Gh)), h.material.toneMapped = rn.getTransfer(A.colorSpace) !== Sn, (u !== A || d !== A.version || f !== s.toneMapping) && (h.material.needsUpdate = !0, u = A, d = A.version, f = s.toneMapping), h.layers.enableAll(), x.unshift(h, h.geometry, h.material, 0, 0, null)) : A && A.isTexture && (c === void 0 && (c = new al(
      new p8(2, 2),
      new Jc({
        name: "BackgroundMaterial",
        uniforms: Up(Fo.background.uniforms),
        vertexShader: Fo.background.vertexShader,
        fragmentShader: Fo.background.fragmentShader,
        side: Kc,
        depthTest: !1,
        depthWrite: !1,
        fog: !1
      })
    ), c.geometry.deleteAttribute("normal"), Object.defineProperty(c.material, "map", {
      get: function() {
        return this.uniforms.t2D.value;
      }
    }), n.update(c)), c.material.uniforms.t2D.value = A, c.material.uniforms.backgroundIntensity.value = y.backgroundIntensity, c.material.toneMapped = rn.getTransfer(A.colorSpace) !== Sn, A.matrixAutoUpdate === !0 && A.updateMatrix(), c.material.uniforms.uvTransform.value.copy(A.matrix), (u !== A || d !== A.version || f !== s.toneMapping) && (c.material.needsUpdate = !0, u = A, d = A.version, f = s.toneMapping), c.layers.enableAll(), x.unshift(c, c.geometry, c.material, 0, 0, null));
  }
  function m(x, y) {
    x.getRGB(Q3, dU(s)), i.buffers.color.setClear(Q3.r, Q3.g, Q3.b, y, a);
  }
  return {
    getClearColor: function() {
      return o;
    },
    setClearColor: function(x, y = 1) {
      o.set(x), l = y, m(o, l);
    },
    getClearAlpha: function() {
      return l;
    },
    setClearAlpha: function(x) {
      l = x, m(o, l);
    },
    render: v,
    addToRenderList: g
  };
}
function D9e(s, e) {
  const t = s.getParameter(s.MAX_VERTEX_ATTRIBS), i = {}, n = d(null);
  let r = n, a = !1;
  function o(E, P, L, I, N) {
    let H = !1;
    const U = u(I, L, P);
    r !== U && (r = U, c(r.object)), H = f(E, I, L, N), H && p(E, I, L, N), N !== null && e.update(N, s.ELEMENT_ARRAY_BUFFER), (H || a) && (a = !1, A(E, P, L, I), N !== null && s.bindBuffer(s.ELEMENT_ARRAY_BUFFER, e.get(N).buffer));
  }
  function l() {
    return s.createVertexArray();
  }
  function c(E) {
    return s.bindVertexArray(E);
  }
  function h(E) {
    return s.deleteVertexArray(E);
  }
  function u(E, P, L) {
    const I = L.wireframe === !0;
    let N = i[E.id];
    N === void 0 && (N = {}, i[E.id] = N);
    let H = N[P.id];
    H === void 0 && (H = {}, N[P.id] = H);
    let U = H[I];
    return U === void 0 && (U = d(l()), H[I] = U), U;
  }
  function d(E) {
    const P = [], L = [], I = [];
    for (let N = 0; N < t; N++)
      P[N] = 0, L[N] = 0, I[N] = 0;
    return {
      // for backward compatibility on non-VAO support browser
      geometry: null,
      program: null,
      wireframe: !1,
      newAttributes: P,
      enabledAttributes: L,
      attributeDivisors: I,
      object: E,
      attributes: {},
      index: null
    };
  }
  function f(E, P, L, I) {
    const N = r.attributes, H = P.attributes;
    let U = 0;
    const W = L.getAttributes();
    for (const X in W)
      if (W[X].location >= 0) {
        const te = N[X];
        let re = H[X];
        if (re === void 0 && (X === "instanceMatrix" && E.instanceMatrix && (re = E.instanceMatrix), X === "instanceColor" && E.instanceColor && (re = E.instanceColor)), te === void 0 || te.attribute !== re || re && te.data !== re.data)
          return !0;
        U++;
      }
    return r.attributesNum !== U || r.index !== I;
  }
  function p(E, P, L, I) {
    const N = {}, H = P.attributes;
    let U = 0;
    const W = L.getAttributes();
    for (const X in W)
      if (W[X].location >= 0) {
        let te = H[X];
        te === void 0 && (X === "instanceMatrix" && E.instanceMatrix && (te = E.instanceMatrix), X === "instanceColor" && E.instanceColor && (te = E.instanceColor));
        const re = {};
        re.attribute = te, te && te.data && (re.data = te.data), N[X] = re, U++;
      }
    r.attributes = N, r.attributesNum = U, r.index = I;
  }
  function v() {
    const E = r.newAttributes;
    for (let P = 0, L = E.length; P < L; P++)
      E[P] = 0;
  }
  function g(E) {
    m(E, 0);
  }
  function m(E, P) {
    const L = r.newAttributes, I = r.enabledAttributes, N = r.attributeDivisors;
    L[E] = 1, I[E] === 0 && (s.enableVertexAttribArray(E), I[E] = 1), N[E] !== P && (s.vertexAttribDivisor(E, P), N[E] = P);
  }
  function x() {
    const E = r.newAttributes, P = r.enabledAttributes;
    for (let L = 0, I = P.length; L < I; L++)
      P[L] !== E[L] && (s.disableVertexAttribArray(L), P[L] = 0);
  }
  function y(E, P, L, I, N, H, U) {
    U === !0 ? s.vertexAttribIPointer(E, P, L, N, H) : s.vertexAttribPointer(E, P, L, I, N, H);
  }
  function A(E, P, L, I) {
    v();
    const N = I.attributes, H = L.getAttributes(), U = P.defaultAttributeValues;
    for (const W in H) {
      const X = H[W];
      if (X.location >= 0) {
        let K = N[W];
        if (K === void 0 && (W === "instanceMatrix" && E.instanceMatrix && (K = E.instanceMatrix), W === "instanceColor" && E.instanceColor && (K = E.instanceColor)), K !== void 0) {
          const te = K.normalized, re = K.itemSize, be = e.get(K);
          if (be === void 0)
            continue;
          const Pe = be.buffer, J = be.type, fe = be.bytesPerElement, Me = J === s.INT || J === s.UNSIGNED_INT || K.gpuType === _w;
          if (K.isInterleavedBufferAttribute) {
            const ye = K.data, Ye = ye.stride, Je = K.offset;
            if (ye.isInstancedInterleavedBuffer) {
              for (let rt = 0; rt < X.locationSize; rt++)
                m(X.location + rt, ye.meshPerAttribute);
              E.isInstancedMesh !== !0 && I._maxInstanceCount === void 0 && (I._maxInstanceCount = ye.meshPerAttribute * ye.count);
            } else
              for (let rt = 0; rt < X.locationSize; rt++)
                g(X.location + rt);
            s.bindBuffer(s.ARRAY_BUFFER, Pe);
            for (let rt = 0; rt < X.locationSize; rt++)
              y(
                X.location + rt,
                re / X.locationSize,
                J,
                te,
                Ye * fe,
                (Je + re / X.locationSize * rt) * fe,
                Me
              );
          } else {
            if (K.isInstancedBufferAttribute) {
              for (let ye = 0; ye < X.locationSize; ye++)
                m(X.location + ye, K.meshPerAttribute);
              E.isInstancedMesh !== !0 && I._maxInstanceCount === void 0 && (I._maxInstanceCount = K.meshPerAttribute * K.count);
            } else
              for (let ye = 0; ye < X.locationSize; ye++)
                g(X.location + ye);
            s.bindBuffer(s.ARRAY_BUFFER, Pe);
            for (let ye = 0; ye < X.locationSize; ye++)
              y(
                X.location + ye,
                re / X.locationSize,
                J,
                te,
                re * fe,
                re / X.locationSize * ye * fe,
                Me
              );
          }
        } else if (U !== void 0) {
          const te = U[W];
          if (te !== void 0)
            switch (te.length) {
              case 2:
                s.vertexAttrib2fv(X.location, te);
                break;
              case 3:
                s.vertexAttrib3fv(X.location, te);
                break;
              case 4:
                s.vertexAttrib4fv(X.location, te);
                break;
              default:
                s.vertexAttrib1fv(X.location, te);
            }
        }
      }
    }
    x();
  }
  function b() {
    C();
    for (const E in i) {
      const P = i[E];
      for (const L in P) {
        const I = P[L];
        for (const N in I)
          h(I[N].object), delete I[N];
        delete P[L];
      }
      delete i[E];
    }
  }
  function M(E) {
    if (i[E.id] === void 0)
      return;
    const P = i[E.id];
    for (const L in P) {
      const I = P[L];
      for (const N in I)
        h(I[N].object), delete I[N];
      delete P[L];
    }
    delete i[E.id];
  }
  function w(E) {
    for (const P in i) {
      const L = i[P];
      if (L[E.id] === void 0)
        continue;
      const I = L[E.id];
      for (const N in I)
        h(I[N].object), delete I[N];
      delete L[E.id];
    }
  }
  function C() {
    T(), a = !0, r !== n && (r = n, c(r.object));
  }
  function T() {
    n.geometry = null, n.program = null, n.wireframe = !1;
  }
  return {
    setup: o,
    reset: C,
    resetDefaultState: T,
    dispose: b,
    releaseStatesOfGeometry: M,
    releaseStatesOfProgram: w,
    initAttributes: v,
    enableAttribute: g,
    disableUnusedAttributes: x
  };
}
function L9e(s, e, t) {
  let i;
  function n(c) {
    i = c;
  }
  function r(c, h) {
    s.drawArrays(i, c, h), t.update(h, i, 1);
  }
  function a(c, h, u) {
    u !== 0 && (s.drawArraysInstanced(i, c, h, u), t.update(h, i, u));
  }
  function o(c, h, u) {
    if (u === 0)
      return;
    e.get("WEBGL_multi_draw").multiDrawArraysWEBGL(i, c, 0, h, 0, u);
    let f = 0;
    for (let p = 0; p < u; p++)
      f += h[p];
    t.update(f, i, 1);
  }
  function l(c, h, u, d) {
    if (u === 0)
      return;
    const f = e.get("WEBGL_multi_draw");
    if (f === null)
      for (let p = 0; p < c.length; p++)
        a(c[p], h[p], d[p]);
    else {
      f.multiDrawArraysInstancedWEBGL(i, c, 0, h, 0, d, 0, u);
      let p = 0;
      for (let v = 0; v < u; v++)
        p += h[v];
      for (let v = 0; v < d.length; v++)
        t.update(p, i, d[v]);
    }
  }
  this.setMode = n, this.render = r, this.renderInstances = a, this.renderMultiDraw = o, this.renderMultiDrawInstances = l;
}
function I9e(s, e, t, i) {
  let n;
  function r() {
    if (n !== void 0)
      return n;
    if (e.has("EXT_texture_filter_anisotropic") === !0) {
      const M = e.get("EXT_texture_filter_anisotropic");
      n = s.getParameter(M.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
    } else
      n = 0;
    return n;
  }
  function a(M) {
    return !(M !== no && i.convert(M) !== s.getParameter(s.IMPLEMENTATION_COLOR_READ_FORMAT));
  }
  function o(M) {
    const w = M === G2 && (e.has("EXT_color_buffer_half_float") || e.has("EXT_color_buffer_float"));
    return !(M !== Sl && i.convert(M) !== s.getParameter(s.IMPLEMENTATION_COLOR_READ_TYPE) && // Edge and Chrome Mac < 52 (#9513)
    M !== sl && !w);
  }
  function l(M) {
    if (M === "highp") {
      if (s.getShaderPrecisionFormat(s.VERTEX_SHADER, s.HIGH_FLOAT).precision > 0 && s.getShaderPrecisionFormat(s.FRAGMENT_SHADER, s.HIGH_FLOAT).precision > 0)
        return "highp";
      M = "mediump";
    }
    return M === "mediump" && s.getShaderPrecisionFormat(s.VERTEX_SHADER, s.MEDIUM_FLOAT).precision > 0 && s.getShaderPrecisionFormat(s.FRAGMENT_SHADER, s.MEDIUM_FLOAT).precision > 0 ? "mediump" : "lowp";
  }
  let c = t.precision !== void 0 ? t.precision : "highp";
  const h = l(c);
  h !== c && (console.warn("THREE.WebGLRenderer:", c, "not supported, using", h, "instead."), c = h);
  const u = t.logarithmicDepthBuffer === !0, d = s.getParameter(s.MAX_TEXTURE_IMAGE_UNITS), f = s.getParameter(s.MAX_VERTEX_TEXTURE_IMAGE_UNITS), p = s.getParameter(s.MAX_TEXTURE_SIZE), v = s.getParameter(s.MAX_CUBE_MAP_TEXTURE_SIZE), g = s.getParameter(s.MAX_VERTEX_ATTRIBS), m = s.getParameter(s.MAX_VERTEX_UNIFORM_VECTORS), x = s.getParameter(s.MAX_VARYING_VECTORS), y = s.getParameter(s.MAX_FRAGMENT_UNIFORM_VECTORS), A = f > 0, b = s.getParameter(s.MAX_SAMPLES);
  return {
    isWebGL2: !0,
    // keeping this for backwards compatibility
    getMaxAnisotropy: r,
    getMaxPrecision: l,
    textureFormatReadable: a,
    textureTypeReadable: o,
    precision: c,
    logarithmicDepthBuffer: u,
    maxTextures: d,
    maxVertexTextures: f,
    maxTextureSize: p,
    maxCubemapSize: v,
    maxAttributes: g,
    maxVertexUniforms: m,
    maxVaryings: x,
    maxFragmentUniforms: y,
    vertexTextures: A,
    maxSamples: b
  };
}
function O9e(s) {
  const e = this;
  let t = null, i = 0, n = !1, r = !1;
  const a = new $h(), o = new xi(), l = { value: null, needsUpdate: !1 };
  this.uniform = l, this.numPlanes = 0, this.numIntersection = 0, this.init = function(u, d) {
    const f = u.length !== 0 || d || // enable state of previous frame - the clipping code has to
    // run another frame in order to reset the state:
    i !== 0 || n;
    return n = d, i = u.length, f;
  }, this.beginShadows = function() {
    r = !0, h(null);
  }, this.endShadows = function() {
    r = !1;
  }, this.setGlobalState = function(u, d) {
    t = h(u, d, 0);
  }, this.setState = function(u, d, f) {
    const p = u.clippingPlanes, v = u.clipIntersection, g = u.clipShadows, m = s.get(u);
    if (!n || p === null || p.length === 0 || r && !g)
      r ? h(null) : c();
    else {
      const x = r ? 0 : i, y = x * 4;
      let A = m.clippingState || null;
      l.value = A, A = h(p, d, y, f);
      for (let b = 0; b !== y; ++b)
        A[b] = t[b];
      m.clippingState = A, this.numIntersection = v ? this.numPlanes : 0, this.numPlanes += x;
    }
  };
  function c() {
    l.value !== t && (l.value = t, l.needsUpdate = i > 0), e.numPlanes = i, e.numIntersection = 0;
  }
  function h(u, d, f, p) {
    const v = u !== null ? u.length : 0;
    let g = null;
    if (v !== 0) {
      if (g = l.value, p !== !0 || g === null) {
        const m = f + v * 4, x = d.matrixWorldInverse;
        o.getNormalMatrix(x), (g === null || g.length < m) && (g = new Float32Array(m));
        for (let y = 0, A = f; y !== v; ++y, A += 4)
          a.copy(u[y]).applyMatrix4(x, o), a.normal.toArray(g, A), g[A + 3] = a.constant;
      }
      l.value = g, l.needsUpdate = !0;
    }
    return e.numPlanes = v, e.numIntersection = 0, g;
  }
}
let $d;
class N9e {
  static getDataURL(e) {
    if (/^data:/i.test(e.src) || typeof HTMLCanvasElement > "u")
      return e.src;
    let t;
    if (e instanceof HTMLCanvasElement)
      t = e;
    else {
      $d === void 0 && ($d = qx("canvas")), $d.width = e.width, $d.height = e.height;
      const i = $d.getContext("2d");
      e instanceof ImageData ? i.putImageData(e, 0, 0) : i.drawImage(e, 0, 0, e.width, e.height), t = $d;
    }
    return t.width > 2048 || t.height > 2048 ? (console.warn("THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons", e), t.toDataURL("image/jpeg", 0.6)) : t.toDataURL("image/png");
  }
  static sRGBToLinear(e) {
    if (typeof HTMLImageElement < "u" && e instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && e instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && e instanceof ImageBitmap) {
      const t = qx("canvas");
      t.width = e.width, t.height = e.height;
      const i = t.getContext("2d");
      i.drawImage(e, 0, 0, e.width, e.height);
      const n = i.getImageData(0, 0, e.width, e.height), r = n.data;
      for (let a = 0; a < r.length; a++)
        r[a] = $f(r[a] / 255) * 255;
      return i.putImageData(n, 0, 0), t;
    } else if (e.data) {
      const t = e.data.slice(0);
      for (let i = 0; i < t.length; i++)
        t instanceof Uint8Array || t instanceof Uint8ClampedArray ? t[i] = Math.floor($f(t[i] / 255) * 255) : t[i] = $f(t[i]);
      return {
        data: t,
        width: e.width,
        height: e.height
      };
    } else
      return console.warn("THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied."), e;
  }
}
let B9e = 0;
class pU {
  constructor(e = null) {
    this.isSource = !0, Object.defineProperty(this, "id", { value: B9e++ }), this.uuid = k2(), this.data = e, this.dataReady = !0, this.version = 0;
  }
  set needsUpdate(e) {
    e === !0 && this.version++;
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    if (!t && e.images[this.uuid] !== void 0)
      return e.images[this.uuid];
    const i = {
      uuid: this.uuid,
      url: ""
    }, n = this.data;
    if (n !== null) {
      let r;
      if (Array.isArray(n)) {
        r = [];
        for (let a = 0, o = n.length; a < o; a++)
          n[a].isDataTexture ? r.push(u6(n[a].image)) : r.push(u6(n[a]));
      } else
        r = u6(n);
      i.url = r;
    }
    return t || (e.images[this.uuid] = i), i;
  }
}
function u6(s) {
  return typeof HTMLImageElement < "u" && s instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && s instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && s instanceof ImageBitmap ? N9e.getDataURL(s) : s.data ? {
    data: Array.from(s.data),
    width: s.width,
    height: s.height,
    type: s.data.constructor.name
  } : (console.warn("THREE.Texture: Unable to serialize Texture."), {});
}
let F9e = 0;
class Ur extends nm {
  constructor(e = Ur.DEFAULT_IMAGE, t = Ur.DEFAULT_MAPPING, i = vu, n = vu, r = Qa, a = yu, o = no, l = Sl, c = Ur.DEFAULT_ANISOTROPY, h = Mc) {
    super(), this.isTexture = !0, Object.defineProperty(this, "id", { value: F9e++ }), this.uuid = k2(), this.name = "", this.source = new pU(e), this.mipmaps = [], this.mapping = t, this.channel = 0, this.wrapS = i, this.wrapT = n, this.magFilter = r, this.minFilter = a, this.anisotropy = c, this.format = o, this.internalFormat = null, this.type = l, this.offset = new tn(0, 0), this.repeat = new tn(1, 1), this.center = new tn(0, 0), this.rotation = 0, this.matrixAutoUpdate = !0, this.matrix = new xi(), this.generateMipmaps = !0, this.premultiplyAlpha = !1, this.flipY = !0, this.unpackAlignment = 4, this.colorSpace = h, this.userData = {}, this.version = 0, this.onUpdate = null, this.isRenderTargetTexture = !1, this.pmremVersion = 0;
  }
  get image() {
    return this.source.data;
  }
  set image(e = null) {
    this.source.data = e;
  }
  updateMatrix() {
    this.matrix.setUvTransform(this.offset.x, this.offset.y, this.repeat.x, this.repeat.y, this.rotation, this.center.x, this.center.y);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    return this.name = e.name, this.source = e.source, this.mipmaps = e.mipmaps.slice(0), this.mapping = e.mapping, this.channel = e.channel, this.wrapS = e.wrapS, this.wrapT = e.wrapT, this.magFilter = e.magFilter, this.minFilter = e.minFilter, this.anisotropy = e.anisotropy, this.format = e.format, this.internalFormat = e.internalFormat, this.type = e.type, this.offset.copy(e.offset), this.repeat.copy(e.repeat), this.center.copy(e.center), this.rotation = e.rotation, this.matrixAutoUpdate = e.matrixAutoUpdate, this.matrix.copy(e.matrix), this.generateMipmaps = e.generateMipmaps, this.premultiplyAlpha = e.premultiplyAlpha, this.flipY = e.flipY, this.unpackAlignment = e.unpackAlignment, this.colorSpace = e.colorSpace, this.userData = JSON.parse(JSON.stringify(e.userData)), this.needsUpdate = !0, this;
  }
  toJSON(e) {
    const t = e === void 0 || typeof e == "string";
    if (!t && e.textures[this.uuid] !== void 0)
      return e.textures[this.uuid];
    const i = {
      metadata: {
        version: 4.6,
        type: "Texture",
        generator: "Texture.toJSON"
      },
      uuid: this.uuid,
      name: this.name,
      image: this.source.toJSON(e).uuid,
      mapping: this.mapping,
      channel: this.channel,
      repeat: [this.repeat.x, this.repeat.y],
      offset: [this.offset.x, this.offset.y],
      center: [this.center.x, this.center.y],
      rotation: this.rotation,
      wrap: [this.wrapS, this.wrapT],
      format: this.format,
      internalFormat: this.internalFormat,
      type: this.type,
      colorSpace: this.colorSpace,
      minFilter: this.minFilter,
      magFilter: this.magFilter,
      anisotropy: this.anisotropy,
      flipY: this.flipY,
      generateMipmaps: this.generateMipmaps,
      premultiplyAlpha: this.premultiplyAlpha,
      unpackAlignment: this.unpackAlignment
    };
    return Object.keys(this.userData).length > 0 && (i.userData = this.userData), t || (e.textures[this.uuid] = i), i;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
  transformUv(e) {
    if (this.mapping !== jF)
      return e;
    if (e.applyMatrix3(this.matrix), e.x < 0 || e.x > 1)
      switch (this.wrapS) {
        case E7:
          e.x = e.x - Math.floor(e.x);
          break;
        case vu:
          e.x = e.x < 0 ? 0 : 1;
          break;
        case T7:
          Math.abs(Math.floor(e.x) % 2) === 1 ? e.x = Math.ceil(e.x) - e.x : e.x = e.x - Math.floor(e.x);
          break;
      }
    if (e.y < 0 || e.y > 1)
      switch (this.wrapT) {
        case E7:
          e.y = e.y - Math.floor(e.y);
          break;
        case vu:
          e.y = e.y < 0 ? 0 : 1;
          break;
        case T7:
          Math.abs(Math.floor(e.y) % 2) === 1 ? e.y = Math.ceil(e.y) - e.y : e.y = e.y - Math.floor(e.y);
          break;
      }
    return this.flipY && (e.y = 1 - e.y), e;
  }
  set needsUpdate(e) {
    e === !0 && (this.version++, this.source.needsUpdate = !0);
  }
  set needsPMREMUpdate(e) {
    e === !0 && this.pmremVersion++;
  }
}
Ur.DEFAULT_IMAGE = null;
Ur.DEFAULT_MAPPING = jF;
Ur.DEFAULT_ANISOTROPY = 1;
class U9e extends nm {
  constructor(e = 1, t = 1, i = {}) {
    super(), this.isRenderTarget = !0, this.width = e, this.height = t, this.depth = 1, this.scissor = new Cs(0, 0, e, t), this.scissorTest = !1, this.viewport = new Cs(0, 0, e, t);
    const n = { width: e, height: t, depth: 1 };
    i = Object.assign({
      generateMipmaps: !1,
      internalFormat: null,
      minFilter: Qa,
      depthBuffer: !0,
      stencilBuffer: !1,
      resolveDepthBuffer: !0,
      resolveStencilBuffer: !0,
      depthTexture: null,
      samples: 0,
      count: 1
    }, i);
    const r = new Ur(n, i.mapping, i.wrapS, i.wrapT, i.magFilter, i.minFilter, i.format, i.type, i.anisotropy, i.colorSpace);
    r.flipY = !1, r.generateMipmaps = i.generateMipmaps, r.internalFormat = i.internalFormat, this.textures = [];
    const a = i.count;
    for (let o = 0; o < a; o++)
      this.textures[o] = r.clone(), this.textures[o].isRenderTargetTexture = !0;
    this.depthBuffer = i.depthBuffer, this.stencilBuffer = i.stencilBuffer, this.resolveDepthBuffer = i.resolveDepthBuffer, this.resolveStencilBuffer = i.resolveStencilBuffer, this.depthTexture = i.depthTexture, this.samples = i.samples;
  }
  get texture() {
    return this.textures[0];
  }
  set texture(e) {
    this.textures[0] = e;
  }
  setSize(e, t, i = 1) {
    if (this.width !== e || this.height !== t || this.depth !== i) {
      this.width = e, this.height = t, this.depth = i;
      for (let n = 0, r = this.textures.length; n < r; n++)
        this.textures[n].image.width = e, this.textures[n].image.height = t, this.textures[n].image.depth = i;
      this.dispose();
    }
    this.viewport.set(0, 0, e, t), this.scissor.set(0, 0, e, t);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(e) {
    this.width = e.width, this.height = e.height, this.depth = e.depth, this.scissor.copy(e.scissor), this.scissorTest = e.scissorTest, this.viewport.copy(e.viewport), this.textures.length = 0;
    for (let i = 0, n = e.textures.length; i < n; i++)
      this.textures[i] = e.textures[i].clone(), this.textures[i].isRenderTargetTexture = !0;
    const t = Object.assign({}, e.texture.image);
    return this.texture.source = new pU(t), this.depthBuffer = e.depthBuffer, this.stencilBuffer = e.stencilBuffer, this.resolveDepthBuffer = e.resolveDepthBuffer, this.resolveStencilBuffer = e.resolveStencilBuffer, e.depthTexture !== null && (this.depthTexture = e.depthTexture.clone()), this.samples = e.samples, this;
  }
  dispose() {
    this.dispatchEvent({ type: "dispose" });
  }
}
class ku extends U9e {
  constructor(e = 1, t = 1, i = {}) {
    super(e, t, i), this.isWebGLRenderTarget = !0;
  }
}
class mU extends Ta {
  constructor() {
    super(), this.isCamera = !0, this.type = "Camera", this.matrixWorldInverse = new ps(), this.projectionMatrix = new ps(), this.projectionMatrixInverse = new ps(), this.coordinateSystem = rl;
  }
  copy(e, t) {
    return super.copy(e, t), this.matrixWorldInverse.copy(e.matrixWorldInverse), this.projectionMatrix.copy(e.projectionMatrix), this.projectionMatrixInverse.copy(e.projectionMatrixInverse), this.coordinateSystem = e.coordinateSystem, this;
  }
  getWorldDirection(e) {
    return super.getWorldDirection(e).negate();
  }
  updateMatrixWorld(e) {
    super.updateMatrixWorld(e), this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  updateWorldMatrix(e, t) {
    super.updateWorldMatrix(e, t), this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  clone() {
    return new this.constructor().copy(this);
  }
}
const ec = /* @__PURE__ */ new je(), NR = /* @__PURE__ */ new tn(), BR = /* @__PURE__ */ new tn();
class Ja extends mU {
  constructor(e = 50, t = 1, i = 0.1, n = 2e3) {
    super(), this.isPerspectiveCamera = !0, this.type = "PerspectiveCamera", this.fov = e, this.zoom = 1, this.near = i, this.far = n, this.focus = 10, this.aspect = t, this.view = null, this.filmGauge = 35, this.filmOffset = 0, this.updateProjectionMatrix();
  }
  copy(e, t) {
    return super.copy(e, t), this.fov = e.fov, this.zoom = e.zoom, this.near = e.near, this.far = e.far, this.focus = e.focus, this.aspect = e.aspect, this.view = e.view === null ? null : Object.assign({}, e.view), this.filmGauge = e.filmGauge, this.filmOffset = e.filmOffset, this;
  }
  /**
   * Sets the FOV by focal length in respect to the current .filmGauge.
   *
   * The default film gauge is 35, so that the focal length can be specified for
   * a 35mm (full frame) camera.
   *
   * Values for focal length and film gauge must have the same unit.
   */
  setFocalLength(e) {
    const t = 0.5 * this.getFilmHeight() / e;
    this.fov = u7 * 2 * Math.atan(t), this.updateProjectionMatrix();
  }
  /**
   * Calculates the focal length from the current .fov and .filmGauge.
   */
  getFocalLength() {
    const e = Math.tan(zA * 0.5 * this.fov);
    return 0.5 * this.getFilmHeight() / e;
  }
  getEffectiveFOV() {
    return u7 * 2 * Math.atan(
      Math.tan(zA * 0.5 * this.fov) / this.zoom
    );
  }
  getFilmWidth() {
    return this.filmGauge * Math.min(this.aspect, 1);
  }
  getFilmHeight() {
    return this.filmGauge / Math.max(this.aspect, 1);
  }
  /**
   * Computes the 2D bounds of the camera's viewable rectangle at a given distance along the viewing direction.
   * Sets minTarget and maxTarget to the coordinates of the lower-left and upper-right corners of the view rectangle.
   */
  getViewBounds(e, t, i) {
    ec.set(-1, -1, 0.5).applyMatrix4(this.projectionMatrixInverse), t.set(ec.x, ec.y).multiplyScalar(-e / ec.z), ec.set(1, 1, 0.5).applyMatrix4(this.projectionMatrixInverse), i.set(ec.x, ec.y).multiplyScalar(-e / ec.z);
  }
  /**
   * Computes the width and height of the camera's viewable rectangle at a given distance along the viewing direction.
   * Copies the result into the target Vector2, where x is width and y is height.
   */
  getViewSize(e, t) {
    return this.getViewBounds(e, NR, BR), t.subVectors(BR, NR);
  }
  /**
   * Sets an offset in a larger frustum. This is useful for multi-window or
   * multi-monitor/multi-machine setups.
   *
   * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
   * the monitors are in grid like this
   *
   *   +---+---+---+
   *   | A | B | C |
   *   +---+---+---+
   *   | D | E | F |
   *   +---+---+---+
   *
   * then for each monitor you would call it like this
   *
   *   const w = 1920;
   *   const h = 1080;
   *   const fullWidth = w * 3;
   *   const fullHeight = h * 2;
   *
   *   --A--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
   *   --B--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
   *   --C--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
   *   --D--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
   *   --E--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
   *   --F--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
   *
   *   Note there is no reason monitors have to be the same size or in a grid.
   */
  setViewOffset(e, t, i, n, r, a) {
    this.aspect = e / t, this.view === null && (this.view = {
      enabled: !0,
      fullWidth: 1,
      fullHeight: 1,
      offsetX: 0,
      offsetY: 0,
      width: 1,
      height: 1
    }), this.view.enabled = !0, this.view.fullWidth = e, this.view.fullHeight = t, this.view.offsetX = i, this.view.offsetY = n, this.view.width = r, this.view.height = a, this.updateProjectionMatrix();
  }
  clearViewOffset() {
    this.view !== null && (this.view.enabled = !1), this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const e = this.near;
    let t = e * Math.tan(zA * 0.5 * this.fov) / this.zoom, i = 2 * t, n = this.aspect * i, r = -0.5 * n;
    const a = this.view;
    if (this.view !== null && this.view.enabled) {
      const l = a.fullWidth, c = a.fullHeight;
      r += a.offsetX * n / l, t -= a.offsetY * i / c, n *= a.width / l, i *= a.height / c;
    }
    const o = this.filmOffset;
    o !== 0 && (r += e * o / this.getFilmWidth()), this.projectionMatrix.makePerspective(r, r + n, t, t - i, e, this.far, this.coordinateSystem), this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.fov = this.fov, t.object.zoom = this.zoom, t.object.near = this.near, t.object.far = this.far, t.object.focus = this.focus, t.object.aspect = this.aspect, this.view !== null && (t.object.view = Object.assign({}, this.view)), t.object.filmGauge = this.filmGauge, t.object.filmOffset = this.filmOffset, t;
  }
}
const ef = -90, tf = 1;
class z9e extends Ta {
  constructor(e, t, i) {
    super(), this.type = "CubeCamera", this.renderTarget = i, this.coordinateSystem = null, this.activeMipmapLevel = 0;
    const n = new Ja(ef, tf, e, t);
    n.layers = this.layers, this.add(n);
    const r = new Ja(ef, tf, e, t);
    r.layers = this.layers, this.add(r);
    const a = new Ja(ef, tf, e, t);
    a.layers = this.layers, this.add(a);
    const o = new Ja(ef, tf, e, t);
    o.layers = this.layers, this.add(o);
    const l = new Ja(ef, tf, e, t);
    l.layers = this.layers, this.add(l);
    const c = new Ja(ef, tf, e, t);
    c.layers = this.layers, this.add(c);
  }
  updateCoordinateSystem() {
    const e = this.coordinateSystem, t = this.children.concat(), [i, n, r, a, o, l] = t;
    for (const c of t)
      this.remove(c);
    if (e === rl)
      i.up.set(0, 1, 0), i.lookAt(1, 0, 0), n.up.set(0, 1, 0), n.lookAt(-1, 0, 0), r.up.set(0, 0, -1), r.lookAt(0, 1, 0), a.up.set(0, 0, 1), a.lookAt(0, -1, 0), o.up.set(0, 1, 0), o.lookAt(0, 0, 1), l.up.set(0, 1, 0), l.lookAt(0, 0, -1);
    else if (e === jx)
      i.up.set(0, -1, 0), i.lookAt(-1, 0, 0), n.up.set(0, -1, 0), n.lookAt(1, 0, 0), r.up.set(0, 0, 1), r.lookAt(0, 1, 0), a.up.set(0, 0, -1), a.lookAt(0, -1, 0), o.up.set(0, -1, 0), o.lookAt(0, 0, 1), l.up.set(0, -1, 0), l.lookAt(0, 0, -1);
    else
      throw new Error("THREE.CubeCamera.updateCoordinateSystem(): Invalid coordinate system: " + e);
    for (const c of t)
      this.add(c), c.updateMatrixWorld();
  }
  update(e, t) {
    this.parent === null && this.updateMatrixWorld();
    const { renderTarget: i, activeMipmapLevel: n } = this;
    this.coordinateSystem !== e.coordinateSystem && (this.coordinateSystem = e.coordinateSystem, this.updateCoordinateSystem());
    const [r, a, o, l, c, h] = this.children, u = e.getRenderTarget(), d = e.getActiveCubeFace(), f = e.getActiveMipmapLevel(), p = e.xr.enabled;
    e.xr.enabled = !1;
    const v = i.texture.generateMipmaps;
    i.texture.generateMipmaps = !1, e.setRenderTarget(i, 0, n), e.render(t, r), e.setRenderTarget(i, 1, n), e.render(t, a), e.setRenderTarget(i, 2, n), e.render(t, o), e.setRenderTarget(i, 3, n), e.render(t, l), e.setRenderTarget(i, 4, n), e.render(t, c), i.texture.generateMipmaps = v, e.setRenderTarget(i, 5, n), e.render(t, h), e.setRenderTarget(u, d, f), e.xr.enabled = p, i.texture.needsPMREMUpdate = !0;
  }
}
class gU extends Ur {
  constructor(e, t, i, n, r, a, o, l, c, h) {
    e = e !== void 0 ? e : [], t = t !== void 0 ? t : Op, super(e, t, i, n, r, a, o, l, c, h), this.isCubeTexture = !0, this.flipY = !1;
  }
  get images() {
    return this.image;
  }
  set images(e) {
    this.image = e;
  }
}
class k9e extends ku {
  constructor(e = 1, t = {}) {
    super(e, e, t), this.isWebGLCubeRenderTarget = !0;
    const i = { width: e, height: e, depth: 1 }, n = [i, i, i, i, i, i];
    this.texture = new gU(n, t.mapping, t.wrapS, t.wrapT, t.magFilter, t.minFilter, t.format, t.type, t.anisotropy, t.colorSpace), this.texture.isRenderTargetTexture = !0, this.texture.generateMipmaps = t.generateMipmaps !== void 0 ? t.generateMipmaps : !1, this.texture.minFilter = t.minFilter !== void 0 ? t.minFilter : Qa;
  }
  fromEquirectangularTexture(e, t) {
    this.texture.type = t.type, this.texture.colorSpace = t.colorSpace, this.texture.generateMipmaps = t.generateMipmaps, this.texture.minFilter = t.minFilter, this.texture.magFilter = t.magFilter;
    const i = {
      uniforms: {
        tEquirect: { value: null }
      },
      vertexShader: (
        /* glsl */
        `

				varying vec3 vWorldDirection;

				vec3 transformDirection( in vec3 dir, in mat4 matrix ) {

					return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );

				}

				void main() {

					vWorldDirection = transformDirection( position, modelMatrix );

					#include <begin_vertex>
					#include <project_vertex>

				}
			`
      ),
      fragmentShader: (
        /* glsl */
        `

				uniform sampler2D tEquirect;

				varying vec3 vWorldDirection;

				#include <common>

				void main() {

					vec3 direction = normalize( vWorldDirection );

					vec2 sampleUV = equirectUv( direction );

					gl_FragColor = texture2D( tEquirect, sampleUV );

				}
			`
      )
    }, n = new W2(5, 5, 5), r = new Jc({
      name: "CubemapFromEquirect",
      uniforms: Up(i.uniforms),
      vertexShader: i.vertexShader,
      fragmentShader: i.fragmentShader,
      side: Fr,
      blending: Nc
    });
    r.uniforms.tEquirect.value = t;
    const a = new al(n, r), o = t.minFilter;
    return t.minFilter === yu && (t.minFilter = Qa), new z9e(1, 10, this).update(e, a), t.minFilter = o, a.geometry.dispose(), a.material.dispose(), this;
  }
  clear(e, t, i, n) {
    const r = e.getRenderTarget();
    for (let a = 0; a < 6; a++)
      e.setRenderTarget(this, a), e.clear(t, i, n);
    e.setRenderTarget(r);
  }
}
function G9e(s) {
  let e = /* @__PURE__ */ new WeakMap();
  function t(a, o) {
    return o === S7 ? a.mapping = Op : o === w7 && (a.mapping = Np), a;
  }
  function i(a) {
    if (a && a.isTexture) {
      const o = a.mapping;
      if (o === S7 || o === w7)
        if (e.has(a)) {
          const l = e.get(a).texture;
          return t(l, a.mapping);
        } else {
          const l = a.image;
          if (l && l.height > 0) {
            const c = new k9e(l.height);
            return c.fromEquirectangularTexture(s, a), e.set(a, c), a.addEventListener("dispose", n), t(c.texture, a.mapping);
          } else
            return null;
        }
    }
    return a;
  }
  function n(a) {
    const o = a.target;
    o.removeEventListener("dispose", n);
    const l = e.get(o);
    l !== void 0 && (e.delete(o), l.dispose());
  }
  function r() {
    e = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: i,
    dispose: r
  };
}
class H9e extends mU {
  constructor(e = -1, t = 1, i = 1, n = -1, r = 0.1, a = 2e3) {
    super(), this.isOrthographicCamera = !0, this.type = "OrthographicCamera", this.zoom = 1, this.view = null, this.left = e, this.right = t, this.top = i, this.bottom = n, this.near = r, this.far = a, this.updateProjectionMatrix();
  }
  copy(e, t) {
    return super.copy(e, t), this.left = e.left, this.right = e.right, this.top = e.top, this.bottom = e.bottom, this.near = e.near, this.far = e.far, this.zoom = e.zoom, this.view = e.view === null ? null : Object.assign({}, e.view), this;
  }
  setViewOffset(e, t, i, n, r, a) {
    this.view === null && (this.view = {
      enabled: !0,
      fullWidth: 1,
      fullHeight: 1,
      offsetX: 0,
      offsetY: 0,
      width: 1,
      height: 1
    }), this.view.enabled = !0, this.view.fullWidth = e, this.view.fullHeight = t, this.view.offsetX = i, this.view.offsetY = n, this.view.width = r, this.view.height = a, this.updateProjectionMatrix();
  }
  clearViewOffset() {
    this.view !== null && (this.view.enabled = !1), this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const e = (this.right - this.left) / (2 * this.zoom), t = (this.top - this.bottom) / (2 * this.zoom), i = (this.right + this.left) / 2, n = (this.top + this.bottom) / 2;
    let r = i - e, a = i + e, o = n + t, l = n - t;
    if (this.view !== null && this.view.enabled) {
      const c = (this.right - this.left) / this.view.fullWidth / this.zoom, h = (this.top - this.bottom) / this.view.fullHeight / this.zoom;
      r += c * this.view.offsetX, a = r + c * this.view.width, o -= h * this.view.offsetY, l = o - h * this.view.height;
    }
    this.projectionMatrix.makeOrthographic(r, a, o, l, this.near, this.far, this.coordinateSystem), this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return t.object.zoom = this.zoom, t.object.left = this.left, t.object.right = this.right, t.object.top = this.top, t.object.bottom = this.bottom, t.object.near = this.near, t.object.far = this.far, this.view !== null && (t.object.view = Object.assign({}, this.view)), t;
  }
}
const Pf = 4, FR = [0.125, 0.215, 0.35, 0.446, 0.526, 0.582], au = 20, d6 = /* @__PURE__ */ new H9e(), UR = /* @__PURE__ */ new hn();
let f6 = null, p6 = 0, m6 = 0, g6 = !1;
const eu = (1 + Math.sqrt(5)) / 2, nf = 1 / eu, zR = [
  /* @__PURE__ */ new je(-eu, nf, 0),
  /* @__PURE__ */ new je(eu, nf, 0),
  /* @__PURE__ */ new je(-nf, 0, eu),
  /* @__PURE__ */ new je(nf, 0, eu),
  /* @__PURE__ */ new je(0, eu, -nf),
  /* @__PURE__ */ new je(0, eu, nf),
  /* @__PURE__ */ new je(-1, 1, -1),
  /* @__PURE__ */ new je(1, 1, -1),
  /* @__PURE__ */ new je(-1, 1, 1),
  /* @__PURE__ */ new je(1, 1, 1)
];
class kR {
  constructor(e) {
    this._renderer = e, this._pingPongRenderTarget = null, this._lodMax = 0, this._cubeSize = 0, this._lodPlanes = [], this._sizeLods = [], this._sigmas = [], this._blurMaterial = null, this._cubemapMaterial = null, this._equirectMaterial = null, this._compileMaterial(this._blurMaterial);
  }
  /**
   * Generates a PMREM from a supplied Scene, which can be faster than using an
   * image if networking bandwidth is low. Optional sigma specifies a blur radius
   * in radians to be applied to the scene before PMREM generation. Optional near
   * and far planes ensure the scene is rendered in its entirety (the cubeCamera
   * is placed at the origin).
   */
  fromScene(e, t = 0, i = 0.1, n = 100) {
    f6 = this._renderer.getRenderTarget(), p6 = this._renderer.getActiveCubeFace(), m6 = this._renderer.getActiveMipmapLevel(), g6 = this._renderer.xr.enabled, this._renderer.xr.enabled = !1, this._setSize(256);
    const r = this._allocateTargets();
    return r.depthBuffer = !0, this._sceneToCubeUV(e, i, n, r), t > 0 && this._blur(r, 0, 0, t), this._applyPMREM(r), this._cleanup(r), r;
  }
  /**
   * Generates a PMREM from an equirectangular texture, which can be either LDR
   * or HDR. The ideal input image size is 1k (1024 x 512),
   * as this matches best with the 256 x 256 cubemap output.
   * The smallest supported equirectangular image size is 64 x 32.
   */
  fromEquirectangular(e, t = null) {
    return this._fromTexture(e, t);
  }
  /**
   * Generates a PMREM from an cubemap texture, which can be either LDR
   * or HDR. The ideal input cube size is 256 x 256,
   * as this matches best with the 256 x 256 cubemap output.
   * The smallest supported cube size is 16 x 16.
   */
  fromCubemap(e, t = null) {
    return this._fromTexture(e, t);
  }
  /**
   * Pre-compiles the cubemap shader. You can get faster start-up by invoking this method during
   * your texture's network fetch for increased concurrency.
   */
  compileCubemapShader() {
    this._cubemapMaterial === null && (this._cubemapMaterial = VR(), this._compileMaterial(this._cubemapMaterial));
  }
  /**
   * Pre-compiles the equirectangular shader. You can get faster start-up by invoking this method during
   * your texture's network fetch for increased concurrency.
   */
  compileEquirectangularShader() {
    this._equirectMaterial === null && (this._equirectMaterial = HR(), this._compileMaterial(this._equirectMaterial));
  }
  /**
   * Disposes of the PMREMGenerator's internal memory. Note that PMREMGenerator is a static class,
   * so you should not need more than one PMREMGenerator object. If you do, calling dispose() on
   * one of them will cause any others to also become unusable.
   */
  dispose() {
    this._dispose(), this._cubemapMaterial !== null && this._cubemapMaterial.dispose(), this._equirectMaterial !== null && this._equirectMaterial.dispose();
  }
  // private interface
  _setSize(e) {
    this._lodMax = Math.floor(Math.log2(e)), this._cubeSize = Math.pow(2, this._lodMax);
  }
  _dispose() {
    this._blurMaterial !== null && this._blurMaterial.dispose(), this._pingPongRenderTarget !== null && this._pingPongRenderTarget.dispose();
    for (let e = 0; e < this._lodPlanes.length; e++)
      this._lodPlanes[e].dispose();
  }
  _cleanup(e) {
    this._renderer.setRenderTarget(f6, p6, m6), this._renderer.xr.enabled = g6, e.scissorTest = !1, $3(e, 0, 0, e.width, e.height);
  }
  _fromTexture(e, t) {
    e.mapping === Op || e.mapping === Np ? this._setSize(e.image.length === 0 ? 16 : e.image[0].width || e.image[0].image.width) : this._setSize(e.image.width / 4), f6 = this._renderer.getRenderTarget(), p6 = this._renderer.getActiveCubeFace(), m6 = this._renderer.getActiveMipmapLevel(), g6 = this._renderer.xr.enabled, this._renderer.xr.enabled = !1;
    const i = t || this._allocateTargets();
    return this._textureToCubeUV(e, i), this._applyPMREM(i), this._cleanup(i), i;
  }
  _allocateTargets() {
    const e = 3 * Math.max(this._cubeSize, 112), t = 4 * this._cubeSize, i = {
      magFilter: Qa,
      minFilter: Qa,
      generateMipmaps: !1,
      type: G2,
      format: no,
      colorSpace: hh,
      depthBuffer: !1
    }, n = GR(e, t, i);
    if (this._pingPongRenderTarget === null || this._pingPongRenderTarget.width !== e || this._pingPongRenderTarget.height !== t) {
      this._pingPongRenderTarget !== null && this._dispose(), this._pingPongRenderTarget = GR(e, t, i);
      const { _lodMax: r } = this;
      (({ sizeLods: this._sizeLods, lodPlanes: this._lodPlanes, sigmas: this._sigmas } = V9e(r))), this._blurMaterial = W9e(r, e, t);
    }
    return n;
  }
  _compileMaterial(e) {
    const t = new al(this._lodPlanes[0], e);
    this._renderer.compile(t, d6);
  }
  _sceneToCubeUV(e, t, i, n) {
    const o = new Ja(90, 1, t, i), l = [1, -1, 1, 1, 1, 1], c = [1, 1, 1, -1, -1, -1], h = this._renderer, u = h.autoClear, d = h.toneMapping;
    h.getClearColor(UR), h.toneMapping = Bc, h.autoClear = !1;
    const f = new fU({
      name: "PMREM.Background",
      side: Fr,
      depthWrite: !1,
      depthTest: !1
    }), p = new al(new W2(), f);
    let v = !1;
    const g = e.background;
    g ? g.isColor && (f.color.copy(g), e.background = null, v = !0) : (f.color.copy(UR), v = !0);
    for (let m = 0; m < 6; m++) {
      const x = m % 3;
      x === 0 ? (o.up.set(0, l[m], 0), o.lookAt(c[m], 0, 0)) : x === 1 ? (o.up.set(0, 0, l[m]), o.lookAt(0, c[m], 0)) : (o.up.set(0, l[m], 0), o.lookAt(0, 0, c[m]));
      const y = this._cubeSize;
      $3(n, x * y, m > 2 ? y : 0, y, y), h.setRenderTarget(n), v && h.render(p, o), h.render(e, o);
    }
    p.geometry.dispose(), p.material.dispose(), h.toneMapping = d, h.autoClear = u, e.background = g;
  }
  _textureToCubeUV(e, t) {
    const i = this._renderer, n = e.mapping === Op || e.mapping === Np;
    n ? (this._cubemapMaterial === null && (this._cubemapMaterial = VR()), this._cubemapMaterial.uniforms.flipEnvMap.value = e.isRenderTargetTexture === !1 ? -1 : 1) : this._equirectMaterial === null && (this._equirectMaterial = HR());
    const r = n ? this._cubemapMaterial : this._equirectMaterial, a = new al(this._lodPlanes[0], r), o = r.uniforms;
    o.envMap.value = e;
    const l = this._cubeSize;
    $3(t, 0, 0, 3 * l, 2 * l), i.setRenderTarget(t), i.render(a, d6);
  }
  _applyPMREM(e) {
    const t = this._renderer, i = t.autoClear;
    t.autoClear = !1;
    const n = this._lodPlanes.length;
    for (let r = 1; r < n; r++) {
      const a = Math.sqrt(this._sigmas[r] * this._sigmas[r] - this._sigmas[r - 1] * this._sigmas[r - 1]), o = zR[(n - r - 1) % zR.length];
      this._blur(e, r - 1, r, a, o);
    }
    t.autoClear = i;
  }
  /**
   * This is a two-pass Gaussian blur for a cubemap. Normally this is done
   * vertically and horizontally, but this breaks down on a cube. Here we apply
   * the blur latitudinally (around the poles), and then longitudinally (towards
   * the poles) to approximate the orthogonally-separable blur. It is least
   * accurate at the poles, but still does a decent job.
   */
  _blur(e, t, i, n, r) {
    const a = this._pingPongRenderTarget;
    this._halfBlur(
      e,
      a,
      t,
      i,
      n,
      "latitudinal",
      r
    ), this._halfBlur(
      a,
      e,
      i,
      i,
      n,
      "longitudinal",
      r
    );
  }
  _halfBlur(e, t, i, n, r, a, o) {
    const l = this._renderer, c = this._blurMaterial;
    a !== "latitudinal" && a !== "longitudinal" && console.error(
      "blur direction must be either latitudinal or longitudinal!"
    );
    const h = 3, u = new al(this._lodPlanes[n], c), d = c.uniforms, f = this._sizeLods[i] - 1, p = isFinite(r) ? Math.PI / (2 * f) : 2 * Math.PI / (2 * au - 1), v = r / p, g = isFinite(r) ? 1 + Math.floor(h * v) : au;
    g > au && console.warn(`sigmaRadians, ${r}, is too large and will clip, as it requested ${g} samples when the maximum is set to ${au}`);
    const m = [];
    let x = 0;
    for (let w = 0; w < au; ++w) {
      const C = w / v, T = Math.exp(-C * C / 2);
      m.push(T), w === 0 ? x += T : w < g && (x += 2 * T);
    }
    for (let w = 0; w < m.length; w++)
      m[w] = m[w] / x;
    d.envMap.value = e.texture, d.samples.value = g, d.weights.value = m, d.latitudinal.value = a === "latitudinal", o && (d.poleAxis.value = o);
    const { _lodMax: y } = this;
    d.dTheta.value = p, d.mipInt.value = y - i;
    const A = this._sizeLods[n], b = 3 * A * (n > y - Pf ? n - y + Pf : 0), M = 4 * (this._cubeSize - A);
    $3(t, b, M, 3 * A, 2 * A), l.setRenderTarget(t), l.render(u, d6);
  }
}
function V9e(s) {
  const e = [], t = [], i = [];
  let n = s;
  const r = s - Pf + 1 + FR.length;
  for (let a = 0; a < r; a++) {
    const o = Math.pow(2, n);
    t.push(o);
    let l = 1 / o;
    a > s - Pf ? l = FR[a - s + Pf - 1] : a === 0 && (l = 0), i.push(l);
    const c = 1 / (o - 2), h = -c, u = 1 + c, d = [h, h, u, h, u, u, h, h, u, u, h, u], f = 6, p = 6, v = 3, g = 2, m = 1, x = new Float32Array(v * p * f), y = new Float32Array(g * p * f), A = new Float32Array(m * p * f);
    for (let M = 0; M < f; M++) {
      const w = M % 3 * 2 / 3 - 1, C = M > 2 ? 0 : -1, T = [
        w,
        C,
        0,
        w + 2 / 3,
        C,
        0,
        w + 2 / 3,
        C + 1,
        0,
        w,
        C,
        0,
        w + 2 / 3,
        C + 1,
        0,
        w,
        C + 1,
        0
      ];
      x.set(T, v * p * M), y.set(d, g * p * M);
      const E = [M, M, M, M, M, M];
      A.set(E, m * p * M);
    }
    const b = new nd();
    b.setAttribute("position", new n0(x, v)), b.setAttribute("uv", new n0(y, g)), b.setAttribute("faceIndex", new n0(A, m)), e.push(b), n > Pf && n--;
  }
  return { lodPlanes: e, sizeLods: t, sigmas: i };
}
function GR(s, e, t) {
  const i = new ku(s, e, t);
  return i.texture.mapping = d8, i.texture.name = "PMREM.cubeUv", i.scissorTest = !0, i;
}
function $3(s, e, t, i, n) {
  s.viewport.set(e, t, i, n), s.scissor.set(e, t, i, n);
}
function W9e(s, e, t) {
  const i = new Float32Array(au), n = new je(0, 1, 0);
  return new Jc({
    name: "SphericalGaussianBlur",
    defines: {
      n: au,
      CUBEUV_TEXEL_WIDTH: 1 / e,
      CUBEUV_TEXEL_HEIGHT: 1 / t,
      CUBEUV_MAX_MIP: `${s}.0`
    },
    uniforms: {
      envMap: { value: null },
      samples: { value: 1 },
      weights: { value: i },
      latitudinal: { value: !1 },
      dTheta: { value: 0 },
      mipInt: { value: 0 },
      poleAxis: { value: n }
    },
    vertexShader: Bw(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;
			uniform int samples;
			uniform float weights[ n ];
			uniform bool latitudinal;
			uniform float dTheta;
			uniform float mipInt;
			uniform vec3 poleAxis;

			#define ENVMAP_TYPE_CUBE_UV
			#include <cube_uv_reflection_fragment>

			vec3 getSample( float theta, vec3 axis ) {

				float cosTheta = cos( theta );
				// Rodrigues' axis-angle rotation
				vec3 sampleDirection = vOutputDirection * cosTheta
					+ cross( axis, vOutputDirection ) * sin( theta )
					+ axis * dot( axis, vOutputDirection ) * ( 1.0 - cosTheta );

				return bilinearCubeUV( envMap, sampleDirection, mipInt );

			}

			void main() {

				vec3 axis = latitudinal ? poleAxis : cross( poleAxis, vOutputDirection );

				if ( all( equal( axis, vec3( 0.0 ) ) ) ) {

					axis = vec3( vOutputDirection.z, 0.0, - vOutputDirection.x );

				}

				axis = normalize( axis );

				gl_FragColor = vec4( 0.0, 0.0, 0.0, 1.0 );
				gl_FragColor.rgb += weights[ 0 ] * getSample( 0.0, axis );

				for ( int i = 1; i < n; i++ ) {

					if ( i >= samples ) {

						break;

					}

					float theta = dTheta * float( i );
					gl_FragColor.rgb += weights[ i ] * getSample( -1.0 * theta, axis );
					gl_FragColor.rgb += weights[ i ] * getSample( theta, axis );

				}

			}
		`
    ),
    blending: Nc,
    depthTest: !1,
    depthWrite: !1
  });
}
function HR() {
  return new Jc({
    name: "EquirectangularToCubeUV",
    uniforms: {
      envMap: { value: null }
    },
    vertexShader: Bw(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;

			#include <common>

			void main() {

				vec3 outputDirection = normalize( vOutputDirection );
				vec2 uv = equirectUv( outputDirection );

				gl_FragColor = vec4( texture2D ( envMap, uv ).rgb, 1.0 );

			}
		`
    ),
    blending: Nc,
    depthTest: !1,
    depthWrite: !1
  });
}
function VR() {
  return new Jc({
    name: "CubemapToCubeUV",
    uniforms: {
      envMap: { value: null },
      flipEnvMap: { value: -1 }
    },
    vertexShader: Bw(),
    fragmentShader: (
      /* glsl */
      `

			precision mediump float;
			precision mediump int;

			uniform float flipEnvMap;

			varying vec3 vOutputDirection;

			uniform samplerCube envMap;

			void main() {

				gl_FragColor = textureCube( envMap, vec3( flipEnvMap * vOutputDirection.x, vOutputDirection.yz ) );

			}
		`
    ),
    blending: Nc,
    depthTest: !1,
    depthWrite: !1
  });
}
function Bw() {
  return (
    /* glsl */
    `

		precision mediump float;
		precision mediump int;

		attribute float faceIndex;

		varying vec3 vOutputDirection;

		// RH coordinate system; PMREM face-indexing convention
		vec3 getDirection( vec2 uv, float face ) {

			uv = 2.0 * uv - 1.0;

			vec3 direction = vec3( uv, 1.0 );

			if ( face == 0.0 ) {

				direction = direction.zyx; // ( 1, v, u ) pos x

			} else if ( face == 1.0 ) {

				direction = direction.xzy;
				direction.xz *= -1.0; // ( -u, 1, -v ) pos y

			} else if ( face == 2.0 ) {

				direction.x *= -1.0; // ( -u, v, 1 ) pos z

			} else if ( face == 3.0 ) {

				direction = direction.zyx;
				direction.xz *= -1.0; // ( -1, v, -u ) neg x

			} else if ( face == 4.0 ) {

				direction = direction.xzy;
				direction.xy *= -1.0; // ( -u, -1, v ) neg y

			} else if ( face == 5.0 ) {

				direction.z *= -1.0; // ( u, v, -1 ) neg z

			}

			return direction;

		}

		void main() {

			vOutputDirection = getDirection( uv, faceIndex );
			gl_Position = vec4( position, 1.0 );

		}
	`
  );
}
function X9e(s) {
  let e = /* @__PURE__ */ new WeakMap(), t = null;
  function i(o) {
    if (o && o.isTexture) {
      const l = o.mapping, c = l === S7 || l === w7, h = l === Op || l === Np;
      if (c || h) {
        let u = e.get(o);
        const d = u !== void 0 ? u.texture.pmremVersion : 0;
        if (o.isRenderTargetTexture && o.pmremVersion !== d)
          return t === null && (t = new kR(s)), u = c ? t.fromEquirectangular(o, u) : t.fromCubemap(o, u), u.texture.pmremVersion = o.pmremVersion, e.set(o, u), u.texture;
        if (u !== void 0)
          return u.texture;
        {
          const f = o.image;
          return c && f && f.height > 0 || h && f && n(f) ? (t === null && (t = new kR(s)), u = c ? t.fromEquirectangular(o) : t.fromCubemap(o), u.texture.pmremVersion = o.pmremVersion, e.set(o, u), o.addEventListener("dispose", r), u.texture) : null;
        }
      }
    }
    return o;
  }
  function n(o) {
    let l = 0;
    const c = 6;
    for (let h = 0; h < c; h++)
      o[h] !== void 0 && l++;
    return l === c;
  }
  function r(o) {
    const l = o.target;
    l.removeEventListener("dispose", r);
    const c = e.get(l);
    c !== void 0 && (e.delete(l), c.dispose());
  }
  function a() {
    e = /* @__PURE__ */ new WeakMap(), t !== null && (t.dispose(), t = null);
  }
  return {
    get: i,
    dispose: a
  };
}
function j9e(s) {
  const e = {};
  function t(i) {
    if (e[i] !== void 0)
      return e[i];
    let n;
    switch (i) {
      case "WEBGL_depth_texture":
        n = s.getExtension("WEBGL_depth_texture") || s.getExtension("MOZ_WEBGL_depth_texture") || s.getExtension("WEBKIT_WEBGL_depth_texture");
        break;
      case "EXT_texture_filter_anisotropic":
        n = s.getExtension("EXT_texture_filter_anisotropic") || s.getExtension("MOZ_EXT_texture_filter_anisotropic") || s.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        break;
      case "WEBGL_compressed_texture_s3tc":
        n = s.getExtension("WEBGL_compressed_texture_s3tc") || s.getExtension("MOZ_WEBGL_compressed_texture_s3tc") || s.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc");
        break;
      case "WEBGL_compressed_texture_pvrtc":
        n = s.getExtension("WEBGL_compressed_texture_pvrtc") || s.getExtension("WEBKIT_WEBGL_compressed_texture_pvrtc");
        break;
      default:
        n = s.getExtension(i);
    }
    return e[i] = n, n;
  }
  return {
    has: function(i) {
      return t(i) !== null;
    },
    init: function() {
      t("EXT_color_buffer_float"), t("WEBGL_clip_cull_distance"), t("OES_texture_float_linear"), t("EXT_color_buffer_half_float"), t("WEBGL_multisampled_render_to_texture"), t("WEBGL_render_shared_exponent");
    },
    get: function(i) {
      const n = t(i);
      return n === null && lU("THREE.WebGLRenderer: " + i + " extension not supported."), n;
    }
  };
}
function q9e(s, e, t, i) {
  const n = {}, r = /* @__PURE__ */ new WeakMap();
  function a(u) {
    const d = u.target;
    d.index !== null && e.remove(d.index);
    for (const p in d.attributes)
      e.remove(d.attributes[p]);
    for (const p in d.morphAttributes) {
      const v = d.morphAttributes[p];
      for (let g = 0, m = v.length; g < m; g++)
        e.remove(v[g]);
    }
    d.removeEventListener("dispose", a), delete n[d.id];
    const f = r.get(d);
    f && (e.remove(f), r.delete(d)), i.releaseStatesOfGeometry(d), d.isInstancedBufferGeometry === !0 && delete d._maxInstanceCount, t.memory.geometries--;
  }
  function o(u, d) {
    return n[d.id] === !0 || (d.addEventListener("dispose", a), n[d.id] = !0, t.memory.geometries++), d;
  }
  function l(u) {
    const d = u.attributes;
    for (const p in d)
      e.update(d[p], s.ARRAY_BUFFER);
    const f = u.morphAttributes;
    for (const p in f) {
      const v = f[p];
      for (let g = 0, m = v.length; g < m; g++)
        e.update(v[g], s.ARRAY_BUFFER);
    }
  }
  function c(u) {
    const d = [], f = u.index, p = u.attributes.position;
    let v = 0;
    if (f !== null) {
      const x = f.array;
      v = f.version;
      for (let y = 0, A = x.length; y < A; y += 3) {
        const b = x[y + 0], M = x[y + 1], w = x[y + 2];
        d.push(b, M, M, w, w, b);
      }
    } else if (p !== void 0) {
      const x = p.array;
      v = p.version;
      for (let y = 0, A = x.length / 3 - 1; y < A; y += 3) {
        const b = y + 0, M = y + 1, w = y + 2;
        d.push(b, M, M, w, w, b);
      }
    } else
      return;
    const g = new (oU(d) ? hU : cU)(d, 1);
    g.version = v;
    const m = r.get(u);
    m && e.remove(m), r.set(u, g);
  }
  function h(u) {
    const d = r.get(u);
    if (d) {
      const f = u.index;
      f !== null && d.version < f.version && c(u);
    } else
      c(u);
    return r.get(u);
  }
  return {
    get: o,
    update: l,
    getWireframeAttribute: h
  };
}
function Y9e(s, e, t) {
  let i;
  function n(d) {
    i = d;
  }
  let r, a;
  function o(d) {
    r = d.type, a = d.bytesPerElement;
  }
  function l(d, f) {
    s.drawElements(i, f, r, d * a), t.update(f, i, 1);
  }
  function c(d, f, p) {
    p !== 0 && (s.drawElementsInstanced(i, f, r, d * a, p), t.update(f, i, p));
  }
  function h(d, f, p) {
    if (p === 0)
      return;
    e.get("WEBGL_multi_draw").multiDrawElementsWEBGL(i, f, 0, r, d, 0, p);
    let g = 0;
    for (let m = 0; m < p; m++)
      g += f[m];
    t.update(g, i, 1);
  }
  function u(d, f, p, v) {
    if (p === 0)
      return;
    const g = e.get("WEBGL_multi_draw");
    if (g === null)
      for (let m = 0; m < d.length; m++)
        c(d[m] / a, f[m], v[m]);
    else {
      g.multiDrawElementsInstancedWEBGL(i, f, 0, r, d, 0, v, 0, p);
      let m = 0;
      for (let x = 0; x < p; x++)
        m += f[x];
      for (let x = 0; x < v.length; x++)
        t.update(m, i, v[x]);
    }
  }
  this.setMode = n, this.setIndex = o, this.render = l, this.renderInstances = c, this.renderMultiDraw = h, this.renderMultiDrawInstances = u;
}
function Z9e(s) {
  const e = {
    geometries: 0,
    textures: 0
  }, t = {
    frame: 0,
    calls: 0,
    triangles: 0,
    points: 0,
    lines: 0
  };
  function i(r, a, o) {
    switch (t.calls++, a) {
      case s.TRIANGLES:
        t.triangles += o * (r / 3);
        break;
      case s.LINES:
        t.lines += o * (r / 2);
        break;
      case s.LINE_STRIP:
        t.lines += o * (r - 1);
        break;
      case s.LINE_LOOP:
        t.lines += o * r;
        break;
      case s.POINTS:
        t.points += o * r;
        break;
      default:
        console.error("THREE.WebGLInfo: Unknown draw mode:", a);
        break;
    }
  }
  function n() {
    t.calls = 0, t.triangles = 0, t.points = 0, t.lines = 0;
  }
  return {
    memory: e,
    render: t,
    programs: null,
    autoReset: !0,
    reset: n,
    update: i
  };
}
class vU extends Ur {
  constructor(e = null, t = 1, i = 1, n = 1) {
    super(null), this.isDataArrayTexture = !0, this.image = { data: e, width: t, height: i, depth: n }, this.magFilter = Ea, this.minFilter = Ea, this.wrapR = vu, this.generateMipmaps = !1, this.flipY = !1, this.unpackAlignment = 1, this.layerUpdates = /* @__PURE__ */ new Set();
  }
  addLayerUpdate(e) {
    this.layerUpdates.add(e);
  }
  clearLayerUpdates() {
    this.layerUpdates.clear();
  }
}
function K9e(s, e, t) {
  const i = /* @__PURE__ */ new WeakMap(), n = new Cs();
  function r(a, o, l) {
    const c = a.morphTargetInfluences, h = o.morphAttributes.position || o.morphAttributes.normal || o.morphAttributes.color, u = h !== void 0 ? h.length : 0;
    let d = i.get(o);
    if (d === void 0 || d.count !== u) {
      let T = function() {
        w.dispose(), i.delete(o), o.removeEventListener("dispose", T);
      };
      d !== void 0 && d.texture.dispose();
      const f = o.morphAttributes.position !== void 0, p = o.morphAttributes.normal !== void 0, v = o.morphAttributes.color !== void 0, g = o.morphAttributes.position || [], m = o.morphAttributes.normal || [], x = o.morphAttributes.color || [];
      let y = 0;
      f === !0 && (y = 1), p === !0 && (y = 2), v === !0 && (y = 3);
      let A = o.attributes.position.count * y, b = 1;
      A > e.maxTextureSize && (b = Math.ceil(A / e.maxTextureSize), A = e.maxTextureSize);
      const M = new Float32Array(A * b * 4 * u), w = new vU(M, A, b, u);
      w.type = sl, w.needsUpdate = !0;
      const C = y * 4;
      for (let E = 0; E < u; E++) {
        const P = g[E], L = m[E], I = x[E], N = A * b * 4 * E;
        for (let H = 0; H < P.count; H++) {
          const U = H * C;
          f === !0 && (n.fromBufferAttribute(P, H), M[N + U + 0] = n.x, M[N + U + 1] = n.y, M[N + U + 2] = n.z, M[N + U + 3] = 0), p === !0 && (n.fromBufferAttribute(L, H), M[N + U + 4] = n.x, M[N + U + 5] = n.y, M[N + U + 6] = n.z, M[N + U + 7] = 0), v === !0 && (n.fromBufferAttribute(I, H), M[N + U + 8] = n.x, M[N + U + 9] = n.y, M[N + U + 10] = n.z, M[N + U + 11] = I.itemSize === 4 ? n.w : 1);
        }
      }
      d = {
        count: u,
        texture: w,
        size: new tn(A, b)
      }, i.set(o, d), o.addEventListener("dispose", T);
    }
    if (a.isInstancedMesh === !0 && a.morphTexture !== null)
      l.getUniforms().setValue(s, "morphTexture", a.morphTexture, t);
    else {
      let f = 0;
      for (let v = 0; v < c.length; v++)
        f += c[v];
      const p = o.morphTargetsRelative ? 1 : 1 - f;
      l.getUniforms().setValue(s, "morphTargetBaseInfluence", p), l.getUniforms().setValue(s, "morphTargetInfluences", c);
    }
    l.getUniforms().setValue(s, "morphTargetsTexture", d.texture, t), l.getUniforms().setValue(s, "morphTargetsTextureSize", d.size);
  }
  return {
    update: r
  };
}
function J9e(s, e, t, i) {
  let n = /* @__PURE__ */ new WeakMap();
  function r(l) {
    const c = i.render.frame, h = l.geometry, u = e.get(l, h);
    if (n.get(u) !== c && (e.update(u), n.set(u, c)), l.isInstancedMesh && (l.hasEventListener("dispose", o) === !1 && l.addEventListener("dispose", o), n.get(l) !== c && (t.update(l.instanceMatrix, s.ARRAY_BUFFER), l.instanceColor !== null && t.update(l.instanceColor, s.ARRAY_BUFFER), n.set(l, c))), l.isSkinnedMesh) {
      const d = l.skeleton;
      n.get(d) !== c && (d.update(), n.set(d, c));
    }
    return u;
  }
  function a() {
    n = /* @__PURE__ */ new WeakMap();
  }
  function o(l) {
    const c = l.target;
    c.removeEventListener("dispose", o), t.remove(c.instanceMatrix), c.instanceColor !== null && t.remove(c.instanceColor);
  }
  return {
    update: r,
    dispose: a
  };
}
class Q9e extends Ur {
  constructor(e = null, t = 1, i = 1, n = 1) {
    super(null), this.isData3DTexture = !0, this.image = { data: e, width: t, height: i, depth: n }, this.magFilter = Ea, this.minFilter = Ea, this.wrapR = vu, this.generateMipmaps = !1, this.flipY = !1, this.unpackAlignment = 1;
  }
}
class yU extends Ur {
  constructor(e, t, i, n, r, a, o, l, c, h = Qf) {
    if (h !== Qf && h !== Fp)
      throw new Error("DepthTexture format must be either THREE.DepthFormat or THREE.DepthStencilFormat");
    i === void 0 && h === Qf && (i = zu), i === void 0 && h === Fp && (i = Bp), super(null, n, r, a, o, l, h, i, c), this.isDepthTexture = !0, this.image = { width: e, height: t }, this.magFilter = o !== void 0 ? o : Ea, this.minFilter = l !== void 0 ? l : Ea, this.flipY = !1, this.generateMipmaps = !1, this.compareFunction = null;
  }
  copy(e) {
    return super.copy(e), this.compareFunction = e.compareFunction, this;
  }
  toJSON(e) {
    const t = super.toJSON(e);
    return this.compareFunction !== null && (t.compareFunction = this.compareFunction), t;
  }
}
const xU = /* @__PURE__ */ new Ur(), WR = /* @__PURE__ */ new yU(1, 1), AU = /* @__PURE__ */ new vU(), bU = /* @__PURE__ */ new Q9e(), MU = /* @__PURE__ */ new gU(), XR = [], jR = [], qR = new Float32Array(16), YR = new Float32Array(9), ZR = new Float32Array(4);
function sm(s, e, t) {
  const i = s[0];
  if (i <= 0 || i > 0)
    return s;
  const n = e * t;
  let r = XR[n];
  if (r === void 0 && (r = new Float32Array(n), XR[n] = r), e !== 0) {
    i.toArray(r, 0);
    for (let a = 1, o = 0; a !== e; ++a)
      o += t, s[a].toArray(r, o);
  }
  return r;
}
function ys(s, e) {
  if (s.length !== e.length)
    return !1;
  for (let t = 0, i = s.length; t < i; t++)
    if (s[t] !== e[t])
      return !1;
  return !0;
}
function xs(s, e) {
  for (let t = 0, i = e.length; t < i; t++)
    s[t] = e[t];
}
function g8(s, e) {
  let t = jR[e];
  t === void 0 && (t = new Int32Array(e), jR[e] = t);
  for (let i = 0; i !== e; ++i)
    t[i] = s.allocateTextureUnit();
  return t;
}
function $9e(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1f(this.addr, e), t[0] = e);
}
function exe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2f(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (ys(t, e))
      return;
    s.uniform2fv(this.addr, e), xs(t, e);
  }
}
function txe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3f(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else if (e.r !== void 0)
    (t[0] !== e.r || t[1] !== e.g || t[2] !== e.b) && (s.uniform3f(this.addr, e.r, e.g, e.b), t[0] = e.r, t[1] = e.g, t[2] = e.b);
  else {
    if (ys(t, e))
      return;
    s.uniform3fv(this.addr, e), xs(t, e);
  }
}
function ixe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4f(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (ys(t, e))
      return;
    s.uniform4fv(this.addr, e), xs(t, e);
  }
}
function nxe(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (ys(t, e))
      return;
    s.uniformMatrix2fv(this.addr, !1, e), xs(t, e);
  } else {
    if (ys(t, i))
      return;
    ZR.set(i), s.uniformMatrix2fv(this.addr, !1, ZR), xs(t, i);
  }
}
function sxe(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (ys(t, e))
      return;
    s.uniformMatrix3fv(this.addr, !1, e), xs(t, e);
  } else {
    if (ys(t, i))
      return;
    YR.set(i), s.uniformMatrix3fv(this.addr, !1, YR), xs(t, i);
  }
}
function rxe(s, e) {
  const t = this.cache, i = e.elements;
  if (i === void 0) {
    if (ys(t, e))
      return;
    s.uniformMatrix4fv(this.addr, !1, e), xs(t, e);
  } else {
    if (ys(t, i))
      return;
    qR.set(i), s.uniformMatrix4fv(this.addr, !1, qR), xs(t, i);
  }
}
function axe(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1i(this.addr, e), t[0] = e);
}
function oxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2i(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (ys(t, e))
      return;
    s.uniform2iv(this.addr, e), xs(t, e);
  }
}
function lxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3i(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else {
    if (ys(t, e))
      return;
    s.uniform3iv(this.addr, e), xs(t, e);
  }
}
function cxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4i(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (ys(t, e))
      return;
    s.uniform4iv(this.addr, e), xs(t, e);
  }
}
function hxe(s, e) {
  const t = this.cache;
  t[0] !== e && (s.uniform1ui(this.addr, e), t[0] = e);
}
function uxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y) && (s.uniform2ui(this.addr, e.x, e.y), t[0] = e.x, t[1] = e.y);
  else {
    if (ys(t, e))
      return;
    s.uniform2uiv(this.addr, e), xs(t, e);
  }
}
function dxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z) && (s.uniform3ui(this.addr, e.x, e.y, e.z), t[0] = e.x, t[1] = e.y, t[2] = e.z);
  else {
    if (ys(t, e))
      return;
    s.uniform3uiv(this.addr, e), xs(t, e);
  }
}
function fxe(s, e) {
  const t = this.cache;
  if (e.x !== void 0)
    (t[0] !== e.x || t[1] !== e.y || t[2] !== e.z || t[3] !== e.w) && (s.uniform4ui(this.addr, e.x, e.y, e.z, e.w), t[0] = e.x, t[1] = e.y, t[2] = e.z, t[3] = e.w);
  else {
    if (ys(t, e))
      return;
    s.uniform4uiv(this.addr, e), xs(t, e);
  }
}
function pxe(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n);
  let r;
  this.type === s.SAMPLER_2D_SHADOW ? (WR.compareFunction = nU, r = WR) : r = xU, t.setTexture2D(e || r, n);
}
function mxe(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTexture3D(e || bU, n);
}
function gxe(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTextureCube(e || MU, n);
}
function vxe(s, e, t) {
  const i = this.cache, n = t.allocateTextureUnit();
  i[0] !== n && (s.uniform1i(this.addr, n), i[0] = n), t.setTexture2DArray(e || AU, n);
}
function yxe(s) {
  switch (s) {
    case 5126:
      return $9e;
    case 35664:
      return exe;
    case 35665:
      return txe;
    case 35666:
      return ixe;
    case 35674:
      return nxe;
    case 35675:
      return sxe;
    case 35676:
      return rxe;
    case 5124:
    case 35670:
      return axe;
    case 35667:
    case 35671:
      return oxe;
    case 35668:
    case 35672:
      return lxe;
    case 35669:
    case 35673:
      return cxe;
    case 5125:
      return hxe;
    case 36294:
      return uxe;
    case 36295:
      return dxe;
    case 36296:
      return fxe;
    case 35678:
    case 36198:
    case 36298:
    case 36306:
    case 35682:
      return pxe;
    case 35679:
    case 36299:
    case 36307:
      return mxe;
    case 35680:
    case 36300:
    case 36308:
    case 36293:
      return gxe;
    case 36289:
    case 36303:
    case 36311:
    case 36292:
      return vxe;
  }
}
function xxe(s, e) {
  s.uniform1fv(this.addr, e);
}
function Axe(s, e) {
  const t = sm(e, this.size, 2);
  s.uniform2fv(this.addr, t);
}
function bxe(s, e) {
  const t = sm(e, this.size, 3);
  s.uniform3fv(this.addr, t);
}
function Mxe(s, e) {
  const t = sm(e, this.size, 4);
  s.uniform4fv(this.addr, t);
}
function Sxe(s, e) {
  const t = sm(e, this.size, 4);
  s.uniformMatrix2fv(this.addr, !1, t);
}
function wxe(s, e) {
  const t = sm(e, this.size, 9);
  s.uniformMatrix3fv(this.addr, !1, t);
}
function Exe(s, e) {
  const t = sm(e, this.size, 16);
  s.uniformMatrix4fv(this.addr, !1, t);
}
function Txe(s, e) {
  s.uniform1iv(this.addr, e);
}
function Cxe(s, e) {
  s.uniform2iv(this.addr, e);
}
function _xe(s, e) {
  s.uniform3iv(this.addr, e);
}
function Pxe(s, e) {
  s.uniform4iv(this.addr, e);
}
function Rxe(s, e) {
  s.uniform1uiv(this.addr, e);
}
function Dxe(s, e) {
  s.uniform2uiv(this.addr, e);
}
function Lxe(s, e) {
  s.uniform3uiv(this.addr, e);
}
function Ixe(s, e) {
  s.uniform4uiv(this.addr, e);
}
function Oxe(s, e, t) {
  const i = this.cache, n = e.length, r = g8(t, n);
  ys(i, r) || (s.uniform1iv(this.addr, r), xs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture2D(e[a] || xU, r[a]);
}
function Nxe(s, e, t) {
  const i = this.cache, n = e.length, r = g8(t, n);
  ys(i, r) || (s.uniform1iv(this.addr, r), xs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture3D(e[a] || bU, r[a]);
}
function Bxe(s, e, t) {
  const i = this.cache, n = e.length, r = g8(t, n);
  ys(i, r) || (s.uniform1iv(this.addr, r), xs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTextureCube(e[a] || MU, r[a]);
}
function Fxe(s, e, t) {
  const i = this.cache, n = e.length, r = g8(t, n);
  ys(i, r) || (s.uniform1iv(this.addr, r), xs(i, r));
  for (let a = 0; a !== n; ++a)
    t.setTexture2DArray(e[a] || AU, r[a]);
}
function Uxe(s) {
  switch (s) {
    case 5126:
      return xxe;
    case 35664:
      return Axe;
    case 35665:
      return bxe;
    case 35666:
      return Mxe;
    case 35674:
      return Sxe;
    case 35675:
      return wxe;
    case 35676:
      return Exe;
    case 5124:
    case 35670:
      return Txe;
    case 35667:
    case 35671:
      return Cxe;
    case 35668:
    case 35672:
      return _xe;
    case 35669:
    case 35673:
      return Pxe;
    case 5125:
      return Rxe;
    case 36294:
      return Dxe;
    case 36295:
      return Lxe;
    case 36296:
      return Ixe;
    case 35678:
    case 36198:
    case 36298:
    case 36306:
    case 35682:
      return Oxe;
    case 35679:
    case 36299:
    case 36307:
      return Nxe;
    case 35680:
    case 36300:
    case 36308:
    case 36293:
      return Bxe;
    case 36289:
    case 36303:
    case 36311:
    case 36292:
      return Fxe;
  }
}
class zxe {
  constructor(e, t, i) {
    this.id = e, this.addr = i, this.cache = [], this.type = t.type, this.setValue = yxe(t.type);
  }
}
class kxe {
  constructor(e, t, i) {
    this.id = e, this.addr = i, this.cache = [], this.type = t.type, this.size = t.size, this.setValue = Uxe(t.type);
  }
}
class Gxe {
  constructor(e) {
    this.id = e, this.seq = [], this.map = {};
  }
  setValue(e, t, i) {
    const n = this.seq;
    for (let r = 0, a = n.length; r !== a; ++r) {
      const o = n[r];
      o.setValue(e, t[o.id], i);
    }
  }
}
const v6 = /(\w+)(\])?(\[|\.)?/g;
function KR(s, e) {
  s.seq.push(e), s.map[e.id] = e;
}
function Hxe(s, e, t) {
  const i = s.name, n = i.length;
  for (v6.lastIndex = 0; ; ) {
    const r = v6.exec(i), a = v6.lastIndex;
    let o = r[1];
    const l = r[2] === "]", c = r[3];
    if (l && (o = o | 0), c === void 0 || c === "[" && a + 2 === n) {
      KR(t, c === void 0 ? new zxe(o, s, e) : new kxe(o, s, e));
      break;
    } else {
      let u = t.map[o];
      u === void 0 && (u = new Gxe(o), KR(t, u)), t = u;
    }
  }
}
class i9 {
  constructor(e, t) {
    this.seq = [], this.map = {};
    const i = e.getProgramParameter(t, e.ACTIVE_UNIFORMS);
    for (let n = 0; n < i; ++n) {
      const r = e.getActiveUniform(t, n), a = e.getUniformLocation(t, r.name);
      Hxe(r, a, this);
    }
  }
  setValue(e, t, i, n) {
    const r = this.map[t];
    r !== void 0 && r.setValue(e, i, n);
  }
  setOptional(e, t, i) {
    const n = t[i];
    n !== void 0 && this.setValue(e, i, n);
  }
  static upload(e, t, i, n) {
    for (let r = 0, a = t.length; r !== a; ++r) {
      const o = t[r], l = i[o.id];
      l.needsUpdate !== !1 && o.setValue(e, l.value, n);
    }
  }
  static seqWithValue(e, t) {
    const i = [];
    for (let n = 0, r = e.length; n !== r; ++n) {
      const a = e[n];
      a.id in t && i.push(a);
    }
    return i;
  }
}
function JR(s, e, t) {
  const i = s.createShader(e);
  return s.shaderSource(i, t), s.compileShader(i), i;
}
const Vxe = 37297;
let Wxe = 0;
function Xxe(s, e) {
  const t = s.split(`
`), i = [], n = Math.max(e - 6, 0), r = Math.min(e + 6, t.length);
  for (let a = n; a < r; a++) {
    const o = a + 1;
    i.push(`${o === e ? ">" : " "} ${o}: ${t[a]}`);
  }
  return i.join(`
`);
}
function jxe(s) {
  const e = rn.getPrimaries(rn.workingColorSpace), t = rn.getPrimaries(s);
  let i;
  switch (e === t ? i = "" : e === Xx && t === Wx ? i = "LinearDisplayP3ToLinearSRGB" : e === Wx && t === Xx && (i = "LinearSRGBToLinearDisplayP3"), s) {
    case hh:
    case f8:
      return [i, "LinearTransferOETF"];
    case Oo:
    case Ow:
      return [i, "sRGBTransferOETF"];
    default:
      return console.warn("THREE.WebGLProgram: Unsupported color space:", s), [i, "LinearTransferOETF"];
  }
}
function QR(s, e, t) {
  const i = s.getShaderParameter(e, s.COMPILE_STATUS), n = s.getShaderInfoLog(e).trim();
  if (i && n === "")
    return "";
  const r = /ERROR: 0:(\d+)/.exec(n);
  if (r) {
    const a = parseInt(r[1]);
    return t.toUpperCase() + `

` + n + `

` + Xxe(s.getShaderSource(e), a);
  } else
    return n;
}
function qxe(s, e) {
  const t = jxe(e);
  return `vec4 ${s}( vec4 value ) { return ${t[0]}( ${t[1]}( value ) ); }`;
}
function Yxe(s, e) {
  let t;
  switch (e) {
    case uve:
      t = "Linear";
      break;
    case dve:
      t = "Reinhard";
      break;
    case fve:
      t = "OptimizedCineon";
      break;
    case pve:
      t = "ACESFilmic";
      break;
    case gve:
      t = "AgX";
      break;
    case vve:
      t = "Neutral";
      break;
    case mve:
      t = "Custom";
      break;
    default:
      console.warn("THREE.WebGLProgram: Unsupported toneMapping:", e), t = "Linear";
  }
  return "vec3 " + s + "( vec3 color ) { return " + t + "ToneMapping( color ); }";
}
function Zxe(s) {
  return [
    s.extensionClipCullDistance ? "#extension GL_ANGLE_clip_cull_distance : require" : "",
    s.extensionMultiDraw ? "#extension GL_ANGLE_multi_draw : require" : ""
  ].filter(f1).join(`
`);
}
function Kxe(s) {
  const e = [];
  for (const t in s) {
    const i = s[t];
    i !== !1 && e.push("#define " + t + " " + i);
  }
  return e.join(`
`);
}
function Jxe(s, e) {
  const t = {}, i = s.getProgramParameter(e, s.ACTIVE_ATTRIBUTES);
  for (let n = 0; n < i; n++) {
    const r = s.getActiveAttrib(e, n), a = r.name;
    let o = 1;
    r.type === s.FLOAT_MAT2 && (o = 2), r.type === s.FLOAT_MAT3 && (o = 3), r.type === s.FLOAT_MAT4 && (o = 4), t[a] = {
      type: r.type,
      location: s.getAttribLocation(e, a),
      locationSize: o
    };
  }
  return t;
}
function f1(s) {
  return s !== "";
}
function $R(s, e) {
  const t = e.numSpotLightShadows + e.numSpotLightMaps - e.numSpotLightShadowsWithMaps;
  return s.replace(/NUM_DIR_LIGHTS/g, e.numDirLights).replace(/NUM_SPOT_LIGHTS/g, e.numSpotLights).replace(/NUM_SPOT_LIGHT_MAPS/g, e.numSpotLightMaps).replace(/NUM_SPOT_LIGHT_COORDS/g, t).replace(/NUM_RECT_AREA_LIGHTS/g, e.numRectAreaLights).replace(/NUM_POINT_LIGHTS/g, e.numPointLights).replace(/NUM_HEMI_LIGHTS/g, e.numHemiLights).replace(/NUM_DIR_LIGHT_SHADOWS/g, e.numDirLightShadows).replace(/NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS/g, e.numSpotLightShadowsWithMaps).replace(/NUM_SPOT_LIGHT_SHADOWS/g, e.numSpotLightShadows).replace(/NUM_POINT_LIGHT_SHADOWS/g, e.numPointLightShadows);
}
function eD(s, e) {
  return s.replace(/NUM_CLIPPING_PLANES/g, e.numClippingPlanes).replace(
    /UNION_CLIPPING_PLANES/g,
    e.numClippingPlanes - e.numClipIntersection
  );
}
const Qxe = /^[ \t]*#include +<([\w\d./]+)>/gm;
function $7(s) {
  return s.replace(Qxe, e4e);
}
const $xe = /* @__PURE__ */ new Map();
function e4e(s, e) {
  let t = yi[e];
  if (t === void 0) {
    const i = $xe.get(e);
    if (i !== void 0)
      t = yi[i], console.warn(
        'THREE.WebGLRenderer: Shader chunk "%s" has been deprecated. Use "%s" instead.',
        e,
        i
      );
    else
      throw new Error("Can not resolve #include <" + e + ">");
  }
  return $7(t);
}
const t4e = /#pragma unroll_loop_start\s+for\s*\(\s*int\s+i\s*=\s*(\d+)\s*;\s*i\s*<\s*(\d+)\s*;\s*i\s*\+\+\s*\)\s*{([\s\S]+?)}\s+#pragma unroll_loop_end/g;
function tD(s) {
  return s.replace(t4e, i4e);
}
function i4e(s, e, t, i) {
  let n = "";
  for (let r = parseInt(e); r < parseInt(t); r++)
    n += i.replace(/\[\s*i\s*\]/g, "[ " + r + " ]").replace(/UNROLLED_LOOP_INDEX/g, r);
  return n;
}
function iD(s) {
  let e = `precision ${s.precision} float;
	precision ${s.precision} int;
	precision ${s.precision} sampler2D;
	precision ${s.precision} samplerCube;
	precision ${s.precision} sampler3D;
	precision ${s.precision} sampler2DArray;
	precision ${s.precision} sampler2DShadow;
	precision ${s.precision} samplerCubeShadow;
	precision ${s.precision} sampler2DArrayShadow;
	precision ${s.precision} isampler2D;
	precision ${s.precision} isampler3D;
	precision ${s.precision} isamplerCube;
	precision ${s.precision} isampler2DArray;
	precision ${s.precision} usampler2D;
	precision ${s.precision} usampler3D;
	precision ${s.precision} usamplerCube;
	precision ${s.precision} usampler2DArray;
	`;
  return s.precision === "highp" ? e += `
#define HIGH_PRECISION` : s.precision === "mediump" ? e += `
#define MEDIUM_PRECISION` : s.precision === "lowp" && (e += `
#define LOW_PRECISION`), e;
}
function n4e(s) {
  let e = "SHADOWMAP_TYPE_BASIC";
  return s.shadowMapType === WF ? e = "SHADOWMAP_TYPE_PCF" : s.shadowMapType === F2e ? e = "SHADOWMAP_TYPE_PCF_SOFT" : s.shadowMapType === V0 && (e = "SHADOWMAP_TYPE_VSM"), e;
}
function s4e(s) {
  let e = "ENVMAP_TYPE_CUBE";
  if (s.envMap)
    switch (s.envMapMode) {
      case Op:
      case Np:
        e = "ENVMAP_TYPE_CUBE";
        break;
      case d8:
        e = "ENVMAP_TYPE_CUBE_UV";
        break;
    }
  return e;
}
function r4e(s) {
  let e = "ENVMAP_MODE_REFLECTION";
  if (s.envMap)
    switch (s.envMapMode) {
      case Np:
        e = "ENVMAP_MODE_REFRACTION";
        break;
    }
  return e;
}
function a4e(s) {
  let e = "ENVMAP_BLENDING_NONE";
  if (s.envMap)
    switch (s.combine) {
      case XF:
        e = "ENVMAP_BLENDING_MULTIPLY";
        break;
      case cve:
        e = "ENVMAP_BLENDING_MIX";
        break;
      case hve:
        e = "ENVMAP_BLENDING_ADD";
        break;
    }
  return e;
}
function o4e(s) {
  const e = s.envMapCubeUVHeight;
  if (e === null)
    return null;
  const t = Math.log2(e) - 2, i = 1 / e;
  return { texelWidth: 1 / (3 * Math.max(Math.pow(2, t), 7 * 16)), texelHeight: i, maxMip: t };
}
function l4e(s, e, t, i) {
  const n = s.getContext(), r = t.defines;
  let a = t.vertexShader, o = t.fragmentShader;
  const l = n4e(t), c = s4e(t), h = r4e(t), u = a4e(t), d = o4e(t), f = Zxe(t), p = Kxe(r), v = n.createProgram();
  let g, m, x = t.glslVersion ? "#version " + t.glslVersion + `
` : "";
  t.isRawShaderMaterial ? (g = [
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p
  ].filter(f1).join(`
`), g.length > 0 && (g += `
`), m = [
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p
  ].filter(f1).join(`
`), m.length > 0 && (m += `
`)) : (g = [
    iD(t),
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p,
    t.extensionClipCullDistance ? "#define USE_CLIP_DISTANCE" : "",
    t.batching ? "#define USE_BATCHING" : "",
    t.batchingColor ? "#define USE_BATCHING_COLOR" : "",
    t.instancing ? "#define USE_INSTANCING" : "",
    t.instancingColor ? "#define USE_INSTANCING_COLOR" : "",
    t.instancingMorph ? "#define USE_INSTANCING_MORPH" : "",
    t.useFog && t.fog ? "#define USE_FOG" : "",
    t.useFog && t.fogExp2 ? "#define FOG_EXP2" : "",
    t.map ? "#define USE_MAP" : "",
    t.envMap ? "#define USE_ENVMAP" : "",
    t.envMap ? "#define " + h : "",
    t.lightMap ? "#define USE_LIGHTMAP" : "",
    t.aoMap ? "#define USE_AOMAP" : "",
    t.bumpMap ? "#define USE_BUMPMAP" : "",
    t.normalMap ? "#define USE_NORMALMAP" : "",
    t.normalMapObjectSpace ? "#define USE_NORMALMAP_OBJECTSPACE" : "",
    t.normalMapTangentSpace ? "#define USE_NORMALMAP_TANGENTSPACE" : "",
    t.displacementMap ? "#define USE_DISPLACEMENTMAP" : "",
    t.emissiveMap ? "#define USE_EMISSIVEMAP" : "",
    t.anisotropy ? "#define USE_ANISOTROPY" : "",
    t.anisotropyMap ? "#define USE_ANISOTROPYMAP" : "",
    t.clearcoatMap ? "#define USE_CLEARCOATMAP" : "",
    t.clearcoatRoughnessMap ? "#define USE_CLEARCOAT_ROUGHNESSMAP" : "",
    t.clearcoatNormalMap ? "#define USE_CLEARCOAT_NORMALMAP" : "",
    t.iridescenceMap ? "#define USE_IRIDESCENCEMAP" : "",
    t.iridescenceThicknessMap ? "#define USE_IRIDESCENCE_THICKNESSMAP" : "",
    t.specularMap ? "#define USE_SPECULARMAP" : "",
    t.specularColorMap ? "#define USE_SPECULAR_COLORMAP" : "",
    t.specularIntensityMap ? "#define USE_SPECULAR_INTENSITYMAP" : "",
    t.roughnessMap ? "#define USE_ROUGHNESSMAP" : "",
    t.metalnessMap ? "#define USE_METALNESSMAP" : "",
    t.alphaMap ? "#define USE_ALPHAMAP" : "",
    t.alphaHash ? "#define USE_ALPHAHASH" : "",
    t.transmission ? "#define USE_TRANSMISSION" : "",
    t.transmissionMap ? "#define USE_TRANSMISSIONMAP" : "",
    t.thicknessMap ? "#define USE_THICKNESSMAP" : "",
    t.sheenColorMap ? "#define USE_SHEEN_COLORMAP" : "",
    t.sheenRoughnessMap ? "#define USE_SHEEN_ROUGHNESSMAP" : "",
    //
    t.mapUv ? "#define MAP_UV " + t.mapUv : "",
    t.alphaMapUv ? "#define ALPHAMAP_UV " + t.alphaMapUv : "",
    t.lightMapUv ? "#define LIGHTMAP_UV " + t.lightMapUv : "",
    t.aoMapUv ? "#define AOMAP_UV " + t.aoMapUv : "",
    t.emissiveMapUv ? "#define EMISSIVEMAP_UV " + t.emissiveMapUv : "",
    t.bumpMapUv ? "#define BUMPMAP_UV " + t.bumpMapUv : "",
    t.normalMapUv ? "#define NORMALMAP_UV " + t.normalMapUv : "",
    t.displacementMapUv ? "#define DISPLACEMENTMAP_UV " + t.displacementMapUv : "",
    t.metalnessMapUv ? "#define METALNESSMAP_UV " + t.metalnessMapUv : "",
    t.roughnessMapUv ? "#define ROUGHNESSMAP_UV " + t.roughnessMapUv : "",
    t.anisotropyMapUv ? "#define ANISOTROPYMAP_UV " + t.anisotropyMapUv : "",
    t.clearcoatMapUv ? "#define CLEARCOATMAP_UV " + t.clearcoatMapUv : "",
    t.clearcoatNormalMapUv ? "#define CLEARCOAT_NORMALMAP_UV " + t.clearcoatNormalMapUv : "",
    t.clearcoatRoughnessMapUv ? "#define CLEARCOAT_ROUGHNESSMAP_UV " + t.clearcoatRoughnessMapUv : "",
    t.iridescenceMapUv ? "#define IRIDESCENCEMAP_UV " + t.iridescenceMapUv : "",
    t.iridescenceThicknessMapUv ? "#define IRIDESCENCE_THICKNESSMAP_UV " + t.iridescenceThicknessMapUv : "",
    t.sheenColorMapUv ? "#define SHEEN_COLORMAP_UV " + t.sheenColorMapUv : "",
    t.sheenRoughnessMapUv ? "#define SHEEN_ROUGHNESSMAP_UV " + t.sheenRoughnessMapUv : "",
    t.specularMapUv ? "#define SPECULARMAP_UV " + t.specularMapUv : "",
    t.specularColorMapUv ? "#define SPECULAR_COLORMAP_UV " + t.specularColorMapUv : "",
    t.specularIntensityMapUv ? "#define SPECULAR_INTENSITYMAP_UV " + t.specularIntensityMapUv : "",
    t.transmissionMapUv ? "#define TRANSMISSIONMAP_UV " + t.transmissionMapUv : "",
    t.thicknessMapUv ? "#define THICKNESSMAP_UV " + t.thicknessMapUv : "",
    //
    t.vertexTangents && t.flatShading === !1 ? "#define USE_TANGENT" : "",
    t.vertexColors ? "#define USE_COLOR" : "",
    t.vertexAlphas ? "#define USE_COLOR_ALPHA" : "",
    t.vertexUv1s ? "#define USE_UV1" : "",
    t.vertexUv2s ? "#define USE_UV2" : "",
    t.vertexUv3s ? "#define USE_UV3" : "",
    t.pointsUvs ? "#define USE_POINTS_UV" : "",
    t.flatShading ? "#define FLAT_SHADED" : "",
    t.skinning ? "#define USE_SKINNING" : "",
    t.morphTargets ? "#define USE_MORPHTARGETS" : "",
    t.morphNormals && t.flatShading === !1 ? "#define USE_MORPHNORMALS" : "",
    t.morphColors ? "#define USE_MORPHCOLORS" : "",
    t.morphTargetsCount > 0 ? "#define MORPHTARGETS_TEXTURE_STRIDE " + t.morphTextureStride : "",
    t.morphTargetsCount > 0 ? "#define MORPHTARGETS_COUNT " + t.morphTargetsCount : "",
    t.doubleSided ? "#define DOUBLE_SIDED" : "",
    t.flipSided ? "#define FLIP_SIDED" : "",
    t.shadowMapEnabled ? "#define USE_SHADOWMAP" : "",
    t.shadowMapEnabled ? "#define " + l : "",
    t.sizeAttenuation ? "#define USE_SIZEATTENUATION" : "",
    t.numLightProbes > 0 ? "#define USE_LIGHT_PROBES" : "",
    t.logarithmicDepthBuffer ? "#define USE_LOGDEPTHBUF" : "",
    "uniform mat4 modelMatrix;",
    "uniform mat4 modelViewMatrix;",
    "uniform mat4 projectionMatrix;",
    "uniform mat4 viewMatrix;",
    "uniform mat3 normalMatrix;",
    "uniform vec3 cameraPosition;",
    "uniform bool isOrthographic;",
    "#ifdef USE_INSTANCING",
    "	attribute mat4 instanceMatrix;",
    "#endif",
    "#ifdef USE_INSTANCING_COLOR",
    "	attribute vec3 instanceColor;",
    "#endif",
    "#ifdef USE_INSTANCING_MORPH",
    "	uniform sampler2D morphTexture;",
    "#endif",
    "attribute vec3 position;",
    "attribute vec3 normal;",
    "attribute vec2 uv;",
    "#ifdef USE_UV1",
    "	attribute vec2 uv1;",
    "#endif",
    "#ifdef USE_UV2",
    "	attribute vec2 uv2;",
    "#endif",
    "#ifdef USE_UV3",
    "	attribute vec2 uv3;",
    "#endif",
    "#ifdef USE_TANGENT",
    "	attribute vec4 tangent;",
    "#endif",
    "#if defined( USE_COLOR_ALPHA )",
    "	attribute vec4 color;",
    "#elif defined( USE_COLOR )",
    "	attribute vec3 color;",
    "#endif",
    "#ifdef USE_SKINNING",
    "	attribute vec4 skinIndex;",
    "	attribute vec4 skinWeight;",
    "#endif",
    `
`
  ].filter(f1).join(`
`), m = [
    iD(t),
    "#define SHADER_TYPE " + t.shaderType,
    "#define SHADER_NAME " + t.shaderName,
    p,
    t.useFog && t.fog ? "#define USE_FOG" : "",
    t.useFog && t.fogExp2 ? "#define FOG_EXP2" : "",
    t.alphaToCoverage ? "#define ALPHA_TO_COVERAGE" : "",
    t.map ? "#define USE_MAP" : "",
    t.matcap ? "#define USE_MATCAP" : "",
    t.envMap ? "#define USE_ENVMAP" : "",
    t.envMap ? "#define " + c : "",
    t.envMap ? "#define " + h : "",
    t.envMap ? "#define " + u : "",
    d ? "#define CUBEUV_TEXEL_WIDTH " + d.texelWidth : "",
    d ? "#define CUBEUV_TEXEL_HEIGHT " + d.texelHeight : "",
    d ? "#define CUBEUV_MAX_MIP " + d.maxMip + ".0" : "",
    t.lightMap ? "#define USE_LIGHTMAP" : "",
    t.aoMap ? "#define USE_AOMAP" : "",
    t.bumpMap ? "#define USE_BUMPMAP" : "",
    t.normalMap ? "#define USE_NORMALMAP" : "",
    t.normalMapObjectSpace ? "#define USE_NORMALMAP_OBJECTSPACE" : "",
    t.normalMapTangentSpace ? "#define USE_NORMALMAP_TANGENTSPACE" : "",
    t.emissiveMap ? "#define USE_EMISSIVEMAP" : "",
    t.anisotropy ? "#define USE_ANISOTROPY" : "",
    t.anisotropyMap ? "#define USE_ANISOTROPYMAP" : "",
    t.clearcoat ? "#define USE_CLEARCOAT" : "",
    t.clearcoatMap ? "#define USE_CLEARCOATMAP" : "",
    t.clearcoatRoughnessMap ? "#define USE_CLEARCOAT_ROUGHNESSMAP" : "",
    t.clearcoatNormalMap ? "#define USE_CLEARCOAT_NORMALMAP" : "",
    t.dispersion ? "#define USE_DISPERSION" : "",
    t.iridescence ? "#define USE_IRIDESCENCE" : "",
    t.iridescenceMap ? "#define USE_IRIDESCENCEMAP" : "",
    t.iridescenceThicknessMap ? "#define USE_IRIDESCENCE_THICKNESSMAP" : "",
    t.specularMap ? "#define USE_SPECULARMAP" : "",
    t.specularColorMap ? "#define USE_SPECULAR_COLORMAP" : "",
    t.specularIntensityMap ? "#define USE_SPECULAR_INTENSITYMAP" : "",
    t.roughnessMap ? "#define USE_ROUGHNESSMAP" : "",
    t.metalnessMap ? "#define USE_METALNESSMAP" : "",
    t.alphaMap ? "#define USE_ALPHAMAP" : "",
    t.alphaTest ? "#define USE_ALPHATEST" : "",
    t.alphaHash ? "#define USE_ALPHAHASH" : "",
    t.sheen ? "#define USE_SHEEN" : "",
    t.sheenColorMap ? "#define USE_SHEEN_COLORMAP" : "",
    t.sheenRoughnessMap ? "#define USE_SHEEN_ROUGHNESSMAP" : "",
    t.transmission ? "#define USE_TRANSMISSION" : "",
    t.transmissionMap ? "#define USE_TRANSMISSIONMAP" : "",
    t.thicknessMap ? "#define USE_THICKNESSMAP" : "",
    t.vertexTangents && t.flatShading === !1 ? "#define USE_TANGENT" : "",
    t.vertexColors || t.instancingColor || t.batchingColor ? "#define USE_COLOR" : "",
    t.vertexAlphas ? "#define USE_COLOR_ALPHA" : "",
    t.vertexUv1s ? "#define USE_UV1" : "",
    t.vertexUv2s ? "#define USE_UV2" : "",
    t.vertexUv3s ? "#define USE_UV3" : "",
    t.pointsUvs ? "#define USE_POINTS_UV" : "",
    t.gradientMap ? "#define USE_GRADIENTMAP" : "",
    t.flatShading ? "#define FLAT_SHADED" : "",
    t.doubleSided ? "#define DOUBLE_SIDED" : "",
    t.flipSided ? "#define FLIP_SIDED" : "",
    t.shadowMapEnabled ? "#define USE_SHADOWMAP" : "",
    t.shadowMapEnabled ? "#define " + l : "",
    t.premultipliedAlpha ? "#define PREMULTIPLIED_ALPHA" : "",
    t.numLightProbes > 0 ? "#define USE_LIGHT_PROBES" : "",
    t.decodeVideoTexture ? "#define DECODE_VIDEO_TEXTURE" : "",
    t.logarithmicDepthBuffer ? "#define USE_LOGDEPTHBUF" : "",
    "uniform mat4 viewMatrix;",
    "uniform vec3 cameraPosition;",
    "uniform bool isOrthographic;",
    t.toneMapping !== Bc ? "#define TONE_MAPPING" : "",
    t.toneMapping !== Bc ? yi.tonemapping_pars_fragment : "",
    // this code is required here because it is used by the toneMapping() function defined below
    t.toneMapping !== Bc ? Yxe("toneMapping", t.toneMapping) : "",
    t.dithering ? "#define DITHERING" : "",
    t.opaque ? "#define OPAQUE" : "",
    yi.colorspace_pars_fragment,
    // this code is required here because it is used by the various encoding/decoding function defined below
    qxe("linearToOutputTexel", t.outputColorSpace),
    t.useDepthPacking ? "#define DEPTH_PACKING " + t.depthPacking : "",
    `
`
  ].filter(f1).join(`
`)), t.isRawShaderMaterial !== !0 && (a = s.frameState.projectCameraMgr.addCodeToVertexShader(a), o = s.frameState.projectCameraMgr.addCodeToFragmentShader(o)), a = $7(a), a = $R(a, t), a = eD(a, t), o = $7(o), o = $R(o, t), o = eD(o, t), a = tD(a), o = tD(o), t.isRawShaderMaterial !== !0 && (x = `#version 300 es
`, g = [
    f,
    "#define attribute in",
    "#define varying out",
    "#define texture2D texture"
  ].join(`
`) + `
` + g, m = [
    "#define varying in",
    t.glslVersion === vR ? "" : "layout(location = 0) out highp vec4 pc_fragColor;",
    t.glslVersion === vR ? "" : "#define gl_FragColor pc_fragColor",
    "#define gl_FragDepthEXT gl_FragDepth",
    "#define texture2D texture",
    "#define textureCube texture",
    "#define texture2DProj textureProj",
    "#define texture2DLodEXT textureLod",
    "#define texture2DProjLodEXT textureProjLod",
    "#define textureCubeLodEXT textureLod",
    "#define texture2DGradEXT textureGrad",
    "#define texture2DProjGradEXT textureProjGrad",
    "#define textureCubeGradEXT textureGrad"
  ].join(`
`) + `
` + m);
  const y = x + g + a, A = x + m + o, b = JR(n, n.VERTEX_SHADER, y), M = JR(n, n.FRAGMENT_SHADER, A);
  n.attachShader(v, b), n.attachShader(v, M), t.index0AttributeName !== void 0 ? n.bindAttribLocation(v, 0, t.index0AttributeName) : t.morphTargets === !0 && n.bindAttribLocation(v, 0, "position"), n.linkProgram(v);
  function w(P) {
    if (s.debug.checkShaderErrors) {
      const L = n.getProgramInfoLog(v).trim(), I = n.getShaderInfoLog(b).trim(), N = n.getShaderInfoLog(M).trim();
      let H = !0, U = !0;
      if (n.getProgramParameter(v, n.LINK_STATUS) === !1)
        if (H = !1, typeof s.debug.onShaderError == "function")
          s.debug.onShaderError(n, v, b, M);
        else {
          const W = QR(n, b, "vertex"), X = QR(n, M, "fragment");
          console.error(
            "THREE.WebGLProgram: Shader Error " + n.getError() + " - VALIDATE_STATUS " + n.getProgramParameter(v, n.VALIDATE_STATUS) + `

Material Name: ` + P.name + `
Material Type: ` + P.type + `

Program Info Log: ` + L + `
` + W + `
` + X
          );
        }
      else
        L !== "" ? console.warn("THREE.WebGLProgram: Program Info Log:", L) : (I === "" || N === "") && (U = !1);
      U && (P.diagnostics = {
        runnable: H,
        programLog: L,
        vertexShader: {
          log: I,
          prefix: g
        },
        fragmentShader: {
          log: N,
          prefix: m
        }
      });
    }
    n.deleteShader(b), n.deleteShader(M), C = new i9(n, v), T = Jxe(n, v);
  }
  let C;
  this.getUniforms = function() {
    return C === void 0 && w(this), C;
  };
  let T;
  this.getAttributes = function() {
    return T === void 0 && w(this), T;
  };
  let E = t.rendererExtensionParallelShaderCompile === !1;
  return this.isReady = function() {
    return E === !1 && (E = n.getProgramParameter(v, Vxe)), E;
  }, this.destroy = function() {
    i.releaseStatesOfProgram(this), n.deleteProgram(v), this.program = void 0;
  }, this.type = t.shaderType, this.name = t.shaderName, this.id = Wxe++, this.cacheKey = e, this.usedTimes = 1, this.program = v, this.vertexShader = b, this.fragmentShader = M, this;
}
let c4e = 0;
class h4e {
  constructor() {
    this.shaderCache = /* @__PURE__ */ new Map(), this.materialCache = /* @__PURE__ */ new Map();
  }
  update(e) {
    const t = e.vertexShader, i = e.fragmentShader, n = this._getShaderStage(t), r = this._getShaderStage(i), a = this._getShaderCacheForMaterial(e);
    return a.has(n) === !1 && (a.add(n), n.usedTimes++), a.has(r) === !1 && (a.add(r), r.usedTimes++), this;
  }
  remove(e) {
    const t = this.materialCache.get(e);
    for (const i of t)
      i.usedTimes--, i.usedTimes === 0 && this.shaderCache.delete(i.code);
    return this.materialCache.delete(e), this;
  }
  getVertexShaderID(e) {
    return this._getShaderStage(e.vertexShader).id;
  }
  getFragmentShaderID(e) {
    return this._getShaderStage(e.fragmentShader).id;
  }
  dispose() {
    this.shaderCache.clear(), this.materialCache.clear();
  }
  _getShaderCacheForMaterial(e) {
    const t = this.materialCache;
    let i = t.get(e);
    return i === void 0 && (i = /* @__PURE__ */ new Set(), t.set(e, i)), i;
  }
  _getShaderStage(e) {
    const t = this.shaderCache;
    let i = t.get(e);
    return i === void 0 && (i = new u4e(e), t.set(e, i)), i;
  }
}
class u4e {
  constructor(e) {
    this.id = c4e++, this.code = e, this.usedTimes = 0;
  }
}
function d4e(s, e, t, i, n, r, a) {
  const o = new uU(), l = new h4e(), c = /* @__PURE__ */ new Set(), h = [], u = n.logarithmicDepthBuffer, d = n.vertexTextures;
  let f = n.precision;
  const p = {
    MeshDepthMaterial: "depth",
    MeshDistanceMaterial: "distanceRGBA",
    MeshNormalMaterial: "normal",
    MeshBasicMaterial: "basic",
    MeshLambertMaterial: "lambert",
    MeshPhongMaterial: "phong",
    MeshToonMaterial: "toon",
    MeshStandardMaterial: "physical",
    MeshPhysicalMaterial: "physical",
    MeshMatcapMaterial: "matcap",
    LineBasicMaterial: "basic",
    LineDashedMaterial: "dashed",
    PointsMaterial: "points",
    ShadowMaterial: "shadow",
    SpriteMaterial: "sprite"
  };
  function v(T) {
    return c.add(T), T === 0 ? "uv" : `uv${T}`;
  }
  function g(T, E, P, L, I) {
    const N = L.fog, H = I.geometry, U = T.isMeshStandardMaterial ? L.environment : null, W = (T.isMeshStandardMaterial ? t : e).get(
      T.envMap || U
    ), X = W && W.mapping === d8 ? W.image.height : null, K = p[T.type];
    T.precision !== null && (f = n.getMaxPrecision(T.precision), f !== T.precision && console.warn(
      "THREE.WebGLProgram.getParameters:",
      T.precision,
      "not supported, using",
      f,
      "instead."
    ));
    const te = H.morphAttributes.position || H.morphAttributes.normal || H.morphAttributes.color, re = te !== void 0 ? te.length : 0;
    let be = 0;
    H.morphAttributes.position !== void 0 && (be = 1), H.morphAttributes.normal !== void 0 && (be = 2), H.morphAttributes.color !== void 0 && (be = 3);
    let Pe, J, fe, Me;
    if (K) {
      const Ot = Fo[K];
      Pe = Ot.vertexShader, J = Ot.fragmentShader;
    } else
      Pe = T.vertexShader, J = T.fragmentShader, l.update(T), fe = l.getVertexShaderID(T), Me = l.getFragmentShaderID(T);
    const ye = s.getRenderTarget(), Ye = I.isInstancedMesh === !0, Je = I.isBatchedMesh === !0, rt = !!T.map, wt = !!T.matcap, k = !!W, Fe = !!T.aoMap, Ge = !!T.lightMap, qe = !!T.bumpMap, Ie = !!T.normalMap, vt = !!T.displacementMap, Ve = !!T.emissiveMap, Ze = !!T.metalnessMap, G = !!T.roughnessMap, D = T.anisotropy > 0, ne = T.clearcoat > 0, ve = T.dispersion > 0, Y = T.iridescence > 0, ee = T.sheen > 0, we = T.transmission > 0, de = D && !!T.anisotropyMap, xe = ne && !!T.clearcoatMap, it = ne && !!T.clearcoatNormalMap, q = ne && !!T.clearcoatRoughnessMap, ae = Y && !!T.iridescenceMap, Ee = Y && !!T.iridescenceThicknessMap, Le = ee && !!T.sheenColorMap, Ae = ee && !!T.sheenRoughnessMap, We = !!T.specularMap, ot = !!T.specularColorMap, Ct = !!T.specularIntensityMap, F = we && !!T.transmissionMap, ue = we && !!T.thicknessMap, Q = !!T.gradientMap, he = !!T.alphaMap, Be = T.alphaTest > 0, ft = !!T.alphaHash, Et = !!T.extensions;
    let ei = Bc;
    T.toneMapped && (ye === null || ye.isXRRenderTarget === !0) && (ei = s.toneMapping);
    const di = {
      shaderID: K,
      shaderType: T.type,
      shaderName: T.name,
      vertexShader: Pe,
      fragmentShader: J,
      defines: T.defines,
      customVertexShaderID: fe,
      customFragmentShaderID: Me,
      isRawShaderMaterial: T.isRawShaderMaterial === !0,
      glslVersion: T.glslVersion,
      precision: f,
      batching: Je,
      batchingColor: Je && I._colorsTexture !== null,
      instancing: Ye,
      instancingColor: Ye && I.instanceColor !== null,
      instancingMorph: Ye && I.morphTexture !== null,
      supportsVertexTextures: d,
      outputColorSpace: ye === null ? s.outputColorSpace : ye.isXRRenderTarget === !0 ? ye.texture.colorSpace : hh,
      alphaToCoverage: !!T.alphaToCoverage,
      map: rt,
      matcap: wt,
      envMap: k,
      envMapMode: k && W.mapping,
      envMapCubeUVHeight: X,
      aoMap: Fe,
      lightMap: Ge,
      bumpMap: qe,
      normalMap: Ie,
      displacementMap: d && vt,
      emissiveMap: Ve,
      normalMapObjectSpace: Ie && T.normalMapType === Mve,
      normalMapTangentSpace: Ie && T.normalMapType === bve,
      metalnessMap: Ze,
      roughnessMap: G,
      anisotropy: D,
      anisotropyMap: de,
      clearcoat: ne,
      clearcoatMap: xe,
      clearcoatNormalMap: it,
      clearcoatRoughnessMap: q,
      dispersion: ve,
      iridescence: Y,
      iridescenceMap: ae,
      iridescenceThicknessMap: Ee,
      sheen: ee,
      sheenColorMap: Le,
      sheenRoughnessMap: Ae,
      specularMap: We,
      specularColorMap: ot,
      specularIntensityMap: Ct,
      transmission: we,
      transmissionMap: F,
      thicknessMap: ue,
      gradientMap: Q,
      opaque: T.transparent === !1 && T.blending === Jf && T.alphaToCoverage === !1,
      alphaMap: he,
      alphaTest: Be,
      alphaHash: ft,
      combine: T.combine,
      //
      mapUv: rt && v(T.map.channel),
      aoMapUv: Fe && v(T.aoMap.channel),
      lightMapUv: Ge && v(T.lightMap.channel),
      bumpMapUv: qe && v(T.bumpMap.channel),
      normalMapUv: Ie && v(T.normalMap.channel),
      displacementMapUv: vt && v(T.displacementMap.channel),
      emissiveMapUv: Ve && v(T.emissiveMap.channel),
      metalnessMapUv: Ze && v(T.metalnessMap.channel),
      roughnessMapUv: G && v(T.roughnessMap.channel),
      anisotropyMapUv: de && v(T.anisotropyMap.channel),
      clearcoatMapUv: xe && v(T.clearcoatMap.channel),
      clearcoatNormalMapUv: it && v(T.clearcoatNormalMap.channel),
      clearcoatRoughnessMapUv: q && v(T.clearcoatRoughnessMap.channel),
      iridescenceMapUv: ae && v(T.iridescenceMap.channel),
      iridescenceThicknessMapUv: Ee && v(T.iridescenceThicknessMap.channel),
      sheenColorMapUv: Le && v(T.sheenColorMap.channel),
      sheenRoughnessMapUv: Ae && v(T.sheenRoughnessMap.channel),
      specularMapUv: We && v(T.specularMap.channel),
      specularColorMapUv: ot && v(T.specularColorMap.channel),
      specularIntensityMapUv: Ct && v(T.specularIntensityMap.channel),
      transmissionMapUv: F && v(T.transmissionMap.channel),
      thicknessMapUv: ue && v(T.thicknessMap.channel),
      alphaMapUv: he && v(T.alphaMap.channel),
      //
      vertexTangents: !!H.attributes.tangent && (Ie || D),
      vertexColors: T.vertexColors,
      vertexAlphas: T.vertexColors === !0 && !!H.attributes.color && H.attributes.color.itemSize === 4,
      pointsUvs: I.isPoints === !0 && !!H.attributes.uv && (rt || he),
      fog: !!N,
      useFog: T.fog === !0,
      fogExp2: !!N && N.isFogExp2,
      flatShading: T.flatShading === !0,
      sizeAttenuation: T.sizeAttenuation === !0,
      logarithmicDepthBuffer: u,
      skinning: I.isSkinnedMesh === !0,
      morphTargets: H.morphAttributes.position !== void 0,
      morphNormals: H.morphAttributes.normal !== void 0,
      morphColors: H.morphAttributes.color !== void 0,
      morphTargetsCount: re,
      morphTextureStride: be,
      numDirLights: E.directional.length,
      numPointLights: E.point.length,
      numSpotLights: E.spot.length,
      numSpotLightMaps: E.spotLightMap.length,
      numRectAreaLights: E.rectArea.length,
      numHemiLights: E.hemi.length,
      numDirLightShadows: E.directionalShadowMap.length,
      numPointLightShadows: E.pointShadowMap.length,
      numSpotLightShadows: E.spotShadowMap.length,
      numSpotLightShadowsWithMaps: E.numSpotLightShadowsWithMaps,
      numLightProbes: E.numLightProbes,
      numClippingPlanes: a.numPlanes,
      numClipIntersection: a.numIntersection,
      dithering: T.dithering,
      shadowMapEnabled: s.shadowMap.enabled && P.length > 0,
      shadowMapType: s.shadowMap.type,
      toneMapping: ei,
      decodeVideoTexture: rt && T.map.isVideoTexture === !0 && rn.getTransfer(T.map.colorSpace) === Sn,
      premultipliedAlpha: T.premultipliedAlpha,
      doubleSided: T.side === Q0,
      flipSided: T.side === Fr,
      useDepthPacking: T.depthPacking >= 0,
      depthPacking: T.depthPacking || 0,
      index0AttributeName: T.index0AttributeName,
      extensionClipCullDistance: Et && T.extensions.clipCullDistance === !0 && i.has("WEBGL_clip_cull_distance"),
      extensionMultiDraw: (Et && T.extensions.multiDraw === !0 || Je) && i.has("WEBGL_multi_draw"),
      rendererExtensionParallelShaderCompile: i.has("KHR_parallel_shader_compile"),
      customProgramCacheKey: T.customProgramCacheKey()
    };
    return di.vertexUv1s = c.has(1), di.vertexUv2s = c.has(2), di.vertexUv3s = c.has(3), c.clear(), di;
  }
  function m(T) {
    const E = [];
    if (T.shaderID ? E.push(T.shaderID) : (E.push(T.customVertexShaderID), E.push(T.customFragmentShaderID)), T.defines !== void 0)
      for (const P in T.defines)
        E.push(P), E.push(T.defines[P]);
    return T.isRawShaderMaterial === !1 && (x(E, T), y(E, T), E.push(s.outputColorSpace)), E.push(T.customProgramCacheKey), E.join();
  }
  function x(T, E) {
    T.push(E.precision), T.push(E.outputColorSpace), T.push(E.envMapMode), T.push(E.envMapCubeUVHeight), T.push(E.mapUv), T.push(E.alphaMapUv), T.push(E.lightMapUv), T.push(E.aoMapUv), T.push(E.bumpMapUv), T.push(E.normalMapUv), T.push(E.displacementMapUv), T.push(E.emissiveMapUv), T.push(E.metalnessMapUv), T.push(E.roughnessMapUv), T.push(E.anisotropyMapUv), T.push(E.clearcoatMapUv), T.push(E.clearcoatNormalMapUv), T.push(E.clearcoatRoughnessMapUv), T.push(E.iridescenceMapUv), T.push(E.iridescenceThicknessMapUv), T.push(E.sheenColorMapUv), T.push(E.sheenRoughnessMapUv), T.push(E.specularMapUv), T.push(E.specularColorMapUv), T.push(E.specularIntensityMapUv), T.push(E.transmissionMapUv), T.push(E.thicknessMapUv), T.push(E.combine), T.push(E.fogExp2), T.push(E.sizeAttenuation), T.push(E.morphTargetsCount), T.push(E.morphAttributeCount), T.push(E.numDirLights), T.push(E.numPointLights), T.push(E.numSpotLights), T.push(E.numSpotLightMaps), T.push(E.numHemiLights), T.push(E.numRectAreaLights), T.push(E.numDirLightShadows), T.push(E.numPointLightShadows), T.push(E.numSpotLightShadows), T.push(E.numSpotLightShadowsWithMaps), T.push(E.numLightProbes), T.push(E.shadowMapType), T.push(E.toneMapping), T.push(E.numClippingPlanes), T.push(E.numClipIntersection), T.push(E.depthPacking);
  }
  function y(T, E) {
    o.disableAll(), E.supportsVertexTextures && o.enable(0), E.instancing && o.enable(1), E.instancingColor && o.enable(2), E.instancingMorph && o.enable(3), E.matcap && o.enable(4), E.envMap && o.enable(5), E.normalMapObjectSpace && o.enable(6), E.normalMapTangentSpace && o.enable(7), E.clearcoat && o.enable(8), E.iridescence && o.enable(9), E.alphaTest && o.enable(10), E.vertexColors && o.enable(11), E.vertexAlphas && o.enable(12), E.vertexUv1s && o.enable(13), E.vertexUv2s && o.enable(14), E.vertexUv3s && o.enable(15), E.vertexTangents && o.enable(16), E.anisotropy && o.enable(17), E.alphaHash && o.enable(18), E.batching && o.enable(19), E.dispersion && o.enable(20), E.batchingColor && o.enable(21), T.push(o.mask), o.disableAll(), E.fog && o.enable(0), E.useFog && o.enable(1), E.flatShading && o.enable(2), E.logarithmicDepthBuffer && o.enable(3), E.skinning && o.enable(4), E.morphTargets && o.enable(5), E.morphNormals && o.enable(6), E.morphColors && o.enable(7), E.premultipliedAlpha && o.enable(8), E.shadowMapEnabled && o.enable(9), E.doubleSided && o.enable(10), E.flipSided && o.enable(11), E.useDepthPacking && o.enable(12), E.dithering && o.enable(13), E.transmission && o.enable(14), E.sheen && o.enable(15), E.opaque && o.enable(16), E.pointsUvs && o.enable(17), E.decodeVideoTexture && o.enable(18), E.alphaToCoverage && o.enable(19), T.push(o.mask);
  }
  function A(T) {
    const E = p[T.type];
    let P;
    if (E) {
      const L = Fo[E];
      P = Xve.clone(L.uniforms);
    } else
      P = T.uniforms;
    return P;
  }
  function b(T, E) {
    let P;
    for (let L = 0, I = h.length; L < I; L++) {
      const N = h[L];
      if (N.cacheKey === E) {
        P = N, ++P.usedTimes;
        break;
      }
    }
    return P === void 0 && (P = new l4e(s, E, T, r), h.push(P)), P;
  }
  function M(T) {
    if (--T.usedTimes === 0) {
      const E = h.indexOf(T);
      h[E] = h[h.length - 1], h.pop(), T.destroy();
    }
  }
  function w(T) {
    l.remove(T);
  }
  function C() {
    l.dispose();
  }
  return {
    getParameters: g,
    getProgramCacheKey: m,
    getUniforms: A,
    acquireProgram: b,
    releaseProgram: M,
    releaseShaderCache: w,
    // Exposed for resource monitoring & error feedback via renderer.info:
    programs: h,
    dispose: C
  };
}
function f4e() {
  let s = /* @__PURE__ */ new WeakMap();
  function e(r) {
    let a = s.get(r);
    return a === void 0 && (a = {}, s.set(r, a)), a;
  }
  function t(r) {
    s.delete(r);
  }
  function i(r, a, o) {
    s.get(r)[a] = o;
  }
  function n() {
    s = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: e,
    remove: t,
    update: i,
    dispose: n
  };
}
function p4e(s, e) {
  return s.groupOrder !== e.groupOrder ? s.groupOrder - e.groupOrder : s.renderOrder !== e.renderOrder ? s.renderOrder - e.renderOrder : s.material.id !== e.material.id ? s.material.id - e.material.id : s.z !== e.z ? s.z - e.z : s.id - e.id;
}
function nD(s, e) {
  return s.groupOrder !== e.groupOrder ? s.groupOrder - e.groupOrder : s.renderOrder !== e.renderOrder ? s.renderOrder - e.renderOrder : s.z !== e.z ? e.z - s.z : s.id - e.id;
}
function sD() {
  const s = [];
  let e = 0;
  const t = [], i = [], n = [];
  function r() {
    e = 0, t.length = 0, i.length = 0, n.length = 0;
  }
  function a(u, d, f, p, v, g) {
    let m = s[e];
    return m === void 0 ? (m = {
      id: u.id,
      object: u,
      geometry: d,
      material: f,
      groupOrder: p,
      renderOrder: u.renderOrder,
      z: v,
      group: g
    }, s[e] = m) : (m.id = u.id, m.object = u, m.geometry = d, m.material = f, m.groupOrder = p, m.renderOrder = u.renderOrder, m.z = v, m.group = g), e++, m;
  }
  function o(u, d, f, p, v, g) {
    const m = a(u, d, f, p, v, g);
    f.transmission > 0 ? i.push(m) : f.transparent === !0 ? n.push(m) : t.push(m);
  }
  function l(u, d, f, p, v, g) {
    const m = a(u, d, f, p, v, g);
    f.transmission > 0 ? i.unshift(m) : f.transparent === !0 ? n.unshift(m) : t.unshift(m);
  }
  function c(u, d) {
    t.length > 1 && t.sort(u || p4e), i.length > 1 && i.sort(d || nD), n.length > 1 && n.sort(d || nD);
  }
  function h() {
    for (let u = e, d = s.length; u < d; u++) {
      const f = s[u];
      if (f.id === null)
        break;
      f.id = null, f.object = null, f.geometry = null, f.material = null, f.group = null;
    }
  }
  return {
    opaque: t,
    transmissive: i,
    transparent: n,
    init: r,
    push: o,
    unshift: l,
    finish: h,
    sort: c
  };
}
function m4e() {
  let s = /* @__PURE__ */ new WeakMap();
  function e(i, n) {
    const r = s.get(i);
    let a;
    return r === void 0 ? (a = new sD(), s.set(i, [a])) : n >= r.length ? (a = new sD(), r.push(a)) : a = r[n], a;
  }
  function t() {
    s = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: e,
    dispose: t
  };
}
function g4e() {
  const s = {};
  return {
    get: function(e) {
      if (s[e.id] !== void 0)
        return s[e.id];
      let t;
      switch (e.type) {
        case "DirectionalLight":
          t = {
            direction: new je(),
            color: new hn()
          };
          break;
        case "SpotLight":
          t = {
            position: new je(),
            direction: new je(),
            color: new hn(),
            distance: 0,
            coneCos: 0,
            penumbraCos: 0,
            decay: 0
          };
          break;
        case "PointLight":
          t = {
            position: new je(),
            color: new hn(),
            distance: 0,
            decay: 0
          };
          break;
        case "HemisphereLight":
          t = {
            direction: new je(),
            skyColor: new hn(),
            groundColor: new hn()
          };
          break;
        case "RectAreaLight":
          t = {
            color: new hn(),
            position: new je(),
            halfWidth: new je(),
            halfHeight: new je()
          };
          break;
      }
      return s[e.id] = t, t;
    }
  };
}
function v4e() {
  const s = {};
  return {
    get: function(e) {
      if (s[e.id] !== void 0)
        return s[e.id];
      let t;
      switch (e.type) {
        case "DirectionalLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new tn()
          };
          break;
        case "SpotLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new tn()
          };
          break;
        case "PointLight":
          t = {
            shadowIntensity: 1,
            shadowBias: 0,
            shadowNormalBias: 0,
            shadowRadius: 1,
            shadowMapSize: new tn(),
            shadowCameraNear: 1,
            shadowCameraFar: 1e3
          };
          break;
      }
      return s[e.id] = t, t;
    }
  };
}
let y4e = 0;
function x4e(s, e) {
  return (e.castShadow ? 2 : 0) - (s.castShadow ? 2 : 0) + (e.map ? 1 : 0) - (s.map ? 1 : 0);
}
function A4e(s) {
  const e = new g4e(), t = v4e(), i = {
    version: 0,
    hash: {
      directionalLength: -1,
      pointLength: -1,
      spotLength: -1,
      rectAreaLength: -1,
      hemiLength: -1,
      numDirectionalShadows: -1,
      numPointShadows: -1,
      numSpotShadows: -1,
      numSpotMaps: -1,
      numLightProbes: -1
    },
    ambient: [0, 0, 0],
    probe: [],
    directional: [],
    directionalShadow: [],
    directionalShadowMap: [],
    directionalShadowMatrix: [],
    spot: [],
    spotLightMap: [],
    spotShadow: [],
    spotShadowMap: [],
    spotLightMatrix: [],
    rectArea: [],
    rectAreaLTC1: null,
    rectAreaLTC2: null,
    point: [],
    pointShadow: [],
    pointShadowMap: [],
    pointShadowMatrix: [],
    hemi: [],
    numSpotLightShadowsWithMaps: 0,
    numLightProbes: 0
  };
  for (let c = 0; c < 9; c++)
    i.probe.push(new je());
  const n = new je(), r = new ps(), a = new ps();
  function o(c) {
    let h = 0, u = 0, d = 0;
    for (let T = 0; T < 9; T++)
      i.probe[T].set(0, 0, 0);
    let f = 0, p = 0, v = 0, g = 0, m = 0, x = 0, y = 0, A = 0, b = 0, M = 0, w = 0;
    c.sort(x4e);
    for (let T = 0, E = c.length; T < E; T++) {
      const P = c[T], L = P.color, I = P.intensity, N = P.distance, H = P.shadow && P.shadow.map ? P.shadow.map.texture : null;
      if (P.isAmbientLight)
        h += L.r * I, u += L.g * I, d += L.b * I;
      else if (P.isLightProbe) {
        for (let U = 0; U < 9; U++)
          i.probe[U].addScaledVector(P.sh.coefficients[U], I);
        w++;
      } else if (P.isDirectionalLight) {
        const U = e.get(P);
        if (U.color.copy(P.color).multiplyScalar(P.intensity), P.castShadow) {
          const W = P.shadow, X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, i.directionalShadow[f] = X, i.directionalShadowMap[f] = H, i.directionalShadowMatrix[f] = P.shadow.matrix, x++;
        }
        i.directional[f] = U, f++;
      } else if (P.isSpotLight) {
        const U = e.get(P);
        U.position.setFromMatrixPosition(P.matrixWorld), U.color.copy(L).multiplyScalar(I), U.distance = N, U.coneCos = Math.cos(P.angle), U.penumbraCos = Math.cos(P.angle * (1 - P.penumbra)), U.decay = P.decay, i.spot[v] = U;
        const W = P.shadow;
        if (P.map && (i.spotLightMap[b] = P.map, b++, W.updateMatrices(P), P.castShadow && M++), i.spotLightMatrix[v] = W.matrix, P.castShadow) {
          const X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, i.spotShadow[v] = X, i.spotShadowMap[v] = H, A++;
        }
        v++;
      } else if (P.isRectAreaLight) {
        const U = e.get(P);
        U.color.copy(L).multiplyScalar(I), U.halfWidth.set(P.width * 0.5, 0, 0), U.halfHeight.set(0, P.height * 0.5, 0), i.rectArea[g] = U, g++;
      } else if (P.isPointLight) {
        const U = e.get(P);
        if (U.color.copy(P.color).multiplyScalar(P.intensity), U.distance = P.distance, U.decay = P.decay, P.castShadow) {
          const W = P.shadow, X = t.get(P);
          X.shadowIntensity = W.intensity, X.shadowBias = W.bias, X.shadowNormalBias = W.normalBias, X.shadowRadius = W.radius, X.shadowMapSize = W.mapSize, X.shadowCameraNear = W.camera.near, X.shadowCameraFar = W.camera.far, i.pointShadow[p] = X, i.pointShadowMap[p] = H, i.pointShadowMatrix[p] = P.shadow.matrix, y++;
        }
        i.point[p] = U, p++;
      } else if (P.isHemisphereLight) {
        const U = e.get(P);
        U.skyColor.copy(P.color).multiplyScalar(I), U.groundColor.copy(P.groundColor).multiplyScalar(I), i.hemi[m] = U, m++;
      }
    }
    g > 0 && (s.has("OES_texture_float_linear") === !0 ? (i.rectAreaLTC1 = Rt.LTC_FLOAT_1, i.rectAreaLTC2 = Rt.LTC_FLOAT_2) : (i.rectAreaLTC1 = Rt.LTC_HALF_1, i.rectAreaLTC2 = Rt.LTC_HALF_2)), i.ambient[0] = h, i.ambient[1] = u, i.ambient[2] = d;
    const C = i.hash;
    (C.directionalLength !== f || C.pointLength !== p || C.spotLength !== v || C.rectAreaLength !== g || C.hemiLength !== m || C.numDirectionalShadows !== x || C.numPointShadows !== y || C.numSpotShadows !== A || C.numSpotMaps !== b || C.numLightProbes !== w) && (i.directional.length = f, i.spot.length = v, i.rectArea.length = g, i.point.length = p, i.hemi.length = m, i.directionalShadow.length = x, i.directionalShadowMap.length = x, i.pointShadow.length = y, i.pointShadowMap.length = y, i.spotShadow.length = A, i.spotShadowMap.length = A, i.directionalShadowMatrix.length = x, i.pointShadowMatrix.length = y, i.spotLightMatrix.length = A + b - M, i.spotLightMap.length = b, i.numSpotLightShadowsWithMaps = M, i.numLightProbes = w, C.directionalLength = f, C.pointLength = p, C.spotLength = v, C.rectAreaLength = g, C.hemiLength = m, C.numDirectionalShadows = x, C.numPointShadows = y, C.numSpotShadows = A, C.numSpotMaps = b, C.numLightProbes = w, i.version = y4e++);
  }
  function l(c, h) {
    let u = 0, d = 0, f = 0, p = 0, v = 0;
    const g = h.matrixWorldInverse;
    for (let m = 0, x = c.length; m < x; m++) {
      const y = c[m];
      if (y.isDirectionalLight) {
        const A = i.directional[u];
        A.direction.setFromMatrixPosition(y.matrixWorld), n.setFromMatrixPosition(y.target.matrixWorld), A.direction.sub(n), A.direction.transformDirection(g), u++;
      } else if (y.isSpotLight) {
        const A = i.spot[f];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), A.direction.setFromMatrixPosition(y.matrixWorld), n.setFromMatrixPosition(y.target.matrixWorld), A.direction.sub(n), A.direction.transformDirection(g), f++;
      } else if (y.isRectAreaLight) {
        const A = i.rectArea[p];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), a.identity(), r.copy(y.matrixWorld), r.premultiply(g), a.extractRotation(r), A.halfWidth.set(y.width * 0.5, 0, 0), A.halfHeight.set(0, y.height * 0.5, 0), A.halfWidth.applyMatrix4(a), A.halfHeight.applyMatrix4(a), p++;
      } else if (y.isPointLight) {
        const A = i.point[d];
        A.position.setFromMatrixPosition(y.matrixWorld), A.position.applyMatrix4(g), d++;
      } else if (y.isHemisphereLight) {
        const A = i.hemi[v];
        A.direction.setFromMatrixPosition(y.matrixWorld), A.direction.transformDirection(g), v++;
      }
    }
  }
  return {
    setup: o,
    setupView: l,
    state: i
  };
}
function rD(s) {
  const e = new A4e(s), t = [], i = [];
  function n(h) {
    c.camera = h, t.length = 0, i.length = 0;
  }
  function r(h) {
    t.push(h);
  }
  function a(h) {
    i.push(h);
  }
  function o() {
    e.setup(t);
  }
  function l(h) {
    e.setupView(t, h);
  }
  const c = {
    lightsArray: t,
    shadowsArray: i,
    camera: null,
    lights: e,
    transmissionRenderTarget: {}
  };
  return {
    init: n,
    state: c,
    setupLights: o,
    setupLightsView: l,
    pushLight: r,
    pushShadow: a
  };
}
function b4e(s) {
  let e = /* @__PURE__ */ new WeakMap();
  function t(n, r = 0) {
    const a = e.get(n);
    let o;
    return a === void 0 ? (o = new rD(s), e.set(n, [o])) : r >= a.length ? (o = new rD(s), a.push(o)) : o = a[r], o;
  }
  function i() {
    e = /* @__PURE__ */ new WeakMap();
  }
  return {
    get: t,
    dispose: i
  };
}
class M4e extends m8 {
  constructor(e) {
    super(), this.isMeshDepthMaterial = !0, this.type = "MeshDepthMaterial", this.depthPacking = xve, this.map = null, this.alphaMap = null, this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.wireframe = !1, this.wireframeLinewidth = 1, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.depthPacking = e.depthPacking, this.map = e.map, this.alphaMap = e.alphaMap, this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this.wireframe = e.wireframe, this.wireframeLinewidth = e.wireframeLinewidth, this;
  }
}
class S4e extends m8 {
  constructor(e) {
    super(), this.isMeshDistanceMaterial = !0, this.type = "MeshDistanceMaterial", this.map = null, this.alphaMap = null, this.displacementMap = null, this.displacementScale = 1, this.displacementBias = 0, this.setValues(e);
  }
  copy(e) {
    return super.copy(e), this.map = e.map, this.alphaMap = e.alphaMap, this.displacementMap = e.displacementMap, this.displacementScale = e.displacementScale, this.displacementBias = e.displacementBias, this;
  }
}
const w4e = (
  /* glsl */
  `
void main() {

	gl_Position = vec4( position, 1.0 );

}
`
), E4e = (
  /* glsl */
  `
uniform sampler2D shadow_pass;
uniform vec2 resolution;
uniform float radius;

#include <packing>

void main() {

	const float samples = float( VSM_SAMPLES );

	float mean = 0.0;
	float squared_mean = 0.0;

	float uvStride = samples <= 1.0 ? 0.0 : 2.0 / ( samples - 1.0 );
	float uvStart = samples <= 1.0 ? 0.0 : - 1.0;
	for ( float i = 0.0; i < samples; i ++ ) {

		float uvOffset = uvStart + i * uvStride;

		#ifdef HORIZONTAL_PASS

			vec2 distribution = unpackRGBATo2Half( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( uvOffset, 0.0 ) * radius ) / resolution ) );
			mean += distribution.x;
			squared_mean += distribution.y * distribution.y + distribution.x * distribution.x;

		#else

			float depth = unpackRGBAToDepth( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( 0.0, uvOffset ) * radius ) / resolution ) );
			mean += depth;
			squared_mean += depth * depth;

		#endif

	}

	mean = mean / samples;
	squared_mean = squared_mean / samples;

	float std_dev = sqrt( squared_mean - mean * mean );

	gl_FragColor = pack2HalfToRGBA( vec2( mean, std_dev ) );

}
`
);
function T4e(s, e, t) {
  let i = new rU();
  const n = new tn(), r = new tn(), a = new Cs(), o = new M4e({ depthPacking: Ave }), l = new S4e(), c = {}, h = t.maxTextureSize, u = { [Kc]: Fr, [Fr]: Kc, [Q0]: Q0 }, d = new Jc({
    defines: {
      VSM_SAMPLES: 8
    },
    uniforms: {
      shadow_pass: { value: null },
      resolution: { value: new tn() },
      radius: { value: 4 }
    },
    vertexShader: w4e,
    fragmentShader: E4e
  }), f = d.clone();
  f.defines.HORIZONTAL_PASS = 1;
  const p = new nd();
  p.setAttribute(
    "position",
    new n0(
      new Float32Array([-1, -1, 0.5, 3, -1, 0.5, -1, 3, 0.5]),
      3
    )
  );
  const v = new al(p, d), g = this;
  this.enabled = !1, this.autoUpdate = !0, this.needsUpdate = !1, this.type = WF;
  let m = this.type;
  this.render = function(M, w, C) {
    if (g.enabled === !1 || g.autoUpdate === !1 && g.needsUpdate === !1 || M.length === 0)
      return;
    const T = s.getRenderTarget(), E = s.getActiveCubeFace(), P = s.getActiveMipmapLevel(), L = s.state;
    L.setBlending(Nc), L.buffers.color.setClear(1, 1, 1, 1), L.buffers.depth.setTest(!0), L.setScissorTest(!1);
    const I = m !== V0 && this.type === V0, N = m === V0 && this.type !== V0;
    for (let H = 0, U = M.length; H < U; H++) {
      const W = M[H], X = W.shadow;
      if (X === void 0) {
        console.warn("THREE.WebGLShadowMap:", W, "has no shadow.");
        continue;
      }
      if (X.autoUpdate === !1 && X.needsUpdate === !1)
        continue;
      n.copy(X.mapSize);
      const K = X.getFrameExtents();
      if (n.multiply(K), r.copy(X.mapSize), (n.x > h || n.y > h) && (n.x > h && (r.x = Math.floor(h / K.x), n.x = r.x * K.x, X.mapSize.x = r.x), n.y > h && (r.y = Math.floor(h / K.y), n.y = r.y * K.y, X.mapSize.y = r.y)), X.map === null || I === !0 || N === !0) {
        const re = this.type !== V0 ? { minFilter: Ea, magFilter: Ea } : {};
        X.map !== null && X.map.dispose(), X.map = new ku(n.x, n.y, re), X.map.texture.name = W.name + ".shadowMap", X.camera.updateProjectionMatrix();
      }
      s.setRenderTarget(X.map), s.clear();
      const te = X.getViewportCount();
      for (let re = 0; re < te; re++) {
        const be = X.getViewport(re);
        a.set(
          r.x * be.x,
          r.y * be.y,
          r.x * be.z,
          r.y * be.w
        ), L.viewport(a), X.updateMatrices(W, re), i = X.getFrustum(), A(w, C, X.camera, W, this.type);
      }
      X.isPointLightShadow !== !0 && this.type === V0 && x(X, C), X.needsUpdate = !1;
    }
    m = this.type, g.needsUpdate = !1, s.setRenderTarget(T, E, P);
  };
  function x(M, w) {
    const C = e.update(v);
    d.defines.VSM_SAMPLES !== M.blurSamples && (d.defines.VSM_SAMPLES = M.blurSamples, f.defines.VSM_SAMPLES = M.blurSamples, d.needsUpdate = !0, f.needsUpdate = !0), M.mapPass === null && (M.mapPass = new ku(n.x, n.y)), d.uniforms.shadow_pass.value = M.map.texture, d.uniforms.resolution.value = M.mapSize, d.uniforms.radius.value = M.radius, s.setRenderTarget(M.mapPass), s.clear(), s.renderBufferDirect(w, null, C, d, v, null), f.uniforms.shadow_pass.value = M.mapPass.texture, f.uniforms.resolution.value = M.mapSize, f.uniforms.radius.value = M.radius, s.setRenderTarget(M.map), s.clear(), s.renderBufferDirect(w, null, C, f, v, null);
  }
  function y(M, w, C, T) {
    let E = null;
    const P = C.isPointLight === !0 ? M.customDistanceMaterial : M.customDepthMaterial;
    if (P !== void 0)
      E = P;
    else if (E = C.isPointLight === !0 ? l : o, s.localClippingEnabled && w.clipShadows === !0 && Array.isArray(w.clippingPlanes) && w.clippingPlanes.length !== 0 || w.displacementMap && w.displacementScale !== 0 || w.alphaMap && w.alphaTest > 0 || w.map && w.alphaTest > 0) {
      const L = E.uuid, I = w.uuid;
      let N = c[L];
      N === void 0 && (N = {}, c[L] = N);
      let H = N[I];
      H === void 0 && (H = E.clone(), N[I] = H, w.addEventListener("dispose", b)), E = H;
    }
    if (E.visible = w.visible, E.wireframe = w.wireframe, T === V0 ? E.side = w.shadowSide !== null ? w.shadowSide : w.side : E.side = w.shadowSide !== null ? w.shadowSide : u[w.side], E.alphaMap = w.alphaMap, E.alphaTest = w.alphaTest, E.map = w.map, E.clipShadows = w.clipShadows, E.clippingPlanes = w.clippingPlanes, E.clipIntersection = w.clipIntersection, E.displacementMap = w.displacementMap, E.displacementScale = w.displacementScale, E.displacementBias = w.displacementBias, E.wireframeLinewidth = w.wireframeLinewidth, E.linewidth = w.linewidth, C.isPointLight === !0 && E.isMeshDistanceMaterial === !0) {
      const L = s.properties.get(E);
      L.light = C;
    }
    return E;
  }
  function A(M, w, C, T, E) {
    if (M.visible === !1)
      return;
    if (M.layers.test(w.layers) && (M.isMesh || M.isLine || M.isPoints) && (M.castShadow || M.receiveShadow && E === V0) && (!M.frustumCulled || i.intersectsObject(M))) {
      M.modelViewMatrix.multiplyMatrices(C.matrixWorldInverse, M.matrixWorld);
      const I = e.update(M), N = M.material;
      if (Array.isArray(N)) {
        const H = I.groups;
        for (let U = 0, W = H.length; U < W; U++) {
          const X = H[U], K = N[X.materialIndex];
          if (K && K.visible) {
            const te = y(M, K, T, E);
            M.onBeforeShadow(s, M, w, C, I, te, X), s.renderBufferDirect(C, null, I, te, M, X), M.onAfterShadow(s, M, w, C, I, te, X);
          }
        }
      } else if (N.visible) {
        const H = y(M, N, T, E);
        M.onBeforeShadow(s, M, w, C, I, H, null), s.renderBufferDirect(C, null, I, H, M, null), M.onAfterShadow(s, M, w, C, I, H, null);
      }
    }
    const L = M.children;
    for (let I = 0, N = L.length; I < N; I++)
      A(L[I], w, C, T, E);
  }
  function b(M) {
    M.target.removeEventListener("dispose", b);
    for (const C in c) {
      const T = c[C], E = M.target.uuid;
      E in T && (T[E].dispose(), delete T[E]);
    }
  }
}
function C4e(s) {
  function e() {
    let F = !1;
    const ue = new Cs();
    let Q = null;
    const he = new Cs(0, 0, 0, 0);
    return {
      setMask: function(Be) {
        Q !== Be && !F && (s.colorMask(Be, Be, Be, Be), Q = Be);
      },
      setLocked: function(Be) {
        F = Be;
      },
      setClear: function(Be, ft, Et, ei, di) {
        di === !0 && (Be *= ei, ft *= ei, Et *= ei), ue.set(Be, ft, Et, ei), he.equals(ue) === !1 && (s.clearColor(Be, ft, Et, ei), he.copy(ue));
      },
      reset: function() {
        F = !1, Q = null, he.set(-1, 0, 0, 0);
      }
    };
  }
  function t() {
    let F = !1, ue = null, Q = null, he = null;
    return {
      setTest: function(Be) {
        Be ? Me(s.DEPTH_TEST) : ye(s.DEPTH_TEST);
      },
      setMask: function(Be) {
        ue !== Be && !F && (s.depthMask(Be), ue = Be);
      },
      setFunc: function(Be) {
        if (Q !== Be) {
          switch (Be) {
            case ive:
              s.depthFunc(s.NEVER);
              break;
            case nve:
              s.depthFunc(s.ALWAYS);
              break;
            case sve:
              s.depthFunc(s.LESS);
              break;
            case Hx:
              s.depthFunc(s.LEQUAL);
              break;
            case rve:
              s.depthFunc(s.EQUAL);
              break;
            case ave:
              s.depthFunc(s.GEQUAL);
              break;
            case ove:
              s.depthFunc(s.GREATER);
              break;
            case lve:
              s.depthFunc(s.NOTEQUAL);
              break;
            default:
              s.depthFunc(s.LEQUAL);
          }
          Q = Be;
        }
      },
      setLocked: function(Be) {
        F = Be;
      },
      setClear: function(Be) {
        he !== Be && (s.clearDepth(Be), he = Be);
      },
      reset: function() {
        F = !1, ue = null, Q = null, he = null;
      }
    };
  }
  function i() {
    let F = !1, ue = null, Q = null, he = null, Be = null, ft = null, Et = null, ei = null, di = null;
    return {
      setTest: function(Ot) {
        F || (Ot ? Me(s.STENCIL_TEST) : ye(s.STENCIL_TEST));
      },
      setMask: function(Ot) {
        ue !== Ot && !F && (s.stencilMask(Ot), ue = Ot);
      },
      setFunc: function(Ot, Wi, Li) {
        (Q !== Ot || he !== Wi || Be !== Li) && (s.stencilFunc(Ot, Wi, Li), Q = Ot, he = Wi, Be = Li);
      },
      setOp: function(Ot, Wi, Li) {
        (ft !== Ot || Et !== Wi || ei !== Li) && (s.stencilOp(Ot, Wi, Li), ft = Ot, Et = Wi, ei = Li);
      },
      setLocked: function(Ot) {
        F = Ot;
      },
      setClear: function(Ot) {
        di !== Ot && (s.clearStencil(Ot), di = Ot);
      },
      reset: function() {
        F = !1, ue = null, Q = null, he = null, Be = null, ft = null, Et = null, ei = null, di = null;
      }
    };
  }
  const n = new e(), r = new t(), a = new i(), o = /* @__PURE__ */ new WeakMap(), l = /* @__PURE__ */ new WeakMap();
  let c = {}, h = {}, u = /* @__PURE__ */ new WeakMap(), d = [], f = null, p = !1, v = null, g = null, m = null, x = null, y = null, A = null, b = null, M = new hn(0, 0, 0), w = 0, C = !1, T = null, E = null, P = null, L = null, I = null;
  const N = s.getParameter(s.MAX_COMBINED_TEXTURE_IMAGE_UNITS);
  let H = !1, U = 0;
  const W = s.getParameter(s.VERSION);
  W.indexOf("WebGL") !== -1 ? (U = parseFloat(/^WebGL (\d)/.exec(W)[1]), H = U >= 1) : W.indexOf("OpenGL ES") !== -1 && (U = parseFloat(/^OpenGL ES (\d)/.exec(W)[1]), H = U >= 2);
  let X = null, K = {};
  const te = s.getParameter(s.SCISSOR_BOX), re = s.getParameter(s.VIEWPORT), be = new Cs().fromArray(te), Pe = new Cs().fromArray(re);
  function J(F, ue, Q, he) {
    const Be = new Uint8Array(4), ft = s.createTexture();
    s.bindTexture(F, ft), s.texParameteri(F, s.TEXTURE_MIN_FILTER, s.NEAREST), s.texParameteri(F, s.TEXTURE_MAG_FILTER, s.NEAREST);
    for (let Et = 0; Et < Q; Et++)
      F === s.TEXTURE_3D || F === s.TEXTURE_2D_ARRAY ? s.texImage3D(ue, 0, s.RGBA, 1, 1, he, 0, s.RGBA, s.UNSIGNED_BYTE, Be) : s.texImage2D(ue + Et, 0, s.RGBA, 1, 1, 0, s.RGBA, s.UNSIGNED_BYTE, Be);
    return ft;
  }
  const fe = {};
  fe[s.TEXTURE_2D] = J(s.TEXTURE_2D, s.TEXTURE_2D, 1), fe[s.TEXTURE_CUBE_MAP] = J(s.TEXTURE_CUBE_MAP, s.TEXTURE_CUBE_MAP_POSITIVE_X, 6), fe[s.TEXTURE_2D_ARRAY] = J(s.TEXTURE_2D_ARRAY, s.TEXTURE_2D_ARRAY, 1, 1), fe[s.TEXTURE_3D] = J(s.TEXTURE_3D, s.TEXTURE_3D, 1, 1), n.setClear(0, 0, 0, 1), r.setClear(1), a.setClear(0), Me(s.DEPTH_TEST), r.setFunc(Hx), qe(!1), Ie(uR), Me(s.CULL_FACE), Fe(Nc);
  function Me(F) {
    c[F] !== !0 && (s.enable(F), c[F] = !0);
  }
  function ye(F) {
    c[F] !== !1 && (s.disable(F), c[F] = !1);
  }
  function Ye(F, ue) {
    return h[F] !== ue ? (s.bindFramebuffer(F, ue), h[F] = ue, F === s.DRAW_FRAMEBUFFER && (h[s.FRAMEBUFFER] = ue), F === s.FRAMEBUFFER && (h[s.DRAW_FRAMEBUFFER] = ue), !0) : !1;
  }
  function Je(F, ue) {
    let Q = d, he = !1;
    if (F) {
      Q = u.get(ue), Q === void 0 && (Q = [], u.set(ue, Q));
      const Be = F.textures;
      if (Q.length !== Be.length || Q[0] !== s.COLOR_ATTACHMENT0) {
        for (let ft = 0, Et = Be.length; ft < Et; ft++)
          Q[ft] = s.COLOR_ATTACHMENT0 + ft;
        Q.length = Be.length, he = !0;
      }
    } else
      Q[0] !== s.BACK && (Q[0] = s.BACK, he = !0);
    he && s.drawBuffers(Q);
  }
  function rt(F) {
    return f !== F ? (s.useProgram(F), f = F, !0) : !1;
  }
  const wt = {
    [ru]: s.FUNC_ADD,
    [z2e]: s.FUNC_SUBTRACT,
    [k2e]: s.FUNC_REVERSE_SUBTRACT
  };
  wt[G2e] = s.MIN, wt[H2e] = s.MAX;
  const k = {
    [V2e]: s.ZERO,
    [W2e]: s.ONE,
    [X2e]: s.SRC_COLOR,
    [b7]: s.SRC_ALPHA,
    [J2e]: s.SRC_ALPHA_SATURATE,
    [Z2e]: s.DST_COLOR,
    [q2e]: s.DST_ALPHA,
    [j2e]: s.ONE_MINUS_SRC_COLOR,
    [M7]: s.ONE_MINUS_SRC_ALPHA,
    [K2e]: s.ONE_MINUS_DST_COLOR,
    [Y2e]: s.ONE_MINUS_DST_ALPHA,
    [Q2e]: s.CONSTANT_COLOR,
    [$2e]: s.ONE_MINUS_CONSTANT_COLOR,
    [eve]: s.CONSTANT_ALPHA,
    [tve]: s.ONE_MINUS_CONSTANT_ALPHA
  };
  function Fe(F, ue, Q, he, Be, ft, Et, ei, di, Ot) {
    if (F === Nc) {
      p === !0 && (ye(s.BLEND), p = !1);
      return;
    }
    if (p === !1 && (Me(s.BLEND), p = !0), F !== U2e) {
      if (F !== v || Ot !== C) {
        if ((g !== ru || y !== ru) && (s.blendEquation(s.FUNC_ADD), g = ru, y = ru), Ot)
          switch (F) {
            case Jf:
              s.blendFuncSeparate(s.ONE, s.ONE_MINUS_SRC_ALPHA, s.ONE, s.ONE_MINUS_SRC_ALPHA);
              break;
            case dR:
              s.blendFunc(s.ONE, s.ONE);
              break;
            case fR:
              s.blendFuncSeparate(s.ZERO, s.ONE_MINUS_SRC_COLOR, s.ZERO, s.ONE);
              break;
            case pR:
              s.blendFuncSeparate(s.ZERO, s.SRC_COLOR, s.ZERO, s.SRC_ALPHA);
              break;
            default:
              console.error("THREE.WebGLState: Invalid blending: ", F);
              break;
          }
        else
          switch (F) {
            case Jf:
              s.blendFuncSeparate(s.SRC_ALPHA, s.ONE_MINUS_SRC_ALPHA, s.ONE, s.ONE_MINUS_SRC_ALPHA);
              break;
            case dR:
              s.blendFunc(s.SRC_ALPHA, s.ONE);
              break;
            case fR:
              s.blendFuncSeparate(s.ZERO, s.ONE_MINUS_SRC_COLOR, s.ZERO, s.ONE);
              break;
            case pR:
              s.blendFunc(s.ZERO, s.SRC_COLOR);
              break;
            default:
              console.error("THREE.WebGLState: Invalid blending: ", F);
              break;
          }
        m = null, x = null, A = null, b = null, M.set(0, 0, 0), w = 0, v = F, C = Ot;
      }
      return;
    }
    Be = Be || ue, ft = ft || Q, Et = Et || he, (ue !== g || Be !== y) && (s.blendEquationSeparate(wt[ue], wt[Be]), g = ue, y = Be), (Q !== m || he !== x || ft !== A || Et !== b) && (s.blendFuncSeparate(k[Q], k[he], k[ft], k[Et]), m = Q, x = he, A = ft, b = Et), (ei.equals(M) === !1 || di !== w) && (s.blendColor(ei.r, ei.g, ei.b, di), M.copy(ei), w = di), v = F, C = !1;
  }
  function Ge(F, ue) {
    F.side === Q0 ? ye(s.CULL_FACE) : Me(s.CULL_FACE);
    let Q = F.side === Fr;
    ue && (Q = !Q), qe(Q), F.blending === Jf && F.transparent === !1 ? Fe(Nc) : Fe(F.blending, F.blendEquation, F.blendSrc, F.blendDst, F.blendEquationAlpha, F.blendSrcAlpha, F.blendDstAlpha, F.blendColor, F.blendAlpha, F.premultipliedAlpha), r.setFunc(F.depthFunc), r.setTest(F.depthTest), r.setMask(F.depthWrite), n.setMask(F.colorWrite);
    const he = F.stencilWrite;
    a.setTest(he), he && (a.setMask(F.stencilWriteMask), a.setFunc(F.stencilFunc, F.stencilRef, F.stencilFuncMask), a.setOp(F.stencilFail, F.stencilZFail, F.stencilZPass)), Ve(F.polygonOffset, F.polygonOffsetFactor, F.polygonOffsetUnits), F.alphaToCoverage === !0 ? Me(s.SAMPLE_ALPHA_TO_COVERAGE) : ye(s.SAMPLE_ALPHA_TO_COVERAGE);
  }
  function qe(F) {
    T !== F && (F ? s.frontFace(s.CW) : s.frontFace(s.CCW), T = F);
  }
  function Ie(F) {
    F !== N2e ? (Me(s.CULL_FACE), F !== E && (F === uR ? s.cullFace(s.BACK) : F === B2e ? s.cullFace(s.FRONT) : s.cullFace(s.FRONT_AND_BACK))) : ye(s.CULL_FACE), E = F;
  }
  function vt(F) {
    F !== P && (H && s.lineWidth(F), P = F);
  }
  function Ve(F, ue, Q) {
    F ? (Me(s.POLYGON_OFFSET_FILL), (L !== ue || I !== Q) && (s.polygonOffset(ue, Q), L = ue, I = Q)) : ye(s.POLYGON_OFFSET_FILL);
  }
  function Ze(F) {
    F ? Me(s.SCISSOR_TEST) : ye(s.SCISSOR_TEST);
  }
  function G(F) {
    F === void 0 && (F = s.TEXTURE0 + N - 1), X !== F && (s.activeTexture(F), X = F);
  }
  function D(F, ue, Q) {
    Q === void 0 && (X === null ? Q = s.TEXTURE0 + N - 1 : Q = X);
    let he = K[Q];
    he === void 0 && (he = { type: void 0, texture: void 0 }, K[Q] = he), (he.type !== F || he.texture !== ue) && (X !== Q && (s.activeTexture(Q), X = Q), s.bindTexture(F, ue || fe[F]), he.type = F, he.texture = ue);
  }
  function ne() {
    const F = K[X];
    F !== void 0 && F.type !== void 0 && (s.bindTexture(F.type, null), F.type = void 0, F.texture = void 0);
  }
  function ve() {
    try {
      s.compressedTexImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Y() {
    try {
      s.compressedTexImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function ee() {
    try {
      s.texSubImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function we() {
    try {
      s.texSubImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function de() {
    try {
      s.compressedTexSubImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function xe() {
    try {
      s.compressedTexSubImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function it() {
    try {
      s.texStorage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function q() {
    try {
      s.texStorage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function ae() {
    try {
      s.texImage2D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Ee() {
    try {
      s.texImage3D.apply(s, arguments);
    } catch (F) {
      console.error("THREE.WebGLState:", F);
    }
  }
  function Le(F) {
    be.equals(F) === !1 && (s.scissor(F.x, F.y, F.z, F.w), be.copy(F));
  }
  function Ae(F) {
    Pe.equals(F) === !1 && (s.viewport(F.x, F.y, F.z, F.w), Pe.copy(F));
  }
  function We(F, ue) {
    let Q = l.get(ue);
    Q === void 0 && (Q = /* @__PURE__ */ new WeakMap(), l.set(ue, Q));
    let he = Q.get(F);
    he === void 0 && (he = s.getUniformBlockIndex(ue, F.name), Q.set(F, he));
  }
  function ot(F, ue) {
    const he = l.get(ue).get(F);
    o.get(ue) !== he && (s.uniformBlockBinding(ue, he, F.__bindingPointIndex), o.set(ue, he));
  }
  function Ct() {
    s.disable(s.BLEND), s.disable(s.CULL_FACE), s.disable(s.DEPTH_TEST), s.disable(s.POLYGON_OFFSET_FILL), s.disable(s.SCISSOR_TEST), s.disable(s.STENCIL_TEST), s.disable(s.SAMPLE_ALPHA_TO_COVERAGE), s.blendEquation(s.FUNC_ADD), s.blendFunc(s.ONE, s.ZERO), s.blendFuncSeparate(s.ONE, s.ZERO, s.ONE, s.ZERO), s.blendColor(0, 0, 0, 0), s.colorMask(!0, !0, !0, !0), s.clearColor(0, 0, 0, 0), s.depthMask(!0), s.depthFunc(s.LESS), s.clearDepth(1), s.stencilMask(4294967295), s.stencilFunc(s.ALWAYS, 0, 4294967295), s.stencilOp(s.KEEP, s.KEEP, s.KEEP), s.clearStencil(0), s.cullFace(s.BACK), s.frontFace(s.CCW), s.polygonOffset(0, 0), s.activeTexture(s.TEXTURE0), s.bindFramebuffer(s.FRAMEBUFFER, null), s.bindFramebuffer(s.DRAW_FRAMEBUFFER, null), s.bindFramebuffer(s.READ_FRAMEBUFFER, null), s.useProgram(null), s.lineWidth(1), s.scissor(0, 0, s.canvas.width, s.canvas.height), s.viewport(0, 0, s.canvas.width, s.canvas.height), c = {}, X = null, K = {}, h = {}, u = /* @__PURE__ */ new WeakMap(), d = [], f = null, p = !1, v = null, g = null, m = null, x = null, y = null, A = null, b = null, M = new hn(0, 0, 0), w = 0, C = !1, T = null, E = null, P = null, L = null, I = null, be.set(0, 0, s.canvas.width, s.canvas.height), Pe.set(0, 0, s.canvas.width, s.canvas.height), n.reset(), r.reset(), a.reset();
  }
  return {
    buffers: {
      color: n,
      depth: r,
      stencil: a
    },
    enable: Me,
    disable: ye,
    bindFramebuffer: Ye,
    drawBuffers: Je,
    useProgram: rt,
    setBlending: Fe,
    setMaterial: Ge,
    setFlipSided: qe,
    setCullFace: Ie,
    setLineWidth: vt,
    setPolygonOffset: Ve,
    setScissorTest: Ze,
    activeTexture: G,
    bindTexture: D,
    unbindTexture: ne,
    compressedTexImage2D: ve,
    compressedTexImage3D: Y,
    texImage2D: ae,
    texImage3D: Ee,
    updateUBOMapping: We,
    uniformBlockBinding: ot,
    texStorage2D: it,
    texStorage3D: q,
    texSubImage2D: ee,
    texSubImage3D: we,
    compressedTexSubImage2D: de,
    compressedTexSubImage3D: xe,
    scissor: Le,
    viewport: Ae,
    reset: Ct
  };
}
function aD(s, e, t, i) {
  const n = _4e(i);
  switch (t) {
    case KF:
      return s * e;
    case QF:
      return s * e;
    case $F:
      return s * e * 2;
    case eU:
      return s * e / n.components * n.byteLength;
    case Dw:
      return s * e / n.components * n.byteLength;
    case tU:
      return s * e * 2 / n.components * n.byteLength;
    case Lw:
      return s * e * 2 / n.components * n.byteLength;
    case JF:
      return s * e * 3 / n.components * n.byteLength;
    case no:
      return s * e * 4 / n.components * n.byteLength;
    case Iw:
      return s * e * 4 / n.components * n.byteLength;
    case Jy:
    case Qy:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 8;
    case $y:
    case e9:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case _7:
    case R7:
      return Math.max(s, 16) * Math.max(e, 8) / 4;
    case C7:
    case P7:
      return Math.max(s, 8) * Math.max(e, 8) / 2;
    case D7:
    case L7:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 8;
    case I7:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case O7:
      return Math.floor((s + 3) / 4) * Math.floor((e + 3) / 4) * 16;
    case N7:
      return Math.floor((s + 4) / 5) * Math.floor((e + 3) / 4) * 16;
    case B7:
      return Math.floor((s + 4) / 5) * Math.floor((e + 4) / 5) * 16;
    case F7:
      return Math.floor((s + 5) / 6) * Math.floor((e + 4) / 5) * 16;
    case U7:
      return Math.floor((s + 5) / 6) * Math.floor((e + 5) / 6) * 16;
    case z7:
      return Math.floor((s + 7) / 8) * Math.floor((e + 4) / 5) * 16;
    case k7:
      return Math.floor((s + 7) / 8) * Math.floor((e + 5) / 6) * 16;
    case G7:
      return Math.floor((s + 7) / 8) * Math.floor((e + 7) / 8) * 16;
    case H7:
      return Math.floor((s + 9) / 10) * Math.floor((e + 4) / 5) * 16;
    case V7:
      return Math.floor((s + 9) / 10) * Math.floor((e + 5) / 6) * 16;
    case W7:
      return Math.floor((s + 9) / 10) * Math.floor((e + 7) / 8) * 16;
    case X7:
      return Math.floor((s + 9) / 10) * Math.floor((e + 9) / 10) * 16;
    case j7:
      return Math.floor((s + 11) / 12) * Math.floor((e + 9) / 10) * 16;
    case q7:
      return Math.floor((s + 11) / 12) * Math.floor((e + 11) / 12) * 16;
    case t9:
    case Y7:
    case Z7:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 16;
    case iU:
    case K7:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 8;
    case J7:
    case Q7:
      return Math.ceil(s / 4) * Math.ceil(e / 4) * 16;
  }
  throw new Error(
    `Unable to determine texture byte length for ${t} format.`
  );
}
function _4e(s) {
  switch (s) {
    case Sl:
    case qF:
      return { byteLength: 1, components: 1 };
    case jg:
    case YF:
    case G2:
      return { byteLength: 2, components: 1 };
    case Pw:
    case Rw:
      return { byteLength: 2, components: 4 };
    case zu:
    case _w:
    case sl:
      return { byteLength: 4, components: 1 };
    case ZF:
      return { byteLength: 4, components: 3 };
  }
  throw new Error(`Unknown texture type ${s}.`);
}
function P4e(s, e, t, i, n, r, a) {
  const o = e.has("WEBGL_multisampled_render_to_texture") ? e.get("WEBGL_multisampled_render_to_texture") : null, l = typeof navigator > "u" ? !1 : /OculusBrowser/g.test(navigator.userAgent), c = new tn(), h = /* @__PURE__ */ new WeakMap();
  let u;
  const d = /* @__PURE__ */ new WeakMap();
  let f = !1;
  try {
    f = typeof OffscreenCanvas < "u" && new OffscreenCanvas(1, 1).getContext("2d") !== null;
  } catch {
  }
  function p(G, D) {
    return f ? (
      // eslint-disable-next-line compat/compat
      new OffscreenCanvas(G, D)
    ) : qx("canvas");
  }
  function v(G, D, ne) {
    let ve = 1;
    const Y = Ze(G);
    if ((Y.width > ne || Y.height > ne) && (ve = ne / Math.max(Y.width, Y.height)), ve < 1)
      if (typeof HTMLImageElement < "u" && G instanceof HTMLImageElement || typeof HTMLCanvasElement < "u" && G instanceof HTMLCanvasElement || typeof ImageBitmap < "u" && G instanceof ImageBitmap || typeof VideoFrame < "u" && G instanceof VideoFrame) {
        const ee = Math.floor(ve * Y.width), we = Math.floor(ve * Y.height);
        u === void 0 && (u = p(ee, we));
        const de = D ? p(ee, we) : u;
        return de.width = ee, de.height = we, de.getContext("2d").drawImage(G, 0, 0, ee, we), console.warn("THREE.WebGLRenderer: Texture has been resized from (" + Y.width + "x" + Y.height + ") to (" + ee + "x" + we + ")."), de;
      } else
        return "data" in G && console.warn("THREE.WebGLRenderer: Image in DataTexture is too big (" + Y.width + "x" + Y.height + ")."), G;
    return G;
  }
  function g(G) {
    return G.generateMipmaps && G.minFilter !== Ea && G.minFilter !== Qa;
  }
  function m(G) {
    s.generateMipmap(G);
  }
  function x(G, D, ne, ve, Y = !1) {
    if (G !== null) {
      if (s[G] !== void 0)
        return s[G];
      console.warn("THREE.WebGLRenderer: Attempt to use non-existing WebGL internal format '" + G + "'");
    }
    let ee = D;
    if (D === s.RED && (ne === s.FLOAT && (ee = s.R32F), ne === s.HALF_FLOAT && (ee = s.R16F), ne === s.UNSIGNED_BYTE && (ee = s.R8)), D === s.RED_INTEGER && (ne === s.UNSIGNED_BYTE && (ee = s.R8UI), ne === s.UNSIGNED_SHORT && (ee = s.R16UI), ne === s.UNSIGNED_INT && (ee = s.R32UI), ne === s.BYTE && (ee = s.R8I), ne === s.SHORT && (ee = s.R16I), ne === s.INT && (ee = s.R32I)), D === s.RG && (ne === s.FLOAT && (ee = s.RG32F), ne === s.HALF_FLOAT && (ee = s.RG16F), ne === s.UNSIGNED_BYTE && (ee = s.RG8)), D === s.RG_INTEGER && (ne === s.UNSIGNED_BYTE && (ee = s.RG8UI), ne === s.UNSIGNED_SHORT && (ee = s.RG16UI), ne === s.UNSIGNED_INT && (ee = s.RG32UI), ne === s.BYTE && (ee = s.RG8I), ne === s.SHORT && (ee = s.RG16I), ne === s.INT && (ee = s.RG32I)), D === s.RGB && ne === s.UNSIGNED_INT_5_9_9_9_REV && (ee = s.RGB9_E5), D === s.RGBA) {
      const we = Y ? Vx : rn.getTransfer(ve);
      ne === s.FLOAT && (ee = s.RGBA32F), ne === s.HALF_FLOAT && (ee = s.RGBA16F), ne === s.UNSIGNED_BYTE && (ee = we === Sn ? s.SRGB8_ALPHA8 : s.RGBA8), ne === s.UNSIGNED_SHORT_4_4_4_4 && (ee = s.RGBA4), ne === s.UNSIGNED_SHORT_5_5_5_1 && (ee = s.RGB5_A1);
    }
    return (ee === s.R16F || ee === s.R32F || ee === s.RG16F || ee === s.RG32F || ee === s.RGBA16F || ee === s.RGBA32F) && e.get("EXT_color_buffer_float"), ee;
  }
  function y(G, D) {
    let ne;
    return G ? D === null || D === zu || D === Bp ? ne = s.DEPTH24_STENCIL8 : D === sl ? ne = s.DEPTH32F_STENCIL8 : D === jg && (ne = s.DEPTH24_STENCIL8, console.warn("DepthTexture: 16 bit depth attachment is not supported with stencil. Using 24-bit attachment.")) : D === null || D === zu || D === Bp ? ne = s.DEPTH_COMPONENT24 : D === sl ? ne = s.DEPTH_COMPONENT32F : D === jg && (ne = s.DEPTH_COMPONENT16), ne;
  }
  function A(G, D) {
    return g(G) === !0 || G.isFramebufferTexture && G.minFilter !== Ea && G.minFilter !== Qa ? Math.log2(Math.max(D.width, D.height)) + 1 : G.mipmaps !== void 0 && G.mipmaps.length > 0 ? G.mipmaps.length : G.isCompressedTexture && Array.isArray(G.image) ? D.mipmaps.length : 1;
  }
  function b(G) {
    const D = G.target;
    D.removeEventListener("dispose", b), w(D), D.isVideoTexture && h.delete(D);
  }
  function M(G) {
    const D = G.target;
    D.removeEventListener("dispose", M), T(D);
  }
  function w(G) {
    const D = i.get(G);
    if (D.__webglInit === void 0)
      return;
    const ne = G.source, ve = d.get(ne);
    if (ve) {
      const Y = ve[D.__cacheKey];
      Y.usedTimes--, Y.usedTimes === 0 && C(G), Object.keys(ve).length === 0 && d.delete(ne);
    }
    i.remove(G);
  }
  function C(G) {
    const D = i.get(G);
    s.deleteTexture(D.__webglTexture);
    const ne = G.source, ve = d.get(ne);
    delete ve[D.__cacheKey], a.memory.textures--;
  }
  function T(G) {
    const D = i.get(G);
    if (G.depthTexture && G.depthTexture.dispose(), G.isWebGLCubeRenderTarget)
      for (let ve = 0; ve < 6; ve++) {
        if (Array.isArray(D.__webglFramebuffer[ve]))
          for (let Y = 0; Y < D.__webglFramebuffer[ve].length; Y++)
            s.deleteFramebuffer(D.__webglFramebuffer[ve][Y]);
        else
          s.deleteFramebuffer(D.__webglFramebuffer[ve]);
        D.__webglDepthbuffer && s.deleteRenderbuffer(D.__webglDepthbuffer[ve]);
      }
    else {
      if (Array.isArray(D.__webglFramebuffer))
        for (let ve = 0; ve < D.__webglFramebuffer.length; ve++)
          s.deleteFramebuffer(D.__webglFramebuffer[ve]);
      else
        s.deleteFramebuffer(D.__webglFramebuffer);
      if (D.__webglDepthbuffer && s.deleteRenderbuffer(D.__webglDepthbuffer), D.__webglMultisampledFramebuffer && s.deleteFramebuffer(D.__webglMultisampledFramebuffer), D.__webglColorRenderbuffer)
        for (let ve = 0; ve < D.__webglColorRenderbuffer.length; ve++)
          D.__webglColorRenderbuffer[ve] && s.deleteRenderbuffer(D.__webglColorRenderbuffer[ve]);
      D.__webglDepthRenderbuffer && s.deleteRenderbuffer(D.__webglDepthRenderbuffer);
    }
    const ne = G.textures;
    for (let ve = 0, Y = ne.length; ve < Y; ve++) {
      const ee = i.get(ne[ve]);
      ee.__webglTexture && (s.deleteTexture(ee.__webglTexture), a.memory.textures--), i.remove(ne[ve]);
    }
    i.remove(G);
  }
  let E = 0;
  function P() {
    E = 0;
  }
  function L() {
    const G = E;
    return G >= n.maxTextures && console.warn("THREE.WebGLTextures: Trying to use " + G + " texture units while this GPU supports only " + n.maxTextures), E += 1, G;
  }
  function I(G) {
    const D = [];
    return D.push(G.wrapS), D.push(G.wrapT), D.push(G.wrapR || 0), D.push(G.magFilter), D.push(G.minFilter), D.push(G.anisotropy), D.push(G.internalFormat), D.push(G.format), D.push(G.type), D.push(G.generateMipmaps), D.push(G.premultiplyAlpha), D.push(G.flipY), D.push(G.unpackAlignment), D.push(G.colorSpace), D.join();
  }
  function N(G, D) {
    const ne = i.get(G);
    if (G.isVideoTexture && vt(G), G.isRenderTargetTexture === !1 && G.version > 0 && ne.__version !== G.version) {
      const ve = G.image;
      if (ve === null)
        console.warn("THREE.WebGLRenderer: Texture marked for update but no image data found.");
      else if (ve.complete === !1)
        console.warn("THREE.WebGLRenderer: Texture marked for update but image is incomplete");
      else {
        Pe(ne, G, D);
        return;
      }
    }
    t.bindTexture(s.TEXTURE_2D, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function H(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      Pe(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_2D_ARRAY, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function U(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      Pe(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_3D, ne.__webglTexture, s.TEXTURE0 + D);
  }
  function W(G, D) {
    const ne = i.get(G);
    if (G.version > 0 && ne.__version !== G.version) {
      J(ne, G, D);
      return;
    }
    t.bindTexture(s.TEXTURE_CUBE_MAP, ne.__webglTexture, s.TEXTURE0 + D);
  }
  const X = {
    [E7]: s.REPEAT,
    [vu]: s.CLAMP_TO_EDGE,
    [T7]: s.MIRRORED_REPEAT
  }, K = {
    [Ea]: s.NEAREST,
    [yve]: s.NEAREST_MIPMAP_NEAREST,
    [L3]: s.NEAREST_MIPMAP_LINEAR,
    [Qa]: s.LINEAR,
    [qA]: s.LINEAR_MIPMAP_NEAREST,
    [yu]: s.LINEAR_MIPMAP_LINEAR
  }, te = {
    [Sve]: s.NEVER,
    [Pve]: s.ALWAYS,
    [wve]: s.LESS,
    [nU]: s.LEQUAL,
    [Eve]: s.EQUAL,
    [_ve]: s.GEQUAL,
    [Tve]: s.GREATER,
    [Cve]: s.NOTEQUAL
  };
  function re(G, D) {
    if (D.type === sl && e.has("OES_texture_float_linear") === !1 && (D.magFilter === Qa || D.magFilter === qA || D.magFilter === L3 || D.magFilter === yu || D.minFilter === Qa || D.minFilter === qA || D.minFilter === L3 || D.minFilter === yu) && console.warn("THREE.WebGLRenderer: Unable to use linear filtering with floating point textures. OES_texture_float_linear not supported on this device."), s.texParameteri(G, s.TEXTURE_WRAP_S, X[D.wrapS]), s.texParameteri(G, s.TEXTURE_WRAP_T, X[D.wrapT]), (G === s.TEXTURE_3D || G === s.TEXTURE_2D_ARRAY) && s.texParameteri(G, s.TEXTURE_WRAP_R, X[D.wrapR]), s.texParameteri(G, s.TEXTURE_MAG_FILTER, K[D.magFilter]), s.texParameteri(G, s.TEXTURE_MIN_FILTER, K[D.minFilter]), D.compareFunction && (s.texParameteri(G, s.TEXTURE_COMPARE_MODE, s.COMPARE_REF_TO_TEXTURE), s.texParameteri(G, s.TEXTURE_COMPARE_FUNC, te[D.compareFunction])), e.has("EXT_texture_filter_anisotropic") === !0) {
      if (D.magFilter === Ea || D.minFilter !== L3 && D.minFilter !== yu || D.type === sl && e.has("OES_texture_float_linear") === !1)
        return;
      if (D.anisotropy > 1 || i.get(D).__currentAnisotropy) {
        const ne = e.get("EXT_texture_filter_anisotropic");
        s.texParameterf(G, ne.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(D.anisotropy, n.getMaxAnisotropy())), i.get(D).__currentAnisotropy = D.anisotropy;
      }
    }
  }
  function be(G, D) {
    let ne = !1;
    G.__webglInit === void 0 && (G.__webglInit = !0, D.addEventListener("dispose", b));
    const ve = D.source;
    let Y = d.get(ve);
    Y === void 0 && (Y = {}, d.set(ve, Y));
    const ee = I(D);
    if (ee !== G.__cacheKey) {
      Y[ee] === void 0 && (Y[ee] = {
        texture: s.createTexture(),
        usedTimes: 0
      }, a.memory.textures++, ne = !0), Y[ee].usedTimes++;
      const we = Y[G.__cacheKey];
      we !== void 0 && (Y[G.__cacheKey].usedTimes--, we.usedTimes === 0 && C(D)), G.__cacheKey = ee, G.__webglTexture = Y[ee].texture;
    }
    return ne;
  }
  function Pe(G, D, ne) {
    let ve = s.TEXTURE_2D;
    (D.isDataArrayTexture || D.isCompressedArrayTexture) && (ve = s.TEXTURE_2D_ARRAY), D.isData3DTexture && (ve = s.TEXTURE_3D);
    const Y = be(G, D), ee = D.source;
    t.bindTexture(ve, G.__webglTexture, s.TEXTURE0 + ne);
    const we = i.get(ee);
    if (ee.version !== we.__version || Y === !0) {
      t.activeTexture(s.TEXTURE0 + ne);
      const de = rn.getPrimaries(rn.workingColorSpace), xe = D.colorSpace === Mc ? null : rn.getPrimaries(D.colorSpace), it = D.colorSpace === Mc || de === xe ? s.NONE : s.BROWSER_DEFAULT_WEBGL;
      s.pixelStorei(s.UNPACK_FLIP_Y_WEBGL, D.flipY), s.pixelStorei(s.UNPACK_PREMULTIPLY_ALPHA_WEBGL, D.premultiplyAlpha), s.pixelStorei(s.UNPACK_ALIGNMENT, D.unpackAlignment), s.pixelStorei(s.UNPACK_COLORSPACE_CONVERSION_WEBGL, it);
      let q = v(D.image, !1, n.maxTextureSize);
      q = Ve(D, q);
      const ae = r.convert(D.format, D.colorSpace), Ee = r.convert(D.type);
      let Le = x(D.internalFormat, ae, Ee, D.colorSpace, D.isVideoTexture);
      re(ve, D);
      let Ae;
      const We = D.mipmaps, ot = D.isVideoTexture !== !0, Ct = we.__version === void 0 || Y === !0, F = ee.dataReady, ue = A(D, q);
      if (D.isDepthTexture)
        Le = y(D.format === Fp, D.type), Ct && (ot ? t.texStorage2D(s.TEXTURE_2D, 1, Le, q.width, q.height) : t.texImage2D(s.TEXTURE_2D, 0, Le, q.width, q.height, 0, ae, Ee, null));
      else if (D.isDataTexture)
        if (We.length > 0) {
          ot && Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, We[0].width, We[0].height);
          for (let Q = 0, he = We.length; Q < he; Q++)
            Ae = We[Q], ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ee, Ae.data) : t.texImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, ae, Ee, Ae.data);
          D.generateMipmaps = !1;
        } else
          ot ? (Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, q.width, q.height), F && t.texSubImage2D(s.TEXTURE_2D, 0, 0, 0, q.width, q.height, ae, Ee, q.data)) : t.texImage2D(s.TEXTURE_2D, 0, Le, q.width, q.height, 0, ae, Ee, q.data);
      else if (D.isCompressedTexture)
        if (D.isCompressedArrayTexture) {
          ot && Ct && t.texStorage3D(s.TEXTURE_2D_ARRAY, ue, Le, We[0].width, We[0].height, q.depth);
          for (let Q = 0, he = We.length; Q < he; Q++)
            if (Ae = We[Q], D.format !== no)
              if (ae !== null)
                if (ot) {
                  if (F)
                    if (D.layerUpdates.size > 0) {
                      const Be = aD(Ae.width, Ae.height, D.format, D.type);
                      for (const ft of D.layerUpdates) {
                        const Et = Ae.data.subarray(
                          ft * Be / Ae.data.BYTES_PER_ELEMENT,
                          (ft + 1) * Be / Ae.data.BYTES_PER_ELEMENT
                        );
                        t.compressedTexSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, ft, Ae.width, Ae.height, 1, ae, Et, 0, 0);
                      }
                      D.clearLayerUpdates();
                    } else
                      t.compressedTexSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, 0, Ae.width, Ae.height, q.depth, ae, Ae.data, 0, 0);
                } else
                  t.compressedTexImage3D(s.TEXTURE_2D_ARRAY, Q, Le, Ae.width, Ae.height, q.depth, 0, Ae.data, 0, 0);
              else
                console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()");
            else
              ot ? F && t.texSubImage3D(s.TEXTURE_2D_ARRAY, Q, 0, 0, 0, Ae.width, Ae.height, q.depth, ae, Ee, Ae.data) : t.texImage3D(s.TEXTURE_2D_ARRAY, Q, Le, Ae.width, Ae.height, q.depth, 0, ae, Ee, Ae.data);
        } else {
          ot && Ct && t.texStorage2D(s.TEXTURE_2D, ue, Le, We[0].width, We[0].height);
          for (let Q = 0, he = We.length; Q < he; Q++)
            Ae = We[Q], D.format !== no ? ae !== null ? ot ? F && t.compressedTexSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ae.data) : t.compressedTexImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, Ae.data) : console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()") : ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, Ae.width, Ae.height, ae, Ee, Ae.data) : t.texImage2D(s.TEXTURE_2D, Q, Le, Ae.width, Ae.height, 0, ae, Ee, Ae.data);
        }
      else if (D.isDataArrayTexture)
        if (ot) {
          if (Ct && t.texStorage3D(s.TEXTURE_2D_ARRAY, ue, Le, q.width, q.height, q.depth), F)
            if (D.layerUpdates.size > 0) {
              const Q = aD(q.width, q.height, D.format, D.type);
              for (const he of D.layerUpdates) {
                const Be = q.data.subarray(
                  he * Q / q.data.BYTES_PER_ELEMENT,
                  (he + 1) * Q / q.data.BYTES_PER_ELEMENT
                );
                t.texSubImage3D(s.TEXTURE_2D_ARRAY, 0, 0, 0, he, q.width, q.height, 1, ae, Ee, Be);
              }
              D.clearLayerUpdates();
            } else
              t.texSubImage3D(s.TEXTURE_2D_ARRAY, 0, 0, 0, 0, q.width, q.height, q.depth, ae, Ee, q.data);
        } else
          t.texImage3D(s.TEXTURE_2D_ARRAY, 0, Le, q.width, q.height, q.depth, 0, ae, Ee, q.data);
      else if (D.isData3DTexture)
        ot ? (Ct && t.texStorage3D(s.TEXTURE_3D, ue, Le, q.width, q.height, q.depth), F && t.texSubImage3D(s.TEXTURE_3D, 0, 0, 0, 0, q.width, q.height, q.depth, ae, Ee, q.data)) : t.texImage3D(s.TEXTURE_3D, 0, Le, q.width, q.height, q.depth, 0, ae, Ee, q.data);
      else if (D.isFramebufferTexture) {
        if (Ct)
          if (ot)
            t.texStorage2D(s.TEXTURE_2D, ue, Le, q.width, q.height);
          else {
            let Q = q.width, he = q.height;
            for (let Be = 0; Be < ue; Be++)
              t.texImage2D(s.TEXTURE_2D, Be, Le, Q, he, 0, ae, Ee, null), Q >>= 1, he >>= 1;
          }
      } else if (We.length > 0) {
        if (ot && Ct) {
          const Q = Ze(We[0]);
          t.texStorage2D(s.TEXTURE_2D, ue, Le, Q.width, Q.height);
        }
        for (let Q = 0, he = We.length; Q < he; Q++)
          Ae = We[Q], ot ? F && t.texSubImage2D(s.TEXTURE_2D, Q, 0, 0, ae, Ee, Ae) : t.texImage2D(s.TEXTURE_2D, Q, Le, ae, Ee, Ae);
        D.generateMipmaps = !1;
      } else if (ot) {
        if (Ct) {
          const Q = Ze(q);
          t.texStorage2D(s.TEXTURE_2D, ue, Le, Q.width, Q.height);
        }
        F && t.texSubImage2D(s.TEXTURE_2D, 0, 0, 0, ae, Ee, q);
      } else
        t.texImage2D(s.TEXTURE_2D, 0, Le, ae, Ee, q);
      g(D) && m(ve), we.__version = ee.version, D.onUpdate && D.onUpdate(D);
    }
    G.__version = D.version;
  }
  function J(G, D, ne) {
    if (D.image.length !== 6)
      return;
    const ve = be(G, D), Y = D.source;
    t.bindTexture(s.TEXTURE_CUBE_MAP, G.__webglTexture, s.TEXTURE0 + ne);
    const ee = i.get(Y);
    if (Y.version !== ee.__version || ve === !0) {
      t.activeTexture(s.TEXTURE0 + ne);
      const we = rn.getPrimaries(rn.workingColorSpace), de = D.colorSpace === Mc ? null : rn.getPrimaries(D.colorSpace), xe = D.colorSpace === Mc || we === de ? s.NONE : s.BROWSER_DEFAULT_WEBGL;
      s.pixelStorei(s.UNPACK_FLIP_Y_WEBGL, D.flipY), s.pixelStorei(s.UNPACK_PREMULTIPLY_ALPHA_WEBGL, D.premultiplyAlpha), s.pixelStorei(s.UNPACK_ALIGNMENT, D.unpackAlignment), s.pixelStorei(s.UNPACK_COLORSPACE_CONVERSION_WEBGL, xe);
      const it = D.isCompressedTexture || D.image[0].isCompressedTexture, q = D.image[0] && D.image[0].isDataTexture, ae = [];
      for (let he = 0; he < 6; he++)
        !it && !q ? ae[he] = v(D.image[he], !0, n.maxCubemapSize) : ae[he] = q ? D.image[he].image : D.image[he], ae[he] = Ve(D, ae[he]);
      const Ee = ae[0], Le = r.convert(D.format, D.colorSpace), Ae = r.convert(D.type), We = x(D.internalFormat, Le, Ae, D.colorSpace), ot = D.isVideoTexture !== !0, Ct = ee.__version === void 0 || ve === !0, F = Y.dataReady;
      let ue = A(D, Ee);
      re(s.TEXTURE_CUBE_MAP, D);
      let Q;
      if (it) {
        ot && Ct && t.texStorage2D(s.TEXTURE_CUBE_MAP, ue, We, Ee.width, Ee.height);
        for (let he = 0; he < 6; he++) {
          Q = ae[he].mipmaps;
          for (let Be = 0; Be < Q.length; Be++) {
            const ft = Q[Be];
            D.format !== no ? Le !== null ? ot ? F && t.compressedTexSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, 0, 0, ft.width, ft.height, Le, ft.data) : t.compressedTexImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, We, ft.width, ft.height, 0, ft.data) : console.warn("THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .setTextureCube()") : ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, 0, 0, ft.width, ft.height, Le, Ae, ft.data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be, We, ft.width, ft.height, 0, Le, Ae, ft.data);
          }
        }
      } else {
        if (Q = D.mipmaps, ot && Ct) {
          Q.length > 0 && ue++;
          const he = Ze(ae[0]);
          t.texStorage2D(s.TEXTURE_CUBE_MAP, ue, We, he.width, he.height);
        }
        for (let he = 0; he < 6; he++)
          if (q) {
            ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, 0, 0, ae[he].width, ae[he].height, Le, Ae, ae[he].data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, We, ae[he].width, ae[he].height, 0, Le, Ae, ae[he].data);
            for (let Be = 0; Be < Q.length; Be++) {
              const Et = Q[Be].image[he].image;
              ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, 0, 0, Et.width, Et.height, Le, Ae, Et.data) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, We, Et.width, Et.height, 0, Le, Ae, Et.data);
            }
          } else {
            ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, 0, 0, Le, Ae, ae[he]) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, 0, We, Le, Ae, ae[he]);
            for (let Be = 0; Be < Q.length; Be++) {
              const ft = Q[Be];
              ot ? F && t.texSubImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, 0, 0, Le, Ae, ft.image[he]) : t.texImage2D(s.TEXTURE_CUBE_MAP_POSITIVE_X + he, Be + 1, We, Le, Ae, ft.image[he]);
            }
          }
      }
      g(D) && m(s.TEXTURE_CUBE_MAP), ee.__version = Y.version, D.onUpdate && D.onUpdate(D);
    }
    G.__version = D.version;
  }
  function fe(G, D, ne, ve, Y, ee) {
    const we = r.convert(ne.format, ne.colorSpace), de = r.convert(ne.type), xe = x(ne.internalFormat, we, de, ne.colorSpace);
    if (!i.get(D).__hasExternalTextures) {
      const q = Math.max(1, D.width >> ee), ae = Math.max(1, D.height >> ee);
      Y === s.TEXTURE_3D || Y === s.TEXTURE_2D_ARRAY ? t.texImage3D(Y, ee, xe, q, ae, D.depth, 0, we, de, null) : t.texImage2D(Y, ee, xe, q, ae, 0, we, de, null);
    }
    t.bindFramebuffer(s.FRAMEBUFFER, G), Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, ve, Y, i.get(ne).__webglTexture, 0, qe(D)) : (Y === s.TEXTURE_2D || Y >= s.TEXTURE_CUBE_MAP_POSITIVE_X && Y <= s.TEXTURE_CUBE_MAP_NEGATIVE_Z) && s.framebufferTexture2D(s.FRAMEBUFFER, ve, Y, i.get(ne).__webglTexture, ee), t.bindFramebuffer(s.FRAMEBUFFER, null);
  }
  function Me(G, D, ne) {
    if (s.bindRenderbuffer(s.RENDERBUFFER, G), D.depthBuffer) {
      const ve = D.depthTexture, Y = ve && ve.isDepthTexture ? ve.type : null, ee = y(D.stencilBuffer, Y), we = D.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT, de = qe(D);
      Ie(D) ? o.renderbufferStorageMultisampleEXT(s.RENDERBUFFER, de, ee, D.width, D.height) : ne ? s.renderbufferStorageMultisample(s.RENDERBUFFER, de, ee, D.width, D.height) : s.renderbufferStorage(s.RENDERBUFFER, ee, D.width, D.height), s.framebufferRenderbuffer(s.FRAMEBUFFER, we, s.RENDERBUFFER, G);
    } else {
      const ve = D.textures;
      for (let Y = 0; Y < ve.length; Y++) {
        const ee = ve[Y], we = r.convert(ee.format, ee.colorSpace), de = r.convert(ee.type), xe = x(ee.internalFormat, we, de, ee.colorSpace), it = qe(D);
        ne && Ie(D) === !1 ? s.renderbufferStorageMultisample(s.RENDERBUFFER, it, xe, D.width, D.height) : Ie(D) ? o.renderbufferStorageMultisampleEXT(s.RENDERBUFFER, it, xe, D.width, D.height) : s.renderbufferStorage(s.RENDERBUFFER, xe, D.width, D.height);
      }
    }
    s.bindRenderbuffer(s.RENDERBUFFER, null);
  }
  function ye(G, D) {
    if (D && D.isWebGLCubeRenderTarget)
      throw new Error("Depth Texture with cube render targets is not supported");
    if (t.bindFramebuffer(s.FRAMEBUFFER, G), !(D.depthTexture && D.depthTexture.isDepthTexture))
      throw new Error("renderTarget.depthTexture must be an instance of THREE.DepthTexture");
    (!i.get(D.depthTexture).__webglTexture || D.depthTexture.image.width !== D.width || D.depthTexture.image.height !== D.height) && (D.depthTexture.image.width = D.width, D.depthTexture.image.height = D.height, D.depthTexture.needsUpdate = !0), N(D.depthTexture, 0);
    const ve = i.get(D.depthTexture).__webglTexture, Y = qe(D);
    if (D.depthTexture.format === Qf)
      Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, s.DEPTH_ATTACHMENT, s.TEXTURE_2D, ve, 0, Y) : s.framebufferTexture2D(s.FRAMEBUFFER, s.DEPTH_ATTACHMENT, s.TEXTURE_2D, ve, 0);
    else if (D.depthTexture.format === Fp)
      Ie(D) ? o.framebufferTexture2DMultisampleEXT(s.FRAMEBUFFER, s.DEPTH_STENCIL_ATTACHMENT, s.TEXTURE_2D, ve, 0, Y) : s.framebufferTexture2D(s.FRAMEBUFFER, s.DEPTH_STENCIL_ATTACHMENT, s.TEXTURE_2D, ve, 0);
    else
      throw new Error("Unknown depthTexture format");
  }
  function Ye(G) {
    const D = i.get(G), ne = G.isWebGLCubeRenderTarget === !0;
    if (G.depthTexture && !D.__autoAllocateDepthBuffer) {
      if (ne)
        throw new Error("target.depthTexture not supported in Cube render targets");
      ye(D.__webglFramebuffer, G);
    } else if (ne) {
      D.__webglDepthbuffer = [];
      for (let ve = 0; ve < 6; ve++)
        t.bindFramebuffer(s.FRAMEBUFFER, D.__webglFramebuffer[ve]), D.__webglDepthbuffer[ve] = s.createRenderbuffer(), Me(D.__webglDepthbuffer[ve], G, !1);
    } else
      t.bindFramebuffer(s.FRAMEBUFFER, D.__webglFramebuffer), D.__webglDepthbuffer = s.createRenderbuffer(), Me(D.__webglDepthbuffer, G, !1);
    t.bindFramebuffer(s.FRAMEBUFFER, null);
  }
  function Je(G, D, ne) {
    const ve = i.get(G);
    D !== void 0 && fe(ve.__webglFramebuffer, G, G.texture, s.COLOR_ATTACHMENT0, s.TEXTURE_2D, 0), ne !== void 0 && Ye(G);
  }
  function rt(G) {
    const D = G.texture, ne = i.get(G), ve = i.get(D);
    G.addEventListener("dispose", M);
    const Y = G.textures, ee = G.isWebGLCubeRenderTarget === !0, we = Y.length > 1;
    if (we || (ve.__webglTexture === void 0 && (ve.__webglTexture = s.createTexture()), ve.__version = D.version, a.memory.textures++), ee) {
      ne.__webglFramebuffer = [];
      for (let de = 0; de < 6; de++)
        if (D.mipmaps && D.mipmaps.length > 0) {
          ne.__webglFramebuffer[de] = [];
          for (let xe = 0; xe < D.mipmaps.length; xe++)
            ne.__webglFramebuffer[de][xe] = s.createFramebuffer();
        } else
          ne.__webglFramebuffer[de] = s.createFramebuffer();
    } else {
      if (D.mipmaps && D.mipmaps.length > 0) {
        ne.__webglFramebuffer = [];
        for (let de = 0; de < D.mipmaps.length; de++)
          ne.__webglFramebuffer[de] = s.createFramebuffer();
      } else
        ne.__webglFramebuffer = s.createFramebuffer();
      if (we)
        for (let de = 0, xe = Y.length; de < xe; de++) {
          const it = i.get(Y[de]);
          it.__webglTexture === void 0 && (it.__webglTexture = s.createTexture(), a.memory.textures++);
        }
      if (G.samples > 0 && Ie(G) === !1) {
        ne.__webglMultisampledFramebuffer = s.createFramebuffer(), ne.__webglColorRenderbuffer = [], t.bindFramebuffer(s.FRAMEBUFFER, ne.__webglMultisampledFramebuffer);
        for (let de = 0; de < Y.length; de++) {
          const xe = Y[de];
          ne.__webglColorRenderbuffer[de] = s.createRenderbuffer(), s.bindRenderbuffer(s.RENDERBUFFER, ne.__webglColorRenderbuffer[de]);
          const it = r.convert(xe.format, xe.colorSpace), q = r.convert(xe.type), ae = x(xe.internalFormat, it, q, xe.colorSpace, G.isXRRenderTarget === !0), Ee = qe(G);
          s.renderbufferStorageMultisample(s.RENDERBUFFER, Ee, ae, G.width, G.height), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + de, s.RENDERBUFFER, ne.__webglColorRenderbuffer[de]);
        }
        s.bindRenderbuffer(s.RENDERBUFFER, null), G.depthBuffer && (ne.__webglDepthRenderbuffer = s.createRenderbuffer(), Me(ne.__webglDepthRenderbuffer, G, !0)), t.bindFramebuffer(s.FRAMEBUFFER, null);
      }
    }
    if (ee) {
      t.bindTexture(s.TEXTURE_CUBE_MAP, ve.__webglTexture), re(s.TEXTURE_CUBE_MAP, D);
      for (let de = 0; de < 6; de++)
        if (D.mipmaps && D.mipmaps.length > 0)
          for (let xe = 0; xe < D.mipmaps.length; xe++)
            fe(ne.__webglFramebuffer[de][xe], G, D, s.COLOR_ATTACHMENT0, s.TEXTURE_CUBE_MAP_POSITIVE_X + de, xe);
        else
          fe(ne.__webglFramebuffer[de], G, D, s.COLOR_ATTACHMENT0, s.TEXTURE_CUBE_MAP_POSITIVE_X + de, 0);
      g(D) && m(s.TEXTURE_CUBE_MAP), t.unbindTexture();
    } else if (we) {
      for (let de = 0, xe = Y.length; de < xe; de++) {
        const it = Y[de], q = i.get(it);
        t.bindTexture(s.TEXTURE_2D, q.__webglTexture), re(s.TEXTURE_2D, it), fe(ne.__webglFramebuffer, G, it, s.COLOR_ATTACHMENT0 + de, s.TEXTURE_2D, 0), g(it) && m(s.TEXTURE_2D);
      }
      t.unbindTexture();
    } else {
      let de = s.TEXTURE_2D;
      if ((G.isWebGL3DRenderTarget || G.isWebGLArrayRenderTarget) && (de = G.isWebGL3DRenderTarget ? s.TEXTURE_3D : s.TEXTURE_2D_ARRAY), t.bindTexture(de, ve.__webglTexture), re(de, D), D.mipmaps && D.mipmaps.length > 0)
        for (let xe = 0; xe < D.mipmaps.length; xe++)
          fe(ne.__webglFramebuffer[xe], G, D, s.COLOR_ATTACHMENT0, de, xe);
      else
        fe(ne.__webglFramebuffer, G, D, s.COLOR_ATTACHMENT0, de, 0);
      g(D) && m(de), t.unbindTexture();
    }
    G.depthBuffer && Ye(G);
  }
  function wt(G) {
    const D = G.textures;
    for (let ne = 0, ve = D.length; ne < ve; ne++) {
      const Y = D[ne];
      if (g(Y)) {
        const ee = G.isWebGLCubeRenderTarget ? s.TEXTURE_CUBE_MAP : s.TEXTURE_2D, we = i.get(Y).__webglTexture;
        t.bindTexture(ee, we), m(ee), t.unbindTexture();
      }
    }
  }
  const k = [], Fe = [];
  function Ge(G) {
    if (G.samples > 0) {
      if (Ie(G) === !1) {
        const D = G.textures, ne = G.width, ve = G.height;
        let Y = s.COLOR_BUFFER_BIT;
        const ee = G.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT, we = i.get(G), de = D.length > 1;
        if (de)
          for (let xe = 0; xe < D.length; xe++)
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglMultisampledFramebuffer), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.RENDERBUFFER, null), t.bindFramebuffer(s.FRAMEBUFFER, we.__webglFramebuffer), s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.TEXTURE_2D, null, 0);
        t.bindFramebuffer(s.READ_FRAMEBUFFER, we.__webglMultisampledFramebuffer), t.bindFramebuffer(s.DRAW_FRAMEBUFFER, we.__webglFramebuffer);
        for (let xe = 0; xe < D.length; xe++) {
          if (G.resolveDepthBuffer && (G.depthBuffer && (Y |= s.DEPTH_BUFFER_BIT), G.stencilBuffer && G.resolveStencilBuffer && (Y |= s.STENCIL_BUFFER_BIT)), de) {
            s.framebufferRenderbuffer(s.READ_FRAMEBUFFER, s.COLOR_ATTACHMENT0, s.RENDERBUFFER, we.__webglColorRenderbuffer[xe]);
            const it = i.get(D[xe]).__webglTexture;
            s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0, s.TEXTURE_2D, it, 0);
          }
          s.blitFramebuffer(0, 0, ne, ve, 0, 0, ne, ve, Y, s.NEAREST), l === !0 && (k.length = 0, Fe.length = 0, k.push(s.COLOR_ATTACHMENT0 + xe), G.depthBuffer && G.resolveDepthBuffer === !1 && (k.push(ee), Fe.push(ee), s.invalidateFramebuffer(s.DRAW_FRAMEBUFFER, Fe)), s.invalidateFramebuffer(s.READ_FRAMEBUFFER, k));
        }
        if (t.bindFramebuffer(s.READ_FRAMEBUFFER, null), t.bindFramebuffer(s.DRAW_FRAMEBUFFER, null), de)
          for (let xe = 0; xe < D.length; xe++) {
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglMultisampledFramebuffer), s.framebufferRenderbuffer(s.FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.RENDERBUFFER, we.__webglColorRenderbuffer[xe]);
            const it = i.get(D[xe]).__webglTexture;
            t.bindFramebuffer(s.FRAMEBUFFER, we.__webglFramebuffer), s.framebufferTexture2D(s.DRAW_FRAMEBUFFER, s.COLOR_ATTACHMENT0 + xe, s.TEXTURE_2D, it, 0);
          }
        t.bindFramebuffer(s.DRAW_FRAMEBUFFER, we.__webglMultisampledFramebuffer);
      } else if (G.depthBuffer && G.resolveDepthBuffer === !1 && l) {
        const D = G.stencilBuffer ? s.DEPTH_STENCIL_ATTACHMENT : s.DEPTH_ATTACHMENT;
        s.invalidateFramebuffer(s.DRAW_FRAMEBUFFER, [D]);
      }
    }
  }
  function qe(G) {
    return Math.min(n.maxSamples, G.samples);
  }
  function Ie(G) {
    const D = i.get(G);
    return G.samples > 0 && e.has("WEBGL_multisampled_render_to_texture") === !0 && D.__useRenderToTexture !== !1;
  }
  function vt(G) {
    const D = a.render.frame;
    h.get(G) !== D && (h.set(G, D), G.update());
  }
  function Ve(G, D) {
    const ne = G.colorSpace, ve = G.format, Y = G.type;
    return G.isCompressedTexture === !0 || G.isVideoTexture === !0 || ne !== hh && ne !== Mc && (rn.getTransfer(ne) === Sn ? (ve !== no || Y !== Sl) && console.warn("THREE.WebGLTextures: sRGB encoded textures have to use RGBAFormat and UnsignedByteType.") : console.error("THREE.WebGLTextures: Unsupported texture color space:", ne)), D;
  }
  function Ze(G) {
    return typeof HTMLImageElement < "u" && G instanceof HTMLImageElement ? (c.width = G.naturalWidth || G.width, c.height = G.naturalHeight || G.height) : typeof VideoFrame < "u" && G instanceof VideoFrame ? (c.width = G.displayWidth, c.height = G.displayHeight) : (c.width = G.width, c.height = G.height), c;
  }
  this.allocateTextureUnit = L, this.resetTextureUnits = P, this.setTexture2D = N, this.setTexture2DArray = H, this.setTexture3D = U, this.setTextureCube = W, this.rebindTextures = Je, this.setupRenderTarget = rt, this.updateRenderTargetMipmap = wt, this.updateMultisampleRenderTarget = Ge, this.setupDepthRenderbuffer = Ye, this.setupFrameBufferTexture = fe, this.useMultisampledRTT = Ie;
}
function R4e(s, e) {
  function t(i, n = Mc) {
    let r;
    const a = rn.getTransfer(n);
    if (i === Sl)
      return s.UNSIGNED_BYTE;
    if (i === Pw)
      return s.UNSIGNED_SHORT_4_4_4_4;
    if (i === Rw)
      return s.UNSIGNED_SHORT_5_5_5_1;
    if (i === ZF)
      return s.UNSIGNED_INT_5_9_9_9_REV;
    if (i === qF)
      return s.BYTE;
    if (i === YF)
      return s.SHORT;
    if (i === jg)
      return s.UNSIGNED_SHORT;
    if (i === _w)
      return s.INT;
    if (i === zu)
      return s.UNSIGNED_INT;
    if (i === sl)
      return s.FLOAT;
    if (i === G2)
      return s.HALF_FLOAT;
    if (i === KF)
      return s.ALPHA;
    if (i === JF)
      return s.RGB;
    if (i === no)
      return s.RGBA;
    if (i === QF)
      return s.LUMINANCE;
    if (i === $F)
      return s.LUMINANCE_ALPHA;
    if (i === Qf)
      return s.DEPTH_COMPONENT;
    if (i === Fp)
      return s.DEPTH_STENCIL;
    if (i === eU)
      return s.RED;
    if (i === Dw)
      return s.RED_INTEGER;
    if (i === tU)
      return s.RG;
    if (i === Lw)
      return s.RG_INTEGER;
    if (i === Iw)
      return s.RGBA_INTEGER;
    if (i === Jy || i === Qy || i === $y || i === e9)
      if (a === Sn)
        if (r = e.get("WEBGL_compressed_texture_s3tc_srgb"), r !== null) {
          if (i === Jy)
            return r.COMPRESSED_SRGB_S3TC_DXT1_EXT;
          if (i === Qy)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT1_EXT;
          if (i === $y)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT3_EXT;
          if (i === e9)
            return r.COMPRESSED_SRGB_ALPHA_S3TC_DXT5_EXT;
        } else
          return null;
      else if (r = e.get("WEBGL_compressed_texture_s3tc"), r !== null) {
        if (i === Jy)
          return r.COMPRESSED_RGB_S3TC_DXT1_EXT;
        if (i === Qy)
          return r.COMPRESSED_RGBA_S3TC_DXT1_EXT;
        if (i === $y)
          return r.COMPRESSED_RGBA_S3TC_DXT3_EXT;
        if (i === e9)
          return r.COMPRESSED_RGBA_S3TC_DXT5_EXT;
      } else
        return null;
    if (i === C7 || i === _7 || i === P7 || i === R7)
      if (r = e.get("WEBGL_compressed_texture_pvrtc"), r !== null) {
        if (i === C7)
          return r.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;
        if (i === _7)
          return r.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;
        if (i === P7)
          return r.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG;
        if (i === R7)
          return r.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG;
      } else
        return null;
    if (i === D7 || i === L7 || i === I7)
      if (r = e.get("WEBGL_compressed_texture_etc"), r !== null) {
        if (i === D7 || i === L7)
          return a === Sn ? r.COMPRESSED_SRGB8_ETC2 : r.COMPRESSED_RGB8_ETC2;
        if (i === I7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ETC2_EAC : r.COMPRESSED_RGBA8_ETC2_EAC;
      } else
        return null;
    if (i === O7 || i === N7 || i === B7 || i === F7 || i === U7 || i === z7 || i === k7 || i === G7 || i === H7 || i === V7 || i === W7 || i === X7 || i === j7 || i === q7)
      if (r = e.get("WEBGL_compressed_texture_astc"), r !== null) {
        if (i === O7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_4x4_KHR : r.COMPRESSED_RGBA_ASTC_4x4_KHR;
        if (i === N7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_5x4_KHR : r.COMPRESSED_RGBA_ASTC_5x4_KHR;
        if (i === B7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_5x5_KHR : r.COMPRESSED_RGBA_ASTC_5x5_KHR;
        if (i === F7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_6x5_KHR : r.COMPRESSED_RGBA_ASTC_6x5_KHR;
        if (i === U7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_6x6_KHR : r.COMPRESSED_RGBA_ASTC_6x6_KHR;
        if (i === z7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x5_KHR : r.COMPRESSED_RGBA_ASTC_8x5_KHR;
        if (i === k7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x6_KHR : r.COMPRESSED_RGBA_ASTC_8x6_KHR;
        if (i === G7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_8x8_KHR : r.COMPRESSED_RGBA_ASTC_8x8_KHR;
        if (i === H7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x5_KHR : r.COMPRESSED_RGBA_ASTC_10x5_KHR;
        if (i === V7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x6_KHR : r.COMPRESSED_RGBA_ASTC_10x6_KHR;
        if (i === W7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x8_KHR : r.COMPRESSED_RGBA_ASTC_10x8_KHR;
        if (i === X7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_10x10_KHR : r.COMPRESSED_RGBA_ASTC_10x10_KHR;
        if (i === j7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_12x10_KHR : r.COMPRESSED_RGBA_ASTC_12x10_KHR;
        if (i === q7)
          return a === Sn ? r.COMPRESSED_SRGB8_ALPHA8_ASTC_12x12_KHR : r.COMPRESSED_RGBA_ASTC_12x12_KHR;
      } else
        return null;
    if (i === t9 || i === Y7 || i === Z7)
      if (r = e.get("EXT_texture_compression_bptc"), r !== null) {
        if (i === t9)
          return a === Sn ? r.COMPRESSED_SRGB_ALPHA_BPTC_UNORM_EXT : r.COMPRESSED_RGBA_BPTC_UNORM_EXT;
        if (i === Y7)
          return r.COMPRESSED_RGB_BPTC_SIGNED_FLOAT_EXT;
        if (i === Z7)
          return r.COMPRESSED_RGB_BPTC_UNSIGNED_FLOAT_EXT;
      } else
        return null;
    if (i === iU || i === K7 || i === J7 || i === Q7)
      if (r = e.get("EXT_texture_compression_rgtc"), r !== null) {
        if (i === t9)
          return r.COMPRESSED_RED_RGTC1_EXT;
        if (i === K7)
          return r.COMPRESSED_SIGNED_RED_RGTC1_EXT;
        if (i === J7)
          return r.COMPRESSED_RED_GREEN_RGTC2_EXT;
        if (i === Q7)
          return r.COMPRESSED_SIGNED_RED_GREEN_RGTC2_EXT;
      } else
        return null;
    return i === Bp ? s.UNSIGNED_INT_24_8 : s[i] !== void 0 ? s[i] : null;
  }
  return { convert: t };
}
class D4e extends Ja {
  constructor(e = []) {
    super(), this.isArrayCamera = !0, this.cameras = e;
  }
}
class ey extends Ta {
  constructor() {
    super(), this.isGroup = !0, this.type = "Group";
  }
}
const L4e = { type: "move" };
class y6 {
  constructor() {
    this._targetRay = null, this._grip = null, this._hand = null;
  }
  getHandSpace() {
    return this._hand === null && (this._hand = new ey(), this._hand.matrixAutoUpdate = !1, this._hand.visible = !1, this._hand.joints = {}, this._hand.inputState = { pinching: !1 }), this._hand;
  }
  getTargetRaySpace() {
    return this._targetRay === null && (this._targetRay = new ey(), this._targetRay.matrixAutoUpdate = !1, this._targetRay.visible = !1, this._targetRay.hasLinearVelocity = !1, this._targetRay.linearVelocity = new je(), this._targetRay.hasAngularVelocity = !1, this._targetRay.angularVelocity = new je()), this._targetRay;
  }
  getGripSpace() {
    return this._grip === null && (this._grip = new ey(), this._grip.matrixAutoUpdate = !1, this._grip.visible = !1, this._grip.hasLinearVelocity = !1, this._grip.linearVelocity = new je(), this._grip.hasAngularVelocity = !1, this._grip.angularVelocity = new je()), this._grip;
  }
  dispatchEvent(e) {
    return this._targetRay !== null && this._targetRay.dispatchEvent(e), this._grip !== null && this._grip.dispatchEvent(e), this._hand !== null && this._hand.dispatchEvent(e), this;
  }
  connect(e) {
    if (e && e.hand) {
      const t = this._hand;
      if (t)
        for (const i of e.hand.values())
          this._getHandJoint(t, i);
    }
    return this.dispatchEvent({ type: "connected", data: e }), this;
  }
  disconnect(e) {
    return this.dispatchEvent({ type: "disconnected", data: e }), this._targetRay !== null && (this._targetRay.visible = !1), this._grip !== null && (this._grip.visible = !1), this._hand !== null && (this._hand.visible = !1), this;
  }
  update(e, t, i) {
    let n = null, r = null, a = null;
    const o = this._targetRay, l = this._grip, c = this._hand;
    if (e && t.session.visibilityState !== "visible-blurred") {
      if (c && e.hand) {
        a = !0;
        for (const v of e.hand.values()) {
          const g = t.getJointPose(v, i), m = this._getHandJoint(c, v);
          g !== null && (m.matrix.fromArray(g.transform.matrix), m.matrix.decompose(m.position, m.rotation, m.scale), m.matrixWorldNeedsUpdate = !0, m.jointRadius = g.radius), m.visible = g !== null;
        }
        const h = c.joints["index-finger-tip"], u = c.joints["thumb-tip"], d = h.position.distanceTo(u.position), f = 0.02, p = 5e-3;
        c.inputState.pinching && d > f + p ? (c.inputState.pinching = !1, this.dispatchEvent({
          type: "pinchend",
          handedness: e.handedness,
          target: this
        })) : !c.inputState.pinching && d <= f - p && (c.inputState.pinching = !0, this.dispatchEvent({
          type: "pinchstart",
          handedness: e.handedness,
          target: this
        }));
      } else
        l !== null && e.gripSpace && (r = t.getPose(e.gripSpace, i), r !== null && (l.matrix.fromArray(r.transform.matrix), l.matrix.decompose(l.position, l.rotation, l.scale), l.matrixWorldNeedsUpdate = !0, r.linearVelocity ? (l.hasLinearVelocity = !0, l.linearVelocity.copy(r.linearVelocity)) : l.hasLinearVelocity = !1, r.angularVelocity ? (l.hasAngularVelocity = !0, l.angularVelocity.copy(r.angularVelocity)) : l.hasAngularVelocity = !1));
      o !== null && (n = t.getPose(e.targetRaySpace, i), n === null && r !== null && (n = r), n !== null && (o.matrix.fromArray(n.transform.matrix), o.matrix.decompose(o.position, o.rotation, o.scale), o.matrixWorldNeedsUpdate = !0, n.linearVelocity ? (o.hasLinearVelocity = !0, o.linearVelocity.copy(n.linearVelocity)) : o.hasLinearVelocity = !1, n.angularVelocity ? (o.hasAngularVelocity = !0, o.angularVelocity.copy(n.angularVelocity)) : o.hasAngularVelocity = !1, this.dispatchEvent(L4e)));
    }
    return o !== null && (o.visible = n !== null), l !== null && (l.visible = r !== null), c !== null && (c.visible = a !== null), this;
  }
  // private method
  _getHandJoint(e, t) {
    if (e.joints[t.jointName] === void 0) {
      const i = new ey();
      i.matrixAutoUpdate = !1, i.visible = !1, e.joints[t.jointName] = i, e.add(i);
    }
    return e.joints[t.jointName];
  }
}
const I4e = `
void main() {

	gl_Position = vec4( position, 1.0 );

}`, O4e = `
uniform sampler2DArray depthColor;
uniform float depthWidth;
uniform float depthHeight;

void main() {

	vec2 coord = vec2( gl_FragCoord.x / depthWidth, gl_FragCoord.y / depthHeight );

	if ( coord.x >= 1.0 ) {

		gl_FragDepth = texture( depthColor, vec3( coord.x - 1.0, coord.y, 1 ) ).r;

	} else {

		gl_FragDepth = texture( depthColor, vec3( coord.x, coord.y, 0 ) ).r;

	}

}`;
class N4e {
  constructor() {
    this.texture = null, this.mesh = null, this.depthNear = 0, this.depthFar = 0;
  }
  init(e, t, i) {
    if (this.texture === null) {
      const n = new Ur(), r = e.properties.get(n);
      r.__webglTexture = t.texture, (t.depthNear != i.depthNear || t.depthFar != i.depthFar) && (this.depthNear = t.depthNear, this.depthFar = t.depthFar), this.texture = n;
    }
  }
  getMesh(e) {
    if (this.texture !== null && this.mesh === null) {
      const t = e.cameras[0].viewport, i = new Jc({
        vertexShader: I4e,
        fragmentShader: O4e,
        uniforms: {
          depthColor: { value: this.texture },
          depthWidth: { value: t.z },
          depthHeight: { value: t.w }
        }
      });
      this.mesh = new al(new p8(20, 20), i);
    }
    return this.mesh;
  }
  reset() {
    this.texture = null, this.mesh = null;
  }
  getDepthTexture() {
    return this.texture;
  }
}
class B4e extends nm {
  constructor(e, t) {
    super();
    const i = this;
    let n = null, r = 1, a = null, o = "local-floor", l = 1, c = null, h = null, u = null, d = null, f = null, p = null;
    const v = new N4e(), g = t.getContextAttributes();
    let m = null, x = null;
    const y = [], A = [], b = new tn();
    let M = null;
    const w = new Ja();
    w.layers.enable(1), w.viewport = new Cs();
    const C = new Ja();
    C.layers.enable(2), C.viewport = new Cs();
    const T = [w, C], E = new D4e();
    E.layers.enable(1), E.layers.enable(2);
    let P = null, L = null;
    this.cameraAutoUpdate = !0, this.enabled = !1, this.isPresenting = !1, this.getController = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new y6(), y[J] = fe), fe.getTargetRaySpace();
    }, this.getControllerGrip = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new y6(), y[J] = fe), fe.getGripSpace();
    }, this.getHand = function(J) {
      let fe = y[J];
      return fe === void 0 && (fe = new y6(), y[J] = fe), fe.getHandSpace();
    };
    function I(J) {
      const fe = A.indexOf(J.inputSource);
      if (fe === -1)
        return;
      const Me = y[fe];
      Me !== void 0 && (Me.update(J.inputSource, J.frame, c || a), Me.dispatchEvent({ type: J.type, data: J.inputSource }));
    }
    function N() {
      n.removeEventListener("select", I), n.removeEventListener("selectstart", I), n.removeEventListener("selectend", I), n.removeEventListener("squeeze", I), n.removeEventListener("squeezestart", I), n.removeEventListener("squeezeend", I), n.removeEventListener("end", N), n.removeEventListener("inputsourceschange", H);
      for (let J = 0; J < y.length; J++) {
        const fe = A[J];
        fe !== null && (A[J] = null, y[J].disconnect(fe));
      }
      P = null, L = null, v.reset(), e.setRenderTarget(m), f = null, d = null, u = null, n = null, x = null, Pe.stop(), i.isPresenting = !1, e.setPixelRatio(M), e.setSize(b.width, b.height, !1), i.dispatchEvent({ type: "sessionend" });
    }
    this.setFramebufferScaleFactor = function(J) {
      r = J, i.isPresenting === !0 && console.warn("THREE.WebXRManager: Cannot change framebuffer scale while presenting.");
    }, this.setReferenceSpaceType = function(J) {
      o = J, i.isPresenting === !0 && console.warn("THREE.WebXRManager: Cannot change reference space type while presenting.");
    }, this.getReferenceSpace = function() {
      return c || a;
    }, this.setReferenceSpace = function(J) {
      c = J;
    }, this.getBaseLayer = function() {
      return d !== null ? d : f;
    }, this.getBinding = function() {
      return u;
    }, this.getFrame = function() {
      return p;
    }, this.getSession = function() {
      return n;
    }, this.setSession = async function(J) {
      if (n = J, n !== null) {
        if (m = e.getRenderTarget(), n.addEventListener("select", I), n.addEventListener("selectstart", I), n.addEventListener("selectend", I), n.addEventListener("squeeze", I), n.addEventListener("squeezestart", I), n.addEventListener("squeezeend", I), n.addEventListener("end", N), n.addEventListener("inputsourceschange", H), g.xrCompatible !== !0 && await t.makeXRCompatible(), M = e.getPixelRatio(), e.getSize(b), n.renderState.layers === void 0) {
          const fe = {
            antialias: g.antialias,
            alpha: !0,
            depth: g.depth,
            stencil: g.stencil,
            framebufferScaleFactor: r
          };
          f = new XRWebGLLayer(n, t, fe), n.updateRenderState({ baseLayer: f }), e.setPixelRatio(1), e.setSize(f.framebufferWidth, f.framebufferHeight, !1), x = new ku(
            f.framebufferWidth,
            f.framebufferHeight,
            {
              format: no,
              type: Sl,
              colorSpace: e.outputColorSpace,
              stencilBuffer: g.stencil
            }
          );
        } else {
          let fe = null, Me = null, ye = null;
          g.depth && (ye = g.stencil ? t.DEPTH24_STENCIL8 : t.DEPTH_COMPONENT24, fe = g.stencil ? Fp : Qf, Me = g.stencil ? Bp : zu);
          const Ye = {
            colorFormat: t.RGBA8,
            depthFormat: ye,
            scaleFactor: r
          };
          u = new XRWebGLBinding(n, t), d = u.createProjectionLayer(Ye), n.updateRenderState({ layers: [d] }), e.setPixelRatio(1), e.setSize(d.textureWidth, d.textureHeight, !1), x = new ku(
            d.textureWidth,
            d.textureHeight,
            {
              format: no,
              type: Sl,
              depthTexture: new yU(d.textureWidth, d.textureHeight, Me, void 0, void 0, void 0, void 0, void 0, void 0, fe),
              stencilBuffer: g.stencil,
              colorSpace: e.outputColorSpace,
              samples: g.antialias ? 4 : 0,
              resolveDepthBuffer: d.ignoreDepthValues === !1
            }
          );
        }
        x.isXRRenderTarget = !0, this.setFoveation(l), c = null, a = await n.requestReferenceSpace(o), Pe.setContext(n), Pe.start(), i.isPresenting = !0, i.dispatchEvent({ type: "sessionstart" });
      }
    }, this.getEnvironmentBlendMode = function() {
      if (n !== null)
        return n.environmentBlendMode;
    }, this.getDepthTexture = function() {
      return v.getDepthTexture();
    };
    function H(J) {
      for (let fe = 0; fe < J.removed.length; fe++) {
        const Me = J.removed[fe], ye = A.indexOf(Me);
        ye >= 0 && (A[ye] = null, y[ye].disconnect(Me));
      }
      for (let fe = 0; fe < J.added.length; fe++) {
        const Me = J.added[fe];
        let ye = A.indexOf(Me);
        if (ye === -1) {
          for (let Je = 0; Je < y.length; Je++)
            if (Je >= A.length) {
              A.push(Me), ye = Je;
              break;
            } else if (A[Je] === null) {
              A[Je] = Me, ye = Je;
              break;
            }
          if (ye === -1)
            break;
        }
        const Ye = y[ye];
        Ye && Ye.connect(Me);
      }
    }
    const U = new je(), W = new je();
    function X(J, fe, Me) {
      U.setFromMatrixPosition(fe.matrixWorld), W.setFromMatrixPosition(Me.matrixWorld);
      const ye = U.distanceTo(W), Ye = fe.projectionMatrix.elements, Je = Me.projectionMatrix.elements, rt = Ye[14] / (Ye[10] - 1), wt = Ye[14] / (Ye[10] + 1), k = (Ye[9] + 1) / Ye[5], Fe = (Ye[9] - 1) / Ye[5], Ge = (Ye[8] - 1) / Ye[0], qe = (Je[8] + 1) / Je[0], Ie = rt * Ge, vt = rt * qe, Ve = ye / (-Ge + qe), Ze = Ve * -Ge;
      fe.matrixWorld.decompose(J.position, J.quaternion, J.scale), J.translateX(Ze), J.translateZ(Ve), J.matrixWorld.compose(J.position, J.quaternion, J.scale), J.matrixWorldInverse.copy(J.matrixWorld).invert();
      const G = rt + Ve, D = wt + Ve, ne = Ie - Ze, ve = vt + (ye - Ze), Y = k * wt / D * G, ee = Fe * wt / D * G;
      J.projectionMatrix.makePerspective(ne, ve, Y, ee, G, D), J.projectionMatrixInverse.copy(J.projectionMatrix).invert();
    }
    function K(J, fe) {
      fe === null ? J.matrixWorld.copy(J.matrix) : J.matrixWorld.multiplyMatrices(fe.matrixWorld, J.matrix), J.matrixWorldInverse.copy(J.matrixWorld).invert();
    }
    this.updateCamera = function(J) {
      if (n === null)
        return;
      v.texture !== null && (J.near = v.depthNear, J.far = v.depthFar), E.near = C.near = w.near = J.near, E.far = C.far = w.far = J.far, (P !== E.near || L !== E.far) && (n.updateRenderState({
        depthNear: E.near,
        depthFar: E.far
      }), P = E.near, L = E.far, w.near = P, w.far = L, C.near = P, C.far = L, w.updateProjectionMatrix(), C.updateProjectionMatrix(), J.updateProjectionMatrix());
      const fe = J.parent, Me = E.cameras;
      K(E, fe);
      for (let ye = 0; ye < Me.length; ye++)
        K(Me[ye], fe);
      Me.length === 2 ? X(E, w, C) : E.projectionMatrix.copy(w.projectionMatrix), te(J, E, fe);
    };
    function te(J, fe, Me) {
      Me === null ? J.matrix.copy(fe.matrixWorld) : (J.matrix.copy(Me.matrixWorld), J.matrix.invert(), J.matrix.multiply(fe.matrixWorld)), J.matrix.decompose(J.position, J.quaternion, J.scale), J.updateMatrixWorld(!0), J.projectionMatrix.copy(fe.projectionMatrix), J.projectionMatrixInverse.copy(fe.projectionMatrixInverse), J.isPerspectiveCamera && (J.fov = u7 * 2 * Math.atan(1 / J.projectionMatrix.elements[5]), J.zoom = 1);
    }
    this.getCamera = function() {
      return E;
    }, this.getFoveation = function() {
      if (!(d === null && f === null))
        return l;
    }, this.setFoveation = function(J) {
      l = J, d !== null && (d.fixedFoveation = J), f !== null && f.fixedFoveation !== void 0 && (f.fixedFoveation = J);
    }, this.hasDepthSensing = function() {
      return v.texture !== null;
    }, this.getDepthSensingMesh = function() {
      return v.getMesh(E);
    };
    let re = null;
    function be(J, fe) {
      if (h = fe.getViewerPose(c || a), p = fe, h !== null) {
        const Me = h.views;
        f !== null && (e.setRenderTargetFramebuffer(x, f.framebuffer), e.setRenderTarget(x));
        let ye = !1;
        Me.length !== E.cameras.length && (E.cameras.length = 0, ye = !0);
        for (let Je = 0; Je < Me.length; Je++) {
          const rt = Me[Je];
          let wt = null;
          if (f !== null)
            wt = f.getViewport(rt);
          else {
            const Fe = u.getViewSubImage(d, rt);
            wt = Fe.viewport, Je === 0 && (e.setRenderTargetTextures(
              x,
              Fe.colorTexture,
              d.ignoreDepthValues ? void 0 : Fe.depthStencilTexture
            ), e.setRenderTarget(x));
          }
          let k = T[Je];
          k === void 0 && (k = new Ja(), k.layers.enable(Je), k.viewport = new Cs(), T[Je] = k), k.matrix.fromArray(rt.transform.matrix), k.matrix.decompose(k.position, k.quaternion, k.scale), k.projectionMatrix.fromArray(rt.projectionMatrix), k.projectionMatrixInverse.copy(k.projectionMatrix).invert(), k.viewport.set(wt.x, wt.y, wt.width, wt.height), Je === 0 && (E.matrix.copy(k.matrix), E.matrix.decompose(E.position, E.quaternion, E.scale)), ye === !0 && E.cameras.push(k);
        }
        const Ye = n.enabledFeatures;
        if (Ye && Ye.includes("depth-sensing")) {
          const Je = u.getDepthInformation(Me[0]);
          Je && Je.isValid && Je.texture && v.init(e, Je, n.renderState);
        }
      }
      for (let Me = 0; Me < y.length; Me++) {
        const ye = A[Me], Ye = y[Me];
        ye !== null && Ye !== void 0 && Ye.update(ye, fe, c || a);
      }
      re && re(J, fe), fe.detectedPlanes && i.dispatchEvent({ type: "planesdetected", data: fe }), p = null;
    }
    const Pe = new aU();
    Pe.setAnimationLoop(be), this.setAnimationLoop = function(J) {
      re = J;
    }, this.dispose = function() {
    };
  }
}
const Hh = /* @__PURE__ */ new id(), F4e = /* @__PURE__ */ new ps();
function U4e(s, e) {
  function t(g, m) {
    g.matrixAutoUpdate === !0 && g.updateMatrix(), m.value.copy(g.matrix);
  }
  function i(g, m) {
    m.color.getRGB(g.fogColor.value, dU(s)), m.isFog ? (g.fogNear.value = m.near, g.fogFar.value = m.far) : m.isFogExp2 && (g.fogDensity.value = m.density);
  }
  function n(g, m, x, y, A) {
    m.isMeshBasicMaterial || m.isMeshLambertMaterial ? r(g, m) : m.isMeshToonMaterial ? (r(g, m), u(g, m)) : m.isMeshPhongMaterial ? (r(g, m), h(g, m)) : m.isMeshStandardMaterial ? (r(g, m), d(g, m), m.isMeshPhysicalMaterial && f(g, m, A)) : m.isMeshMatcapMaterial ? (r(g, m), p(g, m)) : m.isMeshDepthMaterial ? r(g, m) : m.isMeshDistanceMaterial ? (r(g, m), v(g, m)) : m.isMeshNormalMaterial ? r(g, m) : m.isLineBasicMaterial ? (a(g, m), m.isLineDashedMaterial && o(g, m)) : m.isPointsMaterial ? l(g, m, x, y) : m.isSpriteMaterial ? c(g, m) : m.isShadowMaterial ? (g.color.value.copy(m.color), g.opacity.value = m.opacity) : m.isShaderMaterial && (m.uniformsNeedUpdate = !1);
  }
  function r(g, m) {
    g.opacity.value = m.opacity, m.color && g.diffuse.value.copy(m.color), m.emissive && g.emissive.value.copy(m.emissive).multiplyScalar(m.emissiveIntensity), m.map && (g.map.value = m.map, t(m.map, g.mapTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.bumpMap && (g.bumpMap.value = m.bumpMap, t(m.bumpMap, g.bumpMapTransform), g.bumpScale.value = m.bumpScale, m.side === Fr && (g.bumpScale.value *= -1)), m.normalMap && (g.normalMap.value = m.normalMap, t(m.normalMap, g.normalMapTransform), g.normalScale.value.copy(m.normalScale), m.side === Fr && g.normalScale.value.negate()), m.displacementMap && (g.displacementMap.value = m.displacementMap, t(m.displacementMap, g.displacementMapTransform), g.displacementScale.value = m.displacementScale, g.displacementBias.value = m.displacementBias), m.emissiveMap && (g.emissiveMap.value = m.emissiveMap, t(m.emissiveMap, g.emissiveMapTransform)), m.specularMap && (g.specularMap.value = m.specularMap, t(m.specularMap, g.specularMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
    const x = e.get(m), y = x.envMap, A = x.envMapRotation;
    y && (g.envMap.value = y, Hh.copy(A), Hh.x *= -1, Hh.y *= -1, Hh.z *= -1, y.isCubeTexture && y.isRenderTargetTexture === !1 && (Hh.y *= -1, Hh.z *= -1), g.envMapRotation.value.setFromMatrix4(F4e.makeRotationFromEuler(Hh)), g.flipEnvMap.value = y.isCubeTexture && y.isRenderTargetTexture === !1 ? -1 : 1, g.reflectivity.value = m.reflectivity, g.ior.value = m.ior, g.refractionRatio.value = m.refractionRatio), m.lightMap && (g.lightMap.value = m.lightMap, g.lightMapIntensity.value = m.lightMapIntensity, t(m.lightMap, g.lightMapTransform)), m.aoMap && (g.aoMap.value = m.aoMap, g.aoMapIntensity.value = m.aoMapIntensity, t(m.aoMap, g.aoMapTransform));
  }
  function a(g, m) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, m.map && (g.map.value = m.map, t(m.map, g.mapTransform));
  }
  function o(g, m) {
    g.dashSize.value = m.dashSize, g.totalSize.value = m.dashSize + m.gapSize, g.scale.value = m.scale;
  }
  function l(g, m, x, y) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, g.size.value = m.size * x, g.scale.value = y * 0.5, m.map && (g.map.value = m.map, t(m.map, g.uvTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
  }
  function c(g, m) {
    g.diffuse.value.copy(m.color), g.opacity.value = m.opacity, g.rotation.value = m.rotation, m.map && (g.map.value = m.map, t(m.map, g.mapTransform)), m.alphaMap && (g.alphaMap.value = m.alphaMap, t(m.alphaMap, g.alphaMapTransform)), m.alphaTest > 0 && (g.alphaTest.value = m.alphaTest);
  }
  function h(g, m) {
    g.specular.value.copy(m.specular), g.shininess.value = Math.max(m.shininess, 1e-4);
  }
  function u(g, m) {
    m.gradientMap && (g.gradientMap.value = m.gradientMap);
  }
  function d(g, m) {
    g.metalness.value = m.metalness, m.metalnessMap && (g.metalnessMap.value = m.metalnessMap, t(m.metalnessMap, g.metalnessMapTransform)), g.roughness.value = m.roughness, m.roughnessMap && (g.roughnessMap.value = m.roughnessMap, t(m.roughnessMap, g.roughnessMapTransform)), m.envMap && (g.envMapIntensity.value = m.envMapIntensity);
  }
  function f(g, m, x) {
    g.ior.value = m.ior, m.sheen > 0 && (g.sheenColor.value.copy(m.sheenColor).multiplyScalar(m.sheen), g.sheenRoughness.value = m.sheenRoughness, m.sheenColorMap && (g.sheenColorMap.value = m.sheenColorMap, t(m.sheenColorMap, g.sheenColorMapTransform)), m.sheenRoughnessMap && (g.sheenRoughnessMap.value = m.sheenRoughnessMap, t(m.sheenRoughnessMap, g.sheenRoughnessMapTransform))), m.clearcoat > 0 && (g.clearcoat.value = m.clearcoat, g.clearcoatRoughness.value = m.clearcoatRoughness, m.clearcoatMap && (g.clearcoatMap.value = m.clearcoatMap, t(m.clearcoatMap, g.clearcoatMapTransform)), m.clearcoatRoughnessMap && (g.clearcoatRoughnessMap.value = m.clearcoatRoughnessMap, t(m.clearcoatRoughnessMap, g.clearcoatRoughnessMapTransform)), m.clearcoatNormalMap && (g.clearcoatNormalMap.value = m.clearcoatNormalMap, t(m.clearcoatNormalMap, g.clearcoatNormalMapTransform), g.clearcoatNormalScale.value.copy(m.clearcoatNormalScale), m.side === Fr && g.clearcoatNormalScale.value.negate())), m.dispersion > 0 && (g.dispersion.value = m.dispersion), m.iridescence > 0 && (g.iridescence.value = m.iridescence, g.iridescenceIOR.value = m.iridescenceIOR, g.iridescenceThicknessMinimum.value = m.iridescenceThicknessRange[0], g.iridescenceThicknessMaximum.value = m.iridescenceThicknessRange[1], m.iridescenceMap && (g.iridescenceMap.value = m.iridescenceMap, t(m.iridescenceMap, g.iridescenceMapTransform)), m.iridescenceThicknessMap && (g.iridescenceThicknessMap.value = m.iridescenceThicknessMap, t(m.iridescenceThicknessMap, g.iridescenceThicknessMapTransform))), m.transmission > 0 && (g.transmission.value = m.transmission, g.transmissionSamplerMap.value = x.texture, g.transmissionSamplerSize.value.set(x.width, x.height), m.transmissionMap && (g.transmissionMap.value = m.transmissionMap, t(m.transmissionMap, g.transmissionMapTransform)), g.thickness.value = m.thickness, m.thicknessMap && (g.thicknessMap.value = m.thicknessMap, t(m.thicknessMap, g.thicknessMapTransform)), g.attenuationDistance.value = m.attenuationDistance, g.attenuationColor.value.copy(m.attenuationColor)), m.anisotropy > 0 && (g.anisotropyVector.value.set(m.anisotropy * Math.cos(m.anisotropyRotation), m.anisotropy * Math.sin(m.anisotropyRotation)), m.anisotropyMap && (g.anisotropyMap.value = m.anisotropyMap, t(m.anisotropyMap, g.anisotropyMapTransform))), g.specularIntensity.value = m.specularIntensity, g.specularColor.value.copy(m.specularColor), m.specularColorMap && (g.specularColorMap.value = m.specularColorMap, t(m.specularColorMap, g.specularColorMapTransform)), m.specularIntensityMap && (g.specularIntensityMap.value = m.specularIntensityMap, t(m.specularIntensityMap, g.specularIntensityMapTransform));
  }
  function p(g, m) {
    m.matcap && (g.matcap.value = m.matcap);
  }
  function v(g, m) {
    const x = e.get(m).light;
    g.referencePosition.value.setFromMatrixPosition(x.matrixWorld), g.nearDistance.value = x.shadow.camera.near, g.farDistance.value = x.shadow.camera.far;
  }
  return {
    refreshFogUniforms: i,
    refreshMaterialUniforms: n
  };
}
function z4e(s, e, t, i) {
  let n = {}, r = {}, a = [];
  const o = s.getParameter(s.MAX_UNIFORM_BUFFER_BINDINGS);
  function l(x, y) {
    const A = y.program;
    i.uniformBlockBinding(x, A);
  }
  function c(x, y) {
    let A = n[x.id];
    A === void 0 && (p(x), A = h(x), n[x.id] = A, x.addEventListener("dispose", g));
    const b = y.program;
    i.updateUBOMapping(x, b);
    const M = e.render.frame;
    r[x.id] !== M && (d(x), r[x.id] = M);
  }
  function h(x) {
    const y = u();
    x.__bindingPointIndex = y;
    const A = s.createBuffer(), b = x.__size, M = x.usage;
    return s.bindBuffer(s.UNIFORM_BUFFER, A), s.bufferData(s.UNIFORM_BUFFER, b, M), s.bindBuffer(s.UNIFORM_BUFFER, null), s.bindBufferBase(s.UNIFORM_BUFFER, y, A), A;
  }
  function u() {
    for (let x = 0; x < o; x++)
      if (a.indexOf(x) === -1)
        return a.push(x), x;
    return console.error("THREE.WebGLRenderer: Maximum number of simultaneously usable uniforms groups reached."), 0;
  }
  function d(x) {
    const y = n[x.id], A = x.uniforms, b = x.__cache;
    s.bindBuffer(s.UNIFORM_BUFFER, y);
    for (let M = 0, w = A.length; M < w; M++) {
      const C = Array.isArray(A[M]) ? A[M] : [A[M]];
      for (let T = 0, E = C.length; T < E; T++) {
        const P = C[T];
        if (f(P, M, T, b) === !0) {
          const L = P.__offset, I = Array.isArray(P.value) ? P.value : [P.value];
          let N = 0;
          for (let H = 0; H < I.length; H++) {
            const U = I[H], W = v(U);
            typeof U == "number" || typeof U == "boolean" ? (P.__data[0] = U, s.bufferSubData(s.UNIFORM_BUFFER, L + N, P.__data)) : U.isMatrix3 ? (P.__data[0] = U.elements[0], P.__data[1] = U.elements[1], P.__data[2] = U.elements[2], P.__data[3] = 0, P.__data[4] = U.elements[3], P.__data[5] = U.elements[4], P.__data[6] = U.elements[5], P.__data[7] = 0, P.__data[8] = U.elements[6], P.__data[9] = U.elements[7], P.__data[10] = U.elements[8], P.__data[11] = 0) : (U.toArray(P.__data, N), N += W.storage / Float32Array.BYTES_PER_ELEMENT);
          }
          s.bufferSubData(s.UNIFORM_BUFFER, L, P.__data);
        }
      }
    }
    s.bindBuffer(s.UNIFORM_BUFFER, null);
  }
  function f(x, y, A, b) {
    const M = x.value, w = y + "_" + A;
    if (b[w] === void 0)
      return typeof M == "number" || typeof M == "boolean" ? b[w] = M : b[w] = M.clone(), !0;
    {
      const C = b[w];
      if (typeof M == "number" || typeof M == "boolean") {
        if (C !== M)
          return b[w] = M, !0;
      } else if (C.equals(M) === !1)
        return C.copy(M), !0;
    }
    return !1;
  }
  function p(x) {
    const y = x.uniforms;
    let A = 0;
    const b = 16;
    for (let w = 0, C = y.length; w < C; w++) {
      const T = Array.isArray(y[w]) ? y[w] : [y[w]];
      for (let E = 0, P = T.length; E < P; E++) {
        const L = T[E], I = Array.isArray(L.value) ? L.value : [L.value];
        for (let N = 0, H = I.length; N < H; N++) {
          const U = I[N], W = v(U), X = A % b;
          X !== 0 && b - X < W.boundary && (A += b - X), L.__data = new Float32Array(W.storage / Float32Array.BYTES_PER_ELEMENT), L.__offset = A, A += W.storage;
        }
      }
    }
    const M = A % b;
    return M > 0 && (A += b - M), x.__size = A, x.__cache = {}, this;
  }
  function v(x) {
    const y = {
      boundary: 0,
      // bytes
      storage: 0
      // bytes
    };
    return typeof x == "number" || typeof x == "boolean" ? (y.boundary = 4, y.storage = 4) : x.isVector2 ? (y.boundary = 8, y.storage = 8) : x.isVector3 || x.isColor ? (y.boundary = 16, y.storage = 12) : x.isVector4 ? (y.boundary = 16, y.storage = 16) : x.isMatrix3 ? (y.boundary = 48, y.storage = 48) : x.isMatrix4 ? (y.boundary = 64, y.storage = 64) : x.isTexture ? console.warn("THREE.WebGLRenderer: Texture samplers can not be part of an uniforms group.") : console.warn("THREE.WebGLRenderer: Unsupported uniform value type.", x), y;
  }
  function g(x) {
    const y = x.target;
    y.removeEventListener("dispose", g);
    const A = a.indexOf(y.__bindingPointIndex);
    a.splice(A, 1), s.deleteBuffer(n[y.id]), delete n[y.id], delete r[y.id];
  }
  function m() {
    for (const x in n)
      s.deleteBuffer(n[x]);
    a = [], n = {}, r = {};
  }
  return {
    bind: l,
    update: c,
    dispose: m
  };
}
function oD(s, { defines: e = {}, header: t = "", main: i = "", ...n }) {
  let r = s;
  const a = (l, c, h) => l.split(c).join(h);
  return Object.keys(n).forEach((l) => {
    r = a(r, l, n[l]);
  }), r = r.replace(
    "void main() {",
    `
			${t}
			void main() {
				${i}
		`
  ), `
		${Object.keys(e).map((l) => `#define ${l} ${e[l]}`).join(`
`)}
		${r}
	`;
}
const k4e = (
  /* glsl */
  `
#if NUM_PROJECT_CAMERAS > 0

struct ProjectCamera {
  mat4 viewMatrix;
  mat4 projectionMatrix;
  vec3 projPosition;
  vec3 projDirection;
  bool visible;
};

uniform ProjectCamera twin_uProjectCameras[NUM_PROJECT_CAMERAS];

varying vec3 twin_vSavedNormal;
varying vec4 twin_vTexCoords[NUM_PROJECT_CAMERAS];
varying vec4 twin_vWorldPosition;

#endif
`
), G4e = (
  /* glsl */
  `
#if NUM_PROJECT_CAMERAS > 0

#ifdef USE_INSTANCING
  mat4 modelTransformMatrix = instanceMatrix;
#else
  mat4 modelTransformMatrix = modelMatrix;
#endif

twin_vSavedNormal = mat3(modelTransformMatrix) * normal;

twin_vWorldPosition = modelTransformMatrix * vec4(position, 1.0);

#pragma unroll_loop_start
for (int i = 0; i < NUM_PROJECT_CAMERAS; i ++) {

twin_vTexCoords[i] = twin_uProjectCameras[i].projectionMatrix * twin_uProjectCameras[i].viewMatrix * twin_vWorldPosition;

}
#pragma unroll_loop_end

#endif
`
), H4e = (
  /* glsl */
  `
#if NUM_PROJECT_CAMERAS > 0
  
  struct ProjectCamera {
    mat4 viewMatrix;
    mat4 projectionMatrix;
    vec3 projPosition;
    vec3 projDirection;
    bool visible;
	};

  uniform ProjectCamera twin_uProjectCameras[NUM_PROJECT_CAMERAS];

  uniform sampler2D twin_uVideoTextures[NUM_PROJECT_CAMERAS];
  uniform sampler2D twin_uAlphaTextures[NUM_PROJECT_CAMERAS];

  uniform bool twin_uFrontFacesOnly;

  uniform bool twin_uReceiveProjectVideo;

  varying vec3 twin_vSavedNormal;
	varying vec4 twin_vTexCoords[NUM_PROJECT_CAMERAS];
  varying vec4 twin_vWorldPosition;

  vec4 getProjectVideoColor(sampler2D videoTexture, sampler2D videoAlphaTexture, vec4 fragColor, vec4 vTexCoord, ProjectCamera projectCamera) {
    
    float w = max(vTexCoord.w, 0.0);
    
    vec2 uv = (vTexCoord.xy / w) * 0.5 + 0.5;
    
    // this makes sure we don't sample out of the texture
    bool isInTexture = (max(uv.x, uv.y) <= 1.0 && min(uv.x, uv.y) >= 0.0);
    
    vec3 projectorDirection = normalize(projectCamera.projPosition - twin_vWorldPosition.xyz);
    float dotProduct = dot(twin_vSavedNormal, projectorDirection);
    bool isFacingProjector = twin_uFrontFacesOnly ? dotProduct > 0.0000001 : true;
    
    vec4 diffuseColor = vec4(0.0, 0.0, 0.0, 0.0);
    if (isFacingProjector && isInTexture) {
      vec4 textureColor = texture2D(videoTexture, uv);
      vec4 alpha = texture2D( videoAlphaTexture, uv);
      diffuseColor = mix(fragColor, textureColor, alpha.a);
    }
    
    return diffuseColor;
    
  }

#endif
`
), V4e = (
  /* glsl */
  `
#if NUM_PROJECT_CAMERAS > 0

vec4 textureColor = vec4(0.0);

#pragma unroll_loop_start
for (int i = 0; i < NUM_PROJECT_CAMERAS; i ++) {

  textureColor += twin_uProjectCameras[i].visible ? getProjectVideoColor(twin_uVideoTextures[i], twin_uAlphaTextures[i], gl_FragColor, twin_vTexCoords[i], twin_uProjectCameras[i]) : vec4(0.0, 0.0, 0.0, 0.0);

}
#pragma unroll_loop_end

float epsilon_6 = 0.000001;
bool useVideoColor = (abs(textureColor.x - 0.0) > epsilon_6) ||
                          (abs(textureColor.y - 0.0) > epsilon_6) ||
                          (abs(textureColor.z - 0.0) > epsilon_6) ||
                          (abs(textureColor.w - 0.0) > epsilon_6);
if (useVideoColor && twin_uReceiveProjectVideo) {

  gl_FragColor = vec4(textureColor.rgba);

}

#endif
`
);
class W4e {
  constructor(e) {
    this.renderer = e, this.cameras = [];
  }
  updateUniforms(e, t, i, n) {
    if (this.cameras.length > 0) {
      const r = [], a = [], o = [];
      this.cameras.forEach((l, c) => {
        const { camera: h, texture: u, mask: d } = l, f = h.matrixWorld.clone(), p = h.matrixWorldInverse.clone(), v = h.projectionMatrix.clone(), g = h.position.clone(), m = new je().set(0, 0, 1).applyMatrix4(f), x = h.visible;
        r.push({
          viewMatrix: p,
          projectionMatrix: v,
          projPosition: g,
          projDirection: m,
          visible: x
        }), a.push(u), o.push(d);
      }), t.setValue(i, "twin_uFrontFacesOnly", !0), t.setValue(i, "twin_uProjectCameras", r), t.setValue(i, "twin_uVideoTextures", a, n), t.setValue(i, "twin_uAlphaTextures", o, n), t.setValue(i, "twin_uReceiveProjectVideo", e.receiveProjectVideo ?? !1);
    }
  }
  addCodeToVertexShader(e) {
    return oD(e, {
      header: (
        /* glsl */
        `
		${k4e}
		`
      ),
      main: `
		${G4e}
			`
    }).replace(/NUM_PROJECT_CAMERAS/g, `${this.cameras.length}`);
  }
  addCodeToFragmentShader(e) {
    return oD(e, {
      header: (
        /* glsl */
        `
		${H4e}
		`
      ),
      "#include <dithering_fragment>": `
		${V4e}
		#include <dithering_fragment>
		`
    }).replace(/NUM_PROJECT_CAMERAS/g, `${this.cameras.length}`);
  }
}
class X4e {
  constructor(e) {
    S(this, "lastFrameProjectVideoCount", 0);
    this.projectCameraMgr = new W4e(e);
  }
}
function j4e(s, e, t) {
  return new Promise(function(i, n) {
    function r() {
      switch (s.clientWaitSync(e, s.SYNC_FLUSH_COMMANDS_BIT, 0)) {
        case s.WAIT_FAILED:
          n();
          break;
        case s.TIMEOUT_EXPIRED:
          setTimeout(r, t);
          break;
        default:
          i();
      }
    }
    setTimeout(r, t);
  });
}
class SU {
  constructor(e = {}) {
    S(this, "frameState", new X4e(this));
    const {
      canvas: t = Fve(),
      context: i = null,
      depth: n = !0,
      stencil: r = !1,
      alpha: a = !1,
      antialias: o = !1,
      premultipliedAlpha: l = !0,
      preserveDrawingBuffer: c = !1,
      powerPreference: h = "default",
      failIfMajorPerformanceCaveat: u = !1
    } = e;
    this.isWebGLRenderer = !0;
    let d;
    if (i !== null) {
      if (typeof WebGLRenderingContext < "u" && i instanceof WebGLRenderingContext)
        throw new Error("THREE.WebGLRenderer: WebGL 1 is not supported since r163.");
      d = i.getContextAttributes().alpha;
    } else
      d = a;
    const f = new Uint32Array(4), p = new Int32Array(4);
    let v = null, g = null;
    const m = [], x = [];
    this.domElement = t, this.debug = {
      /**
       * Enables error checking and reporting when shader programs are being compiled
       * @type {boolean}
       */
      checkShaderErrors: !0,
      /**
       * Callback for custom error reporting.
       * @type {?Function}
       */
      onShaderError: null
    }, this.autoClear = !0, this.autoClearColor = !0, this.autoClearDepth = !0, this.autoClearStencil = !0, this.sortObjects = !0, this.clippingPlanes = [], this.localClippingEnabled = !1, this._outputColorSpace = Oo, this.toneMapping = Bc, this.toneMappingExposure = 1;
    const y = this;
    let A = !1, b = 0, M = 0, w = null, C = -1, T = null;
    const E = new Cs(), P = new Cs();
    let L = null;
    const I = new hn(0);
    let N = 0, H = t.width, U = t.height, W = 1, X = null, K = null;
    const te = new Cs(0, 0, H, U), re = new Cs(0, 0, H, U);
    let be = !1;
    const Pe = new rU();
    let J = !1, fe = !1;
    const Me = new ps(), ye = new je(), Ye = new Cs(), Je = {
      background: null,
      fog: null,
      environment: null,
      overrideMaterial: null,
      isScene: !0
    };
    let rt = !1;
    function wt() {
      return w === null ? W : 1;
    }
    let k = i;
    function Fe(O, Z) {
      return t.getContext(O, Z);
    }
    try {
      const O = {
        alpha: !0,
        depth: n,
        stencil: r,
        antialias: o,
        premultipliedAlpha: l,
        preserveDrawingBuffer: c,
        powerPreference: h,
        failIfMajorPerformanceCaveat: u
      };
      if ("setAttribute" in t && t.setAttribute("data-engine", `three.js r${O2e}`), t.addEventListener("webglcontextlost", Q, !1), t.addEventListener("webglcontextrestored", he, !1), t.addEventListener("webglcontextcreationerror", Be, !1), k === null) {
        const Z = "webgl2";
        if (k = Fe(Z, O), k === null)
          throw Fe(Z) ? new Error("Error creating WebGL context with your selected attributes.") : new Error("Error creating WebGL context.");
      }
    } catch (O) {
      throw console.error("THREE.WebGLRenderer: " + O.message), O;
    }
    let Ge, qe, Ie, vt, Ve, Ze, G, D, ne, ve, Y, ee, we, de, xe, it, q, ae, Ee, Le, Ae, We, ot, Ct;
    function F() {
      Ge = new j9e(k), Ge.init(), We = new R4e(k, Ge), qe = new I9e(k, Ge, e, We), Ie = new C4e(k), vt = new Z9e(k), Ve = new f4e(), Ze = new P4e(k, Ge, Ie, Ve, qe, We, vt), G = new G9e(y), D = new X9e(y), ne = new Bve(k), ot = new D9e(k, ne), ve = new q9e(k, ne, vt, ot), Y = new J9e(k, ve, ne, vt), Ee = new K9e(k, qe, Ze), it = new O9e(Ve), ee = new d4e(
        y,
        G,
        D,
        Ge,
        qe,
        ot,
        it
      ), we = new U4e(y, Ve), de = new m4e(), xe = new b4e(Ge), ae = new R9e(
        y,
        G,
        D,
        Ie,
        Y,
        d,
        l
      ), q = new T4e(y, Y, qe), Ct = new z4e(k, vt, qe, Ie), Le = new L9e(k, Ge, vt), Ae = new Y9e(k, Ge, vt), vt.programs = ee.programs, y.capabilities = qe, y.extensions = Ge, y.properties = Ve, y.renderLists = de, y.shadowMap = q, y.state = Ie, y.info = vt;
    }
    F();
    const ue = new B4e(y, k);
    this.xr = ue, this.getContext = function() {
      return k;
    }, this.getContextAttributes = function() {
      return k.getContextAttributes();
    }, this.forceContextLoss = function() {
      const O = Ge.get("WEBGL_lose_context");
      O && O.loseContext();
    }, this.forceContextRestore = function() {
      const O = Ge.get("WEBGL_lose_context");
      O && O.restoreContext();
    }, this.getPixelRatio = function() {
      return W;
    }, this.setPixelRatio = function(O) {
      O !== void 0 && (W = O, this.setSize(H, U, !1));
    }, this.getSize = function(O) {
      return O.set(H, U);
    }, this.setSize = function(O, Z, R = !0) {
      if (ue.isPresenting) {
        console.warn("THREE.WebGLRenderer: Can't change size while VR device is presenting.");
        return;
      }
      H = O, U = Z, t.width = Math.floor(O * W), t.height = Math.floor(Z * W), R === !0 && (t.style.width = O + "px", t.style.height = Z + "px"), this.setViewport(0, 0, O, Z);
    }, this.getDrawingBufferSize = function(O) {
      return O.set(H * W, U * W).floor();
    }, this.setDrawingBufferSize = function(O, Z, R) {
      H = O, U = Z, W = R, t.width = Math.floor(O * R), t.height = Math.floor(Z * R), this.setViewport(0, 0, O, Z);
    }, this.getCurrentViewport = function(O) {
      return O.copy(E);
    }, this.getViewport = function(O) {
      return O.copy(te);
    }, this.setViewport = function(O, Z, R, B) {
      O.isVector4 ? te.set(O.x, O.y, O.z, O.w) : te.set(O, Z, R, B), Ie.viewport(E.copy(te).multiplyScalar(W).round());
    }, this.getScissor = function(O) {
      return O.copy(re);
    }, this.setScissor = function(O, Z, R, B) {
      O.isVector4 ? re.set(O.x, O.y, O.z, O.w) : re.set(O, Z, R, B), Ie.scissor(P.copy(re).multiplyScalar(W).round());
    }, this.getScissorTest = function() {
      return be;
    }, this.setScissorTest = function(O) {
      Ie.setScissorTest(be = O);
    }, this.setOpaqueSort = function(O) {
      X = O;
    }, this.setTransparentSort = function(O) {
      K = O;
    }, this.getClearColor = function(O) {
      return O.copy(ae.getClearColor());
    }, this.setClearColor = function() {
      ae.setClearColor.apply(ae, arguments);
    }, this.getClearAlpha = function() {
      return ae.getClearAlpha();
    }, this.setClearAlpha = function() {
      ae.setClearAlpha.apply(ae, arguments);
    }, this.clear = function(O = !0, Z = !0, R = !0) {
      let B = 0;
      if (O) {
        let z = !1;
        if (w !== null) {
          const $ = w.texture.format;
          z = $ === Iw || $ === Lw || $ === Dw;
        }
        if (z) {
          const $ = w.texture.type, ce = $ === Sl || $ === zu || $ === jg || $ === Bp || $ === Pw || $ === Rw, se = ae.getClearColor(), pe = ae.getClearAlpha(), Ne = se.r, De = se.g, Te = se.b;
          ce ? (f[0] = Ne, f[1] = De, f[2] = Te, f[3] = pe, k.clearBufferuiv(k.COLOR, 0, f)) : (p[0] = Ne, p[1] = De, p[2] = Te, p[3] = pe, k.clearBufferiv(k.COLOR, 0, p));
        } else
          B |= k.COLOR_BUFFER_BIT;
      }
      Z && (B |= k.DEPTH_BUFFER_BIT), R && (B |= k.STENCIL_BUFFER_BIT, this.state.buffers.stencil.setMask(4294967295)), k.clear(B);
    }, this.clearColor = function() {
      this.clear(!0, !1, !1);
    }, this.clearDepth = function() {
      this.clear(!1, !0, !1);
    }, this.clearStencil = function() {
      this.clear(!1, !1, !0);
    }, this.dispose = function() {
      t.removeEventListener("webglcontextlost", Q, !1), t.removeEventListener("webglcontextrestored", he, !1), t.removeEventListener("webglcontextcreationerror", Be, !1), de.dispose(), xe.dispose(), Ve.dispose(), G.dispose(), D.dispose(), Y.dispose(), ot.dispose(), Ct.dispose(), ee.dispose(), ue.dispose(), ue.removeEventListener("sessionstart", Li), ue.removeEventListener("sessionend", cr), sn.stop();
    };
    function Q(O) {
      O.preventDefault(), console.log("THREE.WebGLRenderer: Context Lost."), A = !0;
    }
    function he() {
      console.log("THREE.WebGLRenderer: Context Restored."), A = !1;
      const O = vt.autoReset, Z = q.enabled, R = q.autoUpdate, B = q.needsUpdate, z = q.type;
      F(), vt.autoReset = O, q.enabled = Z, q.autoUpdate = R, q.needsUpdate = B, q.type = z;
    }
    function Be(O) {
      console.error(
        "THREE.WebGLRenderer: A WebGL context could not be created. Reason: ",
        O.statusMessage
      );
    }
    function ft(O) {
      const Z = O.target;
      Z.removeEventListener("dispose", ft), Et(Z);
    }
    function Et(O) {
      ei(O), Ve.remove(O);
    }
    function ei(O) {
      const Z = Ve.get(O).programs;
      Z !== void 0 && (Z.forEach(function(R) {
        ee.releaseProgram(R);
      }), O.isShaderMaterial && ee.releaseShaderCache(O));
    }
    this.renderBufferDirect = function(O, Z, R, B, z, $) {
      Z === null && (Z = Je);
      const ce = z.isMesh && z.matrixWorld.determinant() < 0, se = Ll(O, Z, R, B, z);
      Ie.setMaterial(B, ce);
      let pe = R.index, Ne = 1;
      if (B.wireframe === !0) {
        if (pe = ve.getWireframeAttribute(R), pe === void 0)
          return;
        Ne = 2;
      }
      const De = R.drawRange, Te = R.attributes.position;
      let Ue = De.start * Ne, at = (De.start + De.count) * Ne;
      $ !== null && (Ue = Math.max(Ue, $.start * Ne), at = Math.min(at, ($.start + $.count) * Ne)), pe !== null ? (Ue = Math.max(Ue, 0), at = Math.min(at, pe.count)) : Te != null && (Ue = Math.max(Ue, 0), at = Math.min(at, Te.count));
      const ht = at - Ue;
      if (ht < 0 || ht === 1 / 0)
        return;
      ot.setup(z, B, se, R, pe);
      let At, lt = Le;
      if (pe !== null && (At = ne.get(pe), lt = Ae, lt.setIndex(At)), z.isMesh)
        B.wireframe === !0 ? (Ie.setLineWidth(B.wireframeLinewidth * wt()), lt.setMode(k.LINES)) : lt.setMode(k.TRIANGLES);
      else if (z.isLine) {
        let Oe = B.linewidth;
        Oe === void 0 && (Oe = 1), Ie.setLineWidth(Oe * wt()), z.isLineSegments ? lt.setMode(k.LINES) : z.isLineLoop ? lt.setMode(k.LINE_LOOP) : lt.setMode(k.LINE_STRIP);
      } else
        z.isPoints ? lt.setMode(k.POINTS) : z.isSprite && lt.setMode(k.TRIANGLES);
      if (z.isBatchedMesh)
        if (z._multiDrawInstances !== null)
          lt.renderMultiDrawInstances(
            z._multiDrawStarts,
            z._multiDrawCounts,
            z._multiDrawCount,
            z._multiDrawInstances
          );
        else if (Ge.get("WEBGL_multi_draw"))
          lt.renderMultiDraw(
            z._multiDrawStarts,
            z._multiDrawCounts,
            z._multiDrawCount
          );
        else {
          const Oe = z._multiDrawStarts, yt = z._multiDrawCounts, V = z._multiDrawCount, le = pe ? ne.get(pe).bytesPerElement : 1, ie = Ve.get(B).currentProgram.getUniforms();
          for (let ge = 0; ge < V; ge++)
            ie.setValue(k, "_gl_DrawID", ge), lt.render(Oe[ge] / le, yt[ge]);
        }
      else if (z.isInstancedMesh)
        lt.renderInstances(Ue, ht, z.count);
      else if (R.isInstancedBufferGeometry) {
        const Oe = R._maxInstanceCount !== void 0 ? R._maxInstanceCount : 1 / 0, yt = Math.min(R.instanceCount, Oe);
        lt.renderInstances(Ue, ht, yt);
      } else
        lt.render(Ue, ht);
    };
    function di(O, Z, R) {
      O.transparent === !0 && O.side === Q0 && O.forceSinglePass === !1 ? (O.side = Fr, O.needsUpdate = !0, Gr(O, Z, R), O.side = Kc, O.needsUpdate = !0, Gr(O, Z, R), O.side = Q0) : Gr(O, Z, R);
    }
    this.compile = function(O, Z, R = null) {
      R === null && (R = O), g = xe.get(R), g.init(Z), x.push(g), R.traverseVisible(function(z) {
        z.isLight && z.layers.test(Z.layers) && (g.pushLight(z), z.castShadow && g.pushShadow(z));
      }), O !== R && O.traverseVisible(function(z) {
        z.isLight && z.layers.test(Z.layers) && (g.pushLight(z), z.castShadow && g.pushShadow(z));
      }), g.setupLights();
      const B = /* @__PURE__ */ new Set();
      return O.traverse(function(z) {
        const $ = z.material;
        if ($)
          if (Array.isArray($))
            for (let ce = 0; ce < $.length; ce++) {
              const se = $[ce];
              di(se, R, z), B.add(se);
            }
          else
            di($, R, z), B.add($);
      }), x.pop(), g = null, B;
    }, this.compileAsync = function(O, Z, R = null) {
      const B = this.compile(O, Z, R);
      return new Promise((z) => {
        function $() {
          if (B.forEach(function(ce) {
            Ve.get(ce).currentProgram.isReady() && B.delete(ce);
          }), B.size === 0) {
            z(O);
            return;
          }
          setTimeout($, 10);
        }
        Ge.get("KHR_parallel_shader_compile") !== null ? $() : setTimeout($, 10);
      });
    };
    let Ot = null;
    function Wi(O) {
      Ot && Ot(O);
    }
    function Li() {
      sn.stop();
    }
    function cr() {
      sn.start();
    }
    const sn = new aU();
    sn.setAnimationLoop(Wi), typeof self < "u" && sn.setContext(self), this.setAnimationLoop = function(O) {
      Ot = O, ue.setAnimationLoop(O), O === null ? sn.stop() : sn.start();
    }, ue.addEventListener("sessionstart", Li), ue.addEventListener("sessionend", cr), this.render = function(O, Z) {
      if (Z !== void 0 && Z.isCamera !== !0) {
        console.error("THREE.WebGLRenderer.render: camera is not an instance of THREE.Camera.");
        return;
      }
      if (A === !0)
        return;
      if (O.matrixWorldAutoUpdate === !0 && O.updateMatrixWorld(), Z.parent === null && Z.matrixWorldAutoUpdate === !0 && Z.updateMatrixWorld(), ue.enabled === !0 && ue.isPresenting === !0 && (ue.cameraAutoUpdate === !0 && ue.updateCamera(Z), Z = ue.getCamera()), O.isScene === !0 && O.onBeforeRender(y, O, Z, w), g = xe.get(O, x.length), g.init(Z), x.push(g), Me.multiplyMatrices(Z.projectionMatrix, Z.matrixWorldInverse), Pe.setFromProjectionMatrix(Me), fe = this.localClippingEnabled, J = it.init(this.clippingPlanes, fe), v = de.get(O, m.length), v.init(), m.push(v), ue.enabled === !0 && ue.isPresenting === !0) {
        const $ = y.xr.getDepthSensingMesh();
        $ !== null && Os($, Z, -1 / 0, y.sortObjects);
      }
      Os(O, Z, 0, y.sortObjects), v.finish(), y.sortObjects === !0 && v.sort(X, K), rt = ue.enabled === !1 || ue.isPresenting === !1 || ue.hasDepthSensing() === !1, rt && ae.addToRenderList(v, O), this.info.render.frame++, J === !0 && it.beginShadows();
      const R = g.state.shadowsArray;
      q.render(R, O, Z), J === !0 && it.endShadows(), this.info.autoReset === !0 && this.info.reset();
      const B = v.opaque, z = v.transmissive;
      if (g.setupLights(), Z.isArrayCamera) {
        const $ = Z.cameras;
        if (z.length > 0)
          for (let ce = 0, se = $.length; ce < se; ce++) {
            const pe = $[ce];
            kr(B, z, O, pe);
          }
        rt && ae.render(O);
        for (let ce = 0, se = $.length; ce < se; ce++) {
          const pe = $[ce];
          br(v, O, pe, pe.viewport);
        }
      } else
        z.length > 0 && kr(B, z, O, Z), rt && ae.render(O), br(v, O, Z);
      w !== null && (Ze.updateMultisampleRenderTarget(w), Ze.updateRenderTargetMipmap(w)), O.isScene === !0 && O.onAfterRender(y, O, Z), ot.resetDefaultState(), C = -1, T = null, x.pop(), x.length > 0 ? (g = x[x.length - 1], J === !0 && it.setGlobalState(y.clippingPlanes, g.state.camera)) : g = null, m.pop(), m.length > 0 ? v = m[m.length - 1] : v = null;
    };
    function Os(O, Z, R, B) {
      if (O.visible === !1)
        return;
      if (O.layers.test(Z.layers)) {
        if (O.isGroup)
          R = O.renderOrder;
        else if (O.isLOD)
          O.autoUpdate === !0 && O.update(Z);
        else if (O.isLight)
          g.pushLight(O), O.castShadow && g.pushShadow(O);
        else if (O.isSprite) {
          if (!O.frustumCulled || Pe.intersectsSprite(O)) {
            B && Ye.setFromMatrixPosition(O.matrixWorld).applyMatrix4(Me);
            const ce = Y.update(O), se = O.material;
            se.visible && v.push(O, ce, se, R, Ye.z, null);
          }
        } else if ((O.isMesh || O.isLine || O.isPoints) && (!O.frustumCulled || Pe.intersectsObject(O))) {
          const ce = Y.update(O), se = O.material;
          if (B && (O.boundingSphere !== void 0 ? (O.boundingSphere === null && O.computeBoundingSphere(), Ye.copy(O.boundingSphere.center)) : (ce.boundingSphere === null && ce.computeBoundingSphere(), Ye.copy(ce.boundingSphere.center)), Ye.applyMatrix4(O.matrixWorld).applyMatrix4(Me)), Array.isArray(se)) {
            const pe = ce.groups;
            for (let Ne = 0, De = pe.length; Ne < De; Ne++) {
              const Te = pe[Ne], Ue = se[Te.materialIndex];
              Ue && Ue.visible && v.push(
                O,
                ce,
                Ue,
                R,
                Ye.z,
                Te
              );
            }
          } else
            se.visible && v.push(O, ce, se, R, Ye.z, null);
        }
      }
      const $ = O.children;
      for (let ce = 0, se = $.length; ce < se; ce++)
        Os($[ce], Z, R, B);
    }
    function br(O, Z, R, B) {
      const z = O.opaque, $ = O.transmissive, ce = O.transparent;
      g.setupLightsView(R), J === !0 && it.setGlobalState(y.clippingPlanes, R), B && Ie.viewport(E.copy(B)), z.length > 0 && Mr(z, Z, R), $.length > 0 && Mr($, Z, R), ce.length > 0 && Mr(ce, Z, R), Ie.buffers.depth.setTest(!0), Ie.buffers.depth.setMask(!0), Ie.buffers.color.setMask(!0), Ie.setPolygonOffset(!1);
    }
    function kr(O, Z, R, B) {
      if ((R.isScene === !0 ? R.overrideMaterial : null) !== null)
        return;
      g.state.transmissionRenderTarget[B.id] === void 0 && (g.state.transmissionRenderTarget[B.id] = new ku(1, 1, {
        generateMipmaps: !0,
        type: Ge.has("EXT_color_buffer_half_float") || Ge.has("EXT_color_buffer_float") ? G2 : Sl,
        minFilter: yu,
        samples: 4,
        stencilBuffer: r,
        resolveDepthBuffer: !1,
        resolveStencilBuffer: !1,
        colorSpace: rn.workingColorSpace
      }));
      const $ = g.state.transmissionRenderTarget[B.id], ce = B.viewport || E;
      $.setSize(ce.z, ce.w);
      const se = y.getRenderTarget();
      y.setRenderTarget($), y.getClearColor(I), N = y.getClearAlpha(), N < 1 && y.setClearColor(16777215, 0.5), rt ? ae.render(R) : y.clear();
      const pe = y.toneMapping;
      y.toneMapping = Bc;
      const Ne = B.viewport;
      if (B.viewport !== void 0 && (B.viewport = void 0), g.setupLightsView(B), J === !0 && it.setGlobalState(y.clippingPlanes, B), Mr(O, R, B), Ze.updateMultisampleRenderTarget($), Ze.updateRenderTargetMipmap($), Ge.has("WEBGL_multisampled_render_to_texture") === !1) {
        let De = !1;
        for (let Te = 0, Ue = Z.length; Te < Ue; Te++) {
          const at = Z[Te], ht = at.object, At = at.geometry, lt = at.material, Oe = at.group;
          if (lt.side === Q0 && ht.layers.test(B.layers)) {
            const yt = lt.side;
            lt.side = Fr, lt.needsUpdate = !0, A0(ht, R, B, At, lt, Oe), lt.side = yt, lt.needsUpdate = !0, De = !0;
          }
        }
        De === !0 && (Ze.updateMultisampleRenderTarget($), Ze.updateRenderTargetMipmap($));
      }
      y.setRenderTarget(se), y.setClearColor(I, N), Ne !== void 0 && (B.viewport = Ne), y.toneMapping = pe;
    }
    function Mr(O, Z, R) {
      const B = Z.isScene === !0 ? Z.overrideMaterial : null;
      for (let z = 0, $ = O.length; z < $; z++) {
        const ce = O[z], se = ce.object, pe = ce.geometry, Ne = B === null ? ce.material : B, De = ce.group;
        se.layers.test(R.layers) && A0(se, Z, R, pe, Ne, De);
      }
    }
    function A0(O, Z, R, B, z, $) {
      O.onBeforeRender(y, Z, R, B, z, $), O.modelViewMatrix.multiplyMatrices(R.matrixWorldInverse, O.matrixWorld), O.normalMatrix.getNormalMatrix(O.modelViewMatrix), z.transparent === !0 && z.side === Q0 && z.forceSinglePass === !1 ? (z.side = Fr, z.needsUpdate = !0, y.renderBufferDirect(R, Z, B, z, O, $), z.side = Kc, z.needsUpdate = !0, y.renderBufferDirect(R, Z, B, z, O, $), z.side = Q0) : y.renderBufferDirect(R, Z, B, z, O, $), O.onAfterRender(y, Z, R, B, z, $);
    }
    function Gr(O, Z, R) {
      Z.isScene !== !0 && (Z = Je);
      const B = Ve.get(O), z = g.state.lights, $ = g.state.shadowsArray, ce = z.state.version, se = ee.getParameters(
        O,
        z.state,
        $,
        Z,
        R
      );
      let pe = ee.getProgramCacheKey(se);
      pe = `${pe},${y.frameState.projectCameraMgr.cameras.length}`;
      let Ne = B.programs;
      B.environment = O.isMeshStandardMaterial ? Z.environment : null, B.fog = Z.fog, B.envMap = (O.isMeshStandardMaterial ? D : G).get(
        O.envMap || B.environment
      ), B.envMapRotation = B.environment !== null && O.envMap === null ? Z.environmentRotation : O.envMapRotation, Ne === void 0 && (O.addEventListener("dispose", ft), Ne = /* @__PURE__ */ new Map(), B.programs = Ne);
      let De = Ne.get(pe);
      if (De !== void 0) {
        if (B.currentProgram === De && B.lightsStateVersion === ce)
          return M0(O, se), De;
      } else
        se.uniforms = ee.getUniforms(O), O.onBeforeCompile(se, y), De = ee.acquireProgram(se, pe), Ne.set(pe, De), B.uniforms = se.uniforms;
      const Te = B.uniforms;
      return (!O.isShaderMaterial && !O.isRawShaderMaterial || O.clipping === !0) && (Te.clippingPlanes = it.uniform), M0(O, se), B.needsLights = dh(O), B.lightsStateVersion = ce, B.needsLights && (Te.ambientLightColor.value = z.state.ambient, Te.lightProbe.value = z.state.probe, Te.directionalLights.value = z.state.directional, Te.directionalLightShadows.value = z.state.directionalShadow, Te.spotLights.value = z.state.spot, Te.spotLightShadows.value = z.state.spotShadow, Te.rectAreaLights.value = z.state.rectArea, Te.ltc_1.value = z.state.rectAreaLTC1, Te.ltc_2.value = z.state.rectAreaLTC2, Te.pointLights.value = z.state.point, Te.pointLightShadows.value = z.state.pointShadow, Te.hemisphereLights.value = z.state.hemi, Te.directionalShadowMap.value = z.state.directionalShadowMap, Te.directionalShadowMatrix.value = z.state.directionalShadowMatrix, Te.spotShadowMap.value = z.state.spotShadowMap, Te.spotLightMatrix.value = z.state.spotLightMatrix, Te.spotLightMap.value = z.state.spotLightMap, Te.pointShadowMap.value = z.state.pointShadowMap, Te.pointShadowMatrix.value = z.state.pointShadowMatrix), B.currentProgram = De, B.uniformsList = null, De;
    }
    function b0(O) {
      if (O.uniformsList === null) {
        const Z = O.currentProgram.getUniforms();
        O.uniformsList = i9.seqWithValue(
          Z.seq,
          O.uniforms
        );
      }
      return O.uniformsList;
    }
    function M0(O, Z) {
      const R = Ve.get(O);
      R.outputColorSpace = Z.outputColorSpace, R.batching = Z.batching, R.batchingColor = Z.batchingColor, R.instancing = Z.instancing, R.instancingColor = Z.instancingColor, R.instancingMorph = Z.instancingMorph, R.skinning = Z.skinning, R.morphTargets = Z.morphTargets, R.morphNormals = Z.morphNormals, R.morphColors = Z.morphColors, R.morphTargetsCount = Z.morphTargetsCount, R.numClippingPlanes = Z.numClippingPlanes, R.numIntersection = Z.numClipIntersection, R.vertexAlphas = Z.vertexAlphas, R.vertexTangents = Z.vertexTangents, R.toneMapping = Z.toneMapping;
    }
    function Ll(O, Z, R, B, z) {
      Z.isScene !== !0 && (Z = Je), Ze.resetTextureUnits();
      const $ = Z.fog, ce = B.isMeshStandardMaterial ? Z.environment : null, se = w === null ? y.outputColorSpace : w.isXRRenderTarget === !0 ? w.texture.colorSpace : hh, pe = (B.isMeshStandardMaterial ? D : G).get(
        B.envMap || ce
      ), Ne = B.vertexColors === !0 && !!R.attributes.color && R.attributes.color.itemSize === 4, De = !!R.attributes.tangent && (!!B.normalMap || B.anisotropy > 0), Te = !!R.morphAttributes.position, Ue = !!R.morphAttributes.normal, at = !!R.morphAttributes.color;
      let ht = Bc;
      B.toneMapped && (w === null || w.isXRRenderTarget === !0) && (ht = y.toneMapping);
      const At = R.morphAttributes.position || R.morphAttributes.normal || R.morphAttributes.color, lt = At !== void 0 ? At.length : 0, Oe = Ve.get(B), yt = g.state.lights;
      if (J === !0 && (fe === !0 || O !== T)) {
        const Ke = O === T && B.id === C;
        it.setState(B, O, Ke);
      }
      let V = !1;
      B.version === Oe.__version ? (Oe.needsLights && Oe.lightsStateVersion !== yt.state.version || Oe.outputColorSpace !== se || z.isBatchedMesh && Oe.batching === !1 || !z.isBatchedMesh && Oe.batching === !0 || z.isBatchedMesh && Oe.batchingColor === !0 && z.colorTexture === null || z.isBatchedMesh && Oe.batchingColor === !1 && z.colorTexture !== null || z.isInstancedMesh && Oe.instancing === !1 || !z.isInstancedMesh && Oe.instancing === !0 || z.isSkinnedMesh && Oe.skinning === !1 || !z.isSkinnedMesh && Oe.skinning === !0 || z.isInstancedMesh && Oe.instancingColor === !0 && z.instanceColor === null || z.isInstancedMesh && Oe.instancingColor === !1 && z.instanceColor !== null || z.isInstancedMesh && Oe.instancingMorph === !0 && z.morphTexture === null || z.isInstancedMesh && Oe.instancingMorph === !1 && z.morphTexture !== null || Oe.envMap !== pe || B.fog === !0 && Oe.fog !== $ || Oe.numClippingPlanes !== void 0 && (Oe.numClippingPlanes !== it.numPlanes || Oe.numIntersection !== it.numIntersection) || Oe.vertexAlphas !== Ne || Oe.vertexTangents !== De || Oe.morphTargets !== Te || Oe.morphNormals !== Ue || Oe.morphColors !== at || Oe.toneMapping !== ht || Oe.morphTargetsCount !== lt) && (V = !0) : (V = !0, Oe.__version = B.version), Oe.projectVideoCameraCount !== y.frameState.projectCameraMgr.cameras.length && (V = !0, Oe.projectVideoCameraCount = y.frameState.projectCameraMgr.cameras.length);
      let le = Oe.currentProgram;
      V === !0 && (le = Gr(B, Z, z));
      let ie = !1, ge = !1, Ce = !1;
      const oe = le.getUniforms(), He = Oe.uniforms;
      if (Ie.useProgram(le.program) && (ie = !0, ge = !0, Ce = !0), y.frameState.projectCameraMgr.updateUniforms(
        z,
        oe,
        k,
        Ze,
        ie
      ), B.id !== C && (C = B.id, ge = !0), ie || T !== O) {
        oe.setValue(k, "projectionMatrix", O.projectionMatrix), oe.setValue(k, "viewMatrix", O.matrixWorldInverse);
        const Ke = oe.map.cameraPosition;
        Ke !== void 0 && Ke.setValue(k, ye.setFromMatrixPosition(O.matrixWorld)), qe.logarithmicDepthBuffer && oe.setValue(k, "logDepthBufFC", 2 / (Math.log(O.far + 1) / Math.LN2)), (B.isMeshPhongMaterial || B.isMeshToonMaterial || B.isMeshLambertMaterial || B.isMeshBasicMaterial || B.isMeshStandardMaterial || B.isShaderMaterial) && oe.setValue(k, "isOrthographic", O.isOrthographicCamera === !0), T !== O && (T = O, ge = !0, Ce = !0);
      }
      if (z.isSkinnedMesh) {
        oe.setOptional(k, z, "bindMatrix"), oe.setOptional(k, z, "bindMatrixInverse");
        const Ke = z.skeleton;
        Ke && (Ke.boneTexture === null && Ke.computeBoneTexture(), oe.setValue(k, "boneTexture", Ke.boneTexture, Ze));
      }
      z.isBatchedMesh && (oe.setOptional(k, z, "batchingTexture"), oe.setValue(k, "batchingTexture", z._matricesTexture, Ze), oe.setOptional(k, z, "batchingIdTexture"), oe.setValue(k, "batchingIdTexture", z._indirectTexture, Ze), oe.setOptional(k, z, "batchingColorTexture"), z._colorsTexture !== null && oe.setValue(k, "batchingColorTexture", z._colorsTexture, Ze));
      const ct = R.morphAttributes;
      if ((ct.position !== void 0 || ct.normal !== void 0 || ct.color !== void 0) && Ee.update(z, R, le), (ge || Oe.receiveShadow !== z.receiveShadow) && (Oe.receiveShadow = z.receiveShadow, oe.setValue(k, "receiveShadow", z.receiveShadow)), B.isMeshGouraudMaterial && B.envMap !== null && (He.envMap.value = pe, He.flipEnvMap.value = pe.isCubeTexture && pe.isRenderTargetTexture === !1 ? -1 : 1), B.isMeshStandardMaterial && B.envMap === null && Z.environment !== null && (He.envMapIntensity.value = Z.environmentIntensity), ge && (oe.setValue(k, "toneMappingExposure", y.toneMappingExposure), Oe.needsLights && Il(He, Ce), $ && B.fog === !0 && we.refreshFogUniforms(He, $), we.refreshMaterialUniforms(
        He,
        B,
        W,
        U,
        g.state.transmissionRenderTarget[O.id]
      ), i9.upload(k, b0(Oe), He, Ze)), B.isShaderMaterial && B.uniformsNeedUpdate === !0 && (i9.upload(k, b0(Oe), He, Ze), B.uniformsNeedUpdate = !1), B.isSpriteMaterial && oe.setValue(k, "center", z.center), oe.setValue(k, "modelViewMatrix", z.modelViewMatrix), oe.setValue(k, "normalMatrix", z.normalMatrix), oe.setValue(k, "modelMatrix", z.matrixWorld), B.isShaderMaterial || B.isRawShaderMaterial) {
        const Ke = B.uniformsGroups;
        for (let tt = 0, ut = Ke.length; tt < ut; tt++) {
          const _t = Ke[tt];
          Ct.update(_t, le), Ct.bind(_t, le);
        }
      }
      return le;
    }
    function Il(O, Z) {
      O.ambientLightColor.needsUpdate = Z, O.lightProbe.needsUpdate = Z, O.directionalLights.needsUpdate = Z, O.directionalLightShadows.needsUpdate = Z, O.pointLights.needsUpdate = Z, O.pointLightShadows.needsUpdate = Z, O.spotLights.needsUpdate = Z, O.spotLightShadows.needsUpdate = Z, O.rectAreaLights.needsUpdate = Z, O.hemisphereLights.needsUpdate = Z;
    }
    function dh(O) {
      return O.isMeshLambertMaterial || O.isMeshToonMaterial || O.isMeshPhongMaterial || O.isMeshStandardMaterial || O.isShadowMaterial || O.isShaderMaterial && O.lights === !0;
    }
    this.getActiveCubeFace = function() {
      return b;
    }, this.getActiveMipmapLevel = function() {
      return M;
    }, this.getRenderTarget = function() {
      return w;
    }, this.setRenderTargetTextures = function(O, Z, R) {
      Ve.get(O.texture).__webglTexture = Z, Ve.get(O.depthTexture).__webglTexture = R;
      const B = Ve.get(O);
      B.__hasExternalTextures = !0, B.__autoAllocateDepthBuffer = R === void 0, B.__autoAllocateDepthBuffer || Ge.has("WEBGL_multisampled_render_to_texture") === !0 && (console.warn(
        "THREE.WebGLRenderer: Render-to-texture extension was disabled because an external texture was provided"
      ), B.__useRenderToTexture = !1);
    }, this.setRenderTargetFramebuffer = function(O, Z) {
      const R = Ve.get(O);
      R.__webglFramebuffer = Z, R.__useDefaultFramebuffer = Z === void 0;
    }, this.setRenderTarget = function(O, Z = 0, R = 0) {
      w = O, b = Z, M = R;
      let B = !0, z = null, $ = !1, ce = !1;
      if (O) {
        const pe = Ve.get(O);
        pe.__useDefaultFramebuffer !== void 0 ? (Ie.bindFramebuffer(k.FRAMEBUFFER, null), B = !1) : pe.__webglFramebuffer === void 0 ? Ze.setupRenderTarget(O) : pe.__hasExternalTextures && Ze.rebindTextures(
          O,
          Ve.get(O.texture).__webglTexture,
          Ve.get(O.depthTexture).__webglTexture
        );
        const Ne = O.texture;
        (Ne.isData3DTexture || Ne.isDataArrayTexture || Ne.isCompressedArrayTexture) && (ce = !0);
        const De = Ve.get(O).__webglFramebuffer;
        O.isWebGLCubeRenderTarget ? (Array.isArray(De[Z]) ? z = De[Z][R] : z = De[Z], $ = !0) : O.samples > 0 && Ze.useMultisampledRTT(O) === !1 ? z = Ve.get(O).__webglMultisampledFramebuffer : Array.isArray(De) ? z = De[R] : z = De, E.copy(O.viewport), P.copy(O.scissor), L = O.scissorTest;
      } else
        E.copy(te).multiplyScalar(W).floor(), P.copy(re).multiplyScalar(W).floor(), L = be;
      if (Ie.bindFramebuffer(k.FRAMEBUFFER, z) && B && Ie.drawBuffers(O, z), Ie.viewport(E), Ie.scissor(P), Ie.setScissorTest(L), $) {
        const pe = Ve.get(O.texture);
        k.framebufferTexture2D(
          k.FRAMEBUFFER,
          k.COLOR_ATTACHMENT0,
          k.TEXTURE_CUBE_MAP_POSITIVE_X + Z,
          pe.__webglTexture,
          R
        );
      } else if (ce) {
        const pe = Ve.get(O.texture), Ne = Z || 0;
        k.framebufferTextureLayer(
          k.FRAMEBUFFER,
          k.COLOR_ATTACHMENT0,
          pe.__webglTexture,
          R || 0,
          Ne
        );
      }
      C = -1;
    }, this.readRenderTargetPixels = function(O, Z, R, B, z, $, ce) {
      if (!(O && O.isWebGLRenderTarget)) {
        console.error(
          "THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget."
        );
        return;
      }
      let se = Ve.get(O).__webglFramebuffer;
      if (O.isWebGLCubeRenderTarget && ce !== void 0 && (se = se[ce]), se) {
        Ie.bindFramebuffer(k.FRAMEBUFFER, se);
        try {
          const pe = O.texture, Ne = pe.format, De = pe.type;
          if (!qe.textureFormatReadable(Ne)) {
            console.error(
              "THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in RGBA or implementation defined format."
            );
            return;
          }
          if (!qe.textureTypeReadable(De)) {
            console.error(
              "THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in UnsignedByteType or implementation defined type."
            );
            return;
          }
          Z >= 0 && Z <= O.width - B && R >= 0 && R <= O.height - z && k.readPixels(
            Z,
            R,
            B,
            z,
            We.convert(Ne),
            We.convert(De),
            $
          );
        } finally {
          const pe = w !== null ? Ve.get(w).__webglFramebuffer : null;
          Ie.bindFramebuffer(k.FRAMEBUFFER, pe);
        }
      }
    }, this.readRenderTargetPixelsAsync = async function(O, Z, R, B, z, $, ce) {
      if (!(O && O.isWebGLRenderTarget))
        throw new Error(
          "THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget."
        );
      let se = Ve.get(O).__webglFramebuffer;
      if (O.isWebGLCubeRenderTarget && ce !== void 0 && (se = se[ce]), se) {
        Ie.bindFramebuffer(k.FRAMEBUFFER, se);
        try {
          const pe = O.texture, Ne = pe.format, De = pe.type;
          if (!qe.textureFormatReadable(Ne))
            throw new Error(
              "THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in RGBA or implementation defined format."
            );
          if (!qe.textureTypeReadable(De))
            throw new Error(
              "THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in UnsignedByteType or implementation defined type."
            );
          if (Z >= 0 && Z <= O.width - B && R >= 0 && R <= O.height - z) {
            const Te = k.createBuffer();
            k.bindBuffer(k.PIXEL_PACK_BUFFER, Te), k.bufferData(k.PIXEL_PACK_BUFFER, $.byteLength, k.STREAM_READ), k.readPixels(
              Z,
              R,
              B,
              z,
              We.convert(Ne),
              We.convert(De),
              0
            ), k.flush();
            const Ue = k.fenceSync(k.SYNC_GPU_COMMANDS_COMPLETE, 0);
            await j4e(k, Ue, 4);
            try {
              k.bindBuffer(k.PIXEL_PACK_BUFFER, Te), k.getBufferSubData(k.PIXEL_PACK_BUFFER, 0, $);
            } finally {
              k.deleteBuffer(Te), k.deleteSync(Ue);
            }
            return $;
          }
        } finally {
          const pe = w !== null ? Ve.get(w).__webglFramebuffer : null;
          Ie.bindFramebuffer(k.FRAMEBUFFER, pe);
        }
      }
    }, this.copyFramebufferToTexture = function(O, Z = null, R = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyFramebufferToTexture function signature has changed."), Z = arguments[0] || null, O = arguments[1]);
      const B = Math.pow(2, -R), z = Math.floor(O.image.width * B), $ = Math.floor(O.image.height * B), ce = Z !== null ? Z.x : 0, se = Z !== null ? Z.y : 0;
      Ze.setTexture2D(O, 0), k.copyTexSubImage2D(k.TEXTURE_2D, R, 0, 0, ce, se, z, $), Ie.unbindTexture();
    }, this.copyTextureToTexture = function(O, Z, R = null, B = null, z = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyTextureToTexture function signature has changed."), B = arguments[0] || null, O = arguments[1], Z = arguments[2], z = arguments[3] || 0, R = null);
      let $, ce, se, pe, Ne, De;
      R !== null ? ($ = R.max.x - R.min.x, ce = R.max.y - R.min.y, se = R.min.x, pe = R.min.y) : ($ = O.image.width, ce = O.image.height, se = 0, pe = 0), B !== null ? (Ne = B.x, De = B.y) : (Ne = 0, De = 0);
      const Te = We.convert(Z.format), Ue = We.convert(Z.type);
      Ze.setTexture2D(Z, 0), k.pixelStorei(k.UNPACK_FLIP_Y_WEBGL, Z.flipY), k.pixelStorei(k.UNPACK_PREMULTIPLY_ALPHA_WEBGL, Z.premultiplyAlpha), k.pixelStorei(k.UNPACK_ALIGNMENT, Z.unpackAlignment);
      const at = k.getParameter(k.UNPACK_ROW_LENGTH), ht = k.getParameter(k.UNPACK_IMAGE_HEIGHT), At = k.getParameter(k.UNPACK_SKIP_PIXELS), lt = k.getParameter(k.UNPACK_SKIP_ROWS), Oe = k.getParameter(k.UNPACK_SKIP_IMAGES), yt = O.isCompressedTexture ? O.mipmaps[z] : O.image;
      k.pixelStorei(k.UNPACK_ROW_LENGTH, yt.width), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, yt.height), k.pixelStorei(k.UNPACK_SKIP_PIXELS, se), k.pixelStorei(k.UNPACK_SKIP_ROWS, pe), O.isDataTexture ? k.texSubImage2D(
        k.TEXTURE_2D,
        z,
        Ne,
        De,
        $,
        ce,
        Te,
        Ue,
        yt.data
      ) : O.isCompressedTexture ? k.compressedTexSubImage2D(
        k.TEXTURE_2D,
        z,
        Ne,
        De,
        yt.width,
        yt.height,
        Te,
        yt.data
      ) : k.texSubImage2D(
        k.TEXTURE_2D,
        z,
        Ne,
        De,
        $,
        ce,
        Te,
        Ue,
        yt
      ), k.pixelStorei(k.UNPACK_ROW_LENGTH, at), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, ht), k.pixelStorei(k.UNPACK_SKIP_PIXELS, At), k.pixelStorei(k.UNPACK_SKIP_ROWS, lt), k.pixelStorei(k.UNPACK_SKIP_IMAGES, Oe), z === 0 && Z.generateMipmaps && k.generateMipmap(k.TEXTURE_2D), Ie.unbindTexture();
    }, this.copyTextureToTexture3D = function(O, Z, R = null, B = null, z = 0) {
      O.isTexture !== !0 && (console.warn("WebGLRenderer: copyTextureToTexture3D function signature has changed."), R = arguments[0] || null, B = arguments[1] || null, O = arguments[2], Z = arguments[3], z = arguments[4] || 0);
      let $, ce, se, pe, Ne, De, Te, Ue, at;
      const ht = O.isCompressedTexture ? O.mipmaps[z] : O.image;
      R !== null ? ($ = R.max.x - R.min.x, ce = R.max.y - R.min.y, se = R.max.z - R.min.z, pe = R.min.x, Ne = R.min.y, De = R.min.z) : ($ = ht.width, ce = ht.height, se = ht.depth, pe = 0, Ne = 0, De = 0), B !== null ? (Te = B.x, Ue = B.y, at = B.z) : (Te = 0, Ue = 0, at = 0);
      const At = We.convert(Z.format), lt = We.convert(Z.type);
      let Oe;
      if (Z.isData3DTexture)
        Ze.setTexture3D(Z, 0), Oe = k.TEXTURE_3D;
      else if (Z.isDataArrayTexture || Z.isCompressedArrayTexture)
        Ze.setTexture2DArray(Z, 0), Oe = k.TEXTURE_2D_ARRAY;
      else {
        console.warn(
          "THREE.WebGLRenderer.copyTextureToTexture3D: only supports THREE.DataTexture3D and THREE.DataTexture2DArray."
        );
        return;
      }
      k.pixelStorei(k.UNPACK_FLIP_Y_WEBGL, Z.flipY), k.pixelStorei(k.UNPACK_PREMULTIPLY_ALPHA_WEBGL, Z.premultiplyAlpha), k.pixelStorei(k.UNPACK_ALIGNMENT, Z.unpackAlignment);
      const yt = k.getParameter(k.UNPACK_ROW_LENGTH), V = k.getParameter(k.UNPACK_IMAGE_HEIGHT), le = k.getParameter(k.UNPACK_SKIP_PIXELS), ie = k.getParameter(k.UNPACK_SKIP_ROWS), ge = k.getParameter(k.UNPACK_SKIP_IMAGES);
      k.pixelStorei(k.UNPACK_ROW_LENGTH, ht.width), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, ht.height), k.pixelStorei(k.UNPACK_SKIP_PIXELS, pe), k.pixelStorei(k.UNPACK_SKIP_ROWS, Ne), k.pixelStorei(k.UNPACK_SKIP_IMAGES, De), O.isDataTexture || O.isData3DTexture ? k.texSubImage3D(
        Oe,
        z,
        Te,
        Ue,
        at,
        $,
        ce,
        se,
        At,
        lt,
        ht.data
      ) : Z.isCompressedArrayTexture ? k.compressedTexSubImage3D(
        Oe,
        z,
        Te,
        Ue,
        at,
        $,
        ce,
        se,
        At,
        ht.data
      ) : k.texSubImage3D(
        Oe,
        z,
        Te,
        Ue,
        at,
        $,
        ce,
        se,
        At,
        lt,
        ht
      ), k.pixelStorei(k.UNPACK_ROW_LENGTH, yt), k.pixelStorei(k.UNPACK_IMAGE_HEIGHT, V), k.pixelStorei(k.UNPACK_SKIP_PIXELS, le), k.pixelStorei(k.UNPACK_SKIP_ROWS, ie), k.pixelStorei(k.UNPACK_SKIP_IMAGES, ge), z === 0 && Z.generateMipmaps && k.generateMipmap(Oe), Ie.unbindTexture();
    }, this.initRenderTarget = function(O) {
      Ve.get(O).__webglFramebuffer === void 0 && Ze.setupRenderTarget(O);
    }, this.initTexture = function(O) {
      O.isCubeTexture ? Ze.setTextureCube(O, 0) : O.isData3DTexture ? Ze.setTexture3D(O, 0) : O.isDataArrayTexture || O.isCompressedArrayTexture ? Ze.setTexture2DArray(O, 0) : Ze.setTexture2D(O, 0), Ie.unbindTexture();
    }, this.resetState = function() {
      b = 0, M = 0, w = null, Ie.reset(), ot.reset();
    }, typeof __THREE_DEVTOOLS__ < "u" && __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent("observe", { detail: this }));
  }
  get coordinateSystem() {
    return rl;
  }
  get outputColorSpace() {
    return this._outputColorSpace;
  }
  set outputColorSpace(e) {
    this._outputColorSpace = e;
    const t = this.getContext();
    t.drawingBufferColorSpace = e === Ow ? "display-p3" : "srgb", t.unpackColorSpace = rn.workingColorSpace === f8 ? "display-p3" : "srgb";
  }
}
function Gs(s, e) {
  q4e(s) && (s = "100%");
  const t = Y4e(s);
  return s = e === 360 ? s : Math.min(e, Math.max(0, parseFloat(s))), t && (s = parseInt(String(s * e), 10) / 100), Math.abs(s - e) < 1e-6 ? 1 : (e === 360 ? s = (s < 0 ? s % e + e : s % e) / parseFloat(String(e)) : s = s % e / parseFloat(String(e)), s);
}
function ty(s) {
  return Math.min(1, Math.max(0, s));
}
function q4e(s) {
  return typeof s == "string" && s.indexOf(".") !== -1 && parseFloat(s) === 1;
}
function Y4e(s) {
  return typeof s == "string" && s.indexOf("%") !== -1;
}
function wU(s) {
  return s = parseFloat(s), (isNaN(s) || s < 0 || s > 1) && (s = 1), s;
}
function iy(s) {
  return Number(s) <= 1 ? `${Number(s) * 100}%` : s;
}
function xu(s) {
  return s.length === 1 ? "0" + s : String(s);
}
function Z4e(s, e, t) {
  return {
    r: Gs(s, 255) * 255,
    g: Gs(e, 255) * 255,
    b: Gs(t, 255) * 255
  };
}
function lD(s, e, t) {
  s = Gs(s, 255), e = Gs(e, 255), t = Gs(t, 255);
  const i = Math.max(s, e, t), n = Math.min(s, e, t);
  let r = 0, a = 0;
  const o = (i + n) / 2;
  if (i === n)
    a = 0, r = 0;
  else {
    const l = i - n;
    switch (a = o > 0.5 ? l / (2 - i - n) : l / (i + n), i) {
      case s:
        r = (e - t) / l + (e < t ? 6 : 0);
        break;
      case e:
        r = (t - s) / l + 2;
        break;
      case t:
        r = (s - e) / l + 4;
        break;
    }
    r /= 6;
  }
  return { h: r, s: a, l: o };
}
function x6(s, e, t) {
  return t < 0 && (t += 1), t > 1 && (t -= 1), t < 1 / 6 ? s + (e - s) * (6 * t) : t < 1 / 2 ? e : t < 2 / 3 ? s + (e - s) * (2 / 3 - t) * 6 : s;
}
function K4e(s, e, t) {
  let i, n, r;
  if (s = Gs(s, 360), e = Gs(e, 100), t = Gs(t, 100), e === 0)
    n = t, r = t, i = t;
  else {
    const a = t < 0.5 ? t * (1 + e) : t + e - t * e, o = 2 * t - a;
    i = x6(o, a, s + 1 / 3), n = x6(o, a, s), r = x6(o, a, s - 1 / 3);
  }
  return { r: i * 255, g: n * 255, b: r * 255 };
}
function cD(s, e, t) {
  s = Gs(s, 255), e = Gs(e, 255), t = Gs(t, 255);
  const i = Math.max(s, e, t), n = Math.min(s, e, t);
  let r = 0;
  const a = i, o = i - n, l = i === 0 ? 0 : o / i;
  if (i === n)
    r = 0;
  else {
    switch (i) {
      case s:
        r = (e - t) / o + (e < t ? 6 : 0);
        break;
      case e:
        r = (t - s) / o + 2;
        break;
      case t:
        r = (s - e) / o + 4;
        break;
    }
    r /= 6;
  }
  return { h: r, s: l, v: a };
}
function J4e(s, e, t) {
  s = Gs(s, 360) * 6, e = Gs(e, 100), t = Gs(t, 100);
  const i = Math.floor(s), n = s - i, r = t * (1 - e), a = t * (1 - n * e), o = t * (1 - (1 - n) * e), l = i % 6, c = [t, a, r, r, o, t][l], h = [o, t, t, a, r, r][l], u = [r, r, o, t, t, a][l];
  return { r: c * 255, g: h * 255, b: u * 255 };
}
function hD(s, e, t, i) {
  const n = [
    xu(Math.round(s).toString(16)),
    xu(Math.round(e).toString(16)),
    xu(Math.round(t).toString(16))
  ];
  return i && n[0].startsWith(n[0].charAt(1)) && n[1].startsWith(n[1].charAt(1)) && n[2].startsWith(n[2].charAt(1)) ? n[0].charAt(0) + n[1].charAt(0) + n[2].charAt(0) : n.join("");
}
function Q4e(s, e, t, i, n) {
  const r = [
    xu(Math.round(s).toString(16)),
    xu(Math.round(e).toString(16)),
    xu(Math.round(t).toString(16)),
    xu(e8e(i))
  ];
  return n && r[0].startsWith(r[0].charAt(1)) && r[1].startsWith(r[1].charAt(1)) && r[2].startsWith(r[2].charAt(1)) && r[3].startsWith(r[3].charAt(1)) ? r[0].charAt(0) + r[1].charAt(0) + r[2].charAt(0) + r[3].charAt(0) : r.join("");
}
function $4e(s, e, t, i) {
  const n = s / 100, r = e / 100, a = t / 100, o = i / 100, l = 255 * (1 - n) * (1 - o), c = 255 * (1 - r) * (1 - o), h = 255 * (1 - a) * (1 - o);
  return { r: l, g: c, b: h };
}
function uD(s, e, t) {
  let i = 1 - s / 255, n = 1 - e / 255, r = 1 - t / 255, a = Math.min(i, n, r);
  return a === 1 ? (i = 0, n = 0, r = 0) : (i = (i - a) / (1 - a) * 100, n = (n - a) / (1 - a) * 100, r = (r - a) / (1 - a) * 100), a *= 100, {
    c: Math.round(i),
    m: Math.round(n),
    y: Math.round(r),
    k: Math.round(a)
  };
}
function e8e(s) {
  return Math.round(parseFloat(s) * 255).toString(16);
}
function dD(s) {
  return Zr(s) / 255;
}
function Zr(s) {
  return parseInt(s, 16);
}
function t8e(s) {
  return {
    r: s >> 16,
    g: (s & 65280) >> 8,
    b: s & 255
  };
}
const eM = {
  aliceblue: "#f0f8ff",
  antiquewhite: "#faebd7",
  aqua: "#00ffff",
  aquamarine: "#7fffd4",
  azure: "#f0ffff",
  beige: "#f5f5dc",
  bisque: "#ffe4c4",
  black: "#000000",
  blanchedalmond: "#ffebcd",
  blue: "#0000ff",
  blueviolet: "#8a2be2",
  brown: "#a52a2a",
  burlywood: "#deb887",
  cadetblue: "#5f9ea0",
  chartreuse: "#7fff00",
  chocolate: "#d2691e",
  coral: "#ff7f50",
  cornflowerblue: "#6495ed",
  cornsilk: "#fff8dc",
  crimson: "#dc143c",
  cyan: "#00ffff",
  darkblue: "#00008b",
  darkcyan: "#008b8b",
  darkgoldenrod: "#b8860b",
  darkgray: "#a9a9a9",
  darkgreen: "#006400",
  darkgrey: "#a9a9a9",
  darkkhaki: "#bdb76b",
  darkmagenta: "#8b008b",
  darkolivegreen: "#556b2f",
  darkorange: "#ff8c00",
  darkorchid: "#9932cc",
  darkred: "#8b0000",
  darksalmon: "#e9967a",
  darkseagreen: "#8fbc8f",
  darkslateblue: "#483d8b",
  darkslategray: "#2f4f4f",
  darkslategrey: "#2f4f4f",
  darkturquoise: "#00ced1",
  darkviolet: "#9400d3",
  deeppink: "#ff1493",
  deepskyblue: "#00bfff",
  dimgray: "#696969",
  dimgrey: "#696969",
  dodgerblue: "#1e90ff",
  firebrick: "#b22222",
  floralwhite: "#fffaf0",
  forestgreen: "#228b22",
  fuchsia: "#ff00ff",
  gainsboro: "#dcdcdc",
  ghostwhite: "#f8f8ff",
  goldenrod: "#daa520",
  gold: "#ffd700",
  gray: "#808080",
  green: "#008000",
  greenyellow: "#adff2f",
  grey: "#808080",
  honeydew: "#f0fff0",
  hotpink: "#ff69b4",
  indianred: "#cd5c5c",
  indigo: "#4b0082",
  ivory: "#fffff0",
  khaki: "#f0e68c",
  lavenderblush: "#fff0f5",
  lavender: "#e6e6fa",
  lawngreen: "#7cfc00",
  lemonchiffon: "#fffacd",
  lightblue: "#add8e6",
  lightcoral: "#f08080",
  lightcyan: "#e0ffff",
  lightgoldenrodyellow: "#fafad2",
  lightgray: "#d3d3d3",
  lightgreen: "#90ee90",
  lightgrey: "#d3d3d3",
  lightpink: "#ffb6c1",
  lightsalmon: "#ffa07a",
  lightseagreen: "#20b2aa",
  lightskyblue: "#87cefa",
  lightslategray: "#778899",
  lightslategrey: "#778899",
  lightsteelblue: "#b0c4de",
  lightyellow: "#ffffe0",
  lime: "#00ff00",
  limegreen: "#32cd32",
  linen: "#faf0e6",
  magenta: "#ff00ff",
  maroon: "#800000",
  mediumaquamarine: "#66cdaa",
  mediumblue: "#0000cd",
  mediumorchid: "#ba55d3",
  mediumpurple: "#9370db",
  mediumseagreen: "#3cb371",
  mediumslateblue: "#7b68ee",
  mediumspringgreen: "#00fa9a",
  mediumturquoise: "#48d1cc",
  mediumvioletred: "#c71585",
  midnightblue: "#191970",
  mintcream: "#f5fffa",
  mistyrose: "#ffe4e1",
  moccasin: "#ffe4b5",
  navajowhite: "#ffdead",
  navy: "#000080",
  oldlace: "#fdf5e6",
  olive: "#808000",
  olivedrab: "#6b8e23",
  orange: "#ffa500",
  orangered: "#ff4500",
  orchid: "#da70d6",
  palegoldenrod: "#eee8aa",
  palegreen: "#98fb98",
  paleturquoise: "#afeeee",
  palevioletred: "#db7093",
  papayawhip: "#ffefd5",
  peachpuff: "#ffdab9",
  peru: "#cd853f",
  pink: "#ffc0cb",
  plum: "#dda0dd",
  powderblue: "#b0e0e6",
  purple: "#800080",
  rebeccapurple: "#663399",
  red: "#ff0000",
  rosybrown: "#bc8f8f",
  royalblue: "#4169e1",
  saddlebrown: "#8b4513",
  salmon: "#fa8072",
  sandybrown: "#f4a460",
  seagreen: "#2e8b57",
  seashell: "#fff5ee",
  sienna: "#a0522d",
  silver: "#c0c0c0",
  skyblue: "#87ceeb",
  slateblue: "#6a5acd",
  slategray: "#708090",
  slategrey: "#708090",
  snow: "#fffafa",
  springgreen: "#00ff7f",
  steelblue: "#4682b4",
  tan: "#d2b48c",
  teal: "#008080",
  thistle: "#d8bfd8",
  tomato: "#ff6347",
  turquoise: "#40e0d0",
  violet: "#ee82ee",
  wheat: "#f5deb3",
  white: "#ffffff",
  whitesmoke: "#f5f5f5",
  yellow: "#ffff00",
  yellowgreen: "#9acd32"
};
function i8e(s) {
  let e = { r: 0, g: 0, b: 0 }, t = 1, i = null, n = null, r = null, a = !1, o = !1;
  return typeof s == "string" && (s = r8e(s)), typeof s == "object" && (qr(s.r) && qr(s.g) && qr(s.b) ? (e = Z4e(s.r, s.g, s.b), a = !0, o = String(s.r).substr(-1) === "%" ? "prgb" : "rgb") : qr(s.h) && qr(s.s) && qr(s.v) ? (i = iy(s.s), n = iy(s.v), e = J4e(s.h, i, n), a = !0, o = "hsv") : qr(s.h) && qr(s.s) && qr(s.l) ? (i = iy(s.s), r = iy(s.l), e = K4e(s.h, i, r), a = !0, o = "hsl") : qr(s.c) && qr(s.m) && qr(s.y) && qr(s.k) && (e = $4e(s.c, s.m, s.y, s.k), a = !0, o = "cmyk"), Object.prototype.hasOwnProperty.call(s, "a") && (t = s.a)), t = wU(t), {
    ok: a,
    format: s.format || o,
    r: Math.min(255, Math.max(e.r, 0)),
    g: Math.min(255, Math.max(e.g, 0)),
    b: Math.min(255, Math.max(e.b, 0)),
    a: t
  };
}
const n8e = "[-\\+]?\\d+%?", s8e = "[-\\+]?\\d*\\.\\d+%?", Pc = "(?:" + s8e + ")|(?:" + n8e + ")", A6 = "[\\s|\\(]+(" + Pc + ")[,|\\s]+(" + Pc + ")[,|\\s]+(" + Pc + ")\\s*\\)?", ny = (
  // eslint-disable-next-line prettier/prettier
  "[\\s|\\(]+(" + Pc + ")[,|\\s]+(" + Pc + ")[,|\\s]+(" + Pc + ")[,|\\s]+(" + Pc + ")\\s*\\)?"
), da = {
  CSS_UNIT: new RegExp(Pc),
  rgb: new RegExp("rgb" + A6),
  rgba: new RegExp("rgba" + ny),
  hsl: new RegExp("hsl" + A6),
  hsla: new RegExp("hsla" + ny),
  hsv: new RegExp("hsv" + A6),
  hsva: new RegExp("hsva" + ny),
  cmyk: new RegExp("cmyk" + ny),
  hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
  hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
  hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
  hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
};
function r8e(s) {
  if (s = s.trim().toLowerCase(), s.length === 0)
    return !1;
  let e = !1;
  if (eM[s])
    s = eM[s], e = !0;
  else if (s === "transparent")
    return { r: 0, g: 0, b: 0, a: 0, format: "name" };
  let t = da.rgb.exec(s);
  return t ? { r: t[1], g: t[2], b: t[3] } : (t = da.rgba.exec(s), t ? { r: t[1], g: t[2], b: t[3], a: t[4] } : (t = da.hsl.exec(s), t ? { h: t[1], s: t[2], l: t[3] } : (t = da.hsla.exec(s), t ? { h: t[1], s: t[2], l: t[3], a: t[4] } : (t = da.hsv.exec(s), t ? { h: t[1], s: t[2], v: t[3] } : (t = da.hsva.exec(s), t ? { h: t[1], s: t[2], v: t[3], a: t[4] } : (t = da.cmyk.exec(s), t ? {
    c: t[1],
    m: t[2],
    y: t[3],
    k: t[4]
  } : (t = da.hex8.exec(s), t ? {
    r: Zr(t[1]),
    g: Zr(t[2]),
    b: Zr(t[3]),
    a: dD(t[4]),
    format: e ? "name" : "hex8"
  } : (t = da.hex6.exec(s), t ? {
    r: Zr(t[1]),
    g: Zr(t[2]),
    b: Zr(t[3]),
    format: e ? "name" : "hex"
  } : (t = da.hex4.exec(s), t ? {
    r: Zr(t[1] + t[1]),
    g: Zr(t[2] + t[2]),
    b: Zr(t[3] + t[3]),
    a: dD(t[4] + t[4]),
    format: e ? "name" : "hex8"
  } : (t = da.hex3.exec(s), t ? {
    r: Zr(t[1] + t[1]),
    g: Zr(t[2] + t[2]),
    b: Zr(t[3] + t[3]),
    format: e ? "name" : "hex"
  } : !1))))))))));
}
function qr(s) {
  return typeof s == "number" ? !Number.isNaN(s) : da.CSS_UNIT.test(s);
}
class pi {
  constructor(e = "", t = {}) {
    if (e instanceof pi)
      return e;
    typeof e == "number" && (e = t8e(e)), this.originalInput = e;
    const i = i8e(e);
    this.originalInput = e, this.r = i.r, this.g = i.g, this.b = i.b, this.a = i.a, this.roundA = Math.round(100 * this.a) / 100, this.format = t.format ?? i.format, this.gradientType = t.gradientType, this.r < 1 && (this.r = Math.round(this.r)), this.g < 1 && (this.g = Math.round(this.g)), this.b < 1 && (this.b = Math.round(this.b)), this.isValid = i.ok;
  }
  isDark() {
    return this.getBrightness() < 128;
  }
  isLight() {
    return !this.isDark();
  }
  /**
   * Returns the perceived brightness of the color, from 0-255.
   */
  getBrightness() {
    const e = this.toRgb();
    return (e.r * 299 + e.g * 587 + e.b * 114) / 1e3;
  }
  /**
   * Returns the perceived luminance of a color, from 0-1.
   */
  getLuminance() {
    const e = this.toRgb();
    let t, i, n;
    const r = e.r / 255, a = e.g / 255, o = e.b / 255;
    return r <= 0.03928 ? t = r / 12.92 : t = Math.pow((r + 0.055) / 1.055, 2.4), a <= 0.03928 ? i = a / 12.92 : i = Math.pow((a + 0.055) / 1.055, 2.4), o <= 0.03928 ? n = o / 12.92 : n = Math.pow((o + 0.055) / 1.055, 2.4), 0.2126 * t + 0.7152 * i + 0.0722 * n;
  }
  /**
   * Returns the alpha value of a color, from 0-1.
   */
  getAlpha() {
    return this.a;
  }
  /**
   * Sets the alpha value on the current color.
   *
   * @param alpha - The new alpha value. The accepted range is 0-1.
   */
  setAlpha(e) {
    return this.a = wU(e), this.roundA = Math.round(100 * this.a) / 100, this;
  }
  /**
   * Returns whether the color is monochrome.
   */
  isMonochrome() {
    const { s: e } = this.toHsl();
    return e === 0;
  }
  /**
   * Returns the object as a HSVA object.
   */
  toHsv() {
    const e = cD(this.r, this.g, this.b);
    return { h: e.h * 360, s: e.s, v: e.v, a: this.a };
  }
  /**
   * Returns the hsva values interpolated into a string with the following format:
   * "hsva(xxx, xxx, xxx, xx)".
   */
  toHsvString() {
    const e = cD(this.r, this.g, this.b), t = Math.round(e.h * 360), i = Math.round(e.s * 100), n = Math.round(e.v * 100);
    return this.a === 1 ? `hsv(${t}, ${i}%, ${n}%)` : `hsva(${t}, ${i}%, ${n}%, ${this.roundA})`;
  }
  /**
   * Returns the object as a HSLA object.
   */
  toHsl() {
    const e = lD(this.r, this.g, this.b);
    return { h: e.h * 360, s: e.s, l: e.l, a: this.a };
  }
  /**
   * Returns the hsla values interpolated into a string with the following format:
   * "hsla(xxx, xxx, xxx, xx)".
   */
  toHslString() {
    const e = lD(this.r, this.g, this.b), t = Math.round(e.h * 360), i = Math.round(e.s * 100), n = Math.round(e.l * 100);
    return this.a === 1 ? `hsl(${t}, ${i}%, ${n}%)` : `hsla(${t}, ${i}%, ${n}%, ${this.roundA})`;
  }
  /**
   * Returns the hex value of the color.
   * @param allow3Char will shorten hex value to 3 char if possible
   */
  toHex(e = !1) {
    return hD(this.r, this.g, this.b, e);
  }
  /**
   * Returns the hex value of the color -with a # prefixed.
   * @param allow3Char will shorten hex value to 3 char if possible
   */
  toHexString(e = !1) {
    return "#" + this.toHex(e);
  }
  /**
   * Returns the hex 8 value of the color.
   * @param allow4Char will shorten hex value to 4 char if possible
   */
  toHex8(e = !1) {
    return Q4e(this.r, this.g, this.b, this.a, e);
  }
  /**
   * Returns the hex 8 value of the color -with a # prefixed.
   * @param allow4Char will shorten hex value to 4 char if possible
   */
  toHex8String(e = !1) {
    return "#" + this.toHex8(e);
  }
  /**
   * Returns the shorter hex value of the color depends on its alpha -with a # prefixed.
   * @param allowShortChar will shorten hex value to 3 or 4 char if possible
   */
  toHexShortString(e = !1) {
    return this.a === 1 ? this.toHexString(e) : this.toHex8String(e);
  }
  /**
   * Returns the object as a RGBA object.
   */
  toRgb() {
    return {
      r: Math.round(this.r),
      g: Math.round(this.g),
      b: Math.round(this.b),
      a: this.a
    };
  }
  /**
   * Returns the RGBA values interpolated into a string with the following format:
   * "RGBA(xxx, xxx, xxx, xx)".
   */
  toRgbString() {
    const e = Math.round(this.r), t = Math.round(this.g), i = Math.round(this.b);
    return this.a === 1 ? `rgb(${e}, ${t}, ${i})` : `rgba(${e}, ${t}, ${i}, ${this.roundA})`;
  }
  /**
   * Returns the object as a RGBA object.
   */
  toPercentageRgb() {
    const e = (t) => `${Math.round(Gs(t, 255) * 100)}%`;
    return {
      r: e(this.r),
      g: e(this.g),
      b: e(this.b),
      a: this.a
    };
  }
  /**
   * Returns the RGBA relative values interpolated into a string
   */
  toPercentageRgbString() {
    const e = (t) => Math.round(Gs(t, 255) * 100);
    return this.a === 1 ? `rgb(${e(this.r)}%, ${e(this.g)}%, ${e(this.b)}%)` : `rgba(${e(this.r)}%, ${e(this.g)}%, ${e(this.b)}%, ${this.roundA})`;
  }
  toCmyk() {
    return {
      ...uD(this.r, this.g, this.b)
    };
  }
  toCmykString() {
    const { c: e, m: t, y: i, k: n } = uD(this.r, this.g, this.b);
    return `cmyk(${e}, ${t}, ${i}, ${n})`;
  }
  /**
   * The 'real' name of the color -if there is one.
   */
  toName() {
    if (this.a === 0)
      return "transparent";
    if (this.a < 1)
      return !1;
    const e = "#" + hD(this.r, this.g, this.b, !1);
    for (const [t, i] of Object.entries(eM))
      if (e === i)
        return t;
    return !1;
  }
  toString(e) {
    const t = !!e;
    e = e ?? this.format;
    let i = !1;
    const n = this.a < 1 && this.a >= 0;
    return !t && n && (e.startsWith("hex") || e === "name") ? e === "name" && this.a === 0 ? this.toName() : this.toRgbString() : (e === "rgb" && (i = this.toRgbString()), e === "prgb" && (i = this.toPercentageRgbString()), (e === "hex" || e === "hex6") && (i = this.toHexString()), e === "hex3" && (i = this.toHexString(!0)), e === "hex4" && (i = this.toHex8String(!0)), e === "hex8" && (i = this.toHex8String()), e === "name" && (i = this.toName()), e === "hsl" && (i = this.toHslString()), e === "hsv" && (i = this.toHsvString()), e === "cmyk" && (i = this.toCmykString()), i || this.toHexString());
  }
  toNumber() {
    return (Math.round(this.r) << 16) + (Math.round(this.g) << 8) + Math.round(this.b);
  }
  clone() {
    return new pi(this.toString());
  }
  /**
   * Lighten the color a given amount. Providing 100 will always return white.
   * @param amount - valid between 1-100
   */
  lighten(e = 10) {
    const t = this.toHsl();
    return t.l += e / 100, t.l = ty(t.l), new pi(t);
  }
  /**
   * Brighten the color a given amount, from 0 to 100.
   * @param amount - valid between 1-100
   */
  brighten(e = 10) {
    const t = this.toRgb();
    return t.r = Math.max(0, Math.min(255, t.r - Math.round(255 * -(e / 100)))), t.g = Math.max(0, Math.min(255, t.g - Math.round(255 * -(e / 100)))), t.b = Math.max(0, Math.min(255, t.b - Math.round(255 * -(e / 100)))), new pi(t);
  }
  /**
   * Darken the color a given amount, from 0 to 100.
   * Providing 100 will always return black.
   * @param amount - valid between 1-100
   */
  darken(e = 10) {
    const t = this.toHsl();
    return t.l -= e / 100, t.l = ty(t.l), new pi(t);
  }
  /**
   * Mix the color with pure white, from 0 to 100.
   * Providing 0 will do nothing, providing 100 will always return white.
   * @param amount - valid between 1-100
   */
  tint(e = 10) {
    return this.mix("white", e);
  }
  /**
   * Mix the color with pure black, from 0 to 100.
   * Providing 0 will do nothing, providing 100 will always return black.
   * @param amount - valid between 1-100
   */
  shade(e = 10) {
    return this.mix("black", e);
  }
  /**
   * Desaturate the color a given amount, from 0 to 100.
   * Providing 100 will is the same as calling greyscale
   * @param amount - valid between 1-100
   */
  desaturate(e = 10) {
    const t = this.toHsl();
    return t.s -= e / 100, t.s = ty(t.s), new pi(t);
  }
  /**
   * Saturate the color a given amount, from 0 to 100.
   * @param amount - valid between 1-100
   */
  saturate(e = 10) {
    const t = this.toHsl();
    return t.s += e / 100, t.s = ty(t.s), new pi(t);
  }
  /**
   * Completely desaturates a color into greyscale.
   * Same as calling `desaturate(100)`
   */
  greyscale() {
    return this.desaturate(100);
  }
  /**
   * Spin takes a positive or negative amount within [-360, 360] indicating the change of hue.
   * Values outside of this range will be wrapped into this range.
   */
  spin(e) {
    const t = this.toHsl(), i = (t.h + e) % 360;
    return t.h = i < 0 ? 360 + i : i, new pi(t);
  }
  /**
   * Mix the current color a given amount with another color, from 0 to 100.
   * 0 means no mixing (return current color).
   */
  mix(e, t = 50) {
    const i = this.toRgb(), n = new pi(e).toRgb(), r = t / 100, a = {
      r: (n.r - i.r) * r + i.r,
      g: (n.g - i.g) * r + i.g,
      b: (n.b - i.b) * r + i.b,
      a: (n.a - i.a) * r + i.a
    };
    return new pi(a);
  }
  analogous(e = 6, t = 30) {
    const i = this.toHsl(), n = 360 / t, r = [this];
    for (i.h = (i.h - (n * e >> 1) + 720) % 360; --e; )
      i.h = (i.h + n) % 360, r.push(new pi(i));
    return r;
  }
  /**
   * taken from https://github.com/infusion/jQuery-xcolor/blob/master/jquery.xcolor.js
   */
  complement() {
    const e = this.toHsl();
    return e.h = (e.h + 180) % 360, new pi(e);
  }
  monochromatic(e = 6) {
    const t = this.toHsv(), { h: i } = t, { s: n } = t;
    let { v: r } = t;
    const a = [], o = 1 / e;
    for (; e--; )
      a.push(new pi({ h: i, s: n, v: r })), r = (r + o) % 1;
    return a;
  }
  splitcomplement() {
    const e = this.toHsl(), { h: t } = e;
    return [
      this,
      new pi({ h: (t + 72) % 360, s: e.s, l: e.l }),
      new pi({ h: (t + 216) % 360, s: e.s, l: e.l })
    ];
  }
  /**
   * Compute how the color would appear on a background
   */
  onBackground(e) {
    const t = this.toRgb(), i = new pi(e).toRgb(), n = t.a + i.a * (1 - t.a);
    return new pi({
      r: (t.r * t.a + i.r * i.a * (1 - t.a)) / n,
      g: (t.g * t.a + i.g * i.a * (1 - t.a)) / n,
      b: (t.b * t.a + i.b * i.a * (1 - t.a)) / n,
      a: n
    });
  }
  /**
   * Alias for `polyad(3)`
   */
  triad() {
    return this.polyad(3);
  }
  /**
   * Alias for `polyad(4)`
   */
  tetrad() {
    return this.polyad(4);
  }
  /**
   * Get polyad colors, like (for 1, 2, 3, 4, 5, 6, 7, 8, etc...)
   * monad, dyad, triad, tetrad, pentad, hexad, heptad, octad, etc...
   */
  polyad(e) {
    const t = this.toHsl(), { h: i } = t, n = [this], r = 360 / e;
    for (let a = 1; a < e; a++)
      n.push(new pi({ h: (i + a * r) % 360, s: t.s, l: t.l }));
    return n;
  }
  /**
   * compare color vs current color
   */
  equals(e) {
    const t = new pi(e);
    return this.format === "cmyk" || t.format === "cmyk" ? this.toCmykString() === t.toCmykString() : this.toRgbString() === t.toRgbString();
  }
}
function dl(s) {
  return new _e(s.r / 255, s.g / 255, s.b / 255);
}
function EU(s) {
  const e = new pi(s);
  return { color: new _e(e.r / 255, e.g / 255, e.b / 255), alpha: e.a };
}
Ip.install({ THREE: _I });
const n4 = class n4 extends ed {
  constructor(t) {
    super(t);
    S(this, "_dynamicNearFarFactor", -1);
    S(this, "_finalComposer");
    S(this, "_views", []);
    S(this, "_rendererSize", new Se());
    S(this, "event", new es());
    S(this, "cameraControls");
    S(this, "renderQueue", new VF());
    S(this, "_preRenderCallbacks", { _next: null });
    S(this, "_postRenderCallbacks", { _next: null });
    S(this, "isReady", !1);
    S(this, "cameraControlToggleInterval", 3);
    S(this, "threeRenderer", new SU({
      alpha: !0,
      antialias: !0
      // powerPreference: 'high-performance',
      // logarithmicDepthBuffer: true,
    }));
    S(this, "cssRenderer", new I2e(document.createElement("div")));
    S(this, "orthographicCamera", new Xn(
      -this._rendererSize.x,
      this._rendererSize.x,
      this._rendererSize.y,
      -this._rendererSize.y,
      0.5,
      5e4
    ));
    S(this, "perspectiveCamera", new Zt(60, 1, 0.5, 5e4));
    S(this, "mainCamera", this.perspectiveCamera);
    // 活动相机，主要负责第三人称等特殊视角时使用
    S(this, "activeCamera");
    S(this, "rootScene", new Da());
    S(this, "viewMode", gu.Normal);
    S(this, "viewHelper");
    S(this, "background");
    S(this, "freeze", !1);
    S(this, "render", (t) => {
      if (this.updateSize(), this.threeRenderer.getSize(this._rendererSize), this._adjustCameraNearFar(), this.freeze)
        return;
      this.renderQueue.render(), this.cameraControls.update(t) && this.mainCamera instanceof Xn && this.event.dispatchEvent("plottingScaleUpdate", this.getPlottingScale());
      const n = this.activeCamera ?? this.mainCamera;
      this.componentMap.forEach((r) => {
        if (r.hasRender)
          switch (this.viewMode) {
            case gu.FourView:
              this._views.forEach((a) => {
                a.needsUpdate && (a.updateCamera(n), r.render(this.rootScene, a.camera), a.draw(this.threeRenderer.domElement));
              }), this.cssRenderer.domElement.style.display === "flex" && (this.cssRenderer.domElement.style.display = "none");
              break;
            default:
              r.render(this.rootScene, n), this.cssRenderer.domElement.style.display === "none" && (this.cssRenderer.domElement.style.display = "flex"), this.cssRenderer.render(this.rootScene, n);
              break;
          }
      }), this.threeRenderer.autoClear = !1, this.viewHelper.dom = this.threeRenderer.domElement, this.viewHelper.render(this.threeRenderer), this.event.dispatchEvent("afterRender", void 0), this.threeRenderer.setViewport(0, 0, this._rendererSize.x, this._rendererSize.y), this.threeRenderer.setScissor(0, 0, this._rendererSize.x, this._rendererSize.y), this.threeRenderer.setScissorTest(!0), this.threeRenderer.autoClear = !0;
    });
    this.cameraControls = new Ip(this.mainCamera, this.threeRenderer.domElement), this.cameraControls.maxDistance = 1e5, this.cameraControls.setTarget(0, 0, 0), "setPosition" in this.cameraControls && this.cameraControls.setPosition(-1.1, 59.6, 33.4, !1), this.cameraControls.scene = this.rootScene, this.viewHelper = new RF(
      this.mainCamera,
      this.threeRenderer.domElement,
      this.cameraControls
    ), this.background = this.rootScene.background;
  }
  get dynamicNearFarFactor() {
    return this._dynamicNearFarFactor;
  }
  set dynamicNearFarFactor(t) {
    this._dynamicNearFarFactor = t;
  }
  get rendererSize() {
    return this._rendererSize;
  }
  _adjustCameraNearFar() {
    if (this.dynamicNearFarFactor <= 0)
      return;
    const { camera: t } = this.cameraControls;
    if (t instanceof Zt) {
      const i = 2 * Math.tan(t.fov * Math.PI / 180) * this.cameraControls.distance;
      t.near = i / (100 * Math.atan(this.dynamicNearFarFactor)), t.far = i * 20, t.updateProjectionMatrix();
    }
  }
  _addImageQualityComponent() {
    const t = Za(this.uuid, "ImageQuality"), i = this.addComponent(
      t,
      "ImageQuality",
      x7,
      !0
    );
    return i.initialize(), i;
  }
  _addOutlinePassComponent() {
    const t = Za(this.uuid, "Outline"), i = this.addComponent(
      t,
      "Outline",
      g7,
      !0
    );
    return i.initialize(this._rendererSize.clone(), this.rootScene, this.mainCamera), i;
  }
  _addSSAOPassComponent() {
    const t = Za(this.uuid, "SSAO"), i = this.addComponent(t, "SSAO", v7, !0);
    return i.initialize(this.rootScene, this.mainCamera), i;
  }
  _addTaaPassComponent() {
    const t = Za(this.uuid, "Taa"), i = this.addComponent(t, "Taa", y7, !0);
    return i.initialize(this), i;
  }
  _addColorAdjustmentPassComponent() {
    const t = Za(this.uuid, "ColorAdjustment"), i = this.addComponent(
      t,
      "ColorAdjustmentComponent",
      A7,
      !0
    );
    return i.initialize(), i;
  }
  _addBloomEffectPassComponent() {
    const t = Za(this.uuid, "BloomEffect"), i = this.addComponent(
      t,
      "BloomEffectComponent",
      m7,
      !0
    ), n = new Se();
    return this.threeRenderer.getDrawingBufferSize(n), i.initialize(n, 1, 0, 1), i;
  }
  _addFinalComposerComponent(t, i, n) {
    const r = Za(this.uuid, "FinalComposer"), a = this.addComponent(
      r,
      "FinalComposer",
      s7,
      !0
    );
    return a.initialize(t, i, n), a;
  }
  append(t) {
    const i = document.getElementById(t);
    i !== null && (this.initThreeRenderer(i), this.initCssRenderer(), n4.ContainerId = t, this._finalComposer = this._addFinalComposerComponent(
      [
        this._addOutlinePassComponent().pass,
        this._addTaaPassComponent().pass,
        this._addSSAOPassComponent().pass,
        this._addImageQualityComponent().pass,
        this._addBloomEffectPassComponent().pass,
        this._addColorAdjustmentPassComponent().pass
      ],
      this.rootScene,
      this.mainCamera
    ), this.viewHelper.appendShade(i), this.setBackgroundColor("#ffffff", 0));
  }
  toggleCamera(t) {
    let i = "perspective";
    if (this.rootScene.background && (this.background = this.rootScene.background), t)
      switch (t) {
        case "perspective":
          this.setMainCameraPerspective(), i = "perspective";
          break;
        case "orthographic":
          this.setMainCameraOrthographic(), i = "orthographic";
          break;
      }
    else {
      if (this.mainCamera instanceof Zt)
        return this.setMainCameraOrthographic();
      if (this.mainCamera instanceof Xn)
        return this.setMainCameraPerspective();
    }
    return i;
  }
  setMainCameraPerspective() {
    return this.setPerspectiveCamera(), this.viewHelper.editorCamera = this.mainCamera, this.rootScene.background = this.background, this.event.dispatchEvent("toggleCamera", "perspective"), "perspective";
  }
  setMainCameraOrthographic() {
    return this.setOrthographicCamera(), this.background = this.rootScene.background, this.rootScene.background = null, this.viewHelper.editorCamera = this.mainCamera, this.event.dispatchEvent("toggleCamera", "orthographic"), "orthographic";
  }
  getCameraType() {
    return this.mainCamera instanceof Zt ? "perspective" : this.mainCamera instanceof Xn ? "orthographic" : "perspective";
  }
  // @smooth()
  romaCamera(t, i, n) {
    const { cameraControls: r } = this;
    switch (t) {
      case "a":
        r.truck(-0.1 * i * n, 0, !1);
        break;
      case "d":
        r.truck(0.1 * i * n, 0, !1);
        break;
      case "w":
        r.forward(0.1 * i * n, !1);
        break;
      case "s":
        r.forward(-0.1 * i * n, !1);
        break;
      case "q":
        r.truck(0, 0.1 * i * n, !1);
        break;
      case "e":
        r.truck(0, -0.1 * i * n, !1);
        break;
    }
  }
  setOrthographicCamera() {
    this.orthographicCamera.position.copy(this.perspectiveCamera.position);
    const t = this.perspectiveCamera.position.distanceTo(
      this.cameraControls.getTarget(new _())
    ), i = Fhe(this.perspectiveCamera, t) / 2, n = rF(this.perspectiveCamera, t) / 2;
    this.orthographicCamera.top = n, this.orthographicCamera.bottom = -n, this.orthographicCamera.left = -i, this.orthographicCamera.right = i, this.orthographicCamera.zoom = 1, this.orthographicCamera.near = this.perspectiveCamera.near, this.orthographicCamera.far = this.perspectiveCamera.far, this.orthographicCamera.lookAt(this.cameraControls.getTarget(new _())), this.orthographicCamera.updateProjectionMatrix(), this.mainCamera = this.orthographicCamera, this.cameraControls.camera = this.mainCamera, this.cameraControls.mouseButtons.wheel = $e.ZOOM, this.cameraControls.update(0);
  }
  setPerspectiveCamera() {
    const t = this.perspectiveCamera.position.y;
    this.cameraControls.zoomTo(1), this.perspectiveCamera.position.copy(this.orthographicCamera.position), this.perspectiveCamera.position.y = t / this.orthographicCamera.zoom, this.perspectiveCamera.aspect = this._rendererSize.x / this._rendererSize.y, this.perspectiveCamera.updateProjectionMatrix(), this.cameraControls.camera = this.perspectiveCamera, this.mainCamera = this.perspectiveCamera, this.cameraControls.mouseButtons.wheel = $e.DOLLY, this.cameraControls.update(0);
  }
  restoreRender(t) {
    location.reload();
  }
  setPostProcessOptions(t) {
    var h, u, d;
    const { taa: i, ssao: n, imageQuality: r, colorAdjustment: a, bloomEffect: o, outLine: l } = t;
    (h = this.getComponentByType("ImageQuality")) == null || h.setOptions(r), (u = this.getComponentByType("SSAO")) == null || u.setOptions(n), (d = this.getComponentByType("Taa")) == null || d.setOptions(i), this.updateColorAdjustmentPostprocessing(a), this.updateBloomEffectPostprocessing(o);
    const c = this.getComponentByType("Outline");
    if (c) {
      c.pass.edgeStrength = l.strength;
      const f = dl(new pi(l.color.pure));
      c.pass.visibleEdgeColor = f, c.pass.enabled = l.enable;
    }
  }
  setOutlineObjects(t) {
    const i = this.getComponentByType("Outline");
    i == null || i.setObjects(t);
  }
  addObjectsToOutLinePass(t) {
    const i = this.getComponentByType("Outline");
    i && i.pass && t.forEach((n) => {
      i.pass.selectedObjects.findIndex((a) => a.id === n.id) === -1 && i.pass.selectedObjects.push(n);
    });
  }
  removeObjectsFromOutLinePass(t) {
    const i = this.getComponentByType("Outline");
    i && i.pass && t.forEach((n) => {
      const r = i.pass.selectedObjects.findIndex((a) => a.id === n.id);
      r !== -1 && i.pass.selectedObjects.splice(r, 1);
    });
  }
  setViewMode(t) {
    this.viewMode = t, this.viewMode !== gu.Normal ? this._views.forEach((i) => i.appendChild()) : this._views.forEach((i) => i.removeParent());
  }
  getViewCamera(t) {
    let i = this.mainCamera;
    return this._views.forEach((n) => {
      n.canvas === t && (i = n.camera);
    }), i;
  }
  getCameraPoint() {
    return [
      this.cameraControls.getPosition(new _()),
      this.cameraControls.getTarget(new _())
    ];
  }
  initCssRenderer() {
    const t = this.threeRenderer.getSize(new Se());
    this.cssRenderer.setSize(t.x, t.y), this.cssRenderer.domElement.style.position = "absolute", this.cssRenderer.domElement.style.top = "0px", this.cssRenderer.domElement.style.width = `${t.x}px`, this.cssRenderer.domElement.style.height = `${t.y}px`, this.cssRenderer.domElement.style.pointerEvents = "none";
    const i = this.threeRenderer.domElement.parentNode;
    i && i.appendChild(this.cssRenderer.domElement);
  }
  initThreeRenderer(t) {
    t.appendChild(this.threeRenderer.domElement), this.updateSize(), this.isReady = !0, this.threeRenderer.toneMapping = Hp, this.threeRenderer.shadowMap.enabled = !0, this.threeRenderer.shadowMap.type = $g, this.threeRenderer.localClippingEnabled = !0, this.threeRenderer.setClearColor("rgb(0,0,0)");
  }
  updateSize() {
    const { domElement: t } = this.threeRenderer, i = t.parentElement;
    if (i && (i.clientWidth !== this._rendererSize.x || i.clientHeight !== this._rendererSize.y)) {
      const n = Math.max(1, i.clientWidth), r = Math.max(1, i.clientHeight);
      this.setSize(n, r);
    }
  }
  setSize(t, i) {
    this.threeRenderer.setPixelRatio(window.devicePixelRatio), this.threeRenderer.setSize(t, i), this.cssRenderer.setSize(t, i), this.mainCamera instanceof Zt && (this.mainCamera.aspect = t / i), this.mainCamera.updateProjectionMatrix(), this._rendererSize.set(t, i);
  }
  removeDomElement() {
    var t, i;
    this.isReady && ((t = this.threeRenderer.domElement.parentElement) == null || t.removeChild(this.threeRenderer.domElement), (i = this.cssRenderer.domElement.parentElement) == null || i.removeChild(this.cssRenderer.domElement), this.isReady = !1);
  }
  setBackgroundColor(t, i) {
    var a, o;
    this.threeRenderer.setClearColor(t, i);
    const n = (a = this.getComponentByType("ImageQuality")) == null ? void 0 : a.pass;
    n && (n.opacity = i);
    const r = (o = this.getComponentByType("Taa")) == null ? void 0 : o.pass;
    r && (r.opacity = i);
  }
  registerGroup(t) {
    const i = this.rootScene.getObjectByName(t);
    if (i && i instanceof ci)
      return i;
    const n = new ci();
    return n.name = t, this.rootScene.add(n), n;
  }
  setRaycaster(t, i, n) {
    const r = this.mainCamera, a = new Se(i, n);
    switch (this.viewMode) {
      case gu.FourView:
        break;
    }
    t.setFromCamera(a, r);
  }
  addView(t, i) {
    this._views.push(new L2e(t, i, this.mainCamera));
  }
  getSize() {
    return this._rendererSize.clone();
  }
  getPlottingScale() {
    const t = this.orthographicCamera.left, i = this.orthographicCamera.right;
    return Math.abs(t - i) / this.cameraControls.camera.zoom;
  }
  getRendererInfo() {
    const { threeRenderer: t } = this, { triangles: i } = t.info.render, { programs: n } = t.info, { textures: r } = t.info.memory;
    return {
      triangles: i,
      textures: r,
      programs: (n == null ? void 0 : n.length) ?? 0
    };
  }
  onPreRender(t) {
    return this._addCallbackNode(t, this._preRenderCallbacks);
  }
  onPostRender(t) {
    return this._addCallbackNode(t, this._postRenderCallbacks);
  }
  _addCallbackNode(t, i) {
    const n = {
      _previous: i,
      _next: i._next,
      callback: t,
      cancel: () => {
        n._previous && (n._previous._next = n._next), n._next && (n._next._previous = n._previous);
      }
    };
    return i._next && (i._next._previous = n), i._next = n, n;
  }
  shotCanvas() {
    return new Promise((t) => {
      this.render(0.1), this.threeRenderer.domElement.toBlob(t);
    });
  }
  dispose() {
    this.removeDomElement(), this.rootScene.remove(...this.rootScene.children), this.componentMap.forEach((t) => t.dispose()), this.renderQueue.dispose(), this.threeRenderer.dispose(), this.viewHelper.dispose(), this.isReady = !1;
  }
  updateColorAdjustmentPostprocessing(t) {
    this.componentMap.forEach((i) => {
      i.type === "ColorAdjustmentComponent" && i.setOptions(t);
    });
  }
  updateBloomEffectPostprocessing(t) {
    this.componentMap.forEach((i) => {
      i.type === "BloomEffectComponent" && i.setOptions(t);
    });
  }
  updateOutLineEffectPostprocessing(t) {
    const i = this.getComponentByType("Outline");
    if (i) {
      i.pass.edgeStrength = t.strength;
      const n = new pi(t.color.pure), r = dl(n);
      i.pass.visibleEdgeColor = r, i.pass.outLineAlpha = n.a, i.pass.enabled = t.enable;
    }
  }
};
S(n4, "ContainerId");
let qg = n4;
class tM {
  constructor(e) {
    S(this, "_action");
    S(this, "time", 0);
    S(this, "options", {
      name: "",
      loop: "repeat",
      timeScale: 1,
      id: "",
      play: !1,
      endReset: !0
    });
    this._action = e;
  }
  setActionOptions(e) {
    e.loop === "once" ? this._action.loop = PM : this._action.loop = RM, this._action.timeScale = e.timeScale, this._action.clampWhenFinished = !e.endReset;
  }
  play() {
    this._action.paused ? (this._action.time = this.time, this._action.paused = !1, this._action.timeScale = this.options.timeScale) : (this.reset(), this.setActionOptions(this.options)), this._action.play();
  }
  replay() {
    this.reset(), this.setActionOptions(this.options), this.time = 0, this._action.time = 0, this._action.play(), this._action.paused = !1;
  }
  reverse() {
    this._action.time !== 0 && (this.setActionOptions(this.options), this._action.timeScale = -this.options.timeScale, this._action.paused = !1, this._action.play());
  }
  stop() {
    this._action.stop();
  }
  reset() {
    this._action.reset();
  }
  pause() {
    this.time = this._action.time, this._action.paused = !0;
  }
}
class a8e extends Fi {
  constructor() {
    super(...arguments);
    S(this, "_animationMixer");
    S(this, "_actionProxies", []);
    S(this, "_handler");
    S(this, "_play", !0);
    S(this, "_url");
    S(this, "_initDataOptions");
  }
  get animationMixer() {
    return this._animationMixer;
  }
  execute(t, i) {
    switch (t) {
      case "playAnimation":
        this._playAnimation(i);
        break;
      case "stopAnimation":
        this._stopAnimation(i);
        break;
      case "pauseAnimation":
        this._pauseAnimation(i);
        break;
    }
  }
  easyvPlayAnimation(t) {
    const i = this._actionProxies.find((n) => n.options.id === t);
    i == null || i.play();
  }
  easyvPlayAnimationFromStart(t) {
    const i = this._actionProxies.find((n) => n.options.id === t);
    i == null || i.replay();
  }
  easyvStopAnimation(t) {
    const i = this._actionProxies.find((n) => n.options.id === t);
    i == null || i.stop();
  }
  easyvPauseAnimation(t) {
    const i = this._actionProxies.find((n) => n.options.id === t);
    i == null || i.pause();
  }
  _playAnimation(t) {
    const { id: i, options: n } = t, r = this._actionProxies.find((a) => a.options.id === i);
    r && (n.fromStart ? r.replay() : r.play());
  }
  _stopAnimation(t) {
    const i = this._actionProxies.find((n) => n.options.id === t.id);
    i == null || i.stop();
  }
  _pauseAnimation(t) {
    const i = this._actionProxies.find((n) => n.options.id === t.id);
    i == null || i.pause();
  }
  /**
   *
   * @param clips
   * @param root
   */
  setAnimationClips(t, i) {
    this._animationMixer = new rS(i), this._actionProxies = [], t.forEach((n) => {
      const r = this._animationMixer.clipAction(n.clone()), a = new tM(r);
      a.options.id = n.name, a.options.name = n.name, a.options.loop = "repeat", a.options.timeScale = 1, a.time = 0, this._actionProxies.push(a);
    });
  }
  setAnimationMixer(t) {
    this._animationMixer = t;
  }
  setActions(t) {
    this._actionProxies = [], t.forEach((i) => {
      const n = i.getClip(), r = new tM(i);
      r.options.id = n.name, r.options.name = n.name, r.options.loop = "repeat", r.options.timeScale = 1, r.time = 0, this._actionProxies.push(r);
    });
  }
  updateActionOptions(t) {
    this._actionProxies.forEach((i, n) => {
      t[n] && (i.options = { ...t[n] }, t[n].play ? i.play() : i.stop());
    });
  }
  setAnimationFileUrl(t) {
    this._url = t;
  }
  /**
   * 开启动画循环
   */
  startAnimationLoop() {
    const t = new Ju();
    this._play = !0;
    const i = () => {
      this._play && (this._animationMixer.update(t.getDelta()), this._handler = requestAnimationFrame(i));
    };
    i();
  }
  /**
   * 停止动画循环
   */
  stopAnimationLoop() {
    this._play = !1, this._handler && cancelAnimationFrame(this._handler);
  }
  /**
   * 更新动画片段属性
   * @param index
   * @param options
   */
  updateAnimationActionOptions(t, i) {
    const n = this._actionProxies[t];
    n && (n.reset(), n.options.loop = i.loop, n.options.timeScale = i.timeScale, n.options.play = i.play, n.options.endReset = i.endReset, i.play ? n.play() : n.stop(), this.needDataUpdateServer = !0), dt.Entity.refresh();
  }
  fromJson(t, i) {
    const { states: n, data: r } = t, a = n;
    a && this.states.initStates(a);
    const o = r, { asset: l, animation: c } = o;
    c.actions.forEach((u) => {
      u.endReset = u.endReset ?? !0;
    }), this._initDataOptions = { ...o }, this.setAnimationFileUrl(l.url);
    const h = this.entity.getComponentByType("Model");
    h && (h._mixer && h._actions && (this.setAnimationMixer(h._mixer), this.setActions(h._actions), this.updateActionOptions(c.actions)), this.isInit = !0, i == null || i());
  }
  toJson() {
    const t = this.states.toJson(), i = this._actionProxies.map((a) => a.options), n = {
      asset: {
        url: this._url ?? ""
      },
      animation: {
        actions: i
      }
    };
    return {
      id: this.id,
      type: this.type,
      data: n,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: !1,
      inActive: !1,
      states: t
    };
  }
  dispose() {
    this._handler && cancelAnimationFrame(this._handler), this._actionProxies = [];
  }
}
var c8e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const b6 = new zt();
new _();
const h8e = "CustomAnimation";
let Yx = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "tweenAnimation", new XS());
    S(this, "explodeTracksMap", /* @__PURE__ */ new Map());
    S(this, "mixer", new rm());
    S(this, "actionName", "custom-action");
    // 重新计算轨迹
    S(this, "_reComputeTrackFlag", !1);
    S(this, "_playId");
    S(this, "clipPlanes", [
      // yz
      new ln(new _(1, 0, 0), 0),
      new ln(new _(-1, 0, 0), 0),
      // xz
      new ln(new _(0, 1, 0), 0),
      new ln(new _(0, -1, 0), 0),
      // xy
      new ln(new _(0, 0, 1), 0),
      new ln(new _(0, 0, -1), 0)
    ]);
  }
  async start(e, t = this.states.curStateId, i = 0) {
    return console.log(this), new Promise((n) => {
      if (this.mixer.isPlaying)
        n(this);
      else if (this.mixer.isPause)
        this.mixer.play(), n(this);
      else {
        if (i === 0) {
          this.restore(), this.mixer.removeAll(), this._playId = e;
          const r = t && this.states.getStateConfigById(t), a = this._getObject();
          if (r) {
            if (e) {
              const o = r.children.find((l) => l.id === e);
              o && ("times" in o ? this._handleTransformAnimation(a, o) : this._handleExplodeAnimation(a, o));
            } else
              r.children.forEach((o) => {
                "times" in o ? this._handleTransformAnimation(
                  a,
                  this._handleTransformTrackTween(o)
                ) : this._handleExplodeAnimation(a, o);
              });
            this.mixer.setRepeat(r.repeat), this.mixer.setTime(i), this.mixer.play();
          }
        } else
          this.mixer.setTime(i), this.mixer.play();
        n(this);
      }
    });
  }
  preview(e, t) {
    this.mixer.preview(e, void 0, t);
  }
  // 对 不是从0开始的动画进行补帧
  _handleTransformTrackTween(e) {
    let t = Number.MAX_SAFE_INTEGER, i = e.values[0].value;
    e.values.forEach((r, a) => {
      if (a > 0) {
        const o = e.times[a - 1].start;
        o < t && (t = o, i = e.values[a - 1].value);
      }
    });
    const n = JSON.parse(JSON.stringify(e));
    return t !== 0 && (n.times.unshift({
      start: 0,
      end: t,
      mode: "Linear.None"
    }), n.values.unshift({
      value: i,
      name: "tween"
    })), n;
  }
  _handleExplodeAnimation(e, t) {
    const i = this._computeExplodeTrack(e, t), n = this._addAction();
    i.forEach(({ start: r, end: a, object: o }) => {
      this._addTransformTrack(
        n,
        "position",
        o,
        {
          start: 0,
          end: t.duration,
          mode: t.mode
        },
        r,
        a
      );
    });
  }
  _handleTransformAnimation(e, t) {
    const i = this._addAction(), n = this._getTrackName(t);
    console.log("sssss0", t), t.values.forEach(({ value: r }, a) => {
      a > 0 && this._addTransformTrack(
        i,
        n,
        e,
        t.times[a - 1],
        t.values[a - 1].value,
        r
      );
    });
  }
  _computeExplodeTrack(e, t) {
    const i = this.getExplodeTree(e), { axialFactorArr: n, center: r, decay: a = 0, force: o = 2, id: l } = t, c = [];
    b6.expandByObject(e);
    const h = r ? new _(r.x, r.y, r.z) : b6.getCenter(new _());
    h.add(new _().setFromMatrixPosition(e.matrixWorld));
    const u = (d) => {
      let f = new _(), p = 0;
      return d.traverse((v) => {
        if (v instanceof Re) {
          const g = new _();
          v.geometry.computeBoundingBox(), v.geometry.boundingBox.getCenter(g), v.localToWorld(g), f.add(g), p++;
        }
      }), f.divideScalar(p).add(d.position);
    };
    return n.forEach(({ id: d, value: f }) => {
      const { object: p } = i.find((v) => v.id === d) ?? {};
      if (p) {
        const v = u(p), g = h.clone().sub(v).normalize(), m = h.distanceTo(v), x = o * (Math.pow(m - a, 2) / m);
        let y = p.position.clone().addScaledVector(
          g.multiply(new _(f.x, f.y, f.z)).multiplyScalar(0.1),
          isNaN(x) ? 0 : x
        );
        m === 0 && (y = p.position.clone()), c.push({
          object: p,
          start: p.position.clone(),
          end: y
        });
      }
    }), this.explodeTracksMap.set(l, c), this._reComputeTrackFlag = !1, console.log("this.explodeTracksMap", this.explodeTracksMap), c;
  }
  _convertIVector3ToVector3(e) {
    return new _(e.x, e.y, e.z);
  }
  _addTransformTrack(e, t, i, n, r, a) {
    e.addTrack({
      name: t,
      mode: n.mode,
      propertyKey: t,
      roots: [i],
      times: [n.start, n.end],
      values: [this._convertIVector3ToVector3(r), this._convertIVector3ToVector3(a)]
    });
  }
  _getTrackName(e) {
    return "times" in e && e.type === "rotate" ? "rotation" : "times" in e && e.type === "scale" ? "scale" : "position";
  }
  _addAction() {
    return this.mixer.addAction(this.actionName);
  }
  _getObject() {
    let e = new mt();
    const t = this.entity.getComponentByType("Model");
    t && (e = t.group);
    const i = this.entity.getComponentByType("EngineGroup");
    return i && (e = i.entity), e;
  }
  _getObjectConfig() {
    new mt();
    const e = this.entity.getComponentByType("Model");
    e && e.group;
    const t = this.entity.getComponentByType("EngineGroup");
    t && t.entity;
    let i;
    return e && (i = e.states.getCurrentStateConfig()), t && (i = t.states.getCurrentStateConfig()), i;
  }
  tick() {
    this.mixer.tick();
  }
  getExplodeTree(e) {
    const t = [];
    return e.traverse((i) => {
      t.push({
        id: i.uuid,
        layer: this.getObjectLayer(i, e),
        object: i
      });
    }), t;
  }
  getObjectLayer(e, t, i = 0) {
    return e.parent === t ? i : e.parent ? this.getObjectLayer(e.parent, t, ++i) : i;
  }
  getCenter(e) {
    return b6.makeEmpty().expandByObject(e).getCenter(new _());
  }
  update() {
    this._reComputeTrackFlag = !0;
  }
  execute(e, t) {
    const { id: i } = t;
    switch (this.setCurrentStateById(i), e) {
      case "playAnimation":
        this.start();
        break;
      case "stopAnimation":
        this.stop();
        break;
      case "pauseAnimation":
        this.pause();
        break;
    }
  }
  easyvPlay(e) {
    this.setCurrentStateById(e), this.start();
  }
  easyvPause() {
    this.pause();
  }
  easyvStop() {
    const { endReset: e } = this.states.getCurrentStateConfig();
    this.mixer.stop(e ?? !0);
  }
  easyvReverse(e) {
    this.setCurrentStateById(e), this.mixer.reset();
  }
  pause() {
    return this.mixer.pause(), this;
  }
  stop() {
    const e = this.states.getCurrentStateConfig();
    return this.mixer.stop(!Tt.enabled && e.endReset), this.dispatchEvent("stop", { id: this._playId }), this._playId = void 0, Tt.enabled && this.restore(), this;
  }
  restore() {
    const e = this._getObjectConfig(), t = this._getObject();
    if (e) {
      const { transform: i } = e, { position: n, rotation: r, scale: a } = i;
      t.position.set(n.x, n.y, n.z), t.scale.set(a.x, a.y, a.z), t.rotation.set(r.x, r.y, r.z);
    }
    this.explodeTracksMap.forEach((i) => {
      i.forEach(({ object: n, start: r }) => {
        n.position.copy(r);
      });
    }), this.explodeTracksMap.clear();
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && (a.forEach((o) => {
      o.config.children.forEach((l) => {
        (!l.name || l.name === "") && ("type" in l && l.type === "rotate" && (l.name = "旋转"), "type" in l && l.type === "translate" && (l.name = "平移"), "type" in l && l.type === "scale" && (l.name = "缩放"), "type" in l && l.type === "explode" && (l.name = "爆炸"));
      });
    }), this.states.initStates(a)), this.addEventListener("stop", () => {
      const o = this.states.stateList.get(this.getCurStateId());
      o && e.emitFunc && e.emitFunc(
        "customAnimationEnd",
        {
          entityId: this.entity.uuid,
          componentId: this.id,
          animationName: o.name
        },
        this.entity.uuid
      );
    }), this.isInit = !0;
  }
  dispose() {
    this.explodeTracksMap.clear();
  }
};
Yx = c8e([
  ni(h8e)
], Yx);
var f8e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Vh = new _(), sf = new _(), Jm = new zt(), p8e = "external-facade", m8e = "floor", fD = "select-floor", tc = "action-camera", pD = "restore", tu = "BuildingsAnimation";
let iM = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "mixer", new rm());
    S(this, "_lastSelectIndex", -1);
    S(this, "_isSpread", !1);
    // public tweenAnimation: TweenAnimation = new TweenAnimation();
    /**
       *  状态ID : {
            外立面: 外立面的指定mesh 数组
            楼层名: 楼层指定的mesh 数组
       *  }
    
       */
    S(this, "externalFacadeTracks", []);
    // 楼层展开动画轨迹
    S(this, "floorSpreadTracks", []);
    // 楼层展开动画轨迹
    S(this, "floorSelectTracks", []);
    S(this, "floorObjects", []);
    S(this, "originPositionMap", /* @__PURE__ */ new Map());
    // 重新计算轨迹
    S(this, "_reComputeSpreadTrackFlag", !0);
    S(this, "_rayCaster", new lr());
    // private _handleClickBindThis = this._handleClick.bind(this);
    S(this, "_world");
    S(this, "_isPlayRestoreAnimation", !1);
    S(this, "handleMixerRestoreStop", () => {
      this._lastSelectIndex = -1, this._isSpread = !1;
    });
    // 这里楼层传递的是 1 起始
    S(this, "easyvPlaySelectFloorAnimation", (e) => {
      if (e) {
        const t = e - 1, { select: i, floor: n } = this.states.getCurrentStateConfig(), { selectFloor: r, otherOverFloor: a, otherBelowFloor: o, camera: l } = i;
        r && a && o && l && (this.floorObjects[t] === void 0 && (this.floorObjects = this._getFloorObjects(n)), this._playSelectAnimation(
          this.floorObjects[t].items.map((c) => c.obj),
          l,
          0
        ));
      }
    });
    S(this, "easyvRestore", () => {
      this.stop();
    });
    S(this, "easyvPlaySpreadAnimation", () => {
      this.start();
    });
    this.id = e, this.name = t, this.entity = i, Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s(
      tu,
      "selectFloor",
      e,
      this.easyvPlaySelectFloorAnimation
    ), _s(
      tu,
      "resetFloor",
      e,
      this.easyvRestore
    ), _s(
      tu,
      "openFloor",
      e,
      this.easyvPlaySpreadAnimation
    );
  }
  _removeEasyvEventListener() {
    const e = this.entity.uuid;
    Zf(tu, "selectFloor", e), Zf(tu, "resetFloor", e), Zf(tu, "openFloor", e);
  }
  _getCameraTween(e, t) {
    return {
      px: e.x,
      py: e.y,
      pz: e.z,
      tx: t.x,
      ty: t.y,
      tz: t.z
    };
  }
  _getCameraControls() {
    var e;
    return (e = this._world) == null ? void 0 : e.renderer.cameraControls;
  }
  _updateCamera(e, t) {
    var i, n;
    (i = this._getCameraControls()) == null || i.setPosition(e.x, e.y, e.z), (n = this._getCameraControls()) == null || n.setTarget(t.x, t.y, t.z);
  }
  // private _initDomEvent(element: HTMLElement): void {
  //   element.removeEventListener('click', this._handleClickBindThis);
  //   element.addEventListener('click', this._handleClickBindThis);
  // }
  // private _handleClick(ev: MouseEvent): void {
  //   if (this._world && !Api.enabled) {
  //     const coords = projectMousePositionToCanvas(ev.clientX, ev.clientY, ev.target as HTMLElement);
  //     this._rayCaster.setFromCamera(coords, this._world.renderer.mainCamera);
  //     const config = this.states.getCurrentStateConfig();
  //     if (this.floorObjects.length === 0) this.floorObjects = this._getFloorObjects(config.floor);
  //     const { floor, select } = config;
  //     if (floor && select) {
  //       const objects = this._raycasterFloor(floor, ev);
  //       this._playSelectAnimation(objects, select.camera);
  //     }
  //   }
  // }
  _playSelectAnimation(e, t, i) {
    const [n] = e;
    if (n) {
      const r = this._getFloorIndex(n);
      this.floorSelectTracks = this._computeSelectTrack(this.floorObjects, r);
      const a = `${fD}_${r}`, o = this.mixer.addAction(a, !0);
      console.log("this.mixer", this.floorSelectTracks), this.floorSelectTracks.forEach(({ tracks: l, duration: c, name: h }) => {
        l.forEach(({ object: u, start: d, end: f }) => {
          const p = [u], v = Lh(
            u,
            Ya
          );
          v && p.push(v), o.addTrack({
            name: h,
            mode: "Linear.None",
            propertyKey: "position",
            roots: p,
            times: [0, c],
            values: [d, f]
          });
        });
      }), this._focusSelectCamera(r, t.distance, t.duration), this.mixer.setTime(i), this.mixer.play(a), this._lastSelectIndex = r, r !== -1 && this._world && this._world.emitFunc && this._world.emitFunc("buildingsAnimationSelect", r + 1, this.entity.uuid);
    }
  }
  _focusSelectCamera(e, t, i) {
    var a;
    Jm.makeEmpty();
    const n = this.floorSelectTracks[e], r = new zt();
    if (n && n.tracks.length > 0) {
      const { tracks: o } = n;
      o.forEach(({ object: l, end: c, start: h }) => {
        r.makeEmpty(), r.expandByObject(l), r.translate(new _().subVectors(c, h)), Jm.union(r);
      }), Jm.expandByScalar(t);
    } else
      (a = this.floorObjects[e]) == null || a.items.forEach(({ obj: o }) => {
        r.makeEmpty(), r.expandByObject(o), Jm.union(r);
      });
    if (this._world) {
      const [o, l] = this._world.getCameraFitAnchor(Jm), c = this._world.renderer.cameraControls, h = c.mouseButtons.wheel === $e.DOLLY, u = this.mixer.addAction(tc, !0);
      u.addTrack({
        name: tc,
        propertyKey: h ? "dollyTo" : "zoomTo",
        roots: [c],
        mode: "Linear.None",
        times: [0, i],
        values: [o.r, l.r]
      }), u.addTrack({
        name: tc,
        propertyKey: "moveTo",
        roots: [c],
        mode: "Linear.None",
        times: [0, i],
        values: [
          new _(o.x, o.y, o.z),
          new _(l.x, l.y, l.z)
        ]
      }), this.mixer.play(tc);
    }
  }
  // private async _playTrackTweenAnimation(floorTrack: FloorTrackType): Promise<void> {
  //   const { duration, tracks } = floorTrack;
  //   return new Promise((resolve) => {
  //     const tweenHandler = EasyTwinTween.createTweenHandler<NumberState>({
  //       source: { value: 0 },
  //       target: { value: 1 },
  //       duration: duration * 1000,
  //       execute: ({ value }) => {
  //         tracks.forEach(({ object, start, end }) => {
  //           object.position.lerpVectors(start, end, value);
  //         });
  //       },
  //       complete: () => {
  //         Tween.remove(tweenHandler);
  //         resolve();
  //       },
  //     });
  //   });
  // }
  _computeSelectTrack(e, t) {
    const { select: i, spread: n } = this.states.getCurrentStateConfig(), { selectFloor: r, otherOverFloor: a, otherBelowFloor: o } = i, l = [];
    return e.forEach((c, h) => {
      const { name: u, items: d, minY: f } = c, p = [];
      let v = 0;
      const g = new _(
        0,
        this._isSpread ? n.floor.offset.y * h - f : 0,
        0
      );
      d.forEach(({ obj: m, position: x }) => {
        const y = x.clone();
        this.floorSpreadTracks.length > 0 && y.add(g);
        const A = {
          start: m.position.clone(),
          // start: position,
          end: new _(),
          object: m
        };
        h > t ? (A.end.set(a.offset.x, a.offset.y, a.offset.z).add(y), v = a.duration) : h < t && (A.end.set(o.offset.x, o.offset.y, o.offset.z).add(y), v = o.duration), h === t && (A.start.copy(x), A.end.set(r.offset.x, r.offset.y, r.offset.z).add(y), A.end.equals(x) && A.start.copy(A.object.position), v = r.duration), !A.start.equals(A.end) && p.push(A);
      }), l.push({ name: u, tracks: p, duration: v, index: h });
    }), l;
  }
  // 返回自己的楼层所在索引
  _getFloorIndex(e) {
    return this.floorObjects.findIndex(
      ({ items: t }) => t.findIndex(({ obj: i }) => i.uuid === (e == null ? void 0 : e.uuid)) !== -1
    );
  }
  _getFloorObjects(e) {
    const t = [];
    return this._world && e.forEach(({ name: i, items: n }) => {
      const r = /* @__PURE__ */ new Set();
      n.forEach(({ meshId: o, entityId: l }) => {
        this._getComponents(l).forEach((c) => {
          if (o) {
            if (c instanceof Rc) {
              const h = c.group.getObjectByProperty("uuid", o);
              h && r.add(h);
            }
          } else
            r.add(c.entity);
        });
      });
      const a = Array.from(r);
      t.push({
        name: i,
        items: a.map((o) => ({
          position: o.position.clone(),
          obj: o
        })),
        minY: this._getFloorObjectsMinY(a)
      });
    }), this._setOriginPositionMap(t), t;
  }
  _setOriginPositionMap(e, t = !1) {
    e.forEach(({ items: i }) => {
      i.forEach(({ obj: n }) => {
        this._setOriginPosition(n, t);
      });
    });
  }
  _setOriginPosition(e, t = !1) {
    (this.originPositionMap.get(e.uuid) === void 0 || t) && this.originPositionMap.set(e.uuid, e.position.clone());
  }
  _raycasterFloor(e, t) {
    this.floorObjects.length === 0 && (this.floorObjects = this._getFloorObjects(e));
    const i = [];
    if (this._world) {
      const n = [], r = $r(t.clientX, t.clientY, t.target);
      this._rayCaster.setFromCamera(r, this._world.renderer.mainCamera), this.floorObjects.forEach(({ items: o }, l) => {
        o.forEach(({ obj: c }) => {
          let h;
          c instanceof ed ? c.componentMap.forEach((u) => {
            [h] = u.intersectObject(this._rayCaster, t);
          }) : [h] = this._rayCaster.intersectObject(c), h && n.push(Object.assign(h, { floorIndex: l }));
        });
      }), n.sort((o, l) => o.distance - l.distance);
      const [a] = n;
      if (a && a.floorIndex !== -1) {
        const o = this.floorObjects[a.floorIndex];
        o && i.push(...o.items.map((l) => l.obj));
      }
    }
    return i;
  }
  _getComponents(e, t) {
    if (this._world) {
      const i = this._world.entityManager.getEntity(e);
      if (t) {
        const n = i == null ? void 0 : i.getComponentByType("Model");
        if (n)
          return [n];
      } else
        return Array.from((i == null ? void 0 : i.componentMap.values()) ?? []);
    }
    return [];
  }
  _getObject(e, t, i) {
    const n = e.entityManager.getEntity(t);
    return n && (i ? n.getObjectByProperty("uuid", i) : n);
  }
  _entityTracksExecute(e, t) {
    t.forEach(({ object: i, start: n, end: r }) => {
      i.position.lerpVectors(n, r, e);
      const a = Lh(i, Ya);
      a && a.position.copy(i.position);
    });
  }
  _playCameraTweenAnimation(e, t, i, n, r = 1, a) {
    this.mixer.removeByName(tc);
    const o = this.mixer.addAction(tc, !0), l = this._getCameraControls();
    l && (o.addTrack({
      name: tc,
      propertyKey: "setPosition",
      roots: [l],
      times: [0, r],
      mode: "Linear.None",
      values: [e, t]
    }), o.addTrack({
      name: tc,
      propertyKey: "setTarget",
      roots: [l],
      times: [0, r],
      mode: "Linear.None",
      values: [i, n]
    }), this.mixer.play());
  }
  // private async _restoreAnimation(): Promise<void> {
  //   const config = this.states.getCurrentStateConfig();
  //   const restoreTweenFunc = async (tracks: EntityTracksType[], duration: number) => {
  //     return new Promise<void>((resolve) => {
  //       const curPositionMap = new Map();
  //       tracks.forEach(({ object }) => {
  //         curPositionMap.set(object.uuid, object.position.clone());
  //       });
  //       const tween = this.tweenAnimation.addTweenAnimation({
  //         id: RestoreTweenName,
  //         duration: duration * 1000,
  //         execute: ({ value }) => {
  //           tracks.forEach(({ object, end }) => {
  //             const originPos = this.originPositionMap.get(object.uuid);
  //             const curPos = curPositionMap.get(object.uuid);
  //             if (curPos && originPos) {
  //               object.position.lerpVectors(curPos, originPos, value);
  //               const wireframeMesh = getObject3dUserDataProperty<THREE.Object3D>(
  //                 object,
  //                 WireframeMeshName,
  //               );
  //               wireframeMesh && wireframeMesh.position.copy(object.position);
  //             }
  //           });
  //         },
  //         complete: () => {
  //           resolve();
  //         },
  //       });
  //       tween.start();
  //     });
  //   };
  //   return new Promise((resolve) => {
  //     const results: Promise<void>[] = [];
  //     this.floorSelectTracks.forEach(({ tracks, duration }) => {
  //       results.push(restoreTweenFunc(tracks, duration));
  //     });
  //     results.push(restoreTweenFunc(this.floorSpreadTracks, config.spread.floor.duration));
  //     results.push(
  //       restoreTweenFunc(this.externalFacadeTracks, config.spread.externalFacade.duration),
  //     );
  //     Promise.all(results).then(() => resolve());
  //     // if (this.floorSelectTracks.length === 0) resolve();
  //   });
  // }
  _getFloorObjectsMinY(e) {
    return e[0] ? e.reduce((t, i) => i.position.y < t.position.y ? i : t, e[0]).position.y : 0;
  }
  _addRestoreTracks() {
    this.mixer.removeAll();
    const e = this.mixer.addAction(pD), { spread: t } = this.states.getCurrentStateConfig();
    this.floorObjects.forEach(({ name: i, items: n }) => {
      n.forEach(({ position: r, obj: a }) => {
        const o = [a], l = Lh(a, Ya);
        l && o.push(l), e.addTrack({
          name: i,
          propertyKey: "position",
          roots: o,
          times: [0, t.floor.duration],
          values: [a.position.clone(), r],
          mode: "Linear.None"
        });
      });
    }), this.externalFacadeTracks.forEach(({ object: i, start: n }) => {
      const r = [i], a = Lh(i, Ya);
      a && r.push(a);
      const o = this.originPositionMap.get(i.uuid);
      e.addTrack({
        name: "position",
        propertyKey: "position",
        roots: r,
        times: [0, t.externalFacade.duration],
        values: [i.position.clone(), o ?? n],
        mode: "Linear.None"
      });
    }), console.log(this.externalFacadeTracks);
  }
  _addExternalFacadeAnimation(e, t) {
    this.externalFacadeTracks = this.computeExternalFacadeTracks(
      e,
      t.externalFacade.offset
    );
    const i = this.mixer.addAction(p8e, !0);
    this.externalFacadeTracks.forEach(({ object: n, start: r, end: a }) => {
      const o = [n], l = Lh(n, Ya);
      l && o.push(l), i.addTrack({
        name: "position",
        propertyKey: "position",
        roots: o,
        times: [0, t.externalFacade.duration],
        values: [r, a],
        mode: "Linear.None"
      });
    });
  }
  _addFloorSpreadAnimation(e) {
    this.floorSpreadTracks = this.computeFloorSpreadTracks(e.floor.offset);
    const t = this.mixer.addAction(m8e, !0);
    this.floorSpreadTracks.forEach(({ object: i, start: n, end: r }) => {
      const a = [i], o = Lh(i, Ya);
      o && a.push(o), t.addTrack({
        name: "position",
        propertyKey: "position",
        roots: a,
        times: [e.floor.delay, e.floor.delay + e.floor.duration],
        mode: "Linear.None",
        values: [n, r]
      });
    });
  }
  async updateFromJson(e, t) {
    if (t) {
      const { states: i } = t, n = i;
      n && this.states.initStates(n);
    }
    console.log("Building", this), this._world = e, this.isInit = !0;
  }
  update() {
    this.stop(!0), this.mixer.removeAll(), this._reComputeSpreadTrackFlag = !0;
    const e = this.states.getCurrentStateConfig();
    this.floorObjects = this._getFloorObjects(e.floor), this._setOriginPositionMap(this.floorObjects, !0), this.getExternalFacadeObjects(e.externalFacade).forEach(
      (t) => this._setOriginPosition(t, !0)
    );
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  // 开始动画
  start(e = 0) {
    this.mixer.removeEventListener("stop", this.handleMixerRestoreStop);
    const { spread: t, externalFacade: i, floor: n } = this.states.getCurrentStateConfig();
    this.floorObjects.length === 0 && (this.floorObjects = this._getFloorObjects(n)), this._addFloorSpreadAnimation(t), this._addExternalFacadeAnimation(i, t), this.focusSpreadCamera(), this.mixer.setTime(e), this.mixer.play(), this._isSpread = !0, this._world && this._world.emitFunc && this._world.emitFunc("buildingsAnimationSpread", void 0, this.entity.uuid);
  }
  preview(e, t) {
    this.mixer.preview(e, void 0, t), e > 0 && (this._isSpread = !0);
  }
  // 预览选中动画, 默认 1楼
  previewSelectAnimation(e = 1, t = 0, i) {
    this.mixer.preview(t, `${fD}_${e - 1}`, i);
  }
  startSelectAnimation(e = 1, t = 0) {
    const { select: i, floor: n } = this.states.getCurrentStateConfig(), { camera: r } = i;
    this.floorObjects[e - 1] === void 0 && (this.floorObjects = this._getFloorObjects(n)), console.log(this), this.floorObjects[e - 1] && this._playSelectAnimation(
      this.floorObjects[e - 1].items.map((a) => a.obj),
      r,
      t
    ), this.dispatchEvent("preview-select-floor", { floorIndex: e, time: t });
  }
  // 暂停动画
  pause() {
    this.mixer.pause();
  }
  tick() {
    this.mixer.tick();
  }
  stopImmediately() {
    this.mixer.stop(), this.floorObjects.forEach(({ name: e, items: t }) => {
      t.forEach(({ position: i, obj: n }) => {
        const r = [n], a = Lh(n, Ya);
        a && r.push(a), r.forEach((o) => o.position.copy(i));
      });
    }), this._lastSelectIndex = -1, this._isSpread = !1;
  }
  // 结束动画
  stop(e = !1) {
    e ? this.stopImmediately() : (this._addRestoreTracks(), this.mixer.play(pD), this.mixer.addEventListener("stop", this.handleMixerRestoreStop)), this._world && this._world.emitFunc && this._world.emitFunc("buildingsAnimationRestore", void 0, this.entity.uuid);
  }
  disposeTweenAnimation() {
    this.mixer.removeAll(), this.floorSpreadTracks.length = 0, this.externalFacadeTracks.length = 0, this.floorSpreadTracks.length = 0;
  }
  computeFloorSpreadTracks(e) {
    const t = [];
    return this._world && this.floorObjects.forEach((i, n) => {
      const r = this._getFloorObjectsMinY(i.items.map((o) => o.obj)), a = new _(0, e.y * n - r, 0);
      i.items.forEach(({ obj: o, position: l }) => {
        t.push({
          object: o,
          start: l.clone(),
          end: l.clone().add(a)
        });
      });
    }), t;
  }
  getExternalFacadeObjects(e) {
    const t = [];
    return this._world && e.forEach(({ entityId: i, meshId: n }) => {
      const r = this._getObject(this._world, i, n);
      r && t.push(r);
    }), t;
  }
  computeExternalFacadeTracks(e, t) {
    const i = [];
    return this._world && this.getExternalFacadeObjects(e).forEach((r) => {
      this.originPositionMap.get(r.uuid) === void 0 && this.originPositionMap.set(r.uuid, r.position.clone()), i.push({
        object: r,
        start: r.position.clone(),
        end: r.position.clone().add(Vh.set(t.x, t.y, t.z))
      });
    }), i;
  }
  // 更新展开楼层是的相机参数, 自动获取当前的相机的参数
  updateSpreadCamera() {
    var i, n;
    const e = this.states.getCurrentStateConfig();
    this._world && ((i = this._getCameraControls()) == null || i.getPosition(Vh), (n = this._getCameraControls()) == null || n.getTarget(sf), e.spread.camera.position = {
      x: Vh.x,
      y: Vh.y,
      z: Vh.z
    }, e.spread.camera.target = {
      x: sf.x,
      y: sf.y,
      z: sf.z
    });
    const t = this.states.stateList.get(this.getCurStateId());
    t && Qe.ApiServer.updateState(this.entity.uuid, this.id, t), dt.Entity.refresh();
  }
  // 聚焦
  focusSpreadCamera(e) {
    const t = this.states.getCurrentStateConfig();
    if (t.spread) {
      const { camera: i } = t.spread, n = this._getCameraControls();
      n && (this._getCameraTween(
        n.getPosition(Vh),
        n.getTarget(sf)
      ), this._playCameraTweenAnimation(
        Vh.clone(),
        new _(i.position.x, i.position.y, i.position.z),
        sf.clone(),
        new _(i.target.x, i.target.y, i.target.z),
        i.duration,
        e
      ));
    }
  }
  dispose() {
    this._world = void 0, this.floorSpreadTracks.length = 0, this.floorObjects.length = 0, this.externalFacadeTracks.length = 0, Qe.mode === "Easyv" && this._removeEasyvEventListener();
  }
};
iM = f8e([
  ni(tu, { version: "2.0.0" })
], iM);
class rm extends es {
  constructor() {
    super();
    S(this, "_actions", []);
    S(this, "_isPause", !1);
    S(this, "_isPlaying", !1);
    S(this, "_lastTime", 0);
    S(this, "_unit", "time");
  }
  get actions() {
    return this._actions;
  }
  get isPause() {
    return this._isPause;
  }
  set isPause(t) {
    this._isPause = t;
  }
  get isPlaying() {
    return this._isPlaying;
  }
  set isPlaying(t) {
    this._isPlaying = t;
  }
  get unit() {
    return this._unit;
  }
  play(t) {
    this.actions.filter((n) => n.name === t || t === void 0).forEach((n) => n.play()), this._isPlaying = !0, this._isPause = !1, this._lastTime = this._getNow(), this.dispatchEvent("start", void 0), this.updatePlayingState();
    const i = () => {
      let n = !1;
      this.actions.forEach((r) => {
        n || (n = r.enabled);
      }), n || this.dispatchEvent("stop", void 0);
    };
    this.actions.forEach((n) => {
      n.once("stop", i);
    });
  }
  updatePlayingState() {
    this.actions.forEach(() => {
      this.once("stop", () => {
        let t = !1;
        this.actions.forEach((i) => {
          t = t || i.enabled;
        }), this.isPlaying = t;
      });
    });
  }
  getCurTime() {
  }
  setTimeUnit(t) {
    this._unit = t;
  }
  stop(t = !0) {
    this.actions.forEach((i) => i.stop(t)), this._isPause = !1, this._isPlaying = !1, this.dispatchEvent("stop", void 0);
  }
  pause() {
    this._isPause = !0, this.isPlaying = !1, this.actions.forEach((t) => t.pause()), this.dispatchEvent("pause", void 0);
  }
  // 复位
  reset(t) {
    this._isPause = !1, this._lastTime = this._getNow(), this.actions.filter((n) => n.name === t || t === void 0).forEach((n) => n.reset());
    let i = !1;
    this.actions.filter((n) => n.name === t || t === void 0).forEach((n) => {
      n.enabled && (i = !0);
    }), i && (this.dispatchEvent("reset", void 0), this.isPlaying = !0, this.updatePlayingState());
  }
  // 从头播放
  replay() {
    this._isPause = !1, this._lastTime = this._getNow(), this.actions.forEach((t) => t.replay());
  }
  preview(t, i, n = "time") {
    this._lastTime = this._getNow();
    let r = t;
    n === "percent" && (r = this.getDuration() * t), this.pause(), this.actions.filter((a) => a.name === i || i === void 0).forEach((a) => a.preview(r)), this.dispatchEvent("preview", { time: t, unit: this.unit });
  }
  addAction(t, i = !1) {
    let n = this.getActionByName(t);
    if (i)
      this.removeByName(t);
    else if (n)
      return n;
    return n = new v8e(this, t), this.actions.push(n), n;
  }
  setRepeat(t) {
    this.actions.forEach((i) => i.loopCount = t);
  }
  getActionByName(t) {
    return this.actions.find((i) => i.name === t);
  }
  getDuration() {
    let t = 0;
    return this.actions.forEach((i) => {
      t = Math.max(i.getTracksDuration(i.tracks), t);
    }), t;
  }
  removeAll() {
    this.actions.length = 0;
  }
  removeByName(t) {
    this._actions = this.actions.filter((i) => i.name !== t);
  }
  tick() {
    this.isPlaying && this.animate();
  }
  setTime(t) {
    let i = t;
    this.unit === "percent" && (i = this.getDuration() * t), this._lastTime = this._getNow() + t, this.actions.forEach((n) => n.setTime(i));
  }
  getTime() {
    let t = 0;
    return this.actions.forEach((i) => {
      i.time <= i.getTracksDuration(i.tracks) && i.time >= 0 && (t = i.time);
    }), t;
  }
  _getNow() {
    return Number((performance.now() / 1e3).toFixed(4));
  }
  animate() {
    this.renderFrame(this._getNow() - this._lastTime), this._lastTime = this._getNow();
  }
  renderFrame(t) {
    this.actions.forEach(
      (i) => i.enabled && i.update(this._lastTime + t, t, Math.sign(t))
    );
  }
}
class g8e {
  constructor(e) {
    S(this, "id", nt.generateUUID());
    S(this, "valueType", "number");
    S(this, "interpolant");
    S(this, "duration", 0);
    S(this, "name");
    S(this, "roots");
    S(this, "times");
    S(this, "values", new Float32Array());
    S(this, "propertyKey");
    S(this, "defaultExecute");
    const { name: t, roots: i, times: n, values: r, propertyKey: a, mode: o, defaultExecute: l } = e;
    this.name = t, this.roots = i, this.times = n, this.propertyKey = a, this.defaultExecute = l, r[0] instanceof _ ? (this.values = Q6.convertArray(
      r.flatMap((h) => [h.x, h.y, h.z]),
      Float32Array,
      !0
    ), this.valueType = "position") : typeof r[0] == "number" && (this.values = Q6.convertArray(r, Float32Array, !0));
    let c = sg;
    switch (o) {
      case "Linear.None":
        c = sg;
        break;
      case "Sinusoidal.In":
        hc.mode = n9.In, c = hc;
        break;
      case "Sinusoidal.Out":
        hc.mode = n9.Out, c = hc;
        break;
      case "Sinusoidal.InOut":
        hc.mode = n9.InOut, c = hc;
        break;
      case "Exponential.In":
        uc.mode = s9.In, c = uc;
        break;
      case "Exponential.Out":
        uc.mode = s9.Out, c = uc;
        break;
      case "Exponential.InOut":
        uc.mode = s9.InOut, c = uc;
        break;
      case "Circular.In":
        dc.mode = r9.In, c = dc;
        break;
      case "Circular.Out":
        dc.mode = r9.Out, c = dc;
        break;
      case "Circular.InOut":
        dc.mode = r9.InOut, c = dc;
        break;
      case "Elastic.In":
        gc.mode = c9.In, c = gc;
        break;
      case "Elastic.Out":
        gc.mode = c9.Out, c = gc;
        break;
      case "Elastic.InOut":
        gc.mode = c9.InOut, c = gc;
        break;
      case "Back.In":
        fc.mode = a9.In, c = fc;
        break;
      case "Back.Out":
        fc.mode = a9.Out, c = fc;
        break;
      case "Back.InOut":
        fc.mode = a9.InOut, c = fc;
        break;
      case "Bounce.In":
        pc.mode = o9.In, c = pc;
        break;
      case "Bounce.Out":
        pc.mode = o9.Out, c = pc;
        break;
      case "Bounce.InOut":
        pc.mode = o9.InOut, c = pc;
        break;
      case "Cubic.In":
        mc.mode = l9.In, c = mc;
        break;
      case "Cubic.Out":
        mc.mode = l9.Out, c = mc;
        break;
      case "Cubic.InOut":
        mc.mode = l9.InOut, c = mc;
        break;
      case "Quadratic.In":
        vc.mode = h9.In, c = vc;
        break;
      case "Quadratic.Out":
        vc.mode = h9.Out, c = vc;
        break;
      case "Quadratic.InOut":
        vc.mode = h9.InOut, c = vc;
        break;
      case "Quartic.In":
        yc.mode = u9.In, c = yc;
        break;
      case "Quartic.Out":
        yc.mode = u9.Out, c = yc;
        break;
      case "Quartic.InOut":
        yc.mode = u9.InOut, c = yc;
        break;
      case "Quintic.In":
        xc.mode = d9.In, c = xc;
        break;
      case "Quintic.Out":
        xc.mode = d9.Out, c = xc;
        break;
      case "Quintic.InOut":
        xc.mode = d9.InOut, c = xc;
        break;
    }
    this.interpolant = new c(this.times, this.values, this.getValueSize(), []), this.times.length > 1 && (this.duration = this.times[this.times.length - 1] - this.times[0]);
  }
  getStartTime() {
    return this.times[0] ?? 0;
  }
  getEndTime() {
    return this.times[this.times.length < 1 ? 1 : this.times.length - 1] ?? 0;
  }
  getValueSize() {
    return this.values.length / this.times.length;
  }
}
class v8e extends es {
  constructor(t, i) {
    super();
    S(this, "tracks", []);
    S(this, "_enabled", !1);
    S(this, "timeScale", 1);
    S(this, "_loopCount", 0);
    S(this, "_time", 0);
    this.mixer = t, this.name = i;
  }
  get enabled() {
    return this._enabled;
  }
  set enabled(t) {
    this._enabled = t;
  }
  get loopCount() {
    return this._loopCount;
  }
  set loopCount(t) {
    this._loopCount = t;
  }
  get time() {
    return this._time;
  }
  setTime(t) {
    this._time = t;
  }
  addTrack(t) {
    const i = new g8e(t);
    return this.tracks.push(i), i;
  }
  _updateTime(t, i) {
    e: {
      if (i >= t || i < 0)
        if (i = i < 0 ? 0 : t, this.loopCount === 0) {
          this.enabled = !1, this.dispatchEvent("stop", void 0);
          break e;
        } else
          this.loopCount > 0 ? this.loopCount-- : i = i >= t ? 0 : t;
      else
        this._time = i;
      this._time = i;
    }
    return i;
  }
  getTracksDuration(t) {
    let i = 0;
    return t.forEach((n) => {
      i = Math.max(i, n.getEndTime());
    }), i;
  }
  update(t, i, n) {
    const r = this._updateTime(
      this.getTracksDuration(this.tracks),
      this._time + i * this.timeScale
    );
    this.tracks.forEach((a) => {
      r > a.getStartTime() && this.setObjectProperty(a, r);
    }), this._time = r, this.enabled && this.mixer.dispatchEvent("update", { time: r });
  }
  setObjectProperty(t, i) {
    const { interpolant: n, roots: r, propertyKey: a, defaultExecute: o } = t, [l, c, h] = n.evaluate(i);
    r.forEach((u) => {
      if (u instanceof Ip)
        switch (a) {
          case "setPosition":
            u.setPosition(l, c, h);
            break;
          case "setTarget":
            u.setTarget(l, c, h);
            break;
          case "dollyTo":
            u.dollyTo(l);
            break;
          case "zoomTo":
            u.zoomTo(l);
            break;
          case "moveTo":
            u.moveTo(l, c, h);
            break;
          case "rotate":
            u.rotateTo(l, u.polarAngle, !1);
            break;
          default:
            o && o(l, c, h);
            break;
        }
      else
        switch (a) {
          case "position":
          case "rotation":
          case "scale":
          case "target":
            l !== void 0 && c !== void 0 && h !== void 0 && u[a] && u[a].set(l, c, h);
            break;
          case "lookAt":
            "lookAt" in u && u.lookAt(l, c, h);
            break;
          case "number":
            u[a] && (u[a] = l);
            break;
          default:
            o && o(l, c, h);
            break;
        }
    });
  }
  play() {
    this.enabled = !0;
  }
  stop(t) {
    this.enabled = !1, this._time = 0, this.timeScale = 1, this.removeEventListener("stop"), t && this.tracks.forEach((i) => {
      this.setObjectProperty(i, 0);
    });
  }
  pause() {
    this.enabled = !1;
  }
  reset() {
    this.time !== 0 && (this.timeScale = -1, this.play());
  }
  replay() {
    this.reset(), this.play();
  }
  preview(t) {
    this.tracks.forEach((i) => {
      t > i.getStartTime() && this.setObjectProperty(i, t);
    }), this._time = t;
  }
}
var n9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(n9 || {});
const s4 = class s4 extends Is {
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t);
    let a;
    switch (s4.mode) {
      case 0:
        a = Math.sin(r * Math.PI / 2);
        break;
      case 1:
        a = 1 - Math.cos(r * Math.PI / 2);
        break;
      case 2:
        a = Math.sin(r * Math.PI - Math.PI / 2) * 0.5 + 0.5;
        break;
    }
    const o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(s4, "mode", 0);
let hc = s4;
var s9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(s9 || {});
const r4 = class r4 extends Is {
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t);
    let a;
    switch (r4.mode) {
      case 0:
        a = Math.pow(r, 2);
        break;
      case 1:
        a = 1 - Math.pow(1 - r, 2);
        break;
      case 2:
        a = r < 0.5 ? 2 * Math.pow(r, 2) : 1 - Math.pow(-2 * r + 2, 2) / 2;
        break;
    }
    const o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(r4, "mode", 0);
let uc = r4;
var r9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(r9 || {});
const a4 = class a4 extends Is {
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t);
    let a;
    switch (a4.mode) {
      case 0:
        a = Math.sqrt(1 - Math.pow(r, 2));
        break;
      case 1:
        a = Math.sqrt(1 - Math.pow(r - 1, 2));
        break;
      case 2:
        r < 0.5 ? a = Math.sqrt(1 - Math.pow(2 * r, 2)) * 0.5 + 0.5 : a = (2 - Math.sqrt(1 - Math.pow(2 * r - 2, 2))) * 0.5;
        break;
    }
    const o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(a4, "mode", 0);
let dc = a4;
var a9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(a9 || {});
const H1 = class H1 extends Is {
  constructor(t, i, n, r, a = 0, o = 1.70158) {
    super(t, i, n, r);
    S(this, "overshoot");
    H1.mode = a, this.overshoot = o;
  }
  interpolate_(t, i, n, r) {
    let a = (n - i) / (r - i), o;
    switch (H1.mode) {
      case 0:
        o = a * a * ((this.overshoot + 1) * a - this.overshoot);
        break;
      case 1:
        o = --a * a * ((this.overshoot + 1) * a + this.overshoot) + 1;
        break;
      case 2:
        (a *= 2) < 1 ? o = 0.5 * (a * a * ((this.overshoot + 1) * a - this.overshoot)) : o = 0.5 * ((a -= 2) * a * ((this.overshoot + 1) * a + this.overshoot) + 2);
        break;
    }
    const l = this.resultBuffer, c = this.sampleValues, h = this.valueSize, u = t * h, d = u - h;
    for (let f = 0; f !== h; f++)
      l[f] = c[d + f] + (c[u + f] - c[d + f]) * o;
    return l;
  }
};
S(H1, "mode");
let fc = H1;
var o9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(o9 || {});
const V1 = class V1 extends Is {
  constructor(e, t, i, n, r = 1) {
    super(e, t, i, n), V1.mode = r;
  }
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t);
    let a;
    switch (V1.mode) {
      case 0:
        a = 1 - this.bounce(1 - r);
        break;
      case 1:
        a = this.bounce(r);
        break;
      case 2:
        r < 0.5 ? a = (1 - this.bounce(1 - r * 2)) * 0.5 : a = this.bounce(r * 2 - 1) * 0.5 + 0.5;
        break;
    }
    const o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
  bounce(e) {
    return e < 4 / 11 ? 121 * e * e / 16 : e < 8 / 11 ? 363 / 40 * e * e - 99 / 10 * e + 17 / 5 : e < 9 / 10 ? 4356 / 361 * e * e - 35442 / 1805 * e + 16061 / 1805 : 54 / 5 * e * e - 513 / 25 * e + 268 / 25;
  }
};
S(V1, "mode");
let pc = V1;
var l9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s[s.Custom = 3] = "Custom", s))(l9 || {});
const o4 = class o4 extends Is {
  constructor(t, i, n, r, a = 2, o) {
    super(t, i, n, r);
    S(this, "customEasing");
    o4.mode = a, this.customEasing = o || this.getDefaultEasing(a);
  }
  getDefaultEasing(t) {
    switch (t) {
      case 0:
        return (i) => i * i * i;
      case 1:
        return (i) => --i * i * i + 1;
      case 2:
        return (i) => i < 0.5 ? 4 * i * i * i : (i - 1) * (2 * i - 2) * (2 * i - 2) + 1;
      default:
        return (i) => i;
    }
  }
  interpolate_(t, i, n, r) {
    const a = (n - i) / (r - i), o = this.customEasing(a), l = this.resultBuffer, c = this.sampleValues, h = this.valueSize, u = t * h, d = u - h;
    for (let f = 0; f !== h; f++)
      l[f] = c[d + f] + (c[u + f] - c[d + f]) * o;
    return l;
  }
};
S(o4, "mode", 0);
let mc = o4;
var c9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(c9 || {});
const W1 = class W1 extends Is {
  constructor(t, i, n, r, a = 0, o = 1, l = 0.3) {
    super(t, i, n, r);
    S(this, "amplitude");
    S(this, "period");
    W1.mode = a, this.amplitude = o, this.period = l;
  }
  interpolate_(t, i, n, r) {
    const a = (n - i) / (r - i);
    let o;
    switch (W1.mode) {
      case 0:
        o = this.amplitude * Math.pow(2, 10 * (a - 1)) * Math.sin((a - 1) * (2 * Math.PI) / this.period);
        break;
      case 1:
        o = this.amplitude * (-Math.pow(2, -10 * a) * Math.sin((a * 10 - 0.75) * (2 * Math.PI) / this.period) + 1);
        break;
      case 2:
        a < 0.5 ? o = Math.pow(2, 20 * a - 10) * Math.sin((20 * a - 1.1) * (2 * Math.PI) / this.period) * 0.5 : o = (-Math.pow(2, -20 * a + 10) * Math.sin((20 * a - 1.1) * (2 * Math.PI) / this.period) + 2) * 0.5;
        break;
    }
    const l = this.resultBuffer, c = this.sampleValues, h = this.valueSize, u = t * h, d = u - h;
    for (let f = 0; f !== h; f++)
      l[f] = c[d + f] + (c[u + f] - c[d + f]) * o;
    return l;
  }
};
S(W1, "mode");
let gc = W1;
var h9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s[s.Custom = 3] = "Custom", s))(h9 || {});
const l4 = class l4 extends Is {
  getDefaultEasing(e) {
    switch (e) {
      case 0:
        return (t) => t * t;
      case 1:
        return (t) => t * (2 - t);
      case 2:
        return (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      default:
        return (t) => t;
    }
  }
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t), a = this.getDefaultEasing(l4.mode)(r), o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(l4, "mode", 0);
let vc = l4;
var u9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(u9 || {});
const c4 = class c4 extends Is {
  getDefaultEasing(e) {
    switch (e) {
      case 0:
        return (t) => t * t * t * t;
      case 1:
        return (t) => 1 - (t - 1) * (t - 1) * (t - 1) * (t - 1);
      case 2:
        return (t) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (t - 1) * (t - 1) * (t - 1) * (t - 1);
      default:
        return (t) => t;
    }
  }
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t), a = this.getDefaultEasing(c4.mode)(r), o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(c4, "mode", 0);
let yc = c4;
var d9 = /* @__PURE__ */ ((s) => (s[s.In = 0] = "In", s[s.Out = 1] = "Out", s[s.InOut = 2] = "InOut", s))(d9 || {});
const X1 = class X1 extends Is {
  constructor(e, t, i, n, r = 0) {
    super(e, t, i, n), X1.mode = r;
  }
  interpolate_(e, t, i, n) {
    const r = (i - t) / (n - t);
    let a;
    switch (X1.mode) {
      case 0:
        a = r * r * r * (r * (r * 6 - 15) + 10);
        break;
      case 1:
        a = 1 - r * r * r * (r * (r * 6 - 15) + 10);
        break;
      case 2:
        r < 0.5 ? a = 16 * r * r * r * (r * (r * 6 - 15) + 10) * 0.5 : a = 1 - 16 * (1 - r) * (1 - r) * (1 - r) * ((1 - r) * (6 * (1 - r) - 15) + 10) * 0.5;
        break;
    }
    const o = this.resultBuffer, l = this.sampleValues, c = this.valueSize, h = e * c, u = h - c;
    for (let d = 0; d !== c; d++)
      o[d] = l[u + d] + (l[h + d] - l[u + d]) * a;
    return o;
  }
};
S(X1, "mode");
let xc = X1;
function Go(s, ...e) {
  function t(i, n) {
    for (const r in n)
      if (Object.prototype.hasOwnProperty.call(n, r)) {
        const a = n[r];
        typeof i[r] == "object" && i[r] !== null && typeof a == "object" && a !== null ? t(i[r], a) : i[r] === void 0 && a !== null && (i[r] = a);
      }
    return i;
  }
  return e.forEach((i) => {
    t(s, i);
  }), s;
}
function s0(s) {
  return s == null;
}
function U0(s, e, t, i, n) {
  s[i] && e[i] && t[i] && (s[i] instanceof _e ? s[i].lerpColors(e[i], t[i], n) : typeof s[i] == "number" && (s[i] = nt.lerp(e[i], t[i], n)));
}
function rf(s, e, t, i) {
  const n = s.clone(), r = e.clone();
  let a = !0;
  const o = jn.createTweenHandler({
    source: { t: 0 },
    target: { t: 1 },
    duration: t.duration * 1e3,
    mode: t.mode,
    execute: (l) => {
      const { t: c } = l;
      a && (i == null || i(), a = !1), U0(e, n, r, "color", c), U0(e, n, r, "opacity", c), U0(e, n, r, "metalness", c), U0(e, n, r, "roughness", c), U0(e, n, r, "emissiveIntensity", c), U0(e, n, r, "MapBrightness", c), U0(e, n, r, "MapContrast", c), U0(e, n, r, "MapSaturation", c), U0(e, n, r, "emissive", c);
    },
    complete: () => {
      Yi(o);
    }
  });
  return o;
}
class y8e {
  constructor(e) {
    S(this, "_tweenHandlerList", []);
    this.comp = e;
  }
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.comp.states.getStateConfigById(e);
    if (i) {
      this.comp.states.setState(e);
      const n = {
        position_x: this.comp.group.position.x,
        position_y: this.comp.group.position.y,
        position_z: this.comp.group.position.z,
        rotation_x: this.comp.group.rotation.x,
        rotation_y: this.comp.group.rotation.y,
        rotation_z: this.comp.group.rotation.z,
        scale_x: this.comp.group.scale.x,
        scale_y: this.comp.group.scale.y,
        scale_z: this.comp.group.scale.z
      }, r = {
        position_x: i.transform.position.x,
        position_y: i.transform.position.y,
        position_z: i.transform.position.z,
        rotation_x: i.transform.rotation.x,
        rotation_y: i.transform.rotation.y,
        rotation_z: i.transform.rotation.z,
        scale_x: i.transform.scale.x,
        scale_y: i.transform.scale.y,
        scale_z: i.transform.scale.z
      };
      this.componentTransformAnimation(n, r, t);
    }
  }
  easyvSetData(e) {
    "animation" in e || (e.animation = {
      mode: "Linear.None",
      duration: 0
    }), this.playTransformAnimation(e, "deg");
  }
  playTransformAnimation(e, t = "rad") {
    if ("animation" in e) {
      const i = e.animation, n = {
        duration: (i == null ? void 0 : i.duration) ?? 1,
        mode: i == null ? void 0 : i.mode,
        distance: i == null ? void 0 : i.distance,
        durationInSeconds: (i == null ? void 0 : i.durationInSeconds) ?? 1
      }, r = {
        position_x: this.comp.group.position.x,
        position_y: this.comp.group.position.y,
        position_z: this.comp.group.position.z,
        rotation_x: this.comp.group.rotation.x,
        rotation_y: this.comp.group.rotation.y,
        rotation_z: this.comp.group.rotation.z,
        scale_x: this.comp.group.scale.x,
        scale_y: this.comp.group.scale.y,
        scale_z: this.comp.group.scale.z
      }, a = (l) => t === "deg" ? L1(l) : (console.log(l), l), { transform: o } = e;
      if (o) {
        const l = o.position, c = o.rotation, h = o.scale;
        let u = this.comp.group.position.x, d = this.comp.group.position.y, f = this.comp.group.position.z;
        if (l && ("x" in l && (u = l.x), "y" in l && (d = l.y), "z" in l && (f = l.z), "lon" in l && "lat" in l)) {
          const v = Lu(
            this.comp.entity.world,
            l.lon,
            l.lat,
            d
          );
          u = v.x, d = v.y, f = v.z;
        }
        const p = {
          position_x: u,
          position_y: d,
          position_z: f,
          rotation_x: a(c == null ? void 0 : c.x) ?? this.comp.group.rotation.x,
          rotation_y: a(c == null ? void 0 : c.y) ?? this.comp.group.rotation.y,
          rotation_z: a(c == null ? void 0 : c.z) ?? this.comp.group.rotation.z,
          scale_x: (h == null ? void 0 : h.x) ?? this.comp.group.scale.x,
          scale_y: (h == null ? void 0 : h.y) ?? this.comp.group.scale.y,
          scale_z: (h == null ? void 0 : h.z) ?? this.comp.group.scale.z
        };
        this.componentTransformAnimation(r, p, n);
      }
    }
  }
  componentTransformAnimation(e, t, i) {
    this.comp.stopTweenAnimation(), this.comp.tweenHandler = jn.createTweenHandler({
      mode: i.mode,
      duration: Rf(i),
      source: e,
      target: t,
      execute: (n) => {
        mD(this.comp, n);
      },
      complete: () => {
        mD(this.comp, e);
      }
    });
  }
  easyvFocusOnObject3D(e, t, i) {
    const n = wx(this.comp.group, t), r = M6(n, i);
    r && e.flyToBox(r, { duration: Rf(i) });
  }
  easyvFocusOnModel(e, t) {
    const i = M6(this.comp.group, t);
    i && e.flyToBox(i, { duration: Rf(t) });
  }
  easyvReplaceMaterialBySlotIdAndMaterialTag(e, t, i) {
    this.clearAllTween();
    let n = e;
    Array.isArray(e[0]) && (n = e.flat());
    const r = st.assetManager.materialManager.getMaterialByUUID(t);
    n.forEach((a) => {
      var o;
      if (r) {
        const l = (o = this.comp.modelRenderResource.findMaterialSlotById(a)) == null ? void 0 : o.material;
        if (i) {
          if (r && l) {
            const c = rf(
              l,
              r,
              i,
              () => {
                this.comp.modelRenderResource.setSlotMaterial(a, r);
              }
            );
            this._tweenHandlerList.push(c);
          }
        } else
          r && l && this.comp.modelRenderResource.setSlotMaterial(a, r);
      }
    });
  }
  easyvReplaceMaterialByMeshIndexIdAndMaterialTag(e, t, i) {
    this.clearAllTween();
    let n = e;
    Array.isArray(e[0]) && (n = e.flat());
    const r = this.comp.getMeshList().filter((o) => n.includes(o.userData.indexId)), a = st.assetManager.materialManager.getMaterialByUUID(t);
    a && r.forEach((o) => {
      if (i) {
        const l = rf(
          o.material,
          a,
          i,
          () => {
            o.material = a;
          }
        );
        this._tweenHandlerList.push(l);
      } else
        o.material = a;
    });
  }
  easyvResetMaterialBySlotIds(e, t) {
    if (this.clearAllTween(), !e)
      return;
    let i = e;
    Array.isArray(e[0]) && (i = e.flat()), i.forEach((n) => {
      const r = this.findInitMaterialSlotOption(n), a = this.comp.getMaterialSlots().find((o) => o.id === n);
      if (a && r && r.materialId) {
        const o = st.assetManager.materialManager.getMaterialByUUID(
          r.materialId
        );
        if (o)
          if (t) {
            if (a.material && o) {
              const l = rf(
                a.material,
                o,
                t,
                () => {
                  a.setMaterial(o);
                }
              );
              this._tweenHandlerList.push(l);
            }
          } else
            a.setMaterial(o);
      }
    });
  }
  easyvResetMaterialByMeshIndexIds(e, t) {
    this.clearAllTween();
    let i = e;
    Array.isArray(e[0]) && (i = e.flat()), this.comp.getMeshList().filter((r) => i.includes(r.userData.indexId)).map((r) => {
      var l;
      const a = this.comp.getMaterialSlots().find((c) => c.modelMesh.some((h) => h.uuid === r.uuid)), o = a && ((l = this.comp.initDataOptions) == null ? void 0 : l.materialSlots.find((c) => c.slotId === a.id));
      return {
        item: r,
        materialInEditor: o ? st.assetManager.materialManager.getMaterialByUUID(
          o.materialId
        ) : null
      };
    }).forEach(({ item: r, materialInEditor: a }) => {
      if (a)
        if (t) {
          const o = rf(
            r.material,
            a,
            t,
            () => {
              r.material = a;
            }
          );
          this._tweenHandlerList.push(o);
        } else
          r.material = a;
    });
  }
  easyvResetMaterialByMeshName(e, t) {
    this.clearAllTween(), this.getMeshList(e).map((n) => {
      var o;
      const r = this.comp.getMaterialSlots().find((l) => l.modelMesh.some((c) => c.uuid === n.uuid)), a = r && ((o = this.comp.initDataOptions) == null ? void 0 : o.materialSlots.find((l) => l.slotId === r.id));
      return {
        item: n,
        materialInEditor: a ? st.assetManager.materialManager.getMaterialByUUID(
          a.materialId
        ) : null
      };
    }).forEach(({ item: n, materialInEditor: r }) => {
      if (r)
        if (t) {
          const a = rf(
            n.material,
            r,
            t,
            () => {
              n.material = r;
            }
          );
          this._tweenHandlerList.push(a);
        } else
          n.material = r;
    });
  }
  easyvReplaceMaterialByMeshNameAndMaterialName(e, t, i) {
    this.clearAllTween();
    const n = st.assetManager.materialManager.findMaterialByName(
      t,
      "publicMaterial"
    );
    n && this.getMeshList(e).forEach((a) => {
      if (i) {
        const o = rf(
          a.material,
          n,
          i,
          () => {
            a.material = n;
          }
        );
        this._tweenHandlerList.push(o);
      } else
        a.material = n;
    });
  }
  getMeshList(e) {
    const t = this.comp.modelRenderResource.scene.getObjectByName(e), i = /* @__PURE__ */ new Set();
    return p0(t) && i.add(t), t == null || t.traverse((n) => {
      p0(n) && i.add(n);
    }), [...i];
  }
  easyvFocusOnObject3DByName(e, t, i) {
    const n = this.comp.group.getObjectByName(t), r = M6(n, i);
    r && e.flyToBox(r, { duration: Rf(i) });
  }
  easyvPlayAnimation(e) {
    const t = this.comp.animationActions.find((i) => i.options.id === e);
    t == null || t.play();
  }
  easyvPlayAnimationFromStart(e) {
    const t = this.comp.animationActions.find((i) => i.options.id === e);
    t == null || t.replay();
  }
  easyvStopAnimation(e) {
    const t = this.comp.animationActions.find((i) => i.options.id === e);
    t == null || t.stop();
  }
  easyvPauseAnimation(e) {
    const t = this.comp.animationActions.find((i) => i.options.id === e);
    t == null || t.pause();
  }
  easyvReverse(e) {
    const t = this.comp.animationActions.find((i) => i.options.id === e);
    t == null || t.reverse();
  }
  findInitMaterialSlotOption(e) {
    var t;
    return (t = this.comp.initDataOptions) == null ? void 0 : t.materialSlots.find((i) => i.slotId === e);
  }
  clearAllTween() {
    this._tweenHandlerList.forEach((e) => {
      e.stop(), Yi(e);
    }), this._tweenHandlerList = [];
  }
}
function Rf(s) {
  return s.duration !== void 0 ? s.duration * 1e3 : s.durationInSeconds !== void 0 ? s.durationInSeconds * 1e3 : 0.1;
}
function M6(s, e) {
  if (s)
    return new zt().setFromObject(s).expandByScalar(e.distance ?? 0);
}
function mD(s, e) {
  s.group.position.x = e.position_x, s.group.position.y = e.position_y, s.group.position.z = e.position_z, s.group.rotation.x = e.rotation_x, s.group.rotation.y = e.rotation_y, s.group.rotation.z = e.rotation_z, s.group.scale.x = e.scale_x, s.group.scale.y = e.scale_y, s.group.scale.z = e.scale_z;
}
var x8e = Object.defineProperty, A8e = Object.getOwnPropertyDescriptor, TU = (s, e, t, i) => {
  for (var n = i > 1 ? void 0 : i ? A8e(e, t) : e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (i ? a(e, t, n) : a(n)) || n);
  return i && n && x8e(e, t, n), n;
};
let Rc = class extends UF {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "easyvProxy", new y8e(this));
    S(this, "customAttributes", {});
    S(this, "_physicService");
    S(this, "_actionProxies", []);
    S(this, "_nodeBoundingBox", new eb(new mt()));
    /**
     * 蓝图预览页时使用的，用来恢复到编辑器状态的材质设置
     */
    S(this, "_initDataOptions");
    /**
     * 记录模型导入创建时的数据，用来恢复导入时节点变换数据
     */
    S(this, "_initData");
    /**
     * 将initData中模型结构平铺，方便查找
     */
    S(this, "objectDataMap");
    /**
     * 记录每个骨骼的动画句柄
     */
    S(this, "bonesTweenHandlers", {});
    // TODO: delete it
    S(this, "_mixer");
    S(this, "_actions");
    S(this, "wireFrameMaterial", new Bi({
      color: 65535,
      transparent: !0
    }));
    S(this, "world");
    S(this, "setObjectTransform", (e) => {
      var a, o, l, c, h, u, d, f, p, v, g, m, x, y, A, b, M, w;
      const { name: t, transform: i } = e, r = this.group.getObjectsByProperty("name", t)[0];
      if (r)
        if (s0(this.bonesTweenHandlers[r.uuid]) || (this.bonesTweenHandlers[r.uuid].stop(), Yi(this.bonesTweenHandlers[r.uuid]), delete this.bonesTweenHandlers[r.uuid]), s0(e.animation))
          i.position && r.position.set(i.position.x, i.position.y, i.position.z), i.rotation && r.rotation.set(
            i.rotation.x * nt.DEG2RAD,
            i.rotation.y * nt.DEG2RAD,
            i.rotation.z * nt.DEG2RAD
          ), i.scale && r.scale.set(i.scale.x, i.scale.y, i.scale.z);
        else {
          const C = (e.animation.duration ?? 0) * 1e3, T = e.animation.mode ?? "Linear.None", E = {
            position_x: r.position.x,
            position_y: r.position.y,
            position_z: r.position.z,
            rotation_x: r.rotation.x,
            rotation_y: r.rotation.y,
            rotation_z: r.rotation.z,
            scale_x: r.scale.x,
            scale_y: r.scale.y,
            scale_z: r.scale.z
          }, P = {
            position_x: ((o = (a = e == null ? void 0 : e.transform) == null ? void 0 : a.position) == null ? void 0 : o.x) ?? r.position.x,
            position_y: ((c = (l = e == null ? void 0 : e.transform) == null ? void 0 : l.position) == null ? void 0 : c.y) ?? r.position.y,
            position_z: ((u = (h = e == null ? void 0 : e.transform) == null ? void 0 : h.position) == null ? void 0 : u.z) ?? r.position.z,
            rotation_x: L1((f = (d = e == null ? void 0 : e.transform) == null ? void 0 : d.rotation) == null ? void 0 : f.x) ?? r.rotation.x,
            rotation_y: L1((v = (p = e == null ? void 0 : e.transform) == null ? void 0 : p.rotation) == null ? void 0 : v.y) ?? r.rotation.y,
            rotation_z: L1((m = (g = e == null ? void 0 : e.transform) == null ? void 0 : g.rotation) == null ? void 0 : m.z) ?? r.rotation.z,
            scale_x: ((y = (x = e == null ? void 0 : e.transform) == null ? void 0 : x.scale) == null ? void 0 : y.x) ?? r.scale.x,
            scale_y: ((b = (A = e == null ? void 0 : e.transform) == null ? void 0 : A.scale) == null ? void 0 : b.y) ?? r.scale.y,
            scale_z: ((w = (M = e == null ? void 0 : e.transform) == null ? void 0 : M.scale) == null ? void 0 : w.z) ?? r.scale.z
          };
          this.bonesTweenHandlers[r.uuid] = jn.createTweenHandler({
            mode: T,
            duration: C,
            source: E,
            target: P,
            execute: (L) => {
              r.position.x = L.position_x, r.position.y = L.position_y, r.position.z = L.position_z, r.rotation.x = L.rotation_x, r.rotation.y = L.rotation_y, r.rotation.z = L.rotation_z, r.scale.x = L.scale_x, r.scale.y = L.scale_y, r.scale.z = L.scale_z;
            },
            complete: () => {
              r.position.x = P.position_x, r.position.y = P.position_y, r.position.z = P.position_z, r.rotation.x = P.rotation_x, r.rotation.y = P.rotation_y, r.rotation.z = P.rotation_z, r.scale.x = P.scale_x, r.scale.y = P.scale_y, r.scale.z = P.scale_z, s0(this.bonesTweenHandlers[r.uuid]) || (this.bonesTweenHandlers[r.uuid].stop(), Yi(this.bonesTweenHandlers[r.uuid]), delete this.bonesTweenHandlers[r.uuid]);
            }
          });
        }
    });
    S(this, "easyvReverse", (e) => {
      const t = this.animationActions.find(
        (i) => i.options.id === e.detail.id
      );
      t == null || t.reverse();
    });
    Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  set initData(e) {
    this._initData = e, this._initData.scene && this._initData.scene.object && (this.objectDataMap = b8e(this._initData.scene.object));
  }
  get initData() {
    return this._initData;
  }
  get initDataOptions() {
    return this._initDataOptions;
  }
  set initDataOptions(e) {
    this._initDataOptions = e;
  }
  get animationActions() {
    return this._actionProxies;
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s("Model", "setObjectTransform", e, this.setObjectTransform);
  }
  addObject2Renderer(e) {
    this.group.clear();
    const t = e || this.modelRenderResource.scene;
    VF.add(this.group, t);
    const i = () => {
      this.isInit = !0, this.group.removeEventListener(
        "object-queue-render-end",
        i
      );
      const { physicsConfig: n } = this.states.getCurrentStateConfig();
      n && (n == null ? void 0 : n.mode) !== Vn.None && this.addObject2PhysicService(t), this.needsUpdate = !0;
    };
    this.group.addEventListener(
      "object-queue-render-end",
      i
    );
  }
  addObject2PhysicService(e) {
    var n;
    const i = this.getCurrentStateConfig().physicsConfig.mode;
    (n = this._physicService) == null || n.createRigidBody(
      {
        object: e,
        enableGravity: !1,
        entity: this.entity,
        mode: i
      },
      !0
    );
  }
  removeObject2PhysicService(e) {
    var t;
    (t = this._physicService) == null || t.removeRigidBody(e);
  }
  getModelSceneGraph() {
    return S6.serializeData(this).sceneGraph;
  }
  getObjectById(e) {
    return this.group.userData = {
      ...this.customAttributes
    }, this.group;
  }
  getMeshList() {
    return this.modelRenderResource.getMeshList();
  }
  updateInitDataOptions() {
    this._initDataOptions = this.toJson({
      updateModelSceneData: !0
    }).data;
  }
  toJson(e = {
    updateModelSceneData: !1
    // 模型组件数据字段配置项太多存在互相影响的字段，需要能够独立进行更新
  }) {
    return {
      ...super.toJson(),
      states: S6.serializeStates(this),
      data: S6.serializeData(this, e)
    };
  }
  updateMeshRenderOrder(e, t) {
    const i = this.group.getObjectByProperty("uuid", e);
    i && (i.renderOrder = t), this.syncComponentDataConfig({ updateModelSceneData: !0 }), dt.Entity.refresh();
  }
  updateObjectTransform(e, t) {
    const i = this.group.getObjectByProperty("uuid", e);
    i && (i.position.set(t.position.x, t.position.y, t.position.z), i.rotation.set(t.rotation.x, t.rotation.y, t.rotation.z), this.syncComponentDataConfig({ updateModelSceneData: !0 }), dt.Entity.refresh());
  }
  restoreObjectTransform(e) {
    var n;
    const t = this.group.getObjectByProperty("uuid", e), i = (n = this.objectDataMap) == null ? void 0 : n[e];
    i && t && (t.position.set(i.position[0], i.position[1], i.position[2]), t.rotation.set(i.rotation[0], i.rotation[1], i.rotation[2]), t.scale.set(i.scale[0], i.scale[1], i.scale[2]), this.syncComponentDataConfig({ updateModelSceneData: !0 }), dt.Entity.refresh());
  }
  restoreAllObjectTransform() {
    this.objectDataMap && (this.group.traverse((e) => {
      const t = this.objectDataMap[e.uuid];
      t && (e.position.set(t.position[0], t.position[1], t.position[2]), e.rotation.set(t.rotation[0], t.rotation[1], t.rotation[2]), e.scale.set(t.scale[0], t.scale[1], t.scale[2]));
    }), this.syncComponentDataConfig({ updateModelSceneData: !0 }), dt.Entity.refresh());
  }
  async updateFromJson(e, t) {
    this.world = e, this.fromJson(t);
  }
  async fromJson(e) {
    if (e.type !== "Model")
      throw new Error("component type is not model!");
    this.url = e.data.asset.url;
    const t = await CU.updateToLatestVersion(e), { data: i, states: n } = t;
    this._initDataOptions = { ...i }, this.initData = { ...e.initialData };
    const { customAttribute: r } = i;
    this.customAttributes = r ? { ...r } : {}, this.initStates(n);
    const a = this.getCurrentStateConfig();
    await this.prepareRenderResourceData(i, a);
  }
  initStates(e) {
    return e = e.map((t) => {
      const i = Go(t.config, {
        enableWireFrame: !1,
        wireFrameColor: {
          pure: "#00ffff",
          type: "pure"
        },
        disablePick: !1
      });
      return t.config = i, t;
    }), super.initStates(e), this;
  }
  update(e) {
    this.needsUpdate && this.updateRenderResourceByCurrentState();
  }
  onDragEnd() {
    this.updateCurrentStateConfig();
  }
  focusOnObject3D(e, t, i, n) {
    let r = new zt();
    const a = wx(this.group, t);
    a && (r.setFromObject(a), r = r.expandByScalar(i ?? 0), e.flyToBox(r, { duration: n }));
  }
  easyvSetCustomAttribute(e, t) {
    this.setCustomAttribute(e);
  }
  setCustomAttribute(e, t) {
    this.customAttributes = Object.assign({}, this.customAttributes, e), this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  replaceCustomAttribute(e) {
    this.customAttributes = { ...e }, this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  updateCustomAttributeKey(e, t) {
    this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  removeCustomAttribute(e, t) {
    e && (delete this.customAttributes[e], this.syncComponentDataConfig(), dt.Entity.refresh());
  }
  unSelectAllObjects() {
    this._nodeBoundingBox.removeFromParent(), this._nodeBoundingBox = new eb(new mt());
  }
  selectObjectByIndexId(e) {
    const t = wx(this.group, e);
    t ? this._nodeBoundingBox.setFromObject(t) : this._nodeBoundingBox.setFromObject(new mt()), this.group.attach(this._nodeBoundingBox);
  }
  dispose() {
    this.stopAnimationLoop(), this.group.clear(), this.modelRenderResource.dispose();
  }
  startAnimationLoop() {
    this.startOrRestartAnimationLoop();
  }
  stopTweenAnimation() {
    this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler), this.tweenHandler = void 0);
  }
  updateAnimationActionOptions(e, t) {
    const i = this._actionProxies[e];
    i && (i.reset(), i.options.loop = t.loop, i.options.timeScale = t.timeScale, i.options.play = t.play, i.options.endReset = t.endReset, t.play ? i.play() : i.stop(), this.needDataUpdateServer = !0), dt.Entity.refresh();
  }
  createAnimationActions() {
    this._actionProxies = [], this.modelRenderResource.actions.forEach((e) => {
      const t = e.getClip(), i = new tM(e);
      i.options.id = t.name, i.options.name = t.name, i.options.loop = "repeat", i.options.timeScale = 1, i.time = 0, this._actionProxies.push(i);
    });
  }
  updateRenderResource(e) {
    const {
      transform: t,
      castShadow: i,
      receiveShadow: n,
      enableWireFrame: r = !1,
      wireFrameColor: a,
      disablePick: o,
      physicsConfig: l,
      enableOutline: c
    } = e;
    this.disablePick = o;
    const { position: h, rotation: u, scale: d } = t ?? {};
    h && this.group.position.fromArray([h.x, h.y, h.z]), u && this.group.rotation.fromArray([u.x, u.y, u.z]), d && this.group.scale.fromArray([d.x, d.y, d.z]), this.updateShadow(i, n), this.updateWireFrame(r, a), this.updateOutLine(c), l && (l == null ? void 0 : l.mode) !== Vn.None ? this.addObject2PhysicService(this.group) : this.removeObject2PhysicService(this.group);
  }
  updateOutLine(e) {
    if (this.world) {
      const t = [];
      this.group.traverse((i) => {
        Bg(i) && t.push(i);
      }), e ? this.world.renderer.addObjectsToOutLinePass(t) : this.world.renderer.removeObjectsFromOutLinePass(t);
    }
  }
  updateShadow(e, t) {
    e !== void 0 && t !== void 0 && this.modelRenderResource.scene.traverse((i) => {
      i.castShadow = e, i.receiveShadow = t;
    });
  }
  updateWireFrame(e, t) {
    this.modelRenderResource.scene.traverse((i) => {
      var n;
      if (Bg(i)) {
        let r = (n = i.parent) == null ? void 0 : n.children.find(
          (a) => a.name === `${i.uuid}`
        );
        if (e && (r = this.addWireFrameMeshToParent(i), i.userData[Ya] = r, console.log(i)), r) {
          i.visible = !e, e ? r.layers.set(0) : r.layers.set(1);
          const { color: a, alpha: o } = EU(t.pure);
          this.wireFrameMaterial.color = a, this.wireFrameMaterial.opacity = o;
        }
      }
    });
  }
  async prepareRenderResourceData(e, t) {
    try {
      this.modelRenderResource = await this.loadRenderResource(e), this.modelRenderResource.url = e.asset.url, this.createAnimationActions(), this.updateAnimationActions(e), this.updateRenderResource(t), e.scene.skeletons && Object.keys(e.scene.skeletons).length !== 0 ? (this.group.add(this.modelRenderResource.scene), this.isInit = !0) : this.addObject2Renderer(), this.startAnimationLoop();
    } catch (i) {
      console.error("Error loading model resource:", i);
    }
  }
  addWireFrameMeshToParent(e) {
    var i, n;
    let t = (i = e.parent) == null ? void 0 : i.children.find(
      (r) => r.name === `${e.uuid}`
    );
    if (!t) {
      const r = new ap(e.geometry);
      t = new _n(r, this.wireFrameMaterial), t.name = e.uuid, t.userData.skipSerialization = !0, t.position.copy(e.position), t.rotation.copy(e.rotation), t.scale.copy(e.scale), (n = e.parent) == null || n.add(t), zhe(e, Ya, t);
    }
    return t;
  }
  async loadRenderResource(e) {
    const t = await this.loadRenderResourceFromJson(e), i = await st.assetManager.modelManager.getModelByUrl(
      e.asset.url
    );
    return i && qc.bindAnimationsToModelRenderResource(t, i), t;
  }
  updateAnimationActions(e) {
    const { animation: t } = e;
    if (t) {
      t.forEach((n) => {
        n.endReset = n.endReset ?? !0;
      });
      const i = t;
      this._actionProxies.forEach((n, r) => {
        i[r] && (n.options = { ...i[r] }, i[r].play ? n.play() : n.stop());
      });
    }
  }
  updateRenderResourceByCurrentState() {
    const e = this.getCurrentStateConfig();
    e && this.updateRenderResource(e);
  }
  getCurrentStateConfig() {
    return this.states.getCurrentStateConfig();
  }
  updateCurrentStateConfig() {
    const e = this.getCurStateId(), t = this.getStateList().find((i) => i.id === e);
    if (t) {
      const i = { ...t };
      i.config.transform.position.x = this.group.position.x, i.config.transform.position.y = this.group.position.y, i.config.transform.position.z = this.group.position.z, i.config.transform.rotation.x = this.group.rotation.x, i.config.transform.rotation.y = this.group.rotation.y, i.config.transform.rotation.z = this.group.rotation.z, i.config.transform.scale.x = this.group.scale.x, i.config.transform.scale.y = this.group.scale.y, i.config.transform.scale.z = this.group.scale.z, this.updateStateConfig(t.id, i.config, i.name);
    }
  }
  startOrRestartAnimationLoop() {
    if (this.modelRenderResource.mixer) {
      this.modelRenderResource.mixer.addEventListener("finished", (t) => {
        var i, n;
        (n = (i = this.entity.world).emitFunc) == null || n.call(
          i,
          "modelAnimationEnd",
          {
            entityId: this.entity.uuid,
            componentId: this.id,
            animationName: t.action.getClip().name,
            customAttribute: this.customAttributes
          },
          this.entity.uuid
        );
      });
      const e = new Ju();
      this.stopAnimationLoop(), this.animationHandler = requestAnimationFrame(this.animationStep.bind(this, e));
    }
  }
  animationStep(e) {
    var t;
    (t = this.modelRenderResource.mixer) == null || t.update(e.getDelta()), this.modelRenderResource.mixer && (this.animationHandler = requestAnimationFrame(this.animationStep.bind(this, e)));
  }
  stopAnimationLoop() {
    this.animationHandler !== void 0 && (cancelAnimationFrame(this.animationHandler), this.animationHandler = void 0);
  }
};
TU([
  KO("EasyTwinPhysicService")
], Rc.prototype, "_physicService", 2);
Rc = TU([
  ni("Model")
], Rc);
class S6 {
  static serializeStates(e) {
    return e.states.toJson();
  }
  static serializeData(e, t = {
    updateModelSceneData: !1
    // 模型组件数据字段配置项太多存在互相影响的字段，需要能够独立进行更新
  }) {
    var a, o, l, c, h, u, d;
    const i = e.modelRenderResource.toJSON();
    i.asset.url = i.asset.url || e.url;
    const n = (t.updateModelSceneData ? i.scene : (a = e.initDataOptions) == null ? void 0 : a.scene) ?? i.scene;
    return {
      ...i,
      scene: n,
      animation: this.serializeAnimationData(e),
      transform: {
        position: ((l = (o = e == null ? void 0 : e.initDataOptions) == null ? void 0 : o.transform) == null ? void 0 : l.position) ?? { x: 0, y: 0, z: 0 },
        rotation: ((h = (c = e == null ? void 0 : e.initDataOptions) == null ? void 0 : c.transform) == null ? void 0 : h.rotation) ?? { x: 0, y: 0, z: 0 },
        scale: ((d = (u = e == null ? void 0 : e.initDataOptions) == null ? void 0 : u.transform) == null ? void 0 : d.scale) ?? { x: 1, y: 1, z: 1 }
      },
      customAttribute: e.customAttributes
    };
  }
  static serializeAnimationData(e) {
    if (e.modelRenderResource.mixer)
      return e.animationActions.map((i) => i.options);
  }
}
class CU {
  static async updateToLatestVersion(e) {
    const t = await this.updateData(e.data), i = await this.updateStates(e.states);
    return {
      ...e,
      states: i,
      version: "1.7.0",
      data: t
    };
  }
  static async updateStates(e) {
    return e.forEach((t) => {
      const { physicsConfig: i } = t.config;
      i || (t.config.physicsConfig = {
        enableCollision: !1,
        mode: Vn.None
      }), i && !("mode" in i) && (t.config.physicsConfig.mode = t.config.physicsConfig.enableCollision ? Vn.QueryAndCollision : Vn.None);
    }), e;
  }
  static async updateData(e) {
    const { scene: t, materialSlots: i } = await this.createSceneField(e);
    return {
      ...e,
      scene: t,
      materialSlots: i
    };
  }
  static async createSceneField(e) {
    const { asset: t, materialSlots: i, scene: n } = e, r = t.url;
    if (!n) {
      const a = await st.assetManager.modelManager.getModelByUrl(r);
      if (a) {
        const o = qc.InstantiateModelComponentRenderResources(a);
        i ? o.materialSlots.forEach((c) => {
          const h = i.find((u) => u.slotId === c.slotId);
          if (h) {
            const u = new kt();
            if (u.uuid = h.initMaterialId, c.setInitMaterial(u), c.setMaterial(u), h.initMaterialId !== h.materialId) {
              const d = new kt();
              c.setMaterial(d);
            }
          }
        }) : (o.materialSlots.forEach((c) => {
          c.initMaterial && st.assetManager.materialManager.addMaterial(
            c.initMaterial.uuid,
            c.initMaterial
          );
        }), o.materialSlots.map((c) => c.toJSON()));
        const { scene: l } = o.toJSON();
        return {
          scene: l,
          materialSlots: i ?? o.materialSlots.map((c) => c.toJSON())
        };
      }
    }
    return {
      scene: n,
      materialSlots: i
    };
  }
}
function b8e(s) {
  const e = {}, t = (i) => {
    if (e[i.uuid] = i, i.children)
      for (let n of i.children)
        t(n);
  };
  return t(s), e;
}
class Df extends Mu {
  getMatrixAt(e, t) {
    const i = this.getPointAt(e), n = this.getTangentAt(e);
    return t === void 0 && (t = new et()), t.setPosition(i), t.lookAt(i, i.clone().add(n), new _(0, 1, 0)), t;
  }
}
const Wh = new lr(), Ks = new _(), ic = new _(), Ln = new St(), gD = {
  X: new _(1, 0, 0),
  Y: new _(0, 1, 0),
  Z: new _(0, 0, 1)
}, w6 = { type: "change" }, vD = { type: "mouseDown", mode: null }, yD = { type: "mouseUp", mode: null }, xD = { type: "objectChange" };
class M8e extends mt {
  constructor(e, t) {
    super(), t === void 0 && (console.warn('THREE.TransformControls: The second parameter "domElement" is now mandatory.'), t = document), this.isTransformControls = !0, this.visible = !1, this.domElement = t, this.domElement.style.touchAction = "none";
    const i = new _8e();
    this._gizmo = i, this.add(i);
    const n = new P8e();
    this._plane = n, this.add(n);
    const r = this;
    function a(x, y) {
      let A = y;
      Object.defineProperty(r, x, {
        get: function() {
          return A !== void 0 ? A : y;
        },
        set: function(b) {
          A !== b && (A = b, n[x] = b, i[x] = b, r.dispatchEvent({ type: x + "-changed", value: b }), r.dispatchEvent(w6));
        }
      }), r[x] = y, n[x] = y, i[x] = y;
    }
    a("camera", e), a("object", void 0), a("enabled", !0), a("axis", null), a("mode", "translate"), a("translationSnap", null), a("rotationSnap", null), a("scaleSnap", null), a("space", "world"), a("size", 1), a("dragging", !1), a("showX", !0), a("showY", !0), a("showZ", !0);
    const o = new _(), l = new _(), c = new St(), h = new St(), u = new _(), d = new St(), f = new _(), p = new _(), v = new _(), g = 0, m = new _();
    a("worldPosition", o), a("worldPositionStart", l), a("worldQuaternion", c), a("worldQuaternionStart", h), a("cameraPosition", u), a("cameraQuaternion", d), a("pointStart", f), a("pointEnd", p), a("rotationAxis", v), a("rotationAngle", g), a("eye", m), this._offset = new _(), this._startNorm = new _(), this._endNorm = new _(), this._cameraScale = new _(), this._parentPosition = new _(), this._parentQuaternion = new St(), this._parentQuaternionInv = new St(), this._parentScale = new _(), this._worldScaleStart = new _(), this._worldQuaternionInv = new St(), this._worldScale = new _(), this._positionStart = new _(), this._quaternionStart = new St(), this._scaleStart = new _(), this._getPointer = S8e.bind(this), this._onPointerDown = E8e.bind(this), this._onPointerHover = w8e.bind(this), this._onPointerMove = T8e.bind(this), this._onPointerUp = C8e.bind(this), this.domElement.addEventListener("pointerdown", this._onPointerDown), this.domElement.addEventListener("pointermove", this._onPointerHover), this.domElement.addEventListener("pointerup", this._onPointerUp);
  }
  // updateMatrixWorld updates key transformation variables
  updateMatrixWorld(e) {
    this.object !== void 0 && (this.object.updateMatrixWorld(), this.object.parent === null ? console.error("TransformControls: The attached 3D object must be a part of the scene graph.") : this.object.parent.matrixWorld.decompose(this._parentPosition, this._parentQuaternion, this._parentScale), this.object.matrixWorld.decompose(this.worldPosition, this.worldQuaternion, this._worldScale), this._parentQuaternionInv.copy(this._parentQuaternion).invert(), this._worldQuaternionInv.copy(this.worldQuaternion).invert()), this.camera.updateMatrixWorld(), this.camera.matrixWorld.decompose(this.cameraPosition, this.cameraQuaternion, this._cameraScale), this.camera.isOrthographicCamera ? this.camera.getWorldDirection(this.eye).negate() : this.eye.copy(this.cameraPosition).sub(this.worldPosition).normalize(), super.updateMatrixWorld(e);
  }
  pointerHover(e) {
    if (this.object === void 0 || this.dragging === !0)
      return;
    e !== null && Wh.setFromCamera(e, this.camera);
    const t = E6(this._gizmo.picker[this.mode], Wh);
    t ? this.axis = t.object.name : this.axis = null;
  }
  pointerDown(e) {
    if (!(this.object === void 0 || this.dragging === !0 || e != null && e.button !== 0) && this.axis !== null) {
      e !== null && Wh.setFromCamera(e, this.camera);
      const t = E6(this._plane, Wh, !0);
      t && (this.object.updateMatrixWorld(), this.object.parent.updateMatrixWorld(), this._positionStart.copy(this.object.position), this._quaternionStart.copy(this.object.quaternion), this._scaleStart.copy(this.object.scale), this.object.matrixWorld.decompose(this.worldPositionStart, this.worldQuaternionStart, this._worldScaleStart), this.pointStart.copy(t.point).sub(this.worldPositionStart)), this.dragging = !0, vD.mode = this.mode, this.dispatchEvent(vD);
    }
  }
  pointerMove(e) {
    const t = this.axis, i = this.mode, n = this.object;
    let r = this.space;
    if (i === "scale" ? r = "local" : (t === "E" || t === "XYZE" || t === "XYZ") && (r = "world"), n === void 0 || t === null || this.dragging === !1 || e !== null && e.button !== -1)
      return;
    e !== null && Wh.setFromCamera(e, this.camera);
    const a = E6(this._plane, Wh, !0);
    if (a) {
      if (this.pointEnd.copy(a.point).sub(this.worldPositionStart), i === "translate")
        this._offset.copy(this.pointEnd).sub(this.pointStart), r === "local" && t !== "XYZ" && this._offset.applyQuaternion(this._worldQuaternionInv), t.indexOf("X") === -1 && (this._offset.x = 0), t.indexOf("Y") === -1 && (this._offset.y = 0), t.indexOf("Z") === -1 && (this._offset.z = 0), r === "local" && t !== "XYZ" ? this._offset.applyQuaternion(this._quaternionStart).divide(this._parentScale) : this._offset.applyQuaternion(this._parentQuaternionInv).divide(this._parentScale), n.position.copy(this._offset).add(this._positionStart), this.translationSnap && (r === "local" && (n.position.applyQuaternion(Ln.copy(this._quaternionStart).invert()), t.search("X") !== -1 && (n.position.x = Math.round(n.position.x / this.translationSnap) * this.translationSnap), t.search("Y") !== -1 && (n.position.y = Math.round(n.position.y / this.translationSnap) * this.translationSnap), t.search("Z") !== -1 && (n.position.z = Math.round(n.position.z / this.translationSnap) * this.translationSnap), n.position.applyQuaternion(this._quaternionStart)), r === "world" && (n.parent && n.position.add(Ks.setFromMatrixPosition(n.parent.matrixWorld)), t.search("X") !== -1 && (n.position.x = Math.round(n.position.x / this.translationSnap) * this.translationSnap), t.search("Y") !== -1 && (n.position.y = Math.round(n.position.y / this.translationSnap) * this.translationSnap), t.search("Z") !== -1 && (n.position.z = Math.round(n.position.z / this.translationSnap) * this.translationSnap), n.parent && n.position.sub(Ks.setFromMatrixPosition(n.parent.matrixWorld))));
      else if (i === "scale") {
        if (t.search("XYZ") !== -1) {
          let o = this.pointEnd.length() / this.pointStart.length();
          this.pointEnd.dot(this.pointStart) < 0 && (o *= -1), ic.set(o, o, o);
        } else
          Ks.copy(this.pointStart), ic.copy(this.pointEnd), Ks.applyQuaternion(this._worldQuaternionInv), ic.applyQuaternion(this._worldQuaternionInv), ic.divide(Ks), t.search("X") === -1 && (ic.x = 1), t.search("Y") === -1 && (ic.y = 1), t.search("Z") === -1 && (ic.z = 1);
        n.scale.copy(this._scaleStart).multiply(ic), this.scaleSnap && (t.search("X") !== -1 && (n.scale.x = Math.round(n.scale.x / this.scaleSnap) * this.scaleSnap || this.scaleSnap), t.search("Y") !== -1 && (n.scale.y = Math.round(n.scale.y / this.scaleSnap) * this.scaleSnap || this.scaleSnap), t.search("Z") !== -1 && (n.scale.z = Math.round(n.scale.z / this.scaleSnap) * this.scaleSnap || this.scaleSnap));
      } else if (i === "rotate") {
        this._offset.copy(this.pointEnd).sub(this.pointStart);
        const o = 20 / this.worldPosition.distanceTo(Ks.setFromMatrixPosition(this.camera.matrixWorld));
        let l = !1;
        t === "XYZE" ? (this.rotationAxis.copy(this._offset).cross(this.eye).normalize(), this.rotationAngle = this._offset.dot(Ks.copy(this.rotationAxis).cross(this.eye)) * o) : (t === "X" || t === "Y" || t === "Z") && (this.rotationAxis.copy(gD[t]), Ks.copy(gD[t]), r === "local" && Ks.applyQuaternion(this.worldQuaternion), Ks.cross(this.eye), Ks.length() === 0 ? l = !0 : this.rotationAngle = this._offset.dot(Ks.normalize()) * o), (t === "E" || l) && (this.rotationAxis.copy(this.eye), this.rotationAngle = this.pointEnd.angleTo(this.pointStart), this._startNorm.copy(this.pointStart).normalize(), this._endNorm.copy(this.pointEnd).normalize(), this.rotationAngle *= this._endNorm.cross(this._startNorm).dot(this.eye) < 0 ? 1 : -1), this.rotationSnap && (this.rotationAngle = Math.round(this.rotationAngle / this.rotationSnap) * this.rotationSnap), r === "local" && t !== "E" && t !== "XYZE" ? (n.quaternion.copy(this._quaternionStart), n.quaternion.multiply(Ln.setFromAxisAngle(this.rotationAxis, this.rotationAngle)).normalize()) : (this.rotationAxis.applyQuaternion(this._parentQuaternionInv), n.quaternion.copy(Ln.setFromAxisAngle(this.rotationAxis, this.rotationAngle)), n.quaternion.multiply(this._quaternionStart).normalize());
      }
      this.dispatchEvent(w6), this.dispatchEvent(xD);
    }
  }
  pointerUp(e) {
    e !== null && e.button !== 0 || (this.dragging && this.axis !== null && (yD.mode = this.mode, this.dispatchEvent(yD)), this.dragging = !1, this.axis = null);
  }
  dispose() {
    this.domElement.removeEventListener("pointerdown", this._onPointerDown), this.domElement.removeEventListener("pointermove", this._onPointerHover), this.domElement.removeEventListener("pointermove", this._onPointerMove), this.domElement.removeEventListener("pointerup", this._onPointerUp), this.traverse(function(e) {
      e.geometry && e.geometry.dispose(), e.material && e.material.dispose();
    });
  }
  // Set current object
  attach(e) {
    return this.object = e, this.visible = !0, this;
  }
  // Detach from object
  detach() {
    return this.object = void 0, this.visible = !1, this.axis = null, this;
  }
  reset() {
    this.enabled && this.dragging && (this.object.position.copy(this._positionStart), this.object.quaternion.copy(this._quaternionStart), this.object.scale.copy(this._scaleStart), this.dispatchEvent(w6), this.dispatchEvent(xD), this.pointStart.copy(this.pointEnd));
  }
  getRaycaster() {
    return Wh;
  }
  // TODO: deprecate
  getMode() {
    return this.mode;
  }
  setMode(e) {
    this.mode = e;
  }
  setTranslationSnap(e) {
    this.translationSnap = e;
  }
  setRotationSnap(e) {
    this.rotationSnap = e;
  }
  setScaleSnap(e) {
    this.scaleSnap = e;
  }
  setSize(e) {
    this.size = e;
  }
  setSpace(e) {
    this.space = e;
  }
}
function S8e(s) {
  if (this.domElement.ownerDocument.pointerLockElement)
    return {
      x: 0,
      y: 0,
      button: s.button
    };
  {
    const e = this.domElement.getBoundingClientRect();
    return {
      x: (s.clientX - e.left) / e.width * 2 - 1,
      y: -(s.clientY - e.top) / e.height * 2 + 1,
      button: s.button
    };
  }
}
function w8e(s) {
  if (this.enabled)
    switch (s.pointerType) {
      case "mouse":
      case "pen":
        this.pointerHover(this._getPointer(s));
        break;
    }
}
function E8e(s) {
  this.enabled && (document.pointerLockElement || this.domElement.setPointerCapture(s.pointerId), this.domElement.addEventListener("pointermove", this._onPointerMove), this.pointerHover(this._getPointer(s)), this.pointerDown(this._getPointer(s)));
}
function T8e(s) {
  this.enabled && this.pointerMove(this._getPointer(s));
}
function C8e(s) {
  this.enabled && (this.domElement.releasePointerCapture(s.pointerId), this.domElement.removeEventListener("pointermove", this._onPointerMove), this.pointerUp(this._getPointer(s)));
}
function E6(s, e, t) {
  const i = e.intersectObject(s, !0);
  for (let n = 0; n < i.length; n++)
    if (i[n].object.visible || t)
      return i[n];
  return !1;
}
const sy = new Mi(), gn = new _(0, 1, 0), AD = new _(0, 0, 0), bD = new et(), ry = new St(), f9 = new St(), Ro = new _(), MD = new et(), p1 = new _(1, 0, 0), iu = new _(0, 1, 0), m1 = new _(0, 0, 1), ay = new _(), Qm = new _(), $m = new _();
class _8e extends mt {
  constructor() {
    super(), this.isTransformControlsGizmo = !0, this.type = "TransformControlsGizmo";
    const e = new kt({
      depthTest: !1,
      depthWrite: !1,
      fog: !1,
      toneMapped: !1,
      transparent: !0
    }), t = new Bi({
      depthTest: !1,
      depthWrite: !1,
      fog: !1,
      toneMapped: !1,
      transparent: !0
    }), i = e.clone();
    i.opacity = 0.15;
    const n = t.clone();
    n.opacity = 0.5;
    const r = e.clone();
    r.color.setHex(16711680);
    const a = e.clone();
    a.color.setHex(65280);
    const o = e.clone();
    o.color.setHex(255);
    const l = e.clone();
    l.color.setHex(16711680), l.opacity = 0.5;
    const c = e.clone();
    c.color.setHex(65280), c.opacity = 0.5;
    const h = e.clone();
    h.color.setHex(255), h.opacity = 0.5;
    const u = e.clone();
    u.opacity = 0.25;
    const d = e.clone();
    d.color.setHex(16776960), d.opacity = 0.25, e.clone().color.setHex(16776960);
    const p = e.clone();
    p.color.setHex(7895160);
    const v = new Ei(0, 0.04, 0.1, 12);
    v.translate(0, 0.05, 0);
    const g = new oi(0.08, 0.08, 0.08);
    g.translate(0, 0.04, 0);
    const m = new Pt();
    m.setAttribute("position", new gt([0, 0, 0, 1, 0, 0], 3));
    const x = new Ei(75e-4, 75e-4, 0.5, 3);
    x.translate(0, 0.25, 0);
    function y(H, U) {
      const W = new nr(H, 75e-4, 3, 64, U * Math.PI * 2);
      return W.rotateY(Math.PI / 2), W.rotateX(Math.PI / 2), W;
    }
    function A() {
      const H = new Pt();
      return H.setAttribute("position", new gt([0, 0, 0, 1, 1, 1], 3)), H;
    }
    const b = {
      X: [
        [new Re(v, r), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(v, r), [-0.5, 0, 0], [0, 0, Math.PI / 2]],
        [new Re(x, r), [0, 0, 0], [0, 0, -Math.PI / 2]]
      ],
      Y: [
        [new Re(v, a), [0, 0.5, 0]],
        [new Re(v, a), [0, -0.5, 0], [Math.PI, 0, 0]],
        [new Re(x, a)]
      ],
      Z: [
        [new Re(v, o), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
        [new Re(v, o), [0, 0, -0.5], [-Math.PI / 2, 0, 0]],
        [new Re(x, o), null, [Math.PI / 2, 0, 0]]
      ],
      XYZ: [
        [new Re(new Nr(0.1, 0), u.clone()), [0, 0, 0]]
      ],
      XY: [
        [new Re(new oi(0.15, 0.15, 0.01), h.clone()), [0.15, 0.15, 0]]
      ],
      YZ: [
        [new Re(new oi(0.15, 0.15, 0.01), l.clone()), [0, 0.15, 0.15], [0, Math.PI / 2, 0]]
      ],
      XZ: [
        [new Re(new oi(0.15, 0.15, 0.01), c.clone()), [0.15, 0, 0.15], [-Math.PI / 2, 0, 0]]
      ]
    }, M = {
      X: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0.3, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [-0.3, 0, 0], [0, 0, Math.PI / 2]]
      ],
      Y: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0.3, 0]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, -0.3, 0], [0, 0, Math.PI]]
      ],
      Z: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0, 0.3], [Math.PI / 2, 0, 0]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0, -0.3], [-Math.PI / 2, 0, 0]]
      ],
      XYZ: [
        [new Re(new Nr(0.2, 0), i)]
      ],
      XY: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0.15, 0]]
      ],
      YZ: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0, 0.15, 0.15], [0, Math.PI / 2, 0]]
      ],
      XZ: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0, 0.15], [-Math.PI / 2, 0, 0]]
      ]
    }, w = {
      START: [
        [new Re(new Nr(0.01, 2), n), null, null, null, "helper"]
      ],
      END: [
        [new Re(new Nr(0.01, 2), n), null, null, null, "helper"]
      ],
      DELTA: [
        [new ti(A(), n), null, null, null, "helper"]
      ],
      X: [
        [new ti(m, n.clone()), [-1e3, 0, 0], null, [1e6, 1, 1], "helper"]
      ],
      Y: [
        [new ti(m, n.clone()), [0, -1e3, 0], [0, 0, Math.PI / 2], [1e6, 1, 1], "helper"]
      ],
      Z: [
        [new ti(m, n.clone()), [0, 0, -1e3], [0, -Math.PI / 2, 0], [1e6, 1, 1], "helper"]
      ]
    }, C = {
      XYZE: [
        [new Re(y(0.5, 1), p), null, [0, Math.PI / 2, 0]]
      ],
      X: [
        [new Re(y(0.5, 0.5), r)]
      ],
      Y: [
        [new Re(y(0.5, 0.5), a), null, [0, 0, -Math.PI / 2]]
      ],
      Z: [
        [new Re(y(0.5, 0.5), o), null, [0, Math.PI / 2, 0]]
      ],
      E: [
        [new Re(y(0.75, 1), d), null, [0, Math.PI / 2, 0]]
      ]
    }, T = {
      AXIS: [
        [new ti(m, n.clone()), [-1e3, 0, 0], null, [1e6, 1, 1], "helper"]
      ]
    }, E = {
      XYZE: [
        [new Re(new Hs(0.25, 10, 8), i)]
      ],
      X: [
        [new Re(new nr(0.5, 0.1, 4, 24), i), [0, 0, 0], [0, -Math.PI / 2, -Math.PI / 2]]
      ],
      Y: [
        [new Re(new nr(0.5, 0.1, 4, 24), i), [0, 0, 0], [Math.PI / 2, 0, 0]]
      ],
      Z: [
        [new Re(new nr(0.5, 0.1, 4, 24), i), [0, 0, 0], [0, 0, -Math.PI / 2]]
      ],
      E: [
        [new Re(new nr(0.75, 0.1, 2, 24), i)]
      ]
    }, P = {
      X: [
        [new Re(g, r), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(x, r), [0, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(g, r), [-0.5, 0, 0], [0, 0, Math.PI / 2]]
      ],
      Y: [
        [new Re(g, a), [0, 0.5, 0]],
        [new Re(x, a)],
        [new Re(g, a), [0, -0.5, 0], [0, 0, Math.PI]]
      ],
      Z: [
        [new Re(g, o), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
        [new Re(x, o), [0, 0, 0], [Math.PI / 2, 0, 0]],
        [new Re(g, o), [0, 0, -0.5], [-Math.PI / 2, 0, 0]]
      ],
      XY: [
        [new Re(new oi(0.15, 0.15, 0.01), h), [0.15, 0.15, 0]]
      ],
      YZ: [
        [new Re(new oi(0.15, 0.15, 0.01), l), [0, 0.15, 0.15], [0, Math.PI / 2, 0]]
      ],
      XZ: [
        [new Re(new oi(0.15, 0.15, 0.01), c), [0.15, 0, 0.15], [-Math.PI / 2, 0, 0]]
      ],
      XYZ: [
        [new Re(new oi(0.1, 0.1, 0.1), u.clone())]
      ]
    }, L = {
      X: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0.3, 0, 0], [0, 0, -Math.PI / 2]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [-0.3, 0, 0], [0, 0, Math.PI / 2]]
      ],
      Y: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0.3, 0]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, -0.3, 0], [0, 0, Math.PI]]
      ],
      Z: [
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0, 0.3], [Math.PI / 2, 0, 0]],
        [new Re(new Ei(0.2, 0, 0.6, 4), i), [0, 0, -0.3], [-Math.PI / 2, 0, 0]]
      ],
      XY: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0.15, 0]]
      ],
      YZ: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0, 0.15, 0.15], [0, Math.PI / 2, 0]]
      ],
      XZ: [
        [new Re(new oi(0.2, 0.2, 0.01), i), [0.15, 0, 0.15], [-Math.PI / 2, 0, 0]]
      ],
      XYZ: [
        [new Re(new oi(0.2, 0.2, 0.2), i), [0, 0, 0]]
      ]
    }, I = {
      X: [
        [new ti(m, n.clone()), [-1e3, 0, 0], null, [1e6, 1, 1], "helper"]
      ],
      Y: [
        [new ti(m, n.clone()), [0, -1e3, 0], [0, 0, Math.PI / 2], [1e6, 1, 1], "helper"]
      ],
      Z: [
        [new ti(m, n.clone()), [0, 0, -1e3], [0, -Math.PI / 2, 0], [1e6, 1, 1], "helper"]
      ]
    };
    function N(H) {
      const U = new mt();
      for (const W in H)
        for (let X = H[W].length; X--; ) {
          const K = H[W][X][0].clone(), te = H[W][X][1], re = H[W][X][2], be = H[W][X][3], Pe = H[W][X][4];
          K.name = W, K.tag = Pe, te && K.position.set(te[0], te[1], te[2]), re && K.rotation.set(re[0], re[1], re[2]), be && K.scale.set(be[0], be[1], be[2]), K.updateMatrix();
          const J = K.geometry.clone();
          J.applyMatrix4(K.matrix), K.geometry = J, K.renderOrder = 1 / 0, K.position.set(0, 0, 0), K.rotation.set(0, 0, 0), K.scale.set(1, 1, 1), U.add(K);
        }
      return U;
    }
    this.gizmo = {}, this.picker = {}, this.helper = {}, this.add(this.gizmo.translate = N(b)), this.add(this.gizmo.rotate = N(C)), this.add(this.gizmo.scale = N(P)), this.add(this.picker.translate = N(M)), this.add(this.picker.rotate = N(E)), this.add(this.picker.scale = N(L)), this.add(this.helper.translate = N(w)), this.add(this.helper.rotate = N(T)), this.add(this.helper.scale = N(I)), this.picker.translate.visible = !1, this.picker.rotate.visible = !1, this.picker.scale.visible = !1;
  }
  // updateMatrixWorld will update transformations and appearance of individual handles
  updateMatrixWorld(e) {
    const i = (this.mode === "scale" ? "local" : this.space) === "local" ? this.worldQuaternion : f9;
    this.gizmo.translate.visible = this.mode === "translate", this.gizmo.rotate.visible = this.mode === "rotate", this.gizmo.scale.visible = this.mode === "scale", this.helper.translate.visible = this.mode === "translate", this.helper.rotate.visible = this.mode === "rotate", this.helper.scale.visible = this.mode === "scale";
    let n = [];
    n = n.concat(this.picker[this.mode].children), n = n.concat(this.gizmo[this.mode].children), n = n.concat(this.helper[this.mode].children);
    for (let r = 0; r < n.length; r++) {
      const a = n[r];
      a.visible = !0, a.rotation.set(0, 0, 0), a.position.copy(this.worldPosition);
      let o;
      if (this.camera.isOrthographicCamera ? o = (this.camera.top - this.camera.bottom) / this.camera.zoom : o = this.worldPosition.distanceTo(this.cameraPosition) * Math.min(1.9 * Math.tan(Math.PI * this.camera.fov / 360) / this.camera.zoom, 7), a.scale.set(1, 1, 1).multiplyScalar(o * this.size / 4), a.tag === "helper") {
        a.visible = !1, a.name === "AXIS" ? (a.visible = !!this.axis, this.axis === "X" && (Ln.setFromEuler(sy.set(0, 0, 0)), a.quaternion.copy(i).multiply(Ln), Math.abs(gn.copy(p1).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "Y" && (Ln.setFromEuler(sy.set(0, 0, Math.PI / 2)), a.quaternion.copy(i).multiply(Ln), Math.abs(gn.copy(iu).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "Z" && (Ln.setFromEuler(sy.set(0, Math.PI / 2, 0)), a.quaternion.copy(i).multiply(Ln), Math.abs(gn.copy(m1).applyQuaternion(i).dot(this.eye)) > 0.9 && (a.visible = !1)), this.axis === "XYZE" && (Ln.setFromEuler(sy.set(0, Math.PI / 2, 0)), gn.copy(this.rotationAxis), a.quaternion.setFromRotationMatrix(bD.lookAt(AD, gn, iu)), a.quaternion.multiply(Ln), a.visible = this.dragging), this.axis === "E" && (a.visible = !1)) : a.name === "START" ? (a.position.copy(this.worldPositionStart), a.visible = this.dragging) : a.name === "END" ? (a.position.copy(this.worldPosition), a.visible = this.dragging) : a.name === "DELTA" ? (a.position.copy(this.worldPositionStart), a.quaternion.copy(this.worldQuaternionStart), Ks.set(1e-10, 1e-10, 1e-10).add(this.worldPositionStart).sub(this.worldPosition).multiplyScalar(-1), Ks.applyQuaternion(this.worldQuaternionStart.clone().invert()), a.scale.copy(Ks), a.visible = this.dragging) : (a.quaternion.copy(i), this.dragging ? a.position.copy(this.worldPositionStart) : a.position.copy(this.worldPosition), this.axis && (a.visible = this.axis.search(a.name) !== -1));
        continue;
      }
      a.quaternion.copy(i), this.mode === "translate" || this.mode === "scale" ? (a.name === "X" && Math.abs(gn.copy(p1).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "Y" && Math.abs(gn.copy(iu).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "Z" && Math.abs(gn.copy(m1).applyQuaternion(i).dot(this.eye)) > 0.99 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "XY" && Math.abs(gn.copy(m1).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "YZ" && Math.abs(gn.copy(p1).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1), a.name === "XZ" && Math.abs(gn.copy(iu).applyQuaternion(i).dot(this.eye)) < 0.2 && (a.scale.set(1e-10, 1e-10, 1e-10), a.visible = !1)) : this.mode === "rotate" && (ry.copy(i), gn.copy(this.eye).applyQuaternion(Ln.copy(i).invert()), a.name.search("E") !== -1 && a.quaternion.setFromRotationMatrix(bD.lookAt(this.eye, AD, iu)), a.name === "X" && (Ln.setFromAxisAngle(p1, Math.atan2(-gn.y, gn.z)), Ln.multiplyQuaternions(ry, Ln), a.quaternion.copy(Ln)), a.name === "Y" && (Ln.setFromAxisAngle(iu, Math.atan2(gn.x, gn.z)), Ln.multiplyQuaternions(ry, Ln), a.quaternion.copy(Ln)), a.name === "Z" && (Ln.setFromAxisAngle(m1, Math.atan2(gn.y, gn.x)), Ln.multiplyQuaternions(ry, Ln), a.quaternion.copy(Ln))), a.visible = a.visible && (a.name.indexOf("X") === -1 || this.showX), a.visible = a.visible && (a.name.indexOf("Y") === -1 || this.showY), a.visible = a.visible && (a.name.indexOf("Z") === -1 || this.showZ), a.visible = a.visible && (a.name.indexOf("E") === -1 || this.showX && this.showY && this.showZ), a.material._color = a.material._color || a.material.color.clone(), a.material._opacity = a.material._opacity || a.material.opacity, a.material.color.copy(a.material._color), a.material.opacity = a.material._opacity, this.enabled && this.axis && (a.name === this.axis || this.axis.split("").some(function(l) {
        return a.name === l;
      })) && (a.material.color.setHex(16776960), a.material.opacity = 1);
    }
    super.updateMatrixWorld(e);
  }
}
class P8e extends Re {
  constructor() {
    super(
      new $n(1e5, 1e5, 2, 2),
      new kt({ visible: !1, wireframe: !0, side: ui, transparent: !0, opacity: 0.1, toneMapped: !1 })
    ), this.isTransformControlsPlane = !0, this.type = "TransformControlsPlane";
  }
  updateMatrixWorld(e) {
    let t = this.space;
    switch (this.position.copy(this.worldPosition), this.mode === "scale" && (t = "local"), ay.copy(p1).applyQuaternion(t === "local" ? this.worldQuaternion : f9), Qm.copy(iu).applyQuaternion(t === "local" ? this.worldQuaternion : f9), $m.copy(m1).applyQuaternion(t === "local" ? this.worldQuaternion : f9), gn.copy(Qm), this.mode) {
      case "translate":
      case "scale":
        switch (this.axis) {
          case "X":
            gn.copy(this.eye).cross(ay), Ro.copy(ay).cross(gn);
            break;
          case "Y":
            gn.copy(this.eye).cross(Qm), Ro.copy(Qm).cross(gn);
            break;
          case "Z":
            gn.copy(this.eye).cross($m), Ro.copy($m).cross(gn);
            break;
          case "XY":
            Ro.copy($m);
            break;
          case "YZ":
            Ro.copy(ay);
            break;
          case "XZ":
            gn.copy($m), Ro.copy(Qm);
            break;
          case "XYZ":
          case "E":
            Ro.set(0, 0, 0);
            break;
        }
        break;
      case "rotate":
      default:
        Ro.set(0, 0, 0);
    }
    Ro.length() === 0 ? this.quaternion.copy(this.cameraQuaternion) : (MD.lookAt(Ks.set(0, 0, 0), Ro, gn), this.quaternion.setFromRotationMatrix(MD)), super.updateMatrixWorld(e);
  }
}
const af = typeof window < "u" && window.THREE ? window.THREE : {
  LinearFilter: ii,
  Sprite: wn,
  SpriteMaterial: ar,
  SRGBColorSpace: qi,
  Texture: Ut
};
class R8e extends af.Sprite {
  constructor(e = "", t = 10, i = "rgba(255, 255, 255, 1)") {
    super(new af.SpriteMaterial()), this._text = `${e}`, this._textHeight = t, this._color = i, this._backgroundColor = !1, this._padding = 0, this._borderWidth = 0, this._borderRadius = 0, this._borderColor = "white", this._strokeWidth = 0, this._strokeColor = "white", this._fontFace = "system-ui", this._fontSize = 90, this._fontWeight = "normal", this._canvas = document.createElement("canvas"), this._genCanvas();
  }
  get text() {
    return this._text;
  }
  set text(e) {
    this._text = e, this._genCanvas();
  }
  get textHeight() {
    return this._textHeight;
  }
  set textHeight(e) {
    this._textHeight = e, this._genCanvas();
  }
  get color() {
    return this._color;
  }
  set color(e) {
    this._color = e, this._genCanvas();
  }
  get backgroundColor() {
    return this._backgroundColor;
  }
  set backgroundColor(e) {
    this._backgroundColor = e, this._genCanvas();
  }
  get padding() {
    return this._padding;
  }
  set padding(e) {
    this._padding = e, this._genCanvas();
  }
  get borderWidth() {
    return this._borderWidth;
  }
  set borderWidth(e) {
    this._borderWidth = e, this._genCanvas();
  }
  get borderRadius() {
    return this._borderRadius;
  }
  set borderRadius(e) {
    this._borderRadius = e, this._genCanvas();
  }
  get borderColor() {
    return this._borderColor;
  }
  set borderColor(e) {
    this._borderColor = e, this._genCanvas();
  }
  get fontFace() {
    return this._fontFace;
  }
  set fontFace(e) {
    this._fontFace = e, this._genCanvas();
  }
  get fontSize() {
    return this._fontSize;
  }
  set fontSize(e) {
    this._fontSize = e, this._genCanvas();
  }
  get fontWeight() {
    return this._fontWeight;
  }
  set fontWeight(e) {
    this._fontWeight = e, this._genCanvas();
  }
  get strokeWidth() {
    return this._strokeWidth;
  }
  set strokeWidth(e) {
    this._strokeWidth = e, this._genCanvas();
  }
  get strokeColor() {
    return this._strokeColor;
  }
  set strokeColor(e) {
    this._strokeColor = e, this._genCanvas();
  }
  _genCanvas() {
    const e = this._canvas, t = e.getContext("2d"), i = Array.isArray(this.borderWidth) ? this.borderWidth : [this.borderWidth, this.borderWidth], n = i.map((g) => g * this.fontSize * 0.1), a = (Array.isArray(this.borderRadius) ? this.borderRadius : [this.borderRadius, this.borderRadius, this.borderRadius, this.borderRadius]).map((g) => g * this.fontSize * 0.1), o = Array.isArray(this.padding) ? this.padding : [this.padding, this.padding], l = o.map((g) => g * this.fontSize * 0.1), c = this.text.split(`
`), h = `${this.fontWeight} ${this.fontSize}px ${this.fontFace}`;
    t.font = h;
    const u = Math.max(...c.map((g) => t.measureText(g).width)), d = this.fontSize * c.length;
    if (e.width = u + n[0] * 2 + l[0] * 2, e.height = d + n[1] * 2 + l[1] * 2, this.borderWidth) {
      if (t.strokeStyle = this.borderColor, n[0]) {
        const g = n[0] / 2;
        t.lineWidth = n[0], t.beginPath(), t.moveTo(g, a[0]), t.lineTo(g, e.height - a[3]), t.moveTo(e.width - g, a[1]), t.lineTo(e.width - g, e.height - a[2]), t.stroke();
      }
      if (n[1]) {
        const g = n[1] / 2;
        t.lineWidth = n[1], t.beginPath(), t.moveTo(Math.max(n[0], a[0]), g), t.lineTo(e.width - Math.max(n[0], a[1]), g), t.moveTo(Math.max(n[0], a[3]), e.height - g), t.lineTo(e.width - Math.max(n[0], a[2]), e.height - g), t.stroke();
      }
      if (this.borderRadius) {
        const g = Math.max(...n), m = g / 2;
        t.lineWidth = g, t.beginPath(), [
          !!a[0] && [a[0], m, m, a[0]],
          !!a[1] && [
            e.width - a[1],
            e.width - m,
            m,
            a[1]
          ],
          !!a[2] && [
            e.width - a[2],
            e.width - m,
            e.height - m,
            e.height - a[2]
          ],
          !!a[3] && [
            a[3],
            m,
            e.height - m,
            e.height - a[3]
          ]
        ].filter((x) => x).forEach(([x, y, A, b]) => {
          t.moveTo(x, A), t.quadraticCurveTo(y, A, y, b);
        }), t.stroke();
      }
    }
    this.backgroundColor && (t.fillStyle = this.backgroundColor, this.borderRadius ? (t.beginPath(), t.moveTo(n[0], a[0]), [
      [
        n[0],
        a[0],
        e.width - a[1],
        n[1],
        n[1],
        n[1]
      ],
      // t
      [
        e.width - n[0],
        e.width - n[0],
        e.width - n[0],
        n[1],
        a[1],
        e.height - a[2]
      ],
      // r
      [
        e.width - n[0],
        e.width - a[2],
        a[3],
        e.height - n[1],
        e.height - n[1],
        e.height - n[1]
      ],
      // b
      [
        n[0],
        n[0],
        n[0],
        e.height - n[1],
        e.height - a[3],
        a[0]
      ]
      // t
    ].forEach(([g, m, x, y, A, b]) => {
      t.quadraticCurveTo(g, y, m, A), t.lineTo(x, b);
    }), t.closePath(), t.fill()) : t.fillRect(
      n[0],
      n[1],
      e.width - n[0] * 2,
      e.height - n[1] * 2
    )), t.translate(...n), t.translate(...l), t.font = h, t.fillStyle = this.color, t.textBaseline = "bottom";
    const f = this.strokeWidth > 0;
    f && (t.lineWidth = this.strokeWidth * this.fontSize / 10, t.strokeStyle = this.strokeColor), c.forEach((g, m) => {
      const x = (u - t.measureText(g).width) / 2, y = (m + 1) * this.fontSize;
      f && t.strokeText(g, x, y), t.fillText(g, x, y);
    }), this.material.map && this.material.map.dispose();
    const p = this.material.map = new af.Texture(e);
    p.minFilter = af.LinearFilter, p.colorSpace = af.SRGBColorSpace, p.needsUpdate = !0;
    const v = this.textHeight * c.length + i[1] * 2 + o[1] * 2;
    this.scale.set(v * e.width / e.height, v, 0);
  }
  clone() {
    return new this.constructor(this.text, this.textHeight, this.color).copy(this);
  }
  copy(e) {
    return af.Sprite.prototype.copy.call(this, e), this.color = e.color, this.backgroundColor = e.backgroundColor, this.padding = e.padding, this.borderWidth = e.borderWidth, this.borderColor = e.borderColor, this.fontFace = e.fontFace, this.fontSize = e.fontSize, this.fontWeight = e.fontWeight, this.strokeWidth = e.strokeWidth, this.strokeColor = e.strokeColor, this;
  }
}
function g1(s, e) {
  const t = [];
  if (e.type === "fixCount") {
    const { count: i } = e;
    for (let n = 0; n < i; ++n) {
      const r = n / (i - 1);
      t.push(s.getMatrixAt(r));
    }
  } else if (e.type === "fixInterval") {
    const i = e.interval, n = s.getLength();
    let r = 0;
    for (let a = 0; a <= n; a += i, r++) {
      const o = a / n;
      t.push(s.getMatrixAt(o));
    }
  }
  return t;
}
const h4 = class h4 extends ra {
  constructor(t) {
    super();
    S(this, "_controlPointsMaterial");
    S(this, "_controlPointsSpriteGroup");
    S(this, "_anchorMaterial");
    S(this, "_anchorsSpriteGroup");
    S(this, "_controlPoints", []);
    S(this, "_floatingPoint", new _());
    S(this, "_linesMesh");
    S(this, "_textGroup");
    S(this, "_instancedMesh");
    S(this, "_transformControls");
    S(this, "_root");
    S(this, "_drawGroup");
    S(this, "_world");
    S(this, "_mode");
    S(this, "_mouseMoved", !1);
    // 射线拾取
    S(this, "_rayCaster", new lr());
    // 水平面
    S(this, "_ground", new ln(new _(0, 1, 0)));
    this._controlPointsMaterial = new ar(), this._controlPointsMaterial.color = new _e(16711680), this._controlPointsMaterial.depthTest = !1, this._controlPointsSpriteGroup = new ci(), this._anchorMaterial = new ar(), this._anchorMaterial.depthTest = !1, this._anchorsSpriteGroup = new ci(), this._linesMesh = new ti(), this._linesMesh.frustumCulled = !1, this._textGroup = new ci(), this._textGroup.name = "textGroup", this._instancedMesh = this._updateInstancedMeshSize(new _(10, 10, 10)), this._instancedMesh.frustumCulled = !1;
    const { threeRenderer: i, mainCamera: n } = t.renderer;
    this._transformControls = new M8e(n, i.domElement), this._drawGroup = new ci(), this._drawGroup.add(this._controlPointsSpriteGroup), this._drawGroup.add(this._anchorsSpriteGroup), this._drawGroup.add(this._linesMesh), this._drawGroup.add(this._textGroup), this._drawGroup.add(this._instancedMesh), this._root = new ci(), this._root.name = "BatchModelDrawHelper#root", this._root.add(this._transformControls), this._root.add(this._drawGroup), this._world = t, this._mouseDownHandler = this._mouseDownHandler.bind(this), this._mouseUpHandler = this._mouseUpHandler.bind(this), this._mouseMoveHandler = this._mouseMoveHandler.bind(this), this._keyboardUpHandler = this._keyboardUpHandler.bind(this), this._mouseDownHandlerForEdit = this._mouseDownHandlerForEdit.bind(this), this._mouseUpHandlerForEdit = this._mouseUpHandlerForEdit.bind(this), this._mouseMoveHandlerForEdit = this._mouseMoveHandlerForEdit.bind(this), this._keyboardUpHandlerForEdit = this._keyboardUpHandlerForEdit.bind(this);
  }
  updateInstancedMeshSizeByObject(t) {
    const i = new zt();
    i.setFromObject(t);
    const n = new _();
    i.getSize(n), this._updateInstancedMeshSize(n);
  }
  startDraw(t) {
    this._clearDraw(), this._removeAllListeners(), this._initializeDraw(), this._drawGroup.position.copy(t.group.position), this._drawGroup.rotation.copy(t.group.rotation), this._drawGroup.scale.copy(t.group.scale);
    const i = t.states.getCurrentStateConfig(), { mode: n, points: r } = i;
    this._controlPoints = r.map((c) => new _(c.x, c.y, c.z)), this._mode = { ...n };
    const a = () => {
      this.removeEventListener("endDraw", a), this.endDraw(t);
    };
    this.addEventListener("endDraw", a);
    const { threeRenderer: o } = this._world.renderer, l = o.domElement;
    l.addEventListener("mousedown", this._mouseDownHandler), l.addEventListener("mouseup", this._mouseUpHandler), l.addEventListener("mousemove", this._mouseMoveHandler), document.addEventListener("keyup", this._keyboardUpHandler);
  }
  endDraw(t) {
    this._updateComponentState(t), this._clearDraw(), this._removeAllListeners(), this._listeners = void 0;
  }
  startEdit(t) {
    this._clearDraw(), this._removeAllListeners(), this._initializeDraw(), this._drawGroup.position.copy(t.group.position), this._drawGroup.rotation.copy(t.group.rotation), this._drawGroup.scale.copy(t.group.scale);
    const i = t.states.getCurrentStateConfig();
    t.group.visible = !1;
    const { points: n, mode: r, closed: a } = i;
    this._mode = { ...r }, this._controlPoints = n.map((h) => new _(h.x, h.y, h.z)), this._controlPoints.length < 2 && (this._controlPoints.push(new _(0, 0, 0)), this._controlPoints.push(new _(10, 0, 0))), this._updateControlPointsSprite(this._controlPoints), this._updateAnchorSpriteGroup(this._controlPoints), this.updateInstancedMeshSizeByObject(t.model), this.update(), this._instancedMesh.visible = !0;
    const { threeRenderer: o, cameraControls: l } = this._world.renderer;
    this._transformControls.addEventListener("dragging-changed", (h) => {
      l.enabled = !h.value;
    }), this._transformControls.addEventListener("change", (h) => {
      this._controlPoints = this._controlPointsSpriteGroup.children.map((u) => u.position.clone()), this.update(), this._updateAnchorSpriteGroup(this._controlPoints);
    });
    const c = o.domElement;
    c.addEventListener("mousedown", this._mouseDownHandlerForEdit), c.addEventListener("mousemove", this._mouseMoveHandlerForEdit), c.addEventListener("mouseup", this._mouseUpHandlerForEdit), document.addEventListener("keyup", this._keyboardUpHandlerForEdit);
  }
  endEdit(t) {
    this._updateComponentState(t), this._clearDraw(), this._removeAllListeners(), this._listeners = void 0, this._transformControls._listeners = void 0;
  }
  update() {
    if (this._updateLinesMesh(this._controlPoints), this._updateTextGroup(this._controlPoints), this._mode !== void 0) {
      const t = new Df(this._controlPoints, !1, "catmullrom", 0), i = g1(t, this._mode);
      this._updateInstanceMeshMatrices(i);
    }
  }
  dispose() {
    this._controlPointsMaterial.dispose(), this._controlPointsSpriteGroup.clear();
    const t = (i) => {
      const { geometry: n, material: r } = i;
      n.dispose(), Array.isArray(r) ? r.forEach((a) => a.dispose()) : r.dispose();
    };
    t(this._linesMesh), t(this._instancedMesh);
  }
  _updateComponentState(t) {
    const { count: i } = this._instancedMesh, n = new et(), r = [];
    for (let a = 0; a < i; ++a) {
      this._instancedMesh.getMatrixAt(a, n);
      const o = Sx(t.model);
      o.matrix.copy(n), o.matrixAutoUpdate = !1, o.matrixWorldNeedsUpdate = !0, r.push(o);
    }
    t.setModels(r), t.setPathPoints(this._controlPoints), t.updateServerData();
  }
  _removeAllListeners() {
    const { threeRenderer: t } = this._world.renderer, i = t.domElement;
    i.removeEventListener("mousedown", this._mouseDownHandler), i.removeEventListener("mouseup", this._mouseUpHandler), i.removeEventListener("mousemove", this._mouseMoveHandler), document.removeEventListener("keyup", this._keyboardUpHandler), i.removeEventListener("mousedown", this._mouseDownHandlerForEdit), i.removeEventListener("mouseup", this._mouseUpHandlerForEdit), i.removeEventListener("mousemove", this._mouseMoveHandlerForEdit), document.removeEventListener("keyup", this._keyboardUpHandlerForEdit);
  }
  _initializeDraw() {
    const { rootScene: t } = this._world.renderer;
    this._updateControlPointsSprite([]), this._updateLinesMesh([]), this._updateTextGroup([]), this._updateInstanceMeshMatrices([]), this._instancedMesh.visible = !1, this._transformControls.detach(), t.add(this._root), this._mouseMoved = !1;
  }
  _updateControlPointsSprite(t) {
    this._controlPointsSpriteGroup.clear(), t.forEach((i) => {
      const n = new wn(this._controlPointsMaterial);
      n.position.copy(i), this._controlPointsSpriteGroup.add(n);
    });
  }
  _updateLinesMesh(t) {
    this._linesMesh.geometry.setFromPoints(t);
  }
  _updateTextGroup(t) {
    this._textGroup.clear();
    for (let i = 0; i <= t.length - 2; ++i) {
      const n = t[i], r = t[i + 1], a = new R8e(), o = new Qs(n, r);
      o.getCenter(a.position);
      const l = this._numberToString(o.distance());
      a.text = `${l}米`, this._textGroup.add(a);
    }
  }
  _updateAnchorSpriteGroup(t) {
    this._anchorsSpriteGroup.clear();
    for (let i = 0; i <= t.length - 2; ++i) {
      const n = t[i], r = t[i + 1], a = new wn(this._anchorMaterial);
      new Qs(n, r).getCenter(a.position), this._anchorsSpriteGroup.add(a);
    }
  }
  _updateInstancedMeshSize(t) {
    if (this._instancedMesh instanceof ho)
      this._instancedMesh.geometry = new oi(t.x, t.y, t.z), this._instancedMesh.geometry.translate(0, t.y * 0.5, 0);
    else {
      const i = new oi(t.x, t.y, t.z);
      i.translate(0, t.y * 0.5, 0);
      const n = new kt({
        color: "#aa00ff",
        transparent: !0,
        opacity: 0.1
      });
      this._instancedMesh = new ho(
        i,
        n,
        h4.MAX_COUNT
      );
    }
    return this._instancedMesh;
  }
  _updateInstanceMeshMatrices(t) {
    this._mode && this._instancedMesh && (this._instancedMesh.count = t.length, t.forEach((i, n) => {
      this._instancedMesh.setMatrixAt(n, i);
    }), this._instancedMesh.instanceMatrix.needsUpdate = !0);
  }
  _pick(t) {
    const { threeRenderer: i, mainCamera: n } = this._world.renderer, r = i.domElement.getBoundingClientRect(), a = 2 * ((t.clientX - r.x) / r.width) - 1, o = -2 * ((t.clientY - r.y) / r.height) + 1;
    return this._rayCaster.setFromCamera(new Se(a, o), n), this._rayCaster.ray.intersectPlane(this._ground, new _());
  }
  _pickObject(t, i) {
    const { threeRenderer: n, mainCamera: r } = this._world.renderer, a = n.domElement.getBoundingClientRect(), o = 2 * (t.clientX - a.left) / a.width - 1, l = -2 * (t.clientY - a.top) / a.height + 1, c = new lr();
    return c.setFromCamera(new Se(o, l), r), c.intersectObjects(i, !1);
  }
  _mouseDownHandler(t) {
    this._mouseMoved = !1;
  }
  _mouseMoveHandler(t) {
    this._mouseMoved = !0;
    const i = this._pick(t);
    if (!i)
      return;
    this._floatingPoint.copy(i);
    const n = [...this._controlPoints, this._floatingPoint];
    if (this._updateControlPointsSprite(n), n.length > 1 && (this._updateLinesMesh(n), this._updateTextGroup(n), this._mode !== void 0)) {
      const r = new Df(n, !1, "catmullrom", 0), a = g1(r, this._mode);
      this._updateInstanceMeshMatrices(a), this._instancedMesh.visible = !0;
    }
  }
  _mouseUpHandler(t) {
    this._mouseMoved || (t.button === Lr.LEFT ? this._mouseLeftClick(t) : t.button === Lr.RIGHT && this._mouseRightClick(t));
  }
  _mouseLeftClick(t) {
    if (t.button !== Lr.LEFT)
      return;
    const i = this._pick(t);
    i && this._controlPoints.push(i);
  }
  _mouseRightClick(t) {
    t.button === Lr.RIGHT && this.dispatchEvent({ type: "endDraw" });
  }
  _keyboardUpHandler(t) {
    t.key === "Escape" && this.dispatchEvent({ type: "endDraw" });
  }
  _mouseDownHandlerForEdit(t) {
    this._mouseMoved = !1;
  }
  _mouseMoveHandlerForEdit(t) {
    this._mouseMoved = !0;
  }
  _mouseUpHandlerForEdit(t) {
    if (!this._mouseMoved) {
      const i = [
        ...this._controlPointsSpriteGroup.children,
        ...this._anchorsSpriteGroup.children
      ], n = this._pickObject(t, i);
      if (this._transformControls.detach(), n.length > 0) {
        const { object: r } = n[0];
        if (r.parent === this._controlPointsSpriteGroup)
          this._transformControls.attach(r);
        else if (r.parent === this._anchorsSpriteGroup) {
          const a = this._anchorsSpriteGroup.children.indexOf(r);
          if (this._controlPoints.splice(a + 1, 0, r.position), this._updateControlPointsSprite(this._controlPoints), this._updateLinesMesh(this._controlPoints), this._updateTextGroup(this._controlPoints), this._mode !== void 0) {
            const o = new Df(this._controlPoints, !1, "catmullrom", 0), l = g1(o, this._mode);
            this._updateInstanceMeshMatrices(l);
          }
          this._updateAnchorSpriteGroup(this._controlPoints);
        }
      }
    }
  }
  _keyboardUpHandlerForEdit(t) {
    if (t.key === "Backspace" || t.key === "Delete") {
      const { object: i } = this._transformControls;
      if (i && i.parent === this._controlPointsSpriteGroup) {
        if (this._controlPoints.length <= 2)
          return;
        if (this._controlPointsSpriteGroup.remove(i), this._controlPoints = this._controlPointsSpriteGroup.children.map((n) => n.position), this._updateControlPointsSprite(this._controlPoints), this._updateLinesMesh(this._controlPoints), this._updateTextGroup(this._controlPoints), this._mode !== void 0) {
          const n = new Df(this._controlPoints, !1, "catmullrom", 0), r = g1(n, this._mode);
          this._updateInstanceMeshMatrices(r);
        }
        this._updateAnchorSpriteGroup(this._controlPoints), this._transformControls.detach();
      }
    }
  }
  _numberToString(t) {
    if (t < 1e-4)
      return t.toString();
    let i = 2;
    return t < 0.01 ? i = 4 : t < 0.1 && (i = 3), t.toFixed(i);
  }
  _clearDraw() {
    const { rootScene: t } = this._world.renderer;
    this._controlPoints = [], this._updateControlPointsSprite([]), this._updateAnchorSpriteGroup([]), this._updateLinesMesh([]), this._updateTextGroup([]), this._updateInstanceMeshMatrices([]), this._instancedMesh.visible = !1, this._drawGroup.position.set(0, 0, 0), this._drawGroup.rotation.set(0, 0, 0), this._drawGroup.scale.set(1, 1, 1), t.remove(this._root);
  }
};
S(h4, "MAX_COUNT", 1e3);
let nM = h4;
var _U = Object.defineProperty, L8e = (s, e, t) => e in s ? _U(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t, I8e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
}, O8e = (s, e, t) => (L8e(s, e + "" , t), t);
let Yg = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "_path", new ci());
    S(this, "_text", new ci());
    S(this, "_models", new ci());
    S(this, "_model", new mt());
    S(this, "_materials", []);
    S(this, "_triangles", 0);
    const n = {
      id: "",
      name: "",
      rank: 0,
      config: {
        transform: {
          position: {
            x: 0,
            y: 0,
            z: 0
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0
          },
          scale: {
            x: 1,
            y: 1,
            z: 1
          }
        },
        constrainedScale: !1,
        castShadow: !1,
        receiveShadow: !1,
        asset: {
          id: "",
          url: ""
        },
        modelTransform: {
          position: {
            x: 0,
            y: 0,
            z: 0
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0
          },
          scale: {
            x: 1,
            y: 1,
            z: 1
          }
        },
        points: [],
        closed: !1,
        showPath: !1,
        mode: {
          type: "fixCount",
          count: 0
        }
      }
    };
    this.addState(n), this._path.name = "path", this._text.name = "text", this.group.add(this._path), this.group.add(this._text), this.group.add(this._models);
  }
  get model() {
    return this._model;
  }
  set model(e) {
    this._model = e;
  }
  get materials() {
    return this._materials;
  }
  get triangles() {
    return this._triangles;
  }
  setModels(e) {
    this._models.clear(), e.forEach((r) => {
      this._models.add(r);
    });
    const t = /* @__PURE__ */ new Set(), i = /* @__PURE__ */ new Set();
    this._models.children.forEach((r) => {
      r.traverse((a) => {
        if (p0(a)) {
          const { material: o } = a;
          Array.isArray(o) ? o.forEach((l) => t.add(l)) : t.add(o), i.add(a);
        }
      });
    }), this._materials = [...t];
    let n = 0;
    [...i].forEach((r) => {
      const { geometry: a } = r;
      if (a.isBufferGeometry)
        if (a.index)
          n += a.index.count / 3;
        else {
          const c = a.getAttribute("position").count / 3;
          n += c;
        }
    }), this._triangles = n;
  }
  getModels() {
    return this._models;
  }
  cloneModel(e) {
    return Sx(e);
  }
  setMode(e) {
    const t = this.states.getCurrentStateConfig();
    t.mode = { ...e };
  }
  setPathPoints(e) {
    const t = this.states.getCurrentStateConfig();
    t.points = [], e.forEach((i) => {
      t.points.push({
        x: i.x,
        y: i.y,
        z: i.z
      });
    });
  }
  getPathPoints() {
    return this.states.getCurrentStateConfig().points;
  }
  set url(e) {
    const t = this.states.getCurrentStateConfig();
    t.asset.url = e;
  }
  update(e) {
    const t = this.states.getCurrentStateConfig(), { position: i, rotation: n, scale: r } = t.transform;
    if (this.group.position.set(i.x, i.y, i.z), this.group.rotation.set(n.x, n.y, n.z), this.group.scale.set(r.x, r.y, r.z), this._path.visible = t.showPath, t.points.length > 0) {
      const { modelTransform: a } = t, { rotation: o, position: l, scale: c } = a ?? {
        position: {
          x: 0,
          y: 0,
          z: 0
        },
        rotation: {
          x: 0,
          y: 0,
          z: 0
        },
        scale: {
          x: 1,
          y: 1,
          z: 1
        }
      }, h = new et(), u = new St();
      u.setFromEuler(new Mi(o.x, o.y, o.z)), h.compose(
        new _(l.x, l.y, l.z),
        u,
        new _(c.x, c.y, c.z)
      );
      const d = t.points.map((g) => new _(g.x, g.y, g.z));
      t.closed && d.push(new _(d[0].x, d[0].y, d[0].z)), this._path.clear();
      const f = new ci();
      d.forEach((g) => {
        const m = new wn(Yg.spriteMaterial);
        m.position.copy(g), f.add(m);
      }), this._path.add(f);
      const p = new ti();
      p.geometry.setFromPoints(d), this._path.add(p);
      const { mode: v } = t;
      if (t.closed) {
        const g = new Df(d, !0, "catmullrom", 0);
        this._createModelsBySpline(v, g, h);
      } else {
        const g = new Df(d, !1, "catmullrom", 0);
        this._createModelsBySpline(v, g, h);
      }
    }
  }
  _createModelsBySpline(e, t, i) {
    const n = this._model;
    if (n !== void 0) {
      const r = [];
      g1(t, e).forEach((o) => {
        const l = Sx(n);
        l.matrix.copy(o.multiply(i)), l.matrixAutoUpdate = !1, l.matrixWorldNeedsUpdate = !0, r.push(l);
      }), this.setModels(r);
    }
  }
  async fromJson(e) {
    const { type: t, states: i, data: n } = e;
    if (t !== "BatchModel")
      return;
    const r = i;
    r && this.states.initStates(r);
    const a = this.states.getCurrentStateConfig(), { points: o, transform: l, asset: c, modelTransform: h } = a;
    if (o) {
      const u = o.map((v) => {
        const g = new _();
        return g.set(v.x, v.y, v.z), g;
      });
      this.setPathPoints(u);
      const d = Yg.spriteMaterial, f = new ci();
      u.forEach((v) => {
        const g = new wn(d);
        g.position.copy(v), f.add(g);
      }), this._path.add(f);
      const p = new ti();
      p.geometry.setFromPoints(u), this._path.add(p), this._path.visible = a.showPath;
    }
    if (l) {
      const { position: u, rotation: d, scale: f } = l;
      this.group.position.set(u.x, u.y, u.z), this.group.rotation.set(d.x, d.y, d.z), this.group.scale.set(f.x, f.y, f.z);
    }
    if (c.url) {
      const u = await st.assetManager.modelManager.loadAndCacheModelByUrl(
        c.url
      ), d = qc.InstantiateModelComponentRenderResources(u);
      this._model = d.scene, this.update(0);
    }
    this.isInit = !0;
  }
  toJson() {
    const e = this.states.toJson();
    return {
      id: this.id,
      type: "BatchModel",
      name: this.name,
      visible: !0,
      inActive: !0,
      version: this.version,
      // attribute: this.customAttributes,
      data: {},
      states: e
    };
  }
  async updateFromJson(e, t) {
    this.fromJson(t);
  }
  getObjectById(e) {
    return this.group;
  }
  updateServerData() {
    this._updateServerData();
  }
  _updateServerData() {
    const e = this.states.getCurrentStateConfig(), t = this.getCurStateId();
    this.updateStateConfig(t, e);
  }
  dispose() {
    this.group.clear(), this._model.clear(), this._models.clear();
  }
  onDragEnd() {
    const e = this.getCurStateId(), t = this.getStateList().find((i) => i.id === e);
    if (t) {
      const i = { ...t };
      i.config.transform.position.x = this.group.position.x, i.config.transform.position.y = this.group.position.y, i.config.transform.position.z = this.group.position.z, i.config.transform.rotation.x = this.group.rotation.x, i.config.transform.rotation.y = this.group.rotation.y, i.config.transform.rotation.z = this.group.rotation.z, i.config.transform.scale.x = this.group.scale.x, i.config.transform.scale.y = this.group.scale.y, i.config.transform.scale.z = this.group.scale.z, this.updateStateConfig(t.id, i.config);
    }
  }
};
O8e(Yg, "spriteMaterial", new ar());
Yg = I8e([
  ni("BatchModel")
], Yg);
class v8 extends Re {
  constructor() {
    const e = v8.SkyShader, t = new Jt({
      name: "SkyShader",
      fragmentShader: e.fragmentShader,
      vertexShader: e.vertexShader,
      uniforms: rs.clone(e.uniforms),
      side: Ps,
      depthWrite: !1
    });
    super(new oi(1, 1, 1), t), this.isSky = !0;
  }
}
v8.SkyShader = {
  uniforms: {
    turbidity: { value: 2 },
    rayleigh: { value: 1 },
    mieCoefficient: { value: 5e-3 },
    mieDirectionalG: { value: 0.8 },
    sunPosition: { value: new _() },
    up: { value: new _(0, 1, 0) },
    uAtmosphereLightDiscCosHalfApexAngle: {
      value: 0.9999547608465449
      // cos(0.545 * PI / 180)
    }
  },
  vertexShader: (
    /* glsl */
    `
		uniform vec3 sunPosition;
		uniform float rayleigh;
		uniform float turbidity;
		uniform float mieCoefficient;
		uniform vec3 up;

		varying vec3 vWorldPosition;
		varying vec3 vSunDirection;
		varying float vSunfade;
		varying vec3 vBetaR;
		varying vec3 vBetaM;
		varying float vSunE;

		// constants for atmospheric scattering
		const float e = 2.71828182845904523536028747135266249775724709369995957;
		const float pi = 3.141592653589793238462643383279502884197169;

		// wavelength of used primaries, according to preetham
		const vec3 lambda = vec3( 680E-9, 550E-9, 450E-9 );
		// this pre-calcuation replaces older TotalRayleigh(vec3 lambda) function:
		// (8.0 * pow(pi, 3.0) * pow(pow(n, 2.0) - 1.0, 2.0) * (6.0 + 3.0 * pn)) / (3.0 * N * pow(lambda, vec3(4.0)) * (6.0 - 7.0 * pn))
		const vec3 totalRayleigh = vec3( 5.804542996261093E-6, 1.3562911419845635E-5, 3.0265902468824876E-5 );

		// mie stuff
		// K coefficient for the primaries
		const float v = 4.0;
		const vec3 K = vec3( 0.686, 0.678, 0.666 );
		// MieConst = pi * pow( ( 2.0 * pi ) / lambda, vec3( v - 2.0 ) ) * K
		const vec3 MieConst = vec3( 1.8399918514433978E14, 2.7798023919660528E14, 4.0790479543861094E14 );

		// earth shadow hack
		// cutoffAngle = pi / 1.95;
		const float cutoffAngle = 1.6110731556870734;
		const float steepness = 1.5;
		const float EE = 1000.0;

		float sunIntensity( float zenithAngleCos ) {
			zenithAngleCos = clamp( zenithAngleCos, -1.0, 1.0 );
			return EE * max( 0.0, 1.0 - pow( e, -( ( cutoffAngle - acos( zenithAngleCos ) ) / steepness ) ) );
		}

		vec3 totalMie( float T ) {
			float c = ( 0.2 * T ) * 10E-18;
			return 0.434 * c * MieConst;
		}

		void main() {

			vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
			vWorldPosition = worldPosition.xyz;

			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			gl_Position.z = gl_Position.w; // set z to camera.far

			vSunDirection = normalize( sunPosition );

			vSunE = sunIntensity( dot( vSunDirection, up ) );

			vSunfade = 1.0 - clamp( 1.0 - exp( ( sunPosition.y / 450000.0 ) ), 0.0, 1.0 );

			float rayleighCoefficient = rayleigh - ( 1.0 * ( 1.0 - vSunfade ) );

			// extinction (absorbtion + out scattering)
			// rayleigh coefficients
			vBetaR = totalRayleigh * rayleighCoefficient;

			// mie coefficients
			vBetaM = totalMie( turbidity ) * mieCoefficient;

		}`
  ),
  fragmentShader: (
    /* glsl */
    `
		varying vec3 vWorldPosition;
		varying vec3 vSunDirection;
		varying float vSunfade;
		varying vec3 vBetaR;
		varying vec3 vBetaM;
		varying float vSunE;

		uniform float mieDirectionalG;
		uniform vec3 up;
		uniform float uAtmosphereLightDiscCosHalfApexAngle;

		const vec3 cameraPos = vec3( 0.0, 0.0, 0.0 );

		// constants for atmospheric scattering
		const float pi = 3.141592653589793238462643383279502884197169;

		const float n = 1.0003; // refractive index of air
		const float N = 2.545E25; // number of molecules per unit volume for air at 288.15K and 1013mb (sea level -45 celsius)

		// optical length at zenith for molecules
		const float rayleighZenithLength = 8.4E3;
		const float mieZenithLength = 1.25E3;
		// 66 arc seconds -> degrees, and the cosine of that
		const float sunAngularDiameterCos_min = 0.99;
		const float sunAngularDiameterCos_max = 0.999999;

		// 3.0 / ( 16.0 * pi )
		const float THREE_OVER_SIXTEENPI = 0.05968310365946075;
		// 1.0 / ( 4.0 * pi )
		const float ONE_OVER_FOURPI = 0.07957747154594767;

		float rayleighPhase( float cosTheta ) {
			return THREE_OVER_SIXTEENPI * ( 1.0 + pow( cosTheta, 2.0 ) );
		}

		float hgPhase( float cosTheta, float g ) {
			float g2 = pow( g, 2.0 );
			float inverse = 1.0 / pow( 1.0 - 2.0 * g * cosTheta + g2, 1.5 );
			return ONE_OVER_FOURPI * ( ( 1.0 - g2 ) * inverse );
		}

		float saturate(float x) {
			return min(max(x, 0.0), 1.0);
		}

		float GetLightDiskLuminance(vec3 WorldDir, vec3 AtmosphereLightDirection, float AtmosphereLightDiscCosHalfApexAngle) {
			float ViewDotLight = dot(WorldDir, AtmosphereLightDirection);
			float CosHalfApex = AtmosphereLightDiscCosHalfApexAngle;
			if (ViewDotLight > CosHalfApex)
			{
				// const float3 TransmittanceToLight = GetAtmosphereTransmittance(
				// 	PlanetCenterToWorldPos, WorldDir, BottomRadius, TopRadius, TransmittanceLutTexture, TransmittanceLutTextureSampler);
		
				// // Soften out the sun disk to avoid bloom flickering at edge. The soften is applied on the outer part of the disk.
				float SoftEdge = saturate(2.0 * (ViewDotLight - CosHalfApex) / (1.0 - CosHalfApex));
		
				// return TransmittanceToLight * AtmosphereLightDiscLuminance * SoftEdge;
				return SoftEdge;
			}
			return 0.0;
		}

		void main() {

			vec3 direction = normalize( vWorldPosition - cameraPos );

			// optical length
			// cutoff angle at 90 to avoid singularity in next formula.
			float zenithAngle = acos( max( 0.0, dot( up, direction ) ) );
			float inverse = 1.0 / ( cos( zenithAngle ) + 0.15 * pow( 93.885 - ( ( zenithAngle * 180.0 ) / pi ), -1.253 ) );
			float sR = rayleighZenithLength * inverse;
			float sM = mieZenithLength * inverse;

			// combined extinction factor
			vec3 Fex = exp( -( vBetaR * sR + vBetaM * sM ) );

			// in scattering
			float cosTheta = dot( direction, vSunDirection );

			float rPhase = rayleighPhase( cosTheta * 0.5 + 0.5 );
			vec3 betaRTheta = vBetaR * rPhase;

			float mPhase = hgPhase( cosTheta, mieDirectionalG );
			vec3 betaMTheta = vBetaM * mPhase;

			vec3 Lin = pow( vSunE * ( ( betaRTheta + betaMTheta ) / ( vBetaR + vBetaM ) ) * ( 1.0 - Fex ), vec3( 1.5 ) );
			Lin *= mix( vec3( 1.0 ), pow( vSunE * ( ( betaRTheta + betaMTheta ) / ( vBetaR + vBetaM ) ) * Fex, vec3( 1.0 / 2.0 ) ), clamp( pow( 1.0 - dot( up, vSunDirection ), 5.0 ), 0.0, 1.0 ) );

			// nightsky
			float theta = acos( direction.y ); // elevation --> y-axis, [-pi/2, pi/2]
			float phi = atan( direction.z, direction.x ); // azimuth --> x-axis [-pi/2, pi/2]
			vec2 uv = vec2( phi, theta ) / vec2( 2.0 * pi, pi ) + vec2( 0.5, 0.0 );
			vec3 L0 = vec3( 0.1 ) * Fex;

			// composition + solar disc
			float sundisk = GetLightDiskLuminance(direction, vSunDirection, uAtmosphereLightDiscCosHalfApexAngle);
			L0 += ( vSunE * 19000.0 * Fex ) * sundisk;

			vec3 texColor = ( Lin + L0 ) * 0.04 + vec3( 0.0, 0.0003, 0.00075 );

			vec3 retColor = pow( texColor, vec3( 1.0 / ( 1.2 + ( 1.2 * vSunfade ) ) ) );

			gl_FragColor = vec4( retColor * 0.5, 1.0 );

			#include <tonemapping_fragment>
			#include <colorspace_fragment>

		}`
  )
};
const { PI: PU } = Math, { sin: Ca } = Math, { cos: fl } = Math, { tan: RU } = Math, { asin: DU } = Math, LU = Math.atan2, Gu = PU / 180, N8e = 1e3 * 60 * 60 * 24, B8e = 2440588, F8e = 2451545;
function U8e(s) {
  return s.valueOf() / N8e - 0.5 + B8e;
}
function z8e(s) {
  return U8e(s) - F8e;
}
function k8e(s) {
  return Gu * (357.5291 + 0.98560028 * s);
}
function G8e(s) {
  const e = Gu * (1.9148 * Ca(s) + 0.02 * Ca(2 * s) + 3e-4 * Ca(3 * s)), t = Gu * 102.9372;
  return s + e + t + PU;
}
const Zx = Gu * 23.4397;
function H8e(s, e) {
  return LU(Ca(s) * fl(Zx) - RU(e) * Ca(Zx), fl(s));
}
function V8e(s, e) {
  return DU(Ca(e) * fl(Zx) + fl(e) * Ca(Zx) * Ca(s));
}
function W8e(s, e) {
  return Gu * (280.16 + 360.9856235 * s) - e;
}
function X8e(s, e, t) {
  return LU(Ca(s), fl(s) * Ca(e) - RU(t) * fl(e));
}
function j8e(s, e, t) {
  return DU(Ca(e) * Ca(t) + fl(e) * fl(t) * fl(s));
}
function q8e(s) {
  const e = k8e(s), t = G8e(e);
  return {
    dec: V8e(t, 0),
    ra: H8e(t, 0)
  };
}
function Y8e(s, e, t) {
  const i = Gu * -t, n = Gu * e, r = z8e(s), a = q8e(r), o = W8e(r, i) - a.ra;
  return {
    azimuth: X8e(o, n, a.dec),
    altitude: j8e(o, n, a.dec)
  };
}
class SD {
  constructor(e) {
    S(this, "_mesh");
    S(this, "_material");
    S(this, "_cubeCamera");
    S(this, "_scene");
    S(this, "_renderer");
    S(this, "_hdrURL");
    S(this, "_fbo");
    const t = new zM(256);
    t.texture.type = on;
    const i = new UM(1, 1e3, t), n = new Da(), r = new Hs(), a = new kt({
      transparent: !0,
      opacity: 1,
      side: Ps,
      toneMapped: !1
    }), o = new Re(r, a);
    o.scale.set(100, 100, 100), n.add(o), this._mesh = o, this._material = a, this._cubeCamera = i, this._scene = n, this._renderer = e, this._hdrURL = "", this._fbo = t;
  }
  get fbo() {
    return this._fbo;
  }
  get intensity() {
    return this._material.opacity;
  }
  set intensity(e) {
    this._material.opacity = e;
  }
  get rotation() {
    return this._mesh.rotation.y;
  }
  set rotation(e) {
    this._mesh.rotation.y = e;
  }
  update(e, t, i) {
    this.updateHDRUrl(e), this._mesh.rotation.set(t.x, t.y, t.z), this._material.opacity = i, this._cubeCamera.update(this._renderer, this._scene);
  }
  updateHDRUrl(e) {
    if (e !== this._hdrURL) {
      let t;
      e.match(/(.*?)\.(hdr)$/) && (t = st.loadRgbeTexture(e, !0)), e.match(/(.*?)\.(exr)$/) && (t = st.loadExrTexture(e, !0)), t && t.then((i) => {
        this._updateTexture(i);
      }), this._hdrURL = e;
    }
  }
  updateCubeCamera() {
    this._cubeCamera.update(this._renderer, this._scene);
  }
  _mirroredUV(e) {
    e.wrapS = co, e.offset.setX(1);
  }
  _updateTexture(e) {
    this._mirroredUV(e), this._material.map = e, this._material.needsUpdate = !0, this._cubeCamera.update(this._renderer, this._scene);
  }
}
const T6 = {
  uniforms: {
    _Color: {
      value: new Yt(0, 0, 0, 0)
    },
    _MainTex: {
      value: null
    },
    _MainTex_ST: {
      value: new Yt(1, 1, 0, 0)
    },
    _CloudColor: {
      value: new Yt(1, 1, 1, 1)
    },
    _CloudSpeed: {
      value: 1
    },
    _CloudDensity: {
      value: 0.8
    },
    _CloudNumber: {
      value: 0.7
    },
    _Time: {
      value: new Se()
    }
  },
  vertexShader: `
  out vec3 view;

  void main() {

    mat4 viewMatrixInverse = inverse(viewMatrix);

    mat4 viewRotationInverse = mat4(
      viewMatrixInverse[0][0], viewMatrixInverse[1][0], viewMatrixInverse[2][0], 0.0,
      viewMatrixInverse[0][1], viewMatrixInverse[1][1], viewMatrixInverse[2][1], 0.0,
      viewMatrixInverse[0][2], viewMatrixInverse[1][2], viewMatrixInverse[2][2], 0.0,
      viewMatrixInverse[0][3], viewMatrixInverse[1][3], viewMatrixInverse[2][3], 1.0);

    gl_Position = projectionMatrix * viewRotationInverse * modelMatrix * vec4( position, 1.0 );

    vec4 v = modelMatrix * vec4( position, 1.0 );
    view = - v.xyz;
  }
  `,
  fragmentShader: `
// #version 300 es
// #ifdef GL_ES
//     precision highp float;
//     precision highp int;
//     precision mediump sampler3D;
// #endif

uniform float _CloudDensity;
uniform float _CloudSpeed;
uniform vec2 _Time;
uniform vec4 _MainTex_ST;
uniform sampler2D _MainTex;
uniform vec4 _Color;
uniform float _CloudNumber;
uniform vec4 _CloudColor;

#define UNITY_PI            3.14159265359f
#define UNITY_TWO_PI        6.28318530718f

float noise(vec2 uv) {
  return sin(1.5 * uv.x) * sin(1.5 * uv.y);
}

float fbm(vec2 p, int n) {
  mat2 m = mat2(0.6,0.8,-0.8,0.6);
  float f = 0.0;
  float a = 0.5;
	for (int i = 0; i < n; i++)
  {
    f += a * (_CloudDensity + 0.5 * noise(p));
		p = (m * p) * 2.0;
		a *= 0.5;
  }
  return f;
}

float cloud(vec2 uv)
{
  float _sin = sin(_CloudSpeed * 0.05 * _Time.x);
	float _cos = cos(_CloudSpeed * 0.05 * _Time.x);
	uv = vec2((_cos * uv.x + _sin * uv.y), -_sin * uv.x + _cos * uv.y);
	vec2 o = vec2(fbm(uv,6),fbm(uv + 1.2,6));
	float ol = length(o);
	o += 0.05 * vec2((_CloudSpeed * 1.35 * _Time.x + ol),(_CloudSpeed * 1.5 * _Time.x + ol));
	o *= 2.0;
	vec2 n = vec2(fbm(o + 9.0, 6), fbm(o + 5.0, 6));
	float f = fbm(2.0 * (uv + n), 4);
	f = f * 0.5 + smoothstep(0.0, 1.0, pow(f, 3.0) * pow(n.x, 2.0)) * 0.5 + smoothstep(0.0, 1.0, pow(f, 5.0) * pow(n.y, 2.0)) * 0.5;
  return smoothstep(0.0, 2.0, f);
}

vec4 frag(vec3 view)
{
  vec3 viewDir = normalize(view);
  
	vec2 uv = vec2((atan(viewDir.x, viewDir.z) + UNITY_PI) / UNITY_TWO_PI, acos(viewDir.y) / UNITY_PI);
	uv = uv * _MainTex_ST.xy + _MainTex_ST.zw;
	vec4 tex = texture(_MainTex, uv);
	vec4 col = tex * _Color;

	float y = min(viewDir.y + 1.0,1.0);
	float s = 0.5 * (1.0 + 0.4 * tan(1.9 + 2.5 * y));
	float th = uv.x * UNITY_PI * 2.0;
	vec2 _uv = vec2(sin(th) * 0.5, cos(th) * 0.5) * s * 5.0 * _CloudNumber + 0.5;
	float c = cloud(_uv * (s + 1.0));
	c *= smoothstep(0.0,0.4,y) * smoothstep(0.0,0.15,1.0 - y);
	return mix(col, _CloudColor, c * _CloudColor.a);
}

in vec3 view;

void main() {
	gl_FragColor = frag(view);
}
  `
};
class Z8e {
  constructor() {
    S(this, "group", new ci());
    S(this, "type", "CloudComponent");
    S(this, "cloudMaterial");
    S(this, "cloudSpeed", 1);
    S(this, "cloudDensity", 1);
    S(this, "cloudMesh");
    const e = new Hs(1, 32, 8, 0, Math.PI * 2, 0, Math.PI * 0.5);
    this.cloudMaterial = new Jt({
      uniforms: { ...T6.uniforms },
      vertexShader: T6.vertexShader,
      fragmentShader: T6.fragmentShader
    }), this.cloudMaterial.side = ui, this.cloudMaterial.transparent = !0, this.cloudMesh = new Re(e, this.cloudMaterial), this.cloudMesh.frustumCulled = !1, this.group.add(this.cloudMesh);
    const t = this;
    function i(n) {
      t.cloudMaterial.uniforms._Time.value.x = n * 5e-4 * t.cloudSpeed, t.cloudMaterial.uniforms._CloudDensity.value = 0.9 * t.cloudDensity, requestAnimationFrame(i);
    }
    i(1);
  }
  set visible(e) {
    this.cloudMesh.visible = e, this.cloudMesh.frustumCulled = !e;
  }
  dispose() {
    this.group.clear();
  }
}
function wD(s) {
  let e;
  try {
    e = new URL(s, "http://fakehost.com/");
  } catch {
    return null;
  }
  const t = e.pathname.split("/").pop(), i = t.lastIndexOf(".");
  return i === -1 || i === t.length - 1 ? null : t.substring(i + 1);
}
class K8e {
  constructor() {
    this.maxSize = 800, this.minSize = 600, this.unloadPercent = 0.05, this.itemSet = /* @__PURE__ */ new Map(), this.itemList = [], this.usedSet = /* @__PURE__ */ new Set(), this.callbacks = /* @__PURE__ */ new Map(), this.unloadPriorityCallback = null;
    const e = this.itemSet;
    this.defaultPriorityCallback = (t) => e.get(t);
  }
  // Returns whether or not the cache has reached the maximum size
  isFull() {
    return this.itemSet.size >= this.maxSize;
  }
  add(e, t) {
    const i = this.itemSet;
    if (i.has(e) || this.isFull())
      return !1;
    const n = this.usedSet, r = this.itemList, a = this.callbacks;
    return r.push(e), n.add(e), i.set(e, Date.now()), a.set(e, t), !0;
  }
  remove(e) {
    const t = this.usedSet, i = this.itemSet, n = this.itemList, r = this.callbacks;
    if (i.has(e)) {
      r.get(e)(e);
      const a = n.indexOf(e);
      return n.splice(a, 1), t.delete(e), i.delete(e), r.delete(e), !0;
    }
    return !1;
  }
  markUsed(e) {
    const t = this.itemSet, i = this.usedSet;
    t.has(e) && !i.has(e) && (t.set(e, Date.now()), i.add(e));
  }
  markAllUnused() {
    this.usedSet.clear();
  }
  // TODO: this should be renamed because it's not necessarily unloading all unused content
  // Maybe call it "cleanup" or "unloadToMinSize"
  unloadUnusedContent() {
    const e = this.unloadPercent, t = this.minSize, i = this.itemList, n = this.itemSet, r = this.usedSet, a = this.callbacks, o = i.length - r.size, l = i.length - t, c = this.unloadPriorityCallback || this.defaultPriorityCallback;
    if (l > 0 && o > 0) {
      i.sort((p, v) => {
        const g = r.has(p), m = r.has(v);
        return g && m ? 0 : !g && !m ? c(v) - c(p) : g ? 1 : -1;
      });
      const h = Math.min(l, o), u = Math.max(t * e, h * e);
      let d = Math.min(u, o);
      d = Math.ceil(d);
      const f = i.splice(0, d);
      for (let p = 0, v = f.length; p < v; p++) {
        const g = f[p];
        a.get(g)(g), n.delete(g), a.delete(g);
      }
    }
  }
  scheduleUnload(e = !0) {
    this.scheduled || (this.scheduled = !0, queueMicrotask(() => {
      this.scheduled = !1, this.unloadUnusedContent(), e && this.markAllUnused();
    }));
  }
}
class ED {
  constructor() {
    this.maxJobs = 6, this.items = [], this.callbacks = /* @__PURE__ */ new Map(), this.currJobs = 0, this.scheduled = !1, this.autoUpdate = !0, this.priorityCallback = () => {
      throw new Error("PriorityQueue: PriorityCallback function not defined.");
    }, this.schedulingCallback = (e) => {
      requestAnimationFrame(e);
    }, this._runjobs = () => {
      this.tryRunJobs(), this.scheduled = !1;
    };
  }
  sort() {
    const e = this.priorityCallback;
    this.items.sort(e);
  }
  add(e, t) {
    return new Promise((i, n) => {
      const r = (...l) => t(...l).then(i).catch(n), a = this.items, o = this.callbacks;
      a.push(e), o.set(e, r), this.autoUpdate && this.scheduleJobRun();
    });
  }
  remove(e) {
    const t = this.items, i = this.callbacks, n = t.indexOf(e);
    n !== -1 && (t.splice(n, 1), i.delete(e));
  }
  tryRunJobs() {
    this.sort();
    const e = this.items, t = this.callbacks, i = this.maxJobs;
    let n = this.currJobs;
    for (; i > n && e.length > 0; ) {
      n++;
      const r = e.pop(), a = t.get(r);
      t.delete(r), a(r).then(() => {
        this.currJobs--, this.autoUpdate && this.scheduleJobRun();
      }).catch(() => {
        this.currJobs--, this.autoUpdate && this.scheduleJobRun();
      });
    }
    this.currJobs = n;
  }
  scheduleJobRun() {
    this.scheduled || (this.schedulingCallback(this._runjobs), this.scheduled = !0);
  }
}
const oy = 0, ly = 1, C6 = 2, Zg = 3, Kx = 4, zp = 6378137, J8e = 1 / 298.257223563, IU = -(J8e * zp - zp);
function Fw(s) {
  return s === Zg || s === Kx;
}
function Cu(s, e) {
  return s.__lastFrameVisited === e && s.__used;
}
function OU(s, e) {
  s.__lastFrameVisited !== e && (s.__lastFrameVisited = e, s.__used = !1, s.__inFrustum = !1, s.__isLeaf = !1, s.__visible = !1, s.__active = !1, s.__error = 1 / 0, s.__distanceFromCamera = 1 / 0, s.__childrenWereVisible = !1, s.__allChildrenLoaded = !1);
}
function NU(s, e, t, i) {
  if (i.ensureChildrenArePreprocessed(s), OU(s, e), s.__used = !0, t.markUsed(s), s.__contentEmpty) {
    const n = s.children;
    for (let r = 0, a = n.length; r < a; r++)
      NU(n[r], e, t, i);
  }
}
function BU(s, e, t) {
  if (t.ensureChildrenArePreprocessed(s), s.__contentEmpty && (!s.__externalTileSet || Fw(s.__loadingState))) {
    const n = s.children;
    for (let r = 0, a = n.length; r < a; r++) {
      const o = n[r];
      o.__depthFromRenderedParent = e, BU(o, e, t);
    }
  } else
    t.requestTileContents(s);
}
function FU(s, e = null, t = null, i = null, n = 0) {
  if (e && e(s, i, n)) {
    t && t(s, i, n);
    return;
  }
  const r = s.children;
  for (let a = 0, o = r.length; a < o; a++)
    FU(r[a], e, t, s, n + 1);
  t && t(s, i, n);
}
function UU(s, e) {
  e.ensureChildrenArePreprocessed(s);
  const t = e.stats, i = e.frameCount, n = e.errorTarget, r = e.maxDepth, a = e.loadSiblings, o = e.lruCache, l = e.stopAtEmptyTiles;
  if (OU(s, i), e.tileInView(s) === !1)
    return !1;
  if (s.__used = !0, o.markUsed(s), s.__inFrustum = !0, t.inFrustum++, (l || !s.__contentEmpty) && !s.__externalTileSet && (e.calculateError(s), s.__error <= n || e.maxDepth > 0 && s.__depth + 1 >= r))
    return !0;
  let h = !1;
  const u = s.children;
  for (let d = 0, f = u.length; d < f; d++) {
    const p = u[d], v = UU(p, e);
    h = h || v;
  }
  if (h && a)
    for (let d = 0, f = u.length; d < f; d++) {
      const p = u[d];
      NU(p, i, o, e);
    }
  return !0;
}
function zU(s, e) {
  const t = e.stats, i = e.frameCount;
  if (!Cu(s, i))
    return;
  t.used++;
  const n = s.children;
  let r = !1;
  for (let a = 0, o = n.length; a < o; a++) {
    const l = n[a];
    r = r || Cu(l, i);
  }
  if (!r)
    s.__isLeaf = !0;
  else {
    let a = !1, o = !0;
    for (let l = 0, c = n.length; l < c; l++) {
      const h = n[l];
      if (zU(h, e), a = a || h.__wasSetVisible || h.__childrenWereVisible, Cu(h, i)) {
        const u = h.__allChildrenLoaded || !h.__contentEmpty && Fw(h.__loadingState) || !h.__externalTileSet && h.__contentEmpty && h.children.length === 0 || h.__externalTileSet && h.__loadingState === Kx;
        o = o && u;
      }
    }
    s.__childrenWereVisible = a, s.__allChildrenLoaded = o;
  }
}
function kU(s, e) {
  const t = e.stats, i = e.frameCount;
  if (!Cu(s, i))
    return;
  const n = s.parent, r = n ? n.__depthFromRenderedParent : -1;
  s.__depthFromRenderedParent = r;
  const a = e.lruCache;
  if (s.__isLeaf) {
    s.__depthFromRenderedParent++, s.__loadingState === Zg ? (s.__inFrustum && (s.__visible = !0, t.visible++), s.__active = !0, t.active++) : !a.isFull() && (!s.__contentEmpty || s.__externalTileSet) && e.requestTileContents(s);
    return;
  }
  const o = (e.errorTarget + 1) * e.errorThreshold, l = s.__error <= o, c = l || s.refine === "ADD", h = !s.__contentEmpty, u = h || s.__externalTileSet, d = Fw(s.__loadingState) && u, f = s.__childrenWereVisible, p = s.children, v = s.__allChildrenLoaded;
  if (c && h && s.__depthFromRenderedParent++, c && !d && !a.isFull() && u && e.requestTileContents(s), (l && !v && !f && d || s.refine === "ADD" && d) && (s.__inFrustum && (s.__visible = !0, t.visible++), s.__active = !0, t.active++), s.refine !== "ADD" && l && !v && d)
    for (let g = 0, m = p.length; g < m; g++) {
      const x = p[g];
      Cu(x, i) && !a.isFull() && (x.__depthFromRenderedParent = s.__depthFromRenderedParent + 1, BU(x, x.__depthFromRenderedParent, e));
    }
  else
    for (let g = 0, m = p.length; g < m; g++) {
      const x = p[g];
      Cu(x, i) && kU(x, e);
    }
}
function GU(s, e) {
  const t = e.frameCount, i = Cu(s, t);
  if (i || s.__usedLastFrame) {
    let n = !1, r = !1;
    i && (n = s.__active, e.displayActiveTiles ? r = s.__active || s.__visible : r = s.__visible), !s.__contentEmpty && s.__loadingState === Zg && (s.__wasSetActive !== n && e.setTileActive(s, n), s.__wasSetVisible !== r && e.setTileVisible(s, r)), s.__wasSetActive = n, s.__wasSetVisible = r, s.__usedLastFrame = i;
    const a = s.children;
    for (let o = 0, l = a.length; o < l; o++) {
      const c = a[o];
      GU(c, e);
    }
  }
}
const TD = (s, e) => s.__depth !== e.__depth ? s.__depth > e.__depth ? -1 : 1 : s.__inFrustum !== e.__inFrustum ? s.__inFrustum ? 1 : -1 : s.__used !== e.__used ? s.__used ? 1 : -1 : s.__error !== e.__error ? s.__error > e.__error ? 1 : -1 : s.__distanceFromCamera !== e.__distanceFromCamera ? s.__distanceFromCamera > e.__distanceFromCamera ? -1 : 1 : 0, Q8e = (s) => 1 / (s.__depthFromRenderedParent + 1);
class $8e {
  get rootTileSet() {
    const e = this.tileSets[this.rootURL];
    return !e || e instanceof Promise ? null : e;
  }
  get root() {
    const e = this.rootTileSet;
    return e ? e.root : null;
  }
  constructor(e) {
    this.tileSets = {}, this.rootURL = e, this.fetchOptions = {}, this.preprocessURL = null;
    const t = new K8e();
    t.unloadPriorityCallback = Q8e;
    const i = new ED();
    i.maxJobs = 4, i.priorityCallback = TD;
    const n = new ED();
    n.maxJobs = 1, n.priorityCallback = TD, this.lruCache = t, this.downloadQueue = i, this.parseQueue = n, this.stats = {
      parsing: 0,
      downloading: 0,
      failed: 0,
      inFrustum: 0,
      used: 0,
      active: 0,
      visible: 0
    }, this.frameCount = 0, this.errorTarget = 6, this.errorThreshold = 1 / 0, this.loadSiblings = !0, this.displayActiveTiles = !1, this.maxDepth = 1 / 0, this.stopAtEmptyTiles = !0;
  }
  traverse(e, t) {
    const n = this.tileSets[this.rootURL];
    !n || !n.root || FU(n.root, (r, ...a) => (this.ensureChildrenArePreprocessed(r), e ? e(r, ...a) : !1), t);
  }
  // Public API
  update() {
    const e = this.stats, t = this.lruCache, i = this.tileSets, n = i[this.rootURL];
    if (this.rootURL in i) {
      if (!n || !n.root)
        return;
    } else {
      this.loadRootTileSet(this.rootURL);
      return;
    }
    const r = n.root;
    e.inFrustum = 0, e.used = 0, e.active = 0, e.visible = 0, this.frameCount++, UU(r, this), zU(r, this), kU(r, this), GU(r, this), t.scheduleUnload();
  }
  // Overrideable
  parseTile(e, t, i) {
    return null;
  }
  disposeTile(e) {
  }
  preprocessNode(e, t, i = null) {
    if (e.content && (!("uri" in e.content) && "url" in e.content && (e.content.uri = e.content.url, delete e.content.url), e.content.uri && (e.content.uri = new URL(e.content.uri, t + "/").toString()), e.content.boundingVolume && !("box" in e.content.boundingVolume || "sphere" in e.content.boundingVolume || "region" in e.content.boundingVolume) && delete e.content.boundingVolume), e.parent = i, e.children = e.children || [], e.content && e.content.uri) {
      const r = wD(e.content.uri), a = !!(r && r.toLowerCase() === "json");
      e.__externalTileSet = a, e.__contentEmpty = a;
    } else
      e.__externalTileSet = !1, e.__contentEmpty = !0;
    e.__distanceFromCamera = 1 / 0, e.__error = 1 / 0, e.__inFrustum = !1, e.__isLeaf = !1, e.__usedLastFrame = !1, e.__used = !1, e.__wasSetVisible = !1, e.__visible = !1, e.__childrenWereVisible = !1, e.__allChildrenLoaded = !1, e.__wasSetActive = !1, e.__active = !1, e.__loadingState = oy, e.__loadIndex = 0, e.__loadAbort = null, e.__depthFromRenderedParent = -1, i === null ? (e.__depth = 0, e.refine = e.refine || "REPLACE") : (e.__depth = i.__depth + 1, e.refine = e.refine || i.refine), e.__basePath = t;
  }
  setTileActive(e, t) {
  }
  setTileVisible(e, t) {
  }
  calculateError(e) {
    return 0;
  }
  tileInView(e) {
    return !0;
  }
  ensureChildrenArePreprocessed(e) {
    const t = e.children;
    for (let i = 0, n = t.length; i < n; i++) {
      const r = t[i];
      if ("__depth" in r)
        break;
      this.preprocessNode(r, e.__basePath, e);
    }
  }
  resetFailedTiles() {
    const e = this.stats;
    e.failed !== 0 && (this.traverse((t) => {
      t.__loadingState === Kx && (t.__loadingState = oy);
    }), e.failed = 0);
  }
  // Private Functions
  fetchTileSet(e, t, i = null) {
    return fetch(e, t).then((n) => {
      if (n.ok)
        return n.json();
      throw new Error(`TilesRenderer: Failed to load tileset "${e}" with status ${n.status} : ${n.statusText}`);
    }).then((n) => {
      const r = n.asset.version, [a, o] = r.split(".").map((c) => parseInt(c));
      console.assert(
        a <= 1,
        "TilesRenderer: asset.version is expected to be a 1.x or a compatible version."
      ), a === 1 && o > 0 && console.warn("TilesRenderer: tiles versions at 1.1 or higher have limited support. Some new extensions and features may not be supported.");
      let l = e.replace(/\/[^\/]*\/?$/, "");
      return l = new URL(l, window.location.href).toString(), this.preprocessNode(n.root, l, i), n;
    });
  }
  loadRootTileSet(e) {
    const t = this.tileSets;
    if (e in t)
      return t[e] instanceof Error ? Promise.reject(t[e]) : Promise.resolve(t[e]);
    {
      const i = this.fetchTileSet(this.preprocessURL ? this.preprocessURL(e) : e, this.fetchOptions).then((n) => {
        t[e] = n;
      });
      return i.catch((n) => {
        console.error(n), t[e] = n;
      }), t[e] = i, i;
    }
  }
  requestTileContents(e) {
    if (e.__loadingState !== oy)
      return;
    const t = this.stats, i = this.lruCache, n = this.downloadQueue, r = this.parseQueue, a = e.__externalTileSet;
    i.add(e, (u) => {
      u.__loadingState === ly ? (u.__loadAbort.abort(), u.__loadAbort = null) : a ? u.children.length = 0 : this.disposeTile(u), u.__loadingState === ly ? t.downloading-- : u.__loadingState === C6 && t.parsing--, u.__loadingState = oy, u.__loadIndex++, r.remove(u), n.remove(u);
    }), e.__loadIndex++;
    const o = e.__loadIndex, l = new AbortController(), c = l.signal;
    t.downloading++, e.__loadAbort = l, e.__loadingState = ly;
    const h = (u) => {
      e.__loadIndex === o && (u.name !== "AbortError" ? (r.remove(e), n.remove(e), e.__loadingState === C6 ? t.parsing-- : e.__loadingState === ly && t.downloading--, t.failed++, console.error(`TilesRenderer : Failed to load tile at url "${e.content.uri}".`), console.error(u), e.__loadingState = Kx) : i.remove(e));
    };
    a ? n.add(e, (u) => {
      if (u.__loadIndex !== o)
        return Promise.resolve();
      const d = this.preprocessURL ? this.preprocessURL(u.content.uri) : u.content.uri;
      return this.fetchTileSet(d, Object.assign({ signal: c }, this.fetchOptions), u);
    }).then((u) => {
      e.__loadIndex === o && (t.downloading--, e.__loadAbort = null, e.__loadingState = Zg, e.children.push(u.root));
    }).catch(h) : n.add(e, (u) => {
      if (u.__loadIndex !== o)
        return Promise.resolve();
      const d = this.preprocessURL ? this.preprocessURL(u.content.uri) : u.content.uri;
      return fetch(d, Object.assign({ signal: c }, this.fetchOptions));
    }).then((u) => {
      if (e.__loadIndex === o) {
        if (u.ok)
          return u.arrayBuffer();
        throw new Error(`Failed to load model with error code ${u.status}`);
      }
    }).then((u) => {
      if (e.__loadIndex === o)
        return t.downloading--, t.parsing++, e.__loadAbort = null, e.__loadingState = C6, r.add(e, (d) => {
          if (d.__loadIndex !== o)
            return Promise.resolve();
          const f = d.content.uri, p = wD(f);
          return this.parseTile(u, d, p);
        });
    }).then(() => {
      e.__loadIndex === o && (t.parsing--, e.__loadingState = Zg, e.__wasSetVisible && this.setTileVisible(e, !0), e.__wasSetActive && this.setTileActive(e, !0));
    }).catch(h);
  }
  dispose() {
    const e = this.lruCache, t = [];
    this.traverse((i) => (t.push(i), !1));
    for (let i = 0, n = t.length; i < n; i++)
      e.remove(t[i]);
    this.stats = {
      parsing: 0,
      downloading: 0,
      failed: 0,
      inFrustum: 0,
      used: 0,
      active: 0,
      visible: 0
    }, this.frameCount = 0;
  }
}
const e5e = new TextDecoder();
function HU(s) {
  return e5e.decode(s);
}
class y8 {
  constructor(e, t, i, n) {
    this.buffer = e, this.binOffset = t + i, this.binLength = n;
    let r = null;
    if (i !== 0) {
      const a = new Uint8Array(e, t, i);
      r = JSON.parse(HU(a));
    } else
      r = {};
    this.header = r;
  }
  getKeys() {
    return Object.keys(this.header);
  }
  getData(e, t, i = null, n = null) {
    const r = this.header;
    if (!(e in r))
      return null;
    const a = r[e];
    if (a instanceof Object) {
      if (Array.isArray(a))
        return a;
      {
        const { buffer: o, binOffset: l, binLength: c } = this, h = a.byteOffset || 0, u = a.type || n, d = a.componentType || i;
        if ("type" in a && n && a.type !== n)
          throw new Error("FeatureTable: Specified type does not match expected type.");
        let f;
        switch (u) {
          case "SCALAR":
            f = 1;
            break;
          case "VEC2":
            f = 2;
            break;
          case "VEC3":
            f = 3;
            break;
          case "VEC4":
            f = 4;
            break;
          default:
            throw new Error(`FeatureTable : Feature type not provided for "${e}".`);
        }
        let p;
        const v = l + h, g = t * f;
        switch (d) {
          case "BYTE":
            p = new Int8Array(o, v, g);
            break;
          case "UNSIGNED_BYTE":
            p = new Uint8Array(o, v, g);
            break;
          case "SHORT":
            p = new Int16Array(o, v, g);
            break;
          case "UNSIGNED_SHORT":
            p = new Uint16Array(o, v, g);
            break;
          case "INT":
            p = new Int32Array(o, v, g);
            break;
          case "UNSIGNED_INT":
            p = new Uint32Array(o, v, g);
            break;
          case "FLOAT":
            p = new Float32Array(o, v, g);
            break;
          case "DOUBLE":
            p = new Float64Array(o, v, g);
            break;
          default:
            throw new Error(`FeatureTable : Feature component type not provided for "${e}".`);
        }
        if (v + g * p.BYTES_PER_ELEMENT > l + c)
          throw new Error("FeatureTable: Feature data read outside binary body length.");
        return p;
      }
    } else
      return a;
  }
  getBuffer(e, t) {
    const { buffer: i, binOffset: n } = this;
    return i.slice(n + e, n + e + t);
  }
}
class Uw extends y8 {
  constructor(e, t, i, n, r) {
    super(e, i, n, r), this.batchSize = t;
  }
  getData(e, t = null, i = null) {
    return super.getData(e, this.batchSize, t, i);
  }
}
class X2 {
  constructor() {
    this.fetchOptions = {}, this.workingPath = "";
  }
  load(e) {
    return fetch(e, this.fetchOptions).then((t) => {
      if (!t.ok)
        throw new Error(`Failed to load file "${e}" with status ${t.status} : ${t.statusText}`);
      return t.arrayBuffer();
    }).then((t) => (this.workingPath === "" && (this.workingPath = this.workingPathForURL(e)), this.parse(t)));
  }
  resolveExternalURL(e) {
    return /^[^\\/]/.test(e) ? this.workingPath + "/" + e : e;
  }
  workingPathForURL(e) {
    const t = e.split(/[\\/]/g);
    return t.pop(), t.join("/") + "/";
  }
  parse(e) {
    throw new Error("LoaderBase: Parse not implemented.");
  }
}
function kp(s) {
  let e;
  if (s instanceof DataView ? e = s : e = new DataView(s), String.fromCharCode(e.getUint8(0)) === "{")
    return null;
  let t = "";
  for (let i = 0; i < 4; i++)
    t += String.fromCharCode(e.getUint8(i));
  return t;
}
class t5e extends X2 {
  parse(e) {
    const t = new DataView(e), i = kp(t);
    console.assert(i === "b3dm");
    const n = t.getUint32(4, !0);
    console.assert(n === 1);
    const r = t.getUint32(8, !0);
    console.assert(r === e.byteLength);
    const a = t.getUint32(12, !0), o = t.getUint32(16, !0), l = t.getUint32(20, !0), c = t.getUint32(24, !0), h = 28, u = e.slice(
      h,
      h + a + o
    ), d = new y8(
      u,
      0,
      a,
      o
    ), f = h + a + o, p = e.slice(
      f,
      f + l + c
    ), v = new Uw(
      p,
      d.getData("BATCH_LENGTH"),
      0,
      l,
      c
    ), g = f + l + c, m = new Uint8Array(e, g, r - g);
    return {
      version: n,
      featureTable: d,
      batchTable: v,
      glbBytes: m
    };
  }
}
class VU extends t5e {
  constructor(e = Ku) {
    super(), this.manager = e, this.adjustmentTransform = new et();
  }
  parse(e) {
    const t = super.parse(e), i = t.glbBytes.slice().buffer;
    return new Promise((n, r) => {
      const a = this.manager, o = this.fetchOptions, l = a.getHandler("path.gltf") || new K4(a);
      o.credentials === "include" && o.mode === "cors" && l.setCrossOrigin("use-credentials"), "credentials" in o && l.setWithCredentials(o.credentials === "include"), o.headers && l.setRequestHeader(o.headers);
      let c = this.workingPath;
      !/[\\/]$/.test(c) && c.length && (c += "/");
      const h = this.adjustmentTransform;
      l.parse(i, c, (u) => {
        const { batchTable: d, featureTable: f } = t, { scene: p } = u, v = f.getData("RTC_CENTER");
        v && (p.position.x += v[0], p.position.y += v[1], p.position.z += v[2]), u.scene.updateMatrix(), u.scene.matrix.multiply(h), u.scene.matrix.decompose(u.scene.position, u.scene.quaternion, u.scene.scale), u.batchTable = d, u.featureTable = f, p.batchTable = d, p.featureTable = f, n(u);
      }, r);
    });
  }
}
class i5e extends X2 {
  parse(e) {
    const t = new DataView(e), i = kp(t);
    console.assert(i === "pnts");
    const n = t.getUint32(4, !0);
    console.assert(n === 1);
    const r = t.getUint32(8, !0);
    console.assert(r === e.byteLength);
    const a = t.getUint32(12, !0), o = t.getUint32(16, !0), l = t.getUint32(20, !0), c = t.getUint32(24, !0), h = 28, u = e.slice(
      h,
      h + a + o
    ), d = new y8(
      u,
      0,
      a,
      o
    ), f = h + a + o, p = e.slice(
      f,
      f + l + c
    ), v = new Uw(
      p,
      d.getData("BATCH_LENGTH") || d.getData("POINTS_LENGTH"),
      0,
      l,
      c
    );
    return Promise.resolve({
      version: n,
      featureTable: d,
      batchTable: v
    });
  }
}
function n5e(s) {
  const e = s >> 11, t = s >> 5 & 63, i = s & 31, n = Math.round(e / 31 * 255), r = Math.round(t / 63 * 255), a = Math.round(i / 31 * 255);
  return [n, r, a];
}
const CD = {
  RGB: "color",
  POSITION: "position"
};
class WU extends i5e {
  constructor(e = Ku) {
    super(), this.manager = e;
  }
  parse(e) {
    return super.parse(e).then(async (t) => {
      const { featureTable: i, batchTable: n } = t, r = new qu(), a = i.header.extensions, o = new _();
      let l;
      if (a && a["3DTILES_draco_point_compression"]) {
        const { byteOffset: u, byteLength: d, properties: f } = a["3DTILES_draco_point_compression"], p = this.manager.getHandler("draco.drc");
        if (p == null)
          throw new Error("PNTSLoader: dracoLoader not available.");
        const v = {};
        for (const x in f)
          if (x in CD && x in f) {
            const y = CD[x];
            v[y] = f[x];
          }
        const g = {
          attributeIDs: v,
          attributeTypes: {
            position: "Float32Array",
            color: "Uint8Array"
          },
          useUniqueIDs: !0
        }, m = i.getBuffer(u, d);
        l = await p.decodeGeometry(m, g), l.attributes.color && (r.vertexColors = !0);
      } else {
        const u = i.getData("POINTS_LENGTH"), d = i.getData("POSITION", u, "FLOAT", "VEC3"), f = i.getData("RGB", u, "UNSIGNED_BYTE", "VEC3"), p = i.getData("RGBA", u, "UNSIGNED_BYTE", "VEC4"), v = i.getData("RGB565", u, "UNSIGNED_SHORT", "SCALAR"), g = i.getData("CONSTANT_RGBA", u, "UNSIGNED_BYTE", "VEC4"), m = i.getData("POSITION_QUANTIZED", u, "UNSIGNED_SHORT", "VEC3"), x = i.getData("QUANTIZED_VOLUME_SCALE", u, "FLOAT", "VEC3"), y = i.getData("QUANTIZED_VOLUME_OFFSET", u, "FLOAT", "VEC3");
        if (l = new Pt(), m) {
          const A = new Float32Array(u * 3);
          for (let b = 0; b < u; b++)
            for (let M = 0; M < 3; M++) {
              const w = 3 * b + M;
              A[w] = m[w] / 65535 * x[M];
            }
          o.x = y[0], o.y = y[1], o.z = y[2], l.setAttribute("position", new Ht(A, 3, !1));
        } else
          l.setAttribute("position", new Ht(d, 3, !1));
        if (p !== null)
          l.setAttribute("color", new Ht(p, 4, !0)), r.vertexColors = !0, r.transparent = !0, r.depthWrite = !1;
        else if (f !== null)
          l.setAttribute("color", new Ht(f, 3, !0)), r.vertexColors = !0;
        else if (v !== null) {
          const A = new Uint8Array(u * 3);
          for (let b = 0; b < u; b++) {
            const M = n5e(v[b]);
            for (let w = 0; w < 3; w++) {
              const C = 3 * b + w;
              A[C] = M[w];
            }
          }
          l.setAttribute("color", new Ht(A, 3, !0)), r.vertexColors = !0;
        } else if (g !== null) {
          const A = new _e(g[0], g[1], g[2]);
          r.color = A;
          const b = g[3] / 255;
          b < 1 && (r.opacity = b, r.transparent = !0, r.depthWrite = !1);
        }
      }
      [
        "BATCH_LENGTH",
        "NORMAL",
        "NORMAL_OCT16P"
      ].forEach((u) => {
        u in i.header && console.warn(
          `PNTSLoader: Unsupported FeatureTable feature "${u}" detected.`
        );
      });
      const c = new sh(l, r);
      c.position.copy(o), t.scene = c, t.scene.featureTable = i, t.scene.batchTable = n;
      const h = i.getData("RTC_CENTER");
      return h && (t.scene.position.x += h[0], t.scene.position.y += h[1], t.scene.position.z += h[2]), t;
    });
  }
}
class s5e extends X2 {
  parse(e) {
    const t = new DataView(e), i = kp(t);
    console.assert(i === "i3dm");
    const n = t.getUint32(4, !0);
    console.assert(n === 1);
    const r = t.getUint32(8, !0);
    console.assert(r === e.byteLength);
    const a = t.getUint32(12, !0), o = t.getUint32(16, !0), l = t.getUint32(20, !0), c = t.getUint32(24, !0), h = t.getUint32(28, !0), u = 32, d = e.slice(
      u,
      u + a + o
    ), f = new y8(
      d,
      0,
      a,
      o
    ), p = u + a + o, v = e.slice(
      p,
      p + l + c
    ), g = new Uw(
      v,
      f.getData("INSTANCES_LENGTH"),
      0,
      l,
      c
    ), m = p + l + c, x = new Uint8Array(e, m, r - m);
    let y = null, A = null;
    if (h)
      y = x, A = Promise.resolve();
    else {
      const b = this.resolveExternalURL(HU(x));
      A = fetch(b, this.fetchOptions).then((M) => {
        if (!M.ok)
          throw new Error(`I3DMLoaderBase : Failed to load file "${b}" with status ${M.status} : ${M.statusText}`);
        return M.arrayBuffer();
      }).then((M) => {
        y = new Uint8Array(M);
      });
    }
    return A.then(() => ({
      version: n,
      featureTable: f,
      batchTable: g,
      glbBytes: y
    }));
  }
}
const _D = new _(), _6 = new _(), P6 = new _(), PD = new _(), R6 = new St(), cy = new _(), hy = new et();
class XU extends s5e {
  constructor(e = Ku) {
    super(), this.manager = e, this.adjustmentTransform = new et();
  }
  resolveExternalURL(e) {
    return this.manager.resolveURL(super.resolveExternalURL(e));
  }
  parse(e) {
    return super.parse(e).then((t) => {
      const { featureTable: i, batchTable: n } = t, r = t.glbBytes.slice().buffer;
      return new Promise((a, o) => {
        const l = this.fetchOptions, c = this.manager, h = c.getHandler("path.gltf") || new K4(c);
        l.credentials === "include" && l.mode === "cors" && h.setCrossOrigin("use-credentials"), "credentials" in l && h.setWithCredentials(l.credentials === "include"), l.headers && h.setRequestHeader(l.headers);
        let u = this.workingPath;
        /[\\/]$/.test(u) || (u += "/");
        const d = this.adjustmentTransform;
        h.parse(r, u, (f) => {
          const p = i.getData("INSTANCES_LENGTH"), v = i.getData("POSITION", p, "FLOAT", "VEC3"), g = i.getData("NORMAL_UP", p, "FLOAT", "VEC3"), m = i.getData("NORMAL_RIGHT", p, "FLOAT", "VEC3"), x = i.getData("SCALE_NON_UNIFORM", p, "FLOAT", "VEC3"), y = i.getData("SCALE", p, "FLOAT", "SCALAR");
          [
            "RTC_CENTER",
            "QUANTIZED_VOLUME_OFFSET",
            "QUANTIZED_VOLUME_SCALE",
            "EAST_NORTH_UP",
            "POSITION_QUANTIZED",
            "NORMAL_UP_OCT32P",
            "NORMAL_RIGHT_OCT32P"
          ].forEach((w) => {
            w in i.header && console.warn(`I3DMLoader: Unsupported FeatureTable feature "${w}" detected.`);
          });
          const A = /* @__PURE__ */ new Map(), b = [];
          f.scene.traverse((w) => {
            if (w.isMesh) {
              const { geometry: C, material: T } = w, E = new ho(C, T, p);
              E.position.copy(w.position), E.rotation.copy(w.rotation), E.scale.copy(w.scale), b.push(E), A.set(w, E);
            }
          });
          const M = new _();
          for (let w = 0; w < p; w++)
            M.x += v[w * 3 + 0] / p, M.y += v[w * 3 + 1] / p, M.z += v[w * 3 + 2] / p;
          A.forEach((w, C) => {
            const T = C.parent;
            T && (T.remove(C), T.add(w), w.updateMatrixWorld(), w.position.copy(M).applyMatrix4(w.matrixWorld));
          });
          for (let w = 0; w < p; w++) {
            PD.set(
              v[w * 3 + 0] - M.x,
              v[w * 3 + 1] - M.y,
              v[w * 3 + 2] - M.z
            ), g ? (_6.set(
              g[w * 3 + 0],
              g[w * 3 + 1],
              g[w * 3 + 2]
            ), P6.set(
              m[w * 3 + 0],
              m[w * 3 + 1],
              m[w * 3 + 2]
            ), _D.crossVectors(P6, _6).normalize(), hy.makeBasis(
              P6,
              _6,
              _D
            ), R6.setFromRotationMatrix(hy)) : R6.set(0, 0, 0, 1), y ? cy.setScalar(y[w]) : x ? cy.set(
              x[w * 3 + 0],
              x[w * 3 + 1],
              x[w * 3 + 2]
            ) : cy.set(1, 1, 1), hy.compose(PD, R6, cy).multiply(d);
            for (let C = 0, T = b.length; C < T; C++)
              b[C].setMatrixAt(w, hy);
          }
          f.batchTable = n, f.featureTable = i, f.scene.batchTable = n, f.scene.featureTable = i, a(f);
        }, o);
      });
    });
  }
}
class r5e extends X2 {
  parse(e) {
    const t = new DataView(e), i = kp(t);
    console.assert(i === "cmpt", 'CMPTLoader: The magic bytes equal "cmpt".');
    const n = t.getUint32(4, !0);
    console.assert(n === 1, 'CMPTLoader: The version listed in the header is "1".');
    const r = t.getUint32(8, !0);
    console.assert(r === e.byteLength, "CMPTLoader: The contents buffer length listed in the header matches the file.");
    const a = t.getUint32(12, !0), o = [];
    let l = 16;
    for (let c = 0; c < a; c++) {
      const h = new DataView(e, l, 12), u = kp(h), d = h.getUint32(4, !0), f = h.getUint32(8, !0), p = new Uint8Array(e, l, f);
      o.push({
        type: u,
        buffer: p,
        version: d
      }), l += f;
    }
    return {
      version: n,
      tiles: o
    };
  }
}
class a5e extends r5e {
  constructor(e = Ku) {
    super(), this.manager = e, this.adjustmentTransform = new et();
  }
  parse(e) {
    const t = super.parse(e), i = this.manager, n = this.adjustmentTransform, r = [];
    for (const a in t.tiles) {
      const { type: o, buffer: l } = t.tiles[a];
      switch (o) {
        case "b3dm": {
          const c = l.slice(), h = new VU(i);
          h.workingPath = this.workingPath, h.fetchOptions = this.fetchOptions, h.adjustmentTransform.copy(n);
          const u = h.parse(c.buffer);
          r.push(u);
          break;
        }
        case "pnts": {
          const c = l.slice(), h = new WU(i);
          h.workingPath = this.workingPath, h.fetchOptions = this.fetchOptions;
          const u = h.parse(c.buffer);
          r.push(u);
          break;
        }
        case "i3dm": {
          const c = l.slice(), h = new XU(i);
          h.workingPath = this.workingPath, h.fetchOptions = this.fetchOptions, h.adjustmentTransform.copy(n);
          const u = h.parse(c.buffer);
          r.push(u);
          break;
        }
      }
    }
    return Promise.all(r).then((a) => {
      const o = new ci();
      return a.forEach((l) => {
        o.add(l.scene);
      }), {
        tiles: a,
        scene: o
      };
    });
  }
}
class jU {
  constructor() {
    this.name = "CESIUM_RTC";
  }
  afterRoot(e) {
    if (e.parser.json.extensions && e.parser.json.extensions.CESIUM_RTC) {
      const { center: t } = e.parser.json.extensions.CESIUM_RTC;
      t && (e.scene.position.x += t[0], e.scene.position.y += t[1], e.scene.position.z += t[2]);
    }
  }
}
class o5e extends X2 {
  constructor(e = Ku) {
    super(), this.manager = e;
  }
  parse(e) {
    return new Promise((t, i) => {
      const n = this.manager, r = this.fetchOptions;
      let a = n.getHandler("path.gltf") || n.getHandler("path.glb");
      a || (a = new K4(n), a.register(() => new jU()), r.credentials === "include" && r.mode === "cors" && a.setCrossOrigin("use-credentials"), "credentials" in r && a.setWithCredentials(r.credentials === "include"), r.headers && a.setRequestHeader(r.headers));
      let o = a.resourcePath || a.path || this.workingPath;
      !/[\\/]$/.test(o) && o.length && (o += "/"), a.parse(e, o, (l) => {
        t(l);
      }, i);
    });
  }
}
const uy = new et();
class l5e extends ci {
  constructor(e) {
    super(), this.name = "TilesRenderer.TilesGroup", this.tilesRenderer = e;
  }
  raycast(e, t) {
    return this.tilesRenderer.optimizeRaycast ? (this.tilesRenderer.raycast(e, t), !0) : !1;
  }
  updateMatrixWorld(e) {
    if (this.matrixAutoUpdate && this.updateMatrix(), this.matrixWorldNeedsUpdate || e) {
      this.parent === null ? uy.copy(this.matrix) : uy.multiplyMatrices(this.parent.matrixWorld, this.matrix), this.matrixWorldNeedsUpdate = !1;
      const t = uy.elements, i = this.matrixWorld.elements;
      let n = !1;
      for (let r = 0; r < 16; r++) {
        const a = t[r], o = i[r];
        if (Math.abs(a - o) > Number.EPSILON) {
          n = !0;
          break;
        }
      }
      if (n) {
        this.matrixWorld.copy(uy);
        const r = this.children;
        for (let a = 0, o = r.length; a < o; a++)
          r[a].updateMatrixWorld();
      }
    }
  }
}
const c5e = parseInt(Qo) < 165, Jx = new et(), qU = new g0(), D6 = new _(), p9 = [];
function YU(s, e) {
  return s.distance - e.distance;
}
function ZU(s, e, t) {
  c5e ? (s.traverse((i) => {
    Object.getPrototypeOf(i).raycast.call(i, e, t);
  }), p9.sort(YU)) : e.intersectObject(s, !0, t);
}
function h5e(s, e) {
  ZU(s, e, p9);
  const t = p9[0] || null;
  return p9.length = 0, t;
}
function KU(s, e, t, i = null) {
  const { group: n, activeTiles: r } = s;
  s.ensureChildrenArePreprocessed(e), i === null && (i = qU, Jx.copy(n.matrixWorld).invert(), i.copy(t.ray).applyMatrix4(Jx));
  const a = [], o = e.children;
  for (let h = 0, u = o.length; h < u; h++) {
    const d = o[h];
    if (!d.__used)
      continue;
    d.cached.boundingVolume.intersectRay(i, D6) !== null && (D6.applyMatrix4(n.matrixWorld), a.push({
      distance: D6.distanceToSquared(t.ray.origin),
      tile: d
    }));
  }
  a.sort(YU);
  let l = null, c = 1 / 0;
  if (r.has(e)) {
    const h = h5e(e.cached.scene, t);
    h && (l = h, c = h.distance * h.distance);
  }
  for (let h = 0, u = a.length; h < u; h++) {
    const d = a[h], f = d.distance, p = d.tile;
    if (f > c)
      break;
    const v = KU(s, p, t, i);
    if (v) {
      const g = v.distance * v.distance;
      g < c && (l = v, c = g);
    }
  }
  return l;
}
function JU(s, e, t, i, n = null) {
  const { group: r, activeTiles: a } = s, { scene: o, boundingVolume: l } = e.cached;
  if (s.ensureChildrenArePreprocessed(e), n === null && (n = qU, Jx.copy(r.matrixWorld).invert(), n.copy(t.ray).applyMatrix4(Jx)), !e.__used || !l.intersectsRay(n))
    return;
  a.has(e) && ZU(o, t, i);
  const c = e.children;
  for (let h = 0, u = c.length; h < u; h++)
    JU(s, c[h], t, i, n);
}
const dy = new _(), fy = new _(), z0 = new _();
class RD {
  constructor(e = new zt(), t = new et()) {
    this.box = e.clone(), this.transform = t.clone(), this.inverseTransform = new et(), this.points = new Array(8).fill().map(() => new _()), this.planes = new Array(6).fill().map(() => new ln());
  }
  update() {
    const { points: e, inverseTransform: t, transform: i, box: n } = this;
    t.copy(i).invert();
    const { min: r, max: a } = n;
    let o = 0;
    for (let l = -1; l <= 1; l += 2)
      for (let c = -1; c <= 1; c += 2)
        for (let h = -1; h <= 1; h += 2)
          e[o].set(
            l < 0 ? r.x : a.x,
            c < 0 ? r.y : a.y,
            h < 0 ? r.z : a.z
          ).applyMatrix4(i), o++;
    this.updatePlanes();
  }
  updatePlanes() {
    dy.copy(this.box.min).applyMatrix4(this.transform), fy.copy(this.box.max).applyMatrix4(this.transform), z0.set(0, 0, 1).transformDirection(this.transform), this.planes[0].setFromNormalAndCoplanarPoint(z0, dy), this.planes[1].setFromNormalAndCoplanarPoint(z0, fy).negate(), z0.set(0, 1, 0).transformDirection(this.transform), this.planes[2].setFromNormalAndCoplanarPoint(z0, dy), this.planes[3].setFromNormalAndCoplanarPoint(z0, fy).negate(), z0.set(1, 0, 0).transformDirection(this.transform), this.planes[4].setFromNormalAndCoplanarPoint(z0, dy), this.planes[5].setFromNormalAndCoplanarPoint(z0, fy).negate();
  }
  // based on three.js' Box3 "intersects frustum" function
  intersectsFrustum(e) {
    const { points: t } = this, { planes: i } = e;
    for (let n = 0; n < 6; n++) {
      const r = i[n];
      let a = -1 / 0;
      for (let o = 0; o < 8; o++) {
        const l = t[o], c = r.distanceToPoint(l);
        a = a < c ? c : a;
      }
      if (a < 0)
        return !1;
    }
    for (let n = 0; n < 6; n++) {
      const r = this.planes[n];
      let a = -1 / 0;
      for (let o = 0; o < 8; o++) {
        const l = e.points[o], c = r.distanceToPoint(l);
        a = a < c ? c : a;
      }
      if (a < 0)
        return !1;
    }
    return !0;
  }
}
new Ds();
new _();
function u5e(s) {
  const { x: e, y: t, z: i } = s;
  s.x = i, s.y = e, s.z = t;
}
function d5e(s) {
  return -s + Math.PI / 2;
}
const DD = new Ds(), nc = new _(), Yr = new _(), py = new _(), f5e = new _(), my = new et(), L6 = new Tn(), LD = new _(), I6 = new _(), O6 = new _(), ID = new _(), OD = new g0(), p5e = 1e-12, m5e = 0.1;
class QU {
  constructor(e = 1, t = 1, i = 1) {
    this.radius = new _(e, t, i);
  }
  intersectRay(e, t) {
    return my.makeScale(...this.radius).invert(), L6.center.set(0, 0, 0), L6.radius = 1, OD.copy(e).applyMatrix4(my), OD.intersectSphere(L6, t) ? (my.makeScale(...this.radius), t.applyMatrix4(my), t) : null;
  }
  // returns a frame with Z indicating altitude
  // Y pointing north
  // X pointing east
  constructLatLonFrame(e, t, i) {
    return this.getCartographicToPosition(e, t, 0, ID), this.getCartographicToNormal(e, t, O6), this.getNorthernTangent(e, t, I6), LD.crossVectors(I6, O6), i.makeBasis(LD, I6, O6).setPosition(ID);
  }
  getNorthernTangent(e, t, i, n = f5e) {
    let r = 1, a = e + 1e-7;
    e > Math.PI / 4 && (r = -1, a = e - 1e-7);
    const o = this.getCartographicToNormal(e, t, Yr).normalize(), l = this.getCartographicToNormal(a, t, py).normalize();
    return n.crossVectors(o, l).normalize().multiplyScalar(r), i.crossVectors(n, o).normalize();
  }
  getCartographicToPosition(e, t, i, n) {
    this.getCartographicToNormal(e, t, nc);
    const r = this.radius;
    Yr.copy(nc), Yr.x *= r.x ** 2, Yr.y *= r.y ** 2, Yr.z *= r.z ** 2;
    const a = Math.sqrt(nc.dot(Yr));
    return Yr.divideScalar(a), n.copy(Yr).addScaledVector(nc, i);
  }
  getPositionToCartographic(e, t) {
    this.getPositionToSurfacePoint(e, Yr), this.getPositionToNormal(e, nc);
    const i = py.subVectors(e, Yr);
    return t.lon = Math.atan2(nc.y, nc.x), t.lat = Math.asin(nc.z), t.height = Math.sign(i.dot(e)) * i.length(), t;
  }
  getCartographicToNormal(e, t, i) {
    return DD.set(1, d5e(e), t), i.setFromSpherical(DD).normalize(), u5e(i), i;
  }
  getPositionToNormal(e, t) {
    const i = this.radius;
    return t.copy(e), t.x /= i.x ** 2, t.y /= i.y ** 2, t.z /= i.z ** 2, t.normalize(), t;
  }
  getPositionToSurfacePoint(e, t) {
    const i = this.radius, n = 1 / i.x ** 2, r = 1 / i.y ** 2, a = 1 / i.z ** 2, o = e.x * e.x * n, l = e.y * e.y * r, c = e.z * e.z * a, h = o + l + c, u = Math.sqrt(1 / h), d = Yr.copy(e).multiplyScalar(u);
    if (h < m5e)
      return isFinite(u) ? t.copy(d) : null;
    const f = py.set(
      d.x * n * 2,
      d.y * r * 2,
      d.z * a * 2
    );
    let p = (1 - u) * e.length() / (0.5 * f.length()), v = 0, g, m, x, y, A, b, M, w, C, T, E;
    do {
      p -= v, x = 1 / (1 + p * n), y = 1 / (1 + p * r), A = 1 / (1 + p * a), b = x * x, M = y * y, w = A * A, C = b * x, T = M * y, E = w * A, g = o * b + l * M + c * w - 1, m = o * C * n + l * T * r + c * E * a;
      const P = -2 * m;
      v = g / P;
    } while (Math.abs(g) > p5e);
    return t.set(
      e.x * x,
      e.y * y,
      e.z * A
    );
  }
  calculateHorizonDistance(e, t) {
    const i = this.calculateEffectiveRadius(e);
    return Math.sqrt(2 * i * t + t ** 2);
  }
  calculateEffectiveRadius(e) {
    const t = this.radius.x, n = 1 - this.radius.z ** 2 / t ** 2, r = e * nt.DEG2RAD, a = Math.sin(r) ** 2;
    return t / Math.sqrt(1 - n * a);
  }
  getPositionElevation(e) {
    this.getPositionToSurfacePoint(e, Yr);
    const t = py.subVectors(e, Yr);
    return Math.sign(t.dot(e)) * t.length();
  }
}
const ac = Math.PI, gy = ac / 2, e1 = new _(), of = new _(), lf = new _(), ND = new et();
let v1 = 0;
const N6 = [];
function g5e(s = !1) {
  return s ? (N6[v1] || (N6[v1] = new _()), v1++, N6[v1 - 1]) : new _();
}
function BD() {
  v1 = 0;
}
class v5e extends QU {
  constructor(e, t, i, n = -gy, r = gy, a = 0, o = 2 * ac, l = 0, c = 0) {
    super(e, t, i), this.latStart = n, this.latEnd = r, this.lonStart = a, this.lonEnd = o, this.heightStart = l, this.heightEnd = c;
  }
  _getPoints(e = !1) {
    const {
      latStart: t,
      latEnd: i,
      lonStart: n,
      lonEnd: r,
      heightStart: a,
      heightEnd: o
    } = this, l = nt.mapLinear(0.5, 0, 1, t, i), c = nt.mapLinear(0.5, 0, 1, n, r), h = Math.floor(n / gy) * gy, u = [
      [-ac / 2, 0],
      [ac / 2, 0],
      [0, h],
      [0, h + ac / 2],
      [0, h + ac],
      [0, h + 3 * ac / 2],
      [t, r],
      [i, r],
      [t, n],
      [i, n],
      [0, n],
      [0, r],
      [l, c],
      [t, c],
      [i, c],
      [l, n],
      [l, r]
    ], d = [], f = u.length;
    for (let p = 0; p <= 1; p++) {
      const v = nt.mapLinear(p, 0, 1, a, o);
      for (let g = 0, m = f; g < m; g++) {
        const [x, y] = u[g];
        if (x >= t && x <= i && y >= n && y <= r) {
          const A = g5e(e);
          d.push(A), this.getCartographicToPosition(x, y, v, A);
        }
      }
    }
    return d;
  }
  getBoundingBox(e, t) {
    BD();
    const {
      latStart: i,
      latEnd: n,
      lonStart: r,
      lonEnd: a
    } = this;
    if (n - i < ac / 2) {
      const c = nt.mapLinear(0.5, 0, 1, i, n), h = nt.mapLinear(0.5, 0, 1, r, a);
      this.getCartographicToNormal(c, h, lf), of.set(0, 0, 1), e1.crossVectors(of, lf), of.crossVectors(e1, lf), t.makeBasis(e1, of, lf);
    } else
      e1.set(1, 0, 0), of.set(0, 1, 0), lf.set(0, 0, 1), t.makeBasis(e1, of, lf);
    ND.copy(t).invert();
    const l = this._getPoints(!0);
    for (let c = 0, h = l.length; c < h; c++)
      l[c].applyMatrix4(ND);
    e.makeEmpty(), e.setFromPoints(l);
  }
  getBoundingSphere(e, t) {
    BD();
    const i = this._getPoints(!0);
    e.makeEmpty(), e.setFromPoints(i, t);
  }
}
const k0 = new _(), G0 = new _(), H0 = new _(), FD = new _(), UD = new _(), zD = new _(), cf = new g0();
class y5e {
  constructor() {
    this.sphere = null, this.obb = null, this.region = null, this.regionObb = null;
  }
  intersectsRay(e) {
    const t = this.sphere, i = this.obb || this.regionObb;
    return !(t && !e.intersectsSphere(t) || i && (cf.copy(e).applyMatrix4(i.inverseTransform), !cf.intersectsBox(i.box)));
  }
  intersectRay(e, t = null) {
    const i = this.sphere, n = this.obb || this.regionObb;
    let r = -1 / 0, a = -1 / 0;
    i && e.intersectSphere(i, UD) && (r = i.containsPoint(e.origin) ? 0 : e.origin.distanceToSquared(UD)), n && (cf.copy(e).applyMatrix4(n.inverseTransform), cf.intersectBox(n.box, zD) && (a = n.box.containsPoint(cf.origin) ? 0 : cf.origin.distanceToSquared(zD)));
    const o = Math.max(r, a);
    return o === -1 / 0 ? null : (e.at(Math.sqrt(o), t), t);
  }
  distanceToPoint(e) {
    const t = this.sphere, i = this.obb || this.regionObb;
    let n = -1 / 0, r = -1 / 0;
    return t && (n = Math.max(t.distanceToPoint(e), 0)), i && (FD.copy(e).applyMatrix4(i.inverseTransform), r = i.box.distanceToPoint(FD)), n > r ? n : r;
  }
  intersectsFrustum(e) {
    const t = this.obb || this.regionObb, i = this.sphere;
    return i && !e.intersectsSphere(i) || t && !t.intersectsFrustum(e) ? !1 : !!(i || t);
  }
  getOBB(e, t) {
    const i = this.obb || this.regionObb;
    i ? (e.copy(i.box), t.copy(i.transform)) : (this.getAABB(e), t.identity());
  }
  getAABB(e) {
    if (this.sphere)
      this.sphere.getBoundingBox(e);
    else {
      const t = this.obb || this.regionObb;
      e.copy(t.box).applyMatrix4(t.transform);
    }
  }
  getSphere(e) {
    if (this.sphere)
      e.copy(this.sphere);
    else if (this.region)
      this.region.getBoundingSphere(e);
    else {
      const t = this.obb || this.regionObb;
      t.box.getBoundingSphere(e), e.applyMatrix4(t.transform);
    }
  }
  setObbData(e, t) {
    const i = new RD();
    k0.set(e[3], e[4], e[5]), G0.set(e[6], e[7], e[8]), H0.set(e[9], e[10], e[11]);
    const n = k0.length(), r = G0.length(), a = H0.length();
    k0.normalize(), G0.normalize(), H0.normalize(), n === 0 && k0.crossVectors(G0, H0), r === 0 && G0.crossVectors(k0, H0), a === 0 && H0.crossVectors(k0, G0), i.transform.set(
      k0.x,
      G0.x,
      H0.x,
      e[0],
      k0.y,
      G0.y,
      H0.y,
      e[1],
      k0.z,
      G0.z,
      H0.z,
      e[2],
      0,
      0,
      0,
      1
    ).premultiply(t), i.box.min.set(-n, -r, -a), i.box.max.set(n, r, a), i.update(), this.obb = i;
  }
  setSphereData(e, t, i, n, r) {
    const a = new Tn();
    a.center.set(e, t, i), a.radius = n, a.applyMatrix4(r), this.sphere = a;
  }
  setRegionData(e, t, i, n, r, a) {
    const o = new v5e(
      zp,
      zp,
      IU,
      t,
      n,
      e,
      i,
      r,
      a
    ), l = new RD();
    o.getBoundingBox(l.box, l.transform), l.update(), this.region = o, this.regionObb = l;
  }
}
const x5e = new Gt();
function A5e(s, e, t, i) {
  const n = x5e.set(
    s.normal.x,
    s.normal.y,
    s.normal.z,
    e.normal.x,
    e.normal.y,
    e.normal.z,
    t.normal.x,
    t.normal.y,
    t.normal.z
  );
  return i.set(-s.constant, -e.constant, -t.constant), i.applyMatrix3(n.invert()), i;
}
class b5e extends th {
  constructor() {
    super(), this.points = Array(8).fill().map(() => new _());
  }
  setFromProjectionMatrix(e, t) {
    super.setFromProjectionMatrix(e, t), this.calculateFrustumPoints();
  }
  calculateFrustumPoints() {
    const { planes: e, points: t } = this;
    [
      [e[0], e[3], e[4]],
      // Near top left
      [e[1], e[3], e[4]],
      // Near top right
      [e[0], e[2], e[4]],
      // Near bottom left
      [e[1], e[2], e[4]],
      // Near bottom right
      [e[0], e[3], e[5]],
      // Far top left
      [e[1], e[3], e[5]],
      // Far top right
      [e[0], e[2], e[5]],
      // Far bottom left
      [e[1], e[2], e[5]]
      // Far bottom right
    ].forEach((n, r) => {
      A5e(n[0], n[1], n[2], t[r]);
    });
  }
}
const kD = parseInt(Qo) < 165, $U = Symbol("INITIAL_FRUSTUM_CULLED"), vy = new et(), B6 = new et(), hf = new _(), M5e = new _(1, 0, 0), S5e = new _(0, 1, 0);
function GD(s, e) {
  s.traverse((t) => {
    t.frustumCulled = t[$U] && e;
  });
}
class w5e extends $8e {
  get autoDisableRendererCulling() {
    return this._autoDisableRendererCulling;
  }
  set autoDisableRendererCulling(e) {
    this._autoDisableRendererCulling !== e && (super._autoDisableRendererCulling = e, this.forEachLoadedModel((t) => {
      GD(t, !e);
    }));
  }
  constructor(...e) {
    super(...e), this.group = new l5e(this), this.cameras = [], this.cameraMap = /* @__PURE__ */ new Map(), this.cameraInfo = [], this.activeTiles = /* @__PURE__ */ new Set(), this.visibleTiles = /* @__PURE__ */ new Set(), this.optimizeRaycast = !0, this._autoDisableRendererCulling = !0, this._eventDispatcher = new ra(), this.onLoadTileSet = null, this.onLoadModel = null, this.onDisposeModel = null, this.onTileVisibilityChange = null;
    const t = new g2();
    if (t.setURLModifier((i) => this.preprocessURL ? this.preprocessURL(i) : i), this.manager = t, kD) {
      const i = this;
      this._overridenRaycast = function(n, r) {
        i.optimizeRaycast || Object.getPrototypeOf(this).raycast.call(this, n, r);
      };
    }
  }
  addEventListener(...e) {
    this._eventDispatcher.addEventListener(...e);
  }
  hasEventListener(...e) {
    this._eventDispatcher.hasEventListener(...e);
  }
  removeEventListener(...e) {
    this._eventDispatcher.removeEventListener(...e);
  }
  dispatchEvent(...e) {
    this._eventDispatcher.dispatchEvent(...e);
  }
  /* Public API */
  getBounds(...e) {
    return console.warn("TilesRenderer: getBounds has been renamed to getBoundingBox."), this.getBoundingBox(...e);
  }
  getOrientedBounds(...e) {
    return console.warn("TilesRenderer: getOrientedBounds has been renamed to getOrientedBoundingBox."), this.getOrientedBoundingBox(...e);
  }
  getBoundingBox(e) {
    if (!this.root)
      return !1;
    const t = this.root.cached.boundingVolume;
    return t && t.getAABB(e), !0;
  }
  getOrientedBoundingBox(e, t) {
    if (!this.root)
      return !1;
    const i = this.root.cached.boundingVolume;
    return i && i.getOBB(e, t), !0;
  }
  getBoundingSphere(e) {
    if (!this.root)
      return !1;
    const t = this.root.cached.boundingVolume;
    return t ? (t.getSphere(e), !0) : !1;
  }
  forEachLoadedModel(e) {
    this.traverse((t) => {
      const i = t.cached.scene;
      i && e(i, t);
    });
  }
  raycast(e, t) {
    if (this.root)
      if (e.firstHitOnly) {
        const i = KU(this, this.root, e);
        i && t.push(i);
      } else
        JU(this, this.root, e, t);
  }
  hasCamera(e) {
    return this.cameraMap.has(e);
  }
  setCamera(e) {
    const t = this.cameras, i = this.cameraMap;
    return i.has(e) ? !1 : (i.set(e, new Se()), t.push(e), !0);
  }
  setResolution(e, t, i) {
    const n = this.cameraMap;
    return n.has(e) ? (t instanceof Se ? n.get(e).copy(t) : n.get(e).set(t, i), !0) : !1;
  }
  setResolutionFromRenderer(e, t) {
    const i = this.cameraMap;
    if (!i.has(e))
      return !1;
    const n = i.get(e);
    return t.getSize(n), n.multiplyScalar(t.getPixelRatio()), !0;
  }
  deleteCamera(e) {
    const t = this.cameras, i = this.cameraMap;
    if (i.has(e)) {
      const n = t.indexOf(e);
      return t.splice(n, 1), i.delete(e), !0;
    }
    return !1;
  }
  /* Overriden */
  fetchTileSet(e, ...t) {
    const i = super.fetchTileSet(e, ...t);
    return i.then((n) => {
      queueMicrotask(() => {
        this.dispatchEvent({
          type: "load-tile-set",
          tileSet: n,
          url: e
        }), this.onLoadTileSet && this.onLoadTileSet(n, e);
      });
    }), i;
  }
  update() {
    const e = this.group, t = this.cameras, i = this.cameraMap, n = this.cameraInfo;
    if (t.length === 0) {
      console.warn("TilesRenderer: no cameras defined. Cannot update 3d tiles.");
      return;
    }
    for (; n.length > t.length; )
      n.pop();
    for (; n.length < t.length; )
      n.push({
        frustum: new b5e(),
        isOrthographic: !1,
        sseDenominator: -1,
        // used if isOrthographic:false
        position: new _(),
        invScale: -1,
        pixelSize: 0
        // used if isOrthographic:true
      });
    B6.copy(e.matrixWorld).invert(), hf.setFromMatrixScale(B6);
    const r = hf.x;
    Math.abs(Math.max(hf.x - hf.y, hf.x - hf.z)) > 1e-6 && console.warn("ThreeTilesRenderer : Non uniform scale used for tile which may cause issues when calculating screen space error.");
    for (let a = 0, o = n.length; a < o; a++) {
      const l = t[a], c = n[a], h = c.frustum, u = c.position, d = i.get(l);
      (d.width === 0 || d.height === 0) && console.warn("TilesRenderer: resolution for camera error calculation is not set.");
      const f = l.projectionMatrix.elements;
      if (c.isOrthographic = f[15] === 1, c.isOrthographic) {
        const p = 2 / f[0], v = 2 / f[5];
        c.pixelSize = Math.max(v / d.height, p / d.width);
      } else
        c.sseDenominator = 2 / f[5] / d.height;
      c.invScale = r, vy.copy(e.matrixWorld), vy.premultiply(l.matrixWorldInverse), vy.premultiply(l.projectionMatrix), h.setFromProjectionMatrix(vy), u.set(0, 0, 0), u.applyMatrix4(l.matrixWorld), u.applyMatrix4(B6);
    }
    super.update();
  }
  preprocessNode(e, t, i = null) {
    super.preprocessNode(e, t, i);
    const n = new et();
    if (e.transform) {
      const o = e.transform;
      for (let l = 0; l < 16; l++)
        n.elements[l] = o[l];
    }
    i && n.premultiply(i.cached.transform);
    const r = new et().copy(n).invert(), a = new y5e();
    "sphere" in e.boundingVolume && a.setSphereData(...e.boundingVolume.sphere, n), "box" in e.boundingVolume && a.setObbData(e.boundingVolume.box, n), "region" in e.boundingVolume && a.setRegionData(...e.boundingVolume.region), e.cached = {
      loadIndex: 0,
      transform: n,
      transformInverse: r,
      active: !1,
      inFrustum: [],
      boundingVolume: a,
      scene: null,
      geometry: null,
      materials: null,
      textures: null
    };
  }
  parseTile(e, t, i) {
    t._loadIndex = t._loadIndex || 0, t._loadIndex++;
    const r = t.content.uri.split(/[\\\/]/g);
    r.pop();
    const a = r.join("/"), o = this.fetchOptions, l = this.manager, c = t._loadIndex;
    let h = null;
    const u = this.rootTileSet.asset && this.rootTileSet.asset.gltfUpAxis || "y", d = t.cached, f = d.transform, p = new et();
    switch (u.toLowerCase()) {
      case "x":
        p.makeRotationAxis(S5e, -Math.PI / 2);
        break;
      case "y":
        p.makeRotationAxis(M5e, Math.PI / 2);
        break;
    }
    const v = (kp(e) || i).toLowerCase();
    switch (v) {
      case "b3dm": {
        const m = new VU(l);
        m.workingPath = a, m.fetchOptions = o, m.adjustmentTransform.copy(p), h = m.parse(e);
        break;
      }
      case "pnts": {
        const m = new WU(l);
        m.workingPath = a, m.fetchOptions = o, h = m.parse(e);
        break;
      }
      case "i3dm": {
        const m = new XU(l);
        m.workingPath = a, m.fetchOptions = o, m.adjustmentTransform.copy(p), h = m.parse(e);
        break;
      }
      case "cmpt": {
        const m = new a5e(l);
        m.workingPath = a, m.fetchOptions = o, m.adjustmentTransform.copy(p), h = m.parse(e).then((x) => x.scene);
        break;
      }
      case "gltf":
      case "glb":
        const g = new o5e(l);
        g.workingPath = a, g.fetchOptions = o, h = g.parse(e);
        break;
      default:
        console.warn(`TilesRenderer: Content type "${v}" not supported.`), h = Promise.resolve(null);
        break;
    }
    return h.then((g) => {
      let m, x;
      if (g.isObject3D ? (m = g, x = null) : (m = g.scene, x = g), t._loadIndex !== c)
        return;
      m.updateMatrix(), (v === "glb" || v === "gltf") && m.matrix.multiply(p), m.matrix.premultiply(f), m.matrix.decompose(m.position, m.quaternion, m.scale), m.traverse((M) => {
        M[$U] = M.frustumCulled;
      }), GD(m, !this.autoDisableRendererCulling), kD && m.traverse((M) => {
        M.raycast = this._overridenRaycast;
      });
      const y = [], A = [], b = [];
      m.traverse((M) => {
        if (M.geometry && A.push(M.geometry), M.material) {
          const w = M.material;
          y.push(M.material);
          for (const C in w) {
            const T = w[C];
            T && T.isTexture && b.push(T);
          }
        }
      }), d.materials = y, d.geometry = A, d.textures = b, d.scene = m, d.metadata = x, this.dispatchEvent({
        type: "load-model",
        scene: m,
        tile: t
      }), this.onLoadModel && this.onLoadModel(m, t);
    });
  }
  disposeTile(e) {
    const t = e.cached;
    if (t.scene) {
      const i = t.materials, n = t.geometry, r = t.textures, a = t.scene.parent;
      t.scene.traverse((o) => {
        o.userData.meshFeatures && o.userData.meshFeatures.dispose();
      });
      for (let o = 0, l = n.length; o < l; o++)
        n[o].dispose();
      for (let o = 0, l = i.length; o < l; o++)
        i[o].dispose();
      for (let o = 0, l = r.length; o < l; o++) {
        const c = r[o];
        c.image instanceof ImageBitmap && c.image.close(), c.dispose();
      }
      a && a.remove(t.scene), this.dispatchEvent({
        type: "dispose-model",
        scene: t.scene,
        tile: e
      }), this.onDisposeModel && this.onDisposeModel(t.scene, e), t.scene = null, t.materials = null, t.textures = null, t.geometry = null, t.metadata = null;
    }
    this.activeTiles.delete(e), this.visibleTiles.delete(e), e._loadIndex++;
  }
  setTileVisible(e, t) {
    const i = e.cached.scene, n = this.visibleTiles, r = this.group;
    t ? (r.add(i), n.add(e), i.updateMatrixWorld(!0)) : (r.remove(i), n.delete(e)), this.dispatchEvent({
      type: "tile-visibility-change",
      scene: i,
      tile: e,
      visible: t
    }), this.onTileVisibilityChange && this.onTileVisibilityChange(i, e, t);
  }
  setTileActive(e, t) {
    const i = this.activeTiles;
    t ? i.add(e) : i.delete(e);
  }
  calculateError(e) {
    const t = e.cached, i = t.inFrustum, n = this.cameras, r = this.cameraInfo, a = t.boundingVolume;
    let o = -1 / 0, l = 1 / 0;
    for (let c = 0, h = n.length; c < h; c++) {
      if (!i[c])
        continue;
      const u = r[c], d = u.invScale;
      let f;
      if (u.isOrthographic) {
        const p = u.pixelSize;
        f = e.geometricError / (p * d);
      } else {
        const v = a.distanceToPoint(u.position) * d, g = u.sseDenominator;
        f = e.geometricError / (v * g), l = Math.min(l, v);
      }
      o = Math.max(o, f);
    }
    e.__distanceFromCamera = l, e.__error = o;
  }
  tileInView(e) {
    const t = e.cached, i = t.boundingVolume, n = t.inFrustum, r = this.cameraInfo;
    let a = !1;
    for (let o = 0, l = r.length; o < l; o++) {
      const c = r[o].frustum;
      i.intersectsFrustum(c) ? (a = !0, n[o] = !0) : n[o] = !1;
    }
    return a;
  }
}
const yy = new QU(zp, zp, IU);
class E5e {
  constructor(e) {
    S(this, "tilesRenderer");
    S(this, "camera");
    S(this, "renderer");
    S(this, "_url");
    S(this, "_loader");
    S(this, "_rootTilesetLoaded", !1);
    S(this, "ECEFToLocal", new et());
    S(this, "needUpdate", !1);
    S(this, "root", new ci());
    S(this, "isRootLoaded", !1);
    S(this, "ecefToLocalMatrix", new et());
    S(this, "scene");
    S(this, "heightOffset", 0);
    S(this, "scale", 1);
    this._url = e.url, this.tilesRenderer = new w5e(e.url), this.root.add(this.tilesRenderer.group);
    const t = new J4();
    t.setDecoderPath(st.assetManager.dracoPath);
    const i = new K4(this.tilesRenderer.manager);
    i.setDRACOLoader(t), i.register(() => new jU()), this._loader = i, this.tilesRenderer.manager.addHandler(/\.gltf$/, this._loader);
  }
  get url() {
    return this._url;
  }
  init(e, t) {
    this.camera = e, this.renderer = t, this.tilesRenderer.setCamera(this.camera), this.tilesRenderer.setResolutionFromRenderer(this.camera, this.renderer), this.tilesRenderer.onLoadTileSet = (i) => {
      if (this._rootTilesetLoaded)
        return;
      this._rootTilesetLoaded = !0;
      const n = new Tn();
      this.tilesRenderer.getBoundingSphere(n);
      const r = n.center.clone(), a = { lon: 0, lat: 0, height: 0 };
      yy.getPositionToCartographic(r, a), this.scene && this.scene.updateTilesetMapCenter(
        a.lat * nt.RAD2DEG,
        a.lon * nt.RAD2DEG
      );
      const o = yy.constructLatLonFrame(a.lat, a.lon, new et());
      o.invert();
      const l = new et();
      l.makeRotationX(-90 * nt.DEG2RAD), o.premultiply(l), this.ECEFToLocal = o.clone(), this.updateTilesetGroupMatrix();
    }, this.tilesRenderer.onLoadModel = function(i) {
      i.traverse((n) => {
        const r = n;
        if (r.receiveProjectVideo = !0, r.material && (r.originalMaterial = r.material, r.material = new bl(), r.material.map = r.originalMaterial.map, r.geometry)) {
          let a = r.geometry.getAttribute("normal");
          s0(a) && r.geometry.computeVertexNormals();
        }
      });
    }, this.tilesRenderer.onDisposeModel = function(i) {
      i.traverse((n) => {
        const r = n;
        r.material && r.material.dispose();
      });
    };
  }
  getCartographicToLocal(e, t, i) {
    const n = new _();
    yy.getCartographicToPosition(
      t * nt.DEG2RAD,
      e * nt.DEG2RAD,
      i ?? 0,
      n
    );
    const r = this.tilesRenderer.group.matrix.clone();
    return n.applyMatrix4(r);
  }
  getLocalToCartographic(e, t, i) {
    const n = new _(e, t, i), r = this.tilesRenderer.group.matrix.clone().invert(), a = n.applyMatrix4(r), o = { lon: 0, lat: 0, height: 0 };
    return yy.getPositionToCartographic(a, o), {
      lon: o.lon * nt.RAD2DEG,
      lat: o.lat * nt.RAD2DEG,
      height: o.height
    };
  }
  set enable(e) {
    e ? this.needUpdate = !0 : (this.needUpdate = !1, this.dispose());
  }
  setUrl(e) {
    this._url !== e && (this.tilesRenderer.dispose(), this.tilesRenderer.rootURL = e), this._url = e;
  }
  updateHeightOffset(e) {
    this.heightOffset = e, this.updateTilesetGroupMatrix();
  }
  updateScale(e) {
    this.scale = e, this.updateTilesetGroupMatrix();
  }
  updateTilesetGroupMatrix() {
    const e = this.ECEFToLocal.clone(), t = new et();
    t.makeTranslation(0, this.heightOffset, 0), e.premultiply(t);
    const i = new et();
    i.makeScale(this.scale, this.scale, this.scale), e.premultiply(i);
    const n = new _(), r = new St(), a = new _();
    e.decompose(n, r, a), this.tilesRenderer.group.position.copy(n), this.tilesRenderer.group.quaternion.copy(r), this.tilesRenderer.group.scale.copy(a);
  }
  update() {
    this.camera.updateMatrixWorld(), this.tilesRenderer.update();
  }
  dispose() {
    this.tilesRenderer.dispose();
  }
}
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function ms(s, e, t, i) {
  function n(r) {
    return r instanceof t ? r : new t(function(a) {
      a(r);
    });
  }
  return new (t || (t = Promise))(function(r, a) {
    function o(h) {
      try {
        c(i.next(h));
      } catch (u) {
        a(u);
      }
    }
    function l(h) {
      try {
        c(i.throw(h));
      } catch (u) {
        a(u);
      }
    }
    function c(h) {
      h.done ? r(h.value) : n(h.value).then(o, l);
    }
    c((i = i.apply(s, [])).next());
  });
}
class j2 {
  constructor() {
    this.name = "", this.minZoom = 0, this.maxZoom = 20, this.bounds = [], this.center = [];
  }
  fetchTile(e, t, i) {
    return null;
  }
  getMetaData() {
    return ms(this, void 0, void 0, function* () {
    });
  }
}
class T5e extends j2 {
  constructor(e = "https://a.tile.openstreetmap.org/") {
    super(), this.address = e, this.format = "png", this.maxZoom = 19;
  }
  fetchTile(e, t, i) {
    return new Promise((n, r) => {
      const a = document.createElement("img");
      a.onload = function() {
        n(a);
      }, a.onerror = function() {
        r();
      }, a.crossOrigin = "Anonymous", a.src = this.address + e + "/" + t + "/" + i + "." + this.format;
    });
  }
}
class zw {
  static createOffscreenCanvas(e, t) {
    if (typeof OffscreenCanvas < "u")
      return new OffscreenCanvas(e, t);
    {
      let i = document.createElement("canvas");
      return i.width = e, i.height = t, i;
    }
  }
}
class ez {
  static createFillTexture(e = "#000000", t = 1, i = 1) {
    const n = zw.createOffscreenCanvas(t, i), r = n.getContext("2d");
    r.fillStyle = e, r.fillRect(0, 0, t, i);
    const a = new Ut(n);
    return a.format = dn, a.magFilter = ii, a.minFilter = ii, a.generateMipmaps = !1, a.needsUpdate = !0, a;
  }
}
class zn {
}
zn.root = -1;
zn.topLeft = 0;
zn.topRight = 1;
zn.bottomLeft = 2;
zn.bottomRight = 3;
class ir extends Re {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0, o = null, l = null) {
    super(o, l), this.mapView = null, this.parentNode = null, this.subdivided = !1, this.disposed = !1, this.nodesLoaded = 0, this.childrenCache = null, this.isMesh = !0, this.mapView = t, this.parentNode = e, this.disposed = !1, this.location = i, this.level = n, this.x = r, this.y = a, this.initialize();
  }
  initialize() {
    return ms(this, void 0, void 0, function* () {
    });
  }
  createChildNodes() {
  }
  subdivide() {
    const e = this.mapView.maxZoom();
    this.children.length > 0 || this.level + 1 > e || this.parentNode !== null && this.parentNode.nodesLoaded < ir.childrens || (this.mapView.cacheTiles && this.childrenCache !== null ? (this.isMesh = !1, this.children = this.childrenCache, this.nodesLoaded = this.childrenCache.length) : this.createChildNodes(), this.subdivided = !0);
  }
  simplify() {
    const e = this.mapView.minZoom();
    if (!(this.level - 1 < e)) {
      if (this.mapView.cacheTiles)
        this.childrenCache = this.children;
      else
        for (let t = 0; t < this.children.length; t++)
          this.children[t].dispose();
      this.subdivided = !1, this.isMesh = !0, this.children = [], this.nodesLoaded = 0;
    }
  }
  loadData() {
    return ms(this, void 0, void 0, function* () {
      if (this.level < this.mapView.provider.minZoom || this.level > this.mapView.provider.maxZoom) {
        console.warn("Geo-Three: Loading tile outside of provider range.", this), this.material.map = ir.defaultTexture, this.material.needsUpdate = !0;
        return;
      }
      try {
        const e = yield this.mapView.provider.fetchTile(this.level, this.x, this.y);
        yield this.applyTexture(e);
      } catch {
        if (this.disposed)
          return;
        console.warn("Geo-Three: Failed to load node tile data.", this), this.material.map = ir.defaultTexture;
      }
      this.material.needsUpdate = !0;
    });
  }
  applyTexture(e) {
    return ms(this, void 0, void 0, function* () {
      if (this.disposed)
        return;
      const t = new Ut(e);
      parseInt(Qo) >= 152 && (t.colorSpace = "srgb"), t.generateMipmaps = !1, t.format = dn, t.magFilter = ii, t.minFilter = ii, t.needsUpdate = !0, this.material.map = t;
    });
  }
  nodeReady() {
    if (this.disposed) {
      console.warn("Geo-Three: nodeReady() called for disposed node.", this), this.dispose();
      return;
    }
    if (this.parentNode !== null) {
      if (this.parentNode.nodesLoaded++, this.parentNode.nodesLoaded === ir.childrens) {
        this.parentNode.subdivided === !0 && (this.parentNode.isMesh = !1);
        for (let e = 0; e < this.parentNode.children.length; e++)
          this.parentNode.children[e].visible = !0;
      }
      this.parentNode.nodesLoaded > ir.childrens && console.error("Geo-Three: Loaded more children objects than expected.", this.parentNode.nodesLoaded, this);
    } else
      this.visible = !0;
  }
  dispose() {
    this.disposed = !0;
    const e = this;
    try {
      const t = e.material;
      t.dispose(), t.map && t.map !== ir.defaultTexture && t.map.dispose();
    } catch {
    }
    try {
      e.geometry.dispose();
    } catch {
    }
  }
}
ir.defaultTexture = ez.createFillTexture();
ir.baseGeometry = null;
ir.baseScale = null;
ir.childrens = 4;
class wl extends Pt {
  constructor(e = 1, t = 1, i = 1, n = 1, r = !1, a = 10) {
    super();
    const o = [], l = [], c = [], h = [];
    wl.buildPlane(e, t, i, n, o, l, c, h), r && wl.buildSkirt(e, t, i, n, a, o, l, c, h), this.setIndex(o), this.setAttribute("position", new gt(l, 3)), this.setAttribute("normal", new gt(c, 3)), this.setAttribute("uv", new gt(h, 2));
  }
  static buildPlane(e = 1, t = 1, i = 1, n = 1, r, a, o, l) {
    const c = e / 2, h = t / 2, u = i + 1, d = n + 1, f = e / i, p = t / n;
    for (let v = 0; v < d; v++) {
      const g = v * p - h;
      for (let m = 0; m < u; m++) {
        const x = m * f - c;
        a.push(x, 0, g), o.push(0, 1, 0), l.push(m / i, 1 - v / n);
      }
    }
    for (let v = 0; v < n; v++)
      for (let g = 0; g < i; g++) {
        const m = g + u * v, x = g + u * (v + 1), y = g + 1 + u * (v + 1), A = g + 1 + u * v;
        r.push(m, x, A, x, y, A);
      }
  }
  static buildSkirt(e = 1, t = 1, i = 1, n = 1, r, a, o, l, c) {
    const h = e / 2, u = t / 2, d = i + 1, f = n + 1, p = e / i, v = t / n;
    let g = o.length / 3;
    for (let x = 0; x < d; x++) {
      const y = x * p - h, A = -u;
      o.push(y, -r, A), l.push(0, 1, 0), c.push(x / i, 1);
    }
    for (let x = 0; x < i; x++) {
      const y = x, A = x + 1, b = x + g, M = x + g + 1;
      a.push(A, b, y, A, M, b);
    }
    g = o.length / 3;
    for (let x = 0; x < d; x++) {
      const y = x * p - h, A = n * v - u;
      o.push(y, -r, A), l.push(0, 1, 0), c.push(x / i, 0);
    }
    let m = d * f - i - 1;
    for (let x = 0; x < i; x++) {
      const y = m + x, A = m + x + 1, b = x + g, M = x + g + 1;
      a.push(y, b, A, b, M, A);
    }
    g = o.length / 3;
    for (let x = 0; x < f; x++) {
      const y = x * v - u, A = -h;
      o.push(A, -r, y), l.push(0, 1, 0), c.push(0, 1 - x / n);
    }
    for (let x = 0; x < n; x++) {
      const y = x * f, A = (x + 1) * f, b = x + g, M = x + g + 1;
      a.push(y, b, A, b, M, A);
    }
    g = o.length / 3;
    for (let x = 0; x < f; x++) {
      const y = x * v - u, A = i * p - h;
      o.push(A, -r, y), l.push(0, 1, 0), c.push(1, 1 - x / n);
    }
    for (let x = 0; x < n; x++) {
      const y = x * f + n, A = (x + 1) * f + n, b = x + g, M = x + g + 1;
      a.push(A, b, y, A, M, b);
    }
  }
}
class F6 {
  constructor(e, t) {
    this.latitude = e, this.longitude = t;
  }
}
class mi {
  static datumsToSpherical(e, t) {
    const i = t * mi.EARTH_ORIGIN / 180;
    let n = Math.log(Math.tan((90 + e) * Math.PI / 360)) / (Math.PI / 180);
    return n = n * mi.EARTH_ORIGIN / 180, new Se(i, n);
  }
  static sphericalToDatums(e, t) {
    const i = e / mi.EARTH_ORIGIN * 180;
    let n = t / mi.EARTH_ORIGIN * 180;
    return n = 180 / Math.PI * (2 * Math.atan(Math.exp(n * Math.PI / 180)) - Math.PI / 2), new F6(n, i);
  }
  static quadtreeToDatums(e, t, i) {
    const n = Math.pow(2, e), r = t / n * 360 - 180, o = 180 * (Math.atan(Math.sinh(Math.PI * (1 - 2 * i / n))) / Math.PI);
    return new F6(o, r);
  }
  static vectorToDatums(e) {
    const t = 180 / Math.PI, i = Math.atan2(e.y, Math.sqrt(Math.pow(e.x, 2) + Math.pow(-e.z, 2))) * t, n = Math.atan2(-e.z, e.x) * t;
    return new F6(i, n);
  }
  static datumsToVector(e, t) {
    const i = Math.PI / 180, n = t * i, r = e * i;
    var a = Math.cos(r);
    return new _(-Math.cos(n + Math.PI) * a, Math.sin(r), Math.sin(n + Math.PI) * a);
  }
  static mapboxAltitude(e) {
    return (e.r * 255 * 65536 + e.g * 255 * 256 + e.b * 255) * 0.1 - 1e4;
  }
  static getTileSize(e) {
    const t = mi.WEB_MERCATOR_MAX_EXTENT, i = Math.pow(2, e);
    return 2 * t / i;
  }
  static tileBounds(e, t, i) {
    const n = mi.getTileSize(e), r = -mi.WEB_MERCATOR_MAX_EXTENT + t * n, a = mi.WEB_MERCATOR_MAX_EXTENT - (i + 1) * n;
    return [r, n, a, n];
  }
  static webMercatorToLatitude(e, t) {
    const i = mi.WEB_MERCATOR_MAX_EXTENT - t * mi.getTileSize(e);
    return Math.atan(Math.sinh(i / mi.EARTH_RADIUS));
  }
  static webMercatorToLongitude(e, t) {
    return (-mi.WEB_MERCATOR_MAX_EXTENT + t * mi.getTileSize(e)) / mi.EARTH_RADIUS;
  }
}
mi.EARTH_RADIUS = 6371008;
mi.EARTH_RADIUS_A = 6378137;
mi.EARTH_RADIUS_B = 6356752314245e-6;
mi.EARTH_PERIMETER = 2 * Math.PI * mi.EARTH_RADIUS;
mi.EARTH_ORIGIN = mi.EARTH_PERIMETER / 2;
mi.WEB_MERCATOR_MAX_EXTENT = 2003750834e-2;
class Ra extends ir {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0) {
    super(e, t, i, n, r, a, Ra.geometry, new kt({ wireframe: !1 })), this.matrixAutoUpdate = !1, this.isMesh = !0, this.visible = !1;
  }
  initialize() {
    const e = Object.create(null, {
      initialize: { get: () => super.initialize }
    });
    return ms(this, void 0, void 0, function* () {
      e.initialize.call(this), yield this.loadData(), this.nodeReady();
    });
  }
  createChildNodes() {
    const e = this.level + 1, t = this.x * 2, i = this.y * 2, n = Object.getPrototypeOf(this).constructor;
    let r = new n(this, this.mapView, zn.topLeft, e, t, i);
    r.scale.set(0.5, 1, 0.5), r.position.set(-0.25, 0, -0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new n(this, this.mapView, zn.topRight, e, t + 1, i), r.scale.set(0.5, 1, 0.5), r.position.set(0.25, 0, -0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new n(this, this.mapView, zn.bottomLeft, e, t, i + 1), r.scale.set(0.5, 1, 0.5), r.position.set(-0.25, 0, 0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new n(this, this.mapView, zn.bottomRight, e, t + 1, i + 1), r.scale.set(0.5, 1, 0.5), r.position.set(0.25, 0, 0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0);
  }
  raycast(e, t) {
    this.isMesh === !0 && super.raycast(e, t);
  }
}
Ra.geometry = new wl(1, 1, 1, 1, !1);
Ra.baseGeometry = Ra.geometry;
Ra.baseScale = new _(mi.EARTH_PERIMETER, 1, mi.EARTH_PERIMETER);
class C5e extends Pt {
  constructor(e = 1, t = 1, i = 1, n = 1, r = !1, a = 10, o = null, l = !0) {
    super();
    const c = [], h = [], u = [], d = [];
    wl.buildPlane(e, t, i, n, c, h, u, d);
    const f = o.data;
    for (let p = 0, v = 0; p < f.length && v < h.length; p += 4, v += 3) {
      const g = f[p], m = f[p + 1], x = f[p + 2], y = (g * 65536 + m * 256 + x) * 0.1 - 1e4;
      h[v + 1] = y;
    }
    r && wl.buildSkirt(e, t, i, n, a, c, h, u, d), this.setIndex(c), this.setAttribute("position", new gt(h, 3)), this.setAttribute("normal", new gt(u, 3)), this.setAttribute("uv", new gt(d, 2)), l && this.computeNormals(i, n);
  }
  computeNormals(e, t) {
    const i = this.getAttribute("position");
    if (i !== void 0) {
      let n = this.getAttribute("normal");
      const r = t * e;
      for (let v = 0; v < r; v++)
        n.setXYZ(v, 0, 0, 0);
      const a = new _(), o = new _(), l = new _(), c = new _(), h = new _(), u = new _(), d = new _(), f = new _(), p = t * e * 6;
      for (let v = 0; v < p; v += 3) {
        const g = this.index.getX(v + 0), m = this.index.getX(v + 1), x = this.index.getX(v + 2);
        a.fromBufferAttribute(i, g), o.fromBufferAttribute(i, m), l.fromBufferAttribute(i, x), d.subVectors(l, o), f.subVectors(a, o), d.cross(f), c.fromBufferAttribute(n, g), h.fromBufferAttribute(n, m), u.fromBufferAttribute(n, x), c.add(d), h.add(d), u.add(d), n.setXYZ(g, c.x, c.y, c.z), n.setXYZ(m, h.x, h.y, h.z), n.setXYZ(x, u.x, u.y, u.z);
      }
      this.normalizeNormals(), n.needsUpdate = !0;
    }
  }
}
class ao extends ir {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0, o = ao.geometry, l = new m2({ wireframe: !1, color: 16777215 })) {
    super(e, t, i, n, r, a, o, l), this.heightLoaded = !1, this.textureLoaded = !1, this.geometrySize = 16, this.geometryNormals = !1, this.isMesh = !0, this.visible = !1, this.matrixAutoUpdate = !1;
  }
  initialize() {
    const e = Object.create(null, {
      initialize: { get: () => super.initialize }
    });
    return ms(this, void 0, void 0, function* () {
      e.initialize.call(this), yield this.loadData(), yield this.loadHeightGeometry(), this.nodeReady();
    });
  }
  loadData() {
    const e = Object.create(null, {
      loadData: { get: () => super.loadData }
    });
    return ms(this, void 0, void 0, function* () {
      yield e.loadData.call(this), this.textureLoaded = !0;
    });
  }
  loadHeightGeometry() {
    return ms(this, void 0, void 0, function* () {
      if (this.mapView.heightProvider === null)
        throw new Error("GeoThree: MapView.heightProvider provider is null.");
      if (this.level < this.mapView.heightProvider.minZoom || this.level > this.mapView.heightProvider.maxZoom) {
        console.warn("Geo-Three: Loading tile outside of provider range.", this), this.geometry = Ra.baseGeometry;
        return;
      }
      try {
        const e = yield this.mapView.heightProvider.fetchTile(this.level, this.x, this.y);
        if (this.disposed)
          return;
        const t = zw.createOffscreenCanvas(this.geometrySize + 1, this.geometrySize + 1), i = t.getContext("2d");
        i.imageSmoothingEnabled = !1, i.drawImage(e, 0, 0, ao.tileSize, ao.tileSize, 0, 0, t.width, t.height);
        const n = i.getImageData(0, 0, t.width, t.height);
        this.geometry = new C5e(1, 1, this.geometrySize, this.geometrySize, !0, 10, n, !0);
      } catch {
        if (this.disposed)
          return;
        this.geometry = Ra.baseGeometry;
      }
      this.heightLoaded = !0;
    });
  }
  createChildNodes() {
    const e = this.level + 1, t = Object.getPrototypeOf(this).constructor, i = this.x * 2, n = this.y * 2;
    let r = new t(this, this.mapView, zn.topLeft, e, i, n);
    r.scale.set(0.5, 1, 0.5), r.position.set(-0.25, 0, -0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new t(this, this.mapView, zn.topRight, e, i + 1, n), r.scale.set(0.5, 1, 0.5), r.position.set(0.25, 0, -0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new t(this, this.mapView, zn.bottomLeft, e, i, n + 1), r.scale.set(0.5, 1, 0.5), r.position.set(-0.25, 0, 0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0), r = new t(this, this.mapView, zn.bottomRight, e, i + 1, n + 1), r.scale.set(0.5, 1, 0.5), r.position.set(0.25, 0, 0.25), this.add(r), r.updateMatrix(), r.updateMatrixWorld(!0);
  }
  raycast(e, t) {
    this.isMesh === !0 && super.raycast(e, t);
  }
}
ao.tileSize = 256;
ao.geometry = new wl(1, 1, 1, 1);
ao.baseGeometry = Ra.geometry;
ao.baseScale = new _(mi.EARTH_PERIMETER, 1, mi.EARTH_PERIMETER);
class tz extends Pt {
  constructor(e, t, i, n, r, a, o) {
    super();
    const l = a + o;
    let c = 0;
    const h = [], u = new _(), d = new _(), f = [], p = [], v = [], g = [];
    for (let m = 0; m <= i; m++) {
      const x = [], y = m / i;
      for (let A = 0; A <= t; A++) {
        const b = A / t;
        u.x = -e * Math.cos(n + b * r) * Math.sin(a + y * o), u.y = e * Math.cos(a + y * o), u.z = e * Math.sin(n + b * r) * Math.sin(a + y * o), p.push(u.x, u.y, u.z), d.set(u.x, u.y, u.z).normalize(), v.push(d.x, d.y, d.z), g.push(b, 1 - y), x.push(c++);
      }
      h.push(x);
    }
    for (let m = 0; m < i; m++)
      for (let x = 0; x < t; x++) {
        const y = h[m][x + 1], A = h[m][x], b = h[m + 1][x], M = h[m + 1][x + 1];
        (m !== 0 || a > 0) && f.push(y, A, M), (m !== i - 1 || l < Math.PI) && f.push(A, b, M);
      }
    this.setIndex(f), this.setAttribute("position", new gt(p, 3)), this.setAttribute("normal", new gt(v, 3)), this.setAttribute("uv", new gt(g, 2));
  }
}
class Hu extends ir {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0) {
    let o = mi.tileBounds(n, r, a);
    const l = `
		varying vec3 vPosition;

		void main() {
			vPosition = position;
			gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
		}
		`, c = `
		#define PI 3.1415926538
		varying vec3 vPosition;
		uniform sampler2D uTexture;
		uniform vec4 webMercatorBounds;

		void main() {
			// this could also be a constant, but for some reason using a constant causes more visible tile gaps at high zoom
			float radius = length(vPosition);

			float latitude = asin(vPosition.y / radius);
			float longitude = atan(-vPosition.z, vPosition.x);

			float web_mercator_x = radius * longitude;
			float web_mercator_y = radius * log(tan(PI / 4.0 + latitude / 2.0));
			float y = (web_mercator_y - webMercatorBounds.z) / webMercatorBounds.w;
			float x = (web_mercator_x - webMercatorBounds.x) / webMercatorBounds.y;

			vec4 color = texture2D(uTexture, vec2(x, y));
			gl_FragColor = color;
			${parseInt(Qo) < 152 ? "" : `
				#include <tonemapping_fragment>
				#include ${parseInt(Qo) >= 154 ? "<colorspace_fragment>" : "<encodings_fragment>"}
				`}
		}
		`;
    let h = new Yt(...o);
    const u = new Jt({
      uniforms: { uTexture: { value: new Ut() }, webMercatorBounds: { value: h } },
      vertexShader: l,
      fragmentShader: c
    });
    super(e, t, i, n, r, a, Hu.createGeometry(n, r, a), u), this.applyScaleNode(), this.matrixAutoUpdate = !1, this.isMesh = !0, this.visible = !1;
  }
  initialize() {
    const e = Object.create(null, {
      initialize: { get: () => super.initialize }
    });
    return ms(this, void 0, void 0, function* () {
      e.initialize.call(this), yield this.loadData(), this.nodeReady();
    });
  }
  static createGeometry(e, t, i) {
    const n = Math.pow(2, e), r = 40, a = Math.floor(Hu.segments * (r / (e + 1)) / r), o = t > 0 ? mi.webMercatorToLongitude(e, t) + Math.PI : 0, l = t < n - 1 ? mi.webMercatorToLongitude(e, t + 1) + Math.PI : 2 * Math.PI, c = o, h = l - o, u = i > 0 ? mi.webMercatorToLatitude(e, i) : Math.PI / 2, d = i < n - 1 ? mi.webMercatorToLatitude(e, i + 1) : -Math.PI / 2, f = u - d, p = Math.PI - (u + Math.PI / 2);
    return new tz(1, a, a, c, h, p, f);
  }
  applyTexture(e) {
    return ms(this, void 0, void 0, function* () {
      const i = new rh().load(e.src, function() {
        parseInt(Qo) >= 152 && (i.colorSpace = "srgb");
      });
      this.material.uniforms.uTexture.value = i, this.material.uniforms.uTexture.needsUpdate = !0;
    });
  }
  applyScaleNode() {
    this.geometry.computeBoundingBox();
    const t = this.geometry.boundingBox.clone().getCenter(new _()), i = new et();
    i.compose(new _(-t.x, -t.y, -t.z), new St(), new _(mi.EARTH_RADIUS, mi.EARTH_RADIUS, mi.EARTH_RADIUS)), this.geometry.applyMatrix4(i), this.position.copy(t), this.updateMatrix(), this.updateMatrixWorld();
  }
  updateMatrix() {
    this.matrix.setPosition(this.position), this.matrixWorldNeedsUpdate = !0;
  }
  updateMatrixWorld(e = !1) {
    (this.matrixWorldNeedsUpdate || e) && (this.matrixWorld.copy(this.matrix), this.matrixWorldNeedsUpdate = !1);
  }
  createChildNodes() {
    const e = this.level + 1, t = this.x * 2, i = this.y * 2, n = Object.getPrototypeOf(this).constructor;
    let r = new n(this, this.mapView, zn.topLeft, e, t, i);
    this.add(r), r = new n(this, this.mapView, zn.topRight, e, t + 1, i), this.add(r), r = new n(this, this.mapView, zn.bottomLeft, e, t, i + 1), this.add(r), r = new n(this, this.mapView, zn.bottomRight, e, t + 1, i + 1), this.add(r);
  }
  raycast(e, t) {
    this.isMesh === !0 && super.raycast(e, t);
  }
}
Hu.baseGeometry = new tz(mi.EARTH_RADIUS, 64, 64, 0, 2 * Math.PI, 0, Math.PI);
Hu.baseScale = new _(1, 1, 1);
Hu.segments = 80;
class er extends ao {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0) {
    const o = er.prepareMaterial(new m2({ map: ir.defaultTexture, color: 16777215 }));
    super(e, t, i, n, r, a, er.geometry, o), this.frustumCulled = !1;
  }
  static prepareMaterial(e) {
    return e.userData = { heightMap: { value: er.defaultHeightTexture } }, e.onBeforeCompile = (t) => {
      for (const i in e.userData)
        t.uniforms[i] = e.userData[i];
      t.vertexShader = `
			uniform sampler2D heightMap;
			` + t.vertexShader, t.vertexShader = t.vertexShader.replace("#include <fog_vertex>", `
			#include <fog_vertex>
	
			// Calculate height of the title
			vec4 _theight = texture2D(heightMap, vUv);
			float _height = ((_theight.r * 255.0 * 65536.0 + _theight.g * 255.0 * 256.0 + _theight.b * 255.0) * 0.1) - 10000.0;
			vec3 _transformed = position + _height * normal;
	
			// Vertex position based on height
			gl_Position = projectionMatrix * modelViewMatrix * vec4(_transformed, 1.0);
			`);
    }, e;
  }
  loadData() {
    const e = Object.create(null, {
      loadData: { get: () => super.loadData }
    });
    return ms(this, void 0, void 0, function* () {
      yield e.loadData.call(this), this.textureLoaded = !0;
    });
  }
  loadHeightGeometry() {
    return ms(this, void 0, void 0, function* () {
      if (this.mapView.heightProvider === null)
        throw new Error("GeoThree: MapView.heightProvider provider is null.");
      if (this.level < this.mapView.heightProvider.minZoom || this.level > this.mapView.heightProvider.maxZoom) {
        console.warn("Geo-Three: Loading tile outside of provider range.", this), this.material.map = er.defaultTexture, this.material.needsUpdate = !0;
        return;
      }
      try {
        const e = yield this.mapView.heightProvider.fetchTile(this.level, this.x, this.y);
        if (this.disposed)
          return;
        const t = new Ut(e);
        t.generateMipmaps = !1, t.format = dn, t.magFilter = Ni, t.minFilter = Ni, t.needsUpdate = !0, this.material.userData.heightMap.value = t;
      } catch {
        if (this.disposed)
          return;
        console.error("Geo-Three: Failed to load node tile height data.", this), this.material.userData.heightMap.value = er.defaultHeightTexture;
      }
      this.material.needsUpdate = !0, this.heightLoaded = !0;
    });
  }
  raycast(e, t) {
    this.isMesh === !0 && (this.geometry = Ra.geometry, super.raycast(e, t), this.geometry = er.geometry);
  }
  dispose() {
    super.dispose(), this.material.userData.heightMap.value && this.material.userData.heightMap.value !== er.defaultHeightTexture && this.material.userData.heightMap.value.dispose();
  }
}
er.defaultHeightTexture = ez.createFillTexture("#0186C0");
er.geometrySize = 256;
er.geometry = new wl(1, 1, er.geometrySize, er.geometrySize, !0);
er.baseGeometry = Ra.geometry;
er.baseScale = new _(mi.EARTH_PERIMETER, 1, mi.EARTH_PERIMETER);
class _5e {
  constructor() {
    this.subdivisionRays = 1, this.thresholdUp = 0.6, this.thresholdDown = 0.15, this.raycaster = new lr(), this.mouse = new Se(), this.powerDistance = !1, this.scaleDistance = !0;
  }
  updateLOD(e, t, i, n) {
    const r = [];
    for (let a = 0; a < this.subdivisionRays; a++) {
      this.mouse.set(Math.random() * 2 - 1, Math.random() * 2 - 1), this.raycaster.setFromCamera(this.mouse, t);
      let o = [];
      this.raycaster.intersectObjects(e.children, !0, o), o.length > 0 && r.push(o[0]);
    }
    for (let a = 0; a < r.length; a++) {
      const o = r[a].object;
      let l = r[a].distance;
      if (this.powerDistance && (l = Math.pow(l * 2, o.level)), this.scaleDistance) {
        const c = o.matrixWorld.elements;
        l = new _(c[0], c[1], c[2]).length() / l;
      }
      l > this.thresholdUp ? o.subdivide() : l < this.thresholdDown && o.parentNode && o.parentNode.simplify();
    }
  }
}
class P5e {
  constructor(e = 257) {
    this.gridSize = e;
    const t = e - 1;
    if (t & t - 1)
      throw new Error(`Expected grid size to be 2^n+1, got ${e}.`);
    this.numTriangles = t * t * 2 - 2, this.numParentTriangles = this.numTriangles - t * t, this.indices = new Uint32Array(this.gridSize * this.gridSize), this.coords = new Uint16Array(this.numTriangles * 4);
    for (let i = 0; i < this.numTriangles; i++) {
      let n = i + 2, r = 0, a = 0, o = 0, l = 0, c = 0, h = 0;
      for (n & 1 ? o = l = c = t : r = a = h = t; (n >>= 1) > 1; ) {
        const d = r + o >> 1, f = a + l >> 1;
        n & 1 ? (o = r, l = a, r = c, a = h) : (r = o, a = l, o = c, l = h), c = d, h = f;
      }
      const u = i * 4;
      this.coords[u + 0] = r, this.coords[u + 1] = a, this.coords[u + 2] = o, this.coords[u + 3] = l;
    }
  }
  createTile(e) {
    return new R5e(e, this);
  }
}
class R5e {
  constructor(e, t) {
    const i = t.gridSize;
    if (e.length !== i * i)
      throw new Error(`Expected terrain data of length ${i * i} (${i} x ${i}), got ${e.length}.`);
    this.terrain = e, this.martini = t, this.errors = new Float32Array(e.length), this.update();
  }
  update() {
    const { numTriangles: e, numParentTriangles: t, coords: i, gridSize: n } = this.martini, { terrain: r, errors: a } = this;
    for (let o = e - 1; o >= 0; o--) {
      const l = o * 4, c = i[l + 0], h = i[l + 1], u = i[l + 2], d = i[l + 3], f = c + u >> 1, p = h + d >> 1, v = f + p - h, g = p + c - f, m = (r[h * n + c] + r[d * n + u]) / 2, x = p * n + f, y = Math.abs(m - r[x]);
      if (a[x] = Math.max(a[x], y), o < t) {
        const A = (h + g >> 1) * n + (c + v >> 1), b = (d + g >> 1) * n + (u + v >> 1);
        a[x] = Math.max(a[x], a[A], a[b]);
      }
    }
  }
  getMesh(e = 0, t = !1) {
    const { gridSize: i, indices: n } = this.martini, { errors: r } = this;
    let a = 0, o = 0;
    const l = i - 1;
    let c, h, u = 0;
    const d = [], f = [], p = [], v = [];
    n.fill(0);
    function g(w, C, T, E, P, L) {
      const I = w + T >> 1, N = C + E >> 1;
      Math.abs(w - P) + Math.abs(C - L) > 1 && r[N * i + I] > e ? (g(P, L, w, C, I, N), g(T, E, P, L, I, N)) : (c = C * i + w, h = E * i + T, u = L * i + P, n[c] === 0 && (t && (w === 0 ? d.push(a) : w === l && f.push(a), C === 0 ? p.push(a) : C === l && v.push(a)), n[c] = ++a), n[h] === 0 && (t && (T === 0 ? d.push(a) : T === l && f.push(a), E === 0 ? p.push(a) : E === l && v.push(a)), n[h] = ++a), n[u] === 0 && (t && (P === 0 ? d.push(a) : P === l && f.push(a), L === 0 ? p.push(a) : L === l && v.push(a)), n[u] = ++a), o++);
    }
    g(0, 0, l, l, l, 0), g(l, l, 0, 0, 0, l);
    let m = a * 2, x = o * 3;
    t && (m += (d.length + f.length + p.length + v.length) * 2, x += ((d.length - 1) * 2 + (f.length - 1) * 2 + (p.length - 1) * 2 + (v.length - 1) * 2) * 3);
    const y = new Uint16Array(m), A = new Uint32Array(x);
    let b = 0;
    function M(w, C, T, E, P, L) {
      const I = w + T >> 1, N = C + E >> 1;
      if (Math.abs(w - P) + Math.abs(C - L) > 1 && r[N * i + I] > e)
        M(P, L, w, C, I, N), M(T, E, P, L, I, N);
      else {
        const H = n[C * i + w] - 1, U = n[E * i + T] - 1, W = n[L * i + P] - 1;
        y[2 * H] = w, y[2 * H + 1] = C, y[2 * U] = T, y[2 * U + 1] = E, y[2 * W] = P, y[2 * W + 1] = L, A[b++] = H, A[b++] = U, A[b++] = W;
      }
    }
    if (M(0, 0, l, l, l, 0), M(l, l, 0, 0, 0, l), t) {
      let C = function(T) {
        const E = T.length;
        for (let P = 0; P < E - 1; P++) {
          const L = T[P], I = T[P + 1], N = w / 2, H = (w + 2) / 2;
          y[w++] = y[2 * L], y[w++] = y[2 * L + 1], A[b++] = L, A[b++] = N, A[b++] = I, A[b++] = N, A[b++] = H, A[b++] = I;
        }
        y[w++] = y[2 * T[E - 1]], y[w++] = y[2 * T[E - 1] + 1];
      };
      d.sort((T, E) => y[2 * T + 1] - y[2 * E + 1]), f.sort((T, E) => y[2 * E + 1] - y[2 * T + 1]), p.sort((T, E) => y[2 * E] - y[2 * T]), v.sort((T, E) => y[2 * T] - y[2 * E]);
      let w = a * 2;
      C(d), C(f), C(p), C(v);
    }
    return { vertices: y, triangles: A, numVerticesWithoutSkirts: a };
  }
}
class ba extends ao {
  constructor(e = null, t = null, i = zn.root, n = 0, r = 0, a = 0, { elevationDecoder: o = null, meshMaxError: l = 10, exageration: c = 1 } = {}) {
    super(e, t, i, n, r, a, ba.geometry, ba.prepareMaterial(new m2({
      map: ba.emptyTexture,
      color: 16777215,
      side: ui
    }), n, c)), this.elevationDecoder = {
      rScaler: 256,
      gScaler: 1,
      bScaler: 1 / 256,
      offset: -32768
    }, this.exageration = 1, this.meshMaxError = 10, o && (this.elevationDecoder = o), this.meshMaxError = l, this.exageration = c, this.frustumCulled = !1;
  }
  static prepareMaterial(e, t, i = 1) {
    return e.userData = {
      heightMap: { value: ba.emptyTexture },
      drawNormals: { value: 0 },
      drawBlack: { value: 0 },
      zoomlevel: { value: t },
      computeNormals: { value: 1 },
      drawTexture: { value: 1 }
    }, e.onBeforeCompile = (n) => {
      for (let r in e.userData)
        n.uniforms[r] = e.userData[r];
      n.vertexShader = `
				uniform bool computeNormals;
				uniform float zoomlevel;
				uniform sampler2D heightMap;
				` + n.vertexShader, n.fragmentShader = `
				uniform bool drawNormals;
				uniform bool drawTexture;
				uniform bool drawBlack;
				` + n.fragmentShader, n.fragmentShader = n.fragmentShader.replace("#include <dithering_fragment>", `
				if(drawBlack) {
					gl_FragColor = vec4( 0.0,0.0,0.0, 1.0 );
				} else if(drawNormals) {
					gl_FragColor = vec4( ( 0.5 * vNormal + 0.5 ), 1.0 );
				} else if (!drawTexture) {
					gl_FragColor = vec4( 0.0,0.0,0.0, 0.0 );
				}`), n.vertexShader = n.vertexShader.replace("#include <fog_vertex>", `
				#include <fog_vertex>

				// queried pixels:
				// +-----------+
				// |   |   |   |
				// | a | b | c |
				// |   |   |   |
				// +-----------+
				// |   |   |   |
				// | d | e | f |
				// |   |   |   |
				// +-----------+
				// |   |   |   |
				// | g | h | i |
				// |   |   |   |
				// +-----------+

				if (computeNormals) {
					float e = getElevation(vUv, 0.0);
					ivec2 size = textureSize(heightMap, 0);
					float offset = 1.0 / float(size.x);
					float a = getElevation(vUv + vec2(-offset, -offset), 0.0);
					float b = getElevation(vUv + vec2(0, -offset), 0.0);
					float c = getElevation(vUv + vec2(offset, -offset), 0.0);
					float d = getElevation(vUv + vec2(-offset, 0), 0.0);
					float f = getElevation(vUv + vec2(offset, 0), 0.0);
					float g = getElevation(vUv + vec2(-offset, offset), 0.0);
					float h = getElevation(vUv + vec2(0, offset), 0.0);
					float i = getElevation(vUv + vec2(offset,offset), 0.0);


					float normalLength = 500.0 / zoomlevel;

					vec3 v0 = vec3(0.0, 0.0, 0.0);
					vec3 v1 = vec3(0.0, normalLength, 0.0);
					vec3 v2 = vec3(normalLength, 0.0, 0.0);
					v0.z = (e + d + g + h) / 4.0;
					v1.z = (e+ b + a + d) / 4.0;
					v2.z = (e+ h + i + f) / 4.0;
					vNormal = (normalize(cross(v2 - v0, v1 - v0))).rbg;
				}
				`);
    }, e;
  }
  static getTerrain(e, t, i) {
    const { rScaler: n, bScaler: r, gScaler: a, offset: o } = i, l = t + 1, c = new Float32Array(l * l);
    for (let h = 0, u = 0; u < t; u++)
      for (let d = 0; d < t; d++, h++) {
        const f = h * 4, p = e[f + 0], v = e[f + 1], g = e[f + 2];
        c[h + u] = p * n + v * a + g * r + o;
      }
    for (let h = l * (l - 1), u = 0; u < l - 1; u++, h++)
      c[h] = c[h - l];
    for (let h = l - 1, u = 0; u < l; u++, h += l)
      c[h] = c[h - 1];
    return c;
  }
  static getMeshAttributes(e, t, i, n, r) {
    const a = i + 1, o = e.length / 2, l = new Float32Array(o * 3), c = new Float32Array(o * 2), [h, u, d, f] = n || [0, 0, i, i], p = (d - h) / i, v = (f - u) / i;
    for (let g = 0; g < o; g++) {
      const m = e[g * 2], x = e[g * 2 + 1], y = x * a + m;
      l[3 * g + 0] = m * p + h, l[3 * g + 1] = -t[y] * r, l[3 * g + 2] = -x * v + f, c[2 * g + 0] = m / i, c[2 * g + 1] = x / i;
    }
    return {
      position: { value: l, size: 3 },
      uv: { value: c, size: 2 }
    };
  }
  processHeight(e) {
    return ms(this, void 0, void 0, function* () {
      const t = e.width, i = t + 1;
      var n = zw.createOffscreenCanvas(t, t), r = n.getContext("2d");
      r.imageSmoothingEnabled = !1, r.drawImage(e, 0, 0, t, t, 0, 0, n.width, n.height);
      var a = r.getImageData(0, 0, n.width, n.height), o = a.data;
      const l = ba.getTerrain(o, t, this.elevationDecoder), h = new P5e(i).createTile(l), { vertices: u, triangles: d } = h.getMesh(typeof this.meshMaxError == "function" ? this.meshMaxError(this.level) : this.meshMaxError), f = ba.getMeshAttributes(u, l, t, [-0.5, -0.5, 0.5, 0.5], this.exageration);
      this.geometry = new Pt(), this.geometry.setIndex(new M4(d, 1)), this.geometry.setAttribute("position", new gt(f.position.value, f.position.size)), this.geometry.setAttribute("uv", new gt(f.uv.value, f.uv.size)), this.geometry.rotateX(Math.PI);
      var p = new Ut(e);
      p.generateMipmaps = !1, p.format = dn, p.magFilter = Ni, p.minFilter = Ni, p.needsUpdate = !0, this.material.userData.heightMap.value = p, this.material.map = p, this.material.needsUpdate = !0;
    });
  }
  loadHeightGeometry() {
    return ms(this, void 0, void 0, function* () {
      if (this.mapView.heightProvider === null)
        throw new Error("GeoThree: MapView.heightProvider provider is null.");
      const e = yield this.mapView.heightProvider.fetchTile(this.level, this.x, this.y);
      this.disposed || (this.processHeight(e), this.heightLoaded = !0, this.nodeReady());
    });
  }
}
ba.geometrySize = 16;
ba.emptyTexture = new Ut();
ba.geometry = new wl(1, 1, 1, 1);
ba.tileSize = 256;
class us extends Re {
  constructor(e = us.PLANAR, t = new T5e(), i = null) {
    super(void 0, new kt({ transparent: !0, opacity: 0, depthWrite: !1, colorWrite: !1 })), this.lod = null, this.provider = null, this.heightProvider = null, this.root = null, this.cacheTiles = !1, this.onBeforeRender = (n, r, a, o, l, c) => {
      this.lod.updateLOD(this, a, n, r);
    }, this.lod = new _5e(), this.provider = t, this.heightProvider = i, this.setRoot(e), this.preSubdivide();
  }
  setRoot(e) {
    if (typeof e == "number") {
      if (!us.mapModes.has(e))
        throw new Error("Map mode " + e + " does is not registered.");
      const t = us.mapModes.get(e);
      e = new t(null, this);
    }
    this.root !== null && (this.remove(this.root), this.root = null), this.root = e, this.root !== null && (this.geometry = this.root.constructor.baseGeometry, this.scale.copy(this.root.constructor.baseScale), this.root.mapView = this, this.add(this.root), this.root.initialize());
  }
  preSubdivide() {
    var e, t;
    function i(r, a) {
      if (!(a <= 0)) {
        r.subdivide();
        for (let o = 0; o < r.children.length; o++)
          if (r.children[o] instanceof ir) {
            const l = r.children[o];
            i(l, a - 1);
          }
      }
    }
    const n = Math.max(this.provider.minZoom, (t = (e = this.heightProvider) === null || e === void 0 ? void 0 : e.minZoom) !== null && t !== void 0 ? t : -1 / 0);
    n > 0 && i(this.root, n);
  }
  setProvider(e) {
    e !== this.provider && (this.provider = e, this.clear());
  }
  setHeightProvider(e) {
    e !== this.heightProvider && (this.heightProvider = e, this.clear());
  }
  clear() {
    return this.traverse(function(e) {
      e.childrenCache && (e.childrenCache = null), e.initialize && e.initialize();
    }), this;
  }
  minZoom() {
    var e, t;
    return Math.max(this.provider.minZoom, (t = (e = this.heightProvider) === null || e === void 0 ? void 0 : e.minZoom) !== null && t !== void 0 ? t : -1 / 0);
  }
  maxZoom() {
    var e, t;
    return Math.min(this.provider.maxZoom, (t = (e = this.heightProvider) === null || e === void 0 ? void 0 : e.maxZoom) !== null && t !== void 0 ? t : 1 / 0);
  }
  getMetaData() {
    this.provider.getMetaData();
  }
  raycast(e, t) {
    return !1;
  }
}
us.PLANAR = 200;
us.SPHERICAL = 201;
us.HEIGHT = 202;
us.HEIGHT_SHADER = 203;
us.MARTINI = 204;
us.mapModes = /* @__PURE__ */ new Map([
  [us.PLANAR, Ra],
  [us.SPHERICAL, Hu],
  [us.HEIGHT, ao],
  [us.HEIGHT_SHADER, er],
  [us.MARTINI, ba]
]);
new _();
new _();
new et();
new _();
new th();
new _();
class iz {
  static get(e) {
    return ms(this, void 0, void 0, function* () {
      return new Promise(function(t, i) {
        const n = new XMLHttpRequest();
        n.overrideMimeType("text/plain"), n.open("GET", e, !0), n.onload = function() {
          t(n.response);
        }, n.onerror = i, n.send(null);
      });
    });
  }
  static getRaw(e) {
    return ms(this, void 0, void 0, function* () {
      return new Promise(function(t, i) {
        var n = new XMLHttpRequest();
        n.responseType = "arraybuffer", n.open("GET", e, !0), n.onload = function() {
          t(n.response);
        }, n.onerror = i, n.send(null);
      });
    });
  }
  static request(e, t, i, n, r, a, o) {
    function l(h) {
      try {
        return JSON.parse(h);
      } catch {
        return h;
      }
    }
    const c = new XMLHttpRequest();
    if (c.overrideMimeType("text/plain"), c.open(t, e, !0), i != null)
      for (const h in i)
        c.setRequestHeader(h, i[h]);
    return r !== void 0 && (c.onload = function(h) {
      r(l(c.response), c);
    }), a !== void 0 && (c.onerror = a), o !== void 0 && (c.onprogress = o), c.send(n !== void 0 ? n : null), c;
  }
}
class r0 extends j2 {
  constructor(e = "", t = r0.AERIAL) {
    super(), this.maxZoom = 19, this.minZoom = 1, this.format = "jpeg", this.mapSize = 512, this.subdomain = "t1", this.meta = null, this.apiKey = e, this.type = t;
  }
  getMetaData() {
    return ms(this, void 0, void 0, function* () {
      const e = r0.ADDRESS + "/REST/V1/Imagery/Metadata/RoadOnDemand?output=json&include=ImageryProviders&key=" + this.apiKey, t = yield iz.get(e);
      this.meta = JSON.parse(t);
    });
  }
  static quadKey(e, t, i) {
    let n = "";
    for (let r = e; r > 0; r--) {
      const a = 1 << r - 1;
      let o = 0;
      t & a && o++, i & a && (o += 2), n += o;
    }
    return n;
  }
  fetchTile(e, t, i) {
    return new Promise((n, r) => {
      const a = document.createElement("img");
      a.onload = function() {
        n(a);
      }, a.onerror = function() {
        r();
      }, a.crossOrigin = "Anonymous", a.src = "http://ecn." + this.subdomain + ".tiles.virtualearth.net/tiles/" + this.type + r0.quadKey(e, t, i) + ".jpeg?g=1173";
    });
  }
}
r0.ADDRESS = "https://dev.virtualearth.net";
r0.AERIAL = "a";
r0.ROAD = "r";
r0.AERIAL_LABELS = "h";
r0.OBLIQUE = "o";
r0.OBLIQUE_LABELS = "b";
class D5e extends j2 {
  constructor(e = "", t = "", i = "base", n = "normal.day", r = "png", a = 512) {
    super(), this.appId = e, this.appCode = t, this.style = i, this.scheme = n, this.format = r, this.size = a, this.version = "newest", this.server = 1;
  }
  nextServer() {
    this.server = this.server % 4 === 0 ? 1 : this.server + 1;
  }
  getMetaData() {
    return ms(this, void 0, void 0, function* () {
    });
  }
  fetchTile(e, t, i) {
    return this.nextServer(), new Promise((n, r) => {
      const a = document.createElement("img");
      a.onload = function() {
        n(a);
      }, a.onerror = function() {
        r();
      }, a.crossOrigin = "Anonymous", a.src = "https://" + this.server + "." + this.style + ".maps.api.here.com/maptile/2.1/maptile/" + this.version + "/" + this.scheme + "/" + e + "/" + t + "/" + i + "/" + this.size + "/" + this.format + "?app_id=" + this.appId + "&app_code=" + this.appCode;
    });
  }
}
D5e.PATH = "/maptile/2.1/";
class $0 extends j2 {
  constructor(e = "", t = "", i = $0.STYLE, n = "png", r = !1, a = "v4") {
    super(), this.apiToken = e, this.format = n, this.useHDPI = r, this.mode = i, this.mapId = t, this.style = t, this.version = a;
  }
  getMetaData() {
    return ms(this, void 0, void 0, function* () {
      const e = $0.ADDRESS + this.version + "/" + this.mapId + ".json?access_token=" + this.apiToken, t = yield iz.get(e), i = JSON.parse(t);
      this.name = i.name, this.minZoom = i.minZoom, this.maxZoom = i.maxZoom, this.bounds = i.bounds, this.center = i.center;
    });
  }
  fetchTile(e, t, i) {
    return new Promise((n, r) => {
      const a = document.createElement("img");
      a.onload = function() {
        n(a);
      }, a.onerror = function() {
        r();
      }, a.crossOrigin = "Anonymous", this.mode === $0.STYLE ? a.src = $0.ADDRESS + "styles/v1/" + this.style + "/tiles/" + e + "/" + t + "/" + i + (this.useHDPI ? "@2x?access_token=" : "?access_token=") + this.apiToken : a.src = $0.ADDRESS + "v4/" + this.mapId + "/" + e + "/" + t + "/" + i + (this.useHDPI ? "@2x." : ".") + this.format + "?access_token=" + this.apiToken;
    });
  }
}
$0.ADDRESS = "https://api.mapbox.com/";
$0.STYLE = 100;
$0.MAP_ID = 101;
function L5e(s, e) {
  return {
    x: s,
    z: -e
  };
}
function I5e(s, e) {
  return {
    x: s,
    y: -e
  };
}
function O5e(s, e, t) {
  return {
    x: s - t.x,
    z: e - t.z
  };
}
function N5e(s, e, t) {
  return {
    x: s + t.x,
    z: e + t.z
  };
}
class B5e extends j2 {
  constructor() {
    super(...arguments);
    S(this, "urlTemplate", "https://fly.maptiles.arcgis.com/arcgis/rest/services/World_Imagery_Firefly/MapServer/tile/{z}/{y}/{x}");
  }
  fetchTile(t, i, n) {
    return new Promise((r, a) => {
      const o = document.createElement("img");
      o.onload = () => r(o), o.onerror = () => {
        o.src = Lne, r(o);
      }, o.crossOrigin = "Anonymous";
      const l = { x: i, y: n, z: t }, c = this.urlTemplate.replace(
        /{(.*?)}/g,
        (h, u) => encodeURIComponent(l[u] ?? h)
      );
      o.src = c;
    });
  }
}
class F5e {
  constructor() {
    S(this, "group", new ci());
    S(this, "map");
    S(this, "provider");
    const e = new B5e(), t = new us(us.PLANAR, e);
    t.updateMatrixWorld(!0), this.map = t, this.provider = e;
  }
  set enable(e) {
    e ? this.group.add(this.map) : this.group.remove(this.map);
  }
  setUrl(e) {
    this.provider.urlTemplate !== e && (this.dispose(), this.map = new us(us.PLANAR, this.provider), this.group.add(this.map)), this.provider.urlTemplate = e;
  }
  setCenter(e, t) {
    const i = mi.datumsToSpherical(e, t);
    this.group.position.set(-i.x, 0, i.y);
  }
  setMaxZoom(e) {
    this.map.provider.maxZoom = e;
  }
  setMinZoom(e) {
    this.map.provider.minZoom = e;
  }
  getLocalToCartographic(e, t, i) {
    const n = O5e(e, i, this.group.position), r = I5e(n.x, n.z), a = mi.sphericalToDatums(r.x, r.y);
    return {
      lon: a.longitude,
      lat: a.latitude,
      height: i
    };
  }
  getCartographicToLocal(e, t, i) {
    const n = mi.datumsToSpherical(t, e), r = L5e(n.x, n.y), { x: a, z: o } = N5e(r.x, r.z, this.group.position);
    return {
      x: a,
      y: i,
      z: o
    };
  }
  dispose() {
    this.map.clear(), this.group.remove(this.map);
  }
}
var nz = Object.defineProperty, z5e = (s, e, t) => e in s ? nz(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t, k5e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
}, cn = (s, e, t) => (z5e(s, typeof e != "symbol" ? e + "" : e, t), t), G5e = (s, e, t) => {
  if (!e.has(s))
    throw TypeError("Cannot " + t);
}, H5e = (s, e, t) => {
  if (e.has(s))
    throw TypeError("Cannot add the same private member more than once");
  e instanceof WeakSet ? e.add(s) : e.set(s, t);
}, V5e = (s, e, t) => (G5e(s, e, "access private method"), t), sM, sz;
const W5e = "Scene";
let Qx = class extends Fi {
  constructor() {
    super(...arguments), H5e(this, sM), cn(this, "renderer"), cn(this, "ambientLight", new Gc("#ffffff", 0.1)), cn(this, "postEffectOptions", {
      ssao: {
        radius: 0,
        shadowOffset: 0,
        intensity: 0,
        blur: 0,
        sharp: 0,
        enable: !1
      },
      taa: {
        colorScale: 0,
        mixWeight: 0,
        enable: !1
      },
      imageQuality: {
        colorFilter: "",
        exposure: 0,
        hue: 0,
        enable: !1
      },
      colorAdjustment: {
        gamma: 1,
        contrast: 1,
        saturation: 1,
        brightness: 1,
        hue: 0,
        red: 1,
        green: 1,
        blue: 1,
        alpha: 1,
        enable: !1
      },
      bloomEffect: {
        strength: 1,
        radius: 1,
        brightnessThreshold: 0.5,
        enable: !1
      },
      outLine: {
        enable: !1,
        color: {
          type: "pure",
          pure: "#ffffff"
        },
        strength: 1
      }
    }), cn(this, "initCameraOptions", {
      near: 1,
      far: 1e4,
      fov: 45,
      minDistance: Number.EPSILON,
      maxDistance: 1 / 0,
      minPolarAngle: 0,
      maxPolarAngle: Math.PI,
      enableAzimuthAngle: !1,
      maxAzimuthAngle: 1 / 0,
      minAzimuthAngle: -1 / 0,
      enablePolarAngle: !1,
      maxTarget: { x: 1 / 0, y: 1 / 0, z: 1 / 0 },
      enableLimit: !0,
      dynamicNearFar: {
        enable: !1,
        factor: 1
      }
    }), cn(this, "camera", JSON.parse(JSON.stringify(this.initCameraOptions))), cn(this, "raycaster", new lr()), cn(this, "controls", {
      cameraMovingSpeed: 1,
      objectMovingSpeed: 1,
      rotationSpeed: 1,
      zoomSpeed: 1,
      transformShowMode: "center",
      autoAdjustTargetPoint: !0
    }), cn(this, "_geographicProjectionUtils", new kme(
      this.entity
    )), cn(this, "sky", new v8()), cn(this, "sunLightComp"), cn(this, "pmremGenerator"), cn(this, "sceneEnv", new Da()), cn(this, "skyHDRRenderTarget"), cn(this, "cloud", new Z8e()), cn(this, "environmentRenderer"), cn(this, "backgroundRenderer"), cn(this, "loadingScreenOptions", {
      show: !1,
      imageUrl: ""
    }), cn(this, "_currentSkyOption"), cn(this, "_environmentLightMode"), cn(this, "_backgroundMode"), cn(this, "_environmentMode"), cn(this, "easyvProxy", new X5e(this)), cn(this, "light"), cn(this, "cameraHelper"), cn(this, "helper"), cn(this, "tilesetComponent"), cn(this, "mapComponent"), cn(this, "setControlsOrbitPoint", (s) => {
      if (this.renderer) {
        const { cameraControls: e } = this.renderer, t = this.renderer.threeRenderer.domElement.getBoundingClientRect(), i = s.clientX - t.left, n = s.clientY - t.top, r = new Se();
        r.set(
          i / t.width * 2 - 1,
          (t.height - n) / t.height * 2 - 1
        ), e.camera.updateMatrixWorld(), this.raycaster.setFromCamera(r, e.camera);
        const a = this.raycaster.intersectObjects(this.renderer.rootScene.children), o = new _();
        if (a.length !== 0)
          o.set(a[0].point.x, a[0].point.y, a[0].point.z);
        else {
          const l = Y4(
            e.camera,
            s.clientX,
            s.clientY,
            this.renderer.threeRenderer.domElement
          );
          o.copy(l);
        }
        e.setOrbitPoint(o.x, o.y, o.z);
      }
    });
  }
  get geographicProjectionUtils() {
    return this._geographicProjectionUtils;
  }
  initialize(s) {
    this.renderer = s, this.renderer.rootScene.add(this.ambientLight), this.sky.scale.setScalar(45e4);
    const { uniforms: e } = this.sky.material, t = {
      turbidity: 10,
      rayleigh: 1.5,
      mieCoefficient: 5e-3,
      mieDirectionalG: 0.8,
      elevation: 2,
      azimuth: 180
    };
    e.turbidity.value = t.turbidity, e.rayleigh.value = t.rayleigh, e.mieCoefficient.value = t.mieCoefficient, e.mieDirectionalG.value = t.mieDirectionalG;
    const i = nt.degToRad(90 - t.elevation), n = nt.degToRad(t.azimuth), r = new _();
    r.setFromSphericalCoords(1, i, n), e.sunPosition.value.copy(r), this.pmremGenerator = new rp(this.renderer.threeRenderer), this.renderer.rootScene.add(this.sky);
    const a = new ed({
      id: "",
      type: "EngineGroup"
    }), o = Hc.getInstance().resolve("SunLight@1.8.0");
    this.sunLightComp = a.addComponent(
      "id",
      "",
      o.ctor,
      !0
    ), this.sunLightComp.hideHelper(), this.sunLightComp.setHelperVisible(!1), this.sunLightComp.initialize(this.renderer.mainCamera), this.sunLightComp.isInit = !0, this.sunLightComp.light.visible = !1, this.renderer.rootScene.add(this.sunLightComp.group), this.cloud.group.scale.setScalar(5e3), this.renderer.rootScene.add(this.cloud.group), this.environmentRenderer = new SD(this.renderer.threeRenderer), this.backgroundRenderer = new SD(this.renderer.threeRenderer);
    const l = new _a();
    l.castShadow = !0, l.position.set(0, 10, 0), l.target.position.set(-4, 0, -4), this.group.add(l), this.group.add(l.target);
    const c = new b2(l.shadow.camera);
    this.group.add(c);
    const h = new aS(l);
    this.group.add(h), this.light = l, this.cameraHelper = c, this.helper = h, this.cameraHelper.visible = !1, this.helper.visible = !1, this.tilesetComponent = new E5e({ url: "" }), this.tilesetComponent.init(this.renderer.mainCamera, this.renderer.threeRenderer), this.renderer.rootScene.add(this.tilesetComponent.root), this.mapComponent = new F5e(), this.mapComponent.setCenter(30, 120), this.renderer.rootScene.add(this.mapComponent.group), this.tilesetComponent.scene = this;
  }
  initStates(s) {
    return s.forEach((e) => {
      const t = e.config;
      t.environmentLightMode = t.environmentLightMode ?? (t.hdrIsShow ? "mix" : "color"), t.backgroundMode = t.backgroundMode ?? (t.hdrIsShowBackground ? "hdr" : "color"), t.sunLightShadowMapWidth = t.sunLightShadowMapWidth ?? 512, t.sunLightShadowMapHeight = t.sunLightShadowMapHeight ?? 512, t.sunLightIntensityFactor = t.sunLightIntensityFactor ?? t.sunLightIntensity, t.mode = t.mode ?? "customSky", t.environmentLightMode = t.environmentLightMode ?? "mix", t.backgroundMode = t.backgroundMode ?? "hdr", t.backgroundUrl = t.backgroundUrl ?? t.hdrUrl, t.backgroundIntensity = t.backgroundIntensity ?? 1, t.backgroundRotation = t.backgroundRotation ?? 0, t.hdrIsShowBackground = t.hdrIsShowBackground ?? !1, t.hdrIsShow = t.hdrIsShow ?? !1, t.hdrUrl = t.hdrUrl ?? "", t.hdrRotation = t.hdrRotation ?? 0, t.hdrIntensity = t.hdrIntensity ?? 1, t.longitude = t.longitude ?? 120.012, t.latitude = t.latitude ?? 30.286, t.northOffset = t.northOffset ?? 0, t.sunLightCastShadow = t.sunLightCastShadow ?? !1, t.sunLightIntensityFactor = t.sunLightIntensityFactor ?? 1, t.timeZone = t.timeZone ?? 8, t.year = t.year ?? 2023, t.month = t.month ?? 10, t.day = t.day ?? 13, t.hours = t.hours ?? 7, t.minutes = t.minutes ?? 30, t.seconds = t.seconds ?? 0, t.enableFogV2 = t.enableFogV2 ?? !1, t.fogNear = t.fogNear ?? 1, t.fogFar = t.fogFar ?? 1e3, t.fogColor = t.fogColor ?? "#ffffff", t.sunLightShadowRadius = t.sunLightShadowRadius ?? 0, t.sunLightShadowBias = t.sunLightShadowBias ?? 0, t.sunSize = t.sunSize ?? 0.545, t.skyHDRIntensity = t.skyHDRIntensity ?? 1, t.sunLightShadowHelperVisible = t.sunLightShadowHelperVisible ?? !1, t.sunLightPosition = t.sunLightPosition ?? { x: 0, y: 0, z: 0 }, t.sunLightShadowArea = t.sunLightShadowArea ?? {
        near: 30,
        far: 500,
        size: 1024
      }, t.enableCloud = t.enableCloud ?? !1, t.cloudIntensity = t.cloudIntensity ?? 1, t.windSpeed = t.windSpeed ?? 1, t.windDirection = t.windDirection ?? 0;
    }), super.initStates(s), this.states.curStateId && (this.needsUpdate = !0), this;
  }
  updateDynamicNearFar(s) {
    const { enable: e, factor: t } = s.dynamicNearFar ?? {};
    this.renderer && (this.renderer.dynamicNearFarFactor = e ? t ?? 0 : -1);
  }
  updateSceneData(s) {
    const { controls: e, camera: t, loadingScreen: i } = s;
    if (Object.assign(this.controls, e), Object.assign(this.camera, t), Object.assign(this.loadingScreenOptions, i), this.updateDynamicNearFar(this.camera), this.renderer) {
      this.updateCamera(
        this.renderer,
        t.near ?? this.initCameraOptions.near,
        t.far ?? this.initCameraOptions.far,
        t.fov ?? this.initCameraOptions.fov
      );
      let n = t;
      this.camera.enableLimit || (n = this.initCameraOptions);
      const { cameraControls: r } = this.renderer;
      r.azimuthRotateSpeed = e.rotationSpeed, r.polarRotateSpeed = e.rotationSpeed, r.dollySpeed = e.zoomSpeed, r.minDistance = n.minDistance ?? this.initCameraOptions.minDistance, r.maxDistance = n.maxDistance ?? this.initCameraOptions.maxDistance, r.autoAdjustTargetPoint = e.autoAdjustTargetPoint ?? !1, n.enablePolarAngle ? (r.minPolarAngle = n.minPolarAngle ?? this.initCameraOptions.minPolarAngle, r.maxPolarAngle = n.maxPolarAngle ?? this.initCameraOptions.maxPolarAngle) : (r.minPolarAngle = this.initCameraOptions.minPolarAngle, r.maxPolarAngle = this.initCameraOptions.maxPolarAngle), n.enableAzimuthAngle ? (r.minAzimuthAngle = n.minAzimuthAngle ?? this.initCameraOptions.minAzimuthAngle, r.maxAzimuthAngle = n.maxAzimuthAngle ?? this.initCameraOptions.maxAzimuthAngle) : (r.minAzimuthAngle = this.initCameraOptions.minAzimuthAngle, r.maxAzimuthAngle = this.initCameraOptions.maxAzimuthAngle);
      const a = n.maxTarget ? new _(
        n.maxTarget.x,
        n.maxTarget.y,
        n.maxTarget.z
      ) : new _(this.camera.maxTarget.x, this.camera.maxTarget.y, this.camera.maxTarget.z);
      isNaN(a.x) && (a.x = this.camera.maxTarget.x), isNaN(a.y) && (a.y = this.camera.maxTarget.y), isNaN(a.z) && (a.z = this.camera.maxTarget.z), r.maxTarget = a, r.minTarget = a.clone().multiplyScalar(-1);
      const o = this.entity.getComponentByType("KeyboardRoma");
      o == null || o.setCameraStep(e.cameraMovingSpeed), this.entity.selection.transformObjectControls.setObjectStep(e.objectMovingSpeed), this.setPostProcessOptions(s), s.geographicProjection && this._geographicProjectionUtils.setProjectionOptions(s.geographicProjection), e.transformShowMode && this.entity.selection.setTransformControlsShowMode(e.transformShowMode), s0(s.geographicProjection) || this.updateGeographicMapService(s.geographicProjection);
    }
  }
  updateTilesetMapCenter(s, e) {
    this._geographicProjectionUtils.projectOptions.tileSetMapCenter = {
      lat: s,
      lon: e
    }, this.mapComponent.setCenter(s, e), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  updateGeographicProjection(s) {
    this._geographicProjectionUtils.setProjectionOptions(s), this.updateGeographicMapService(s), this.needDataUpdateServer = !0;
  }
  updateGeographicMapService(s) {
    const e = () => {
      this._geographicProjectionUtils.referencePoints.forEach((t) => {
        t.visible = !1;
      });
    };
    if (!s.enable)
      this.mapComponent.enable = !1, this.tilesetComponent.enable = !1;
    else
      switch (s.mode) {
        case "geographic":
          this.mapComponent.enable = !1, this.tilesetComponent.enable = !1;
          break;
        case "xyz":
          this.mapComponent.setUrl(s.mapUrl), this.mapComponent.setCenter(s.mapCenter.lat, s.mapCenter.lon), this.mapComponent.setMaxZoom(s.maxZoom), this.mapComponent.setMinZoom(s.minZoom), this.mapComponent.enable = !0, this.tilesetComponent.enable = !1, e();
          break;
        case "tileset":
          this.mapComponent.enable = !1, this.tilesetComponent.enable = !0, this.tilesetComponent.setUrl(s.tileSetUrl), this.tilesetComponent.heightOffset = 0, this.tilesetComponent.scale = 1, this.tilesetComponent.updateTilesetGroupMatrix(), e();
          break;
        case "xyz_tileset":
          this.tilesetComponent.enable = !0, this.tilesetComponent.setUrl(s.tileSetUrl), this.tilesetComponent.heightOffset = s.tileSetHeightOffsetInMap ?? 0, this.tilesetComponent.scale = s.tileSetScaleInMap ?? 1, this.tilesetComponent.updateTilesetGroupMatrix(), this.mapComponent.setUrl(s.mapUrl), this.mapComponent.setCenter(s.tileSetMapCenter.lat, s.tileSetMapCenter.lon), this.mapComponent.setMaxZoom(s.maxZoom), this.mapComponent.setMinZoom(s.minZoom), this.mapComponent.enable = !0, e();
          break;
      }
  }
  updateCamera(s, e, t, i) {
    e > t && console.error(`near ${e}不能大于 far ${t}`), s.mainCamera.near = e, s.mainCamera.far = t, s.mainCamera instanceof Zt && (s.mainCamera.fov = i), s.mainCamera.updateProjectionMatrix();
  }
  updateAmbient(s) {
    const { color: e, intensity: t } = s;
    this.ambientLight.color = new _e(e), this.ambientLight.intensity = t;
  }
  async updateHdr(s) {
    const { hdrUrl: e, isShow: t, isShowBackground: i } = s;
    if (this.renderer) {
      let n;
      e.match(/(.*?)\.(hdr)$/) && (n = await st.loadRgbeTexture(e, !0)), e.match(/(.*?)\.(exr)$/) && (n = await st.loadExrTexture(e, !0)), n && (t ? (this.renderer.rootScene.environment = n, i ? this.renderer.rootScene.background = n : this.renderer.rootScene.background = null) : (this.renderer.rootScene.environment = null, this.renderer.rootScene.background = null)), e === "" && (this.renderer.rootScene.environment = null, this.renderer.rootScene.background = null);
    }
  }
  // public updateBackground(background: RGBA) {
  //   const { color, opacity } = background;
  //   this.renderer?.setBackgroundColor(color, opacity);
  // }
  setPostProcessOptions(s) {
    return this.postEffectOptions = s.postEffects, this.postEffectOptions.colorAdjustment = this.postEffectOptions.colorAdjustment ?? {
      gamma: 1,
      contrast: 1,
      saturation: 1,
      brightness: 1,
      hue: 0,
      red: 1,
      green: 1,
      blue: 1,
      alpha: 1,
      enable: !1
    }, this.postEffectOptions.bloomEffect = this.postEffectOptions.bloomEffect ?? {
      strength: 1,
      radius: 0,
      brightnessThreshold: 1,
      enable: !1
    }, this.postEffectOptions.outLine = this.postEffectOptions.outLine ?? {
      strength: 1,
      color: {
        type: "pure",
        pure: "#ffffff"
      },
      enable: !1
    }, this.entity.renderer.setPostProcessOptions(s.postEffects);
  }
  setTaaOptions(s) {
    this.postEffectOptions.taa = s;
    const e = this.entity.renderer.getComponentByType("Taa");
    e == null || e.setOptions(s), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  setSsaoOptions(s) {
    this.postEffectOptions.ssao = s;
    const e = this.entity.renderer.getComponentByType("SSAO");
    e == null || e.setOptions(s), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  setImageQualityOptions(s) {
    this.postEffectOptions.imageQuality = s;
    const e = this.entity.renderer.getComponentByType("ImageQuality");
    e == null || e.setOptions(s), dt.Entity.refresh(), console.log("refresh"), this.needDataUpdateServer = !0;
  }
  setColorAdjustmentOptions(s) {
    this.postEffectOptions.colorAdjustment = {
      ...this.postEffectOptions.colorAdjustment,
      ...s
    }, this.entity.renderer.updateColorAdjustmentPostprocessing(
      this.postEffectOptions.colorAdjustment
    ), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  setBloomEffectOptions(s) {
    this.postEffectOptions.bloomEffect = {
      ...this.postEffectOptions.bloomEffect,
      ...s
    }, this.entity.renderer.updateBloomEffectPostprocessing(this.postEffectOptions.bloomEffect), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  setOutLineOptions(s) {
    this.postEffectOptions.outLine = {
      ...this.postEffectOptions.outLine,
      ...s
    }, this.entity.renderer.updateOutLineEffectPostprocessing(this.postEffectOptions.outLine), dt.Entity.refresh(), this.needDataUpdateServer = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      visible: !1,
      inActive: !1,
      version: this.version,
      // attribute: this.customAttributes,
      data: {
        postEffects: this.postEffectOptions,
        camera: this.camera,
        controls: this.controls,
        geographicProjection: { ...this.geographicProjectionUtils.projectOptions },
        loadingScreen: { ...this.loadingScreenOptions }
      },
      states: this.states.toJson()
    };
  }
  updateSky(s) {
    var c, h, u, d, f, p;
    this._currentSkyOption = {
      ...s,
      time: s.date.getTime()
    };
    const { azimuth: e, altitude: t } = Y8e(s.date, s.lat, s.lng), i = Math.PI / 2 - t, n = e - (s.northOffset ?? 0), r = new _();
    r.setFromSphericalCoords(1, i, n);
    const { uniforms: a } = this.sky.material;
    a.sunPosition.value.copy(r), a.uAtmosphereLightDiscCosHalfApexAngle.value = Math.cos(
      s.sunSize * nt.DEG2RAD
    );
    const o = s.sunLightIntensityFactor * 1, l = new Mi().setFromRotationMatrix(
      new et().lookAt(r, new _(), new _(0, 1, 0))
    );
    if (this.sunLightComp.shadowHelper.camera.rotation.x = 0, this.sunLightComp.shadowHelper.camera.updateMatrixWorld(), this.sunLightComp.updateLightOptions({
      intensity: {
        value: o,
        unit: "lumen"
      },
      position: {
        x: ((c = s.sunLightPosition) == null ? void 0 : c.x) ?? 0,
        y: ((h = s.sunLightPosition) == null ? void 0 : h.y) ?? 0,
        z: ((u = s.sunLightPosition) == null ? void 0 : u.z) ?? 0
      },
      rotation: {
        x: l.x,
        y: l.y,
        z: l.z
      },
      color: "#ffffff",
      kelvin: 4500,
      enable: !0,
      castShadow: s.sunLightCastShadow,
      shadowRange: 20,
      bias: s.sunLightShadowBias,
      radius: s.sunLightShadowRadius,
      shadowArea: {
        near: ((d = s.sunLightShadowArea) == null ? void 0 : d.near) ?? 30,
        far: ((f = s.sunLightShadowArea) == null ? void 0 : f.far) ?? 500,
        size: ((p = s.sunLightShadowArea) == null ? void 0 : p.size) ?? 1024
      },
      shadowHelperVisible: (s.sunLightCastShadow && s.sunLightShadowHelperVisible) ?? !1
    }), this.sunLightComp.updateShadowMapSize(
      new Se(
        s.sunLightShadowMapWidth ?? 512,
        s.sunLightShadowMapWidth ?? s.sunLightShadowMapHeight ?? 512
      )
    ), this.sunLightComp.light.visible = !1, this.updateDirectionalLight(r), this.skyHDRRenderTarget !== void 0 && this.skyHDRRenderTarget.dispose(), this.sceneEnv.add(this.sky), this.renderer) {
      const v = this.renderer.threeRenderer.toneMappingExposure;
      this.renderer.threeRenderer.toneMappingExposure = s.skyHDRIntensity, this.skyHDRRenderTarget = this.pmremGenerator.fromScene(this.sceneEnv), this.renderer.threeRenderer.toneMappingExposure = v, this.renderer.rootScene.add(this.sky), this.renderer.rootScene.environment = this.skyHDRRenderTarget.texture;
    }
  }
  updateDirectionalLight(s) {
    const e = this.light;
    e.shadow.bias = this.sunLightComp.light.shadow.bias, e.color = this.sunLightComp.light.color, e.castShadow = this.sunLightComp.light.castShadow, e.intensity = this.sunLightComp.light.intensity, e.shadow.mapSize = this.sunLightComp.light.shadow.mapSize.clone(), e.shadow.radius = this.sunLightComp.light.shadow.radius, e.visible = this.sunLightComp.light.visible, e.shadow.camera.top = this.sunLightComp.light.shadow.camera.top, e.shadow.camera.bottom = this.sunLightComp.light.shadow.camera.bottom, e.shadow.camera.left = this.sunLightComp.light.shadow.camera.left, e.shadow.camera.right = this.sunLightComp.light.shadow.camera.right, e.shadow.camera.near = this.sunLightComp.light.shadow.camera.near, e.shadow.camera.far = this.sunLightComp.light.shadow.camera.far, e.shadow.camera.updateProjectionMatrix(), e.position.copy(this.sunLightComp.dragObject.position);
    const t = this.sunLightComp.dragObject.position.clone().sub(s);
    e.target.position.copy(t), e.target.updateMatrixWorld(), this.light.target.updateMatrixWorld(), this.helper.update(), this.light.shadow.camera.updateProjectionMatrix(), this.cameraHelper.update(), this.cameraHelper.visible = !1, this.helper.visible = !1, s.y < 0 ? (this.sunLightComp.light.visible = !1, this.light.visible = !1, this.light.castShadow = !1) : (this.sunLightComp.light.visible = !1, this.light.visible = !0, this.light.castShadow = !0);
  }
  update() {
    const s = this.states.deepGet(this.getCurStateId());
    s && this._updateRenderData(s.config);
  }
  dispose() {
    var s;
    this.renderer = void 0, (s = this.skyHDRRenderTarget) == null || s.dispose(), this.sceneEnv.clear(), this.pmremGenerator.dispose(), this.geographicProjectionUtils.dispose(), super.dispose();
  }
  updateSceneBackground(s, e, t) {
    if (this.renderer) {
      const i = this.renderer;
      let n;
      s.match(/(.*?)\.(hdr)$/) && (n = st.loadRgbeTexture(s, !0)), s.match(/(.*?)\.(exr)$/) && (n = st.loadExrTexture(s, !0)), n && n.then((r) => {
        i.rootScene.background = r, i.rootScene.backgroundRotation.y = e, i.rootScene.backgroundIntensity = t;
      });
    }
  }
  updateSceneEnvironment(s, e, t) {
    if (this.renderer) {
      const i = this.renderer;
      let n;
      s.match(/(.*?)\.(hdr)$/) && (n = st.loadRgbeTexture(s, !0)), s.match(/(.*?)\.(exr)$/) && (n = st.loadExrTexture(s, !0)), n && n.then((r) => {
        i.rootScene.environment = r, i.rootScene.environmentRotation.y = e, i.rootScene.environmentIntensity = t;
      });
    }
  }
  updateBackgroundConfig(s) {
    const {
      backgroundMode: e,
      backgroundColor: t,
      backgroundIntensity: i,
      backgroundOpacity: n,
      backgroundUrl: r,
      backgroundRotation: a
    } = s;
    if (e === "color") {
      if (this.renderer) {
        if (typeof t == "string")
          this.renderer.setBackgroundColor(t, n);
        else {
          const o = new pi(t.pure);
          this.renderer.setBackgroundColor(dl(o), o.getAlpha());
        }
        this.renderer.rootScene.background = null, this.renderer.background = null;
      }
    } else
      e === "hdr" && this.updateSceneBackground(
        r,
        a * nt.DEG2RAD,
        i
      );
    this._backgroundMode = e;
  }
  /**
   * 更新当前场景组件状态配置项
   * @param stateConfig
   */
  easyvUpdateSceneStateConfig(s) {
    const t = {
      ...this.states.getCurrentStateConfig(),
      ...s
    };
    this._updateRenderData(t);
  }
  easyvSwitchComponentStateWithTWEEN(s, e) {
    const t = this.states.getCurrentStateConfig(), i = this.states.getStateConfigById(s);
    if (t.mode === "sky" && (i == null ? void 0 : i.mode) === "sky") {
      if (i) {
        if (this.states.setState(s), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), !this._currentSkyOption)
          return;
        const n = {
          ...this._currentSkyOption
        }, r = new Date(
          i.year,
          i.month,
          i.day,
          i.hours,
          i.minutes,
          i.seconds
        ), a = {
          date: r,
          time: r.getTime(),
          lat: i.latitude,
          lng: i.longitude,
          sunLightIntensityFactor: i.sunLightIntensityFactor,
          sunLightCastShadow: i.sunLightCastShadow,
          northOffset: i.northOffset,
          sunLightShadowRadius: i.sunLightShadowRadius,
          sunLightShadowBias: i.sunLightShadowBias,
          sunSize: i.sunSize,
          skyHDRIntensity: i.skyHDRIntensity,
          sunLightShadowArea: i.sunLightShadowArea,
          sunLightShadowHelperVisible: i.sunLightShadowHelperVisible
        }, o = jn.createTweenHandler({
          mode: e.mode,
          duration: e.duration,
          source: n,
          target: a,
          execute: (l, c) => {
            const h = new Date(l.time);
            this.updateSky({
              date: h,
              lat: l.lat,
              lng: l.lng,
              sunLightIntensityFactor: l.sunLightIntensityFactor,
              sunLightCastShadow: !0,
              northOffset: l.northOffset,
              sunLightShadowRadius: l.sunLightShadowRadius,
              sunLightShadowBias: l.sunLightShadowBias,
              sunSize: l.sunSize,
              sunLightShadowArea: {
                near: l.sunLightShadowArea.near,
                far: l.sunLightShadowArea.far,
                size: l.sunLightShadowArea.size
              },
              sunLightShadowHelperVisible: l.sunLightShadowHelperVisible,
              skyHDRIntensity: l.skyHDRIntensity,
              sunLightShadowMapWidth: i.sunLightShadowMapWidth,
              sunLightShadowMapHeight: i.sunLightShadowMapHeight,
              sunLightPosition: i.sunLightPosition ?? { x: 0, y: 0, z: 0 }
            });
          },
          complete: () => {
            this.setCurrentStateById(s), Yi(o), this.tweenHandler = void 0;
          }
        });
        this.tweenHandler = o;
      }
    } else
      this.setCurrentStateById(s);
  }
  _updateRenderData(s) {
    const {
      hdrUrl: e,
      backgroundColor: t,
      backgroundOpacity: i,
      ambientColor: n,
      ambientIntensity: r,
      hdrIsShow: a,
      hdrIsShowBackground: o,
      timeZone: l,
      year: c,
      month: h,
      day: u,
      hours: d,
      minutes: f,
      seconds: p,
      latitude: v,
      longitude: g,
      northOffset: m,
      sunLightIntensity: x,
      sunLightIntensityFactor: y,
      sunLightCastShadow: A = !1,
      mode: b,
      hdrRotation: M,
      hdrIntensity: w,
      //
      enableFogV2: C,
      fogNear: T,
      fogFar: E,
      fogColor: P,
      //
      sunLightShadowBias: L,
      sunLightShadowRadius: I,
      sunSize: N,
      skyHDRIntensity: H,
      cloudIntensity: U,
      windSpeed: W,
      windDirection: X,
      sunLightShadowMapWidth: K,
      sunLightShadowMapHeight: te,
      sunLightShadowArea: re,
      sunLightShadowHelperVisible: be,
      sunLightPosition: Pe
    } = s;
    if (b === "sky") {
      this.sunLightComp.group.visible = !0, this.sky.visible = !0;
      const J = new Date(c, h, u, d, f, p);
      this.updateSky({
        date: J,
        lat: v,
        lng: g,
        sunLightCastShadow: A,
        sunLightIntensityFactor: y,
        northOffset: m,
        sunLightShadowRadius: I,
        sunLightShadowBias: L,
        sunSize: N,
        skyHDRIntensity: H,
        sunLightShadowMapWidth: K,
        sunLightShadowMapHeight: te,
        sunLightShadowArea: re,
        sunLightShadowHelperVisible: be,
        sunLightPosition: Pe
      }), this.ambientLight.visible = !1, this.cloud.group.visible = !0, this.cloud.cloudSpeed = W, this.cloud.cloudDensity = U;
    } else
      this.light.visible = !1, this.ambientLight.visible = !0, V5e(this, sM, sz).call(this, s);
    if (this._environmentMode = b, this.renderer)
      if (C) {
        const J = new Wu(new _e(P));
        J.near = T, J.far = E, this.renderer.rootScene.fog = J;
      } else
        this.renderer.rootScene.fog = null;
  }
};
sM = /* @__PURE__ */ new WeakSet();
sz = function(s) {
  const {
    ambientColor: e,
    ambientIntensity: t,
    backgroundColor: i,
    backgroundOpacity: n,
    hdrUrl: r,
    hdrIsShow: a,
    hdrIsShowBackground: o,
    environmentLightMode: l,
    backgroundMode: c,
    hdrIntensity: h,
    hdrRotation: u,
    backgroundIntensity: d,
    backgroundRotation: f,
    backgroundUrl: p
  } = s;
  if (this.sunLightComp.group.visible = !1, this.sky.visible = !1, this.cameraHelper.visible = !1, this.helper.visible = !1, this.renderer && (this.renderer.threeRenderer.toneMappingExposure = 1), this.cloud.group.visible = !1, l === "color") {
    if (this.renderer && (this.renderer.rootScene.environment = null), this.ambientLight.visible = !0, this.ambientLight.visible = !0, typeof e == "string")
      this.ambientLight.color = new _e(e);
    else {
      const v = new pi(e.pure);
      this.ambientLight.color = dl(v);
    }
    this.ambientLight.intensity = t;
  } else if (l === "hdr")
    this.renderer && (this.renderer.rootScene.environment = null), this.ambientLight.visible = !1, this.updateSceneEnvironment(r, u * nt.DEG2RAD, h);
  else if (l === "mix") {
    if (this.ambientLight.visible = !0, typeof e == "string")
      this.ambientLight.color = new _e(e);
    else {
      const v = new pi(e.pure);
      this.ambientLight.color = dl(v);
    }
    this.ambientLight.intensity = t, this.updateSceneEnvironment(r, u * nt.DEG2RAD, h);
  }
  if (this._environmentLightMode = l, c === "color") {
    if (this.renderer) {
      if (typeof i == "string")
        this.renderer.setBackgroundColor(i, n);
      else {
        const v = new pi(i.pure);
        this.renderer.setBackgroundColor(dl(v), v.getAlpha());
      }
      this.renderer.rootScene.background = null, this.renderer.background = null;
    }
  } else
    c === "hdr" && this.updateSceneBackground(
      p,
      f * nt.DEG2RAD,
      d
    );
  this._backgroundMode = c;
};
Qx = k5e([
  ni(W5e)
], Qx);
class X5e {
  constructor(e) {
    S(this, "_tweenHandlerList", []);
    this.comp = e;
  }
  easyvUpdateSceneStateConfig(e) {
    const i = {
      ...this.comp.states.getCurrentStateConfig(),
      ...e
    };
    this.comp._updateRenderData(i);
  }
  easyvSetSceneStateById(e) {
    this.clearAllTween(), this.comp.setCurrentStateById(e);
  }
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.comp.states.getCurrentStateConfig(), n = this.comp.states.getStateConfigById(e);
    if (n) {
      if (this.clearAllTween(), this.comp.states.setState(e), i.mode !== n.mode) {
        this.comp.setCurrentStateById(e);
        return;
      }
      if (i.mode === "customSky" && n.mode === "customSky") {
        const r = this.comp.ambientLight.color.clone(), a = this.comp.ambientLight.intensity, o = this.comp.environmentRenderer.intensity, l = this.comp.environmentRenderer.rotation * nt.DEG2RAD, c = this.comp.backgroundRenderer.intensity, h = this.comp.backgroundRenderer.rotation * nt.DEG2RAD, u = this.comp.renderer.threeRenderer.getClearColor(new _e()), d = this.comp.renderer.threeRenderer.getClearAlpha(), f = jn.createTweenHandler({
          mode: t.mode,
          duration: Rf(t),
          source: {
            t: 0
          },
          target: {
            t: 1
          },
          execute: (p) => {
            var v, g, m, x, y;
            if (i.environmentLightMode === "color" && n.environmentLightMode === "color") {
              const A = n.ambientColor;
              if (typeof A == "string")
                this.comp.ambientLight.color = new _e().lerpColors(
                  r,
                  new _e(A),
                  p.t
                );
              else {
                const b = new pi(A.pure);
                this.comp.ambientLight.color = new _e().lerpColors(
                  r,
                  dl(b),
                  p.t
                );
              }
              this.comp.ambientLight.intensity = nt.lerp(
                a,
                n.ambientIntensity,
                p.t
              );
            } else if (i.environmentLightMode === "hdr" && n.environmentLightMode === "hdr") {
              if ((v = this.comp.renderer) != null && v.rootScene) {
                const A = this.comp.renderer.rootScene;
                A.environmentIntensity = nt.lerp(
                  o,
                  n.hdrIntensity,
                  p.t
                ), A.environmentRotation.y = nt.lerp(
                  l,
                  n.hdrRotation * nt.DEG2RAD,
                  p.t
                );
              }
            } else if (i.environmentLightMode === "mix" && n.environmentLightMode === "mix") {
              if ((g = this.comp.renderer) != null && g.rootScene) {
                const b = this.comp.renderer.rootScene;
                b.environmentIntensity = nt.lerp(
                  o,
                  n.hdrIntensity,
                  p.t
                ), b.environmentRotation.y = nt.lerp(
                  l,
                  n.hdrRotation * nt.DEG2RAD,
                  p.t
                );
              }
              const A = n.ambientColor;
              if (typeof A == "string")
                this.comp.ambientLight.color = new _e().lerpColors(
                  r,
                  new _e(A),
                  p.t
                );
              else {
                const b = new pi(A.pure);
                this.comp.ambientLight.color = new _e().lerpColors(
                  r,
                  dl(b),
                  p.t
                );
              }
              this.comp.ambientLight.intensity = nt.lerp(
                a,
                n.ambientIntensity,
                p.t
              );
            } else if ((m = this.comp.renderer) != null && m.rootScene) {
              const A = this.comp.renderer.rootScene;
              A.environmentIntensity = n.hdrIntensity, A.environmentRotation.y = n.hdrRotation * nt.DEG2RAD;
            }
            if (i.backgroundMode === "color" && n.backgroundMode === "color") {
              const A = n.backgroundColor;
              let b = new _e();
              if (typeof A == "string")
                b = new _e().lerpColors(
                  u,
                  new _e(A),
                  p.t
                );
              else {
                const w = new pi(A.pure);
                b = new _e().lerpColors(
                  u,
                  dl(w),
                  p.t
                );
              }
              const M = nt.lerp(
                d,
                n.backgroundOpacity,
                p.t
              );
              (x = this.comp.renderer) == null || x.setBackgroundColor(b, M);
            } else if (i.backgroundMode === "hdr" && n.backgroundMode === "hdr") {
              if ((y = this.comp.renderer) != null && y.rootScene) {
                const A = this.comp.renderer.rootScene;
                A.backgroundIntensity = nt.lerp(
                  c,
                  n.backgroundIntensity,
                  p.t
                ), A.backgroundRotation.y = nt.lerp(
                  h,
                  n.backgroundRotation * nt.DEG2RAD,
                  p.t
                );
              }
            } else
              this.comp.updateBackgroundConfig(n);
          },
          complete: () => {
            Yi(f);
          }
        });
        this._tweenHandlerList.push(f);
      } else
        i.mode === "sky" && n.mode === "sky" && this._switchSkyStateWithTWEEN(e, t);
    }
  }
  clearAllTween() {
    this._tweenHandlerList.forEach((e) => {
      e.stop(), Yi(e);
    }), this._tweenHandlerList = [];
  }
  _switchSkyStateWithTWEEN(e, t) {
    var r, a, o;
    const i = this.comp.states.getCurrentStateConfig(), n = this.comp.states.getStateConfigById(e);
    if (i.mode === "sky" && (n == null ? void 0 : n.mode) === "sky") {
      if (n) {
        if (this.comp.states.setState(e), this.comp.tweenHandler && (this.comp.tweenHandler.stop(), Yi(this.comp.tweenHandler)), !this.comp._currentSkyOption)
          return;
        const l = {
          ...this.comp._currentSkyOption
        }, c = new Date(
          n.year,
          n.month,
          n.day,
          n.hours,
          n.minutes,
          n.seconds
        ), h = {
          date: c,
          time: c.getTime(),
          lat: n.latitude,
          lng: n.longitude,
          sunLightIntensityFactor: n.sunLightIntensityFactor,
          sunLightCastShadow: n.sunLightCastShadow,
          northOffset: n.northOffset,
          sunLightShadowRadius: n.sunLightShadowRadius,
          sunLightShadowBias: n.sunLightShadowBias,
          sunSize: n.sunSize,
          skyHDRIntensity: n.skyHDRIntensity,
          sunLightShadowArea: {
            near: ((r = n.sunLightShadowArea) == null ? void 0 : r.near) ?? 30,
            far: ((a = n.sunLightShadowArea) == null ? void 0 : a.far) ?? 500,
            size: ((o = n.sunLightShadowArea) == null ? void 0 : o.size) ?? 1024
          },
          sunLightShadowHelperVisible: n.sunLightShadowHelperVisible
        }, u = jn.createTweenHandler({
          mode: t.mode,
          duration: Rf(t),
          source: l,
          target: h,
          execute: (d, f) => {
            var v, g, m;
            const p = new Date(d.time);
            this.comp.updateSky({
              date: p,
              lat: d.lat,
              lng: d.lng,
              sunLightIntensityFactor: d.sunLightIntensityFactor,
              sunLightCastShadow: !0,
              northOffset: d.northOffset,
              sunLightShadowRadius: d.sunLightShadowRadius,
              sunLightShadowBias: d.sunLightShadowBias,
              sunSize: d.sunSize,
              skyHDRIntensity: d.skyHDRIntensity,
              sunLightShadowMapWidth: n.sunLightShadowMapWidth,
              sunLightShadowMapHeight: n.sunLightShadowMapHeight,
              sunLightShadowArea: {
                near: ((v = n.sunLightShadowArea) == null ? void 0 : v.near) ?? 30,
                far: ((g = n.sunLightShadowArea) == null ? void 0 : g.far) ?? 500,
                size: ((m = n.sunLightShadowArea) == null ? void 0 : m.size) ?? 1024
              },
              sunLightPosition: n.sunLightPosition ?? { x: 0, y: 0, z: 0 },
              sunLightShadowHelperVisible: n.sunLightShadowHelperVisible
            });
          },
          complete: () => {
            this.comp.setCurrentStateById(e), Yi(u), this.comp.tweenHandler = void 0;
          }
        });
        this.comp.tweenHandler = u;
      }
    } else
      this.comp.setCurrentStateById(e);
  }
}
var Y5e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const Z5e = "EngineGroup";
let rM = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "easyvProxy", new K5e(this));
  }
  update(e) {
    const t = this.states.getCurrentStateConfig(), { transform: i } = t;
    i && this.applyOffsetTransform(i);
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    if (e) {
      const { transform: t } = e.config;
      t && (t.position.x = this.entity.position.x, t.position.y = this.entity.position.y, t.position.z = this.entity.position.z, t.rotation.x = this.entity.rotation.x, t.rotation.y = this.entity.rotation.y, t.rotation.z = this.entity.rotation.z, t.scale.x = this.entity.scale.x, t.scale.y = this.entity.scale.y, t.scale.z = this.entity.scale.z, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), dt.Entity.refresh());
    }
  }
  applyOffsetTransform(e) {
    const { position: t, rotation: i, scale: n } = e;
    this.entity.position.set(t.x, t.y, t.z), this.entity.rotation.set(i.x, i.y, i.z), this.entity.scale.set(n.x, n.y, n.z);
  }
  getObjectById(e) {
    return this.entity;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  async updateFromJson(e, t) {
    const i = t.states;
    i && this.states.initStates(i), this.update(0), this.entity.eType = "EngineGroup", this.isInit = !0;
  }
};
rM = Y5e([
  ni(Z5e)
], rM);
class K5e {
  constructor(e) {
    this.comp = e;
  }
  /**
   * 切换组的状态
   * @param targetStateId
   * @param options
   */
  easyvSwitchComponentStateWithTWEEN(e, t) {
    const i = this.comp.states.getStateConfigById(e);
    if (i) {
      this.comp.states.setState(e);
      const n = {
        position_x: this.comp.entity.position.x,
        position_y: this.comp.entity.position.y,
        position_z: this.comp.entity.position.z,
        rotation_x: this.comp.entity.rotation.x,
        rotation_y: this.comp.entity.rotation.y,
        rotation_z: this.comp.entity.rotation.z,
        scale_x: this.comp.entity.scale.x,
        scale_y: this.comp.entity.scale.y,
        scale_z: this.comp.entity.scale.z
      }, r = {
        position_x: i.transform.position.x,
        position_y: i.transform.position.y,
        position_z: i.transform.position.z,
        rotation_x: i.transform.rotation.x,
        rotation_y: i.transform.rotation.y,
        rotation_z: i.transform.rotation.z,
        scale_x: i.transform.scale.x,
        scale_y: i.transform.scale.y,
        scale_z: i.transform.scale.z
      };
      this.componentTransformAnimation(n, r, t);
    }
  }
  easyvSetData(e) {
    "animation" in e || (e.animation = {
      mode: "Linear.None",
      duration: 0
    }), this.playTransformAnimation(e, "deg");
  }
  playTransformAnimation(e, t = "rad") {
    if ("animation" in e) {
      const i = e.animation, n = {
        duration: (i == null ? void 0 : i.duration) ?? 1,
        mode: i == null ? void 0 : i.mode,
        distance: i == null ? void 0 : i.distance
      }, r = {
        position_x: this.comp.entity.position.x,
        position_y: this.comp.entity.position.y,
        position_z: this.comp.entity.position.z,
        rotation_x: this.comp.entity.rotation.x,
        rotation_y: this.comp.entity.rotation.y,
        rotation_z: this.comp.entity.rotation.z,
        scale_x: this.comp.entity.scale.x,
        scale_y: this.comp.entity.scale.y,
        scale_z: this.comp.entity.scale.z
      }, a = (l) => t === "deg" ? L1(l) : l, { transform: o } = e;
      if (o) {
        const l = o.position, c = o.rotation, h = o.scale;
        let u = this.comp.group.position.x, d = this.comp.group.position.y, f = this.comp.group.position.z;
        if (l && ("x" in l && (u = l.x), "y" in l && (d = l.y), "z" in l && (f = l.z), "lon" in l && "lat" in l)) {
          const v = Lu(
            this.comp.entity.world,
            l.lon,
            l.lat,
            d
          );
          u = v.x, d = v.y, f = v.z;
        }
        const p = {
          position_x: u,
          position_y: d,
          position_z: f,
          rotation_x: a(c == null ? void 0 : c.x) ?? this.comp.group.rotation.x,
          rotation_y: a(c == null ? void 0 : c.y) ?? this.comp.group.rotation.y,
          rotation_z: a(c == null ? void 0 : c.z) ?? this.comp.group.rotation.z,
          scale_x: (h == null ? void 0 : h.x) ?? this.comp.group.scale.x,
          scale_y: (h == null ? void 0 : h.y) ?? this.comp.group.scale.y,
          scale_z: (h == null ? void 0 : h.z) ?? this.comp.group.scale.z
        };
        this.componentTransformAnimation(r, p, n);
      }
    }
  }
  componentTransformAnimation(e, t, i) {
    this.stopTweenAnimation(), this.comp.tweenHandler = jn.createTweenHandler({
      mode: i.mode,
      duration: J5e(i),
      source: e,
      target: t,
      execute: (n) => {
        Q5e(this.comp, n);
      },
      complete: () => {
        this.stopTweenAnimation();
      }
    });
  }
  stopTweenAnimation() {
    this.comp.tweenHandler && (this.comp.tweenHandler.stop(), Yi(this.comp.tweenHandler), this.comp.tweenHandler = void 0);
  }
}
function J5e(s) {
  return s.duration * 1e3;
}
function Q5e(s, e) {
  s.entity.position.x = e.position_x, s.entity.position.y = e.position_y, s.entity.position.z = e.position_z, s.entity.rotation.x = e.rotation_x, s.entity.rotation.y = e.rotation_y, s.entity.rotation.z = e.rotation_z, s.entity.scale.x = e.scale_x, s.entity.scale.y = e.scale_y, s.entity.scale.z = e.scale_z;
}
var tAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const iAe = "Point";
let Kg = class extends u8 {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
    S(this, "queue", []);
  }
  static getName() {
    return "散点组件";
  }
  add(e, t, i = {}, n, r = !0) {
    if (this.states.curStateId) {
      const a = this.states.deepGet(this.states.curStateId);
      if (a && a.config) {
        const { alwaysTop: o, imgUrl: l, size: c, offset: h, padding: u } = a.config, d = new Xme(
          e,
          n ?? `散点${this.childMap.size}`,
          {
            imgUrl: l,
            alwaysTop: o,
            size: c,
            offset: h,
            position: t,
            padding: u
          }
          // rows,
        );
        d.userData = i, this.childMap.set(e, d), r ? this.group.attach(d) : this.group.add(d);
      }
    }
  }
  easyvSetStateByAttributes(e, t) {
    const i = this.states.deepGet(e), n = this.filterByAttribute(Array.from(this.childMap.values()), t);
    i && n.forEach((r) => {
      r.updateConfig(i.config);
    }), e || console.warn(`${e}状态未找到`), n.length === 0 && console.warn(`${t}空间物体未找到`);
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    this.childMap.forEach((i) => i.updateConfig(t)), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    );
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  getDataLabels() {
    const e = [];
    return this.childMap.forEach((t, i) => {
      e.push({
        id: i,
        label: t.name,
        componentId: this.id,
        entityId: this.entity.uuid
      });
    }), e;
  }
  setData(e) {
    this.childMap.forEach((t) => this.group.remove(t)), this.childMap.clear(), e.children.forEach((t) => {
      const { id: i, name: n, position: r, customAttribute: a } = t;
      if ("lon" in r && "lat" in r) {
        const { x: o, z: l } = Lu(
          this.entity.world,
          r.lon,
          r.lat,
          r.y ?? 0
        );
        r.x = o, r.z = l;
      }
      this.add(i, r, a, n);
    });
  }
  getSize() {
    this.box.makeEmpty();
    const e = this.states.getCurrentStateConfig(), t = new _(1, 1, 1);
    e && t.set(e.size.x, e.size.y, 1);
    const i = new zt();
    return this.childMap.forEach((n) => {
      i.setFromCenterAndSize(n.position, t), this.box.union(i), i.makeEmpty();
    }), this.box.getSize(new _());
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      scaleOptions: this.scaleOptions,
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (d) => this.add(d.id, d.position, d.customAttribute, d.name)
    );
    const { x: l, y: c, z: h } = uh(o, this);
    l && this.group.position.setX(l), c && this.group.position.setY(c), h && this.group.position.setZ(h);
    const { scaleOptions: u } = this;
    Object.assign(u, o.scaleOptions), this.setScaleOptions(u), this.positionMode = o.positionMode ?? "xyz", this.isInit = !0;
  }
  draw(e, t) {
    e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this);
    const i = /* @__PURE__ */ new Set(), n = (l) => {
      const { position: c, coord: h } = l, u = e.raycaster(h, this.group);
      let d = !1;
      if (!u)
        d = !0;
      else {
        const f = this.getObjectById(u.object.uuid);
        if (f) {
          const p = this.childMap.get(f.uuid);
          p && (d = !p.visible);
        }
      }
      d && e.executeDrawCommand("add-point", { position: c });
    }, r = (l) => {
      const c = this.getObjectById(l.point.uuid);
      c && (c.visible = !1), c && i.add(c.uuid);
    }, a = (l) => {
      if (i.has(l.point.uuid)) {
        const h = this.getObjectById(l.point.uuid);
        h && (h.visible = !0), i.delete(l.point.uuid);
      } else {
        console.log(l.point);
        const h = {};
        this.group.children.forEach((u) => {
          Object.assign(h, u.userData);
        });
        for (let u in h)
          h[u] = void 0;
        this.add(l.point.uuid, l.point.sprite.position, h);
      }
    }, o = () => {
      i.forEach((l) => {
        this.removeObjectById(l);
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", n), e.removeEventListener("end-draw", o), e.removeEventListener("add-point", a), e.removeEventListener("remove-point", r);
    };
    e.addEventListener("draw-point", n), e.addEventListener("end-draw", o), e.addEventListener("add-point", a), e.addEventListener("remove-point", r);
  }
  dispose() {
    this.childMap.forEach((e) => {
      var t;
      (t = e.parent) == null || t.remove(e), e.dispose();
    });
  }
};
Kg = tAe([
  ni(iAe)
], Kg);
const pl = 11102230246251565e-32, Ys = 134217729, nAe = (3 + 8 * pl) * pl;
function U6(s, e, t, i, n) {
  let r, a, o, l, c = e[0], h = i[0], u = 0, d = 0;
  h > c == h > -c ? (r = c, c = e[++u]) : (r = h, h = i[++d]);
  let f = 0;
  if (u < s && d < t)
    for (h > c == h > -c ? (a = c + r, o = r - (a - c), c = e[++u]) : (a = h + r, o = r - (a - h), h = i[++d]), r = a, o !== 0 && (n[f++] = o); u < s && d < t; )
      h > c == h > -c ? (a = r + c, l = a - r, o = r - (a - l) + (c - l), c = e[++u]) : (a = r + h, l = a - r, o = r - (a - l) + (h - l), h = i[++d]), r = a, o !== 0 && (n[f++] = o);
  for (; u < s; )
    a = r + c, l = a - r, o = r - (a - l) + (c - l), c = e[++u], r = a, o !== 0 && (n[f++] = o);
  for (; d < t; )
    a = r + h, l = a - r, o = r - (a - l) + (h - l), h = i[++d], r = a, o !== 0 && (n[f++] = o);
  return (r !== 0 || f === 0) && (n[f++] = r), f;
}
function sAe(s, e) {
  let t = e[0];
  for (let i = 1; i < s; i++)
    t += e[i];
  return t;
}
function q2(s) {
  return new Float64Array(s);
}
const rAe = (3 + 16 * pl) * pl, aAe = (2 + 12 * pl) * pl, oAe = (9 + 64 * pl) * pl * pl, uf = q2(4), HD = q2(8), VD = q2(12), WD = q2(16), ur = q2(4);
function lAe(s, e, t, i, n, r, a) {
  let o, l, c, h, u, d, f, p, v, g, m, x, y, A, b, M, w, C;
  const T = s - n, E = t - n, P = e - r, L = i - r;
  A = T * L, d = Ys * T, f = d - (d - T), p = T - f, d = Ys * L, v = d - (d - L), g = L - v, b = p * g - (A - f * v - p * v - f * g), M = P * E, d = Ys * P, f = d - (d - P), p = P - f, d = Ys * E, v = d - (d - E), g = E - v, w = p * g - (M - f * v - p * v - f * g), m = b - w, u = b - m, uf[0] = b - (m + u) + (u - w), x = A + m, u = x - A, y = A - (x - u) + (m - u), m = y - M, u = y - m, uf[1] = y - (m + u) + (u - M), C = x + m, u = C - x, uf[2] = x - (C - u) + (m - u), uf[3] = C;
  let I = sAe(4, uf), N = aAe * a;
  if (I >= N || -I >= N || (u = s - T, o = s - (T + u) + (u - n), u = t - E, c = t - (E + u) + (u - n), u = e - P, l = e - (P + u) + (u - r), u = i - L, h = i - (L + u) + (u - r), o === 0 && l === 0 && c === 0 && h === 0) || (N = oAe * a + nAe * Math.abs(I), I += T * h + L * o - (P * c + E * l), I >= N || -I >= N))
    return I;
  A = o * L, d = Ys * o, f = d - (d - o), p = o - f, d = Ys * L, v = d - (d - L), g = L - v, b = p * g - (A - f * v - p * v - f * g), M = l * E, d = Ys * l, f = d - (d - l), p = l - f, d = Ys * E, v = d - (d - E), g = E - v, w = p * g - (M - f * v - p * v - f * g), m = b - w, u = b - m, ur[0] = b - (m + u) + (u - w), x = A + m, u = x - A, y = A - (x - u) + (m - u), m = y - M, u = y - m, ur[1] = y - (m + u) + (u - M), C = x + m, u = C - x, ur[2] = x - (C - u) + (m - u), ur[3] = C;
  const H = U6(4, uf, 4, ur, HD);
  A = T * h, d = Ys * T, f = d - (d - T), p = T - f, d = Ys * h, v = d - (d - h), g = h - v, b = p * g - (A - f * v - p * v - f * g), M = P * c, d = Ys * P, f = d - (d - P), p = P - f, d = Ys * c, v = d - (d - c), g = c - v, w = p * g - (M - f * v - p * v - f * g), m = b - w, u = b - m, ur[0] = b - (m + u) + (u - w), x = A + m, u = x - A, y = A - (x - u) + (m - u), m = y - M, u = y - m, ur[1] = y - (m + u) + (u - M), C = x + m, u = C - x, ur[2] = x - (C - u) + (m - u), ur[3] = C;
  const U = U6(H, HD, 4, ur, VD);
  A = o * h, d = Ys * o, f = d - (d - o), p = o - f, d = Ys * h, v = d - (d - h), g = h - v, b = p * g - (A - f * v - p * v - f * g), M = l * c, d = Ys * l, f = d - (d - l), p = l - f, d = Ys * c, v = d - (d - c), g = c - v, w = p * g - (M - f * v - p * v - f * g), m = b - w, u = b - m, ur[0] = b - (m + u) + (u - w), x = A + m, u = x - A, y = A - (x - u) + (m - u), m = y - M, u = y - m, ur[1] = y - (m + u) + (u - M), C = x + m, u = C - x, ur[2] = x - (C - u) + (m - u), ur[3] = C;
  const W = U6(U, VD, 4, ur, WD);
  return WD[W - 1];
}
function xy(s, e, t, i, n, r) {
  const a = (e - r) * (t - n), o = (s - n) * (i - r), l = a - o, c = Math.abs(a + o);
  return Math.abs(l) >= rAe * c ? l : -lAe(s, e, t, i, n, r, c);
}
const XD = Math.pow(2, -52), Ay = new Uint32Array(512);
class Gp {
  static from(e, t = fAe, i = pAe) {
    const n = e.length, r = new Float64Array(n * 2);
    for (let a = 0; a < n; a++) {
      const o = e[a];
      r[2 * a] = t(o), r[2 * a + 1] = i(o);
    }
    return new Gp(r);
  }
  constructor(e) {
    const t = e.length >> 1;
    if (t > 0 && typeof e[0] != "number")
      throw new Error("Expected coords to contain numbers.");
    this.coords = e;
    const i = Math.max(2 * t - 5, 0);
    this._triangles = new Uint32Array(i * 3), this._halfedges = new Int32Array(i * 3), this._hashSize = Math.ceil(Math.sqrt(t)), this._hullPrev = new Uint32Array(t), this._hullNext = new Uint32Array(t), this._hullTri = new Uint32Array(t), this._hullHash = new Int32Array(this._hashSize), this._ids = new Uint32Array(t), this._dists = new Float64Array(t), this.update();
  }
  update() {
    const { coords: e, _hullPrev: t, _hullNext: i, _hullTri: n, _hullHash: r } = this, a = e.length >> 1;
    let o = 1 / 0, l = 1 / 0, c = -1 / 0, h = -1 / 0;
    for (let T = 0; T < a; T++) {
      const E = e[2 * T], P = e[2 * T + 1];
      E < o && (o = E), P < l && (l = P), E > c && (c = E), P > h && (h = P), this._ids[T] = T;
    }
    const u = (o + c) / 2, d = (l + h) / 2;
    let f, p, v;
    for (let T = 0, E = 1 / 0; T < a; T++) {
      const P = z6(u, d, e[2 * T], e[2 * T + 1]);
      P < E && (f = T, E = P);
    }
    const g = e[2 * f], m = e[2 * f + 1];
    for (let T = 0, E = 1 / 0; T < a; T++) {
      if (T === f)
        continue;
      const P = z6(g, m, e[2 * T], e[2 * T + 1]);
      P < E && P > 0 && (p = T, E = P);
    }
    let x = e[2 * p], y = e[2 * p + 1], A = 1 / 0;
    for (let T = 0; T < a; T++) {
      if (T === f || T === p)
        continue;
      const E = uAe(g, m, x, y, e[2 * T], e[2 * T + 1]);
      E < A && (v = T, A = E);
    }
    let b = e[2 * v], M = e[2 * v + 1];
    if (A === 1 / 0) {
      for (let P = 0; P < a; P++)
        this._dists[P] = e[2 * P] - e[0] || e[2 * P + 1] - e[1];
      Lf(this._ids, this._dists, 0, a - 1);
      const T = new Uint32Array(a);
      let E = 0;
      for (let P = 0, L = -1 / 0; P < a; P++) {
        const I = this._ids[P], N = this._dists[I];
        N > L && (T[E++] = I, L = N);
      }
      this.hull = T.subarray(0, E), this.triangles = new Uint32Array(0), this.halfedges = new Uint32Array(0);
      return;
    }
    if (xy(g, m, x, y, b, M) < 0) {
      const T = p, E = x, P = y;
      p = v, x = b, y = M, v = T, b = E, M = P;
    }
    const w = dAe(g, m, x, y, b, M);
    this._cx = w.x, this._cy = w.y;
    for (let T = 0; T < a; T++)
      this._dists[T] = z6(e[2 * T], e[2 * T + 1], w.x, w.y);
    Lf(this._ids, this._dists, 0, a - 1), this._hullStart = f;
    let C = 3;
    i[f] = t[v] = p, i[p] = t[f] = v, i[v] = t[p] = f, n[f] = 0, n[p] = 1, n[v] = 2, r.fill(-1), r[this._hashKey(g, m)] = f, r[this._hashKey(x, y)] = p, r[this._hashKey(b, M)] = v, this.trianglesLen = 0, this._addTriangle(f, p, v, -1, -1, -1);
    for (let T = 0, E, P; T < this._ids.length; T++) {
      const L = this._ids[T], I = e[2 * L], N = e[2 * L + 1];
      if (T > 0 && Math.abs(I - E) <= XD && Math.abs(N - P) <= XD || (E = I, P = N, L === f || L === p || L === v))
        continue;
      let H = 0;
      for (let te = 0, re = this._hashKey(I, N); te < this._hashSize && (H = r[(re + te) % this._hashSize], !(H !== -1 && H !== i[H])); te++)
        ;
      H = t[H];
      let U = H, W;
      for (; W = i[U], xy(I, N, e[2 * U], e[2 * U + 1], e[2 * W], e[2 * W + 1]) >= 0; )
        if (U = W, U === H) {
          U = -1;
          break;
        }
      if (U === -1)
        continue;
      let X = this._addTriangle(U, L, i[U], -1, -1, n[U]);
      n[L] = this._legalize(X + 2), n[U] = X, C++;
      let K = i[U];
      for (; W = i[K], xy(I, N, e[2 * K], e[2 * K + 1], e[2 * W], e[2 * W + 1]) < 0; )
        X = this._addTriangle(K, L, W, n[L], -1, n[K]), n[L] = this._legalize(X + 2), i[K] = K, C--, K = W;
      if (U === H)
        for (; W = t[U], xy(I, N, e[2 * W], e[2 * W + 1], e[2 * U], e[2 * U + 1]) < 0; )
          X = this._addTriangle(W, L, U, -1, n[U], n[W]), this._legalize(X + 2), n[W] = X, i[U] = U, C--, U = W;
      this._hullStart = t[L] = U, i[U] = t[K] = L, i[L] = K, r[this._hashKey(I, N)] = L, r[this._hashKey(e[2 * U], e[2 * U + 1])] = U;
    }
    this.hull = new Uint32Array(C);
    for (let T = 0, E = this._hullStart; T < C; T++)
      this.hull[T] = E, E = i[E];
    this.triangles = this._triangles.subarray(0, this.trianglesLen), this.halfedges = this._halfedges.subarray(0, this.trianglesLen);
  }
  _hashKey(e, t) {
    return Math.floor(cAe(e - this._cx, t - this._cy) * this._hashSize) % this._hashSize;
  }
  _legalize(e) {
    const { _triangles: t, _halfedges: i, coords: n } = this;
    let r = 0, a = 0;
    for (; ; ) {
      const o = i[e], l = e - e % 3;
      if (a = l + (e + 2) % 3, o === -1) {
        if (r === 0)
          break;
        e = Ay[--r];
        continue;
      }
      const c = o - o % 3, h = l + (e + 1) % 3, u = c + (o + 2) % 3, d = t[a], f = t[e], p = t[h], v = t[u];
      if (hAe(
        n[2 * d],
        n[2 * d + 1],
        n[2 * f],
        n[2 * f + 1],
        n[2 * p],
        n[2 * p + 1],
        n[2 * v],
        n[2 * v + 1]
      )) {
        t[e] = v, t[o] = d;
        const m = i[u];
        if (m === -1) {
          let y = this._hullStart;
          do {
            if (this._hullTri[y] === u) {
              this._hullTri[y] = e;
              break;
            }
            y = this._hullPrev[y];
          } while (y !== this._hullStart);
        }
        this._link(e, m), this._link(o, i[a]), this._link(a, u);
        const x = c + (o + 1) % 3;
        r < Ay.length && (Ay[r++] = x);
      } else {
        if (r === 0)
          break;
        e = Ay[--r];
      }
    }
    return a;
  }
  _link(e, t) {
    this._halfedges[e] = t, t !== -1 && (this._halfedges[t] = e);
  }
  // add a new triangle given vertex indices and adjacent half-edge ids
  _addTriangle(e, t, i, n, r, a) {
    const o = this.trianglesLen;
    return this._triangles[o] = e, this._triangles[o + 1] = t, this._triangles[o + 2] = i, this._link(o, n), this._link(o + 1, r), this._link(o + 2, a), this.trianglesLen += 3, o;
  }
}
function cAe(s, e) {
  const t = s / (Math.abs(s) + Math.abs(e));
  return (e > 0 ? 3 - t : 1 + t) / 4;
}
function z6(s, e, t, i) {
  const n = s - t, r = e - i;
  return n * n + r * r;
}
function hAe(s, e, t, i, n, r, a, o) {
  const l = s - a, c = e - o, h = t - a, u = i - o, d = n - a, f = r - o, p = l * l + c * c, v = h * h + u * u, g = d * d + f * f;
  return l * (u * g - v * f) - c * (h * g - v * d) + p * (h * f - u * d) < 0;
}
function uAe(s, e, t, i, n, r) {
  const a = t - s, o = i - e, l = n - s, c = r - e, h = a * a + o * o, u = l * l + c * c, d = 0.5 / (a * c - o * l), f = (c * h - o * u) * d, p = (a * u - l * h) * d;
  return f * f + p * p;
}
function dAe(s, e, t, i, n, r) {
  const a = t - s, o = i - e, l = n - s, c = r - e, h = a * a + o * o, u = l * l + c * c, d = 0.5 / (a * c - o * l), f = s + (c * h - o * u) * d, p = e + (a * u - l * h) * d;
  return { x: f, y: p };
}
function Lf(s, e, t, i) {
  if (i - t <= 20)
    for (let n = t + 1; n <= i; n++) {
      const r = s[n], a = e[r];
      let o = n - 1;
      for (; o >= t && e[s[o]] > a; )
        s[o + 1] = s[o--];
      s[o + 1] = r;
    }
  else {
    const n = t + i >> 1;
    let r = t + 1, a = i;
    t1(s, n, r), e[s[t]] > e[s[i]] && t1(s, t, i), e[s[r]] > e[s[i]] && t1(s, r, i), e[s[t]] > e[s[r]] && t1(s, t, r);
    const o = s[r], l = e[o];
    for (; ; ) {
      do
        r++;
      while (e[s[r]] < l);
      do
        a--;
      while (e[s[a]] > l);
      if (a < r)
        break;
      t1(s, r, a);
    }
    s[t + 1] = s[a], s[a] = o, i - r + 1 >= a - t ? (Lf(s, e, r, i), Lf(s, e, t, a - 1)) : (Lf(s, e, t, a - 1), Lf(s, e, r, i));
  }
}
function t1(s, e, t) {
  const i = s[e];
  s[e] = s[t], s[t] = i;
}
function fAe(s) {
  return s[0];
}
function pAe(s) {
  return s[1];
}
class mAe extends Pt {
  constructor() {
    super(...arguments);
    S(this, "box", new zt());
    S(this, "pointData", []);
    S(this, "radius", 0.4);
  }
  addPoint(t, i, n) {
    const r = { x: i.x, y: i.y, z: i.z };
    this.pointData.push({
      id: t,
      position: r,
      value: n,
      name: `聚合热力${this.pointData.length + 1}`,
      customAttribute: {}
    }), this.box.expandByPoint(i);
    const a = new _(), o = new _();
    this.box.getCenter(a), this.box.getSize(o);
    const l = this.radius, h = [...[
      new _(-o.x / 2 + a.x - l, 0, o.z / 2 + a.z + l),
      new _(o.x / 2 + a.x + l, 0, o.z / 2 + a.z + l),
      new _(-o.x / 2 + a.x - l, 0, -o.z / 2 + a.z - l),
      new _(o.x / 2 + a.x + l, 0, -o.z / 2 + a.z - l)
    ], ...this.getDivisionPoints(l, 10)];
    this.setFromPoints(h);
    const u = Gp.from(
      h.map((f) => [f.x, f.z])
    ), d = [];
    for (let f = 0; f < u.triangles.length; f++)
      d.push(u.triangles[f]);
    this.setIndex(d), this.computeVertexNormals(), this.computeBoundingSphere();
  }
  getDivisionPoints(t, i) {
    const n = [];
    return this.pointData.forEach(({ position: r }) => {
      n.push(new _(r.x, r.y, r.z));
      for (let a = 0.5; a < t * 1.5; a += 2)
        for (let o = 0; o < 360; o += 360 / i) {
          const l = o * Math.PI / 180, c = r.x + a * Math.cos(l), h = r.z + a * Math.sin(l);
          n.push(new _(c, r.y, h));
        }
    }), n;
  }
  removePoints(t) {
    const i = this.pointData.filter((n) => !t.includes(n.id));
    this.setPoints(i);
  }
  setPoints(t) {
    this.pointData = [...t], this.box.makeEmpty();
    const i = new _();
    this.pointData.forEach(({ position: u }) => {
      i.set(u.x, u.y, u.z), this.box.expandByPoint(i);
    });
    const n = new _(), r = new _();
    this.box.getCenter(n), this.box.getSize(r);
    const a = this.radius, l = [...[
      new _(-r.x / 2 + n.x - a, 0, r.z / 2 + n.z + a),
      new _(r.x / 2 + n.x + a, 0, r.z / 2 + n.z + a),
      new _(-r.x / 2 + n.x - a, 0, -r.z / 2 + n.z - a),
      new _(r.x / 2 + n.x + a, 0, -r.z / 2 + n.z - a)
    ], ...this.getDivisionPoints(a, 10)];
    this.setFromPoints(l);
    const c = Gp.from(
      l.map((u) => [u.x, u.z])
    ), h = [];
    for (let u = 0; u < c.triangles.length; u++)
      h.push(c.triangles[u]);
    this.setIndex(h), this.computeVertexNormals(), this.computeBoundingSphere();
  }
  refresh() {
    this.setPoints(this.pointData);
  }
  updatePointValue(t, i) {
    this.pointData.map((n) => (n.id === t && (n.value = i), n));
  }
  updatePointNamePosition(t, i, n) {
    let r = !1;
    this.pointData.forEach((a) => (a.id === t && (a.name = i, n.x === a.position.x && n.y === a.position.y && n.z === a.position.z ? r = !0 : a.position = n, a.position = n), a)), r && this.setPoints(this.pointData);
  }
  dispose() {
    super.dispose(), this.pointData.length = 0, this.box.makeEmpty();
  }
}
const Of = class Of extends Jt {
  constructor(e, t) {
    const { stepColor: i, radius: n, blurFactor: r, minHeatHeight: a, maxHeatHeight: o } = t;
    i.sort((g, m) => g.offset - m.offset);
    const l = i.map(({ color: g }) => new pi(g)), c = e.map((g) => g.position), h = e.map((g) => g.value), u = i.map((g, m) => l[m].getAlpha()), d = i.map((g) => g.offset / 100), f = i.map(
      (g, m) => new _e(l[m].r / 255, l[m].g / 255, l[m].b / 255)
    );
    let p = Of.vertex, v = Of.frag;
    e.length === 0 && (p = "", v = ""), super({
      vertexShader: p,
      fragmentShader: v,
      // wireframe: true,
      side: ui,
      transparent: !0,
      depthWrite: !1,
      defines: {
        PointCount: e.length,
        StepCount: f.length
      },
      uniforms: {
        points: {
          value: c
        },
        pointValues: {
          value: h
        },
        stepOpacity: {
          value: u
        },
        stepOffset: {
          value: d
        },
        stepColor: {
          value: f
        },
        maxValue: {
          value: Math.max(...h)
        },
        blurFactor: {
          value: r
        },
        radius: {
          value: n
        },
        minHeight: {
          value: a
        },
        maxHeight: {
          value: o
        }
      }
    });
  }
};
S(
  Of,
  "vertex",
  /* glsl */
  `
    varying vec2 vUv;
    varying mat4 pointMat;
    varying vec4 vPosition;
    uniform vec3 points[PointCount];
    uniform float pointValues[PointCount];
    uniform float maxValue;
    uniform float radius;
    uniform float minHeight;
    uniform float maxHeight;
    
    void main() {
      vUv = uv;
      
      float d = 0.;
      vec4 mp = modelMatrix * vec4( position, 1.0 );
      float deltaHeight = maxHeight - minHeight;
      for (int i = 0; i < PointCount; i++) {
          vec3 v = vec4(modelMatrix * vec4(points[i], 1.)).xyz;
          float intensity = pointValues[i] / maxValue;
          float pd = (1. - length(mp.xz - v.xz) / radius) * intensity;
          d += pow(max(0., pd), 2.);
      }
      // mp.y += d;
      float y = clamp(d * (maxHeight + minHeight), 0., maxHeight);
      mp.y += y;
      gl_Position = projectionMatrix * viewMatrix * mp;
      vPosition = mp;
      pointMat = modelMatrix;
    }
  `
), S(
  Of,
  "frag",
  /* glsl */
  `
    varying vec2 vUv;
    varying vec4 vPosition;
    varying mat4 pointMat;
    uniform float blurFactor;
    uniform float stepOffset[StepCount];
    uniform vec3 stepColor[StepCount];
    uniform float stepOpacity[StepCount];
    uniform vec3 points[PointCount];
    uniform float pointValues[PointCount];
    uniform float maxValue;
    uniform float radius;

    vec4 gradient(float w) {
      // w = pow(clamp(w, 0., 1.) * 3.14159 * .5, .9);

      w = clamp(w, 0., 1.);
      // return vec4(mix(vec3(1.0, 1.0, 1.0), c, w), 1.);
      for (int i = 1; i < StepCount; i++) {
        if(w >= stepOffset[i - 1] && w <= stepOffset[i]) {
          float opacity = mix(stepOpacity[i - 1], stepOpacity[i], w);
          return vec4(mix(stepColor[i - 1], stepColor[i], w / blurFactor), opacity);
        }
      }

      vec3 c = vec3(sin(w), sin(w * 2.), cos(w)) * 1.1;
      return vec4(1.0);
    }

    void main() {
      vec2 uv = vPosition.xz;

      float d = 0.;
      for (int i = 0; i < PointCount; i++) {
          vec3 v = vec4(pointMat * vec4(points[i], 1.)).xyz;
          float intensity = pointValues[i] / maxValue;
          float pd = (1. - length(uv - v.xz) / radius) * intensity;
          d += pow(max(0., pd), 2.);
      }

      vec4 color = gradient(d);
      float alpha = color.a;
      if(d == 0.) discard;
      gl_FragColor = vec4(color.xyz, alpha);
    }
  `
);
let ou = Of;
var yAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
document.createElement("canvas");
const xAe = "ClusteringHeatmap";
let $x = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "mesh", new Re(
      new mAe()
    ));
    S(this, "_pickObject", new mt());
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {
        groupPosition: {
          x: this.group.position.x,
          y: this.group.position.y,
          z: this.group.position.z
        },
        children: this.mesh.geometry.pointData,
        positionMode: this.positionMode
      }
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a), this.initialize(e.renderer.threeRenderer.domElement), this.setData(t.data), this.isInit = !0;
  }
  initMesh() {
    this.group.add(this.mesh), this.mesh.renderOrder = 10;
  }
  setData(e) {
    this.initMesh();
    const { groupPosition: t, children: i } = e, n = this.states.getCurrentStateConfig(), { radius: r = 5 } = n;
    this.group.position.set(t.x, t.y, t.z), this.mesh.geometry.radius = r, this.mesh.geometry.setPoints(i), this.mesh.material.dispose();
    const a = this.mesh.geometry.pointData.map(({ position: o, value: l }) => ({
      position: new _(o.x, o.y, o.z),
      value: l
    }));
    this.mesh.material = new ou(a, n);
  }
  addPoint(e, t, i) {
    const n = this.states.getCurrentStateConfig();
    this.mesh.geometry.radius = n.radius, this.mesh.geometry.addPoint(e, t, i), this.mesh.material.dispose();
    const r = this.mesh.geometry.pointData.map(({ position: a, value: o }) => ({
      position: new _(a.x, a.y, a.z),
      value: o
    }));
    this.mesh.material = new ou(r, n);
  }
  updateDataById(e, t, i, n = 10) {
    this.mesh.geometry.updatePointValue(e, n), this.mesh.geometry.updatePointNamePosition(e, t, i);
    const r = this.states.getCurrentStateConfig();
    this.mesh.material.dispose();
    const a = this.mesh.geometry.pointData.map(({ position: o, value: l }) => ({
      position: new _(o.x, o.y, o.z),
      value: l
    }));
    this.mesh.material = new ou(a, r), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  removeObjectById(e) {
    const t = this.states.getCurrentStateConfig();
    this.mesh.geometry.removePoints([e]), this.mesh.material.dispose();
    const i = this.mesh.geometry.pointData.map(({ position: n, value: r }) => ({
      position: new _(n.x, n.y, n.z),
      value: r
    }));
    this.mesh.material = new ou(i, t), this.needDataUpdateServer = !0;
  }
  // public intersectObject(
  //   raycaster: Raycaster,
  //   ev: MouseEvent,
  // ): Array<Intersection<Object3D<Object3DEventMap>>> {
  //   return raycaster.intersectObjects([this.mesh]);
  // }
  draw(e, t) {
    e.mode = "multi-point", e.startDraw(), e.setActiveComponent(this);
    const i = /* @__PURE__ */ new Set();
    this.initMesh();
    const { height: n } = this.states.getCurrentStateConfig();
    this.group.position.setY(n);
    const r = (c) => {
      const { position: h, coord: u } = c;
      e.executeDrawCommand("add-point", { position: h });
    }, a = (c) => {
      const h = this.getObjectById(c.point.uuid);
      h && (h.visible = !1), h && i.add(h.uuid);
    }, o = (c) => {
      if (i.has(c.point.uuid)) {
        const u = this.getObjectById(c.point.uuid);
        u && (u.visible = !0), i.delete(c.point.uuid);
      } else {
        const { position: u } = c.point.sprite;
        this.addPoint(c.point.uuid, u.clone(), 10);
      }
    }, l = () => {
      i.forEach((c) => {
        this.removeObjectById(c);
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", r), e.removeEventListener("end-draw", l), e.removeEventListener("add-point", o), e.removeEventListener("remove-point", a);
    };
    e.addEventListener("draw-point", r), e.addEventListener("end-draw", l), e.addEventListener("add-point", o), e.addEventListener("remove-point", a);
  }
  setCustomAttribute(e, t) {
    if (t) {
      const i = this.mesh.geometry.pointData.find((n) => n.id === t);
      i && Object.assign(i.customAttribute, e);
    } else
      this.mesh.geometry.pointData.forEach((i) => {
        Object.assign(i.customAttribute, e);
      });
    this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  getObjectById(e) {
    const { pointData: t } = this.mesh.geometry;
    this.group.add(this._pickObject);
    let i;
    return i = t.find((n) => n.id === e), i ? (this._pickObject.uuid = i.id, this._pickObject.userData = i.customAttribute, this._pickObject.name = i.name, this._pickObject.position.set(
      i.position.x,
      this.group.position.y + i.position.y,
      i.position.z
    ), this._pickObject) : (this._pickObject.uuid = "", this._pickObject);
  }
  updateCustomAttributeKey(e, t) {
    this.mesh.geometry.pointData.forEach((i) => {
      const n = i.customAttribute[e];
      delete i.customAttribute[e], i.customAttribute[t] = n;
    }), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  onDragEnd() {
    const { pointData: e } = this.mesh.geometry, t = e.find((i) => i.id === this._pickObject.uuid);
    t && (t.position.x = this._pickObject.position.x, t.position.y = this._pickObject.position.y - this.group.position.y, t.position.z = this._pickObject.position.z, this.mesh.geometry.setPoints(e), this.update(0)), dt.Entity.refresh();
  }
  removeCustomAttribute(e, t) {
    e && (this.mesh.geometry.pointData.forEach((i) => {
      delete i.customAttribute[e];
    }), this.needDataUpdateServer = !0, dt.Entity.refresh());
  }
  update(e) {
    this.mesh.material.dispose();
    const t = this.states.getCurrentStateConfig(), i = this.mesh.geometry.pointData.map(({ position: n, value: r }) => ({
      position: new _(n.x, n.y, n.z),
      value: r
    }));
    this.mesh.geometry.radius = t.radius, this.mesh.geometry.refresh(), this.group.position.setY(t.height), this.mesh.material = new ou(i, t);
  }
  dispose() {
    this.mesh.geometry.dispose(), this.mesh.material.dispose();
  }
};
$x = yAe([
  ni(xAe)
], $x);
class AAe extends Re {
  constructor(t) {
    super();
    S(this, "points", []);
    S(this, "meshIndex", []);
    S(this, "value", 10);
    const {
      points: i,
      meshIndex: n,
      id: r,
      name: a,
      customAttribute: o,
      value: l,
      position: c = { x: 0, y: 0, z: 0 }
    } = t;
    this.uuid = r, this.name = a, this.userData = o, this.value = Number(l), this.position.set(c.x, c.y, c.z), this.material.side = ui, i.length && n.length && this.updatePoints(
      i.map((h) => new _(h.x, h.y, h.z)),
      n
    );
  }
  updateGeometryNormal() {
    const t = this.geometry.index, i = this.geometry.getAttribute("position"), n = new Ht(new Float32Array(i.count * 3), 3);
    this.geometry.setAttribute("normal", n);
    const r = new _(), a = new _(), o = new _(), l = new _(), c = new _(), h = new _(), u = new _(), d = new _();
    if (t)
      for (let f = 0, p = t.count; f < p; f += 3) {
        const v = t.getX(f + 0), g = t.getX(f + 1), m = t.getX(f + 2);
        r.fromBufferAttribute(i, v), a.fromBufferAttribute(i, g), o.fromBufferAttribute(i, m), u.subVectors(o, a), d.subVectors(r, a), u.cross(d), l.fromBufferAttribute(n, v), c.fromBufferAttribute(n, g), h.fromBufferAttribute(n, m), l.add(u), c.add(u), h.add(u), n.setXYZ(v, l.x, l.y, l.z), n.setXYZ(g, c.x, c.y, c.z), n.setXYZ(m, h.x, h.y, h.z), this.geometry.normalizeNormals(), n.needsUpdate = !0;
      }
  }
  updatePoints(t, i) {
    this.points = [...t], this.meshIndex = [...i], this.geometry.setFromPoints(this.points), this.geometry.setIndex(this.meshIndex), this.updateGeometryNormal();
  }
  updateColor(t, i, n, r) {
    this.material.color = new _e(t, i, n), this.material.opacity = r, this.material.transparent = !0;
  }
  dispose() {
    this.geometry.dispose(), this.material.dispose();
  }
}
var SAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const wAe = "AreaHeatmap";
let aM = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  add(e, t = !0) {
    const i = new AAe(e);
    return t ? this.group.attach(i) : this.group.add(i), this.childMap.set(e.id, i), this.update(0), i;
  }
  update(e) {
    const t = this.states.getCurrentStateConfig(), { height: i, colorType: n, unFillColor: r, subsectionOptions: a, continuousOptions: o } = t;
    let l = r;
    r && typeof r != "string" && "pure" in r && (l = r.pure);
    const c = new pi(l);
    let h = Number.MAX_VALUE, u = 0;
    this.childMap.forEach((f) => {
      Number(f.value) > Number(u) && (u = f.value), Number(f.value) < Number(h) && (h = f.value);
    });
    const d = [];
    switch (n) {
      case "subsection":
        a.forEach(({ range: f, color: p }) => {
          const { min: v, max: g } = f;
          let m = p;
          typeof p != "string" && "pure" in p && (m = p.pure), d.push([new pi(m), v, g]);
        });
        break;
      case "continuous":
        o.sort((f, p) => f.offset - p.offset), o.forEach(({ offset: f }, p) => {
          if (p > 0) {
            const v = f * u / 100, g = o[p - 1].offset * u / 100;
            this.childMap.forEach((m) => {
              if (this._checkInvalidValue(m.value) && d.push([c, 0, 0]), m.value > g && m.value <= v) {
                const x = new pi(o[p - 1].color), y = new pi(o[p].color), A = (w, C, T) => w * (1 - T) + C * T, b = (m.value - g) / (v - g), M = new pi({
                  r: A(x.r, y.r, b),
                  g: A(x.g, y.g, b),
                  b: A(x.b, y.b, b),
                  a: A(x.a, y.a, b)
                });
                d.push([M, Number(m.value) - 1, Number(m.value) + 1]);
              }
            });
          }
        });
        break;
      default:
        d.push([new pi("#ffffff"), 0, 1]);
        break;
    }
    this.childMap.forEach((f) => {
      f.position.y = i;
      let p = !0;
      d.forEach(([v, g, m]) => {
        this._checkChildValueInRange(f.value, g, m) && (p = !1, f.updateColor(v.r / 255, v.g / 255, v.b / 255, v.a));
      }), p && f.updateColor(
        c.r / 255,
        c.g / 255,
        c.b / 255,
        c.a
      );
    });
  }
  updateDataById(e, t, i, n = [], r = 10) {
    const a = this.childMap.get(e);
    if (a) {
      a.name = t, a.position.set(i.x, i.y, i.z), a.name = t, a.value = r;
      const o = n.map((l) => new _(l.x, l.y, l.z));
      a.updatePoints(o, this._getDelaunatorMeshIndex(o)), this.update(0), this.needDataUpdateServer = !0;
    }
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = n);
      });
    }), e === "" ? this.group : t;
  }
  onDragEnd() {
    const e = this.states.getCurrentStateConfig();
    e.height = this.group.position.y, dt.Entity.refresh();
  }
  toJson() {
    const e = {
      groupPosition: {
        x: this.group.position.x,
        y: this.group.position.y,
        z: this.group.position.z
      },
      children: [],
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        name: t.name,
        value: t.value,
        points: t.points,
        position: t.position,
        meshIndex: t.meshIndex,
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r, data: a } = t;
    this.setData(a);
    const o = r;
    o && this.states.initStates(o), this.positionMode = a.positionMode ?? "xyz", this.update(0), this.isInit = !0;
  }
  setData(e) {
    const { children: t, groupPosition: i } = e;
    this.clear(), t.forEach((n) => {
      this.add({
        id: n.id,
        name: n.name,
        value: Number(n.value),
        points: n.points,
        meshIndex: n.meshIndex,
        position: n.position,
        customAttribute: n.customAttribute
      });
    }), i && this.group.position.set(i.x, i.y, i.z);
  }
  removeObjectById(e) {
    const t = this.childMap.get(e);
    this.childMap.delete(e), t && (t.dispose(), this.group.remove(t), this.needDataUpdateServer = !0, dt.Entity.refresh(), this.dispatchEvent(_i.RemoveChild, { id: e }));
  }
  draw(e, t) {
    e.mode = "multi-polygon", e.startDraw(), e.setActiveComponent(this), e.polygonUtils.setDrawGroupsVisible(["point"], !0);
    const i = /* @__PURE__ */ new Set();
    let n = nt.generateUUID();
    const r = () => {
      this.childMap.get(n) && (n = nt.generateUUID());
    };
    this.childMap.forEach((d) => {
      e.polygonUtils.addDrawPolygon(
        d.uuid,
        d.position,
        d.points,
        d.meshIndex
      );
    });
    const a = (d) => {
      e.executeDrawCommand("add-point", { position: d.position, drawId: n });
    }, o = (d) => {
      const { id: f } = d;
      [...e.drawRootPoint.children].forEach((p) => {
        p.userData.drawId === f && e.drawRootPoint.remove(p);
      });
    }, l = (d) => {
      if (i.has(d.polygon.uuid)) {
        const p = this.getObjectById(d.polygon.uuid);
        p && (p.visible = !0), i.delete(d.polygon.uuid);
      } else {
        const p = {};
        if (p) {
          const [g] = this.group.children;
          g && Object.keys(g.userData).forEach((m) => {
            p[m] = void 0;
          });
        }
        const v = this.childMap.get(d.polygon.uuid);
        v ? v.updatePoints(d.polygon.points, d.polygon.meshIndex) : this.add(
          {
            id: d.polygon.uuid,
            points: d.polygon.points,
            position: d.polygon.position,
            meshIndex: d.polygon.meshIndex,
            customAttribute: p,
            name: `区域${this.childMap.size}`,
            value: 10
          },
          !1
        );
      }
    }, c = (d) => {
      const f = d.point.userData.drawId, p = e.polygonUtils.updatePolygonMeshByDrawPointGroup(f), v = this.childMap.get(f);
      v && p && v.updatePoints(p.points, p.meshIndex);
    }, h = () => {
      this.needDataUpdateServer = !0;
    }, u = () => {
      i.forEach((d) => {
        this.removeObjectById(d);
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", a), e.removeEventListener("end-draw", u), e.removeEventListener("add-polygon", l), this.removeEventListener(_i.RemoveChild, o), e.removeEventListener("drag", c), e.removeEventListener("end-drag", h), e.removeEventListener("double-click", r);
    };
    e.addEventListener("draw-point", a), e.addEventListener("end-draw", u), e.addEventListener("add-polygon", l), this.addEventListener(_i.RemoveChild, o), e.addEventListener("drag", c), e.addEventListener("end-drag", h), e.addEventListener("double-click", r);
  }
  _checkInvalidValue(e) {
    return e === void 0 || e === "" || e === 0 || isNaN(e);
  }
  _checkChildValueInRange(e, t, i) {
    let n = !1;
    return e >= t && e <= i && (n = !0), n;
  }
  _getDelaunatorMeshIndex(e) {
    const t = Gp.from(
      e.map((n) => [n.x, n.z])
    ), i = [];
    for (let n = 0; n < t.triangles.length; n++)
      i.push(t.triangles[n]);
    return i;
  }
  // 为了防止清除和销毁可能存在逻辑不一致, 清除可以不清理材质等, 但销毁一定会
  clear() {
    this.group.remove(...this.group.children), this.childMap.clear();
  }
  dispose() {
    this.clear();
  }
};
aM = SAe([
  ni(wAe)
], aM);
var CAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const _Ae = "Template";
let e4 = class extends Fi {
  async updateFromJson(s, e) {
    this.isInit = !0;
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
};
e4 = CAe([
  ni(_Ae)
], e4);
const jD = new _(), PAe = {
  id: nt.generateUUID(),
  name: "路径",
  points: [],
  position: { x: 0, y: 0, z: 0 },
  config: {
    groupPosition: { x: 0, y: 0, z: 0 },
    animateType: "default",
    blendMode: Ac.NormalBlending,
    defaultAnimate: {
      routeColor: "#ffffff",
      routeOpacity: 0.5,
      flowPercent: 0.15,
      flowColor: {
        startColor: "#ffff00",
        endColor: "#ffffff",
        startOpacity: 0.8,
        endOpacity: 0.3
      },
      speed: 5,
      interval: 2,
      randomStart: !1
    },
    textureAnimate: {
      speed: 5,
      routeIconSpeed: 5,
      routeIconRotateAngle: 0,
      routeTextureRotateAngle: 0,
      repeat: 2,
      routeTextureUrl: "",
      routeIconUrl: "",
      routeIconVisible: !0,
      routeIconConstrainedScale: !1,
      routeIconAutoRotate: !1,
      routeIconSize: {
        x: 10,
        y: 10
      },
      reverseDirection: !1,
      routeIconCenter: {
        x: 0.5,
        y: 0.5
      },
      routeIconOffset: {
        x: 0,
        y: 0,
        z: 0
      }
    },
    lineWidth: 2
  },
  customAttribute: {}
};
class RAe extends Pl {
  constructor(t = PAe) {
    const { id: i, name: n, points: r, position: a, config: o } = t;
    super({
      id: i,
      type: "FlowRoute"
    });
    S(this, "lineMesh", new Re());
    S(this, "iconSprite", new wn());
    S(this, "iconMesh", new Re(new $n(1, 1)));
    S(this, "tweenId", "flow-route-tween");
    S(this, "width", 1);
    S(this, "path", new Mu());
    S(this, "_vec3Points", []);
    S(this, "type");
    // 上一次 默认纹理动画的循环次数
    S(this, "_lastDefaultCount", 0);
    S(this, "_time", performance.now());
    S(this, "_length", 0);
    S(this, "_percents", []);
    S(this, "_lastIconPosition", new _());
    this.options = t, this.lineMesh.renderOrder = 0, this.add(this.lineMesh), this.add(this.iconSprite), this.add(this.iconMesh), this.lineMesh.renderOrder = 10, this.iconSprite.renderOrder = 1e4, this.iconSprite.visible = !1, this.iconMesh.visible = !1, this.iconMesh.geometry.rotateX(Math.PI / 2), this.uuid = i, this.name = n, this.width = t.config.lineWidth, this.position.set(a.x, a.y, a.z), this.type = o.animateType, this.createWidthLineGeometry(r, o.lineWidth);
  }
  get length() {
    return this._length;
  }
  getVec3Points() {
    return [...this._vec3Points];
  }
  getPoints() {
    return [...this.options.points];
  }
  createMaterial(t) {
    const { defaultAnimate: i, textureAnimate: n } = t;
    switch (t.animateType) {
      case "default":
        this.createDefaultFlowMaterial({
          routeColor: i.routeColor,
          routeOpacity: i.routeOpacity,
          flowStartColor: i.flowColor.startColor,
          flowEndColor: i.flowColor.endColor,
          // 传入的透明度是 0 - 100
          flowStartOpacity: i.flowColor.startOpacity / 100,
          flowEndOpacity: i.flowColor.endOpacity / 100,
          percent: i.flowPercent / 100,
          speed: i.speed,
          interval: i.interval,
          randomStart: i.randomStart
        });
        break;
      case "texture":
        this.createRouteMaterial({
          color: "#ffffff",
          imgUrl: n.routeTextureUrl,
          repeat: n.repeat,
          speed: n.speed,
          rotationAngle: n.routeTextureRotateAngle
        }), this.createTextureFlowMaterial({
          speed: n.speed,
          imgUrl: n.routeIconUrl,
          reverseDirection: n.reverseDirection,
          size: n.routeIconSize,
          visible: n.routeIconVisible,
          center: n.routeIconCenter,
          offset: n.routeIconOffset,
          rotateAngle: n.routeIconRotateAngle,
          autoRotate: n.routeIconAutoRotate
        });
        break;
    }
    this.lineMesh.material.blending = Lx(t.blendMode), this.iconSprite.material.blending = Lx(t.blendMode);
  }
  updateMaterial(t) {
    this.lineMesh.material.dispose(), Object.assign(this.options.config, t), this.createMaterial(t);
  }
  createWidthLineGeometry(t, i) {
    if (t.length === 0)
      return;
    this._vec3Points.length = 0, this._length = 0, this._percents = [0], this.options.points = [...t], t.forEach((r, a) => {
      this._vec3Points.push(new _(r.x, r.y, r.z)), a > 0 && (this._length += this._vec3Points[a - 1].distanceTo(this._vec3Points[a]), this._percents.push(this._length));
    }), this._percents = this._percents.map((r) => r / this._length);
    const n = new NAe(this.options.points, i / 2);
    this.lineMesh.geometry.dispose(), this.lineMesh.geometry = n, this.path = new Mu(this._vec3Points), this.updateMaterial(this.options.config);
  }
  createDefaultFlowMaterial(t) {
    const {
      flowStartColor: i,
      flowEndColor: n,
      speed: r,
      percent: a,
      interval: o,
      randomStart: l,
      routeColor: c,
      flowStartOpacity: h,
      flowEndOpacity: u,
      routeOpacity: d
    } = t, f = new Jt({
      transparent: !0,
      depthWrite: !1,
      side: ui,
      uniforms: {
        flowStartColor: {
          value: new _e(i)
        },
        flowEndColor: {
          value: new _e(n)
        },
        flowStartOpacity: {
          value: h
        },
        flowEndOpacity: {
          value: u
        },
        time: {
          value: l ? Math.random() * 2e3 : 0
        },
        lineLength: {
          value: a
        },
        routeColor: {
          value: new _e(c)
        },
        routeOpacity: {
          value: d
        }
      },
      vertexShader: hC,
      fragmentShader: See
    });
    this.lineMesh.material.dispose(), this.lineMesh.material = f, this.iconSprite.visible = !1, this.iconMesh.visible = !1;
  }
  createTextureFlowMaterial(t) {
    const {
      speed: i,
      imgUrl: n,
      reverseDirection: r,
      size: a,
      rotateAngle: o,
      visible: l,
      center: c = { x: 0, y: 0 },
      offset: h = { x: 0, y: 0, z: 0 }
    } = t;
    new Mu(this._vec3Points), st.loadTexture(n).then((u) => {
      var d, f;
      if (u.image)
        if (o === 0) {
          this.iconMesh.visible = !1;
          const p = new ar({
            depthTest: !1,
            transparent: !0,
            side: ui,
            alphaTest: 0.1,
            map: u
          });
          (d = this.iconSprite.material) == null || d.dispose(), this.iconSprite.material = p, this.iconSprite.center.set(c.x, c.y), this.iconSprite.visible = l, this.iconSprite.scale.set(a.x, a.y, 1);
          const v = this.iconSprite;
          if (this._vec3Points.length > 0) {
            const g = new _(h.x, h.y, h.z);
            v.position.copy(this._vec3Points[0].clone().add(g));
          }
        } else {
          this.iconSprite.visible = !1;
          const p = new kt({
            depthTest: !1,
            transparent: !0,
            side: ui,
            alphaTest: 0.1,
            map: u
          });
          if ((f = this.iconMesh.material) == null || f.dispose(), this.iconMesh.material = p, u.center.set(c.x, c.y), this.iconMesh.visible = l, this.iconMesh.scale.set(a.x, a.x, a.y), this._vec3Points.length > 0) {
            const v = new _(h.x, h.y, h.z);
            this.iconMesh.position.copy(this._vec3Points[0].clone().add(v));
          }
        }
    });
  }
  createRouteMaterial(t) {
    const { imgUrl: i, repeat: n, speed: r = 5, rotationAngle: a = 0 } = t;
    i !== void 0 && n !== void 0 ? st.loadTexture(i).then((o) => {
      const l = new Jt({
        transparent: !0,
        side: ui,
        uniforms: {
          time: {
            value: 0
          },
          count: {
            value: Math.floor(this._length / n)
          },
          rotationAngle: {
            value: a
          },
          offsetX: {
            value: a > Math.PI / 2 ? -1 : 1
          },
          routeTexture: {
            value: o
          }
        },
        vertexShader: hC,
        fragmentShader: wee
        // wireframe: true
      });
      this.lineMesh.material.dispose(), this.lineMesh.material = l, this.lineMesh.material.uniformsNeedUpdate = !0;
    }) : (t.color, this.lineMesh.material instanceof Jt && (this.lineMesh.material.uniforms.routeColor.value = new _e(t.color), this.lineMesh.material.needsUpdate = !0));
  }
  update(t) {
    const { material: i } = this.lineMesh;
    switch (t.animateType) {
      case "default":
        if (i.uniforms) {
          const n = Math.floor(i.uniforms.time.value / 2e3), r = performance.now();
          n === this._lastDefaultCount ? (i.uniforms.time.value += t.defaultAnimate.speed, i.uniformsNeedUpdate = !0, this._time = r) : r - this._time > t.defaultAnimate.interval * 1e3 && (this._lastDefaultCount = n);
        }
        break;
      case "texture":
        if (i.uniforms) {
          let n = i.uniforms ? i.uniforms.count.value : 1;
          n === 0 && (n = 1);
          const r = i.uniforms.time.value / n, a = r - Math.floor(r), { routeIconSpeed: o } = t.textureAnimate;
          let c = (o ? o / t.defaultAnimate.speed * a : a) % 1;
          c = t.textureAnimate.reverseDirection ? 1 - c : c;
          const { routeIconOffset: h } = t.textureAnimate, u = this._findPosition(c);
          if (t.textureAnimate.routeIconAutoRotate, t.textureAnimate.routeIconRotateAngle) {
            const d = this.iconMesh.position.clone().sub(u).normalize(), f = new St();
            f.setFromUnitVectors(new _(1, 0, 0), d), this.iconMesh.quaternion.copy(f);
          }
          this.iconSprite.position.copy(
            u.add(jD.set(h.x, h.y, h.z))
          ), this.iconMesh.position.copy(
            u.add(jD.set(h.x, h.y, h.z))
          ), this._lastIconPosition.copy(u), i.uniforms.time.value += t.textureAnimate.speed / 1e3, i.uniformsNeedUpdate = !0;
        }
        break;
    }
  }
  _findPosition(t) {
    let i = 0;
    for (let n = 0; n < this._percents.length; n++)
      if (this._percents[n] >= t) {
        i = n;
        break;
      }
    if (i > 0) {
      const n = this._vec3Points[i - 1], r = this._vec3Points[i], a = (t - this._percents[i - 1]) / (this._percents[i] - this._percents[i - 1]);
      return new _().lerpVectors(n, r, a);
    }
    return new _();
  }
  clearGeometry() {
    this.lineMesh.geometry.dispose(), this.lineMesh.geometry = new Pt();
  }
  dispose() {
    this.lineMesh.geometry.dispose(), this.lineMesh.material.dispose();
  }
}
var IAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const OAe = "FlowRoute";
let t4 = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  add(e, t = !0) {
    const { id: i, name: n, points: r } = e, a = this.states.getCurrentStateConfig();
    if (a) {
      const o = new RAe({
        id: i,
        name: n,
        points: r,
        position: { x: 0, y: 0, z: 0 },
        customAttribute: e.customAttribute,
        config: a
      });
      o.userData = e.customAttribute, this.childMap.set(i, o), t ? this.group.attach(o) : this.group.add(o);
    }
  }
  setData(e) {
    this.clear(), e.children.forEach((t) => {
      this.add(t);
    });
  }
  updateDataById(e, t, i, n = []) {
    const r = this.childMap.get(e), a = this.states.getCurrentStateConfig();
    if (a && r) {
      const { lineWidth: o } = a;
      r.options.config = a, r.createWidthLineGeometry(n, o), r.name = t, r.position.set(i.x, i.y, i.z), this.needDataUpdateServer = !0;
    }
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    if (t) {
      const { lineWidth: i } = t;
      this.childMap.forEach((n) => {
        n.options.config = t, n.createWidthLineGeometry(n.getVec3Points(), i);
      }), "groupPosition" in t && this.group.position.set(
        t.groupPosition.x,
        t.groupPosition.y,
        t.groupPosition.z
      );
    }
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  render(e, t) {
    const i = this.states.getCurrentStateConfig();
    this.childMap.forEach((n) => {
      n.update(i);
    });
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0, this.dispatchEvent(_i.RemoveChild, { id: e });
    }
  }
  draw(e, t, i) {
    e.mode = "multi-line", e.setActiveComponent(this), e.showDrawPoint(this.group), e.showMoveLine(this.group), e.startDraw(), new _().setFromMatrixPosition(this.group.matrixWorld);
    let n;
    const r = {
      index: -1,
      position: new _(),
      id: ""
    }, a = /* @__PURE__ */ new Map();
    e.disableType("add-point"), this.childMap.forEach((m) => {
      const x = m.getVec3Points(), y = new hg();
      a.set(m.uuid, y), x.forEach((A) => {
        const b = nt.generateUUID(), M = new _(A.x, A.y, A.z);
        y.set(b, M), e.executeDrawCommand("add-point", {
          position: M,
          id: b,
          drawId: m.uuid
        });
      }), e.enableType("add-point");
    }), this.childMap.size === 0 && e.enableType("add-point"), e.drawRootPoint.children.forEach((m) => {
      const { drawId: x } = m.userData, y = this.childMap.get(x);
      y && m.position.copy(y.position);
    });
    const o = (m) => {
      const { position: x, coord: y } = m;
      n || u();
      const A = e.raycaster(y, this.group);
      let b = x, M = !1;
      const w = e.getLineMidPoint();
      (!A || w.visible) && (M = !0), w.visible && A && (n = this.childMap.get(r.id), b = w.position);
      const C = e.ray.intersectObject(e.getLineMidPoint());
      w.visible && A && C[0].object.userData.drawId !== r.id && (M = !1), !w.visible && (r.index = -1), console.log(b.clone()), M && (e.executeDrawCommand("add-point", {
        position: b,
        drawId: n == null ? void 0 : n.uuid
      }), e.transformControls.detach());
    }, l = (m) => {
      const x = m.point.parent, y = this.childMap.get(x.userData.drawId), A = a.get(x.userData.drawId), b = A == null ? void 0 : A.get(x.uuid);
      b && b.copy(x.sprite.position), y && A && y.createWidthLineGeometry(
        A.values().map((M) => M.clone()),
        y.width
      );
    }, c = (m) => {
      const { id: x } = m;
      [...e.drawRootPoint.children].forEach((y) => {
        y.userData.drawId === x && e.drawRootPoint.remove(y);
      });
    }, h = (m) => {
      e.getLineMidPoint().visible = !1;
      const { uuid: x } = m.point;
      if (n) {
        const y = a.get(n.uuid);
        console.log(y), y && (r.index !== -1 ? (y.insert(r.index, x, r.position.clone()), y.size > 1 && n.createWidthLineGeometry(
          y.values().map((A) => A.clone()),
          n.width
        ), console.log(y.values(), r.position.clone())) : (y.set(x, m.point.sprite.position), y.size > 1 && n.createWidthLineGeometry(
          y.values().map((A) => A.clone()),
          n.width
        )));
      }
    }, u = () => {
      const m = nt.generateUUID();
      this.add(
        {
          id: m,
          points: [],
          position: { x: 0, y: 0, z: 0 },
          name: "路径",
          customAttribute: {}
        },
        !1
      ), n = this.childMap.get(m);
      const x = new hg();
      a.set(m, x);
    }, d = new Qs(), f = (m, x, y) => (d.set(x, y), d.closestPointToPoint(m, !0, new _()).distanceTo(m)), p = (m) => {
      const x = this.childMap.get(m.id), y = x == null ? void 0 : x.getVec3Points();
      let A = 1 / 0, b = -1;
      if (y) {
        y.forEach((w, C) => {
          if (C > 0) {
            const T = f(m.position, y[C - 1], w);
            A > T && (A = T, b = C);
          }
        });
        const M = new _().addVectors(y[b - 1], y[b]).multiplyScalar(0.5);
        e.showLineMidPoint(M, this.group), r.index = b, r.position = M, r.id = m.id;
      }
    }, v = () => {
      this.needDataUpdateServer = !0;
    }, g = () => {
      i && i(), this.childMap.forEach((m) => {
        m.getVec3Points().length < 2 && (m.dispose(), this.childMap.delete(m.uuid));
      }), a.clear(), e.removeEventListener("draw-line", o), e.removeEventListener("end-draw", g), e.removeEventListener("add-point", h), e.removeEventListener("drag", l), e.removeEventListener("end-drag", v), this.removeEventListener(_i.RemoveChild, c), e.removeEventListener("double-click", u), e.removeEventListener("hover-component", p), this.needDataUpdateServer = !0;
    };
    e.addEventListener("draw-line", o), e.addEventListener("drag", l), e.addEventListener("end-drag", v), e.addEventListener("end-draw", g), e.addEventListener("add-point", h), this.addEventListener(_i.RemoveChild, c), e.addEventListener("double-click", u), e.addEventListener("hover-component", p);
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        points: t.getPoints(),
        customAttribute: t.userData
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r } = t, a = r;
    a && this.states.initStates(a);
    const o = t.data;
    o.children.forEach(
      (u) => this.add({
        id: u.id,
        name: u.name,
        points: u.points,
        position: {
          x: u.position.x,
          y: u.position.y,
          z: u.position.z
        },
        customAttribute: u.customAttribute
      })
    );
    const { x: l, y: c, z: h } = uh(o, this);
    l && this.group.position.setX(l), c && this.group.position.setY(c), h && this.group.position.setZ(h), this.positionMode = o.positionMode ?? "xyz", this.isInit = !0;
  }
  clear() {
    this.childMap.forEach((e) => {
      e.traverse((t) => {
        var i;
        p0(t) && (t.geometry.dispose(), Array.isArray(t.material) || ((i = t.material.map) == null || i.dispose(), t.material.dispose()));
      }), this.group.remove(e);
    }), this.childMap.clear();
  }
};
t4 = IAe([
  ni(OAe)
], t4);
const Bs = new _(), Fs = new _();
class NAe extends Pt {
  constructor(e, t) {
    super();
    const i = new Array(), n = new Array(), r = new Array(), a = new Array(), o = new Array(), l = new Array();
    if (o[0] = 0, l[0] = 0, e.length > 1) {
      e.forEach(({ x: d, y: f, z: p }, v) => {
        if (v === 0) {
          const [g, m] = this.getNormalByPoints(e[v], e[v + 1], t);
          i.push(g.x + e[v].x, g.y + e[v].y, g.z + e[v].z), i.push(m.x + e[v].x, m.y + e[v].y, m.z + e[v].z);
        } else if (e[v + 1]) {
          const g = new _(e[v - 1].x, e[v - 1].y, e[v - 1].z), m = new _(e[v].x, e[v].y, e[v].z), x = new _(e[v + 1].x, e[v + 1].y, e[v + 1].z), y = new _(g.x - m.x, 0, g.z - m.z), A = new _(x.x - m.x, 0, x.z - m.z);
          if (y.angleTo(A) === Math.PI) {
            const [M, w] = this.getNormalByPoints(g, m, t);
            i.push(M.x + e[v].x, M.y + e[v].y, M.z + e[v].z), i.push(w.x + e[v].x, w.y + e[v].y, w.z + e[v].z), i.push(M.x + e[v].x, M.y + e[v].y, M.z + e[v].z), i.push(w.x + e[v].x, w.y + e[v].y, w.z + e[v].z);
          } else {
            const [M, w] = this.getNormalByPoints(g, m, t), [C, T] = this.getNormalByPoints(m, x, t);
            Bs.subVectors(g, m), Fs.subVectors(x, m);
            const E = Bs.angleTo(Fs), P = Math.tan(E / 2) * t * 2, L = g.distanceTo(m), I = m.distanceTo(x);
            let N = P / L;
            N > 0.5 && (N = 0.5);
            let H = P / I;
            H > 0.5 && (H = 0.5), Bs.lerpVectors(m, g, N), Fs.lerpVectors(m, x, H);
            const U = this.findLineIntersection(
              [M.x + g.x, M.z + g.z],
              [M.x + m.x, M.z + m.z],
              [C.x + m.x, C.z + m.z],
              [C.x + x.x, C.z + x.z]
            ), W = this.findLineIntersection(
              [w.x + g.x, w.z + g.z],
              [w.x + m.x, w.z + m.z],
              [T.x + m.x, T.z + m.z],
              [T.x + x.x, T.z + x.z]
            );
            if (U instanceof _ && W instanceof _) {
              U.y = f, W.y = f, i.push(M.x + Bs.x, M.y + Bs.y, M.z + Bs.z), i.push(w.x + Bs.x, w.y + Bs.y, w.z + Bs.z), i.push(M.x + Bs.x, M.y + Bs.y, M.z + Bs.z), i.push(w.x + Bs.x, w.y + Bs.y, w.z + Bs.z);
              const X = t * 4;
              if (U.distanceTo(W) > X) {
                const te = new _().lerpVectors(W, U, 0.5), re = new _().lerpVectors(U, W, 0.5);
                i.push(te.x, te.y, te.z), i.push(re.x, re.y, re.z), i.push(te.x, te.y, te.z), i.push(re.x, re.y, re.z);
              } else
                i.push(U.x, U.y, U.z), i.push(W.x, W.y, W.z), i.push(U.x, U.y, U.z), i.push(W.x, W.y, W.z);
              i.push(C.x + Fs.x, C.y + Fs.y, C.z + Fs.z), i.push(T.x + Fs.x, T.y + Fs.y, T.z + Fs.z), i.push(C.x + Fs.x, C.y + Fs.y, C.z + Fs.z), i.push(T.x + Fs.x, T.y + Fs.y, T.z + Fs.z);
            }
          }
        } else {
          const [g, m] = this.getNormalByPoints(e[v - 1], e[v], t);
          i.push(g.x + e[v].x, g.y + e[v].y, g.z + e[v].z), i.push(m.x + e[v].x, m.y + e[v].y, m.z + e[v].z);
        }
      });
      for (let d = 1; d <= i.length / 12; d++) {
        const f = (d - 1) * 4, p = f, v = f + 2, g = f + 3, m = f + 1;
        n.push(p, v, m), n.push(v, g, m);
      }
    }
    for (let d = 0; d < i.length; d += 3)
      r.push(0, 1, 0);
    let c = 0;
    for (let d = 6; d < i.length; d += 12)
      Bs.set(i[d - 6], i[d - 5], i[d - 4]), Fs.set(i[d], i[d + 1], i[d + 2]), c += Fs.distanceTo(Bs), o.push(c), o.push(c), o.push(c), o.push(c);
    const h = new Se(), u = i.length / 3 - 1;
    for (let d = 0; d <= u; d += 2)
      for (let f = 0; f <= 1; f++)
        h.x = o[d] / c, h.y = f, a.push(h.x, h.y);
    this.setIndex(n), this.setAttribute("position", new gt(i, 3)), this.setAttribute("normal", new gt(r, 3)), this.setAttribute("uv", new gt(a, 2));
  }
  // p2 绕 p1 旋转 +- 90度
  getNormalByPoints(e, t, i) {
    const n = new _(e.z - t.z, 0, t.x - e.x).normalize().multiplyScalar(i);
    return [n, n.clone().negate()];
  }
  generateCornerVertices(e, t, i, n, r) {
  }
  findLineIntersection(e, t, i, n) {
    const r = (t[1] - e[1]) / (t[0] - e[0] === 0 ? 1 : t[0] - e[0]), a = (n[1] - i[1]) / (n[0] - i[0] === 0 ? 1 : n[0] - i[0]), o = e[1] - r * e[0], l = i[1] - a * i[0];
    if (r !== a) {
      const c = (l - o) / (r - a), h = r * c + o;
      return new _(c, 0, h);
    } else
      return o === l ? "两条直线重合，有无穷多个交点" : "两条直线平行，没有交点";
  }
}
var UAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const zAe = "Floor";
let oM = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  add(e, t = !0) {
    const i = this.states.getCurrentStateConfig(), n = new DF(e);
    return t ? this.group.attach(n) : this.group.add(n), i && n.updateMaterial(i), this.childMap.set(n.uuid, n), n;
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    t && (this.childMap.forEach((i) => {
      i.updateMaterial(t);
    }), "groupPosition" in t && this.group.position.set(
      t.groupPosition.x,
      t.groupPosition.y,
      t.groupPosition.z
    ));
  }
  updateDataById(e, t, i, n, r, a) {
    const o = this.childMap.get(e);
    o && (o.name = t, o.position.set(i.x, i.y, i.z), n && o.rotation.set(n.x, n.y, n.z), r && o.createGeometry(r.x, r.y), a !== void 0 && (o.constrainedScale = a), this.needDataUpdateServer = !0, dt.Entity.refresh(), this.dispatchEvent("change-size", { id: e }), r && this._updateDrawPoint(o, r));
  }
  _updateDrawPoint(e, t) {
    const i = [], { x: n, y: r } = t, a = n / 2, o = r / 2, l = [
      new _(-a, 0, o),
      new _(-a, 0, -o),
      new _(a, 0, o),
      new _(a, 0, -o)
    ];
    e.traverse((c) => {
      c.userData.drawId === e.uuid && c instanceof ul && i.push(c);
    }), i.forEach((c, h) => {
      c.children[0].position.copy(l[h]);
    });
  }
  setData(e) {
    this.clear(), e.children.forEach((t) => {
      this.add(t);
    });
  }
  clear() {
    this.childMap.forEach((e) => {
      e.traverse((t) => {
        var i;
        p0(t) && (t.geometry.dispose(), Array.isArray(t.material) || ((i = t.material.map) == null || i.dispose(), t.material.dispose()));
      }), this.group.remove(e);
    }), this.childMap.clear();
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = i);
      });
    }), e === "" ? this.group : t;
  }
  draw(e, t, i) {
    e.mode = "multi-rect", e.setActiveComponent(this), e.showDrawPoint(this.group), e.showDrawMoveRect(this.group), console.log("draw"), e.startDraw();
    const n = (d, f, p) => [
      new _(-d, 0, f),
      new _(-d, 0, -f),
      new _(d, 0, f),
      new _(d, 0, -f)
    ], r = (d, f, p) => {
      const v = nt.generateUUID();
      return this.add(
        {
          id: v,
          width: d,
          height: f,
          position: p,
          name: `平面${this.childMap.size}`,
          customAttribute: {},
          rotation: {
            x: 0,
            y: 0,
            z: 0
          },
          constrainedScale: !1
        },
        !1
      );
    };
    let a;
    e.disableType("add-points"), this.childMap.forEach((d) => {
      const f = d.getWidth(), p = d.getHeight();
      e.executeDrawCommand("add-points", {
        root: d,
        points: n(f / 2, p / 2),
        drawId: d.uuid
      }), e.enableType("add-points");
    });
    const o = (d) => {
      const f = e.raycaster(d.coord, this.group);
      if (console.log(f), !f) {
        const { position: p } = d;
        a = r(Ox.x, Ox.y, p), e.executeDrawCommand("add-points", {
          root: a,
          points: n(a.getWidth() / 2, a.getHeight() / 2),
          drawId: a.uuid
        });
      }
    }, l = (d) => {
      var p, v;
      const f = this.childMap.get(d.point.userData.drawId);
      if (parent && f) {
        const { x: g } = d.point.position, { z: m } = d.point.position, x = [
          new _(-g, 0, m),
          new _(-g, 0, -m),
          new _(g, 0, m),
          new _(g, 0, -m)
        ];
        (v = (p = d.point.parent) == null ? void 0 : p.parent) == null || v.children.forEach(
          (y, A) => y.sprite.position.copy(x[A])
        ), f.createGeometry(g * 2, m * 2);
      }
    }, c = (d) => {
    }, h = (d) => {
    }, u = () => {
      i && i(), e.removeEventListener("draw-rect", o), e.removeEventListener("end-draw", u), e.removeEventListener("drag", l), e.removeEventListener("remove-point", c), this.removeEventListener("change-size", h), this.needDataUpdateServer = !0;
    };
    e.addEventListener("draw-rect", o), e.addEventListener("drag", l), e.addEventListener("end-draw", u), e.addEventListener("remove-point", c), this.addEventListener("change-size", h);
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  toJson() {
    const e = {
      children: [],
      // groupPosition: this.group.position.clone(),
      positionMode: this.positionMode
    };
    return this.childMap.forEach((t) => {
      e.children.push({
        id: t.uuid,
        position: { x: t.position.x, y: t.position.y, z: t.position.z },
        name: t.name,
        width: t.getWidth(),
        height: t.getHeight(),
        customAttribute: t.userData,
        constrainedScale: t.constrainedScale,
        rotation: {
          x: t.rotation.x,
          y: t.rotation.y,
          z: t.rotation.z
        }
      });
    }), {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: e
    };
  }
  async updateFromJson(e, t) {
    const i = t.states;
    i && this.states.initStates(i);
    const n = t.data;
    n.children.forEach(
      (l) => this.add({
        id: l.id,
        name: l.name,
        width: l.width,
        height: l.height,
        position: {
          x: l.position.x,
          y: l.position.y,
          z: l.position.z
        },
        customAttribute: l.customAttribute,
        rotation: {
          x: l.rotation ? l.rotation.x : 0,
          y: l.rotation ? l.rotation.y : 0,
          z: l.rotation ? l.rotation.z : 0
        },
        constrainedScale: !1
      })
    ), this.positionMode = n.positionMode ?? "xyz";
    const { x: r, y: a, z: o } = uh(n, this);
    r && this.group.position.setX(r), a && this.group.position.setY(a), o && this.group.position.setZ(o), this.isInit = !0;
  }
};
oM = UAe([
  ni(zAe)
], oM);
var HAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const lM = "InstanceModel", VAe = new zt();
let Jg = class extends UF {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "instanceGroup", new WAe());
    S(this, "_groupConstrainedScale", !1);
    S(this, "_childArr", []);
    S(this, "_castShadow", !1);
    S(this, "_receiveShadow", !1);
    S(this, "mixer", new rm());
    S(this, "_childTransform", {
      rotation: new _(),
      scale: new _(),
      constrainedScale: !1
    });
    S(this, "_realChildMap", /* @__PURE__ */ new Map());
    S(this, "_pickObject", new mt());
    S(this, "_drawBoxMesh", new Re(
      new oi(1, 1, 1),
      new kt({ color: "#aa00ff", transparent: !0, opacity: 0.2 })
    ));
    S(this, "_initDataOptions");
    S(this, "_world");
    S(this, "updateObjectVisible", (e, t, i) => {
      const n = /* @__PURE__ */ new Map();
      if (e && e.forEach((r) => {
        const a = this._realChildMap.get(r);
        a && n.set(a.id, a);
      }), t) {
        let r = [];
        t instanceof Array ? r = [...t] : r = [t], r.forEach((a) => {
          Object.keys(a).forEach((o) => {
            this._realChildMap.forEach((l) => {
              l.customAttribute[o] === a[o] && n.set(l.id, l);
            });
          });
        });
      }
      if (i)
        this.addChilds(Array.from(n.values()));
      else if (i === !1)
        this.tempRemoveChilds(Array.from(n.values()));
      else {
        const r = Array.from(n.values()).filter(
          (o) => this._childArr.findIndex((l) => l.id === o.id) === -1
        ), a = Array.from(n.values()).filter(
          (o) => this._childArr.findIndex((l) => l.id === o.id) !== -1
        );
        r.length && this.addChilds(r), a.length && this.tempRemoveChilds(a);
      }
    });
    S(this, "tempRemoveChilds", (e) => {
      const t = [];
      this._childArr.forEach((i) => {
        e.findIndex((n) => n.id === i.id) === -1 && t.push(i);
      }), this._childArr = [...t], this.instanceGroup.setInstanceCount(this._childArr.length), this.update(0);
    });
    S(this, "addChilds", (e) => {
      const t = e.filter((i) => this._childArr.findIndex((n) => n.id === i.id) === -1);
      this._childArr.push(...t), this.instanceGroup.setInstanceCount(this._childArr.length), this.update(0);
    });
    S(this, "easyvUpdateData", (e) => {
      this._childArr = [...e], this._realChildMap.clear(), this._childArr.forEach((t) => {
        this._realChildMap.set(t.id, t);
      }), this.createInstanceModelFromRenderResource(this.modelRenderResource, e.length), this.update(0);
    });
    S(this, "easyvAnimationByAttributes", (e) => {
      (this.mixer.isPlaying || this.mixer.isPause) && this.mixer.removeAll(), e.forEach((t) => {
        t.customAttribute && [...this._childArr.values()].forEach((i, n) => {
          var h, u, d, f, p, v, g, m, x, y, A, b;
          const { position: r, rotation: a, scale: o, customAttribute: l } = i;
          let c = !1;
          for (let M in t.customAttribute)
            l[M] && l[M] === t.customAttribute[M] && (c = !0);
          if (c) {
            const M = new et(), w = this.mixer.addAction(`${i.id}`), C = new _(), T = new Mi(), E = new St(), P = new _(), L = new _(r.x, r.y, r.z);
            if (t.position) {
              if ("x" in t.position && (L.x = t.position.x), "y" in t.position && (L.y = t.position.y), "z" in t.position && (L.z = t.position.z), "lon" in t.position && "lat" in t.position && this._world) {
                const I = Lu(
                  this._world,
                  t.position.lon,
                  t.position.lat,
                  L.y
                );
                L.set(I.x, I.y, I.z);
              }
              w.addTrack({
                name: `${i.name}-position`,
                propertyKey: "default",
                roots: [this.group],
                mode: ((h = t.animation) == null ? void 0 : h.mode) ?? "Linear.None",
                times: [0, ((u = t.animation) == null ? void 0 : u.duration) ?? 1],
                values: [new _(r.x, r.y, r.z), L],
                defaultExecute: (I, N, H) => {
                  C.set(I, N, H), i.position.x = I, i.position.y = N, i.position.z = H;
                }
              });
            }
            t.rotation && w.addTrack({
              name: `${i.name}-rotation`,
              propertyKey: "default",
              roots: [this.group],
              times: [0, ((d = t.animation) == null ? void 0 : d.duration) ?? 1],
              values: [
                new _(a.x, a.y, a.z),
                new _(
                  ((f = t.rotation) == null ? void 0 : f.x) ?? a.x,
                  ((p = t.rotation) == null ? void 0 : p.y) ?? a.y,
                  ((v = t.rotation) == null ? void 0 : v.z) ?? a.z
                )
              ],
              mode: ((g = t.animation) == null ? void 0 : g.mode) ?? "Linear.None",
              defaultExecute: (I, N, H) => {
                T.set(I, N, H), E.setFromEuler(T), i.rotation.x = I, i.rotation.y = N, i.rotation.z = H;
              }
            }), t.scale && w.addTrack({
              name: `${i.name}-scale`,
              propertyKey: "default",
              roots: [this.group],
              times: [0, ((m = t.animation) == null ? void 0 : m.duration) ?? 1],
              values: [
                new _(o.x, o.y, o.z),
                new _(
                  ((x = t.scale) == null ? void 0 : x.x) ?? o.x,
                  ((y = t.scale) == null ? void 0 : y.y) ?? o.y,
                  ((A = t.scale) == null ? void 0 : A.z) ?? o.z
                )
              ],
              mode: ((b = t.animation) == null ? void 0 : b.mode) ?? "Linear.None",
              defaultExecute: (I, N, H) => {
                P.set(I, N, H), i.scale.x = I, i.scale.y = N, i.scale.z = H, M.compose(C, E, P), this.instanceGroup.setMatrixAt(n, M);
              }
            });
          }
        });
      }), this.mixer.play();
    });
    this.id = e, this.name = t, this.entity = i, Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s(
      lM,
      // 组件的类型
      "animationByAttributes",
      // action 中的value
      e,
      // 组件对应的实体id
      this.easyvAnimationByAttributes
      // 对应事件的执行函数
    ), _s(
      lM,
      // 组件的类型
      "updateData",
      // action 中的value
      e,
      // 组件对应的实体id
      this.easyvUpdateData
      // 对应事件的执行函数
    );
  }
  get initDataOptions() {
    return this._initDataOptions;
  }
  set initDataOptions(e) {
    this._initDataOptions = e;
  }
  async fromJson(e) {
    if (e.type !== "InstanceModel")
      throw new Error("component type is not InstanceModel!");
    this.url = e.data.asset.url;
    const t = await CU.updateToLatestVersion(e), { data: i } = t, n = i;
    this._initDataOptions = { ...i }, this.modelRenderResource = await this.loadRenderResourceFromJson(n), this.initInstanceGroup(n), this.positionMode = n.positionMode ?? "xyz";
  }
  updateInitDataOptions() {
    this._initDataOptions = this.toJson({
      updateModelSceneData: !0
    }).data;
  }
  toJson(e = {
    updateModelSceneData: !1
    // 模型组件数据字段配置项太多存在互相影响的字段，需要能够独立进行更新
  }) {
    var c;
    const t = this.states.toJson(), i = {
      x: this.group.position.x,
      y: this.group.position.y,
      z: this.group.position.z
    }, n = {
      x: this.group.rotation.x,
      y: this.group.rotation.y,
      z: this.group.rotation.z
    }, r = {
      x: this.group.scale.x,
      y: this.group.scale.y,
      z: this.group.scale.z
    }, a = this.modelRenderResource.toJSON(), o = (e.updateModelSceneData ? a.scene : (c = this._initDataOptions) == null ? void 0 : c.scene) ?? a.scene, l = {
      transform: {
        position: {
          x: 0,
          y: 0,
          z: 0
        },
        rotation: {
          x: 0,
          y: 0,
          z: 0
        },
        scale: {
          x: 1,
          y: 1,
          z: 1
        }
      },
      castShadow: this._castShadow,
      receiveShadow: this._receiveShadow,
      disablePick: this.disablePick,
      positionMode: this.positionMode,
      customAttribute: {},
      sceneGraph: {
        isMesh: !1,
        children: [],
        name: "",
        type: "",
        indexId: "",
        transform: {
          position: [],
          rotation: [],
          scale: []
        }
      },
      materialSlots: a.materialSlots,
      scene: o,
      asset: {
        url: this.modelRenderResource.url || this.url
      },
      childTransform: {
        scale: {
          x: this._childTransform.scale.x,
          y: this._childTransform.scale.y,
          z: this._childTransform.scale.z
        },
        constrainedScale: this._childTransform.constrainedScale,
        rotation: {
          x: this._childTransform.rotation.x,
          y: this._childTransform.rotation.y,
          z: this._childTransform.rotation.z
        }
      },
      children: [...this._childArr.values()],
      groupPosition: i,
      groupRotation: n,
      groupScale: r,
      groupConstrainedScale: this._groupConstrainedScale
    };
    return {
      name: this.name,
      visible: !0,
      inActive: this.entity.inActive,
      states: t,
      version: this.version,
      id: this.id,
      type: this.type,
      data: l
    };
  }
  async updateFromJson(e, t) {
    this.fromJson(t).then(() => this.isInit = !0), this._world = e;
  }
  getObjectById(e, t) {
    let i;
    return i = this._childArr.find((n) => n.id === e), t !== void 0 && (i = this._childArr[t]), i ? (this._pickObject.uuid = i.id, this._pickObject.userData = i.customAttribute, this._pickObject.name = i.name, this._pickObject.position.set(i.position.x, i.position.y, i.position.z), this._pickObject) : this.group;
  }
  removeObjectById(e) {
    const t = this._childArr.findIndex((i) => i.id === e);
    this._childArr.splice(t, 1), this.instanceGroup.setInstanceCount(this._childArr.length), this.update(0), this.needDataUpdateServer = !0;
  }
  setCustomAttribute(e, t) {
    if (t) {
      const i = this._childArr.find((n) => n.id === t);
      i && Object.assign(i.customAttribute, e);
    } else
      this._childArr.forEach((i) => {
        Object.assign(i.customAttribute, e);
      });
    this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  replaceCustomAttribute(e) {
    this._childArr.forEach((t) => {
      Object.assign(t.customAttribute, e);
    }), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  updateCustomAttributeKey(e, t) {
    this._childArr.forEach((i) => {
      const n = i.customAttribute[e];
      delete i.customAttribute[e], i.customAttribute[t] = n;
    }), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  removeCustomAttribute(e, t) {
    e && (this._childArr.forEach((i) => {
      delete i.customAttribute[e];
    }), this.needDataUpdateServer = !0, dt.Entity.refresh());
  }
  updateChildPosition(e, t) {
    const i = this._childArr.find((n) => n.id === e);
    i && (i.position.x = t.x, i.position.y = t.y, i.position.z = t.z), this.update(0);
  }
  addObject2Renderer() {
    this.createInstanceModelFromRenderResource(this.modelRenderResource, this._childArr.length);
  }
  update(e) {
    const { rotation: t, scale: i } = this._childTransform;
    [...this._childArr.values()].forEach((n, r) => {
      const a = new _(n.position.x, n.position.y, n.position.z), o = new St().setFromEuler(
        new Mi(
          n.rotation.x + t.x,
          n.rotation.y + t.y,
          n.rotation.z + t.z
        )
      ), l = new _(
        n.scale.x + i.x,
        n.scale.y + i.y,
        n.scale.z + i.z
      ), c = new et();
      c.compose(a, o, l), this.instanceGroup.setMatrixAt(r, c), console.log("instance model233", n.scale, n.rotation, n.name);
    });
  }
  setInstanceModelTransform(e, t, i, n) {
    this.group.position.set(e.x, e.y, e.z), this.group.rotation.set(t.x, t.y, t.z), this.group.scale.set(i.x, i.y, i.z), this._groupConstrainedScale = n, this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  setInstanceModelChildRotation(e) {
    this._childTransform.rotation = new _(e.x, e.y, e.z), this.update(0), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  setInstanceModelChildScale(e, t) {
    this._childTransform.scale = new _(e.x, e.y, e.z), this._childTransform.constrainedScale = t, this.update(0), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  updateDataById(e, t, i, n, r, a = !1) {
    const o = this._childArr.find((l) => l.id === e);
    o && (o.name = t, o.position.x = i.x, o.position.y = i.y, o.position.z = i.z, n && (o.rotation.x = n.x, o.rotation.y = n.y, o.rotation.z = n.z), r && (o.scale.x = r.x, o.scale.y = r.y, o.scale.z = r.z), o.constrainedScale = a), this.update(0), this.needDataUpdateServer = !0, dt.Entity.refresh();
  }
  updateChildrenTransform(e, t) {
    this._childTransform.scale.set(e.x, e.y, e.z), this._childTransform.rotation.set(t.x, t.y, t.z);
  }
  draw(e, t) {
    e.mode = "multi-point", e.startDraw(), e.setDrawPoint(this._drawBoxMesh), e.setActiveComponent(this), this._drawBoxMesh.visible = !1;
    const i = /* @__PURE__ */ new Set(), n = (h) => {
      const { position: u, coord: d } = h, f = e.raycaster(d, this.instanceGroup);
      let p = !1;
      f || (p = !0), p && e.executeDrawCommand("add-point", { position: u });
    }, r = (h) => {
      const u = this.getObjectById(h.point.uuid);
      u && (u.visible = !1), u && i.add(u.uuid);
    }, a = (h) => {
      if (i.has(h.point.uuid)) {
        const d = this.getObjectById(h.point.uuid);
        d && (d.visible = !0), i.delete(h.point.uuid);
      } else {
        const d = new _().setFromMatrixPosition(this.group.matrixWorld), [f] = this._childArr, p = {}, v = f == null ? void 0 : f.scale, g = f == null ? void 0 : f.rotation;
        p && f && Object.keys(f.customAttribute).forEach((m) => {
          p[m] = void 0;
        }), this._childArr.push({
          id: h.point.uuid,
          position: h.point.sprite.position.sub(d),
          rotation: g ? { x: g.x, y: g.y, z: g.z } : { x: 0, y: 0, z: 0 },
          scale: v ? { x: v.x, y: v.y, z: v.z } : { x: 1, y: 1, z: 1 },
          name: `实例模型${this._childArr.length}`,
          constrainedScale: !1,
          customAttribute: p
        }), this.instanceGroup.setInstanceCount(this._childArr.length), this.update(0);
      }
    }, o = (h) => {
      if (e.editMode === bn.Move)
        this._drawBoxMesh.visible = !1;
      else {
        const { x: u, y: d } = h, f = Y4(
          e.camera,
          u,
          d,
          e.domElement,
          0
        );
        this._drawBoxMesh.position.set(f.x, f.y, f.z), this._drawBoxMesh.visible = !0;
      }
    }, l = (h) => {
      switch (h) {
        case bn.Create:
          e.setDrawPoint(this._drawBoxMesh);
          break;
        case bn.Move:
          e.removeDrawPoint(this._drawBoxMesh);
          break;
      }
    }, c = () => {
      i.forEach((h) => {
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", n), e.removeEventListener("end-draw", c), e.removeEventListener("add-point", a), e.removeEventListener("remove-point", r), e.removeEventListener("mouse-move", o), e.removeEventListener("switch-edit-mode", l);
    };
    e.addEventListener("draw-point", n), e.addEventListener("end-draw", c), e.addEventListener("add-point", a), e.addEventListener("remove-point", r), e.addEventListener("mouse-move", o), e.addEventListener("switch-edit-mode", l);
  }
  dispose() {
    this.group.clear(), this.instanceGroup.clear(), this._world = void 0;
  }
  _updateVersion(e) {
    const { children: t } = e;
    if (!("childTransform" in e))
      t[0] && (this._childTransform.rotation = new _(
        t[0].rotation.x,
        t[0].rotation.y,
        t[0].rotation.z
      ), this._childTransform.scale = new _(
        t[0].scale.x,
        t[0].scale.y,
        t[0].scale.z
      )), t.forEach((i) => {
        i.rotation = { x: 0, y: 0, z: 0 }, i.scale = {
          x: 1 - t[0].scale.x,
          y: 1 - t[0].scale.y,
          z: 1 - t[0].scale.z
        };
      }), this._childTransform.constrainedScale = !1, this._childArr = [...t], this._realChildMap.clear(), this._childArr.forEach((i) => {
        this._realChildMap.set(i.id, i);
      }), e.childTransform = {
        rotation: {
          x: t[0].rotation.x,
          y: t[0].rotation.y,
          z: t[0].rotation.z
        },
        scale: {
          x: t[0].scale.x,
          y: t[0].scale.y,
          z: t[0].scale.z
        },
        constrainedScale: !1
      }, this.needDataUpdateServer = !0;
    else {
      const { rotation: i, scale: n, constrainedScale: r } = e.childTransform;
      this._childTransform.rotation = new _(i.x, i.y, i.z), this._childTransform.scale = new _(n.x, n.y, n.z), this._childTransform.constrainedScale = r ?? !1, this._childArr = [...t], this._realChildMap.clear(), this._childArr.forEach((a) => {
        this._realChildMap.set(a.id, a);
      });
    }
    return t;
  }
  initInstanceGroup(e) {
    const { groupPosition: t, groupRotation: i, groupScale: n, groupConstrainedScale: r } = e;
    this._groupConstrainedScale = r, this.group.position.x = t.x, this.group.position.y = t.y, this.group.position.z = t.z, this.group.rotation.x = i.x, this.group.rotation.y = i.y, this.group.rotation.z = i.z, this.group.scale.x = n.x, this.group.scale.y = n.y, this.group.scale.z = n.z, e.children = this._updateVersion(this._transformLonLat(e)), this.createInstanceModelFromRenderResource(
      this.modelRenderResource,
      e.children.length
    ), this._updateInstanceModelShadowConfig(e.castShadow, e.receiveShadow);
  }
  _transformLonLat(e) {
    return e.children.forEach((t) => {
      if ("lon" in t.position && "lat" in t.position) {
        const i = Lu(
          this.entity.world,
          t.position.lon,
          t.position.lat,
          t.position.y
        );
        t.position.x = i.x, t.position.y = i.y, t.position.z = i.z;
      }
    }), e;
  }
  _updateInstanceModelShadowConfig(e, t) {
    for (let i in this.instanceGroup.instanceCollect)
      this.instanceGroup.instanceCollect[i].receiveShadow = t ?? !1, this.instanceGroup.instanceCollect[i].castShadow = e ?? !1;
  }
  createInstanceModelFromRenderResource(e, t) {
    this.addObject2RendererImplement(e.scene, t), this.instanceGroup.setInstanceCount(this._childArr.length), this.update(0);
  }
  setInstanceModelShadowConfig(e, t) {
    this._castShadow = e, this._receiveShadow = t, this._updateInstanceModelShadowConfig(e, t);
  }
  setInstanceModelDisablePick(e) {
    this.disablePick = e;
  }
  addObject2RendererImplement(e, t = 1) {
    this.group.clear(), this.group.add(this.instanceGroup), this.instanceGroup.setGroup(e, t);
    const i = new _();
    VAe.setFromObject(e).getSize(i), this._drawBoxMesh.scale.set(i.x, i.y, i.z);
  }
  setData(e) {
    this.initInstanceGroup(e);
  }
  tick() {
    this.mixer.tick();
  }
  /**
   * @deprecated 后期会从组件上移除该函数
   * @param world
   * @param params
   */
  easyvFocusOnObject3D(e, t) {
    const { id: i, customAttribute: n, distance: r = 0 } = t;
    t.duration && (t.duration *= 1e3);
    let a;
    if (n ? [a] = this.filterByAttribute(this._childArr, n) : i && (a = this._childArr.find((o) => o.id === i)), a) {
      const o = this.instanceGroup.box.getSize(new _()), { x: l, y: c, z: h } = a.position;
      e.flyToBox(
        new zt().setFromCenterAndSize(new _(l, c, h), o).expandByScalar(r),
        t
      );
    } else
      e.flyToBox(new zt().setFromObject(this.group).expandByScalar(r), t);
  }
};
Jg = HAe([
  ni(lM)
], Jg);
const k6 = new et();
class WAe extends Re {
  constructor() {
    super(...arguments);
    S(this, "isInstancedGroupMesh", !0);
    S(this, "count", 0);
    S(this, "meshCollect", {});
    S(this, "instanceCollect", {});
    S(this, "mesh2InstanceMesh", {});
    S(this, "box", new zt());
  }
  setGroup(t, i) {
    this.count = i, this.meshCollect = {}, this.instanceCollect = {};
    const n = new zt().setFromObject(t);
    this.box.setFromCenterAndSize(new _(), n.getSize(new _()));
    const { meshCollect: r, instanceCollect: a } = this;
    t.traverse((o) => {
      if (o.updateMatrixWorld(), !o.type.includes("Mesh"))
        return;
      const l = o, c = `${l.geometry.uuid}/${l.material.uuid}`;
      r[c] ? r[c].push(l) : r[c] = [l];
    });
  }
  setInstanceCount(t) {
    this.count = t, Object.values(this.instanceCollect).forEach((i) => i.dispose()), this.remove(...this.children), Object.keys(this.meshCollect).forEach((i) => {
      const n = this.meshCollect[i], r = n[0], a = new ho(
        r.geometry,
        r.material,
        n.length * this.count
      );
      this.instanceCollect[i] = a, this.mesh2InstanceMesh[r.uuid] = a, this.add(a);
    });
  }
  setMatrixAt(t, i) {
    Object.keys(this.meshCollect).forEach((n) => {
      const r = this.instanceCollect[n], a = this.meshCollect[n];
      a.forEach((o, l) => {
        k6.copy(o.matrixWorld), k6.premultiply(i), r.setMatrixAt(a.length * t + l, k6), r.instanceMatrix.needsUpdate = !0;
      });
    });
  }
  setMeshMaterial(t, i) {
    this.mesh2InstanceMesh[t] && (this.mesh2InstanceMesh[t].material = i);
  }
}
var qAe = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const YAe = "Water";
let cM = class extends Fi {
  constructor() {
    super(...arguments);
    S(this, "childMap", /* @__PURE__ */ new Map());
  }
  _getDelaunatorMeshIndex(e) {
    const t = Gp.from(
      e.map((n) => [n.x, n.z])
    ), i = [];
    for (let n = 0; n < t.triangles.length; n++)
      i.push(t.triangles[n]);
    return i;
  }
  add(e, t = !0) {
    const i = new qD(e);
    return t ? this.group.attach(i) : this.group.add(i), this.childMap.set(e.id, i), this.update(0), i;
  }
  updateDataById(e, t, i, n = []) {
    const r = this.childMap.get(e);
    if (r) {
      r.name = t, r.position.set(i.x, i.y, i.z), r.name = t;
      const a = n.map((o) => new _(o.x, o.y, o.z));
      r.updatePoints(a, this._getDelaunatorMeshIndex(a)), this.update(0), this.needDataUpdateServer = !0;
    }
  }
  update(e) {
    const { color: t, metalness: i, roughness: n } = this.states.getCurrentStateConfig();
    this.childMap.forEach((r) => {
      t && r.setColor(t), i && r.setMetalness(i), n && r.setRoughness(n);
    });
  }
  tick() {
    this.childMap.forEach((e) => {
      e.material.uniforms.time.value += 1;
    });
  }
  setData(e) {
    const { children: t, groupPosition: i } = e;
    t.forEach((n) => {
      this.add({
        id: n.id,
        points: n.points,
        position: n.position,
        meshIndex: n.meshIndex,
        name: n.name,
        customAttribute: n.customAttribute
      });
    }), this.group.position.set(i.x, i.y, i.z);
  }
  removeObjectById(e) {
    const t = this.getObjectById(e);
    if (t) {
      const i = this.childMap.get(t.uuid);
      this.childMap.delete(t.uuid), i && i.dispose(), i && this.group.remove(i), this.needDataUpdateServer = !0;
    }
  }
  draw(e, t) {
    e.mode = "multi-polygon", e.startDraw(), e.setActiveComponent(this), e.polygonUtils.setDrawGroupsVisible(["point"], !0);
    const i = /* @__PURE__ */ new Set();
    let n = nt.generateUUID();
    const r = () => {
      this.childMap.get(n) && (n = nt.generateUUID());
    };
    this.childMap.forEach((d) => {
      e.polygonUtils.addDrawPolygon(
        d.uuid,
        d.position,
        d.points,
        d.meshIndex
      );
    });
    const a = (d) => {
      e.executeDrawCommand("add-point", { position: d.position, drawId: n });
    }, o = (d) => {
      const f = this.getObjectById(d.point.uuid);
      f && (f.visible = !1), f && i.add(f.uuid);
    }, l = (d) => {
      if (i.has(d.polygon.uuid)) {
        const p = this.getObjectById(d.polygon.uuid);
        p && (p.visible = !0), i.delete(d.polygon.uuid);
      } else {
        const p = {};
        if (p) {
          const [g] = this.group.children;
          g && Object.keys(g.userData).forEach((m) => {
            p[m] = void 0;
          });
        }
        const v = this.childMap.get(d.polygon.uuid);
        v ? v.updatePoints(d.polygon.points, d.polygon.meshIndex) : this.add(
          {
            id: d.polygon.uuid,
            points: d.polygon.points,
            position: d.polygon.position,
            meshIndex: d.polygon.meshIndex,
            customAttribute: p,
            name: `区域${this.childMap.size}`
          },
          !1
        );
      }
    }, c = (d) => {
      if (!(d.point instanceof qD)) {
        const f = d.point.userData.drawId, p = e.polygonUtils.updatePolygonMeshByDrawPointGroup(f), v = this.childMap.get(f);
        v && p && v.updatePoints(p.points, p.meshIndex);
      }
    }, h = () => {
      this.needDataUpdateServer = !0;
    }, u = () => {
      i.forEach((d) => {
        this.removeObjectById(d);
      }), t && t(), this.needDataUpdateServer = !0, e.removeEventListener("draw-point", a), e.removeEventListener("end-draw", u), e.removeEventListener("add-polygon", l), e.removeEventListener("remove-point", o), e.removeEventListener("drag", c), e.removeEventListener("end-drag", h), e.removeEventListener("double-click", r);
    };
    e.addEventListener("draw-point", a), e.addEventListener("end-draw", u), e.addEventListener("add-polygon", l), e.addEventListener("remove-point", o), e.addEventListener("drag", c), e.addEventListener("end-drag", h), e.addEventListener("double-click", r);
  }
  getObjectById(e) {
    let t;
    return this.childMap.forEach((i) => {
      i.traverse((n) => {
        n.uuid === e && (t = n);
      });
    }), e === "" ? this.group : t;
  }
  toJson() {
    const e = [];
    this.childMap.forEach((i) => {
      e.push({
        id: i.uuid,
        points: i.points,
        position: i.position,
        meshIndex: i.meshIndex,
        name: i.name,
        customAttribute: i.userData
      });
    });
    const t = {
      groupPosition: {
        x: this.group.position.x,
        y: this.group.position.y,
        z: this.group.position.z
      },
      children: e,
      positionMode: this.positionMode
    };
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: t
    };
  }
  async updateFromJson(e, t) {
    const { id: i, name: n, states: r, data: a } = t;
    this.setData(a);
    const o = r;
    o && this.states.initStates(o), this.positionMode = a.positionMode ?? "xyz", this.update(0), this.isInit = !0;
  }
};
cM = qAe([
  ni(YAe)
], cM);
class qD extends Re {
  constructor(t) {
    super();
    S(this, "points", []);
    S(this, "meshIndex", []);
    this.params = t;
    const { points: i, meshIndex: n, id: r, name: a, customAttribute: o, position: l } = t;
    this.uuid = r, this.name = a, this.userData = o, i.length && n.length && this.updatePoints(
      i.map((c) => new _(c.x, c.y, c.z)),
      n
    ), this.material = new ZAe({
      side: ui,
      transparent: !0
    }), this.position.set(t.position.x, t.position.y, t.position.z);
  }
  setColor(t) {
    let i = t;
    typeof t != "string" && "pure" in t && (i = t.pure);
    const n = new pi(i);
    this.material.color.set(n.r / 255, n.g / 255, n.b / 255), this.material.opacity = n.getAlpha();
  }
  setMetalness(t) {
    this.material.metalness = t;
  }
  setRoughness(t) {
    this.material.roughness = t;
  }
  updatePoints(t, i) {
    this.points = [...t], this.meshIndex = [...i], this.geometry.setFromPoints(this.points), this.geometry.setIndex(this.meshIndex), this.updateGeometryUv(t), this.updateGeometryNormal();
  }
  updateGeometryNormal() {
    const t = this.geometry.index, i = this.geometry.getAttribute("position"), n = new Ht(new Float32Array(i.count * 3), 3);
    this.geometry.setAttribute("normal", n);
    const r = new _(), a = new _(), o = new _(), l = new _(), c = new _(), h = new _(), u = new _(), d = new _();
    if (t)
      for (let f = 0, p = t.count; f < p; f += 3) {
        const v = t.getX(f + 0), g = t.getX(f + 1), m = t.getX(f + 2);
        r.fromBufferAttribute(i, v), a.fromBufferAttribute(i, g), o.fromBufferAttribute(i, m), u.subVectors(o, a), d.subVectors(r, a), u.cross(d), l.fromBufferAttribute(n, v), c.fromBufferAttribute(n, g), h.fromBufferAttribute(n, m), l.add(u), c.add(u), h.add(u), n.setXYZ(v, l.x, l.y, l.z), n.setXYZ(g, c.x, c.y, c.z), n.setXYZ(m, h.x, h.y, h.z), this.geometry.normalizeNormals(), n.needsUpdate = !0;
      }
  }
  updateGeometryUv(t) {
    let i = Number.MAX_SAFE_INTEGER, n = -Number.MAX_SAFE_INTEGER, r = Number.MAX_SAFE_INTEGER, a = -Number.MAX_SAFE_INTEGER;
    t.forEach((h) => {
      h.x < i && (i = h.x), h.x > n && (n = h.x), h.z < r && (r = h.z), h.z > a && (a = h.z);
    });
    const o = n - i, l = a - r, c = [];
    t.forEach((h) => {
      c.push((h.x - i) / o), c.push((h.z - r) / l);
    }), this.geometry.setAttribute("uv", new gt(c, 2));
  }
  dispose() {
    this.material.dispose();
  }
}
class ZAe extends nn {
  constructor(t) {
    super(t);
    S(this, "uniforms", rs.merge([
      xa.standard.uniforms,
      {
        clearcoat: { value: 0 },
        clearcoatMap: { value: null },
        clearcoatMapTransform: { value: /* @__PURE__ */ new Gt() },
        clearcoatNormalMap: { value: null },
        clearcoatNormalMapTransform: { value: /* @__PURE__ */ new Gt() },
        clearcoatNormalScale: { value: /* @__PURE__ */ new Se(1, 1) },
        clearcoatRoughness: { value: 0 },
        clearcoatRoughnessMap: { value: null },
        clearcoatRoughnessMapTransform: { value: /* @__PURE__ */ new Gt() },
        iridescence: { value: 0 },
        iridescenceMap: { value: null },
        iridescenceMapTransform: { value: /* @__PURE__ */ new Gt() },
        iridescenceIOR: { value: 1.3 },
        iridescenceThicknessMinimum: { value: 100 },
        iridescenceThicknessMaximum: { value: 400 },
        iridescenceThicknessMap: { value: null },
        iridescenceThicknessMapTransform: { value: /* @__PURE__ */ new Gt() },
        sheen: { value: 0 },
        sheenColor: { value: /* @__PURE__ */ new _e(0) },
        sheenColorMap: { value: null },
        sheenColorMapTransform: { value: /* @__PURE__ */ new Gt() },
        sheenRoughness: { value: 1 },
        sheenRoughnessMap: { value: null },
        sheenRoughnessMapTransform: { value: /* @__PURE__ */ new Gt() },
        transmission: { value: 0 },
        transmissionMap: { value: null },
        transmissionMapTransform: { value: /* @__PURE__ */ new Gt() },
        transmissionSamplerSize: { value: /* @__PURE__ */ new Se() },
        transmissionSamplerMap: { value: null },
        thickness: { value: 0 },
        thicknessMap: { value: null },
        thicknessMapTransform: { value: /* @__PURE__ */ new Gt() },
        attenuationDistance: { value: 0 },
        attenuationColor: { value: /* @__PURE__ */ new _e(0) },
        specularColor: { value: /* @__PURE__ */ new _e(1, 1, 1) },
        specularColorMap: { value: null },
        specularColorMapTransform: { value: /* @__PURE__ */ new Gt() },
        specularIntensity: { value: 1 },
        specularIntensityMap: { value: null },
        specularIntensityMapTransform: { value: /* @__PURE__ */ new Gt() }
      },
      {
        tNormalMap0: { value: null },
        tNormalMap1: { value: null },
        tNormalMap2: { value: null },
        normalTilingVelocity_0: { value: new Yt(0.035, 0.04, 5 * 3e-3, 0) },
        normalTilingVelocity_1: { value: new Yt(0.04, 0.06, -4.4 * 3e-3, 0) },
        normalTilingVelocity_2: {
          value: new Yt(5e-3, 0.01, 3.5 * 0.2576 * 3e-3, 3.5 * 0.9698 * 3e-3)
        },
        normalScale_0: { value: 0.144 },
        normalScale_1: { value: 0.144 },
        normalScale_2: { value: 0.197 },
        time: { value: 0 },
        noiseMap: { value: null }
      }
    ]));
    S(this, "vertexShader", KAe);
    S(this, "fragmentShader", JAe);
    this.type = "GisMapWater", this.defines.USE_UV = "", this.color.setStyle("#ffffff"), this.metalness = 0.083, this.roughness = 0.2, this.transmission = 0.9, this.ior = 1.333, this.thickness = 20, this.depthWrite = !1;
    const i = new rh();
    i.load(Rne, (n) => {
      n.wrapS = un, n.wrapT = un, this.uniforms.noiseMap.value = n;
    }), i.load(Dne, (n) => {
      n.wrapS = un, n.wrapT = un, this.uniforms.tNormalMap0.value = n, this.uniforms.tNormalMap1.value = n, this.uniforms.tNormalMap2.value = n;
    });
  }
  setUniforms(t) {
    for (const i in t)
      this.uniforms[i].value = t[i];
  }
}
const KAe = (
  /* glsl */
  `
#define STANDARD

varying vec3 vViewPosition;

varying vec3 vWorldPosition;

#include <common>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

	#include <uv_vertex>
	#include <color_vertex>
	#include <morphcolor_vertex>

	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>

	#include <begin_vertex>
	#include <morphtarget_vertex>
	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>

	vViewPosition = - mvPosition.xyz;

	// #include <worldpos_vertex>
	vec4 worldPosition = vec4( transformed, 1.0 );
	#ifdef USE_INSTANCING
		worldPosition = instanceMatrix * worldPosition;
	#endif
	worldPosition = modelMatrix * worldPosition;

	#include <shadowmap_vertex>
	#include <fog_vertex>

	vWorldPosition = worldPosition.xyz;

}
`
), JAe = (
  /* glsl */
  `
#define STANDARD

#ifdef PHYSICAL
	#define IOR
	#define USE_SPECULAR
#endif

uniform vec3 diffuse;
uniform vec3 emissive;
uniform float roughness;
uniform float metalness;
uniform float opacity;

#ifdef IOR
	uniform float ior;
#endif

#ifdef USE_SPECULAR
	uniform float specularIntensity;
	uniform vec3 specularColor;

	#ifdef USE_SPECULAR_COLORMAP
		uniform sampler2D specularColorMap;
	#endif

	#ifdef USE_SPECULAR_INTENSITYMAP
		uniform sampler2D specularIntensityMap;
	#endif
#endif

#ifdef USE_CLEARCOAT
	uniform float clearcoat;
	uniform float clearcoatRoughness;
#endif

#ifdef USE_IRIDESCENCE
	uniform float iridescence;
	uniform float iridescenceIOR;
	uniform float iridescenceThicknessMinimum;
	uniform float iridescenceThicknessMaximum;
#endif

#ifdef USE_SHEEN
	uniform vec3 sheenColor;
	uniform float sheenRoughness;

	#ifdef USE_SHEEN_COLORMAP
		uniform sampler2D sheenColorMap;
	#endif

	#ifdef USE_SHEEN_ROUGHNESSMAP
		uniform sampler2D sheenRoughnessMap;
	#endif
#endif

varying vec3 vViewPosition;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <alphatest_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>
#include <iridescence_fragment>
#include <cube_uv_reflection_fragment>
#include <envmap_common_pars_fragment>
#include <envmap_physical_pars_fragment>
#include <fog_pars_fragment>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_physical_pars_fragment>
#include <transmission_pars_fragment>
#include <shadowmap_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <clearcoat_pars_fragment>
#include <iridescence_pars_fragment>
#include <roughnessmap_pars_fragment>
#include <metalnessmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

uniform float time;
uniform sampler2D tNormalMap0;
uniform sampler2D tNormalMap1;
uniform sampler2D tNormalMap2;
uniform vec4 normalTilingVelocity_0;
uniform vec4 normalTilingVelocity_1;
uniform vec4 normalTilingVelocity_2;
uniform float normalScale_0;
uniform float normalScale_1;
uniform float normalScale_2;

#ifndef USE_TRANSMISSION
varying vec3 vWorldPosition;
#endif

uniform sampler2D noiseMap;

//根据UE的DeriveNormalZ节点而来
vec3 deriveNormalZ(vec2 xy){
	return vec3(xy, clamp( sqrt(1.0 - (xy.x * xy.x + xy.y * xy.y)), 0.001, 1.0 )); //限制z的最小值，不然有黑块
}

vec4 textureNoTile( sampler2D tex, in vec2 uv ) {
	const float amplitude = 20.0;
    const float frequency = 0.005;

    // sample variation pattern    
    float k = texture2D( noiseMap, frequency * uv ).r; // cheap (cache friendly) lookup    
    
    // compute index    
    float index = k * amplitude;
    float i = floor( index );
    float f = fract( index );

    // offsets for the different virtual patterns    
    vec2 offa = sin(vec2(3.0,7.0)*(i+0.0)); // can replace with any other hash    
    vec2 offb = sin(vec2(3.0,7.0)*(i+1.0)); // can replace with any other hash    

    // compute derivatives for mip-mapping    
    vec2 dx = dFdx(uv), dy = dFdy(uv);
    
    // sample the two closest virtual patterns    
    vec4 cola = textureGrad( tex, uv + offa, dx, dy );
    vec4 colb = textureGrad( tex, uv + offb, dx, dy );

	vec4 diff = cola-colb;
	float sum = diff.x+diff.y+diff.z;
	
    // interpolate between the two virtual patterns    
    return mix( cola, colb, smoothstep( 0.2, 0.8, f-0.1*sum ) );

}

vec3 getNormalPass(const in vec2 scale, const in vec2 velocity, sampler2D normalMap) {
	vec2 uv = ( vWorldPosition.xz + velocity * time ) * scale; //取世界坐标的x和z作为uv
	uv.y = -uv.y; //不反一下就会法线看起来不对劲
	vec3 normal = textureNoTile( normalMap, uv ).xyz * 2.0 - 1.0;
	return normal;
}

vec3 getNormal() {
	vec3 pass1 = getNormalPass(vec2(normalTilingVelocity_0.xy), vec2(normalTilingVelocity_0.zw), tNormalMap0);
	vec3 pass2 = getNormalPass(vec2(normalTilingVelocity_1.xy), vec2(normalTilingVelocity_1.zw), tNormalMap1);
	vec3 pass3 = getNormalPass(vec2(normalTilingVelocity_2.xy), vec2(normalTilingVelocity_2.zw), tNormalMap2);
	vec3 normal = normalize( deriveNormalZ( (pass1.xy*normalScale_0 + pass2.xy*normalScale_1 ) + pass3.xy * normalScale_2 ) ); //不能归一化入参，否则有噪点
	return normal;
}

vec3 perturbNormal2Arb( vec3 eye_pos, vec3 surf_norm, vec3 mapN, float faceDirection ) {

	vec3 q0 = dFdx( eye_pos.xyz );
	vec3 q1 = dFdy( eye_pos.xyz );
	vec2 st0 = dFdx( vUv.st );
	vec2 st1 = dFdy( vUv.st );

	vec3 N = surf_norm; // normalized

	vec3 q1perp = cross( q1, N );
	vec3 q0perp = cross( N, q0 );

	vec3 T = q1perp * st0.x + q0perp * st1.x;
	vec3 B = q1perp * st0.y + q0perp * st1.y;

	float det = max( dot( T, T ), dot( B, B ) );
	float scale = ( det == 0.0 ) ? 0.0 : faceDirection * inversesqrt( det );

	return normalize( T * ( mapN.x * scale ) + B * ( mapN.y * scale ) + N * mapN.z );

}

void main() {

	#include <clipping_planes_fragment>

	vec4 diffuseColor = vec4( diffuse, opacity );
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = emissive;

	#include <logdepthbuf_fragment>
	#include <map_fragment>
	#include <color_fragment>
	#include <alphamap_fragment>
	#include <alphatest_fragment>
	#include <roughnessmap_fragment>
	#include <metalnessmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>

	vec3 vertexNormal = vec3(normal);
	//只支持切线空间法线
	vec3 mapN = getNormal();
	#ifdef USE_TANGENT
		normal = normalize( vTBN * mapN );
	#else
		normal = perturbNormal2Arb( - vViewPosition, normal, mapN, faceDirection );
	#endif

	#include <clearcoat_normal_fragment_begin>
	#include <clearcoat_normal_fragment_maps>
	#include <emissivemap_fragment>

	// accumulation
	#include <lights_physical_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>

	// modulation
	#include <aomap_fragment>

	vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
	vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;

	#include <transmission_fragment>

	vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;

	#ifdef USE_SHEEN

		// Sheen energy compensation approximation calculation can be found at the end of
		// https://drive.google.com/file/d/1T0D1VSyR4AllqIJTQAraEIzjlb5h4FKH/view?usp=sharing
		float sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );

		outgoingLight = outgoingLight * sheenEnergyComp + sheenSpecular;

	#endif

	#ifdef USE_CLEARCOAT

		float dotNVcc = saturate( dot( geometry.clearcoatNormal, geometry.viewDir ) );

		vec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );

		outgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + clearcoatSpecular * material.clearcoat;

	#endif

	#include <opaque_fragment>
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
	#include <fog_fragment>
	#include <premultiplied_alpha_fragment>
	#include <dithering_fragment>

}
`
);
function uh(s, e) {
  let t = { x: 0, y: 0, z: 0 };
  const i = e.states.getCurrentStateConfig();
  let n = !1;
  return "groupPosition" in s && (t = s.groupPosition, i.groupPosition = t, e.states.needsUpdateStateSet.add(e.getCurStateId()), e.needDataUpdateServer = !0, n = !0), "groupPosition" in i && (t = i.groupPosition, n = !0), n || (i.groupPosition = t, e.states.needsUpdateStateSet.add(e.getCurStateId())), t;
}
function QAe(s) {
  const { position: e } = s.group, t = s.states.getCurrentStateConfig();
  if ("groupPosition" in t) {
    t.groupPosition = { x: e.x, y: e.y, z: e.z }, s.states.needsUpdateStateSet.add(s.getCurStateId());
    return;
  }
  "groupPosition" in s.toJson().data && (s.needDataUpdateServer = !0), s.needsUpdate = !0;
}
var t6e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
let hM = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "camera", new Zt());
    S(this, "texture", null);
    S(this, "maskTexture", null);
    S(this, "targetSprite", Ex(!0));
    S(this, "url", "");
    S(this, "defaultUrl", "");
    S(this, "maskUrl", "");
    S(this, "fov", 45);
    S(this, "width", 16);
    S(this, "height", 9);
    S(this, "customAttributes", {});
    S(this, "cameraHelper", new b2(
      new Zt(50, 1, 0.1, 50)
    ));
    S(this, "cameraSprite", Ex(!0));
    S(this, "world");
    S(this, "_mouseMoved", !1);
    // 射线拾取
    S(this, "_rayCaster", new lr());
    // 水平面
    S(this, "_ground", new ln(new _(0, 1, 0)));
    S(this, "_addState", {
      targetAdded: !1,
      viewPointAdded: !1
    });
    S(this, "_selectedObject");
    this._mouseDownHandler = this._mouseDownHandler.bind(this), this._mouseUpHandler = this._mouseUpHandler.bind(this), this._mouseMoveHandler = this._mouseMoveHandler.bind(this), this._keyboardUpHandler = this._keyboardUpHandler.bind(this);
  }
  getCanAttach() {
    return !0;
  }
  onDragEnd() {
    this._selectedObject === this.cameraSprite ? this.cameraSprite.position.copy(this._selectedObject.position) : this._selectedObject === this.targetSprite && this.targetSprite.position.copy(this._selectedObject.position);
    const e = this.cameraSprite.position.distanceTo(this.targetSprite.position);
    this.camera.position.copy(this.cameraSprite.position), this.camera.lookAt(this.targetSprite.position), this.camera.updateMatrixWorld(!0), this.camera.far = e, this.camera.updateProjectionMatrix(), this.cameraHelper.camera.position.copy(this.cameraSprite.position), this.cameraHelper.camera.lookAt(this.targetSprite.position), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraHelper.camera.far = e, this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update(), dt.Entity.refresh(), Qe.ApiServer.updateComponent(this.entity.uuid, this.toJson());
  }
  getObjectById(e) {
    if (e === "")
      this._selectedObject = this.cameraSprite;
    else {
      const t = this.group.getObjectByProperty("uuid", e);
      this._selectedObject = t;
    }
    return this._selectedObject;
  }
  updateCameraPosition(e) {
    this.cameraSprite.position.copy(e), this.camera.position.copy(e), this.camera.lookAt(this.targetSprite.position), this.camera.updateMatrixWorld(!0), this.camera.updateProjectionMatrix(), this.cameraHelper.camera.copy(this.camera), this.cameraHelper.update();
  }
  updateCameraTargetPosition(e) {
  }
  async initialize() {
    this.cameraSprite.scale.set(8, 8, 8), this.cameraSprite.uuid = `obj_${this.cameraSprite.uuid}`, this.initSprite(Ehe, this.cameraSprite), this.targetSprite.scale.set(2, 4, 2), this.targetSprite.position.set(0, 0, -50), Qe.mode !== "VideoProjectEditor" && (this.cameraSprite.visible = !1, this.targetSprite.visible = !1, this.cameraHelper.visible = !1), this.initSprite(The, this.targetSprite), this.group.add(this.cameraSprite), this.group.add(this.targetSprite), this.world.renderer.rootScene.add(this.cameraHelper), this.cameraSprite.userData.ignore_raycaster = !0, this.targetSprite.userData.ignore_raycaster = !0, this.cameraHelper.userData.ignore_raycaster = !0, this.texture = await st.getVideoProjectDefaultTexture(), this.maskTexture = await st.getVideoProjectDefaultMaskTexture();
    const e = this.world;
    this.addProjectCameraToRenderer(e);
  }
  /**
   * 更新目标点位置(内部会根据目标点重新计算相机的欧拉旋转)
   * @param position
   */
  updateCameraTargetData(e) {
    const t = new _(e.x, e.y, e.z);
    this.targetSprite.position.copy(t);
    const i = this.cameraSprite.position.distanceTo(this.targetSprite.position);
    this.camera.lookAt(this.targetSprite.position), this.camera.updateMatrixWorld(!0), this.camera.far = i, this.camera.updateProjectionMatrix(), this.cameraHelper.camera.lookAt(this.targetSprite.position), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraHelper.camera.far = i, this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update(), this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  /**
   * 更新视点位置（内部会根据视点的欧拉角变换，重新计算目标点世界位置）
   * @param dataOptions
   */
  updateCameraViewData(e) {
    this.updateVideoTexture(e), this.url = e.url, this.defaultUrl = e.defaultUrl, this.maskUrl = e.maskUrl ?? "", this.fov = e.fov, this.width = e.width, this.height = e.height;
    const t = new _(
      e.position.x,
      e.position.y,
      e.position.z
    ), i = 1e-5, n = Math.abs(e.rotation.x - this.camera.rotation.x) > i, r = Math.abs(e.rotation.y - this.camera.rotation.y) > i, a = Math.abs(e.rotation.z - this.camera.rotation.z) > i, o = Math.abs(e.position.x - this.camera.position.x) > i, l = Math.abs(e.position.y - this.camera.position.y) > i, c = Math.abs(e.position.z - this.camera.position.z) > i;
    if (n || r || a) {
      const h = new Mi(
        e.rotation.x,
        e.rotation.y,
        e.rotation.z
      ), u = t.distanceTo(this.targetSprite.position);
      this.camera.position.copy(t), this.camera.rotation.copy(h), this.camera.updateMatrixWorld(!0), this.camera.aspect = this.width / this.height, this.camera.fov = this.fov, this.camera.far = u, this.camera.updateProjectionMatrix(), this.cameraHelper.camera.position.copy(t), this.cameraHelper.camera.rotation.copy(h), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraHelper.camera.aspect = this.width / this.height, this.cameraHelper.camera.fov = this.fov, this.cameraHelper.camera.far = u, this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update(), this.cameraSprite.position.copy(t), this.targetSprite.position.copy(
        new _(0, 0, -u).applyMatrix4(this.camera.matrixWorld)
      );
    } else if (o || l || c) {
      const h = t.distanceTo(this.targetSprite.position);
      this.camera.position.copy(t), this.camera.lookAt(this.targetSprite.position), this.camera.updateMatrixWorld(!0), this.camera.aspect = this.width / this.height, this.camera.fov = this.fov, this.camera.far = h, this.camera.updateProjectionMatrix(), this.cameraHelper.camera.position.copy(t), this.cameraHelper.camera.lookAt(this.targetSprite.position), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraHelper.camera.aspect = this.width / this.height, this.cameraHelper.camera.fov = this.fov, this.cameraHelper.camera.far = h, this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update(), this.cameraSprite.position.copy(t);
    }
    this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  updateDataFromJson(e) {
    this.updateVideoTexture(e), this.url = e.url, this.defaultUrl = e.defaultUrl, this.maskUrl = e.maskUrl ?? "", this.fov = e.fov, this.width = e.width, this.height = e.height;
    const t = new _(
      e.position.x,
      e.position.y,
      e.position.z
    ), i = new Mi(
      e.rotation.x,
      e.rotation.y,
      e.rotation.z
    ), n = new _(
      e.target.x,
      e.target.y,
      e.target.z
    ), r = t.distanceTo(n);
    this.cameraSprite.position.copy(t), this.targetSprite.position.copy(n), this.camera.position.copy(t), this.camera.rotation.copy(i), this.camera.updateMatrixWorld(!0), this.camera.aspect = this.width / this.height, this.camera.fov = this.fov, this.camera.far = r, this.camera.updateProjectionMatrix(), this.cameraHelper.camera.position.copy(t), this.cameraHelper.camera.rotation.copy(i), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraHelper.camera.aspect = this.width / this.height, this.cameraHelper.camera.fov = this.fov, this.cameraHelper.camera.far = r, this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update();
  }
  async updateFromJson(e, t) {
    const i = t.data;
    this.world = e, await this.initialize(), this.updateDataFromJson(i), this.hideCameraHelper(), this.isInit = !0;
  }
  addProjectCameraToRenderer(e) {
    const t = e.renderer.threeRenderer;
    t.frameState.projectCameraMgr.cameras.find((n) => n.camera.uuid === this.camera.uuid) || t.frameState.projectCameraMgr.cameras.push({
      camera: this.camera,
      texture: this.texture,
      mask: this.maskTexture
    });
  }
  updateVideoTexture(e) {
    if (e.defaultUrl !== "" && e.url === "") {
      const t = na(e.defaultUrl);
      ["png", "jpg", "jpeg", "webp"].includes(t) ? st.loadTexture(e.defaultUrl).then((i) => {
        this.texture = i, this.updateProjectCamera();
      }) : this.texture = st.getVideoTexture(e.defaultUrl);
    }
    e.url !== "" && (this.texture = st.getVideoTexture(e.url)), e.maskUrl !== "" ? st.loadTexture(e.maskUrl).then((t) => {
      this.maskTexture = t, this.updateProjectCamera();
    }) : st.getVideoProjectDefaultMaskTexture().then((t) => {
      this.maskTexture = t, this.updateProjectCamera();
    });
  }
  updateProjectCamera() {
    const t = this.world.renderer.threeRenderer.frameState.projectCameraMgr.cameras.find((i) => i.camera.uuid === this.camera.uuid);
    t && (t.texture = this.texture, t.mask = this.maskTexture);
  }
  toJson() {
    const e = super.toJson(), t = {
      position: {
        x: this.camera.position.x,
        y: this.camera.position.y,
        z: this.camera.position.z
      },
      rotation: {
        x: this.camera.rotation.x,
        y: this.camera.rotation.y,
        z: this.camera.rotation.z
      },
      target: {
        x: this.targetSprite.position.x,
        y: this.targetSprite.position.y,
        z: this.targetSprite.position.z
      },
      url: this.url,
      defaultUrl: this.defaultUrl,
      maskUrl: this.maskUrl,
      fov: this.fov,
      width: this.width,
      height: this.height,
      customAttribute: JSON.parse(JSON.stringify(this.customAttributes))
    };
    return { ...e, data: t, type: "VideoProject" };
  }
  setCustomAttribute(e, t) {
    Object.assign(this.customAttributes, e), this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  replaceCustomAttribute(e) {
    this.customAttributes = { ...e }, this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  updateCustomAttributeKey(e, t) {
    this.syncComponentDataConfig(), dt.Entity.refresh();
  }
  removeCustomAttribute(e, t) {
    e && (delete this.customAttributes[e], this.syncComponentDataConfig(), dt.Entity.refresh());
  }
  initSprite(e, t) {
    const i = document.createElement("canvas");
    i.width = 64, i.height = 64, i.style.width = "64px", i.style.height = "64px";
    const n = i.getContext("2d"), r = new Image();
    r.src = e, r.onload = () => {
      n == null || n.drawImage(r, 0, 0, 64, 64);
      const a = new zc(i);
      t.material.map = a;
    };
  }
  startDraw() {
    const { threeRenderer: e } = this.world.renderer, t = e.domElement;
    cs.setCursorCssText(t, "create"), this.dispatchEvent("DrawEvent", { state: "startDraw" }), t.addEventListener("mousedown", this._mouseDownHandler), t.addEventListener("mouseup", this._mouseUpHandler), t.addEventListener("mousemove", this._mouseMoveHandler), document.addEventListener("keyup", this._keyboardUpHandler);
  }
  endDraw() {
    this._removeAllListeners();
  }
  hideCameraHelper() {
    this.cameraHelper.visible = !1;
  }
  showCameraHelper() {
    this.cameraHelper.visible = !0;
  }
  dispose() {
    const e = this.world.renderer.threeRenderer, t = e.frameState.projectCameraMgr.cameras.findIndex((i) => i.camera.uuid === this.camera.uuid);
    t > -1 && e.frameState.projectCameraMgr.cameras.splice(t, 1);
  }
  showHelper() {
    this.cameraHelper.visible = !0;
  }
  hideHelper() {
    this.cameraHelper.visible = !1;
  }
  _mouseDownHandler(e) {
    this._mouseMoved = !1;
  }
  _mouseMoveHandler(e) {
    this._mouseMoved = !0;
    const t = this._pick(e);
    if (t)
      if (this._addState.viewPointAdded)
        this._addState.targetAdded || (this.targetSprite.position.copy(t), this.camera.lookAt(t), this.cameraHelper.camera.lookAt(t), this.cameraHelper.camera.far = this.cameraSprite.position.distanceTo(this.targetSprite.position), this.cameraHelper.camera.updateProjectionMatrix(), this.cameraHelper.update());
      else {
        this.camera.position.copy(t), this.cameraSprite.position.copy(t);
        const i = t.clone().addVectors(t, new _(0, 0, -50));
        this.targetSprite.position.copy(i), this.cameraHelper.camera.position.copy(t), this.cameraHelper.camera.updateMatrixWorld(!0);
      }
  }
  _mouseUpHandler(e) {
    this._mouseMoved || (e.button === Lr.LEFT ? this._mouseLeftClick(e) : e.button === Lr.RIGHT && this._mouseRightClick(e));
  }
  _mouseLeftClick(e) {
    if (e.button !== Lr.LEFT)
      return;
    const t = this._pick(e);
    if (t) {
      if (!this._addState.viewPointAdded)
        this.camera.position.copy(t), this.cameraHelper.camera.position.copy(t), this.cameraHelper.camera.updateMatrixWorld(!0), this.cameraSprite.position.copy(t), this._addState.viewPointAdded = !0;
      else if (!this._addState.targetAdded) {
        this.camera.lookAt(t), this.camera.updateMatrixWorld(), this.camera.updateProjectionMatrix(), this.updateProjectCamera(), this.cameraHelper.camera.lookAt(t), this.cameraHelper.camera.updateMatrixWorld(!0), this._addState.targetAdded = !0, this.syncComponentDataConfig(), this.dispatchEvent("DrawEvent", { state: "EndDraw" });
        const { threeRenderer: i } = this.world.renderer, n = i.domElement;
        cs.setCursorCssText(n, "default"), this._removeAllListeners();
      }
    }
  }
  _mouseRightClick(e) {
  }
  _keyboardUpHandler(e) {
  }
  _removeAllListeners() {
    const { threeRenderer: e } = this.world.renderer, t = e.domElement;
    t.removeEventListener("mousedown", this._mouseDownHandler), t.removeEventListener("mouseup", this._mouseUpHandler), t.removeEventListener("mousemove", this._mouseMoveHandler), document.removeEventListener("keyup", this._keyboardUpHandler);
  }
  _pick(e) {
    const { threeRenderer: t, mainCamera: i, rootScene: n } = this.world.renderer, r = t.domElement.getBoundingClientRect(), a = 2 * ((e.clientX - r.x) / r.width) - 1, o = -2 * ((e.clientY - r.y) / r.height) + 1;
    this._rayCaster.setFromCamera(new Se(a, o), i);
    let l = this._rayCaster.ray.intersectPlane(this._ground, new _());
    const c = this._intersect(n, this._rayCaster);
    if (c.length > 0) {
      const h = c[0].point.distanceTo(i.position);
      if (l) {
        const u = l.distanceTo(i.position);
        h < u && (l = c[0].point);
      }
    }
    return l;
  }
  _intersect(e, t, i = []) {
    return rz(e, t, i), i.sort(i6e), i;
  }
  async syncComponentDataConfig() {
    await Qe.ApiServer.updateComponent(this.entity.uuid, this.toJson());
  }
};
hM = t6e([
  ni("VideoProject")
], hM);
function rz(s, e, t, i = !0) {
  if (s.layers.test(e.layers) && !s.userData.ignore_raycaster && s.type !== "TransformControlsPlane" && s.raycast(e, t), i === !0) {
    const n = s.children;
    for (let r = 0, a = n.length; r < a; r++)
      rz(n[r], e, t, !0);
  }
}
function i6e(s, e) {
  return s.distance - e.distance;
}
class n6e {
  constructor(e) {
    this.comp = e;
  }
  play() {
    this.comp.play();
  }
  stop() {
    this.comp.stop();
  }
  pause() {
    this.comp.pause();
  }
  setData(e) {
    for (const [t, i] of Object.entries(e)) {
      const n = this.comp.particleSystemMap[t];
      if (n) {
        const r = this.comp.states.getCurrentStateConfig().particleSystems.find((o) => o.id === t);
        let a = JSON.parse(JSON.stringify(r));
        a = { ...a, ...i }, this.comp.updateParticleSystem(n, a);
      }
    }
  }
}
const s6e = "/easytwin/system/particle/fire1_0.png", r6e = "/easytwin/system/particle/fire1_1.png", a6e = "/easytwin/system/particle/fire1_2.png", o6e = "/easytwin/system/particle/fire2_0.png", l6e = "/easytwin/system/particle/number0.png", c6e = "/easytwin/system/particle/rain0.png", YD = "/easytwin/system/particle/snow0.png", h6e = [
  {
    id: "",
    base: {
      play: !1,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !1,
      duration: 5,
      position: {
        x: 0,
        y: 0,
        z: 0
      },
      rotation: {
        x: -1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 0.2,
            b: 0.8
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 1
          },
          IntervalValue: {
            a: 0.6,
            b: 0.9
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "#ffffff",
          type: "pure",
          pures: ["rgb(255, 255, 255)"],
          linear: {
            stops: [
              {
                color: "rgb(246, 124, 4)",
                offset: 0.6726183353671257
              },
              {
                color: "rgb(255, 255, 137)",
                offset: 100
              }
            ]
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 0.4,
            b: 1.6
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 500,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 6.283185307179586
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: 0,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: s6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 6,
        vTileCount: 6,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.17453292519943295,
          radius: 0.01,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "Cone",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 35
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: 0.07747887820005417,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.5645786038863093,
                interpMode: "Cubic",
                outTangent: 1.1627688335260078,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: 0.18393118679523468,
                inWeight: 1.8979158298675793,
                inTangent: -1.7306124559726848,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "linear",
        pures: ["#ffffff"],
        linear: {
          stops: [
            {
              color: "rgb(247, 241, 236)",
              offset: 0
            },
            {
              color: "rgba(196, 190, 181, 0)",
              offset: 98.2062456170542
            }
          ]
        }
      },
      mode: "d1",
      enable: !1
    },
    frameOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: 35.856300354003906,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    rotationOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    }
  },
  {
    id: "",
    base: {
      play: !1,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !1,
      duration: 5,
      position: {
        x: 0,
        y: 0,
        z: 0
      },
      rotation: {
        x: -1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 0.2,
            b: 0.6
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 1.2
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "rgba(255, 100, 0, 0.65)",
          type: "pure",
          pures: ["#ffffff"],
          linear: {
            stops: [
              {
                color: "#000000",
                offset: 0
              },
              {
                color: "ffffff",
                offset: 100
              }
            ]
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 0,
            b: 1.4
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 500,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 6.283185307179586
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: 0,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: r6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 0,
        vTileCount: 0,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.2617993877991494,
          radius: 0.01,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "Cone",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 20
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: 0.11529257893562317,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "linear",
        pures: ["#ffffff"],
        linear: {
          stops: [
            {
              color: "rgba(255, 255, 255, 0)",
              offset: 0
            },
            {
              color: "rgba(255, 50, 0, 0.4)",
              offset: 98.2062456170542
            }
          ]
        }
      },
      mode: "d1",
      enable: !0
    },
    frameOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    rotationOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    }
  },
  {
    id: "",
    base: {
      play: !1,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0.05
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !1,
      duration: 5,
      position: {
        x: 0,
        y: 0,
        z: 0
      },
      rotation: {
        x: -1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 1,
            b: 1.2
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 1.2
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "rgba(255, 255, 255, 0.84)",
          type: "pure",
          pures: ["#ffffff"],
          linear: {
            stops: [
              {
                color: "#000000",
                offset: 0
              },
              {
                color: "ffffff",
                offset: 100
              }
            ]
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 1.5
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 500,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 6.283185307179586
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: -1,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: a6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 8,
        vTileCount: 8,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.17453292519943295,
          radius: 0.1,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "Cone",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 25
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: 0.09955593943595886,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 1.0434276311185782,
                interpMode: "Cubic",
                outTangent: 4.641092341224361,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: 0.7194146513938904,
                inWeight: 0.5747063572958861,
                inTangent: -1.0838075285397888,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "linear",
        pures: ["#ffffff"],
        linear: {
          stops: [
            {
              color: "rgba(233, 230, 227, 0.1)",
              offset: 1.7936945110187013
            },
            {
              color: "rgba(14, 13, 13, 0.2)",
              offset: 97.6457075292284
            }
          ]
        }
      },
      mode: "d1",
      enable: !0
    },
    frameOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: 40.80387496948242,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    rotationOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    }
  }
], u6e = [
  {
    id: "",
    base: {
      play: !0,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: -0.1,
            b: 0.1
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !0,
      duration: 5,
      position: {
        x: 0,
        y: 0,
        z: 0
      },
      rotation: {
        x: -1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 1.2,
            b: 1.4
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 1
          },
          IntervalValue: {
            a: 0.3,
            b: 0.4
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "#ffffff",
          type: "pure"
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 1,
            b: 1.4
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 500,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0.017453292519943295,
            b: 0.02617993877991494
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0.5,
        y: 0,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: o6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 2,
        vTileCount: 2,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0,
          radius: 0.1,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "Cone",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 60
          },
          IntervalValue: {
            a: 40,
            b: 48
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0.0025474576503038407,
                value: 0.8529208898544312,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.05331324355532505,
                interpMode: "Cubic",
                outTangent: 0.7783859321396425,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: 2.4714033603668213,
                inWeight: 0.1475759268152904,
                inTangent: 0.36236121687053857,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !0
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "linear",
        pures: ["#ffffff", "#000000"],
        linear: {
          angle: 0,
          stops: [
            {
              color: "rgba(0, 0, 0, 0)",
              offset: 0
            },
            {
              color: "rgb(255, 26, 0)",
              offset: 100
            },
            {
              color: "rgb(255, 213, 99)",
              offset: 14.04494382022472
            },
            {
              color: "rgba(255, 145, 55, 0.24)",
              offset: 35.95505617977528
            },
            {
              color: "rgba(255, 93, 21, 0.32)",
              offset: 57.865168539325836
            }
          ],
          opacity: 1
        }
      },
      mode: "d1",
      enable: !0
    },
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        }
      },
      mode: "d1",
      enable: !1
    }
  }
], d6e = [
  {
    id: "",
    base: {
      play: !0,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0.1
          },
          IntervalValue: {
            a: -1,
            b: 1
          },
          AnimationCurve: {}
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !0,
      duration: 5,
      position: {
        x: 0,
        y: 220,
        z: 0
      },
      rotation: {
        x: 1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 25
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0.8
          },
          IntervalValue: {
            a: 0.4,
            b: 0.8
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "rgba(169, 194, 219, 0.8)",
          type: "pure",
          pures: ["#ffffff", "#000000"],
          linear: {
            angle: 0,
            stops: [
              {
                color: "#ffffff",
                offset: 0
              },
              {
                color: "#000000",
                offset: 100
              }
            ],
            opacity: 1
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 1e3,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 3.141592653589793
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: -1,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: YD,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 2,
        vTileCount: 2,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          mode: "Random",
          size: {
            x: 1e3,
            y: 10,
            z: 1e3
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.6981317007977318,
          radius: 4,
          thickness: 1
        },
        Grid: {
          row: 100,
          width: 1e3,
          column: 100,
          height: 1e3
        },
        type: "GridEmitter",
        Point: {
          mode: "Random",
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Loop",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 1e3
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 1
        },
        IntervalValue: {
          a: 0.1,
          b: 3
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: 3.3766660690307617,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: -0.314605712890625,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "pure"
      },
      mode: "d1",
      enable: !1
    },
    frameOverTime: {},
    speedOverTime: {
      d1: {
        type: "IntervalValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 1,
          b: 0.1
        },
        AnimationCurve: {}
      },
      mode: "d1",
      enable: !1
    }
  },
  {
    id: "",
    base: {
      play: !0,
      delay: 0,
      enable: !1,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 50
          },
          IntervalValue: {
            a: -1,
            b: 1
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !0,
      duration: 5,
      position: {
        x: 0,
        y: 220,
        z: 0
      },
      rotation: {
        x: 1.605702911834783,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 25
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 1
          },
          IntervalValue: {
            a: 0.2,
            b: 2
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "#ffffff",
          type: "pure"
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 2
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 1e3,
      startRotation: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 1.5707963267948966,
            b: 6.283185307179586
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: -20,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: YD,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 2,
        vTileCount: 2,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          mode: "Random",
          size: {
            x: 1e3,
            y: 1e3,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.6981317007977318,
          radius: 4,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 1e3,
          column: 100,
          height: 1e3
        },
        type: "GridEmitter",
        Point: {
          mode: "Random",
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Loop",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 100
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "AnimationCurve",
        ConstantValue: {
          value: 1
        },
        IntervalValue: {
          a: 0.1,
          b: 3
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: 3.3766660690307617,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 0.999,
                value: -0.314605712890625,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "pure"
      },
      mode: "d1",
      enable: !1
    },
    speedOverTime: {
      d1: {
        type: "IntervalValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 1,
          b: 0.1
        },
        AnimationCurve: {}
      },
      mode: "d1",
      enable: !1
    }
  }
], f6e = [
  {
    id: "",
    base: {
      play: !1,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0.1
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !0,
      duration: 5,
      position: {
        x: 0,
        y: 60,
        z: 0
      },
      rotation: {
        x: 1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 2.4,
            b: 4.8
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 1
          },
          IntervalValue: {
            a: 0.3,
            b: 0.6
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "#ffffff",
          type: "linear",
          pures: ["#ffffff", "#000000"],
          linear: {
            angle: 0,
            stops: [
              {
                color: "rgb(204, 204, 204)",
                offset: 0
              },
              {
                color: "rgb(255, 255, 255)",
                offset: 100
              }
            ],
            opacity: 1
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 10,
            b: 30
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 1e3,
      startRotation: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0.8726646259971648
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: -1,
        z: 0
      }
    },
    render: {
      mode: "BillBoard",
      texture: c6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 0,
        vTileCount: 0,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0.6981317007977318,
          radius: 4,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "GridEmitter",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 1e3
          },
          IntervalValue: {
            a: 500,
            b: 1e3
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        }
      },
      mode: "d1",
      enable: !1
    },
    colorOverTime: {
      d1: {
        pure: "#ffffff",
        type: "pure"
      },
      mode: "d1",
      enable: !1
    },
    frameOverTime: {},
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        }
      },
      mode: "d1",
      enable: !1
    }
  }
], p6e = [
  {
    id: "",
    base: {
      play: !1,
      delay: 0,
      enable: !0,
      gravity: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      looping: !0,
      autoPlay: !0,
      duration: 5,
      position: {
        x: 0,
        y: 0,
        z: 0
      },
      rotation: {
        x: -1.5707963267948966,
        y: 0,
        z: 0
      },
      startLife: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 20
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startSize: {
        d1: {
          type: "IntervalValue",
          ConstantValue: {
            value: 3
          },
          IntervalValue: {
            a: 1,
            b: 3
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      startColor: {
        d1: {
          pure: "rgb(255, 255, 255)",
          type: "pure",
          pures: ["#ffffff"],
          linear: {
            stops: [
              {
                color: "#000000",
                offset: 0
              },
              {
                color: "ffffff",
                offset: 100
              }
            ]
          }
        },
        mode: "d1",
        enable: !0
      },
      startSpeed: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 30
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      worldSpace: !1,
      renderOrder: 0,
      maxParticles: 500,
      startRotation: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      gravityDirection: {
        x: 0,
        y: 0,
        z: 0
      }
    },
    render: {
      mode: "Billboard",
      texture: l6e,
      material: {
        map: ""
      },
      textureSheet: {
        enable: !1,
        uTileCount: 3,
        vTileCount: 3,
        startTileIndex: 0
      }
    },
    emitter: {
      emitterShape: {
        Box: {
          size: {
            x: 10,
            y: 10,
            z: 10
          },
          type: "Box"
        },
        Cone: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Cone",
          angle: 0,
          radius: 4,
          thickness: 1
        },
        Grid: {
          row: 100,
          type: "Grid",
          width: 100,
          column: 100,
          height: 100
        },
        type: "Cone",
        Point: {
          type: "Point"
        },
        Circle: {
          arc: 6.283185307179586,
          mode: "Loop",
          type: "Circle",
          radius: 10,
          thickness: 1
        },
        Sphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Sphere",
          radius: 10,
          thickness: 1
        },
        Hemisphere: {
          arc: 6.283185307179586,
          mode: "Random",
          type: "Hemisphere",
          radius: 10,
          thickness: 1
        }
      },
      emissionBursts: [],
      emissionOverTime: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 5
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      },
      emissionOverDistance: {
        d1: {
          type: "ConstantValue",
          ConstantValue: {
            value: 0
          },
          IntervalValue: {
            a: 0,
            b: 0
          },
          AnimationCurve: {
            value: {
              keys: [
                {
                  time: 0,
                  value: -9823503205552697e-19,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                },
                {
                  time: 1,
                  value: 1,
                  inWeight: 0.3,
                  inTangent: 0,
                  outWeight: 0.3,
                  interpMode: "Cubic",
                  outTangent: 0,
                  weightMode: "Both",
                  tangentMode: "User"
                }
              ],
              type: "AnimationCurve",
              preExtrapolation: "Constant",
              postExtrapolation: "Constant"
            }
          }
        },
        mode: "d1",
        enable: !0
      }
    },
    sizeOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    },
    colorOverTime: {
      d1: {
        pure: "rgb(255, 255, 255)",
        type: "pure",
        pures: ["#ffffff"],
        linear: {
          stops: [
            {
              color: "#000000",
              offset: 0
            },
            {
              color: "ffffff",
              offset: 100
            }
          ]
        }
      },
      mode: "d1",
      enable: !1
    },
    frameOverTime: {},
    speedOverTime: {
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: {
          value: {
            keys: [
              {
                time: 0,
                value: -9823503205552697e-19,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              },
              {
                time: 1,
                value: 1,
                inWeight: 0.3,
                inTangent: 0,
                outWeight: 0.3,
                interpMode: "Cubic",
                outTangent: 0,
                weightMode: "Both",
                tangentMode: "User"
              }
            ],
            type: "AnimationCurve",
            preExtrapolation: "Constant",
            postExtrapolation: "Constant"
          }
        }
      },
      mode: "d1",
      enable: !1
    }
  }
];
class m6e extends mt {
  updateMatrixWorld(e) {
    this.matrixWorld.identity();
  }
  updateWorldMatrix(e, t) {
    this.matrixWorld.identity();
  }
}
var az = Object.defineProperty, v6e = (s, e, t) => e in s ? az(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t, y6e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
}, x6e = (s, e, t) => (v6e(s, e + "" , t), t);
let Jr = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "particleSystemMap", {});
    S(this, "easyvProxy", new n6e(this));
    S(this, "particleSystemRenderOrder", 0);
    S(this, "batchRenderer", new kie());
    S(this, "play", () => {
      for (const [, e] of Object.entries(this.particleSystemMap))
        e.restart(), e.play();
    });
    S(this, "stop", () => {
      for (const [, e] of Object.entries(this.particleSystemMap))
        e.endEmit();
    });
    S(this, "pause", () => {
      for (const [, e] of Object.entries(this.particleSystemMap))
        e.pause();
    });
    this.particleSystemRenderOrder = Jr.ParticleComponentCount++;
    const n = new m6e();
    n.add(this.batchRenderer), this.group.add(n), Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  static getParticleDefaultConfig() {
    return JSON.parse(JSON.stringify(sc));
  }
  static getDefaultFireConfig() {
    const e = JSON.parse(JSON.stringify(h6e)), t = JSON.parse(JSON.stringify(sc));
    return e.map((i) => Go(i, t));
  }
  static getDefaultFire1Config() {
    const e = JSON.parse(JSON.stringify(u6e)), t = JSON.parse(JSON.stringify(sc));
    return e.map((i) => Go(i, t));
  }
  static getDefaultSnowConfig() {
    const e = JSON.parse(JSON.stringify(d6e)), t = JSON.parse(JSON.stringify(sc));
    return e.map((i) => Go(i, t));
  }
  static getDefaultRainConfig() {
    const e = JSON.parse(JSON.stringify(f6e)), t = JSON.parse(JSON.stringify(sc));
    return e.map((i) => Go(i, t));
  }
  static getDefaultNumberConfig() {
    const e = JSON.parse(JSON.stringify(p6e)), t = JSON.parse(JSON.stringify(sc));
    return e.map((i) => Go(i, t));
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s("ParticleSystem", "play", e, this.play), _s("ParticleSystem", "pause", e, this.pause), _s("ParticleSystem", "stop", e, this.stop);
  }
  updateParticlesRender(e) {
    this.isInit && this.batchRenderer.update(e);
  }
  setVisible(e, t) {
    super.setVisible(e, t);
  }
  async createFromState(e) {
    e.particleSystems.forEach((t) => {
      const i = this.createNewParticleSystem();
      this.particleSystemMap[i.id] = i, t.id = i.id, this.addParticleSystemToRenderer(i);
    }), this.addState({
      name: "状态1",
      rank: 0,
      config: JSON.parse(JSON.stringify(e))
    }), await this.syncComponentDataConfig(), this.isInit = !0;
  }
  createNewParticleSystem() {
    const e = new hx({
      material: new kt({
        transparent: !0
      }),
      renderOrder: this.particleSystemRenderOrder
    }), t = new gp(new Ai(1));
    t.enable = !1, e.addBehavior(t);
    const i = new vp(new Ai(1));
    i.enable = !1, e.addBehavior(i);
    const n = new mp(new Ai(0));
    n.enable = !1, e.addBehavior(n);
    const r = new pp(new Vc(new Yt(1, 1, 1, 1)));
    r.enable = !1, e.addBehavior(r);
    const a = new yp(new Ai(0));
    return a.enable = !1, e.addBehavior(a), e.addEventListener("EndEmit", () => {
      var o, l;
      (l = (o = this.entity.world).emitFunc) == null || l.call(
        o,
        "particleSystemEnd",
        {
          componentId: this.id,
          particleId: e.id,
          allEnd: !1
        },
        this.entity.uuid
      );
    }), e;
  }
  showHelper() {
    Object.values(this.particleSystemMap).forEach((e) => {
      e.emitter.emitterGizmo.visible = !0;
    });
  }
  hideHelper() {
    Object.values(this.particleSystemMap).forEach((e) => {
      e.emitter.emitterGizmo.visible = !1;
    });
  }
  async initialize() {
    this.isInit = !0;
  }
  update(e) {
    if (this.needsUpdate) {
      const t = this.states.getCurrentStateConfig();
      for (const [i, n] of Object.entries(this.particleSystemMap)) {
        const r = t.particleSystems.find((a) => a.id === i);
        r && this.updateParticleSystem(n, r);
      }
    }
  }
  onDragEnd() {
    const e = this.getCurStateId(), t = this.getStateList().find((i) => i.id === e);
    if (t) {
      const i = { ...t };
      s0(i.config.position) && (i.config.position = { x: 0, y: 0, z: 0 }), i.config.position.x = this.group.position.x, i.config.position.y = this.group.position.y, i.config.position.z = this.group.position.z, s0(i.config.rotation) && (i.config.rotation = { x: 0, y: 0, z: 0 }), i.config.rotation.x = this.group.rotation.x, i.config.rotation.y = this.group.rotation.y, i.config.rotation.z = this.group.rotation.z, s0(i.config.scale) && (i.config.scale = { x: 0, y: 0, z: 0 }), i.config.scale.x = this.group.scale.x, i.config.scale.y = this.group.scale.y, i.config.scale.z = this.group.scale.z, this.updateStateConfig(t.id, i.config, i.name);
    }
  }
  toJson() {
    const e = super.toJson();
    return e.data = {
      particleSystems: Object.entries(this.particleSystemMap).map((t) => ({
        name: t[1].name,
        id: t[1].id
      }))
    }, e.states.forEach((t) => {
      const i = t.config;
      i.position = {
        x: this.group.position.x,
        y: this.group.position.y,
        z: this.group.position.z
      }, i.rotation = {
        x: this.group.rotation.x,
        y: this.group.rotation.y,
        z: this.group.rotation.z
      }, i.scale = {
        x: this.group.scale.x,
        y: this.group.scale.y,
        z: this.group.scale.z
      };
    }), e;
  }
  async updateFromJson(e, t) {
    var a, o;
    const i = t.states;
    this.states.initStates(i);
    const n = i[0].config;
    t.data.particleSystems.forEach((l) => {
      const c = this.createNewParticleSystem();
      c.name = l.name, c.id = l.id, this.particleSystemMap[c.id] = c, this.addParticleSystemToRenderer(c);
      const h = n.particleSystems.find((u) => u.id === l.id);
      h && (this.updateParticleSystem(c, h), c.emitter.emitterGizmo.visible = !1);
    }), n.position && this.group.position.set(
      n.position.x,
      n.position.y,
      n.position.z
    ), n.rotation && this.group.rotation.set(
      n.rotation.x,
      n.rotation.y,
      n.rotation.z
    ), n.scale && this.group.scale.set(n.scale.x, n.scale.y, n.scale.z), (o = (a = this.entity.world).emitFunc) == null || o.call(
      a,
      "particleSystemInit",
      {
        componentId: this.id,
        entityId: this.entity.uuid
      },
      this.entity.uuid
    ), this.isInit = !0;
  }
  updateParticleStateConfig(e, t, i) {
    var r;
    const n = (r = this.states.deepGet(e)) == null ? void 0 : r.config;
    if (n) {
      const a = n.particleSystems.find((o) => o.id === t);
      if (a) {
        const o = {
          ...a,
          base: {
            ...a == null ? void 0 : a.base,
            ...i.base
          },
          emitter: {
            ...a == null ? void 0 : a.emitter,
            ...i.emitter
          },
          render: {
            ...a == null ? void 0 : a.render,
            ...i.render
          }
          // sizeOverTime: {...config.sizeOverTime!}
        }, l = n.particleSystems.findIndex((c) => c.id === t);
        l !== -1 && (n.particleSystems[l] = o, this.updateStateConfig(e, n));
      }
    }
  }
  updateParticleName(e, t) {
    this.particleSystemMap[e] && (this.particleSystemMap[e].name = t, this.syncComponentDataConfig(), dt.Entity.refresh());
  }
  addParticle() {
    const e = this.createNewParticleSystem();
    this.particleSystemMap[e.id] = e, this.addParticleSystemToRenderer(e);
    const t = [];
    this.states.stateList.forEach((n) => {
      const r = JSON.parse(JSON.stringify(sc));
      r.id = e.id, n.config.particleSystems.push(r), t.push(n);
    });
    const i = JSON.parse(JSON.stringify(sc));
    return this.updateParticleSystem(e, i), this.syncComponentDataConfig(), Qe.ApiServer.updateStates(t), dt.Entity.refresh(), e.id;
  }
  removeParticle(e) {
    const t = this.particleSystemMap[e];
    if (t) {
      this.removeParticleSystemFromRenderer(t), delete this.particleSystemMap[e];
      const i = [];
      this.states.stateList.forEach((n) => {
        n.config.particleSystems = n.config.particleSystems.filter(
          (r) => r.id !== e
        ), i.push(n);
      }), this.syncComponentDataConfig(), Qe.ApiServer.updateStates(i), dt.Entity.refresh();
    }
  }
  addParticleSystemToRenderer(e) {
    this.batchRenderer.addSystem(e), this.group.add(e.emitter);
  }
  removeParticleSystemFromRenderer(e) {
    this.batchRenderer.deleteSystem(e), this.group.remove(e.emitter);
  }
  updateParticleSystem(e, t) {
    !e.looping && t.base.looping && (e.restart(), e.play()), e.looping = t.base.looping, t.base.enable ? (e.restart(), e.play()) : e.endEmit(), Qe.mode === "Player" && (t.base.autoPlay || e.endEmit()), e.delay = t.base.delay, e.duration = Math.max(0.01, t.base.duration), i1(e, "startSpeed", "startSpeed", t), i1(e, "startLife", "startLife", t), i1(e, "startSize", "startSize", t), i1(e, "startRotation", "startRotation", t), i1(e, "startColor", "startColor", t), M6e(e, "emissionOverTime", "emissionOverTime", t), e.emissionBursts = [], t.emitter.emissionBursts.forEach((a) => {
      e.emissionBursts.push({
        time: a.time,
        count: new Ai(a.count),
        cycle: 1,
        interval: 0,
        probability: 1
      });
    }), e.emitter.position.set(
      t.base.position.x,
      t.base.position.y,
      t.base.position.z
    ), e.emitter.rotation.set(
      t.base.rotation.x,
      t.base.rotation.y,
      t.base.rotation.z
    ), e.gravity.set(
      t.base.gravityDirection.x,
      t.base.gravityDirection.y,
      t.base.gravityDirection.z
    ), e.gravity.normalize();
    const i = t.base.gravity.d1.type;
    e.gravityValue = kw[i](t.base.gravity.d1[i]);
    const n = t.emitter.emitterShape;
    switch (n.type) {
      case "Circle":
        e.emitterShape = new Ap({
          radius: n.Circle.radius,
          arc: n.Circle.arc,
          thickness: n.Circle.thickness,
          mode: by[n.Circle.mode]
        }), e.emitter.setGizmo(
          new Aie({
            radius: n.Circle.radius
          })
        );
        break;
      case "Box":
        {
          const a = new _(n.Box.size.x, n.Box.size.y, n.Box.size.z);
          e.emitterShape = new US({
            size: a
          }), e.emitter.setGizmo(
            new Mie({
              size: a
            })
          );
        }
        break;
      case "Sphere":
        e.emitterShape = new Ou({
          radius: n.Sphere.radius,
          thickness: n.Sphere.thickness,
          mode: by[n.Sphere.mode]
        }), e.emitter.setGizmo(
          new wie({
            radius: n.Sphere.radius
          })
        );
        break;
      case "Cone":
        e.emitterShape = new xp({
          radius: n.Cone.radius,
          angle: n.Cone.angle,
          arc: n.Cone.arc,
          thickness: n.Cone.thickness,
          mode: by[n.Cone.mode]
        }), e.emitter.setGizmo(
          new bie({
            radius: n.Cone.radius,
            angle: n.Cone.angle
          })
        );
        break;
      case "Hemisphere":
        e.emitterShape = new Mp({
          radius: n.Hemisphere.radius,
          thickness: n.Hemisphere.thickness,
          mode: by[n.Hemisphere.mode]
        }), e.emitter.setGizmo(
          new Sie({
            radius: n.Hemisphere.radius
          })
        );
        break;
      case "GridEmitter":
        e.emitterShape = new Sp({
          row: n.Grid.row,
          column: n.Grid.column,
          width: n.Grid.width,
          height: n.Grid.height
        }), e.emitter.setGizmo(
          new Tie({
            width: n.Grid.width,
            height: n.Grid.height,
            column: n.Grid.column,
            row: n.Grid.row
          })
        );
        break;
      case "Point":
        e.emitterShape = new bp(), e.emitter.setGizmo(new Eie());
        break;
    }
    Qe.mode !== "Editor" && (e.emitter.emitterGizmo.visible = !1), t.render.texture && t.render.texture !== "" && st.loadTexture(t.render.texture).then((a) => {
      e.texture = a, e.texture.needsUpdate = !0;
    }), e.rendererSettings.uTileCount = t.render.textureSheet.uTileCount ?? 1, e.rendererSettings.vTileCount = t.render.textureSheet.vTileCount ?? 1;
    const r = e.rendererSettings.uTileCount * e.rendererSettings.vTileCount;
    e.startTileIndex = new sr(0, r), n1(e, "SizeOverLife", t), n1(e, "SpeedOverLife", t), n1(e, "ColorOverLife", t), n1(e, "RotationOverLife", t), n1(e, "FrameOverLife", t);
  }
  async syncComponentDataConfig() {
    await Qe.ApiServer.updateComponent(this.entity.uuid, this.toJson());
  }
  dispose() {
    for (const [, t] of Object.entries(this.particleSystemMap))
      this.removeParticleSystemFromRenderer(t);
    this.particleSystemMap = {};
    const e = this.entity.uuid;
    Zf("ParticleSystem", "play", e), Zf("ParticleSystem", "pause", e), Zf("ParticleSystem", "stop", e);
  }
};
x6e(Jr, "ParticleComponentCount", 0);
Jr = y6e([
  ni("ParticleSystem", { version: "1.11.0" })
], Jr);
const by = {
  Random: Br.Random,
  Loop: Br.Loop,
  PingPong: Br.PingPong
};
function oz(s) {
  const e = new pi(s);
  return new Vc(new Yt(e.r / 255, e.g / 255, e.b / 255, e.a));
}
function lz(s) {
  const e = s.stops.map((i) => {
    const n = new _e(i.color);
    return [new _(n.r, n.g, n.b), i.offset / 100];
  }), t = s.stops.map((i) => [new pi(i.color).a, i.offset / 100]);
  return new Ic(e, t);
}
function A6e(s) {
  return Ai.fromJSON(s);
}
function b6e(s) {
  return s ? sr.fromJSON({ a: s.a ?? 0, b: s.b ?? 0 }) : sr.fromJSON({ a: 0, b: 0 });
}
const kw = {
  ConstantValue: A6e,
  IntervalValue: b6e,
  pure: oz,
  linear: lz,
  pures: (s) => {
    const e = s.map((t) => {
      const i = new pi(t);
      return [i.r / 255, i.g / 255, i.b / 255, i.a];
    });
    return cx.fromJSON({
      colors: e
    });
  },
  AnimationCurve: (s) => Xo.fromJSON(s.value)
};
function i1(s, e, t, i) {
  const n = i.base[t];
  if (n.mode === "d1" && n.d1) {
    const r = n.d1, a = r.type, o = kw[a];
    o ? s[e] = o(r[a]) : console.warn(`Unsupported value type for ${t}: ${n.type}`);
  }
}
function M6e(s, e, t, i) {
  const n = i.emitter[t];
  if (n.mode === "d1" && n.d1) {
    const r = n.d1, a = r.type, o = kw[a];
    o ? s[e] = o(r[a]) : console.warn(`Unsupported value type for ${t}: ${n.type}`);
  }
}
function n1(s, e, t) {
  var n, r, a, o, l, c, h, u, d, f;
  const i = s.behaviors.find((p) => p.type === e);
  if (e === "SizeOverLife" && (i.enable = ((n = t.sizeOverTime) == null ? void 0 : n.enable) ?? !1, ((r = t.sizeOverTime) == null ? void 0 : r.mode) === "d1"))
    if (t.sizeOverTime.d1.type === "AnimationCurve" && t.sizeOverTime.d1.AnimationCurve) {
      const p = Xo.fromJSON(t.sizeOverTime.d1.AnimationCurve.value);
      i.size = p;
    } else
      t.sizeOverTime.d1.type === "ConstantValue" && t.sizeOverTime.d1.ConstantValue ? i.size = Ai.fromJSON(
        t.sizeOverTime.d1.ConstantValue
      ) : t.sizeOverTime.d1.type === "IntervalValue" && t.sizeOverTime.d1.IntervalValue && (i.size = sr.fromJSON(
        t.sizeOverTime.d1.IntervalValue
      ));
  if (e === "SpeedOverLife" && (i.enable = ((a = t.speedOverTime) == null ? void 0 : a.enable) ?? !1, ((o = t.speedOverTime) == null ? void 0 : o.mode) === "d1"))
    if (t.speedOverTime.d1.type === "AnimationCurve" && t.speedOverTime.d1.AnimationCurve) {
      const p = Xo.fromJSON(t.speedOverTime.d1.AnimationCurve.value);
      i.speed = p;
    } else
      t.speedOverTime.d1.type === "ConstantValue" && t.speedOverTime.d1.ConstantValue ? i.speed = Ai.fromJSON(
        t.speedOverTime.d1.ConstantValue
      ) : t.speedOverTime.d1.type === "IntervalValue" && t.speedOverTime.d1.IntervalValue && (i.speed = sr.fromJSON(
        t.speedOverTime.d1.IntervalValue
      ));
  if (e === "ColorOverLife" && (i.enable = ((l = t.colorOverTime) == null ? void 0 : l.enable) ?? !1, ((c = t.colorOverTime) == null ? void 0 : c.mode) === "d1" && (t.colorOverTime.d1.type === "pure" && t.colorOverTime.d1.pure ? i.color = oz(
    t.colorOverTime.d1.pure
  ) : t.colorOverTime.d1.type === "linear" && t.colorOverTime.d1.linear && (i.color = lz(
    t.colorOverTime.d1.linear
  )))), e === "RotationOverLife" && (i.enable = ((h = t.rotationOverTime) == null ? void 0 : h.enable) ?? !1, ((u = t.rotationOverTime) == null ? void 0 : u.mode) === "d1"))
    if (t.rotationOverTime.d1.type === "AnimationCurve" && t.rotationOverTime.d1.AnimationCurve) {
      const p = Xo.fromJSON(t.rotationOverTime.d1.AnimationCurve.value);
      i.angularVelocity = p;
    } else
      t.rotationOverTime.d1.type === "ConstantValue" && t.rotationOverTime.d1.ConstantValue ? i.angularVelocity = Ai.fromJSON(
        t.rotationOverTime.d1.ConstantValue
      ) : t.rotationOverTime.d1.type === "IntervalValue" && t.rotationOverTime.d1.IntervalValue && (i.angularVelocity = sr.fromJSON(
        t.rotationOverTime.d1.IntervalValue
      ));
  if (e === "FrameOverLife" && (i.enable = ((d = t.frameOverTime) == null ? void 0 : d.enable) ?? !1, ((f = t.frameOverTime) == null ? void 0 : f.mode) === "d1"))
    if (t.frameOverTime.d1.type === "AnimationCurve" && t.frameOverTime.d1.AnimationCurve) {
      const p = Xo.fromJSON(t.frameOverTime.d1.AnimationCurve.value);
      i.frame = p;
    } else
      t.frameOverTime.d1.type === "ConstantValue" && t.frameOverTime.d1.ConstantValue ? i.frame = Ai.fromJSON(
        t.frameOverTime.d1.ConstantValue
      ) : t.frameOverTime.d1.type === "IntervalValue" && t.frameOverTime.d1.IntervalValue && (i.frame = sr.fromJSON(
        t.frameOverTime.d1.IntervalValue
      ));
}
const Ha = {
  value: {
    type: "AnimationCurve",
    keys: [
      {
        time: 0,
        value: -9823503205552697e-19,
        tangentMode: bb.User,
        weightMode: Ab.Both,
        interpMode: Gf.Cubic,
        inTangent: 0,
        outTangent: 0,
        inWeight: 0.3,
        outWeight: 0.3
      },
      {
        time: 1,
        value: 1,
        tangentMode: bb.User,
        weightMode: Ab.Both,
        interpMode: Gf.Cubic,
        inTangent: 0,
        outTangent: 0,
        inWeight: 0.3,
        outWeight: 0.3
      }
    ],
    preExtrapolation: Mb.Constant,
    postExtrapolation: Mb.Constant
  }
}, ZD = {
  stops: [
    {
      color: "#000000",
      offset: 0
    },
    {
      color: "ffffff",
      offset: 100
    }
  ]
}, sc = {
  id: "",
  base: {
    autoPlay: !1,
    enable: !0,
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    duration: 5,
    play: !1,
    delay: 0,
    looping: !0,
    startLife: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 5
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    startSize: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 1
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    startSpeed: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 5
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    startRotation: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    startColor: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "pure",
        pure: "#ffffff",
        linear: ZD,
        pures: ["#ffffff"]
      }
    },
    renderOrder: 0,
    worldSpace: !1,
    maxParticles: 500,
    gravity: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    gravityDirection: { x: 0, y: 0, z: 0 }
  },
  emitter: {
    emitterShape: {
      type: "Cone",
      Sphere: {
        radius: 10,
        thickness: 1,
        arc: 2 * Math.PI,
        mode: "Random",
        type: "Sphere"
      },
      Box: {
        type: "Box",
        size: {
          x: 10,
          y: 10,
          z: 10
        }
      },
      Circle: {
        type: "Circle",
        arc: 2 * Math.PI,
        radius: 10,
        thickness: 1,
        mode: "Random"
      },
      Cone: {
        type: "Cone",
        angle: 40 * (Math.PI / 180),
        mode: "Random",
        radius: 4,
        arc: 2 * Math.PI,
        thickness: 1
      },
      Hemisphere: {
        type: "Hemisphere",
        radius: 10,
        arc: 2 * Math.PI,
        thickness: 1,
        mode: "Random"
      },
      Point: {
        type: "Point"
      },
      Grid: {
        type: "Grid",
        width: 100,
        height: 100,
        row: 100,
        column: 100
      }
    },
    emissionOverTime: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 10
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    emissionOverDistance: {
      enable: !0,
      mode: "d1",
      d1: {
        type: "ConstantValue",
        ConstantValue: {
          value: 0
        },
        IntervalValue: {
          a: 0,
          b: 0
        },
        AnimationCurve: Ha
      }
    },
    emissionBursts: []
  },
  render: {
    mode: "Billboard",
    material: {
      map: ""
    },
    textureSheet: {
      enable: !1,
      startTileIndex: 0,
      uTileCount: 0,
      vTileCount: 0
    }
  },
  colorOverTime: {
    enable: !1,
    mode: "d1",
    d1: {
      type: "pure",
      pure: "#ffffff",
      linear: ZD,
      pures: ["#ffffff"]
    }
  },
  speedOverTime: {
    enable: !1,
    mode: "d1",
    d1: {
      type: "ConstantValue",
      ConstantValue: {
        value: 0
      },
      IntervalValue: {
        a: 0,
        b: 0
      },
      AnimationCurve: Ha
    }
  },
  sizeOverTime: {
    enable: !1,
    mode: "d1",
    d1: {
      type: "ConstantValue",
      ConstantValue: {
        value: 0
      },
      IntervalValue: {
        a: 0,
        b: 0
      },
      AnimationCurve: Ha
    }
  },
  rotationOverTime: {
    enable: !0,
    mode: "d1",
    d1: {
      type: "ConstantValue",
      ConstantValue: {
        value: 0
      },
      IntervalValue: {
        a: 0,
        b: 0
      },
      AnimationCurve: Ha
    }
  },
  frameOverTime: {
    enable: !1,
    mode: "d1",
    d1: {
      type: "ConstantValue",
      ConstantValue: {
        value: 0
      },
      IntervalValue: {
        a: 0,
        b: 0
      },
      AnimationCurve: Ha
    }
  }
};
class S6e extends ci {
  constructor(t, i) {
    super();
    S(this, "material", new kt({
      color: new _e(16777215)
    }));
    S(this, "startSphere");
    S(this, "endSphere");
    S(this, "sphereMaterial");
    const n = new Hs();
    this.sphereMaterial = new kt({
      color: new _e(1, 0, 0)
    }), this.startSphere = new Re(n, this.sphereMaterial), this.endSphere = new Re(n, this.sphereMaterial), this.startSphere.position.copy(t), this.endSphere.position.copy(i);
    const r = i.clone().sub(t).normalize(), a = 10, o = new $n(a, a), l = new _(0, 1, 0), c = l.clone().cross(r).normalize(), h = l.angleTo(r), u = new et().makeRotationAxis(c, h);
    o.applyMatrix4(u), new Re(
      o,
      new kt({ color: 65280, side: ui })
    ).position.copy(t);
    const f = new Y9(t, i), p = new kc(f, 20, 2, 8, !1), v = new Re(p, this.material);
    this.add(v), this.add(this.startSphere, this.endSphere);
  }
  updateData(t, i, n) {
    this.clear();
    const r = new Y9(t, i), a = new kc(r, 20, 0.5, 8, !1), o = new Re(a, this.material), l = i.clone().sub(t).normalize(), c = 1e3, h = new $n(c, c), u = new _(0, 0, 1), d = u.clone().cross(l).normalize(), f = u.angleTo(l), p = new et().makeRotationAxis(d, f);
    h.applyMatrix4(p);
    const v = new Re(
      h,
      new kt({ color: 16777215, transparent: !0, opacity: 0.1, side: ui })
    );
    v.position.copy(n), this.add(v);
    const g = new Ei(0, 1, 8);
    g.translate(0, -4, 0), g.rotateX(Math.PI / 2), g.applyMatrix4(p);
    const m = new Re(g);
    m.position.copy(i), this.add(m), this.add(o), this.startSphere.position.copy(t), this.endSphere.position.copy(i), this.add(this.startSphere, this.endSphere);
  }
}
var T6e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
let uM = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "meshes", []);
    S(this, "clipPlane", new ln());
    S(this, "inverseClipPlane", new ln());
    S(this, "clippedMeshes", []);
    S(this, "clippedPartMaterial", new kt({
      transparent: !0
    }));
    S(this, "clipHelper", new S6e(new _(), new _()));
    S(this, "helper", new ci());
    S(this, "world");
    S(this, "setClipData", (e) => {
      const t = JSON.parse(JSON.stringify(e)), i = this.states.curStateId, n = i && this.states.getStateConfigById(i);
      if (n)
        if (this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler)), s0(t.animation)) {
          const r = Go(t, n);
          this.states.updateStateConfig(i, r), this.updateRender(r);
        } else {
          const r = (t.animation.duration ?? 0) * 1e3, a = t.animation.mode ?? "Linear.None", o = Go(t, n);
          this.tweenHandler = jn.createTweenHandler({
            mode: a,
            duration: r,
            source: { clipPos: n.clipPos },
            target: { clipPos: o.clipPos },
            execute: ({ clipPos: l }) => {
              n.clipPos = l, this.states.updateStateConfig(i, n), this.updateRender(n);
            },
            complete: () => {
              this.states.updateStateConfig(i, o), this.updateRender(o), this.tweenHandler && (this.tweenHandler.stop(), Yi(this.tweenHandler));
            }
          });
        }
    });
    S(this, "enableClip", () => {
      const e = this.states.curStateId, t = e && this.states.getStateConfigById(e);
      if (t) {
        const i = Go(
          {
            enable: !0
          },
          t
        );
        this.states.updateStateConfig(e, i), this.updateRender(i);
      }
    });
    S(this, "disableClip", () => {
      const e = this.states.curStateId, t = e && this.states.getStateConfigById(e);
      if (t) {
        const i = Go(
          {
            enable: !1
          },
          t
        );
        this.states.updateStateConfig(e, i), this.updateRender(i);
      }
    });
    this.clipPlane = P6e(this.clipPlane, (n) => {
      this.inverseClipPlane.copy(n.clone().negate());
    }), this.helper.add(this.clipHelper), this.group.add(this.helper), Qe.mode === "Easyv" && this._initEasyvEventListener(), Qe.mode !== "Editor" && (this.helper.visible = !1);
  }
  intersectObject(e, t) {
    return e.intersectObjects([this.clipHelper.startSphere, this.clipHelper.endSphere]);
  }
  getObjectById(e) {
    return this.helper.getObjectByProperty("uuid", e);
  }
  getCanAttach() {
    return !0;
  }
  onDragEnd() {
    const e = this.states.stateList.get(this.getCurStateId());
    e && (e.config.startPoint.x = this.clipHelper.startSphere.position.x, e.config.startPoint.y = this.clipHelper.startSphere.position.y, e.config.startPoint.z = this.clipHelper.startSphere.position.z, e.config.endPoint.x = this.clipHelper.endSphere.position.x, e.config.endPoint.y = this.clipHelper.endSphere.position.y, e.config.endPoint.z = this.clipHelper.endSphere.position.z, Qe.ApiServer.updateState(this.entity.uuid, this.id, e), dt.Entity.refresh());
  }
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s("ClipModel", "receiveData", e, this.setClipData), _s("ClipModel", "enable", e, this.enableClip), _s("ClipModel", "disable", e, this.disableClip);
  }
  onWorldInitialized(e) {
    e.renderer.threeRenderer.localClippingEnabled = !0;
    const t = this.states.getCurrentStateConfig(), i = this.getNewMeshes(t);
    this.setClipMeshes(i), this.updateClipPlane(t), this.update(0);
  }
  async updateFromJson(e, t) {
    this.world = e, this.initStates(t.states), this.isInit = !0;
  }
  updateStateConfig(e, t, i) {
    const n = this.states.deepGet(e);
    if (n) {
      if (this.isMeshChanged([...n.config.meshes], [...t.meshes])) {
        const r = this.getNewMeshes(t);
        this.setClipMeshes(r);
      }
      super.updateStateConfig(e, t, i);
    }
  }
  setClipMeshes(e) {
    const t = new Set(this.clippedMeshes), i = new Set(e);
    i.forEach((n) => {
      t.has(n) || KD(this.clipPlane, n);
    }), t.forEach((n) => {
      i.has(n) || My(this.clipPlane, n);
    }), this.clippedMeshes = Array.from(i);
  }
  update(e) {
    const t = this.states.getCurrentStateConfig();
    this.updateRender(t);
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
  dispose() {
    this.clippedMeshes.forEach((e) => {
      My(this.clipPlane, e), this.clearDerivedMesh(e);
    });
  }
  clearDerivedMesh(e) {
    let t = e.children.find((i) => i.uuid === dM(e));
    t && (My(this.inverseClipPlane, t), (Array.isArray(t.material) ? t.material : [t.material]).every(
      (r) => !r.clippingPlanes || r.clippingPlanes.length === 0
    ) || e.remove(t));
  }
  updateRender(e) {
    this.updateClipPlane(e), this.updateClippedPart(e), e.enable ? (this.clippedMeshes.forEach((t) => {
      KD(this.clipPlane, t);
    }), this.clippedPartMaterial.visible = !0, this.clipHelper.visible = e.showHelper) : (this.clippedMeshes.forEach((t) => {
      My(this.clipPlane, t);
    }), this.clippedPartMaterial.visible = !1, this.clipHelper.visible = e.showHelper);
  }
  updateClippedPart(e) {
    e.enableClipMaterial ? this.showClippedPart() : this.hideClippedPart();
    const { color: t, alpha: i } = EU(e.clipMaterialColor.pure);
    this.clippedPartMaterial.color = t, this.clippedPartMaterial.opacity = i;
    const n = this.clipPlane.clone().negate();
    this.inverseClipPlane.copy(n);
  }
  showClippedPart() {
    this.clippedMeshes.forEach((e) => {
      const t = C6e(e, this.clippedPartMaterial);
      cz(this.inverseClipPlane, t.material), t.visible = !0;
    });
  }
  hideClippedPart() {
    this.clippedMeshes.forEach((e) => {
      const t = dM(e), i = e.children.find((n) => n.uuid === t);
      i && (i.visible = !1);
    });
  }
  updateClipPlane(e) {
    const t = e.startPoint, i = e.endPoint, n = e.clipPos, r = new _(t.x, t.y, t.z), a = new _(i.x, i.y, i.z), o = a.clone().sub(r), l = o.length(), c = o.normalize(), h = r.clone().addScaledVector(c, n * l);
    if (this.clipPlane.normal = c, this.clipPlane.constant = -h.dot(c), this.clipHelper.updateData(r, a, h), this.clipHelper.visible = e.showHelper, e.reverse) {
      const u = this.clipPlane.clone().negate();
      this.clipPlane.normal = u.normal, this.clipPlane.constant = u.constant;
    }
  }
  getNewMeshes(e) {
    const t = [];
    return e.meshes.forEach((i) => {
      const n = this.world.entityManager.getEntity(i.entityId);
      if (n) {
        const r = n.getComponentByType("Model");
        if (r)
          if (s0(i.meshId))
            r.group.traverse((a) => {
              a.material && t.push(a);
            });
          else {
            const a = r.group.getObjectByProperty("uuid", i.meshId);
            a && (a.material && t.push(a), a.children.length > 0 && a.traverse((o) => {
              o.material && t.push(o);
            }));
          }
      }
    }), t;
  }
  isMeshChanged(e, t) {
    if (e.length !== t.length)
      return !0;
    for (let i = 0; i < e.length; i++)
      if (e[i].entityId !== t[i].entityId || e[i].meshId !== void 0 && t[i].meshId !== void 0 && e[i].meshId !== t[i].meshId)
        return !0;
    return !1;
  }
};
uM = T6e([
  ni("ClipModel", { version: "2.2.0" })
], uM);
function dM(s) {
  return `derived-${s.uuid}`;
}
function C6e(s, e) {
  const t = dM(s);
  let i = s.children.find((n) => n.uuid === t);
  return i || (i = s.clone(), i.userData.skipSerialization = !0, i.uuid = t, i.material = Array.isArray(s.material) ? s.material.map(() => e) : e, i.position.set(0, 0, 0), i.rotation.set(0, 0, 0), i.scale.set(1, 1, 1), s.add(i)), i;
}
function KD(s, e) {
  const t = Array.isArray(e.material) ? e.material : [e.material];
  cz(s, t);
}
function My(s, e) {
  (Array.isArray(e.material) ? e.material : [e.material]).forEach((i) => {
    if (i.clippingPlanes && i.clippingPlanes.length > 0) {
      const n = i.clippingPlanes.findIndex((r) => r === s);
      n !== -1 && i.clippingPlanes.splice(n, 1);
    }
  });
}
function _6e(s, e) {
  e.clippingPlanes = e.clippingPlanes ?? [], e.clippingPlanes.includes(s) || e.clippingPlanes.push(s);
}
function cz(s, e) {
  (Array.isArray(e) ? e : [e]).forEach((i) => {
    _6e(s, i);
  });
}
function P6e(s, e) {
  return new Proxy(s, {
    set: function(t, i, n) {
      return t[i] = n, e == null || e(t), !0;
    }
  });
}
var L6e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
const y1 = "MiniMap";
let JD = class extends Fi {
  constructor(e, t, i) {
    super(e, t, i);
    S(this, "_curConfig", {
      enable: !1,
      mapConfig: {
        pointer: {
          color: {
            type: "pure",
            pure: "",
            pures: void 0,
            linear: void 0
          },
          size: {
            x: 50,
            y: 50
          },
          constrainedScale: !1
        },
        shapeConfig: {
          position: {
            x: 0,
            y: 0
          },
          size: {
            x: 0,
            y: 0
          },
          border: {
            enable: !1,
            thickness: 0,
            color: {
              type: "pure",
              pure: "",
              pures: void 0,
              linear: void 0
            },
            radius: 0
          }
        },
        shapeType: "rect"
      },
      projectConfig: {
        zoom: 1,
        position: {
          x: 0,
          y: 0
        },
        url: "",
        size: {
          x: 100,
          y: 100
        },
        angle: 0
      }
    });
    S(this, "_bindEntityIdMap", /* @__PURE__ */ new Map());
    S(this, "_projectPlane", new Re());
    S(this, "_projectObject", new mt());
    S(this, "_mapRender", new O6e(this, this._curConfig));
    S(this, "_mapTexture", new Ut());
    S(this, "_move", (e) => {
      this._mapRender.updatePosition(e.position, e.direction);
    });
    S(this, "_updateConfig", (e) => {
      const t = e.comp.states.getCurrentStateConfig();
      console.log(e.comp), this._mapRender.updatePosition(t.cameraConfig.position, t.cameraConfig.rotation);
    });
    this.id = e, this.name = t, this.entity = i, Qe.mode === "Easyv" && this._initEasyvEventListener();
  }
  static async create(e, t) {
    const i = {
      id: "",
      type: y1,
      name: (t == null ? void 0 : t.componentName) ?? "小地图",
      visible: !0,
      inActive: !0,
      version: "2.7.0",
      data: {},
      states: (t == null ? void 0 : t.states) ?? []
    }, [n, r] = await e.entityManager.addEntityAsync(
      {
        id: "",
        parentId: null,
        name: (t == null ? void 0 : t.entityName) ?? "小地图",
        type: y1,
        commonConfig: {
          version: "2.7.0"
        }
      },
      [i]
    ), [a] = r;
    return i.id = a.id, i.states.forEach((o, l) => {
      o.id = a.componentStateIds[l];
    }), await e.componentManager.parseComponentOptions(
      e,
      n,
      i,
      !0
    );
  }
  get projectPlane() {
    return this._projectPlane;
  }
  get mapTexture() {
    return this._mapTexture;
  }
  async _initProjectPlane(e) {
    this._projectPlane.scale.set(e.size.x, 1, e.size.y);
    const t = await st.loadTexture(e.url);
    t.colorSpace = qi, this._mapTexture.dispose(), this._mapTexture = t, this.mapTexture.repeat.set(1, 1), this._projectPlane.material = new kt({
      map: this._mapTexture,
      side: ui
    }), this._projectObject.position.setX(e.position.x), this._projectObject.position.setZ(e.position.y), this._projectObject.quaternion.setFromAxisAngle(new _(0, 1, 0), e.angle);
  }
  onDragEnd() {
    const e = this.states.getCurrentStateConfig();
    e.projectConfig.position.x = this._projectObject.position.x, e.projectConfig.position.y = this._projectObject.position.z;
    const t = (i) => {
      const n = Math.PI * 2, r = i % n;
      return r > Math.PI ? r - n : r;
    };
    e.projectConfig.angle += this.entity.world.selection.transformControls.rotationAngle, e.projectConfig.angle = t(e.projectConfig.angle), this.states.needsUpdateStateSet.add(this.getCurStateId()), this.entity.world.selection.transformControls.rotationAngle = 0, dt.Entity.refresh();
  }
  // 2 在组件中实现easyv 事件的监听函数
  _initEasyvEventListener() {
    const e = this.entity.uuid;
    _s(
      y1,
      // 组件的类型
      "start",
      // action 中的value
      e,
      // 组件对应的实体id
      () => this.setVisible(!0)
      // 对应事件的执行函数
    ), _s(
      y1,
      // 组件的类型
      "end",
      // action 中的value
      e,
      // 组件对应的实体id
      () => this.setVisible(!1)
      // 对应事件的执行函数
    );
  }
  async updateFromJson(e, t) {
    const { states: i, rels: n } = t, r = n == null ? void 0 : n.filter((o) => o.componentId === this.id);
    r == null || r.forEach((o) => {
      const { stateId: l, instanceId: c } = o, h = i.findIndex((u) => u.id === l);
      h !== -1 && (i[h].config.projectConfig.bindEntityId = c);
    }), this.initStates(i), this.states.stateList.forEach((o) => {
      o.config.projectConfig.bindEntityId && this._bindEntityIdMap.set(o.id, o.config.projectConfig.bindEntityId);
    });
    const a = this.states.getCurrentStateConfig();
    Object.assign(this._curConfig, a), this._projectPlane.geometry = new $n(1, 1), this._projectPlane.geometry.rotateX(Math.PI / 2), await this._initProjectPlane(this._curConfig.projectConfig), this._mapRender.updateConfig(this._curConfig.mapConfig), e.renderer.event.addEventListener("afterRender", () => {
      this._mapRender.render();
    }), this.group.add(this._projectObject), this.update(), this.isInit = !0, e.event.addEventListener("initComplete", () => {
      this._curConfig.projectConfig.bindEntityId && this.bindMoveComponent(this._curConfig.projectConfig.bindEntityId);
    });
  }
  // 实体的id
  async bindMoveComponent(e) {
    const t = this.entity.world.entityManager.getEntity(e);
    if (t) {
      const i = t.getComponentByType("CameraFirstPerson");
      i && (i.addEventListener("move", this._move), this._updateConfig({ comp: i }), i.addEventListener("update", this._updateConfig));
    } else
      console.warn(`找不到对应的实体:${e}`);
  }
  async unBindMoveComponent(e) {
    const t = this.entity.world.entityManager.getEntity(e);
    if (t) {
      const i = t.getComponentByType("CameraFirstPerson");
      i == null || i.removeEventListener("move", this._move), i == null || i.removeEventListener("update", this._updateConfig), this._mapRender.updateConfig(this._curConfig.mapConfig), console.log("un bind");
    } else
      console.warn(`找不到对应的实体:${e}`);
  }
  async _handleComponentRelOnServe() {
    const e = this._bindEntityIdMap.get(this.getCurStateId());
    if (e !== this._curConfig.projectConfig.bindEntityId)
      if (e && this._curConfig.projectConfig.bindEntityId)
        await Qe.ApiServer.updateComponentRel(
          this.entity.uuid,
          this.id,
          this.getCurStateId(),
          e,
          this._curConfig.projectConfig.bindEntityId
        ), this._bindEntityIdMap.set(this.getCurStateId(), this._curConfig.projectConfig.bindEntityId);
      else {
        const t = this.states.getCurrentStateConfig();
        t.projectConfig.bindEntityId && (this._bindEntityIdMap.set(this.getCurStateId(), t.projectConfig.bindEntityId), await Qe.ApiServer.addComponentRel(
          this.entity.uuid,
          this.id,
          this.getCurStateId(),
          [t.projectConfig.bindEntityId],
          this.type
        ));
      }
  }
  update() {
    const e = this.states.getCurrentStateConfig();
    this._curConfig.projectConfig.bindEntityId && this.unBindMoveComponent(this._curConfig.projectConfig.bindEntityId), Object.assign(this._curConfig, e), e.projectConfig.bindEntityId && this.bindMoveComponent(e.projectConfig.bindEntityId), this.group.visible && this._mapRender.setVisible(e.enable), this._initProjectPlane(e.projectConfig).then(() => {
      this._mapRender.updateConfig(this._curConfig.mapConfig);
    }), this._handleComponentRelOnServe();
  }
  setVisible(e, t) {
    this._mapRender.setVisible(e), super.setVisible(e, t);
  }
  getObjectById() {
    return this._projectObject;
  }
  startAdjustMiniMapPositionAndSize(e) {
    const { drawUtils: t, selection: i } = this.entity.world;
    this._projectObject.add(this._projectPlane), i.transformControls.setSpace("world"), t.mode = "multi-rect", t.setActiveComponent(this), t.showDrawPoint(this.group), t.showDrawMoveRect(this.group), t.editMode = bn.Move, t.startDraw(), t.disableType("add-points");
    const n = (p, v, g) => [
      new _(-p, 0, v),
      new _(-p, 0, -v),
      new _(p, 0, v),
      new _(p, 0, -v)
    ], r = this._projectPlane.scale.x, a = this._projectPlane.scale.z;
    t.executeDrawCommand("add-points", {
      root: this._projectObject,
      points: n(r / 2, a / 2, this._projectPlane),
      drawId: this._projectPlane.uuid
    }), t.enableType("add-points");
    let o = 1, l = 1;
    const c = (p) => {
      if (parent && p.point.userData.drawId === this._projectPlane.uuid) {
        const v = p.point.position.x, g = p.point.position.z, m = [
          new _(-v, 0, g),
          new _(-v, 0, -g),
          // new THREE.Vector3(x, 0, z),
          new _(v, 0, -g)
        ];
        p.points.forEach((A, b) => {
          if (A.sprite.uuid !== p.point.uuid) {
            const M = m.pop();
            M && A.sprite.position.copy(M);
          }
        });
        const x = v * o, y = g * l;
        this._curConfig.projectConfig.size = { x: x * 2, y: y * 2 }, this._projectPlane.scale.set(x * 2, 1, y * 2);
      }
    }, h = (p) => {
      this._projectPlane.scale.x * p.point.position.x < 0 && (o = -1), this._projectPlane.scale.z * p.point.position.z < 0 && (l = -1);
    }, u = () => {
      o = 1, l = 1;
    }, d = () => {
      e && e(), t.removeEventListener("end-draw", d), t.removeEventListener("drag", c), t.removeEventListener("start-drag", h), t.removeEventListener("end-drag", u), document.removeEventListener("keydown", f);
      const p = this.states.getCurrentStateConfig();
      Object.assign(p, this._curConfig), this.states.needsUpdateStateSet.add(this.getCurStateId()), this._projectObject.remove(this._projectPlane);
    }, f = (p) => {
      this.entity.world.drawUtils.state === to.StartDraw && (p.key === "r" ? this.entity.world.selection.transformControls.setMode("rotate") : p.key === "m" && this.entity.world.selection.transformControls.setMode("translate"));
    };
    document.addEventListener("keydown", f), t.addEventListener("end-drag", u), t.addEventListener("start-drag", h), t.addEventListener("drag", c), t.addEventListener("end-draw", d);
  }
  dispose() {
    this.mapTexture.dispose(), this._mapRender.dispose();
  }
  toJson() {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      version: this.version,
      // attribute: this.customAttributes,
      visible: this.entity.visible,
      inActive: this.entity.inActive,
      states: this.states.toJson(),
      data: {}
    };
  }
};
JD = L6e([
  ni(y1, { version: "2.7.0" })
], JD);
const I6e = new _e();
new _();
class O6e {
  constructor(e, t) {
    S(this, "_viewportStart", { x: 0, y: 0 });
    S(this, "_camera", new Xn(-1, 1, 1, -1, 1, 500));
    S(this, "_root", new Da());
    S(this, "_mapPlane", new Re());
    S(this, "_renderer", new SU({
      alpha: !0,
      antialias: !0
    }));
    S(this, "_lastPosition", new Se());
    // private _pointerObject: THREE.Object3D = new THREE.Object3D();
    S(this, "_pointElement", document.createElement("canvas"));
    S(this, "updatePosition", (e, t) => {
      const { size: i, angle: n } = this._config.projectConfig, r = this._mapPlane.scale.x / i.x, a = this._mapPlane.scale.y / i.y, o = new _(e.x * r, 0, e.z * a);
      o.applyAxisAngle(new _(0, 1, 0), n), this._camera.position.setX(o.x), this._camera.position.setY(o.z);
      const l = nt.radToDeg(-t.z);
      this._pointElement.style.transform = `rotate(${l}deg)`;
    });
    var r;
    this._miniMapComponent = e, this._config = t, this._renderer.toneMapping = Hp, this._renderer.shadowMap.enabled = !0, this._renderer.shadowMap.type = $g, this._renderer.localClippingEnabled = !0;
    const { renderer: i } = this._miniMapComponent.entity.world;
    (r = i.threeRenderer.domElement.parentElement) == null || r.appendChild(this._renderer.domElement), this._miniMapComponent._projectObject, "size" in this._config.mapConfig.shapeConfig && (this._initMapPlane(this._config.mapConfig.shapeConfig), this._updateCanvas(this._config.mapConfig.shapeConfig), this._initPointer(
      this._config.projectConfig.size,
      this._config.mapConfig.pointer.size,
      this._config.mapConfig.pointer.color
    )), this._initCamera(this._config.projectConfig.zoom);
    const n = this._mapPlane.position;
    this._camera.position.copy(n), this._initMapBackground();
  }
  _initMapPlane(e) {
    var n, r;
    this._mapPlane.geometry = new $n(1, 1);
    const { scale: t } = this._miniMapComponent.projectPlane, { position: i } = this._miniMapComponent._projectObject;
    if (this._mapPlane.scale.set(t.z, t.x, 1), this._mapPlane.position.copy(i), this._mapPlane.material.map = this._miniMapComponent.mapTexture, (r = (n = this._miniMapComponent.mapTexture) == null ? void 0 : n.image) != null && r.width) {
      const { width: a, height: o } = this._miniMapComponent.mapTexture.image;
      this._mapPlane.scale.set(a, o, 1), this._initCamera(this._config.projectConfig.zoom);
    }
    this._mapPlane.material.needsUpdate = !0, this._root.add(this._mapPlane);
  }
  _initPointer(e, t, i) {
    var a;
    const n = this._pointElement;
    n.style.position = "absolute", n.style.left = `${this._viewportStart.x + (e.x - t.x) / 2}px`, n.style.top = `${this._viewportStart.y + (e.y - t.y) / 2}px`, n.width = t.x, n.height = t.y, n.style.transformOrigin = "center center";
    const r = n.getContext("2d");
    if (r) {
      const o = i.pure;
      r.strokeStyle = o, r.beginPath(), r.moveTo(n.width / 2, 0), r.lineTo(n.width, n.height), r.lineTo(n.width / 2, n.height - n.width * 0.268), r.lineTo(0, n.height), r.closePath(), r.strokeStyle = o, r.stroke(), r.fillStyle = o, r.fill();
    }
    (a = this._renderer.domElement.parentElement) == null || a.appendChild(this._pointElement);
  }
  _updateCanvas(e) {
    const t = e.border.thickness, i = this._renderer.domElement;
    i.style.position = "absolute", i.style.left = `${this._viewportStart.x}px`, i.style.top = `${this._viewportStart.y}px`, i.width = e.size.x, i.height = e.size.y, e.border.enable ? (i.style.border = `${t}px solid`, i.style.borderRadius = `${e.border.radius}px`, i.style.borderColor = `${e.border.color.pure}`) : i.style.border = "none", this._renderer.setSize(e.size.x, e.size.y);
  }
  _setScissorForElement(e) {
    const i = this._renderer.domElement.getBoundingClientRect(), n = e.getBoundingClientRect(), r = Math.min(n.right, i.right) - i.left, a = Math.max(0, n.left - i.left), o = Math.min(n.bottom, i.bottom) - i.top, l = Math.max(0, n.top - i.top), c = Math.min(i.width, r - a), h = Math.min(i.height, o - l), u = i.height - o;
    return this._renderer.setScissor(a, u, c, h), this._renderer.setViewport(a, u, c, h), c / h;
  }
  _initCamera(e) {
    const t = new _(
      this._mapPlane.scale.x,
      this._mapPlane.scale.y,
      this._mapPlane.scale.z
    ), i = e, n = this._camera, r = this._setScissorForElement(this._renderer.domElement);
    n.left = -r, n.right = r;
    const a = r * 2, o = n.top - n.bottom, l = Math.min(a / i / t.x, o / i / t.y);
    this._camera.zoom = l, this._camera.updateProjectionMatrix(), console.log("center");
  }
  _initMapBackground() {
    const t = new Uint8Array(1048576), i = 20, n = -45, r = (o, l, c) => Math.floor((o * Math.cos(c) + l * Math.sin(c)) / i) % 2 === 0 ? 20 : 10;
    for (let o = 0; o < 512; o++)
      for (let l = 0; l < 512; l++) {
        const c = (o * 512 + l) * 4, h = r(l, o, n), u = r(l + 1, o, n), d = r(l, o + 1, n);
        let f = h;
        h !== u && (f = Math.round((h + u) / 2)), h !== d && (f = Math.round((h + u) / 2)), t[c] = f, t[c + 1] = f, t[c + 2] = f, t[c + 3] = 255;
      }
    const a = new Qn(t, 512, 512);
    a.wrapS = un, a.wrapT = un, a.needsUpdate = !0, this._root.background = a;
  }
  updateConfig(e) {
    Object.assign(this._config, e);
    const { shapeConfig: t } = e;
    "size" in t && (this._viewportStart.x = this._config.mapConfig.shapeConfig.position.x - t.size.x / 2, this._viewportStart.y = this._config.mapConfig.shapeConfig.position.y - t.size.y / 2, this._initMapPlane(t), this._updateCanvas(t), this._initPointer(
      t.size,
      this._config.mapConfig.pointer.size ?? { x: 50, y: 50 },
      this._config.mapConfig.pointer.color
    )), this._initCamera(this._config.projectConfig.zoom);
  }
  setVisible(e) {
    e ? (this._renderer.domElement.style.display = "block", this._pointElement.style.display = "block") : (this._renderer.domElement.style.display = "none", this._pointElement.style.display = "none");
  }
  render() {
    this._renderer.render(this._root, this._camera), this._renderer.setClearColor(I6e);
  }
  dispose() {
    this._renderer.domElement.remove(), this._renderer.dispose(), this._pointElement.remove();
  }
}
function N6e(s, e, t = !0) {
  if (!s.entity.inActive && (s.group.position.copy(e), t)) {
    const i = s.getCurStateId(), n = s.states.getCurrentStateConfig();
    n.transform.position.x = e.x, n.transform.position.y = e.y, n.transform.position.z = e.z, s.updateStateConfig(i, n);
  }
}
function B6e(s, e, t = !0) {
  if (!s.entity.inActive && (s.group.position.copy(e), t)) {
    const i = s.getCurStateId(), n = s.states.getCurrentStateConfig();
    n.transform.position.x = e.x, n.transform.position.y = e.y, n.transform.position.z = e.z, s.updateStateConfig(i, n);
  }
}
function F6e(s, e, t, i = !0) {
  if (!s.entity.inActive) {
    const n = t.clone().sub(s.group.position);
    e.position.copy(n), i && (s.needDataUpdateServer = !0);
  }
}
function df(s, e, t = !0) {
  const { object3d: i, component: n } = s;
  if (n && i)
    switch (n.type) {
      case "BatchModel":
        B6e(
          n,
          e,
          t
        );
        break;
      case "Model":
        N6e(n, e, t);
        break;
      case "Point":
      case "PoiPanel":
      case "FlowFence":
        F6e(
          n,
          i,
          e,
          t
        );
        break;
    }
}
class U6e {
  constructor(e) {
    S(this, "root", new ci());
    S(this, "box");
    S(this, "boxHelper");
    S(this, "selectedInfos", []);
    this.box = new zt(), this.box.expandByPoint(new _()), this.boxHelper = new L4(this.box, new _e(16777215)), this.boxHelper.visible = !1, this.root.name = "AlignmentTool_root", this.root.add(this.boxHelper);
    const { rootScene: t } = e.renderer;
    t.add(this.boxHelper), e.getSelectComponent().addEventListener("select", ({ selectItems: i }) => {
      const n = i.filter((r) => r.componentType !== "Iframe").map((r) => {
        const a = e.findComponentById(r.entityId, r.componentId);
        let o = a == null ? void 0 : a.getObjectById(r.objId);
        const { ...l } = r, c = new _();
        return (a == null ? void 0 : a.type) === "Model" || (a == null ? void 0 : a.type) === "BatchModel" ? (c.copy(a.group.position), o = a.group) : o && o.getWorldPosition(c), {
          component: a,
          object3d: o,
          oldPosition: c,
          info: l
        };
      });
      this.box.makeEmpty(), n.forEach((r) => {
        const { component: a, object3d: o } = r;
        a && (a.type === "Model" || a.type === "BatchModel") ? this.box.expandByObject(a.group) : o && this.box.expandByObject(o);
      }), this.boxHelper.updateMatrixWorld(), this.selectedInfos = n;
    });
  }
  alignment(e, t = !0) {
    const { axis: i, t: n } = e, { min: r, max: a } = this.box;
    switch (i) {
      case Zh.X: {
        const o = r.x + (a.x - r.x) * n;
        this.selectedInfos.forEach((l) => {
          const { y: c, z: h } = l.oldPosition;
          df(l, new _(o, c, h), t);
        });
        break;
      }
      case Zh.Y: {
        const o = r.y + (a.y - r.y) * n;
        this.selectedInfos.forEach((l) => {
          const { x: c, z: h } = l.oldPosition;
          df(l, new _(c, o, h), t);
        });
        break;
      }
      case Zh.Z: {
        const o = r.z + (a.z - r.z) * n;
        this.selectedInfos.forEach((l) => {
          const { x: c, y: h } = l.oldPosition;
          df(l, new _(c, h, o), t);
        });
        break;
      }
    }
  }
  distribute(e, t = !0) {
    const { axis: i } = e, { min: n, max: r } = this.box;
    switch (i) {
      case Zh.X: {
        const a = this.selectedInfos.sort((c, h) => c.oldPosition.x - h.oldPosition.x), o = a.length, l = [];
        for (let c = 0; c < o; ++c) {
          const h = c / (o - 1), u = n.x + (r.x - n.x) * h, { y: d, z: f } = a[c].oldPosition;
          l.push(new _(u, d, f));
        }
        a.forEach((c, h) => {
          df(c, l[h], t);
        });
        break;
      }
      case Zh.Y: {
        const a = this.selectedInfos.sort((c, h) => c.oldPosition.y - h.oldPosition.y), o = a.length, l = [];
        for (let c = 0; c < o; ++c) {
          const h = c / (o - 1), u = n.y + (r.y - n.y) * h, { x: d, z: f } = a[c].oldPosition;
          l.push(new _(d, u, f));
        }
        a.forEach((c, h) => {
          df(c, l[h], t);
        });
        break;
      }
      case Zh.Z: {
        const a = this.selectedInfos.sort((c, h) => c.oldPosition.z - h.oldPosition.z), o = a.length, l = [];
        for (let c = 0; c < o; ++c) {
          const h = c / (o - 1), u = n.z + (r.z - n.z) * h, { x: d, y: f } = a[c].oldPosition;
          l.push(new _(d, f, u));
        }
        a.forEach((c, h) => {
          df(c, l[h], t);
        });
        break;
      }
    }
  }
  start() {
  }
  end() {
    this.selectedInfos.forEach((e) => {
      const { oldPosition: t, object3d: i } = e;
      i && i.position.copy(t);
    });
  }
  dispose() {
    this.root.parent && this.root.parent.remove(this.root);
  }
}
class z6e {
  constructor() {
    S(this, "_frameTimes", []);
    S(this, "_fps", 0);
    S(this, "_averageFps", 0);
    S(this, "numFramesToTrack", 60);
  }
  get fps() {
    return this._fps;
  }
  get averageFps() {
    return this._averageFps;
  }
  update(e) {
    if (e > 0) {
      const t = 1 / e * 1e3;
      this._fps = t, this._frameTimes.push(t);
    }
    this._frameTimes.length > this.numFramesToTrack && this._frameTimes.shift(), this._averageFps = this.calculateAverageFps();
  }
  calculateAverageFps() {
    return this._frameTimes.length === 0 ? 0 : this._frameTimes.reduce((t, i) => t + i, 0) / this._frameTimes.length;
  }
}
var k6e = Object.defineProperty, G6e = Object.getOwnPropertyDescriptor, H6e = (s, e, t, i) => {
  for (var n = G6e(e, t) , r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(e, t, n) ) || n);
  return n && k6e(e, t, n), n;
}, Jo;
const hz = (Jo = class extends ed {
  constructor(t) {
    super({ id: t.containerId, type: "EngineGroup", components: [] });
    S(this, "_containerId");
    S(this, "isActive", !1);
    S(this, "renderer", new qg({
      id: `${this.id}_renderer`,
      type: "EngineGroup"
    }));
    S(this, "clock", new Ju());
    // public readonly tickBindThis = this.tick.bind(this);
    S(this, "entityManager");
    S(this, "roma");
    S(this, "componentManager", new Hge());
    S(this, "selection");
    S(this, "drawUtils");
    S(this, "emitFunc");
    // public readonly physicalService!: EasyTwinPhysicService;
    S(this, "_lastFrame", 0);
    S(this, "_batchModelDrawHelper");
    S(this, "alignmentTool");
    S(this, "event", new es());
    S(this, "worldInfo", {
      fps: 0,
      averageFps: 0,
      modelCount: 0,
      triangleCount: 0,
      textureCount: 0,
      featureCount: 0,
      programCount: 0,
      materialCount: 0
    });
    S(this, "fpsTool", new z6e());
    S(this, "_animateFrameId", 0);
    S(this, "THREE", _I);
    S(this, "restoreRender", () => {
      this.renderer.restoreRender(this.containerId);
    });
    this._containerId = t.containerId, this.renderer.append(this._containerId), this._handleViewControl(this.renderer.threeRenderer.domElement), this.drawUtils = new Dhe(
      this,
      this.renderer.threeRenderer.domElement,
      this.renderer.mainCamera
    ), this.selection = this._addSelection(), this._handleEvent(), "sceneId" in t && (this._addInfiniteGridComponent(), this.renderer.viewHelper.visible = !0), this.tick(this._lastFrame), this.renderer.rootScene.add(this), this.entityManager = new Vge(this), this.renderer.threeRenderer.domElement.addEventListener("webglcontextlost", this.restoreRender), this._batchModelDrawHelper = new nM(this), this.alignmentTool = new U6e(this), this._handleSpriteScaleUpdate(), this.roma = new qge(this.selection, this.renderer);
  }
  get containerId() {
    return this._containerId;
  }
  _handleSpriteScaleUpdate() {
    this.renderer.cameraControls.addEventListener("control", () => {
      this._updateSpriteScale();
    });
  }
  _updateSpriteScale() {
    this.entityManager.entityMap.forEach((t) => {
      t.componentMap.forEach((i) => {
        "updateSpriteScale" in i && i instanceof u8 && this.renderer.mainCamera instanceof Zt && i.updateSpriteScale(this.renderer.cameraControls);
      });
    });
  }
  _handleEvent() {
    this.drawUtils.addEventListener("start-drag", () => {
      this.renderer.cameraControls.enabled = !1;
    }), this.drawUtils.addEventListener("end-drag", () => {
      this.renderer.cameraControls.enabled = !0;
    }), this.renderer.event.addEventListener("toggleCamera", () => {
      this.drawUtils.camera = this.renderer.mainCamera;
      const { transformControls: n } = this.selection;
      n && (n.camera = this.renderer.mainCamera);
    });
    const t = this._addKeyboardRomaComponent();
    this.selection.addEventListener("select", ({ objs: n, selectItems: r }) => {
      n.length > 0 ? t.enable = !1 : t.enable = !0, dt.Select.setState(r), r.forEach((a) => {
        const o = {
          entityId: a.entityId,
          componentId: a.componentId,
          objId: a.objId,
          customAttribute: a.customAttribute,
          id: a.id,
          name: a.name,
          position: a.position,
          sceneGraph: a.sceneGraph,
          meshName: a.meshName
        };
        this.emitFunc && this.emitFunc("click", o, a.entityId);
        let l = this.entityManager.getEntity(a.entityId);
        for (; l !== void 0; )
          l.parentId ? l = this.entityManager.getEntity(l.parentId) : l = void 0, l && l.hasComponent("EngineGroup") && this.emitFunc && this.emitFunc("click", o, l.uuid);
      });
    });
    const i = this._addDrawComponent();
    i.addEventListener(bc.Start, () => {
      this.selection.enable = !1, this.selection.transformControls && (this.selection.transformControls.visible = !1);
    }), i.addEventListener(bc.End, () => {
      this.selection.enable = !0, this.selection.transformControls && (this.selection.transformControls.visible = !0);
    });
  }
  _handleViewControl(t) {
    const { viewHelper: i } = this.renderer;
    i.event.addEventListener("toggleViewEnd", () => {
      if (this.renderer.mainCamera instanceof Zt) {
        this.renderer.toggleCamera();
        const n = () => {
          this.renderer.cameraControls.state === 1 && this.renderer.mainCamera instanceof Xn && (this.renderer.toggleCamera(), this.renderer.cameraControls.removeEventListener("control", n));
        };
        this.renderer.cameraControls.addEventListener("control", n);
      }
    });
  }
  _addKeyboardRomaComponent() {
    const t = Za(this.uuid, "KeyboardRoma"), i = this.addComponent(
      t,
      "KeyboardRoma",
      a7,
      !0
    );
    return i.initialize(), i.isInit = !0, i;
  }
  _addSelection() {
    return new io(
      this,
      this.renderer.mainCamera,
      this.renderer.threeRenderer.domElement
    ).initialize();
  }
  _addInfiniteGridComponent() {
    const t = Za(this.containerId, "InfiniteGrid");
    this.componentManager.addInfiniteGridComponent(t, this);
  }
  _addDrawComponent() {
    const t = Za(this.containerId, "Draw");
    return this.componentManager.addDrawComponent(
      t,
      this.renderer.threeRenderer.domElement,
      this
    );
  }
  getCameraOptions() {
    const t = this.renderer.activeCamera ?? this.renderer.cameraControls.camera, i = t.rotation, n = "getFocalLength" in t ? t.getFocalLength() : 12, r = new _().setFromMatrixPosition(t.matrixWorld), a = this.renderer.cameraControls.getTarget(new _()), o = r.clone().sub(a).length();
    return {
      position: { x: r.x, y: r.y, z: r.z },
      target: { x: a.x, y: a.y, z: a.z },
      rotation: { x: i.x, y: i.y, z: i.z },
      targetOffset: o,
      focalLength: n
    };
  }
  getGridUnit() {
    const t = this.getComponentByType("InfiniteGrid");
    return t ? t.getUnit() : 10;
  }
  setTransformControlsMode(t) {
    var n;
    const i = this.getSelectComponent();
    (n = i == null ? void 0 : i.transformControls) == null || n.setMode(t);
  }
  getSceneId() {
    return this.renderer.rootScene.uuid;
  }
  setEntityNameById(t, i) {
    return this.entityManager.setEntityName(t, i);
  }
  setFourViewMode() {
    this.renderer.setViewMode(gu.FourView);
  }
  setNormalViewMode() {
    this.renderer.setViewMode(gu.Normal);
  }
  startDrawFlowRoute(t, i) {
    const n = nt.generateUUID(), a = this.entityManager.addEntity({
      id: n,
      parentId: i,
      name: t,
      type: "FlowRoute",
      commonConfig: {
        version: "2.0.0"
      }
    }).addComponent(n, "路径", t4), o = () => {
      a.removeEventListener(_i.Add, o), a.draw(this.drawUtils);
    };
    return a.addEventListener(_i.Add, o), a.isInit = !0, a;
  }
  startDrawPoiPanelGroup(t, i, n) {
    const r = nt.generateUUID(), o = this.entityManager.addEntity({
      id: r,
      parentId: n,
      name: t,
      type: "PoiPanel"
    }).addComponent(
      r,
      Nx.getName(),
      Nx
    ), l = () => {
      o.removeEventListener(_i.Add, l), o.draw(this.drawUtils, i);
    };
    return o.addEventListener(_i.Add, l), o.isInit = !0, o;
  }
  async startDrawPoiPanelV2Async(t, i, n) {
    const r = nt.generateUUID(), [a, o] = await this.entityManager.addEntityAsync(
      {
        id: r,
        parentId: n,
        name: t,
        type: "PoiPanelV2",
        commonConfig: { version: "2.0.0" }
      },
      [
        {
          id: "PoiPanelV2",
          type: "PoiPanelV2",
          name: "PoiPanelV2",
          visible: !0,
          version: "1.7.0",
          inActive: !1,
          data: {
            children: [
              {
                id: "0",
                name: "child 1",
                position: { x: 0, y: 0, z: 0 },
                customAttribute: { 位置: "1" }
              }
            ],
            groupPosition: { x: 0, y: 0, z: 0 },
            scaleOptions: i
          },
          states: []
        }
      ]
    ), l = a.addComponent(
      o[0].id ?? r,
      "信息面板",
      Fx,
      !0
    );
    return l.dispatchEvent(_i.Add, {
      id: "",
      type: "PoiPanel",
      name: "",
      visible: !1,
      inActive: !1,
      version: l.version,
      data: {},
      states: []
    }), l.isInit = !0, l;
  }
  async startDrawClusteringHeatmapAsync(t, i, n, r, a) {
    const o = nt.generateUUID(), [l, c] = await this.entityManager.addEntityAsync(
      {
        id: o,
        parentId: a,
        name: t,
        type: "ClusteringHeatmap",
        commonConfig: {
          version: "2.0.0"
        }
      },
      [
        {
          id: "",
          type: "ClusteringHeatmap",
          name: i,
          visible: !0,
          inActive: !0,
          version: "1.7.0",
          data: n,
          states: r
        }
      ]
    ), h = l.addComponent(
      c[0].id ?? o,
      "聚合热力",
      $x,
      !0
    ), [u] = c, d = [];
    return r.forEach((f, p) => {
      d.push({
        id: u.componentStateIds[p],
        name: f.name,
        rank: f.rank,
        createdAt: "",
        config: f.config
      });
    }), h.initStates(d), h.dispatchEvent(_i.Add, {
      id: c[0].id ?? o,
      type: "ClusteringHeatmap",
      name: i,
      visible: !1,
      inActive: !1,
      version: h.version,
      data: {},
      states: []
    }), h.isInit = !0, h;
  }
  async startDrawAreaHeatmapComponentAsync(t, i, n, r, a) {
    const o = nt.generateUUID(), [l, c] = await this.entityManager.addEntityAsync(
      {
        id: o,
        parentId: a,
        name: t,
        type: "AreaHeatmap",
        commonConfig: {
          version: "2.0.0"
        }
      },
      [
        {
          id: "",
          type: "AreaHeatmap",
          name: i,
          visible: !0,
          inActive: !0,
          version: "1.7.0",
          data: n,
          states: r
        }
      ]
    ), [h] = c, u = l.addComponent(
      h.id ?? o,
      "区域热力",
      aM,
      !0
    ), d = [];
    return r.forEach((f, p) => {
      d.push({
        id: h.componentStateIds[p],
        name: f.name,
        rank: f.rank,
        createdAt: "",
        config: f.config
      });
    }), u.initStates(d), u.dispatchEvent(_i.Add, {
      id: c[0].id ?? o,
      type: "ClusteringHeatmap",
      name: i,
      visible: !1,
      inActive: !1,
      version: u.version,
      data: {},
      states: []
    }), u.isInit = !0, u;
  }
  async startDrawWaterComponentAsync(t, i, n, r, a) {
    const o = nt.generateUUID(), [l, c] = await this.entityManager.addEntityAsync(
      {
        id: o,
        parentId: a,
        name: t,
        type: "Water",
        commonConfig: {
          version: "2.0.0"
        }
      },
      [
        {
          id: "",
          type: "Water",
          name: i,
          visible: !0,
          inActive: !0,
          version: "1.7.0",
          data: n,
          states: r
        }
      ]
    ), [h] = c, u = l.addComponent(
      h.id ?? o,
      "水面",
      cM,
      !0
    ), d = [];
    return r.forEach((f, p) => {
      d.push({
        id: h.componentStateIds[p],
        name: f.name,
        rank: f.rank,
        createdAt: "",
        config: f.config
      });
    }), u.initStates(d), u.dispatchEvent(_i.Add, {
      id: c[0].id ?? o,
      type: "Water",
      name: i,
      visible: !1,
      inActive: !1,
      version: u.version,
      data: {},
      states: []
    }), u.isInit = !0, u;
  }
  async startDrawInstanceModelComponentAsync(t, i, n, r, a) {
    const o = nt.generateUUID(), [l, c] = await this.entityManager.addEntityAsync(
      {
        id: o,
        parentId: a,
        name: t,
        type: "InstanceModel",
        commonConfig: {
          version: "2.8.0"
        }
      },
      [
        {
          id: "",
          type: "InstanceModel",
          name: i,
          visible: !0,
          inActive: !0,
          version: "1.7.0",
          data: n,
          states: []
        }
      ]
    ), h = await l7(n.asset.url);
    await c7(h, c[0].id), r == null || r(0.9, Jh.loading);
    const u = l.addComponent(
      c[0].id ?? o,
      "实例模型",
      Jg,
      !0
    );
    return u && (u.modelRenderResource = h, u.addObject2RendererImplement(u.modelRenderResource.scene), u.initDataOptions = u.toJson().data, u.dispatchEvent(_i.Add, {
      id: "",
      type: "InstanceModel",
      name: "",
      visible: !1,
      inActive: !1,
      version: u.version,
      data: {},
      states: []
    }), u.isInit = !0, this.entityManager.updateWorldListStore()), u;
  }
  async startDrawFloorComponentAsync(t, i, n, r) {
    const a = nt.generateUUID(), o = {
      id: "",
      name: "状态",
      rank: 0,
      createdAt: "",
      config: n
    }, [l, c] = await this.entityManager.addEntityAsync(
      {
        id: a,
        parentId: r,
        name: t,
        type: "Floor",
        commonConfig: {
          version: "2.0.0"
        }
      },
      [
        {
          id: "",
          type: "Floor",
          name: i,
          visible: !0,
          inActive: !0,
          version: "1.7.0",
          data: {
            children: [],
            groupPosition: { x: 0, y: 0, z: 0 }
          },
          states: [o]
        }
      ]
    ), h = l.addComponent(
      c[0].id ?? a,
      i,
      oM,
      !0
    );
    return c[0] && ([o.id] = c[0].componentStateIds), h.initStates([o]), h.dispatchEvent(_i.Add, h.toJson()), h.isInit = !0, h;
  }
  startDrawPointGroup(t, i, n) {
    const r = nt.generateUUID(), o = this.entityManager.addEntity({
      id: r,
      parentId: n,
      name: t,
      type: "Point",
      commonConfig: {
        version: "2.0.0"
      }
    }).addComponent(
      r,
      Kg.getName(),
      Kg
    ), l = () => {
      o.removeEventListener(_i.Add, l), o.setScaleOptions(i), o.draw(this.drawUtils);
    };
    return o.addEventListener(_i.Add, l), o.isInit = !0, o;
  }
  async startDrawVideoProject(t) {
    let i = 0;
    for (const a of this.entityManager.entityMap)
      if (a[1].eType === "VideoProject" && (i++, i > 4))
        return {
          success: !0,
          msg: "单个场景最大视频投影上限为5个.",
          component: void 0
        };
    const n = this.entityManager.addEntity(
      {
        id: nt.generateUUID(),
        type: "VideoProject",
        name: t
      },
      !0
    );
    n.isAdded = !0;
    let r = new hM(nt.generateUUID(), t, n);
    r.isInit = !0, r.world = this;
    try {
      const a = await Qe.ApiServer.createVideoProjectEntity({
        name: t,
        commonConfig: {
          version: "2.5.0"
        },
        component: r.toJson()
      });
      return a.success ? (r.id = a.components[0].id, n.componentMap.set(r.id, r), n.add(r.group), await r.initialize(), r.isInit = !0, this.entityManager.entityMap.delete(n.uuid), n.setId(a.objId), this.entityManager.entityMap.set(n.uuid, n), this.entityManager.updateWorldListStore(), {
        success: !0,
        msg: "视频投影组件创建成功.",
        component: r
      }) : {
        success: !1,
        msg: "视频融合余额不足，请删除部分数据后导入场景",
        component: null
      };
    } catch (a) {
      return {
        success: !1,
        msg: a.message,
        component: null
      };
    }
  }
  endDrawVideoProject() {
    [...this.entityManager.entityMap.values()].forEach((t) => {
      const i = t.getComponentByType("VideoProject");
      i == null || i.endDraw();
    });
  }
  getBatchModelDrawHelper() {
    return this._batchModelDrawHelper;
  }
  startDrawBatchModel(t) {
    this._batchModelDrawHelper.startDraw(t);
  }
  endDrawBatchModel(t) {
    this._batchModelDrawHelper.endDraw(t);
  }
  startEditBatchModel(t) {
    const i = this.getDrawComponent();
    i == null || i.setMode(qa.None);
    const n = this.getSelectComponent();
    n && (n.enable = !1, n.transformControls && (n.transformControls.visible = !1)), t.group.visible = !1, this._batchModelDrawHelper.startEdit(t);
  }
  endEditBatchModel(t) {
    const i = this.getDrawComponent();
    i == null || i.setMode(qa.None);
    const n = this.getSelectComponent();
    n && (n.clearIntersections(), n.enable = !0, n.transformControls && (n.transformControls.visible = !0)), this._batchModelDrawHelper.endEdit(t), t.group.visible = !0;
  }
  updateComponent(t, i) {
    const n = () => {
      Tt.enabled ? Qe.ApiServer.updateComponent(
        t.uuid,
        i.toJson()
        // component.customAttributes,
      ).then(() => {
        dt.Entity.setState({
          id: t.uuid,
          name: t.name,
          components: t.getComponentArray().map((r) => r.toJson())
        }), this.entityManager.updateWorldListStore();
      }) : (i.removeEventListener(_i.Add, n), this.entityManager.updateWorldListStore());
    };
    i.isAdded ? n() : i.addEventListener(_i.Add, n);
  }
  async makeGroup(t, i) {
    const n = new Set(t);
    Jo.EnableTick = !1;
    const r = await Qe.ApiServer.makeGroup(
      this.renderer.rootScene.uuid,
      Array.from(n),
      "分组",
      i,
      "2.6.0"
    ), { objId: a } = r, o = this.entityManager.createEntity({
      id: a,
      type: "EngineGroup",
      name: "分组",
      commonConfig: {
        version: "2.6.0"
      }
    });
    o.isAdded = !0, this.renderer.rootScene.add(o), this.entityManager.entityMap.set(a, o);
    const l = await o.addComponentAsync(
      "",
      "分组",
      rM
    );
    l.isInit = !0, l.addState({
      name: "状态",
      rank: 0,
      config: {
        transform: {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 }
        },
        constrainedScale: !1
      }
    });
    const c = await o.addComponentAsync(
      "",
      "自定义动画",
      Yx
    );
    c.isInit = !0;
    const h = await Qe.ApiServer.getObjects(this.renderer.rootScene.uuid), u = new Map(this.entityManager.entityMap);
    this.entityManager.entityMap.clear(), h == null || h.objs.forEach((d) => {
      const f = u.get(d.id);
      if (f) {
        if (f.parentId = d.parentObjId, f.mPath = d.mpath, d.parentObjId) {
          const p = u.get(d.parentObjId);
          p == null || p.add(f);
        } else
          this.renderer.rootScene.add(f);
        this.entityManager.entityMap.set(d.id, f);
      }
    }), Jo.EnableTick = !0, this.entityManager.updateWorldListStore();
  }
  async unMakeGroup(t) {
    const i = this.entityManager.getEntity(t);
    if (i) {
      this.entityManager.entityMap.delete(t), i.parent ? (i.parent.add(...i.children), i.parent.remove(i)) : (this.renderer.rootScene.add(...i.children), this.renderer.rootScene.remove(i));
      const n = new Map(this.entityManager.entityMap);
      this.entityManager.entityMap.clear(), await Qe.ApiServer.splitGroup(t, this.renderer.rootScene.uuid);
      const r = await Qe.ApiServer.getObjects(this.renderer.rootScene.uuid);
      r == null || r.objs.forEach((a) => {
        const o = n.get(a.id);
        o && (o.parentId = a.parentObjId, o.mPath = a.mpath, this.entityManager.entityMap.set(a.id, o), o.componentMap.forEach((l) => l.setVisible(o.visible)));
      }), i.isInitComplete = !0, this.entityManager.updateWorldListStore();
    }
  }
  startDrawIframe(t, i, n) {
    const r = nt.generateUUID(), o = this.entityManager.addEntity({ id: r, parentId: n, name: t, type: "Iframe" }).addComponent(
      r,
      Bx.getName(),
      Bx
    );
    o.initialize(this.renderer.cssRenderer.domElement);
    const l = () => {
      o.removeEventListener(_i.Add, l), o.draw(this.drawUtils, i);
    };
    return o.addEventListener(_i.Add, l), o.isInit = !0, o;
  }
  startDrawIframeV2(t, i, n) {
    const r = nt.generateUUID(), o = this.entityManager.addEntity({
      id: r,
      parentId: n,
      name: t,
      type: "IframeV2",
      commonConfig: {
        version: "2.0.0"
      }
    }).addComponent(
      r,
      Wg.getName(),
      Wg
    );
    o.initialize(this.renderer.cssRenderer.domElement);
    const l = () => {
      o.removeEventListener(_i.Add, l), o.draw(this.drawUtils, i);
    };
    return o.addEventListener(_i.Add, l), o.isInit = !0, o;
  }
  startDrawFlowFence(t, i, n) {
    const r = nt.generateUUID(), o = this.entityManager.addEntity({
      id: r,
      parentId: n,
      name: t,
      type: "FlowFence",
      commonConfig: {
        version: "2.0.0"
      }
    }).addComponent(
      r,
      Vg.getName(),
      Vg
    ), l = () => {
      o.removeEventListener(_i.Add, l), o.draw(this.drawUtils);
    };
    return o.addEventListener(_i.Add, l), o.isInit = !0, o;
  }
  async addCameraFirstPersonComponent(t, i) {
    return kx.addCameraFirstPersonComponent(this, t, i);
  }
  addCameraSurroundComponent(t, i = "全景漫游", n) {
    const r = t.addComponent(
      nt.generateUUID(),
      i,
      Ux,
      n
    ).initialize(this.renderer.cameraControls);
    return this.setCameraSurroundComponentEventsActions(r), r.initCameraEvent(this), r.isInit = !0, r;
  }
  addCameraCruiseComponent(t, i = "镜头巡航", n) {
    const r = t.addComponent(
      nt.generateUUID(),
      i,
      r7,
      n
    ).initialize(this.renderer.cameraControls);
    return this.setCameraCruiseComponentEventsActions(r), r.isInit = !0, r;
  }
  setCameraSurroundComponentEventsActions(t) {
    t.addEventListener("start", () => {
      this.emitFunc && this.emitFunc("camera-animation-start", {}, t.entity.uuid);
    }), t.addEventListener("end", () => {
      this.emitFunc && this.emitFunc("camera-animation-end", {}, t.entity.uuid);
    }), t.addEventListener("restart", () => {
      this.emitFunc && this.emitFunc("camera-animation-restart", {}, t.entity.uuid);
    }), t.addEventListener("pause", () => {
      this.emitFunc && this.emitFunc("camera-animation-pause", {}, t.entity.uuid);
    });
  }
  setCameraCruiseComponentEventsActions(t) {
    const i = new _(), n = new _();
    this.renderer.cameraControls.getPosition(i), this.renderer.cameraControls.getTarget(n), t.addEventListener("easyvStart", () => {
      t._curRenderCamera = this.getCurCamera();
    }), t.addEventListener("start", () => {
      this.renderer.activeCamera = t.camera, this.renderer.cameraControls.enabled = !1, this.selection.enable = !1, this.emitFunc && this.emitFunc("camera-animation-start", {}, t.entity.uuid);
    }), t.addEventListener("restart", () => {
      this.renderer.activeCamera = t.camera, this.renderer.cameraControls.enabled = !1, this.selection.enable = !1, this.emitFunc && this.emitFunc("camera-animation-restart", {}, t.entity.uuid);
    }), t.addEventListener("end", () => {
      this.renderer.activeCamera = void 0, this.renderer.cameraControls.enabled = !0, this.selection.enable = !0, this.emitFunc && this.emitFunc("camera-animation-end", {}, t.entity.uuid);
    }), t.addEventListener("pause", ({ lock: r }) => {
      if (!r) {
        const { position: a } = t.camera, o = t._target;
        this.renderer.cameraControls.setPosition(a.x, a.y, a.z), this.renderer.cameraControls.setTarget(o.x, o.y, o.z), this.renderer.activeCamera = void 0, this.renderer.cameraControls.enabled = !0, this.selection.enable = !0;
      }
      this.emitFunc && this.emitFunc("camera-animation-pause", {}, t.entity.uuid);
    });
  }
  async importGLBFile(t) {
  }
  async addModelComponentAsync(t) {
    const [i] = await this.entityManager.addEntityAsync({
      id: "",
      name: t.name,
      visible: !0,
      type: "Model",
      commonConfig: {
        version: "2.3.1"
      }
    });
    return await this.addModelComponentAsyncImplement(i, t);
  }
  async addModelComponentAsyncImplement(t, i) {
    var n, r, a, o, l;
    try {
      const c = na(i.url);
      if (c === "glb") {
        let h = new Rc(nt.generateUUID(), i.name, t);
        h = await t.addComponentAsync(
          nt.generateUUID(),
          i.name,
          Rc,
          !1
        ), h.world = this;
        const u = await l7(i.url);
        (n = i.onProgress) == null || n.call(i, 0.9, Jh.loading), await c7(u, h.id), h.modelRenderResource = u, h.createAnimationActions();
        const d = h.toJson();
        return h.initDataOptions = d.data, h.initData = d.data, Qe.ApiServer.updateComponent(
          t.uuid,
          d,
          d.data
        ), h.addObject2Renderer(), h.startAnimationLoop(), (r = i.onProgress) == null || r.call(i, 1, Jh.loaded), this.entityManager.updateWorldListStore(), h;
      } else if (c === "json") {
        const h = await st.assetManager.loadJson(i.url), { materials: u, scene: d, meshSetMap: f } = await st.assetManager.loadTwinModel(h);
        (a = i.onProgress) == null || a.call(i, 0.9, Jh.loading);
        let p = new Rc(nt.generateUUID(), i.name, t);
        p = await t.addComponentAsync(p.id, i.name, Rc, !1);
        const v = u.map((m) => ({
          material: m,
          type: "privateMaterial",
          componentId: p.id
        }));
        await st.assetManager.materialManager.createMaterialsOnServer(v), yw(u), (o = i.onProgress) == null || o.call(i, 1, Jh.loaded);
        const g = new jc();
        return g.scene = d, g.materials = u, g.url = h.resources.geometries[0].url, g.geometries = h.resources.geometries, YB(g, g.materials, f), p.modelRenderResource = g, Qe.ApiServer.updateComponent(t.uuid, p.toJson()), p.addObject2Renderer(), p;
      }
    } catch {
      throw (l = i.onProgress) == null || l.call(i, 1, Jh.error), new Error("Failed to create the model component!");
    }
  }
  async addParticleEntity(t) {
    const i = (await this.entityManager.addEntityAsync({
      id: "",
      name: t.entityName,
      visible: !0,
      type: "ParticleSystem",
      commonConfig: {
        version: "2.1.0"
      }
    }))[0], n = await (i == null ? void 0 : i.addComponentAsync(
      nt.generateUUID(),
      t.componentName,
      Jr
    ));
    return await (n == null ? void 0 : n.createFromState(t.stateConfig)), {
      entity: i,
      component: n
    };
  }
  async addDefaultParticleEntity(t) {
    let i = [];
    switch (t.particleType) {
      case "fire":
        i = Jr.getDefaultFireConfig();
        break;
      case "snow":
        i = Jr.getDefaultSnowConfig();
        break;
      case "rain":
        i = Jr.getDefaultRainConfig();
        break;
      case "fire1":
        i = Jr.getDefaultFire1Config();
        break;
      case "number":
        i = Jr.getDefaultNumberConfig();
        break;
      default:
        i = [Jr.getParticleDefaultConfig()];
        break;
    }
    const n = (await this.entityManager.addEntityAsync({
      id: "",
      name: t.entityName,
      visible: !0,
      type: "ParticleSystem",
      commonConfig: {
        version: "2.1.0"
      }
    }))[0], r = await (n == null ? void 0 : n.addComponentAsync(
      nt.generateUUID(),
      t.componentName,
      Jr
    ));
    return r.createFromState({
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      particleSystems: i
    }), {
      entity: n,
      component: r
    };
  }
  async addClipModelEntity(t) {
    const i = (await this.entityManager.addEntityAsync({
      id: "",
      name: t.entityName,
      visible: !0,
      type: "ClipModel",
      commonConfig: {
        version: "2.2.0"
      }
    }))[0], n = await (i == null ? void 0 : i.addComponentAsync(
      nt.generateUUID(),
      t.componentName,
      uM
    ));
    return n.world = this, n.addState(t.stateOption), {
      entity: i,
      component: n
    };
  }
  /**
   * @deprecated
   * @param entity
   * @param stateConfig
   * @param name
   * @returns
   */
  async addParticleComponent(t, i, n = "粒子组件") {
    const r = await (t == null ? void 0 : t.addComponentAsync(
      nt.generateUUID(),
      n,
      Jr
    ));
    return await (r == null ? void 0 : r.createFromState(i)), r;
  }
  getCurCamera() {
    return this.renderer.mainCamera;
  }
  addCameraPointComponent(t, i = "镜头") {
    const n = t.addComponent(
      nt.generateUUID(),
      i,
      Kf
    );
    return n.initialize(this.renderer.cameraControls), n.isInit = !0, n;
  }
  getDrawComponent() {
    const t = Za(this.containerId, "Draw");
    return this.componentMap.get(t);
  }
  getSelectComponent() {
    return this.selection;
  }
  getEngineSelection() {
    return this.selection;
  }
  endDrawComponent() {
    var t;
    (t = this.getDrawComponent()) == null || t.clearDraw();
  }
  tick(t) {
    const i = this;
    this._animateFrameId = requestAnimationFrame((r) => {
      i.tick(r);
    });
    const n = t - this._lastFrame;
    if (n > Jo.FreeInterval) {
      if (Jo.EnableTick) {
        this.fpsTool.update(n), this.prepareUpdate(n), this.prepareStateUpdate(), this.updateVideoProjectComponent(), this.updateCesium3DTilesetComponent();
        const r = this.clock.getDelta();
        this.updateParticleComponent(r), this.renderer.render(r), this.updateRenderInfo(), this.roma.tick(), this.selection.tick(), hp.getInstance().dependenciesInstance.forEach((a) => {
          a.tick();
        }), this.worldInfo.fps = this.fpsTool.fps, this.worldInfo.averageFps = this.fpsTool.averageFps;
      }
      this._lastFrame = t;
    }
    sq();
  }
  updateParticleComponent(t) {
    const i = [];
    [...this.entityManager.entityMap.values()].forEach((n) => {
      const r = n.getComponentByType("ParticleSystem");
      r && i.push(r);
    }), i.forEach((n) => n.updateParticlesRender(t));
  }
  updateCesium3DTilesetComponent() {
    const t = this.getComponentByType("Scene");
    t && t.tilesetComponent.needUpdate && t.tilesetComponent.update();
  }
  updateVideoProjectComponent() {
    const t = [];
    [...this.entityManager.entityMap.values()].forEach((i) => {
      const n = i.getComponentByType("VideoProject");
      n && t.push(n);
    }), t.forEach((i) => {
      i && i.type === "VideoProject" && (i.camera.visible = i.entity.visible);
    });
  }
  updateWorldInfo(t) {
    this.worldInfo.modelCount = 0, this.worldInfo.featureCount = 0, this.worldInfo.materialCount = 0, this.worldInfo.triangleCount = 0, t.forEach((i) => {
      switch (i.type) {
        case "BatchModel":
        case "Model":
          this.worldInfo.modelCount++, this.worldInfo.materialCount += i.materials.length, this.worldInfo.triangleCount += i.triangles;
          break;
        case "Point":
        case "PoiPanel":
        case "FlowFence":
        case "Iframe":
          this.worldInfo.featureCount++;
          break;
      }
    });
  }
  updateRenderInfo() {
    const t = this.renderer.getRendererInfo();
    this.worldInfo.programCount = t.programs, this.worldInfo.textureCount = t.textures;
  }
  prepareUpdate(t) {
    const i = [];
    this.entityManager.entityMap.forEach((n) => {
      if (i.push(...Array.from(n.componentMap.values())), !n.isInitComplete && n.getComponentInit()) {
        const r = {};
        n.componentMap.forEach((a) => {
          Object.assign(r, a.toJson().data);
        }), this.emitFunc && this.emitFunc(
          "init",
          {
            ...r,
            name: n.name,
            id: n.uuid
          },
          n.uuid
        ), n.isInitComplete = !0, this.entityManager.updateWorldListStore();
      }
    }), i.push(...Array.from(this.componentMap.values())), this.updateWorldInfo(i), i.forEach((n) => {
      try {
        n.needsUpdate && (n.update(t), this.selection.refresh(), n.needsUpdate = !1), n.needDataUpdateServer && (Qe.ApiServer.updateComponent(
          n.entity.uuid,
          n.toJson()
          // component.customAttributes,
        ), n.needDataUpdateServer = !1), n.render(this.renderer.rootScene, this.renderer.mainCamera), n.tick();
      } catch (r) {
        console.log(r), n.needsUpdate = !1, n.needDataUpdateServer = !1;
      }
    }), this.isInitComplete && this.getComponentInit() && (this.emitFunc && this.emitFunc("init", { worldId: this.containerId }, this.uuid), this.event.dispatchEvent("initComplete", void 0), console.log(`场景${this.uuid}初始化完成`), this.isInitComplete = !1, this._handleCustomAnimationAutoPlay(), this._updateSpriteScale(), this._updateClipModelComponent());
  }
  _updateClipModelComponent() {
    this.entityManager.entityMap.forEach((t) => {
      const i = t.getComponentByType("ClipModel");
      i && i.onWorldInitialized(this);
    });
  }
  _handleCustomAnimationAutoPlay() {
    this.entityManager.entityMap.forEach((t) => {
      const i = t.getComponentByType("CustomAnimation");
      if (i) {
        const n = Array.from(i.states.stateList.values()), r = n == null ? void 0 : n.find((a) => a.config.autoPlay);
        r && i.start(void 0, r.id);
      }
    });
  }
  prepareStateUpdate() {
    const t = [];
    this.entityManager.entityMap.forEach((n) => {
      t.push(...Array.from(n.componentMap.values()));
    }), t.push(...Array.from(this.componentMap.values()));
    const i = [];
    t.forEach((n) => {
      n.states.needsUpdateStateSet.forEach((r) => {
        const a = n.states.stateList.get(r);
        i.push(a);
      }), n.states.needsUpdateStateSet.clear();
    }), i.length > 0 && Qe.ApiServer.updateStates(i, this.getSceneId());
  }
  projectCanvasPositionToScene(t, i, n, r = 0) {
    return Y4(this.renderer.mainCamera, t, i, n, r);
  }
  flyTo(t, i, n) {
    const r = new zt(), a = (o, l) => {
      const c = new zt().setFromObject(o);
      return c.min.x !== 1 / 0 ? c : l.getBox(o.uuid);
    };
    if (i) {
      const o = [];
      if (i instanceof Array)
        i.forEach((l) => {
          const c = this.entityManager.getEntity(l);
          c && o.push(c);
        });
      else {
        const l = this.entityManager.getEntity(i);
        l && o.push(l);
      }
      o.forEach((l) => {
        l.componentMap.forEach((c) => {
          if (n)
            if (n instanceof Array)
              n.forEach((h) => {
                const u = c.getObjectById(h);
                u && r.union(a(u, c));
              });
            else {
              const h = c.getObjectById(n);
              h && r.union(a(h, c));
            }
          else
            r.union(a(c.group, c));
        });
      });
    } else
      this.entityManager.entityMap.forEach((o) => {
        o.componentMap.forEach((l) => r.union(a(l.group, l)));
      });
    console.log("box", r), this.flyToBox(r, t);
  }
  getCameraFitAnchor(t) {
    const i = t.getBoundingSphere(new Tn());
    let n = { r: 0, x: 0, y: 0, z: 0 }, r = { r: 0, x: 0, y: 0, z: 0 };
    const a = t.getCenter(new _());
    if (this.renderer.mainCamera instanceof Zt) {
      const o = this.renderer.mainCamera.getEffectiveFOV() * nt.DEG2RAD, l = Math.atan(Math.tan(o * 0.5) * this.renderer.mainCamera.aspect) * 2, c = this.renderer.mainCamera.aspect > 1 ? o : l, h = i.radius / Math.sin(c * 0.5), u = nt.clamp(
        h,
        this.renderer.cameraControls.minDistance,
        this.renderer.cameraControls.maxDistance
      ), d = new _();
      this.renderer.cameraControls.getTarget(d), n = {
        r: this.renderer.cameraControls.getSpherical(new Ds()).radius,
        x: d.x,
        y: d.y,
        z: d.z
      }, r = {
        r: u,
        x: a.x,
        y: a.y,
        z: a.z
      };
    }
    if (this.renderer.mainCamera instanceof Xn) {
      const o = i.radius, l = nt.clamp(
        o,
        this.renderer.cameraControls.minDistance,
        this.renderer.cameraControls.maxDistance
      ), c = new _();
      this.renderer.cameraControls.getTarget(c);
      const h = this.renderer.cameraControls.getSpherical(new Ds()).radius;
      n = {
        r: this.renderer.cameraControls.camera.zoom,
        x: c.x,
        y: c.y,
        z: c.z
      }, r = {
        r: (1 + h) / l,
        x: a.x,
        y: a.y,
        z: a.z
      };
    }
    return [n, r];
  }
  flyToBox(t, i) {
    const { duration: n = 1e3, delay: r, mode: a } = i, o = this.renderer.cameraControls.mouseButtons.wheel === $e.DOLLY, [l, c] = this.getCameraFitAnchor(t), h = jn.createTweenHandler({
      source: l,
      target: c,
      duration: n,
      mode: a,
      delay: r,
      execute: (u) => {
        o ? this.renderer.cameraControls.dollyTo(u.r) : this.renderer.cameraControls.zoomTo(u.r), this.renderer.cameraControls.moveTo(u.x, u.y, u.z);
      },
      complete: () => {
        Yi(h);
      }
    });
  }
  resize(t, i) {
    this.renderer.mainCamera instanceof Zt && (this.renderer.mainCamera.aspect = t / i), this.renderer.setSize(t, i), this.renderer.mainCamera.updateProjectionMatrix();
  }
  deserialization() {
    return this.entityManager.toJson(), {
      entities: [],
      components: []
    };
  }
  registerEngineEmitFunc(t) {
    this.emitFunc = t;
  }
  execute(t) {
    const { command: i } = t;
    try {
      switch (i) {
        case "updateScene":
          this.executeSetWorldState(t);
          break;
        case "setComponentState":
          this.executeSetComponentState(t);
          break;
        case "setComponentData":
          this.executeSetComponentData(t);
          break;
        case "componentExecute":
          this.executeComponentFunc(t);
          break;
        case "setObjectState":
          this.executeSetObject3dByState(t);
          break;
        case "flyToBox":
          this.executeFlyTo(t);
          break;
        default:
          this.entityManager.execute(t);
          break;
      }
    } catch (n) {
      console.error("执行命令错误", t, n);
    }
  }
  /**
   *
   * @deprecated 已经废弃
   */
  executeFlyTo(t) {
    const { data: i, targetId: n } = t, { name: r, distance: a, duration: o = 1 } = i, l = this.entityManager.getEntity(n), c = l == null ? void 0 : l.getComponentByType("Model");
    if (c) {
      const h = new zt();
      if (r) {
        const u = c.group.getObjectByName(r);
        u && h.setFromObject(u);
      } else
        h.setFromObject(c.group);
      a && h.expandByScalar(a), this.flyToBox(h, {
        duration: o * 1e3
      });
    }
  }
  executeSetWorldState(t) {
    const i = t.data, n = this == null ? void 0 : this.getComponentByType(i.type);
    n == null || n.setCurrentStateById(i.stateId), n instanceof Kf && (n == null || n.focusCurStateCamera(i.stateId));
  }
  executeComponentFunc(t) {
    const i = t.data, n = this.entityManager.getEntity(t.targetId), r = n == null ? void 0 : n.getComponentByType(i.type), a = [];
    switch (i.funcName) {
      case "focusOnModel":
        this.executeFocusComponent(t);
        break;
      default:
        switch (r == null ? void 0 : r.type) {
          case "CameraCruise":
            a.push({
              curCamera: this.renderer.mainCamera
            });
            break;
          case "CameraSurround":
            break;
          case "Model":
            a.push({
              world: this
            });
            break;
        }
        r == null || r.execute(i.funcName, i.args, ...a);
        break;
    }
  }
  executeFocusComponent(t) {
    const n = t.data.args;
    Qe.Info.autoFocus.distance = n.distance ?? 0, Qe.Info.autoFocus.duration = (n.duration ?? 1) * 1e3, Qe.Info.autoFocus.model = !0;
  }
  executeSetComponentData(t) {
    const i = t.data, n = this.entityManager.getEntity(t.targetId), r = n == null ? void 0 : n.getComponentByType(i.type);
    r == null || r.setData(i.data);
  }
  executeSetComponentState(t) {
    const i = t.data, n = this.entityManager.getEntity(t.targetId), r = n == null ? void 0 : n.getComponentByType(i.type);
    r == null || r.setCurrentStateById(i.stateId), r && this._stopComponentTWEEN(r), r && this.executeComponentAction(r, i.stateId);
  }
  // 设置当前状态后，部分组件是需要执行函数的
  executeComponentAction(t, i) {
    t instanceof Kf && t.focusCurStateCamera(i);
  }
  executeSetObject3dByState(t) {
    const i = t.data, n = this.entityManager.getEntity(t.targetId), r = n == null ? void 0 : n.getComponentByType(i.type);
    r && r.easyvSetStateByAttributes(i.stateId, i.attributes);
  }
  findComponentById(t, i) {
    const n = this.entityManager.getEntity(t);
    return n == null ? void 0 : n.getComponentById(i);
  }
  update() {
  }
  dispose() {
    this.entityManager.clear(), this.emitFunc = void 0, this.selection.dispose(), this.drawUtils.dispose(), this.componentMap.forEach((t) => t.dispose()), this.componentMap.clear(), this.renderer.dispose(), this._batchModelDrawHelper.dispose(), cancelAnimationFrame(this._animateFrameId), this.renderer.threeRenderer.domElement.removeEventListener(
      "webglcontextlost",
      this.restoreRender
    );
  }
  clearEntities() {
    this.entityManager.clear();
  }
  deleteEntities() {
    this.entityManager.delete();
  }
  getTransformAdsorb() {
    return this.selection.transformAdsorb;
  }
  unLinkAllComponentMaterials(t) {
    this.entityManager.entityMap.forEach((i) => {
      i.componentMap.forEach((n) => {
        if (n.type === "Model" || n.type === "InstanceModel") {
          const r = n;
          r.getMaterialSlots().forEach((a) => {
            a.materialId === t && r.unLinkMaterialSlot(a.slotId);
          });
        }
      });
    });
  }
  // 通过scene component 中的经纬映射配置项, 将输入的三维坐标 转换为经纬坐标
  projectPositionToGeographic(t, i, n) {
    return gee(this, t, i, n);
  }
  getGeographicProjectionUtils() {
    const t = this.getComponentByType("Scene");
    return t == null ? void 0 : t.geographicProjectionUtils;
  }
  projectGeographicToPosition(t, i, n) {
    return Lu(this, t, i, n);
  }
  getBuildingsComponents() {
    const t = [];
    return this.entityManager.entityMap.forEach((i) => {
      const n = i.getComponentByType("BuildingsAnimation");
      n && t.push(n);
    }), t;
  }
  getClipModelComponents() {
    const t = [];
    return this.entityManager.entityMap.forEach((i) => {
      const n = i.getComponentByType("ClipModel");
      n && t.push(n);
    }), t;
  }
  async createBuildingsAnimationComponent(t, i) {
    var l, c;
    const n = nt.generateUUID(), [r, a] = await this.entityManager.addEntityAsync(
      {
        id: n,
        parentId: null,
        name: t,
        type: "BuildingsAnimation",
        commonConfig: { version: "2.0.0" }
      },
      [
        {
          id: "BuildingsAnimation",
          type: "BuildingsAnimation",
          name: t,
          visible: !0,
          version: "2.0.0",
          inActive: !1,
          data: {},
          states: i ? [i] : []
        }
      ]
    ), o = r.addComponent(
      a[0].id ?? n,
      t,
      iM,
      !0
    );
    return i && ((l = a[0]) != null && l.componentStateIds[0]) && (o.updateFromJson(this, {
      id: o.id,
      type: "BuildingsAnimation",
      name: t,
      visible: !0,
      inActive: !1,
      data: {},
      version: "2.0.0",
      states: i ? [i] : []
    }), o.initStates([
      {
        id: (c = a[0]) == null ? void 0 : c.componentStateIds[0],
        name: i.name,
        config: i.config,
        rank: i.rank,
        createdAt: Date.now().toString()
      }
    ])), dt.Entity.refresh(), o;
  }
  _stopComponentTWEEN(t) {
    t && t.tweenHandler && (t.tweenHandler.stop(), Yi(t.tweenHandler), t.tweenHandler = void 0);
  }
}, S(Jo, "FreeInterval", 15), S(Jo, "EnableTick", !0), Jo);
H6e([
  Qp(1)
], hz.prototype, "prepareStateUpdate");
let uz = hz;
class V6e {
  setGLTFCache(e, t) {
    const i = new Nn();
    i.setResponseType("arraybuffer"), i.load(e, (n) => {
      ux.setItem(e, n), console.log("cache set file");
    });
  }
}
class W6e {
  constructor() {
    S(this, "wrapper");
    S(this, "splash");
    S(this, "logo");
    S(this, "container");
    S(this, "bar");
    S(this, "textNode");
    this.createCss();
  }
  show(e) {
    this.wrapper = document.createElement("div"), this.wrapper.id = "application-splash-wrapper", e.parentDom ? e.parentDom.appendChild(this.wrapper) : document.body.appendChild(this.wrapper), this.splash = document.createElement("div"), this.splash.id = "application-splash", this.wrapper.appendChild(this.splash), this.splash.style.display = "none", this.logo = document.createElement("img"), this.logo.src = e.imageUrl ?? "https://playcanvas.com/static-assets/images/play_text_252_white.png", this.splash.appendChild(this.logo), this.logo.onload = () => {
      this.splash.style.display = "block";
    }, this.container = document.createElement("div"), this.container.id = "progress-bar-container", this.splash.appendChild(this.container), this.bar = document.createElement("div"), this.bar.id = "progress-bar", this.textNode = document.createTextNode("加载中 0%"), this.container.appendChild(this.textNode);
  }
  hide() {
    var t;
    const e = document.getElementById("application-splash-wrapper");
    e && ((t = e.parentElement) == null || t.removeChild(e));
  }
  setProgress(e) {
    this.bar && (e = Math.min(1, Math.max(0, e)), this.bar.style.width = `${e * 100}%`, this.textNode.textContent = `加载中 ${Math.floor(e * 100)}%`);
  }
  createCss() {
    const e = [
      "body {",
      // '    background-color: #283538;',
      "}",
      "#application-splash-wrapper {",
      "    position: absolute;",
      "    top: 0;",
      "    left: 0;",
      "    height: 100%;",
      "    width: 100%;",
      "    background-color: #000000;",
      "}",
      "#application-splash {",
      "    position: absolute;",
      "    top: calc(50% - 90px);",
      "    width: 320px;",
      "    height: 180px;",
      "    left: calc(50% - 160px);",
      "}",
      "#application-splash img {",
      "    width: 100%;",
      "}",
      "#progress-bar-container {",
      // '    margin: 20px auto 0 auto;',
      // '    height: 2px;',
      "    width: 100%;",
      // '    background-color: #1d292c;',
      "    text-align: center;",
      "    font-size: 12px;",
      "}",
      "#progress-bar {",
      "    width: 0%;",
      "    height: 100%;",
      "    background-color: #f60;",
      "}",
      "@media (max-width: 480px) {",
      "    #application-splash {",
      "        width: 170px;",
      "        left: calc(50% - 85px);",
      "    }",
      "}"
    ].join(`
`), t = document.createElement("style");
    t.type = "text/css", t.styleSheet ? t.styleSheet.cssText = e : t.appendChild(document.createTextNode(e)), document.head.appendChild(t);
  }
}
window.EngineDebugInfo = {
  enableConsole: !1
};
class X6e extends es {
  constructor() {
    super();
    S(this, "cache", new V6e());
    S(this, "ApiServer", new Tt());
    S(this, "worldMap", /* @__PURE__ */ new Map());
    S(this, "history", new jO());
    S(this, "EntityCommonConfigVersionMap", /* @__PURE__ */ new Map());
    S(this, "readyWorldId");
    S(this, "Info", {
      gridAxisPlane: "xz",
      autoFocus: {
        model: !1,
        distance: 0,
        duration: 1e3
      }
    });
    S(this, "loadingScreen", new W6e());
    S(this, "runningInEditor", !1);
    S(this, "mode", "Player");
    this.loader = st, window.easytwin_engine = this;
  }
  createWorld(t) {
    "sceneId" in t && (Tt.enabled = !0);
    let i = this.worldMap.get(t.containerId);
    return i || (i = new uz(t), "sceneId" in t && (i.renderer.rootScene.uuid = t.sceneId), i.isAdded = !0, this.worldMap.set(i.containerId, i), this.readyWorldId = i.containerId, this.history.clear(), this.history.active(), i);
  }
  getWorld(t) {
    return this.worldMap.get(t);
  }
  getRenderer(t) {
    var i;
    return (i = this.worldMap.get(t)) == null ? void 0 : i.renderer;
  }
  getRendererEvent(t) {
    var i;
    return (i = this.getRenderer(t)) == null ? void 0 : i.event;
  }
  getViewHelper(t) {
    var i;
    return (i = this.getRenderer(t)) == null ? void 0 : i.viewHelper;
  }
  dispose() {
    this.worldMap.forEach((t) => {
      t.dispose(), t.renderer.threeRenderer.forceContextLoss();
    }), this.worldMap.clear(), this.history.clear(), dt.dispose(), st.assetManager.dispose(), delete window.easytwin_engine, this.removeAllEventListeners(), this.EntityCommonConfigVersionMap.clear(), hp.getInstance().dispose();
  }
  disposeWorldById(t) {
    const i = this.worldMap.get(t);
    i && (i.dispose(), i.renderer.threeRenderer.forceContextLoss(), this.worldMap.delete(t), st.assetManager.dispose());
  }
  static Update() {
  }
  getEasyTwinStore() {
    return dt;
  }
  createTweenHandler(t) {
    return jn.createTweenHandler(t);
  }
  removeTweenHandler(t) {
    jn.removeTweenHandler(t);
  }
}
const Qe = new X6e();
var Vn = /* @__PURE__ */ ((s) => (s.None = "None", s.Query = "Query", s.QueryAndCollision = "QueryAndCollision", s))(Vn || {});
class ks extends NS {
  constructor(t, i) {
    super(new zt(t, i));
    // RigidBodyCollision 在 x 轴和 y 轴上的最小和最大坐标
    S(this, "_min");
    S(this, "_max");
    S(this, "_calVec3", new _());
    S(this, "boxHelper");
    this._min = t, this._max = i, this.box && (this.boxHelper = new L4(this.box)), i.x - t.x > 3 && i.z - t.z > 3;
  }
  get min() {
    return this._min;
  }
  get max() {
    return this._max;
  }
  static fromBox(t) {
    return new ks(t.min, t.max);
  }
  static async fromGraphNode(t) {
    return new Promise((i) => {
      t.updateWorldMatrix(!0, !0);
      const n = new zt().setFromObject(t), r = new ks(n.min, n.max);
      t.traverse((a) => {
        if (a instanceof Re && r.layers.test(a.layers)) {
          let o, l = !1;
          a.geometry.index !== null ? (l = !0, o = a.geometry.toNonIndexed()) : o = a.geometry, console.time("octree"), o.computeVertexNormals();
          const c = o.getAttribute("position");
          let h = 3;
          c.count > 1e4 && (h = Math.ceil(c.count / 1e4) * 3);
          for (let u = 0; u < c.count; u += h) {
            const d = new _().fromBufferAttribute(c, u), f = new _().fromBufferAttribute(c, u + 1), p = new _().fromBufferAttribute(c, u + 2);
            d.applyMatrix4(a.matrixWorld), f.applyMatrix4(a.matrixWorld), p.applyMatrix4(a.matrixWorld), r.addTriangle(new Wo(d, f, p));
          }
          console.timeEnd("octree"), l && o.dispose();
        }
      }), r.build(), i(r);
    });
  }
  static async fromObject(t) {
    return Promise.all([ks.fromGraphNode(t)]);
  }
  static calcIntersectionSize(t) {
    const [[i, n, r], [a, o, l]] = t;
    return Math.abs((a - i) * (o - n) * (l - r));
  }
  static getIntersectionArea(t, i, n, r) {
    const a = Math.max(t.min.x + i.x, n.min.x + r.x), o = Math.min(t.max.x + i.x, n.max.x + r.x), l = Math.max(t.min.y + i.y, n.min.y + r.y), c = Math.min(t.max.y + i.y, n.max.y + r.y), h = Math.max(t.min.z + i.z, n.min.z + r.z), u = Math.min(t.max.z + i.z, n.max.z + r.z);
    return a <= o && l <= c && h <= u ? [
      [a, l, h],
      [o, c, u]
    ] : [
      [0, 0, 0],
      [0, 0, 0]
    ];
  }
  static unionByArea(t, i) {
    const n = Math.max(t[0][0], i[0][0]), r = Math.min(t[1][0], i[1][0]), a = Math.max(t[0][1], i[0][1]), o = Math.min(t[1][1], i[1][1]), l = Math.max(t[0][2], i[0][2]), c = Math.min(t[1][2], i[1][2]);
    return [
      [n, a, l],
      [r, o, c]
    ];
  }
  updateYFromPositionArray(t) {
    const i = [];
    for (let n = 0; n < t.length; n += 3)
      t[n] > this.min.x && t[n] < this.max.x && t[n + 2] > this.min.z && t[n + 2] < this.max.z && (i.push(t[n]), i.push(t[n + 1]), i.push(t[n + 2]), this.min.y = Math.min(this.min.y, t[n + 1]), this.max.y = Math.max(this.max.y, t[n + 1]));
  }
  updateOffset(t) {
    this.min.add(t), this.max.add(t);
  }
  get size() {
    return this._calVec3.subVectors(this.max, this.min).clone();
  }
  get center() {
    return this._calVec3.addVectors(this.max, this.min).multiplyScalar(0.5).clone();
  }
  getMaxAxis(t) {
    const i = Math.max(Math.max(t.x, t.y), t.z);
    let n = 0;
    return i === t.y && (n = 1), i === t.z && (n = 2), n;
  }
  subdivision() {
    const t = [], i = this.size, n = this.getMaxAxis(i);
    if (n === 0) {
      const r = (this.min.x + this.max.x) / 2;
      t.push(
        new ks(this.min.clone(), new _(r, this.max.y, this.max.z))
      ), t.push(
        new ks(new _(r, this.min.y, this.min.z), this.max.clone())
      );
    } else if (n === -1) {
      const r = (this.min.y + this.max.y) / 2;
      t.push(
        new ks(this.min.clone(), new _(this.max.x, r, this.max.z))
      ), t.push(
        new ks(new _(this.min.x, r, this.min.z), this.max.clone())
      );
    } else if (n === 2) {
      const r = (this.min.z + this.max.z) / 2;
      t.push(
        new ks(this.min.clone(), new _(this.max.x, this.max.y, r))
      ), t.push(
        new ks(new _(this.min.x, this.min.y, r), this.max.clone())
      );
    }
    return t;
  }
  // 计算 RigidBodyCollision 的分数
  get score() {
    return this._calVec3.subVectors(this.max, this.min).length();
  }
  intersectsWithOffset(t, i) {
    return this.min.x + t.x <= i.max.x && this.max.x + t.x >= i.min.x && this.min.y + t.y <= i.max.y && this.max.y + t.y >= i.min.y && this.min.z + t.z <= i.max.z && this.max.z + t.z >= i.min.z;
  }
  // 检查两个 RigidBodyCollision 是否相交
  intersects(t, i, n) {
    const r = this.min.clone().add(i), a = this.max.clone().add(i), o = t.min.clone().add(n), l = t.max.clone().add(n);
    return r.x <= l.x && a.x >= o.x && r.y <= l.y && a.y >= o.y && r.z <= l.z && a.z >= o.z;
  }
  // 得到相交区域
  // public depthIntersection(other: RigidBodyCollision): [[number, number, number], [number, number, number]] {
  //   if (this.intersection(other)) {
  //     let resultPair: [RigidBodyCollision, RigidBodyCollision] = [this, other];
  //     let minSize: number = Number.MAX_VALUE;
  //     const recursion = (
  //       bodyA: RigidBodyCollision,
  //       bodyB: RigidBodyCollision,
  //       result: [RigidBodyCollision, RigidBodyCollision] = resultPair,
  //     ) => {
  //       bodyA.children.forEach((child) => {
  //         bodyB.children.forEach((otherChild) => {
  //           if (child.intersection(otherChild)) {
  //             const area = RigidBodyCollision.getIntersectionArea(child, otherChild);
  //             const size = RigidBodyCollision.calcIntersectionSize(area);
  //             if (size > 0 && size < minSize) {
  //               result = [child, otherChild];
  //               recursion(child, otherChild, result);
  //             }
  //           }
  //         });
  //       });
  //     };
  //     recursion(this, other);
  //     return RigidBodyCollision.getIntersectionArea(resultPair[0], resultPair[1]);
  //   }
  //   return [
  //     [0, 0, 0],
  //     [0, 0, 0],
  //   ];
  // }
  // 获取两个 RigidBodyCollision 的交集
  intersection(t) {
    const i = Math.max(this.min.x, t.min.x), n = Math.min(this.max.x, t.max.x), r = Math.max(this.min.y, t.min.y), a = Math.min(this.max.y, t.max.y), o = Math.max(this.min.z, t.min.z), l = Math.min(this.max.z, t.max.z);
    return i <= n && r <= a && o <= l ? new ks(
      new _(i, r, o),
      new _(n, a, l)
    ) : null;
  }
  // 获取两个 RigidBodyCollision 的并集
  union(t) {
    const i = Math.min(this.min.x, t.min.x), n = Math.max(this.max.x, t.max.x), r = Math.min(this.min.y, t.min.y), a = Math.max(this.max.y, t.max.y), o = Math.min(this.min.z, t.min.z), l = Math.max(this.max.z, t.max.z);
    return new ks(
      new _(i, r, o),
      new _(n, a, l)
    );
  }
}
class j6e {
  constructor() {
    S(this, "_value", new _());
  }
  getVelocity() {
    return this._value;
  }
  setVelocity(e, t, i) {
    this._value.set(e, t, i);
  }
  add(e, t, i) {
    this._value.x += e, this._value.y += t, this._value.z += i;
  }
}
class q6e {
  constructor() {
    //阻力
    S(this, "_resistanceForce", new _());
    S(this, "_value", new _());
  }
  add(e, t, i) {
    this._value.x += e, this._value.y += t, this._value.z += i;
  }
  init(e, t, i) {
    this._value.set(e, t, i);
  }
  getForce() {
    return this._value.sub(this._resistanceForce);
  }
}
class Qg extends es {
  constructor(t, i) {
    super();
    S(this, "id", nt.generateUUID());
    S(this, "_type", "static");
    // 记录首次碰撞, 在第一次不碰撞后重置
    S(this, "_collisionStatus", 0);
    S(this, "_freeze", !1);
    // private _temPts: number[] = [];
    S(this, "_collisions", []);
    S(this, "_velocity", new j6e());
    S(this, "_force", new q6e());
    S(this, "_enableGravity", !0);
    S(this, "_mode", Vn.Query);
    S(this, "position", new _());
    S(this, "worldPosition", new _());
    S(this, "maxStep", 1);
    S(this, "minStep", 0.1);
    this._type = t, this._mode = i;
  }
  get type() {
    return this._type;
  }
  get freeze() {
    return this._freeze;
  }
  set freeze(t) {
    this._freeze = t;
  }
  get collisions() {
    return this._collisions;
  }
  get velocity() {
    return this._velocity;
  }
  get force() {
    return this._force;
  }
  get enableGravity() {
    return this._enableGravity;
  }
  set enableGravity(t) {
    this._enableGravity = t, t ? (this._force.add(0, -9.8, 0), this._type = "dynamic") : (this._force.init(0, 0, 0), this._type = "static");
  }
  get mode() {
    return this._mode;
  }
  set mode(t) {
    this._mode = t;
  }
  static fromObject(t, i = Vn.None) {
    const n = new Qg("static", i);
    return ks.fromObject(t).then((r) => {
      if (n._collisions.push(...r), n._collisions.length === 0) {
        const a = new zt().setFromObject(t);
        n._collisions.push(ks.fromBox(a));
      }
    }), t.getWorldPosition(n.worldPosition), n.position.copy(t.position), n.resetPosition(), n;
  }
  static fromBox(t, i = Vn.QueryAndCollision) {
    const n = new Qg("static", i);
    return n._collisions.push(ks.fromBox(t)), n;
  }
  static getIntersectionArea(t, i) {
    if (t.mode === Vn.None || i.mode === Vn.None || t.mode === Vn.Query && i.mode === Vn.Query)
      return [
        [0, 0, 0],
        [0, 0, 0]
      ];
    if (t.collisions.length) {
      let n = [
        [t.collisions[0].min.x, t.collisions[0].min.y, t.collisions[0].min.z],
        [t.collisions[0].max.x, t.collisions[0].max.y, t.collisions[0].max.z]
      ];
      return t.collisions.forEach((r) => {
        i.collisions.forEach((a) => {
          n = ks.unionByArea(
            n,
            ks.getIntersectionArea(
              r,
              t.worldPosition,
              a,
              i.worldPosition
            )
          );
        });
      }), n;
    }
    return [
      [0, 0, 0],
      [0, 0, 0]
    ];
  }
  // 把位置重新设置到水平面之上
  resetPosition() {
    var i;
    let t = (i = this._collisions[0]) == null ? void 0 : i.min.y;
    if (this._collisions.forEach((n) => t = Math.min(n.min.y, t)), this.worldPosition.y + t < 0) {
      let n = this.worldPosition.y;
      this.worldPosition.y = -t, this.position.y = this.position.y - n - t;
    }
  }
  getWorldMinY() {
    var i;
    let t = (i = this._collisions[0]) == null ? void 0 : i.min.y;
    return this._collisions.forEach((n) => t = Math.min(n.min.y, t)), this.worldPosition.y + t;
  }
  updatePosition(t) {
    this.worldPosition.add(t.clone().sub(this.position)), this.position.copy(t), this.resetPosition();
  }
  translate(t) {
    this.worldPosition.add(t), this.position.add(t);
  }
  capsuleIntersect(t) {
    let i = {};
    if (this.mode === Vn.None)
      return !1;
    for (let n = 0; n < this._collisions.length; n++)
      Object.assign(i, this._collisions[n].capsuleIntersect(t));
    if (this.mode === Vn.Query || this.mode === Vn.QueryAndCollision)
      switch (this._collisionStatus) {
        case 0:
          i && (this.dispatchEvent("collision-start", this), this._collisionStatus = 1);
          break;
        case 1:
          i || (this.dispatchEvent("collision-end", this), this._collisionStatus = 2);
          break;
        case 2:
          this._collisionStatus = 0;
          break;
        default:
          this._collisionStatus = 0;
          break;
      }
    return this.mode === Vn.Query ? !1 : i;
  }
  intersects(t, i) {
    let n = !1;
    if (t.mode === Vn.None)
      return !1;
    for (let r = 0; r < this._collisions.length; r++)
      for (let a = 0; a < t.collisions.length; a++)
        if (this._collisions[r].intersects(
          t.collisions[a],
          this.worldPosition.clone().add(i),
          t.worldPosition
        )) {
          n = !0;
          break;
        }
    if (t.mode === Vn.Query || t.mode === Vn.QueryAndCollision)
      switch (t._collisionStatus) {
        case 0:
          n && (t.dispatchEvent("collision-start", t), t._collisionStatus = 1);
          break;
        case 1:
          n || (t.dispatchEvent("collision-end", t), t._collisionStatus = 2);
          break;
        case 2:
          t._collisionStatus = 0;
          break;
        default:
          t._collisionStatus = 0;
          break;
      }
    return t.mode === Vn.Query ? !1 : n;
  }
  // 单位是 s
  update(t) {
    var o, l;
    const i = this._force.getForce(), n = t, r = this._velocity.getVelocity().clone(), a = r.multiplyScalar(n).add(i.clone().multiplyScalar(n * n * 0.5));
    this._velocity.add(i.x * n, i.y * n, i.z * n), a.y + this.getWorldMinY() < 0 && (this._velocity.setVelocity(r.x, 0, r.z), a.y = 0), this.position.add(a), this.worldPosition.add(a), (l = (o = this.collisions[0]) == null ? void 0 : o.boxHelper.parent) == null || l.position.copy(this.worldPosition), i.x === 0 && i.y === 0 && i.z === 0 && (this.freeze = !0), r.x === 0 && r.y === 0 && r.z === 0 && (this.freeze = !0);
  }
}
var K6e = (s, e, t, i) => {
  for (var n = e, r = s.length - 1, a; r >= 0; r--)
    (a = s[r]) && (n = (a(n)) || n);
  return n;
};
class J6e {
  constructor() {
    S(this, "_now", Date.now());
    S(this, "_last", Date.now());
    S(this, "_delta", 0);
  }
  update() {
    this._now = Date.now(), this._delta = this._now - this._last, this._last = this._now;
  }
  getDelta() {
    return this._delta;
  }
}
new _();
class Q6e extends $4 {
  constructor() {
    super(...arguments);
    S(this, "_enable", !1);
    S(this, "_enableGravity", !0);
    S(this, "_onFloor", !1);
    S(this, "velocity", new _());
    S(this, "targetPosition", new _());
    S(this, "_object");
    S(this, "_moveDamping", 0);
  }
  get enable() {
    return this._enable;
  }
  set enable(t) {
    this._enable = t;
  }
  get enableGravity() {
    return this._enableGravity;
  }
  set enableGravity(t) {
    this._enableGravity = t;
  }
  get onFloor() {
    return this._onFloor;
  }
  set onFloor(t) {
    this._onFloor = t;
  }
  getPosition() {
    var t;
    return ((t = this.object) == null ? void 0 : t.position) ?? this.targetPosition;
  }
  get object() {
    return this._object;
  }
  set object(t) {
    this._object = t;
  }
  applyPosition() {
    var t;
    (t = this._object) == null || t.position.copy(this.end);
  }
  get moveDamping() {
    return this._moveDamping;
  }
  set moveDamping(t) {
    this._moveDamping = t;
  }
}
let QD = class extends es {
  constructor() {
    super();
    S(this, "_staticRigidBodyArr", []);
    S(this, "_bodyIds", /* @__PURE__ */ new Map());
    // body id 和 obj 的对应
    S(this, "_objectMap", /* @__PURE__ */ new Map());
    // private _enableGravityHeightMap: Map<string, { v: number; minY: number }> = new Map();
    S(this, "_horizontalPlane", 0);
    S(this, "_gravityCoefficient", 9.8);
    S(this, "_perFrame", 0.2);
    S(this, "_gravityDistance", this._perFrame * this._perFrame * 0.5 * this._gravityCoefficient);
    S(this, "_clock", new J6e());
    S(this, "_id", nt.generateUUID());
    S(this, "_rootHelper", new mt());
    S(this, "_playerCollision", new Q6e());
    const e = new $n(Number.MAX_SAFE_INTEGER / 2, Number.MAX_SAFE_INTEGER / 2);
    e.rotateX(-Math.PI / 2);
    const t = new Re(e);
    this._staticRigidBodyArr.push(
      Qg.fromObject(t, Vn.QueryAndCollision)
    );
  }
  get gravityCoefficient() {
    return this._gravityCoefficient;
  }
  set gravityCoefficient(e) {
    this._gravityCoefficient = e;
  }
  get playerCollision() {
    return this._playerCollision;
  }
  // 筛选与给定物体 可能相交的实体列表
  interferencesWith(e, t) {
    let i = [];
    return t.forEach((n) => {
      e.intersects(n, new _()) && (i = [e, n]);
    }), i;
  }
  showBoxHelper(e) {
    e.add(this._rootHelper);
  }
  translate(e, t, i) {
    const n = this._playerCollision.onFloor ? 25 : 8, r = t.clone().multiplyScalar(i * n).setY(0);
    this._playerCollision.velocity.add(r);
  }
  jump(e, t) {
    this._playerCollision.onFloor && (this._playerCollision.velocity.y = t);
  }
  removeRigidBody(e) {
    const t = this._bodyIds.get(e);
    t && this._objectMap.delete(t);
    const i = this._staticRigidBodyArr.findIndex((n) => n.id === t);
    i !== -1 && this._staticRigidBodyArr.splice(i, 1);
  }
  tick() {
    if (this._playerCollision.enable) {
      let t = Math.exp(-0.048) - 1;
      this._playerCollision.onFloor || (this.playerCollision.enableGravity && (this._playerCollision.velocity.y -= this._gravityCoefficient * 0.012 * 4), t *= 0.1);
      const i = this.playerCollision.moveDamping, n = 12 * Math.pow(i, 2) - 21 * i + 10;
      t *= n, this._playerCollision.velocity.addScaledVector(this._playerCollision.velocity, t);
      const r = this._playerCollision.velocity.clone().multiplyScalar(0.012 * n);
      this._playerCollision.translate(r);
      let a = {};
      if (this._playerCollision.onFloor = !1, this._staticRigidBodyArr.forEach((o) => {
        Object.assign(a, o.capsuleIntersect(this._playerCollision));
      }), a.normal) {
        if (this._playerCollision.onFloor = a.normal.y > 0, !this._playerCollision.onFloor) {
          const o = new _(0, 1, 0);
          this._playerCollision.velocity.addScaledVector(o, -o.dot(this._playerCollision.velocity));
        }
        a.depth >= 1e-10 && this._playerCollision.translate(a.normal.multiplyScalar(a.depth));
      }
      this._playerCollision.applyPosition();
    }
    this._clock.update();
  }
  setMaxStep(e) {
  }
  // public setGravityConfig(enable: boolean, object: THREE.Object3D): void {
  //   const id = this._bodyIds.get(object);
  //   if (enable) {
  //     const index = this._staticRigidBodyArr.findIndex((c) => c.id === id);
  //     if (index !== -1) {
  //       // 把原先 静态的 转为动态的
  //       this._staticRigidBodyArr[index].enableGravity = true;
  //       this._staticRigidBodyArr[index].force.init(0, -this._gravityCoefficient, 0);
  //       this._staticRigidBodyArr[index].freeze = false;
  //       this._dynamicRigidBodyArr.push(this._staticRigidBodyArr[index]);
  //       this._staticRigidBodyArr.splice(index, 1);
  //     }
  //   } else {
  //     const index = this._dynamicRigidBodyArr.findIndex((c) => c.id === id);
  //     if (index !== -1) {
  //       // 把原先 静态的 转为动态的
  //       this._dynamicRigidBodyArr[index].enableGravity = false;
  //       this._dynamicRigidBodyArr[index].force.init(0, 0, 0);
  //       this._staticRigidBodyArr.push(this._dynamicRigidBodyArr[index]);
  //       this._dynamicRigidBodyArr.splice(index, 1);
  //     }
  //   }
  // }
  updateObjectPosition(e, t) {
  }
  setPlayerController(e) {
    const { start: t, end: i, radius: n, object: r, enable: a } = e;
    this._playerCollision.set(t, i, n), this._playerCollision.object = r, this._playerCollision.velocity.set(0, 0, 0), this._playerCollision.enable = a, this._playerCollision.onFloor = !1;
  }
  createRigidBody(e, t = !1) {
    const { object: i } = e;
    if (!this._bodyIds.get(i) || t) {
      const n = Qg.fromObject(i, e.mode);
      this._staticRigidBodyArr.push(n), this._bodyIds.set(i, n.id), this._objectMap.set(n.id, i);
      const r = (a) => {
        const { entity: o } = e;
        if (o.hasComponent("Model")) {
          const l = o.getComponentByType("Model");
          if (l) {
            const c = {
              entityId: e.entity.uuid,
              componentId: l.id,
              objId: i.uuid,
              customAttribute: l.customAttributes,
              id: o.uuid,
              name: o.name,
              position: l.group.position,
              sceneGraph: l.getModelSceneGraph(),
              meshName: ""
            };
            o.world.emitFunc && o.world.emitFunc(a, c, o.uuid);
          }
        }
      };
      n.addEventListener("collision-start", () => {
        r("collision");
      }), n.addEventListener("collision-end", () => {
        r("collisionEnd");
      });
    }
  }
  dispose() {
    this._staticRigidBodyArr.length = 0;
  }
};
QD = K6e([
  xee("EasyTwinPhysicService")
], QD);

class FederatedEventTarget {
    parent;
    cursor = 'default';
    emitter = new EventEmitter();
    addEventListener(type, listener, options) {
        const capture = (typeof options === 'boolean' && options) || (typeof options === 'object' && options.capture);
        const signal = typeof options === 'object' ? options.signal : undefined;
        const once = typeof options === 'object' ? options.once === true : false;
        type = capture ? `${type}capture` : type;
        const listenerFn = typeof listener === 'function' ? listener : listener.handleEvent;
        if (signal) {
            signal.addEventListener('abort', () => {
                this.emitter.off(type, listenerFn);
            });
        }
        if (once) {
            this.emitter.once(type, listenerFn);
        }
        else {
            this.emitter.on(type, listenerFn);
        }
    }
    removeEventListener(type, listener, options) {
        const capture = (typeof options === 'boolean' && options) || (typeof options === 'object' && options.capture);
        type = capture ? `${type}capture` : type;
        listener = typeof listener === 'function' ? listener : listener.handleEvent;
        this.emitter.off(type, listener);
    }
    dispatchEvent(e) {
        const canvas = this.ownerCanvas;
        e.manager = canvas.getEventSystem();
        e.target = this;
        if (!e.manager)
            return false;
        e.manager.dispatchEvent(e, e.type);
        return !e.defaultPrevented;
    }
}

class Transform {
    owner;
    /** Local Space */
    localMatrix = create$3();
    localPosition = create$2();
    localRotation = create();
    localScale = fromValues(1, 1, 1);
    /** World Space */
    worldMatrix = create$3();
    worldMatrixInverse = create$3();
    worldPosition = create$2();
    worldRotation = create();
    worldScale = create$2();
    // will be dirtified by self changed or parent changed
    dirtyFlag = false;
    // will be dirtified by self changed only.
    localDirtyFlag = false;
    constructor(owner) {
        this.owner = owner;
    }
    /** sync self */
    syncMatrix = () => {
        if (this.localDirtyFlag) {
            fromRotationTranslationScale(this.localMatrix, this.localRotation, this.localPosition, this.localScale);
            this.localDirtyFlag = false;
        }
        if (this.dirtyFlag) {
            const parent = this.owner.parent;
            if (isNil(parent)) {
                copy$3(this.worldMatrix, this.localMatrix);
            }
            else {
                multiply$1(this.worldMatrix, parent.transform.worldMatrix, this.localMatrix);
            }
            invert$1(this.worldMatrixInverse, this.worldMatrix);
            getTranslation(this.worldPosition, this.worldMatrix);
            getRotation(this.worldRotation, this.worldMatrix);
            getScaling(this.worldScale, this.worldMatrix);
            this.dirtyFlag = false;
        }
    };
    /** sync self and children */
    syncRecursive = () => {
        this.syncMatrix();
        for (const child of this.owner.children) {
            child.transform.syncRecursive();
        }
    };
    syncFromParentsToChildren = (updateParents, updateChildren) => {
        if (updateParents) {
            const parent = this.owner.parent;
            if (!isNil(parent)) {
                parent.transform.syncFromParentsToChildren(true, false);
            }
        }
        this.syncMatrix();
        if (updateChildren) {
            for (const child of this.owner.children) {
                child.transform.syncFromParentsToChildren(false, true);
            }
        }
    };
    /** local to parent */
    getLocalTransform = () => {
        return this.localMatrix;
    };
    /** local to world */
    getWorldTransform = () => {
        this.syncFromParentsToChildren(true, false);
        return this.worldMatrix;
    };
    /** world to local */
    getWorldTransformInverse = () => {
        this.syncFromParentsToChildren(true, false);
        return this.worldMatrixInverse;
    };
    getPosition = () => {
        this.syncFromParentsToChildren(true, false);
        return this.worldPosition;
    };
    setPosition = (pos) => {
        this.syncFromParentsToChildren(true, false);
        if (equals(this.worldPosition, pos))
            return;
        copy$2(this.worldPosition, pos);
        const parent = this.owner.parent;
        if (isNil(parent)) {
            copy$2(this.localPosition, pos);
        }
        else {
            transformMat4$1(this.localPosition, pos, parent.transform.getWorldTransformInverse());
        }
        this.dirtifyLocal();
        return this;
    };
    getRotation = () => {
        this.syncFromParentsToChildren(true, false);
        return this.worldRotation;
    };
    setRotation = (q) => {
        this.syncFromParentsToChildren(true, false);
        const parent = this.owner.parent;
        if (isNil(parent)) {
            copy(this.localRotation, q);
        }
        else {
            const parentQuat = parent.transform.worldRotation;
            const parentQuatInverse = invert(create(), parentQuat);
            multiply(this.localRotation, parentQuatInverse, q);
            normalize(this.localRotation, this.localRotation);
        }
        this.dirtifyLocal();
        return this;
    };
    getScale = () => {
        this.syncFromParentsToChildren(true, false);
        return this.worldScale;
    };
    getLocalPosition = () => {
        return this.localPosition;
    };
    setLocalPosition = (pos) => {
        const p = createVec3(pos);
        if (equals(this.localPosition, p))
            return;
        this.localPosition = p;
        this.dirtifyLocal();
        return this;
    };
    getLocalRotation = () => {
        return this.localRotation;
    };
    setLocalRotition = (q) => {
        this.localRotation = q;
        this.dirtifyLocal();
        return this;
    };
    getLocalScale = () => {
        return this.localScale;
    };
    /**
     * Apply scale
     */
    setLocalScale(sx, sy, sz = this.localScale[2]) {
        const scale = fromValues(sx, sy, sz);
        if (equals(scale, this.localScale))
            return this;
        copy$2(this.localScale, scale);
        this.dirtifyLocal();
        return this;
    }
    /**
     * Apply translation to world position
     */
    translate = (() => {
        const tmpVec3 = create$2();
        const tmpPos = create$2();
        return (x, y) => {
            if (x === 0 && y === 0)
                return this;
            const move = set(tmpVec3, x, y, 0);
            add(tmpPos, this.getPosition(), move);
            this.setPosition(tmpPos);
            this.dirtifyLocal();
            return this;
        };
    })();
    /** localMatrix will be recomposed in next frame */
    dirtifyLocal = () => {
        this.localDirtyFlag = true;
        if (!this.dirtyFlag) {
            this.dirtyWorld();
        }
    };
    /** matrix will be recomposed in next frame, and notify children / parents this has been updated */
    dirtyWorld = () => {
        this.dirtyFlag = true;
        dirtifySelfAndDescendant(this.owner);
        dirtifyAncestorBounds(this.owner);
    };
    toFloat32Array() {
        const m = this.worldMatrix;
        return Float32Array.of(m[0], m[4], m[12], m[1], m[5], m[13], 0, 0, 1);
    }
}
const dirtifySelfAndDescendant = (object) => {
    const { transform, renderable } = object;
    transform.dirtyFlag = true;
    renderable.dirty = true;
    object.children.forEach((child) => {
        dirtifySelfAndDescendant(child);
    });
};
const dirtifyAncestorBounds = (object) => {
    object.renderable.dirty = true;
    let p = object;
    while (p) {
        p.renderable.boundsDirty = true;
        p = p.parent;
    }
};

class DisplayObject extends FederatedEventTarget {
    id;
    name;
    type;
    style;
    visible = true;
    transform = new Transform(this);
    sortable = {
        dirty: false,
        renderOrder: 0,
        sorted: undefined,
    };
    renderable = {
        dirty: false,
        boundsDirty: false,
    };
    ownerCanvas;
    children = [];
    constructor(config) {
        super();
        this.id = config.id;
        this.name = config.name;
        this.type = config.type;
        this.style = config.style;
    }
    toJSON = () => {
        return {
            id: this.id,
            name: this.name,
            type: this.type,
            style: this.style,
        };
    };
    setOptions(options) {
        merge(this.style, options);
        this.transform.dirtifyLocal();
    }
    getWorldTransform = () => {
        return this.transform.getWorldTransform();
    };
    addChild = (drawable) => {
        this.children.push(drawable);
        drawable.parent = this;
        drawable.transform.dirtyWorld();
    };
    removeChild = (drawable) => {
        const index = this.children.findIndex((a) => a === drawable);
        if (index === -1) {
            console.warn('drawable is not exit');
            return;
        }
        drawable.parent = undefined;
        this.children.splice(index, 1);
    };
    removeAllChildren = () => {
        for (const c of this.children) {
            c.parent = undefined;
        }
        this.children.length = 0;
    };
    worldToLocal = (point) => {
        const tmpVec3 = fromValues(point.x, point.y, 0);
        const res = transformMat4$1(tmpVec3, tmpVec3, this.transform.getWorldTransformInverse());
        return { x: res[0], y: res[1] };
    };
    localToWorld = (point) => {
        const tmpVec3 = fromValues(point.x, point.y, 0);
        const res = transformMat4$1(tmpVec3, tmpVec3, this.transform.getWorldTransform());
        return { x: res[0], y: res[1] };
    };
}

var Shape;
(function (Shape) {
    Shape["Group"] = "group";
    Shape["Circle"] = "circle";
    Shape["ControlCircle"] = "control_circle";
    Shape["Curve"] = "curve";
    Shape["Line"] = "line";
    Shape["OffsetLine"] = "offset_line";
    Shape["Polygon"] = "polygon";
})(Shape || (Shape = {}));
var ContextMenuType;
(function (ContextMenuType) {
    ContextMenuType[ContextMenuType["Handle"] = 0] = "Handle";
})(ContextMenuType || (ContextMenuType = {}));

class Signal {
    listeners = [];
    active = true;
    on = (fn) => {
        this.listeners.push(fn);
    };
    addListener = this.on;
    emit = (...args) => {
        if (!this.active) {
            return;
        }
        for (const fn of this.listeners) {
            fn.apply(this, args);
        }
    };
    off = (fn) => {
        for (let i = 0; i < this.listeners.length; i++) {
            if (fn === this.listeners[i]) {
                this.listeners.splice(i, 1);
            }
        }
    };
    removeListener = this.off;
    removeAllListeners = () => {
        this.listeners = [];
    };
    once = (fn) => {
        const onceWrapper = (...args) => {
            this.off(onceWrapper);
            fn.apply(this, args);
        };
        this.on(onceWrapper);
    };
}

const DEFAULT_CIRCLE_CONFIG = {
    center: { x: 0, y: 0 },
    radius: 5,
    hitBias: 0.1,
};
class Circle extends DisplayObject {
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_CIRCLE_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'Circle',
            type: Shape.Circle,
            style: parsedConfig,
        });
    }
    isPointHit = (point) => {
        const { center, radius, hitBias } = this.style;
        const realCenter = this.ownerCanvas.canvas2Viewport(center);
        return distance(point, realCenter) <= radius + hitBias;
    };
}

const DEFAULT_LINE_CONFIG = {
    begin: { x: 0, y: 0 },
    end: { x: 2, y: 0 },
    hitBias: 0.1,
};
class Line extends DisplayObject {
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_LINE_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'line',
            type: Shape.Line,
            style: parsedConfig,
        });
    }
    isPointHit = (point) => {
        // const localP = this.worldToLocal(point);
        // const { begin, end, hitBias } = this.style;
        // return pointToSegment(localP.x, localP.y, begin.x, begin.y, end.x, end.y) <= hitBias;
        return false;
    };
}

class Handle {
    circle;
    signals = {
        onDrag: new Signal(),
        onDragEnd: new Signal(),
    };
    constructor(circle, opts) {
        this.circle = circle;
        circle.addEventListener('pointerdown', this.onDragStart);
        circle.addEventListener('pointerup', this.onDragEnd);
        circle.addEventListener('pointerupoutside', this.onDragEnd);
        opts?.onContextMenu && circle.addEventListener('rightclick', opts.onContextMenu);
        circle.cursor = 'pointer';
    }
    setOptions(options) {
        this.circle.setOptions(options);
    }
    onDragStart = () => {
        this.circle.ownerCanvas.root.addEventListener('pointermove', this.onDragMove);
    };
    onDragMove = (e) => {
        const localP = this.circle.worldToLocal({ x: e.globalX, y: e.globalY });
        this.signals.onDrag.emit(localP);
    };
    onDragEnd = (e) => {
        this.circle.ownerCanvas.root.removeEventListener('pointermove', this.onDragMove);
        const localP = this.circle.worldToLocal({ x: e.globalX, y: e.globalY });
        this.signals.onDrag.emit(localP);
        this.signals.onDragEnd.emit(localP);
    };
}

const DEFAULT_CURVE_CONFIG = {
    disable: false,
    lineStyle: {
        hitBias: 2,
        lineWidth: 2,
        strokeStyle: '#757881',
    },
    handleStyle: {
        fillStyle: '#7262fd',
        radius: 3,
        hitBias: 2,
    },
    keyStyle: {
        fillStyle: '#ffffff',
        radius: 3,
        hitBias: 2,
    },
    lineWidth: 1,
};
const EPSILON = 10e-4;
class Curve extends DisplayObject {
    type = Shape.Curve;
    curve;
    signals = {
        onDragEnd: new Signal(),
    };
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_CURVE_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'Line',
            type: Shape.Curve,
            style: parsedConfig,
        });
        // this.cursor = 'pointer';
        // this.addEventListener('click', (e) => {
        //   if (e.target !== this) return;
        //   if (!isNil(this.curve)) {
        //     const time = e.globalX;
        //     const value = this.curve.genValue(time);
        //     const keyFrame = {
        //       time,
        //       value,
        //       tangentMode: TangentMode.User,
        //       weightMode: WeightMode.Both,
        //       interpMode: InterpMode.Cubic,
        //       inTangent: 0,
        //       outTangent: 0,
        //       inWeight: 0.3,
        //       outWeight: 0.3,
        //     };
        //     this.curve.addKey(keyFrame);
        //     this.signals.onDragEnd.emit(this.curve?.toJSON());
        //   }
        // });
    }
    setCurve = (curve) => {
        this.curve = curve;
        this.removeAllChildren();
        if (isNil(curve))
            return;
        for (let j = 0; j < curve.keys.length; j += 1) {
            const key = curve.keys[j];
            !this.style.disable && this.createHandle(curve, key);
        }
    };
    isControlEnabled = (tangentMode, interpMode) => {
        return (interpMode === Gf.Cubic &&
            (tangentMode === bb.User || tangentMode === bb.Break));
    };
    createHandle = (curve, key) => {
        const lineStyle = this.style.lineStyle;
        const handleStyle = this.style.handleStyle;
        const keyStyle = this.style.keyStyle;
        // draw key handle
        const keyCircle = this.ownerCanvas.createElement(Circle, {
            name: 'handle',
            style: {
                ...keyStyle,
                center: {
                    x: key.time,
                    y: key.value,
                },
            },
        });
        const keyHandle = new Handle(keyCircle, {
            onContextMenu: (e) => {
                keyCircle.ownerCanvas.eventEmitter.emit('DisplayObjectContextMenu', e, key, ContextMenuType.Handle);
            },
        });
        keyHandle.signals.onDrag.on((pos) => {
            pos = this.clampPos(pos);
            const index = curve.keys.findIndex((a) => a === key);
            key.time = pos.x;
            key.value = pos.y;
            curve.moveKey(index, key);
            keyHandle.setOptions({ center: pos });
        });
        keyHandle.signals.onDragEnd.on(() => {
            this.signals.onDragEnd.emit(this.curve?.toJSON());
        });
        this.addChild(keyCircle);
        if (this.isControlEnabled(key.tangentMode, key.interpMode)) {
            const { time, value, inTangent, outTangent, inWeight, outWeight, tangentMode } = key;
            const index = curve.keys.findIndex((a) => a === key);
            // draw left control
            if (sie(key) && index !== 0) {
                const angle = Math.atan(inTangent);
                const cx = time - Math.cos(angle) * inWeight;
                const cy = value - Math.sin(angle) * inWeight;
                const inLine = this.ownerCanvas.createElement(Line, {
                    name: 'in line',
                    style: {
                        ...lineStyle,
                        begin: {
                            x: cx,
                            y: cy,
                        },
                        end: { x: time, y: value },
                    },
                });
                inLine.sortable.renderOrder = -1;
                const inControlCircle = this.ownerCanvas.createElement(Circle, {
                    name: 'in handle',
                    style: {
                        ...handleStyle,
                        center: {
                            x: cx,
                            y: cy,
                        },
                    },
                });
                keyHandle.signals.onDrag.on((pos) => {
                    pos = this.clampPos(pos);
                    const angle = Math.atan(key.inTangent);
                    const x = pos.x - Math.cos(angle) * key.inWeight;
                    const y = pos.y - Math.sin(angle) * key.inWeight;
                    inLine.setOptions({ begin: { x, y }, end: pos });
                    inControlCircle.setOptions({ center: { x, y } });
                });
                keyHandle.signals.onDragEnd.on(() => {
                    this.signals.onDragEnd.emit(this.curve?.toJSON());
                });
                this.addChild(inLine);
                this.addChild(inControlCircle);
                const inHandle = new Handle(inControlCircle);
                inHandle.signals.onDrag.on((pos) => {
                    const index = curve.keys.findIndex((a) => a === key);
                    if (pos.x >= key.time)
                        pos.x = key.time - EPSILON;
                    key.inTangent = (pos.y - key.value) / (pos.x - key.time);
                    key.inWeight = length(pos.x - key.time, pos.y - key.value);
                    curve.moveKey(index, key);
                    inHandle.setOptions({ center: pos });
                    inLine.setOptions({ begin: pos });
                });
                inHandle.signals.onDragEnd.on(() => {
                    this.signals.onDragEnd.emit(this.curve?.toJSON());
                });
            }
            // draw right control
            if (rie(key) && index !== curve.length - 1) {
                const angle = Math.atan(outTangent);
                const cx = time + Math.cos(angle) * outWeight;
                const cy = value + Math.sin(angle) * outWeight;
                const outLine = this.ownerCanvas.createElement(Line, {
                    name: 'out line',
                    style: {
                        ...lineStyle,
                        begin: {
                            x: cx,
                            y: cy,
                        },
                        end: { x: time, y: value },
                    },
                });
                outLine.sortable.renderOrder = -1;
                const outControlCircle = this.ownerCanvas.createElement(Circle, {
                    name: 'out handle',
                    style: {
                        ...handleStyle,
                        center: {
                            x: cx,
                            y: cy,
                        },
                    },
                });
                keyHandle.signals.onDrag.on((pos) => {
                    pos = this.clampPos(pos);
                    const angle = Math.atan(key.outTangent);
                    const x = pos.x + Math.cos(angle) * key.outWeight;
                    const y = pos.y + Math.sin(angle) * key.outWeight;
                    outLine.setOptions({ begin: { x, y }, end: pos });
                    outControlCircle.setOptions({ center: { x, y } });
                });
                keyHandle.signals.onDragEnd.on(() => {
                    this.signals.onDragEnd.emit(this.curve?.toJSON());
                });
                this.addChild(outLine);
                this.addChild(outControlCircle);
                const outHandle = new Handle(outControlCircle);
                outHandle.signals.onDrag.on((pos) => {
                    const index = curve.keys.findIndex((a) => a === key);
                    if (pos.x <= key.time)
                        pos.x = key.time + EPSILON;
                    key.outTangent = (pos.y - key.value) / (pos.x - key.time);
                    key.outWeight = length(pos.x - key.time, pos.y - key.value);
                    curve.moveKey(index, key);
                    outHandle.setOptions({ center: pos });
                    outLine.setOptions({ begin: pos });
                });
                outHandle.signals.onDragEnd.on(() => {
                    this.signals.onDragEnd.emit(this.curve?.toJSON());
                });
            }
        }
    };
    clampPos = (pos) => {
        let x = !isNil(this.style.minX) ? Math.max(this.style.minX, pos.x + EPSILON) : pos.x;
        x = !isNil(this.style.maxX) ? Math.min(this.style.maxX - EPSILON, x) : x;
        return { x, y: pos.y };
    };
    isPointHit = (point) => {
        return isPointInPath(point, this.curve, this.ownerCanvas);
    };
}
const isPointInPath = (point, curve, canvas) => {
    if (isNil(curve))
        return false;
    if (curve.keys.length === 0)
        return false;
    const ctx = canvas.getContextSystem().getContext();
    ctx.strokeStyle = 'opacity';
    ctx.beginPath();
    const key0 = curve.keys[0];
    const a = canvas.canvas2Viewport({ x: key0.time, y: key0.value });
    ctx.moveTo(a.x, a.y);
    const keys = curve.keys;
    for (let j = 1; j < curve.keys.length; j++) {
        const prevKey = keys[j - 1];
        const nowKey = keys[j];
        switch (prevKey.interpMode) {
            case Gf.Constant: {
                const a = canvas.canvas2Viewport({ x: nowKey.time, y: prevKey.value });
                ctx.lineTo(a.x, a.y);
                const b = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                ctx.lineTo(b.x, b.y);
                break;
            }
            case Gf.Linaer: {
                const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                ctx.lineTo(a.x, a.y);
                break;
            }
            case Gf.Cubic: {
                // not weighted curve
                if (nie(prevKey, nowKey)) {
                    const dt = nowKey.time - prevKey.time;
                    const oneThird = 1 / 3;
                    const p1x = prevKey.time + oneThird * dt;
                    const p2x = nowKey.time - oneThird * dt;
                    const p1y = prevKey.value + prevKey.outTangent * dt * oneThird;
                    const p2y = nowKey.value - nowKey.inTangent * dt * oneThird;
                    const p1 = canvas.canvas2Viewport({ x: p1x, y: p1y });
                    const p2 = canvas.canvas2Viewport({ x: p2x, y: p2y });
                    const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                    ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, a.x, a.y);
                }
                // weighted curve
                else {
                    const getp1p2 = (key1, key2) => {
                        const t1 = key1.time;
                        const t2 = key2.time;
                        const dt = t2 - t1;
                        const oneThird = 1 / 3;
                        let prevWeight = 0;
                        if (rie(key1)) {
                            prevWeight = key1.outWeight;
                        }
                        else {
                            const x = dt;
                            const y = x * key1.outTangent;
                            prevWeight = Math.sqrt(x * x + y * y) * oneThird;
                        }
                        const angle1 = Math.atan(key1.outTangent);
                        const tx1 = Math.cos(angle1) * prevWeight + t1;
                        const ty1 = Math.sin(angle1) * prevWeight + key1.value;
                        let nextWeight = 0;
                        if (sie(key2)) {
                            nextWeight = key2.inWeight;
                        }
                        else {
                            const x = dt;
                            const y = x * key2.inTangent;
                            nextWeight = Math.sqrt(x * x + y * y) * oneThird;
                        }
                        const angle2 = Math.atan(key2.inTangent);
                        const tx2 = t2 - Math.cos(angle2) * nextWeight;
                        const ty2 = key2.value - Math.sin(angle2) * nextWeight;
                        return [tx1, ty1, tx2, ty2];
                    };
                    const [p1x, p1y, p2x, p2y] = getp1p2(prevKey, nowKey);
                    const p1 = canvas.canvas2Viewport({ x: p1x, y: p1y });
                    const p2 = canvas.canvas2Viewport({ x: p2x, y: p2y });
                    const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                    ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, a.x, a.y);
                }
                break;
            }
        }
        ctx.stroke();
    }
    return ctx.isPointInStroke(point.x, point.y);
};

const DEFAULT_POLYGON_CONFIG = {
    points: [],
};
class Polygon extends DisplayObject {
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_POLYGON_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'Polygon',
            type: Shape.Polygon,
            style: parsedConfig,
        });
    }
    isPointHit = (point) => {
        return isPointInShape(this.style.points, point);
    };
}

const DEFAULT_GROUP_CONFIG = {
    center: { x: 0, y: 0 },
};
class Group extends DisplayObject {
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_GROUP_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'Group',
            type: Shape.Group,
            style: parsedConfig,
        });
    }
    isPointHit = (point) => {
        // FIXME should be false when point out of canvas.
        return true;
    };
}

const DEFAULT_OFFSET_LINE_CONFIG = {
    beginOffset: { x: 0, y: 0 },
    endOffset: { x: 2, y: 0 },
    origin: { x: 0, y: 0 },
    lengthFactor: 1,
    hitBias: 0.1,
};
class OffsetLine extends DisplayObject {
    constructor(config) {
        const parsedConfig = merge({}, DEFAULT_OFFSET_LINE_CONFIG, config.style);
        super({
            id: config.id || genUUID(''),
            name: config.name || 'OffsetLine',
            type: Shape.OffsetLine,
            style: parsedConfig,
        });
    }
    isPointHit = (point) => {
        // const localP = this.worldToLocal(point);
        // const { begin, end, hitBias } = this.style;
        // return pointToSegment(localP.x, localP.y, begin.x, begin.y, end.x, end.y) <= hitBias;
        return false;
    };
}

class SyncHook {
    callbacks = [];
    tap(fn) {
        this.callbacks.push(fn);
    }
    call(...args) {
        let res = undefined;
        this.callbacks.forEach((callback) => {
            res = callback(...args);
        });
        return res;
    }
}

class RenderingSystem {
    context;
    renderListCurrentFrame = [];
    stats = {
        /**
         * the count of display objects in scene
         */
        totalCount: 0,
        /**
         * the count of display objects need to be rerendered in this frame
         */
        rerenderedCount: 0,
    };
    hooks = {
        init: new SyncHook(),
        cull: new SyncHook(),
        /**
         * called at the beginning of every frame
         */
        beginFrame: new SyncHook(),
        /**
         * called before every object is rendered
         */
        beforeRender: new SyncHook(),
        /**
         * called when every object is renderings
         */
        render: new SyncHook(),
        /**
         * called after every object is rendered
         */
        afterRender: new SyncHook(),
        /**
         * called at the end of every frame
         */
        endFrame: new SyncHook(),
        destroy: new SyncHook(),
    };
    constructor(context) {
        this.context = context;
    }
    init = () => {
        this.hooks.init.call();
    };
    dispose = () => {
        this.hooks.destroy.call();
    };
    render = () => {
        this.stats = {
            totalCount: 0,
            rerenderedCount: 0,
        };
        const root = this.context.renderingContext.root;
        root.transform.syncRecursive();
        this.renderDisplayObject(root, this.context.renderingContext);
        // console.log(this.stats);
        this.hooks.beginFrame.call();
        this.hooks.beforeRender.call();
        this.hooks.render.call(this.renderListCurrentFrame);
        this.hooks.afterRender.call();
        this.hooks.endFrame.call();
        this.renderListCurrentFrame = [];
    };
    /** Collect objects to render */
    renderDisplayObject(displayObject, renderingContext) {
        // TODO: dirty check first
        displayObject.renderable;
        {
            // TODO: cull
            const needRender = this.hooks.cull.call(displayObject, this.context.camera);
            if (needRender) {
                this.stats.rerenderedCount++;
                this.renderListCurrentFrame.push(displayObject);
            }
        }
        this.stats.totalCount++;
        displayObject.children.forEach((child) => {
            this.renderDisplayObject(child, renderingContext);
        });
    }
}

class FederatedEvent {
    NONE = 0;
    CAPTURING_PHASE = 1;
    AT_TARGET = 2;
    BUBBLING_PHASE = 3;
    manager;
    eventPhase = 0;
    target = null;
    currentTarget = null;
    path = [];
    /** Event-specific detail */
    detail;
    /**
     * the original event.
     */
    nativeEvent = null;
    originalEvent = null;
    /** Flags whether propagation was stopped. */
    propagationStopped = false;
    /** Flags whether the default response of the user agent was prevent through this event. */
    defaultPrevented = false;
    /** Flags whether propagation was immediately stopped. */
    propagationImmediatelyStopped = false;
    type = '';
    /**
     * timestamp when the event created
     */
    timeStamp = 0;
    /**
     * The coordinates of the event relative to the DOM document.
     * This is a non-standard property.
     * relative to the DOM document.
     * @see https://developer.mozilla.org/zh-CN/docs/Web/API/MouseEvent/pageX
     */
    page = new Point();
    get pageX() {
        return this.page.x;
    }
    get pageY() {
        return this.page.y;
    }
    /**
     * world space, account for Camera
     */
    canvas = new Point();
    get canvasX() {
        return this.canvas.x;
    }
    get canvasY() {
        return this.canvas.y;
    }
    /**
     * relative to HTMLCanvasElement, origin is left-top
     */
    viewport = new Point();
    get viewportX() {
        return this.viewport.x;
    }
    get viewportY() {
        return this.viewport.y;
    }
    constructor(manager) {
        this.manager = manager;
    }
    preventDefault() {
        if (this.nativeEvent instanceof Event && this.nativeEvent.cancelable) {
            this.nativeEvent.preventDefault();
        }
        this.defaultPrevented = true;
    }
    stopImmediatePropagation() {
        this.propagationImmediatelyStopped = true;
    }
    stopPropagation() {
        this.propagationStopped = true;
    }
    /** return [target, parent, root] */
    composedPath() {
        if (this.manager && (!this.path || this.path[0] !== this.target)) {
            this.path = this.target ? this.manager.propagationPath(this.target) : [];
        }
        return this.path;
    }
}

class FederatedMouseEvent extends FederatedEvent {
    /** Whether the "alt" key was pressed when this mouse event occurred. */
    altKey = false;
    /** The specific button that was pressed in this mouse event. */
    button = 0;
    /** The button depressed when this event occurred. */
    buttons = 0;
    /** Whether the "control" key was pressed when this mouse event occurred. */
    ctrlKey = false;
    /** Whether the "meta" key was pressed when this mouse event occurred. */
    metaKey = false;
    /** This is currently not implemented in the Federated Events API. */
    relatedTarget = null;
    /** Whether the "shift" key was pressed when this mouse event occurred. */
    shiftKey = false;
    /**
     * The coordinates of the mouse event relative to the canvas.
     */
    client = new Point();
    get clientX() {
        return this.client.x;
    }
    get clientY() {
        return this.client.y;
    }
    /**
     * The movement in this pointer relative to the last `mousemove` event.
     */
    movement = new Point();
    get movementX() {
        return this.movement.x;
    }
    get movementY() {
        return this.movement.y;
    }
    /**
     * The pointer coordinates in world space.
     */
    global = new Point();
    get globalX() {
        return this.global.x;
    }
    get globalY() {
        return this.global.y;
    }
    /**
     * The pointer coordinates in screen space, from native event.
     */
    screen = new Point();
    get screenX() {
        return this.screen.x;
    }
    get screenY() {
        return this.screen.y;
    }
    getModifierState(key) {
        if (!this.nativeEvent)
            return false;
        return 'getModifierState' in this.nativeEvent && this.nativeEvent.getModifierState(key);
    }
}

class FederatedPointerEvent extends FederatedMouseEvent {
    /**
     * The unique identifier of the pointer.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/pointerId
     */
    pointerId = 0;
    /**
     * The width of the pointer's contact along the x-axis, measured in CSS pixels.
     * radiusX of TouchEvents will be represented by this value.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/width
     */
    width = 0;
    /**
     * The height of the pointer's contact along the y-axis, measured in CSS pixels.
     * radiusY of TouchEvents will be represented by this value.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/height
     */
    height = 0;
    /**
     * Indicates whether or not the pointer device that created the event is the primary pointer.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/isPrimary
     */
    isPrimary = false;
    /**
     * The type of pointer that triggered the event.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/pointerType
     */
    pointerType = '';
    /**
     * Pressure applied by the pointing device during the event.
     *s
     * A Touch's force property will be represented by this value.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/pressure
     */
    pressure = 0;
    /**
     * Barrel pressure on a stylus pointer.
     *
     * @see https://w3c.github.io/pointerevents/#pointerevent-interface
     */
    tangentialPressure = 0;
    /**
     * The angle, in degrees, between the pointer device and the screen.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/tiltX
     */
    tiltX = 0;
    /**
     * The angle, in degrees, between the pointer device and the screen.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/tiltY
     */
    tiltY = 0;
    /**
     * Twist of a stylus pointer.
     *
     * @see https://w3c.github.io/pointerevents/#pointerevent-interface
     */
    twist = 0;
}

/**
 * A {@link PIXI.FederatedEvent} for wheel events.
 * @memberof PIXI
 */
class FederatedWheelEvent extends FederatedMouseEvent {
    /**
     * The units of `deltaX`, `deltaY`, and `deltaZ`. This is one of `DOM_DELTA_LINE`,
     * `DOM_DELTA_PAGE`, `DOM_DELTA_PIXEL`.
     */
    deltaMode = 0;
    /** Horizontal scroll amount */
    deltaX = 0;
    /** Vertical scroll amount */
    deltaY = 0;
    /** z-axis scroll amount. */
    deltaZ = 0;
    /** Units specified in pixels. */
    DOM_DELTA_PIXEL = 0;
    /** Units specified in lines. */
    DOM_DELTA_LINE = 1;
    /** Units specified in pages. */
    DOM_DELTA_PAGE = 2;
}

const PROPAGATION_LIMIT = 2048;
class EventSystem {
    context;
    cursor = 'default';
    rootTarget;
    mappingTable = {};
    mappingState = {
        trackingData: {},
    };
    eventPool = new Map();
    constructor(context) {
        this.context = context;
    }
    init() {
        this.rootTarget = this.context.renderingContext.root;
        this.addEventMapping('pointerdown', this.onPointerDown);
        this.addEventMapping('pointerup', this.onPointerUp);
        this.addEventMapping('pointermove', this.onPointerMove);
        this.addEventMapping('pointerout', this.onPointerOut);
        this.addEventMapping('pointerleave', this.onPointerOut);
        this.addEventMapping('pointercancel', this.onPointerCancel);
        this.addEventMapping('pointerover', this.onPointerOver);
        this.addEventMapping('pointerupoutside', this.onPointerUpOutside);
        this.addEventMapping('wheel', this.onWheel);
    }
    destroy() {
        this.mappingTable = {};
        this.mappingState = {};
        this.eventPool.clear();
    }
    addEventMapping(type, fn) {
        if (!this.mappingTable[type]) {
            this.mappingTable[type] = [];
        }
        this.mappingTable[type].push({
            fn,
            priority: 0,
        });
        this.mappingTable[type].sort((a, b) => a.priority - b.priority);
    }
    mapEvent(e) {
        if (!this.rootTarget) {
            return;
        }
        const mappers = this.mappingTable[e.type];
        if (mappers) {
            for (let i = 0, j = mappers.length; i < j; i++) {
                mappers[i].fn(e);
            }
        }
        else {
            console.warn(`[EventBoundary]: Event mapping not defined for ${e.type}`);
        }
    }
    dispatchEvent(e, type) {
        e.propagationStopped = false;
        e.propagationImmediatelyStopped = false;
        this.propagate(e, type);
    }
    propagate(e, type) {
        // This usually occurs when the scene graph is not interactive.
        if (!e.target) {
            return;
        }
        // see https://www.w3.org/TR/DOM-Level-3-Events/#event-flow
        const composedPath = e.composedPath();
        // event flow: Capture -> Target -> Bubbling
        // Capture Phase
        e.eventPhase = e.CAPTURING_PHASE;
        for (let i = composedPath.length - 1; i >= 1; i--) {
            e.currentTarget = composedPath[i];
            this.notifyTarget(e, type);
            if (e.propagationStopped || e.propagationImmediatelyStopped)
                return;
        }
        // Target Phase
        e.eventPhase = e.AT_TARGET;
        e.currentTarget = e.target;
        this.notifyTarget(e, type);
        if (e.propagationStopped || e.propagationImmediatelyStopped)
            return;
        // Bubbling Phase
        e.eventPhase = e.BUBBLING_PHASE;
        for (let i = 1; i < composedPath.length; i++) {
            e.currentTarget = composedPath[i];
            this.notifyTarget(e, type);
            if (e.propagationStopped || e.propagationImmediatelyStopped)
                return;
        }
    }
    notifyTarget(e, type) {
        type = type ?? e.type;
        const key = e.eventPhase === e.CAPTURING_PHASE || e.eventPhase === e.AT_TARGET ? `${type}capture` : type;
        this.notifyListeners(e, key);
        if (e.eventPhase === e.AT_TARGET) {
            this.notifyListeners(e, type);
        }
    }
    notifyListeners(e, type) {
        const emitter = e.currentTarget?.emitter;
        const listeners = emitter?.listeners[type];
        if (!listeners)
            return;
        for (let i = 0; i < listeners.length && !e.propagationImmediatelyStopped; i++) {
            // 'this' is always e.currentTarget, so we can just call listeners[i](e)
            listeners[i].call(e.currentTarget, e);
        }
    }
    /** return [target, parent, root] */
    propagationPath(target) {
        const propagationPath = [target];
        for (let i = 0; i < PROPAGATION_LIMIT && target !== this.rootTarget; i++) {
            if (!target.parent) {
                throw new Error('Cannot find propagation path to disconnected target');
            }
            propagationPath.push(target.parent);
            target = target.parent;
        }
        return propagationPath;
    }
    onPointerDown = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const e = this.createPointerEvent(from);
        this.dispatchEvent(e, 'pointerdown');
        if (e.pointerType === 'touch') {
            this.dispatchEvent(e, 'touchstart');
        }
        else if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
            const isRightButton = e.button === 2;
            this.dispatchEvent(e, isRightButton ? 'rightdown' : 'mousedown');
        }
        const trackingData = this.trackingData(from.pointerId);
        trackingData.pressTargetsByButton[from.button] = e.composedPath();
        this.freeEvent(e);
    };
    onPointerUp = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const now = performance.now();
        const e = this.createPointerEvent(from);
        this.dispatchEvent(e, 'pointerup');
        if (e.pointerType === 'touch') {
            this.dispatchEvent(e, 'touchend');
        }
        else if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
            const isRightButton = e.button === 2;
            this.dispatchEvent(e, isRightButton ? 'rightup' : 'mouseup');
        }
        const trackingData = this.trackingData(from.pointerId);
        const pressTarget = this.findMountedTarget(trackingData.pressTargetsByButton[from.button]);
        let clickTarget = pressTarget;
        // pointerupoutside only bubbles. It only bubbles upto the parent that doesn't contain
        // the pointerup location.
        if (pressTarget && !e.composedPath().includes(pressTarget)) {
            let currentTarget = pressTarget;
            while (currentTarget && !e.composedPath().includes(currentTarget)) {
                e.currentTarget = currentTarget;
                this.notifyTarget(e, 'pointerupoutside');
                if (e.pointerType === 'touch') {
                    this.notifyTarget(e, 'touchendoutside');
                }
                else if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
                    const isRightButton = e.button === 2;
                    this.notifyTarget(e, isRightButton ? 'rightupoutside' : 'mouseupoutside');
                }
                currentTarget = currentTarget.parent;
            }
            delete trackingData.pressTargetsByButton[from.button];
            // currentTarget is the most specific ancestor holding both the pointerdown and pointerup
            // targets. That is - it's our click target!
            clickTarget = currentTarget;
        }
        if (clickTarget) {
            const clickEvent = this.clonePointerEvent(e, 'click');
            clickEvent.target = clickTarget;
            clickEvent.path = [];
            if (!trackingData.clicksByButton[from.button]) {
                trackingData.clicksByButton[from.button] = {
                    clickCount: 0,
                    target: clickEvent.target,
                    timeStamp: now,
                };
            }
            const clickHistory = trackingData.clicksByButton[from.button];
            if (clickHistory.target === clickEvent.target && now - clickHistory.timeStamp < 200) {
                ++clickHistory.clickCount;
            }
            else {
                clickHistory.clickCount = 1;
            }
            clickHistory.target = clickEvent.target;
            clickHistory.timeStamp = now;
            clickEvent.detail = clickHistory.clickCount;
            if (clickEvent.pointerType === 'mouse') {
                const isRightButton = clickEvent.button === 2;
                this.dispatchEvent(clickEvent, isRightButton ? 'rightclick' : 'click');
            }
            else if (clickEvent.pointerType === 'touch') {
                this.dispatchEvent(clickEvent, 'tap');
            }
            this.dispatchEvent(clickEvent, 'pointertap');
            this.freeEvent(clickEvent);
        }
        this.freeEvent(e);
    };
    onPointerMove = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const e = this.createPointerEvent(from);
        const isMouse = e.pointerType === 'mouse' || e.pointerType === 'pen';
        const trackingData = this.trackingData(from.pointerId);
        const outTarget = this.findMountedTarget(trackingData.overTargets);
        // First pointerout/pointerleave
        if (trackingData.overTargets?.length > 0 && outTarget !== e.target) {
            // pointerout always occurs on the overTarget when the pointer hovers over another element.
            const outType = from.type === 'mousemove' ? 'mouseout' : 'pointerout';
            const outEvent = this.createPointerEvent(from, outType, outTarget);
            this.dispatchEvent(outEvent, 'pointerout');
            if (isMouse)
                this.dispatchEvent(outEvent, 'mouseout');
            // If the pointer exits overTarget and its descendants, then a pointerleave event is also fired. This event
            // is dispatched to all ancestors that no longer capture the pointer.
            if (!(outTarget && e.composedPath().includes(outTarget))) {
                const leaveEvent = this.createPointerEvent(from, 'pointerleave', outTarget);
                leaveEvent.eventPhase = leaveEvent.AT_TARGET;
                while (leaveEvent.target && !e.composedPath().includes(leaveEvent.target)) {
                    leaveEvent.currentTarget = leaveEvent.target;
                    this.notifyTarget(leaveEvent);
                    if (isMouse)
                        this.notifyTarget(leaveEvent, 'mouseleave');
                    leaveEvent.target = leaveEvent.target.parent ?? null;
                }
                this.freeEvent(leaveEvent);
            }
            this.freeEvent(outEvent);
        }
        // Then pointerover
        if (outTarget !== e.target) {
            // pointerover always occurs on the new overTarget
            const overType = from.type === 'mousemove' ? 'mouseover' : 'pointerover';
            const overEvent = this.clonePointerEvent(e, overType); // clone faster
            this.dispatchEvent(overEvent, 'pointerover');
            if (isMouse)
                this.dispatchEvent(overEvent, 'mouseover');
            // Probe whether the newly hovered DisplayObject is an ancestor of the original overTarget.
            let overTargetAncestor = outTarget?.parent;
            while (overTargetAncestor && overTargetAncestor !== this.rootTarget.parent) {
                if (overTargetAncestor === e.target)
                    break;
                overTargetAncestor = overTargetAncestor.parent;
            }
            // The pointer has entered a non-ancestor of the original overTarget. This means we need a pointerentered event
            const didPointerEnter = !overTargetAncestor || overTargetAncestor === this.rootTarget.parent;
            if (didPointerEnter) {
                const enterEvent = this.clonePointerEvent(e, 'pointerenter');
                enterEvent.eventPhase = enterEvent.AT_TARGET;
                while (enterEvent.target &&
                    enterEvent.target !== outTarget &&
                    enterEvent.target !== this.rootTarget.parent) {
                    enterEvent.currentTarget = enterEvent.target;
                    this.notifyTarget(enterEvent);
                    if (isMouse)
                        this.notifyTarget(enterEvent, 'mouseenter');
                    enterEvent.target = enterEvent.target.parent ?? null;
                }
                this.freeEvent(enterEvent);
            }
            this.freeEvent(overEvent);
        }
        this.dispatchEvent(e, 'pointermove');
        if (e.pointerType === 'touch')
            this.dispatchEvent(e, 'touchmove');
        if (isMouse) {
            this.dispatchEvent(e, 'mousemove');
            this.cursor = e.target?.cursor || null;
        }
        trackingData.overTargets = e.composedPath();
        this.freeEvent(e);
    };
    onPointerOut = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const trackingData = this.trackingData(from.pointerId);
        if (trackingData.overTargets) {
            const isMouse = from.pointerType === 'mouse' || from.pointerType === 'pen';
            const outTarget = this.findMountedTarget(trackingData.overTargets);
            // pointerout first
            const outEvent = this.createPointerEvent(from, 'pointerout', outTarget || undefined);
            this.dispatchEvent(outEvent);
            if (isMouse)
                this.dispatchEvent(outEvent, 'mouseout');
            // pointerleave(s) are also dispatched b/c the pointer must've left rootTarget and its descendants to
            // get an upstream pointerout event (upstream events do not know rootTarget has descendants).
            const leaveEvent = this.createPointerEvent(from, 'pointerleave', outTarget);
            leaveEvent.eventPhase = leaveEvent.AT_TARGET;
            while (leaveEvent.target && leaveEvent.target !== this.rootTarget.parent) {
                leaveEvent.currentTarget = leaveEvent.target;
                this.notifyTarget(leaveEvent);
                if (isMouse) {
                    this.notifyTarget(leaveEvent, 'mouseleave');
                }
                leaveEvent.target = leaveEvent.target.parent || null;
            }
            trackingData.overTargets = [];
            this.freeEvent(outEvent);
            this.freeEvent(leaveEvent);
        }
        this.cursor = null;
    };
    onPointerOver = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const trackingData = this.trackingData(from.pointerId);
        const e = this.createPointerEvent(from);
        const isMouse = e.pointerType === 'mouse' || e.pointerType === 'pen';
        this.dispatchEvent(e, 'pointerover');
        if (isMouse)
            this.dispatchEvent(e, 'mouseover');
        if (e.pointerType === 'mouse')
            this.cursor = e.target?.cursor || null;
        // pointerenter events must be fired since the pointer entered from upstream.
        const enterEvent = this.clonePointerEvent(e, 'pointerenter');
        enterEvent.eventPhase = enterEvent.AT_TARGET;
        while (enterEvent.target && enterEvent.target !== this.rootTarget.parent) {
            enterEvent.currentTarget = enterEvent.target;
            this.notifyTarget(enterEvent);
            if (isMouse) {
                // mouseenter should not bubble
                this.notifyTarget(enterEvent, 'mouseenter');
            }
            enterEvent.target = enterEvent.target.parent || null;
        }
        trackingData.overTargets = e.composedPath();
        this.freeEvent(e);
        this.freeEvent(enterEvent);
    };
    onPointerUpOutside = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const trackingData = this.trackingData(from.pointerId);
        const pressTarget = this.findMountedTarget(trackingData.pressTargetsByButton[from.button]);
        const e = this.createPointerEvent(from);
        if (pressTarget) {
            let currentTarget = pressTarget;
            while (currentTarget) {
                e.currentTarget = currentTarget;
                this.notifyTarget(e, 'pointerupoutside');
                if (e.pointerType === 'touch') ;
                else if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
                    this.notifyTarget(e, e.button === 2 ? 'rightupoutside' : 'mouseupoutside');
                }
                currentTarget = currentTarget.parent || null;
            }
            delete trackingData.pressTargetsByButton[from.button];
        }
        this.freeEvent(e);
    };
    onWheel = (from) => {
        if (!(from instanceof FederatedWheelEvent)) {
            console.warn('EventBoundary cannot map a non-wheel event as a wheel event');
            return;
        }
        const wheelEvent = this.createWheelEvent(from);
        this.dispatchEvent(wheelEvent);
        this.freeEvent(wheelEvent);
    };
    onPointerCancel = (from) => {
        if (!(from instanceof FederatedPointerEvent)) {
            console.warn('EventBoundary cannot map a non-pointer event as a pointer event');
            return;
        }
        const e = this.createPointerEvent(from);
        this.dispatchEvent(e);
        this.freeEvent(e);
    };
    /**
     * Finds the most specific event-target in the given propagation path that is still mounted in the scene graph.
     *
     * This is used to find the correct `pointerup` and `pointerout` target in the case that the original `pointerdown`
     * or `pointerover` target was unmounted from the scene graph.
     */
    findMountedTarget(propagationPath) {
        if (!propagationPath) {
            return;
        }
        let currentTarget = propagationPath[propagationPath.length - 1];
        for (let i = propagationPath.length - 2; i >= 0; i--) {
            const target = propagationPath[i];
            if (target.parent === currentTarget) {
                currentTarget = target;
            }
            else {
                break;
            }
        }
        return currentTarget;
    }
    createPointerEvent(from, type, target) {
        const event = this.allocateEvent(FederatedPointerEvent);
        this.copyPointerData(from, event);
        this.copyMouseData(from, event);
        this.copyData(from, event);
        event.nativeEvent = from.nativeEvent;
        event.originalEvent = from;
        event.target = target ?? this.hitTest(event.viewport.x, event.viewport.y);
        if (typeof type === 'string') {
            event.type = type;
        }
        return event;
    }
    createWheelEvent(from) {
        const event = this.allocateEvent(FederatedWheelEvent);
        this.copyWheelData(from, event);
        this.copyMouseData(from, event);
        this.copyData(from, event);
        event.nativeEvent = from.nativeEvent;
        event.originalEvent = from;
        event.target = this.hitTest(event.viewport.x, event.viewport.y);
        return event;
    }
    clonePointerEvent(from, type) {
        const event = this.allocateEvent(FederatedPointerEvent);
        event.nativeEvent = from.nativeEvent;
        event.originalEvent = from.originalEvent;
        this.copyPointerData(from, event);
        this.copyMouseData(from, event);
        this.copyData(from, event);
        event.target = from.target;
        event.path = from.composedPath().slice();
        event.type = type ?? event.type;
        return event;
    }
    copyPointerData(from, to) {
        if (!(from instanceof FederatedPointerEvent && to instanceof FederatedPointerEvent))
            return;
        to.pointerId = from.pointerId;
        to.width = from.width;
        to.height = from.height;
        to.isPrimary = from.isPrimary;
        to.pointerType = from.pointerType;
        to.pressure = from.pressure;
        to.tangentialPressure = from.tangentialPressure;
        to.tiltX = from.tiltX;
        to.tiltY = from.tiltY;
        to.twist = from.twist;
    }
    copyMouseData(from, to) {
        if (!(from instanceof FederatedMouseEvent && to instanceof FederatedMouseEvent))
            return;
        to.altKey = from.altKey;
        to.button = from.button;
        to.buttons = from.buttons;
        to.shiftKey = from.shiftKey;
        to.ctrlKey = from.ctrlKey;
        to.metaKey = from.metaKey;
        to.client.copyFrom(from.client);
        to.movement.copyFrom(from.movement);
        to.screen.copyFrom(from.screen);
        to.global.copyFrom(from.global);
        to.canvas.copyFrom(from.canvas);
    }
    copyWheelData(from, to) {
        to.deltaMode = from.deltaMode;
        to.deltaX = from.deltaX;
        to.deltaY = from.deltaY;
        to.deltaZ = from.deltaZ;
    }
    copyData(from, to) {
        to.timeStamp = performance.now();
        to.type = from.type;
        to.detail = from.detail;
        to.page.copyFrom(from.page);
        to.viewport.copyFrom(from.viewport);
    }
    trackingData(id) {
        if (!this.mappingState.trackingData[id]) {
            this.mappingState.trackingData[id] = {
                pressTargetsByButton: {},
                clicksByButton: {},
                overTargets: null,
            };
        }
        return this.mappingState.trackingData[id];
    }
    hitTest(x, y) {
        const invertedPath = this.hitTestRecursive(this.rootTarget, new Point(x, y));
        return invertedPath && invertedPath[0];
    }
    hitTestRecursive(currentTarget, location) {
        if (!currentTarget.visible)
            return null;
        if (currentTarget.children.length > 0) {
            // TODO sort cache
            const children = currentTarget.children.sort((a, b) => a.sortable.renderOrder - b.sortable.renderOrder);
            for (let i = children.length - 1; i >= 0; i--) {
                const child = children[i];
                const nestedHit = this.hitTestRecursive(child, location);
                if (nestedHit) {
                    // check if not hit or child has been removed from its parent
                    if (nestedHit.length > 0 && !nestedHit[nestedHit.length - 1].parent) {
                        continue;
                    }
                    if (nestedHit.length > 0) {
                        nestedHit.push(currentTarget);
                    }
                    return nestedHit;
                }
            }
        }
        // TODO convert location to local space in here intend of in isPointHit
        if (currentTarget.isPointHit(location)) {
            return [currentTarget];
        }
        return null;
    }
    allocateEvent(constructor) {
        if (!this.eventPool.has(constructor)) {
            this.eventPool.set(constructor, []);
        }
        const event = this.eventPool.get(constructor).pop() || new constructor(this);
        event.eventPhase = event.NONE;
        event.currentTarget = null;
        event.path = [];
        event.target = null;
        return event;
    }
    freeEvent(event) {
        if (event.manager !== this)
            throw new Error('It is illegal to free an event not managed by this EventBoundary!');
        const { constructor } = event;
        if (!this.eventPool.has(constructor)) {
            this.eventPool.set(constructor, []);
        }
        this.eventPool.get(constructor).push(event);
    }
}

class CircleRenderer {
    render = (ctx, object, context) => {
        const { center, radius } = object.style;
        const canvas = context.renderingContext.root.ownerCanvas;
        const realCenter = canvas.canvas2Viewport(center);
        ctx.beginPath();
        ctx.arc(realCenter.x, realCenter.y, radius, 0, Math.PI * 2, false);
        ctx.fill();
    };
}

class CurveRenderer {
    render = (ctx, object, context) => {
        const { curve } = object;
        this.drawBezierCurve(curve, ctx, context);
    };
    drawBezierCurve = (curve, ctx, context) => {
        if (isNil(curve))
            return;
        if (curve.keys.length === 0)
            return;
        const canvas = context.renderingContext.root.ownerCanvas;
        ctx.beginPath();
        const key0 = curve.keys[0];
        const a = canvas.canvas2Viewport({ x: key0.time, y: key0.value });
        ctx.moveTo(a.x, a.y);
        const keys = curve.keys;
        for (let j = 1; j < curve.keys.length; j++) {
            const prevKey = keys[j - 1];
            const nowKey = keys[j];
            switch (prevKey.interpMode) {
                case Gf.Constant: {
                    const a = canvas.canvas2Viewport({ x: nowKey.time, y: prevKey.value });
                    ctx.lineTo(a.x, a.y);
                    const b = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                    ctx.lineTo(b.x, b.y);
                    break;
                }
                case Gf.Linaer: {
                    const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                    ctx.lineTo(a.x, a.y);
                    break;
                }
                case Gf.Cubic: {
                    // not weighted curve
                    if (nie(prevKey, nowKey)) {
                        const dt = nowKey.time - prevKey.time;
                        const oneThird = 1 / 3;
                        const p1x = prevKey.time + oneThird * dt;
                        const p2x = nowKey.time - oneThird * dt;
                        const p1y = prevKey.value + prevKey.outTangent * dt * oneThird;
                        const p2y = nowKey.value - nowKey.inTangent * dt * oneThird;
                        const p1 = canvas.canvas2Viewport({ x: p1x, y: p1y });
                        const p2 = canvas.canvas2Viewport({ x: p2x, y: p2y });
                        const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                        ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, a.x, a.y);
                    }
                    // weighted curve
                    else {
                        const getp1p2 = (key1, key2) => {
                            const t1 = key1.time;
                            const t2 = key2.time;
                            const dt = t2 - t1;
                            const oneThird = 1 / 3;
                            let prevWeight = 0;
                            if (rie(key1)) {
                                prevWeight = key1.outWeight;
                            }
                            else {
                                const x = dt;
                                const y = x * key1.outTangent;
                                prevWeight = Math.sqrt(x * x + y * y) * oneThird;
                            }
                            const angle1 = Math.atan(key1.outTangent);
                            const tx1 = Math.cos(angle1) * prevWeight + t1;
                            const ty1 = Math.sin(angle1) * prevWeight + key1.value;
                            let nextWeight = 0;
                            if (sie(key2)) {
                                nextWeight = key2.inWeight;
                            }
                            else {
                                const x = dt;
                                const y = x * key2.inTangent;
                                nextWeight = Math.sqrt(x * x + y * y) * oneThird;
                            }
                            const angle2 = Math.atan(key2.inTangent);
                            const tx2 = t2 - Math.cos(angle2) * nextWeight;
                            const ty2 = key2.value - Math.sin(angle2) * nextWeight;
                            return [tx1, ty1, tx2, ty2];
                        };
                        const [p1x, p1y, p2x, p2y] = getp1p2(prevKey, nowKey);
                        const p1 = canvas.canvas2Viewport({ x: p1x, y: p1y });
                        const p2 = canvas.canvas2Viewport({ x: p2x, y: p2y });
                        const a = canvas.canvas2Viewport({ x: nowKey.time, y: nowKey.value });
                        ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, a.x, a.y);
                    }
                    break;
                }
            }
            ctx.stroke();
        }
    };
}

class OffsetLineRenderer {
    render = (ctx, object, context) => {
        const { beginOffset, endOffset, origin } = object.style;
        const canvas = context.renderingContext.root.ownerCanvas;
        const lengthFactor = 1;
        const realOrigin = canvas.canvas2Viewport(origin);
        const realBegin = {
            x: realOrigin.x + beginOffset.x * lengthFactor,
            y: realOrigin.y + beginOffset.y * lengthFactor,
        };
        const realEnd = {
            x: realOrigin.x + endOffset.x * lengthFactor,
            y: realOrigin.y + endOffset.y * lengthFactor,
        };
        ctx.beginPath();
        ctx.moveTo(realBegin.x, realBegin.y);
        ctx.lineTo(realEnd.x, realEnd.y);
        ctx.stroke();
    };
}

class LineRenderer {
    render = (ctx, object, context) => {
        const { begin, end } = object.style;
        const canvas = context.renderingContext.root.ownerCanvas;
        const realBegin = canvas.canvas2Viewport(begin);
        const realEnd = canvas.canvas2Viewport(end);
        ctx.beginPath();
        ctx.moveTo(realBegin.x, realBegin.y);
        ctx.lineTo(realEnd.x, realEnd.y);
        ctx.stroke();
    };
}

class ControlCircleRender {
    render = (ctx, object, context) => {
        const { centerOffset, origin, radius } = object.style;
        const canvas = context.renderingContext.root.ownerCanvas;
        const realOrigin = canvas.canvas2Viewport(origin);
        const realCenter = { x: realOrigin.x + centerOffset.x, y: realOrigin.y + centerOffset.y };
        ctx.beginPath();
        ctx.arc(realCenter.x, realCenter.y, radius, 0, Math.PI * 2, false);
        ctx.fill();
    };
}

class Canvas2DRendererPlugin {
    name = 'Canvas2DRenderer';
    fullRendering = false;
    styleRendererFactory;
    tmpMat4 = create$3();
    forceSkipClear = false;
    constructor(opts) {
        if (opts) {
            opts.forceSkipClear && (this.forceSkipClear = opts.forceSkipClear);
        }
    }
    apply = (context) => {
        const { renderingSystem, renderingContext, contextSystem, config, camera } = context;
        const styleRendererFactory = {
            [Shape.Group]: undefined,
            [Shape.Circle]: new CircleRenderer(),
            [Shape.Curve]: new CurveRenderer(),
            [Shape.Line]: new LineRenderer(),
            [Shape.OffsetLine]: new OffsetLineRenderer(),
            [Shape.ControlCircle]: new ControlCircleRender(),
            [Shape.Polygon]: undefined,
        };
        this.styleRendererFactory = styleRendererFactory;
        renderingSystem.hooks.render.tap((renderQueue) => {
            const ctx = contextSystem.getContext();
            if (this.fullRendering) {
                this.renderByZIndex(renderingContext.root, ctx, context);
            }
            else {
                const dpr = config.devicePixelRatio;
                ctx.save();
                !this.forceSkipClear && ctx.clearRect(0, 0, config.width * dpr, config.height * dpr);
                ctx.scale(dpr, dpr);
                // rendering
                renderQueue
                    .sort((a, b) => a.sortable.renderOrder - b.sortable.renderOrder)
                    .forEach((object) => {
                    if (object.visible) {
                        this.renderDisplayObject(object, ctx, context);
                    }
                });
                // reset matrix
                ctx.restore();
            }
        });
        renderingSystem.hooks.endFrame.tap(() => { });
    };
    renderByZIndex = (object, ctx, canvasContext) => {
        if (object.visible) {
            this.renderDisplayObject(object, ctx, canvasContext);
        }
        const sorted = object.sortable.sorted || object.children;
        sorted.forEach((child) => {
            this.renderByZIndex(child, ctx, canvasContext);
        });
    };
    renderDisplayObject = (object, ctx, canvasContext) => {
        const objType = object.type;
        const renderer = this.styleRendererFactory[objType];
        if (renderer) {
            ctx.save();
            // apply style
            this.applyStyle(ctx, object);
            // render shape
            renderer.render(ctx, object, canvasContext);
            ctx.restore();
        }
    };
    applyStyle = (ctx, object) => {
        const { opacity, fillStyle, strokeStyle, lineWidth } = object.style;
        if (!isNil(opacity))
            ctx.globalAlpha = opacity;
        if (!isNil(fillStyle))
            ctx.fillStyle = fillStyle;
        if (!isNil(strokeStyle))
            ctx.strokeStyle = strokeStyle;
        if (!isNil(lineWidth))
            ctx.lineWidth = lineWidth;
    };
    destroy() { }
}

const MOUSE_POINTER_ID = 1;
const TOUCH_TO_POINTER = {
    touchstart: 'pointerdown',
    touchend: 'pointerup',
    touchendoutside: 'pointerupoutside',
    touchmove: 'pointermove',
    touchcancel: 'pointercancel',
};
class EventPlugin {
    name = 'EventPlugin';
    eventSystem;
    canvasEl;
    canvas;
    rootPointerEvent = new FederatedPointerEvent(null);
    rootWheelEvent = new FederatedWheelEvent(null);
    config;
    apply(context) {
        const { renderingContext, config, eventSystem, renderingSystem } = context;
        const canvas = renderingContext.root.ownerCanvas;
        this.canvas = canvas;
        this.config = config;
        const canvasEl = context.contextSystem.getCanvasElement();
        this.canvasEl = canvasEl;
        this.eventSystem = eventSystem;
        renderingSystem.hooks.init.tap(() => {
            this.addEvents(canvasEl);
        });
        renderingSystem.hooks.destroy.tap(() => {
            this.removeEvents(canvasEl);
        });
    }
    addEvents(el) {
        if (this.config.supportsPointerEvents) {
            this.addPointerEventListener(el);
        }
        else {
            this.addMouseEventListener(el);
        }
        if (this.config.supportsTouchEvents) {
            this.addTouchEventListener(el);
        }
        el.addEventListener('wheel', this.onPointerWheel, { passive: true, capture: true });
    }
    removeEvents(el) {
        if (!el)
            return;
        if (this.config.supportsPointerEvents) {
            this.removePointerEventListener(el);
        }
        else {
            this.removeMouseEventListener(el);
        }
        if (this.config.supportsTouchEvents) {
            this.removeTouchEventListener(el);
        }
        el.removeEventListener('wheel', this.onPointerWheel, true);
    }
    addPointerEventListener(el) {
        window.addEventListener('pointermove', this.onPointerMove, true);
        el.addEventListener('pointerdown', this.onPointerDown, true);
        el.addEventListener('pointerleave', this.onPointerOverOut, true);
        el.addEventListener('pointerover', this.onPointerOverOut, true);
        window.addEventListener('pointerup', this.onPointerUp, true);
        window.addEventListener('pointercancel', this.onPointerCancel, true);
        el.addEventListener('contextmenu', this.onContextMenu);
    }
    removePointerEventListener(el) {
        window.removeEventListener('pointermove', this.onPointerMove, true);
        el.removeEventListener('pointerdown', this.onPointerDown, true);
        el.removeEventListener('pointerleave', this.onPointerOverOut, true);
        el.removeEventListener('pointerover', this.onPointerOverOut, true);
        window.removeEventListener('pointerup', this.onPointerUp, true);
        window.removeEventListener('pointercancel', this.onPointerCancel, true);
        el.removeEventListener('contextmenu', this.onContextMenu);
    }
    addTouchEventListener(el) {
        el.addEventListener('touchstart', this.onPointerDown, true);
        el.addEventListener('touchend', this.onPointerUp, true);
        el.addEventListener('touchmove', this.onPointerMove, true);
        el.addEventListener('touchcancel', this.onPointerCancel, true);
    }
    removeTouchEventListener(el) {
        el.removeEventListener('touchstart', this.onPointerDown, true);
        el.removeEventListener('touchend', this.onPointerUp, true);
        el.removeEventListener('touchmove', this.onPointerMove, true);
        el.removeEventListener('touchcancel', this.onPointerCancel, true);
    }
    addMouseEventListener = (el) => {
        window.addEventListener('mousemove', this.onPointerMove, true);
        el.addEventListener('mousedown', this.onPointerDown, true);
        el.addEventListener('mouseout', this.onPointerOverOut, true);
        el.addEventListener('mouseover', this.onPointerOverOut, true);
        window.addEventListener('mouseup', this.onPointerUp, true);
    };
    removeMouseEventListener = (el) => {
        window.removeEventListener('mousemove', this.onPointerMove, true);
        el.removeEventListener('mousedown', this.onPointerDown, true);
        el.removeEventListener('mouseout', this.onPointerOverOut, true);
        el.removeEventListener('mouseover', this.onPointerOverOut, true);
        window.removeEventListener('mouseup', this.onPointerUp, true);
    };
    onPointerMove = (nativeEvent) => {
        const canvas = this.canvas;
        if (!canvas)
            return;
        if (this.config.supportsTouchEvents && nativeEvent.type === 'touch') {
            return;
        }
        const normalizedEvents = this.normalizeToPointerEvent(nativeEvent);
        for (const normalizedEvent of normalizedEvents) {
            const event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvent, canvas, nativeEvent);
            this.eventSystem.mapEvent(event);
        }
        this.setCursor(this.eventSystem.cursor);
    };
    onPointerDown = (nativeEvent) => {
        const canvas = this.canvas;
        if (!canvas)
            return;
        if (this.config.supportsTouchEvents && nativeEvent.pointerType === 'touch')
            return;
        const events = this.normalizeToPointerEvent(nativeEvent);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (events[0].isNormalized) {
            const cancelable = nativeEvent.cancelable || !('cancelable' in nativeEvent);
            if (cancelable) {
                nativeEvent.preventDefault();
            }
        }
        for (const event of events) {
            const federatedEvent = this.bootstrapEvent(this.rootPointerEvent, event, canvas, nativeEvent);
            this.eventSystem.mapEvent(federatedEvent);
        }
        this.setCursor(this.eventSystem.cursor);
    };
    // same as onPointerMove
    onPointerOverOut = (nativeEvent) => {
        const canvas = this.canvas;
        if (!canvas)
            return;
        if (this.config.supportsTouchEvents && nativeEvent.type === 'touch') {
            return;
        }
        const normalizedEvents = this.normalizeToPointerEvent(nativeEvent);
        for (const normalizedEvent of normalizedEvents) {
            const event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvent, canvas, nativeEvent);
            this.eventSystem.mapEvent(event);
        }
        this.setCursor(this.eventSystem.cursor);
    };
    onPointerUp = (nativeEvent) => {
        const canvas = this.canvas;
        if (!canvas)
            return;
        if (this.config.supportsTouchEvents && nativeEvent.pointerType === 'touch')
            return;
        const canvasEl = this.canvasEl;
        let outside = 'outside';
        try {
            outside =
                canvasEl &&
                    nativeEvent.target &&
                    nativeEvent.target !== canvasEl &&
                    canvasEl.contains &&
                    !canvasEl.contains(nativeEvent.target)
                    ? 'outside'
                    : '';
        }
        catch (e) {
            // nativeEvent.target maybe not Node, such as Window
        }
        const normalizedEvents = this.normalizeToPointerEvent(nativeEvent);
        for (const normalizedEvent of normalizedEvents) {
            const event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvent, canvas, nativeEvent);
            event.type += outside;
            this.eventSystem.mapEvent(event);
        }
        this.setCursor(this.eventSystem.cursor);
    };
    onPointerWheel = (nativeEvent) => {
        const wheelEvent = this.normalizeWheelEvent(nativeEvent);
        this.eventSystem.mapEvent(wheelEvent);
    };
    onPointerCancel = (nativeEvent) => {
        const canvas = this.canvas;
        if (!canvas)
            return;
        const normalizedEvents = this.normalizeToPointerEvent(nativeEvent);
        for (const normalizedEvent of normalizedEvents) {
            const event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvent, canvas, nativeEvent);
            this.eventSystem.mapEvent(event);
        }
        this.setCursor(this.eventSystem.cursor);
    };
    onContextMenu = (nativeEvent) => {
        nativeEvent.preventDefault();
    };
    /**
     * Transfers base & mouse event data from the nativeEvent to the federated event.
     */
    transferMouseData(event, nativeEvent) {
        event.timeStamp = performance.now();
        event.type = nativeEvent.type;
        event.altKey = nativeEvent.altKey;
        event.metaKey = nativeEvent.metaKey;
        event.shiftKey = nativeEvent.shiftKey;
        event.ctrlKey = nativeEvent.ctrlKey;
        event.button = nativeEvent.button;
        event.buttons = nativeEvent.buttons;
        event.client.x = nativeEvent.clientX;
        event.client.y = nativeEvent.clientY;
        event.movement.x = nativeEvent.movementX;
        event.movement.y = nativeEvent.movementY;
        event.page.x = nativeEvent.pageX;
        event.page.y = nativeEvent.pageY;
        event.screen.x = nativeEvent.screenX;
        event.screen.y = nativeEvent.screenY;
        event.relatedTarget = null;
    }
    setCursor(cursor) {
        this.canvasEl && (this.canvasEl.style.cursor = cursor || 'default');
    }
    bootstrapEvent(event, normalizedEvent, view, nativeEvent) {
        event.originalEvent = null;
        event.nativeEvent = nativeEvent;
        event.pointerId = normalizedEvent.pointerId;
        event.width = normalizedEvent.width;
        event.height = normalizedEvent.height;
        event.isPrimary = normalizedEvent.isPrimary;
        event.pointerType = normalizedEvent.pointerType;
        event.pressure = normalizedEvent.pressure;
        event.tangentialPressure = normalizedEvent.tangentialPressure;
        event.tiltX = normalizedEvent.tiltX;
        event.tiltY = normalizedEvent.tiltY;
        event.twist = normalizedEvent.twist;
        this.transferMouseData(event, normalizedEvent);
        // map position to point in canvas
        const { x, y } = this.getViewportXY(normalizedEvent);
        event.viewport.x = x;
        event.viewport.y = y;
        const { x: canvasX, y: canvasY } = this.canvas.viewport2Canvas(event.viewport);
        event.canvas.x = canvasX;
        event.canvas.y = canvasY;
        event.global.copyFrom(event.canvas);
        if (event.type === 'pointerleave') {
            event.type = 'pointerout';
        }
        if (event.type.startsWith('mouse')) {
            event.type = event.type.replace('mouse', 'pointer');
        }
        if (event.type.startsWith('touch')) {
            event.type = TOUCH_TO_POINTER[event.type] || event.type;
        }
        return event;
    }
    normalizeToPointerEvent(event) {
        const normalizedEvents = [];
        if (this.config.supportsTouchEvents && event instanceof TouchEvent) {
            for (let i = 0; i < event.changedTouches.length; i++) {
                const touch = event.changedTouches[i];
                // use changedTouches instead of touches since touchend has no touches
                // @see https://stackoverflow.com/a/10079076
                if (isUndefined(touch.button))
                    touch.button = 0;
                if (isUndefined(touch.buttons))
                    touch.buttons = 1;
                if (isUndefined(touch.isPrimary)) {
                    touch.isPrimary = event.touches.length === 1 && event.type === 'touchstart';
                }
                if (isUndefined(touch.width))
                    touch.width = touch.radiusX || 1;
                if (isUndefined(touch.height))
                    touch.height = touch.radiusY || 1;
                if (isUndefined(touch.tiltX))
                    touch.tiltX = 0;
                if (isUndefined(touch.tiltY))
                    touch.tiltY = 0;
                if (isUndefined(touch.pointerType))
                    touch.pointerType = 'touch';
                if (isUndefined(touch.pointerId))
                    touch.pointerId = touch.identifier || 0;
                if (isUndefined(touch.pressure))
                    touch.pressure = touch.force || 0.5;
                if (isUndefined(touch.twist))
                    touch.twist = 0;
                if (isUndefined(touch.tangentialPressure))
                    touch.tangentialPressure = 0;
                touch.isNormalized = true;
                touch.type = event.type;
                normalizedEvents.push(touch);
            }
        }
        else if (!globalThis.MouseEvent ||
            (event instanceof globalThis.MouseEvent &&
                (!this.config.supportsPointerEvents || !(event instanceof globalThis.PointerEvent)))) {
            const tempEvent = event;
            if (isUndefined(tempEvent.isPrimary))
                tempEvent.isPrimary = true;
            if (isUndefined(tempEvent.width))
                tempEvent.width = 1;
            if (isUndefined(tempEvent.height))
                tempEvent.height = 1;
            if (isUndefined(tempEvent.tiltX))
                tempEvent.tiltX = 0;
            if (isUndefined(tempEvent.tiltY))
                tempEvent.tiltY = 0;
            if (isUndefined(tempEvent.pointerType))
                tempEvent.pointerType = 'mouse';
            if (isUndefined(tempEvent.pointerId))
                tempEvent.pointerId = MOUSE_POINTER_ID;
            if (isUndefined(tempEvent.pressure))
                tempEvent.pressure = 0.5;
            if (isUndefined(tempEvent.twist))
                tempEvent.twist = 0;
            if (isUndefined(tempEvent.tangentialPressure))
                tempEvent.tangentialPressure = 0;
            tempEvent.isNormalized = true;
            normalizedEvents.push(tempEvent);
        }
        else {
            normalizedEvents.push(event);
        }
        return normalizedEvents;
    }
    normalizeWheelEvent(nativeEvent) {
        const event = this.rootWheelEvent;
        this.transferMouseData(event, nativeEvent);
        event.deltaMode = nativeEvent.deltaMode;
        event.deltaX = nativeEvent.deltaX;
        event.deltaY = nativeEvent.deltaY;
        event.deltaZ = nativeEvent.deltaZ;
        const { x, y } = this.getViewportXY(nativeEvent);
        event.viewport.x = x;
        event.viewport.y = y;
        const { x: canvasX, y: canvasY } = this.canvas.viewport2Canvas(event.viewport);
        event.canvas.x = canvasX;
        event.canvas.y = canvasY;
        event.global.copyFrom(event.canvas);
        event.nativeEvent = nativeEvent;
        event.type = nativeEvent.type;
        return event;
    }
    getViewportXY(nativeEvent) {
        const { offsetX, offsetY, clientX, clientY } = nativeEvent;
        if (this.config.supportsCSSTransform) {
            return { x: offsetX, y: offsetY };
        }
        else {
            return this.canvas.client2Viewport(new Point(clientX, clientY));
        }
    }
    destroy() { }
}

class CoordinatePlugin {
    name = 'Coordinate';
    minstep = 0.05 * 16;
    maxStep = 100 * 16;
    rulerTextOffset;
    /** style */
    lineWidth = 1;
    fontSize = 8;
    fontFamily = 'monospace';
    fontWeight = 100;
    textColor = '#757881';
    gridSubkeyColor = '#CCCCCC20';
    gridKeyColor = '#ffffff0F';
    originColor = '#ffffff24';
    dpr = 1;
    showCoordinate = true;
    constructor(opts) {
        if (opts) {
            opts.minstep && (this.minstep = opts.minstep);
            opts.maxStep && (this.maxStep = opts.maxStep);
            opts.lineWidth && (this.lineWidth = opts.lineWidth);
            opts.fontSize && (this.fontSize = opts.fontSize);
            opts.fontFamily && (this.fontFamily = opts.fontFamily);
            opts.fontWeight && (this.fontWeight = opts.fontWeight);
            opts.textColor && (this.textColor = opts.textColor);
            opts.gridSubkeyColor && (this.gridSubkeyColor = opts.gridSubkeyColor);
            opts.gridKeyColor && (this.gridKeyColor = opts.gridKeyColor);
            opts.showCoordinate && (this.showCoordinate = opts.showCoordinate);
        }
        this.rulerTextOffset = [20, 20];
    }
    apply = (context) => {
        const { renderingSystem, renderingContext, camera, config, contextSystem } = context;
        const { devicePixelRatio } = config;
        this.dpr = devicePixelRatio;
        renderingSystem.hooks.init.tap(() => {
            camera.setFlipY(true);
        });
        const drawCoordinate = () => {
            const { width, height } = config;
            const ctx = contextSystem.getContext();
            const cam = camera;
            const zoom = cam.Zoom;
            const viewScaleX = zoom[0]; // fixed pixel per unit
            const viewScaleY = zoom[1];
            const left = cam.BoxLeft / zoom[0] + cam.Position[0];
            const right = cam.BoxRight / zoom[0] + cam.Position[0];
            const top = cam.BoxTop / zoom[1] + cam.Position[1];
            const bottom = cam.BoxBottom / zoom[1] + cam.Position[1];
            const xList = this.calculateRulerScale(width, viewScaleX, false, left, right);
            const yList = this.calculateRulerScale(height, viewScaleY, true, bottom, top);
            ctx.save();
            ctx.scale(this.dpr, this.dpr);
            ctx.clearRect(0, 0, width, height);
            this.drawXRulerScale(ctx, xList, height);
            this.drawYRulerScale(ctx, yList);
            this.drawGrid(ctx, {
                xRulerScale: xList,
                yRulerScale: yList,
                width,
                height,
            });
            ctx.restore();
        };
        renderingSystem.hooks.beforeRender.tap(() => {
            this.showCoordinate && drawCoordinate();
        });
    };
    calculateRulerScale(length, scale, flip, begin, end) {
        const list = [];
        //  const step = clamp(scale, this.minstep, this.maxStep);
        const step = scale;
        // console.log(
        //   'axis: %s, number from %s to %s, offset from %s to %s, scale: %s',
        //   flip ? 'y' : 'x',
        //   b,
        //   e,
        //   begin,
        //   end,
        //   scale
        // );
        const be = end - begin;
        // be         keyunit
        // 0 ~ 0.5 -   0.05
        // 0.5 ~ 1 -   0.1
        // 1 ~ 5   -   0.5
        // 5 ~ 10  -   1
        // 10 ~ 50 -   5
        // 50 ~ 100-   10
        let keyUnit = Math.pow(10, Math.ceil(Math.log10(be)) - 1);
        const temp = Math.pow(10, Math.ceil(Math.log10(be)));
        if (be < temp / 2)
            keyUnit /= 2;
        // find biggest lower bounds
        const beginBounds = Math.floor(begin / keyUnit) * keyUnit;
        const offsetOfZero = -begin * step;
        const offset = beginBounds * step + offsetOfZero; // offset of begin num
        for (let i = beginBounds; i <= end; i += keyUnit) {
            list.push({
                num: i,
                position: flip
                    ? length - ((i - beginBounds) * step + offset)
                    : (i - beginBounds) * step + offset,
                isPrimaryKey: true,
            });
            // for (let subi = i + keyUnit / 5; subi < i + keyUnit; subi += keyUnit / 5) {
            //   list.push({
            //     num: subi,
            //     position: flip
            //       ? length - ((subi - beginBounds) * step + offset)
            //       : (subi - beginBounds) * step + offset,
            //     isPrimaryKey: false,
            //   });
            // }
        }
        return list;
    }
    drawXRulerScale(ctx, rulerScale, canvasHeight) {
        rulerScale.forEach((r) => {
            if (!r.isPrimaryKey)
                return;
            ctx.fillStyle = this.textColor;
            setFont(ctx, this.dpr, {
                fontSize: this.fontSize,
                fontFamily: this.fontFamily,
                fontWeight: this.fontWeight,
            });
            ctx.fillText((Math.round(r.num * 1000) / 1000).toString(), r.position, canvasHeight - this.rulerTextOffset[0] + this.fontSize);
        });
    }
    drawYRulerScale(ctx, rulerScale) {
        ctx.textAlign = 'right';
        rulerScale.forEach((r) => {
            if (!r.isPrimaryKey)
                return;
            ctx.fillStyle = this.textColor;
            setFont(ctx, this.dpr, {
                fontSize: this.fontSize,
                fontFamily: this.fontFamily,
                fontWeight: this.fontWeight,
            });
            ctx.fillText((Math.round(r.num * 1000) / 1000).toString(), this.rulerTextOffset[1], r.position);
        });
    }
    drawGrid(ctx, opts) {
        const { xRulerScale, yRulerScale, width, height } = opts;
        for (const a of xRulerScale) {
            ctx.beginPath();
            ctx.moveTo(a.position, 0);
            ctx.lineTo(a.position, height);
            if (a.num === 0)
                ctx.strokeStyle = this.originColor;
            else
                ctx.strokeStyle = a.isPrimaryKey ? this.gridKeyColor : this.gridSubkeyColor;
            ctx.closePath();
            ctx.lineWidth = this.lineWidth;
            ctx.setLineDash([]);
            ctx.stroke();
        }
        for (const a of yRulerScale) {
            ctx.beginPath();
            ctx.moveTo(0, a.position);
            ctx.lineTo(width, a.position);
            if (a.num === 0)
                ctx.strokeStyle = this.originColor;
            else
                ctx.strokeStyle = a.isPrimaryKey ? this.gridKeyColor : this.gridSubkeyColor;
            ctx.closePath();
            ctx.lineWidth = this.lineWidth;
            ctx.setLineDash([]);
            ctx.stroke();
        }
    }
    destroy() { }
}
function setFont(ctx, dpr, opts) {
    const strList = [];
    if (opts.fontWeight) {
        strList.push(`${opts.fontWeight}`);
    }
    strList.push(`${opts.fontSize || 12}px`);
    strList.push(`${opts.fontFamily || 'sans-serif'}`);
    ctx.font = `${strList.join(' ')}`;
}

class CullingPlugin {
    name = 'Culling';
    apply(context) {
        context.renderingSystem.hooks.cull.tap((object, camera) => {
            return object;
        });
    }
    destroy() { }
}

class ResizePlugin {
    name = 'ResizePlugin';
    apply(context) {
        const renderingSystem = context.renderingSystem;
        const resize = (width, height) => {
            const config = context.config;
            const contextSystem = context.contextSystem;
            config.width = width;
            config.height = height;
            contextSystem.resize(width, height);
        };
        const debouncedResize = throttle(resize, 50, {
            trailing: true,
        });
        renderingSystem.hooks.init.tap(() => {
            const resizeObserver = new ResizeObserver((entries) => {
                const { width, height } = entries[0].contentRect;
                debouncedResize(width, height);
            });
            const container = context.config.container;
            resizeObserver.observe(container);
            const dispose = () => {
                resizeObserver.unobserve(container);
            };
            renderingSystem.hooks.destroy.tap(() => {
                dispose();
            });
        });
    }
    destroy() { }
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var hammer = {exports: {}};

/*! Hammer.JS - v2.0.7 - 2016-04-22
 * http://hammerjs.github.io/
 *
 * Copyright (c) 2016 Jorik Tangelder;
 * Licensed under the MIT license */

(function (module) {
	(function(window, document, exportName, undefined$1) {

	var VENDOR_PREFIXES = ['', 'webkit', 'Moz', 'MS', 'ms', 'o'];
	var TEST_ELEMENT = document.createElement('div');

	var TYPE_FUNCTION = 'function';

	var round = Math.round;
	var abs = Math.abs;
	var now = Date.now;

	/**
	 * set a timeout with a given scope
	 * @param {Function} fn
	 * @param {Number} timeout
	 * @param {Object} context
	 * @returns {number}
	 */
	function setTimeoutContext(fn, timeout, context) {
	    return setTimeout(bindFn(fn, context), timeout);
	}

	/**
	 * if the argument is an array, we want to execute the fn on each entry
	 * if it aint an array we don't want to do a thing.
	 * this is used by all the methods that accept a single and array argument.
	 * @param {*|Array} arg
	 * @param {String} fn
	 * @param {Object} [context]
	 * @returns {Boolean}
	 */
	function invokeArrayArg(arg, fn, context) {
	    if (Array.isArray(arg)) {
	        each(arg, context[fn], context);
	        return true;
	    }
	    return false;
	}

	/**
	 * walk objects and arrays
	 * @param {Object} obj
	 * @param {Function} iterator
	 * @param {Object} context
	 */
	function each(obj, iterator, context) {
	    var i;

	    if (!obj) {
	        return;
	    }

	    if (obj.forEach) {
	        obj.forEach(iterator, context);
	    } else if (obj.length !== undefined$1) {
	        i = 0;
	        while (i < obj.length) {
	            iterator.call(context, obj[i], i, obj);
	            i++;
	        }
	    } else {
	        for (i in obj) {
	            obj.hasOwnProperty(i) && iterator.call(context, obj[i], i, obj);
	        }
	    }
	}

	/**
	 * wrap a method with a deprecation warning and stack trace
	 * @param {Function} method
	 * @param {String} name
	 * @param {String} message
	 * @returns {Function} A new function wrapping the supplied method.
	 */
	function deprecate(method, name, message) {
	    var deprecationMessage = 'DEPRECATED METHOD: ' + name + '\n' + message + ' AT \n';
	    return function() {
	        var e = new Error('get-stack-trace');
	        var stack = e && e.stack ? e.stack.replace(/^[^\(]+?[\n$]/gm, '')
	            .replace(/^\s+at\s+/gm, '')
	            .replace(/^Object.<anonymous>\s*\(/gm, '{anonymous}()@') : 'Unknown Stack Trace';

	        var log = window.console && (window.console.warn || window.console.log);
	        if (log) {
	            log.call(window.console, deprecationMessage, stack);
	        }
	        return method.apply(this, arguments);
	    };
	}

	/**
	 * extend object.
	 * means that properties in dest will be overwritten by the ones in src.
	 * @param {Object} target
	 * @param {...Object} objects_to_assign
	 * @returns {Object} target
	 */
	var assign;
	if (typeof Object.assign !== 'function') {
	    assign = function assign(target) {
	        if (target === undefined$1 || target === null) {
	            throw new TypeError('Cannot convert undefined or null to object');
	        }

	        var output = Object(target);
	        for (var index = 1; index < arguments.length; index++) {
	            var source = arguments[index];
	            if (source !== undefined$1 && source !== null) {
	                for (var nextKey in source) {
	                    if (source.hasOwnProperty(nextKey)) {
	                        output[nextKey] = source[nextKey];
	                    }
	                }
	            }
	        }
	        return output;
	    };
	} else {
	    assign = Object.assign;
	}

	/**
	 * extend object.
	 * means that properties in dest will be overwritten by the ones in src.
	 * @param {Object} dest
	 * @param {Object} src
	 * @param {Boolean} [merge=false]
	 * @returns {Object} dest
	 */
	var extend = deprecate(function extend(dest, src, merge) {
	    var keys = Object.keys(src);
	    var i = 0;
	    while (i < keys.length) {
	        if (!merge || (merge && dest[keys[i]] === undefined$1)) {
	            dest[keys[i]] = src[keys[i]];
	        }
	        i++;
	    }
	    return dest;
	}, 'extend', 'Use `assign`.');

	/**
	 * merge the values from src in the dest.
	 * means that properties that exist in dest will not be overwritten by src
	 * @param {Object} dest
	 * @param {Object} src
	 * @returns {Object} dest
	 */
	var merge = deprecate(function merge(dest, src) {
	    return extend(dest, src, true);
	}, 'merge', 'Use `assign`.');

	/**
	 * simple class inheritance
	 * @param {Function} child
	 * @param {Function} base
	 * @param {Object} [properties]
	 */
	function inherit(child, base, properties) {
	    var baseP = base.prototype,
	        childP;

	    childP = child.prototype = Object.create(baseP);
	    childP.constructor = child;
	    childP._super = baseP;

	    if (properties) {
	        assign(childP, properties);
	    }
	}

	/**
	 * simple function bind
	 * @param {Function} fn
	 * @param {Object} context
	 * @returns {Function}
	 */
	function bindFn(fn, context) {
	    return function boundFn() {
	        return fn.apply(context, arguments);
	    };
	}

	/**
	 * let a boolean value also be a function that must return a boolean
	 * this first item in args will be used as the context
	 * @param {Boolean|Function} val
	 * @param {Array} [args]
	 * @returns {Boolean}
	 */
	function boolOrFn(val, args) {
	    if (typeof val == TYPE_FUNCTION) {
	        return val.apply(args ? args[0] || undefined$1 : undefined$1, args);
	    }
	    return val;
	}

	/**
	 * use the val2 when val1 is undefined
	 * @param {*} val1
	 * @param {*} val2
	 * @returns {*}
	 */
	function ifUndefined(val1, val2) {
	    return (val1 === undefined$1) ? val2 : val1;
	}

	/**
	 * addEventListener with multiple events at once
	 * @param {EventTarget} target
	 * @param {String} types
	 * @param {Function} handler
	 */
	function addEventListeners(target, types, handler) {
	    each(splitStr(types), function(type) {
	        target.addEventListener(type, handler, false);
	    });
	}

	/**
	 * removeEventListener with multiple events at once
	 * @param {EventTarget} target
	 * @param {String} types
	 * @param {Function} handler
	 */
	function removeEventListeners(target, types, handler) {
	    each(splitStr(types), function(type) {
	        target.removeEventListener(type, handler, false);
	    });
	}

	/**
	 * find if a node is in the given parent
	 * @method hasParent
	 * @param {HTMLElement} node
	 * @param {HTMLElement} parent
	 * @return {Boolean} found
	 */
	function hasParent(node, parent) {
	    while (node) {
	        if (node == parent) {
	            return true;
	        }
	        node = node.parentNode;
	    }
	    return false;
	}

	/**
	 * small indexOf wrapper
	 * @param {String} str
	 * @param {String} find
	 * @returns {Boolean} found
	 */
	function inStr(str, find) {
	    return str.indexOf(find) > -1;
	}

	/**
	 * split string on whitespace
	 * @param {String} str
	 * @returns {Array} words
	 */
	function splitStr(str) {
	    return str.trim().split(/\s+/g);
	}

	/**
	 * find if a array contains the object using indexOf or a simple polyFill
	 * @param {Array} src
	 * @param {String} find
	 * @param {String} [findByKey]
	 * @return {Boolean|Number} false when not found, or the index
	 */
	function inArray(src, find, findByKey) {
	    if (src.indexOf && !findByKey) {
	        return src.indexOf(find);
	    } else {
	        var i = 0;
	        while (i < src.length) {
	            if ((findByKey && src[i][findByKey] == find) || (!findByKey && src[i] === find)) {
	                return i;
	            }
	            i++;
	        }
	        return -1;
	    }
	}

	/**
	 * convert array-like objects to real arrays
	 * @param {Object} obj
	 * @returns {Array}
	 */
	function toArray(obj) {
	    return Array.prototype.slice.call(obj, 0);
	}

	/**
	 * unique array with objects based on a key (like 'id') or just by the array's value
	 * @param {Array} src [{id:1},{id:2},{id:1}]
	 * @param {String} [key]
	 * @param {Boolean} [sort=False]
	 * @returns {Array} [{id:1},{id:2}]
	 */
	function uniqueArray(src, key, sort) {
	    var results = [];
	    var values = [];
	    var i = 0;

	    while (i < src.length) {
	        var val = src[i][key] ;
	        if (inArray(values, val) < 0) {
	            results.push(src[i]);
	        }
	        values[i] = val;
	        i++;
	    }

	    {
	        {
	            results = results.sort(function sortUniqueArray(a, b) {
	                return a[key] > b[key];
	            });
	        }
	    }

	    return results;
	}

	/**
	 * get the prefixed property
	 * @param {Object} obj
	 * @param {String} property
	 * @returns {String|Undefined} prefixed
	 */
	function prefixed(obj, property) {
	    var prefix, prop;
	    var camelProp = property[0].toUpperCase() + property.slice(1);

	    var i = 0;
	    while (i < VENDOR_PREFIXES.length) {
	        prefix = VENDOR_PREFIXES[i];
	        prop = (prefix) ? prefix + camelProp : property;

	        if (prop in obj) {
	            return prop;
	        }
	        i++;
	    }
	    return undefined$1;
	}

	/**
	 * get a unique id
	 * @returns {number} uniqueId
	 */
	var _uniqueId = 1;
	function uniqueId() {
	    return _uniqueId++;
	}

	/**
	 * get the window object of an element
	 * @param {HTMLElement} element
	 * @returns {DocumentView|Window}
	 */
	function getWindowForElement(element) {
	    var doc = element.ownerDocument || element;
	    return (doc.defaultView || doc.parentWindow || window);
	}

	var MOBILE_REGEX = /mobile|tablet|ip(ad|hone|od)|android/i;

	var SUPPORT_TOUCH = ('ontouchstart' in window);
	var SUPPORT_POINTER_EVENTS = prefixed(window, 'PointerEvent') !== undefined$1;
	var SUPPORT_ONLY_TOUCH = SUPPORT_TOUCH && MOBILE_REGEX.test(navigator.userAgent);

	var INPUT_TYPE_TOUCH = 'touch';
	var INPUT_TYPE_PEN = 'pen';
	var INPUT_TYPE_MOUSE = 'mouse';
	var INPUT_TYPE_KINECT = 'kinect';

	var COMPUTE_INTERVAL = 25;

	var INPUT_START = 1;
	var INPUT_MOVE = 2;
	var INPUT_END = 4;
	var INPUT_CANCEL = 8;

	var DIRECTION_NONE = 1;
	var DIRECTION_LEFT = 2;
	var DIRECTION_RIGHT = 4;
	var DIRECTION_UP = 8;
	var DIRECTION_DOWN = 16;

	var DIRECTION_HORIZONTAL = DIRECTION_LEFT | DIRECTION_RIGHT;
	var DIRECTION_VERTICAL = DIRECTION_UP | DIRECTION_DOWN;
	var DIRECTION_ALL = DIRECTION_HORIZONTAL | DIRECTION_VERTICAL;

	var PROPS_XY = ['x', 'y'];
	var PROPS_CLIENT_XY = ['clientX', 'clientY'];

	/**
	 * create new input type manager
	 * @param {Manager} manager
	 * @param {Function} callback
	 * @returns {Input}
	 * @constructor
	 */
	function Input(manager, callback) {
	    var self = this;
	    this.manager = manager;
	    this.callback = callback;
	    this.element = manager.element;
	    this.target = manager.options.inputTarget;

	    // smaller wrapper around the handler, for the scope and the enabled state of the manager,
	    // so when disabled the input events are completely bypassed.
	    this.domHandler = function(ev) {
	        if (boolOrFn(manager.options.enable, [manager])) {
	            self.handler(ev);
	        }
	    };

	    this.init();

	}

	Input.prototype = {
	    /**
	     * should handle the inputEvent data and trigger the callback
	     * @virtual
	     */
	    handler: function() { },

	    /**
	     * bind the events
	     */
	    init: function() {
	        this.evEl && addEventListeners(this.element, this.evEl, this.domHandler);
	        this.evTarget && addEventListeners(this.target, this.evTarget, this.domHandler);
	        this.evWin && addEventListeners(getWindowForElement(this.element), this.evWin, this.domHandler);
	    },

	    /**
	     * unbind the events
	     */
	    destroy: function() {
	        this.evEl && removeEventListeners(this.element, this.evEl, this.domHandler);
	        this.evTarget && removeEventListeners(this.target, this.evTarget, this.domHandler);
	        this.evWin && removeEventListeners(getWindowForElement(this.element), this.evWin, this.domHandler);
	    }
	};

	/**
	 * create new input type manager
	 * called by the Manager constructor
	 * @param {Hammer} manager
	 * @returns {Input}
	 */
	function createInputInstance(manager) {
	    var Type;
	    var inputClass = manager.options.inputClass;

	    if (inputClass) {
	        Type = inputClass;
	    } else if (SUPPORT_POINTER_EVENTS) {
	        Type = PointerEventInput;
	    } else if (SUPPORT_ONLY_TOUCH) {
	        Type = TouchInput;
	    } else if (!SUPPORT_TOUCH) {
	        Type = MouseInput;
	    } else {
	        Type = TouchMouseInput;
	    }
	    return new (Type)(manager, inputHandler);
	}

	/**
	 * handle input events
	 * @param {Manager} manager
	 * @param {String} eventType
	 * @param {Object} input
	 */
	function inputHandler(manager, eventType, input) {
	    var pointersLen = input.pointers.length;
	    var changedPointersLen = input.changedPointers.length;
	    var isFirst = (eventType & INPUT_START && (pointersLen - changedPointersLen === 0));
	    var isFinal = (eventType & (INPUT_END | INPUT_CANCEL) && (pointersLen - changedPointersLen === 0));

	    input.isFirst = !!isFirst;
	    input.isFinal = !!isFinal;

	    if (isFirst) {
	        manager.session = {};
	    }

	    // source event is the normalized value of the domEvents
	    // like 'touchstart, mouseup, pointerdown'
	    input.eventType = eventType;

	    // compute scale, rotation etc
	    computeInputData(manager, input);

	    // emit secret event
	    manager.emit('hammer.input', input);

	    manager.recognize(input);
	    manager.session.prevInput = input;
	}

	/**
	 * extend the data with some usable properties like scale, rotate, velocity etc
	 * @param {Object} manager
	 * @param {Object} input
	 */
	function computeInputData(manager, input) {
	    var session = manager.session;
	    var pointers = input.pointers;
	    var pointersLength = pointers.length;

	    // store the first input to calculate the distance and direction
	    if (!session.firstInput) {
	        session.firstInput = simpleCloneInputData(input);
	    }

	    // to compute scale and rotation we need to store the multiple touches
	    if (pointersLength > 1 && !session.firstMultiple) {
	        session.firstMultiple = simpleCloneInputData(input);
	    } else if (pointersLength === 1) {
	        session.firstMultiple = false;
	    }

	    var firstInput = session.firstInput;
	    var firstMultiple = session.firstMultiple;
	    var offsetCenter = firstMultiple ? firstMultiple.center : firstInput.center;

	    var center = input.center = getCenter(pointers);
	    input.timeStamp = now();
	    input.deltaTime = input.timeStamp - firstInput.timeStamp;

	    input.angle = getAngle(offsetCenter, center);
	    input.distance = getDistance(offsetCenter, center);

	    computeDeltaXY(session, input);
	    input.offsetDirection = getDirection(input.deltaX, input.deltaY);

	    var overallVelocity = getVelocity(input.deltaTime, input.deltaX, input.deltaY);
	    input.overallVelocityX = overallVelocity.x;
	    input.overallVelocityY = overallVelocity.y;
	    input.overallVelocity = (abs(overallVelocity.x) > abs(overallVelocity.y)) ? overallVelocity.x : overallVelocity.y;

	    input.scale = firstMultiple ? getScale(firstMultiple.pointers, pointers) : 1;
	    input.rotation = firstMultiple ? getRotation(firstMultiple.pointers, pointers) : 0;

	    input.maxPointers = !session.prevInput ? input.pointers.length : ((input.pointers.length >
	        session.prevInput.maxPointers) ? input.pointers.length : session.prevInput.maxPointers);

	    computeIntervalInputData(session, input);

	    // find the correct target
	    var target = manager.element;
	    if (hasParent(input.srcEvent.target, target)) {
	        target = input.srcEvent.target;
	    }
	    input.target = target;
	}

	function computeDeltaXY(session, input) {
	    var center = input.center;
	    var offset = session.offsetDelta || {};
	    var prevDelta = session.prevDelta || {};
	    var prevInput = session.prevInput || {};

	    if (input.eventType === INPUT_START || prevInput.eventType === INPUT_END) {
	        prevDelta = session.prevDelta = {
	            x: prevInput.deltaX || 0,
	            y: prevInput.deltaY || 0
	        };

	        offset = session.offsetDelta = {
	            x: center.x,
	            y: center.y
	        };
	    }

	    input.deltaX = prevDelta.x + (center.x - offset.x);
	    input.deltaY = prevDelta.y + (center.y - offset.y);
	}

	/**
	 * velocity is calculated every x ms
	 * @param {Object} session
	 * @param {Object} input
	 */
	function computeIntervalInputData(session, input) {
	    var last = session.lastInterval || input,
	        deltaTime = input.timeStamp - last.timeStamp,
	        velocity, velocityX, velocityY, direction;

	    if (input.eventType != INPUT_CANCEL && (deltaTime > COMPUTE_INTERVAL || last.velocity === undefined$1)) {
	        var deltaX = input.deltaX - last.deltaX;
	        var deltaY = input.deltaY - last.deltaY;

	        var v = getVelocity(deltaTime, deltaX, deltaY);
	        velocityX = v.x;
	        velocityY = v.y;
	        velocity = (abs(v.x) > abs(v.y)) ? v.x : v.y;
	        direction = getDirection(deltaX, deltaY);

	        session.lastInterval = input;
	    } else {
	        // use latest velocity info if it doesn't overtake a minimum period
	        velocity = last.velocity;
	        velocityX = last.velocityX;
	        velocityY = last.velocityY;
	        direction = last.direction;
	    }

	    input.velocity = velocity;
	    input.velocityX = velocityX;
	    input.velocityY = velocityY;
	    input.direction = direction;
	}

	/**
	 * create a simple clone from the input used for storage of firstInput and firstMultiple
	 * @param {Object} input
	 * @returns {Object} clonedInputData
	 */
	function simpleCloneInputData(input) {
	    // make a simple copy of the pointers because we will get a reference if we don't
	    // we only need clientXY for the calculations
	    var pointers = [];
	    var i = 0;
	    while (i < input.pointers.length) {
	        pointers[i] = {
	            clientX: round(input.pointers[i].clientX),
	            clientY: round(input.pointers[i].clientY)
	        };
	        i++;
	    }

	    return {
	        timeStamp: now(),
	        pointers: pointers,
	        center: getCenter(pointers),
	        deltaX: input.deltaX,
	        deltaY: input.deltaY
	    };
	}

	/**
	 * get the center of all the pointers
	 * @param {Array} pointers
	 * @return {Object} center contains `x` and `y` properties
	 */
	function getCenter(pointers) {
	    var pointersLength = pointers.length;

	    // no need to loop when only one touch
	    if (pointersLength === 1) {
	        return {
	            x: round(pointers[0].clientX),
	            y: round(pointers[0].clientY)
	        };
	    }

	    var x = 0, y = 0, i = 0;
	    while (i < pointersLength) {
	        x += pointers[i].clientX;
	        y += pointers[i].clientY;
	        i++;
	    }

	    return {
	        x: round(x / pointersLength),
	        y: round(y / pointersLength)
	    };
	}

	/**
	 * calculate the velocity between two points. unit is in px per ms.
	 * @param {Number} deltaTime
	 * @param {Number} x
	 * @param {Number} y
	 * @return {Object} velocity `x` and `y`
	 */
	function getVelocity(deltaTime, x, y) {
	    return {
	        x: x / deltaTime || 0,
	        y: y / deltaTime || 0
	    };
	}

	/**
	 * get the direction between two points
	 * @param {Number} x
	 * @param {Number} y
	 * @return {Number} direction
	 */
	function getDirection(x, y) {
	    if (x === y) {
	        return DIRECTION_NONE;
	    }

	    if (abs(x) >= abs(y)) {
	        return x < 0 ? DIRECTION_LEFT : DIRECTION_RIGHT;
	    }
	    return y < 0 ? DIRECTION_UP : DIRECTION_DOWN;
	}

	/**
	 * calculate the absolute distance between two points
	 * @param {Object} p1 {x, y}
	 * @param {Object} p2 {x, y}
	 * @param {Array} [props] containing x and y keys
	 * @return {Number} distance
	 */
	function getDistance(p1, p2, props) {
	    if (!props) {
	        props = PROPS_XY;
	    }
	    var x = p2[props[0]] - p1[props[0]],
	        y = p2[props[1]] - p1[props[1]];

	    return Math.sqrt((x * x) + (y * y));
	}

	/**
	 * calculate the angle between two coordinates
	 * @param {Object} p1
	 * @param {Object} p2
	 * @param {Array} [props] containing x and y keys
	 * @return {Number} angle
	 */
	function getAngle(p1, p2, props) {
	    if (!props) {
	        props = PROPS_XY;
	    }
	    var x = p2[props[0]] - p1[props[0]],
	        y = p2[props[1]] - p1[props[1]];
	    return Math.atan2(y, x) * 180 / Math.PI;
	}

	/**
	 * calculate the rotation degrees between two pointersets
	 * @param {Array} start array of pointers
	 * @param {Array} end array of pointers
	 * @return {Number} rotation
	 */
	function getRotation(start, end) {
	    return getAngle(end[1], end[0], PROPS_CLIENT_XY) + getAngle(start[1], start[0], PROPS_CLIENT_XY);
	}

	/**
	 * calculate the scale factor between two pointersets
	 * no scale is 1, and goes down to 0 when pinched together, and bigger when pinched out
	 * @param {Array} start array of pointers
	 * @param {Array} end array of pointers
	 * @return {Number} scale
	 */
	function getScale(start, end) {
	    return getDistance(end[0], end[1], PROPS_CLIENT_XY) / getDistance(start[0], start[1], PROPS_CLIENT_XY);
	}

	var MOUSE_INPUT_MAP = {
	    mousedown: INPUT_START,
	    mousemove: INPUT_MOVE,
	    mouseup: INPUT_END
	};

	var MOUSE_ELEMENT_EVENTS = 'mousedown';
	var MOUSE_WINDOW_EVENTS = 'mousemove mouseup';

	/**
	 * Mouse events input
	 * @constructor
	 * @extends Input
	 */
	function MouseInput() {
	    this.evEl = MOUSE_ELEMENT_EVENTS;
	    this.evWin = MOUSE_WINDOW_EVENTS;

	    this.pressed = false; // mousedown state

	    Input.apply(this, arguments);
	}

	inherit(MouseInput, Input, {
	    /**
	     * handle mouse events
	     * @param {Object} ev
	     */
	    handler: function MEhandler(ev) {
	        var eventType = MOUSE_INPUT_MAP[ev.type];

	        // on start we want to have the left mouse button down
	        if (eventType & INPUT_START && ev.button === 0) {
	            this.pressed = true;
	        }

	        if (eventType & INPUT_MOVE && ev.which !== 1) {
	            eventType = INPUT_END;
	        }

	        // mouse must be down
	        if (!this.pressed) {
	            return;
	        }

	        if (eventType & INPUT_END) {
	            this.pressed = false;
	        }

	        this.callback(this.manager, eventType, {
	            pointers: [ev],
	            changedPointers: [ev],
	            pointerType: INPUT_TYPE_MOUSE,
	            srcEvent: ev
	        });
	    }
	});

	var POINTER_INPUT_MAP = {
	    pointerdown: INPUT_START,
	    pointermove: INPUT_MOVE,
	    pointerup: INPUT_END,
	    pointercancel: INPUT_CANCEL,
	    pointerout: INPUT_CANCEL
	};

	// in IE10 the pointer types is defined as an enum
	var IE10_POINTER_TYPE_ENUM = {
	    2: INPUT_TYPE_TOUCH,
	    3: INPUT_TYPE_PEN,
	    4: INPUT_TYPE_MOUSE,
	    5: INPUT_TYPE_KINECT // see https://twitter.com/jacobrossi/status/480596438489890816
	};

	var POINTER_ELEMENT_EVENTS = 'pointerdown';
	var POINTER_WINDOW_EVENTS = 'pointermove pointerup pointercancel';

	// IE10 has prefixed support, and case-sensitive
	if (window.MSPointerEvent && !window.PointerEvent) {
	    POINTER_ELEMENT_EVENTS = 'MSPointerDown';
	    POINTER_WINDOW_EVENTS = 'MSPointerMove MSPointerUp MSPointerCancel';
	}

	/**
	 * Pointer events input
	 * @constructor
	 * @extends Input
	 */
	function PointerEventInput() {
	    this.evEl = POINTER_ELEMENT_EVENTS;
	    this.evWin = POINTER_WINDOW_EVENTS;

	    Input.apply(this, arguments);

	    this.store = (this.manager.session.pointerEvents = []);
	}

	inherit(PointerEventInput, Input, {
	    /**
	     * handle mouse events
	     * @param {Object} ev
	     */
	    handler: function PEhandler(ev) {
	        var store = this.store;
	        var removePointer = false;

	        var eventTypeNormalized = ev.type.toLowerCase().replace('ms', '');
	        var eventType = POINTER_INPUT_MAP[eventTypeNormalized];
	        var pointerType = IE10_POINTER_TYPE_ENUM[ev.pointerType] || ev.pointerType;

	        var isTouch = (pointerType == INPUT_TYPE_TOUCH);

	        // get index of the event in the store
	        var storeIndex = inArray(store, ev.pointerId, 'pointerId');

	        // start and mouse must be down
	        if (eventType & INPUT_START && (ev.button === 0 || isTouch)) {
	            if (storeIndex < 0) {
	                store.push(ev);
	                storeIndex = store.length - 1;
	            }
	        } else if (eventType & (INPUT_END | INPUT_CANCEL)) {
	            removePointer = true;
	        }

	        // it not found, so the pointer hasn't been down (so it's probably a hover)
	        if (storeIndex < 0) {
	            return;
	        }

	        // update the event in the store
	        store[storeIndex] = ev;

	        this.callback(this.manager, eventType, {
	            pointers: store,
	            changedPointers: [ev],
	            pointerType: pointerType,
	            srcEvent: ev
	        });

	        if (removePointer) {
	            // remove from the store
	            store.splice(storeIndex, 1);
	        }
	    }
	});

	var SINGLE_TOUCH_INPUT_MAP = {
	    touchstart: INPUT_START,
	    touchmove: INPUT_MOVE,
	    touchend: INPUT_END,
	    touchcancel: INPUT_CANCEL
	};

	var SINGLE_TOUCH_TARGET_EVENTS = 'touchstart';
	var SINGLE_TOUCH_WINDOW_EVENTS = 'touchstart touchmove touchend touchcancel';

	/**
	 * Touch events input
	 * @constructor
	 * @extends Input
	 */
	function SingleTouchInput() {
	    this.evTarget = SINGLE_TOUCH_TARGET_EVENTS;
	    this.evWin = SINGLE_TOUCH_WINDOW_EVENTS;
	    this.started = false;

	    Input.apply(this, arguments);
	}

	inherit(SingleTouchInput, Input, {
	    handler: function TEhandler(ev) {
	        var type = SINGLE_TOUCH_INPUT_MAP[ev.type];

	        // should we handle the touch events?
	        if (type === INPUT_START) {
	            this.started = true;
	        }

	        if (!this.started) {
	            return;
	        }

	        var touches = normalizeSingleTouches.call(this, ev, type);

	        // when done, reset the started state
	        if (type & (INPUT_END | INPUT_CANCEL) && touches[0].length - touches[1].length === 0) {
	            this.started = false;
	        }

	        this.callback(this.manager, type, {
	            pointers: touches[0],
	            changedPointers: touches[1],
	            pointerType: INPUT_TYPE_TOUCH,
	            srcEvent: ev
	        });
	    }
	});

	/**
	 * @this {TouchInput}
	 * @param {Object} ev
	 * @param {Number} type flag
	 * @returns {undefined|Array} [all, changed]
	 */
	function normalizeSingleTouches(ev, type) {
	    var all = toArray(ev.touches);
	    var changed = toArray(ev.changedTouches);

	    if (type & (INPUT_END | INPUT_CANCEL)) {
	        all = uniqueArray(all.concat(changed), 'identifier');
	    }

	    return [all, changed];
	}

	var TOUCH_INPUT_MAP = {
	    touchstart: INPUT_START,
	    touchmove: INPUT_MOVE,
	    touchend: INPUT_END,
	    touchcancel: INPUT_CANCEL
	};

	var TOUCH_TARGET_EVENTS = 'touchstart touchmove touchend touchcancel';

	/**
	 * Multi-user touch events input
	 * @constructor
	 * @extends Input
	 */
	function TouchInput() {
	    this.evTarget = TOUCH_TARGET_EVENTS;
	    this.targetIds = {};

	    Input.apply(this, arguments);
	}

	inherit(TouchInput, Input, {
	    handler: function MTEhandler(ev) {
	        var type = TOUCH_INPUT_MAP[ev.type];
	        var touches = getTouches.call(this, ev, type);
	        if (!touches) {
	            return;
	        }

	        this.callback(this.manager, type, {
	            pointers: touches[0],
	            changedPointers: touches[1],
	            pointerType: INPUT_TYPE_TOUCH,
	            srcEvent: ev
	        });
	    }
	});

	/**
	 * @this {TouchInput}
	 * @param {Object} ev
	 * @param {Number} type flag
	 * @returns {undefined|Array} [all, changed]
	 */
	function getTouches(ev, type) {
	    var allTouches = toArray(ev.touches);
	    var targetIds = this.targetIds;

	    // when there is only one touch, the process can be simplified
	    if (type & (INPUT_START | INPUT_MOVE) && allTouches.length === 1) {
	        targetIds[allTouches[0].identifier] = true;
	        return [allTouches, allTouches];
	    }

	    var i,
	        targetTouches,
	        changedTouches = toArray(ev.changedTouches),
	        changedTargetTouches = [],
	        target = this.target;

	    // get target touches from touches
	    targetTouches = allTouches.filter(function(touch) {
	        return hasParent(touch.target, target);
	    });

	    // collect touches
	    if (type === INPUT_START) {
	        i = 0;
	        while (i < targetTouches.length) {
	            targetIds[targetTouches[i].identifier] = true;
	            i++;
	        }
	    }

	    // filter changed touches to only contain touches that exist in the collected target ids
	    i = 0;
	    while (i < changedTouches.length) {
	        if (targetIds[changedTouches[i].identifier]) {
	            changedTargetTouches.push(changedTouches[i]);
	        }

	        // cleanup removed touches
	        if (type & (INPUT_END | INPUT_CANCEL)) {
	            delete targetIds[changedTouches[i].identifier];
	        }
	        i++;
	    }

	    if (!changedTargetTouches.length) {
	        return;
	    }

	    return [
	        // merge targetTouches with changedTargetTouches so it contains ALL touches, including 'end' and 'cancel'
	        uniqueArray(targetTouches.concat(changedTargetTouches), 'identifier'),
	        changedTargetTouches
	    ];
	}

	/**
	 * Combined touch and mouse input
	 *
	 * Touch has a higher priority then mouse, and while touching no mouse events are allowed.
	 * This because touch devices also emit mouse events while doing a touch.
	 *
	 * @constructor
	 * @extends Input
	 */

	var DEDUP_TIMEOUT = 2500;
	var DEDUP_DISTANCE = 25;

	function TouchMouseInput() {
	    Input.apply(this, arguments);

	    var handler = bindFn(this.handler, this);
	    this.touch = new TouchInput(this.manager, handler);
	    this.mouse = new MouseInput(this.manager, handler);

	    this.primaryTouch = null;
	    this.lastTouches = [];
	}

	inherit(TouchMouseInput, Input, {
	    /**
	     * handle mouse and touch events
	     * @param {Hammer} manager
	     * @param {String} inputEvent
	     * @param {Object} inputData
	     */
	    handler: function TMEhandler(manager, inputEvent, inputData) {
	        var isTouch = (inputData.pointerType == INPUT_TYPE_TOUCH),
	            isMouse = (inputData.pointerType == INPUT_TYPE_MOUSE);

	        if (isMouse && inputData.sourceCapabilities && inputData.sourceCapabilities.firesTouchEvents) {
	            return;
	        }

	        // when we're in a touch event, record touches to  de-dupe synthetic mouse event
	        if (isTouch) {
	            recordTouches.call(this, inputEvent, inputData);
	        } else if (isMouse && isSyntheticEvent.call(this, inputData)) {
	            return;
	        }

	        this.callback(manager, inputEvent, inputData);
	    },

	    /**
	     * remove the event listeners
	     */
	    destroy: function destroy() {
	        this.touch.destroy();
	        this.mouse.destroy();
	    }
	});

	function recordTouches(eventType, eventData) {
	    if (eventType & INPUT_START) {
	        this.primaryTouch = eventData.changedPointers[0].identifier;
	        setLastTouch.call(this, eventData);
	    } else if (eventType & (INPUT_END | INPUT_CANCEL)) {
	        setLastTouch.call(this, eventData);
	    }
	}

	function setLastTouch(eventData) {
	    var touch = eventData.changedPointers[0];

	    if (touch.identifier === this.primaryTouch) {
	        var lastTouch = {x: touch.clientX, y: touch.clientY};
	        this.lastTouches.push(lastTouch);
	        var lts = this.lastTouches;
	        var removeLastTouch = function() {
	            var i = lts.indexOf(lastTouch);
	            if (i > -1) {
	                lts.splice(i, 1);
	            }
	        };
	        setTimeout(removeLastTouch, DEDUP_TIMEOUT);
	    }
	}

	function isSyntheticEvent(eventData) {
	    var x = eventData.srcEvent.clientX, y = eventData.srcEvent.clientY;
	    for (var i = 0; i < this.lastTouches.length; i++) {
	        var t = this.lastTouches[i];
	        var dx = Math.abs(x - t.x), dy = Math.abs(y - t.y);
	        if (dx <= DEDUP_DISTANCE && dy <= DEDUP_DISTANCE) {
	            return true;
	        }
	    }
	    return false;
	}

	var PREFIXED_TOUCH_ACTION = prefixed(TEST_ELEMENT.style, 'touchAction');
	var NATIVE_TOUCH_ACTION = PREFIXED_TOUCH_ACTION !== undefined$1;

	// magical touchAction value
	var TOUCH_ACTION_COMPUTE = 'compute';
	var TOUCH_ACTION_AUTO = 'auto';
	var TOUCH_ACTION_MANIPULATION = 'manipulation'; // not implemented
	var TOUCH_ACTION_NONE = 'none';
	var TOUCH_ACTION_PAN_X = 'pan-x';
	var TOUCH_ACTION_PAN_Y = 'pan-y';
	var TOUCH_ACTION_MAP = getTouchActionProps();

	/**
	 * Touch Action
	 * sets the touchAction property or uses the js alternative
	 * @param {Manager} manager
	 * @param {String} value
	 * @constructor
	 */
	function TouchAction(manager, value) {
	    this.manager = manager;
	    this.set(value);
	}

	TouchAction.prototype = {
	    /**
	     * set the touchAction value on the element or enable the polyfill
	     * @param {String} value
	     */
	    set: function(value) {
	        // find out the touch-action by the event handlers
	        if (value == TOUCH_ACTION_COMPUTE) {
	            value = this.compute();
	        }

	        if (NATIVE_TOUCH_ACTION && this.manager.element.style && TOUCH_ACTION_MAP[value]) {
	            this.manager.element.style[PREFIXED_TOUCH_ACTION] = value;
	        }
	        this.actions = value.toLowerCase().trim();
	    },

	    /**
	     * just re-set the touchAction value
	     */
	    update: function() {
	        this.set(this.manager.options.touchAction);
	    },

	    /**
	     * compute the value for the touchAction property based on the recognizer's settings
	     * @returns {String} value
	     */
	    compute: function() {
	        var actions = [];
	        each(this.manager.recognizers, function(recognizer) {
	            if (boolOrFn(recognizer.options.enable, [recognizer])) {
	                actions = actions.concat(recognizer.getTouchAction());
	            }
	        });
	        return cleanTouchActions(actions.join(' '));
	    },

	    /**
	     * this method is called on each input cycle and provides the preventing of the browser behavior
	     * @param {Object} input
	     */
	    preventDefaults: function(input) {
	        var srcEvent = input.srcEvent;
	        var direction = input.offsetDirection;

	        // if the touch action did prevented once this session
	        if (this.manager.session.prevented) {
	            srcEvent.preventDefault();
	            return;
	        }

	        var actions = this.actions;
	        var hasNone = inStr(actions, TOUCH_ACTION_NONE) && !TOUCH_ACTION_MAP[TOUCH_ACTION_NONE];
	        var hasPanY = inStr(actions, TOUCH_ACTION_PAN_Y) && !TOUCH_ACTION_MAP[TOUCH_ACTION_PAN_Y];
	        var hasPanX = inStr(actions, TOUCH_ACTION_PAN_X) && !TOUCH_ACTION_MAP[TOUCH_ACTION_PAN_X];

	        if (hasNone) {
	            //do not prevent defaults if this is a tap gesture

	            var isTapPointer = input.pointers.length === 1;
	            var isTapMovement = input.distance < 2;
	            var isTapTouchTime = input.deltaTime < 250;

	            if (isTapPointer && isTapMovement && isTapTouchTime) {
	                return;
	            }
	        }

	        if (hasPanX && hasPanY) {
	            // `pan-x pan-y` means browser handles all scrolling/panning, do not prevent
	            return;
	        }

	        if (hasNone ||
	            (hasPanY && direction & DIRECTION_HORIZONTAL) ||
	            (hasPanX && direction & DIRECTION_VERTICAL)) {
	            return this.preventSrc(srcEvent);
	        }
	    },

	    /**
	     * call preventDefault to prevent the browser's default behavior (scrolling in most cases)
	     * @param {Object} srcEvent
	     */
	    preventSrc: function(srcEvent) {
	        this.manager.session.prevented = true;
	        srcEvent.preventDefault();
	    }
	};

	/**
	 * when the touchActions are collected they are not a valid value, so we need to clean things up. *
	 * @param {String} actions
	 * @returns {*}
	 */
	function cleanTouchActions(actions) {
	    // none
	    if (inStr(actions, TOUCH_ACTION_NONE)) {
	        return TOUCH_ACTION_NONE;
	    }

	    var hasPanX = inStr(actions, TOUCH_ACTION_PAN_X);
	    var hasPanY = inStr(actions, TOUCH_ACTION_PAN_Y);

	    // if both pan-x and pan-y are set (different recognizers
	    // for different directions, e.g. horizontal pan but vertical swipe?)
	    // we need none (as otherwise with pan-x pan-y combined none of these
	    // recognizers will work, since the browser would handle all panning
	    if (hasPanX && hasPanY) {
	        return TOUCH_ACTION_NONE;
	    }

	    // pan-x OR pan-y
	    if (hasPanX || hasPanY) {
	        return hasPanX ? TOUCH_ACTION_PAN_X : TOUCH_ACTION_PAN_Y;
	    }

	    // manipulation
	    if (inStr(actions, TOUCH_ACTION_MANIPULATION)) {
	        return TOUCH_ACTION_MANIPULATION;
	    }

	    return TOUCH_ACTION_AUTO;
	}

	function getTouchActionProps() {
	    if (!NATIVE_TOUCH_ACTION) {
	        return false;
	    }
	    var touchMap = {};
	    var cssSupports = window.CSS && window.CSS.supports;
	    ['auto', 'manipulation', 'pan-y', 'pan-x', 'pan-x pan-y', 'none'].forEach(function(val) {

	        // If css.supports is not supported but there is native touch-action assume it supports
	        // all values. This is the case for IE 10 and 11.
	        touchMap[val] = cssSupports ? window.CSS.supports('touch-action', val) : true;
	    });
	    return touchMap;
	}

	/**
	 * Recognizer flow explained; *
	 * All recognizers have the initial state of POSSIBLE when a input session starts.
	 * The definition of a input session is from the first input until the last input, with all it's movement in it. *
	 * Example session for mouse-input: mousedown -> mousemove -> mouseup
	 *
	 * On each recognizing cycle (see Manager.recognize) the .recognize() method is executed
	 * which determines with state it should be.
	 *
	 * If the recognizer has the state FAILED, CANCELLED or RECOGNIZED (equals ENDED), it is reset to
	 * POSSIBLE to give it another change on the next cycle.
	 *
	 *               Possible
	 *                  |
	 *            +-----+---------------+
	 *            |                     |
	 *      +-----+-----+               |
	 *      |           |               |
	 *   Failed      Cancelled          |
	 *                          +-------+------+
	 *                          |              |
	 *                      Recognized       Began
	 *                                         |
	 *                                      Changed
	 *                                         |
	 *                                  Ended/Recognized
	 */
	var STATE_POSSIBLE = 1;
	var STATE_BEGAN = 2;
	var STATE_CHANGED = 4;
	var STATE_ENDED = 8;
	var STATE_RECOGNIZED = STATE_ENDED;
	var STATE_CANCELLED = 16;
	var STATE_FAILED = 32;

	/**
	 * Recognizer
	 * Every recognizer needs to extend from this class.
	 * @constructor
	 * @param {Object} options
	 */
	function Recognizer(options) {
	    this.options = assign({}, this.defaults, options || {});

	    this.id = uniqueId();

	    this.manager = null;

	    // default is enable true
	    this.options.enable = ifUndefined(this.options.enable, true);

	    this.state = STATE_POSSIBLE;

	    this.simultaneous = {};
	    this.requireFail = [];
	}

	Recognizer.prototype = {
	    /**
	     * @virtual
	     * @type {Object}
	     */
	    defaults: {},

	    /**
	     * set options
	     * @param {Object} options
	     * @return {Recognizer}
	     */
	    set: function(options) {
	        assign(this.options, options);

	        // also update the touchAction, in case something changed about the directions/enabled state
	        this.manager && this.manager.touchAction.update();
	        return this;
	    },

	    /**
	     * recognize simultaneous with an other recognizer.
	     * @param {Recognizer} otherRecognizer
	     * @returns {Recognizer} this
	     */
	    recognizeWith: function(otherRecognizer) {
	        if (invokeArrayArg(otherRecognizer, 'recognizeWith', this)) {
	            return this;
	        }

	        var simultaneous = this.simultaneous;
	        otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
	        if (!simultaneous[otherRecognizer.id]) {
	            simultaneous[otherRecognizer.id] = otherRecognizer;
	            otherRecognizer.recognizeWith(this);
	        }
	        return this;
	    },

	    /**
	     * drop the simultaneous link. it doesnt remove the link on the other recognizer.
	     * @param {Recognizer} otherRecognizer
	     * @returns {Recognizer} this
	     */
	    dropRecognizeWith: function(otherRecognizer) {
	        if (invokeArrayArg(otherRecognizer, 'dropRecognizeWith', this)) {
	            return this;
	        }

	        otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
	        delete this.simultaneous[otherRecognizer.id];
	        return this;
	    },

	    /**
	     * recognizer can only run when an other is failing
	     * @param {Recognizer} otherRecognizer
	     * @returns {Recognizer} this
	     */
	    requireFailure: function(otherRecognizer) {
	        if (invokeArrayArg(otherRecognizer, 'requireFailure', this)) {
	            return this;
	        }

	        var requireFail = this.requireFail;
	        otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
	        if (inArray(requireFail, otherRecognizer) === -1) {
	            requireFail.push(otherRecognizer);
	            otherRecognizer.requireFailure(this);
	        }
	        return this;
	    },

	    /**
	     * drop the requireFailure link. it does not remove the link on the other recognizer.
	     * @param {Recognizer} otherRecognizer
	     * @returns {Recognizer} this
	     */
	    dropRequireFailure: function(otherRecognizer) {
	        if (invokeArrayArg(otherRecognizer, 'dropRequireFailure', this)) {
	            return this;
	        }

	        otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
	        var index = inArray(this.requireFail, otherRecognizer);
	        if (index > -1) {
	            this.requireFail.splice(index, 1);
	        }
	        return this;
	    },

	    /**
	     * has require failures boolean
	     * @returns {boolean}
	     */
	    hasRequireFailures: function() {
	        return this.requireFail.length > 0;
	    },

	    /**
	     * if the recognizer can recognize simultaneous with an other recognizer
	     * @param {Recognizer} otherRecognizer
	     * @returns {Boolean}
	     */
	    canRecognizeWith: function(otherRecognizer) {
	        return !!this.simultaneous[otherRecognizer.id];
	    },

	    /**
	     * You should use `tryEmit` instead of `emit` directly to check
	     * that all the needed recognizers has failed before emitting.
	     * @param {Object} input
	     */
	    emit: function(input) {
	        var self = this;
	        var state = this.state;

	        function emit(event) {
	            self.manager.emit(event, input);
	        }

	        // 'panstart' and 'panmove'
	        if (state < STATE_ENDED) {
	            emit(self.options.event + stateStr(state));
	        }

	        emit(self.options.event); // simple 'eventName' events

	        if (input.additionalEvent) { // additional event(panleft, panright, pinchin, pinchout...)
	            emit(input.additionalEvent);
	        }

	        // panend and pancancel
	        if (state >= STATE_ENDED) {
	            emit(self.options.event + stateStr(state));
	        }
	    },

	    /**
	     * Check that all the require failure recognizers has failed,
	     * if true, it emits a gesture event,
	     * otherwise, setup the state to FAILED.
	     * @param {Object} input
	     */
	    tryEmit: function(input) {
	        if (this.canEmit()) {
	            return this.emit(input);
	        }
	        // it's failing anyway
	        this.state = STATE_FAILED;
	    },

	    /**
	     * can we emit?
	     * @returns {boolean}
	     */
	    canEmit: function() {
	        var i = 0;
	        while (i < this.requireFail.length) {
	            if (!(this.requireFail[i].state & (STATE_FAILED | STATE_POSSIBLE))) {
	                return false;
	            }
	            i++;
	        }
	        return true;
	    },

	    /**
	     * update the recognizer
	     * @param {Object} inputData
	     */
	    recognize: function(inputData) {
	        // make a new copy of the inputData
	        // so we can change the inputData without messing up the other recognizers
	        var inputDataClone = assign({}, inputData);

	        // is is enabled and allow recognizing?
	        if (!boolOrFn(this.options.enable, [this, inputDataClone])) {
	            this.reset();
	            this.state = STATE_FAILED;
	            return;
	        }

	        // reset when we've reached the end
	        if (this.state & (STATE_RECOGNIZED | STATE_CANCELLED | STATE_FAILED)) {
	            this.state = STATE_POSSIBLE;
	        }

	        this.state = this.process(inputDataClone);

	        // the recognizer has recognized a gesture
	        // so trigger an event
	        if (this.state & (STATE_BEGAN | STATE_CHANGED | STATE_ENDED | STATE_CANCELLED)) {
	            this.tryEmit(inputDataClone);
	        }
	    },

	    /**
	     * return the state of the recognizer
	     * the actual recognizing happens in this method
	     * @virtual
	     * @param {Object} inputData
	     * @returns {Const} STATE
	     */
	    process: function(inputData) { }, // jshint ignore:line

	    /**
	     * return the preferred touch-action
	     * @virtual
	     * @returns {Array}
	     */
	    getTouchAction: function() { },

	    /**
	     * called when the gesture isn't allowed to recognize
	     * like when another is being recognized or it is disabled
	     * @virtual
	     */
	    reset: function() { }
	};

	/**
	 * get a usable string, used as event postfix
	 * @param {Const} state
	 * @returns {String} state
	 */
	function stateStr(state) {
	    if (state & STATE_CANCELLED) {
	        return 'cancel';
	    } else if (state & STATE_ENDED) {
	        return 'end';
	    } else if (state & STATE_CHANGED) {
	        return 'move';
	    } else if (state & STATE_BEGAN) {
	        return 'start';
	    }
	    return '';
	}

	/**
	 * direction cons to string
	 * @param {Const} direction
	 * @returns {String}
	 */
	function directionStr(direction) {
	    if (direction == DIRECTION_DOWN) {
	        return 'down';
	    } else if (direction == DIRECTION_UP) {
	        return 'up';
	    } else if (direction == DIRECTION_LEFT) {
	        return 'left';
	    } else if (direction == DIRECTION_RIGHT) {
	        return 'right';
	    }
	    return '';
	}

	/**
	 * get a recognizer by name if it is bound to a manager
	 * @param {Recognizer|String} otherRecognizer
	 * @param {Recognizer} recognizer
	 * @returns {Recognizer}
	 */
	function getRecognizerByNameIfManager(otherRecognizer, recognizer) {
	    var manager = recognizer.manager;
	    if (manager) {
	        return manager.get(otherRecognizer);
	    }
	    return otherRecognizer;
	}

	/**
	 * This recognizer is just used as a base for the simple attribute recognizers.
	 * @constructor
	 * @extends Recognizer
	 */
	function AttrRecognizer() {
	    Recognizer.apply(this, arguments);
	}

	inherit(AttrRecognizer, Recognizer, {
	    /**
	     * @namespace
	     * @memberof AttrRecognizer
	     */
	    defaults: {
	        /**
	         * @type {Number}
	         * @default 1
	         */
	        pointers: 1
	    },

	    /**
	     * Used to check if it the recognizer receives valid input, like input.distance > 10.
	     * @memberof AttrRecognizer
	     * @param {Object} input
	     * @returns {Boolean} recognized
	     */
	    attrTest: function(input) {
	        var optionPointers = this.options.pointers;
	        return optionPointers === 0 || input.pointers.length === optionPointers;
	    },

	    /**
	     * Process the input and return the state for the recognizer
	     * @memberof AttrRecognizer
	     * @param {Object} input
	     * @returns {*} State
	     */
	    process: function(input) {
	        var state = this.state;
	        var eventType = input.eventType;

	        var isRecognized = state & (STATE_BEGAN | STATE_CHANGED);
	        var isValid = this.attrTest(input);

	        // on cancel input and we've recognized before, return STATE_CANCELLED
	        if (isRecognized && (eventType & INPUT_CANCEL || !isValid)) {
	            return state | STATE_CANCELLED;
	        } else if (isRecognized || isValid) {
	            if (eventType & INPUT_END) {
	                return state | STATE_ENDED;
	            } else if (!(state & STATE_BEGAN)) {
	                return STATE_BEGAN;
	            }
	            return state | STATE_CHANGED;
	        }
	        return STATE_FAILED;
	    }
	});

	/**
	 * Pan
	 * Recognized when the pointer is down and moved in the allowed direction.
	 * @constructor
	 * @extends AttrRecognizer
	 */
	function PanRecognizer() {
	    AttrRecognizer.apply(this, arguments);

	    this.pX = null;
	    this.pY = null;
	}

	inherit(PanRecognizer, AttrRecognizer, {
	    /**
	     * @namespace
	     * @memberof PanRecognizer
	     */
	    defaults: {
	        event: 'pan',
	        threshold: 10,
	        pointers: 1,
	        direction: DIRECTION_ALL
	    },

	    getTouchAction: function() {
	        var direction = this.options.direction;
	        var actions = [];
	        if (direction & DIRECTION_HORIZONTAL) {
	            actions.push(TOUCH_ACTION_PAN_Y);
	        }
	        if (direction & DIRECTION_VERTICAL) {
	            actions.push(TOUCH_ACTION_PAN_X);
	        }
	        return actions;
	    },

	    directionTest: function(input) {
	        var options = this.options;
	        var hasMoved = true;
	        var distance = input.distance;
	        var direction = input.direction;
	        var x = input.deltaX;
	        var y = input.deltaY;

	        // lock to axis?
	        if (!(direction & options.direction)) {
	            if (options.direction & DIRECTION_HORIZONTAL) {
	                direction = (x === 0) ? DIRECTION_NONE : (x < 0) ? DIRECTION_LEFT : DIRECTION_RIGHT;
	                hasMoved = x != this.pX;
	                distance = Math.abs(input.deltaX);
	            } else {
	                direction = (y === 0) ? DIRECTION_NONE : (y < 0) ? DIRECTION_UP : DIRECTION_DOWN;
	                hasMoved = y != this.pY;
	                distance = Math.abs(input.deltaY);
	            }
	        }
	        input.direction = direction;
	        return hasMoved && distance > options.threshold && direction & options.direction;
	    },

	    attrTest: function(input) {
	        return AttrRecognizer.prototype.attrTest.call(this, input) &&
	            (this.state & STATE_BEGAN || (!(this.state & STATE_BEGAN) && this.directionTest(input)));
	    },

	    emit: function(input) {

	        this.pX = input.deltaX;
	        this.pY = input.deltaY;

	        var direction = directionStr(input.direction);

	        if (direction) {
	            input.additionalEvent = this.options.event + direction;
	        }
	        this._super.emit.call(this, input);
	    }
	});

	/**
	 * Pinch
	 * Recognized when two or more pointers are moving toward (zoom-in) or away from each other (zoom-out).
	 * @constructor
	 * @extends AttrRecognizer
	 */
	function PinchRecognizer() {
	    AttrRecognizer.apply(this, arguments);
	}

	inherit(PinchRecognizer, AttrRecognizer, {
	    /**
	     * @namespace
	     * @memberof PinchRecognizer
	     */
	    defaults: {
	        event: 'pinch',
	        threshold: 0,
	        pointers: 2
	    },

	    getTouchAction: function() {
	        return [TOUCH_ACTION_NONE];
	    },

	    attrTest: function(input) {
	        return this._super.attrTest.call(this, input) &&
	            (Math.abs(input.scale - 1) > this.options.threshold || this.state & STATE_BEGAN);
	    },

	    emit: function(input) {
	        if (input.scale !== 1) {
	            var inOut = input.scale < 1 ? 'in' : 'out';
	            input.additionalEvent = this.options.event + inOut;
	        }
	        this._super.emit.call(this, input);
	    }
	});

	/**
	 * Press
	 * Recognized when the pointer is down for x ms without any movement.
	 * @constructor
	 * @extends Recognizer
	 */
	function PressRecognizer() {
	    Recognizer.apply(this, arguments);

	    this._timer = null;
	    this._input = null;
	}

	inherit(PressRecognizer, Recognizer, {
	    /**
	     * @namespace
	     * @memberof PressRecognizer
	     */
	    defaults: {
	        event: 'press',
	        pointers: 1,
	        time: 251, // minimal time of the pointer to be pressed
	        threshold: 9 // a minimal movement is ok, but keep it low
	    },

	    getTouchAction: function() {
	        return [TOUCH_ACTION_AUTO];
	    },

	    process: function(input) {
	        var options = this.options;
	        var validPointers = input.pointers.length === options.pointers;
	        var validMovement = input.distance < options.threshold;
	        var validTime = input.deltaTime > options.time;

	        this._input = input;

	        // we only allow little movement
	        // and we've reached an end event, so a tap is possible
	        if (!validMovement || !validPointers || (input.eventType & (INPUT_END | INPUT_CANCEL) && !validTime)) {
	            this.reset();
	        } else if (input.eventType & INPUT_START) {
	            this.reset();
	            this._timer = setTimeoutContext(function() {
	                this.state = STATE_RECOGNIZED;
	                this.tryEmit();
	            }, options.time, this);
	        } else if (input.eventType & INPUT_END) {
	            return STATE_RECOGNIZED;
	        }
	        return STATE_FAILED;
	    },

	    reset: function() {
	        clearTimeout(this._timer);
	    },

	    emit: function(input) {
	        if (this.state !== STATE_RECOGNIZED) {
	            return;
	        }

	        if (input && (input.eventType & INPUT_END)) {
	            this.manager.emit(this.options.event + 'up', input);
	        } else {
	            this._input.timeStamp = now();
	            this.manager.emit(this.options.event, this._input);
	        }
	    }
	});

	/**
	 * Rotate
	 * Recognized when two or more pointer are moving in a circular motion.
	 * @constructor
	 * @extends AttrRecognizer
	 */
	function RotateRecognizer() {
	    AttrRecognizer.apply(this, arguments);
	}

	inherit(RotateRecognizer, AttrRecognizer, {
	    /**
	     * @namespace
	     * @memberof RotateRecognizer
	     */
	    defaults: {
	        event: 'rotate',
	        threshold: 0,
	        pointers: 2
	    },

	    getTouchAction: function() {
	        return [TOUCH_ACTION_NONE];
	    },

	    attrTest: function(input) {
	        return this._super.attrTest.call(this, input) &&
	            (Math.abs(input.rotation) > this.options.threshold || this.state & STATE_BEGAN);
	    }
	});

	/**
	 * Swipe
	 * Recognized when the pointer is moving fast (velocity), with enough distance in the allowed direction.
	 * @constructor
	 * @extends AttrRecognizer
	 */
	function SwipeRecognizer() {
	    AttrRecognizer.apply(this, arguments);
	}

	inherit(SwipeRecognizer, AttrRecognizer, {
	    /**
	     * @namespace
	     * @memberof SwipeRecognizer
	     */
	    defaults: {
	        event: 'swipe',
	        threshold: 10,
	        velocity: 0.3,
	        direction: DIRECTION_HORIZONTAL | DIRECTION_VERTICAL,
	        pointers: 1
	    },

	    getTouchAction: function() {
	        return PanRecognizer.prototype.getTouchAction.call(this);
	    },

	    attrTest: function(input) {
	        var direction = this.options.direction;
	        var velocity;

	        if (direction & (DIRECTION_HORIZONTAL | DIRECTION_VERTICAL)) {
	            velocity = input.overallVelocity;
	        } else if (direction & DIRECTION_HORIZONTAL) {
	            velocity = input.overallVelocityX;
	        } else if (direction & DIRECTION_VERTICAL) {
	            velocity = input.overallVelocityY;
	        }

	        return this._super.attrTest.call(this, input) &&
	            direction & input.offsetDirection &&
	            input.distance > this.options.threshold &&
	            input.maxPointers == this.options.pointers &&
	            abs(velocity) > this.options.velocity && input.eventType & INPUT_END;
	    },

	    emit: function(input) {
	        var direction = directionStr(input.offsetDirection);
	        if (direction) {
	            this.manager.emit(this.options.event + direction, input);
	        }

	        this.manager.emit(this.options.event, input);
	    }
	});

	/**
	 * A tap is ecognized when the pointer is doing a small tap/click. Multiple taps are recognized if they occur
	 * between the given interval and position. The delay option can be used to recognize multi-taps without firing
	 * a single tap.
	 *
	 * The eventData from the emitted event contains the property `tapCount`, which contains the amount of
	 * multi-taps being recognized.
	 * @constructor
	 * @extends Recognizer
	 */
	function TapRecognizer() {
	    Recognizer.apply(this, arguments);

	    // previous time and center,
	    // used for tap counting
	    this.pTime = false;
	    this.pCenter = false;

	    this._timer = null;
	    this._input = null;
	    this.count = 0;
	}

	inherit(TapRecognizer, Recognizer, {
	    /**
	     * @namespace
	     * @memberof PinchRecognizer
	     */
	    defaults: {
	        event: 'tap',
	        pointers: 1,
	        taps: 1,
	        interval: 300, // max time between the multi-tap taps
	        time: 250, // max time of the pointer to be down (like finger on the screen)
	        threshold: 9, // a minimal movement is ok, but keep it low
	        posThreshold: 10 // a multi-tap can be a bit off the initial position
	    },

	    getTouchAction: function() {
	        return [TOUCH_ACTION_MANIPULATION];
	    },

	    process: function(input) {
	        var options = this.options;

	        var validPointers = input.pointers.length === options.pointers;
	        var validMovement = input.distance < options.threshold;
	        var validTouchTime = input.deltaTime < options.time;

	        this.reset();

	        if ((input.eventType & INPUT_START) && (this.count === 0)) {
	            return this.failTimeout();
	        }

	        // we only allow little movement
	        // and we've reached an end event, so a tap is possible
	        if (validMovement && validTouchTime && validPointers) {
	            if (input.eventType != INPUT_END) {
	                return this.failTimeout();
	            }

	            var validInterval = this.pTime ? (input.timeStamp - this.pTime < options.interval) : true;
	            var validMultiTap = !this.pCenter || getDistance(this.pCenter, input.center) < options.posThreshold;

	            this.pTime = input.timeStamp;
	            this.pCenter = input.center;

	            if (!validMultiTap || !validInterval) {
	                this.count = 1;
	            } else {
	                this.count += 1;
	            }

	            this._input = input;

	            // if tap count matches we have recognized it,
	            // else it has began recognizing...
	            var tapCount = this.count % options.taps;
	            if (tapCount === 0) {
	                // no failing requirements, immediately trigger the tap event
	                // or wait as long as the multitap interval to trigger
	                if (!this.hasRequireFailures()) {
	                    return STATE_RECOGNIZED;
	                } else {
	                    this._timer = setTimeoutContext(function() {
	                        this.state = STATE_RECOGNIZED;
	                        this.tryEmit();
	                    }, options.interval, this);
	                    return STATE_BEGAN;
	                }
	            }
	        }
	        return STATE_FAILED;
	    },

	    failTimeout: function() {
	        this._timer = setTimeoutContext(function() {
	            this.state = STATE_FAILED;
	        }, this.options.interval, this);
	        return STATE_FAILED;
	    },

	    reset: function() {
	        clearTimeout(this._timer);
	    },

	    emit: function() {
	        if (this.state == STATE_RECOGNIZED) {
	            this._input.tapCount = this.count;
	            this.manager.emit(this.options.event, this._input);
	        }
	    }
	});

	/**
	 * Simple way to create a manager with a default set of recognizers.
	 * @param {HTMLElement} element
	 * @param {Object} [options]
	 * @constructor
	 */
	function Hammer(element, options) {
	    options = options || {};
	    options.recognizers = ifUndefined(options.recognizers, Hammer.defaults.preset);
	    return new Manager(element, options);
	}

	/**
	 * @const {string}
	 */
	Hammer.VERSION = '2.0.7';

	/**
	 * default settings
	 * @namespace
	 */
	Hammer.defaults = {
	    /**
	     * set if DOM events are being triggered.
	     * But this is slower and unused by simple implementations, so disabled by default.
	     * @type {Boolean}
	     * @default false
	     */
	    domEvents: false,

	    /**
	     * The value for the touchAction property/fallback.
	     * When set to `compute` it will magically set the correct value based on the added recognizers.
	     * @type {String}
	     * @default compute
	     */
	    touchAction: TOUCH_ACTION_COMPUTE,

	    /**
	     * @type {Boolean}
	     * @default true
	     */
	    enable: true,

	    /**
	     * EXPERIMENTAL FEATURE -- can be removed/changed
	     * Change the parent input target element.
	     * If Null, then it is being set the to main element.
	     * @type {Null|EventTarget}
	     * @default null
	     */
	    inputTarget: null,

	    /**
	     * force an input class
	     * @type {Null|Function}
	     * @default null
	     */
	    inputClass: null,

	    /**
	     * Default recognizer setup when calling `Hammer()`
	     * When creating a new Manager these will be skipped.
	     * @type {Array}
	     */
	    preset: [
	        // RecognizerClass, options, [recognizeWith, ...], [requireFailure, ...]
	        [RotateRecognizer, {enable: false}],
	        [PinchRecognizer, {enable: false}, ['rotate']],
	        [SwipeRecognizer, {direction: DIRECTION_HORIZONTAL}],
	        [PanRecognizer, {direction: DIRECTION_HORIZONTAL}, ['swipe']],
	        [TapRecognizer],
	        [TapRecognizer, {event: 'doubletap', taps: 2}, ['tap']],
	        [PressRecognizer]
	    ],

	    /**
	     * Some CSS properties can be used to improve the working of Hammer.
	     * Add them to this method and they will be set when creating a new Manager.
	     * @namespace
	     */
	    cssProps: {
	        /**
	         * Disables text selection to improve the dragging gesture. Mainly for desktop browsers.
	         * @type {String}
	         * @default 'none'
	         */
	        userSelect: 'none',

	        /**
	         * Disable the Windows Phone grippers when pressing an element.
	         * @type {String}
	         * @default 'none'
	         */
	        touchSelect: 'none',

	        /**
	         * Disables the default callout shown when you touch and hold a touch target.
	         * On iOS, when you touch and hold a touch target such as a link, Safari displays
	         * a callout containing information about the link. This property allows you to disable that callout.
	         * @type {String}
	         * @default 'none'
	         */
	        touchCallout: 'none',

	        /**
	         * Specifies whether zooming is enabled. Used by IE10>
	         * @type {String}
	         * @default 'none'
	         */
	        contentZooming: 'none',

	        /**
	         * Specifies that an entire element should be draggable instead of its contents. Mainly for desktop browsers.
	         * @type {String}
	         * @default 'none'
	         */
	        userDrag: 'none',

	        /**
	         * Overrides the highlight color shown when the user taps a link or a JavaScript
	         * clickable element in iOS. This property obeys the alpha value, if specified.
	         * @type {String}
	         * @default 'rgba(0,0,0,0)'
	         */
	        tapHighlightColor: 'rgba(0,0,0,0)'
	    }
	};

	var STOP = 1;
	var FORCED_STOP = 2;

	/**
	 * Manager
	 * @param {HTMLElement} element
	 * @param {Object} [options]
	 * @constructor
	 */
	function Manager(element, options) {
	    this.options = assign({}, Hammer.defaults, options || {});

	    this.options.inputTarget = this.options.inputTarget || element;

	    this.handlers = {};
	    this.session = {};
	    this.recognizers = [];
	    this.oldCssProps = {};

	    this.element = element;
	    this.input = createInputInstance(this);
	    this.touchAction = new TouchAction(this, this.options.touchAction);

	    toggleCssProps(this, true);

	    each(this.options.recognizers, function(item) {
	        var recognizer = this.add(new (item[0])(item[1]));
	        item[2] && recognizer.recognizeWith(item[2]);
	        item[3] && recognizer.requireFailure(item[3]);
	    }, this);
	}

	Manager.prototype = {
	    /**
	     * set options
	     * @param {Object} options
	     * @returns {Manager}
	     */
	    set: function(options) {
	        assign(this.options, options);

	        // Options that need a little more setup
	        if (options.touchAction) {
	            this.touchAction.update();
	        }
	        if (options.inputTarget) {
	            // Clean up existing event listeners and reinitialize
	            this.input.destroy();
	            this.input.target = options.inputTarget;
	            this.input.init();
	        }
	        return this;
	    },

	    /**
	     * stop recognizing for this session.
	     * This session will be discarded, when a new [input]start event is fired.
	     * When forced, the recognizer cycle is stopped immediately.
	     * @param {Boolean} [force]
	     */
	    stop: function(force) {
	        this.session.stopped = force ? FORCED_STOP : STOP;
	    },

	    /**
	     * run the recognizers!
	     * called by the inputHandler function on every movement of the pointers (touches)
	     * it walks through all the recognizers and tries to detect the gesture that is being made
	     * @param {Object} inputData
	     */
	    recognize: function(inputData) {
	        var session = this.session;
	        if (session.stopped) {
	            return;
	        }

	        // run the touch-action polyfill
	        this.touchAction.preventDefaults(inputData);

	        var recognizer;
	        var recognizers = this.recognizers;

	        // this holds the recognizer that is being recognized.
	        // so the recognizer's state needs to be BEGAN, CHANGED, ENDED or RECOGNIZED
	        // if no recognizer is detecting a thing, it is set to `null`
	        var curRecognizer = session.curRecognizer;

	        // reset when the last recognizer is recognized
	        // or when we're in a new session
	        if (!curRecognizer || (curRecognizer && curRecognizer.state & STATE_RECOGNIZED)) {
	            curRecognizer = session.curRecognizer = null;
	        }

	        var i = 0;
	        while (i < recognizers.length) {
	            recognizer = recognizers[i];

	            // find out if we are allowed try to recognize the input for this one.
	            // 1.   allow if the session is NOT forced stopped (see the .stop() method)
	            // 2.   allow if we still haven't recognized a gesture in this session, or the this recognizer is the one
	            //      that is being recognized.
	            // 3.   allow if the recognizer is allowed to run simultaneous with the current recognized recognizer.
	            //      this can be setup with the `recognizeWith()` method on the recognizer.
	            if (session.stopped !== FORCED_STOP && ( // 1
	                    !curRecognizer || recognizer == curRecognizer || // 2
	                    recognizer.canRecognizeWith(curRecognizer))) { // 3
	                recognizer.recognize(inputData);
	            } else {
	                recognizer.reset();
	            }

	            // if the recognizer has been recognizing the input as a valid gesture, we want to store this one as the
	            // current active recognizer. but only if we don't already have an active recognizer
	            if (!curRecognizer && recognizer.state & (STATE_BEGAN | STATE_CHANGED | STATE_ENDED)) {
	                curRecognizer = session.curRecognizer = recognizer;
	            }
	            i++;
	        }
	    },

	    /**
	     * get a recognizer by its event name.
	     * @param {Recognizer|String} recognizer
	     * @returns {Recognizer|Null}
	     */
	    get: function(recognizer) {
	        if (recognizer instanceof Recognizer) {
	            return recognizer;
	        }

	        var recognizers = this.recognizers;
	        for (var i = 0; i < recognizers.length; i++) {
	            if (recognizers[i].options.event == recognizer) {
	                return recognizers[i];
	            }
	        }
	        return null;
	    },

	    /**
	     * add a recognizer to the manager
	     * existing recognizers with the same event name will be removed
	     * @param {Recognizer} recognizer
	     * @returns {Recognizer|Manager}
	     */
	    add: function(recognizer) {
	        if (invokeArrayArg(recognizer, 'add', this)) {
	            return this;
	        }

	        // remove existing
	        var existing = this.get(recognizer.options.event);
	        if (existing) {
	            this.remove(existing);
	        }

	        this.recognizers.push(recognizer);
	        recognizer.manager = this;

	        this.touchAction.update();
	        return recognizer;
	    },

	    /**
	     * remove a recognizer by name or instance
	     * @param {Recognizer|String} recognizer
	     * @returns {Manager}
	     */
	    remove: function(recognizer) {
	        if (invokeArrayArg(recognizer, 'remove', this)) {
	            return this;
	        }

	        recognizer = this.get(recognizer);

	        // let's make sure this recognizer exists
	        if (recognizer) {
	            var recognizers = this.recognizers;
	            var index = inArray(recognizers, recognizer);

	            if (index !== -1) {
	                recognizers.splice(index, 1);
	                this.touchAction.update();
	            }
	        }

	        return this;
	    },

	    /**
	     * bind event
	     * @param {String} events
	     * @param {Function} handler
	     * @returns {EventEmitter} this
	     */
	    on: function(events, handler) {
	        if (events === undefined$1) {
	            return;
	        }
	        if (handler === undefined$1) {
	            return;
	        }

	        var handlers = this.handlers;
	        each(splitStr(events), function(event) {
	            handlers[event] = handlers[event] || [];
	            handlers[event].push(handler);
	        });
	        return this;
	    },

	    /**
	     * unbind event, leave emit blank to remove all handlers
	     * @param {String} events
	     * @param {Function} [handler]
	     * @returns {EventEmitter} this
	     */
	    off: function(events, handler) {
	        if (events === undefined$1) {
	            return;
	        }

	        var handlers = this.handlers;
	        each(splitStr(events), function(event) {
	            if (!handler) {
	                delete handlers[event];
	            } else {
	                handlers[event] && handlers[event].splice(inArray(handlers[event], handler), 1);
	            }
	        });
	        return this;
	    },

	    /**
	     * emit event to the listeners
	     * @param {String} event
	     * @param {Object} data
	     */
	    emit: function(event, data) {
	        // we also want to trigger dom events
	        if (this.options.domEvents) {
	            triggerDomEvent(event, data);
	        }

	        // no handlers, so skip it all
	        var handlers = this.handlers[event] && this.handlers[event].slice();
	        if (!handlers || !handlers.length) {
	            return;
	        }

	        data.type = event;
	        data.preventDefault = function() {
	            data.srcEvent.preventDefault();
	        };

	        var i = 0;
	        while (i < handlers.length) {
	            handlers[i](data);
	            i++;
	        }
	    },

	    /**
	     * destroy the manager and unbinds all events
	     * it doesn't unbind dom events, that is the user own responsibility
	     */
	    destroy: function() {
	        this.element && toggleCssProps(this, false);

	        this.handlers = {};
	        this.session = {};
	        this.input.destroy();
	        this.element = null;
	    }
	};

	/**
	 * add/remove the css properties as defined in manager.options.cssProps
	 * @param {Manager} manager
	 * @param {Boolean} add
	 */
	function toggleCssProps(manager, add) {
	    var element = manager.element;
	    if (!element.style) {
	        return;
	    }
	    var prop;
	    each(manager.options.cssProps, function(value, name) {
	        prop = prefixed(element.style, name);
	        if (add) {
	            manager.oldCssProps[prop] = element.style[prop];
	            element.style[prop] = value;
	        } else {
	            element.style[prop] = manager.oldCssProps[prop] || '';
	        }
	    });
	    if (!add) {
	        manager.oldCssProps = {};
	    }
	}

	/**
	 * trigger dom event
	 * @param {String} event
	 * @param {Object} data
	 */
	function triggerDomEvent(event, data) {
	    var gestureEvent = document.createEvent('Event');
	    gestureEvent.initEvent(event, true, true);
	    gestureEvent.gesture = data;
	    data.target.dispatchEvent(gestureEvent);
	}

	assign(Hammer, {
	    INPUT_START: INPUT_START,
	    INPUT_MOVE: INPUT_MOVE,
	    INPUT_END: INPUT_END,
	    INPUT_CANCEL: INPUT_CANCEL,

	    STATE_POSSIBLE: STATE_POSSIBLE,
	    STATE_BEGAN: STATE_BEGAN,
	    STATE_CHANGED: STATE_CHANGED,
	    STATE_ENDED: STATE_ENDED,
	    STATE_RECOGNIZED: STATE_RECOGNIZED,
	    STATE_CANCELLED: STATE_CANCELLED,
	    STATE_FAILED: STATE_FAILED,

	    DIRECTION_NONE: DIRECTION_NONE,
	    DIRECTION_LEFT: DIRECTION_LEFT,
	    DIRECTION_RIGHT: DIRECTION_RIGHT,
	    DIRECTION_UP: DIRECTION_UP,
	    DIRECTION_DOWN: DIRECTION_DOWN,
	    DIRECTION_HORIZONTAL: DIRECTION_HORIZONTAL,
	    DIRECTION_VERTICAL: DIRECTION_VERTICAL,
	    DIRECTION_ALL: DIRECTION_ALL,

	    Manager: Manager,
	    Input: Input,
	    TouchAction: TouchAction,

	    TouchInput: TouchInput,
	    MouseInput: MouseInput,
	    PointerEventInput: PointerEventInput,
	    TouchMouseInput: TouchMouseInput,
	    SingleTouchInput: SingleTouchInput,

	    Recognizer: Recognizer,
	    AttrRecognizer: AttrRecognizer,
	    Tap: TapRecognizer,
	    Pan: PanRecognizer,
	    Swipe: SwipeRecognizer,
	    Pinch: PinchRecognizer,
	    Rotate: RotateRecognizer,
	    Press: PressRecognizer,

	    on: addEventListeners,
	    off: removeEventListeners,
	    each: each,
	    merge: merge,
	    extend: extend,
	    assign: assign,
	    inherit: inherit,
	    bindFn: bindFn,
	    prefixed: prefixed
	});

	// this prevents errors when Hammer is loaded in the presence of an AMD
	//  style loader but by script tag, not by the loader.
	var freeGlobal = (typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : {})); // jshint ignore:line
	freeGlobal.Hammer = Hammer;

	if (module.exports) {
	    module.exports = Hammer;
	} else {
	    window[exportName] = Hammer;
	}

	})(window, document, 'Hammer'); 
} (hammer));

var hammerExports = hammer.exports;
var Hammer = /*@__PURE__*/getDefaultExportFromCjs(hammerExports);

class ControlPlugin {
    name = 'Control';
    hammer;
    context = {};
    lastX = 0;
    lastY = 0;
    isMoving = false;
    config = {};
    constructor(config) {
        this.config = config;
    }
    apply(context) {
        this.context = context;
        const { renderingContext, config } = context;
        const { canvasEl } = config;
        const { root } = renderingContext;
        const isHitRoot = (e) => {
            if (e instanceof TouchEvent || e instanceof KeyboardEvent) {
                console.warn('Control Plugin is not support for TouchEvent or KeyboardEvent');
                return false;
            }
            const a = root.ownerCanvas.client2Viewport(new Point(e.clientX, e.clientY));
            const hitResult = context.eventSystem.hitTest(a.x, a.y);
            if (hitResult === root) {
                return true;
            }
            return false;
        };
        const onPanStart = (e) => {
            if (!isHitRoot(e.srcEvent))
                return;
            this.lastX = e.center.x;
            this.lastY = e.center.y;
            this.isMoving = true;
        };
        const onPanMove = (e) => {
            e.srcEvent.stopPropagation();
            if (this.isMoving) {
                const deltaX = e.center.x - this.lastX;
                const deltaY = e.center.y - this.lastY;
                this.lastX = e.center.x;
                this.lastY = e.center.y;
                const { camera } = this.context;
                camera.pan(-deltaX, deltaY);
            }
        };
        const onPanEnd = () => {
            this.isMoving = false;
        };
        const preventScroll = (e) => {
            e.preventDefault();
        };
        context.renderingSystem.hooks.init.tap(() => {
            const hammer = new Hammer(root, {
                inputClass: Hammer.PointerEventInput,
            });
            this.hammer = hammer;
            this.hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
            this.hammer.get('pinch').set({ enable: true });
            this.hammer.on('panstart', onPanStart);
            this.hammer.on('panmove', onPanMove);
            this.hammer.on('panend', onPanEnd);
            this.hammer.on('pinch', this.onPinch);
            canvasEl.addEventListener('wheel', preventScroll, { passive: false });
            root.addEventListener('wheel', this.onScroll);
        });
        context.renderingSystem.hooks.destroy.tap(() => {
            this.hammer.off('panstart', onPanStart);
            this.hammer.off('panmove', onPanMove);
            this.hammer.off('panend', onPanEnd);
            this.hammer.off('pinch', this.onPinch);
            canvasEl.removeEventListener('wheel', preventScroll);
            root.removeEventListener('wheel', this.onScroll);
        });
    }
    onPinch = (e) => {
        console.log('pinch', e);
        const { camera } = this.context;
        camera.dolly(e.deltaY);
    };
    onScroll = (e) => {
        const { camera } = this.context;
        if (isCtrlKey(e)) {
            const newZoom = this.clampZoom(camera.Zoom[0] + e.deltaY / 3);
            camera.setZoomByScroll([newZoom, camera.Zoom[1]], [e.viewportX, e.viewportY]);
        }
        else {
            const newZoom = this.clampZoom(camera.Zoom[1] + e.deltaY / 3);
            camera.setZoomByScroll([camera.Zoom[0], newZoom], [e.viewportX, e.viewportY]);
        }
    };
    clampZoom = (zoom) => {
        const min = this.config.minZoom;
        const max = this.config.maxZoom;
        let a = !isNil(min) ? Math.max(min, zoom) : zoom;
        a = !isNil(max) ? Math.min(max, a) : a;
        return a;
    };
    destroy() {
        this.hammer.destroy();
    }
}

class Canvas2DContextSystem {
    dpr;
    canvasEl;
    canvas2D;
    config;
    constructor(context) {
        this.config = context.config;
    }
    init = () => {
        const { canvasEl, devicePixelRatio, width, height } = this.config;
        this.dpr = devicePixelRatio;
        this.canvasEl = canvasEl;
        this.canvas2D = canvasEl.getContext('2d');
        this.resize(width, height);
    };
    dispose = () => {
        return;
    };
    resize = (width, height) => {
        this.canvasEl.style.width = width + 'px';
        this.canvasEl.style.height = height + 'px';
        const canvasEl = this.canvasEl;
        canvasEl.width = width * this.dpr; // CSS pixels to physical pixels
        canvasEl.height = height * this.dpr;
    };
    getContext = () => this.canvas2D;
    getCanvasElement = () => this.canvasEl;
}

const DEFAULT_CAMERA_Z = 500;
const DEFAULT_CAMERA_NEAR = 0.1;
const DEFAULT_CAMERA_FAR = 1000;
class CanvasEditor {
    container;
    canvasEl;
    root;
    camera;
    plugins = [];
    context = {};
    animateID;
    eventEmitter = new EventEmitter();
    constructor(options, plugins) {
        const { container, canvasEl, width, height, devicePixelRatio = window.devicePixelRatio, supportsCSSTransform = false, supportsPointerEvents = true, supportsTouchEvents = false, } = options;
        const root = new Group({
            id: 'root',
            name: 'root',
        });
        root.ownerCanvas = this;
        this.root = root;
        if (isString(container)) {
            const c = document.getElementById(container);
            if (isNil(c))
                throw new Error(`cannot find HTMLElement(id= ${container})`);
            this.container = c;
        }
        else
            this.container = container;
        this.container.style.touchAction = 'none';
        if (canvasEl) {
            this.canvasEl = canvasEl;
            if (container && canvasEl.parentElement !== container) {
                this.container.appendChild(canvasEl);
            }
        }
        else if (container) {
            const canvasEl = document.createElement('canvas');
            this.container.appendChild(canvasEl);
            this.canvasEl = canvasEl;
        }
        else {
            throw new Error('need canvasEl or container');
        }
        this.canvasEl.style.touchAction = 'none';
        let canvasWidth = width || -1;
        let canvasHeight = height || -1;
        if (canvasEl) {
            canvasWidth = width || canvasEl.offsetWidth || canvasEl.width / devicePixelRatio;
            canvasHeight = height || canvasEl.offsetHeight || canvasEl.height / devicePixelRatio;
        }
        this.context.config = {
            canvasEl: this.canvasEl,
            container: this.container,
            width: canvasWidth,
            height: canvasHeight,
            devicePixelRatio,
            supportsCSSTransform,
            supportsPointerEvents,
            supportsTouchEvents,
        };
        // init context, then init systems, finally init plugins
        this.initRenderContext();
        this.initCamera(canvasWidth, canvasHeight, ClipSpaceNearZ.NEGATIVE_ONE);
        this.initRenderer(plugins || []);
    }
    initRenderContext = () => {
        const renderContext = {
            root: this.root,
        };
        this.context.renderingContext = renderContext;
    };
    initCamera = (width, height, clipSpaceNearZ) => {
        const camera = new Camera(this);
        camera.clipSpaceNearZ = clipSpaceNearZ;
        camera
            .setPosition(width / 2, height / 2, DEFAULT_CAMERA_Z)
            .setFocalPoint(width / 2, height / 2, 0)
            .setOrthographic(-width / 2, width / 2, height / 2, -height / 2, DEFAULT_CAMERA_NEAR, DEFAULT_CAMERA_FAR);
        this.camera = camera;
        this.context.camera = camera;
    };
    initRenderer = (plugins) => {
        this.context.renderingSystem = new RenderingSystem(this.context);
        this.context.eventSystem = new EventSystem(this.context);
        this.context.contextSystem = new Canvas2DContextSystem(this.context);
        this.context.eventSystem.init();
        if (this.context.contextSystem.init) {
            this.context.contextSystem.init();
            this.initRenderingSystem(plugins);
        }
        else if (this.context.contextSystem.initAsync) {
            this.context.contextSystem.initAsync().then(() => {
                this.initRenderingSystem(plugins);
            });
        }
        else {
            throw new Error('contextSystem must has init() or initAsync()');
        }
    };
    initRenderingSystem = (plugins) => {
        // plugins.apply will hook to renderSystem.hooks
        this.plugins.push(new EventPlugin(), new CullingPlugin(), ...plugins);
        this.plugins.forEach((a) => a.apply(this.context));
        this.context.renderingSystem.init();
        this.run();
    };
    run = () => {
        const tick = () => {
            this.getRendererSystem().render();
            this.animateID = requestAnimationFrame(tick);
        };
        tick();
    };
    createElement = (ctor, config) => {
        const obj = new ctor(config);
        obj.ownerCanvas = this;
        return obj;
    };
    tmpMat4 = create$3();
    tmpVec3 = create$2();
    viewport2Canvas = (point) => {
        const camera = this.camera;
        const { width, height } = this.context.config;
        const flipY = camera.FlipY;
        const w = camera.WorldTransform;
        const pi = camera.ProjectionInverse;
        const piw = multiply$1(this.tmpMat4, w, pi);
        // viewport -> NDC -> clip. Flip Y.
        const clip = set(this.tmpVec3, (point.x / width) * 2 - 1, (flipY ? 1 - point.y / height : point.y / height) * 2 - 1, 0);
        // clip -> view -> world
        const world = transformMat4$1(clip, clip, piw);
        return { x: world[0], y: world[1] };
    };
    canvas2Viewport = (point) => {
        const camera = this.camera;
        const { width, height } = this.context.config;
        const flipY = camera.FlipY;
        const v = camera.ViewTransform;
        const p = camera.ProjectionMatrix;
        const vp = multiply$1(this.tmpMat4, p, v);
        const clip = set(this.tmpVec3, point.x, point.y, 0);
        // world -> view -> clip
        transformMat4$1(clip, clip, vp);
        // clip(-1 ~ 1) -> NDC -> viewport. Flip Y
        return {
            x: ((clip[0] + 1) / 2) * width,
            y: (flipY ? 1 - (clip[1] + 1) / 2 : (clip[1] + 1) / 2) * height,
        };
    };
    client2Viewport = (client) => {
        const bbox = this.canvasEl?.getBoundingClientRect();
        return { x: client.x - (bbox?.left || 0), y: client.y - (bbox?.top || 0) };
    };
    viewport2Client = (viewport) => {
        const bbox = this.canvasEl?.getBoundingClientRect();
        return { x: viewport.x + (bbox?.left || 0), y: viewport.y + (bbox?.top || 0) };
    };
    getEventSystem = () => this.context.eventSystem;
    getRendererSystem = () => this.context.renderingSystem;
    getContextSystem = () => this.context.contextSystem;
    getConfig = () => this.context.config;
    getCamera = () => this.camera;
    dispose = () => {
        //  this.resizeObserver.unobserve(this.container);
        this.container.removeChild(this.canvasEl);
        this.context.renderingSystem.dispose();
        this.animateID && cancelAnimationFrame(this.animateID);
        this.plugins.forEach((a) => a.destroy());
    };
}

export { Camera, CameraEvent, CameraProjectionMode, CameraTrackingMode, CameraType, Canvas2DRendererPlugin, CanvasEditor, Circle, ClipSpaceNearZ, ContextMenuType, ControlPlugin, CoordinatePlugin, CullingPlugin, Curve, EventPlugin, Group, Line, OffsetLine, Polygon, ResizePlugin, Shape };
//# sourceMappingURL=index.js.map
